/* ============================================================
   Lougha Chats — Phase 1 (foundation)
   DOM-based rendering (no innerHTML on user data) for XSS safety

   BUNDLE REVISION: 2026-07-26-b
   Bumped to force a NEW content hash. The previous hash (44d63c95d1b4) is
   poisoned in Cloudflare: a rolling deploy let the old container answer that
   URL, and the stale body was stored `immutable, 1y`, so every browser kept
   the pre-note build. We have no purge credentials, so the way out is a URL
   the CDN has never seen. The guard added in the same batch (the immutable
   header now requires the requested ?v= to match this container's file) is
   what stops the next deploy from doing it again — bump this line only if a
   CDN entry is ever poisoned again.
   ============================================================ */

(function () {
  'use strict';
    // BEGIN INBOX PERFORMANCE CLIENT
// Embedded in the desktop bundle by scripts/build-inbox-performance.cjs.
// Only fixed metric names and duration buckets leave the browser.
function createInboxPerformanceClient({ app, getToken, isVisible }) {
    const bounds = [16, 50, 100, 250, 500, 1000, 2000, 5000, 10000, 15000, 30000, 60000, 120000];
    const names = ['render_ms', 'first_messages_ms', 'message_read_ms', 'list_read_ms', 'long_task_ms', 'input_delay_ms'];
    let metrics = Object.create(null), pending = false, stopped = false, open = null, sequence = 0;
    const visible = () => { try { return !document.hidden && isVisible(); } catch { return false; } };
    function record(metric, duration) {
        if (!visible() || !names.includes(metric) || !Number.isFinite(duration) || duration < 0) return;
        const bins = metrics[metric] || (metrics[metric] = bounds.map(() => 0));
        const index = bounds.findIndex(bound => duration <= bound);
        if (bins.reduce((a,b)=>a+b,0)<10000) bins[index < 0 ? bounds.length - 1 : index]++;
    }
    function beginOpen() { open = { id: ++sequence, at: performance.now() }; }
    function painted(hasMessages) {
        if (!open || !hasMessages || !visible()) return;
        const attempt = open; open = null;
        requestAnimationFrame(() => { if (attempt.id === sequence) record('first_messages_ms', performance.now() - attempt.at); });
    }
    async function flush() {
        if (pending || stopped || !visible()) return;
        let token; try { token = getToken(); } catch { return; }
        if (!token) { metrics = Object.create(null); open = null; return; }
        const batch = Object.entries(metrics).map(([metric,buckets])=>({metric,buckets}));
        if (!batch.length) return;
        metrics = Object.create(null); pending = true;
        const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 5000);
        try {
            await fetch('/api/inbox-performance', { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
                body: JSON.stringify({ app, build: 'inbox-20260911', metrics: batch }), signal: controller.signal, cache: 'no-store' });
        } catch { /* Best effort; telemetry never retries or blocks user work. */ }
        finally { clearTimeout(timer); pending = false; }
    }
    const observers = [];
    function observe(type, consume, options = {}) {
        try {
            if (!PerformanceObserver.supportedEntryTypes.includes(type)) return;
            const observer = new PerformanceObserver(list => {
                if (!visible()) return;
                for (const entry of list.getEntries()) consume(entry);
            });
            observer.observe({ type, buffered: false, ...options }); observers.push(observer);
        } catch { /* Unsupported browser: do not substitute fake measurements. */ }
    }
    observe('longtask', entry => record('long_task_ms', entry.duration));
    observe('event', entry => record('input_delay_ms', Math.max(0, entry.processingStart - entry.startTime)), { durationThreshold: 16 });
    observe('resource', entry => {
        if (!['fetch','xmlhttprequest'].includes(entry.initiatorType)) return;
        let url; try { url = new URL(entry.name, location.href); } catch { return; }
        if (url.origin !== location.origin || !url.pathname.startsWith('/api/')) return;
        // URL and identifiers are inspected locally and never copied to the report.
        if (/\/messages(?:\/|$)/.test(url.pathname)) record('message_read_ms', entry.duration);
        else if (/\/(?:chats|conversations)$/.test(url.pathname)) record('list_read_ms', entry.duration);
    });
    const timer = setInterval(flush, 60000);
    return { record, beginOpen, painted, flush, stop() { stopped = true; clearInterval(timer); observers.forEach(o=>o.disconnect()); metrics=Object.create(null); open=null; } };
}

    const _inboxPerf = createInboxPerformanceClient({ app: 'leads7', getToken: () => state.token, isVisible: () => state.activeView === 'inbox' });
    // END INBOX PERFORMANCE CLIENT
  // Admin display labels (Ayoub, 2026-09-23): the raw login of the three
  // administration accounts never reaches a screen. ONLY these three — every
  // other user's text stays byte-identical. Mirrors lib/admin-display.js;
  // /api/me extends the map with the admins' user ids (adminAliases).
  // Usernames stay the identity key everywhere: this is display only.
  const _ADMIN_ALIASES = Object.create(null);
  (function _seedAdminAliases() {
    const base = { ayoub: 'Administration 1', mmehanane: 'Administration 2', mansour: 'Administration 2' };
    for (const u of Object.keys(base)) {
      _ADMIN_ALIASES[u] = base[u];
      for (const p of ['leads7@', 'support2@', 's2:', 'leads7:', 'support2:']) _ADMIN_ALIASES[p + u] = base[u];
    }
    _ADMIN_ALIASES['team-person:administration-a'] = 'Administration 1';
    _ADMIN_ALIASES['team-person:administration-h'] = 'Administration 2';
  })();
  function _mergeAdminAliases(map) {
    if (!map || typeof map !== 'object') return;
    for (const k of Object.keys(map)) {
      const v = map[k];
      if (typeof v === 'string' && v) _ADMIN_ALIASES[String(k).trim().toLowerCase()] = v;
    }
  }
  // "Administration 1/2" when `ref` (login, user id, canon form, @login,
  // frozen team label) is an admin account, else ''.
  function adminLabelFor(ref) {
    let k = String(ref == null ? '' : ref).trim().toLowerCase();
    if (!k) return '';
    if (_ADMIN_ALIASES[k]) return _ADMIN_ALIASES[k];
    if (k[0] === '@') k = k.slice(1);
    return _ADMIN_ALIASES[k] || '';
  }
  // Display label for a TEAM identity: admin → "Administration N", anyone
  // else → `fallback` (default: the ref itself), unchanged.
  function userLabel(ref, fallback) {
    return adminLabelFor(ref) || (fallback !== undefined ? fallback : ref);
  }
  // The @ handle the note box shows for a teammate: an admin's label without
  // spaces ("@administration1", as support2 writes it) — never the login.
  // The server reads the handle back as that login. Anyone else: the login.
  function mentionHandle(username) {
    const label = adminLabelFor(username);
    return label ? label.toLowerCase().replace(/[^a-z0-9]+/g, '') : String(username || '');
  }
  // A stored note opened for edit: its "@ayoub" tokens shown as handles.
  function adminMentionHandles(text) {
    if (typeof text !== 'string' || !/ayoub|mmehanane|mansour/.test(text)) return text;
    return text.replace(_ADMIN_TEXT_RULES[0][0], (m, pre, login) =>
      adminLabelFor(login) ? pre + '@' + mentionHandle(login) : m);
  }
  // Team-chat sender: resolve by the sender's ids, then the (possibly frozen)
  // stored label — "Ayoub"/"MmeHanane" written before the rename included.
  function _teamMsgFromLabel(m, fallback) {
    if (!m) return fallback;
    return adminLabelFor(m.fromUserId) || adminLabelFor(m.fromIdentityId) || adminLabelFor(m.fromCanon)
      || adminLabelFor(m.fromName) || fallback;
  }
  // Admin logins inside KNOWN system sentences + "@login" mention tokens.
  // Case-sensitive on the lowercase login: "bouazzaoui Ayoub" (a client) and
  // "@mary" are returned untouched.
  const _ADMIN_LOGIN_RE = '(?:(?:leads7|support2|s2)[@:])?(?:ayoub|mmehanane|mansour)';
  const _ADMIN_TEXT_RULES = [
    [new RegExp('(^|[^\\w@])@(' + _ADMIN_LOGIN_RE + ')(?![\\w.@-])', 'g'), true],
    [new RegExp('(owner changed by |owner was cleared by |\\(changed by |Bot looped in |Bot a assigné |Demande de |restauré par )(' + _ADMIN_LOGIN_RE + ')(?![\\w.@-])', 'g'), false],
    [new RegExp('(^)(' + _ADMIN_LOGIN_RE + ')(?= edited an internal note)', 'g'), false],
    [new RegExp('(— )(' + _ADMIN_LOGIN_RE + ')(?= doit reprendre)', 'g'), false],
  ];
  function adminText(text) {
    if (typeof text !== 'string' || !text || !/ayoub|mmehanane|mansour/.test(text)) return text;
    let out = text;
    for (const [re, isMention] of _ADMIN_TEXT_RULES) {
      out = out.replace(re, (m, pre, login) => {
        const label = adminLabelFor(login);
        return label ? pre + (isMention ? '@' : '') + label : m;
      });
    }
    return out;
  }
  // Bundle revision — see BUNDLE REVISION above. This is a real statement on
  // purpose: comments are stripped by the minifier, so only executable code
  // changes app.min.js's bytes (and therefore its ?v= hash). It doubles as a
  // way to read the shipped build from the console: window.__leads7Bundle.
  window.__leads7Bundle = '2026-07-26-b';

  // ============ State ============
  const TOKEN_KEY = 'lougha_chats_token';
  const state = {
    token: localStorage.getItem(TOKEN_KEY) || '',
    me: null,
    // Non-null while the super admin is inside another account ("Log as").
    // Comes from /api/me on every boot — never inferred from storage.
    impersonatedBy: null,
    socket: null,
    // Tutoriels picker: null until first load (distinguishes "not fetched"
    // from "fetched, none exist" — the empty state wording differs).
    mediaTemplates: null,
    mediaTemplateCategory: '__all__',
    mediaTemplateSearch: '',
    clientState: 'DISCONNECTED',
    info: null,
    activeChannel: 'whatsapp',
    // Platform sub-filter inside the merged "Social media" channel
    // (2026-07-17): 'all' | 'fbm' | 'ig' | 'tt'. Driven by the #socialChips
    // pills above the conversation list; only consulted when
    // activeChannel === 'social'.
    socialFilter: 'all',
    // Multi-select inbox filters (HubSpot-style, 2026-07-08). OR inside each
    // set (values of one property), AND across the three sets. All empty =
    // "All conversations". `date` is a single-select recency slice on the
    // conversation's last-message timestamp (2026-07-14): preset ∈ '' | today |
    // yesterday | this_week | last_week | this_month | last_month | between;
    // for 'between', from/to hold local-midnight ms bounds ([from, to)).
    inboxFilters: { types: new Set(), assignees: new Set(), stages: new Set(), crm: new Set(),
                    date: { preset: '', from: null, to: null } },
    // Empty = All sources. The compact source rail narrows the ONE person list
    // without changing who the signed-in agent is authorised to see.
    srcFilter: '',
    chats: [],
    selectedChatId: null,
    selectedChat: null,
    // The rendered inbox row is a person, not a transport thread. These two
    // fields retain the authorised Leads7/SMS/Support2/App rows behind it so
    // opening one row can build one chronological timeline and route replies.
    personChat: null,
    renderedPersonRows: new Map(),
    personMessageCache: new Map(),
    // Leads7 ⇄ support2 mirror: support2's rows per open client (keyed by the
    // local WA/SMS chat id), read from support2's store — never copied here.
    peerFeed: new Map(),
    messages: [],
    searchQ: '',
    searchTimer: null,
    refreshTimer: null,
    msgPollTimer: null,
    recording: null,
    assignments: {},        // chatId -> { userId, username, displayName, role, ... }
    assignableUsers: [],
    assignDropdownOpen: false,
    recruitment: {
      candidate: null,
      overview: null,
      library: null,      // { journeyCount } — the shadow library's real size

      lane: null,          // { entries, bot, knowledge } — recruitment lane + its bot
      trialLeads: [],
      loaded: false,
      adminLanguage: localStorage.getItem('leads7_recruitment_admin_language') === 'ar' ? 'ar' : 'en',
      adminFilter: 'all',
      adminSearch: '',
      // Hand-picking practice conversations for one candidate.
      picker: null,
    },
    // Monotonic authorization boundary. Inbox responses capture this before
    // fetch and may commit only while it is unchanged.
    ownershipEpoch: 0
  };

  // ============ DOM helpers ============
  const $ = id => document.getElementById(id);
  function el(tag, props, children) {
    const node = document.createElement(tag);
    if (props) {
      for (const k in props) {
        const v = props[k];
        if (v == null) continue;
        if (k === 'class') node.className = v;
        else if (k === 'text') node.textContent = v;
        else if (k === 'html') node.innerHTML = v; // STATIC ONLY
        else if (k.startsWith('data-')) node.setAttribute(k, v);
        else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v);
        else node.setAttribute(k, v);
      }
    }
    if (children) {
      for (const c of [].concat(children)) {
        if (c == null) continue;
        node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      }
    }
    return node;
  }
  function clear(node) { while (node.firstChild) node.removeChild(node.firstChild); }
  // Word / PowerPoint / Excel open as a PDF in a new tab — most PCs and phones
  // have no Office (server: lib/media/office-pdf-view.js). '' = open as is.
  const _OFFICE_DOC_RE = /\.(docx?|pptx?|ppsx?|xlsx?|odt|odp|ods|rtf)$/i;
  function _officePdfUrl(m) {
    const url = String((m && m.mediaUrl) || '');
    if (!/^\/uploads\//.test(url)) return '';
    const stored = url.split('?')[0];
    if (!_OFFICE_DOC_RE.test(m.fileName || '') && !_OFFICE_DOC_RE.test(stored) && !/\.vndopenxmlfo$/i.test(stored)) return '';
    return url + (url.includes('?') ? '&' : '?') + 'view=pdf&name=' + encodeURIComponent(m.fileName || '');
  }
  function lucideIcon(name, opts) {
    const i = document.createElement('i');
    i.setAttribute('data-lucide', name);
    if (opts && opts.style) i.setAttribute('style', opts.style);
    return i;
  }
  // Custom raster/SVG icon helper — points at a file under /icons/. Used by the
  // custom-time toggle (calendar-plus-custom.png, a transparent cobalt glyph).
  function customIcon(file, alt) {
    return el('img', { src: '/icons/' + file, alt: alt || '', class: 'pt-custom-icon' });
  }
  // Optional `root` scopes lucide's DOM walk to a subtree so the 67+ render
  // call sites don't each rescan the whole document (~800 icons). Falls back
  // to a global call when the bundled lucide doesn't accept the option.
  function refreshIcons(root) {
    if (!window.lucide || !lucide.createIcons) return;
    // Leads7 Speed, 2026-10-09: lucide.createIcons() re-creates EVERY icon under
    // its root, already-drawn SVGs included (they keep data-lucide). Draw only
    // what needs it: icons not drawn yet, or whose data-lucide changed since
    // (play/pause, mic, call states). Same fix as support2 (14-20 ms → 0.2-0.4 ms
    // per call there, identical SVG output); any error falls back to the full redraw.
    try {
      const scope = root && root.querySelectorAll ? root : document;
      let todo = 0;
      for (const node of scope.querySelectorAll('[data-lucide]')) {
        const name = node.getAttribute('data-lucide');
        if (node.tagName.toLowerCase() === 'svg' && node.getAttribute('data-lucide-drawn') === name) continue;
        node.setAttribute('data-lucide-todo', name);
        todo++;
      }
      if (!todo) return;
      lucide.createIcons({ nameAttr: 'data-lucide-todo', root: scope });
      for (const node of scope.querySelectorAll('[data-lucide-todo]')) {
        const name = node.getAttribute('data-lucide-todo');
        node.removeAttribute('data-lucide-todo');
        if (node.tagName.toLowerCase() === 'svg') node.setAttribute('data-lucide-drawn', name);
      }
      return;
    } catch (_) { /* fall back to the full redraw below */ }
    if (root) {
      try { lucide.createIcons({ root }); return; } catch (_) {}
    }
    lucide.createIcons();
  }

  const E = {
    loginScreen: $('loginScreen'),
    qrScreen: $('qrScreen'),
    cloudApiErrorScreen: $('cloudApiErrorScreen'),
    cloudApiErrorDetail: $('cloudApiErrorDetail'),
    cloudApiRetryBtn: $('cloudApiRetryBtn'),
    cloudApiSkipBtn: $('cloudApiSkipBtn'),
    waDownBanner: $('waDownBanner'),
    waDownDetail: $('waDownDetail'),
    waDownRetryBtn: $('waDownRetryBtn'),
    waDownDismissBtn: $('waDownDismissBtn'),
    appUpdateBanner: $('appUpdateBanner'),
    appUpdateReloadBtn: $('appUpdateReloadBtn'),
    appUpdateDismissBtn: $('appUpdateDismissBtn'),
    app: $('app'),
    usernameInput: $('usernameInput'),
    passwordInput: $('passwordInput'),
    loginBtn: $('loginBtn'),
    loginError: $('loginError'),
    qrLoading: $('qrLoading'),
    qrImage: $('qrImage'),
    qrStatus: $('qrStatus'),
    qrLogoutBtn: $('qrLogoutBtn'),
    sidebarToggleBtn: $('sidebarToggleBtn'),
    notifBtn: $('notifBtn'),
    main: () => document.querySelector('.main'),
    sidebar: $('sidebar'),
    refreshChatsBtn: $('refreshChatsBtn'),
    connStatus: $('connStatus'),
    connText: $('connText'),
    connActionBtn: $('connActionBtn'),
    connActionLabel: $('connActionLabel'),
    filterBtn: $('filterBtn'),
    filterMenu: $('filterMenu'),
    filterLabel: $('filterLabel'),
    searchInput: $('searchInput'),
    conversationList: $('conversationList'),
    chatPane: $('chatPane'),
    emptyState: $('emptyState'),
    convView: $('convView'),
    chatBackBtn: $('chatBackBtn'),
    chatAvatar: $('chatAvatar'),
    chatName: $('chatName'),
    chatPhone: $('chatPhone'),
    messagesArea: $('messagesArea'),
    composeInput: $('composeInput'),
    composeChannelBtn: $('composeChannelBtn'),
    composeNoteBtn: $('composeNoteBtn'),
    composeChannelLabel: $('composeChannelLabel'),
    sendBtn: $('sendBtn'),
    micBtn: $('micBtn'),
    emojiBtn: $('emojiBtn'),
    attachBtn: $('attachBtn'),
    attachInput: $('attachInput'),
    replyPreview: $('replyPreview'),
    replyPreviewAuthor: $('replyPreviewAuthor'),
    replyPreviewSnippet: $('replyPreviewSnippet'),
    replyPreviewClose: $('replyPreviewClose'),
    recordOverlay: $('recordOverlay'),
    recordTime: $('recordTime'),
    cancelRecordBtn: $('cancelRecordBtn'),
    sendRecordBtn: $('sendRecordBtn'),
    botToggleBtn: $('botToggleBtn'),
    availabilityRequestBtn: $('availabilityRequestBtn'),
    waCallBtn: $('waCallBtn'),
    cxCallBtn: $('cxCallBtn'),
    kanzaCallBtn: $('kanzaCallBtn'),
    addContactBtn: $('addContactBtn'),
    blockContactBtn: $('blockContactBtn'),
    deleteContactBtn: $('deleteContactBtn'),
    teamChatBtn: $('teamChatBtn'),
    assignBtn: $('assignBtn'),
    assignDropdown: $('assignDropdown'),
    assignDropdownList: $('assignDropdownList'),
    notesBtn: $('notesBtn'),
    templatesBtn: $('templatesBtn'),
    mediaTemplatesBtn: $('mediaTemplatesBtn'),
    documentsBtn: $('documentsBtn'),
    emailComposeBtn: $('emailComposeBtn'),
    websiteTestBtn: $('websiteTestBtn'),
    testRecordingBtn: $('testRecordingBtn'),
    emailSendBtn: $('emailSendBtn'),
    emailAiBtn: $('emailAiBtn'),
    profileBtn: $('profileBtn'),
    toast: $('toast')
  };

  // <leads7-speed:begin> 2026-10-09 — Leads7 Speed probe: what THIS agent feels (chat
  // open, send, list refresh, page freezes, ping) and which code froze the page (Long
  // Animation Frames, Chrome/Edge 123+). One beacon a minute to /api/perf/beacon.
  // Read-only observers; any failure is swallowed. Report: /inbox-speed.html (super admins).
  function _l7SpeedClassify(pathname, initiator) {
    if (!pathname || !pathname.startsWith('/api/') || pathname === '/api/perf/beacon' || pathname === '/api/inbox-performance') return null;
    if (/^\/api\/inbox\/chats\/[^/]+\/messages$/.test(pathname)) return 'open';
    if (pathname === '/api/inbox/chats') return 'list';
    if ((pathname === '/api/inbox/send-audio' || pathname === '/api/inbox/send-file') && initiator !== 'beacon') return 'media';
    if (/^\/api\/inbox\/send(?:-[a-z-]+)?$/.test(pathname) && initiator !== 'beacon') return 'send';
    if (pathname === '/api/app-version') return 'ping';
    return 'api';
  }
  (function _l7SpeedProbe() {
    try {
      if (window.__l7Speed || typeof PerformanceObserver !== 'function') return;
      window.__l7Speed = true;
      const buf = { open: [], list: [], send: [], media: [], api: [], ping: [], freeze: [] };
      const push = (k, v) => { const a = buf[k]; if (a && a.length < 60 && v >= 0) a.push(Math.round(v)); };
      let visMs = 0, visSince = document.hidden ? 0 : performance.now();
      new PerformanceObserver(list => {
        for (const e of list.getEntries()) {
          let u; try { u = new URL(e.name, location.href); } catch { continue; }
          if (u.origin !== location.origin) continue;
          const k = _l7SpeedClassify(u.pathname, e.initiatorType);
          if (k && !document.hidden) push(k, e.duration);
        }
      }).observe({ type: 'resource', buffered: false });
      try {
        new PerformanceObserver(list => {
          if (document.hidden) return;
          for (const e of list.getEntries()) push('freeze', e.duration);
        }).observe({ type: 'longtask', buffered: false });
      } catch { /* no Long Tasks API (Safari): freezes stay unmeasured */ }
      const loaf = new Map();
      const loafAdd = (k, ms) => { const a = loaf.get(k) || [0, 0, 0]; a[0]++; a[1] += ms; if (ms > a[2]) a[2] = ms; loaf.set(k, a); };
      try {
        new PerformanceObserver(list => {
          if (document.hidden) return;
          for (const e of list.getEntries()) {
            let inScripts = 0;
            for (const s of e.scripts || []) {
              const d = s.duration || 0;
              inScripts += d;
              if (d < 30) continue;
              const file = String(s.sourceURL || '').split('/').pop().split('?')[0] || String(s.invokerType || '?');
              loafAdd(((s.sourceFunctionName || '(anonymous)') + ' @' + file + ':' + (s.sourceCharPosition ?? '?') + ' <- ' + String(s.invoker || s.invokerType || '')).slice(0, 140), d);
            }
            const render = (e.duration || 0) - inScripts;
            if (render >= 50) loafAdd('render (style/layout/paint)', render);
          }
        }).observe({ type: 'long-animation-frame', buffered: false });
      } catch { /* no LoAF (Safari/Firefox): attribution stays empty */ }
      document.addEventListener('visibilitychange', () => {
        const t = performance.now();
        if (document.hidden) { if (visSince) visMs += t - visSince; visSince = 0; } else visSince = t;
      });
      setInterval(() => {
        try {
          const t = performance.now();
          if (visSince) { visMs += t - visSince; visSince = t; }
          const vis = Math.min(120, Math.round(visMs / 1000)); visMs = 0;
          const any = Object.keys(buf).some(k => buf[k].length);
          const body = { vis, view: String(state.activeView || ''), sock: !!(state.socket && state.socket.connected) };
          for (const k of Object.keys(buf)) { body[k] = buf[k]; buf[k] = []; }
          if (loaf.size) { body.loaf = [...loaf.entries()].sort((a, z) => z[1][1] - a[1][1]).slice(0, 8).map(([k, a]) => ({ k, n: a[0], ms: Math.round(a[1]), max: Math.round(a[2]) })); loaf.clear(); }
          if (!state.token || (!any && !vis)) return;
          api('/api/perf/beacon', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive: true, timeoutMs: 15000 }).catch(() => {});
        } catch { /* never */ }
      }, 60000);
    } catch { /* a probe must never break Leads7 */ }
  })();
  // <leads7-speed:end>

  // ============ Utils ============
  function authHeaders(extra = {}) {
    return { 'x-auth-token': state.token, ...extra };
  }
  // Default watchdog for api() calls. Generous on purpose: api() also wraps
  // WhatsApp send paths, and aborting client-side does NOT stop the server —
  // a false "failed" on a send that actually went out is worse than waiting.
  // Long operations (AI assistant, bulk sends, re-engage waves) override via
  // opts.timeoutMs; 0 disables the watchdog entirely.
  const API_DEFAULT_TIMEOUT_MS = 60000;
  function api(path, opts = {}) {
    opts.headers = authHeaders(opts.headers || {});
    const timeoutMs = opts.timeoutMs === undefined ? API_DEFAULT_TIMEOUT_MS : opts.timeoutMs;
    delete opts.timeoutMs; // not a fetch init key
    // A caller-supplied signal owns the lifecycle — don't stack a watchdog on it.
    if (!timeoutMs || opts.signal) return fetch(path, opts);
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    return fetch(path, { ...opts, signal: ctrl.signal })
      .catch(err => {
        // Surface a watchdog abort as a plain Error so it flows through the
        // exact same rejection path callers already handle for network errors.
        if (ctrl.signal.aborted) throw new Error('Request timed out');
        throw err;
      })
      .finally(() => clearTimeout(timer));
  }
  // Thin watchdog wrapper for raw fetch() calls that must NOT route through
  // api() — uploads (FormData/blobs), auth, document generation. Unlike api()
  // it touches NOTHING about the request (no header/body mutation): it only
  // bolts on an AbortController timeout so a hung request rejects (clearing the
  // spinner) instead of hanging the UI forever. Caller's exact opts pass through.
  function fetchT(path, opts = {}, timeoutMs = 60000) {
    if (!timeoutMs || opts.signal) return fetch(path, opts); // caller owns the signal
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    return fetch(path, { ...opts, signal: ctrl.signal })
      .catch(err => { if (ctrl.signal.aborted) throw new Error('Request timed out'); throw err; })
      .finally(() => clearTimeout(timer));
  }
  // 'EG' → 🇪🇬. Regional-indicator letters, so every country works without a
  // lookup table. Returns '' for anything that is not a 2-letter ISO code.
  function _countryFlag(code) {
    const c = String(code || '').toUpperCase();
    if (!/^[A-Z]{2}$/.test(c)) return '';
    return String.fromCodePoint(...[...c].map(ch => 0x1F1E6 + ch.charCodeAt(0) - 65));
  }
  function toast(msg, type = '') {
    E.toast.textContent = msg;
    E.toast.className = 'toast show ' + type;
    // Errors get extra reading time — agents complained that stack-trace
    // toasts vanished before they could read them. Click anywhere on the
    // toast to dismiss early.
    const dwellMs = type === 'error' ? 6000 : 2400;
    if (E.toast._dismissTimer) clearTimeout(E.toast._dismissTimer);
    E.toast._dismissTimer = setTimeout(() => { E.toast.className = 'toast hidden'; }, dwellMs);
    if (!E.toast._dismissBound) {
      E.toast._dismissBound = true;
      E.toast.addEventListener('click', () => {
        if (E.toast._dismissTimer) clearTimeout(E.toast._dismissTimer);
        E.toast.className = 'toast hidden';
      });
      E.toast.style.pointerEvents = 'auto';
      E.toast.style.cursor = 'pointer';
    }
  }
  // Small profile modules live outside this already-large bundle. Give them
  // the same toast surface instead of creating a second notification style.
  window.__loughaToast = (message, kind) => toast(message, kind || '');
  function avatarColorClass(seed) {
    const s = String(seed || '');
    let h = 0;
    for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
    return 'avatar-c' + (Math.abs(h) % 8 + 1);
  }
  function initials(name) {
    if (!name) return '?';
    return String(name).trim().split(/\s+/).map(w => w[0] || '').join('').toUpperCase().slice(0, 2) || '?';
  }

  // Personal call sounds. Name-specific recordings are listed only for their
  // matching account. Client/team sound controls never block the incoming call.
  function createClientCallRingtones(win) {
    const commonOptions = [
      { id: 'classic', label: 'Classic', group: 'Simple sounds' }, { id: 'chime', label: 'Chime', group: 'Simple sounds' },
      { id: 'double-bell', label: 'Double bell', group: 'Simple sounds' }, { id: 'marimba', label: 'Marimba', group: 'Simple sounds' },
      { id: 'pulse', label: 'Pulse', group: 'Simple sounds' },
      { id: 'chaabi-vibe', label: 'Chaabi vibe', group: 'Music', asset: '/ringtones/chaabi-vibe.mp3' },
      { id: 'happy-beat', label: 'Happy beat', group: 'Music', asset: '/ringtones/happy-beat.mp3' },
    ];
    const voiceNames = { mary: 'Mary', bouchra: 'Bouchra', doha: 'Doha' };
    let options = commonOptions, voiceKey = '', voiceName = '';
    const live = new Set(), listeners = new Set();
    let userKey = '', choice = 'classic', previewPlayer = null, saved = true, audioError = '';
    let scopes = { client: true, team: true };
    const scopeFor = source => source === 'team' ? 'team' : 'client';
    const valid = value => options.some(o => o.id === value);
    const emit = () => { listeners.forEach(fn => { try { fn(); } catch {} }); };
    function pattern(source, mode, sound) {
      // Waiting calls always keep the original quiet beep, even for music/voice.
      if (mode === 'wait') return { every: 3000, notes: [[659.25, 0, .25, .05, .05]] };
      if (sound === 'classic' && source === 'team') return { every: 1500, shape: 'team', notes: [[480, 0, .65, .14, .05]] };
      if (sound === 'classic') return source === '3cx'
        ? { every: 2600, notes: [[523.25, 0, 1.2, .08, .05], [659.25, 0, 1.2, .08, .05]] }
        : { every: 3500, notes: [[440, 0, 1.5, .08, .05], [480, 0, 1.5, .08, .05]] };
      if (sound === 'chime') return { every: 3200, notes: [[523.25, 0, .28, .08, .03], [659.25, .32, .28, .08, .03], [783.99, .64, .4, .08, .03]] };
      if (sound === 'double-bell') return { every: 3000, notes: [[880, 0, .22, .07, .03], [1174.66, 0, .22, .04, .03], [880, .4, .22, .07, .03], [1174.66, .4, .22, .04, .03]] };
      if (sound === 'marimba') return { every: 3000, notes: [[523.25, 0, .18, .08, .01], [783.99, .22, .18, .08, .01], [659.25, .44, .18, .08, .01], [1046.5, .66, .3, .08, .01]] };
      return { every: 2800, notes: [[440, 0, .18, .08, .03], [440, .3, .18, .08, .03], [660, .6, .3, .08, .03]] };
    }
    function makePlayer(source, mode, preview) {
      const selected = options.find(o => o.id === choice), sound = choice;
      const Ctx = win.AudioContext || win.webkitAudioContext;
      let ctx = null, media = null, interval = null, timer = null, stopped = false, fallback = false;
      const player = { scope: scopeFor(source), stop() {
        if (stopped) return;
        stopped = true;
        if (interval != null) win.clearInterval(interval);
        if (timer != null) win.clearTimeout(timer);
        if (media) {
          media.onended = media.onerror = null;
          try { media.pause(); media.removeAttribute('src'); media.load(); } catch {}
        }
        if (ctx) { try { Promise.resolve(ctx.close()).catch(() => {}); } catch {} }
        live.delete(player);
        if (previewPlayer === player) previewPlayer = null;
        emit();
      } };
      function synthPlay() {
        if (stopped || !ctx || ctx.state === 'closed') return;
        const p = pattern(source, mode, fallback ? 'classic' : sound), t = ctx.currentTime;
        for (const [freq, at, duration, peak, fade] of p.notes) {
          const osc = ctx.createOscillator(), gain = ctx.createGain();
          osc.type = 'sine'; osc.frequency.value = freq;
          if (p.shape === 'team') {
            gain.gain.setValueAtTime(.0001, t + at);
            gain.gain.exponentialRampToValueAtTime(peak, t + at + .05);
            gain.gain.exponentialRampToValueAtTime(.0001, t + at + .6);
          } else {
            gain.gain.setValueAtTime(0, t + at);
            gain.gain.linearRampToValueAtTime(peak, t + at + fade);
            gain.gain.setValueAtTime(peak, t + at + duration - fade);
            gain.gain.linearRampToValueAtTime(0, t + at + duration);
          }
          osc.connect(gain).connect(ctx.destination); osc.start(t + at); osc.stop(t + at + duration);
        }
        return p;
      }
      function startSynth() {
        if (stopped || !Ctx) return false;
        try {
          ctx = new Ctx();
          if (ctx.state === 'suspended') { try { Promise.resolve(ctx.resume()).catch(() => {}); } catch {} }
          const p = synthPlay();
          if (preview) timer = win.setTimeout(() => player.stop(), Math.ceil(Math.max(...p.notes.map(n => n[1] + n[2])) * 1000) + 100);
          else interval = win.setInterval(() => { try { synthPlay(); } catch { player.stop(); } }, p.every);
          return true;
        } catch { return false; }
      }
      function assetFailure() {
        if (stopped || fallback) return;
        fallback = true;
        if (media) { media.onended = media.onerror = null; try { media.pause(); media.removeAttribute('src'); media.load(); } catch {} }
        audioError = preview ? 'Could not play this preview. Check your browser audio.' : 'Audio unavailable. Using Classic for this call.';
        if (preview || !startSynth()) player.stop();
        else emit();
      }
      function assetPlay() {
        if (stopped || fallback) return;
        try {
          media.currentTime = 0;
          Promise.resolve(media.play()).then(() => {
            // A pending play() may resolve after answer/logout. Silence again;
            // never let its completion schedule or restart an obsolete ring.
            if (stopped || fallback) { try { media.pause(); } catch {} }
          }).catch(assetFailure);
        } catch { assetFailure(); }
      }
      if (preview) previewPlayer = player; else live.add(player);
      audioError = '';
      try {
        if (mode !== 'wait' && selected?.asset && win.Audio) {
          media = new win.Audio(selected.asset); media.preload = 'auto'; media.volume = .45;
          media.onended = () => {
            if (stopped || fallback) return;
            if (preview) player.stop();
            else timer = win.setTimeout(assetPlay, 800);
          };
          media.onerror = assetFailure;
          assetPlay();
        } else {
          fallback = !!selected?.asset;
          if (!startSynth()) { player.stop(); return null; }
        }
        emit(); return stopped ? null : player;
      } catch { player.stop(); return null; }
    }
    function stopPreview() { if (previewPlayer) previewPlayer.stop(); }
    function stopAll() { stopPreview(); [...live].forEach(p => p.stop()); }
    // Settings in another Leads7 tab apply to the same signed-in account here.
    // This matters when the inbox tab owns the actual incoming-call audio.
    win.addEventListener?.('storage', event => {
      if (!userKey) return;
      if (event.key === userKey) {
        stopPreview(); choice = valid(event.newValue) ? event.newValue : 'classic'; audioError = ''; emit();
      } else if (event.key === userKey + '.scopes') {
        const next = { client: true, team: true };
        try { const value = JSON.parse(event.newValue); for (const key of ['client', 'team']) if (typeof value?.[key] === 'boolean') next[key] = value[key]; } catch { /* reset corrupt or cleared preferences */ }
        scopes = next;
        [...live].filter(player => !scopes[player.scope]).forEach(player => player.stop());
        emit();
      }
    });
    return {
      get options() { return options; },
      getVoiceName: () => voiceName,
      setUser(user) {
        const id = user && (user.id ? 'id:' + user.id : user.username ? 'username:' + String(user.username).toLowerCase() : '');
        const next = id ? 'leads7.clientRingtone.v1.' + encodeURIComponent(id) : '';
        const nextVoice = String(user?.username || '').trim().toLowerCase();
        if (next === userKey && nextVoice === voiceKey) return;
        stopAll(); userKey = next; voiceKey = nextVoice;
        voiceName = Object.prototype.hasOwnProperty.call(voiceNames, voiceKey) ? voiceNames[voiceKey] : '';
        options = [...commonOptions, ...(!voiceName ? [] : ['female-darija', 'female-english', 'male-darija', 'male-english'].map(id => ({
          id: voiceKey + '-' + id, label: voiceName + ' · ' + id.split('-').map(s => s[0].toUpperCase() + s.slice(1)).join(' · '),
          group: 'Voice · says ' + voiceName, asset: '/ringtones/' + voiceKey + '-' + id + '.mp3',
        })))];
        choice = 'classic'; scopes = { client: true, team: true }; saved = true; audioError = '';
        if (userKey) { try {
          const v = win.localStorage.getItem(userKey); if (valid(v)) choice = v;
          const rawScopes = win.localStorage.getItem(userKey + '.scopes');
          if (rawScopes) {
            try { const stored = JSON.parse(rawScopes); for (const key of ['client', 'team']) if (typeof stored?.[key] === 'boolean') scopes[key] = stored[key]; } catch { /* invalid preference keeps default sounds */ }
          }
        } catch { saved = false; } }
        emit();
      },
      getChoice: () => choice, getAudioError: () => audioError,
      setChoice(value) {
        if (!userKey || !valid(value)) return false;
        stopPreview(); choice = value; audioError = '';
        try { win.localStorage.setItem(userKey, value); saved = true; } catch { saved = false; }
        emit(); return saved;
      },
      isSaved: () => saved, isPreviewing: () => !!previewPlayer, isRinging: () => live.size > 0,
      getScopes: () => ({ ...scopes }),
      setScope(scope, enabled) {
        if (!userKey || !['client', 'team'].includes(scope) || typeof enabled !== 'boolean') return false;
        scopes[scope] = enabled;
        if (!enabled) [...live].filter(player => player.scope === scope).forEach(player => player.stop());
        try { win.localStorage.setItem(userKey + '.scopes', JSON.stringify(scopes)); saved = true; } catch { saved = false; }
        emit(); return saved;
      },
      start(source, mode) {
        stopPreview();
        // A truthy, silent handle prevents older ringer hooks from falling back
        // to their legacy sound when this call category is intentionally muted.
        if (!scopes[scopeFor(source)]) return { muted: true, stop() {} };
        return makePlayer(source, mode || 'ring', false);
      },
      preview() { if (live.size || !userKey) return false; stopPreview(); return !!makePlayer('3cx', 'ring', true); },
      stopPreview, stopAll,
      subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    };
  }
  const clientCallRingtones = createClientCallRingtones(window);
  window.__loughaCallRingtone = clientCallRingtones;

  // ─── WhatsApp Calling (WebRTC + Meta /calls signaling) ───────────────
  // Browser drives the RTCPeerConnection — captures mic, generates SDP
  // offer, waits for ICE gathering, ships full SDP to /api/calls/initiate.
  // The server forwards to Meta /v23.0/{PNID}/calls (action=connect),
  // Meta rings the recipient's WhatsApp app. When they accept, Meta posts
  // the SDP answer back through our webhook → server emits `wa_call`
  // with event=connect → handleServerEvent applies the answer and audio
  // flows. Hang-up either side terminates the peer + posts terminate to
  // /api/calls/:callId/terminate.
  const waCall = (function () {
    let pc = null;          // RTCPeerConnection
    let localStream = null; // captured mic track
    let remoteStream = null; // received audio track (from pc.ontrack)
    let callId = null;
    let chatId = null;
    let timerInt = null;
    let timerStartedAt = 0;
    let muted = false;
    // Recording state — MediaRecorder on a mixed mic+remote MediaStream
    // built via Web Audio. We start when connectionState=connected and
    // stop in teardown(), then POST the blob to
    // /api/calls/:callId/recording so the in-chat bubble can play it.
    let recorder = null;
    let recorderChunks = [];
    let recorderCtx = null;

    // Meta's calls endpoint is ice-lite and publishes ONE host candidate —
    // the browser owns ALL connectivity checks, so a working TURN relay is
    // mandatory whenever the agent sits behind symmetric NAT (corporate
    // wifi, home routers with hairpinning, etc). Without TURN the outbound
    // mic RTP is silently dropped while inbound still flows — exactly the
    // "recipient hears silence" symptom we observed.
    //
    // Fetched per-call from /api/calls/turn-credentials so the server can
    // mint short-lived Cloudflare TURN HMAC creds. Falls back to STUN-only
    // if the server isn't configured (recipient may still not hear you on
    // strict NAT — TURN is required for production).
    async function fetchRtcConfig() {
      try {
        const r = await api('/api/calls/turn-credentials');
        if (r.ok) {
          const j = await r.json();
          if (Array.isArray(j.iceServers) && j.iceServers.length) {
            // Only force relay when a REAL turn:/turns: server is present. The
            // server's STUN-only fallback (CF TURN down/misconfigured) also
            // returns a non-empty iceServers array — forcing relay there would
            // leave the ICE agent with zero relay candidates and the call could
            // NEVER connect (dead air / stuck ringing / duration-0). Relay still
            // GUARANTEES bidirectional flow when TURN exists (avoids the
            // symmetric-NAT one-way-audio drop); without TURN we fall back to
            // 'all' so direct/STUN paths can still complete. Mirrors the
            // team-call path's guard.
            const hasTurn = j.iceServers.some(s => {
              const u = s.urls;
              return Array.isArray(u) ? u.some(x => /^turns?:/i.test(x)) : /^turns?:/i.test(u || '');
            });
            if (j.warning) console.warn('[wa-call] TURN degraded — ' + j.warning + ' (falling back to ICE policy "all")');
            return {
              iceServers: j.iceServers,
              iceTransportPolicy: hasTurn ? 'relay' : 'all',
              bundlePolicy: 'max-bundle',
              rtcpMuxPolicy: 'require',
            };
          }
        }
      } catch (_) { /* fall through */ }
      return {
        iceServers: [
          { urls: 'stun:stun.cloudflare.com:3478' },
          { urls: 'stun:stun.l.google.com:19302' },
        ],
        iceTransportPolicy: 'all',
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
      };
    }

    // Surgical SDP rewrite — touches ONLY the audio m-section, never the
    // session level or other m-sections. The previous global regex was a
    // landmine the moment a data channel got bundled (would flip a=inactive
    // on a rejected m-line to a=sendrecv → Meta rejection).
    function rewriteAudioSdp(sdp) {
        const lines = sdp.split(/\r?\n/);
        let inAudio = false;
        const out = [];
        // Build a set of Opus payload-type numbers so we can target their
        // fmtp lines without touching DTMF/CN.
        const opusPts = new Set();
        for (const ln of lines) {
            const m = ln.match(/^a=rtpmap:(\d+)\s+opus/i);
            if (m) opusPts.add(m[1]);
        }
        for (const line of lines) {
            if (line.startsWith('m=')) inAudio = line.startsWith('m=audio');
            if (inAudio) {
                if (/^a=(sendonly|recvonly|inactive)\s*$/.test(line)) { out.push('a=sendrecv'); continue; }
                const fmtp = line.match(/^a=fmtp:(\d+)\s+(.*)$/);
                if (fmtp && opusPts.has(fmtp[1]) && !/useinbandfec/i.test(fmtp[2])) {
                    out.push(`a=fmtp:${fmtp[1]} ${fmtp[2]}; minptime=10; useinbandfec=1; stereo=0; usedtx=0`);
                    continue;
                }
            }
            out.push(line);
        }
        return out.join('\r\n');
    }

    // Diagnostic stats poller — every 2s while the call is up, log
    // packetsSent / packetsReceived on the audio RTP streams. If
    // packetsSent stays at 0, the agent's mic isn't reaching the peer
    // (NAT / firewall / codec issue). If packetsReceived stays at 0,
    // the remote side isn't sending or media isn't reaching us.
    let statsPoller = null;
    let candidateTypeSeen = new Set();
    // Reliable mic-level reader via Web Audio API analyzer. The
    // `media-source.audioLevel` stat is unreliable on some Chrome builds
    // (returns 0 even when the mic IS capturing). The analyzer reads
    // straight from the live track so it always reflects reality —
    // critical for telling "mic is muted" apart from "stats are broken".
    let _micAnalyser = null;
    let _micAnalyserCtx = null;
    let _micAnalyserData = null;
    function attachMicAnalyser(stream) {
      try {
        if (_micAnalyser) detachMicAnalyser();
        _micAnalyserCtx = new (window.AudioContext || window.webkitAudioContext)();
        const src = _micAnalyserCtx.createMediaStreamSource(stream);
        _micAnalyser = _micAnalyserCtx.createAnalyser();
        _micAnalyser.fftSize = 512;
        src.connect(_micAnalyser);
        _micAnalyserData = new Uint8Array(_micAnalyser.fftSize);
      } catch (e) { console.warn('[waCall] AnalyserNode setup failed:', e); }
    }
    function detachMicAnalyser() {
      try { _micAnalyserCtx?.close(); } catch { /* ignore */ }
      _micAnalyser = null;
      _micAnalyserCtx = null;
      _micAnalyserData = null;
    }
    function readMicLevelPct() {
      if (!_micAnalyser || !_micAnalyserData) return null;
      _micAnalyser.getByteTimeDomainData(_micAnalyserData);
      // RMS over the time-domain buffer (Uint8 centered at 128 = silence).
      let sum = 0;
      for (let i = 0; i < _micAnalyserData.length; i++) {
        const v = (_micAnalyserData[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / _micAnalyserData.length);
      // Boost a bit so a normal speaking voice shows around 30-60%.
      return Math.min(100, Math.round(rms * 200));
    }
    function startStatsPoller() {
      if (statsPoller) clearInterval(statsPoller);
      const diag = document.getElementById('wcpDiag');
      if (diag) diag.hidden = false;
      statsPoller = setInterval(async () => {
        if (!pc) return;
        try {
          const stats = await pc.getStats();
          let sent = 0, received = 0, audioLevelOut = null, audioLevelIn = null;
          let activePairLocalType = null, activePairRemoteType = null;
          stats.forEach((r) => {
            if (r.type === 'outbound-rtp' && r.kind === 'audio') sent = r.packetsSent || 0;
            if (r.type === 'inbound-rtp' && r.kind === 'audio') received = r.packetsReceived || 0;
            if (r.type === 'media-source' && r.kind === 'audio') audioLevelOut = r.audioLevel;
            if (r.type === 'remote-inbound-rtp' && r.kind === 'audio') audioLevelIn = r.jitter;
            if (r.type === 'candidate-pair' && r.selected && r.state === 'succeeded') {
              const local = stats.get(r.localCandidateId);
              const remote = stats.get(r.remoteCandidateId);
              if (local) activePairLocalType = local.candidateType;
              if (remote) activePairRemoteType = remote.candidateType;
            }
          });
          console.log('[waCall stats]', { sent, received, audioLevelOut, audioLevelIn, ice: activePairLocalType + '↔' + activePairRemoteType });
          // Prefer the AnalyserNode-based reading — it reads straight from
          // the live mic stream and is reliable across Chrome versions.
          // Fall back to the WebRTC stat only if the analyser hasn't been
          // attached yet (race during the first poll).
          const analyserPct = readMicLevelPct();
          const fmtMic = analyserPct != null ? (analyserPct + '%') :
                         (audioLevelOut != null ? (Math.round(audioLevelOut * 100) + '%') : '—');
          const elMic = document.getElementById('wcpDiagMic');
          const elSent = document.getElementById('wcpDiagSent');
          const elRecv = document.getElementById('wcpDiagRecv');
          const elIce = document.getElementById('wcpDiagIce');
          if (elMic)  elMic.textContent  = fmtMic;
          if (elSent) elSent.textContent = String(sent) + ' pkt';
          if (elRecv) elRecv.textContent = String(received) + ' pkt';
          if (elIce)  elIce.textContent  = activePairLocalType ? (activePairLocalType + '/' + activePairRemoteType) : '—';
          // Red-flag: 5s in, mic shows >0% but sent stays 0 → outbound RTP dropping.
          if (sent === 0 && audioLevelOut > 0.01) {
            if (elSent) elSent.style.color = 'oklch(0.55 0.22 25)';
          } else {
            if (elSent) elSent.style.color = '';
          }
        } catch (e) { /* ignore */ }
      }, 1500);
    }
    function stopStatsPoller() {
      if (statsPoller) { clearInterval(statsPoller); statsPoller = null; }
      const diag = document.getElementById('wcpDiag');
      if (diag) diag.hidden = true;
    }

    function panel() { return document.getElementById('waCallPanel'); }
    function setStatus(state, text) {
      const el = document.getElementById('wcpStatus');
      if (!el) return;
      el.setAttribute('data-state', state);
      el.textContent = text || state;
    }
    function setHeader(name, phone) {
      const a = document.getElementById('wcpAvatar');
      const n = document.getElementById('wcpName');
      const p = document.getElementById('wcpPhone');
      if (a) a.textContent = initials(name);
      if (n) n.textContent = name || '—';
      if (p) p.textContent = phone || '';
    }
    function showPanel() {
      const p = panel();
      if (!p) return;
      p.classList.remove('hidden');
      // Admin / super_sales: drag by the header, spot remembered (see _enableAdminDrag).
      _enableAdminDrag(p, '.wcp-head', 'leads7.waCallPanelPos');
      refreshIcons(p);
    }
    function hidePanel() { panel()?.classList.add('hidden'); }
    function startTimer() {
      timerStartedAt = Date.now();
      const el = document.getElementById('wcpTimer');
      if (timerInt) clearInterval(timerInt);
      timerInt = setInterval(() => {
        const s = Math.floor((Date.now() - timerStartedAt) / 1000);
        const mm = String(Math.floor(s / 60)).padStart(2, '0');
        const ss = String(s % 60).padStart(2, '0');
        if (el) el.textContent = mm + ':' + ss;
      }, 1000);
    }
    function stopTimer() {
      if (timerInt) { clearInterval(timerInt); timerInt = null; }
    }

    async function start() {
      if (!state.selectedChatId) { toast('Open a conversation first', 'error'); return; }
      if (pc) { toast('Call already in progress', 'warning'); return; }
      const chat = state.selectedChat || {};
      chatId = state.selectedChatId;
      setHeader(chat.name || chat.phone || '—', displayPhone(chat.phone || ''));
      setStatus('connecting', 'requesting mic…');
      showPanel();
      // Wire panel buttons (idempotent — listeners re-added on each call).
      const muteBtn = document.getElementById('wcpMuteBtn');
      const endBtn = document.getElementById('wcpEndBtn');
      if (muteBtn && !muteBtn._wired) {
        muteBtn.addEventListener('click', toggleMute);
        muteBtn._wired = true;
      }
      if (endBtn && !endBtn._wired) {
        endBtn.addEventListener('click', () => end({ reason: 'user' }));
        endBtn._wired = true;
      }
      try {
        // Mic permission — browser will prompt if not previously granted.
        // Request echoCancellation + noiseSuppression + autoGainControl so
        // the recipient hears clean audio (Chrome's defaults are on but
        // explicit is safer across browser versions).
        localStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        });
        // Wire the analyser immediately so the diag panel reflects real
        // mic activity from the first stats tick.
        attachMicAnalyser(localStream);
      } catch (err) {
        setStatus('failed', 'mic denied');
        toast('Microphone permission required for calls: ' + (err?.message || err), 'error');
        await teardown();
        return;
      }
      try {
        const rtcCfg = await fetchRtcConfig();
        console.log('[waCall] RTC config:', JSON.stringify({
          iceServers: rtcCfg.iceServers.map(s => ({ urls: s.urls, hasCreds: !!s.username })),
          iceTransportPolicy: rtcCfg.iceTransportPolicy,
        }));
        pc = new RTCPeerConnection(rtcCfg);
        // Add the audio transceiver FIRST with explicit sendrecv direction
        // before binding the mic track. addTrack alone occasionally
        // produced an SDP with a=recvonly under some Chrome versions —
        // recipient would hear silence even though the mic was capturing.
        // Explicit transceiver locks the direction; replaceTrack binds
        // the live mic into the prepared transceiver.
        const audioTrack = localStream.getAudioTracks()[0];
        if (!audioTrack) throw new Error('No audio track captured from getUserMedia');
        audioTrack.enabled = true; // defensive — make sure not muted by default
        const transceiver = pc.addTransceiver('audio', {
          direction: 'sendrecv',
          streams: [localStream],
        });
        await transceiver.sender.replaceTrack(audioTrack);
        // Remote audio — attach the inbound stream to the hidden <audio>
        // element so browser autoplay policy lets us play without a user
        // gesture (the panel button click counts as the gesture).
        pc.ontrack = (ev) => {
          const audio = document.getElementById('wcpRemoteAudio');
          if (audio && ev.streams && ev.streams[0]) {
            audio.srcObject = ev.streams[0];
            audio.play().catch(() => {});
            // Save the remote stream for the recording mixer.
            remoteStream = ev.streams[0];
          }
        };
        pc.onconnectionstatechange = () => {
          const s = pc?.connectionState;
          console.log('[waCall] connectionState=' + s);
          if (s === 'connected') {
            setStatus('connected', 'on call');
            if (!timerInt) startTimer();
            startStatsPoller();
            try { startRecording(); } catch (e) { console.warn('[waCall] startRecording failed:', e); }
          } else if (s === 'failed') {
            setStatus('failed', 'connection lost');
            stopStatsPoller();
            end({ reason: 'rtc_failed', notifyServer: true });
          } else if (s === 'disconnected') {
            setStatus('ended', 'disconnected');
            stopStatsPoller();
          }
        };
        pc.oniceconnectionstatechange = () => {
          console.log('[waCall] iceConnectionState=' + pc?.iceConnectionState);
        };
        pc.onicecandidate = (e) => {
          if (e.candidate) console.log('[waCall] ICE candidate type=' + e.candidate.type + ' protocol=' + e.candidate.protocol);
        };
        // Create offer + wait for ICE gathering complete so we ship a
        // full SDP to Meta. Meta's /calls connect path doesn't support
        // trickle ICE.
        // Prefer Opus first on the audio transceiver — Chrome occasionally
        // lists telephone-event / CN / RED ahead of Opus and Meta's first-
        // match codec selection ends up on something Meta won't actually
        // transmit, producing silent audio.
        try {
          if (typeof RTCRtpSender !== 'undefined' && RTCRtpSender.getCapabilities) {
            const caps = RTCRtpSender.getCapabilities('audio');
            if (caps?.codecs && transceiver.setCodecPreferences) {
              const opus = caps.codecs.filter(c => /opus/i.test(c.mimeType));
              const rest = caps.codecs.filter(c => !/opus/i.test(c.mimeType));
              transceiver.setCodecPreferences([...opus, ...rest]);
            }
          }
        } catch (e) { console.warn('[waCall] setCodecPreferences failed:', e); }
        const offer = await pc.createOffer();
        if (offer.sdp) offer.sdp = rewriteAudioSdp(offer.sdp);
        // Hard guard: refuse to ship if DTLS setup role is wrong. Meta
        // can only answer setup:passive correctly when we offer actpass.
        if (!/a=setup:actpass/.test(offer.sdp || '')) {
          throw new Error('SDP missing a=setup:actpass — Meta will reject DTLS');
        }
        console.log('[waCall] LOCAL OFFER SDP:\n' + offer.sdp);
        await pc.setLocalDescription(offer);
        // Expose for ad-hoc devtools inspection: window.__waCall.pc.getStats()
        window.__waCall = { pc, localStream };
        await waitForIceGatheringComplete(pc);
        setStatus('ringing', 'ringing…');
        const r = await api('/api/calls/initiate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatId,
            sdpOffer: pc.localDescription.sdp,
            socketId: state.socket?.id || null,
          }),
        });
        const j = await r.json();
        if (!r.ok || !j.callId) {
          setStatus('failed', 'init failed');
          // Meta 138006 — recipient hasn't granted calling permission yet.
          // Offer to send the permission-request message in one tap so
          // the agent doesn't have to leave the call panel.
          const errStr = String(j.error || '');
          if (errStr.includes('138006') || /call permission/i.test(errStr)) {
            // Capture chatId BEFORE teardown() — teardown() nulls the module-level
            // `chatId` (see line ~790), so the permission request below would
            // otherwise POST a null chatId and fail with "chatId required".
            const chatIdForPerm = chatId;
            await teardown();
            const ok = confirm('Recipient hasn\'t granted calling permission yet.\n\nSend them a WhatsApp message with an "Allow calls" button so they can grant it?\n\nOnce they tap Allow, your next call will go through.');
            if (ok) {
              if (!chatIdForPerm) {
                toast('Cannot send permission request — open the contact\'s WhatsApp conversation first', 'error');
                return;
              }
              try {
                const pr = await api('/api/calls/request-permission', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ chatId: chatIdForPerm }),
                });
                const pj = await pr.json();
                if (pr.ok) {
                  toast('Permission request sent — wait for the contact to tap Allow', 'success');
                } else {
                  toast('Failed to send permission request: ' + (pj.error || 'unknown'), 'error');
                }
              } catch (e) {
                toast('Network error sending permission request', 'error');
              }
            }
            return;
          }
          toast(errStr || 'Failed to start call', 'error');
          await teardown();
          return;
        }
        callId = j.callId;
      } catch (err) {
        setStatus('failed', 'init failed');
        toast(err?.message || 'Call init failed', 'error');
        await teardown();
      }
    }

    function waitForIceGatheringComplete(peer, timeoutMs = 5000) {
      return new Promise((resolve) => {
        if (peer.iceGatheringState === 'complete') return resolve();
        const onChange = () => {
          if (peer.iceGatheringState === 'complete') {
            peer.removeEventListener('icegatheringstatechange', onChange);
            resolve();
          }
        };
        peer.addEventListener('icegatheringstatechange', onChange);
        // Fallback — if ICE never completes, ship what we have.
        setTimeout(() => {
          peer.removeEventListener('icegatheringstatechange', onChange);
          resolve();
        }, timeoutMs);
      });
    }

    // INBOUND-ANSWER variant. Meta's Cloud API takes a SINGLE SDP answer (no
    // separate ICE-candidate channel → we can't trickle), so the answer must
    // already carry a usable candidate. But waiting for FULL ICE gathering
    // (waitForIceGatheringComplete, up to 5s) made the answer reach Meta AFTER
    // its short inbound ring window → inbound calls failed: "missed" / "answered
    // dur=0" / no audio (while outbound, where we're the offerer, worked fine).
    // Fix: resolve as soon as we have a RELAY (TURN) candidate — enough to carry
    // media through symmetric NAT — then ship immediately. Falls back to
    // gathering-complete or a 2.5s hard cap, so it's never worse than before.
    function waitForRelayCandidate(peer, timeoutMs = 2500) {
      return new Promise((resolve) => {
        if (peer.iceGatheringState === 'complete') return resolve();
        let done = false, graceTimer = null;
        const finish = () => {
          if (done) return; done = true;
          peer.removeEventListener('icecandidate', onCand);
          peer.removeEventListener('icegatheringstatechange', onState);
          clearTimeout(hardTimer); if (graceTimer) clearTimeout(graceTimer);
          resolve();
        };
        const onCand = (e) => {
          // The candidate line carries "typ relay" for TURN-relayed candidates.
          if (e.candidate && / typ relay/.test(e.candidate.candidate || '')) {
            // Give the browser a beat to serialize the candidate into
            // localDescription.sdp, then ship — don't wait for slow host/srflx.
            if (!graceTimer) graceTimer = setTimeout(finish, 300);
          }
        };
        const onState = () => { if (peer.iceGatheringState === 'complete') finish(); };
        peer.addEventListener('icecandidate', onCand);
        peer.addEventListener('icegatheringstatechange', onState);
        const hardTimer = setTimeout(finish, timeoutMs);
      });
    }

    async function handleServerEvent(ev) {
      if (!ev) return;
      // First — handle the case where the CALLER hangs up before the
      // agent accepted. The incoming banner is still up; the post-accept
      // panel hasn't mounted yet. The early `callId` guard below would
      // skip us into the active-call branch, but `end()` only tears
      // down the active panel — the banner would stay stuck. So
      // intercept terminate-while-ringing here.
      if (ev.event === 'terminate' && ev.callId && waWaiting.has(ev.callId)) {
        _removeWaiting(ev.callId);
        return;
      }
      if (ev.event === 'terminate' && incomingData?.callId === ev.callId) {
        console.log('[waCall:in] caller hung up before accept — dismissing banner');
        hideIncomingBanner();
        _promoteWaiting();
        return;
      }
      // Only act on events for OUR active call; ignore events for other
      // tabs / other agents.
      if (callId && ev.callId && ev.callId !== callId) return;
      if (ev.event === 'connect' && ev.session?.sdp && pc) {
        try {
          console.log('[waCall] REMOTE ANSWER SDP:\n' + ev.session.sdp);
          // Meta MUST answer setup:passive (we offered actpass). If Meta
          // answers actpass/active, DTLS will half-handshake — outbound
          // SRTP uses a key Meta cannot decrypt → one-way audio.
          if (!/a=setup:passive/.test(ev.session.sdp)) {
            console.warn('[waCall] Meta answer missing setup:passive — likely cause of one-way audio');
            toast('DTLS role mismatch — audio may be one-way. See console.', 'warning');
          }
          if (!/a=ice-lite/.test(ev.session.sdp)) {
            console.warn('[waCall] Meta answer missing a=ice-lite — unexpected');
          }
          await pc.setRemoteDescription({ type: 'answer', sdp: ev.session.sdp });
          setStatus('connecting', 'handshaking…');
        } catch (err) {
          setStatus('failed', 'sdp failed');
          toast('Failed to apply remote SDP: ' + (err?.message || err), 'error');
          end({ reason: 'sdp_apply_failed', notifyServer: true });
        }
      } else if (ev.event === 'terminate') {
        setStatus('ended', 'ended');
        end({ reason: 'remote_terminated', notifyServer: false });
      } else if (ev.event === 'call_permission_update') {
        if (ev.permission?.status === 'rejected') {
          setStatus('failed', 'permission denied');
          toast('Recipient denied call permission', 'warning');
          end({ reason: 'permission_denied', notifyServer: true });
        }
      } else if (ev.error) {
        setStatus('failed', ev.error.title || 'error');
        toast('Call error: ' + (ev.error.message || ev.error.title), 'error');
        end({ reason: 'meta_error', notifyServer: true });
      }
    }

    function toggleMute() {
      if (!localStream) return;
      muted = !muted;
      for (const track of localStream.getAudioTracks()) track.enabled = !muted;
      const btn = document.getElementById('wcpMuteBtn');
      if (btn) {
        btn.classList.toggle('is-muted', muted);
        btn.title = muted ? 'Unmute mic' : 'Mute mic';
      }
    }

    async function end({ reason, notifyServer = true } = {}) {
      if (notifyServer && callId) {
        try {
          await api('/api/calls/' + encodeURIComponent(callId) + '/terminate', { method: 'POST' });
        } catch { /* terminate is best-effort */ }
      }
      await teardown();
      // Keep the panel visible for 2s with "ended" status so the agent
      // sees the final state before it disappears.
      setStatus('ended', reason === 'user' ? 'ended' : (reason || 'ended'));
      setTimeout(() => hidePanel(), 2000);
      // A ring that waited in the stack during the call takes the banner.
      _promoteWaiting();
      _renderWaStack();
    }

    function startRecording() {
      if (recorder || !localStream) return;
      try {
        recorderCtx = new (window.AudioContext || window.webkitAudioContext)();
        const dest = recorderCtx.createMediaStreamDestination();
        // Mic in
        try {
          const micSrc = recorderCtx.createMediaStreamSource(localStream);
          micSrc.connect(dest);
        } catch (e) { console.warn('[waCall] recorder: mic connect failed:', e); }
        // Remote in (may not be ready on first attempt — retry once)
        const attachRemote = () => {
          if (!remoteStream) return false;
          try {
            const remSrc = recorderCtx.createMediaStreamSource(remoteStream);
            remSrc.connect(dest);
            return true;
          } catch (e) { console.warn('[waCall] recorder: remote connect failed:', e); return false; }
        };
        if (!attachRemote()) setTimeout(attachRemote, 800);
        // Pick the first MIME type the browser actually supports.
        const candidates = [
          'audio/webm;codecs=opus',
          'audio/webm',
          'audio/ogg;codecs=opus',
        ];
        const mime = candidates.find(m => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m));
        recorder = new MediaRecorder(dest.stream, mime ? { mimeType: mime, audioBitsPerSecond: 32000 } : undefined);
        recorderChunks = [];
        recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) recorderChunks.push(e.data); };
        recorder.start(1000); // 1s timeslice keeps blobs partial-flushable
        console.log('[waCall] recorder started (' + (mime || 'default') + ')');
      } catch (e) {
        console.warn('[waCall] recorder init failed:', e);
        recorder = null;
      }
    }

    async function stopRecordingAndUpload(snapshot) {
      if (!recorder) return;
      const rec = recorder;
      recorder = null; // prevent double-stop
      try {
        // Wait for the final blob.
        const blob = await new Promise((resolve) => {
          rec.onstop = () => {
            const mime = rec.mimeType || 'audio/webm';
            resolve(new Blob(recorderChunks, { type: mime }));
          };
          try { rec.stop(); } catch (_) { resolve(new Blob(recorderChunks, { type: 'audio/webm' })); }
        });
        recorderChunks = [];
        try { recorderCtx?.close(); } catch { /* */ }
        recorderCtx = null;
        if (!blob || blob.size < 1024) {
          console.warn('[waCall] recording blob too small to upload (' + (blob?.size || 0) + ' bytes)');
          return;
        }
        const fd = new FormData();
        fd.append('recording', blob, snapshot.callId + '.webm');
        fd.append('chatId', snapshot.chatId);
        fd.append('durationSec', String(snapshot.durationSec || 0));
        fd.append('direction', snapshot.direction || 'outbound');
        const r = await fetchT('/api/calls/' + encodeURIComponent(snapshot.callId) + '/recording', {
          method: 'POST',
          headers: authHeaders(),
          body: fd,
        }, 120000);
        const j = await r.json().catch(() => ({}));
        if (!r.ok) {
          console.warn('[waCall] recording upload failed:', r.status, j);
          return;
        }
        // Inject the call bubble into the chat thread if the agent is
        // still looking at this conversation. chat_event broadcast also
        // picks this up across other open tabs.
        if (j.message && state.selectedChatId === snapshot.chatId) {
          state.messages = state.messages || [];
          const idx = state.messages.findIndex(m => m.id === j.message.id);
          if (idx >= 0) state.messages[idx] = j.message; else state.messages.push(j.message);
          try { renderMessages(); } catch { /* */ }
        }
      } catch (err) {
        console.warn('[waCall] stopRecordingAndUpload error:', err?.message || err);
      }
    }

    async function teardown() {
      // Snapshot state BEFORE we null it out so the recording upload
      // still knows which call+chat it belonged to.
      const snapshot = {
        callId,
        chatId,
        direction: 'outbound', // inbound not yet wired
        durationSec: timerStartedAt ? Math.floor((Date.now() - timerStartedAt) / 1000) : 0,
      };
      if (recorder) {
        // Fire-and-forget — don't block teardown on the upload.
        stopRecordingAndUpload(snapshot).catch(e => console.warn('[waCall] upload error:', e));
      }
      stopTimer();
      stopStatsPoller();
      detachMicAnalyser();
      try { if (pc) pc.close(); } catch { /* ignore */ }
      pc = null;
      if (localStream) {
        for (const track of localStream.getTracks()) track.stop();
        localStream = null;
      }
      remoteStream = null;
      callId = null;
      chatId = null;
      muted = false;
      const audio = document.getElementById('wcpRemoteAudio');
      if (audio) { try { audio.srcObject = null; } catch {} }
      const btn = document.getElementById('wcpMuteBtn');
      if (btn) btn.classList.remove('is-muted');
    }

    // ─── Inbound (incoming) call ────────────────────────────────────
    // Meta delivers the offer SDP via webhook → server emits
    // wa_call_incoming → we render the ringer banner.
    let incomingData = null;
    let incomingAutoDismissTimer = null;
    // WhatsApp rings that could not take the banner — it already shows another
    // ring, or this agent is on a WhatsApp call — are no longer dropped: each
    // gets a small card in #lg-call-stack (shared with softphone.js's 3CX
    // calls) until it is answered, taken, hung up or 60 s pass (2026-09-27).
    const waWaiting = new Map(); // callId → { ev, timer }
    let waStackTick = null;
    function _waStackHost() {
      let host = document.getElementById('lg-call-stack');
      if (!host) {
        host = el('div', { id: 'lg-call-stack', role: 'region', 'aria-label': 'Autres appels' });
        document.body.appendChild(host);
      }
      return host;
    }
    function _waMmss(ms) {
      const t = Math.max(0, Math.floor(ms / 1000)), m = Math.floor(t / 60);
      return String(m).padStart(2, '0') + ':' + String(t % 60).padStart(2, '0');
    }
    function _waCallerName(ev) {
      const chat = (state.chats || []).find(c => c.id === ev.chatId);
      return ev.displayName || chat?.name || displayPhone(ev.from) || 'Unknown';
    }
    function _renderWaStack() {
      const existing = document.getElementById('lg-call-stack');
      if (!waWaiting.size && !existing) return;
      const host = _waStackHost();
      const seen = new Set();
      host.querySelectorAll('.lg-cs-card[data-src="wa"]').forEach(n => { seen.add(n.dataset.callId); n.remove(); });
      for (const [cid, w] of waWaiting) {
        const ev = w.ev;
        const chat = (state.chats || []).find(c => c.id === ev.chatId);
        const name = _waCallerName(ev);
        // Same facts as the 3CX call card (_cxCallMeta): agent, stage, ticket,
        // created date — the ring's own agent/stage win over the loaded chat.
        const base = chat ? _cxCallMeta(chat, name) : null;
        const stage = ev.pipelineStage || chat?.pipelineStage || null;
        let agentName = ev.agentName || '';
        if (!agentName && ev.agentId) agentName = (state.teamUsers || []).find(u => u.username === ev.agentId)?.displayName || ev.agentId;
        if (!agentName && base) agentName = base.agentName;
        // Tickets: one lookup per ring, cached on the entry; repaint when it answers.
        let tkTxt = '', tkCls = '';
        if (w.tk) { tkTxt = _callTicketText(w.tk.d); if (w.tk.d?.ticketsOpen > 0) tkCls = 'open'; }
        else if (ev.chatId && typeof window.__sfpDetails === 'function') {
          tkTxt = '…'; tkCls = 'muted';
          if (!w.tkP) {
            w.tkP = Promise.resolve(window.__sfpDetails({ chatId: ev.chatId })).catch(() => null)
              .then(d => { w.tk = { d }; if (waWaiting.get(cid) === w) _renderWaStack(); });
          }
        }
        const cell = (k, v, cls) => el('div', { class: 'lg-cs-cell' }, [
          el('span', { class: 'lg-cs-k', text: k }),
          el('span', { class: 'lg-cs-v' + (v ? '' : ' muted') + (cls ? ' ' + cls : ''), text: v || '—', title: v || null }),
        ]);
        // Admins: the Agent tile is the «Réassigner» control, same picker and
        // same server path as the banner's (the ring moves to the new owner).
        let agentCell = cell('Agent', agentName);
        if (_bannerCanReassign(ev)) {
          agentCell = el('button', {
            type: 'button', class: 'lg-cs-cell lg-cs-reassign', 'aria-haspopup': 'menu',
            title: 'Réassigner l\'appel à un autre agent',
          }, [
            el('span', { class: 'lg-cs-k' }, ['Agent', el('span', { class: 'lg-cs-re-hint', text: 'Réassigner ▾' })]),
            el('span', { class: 'lg-cs-v' + (agentName ? '' : ' muted'), text: agentName || '—' }),
          ]);
          agentCell.addEventListener('click', (e) => {
            e.stopPropagation();
            if (_reassignAnchor === agentCell) { _closeBannerReassignPicker(); return; }
            _openCallReassignPicker(agentCell, ev, (u, out) => {
              ev.agentId = u.username;
              ev.agentName = (out.assignment && out.assignment.displayName) || userLabel(u.username, u.displayName || u.username);
              _renderWaStack();
            });
          });
        }
        const btn = (label, cls, title, fn) => {
          const b = el('button', { type: 'button', class: 'lg-cs-b ' + cls, text: label, title: title || '' });
          b.addEventListener('click', (e) => { e.stopPropagation(); fn(); });
          return b;
        };
        const card = el('div', {
          class: 'lg-cs-card' + (seen.has(String(cid)) ? '' : ' lg-cs-new'), 'data-src': 'wa', 'data-state': 'ringing', 'data-call-id': String(cid),
          title: 'Ouvrir la conversation',
        }, [
          el('div', { class: 'lg-cs-head' }, [
            el('div', { class: 'lg-cs-av', text: initials(name) }),
            el('div', { class: 'lg-cs-id' }, [
              el('div', { class: 'lg-cs-name', text: name }),
              el('div', { class: 'lg-cs-dir', title: ev.lineNumber ? 'Numéro appelé : ' + _ourLineText('WhatsApp', ev.lineNumber) : null }, [
                'Sur ', el('b', { class: 'sfp-line', text: _ourLineText('WhatsApp', ev.lineNumber) }),
              ]),
            ]),
            el('span', { class: 'lg-cs-dot' }),
          ]),
          el('div', { class: 'lg-cs-info' }, [
            agentCell,
            cell('Stage', stage ? (PIPELINE_LABELS[stage] || stage) : ''),
            cell('Ticket', tkTxt, tkCls),
            cell('Créé le', base?.createdLabel || ''),
          ]),
          el('div', { class: 'lg-cs-status' }, [
            el('span', { class: 'lg-cs-label', text: 'Appel entrant…' }),
            el('span', { class: 'lg-cs-timer', 'data-since': String(w.since) }),
          ]),
          el('div', { class: 'lg-cs-acts' }, [
            btn('Répondre', 'ans', pc ? 'L\'appel WhatsApp en cours sera raccroché' : '', () => answerWaiting(cid)),
            btn('Ignorer', 'ghost', 'Masquer ici — l\'appel continue de sonner pour l\'équipe', () => _removeWaiting(cid)),
          ]),
        ]);
        if (stage) card.setAttribute('data-stage', stage);
        card.style.cursor = 'pointer';
        card.addEventListener('click', () => {
          const phone = ev.from || String(ev.chatId || '').split('@')[0];
          openInboxForPhone(phone, name).catch(() => toast('Could not open conversation', 'error'));
        });
        host.appendChild(card);
      }
      _tickWaStack();
      if (waWaiting.size && !waStackTick) waStackTick = setInterval(_tickWaStack, 1000);
      if (!waWaiting.size && waStackTick) { clearInterval(waStackTick); waStackTick = null; }
    }
    function _tickWaStack() {
      document.querySelectorAll('#lg-call-stack .lg-cs-card[data-src="wa"] .lg-cs-timer').forEach(n => {
        n.textContent = _waMmss(Date.now() - (Number(n.dataset.since) || Date.now()));
      });
    }
    function _queueWaiting(ev) {
      if (!ev?.callId) return;
      const prev = waWaiting.get(ev.callId);
      if (prev?.timer) clearTimeout(prev.timer);
      const timer = setTimeout(() => _removeWaiting(ev.callId), 60000);
      // Update in place so the ticket lookup cached on the entry survives.
      if (prev) { Object.assign(prev.ev, ev); prev.timer = timer; }
      else waWaiting.set(ev.callId, { ev, since: Date.now(), timer });
      _renderWaStack();
      if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try { new Notification('Incoming WhatsApp Call', { body: _waCallerName(ev), tag: 'wa-call-' + ev.callId }); } catch (e) { /* ignore */ }
      }
    }
    function _removeWaiting(cid) {
      const w = waWaiting.get(cid);
      if (!w) return;
      if (w.timer) clearTimeout(w.timer);
      waWaiting.delete(cid);
      _renderWaStack();
    }
    // The banner is free again (declined / taken / hung up / call ended) →
    // the oldest waiting ring takes it.
    function _promoteWaiting() {
      if (pc || incomingData || !waWaiting.size) return;
      const [cid, w] = waWaiting.entries().next().value;
      _removeWaiting(cid);
      handleIncoming(w.ev);
    }
    // Answer a ring from the stack. One WhatsApp call per browser: the call in
    // progress is hung up first, and a ring on the banner goes to the stack.
    async function answerWaiting(cid) {
      const w = waWaiting.get(cid);
      if (!w) return;
      _removeWaiting(cid);
      if (pc) await end({ reason: 'user' });
      if (incomingData && incomingData.callId !== cid) {
        const cur = incomingData;
        hideIncomingBanner();
        _queueWaiting(cur);
      }
      handleIncoming(w.ev);
      if (incomingData?.callId === cid) acceptIncoming();
    }
    // Web Audio ringtone — synthesized dual-tone (440+480 Hz, NA-style
    // 1.5s on / 2s off) so we don't have to ship/host an mp3. Browser
    // autoplay rules typically allow AudioContext after the user has
    // clicked anywhere on the page (login button counts), which is the
    // default state by the time an inbound call arrives.
    let _ringCtx = null;
    let _ringInterval = null;
    let _ringPlayer = null;
    function startRingtone() {
      stopRingtone();
      try { _ringPlayer = window.__loughaCallRingtone?.start('whatsapp', 'ring'); if (_ringPlayer) return; } catch { /* legacy sound below */ }
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        _ringCtx = new Ctx();
        // If the tab had no recent gesture the context starts suspended and the
        // ring is silent (a cause of "missed" inbound calls). Best-effort resume.
        if (_ringCtx.state === 'suspended') { try { _ringCtx.resume(); } catch {} }
        const playOneRing = () => {
          if (!_ringCtx || _ringCtx.state === 'closed') return;
          const t0 = _ringCtx.currentTime;
          for (const freq of [440, 480]) {
            const osc = _ringCtx.createOscillator();
            const gain = _ringCtx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            // Soft fade-in / fade-out so it doesn't click on start/stop.
            gain.gain.setValueAtTime(0, t0);
            gain.gain.linearRampToValueAtTime(0.08, t0 + 0.05);
            gain.gain.setValueAtTime(0.08, t0 + 1.45);
            gain.gain.linearRampToValueAtTime(0, t0 + 1.5);
            osc.connect(gain).connect(_ringCtx.destination);
            osc.start(t0);
            osc.stop(t0 + 1.5);
          }
        };
        playOneRing();
        _ringInterval = setInterval(playOneRing, 3500); // 1.5s tone + 2s silence
      } catch (e) {
        console.warn('[waCall] ringtone start failed:', e);
      }
    }
    function stopRingtone() {
      if (_ringPlayer) { _ringPlayer.stop(); _ringPlayer = null; }
      if (_ringInterval) { clearInterval(_ringInterval); _ringInterval = null; }
      if (_ringCtx) {
        try { _ringCtx.close(); } catch {}
        _ringCtx = null;
      }
    }
    // Admin / super_sales only: the floating call surfaces (incoming banner +
    // active-call panel) can be dragged anywhere and the spot is remembered per
    // browser (localStorage). Agents keep the fixed default placement.
    // Delegates to the shared _makeDraggable helper (outer scope, hoisted);
    // the extra work here is the banner's centred `translateX(-50%)`: the
    // helper pins inline left/top from the bounding rect, so the transform
    // must be switched off (`.is-positioned`) at the SAME moment, or the card
    // jumps half its width on the first drag.
    const _DRAG_MOBILE_QUERY = '(max-width: 640px)';
    function _readStoredPanelPos(key) {
      try {
        const s = JSON.parse(localStorage.getItem(key) || 'null');
        return (s && typeof s.left === 'number' && typeof s.top === 'number') ? s : null;
      } catch { return null; }
    }
    function _pinPanelAtCurrentRect(panelEl) {
      const r = panelEl.getBoundingClientRect();
      panelEl.style.left = r.left + 'px'; panelEl.style.top = r.top + 'px';
      panelEl.style.right = 'auto'; panelEl.style.bottom = 'auto';
      panelEl.classList.add('is-positioned');
    }
    function _enableAdminDrag(panelEl, handleSel, storageKey) {
      if (!panelEl || typeof _isSalesAdmin !== 'function' || !_isSalesAdmin()) return false;
      if (typeof _makeDraggable !== 'function') return false;
      panelEl.classList.add('is-draggable');
      if (!panelEl._adminDragWired) {
        panelEl._adminDragWired = true;
        const onDown = (e) => {
          if (window.matchMedia?.(_DRAG_MOBILE_QUERY).matches) return;
          const h = e.target?.closest?.(handleSel);
          if (!h || !panelEl.contains(h) || e.target.closest('button')) return;
          if (!panelEl.classList.contains('is-positioned')) _pinPanelAtCurrentRect(panelEl);
          const p = e.touches ? e.touches[0] : e;
          const start = { x: p.clientX, y: p.clientY };
          const onUp = (ue) => {
            document.removeEventListener('mouseup', onUp); document.removeEventListener('touchend', onUp);
            const q = (ue.changedTouches && ue.changedTouches[0]) || ue;
            const moved = Math.abs((q.clientX ?? start.x) - start.x) + Math.abs((q.clientY ?? start.y) - start.y);
            // A real drag must never count as the banner's open-conversation click.
            if (moved > 3) panelEl._suppressClickUntil = Date.now() + 400;
          };
          document.addEventListener('mouseup', onUp); document.addEventListener('touchend', onUp);
        };
        // Capture phase → runs before _makeDraggable's own mousedown reads the rect.
        panelEl.addEventListener('mousedown', onDown, true);
        panelEl.addEventListener('touchstart', onDown, { capture: true, passive: true });
        _makeDraggable(panelEl, handleSel, storageKey);
      }
      // Every show: re-apply the remembered spot (default placement on a small
      // screen) and keep the card inside the current viewport.
      const stored = window.matchMedia?.(_DRAG_MOBILE_QUERY).matches ? null : _readStoredPanelPos(storageKey);
      if (stored) {
        panelEl.style.left = stored.left + 'px'; panelEl.style.top = stored.top + 'px';
        panelEl.style.right = 'auto'; panelEl.style.bottom = 'auto';
        panelEl.classList.add('is-positioned');
      } else {
        panelEl.classList.remove('is-positioned');
        panelEl.style.removeProperty('left'); panelEl.style.removeProperty('top');
        panelEl.style.removeProperty('right'); panelEl.style.removeProperty('bottom');
      }
      requestAnimationFrame(() => { try { panelEl._clampFixedPanel?.(); } catch { /* ignore */ } });
      return true;
    }
    // ── Live reassign while ringing (admin / super_sales) ──────────────────
    // The ringing card gets a "Réassigner" control next to the 👤 pill: pick
    // a teammate → the ONE client assign path runs (/api/inbox/assign:
    // ownership + HubSpot owner sync + audit trail) and the server moves the
    // ring to the new owner in real time (wa_call_incoming at their screen,
    // wa_call_taken {action:'rerouted'} at the old one). WhatsApp chats only —
    // assignment of a social thread never touches call routing.
    // Who can take a call right now, by user id (GET /api/team/availability,
    // 2026-09-28). Best effort: slow (>1.5 s) or failed → null, and the
    // picker opens as before, everyone pickable.
    async function teamAvailability(callerDigits) {
      try {
        const d = String(callerDigits || '').replace(/\D/g, '');
        const r = await Promise.race([
          api('/api/team/availability' + (d ? '?phone=' + encodeURIComponent(d) : '')),
          new Promise(res => setTimeout(() => res(null), 1500)),
        ]);
        if (!r || !r.ok) return null;
        const j = await r.json();
        return (j && j.users && typeof j.users === 'object') ? j.users : null;
      } catch { return null; }
    }
    function _bannerCanReassign(ev) {
      return !!(ev && /@s\.whatsapp\.net$/.test(String(ev.chatId || '')))
        && typeof _isSalesAdmin === 'function' && _isSalesAdmin();
    }
    function _syncBannerReassignBtn(ev) {
      const btn = document.getElementById('wibReassignBtn');
      if (!btn) return;
      if (!_bannerCanReassign(ev)) { btn.classList.add('hidden'); _closeBannerReassignPicker(); return; }
      btn.classList.remove('hidden');
      if (!btn._wired) {
        btn.addEventListener('click', (event) => {
          event.stopPropagation();
          if (document.getElementById('wibReassignMenu')) _closeBannerReassignPicker();
          else _openBannerReassignPicker(btn);
        });
        btn._wired = true;
      }
    }
    // The control the open picker hangs off — the banner's button or a stack
    // card's Agent tile (one picker at a time for both).
    let _reassignAnchor = null;
    // Bumped on every close: an open still waiting on the availability lookup
    // gives up (another ring may own the banner by then).
    let _reassignSeq = 0;
    function _closeBannerReassignPicker() {
      _reassignSeq++;
      const menu = document.getElementById('wibReassignMenu');
      document.removeEventListener('mousedown', _onBannerReassignOutside, true);
      document.removeEventListener('keydown', _onBannerReassignKey, true);
      _reassignAnchor = null;
      if (menu) menu.remove();
    }
    function _onBannerReassignOutside(e) {
      const menu = document.getElementById('wibReassignMenu');
      if (!menu) return;
      if (menu.contains(e.target) || (_reassignAnchor && _reassignAnchor.contains(e.target))) return;
      _closeBannerReassignPicker();
    }
    function _onBannerReassignKey(e) {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      _closeBannerReassignPicker();
    }
    function _openBannerReassignPicker(anchorBtn) {
      const ev = incomingData;
      _openCallReassignPicker(anchorBtn, ev, (u, out) => {
        // Same ring still on screen → the pill flips to the new owner now;
        // the server's wa_call_incoming_enriched confirms it for every admin.
        if (incomingData && incomingData.chatId === ev.chatId) {
          incomingData.agentId = u.username;
          incomingData.agentName = (out.assignment && out.assignment.displayName) || userLabel(u.username, u.displayName || u.username);
          _setBannerMeta(incomingData, incomingData.pipelineStage || null);
        }
      });
    }
    // Picker for one ringing WhatsApp call (the banner's or a stack card's):
    // pick a teammate → /api/inbox/assign, and the server moves the ring.
    async function _openCallReassignPicker(anchorBtn, ev, onPicked) {
      _closeBannerReassignPicker();
      if (!_bannerCanReassign(ev) || !anchorBtn) return;
      const seq = ++_reassignSeq;
      const chatIdForCall = ev.chatId;
      // The ring is routed by the CHAT ASSIGNMENT, not by leads.agent_id (the
      // pill's source) — exclude the real assignee, matched the way the inbox
      // matches ownership (phone-variant aware). Unknown → exclude nobody.
      let assignedUserId = null;
      try {
        for (const [key, a] of Object.entries(state.assignments || {})) {
          if (_sameOwnershipChat(key, chatIdForCall)) { assignedUserId = a?.userId || null; break; }
        }
      } catch { /* no exclusion */ }
      // Mirrors the server's SALES_CALL_ROLES; an agent without a HubSpot
      // owner mapping is refused by /api/inbox/assign (409), so not offered.
      const CALL_ROLES = new Set(['agent', 'admin', 'super_admin', 'super_sales']);
      const users = (state.assignableUsers || [])
        .filter(u => u && u.id && CALL_ROLES.has(String(u.role || '').toLowerCase()))
        .filter(u => u.hubspotOwnerId)
        .filter(u => !assignedUserId || u.id !== assignedUserId);
      const avail = await teamAvailability(String(chatIdForCall).split('@')[0]);
      // A second click, another ring, or the card gone while we asked → drop.
      if (seq !== _reassignSeq || !anchorBtn.isConnected || !_bannerCanReassign(ev)) return;
      const withAvail = avail ? users.map(u => ({ ...u, availability: avail[u.id] || null })) : users;
      _openReassignMenu(anchorBtn, withAvail, async (u) => {
        const out = await assignChatTo(u.id, chatIdForCall);
        if (out) onPicked(u, out);
      });
    }
    // The teammate list (one open at a time), anchored under `anchorBtn`.
    // users = [{ id, username, displayName }]; onPick(u) runs, then it closes.
    // Availability order (lib/team/availability.js ORDER).
    const _AVAIL_RANK = { available: 0, on_call: 1, pause: 2, off: 3, offline: 4 };
    function _openReassignMenu(anchorBtn, users, onPick) {
      _closeBannerReassignPicker();
      if (!anchorBtn) return;
      _reassignAnchor = anchorBtn;
      users = users.slice().sort((a, b) => (userLabel(a.username, a.displayName || a.username) || '').localeCompare(userLabel(b.username, b.displayName || b.username) || ''));
      // u.availability = { state, label, pickable } (Ayoub, 2026-09-28): who
      // can take the call now comes first and is the only one you can pick;
      // the rest stay listed, greyed, with the reason. No availability (the
      // lookup failed) → everyone pickable, as before.
      const hasAvail = users.some(u => u && u.availability);
      if (hasAvail) users.sort((a, b) => (_AVAIL_RANK[a.availability?.state] ?? 9) - (_AVAIL_RANK[b.availability?.state] ?? 9));
      const unavailable = (u) => !!(u.availability && u.availability.pickable === false);
      const menu = el('div', { id: 'wibReassignMenu', class: 'wib-reassign-menu', role: 'menu' });
      menu.addEventListener('click', (e) => e.stopPropagation());
      menu.appendChild(el('div', { class: 'assign-dropdown-head', text: 'RÉASSIGNER L\'APPEL À' }));
      const list = el('div', { class: 'assign-dropdown-list' });
      if (!users.length) list.appendChild(el('div', { class: 'wib-reassign-empty', text: 'Aucun autre agent disponible' }));
      else if (hasAvail && users.every(unavailable)) list.appendChild(el('div', { class: 'wib-reassign-empty', text: 'Personne n\'est disponible pour le moment' }));
      let dividerShown = false;
      for (const u of users) {
        const av = u.availability || null;
        const off = unavailable(u);
        if (off && !dividerShown && users.some(x => !unavailable(x))) {
          list.appendChild(el('div', { class: 'wib-reassign-divider', text: 'Indisponibles' }));
        }
        if (off) dividerShown = true;
        const name = el('div', { class: 'ad-name', text: userLabel(u.username, u.displayName || u.username) });
        const row = el('div', {
          class: 'assign-dropdown-row' + (off ? ' is-unavailable' : ''),
          role: 'menuitem', tabindex: off ? '-1' : '0', 'aria-disabled': off ? 'true' : null,
          title: off ? av.label : null,
        }, [
          el('div', { class: 'ad-avatar ' + colorClassForName(u.username), text: (userLabel(u.username, u.displayName || u.username) || '?').charAt(0) }),
          av ? el('div', { class: 'ad-main' }, [name, el('div', { class: 'ad-avail', 'data-state': av.state, text: av.label })]) : name,
        ]);
        if (off) { list.appendChild(row); continue; }
        // Close at once: the card itself shows what happens next (the new
        // owner, or «Transfert vers X…»), and a second pick can't slip in.
        const pick = () => {
          if (row._busy) return;
          row._busy = true;
          _closeBannerReassignPicker();
          Promise.resolve().then(() => onPick(u)).catch(() => {});
        };
        row.addEventListener('click', pick);
        row.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } });
        list.appendChild(row);
      }
      menu.appendChild(list);
      document.body.appendChild(menu);
      // Anchor under the control; flip above / pull left when the card sits
      // near a viewport edge (the banner is draggable for the same users).
      const r = anchorBtn.getBoundingClientRect();
      const mw = menu.offsetWidth || 240, mh = menu.offsetHeight || 200;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - mw - 8));
      let top = r.bottom + 6;
      if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - 6 - mh);
      menu.style.left = left + 'px'; menu.style.top = top + 'px';
      document.addEventListener('mousedown', _onBannerReassignOutside, true);
      document.addEventListener('keydown', _onBannerReassignKey, true);
      refreshIcons(menu);
      list.querySelector('.assign-dropdown-row:not(.is-unavailable)')?.focus?.();
    }
    function openIncomingConversation() {
      const ev = incomingData;
      if (!ev) return;
      const phone = ev.from || String(ev.chatId || '').split('@')[0];
      const displayName = ev.displayName || document.getElementById('wibName')?.textContent || '';
      // Reuse the inbox helper so a first-time caller still opens even when
      // the conversation list has not loaded a real chat row yet.
      openInboxForPhone(phone, displayName).catch(() => {
        toast('Could not open conversation', 'error');
      });
    }
    function handleIncoming(ev) {
      // Already on a WhatsApp call, or the banner already shows another ring
      // → this ring goes to the call stack instead of being ignored (Ayoub,
      // 2026-09-27: several calls at once must all stay visible). No peer
      // connection is created unless this browser accepts. Never reject:
      // /api/calls/:id/reject hangs up at Meta for EVERYONE (a reroute or
      // live reassign onto a busy agent would kill the client's live call
      // for the whole team).
      if (pc || (incomingData && ev?.callId && incomingData.callId !== ev.callId)) {
        console.log('[waCall:in] ring while busy → call stack:', ev?.callId);
        _queueWaiting(ev);
        return;
      }
      // A picker left open for a previous ring must never assign the previous
      // chat once a different call takes the banner.
      if (incomingData?.callId !== ev?.callId) _closeBannerReassignPicker();
      if (ev?.callId) _removeWaiting(ev.callId);
      incomingData = ev;
      // Prefer the server-enriched fields (displayName / firstname /
      // pipelineStage) — they're populated from the HubSpot cache + mirror
      // + leads table on the call hot path. Fall back to local chat state
      // and the raw phone digits so the banner is never empty even on a
      // cold mirror miss (the deferred wa_call_incoming_enriched event
      // upgrades the row in place once the lookup returns).
      const chat = (state.chats || []).find(c => c.id === ev.chatId);
      const callerName = ev.displayName || chat?.name || displayPhone(ev.from) || 'Unknown';
      const callerPhone = displayPhone(ev.from || (ev.chatId || '').split('@')[0]);
      const stage = ev.pipelineStage || chat?.pipelineStage || null;
      const banner = document.getElementById('waIncomingCallBanner');
      if (!banner) return;
      document.getElementById('wibAvatar').textContent = initials(callerName);
      document.getElementById('wibName').textContent = callerName;
      document.getElementById('wibPhone').textContent = callerPhone;
      _renderBannerStage(stage);
      _setBannerMeta(ev, stage);
      _syncBannerReassignBtn(ev);
      banner.tabIndex = 0;
      banner.setAttribute('aria-label', 'Incoming WhatsApp call from ' + callerName + '. Open conversation.');
      banner.classList.remove('hidden');
      startRingtone();
      // Safety timeout — if Meta never sends `terminate` for an
      // unanswered call (network blip, etc.) we still want the banner
      // gone after 60 s instead of stuck forever on the agent's screen.
      // Cleared by accept / decline / remote-terminate, whichever fires first.
      if (incomingAutoDismissTimer) clearTimeout(incomingAutoDismissTimer);
      incomingAutoDismissTimer = setTimeout(() => {
        if (incomingData && incomingData.callId === ev.callId) {
          console.log('[waCall:in] safety auto-dismiss after 60s — no terminate received');
          hideIncomingBanner();
          _promoteWaiting();
        }
      }, 60000);
      // Admin / super_sales: movable card (grip on the left edge, position
      // remembered). Agents never see the grip.
      const grip = document.getElementById('wibGrip');
      if (_enableAdminDrag(banner, '.wib-grip', 'leads7.waIncomingBannerPos')) grip?.classList.remove('hidden');
      else grip?.classList.add('hidden');
      refreshIcons(banner);
      // The card opens the caller's Inbox thread. Controls stop propagation
      // below, so accepting/rejecting never triggers chat navigation.
      const acceptBtn = document.getElementById('wibAcceptBtn');
      const declineBtn = document.getElementById('wibDeclineBtn');
      if (!banner._openChatWired) {
        banner.addEventListener('click', (event) => {
          if (event.target?.closest?.('.wib-btn, .wib-grip, .wib-reassign')) return;
          // A drag that ended over the card must not open the conversation.
          if (banner._suppressClickUntil && Date.now() < banner._suppressClickUntil) {
            banner._suppressClickUntil = 0;
            return;
          }
          openIncomingConversation();
        });
        banner.addEventListener('keydown', (event) => {
          if (event.target !== banner || (event.key !== 'Enter' && event.key !== ' ')) return;
          event.preventDefault();
          openIncomingConversation();
        });
        banner._openChatWired = true;
      }
      if (acceptBtn && !acceptBtn._wired) {
        acceptBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          acceptIncoming();
        });
        acceptBtn._wired = true;
      }
      if (declineBtn && !declineBtn._wired) {
        declineBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          declineIncoming();
        });
        declineBtn._wired = true;
      }
      // Browser notification when tab not focused so the agent doesn't
      // miss it. Silent if Notification permission isn't granted.
      if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          new Notification('Incoming WhatsApp Call', {
            body: callerName + ' • ' + callerPhone,
            tag: 'wa-call-' + ev.callId,
          });
        } catch (e) { /* ignore */ }
      }
    }

    // Stamps the pipeline stage onto the banner element itself so the
    // WHOLE banner background switches colour per stage — per Ayoub:
    // "mafhmtinish mbghitsh n9ota bghit hadok pop up kaml backgroyn
    // dyala kmla ktl3 bi nfes color". The actual paint is in CSS via
    // .wa-incoming-banner[data-stage="X"] rules.
    function _renderBannerStage(stage) {
      const banner = document.getElementById('waIncomingCallBanner');
      if (!banner) return;
      if (stage) banner.dataset.stage = stage;
      else delete banner.dataset.stage;
      banner.title = stage ? ('Stage: ' + (PIPELINE_LABELS[stage] || stage)) : '';
    }

    // Fills the two pills under the phone: WHO owns this lead (assigned agent)
    // + the visible pipeline-stage label. agentName comes from the server
    // payload; falls back to a client teamUsers lookup by agentId. Each pill
    // hides itself when its data is absent (unassigned lead / unknown stage).
    function _setBannerMeta(ev, stage) {
      const agentEl = document.getElementById('wibAgent');
      const stageEl = document.getElementById('wibStage');
      let agentName = (ev && ev.agentName) || '';
      if (!agentName && ev && ev.agentId) {
        agentName = (state.teamUsers || []).find(u => u.username === ev.agentId)?.displayName || ev.agentId;
      }
      if (ev && ev.agentId && adminLabelFor(ev.agentId)) {
        agentName = adminLabelFor(ev.agentId);
      }
      if (agentEl) {
        if (agentName) { agentEl.textContent = '👤 ' + agentName; agentEl.classList.remove('hidden'); }
        else { agentEl.textContent = ''; agentEl.classList.add('hidden'); }
      }
      if (stageEl) {
        const label = stage ? (PIPELINE_LABELS[stage] || stage) : '';
        if (label) { stageEl.textContent = label; stageEl.dataset.stage = stage; stageEl.classList.remove('hidden'); }
        else { stageEl.textContent = ''; delete stageEl.dataset.stage; stageEl.classList.add('hidden'); }
      }
      const lineEl = document.getElementById('wibLine');
      if (lineEl) {
        const n = ev && ev.lineNumber;
        lineEl.textContent = n ? '📞 ' + _ourLineText('WhatsApp', n) : '';
        lineEl.title = n ? 'Numéro appelé' : '';
        lineEl.classList.toggle('hidden', !n);
      }
    }

    // Deferred enrichment event — fires when the cold-mirror HubSpot
    // lookup resolves after the banner already painted with a fallback
    // name. Upgrades the banner in place; safe to call after the banner
    // is dismissed (the lookup checks the visible callId).
    function handleIncomingEnriched(ev) {
      if (ev?.callId && waWaiting.has(ev.callId)) {
        Object.assign(waWaiting.get(ev.callId).ev, ev);
        _renderWaStack();
        return;
      }
      if (!incomingData || incomingData.callId !== ev?.callId) return;
      if (ev.displayName) {
        document.getElementById('wibName').textContent = ev.displayName;
        document.getElementById('wibAvatar').textContent = initials(ev.displayName);
      }
      if (ev.pipelineStage) _renderBannerStage(ev.pipelineStage);
      // Merge back into incomingData so the active-call panel inherits
      // the enriched fields when the agent picks up.
      Object.assign(incomingData, ev);
      _setBannerMeta(incomingData, incomingData.pipelineStage || null);
    }

    function hideIncomingBanner() {
      const banner = document.getElementById('waIncomingCallBanner');
      banner?.classList.add('hidden');
      _closeBannerReassignPicker();
      incomingData = null;
      stopRingtone();
      if (incomingAutoDismissTimer) {
        clearTimeout(incomingAutoDismissTimer);
        incomingAutoDismissTimer = null;
      }
    }

    // Another recipient (e.g. the contact's owner) answered the call — the
    // server tells everyone else who got the ringer to drop their banner so it
    // stops ringing while that agent is already on the line.
    function handleTaken(ev) {
      if (ev?.callId && waWaiting.has(ev.callId)) { _removeWaiting(ev.callId); return; }
      if (!ev?.callId || !incomingData || incomingData.callId !== ev.callId) return;
      hideIncomingBanner();
      _promoteWaiting();
      // Reassigned while ringing → tell the agent who lost the ring where it went.
      if (ev.action === 'rerouted' && ev.reassignedTo) toast('Appel réassigné à ' + ev.reassignedTo, 'info');
      else if (ev.by) toast('Appel pris par ' + ev.by, 'info');
    }

    async function declineIncoming() {
      const ev = incomingData;
      hideIncomingBanner();
      _promoteWaiting();
      if (!ev?.callId) return;
      try {
        await fetchT('/api/calls/' + encodeURIComponent(ev.callId) + '/reject', {
          method: 'POST', headers: authHeaders(),
        });
      } catch (e) { /* */ }
    }

    async function acceptIncoming() {
      const ev = incomingData;
      if (!ev?.callId || !ev?.offerSdp) return;
      hideIncomingBanner();
      // Reuse start()'s panel + WebRTC setup, but answer-flavoured.
      const chat = (state.chats || []).find(c => c.id === ev.chatId) || { name: ev.from, phone: ev.from };
      chatId = ev.chatId;
      callId = ev.callId;
      setHeader(chat.name || displayPhone(ev.from), displayPhone(chat.phone || (ev.chatId || '').split('@')[0]));
      setStatus('connecting', 'answering…');
      showPanel();
      const muteBtn = document.getElementById('wcpMuteBtn');
      const endBtn = document.getElementById('wcpEndBtn');
      if (muteBtn && !muteBtn._wired) { muteBtn.addEventListener('click', toggleMute); muteBtn._wired = true; }
      if (endBtn && !endBtn._wired) { endBtn.addEventListener('click', () => end({ reason: 'user' })); endBtn._wired = true; }
      try {
        localStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          video: false,
        });
        attachMicAnalyser(localStream);
        const rtcCfg = await fetchRtcConfig();
        pc = new RTCPeerConnection(rtcCfg);
        const audioTrack = localStream.getAudioTracks()[0];
        audioTrack.enabled = true;
        pc.ontrack = (e) => {
          const audio = document.getElementById('wcpRemoteAudio');
          if (audio && e.streams?.[0]) {
            audio.srcObject = e.streams[0];
            audio.play().catch(() => {});
            remoteStream = e.streams[0];
          }
        };
        pc.onconnectionstatechange = () => {
          const s = pc?.connectionState;
          console.log('[waCall:in] connectionState=' + s);
          if (s === 'connected') {
            setStatus('connected', 'on call');
            if (!timerInt) startTimer();
            startStatsPoller();
            try { startRecording(); } catch (e) { console.warn('[waCall:in] recording failed:', e); }
          } else if (s === 'failed') {
            setStatus('failed', 'connection lost');
            end({ reason: 'rtc_failed', notifyServer: true });
          }
        };
        // CRITICAL: setRemoteDescription FIRST for the answerer pattern.
        // This creates the transceiver matched to Meta's audio m-line.
        // If we call addTransceiver beforehand (as the outbound path does),
        // we'd end up with an orphan transceiver unmatched to any m-line
        // — Meta's offer has 1 audio m-line, and our pre-created one
        // would have mid=null → outbound mic never reaches the recipient.
        // This was the cause of the one-way audio observed Mary→Ayoub.
        console.log('[waCall:in] REMOTE OFFER SDP:\n' + ev.offerSdp);
        await pc.setRemoteDescription({ type: 'offer', sdp: ev.offerSdp });
        // Now bind the mic into the auto-created audio transceiver +
        // force its direction to sendrecv so we can both send and receive.
        const transceiver = pc.getTransceivers().find(t => t.receiver?.track?.kind === 'audio' || t.sender?.track?.kind === 'audio') || pc.getTransceivers()[0];
        if (transceiver) {
          try { transceiver.direction = 'sendrecv'; } catch (e) { console.warn('[waCall:in] set direction failed:', e); }
          await transceiver.sender.replaceTrack(audioTrack);
        } else {
          // Defensive fallback — shouldn't happen but better than silent send loss.
          pc.addTrack(audioTrack, localStream);
        }
        // Codec preferences: Opus first.
        try {
          if (typeof RTCRtpSender !== 'undefined' && RTCRtpSender.getCapabilities) {
            const caps = RTCRtpSender.getCapabilities('audio');
            if (caps?.codecs && transceiver?.setCodecPreferences) {
              const opus = caps.codecs.filter(c => /opus/i.test(c.mimeType));
              const rest = caps.codecs.filter(c => !/opus/i.test(c.mimeType));
              transceiver.setCodecPreferences([...opus, ...rest]);
            }
          }
        } catch (e) { console.warn('[waCall:in] setCodecPreferences failed:', e); }
        const answer = await pc.createAnswer();
        if (answer.sdp) answer.sdp = rewriteAudioSdp(answer.sdp);
        console.log('[waCall:in] LOCAL ANSWER SDP:\n' + answer.sdp);
        await pc.setLocalDescription(answer);
        // Ship the answer as soon as a TURN relay candidate is ready (not after
        // full gathering) so it reaches Meta inside its inbound ring window.
        await waitForRelayCandidate(pc);
        const r = await fetchT('/api/calls/' + encodeURIComponent(ev.callId) + '/answer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({
            sdpAnswer: pc.localDescription.sdp,
            socketId: state.socket?.id || null,
            chatId: ev.chatId,
          }),
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          setStatus('failed', 'accept failed');
          toast('Failed to accept call: ' + (j.error || 'unknown'), 'error');
          await teardown();
        }
        window.__waCall = { pc, localStream };
      } catch (err) {
        console.error('[waCall:in] accept failed:', err);
        setStatus('failed', 'mic / accept failed');
        toast('Failed to accept call: ' + (err?.message || err), 'error');
        await teardown();
      }
    }

    return { start, end, handleServerEvent, handleIncoming, handleIncomingEnriched, handleTaken, openReassignMenu: _openReassignMenu };
  })();

  // ─── Your lead is calling WhatsApp Support (0621) — 2026-09-29 ───
  // support2 stays silent when a caller on its own WhatsApp line is still a
  // sales lead; the server rings ONLY the lead's owner here. The call can only
  // be answered on the support phone, so the card rings like a call and its
  // button opens the chat (call the client back from there).
  const supportLineCall = (function () {
    const calls = new Map(); // callId -> payload, newest shown
    let current = null;
    let ringCtx = null;
    let ringTimer = null;
    let expiryTimer = null;
    let ringPlayer = null;

    function stopRing() {
      if (ringPlayer) { ringPlayer.stop(); ringPlayer = null; }
      if (ringTimer) { clearInterval(ringTimer); ringTimer = null; }
      if (ringCtx) { try { ringCtx.close(); } catch {} ringCtx = null; }
    }
    function startRing() {
      stopRing();
      try { ringPlayer = window.__loughaCallRingtone?.start('support-line', 'ring'); if (ringPlayer) return; } catch { /* legacy sound below */ }
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        ringCtx = new Ctx();
        if (ringCtx.state === 'suspended') { try { ringCtx.resume(); } catch {} }
        const one = () => {
          if (!ringCtx || ringCtx.state === 'closed') return;
          const t0 = ringCtx.currentTime;
          for (const freq of [440, 480]) {
            const osc = ringCtx.createOscillator();
            const gain = ringCtx.createGain();
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0, t0);
            gain.gain.linearRampToValueAtTime(0.08, t0 + 0.05);
            gain.gain.setValueAtTime(0.08, t0 + 1.45);
            gain.gain.linearRampToValueAtTime(0, t0 + 1.5);
            osc.connect(gain).connect(ringCtx.destination);
            osc.start(t0);
            osc.stop(t0 + 1.5);
          }
        };
        one();
        ringTimer = setInterval(one, 3500);
      } catch (e) {
        console.warn('[supportLineCall] ringtone failed:', e);
      }
    }
    function render() {
      const banner = document.getElementById('supportLineCallBanner');
      if (!banner) return;
      if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = null; }
      const ev = [...calls.values()].pop() || null;
      current = ev;
      if (!ev) {
        banner.classList.add('hidden');
        stopRing();
        return;
      }
      const chat = (state.chats || []).find(c => c.id === ev.chatId);
      const name = ev.displayName || chat?.name || displayPhone(ev.from) || 'Unknown';
      const stage = ev.pipelineStage || chat?.pipelineStage || null;
      document.getElementById('slbAvatar').textContent = initials(name);
      document.getElementById('slbName').textContent = name;
      document.getElementById('slbPhone').textContent = displayPhone(ev.from || String(ev.chatId || '').split('@')[0]);
      const stagePill = document.getElementById('slbStage');
      if (stage) {
        banner.dataset.stage = stage;
        stagePill.textContent = PIPELINE_LABELS[stage] || stage;
        stagePill.classList.remove('hidden');
      } else {
        delete banner.dataset.stage;
        stagePill.classList.add('hidden');
      }
      const wasHidden = banner.classList.contains('hidden');
      banner.classList.remove('hidden');
      if (wasHidden) startRing();
      if (!banner._slbWired) {
        banner._slbWired = true;
        const open = () => {
          const ev2 = current;
          if (!ev2) return;
          end({ callId: ev2.callId });
          openInboxForPhone(ev2.from || String(ev2.chatId || '').split('@')[0], ev2.displayName || '')
            .catch(() => toast('Could not open conversation', 'error'));
        };
        banner.addEventListener('click', (event) => {
          if (event.target?.closest?.('#slbDismissBtn')) return;
          open();
        });
        document.getElementById('slbDismissBtn')?.addEventListener('click', (event) => {
          event.stopPropagation();
          if (current) end({ callId: current.callId });
        });
      }
      refreshIcons(banner);
      const left = Math.max(5000, Number(ev.expiresAt || 0) - Date.now());
      expiryTimer = setTimeout(() => end({ callId: ev.callId }), Math.min(left, 60000));
      if (wasHidden && document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          new Notification('Your lead is calling WhatsApp Support', { body: name, tag: 'support-wa-' + ev.callId });
        } catch {}
      }
    }
    function show(ev) {
      if (!ev?.callId) return;
      calls.delete(ev.callId);
      calls.set(ev.callId, ev);
      render();
    }
    function end(ev) {
      if (!ev?.callId || !calls.delete(ev.callId)) return;
      render();
    }
    return { show, end };
  })();

  // ─── Test Studio "Need help" card (2026-10-07) ─────────────────────
  // A teacher in a placement test pressed Need help: ring like a call with the
  // teacher's name and an Assist button that opens their live Zoom as co-host
  // (/zoom-assist.html). First Assist wins server-side; 'teacher_help_taken'
  // closes the card for everyone else. Dismiss only hides it for this agent.
  const teacherHelpCall = (function () {
    const reqs = new Map(); // id -> payload, newest shown
    let current = null;
    let ringCtx = null;
    let ringTimer = null;
    let expiryTimer = null;
    // The PA announcement (Ayoub, 2026-10-27): a ding-dong chime, then a voice
    // that names the agent who owns the client — "Attention please. Imane,
    // please join Teacher Morad. Your client, Moussa Kchibi, may need help." —
    // through a small "big hall" chain, repeated while the card rings.
    const PA_CHIME_URL = '/sounds/pa-chime.mp3?v=20261027';
    const PA_REPEAT_MS = 14000;
    const PA_MAX_PLAYS = 4;
    const PA_VOICE_WAIT_MS = 6000;
    let ringToken = 0;      // bumps on every stop: late async work checks it
    let ringingId = null;   // the request the sound is for
    let paTimers = [];
    let paSources = [];
    let chimeBytes = null;  // the chime file, fetched once per page

    function stopRing() {
      ringToken++;
      ringingId = null;
      if (ringTimer) { clearInterval(ringTimer); ringTimer = null; }
      paTimers.forEach(t => clearTimeout(t));
      paTimers = [];
      paSources.forEach(src => { try { src.stop(); } catch {} });
      paSources = [];
      if (ringCtx) { try { ringCtx.close(); } catch {} ringCtx = null; }
    }
    // Two short rising beeps — the old ring, kept as the last fallback. Not
    // the WhatsApp ring, so nobody confuses a teacher asking for help with a
    // client calling.
    function beepOnce(ctx, at = 0) {
      if (!ctx || ctx.state === 'closed') return;
      const t0 = ctx.currentTime + at;
      [[660, 0], [880, 0.28]].forEach(([freq, d]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, t0 + d);
        gain.gain.linearRampToValueAtTime(0.09, t0 + d + 0.03);
        gain.gain.setValueAtTime(0.09, t0 + d + 0.2);
        gain.gain.linearRampToValueAtTime(0, t0 + d + 0.24);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0 + d);
        osc.stop(t0 + d + 0.25);
      });
    }
    function beepRing(ctx) {
      beepOnce(ctx);
      ringTimer = setInterval(() => beepOnce(ctx), 2500);
    }
    function decodeAudio(ctx, bytes) {
      return new Promise((resolve, reject) => {
        try {
          const p = ctx.decodeAudioData(bytes, resolve, reject);
          if (p && typeof p.then === 'function') p.then(resolve, reject);
        } catch (e) { reject(e); }
      });
    }
    async function loadChime(ctx) {
      try {
        if (!chimeBytes) {
          const r = await fetch(PA_CHIME_URL);
          if (!r.ok) return null;
          chimeBytes = await r.arrayBuffer();
        }
        // decodeAudioData detaches its input — keep our copy for the next ring.
        return await decodeAudio(ctx, chimeBytes.slice(0));
      } catch { chimeBytes = null; return null; }
    }
    async function loadAnnounce(ctx, ev) {
      try {
        // A pre-recorded clip (public/sounds/help) — never generated live.
        if (!ev.announceClip || !/^help-[a-z-]+$/.test(ev.announceClip)) return null;
        const r = await fetchT('/sounds/help/' + ev.announceClip + '.mp3?v=20261027', {}, 15000);
        if (!r.ok) return null;
        return await decodeAudio(ctx, await r.arrayBuffer());
      } catch { return null; }
    }
    // Plain output: the chime and the recorded voice play as they are — no
    // speaker filter or reverb (Ayoub 27/10: a natural human voice, not a robot).
    function paChain(ctx) {
      const out = ctx.createGain();
      out.gain.value = 0.9;
      out.connect(ctx.destination);
      return out;
    }
    function paPlay(ctx, chain, buffer, at) {
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(chain);
      src.start(at);
      paSources.push(src);
      src.onended = () => { paSources = paSources.filter(x => x !== src); };
    }
    function startRing(ev) {
      stopRing();
      const token = ringToken;
      ringingId = ev ? ev.id : null;
      let ctx;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        ctx = ringCtx = new Ctx();
        if (ctx.state === 'suspended') { try { ctx.resume(); } catch {} }
      } catch (e) {
        console.warn('[teacherHelpCall] ring failed:', e);
        return;
      }
      (async () => {
        // The voice keeps loading after the wait: a later repeat uses it.
        let voice = null;
        const voiceP = loadAnnounce(ctx, ev || {}).then(b => { voice = b; });
        const chime = await loadChime(ctx);
        await Promise.race([voiceP, new Promise(r => setTimeout(r, PA_VOICE_WAIT_MS))]);
        if (token !== ringToken) return;
        // Neither the chime nor the voice: the old two-beep ring.
        if (!chime && !voice) { beepRing(ctx); return; }
        const chain = paChain(ctx);
        let plays = 0;
        const playOnce = () => {
          if (token !== ringToken || plays >= PA_MAX_PLAYS || !ctx || ctx.state === 'closed') return;
          plays++;
          let at = ctx.currentTime + 0.05;
          // The voice starts while the chime's last note rings out.
          if (chime) { paPlay(ctx, chain, chime, at); at += Math.max(0.5, chime.duration - 0.7); }
          else { beepOnce(ctx, 0.05); at += 0.85; }
          // No ElevenLabs voice → the chime alone (never a robot browser voice).
          if (voice) paPlay(ctx, chain, voice, at);
          paTimers.push(setTimeout(playOnce, PA_REPEAT_MS));
        };
        playOnce();
      })().catch(e => {
        console.warn('[teacherHelpCall] announcement failed:', e);
        if (token === ringToken && ringCtx === ctx) { try { beepRing(ctx); } catch {} }
      });
    }
    function render() {
      const banner = document.getElementById('teacherHelpBanner');
      if (!banner) return;
      if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = null; }
      const ev = [...reqs.values()].pop() || null;
      current = ev;
      if (!ev) {
        banner.classList.add('hidden');
        stopRing();
        return;
      }
      const avatar = document.getElementById('thbAvatar');
      avatar.textContent = ev.initials || initials(ev.teacherName || '?');
      avatar.style.background = ev.color || '';
      document.getElementById('thbName').textContent = (ev.teacherName || 'A teacher') + ' needs help';
      // The agent the PA calls by name sees it is their own client.
      const isOwner = !!ev.ownerUserId && String(ev.ownerUserId) === String(state.me?.id || '');
      const when = ev.learnerTime ? ' · ' + ev.learnerTime : '';
      const learner = isOwner
        ? 'Your client' + (ev.learnerName ? ' ' + ev.learnerName : '') + when
        : (ev.learnerName ? 'with ' + ev.learnerName + when : '')
          + (ev.ownerName ? (ev.learnerName ? ' · ' : '') + ev.ownerName + "'s client" : '');
      document.getElementById('thbSub').textContent = [ev.topic, learner].filter(Boolean).join(' — ') || 'Placement test';
      const zoomPill = document.getElementById('thbZoom');
      zoomPill.textContent = ev.meetingId ? 'Live in Zoom' : 'Not live in Zoom';
      zoomPill.classList.toggle('thb-nozoom', !ev.meetingId);
      const assistBtn = document.getElementById('thbAssistBtn');
      assistBtn.title = ev.meetingId ? 'Assist — join their Zoom as co-host' : 'Open Zoom Live';
      const wasHidden = banner.classList.contains('hidden');
      banner.classList.remove('hidden');
      // A new request on top of a ringing card gets its own announcement.
      if (wasHidden || ringingId !== ev.id) startRing(ev);
      if (!banner._thbWired) {
        banner._thbWired = true;
        assistBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          const ev2 = current;
          if (!ev2) return;
          if (ev2.meetingId) {
            // Open in the click itself, or the pop-up blocker eats it.
            const w = window.open('/zoom-assist.html?m=' + encodeURIComponent(ev2.meetingId) + '&help=' + encodeURIComponent(ev2.id), 'lougha-zoom-assist');
            if (!w) toast('Allow pop-ups for Leads7 to open the assistant tab.', 'warning');
          } else {
            showView('zoom-live');
          }
          end({ id: ev2.id });
        });
        document.getElementById('thbDismissBtn')?.addEventListener('click', (event) => {
          event.stopPropagation();
          if (current) end({ id: current.id });
        });
      }
      refreshIcons(banner);
      const left = Math.max(5000, Number(ev.expiresAt || 0) - Date.now());
      expiryTimer = setTimeout(() => end({ id: ev.id }), Math.min(left, 70000));
      if (wasHidden && document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          new Notification((ev.teacherName || 'A teacher') + ' needs help', { body: ev.topic || 'Placement test', tag: 'teacher-help-' + ev.id });
        } catch {}
      }
    }
    function show(ev) {
      if (!ev?.id) return;
      reqs.delete(ev.id);
      reqs.set(ev.id, ev);
      render();
    }
    function end(ev) {
      if (!ev?.id || !reqs.delete(ev.id)) return;
      render();
    }
    function taken(ev) {
      if (!ev?.id) return;
      const had = reqs.has(ev.id);
      end(ev);
      const me = String(state.me?.id || '');
      if (had && ev.by && String(ev.byUserId || '') !== me) {
        toast(ev.by + ' is helping ' + (ev.teacherName || 'the teacher'), 'info');
      }
    }
    return { show, end, taken };
  })();

  // ─── Admin Calls page ──────────────────────────────────────────────
  // Per Ayoub 2026-05-21: admins get a dedicated full page with EVERY
  // call (in/out, count, time, recording). The server chooses the role scope:
  // agents get their own calls; Super Sales/Admin get the whole team.
  // Live-updates via the same socket 'call_updated' event as the top
  // bar, so a call answered mid-browsing reflects immediately.
  state.callsAdmin = {
    rows: [], stats: {}, total: 0, bucket: 'all', source: 'all', direction: 'all',
    status: 'all', agent: '', selectedId: '', limit: 100, canSeeAll: false,
    wired: false, loading: false, pendingReload: false,
  };

  // ─── Recordings finder ────────────────────────────────────────────────
  // Searchable recording index (MinIO). Server scopes per role (agents see
  // their own extensions, admins/viewers see all). Loads GET
  // /api/recordings/search; admins can POST /api/recordings/reindex.
  state.recordings = { rows: [], wired: false, offset: 0 };

  // ─── Documents registry (admin + super_sales) ─────────────────────────
  // Read-only registry of every document the sales team has sent. Loads
  // GET /api/documents/registry (server-side admin gate). Filters are
  // server-side (type/agent/from/to/q) — every filter change re-fetches.
  state.documents = { wired: false, rows: [], agents: [], total: 0, filters: { type: '', agent: '', from: '', to: '', q: '' } };

  // doc_type → FR label for the type chip (unknown key → raw key).
  const DOC_TYPE_LABELS = {
    proforma: 'Proforma',
    devis: 'Devis',
    registration: "Fiche d'inscription",
    facture: 'Facture',
    receipt: 'Reçu',
    attestation: 'Attestation',
    certificate_of_completion: 'Certificat',
    test_result: 'Résultat test',
    contract_preview: 'Contrat',
  };

  function ensureDocumentsInit() {
    if (state.documents.wired) { loadDocuments(); return; }
    state.documents.wired = true;
    document.getElementById('documentsRefreshBtn')?.addEventListener('click', () => loadDocuments());
    document.getElementById('docFilterType')?.addEventListener('change', (e) => {
      state.documents.filters.type = e.target.value;
      loadDocuments();
    });
    document.getElementById('docFilterAgent')?.addEventListener('change', (e) => {
      state.documents.filters.agent = e.target.value;
      loadDocuments();
    });
    document.getElementById('docFilterFrom')?.addEventListener('change', (e) => {
      state.documents.filters.from = e.target.value;
      loadDocuments();
    });
    document.getElementById('docFilterTo')?.addEventListener('change', (e) => {
      state.documents.filters.to = e.target.value;
      loadDocuments();
    });
    let _docSearchT = null;
    document.getElementById('docFilterSearch')?.addEventListener('input', (e) => {
      const v = e.target.value;
      clearTimeout(_docSearchT);
      _docSearchT = setTimeout(() => {
        state.documents.filters.q = v.trim();
        loadDocuments();
      }, 300);
    });
    loadDocuments();
  }

  async function loadDocuments() {
    const tbody = document.querySelector('#documentsTable tbody');
    const empty = document.getElementById('documentsEmpty');
    const count = document.getElementById('documentsCount');
    if (!tbody) return;
    clear(tbody);
    empty?.classList.add('hidden');
    if (count) count.textContent = 'Loading…';
    try {
      const f = state.documents.filters;
      const params = new URLSearchParams();
      if (f.type)  params.set('type', f.type);
      if (f.agent) params.set('agent', f.agent);
      if (f.from)  params.set('from', f.from);
      if (f.to)    params.set('to', f.to);
      if (f.q)     params.set('q', f.q);
      const qs = params.toString();
      const r = await api('/api/documents/registry' + (qs ? '?' + qs : ''));
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      state.documents.rows = Array.isArray(j.documents) ? j.documents : [];
      state.documents.agents = Array.isArray(j.agents) ? j.agents : [];
      state.documents.total = Number(j.total) || 0;
      _populateDocAgentSelect();
      renderDocuments();
    } catch (err) {
      if (count) count.textContent = '';
      tbody.appendChild(el('tr', {}, [
        el('td', { colspan: '6', class: 'ts-book-empty', text: 'Failed to load: ' + (err.message || err) }),
      ]));
    }
  }

  // Populate the agent <select> once from the server's agent list, preserving
  // the current selection across reloads.
  function _populateDocAgentSelect() {
    const sel = document.getElementById('docFilterAgent');
    if (!sel) return;
    const agents = state.documents.agents || [];
    // Already populated (more than the placeholder option) → leave as-is.
    if (sel.options.length > 1) return;
    const keep = sel.value;
    for (const a of agents) {
      if (!a || !a.username) continue;
      sel.appendChild(el('option', { value: a.username, text: userLabel(a.username, a.displayName || a.username) }));
    }
    if (keep) sel.value = keep;
  }

  function renderDocuments() {
    const tbody = document.querySelector('#documentsTable tbody');
    const empty = document.getElementById('documentsEmpty');
    const count = document.getElementById('documentsCount');
    if (!tbody) return;
    clear(tbody);
    const rows = state.documents.rows || [];
    const total = state.documents.total || rows.length;
    if (count) count.textContent = rows.length ? '(' + rows.length + ' of ' + total + ')' : '';
    if (!rows.length) { empty?.classList.remove('hidden'); return; }
    empty?.classList.add('hidden');
    for (const r of rows) {
      const tr = el('tr');
      // Date — day + time, FR locale.
      const dateCell = el('td');
      if (r.sent_at) {
        const d = new Date(r.sent_at);
        dateCell.appendChild(el('div', { text: MA.loc(d, 'd', 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) }));
        dateCell.appendChild(el('div', { class: 'muted', style: 'font-size:11px;', text: MA.loc(d, 't', 'fr-FR', { hour: '2-digit', minute: '2-digit' }) }));
      } else {
        dateCell.appendChild(document.createTextNode('—'));
      }
      tr.appendChild(dateCell);
      // Type chip — FR label, fall back to raw key.
      const typeCell = el('td');
      typeCell.appendChild(el('span', { class: 'doc-type-chip', text: DOC_TYPE_LABELS[r.doc_type] || r.doc_type || '—' }));
      tr.appendChild(typeCell);
      // Client — clickable → inbox when a phone exists. Pipeline stage as a
      // tiny muted sub-label.
      const clientCell = el('td');
      const digits = r.phone ? String(r.phone).replace(/\D/g, '') : '';
      const clientLabel = r.client_name || r.phone || '—';
      const clientName = el('div', { style: 'font-weight:600;', text: clientLabel });
      clientCell.appendChild(clientName);
      if (r.pipeline_stage) {
        clientCell.appendChild(el('div', { class: 'muted', style: 'font-size:11px;', text: PIPELINE_LABELS[r.pipeline_stage] || r.pipeline_stage }));
      }
      if (digits) {
        clientCell.style.cursor = 'pointer';
        clientCell.addEventListener('click', () => {
          if (window.openInboxForPhone) window.openInboxForPhone(digits, r.client_name || '');
        });
      }
      tr.appendChild(clientCell);
      // Agent
      tr.appendChild(el('td', { text: r.agent_name || r.sent_by || '—' }));
      // N°
      tr.appendChild(el('td', { text: r.document_number || '—' }));
      // PDF link — cookie-gated /uploads/documents for authed admins.
      const pdfCell = el('td');
      if (r.file_url) {
        const a = el('a', { href: r.file_url, target: '_blank', rel: 'noopener', class: 'doc-pdf-link', title: r.file_name || 'PDF' });
        a.appendChild(lucideIcon('file'));
        pdfCell.appendChild(a);
      } else {
        pdfCell.appendChild(document.createTextNode('—'));
      }
      tr.appendChild(pdfCell);
      tbody.appendChild(tr);
    }
    refreshIcons(tbody);
  }

  // ─── Agents & Lignes 3CX (admin) ───────────────────────────────────────
  // 3CX-style table: one row per extension — agent, extension, in-page
  // softphone status, and the outbound line/number the agent dials with
  // (editable inline; saved to /api/3cx/agents-overview/:ext).
  function ensureThreecxAgentsInit() {
    state.threecxAgents = state.threecxAgents || { rows: [], q: '', wired: false };
    if (state.threecxAgents.wired) { _loadThreecxAgents(); return; }
    state.threecxAgents.wired = true;
    document.getElementById('cxAgentsRefreshBtn')?.addEventListener('click', () => _loadThreecxAgents());
    document.getElementById('cxAgentsSearch')?.addEventListener('input', (e) => {
      state.threecxAgents.q = e.target.value.toLowerCase(); _renderThreecxAgents();
    });
    document.getElementById('cxAgentsOnlineOnly')?.addEventListener('change', (e) => {
      state.threecxAgents.onlineOnly = e.target.checked; _renderThreecxAgents();
    });
    _loadThreecxAgents();
  }

  async function _loadThreecxAgents() {
    const tbody = document.querySelector('#cxAgentsTable tbody');
    const count = document.getElementById('cxAgentsCount');
    if (!tbody) return;
    clear(tbody);
    if (count) count.textContent = 'Chargement…';
    try {
      const r = await api('/api/3cx/agents-overview');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      state.threecxAgents.rows = Array.isArray(j.agents) ? j.agents : [];
      _renderThreecxAgents();
    } catch (err) {
      if (count) count.textContent = '';
      tbody.appendChild(el('tr', {}, [el('td', { colspan: '6', class: 'ts-book-empty', text: 'Échec du chargement : ' + (err.message || err) })]));
    }
  }

  function _renderThreecxAgents() {
    const tbody = document.querySelector('#cxAgentsTable tbody');
    const empty = document.getElementById('cxAgentsEmpty');
    const count = document.getElementById('cxAgentsCount');
    if (!tbody) return;
    clear(tbody);
    let rows = state.threecxAgents.rows || [];
    const total = rows.length;
    if (state.threecxAgents.onlineOnly) rows = rows.filter(a => a.status === 'online' || a.status === 'ringing' || a.status === 'busy');
    const q = state.threecxAgents.q;
    if (q) rows = rows.filter(a => ((a.agentName || '') + ' ' + (a.agentUsername || '') + ' ' + (a.extension || '')).toLowerCase().includes(q));
    if (count) count.textContent = rows.length ? '(' + rows.length + (rows.length !== total ? ' / ' + total : '') + ')' : '';
    if (!rows.length) {
      if (empty) { empty.textContent = state.threecxAgents.onlineOnly ? 'Aucun agent en ligne pour le moment.' : 'Aucune extension.'; empty.classList.remove('hidden'); }
      return;
    }
    empty?.classList.add('hidden');
    const STATUS_LABEL = { online: 'En ligne', ringing: 'Sonne', busy: 'Occupé', dnd: 'Ne pas déranger', offline: 'Hors ligne' };
    const STATUS_CLASS = { online: 'cxa-on', ringing: 'cxa-ring', busy: 'cxa-busy', dnd: 'cxa-off', offline: 'cxa-off' };
    const pill = (cls, label) => el('span', { class: 'cxa-pill ' + cls }, [el('span', { class: 'cxa-dot' }), label]);
    for (const a of rows) {
      const st = a.status || 'offline';
      const lineInput = el('input', { class: 'cxa-line', value: a.outboundLine || '', placeholder: 'Ligne / port', 'aria-label': 'Ligne sortante' });
      const numInput = el('input', { class: 'cxa-num', value: a.outboundNumber || '', placeholder: '06…', 'aria-label': 'Numéro sortant' });
      const save = async () => {
        const nl = lineInput.value.trim(), nn = numInput.value.trim();
        // Only persist a real change — focusing then leaving a field must never
        // overwrite (or clear) a value the user didn't touch.
        if (nl === (a.outboundLine || '') && nn === (a.outboundNumber || '')) return;
        try {
          const r = await api('/api/3cx/agents-overview/' + encodeURIComponent(a.extension), {
            method: 'POST', body: JSON.stringify({ line: nl, number: nn }),
          });
          if (!r.ok) throw new Error('HTTP ' + r.status);
          a.outboundLine = nl; a.outboundNumber = nn;
          toast('Enregistré — ' + (a.agentName || a.extension));
        } catch (e) { toast('Échec de l’enregistrement', 'error'); }
      };
      lineInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') numInput.focus(); });
      numInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') numInput.blur(); });
      lineInput.addEventListener('blur', save);
      numInput.addEventListener('blur', save);
      tbody.appendChild(el('tr', {}, [
        el('td', {}, [
          el('div', { class: 'cxa-name', text: userLabel(a.agentUsername, a.agentName || a.agentUsername || '—') }),
          a.agentUsername && !adminLabelFor(a.agentUsername) ? el('div', { class: 'cxa-user', text: '@' + a.agentUsername }) : null,
        ]),
        el('td', {}, [el('span', { class: 'cxa-ext', text: String(a.extension) })]),
        el('td', {}, [a.softphoneEnabled ? pill('cxa-sp-on', 'Activé') : pill('cxa-off', 'Popup')]),
        el('td', {}, [lineInput]),
        el('td', {}, [numInput]),
        el('td', {}, [pill(STATUS_CLASS[st] || 'cxa-off', STATUS_LABEL[st] || st)]),
        el('td', { class: 'cxa-calls', 'data-hot': a.callsToday > 0 ? '1' : '0', text: String(a.callsToday || 0) }),
      ]));
    }
    refreshIcons();
  }

  let _callsSearchTimer = null;
  let _callsSocketTimer = null;
  const CALL_STATUS_LABELS = {
    answered: 'Answered', ended: 'Ended', completed: 'Completed', missed: 'Missed',
    rejected: 'Rejected', voicenote_instead: 'Voicenote', ringing: 'Ringing',
    noanswer: 'No answer', busy: 'Busy', canceled: 'Canceled', recorded: 'Enregistrement 3CX',
  };

  function _callsDayIso(value) {
    return MA.dayKey(new Date(value));
  }
  function _callsApplyPeriod(period) {
    const fromEl = document.getElementById('callsFrom');
    const toEl = document.getElementById('callsTo');
    if (!fromEl || !toEl || period === 'custom') return false;
    const today = new Date(); today.setHours(12, 0, 0, 0);
    const add = (date, days) => { const d = new Date(date); d.setDate(d.getDate() + days); return d; };
    let from = today, to = today;
    if (period === 'yesterday') from = to = add(today, -1);
    else if (period === 'this_week' || period === 'last_week') {
      const monday = add(today, -((today.getDay() + 6) % 7));
      if (period === 'this_week') from = monday;
      else { from = add(monday, -7); to = add(monday, -1); }
    } else if (period === 'this_month') { from = new Date(today); from.setDate(1); }
    fromEl.value = _callsDayIso(from); toEl.value = _callsDayIso(to);
    return true;
  }
  function _callsDuration(seconds) {
    const sec = Math.max(0, Math.round(Number(seconds) || 0));
    if (!sec) return '0 sec';
    const m = Math.floor(sec / 60), s = sec % 60;
    return m ? m + 'm ' + String(s).padStart(2, '0') + 's' : s + ' sec';
  }
  function _callsTalkTime(seconds) {
    const sec = Math.max(0, Math.round(Number(seconds) || 0));
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
    return h ? h + 'h ' + m + 'm' : m + 'm';
  }
  function _callsFileSize(bytes) {
    const n = Number(bytes) || 0;
    if (!n) return '';
    if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
    return (n / (1024 * 1024)).toFixed(1) + ' MB';
  }
  function _callsWhen(ts, full) {
    const d = new Date(Number(ts) || 0);
    if (!Number.isFinite(d.getTime())) return '—';
    const time = MA.fmt('gb-hm', 'en-GB', { hour: '2-digit', minute: '2-digit' }, d);
    const day = full
      ? MA.fmt('gb-dMy', 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' }, d)
      : MA.fmt('gb-dM', 'en-GB', { day: '2-digit', month: 'short' }, d);
    return day + ' · ' + time;
  }
  function _callsInitials(name) {
    const bits = String(name || '?').trim().split(/\s+/).filter(Boolean);
    return (bits.slice(0, 2).map(x => x[0]).join('') || '?').toUpperCase();
  }
  function _threecxRecordingMayStillArrive(status) {
    const normalized = String(status || '').trim().toLowerCase().replace(/[-_]/g, '');
    return !['missed', 'noanswer', 'busy', 'canceled', 'cancelled', 'rejected'].includes(normalized);
  }
  const THREECX_RECORDING_PENDING_MAX_MS = 30 * 60 * 1000;
  function _threecxRecordingIsPending(call, fallbackTimestamp = 0, nowMs = Date.now()) {
    if (!call?.recordingPending || String(call.source || '').toLowerCase() !== '3cx') return false;
    if (!_threecxRecordingMayStillArrive(call.status)) return false;
    const rawAt = call.endedAt || call.startedAt || call.timestamp || fallbackTimestamp;
    let atMs = Number(rawAt);
    if (!Number.isFinite(atMs) || atMs <= 0) atMs = Date.parse(String(rawAt || ''));
    if (Number.isFinite(atMs) && atMs > 0 && atMs < 1e12) atMs *= 1000;
    if (!Number.isFinite(atMs) || atMs <= 0) return false;
    return Math.max(0, Number(nowMs) - atMs) <= THREECX_RECORDING_PENDING_MAX_MS;
  }
  function _callsIsMissed(call) {
    if (call?.recordingOnly || String(call?.status || '').toLowerCase() === 'recorded') return false;
    return call?.direction === 'inbound' && ['missed', 'rejected', 'noanswer', 'busy', 'canceled'].includes(String(call?.status || '').toLowerCase());
  }
  function _callsOutcome(call) {
    if (call?.recordingOnly || String(call?.status || '').toLowerCase() === 'recorded') return 'Enregistrement 3CX';
    if (_callsIsMissed(call)) return 'Missed incoming call';
    return call.direction === 'outbound' ? 'Outgoing call' : 'Incoming call';
  }

  // ─── Conversations Live — Kanza (AI) + team calls supervision ─────────────
  // Data flow: lib/kanza/routes.js fans out socket events (conversation:event /
  // conversation:alert / conversation:result / kanza:status). Cold load comes
  // from GET /api/conversations/live; history from GET /api/conversations.
  state.conv = {
    inited: false, tab: 'live', live: new Map(), alerts: [], status: null, today: null,
    hist: { rows: [], total: 0, selectedId: null, offset: 0, loading: false, detail: null },
    policy: null, queue: [],
  };
  const CONV_SCORE_LABEL = { hot: 'HOT', warm: 'WARM', cold: 'COLD', needs_human: 'Needs a human' };
  // Emoji render differently per platform and cannot be themed, so every
  // score/state indicator below is a pill or a dot painted from the tab tokens.
  const CONV_SCORE_AR = { hot: 'HOT', warm: 'WARM', cold: 'COLD', needs_human: 'Needs a human' };
  function _convScorePill(score, n) {
    return el('span', { class: 'conv-score conv-score-' + score, text: (n === undefined ? '' : n + ' ') + CONV_SCORE_AR[score] });
  }
  function _convStatePill(tone, text) {
    const sp = el('span', { class: 'conv-chip' + (tone === 'on' ? ' conv-chip-on' : '') }, [el('i', {})]);
    sp.appendChild(document.createTextNode(text));
    return sp;
  }
  const CONV_STATUS_LABEL = { queued: 'f l\'queue', dialing: 'Dialing…', ringing: 'Ringing…', live: 'LIVE', ended: 'Done', no_answer: 'No answer', voicemail: 'messagerie', failed: 'Failed', cancelled: 'Cancelled' };

  function _convFmtDur(sec) { sec = Math.max(0, Math.round(Number(sec) || 0)); return String(Math.floor(sec / 60)).padStart(2, '0') + ':' + String(sec % 60).padStart(2, '0'); }
  function _convWhen(ts) { if (!ts) return ''; const d = new Date(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts); return Number.isNaN(d.getTime()) ? '' : MA.loc(d, 'dt', 'fr-MA', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }); }
  function _convPhoneOf(c) { return c?.phone || (c?.direction === 'outbound' ? c?.to_number : c?.from_number) || ''; }
  function _convWho(c) { return c?.actor === 'human' ? userLabel(c.agent_username, c.agent_username || c.extension || 'Agent') : 'Kenza'; }
  function _convIsAdmin() { return isSalesAdminMe(); }

  function _convSyncNav() {
    const st = state.conv;
    const enabled = st.policy?.enabled;
    const online = !!st.status?.online;
    const liveCount = st.live?.size || 0;
    // Two dots share this state: the one on her menu item and the mirror on the
    // 3LASH trigger. Her tab now lives inside a dropdown (2026-08-30 regroup),
    // so without the mirror an operator would have to open the menu to notice
    // she went offline or that a call is live. Paint both from one place.
    const cls = 'kz-nav-state' + (liveCount ? ' is-live' : (online && enabled !== false ? ' is-on' : ' is-off'));
    const dotTitle = liveCount ? (liveCount + ' calls now') : (online ? (enabled === false ? 'Kenza paused' : 'Kenza online') : 'Kenza offline');
    document.querySelectorAll('.kz-nav-state').forEach(dot => {
      // The mirror carries its own role gate as a data-attribute — className
      // rewrites drop classes only, never attributes, so the gate survives.
      dot.className = cls;
      dot.title = dotTitle;
    });
    const badge = document.getElementById('convTabBadge');
    if (!badge) return;
    const alerts = (st.alerts || []).length;
    const value = alerts || liveCount;
    badge.textContent = String(value || 0);
    badge.classList.toggle('hidden', !value || state.activeView === 'conversations');
    badge.classList.toggle('is-live', !alerts && liveCount > 0);
    badge.title = alerts ? (alerts + ' alerts') : (liveCount ? liveCount + ' live' : '');
    // Her badge sits inside the 3LASH dropdown now — roll it up to the trigger
    // so an alert is visible with the menu closed.
    try { refreshGroupBadges(); } catch (_) { /* badge is advisory */ }
  }

  function _openKanzaInbox() {
    const item = document.querySelector('.view-tab-item[data-view="inbox"][data-channel="kanza"]');
    if (item) item.click();
    else showView('inbox');
  }

  async function _convApi(path, opts = {}) {
    const res = await fetch(path, Object.assign({ headers: authHeaders(opts.body ? { 'Content-Type': 'application/json' } : {}) }, opts, opts.body && typeof opts.body !== 'string' ? { body: JSON.stringify(opts.body) } : {}));
    let data = null; try { data = await res.json(); } catch { data = null; }
    if (!res.ok) { const err = new Error((data && (data.message || data.error)) || ('HTTP ' + res.status)); err.status = res.status; err.data = data; throw err; }
    return data;
  }

  function ensureConversationsInit() {
    const st = state.conv;
    if (!st.inited) {
      st.inited = true;
      document.getElementById('convRefreshBtn')?.addEventListener('click', () => convRefresh(true));
      document.getElementById('convPauseBtn')?.addEventListener('click', () => convTogglePause());
      document.getElementById('ckzPauseBtn')?.addEventListener('click', () => convSetEnabled(false));
      document.getElementById('ckzResumeBtn')?.addEventListener('click', () => convSetEnabled(true));
      document.getElementById('ckzMode')?.addEventListener('change', (e) => convSavePolicy({ mode: e.target.value }));
      document.getElementById('ckzSaveBtn')?.addEventListener('click', () => {
        let cfg = null; try { cfg = JSON.parse(document.getElementById('ckzConfig').value || '{}'); } catch { toast('Invalid JSON', 'error'); return; }
        convSavePolicy({ config: cfg });
      });
      setInterval(() => { if (state.activeView === 'conversations') convCampStatus(); }, 15000);
      document.getElementById('ckzCallBtn')?.addEventListener('click', () => {
        const phone = String(document.getElementById('ckzPhone')?.value || '').replace(/\D/g, '');
        if (phone.length < 9) { toast('Invalid number', 'error'); return; }
        kanzaRequestCall({ phone, name: '' }, document.getElementById('ckzCallBtn'));
      });
      ['convPeriod', 'convFrom', 'convTo', 'convScore', 'convActor'].forEach(id => document.getElementById(id)?.addEventListener('change', () => { st.hist.offset = 0; convLoadHistory(); }));
      document.getElementById('convLoadMore')?.addEventListener('click', () => { st.hist.offset = st.hist.rows.length; convLoadHistory(true); });
      setInterval(() => { if (state.activeView === 'conversations') _convTick(); }, 1000);
    }
    kzpInit();
    convRefresh(false);
  }

  // The page has no tabs any more — it is one list plus the detail panel. The
  // older call sites that ask for a "tab" all mean "put this on screen", which
  // the detail panel already does; this keeps them harmless.
  function convSetTab(tab) { state.conv.tab = tab; }

  async function convRefresh(force) {
    const st = state.conv;
    try {
      const live = await _convApi('/api/conversations/live');
      st.live.clear();
      for (const c of (live.calls || [])) st.live.set(c.id, Object.assign({ lines: _convLinesFromTail(c.tail) }, c));
      st.human = live.human || [];
      if (live.controller) convOnStatus(live.controller);
      convRenderLive();
    } catch (e) { if (force) toast('Conversations: ' + (e.message || e), 'error'); }
    try { const s = await _convApi('/api/kanza/status'); convOnStatus(s); } catch { /* */ }
    convLoadKanza();                     // policy + settings card
    kzpLoad({ quiet: !force });          // the list itself
  }

  function _convLinesFromTail(tail) {
    const lines = [];
    for (const ev of (tail || [])) {
      if (ev.type === 'transcript.final' || ev.type === 'transcript.partial') lines.push({ role: ev.payload?.role, text: ev.payload?.text, ts: ev.ts, partial: ev.type === 'transcript.partial' });
      else if (ev.type === 'alert') lines.push({ role: 'alert', text: (ev.payload?.hits || []).join(', ') || ev.payload?.reason, ts: ev.ts });
      else if (ev.type === 'tool') lines.push({ role: 'tool', text: ev.payload?.name || '', ts: ev.ts });
    }
    return lines.slice(-40);
  }

  function convOnStatus(s) {
    const st = state.conv;
    if (!s) return;
    st.status = Object.assign({}, st.status || {}, s);
    if (s.today) st.today = s.today;
    if (s.policy) st.policy = Object.assign({}, st.policy || {}, s.policy);
    const chip = document.getElementById('convCtrlChip');
    if (chip) {
      const on = !!st.status.online;
      chip.classList.toggle('conv-chip-on', on); chip.classList.toggle('conv-chip-off', !on);
      const enabled = st.policy ? st.policy.enabled : null;
      chip.querySelector('span').textContent = !on ? 'Kenza offline' : (enabled === false ? 'Kenza paused' : 'Kenza working · online');
    }
    const pb = document.getElementById('convPauseBtn');
    if (pb) {
      pb.classList.toggle('hidden', !_convIsAdmin() || !st.policy);
      const paused = st.policy && st.policy.enabled === false;
      pb.querySelector('span').textContent = paused ? 'Start Kenza' : 'Pause Kenza';
      pb.classList.toggle('is-paused', !!paused);
    }
    const t = st.today || {};
    const set = (id, v) => { const n = document.getElementById(id); if (n) n.textContent = String(v ?? 0); };
    set('ckCalls', t.calls); set('ckAnswered', t.answered); set('ckHot', t.hot); set('ckHuman', t.needs_human); set('ckMinutes', Math.round((Number(t.seconds) || 0) / 60)); set('ckWaiting', st.status?.status?.queue_pending);
    _convSyncNav();
    renderKanzaChatStrip();
    kzRenderSidebarStatus();
    if (st.tab === 'kanza') convRenderKanza();
  }

  function convOnEvent(ev) {
    const st = state.conv;
    if (!ev || !ev.call_id) return;
    const type = ev.type, p = ev.payload || {};
    let c = st.live.get(ev.call_id);
    if (type === 'call.started') {
      c = Object.assign({ lines: [], id: ev.call_id }, c || {}, p, { status: p.status || 'live' });
      st.live.set(ev.call_id, c);
    } else if (!c) {
      // event for a call we haven't loaded (page opened mid-call) — pull the live list
      convRefresh(false); return;
    }
    if (type === 'transcript.partial' || type === 'transcript.final') {
      const last = c.lines[c.lines.length - 1];
      if (last && last.partial && last.role === p.role) { last.text = p.text; last.partial = type === 'transcript.partial'; last.ts = ev.ts; }
      else c.lines.push({ role: p.role, text: p.text, ts: ev.ts, partial: type === 'transcript.partial' });
      if (c.lines.length > 60) c.lines.splice(0, c.lines.length - 60);
    }
    if (type === 'mood') c.mood = p.level || p.mood || c.mood;
    if (type === 'step') c.step = p.step || c.step;
    if (type === 'tool') c.lines.push({ role: 'tool', text: p.name || '', ts: ev.ts });
    if (type === 'call.answered') { c.status = 'live'; c.answered_at = p.answered_at || ev.ts; }
    if (type === 'call.ended') { c.status = p.status || 'ended'; c.ended_at = p.ended_at || ev.ts; setTimeout(() => { st.live.delete(ev.call_id); convRenderLive(); }, 15000); }
    convRenderLive();
    renderKanzaChatStrip();
  }

  function convOnAlert(a) {
    const st = state.conv;
    if (!a) return;
    st.alerts.unshift(Object.assign({ at: Date.now() }, a));
    st.alerts = st.alerts.slice(0, 8);
    const c = st.live.get(a.call_id);
    if (c) { c.alert = a.level; c.lines.push({ role: 'alert', text: (a.hits || []).join(', ') || a.reason, ts: Date.now() }); }
    if (_convIsAdmin()) {
      const who = a.lead_name || displayPhone(a.phone || '') || 'Call';
      toast((a.level === 'red' ? 'Important alert: ' : 'Alert: ') + who + ' — ' + ((a.hits || []).join(', ') || a.reason || ''), a.level === 'red' ? 'error' : '');
      try { if (a.level === 'red' && typeof playNotificationSound === 'function') playNotificationSound(); } catch { /* */ }
    }
    _convSyncNav();
    convRenderLive();
  }

  function convOnResult(r) {
    const st = state.conv;
    const c = r && r.call;
    if (!c) return;
    const live = st.live.get(c.id);
    if (live) { live.score = c.score; live.outcome = c.outcome; live.status = c.status || 'ended'; }
    if (_convIsAdmin() && c.score) {
      toast('Kenza: ' + (CONV_SCORE_LABEL[c.score] || c.score) + ' — ' + (c.lead_name || displayPhone(_convPhoneOf(c))) + (r.stage_move ? ' → ' + r.stage_move.to : ''), c.score === 'hot' ? 'success' : '');
    }
    if (st.tab === 'history') { st.hist.offset = 0; convLoadHistory(); }
    convRenderLive();
    renderKanzaChatStrip();
  }

  function _convTick() {
    document.querySelectorAll('[data-conv-timer]').forEach(n => {
      const start = Number(n.getAttribute('data-conv-timer')) || 0;
      if (start) n.textContent = _convFmtDur((Date.now() - start) / 1000);
    });
  }

  function convRenderLive() {
    const st = state.conv;
    const list = document.getElementById('convLiveList');
    if (!list) return;
    const countEl = document.getElementById('convLiveCount'); if (countEl) countEl.textContent = String(st.live.size);
    _convSyncNav();
    const alertsBox = document.getElementById('convAlerts');
    if (alertsBox) {
      alertsBox.classList.toggle('hidden', !st.alerts.length);
      alertsBox.replaceChildren(...st.alerts.slice(0, 4).map(a => el('div', { class: 'conv-alert conv-alert-' + (a.level || 'amber') }, [
        lucideIcon(a.level === 'red' ? 'circle-alert' : 'triangle-alert'),
        el('span', { text: (a.lead_name || displayPhone(a.phone || '') || '') + ' — ' + ((a.hits || []).join(', ') || a.reason || '') + (a.text ? ' · «' + a.text.slice(0, 80) + '»' : '') }),
        el('small', { text: _convWhen(a.at) }),
      ])));
    }
    list.replaceChildren();
    const human = (st.human || []).filter(h => ![...st.live.values()].some(c => c.actor === 'human' && String(c.extension || '') === String(h.extension || '')));
    if (!st.live.size && !human.length) {
      const next = (typeof kzIn !== 'undefined' ? (kzIn.people || []).find(r => r.status === 'pending') : null);
      const waiting = Number(kzIn?.counts?.waiting || st.status?.status?.queue_pending || 0);
      const primary = el('button', { class: 'calls-action primary', type: 'button' }, [lucideIcon('inbox'), el('span', { text: 'Open Kenza Inbox' })]);
      primary.addEventListener('click', _openKanzaInbox);
      const history = el('button', { class: 'calls-action', type: 'button' }, [lucideIcon('history'), el('span', { text: 'See results' })]);
      history.addEventListener('click', () => convSetTab('history'));
      list.appendChild(el('section', { class: 'conv-empty-board' }, [
        el('span', { class: 'conv-empty-icon' }, [lucideIcon('headset')]),
        el('div', { class: 'conv-empty-copy' }, [
          el('strong', { text: 'Kenza is not on a call right now' }),
          el('span', { text: next ? ('She has ' + waiting + ' waiting · next ' + kzWhen(next.scheduled_at)) : 'Hand her a client with Assign inside any conversation — she starts two minutes later.' }),
        ]),
        el('div', { class: 'conv-empty-actions' }, [primary, history]),
      ]));
      refreshIcons(list); return;
    }
    const calls = [...st.live.values()].sort((a, b) => (new Date(b.started_at).getTime() || 0) - (new Date(a.started_at).getTime() || 0));
    for (const c of calls) list.appendChild(_convLiveCard(c));
    for (const h of human) list.appendChild(_convHumanCard(h));
    refreshIcons(list);
    _convTick();
  }

  // Team call in progress (from call_logs) — no transcript yet; supervisor can start one (ARI snoop).
  function _convHumanCard(h) {
    const card = el('article', { class: 'conv-card conv-live is-human is-pending', 'data-ext': h.extension || '' });
    const startMs = new Date(h.started_at).getTime() || Date.now();
    card.appendChild(el('div', { class: 'conv-live-head' }, [
      el('div', { class: 'conv-live-who' }, [
        el('span', { class: 'conv-actor human', text: userLabel(h.agent_username, h.agent_name || h.agent_username || ('ext ' + (h.extension || '?'))) }),
        el('span', { class: 'conv-arrow', text: h.direction === 'outbound' ? '→' : '←' }),
        el('strong', { text: h.lead_name || displayPhone(h.phone || '') || 'Unknown' }),
        h.lead_name ? el('span', { class: 'conv-phone', text: displayPhone(h.phone || '') }) : null,
        h.lead_stage ? el('span', { class: 'calls-stage-pill', text: (typeof PIPELINE_LABELS !== 'undefined' && PIPELINE_LABELS[h.lead_stage]) || h.lead_stage }) : null,
      ]),
      el('div', { class: 'conv-live-meta' }, [
        el('span', { class: 'conv-timer', 'data-conv-timer': String(startMs), text: '00:00' }),
        el('span', { class: 'conv-status conv-status-' + (h.status === 'answered' ? 'live' : 'ringing'), text: h.status === 'answered' ? 'LIVE' : 'Ringing…' }),
      ]),
    ]));
    card.appendChild(el('div', { class: 'conv-transcript' }, [el('div', { class: 'conv-line conv-line-sys', text: 'No transcript — press «Live transcript» to start it (it does not affect the call audio).' })]));
    if (_convIsAdmin()) {
      const acts = el('div', { class: 'conv-actions' });
      acts.appendChild(el('span', { class: 'conv-help', text: 'To listen: use Listen in 3CX on ext ' + (h.extension || '?') }));
      const b = el('button', { class: 'calls-action primary', type: 'button' }, [lucideIcon('captions'), el('span', { text: 'Transcript live' })]);
      b.addEventListener('click', async () => {
        b.disabled = true;
        try { await _convApi('/api/kanza/listen', { method: 'POST', body: { extension: h.extension, agent_username: h.agent_username || '', call_id: h.call_id || '' } }); toast('Live transcript starting…', 'success'); }
        catch (e) { toast(e.message || e, 'error'); b.disabled = false; }
      });
      acts.appendChild(b);
      card.appendChild(acts);
    }
    return card;
  }

  function _convLiveCard(c) {
    const isKanza = c.actor !== 'human';
    const ended = !['live', 'dialing', 'ringing', 'queued'].includes(String(c.status || 'live'));
    const card = el('article', { class: 'conv-card conv-live' + (isKanza ? ' is-kanza' : ' is-human') + (c.alert === 'red' ? ' is-red' : c.alert === 'amber' ? ' is-amber' : '') + (ended ? ' is-ended' : ''), 'data-call': c.id });
    const startMs = new Date(c.answered_at || c.started_at).getTime() || Date.now();
    const head = el('div', { class: 'conv-live-head' }, [
      el('div', { class: 'conv-live-who' }, [
        el('span', { class: 'conv-actor' + (isKanza ? ' kanza' : ' human'), text: _convWho(c) }),
        el('span', { class: 'conv-arrow', text: c.direction === 'outbound' ? '→' : '←' }),
        el('strong', { text: c.lead_name || displayPhone(_convPhoneOf(c)) || 'Unknown' }),
        c.lead_name ? el('span', { class: 'conv-phone', text: displayPhone(_convPhoneOf(c)) }) : null,
        c.lead_stage ? el('span', { class: 'calls-stage-pill', text: (typeof PIPELINE_LABELS !== 'undefined' && PIPELINE_LABELS[c.lead_stage]) || c.lead_stage }) : null,
        c.target_agent ? el('span', { class: 'conv-phone', text: 'to ' + c.target_agent }) : null,
      ]),
      el('div', { class: 'conv-live-meta' }, [
        el('span', { class: 'conv-mood conv-mood-' + (c.mood || 'green'), title: 'mood' }),
        c.step ? el('span', { class: 'conv-step', text: 'Step ' + c.step }) : null,
        el('span', { class: 'conv-timer', 'data-conv-timer': ended ? '' : String(startMs), text: ended ? _convFmtDur(c.duration_seconds || ((new Date(c.ended_at).getTime() - startMs) / 1000)) : '00:00' }),
        el('span', { class: 'conv-status conv-status-' + (c.status || 'live'), text: CONV_STATUS_LABEL[c.status] || c.status || 'LIVE' }),
        c.score ? el('span', { class: 'conv-score conv-score-' + c.score, text: CONV_SCORE_LABEL[c.score] || c.score }) : null,
      ]),
    ]);
    card.appendChild(head);
    const tr = el('div', { class: 'conv-transcript' });
    for (const ln of (c.lines || []).slice(-14)) tr.appendChild(_convLine(ln));
    if (!c.lines || !c.lines.length) tr.appendChild(el('div', { class: 'conv-line conv-line-sys', text: c.status === 'dialing' || c.status === 'ringing' ? 'Dialing…' : 'Waiting for audio…' }));
    card.appendChild(tr);
    if (_convIsAdmin() && !ended) {
      const acts = el('div', { class: 'conv-actions' });
      if (c.spy_code) { const b = el('button', { class: 'calls-action', type: 'button' }, [lucideIcon('headphones'), el('span', { text: 'Listen' })]); b.addEventListener('click', () => cxDial(String(c.spy_code), 'Listen ' + (c.lead_name || ''), b)); acts.appendChild(b); }
      else acts.appendChild(el('span', { class: 'conv-help', text: 'To listen: use Listen in 3CX on ext ' + (c.extension || '1077') }));
      if (!isKanza) {
        const s = el('button', { class: 'calls-action', type: 'button' }, [lucideIcon('captions-off'), el('span', { text: 'Stop transcript' })]);
        s.addEventListener('click', async () => { s.disabled = true; try { await _convApi('/api/kanza/listen/stop', { method: 'POST', body: { extension: c.extension } }); } catch (e) { toast(e.message || e, 'error'); s.disabled = false; } });
        acts.appendChild(s);
      }
      if (isKanza) {
        const w = el('button', { class: 'calls-action', type: 'button' }, [lucideIcon('message-square-text'), el('span', { text: 'Whisper' })]);
        w.addEventListener('click', () => { const text = prompt('Instruction for Kenza (the client cannot hear it):', ''); if (text && text.trim()) convCmd(c.id, 'whisper', { text: text.trim() }, w); });
        const h = el('button', { class: 'calls-action primary', type: 'button' }, [lucideIcon('hand'), el('span', { text: 'Take the call' })]);
        h.addEventListener('click', () => { const ext = prompt('Your ext (3CX):', localStorage.getItem('kanza_my_ext') || ''); if (!ext) return; localStorage.setItem('kanza_my_ext', ext); convCmd(c.id, 'handoff', { to_ext: ext, to_name: state.me?.displayName || state.me?.username || '' }, h); });
        const e = el('button', { class: 'calls-action danger', type: 'button' }, [lucideIcon('phone-off'), el('span', { text: 'End' })]);
        e.addEventListener('click', () => { if (confirm('End the call now?')) convCmd(c.id, 'end', { reason: 'supervisor' }, e); });
        acts.appendChild(w); acts.appendChild(h); acts.appendChild(e);
      }
      card.appendChild(acts);
    }
    return card;
  }

  function _convLine(ln) {
    if (ln.role === 'alert') return el('div', { class: 'conv-line conv-line-alert' }, [lucideIcon('triangle-alert'), el('span', { text: ln.text || '' })]);
    if (ln.role === 'tool') return el('div', { class: 'conv-line conv-line-sys' }, [lucideIcon('settings-2'), el('span', { text: ln.text || '' })]);
    const who = ln.role === 'agent' ? 'Kenza' : (ln.role === 'caller' ? 'Client' : (ln.role || ''));
    return el('div', { class: 'conv-line conv-line-' + (ln.role || 'x') + (ln.partial ? ' is-partial' : '') }, [el('b', { text: who + ':' }), el('span', { text: ' ' + (ln.text || '') })]);
  }

  async function convCmd(callId, cmd, args, btn) {
    if (btn) btn.disabled = true;
    try {
      await _convApi('/api/kanza/calls/' + encodeURIComponent(callId) + '/' + cmd, { method: 'POST', body: args || {} });
      toast({ whisper: 'Sent to Kenza', handoff: 'Kenza is transferring the call to you', end: 'Kenza is ending the call…' }[cmd] || 'OK', 'success');
    } catch (e) { toast((e.message || e), 'error'); }
    finally { if (btn) btn.disabled = false; }
  }

  async function convTogglePause() { const paused = state.conv.policy && state.conv.policy.enabled === false; return convSetEnabled(!!paused); }
  async function convSetEnabled(enabled) {
    try {
      const r = await _convApi('/api/kanza/' + (enabled ? 'resume' : 'pause'), { method: 'POST', body: {} });
      state.conv.policy = Object.assign({}, state.conv.policy || {}, { enabled: !!r.enabled });
      toast(enabled ? 'Kenza is working' : 'Kenza paused (live calls finish)', enabled ? 'success' : '');
      convOnStatus({}); convLoadKanza();
    } catch (e) { toast(e.message || e, 'error'); }
  }
  async function convSavePolicy(patch) {
    try {
      const r = await _convApi('/api/kanza/policy', { method: 'PUT', body: patch });
      state.conv.policy = r.policy; toast('Policy saved', 'success'); convRenderKanza(); convOnStatus({});
    } catch (e) { toast(e.message || e, 'error'); }
  }

  async function kanzaRequestCall({ phone, name, leadId }, btn, { delaySeconds = 120, skipConfirm = false } = {}) {
    if (!_convIsAdmin()) { toast('Sales admin only', 'error'); return; }
    const who = name || displayPhone(phone || '');
    const safeDelay = Math.max(0, Math.min(Number(delaySeconds) || 0, 3600));
    if (!skipConfirm && !confirm('Hand ' + who + ' to Kenza? She starts in ' + Math.max(1, Math.round(safeDelay / 60)) + ' minutes.')) return;
    if (btn) btn.disabled = true;
    try {
      const request = (overrideDoNotCall = false) => _convApi('/api/kanza/calls', {
        method: 'POST',
        body: { phone, lead_id: leadId || undefined, reason: 'manual', delay_seconds: safeDelay, override_do_not_call: overrideDoNotCall || undefined },
      });
      let overrideDoNotCall = false;
      let r;
      try {
        r = await request(false);
      } catch (e) {
        if (e?.status !== 409 || e?.data?.error !== 'do_not_call') throw e;
        const approved = confirm(
          'This client is marked «Do not call».\n\n' +
          'Are you sure you want one exceptional call from Kenza ' +
          (safeDelay ? 'in ' + Math.max(1, Math.round(safeDelay / 60)) + ' min?' : 'now?')
        );
        if (!approved) { toast('Do-not-call kept · nothing was scheduled', ''); return; }
        overrideDoNotCall = true;
        r = await request(true);
      }
      toast((r.mode === 'dry_run' ? 'Dry run: ' : '') + (overrideDoNotCall ? 'One-time override · ' : '') + 'Handed to Kenza · ' + (safeDelay ? 'starts in ' + Math.max(1, Math.round(safeDelay / 60)) + ' min' : 'starts now') + (r.online ? '' : ' · waiting for the line to come back'), 'success');
      await kzLoadInbox({ quiet: true });
      renderKanzaChatStrip();
      if (state.activeView !== 'conversations') { /* stay on the chat; the Live tab badge will light up */ }
    } catch (e) { toast(e.message || e, 'error'); }
    finally { if (btn) btn.disabled = false; }
  }

  // ── history ──
  function _convPeriodRange() {
    const p = document.getElementById('convPeriod')?.value || 'this_week';
    const d = new Date(); const iso = x => x.toISOString().slice(0, 10);
    const from = document.getElementById('convFrom'), to = document.getElementById('convTo');
    if (p === 'custom') return { from: from?.value || '', to: to?.value || '' };
    if (p === 'today') return { from: iso(d), to: iso(d) };
    if (p === 'yesterday') { const y = new Date(d.getTime() - 864e5); return { from: iso(y), to: iso(y) }; }
    if (p === 'this_month') { return { from: iso(new Date(d.getFullYear(), d.getMonth(), 1)), to: iso(d) }; }
    const day = (d.getDay() + 6) % 7; const mon = new Date(d.getTime() - day * 864e5); return { from: iso(mon), to: iso(d) };
  }
  async function convLoadHistory(append) {
    const st = state.conv.hist;
    if (st.loading) return; st.loading = true;
    try {
      const r = _convPeriodRange();
      const q = new URLSearchParams({ from: r.from, to: r.to, score: document.getElementById('convScore')?.value || '', actor: document.getElementById('convActor')?.value || '', limit: '50', offset: String(append ? st.offset : 0) });
      const data = await _convApi('/api/conversations?' + q.toString());
      st.rows = append ? st.rows.concat(data.calls || []) : (data.calls || []); st.total = data.total || 0;
      if (!append && !st.rows.find(c => c.id === st.selectedId)) st.selectedId = st.rows[0]?.id || null;
      convRenderHistory();
      if (st.selectedId) convLoadDetail(st.selectedId);
    } catch (e) { toast('Historique: ' + (e.message || e), 'error'); }
    finally { st.loading = false; }
  }
  function convRenderHistory() {
    const st = state.conv.hist;
    const list = document.getElementById('convHistList'); if (!list) return;
    const cnt = document.getElementById('convCount'); if (cnt) cnt.textContent = st.total + ' call' + (st.total === 1 ? '' : 's');
    list.replaceChildren();
    if (!st.rows.length) list.appendChild(el('div', { class: 'calls-empty' }, [lucideIcon('radio'), el('strong', { text: 'Nothing in this period' })]));
    for (const c of st.rows) {
      const row = el('button', { class: 'calls-row source-3cx' + (c.id === st.selectedId ? ' is-selected' : ''), type: 'button' });
      row.appendChild(el('span', { class: 'calls-row-avatar ' + (c.actor === 'human' ? 'source-whatsapp' : 'source-3cx'), text: c.actor === 'human' ? (adminLabelFor(c.agent_username) ? 'A' + adminLabelFor(c.agent_username).slice(-1) : (c.agent_username || 'A').slice(0, 2).toUpperCase()) : 'K' }));
      const main = el('span', { class: 'calls-row-main' });
      const l1 = el('span', { class: 'calls-row-line1' }, [el('span', { class: 'calls-row-name', text: c.lead_name || displayPhone(_convPhoneOf(c)) || 'Unknown' })]);
      if (c.score) l1.appendChild(el('span', { class: 'conv-score conv-score-' + c.score, text: CONV_SCORE_LABEL[c.score] || c.score }));
      if (c.recording_key) l1.appendChild(el('span', { class: 'calls-recorded-pill' }, [lucideIcon('audio-lines'), el('span', { text: 'REC' })]));
      main.appendChild(l1);
      const sub = el('span', { class: 'calls-row-line2' }, [
        lucideIcon(c.direction === 'outbound' ? 'phone-outgoing' : 'phone-incoming'),
        el('span', { text: _convWho(c) + ' · ' + (CONV_STATUS_LABEL[c.status] || c.status || '') + ' · ' + _convFmtDur(c.duration_seconds) }),
      ]);
      if (c.up_votes || c.down_votes) sub.appendChild(el('span', { class: 'conv-votes' }, [
        lucideIcon('thumbs-up'), el('span', { text: String(c.up_votes || 0) }),
        lucideIcon('thumbs-down'), el('span', { text: String(c.down_votes || 0) }),
      ]));
      main.appendChild(sub);
      row.appendChild(main);
      row.appendChild(el('span', { class: 'calls-row-time', text: _convWhen(c.started_at) }));
      row.addEventListener('click', () => { st.selectedId = c.id; convRenderHistory(); convLoadDetail(c.id); });
      list.appendChild(row);
    }
    document.getElementById('convLoadMore')?.classList.toggle('hidden', st.rows.length >= st.total);
    refreshIcons(list);
  }
  async function convLoadDetail(id) {
    const pane = document.getElementById('convDetail'); if (!pane) return;
    try {
      const d = await _convApi('/api/conversations/' + encodeURIComponent(id));
      state.conv.hist.detail = d;
      convRenderDetail(d);
    } catch (e) { pane.replaceChildren(el('div', { class: 'calls-detail-empty' }, [el('h3', { text: 'Could not open the call' }), el('p', { text: e.message || '' })])); }
  }
  function convRenderDetail(d) {
    const pane = document.getElementById('convDetail'); if (!pane) return;
    const c = d.call || {}; const a = c.analysis || {};
    const card = el('div', { class: 'calls-detail-card source-3cx conv-detail' });
    card.appendChild(el('div', { class: 'calls-detail-top' }, [
      el('div', { class: 'calls-detail-contact' }, [
        el('div', { class: 'calls-detail-avatar source-3cx', text: c.actor === 'human' ? (adminLabelFor(c.agent_username) ? 'A' + adminLabelFor(c.agent_username).slice(-1) : (c.agent_username || 'A').slice(0, 2).toUpperCase()) : 'K' }),
        el('div', {}, [el('h3', { text: c.lead_name || displayPhone(_convPhoneOf(c)) || 'Unknown' }), el('p', { text: _convWho(c) + ' · ' + (c.direction === 'outbound' ? 'Outbound' : 'Inbound') + ' · ' + _convWhen(c.started_at) + ' · ' + _convFmtDur(c.duration_seconds) + (c.lead_stage ? ' · ' + c.lead_stage : '') })]),
      ]),
      c.score ? el('span', { class: 'conv-score conv-score-' + c.score, text: CONV_SCORE_LABEL[c.score] || c.score }) : el('span', { class: 'calls-detail-status', text: CONV_STATUS_LABEL[c.status] || c.status || '' }),
    ]));
    const acts = el('div', { class: 'calls-detail-actions' });
    const phone = String(_convPhoneOf(c) || '').replace(/\D/g, '');
    if (phone) {
      const chat = (state.chats || []).find(x => String(x.id || '').startsWith(phone) || String(x.phone || '').replace(/\D/g, '') === phone);
      if (chat) { const b = el('button', { class: 'calls-action primary', type: 'button' }, [lucideIcon('message-circle'), el('span', { text: 'Open chat' })]); b.addEventListener('click', () => { showView('inbox'); selectConversation(chat.id); }); acts.appendChild(b); }
      const dial = el('button', { class: 'calls-action', type: 'button' }, [lucideIcon('phone-call'), el('span', { text: 'Call now' })]); dial.addEventListener('click', () => cxDial(phone, c.lead_name || '', dial)); acts.appendChild(dial);
    }
    const up = el('button', { class: 'calls-action', type: 'button', title: 'Score is right', 'aria-label': 'Score is right' }, [lucideIcon('thumbs-up')]);
    const down = el('button', { class: 'calls-action', type: 'button', title: 'Score is wrong / bad behaviour', 'aria-label': 'Score is wrong' }, [lucideIcon('thumbs-down')]);
    up.addEventListener('click', () => convFeedback(c.id, 'up', up));
    down.addEventListener('click', () => convFeedback(c.id, 'down', down));
    acts.appendChild(up); acts.appendChild(down);
    card.appendChild(acts);
    // Recording — always first (Ayoub 2026-08-23: audio + a big clear note on every Kanza call).
    if (d.recording_url) card.appendChild(el('div', { class: 'conv-audio' }, [el('audio', { controls: 'controls', preload: 'none', src: d.recording_url })]));
    else if (c.actor !== 'human') {
      // No file yet — say WHICH of the three reasons it is. A live row used to
      // render nothing at all here, which reads as "the player is broken"; and
      // "still uploading" is a lie once the call ended hours ago.
      const endedMs = c.ended_at ? Date.parse(c.ended_at) : NaN;
      const note = c.status === 'live'
        ? 'The recording appears once the call ends.'
        : (Number.isFinite(endedMs) && Date.now() - endedMs > 10 * 60 * 1000
          ? 'No recording for this call.'
          : 'Recording still uploading…');
      card.appendChild(el('div', { class: 'conv-audio conv-audio-pending' }, [lucideIcon('audio-lines'), el('span', { text: note })]));
    }
    // Big note: what the prospect wanted + what happened.
    const sum = a.summary_darija || a.summary || a.note_darija;
    const bookingEv = (d.events || []).find(e => e.type === 'booking');
    const booked = bookingEv ? bookingEv.payload || {} : null;
    if (sum || a.next_action || a.goal || booked) {
      const box = el('div', { class: 'conv-analysis conv-note' });
      box.appendChild(el('div', { class: 'conv-note-head' }, [
        lucideIcon('notebook-pen'),
        el('span', { text: 'Note' }),
        c.score ? el('span', { class: 'conv-score conv-score-' + c.score, text: CONV_SCORE_LABEL[c.score] || c.score }) : null,
        a.analysis_source === 'audio' ? el('span', { class: 'conv-tag', text: 'from the audio' }) : null,
      ].filter(Boolean)));
      if (sum) box.appendChild(el('p', { class: 'conv-sum conv-sum-big', text: String(sum) }));
      if (booked) box.appendChild(el('p', { class: 'conv-booked' }, [lucideIcon('calendar-check'), el('span', { text: 'Test booked: ' + [booked.date, booked.time].filter(Boolean).join(' at ') + (booked.teacher ? ' — ' + booked.teacher : '') })]));
      const chips = [['Goal', a.goal], ['Challenge', a.challenge], ['Level', a.level_hint], ['Time', a.slot_pref || a.preferred_time], ['Wants the test', a.wants_test === true ? 'Yes' : a.wants_test === false ? 'No' : null]].filter(x => x[1]);
      if (chips.length) box.appendChild(el('div', { class: 'conv-chips' }, chips.map(([k, v]) => el('span', { class: 'conv-tag', text: k + ': ' + v }))));
      const kv = [['Next step', a.next_action && a.next_action.type ? a.next_action.type + (a.next_action.when_text ? ' — ' + a.next_action.when_text : '') : null], ['Objections', Array.isArray(a.objections) && a.objections.length ? a.objections.join(' · ') : null], ['Mistakes by Kenza', Array.isArray(a.kanza_mistakes) && a.kanza_mistakes.length ? a.kanza_mistakes.join(' · ') : null], ['ASR', a.asr_quality], ['Confidence', c.confidence != null ? Math.round(Number(c.confidence) * 100) + '%' : null]];
      for (const [k, v] of kv) if (v) box.appendChild(el('div', { class: 'conv-kv' }, [el('span', { text: k }), el('b', { text: String(v) })]));
      card.appendChild(box);
    }
    const tr = el('div', { class: 'conv-transcript conv-transcript-full' });
    // Prefer the clean Darija transcript the audio pass wrote (the live ASR turns Darija into noise).
    const clean = Array.isArray(a.transcript_darija) && a.transcript_darija.length ? a.transcript_darija : null;
    const lines = clean ? [] : (d.events || []).filter(e => e.type === 'transcript.final' || e.type === 'alert' || e.type === 'tool' || e.type === 'whisper');
    if (clean) for (const e of clean) if (e && e.text) tr.appendChild(_convLine({ role: e.role, text: e.text }));
    if (!clean && !lines.length && Array.isArray(c.transcript)) for (const e of c.transcript) tr.appendChild(_convLine({ role: e.role, text: e.text }));
    for (const e of lines) {
      if (e.type === 'alert') tr.appendChild(_convLine({ role: 'alert', text: (e.payload?.hits || []).join(', ') || e.payload?.reason }));
      else if (e.type === 'tool') tr.appendChild(_convLine({ role: 'tool', text: e.payload?.name }));
      else if (e.type === 'whisper') tr.appendChild(_convLine({ role: 'tool', text: 'whisper (' + (e.payload?.by || '') + '): ' + (e.payload?.text || '') }));
      else tr.appendChild(_convLine({ role: e.payload?.role, text: e.payload?.text }));
    }
    if (!tr.childNodes.length) tr.appendChild(el('div', { class: 'conv-line conv-line-sys', text: 'No transcript' }));
    card.appendChild(el('h4', { class: 'conv-h4', text: clean ? 'Transcript (cleaned from the audio)' : 'Transcript' })); card.appendChild(tr);
    pane.replaceChildren(card); refreshIcons(pane);
  }
  async function convFeedback(id, verdict, btn) {
    const reason = verdict === 'down' ? (prompt('Why? (wrong score, repeated itself, talked price…)', '') || '') : '';
    if (btn) btn.disabled = true;
    try { await _convApi('/api/conversations/' + encodeURIComponent(id) + '/feedback', { method: 'POST', body: { verdict, reason } }); toast('Thanks — recorded', 'success'); }
    catch (e) { toast(e.message || e, 'error'); } finally { if (btn) btn.disabled = false; }
  }

  // ── Kanza tab ──
  async function convLoadKanza() {
    convCampStatus();
    try { const r = await _convApi('/api/kanza/policy'); state.conv.policy = r.policy; } catch { /* non-admin */ }
    try { const s = await _convApi('/api/kanza/status'); state.conv.status = Object.assign({}, state.conv.status || {}, s); state.conv.today = s.today || state.conv.today; } catch { /* */ }
    convRenderKanza();
  }
  function convRenderKanza() {
    const st = state.conv; const pol = st.policy || {};
    const t = st.today || {}; const sysStat = st.status?.status || {};

    // Working / stopped is the one thing Ayoub checks first, so it is a pill
    // next to her name rather than a row in a settings list.
    const enabledEl = document.getElementById('ckzEnabled');
    if (enabledEl) {
      const on = pol.enabled === true, off = pol.enabled === false;
      enabledEl.className = 'kz-state' + (on ? ' is-on' : off ? ' is-off' : '');
      enabledEl.textContent = on ? 'Working' : off ? 'Paused' : '—';
    }
    const online = document.getElementById('ckzOnline');
    if (online) {
      const up = !!st.status?.online;
      online.className = 'kz-dot-line ' + (up ? 'is-on' : 'is-off');
      online.textContent = (up ? 'online' : 'offline') + (st.status?.lastSeenAt ? ' · ' + _convWhen(st.status.lastSeenAt) : '');
    }
    const mins = Math.round((t.seconds || 0) / 60);
    const metrics = document.getElementById('ckzMetrics');
    if (metrics) metrics.replaceChildren(...[
      ['Calls today', t.calls ?? 0, ''],
      ['Answered', t.answered ?? 0, ''],
      ['Booked a test', t.booked ?? 0, (t.booked ?? 0) > 0 ? 'is-good' : ''],
      ['Need a human', t.needs_human ?? 0, ''],
      ['Minutes on the phone', mins, ''],
      ['Waiting', sysStat.queue_pending ?? 0, (sysStat.queue_pending ?? 0) > 0 ? 'is-kz' : ''],
    ].map(([label, value, tone]) => el('div', { class: 'kz-metric' + (tone ? ' ' + tone : '') }, [
      el('b', { text: String(value) }), el('span', { text: label }),
    ])));

    const mode = document.getElementById('ckzMode'); if (mode && pol.mode) mode.value = pol.mode;
    const upd = document.getElementById('ckzUpdated');
    if (upd) upd.textContent = pol.updated_at ? 'Last change: ' + _convWhen(pol.updated_at) + (pol.updated_by ? ' · ' + pol.updated_by : '') : '';
    const hint = document.getElementById('ckzCampHint');
    if (hint) hint.innerHTML = pol.enabled === true
      ? 'Kenza is <b>working</b> — she starts as soon as you hand her the list (inside the allowed hours).'
      : 'Kenza is <b>paused</b> — the list is saved, but she will not start calling until you press <b>Start her</b> above.';
    const cfg = document.getElementById('ckzConfig');
    if (cfg && pol.config && document.activeElement !== cfg) cfg.value = JSON.stringify(pol.config, null, 2);
  }

  // ── Kanza campaigns: give her a list, she works through it ──
  const CAMP_STAGE_LABEL = { new: 'New', no_answer: 'No answer', contacted: 'Contacted', no_show: 'No show', book_test: 'Test booked', test_done: 'Test done' };
  // Both columns hold slugs the team never chose: `leads.source` mixes real
  // sources with course durations (6mois is the 2nd most common value in the
  // table), and bot_learning_goal holds English sentences, sometimes several
  // joined by "; ". One map covers both, since the values leak across fields.
  const CAMP_VALUE_LABEL = {
    '6mois': '6 months', '3mois': '3 months', '12mois': '12 months', 'one-year': 'One year',
    hubspot_student: 'HubSpot student', hubspot_contact: 'HubSpot contact',
    whatsapp_direct: 'WhatsApp', whatsapp: 'WhatsApp', sms: 'SMS', support2: 'support2',
    page: 'Page', 'website_test-level-lead': 'Level test', 'website_lead-quiz': 'Quiz',
    'website_program-general': 'General program', 'website_program-business': 'Business program',
    'website_teacher-application': 'Teacher application', website_free_trial: 'Free trial', website: 'Website',
    instagram: 'Instagram', instagram_dm: 'Instagram DM', facebook: 'Facebook', facebook_dm: 'Facebook DM',
    tiktok: 'TikTok', threecx_call: '3CX call', client_referral: 'Client referral', referral: 'Referral',
    youtube: 'YouTube', google_maps: 'Google Maps', ai_assistant: 'AI assistant',
    youtube_whatsapp: 'YouTube → WhatsApp', instagram_whatsapp: 'Instagram → WhatsApp',
    facebook_whatsapp: 'Facebook → WhatsApp', tiktok_whatsapp: 'TikTok → WhatsApp',
    google_maps_whatsapp: 'Google Maps → WhatsApp', ai_assistant_whatsapp: 'AI assistant → WhatsApp',
    shared_contact: 'Shared contact', manual: 'Manual', manual_phone: 'Manual (phone)',
    auto: 'Automatic', other: 'Other', walk_in: 'Walk-in',
    // the rest of what `leads.source` actually holds today (measured, not guessed)
    google_sheets: 'Google Sheets', inbound_whatsapp: 'WhatsApp (inbound)',
    facebook_comment: 'Facebook comment', instagram_comment: 'Instagram comment', website_contact: 'Website contact',
    'website_program-kids': 'Kids program', 'website_program-exam': 'Exam program',
    'website_test-level-result': 'Level test result', 'website_test-level-speaking-lead': 'Speaking test',
    'website_6mois': '6 months', 'english-everyday': 'Everyday English',
    proactive: 'Proactive', organic: 'Organic', recruitment: 'Recruitment', affiliate: 'Affiliate partner',
    'Work and professional communication': 'Work', 'Everyday conversation and meeting people': 'Everyday conversation',
    'Studies and exams': 'Studies', 'Travel': 'Travel', 'Family support and communication': 'Family',
  };
  // Goals arrive as "A; B" — map each part so a two-goal lead stays one short cell.
  const _campLabel = (v) => (v
    ? String(v).split(';').map(x => x.trim()).filter(Boolean).map(x => CAMP_VALUE_LABEL[x] || x.replace(/_/g, ' ')).join(', ')
    : '—');
  // A single stored value (a filter option, a row tag) — never a "; " list. An
  // unmapped slug still has to read as words, so it falls back to de-slugging
  // rather than showing website_test-level-lead to the team.
  const _sourceLabel = (v) => {
    const k = String(v || '').trim();
    if (!k) return '—';
    if (CAMP_VALUE_LABEL[k]) return CAMP_VALUE_LABEL[k];
    const words = k.replace(/^website[_-]/, '').replace(/[_-]+/g, ' ').trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : k;
  };
  const _e164ma = (v) => {
    const d = String(v || '').replace(/\D/g, '');
    if (/^212[5-7]\d{8}$/.test(d)) return d;
    if (/^0[5-7]\d{8}$/.test(d)) return '212' + d.slice(1);
    if (/^[5-7]\d{8}$/.test(d)) return '212' + d;
    return d || String(v || '');
  };
  const _campPhone = (v) => formatPhoneForDisplay(displayPhone(_e164ma(v)));
  function _campWhen(iso) { if (!iso) return ''; const d = new Date(iso); return MA.loc(d, 'd', 'fr-MA', { day: '2-digit', month: '2-digit' }) + ' ' + MA.loc(d, 't', 'fr-MA', { hour: '2-digit', minute: '2-digit' }); }
  const _kzInitial = (name, phone) => {
    const n = String(name || '').trim();
    return n ? n[0] : (String(phone || '').slice(-2, -1) || '?');
  };
  async function convCampStatus() {
    const box = document.getElementById('ckzCampStatus'); if (!box) return;
    let r; try { r = await _convApi('/api/kanza/campaign?days=7'); } catch { return; }
    const camps = r.campaigns || [];
    if (!camps.length) { box.replaceChildren(el('p', { class: 'kz-empty', text: 'No list in the last 7 days — hand her one from the card above.' })); return; }
    const frag = document.createDocumentFragment();
    for (const c of camps) {
      const finished = c.done + c.skipped + c.cancelled;
      const pct = c.total ? Math.round(100 * finished / c.total) : 0;
      const head = el('div', { class: 'kz-camp-h' }, [
        el('span', { class: 'kz-camp-name', text: c.name === '(manuel)' ? 'Manual calls' : c.name }),
        el('span', { class: 'kz-camp-of', text: finished + ' / ' + c.total + ' · ' + c.answered + ' answered' }),
        el('div', { class: 'kz-bar' }, [el('i', { style: 'width:' + pct + '%' })]),
        c.booked ? el('span', { class: 'kz-st done', text: c.booked + ' tests' }) : null,
        el('span', { class: 'kz-camp-spacer' }),
      ]);
      if (c.pending > 0) {
        const stop = el('button', { class: 'calls-action danger', type: 'button' }, [el('span', { text: 'Stop the rest (' + c.pending + ')' })]);
        stop.addEventListener('click', async () => {
          if (!confirm('Stop the rest of "' + c.name + '"?')) return;
          try { await _convApi('/api/kanza/campaign/cancel', { method: 'POST', body: { name: c.name === '(manuel)' ? null : c.name } }); toast('Stopped', 'success'); convCampStatus(); }
          catch (e) { toast(e.message || e, 'error'); }
        });
        head.appendChild(stop);
      }
      const rows = c.rows.map(q => {
        const tags = [];
        if (q.status === 'done') {
          if (q.outcome === 'no_answer') tags.push(el('span', { class: 'kz-st skipped', text: 'No answer' }));
          else {
            if (q.score) tags.push(el('span', { class: 'conv-score conv-score-' + q.score, text: CONV_SCORE_AR[q.score] || q.score }));
            if (q.booked) tags.push(el('span', { class: 'kz-st done', text: 'Booked a test' }));
            if (q.duration_seconds) tags.push(el('span', { class: 'kz-tag is-when', text: _convFmtDur(q.duration_seconds) }));
          }
        } else if (q.status === 'skipped') tags.push(el('span', { class: 'kz-tag', text: 'Skipped: ' + (q.reason || '—') }));
        else tags.push(el('span', { class: 'kz-st ' + q.status, text: CAMP_QSTATUS[q.status] || q.status }));
        const row = el('div', { class: 'kz-crow' }, [
          el('span', { class: 'kz-face', 'aria-hidden': 'true', text: _kzInitial(q.lead_name, q.to_number) }),
          el('span', { class: 'kz-crow-main' }, [
            el('span', { class: 'kz-row-name', text: q.lead_name || 'No name' }),
            el('span', { class: 'kz-row-sub', text: _campPhone(q.to_number) + '  ·  ' + _campWhen(q.scheduled_at) }),
            q.summary ? el('span', { class: 'kz-crow-note', text: q.summary }) : null,
          ]),
          el('span', { class: 'kz-crow-right' }, tags),
        ]);
        if (q.call_id) { row.setAttribute('data-call', q.call_id); row.addEventListener('click', () => { convSetTab('history'); convLoadDetail(q.call_id); }); }
        return row;
      });
      frag.appendChild(el('div', { class: 'kz-camp' }, [head, el('div', { class: 'kz-camp-rows' }, rows)]));
    }
    box.replaceChildren(frag);
  }


  // ── The Kanza page ────────────────────────────────────────────────────────
  // One list, and a segment at the top that swaps who it holds: the people she
  // has called, or the people she could be given next. Both sides share the
  // filters, the row shape and the ticking, so handing her a list of a hundred
  // is three clicks inside the page you were already reading — not a second
  // screen with its own form. Filters live in the URL of the mind, not in a
  // wizard: what you are looking at is spelled out in words under the chips.
  const KZP_CHIPS = [
    { key: 'all', label: 'All' },
    { key: 'live', label: 'Calling now' },
    { key: 'pending', label: 'Waiting' },
    { key: 'answered', label: 'Answered' },
    { key: 'no_answer', label: 'No answer' },
    { key: 'booked', label: 'Booked a test' },
    { key: 'hot', label: 'HOT' },
    { key: 'warm', label: 'WARM' },
    { key: 'cold', label: 'COLD' },
    { key: 'needs_human', label: 'Need a human' },
    { key: 'skipped', label: 'Skipped' },
    { key: 'dnc', label: 'Do not call' },
  ];
  const KZP_STAGE_LABEL = {
    new: 'New', no_answer: 'No answer', contacted: 'Contacted', book_test: 'Test booked',
    no_show: 'No show', test_done: 'Test done', proforma_sent: 'Proforma',
    paid: 'Paid', not_interested: 'Not interested', lost: 'Lost',
  };
  const KZP_SAVED_KEY = 'kz_saved_filters_v1';
  const kzP = {
    side: 'called', days: 30, stage: [], source: [], owner: '', sort: 'recent', q: '', state: 'all',
    createdFrom: '', createdTo: '',
    // Every source the page has seen this session. The rows alone would not do:
    // once you tick WhatsApp the response holds only WhatsApp rows, so a list
    // built from them offers the one source you already have and no way to add
    // a second. This only ever grows.
    sourcesSeen: new Set(),
    rows: [], counts: {}, total: 0, sel: new Set(), selectedId: null,
    loading: false, inited: false, timer: null, uncalled: null,
  };

  function kzpFilters() {
    return {
      side: kzP.side, days: kzP.days, stage: kzP.stage, source: kzP.source, owner: kzP.owner,
      sort: kzP.sort, state: kzP.state, q: kzP.q, createdFrom: kzP.createdFrom, createdTo: kzP.createdTo,
    };
  }
  // Stage and source are questions with more than one right answer. The API has
  // taken a comma-separated list for both since day one — only the bar could
  // not say it, so these two are checkbox popovers now.
  //
  // A filter saved before that (or a stray '' default) is a bare string, so
  // every read goes through here rather than trusting the stored shape.
  function kzpList(v) { return Array.isArray(v) ? v.filter(Boolean).map(String) : (v ? [String(v)] : []); }
  const KZP_MULTI = {
    stage: {
      id: 'kzStage', label: 'Stage',
      options: () => Object.entries(KZP_STAGE_LABEL).map(([v, l]) => ({ v, l })),
    },
    source: {
      // Sources are slugs nobody chose (whatsapp_direct, website_test-level-lead),
      // read through the same map as the rest of the page and sorted on what is
      // shown. They come from the rows, so the bar only offers what would match.
      id: 'kzSource', label: 'Source',
      options: () => [...new Set([...kzP.sourcesSeen, ...kzpList(kzP.source)])]
        .map(v => ({ v, l: _sourceLabel(v) }))
        .sort((a, b) => String(a.l).localeCompare(String(b.l))),
    },
  };
  function kzpMultiLabel(key) {
    const spec = KZP_MULTI[key], picked = kzpList(kzP[key]);
    if (!picked.length) return spec.label + ': All';
    // One pick still reads as itself; past that a count beats a list that would
    // push the rest of the bar off the row.
    if (picked.length === 1) {
      const o = spec.options().find(x => x.v === picked[0]);
      return spec.label + ': ' + ((o && o.l) || picked[0]);
    }
    return spec.label + ': ' + picked.length + ' chosen';
  }
  // The button only; the option list is rebuilt on open, so ticking a box does
  // not yank the list out from under the pointer.
  function kzpMultiSyncLabel(key) {
    const box = document.getElementById(KZP_MULTI[key].id); if (!box) return;
    const n = kzpList(kzP[key]).length;
    const lb = box.querySelector('.kz-multi-lb'); if (lb) lb.textContent = kzpMultiLabel(key);
    box.classList.toggle('is-on', n > 0);
    const all = box.querySelector('.kz-multi-all'); if (all) all.textContent = n ? 'Clear' : 'All';
  }
  function kzpMultiFill(key) {
    const box = document.getElementById(KZP_MULTI[key].id); if (!box) return;
    const pop = box.querySelector('.kz-multi-pop'); if (!pop) return;
    clear(pop);
    const picked = new Set(kzpList(kzP[key]));
    const all = el('button', { type: 'button', class: 'kz-multi-all', text: picked.size ? 'Clear' : 'All' });
    all.addEventListener('click', () => { kzP[key] = []; kzpMultiFill(key); kzpMultiSyncLabel(key); kzpLoad(); });
    pop.appendChild(all);
    const opts = KZP_MULTI[key].options();
    if (!opts.length) pop.appendChild(el('span', { class: 'kz-multi-none', text: 'Nothing to pick from yet' }));
    for (const o of opts) {
      const cb = el('input', { type: 'checkbox', value: o.v });
      cb.checked = picked.has(o.v);
      cb.addEventListener('change', () => {
        const next = new Set(kzpList(kzP[key]));
        if (cb.checked) next.add(o.v); else next.delete(o.v);
        kzP[key] = [...next];
        kzpMultiSyncLabel(key); kzpLoad();
      });
      pop.appendChild(el('label', { class: 'kz-multi-opt' }, [cb, el('span', { text: o.l })]));
    }
  }
  function kzpMultiOpen(key, open) {
    const box = document.getElementById(KZP_MULTI[key].id); if (!box) return;
    const pop = box.querySelector('.kz-multi-pop'), btn = box.querySelector('.kz-multi-btn');
    if (open) kzpMultiFill(key);   // sources change with the rows
    pop?.classList.toggle('hidden', !open);
    btn?.setAttribute('aria-expanded', String(!!open));
  }
  function kzpMultiCloseAll() { for (const k of Object.keys(KZP_MULTI)) kzpMultiOpen(k, false); }

  // A day the browser's date input produced (always YYYY-MM-DD), or ''.
  function kzpDay(v) { const s = String(v || '').trim(); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : ''; }
  function kzpHasRange() { return !!(kzpDay(kzP.createdFrom) || kzpDay(kzP.createdTo)); }
  // The contact's create date, as a day rather than a time: nobody picks a
  // range to find out what minute a lead landed.
  // Half a range is still a range: one end left empty reads as open-ended
  // rather than as "no filter".
  function kzpRangeLabel() {
    const f = kzpDay(kzP.createdFrom), t = kzpDay(kzP.createdTo);
    const nice = d => kzpMadeOn(d + 'T12:00:00') || d;
    if (f && t) return 'Created ' + nice(f) + ' → ' + nice(t);
    if (f) return 'Created from ' + nice(f);
    return 'Created up to ' + nice(t);
  }
  function kzpMadeOn(iso) {
    if (!iso) return '';
    const d = new Date(iso); if (Number.isNaN(d.getTime())) return '';
    return MA.fmt('gb-dMy', 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' }, d);
  }
  function kzpRowKey(r) { return String(r.lead_id || r.id); }
  function kzpName(r) { return r.lead_name || _campPhone(r.to_number) || 'No name'; }

  async function kzpLoad({ quiet = false } = {}) {
    const listBox = document.getElementById('kzPeople');
    if (!listBox) return;
    kzP.loading = true;
    if (!quiet) kzpRender();
    try {
      const qs = new URLSearchParams({
        side: kzP.side, days: String(kzP.days), sort: kzP.sort, state: kzP.state, q: kzP.q, limit: '200',
      });
      // An empty array is truthy — always ask for the length, never the value.
      if (kzpList(kzP.stage).length) qs.set('stage', kzpList(kzP.stage).join(','));
      if (kzpList(kzP.source).length) qs.set('source', kzpList(kzP.source).join(','));
      if (kzP.owner) qs.set('owner', kzP.owner);
      if (kzpDay(kzP.createdFrom)) qs.set('created_from', kzpDay(kzP.createdFrom));
      if (kzpDay(kzP.createdTo)) qs.set('created_to', kzpDay(kzP.createdTo));
      const r = await _convApi('/api/kanza/people?' + qs.toString());
      kzP.rows = r.people || []; kzP.counts = r.counts || {}; kzP.total = r.total || 0;
      for (const row of kzP.rows) if (row.lead_source) kzP.sourcesSeen.add(String(row.lead_source));
      kzP.error = null;
      if (r.today) state.conv.today = r.today;
      if (r.policy) state.conv.policy = Object.assign({}, state.conv.policy || {}, r.policy);
      if (typeof r.online === 'boolean') state.conv.status = Object.assign({}, state.conv.status || {}, { online: r.online });
      // The other side's total feeds the segment badge, so "370 waiting to be
      // called" is visible without switching to look.
      if (kzP.side === 'called' && kzP.uncalled === null) kzpLoadOtherCount();
    } catch (e) {
      kzP.error = e.message || String(e);
      if (!quiet) toast('Kenza: ' + kzP.error, 'error');
    }
    kzP.loading = false;
    kzpRender();
  }

  async function kzpLoadOtherCount() {
    try {
      // The badge counts the same people the other side would show, so it has
      // to carry the created range too — otherwise it promises rows the tab
      // then does not have.
      const qs = new URLSearchParams({ side: 'uncalled', days: String(kzP.days), limit: '1' });
      if (kzpDay(kzP.createdFrom)) qs.set('created_from', kzpDay(kzP.createdFrom));
      if (kzpDay(kzP.createdTo)) qs.set('created_to', kzpDay(kzP.createdTo));
      const r = await _convApi('/api/kanza/people?' + qs.toString());
      kzP.uncalled = r.total || 0;
      const b = document.getElementById('kzUncalledN');
      if (b) b.textContent = kzP.uncalled ? String(kzP.uncalled) : '';
    } catch { /* the badge is a nicety, never a blocker */ }
  }

  function kzpRender() {
    kzpRenderKpis();
    kzpRenderChips();
    kzpRenderSelects();
    kzpRenderActive();
    kzpRenderList();
    kzpRenderBulk();
  }

  function kzpRenderKpis() {
    const box = document.getElementById('kzKpis'); if (!box) return;
    const t = state.conv.today || {};
    const c = kzP.counts || {};
    const cells = [
      { n: t.calls || 0, lb: 'Calls today' },
      { n: c.answered || 0, lb: 'Answered', cls: 'is-good' },
      { n: c.booked || 0, lb: 'Booked a test', cls: 'is-good' },
      { n: c.hot || 0, lb: 'HOT', cls: 'is-hot' },
      { n: c.needs_human || 0, lb: 'Need a human', cls: 'is-warn' },
      { n: Math.round((t.seconds || 0) / 60), lb: 'min' },
      { n: c.pending || 0, lb: 'Waiting' },
    ];
    clear(box);
    for (const k of cells) {
      box.appendChild(el('div', { class: 'kz-kpi ' + (k.cls || '') }, [
        el('b', { text: String(k.n) }), el('span', { text: k.lb }),
      ]));
    }
  }

  function kzpRenderChips() {
    const box = document.getElementById('kzChips'); if (!box) return;
    clear(box);
    for (const chip of KZP_CHIPS) {
      // A chip with nothing behind it is noise: only "All" and the active one
      // stay when the count is zero.
      const n = chip.key === 'all' ? (kzP.counts.all || 0) : (kzP.counts[chip.key] || 0);
      if (!n && chip.key !== 'all' && kzP.state !== chip.key) continue;
      const b = el('button', { type: 'button', class: 'kz-chip' + (kzP.state === chip.key ? ' is-on' : '') }, [
        el('span', { text: chip.label }),
        el('em', { class: 'kz-chip-n', text: String(n) }),
      ]);
      b.addEventListener('click', () => { kzP.state = chip.key; kzpLoad(); });
      box.appendChild(b);
    }
  }

  // The owner options come from the rows themselves: the page only ever offers
  // a filter that would actually match something. Stage and source do the same
  // through KZP_MULTI, which is rebuilt each time its popover opens.
  function kzpRenderSelects() {
    for (const key of Object.keys(KZP_MULTI)) kzpMultiSyncLabel(key);
    // `label` turns the stored value into something a human picked, and sorts
    // on what is shown rather than on the slug.
    const fill = (id, values, prefix, allLabel, label = (v) => v) => {
      const sel = document.getElementById(id); if (!sel) return;
      const current = sel.value;
      const seen = [...new Set(values.filter(Boolean).map(String))]
        .sort((a, b) => String(label(a)).localeCompare(String(label(b))));
      clear(sel);
      sel.appendChild(el('option', { value: '', text: allLabel }));
      if (id === 'kzOwner') sel.appendChild(el('option', { value: 'none', text: prefix + 'No owner' }));
      for (const v of seen) sel.appendChild(el('option', { value: v, text: prefix + label(v) }));
      sel.value = seen.includes(current) || current === 'none' ? current : '';
    };
    fill('kzOwner', kzP.rows.map(r => r.lead_agent), 'Owner: ', 'Owner: All');
  }

  function kzpRenderActive() {
    const line = document.getElementById('kzActiveLine'); if (!line) return;
    const bits = [kzP.side === 'uncalled' ? 'Not called yet' : 'the ones she called'];
    const days = { 1: 'Today', 7: 'Last 7 days', 30: 'Last 30 days', 60: 'Last 2 months', 90: 'Last 3 months', 365: 'Full year' };
    // The range takes the period's place in the sentence too, because that is
    // exactly what it does to the list.
    if (kzpHasRange()) bits.push(kzpRangeLabel());
    else bits.push(days[kzP.days] || ('Last ' + kzP.days + ' days'));
    // Every stage you picked is named, so the line stays a sentence you can
    // check rather than a count you have to trust.
    const stages = kzpList(kzP.stage);
    if (stages.length) bits.push(stages.map(s => KZP_STAGE_LABEL[s] || s).join(' + '));
    if (kzP.state !== 'all') bits.push((KZP_CHIPS.find(c => c.key === kzP.state) || {}).label || kzP.state);
    const sources = kzpList(kzP.source);
    if (sources.length) bits.push(sources.map(_sourceLabel).join(' + '));
    if (kzP.owner) bits.push(kzP.owner === 'none' ? 'No owner' : kzP.owner);
    if (kzP.q) bits.push('«' + kzP.q + '»');
    clear(line);
    line.appendChild(el('span', { text: 'Showing: ' }));
    bits.forEach((b, i) => {
      if (i) line.appendChild(el('span', { class: 'kz-active-sep', text: ' · ' }));
      line.appendChild(el('b', { text: b }));
    });
    line.appendChild(el('span', { class: 'kz-active-sep', text: ' · ' }));
    line.appendChild(el('b', { text: kzP.total + ' clients' }));
  }

  function kzpRenderList() {
    const box = document.getElementById('kzPeople'); if (!box) return;
    clear(box);
    const count = document.getElementById('kzListCount');
    if (count) count.textContent = kzP.total ? (kzP.total + ' clients') : '';
    if (kzP.loading && !kzP.rows.length) {
      box.appendChild(el('div', { class: 'conv-empty' }, [el('div', { class: 'spinner-sm' }), el('span', { text: 'Loading the list…' })]));
      return;
    }
    if (kzP.error) {
      const retry = el('button', { class: 'calls-action', type: 'button' }, [lucideIcon('refresh-cw'), el('span', { text: 'Try again' })]);
      retry.addEventListener('click', () => kzpLoad());
      box.appendChild(el('div', { class: 'conv-empty kz-list-message' }, [
        lucideIcon('wifi-off'), el('strong', { text: 'Could not load the list' }), el('span', { text: kzP.error }), retry,
      ]));
      refreshIcons(box);
      return;
    }
    if (!kzP.rows.length) {
      box.appendChild(el('div', { class: 'conv-empty kz-list-message' }, [
        lucideIcon('headset', { style: 'width:44px;height:44px;opacity:.35;' }),
        el('strong', { text: 'No client matches this filter' }),
        el('span', { text: kzP.side === 'uncalled' ? 'Widen the period or change the stage.' : 'Try «Not called yet» and hand her a list.' }),
      ]));
      refreshIcons(box);
      return;
    }
    for (const r of kzP.rows) box.appendChild(kzpRow(r));
    refreshIcons(box);
  }

  function kzpRowTags(r) {
    const tags = [];
    if (r.kind === 'candidate') {
      if (r.lead_stage) tags.push(el('span', { class: 'kz-tag', text: KZP_STAGE_LABEL[r.lead_stage] || r.lead_stage }));
      if (r.kanza_calls) tags.push(el('span', { class: 'kz-tag', text: 'Called them ' + r.kanza_calls + ' times' }));
    } else if (r.status === 'picked') {
      tags.push(el('span', { class: 'kz-st picked', text: 'On the line now' }));
    } else if (r.status === 'pending') {
      tags.push(el('span', { class: 'kz-st pending', text: 'Waiting' }));
    } else if (r.status === 'skipped' || r.status === 'cancelled') {
      tags.push(el('span', { class: 'kz-st skipped', text: r.reason === 'do_not_call' ? 'Skipped: do not call' : 'Skipped' }));
    } else if (r.outcome === 'no_answer') {
      tags.push(el('span', { class: 'kz-st skipped', text: 'No answer' + (r.attempts ? ' · ' + r.attempts + '/3' : '') }));
    } else {
      if (r.score) tags.push(el('span', { class: 'conv-score conv-score-' + r.score, text: CONV_SCORE_AR[r.score] || r.score }));
      if (r.booked) tags.push(el('span', { class: 'kz-st done', text: 'Booked a test' }));
    }
    if (r.do_not_call) tags.push(el('span', { class: 'kz-st dnc', text: 'Do not call' }));
    return tags;
  }

  function kzpRow(r) {
    const key = kzpRowKey(r);
    const name = kzpName(r);
    const live = r.status === 'picked';
    const row = el('div', {
      class: 'kz-prow' + (kzP.selectedId === String(r.id) ? ' is-active' : '') + (live ? ' is-live' : '')
        + (r.score === 'hot' ? ' is-hot' : '') + (r.score === 'needs_human' || r.outcome === 'needs_human' ? ' is-warn' : ''),
      role: 'listitem', tabindex: '0',
    });

    const cb = el('input', { type: 'checkbox', class: 'kz-prow-cb', 'aria-label': 'Select ' + name });
    cb.checked = kzP.sel.has(key);
    // Only someone she can still be given is tickable: a live call or a row she
    // already holds would just be refused by the queue.
    cb.disabled = !(r.lead_id || r.to_number) || live || r.status === 'pending';
    cb.addEventListener('click', (e) => e.stopPropagation());
    cb.addEventListener('change', () => {
      if (cb.checked) kzP.sel.add(key); else kzP.sel.delete(key);
      kzpRenderBulk();
    });
    row.appendChild(cb);

    row.appendChild(el('span', { class: 'avatar kz-prow-ava ' + avatarColorClass(r.to_number || key), 'aria-hidden': 'true', text: _kzInitial(name, r.to_number) }));

    const body = el('div', { class: 'kz-prow-body' });
    const line1 = el('div', { class: 'kz-prow-l1' }, [
      el('span', { class: 'kz-prow-name', dir: 'auto', text: name }),
      el('span', { class: 'kz-prow-phone', dir: 'ltr', text: _campPhone(r.to_number) || '' }),
    ]);
    for (const t of kzpRowTags(r)) line1.appendChild(t);
    body.appendChild(line1);
    body.appendChild(el('div', { class: 'kz-prow-sub', dir: 'auto', text: kzpRowSub(r) }));
    row.appendChild(body);

    const meta = el('div', { class: 'kz-prow-meta' }, [
      el('span', { class: 'kz-prow-when', text: kzWhen(r.status === 'pending' ? r.scheduled_at : (r.started_at || r.created_at)) }),
    ]);
    if (r.duration_seconds) meta.appendChild(el('span', { class: 'kz-prow-dur', text: '🎧 ' + _convFmtDur(r.duration_seconds) }));
    // When the contact itself was made — the thing the created range filters on.
    // Showing it on the row is what makes the range checkable at a glance.
    const made = kzpMadeOn(r.lead_created_at);
    if (made) meta.appendChild(el('span', { class: 'kz-prow-made', title: 'Contact created ' + made, text: 'Created ' + made }));
    row.appendChild(meta);

    if (live) {
      const acts = el('div', { class: 'kz-prow-acts' });
      const act = (icon, label, cls, fn) => {
        const b = el('button', { class: 'calls-action ' + (cls || ''), type: 'button' }, [lucideIcon(icon), el('span', { text: label })]);
        b.addEventListener('click', (e) => { e.stopPropagation(); fn(e); });
        acts.appendChild(b);
      };
      act('headphones', 'Listen now', 'primary', () => {
        if (r.spy_code) { cxDial(String(r.spy_code), 'Listen ' + kzpName(r)); return; }
        toast('To listen now: use Listen in 3CX on ext ' + (r.extension || '1077'), '');
      });
      act('message-square', 'Whisper to her', '', () => kzpWhisper(r));
      act('hand', 'Take the call', '', () => convCmd(r.call_id, 'handoff', {}));
      act('phone-off', 'End', 'danger', () => convCmd(r.call_id, 'end', {}));
      body.appendChild(acts);
    }

    row.addEventListener('click', () => kzpOpen(r));
    row.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); kzpOpen(r); } });
    return row;
  }

  function kzpRowSub(r) {
    if (r.kind === 'candidate') {
      const bits = [];
      if (r.goal) bits.push('Wants: ' + _campLabel(r.goal));
      bits.push('Came in ' + kzWhen(r.created_at));
      if (r.lead_source) bits.push(_sourceLabel(r.lead_source));
      return bits.join(' · ');
    }
    if (r.status === 'pending') {
      const late = new Date(r.scheduled_at).getTime() < Date.now();
      return late ? 'Their turn is up — waiting for Kenza to finish the one before' : 'She calls them ' + kzWhen(r.scheduled_at);
    }
    if (r.status === 'picked') return 'On the line now — you can listen or step in.';
    if (r.status === 'skipped' && r.reason === 'do_not_call') return 'Marked «Do not call» — remove the mark to let her call again.';
    if (r.status === 'skipped' || r.status === 'cancelled') return 'Skipped: ' + (r.reason && !String(r.reason).startsWith('campaign:') ? r.reason : '—');
    if (r.outcome === 'no_answer') return 'No answer.';
    return r.summary || 'Call finished — the summary is not ready yet.';
  }

  async function kzpWhisper(r) {
    const text = prompt('What should we tell her? (only she hears it)');
    if (!text) return;
    convCmd(r.call_id, 'whisper', { text });
  }

  function kzpOpen(r) {
    kzP.selectedId = String(r.id);
    document.querySelectorAll('.kz-prow').forEach(x => x.classList.remove('is-active'));
    kzpRenderList();
    const box = document.getElementById('convDetail'); if (!box) return;
    if (r.call_id) { convLoadDetail(r.call_id); return; }
    // No call yet (a candidate, or a row she is still holding): the panel is
    // then about what happens next, not about what was said.
    clear(box);
    const acts = el('div', { class: 'conv-actions' });
    const add = (label, icon, cls, fn) => {
      const b = el('button', { class: 'calls-action ' + (cls || ''), type: 'button' }, [lucideIcon(icon), el('span', { text: label })]);
      b.addEventListener('click', () => fn(b)); acts.appendChild(b);
    };
    if (r.status === 'pending') {
      add('Call now', 'phone-call', 'primary', () => kzPlanOne(r, 'reschedule', new Date().toISOString()));
      add('Remove from the list', 'x', '', () => kzPlanOne(r, 'drop'));
    } else {
      add('Give her this client', 'send', 'primary', (b) => kanzaRequestCall({ phone: r.to_number, name: kzpName(r), leadId: r.lead_id }, b));
    }
    add('Open chat', 'message-circle', '', () => openInboxForPhone(r.to_number, kzpName(r)));
    if (r.lead_id) add(r.do_not_call ? 'Allow Kenza to call' : 'Do not call them', r.do_not_call ? 'unlock' : 'phone-off', '', (b) => kzSetDoNotCall(r, !r.do_not_call, b).then(() => kzpLoad({ quiet: true })));
    box.appendChild(el('div', { class: 'kz-detail-lead' }, [
      el('h3', { text: kzpName(r) }),
      el('p', { class: 'kz-detail-sub', dir: 'ltr', text: _campPhone(r.to_number) || '' }),
      el('p', { class: 'kz-detail-sub', text: kzpRowSub(r) }),
      acts,
    ]));
    refreshIcons(box);
  }

  function kzpRenderBulk() {
    const bar = document.getElementById('kzBulk'); if (!bar) return;
    const n = kzP.sel.size;
    bar.classList.toggle('hidden', n === 0);
    const label = document.getElementById('kzBulkCount');
    if (label) label.textContent = 'Selected: ' + n + ' clients';
    const sel = document.getElementById('kzSelLabel');
    if (sel) sel.textContent = 'Selected ' + n;
    const btn = document.getElementById('kzGiveBtn');
    if (btn) btn.disabled = n === 0;
    const note = document.getElementById('kzBulkNote');
    if (note && n) {
      const gap = Number(document.getElementById('kzGap')?.value || 2);
      const cap = Number(state.conv.policy?.config?.daily_cap_calls || 60);
      const mins = n * (gap + 2);
      const spill = n > cap ? ' · daily cap is ' + cap + ' calls; the rest continues tomorrow' : '';
      note.textContent = 'About ' + (mins >= 60 ? (Math.round(mins / 6) / 10) + ' h' : mins + ' min') + ' of work' + spill;
    }
  }

  async function kzpGive() {
    const rows = kzP.rows.filter(r => kzP.sel.has(kzpRowKey(r)));
    if (!rows.length) return;
    const gapMin = Number(document.getElementById('kzGap')?.value || 2);
    const startSel = document.getElementById('kzStart')?.value || 'now';
    const name = String(document.getElementById('kzName')?.value || '').trim() || null;
    let startAt = null;
    if (startSel === 'tomorrow') { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0); startAt = d.toISOString(); }
    if (!confirm('Give ' + rows.length + ' clients to Kenza? She calls them one at a time.')) return;
    const btn = document.getElementById('kzGiveBtn'); if (btn) btn.disabled = true;
    try {
      const r = await _convApi('/api/kanza/campaign', {
        method: 'POST',
        body: {
          lead_ids: rows.filter(x => x.lead_id).map(x => String(x.lead_id)),
          phones: rows.filter(x => !x.lead_id && x.to_number).map(x => String(x.to_number)),
          gap_seconds: Math.max(0, gapMin) * 60,
          start_at: startAt,
          name,
        },
      });
      const skipped = (r.skipped || []).length;
      toast('Gave her ' + r.queued + ' clients' + (skipped ? ' · ' + skipped + ' skipped' : ''), 'success');
      kzP.sel.clear();
      kzpLoad();
    } catch (e) { toast(e.message || e, 'error'); }
    finally { if (btn) btn.disabled = false; }
  }

  // Saved filters: the combination you keep coming back to ("No answer · August")
  // is one click, not five. They live in this browser — a shared server-side
  // list would need an owner, and nobody asked for that yet.
  function kzpSavedRead() {
    try { return JSON.parse(localStorage.getItem(KZP_SAVED_KEY) || '[]'); } catch { return []; }
  }
  function kzpSavedWrite(list) {
    try { localStorage.setItem(KZP_SAVED_KEY, JSON.stringify(list.slice(0, 12))); } catch { /* private mode */ }
  }
  function kzpRenderSaved() {
    const box = document.getElementById('kzSaved'); if (!box) return;
    clear(box);
    for (const item of kzpSavedRead()) {
      const b = el('button', { type: 'button', class: 'kz-chip kz-chip-sm', title: 'Click to apply · double-click to delete' }, [el('span', { text: item.name })]);
      // Filters saved before the created range existed carry no dates. Defaulting
      // them here means applying an old one clears the range instead of leaving
      // the last one silently on.
      b.addEventListener('click', () => {
        Object.assign(kzP, { createdFrom: '', createdTo: '' }, item.f, { sel: kzP.sel });
        // A filter saved when stage/source were single-pick holds a bare string.
        kzP.stage = kzpList(kzP.stage); kzP.source = kzpList(kzP.source);
        kzP.uncalled = null;
        kzpMultiCloseAll(); kzpSyncControls(); kzpLoad();
      });
      b.addEventListener('dblclick', () => { kzpSavedWrite(kzpSavedRead().filter(x => x.name !== item.name)); kzpRenderSaved(); });
      box.appendChild(b);
    }
  }
  function kzpSyncControls() {
    const set = (id, v) => { const n = document.getElementById(id); if (n) n.value = v; };
    set('kzDays', String(kzP.days)); set('kzOwner', kzP.owner); set('kzSort', kzP.sort); set('kzQ', kzP.q);
    for (const key of Object.keys(KZP_MULTI)) kzpMultiSyncLabel(key);
    set('kzCreatedFrom', kzpDay(kzP.createdFrom)); set('kzCreatedTo', kzpDay(kzP.createdTo));
    // The period select is not merely ignored while a range is on — it is shown
    // as off, so the list never disagrees with what the bar says.
    const on = kzpHasRange();
    document.getElementById('kzDays')?.toggleAttribute('disabled', on);
    document.querySelector('.kz-daterange')?.classList.toggle('is-on', on);
    document.getElementById('kzCreatedClear')?.classList.toggle('hidden', !on);
    document.getElementById('kzCreatedNote')?.classList.toggle('hidden', !on);
    document.querySelectorAll('#kzSideSeg .kz-seg-b').forEach(b => {
      const on = b.getAttribute('data-side') === kzP.side;
      b.classList.toggle('is-on', on); b.setAttribute('aria-selected', String(on));
    });
  }

  function kzpInit() {
    if (kzP.inited) { kzpLoad({ quiet: true }); return; }
    kzP.inited = true;
    document.querySelectorAll('#kzSideSeg .kz-seg-b').forEach(b => b.addEventListener('click', () => {
      kzP.side = b.getAttribute('data-side') || 'called';
      kzP.state = 'all'; kzP.sel.clear(); kzP.uncalled = null;
      kzpSyncControls(); kzpLoad();
    }));
    document.getElementById('kzDays')?.addEventListener('change', e => { kzP.days = Number(e.target.value) || 30; kzP.uncalled = null; kzpLoad(); });
    for (const key of Object.keys(KZP_MULTI)) {
      const box = document.getElementById(KZP_MULTI[key].id);
      box?.querySelector('.kz-multi-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const wasOpen = box.querySelector('.kz-multi-pop')?.classList.contains('hidden') === false;
        kzpMultiCloseAll();
        kzpMultiOpen(key, !wasOpen);
      });
      // Ticking a box inside must not read as "clicked outside".
      box?.querySelector('.kz-multi-pop')?.addEventListener('click', e => e.stopPropagation());
    }
    document.addEventListener('click', kzpMultiCloseAll);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') kzpMultiCloseAll(); });
    document.getElementById('kzOwner')?.addEventListener('change', e => { kzP.owner = e.target.value || ''; kzpLoad(); });
    document.getElementById('kzSort')?.addEventListener('change', e => { kzP.sort = e.target.value || 'recent'; kzpLoad(); });
    // Both ends reload on their own: half a range is a legitimate question
    // ("everyone created since 1 August"), not a half-finished one.
    const onCreated = () => { kzP.uncalled = null; kzpSyncControls(); kzpLoad(); };
    document.getElementById('kzCreatedFrom')?.addEventListener('change', e => { kzP.createdFrom = kzpDay(e.target.value); onCreated(); });
    document.getElementById('kzCreatedTo')?.addEventListener('change', e => { kzP.createdTo = kzpDay(e.target.value); onCreated(); });
    document.getElementById('kzCreatedClear')?.addEventListener('click', () => { kzP.createdFrom = ''; kzP.createdTo = ''; onCreated(); });
    let qTimer = null;
    document.getElementById('kzQ')?.addEventListener('input', e => {
      kzP.q = e.target.value || '';
      clearTimeout(qTimer); qTimer = setTimeout(() => kzpLoad({ quiet: true }), 300);
    });
    document.getElementById('kzClearBtn')?.addEventListener('click', () => {
      Object.assign(kzP, { days: 30, stage: [], source: [], owner: '', sort: 'recent', q: '', state: 'all', createdFrom: '', createdTo: '' });
      kzP.uncalled = null;
      kzpMultiCloseAll(); kzpSyncControls(); kzpLoad();
    });
    const selectAll = () => {
      const pick = kzP.rows.filter(r => (r.lead_id || r.to_number) && r.status !== 'picked' && r.status !== 'pending');
      const allOn = pick.every(r => kzP.sel.has(kzpRowKey(r)));
      for (const r of pick) { if (allOn) kzP.sel.delete(kzpRowKey(r)); else kzP.sel.add(kzpRowKey(r)); }
      kzpRenderList(); kzpRenderBulk();
      const cb = document.getElementById('kzAllCb'); if (cb) cb.checked = !allOn;
    };
    document.getElementById('kzSelectAllBtn')?.addEventListener('click', selectAll);
    document.getElementById('kzAllCb')?.addEventListener('change', selectAll);
    document.getElementById('kzGiveBtn')?.addEventListener('click', () => kzpGive());
    document.getElementById('kzGap')?.addEventListener('input', () => kzpRenderBulk());
    document.getElementById('kzSaveFilterBtn')?.addEventListener('click', () => {
      const name = prompt('Name this filter:', kzP.side === 'uncalled' ? 'Not called yet' : 'the ones she called');
      if (!name) return;
      const list = kzpSavedRead().filter(x => x.name !== name);
      list.unshift({ name, f: kzpFilters() });
      kzpSavedWrite(list); kzpRenderSaved();
    });
    kzpRenderSaved();
    kzpSyncControls();
    kzpLoad();
    clearInterval(kzP.timer);
    kzP.timer = setInterval(() => {
      if (state.activeView === 'conversations' && !document.hidden) kzpLoad({ quiet: true });
    }, 15000);
  }

  function ensureCallsAdminInit() {
    if (state.callsAdmin.wired) { _loadCallsAdmin({ reset: true }); return; }
    state.callsAdmin.wired = true;
    const period = document.getElementById('callsPeriod');
    if (period) period.value = 'this_month';
    _callsApplyPeriod('this_month');
    document.querySelectorAll('[data-calls-bucket]').forEach(btn => btn.addEventListener('click', () => {
      state.callsAdmin.bucket = btn.dataset.callsBucket || 'all';
      document.querySelectorAll('[data-calls-bucket]').forEach(x => x.classList.toggle('is-active', x === btn));
      _loadCallsAdmin({ reset: true });
    }));
    document.getElementById('callsRefreshBtn')?.addEventListener('click', () => _loadCallsAdmin({ reset: true }));
    document.getElementById('callsLoadMore')?.addEventListener('click', () => _loadCallsAdmin({ append: true }));
    document.getElementById('callsSource')?.addEventListener('change', e => { state.callsAdmin.source = e.target.value || 'all'; _loadCallsAdmin({ reset: true }); });
    document.getElementById('callsDirection')?.addEventListener('change', e => { state.callsAdmin.direction = e.target.value || 'all'; _loadCallsAdmin({ reset: true }); });
    document.getElementById('callsStatus')?.addEventListener('change', e => { state.callsAdmin.status = e.target.value || 'all'; _loadCallsAdmin({ reset: true }); });
    document.getElementById('callsAgent')?.addEventListener('change', e => { state.callsAdmin.agent = e.target.value || ''; _loadCallsAdmin({ reset: true }); });
    period?.addEventListener('change', e => { if (_callsApplyPeriod(e.target.value || 'custom')) _loadCallsAdmin({ reset: true }); });
    for (const id of ['callsFrom', 'callsTo']) document.getElementById(id)?.addEventListener('change', () => {
      if (period) period.value = 'custom';
      _loadCallsAdmin({ reset: true });
    });
    document.getElementById('callsSearch')?.addEventListener('input', () => {
      clearTimeout(_callsSearchTimer);
      _callsSearchTimer = setTimeout(() => _loadCallsAdmin({ reset: true }), 260);
    });
    if (state.socket && !state.callsAdmin._socketBound) {
      state.callsAdmin._socketBound = true;
      state.socket.on('call_updated', () => {
        if (document.getElementById('callsView')?.classList.contains('hidden')) return;
        clearTimeout(_callsSocketTimer);
        _callsSocketTimer = setTimeout(() => _loadCallsAdmin({ reset: true }), 350);
      });
    }
    _loadCallsAdmin({ reset: true });
  }

  async function _loadCallsAdmin(options = {}) {
    const st = state.callsAdmin;
    if (st.loading) {
      // A socket update or a quick filter change can arrive while a request is
      // in flight. Queue one clean reload so the UI never gets stuck showing
      // the previous filter's response.
      st.pendingReload = true;
      return;
    }
    const append = !!options.append;
    if (options.reset) st.rows = [];
    st.loading = true;
    document.getElementById('callsRefreshBtn')?.classList.add('is-loading');
    if (!append) {
      const list = document.getElementById('callsList');
      if (list) list.innerHTML = '<div class="calls-empty"><i data-lucide="loader-circle"></i><strong>Loading calls…</strong></div>';
      refreshIcons(list || undefined);
    }
    try {
      const params = new URLSearchParams({
        bucket: st.bucket, limit: String(st.limit), offset: String(append ? st.rows.length : 0),
      });
      const from = document.getElementById('callsFrom')?.value || '';
      const to = document.getElementById('callsTo')?.value || '';
      const q = document.getElementById('callsSearch')?.value.trim() || '';
      if (from) params.set('from', from); if (to) params.set('to', to); if (q) params.set('q', q);
      if (st.source !== 'all') params.set('source', st.source);
      if (st.direction !== 'all') params.set('direction', st.direction);
      if (st.status !== 'all') params.set('status', st.status);
      if (st.agent) params.set('agent', st.agent);
      // Deliberately omit scope: the server chooses mine for agents and all
      // for Super Sales/Admin. Client code can never widen an agent's scope.
      const r = await api('/api/calls/recent?' + params.toString());
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Failed to load calls');
      const incoming = Array.isArray(j.calls) ? j.calls : [];
      st.rows = append ? st.rows.concat(incoming) : incoming;
      st.total = Number(j.total) || 0; st.stats = j.stats || {}; st.canSeeAll = !!j.canSeeAll;
      if (!st.rows.some(x => x.callId === st.selectedId)) st.selectedId = st.rows[0]?.callId || '';
      _callsRenderAgents(j.agents || [], st.canSeeAll);
      _renderCallsAdmin();
    } catch (err) {
      const list = document.getElementById('callsList');
      if (list) list.replaceChildren(el('div', { class: 'calls-empty' }, [lucideIcon('circle-alert'), el('strong', { text: 'Could not load calls' }), el('span', { text: err.message || String(err) })]));
      refreshIcons(list || undefined);
    } finally {
      st.loading = false;
      document.getElementById('callsRefreshBtn')?.classList.remove('is-loading');
      if (st.pendingReload) {
        st.pendingReload = false;
        _loadCallsAdmin({ reset: true });
      }
    }
  }

  function _callsRenderAgents(agents, canSeeAll) {
    const field = document.getElementById('callsAgentField');
    const select = document.getElementById('callsAgent');
    field?.classList.toggle('hidden', !canSeeAll);
    const scope = document.getElementById('callsScopeLabel');
    if (scope) scope.textContent = canSeeAll ? 'Complete sales-team call history, outcomes and recordings.' : 'Your calls and calls from leads assigned to you.';
    if (!select || !canSeeAll) return;
    const current = state.callsAdmin.agent;
    select.replaceChildren(el('option', { value: '', text: 'All agents' }));
    for (const a of agents) select.appendChild(el('option', { value: a.username, text: userLabel(a.username, a.displayName || a.username) }));
    if ([...select.options].some(o => o.value === current)) select.value = current;
  }

  function _renderCallsAdmin() {
    const st = state.callsAdmin;
    const list = document.getElementById('callsList');
    if (!list) return;
    const stats = st.stats || {};
    const set = (id, value) => { const node = document.getElementById(id); if (node) node.textContent = String(Number(value) || 0); };
    set('csTotal', stats.all); set('csInbound', stats.inbound); set('csOutbound', stats.outbound); set('csAnswered', stats.answered); set('csMissed', stats.missed); set('csRecorded', stats.recorded);
    const talk = document.querySelector('#callsTalkTime span'); if (talk) talk.textContent = _callsTalkTime(stats.durationSec) + ' talk time';
    const titles = { all: 'All calls', inbound: 'Incoming calls', outbound: 'Outgoing calls', answered: 'Answered calls', missed: 'Missed calls', recorded: 'Recorded calls' };
    const title = document.getElementById('callsListTitle'); if (title) title.textContent = titles[st.bucket] || 'Calls';
    const count = document.getElementById('callsCount'); if (count) count.textContent = st.total + ' call' + (st.total === 1 ? '' : 's');
    list.replaceChildren();
    if (!st.rows.length) list.appendChild(el('div', { class: 'calls-empty' }, [lucideIcon('phone-off'), el('strong', { text: 'No calls found' }), el('span', { text: 'Try another period, source or filter.' })]));
    for (const call of st.rows) {
      const missed = _callsIsMissed(call);
      const recordingOnly = !!call.recordingOnly || String(call.status || '').toLowerCase() === 'recorded';
      const source = String(call.source || 'whatsapp').toLowerCase();
      const row = el('button', { class: 'calls-row source-' + source + (call.callId === st.selectedId ? ' is-selected' : ''), type: 'button' });
      row.appendChild(el('span', { class: 'calls-row-avatar source-' + source + (missed ? ' is-missed' : ''), text: _callsInitials(call.callerName || call.phone) }));
      const main = el('span', { class: 'calls-row-main' });
      const line1 = el('span', { class: 'calls-row-line1' }, [el('span', { class: 'calls-row-name', text: call.callerName || call.phone || 'Unknown caller' })]);
      line1.appendChild(el('span', { class: 'calls-source-pill source-' + source, text: source === '3cx' ? '3CX' : 'WhatsApp' }));
      if (call.recordingUrl) line1.appendChild(el('span', { class: 'calls-recorded-pill', title: 'Recording available' }, [lucideIcon('audio-lines'), el('span', { text: 'REC' })]));
      if (call.pipelineStage) line1.appendChild(el('span', { class: 'calls-stage-pill', text: PIPELINE_LABELS[call.pipelineStage] || call.pipelineStage }));
      main.appendChild(line1);
      main.appendChild(el('span', { class: 'calls-row-line2' }, [
        lucideIcon(recordingOnly ? 'audio-lines' : (missed ? 'phone-missed' : (call.direction === 'outbound' ? 'phone-outgoing' : 'phone-incoming'))),
        el('span', { class: missed ? 'is-missed' : '', text: _callsOutcome(call) }), el('span', { text: '·' }), el('span', { text: _callsDuration(call.durationSec) }),
      ]));
      if (call.agentDisplayName || call.agentUsername) main.appendChild(el('span', { class: 'calls-row-agent', text: userLabel(call.agentUsername, call.agentDisplayName || call.agentUsername) }));
      row.appendChild(main);
      row.appendChild(el('span', { class: 'calls-row-time', text: _callsWhen(call.startedAt, false) }));
      row.addEventListener('click', () => { st.selectedId = call.callId; _renderCallsAdmin(); });
      list.appendChild(row);
    }
    const more = document.getElementById('callsLoadMore');
    more?.classList.toggle('hidden', st.rows.length >= st.total);
    _callsRenderDetail(st.rows.find(c => c.callId === st.selectedId));
    refreshIcons(document.getElementById('callsView') || undefined);
  }

  function _callsRenderDetail(call) {
    const pane = document.getElementById('callsDetail');
    if (!pane) return;
    if (!call) {
      pane.innerHTML = '<div class="calls-detail-empty"><div><i data-lucide="phone-call"></i></div><h3>Select a call</h3><p>Open a call to see its details and recording.</p></div>';
      refreshIcons(pane); return;
    }
    const missed = _callsIsMissed(call), source = String(call.source || 'whatsapp').toLowerCase();
    const recordingOnly = !!call.recordingOnly || String(call.status || '').toLowerCase() === 'recorded';
    const card = el('div', { class: 'calls-detail-card source-' + source });
    const top = el('div', { class: 'calls-detail-top' });
    top.appendChild(el('div', { class: 'calls-detail-contact' }, [
      el('div', { class: 'calls-detail-avatar source-' + source, text: _callsInitials(call.callerName || call.phone) }),
      el('div', {}, [el('h3', { text: call.callerName || call.phone || 'Unknown caller' }), el('p', { text: call.phone ? formatPhoneForDisplay(displayPhone(call.phone)) : 'No phone available' })]),
    ]));
    top.appendChild(el('span', { class: 'calls-detail-status' + (missed ? ' is-missed' : ''), text: CALL_STATUS_LABELS[call.status] || call.status || 'Unknown' }));
    card.appendChild(top);
    const actions = el('div', { class: 'calls-detail-actions' });
    if (call.chatId) {
      const chat = el('button', { class: 'calls-action primary', type: 'button' }, [lucideIcon('message-circle'), el('span', { text: 'Open chat' })]);
      chat.addEventListener('click', () => {
        openInboxForCall(call, call.callerName || '').catch(() => toast('Could not open conversation', 'error'));
      });
      actions.appendChild(chat);
    }
    const dialDigits = callDialDigits(call);
    if (dialDigits) {
      const dial = el('button', { class: 'calls-action', type: 'button' }, [lucideIcon('phone-call'), el('span', { text: 'Call now' })]);
      dial.addEventListener('click', () => cxDial(dialDigits, call.callerName || '', dial));
      actions.appendChild(dial);
    }
    card.appendChild(actions);
    const meta = el('div', { class: 'calls-meta' });
    const fields = [
      ['Date & time', _callsWhen(call.startedAt, true)], ['Direction', recordingOnly ? '—' : (call.direction === 'outbound' ? 'Outgoing' : 'Incoming')],
      ['Source', source === '3cx' ? '3CX' : 'WhatsApp'], ['Status', CALL_STATUS_LABELS[call.status] || call.status || '—'],
      ['Duration', _callsDuration(call.durationSec)], ['Agent', userLabel(call.agentUsername, call.agentDisplayName || call.agentUsername || '—')],
      ['Pipeline', call.pipelineStage ? (PIPELINE_LABELS[call.pipelineStage] || call.pipelineStage) : '—'], ['Scope', state.callsAdmin.canSeeAll ? 'Team' : 'Mine'],
    ];
    for (const [label, value] of fields) meta.appendChild(el('div', { class: 'calls-meta-item' }, [el('small', { text: label }), el('strong', { text: value })]));
    card.appendChild(meta);
    if (call.recordingUrl) {
      const recording = el('div', { class: 'calls-recording' });
      const recordingHead = el('div', { class: 'calls-recording-head' });
      const title = el('div', { class: 'calls-recording-title' }, [lucideIcon('audio-lines'), el('strong', { text: (source === '3cx' ? '3CX' : 'WhatsApp') + ' recording' })]);
      const size = _callsFileSize(call.recordingSize);
      if (size) title.appendChild(el('small', { text: size }));
      recordingHead.appendChild(title);
      const downloadUrl = call.recordingUrl.includes('/api/recordings/play?')
        ? call.recordingUrl + '&download=1'
        : call.recordingUrl;
      recordingHead.appendChild(el('a', { class: 'calls-recording-download', href: downloadUrl, target: '_blank', rel: 'noopener', title: 'Download recording' }, [lucideIcon('download'), el('span', { text: 'Download' })]));
      recording.appendChild(recordingHead);
      recording.appendChild(el('audio', { class: 'calls-audio', controls: '', preload: 'none', src: call.recordingUrl }));
      card.appendChild(recording);
    } else if (_threecxRecordingIsPending(call, call.timestamp)) {
      card.appendChild(el('div', { class: 'calls-recording is-pending' }, [lucideIcon('loader-circle'), el('div', {}, [el('strong', { text: 'Recording is processing' }), el('p', { text: 'It will appear automatically on this call when 3CX finishes uploading it.' })])]));
    }
    pane.replaceChildren(card); refreshIcons(pane);
  }

  // ─── Recordings finder ─────────────────────────────────────────────
  // Searchable recording index. Server scopes results per role, so the
  // tab is open to everyone (agents auto-filtered to their own
  // extensions). Filters (q / agent / from / to) are sent server-side on
  // every Search; the agent <select> is populated from the distinct
  // agentLabel values the search returns. Each row gets an inline <audio>
  // player + a download link, both cookie-gated via /api/recordings/play.
  function ensureRecordingsInit() {
    if (state.recordings.wired) { _loadRecordings(); return; }
    state.recordings.wired = true;
    document.getElementById('recSearchBtn')?.addEventListener('click', () => _loadRecordings());
    document.getElementById('recQ')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); _loadRecordings(); }
    });
    document.getElementById('recAgent')?.addEventListener('change', () => _loadRecordings());
    document.getElementById('recFrom')?.addEventListener('change', () => _loadRecordings());
    document.getElementById('recTo')?.addEventListener('change', () => _loadRecordings());
    document.getElementById('recRefreshBtn')?.addEventListener('click', async () => {
      // Admins force a fresh MinIO listing (reindex) before reloading;
      // everyone else just re-runs the search against the cached index.
      const role = String(state.me?.role || '').toLowerCase();
      const isAdmin = role === 'admin' || role === 'super_admin' || role === 'super_sales' || !!state.me?.superAdmin;
      if (isAdmin) {
        try { await api('/api/recordings/reindex', { method: 'POST' }); } catch { /* non-fatal */ }
      }
      _loadRecordings();
    });
    _loadRecordings();
  }

  async function _loadRecordings() {
    const tbody = document.querySelector('#recordingsTable tbody');
    const empty = document.getElementById('recordingsEmpty');
    const builtAt = document.getElementById('recBuiltAt');
    if (!tbody) return;
    clear(tbody);
    empty?.classList.add('hidden');
    if (builtAt) builtAt.textContent = 'Loading…';
    const params = new URLSearchParams();
    const q = document.getElementById('recQ')?.value?.trim();
    const agent = document.getElementById('recAgent')?.value || '';
    const from = document.getElementById('recFrom')?.value || '';
    const to = document.getElementById('recTo')?.value || '';
    if (q) params.set('q', q);
    if (agent) params.set('agent', agent);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    params.set('limit', '200');
    try {
      const r = await api('/api/recordings/search?' + params.toString());
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      state.recordings.rows = Array.isArray(j.recordings) ? j.recordings : [];
      state.recordings.building = !!j.building;
      state.recordings.total = j.total || 0;
      state.recordings.builtAtTs = j.builtAt || null;
      _renderRecordings();
    } catch (err) {
      if (builtAt) builtAt.textContent = '';
      tbody.appendChild(el('tr', {}, [
        el('td', { colspan: '7', class: 'ts-book-empty', text: 'Failed to load: ' + (err.message || err) }),
      ]));
    }
  }

  function _humanSize(bytes) {
    const n = Number(bytes);
    if (!n || n < 0) return '—';
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
    return (n / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function _renderRecordings() {
    const tbody = document.querySelector('#recordingsTable tbody');
    const empty = document.getElementById('recordingsEmpty');
    const builtAt = document.getElementById('recBuiltAt');
    if (!tbody) return;
    clear(tbody);
    const rows = state.recordings.rows || [];
    // "آخر تحديث" label — index build time, plus an "index building" hint.
    if (builtAt) {
      const parts = [];
      if (state.recordings.builtAtTs) {
        parts.push('آخر تحديث: ' + MA.loc(new Date(state.recordings.builtAtTs), 'dt', 'en-GB'));
      }
      if (state.recordings.building) parts.push('(index en cours…)');
      if (typeof state.recordings.total === 'number') parts.push('(' + rows.length + ' / ' + state.recordings.total + ')');
      builtAt.textContent = parts.join(' ');
    }
    // Populate the agent <select> from the distinct agentLabel values
    // returned, preserving the current selection. "Tous" stays first.
    const agentSel = document.getElementById('recAgent');
    if (agentSel) {
      const cur = agentSel.value;
      const labels = Array.from(new Set(rows.map(r => r.agentLabel).filter(Boolean))).sort();
      // Only rebuild when the set actually changed, to avoid clobbering
      // the user's selection on every render.
      const existing = Array.from(agentSel.options).slice(1).map(o => o.value);
      const changed = labels.length !== existing.length || labels.some((l, i) => l !== existing[i]);
      if (changed) {
        clear(agentSel);
        agentSel.appendChild(el('option', { value: '', text: 'Tous' }));
        for (const lbl of labels) agentSel.appendChild(el('option', { value: lbl, text: lbl }));
        if (cur && labels.includes(cur)) agentSel.value = cur;
      }
    }
    if (!rows.length) {
      if (empty) {
        empty.textContent = state.recordings.building
          ? 'Index en cours de construction — réessayez dans un instant.'
          : 'Aucun enregistrement.';
        empty.classList.remove('hidden');
      }
      return;
    }
    empty?.classList.add('hidden');
    for (const row of rows) {
      const tr = el('tr');
      // Date
      tr.appendChild(el('td', { text: row.recordedAt ? MA.loc(new Date(row.recordedAt), 'dt', 'en-GB') : (row.dateStr || '—') }));
      // Agent
      tr.appendChild(el('td', { text: row.agentLabel || '—' }));
      // Client
      tr.appendChild(el('td', { text: row.clientName || '—' }));
      // Phone (raw as in key)
      tr.appendChild(el('td', { text: row.phone || '—' }));
      // Size
      tr.appendChild(el('td', { text: _humanSize(row.size) }));
      // Play — inline audio player (cookie-gated presign via /api/recordings/play)
      const playCell = el('td');
      const playUrl = '/api/recordings/play?key=' + encodeURIComponent(row.key);
      // Wide player so the seek bar is long enough to scrub precisely in long
      // recordings (e.g. jump to 1:20 in a 2-min call).
      playCell.appendChild(el('audio', { controls: '', src: playUrl, preload: 'none', style: 'width:520px;max-width:56vw;vertical-align:middle;' }));
      tr.appendChild(playCell);
      // Download
      const dlCell = el('td');
      const dl = el('a', { href: playUrl + '&download=1', class: 'call-open-chat-btn', title: 'Télécharger', target: '_blank', rel: 'noopener', download: '' });
      dl.appendChild(lucideIcon('download'));
      dlCell.appendChild(dl);
      tr.appendChild(dlCell);
      tbody.appendChild(tr);
    }
    refreshIcons(tbody);
  }

  // ─── Call history top bar (Step 4 of call-mgmt plan) ──────────────
  // Always-on strip below the global topbar. Loads /api/calls/recent
  // on boot, subscribes to socket 'call_updated' for live updates, and
  // refreshes the "time-ago" column every 30 s without re-fetching.
  const callHistory = (function () {
    const LS_COLLAPSED_KEY = 'leads7.callHistoryCollapsed';
    const STATUS_LABEL = {
      answered: 'Answered',
      ended: 'Ended',
      missed: 'Missed',
      rejected: 'Rejected',
      voicenote_instead: 'Voicenote',
      ringing: 'Ringing',
      noanswer: 'Sans réponse',
      busy: 'Occupé',
      canceled: 'Annulé',
    };
    const DIR_ICON = { inbound: 'arrow-down-left', outbound: 'arrow-up-right' };

    let _rows = [];   // canonical state — flat call shapes
    let _wired = false;

    function _formatTimeAgo(ts) {
      if (!ts) return '';
      const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
      if (sec < 60) return 'just now';
      if (sec < 3600) return Math.floor(sec / 60) + 'm';
      if (sec < 86400) return Math.floor(sec / 3600) + 'h';
      if (sec < 7 * 86400) return Math.floor(sec / 86400) + 'd';
      const d = new Date(ts);
      return MA.loc(d, 'd', 'en-GB', { day: '2-digit', month: 'short' });
    }

    // Light, locale-safe phone prettifier. Morocco (+212) gets grouped;
    // everything else just gets a leading + so it reads as a dialable number.
    function _formatPhone(p) {
      // Masked values ('0653****32') pass through — see formatPhoneForDisplay.
      if (String(p || '').includes('*')) return String(p);
      const digits = String(p || '').replace(/\D/g, '');
      if (!digits) return String(p || '');
      if (digits.startsWith('212') && digits.length === 12) {
        return '+212 ' + digits.slice(3, 6) + '-' + digits.slice(6);
      }
      return digits.length >= 8 ? '+' + digits : digits;
    }
    function _initial(name) {
      const s = String(name || '').trim();
      return s ? s.charAt(0).toUpperCase() : '?';
    }

    function _renderRow(c) {
      const row = el('div', {
        class: 'call-row' + (c.chatId ? '' : ' call-row-nochat'),
        'data-status': c.status,
        'data-stage': c.pipelineStage || '',
        'data-call-id': c.callId,
        title: (STATUS_LABEL[c.status] || c.status) + (c.agentDisplayName ? ' · by ' + c.agentDisplayName : ''),
      });
      const dirIcon = DIR_ICON[c.direction] || 'phone';

      // ── Head: direction icon · name + phone · time-ago ──
      const head = el('div', { class: 'call-row-head' });
      head.appendChild(el('div', { class: 'call-row-dir' }, [lucideIcon(dirIcon)]));
      // Known contact → full name on top, phone underneath. Unknown caller →
      // the phone becomes the title so the team sees a dialable number, not
      // "Unknown". Treat callerName == phone (or the "Unknown" placeholder) as
      // "no name" so we never print the same number twice.
      const phoneDigits = String(c.phone || '').replace(/\D/g, '');
      const nameDigits = String(c.callerName || '').replace(/\D/g, '');
      const hasName = c.callerName && c.callerName !== 'Unknown' && nameDigits !== phoneDigits;
      const id = el('div', { class: 'call-row-id' });
      id.appendChild(el('span', { class: 'call-row-name', text: hasName ? c.callerName : (c.phone ? _formatPhone(displayPhone(c.phone)) : (c.callerName || 'Unknown')) }));
      if (hasName && c.phone) id.appendChild(el('span', { class: 'call-row-phone', text: _formatPhone(displayPhone(c.phone)) }));
      head.appendChild(id);
      head.appendChild(el('span', { class: 'call-row-time', text: _formatTimeAgo(c.startedAt) }));
      row.appendChild(head);

      // ── Foot: pipeline stage · call status · assigned agent ──
      const foot = el('div', { class: 'call-row-foot' });
      if (c.pipelineStage) {
        foot.appendChild(el('span', { class: 'call-row-stage', title: 'Pipeline: ' + (PIPELINE_LABELS[c.pipelineStage] || c.pipelineStage) }, [
          el('span', { class: 'dot' }),
          el('span', { class: 'lbl', text: PIPELINE_LABELS[c.pipelineStage] || c.pipelineStage }),
        ]));
      }
      foot.appendChild(el('span', { class: 'call-row-status', text: STATUS_LABEL[c.status] || c.status }));
      if (c.agentDisplayName) {
        foot.appendChild(el('span', { class: 'call-row-agent', title: 'Assigned: ' + c.agentDisplayName }, [
          el('span', { class: 'call-row-agent-av', text: _initial(c.agentDisplayName) }),
          el('span', { class: 'call-row-agent-nm', text: c.agentDisplayName }),
        ]));
      }
      row.appendChild(foot);

      row.addEventListener('click', () => {
        // Click anywhere on the card → open that contact's inbox conversation
        // (switches view + creates the chat stub if it isn't loaded yet). By the
        // chat JID: the phone is masked for agents.
        openInboxForCall(c, hasName ? c.callerName : '')
          .then((ok) => { if (!ok) toast('Could not open conversation', 'error'); })
          .catch(() => toast('Could not open conversation', 'error'));
      });
      return row;
    }

    function _render() {
      const rowsEl = document.getElementById('chbRows');
      const countEl = document.getElementById('chbCount');
      if (!rowsEl) return;
      clear(rowsEl);
      if (!_rows.length) {
        rowsEl.appendChild(el('div', { class: 'chb-empty', text: 'No recent calls yet.' }));
      } else {
        for (const c of _rows) rowsEl.appendChild(_renderRow(c));
        refreshIcons(rowsEl);
      }
      if (countEl) {
        countEl.textContent = String(_rows.length);
        countEl.classList.toggle('zero', _rows.length === 0);
      }
    }

    function upsert(flatCall) {
      if (!flatCall || !flatCall.callId) return;
      const idx = _rows.findIndex(r => r.callId === flatCall.callId);
      if (idx >= 0) _rows[idx] = flatCall;
      else _rows.unshift(flatCall);
      _rows.sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
      if (_rows.length > 60) _rows.length = 60;   // hard cap so the lane stays manageable
      _render();
    }

    async function loadRecent() {
      try {
        const r = await api('/api/calls/recent?limit=30');
        if (!r.ok) return;
        const j = await r.json();
        _rows = Array.isArray(j.calls) ? j.calls : [];
        _render();
      } catch (_) { /* swallow — empty bar is acceptable */ }
    }

    function _applyCollapsedFromStorage() {
      const bar = document.getElementById('callHistoryBar');
      if (!bar) return;
      const stored = localStorage.getItem(LS_COLLAPSED_KEY);
      // Default expanded for new visitors so they discover the feature;
      // remember collapse preference after the first toggle.
      if (stored === 'collapsed') bar.classList.add('collapsed');
      else if (stored === 'expanded') bar.classList.remove('collapsed');
      else bar.classList.remove('collapsed');
    }

    function init() {
      if (_wired) return;
      _wired = true;
      _applyCollapsedFromStorage();
      const bar = document.getElementById('callHistoryBar');
      const toggle = () => {
        if (!bar) return;
        bar.classList.toggle('collapsed');
        localStorage.setItem(LS_COLLAPSED_KEY, bar.classList.contains('collapsed') ? 'collapsed' : 'expanded');
      };
      document.getElementById('chbToggleBtn')?.addEventListener('click', toggle);
      document.getElementById('chbChevBtn')?.addEventListener('click', toggle);
      loadRecent();
      // Refresh the time-ago column every 30 s without re-fetching.
      setInterval(() => {
        if (document.hidden) return;
        const cells = document.querySelectorAll('#chbRows .call-row-time');
        if (!cells.length) return;
        cells.forEach((cell, i) => {
          const c = _rows[i];
          if (c) cell.textContent = _formatTimeAgo(c.startedAt);
        });
      }, 30000);
    }

    return { init, loadRecent, upsert };
  })();

  // ── Morocco time ──────────────────────────────────────────────────────────
  // Morocco left UTC+1 on 2026-09-20. Chrome still ships an older ICU tz
  // database, so the browser puts Africa/Casablanca — and the team's own local
  // time — an hour ahead of the real clock. We cannot fix the browser's zone,
  // so we stop asking it: the server hands us the true offset on
  // /api/app-version, we add it to the instant and format in UTC, which every
  // engine gets right. Until the offset arrives (or if the request fails) we
  // use what the browser believes, i.e. the old behaviour; once Chrome catches
  // up the two agree and this is a no-op.
  // The server's offset only applies from `since` onward: a stale browser is
  // wrong about the change it has not heard of, not about history — a message
  // from before 2026-09-20 really was sent at +01:00 and must keep its hour.
  const MA = {
    offMin: null,                         // real Morocco offset, minutes east of UTC
    since: 0,                             // instant that offset came into force
    _fmts: new Map(),
    believedMin(ms) {
      try {
        const p = MA.dtf('probe', 'en-US', { timeZone: 'Africa/Casablanca', timeZoneName: 'longOffset' }, true)
          .formatToParts(new Date(ms)).find(x => x.type === 'timeZoneName');
        const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec((p && p.value) || '');
        return m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0)) : 0;
      } catch { return 0; }
    },
    setOffset(min, since) {
      if (!Number.isFinite(min)) return false;
      const s = Number.isFinite(since) ? since : 0;
      if (MA.offMin === min && MA.since === s) return false;
      MA.offMin = min; MA.since = s;
      return true;
    },
    offsetAt(ms) { return (MA.offMin == null || ms < MA.since) ? MA.believedMin(ms) : MA.offMin; },
    // A Date whose UTC fields read as the Morocco wall clock for that instant.
    at(v) {
      const d = v instanceof Date ? v : new Date(typeof v === 'number' ? v : Date.parse(v));
      const ms = d.getTime();
      if (!Number.isFinite(ms)) return d;
      return new Date(ms + MA.offsetAt(ms) * 60000);
    },
    // Cached formatter pinned to UTC, so it renders MA.at() values as-is.
    dtf(key, locale, opts, raw) {
      let f = MA._fmts.get(key);
      if (!f) { f = new Intl.DateTimeFormat(locale, raw ? opts : Object.assign({}, opts, { timeZone: 'UTC' })); MA._fmts.set(key, f); }
      return f;
    },
    fmt(key, locale, opts, v) { const d = MA.at(v); return Number.isFinite(d.getTime()) ? MA.dtf(key, locale, opts).format(d) : ''; },
    // 'YYYY-MM-DD' of the Morocco calendar day (now, when called with nothing).
    dayKey(v) { return MA.fmt('ca-day', 'en-CA', {}, v === undefined ? Date.now() : v); },
    // Local midnight of Morocco's day — for code that walks the calendar with getDate()/setDate().
    today() { return new Date(MA.dayKey() + 'T00:00:00'); },
    // Drop-in for d.toLocaleString / toLocaleDateString / toLocaleTimeString ('dt' / 'd' / 't')
    // on a REAL instant: same default fields as the native call (ECMA-402 ToDateTimeOptions),
    // Morocco's wall clock instead of the browser's. Not for dates built from local parts
    // (new Date(y, m, d)) — those already are the browser's calendar.
    loc(v, kind, locale, opts) {
      const d = v instanceof Date ? v : new Date(v);
      if (!Number.isFinite(d.getTime())) return 'Invalid Date';
      const o = Object.assign({}, opts);
      if (o.timeZone !== undefined) return kind === 'd' ? d.toLocaleDateString(locale, o) : kind === 't' ? d.toLocaleTimeString(locale, o) : d.toLocaleString(locale, o);
      const has = ks => ks.some(k => o[k] !== undefined);
      let need = !has(['dateStyle', 'timeStyle']);
      if (need && kind !== 't' && has(['weekday', 'year', 'month', 'day'])) need = false;
      if (need && kind !== 'd' && has(['dayPeriod', 'hour', 'minute', 'second', 'fractionalSecondDigits'])) need = false;
      if (need && kind !== 't') Object.assign(o, { year: 'numeric', month: 'numeric', day: 'numeric' });
      if (need && kind !== 'd') Object.assign(o, { hour: 'numeric', minute: 'numeric', second: 'numeric' });
      return MA.fmt('loc|' + String(locale) + '|' + JSON.stringify(o), locale, o, d);
    },
    pull() {
      try {
        return fetch('/api/app-version', { cache: 'no-store' })
          .then(r => (r.ok ? r.json() : null))
          .then(j => !!(j && MA.setOffset(j.tzOffsetMinutes, j.tzOffsetSince)))
          .catch(() => false);
      } catch { return Promise.resolve(false); }
    },
  };
  // The first paint may already have used the browser's wrong offset; repaint
  // the list once the truth lands. Re-asked every 10 min — a tz shift is rare.
  MA.pull().then(changed => { try { if (changed && typeof renderConversations === 'function' && state.chats) renderConversations(); } catch { /* not booted yet */ } });
  setInterval(MA.pull, 600000);

  function formatChatDate(ts) {
    if (!ts) return '';
    const d = new Date(typeof ts === 'number' ? ts : Date.parse(ts));
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const days = Math.floor((now - d) / 86400000);
    if (days === 0) return MA.fmt('us-hm', 'en-US', { hour: '2-digit', minute: '2-digit', hour12: false }, d);
    if (days === 1) return 'Yesterday';
    if (days < 7) return MA.fmt('us-wd', 'en-US', { weekday: 'short' }, d);
    return MA.fmt('gb-dmy', 'en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }, d);
  }
  function formatTime(ts) {
    const d = new Date(typeof ts === 'number' ? ts : Date.parse(ts));
    if (isNaN(d.getTime())) return '';
    return MA.fmt('us-hm', 'en-US', { hour: '2-digit', minute: '2-digit', hour12: false }, d);
  }

  // ============ Screen routing ============
  function showScreen(name) {
    if (name !== 'app') _ltStopPolling();
    E.loginScreen.classList.toggle('hidden', name !== 'login');
    E.qrScreen.classList.toggle('hidden', name !== 'qr');
    if (E.cloudApiErrorScreen) E.cloudApiErrorScreen.classList.toggle('hidden', name !== 'cloud-api-error');
    E.app.classList.toggle('hidden', name !== 'app');
    refreshIcons();
  }

  // ============ Auth ============
  const USERNAME_KEY = 'wa-claude-username';

  async function tryLogin() {
    const username = (E.usernameInput?.value || '').trim().toLowerCase();
    const password = E.passwordInput.value;
    E.loginError.classList.add('hidden');
    try {
      const r = await fetchT('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await r.json();
      if (data.success && data.token) {
        state.token = data.token;
        state.me = data.user || null;
        localStorage.setItem(TOKEN_KEY, state.token);
        if (username) localStorage.setItem(USERNAME_KEY, username);
        // A session already booted in this page lifetime (401-expired → login
        // screen → possibly a DIFFERENT user): reload instead of booting in
        // place so no closure/DOM state from the old session bleeds into the
        // new one. Boot picks the token back up from localStorage.
        if (state._sessionBooted) { location.reload(); return; }
        bootApp();
      } else {
        const msg = data.error || 'Wrong username or password';
        E.loginError.textContent = msg;
        E.loginError.classList.remove('hidden');
      }
    } catch {
      E.loginError.classList.remove('hidden');
    }
  }

  async function verifyToken() {
    if (!state.token) return false;
    try {
      // Fire both boot fetches in PARALLEL — /api/status doesn't depend on
      // /api/me's result, so awaiting them together shaves a full round-trip
      // off the hard-refresh boot (the app screen waits on both). The early
      // 401/5xx returns below still take precedence; the in-flight /api/status
      // is simply discarded on those bail-out paths.
      const [r, sr] = await Promise.all([api('/api/me'), api('/api/status')]);
      if (r.status === 401) {
        state.token = '';
        state.me = null;
        localStorage.removeItem(TOKEN_KEY);
        return false;
      }
      // Don't kick the user to login on 5xx — those are transient
      // server errors and the cached token may still be perfectly
      // valid. Just bail out of this verify pass and let the next
      // poll retry. Returning true here keeps the UI mounted.
      if (!r.ok) {
        console.warn('[verifyToken] non-401 error from /api/me:', r.status);
        return !!state.me;
      }
      const data = await r.json();
      state.me = data.user || null;
      state.myWorkPhone = data.workPhone || null;
      if (Array.isArray(data.paymentModes) && data.paymentModes.length) state.paymentModes = data.paymentModes;
      _mergeAdminAliases(data.adminAliases);
      state.impersonatedBy = data.impersonatedBy || null;
      state.mySupport2Username = String(data.support2Username || '').toLowerCase();
      // NOTE: data.threecxWebclientUrl is deliberately ignored. Calls happen
      // in-page via the softphone; nothing may open the external 3CX client
      // (swat:B1). The server still sends the field — harmless, unused.
      // WA status (fetched in parallel above)
      if (sr.ok) {
        const sd = await sr.json();
        state.clientState = sd.state || 'DISCONNECTED';
        state.info = sd.info || null;
        state.driver = sd.driver || 'baileys';     // WHATSAPP_DRIVER from server
        state.initError = sd.initError || null;    // surfaced on cloud-api-error screen
      }
      return true;
    } catch {
      return false;
    }
  }

  // Teacher UI is English-only (2026-07-21 audit). Static index.html strings
  // that are SHARED with sales/admin (who keep French/Darija) get rewritten
  // here for role=teacher only — teacher-only sections are English directly in
  // the HTML, and JS-rendered shared surfaces use inline role checks (_ltT…).
  function _applyTeacherStaticEnglish() {
    const setText = (sel, text) => { const n = document.querySelector(sel); if (n) n.textContent = text; };
    const swapText = (node, from, to) => {
      for (const child of (node?.childNodes || [])) {
        if (child.nodeType === 3 && child.textContent.includes(from)) {
          child.textContent = child.textContent.replace(from, to);
          return;
        }
      }
    };
    try {
      // Website Tests tab (shared with sales/admin)
      setText('[data-view="level-tests"] span:not(.view-tab-badge)', 'Website Tests');
      if (state.me?.teacherManager === true) {
        // The availability surface is relabelled by _applyAvailabilityManagerLabels,
        // which covers admins and super_sales too — a manager is a manager
        // whatever their role, and two places setting the same three strings
        // is how they end up disagreeing.
        setText('[data-view="teacher-tests"] span', "Teachers' tests");
        swapText(document.querySelector('#teacherTestsView .ts-title-block h2'), 'My tests', "Teachers' tests");
        setText('#teacherTestsView .ts-subtitle', "All teachers' placement-test history and evaluations.");
      }
      // Website Tests view statics
      const ltH2 = document.querySelector('#levelTestsView .ts-title-block h2');
      if (ltH2 && ltH2.lastChild && ltH2.lastChild.nodeType === 3) ltH2.lastChild.textContent = ' Website Tests';
      setText('#levelTestsView .ts-subtitle', 'Track personalized website tests and open the complete submission as soon as it arrives.');
      document.getElementById('ltRefreshBtn')?.setAttribute('title', 'Refresh');
      for (const [status, label] of [['', 'All '], ['pending', 'Pending '], ['reviewed', 'Reviewed '], ['sent', 'Sent ']]) {
        const chip = document.querySelector('#ltChips .lt-chip[data-status="' + status + '"]');
        if (chip && chip.firstChild && chip.firstChild.nodeType === 3) chip.firstChild.textContent = label;
      }
      document.getElementById('ltSearch')?.setAttribute('placeholder', 'Search a name, a phone…');
      setText('label[for="ltAgentFilter"] > span', 'Agent');
      setText('label[for="ltTeacherFilter"] > span', 'Teacher');
      setText('label[for="ltDateFilter"] > span', 'Test date');
      setText('label[for="ltSearch"] > span:first-child', 'Search');
      setText('#ltClearFilters span', 'Reset');
      document.getElementById('ltClearFilters')?.setAttribute('title', 'Clear filters');
      setText('#ltLoading', 'Loading…');
      setText('#ltEmpty strong', 'No tests found');
      setText('#ltEmpty > span:last-child', 'Change the filters or wait for a new submission.');
      document.getElementById('ltDrawerPanel')?.setAttribute('aria-label', 'Test details');
      // Team chat segmented toggle (both copies: inbox sidebar + team view)
      document.querySelectorAll('.teamchat-seg-btn[data-seg="team"]').forEach(b => swapText(b, 'Équipe', 'Team'));
      document.querySelectorAll('.teamchat-seg').forEach(s => s.setAttribute('aria-label', 'Inbox, Team or Rooms'));
      document.getElementById('teamchatSearch')?.setAttribute('placeholder', 'Search a teammate, a room…');
      // My availability leftovers
      setText('#taLoading', 'Loading…');
      document.getElementById('taDays')?.setAttribute('aria-label', 'Pick a day');
    } catch (_) { /* cosmetic — never block the role gates */ }
  }

  // Who manages OTHER people's test availability, as opposed to their own.
  // Mirrors the server's canPickTeacher (see /api/test-schedule/availability-editor):
  // a teacher flagged teacherManager, or a sales-admin who is not a teacher.
  function _managesOthersAvailability() {
    return state.me?.teacherManager === true
      || (_isSalesAdmin() && state.me?.role !== 'teacher');
  }

  // The page reads as "My schedule" by default, which is the truth for a
  // teacher: administration sets their hours and they look at them. For a
  // manager the same page is where those hours are DECIDED, so it is renamed.
  // (Ayoub, 2026-07-27: "administration howa li ikhtaro availability, machi
  // teachers" — and earlier, that an admin hunting for a teacher's schedule
  // never opened a tab called "My availability".)
  function _applyAvailabilityManagerLabels() {
    if (!_managesOthersAvailability()) return;
    try {
      const nav = document.querySelector('[data-view="teacher-availability"] span');
      if (nav) nav.textContent = "Teachers' availability";
      const h2 = document.querySelector('#teacherAvailabilityView .ts-title-block h2');
      for (const child of (h2?.childNodes || [])) {
        if (child.nodeType === 3 && child.textContent.includes('My schedule')) {
          child.textContent = child.textContent.replace('My schedule', "Teachers' availability");
          break;
        }
      }
      const sub = document.querySelector('#teacherAvailabilityView .ts-subtitle');
      if (sub) sub.textContent = "Choose a teacher, then tap the times they can't take a test — removed from the booking grid and the bot straight away.";
    } catch (_) { /* cosmetic — never block the role gates */ }
  }

  function applyRoleGates() {
    clientCallRingtones.setUser(state.me);
    initClientRingtoneSettings();
    const role = state.me?.role || 'agent';
    document.body.classList.toggle('role-admin', role === 'admin');
    document.body.classList.toggle('role-agent', role === 'agent');
    document.body.classList.toggle('role-trial-agent', role === 'trial_agent');
    document.body.classList.toggle('role-viewer', role === 'viewer');
    document.body.classList.toggle('role-teacher', role === 'teacher');
    document.body.classList.toggle('role-teacher-manager', role === 'teacher' && state.me?.teacherManager === true);
    // meta_review = Meta App Review reviewer: FB/IG inbox ONLY. CSS hides every
    // [data-meta-review-hidden] nav/chrome element + all non-inbox views; the
    // server is the real boundary (see /api/inbox/{chats,send} + _canAccessLead).
    document.body.classList.toggle('role-meta-review', role === 'meta_review');
    // Super-sales = sales super-user: admin-equivalent sales chrome (Calls tab,
    // bulk owner edit, CSV) but NOT system/super-admin chrome (handled in CSS).
    document.body.classList.toggle('role-sales-admin', role === 'super_sales');
    // System-admin gate (2026-06-12) — STRICT admin chrome, visible only to
    // role admin / super_admin. super_sales sees data-admin-only elements
    // (its body carries no .role-agent class, so that hide rule never fires)
    // but must NOT see data-system-admin-only ones (Virements / payment
    // verification). CSS: body:not(.role-system-admin) [data-system-admin-only].
    document.body.classList.toggle('role-system-admin', role === 'admin' || role === 'super_admin');
    document.querySelector('[data-view="campaigns"]')?.classList.toggle('hidden', role !== 'admin');
    // Super-admin gate — only the allow-listed user (server's
    // SUPER_ADMIN_USERNAMES) can see the WhatsApp Disconnect button.
    document.body.classList.toggle('role-super-admin', !!state.me?.superAdmin);
    // Vacation page is for the whole team (teachers + sales) to request + system-admins
    // to approve. Toggle the nav here (super_sales has no role-* body class).
    _applyAvailabilityManagerLabels();
    try {
      const _vacRole = ['teacher', 'agent', 'super_sales', 'admin', 'super_admin'].includes(role);
      document.querySelector('[data-vac-nav="true"]')?.classList.toggle('vac-show', _vacRole);
      _tvRefreshBadge(); // pending count badge (system-admins only, fn-gated)
      // Availability-request queue: Sales sees their own requests/replies;
      // sales-admins + the teacher-manager process everybody's requests.
      const _arRole = ['agent', 'super_sales', 'admin', 'super_admin'].includes(role)
        || state.me?.teacherManager === true;
      document.querySelector('[data-ar-nav="true"]')?.classList.toggle('ar-show', _arRole);
      _arRefreshBadge();
      // Website Tests — agents see only their own submissions; reviewer roles
      // see the broader queue (the server owns the real row scope).
      const _ltRole = ['agent', 'teacher', 'super_sales', 'admin', 'super_admin'].includes(role);
      document.querySelector('[data-leveltest-nav="true"]')?.classList.toggle('lt-show', _ltRole);
      // Test recordings — same reviewer audience as Niveau (server gate is
      // requireLevelTestReviewer). querySelectorAll, not querySelector: the nav
      // may grow a second entry and a single-node toggle would strand it.
      document.querySelectorAll('[data-trec-nav="true"]').forEach(n => n.classList.toggle('trec-show', _ltRole));
    } catch (_) { /* advisory */ }
    // Topbar user pill
    const pill = document.getElementById('userPill');
    if (state.me) {
      pill?.classList.remove('hidden');
      const name = document.getElementById('userPillName');
      const roleEl = document.getElementById('userPillRole');
      const avatar = document.getElementById('userPillAvatar');
      if (name) name.textContent = userLabel(state.me.username, state.me.displayName || state.me.username);
      if (roleEl) {
        roleEl.textContent = state.me.teacherManager === true ? 'teacher manager' : (state.me.role || '');
        roleEl.classList.toggle('role-admin', state.me.role === 'admin');
      }
      paintPersonalAvatar(avatar, state.me);
    } else {
      pill?.classList.add('hidden');
    }
    // Collapse any top-bar dropdown group whose every menu item is role-gated
    // out for the current user — without this a group like Contacts▾ (all
    // children data-agent-only) would still render an empty trigger for a
    // teacher. Generic: works for any present or future .view-tab-group.
    hideEmptyTabGroups();
    // Teachers get English-only chrome on shared statics (audit 2026-07-21).
    if (role === 'teacher') _applyTeacherStaticEnglish();
  }

  const PERSONAL_AVATARS = [
    { id: 'initials', label: 'Your initials' }, { id: 'amina', label: 'Mint hijab' },
    { id: 'sara', label: 'Peach waves' }, { id: 'nora', label: 'Lilac hijab' },
    { id: 'lina', label: 'Rose waves' }, { id: 'salma', label: 'Blue hijab' },
    { id: 'yasmin', label: 'Golden waves' }, { id: 'adam', label: 'Sunny boy' },
    { id: 'ilyas', label: 'Curly boy' }, { id: 'yanis', label: 'Cool boy' },
  ];
  function paintPersonalAvatar(node, user) {
    if (!node) return node;
    const name = userLabel(user?.username, user?.displayName || user?.username || '?');
    const id = user?.avatarId;
    node.replaceChildren();
    const illustrated = id !== 'initials' && PERSONAL_AVATARS.some(o => o.id === id);
    node.classList.toggle('personal-avatar-image', illustrated);
    if (illustrated) {
      const img = el('img', { src: '/avatars/' + id + '.svg', alt: '', draggable: 'false' });
      img.addEventListener('error', () => { if (node.contains(img)) { node.textContent = initials(name); node.classList.remove('personal-avatar-image'); } }, { once: true });
      node.appendChild(img);
    } else node.textContent = initials(name);
    return node;
  }
  function personalAvatar(user, classes) {
    return paintPersonalAvatar(el('div', { class: classes + ' ' + avatarColorClass(user?.id) }), user);
  }
  function renderPersonalSettings() {
    const me = state.me;
    if (!me) return;
    const put = (id, value) => { const node = document.getElementById(id); if (node) node.textContent = value; };
    const phone = state.myWorkPhone || {};
    put('settingsAgentName', userLabel(me.username, me.displayName || me.username));
    put('settingsAgentRole', String(me.role || 'Team member').replaceAll('_', ' '));
    put('settingsWorkPhone', phone.number || me.whatsappPhone || 'Not assigned');
    put('settingsExtension', phone.extension || 'Not assigned');
    put('settingsPhoneSource', phone.number
      ? (phone.shared ? 'Shared 3CX work line' : 'Assigned 3CX work line')
      : me.whatsappPhone ? 'WhatsApp number saved on your profile' : 'No work number is configured for this account.');
    paintPersonalAvatar(document.getElementById('settingsProfileAvatar'), me);
    paintPersonalAvatar(document.getElementById('userPillAvatar'), me);
    document.querySelectorAll('#settingsAvatarChoices button').forEach(btn => {
      btn.setAttribute('aria-pressed', String(btn.dataset.avatarId === (me.avatarId || 'initials')));
      if (btn.dataset.avatarId === 'initials') paintPersonalAvatar(btn.querySelector('.settings-avatar-picture'), { ...me, avatarId: 'initials' });
    });
  }
  function initClientRingtoneSettings() {
    const btn = document.getElementById('personalSettingsBtn');
    const select = document.getElementById('callRingtoneSelect');
    const preview = document.getElementById('callRingtonePreview');
    const status = document.getElementById('callRingtoneStatus');
    if (!btn || !select || !preview || !status) return;
    if (btn._wired) { renderPersonalSettings(); return; }
    btn._wired = true;
    let optionSignature = '';
    const render = () => {
      const signature = clientCallRingtones.options.map(o => o.id).join(',');
      if (signature !== optionSignature) {
        optionSignature = signature; select.replaceChildren();
        const groups = new Map();
        clientCallRingtones.options.forEach(o => {
          if (!groups.has(o.group)) { const group = el('optgroup', { label: o.group }); select.appendChild(group); groups.set(o.group, group); }
          groups.get(o.group).appendChild(el('option', { value: o.id, text: o.label }));
        });
      }
      select.value = clientCallRingtones.getChoice();
      const scopes = clientCallRingtones.getScopes();
      document.getElementById('callRingtoneClients').checked = scopes.client;
      document.getElementById('callRingtoneTeam').checked = scopes.team;
      const playing = clientCallRingtones.isPreviewing(), ringing = clientCallRingtones.isRinging();
      const available = !!(window.AudioContext || window.webkitAudioContext || window.Audio);
      preview.textContent = playing ? 'Stop preview' : 'Preview';
      preview.setAttribute('aria-pressed', String(playing)); preview.disabled = ringing || !available;
      status.textContent = ringing ? 'Preview is paused while a client call rings.'
        : clientCallRingtones.getAudioError() || (!available ? 'Audio previews are unavailable in this browser.'
        : clientCallRingtones.isSaved() ? 'Saved for your account in this browser.'
        : 'Used until this page closes. Browser storage is unavailable.');
      const voiceNote = document.getElementById('settingsVoiceNote');
      const personalized = clientCallRingtones.options.some(o => o.id === select.value && o.group.startsWith('Voice'));
      voiceNote.classList.toggle('hidden', !personalized);
      voiceNote.textContent = personalized ? 'This voice reminder says “' + clientCallRingtones.getVoiceName() + '”.' : '';
    };
    btn.addEventListener('click', () => showView('settings'));
    select.addEventListener('change', () => clientCallRingtones.setChoice(select.value));
    document.getElementById('callRingtoneClients').addEventListener('change', e => clientCallRingtones.setScope('client', e.target.checked));
    document.getElementById('callRingtoneTeam').addEventListener('change', e => clientCallRingtones.setScope('team', e.target.checked));
    preview.addEventListener('click', () => {
      if (clientCallRingtones.isPreviewing()) clientCallRingtones.stopPreview();
      else if (!clientCallRingtones.preview()) status.textContent = 'Could not play preview. Check your browser audio.';
    });
    window.addEventListener('hashchange', () => { if (window.location.hash === '#settings' && state.me) showView('settings'); });
    const choices = document.getElementById('settingsAvatarChoices');
    for (const option of PERSONAL_AVATARS) {
      const avatarBtn = el('button', { class: 'settings-avatar-option', type: 'button', 'data-avatar-id': option.id, 'aria-pressed': 'false', 'aria-label': option.label });
      avatarBtn.append(personalAvatar({ ...state.me, avatarId: option.id }, 'settings-avatar-picture'), el('span', { text: option.label }));
      avatarBtn.addEventListener('click', async () => {
        const me = state.me, token = state.token, note = document.getElementById('settingsAvatarStatus');
        if (!me || me.avatarId === option.id) return;
        const allButtons = [...choices.querySelectorAll('button')]; allButtons.forEach(b => { b.disabled = true; });
        note.textContent = 'Saving your avatar…';
        try {
          const response = await api('/api/preferences', { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: 'profile-avatar:' + me.id, prefs: { avatarId: option.id } }) });
          const data = await response.json();
          if (state.me?.id !== me.id || state.token !== token) return;
          if (!response.ok) throw new Error(data.error || 'Could not save your avatar. Try again.');
          state.me.avatarId = data.avatarId;
          const peer = state.teamUsers?.find(u => u.id === me.id); if (peer) peer.avatarId = data.avatarId;
          renderPersonalSettings(); note.textContent = 'Saved to your profile. Teammates see this avatar in Leads7.';
        } catch (error) { if (state.me?.id === me.id && state.token === token) note.textContent = error.message || 'Could not save your avatar. Try again.'; }
        finally { allButtons.forEach(b => { b.disabled = false; }); }
      });
      choices.appendChild(avatarBtn);
    }
    clientCallRingtones.subscribe(render); render(); renderPersonalSettings();
  }

  // Hide a .view-tab-group trigger when ALL of its .view-tab-item children are
  // role-hidden (display:none via the CSS role gates). Reads each item's OWN
  // computed display so a closed menu (display:none on .view-tab-menu) doesn't
  // false-positive — getComputedStyle returns the item's own resolved display
  // regardless of the ancestor's visibility. Called after applyRoleGates sets
  // the body.role-* classes (and the vac-show/lt-show JS toggles) so the CSS
  // gates have already resolved. The group itself uses .view-tab-group display
  // :inline-flex by default; we toggle a .hidden helper that forces none.
  function hideEmptyTabGroups() {
    document.querySelectorAll('.view-tab-group').forEach(group => {
      const items = group.querySelectorAll('.view-tab-item');
      if (!items.length) return;
      let anyVisible = false;
      items.forEach(it => {
        if (window.getComputedStyle(it).display !== 'none') anyVisible = true;
      });
      group.classList.toggle('hidden', !anyVisible);
    });
  }

  // Read-only deep link used by SwufiPay to open the exact payment
  // conversation. The server remains the authority for chat ownership and
  // message access; this parser only accepts canonical 1-1 WhatsApp JIDs and
  // never sends, marks, or mutates a payment.
  function _requestedPaymentChatLink() {
    try {
      const params = new URLSearchParams(window.location.search);
      const chatId = String(params.get('chat') || '').trim();
      if (!/^\d{8,17}@s\.whatsapp\.net$/i.test(chatId)) return null;
      const rawMessageId = String(params.get('message') || '').trim();
      const messageId = rawMessageId && rawMessageId.length <= 240 && /^[A-Za-z0-9._:=@-]+$/.test(rawMessageId)
        ? rawMessageId
        : '';
      return { chatId, messageId };
    } catch { return null; }
  }

  async function _openRequestedPaymentChat(link) {
    if (!link?.chatId) return;
    // Opening a payment hand-off is inspection, not an Inbox decision. Keep
    // this chat read-only until the user navigates away: do not clear unread
    // state and do not auto-create a lead merely because the link was opened.
    state._paymentChatReadOnlyChatId = link.chatId;
    showView('inbox');
    try { await loadConversations(); } catch { /* the message endpoint remains ownership-gated */ }
    if (link.messageId) openChatAtMessage(link.chatId, link.messageId, String(link.chatId).split('@')[0]);
    else openChatForPhoneNumber(String(link.chatId).split('@')[0]);
  }

  // ── Team status: Disponible / Pause / Fin de journée (Ayoub 2026-09-27) ──
  // The top-bar pill sets MY status; sales admins also get the Équipe button
  // (live counts + who is there, since when, pauses, unread chats). A prompt
  // asks once a day "tu commences ?". Server: /api/team/status + the
  // 'team_status' socket event (sent to sales admins + the user only).
  const TST_ROLES = new Set(['agent', 'admin', 'super_admin', 'super_sales']);
  const TST_REASON = { lunch: 'Déjeuner', prayer: 'Prière', short: 'Pause courte' };
  const TST_LONG_PAUSE_MIN = 30;
  const tst = { rows: [], skew: 0, wired: false };
  function _tstNow() { return Date.now() + tst.skew; }
  function _tstHm(ms) { return ms ? MA.fmt('gb-hm', 'en-GB', { hour: '2-digit', minute: '2-digit' }, new Date(ms)) : ''; }
  function _tstMine() { return tst.rows.find(r => r.userId === state.me?.id) || null; }
  function _tstName(r) { return userLabel(r.username, r.displayName || r.username); }
  // 'none' = hasn't started today (the server says off + not started).
  function _tstKey(r) { return !r || (!r.started && r.status === 'off') ? 'none' : r.status; }
  function _tstPauseMin(r) { return r && r.status === 'pause' && r.since ? Math.max(0, Math.floor((_tstNow() - r.since) / 60000)) : 0; }
  function _tstPauseTotal(r) { return Math.max(0, (r._pauseBase || 0) + _tstPauseMin(r)); }
  function _tstUnread(userId) {
    return (state.chats || []).filter(c => c.assignedTo?.userId === userId && (c.unread || 0) > 0).length;
  }
  function _tstUpsert(row, quiet) {
    if (!row || !row.userId) return;
    // pauseMinutes already counts the ongoing pause — keep the closed part so
    // the total keeps ticking client-side between server updates.
    row._pauseBase = (row.pauseMinutes || 0) - _tstPauseMin(row);
    const i = tst.rows.findIndex(x => x.userId === row.userId);
    if (i >= 0) tst.rows[i] = row; else tst.rows.push(row);
    if (!quiet) _tstRender();
  }
  function _tstRenderMine() {
    const btn = document.getElementById('tstIconBtn');
    if (!btn) return;
    const r = _tstMine(), key = _tstKey(r);
    let label = 'Pas commencé', since = '';
    if (key === 'available') { label = 'Disponible'; since = 'depuis ' + _tstHm(r.since); }
    else if (key === 'pause') { label = r.reason ? 'Pause · ' + TST_REASON[r.reason] : 'Pause'; since = _tstPauseMin(r) + ' min'; }
    else if (key === 'off') { label = 'Fin de journée'; since = r.endedAt ? 'à ' + _tstHm(r.endedAt) : ''; }
    document.getElementById('tstIconDot').dataset.status = key;
    document.getElementById('tstMyNow').dataset.status = key;
    // Started from another tab / by an admin → the "tu commences ?" prompt is moot.
    if (key !== 'none') _tstHideStart();
    btn.classList.toggle('tst-long', key === 'pause' && _tstPauseMin(r) >= TST_LONG_PAUSE_MIN);
    btn.title = 'Mon statut : ' + label + (since ? ' (' + since + ')' : '') + (_isSalesAdmin() ? ' · Équipe' : '');
    btn.setAttribute('aria-label', btn.title);
    document.getElementById('tstMyLabel').textContent = label;
    document.getElementById('tstMySince').textContent = since;
    document.querySelectorAll('#tstMenu .tst-opt').forEach(o => o.classList.toggle('on', o.dataset.set === key));
    document.querySelectorAll('#tstMenu .tst-reasons button').forEach(b => b.classList.toggle('on', key === 'pause' && r.reason === b.dataset.reason));
    const foot = document.getElementById('tstMenuFoot');
    foot.textContent = '';
    if (r && r.started) foot.append(el('span', { text: 'Arrivée ' + _tstHm(r.arrivedAt) }), el('span', { text: 'Pauses aujourd\'hui ' + _tstPauseTotal(r) + ' min' }));
  }
  function _tstRenderTeam() {
    const sec = document.getElementById('tstTeam');
    if (!sec) return;
    const admin = _isSalesAdmin();
    sec.classList.toggle('hidden', !admin);
    const badge = document.getElementById('tstIconBadge');
    if (!admin) { badge.classList.add('hidden'); return; }
    const c = { available: 0, pause: 0, off: 0 };
    let longPauses = 0;
    for (const r of tst.rows) {
      const k = _tstKey(r);
      c[k === 'available' || k === 'pause' ? k : 'off']++;
      if (k === 'pause' && r.userId !== state.me?.id && _tstPauseMin(r) >= TST_LONG_PAUSE_MIN) longPauses++;
    }
    document.getElementById('tstCountAvailable').textContent = c.available;
    document.getElementById('tstCountPause').textContent = c.pause;
    document.getElementById('tstCountOff').textContent = c.off;
    // Admin badge: how many people have been on pause for 30 min or more.
    badge.textContent = String(longPauses);
    badge.title = longPauses + ' en pause depuis ' + TST_LONG_PAUSE_MIN + ' min ou plus';
    badge.classList.toggle('hidden', !longPauses);
    if (!document.getElementById('tstPop').classList.contains('hidden')) _tstRenderPanel();
  }
  function _tstRenderPanel() {
    const list = document.getElementById('tstPanelList');
    list.textContent = '';
    const order = { available: 0, pause: 1, none: 2, off: 3 };
    const rows = [...tst.rows].sort((a, b) => order[_tstKey(a)] - order[_tstKey(b)] || _tstName(a).localeCompare(_tstName(b)));
    if (!rows.length) { list.append(el('div', { class: 'tst-empty', text: 'Personne dans l\'équipe.' })); return; }
    for (const r of rows) {
      const k = _tstKey(r), name = _tstName(r), dot = k === 'none' ? 'off' : k;
      const chip = k === 'none' ? 'Pas commencé'
        : k === 'available' ? 'Disponible · ' + _tstHm(r.since)
        : k === 'pause' ? (r.reason ? 'Pause ' + TST_REASON[r.reason].toLowerCase() : 'Pause') + ' · ' + _tstPauseMin(r) + ' min'
        : 'Fin de journée' + (r.endedAt ? ' · ' + _tstHm(r.endedAt) : '');
      const meta = [];
      if (r.arrivedAt) meta.push('Arrivée ' + _tstHm(r.arrivedAt));
      const pt = _tstPauseTotal(r);
      if (pt) meta.push('Pauses ' + pt + ' min');
      const unread = _tstUnread(r.userId);
      if (unread) meta.push(unread + (unread > 1 ? ' chats non lus' : ' chat non lu'));
      list.append(el('div', { class: 'tst-row', 'data-status': dot }, [
        el('div', { class: 'tst-row-av' }, [initials(name), el('span', { class: 'tst-dot', 'data-status': dot })]),
        el('div', { class: 'tst-row-main' }, [
          el('div', { class: 'tst-row-top' }, [
            el('span', { class: 'tst-row-name', text: name }),
            el('span', { class: 'tst-chip', 'data-status': dot, text: chip }),
            r.online ? null : el('span', { class: 'tst-chip tst-offline', text: 'Hors ligne' }),
          ]),
          meta.length ? el('div', { class: 'tst-row-meta', text: meta.join(' · ') }) : null,
          el('div', { class: 'tst-row-actions' }, [
            el('button', { type: 'button', text: 'Voir ses chats', onclick: () => _tstShowChatsOf(r.userId) }),
          ]),
        ]),
      ]));
    }
  }
  function _tstRender() { _tstRenderMine(); _tstRenderTeam(); }
  // Admin: open the Inbox filtered on that person's chats (existing assignee filter).
  function _tstShowChatsOf(userId) {
    const F = state.inboxFilters;
    if (!F || !F.assignees) return;
    _tstClosePop();
    F.assignees.clear();
    F.assignees.add(userId);
    showView('inbox');
    syncFilterMenuChecks();
    updateFilterLabel();
    renderConversations();
  }
  function _tstClosePop() {
    document.getElementById('tstPop')?.classList.add('hidden');
    document.getElementById('tstIconBtn')?.setAttribute('aria-expanded', 'false');
  }
  async function _tstSet(status, reason) {
    try {
      const r = await api('/api/team/status', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, reason: reason || null }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(j.error || 'Statut non enregistré', 'error'); return false; }
      if (j.status) _tstUpsert(j.status);
      return true;
    } catch { toast('Statut non enregistré', 'error'); return false; }
  }
  async function _tstLoad() {
    try {
      const r = await api('/api/team/status');
      if (!r.ok) return;
      const j = await r.json();
      if (Number.isFinite(j.serverNow)) tst.skew = j.serverNow - Date.now();
      tst.rows = [];
      for (const row of (j.statuses || [])) _tstUpsert(row, true);
      _tstRender();
    } catch { /* the pill stays "Pas commencé" until the next load */ }
  }
  function _tstHideStart() { document.getElementById('tstStartModal')?.classList.add('hidden'); }
  function _tstMaybeStart() {
    if (state.impersonatedBy) return;
    if (_tstKey(_tstMine()) !== 'none') return;
    let seen = '';
    try { seen = localStorage.getItem('leads7.tstStartShown') || ''; } catch { /* private mode */ }
    if (seen === MA.dayKey()) return;
    const name = String(state.me?.displayName || state.me?.username || '').trim();
    document.getElementById('tstStartTitle').textContent = 'Bonjour ' + (name.split(/\s+/)[0] || '');
    document.getElementById('tstStartAv').textContent = initials(name);
    document.getElementById('tstStartModal').classList.remove('hidden');
  }
  function _tstRememberStart() { try { localStorage.setItem('leads7.tstStartShown', MA.dayKey()); } catch { /* private mode */ } }
  function teamStatusInit() {
    const wrap = document.getElementById('tstWrap');
    if (!wrap || !TST_ROLES.has(String(state.me?.role || '').toLowerCase())) return;
    wrap.classList.remove('hidden');
    if (!tst.wired) {
      tst.wired = true;
      const iconBtn = document.getElementById('tstIconBtn'), pop = document.getElementById('tstPop');
      iconBtn.addEventListener('click', e => {
        e.stopPropagation();
        const open = pop.classList.contains('hidden');
        _tstClosePop();
        if (open) {
          _tstRender();
          if (_isSalesAdmin()) _tstRenderPanel();
          pop.classList.remove('hidden');
          iconBtn.setAttribute('aria-expanded', 'true');
          _tstLoad(); // fresh numbers every time it opens
        }
      });
      pop.addEventListener('click', async e => {
        e.stopPropagation();
        const b = e.target.closest('#tstMenu [data-set]');
        // Admins keep the popover open (the team list is under it); agents are done.
        if (b && await _tstSet(b.dataset.set, b.dataset.reason) && !_isSalesAdmin()) _tstClosePop();
      });
      document.addEventListener('click', _tstClosePop);
      document.addEventListener('keydown', e => { if (e.key === 'Escape') _tstClosePop(); });
      document.getElementById('tstStartGo').addEventListener('click', async () => {
        if (await _tstSet('available')) { _tstRememberStart(); _tstHideStart(); }
      });
      document.getElementById('tstStartLater').addEventListener('click', () => { _tstRememberStart(); _tstHideStart(); });
      setInterval(_tstRender, 30000); // pause minutes / long-pause blink
    }
    refreshIcons(wrap);
    _tstLoad().then(_tstMaybeStart);
  }

  // ── Serveur & coûts: Supabase compute usage (Ayoub 2026-09-27) ──────────────
  // "n3ref ch7al kan-stahlek o wach n-na9es wla nzid". Super admins only. The
  // server samples Supabase every minute (/api/infra/usage); the page shows the
  // verdict first, then the curves, the size ladder, the cost and what eats the DB.
  const _inf = { range: '24h', data: null, top: null, timer: null, wired: false, loading: false };
  const INF_TONE = { down: 'good', 'down-risky': 'warn', keep: 'info', up: 'bad' };
  const INF_GB = 1073741824;
  const _infEsc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const _infGB = b => (b == null ? '—' : (b / INF_GB).toFixed(1) + ' GB');
  function _infBytes(b) {
    if (b == null) return '—';
    const u = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = Number(b), i = 0;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return (v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)) + ' ' + u[i];
  }
  const _infUsd = n => '$' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: Math.abs(n) >= 100 ? 0 : 2 });
  const _infNum = n => Number(n || 0).toLocaleString('fr-FR');
  const _infPct = n => (n == null ? '—' : (Math.round(n * 10) / 10) + '%');
  const _infTier = id => (_inf.data?.tiers || []).find(t => t.id === id) || null;
  // The next size down from the current one — the comparison drawn on every meter/curve.
  function _infLower() {
    const tiers = _inf.data?.tiers || [];
    const i = tiers.findIndex(t => t.id === _inf.data?.current);
    return i > 0 ? tiers[i - 1] : null;
  }
  function _infRows() {
    const s = _inf.data?.series;
    if (!s) return [];
    return s.rows.map(a => Object.fromEntries(s.fields.map((f, i) => [f, a[i]])));
  }

  function initInfraView() {
    if (!state.me?.superAdmin) return;
    if (!_inf.wired) {
      _inf.wired = true;
      document.getElementById('infRange')?.addEventListener('click', e => {
        const b = e.target.closest('[data-range]');
        if (!b || b.dataset.range === _inf.range) return;
        _inf.range = b.dataset.range;
        document.querySelectorAll('#infRange [data-range]').forEach(x => x.classList.toggle('on', x === b));
        _infLoad();
      });
      let rz = null;
      window.addEventListener('resize', () => {
        clearTimeout(rz);
        rz = setTimeout(() => { if (state.activeView === 'infra' && _inf.data) _infRenderCharts(); }, 150);
      });
    }
    _infLoad();
    _infLoadTop();
    clearInterval(_inf.timer);
    _inf.timer = setInterval(() => {
      if (state.activeView !== 'infra') { clearInterval(_inf.timer); _inf.timer = null; return; }
      if (!document.hidden) _infLoad();
    }, 60000);
  }

  async function _infLoad() {
    if (_inf.loading) return;
    _inf.loading = true;
    try {
      const r = await api('/api/infra/usage?range=' + encodeURIComponent(_inf.range));
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      _inf.data = j;
      _infRender();
    } catch (err) {
      const v = document.getElementById('infVerdict');
      if (v && !_inf.data) { v.dataset.tone = 'bad'; v.innerHTML = `<div class="inf-v-title">Chargement impossible — ${_infEsc(err.message)}</div>`; }
    } finally {
      _inf.loading = false;
    }
  }

  async function _infLoadTop() {
    const box = document.getElementById('infTop');
    if (!box) return;
    try {
      const r = await api('/api/infra/top-queries', { timeoutMs: 20000 });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      _inf.top = j;
      _infRenderTop();
    } catch (err) {
      box.innerHTML = `<div class="inf-card-h"><h3>Qui consomme la base</h3></div><div class="inf-empty">Indisponible — ${_infEsc(err.message)}</div>`;
    }
  }

  function _infRender() {
    const d = _inf.data;
    const cur = _infTier(d.current);
    const chip = document.getElementById('infTier');
    if (chip) {
      chip.innerHTML = cur
        ? `<b>${_infEsc(cur.name)}</b> · ${cur.cores} vCPU ${cur.dedicated ? 'dédiés' : 'partagés'} · ${cur.memGb} GB RAM · <b>${_infUsd(cur.monthly)}/mois</b>`
        : 'Taille inconnue';
    }
    _infRenderFresh();
    _infRenderVerdict();
    _infRenderKpis();
    _infRenderCharts();
    _infRenderTiers();
    _infRenderCost();
  }

  function _infRenderFresh() {
    const d = _inf.data;
    const box = document.getElementById('infFresh');
    if (!box) return;
    if (d.lastError) { box.dataset.tone = 'bad'; box.textContent = 'Collecte en erreur: ' + d.lastError; return; }
    if (!d.source) { box.dataset.tone = 'bad'; box.textContent = 'Collecte non configurée'; return; }
    const at = d.lastOkAt || d.nowAt;
    box.dataset.tone = at && d.serverNow - at < 180000 ? 'good' : 'warn';
    box.textContent = at ? 'Mesuré à ' + MA.fmt('gb-hm', 'en-GB', { hour: '2-digit', minute: '2-digit' }, at) + ' · chaque minute' : 'Première mesure dans moins d\'une minute…';
  }

  function _infChecksHtml(fit) {
    if (!fit) return '';
    const icon = { ok: '✓', warn: '!', no: '✕' };
    return `<ul class="inf-checks">${fit.checks.map(c => `<li data-s="${c.status}"><span class="inf-ck">${icon[c.status]}</span>${_infEsc(c.detail)}</li>`).join('')}</ul>`;
  }

  function _infRenderVerdict() {
    const d = _inf.data, v = d.verdict;
    const box = document.getElementById('infVerdict');
    if (!box) return;
    if (!v) {
      box.dataset.tone = 'wait';
      box.innerHTML = `<div class="inf-v-title">En attente de la première mesure…</div><div class="inf-v-sub">${_infEsc(d.lastError || 'La collecte démarre ~20 s après le déploiement, puis chaque minute.')}</div>`;
      return;
    }
    const cur = _infTier(d.current), tgt = _infTier(v.target), lower = _infLower();
    let title, why, fit;
    if (v.action === 'down') {
      title = `Tu peux descendre en ${tgt.name} — économie ${_infUsd(v.saving)}/mois (${_infUsd(v.saving * 12)}/an)`;
      why = `Ce que ${tgt.name} devrait tenir :`; fit = v.fits.find(f => f.id === tgt.id);
    } else if (v.action === 'down-risky') {
      title = `${tgt.name} est possible, avec un risque — économie ${_infUsd(v.saving)}/mois`;
      why = `${tgt.name} face à ta charge :`; fit = v.fits.find(f => f.id === tgt.id);
    } else if (v.action === 'up') {
      title = `${cur.name} est trop juste — passe en ${tgt.name} (+${_infUsd(-v.saving)}/mois)`;
      why = `Ce qui bloque en ${cur.name} :`; fit = v.fits.find(f => f.id === cur.id);
    } else {
      title = `${cur.name} est la bonne taille — ne change rien`;
      why = lower ? `Pourquoi pas ${lower.name} :` : ''; fit = lower ? v.fits.find(f => f.id === lower.id) : null;
    }
    const p = d.peaks || {};
    const cover = Math.min(100, Math.round(100 * (p.hours || 0) / (d.minHours || 72)));
    const sb = d.sinceBoot;
    box.dataset.tone = INF_TONE[v.action] || 'info';
    box.innerHTML = `
      <div class="inf-v-main">
        <div class="inf-v-kicker">${v.enoughData ? 'Verdict' : 'Verdict provisoire'}</div>
        <div class="inf-v-title">${_infEsc(title)}</div>
        ${why ? `<div class="inf-v-sub">${_infEsc(why)}</div>${_infChecksHtml(fit)}` : ''}
      </div>
      <div class="inf-v-side">
        ${v.enoughData ? '<div class="inf-v-ok">Assez de mesures (3 jours + un soir de cours) ✓</div>' : `
          <div class="inf-v-prog-l">Mesures: <b>${p.hours || 0} h</b> / ${d.minHours || 72} h · ${p.eveningSeen ? 'soir de cours vu ✓' : 'pas encore de soir 19–23 h'}</div>
          <div class="inf-prog"><span style="width:${cover}%"></span></div>
          <div class="inf-v-note">Le verdict devient sûr après 3 jours, dont un soir de cours en semaine.</div>`}
        ${v.action === 'keep' ? '' : '<div class="inf-v-note">Changer de taille redémarre la base : quelques minutes de coupure pour Leads7, s2, le markaz et le login students7 — à faire un dimanche matin. Revenir en arrière reste possible.</div>'}
        ${sb ? `<div class="inf-v-boot">Depuis le dernier redémarrage de la base (${sb.days} j) : CPU moyen <b>${_infPct(sb.cpuAvg)}</b> · cache <b>${_infPct(sb.hit)}</b> · écrit <b>${_infBytes(sb.writtenPerDay)}/jour</b> · fichiers temporaires <b>${_infBytes(sb.tempPerDay)}/jour</b></div>` : ''}
      </div>`;
  }

  function _infMeter(pct, markPct, markLabel) {
    const w = Math.max(0, Math.min(100, pct || 0));
    const tone = w >= 85 ? 'bad' : w >= 65 ? 'warn' : 'good';
    const mark = markPct != null && markPct > 0 && markPct < 100
      ? `<i class="inf-mark" style="left:${markPct}%" title="${_infEsc(markLabel)}"><em>${_infEsc(markLabel)}</em></i>` : '';
    return `<div class="inf-meter" data-tone="${tone}"><span style="width:${w}%"></span>${mark}</div>`;
  }

  function _infSpark(key) {
    const vals = _infRows().map(r => r[key]).filter(x => x != null);
    if (vals.length < 2) return '';
    const n = vals.length, max = Math.max(...vals) || 1, W = 120, H = 28;
    const pts = vals.map((v, i) => `${(W * i / (n - 1)).toFixed(1)},${(H - 2 - (H - 4) * v / max).toFixed(1)}`).join(' ');
    return `<svg class="inf-spark" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" aria-hidden="true"><polyline points="${pts}"/></svg>`;
  }

  function _infRenderKpis() {
    const d = _inf.data, box = document.getElementById('infKpis');
    if (!box) return;
    const now = d.now || {}, cap = d.capacity || {}, p = d.peaks || {}, lower = _infLower();
    const memTotal = cap.memTotal || 0;
    const memPct = memTotal && now.mem != null ? 100 * now.mem / memTotal : null;
    const connPct = cap.maxConn && now.conn != null ? 100 * now.conn / cap.maxConn : null;
    const diskPct = cap.diskSize && now.disk != null ? 100 * now.disk / cap.diskSize : null;
    const card = (k, label, value, unit, sub, meter, spark) => `
      <div class="inf-kpi" data-k="${k}">
        <div class="inf-kpi-top"><span class="inf-kpi-l">${label}</span>${spark}</div>
        <div class="inf-kpi-v">${value}<small>${unit}</small></div>
        ${meter}
        <div class="inf-kpi-s">${sub}</div>
      </div>`;
    box.innerHTML = [
      card('cpu', 'CPU', now.cpu == null ? '—' : Math.round(now.cpu), '%',
        `pic 7 j <b>${_infPct(p.cpuMax)}</b> · moyenne depuis le boot <b>${_infPct(d.sinceBoot?.cpuAvg)}</b>`,
        _infMeter(now.cpu), _infSpark('cpu')),
      card('ram', 'RAM utilisée', now.mem == null ? '—' : (now.mem / INF_GB).toFixed(1), ` / ${(memTotal / INF_GB).toFixed(1)} GB`,
        `dont cache Postgres <b>${_infGB(cap.shmem)}</b> · pic 7 j <b>${_infGB(p.memPeak)}</b>`,
        _infMeter(memPct, lower && memTotal ? 100 * lower.memGb * INF_GB / memTotal : null, lower ? `${lower.name} ${lower.memGb} GB` : ''), _infSpark('mem')),
      card('conn', 'Connexions', now.conn == null ? '—' : now.conn, ` / ${cap.maxConn || '—'}`,
        `pic 7 j <b>${p.connPeak ?? '—'}</b>${lower ? ` · ${_infEsc(lower.name)} max <b>${lower.direct}</b>` : ''}`,
        _infMeter(connPct, lower && cap.maxConn ? 100 * lower.direct / cap.maxConn : null, lower ? `${lower.name} ${lower.direct}` : ''), _infSpark('conn')),
      card('disk', 'Base de données', now.db == null ? '—' : (now.db / INF_GB).toFixed(1), ' GB',
        `disque <b>${_infGB(now.disk)}</b> / ${_infGB(cap.diskSize)} · cache <b>${_infPct(d.sinceBoot?.hit)}</b>`,
        _infMeter(diskPct), ''),
    ].join('');
  }

  // Round an axis max up to 1 / 2 / 2.5 / 5 × 10^k.
  function _infNiceMax(v) {
    if (!(v > 0)) return 1;
    const k = Math.pow(10, Math.floor(Math.log10(v)));
    for (const m of [1, 2, 2.5, 5, 10]) if (m * k >= v) return m * k;
    return 10 * k;
  }

  // Hand-drawn SVG time chart: areas/lines per series, dashed capacity marks,
  // 19:00–23:00 class evenings shaded, hover crosshair + tooltip.
  function _infChart(host, cfg) {
    const rows = cfg.rows;
    const plot = host.querySelector('.inf-plot');
    if (!rows.length) { plot.innerHTML = '<div class="inf-empty">Pas encore de mesures — la courbe se remplit chaque minute.</div>'; return; }
    const W = Math.max(280, plot.clientWidth || 560), H = 176, P = { l: 44, r: 12, t: 12, b: 24 };
    const t1 = _inf.data.serverNow, span = { '24h': 864e5, '7d': 7 * 864e5, '30d': 30 * 864e5 }[_inf.range], t0 = t1 - span;
    let top = cfg.yMin || 0;
    for (const s of cfg.series) for (const r of rows) { const v = r[s.key]; if (v != null && v > top) top = v; }
    for (const m of cfg.marks || []) if (m.y <= top * 1.6 && m.y > top) top = m.y;
    const yMax = cfg.yMax || _infNiceMax(top * 1.08);
    const X = t => P.l + (W - P.l - P.r) * (t - t0) / span;
    const Y = v => P.t + (H - P.t - P.b) * (1 - Math.min(v, yMax) / yMax);
    // Raw points land every 1–2 min (Supabase caches its scrape); a gap is > 5 min.
    const step = _inf.data.series.kind === 'raw' ? 100e3 : 900e3;
    const out = [];
    // Class evenings (Morocco wall clock).
    const off = MA.offsetAt(t1) * 60e3;
    for (let day = Math.floor((t0 + off) / 864e5) * 864e5; day < t1 + off; day += 864e5) {
      const a = Math.max(t0, day + 19 * 36e5 - off), b = Math.min(t1, day + 23 * 36e5 - off);
      if (b > a && new Date(day).getUTCDay() !== 0) out.push(`<rect class="inf-eve" x="${X(a).toFixed(1)}" y="${P.t}" width="${(X(b) - X(a)).toFixed(1)}" height="${H - P.t - P.b}"/>`);
    }
    for (let i = 0; i <= 4; i++) {
      const v = yMax * i / 4, y = Y(v).toFixed(1);
      out.push(`<line class="inf-grid" x1="${P.l}" x2="${W - P.r}" y1="${y}" y2="${y}"/><text class="inf-ax" x="${P.l - 6}" y="${(+y + 3.5).toFixed(1)}" text-anchor="end">${_infEsc(cfg.fmt(v))}</text>`);
    }
    const tickEvery = { '24h': 3 * 36e5, '7d': 864e5, '30d': 5 * 864e5 }[_inf.range] * (W < 520 ? 2 : 1);
    for (let t = Math.ceil((t0 + off) / tickEvery) * tickEvery - off; t < t1; t += tickEvery) {
      const label = _inf.range === '24h'
        ? MA.fmt('gb-hm', 'en-GB', { hour: '2-digit', minute: '2-digit' }, t)
        : MA.fmt('fr-wdd', 'fr-FR', { weekday: 'short', day: 'numeric' }, t);
      out.push(`<text class="inf-ax" x="${X(t).toFixed(1)}" y="${H - 6}" text-anchor="middle">${_infEsc(label)}</text>`);
    }
    for (const s of cfg.series) {
      let seg = [], prevT = null;
      const segs = [];
      for (const r of rows) {
        const v = r[s.key];
        if (v == null || (prevT != null && r.t - prevT > 3 * step)) { if (seg.length) segs.push(seg); seg = []; }
        if (v != null) seg.push([X(r.t), Y(v)]);
        prevT = r.t;
      }
      if (seg.length) segs.push(seg);
      for (const sg of segs) {
        const line = sg.map(([x, y], i) => (i ? 'L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1)).join('');
        if (s.fill) out.push(`<path class="inf-area" style="fill:${s.color}" d="${line}L${sg[sg.length - 1][0].toFixed(1)} ${Y(0).toFixed(1)}L${sg[0][0].toFixed(1)} ${Y(0).toFixed(1)}Z"/>`);
        out.push(`<path class="inf-line${s.faint ? ' faint' : ''}" style="stroke:${s.color}" d="${sg.length === 1 ? line + 'h1' : line}"/>`);
      }
    }
    for (const m of cfg.marks || []) {
      if (m.y > yMax) continue;
      const y = Y(m.y).toFixed(1);
      out.push(`<line class="inf-markline" x1="${P.l}" x2="${W - P.r}" y1="${y}" y2="${y}"/><text class="inf-marktext" x="${W - P.r - 4}" y="${(+y - 4).toFixed(1)}" text-anchor="end">${_infEsc(m.label)}</text>`);
    }
    out.push(`<line class="inf-cross" x1="0" x2="0" y1="${P.t}" y2="${H - P.b}" style="display:none"/>`);
    plot.innerHTML = `<svg viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" role="img" aria-label="${_infEsc(cfg.title)}">${out.join('')}</svg><div class="inf-tip hidden"></div>`;
    const svg = plot.querySelector('svg'), cross = svg.querySelector('.inf-cross'), tip = plot.querySelector('.inf-tip');
    svg.addEventListener('mousemove', e => {
      const box = svg.getBoundingClientRect();
      const px = (e.clientX - box.left) * W / box.width;
      if (px < P.l || px > W - P.r) { cross.style.display = 'none'; tip.classList.add('hidden'); return; }
      const t = t0 + (px - P.l) / (W - P.l - P.r) * span;
      let lo = 0, hi = rows.length - 1;
      while (lo < hi) { const mid = (lo + hi) >> 1; if (rows[mid].t < t) lo = mid + 1; else hi = mid; }
      if (lo > 0 && Math.abs(rows[lo - 1].t - t) < Math.abs(rows[lo].t - t)) lo--;
      const r = rows[lo];
      if (Math.abs(r.t - t) > 3 * step) { cross.style.display = 'none'; tip.classList.add('hidden'); return; }
      const x = X(r.t);
      cross.setAttribute('x1', x); cross.setAttribute('x2', x); cross.style.display = '';
      const when = MA.fmt('fr-dhm', 'fr-FR', { weekday: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }, r.t);
      tip.innerHTML = `<b>${_infEsc(when)}</b>` + cfg.series.map(s => `<span><i style="background:${s.color}"></i>${_infEsc(s.label)} <b>${r[s.key] == null ? '—' : _infEsc(cfg.fmt(r[s.key]))}</b></span>`).join('');
      tip.classList.remove('hidden');
      const left = x * box.width / W;
      tip.style.left = Math.min(box.width - tip.offsetWidth - 4, Math.max(4, left + 12)) + 'px';
    });
    svg.addEventListener('mouseleave', () => { cross.style.display = 'none'; tip.classList.add('hidden'); });
  }

  function _infRenderCharts() {
    const d = _inf.data, box = document.getElementById('infCharts');
    if (!box || !d) return;
    const rows = _infRows(), bucket = d.series?.kind === 'bucket', lower = _infLower(), cap = d.capacity || {};
    const gb = v => (v / INF_GB).toFixed(v >= 10 * INF_GB ? 0 : 1) + ' GB';
    const mbs = v => (v / 1e6 >= 10 ? Math.round(v / 1e6) : (v / 1e6).toFixed(1)) + ' MB/s';
    const charts = [
      { id: 'cpu', title: 'CPU', note: bucket ? 'moyenne 15 min · trait fin = pic' : '% des 2 cœurs, chaque minute', fmt: v => Math.round(v) + '%', yMax: 100,
        series: [{ key: 'cpu', label: 'CPU', color: 'var(--inf-c1)', fill: true }].concat(bucket ? [{ key: 'cpuMax', label: 'Pic', color: 'var(--inf-c1)', faint: true }] : []),
        marks: lower && !lower.dedicated ? [{ y: 35, label: `${lower.name} (CPU partagé) : ~35% soutenu max` }] : [] },
      { id: 'ram', title: 'RAM utilisée', note: 'hors cache disque du système', fmt: gb, yMax: cap.memTotal ? Math.ceil(cap.memTotal / INF_GB) * INF_GB : null,
        series: [{ key: 'mem', label: 'RAM', color: 'var(--inf-c2)', fill: true }].concat(bucket ? [{ key: 'memMax', label: 'Pic', color: 'var(--inf-c2)', faint: true }] : []),
        marks: lower ? [{ y: lower.memGb * INF_GB, label: `${lower.name} : ${lower.memGb} GB` }] : [] },
      { id: 'conn', title: 'Connexions à la base', note: 'directes (pooler inclus)', fmt: v => String(Math.round(v)), yMax: cap.maxConn || null,
        series: [{ key: 'conn', label: 'Connexions', color: 'var(--inf-c3)', fill: true }].concat(bucket ? [{ key: 'connMax', label: 'Pic', color: 'var(--inf-c3)', faint: true }] : []),
        marks: lower ? [{ y: lower.direct, label: `${lower.name} : ${lower.direct} max` }] : [] },
      { id: 'io', title: 'Disque (lecture / écriture)', note: lower ? `limite de base ${lower.name} : ${Math.round(lower.ioMbps / 8)} MB/s` : '', fmt: mbs,
        series: bucket
          ? [{ key: 'wrMax', label: 'Écriture (pic)', color: 'var(--inf-c4)', fill: true }, { key: 'rdMax', label: 'Lecture (pic)', color: 'var(--inf-c5)' }]
          : [{ key: 'wr', label: 'Écriture', color: 'var(--inf-c4)', fill: true }, { key: 'rd', label: 'Lecture', color: 'var(--inf-c5)' }],
        marks: lower ? [{ y: lower.ioMbps / 8 * 1e6, label: `${lower.name} : ${Math.round(lower.ioMbps / 8)} MB/s` }] : [] },
    ];
    if (box.children.length !== charts.length) {
      box.innerHTML = charts.map(c => `
        <div class="inf-card inf-chart" data-chart="${c.id}">
          <div class="inf-card-h"><h3>${_infEsc(c.title)}</h3><span class="inf-note"></span></div>
          <div class="inf-plot"></div>
        </div>`).join('');
    }
    for (const c of charts) {
      const host = box.querySelector(`[data-chart="${c.id}"]`);
      host.querySelector('.inf-note').textContent = c.note;
      _infChart(host, { ...c, rows });
    }
  }

  function _infRenderTiers() {
    const d = _inf.data, box = document.getElementById('infTiers');
    if (!box) return;
    const v = d.verdict;
    const label = { ok: 'Ça passe', warn: 'Juste', no: 'Trop petit' };
    const rows = (d.tiers || []).map(t => {
      const fit = v?.fits.find(f => f.id === t.id);
      const tags = (t.id === d.current ? '<span class="inf-tag cur">Actuel</span>' : '') + (v && t.id === v.target && t.id !== d.current ? '<span class="inf-tag tgt">Conseillé</span>' : '');
      const tip = fit ? fit.checks.map(c => `${c.status === 'ok' ? '✓' : c.status === 'warn' ? '!' : '✕'} ${c.detail}`).join('\n') : '';
      return `<tr class="${t.id === d.current ? 'cur' : ''}${v && t.id === v.target ? ' tgt' : ''}">
        <td><b>${_infEsc(t.name)}</b>${tags}</td>
        <td>${t.cores} ${t.dedicated ? 'dédiés' : 'partagés'}</td>
        <td>${t.memGb} GB</td>
        <td>${t.direct}</td>
        <td class="num"><b>${_infUsd(t.monthly)}</b></td>
        <td>${fit ? `<span class="inf-fit" data-s="${fit.status}" title="${_infEsc(tip)}">${label[fit.status]}</span>` : '—'}</td>
      </tr>`;
    }).join('');
    box.innerHTML = `
      <div class="inf-card-h"><h3>Quelle taille ?</h3><span class="inf-note">selon tes pics des 7 derniers jours</span></div>
      <div class="inf-scroll"><table class="inf-table inf-tiers"><thead><tr><th>Taille</th><th>CPU</th><th>RAM</th><th>Connexions</th><th class="num">Prix</th><th>Pour toi</th></tr></thead><tbody>${rows}</tbody></table></div>`;
  }

  function _infRenderCost() {
    const d = _inf.data, box = document.getElementById('infCost');
    if (!box) return;
    const c = d.cost, cur = _infTier(d.current), v = d.verdict, tgt = v ? _infTier(v.target) : null;
    if (!c || !cur) { box.innerHTML = '<div class="inf-card-h"><h3>Coût</h3></div><div class="inf-empty">—</div>'; return; }
    const save = v && v.saving > 0 && tgt ? `
      <div class="inf-save">
        <div>En <b>${_infEsc(tgt.name)}</b> : ${_infUsd(tgt.monthly)}/mois</div>
        <div class="inf-save-v">−${_infUsd(v.saving)}/mois · −${_infUsd(v.saving * 12)}/an</div>
      </div>` : '';
    box.innerHTML = `
      <div class="inf-card-h"><h3>Coût</h3><span class="inf-note">${_infEsc(cur.name)} · $${c.hourly}/heure</span></div>
      <div class="inf-cost">
        <div><span>Ce mois jusqu'à maintenant</span><b>${_infUsd(c.soFar)}</b></div>
        <div><span>Fin du mois</span><b>${_infUsd(c.monthEnd)}</b></div>
        <div><span>Par an</span><b>${_infUsd(c.monthly * 12)}</b></div>
      </div>
      ${save}
      <div class="inf-foot">Compute de la base Leads7 seulement (au prix ${_infEsc(cur.name)}). Le plan Supabase Pro et les autres projets sont facturés à part.</div>`;
  }

  function _infRenderTop() {
    const box = document.getElementById('infTop');
    if (!box || !_inf.top) return;
    const q = _inf.top.queries || [], t = _inf.top.tables || [];
    const first = q[0];
    const insight = first && first.pct >= 40
      ? `<div class="inf-insight"><b>Une seule requête = ${_infPct(first.pct)} du temps de la base.</b> L'optimiser baisse le CPU et les fichiers temporaires — c'est le levier le plus sûr pour descendre d'une taille.</div>` : '';
    const qRows = q.map(r => `<tr>
        <td class="inf-pctcell"><span class="inf-pctbar" style="width:${Math.min(100, r.pct || 0)}%"></span><b>${_infPct(r.pct)}</b></td>
        <td class="inf-q" title="${_infEsc(r.query)}"><code>${_infEsc(r.query)}</code></td>
        <td>${_infEsc(r.role || '')}</td>
        <td class="num">${_infNum(r.calls)}</td>
        <td class="num">${r.mean_ms == null ? '—' : _infNum(Math.round(r.mean_ms)) + ' ms'}</td>
        <td class="num">${Number(r.temp_bytes) > 0 ? _infBytes(r.temp_bytes) : '—'}</td>
      </tr>`).join('');
    const tRows = t.map(r => `<tr><td><code>${_infEsc(r.name)}</code></td><td class="num">${_infBytes(r.bytes)}</td><td class="num">${_infNum(r.rows)}</td></tr>`).join('');
    box.innerHTML = `
      <div class="inf-card-h"><h3>Qui consomme la base</h3><span class="inf-note">temps cumulé depuis le dernier reset des statistiques</span></div>
      ${insight}
      <div class="inf-top-grid">
        <div class="inf-scroll"><table class="inf-table inf-qt"><thead><tr><th>Part</th><th>Requête</th><th>Rôle</th><th class="num">Appels</th><th class="num">Moy.</th><th class="num">Temp</th></tr></thead><tbody>${qRows}</tbody></table></div>
        <div class="inf-scroll"><table class="inf-table"><thead><tr><th>Plus grosses tables</th><th class="num">Taille</th><th class="num">Lignes</th></tr></thead><tbody>${tRows}</tbody></table></div>
      </div>`;
  }

  // Security tracking: tell the server what machine this is and, when the
  // member allows it, where they are (GPS). The IP only ever gives the mobile
  // operator's city; GPS gives the member's real one, so a shared or stolen
  // login shows up. Impersonation ("Log as") is the admin's browser, so the
  // server drops it. Runs once per boot and again every 3h while open; the
  // browser caches a granted location so it isn't prompted every time.
  let _deviceReportTimer = null;
  function _machineFingerprint() {
    try {
      const nav = navigator || {};
      let gpu = '';
      try {
        const gl = document.createElement('canvas').getContext('webgl') || document.createElement('canvas').getContext('experimental-webgl');
        const dbg = gl && gl.getExtension('WEBGL_debug_renderer_info');
        if (dbg) gpu = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '');
      } catch {}
      return {
        platform: nav.userAgentData?.platform || nav.platform || '',
        screenW: (window.screen && screen.width) || 0,
        screenH: (window.screen && screen.height) || 0,
        cores: nav.hardwareConcurrency || 0,
        memory: nav.deviceMemory || 0,
        gpu: gpu.slice(0, 80),
        language: nav.language || '',
        timezone: (Intl.DateTimeFormat().resolvedOptions().timeZone) || '',
      };
    } catch { return {}; }
  }
  function _getGps() {
    return new Promise(resolve => {
      if (!('geolocation' in navigator) || !window.isSecureContext) { resolve({ status: 'unsupported' }); return; }
      let done = false;
      const finish = v => { if (!done) { done = true; resolve(v); } };
      try {
        navigator.geolocation.getCurrentPosition(
          pos => finish({ status: 'ok', lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy }),
          err => finish({ status: err && err.code === 1 ? 'denied' : (err && err.code === 3 ? 'timeout' : 'unavailable') }),
          { enableHighAccuracy: false, timeout: 15000, maximumAge: 10 * 60 * 1000 },
        );
      } catch { finish({ status: 'unavailable' }); }
      setTimeout(() => finish({ status: 'timeout' }), 16000);
    });
  }
  async function reportDeviceAndLocation() {
    if (state.impersonatedBy) return; // admin's browser, not the agent's
    const send = async () => {
      if (!state.token || state.impersonatedBy) return;
      try {
        const [machine, gps] = [_machineFingerprint(), await _getGps()];
        await api('/api/me/device', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ machine, gps }) });
      } catch {}
    };
    send();
    if (_deviceReportTimer) clearInterval(_deviceReportTimer);
    _deviceReportTimer = setInterval(send, 3 * 60 * 60 * 1000);
  }

  async function bootApp() {
    const ok = await verifyToken();
    if (!ok) { showScreen('login'); return; }
    // Marks "this page has hosted a session" — tryLogin reloads instead of
    // re-booting in place when set, so per-session closure state can't leak
    // across account switches.
    state._sessionBooted = true;
    applyRoleGates();
    renderImpersonationBanner();
    kzInboxInit();
    teamStatusInit();
    const teacherOnly = state.me?.role === 'teacher';
    // Warm the template-variable catalog so the property picker + send-time
    // "auto-resolved" check are ready before the agent opens a template.
    if (!teacherOnly) loadVarCatalog();
    // Kept as a promise: cxDial awaits it so a click in the first seconds
    // after boot knows whether this account has a softphone before it reports
    // anything (swat:B1). _sfpBootDone/_sfpBootOk let a later dial skip the
    // await once the server has actually answered.
    state._sfpBootDone = false;
    state._sfpBootOk = false;
    state._sfpBoot = teacherOnly
      ? Promise.resolve().finally(() => { state._sfpBootDone = true; })
      : initSoftphone().finally(() => { state._sfpBootDone = true; });
    connectSocket();
    reportDeviceAndLocation();
    routeOnState();
    // Inbox onboarding tutorial — fetch mode/progress and show the header
    // icon. The video only opens when someone clicks that icon: the mandatory
    // campaign is off (server flag INBOX_TUTORIAL_MANDATORY), so nothing
    // auto-opens on the Inbox any more.
    inboxTutorialInit().catch(() => {});
    const paymentChatLink = _requestedPaymentChatLink();
    // Teachers land directly on their slot grid — the inbox/contacts/etc tabs
    // are CSS-hidden for them, so without this they'd see an empty Inbox flash.
    if (state.me?.role === 'teacher') {
      try { showView('test-schedule'); } catch {}
      // Start the private bonus monitor while the teacher works in the test
      // schedule; visiting the salary page is not required for a milestone.
      if (state.me.teacherId) ensureTeacherEarnings(false);
    } else if (state.me?.role === 'trial_agent') {
      try {
        await loadRecruitmentCandidate();
        showView('academy');
      } catch {
        showView('academy');
      }
    } else if (state.me?.role === 'meta_review') {
      // Meta App Review reviewer: land straight on the FB/IG inbox — the merged
      // Social channel (their list is hard-floored to FB/IG only, both server-
      // side and in renderConversations). Personal Settings is available too.
      state.activeChannel = 'social';
      try { showView('inbox'); } catch {}
    } else if (paymentChatLink) {
      // A deliberate SwufiPay hand-off wins over a stale saved tab. Defer one
      // tick so routeOnState has mounted the inbox DOM before selecting chat.
      setTimeout(() => { _openRequestedPaymentChat(paymentChatLink); }, 0);
    } else if (new URLSearchParams(window.location.search).get('dashboard') === 'support' && state.me?.superAdmin) {
      // S2 Admin links straight to Support Payments, even when Leads7 last
      // saved a different dashboard or Inbox view for this owner.
      showView('dashboards');
      dashSwitchTab('others');
    } else {
      // Restore the agent's last-active view from localStorage. Without this,
      // every F5 / refresh dumped them back on the Inbox even when they were
      // mid-task on Contacts / Tickets / Dashboards. Validated against the
      // tab list so an old / removed view slug can't trap them on a missing
      // panel. Falls back to inbox (current default) if nothing saved or the
      // saved slug isn't reachable for this user's role.
      try {
        // The Test-Day Cockpit slug is absent ON PURPOSE (retired 2026-09-01 —
        // see data-retired in index.html): restoring it would drop whoever was
        // last on the cockpit back onto a page whose tab no longer exists.
        const VALID_VIEWS = new Set([
          'inbox', 'contacts', 'tickets', 'groups', 'influencers', 'bulk', 'team', 'teamchat',
          'hubspot', 'dashboards', 'why-analytics', 'zoom-live', 'threecx-live', 'test-schedule', 'availability', 'tests', 'level-tests', 'lead-status-map', 'calls', 'conversations', 'dedupe-contacts', 'rest', 'verify-payment',
          'academy', 'trial-leads', 'infra', 'security', 'settings',
        ]);
        // The two recruitment slugs are candidate-only. localStorage is shared
        // by every account this browser opens (an admin's "Log as" of a trial
        // agent leaves 'academy' behind), so restoring them for an agent
        // landed حنان نور on an empty "Your guided sales trial" shell
        // (Ayoub, 2026-09-13). Agents boot on the Inbox instead.
        const CANDIDATE_VIEWS = new Set(['academy', 'trial-leads']);
        const saved = window.location.hash === '#settings' ? 'settings' : localStorage.getItem(LAST_VIEW_KEY);
        if (saved && saved !== 'inbox' && VALID_VIEWS.has(saved)
            && !(CANDIDATE_VIEWS.has(saved) && state.me?.role !== 'trial_agent')) {
          // Defer one tick so routeOnState's loadConversations + DOM setup
          // finish first — switching views during their cold load triggers
          // a flicker we don't want.
          setTimeout(() => { try { showView(saved); } catch {} }, 0);
        }
      } catch { /* localStorage disabled — silently keep default inbox */ }
    }
    if (window.location.hash === '#settings') showView('settings');
    // Background loads — not blocking
    if (!teacherOnly && state.me?.role !== 'trial_agent') {
      loadAssignableUsers();
      loadAllAssignments();
      loadPropTemplates();
      // "Mes notes" pending-mention badge — boot + 3-min refresh.
      _fetchNoteMentions();
      setInterval(_fetchNoteMentions, 180000);
    }
    // Step 4 — wire the call-history top bar (button + initial fetch +
    // 30s time-ago refresh). Socket subscription is set up separately
    // in connectSocket() so events arrive even when the bar is collapsed.
    if (!teacherOnly && state.me?.role !== 'trial_agent') {
      try { callHistory.init(); } catch (e) { /* never block boot on the bar */ }
    }
    // Pre-load team roster so the inbox "Team" filter can inject synthetic
    // rows for platform users without WhatsApp on first use. Also pull the
    // unread digest so the Team-chat tab badge is correct from the start
    // (before the agent visits the Team chat view for the first time).
    if (typeof loadTeamchatUsers === 'function') loadTeamchatUsers().then(() => renderConversations());
    if (typeof loadTeamchatDigest === 'function') loadTeamchatDigest();
  }

  // ============ Chat assignment ============
  // Per-team-member avatar color map — built once from the assignable
  // users roster so each teammate gets a distinct av-cN class instead of
  // colliding through the generic hash. Lookups by username or by user
  // id; non-team strings (random WA chat names, etc.) still fall through
  // to the hash so they get a stable-ish color too.
  state.userColorByUsername = new Map();
  state.userColorById = new Map();

  function rebuildUserColorMap() {
    state.userColorByUsername.clear();
    state.userColorById.clear();
    // Stable ordering = alphabetical by username so every browser session
    // sees the same color for the same teammate, regardless of API order.
    const sorted = (state.assignableUsers || []).slice().sort((a, b) =>
      (a.username || '').localeCompare(b.username || '')
    );
    sorted.forEach((u, idx) => {
      const cls = 'av-c' + ((idx % 8) + 1);
      if (u.username) state.userColorByUsername.set(String(u.username).toLowerCase(), cls);
      if (u.id) state.userColorById.set(u.id, cls);
    });
  }

  function colorClassForName(seed) {
    const s = String(seed || '');
    // Prefer the team-member map so each teammate has a unique color
    // across the whole UI (filter dropdown, conv-list assignee dot,
    // notes author avatar, etc.).
    const lower = s.toLowerCase();
    if (state.userColorByUsername.has(lower)) return state.userColorByUsername.get(lower);
    if (state.userColorById.has(s)) return state.userColorById.get(s);
    // Fallback: legacy hash mod 8 for non-team strings (random WA pushNames
    // etc.). Stable per input, may collide between strangers — fine.
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i) | 0;
    return 'av-c' + ((Math.abs(h) % 8) + 1);
  }

  async function loadAssignableUsers() {
    try {
      const r = await api('/api/users/assignable');
      const j = await r.json();
      // Show every team member (including the current agent) since
      // self-assign is a valid action. Earlier code had `|| true`
      // which made the filter a no-op; we just keep it explicit.
      state.assignableUsers = (j.users || []);
      rebuildUserColorMap();
      populateFilterAssignedList();
      // The roster lands AFTER boot, but the app restores the last view on the
      // same tick — so a user whose last view was Tests reaches
      // _testsPopulateAgentFilter() while this list is still empty and gets an
      // agent filter with no agents in it. Repopulate now that we have names;
      // the call is idempotent and no-ops when the select isn't in the DOM.
      _testsPopulateAgentFilter();
    } catch { /* non-fatal */ }
  }

  // Populate the "Assigned to" section in the inbox filter dropdown — one
  // row per assignable team member, with their colored avatar initial AND
  // a count badge showing how many open chats are currently assigned to
  // them. Counts come from state.chats so they refresh whenever the inbox
  // re-renders (i.e. on every load + every chat_assignment socket event).
  function populateFilterAssignedList() {
    const list = document.getElementById('filterAssignedList');
    const divider = document.getElementById('filterAssignedDivider');
    const title = document.getElementById('filterAssignedTitle');
    if (!list) return;
    clear(list);
    const users = (state.assignableUsers || []).slice().sort((a, b) =>
      (userLabel(a.username, a.displayName || a.username) || '').localeCompare(userLabel(b.username, b.displayName || b.username) || '')
    );
    const show = users.length > 0;
    if (divider) divider.style.display = show ? '' : 'none';
    if (title) title.style.display = show ? '' : 'none';
    if (!show) return;
    // Build per-user assignment count from state.chats (source of truth).
    const counts = new Map();
    for (const c of (state.chats || [])) {
      const uid = c.assignedTo?.userId;
      if (uid) counts.set(uid, (counts.get(uid) || 0) + 1);
    }
    for (const u of users) {
      const initial = (userLabel(u.username, u.displayName || u.username) || '?').charAt(0).toUpperCase();
      const colorClass = colorClassForName(u.username || u.id || '');
      const isMe = u.id === state.me?.id;
      const displayName = userLabel(u.username, u.displayName || u.username);
      const count = counts.get(u.id) || 0;
      const row = el('button', {
        class: 'dropdown-item' + (state.inboxFilters.assignees.has(u.id) ? ' checked' : ''),
        'data-filter': 'assigned:' + u.id,
        'data-display-label': 'Assigned to ' + displayName,
        style: 'display:flex;align-items:center;gap:8px;text-align:left;'
      });
      row.appendChild(el('span', { class: 'filter-check' }));
      row.appendChild(el('span', {
        class: 'assignee-avatar ' + colorClass,
        style: 'width:18px;height:18px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:#fff;flex-shrink:0;',
        text: initial
      }));
      row.appendChild(el('span', { style: 'flex:1;', text: displayName }));
      if (isMe) row.appendChild(el('span', { class: 'muted small', style: 'font-size:10px;', text: '(me)' }));
      // Count pill — gray when 0, indigo accent when > 0 so the agent can
      // spot teammates with active workload at a glance.
      row.appendChild(el('span', {
        class: 'assigned-count-pill' + (count > 0 ? ' has-chats' : ''),
        style: 'font-size:11px;font-weight:600;padding:1px 8px;border-radius:999px;background:'
          + (count > 0 ? 'rgba(99,102,241,0.15)' : 'var(--gray-100)')
          + ';color:' + (count > 0 ? '#4338ca' : 'var(--gray-500)')
          + ';font-variant-numeric:tabular-nums;',
        text: String(count)
      }));
      list.appendChild(row);
    }
    // Rows were just rebuilt — re-apply the checkbox state everywhere.
    syncFilterMenuChecks();
  }

  async function loadAllAssignments() {
    try {
      const r = await api('/api/inbox/assignments');
      const j = await r.json();
      state.assignments = j.assignments || {};
      updateAssignBtnState();
    } catch { /* non-fatal */ }
  }

  function _sameOwnershipChat(a, b) {
    const left = String(a || '');
    const right = String(b || '');
    if (left === right) return true;
    // Social ids are opaque and exact-only. Phone equivalence is allowed only
    // for genuine WA JIDs and uses the served-country-aware phone helper, so
    // 0033… and canonical 33… are the same conversation without cross-channel
    // numeric collisions.
    if (!left.endsWith('@s.whatsapp.net') || !right.endsWith('@s.whatsapp.net')) return false;
    const ld = _phoneForWhatsapp(left.split('@')[0]);
    const rd = _phoneForWhatsapp(right.split('@')[0]);
    return !!ld && ld === rd;
  }

  function _cacheOwnershipAssignment(chatId, assignment) {
    if (!chatId) return;
    for (const key of Object.keys(state.assignments || {})) {
      if (_sameOwnershipChat(key, chatId)) delete state.assignments[key];
    }
    if (assignment) state.assignments[chatId] = assignment;
  }

  function currentAssignment() {
    if (!state.selectedChatId) return null;
    for (const [chatId, assignment] of Object.entries(state.assignments || {})) {
      if (_sameOwnershipChat(chatId, state.selectedChatId)) return assignment || null;
    }
    return null;
  }

  function updateAssignBtnState() {
    const a = currentAssignment();
    if (!E.assignBtn) return;
    if (a) {
      E.assignBtn.classList.add('is-assigned');
      E.assignBtn.setAttribute('data-tip', 'Assigned to ' + (userLabel(a.username, a.displayName || a.username)));
    } else {
      E.assignBtn.classList.remove('is-assigned');
      E.assignBtn.setAttribute('data-tip', 'Assign to');
    }
  }

  function renderAssignDropdown() {
    const list = E.assignDropdownList;
    clear(list);
    const current = currentAssignment();

    // Unassigned row
    const unassigned = el('div', { class: 'assign-dropdown-row unassigned' + (!current ? ' active' : '') }, [
      el('div', { class: 'ad-avatar', text: '—' }),
      el('div', { class: 'ad-name', text: 'Unassigned' })
    ]);
    if (!current) unassigned.appendChild(lucideIcon('check', { class: 'ad-check' }));
    unassigned.addEventListener('click', () => assignChatTo(null));
    list.appendChild(unassigned);

    // Kanza sits with the team, because that is how Ayoub manages her: the
    // agent hands the conversation over the same way they would to a person.
    // She takes a call, not ownership of the thread, so she is her own row
    // above the humans rather than one of them.
    {
      // Gated the way every other admin control in this app is gated —
      // data-admin-only + the CSS role rule — rather than a JS role check
      // that can disagree with it. The route enforces requireSalesAdmin.
      const phone = typeof cxDialableDigits === 'function' ? cxDialableDigits(state.selectedChat) : '';
      const ok = /^(212|0)?[5-7]\d{8}$/.test(String(phone || '').replace(/\D/g, ''));
      const row = el('div', { class: 'assign-dropdown-row ad-kanza' + (ok ? '' : ' is-off'), 'data-admin-only': 'true' }, [
        el('div', { class: 'ad-avatar ad-avatar-kanza', text: 'K' }),
        el('div', { class: 'ad-name' }, [
          el('span', { text: 'Kenza' }),
          el('small', { text: ok ? 'Hand to Kenza — she starts in two minutes' : 'No Moroccan number' }),
        ]),
      ]);
      if (ok) row.addEventListener('click', () => {
        closeAssignDropdown();
        kanzaRequestCall({ phone, name: state.selectedChat?.name }, null, { delaySeconds: 120, skipConfirm: true });
      });
      list.appendChild(row);
      list.appendChild(el('div', { class: 'assign-dropdown-sep', 'data-admin-only': 'true' }));
    }

    // Each user
    for (const u of state.assignableUsers) {
      const isCurrent = current && current.userId === u.id;
      const row = el('div', { class: 'assign-dropdown-row' + (isCurrent ? ' active' : '') }, [
        el('div', { class: 'ad-avatar ' + colorClassForName(u.username), text: (userLabel(u.username, u.displayName || u.username) || '?').charAt(0) }),
        el('div', { class: 'ad-name', text: userLabel(u.username, u.displayName || u.username) })
      ]);
      if (isCurrent) row.appendChild(lucideIcon('check', { class: 'ad-check' }));
      row.addEventListener('click', () => assignChatTo(u.id));
      list.appendChild(row);
    }
    refreshIcons();
  }

  function openAssignDropdown() {
    if (!state.selectedChatId) { toast('Open a chat first', 'error'); return; }
    renderAssignDropdown();
    E.assignDropdown.classList.remove('hidden');
    state.assignDropdownOpen = true;
  }
  function closeAssignDropdown() {
    E.assignDropdown.classList.add('hidden');
    state.assignDropdownOpen = false;
  }
  function toggleAssignDropdown() {
    if (state.assignDropdownOpen) closeAssignDropdown();
    else openAssignDropdown();
  }

  // ============ Conversation toolbar overflow ("…") ============
  // The chat header carries ~19 icon actions. Opening the contact-profile
  // panel takes 340px out of the pane, which is more than the strip can give
  // back: the trailing icons used to spill past the pane and vanish under the
  // panel while the name/phone box collapsed. Breakpoint rules cannot fix that
  // — they key on the viewport, and the panel is what actually removes the
  // room. So measure the real strip instead and MOVE what overflows into the
  // "…" menu. The buttons are moved, never cloned, so ids, event listeners,
  // badges and the per-chat `.hidden` toggles all keep working unchanged.
  //
  // Demotion order = least-used first. The tail (search, Mr.Bot, WhatsApp call)
  // only ever moves on a pane too narrow to hold anything else — at 1024px with
  // the profile open the conversation is barely 320px wide. Assign and Contact
  // profile are pinned: Assign carries its own dropdown, and the profile button
  // is what closes the panel that caused the squeeze.
  const CHAT_ACTION_OVERFLOW_ORDER = [
    'deleteContactBtn', 'blockContactBtn', 'recruitmentLaneBtn', 'testRecordingBtn', 'objectionsBtn',
    'addContactBtn', 'teamChatBtn', 'documentsBtn', 'emailComposeBtn',
    'mediaTemplatesBtn', 'notesBtn', 'availabilityRequestBtn', 'websiteTestBtn',
    'bookTestBtn', 'templatesBtn', 'kanzaCallBtn', 'cxCallBtn',
    'chatSearchBtn', 'botToggleBtn', 'waCallBtn',
  ];
  let _chatActionHomes = null;      // id → the DOM slot the button came from
  let _reflowingChatActions = false;

  function _rememberChatActionHomes() {
    if (_chatActionHomes) return;
    _chatActionHomes = new Map();
    for (const id of CHAT_ACTION_OVERFLOW_ORDER) {
      const btn = document.getElementById(id);
      if (btn && btn.parentElement) {
        _chatActionHomes.set(id, { parent: btn.parentElement, next: btn.nextSibling });
      }
    }
  }
  function _restoreChatAction(id) {
    const btn = document.getElementById(id);
    const home = _chatActionHomes?.get(id);
    if (!btn || !home || btn.parentElement === home.parent) return;
    const before = home.next && home.next.parentElement === home.parent ? home.next : null;
    home.parent.insertBefore(btn, before);
  }
  // Geometric overflow test: the strip is the last flex item in the header, so
  // its right edge is fixed — any child whose right edge passes it is spilling.
  function _chatActionsOverflow(strip) {
    const box = strip.getBoundingClientRect();
    for (const child of strip.children) {
      if (!child.offsetParent && !child.offsetWidth) continue;
      if (child.getBoundingClientRect().right > box.right + 0.5) return true;
    }
    return false;
  }
  function closeChatMoreMenu() {
    document.getElementById('chatMoreMenu')?.classList.add('hidden');
    document.getElementById('chatMoreBtn')?.setAttribute('aria-expanded', 'false');
  }
  function toggleChatMoreMenu() {
    const menu = document.getElementById('chatMoreMenu');
    if (!menu) return;
    const open = menu.classList.toggle('hidden') === false;
    document.getElementById('chatMoreBtn')?.setAttribute('aria-expanded', String(open));
  }
  function reflowChatActions() {
    if (_reflowingChatActions) return;
    const strip = document.querySelector('.chat-header .chat-actions');
    const wrap = document.getElementById('chatMoreWrap');
    const list = document.getElementById('chatMoreList');
    if (!strip || !wrap || !list) return;
    if (!strip.offsetParent && !strip.offsetWidth) return; // no conversation on screen
    _reflowingChatActions = true;
    try {
      _rememberChatActionHomes();
      // Always start from the full strip so a widening pane promotes buttons back.
      for (const id of CHAT_ACTION_OVERFLOW_ORDER) _restoreChatAction(id);
      wrap.classList.remove('hidden'); // reserve the "…" slot while measuring
      let demoted = 0;
      for (const id of CHAT_ACTION_OVERFLOW_ORDER) {
        if (!_chatActionsOverflow(strip)) break;
        const btn = document.getElementById(id);
        if (!btn || btn.parentElement === list) continue;
        if (!btn.offsetParent && !btn.offsetWidth) continue; // hidden for this chat/role
        list.appendChild(btn);
        demoted++;
      }
      if (!demoted) { closeChatMoreMenu(); wrap.classList.add('hidden'); }
    } finally {
      _reflowingChatActions = false;
    }
  }
  // Width changes (profile panel, conv-details panel, sidebar collapse, window
  // resize) all land as a header resize, so one observer covers every case.
  // The panel slide is a 200ms grid transition that fires the observer on every
  // frame, so the work is coalesced into one pass per frame.
  let _chatActionsReflowQueued = false;
  function queueChatActionsReflow() {
    if (_chatActionsReflowQueued) return;
    _chatActionsReflowQueued = true;
    requestAnimationFrame(() => { _chatActionsReflowQueued = false; reflowChatActions(); });
  }
  function watchChatActionsWidth() {
    const header = document.querySelector('.chat-header');
    if (!header || typeof ResizeObserver !== 'function') return;
    new ResizeObserver(queueChatActionsReflow).observe(header);
  }

  // ============ Labels dropdown (REMOVED — Sprint 3) ============
  // WhatsApp Business label sync was Baileys-only. Removed alongside
  // /api/labels and /api/inbox/chats/:id/labels server routes.

  // `chatIdOverride` lets a surface outside the conversation pane (the
  // unassigned-conversations tab) assign a row it is not currently viewing,
  // through this one path — so there is exactly one assign implementation in
  // the client, and the server-side ownership repair + audit trail always run.
  // Resolves to the server's { chatId, assignment } on success, null on any
  // failure (already toasted) — so a caller such as the ringing-banner picker
  // can react without re-implementing the request.
  async function assignChatTo(userId, chatIdOverride = null) {
    const requestedChatId = chatIdOverride || state.selectedChatId;
    if (!requestedChatId) return null;
    if (!chatIdOverride) closeAssignDropdown();
    try {
      const r = await api('/api/inbox/assign', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: requestedChatId, userId })
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast('Assign failed: ' + (j.error || ('HTTP ' + r.status)), 'error');
        return null;
      }
      const j = await r.json();
      if (j.error) { toast(j.error, 'error'); return null; }
      const committedChatId = j.chatId || requestedChatId;
      _cacheOwnershipAssignment(committedChatId, j.assignment || null);
      if (j.assignment) {
        toast('Assigned to ' + userLabel(j.assignment.username, j.assignment.displayName || j.assignment.username), 'success');
      } else {
        toast('Unassigned', 'success');
      }
      updateAssignBtnState();
      // Refresh the conv list ordering / indicator
      renderConversations();
      return j;
    } catch (err) {
      toast('Assign failed: ' + (err.message || err), 'error');
      return null;
    }
  }

  async function userLogout() {
    clientCallRingtones.setUser(null);
    try { await api('/api/auth/logout', { method: 'POST' }); }
    catch { /* ignore */ }
    state.token = '';
    state.me = null;
    // Wipe per-session navigation state so a different user logging in
    // through the same tab doesn't inherit the previous user's back-button
    // history (chat IDs they may not have access to or that don't exist
    // in their filtered view).
    state.chatHistory = [];
    state.selectedChatId = null;
    document.body.classList.remove('has-chat-history');
    localStorage.removeItem(TOKEN_KEY);
    // A logout from inside a "Log as" session drops the parked admin token
    // too — it must not survive for whoever logs in next on this browser.
    try { localStorage.removeItem(PARENT_TOKEN_KEY); } catch { /* ignore */ }
    try { window.Softphone && window.Softphone.destroy(); } catch { /* ignore */ }
    // The next account may not have gateway creds — never inherit this one's.
    state.sfpProvisioned = false;
    state._sfpBoot = null;
    state._sfpBootDone = false;
    state._sfpBootOk = false;
    if (state.socket) state.socket.disconnect();
    document.body.classList.remove('role-admin', 'role-agent', 'role-trial-agent', 'trial-inbox-unlocked', 'role-viewer', 'role-teacher', 'role-teacher-manager', 'role-meta-review', 'role-sales-admin', 'role-super-admin', 'role-system-admin');
    document.getElementById('userPill')?.classList.add('hidden');
    // Hard reload — the SPA's closures and rendered DOM (calls-strip rows,
    // cached admin-scope data) survive an in-place logout and leak into the
    // next account that logs in through this tab (raw-phone bleed 2026-07-17).
    // Boot lands on the login screen (token gone) with the username prefilled
    // from USERNAME_KEY.
    location.reload();
  }

  // ── "Log as" (super admin enters an agent's account) ──────────────────────
  // The admin's own token is parked under PARENT_TOKEN_KEY and the minted
  // agent token takes TOKEN_KEY; both directions end in location.reload() —
  // the same rule as logout: closures and rendered DOM from one account must
  // never bleed into the next (raw-phone bleed, 2026-07-17). localStorage
  // (not sessionStorage) on purpose: every tab of this browser follows the
  // switch, so "Revenir" works from whichever tab the admin is in.
  const PARENT_TOKEN_KEY = 'lougha_logas_parent';
  function _tearDownSessionUi() {
    clientCallRingtones.setUser(null);
    state.chatHistory = [];
    state.selectedChatId = null;
    document.body.classList.remove('has-chat-history');
    try { window.Softphone && window.Softphone.destroy(); } catch { /* ignore */ }
    state.sfpProvisioned = false;
    state._sfpBoot = null;
    state._sfpBootDone = false;
    state._sfpBootOk = false;
    if (state.socket) state.socket.disconnect();
  }
  async function startImpersonation(user) {
    if (!user || !user.id) return;
    if (state.impersonatedBy) { toast('Revenez d\'abord à votre compte', 'error'); return; }
    let payload;
    try {
      const r = await api('/api/admin/impersonate/' + encodeURIComponent(user.id), { method: 'POST' });
      payload = await r.json().catch(() => ({}));
      if (!r.ok || !payload.token) { toast(payload.error || 'Log as impossible', 'error'); return; }
    } catch (err) { toast('Log as impossible: ' + (err.message || err), 'error'); return; }
    try { localStorage.setItem(PARENT_TOKEN_KEY, state.token); } catch { /* storage full/blocked → Revenir will log out */ }
    localStorage.setItem(TOKEN_KEY, payload.token);
    // Land where the AGENT lands (their role's default view), not on the
    // Super Admin page this click came from — that page would restore for
    // them with the wrong badge and empty lists.
    try { localStorage.removeItem(LAST_VIEW_KEY); } catch { /* ignore */ }
    _tearDownSessionUi();
    location.reload();
  }
  async function stopImpersonation() {
    // Invalidate the minted session server-side first; if the network fails
    // the swap below still gets the admin out (the session expires in 2h).
    try { await api('/api/admin/impersonate/stop', { method: 'POST' }); } catch { /* ignore */ }
    let parent = '';
    try { parent = localStorage.getItem(PARENT_TOKEN_KEY) || ''; localStorage.removeItem(PARENT_TOKEN_KEY); } catch { /* ignore */ }
    if (parent) localStorage.setItem(TOKEN_KEY, parent);
    else localStorage.removeItem(TOKEN_KEY); // parent token gone (cleared storage) → login screen
    // Back to the page the admin left from.
    try { localStorage.setItem(LAST_VIEW_KEY, 'team'); } catch { /* ignore */ }
    _tearDownSessionUi();
    location.reload();
  }
  function renderImpersonationBanner() {
    const banner = document.getElementById('impersonationBanner');
    if (!banner) return;
    const imp = state.impersonatedBy;
    document.body.classList.toggle('is-impersonating', !!imp);
    banner.classList.toggle('hidden', !imp);
    if (!imp) return;
    const me = state.me || {};
    const target = document.getElementById('impersonationTarget');
    if (target) target.textContent = (userLabel(me.username, me.displayName || me.username) || '?') + ' (@' + (me.username || '') + ')';
    const admin = document.getElementById('impersonationAdmin');
    if (admin) admin.textContent = userLabel(imp.username, imp.displayName || imp.username || 'mon compte');
    const back = document.getElementById('impersonationBackBtn');
    if (back && !back._wired) { back._wired = true; back.addEventListener('click', () => stopImpersonation()); }
    refreshIcons(banner);
  }

  function routeOnState() {
    const teacherOnly = state.me?.role === 'teacher';
    if (state.me?.role === 'trial_agent') {
      showScreen('app');
      updateConnUI(state.clientState === 'READY');
      if (state.clientState === 'READY') loadConversations();
      return;
    }
    if (state.clientState === 'READY') {
      showScreen('app');
      updateConnUI(true);
      if (!teacherOnly) loadConversations();
    } else if (state.driver === 'cloud-api') {
      // 2026-06-12 (Meta outage incident): NEVER block the team on a WhatsApp
      // failure. Everyone boots straight into the app — Pipeline, Contacts,
      // Test Schedule, Dashboards all work without WA. The conn pill shows
      // "Not connected"; admins additionally get the dismissible #waDownBanner
      // with the Retry affordance (rendered by syncWaDownBanner via
      // updateConnUI). The old full-screen cloudApiErrorScreen gate is no
      // longer routed to.
      showScreen('app');
      updateConnUI(false);
      if (!teacherOnly) loadConversations();
    } else {
      showScreen('qr');
      fetchQrFallback();
    }
  }

  // ── WhatsApp-down banner (admin-only, non-blocking) ──────────────────────
  // Strict system-admin check — mirrors the `role-system-admin` body-class
  // gate in applyRoleGates(). super_sales is deliberately excluded: WA
  // connectivity is a system concern, not a sales-surface one.
  function _isSystemAdmin() {
    const role = String(state.me?.role || '').toLowerCase();
    return role === 'admin' || role === 'super_admin';
  }

  // Show/hide the #waDownBanner strip from current state. Called from
  // updateConnUI (the single choke point for connection-state changes), so
  // every path — boot routing, socket status events, failed sends, retries —
  // keeps the banner honest. Dismissal (state.waDownDismissed) is per
  // page-load by design: the banner reappears on the next full reload if the
  // API is still down, and a recovery clears the flag so a NEW outage
  // re-shows it.
  function syncWaDownBanner() {
    if (!E.waDownBanner) return;
    const down = state.driver === 'cloud-api' && state.clientState !== 'READY';
    const show = down && _isSystemAdmin() && !state.waDownDismissed;
    E.waDownBanner.classList.toggle('hidden', !show);
    if (show && E.waDownDetail) {
      E.waDownDetail.textContent = state.initError
        ? String(state.initError)
        : 'Connection to Meta failed — send/receive disabled.';
    }
  }

  // ── New-build watchdog ──────────────────────────────────────────────────
  // Every bundle URL carries a content hash and index.html is no-store, so a
  // RELOAD always lands on the current build. The hole is the tab nobody
  // reloads: it runs the bundle it booted with for as long as it lives. On
  // 2026-08-05 an agent failed every Book-class send for two days on a bug
  // fixed 45 minutes after her last success — teammates who had reloaded were
  // booking fine, and nothing in the UI could have told her. We never reload
  // for the user: a forced reload can drop a half-typed reply.
  const APP_VERSION_POLL_MS = 5 * 60 * 1000;
  const APP_VERSION_SNOOZE_MS = 30 * 60 * 1000;
  let _bootAssetVersion = null;
  let _appUpdateQuietUntil = 0;
  // What this page actually booted with — read off our own <script> and
  // <link>, so it is the truth about what is running, not what the server has
  // rendered since. CSS counts: a stale stylesheet is a real bug (PR #110 was
  // a badge clipped out of its own cell), and must match the server's format.
  // documents-v2.js counts for the same reason: injectCacheBust versions all
  // THREE assets, so anything it can bust must be able to move this string.
  // Leaving it out cost a day on 2026-09-04 — the Fiche d'inscription fix
  // (13.2 MB → 0.37 MB) was a documents-v2.js-only deploy, so app.js and
  // app.css never moved, no tab was ever told, and every agent kept
  // generating 13 MB PDFs that timed out on upload until someone reloaded
  // by hand. Keep this in lockstep with GET /api/app-version: an asymmetric
  // format pins the banner open forever.
  function _ownAssetVersion() {
    const hash = (node, attr) => {
      const m = node && /[?&]v=([a-z0-9]+)/.exec(node.getAttribute(attr) || '');
      return (m && m[1]) || '';
    };
    const js = hash(document.querySelector('script[src*="/app.js?v="], script[src*="/app.min.js?v="]'), 'src');
    const css = hash(document.querySelector('link[href*="/app.css?v="], link[href*="/app.min.css?v="]'), 'href');
    const docs = hash(document.querySelector('script[src*="/documents-v2.js?v="]'), 'src');
    const earningsJs = hash(document.querySelector('script[src*="/teacher-earnings.js?v="]'), 'src');
    const earningsCss = hash(document.querySelector('link[href*="/teacher-earnings.css?v="]'), 'href');
    const campaignsCss = hash(document.querySelector('link[href*="/campaigns.css?v="]'), 'href');
    const studentStatesJs = hash(document.querySelector('script[src*="/student-states.js?v="]'), 'src');
    const metaCostsJs = hash(document.querySelector('script[src*="/meta-costs.js?v="]'), 'src');
    const automationCostsJs = hash(document.querySelector('script[src*="/automation-costs.js?v="]'), 'src');
    const classesAppJs = hash(document.querySelector('script[src*="/contact-classes-app.js?v="]'), 'src');
    return js ? js + '.' + css + '.' + docs + '.' + earningsJs + '.' + earningsCss + '.' + campaignsCss + '.' + studentStatesJs + '.' + metaCostsJs + '.' + automationCostsJs + '.' + classesAppJs : '';
  }
  async function _checkAppVersion() {
    if (!_bootAssetVersion || Date.now() < _appUpdateQuietUntil) return;
    if (E.appUpdateBanner && !E.appUpdateBanner.classList.contains('hidden')) return;
    try {
      // Plain fetch, not api(): the endpoint is public, so this keeps working
      // on the login screen and after a token expires.
      const r = await fetch('/api/app-version', { cache: 'no-store' });
      if (!r.ok) return;
      const j = await r.json();
      if (!j || !j.version || j.version === _bootAssetVersion) return;
      E.appUpdateBanner?.classList.remove('hidden');
      refreshIcons();
    } catch { /* offline, or a deploy mid-flight — the next tick asks again */ }
  }
  function startAppVersionWatch() {
    _bootAssetVersion = _ownAssetVersion();
    // A dev checkout serves /app.js with no ?v= — nothing to compare, stay quiet.
    if (!_bootAssetVersion) return;
    setInterval(_checkAppVersion, APP_VERSION_POLL_MS);
    // Coming back to the tab is the moment a stale one is about to be used.
    document.addEventListener('visibilitychange', () => { if (!document.hidden) _checkAppVersion(); });
  }

  // Retry the WhatsApp connection. Admins hit POST /api/whatsapp/retry-init,
  // which re-runs the adapter's init sanity check server-side (init() is
  // one-shot at boot, so during a Meta outage the server stays WA-dead until
  // re-inited — a passive /api/status re-read could never recover it).
  // Non-admins (Connect pill path) fall back to the passive status read.
  async function retryWaConnection(btn) {
    const label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Checking…'; }
    try {
      let st, initError;
      if (_isSystemAdmin()) {
        try {
          const r = await api('/api/whatsapp/retry-init', { method: 'POST' });
          if (r.ok) {
            const d = await r.json();
            st = d.state; initError = d.initError;
          }
        } catch { /* fall through to passive read */ }
      }
      if (st === undefined) {
        const sr = await api('/api/status');
        const sd = await sr.json();
        st = sd.state; initError = sd.initError;
        if (sd.driver) state.driver = sd.driver;
      }
      state.clientState = st || 'DISCONNECTED';
      state.initError = initError || null;
      if (state.clientState === 'READY') {
        updateConnUI(true);
        loadConversations();
        toast('WhatsApp Cloud API reconnected', 'success');
        return true;
      }
      updateConnUI(false);
      toast('WhatsApp API still down' + (state.initError ? ' — ' + String(state.initError).slice(0, 140) : '') + '. Meta may still be recovering.', 'warning');
      return false;
    } catch (e) {
      toast('Retry failed: ' + (e?.message || e), 'error');
      return false;
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = label || 'Retry connection'; }
    }
  }

  // ============ Test rescue ring (2026-09-28) ============
  // A client whose placement test started 2–40 min ago and who is not in the
  // teacher's room rings here like an incoming call (server: lib/meet/rescue.js,
  // socket 'test-rescue:alerts' + GET /api/test-rescue/alerts). The card says
  // WHY — waiting, teacher absent, mic refused, Zoom failed, never opened the
  // link — and "Open chat" jumps to the conversation. Each alert rings once (max
  // 60 s); a card stays until it is opened or dismissed. Acks are per browser.
  const testRescue = (function () {
    const ACK_KEY = 'leads7.testRescue.ack';
    const RING_MAX_MS = 60 * 1000;
    const REASONS = {
      waiting_teacher_live:  { tone: 'info',    icon: 'hourglass',       text: "Kay-tsenna f l'page — mazal ma-dkhel l Zoom" },
      teacher_absent:        { tone: 'warning', icon: 'user-x',          text: "Kay-tsenna — l'prof ma-kayna-ch f room dyalha" },
      never_opened:          { tone: 'warning', icon: 'link-2-off',      text: "Ma-7ell-ch l'lien — 3eyyet 3lih (y9der y-kon réseau)" },
      opened:                { tone: 'warning', icon: 'log-out',         text: "7ell l'lien walakin ma-dkhel-ch l Zoom" },
      home:                  { tone: 'warning', icon: 'mouse-pointer-click', text: "7ell l'page walakin ma-cliqua-ch 3la Rejoindre" },
      connecting:            { tone: 'warning', icon: 'loader',          text: "T7ebs f l'connexion l Zoom" },
      waiting:               { tone: 'warning', icon: 'log-out',         text: "Kan kay-tsenna o kherj mn l'page" },
      left:                  { tone: 'warning', icon: 'log-out',         text: 'Kherj mn Zoom' },
      in_meeting:            { tone: 'warning', icon: 'log-out',         text: 'Dkhel l Zoom o kherj' },
      no_booking:            { tone: 'danger',  icon: 'calendar-x',      text: "L'page ma-l9at-lih 7tta test" },
      closed:                { tone: 'danger',  icon: 'clock',           text: "L'page 9alet lih mseddin" },
      invalid:               { tone: 'danger',  icon: 'link-2-off',      text: "L'page ma-t7ellat-ch (lien / réseau)" },
      reported_link_problem: { tone: 'danger',  icon: 'message-square-warning', text: "Kteb bli l'lien / Zoom ma-khdam-ch" },
      in_app_browser:        { tone: 'danger',  icon: 'smartphone',      text: "7ell l'lien dakhel WhatsApp — khasso Safari / Chrome" },
      old_browser:           { tone: 'danger',  icon: 'smartphone',      text: "Browser 9dim — l'page ma-khdmat-ch 3ndo" },
      mic_denied:            { tone: 'danger',  icon: 'mic-off',         text: "Rfed l'micro f l'browser" },
      mic_missing:           { tone: 'danger',  icon: 'mic-off',         text: 'Ma-l9ina 7tta micro f telephone' },
      mic_busy:              { tone: 'danger',  icon: 'mic-off',         text: "L'micro m-chghoul b app okhra" },
      mic_insecure:          { tone: 'danger',  icon: 'mic-off',         text: "L'browser ma-3ta-ch l'micro" },
      mic_unsupported:       { tone: 'danger',  icon: 'mic-off',         text: "L'browser ma-kay-dir-ch l'audio dyal Zoom" },
      zoom_failed:           { tone: 'danger',  icon: 'video-off',       text: "Zoom ma-bgha-ch y-t7ell f l'page" },
      zoom_stuck:            { tone: 'danger',  icon: 'video-off',       text: "Wa9ef 3la \"Joining Meeting…\" — sifet lih link App wla y-installi Zoom" },
      room_not_ready:        { tone: 'danger',  icon: 'video-off',       text: "Room dyal l'prof ma-wajda-ch" },
      sound_check:           { tone: 'warning', icon: 'headphones',      text: "Kay-jerreb s-sout f l'page — mazal ma-kmmel-ch" },
      sound_ok:              { tone: 'warning', icon: 'headphones',      text: "Jerreb s-sout ✓ walakin ma-dkhel-ch l Zoom" },
      sound_skipped:         { tone: 'warning', icon: 'headphones',      text: "Dkhel bla ma y-kmmel test s-sout" },
      sound_mic_silent:      { tone: 'danger',  icon: 'mic-off',         text: "Test s-sout: l'micro ma-kay-wsel-ch sout" },
      sound_speaker_no:      { tone: 'danger',  icon: 'volume-x',        text: "Test s-sout: ma-kay-sme3-ch f telephone dyalo" },
    };
    const RUNG_KEY = 'leads7.testRescue.rung';
    let alerts = [];
    let receivedAt = 0;
    // Reply ring (same cards): a client waiting 30 min without an answer. These
    // keep ringing until opened, dismissed or answered (server drops them).
    let replies = [];
    let repliesAt = 0;
    // Speed to lead (plan item 7, 2026-10-17; server lib/leads/speed-to-lead.js,
    // socket 'speed-to-lead:alerts' + GET /api/speed-to-lead/alerts): a new lead
    // nobody called — the owner at +5 min, the super_sales at +15, the night's
    // leads to the first seat back. Rings once; the server drops it once called.
    let leads = [];
    let leadsAt = 0;
    let ringCtx = null, ringTimer = null, ringStop = null, ringMode = null, tick = null, boundSocket = null;
    let laneSeen = new Set(), laneWheelBound = false;

    function eligible() {
      const role = String(state.me?.role || '').toLowerCase();
      return !!state.me && role !== 'teacher' && role !== 'meta_review';
    }
    // Per USER (a shared office PC) and kept across the auto-reload a deploy
    // triggers — an alert already opened / already rung stays quiet. 12 h TTL.
    function readMap(base) {
      try {
        const m = JSON.parse(localStorage.getItem(base + ':' + (state.me?.id || '')) || '{}') || {};
        const cutoff = Date.now() - 12 * 3600 * 1000;
        for (const k of Object.keys(m)) if (!(m[k] > cutoff)) delete m[k];
        return m;
      } catch { return {}; }
    }
    function writeMap(base, m) {
      try { localStorage.setItem(base + ':' + (state.me?.id || ''), JSON.stringify(m)); } catch {}
    }
    function pending() {
      const acked = readMap(ACK_KEY);
      return [...alerts, ...replies, ...leads].filter(a => a && a.id && !acked[a.id]);
    }
    function ack(id) {
      const m = readMap(ACK_KEY);
      m[id] = Date.now();
      writeMap(ACK_KEY, m);
      render();
    }
    // A short two-note chime, repeated — distinct from the WhatsApp call's
    // dual-tone so the agent knows it is a TEST client before looking.
    // persistent = a waiting client: chime until nothing is left to answer.
    function startRing(persistent) {
      stopRing();
      ringMode = persistent ? 'persistent' : 'once';
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        ringCtx = new Ctx();
        if (ringCtx.state === 'suspended') { try { ringCtx.resume(); } catch {} }
        const chime = () => {
          if (!ringCtx || ringCtx.state === 'closed') return;
          const t0 = ringCtx.currentTime;
          [[660, 0], [880, 0.22]].forEach(([freq, at]) => {
            const osc = ringCtx.createOscillator();
            const gain = ringCtx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0, t0 + at);
            gain.gain.linearRampToValueAtTime(0.09, t0 + at + 0.03);
            gain.gain.linearRampToValueAtTime(0, t0 + at + 0.2);
            osc.connect(gain).connect(ringCtx.destination);
            osc.start(t0 + at);
            osc.stop(t0 + at + 0.22);
          });
        };
        chime();
        ringTimer = setInterval(chime, persistent ? 4000 : 2500);
        if (!persistent) ringStop = setTimeout(stopRing, RING_MAX_MS);
      } catch (e) {
        console.warn('[testRescue] ring failed:', e);
      }
    }
    function stopRing() {
      ringMode = null;
      if (ringTimer) { clearInterval(ringTimer); ringTimer = null; }
      if (ringStop) { clearTimeout(ringStop); ringStop = null; }
      if (ringCtx) { try { ringCtx.close(); } catch {} ringCtx = null; }
    }
    // "+7 min" once the test time has passed; "f 5 min" when a client reported
    // the link failing before the test started.
    function lateLabel(a) {
      const m = (Number(a.mins_late) || 0) + Math.floor((Date.now() - receivedAt) / 60000);
      return m >= 0 ? '+' + m + ' min' : 'f ' + (-m) + ' min';
    }
    function syncRing(freshTest) {
      const waitingReply = eligible() && pending().some(a => a.type === 'reply');
      if (waitingReply) { if (ringMode !== 'persistent') startRing(true); return; }
      if (ringMode === 'persistent') stopRing();
      if (freshTest) startRing(false);
    }
    function waitLabel(a) {
      const m = Math.max(0, (Number(a.wait_min) || 0) + Math.floor((Date.now() - repliesAt) / 60000));
      return m < 60 ? m + ' min' : Math.floor(m / 60) + 'h' + String(m % 60).padStart(2, '0');
    }
    function leadWaitLabel(a) {
      const m = Math.max(0, (Number(a.wait_min) || 0) + Math.floor((Date.now() - leadsAt) / 60000));
      return m < 60 ? m + ' min' : Math.floor(m / 60) + 'h' + String(m % 60).padStart(2, '0');
    }
    // What a speed-to-lead alert says: night or new, the level, and whose lead
    // it is when the viewer is not its owner.
    function leadText(a) {
      const head = a.night ? 'Lead dyal l-lil' : 'Lead jdid';
      const why = a.level === 'escalate' ? 'Fat l-wa9t — mazal 7tta wa7ed ma-3ayyet 3lih' : '3eyyet 3lih daba';
      const mine = String(a.owner_username || '').toLowerCase() === String(state.me?.username || '').toLowerCase();
      return { head, why: why + (a.owner && !mine ? ' · dyal ' + a.owner : '') };
    }
    function leadCard(a) {
      const t = leadText(a);
      const open = el('button', { class: 'trs-open', type: 'button', disabled: a.chatId ? null : 'disabled' },
        [lucideIcon('phone-outgoing'), el('span', { text: 'Open chat' })]);
      open.addEventListener('click', () => { ack(a.id); openChatId(a.chatId, a.lead_name); });
      const close = el('button', { class: 'trs-close', type: 'button', title: 'Masquer', 'aria-label': 'Masquer' }, [lucideIcon('x')]);
      close.addEventListener('click', () => ack(a.id));
      return el('div', { class: 'trs-card', 'data-tone': a.level === 'escalate' ? 'danger' : 'warning', 'data-kind': 'lead', role: 'alertdialog', 'aria-label': t.head + ' — ' + (a.lead_name || 'Client') }, [
        el('div', { class: 'trs-ring', 'aria-hidden': 'true' }, [lucideIcon('bell-ring')]),
        el('div', { class: 'trs-body' }, [
          el('div', { class: 'trs-eyebrow' }, [
            el('span', { text: t.head }),
            el('span', { class: 'trs-late', text: leadWaitLabel(a) }),
          ]),
          el('div', { class: 'trs-name', text: a.lead_name || 'Client' }),
          el('div', { class: 'trs-reason' }, [lucideIcon('phone-outgoing'), el('span', { text: t.why })]),
        ]),
        close,
        el('div', { class: 'trs-actions' }, [open]),
      ]);
    }
    // WhatsApp jids reuse the phone opener; SMS / social ids open as they are.
    function openChatId(chatId, name) {
      const id = String(chatId || '');
      if (!id) return;
      if (id.endsWith('@s.whatsapp.net')) { openChatForPhoneNumber(id.split('@')[0]); return; }
      showView('inbox');
      if (!state.chats.find(c => c.id === id)) {
        state.chats.unshift({ id, name: name || id, isGroup: false, lastMessage: null, unread: 0 });
      }
      setTimeout(() => selectConversation(id), 80);
    }
    function replyCard(a) {
      const late = (Number(a.wait_min) || 0) + Math.floor((Date.now() - repliesAt) / 60000);
      const open = el('button', { class: 'trs-open', type: 'button', disabled: a.chatId ? null : 'disabled' },
        [lucideIcon('message-circle'), el('span', { text: 'Open chat' })]);
      open.addEventListener('click', () => { ack(a.id); openChatId(a.chatId, a.lead_name); });
      const close = el('button', { class: 'trs-close', type: 'button', title: 'Masquer', 'aria-label': 'Masquer' }, [lucideIcon('x')]);
      close.addEventListener('click', () => ack(a.id));
      const reason = 'Kteb o 7tta wa7ed ma-jawbo' + (a.bot_stopped ? " — l'bot 7bes" : '');
      return el('div', { class: 'trs-card', 'data-tone': late >= 60 ? 'danger' : 'warning', 'data-kind': 'reply', role: 'alertdialog', 'aria-label': 'Client bla jawab — ' + (a.lead_name || 'Client') }, [
        el('div', { class: 'trs-ring', 'aria-hidden': 'true' }, [lucideIcon('bell-ring')]),
        el('div', { class: 'trs-body' }, [
          el('div', { class: 'trs-eyebrow' }, [
            el('span', { text: a.channel || 'Message' }),
            el('span', { class: 'trs-late', text: 'Kay-tsenna ' + waitLabel(a) }),
          ]),
          el('div', { class: 'trs-name', text: a.lead_name || 'Client' }),
          el('div', { class: 'trs-reason' }, [lucideIcon('message-square-warning'), el('span', { text: reason })]),
        ]),
        close,
        el('div', { class: 'trs-actions' }, [open]),
      ]);
    }
    function card(a) {
      if (a.type === 'reply') return replyCard(a);
      if (a.type === 'lead') return leadCard(a);
      const r = REASONS[a.reason] || { tone: a.kind === 'problem' ? 'danger' : 'warning', icon: 'alert-triangle', text: 'Mazal ma-dkhel l test' };
      const open = el('button', { class: 'trs-open', type: 'button', disabled: a.chatId ? null : 'disabled' },
        [lucideIcon('message-circle'), el('span', { text: 'Open chat' })]);
      open.addEventListener('click', () => {
        ack(a.id);
        if (a.chatId) openChatForPhoneNumber(String(a.chatId).split('@')[0]);
      });
      const close = el('button', { class: 'trs-close', type: 'button', title: 'Masquer', 'aria-label': 'Masquer' }, [lucideIcon('x')]);
      close.addEventListener('click', () => ack(a.id));
      const meta = [a.teacher_name, a.browser, a.backup_sent_at ? 'Lien de secours t-sifet' : ''].filter(Boolean).join(' · ');
      return el('div', { class: 'trs-card', 'data-tone': r.tone, role: 'alertdialog', 'aria-label': 'Test en retard — ' + (a.lead_name || 'Client') }, [
        el('div', { class: 'trs-ring', 'aria-hidden': 'true' }, [lucideIcon('bell-ring')]),
        el('div', { class: 'trs-body' }, [
          el('div', { class: 'trs-eyebrow' }, [
            el('span', { text: 'Test ' + (a.slot_time || '') }),
            el('span', { class: 'trs-late', text: lateLabel(a) }),
            a.status === 'no_show' ? el('span', { class: 'trs-tag', text: 'No show' }) : null,
          ]),
          el('div', { class: 'trs-name', text: a.lead_name || 'Client' }),
          el('div', { class: 'trs-reason' }, [lucideIcon(r.icon), el('span', { text: r.text })]),
          meta ? el('div', { class: 'trs-meta', text: meta }) : null,
        ]),
        close,
        el('div', { class: 'trs-actions' }, [open]),
      ]);
    }
    // ── Calls-bar chips (2026-09-29) ──
    // The alerts sit as chips in the Calls bar head, next to "CALLS n", painted
    // in the lead's pipeline colour like the incoming-call banner — they never
    // float over the chat header, the call buttons or the composer (Ayoub: "ma-
    // y-blokki-ch calls, ma-y-blokki-ch conversation"). The ring is unchanged.
    // A role that hides the Calls bar keeps the floating cards below.
    function stageLabel(s) {
      try { return (s && PIPELINE_LABELS[s]) || ''; } catch { return ''; }
    }
    function chipStage(a) {
      const s = a.pipeline_stage || ((state.chats || []).find(c => c && c.id === a.chatId) || {}).pipelineStage || '';
      return stageLabel(s) ? s : '';
    }
    function laneTarget() {
      const lane = document.getElementById('chbAlerts');
      const bar = document.getElementById('callHistoryBar');
      if (!lane || !bar || getComputedStyle(bar).display === 'none') return null;
      return lane;
    }
    function chip(a, fresh) {
      const isReply = a.type === 'reply';
      const isLead = a.type === 'lead';
      const owned = isLead && a.owner && String(a.owner_username || '').toLowerCase() !== String(state.me?.username || '').toLowerCase();
      const name = (a.lead_name || 'Client') + (owned ? ' · dyal ' + a.owner : '');
      const stage = chipStage(a);
      let tone, pill, why;
      if (isLead) {
        const t = leadText(a);
        tone = a.level === 'escalate' ? 'danger' : 'warning';
        pill = (a.night ? 'L-lil · ' : '') + leadWaitLabel(a);
        why = t.head + ' · ' + leadWaitLabel(a) + ' — ' + t.why;
      } else if (isReply) {
        const late = (Number(a.wait_min) || 0) + Math.floor((Date.now() - repliesAt) / 60000);
        tone = late >= 60 ? 'danger' : 'warning';
        pill = waitLabel(a);
        why = (a.channel ? a.channel + ' · ' : '') + 'Kay-tsenna ' + waitLabel(a)
          + ' — Kteb o 7tta wa7ed ma-jawbo' + (a.bot_stopped ? " — l'bot 7bes" : '');
      } else {
        const r = REASONS[a.reason] || { tone: a.kind === 'problem' ? 'danger' : 'warning', text: 'Mazal ma-dkhel l test' };
        const meta = [a.teacher_name, a.browser, a.backup_sent_at ? 'Lien de secours t-sifet' : ''].filter(Boolean).join(' · ');
        tone = r.tone;
        pill = 'Test ' + (a.slot_time || '') + ' · ' + lateLabel(a);
        why = 'Test ' + (a.slot_time || '') + ' ' + lateLabel(a) + (a.status === 'no_show' ? ' · No show' : '')
          + ' — ' + r.text + (meta ? ' · ' + meta : '');
      }
      const open = el('button', { class: 'chb-alert-open', type: 'button', disabled: a.chatId ? null : 'disabled', 'aria-label': 'Open chat — ' + name + ' — ' + why }, [
        el('span', { class: 'chb-alert-bell', 'aria-hidden': 'true' }, [lucideIcon('bell-ring')]),
        el('span', { class: 'chb-alert-name', text: name }),
        stage ? el('span', { class: 'chb-alert-stage', text: stageLabel(stage) }) : null,
        el('span', { class: 'chb-alert-wait', text: pill }),
      ]);
      open.addEventListener('click', () => {
        ack(a.id);
        if (isReply || isLead) openChatId(a.chatId, a.lead_name);
        else if (a.chatId) openChatForPhoneNumber(String(a.chatId).split('@')[0]);
      });
      const close = el('button', { class: 'chb-alert-x', type: 'button', title: 'Masquer', 'aria-label': 'Masquer — ' + name }, [lucideIcon('x')]);
      close.addEventListener('click', e => { e.stopPropagation(); ack(a.id); });
      return el('div', {
        class: 'chb-alert' + (fresh ? ' is-new' : ''), role: 'group', title: why,
        'data-stage': stage || null, 'data-tone': tone, 'data-kind': isReply ? 'reply' : (isLead ? 'lead' : 'test'),
      }, [open, close]);
    }
    function renderLane(lane, list) {
      const bar = document.getElementById('callHistoryBar');
      if (!laneWheelBound) {
        laneWheelBound = true;
        // A mouse wheel scrolls the lane sideways (the scrollbar is hidden).
        lane.addEventListener('wheel', e => {
          if (Math.abs(e.deltaY) <= Math.abs(e.deltaX) || lane.scrollWidth <= lane.clientWidth) return;
          lane.scrollLeft += e.deltaY;
          e.preventDefault();
        }, { passive: false });
      }
      const keep = lane.scrollLeft;
      clear(lane);
      if (list.length > 1) lane.appendChild(el('span', { class: 'chb-alerts-count', text: list.length + ' kay-tsennaw' }));
      for (const a of list) lane.appendChild(chip(a, !laneSeen.has(a.id)));
      laneSeen = new Set(list.map(a => a.id));
      lane.classList.remove('hidden');
      if (bar) bar.classList.add('has-alerts');
      refreshIcons(lane);
      lane.scrollLeft = keep;
    }
    function clearLane() {
      const lane = document.getElementById('chbAlerts');
      if (lane) { clear(lane); lane.classList.add('hidden'); }
      document.getElementById('callHistoryBar')?.classList.remove('has-alerts');
      laneSeen = new Set();
    }
    function render() {
      const list = eligible() ? pending() : [];
      let root = document.getElementById('testRescueStack');
      if (!list.length) {
        if (root) root.remove();
        clearLane();
        stopRing();
        if (tick) { clearInterval(tick); tick = null; }
        return;
      }
      if (ringMode === 'persistent' && !list.some(a => a.type === 'reply')) stopRing();
      if (!tick) tick = setInterval(render, 30 * 1000); // keeps "+N min" current
      const lane = laneTarget();
      if (lane) {
        if (root) root.remove();
        renderLane(lane, list);
        return;
      }
      clearLane();
      if (!root) {
        root = el('div', { id: 'testRescueStack', class: 'trs-stack', 'aria-live': 'assertive' });
        document.body.appendChild(root);
      }
      clear(root);
      const shown = list.slice(0, 3);
      for (const a of shown) root.appendChild(card(a));
      if (list.length > shown.length) {
        root.appendChild(el('div', { class: 'trs-more', text: '+' + (list.length - shown.length) + ' autres alertes' }));
      }
      refreshIcons(root);
    }
    function set(list) {
      alerts = Array.isArray(list) ? list : [];
      receivedAt = Date.now();
      const p = eligible() ? pending() : [];
      const rung = readMap(RUNG_KEY);
      const fresh = p.filter(a => !rung[a.id]);
      if (fresh.length) {
        for (const a of fresh) rung[a.id] = Date.now();
        writeMap(RUNG_KEY, rung);
      }
      render();
      syncRing(fresh.length > 0);
    }
    function setReplies(list) {
      replies = (Array.isArray(list) ? list : []).map(a => ({ ...a, type: 'reply' }));
      repliesAt = Date.now();
      render();
      syncRing(false);
    }
    // A new lead rings once (like a late test), then stays until opened,
    // dismissed or called.
    function setLeads(list) {
      leads = (Array.isArray(list) ? list : []).map(a => ({ ...a, type: 'lead' }));
      leadsAt = Date.now();
      const p = eligible() ? pending() : [];
      const rung = readMap(RUNG_KEY);
      const fresh = p.filter(a => a.type === 'lead' && !rung[a.id]);
      if (fresh.length) {
        for (const a of fresh) rung[a.id] = Date.now();
        writeMap(RUNG_KEY, rung);
      }
      render();
      syncRing(fresh.length > 0);
    }
    async function refresh() {
      if (!eligible()) return;
      const load = async (url, apply) => {
        try {
          const r = await api(url, { timeoutMs: 15000 });
          if (!r.ok) return;
          const j = await r.json();
          apply(j && j.alerts);
        } catch { /* the socket push is the primary path */ }
      };
      await Promise.all([load('/api/test-rescue/alerts', set), load('/api/reply-ring/alerts', setReplies), load('/api/speed-to-lead/alerts', setLeads)]);
    }
    function bind(socket) {
      if (!socket || boundSocket === socket) return;
      boundSocket = socket;
      socket.on('test-rescue:alerts', payload => { if (eligible()) set(payload && payload.alerts); });
      socket.on('reply-ring:alerts', payload => { if (eligible()) setReplies(payload && payload.alerts); });
      socket.on('speed-to-lead:alerts', payload => { if (eligible()) setLeads(payload && payload.alerts); });
      socket.on('connect', refresh);
      refresh();
    }
    return { bind, refresh };
  })();

  // ===== Statement freshness alert (2026-10-17) =====
  // Every Monday from 09:00 the server tells the sales admins which statement
  // (the bank accounts, Stripe) nobody imported for over 7 days — live over the
  // socket, and from GET /api/payment-verify/statement-freshness on (re)connect
  // for a tab that was closed at 09:00 (only once this week's check has run).
  // One card, dismissable for the week, gone by itself once SwufiPay has the
  // statement.
  const statementFreshness = (function () {
    const DISMISS_KEY = 'leads7:stmtFreshDismissed';
    let boundSocket = null;
    const dismissedWeek = () => { try { return localStorage.getItem(DISMISS_KEY) || ''; } catch { return ''; } };
    const hide = () => document.getElementById('stmtFreshCard')?.remove();
    function render(report, { live = false } = {}) {
      hide();
      const stale = Array.isArray(report?.stale) ? report.stale : [];
      if (!isSalesAdminMe() || !stale.length || !report.week || dismissedWeek() === report.week) return;
      if (!live && report.alert_week !== report.week) return;
      const close = el('button', { class: 'stmt-fresh-x', type: 'button', 'aria-label': 'Masquer jusqu’à lundi prochain', title: 'Masquer jusqu’à lundi prochain' }, [lucideIcon('x')]);
      close.addEventListener('click', () => {
        try { localStorage.setItem(DISMISS_KEY, report.week); } catch { /* private mode: hide for this tab only */ }
        hide();
      });
      const card = el('div', { id: 'stmtFreshCard', class: 'stmt-fresh-card', role: 'status' }, [
        el('div', { class: 'stmt-fresh-head' }, [lucideIcon('alert-circle'), el('strong', { text: 'Relevés à importer dans SwufiPay' }), close]),
        el('ul', { class: 'stmt-fresh-list' }, stale.map(a => el('li', {
          text: a.label + ' — ' + (a.last_import_at ? 'dernier import il y a ' + a.age_days + ' j' : 'jamais importé'),
        }))),
        el('a', { class: 'btn btn-sm btn-primary', href: '/payments?view=bank', target: '_blank', rel: 'noopener', text: 'Importer un relevé' }),
      ]);
      document.body.appendChild(card);
      refreshIcons(card);
    }
    async function refresh() {
      if (!isSalesAdminMe()) return;
      try {
        const r = await api('/api/payment-verify/statement-freshness', { timeoutMs: 15000 });
        if (r.ok) render(await r.json());
      } catch { /* the Monday socket push is the primary path */ }
    }
    function bind(socket) {
      if (!socket || boundSocket === socket) return;
      boundSocket = socket;
      socket.on('statements:stale', payload => render(payload, { live: true }));
      socket.on('connect', refresh);
      return refresh();
    }
    return { bind, refresh };
  })();

  // ============ Socket ============
  function connectSocket() {
    if (state.socket) state.socket.disconnect();
    // Browser audit 2026-05-23: socket.io-client's default transport order
    // (['polling', 'websocket']) was tripping a Cloudflare WAF rule on the
    // initial polling POST. The body `40{"token":"<uuid>"}` matched CF's
    // sensitive-data inspection often enough that ~1/3 of POSTs returned
    // 502 (origin Bad Gateway from CF). The library then entered a tight
    // reconnect loop — 100+ /socket.io/* requests in 30s with no usable
    // events delivered. By preferring websocket first the auth payload
    // travels inside the WS upgrade frame (not a body CF inspects), and
    // the upgrade succeeds in ~400ms with no follow-up polling POST.
    // Polling is retained as a fallback for networks that block WS.
    state.socket = io({
      auth: { token: state.token },
      transports: ['websocket', 'polling'],
    });

    state.socket.on('qr', data => {
      state.clientState = 'QR';
      showScreen('qr');
      E.qrLoading.classList.add('hidden');
      E.qrImage.src = data.qr;
      E.qrImage.classList.remove('hidden');
      E.qrStatus.textContent = 'Scan with your phone';
    });

    state.socket.on('status', data => {
      state.clientState = data.state;
      if (data.state === 'READY') {
        // WA is healthy again — drop the legacy "skip" flag + the init error
        // (updateConnUI hides the admin WA-down banner).
        try { localStorage.removeItem('leads7.skipWaError'); } catch {}
        state.initError = null;
        showScreen('app');
        updateConnUI(true);
        loadConversations();
      } else if (data.state === 'QR') {
        showScreen('qr');
      } else if (data.state === 'DISCONNECTED') {
        updateConnUI(false);
      }
    });

    state.socket.on('ready', info => {
      state.info = info;
      state.clientState = 'READY';
      state.initError = null;
      try { localStorage.removeItem('leads7.skipWaError'); } catch {}
      updateConnUI(true);
      showScreen('app');
      loadConversations();
    });

    state.socket.on('disconnected', () => {
      state.clientState = 'DISCONNECTED';
      updateConnUI(false);
    });

    state.socket.on('connect_error', () => { /* ignore */ });
    testRescue.bind(state.socket);
    statementFreshness.bind(state.socket);

    // bot_reply bursts (the funnel bot fanning out across many chats) each
    // used to trigger an immediate full conversation-list reload — a major
    // contributor to /api/inbox/chats saturation (perf recon 2026-07-21).
    // Trailing 5s debounce: ONE list reload fires 5s after the LAST bot_reply
    // in a burst. The open-chat message reload stays immediate so the agent
    // watching a conversation still sees the bot's reply in real time.
    let botReplyConvReloadTimer = null;
    state.socket.on('bot_reply', payload => {
      if (!payload?.chatId) return;
      // If the bot replied to the chat we're currently viewing → reload messages
      if (state.selectedChatId === payload.chatId) {
        loadMessages();
      }
      // Refresh conversation list ordering (last message changed) — debounced
      if (botReplyConvReloadTimer) clearTimeout(botReplyConvReloadTimer);
      botReplyConvReloadTimer = setTimeout(() => {
        botReplyConvReloadTimer = null;
        loadConversations();
      }, 5000);
    });

    state.socket.on('bot_feedback', () => {
      // Live-refresh the AI Bot feedback pane when a teammate votes.
      if (state.activeView === 'ai-bot' && typeof AIBOT !== 'undefined' && AIBOT.pane === 'feedback') {
        try { aiBotRenderFeedback(); } catch { /* pane not ready */ }
      }
    });

    function bulkResolveGroupName(p) {
      if (p.groupName && p.groupName.trim()) return p.groupName.trim();
      const lookup = (state.bulkGroups || []).find(g => g.id === p.groupId);
      return lookup?.name || p.groupId || '?';
    }

    state.socket.on('send_progress', p => {
      const label = p.status === 'pause' ? '⏸ ' + (p.message || 'Pausing…')
                  : p.status === 'failed' ? '✗ failed'
                  : '✓ sent';
      showBulkProgress(p.current || 0, p.total || 0, label);
      const name = bulkResolveGroupName(p);
      if (p.status === 'success') appendBulkLog(`✓ ${p.current}/${p.total} → ${name}`, 'row-ok');
      else if (p.status === 'failed') appendBulkLog(`✗ ${p.current}/${p.total} → ${name}: ${p.reason || ''}`, 'row-fail');
      else if (p.status === 'pause') appendBulkLog(p.message || 'pause', '');
    });
    state.socket.on('send_complete', s => {
      bulkResetSendingUi();
      showBulkProgress(s.total || 0, s.total || 0, `✅ Done — ${s.success || 0} sent, ${s.failed || 0} failed`);
      // If there were failures, surface each failed group name explicitly so the user knows WHO failed
      if (s.failed && Array.isArray(s.failedGroups) && s.failedGroups.length) {
        appendBulkLog(`— ${s.failed} failed:`, 'row-fail');
        for (const fg of s.failedGroups) {
          const nm = fg.name && fg.name !== 'Unknown' ? fg.name : ((state.bulkGroups || []).find(g => g.id === fg.id)?.name || fg.id);
          appendBulkLog(`  ✗ ${nm}: ${fg.reason || 'failed'}`, 'row-fail');
        }
      }
      toast(`Sent to ${s.success || 0}/${s.total || 0}`, s.failed ? 'error' : 'success');
    });
    state.socket.on('send_cancelled', s => {
      bulkResetSendingUi();
      showBulkProgress(s.sent || 0, s.total || 0, '⛔ Cancelled');
      toast('Cancelled', '');
    });

    state.socket.on('hs_send_progress', p => {
      const label = p.status === 'success' ? '✓ ' + (p.name || '') : '✗ ' + (p.name || '');
      showHsProgress(p.current || 0, p.total || 0, label);
      if (p.status === 'success') appendHsLog(`✓ ${p.current}/${p.total} → ${p.name || ''} (${p.phone || ''})`, 'row-ok');
      else if (p.status === 'failed') appendHsLog(`✗ ${p.current}/${p.total} → ${p.name || ''}: ${p.reason || ''}`, 'row-fail');
    });
    state.socket.on('hs_send_complete', s => {
      resetHsSendingUi();
      showHsProgress(s.total || 0, s.total || 0, `✅ Terminé — ${s.success || 0} envoyés, ${s.failed || 0} échoués`);
      toast(`Envoyé à ${s.success || 0}/${s.total || 0} membres`, s.failed ? 'error' : 'success');
    });
    state.socket.on('hs_send_cancelled', s => {
      resetHsSendingUi();
      showHsProgress(s.sent || 0, s.total || 0, '⛔ Annulé');
      toast('Envoi annulé', '');
    });

    // Ownership mirror bursts can contain hundreds of per-chat events. Apply
    // each authorization change to local state immediately, but coalesce the
    // expensive authoritative surface reloads. The first event refreshes after
    // a short debounce; a sustained burst gets at most one follow-up per 5s.
    const OWNERSHIP_SURFACE_REFRESH_DEBOUNCE_MS = 200;
    const OWNERSHIP_SURFACE_REFRESH_MIN_GAP_MS = 5000;
    let _ownershipSurfaceRefreshTimer = null;
    let _ownershipSurfaceRefreshRunning = false;
    let _ownershipSurfaceRefreshLastAt = 0;
    let _ownershipInboxRefreshNeeded = false;
    let _ownershipContactsRefreshNeeded = false;

    function _advanceOwnershipResponseBoundary() {
      state.ownershipEpoch = Number(state.ownershipEpoch || 0) + 1;
      // Contacts already has a latest-request token. Advancing it at the socket
      // boundary invalidates a pre-event response immediately, before the
      // coalesced replacement fetch starts 200ms later.
      CONTACTS.searchToken = Number(CONTACTS.searchToken || 0) + 1;
      return state.ownershipEpoch;
    }

    function _scheduleOwnershipSurfaceRefresh({ inbox = false, contacts = false } = {}) {
      if (inbox) _ownershipInboxRefreshNeeded = true;
      if (contacts) _ownershipContactsRefreshNeeded = true;
      if (!_ownershipInboxRefreshNeeded && !_ownershipContactsRefreshNeeded) return;
      if (_ownershipSurfaceRefreshRunning || _ownershipSurfaceRefreshTimer) return;
      const sinceLast = Date.now() - _ownershipSurfaceRefreshLastAt;
      const delay = _ownershipSurfaceRefreshLastAt
        ? Math.max(OWNERSHIP_SURFACE_REFRESH_DEBOUNCE_MS, OWNERSHIP_SURFACE_REFRESH_MIN_GAP_MS - sinceLast)
        : OWNERSHIP_SURFACE_REFRESH_DEBOUNCE_MS;
      _ownershipSurfaceRefreshTimer = setTimeout(async () => {
        _ownershipSurfaceRefreshTimer = null;
        const reloadInbox = _ownershipInboxRefreshNeeded;
        const reloadContacts = _ownershipContactsRefreshNeeded && state.activeView === 'contacts';
        _ownershipInboxRefreshNeeded = false;
        _ownershipContactsRefreshNeeded = false;
        if (!reloadInbox && !reloadContacts) return;
        _ownershipSurfaceRefreshRunning = true;
        _ownershipSurfaceRefreshLastAt = Date.now();
        try {
          await Promise.all([
            reloadInbox ? Promise.resolve(loadConversations({ force: true })).catch(() => {}) : null,
            reloadContacts && typeof refreshContacts === 'function'
              ? Promise.resolve(refreshContacts()).catch(() => {})
              : null,
          ]);
        } finally {
          _ownershipSurfaceRefreshRunning = false;
          // Events that landed during the reload retain their dirty flags and
          // get one trailing refresh after the minimum gap.
          _scheduleOwnershipSurfaceRefresh();
        }
      }, delay);
    }

    function _removeRevokedContactRows(chatId) {
      if (!Array.isArray(CONTACTS.rows)) return false;
      const next = CONTACTS.rows.filter(row => !_sameOwnershipChat(row.chatJid, chatId));
      if (next.length === CONTACTS.rows.length) return false;
      CONTACTS.rows = next;
      // Purge the cached owner-scoped row even while another view is active.
      // Rendering is Contacts-only, but keeping a revoked row in memory lets it
      // flash back when the user later switches views before the refresh lands.
      if (state.activeView !== 'contacts') return true;
      _renderContactsTable(CONTACTS.rows);
      const count = document.getElementById('contactsCount');
      if (count) {
        count.textContent = /^\(\d+\//.test(count.textContent || '')
          ? String(count.textContent).replace(/^\(\d+\//, '(' + CONTACTS.rows.length + '/')
          : '(' + CONTACTS.rows.length + ')';
      }
      document.getElementById('contactsEmpty')?.classList.toggle('hidden', CONTACTS.rows.length !== 0);
      document.getElementById('contactsTable')?.classList.toggle('hidden', CONTACTS.rows.length === 0);
      return true;
    }

    function _applyOwnershipAssignmentSocket(payload) {
      if (!payload?.chatId) return;
      _advanceOwnershipResponseBoundary();
      _cacheOwnershipAssignment(payload.chatId, payload.assignment || null);

      const admin = _isSalesAdmin();
      const assignedToMe = payload.assignment?.userId === state.me?.id;
      const revokedForMe = !admin && !assignedToMe;
      if (revokedForMe) {
        // Authorization changed now, not on the next 8/30-second poll. Remove
        // agent A's row and close an already-open pane immediately so stale
        // messages/call/send controls never remain actionable on screen. The
        // owner-scoped Contacts row is removed synchronously for the same
        // reason; the coalesced server refresh below reconciles totals/new rows.
        state.chats = (state.chats || []).filter(c => !_sameOwnershipChat(c.id, payload.chatId));
        _removeRevokedContactRows(payload.chatId);
        if (_sameOwnershipChat(state.selectedChatId, payload.chatId)) {
          state.selectedChatId = null;
          state.selectedChat = null;
          state.messages = [];
          E.convView?.classList.add('hidden');
          E.emptyState?.classList.remove('hidden');
          E.main()?.classList.remove('show-chat', 'with-profile', 'with-conv-details');
        }
      }
      // Mirror onto state.chats[i].assignedTo so the assigned-to count
      // pills in the filter dropdown stay accurate without waiting for
      // the next inbox reload.
      const chat = (state.chats || []).find(c => _sameOwnershipChat(c.id, payload.chatId));
      if (chat) chat.assignedTo = payload.assignment || null;
      renderConversations();
      if (_sameOwnershipChat(state.selectedChatId, payload.chatId)) updateAssignBtnState();
      // B may not have had this chat in memory; A must lose any variant the
      // server revoked. Reload the authoritative role-scoped Inbox for both,
      // but never once per mirror event.
      // Contacts ownership is scoped by HubSpot owner. Refreshing on the same
      // committed event adds the row for B; A's stale row was already removed
      // synchronously above.
      _scheduleOwnershipSurfaceRefresh({
        inbox: !admin,
        contacts: state.activeView === 'contacts',
      });
    }

    state.socket.on('chat_assignment', payload => {
      _applyOwnershipAssignmentSocket(payload);
    });

    // Per-chat timeline event (assign, label, ticket-create, group-edit…).
    // Append into the local cache + re-render messages so the new chip
    // appears inline without waiting for the next chat-open round-trip.
    state.socket.on('chat_event', payload => {
      if (!payload?.chatId || !payload?.event) return;
      if (!state.chatEventsById[payload.chatId]) state.chatEventsById[payload.chatId] = [];
      state.chatEventsById[payload.chatId].push(payload.event);
      if (state.selectedChatId === payload.chatId) {
        forceMessagesRerender();
        try { renderMessages(); } catch { /* renderer may not be ready */ }
        try { renderTimelineFilterBar(); } catch { /* bar is optional chrome */ }
      }
    });

    // Team-shared attention flag changed elsewhere — sync our local copy
    // so agents see each other's flags update live.
    state.socket.on('chat_attention', payload => {
      if (!payload?.chatId) return;
      const chat = (state.chats || []).find(c => c.id === payload.chatId);
      if (chat) chat.needsAttention = payload.needsAttention ? payload.flag : null;
      renderConversations();
    });

    // Team chat real-time
    state.socket.on('team_message', payload => onTeamchatSocketMessage(payload));
    state.socket.on('team_message:updated', payload => onTeamchatMessageUpdated(payload));
    state.socket.on('team_message:deleted', payload => onTeamchatMessageDeleted(payload));
    state.socket.on('team_read', payload => onTeamchatSocketRead(payload));

    // Inbox (WhatsApp) edit/delete real-time — distinct event names from
    // the existing media-only `message:updated` so listeners stay clean.
    // Email channel — refresh the list + the open conversation when an email
    // bubble lands (sent copy, or a client reply ingested by the 90s poller).
    // Regular polling remains the safety net.
    state.socket.on('email:new-message', payload => {
      if (!payload?.chatId) return;
      loadConversations({ force: true }).catch(() => {});
      if (state.selectedChatId === payload.chatId) loadMessages({ force: true }).catch(() => {});
    });
    state.socket.on('message:edited', payload => updateInboxMessageLocal(payload));
    state.socket.on('message:deleted', payload => removeInboxMessageLocal(payload.messageId));
    state.socket.on('message:reaction-changed', payload => onReactionChanged(payload));
    state.socket.on('message:status', payload => updateInboxMessageStatusLocal(payload));
    // WhatsApp Calling — Meta-side events relayed from server.js onCall.
    // We pass them to the call manager so it can complete the WebRTC
    // handshake (SDP answer) or tear down on terminate.
    state.socket.on('wa_call', payload => waCall.handleServerEvent(payload));
    // Inbound WhatsApp call — Meta delivers the offer via webhook, server
    // relays as wa_call_incoming. We pop the ringer banner; Accept builds
    // the WebRTC answer and POSTs it back.
    state.socket.on('wa_call_incoming', payload => waCall.handleIncoming(payload));
    // Someone else answered → dismiss our ringer banner.
    state.socket.on('wa_call_taken', payload => waCall.handleTaken(payload));
    // Deferred enrichment — fires when the server's cold-mirror HubSpot
    // lookup resolves after the initial banner already painted with a
    // fallback name. Upgrades name + pipeline-stage pip in place.
    state.socket.on('wa_call_incoming_enriched', payload => waCall.handleIncomingEnriched(payload));
    // A lead of mine is calling WhatsApp Support (0621): ring + open-chat card.
    state.socket.on('support_wa_call', payload => supportLineCall.show(payload));
    state.socket.on('support_wa_call_end', payload => supportLineCall.end(payload));
    state.socket.on('teacher_help', payload => teacherHelpCall.show(payload));
    state.socket.on('teacher_help_taken', payload => teacherHelpCall.taken(payload));
    state.socket.on('teacher_help_end', payload => teacherHelpCall.end(payload));
    // Top-bar call history live updates — every _persistCallRow on the
    // server fans this event so the bar reflects ringing → answered →
    // ended (or missed / voicenote_instead) without polling.
    // Conversations Live (Kanza + team) — fan-out from lib/kanza/routes.js.
    state.socket.on('conversation:event', payload => { try { convOnEvent(payload); } catch (e) { console.warn('[conv] event', e); } });
    state.socket.on('conversation:alert', payload => { try { convOnAlert(payload); } catch (e) { console.warn('[conv] alert', e); } });
    state.socket.on('conversation:result', payload => { try { convOnResult(payload); } catch (e) { console.warn('[conv] result', e); } });
    state.socket.on('influencer:handoff', payload => {
      try {
        toast('⭐ Bot Influencer passed a partner to the team' + (payload?.reason ? ' · ' + payload.reason : ''), 'warning');
        if (payload?.chatId && payload.chatId === state.selectedChatId) refreshInfluencerLane();
      } catch (e) { console.warn('[influencer] handoff', e); }
    });
    state.socket.on('kanza:status', payload => { try { convOnStatus(payload); } catch (e) { console.warn('[conv] status', e); } });
    state.socket.on('call_updated', payload => {
      callHistory.upsert(payload);
      // A call row changing for the OPEN chat re-syncs the thread so the
      // event shows in the conversation live (swat:B2). Last-9 compare
      // because 3CX rows use the wa-jid while the open chat may be sms:.
      // loadMessages(force=false) fingerprint-no-ops when nothing changed
      // and keeps the agent's scroll position.
      try {
        const evD9 = String(payload?.chatId || '').split('@')[0].replace(/\D/g, '').slice(-9);
        const openD9 = String(state.selectedChatId || '').split('@')[0].replace(/\D/g, '').slice(-9);
        if (evD9 && evD9.length >= 6 && evD9 === openD9) loadMessages().catch(() => {});
      } catch (_) { /* thread refresh is best-effort */ }
    });
    // Ask the browser for Notification permission once (idempotent — no
    // prompt if already granted or already permanently denied). Used by
    // the inbound-call banner to alert the agent when the tab isn't focused.
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { Notification.requestPermission().catch(() => {}); } catch { /* ignore */ }
    }

    // Contact name override — any agent renaming a chat broadcasts to
    // every open tab so all team members see the same display name.
    state.socket.on('chat:nameUpdated', payload => applyChatNameUpdate(payload.chatId, payload.name));
    // A teammate renamed one person on a number (header / popover / Contacts).
    state.socket.on('line:personRenamed', payload => _personRenameApply(payload || {}));
    state.socket.on('presence', payload => onPresenceUpdate(payload));
    state.socket.on('team_status', row => _tstUpsert(row));
    // AI Bot (Mr.Call) — keep the status bar live.
    state.socket.on('funnel_event', evt => {
      try {
        if (state.activeView === 'ai-bot' && (evt.action === 'ai_error' || evt.action === 'breaker_tripped' || evt.action === 'booked')) aiBotLoadOverview();
        if (state.activeView === 'ai-bot' && evt.action === 'breaker_tripped') toast('⚠️ Bot auto-paused — error spike', 'error');
      } catch (_) { /* */ }
    });
    // Mbrouk! — platform-wide celebration whenever any lead is marked Paid.
    // The document event lets the Objectif dashboard live-refresh without
    // coupling to the celebration module.
    state.socket.on('sale:paid', p => {
      if (!p) return;
      enqueueCelebration(p);
      try { document.dispatchEvent(new CustomEvent('leads7:leadPaid', { detail: p })); } catch (_) { /* */ }
    });
    // Sales availability queue. Managers get a live New badge; the requester
    // gets the Done/Rejected reply immediately and can open the durable card.
    state.socket.on('test-availability-request:updated', p => {
      try {
        if (state.activeView === 'availability-requests') _arLoad();
        else _arRefreshBadge();
        const mine = p?.requestedBy && p.requestedBy === state.me?.username;
        if (mine && p.status === 'done') toast('Availability found — open Availability requests', 'success');
        if (mine && p.status === 'rejected') toast('No availability found — open Availability requests', 'error');
      } catch (_) { /* never break the socket pump */ }
    });
    // Payment verification lifecycle (Virements, 2026-06-12) — fired whenever
    // a claim is created / confirmed / rejected / reopened or its Gemini
    // extraction lands. Refresh the Virements view if it's open, and keep the
    // pipeline-bar pv-chip honest for the affected chat (drop the cached lead
    // so the next render refetches the aggregate; live re-render when the
    // agent is looking at that very conversation).
    state.socket.on('payment-verification:updated', p => {
      try {
        if (state.activeView === 'verify-payment') {
          // (swat:B9) Defers the rebuild while an input inside the board is
          // focused — a socket refresh mid-typing destroyed the "Qui a payé ?"
          // / montant value. Reload lands on blur instead.
          _vpSocketRefresh();
        }
        if (p && p.chatId) {
          delete pipelineLeadCache[p.chatId];
          if (p.chatId === state.selectedChatId) {
            renderPipelineBar(p.chatId).catch(() => { /* non-fatal */ });
          }
        } else if (p && p.leadId && state.selectedChatId) {
          // Paid-hook claims carry no inbox_chat_id — match the open chat's
          // cached lead by id so the pv-chip still refreshes live.
          const cached = pipelineLeadCache[state.selectedChatId];
          if (cached && cached.id === p.leadId) {
            delete pipelineLeadCache[state.selectedChatId];
            renderPipelineBar(state.selectedChatId).catch(() => { /* non-fatal */ });
          }
        }
      } catch (_) { /* never break the socket pump */ }
    });
    // Teacher vacations (2026-06-16). Live-refresh the Vacation view if open;
    // keep the admin pending badge honest; toast the teacher when their own
    // request is decided.
    state.socket.on('vacation:updated', p => {
      try {
        if (state.activeView === 'teacher-vacation') { _tvLoad(); }
        else { _tvRefreshBadge(); }
        if (p && (p.status === 'approved' || p.status === 'rejected') && state.me?.username && p.username === state.me.username) {
          toast('Your vacation request was ' + p.status, p.status === 'approved' ? 'success' : 'error');
        }
      } catch (_) { /* never break the socket pump */ }
    });
    // Company holidays (2026-06-16) — live-refresh the admin holidays panel.
    state.socket.on('holiday:updated', () => {
      try { if (state.activeView === 'teacher-vacation' && _isSystemAdmin()) _tvLoadHolidays(); } catch (_) { /* */ }
    });
    // Reminders (Rappels, 2026-06-13). 'reminder:due' fires to the OWNER when a
    // pending reminder reaches its time — IN-APP ONLY, the server sends nothing
    // outbound. We toast, bump the Rappels nav-tab badge, and live-refresh the
    // pipeline bar if that lead's chat is open. 'reminder:changed' fires on any
    // create/update/delete → reload the Rappels view if open + refresh the bar.
    // Pre-warning fires at 5 / 3 / 1 min before due: Darija chime + voice + amber card.
    state.socket.on('reminder:warning', p => {
      try {
        const rem = p?.reminder || p || {};
        const minsLeft = p?.minsLeft || 5;
        const agent = p?.agentName || state.me?.displayName || state.me?.username || '';
        const client = rem.lead_name || rem.lead_phone || '';
        const chimeType = minsLeft <= 1 ? 'medium-hard' : minsLeft <= 3 ? 'medium' : 'warning';
        // Darija text (Arabic script — ar-MA TTS reads naturally)
        let text;
        if (minsLeft <= 1)      text = agent + '! دقيقة على ريميندر ديال ' + client;
        else if (minsLeft <= 3) text = agent + '، ' + minsLeft + ' دقايق على ريميندر ديال ' + client;
        else                    text = agent + '، تنساي! عندك ريميندر ديال ' + client + ' من بعد ' + minsLeft + ' دقايق';
        _remPlayChime(chimeType);
        _remSpeak(text, p?.speech);
        _remShowCard(rem, 'warning', minsLeft);
      } catch (_) { /* never break the socket pump */ }
    });
    // Due now: strong chime + Darija voice + cobalt card + toast + badge.
    state.socket.on('reminder:due', p => {
      try {
        const rem = p?.reminder || p || {};
        const agent = p?.agentName || state.me?.displayName || state.me?.username || '';
        const client = rem.lead_name || rem.lead_phone || '';
        _remPlayChime('due');
        // voice 'browser' = an automatic follow-up task: the browser's own
        // voice, never the paid server TTS.
        _remSpeak(agent + '! ريميندر ديال ' + client + '، دابا وقتو!', p?.speech, p?.voice);
        _remShowCard(rem, 'due', 0);
        const who = rem.lead_name || rem.lead_phone || 'un lead';
        toast('Rappel : ' + who, 'warning');
        if (state.activeView === 'reminders') _remLoad();
        else _remBumpTabBadge(1);
        const phone = String(rem.lead_phone || '').replace(/\D/g, '');
        const openPhone = pipelineExtractPhone(state.selectedChat || {});
        if (state.selectedChatId && phone && openPhone && phone.slice(-9) === String(openPhone).replace(/\D/g, '').slice(-9)) {
          delete pipelineLeadCache[state.selectedChatId];
          renderPipelineBar(state.selectedChatId).catch(() => {});
        }
      } catch (_) { /* never break the socket pump */ }
    });
    state.socket.on('reminder:changed', p => {
      try {
        if (state.activeView === 'reminders') _remLoad();
        const rem = p?.reminder || p || {};
        const phone = String(rem.lead_phone || '').replace(/\D/g, '');
        const openPhone = pipelineExtractPhone(state.selectedChat || {});
        if (state.selectedChatId && phone && openPhone && phone.slice(-9) === String(openPhone).replace(/\D/g, '').slice(-9)) {
          delete pipelineLeadCache[state.selectedChatId];
          renderPipelineBar(state.selectedChatId).catch(() => {});
        }
      } catch (_) { /* never break the socket pump */ }
    });
    _celeVoicePreload(); // warm the Mbrouk voice MP3s before any celebration fires
    state.socket.on('presence_initial', payload => onPresenceInitial(payload));
    state.socket.on('team_room_created', () => { if (state.activeView === 'teamchat') loadTeamchatRooms().then(renderTeamchatList); });
    state.socket.on('team_room_updated', (room) => {
      try {
        // Patch in place — a remote rename/member change must NOT repaint the
        // whole pane: renderTeamchatActive() rebuilds the composer (destroys a
        // teammate's in-progress draft) and orphans a live voice recording
        // (state.teamchatRecording keeps the mic while its overlay is rebuilt
        // hidden). The acting admin's own save/rename success paths already
        // re-render, so they lose nothing.
        if (room?.id) {
          const idx = (state.teamRooms || []).findIndex(r => r.id === room.id);
          if (idx >= 0) state.teamRooms[idx] = { ...state.teamRooms[idx], ...room };
          if (state.teamchatActiveKey === 'team:room:' + room.id) {
            if (state.me?.id && Array.isArray(room.memberIds) && !room.memberIds.includes(state.me.identityId || state.me.canonicalId || state.me.id)) {
              // I was removed from the open room — close the pane back to the
              // placeholder. A live recording is cancelled first via the same
              // cleanup path beforeunload uses, so the mic never stays hot.
              try { if (state.teamchatRecording) cancelTeamchatRecording(); } catch {}
              state.teamchatActiveKey = null;
              state.teamchatActiveTarget = null;
              state.teamchatMessages = [];
              document.getElementById('teamchatActiveChat')?.classList.add('hidden');
              document.getElementById('teamchatPanePlaceholder')?.classList.remove('hidden');
            } else {
              state.teamchatActiveTarget = { type: 'room', room };
              const pane = document.getElementById('teamchatActiveChat');
              const nameEl = pane?.querySelector('.teamchat-active-name');
              if (nameEl) nameEl.textContent = room.name || 'Room';
              const subEl = pane?.querySelector('.teamchat-active-sub');
              if (subEl && Array.isArray(room.memberIds)) subEl.textContent = (room.memberCount ?? room.memberIds.length) + ' members';
            }
          }
        }
        if (state.activeView === 'teamchat') loadTeamchatRooms().then(renderTeamchatList);
      } catch (_) { /* a payload surprise must never kill the socket listener chain */ }
    });

    // Team voice call signalling (2026-06-01). Local Leads7 calls arrive via
    // the 4 call:* events (server echoes the sender's own emit back, ignored by
    // the from===me guard); a cross-platform call (Leads7 ↔ support2) is relayed
    // by the server over the team bridge and arrives as 'team_call_signal'.
    state.socket.on('call:invite', p => { if (!p || p.from === state.me?.id) return; _tcOnIncomingInvite(p); });
    // answer/ice/end only apply to the in-flight call's peer — guard against a
    // stray signal from a non-current peer corrupting/tearing down the call.
    state.socket.on('call:answer', p => { if (!p || p.from === state.me?.id) return; if (state.teamCall.peerUserId !== p.from) return; _tcOnAnswer(p); });
    state.socket.on('call:ice-candidate', p => { if (!p || p.from === state.me?.id) return; if (state.teamCall.peerUserId !== p.from) return; _tcOnIce(p); });
    state.socket.on('call:end', p => { if (!p || p.from === state.me?.id) return; if (state.teamCall.peerUserId !== p.from) return; _tcOnRemoteEnd(p || {}); });
    state.socket.on('team_call_signal', payload => {
      if (!payload || !payload.signal) return;
      const from = payload.fromCanonId;
      if (!from || from === myCanonId()) return;            // never our own echo
      const sig = payload.signal;
      if (sig.kind === 'invite') { _tcOnIncomingInvite({ from, sdp: sig.sdp }); return; }
      if (state.teamCall.peerUserId !== from) return;        // only the in-flight peer
      if (sig.kind === 'answer') _tcOnAnswer({ sdp: sig.sdp });
      else if (sig.kind === 'ice') _tcOnIce({ candidate: sig.candidate });
      else if (sig.kind === 'end') _tcOnRemoteEnd({ reason: sig.reason });
    });
    // Group (mesh ≤5) call signalling — port of support2's engine (2026-07-15).
    // Same event names/payloads on both platforms; a cross-platform participant
    // gets these relayed through the team bridge (op=group_signal) and they
    // arrive here identically to a local emit.
    state.socket.on('group:invite', p => _gcOnInvite(p || {}));
    state.socket.on('group:roster', p => _gcOnRoster(p || {}));
    state.socket.on('group:peer-joined', p => _gcOnPeerJoined(p || {}));
    state.socket.on('group:signal', p => _gcOnSignal(p || {}));
    state.socket.on('group:peer-left', p => _gcOnPeerLeft(p || {}));
    state.socket.on('group:end', p => _gcOnEnd(p || {}));
    state.socket.on('group:handled', p => _gcDismissHandledElsewhere(p && p.groupCallId));

    // support2 changed something for a client (message, note, event): if it
    // is the conversation on screen, read its feed and notes again.
    state.socket.on('peer:activity', payload => {
      try {
        if (!_peerPingIsOpenChat(payload?.chatId)) return;
        _refreshPeerFeed({ force: true });
        if (payload.kind === 'note') {
          refreshNotesBadge();
          if (E.main().classList.contains('with-conv-details') && state.convDetailsTab === 'notes') loadNotes();
        }
      } catch (e) { console.warn('[peer] activity', e); }
    });

    state.socket.on('note_changed', payload => {
      if (!payload?.chatId || payload.chatId !== state.selectedChatId) return;
      refreshNotesBadge();
      if (E.main().classList.contains('with-conv-details') && state.convDetailsTab === 'notes') {
        loadNotes();
      }
    });

    state.socket.on('custom_prop_changed', payload => {
      if (!payload?.chatId || payload.chatId !== state.selectedChatId) return;
      // Reload custom props + re-render profile if it's open
      if (E.main().classList.contains('with-profile')) {
        loadProfileData();
      } else {
        loadCustomProps(state.selectedChatId);
      }
    });

    // Background HubSpot deep-enrichment hits this when a chat that was in
    // "Unknown numbers" gets matched against a hyphenated/spaced HubSpot
    // phone via the query fallback. Update the chat row in place + redraw.
    state.socket.on('chat_enriched', payload => {
      if (!payload?.chatId) return;
      const chat = state.chats.find(c => c.id === payload.chatId);
      if (!chat) return;
      let changed = false;
      if (payload.hubspotContactId && chat.hubspotContactId !== payload.hubspotContactId) {
        chat.hubspotContactId = payload.hubspotContactId;
        changed = true;
      }
      if (payload.name && chat.name !== payload.name) {
        chat.name = payload.name;
        changed = true;
      }
      if (Array.isArray(payload.hubspotGroups)) {
        chat.hubspotGroups = payload.hubspotGroups;
        changed = true;
      }
      if (changed) renderConversations();
    });
  }

  async function fetchQrFallback() {
    try {
      const r = await api('/api/qr');
      const d = await r.json();
      if (d.qr) {
        E.qrLoading.classList.add('hidden');
        E.qrImage.src = d.qr;
        E.qrImage.classList.remove('hidden');
      }
    } catch { /* ignore */ }
  }

  function updateConnUI(connected) {
    E.connStatus.classList.toggle('conn-connected', connected);
    E.connStatus.classList.toggle('conn-disconnected', !connected);
    if (connected) {
      const phone = (state.info && (state.info.pushname || state.info.wid)) || 'Connected';
      E.connText.textContent = phone;
      E.connActionLabel.textContent = 'Disconnect';
      E.connActionBtn.querySelector('[data-lucide]').setAttribute('data-lucide', 'power');
      // Recovery resets the per-load dismissal so a FUTURE outage re-shows
      // the admin banner instead of staying silently dismissed.
      state.waDownDismissed = false;
    } else {
      E.connText.textContent = 'Not connected';
      E.connActionLabel.textContent = 'Connect';
      E.connActionBtn.querySelector('[data-lucide]').setAttribute('data-lucide', 'settings-2');
    }
    syncWaDownBanner();
    refreshIcons();
  }

  async function handleConnAction() {
    if (state.clientState === 'READY') {
      const msg = state.driver === 'cloud-api'
        ? 'Disconnect WhatsApp session?'
        : 'Disconnect WhatsApp session? You\'ll need to scan a new QR.';
      if (!confirm(msg)) return;
      await api('/api/logout', { method: 'POST' });
      state.clientState = 'DISCONNECTED';
      // Cloud API: stay in the app (never the blocking screen) — the conn
      // pill flips to "Not connected" and admins see the WA-down banner.
      updateConnUI(false);
      if (state.driver !== 'cloud-api') showScreen('qr');
      return;
    }
    // DISCONNECTED → route by driver. Cloud API has no QR: re-show the admin
    // banner (if dismissed) and actively retry the connection right here —
    // no full-screen gate (2026-06-12). Baileys keeps its QR screen.
    if (state.driver === 'cloud-api') {
      state.waDownDismissed = false;
      syncWaDownBanner();
      retryWaConnection(null);
    } else {
      showScreen('qr');
      fetchQrFallback();
    }
  }

  // ============ Conversations ============
  // Cheap fingerprint of a chat list — same trick as messagesFingerprint.
  // Skips re-render when the 15-second poll returns identical data, which
  // otherwise blows away DOM (kills focus on inputs, scroll position, any
  // playing media inside chat rows, etc.) every quarter-minute.
  function chatsFingerprint(list) {
    if (!Array.isArray(list)) return '';
    let fp = '';
    for (const c of list) {
      const last = c.lastMessage || {};
      fp += (c.id || '') + ':' + (c.name || '') + ':' + (c.profilePicUrl || '') + ':'
        + (last.timestamp || 0) + ':' + (c.unread || 0) + ':' + (c.assignedTo?.userId || '') + ':'
        + ((c.labels || []).map(l => l.id).join(',')) + ':' + (c.pipelineStage || '') + ':'
        + (c.isPinned ? 'pinned' : '') + ':' + (c.isArchived ? 'archived' : '') + ':'
        + (c.needsAttention ? (c.needsAttention.flaggedAt || c.needsAttention.flaggedBy || 'flagged') : '') + '|';
    }
    return fp;
  }

  // ─── Notification sound + desktop alerts ──────────────────────────
  // Synthesised via Web Audio so no asset file is needed. Two distinct
  // tones so agents can tell inbox vs team-chat apart by ear.
  //   inbox → bright high bell (880 Hz sine, 350ms decay)
  //   team  → softer chime (523 Hz triangle, 500ms decay)
  // Throttled to one sound per 800ms so a burst of messages doesn't
  // blast 20 chimes.
  let _lastNotifSoundAt = 0;
  function playNotifSound(kind) {
    if (Date.now() - _lastNotifSoundAt < 800) return;
    _lastNotifSoundAt = Date.now();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      if (kind === 'team') {
        osc.frequency.value = 523.25; // C5
        osc.type = 'triangle';
      } else {
        osc.frequency.value = 880;     // A5
        osc.type = 'sine';
      }
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.18, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + (kind === 'team' ? 0.5 : 0.35));
      osc.start(now);
      osc.stop(now + 0.6);
      setTimeout(() => { try { ctx.close(); } catch (_) { /* */ } }, 700);
    } catch (e) { /* AudioContext may not be allowed yet — ignore */ }
  }

  function showInboxNotification(chat, lastMsg) {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    // Don't double-prompt the agent — they're already looking at this chat.
    if (state.selectedChatId === chat.id && !document.hidden) return;
    try {
      const preview = lastMsg?.text
        || (lastMsg?.type === 'audio' ? '🎙️ Voice note' : '')
        || (lastMsg?.type === 'image' ? '📷 Photo' : '')
        || (lastMsg?.type === 'video' ? '🎥 Video' : '')
        || (lastMsg?.type === 'document' ? '📎 Document' : '')
        || (lastMsg?.type ? '[' + lastMsg.type + ']' : 'New message');
      const n = new Notification(chat.name || chat.phone || 'New message', {
        body: String(preview).slice(0, 120),
        icon: '/favicon.ico',
        tag: 'inbox-' + chat.id,    // dedupe rapid bursts
      });
      n.onclick = () => {
        window.focus();
        try { navigateToChat(chat.id); } catch { /* ignore */ }
        n.close();
      };
    } catch (e) { /* */ }
  }

  function showTeamNotification(m) {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    // Skip if agent is actively in this team chat.
    if (state.activeView === 'teamchat' && state.teamchatActiveKey === m.chatKey && !document.hidden) return;
    try {
      const isBot = m.fromUserId === 'team-bot';
      const sender = isBot ? state.teamBotUser : (state.teamUsers || []).find(u => u.id === m.fromUserId);
      const senderName = sender?.displayName || (isBot ? 'Team Bot' : 'Teammate');
      // Carry the sender's section (Sales / Support / Teachers / Admin) in
      // the title so the agent knows the context before clicking.
      const TS = window.LoughaTeamSections;
      const secLabel = isBot ? 'Alertes' : ((sender && TS) ? (TS.SECTIONS[TS.sectionOf(sender)]?.label || 'Équipe') : 'Équipe');
      const preview = m.text || (m.type === 'audio' ? '🎙️ Voice note' : '[Media]');
      const n = new Notification(senderName + ' — ' + secLabel, {
        body: String(preview).slice(0, 120),
        icon: '/favicon.ico',
        tag: 'team-' + m.chatKey,
      });
      // Land on the exact conversation, not just the app window.
      n.onclick = () => { try { window.focus(); openTeamchatByKey(m.chatKey); } catch { /* */ } n.close(); };
    } catch (e) { /* */ }
  }

  // ─── Mbrouk! paid celebration (platform-wide) ──────────────────────
  // Fired by the `sale:paid` socket broadcast whenever ANY lead is marked
  // Paid. Full-viewport but non-blocking: the overlay root ignores pointer
  // events (work continues underneath), only the centered card is clickable
  // (click = dismiss). Fireworks are a self-contained canvas particle system —
  // no library. Sound is a per-agent voice MP3 (/sounds/cele-<agent>.mp3)
  // with a persisted mute; degrades to the WebAudio fanfare when the MP3
  // can't load or play. Payloads queue so two paids play back-to-back.
  const CELE = { queue: [], playing: false, seen: new Set(), lastSoundAt: 0, cutShort: null };
  const CELE_MUTE_KEY = 'leads7.celebrationMuted';

  // French money formatting — '12 500 DH' (same convention as documents-v2).
  function fmtDH(n) {
    const v = Math.round(Number(n) || 0);
    return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' DH';
  }

  function enqueueCelebration(p) {
    if (!p) return;
    // Teachers receive only their own private 500-DH bonus milestones.
    if (state.me?.role === 'teacher') return;
    const key = p.historyId || ((p.leadId || '?') + ':' + (p.amount || 0) + ':' + Math.floor(Date.now() / 60000));
    if (CELE.seen.has(key)) return;            // socket replay / double-fire guard
    CELE.seen.add(key);
    setTimeout(() => CELE.seen.delete(key), 120000);
    CELE.queue.push(p);
    // A payment landing while a show is on screen trims that show to the
    // short duration so back-to-back paids don't stack into minutes.
    if (CELE.playing && CELE.cutShort) { try { CELE.cutShort(); } catch (_) { /* */ } }
    _celePump();
  }
  function _celePump() {
    if (CELE.playing || !CELE.queue.length) return;
    CELE.playing = true;
    _celeShow(CELE.queue.shift(), () => {
      CELE.playing = false;
      setTimeout(_celePump, 350);
    });
  }
  // Debug / Playwright hook — the socket handler is closure-scoped.
  try { window.__celebrate = enqueueCelebration; } catch (_) { /* */ }

  // Starburst glyph — amber rays + lougha coin with a check. Inline SVG
  // (static string, no user data) so it stays crisp and theme-correct.
  const CELE_GLYPH_SVG =
    '<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<g stroke="#f59e0b" stroke-width="2.4" stroke-linecap="round">' +
    '<path d="M24 4v7"/><path d="M24 37v7"/><path d="M4 24h7"/><path d="M37 24h7"/>' +
    '<path d="M9.9 9.9l4.9 4.9"/><path d="M33.2 33.2l4.9 4.9"/><path d="M9.9 38.1l4.9-4.9"/><path d="M33.2 14.8l4.9-4.9"/>' +
    '</g>' +
    '<circle cx="24" cy="24" r="9.5" fill="#10b7e2"/>' +
    '<path d="M20.5 24.2l2.4 2.4 4.8-5" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" fill="none"/>' +
    '</svg>';

  function _celeMuted() {
    try { return localStorage.getItem(CELE_MUTE_KEY) === '1'; } catch (_) { return false; }
  }

  // Per-agent celebration voice — "Mbrouk lghezala <Name>!" MP3s generated
  // offline and served from /sounds/. Unknown agents get the generic file;
  // any load/play failure degrades to the synth fanfare. Imane and Asma
  // (2026-10-22) are the same voice with their name spliced in. A login that
  // is not a file name (an email) falls back to a word of the display name
  // ("Imane", "Mme Asma"), so no login ever has to be written here.
  const CELE_VOICE_AGENTS = ['mary', 'doha', 'bouchra', 'imane', 'asma'];
  function _celeVoiceSlug(username, displayName) {
    const login = String(username || '').trim().toLowerCase();
    if (CELE_VOICE_AGENTS.includes(login)) return login;
    const word = String(displayName || '').toLowerCase().split(/[^a-z]+/).find(w => CELE_VOICE_AGENTS.includes(w));
    return word || login;
  }
  const CELE_VOICE = { cache: {}, ok: {}, current: null, preloaded: false };
  function _celeVoiceUrl(name) { return '/sounds/cele-' + name + '.mp3'; }
  function _celeVoicePreload() {
    if (CELE_VOICE.preloaded) return;
    CELE_VOICE.preloaded = true;
    try {
      for (const name of CELE_VOICE_AGENTS.concat('generic')) {
        const url = _celeVoiceUrl(name);
        const a = new Audio(url);
        a.preload = 'auto';
        a.addEventListener('canplaythrough', () => { CELE_VOICE.ok[url] = true; }, { once: true });
        a.addEventListener('error', () => { CELE_VOICE.ok[url] = false; }, { once: true });
        CELE_VOICE.cache[url] = a;
      }
    } catch (_) { /* */ }
  }
  function _celeVoiceStop() {
    const cur = CELE_VOICE.current;
    if (cur) {
      CELE_VOICE.current = null;
      try { cur.pause(); cur.currentTime = 0; } catch (_) { /* */ }
    }
    _celeMusicStop();
  }

  // Win music — a single celebration track: a theater-applause ovation
  // (cele-music-01.mp3) played UNDER the voice line: music ducks low for the
  // first ~5s, then swells for the rest of the show. Stopped by dismiss/mute/
  // next show. (Was 11 random chaabi tracks; swapped to the applause ovation
  // 2026-06-18 per Ayoub — the old cele-music-02..11 stay but are never picked.)
  const CELE_MUSIC_COUNT = 1;
  const CELE_MUSIC = { current: null, lastIdx: -1, fadeT: null };
  function _celeMusicStop() {
    const m = CELE_MUSIC.current;
    if (!m) return;
    CELE_MUSIC.current = null;
    if (CELE_MUSIC.fadeT) { clearTimeout(CELE_MUSIC.fadeT); CELE_MUSIC.fadeT = null; }
    try { m.pause(); m.currentTime = 0; } catch (_) { /* */ }
  }
  function playCelebrationMusic() {
    try {
      let idx = 1 + Math.floor(Math.random() * CELE_MUSIC_COUNT);
      if (idx === CELE_MUSIC.lastIdx) idx = (idx % CELE_MUSIC_COUNT) + 1; // never repeat back-to-back
      CELE_MUSIC.lastIdx = idx;
      // ?v= cache-buster: /sounds/*.mp3 carry max-age 4h, so swapping a file's
      // bytes (e.g. the 2026-06-18 ovation) wouldn't reach browsers that cached
      // the old track for hours. Bump this token whenever a cele-music file is
      // replaced to force a fresh fetch.
      const m = new Audio('/sounds/cele-music-' + String(idx).padStart(2, '0') + '.mp3?v=ov20260618');
      m.volume = 0.35; // ducked under the voice line
      m.loop = false;  // 5s applause ovation plays ONCE (was looping → applause repeated ~4× across the 20s show)
      CELE_MUSIC.current = m;
      m.play().catch(() => { CELE_MUSIC.current = null; /* autoplay not unlocked — voice/synth already degrade */ });
      CELE_MUSIC.fadeT = setTimeout(() => {
        if (CELE_MUSIC.current === m) { try { m.volume = 0.75; } catch (_) { /* */ } }
      }, 5200); // swell once the voice line is done
    } catch (_) { /* music is decoration — never break the celebration */ }
  }

  function playCelebrationVoice(username, displayName) {
    if (_celeMuted()) return;
    if (Date.now() - CELE.lastSoundAt < 3000) return;
    CELE.lastSoundAt = Date.now();
    _celeVoiceStop();
    playCelebrationMusic();
    try {
      const slug = _celeVoiceSlug(username, displayName);
      const genericUrl = _celeVoiceUrl('generic');
      const agentUrl = _celeVoiceUrl(slug);
      const synth = () => { CELE_VOICE.current = null; _celeSynthFanfare(); };
      const tryPlay = (url, onFail) => {
        const audio = CELE_VOICE.cache[url] || (CELE_VOICE.cache[url] = new Audio(url));
        try { audio.currentTime = 0; } catch (_) { /* */ }
        audio.volume = 1;
        CELE_VOICE.current = audio;
        audio.play().catch(onFail);
      };
      if (CELE_VOICE_AGENTS.includes(slug) && CELE_VOICE.ok[agentUrl] !== false) {
        tryPlay(agentUrl, () => {
          if (CELE_VOICE.ok[genericUrl] !== false) tryPlay(genericUrl, synth); else synth();
        });
      } else if (CELE_VOICE.ok[genericUrl] !== false) {
        tryPlay(genericUrl, synth);
      } else {
        synth();
      }
    } catch (_) { _celeSynthFanfare(); }
  }

  function _celeShow(p, done) {
    const reduced = !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
    // Full 20s show normally; compress to 6s when more payments are queued
    // behind this one so back-to-back paids don't stack into a minute of
    // celebration (the voice ~5s still fits the short show).
    const showMs = CELE.queue.length ? 6000 : 20000;
    const overlay = el('div', { class: 'cele-overlay' + (reduced ? ' is-static' : '') });
    let stopFireworks = null;
    if (!reduced && !document.hidden) {
      const canvas = el('canvas', { class: 'cele-canvas' });
      overlay.appendChild(canvas);
      stopFireworks = celeFireworks(canvas, showMs);
    }

    const amount = Number(p.amount) || 0;
    const total = Number(p.monthly && p.monthly.total) || 0;
    const objective = Number(p.monthly && p.monthly.objective) || 0;
    const newPct = objective > 0 ? Math.min(100, (total / objective) * 100) : 0;
    const oldPct = objective > 0 ? Math.min(100, Math.max(0, ((total - amount) / objective) * 100)) : 0;

    const glyph = el('div', { class: 'cele-glyph' });
    glyph.innerHTML = CELE_GLYPH_SVG;

    const line = el('div', { class: 'cele-line' });
    line.appendChild(document.createTextNode((p.agent && p.agent.displayName ? p.agent.displayName : '—') + ' a encaissé '));
    line.appendChild(el('strong', { text: fmtDH(amount) }));

    const subKids = [el('span', { class: 'cele-lead', text: p.leadName || '' })];
    if (p.pack) subKids.push(el('span', { class: 'cele-pack', text: p.pack }));

    const fill = el('div', { class: 'cele-progress-fill' + (newPct >= 100 ? ' is-done' : '') });
    fill.style.width = oldPct.toFixed(1) + '%';
    const labelText = objective > 0
      ? (total >= objective
        ? Math.round((total / objective) * 100) + ' % — objectif atteint !'
        : Math.round(newPct) + ' % de l\'objectif · reste ' + fmtDH(objective - total))
      : '';
    const muteBtn = el('button', {
      class: 'cele-mute icon-btn',
      type: 'button',
      'aria-label': 'Couper le son des célébrations',
      onclick: (ev) => {
        ev.stopPropagation();
        const next = !_celeMuted();
        try { localStorage.setItem(CELE_MUTE_KEY, next ? '1' : '0'); } catch (_) { /* */ }
        if (next) _celeVoiceStop(); // cut the current voice, not just future ones
        muteBtn.innerHTML = '<i data-lucide="' + (next ? 'volume-x' : 'volume-2') + '"></i>';
        refreshIcons(muteBtn);
      },
    });
    muteBtn.innerHTML = '<i data-lucide="' + (_celeMuted() ? 'volume-x' : 'volume-2') + '"></i>';

    const closeBtn = el('button', {
      class: 'cele-close icon-btn',
      type: 'button',
      'aria-label': 'Fermer la célébration',
      onclick: (ev) => { ev.stopPropagation(); _celeVoiceStop(); dismiss(); },
    });
    closeBtn.innerHTML = '<i data-lucide="x"></i>';

    const card = el('div', { class: 'cele-card' }, [
      muteBtn,
      closeBtn,
      glyph,
      el('div', { class: 'cele-title', text: 'Mbrouk !' }),
      line,
      el('div', { class: 'cele-sub' }, subKids),
      el('div', { class: 'cele-progress' }, [
        el('div', { class: 'cele-progress-track' }, [fill]),
        el('div', { class: 'cele-progress-label', text: labelText }),
      ]),
    ]);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    refreshIcons(overlay);

    let dismissed = false;
    let autoT = null;
    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      CELE.cutShort = null;
      if (autoT) clearTimeout(autoT);
      _celeMusicStop(); // music is tied to the show; the voice may finish on its own
      if (stopFireworks) { try { stopFireworks(); } catch (_) { /* */ } }
      overlay.classList.add('is-leaving');
      setTimeout(() => { try { overlay.remove(); } catch (_) { /* */ } done(); }, 260);
    }
    // Explicit dismiss (card click or ✕) also cuts the voice — the agent
    // asked for it gone; the auto-timeout path lets the voice finish.
    card.addEventListener('click', () => { _celeVoiceStop(); dismiss(); });
    autoT = setTimeout(dismiss, showMs);
    // Re-armed by enqueueCelebration when another paid lands mid-show:
    // trim this show to ~6s total so the next one starts sooner.
    const shownAt = Date.now();
    CELE.cutShort = () => {
      if (dismissed) return;
      const remaining = 6000 - (Date.now() - shownAt);
      if (remaining < showMs - (Date.now() - shownAt)) {
        if (autoT) clearTimeout(autoT);
        autoT = setTimeout(dismiss, Math.max(remaining, 400));
      }
    };

    // Progress sweep old% → new% once the card has landed.
    if (reduced) {
      fill.style.width = newPct.toFixed(1) + '%';
    } else {
      setTimeout(() => { fill.style.width = newPct.toFixed(1) + '%'; }, 600);
    }

    playCelebrationVoice(p.agent && p.agent.username, p.agent && p.agent.displayName);
  }

  // Canvas fireworks — staggered rockets rise from the bottom with a glowing
  // trail and explode into two-tone radial spark bursts. Single rAF loop,
  // DPR capped at 2, live sparks capped at 600 so older laptops keep their
  // frame rate. Auto-cleans at durationMs, when the tab hides, or once the
  // last spark fades. Same contract as the old confetti: returns cleanup().
  function celeFireworks(canvas, durationMs = 20000) {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = window.innerWidth, H = window.innerHeight;
    canvas.width = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const PAIRS = [
      ['#f59e0b', '#fbbf24'], ['#10b7e2', '#67e8f9'], ['#ec4899', '#f9a8d4'],
      ['#6366f1', '#a5b4fc'], ['#16a34a', '#86efac'],
    ];
    const MAX_SPARKS = 600;
    const rockets = [];
    const sparks = [];
    // One rocket every ~1.1-1.8s for the whole show, stopping early enough
    // that the last burst has ~2.5s to fade before the canvas is torn down.
    const launches = [];
    for (let at = 200; at < durationMs - 2500; at += 1100 + Math.random() * 700) {
      launches.push(at);
    }
    let nextLaunch = 0;
    function launch() {
      const x = W * (0.15 + Math.random() * 0.7);
      rockets.push({
        x, y: H + 10, px: x, py: H + 10,
        vx: (Math.random() - 0.5) * 1.2,
        vy: -(H / 55 + Math.random() * 2.5),
        targetY: H * (0.2 + Math.random() * 0.25),
        pair: PAIRS[Math.floor(Math.random() * PAIRS.length)],
      });
    }
    function explode(r, now) {
      const n = Math.min(70 + Math.floor(Math.random() * 31), MAX_SPARKS - sparks.length);
      if (n < 20) return;
      for (let i = 0; i < n; i++) {
        const ang = Math.random() * Math.PI * 2;
        const speed = 2 + Math.random() * 5;
        sparks.push({
          x: r.x, y: r.y,
          vx: Math.cos(ang) * speed,
          vy: Math.sin(ang) * speed,
          life: 1400 + Math.random() * 500,
          born: now,
          color: r.pair[i % 2],
        });
      }
    }
    const t0 = performance.now();
    let raf = null;
    let stopped = false;
    function cleanup() {
      if (stopped) return;
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      try { canvas.remove(); } catch (_) { /* */ }
      rockets.length = 0;
      sparks.length = 0;
    }
    function frame(t) {
      if (stopped) return;
      if (document.hidden || (t - t0) > durationMs) { cleanup(); return; }
      while (nextLaunch < launches.length && (t - t0) >= launches[nextLaunch]) {
        launch();
        nextLaunch++;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      // Rockets: rise, draw the trail segment, explode at apex.
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.px = r.x; r.py = r.y;
        r.x += r.vx; r.y += r.vy;
        r.vy += 0.06; // decelerate on the way up
        ctx.globalAlpha = 0.9;
        ctx.strokeStyle = r.pair[0];
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(r.px, r.py + 14); // stretched segment reads as a trail
        ctx.lineTo(r.x, r.y);
        ctx.stroke();
        if (r.y <= r.targetY || r.vy >= -1) {
          explode(r, t);
          rockets.splice(i, 1);
        }
      }
      // Sparks: gravity + drag, alpha follows remaining life, swap-remove dead.
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        const age = t - s.born;
        if (age > s.life) {
          const last = sparks.pop();
          if (i < sparks.length) sparks[i] = last;
          continue;
        }
        s.vy += 0.07; s.vx *= 0.985; s.vy *= 0.985;
        s.x += s.vx; s.y += s.vy;
        ctx.globalAlpha = Math.max(0, 1 - age / s.life);
        ctx.fillStyle = s.color;
        ctx.fillRect(s.x - 1.25, s.y - 1.25, 2.5, 2.5);
      }
      ctx.globalAlpha = 1;
      if (nextLaunch >= launches.length && !rockets.length && !sparks.length) { cleanup(); return; }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return cleanup;
  }

  // Short rising fanfare (C5–E5–G5–C6 arpeggio + resolving chord, ~1.4s).
  // FALLBACK when the voice MP3 can't load or play. Mute + throttle are
  // checked by playCelebrationVoice — the play() rejection lands here a few
  // ms after lastSoundAt was stamped, so re-checking would always suppress.
  let _celeAudioCtx = null;
  function _celeSynthFanfare() {
    try {
      if (!_celeAudioCtx) _celeAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = _celeAudioCtx;
      if (ctx.state === 'suspended') ctx.resume().catch(() => { /* */ });
      const master = ctx.createGain();
      const comp = ctx.createDynamicsCompressor();
      master.gain.value = 0.5;
      master.connect(comp);
      comp.connect(ctx.destination);
      const note = (freq, t, dur, type, vol) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = type; o.frequency.value = freq;
        o.connect(g); g.connect(master);
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(vol, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + dur);
        o.start(t); o.stop(t + dur + 0.05);
      };
      const now = ctx.currentTime + 0.02;
      const ARP = [[523.25, 0], [659.25, 0.09], [783.99, 0.18], [1046.5, 0.27]];
      for (const fd of ARP) {
        note(fd[0], now + fd[1], 0.35, 'triangle', 0.22);
        note(fd[0] / 2, now + fd[1], 0.35, 'sine', 0.09);  // sub-octave warmth
      }
      for (const f of [523.25, 659.25, 783.99, 1046.5]) note(f, now + 0.42, 0.9, 'sine', 0.12);
    } catch (_) { /* AudioContext may not be allowed yet — ignore */ }
  }

  // Reminder chime — Web Audio synth, reuses _celeAudioCtx.
  // 'warning'     = soft  D5→F#5→A5 (3 tones, ~0.9s)   — 5 min pre-alert
  // 'medium'      = med   D5→F#5→A5→D6 (4 tones, ~1.1s) — 3 min pre-alert
  // 'medium-hard' = firm  same + short chord              — 1 min pre-alert
  // 'due'         = strong full arpeggio + resolving chord — due now
  function _remPlayChime(type) {
    try {
      if (_celeMuted()) return;
      if (!_celeAudioCtx) _celeAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = _celeAudioCtx;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const master = ctx.createGain();
      const comp = ctx.createDynamicsCompressor();
      master.connect(comp); comp.connect(ctx.destination);
      const note = (freq, t, dur, vol) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = 'triangle'; o.frequency.value = freq;
        o.connect(g); g.connect(master);
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(vol, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + dur);
        o.start(t); o.stop(t + dur + 0.05);
      };
      const now = ctx.currentTime + 0.02;
      if (type === 'due') {
        master.gain.value = 0.45;
        [[587.33, 0], [739.99, 0.11], [880, 0.22], [1174.66, 0.33]].forEach(([f, d]) => note(f, now + d, 0.35, 0.24));
        [587.33, 739.99, 880, 1174.66].forEach(f => note(f, now + 0.5, 0.85, 0.12));
      } else if (type === 'medium-hard') {
        master.gain.value = 0.38;
        [[587.33, 0], [739.99, 0.11], [880, 0.22], [1174.66, 0.33]].forEach(([f, d]) => note(f, now + d, 0.32, 0.2));
        [739.99, 880].forEach(f => note(f, now + 0.48, 0.5, 0.1));
      } else if (type === 'medium') {
        master.gain.value = 0.33;
        [[587.33, 0], [739.99, 0.12], [880, 0.24], [1174.66, 0.36]].forEach(([f, d]) => note(f, now + d, 0.3, 0.17));
      } else {
        master.gain.value = 0.28;
        [[587.33, 0], [739.99, 0.11], [880, 0.22]].forEach(([f, d]) => note(f, now + d, 0.28, 0.14));
      }
    } catch (_) { /* AudioContext may not be ready */ }
  }

  // Reminder voice. Prefers high-quality server TTS (ElevenLabs / Haytham) when
  // configured, falls back to browser SpeechSynthesis otherwise.
  //   text       — local Arabic phrase, used for the browser fallback
  //   serverText — server-built phrase (payload.speech); preferred for TTS
  //   voice      — 'browser' skips the server TTS (automatic follow-up tasks)
  async function _remSpeak(text, serverText, voice) {
    if (_celeMuted()) return;
    if (voice === 'browser') { _remSpeakBrowser(text); return; }
    const phrase = serverText || text;
    try {
      const r = await api('/api/reminders/tts?text=' + encodeURIComponent(phrase), { timeoutMs: 15000 });
      if (r && r.ok) {
        const blob = await r.blob();
        if (blob && blob.size > 0) {
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audio.volume = 0.95;
          audio.addEventListener('ended', () => URL.revokeObjectURL(url));
          await audio.play().then(() => true).catch(() => { URL.revokeObjectURL(url); throw new Error('play blocked'); });
          return; // high-quality voice played
        }
      }
      // 503 (no key) / any non-ok → browser fallback
    } catch (_) { /* network / blocked / not configured → fall through */ }
    _remSpeakBrowser(text);
  }

  // Browser SpeechSynthesis fallback — Arabic (ar-MA/ar) when available.
  function _remSpeakBrowser(text) {
    try {
      if (!window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.92; u.pitch = 1.05; u.volume = 0.95;
      const voices = window.speechSynthesis.getVoices();
      const ar = voices.find(v => v.lang === 'ar-MA')
              || voices.find(v => v.lang.startsWith('ar-'))
              || voices.find(v => v.lang === 'ar');
      if (ar) u.voice = ar;
      window.speechSynthesis.speak(u);
    } catch (_) { /* SpeechSynthesis may be blocked */ }
  }

  // Test the reminder ring immediately — callable from the Rappels view.
  // Uses the real server TTS so the agent hears the actual Haytham voice.
  function _remTestSound() {
    const agent = state.me?.displayName || state.me?.username || 'Agent';
    const arName = ({ mary: 'ماري', doha: 'دوحة', bouchra: 'بشرى' })[String(agent).toLowerCase()] || agent;
    _remPlayChime('due');
    setTimeout(() => _remSpeak(
      arName + '! ريميندر ديال العميل، دابا وقتو!',
      arName + '! ريميندر ديال الكليان ديالك، دابا وقتو! سيري شوفي.'
    ), 200);
    _remShowCard(
      { lead_name: 'Test Client', note: 'Test du son rappel', remind_at: new Date().toISOString() },
      'due', 0
    );
  }

  // Persistent reminder notification card — bottom-right, slide-in.
  // type='warning' → amber border (5-min pre-alert).
  // type='due'     → accent/cobalt border + pulse (due now).
  // At most one card visible at a time; auto-dismissed after 30s.
  let _remNotifTimer = null;
  function _remShowCard(rem, type, minsLeft) {
    try {
      const existing = document.getElementById('remNotifCard');
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      if (_remNotifTimer) { clearTimeout(_remNotifTimer); _remNotifTimer = null; }

      const isDue = type === 'due';
      const card = el('div', { id: 'remNotifCard', class: 'rem-notif-card' + (isDue ? ' rem-notif-card--due' : ' rem-notif-card--warning') });

      // Dynamic title based on minsLeft
      let cardTitle;
      if (isDue) cardTitle = 'Rappel !';
      else if (minsLeft != null && minsLeft <= 1) cardTitle = 'Rappel dans 1 min !';
      else if (minsLeft != null && minsLeft <= 3) cardTitle = 'Rappel dans 3 min';
      else cardTitle = 'Rappel dans 5 min';

      // Top row: icon + title + close
      const closeBtn = el('button', { class: 'rem-notif-close', type: 'button', 'aria-label': 'Fermer' }, [lucideIcon('x')]);
      const topRow = el('div', { class: 'rem-notif-top' }, [
        el('div', { class: 'rem-notif-top-left' }, [
          lucideIcon(isDue ? 'bell-ring' : 'bell'),
          el('span', { class: 'rem-notif-title', text: cardTitle }),
        ]),
        closeBtn,
      ]);
      card.appendChild(topRow);

      // Lead name / phone
      const who = rem.lead_name || rem.lead_phone || '';
      if (who) card.appendChild(el('div', { class: 'rem-notif-lead', text: who }));

      // Note (action)
      if (rem.note) card.appendChild(el('div', { class: 'rem-notif-note' }, [lucideIcon('sticky-note'), el('span', { text: rem.note })]));

      // Exact time
      if (rem.remind_at) {
        const d = new Date(rem.remind_at);
        const fmt = MA.loc(d, 'dt', 'fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
        card.appendChild(el('div', { class: 'rem-notif-when' }, [lucideIcon('clock'), el('span', { text: fmt })]));
      }

      // Actions
      const dismiss = () => {
        if (_remNotifTimer) { clearTimeout(_remNotifTimer); _remNotifTimer = null; }
        card.style.animation = 'remCardOut .25s ease forwards';
        setTimeout(() => { if (card.parentNode) card.parentNode.removeChild(card); }, 260);
      };
      const doneBtn = el('button', { class: 'btn btn-sm btn-primary', type: 'button' }, [lucideIcon('check'), el('span', { text: 'Terminer' })]);
      doneBtn.addEventListener('click', async () => {
        doneBtn.disabled = true;
        try {
          if (rem.id) await api('/api/reminders/' + rem.id, { method: 'PATCH', body: JSON.stringify({ status: 'done' }) });
        } catch (_) {}
        dismiss();
        if (state.selectedChatId) { delete pipelineLeadCache[state.selectedChatId]; renderPipelineBar(state.selectedChatId).catch(() => {}); }
        if (state.activeView === 'reminders') _remLoad();
      });
      const openBtn = el('button', { class: 'btn btn-sm btn-ghost', type: 'button' }, [lucideIcon('bell'), el('span', { text: 'Rappels' })]);
      openBtn.addEventListener('click', () => { dismiss(); try { showView('reminders'); } catch (_) {} });
      card.appendChild(el('div', { class: 'rem-notif-actions' }, [doneBtn, openBtn]));

      closeBtn.addEventListener('click', dismiss);
      document.body.appendChild(card);
      refreshIcons(card);
      _remNotifTimer = setTimeout(dismiss, 30000);

      // Browser Notification when tab is hidden
      if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          new Notification(isDue ? 'Rappel !' : 'Rappel dans 5 min', {
            body: (rem.note || who || ''),
            tag: 'reminder-' + (rem.id || ''),
          });
        } catch (_) {}
      }
    } catch (_) { /* never break the socket pump */ }
  }

  // Compare the incoming chat list against the last seen inbound
  // timestamps; fire notification + sound for any chat whose newest
  // message is now inbound AND newer than before.
  function _scanNewInbounds(next) {
    if (!state._seenLastInboundTs) {
      // First call after login — seed without firing so we don't
      // ring once for every existing chat.
      state._seenLastInboundTs = {};
      for (const c of next) {
        const lm = c.lastMessage;
        if (lm && !lm.fromMe) state._seenLastInboundTs[c.id] = lm.timestamp || 0;
      }
      return;
    }
    let fired = false;
    for (const c of next) {
      const lm = c.lastMessage;
      // A support2 message lifting the row (lm.peer) is shown, never chimed.
      if (!lm || lm.fromMe || lm.peer) continue;
      const prev = state._seenLastInboundTs[c.id] || 0;
      const cur = lm.timestamp || 0;
      if (cur > prev) {
        state._seenLastInboundTs[c.id] = cur;
        if (!fired) {
          playNotifSound('inbox');
          fired = true; // single chime per refresh tick — extra new chats just bump unread badges
        }
        showInboxNotification(c, lm);
      }
    }
  }

  // F5 (2026-07-09): in-flight coalescing — boot fires loadConversations 3×
  // concurrently (routeOnState + socket 'status' READY + socket 'ready').
  // At admin scale (2.8k chats) each duplicate response is a multi-MB JSON
  // parse + a cold list render, tripling the boot freeze. Concurrent calls
  // with the SAME query key now share one fetch; a different key (channel
  // type / search changed mid-flight) or force:true still proceeds.
  let _convInflight = null;
  let _convInflightKey = '';

  async function loadConversations({ force = false, ownershipRetry = 0 } = {}) {
    // Kanza is an independent 3CX teammate, not a WhatsApp transport. Her
    // queue must stay usable even when the WhatsApp connection is down.
    if (state.activeChannel === 'kanza') { kzEnterChannel(); return; }
    if (state.clientState !== 'READY') return;
    // Global people-search: a query searches ALL connected data (leads, HubSpot
    // contacts, inbox conversations, 3CX/WA calls) — not just the local inbox
    // list — and renders a grouped panel. Bypasses the channel + scope filters.
    if (state.searchQ && state.searchQ.length >= 2) {
      try {
        const qAtSend = state.searchQ;
        const ownershipEpochAtSend = Number(state.ownershipEpoch || 0);
        const r = await api('/api/search/global?q=' + encodeURIComponent(qAtSend));
        if (r.status === 409 && ownershipRetry < 2) {
          await new Promise(resolve => setTimeout(resolve, 50));
          return loadConversations({ force: true, ownershipRetry: ownershipRetry + 1 });
        }
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const data = await r.json();
        // Ownership events invalidate people-search results too: they combine
        // leads, Contacts, calls and Inbox rows under the same agent scope.
        if (state.searchQ === qAtSend
            && ownershipEpochAtSend === Number(state.ownershipEpoch || 0)) {
          state.globalSearchResults = Array.isArray(data.results) ? data.results : [];
          // Global search owns the shared list container. Reset the regular
          // inbox window now so clearing the query cannot remount a previously
          // expanded (potentially 1,800-row) window in one frame.
          _convResetWindow();
          renderGlobalSearch(state.globalSearchResults);
        }
      } catch { toast('Search failed', 'error'); }
      return;
    }
    // Returning from search → force the normal list to redraw (its fingerprint
    // cache would otherwise short-circuit and leave the grouped panel on screen).
    if (state.globalSearchResults) _convExitGlobalSearch();
    if (state.activeChannel === 'email') {
      renderEmptyChannelState();
      return;
    }
    const params = new URLSearchParams();
    if (state.searchQ) params.set('q', state.searchQ);
    const type = (state.activeChannel === 'groups' || state.activeChannel === 'clubs') ? 'groups' : 'all';
    params.set('type', type);
    const key = params.toString();
    // F5: same-key call already in flight → piggyback on it instead of
    // firing a duplicate fetch + duplicate render.
    if (_convInflight && _convInflightKey === key && !force) return _convInflight;
    const ownershipEpochAtSend = Number(state.ownershipEpoch || 0);
    const job = (async () => {
      try {
        // ETag / 304 (perf audit 2026-07-28 #2): the server already hashes the
        // per-user chat payload and honors If-None-Match (previously only the
        // support2-mirror path used it) — replay the validator so an unchanged
        // poll costs an empty 304 instead of the full ~2,500-row / ~870 KB
        // body that chatsFingerprint was discarding after download.
        // Guards — the validator is only sent when:
        //   - not `force` (forced calls exist to repaint no matter what),
        //   - it was captured under the SAME key (q + type) — a search or
        //     channel switch is a different payload,
        //   - state._chatsFingerprint is set: the render pump and the
        //     search-return path null it precisely to demand a full repaint
        //     on the next poll, which a 304 would silently skip.
        const inmEtag = (!force && state._chatsFingerprint
          && state._chatsEtag && state._chatsEtag.key === key)
          ? state._chatsEtag.etag : null;
        const r = await api('/api/inbox/chats?' + key, inmEtag ? { headers: { 'If-None-Match': inmEtag } } : {});
        if (r.status === 409 && ownershipRetry < 2) {
          await new Promise(resolve => setTimeout(resolve, 50));
          return loadConversations({ force: true, ownershipRetry: ownershipRetry + 1 });
        }
        // Unchanged payload — keep the current list, unread badges, search
        // results and DOM exactly as they are. Must be checked before the
        // r.ok gate (304 is not "ok") and never reaches r.json() (no body).
        if (r.status === 304) return;
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const data = await r.json();
        // A chat_assignment event is an authorization boundary. Never let a
        // response that started before it restore a locally revoked row or
        // assignment; the socket scheduler has already queued a fresh fetch.
        if (ownershipEpochAtSend !== Number(state.ownershipEpoch || 0)) return;
        // Remember the validator only for a response we are about to accept —
        // an epoch-discarded body must not leave its ETag behind to 304-mask
        // the follow-up fetch the socket scheduler queued.
        const freshEtag = r.headers.get('ETag');
        if (freshEtag) state._chatsEtag = { key, etag: freshEtag };
        const next = Array.isArray(data.chats) ? data.chats : [];
        // Always sync assignment cache (cheap, no DOM touch).
        for (const chat of next) {
          if (chat.assignedTo) state.assignments[chat.id] = chat.assignedTo;
        }
        // When the agent searches by name/phone and the local inbox has nothing
        // matching, look the term up in HubSpot too. Found contacts get rendered
        // as synthetic chat rows below the regular inbox list — the agent can
        // click one to open a brand-new conversation with that student.
        let hubspotResults = [];
        if (state.searchQ && state.searchQ.length >= 2) {
          try {
            const hr = await api('/api/hubspot/contacts/search?q=' + encodeURIComponent(state.searchQ));
            const hj = await hr.json();
            const existingIds = new Set(next.map(c => (c.phone || jidUserSafe(c.id))));
            hubspotResults = (hj.contacts || []).filter(c => c.phone && !existingIds.has(normalizeMoroccanDigits(c.phone)));
          } catch { /* HubSpot lookup is optional */ }
        }
        if (ownershipEpochAtSend !== Number(state.ownershipEpoch || 0)) return;
        const fp = chatsFingerprint(next) + '|q=' + (state.searchQ || '') + '|ch=' + (state.activeChannel || '') + '|hs=' + hubspotResults.map(c => c.id + ':' + c.phone).join(',');
        if (!force && fp === state._chatsFingerprint) return;
        state._chatsFingerprint = fp;
        // Detect new inbound messages BEFORE swapping state.chats so the
        // delta is correctly diffed against the previous baseline.
        try { _scanNewInbounds(next); } catch (e) { /* never block render */ }
        state.chats = next;
        state.hubspotSearchResults = hubspotResults;
        renderConversations();
        // Fire-and-forget, admin-only, self-throttled to one call per 45s (or
        // served from the server's 20s memo). Never awaited — the conversation
        // list must not wait on the safety-net count.
        loadOrphans();
      } catch {
        toast('Failed to load conversations', 'error');
      }
    })();
    _convInflight = job;
    _convInflightKey = key;
    job.finally(() => {
      if (_convInflight === job) { _convInflight = null; _convInflightKey = ''; }
    });
    return job;
  }

  // ── Unassigned conversations — admin safety net (swat:B5) ───────────────
  // The inbox visibility gate can leave a conversation visible to NO agent
  // (measured 2026-08-15: 77 of them, 68 with unread, and 51% of all FB/IG
  // threads — social chats have no phone so ownership can never be inferred).
  // The server decides which ones those are, by running the real gate for
  // every agent; this tab is the place someone finally sees them.
  state.orphans = { reasons: new Map(), total: 0, unreadTotal: 0, loadedAt: 0, loading: false, error: '',
    // Channel sub-filter (#orphanChips). 'all' | 'wa' | 'fbm' | 'ig'.
    // `byChannel` is the server's count over the FULL orphan set (computed
    // before the 300-row cap), so the chips agree with the tab badge; when
    // the body IS capped the existing #orphanNotice says so. `visibleIds`
    // is stamped by renderConversations with exactly the rows the admin can
    // see right now — the bulk action must use that, never a count.
    // Set when a bulk run's HTTP call aborted while the server was still
    // writing: until it expires the action stays locked, because the ONE
    // thing we must not do is invite a second run that races the first.
    byChannel: {}, channelFilter: 'all', visibleIds: [], bulkUnknownUntil: 0 };
  const _ORPHAN_REFRESH_MS = 45000;
  const ORPHAN_REASON_LABEL = {
    no_assignment: 'Jamais attribuée',
    assigned_off_roster: 'Chez un compte hors équipe',
    no_phone: 'Sans numéro',
    no_hubspot_owner: 'Pas de propriétaire HubSpot',
    // Degraded mode: the mirror could not answer, so "nobody owns it" is not
    // something we know. Say so instead of asserting it.
    hubspot_owner_unknown: 'Propriétaire HubSpot inconnu — miroir indisponible',
    owned_off_agent_roster: 'Propriétaire HubSpot hors agents',
    no_lead_owner: 'Pas de propriétaire lead',
    no_participation: 'Aucun agent n\'a répondu',
  };
  // A row shows ONE reason — the one that answers "why does nobody see it" —
  // and keeps the full diagnosis in its tooltip (2026-10-17: up to five orange
  // chips per row made the list unreadable).
  const ORPHAN_PRIMARY_ORDER = ['hubspot_owner_unknown', 'assigned_off_roster', 'no_assignment',
    'owned_off_agent_roster', 'no_hubspot_owner', 'no_lead_owner', 'no_participation', 'no_phone'];
  function _orphanPrimaryReason(reasons) {
    const list = Array.isArray(reasons) ? reasons : [];
    return ORPHAN_PRIMARY_ORDER.find(r => list.includes(r)) || list[0] || '';
  }
  function _orphanReasonText(reason, row) {
    if (reason === 'assigned_off_roster') {
      const who = row?.assignedTo?.displayName || row?.assignedTo?.username || '';
      // A switched-off account: nobody reads its inbox — say it on the row.
      if (who) return 'Chez ' + who + (row.assignedTo.inactive ? ' · compte désactivé' : '');
    }
    return ORPHAN_REASON_LABEL[reason] || reason;
  }

  async function loadOrphans({ force = false } = {}) {
    if (!isSalesAdminMe()) return;
    if (state.orphans.loading) return;
    if (!force && (Date.now() - state.orphans.loadedAt) < _ORPHAN_REFRESH_MS) return;
    state.orphans.loading = true;
    try {
      const r = await api('/api/inbox/orphans' + (force ? '?refresh=1' : ''));
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      const reasons = new Map();
      for (const row of (j.chats || [])) reasons.set(row.chatId, row);
      state.orphans.reasons = reasons;
      // `assignable` is narrower than `viewers`: a teacher or Meta reviewer
      // counts as coverage but must never be handed a client conversation.
      state.orphans.assignable = Array.isArray(j.assignable) ? j.assignable : [];
      state.orphans.viewers = Array.isArray(j.viewers) ? j.viewers : [];
      state.orphans.total = Number(j.total || 0);
      state.orphans.returned = Number(j.returned || (j.chats || []).length);
      state.orphans.truncated = j.truncated === true;
      state.orphans.hubspotOwnershipKnown = j.hubspotOwnershipKnown !== false;
      state.orphans.byChannel = (j.byChannel && typeof j.byChannel === 'object') ? j.byChannel : {};
      state.orphans.unreadTotal = Number(j.unreadTotal || 0);
      state.orphans.excludedStudents = Number(j.excluded?.students || 0);
      state.orphans.error = '';
      state.orphans.loadedAt = Date.now();
      _paintOrphanBadge();
      _paintOrphanNotice();
      _paintOrphanChips();
      // Only repaint the list when the admin is actually looking at this tab —
      // the inbox list is the hottest render in the app.
      if (state.activeChannel === 'orphans') renderConversations();
    } catch (err) {
      state.orphans.error = err.message || String(err);
      _paintOrphanNotice();
    } finally {
      state.orphans.loading = false;
    }
  }

  // Degraded-mode / truncation banner above the list. Kept OUT of the row
  // renderer so the incremental conversation-list update is untouched: it is a
  // static node in index.html that we only show/hide and retext.
  function _paintOrphanNotice() {
    const node = document.getElementById('orphanNotice');
    if (!node) return;
    const parts = [];
    if (state.orphans.error) {
      parts.push('Liste indisponible — ' + state.orphans.error);
    } else {
      if (state.orphans.hubspotOwnershipKnown === false) {
        parts.push('Miroir HubSpot indisponible : la propriété des numéros est inconnue, '
          + 'donc cette liste est plus longue que la réalité. Vérifiez avant d\'attribuer.');
      }
      if (state.orphans.truncated) {
        parts.push(state.orphans.returned + ' conversations affichées sur ' + state.orphans.total
          + '. Attribuez celles-ci, la liste se recharge.');
      }
      // Students are kept out by the server: support follows them (2026-10-17).
      if (state.orphans.excludedStudents > 0) {
        parts.push(state.orphans.excludedStudents > 1
          ? state.orphans.excludedStudents + ' conversations d\'élèves actifs ne sont pas listées : le support les suit, elles ne vont pas aux sales.'
          : '1 conversation d\'élève actif n\'est pas listée : le support la suit, elle ne va pas aux sales.');
      }
    }
    node.textContent = parts.join(' · ');
    const show = parts.length > 0 && state.activeChannel === 'orphans';
    node.classList.toggle('hidden', !show);
    node.classList.toggle('is-error', !!state.orphans.error);
  }

  // The whole failure mode is "nobody looks", so the count has to be visible
  // WITHOUT opening the tab: a dot on the Inbox trigger plus the number on the
  // menu item itself.
  function _paintOrphanBadge() {
    const count = state.orphans.total || 0;
    const pill = document.getElementById('orphanTabCount');
    if (pill) {
      pill.textContent = count > 99 ? '99+' : String(count);
      pill.classList.toggle('hidden', count === 0);
    }
    const dot = document.getElementById('orphanTriggerDot');
    if (dot) {
      dot.classList.toggle('hidden', count === 0 || !isSalesAdminMe());
      dot.title = count + ' conversation(s) visibles par aucun agent';
    }
  }

  // Channel sub-filter for "Non attribuées". Same component as the Social
  // rail (identical .social-chip classes) — this invents no new chip style.
  // Counts come from the payload already in hand; clicking only re-renders.
  const ORPHAN_CHANNEL_SVG = {
    wa: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.6 2 2.2 6.4 2.2 11.84c0 1.74.46 3.44 1.32 4.94L2 22l5.35-1.4a9.8 9.8 0 0 0 4.69 1.2c5.43 0 9.84-4.4 9.84-9.84C21.88 6.4 17.47 2 12.04 2m0 17.98a8.2 8.2 0 0 1-4.16-1.13l-.3-.18-3.09.81.82-3.02-.2-.31a8.13 8.13 0 0 1-1.25-4.31c0-4.51 3.68-8.18 8.19-8.18 4.51 0 8.18 3.67 8.18 8.18 0 4.51-3.67 8.14-8.19 8.14m4.49-6.09c-.25-.12-1.46-.72-1.68-.8-.23-.09-.39-.12-.55.12-.16.25-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.71-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.55-1.33-.76-1.82-.2-.48-.4-.41-.55-.42h-.47c-.16 0-.43.06-.65.31-.22.25-.85.83-.85 2.03s.87 2.35.99 2.51c.12.16 1.71 2.61 4.15 3.66.58.25 1.03.4 1.39.51.58.19 1.11.16 1.53.1.47-.07 1.46-.6 1.66-1.17.21-.58.21-1.07.14-1.17-.06-.11-.22-.18-.47-.3"/></svg>',
  };
  const ORPHAN_CHANNEL_LABEL = { all: '', wa: 'WhatsApp', fbm: 'Facebook', ig: 'Instagram' };
  // Mirror of the server's INBOX_ORPHANS_BULK_CAP. Kept as a constant here only
  // to DISABLE the action and say why when a filtered set is bigger — the
  // server rejects an oversized request regardless, and the UI must never
  // silently send the first 100 of 300.
  const ORPHAN_BULK_CAP = 100;
  // Mirrors INBOX_ORPHANS_BULK_WA_CAP. WhatsApp rows each cost live HubSpot
  // writes on a limiter the whole team shares, so they carry a tighter ceiling
  // than the cheap social rows — surfaced here BEFORE the click, with the
  // reason, rather than as a bare 400 after it.
  const ORPHAN_BULK_WA_CAP = 25;
  // The server bounds its own run (ORPHAN_BULK_RUN_BUDGET_MS) and answers with
  // a partial tally rather than working forever, so this watchdog only has to
  // out-wait that budget plus the response. api()'s 60s default did NOT: a
  // WhatsApp-heavy run crosses it while the server keeps writing, and the
  // abort would then have reported a failure that never happened.
  const ORPHAN_BULK_TIMEOUT_MS = 120000;
  // How long the action stays locked after such an abort, so the admin cannot
  // fire a second run into a list that is still being drained.
  const ORPHAN_BULK_UNKNOWN_LOCK_MS = 90000;
  function _orphanChannelCount(ch) {
    const by = state.orphans.byChannel || {};
    if (ch === 'all') return Number(state.orphans.total || 0);
    return Number(by[ch] || 0);
  }
  function _paintOrphanChips() {
    const row = document.getElementById('orphanChips');
    if (!row) return;
    // A channel that drops to zero (the admin just cleared it) must not leave
    // the list filtered to nothing with no way back — fall back to "Tous".
    if (state.orphans.channelFilter !== 'all' && _orphanChannelCount(state.orphans.channelFilter) === 0) {
      state.orphans.channelFilter = 'all';
    }
    row.querySelectorAll('.social-chip').forEach(chip => {
      const ch = chip.getAttribute('data-orphan-channel') || 'all';
      const count = _orphanChannelCount(ch);
      const badge = chip.querySelector('.social-chip-count');
      if (badge) badge.textContent = count > 999 ? '999+' : String(count);
      // "Tous" always stays clickable — it is the escape hatch.
      chip.disabled = ch !== 'all' && count === 0;
      const on = ch === (state.orphans.channelFilter || 'all');
      chip.classList.toggle('active', on);
      chip.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    _paintOrphanBulk();
  }

  // Bulk assign bar. Scope = state.orphans.visibleIds, i.e. exactly the rows
  // rendered under the current channel chip — never a count, never the whole
  // payload. The server re-derives the orphan set anyway and skips anything
  // claimed in the meantime, so a stale client list can only under-assign.
  function _paintOrphanBulk() {
    const row = document.getElementById('orphanBulk');
    if (!row) return;
    const sel = document.getElementById('orphanBulkUser');
    const btn = document.getElementById('orphanBulkBtn');
    const label = document.getElementById('orphanBulkCount');
    const ids = state.orphans.visibleIds || [];
    if (sel) {
      const users = _orphanAssignableUsers();
      const sig = users.map(u => String(u.userId || u.id)).join(',');
      if (sel.getAttribute('data-sig') !== sig) {
        const keep = sel.value;
        sel.innerHTML = '';
        sel.appendChild(el('option', { value: '', text: 'Attribuer tout à…' }));
        for (const u of users) {
          sel.appendChild(el('option', { value: u.userId || u.id, text: userLabel(u.username, u.displayName || u.username) }));
        }
        sel.setAttribute('data-sig', sig);
        if (keep && users.some(u => String(u.userId || u.id) === keep)) sel.value = keep;
      }
    }
    const scope = _orphanBulkScope();
    const partial = scope.length < ids.length;
    const unknown = Date.now() < Number(state.orphans.bulkUnknownUntil || 0);
    if (label) {
      const plural = ids.length > 1 ? 's' : '';
      label.textContent = unknown
        ? 'Attribution toujours en cours côté serveur — la liste se met à jour toute seule.'
        : (ids.length === 0 ? 'Aucune conversation affichée' : (ids.length + ' conversation' + plural));
      label.title = partial
        ? 'Un clic attribue les ' + scope.length + ' premières de la liste (maximum ' + ORPHAN_BULK_WA_CAP
          + ' WhatsApp par action : chacune écrit dans HubSpot, partagé par toute l\'équipe). Recliquez pour la suite.'
        : '';
      label.classList.toggle('is-warn', unknown);
    }
    if (btn) {
      if (btn.textContent !== '…') btn.textContent = partial ? 'Attribuer ' + scope.length : 'Attribuer tout';
      btn.disabled = unknown || !(sel && sel.value) || scope.length === 0;
    }
    // Self-healing: repaint once the lock expires so the bar re-arms itself.
    if (unknown && !_orphanBulkUnlockTimer) {
      _orphanBulkUnlockTimer = setTimeout(() => {
        _orphanBulkUnlockTimer = null;
        _paintOrphanBulk();
      }, Math.max(1000, Number(state.orphans.bulkUnknownUntil || 0) - Date.now() + 250));
    }
  }
  let _orphanBulkUnlockTimer = null;

  // The rows ONE click assigns: every row shown, or — past a cap — the first
  // ones in list order (a WhatsApp row writes to HubSpot, so it caps lower).
  // The list reloads after each run, so the next click takes the next batch:
  // the button always works instead of greying out with a paragraph.
  function _orphanBulkScope() {
    const out = [];
    let wa = 0;
    for (const id of (state.orphans.visibleIds || [])) {
      if (out.length >= ORPHAN_BULK_CAP) break;
      const isWa = (state.orphans.reasons?.get(id)?.channel || 'wa') === 'wa';
      if (isWa && wa >= ORPHAN_BULK_WA_CAP) continue;
      if (isWa) wa++;
      out.push(id);
    }
    return out;
  }

  async function _runOrphanBulkAssign() {
    const sel = document.getElementById('orphanBulkUser');
    const btn = document.getElementById('orphanBulkBtn');
    const shown = (state.orphans.visibleIds || []).length;
    const ids = _orphanBulkScope();
    if (!sel || !sel.value || !ids.length || ids.length > ORPHAN_BULK_CAP) return;
    const who = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text : '';
    const chLabel = ORPHAN_CHANNEL_LABEL[state.orphans.channelFilter || 'all'] || '';
    const plural = ids.length > 1 ? 's' : '';
    const ok = window.confirm(
      'Attribuer ' + ids.length + ' conversation' + plural
      + (chLabel ? ' ' + chLabel : '') + ' à ' + who + ' ?'
      + (ids.length < shown ? ' (les ' + ids.length + ' premières de la liste sur ' + shown + ')' : '') + '\n\n'
      + 'Chaque conversation passe par l\'attribution normale : le propriétaire '
      + 'HubSpot est mis à jour. Celles réclamées entre-temps sont ignorées, '
      + 'jamais écrasées.');
    if (!ok) return;
    if (btn) { btn.disabled = true; btn.textContent = '…'; }
    try {
      const r = await api('/api/inbox/orphans/bulk-assign', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: sel.value, chatIds: ids }),
        timeoutMs: ORPHAN_BULK_TIMEOUT_MS,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(j.error || ('HTTP ' + r.status), 'error'); return; }
      // Report what ACTUALLY happened — a partial run must never read as success.
      const parts = [j.assigned + ' attribuée(s)'];
      // "partielle" = access moved, CRM reflection still pending. Counting it as
      // an échec would contradict the per-chat detail the server returns.
      if (j.partial) parts.push(j.partial + ' partielle(s) — à relancer');
      if (j.skipped) parts.push(j.skipped + ' ignorée(s)');
      if (j.failed) parts.push(j.failed + ' échec(s)');
      // A run that stopped short must SAY so — the remaining rows are still
      // there and the admin needs to know to run it again, not wonder why.
      if (j.stoppedEarly === 'run_budget') {
        parts.push('arrêtée à mi-course (trop long) — relancez pour le reste');
      } else if (j.stoppedEarly === 'hubspot_rate_limited') {
        parts.push('arrêtée : HubSpot limite les appels — réessayez dans une minute');
      }
      toast(parts.join(' · '),
        j.failed ? 'error' : ((j.partial || j.stoppedEarly) ? 'warning' : 'success'));
    } catch (err) {
      // A client-side abort does NOT stop the server: it is still assigning.
      // Saying "échouée" here would be a lie the admin acts on (by re-running).
      const aborted = /timed out|aborted/i.test(err && err.message ? err.message : String(err));
      if (aborted) {
        state.orphans.bulkUnknownUntil = Date.now() + ORPHAN_BULK_UNKNOWN_LOCK_MS;
        toast('Attribution toujours en cours côté serveur. Ne relancez pas — '
          + 'la liste se met à jour toute seule.', 'warning');
      } else {
        toast('Attribution groupée échouée : ' + (err.message || err), 'error');
      }
    } finally {
      if (btn) btn.textContent = 'Attribuer tout';
      await loadOrphans({ force: true });
      try { loadConversations({ force: true }); } catch (_) { /* not ready */ }
      _paintOrphanBulk();
    }
  }

  // One-action assign, straight from the row. Deliberately the SAME
  // POST /api/inbox/assign the Inbox already uses (via assignChatTo) so the
  // server repairs both ownership stores through _syncContactOwnerToAssignee
  // and writes the usual audit trail — this surface invents no second
  // assignment mechanism and never touches chat_assignments.json itself.
  function _buildOrphanRow(chat, row) {
    const wrap = el('div', { class: 'conv-orphan' });
    const chips = el('div', { class: 'conv-orphan-reasons' });
    const reasons = row.reasons || [];
    const primary = _orphanPrimaryReason(reasons);
    if (primary) {
      chips.appendChild(el('span', {
        class: 'conv-orphan-chip' + (primary === 'no_phone' ? ' is-structural' : ''),
        text: _orphanReasonText(primary, row),
        title: 'Aucun agent ne voit cette conversation :\n'
          + reasons.map(r => '• ' + _orphanReasonText(r, row)).join('\n'),
      }));
    }
    // No number is a fact about the client (social DM, WhatsApp username), not
    // a gap to fix — a quiet second chip.
    if (primary !== 'no_phone' && reasons.includes('no_phone')) {
      chips.appendChild(el('span', { class: 'conv-orphan-chip is-structural', text: ORPHAN_REASON_LABEL.no_phone }));
    }
    wrap.appendChild(chips);
    const actions = el('div', { class: 'conv-orphan-actions' });
    const select = el('select', { class: 'conv-orphan-select', 'aria-label': 'Attribuer à' });
    select.appendChild(el('option', { value: '', text: 'Attribuer à…' }));
    for (const u of _orphanAssignableUsers()) {
      // Server viewers carry `userId`; the general assignable list carries `id`.
      select.appendChild(el('option', { value: u.userId || u.id, text: userLabel(u.username, u.displayName || u.username) }));
    }
    const button = el('button', { class: 'conv-orphan-btn', type: 'button', text: 'Attribuer', disabled: true });
    select.addEventListener('change', () => { button.disabled = !select.value; });
    // The row sits inside a clickable conversation item — every interaction
    // here must stay local or the admin opens the chat instead of assigning.
    for (const node of [wrap, select, button]) {
      node.addEventListener('click', ev => ev.stopPropagation());
    }
    button.addEventListener('click', async () => {
      if (!select.value) return;
      button.disabled = true;
      button.textContent = '…';
      await assignChatTo(select.value, chat.id);
      await loadOrphans({ force: true });
    });
    actions.appendChild(select);
    actions.appendChild(button);
    wrap.appendChild(actions);
    return wrap;
  }

  // Who may RECEIVE one of these conversations — the server's `assignable`
  // list, NOT its `viewers` list. Coverage and eligibility are different
  // questions: a teacher or a Meta reviewer can see a chat (so it is not an
  // orphan) yet must never be handed a client. Falls back to the general
  // assignable roster only if the payload predates this field.
  function _orphanAssignableUsers() {
    if (state.orphans.assignable?.length) return state.orphans.assignable;
    return (state.assignableUsers || []).filter(u =>
      u.active !== false && String(u.role || '').toLowerCase() === 'agent');
  }

  // Helpers used above. Defined inline so they're hoisted at module init.
  function jidUserSafe(jid) {
    return String(jid || '').split('@')[0].split(':')[0].replace(/\D/g, '');
  }
  function normalizeMoroccanDigits(value) {
    // Keep legacy call sites on the same fail-closed phone contract as every
    // new open-chat action. The old prefix-only normalizer accepted fragments
    // such as 062026 and could still synthesize a bogus inbox conversation.
    return _phoneForWhatsapp(value) || '';
  }

  // ---- Unified channel compose toggle (WhatsApp <-> SMS) ----
  // A 1-1 contact's WhatsApp and SMS conversations are shown as ONE thread; the
  // compose box lets the agent pick which channel to send on. The toggle only
  // appears for wa/sms 1-1 contacts (not groups / FB / IG).
  function _chatIsWaOrSms(chat) {
    if (!chat || chat.isGroup) return false;
    if (state.personChat?.phoneKey) return true;
    const ch = chat.channel || ((chat.id || '').endsWith('@s.whatsapp.net') ? 'wa'
              : ((chat.id || '').startsWith('sms:') ? 'sms' : ''));
    return ch === 'wa' || ch === 'sms';
  }
  // Does this conversation have a Lougha-app side? Read from the loaded
  // messages: an app row IS the evidence this person writes from the app, and
  // it is what lets the server resolve the thread to answer into. No app rows
  // means there is nothing to reply to there — so the option is not offered.
  function _chatHasAppThread() {
    return (state.messages || []).some(m => m && m.channel === 'app');
  }
  function _composeChannel() {
    if (!_chatIsWaOrSms(state.selectedChat)) return null;
    if (state.composeChannel === 'sms') return 'sms';
    // 'app' survives only while the open chat actually has an app thread —
    // switching to a contact without one must not leave a dead channel armed.
    if (state.composeChannel === 'app' && _chatHasAppThread()) return 'app';
    // support2 needs nothing but a phone, which every WA/SMS chat has.
    if (state.composeChannel === 'support2') return 'support2';
    return 'wa';
  }
  // The chatId a compose-context send must ACTUALLY target once the
  // WhatsApp/SMS toggle is honored. Every compose-side send path (free-form
  // sendText, template quick-send, multi-send) must route through this —
  // posting state.selectedChatId raw ignores the toggle and ships an "SMS"
  // down the WhatsApp branch.
  // PHONE MASKING: selectedChat.phone is masked for agents ('2126****27' —
  // the 2026-07-08 lost-zoom-link bug). The chat id always carries the real
  // digits, so derive from it FIRST.
  function _composeTargetChatId() {
    const base = state.selectedChatId;
    const _cc = _composeChannel();
    if (!_cc) return base;
    const wantedSource = _cc === 'wa' ? 'leads7' : _cc;
    const exactRow = state.personChat?.rows?.find(row => _inboxSourceOf(row) === wantedSource);
    if (exactRow?.id) return exactRow.id;
    const _idDigits = String(base || '').replace(/^support2:/, '').replace(/^sms:/, '').split('@')[0].replace(/\D/g, '');
    const _personDigits = state.personChat?.phoneKey || '';
    const _ph = (_idDigits.length >= 8 ? _idDigits : '') || _personDigits
      || normalizeMoroccanDigits(state.selectedChat?.phone || '');
    if (_ph && _ph.length >= 8 && (_cc === 'wa' || _cc === 'sms' || _cc === 'support2')) {
      return (_cc === 'sms') ? ('sms:' + _ph) : (_ph + '@s.whatsapp.net');
    }
    return base;
  }
  // "Support2" picked for one of OUR WhatsApp threads. A mirrored support2:
  // row already sends through support2 on its own; any other target must carry
  // via=support2, or the send quietly leaves on the Leads7 number — which is how
  // Doha's fiches and voice notes hit a closed 24h window (131047) on 2026-09-16
  // while she had Support2 picked. Every compose-side send path checks this.
  function _composeViaSupport2Line(targetChatId) {
    return _composeChannel() === 'support2' && !String(targetChatId || '').startsWith('support2:');
  }
  // ── Where the reply leaves from ──────────────────────────────────────────
  // One conversation, several ways out of it: this team's WhatsApp line, an
  // SMS, or the Lougha app itself. A cycling button hid that — you had to click
  // to discover what existed. The picker names every channel, says which number
  // the CLIENT will see it come from, and greys the ones that aren't available
  // for this contact instead of hiding them. Same shape as support2's One Chat.
  const _CH_COLOR = { wa: '#16a34a', sms: '#7c3aed', app: '#e11d48', support2: '#0ea5e9' };
  const _CH_LABEL = { wa: 'Leads7', sms: 'SMS', app: 'App', support2: 'Support2' };
  // The sales line the client sees a Leads7 message arrive from.
  const _CH_WA_NUMBER = '0661 557 999';
  // The support line. A client who never answered on the Leads7 number may
  // already be talking to the team on this one — the message goes out through
  // support2's own WhatsApp, and the reply lands in support2's inbox.
  const _CH_SUB = { wa: _CH_WA_NUMBER, sms: 'Yeastar', app: "f l'app", support2: 'ra9m support2' };
  const _COMPOSE_CHANNELS = ['wa', 'sms', 'app', 'support2'];
  // Meta's free-form window: 24 h after the client's last message TO THAT number.
  const WA_WINDOW_MS = 24 * 60 * 60 * 1000;

  // ── WhatsApp line picker (Ayoub 2026-10-05) ──────────────────────────────
  // With several of our numbers live (0661 + Douha, Bouchra, Kanza…), the
  // picker lists each one, and the team chooses which number the reply leaves
  // from. The server keeps the pick until the client writes again (then the
  // reply follows the client). The view comes from GET …/wa-lines, per chat.
  function _waLinesView() {
    const v = state.waLinesView;
    return v && v.chatId === state.selectedChatId && v.enabled === true
      && Array.isArray(v.lines) && v.lines.length > 1 ? v : null;
  }
  function _loadWaLinesView(force) {
    const chatId = state.selectedChatId;
    if (!chatId || !String(chatId).endsWith('@s.whatsapp.net')) return;
    const v = state.waLinesView;
    // Fresh enough: the client may have written to another number since.
    if (!force && v && v.chatId === chatId && Date.now() - (v.at || 0) < 30000) return;
    if (state._waLinesLoading === chatId) return;
    state._waLinesLoading = chatId;
    api('/api/inbox/chats/' + encodeURIComponent(chatId) + '/wa-lines')
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        if (state.selectedChatId !== chatId) return;
        state.waLinesView = Object.assign({ chatId, at: Date.now() }, j || { enabled: false, lines: [] });
        if (_waLinesView()) {
          _renderComposeChannelBtn();
          try { _paintWaLineHeader(); } catch (_) { /* chip is optional */ }
        }
      })
      .catch(() => { /* picker falls back to the single Leads7 row */ })
      .finally(() => { if (state._waLinesLoading === chatId) state._waLinesLoading = null; });
  }
  // Latest client message per number in the loaded thread ('main' = untagged).
  function _waLineLastInbound() {
    const last = {};
    for (const m of (state.messages || [])) {
      if (!m || m.fromMe || m.internal || m.peer) continue;
      if (m.type === 'call' || m.type === 'system' || m.type === 'note') continue;
      if (m.sentVia === 'support2' || String(m.id || '').startsWith('s2in:')) continue;
      if (m._personSource && m._personSource !== 'leads7') continue;
      if (!String(m.chatId || '').endsWith('@s.whatsapp.net')) continue;
      if (m.channel && m.channel !== 'wa' && m.channel !== 'whatsapp') continue;
      const key = m.waLine && m.waLine.id ? String(m.waLine.id) : 'main';
      let ts = Number(m.timestamp) || 0;
      if (ts && ts < 1e12) ts *= 1000;
      if (!last[key] || ts > last[key]) last[key] = ts;
    }
    return last;
  }
  // "0664598906" / "212664598906" → "0664 598 906".
  function _waLineNumberFmt(number) {
    const d = String(number || '').replace(/\D/g, '');
    const local = d.length === 12 && d.startsWith('212') ? '0' + d.slice(3) : d;
    return /^0\d{9}$/.test(local) ? local.slice(0, 4) + ' ' + local.slice(4, 7) + ' ' + local.slice(7) : local;
  }
  async function _pickWaLine(lineId) {
    const chatId = state.selectedChatId;
    if (!chatId) return;
    try {
      const r = await api('/api/inbox/chats/' + encodeURIComponent(chatId) + '/wa-line', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lineId }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error((j && j.error) || ('HTTP ' + r.status));
      if (state.selectedChatId === chatId) state.waLinesView = Object.assign({ chatId, at: Date.now() }, j);
      _setComposeChannel('wa', true);
      try { _paintWaLineHeader(); } catch (_) { /* chip is optional */ }
    } catch (err) {
      toast('Ligne WhatsApp non changée : ' + (err && err.message ? err.message : err), 'error');
    }
  }

  function _renderComposeChannelBtn() {
    const btn = E.composeChannelBtn, lbl = E.composeChannelLabel;
    if (!btn || !lbl) return;
    document.getElementById('composeChannelMenu')?.remove();
    if (!_chatIsWaOrSms(state.selectedChat)) { btn.classList.add('hidden'); return; }
    btn.classList.remove('hidden');
    btn.classList.add('compose-channel-pick');
    const ch = _composeChannel();
    const hasApp = _chatHasAppThread();
    _loadWaLinesView(false);
    const waView = _waLinesView();
    const waCurrent = waView ? waView.lines.find(l => l.id === waView.current) : null;

    lbl.textContent = (ch === 'wa' && waCurrent && waCurrent.id !== 'main' ? (waCurrent.label || 'WhatsApp') : _CH_LABEL[ch]) + ' ▾';
    const icon = btn.querySelector('[data-lucide]');
    if (icon) icon.remove();                       // the coloured dot replaces it
    let dot = btn.querySelector('.compose-channel-dot');
    if (!dot) {
      dot = el('i', { class: 'compose-channel-dot' });
      btn.insertBefore(dot, btn.firstChild);
    }
    dot.style.background = _CH_COLOR[ch];
    btn.style.color = '';
    btn.title = "D'où part la réponse — cliquer pour changer";

    const menu = el('div', { id: 'composeChannelMenu', class: 'compose-channel-menu hidden' });
    for (const k of _COMPOSE_CHANNELS) {
      // Several WhatsApp numbers live → one row per number instead of "Leads7".
      if (k === 'wa' && waView) {
        const lastIn = _waLineLastInbound();
        const now = Date.now();
        for (const line of waView.lines) {
          const open = (now - (lastIn[line.id] || 0)) < WA_WINDOW_MS;
          const item = el('button', {
            class: 'compose-channel-item' + (ch === 'wa' && line.id === waView.current ? ' active' : ''),
            type: 'button',
            title: open ? 'Le client a écrit à ce numéro ces dernières 24 h'
              : "Le client n'a pas écrit à ce numéro depuis 24 h — seul un template part",
          }, [
            el('i', { class: 'compose-channel-dot', style: 'background:' + _CH_COLOR.wa }),
            el('span', { class: 'compose-channel-name' }, [
              document.createTextNode(line.id === 'main' ? _CH_LABEL.wa : (line.label || 'WhatsApp')),
              el('small', { class: 'compose-channel-sub', text: _waLineNumberFmt(line.number) || (line.id === 'main' ? _CH_WA_NUMBER : '') }),
            ]),
            el('em', { class: open ? 'compose-channel-on' : 'compose-channel-tpl', text: open ? 'kayn' : 'template' }),
          ]);
          item.addEventListener('click', (ev) => {
            ev.stopPropagation();
            menu.classList.add('hidden');
            _pickWaLine(line.id);
          });
          menu.appendChild(item);
        }
        continue;
      }
      // The app needs a students7 thread key, which cannot be derived from a
      // phone — so it is only live for a contact who has actually written from
      // the app. Greyed rather than hidden: the team should see the channel
      // exists and why it is unavailable here.
      const blocked = k === 'app' && !hasApp;
      const item = el('button', {
        class: 'compose-channel-item' + (k === ch ? ' active' : '') + (blocked ? ' is-off' : ''),
        type: 'button',
        title: blocked ? "Ce contact n'écrit pas depuis l'app" : '',
      }, [
        el('i', { class: 'compose-channel-dot', style: 'background:' + _CH_COLOR[k] }),
        el('span', { class: 'compose-channel-name' }, [
          document.createTextNode(_CH_LABEL[k]),
          el('small', { class: 'compose-channel-sub', text: k === 'wa' ? _composeWaNumberLabel() : _CH_SUB[k] }),
        ]),
        el('em', { class: blocked ? 'compose-channel-off' : 'compose-channel-on', text: blocked ? '—' : 'kayn' }),
      ]);
      if (!blocked) item.addEventListener('click', (ev) => {
        ev.stopPropagation();
        menu.classList.add('hidden');
        _setComposeChannel(k, true);
      });
      menu.appendChild(item);
    }
    (btn.parentNode || document.body).insertBefore(menu, btn.nextSibling);
    try { refreshIcons(btn); } catch {}
  }
  // The WhatsApp line the CLIENT is actually talking on, read from the loaded
  // thread: the last WhatsApp message (either direction, calls skipped) — on
  // the Support2 line → 'support2', on our own number → 'wa', no WhatsApp
  // message yet → null. Support2-line rows carry sentVia:'support2' (outbound),
  // an 's2in:' id (inbound forwarded by support2) or a support2 person source.
  function _lastWaLineInThread() {
    const msgs = Array.isArray(state.messages) ? state.messages : [];
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i];
      if (!m) continue;
      const ch = String(m.channel || '').toLowerCase();
      if (ch && !['wa', 'whatsapp', 'support2'].includes(ch)) continue;
      if (m.type === 'call' || m.type === 'call_event' || m.type === 'system') continue;
      if (m.sentVia === 'support2' || ch === 'support2' || String(m.id || '').startsWith('s2in:')
          || m._personSource === 'support2' || m._ocSrc === 'support2') return 'support2';
      return 'wa';
    }
    return null;
  }
  function _setComposeChannel(ch, userSet) {
    state.composeChannel = ch;
    if (userSet) state.composeChannelUserSet = true;
    _renderComposeChannelBtn();
  }
  function _toggleComposeChannel(ev) {
    ev?.stopPropagation?.();
    const menu = document.getElementById('composeChannelMenu');
    if (!menu) return;
    const opening = menu.classList.contains('hidden');
    menu.classList.toggle('hidden');
    // Click anywhere else closes it — registered only while open so we never
    // leave a listener behind that swallows the next click on the page.
    if (opening) setTimeout(() => document.addEventListener('click', () => menu.classList.add('hidden'), { once: true }), 0);
  }

  // ─── @mention autocomplete for notes (2026-08-14, Ayoub) ──────────────
  // Typing @ in a note input suggests the WHOLE team — this platform's users
  // AND the support2 peers (tagged "· Support2"). The dropdown inserts plain
  // @username text; the server re-parses it against the combined roster.
  // Mentionable = the active team WITH an inbox (Ayoub 2026-08-14): teachers,
  // trial/test accounts and viewers never surface in the @ autocomplete.
  function _mentionableRole(u) {
    return ['admin', 'agent', 'super_sales'].includes(String((u && u.role) || '').toLowerCase());
  }
  function _mentionRoster() {
    const own = (state.assignableUsers || [])
      .filter(u => u.username && !u.remote && _mentionableRole(u))
      .map(u => ({ username: u.username, displayName: userLabel(u.username, u.displayName || u.username) }));
    const peers = Array.isArray(state._mentionPeerUsers) ? state._mentionPeerUsers : [];
    if (!peers.length && !state._mentionPeerFetching) {
      state._mentionPeerFetching = true;
      api('/api/team/peer-users').then(r => r.ok ? r.json() : null).then(j => {
        state._mentionPeerUsers = ((j && j.users) || []).filter(_mentionableRole).map(u => ({
          username: u.username,
          displayName: (userLabel(u.username, u.displayName || u.username)) + ' · Support2'
        }));
      }).catch(() => { /* peer down → local names only */ });
    }
    const seen = new Set(own.map(u => String(u.username || '').toLowerCase()));
    for (const u of own) if (adminLabelFor(u.username)) seen.add(mentionHandle(u.username));
    return own.concat(peers.filter(u => !seen.has(String(u.username || '').toLowerCase())
      && !(adminLabelFor(u.username) && seen.has(mentionHandle(u.username)))));
  }
  function _attachMentionAc(input, opts = {}) {
    if (!input || input._mentionAcWired) return;
    input._mentionAcWired = true;
    const ac = el('div', { class: 'mention-ac hidden' });
    ac.style.cssText = 'position:absolute;left:8px;bottom:calc(100% + 6px);z-index:1200;background:#fff;border:1px solid #e5e7eb;border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,.14);min-width:230px;max-height:230px;overflow:auto;padding:4px';
    const host = input.parentElement || document.body;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(ac);
    const hide = () => ac.classList.add('hidden');
    const update = () => {
      if (opts.noteModeOnly && !state.composeNoteMode) return hide();
      const upto = input.value.slice(0, input.selectionStart ?? input.value.length);
      const m = /(^|\s)@([a-z0-9._-]*)$/i.exec(upto);
      if (!m) return hide();
      const q = m[2].toLowerCase();
      const items = _mentionRoster().filter(u =>
        mentionHandle(u.username).toLowerCase().startsWith(q) ||
        String(u.displayName || '').toLowerCase().includes(q)).slice(0, 6);
      if (!items.length) return hide();
      clear(ac);
      for (const u of items) {
        const it = el('button', { type: 'button' });
        it.style.cssText = 'display:flex;gap:8px;align-items:center;width:100%;padding:6px 10px;border:0;background:transparent;cursor:pointer;border-radius:8px;text-align:left;font:inherit';
        const b = el('b', { text: '@' + userLabel(u.username) });
        it.appendChild(b);
        if (u.displayName && u.displayName !== u.username && !adminLabelFor(u.username)) {
          it.appendChild(el('span', { text: u.displayName, style: 'color:#6b7280;font-size:12px' }));
        }
        it.addEventListener('mousedown', e => e.preventDefault());
        it.addEventListener('mouseenter', () => { it.style.background = '#f3f4f6'; });
        it.addEventListener('mouseleave', () => { it.style.background = 'transparent'; });
        it.addEventListener('click', () => {
          const pos = input.selectionStart ?? input.value.length;
          const before = input.value.slice(0, pos).replace(/@[a-z0-9._-]*$/i, '@' + mentionHandle(u.username) + ' ');
          input.value = before + input.value.slice(pos);
          hide(); input.focus();
          input.setSelectionRange(before.length, before.length);
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
        ac.appendChild(it);
      }
      ac.classList.remove('hidden');
    };
    input.addEventListener('input', update);
    input.addEventListener('keyup', e => { if (e.key === 'Escape') hide(); else update(); });
    input.addEventListener('blur', () => setTimeout(hide, 150));
  }

  // ─── Internal note mode ───────────────────────────────────────────────
  // The composer doubles as the note box: toggle it and whatever you type
  // stays inside the team, pinned inline in the conversation at the moment
  // you wrote it. The amber composer + placeholder are the ONLY thing telling
  // an agent that pressing Enter won't message the client, so both must be
  // unmistakable — a note sent to a client by accident is a real incident.
  function _renderComposeNoteBtn() {
    const btn = E.composeNoteBtn;
    if (!btn) return;
    const on = !!state.composeNoteMode;
    btn.classList.toggle('active', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    document.querySelector('.compose')?.classList.toggle('note-mode', on);
    if (E.composeInput) {
      E.composeInput.placeholder = on
        ? "Note interne — l'équipe bark, ma-kat-tsift-ch l'client"
        : (E.composeInput.dataset.basePlaceholder || 'Type a message…');
    }
    // The WA/SMS toggle is meaningless while writing a note.
    E.composeChannelBtn?.classList.toggle('dimmed', on);
    try { refreshIcons(btn); } catch {}
  }
  function _setComposeNoteMode(on) {
    state.composeNoteMode = !!on;
    _renderComposeNoteBtn();
    if (on) E.composeInput?.focus();
  }
  async function _submitInternalNote(text) {
    const chatId = state.selectedChatId;
    if (!chatId) return;
    const input = E.composeInput;
    const prev = input ? input.value : '';
    if (input) { input.value = ''; autoSize(); toggleSendBtn(); }
    try {
      const r = await api('/api/inbox/chats/' + encodeURIComponent(chatId) + '/note', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'note failed');
      const { note, twin } = await r.json();
      _landComposerNote(chatId, note, twin);
      // Stay in note mode: notes usually come in twos and threes.
    } catch (err) {
      if (input) { input.value = prev; autoSize(); toggleSendBtn(); }
      toast("L'note ma-tsjelatch — 3awd", 'error');
      console.warn('[note] failed:', err);
    }
  }

  // A composer note the server just filed (text or voice). The notes-store
  // copy (twin) is what the thread draws — card with tags, "Marki seen",
  // edit, player — so it lands in the cache now; the row is pushed so the
  // socket echo de-duplicates by id like every other inbox row.
  function _landComposerNote(chatId, note, twin) {
    if (twin && state.selectedChatId === chatId) {
      if (!state._notesByChat) state._notesByChat = {};
      const cached = state._notesByChat[chatId] || [];
      if (!cached.some(n => n.id === twin.id)) state._notesByChat[chatId] = cached.concat(twin);
      forceMessagesRerender();
    }
    if (note && state.selectedChatId === chatId && !state.messages.some(m => m.id === note.id)) {
      state.messages.push(note);
      renderMessages({ forceBottom: true });
    }
    if (twin) refreshNotesBadge();
  }

  // Voice note for the team (2026-10-20, Ayoub): recorded with Note on, the
  // clip is filed as a team note — /note-audio never sends to WhatsApp. Text
  // already typed in the composer goes with it, so it can tag someone.
  async function _submitVoiceNote(blob, durationMs, chatId) {
    if (!chatId) return;
    const input = E.composeInput;
    // The composer's text belongs to the chat on screen — only take it when
    // that is still the chat the clip was recorded in.
    const caption = (input && state.selectedChatId === chatId) ? input.value.trim() : '';
    const fd = new FormData();
    fd.append('audio', blob, 'voice-note.webm');
    fd.append('durationMs', String(Math.round(durationMs || 0)));
    if (caption) fd.append('text', caption);
    try {
      const r = await fetchT('/api/inbox/chats/' + encodeURIComponent(chatId) + '/note-audio', {
        method: 'POST', headers: authHeaders(), body: fd,
      }, 120000);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      if (caption && input && state.selectedChatId === chatId && input.value.trim() === caption) {
        input.value = ''; autoSize(); toggleSendBtn();
      }
      _landComposerNote(chatId, j.note, j.twin);
      toast('Note vocale tsjelat — team ghir', 'success');
    } catch (err) {
      toast("Note vocale ma-tsjelatch — 3awd", 'error');
      console.warn('[note] voice failed:', err);
    }
  }

  // The clip of a voice note (row or store copy), under its text.
  function _noteAudioEl(audio) {
    if (!audio || !audio.url) return null;
    const box = el('div', { class: 'note-audio', style: 'margin-top:6px;min-width:220px' });
    box.appendChild(buildAudioPlayer(audio.url, {
      mime: audio.mimeType || 'audio/ogg',
      durationSec: audio.durationMs ? Math.round(audio.durationMs / 1000) : undefined,
    }));
    return box;
  }

  function renderEmptyChannelState() {
    // F6: cancel any in-flight chunked list render — this painter wipes the
    // list container, and a stale pump tick would insert chat rows into the
    // "Coming soon" panel. (Called both via renderConversations, where the
    // bump is redundant, and directly from loadConversations' email branch.)
    _convRenderToken++;
    clear(E.conversationList);
    const isSms = state.activeChannel === 'sms';
    const wrap = el('div', { class: 'conv-empty' }, [
      lucideIcon(isSms ? 'smartphone' : 'mail', { style: 'width:48px;height:48px;opacity:0.4;' }),
      el('strong', { style: 'color:var(--gray-700);font-size:14px;', text: (isSms ? 'SMS' : 'Email') + ' — Coming soon' }),
      el('span', { text: isSms
        ? 'Twilio/Vonage integration planned.'
        : 'IMAP/Gmail integration planned.' })
    ]);
    E.conversationList.appendChild(wrap);
    refreshIcons();
  }

  // Boolean quick-filter predicates. Each checked type must hold (AND —
  // they're independent properties, not values of one property).
  // - "unread": the LAST inbound message wasn't from us — customer waiting.
  // - "team": WA groups marked team-only + 1-1 chats with a platform user
  //   (synthetic no-WA teammate rows are injected later in renderConversations).
  const INBOX_TYPE_PREDICATES = {
    unread: c => c.lastMessage && c.lastMessage.fromMe === false,
    bot: c => c.botHandled,
    hubspot: c => c.hubspotContactId,
    unknown: c => !c.hubspotContactId && !c.isGroup,
    team: c => (c.isGroup && c.isTeamOnly) || c.teamMember,
    // "Mes notes" (2026-08-14): chats holding notes that tag ME and are still
    // unchecked — Leads7 notes ∪ mentions mirrored from Support2 (by phone).
    mynotes: c => !!((state._noteMentionPending || {})[c.id]),
  };

  // Pending-mention counts for the calling agent, keyed by chatId. Refreshed
  // at boot, every 3 min, when the mynotes filter engages, and after a ✓
  // toggle. Fail-open — a fetch error keeps the last known map.
  async function _fetchNoteMentions() {
    try {
      const r = await api('/api/inbox/note-mentions');
      if (!r.ok) return;
      const j = await r.json();
      state._noteMentionPending = (j && j.chats) || {};
      state._noteMentionTotal = (j && j.total) || 0;
      state._noteMentionNames = (j && j.names) || {};
      _renderMyNotesUi();
      if (state.inboxFilters.types.has('mynotes')) renderConversations();
    } catch { /* peer down → keep last map */ }
  }
  function _renderMyNotesUi() {
    const total = state._noteMentionTotal || 0;
    const active = state.inboxFilters.types.has('mynotes');
    const chip = document.getElementById('myNotesChip');
    if (chip) {
      // Always on the rail, 0 included — like L7/S2/SMS/App (2026-09-23).
      chip.style.display = 'inline-flex';
      const cnt = document.getElementById('myNotesChipCount');
      if (cnt) cnt.textContent = total > 99 ? '99+' : String(total);
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
    const menuCnt = document.getElementById('myNotesMenuCount');
    if (menuCnt) {
      menuCnt.textContent = String(total);
      menuCnt.classList.toggle('hidden', !total);
    }
    // The chip lives in the source-chip row — that row's visibility depends on
    // this count too.
    try { _s2mRenderChips(); } catch { /* chips are decoration */ }
  }
  // Toggle "seen" on a mention of me (note card ✓). s2:-prefixed ids relay to
  // support2 over the bridge server-side.
  // ─── "Kayna note 3lik hna" jump bar (2026-09-11, Ayoub) ───────────────
  // "khassni ghir ndkhol conversation ytle3 lia chi 7aja b7al fin kaynin":
  // the Mes notes filter already says WHICH chats hold a note for you, but
  // inside a 90-message thread the note itself still had to be hunted for.
  // This bar announces them on open and walks through them one click at a
  // time. Only notes that tag YOU and are still unchecked count — marking
  // one seen makes it disappear, so the bar empties as you work.
  // Support2-origin notes (s2: ids) tag and stamp my SUPPORT2 login — the same
  // person can be mmehanane here and mansour there (2026-09-23). Local notes
  // use my Leads7 login.
  function _mentionKeyFor(n) {
    const me = String(state.me?.username || '').toLowerCase();
    if (String((n && n.id) || '').startsWith('s2:')) return state.mySupport2Username || me;
    return me;
  }
  function _myPendingNotesInChat() {
    if (!String(state.me?.username || '')) return [];
    const notes = (state._notesByChat || {})[state.selectedChatId] || [];
    return notes.filter(n => {
      const me = _mentionKeyFor(n);
      return Array.isArray(n.mentions) && n.mentions.includes(me)
        && !((n.mentionChecks || {})[me]);
    });
  }

  let _noteJumpIdx = 0;

  function _scrollToNote(noteId) {
    const sel = '.note-card-row[data-note-id="' + String(noteId).replace(/"/g, '\\"') + '"]';
    const row = document.querySelector(sel);
    // The thread renders lazily; a note above the loaded window has no row yet.
    if (!row) { toast("L'note mazal ma-tcharjat-ch — scrolli chwiya l fo9", ''); return; }
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    row.classList.remove('note-flash');
    void row.offsetWidth; // restart the animation on a repeat click
    row.classList.add('note-flash');
    setTimeout(() => row.classList.remove('note-flash'), 2600);
  }

  function renderNoteJumpBar() {
    const bar = document.getElementById('noteJumpBar');
    if (!bar) return;
    const pending = _myPendingNotesInChat();
    clear(bar);
    if (!pending.length) { bar.classList.add('hidden'); return; }
    bar.classList.remove('hidden');
    if (_noteJumpIdx >= pending.length) _noteJumpIdx = 0;
    bar.appendChild(el('span', { class: 'note-jump-icon', text: '📝' }));
    bar.appendChild(el('span', {
      class: 'note-jump-label',
      text: pending.length === 1
        ? "Kayna note 3lik f had l'conversation"
        : pending.length + " notes 3lik f had l'conversation",
    }));
    // Who wrote it, when — enough to know if it still matters before jumping.
    const first = pending[_noteJumpIdx] || pending[0];
    const who = first.author ? userLabel(first.author.username, first.author.displayName || first.author.username || '') : '';
    if (who) bar.appendChild(el('span', { class: 'note-jump-meta', text: who + (first.createdAt ? ' · ' + formatNoteTime(first.createdAt) : '') }));
    const go = el('button', {
      class: 'note-jump-btn',
      type: 'button',
      text: pending.length > 1 ? 'Chouf ' + (_noteJumpIdx + 1) + '/' + pending.length : 'Chouf',
      title: 'Nzel 3la l\'note',
    });
    go.addEventListener('click', () => {
      const n = pending[_noteJumpIdx % pending.length];
      _scrollToNote(n.id);
      _noteJumpIdx = (_noteJumpIdx + 1) % pending.length;
      renderNoteJumpBar();
    });
    bar.appendChild(go);
  }

  async function _toggleMentionCheck(noteId) {
    const chatId = state.selectedChatId;
    if (!chatId) return;
    try {
      const r = await api('/api/notes/' + encodeURIComponent(noteId) + '/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(j.error || 'Failed', 'error'); return; }
      refreshNotesBadge();
      loadNotes();
      _fetchNoteMentions();
    } catch (e) { toast('Failed: ' + (e.message || e), 'error'); }
  }

  // ── One person / many source threads (2026-08-14, Ayoub) ──────────────────
  // The server deliberately returns authorised transport rows: one for our WA
  // line, one for SMS, and assigned rows mirrored from Support2/App. The UI's
  // unit is different: ONE person. Group by the contact's normalized last-nine
  // phone digits, keep every backing row for timeline/send routing, and render
  // one canonical row. This mirrors Support2's One Chat identity model and is
  // what prevents the same student appearing twice after switching numbers.
  const _PERSON_SOURCE_ORDER = ['leads7', 'support2', 'sms', 'app'];
  const _PERSON_SOURCE_LABEL = { leads7: 'L7', support2: 'S2', sms: 'SMS', app: 'APP' };

  function _inboxSourceOf(c) {
    if (!c || c.isGroup) return '';
    const ch = String(c.channel || '').toLowerCase();
    const id = String(c.id || '');
    if (ch === 'support2') return c.s2Kind === 'app' ? 'app' : 'support2';
    if (ch === 'app' || id.startsWith('app:')) return 'app';
    if (ch === 'sms' || id.startsWith('sms:')) return 'sms';
    if (ch === 'wa' || (!ch && id.endsWith('@s.whatsapp.net'))) return 'leads7';
    return '';
  }

  function _inboxPersonPhoneKey(c) {
    if (!c) return '';
    if (c.personPhoneKey && /^\d{8,15}$/.test(String(c.personPhoneKey))) {
      return String(c.personPhoneKey).slice(-9);
    }
    // Prefer explicit phone data when it is not masked. For normal agents the
    // operational chat id still carries the real destination, so fall through
    // to s2ChatId/id when the display phone contains ****.
    const candidates = [c.phone, c.s2ChatId, String(c.id || '').replace(/^support2:/, '')];
    for (let raw of candidates) {
      raw = String(raw || '');
      if (!raw || raw.includes('*')) continue;
      if (raw.startsWith('sms:')) raw = raw.slice(4);
      raw = raw.split('@')[0];
      const digits = raw.replace(/\D/g, '');
      if (digits.length >= 8 && digits.length <= 15) return digits.slice(-9);
    }
    return '';
  }

  function _inboxPersonKey(c) {
    const phone = _inboxPersonPhoneKey(c);
    return phone ? ('phone:' + phone) : ('chat:' + String(c?.id || ''));
  }

  // A source-filtered person can be anchored on `support2:<remote id>` or an
  // App thread even though the same person's HubSpot/profile identity belongs
  // to their real phone chat. Resolve that canonical profile id once and use
  // it for every HubSpot-backed profile action. Prefer the loaded Leads7 row;
  // otherwise recover the remote WhatsApp id or synthesize it from an explicit
  // unmasked phone. Never synthesize from the last-nine grouping key alone —
  // foreign numbers need their country prefix intact.
  function _profilePersonChatId(chat = state.selectedChat, person = state.personChat) {
    const rows = Array.isArray(person?.rows) ? person.rows : [];
    const local = rows.find(row => _inboxSourceOf(row) === 'leads7'
      && /^\d{8,15}@s\.whatsapp\.net$/.test(String(row?.id || '')));
    if (local?.id) return String(local.id);

    const asWhatsappChatId = (raw) => {
      let value = String(raw || '').trim();
      if (!value || value.includes('*')) return '';
      value = value.replace(/^support2:/i, '').replace(/^sms:/i, '');
      if (/^\d{8,15}@s\.whatsapp\.net$/.test(value)) return value;
      if (value.includes('@')) return '';
      const digits = value.replace(/\D/g, '');
      return /^\d{8,15}$/.test(digits) ? (digits + '@s.whatsapp.net') : '';
    };

    for (const row of rows) {
      const remote = asWhatsappChatId(row?.s2ChatId) || asWhatsappChatId(row?.id);
      if (remote) return remote;
    }
    for (const raw of [chat?.phone, ...rows.map(row => row?.phone)]) {
      const fromPhone = asWhatsappChatId(raw);
      if (fromPhone) return fromPhone;
    }
    return String(chat?.id || state.selectedChatId || '');
  }

  // Exact membership predicate for the default One-Person inbox. Keeping it
  // tied to _inboxSourceOf means the list and its source counters cannot drift
  // (the production symptom was App=1 in the rail but zero rows after click).
  function _isUnifiedInboxTransportRow(c) {
    return !!c && !c.isGroup && !!_inboxSourceOf(c);
  }

  function _inboxRowTimestamp(c) {
    return Number(c?.lastMessage?.timestamp || 0);
  }

  // `sourceFilter` defaults to the source rail's selection, but a channel tab
  // that is itself a single transport (the SMS tab) passes its own so the
  // person anchors on that transport instead of the Leads7 row.
  function _mergeInboxPeople(visibleRows, allRows, sourceFilter = state.srcFilter) {
    const visible = Array.isArray(visibleRows) ? visibleRows : [];
    const backing = Array.isArray(allRows) ? allRows : visible;
    const allByPerson = new Map();
    for (const row of backing) {
      const key = _inboxPersonKey(row);
      if (!allByPerson.has(key)) allByPerson.set(key, []);
      allByPerson.get(key).push(row);
    }
    const visibleByPerson = new Map();
    const order = [];
    for (const row of visible) {
      const key = _inboxPersonKey(row);
      if (!visibleByPerson.has(key)) { visibleByPerson.set(key, []); order.push(key); }
      visibleByPerson.get(key).push(row);
    }
    return order.map(key => {
      const shownRows = visibleByPerson.get(key) || [];
      const personRows = allByPerson.get(key) || shownRows;
      const newestShown = shownRows.reduce((best, row) =>
        !best || _inboxRowTimestamp(row) > _inboxRowTimestamp(best) ? row : best, null) || shownRows[0];
      // In All, anchor the person on Leads7 when possible: its id owns the
      // pipeline/profile/notes data. Inside a source filter, anchor on that
      // source so direct actions naturally address the filtered transport.
      const local = personRows.find(row => _inboxSourceOf(row) === 'leads7');
      const sms = personRows.find(row => _inboxSourceOf(row) === 'sms');
      const primary = sourceFilter ? newestShown : (local || sms || newestShown);
      const identity = local || sms || newestShown || primary;
      const merged = { ...primary };
      for (const field of ['name', 'phone', 'profilePicUrl', 'pipelineStage', 'leadCreatedAt',
        'hubspotContactId', 'recruitmentLane', 'influencerLane', 'teamMember', 'needsAttention']) {
        if (identity && identity[field] != null && identity[field] !== '') merged[field] = identity[field];
      }
      merged.lastMessage = newestShown?.lastMessage || null;
      merged.unread = shownRows.reduce((sum, row) => sum + (Number(row.unread) || 0), 0);
      merged.assignedTo = primary?.assignedTo || identity?.assignedTo || newestShown?.assignedTo || null;
      merged.isPinned = personRows.some(row => !!row.isPinned);
      const labels = new Map();
      for (const row of personRows) for (const lab of (row.labels || [])) {
        const labKey = String(lab.id || lab.name || lab.hex || labels.size);
        if (!labels.has(labKey)) labels.set(labKey, lab);
      }
      merged.labels = [...labels.values()];
      merged._personKey = key;
      merged._personPhoneKey = key.startsWith('phone:') ? key.slice(6) : '';
      merged._personRows = personRows.slice().sort((a, b) => _inboxRowTimestamp(b) - _inboxRowTimestamp(a));
      merged._personSources = _PERSON_SOURCE_ORDER.filter(source =>
        personRows.some(row => _inboxSourceOf(row) === source));
      return merged;
    }).sort((a, b) => _inboxRowTimestamp(b) - _inboxRowTimestamp(a));
  }

  function _sourceRows(rows, source) {
    if (!source) return rows;
    return rows.filter(row => _inboxSourceOf(row) === source);
  }

  // The server keeps WA+SMS compact for older/mobile clients, but preserves
  // both transports in sourceThreads. Expand them only inside this One-Person
  // inbox so source filters and read markers address the real thread ids.
  function _expandInboxSourceRows(rows) {
    const expanded = [];
    for (const row of (Array.isArray(rows) ? rows : [])) {
      const threads = Array.isArray(row?.sourceThreads) ? row.sourceThreads : [];
      if (!threads.length) {
        expanded.push(row);
        continue;
      }
      for (const thread of threads) {
        if (!thread?.id) continue;
        expanded.push({
          ...row,
          id: thread.id,
          channel: thread.channel || (String(thread.id).startsWith('sms:') ? 'sms' : 'wa'),
          lastMessage: thread.lastMessage || null,
          unread: Number(thread.unread || 0),
          _sourceProjectionOf: row.id,
        });
      }
    }
    return expanded;
  }

  function _setInboxThreadUnread(threadId, unread) {
    const id = String(threadId || '');
    for (const row of (state.chats || [])) {
      if (Array.isArray(row.sourceThreads)) {
        const thread = row.sourceThreads.find(item => String(item?.id || '') === id);
        if (!thread) continue;
        thread.unread = Number(unread || 0);
        row.unread = row.sourceThreads.reduce((sum, item) => sum + Number(item?.unread || 0), 0);
        return;
      }
      if (String(row.id || '') === id) {
        row.unread = Number(unread || 0);
        return;
      }
    }
  }

  // Source counts describe unique people in each source, never raw rows. A
  // duplicate transport record therefore cannot inflate the rail count.
  function _s2mCounts() {
    const keys = { leads7: new Set(), support2: new Set(), sms: new Set(), app: new Set() };
    for (const c of _expandInboxSourceRows((state.chats || []).filter(row => !row.isArchived))) {
      const source = _inboxSourceOf(c);
      if (source && keys[source]) keys[source].add(_inboxPersonKey(c));
    }
    return {
      leads7: keys.leads7.size,
      support2: keys.support2.size,
      sms: keys.sms.size,
      app: keys.app.size,
    };
  }
  function _s2mRenderChips() {
    const n = _s2mCounts();
    const wrap = document.getElementById('s2MirrorChips');
    // Mes notes lives ON this rail, so it must not hide it (2026-09-23 —
    // otherwise the chip you just clicked disappears and can't be undone).
    const myNotesOn = state.inboxFilters.types.has('mynotes');
    const normalInbox = state.activeChannel === 'whatsapp' && !state.archiveView
      && !state.inboxFilters.types.has('team');
    // Keep every source visible (including zero) so agents can understand and
    // deliberately choose All/L7/S2/SMS/App instead of guessing from a rail
    // whose buttons appear and disappear as assignments change.
    if (wrap) wrap.style.display = normalInbox ? 'flex' : 'none';
    // State lives in aria-pressed — the stylesheet owns every colour, so the
    // rail themes itself and a screen reader announces the selection.
    const paint = (id, cntId, count, key) => {
      const b = document.getElementById(id);
      if (!b) return;
      b.style.display = 'inline-flex';
      const c = document.getElementById(cntId);
      if (c) c.textContent = count > 99 ? '99+' : String(count);
      b.setAttribute('aria-pressed', !myNotesOn && state.srcFilter === key ? 'true' : 'false');
    };
    paint('s2ChipLeads7', 's2ChipLeads7Count', n.leads7, 'leads7');
    paint('s2ChipSupport2', 's2ChipSupport2Count', n.support2, 'support2');
    paint('s2ChipSms', 's2ChipSmsCount', n.sms, 'sms');
    paint('s2ChipApp', 's2ChipAppCount', n.app, 'app');
  }
  function _s2mToggleSrc(key) {
    state.srcFilter = (state.srcFilter === key) ? '' : key;
    // The rail is ONE choice: picking a source leaves Mes notes.
    if (state.srcFilter && state.inboxFilters.types.delete('mynotes')) {
      syncFilterMenuChecks();
      updateFilterLabel();
      _renderMyNotesUi();
    }
    _s2mRenderChips();
    renderConversations();
  }

  function inboxFilterCount() {
    const F = state.inboxFilters;
    return F.types.size + F.assignees.size + F.stages.size + F.crm.size + (inboxDateActive() ? 1 : 0);
  }

  // ── Inbox date filter (2026-07-14) ─────────────────────────────────────────
  // Single-select recency slice, client-side, on chat.lastMessage.timestamp
  // (ms epoch). Ranges are half-open [from, to) in the browser's local time
  // (= Morocco for the team). Week starts Monday to match the app convention
  // (server _resolveDatePreset uses the same (getDay()+6)%7 pivot).
  const DAY_MS = 86400000;
  function inboxDateActive() {
    const d = state.inboxFilters.date;
    if (!d || !d.preset) return false;
    if (d.preset === 'between') return d.from != null && d.to != null;
    return true;
  }
  // Preset → { from, to } ms. Computed live so "today"/"this_week" stay correct
  // across midnight without any recompute step. Returns null for 'between'
  // (custom range is read straight from state) and unknown presets.
  function _dateRangeForPreset(preset) {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    switch (preset) {
      case 'today':     return { from: todayStart, to: todayStart + DAY_MS };
      case 'yesterday': return { from: todayStart - DAY_MS, to: todayStart };
      case 'this_week': {
        const dow = (now.getDay() + 6) % 7;               // Monday = 0
        const from = todayStart - dow * DAY_MS;
        return { from, to: from + 7 * DAY_MS };
      }
      case 'last_week': {
        const dow = (now.getDay() + 6) % 7;
        const from = todayStart - (dow + 7) * DAY_MS;
        return { from, to: from + 7 * DAY_MS };
      }
      case 'this_month': return {
        from: new Date(now.getFullYear(), now.getMonth(), 1).getTime(),
        to:   new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime(),
      };
      case 'last_month': return {
        from: new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime(),
        to:   new Date(now.getFullYear(), now.getMonth(), 1).getTime(),
      };
      default: return null;
    }
  }
  // Resolve the active date filter to { from, to } ms, or null when inactive.
  function inboxDateRange() {
    const d = state.inboxFilters.date;
    if (!d || !d.preset) return null;
    if (d.preset === 'between') {
      return (d.from != null && d.to != null) ? { from: d.from, to: d.to } : null;
    }
    return _dateRangeForPreset(d.preset);
  }
  // '2026-07-14' (a native <input type=date> value) → local-midnight ms.
  function _inboxYmdToLocalStart(ymd) {
    const [Y, M, D] = String(ymd).split('-').map(Number);
    return new Date(Y, (M || 1) - 1, D || 1).getTime();
  }
  const INBOX_DATE_LABELS = {
    today: 'Today', yesterday: 'Yesterday', this_week: 'This week',
    last_week: 'Last week', this_month: 'This month', last_month: 'Last month',
  };
  // Short label for the filter pill / dropdown label composition.
  function _inboxDateLabel() {
    const d = state.inboxFilters.date;
    if (!d || !d.preset) return '';
    if (d.preset === 'between') {
      if (d.from == null || d.to == null) return 'Custom range';
      const fmt = ms => MA.loc(new Date(ms), 'd', 'en-GB', { day: '2-digit', month: 'short' });
      return fmt(d.from) + ' – ' + fmt(d.to - DAY_MS); // 'to' is exclusive; show the last included day
    }
    return INBOX_DATE_LABELS[d.preset] || d.preset;
  }
  // Clear the date slice back to "no date filter" and reset the custom inputs.
  function _resetInboxDateFilter() {
    const d = state.inboxFilters.date;
    d.preset = ''; d.from = null; d.to = null;
    const fromEl = document.getElementById('inboxDateFrom');
    const toEl = document.getElementById('inboxDateTo');
    if (fromEl) fromEl.value = '';
    if (toEl) toEl.value = '';
    _syncInboxDateUI();
  }
  // Show the custom "Between" range inputs only when that preset is active.
  function _syncInboxDateUI() {
    const row = document.getElementById('inboxDateCustomRow');
    if (row) row.classList.toggle('hidden', state.inboxFilters.date.preset !== 'between');
  }
  // Read the two <input type=date> values → set the custom range on state and
  // re-render. Inclusive of both endpoint days ([fromDay 00:00, toDay+1 00:00)).
  // Swaps the pair if the user picks from > to.
  function _applyInboxCustomRange() {
    const D = state.inboxFilters.date;
    D.preset = 'between';
    const fv = document.getElementById('inboxDateFrom')?.value || '';
    const tv = document.getElementById('inboxDateTo')?.value || '';
    if (fv && tv) {
      let a = _inboxYmdToLocalStart(fv), b = _inboxYmdToLocalStart(tv);
      if (a > b) { const t = a; a = b; b = t; }
      D.from = a; D.to = b + DAY_MS;
    } else {
      D.from = null; D.to = null; // partial selection → not yet active
    }
    syncFilterMenuChecks();
    updateFilterLabel();
    renderConversations();
  }
  // Inject the preset rows + custom range into the Date bar's menu (#dateFilterMenu,
  // once). Kept in JS rather than index.html so all the date wiring lives in one
  // place; the preset rows are plain .dropdown-item[data-filter="date:*"] so the
  // delegated click handler on #dateFilterMenu drives them. The "Any date" clear
  // row is static in index.html and stays at the top.
  function _buildInboxDateFilter() {
    const menu = document.getElementById('dateFilterMenu');
    if (!menu || document.getElementById('inboxDateCustomRow')) return;
    const presets = [
      ['today', 'Today'], ['yesterday', 'Yesterday'],
      ['this_week', 'This week'], ['last_week', 'Last week'],
      ['this_month', 'This month'], ['last_month', 'Last month'],
      ['between', 'Between…'],
    ];
    const frag = document.createDocumentFragment();
    for (const [val, label] of presets) {
      frag.appendChild(el('button', {
        class: 'dropdown-item filter-date', 'data-filter': 'date:' + val,
        'data-display-label': label,
      }, [ el('span', { class: 'filter-check' }), el('span', { text: label }) ]));
    }
    // Custom "Between" range — two stacked date inputs (safe on a narrow inbox
    // column); auto-applies on change. Hidden until the Between preset is picked.
    const custom = el('div', { class: 'inbox-date-custom hidden', id: 'inboxDateCustomRow' }, [
      el('label', { class: 'idc-field' }, [
        el('span', { class: 'idc-label', text: 'From' }),
        el('input', { type: 'date', id: 'inboxDateFrom', class: 'idc-input' }),
      ]),
      el('label', { class: 'idc-field' }, [
        el('span', { class: 'idc-label', text: 'To' }),
        el('input', { type: 'date', id: 'inboxDateTo', class: 'idc-input' }),
      ]),
    ]);
    frag.appendChild(custom);
    menu.appendChild(frag);
    // The date inputs are not .dropdown-item, so the delegated handler ignores
    // them — wire their change events directly. Stop propagation so a click on
    // the input doesn't bubble to the menu delegator.
    for (const id of ['inboxDateFrom', 'inboxDateTo']) {
      const inp = document.getElementById(id);
      if (!inp) continue;
      inp.addEventListener('change', _applyInboxCustomRange);
      inp.addEventListener('click', ev => ev.stopPropagation());
    }
  }

  const INBOX_TYPE_LABELS = {
    unread: 'Awaiting reply', bot: 'Bot', hubspot: 'HubSpot',
    unknown: 'Unknown', team: 'Team',
  };

  // Reflect state.inboxFilters onto the dropdown rows (checkbox visuals) across
  // all three filter bars. Re-run after populateFilterAssignedList rebuilds the
  // per-agent rows. Each bar's "clear" row (all / date-clear / assign-clear) is
  // .active only when that bar has no active selection.
  function syncFilterMenuChecks() {
    const F = state.inboxFilters;
    // Bar 1 owns types + stages; date/assignees live in their own bars now.
    // Mes notes is driven from its chip — it never counts as a Bar 1 choice.
    const bar1Count = F.types.size - (F.types.has('mynotes') ? 1 : 0) + F.stages.size + F.crm.size;
    const menus = [
      E.filterMenu,
      document.getElementById('dateFilterMenu'),
      document.getElementById('assignFilterMenu'),
    ];
    for (const menu of menus) {
      if (!menu) continue;
      menu.querySelectorAll('.dropdown-item').forEach(item => {
        const f = item.getAttribute('data-filter') || '';
        if (f === 'all') { item.classList.toggle('active', bar1Count === 0); return; }
        if (f === 'date-clear') { item.classList.toggle('active', !inboxDateActive()); return; }
        if (f === 'assign-clear') { item.classList.toggle('active', F.assignees.size === 0); return; }
        let on = false;
        if (f.startsWith('crm:')) on = F.crm.has(f.slice(4));
        else if (f.startsWith('pipeline:')) on = F.stages.has(f.slice('pipeline:'.length));
        else if (f.startsWith('assigned:')) on = F.assignees.has(f.slice('assigned:'.length));
        else if (f.startsWith('date:')) on = F.date.preset === f.slice('date:'.length);
        else if (f === 'assigned-me') on = !!state.me?.id && F.assignees.has(state.me.id);
        else if (f) on = F.types.has(f);
        item.classList.toggle('checked', on);
      });
    }
  }

  // Refresh the labels + active markers on all three filter bars. Bar 1's label
  // composes its quick-filter types + pipeline stages; date/assignee each drive
  // their own bar. Single entry point so existing call sites update everything.
  function updateFilterLabel() {
    const F = state.inboxFilters;
    // Bar 1 · All conversations — types + stages only.
    const parts = [];
    // Mes notes leaves the label alone (2026-09-23, Ayoub: "ma-khassch
    // y-tbeddel") — the highlighted chip already says it's on.
    for (const t of F.types) if (t !== 'mynotes') parts.push(INBOX_TYPE_LABELS[t] || t);
    for (const s of F.stages) parts.push(PIPELINE_LABELS[s] || s);
    for (const s of F.crm) parts.push(studentStates.labels[s] || s);
    const label = parts.length === 0 ? 'All conversations'
      : parts.length <= 2 ? parts.join(' · ')
      : parts.length + ' filters';
    if (E.filterLabel) E.filterLabel.textContent = label;
    updateDateFilterLabel();
    updateAssignFilterLabel();
  }

  // Bar 2 · Date — label shows the active preset / custom range, else "Date".
  function updateDateFilterLabel() {
    const lbl = document.getElementById('dateFilterLabel');
    const mark = document.getElementById('dateFilterMark');
    const active = inboxDateActive();
    if (lbl) lbl.textContent = active ? _inboxDateLabel() : 'Date';
    if (mark) mark.classList.toggle('hidden', !active);
  }

  // Bar 3 · Assign to — label shows the sole assignee's name (or "Me"), or
  // "N agents" for multiple, else "Assign to".
  function updateAssignFilterLabel() {
    const F = state.inboxFilters;
    const lbl = document.getElementById('assignFilterLabel');
    const mark = document.getElementById('assignFilterMark');
    const n = F.assignees.size;
    let text = 'Assign to';
    if (n === 1) {
      const id = [...F.assignees][0];
      if (id === state.me?.id) text = 'Me';
      else {
        const u = (state.assignableUsers || []).find(x => x.id === id);
        text = u ? (userLabel(u.username, u.displayName || u.username)) : 'Agent';
      }
    } else if (n > 1) {
      text = n + ' agents';
    }
    if (lbl) lbl.textContent = text;
    if (mark) mark.classList.toggle('hidden', n === 0);
  }

  function applyClientFilter(chats) {
    // Multi-select filter model: OR inside each set, AND across the sets.
    // (assigned to A ∨ B) ∧ (stage ∈ {x,y}) ∧ every checked quick filter.
    const F = state.inboxFilters;
    let out = chats;
    for (const t of F.types) {
      const pred = INBOX_TYPE_PREDICATES[t];
      if (pred) out = out.filter(pred);
    }
    if (F.assignees.size) out = out.filter(c => c.assignedTo && F.assignees.has(c.assignedTo.userId));
    // Server attaches chat.pipelineStage in /api/inbox/chats via a bulk SQL
    // lookup against the leads table. Chats with no matching lead row have
    // no pipelineStage and are excluded by the Set membership check.
    if (F.stages.size) out = out.filter(c => F.stages.has(c.pipelineStage));
    if (F.crm.size) out = out.filter(c => studentStates.resolve(c).flags.some(flag => F.crm.has(flag)));
    // Date slice on the CONTACT'S creation date in our system (leads.created_at,
    // ms epoch), attached by the server as chat.leadCreatedAt. So "This week"
    // means "conversations whose contact was CREATED this week", not messaged.
    // Chats with no linked lead carry no leadCreatedAt → excluded from any dated
    // view (a conversation with no creation date can't match a creation range).
    const dr = inboxDateRange();
    if (dr) out = out.filter(c => {
      const ts = c.leadCreatedAt;
      return typeof ts === 'number' && ts >= dr.from && ts < dr.to;
    });
    return out;
  }

  function previewText(last) {
    if (!last) return 'No messages';
    if (last.type === 'audio') return '🎵 Voice note';
    if (last.type === 'image') return '📷 Photo';
    if (last.type === 'video') return '🎥 Video';
    if (last.type === 'document') return '📄 ' + (last.fileName || 'Document');
    if (last.type === 'sticker') return '🌟 Sticker';
    return last.text || 'No messages';
  }

  function profilePhotoUrl(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
      const parsed = new URL(raw, window.location.origin);
      if (parsed.protocol === 'https:' || (parsed.origin === window.location.origin && parsed.protocol === window.location.protocol)) {
        return parsed.href;
      }
    } catch { /* initials stay visible */ }
    return '';
  }

  // User data is attached through DOM properties/textContent only. If Meta's
  // temporary CDN URL expires, removing the failed image reveals the initials
  // already rendered underneath it.
  function applyContactAvatar(node, chat, className) {
    if (!node) return;
    const c = chat || {};
    node.className = className || ('avatar ' + avatarColorClass(c.id || ''));
    node.textContent = c.isGroup ? '#' : (initials(c.name || c.phone) || '?');
    const src = profilePhotoUrl(c.profilePicUrl);
    if (!src || c.isGroup) return;
    const img = document.createElement('img');
    img.className = 'avatar-photo';
    img.alt = '';
    img.referrerPolicy = 'no-referrer';
    img.src = src;
    img.addEventListener('error', () => img.remove(), { once: true });
    node.appendChild(img);
  }

  // F6 (2026-07-09): render-generation token. Every renderConversations call
  // bumps it; the time-sliced cold-render continuations (see bottom of the
  // function) re-check it each tick and exit silently when a newer render —
  // socket nudge, poll, filter/tab switch, unread-clear, empty/email branch —
  // has superseded them. Other painters that wipe the list container
  // (renderGlobalSearch, renderEmptyChannelState) bump it too so a stale
  // chunk job can never insert rows into their repainted panel.
  let _convRenderToken = 0;

  // Cold-refresh performance (2026-08-14): keep the complete authorised chat
  // list in state so client-side filters, counts, pins, archive, assignment
  // changes and notification scans remain exact, but never mount thousands of
  // conversation rows at once. A super-admin currently has ~1,800 chats; the
  // old cold render expanded those into ~30k DOM elements and blocked Chrome's
  // main thread even after the page looked ready. The visible window grows as
  // the agent scrolls, while polls/realtime updates preserve its current size.
  const CONV_INITIAL_ROWS = 100;
  const CONV_LOAD_STEP = 100;
  const CONV_LOAD_AHEAD_PX = 480;
  let _convVisibleRows = CONV_INITIAL_ROWS;
  let _convMatchingRows = 0;
  let _convWindowKey = '';
  let _convScrollFrame = 0;

  function _convRenderContextKey() {
    const F = state.inboxFilters;
    const D = F.date || {};
    const sorted = values => [...values].sort();
    return JSON.stringify([
      state.activeChannel || '',
      state.socialFilter || '',
      state.srcFilter || '',
      state.archiveView ? 1 : 0,
      state.searchQ || '',
      sorted(F.types),
      sorted(F.assignees),
      sorted(F.stages),
      sorted(F.crm),
      D.preset || '',
      D.from || null,
      D.to || null,
    ]);
  }

  function _convGrowVisibleRows(current, total) {
    const safeCurrent = Math.max(CONV_INITIAL_ROWS, Number(current) || 0);
    const safeTotal = Math.max(0, Number(total) || 0);
    return Math.min(safeTotal, safeCurrent + CONV_LOAD_STEP);
  }

  function _convResetWindow() {
    _convVisibleRows = CONV_INITIAL_ROWS;
    _convMatchingRows = 0;
    _convWindowKey = '';
    if (E.conversationList) E.conversationList.scrollTop = 0;
  }

  function _convExitGlobalSearch() {
    state.globalSearchResults = null;
    state._chatsFingerprint = null;
    _convResetWindow();
    // Global and regular results share this container. Clear every grouped
    // search section before normal row reconciliation, then paint the cached
    // inbox immediately at its safe initial size while any fetch is in flight.
    clear(E.conversationList);
    renderConversations();
  }

  function _convGrowWindow() {
    if (_convVisibleRows >= _convMatchingRows) return false;
    const next = _convGrowVisibleRows(_convVisibleRows, _convMatchingRows);
    if (next <= _convVisibleRows) return false;
    _convVisibleRows = next;
    renderConversations();
    return true;
  }

  function _convMaybeGrowWindow() {
    const list = E.conversationList;
    // Global search paints a different result model into the same container;
    // a scroll there must never restore the regular inbox underneath it.
    if (!list || state.globalSearchResults || state.activeChannel === 'email') return false;
    if (_convVisibleRows >= _convMatchingRows) return false;
    const remaining = list.scrollHeight - list.scrollTop - list.clientHeight;
    if (remaining > CONV_LOAD_AHEAD_PX) return false;
    return _convGrowWindow();
  }

  function renderConversations() {
    studentStates.start();
    const _token = ++_convRenderToken;
    kzRenderSidebarStatus();
    try { _s2mRenderChips(); } catch { /* chips are decoration — never block the list */ }
    // Socket assignment/attention handlers can request a regular repaint
    // while global search is open. Keep the search result model authoritative
    // instead of letting a realtime event overwrite its shared container or
    // reset its scroll/focus by rebuilding unchanged search results.
    if (state.globalSearchResults) {
      return;
    }
    // Refresh the per-member counts in the filter dropdown — keeps the
    // numbers in sync with whatever's in state.chats right now (chats
    // load, socket assignment changes, etc.).
    populateFilterAssignedList();
    // Platform sub-filter pills only make sense on the merged Social channel —
    // keep them hidden everywhere else (incl. email's early return below).
    const socialChipsRow = document.getElementById('socialChips');
    if (socialChipsRow) socialChipsRow.classList.toggle('hidden', state.activeChannel !== 'social');
    // Same treatment for the "Non attribuées" channel sub-filter.
    const orphanChipsRow = document.getElementById('orphanChips');
    if (orphanChipsRow) orphanChipsRow.classList.toggle('hidden', state.activeChannel !== 'orphans');
    const orphanBulkRow = document.getElementById('orphanBulk');
    if (orphanBulkRow) orphanBulkRow.classList.toggle('hidden', state.activeChannel !== 'orphans');
    _syncSearchyBar();
    if (state.activeChannel === 'email') {
      renderEmptyChannelState();
      return;
    }
    // TikTok DM tab falls through to the standard channel filter
    // below. The filter narrows to zero rows (no TT messages today),
    // and the empty-state branch at line ~2407 emits the
    // "TikTok DM — Business Messaging API access pending" label.
    // (Earlier this short-circuited to renderEmptyChannelState which
    // wrongly rendered the SMS/Email "Coming soon" copy.)
    let chats = state.chats;
    let unifyInboxPeople = false;
    let unifySource = null; // non-null = this tab IS one transport (SMS tab)
    // Sync the Archived shortcut row's count + active styling with the
    // current chat list. Hides the row entirely when nothing is archived
    // so the sidebar isn't cluttered.
    updateArchiveRow(chats);
    // Hide archived chats from the main inbox unless the agent has opened
    // the archived view. In archived view, only show archived ones.
    if (state.archiveView) {
      chats = chats.filter(c => c.isArchived);
    } else {
      chats = chats.filter(c => !c.isArchived);
    }
    // The "Team" filter spans both 1-1 chats AND groups, so it bypasses
    // the inbox/groups/clubs channel pre-filter. Per-label filters do the
    // same — a label like "Pending payment" can apply to a mix of groups
    // and 1-1 chats, and forcing the agent to flip the channel toggle
    // per filter would just hide labeled groups while in inbox channel.
    // The archive view also bypasses the channel filter so 1-1 + groups
    // mix freely (matches WhatsApp's archived-list behaviour).
    // "Team" spans WA groups + 1-1 teammate chats, so it bypasses the
    // channel pre-filter (same as archive view). "Mes notes" bypasses it too —
    // a tagged chat must surface whatever channel tab the agent sits on.
    if (!state.inboxFilters.types.has('team') && !state.inboxFilters.types.has('mynotes') && !state.archiveView) {
      if (state.activeChannel === 'clubs') {
        chats = chats.filter(c => c.isGroup && /club/i.test(c.name || ''));
      } else if (state.activeChannel === 'groups') {
        chats = chats.filter(c => c.isGroup && !/club/i.test(c.name || ''));
      } else if (state.activeChannel === 'social') {
        // Merged "Social media" channel (2026-07-17) — Facebook Messenger +
        // Instagram + TikTok in one list. Comment/mention threads ride along
        // via their chatId prefixes (see _socialPlatform). The #socialChips
        // pills narrow to one platform without refetching.
        chats = chats.filter(c => !!_socialPlatform(c));
        if (state.socialFilter && state.socialFilter !== 'all') {
          chats = chats.filter(c => _socialPlatform(c) === state.socialFilter);
        }
      } else if (state.activeChannel === 'fbm') {
        // Facebook Messenger conversations + Page comments — channel field
        // is 'fbm' for both. Comment threads have chatId prefix
        // 'fbm-comment:' so they group per-post. Both surface in this tab.
        // (No dropdown entry since the Social merge — kept for direct/legacy
        // state.activeChannel values.)
        chats = chats.filter(c => (c.channel === 'fbm')
          || (c.id || '').startsWith('fbm:')
          || (c.id || '').startsWith('fbm-comment:'));
      } else if (state.activeChannel === 'ig') {
        // Instagram tab: DMs + comments + mentions all share channel='ig'
        // but use distinct chatId prefixes (ig: / ig-comment: / ig-mention:).
        chats = chats.filter(c => (c.channel === 'ig')
          || (c.id || '').startsWith('ig:')
          || (c.id || '').startsWith('ig-comment:')
          || (c.id || '').startsWith('ig-mention:'));
      } else if (state.activeChannel === 'tt') {
        chats = chats.filter(c => (c.channel === 'tt') || (c.id || '').startsWith('tt:'));
      } else if (state.activeChannel === 'sms') {
        // SMS tab (Yeastar GSM gateway). The server folds a contact's
        // sms:<digits> thread INTO their WhatsApp row (sourceThreads), so a
        // raw `channel === 'sms'` filter only ever matched SMS-only contacts
        // and hid every SMS conversation whose contact also has WhatsApp —
        // in practice all of them (prod audit 2026-08-15: 23/23 merged away).
        // Expand the transports and keep the SMS side, exactly like the SMS
        // chip of the source rail already does, so rows carry the real
        // sms:<digits> thread id for open / mark-read / send.
        chats = chats.filter(_isUnifiedInboxTransportRow);
        unifyInboxPeople = true;
        unifySource = 'sms';
      } else if (state.activeChannel === 'orphans') {
        // "Non attribuées" — conversations the server proved are visible to no
        // agent. Membership is decided server-side by running the real
        // visibility gate for every agent, so the client only intersects the
        // returned ids with the rows it already has (the admin sees them all).
        // No people-merge here: an id must stay exactly what /api/inbox/assign
        // will be called with.
        const orphanRows = state.orphans?.reasons;
        chats = orphanRows ? chats.filter(c => orphanRows.has(c.id)) : [];
        // Channel sub-filter. The channel comes from the SERVER row, not from
        // a client-side id sniff, so it can never disagree with the counts.
        const orphanChannelFilter = state.orphans?.channelFilter || 'all';
        if (orphanRows && orphanChannelFilter !== 'all') {
          chats = chats.filter(c => (orphanRows.get(c.id)?.channel || 'wa') === orphanChannelFilter);
        }
        // Exactly what the admin can act on right now — the bulk action reads
        // this, so it can never assign a row that is filtered out or unloaded.
        state.orphans.visibleIds = chats.map(c => c.id);
        _paintOrphanBulk();
      } else if (state.activeChannel === 'recruitment') {
        // Recrutement tab — conversations whose number is in the recruitment
        // lane. The flag is stamped server-side and ONLY for observer roles
        // (admin / super_admin / super_sales), so for anyone else this list
        // is empty — and the tab itself is role-hidden in CSS anyway. Lane
        // chats also still appear in the WhatsApp tab (unchanged behavior);
        // this tab is purely an additive focused view.
        chats = chats.filter(c => c.recruitmentLane === true);
      } else if (state.activeChannel === 'influencer') {
        // Influencer tab — influencers / affiliate partners. Flag stamped
        // server-side for admin roles only (same gate as Recrutement); the
        // chats also stay in their WhatsApp / Social tab.
        chats = chats.filter(c => c.influencerLane === true);
      } else {
        // Default ONE inbox — our own WhatsApp/SMS threads plus the Support2/App
        // conversations assigned to this agent. They are grouped into people
        // immediately below, before any row is rendered or counted.
        chats = chats.filter(_isUnifiedInboxTransportRow);
        unifyInboxPeople = true;
      }
    }
    // meta_review hard floor — force the rendered list to FB/IG only, after and
    // regardless of the label/archive/channel branches above, so a tampered
    // activeChannel/activeFilter can never surface a non-FB/IG chat in the UI.
    // (The server already strips them from the payload; this is belt-and-braces.)
    if (state.me?.role === 'meta_review') {
      chats = chats.filter(c => c.channel === 'fbm' || c.channel === 'ig');
    }
    if (unifyInboxPeople) {
      const allSourceRows = _expandInboxSourceRows(chats);
      const srcFilter = unifySource || state.srcFilter;
      chats = _mergeInboxPeople(_sourceRows(allSourceRows, srcFilter), allSourceRows, srcFilter);
    }
    chats = applyClientFilter(chats);

    // When the Team filter is active, also inject every platform user as
    // a synthetic row so the agent sees the *complete* team in one list —
    // not just the ones who happen to have a WA chat with the support
    // number. Click on a synthetic row routes to Team chat (the new
    // internal-channel UI). Existing WA-routed teammates already have
    // real chat rows from the inbox enrichment pass, so we skip them
    // here to avoid duplication.
    // Only when "Team" is the sole active filter — synthetic rows have no
    // pipelineStage/assignedTo, so they'd bypass any combined stage/assignee
    // filter the user checked alongside.
    if (state.inboxFilters.types.has('team') && inboxFilterCount() === 1 && Array.isArray(state.teamUsers)) {
      const usedUserIds = new Set(chats.map(c => c.teamMember?.userId).filter(Boolean));
      for (const u of state.teamUsers) {
        if (u.id === state.me?.id) continue;
        if (usedUserIds.has(u.id)) continue;
        chats.push({
          id: 'team-user:' + u.id,
          name: userLabel(u.username, u.displayName || u.username),
          phone: u.whatsappPhone || '',
          isGroup: false,
          isSyntheticTeam: true,
          teamUserId: u.id,
          lastMessage: null,
          unread: 0
        });
      }
    }

    // "Mes notes" (2026-10-19, Ayoub): a client tagged in a support2 note may
    // have no WhatsApp thread here (students followed in s2), so the chip
    // counted 4 and listed 1. Every tagged chat gets a row; opening one starts
    // the chat like openChatForPhoneNumber and shows its notes.
    state._noteStubRows = new Map();
    if (state.inboxFilters.types.has('mynotes')) {
      const shown = new Set(chats.map(c => String(c.id)));
      const names = state._noteMentionNames || {};
      for (const jid of Object.keys(state._noteMentionPending || {})) {
        if (shown.has(jid)) continue;
        const real = state.chats.find(c => c.id === jid);
        if (real) { chats.push(real); continue; }
        if (!jid.endsWith('@s.whatsapp.net')) continue;
        const digits = jid.split('@')[0];
        const stub = { id: jid, name: names[jid] || ('+' + digits), phone: digits, isGroup: false, lastMessage: null, unread: 0, isNoteStub: true };
        state._noteStubRows.set(jid, stub);
        chats.push(stub);
      }
    }

    // Total-count pill on the filter button — kept in sync with the complete
    // result model, including synthetic teammates in the Team-only view. It
    // deliberately runs before the DOM slice below so counts never describe
    // just the mounted window.
    const countPill = document.getElementById('filterCount');
    if (countPill) {
      countPill.textContent = String(chats.length);
      countPill.classList.remove('hidden');
    }
    // Mirror the same count into the Inbox ⇄ Équipe toggles (both sidebars).
    document.querySelectorAll('[data-seg-count="inbox"]').forEach(elc => {
      elc.textContent = String(chats.length);
    });

    // Row clicks carry an operational chat id for compatibility with every
    // existing action. Keep the richer person model beside it so selection can
    // recover all backing source rows without inventing a synthetic server id.
    state.renderedPersonRows = new Map(chats
      .filter(c => c && c._personKey)
      .map(c => [String(c.id), c]));
    if (state.personChat?.key) {
      const refreshedPerson = chats.find(c => c._personKey === state.personChat.key);
      if (refreshedPerson) {
        state.personChat.rows = refreshedPerson._personRows.slice();
        state.personChat.sources = refreshedPerson._personSources.slice();
      }
    }

    // Pinned chats float to the top while keeping their relative ordering
    // determined by the server (most-recent-message-first). Stable sort —
    // a non-pinned chat with newer activity stays above the next-highest
    // non-pinned chat, but always below all pinned ones.
    chats = chats.slice().sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0));

    // Reset to the first lightweight window only when the agent changes the
    // view itself (channel/filter/archive/search). Ordinary polls and realtime
    // updates keep the current window and scroll position intact.
    const windowKey = _convRenderContextKey();
    if (_convWindowKey !== windowKey) {
      _convWindowKey = windowKey;
      _convVisibleRows = CONV_INITIAL_ROWS;
      if (E.conversationList) E.conversationList.scrollTop = 0;
    }
    _convMatchingRows = chats.length;

    if (chats.length === 0 && (!state.hubspotSearchResults || !state.hubspotSearchResults.length)) {
      clear(E.conversationList);
      const label = state.activeChannel === 'whatsapp' ? 'No conversations yet'
                  : state.activeChannel === 'groups' ? 'No groups linked'
                  : state.activeChannel === 'social' ? (
                      state.socialFilter === 'fbm' ? 'No Facebook messages yet'
                    : state.socialFilter === 'ig'  ? 'No Instagram messages yet'
                    : state.socialFilter === 'tt'  ? 'No TikTok messages yet'
                    : 'No social media messages yet')
                  : state.activeChannel === 'fbm' ? 'No Facebook messages yet'
                  : state.activeChannel === 'ig'  ? 'No Instagram messages yet'
                  : state.activeChannel === 'tt'  ? 'TikTok DM — Business Messaging API access pending'
                  : state.activeChannel === 'sms' ? 'No SMS yet'
                  : state.activeChannel === 'recruitment' ? 'No candidate conversations yet'
                  : state.activeChannel === 'influencer' ? 'No influencer conversations yet. Open a chat and click the ⭐ to add one.'
                  // Zero here is the GOOD outcome — every conversation reaches
                  // at least one agent — so say that rather than "nothing found".
                  : state.activeChannel === 'orphans' ? (state.orphans?.error
                      ? 'Impossible de charger les conversations non attribuées'
                      : 'Toutes les conversations sont visibles par au moins un agent')
                  : 'No clubs yet';
      E.conversationList.appendChild(el('div', { class: 'conv-empty' }, [
        lucideIcon('message-circle', { style: 'width:40px;height:40px;color:var(--gray-300);' }),
        el('span', { text: label })
      ]));
      refreshIcons(E.conversationList);
      return;
    }

    // Filtering, synthetic team injection and pinned ordering deliberately
    // happen before this slice. A matching/pinned chat deep in state.chats can
    // therefore still appear in the first window, and count pills continue to
    // describe the full filtered result rather than the mounted DOM subset.
    chats = chats.slice(0, _convVisibleRows);

    // Incremental DOM update — keep stable rows in place, only patch what changed.
    // The previous version blew away the whole list every render, which caused
    // visible flicker on the agents' machines when a chat was actively receiving
    // traffic (each new message triggered a full rebuild every poll).
    const empty = E.conversationList.querySelector('.conv-empty');
    if (empty) E.conversationList.removeChild(empty);
    const existingNodes = new Map(); // chatId → element
    for (const node of [...E.conversationList.querySelectorAll('.conv-item')]) {
      const id = node.getAttribute('data-chat-id');
      if (id) existingNodes.set(id, node);
    }
    // F6: up-front orphan removal — drop rows that aren't in the incoming
    // list BEFORE building, so a chunked cold render never leaves stale rows
    // from the previous filter/tab visible while the tail is still pumping.
    // The end-of-loop sweep in finishRender() stays as belt-and-braces
    // (it becomes a no-op).
    const newIds = new Set(chats.map(c => c.id));
    for (const [id, node] of existingNodes) {
      if (!newIds.has(id)) { node.remove(); existingNodes.delete(id); }
    }
    // Per-row signature: rebuild row only when its visible content actually changes.
    function rowSig(c) {
      const last = c.lastMessage || {};
      return [
        c.id, c.name, c.phone, c.profilePicUrl || '', c.unread || 0,
        last.timestamp || 0, last.type || '', last.text || '', last.fileName || '',
        c.assignedTo?.userId || '',
        c.pipelineStage || '', c.isPinned ? 'pinned' : '',
        (c._personSources || []).join(','), c._personKey || '',
        c.needsAttention ? (c.needsAttention.flaggedAt || c.needsAttention.flaggedBy || 'flagged') : '',
        (c.labels || []).map(l => l.id + ':' + l.hex).join(','),
        (state.selectedChatId === c.id || (c._personKey && c._personKey === state.personChat?.key)) ? '1' : '0'
      ].join('|');
    }
    let cursor = null; // last DOM node we placed; used as anchor for next insert
    const processRow = (c) => {
      const existing = existingNodes.get(c.id);
      const sig = rowSig(c);
      if (existing) {
        existingNodes.delete(c.id);
        let row = existing;
        if (existing._sig !== sig) {
          const fresh = buildConvItem(c);
          fresh._sig = sig;
          // Replace the wrapper too, not just its children. That removes stale
          // data-stage attributes and refreshes context-menu/click closures so
          // actions always use the newest pin/archive/attention state.
          existing.replaceWith(fresh);
          row = fresh;
        }
        // Move the row to the right position only if order changed.
        const expectedAfter = cursor ? cursor.nextSibling : E.conversationList.firstChild;
        if (row !== expectedAfter) E.conversationList.insertBefore(row, expectedAfter || null);
        cursor = row;
      } else {
        const fresh = buildConvItem(c);
        fresh._sig = sig;
        const expectedAfter = cursor ? cursor.nextSibling : E.conversationList.firstChild;
        E.conversationList.insertBefore(fresh, expectedAfter || null);
        cursor = fresh;
      }
    };
    const finishRender = () => {
      // Anything that survived in existingNodes was removed from the chat list.
      for (const orphan of existingNodes.values()) orphan.remove();

      // The sentinel is both an infinite-scroll status and a keyboard/click
      // fallback. Rebuild one tiny node per render instead of keeping thousands
      // of off-screen conversation rows mounted.
      const previousMore = E.conversationList.querySelector('.conv-list-more');
      const restoreMoreFocus = previousMore && document.activeElement === previousMore;
      previousMore?.remove();

      // Append HubSpot search results as a separate section so the agent can open
      // a fresh conversation with a known student who hasn't messaged the
      // platform yet. Cleared and rebuilt on every search change — these rows
      // aren't part of the regular inbox so we don't need the per-row diff.
      [...E.conversationList.querySelectorAll('.hs-search-section')].forEach(n => n.remove());
      if (Array.isArray(state.hubspotSearchResults) && state.hubspotSearchResults.length) {
        const section = el('div', { class: 'hs-search-section' });
        section.appendChild(el('div', {
          class: 'hs-search-header',
          style: 'padding:8px 12px;font-size:11px;font-weight:700;color:var(--gray-500);text-transform:uppercase;letter-spacing:0.05em;background:var(--gray-50);border-top:1px solid var(--gray-200);',
          text: 'HubSpot — ' + state.hubspotSearchResults.length + ' contact' + (state.hubspotSearchResults.length > 1 ? 's' : '')
        }));
        for (const c of state.hubspotSearchResults) {
          const item = buildHubspotSearchItem(c);
          section.appendChild(item);
        }
        E.conversationList.appendChild(section);
      }
      if (_convVisibleRows < _convMatchingRows) {
        const shown = Math.min(_convVisibleRows, _convMatchingRows);
        const remaining = _convMatchingRows - shown;
        const more = el('button', {
          class: 'conv-list-more',
          type: 'button',
          text: 'Show more · ' + shown + ' of ' + _convMatchingRows,
          'aria-label': 'Show ' + Math.min(CONV_LOAD_STEP, remaining) + ' more conversations',
        });
        more.addEventListener('click', _convGrowWindow);
        E.conversationList.appendChild(more);
        if (restoreMoreFocus) more.focus({ preventScroll: true });
      }
      refreshIcons(E.conversationList);
    };

    // F6: time-slice COLD renders only. Warm renders (poll diffs, socket
    // nudges, unread-clears) and agent-scale lists build few NEW rows and
    // keep the fully-synchronous path — byte-for-byte previous behaviour.
    // An un-scoped admin's cold boot builds ~2.8k rows in one go (~20s
    // main-thread freeze), so when the new-row count is large we paint the
    // above-the-fold head sync and pump the tail in token-guarded chunks.
    const newRowCount = chats.length - existingNodes.size;
    if (newRowCount <= 300) {
      for (const c of chats) processRow(c);
      finishRender();
      return;
    }
    const SYNC_HEAD = 150, CHUNK = 250;
    let i = 0;
    for (; i < Math.min(SYNC_HEAD, chats.length); i++) processRow(chats[i]);
    // Above-the-fold rows get their icons right away; the rest are covered
    // by finishRender()'s final refreshIcons pass.
    refreshIcons(E.conversationList);
    // requestAnimationFrame never fires in a hidden tab — fall back to a
    // 16ms timeout so a backgrounded admin tab still finishes the build.
    // document.hidden is re-read on every tick (visibility can flip mid-pump).
    const schedule = (fn) => (document.hidden ? setTimeout(fn, 16) : requestAnimationFrame(fn));
    const pump = () => {
      if (_token !== _convRenderToken) {
        // A newer render superseded this job mid-pump. It repaints from
        // state.chats itself, so just stop — but null the list fingerprint:
        // if the canceller didn't complete a full paint, the next poll must
        // NOT short-circuit on "nothing changed" and leave the tail unbuilt.
        state._chatsFingerprint = null;
        return;
      }
      const end = Math.min(i + CHUNK, chats.length);
      for (; i < end; i++) processRow(chats[i]);
      if (i < chats.length) schedule(pump);
      else finishRender();
    };
    schedule(pump);
  }

  function buildHubspotSearchItem(c) {
    const item = el('div', { class: 'conv-item hs-search-item', style: 'background:#fafbff;' });
    item.appendChild(el('div', {
      class: 'avatar ' + avatarColorClass(c.id),
      text: initials(c.name) || '?'
    }));
    const body = el('div', { class: 'conv-body' });
    body.appendChild(el('div', { class: 'conv-row1' }, [
      el('span', { class: 'conv-name', text: c.name || c.phone }),
      el('span', { class: 'conv-date', style: 'color:var(--gray-400);font-size:10.5px;', text: 'HubSpot' })
    ]));
    body.appendChild(el('div', { class: 'conv-row2' }, [
      el('span', { class: 'conv-preview muted', text: c.phone })
    ]));
    item.appendChild(body);
    item.addEventListener('click', () => openChatForHubspotContact(c));
    return item;
  }

  function openChatForHubspotContact(c) {
    // The server-provided functional JID is authoritative when the displayed
    // phone is masked. A raw phone is accepted only after strict validation.
    const localPhone = normalizeMoroccanDigits(c.phone);
    const functionalJid = _validatedFunctionalChatJid(c.chatJid)
      || (localPhone ? localPhone + '@s.whatsapp.net' : '');
    const phone = functionalJid ? functionalJid.split('@')[0] : '';
    if (!phone) { toast('Numéro invalide', 'error'); return; }
    const chatId = functionalJid;
    // Synthesize a chat row in state.chats so selectConversation works without
    // needing the inbox to round-trip first. The next poll will replace it
    // with the real entry once a message exists.
    const synthetic = {
      id: chatId,
      name: c.name,
      phone,
      isGroup: false,
      hubspotContactId: c.id,
      lastMessage: null,
      unread: 0
    };
    const idx = state.chats.findIndex(x => x.id === chatId);
    if (idx >= 0) state.chats[idx] = synthetic; else state.chats.unshift(synthetic);
    state.searchQ = '';
    if (E.searchInput) E.searchInput.value = '';
    state.hubspotSearchResults = [];
    selectConversation(chatId);
  }

  // ---- Global people-search (GET /api/search/global) ----
  // Renders merged results grouped by source. Each row → opens the chat by
  // phone (HubSpot-linked when we have a contact id).
  function renderGlobalSearch(results) {
    // F6: cancel any in-flight chunked list render — same reason as
    // renderEmptyChannelState: this wipes the list container and a stale
    // pump tick would inject inbox rows into the search results panel.
    _convRenderToken++;
    clear(E.conversationList);
    if (!results || !results.length) {
      E.conversationList.appendChild(el('div', {
        class: 'conv-empty',
        style: 'padding:24px 16px;text-align:center;color:var(--gray-500);font-size:13px;',
        text: 'Aucun résultat pour « ' + (state.searchQ || '') + ' »'
      }));
      return;
    }
    // Conversations FIRST, newest-talked first (the server already ordered
    // them). Anyone we actually messaged or called lands here even when their
    // richest record is a lead — searching "nouh" must surface the Nouhaila
    // you spoke to yesterday above 40 Nouhailas nobody ever contacted.
    const talked = results.filter(r => Number(r.lastTs || 0) > 0);
    const rest = results.filter(r => !(Number(r.lastTs || 0) > 0));
    const groups = [
      { label: 'Conversations', rows: talked },
      { label: 'Leads', rows: rest.filter(r => r.source === 'lead') },
      { label: 'Contacts', rows: rest.filter(r => r.source === 'contact') },
      { label: 'Appels', rows: rest.filter(r => r.source === 'call' || r.source === 'chat') },
    ];
    for (const g of groups) {
      if (!g.rows.length) continue;
      const section = el('div', { class: 'gsearch-section' });
      section.appendChild(el('div', { class: 'gsearch-header', text: g.label + ' — ' + g.rows.length }));
      for (const r of g.rows) section.appendChild(buildGlobalSearchItem(r));
      E.conversationList.appendChild(section);
    }
    if (results.length >= 200) {
      E.conversationList.appendChild(el('div', {
        class: 'gsearch-more',
        text: '200 premiers résultats — précisez la recherche pour en voir moins'
      }));
    }
    refreshIcons(E.conversationList);
  }

  // Wraps every occurrence of the query in <mark> so a row shows WHY it
  // matched (a name fragment, or a word said inside the thread). Plain
  // case-insensitive indexOf: an accent mismatch just renders unhighlighted
  // text rather than risking an offset drift from a normalized haystack.
  function _gsearchHighlight(text, q) {
    const span = el('span');
    const s = String(text || '');
    const needle = String(q || '').trim().toLowerCase();
    if (!s) return span;
    if (!needle) { span.appendChild(document.createTextNode(s)); return span; }
    const hay = s.toLowerCase();
    let i = 0;
    let at = hay.indexOf(needle);
    while (at !== -1) {
      if (at > i) span.appendChild(document.createTextNode(s.slice(i, at)));
      span.appendChild(el('mark', { class: 'gsearch-hl', text: s.slice(at, at + needle.length) }));
      i = at + needle.length;
      at = hay.indexOf(needle, i);
    }
    if (i < s.length) span.appendChild(document.createTextNode(s.slice(i)));
    return span;
  }

  function buildGlobalSearchItem(r) {
    const item = el('div', { class: 'conv-item gsearch-item' });
    item.appendChild(el('div', {
      class: 'avatar ' + avatarColorClass(r.chatId || r.phone || r.name || ''),
      text: initials(r.name) || '?'
    }));
    const q = state.searchQ || '';
    const body = el('div', { class: 'conv-body' });
    const name = el('span', { class: 'conv-name' });
    name.appendChild(_gsearchHighlight(r.name || r.phone || '—', q));
    body.appendChild(el('div', { class: 'conv-row1' }, [
      name,
      el('span', {
        class: 'conv-date',
        style: 'color:var(--gray-400);font-size:10.5px;',
        text: Number(r.lastTs || 0) > 0 ? formatChatDate(r.lastTs) : ''
      })
    ]));
    // The matched message wins the preview slot — that IS the answer when the
    // search term was something said in the thread. Otherwise the last message,
    // otherwise the number.
    const line = r.snippet || r.preview || '';
    const preview = el('span', { class: 'conv-preview' + (r.snippet ? '' : ' muted') });
    if (line) preview.appendChild(_gsearchHighlight(line, q));
    else preview.appendChild(document.createTextNode(r.phone || ''));
    body.appendChild(el('div', { class: 'conv-row2' }, [preview]));
    const meta = [r.context || '', line && r.phone ? r.phone : ''].filter(Boolean).join(' · ');
    if (meta) body.appendChild(el('div', { class: 'gsearch-meta', text: meta }));
    item.appendChild(body);
    item.addEventListener('click', () => _openGlobalResult(r));
    return item;
  }

  function _openGlobalResult(r) {
    // A client who hides their number behind a WhatsApp username has no
    // number at all: open their own chat by its id (wa-user-id.js).
    if (r.chatId && _isWaUsernameChatId(r.chatId)) {
      state.searchQ = '';
      if (E.searchInput) E.searchInput.value = '';
      _convExitGlobalSearch();
      showView('inbox');
      if (!state.chats.some(c => c.id === r.chatId)) {
        state.chats.unshift({ id: r.chatId, name: r.name || '', phone: '', isGroup: false, lastMessage: null, unread: 0 });
      }
      setTimeout(() => selectConversation(r.chatId), 80);
      return;
    }
    // r.phone is masked for agents — the server ships the raw chatId for
    // exactly this: prefer its digits.
    let phone = normalizeMoroccanDigits(r.phone || '');
    if (!phone && r.chatId && String(r.chatId).endsWith('@s.whatsapp.net')) {
      phone = String(r.chatId).split('@')[0].replace(/\D/g, '');
    }
    state.searchQ = '';
    if (E.searchInput) E.searchInput.value = '';
    _convExitGlobalSearch();
    if (r.hubspotContactId && phone) {
      openChatForHubspotContact({ id: r.hubspotContactId, name: r.name, phone });
      return;
    }
    if (phone) { openChatForPhoneNumber(phone); return; }
    toast('Numéro indisponible', 'error');
  }

  // A WhatsApp contact card the customer shared in the thread. Renders name +
  // phone; clicking opens a NEW conversation with that number (and creates the
  // lead owned by the agent who reached out — POST /api/leads sets agent_id to
  // the caller). Replaces the old "[contact — failed to download]" placeholder.
  function _sharedContactCard(c) {
    const name = c.name || (c.phone ? ('+' + c.phone) : 'Contact');
    const card = el('div', { class: 'shared-contact-card', title: c.phone ? 'Ouvrir une conversation avec ce contact' : '' }, [
      el('div', { class: 'scc-avatar' }, [lucideIcon('user')]),
      el('div', { class: 'scc-body' }, [
        el('div', { class: 'scc-name', text: name }),
        c.phone ? el('div', { class: 'scc-phone', text: '+' + c.phone }) : null,
      ].filter(Boolean)),
      c.phone ? el('div', { class: 'scc-open' }, [lucideIcon('message-circle')]) : null,
    ].filter(Boolean));
    if (c.phone) card.addEventListener('click', () => openChatForSharedContact(c));
    return card;
  }
  function openChatForSharedContact(c) {
    const phone = _phoneForWhatsapp(c.phone);
    if (!phone) { toast('Numéro invalide', 'error'); return; }
    const chatId = phone + '@s.whatsapp.net';
    // Create + own the lead for the agent reaching out (agent_id defaults to the
    // caller server-side), then open the conversation.
    api('/api/leads', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, name: c.name || '', source: 'shared_contact', chat_id: chatId }),
    }).catch(() => {});
    const synthetic = { id: chatId, name: c.name || ('+' + phone), phone, isGroup: false, hubspotContactId: null, lastMessage: null, unread: 0, _leadCreationSource: 'shared_contact' };
    const idx = state.chats.findIndex(x => x.id === chatId);
    if (idx >= 0) state.chats[idx] = synthetic; else state.chats.unshift(synthetic);
    selectConversation(chatId);
  }

  // ============ Chat context menu (right-click on a conv-item) ============
  // Single floating menu reused for every chat. Click outside or scroll to
  // dismiss. Keep the option list short — currently just Pin/Unpin, but the
  // hook is ready for "Mark team-only" once Phase 1's group-toggle ships.
  let _convCtxMenu = null;
  function openChatContextMenu(chat, x, y) {
    closeChatContextMenu();
    const menu = el('div', {
      class: 'conv-ctx-menu',
      style: `position:fixed;top:${y}px;left:${x}px;z-index:9999;`
    });
    const pinLabel = chat.isPinned ? 'Unpin' : 'Pin to top';
    const pinIcon = lucideIcon('pin', { style: 'width:14px;height:14px;' });
    const pinItem = el('button', { class: 'conv-ctx-item' });
    pinItem.appendChild(pinIcon);
    pinItem.appendChild(el('span', { text: pinLabel }));
    pinItem.addEventListener('click', () => {
      closeChatContextMenu();
      togglePinChat(chat);
    });
    menu.appendChild(pinItem);

    // "Mark as needs attention" — team-shared flag. Stays unread + flagged
    // for every agent until somebody manually clears it. Useful when an
    // agent already opened the chat (so it auto-cleared) but wants to
    // signal the rest of the team that it still needs follow-up.
    const flagged = !!chat.needsAttention;
    const flagItem = el('button', { class: 'conv-ctx-item' });
    flagItem.appendChild(lucideIcon(flagged ? 'flag-off' : 'flag', { style: 'width:14px;height:14px;' }));
    flagItem.appendChild(el('span', { text: flagged ? 'Clear flag' : 'Mark as needs attention' }));
    flagItem.addEventListener('click', () => {
      closeChatContextMenu();
      toggleAttentionChat(chat);
    });
    menu.appendChild(flagItem);

    // Archive / Unarchive — per-user. Hides the chat from the main inbox
    // until the agent opens the dedicated archive view (#archiveRow).
    const archived = !!chat.isArchived;
    const archItem = el('button', { class: 'conv-ctx-item' });
    archItem.appendChild(lucideIcon(archived ? 'archive-restore' : 'archive', { style: 'width:14px;height:14px;' }));
    archItem.appendChild(el('span', { text: archived ? 'Unarchive' : 'Archive' }));
    archItem.addEventListener('click', () => {
      closeChatContextMenu();
      toggleArchiveChat(chat);
    });
    menu.appendChild(archItem);

    document.body.appendChild(menu);
    _convCtxMenu = menu;
    // Reposition if it's off-screen (e.g. right-clicked near the edge).
    const r = menu.getBoundingClientRect();
    if (r.right > window.innerWidth) menu.style.left = (window.innerWidth - r.width - 8) + 'px';
    if (r.bottom > window.innerHeight) menu.style.top = (window.innerHeight - r.height - 8) + 'px';
    setTimeout(() => {
      document.addEventListener('click', closeChatContextMenu, { once: true });
      document.addEventListener('scroll', closeChatContextMenu, { once: true, capture: true });
    }, 0);
    refreshIcons();
  }
  function closeChatContextMenu() {
    if (_convCtxMenu && _convCtxMenu.parentNode) _convCtxMenu.parentNode.removeChild(_convCtxMenu);
    _convCtxMenu = null;
  }

  // Same right-click menu reused from Team chat / Team Groups rows. Looks
  // up the chat in state.chats so Pin/Unpin labels reflect current state;
  // falls back to a stub object so the menu still works for chats that
  // haven't been loaded into the inbox list yet.
  function openChatContextMenuForChatId(chatId, x, y, fallback) {
    if (!chatId) return;
    const known = (state.chats || []).find(c => c.id === chatId);
    const chat = known || fallback || { id: chatId, isPinned: false, needsAttention: null };
    openChatContextMenu(chat, x, y);
  }
  async function toggleAttentionChat(chat) {
    const next = !chat.needsAttention;
    // Optimistic — flip locally so the team immediately sees the new state
    // (red flag dot on the row, badge count restored if cleared).
    chat.needsAttention = next ? { flaggedBy: state.me?.username || '?', flaggedAt: new Date().toISOString() } : null;
    const idx = (state.chats || []).findIndex(x => x.id === chat.id);
    if (idx >= 0) state.chats[idx].needsAttention = chat.needsAttention;
    renderConversations();
    try {
      await api('/api/inbox/chats/' + encodeURIComponent(chat.id) + '/attention', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ needsAttention: next })
      });
    } catch {
      // Roll back on failure.
      chat.needsAttention = next ? null : { flaggedBy: '?', flaggedAt: '' };
      if (idx >= 0) state.chats[idx].needsAttention = chat.needsAttention;
      renderConversations();
      toast('Échec de la mise à jour — réessayez', 'error');
    }
  }

  async function togglePinChat(chat) {
    const next = !chat.isPinned;
    // Optimistic update — flip the flag locally + re-render so the row
    // jumps to the top instantly. The server call settles in the background.
    chat.isPinned = next;
    const idx = (state.chats || []).findIndex(x => x.id === chat.id);
    if (idx >= 0) state.chats[idx].isPinned = next;
    renderConversations();
    try {
      await api('/api/inbox/pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: chat.id, pinned: next })
      });
      // Confirm visibly so the agent doesn't second-guess whether the
      // click registered (the row already jumped to the top, but a
      // success toast removes ambiguity).
      toast(next ? 'Pinned' : 'Unpinned', 'success');
    } catch {
      // Roll back on failure.
      chat.isPinned = !next;
      if (idx >= 0) state.chats[idx].isPinned = !next;
      renderConversations();
      toast('Impossible d\'épingler — vérifiez votre connexion', 'error');
    }
  }

  // Toggle between the main inbox and the archived-only view. Sticky on
  // state so the next renderConversations picks up the right slice.
  state.archiveView = false;
  function setArchiveView(on) {
    if (state.archiveView === !!on) return;
    state.archiveView = !!on;
    // Drop any active filter when entering archive view — the archived
    // list is its own slice and re-applying e.g. "Awaiting reply" on top
    // would just hide everything.
    if (state.archiveView && inboxFilterCount() > 0) {
      state.inboxFilters.types.clear();
      state.inboxFilters.assignees.clear();
      state.inboxFilters.stages.clear();
      _resetInboxDateFilter();
      syncFilterMenuChecks();
      updateFilterLabel();
    }
    document.getElementById('archiveRow')?.classList.toggle('active', state.archiveView);
    renderConversations();
  }

  function updateArchiveRow(chats) {
    const row = document.getElementById('archiveRow');
    const cnt = document.getElementById('archiveRowCount');
    if (!row || !cnt) return;
    const archivedCount = (chats || []).filter(c => c.isArchived).length;
    // Hide the row completely when nothing is archived AND we're not
    // already in archive view (so the agent can always exit if they
    // un-archive every chat from inside the view).
    const shouldShow = archivedCount > 0 || state.archiveView;
    row.classList.toggle('hidden', !shouldShow);
    cnt.textContent = String(archivedCount);
    cnt.classList.toggle('zero', archivedCount === 0);
  }

  // Per-user archive — hides the chat from the main inbox until the agent
  // toggles the archive view. Mirrors togglePinChat: optimistic flip, API
  // round-trip, roll back on failure.
  async function toggleArchiveChat(chat) {
    const next = !chat.isArchived;
    chat.isArchived = next;
    const idx = (state.chats || []).findIndex(x => x.id === chat.id);
    if (idx >= 0) state.chats[idx].isArchived = next;
    // If we just unarchived inside the archive view AND nothing else is
    // left archived, auto-exit so the agent isn't staring at an empty
    // list — drop them back into the main inbox.
    if (!next && state.archiveView) {
      const stillArchived = (state.chats || []).some(c => c.isArchived);
      if (!stillArchived) state.archiveView = false;
    }
    renderConversations();
    try {
      await api('/api/inbox/archive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: chat.id, archived: next })
      });
      toast(next ? 'Archived' : 'Unarchived', 'success');
    } catch {
      chat.isArchived = !next;
      if (idx >= 0) state.chats[idx].isArchived = !next;
      renderConversations();
      toast('Archive update failed', 'error');
    }
  }

  // ---- Social platform helpers (merged "Social media" channel, 2026-07-17) ----
  // Resolve which social platform a chat belongs to: 'fbm' | 'ig' | 'tt', or ''
  // for non-social chats (WA / SMS / groups). Trusts the channel field first,
  // falls back to the chatId prefix scheme (fbm: / fbm-comment: / ig: /
  // ig-comment: / ig-mention: / tt:) for legacy rows saved without one.
  function _socialPlatform(c) {
    const ch = c?.channel || '';
    if (ch === 'fbm' || ch === 'ig' || ch === 'tt') return ch;
    const id = c?.id || '';
    if (/^fbm(-comment)?:/.test(id)) return 'fbm';
    if (/^ig(-comment|-mention)?:/.test(id)) return 'ig';
    if (/^tt:/.test(id)) return 'tt';
    return '';
  }
  const SOCIAL_PLATFORM_META = {
    fbm: { label: 'Facebook' },
    ig:  { label: 'Instagram' },
    tt:  { label: 'TikTok' },
  };
  // Brand glyphs as inline SVG (static markup — safe for el()'s html prop).
  // lucide has no TikTok icon, so all three are inlined for visual consistency;
  // they inherit currentColor so the badge/chip CSS controls the color.
  const SOCIAL_PLATFORM_SVG = {
    fbm: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13.5 21.5v-8h2.8l.4-3.3h-3.2V8.1c0-1 .3-1.7 1.7-1.7h1.6V3.5c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.5-4 4.2v2.6H7.7v3.3h2.7v8z"/></svg>',
    ig:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4.2"/><circle cx="17.4" cy="6.6" r=".7" fill="currentColor" stroke="none"/></svg>',
    tt:  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.53.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>',
  };
  // Small colored platform badge (FB blue / IG gradient / TT black) used on
  // conv rows and the chat header. `size` ∈ 'sm' (row, 15px) | 'md' (header).
  function _socialBadge(platform, size) {
    const meta = SOCIAL_PLATFORM_META[platform];
    if (!meta) return null;
    return el('span', {
      class: 'conv-channel-badge conv-channel-' + platform + (size === 'md' ? ' conv-channel-badge-md' : ''),
      title: meta.label,
      html: SOCIAL_PLATFORM_SVG[platform]
    });
  }

  // ── Kanza's inbox ──────────────────────────────────────────────────────
  // Most people assigned to Kanza have never messaged us, so her channel is
  // backed by the 3CX queue rather than WhatsApp. Visually it stays a normal
  // Inbox: one customer per row; opening the row opens the same customer chat,
  // where the Kanza strip carries every call action and result.
  const kzIn = { people: [], counts: null, online: null, loading: false, error: null, query: '', timer: null };
  const KZ_STATE = {
    pending: { label: 'Waiting', cls: 'pending' },
    picked: { label: 'Calling now', cls: 'picked' },
    done: { label: 'Done', cls: 'done' },
    skipped: { label: 'Skipped', cls: 'skipped' },
    cancelled: { label: 'Stopped', cls: 'cancelled' },
  };
  function kzIsOpen(r) { return r.status === 'pending' || r.status === 'picked'; }
  function kzPhoneKey(value) { return String(value || '').replace(/\D/g, '').slice(-9); }
  function kzChatForRow(row) {
    const key = kzPhoneKey(row?.to_number);
    if (!key) return null;
    return (state.chats || []).find(chat => _inboxPersonPhoneKey(chat) === key) || null;
  }
  function kzRowName(row) {
    const leadName = String(row?.lead_name || '').trim();
    if (leadName) return leadName;
    // Older/manual Kanza queue rows can predate their lead link. The normal
    // Inbox still knows the same person by phone, so reuse that canonical
    // conversation name instead of showing a misleading "no name" label.
    const inboxName = String(kzChatForRow(row)?.name || '').trim();
    if (inboxName) return inboxName;
    return displayPhone(row?.to_number || '') || 'Client';
  }
  function kzCurrentRow() {
    const phone = typeof cxDialableDigits === 'function'
      ? cxDialableDigits(state.selectedChat)
      : (state.selectedChat?.phone || '');
    const key = kzPhoneKey(phone);
    return key ? (kzIn.people || []).find(r => kzPhoneKey(r.to_number) === key) || null : null;
  }
  function kzLiveCall(row) {
    if (!row) return null;
    const direct = row.call_id ? state.conv.live.get(String(row.call_id)) : null;
    if (direct) return direct;
    const key = kzPhoneKey(row.to_number);
    return [...state.conv.live.values()].find(c => c.actor !== 'human' && kzPhoneKey(_convPhoneOf(c)) === key) || null;
  }
  function kzRenderSidebarStatus() {
    const on = state.activeView === 'inbox' && state.activeChannel === 'kanza';
    const normal = document.getElementById('connStatus');
    const bar = document.getElementById('kzSidebarStatus');
    E.sidebar?.classList.toggle('is-kanza-channel', on);
    normal?.classList.toggle('hidden', on);
    bar?.classList.toggle('hidden', !on);
    document.getElementById('inboxSearch')?.classList.toggle('hidden', on);
    document.getElementById('kzSidebarSearch')?.classList.toggle('hidden', !on);
    const title = document.getElementById('inboxSidebarTitle');
    if (title) title.textContent = on ? 'Kanza Inbox' : 'Inbox';
    if (!on || !bar) return;
    const online = kzIn.online == null ? !!state.conv.status?.online : !!kzIn.online;
    const enabled = state.conv.policy?.enabled;
    const calling = Number(kzIn.counts?.calling || 0);
    const waiting = Number(kzIn.counts?.waiting || 0);
    bar.classList.toggle('is-live', calling > 0);
    bar.classList.toggle('is-on', calling === 0 && online && enabled !== false);
    bar.classList.toggle('is-off', calling === 0 && (!online || enabled === false));
    const stateEl = document.getElementById('kzSidebarState');
    // "Blocked": the controller says a line is busy but the board sees no call
    // being made, and that has lasted longer than a call can (ring + max call +
    // grace). This is what 2026-09-01 → 09-05 looked like under "is working".
    const ctl = kzIn.controller || {};
    const stuckSec = Number(ctl.oldest_live_sec || 0);
    const stuckAfter = Number(ctl.stale_deadline_sec || 0) || 355;
    const blocked = calling === 0 && online && Number(ctl.outbound_active || 0) > 0 && stuckSec > stuckAfter;
    if (blocked) { bar.classList.remove('is-on'); bar.classList.add('is-off'); }
    if (stateEl) stateEl.textContent = calling ? 'Kenza is on the line now' : (!online ? 'Kenza offline' : (blocked ? 'Kenza blocked — a call never closed (' + Math.max(1, Math.round(stuckSec / 60)) + ' min)' : (enabled === false ? 'Kenza paused' : 'Kenza is working')));
    const queue = document.getElementById('kzSidebarQueue');
    if (queue) queue.textContent = waiting ? waiting + ' waiting' : 'Nobody waiting';
  }
  async function kzPlanOne(row, action, startAt) {
    if (!row?.id) return;
    try {
      await _convApi('/api/kanza/inbox/plan', {
        method: 'POST',
        body: { ids: [String(row.id)], action, gap_seconds: 0, start_at: startAt || null },
      });
      toast(action === 'drop' ? 'Removed from Kenza' : 'Kenza will call now', 'success');
      await kzLoadInbox({ quiet: true });
    } catch (e) { toast(e.message || e, 'error'); }
  }
  function kzOpenResult(row) {
    if (!row?.call_id) return;
    showView('conversations');
    setTimeout(() => { convSetTab('history'); state.conv.hist.selectedId = row.call_id; convLoadDetail(row.call_id); }, 60);
  }
  function kzOpenLive(row) {
    showView('conversations');
    setTimeout(() => convSetTab('live'), 60);
  }
  function renderKanzaChatStrip() {
    const strip = document.getElementById('kanzaChatStrip');
    if (!strip) return;
    const row = state.activeView === 'inbox' ? kzCurrentRow() : null;
    if (!row) { strip.classList.add('hidden'); strip.replaceChildren(); return; }
    const live = kzLiveCall(row);
    const effective = live && !['ended', 'no_answer', 'failed', 'cancelled'].includes(String(live.status || '')) ? 'picked' : row.status;
    const tone = effective === 'picked' ? 'live' : (effective === 'pending' ? 'waiting' : (row.booked || row.score === 'hot' ? 'success' : 'done'));
    strip.className = 'kanza-chat-strip is-' + tone;
    const identity = el('span', { class: 'kanza-chat-avatar', 'aria-hidden': 'true' }, [lucideIcon(effective === 'picked' ? 'audio-lines' : 'headset')]);
    let title = 'Kenza is working on this client';
    let detail = '';
    if (effective === 'pending') {
      const at = new Date(row.scheduled_at || 0).getTime();
      const mins = at > Date.now() ? Math.max(1, Math.ceil((at - Date.now()) / 60000)) : 0;
      title = 'Scheduled with Kenza';
      detail = mins ? ('She calls in ' + mins + ' min · ' + kzWhen(row.scheduled_at)) : 'Their turn is up — she starts once she finishes the one before.';
    } else if (effective === 'picked') {
      title = 'Kenza is on the line now';
      detail = 'You can listen to the call or watch the transcript live.';
    } else if (row.outcome === 'no_answer') {
      title = 'Kenza called and got no answer';
      detail = 'The outcome is saved, and you can schedule the call again.';
    } else if (row.status === 'skipped' && row.reason === 'do_not_call') {
      title = 'This client is marked «Do not call»';
      detail = 'Kenza dropped the call. Remove the mark to let her call them again.';
    } else if (row.status === 'skipped' || row.status === 'cancelled') {
      title = 'Remove from Kenza list';
      detail = row.reason && !String(row.reason).startsWith('campaign:') ? row.reason : 'You can hand them back to her from here.';
    } else {
      title = row.booked ? 'Kenza booked their test' : 'Kenza finished the call';
      detail = row.summary || 'Summary and recording are ready.';
    }
    const copy = el('div', { class: 'kanza-chat-copy' }, [
      el('span', { class: 'kanza-chat-eyebrow', text: 'KENZA' }),
      el('strong', { text: title }),
      el('small', { text: detail }),
    ]);
    const tags = el('div', { class: 'kanza-chat-tags' });
    if (row.score) tags.appendChild(_convScorePill(row.score));
    if (row.booked) tags.appendChild(el('span', { class: 'kz-st done', text: 'Test booked' }));
    if (row.do_not_call) tags.appendChild(el('span', { class: 'kz-st skipped', text: 'Do not call' }));
    if (row.duration_seconds) tags.appendChild(el('span', { class: 'kz-tag is-when', text: _convFmtDur(row.duration_seconds) }));
    const actions = el('div', { class: 'kanza-chat-actions' });
    const action = (icon, label, primary, fn) => {
      const b = el('button', { class: 'calls-action' + (primary ? ' primary' : ''), type: 'button' }, [lucideIcon(icon), el('span', { text: label })]);
      b.addEventListener('click', fn); actions.appendChild(b); return b;
    };
    if (effective === 'pending') {
      action('phone-call', 'Call now', true, () => kzPlanOne(row, 'reschedule', new Date().toISOString()));
      action('x', 'Remove', false, () => kzPlanOne(row, 'drop'));
    } else if (effective === 'picked') {
      if (live?.spy_code) action('headphones', 'Listen now', true, (e) => cxDial(String(live.spy_code), 'Listen ' + kzRowName(row), e.currentTarget));
      action('radio', 'Watch live', !live?.spy_code, () => kzOpenLive(row));
    } else {
      if (row.call_id) action('play-circle', 'Listen and see the outcome', true, () => kzOpenResult(row));
      action('rotate-ccw', 'Retry in 2 min', false, () => kanzaRequestCall({ phone: row.to_number, name: kzRowName(row) }, null, { delaySeconds: 120, skipConfirm: true }));
    }
    if (row.do_not_call && row.lead_id) action('unlock', 'Allow Kenza to call', row.status === 'skipped', (e) => kzSetDoNotCall(row, false, e.currentTarget));
    strip.replaceChildren(identity, copy, tags, actions);
    strip.classList.remove('hidden');
    refreshIcons(strip);
  }
  // Lift — or put back — the standing «Do not call» mark. Kanza writes it herself
  // after a not_interested / wrong_number call and never lifts it, so this is the
  // only way back short of an SQL update.
  async function kzSetDoNotCall(row, value, btn) {
    if (!row?.lead_id) { toast('This client was not found in Leads7', 'error'); return; }
    const who = kzRowName(row);
    const ask = value
      ? 'Mark ' + who + ' «Do not call»? Kenza will not call them, and anything scheduled is cancelled.'
      : 'Remove the «Do not call» mark on ' + who + '? Kenza can call them again as normal.';
    if (!confirm(ask)) return;
    if (btn) btn.disabled = true;
    try {
      await _convApi('/api/kanza/leads/' + encodeURIComponent(row.lead_id) + '/do-not-call', { method: 'POST', body: { value: value === true } });
      toast(value ? 'Mark «Do not call»' : 'Mark removed · Kenza can call them', 'success');
      await kzLoadInbox({ quiet: true });
      renderKanzaChatStrip();
    } catch (e) { toast(e.message || e, 'error'); }
    finally { if (btn) btn.disabled = false; }
  }
  async function kzLoadInbox({ quiet = false } = {}) {
    if (!quiet) { kzIn.loading = true; renderKanzaInbox(); }
    try {
      const r = await _convApi('/api/kanza/inbox?days=14');
      kzIn.people = r.people || []; kzIn.counts = r.counts || null; kzIn.online = r.online; kzIn.controller = r.controller || null; kzIn.error = null;
    } catch (e) { kzIn.error = e.message || String(e); }
    kzIn.loading = false;
    kzTabBadge();
    kzRenderSidebarStatus();
    renderKanzaChatStrip();
    if (state.activeChannel === 'kanza') renderKanzaInbox();
  }
  function kzTabBadge() {
    const badge = document.getElementById('kzTabCount'); if (!badge) return;
    const n = (kzIn.counts?.waiting || 0) + (kzIn.counts?.calling || 0);
    badge.textContent = String(n);
    badge.classList.toggle('hidden', n === 0);
  }
  function kzEnterChannel() {
    kzRenderSidebarStatus();
    kzLoadInbox();
    clearInterval(kzIn.timer);
    kzIn.timer = setInterval(() => { if (state.activeChannel === 'kanza') kzLoadInbox({ quiet: true }); else { clearInterval(kzIn.timer); kzIn.timer = null; } }, 20000);
  }
  function kzWhen(iso) {
    if (!iso) return '';
    const d = new Date(iso); if (Number.isNaN(d.getTime())) return '';
    const today = MA.dayKey() === MA.dayKey(d);
    const hm = MA.loc(d, 't', 'fr-MA', { hour: '2-digit', minute: '2-digit' });
    return today ? hm : MA.loc(d, 'd', 'fr-MA', { day: '2-digit', month: '2-digit' }) + ' ' + hm;
  }
  function kzRowSub(r) {
    if (r.status === 'pending') {
      const late = new Date(r.scheduled_at).getTime() < Date.now();
      return late ? 'Their turn is up — waiting for Kenza to be free' : 'Scheduled for ' + kzWhen(r.scheduled_at);
    }
    if (r.status === 'picked') return 'On the line now';
    if (r.status === 'skipped' || r.status === 'cancelled') return 'Skipped: ' + (r.reason && !String(r.reason).startsWith('campaign:') ? r.reason : '—');
    if (r.outcome === 'no_answer') return 'No answer · ' + kzWhen(r.started_at || r.updated_at);
    return r.summary || (kzWhen(r.started_at || r.updated_at) + ' · ' + (r.duration_seconds ? _convFmtDur(r.duration_seconds) : ''));
  }
  function kzRowState(r) {
    const base = KZ_STATE[r.status] || KZ_STATE.pending;
    if (r.status !== 'done') return base;
    if (r.booked) return { label: 'Booked a test', cls: 'done' };
    if (r.outcome === 'no_answer') return { label: 'No answer', cls: 'skipped' };
    if (r.score === 'hot') return { label: 'HOT', cls: 'hot' };
    if (r.score === 'needs_human') return { label: 'Needs you', cls: 'picked' };
    return base;
  }
  function renderKanzaInbox() {
    _convRenderToken++;
    const list = E.conversationList; if (!list) return;
    clear(list);
    list.classList.add('kz-inbox');
    if (kzIn.loading && !kzIn.people.length) {
      list.appendChild(el('div', { class: 'conv-empty' }, [el('div', { class: 'spinner-sm' }), el('span', { text: 'Loading Kenza list…' })]));
      return;
    }
    if (kzIn.error) {
      const retry = el('button', { class: 'calls-action', type: 'button' }, [lucideIcon('refresh-cw'), el('span', { text: 'Try again' })]);
      retry.addEventListener('click', () => kzLoadInbox());
      list.appendChild(el('div', { class: 'conv-empty kz-list-message' }, [
        lucideIcon('wifi-off'),
        el('strong', { text: 'Could not load Kenza Inbox' }),
        el('span', { text: 'Try refreshing the list.' }),
        retry,
      ]));
      refreshIcons(list);
      return;
    }
    const query = String(kzIn.query || '').trim().toLocaleLowerCase();
    const people = query
      ? kzIn.people.filter(r => [kzRowName(r), r.to_number, r.summary, r.outcome, r.score]
        .some(value => String(value || '').toLocaleLowerCase().includes(query)))
      : kzIn.people;
    if (!people.length) {
      list.appendChild(el('div', { class: 'conv-empty kz-list-message' }, [
        lucideIcon('headset', { style: 'width:44px;height:44px;opacity:.35;' }),
        el('strong', { style: 'color:var(--text-secondary);font-size:14px;', text: query ? 'No client found' : 'Kenza has no client right now' }),
        el('span', { text: query ? 'Change the search term.' : 'Open any conversation and use Assign → Kanza. The client shows up here and she calls them two minutes later.' }),
      ]));
      return;
    }
    for (const r of people) list.appendChild(kzBuildRow(r));
  }
  function kzBuildRow(r) {
    const st = kzRowState(r);
    const name = kzRowName(r);
    const selectedKey = kzPhoneKey(typeof cxDialableDigits === 'function' ? cxDialableDigits(state.selectedChat) : state.selectedChat?.phone);
    const row = el('div', {
      class: 'conv-item kz-conv-item' + (selectedKey && selectedKey === kzPhoneKey(r.to_number) ? ' active' : ''),
      'data-qid': String(r.id), role: 'button', tabindex: '0',
      'aria-label': name + ' — ' + st.label,
    });
    row.appendChild(el('span', {
      class: 'avatar ' + avatarColorClass(r.to_number || String(r.id)),
      'aria-hidden': 'true', text: _kzInitial(name, r.to_number),
    }));
    const body = el('div', { class: 'conv-body' });
    const nameWrap = el('span', { class: 'conv-name-wrap' }, [
      el('span', { class: 'conv-name', dir: 'auto', text: name }),
    ]);
    const when = kzWhen(kzIsOpen(r) ? r.scheduled_at : (r.started_at || r.updated_at));
    body.appendChild(el('div', { class: 'conv-row1' }, [nameWrap, el('span', { class: 'conv-date', text: when })]));
    const phone = _campPhone(r.to_number);
    const detail = kzRowSub(r);
    const preview = [phone, detail].filter(Boolean).join(' · ');
    const previewEl = el('span', { class: 'conv-preview kz-conv-preview', title: preview });
    if (phone) previewEl.appendChild(el('span', { class: 'kz-conv-phone', dir: 'ltr', text: phone }));
    if (phone && detail) previewEl.appendChild(el('span', { class: 'kz-conv-sep', 'aria-hidden': 'true', text: '·' }));
    if (detail) previewEl.appendChild(el('span', { class: 'kz-conv-note', dir: 'auto', text: detail }));
    body.appendChild(el('div', { class: 'conv-row2' }, [
      previewEl,
      el('span', { class: 'kz-st ' + st.cls, text: st.label }),
    ]));
    row.appendChild(body);
    row.addEventListener('click', () => openInboxForPhone(r.to_number, name));
    row.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); openInboxForPhone(r.to_number, name); }
    });
    return row;
  }
  function kzInboxInit() {
    document.getElementById('kzSearchInput')?.addEventListener('input', (e) => {
      kzIn.query = e.currentTarget.value || '';
      if (state.activeChannel === 'kanza') renderKanzaInbox();
    });
    kzLoadInbox({ quiet: true });
  }

  const studentStates = (() => {
    const rules = window.LeadsStudentStates;
    let data = rules.index(), pending = null, lastAttempt = 0, timer = null, owner = null;
    const canRead = () => !!state.token && state.activeView === 'inbox' && !document.hidden && !['teacher', 'meta_review'].includes(state.me?.role);
    const resolve = c => { if (owner !== state.token) data = rules.index(); const now = Date.now(), wall = MA.at(now); return rules.resolve(c, data, PIPELINE_LABELS, now, wall.getUTCHours() * 60 + wall.getUTCMinutes(), MA.dayKey(now)); };
    function paint(item, c) {
      item.querySelectorAll('.student-state-pill').forEach(n => n.remove());
      const status = resolve(c);
      for (const [kind, info] of [['identity', status.identity], ['today', status.today]]) {
        if (!info) continue;
        const pill = el('span', { class: 'student-state-pill student-state-' + kind + ' student-tone-' + info.tone,
          title: info.title, text: info.label });
        if (info.stage) pill.dataset.stage = info.stage;
        if (kind === 'identity') item.querySelector('.conv-name')?.after(pill);
        else { const row = item.querySelector('.conv-row2'); if (row) row.insertBefore(pill, row.querySelector('.conv-assignee-pill')); }
      }
    }
    // The open chat's header carries the same student pill as its row, so a
    // student is never taken for a lead (2026-10-17).
    function paintHeader(chat) {
      const nameEl = document.getElementById('chatName');
      const row = nameEl && nameEl.parentElement;
      if (!row) return;
      row.querySelectorAll('.student-state-pill').forEach(n => n.remove());
      const info = chat ? resolve(chat).identity : null;
      // A pipeline stage is not a student state: the pipeline bar shows it.
      if (!info || info.stage) return;
      nameEl.after(el('span', { class: 'student-state-pill student-state-identity student-tone-' + info.tone,
        title: info.title, text: info.label }));
    }
    function repaint() {
      document.querySelectorAll('.conv-item[data-chat-id]').forEach(item => {
        const id = item.dataset.chatId;
        const chat = state.renderedPersonRows?.get(id) || state.chats.find(c => c.id === id);
        if (chat) paint(item, chat);
      });
      const open = state.selectedChat;
      if (open && String(open.id || '') === String(state.selectedChatId || '')) paintHeader(open);
    }
    async function refresh(force = false) {
      if (!canRead() || pending || (!force && owner === state.token && Date.now() - lastAttempt < 55000)) return;
      const token = state.token;
      if (owner !== token) { data = rules.index(); owner = token; }
      lastAttempt = Date.now();
      pending = (async () => {
        try {
          const response = await api('/api/inbox/student-states', { signal: AbortSignal.timeout(5000) });
          if (response.ok && state.token === token) data = rules.index(await response.json());
        } catch { /* Preserve identity; resolve() expires live presence automatically. */ }
        finally {
          pending = null;
          if (canRead() && state.token === token) { renderConversations(); repaint(); }
        }
      })();
      return pending;
    }
    function start() {
      if (!timer) timer = setInterval(() => { if (canRead()) { refresh(); if (state.inboxFilters.crm.size) renderConversations(); repaint(); } }, 60000);
      refresh();
    }
    document.addEventListener('visibilitychange', () => { if (canRead()) { refresh(); repaint(); } });
    return { labels: rules.labels, resolve, paint, paintHeader, repaint, start, refresh };
  })();

  function buildConvItem(c) {
    const last = c.lastMessage || null;
    const channelClass = c.isGroup
      ? (state.activeChannel === 'clubs' ? 'channel-clubs' : 'channel-groups')
      : '';
    const isActive = state.selectedChatId === c.id
      || (!!c._personKey && c._personKey === state.personChat?.key);
    const itemAttrs = {
      class: 'conv-item ' + (isActive ? 'active' : '') + ' ' + channelClass + (c.isPinned ? ' is-pinned' : '') + (c.isSyntheticTeam ? ' is-synthetic-team' : ''),
      'data-chat-id': c.id,
    };

    // data-stage drives the per-row pipeline tint — every conv-item with
    // a known stage gets a faint background of its stage's colour so
    // agents can scan the inbox at a glance ("all green = paid, all blue
    // = new"). Hover/active/pinned all have higher selector specificity
    // and continue to win over the tint.
    if (c.pipelineStage && !c.isGroup) itemAttrs['data-stage'] = c.pipelineStage;
    const item = el('div', itemAttrs);
    // Synthetic team-user rows route to Team chat tab instead of opening a
    // (non-existent) WA chat in the inbox. Skip the right-click menu on
    // them — pinning a row that doesn't have a real chatId would just
    // confuse the per-user pin store.
    if (c.isSyntheticTeam) {
      item.addEventListener('click', e => {
        e.preventDefault();
        const target = state.teamUsers?.find(u => u.id === c.teamUserId);
        if (target) {
          showView('teamchat');
          setTimeout(() => openTeamchatWith(target, null), 50);
        }
      });
    } else {
      // Right-click → context menu (pin / unpin). Single source of truth so we
      // don't need a hover-only icon that would crowd the row on mobile.
      item.addEventListener('contextmenu', e => {
        e.preventDefault();
        openChatContextMenu(c, e.clientX, e.clientY);
      });
    }
    const avatar = el('div');
    applyContactAvatar(avatar, c, 'avatar ' + avatarColorClass(c.id));
    item.appendChild(avatar);
    const body = el('div', { class: 'conv-body' });
    const nameWrap = el('span', { class: 'conv-name-wrap' });
    if (c.isPinned) {
      // Tiny 📌 prefix to the chat name so the agent can see at a glance
      // why it's at the top.
      const pin = lucideIcon('pin', { class: 'conv-pin-icon', style: 'width:12px;height:12px;color:var(--gray-500);flex-shrink:0;' });
      nameWrap.appendChild(pin);
    }
    if (c.needsAttention) {
      // 🚩 flag icon — stays until an agent right-clicks → Clear flag.
      const flag = lucideIcon('flag', { class: 'conv-flag-icon', style: 'width:12px;height:12px;color:#dc2626;flex-shrink:0;', title: 'Needs attention — flagged by ' + userLabel(c.needsAttention.flaggedBy || 'team') });
      nameWrap.appendChild(flag);
    }
    nameWrap.appendChild(el('span', { class: 'conv-name', text: c.name || c.phone || 'Unknown' }));
    // Platform badge (FB / IG / TikTok) — rendered on every social chat row,
    // not just inside the Social channel, so the origin is always one glance
    // away (e.g. in the archive or a label-filtered mixed list).
    const socialPf = _socialPlatform(c);
    if (socialPf) nameWrap.appendChild(_socialBadge(socialPf));
    // A row is a person and may own several transport threads. Show every
    // available source on that one row; never render a second contact just to
    // communicate that the same person also wrote to Support2/SMS/App.
    const personSources = Array.isArray(c._personSources) && c._personSources.length
      ? c._personSources
      : [_inboxSourceOf(c)].filter(Boolean);
    for (const source of personSources) {
      nameWrap.appendChild(el('span', {
        class: 'conv-person-source conv-person-source-' + source,
        text: _PERSON_SOURCE_LABEL[source] || source.toUpperCase(),
        title: source === 'leads7' ? 'Leads7 WhatsApp'
          : source === 'support2' ? 'Support2 WhatsApp'
          : source === 'sms' ? 'SMS (Yeastar)'
          : "Lougha app",
      }));
    }
    // (Pipeline-stage pip removed 2026-05-20 — the full-row background
    // tint set via .conv-item[data-stage] is the signal Ayoub wanted.
    // The .conv-stage-pip CSS class is kept for the banner via
    // .wib-stage-pip which still extends it.)
    if (Array.isArray(c.labels) && c.labels.length) {
      for (const lab of c.labels.slice(0, 4)) {
        const chip = el('span', {
          class: 'conv-label-chip',
          title: lab.name || '',
          style: 'background:' + (lab.hex || '#888')
        });
        nameWrap.appendChild(chip);
      }
    }
    // A flagged chat counts as unread for visual purposes — keeps the row
    // bold + the badge visible until somebody clears the flag, so the
    // team can't accidentally miss a chat that an agent flagged for
    // follow-up.
    const flaggedAttention = !!c.needsAttention;
    const hasUnread = (c.unread || 0) > 0 || flaggedAttention;
    // Right-side stack: time on top, unread badge below it. Same layout as the
    // WhatsApp app — keeps the assignee pill (rendered separately at bottom-
    // right) from overlapping the unread count.
    const rightCol = el('div', {
      class: 'conv-right' + (hasUnread ? ' has-unread' : '') + (flaggedAttention ? ' flagged-attention' : ''),
      style: 'display:flex;flex-direction:column;align-items:flex-end;gap:4px;flex-shrink:0;'
    }, [
      el('span', {
        class: 'conv-date' + (hasUnread ? ' conv-date-unread' : ''),
        text: last && last.timestamp ? formatChatDate(last.timestamp) : ''
      })
    ]);
    if (hasUnread) {
      const badgeText = flaggedAttention && (c.unread || 0) === 0 ? '!' : (c.unread > 99 ? '99+' : String(c.unread || ''));
      rightCol.appendChild(el('span', { class: 'conv-unread' + (flaggedAttention ? ' flag-badge' : ''), text: badgeText }));
    }
    const row1 = el('div', { class: 'conv-row1' }, [nameWrap, rightCol]);
    const row2 = el('div', { class: 'conv-row2' });
    const previewWrap = el('span', { class: 'conv-preview' + (hasUnread ? ' conv-preview-unread' : ''), text: previewText(last) });
    if (c.isGroup && typeof c.members === 'number') {
      previewWrap.appendChild(el('span', {
        class: 'muted', style: 'font-size:11px;', text: ' · ' + c.members + ' members'
      }));
    }
    row2.appendChild(previewWrap);
    // Assignee pill rides at the right end of row2 (after the preview).
    // Preview is flex:1 + min-width:0 so it truncates with ellipsis BEFORE
    // bumping into the pill — guarantees no text overlap regardless of
    // language direction (RTL Arabic preview was overlapping the
    // absolute-positioned pill before this).
    const assigned = state.assignments[c.id];
    if (assigned) {
      const fullLabel = userLabel(assigned.username, assigned.displayName || assigned.username) || '?';
      // Show WHO owns the chat, not a shared honorific. Display names like
      // "Mme Bouchra" / "Mme Mary" all collapsed to "Mme" with a plain
      // first-word split — useless for telling agents apart. Strip a leading
      // title first ("Mme Bouchra" → "Bouchra"); if only the title remains,
      // fall back to the (distinct) username. The colored dot still encodes
      // the agent; the tooltip keeps the full label.
      const noTitle = String(fullLabel)
        .replace(/^\s*(mme|mlle|mrs|mr|madame|monsieur|dr|sir)\b\.?\s*/i, '')
        .trim();
      const firstName = noTitle.split(/\s+/)[0] || assigned.username || '?';
      const pill = el('span', {
        class: 'conv-assignee-pill',
        title: 'Assigned to ' + fullLabel
      }, [
        el('span', { class: 'conv-assignee-dot ' + colorClassForName(assigned.username) }),
        el('span', { class: 'conv-assignee-name', text: firstName })
      ]);
      row2.appendChild(pill);
    }
    body.appendChild(row1);
    body.appendChild(row2);
    // Unassigned-conversations tab only: why this row is invisible to everyone,
    // plus a one-action assign. Read from state instead of stamping the chat
    // object so a row can never keep an orphan footer after it is rescued.
    if (state.activeChannel === 'orphans') {
      const orphanRow = state.orphans?.reasons?.get(c.id);
      if (orphanRow) body.appendChild(_buildOrphanRow(c, orphanRow));
    }
    item.appendChild(body);
    studentStates.paint(item, c);
    return item;
  }

  // Browser-style chat back history. Push happens only on cross-chat link
  // navigations (Open group from profile, Open student from schedule, etc.)
  // via navigateToChat(). Plain inbox-list clicks don't push so the back
  // button doesn't accidentally walk the user through every chat they
  // browsed today.
  state.chatHistory = state.chatHistory || [];
  let _suppressChatHistoryPush = false;

  function navigateToChat(chatId) {
    if (chatId && state.selectedChatId && chatId !== state.selectedChatId) {
      state.chatHistory.push(state.selectedChatId);
      // Cap the stack at 20 entries so a long browsing session doesn't
      // hold every chat ever visited in memory.
      if (state.chatHistory.length > 20) state.chatHistory.shift();
    }
    updateChatHistoryBodyClass();
    selectConversation(chatId);
  }
  function updateChatHistoryBodyClass() {
    document.body.classList.toggle('has-chat-history', (state.chatHistory || []).length > 0);
  }

  function selectConversation(chatId) {
    let rawChat = state.chats.find(c => c.id === chatId);
    // A "Mes notes" row for a client with no thread here becomes a real row on
    // open, the same way openChatForPhoneNumber starts a chat.
    const noteStub = !rawChat && state._noteStubRows ? state._noteStubRows.get(String(chatId)) : null;
    if (noteStub) {
      rawChat = { ...noteStub };
      delete rawChat.isNoteStub;
      state.chats.unshift(rawChat);
    }
    const renderedPerson = state.renderedPersonRows?.get(String(chatId));
    const chat = renderedPerson || rawChat;
    if (!chat) return;
    _cancelInboxReads();
    _inboxReadError(false);
    // A generated website-test URL is pinned to the exact lead that was
    // active when it was created. selectConversation also resets the shared-
    // number selector to its default lead, so invalidate any open preview
    // before that identity can change underneath it.
    if (typeof _websiteTestInvalidatePreview === 'function') {
      _websiteTestInvalidatePreview();
    }
    if (state._paymentChatReadOnlyChatId && state._paymentChatReadOnlyChatId !== chatId) {
      state._paymentChatReadOnlyChatId = null;
    }
    const paymentReadOnlyOpen = state._paymentChatReadOnlyChatId === chatId;
    if (state.selectedChatId !== chatId) {
      cancelReply();
      // Reset in-chat search when switching chats — query / matches don't
      // make sense across conversations and the highlight DOM was wiped.
      if (state.chatSearch?.active) closeChatSearch();
      // Drop any half-staged attach batch — those files were meant for
      // the previous chat. Refusing to silently send to a different
      // chat is safer than carrying the batch over.
      if (typeof cancelAttachBatch === 'function' && (state.pendingAttachments?.length || state.pendingVoiceBlob)) {
        cancelAttachBatch();
      }
      // Drop any message multi-select carried from the previous chat
      // — otherwise clicking Forward would target the OLD chat's
      // message IDs against the new chat's targets.
      if (state.selectedMessageIds?.size && typeof clearMessageSelection === 'function') {
        clearMessageSelection();
      }
    }
    if (state.selectedChatId !== chatId) _inboxPerf.beginOpen();
    const prevChatId = state.selectedChatId;
    // A 3CX dial panel belongs to the conversation it was opened from — it
    // must not linger over another lead (swat:B1).
    if (prevChatId !== chatId && typeof _cxClosePanel === 'function') _cxClosePanel();
    const previousPersonKey = state.personChat?.key || '';
    const personRows = Array.isArray(chat._personRows) && chat._personRows.length
      ? chat._personRows.slice()
      : [rawChat || chat];
    state.personChat = chat._personKey ? {
      key: chat._personKey,
      phoneKey: chat._personPhoneKey || _inboxPersonPhoneKey(chat),
      primaryId: chatId,
      rows: personRows,
      sources: (chat._personSources || []).slice(),
    } : null;
    if ((state.personChat?.key || '') !== previousPersonKey) {
      state.personMessageCache = new Map();
    }
    state.selectedChatId = chatId;
    state.selectedChat = chat;
    // New chat, new walk-through: hide the previous chat's bar immediately so
    // it never shows a stale count while this chat's notes are still loading.
    _noteJumpIdx = 0;
    renderNoteJumpBar();
    renderTrialEvidenceForChat(chatId);
    state._messagesFingerprint = ''; // force initial render for the new chat
    // A chat switch must never leave the PREVIOUS chat on screen under the new
    // header. Production 2026-09-25 19:00–21:23: with the server slow, the new
    // chat's history took seconds (or hit the 12 s read timeout) and agents
    // read — and answered — another client's conversation. Paint only what
    // belongs to THIS chat now (the same person's cached sources, if any) plus
    // a "Loading messages…" line until _loadInboxMessages lands. The ETag goes
    // too: a failed first load must never be answered by a 304 that "keeps"
    // an empty pane.
    if (prevChatId !== chatId) {
      state.messages = state.personChat ? _messagesForPerson(state.personChat) : [];
      state.messagesHasMore = false;
      state._messagesEtag = null;
      state._messagesLoadingFor = chatId;
      renderMessages({ forceBottom: true });
    }
    // Reflect the chat's pipeline stage onto the chat pane so the message
    // background gets the same faint tint as the row — visual continuity
    // when the agent opens the chat. Cleared on group chats / no stage.
    const chatPaneEl = document.getElementById('chatPane');
    if (chatPaneEl) {
      if (chat?.pipelineStage && !chat.isGroup) chatPaneEl.dataset.stage = chat.pipelineStage;
      else delete chatPaneEl.dataset.stage;
    }
    // CRITICAL: clear the compose textarea on every chat switch. Production
    // bug 2026-05-19 — agent picked a template in chat A, switched to
    // chat B, the templated body (with chat A's name substituted) stayed
    // in the input and got sent to chat B addressed to the wrong person.
    // Drafts-per-chat is a separate feature; safety wins here.
    if (prevChatId && prevChatId !== chatId && E.composeInput && E.composeInput.value) {
      E.composeInput.value = '';
      if (typeof autoSize === 'function') autoSize();
      if (typeof toggleSendBtn === 'function') toggleSendBtn();
    }
    // Leave note mode on every chat switch — same safety rule as the draft
    // above: mode carried across chats is how a note ends up in the wrong
    // place (or, worse, how an agent thinks they're noting and is messaging).
    if (prevChatId && prevChatId !== chatId && state.composeNoteMode) _setComposeNoteMode(false);
    // Reset reply target too — replying to a message in chat A makes no
    // sense in chat B and would land as a plain message anyway.
    if (state.replyingTo) {
      state.replyingTo = null;
      if (typeof renderReplyPreview === 'function') renderReplyPreview();
    }
    // Reset WABA-template routing when switching chats — the rendered
    // body had the previous contact's name baked in, so it would no
    // longer match the new contact's templated text.
    if (state.composeTemplate) {
      state.composeTemplate = null;
      state.composeTemplateRendered = '';
      updateTemplateBadge();
    }
    // A minted test link belongs to the chat it was created for and nowhere else.
    state.pendingTestLink = null;
    // Lazy-load WA group members so @-mentions in this chat's messages can be
    // resolved to names by buildTextWithPhones. The cache is shared with the
    // Forward modal (per-session, keyed by group JID). Safe to call even if
    // we already have the data — loadForwardGroupMembers no-ops on cache hit.
    if (chat?.isGroup && typeof loadForwardGroupMembers === 'function') {
      loadForwardGroupMembers(chatId);
    }
    document.querySelectorAll('.conv-item').forEach(node => {
      node.classList.toggle('active', node.getAttribute('data-chat-id') === chatId);
    });
    // Clear the unread badge for this agent — fire-and-forget, the next inbox
    // poll will reflect the new state. We also zero it locally so the badge
    // disappears immediately without waiting for the round-trip.
    const unreadRows = personRows.filter(row => Number(row?.unread || 0) > 0);
    if (!paymentReadOnlyOpen && unreadRows.length) {
      const prevUnread = Number(chat.unread || 0);
      chat.unread = 0;
      const unreadBefore = new Map(unreadRows.map(row => [row.id, Number(row.unread || 0)]));
      for (const row of unreadRows) {
        row.unread = 0;
        _setInboxThreadUnread(row.id, 0);
      }
      renderConversations();
      Promise.allSettled(unreadRows.map(row =>
        api('/api/inbox/chats/' + encodeURIComponent(row.id) + '/mark-read', { method: 'POST' })
          .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); })
      ))
        .then(results => {
          const failed = results
            .map((result, i) => ({ result, row: unreadRows[i] }))
            .filter(x => x.result.status === 'rejected');
          if (!failed.length) return;
          // Restore only transports whose read marker failed; successful
          // sources stay cleared. The next poll remains the final authority.
          for (const { row } of failed) {
            row.unread = unreadBefore.get(row.id) || 1;
            _setInboxThreadUnread(row.id, row.unread);
          }
          chat.unread = failed.reduce((sum, x) => sum + (unreadBefore.get(x.row.id) || 1), 0) || prevUnread;
          renderConversations();
          console.warn('[mark-read] source sync failed:', failed.map(x => x.result.reason?.message || x.result.reason));
        });
    }
    showConversation(chat);
    // Shared-number switcher (Task B1): each chat open starts at the default
    // contact (oldest lead for the phone). Clearing the per-chat active id here
    // means a previous pick never lingers when re-opening the conversation.
    if (typeof _activeLeadIdByChat !== 'undefined') delete _activeLeadIdByChat[chatId];
    // "People on this number" ring on the header avatar. Runs AFTER the reset
    // above so it reads a clean slate, and before the pipeline bar so a team
    // choice persisted server-side is adopted in the same paint.
    try { _frOnChat(chat); } catch (err) { console.warn('[people]', err?.message || err); }
    // Mr.Call pipeline bar (Sprint 4 Phase 1). Fire-and-forget — it
    // fetches the lead by phone and re-renders. Hidden for groups + on
    // 404 (no lead row yet) without blocking the rest of the chat open.
    if (typeof renderPipelineBar === 'function') {
      renderPipelineBar(chatId, { readOnly: paymentReadOnlyOpen }).catch(err => console.warn('[pipeline] render failed:', err));
    }
    // Mr.Call AI coach banner (Sprint 7+) — hide the previous chat's banner
    // immediately; refreshMrCallBanner() runs after loadMessages() populates
    // state.messages with the new chat's history.
    document.getElementById('mrCallBanner')?.classList.add('hidden');
    // Hide the 24h-window banner during the load — refreshWindow24hBanner()
    // re-shows it after the new chat's messages render if still applicable.
    // Avoids a stale banner from the previous chat briefly flashing through.
    document.getElementById('window24hBanner')?.classList.add('hidden');
    loadMessages({ force: true });
    loadBotState();
    refreshRecruitmentLaneBtn();
    refreshInfluencerLane();
    refreshNotesBadge();
    updateAssignBtnState();
    closeAssignDropdown();
    // Drop any in-flight section drag from a previous chat
    if (typeof dragSrcSection !== 'undefined') dragSrcSection = null;
    // If conv-details panel is open, refresh its content for the new chat
    if (E.main().classList.contains('with-conv-details')) {
      switchConvDetailsTab(state.convDetailsTab);
    }
    // refresh profile panel if it was open for a different chat
    if (E.main().classList.contains('with-profile')) {
      profileOpenChatId = chatId;
      loadProfileData();
      loadAiSummaryCache();
      loadAiFeedback();
    }
    if (window.matchMedia('(max-width: 768px)').matches) {
      E.main().classList.add('show-chat');
    }
  }

  async function refreshNotesBadge() {
    const chatId = state.selectedChatId;
    try {
      const r = await _inboxRead('/api/notes?chatId=' + encodeURIComponent(chatId));
      const j = await r.json();
      if (chatId !== state.selectedChatId) return;
      const notes = j.notes || [];
      updateNotesBadge(notes.length);
      // Inline notes (2026-08-14, Ayoub): the conversation stream renders
      // these as amber note cards at their chronological spot, so cache them
      // and repaint when they change. Guarded on the chat still being open —
      // a slow response for a chat the agent already left must not repaint
      // the wrong thread.
      if (!state._notesByChat) state._notesByChat = {};
      const before = JSON.stringify(state._notesByChat[chatId] || []);
      state._notesByChat[chatId] = notes;
      if (chatId === state.selectedChatId && JSON.stringify(notes) !== before) {
        forceMessagesRerender();
        renderMessages();
      }
      // The bar reads the same cache — repaint it whenever the notes land,
      // including the first load of a freshly opened chat.
      if (chatId === state.selectedChatId) renderNoteJumpBar();
    } catch { /* ignore */ }
  }

  // PHONE MASKING (client side). The server already masks every phone-labeled
  // field for non-sales-admin users (see server.js res.json wrapper +
  // lib/phone-mask.js); these helpers cover the display paths that derive
  // digits from a chat/JID id, which the server ships raw for functionality.
  // maskPhone is a literal copy of lib/phone.js maskPhone — keep in sync.
  function maskPhone(value) {
    const s = String(value == null ? '' : value);
    if (s.includes('*')) return s;
    let digits = s.replace(/\D/g, '');
    const intl = /^\s*(\+|00)/.test(s);
    if (/^\s*00/.test(s)) digits = digits.replace(/^00/, '');
    if (digits.length < 8 || digits.length > 15) return s;
    return (intl ? '+' : '') + digits.slice(0, 4) + '****' + digits.slice(-2);
  }
  function isSalesAdminMe() {
    const role = String(state.me?.role || '').toLowerCase();
    return role === 'admin' || role === 'super_admin' || role === 'super_sales' || !!state.me?.superAdmin;
  }
  // May THIS viewer see raw client numbers? Sales admins always; otherwise
  // the per-user canSeePhones flag (served by /api/me — full numbers without
  // any other sales-admin power, Mary's case). Gates ONLY phone display —
  // never admin UI (that stays on isSalesAdminMe).
  function canSeePhonesMe() {
    return isSalesAdminMe() || state.me?.canSeePhones === true;
  }
  // Display-side gate: mask a phone-shaped string unless the viewer may see
  // raw numbers. Use on any string ABOUT to be rendered that may carry raw
  // digits taken from chat.id / a JID / a socket payload.
  function displayPhone(value) {
    const s = String(value == null ? '' : value);
    return canSeePhonesMe() ? s : maskPhone(s);
  }

  // Format a raw E.164-ish phone for the chat header subtitle. The bare
  // digits "966574905325" reads as a number-soup; "+966 574 905 325" is
  // immediately scannable as a Saudi mobile. Tries a known-country-code
  // prefix list first (Lougha's main markets), falls back to a + plus
  // 3-digit groups if nothing matches.
  function formatPhoneForDisplay(phone) {
    // Masked values pass through untouched — digit-stripping would turn
    // '0653****32' into garbage like '+065 332'.
    if (String(phone || '').includes('*')) return String(phone);
    const digits = String(phone || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length < 7) return digits;
    const knownCC = ['212','213','216','218','220','966','967','968','971','973','974','20','33','34','39','44','49','32','31','30','41','43','46','47','48','353','351','1','7'];
    knownCC.sort((a, b) => b.length - a.length);
    let cc = '', rest = digits;
    for (const c of knownCC) {
      if (digits.startsWith(c) && digits.length - c.length >= 6) {
        cc = c; rest = digits.slice(c.length); break;
      }
    }
    const groups = (rest || digits).match(/.{1,3}/g);
    return '+' + (cc ? cc + ' ' : '') + (groups ? groups.join(' ') : '');
  }

  // Click-to-call via the office 3CX PBX. Returns the dialable digits for a
  // chat, or '' when it has no real phone — groups, and FB/IG/TT threads whose
  // numeric ids are PSIDs, not phone numbers. Channel detection mirrors
  // showConversation(). Used both to show/hide the Call buttons and to dial.
  function cxDialableDigits(chat) {
    if (!chat || chat.isGroup) return '';
    const channel = chat.channel || ((chat.id || '').endsWith('@s.whatsapp.net') ? 'wa'
                  : (chat.id || '').endsWith('@g.us') ? 'wa-group'
                  : ((chat.id || '').includes(':') ? (chat.id || '').split(':')[0] : 'wa'));
    const personSources = Array.isArray(chat._personSources) ? chat._personSources : [];
    if (channel !== 'wa' && channel !== 'sms' && !personSources.length) return '';
    // Masked chat.phone would strip to short garbage and hide the Call
    // button — dial from an authorised local backing thread, then the person
    // key, before falling back to the representative id.
    const src = String(chat.phone || '');
    const rows = chat._personRows || [];
    const localThread = rows.find(row => ['leads7', 'sms'].includes(_inboxSourceOf(row)));
    // A full remote WhatsApp id (support2/app mirror: `s2ChatId`, or the row id
    // with its `support2:` prefix stripped) carries the country code. The
    // last-nine person key does NOT — dialling it sent a masked agent's call to
    // "724403689" (a +7 number that does not exist) instead of +212 724…
    // (Mme Mariem, 2026-09-05). Only fall back to the key when nothing better
    // exists.
    const fullRemote = rows.map(row => String(row?.s2ChatId || row?.id || '').replace(/^support2:/i, '').replace(/^sms:/i, ''))
      .map(v => (v.includes('@') ? v.split('@')[0] : v).replace(/\D/g, ''))
      .find(d => d.length >= 10) || '';
    // A local thread keyed by a truncated number (the misdial above creates
    // exactly such a thread) must not win over the full remote id.
    const localDigits = String(localThread?.id || '').split('@')[0].replace(/\D/g, '');
    const fallback = (localDigits.length >= 10 ? localThread.id : '') || fullRemote || localThread?.id || chat._personPhoneKey || (chat.id || '').split('@')[0];
    const digits = String((src && !src.includes('*')) ? src : fallback).replace(/\D/g, '');
    return digits.length >= 7 ? digits : '';
  }

  // Place a 3CX call for a lead. Prefers the IN-PAGE softphone (audio inside
  // the Leads7 tab via the office WebRTC→SIP gateway) when it's connected;
  // otherwise falls back to the 3CX Web Client deep-link popup. Either way the
  // call egresses via the agent's own extension → Yeastar GSM and the PBX
  // webhook logs it to the lead timeline.
  // Build the call-card identity for a chat: full name + pipeline stage (the
  // softphone card shows these, never the phone number).
  function _cxCallMeta(chat, name) {
    const stageKey = (chat && chat.pipelineStage) || '';
    // Who owns the lead (Ayoub 2026-09-27: agent + stage + ticket + created
    // date on the 3CX call card, like the WhatsApp call card). Admin logins
    // show as "Administration N", same as everywhere else.
    const a = chat && chat.assignedTo;
    let agentName = '';
    if (a && (a.userId || a.username)) {
      const u = (state.teamUsers || []).find(x => (a.userId && x.id === a.userId) || (a.username && x.username === a.username));
      const login = a.username || (u && u.username) || '';
      agentName = userLabel(login, a.displayName || (u && u.displayName) || login);
    }
    const created = chat && chat.leadCreatedAt ? new Date(chat.leadCreatedAt) : null;
    return {
      name: name || (chat && chat.name) || '',
      stageKey,
      stageLabel: stageKey ? (PIPELINE_LABELS[stageKey] || stageKey) : '',
      agentName,
      createdLabel: created && !Number.isNaN(created.getTime())
        ? MA.loc(created, 'd', 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
        : '',
      // Caller tickets and conversation navigation — never rendered on the card.
      chatId: (chat && chat.id) || '',
    };
  }
  // Ticket stages that count as CLOSED (flagged closed by HubSpot, or labelled
  // so) — loaded once per page, reusing the Tickets view's list when present.
  // Resolves null when the list can't be loaded, so the card never guesses.
  let _sfpClosedStagesP = null;
  function _sfpClosedTicketStages() {
    if (!_sfpClosedStagesP) {
      _sfpClosedStagesP = (async () => {
        let pipes = state.ticketsPipelines;
        if (!Array.isArray(pipes) || !pipes.length) {
          const r = await api('/api/hubspot/ticket-pipelines');
          if (!r.ok) throw new Error('pipelines ' + r.status);
          const j = await r.json();
          pipes = (j && j.pipelines) || j || [];
        }
        const closed = new Set();
        for (const p of (Array.isArray(pipes) ? pipes : [])) {
          for (const s of (p.stages || [])) {
            if (s.closed || /closed|ferm/i.test(String(s.label || ''))) closed.add(String(s.id));
          }
        }
        return closed.size ? closed : null;
      })().catch(() => { _sfpClosedStagesP = null; return null; });
    }
    return _sfpClosedStagesP;
  }
  // Call-card facts that need a round-trip: the caller's HubSpot tickets.
  // → { ticketsTotal, ticketsOpen } (ticketsOpen null when the closed stages
  // are unknown), or null when the lookup failed.
  window.__sfpDetails = async function (meta) {
    const chatId = meta && meta.chatId;
    if (!chatId) return null;
    try {
      const [r, closed] = await Promise.all([
        api('/api/hubspot/tickets-full?chatId=' + encodeURIComponent(chatId)),
        _sfpClosedTicketStages(),
      ]);
      if (!r.ok) return null;
      const j = await r.json();
      const list = Array.isArray(j && j.tickets) ? j.tickets : [];
      return {
        ticketsTotal: list.length,
        ticketsOpen: closed
          ? list.filter(t => !closed.has(String((t.properties && t.properties.hs_pipeline_stage) || ''))).length
          : null,
      };
    } catch (_) { return null; }
  };
  // «Réassigner» on a 3CX call card (admins, Ayoub 2026-09-28). softphone.js
  // asks for the teammate list here; the pick blind-transfers the call to that
  // agent's line (transfer(endpoint, name) → Promise), then the lead is
  // assigned to them exactly like a WhatsApp reassign (ownership + HubSpot
  // owner + audit trail), so the next call routes to them too.
  window.__sfpCanReassign = function () { return _isSalesAdmin(); };
  // A 3CX caller with no name shows their number on the card — in full only
  // for viewers allowed raw numbers (same gate as displayPhone); softphone.js
  // masks it otherwise.
  window.__sfpCanSeePhones = function () { return canSeePhonesMe(); };
  // Open the caller represented by the clicked 3CX card, even when another
  // chat is on screen. Prefer the exact resolved JID; unknown callers use the
  // existing validated phone path, which also handles unloaded conversations.
  function _sfpConversationJid(meta, digits) {
    const exact = _validatedFunctionalChatJid(meta && meta.chatId);
    if (exact) return exact;
    const phone = _phoneForWhatsapp(digits);
    return phone ? phone + '@s.whatsapp.net' : '';
  }
  window.__sfpCanOpenConversation = function (meta, digits) {
    return !!_sfpConversationJid(meta, digits);
  };
  window.__sfpOpenConversation = async function (meta, digits) {
    const jid = _sfpConversationJid(meta, digits);
    if (!jid) return false;
    const name = String((meta && meta.name) || '').trim();
    // Numeric SIP display names must keep the app's phone visibility gate
    // when they become a virtual conversation's name.
    const displayName = name && !/^[+\d\s().-]+$/.test(name)
      ? name : displayPhone(jid.split('@')[0]);
    return openInboxForChatJid(jid, displayName);
  };
  // Never cached: each teammate's availability (Disponible / En appel / Pause
  // / Fin / Hors ligne) is read when the list opens. callerDigits = the caller
  // being handed over, so their own call never marks a teammate busy.
  async function _sfpTransferTargets(callerDigits) {
    const d = String(callerDigits || '').replace(/\D/g, '');
    const r = await api('/api/softphone/transfer-targets' + (d ? '?phone=' + encodeURIComponent(d) : ''));
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const j = await r.json();
    return Array.isArray(j && j.targets) ? j.targets : [];
  }
  let _sfpReassignSeq = 0;
  window.__sfpReassign = async function (anchorEl, meta, transfer, callerDigits) {
    if (!_isSalesAdmin() || typeof transfer !== 'function') return;
    const seq = ++_sfpReassignSeq;
    let targets;
    try { targets = await _sfpTransferTargets(callerDigits); }
    catch (_) { toast('Liste de l\'équipe indisponible — réessayez', 'error'); return; }
    // A second click, or the card gone while we asked → drop.
    if (seq !== _sfpReassignSeq || (anchorEl && !anchorEl.isConnected)) return;
    const users = targets.map(t => ({ id: t.userId, username: t.username, displayName: t.displayName, endpoint: t.endpoint, availability: t.availability || null }));
    waCall.openReassignMenu(anchorEl, users, async (u) => {
      const name = userLabel(u.username, u.displayName || u.username);
      try { await transfer(u.endpoint, name); }
      catch (err) { toast('Transfert impossible : ' + ((err && err.message) || 'erreur'), 'error'); return; }
      toast('Appel transféré à ' + name, 'success');
      if (meta && meta.chatId && u.id) await assignChatTo(u.id, meta.chatId);
    });
  };
  // «WhatsApp 0661 55 79 99» — one of our lines as a call card names it
  // (same format as softphone.js fmtLine).
  function _ourLineText(label, number) {
    let d = String(number || '').replace(/\D/g, '');
    if (d.startsWith('212') && d.length === 12) d = '0' + d.slice(3);
    const n = d.length === 10 ? d.slice(0, 4) + ' ' + d.slice(4, 6) + ' ' + d.slice(6, 8) + ' ' + d.slice(8) : d;
    return [label, n].filter(Boolean).join(' ');
  }
  // Which of OUR numbers an inbound 3CX caller dialled — from the live PBX
  // board (softphone.js polls this a few times after the ring). null = not
  // on the board yet; false = this account may not ask (stop polling).
  window.__sfpLine = async function (digits) {
    const d = String(digits || '').replace(/\D/g, '');
    if (d.length < 9) return false;
    const r = await api('/api/3cx/inbound-line?phone=' + encodeURIComponent(d));
    if (r.status === 401 || r.status === 403) return false;
    if (!r.ok) return null;
    const j = await r.json().catch(() => null);
    return (j && j.line) || null;
  };
  // Ticket cell wording — same as softphone.js ticketText, for the WhatsApp
  // cards in the call stack (softphone.js is not loaded for every agent).
  function _callTicketText(d) {
    if (!d) return '';
    if (!d.ticketsTotal) return 'Aucun';
    if (d.ticketsOpen == null) return d.ticketsTotal + ' ticket' + (d.ticketsTotal > 1 ? 's' : '');
    if (d.ticketsOpen > 0) return d.ticketsOpen + ' ouvert' + (d.ticketsOpen > 1 ? 's' : '');
    return 'Fermé' + (d.ticketsTotal > 1 ? 's (' + d.ticketsTotal + ')' : '');
  }
  // Resolve an inbound caller's digits to a lead (name + stage) from the loaded
  // inbox, so the incoming-call card shows who it is — never the number.
  window.__sfpResolve = function (digits) {
    const d9 = String(digits || '').replace(/\D/g, '').slice(-9);
    if (d9.length < 6) return null;
    // Compare against the JID digits when chat.phone is masked (agents) —
    // a masked value can never match an inbound caller's real last-9.
    const _chatD9 = (x) => {
      const p = String(x.phone || '');
      const src = (p && !p.includes('*')) ? p : String(x.id || '');
      return src.replace(/\D/g, '').slice(-9);
    };
    const c = (state.chats || []).find(x => _chatD9(x) === d9);
    return c ? _cxCallMeta(c) : null;
  };
  // Same, for a caller outside the loaded inbox page (2026-10-02): asked once
  // per call by softphone.js after the ring — /api/softphone/caller reads the
  // server's stage / owner / created caches, no DB on that path.
  window.__sfpResolveAsync = async function (digits) {
    const d = String(digits || '').replace(/\D/g, '');
    if (d.length < 9) return null;
    try {
      const r = await api('/api/softphone/caller?digits=' + encodeURIComponent(d));
      if (!r.ok) return null;
      const j = await r.json();
      if (!j || !j.found) return null;
      return _cxCallMeta({
        id: j.chatId || '',
        name: j.name || '',
        pipelineStage: j.pipelineStage || '',
        assignedTo: j.assignedTo || null,
        leadCreatedAt: j.leadCreatedAt || null,
      });
    } catch (_) { return null; }
  };

  // ── 3CX dial path (swat:B1) ──────────────────────────────────────────────
  // Softphone registration is asynchronous (config fetch → 2 lazy scripts →
  // WSS REGISTER) and drops on any network blip / laptop sleep. A click that
  // lands inside that window must WAIT — it used to fall straight through to
  // the external 3CX Web Client, which opens an EMPTY dialer (?phone= sits
  // inside the URL fragment) and leaves the agent blocked.
  //
  // The wait is VISIBLE (panel with a live counter + Cancel) and its dead end
  // stays IN-APP: Ayoub's rule (2026-07-21) is that clicking Call must never
  // open an external tab/window. There is no window.open anywhere in this
  // path — not as a fallback, not behind a button. (Embedding the 3CX web
  // client in an iframe is not an option either: institute-l.3cx.sc answers
  // with `x-frame-options: DENY` and `frame-ancestors 'none'`, and a second
  // registration of the same extension would fight our own JsSIP client for
  // inbound calls.) The in-page softphone IS the 3CX client.
  function _sfpReady() { return !!(window.Softphone && window.Softphone.isReady()); }
  function _sfpCause() {
    const st = (window.Softphone && window.Softphone.status) ? window.Softphone.status() : {};
    return st.lastCause || '';
  }
  function _sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

  // Single floating status card for the dial attempt. It sits in the
  // notification band (bottom-right, z-index 9800 like #remNotifCard), NOT in
  // the softphone card's slot (#sfp-panel, bottom:92px / z-index 99999) — an
  // inbound call must never be covered by it. It also self-dismisses: on a
  // timer, on any softphone call event, and on view / conversation changes.
  let _cxPanel = null;
  let _cxPanelTimer = null;
  function _cxOnSfpCall() { _cxClosePanel(); }
  function _cxClosePanel() {
    if (_cxPanelTimer) { clearTimeout(_cxPanelTimer); _cxPanelTimer = null; }
    try { document.removeEventListener('sfp:call', _cxOnSfpCall); } catch (_) {}
    if (_cxPanel) { try { _cxPanel.remove(); } catch (_) {} _cxPanel = null; }
  }
  function _cxOpenPanel(nodes, autoCloseMs) {
    _cxClosePanel();
    const host = el('div', { class: 'cx-dial-panel', role: 'status' });
    host.style.cssText = 'position:fixed;right:24px;bottom:24px;z-index:9800;width:300px;'
      + 'max-width:calc(100vw - 32px);'
      + 'background:var(--surface-elevated);color:var(--text);border:1px solid var(--border);'
      + 'border-radius:var(--radius-lg);box-shadow:var(--shadow-lg);padding:14px 16px 12px;'
      + 'font-size:13px;line-height:1.45;';
    nodes.forEach(n => host.appendChild(n));
    document.body.appendChild(host);
    _cxPanel = host;
    try { document.addEventListener('sfp:call', _cxOnSfpCall); } catch (_) {}
    if (autoCloseMs) _cxPanelTimer = setTimeout(_cxClosePanel, autoCloseMs);
    return host;
  }
  // Title row with a discreet × so "Fermer" doesn't have to compete for width
  // with the two real actions (three .btn labels overflow 300px at 13").
  function _cxPanelHead(text) {
    return el('div', { style: 'display:flex;align-items:flex-start;gap:8px;margin-bottom:4px;' }, [
      el('div', { text, style: 'flex:1;font-weight:650;font-size:13.5px;' }),
      el('button', {
        type: 'button', 'aria-label': 'Fermer', text: '×', onclick: _cxClosePanel,
        style: 'flex:0 0 auto;border:0;background:transparent;cursor:pointer;padding:0 2px;'
          + 'font-size:18px;line-height:1;color:var(--text-tertiary);',
      }),
    ]);
  }
  function _cxPanelNote(text) {
    return el('div', { text, style: 'color:var(--text-secondary);margin-bottom:10px;' });
  }
  function _cxPanelRow(children) {
    return el('div', { style: 'display:flex;flex-wrap:wrap;gap:8px;' }, children);
  }
  function _cxPanelBtn(label, cls, onclick, title) {
    return el('button', {
      class: 'btn ' + cls, type: 'button', text: label, title: title || null, onclick,
      style: 'white-space:nowrap;',
    });
  }

  // Disable the clicked button for the whole attempt so a second click can't
  // start a second wait (or dial a different lead behind the first one).
  function _cxLockBtn(btn) {
    if (!btn || !btn.tagName) return () => {};
    const prevOpacity = btn.style.opacity;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.style.opacity = '0.55';
    return () => {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.style.opacity = prevOpacity;
    };
  }

  // Wait for registration, kicking a real reconnect first. Shows the panel as
  // soon as the first check fails, so the agent never stares at a dead UI.
  let _cxWaitCancelled = false;
  async function _cxWaitForSoftphone(maxMs = 6000) {
    if (_sfpReady()) return 'ready';
    _cxWaitCancelled = false;
    const started = Date.now();
    const counter = el('div', { text: 'Connexion du softphone… 0 s', style: 'flex:1;font-weight:650;font-size:13.5px;' });
    _cxOpenPanel([
      el('div', { style: 'display:flex;align-items:flex-start;gap:8px;margin-bottom:4px;' }, [counter]),
      _cxPanelNote('L\'appel démarre dès que la ligne est prête.'),
      _cxPanelRow([_cxPanelBtn('Annuler', 'btn-ghost', () => { _cxWaitCancelled = true; })]),
    ]);
    // Scripts never loaded (failed lazy load) → re-run the boot; the fetch and
    // the <script> injection are both retry-safe. Capped: a hung config fetch
    // must not add unbounded silence on top of the wait.
    if (!window.Softphone) {
      try { await Promise.race([initSoftphone(), _sleep(3000)]); } catch (_) {}
    } else if (window.Softphone.reconnect) {
      try { window.Softphone.reconnect(); } catch (_) {}
    }
    while (Date.now() - started < maxMs) {
      if (_cxWaitCancelled) return 'cancelled';
      if (_sfpReady()) return 'ready';
      counter.textContent = 'Connexion du softphone… ' + Math.round((Date.now() - started) / 1000) + ' s';
      await _sleep(250);
    }
    if (_cxWaitCancelled) return 'cancelled';
    return _sfpReady() ? 'ready' : 'failed';
  }

  // Dead end: explain and let the agent retry — in-app, no external window.
  // `meta` is carried through so a retry can't pick up another lead's stage.
  // The number is shown ONLY to viewers that may see raw numbers elsewhere
  // (canSeePhonesMe, same gate as displayPhone) — the panel must not become a
  // masking bypass. Those viewers also get a Copier button so they can dial on
  // a handset while the line is down; that copy runs on its own fresh click.
  function _cxFailurePanel(digits, name, btn, meta) {
    const cause = _sfpCause();
    const nodes = [
      _cxPanelHead('Softphone non connecté'),
      _cxPanelNote(cause
        ? 'Raison : ' + cause + '. La ligne se reconnecte en arrière-plan.'
        : 'La ligne se reconnecte en arrière-plan.'),
    ];
    if (canSeePhonesMe()) {
      const shown = formatPhoneForDisplay('+' + digits);
      nodes.push(el('div', {
        style: 'display:flex;align-items:center;gap:8px;margin-bottom:10px;',
      }, [
        el('span', {
          text: shown,
          style: 'flex:1;min-width:0;user-select:text;font-variant-numeric:tabular-nums;'
            + 'font-weight:600;overflow:hidden;text-overflow:ellipsis;',
        }),
        _cxPanelBtn('Copier', 'btn-ghost', () => {
          // Fresh click → the document still has focus, so writeText resolves.
          // Only claim success once it actually did.
          try {
            navigator.clipboard.writeText('+' + digits).then(
              () => toast('Numéro copié'),
              () => toast('Copie impossible', 'error'));
          } catch (_) { toast('Copie impossible', 'error'); }
        }),
      ]));
    }
    nodes.push(_cxPanelRow([
      _cxPanelBtn('Réessayer', 'btn-primary', () => { _cxClosePanel(); cxDial(digits, name, btn, meta); }),
    ]));
    _cxOpenPanel(nodes, 20000);
  }

  // ── "Call via 3CX" line picker (2026-09-24) ──────────────────────────────
  // Ayoub's ask: before a call, the agent sees every SIM on the Yeastar with
  // its live state (free / busy — by whom, for how long) and picks the number
  // the client will see. GET /api/3cx/lines is polled every 3 s only while the
  // picker is open. The pick rides to the office gateway as X-Lougha-Line
  // (softphone.js), which forces that SIM through the 3CX "Prefix 8N" rule.
  // Callbacks are unchanged: a client calling a SIM back reaches its owner.
  const _CX_LINE_KEY = 'leads7.cxLastLine';
  let _cxLinePicker = null;
  function _cxCloseLinePicker() {
    if (!_cxLinePicker) return;
    const p = _cxLinePicker;
    _cxLinePicker = null;
    clearInterval(p.timer);
    document.removeEventListener('mousedown', p.onOutside, true);
    document.removeEventListener('keydown', p.onKey, true);
    window.removeEventListener('resize', p.onResize);
    try { p.node.remove(); } catch (_) {}
  }
  function _cxFmtLineNumber(n) {
    const d = String(n || '').replace(/\D/g, '');
    return d.length === 10 ? d.replace(/(\d{4})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4') : d;
  }
  function _cxFmtDur(sec) {
    if (!Number.isFinite(sec) || sec < 0) return '';
    const m = Math.floor(sec / 60), s = sec % 60;
    return m >= 60 ? Math.floor(m / 60) + 'h' + String(m % 60).padStart(2, '0') : m + ':' + String(s).padStart(2, '0');
  }
  // Same rule as the office gateway: 212… and 0… (not 00…) are Moroccan;
  // anything else leaves as 00… and only SIMs flagged `intl` (live.js) carry it.
  function _cxIsIntl(digits) {
    const d = String(digits || '');
    return !(d.startsWith('212') || (d.startsWith('0') && !d.startsWith('00')));
  }
  function _cxLastLine() { try { return Number(localStorage.getItem(_CX_LINE_KEY)) || null; } catch (_) { return null; } }
  function _cxRememberLine(port) { try { localStorage.setItem(_CX_LINE_KEY, String(port)); } catch (_) {} }
  function _cxOrderLines(lines) {
    const mine = String(state.sfpExt || '');
    const last = _cxLastLine();
    const rank = l => (mine && (l.exts || []).includes(mine)) ? 0 : (l.port === last ? 1 : 2);
    return lines.slice().sort((a, b) => rank(a) - rank(b) || a.port - b.port);
  }
  function _cxRenderLinePicker() {
    const p = _cxLinePicker;
    if (!p) return;
    const body = p.body;
    clear(body);
    if (p.error && !p.data) {
      body.appendChild(el('div', { class: 'cx-lp-note', text: 'Statut des lignes indisponible.' }));
      body.appendChild(el('button', {
        type: 'button', class: 'btn btn-primary cx-lp-fallback', text: 'Appeler avec ma ligne habituelle',
        onclick: () => { const d = p.digits, n = p.name, b = p.btn; _cxCloseLinePicker(); cxDial(d, n, b); },
      }));
      return;
    }
    if (!p.data) { body.appendChild(el('div', { class: 'cx-lp-note', text: 'Chargement des lignes…' })); return; }
    const intl = _cxIsIntl(p.digits);
    // The International tag only means something for a number abroad while
    // some lines can't dial it (the fixe can't, 2026-09-27) — for a Moroccan
    // number it would just repeat on every SIM row.
    const allLines = p.data.lines || [];
    const tagIntl = intl && allLines.some(l => l.intl) && allLines.some(l => !l.intl);
    const mine = String(state.sfpExt || '');
    if (!p.data.online) body.appendChild(el('div', { class: 'cx-lp-note cx-lp-warn', text: 'Statut en direct indisponible — la ligne peut être occupée.' }));
    for (const l of _cxOrderLines(p.data.lines || [])) {
      const blockedIntl = intl && !l.intl;
      const disabled = l.status === 'busy' || l.status === 'offline' || blockedIntl;
      let statusText = 'Libre';
      if (l.status === 'busy') statusText = 'Occupée' + (l.agent_name ? ' · ' + l.agent_name : '') + (l.duration_sec != null ? ' · ' + _cxFmtDur(l.duration_sec) : '');
      else if (l.status === 'offline') statusText = 'Hors ligne';
      else if (l.status === 'unknown') statusText = 'Statut inconnu';
      if (blockedIntl && l.status !== 'busy' && l.status !== 'offline') statusText = 'Pas d\'international';
      const isMine = mine && (l.exts || []).includes(mine);
      const row = el('button', {
        type: 'button', role: 'menuitem',
        class: 'cx-lp-row cx-lp-' + (blockedIntl && l.status === 'free' ? 'blocked' : l.status),
        'data-port': String(l.port),
        title: disabled ? statusText : 'Appeler depuis le ' + l.number,
        onclick: () => {
          if (disabled) return;
          const d = p.digits, n = p.name, b = p.btn, meta = { ...p.meta, line: l.port, lineNumber: l.number, lineLabel: l.label || '' };
          _cxRememberLine(l.port);
          _cxCloseLinePicker();
          cxDial(d, n, b, meta);
        },
      }, [
        el('span', { class: 'cx-lp-dot', 'aria-hidden': 'true' }),
        el('span', { class: 'cx-lp-main' }, [
          el('span', { class: 'cx-lp-num', text: _cxFmtLineNumber(l.number) }),
          el('span', { class: 'cx-lp-label' }, [
            l.label || '',
            isMine ? el('span', { class: 'cx-lp-mine', text: 'Ma ligne' }) : null,
            // Admins only (server-side): who the busy line is talking with.
            l.status === 'busy' && l.client ? el('span', {
              class: 'cx-lp-client',
              text: (l.client.direction === 'inbound' ? '← ' : '→ ') + (l.client.name || _cxFmtLineNumber(l.client.phone)),
              title: (l.client.direction === 'inbound' ? 'Appel entrant de ' : 'Appel vers ') + (l.client.name ? l.client.name + ' · ' : '') + _cxFmtLineNumber(l.client.phone),
            }) : null,
            tagIntl && l.intl ? el('span', { class: 'cx-lp-tag', text: 'International' }) : null,
          ]),
        ]),
        el('span', { class: 'cx-lp-status', text: statusText }),
      ]);
      if (disabled) { row.disabled = true; row.setAttribute('aria-disabled', 'true'); }
      body.appendChild(row);
    }
  }
  async function _cxRefreshLines() {
    const p = _cxLinePicker;
    if (!p || p.loading) return;
    p.loading = true;
    try {
      const r = await api('/api/3cx/lines');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const data = await r.json();
      if (_cxLinePicker !== p) return;
      p.data = data; p.error = null;
    } catch (e) {
      if (_cxLinePicker !== p) return;
      p.error = e;
    } finally {
      p.loading = false;
    }
    _cxRenderLinePicker();
  }
  function _cxPlaceLinePicker() {
    const p = _cxLinePicker;
    if (!p || !p.btn || !p.btn.getBoundingClientRect) return;
    const width = Math.min(340, window.innerWidth - 16);
    const rect = p.btn.getBoundingClientRect();
    p.node.style.width = width + 'px';
    p.node.style.left = Math.max(8, Math.min(window.innerWidth - width - 8, rect.right - width)) + 'px';
    p.node.style.top = Math.min(window.innerHeight - 120, rect.bottom + 6) + 'px';
  }
  function openCxLinePicker(digits, name, btn) {
    if (!digits) { toast('Aucun numéro pour ce contact', 'error'); return; }
    if (_cxLinePicker) { _cxCloseLinePicker(); return; }
    // Every "can't call" case keeps cxDial's own message (in call, Log as,
    // not provisioned) — the picker only opens when a call can really start.
    const sfpStatus = (window.Softphone && window.Softphone.status) ? window.Softphone.status() : {};
    if (sfpStatus.inCall || state.sfpImpersonated || (state._sfpBootOk && !state.sfpProvisioned) || _cxDialInFlight) {
      cxDial(digits, name, btn);
      return;
    }
    const body = el('div', { class: 'cx-lp-body' });
    const node = el('div', { class: 'cx-line-picker', role: 'menu', 'aria-label': 'Choisir la ligne' }, [
      el('div', { class: 'cx-lp-head' }, [
        el('div', { class: 'cx-lp-title', text: 'Appeler depuis…' }),
        el('div', { class: 'cx-lp-sub', text: name || '' }),
      ]),
      body,
    ]);
    document.body.appendChild(node);
    const p = {
      node, body, btn, digits, name, meta: _cxCallMeta(state.selectedChat, name),
      data: null, error: null, loading: false, timer: null,
    };
    p.onOutside = ev => { if (!node.contains(ev.target) && !(btn && btn.contains(ev.target))) _cxCloseLinePicker(); };
    p.onKey = ev => { if (ev.key === 'Escape') _cxCloseLinePicker(); };
    p.onResize = () => _cxPlaceLinePicker();
    _cxLinePicker = p;
    _cxPlaceLinePicker();
    _cxRenderLinePicker();
    document.addEventListener('mousedown', p.onOutside, true);
    document.addEventListener('keydown', p.onKey, true);
    window.addEventListener('resize', p.onResize);
    p.timer = setInterval(() => {
      if (!document.body.contains(node)) { _cxCloseLinePicker(); return; }
      _cxRefreshLines();
    }, 3000);
    _cxRefreshLines();
  }

  let _cxDialInFlight = false;
  async function cxDial(digits, name, btn, metaOverride) {
    if (!digits) { toast('Aucun numéro pour ce contact', 'error'); return; }
    // A call is already up: never run the wait (its reconnect tears the SIP
    // transport down, which kills a still-ringing outbound leg).
    const sfpStatus = (window.Softphone && window.Softphone.status) ? window.Softphone.status() : {};
    if (sfpStatus.inCall) { toast('Un appel est déjà en cours'); return; }
    // Inside a "Log as" session the softphone is deliberately off — say what
    // the admin came to learn (is this account provisioned?) instead of
    // faking the agent's error.
    if (state.sfpImpersonated) {
      if (state.sfpImpersonatedProvisioned) toast('Log as : ce compte a bien une ligne 3CX (softphone désactivé en Log as)');
      else toast('3CX n\'est pas activé pour ce compte — aucune ligne dans softphone.json', 'error');
      return;
    }
    // No gateway creds for this account → 3CX simply isn't available to them.
    // Say so in-app (the WhatsApp call button is still there); never open an
    // external 3CX window. Gated on _sfpBootOk, not merely "boot finished":
    // a failed config call must not misreport a provisioned agent.
    if (state._sfpBootOk && !state.sfpProvisioned) {
      toast('3CX n\'est pas activé pour ce compte', 'error');
      return;
    }
    if (_cxDialInFlight) { toast('Un appel est déjà en cours de connexion…'); return; }
    // Freeze the lead identity NOW: state.selectedChat can change during the
    // wait, which would put another lead's stage on the call card and in the
    // /api/3cx/softphone-event payload.
    const meta = metaOverride || _cxCallMeta(state.selectedChat, name);
    let unlock = () => {};
    try {
      _cxDialInFlight = true;
      unlock = _cxLockBtn(btn);
      // No verdict yet: either the boot is still in flight (first clicks after
      // load) or its config call failed — e.g. a 502 during a rolling deploy,
      // which must not silently demote this agent for the whole session.
      if (!state._sfpBootOk) {
        const boot = state._sfpBootDone ? _sfpBootRetry() : state._sfpBoot;
        if (boot) { try { await Promise.race([boot, _sleep(4000)]); } catch (_) {} }
      }
      if (!state.sfpProvisioned) {
        toast('3CX n\'est pas activé pour ce compte', 'error');
        return;
      }
      // Provisioned → the call MUST happen inside Leads7.
      const outcome = await _cxWaitForSoftphone();
      if (outcome === 'cancelled') { _cxClosePanel(); return; }
      if (outcome === 'ready' && window.Softphone.call(digits, meta)) { _cxClosePanel(); return; }
      _cxFailurePanel(digits, name, btn, meta);
    } finally {
      _cxDialInFlight = false;
      unlock();
    }
  }

  // Boot the in-page softphone if the server has per-agent gateway creds for
  // this user. JsSIP + softphone.js are lazy-loaded ONLY when enabled, so
  // agents without softphone access never download them. Safe no-op when
  // disabled — cxDial then uses the 3CX Web Client popup fallback.
  function loadScript(src) {
    // Idempotent: don't re-inject (which would re-execute the module) if the
    // script is already present — e.g. a second agent logging into the same tab.
    if (document.querySelector('script[data-sfp="' + src + '"]')) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src; s.async = true; s.setAttribute('data-sfp', src);
      s.onload = resolve;
      // Drop the failed node — otherwise the idempotence guard above would
      // short-circuit every retry and the softphone could never recover.
      s.onerror = () => { try { s.remove(); } catch (_) {} reject(new Error('load ' + src)); };
      document.head.appendChild(s);
    });
  }
  // Re-run the boot after a failed/unanswered config call. Kept next to
  // initSoftphone so the two stay in sync — cxDial calls it when it has no
  // verdict (state._sfpBootOk false) instead of assuming "no softphone".
  function _sfpBootRetry() {
    state._sfpBootDone = false;
    state._sfpBoot = initSoftphone().finally(() => { state._sfpBootDone = true; });
    return state._sfpBoot;
  }
  async function initSoftphone() {
    try {
      const r = await api('/api/softphone/config');
      // No verdict (502 during a rolling deploy, network drop…) — leave
      // _sfpBootOk false so the next dial retries instead of demoting a
      // softphone agent to the empty 3CX dialer for the whole session.
      if (!r.ok) return;
      const cfg = await r.json();
      // From here the server HAS answered: the verdict is trustworthy either
      // way (provisioned or not).
      state._sfpBootOk = true;
      // "Log as": the server refuses to hand out the agent's gateway creds
      // (registering here would knock the real agent off her line) but says
      // whether the account IS provisioned — cxDial reports that instead of
      // the misleading "3CX n'est pas activé".
      if (cfg && cfg.impersonated) {
        state.sfpImpersonated = true;
        state.sfpImpersonatedProvisioned = !!cfg.provisioned;
        return;
      }
      if (!cfg || !cfg.enabled) return;
      // Provisioned agents call INSIDE Leads7 — cxDial reads this flag to
      // refuse the external 3CX popup for them (swat:B1).
      state.sfpProvisioned = true;
      window.__sfpToast = (m) => { try { toast(m); } catch (e) {} };
      // Forgotten-call guard (2026-10-29): the PBX's answered calls on this
      // agent's own extension, so a call left open in this tab hangs up once
      // she is on a newer one elsewhere. Any failure = no answer = no hang-up.
      window.__sfpMyCalls = () => api('/api/softphone/my-calls', { timeoutMs: 4000 })
        .then(r => (r.ok ? r.json() : null)).catch(() => null);
      // The gateway SIP user is the agent's 3CX extension — kept for the
      // softphone-event posts so the server can fuzzy-match gateway reports.
      state.sfpExt = String(cfg.ext || cfg.authUser || '');
      // Version tag busts the browser cache for the lazily-injected scripts on
      // each softphone release (a hard-refresh doesn't reliably re-fetch a
      // dynamically-added <script>). Bump SFP_VER when softphone.js changes.
      const SFP_VER = 'r21';
      // Load via a versioned PATH (not ?v=) — Cloudflare ignores query strings
      // for these JS files, so only a distinct path reliably busts its cache.
      await loadScript('/sfp/' + SFP_VER + '/jssip.min.js');
      await loadScript('/sfp/' + SFP_VER + '/softphone.js');
      if (window.Softphone) window.Softphone.init(cfg);
    } catch { /* softphone stays off; popup fallback remains */ }
  }

  // ── Softphone lifecycle → server call timeline (swat:B2) ─────────────────
  // softphone.js dispatches 'sfp:call' DOM events (dial / inbound-ringing /
  // answered / ended / failed). We mint a per-call token and POST each phase
  // to /api/3cx/softphone-event so the conversation shows the call attempt
  // IMMEDIATELY — the office gateway's authoritative report merges into the
  // same row minutes later (server-side fuzzy merge). Best-effort: a failed
  // post never disturbs the call UI.
  // One entry per live softphone call, keyed by softphone.js's per-call `sid`
  // — several calls can ring / be held at once (2026-09-27). An event without
  // a sid (older cached softphone.js) uses the single '_' slot as before.
  const _sfpLive = new Map(); // sid → { token, number, direction, answered }
  function _sfpNewToken() {
    let t = '';
    try {
      const a = new Uint8Array(12);
      crypto.getRandomValues(a);
      t = Array.from(a, b => (b % 36).toString(36)).join('');
    } catch (_) {
      t = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
    }
    return ('sf' + t).slice(0, 32);
  }
  function _sfpPost(live, phase, extra) {
    if (!live) return;
    api('/api/3cx/softphone-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phase,
        token: live.token,
        number: live.number,
        direction: live.direction,
        ext: state.sfpExt || '',
        ...(extra || {}),
      }),
    }).catch(() => { /* timeline write is best-effort */ });
  }
  document.addEventListener('sfp:call', (ev) => {
    const d = (ev && ev.detail) || {};
    const digits = String(d.number || '').replace(/\D/g, '');
    const sid = String(d.sid || '_');
    if (d.phase === 'dial' || d.phase === 'inbound-ringing') {
      const live = {
        token: _sfpNewToken(),
        number: digits,
        direction: d.direction === 'inbound' ? 'inbound' : 'outbound',
        answered: false,
      };
      _sfpLive.set(sid, live);
      // Outbound dial → immediate 'ringing' stub. Inbound ringing posts
      // nothing yet: the row is created on answer, or as 'missed' below.
      if (d.phase === 'dial') _sfpPost(live, 'dial', d.line ? { line: d.line } : null);
      return;
    }
    const live = _sfpLive.get(sid);
    if (!live) return; // stray event (page reloaded mid-call)
    if (d.phase === 'answered') {
      live.answered = true;
      _sfpPost(live, 'answered');
      return;
    }
    if (d.phase === 'ended') {
      // autoEnd = the forgotten-call guard hung up (silence | other_call):
      // the server keeps it in its auto hang-up ledger (2026-10-29).
      _sfpPost(live, 'ended', { durationSec: Math.max(0, Math.floor(Number(d.durationSec) || 0)),
        ...(d.autoEnd === 'silence' || d.autoEnd === 'other_call' ? { autoEnd: d.autoEnd } : {}) });
      _sfpLive.delete(sid);
      return;
    }
    if (d.phase === 'failed') {
      // Inbound that rang here and was never answered → 'missed-soft': lands
      // as 'missed', upgradeable when the gateway reports it was answered
      // elsewhere (desk phone / another agent).
      const cause = (live.direction === 'inbound' && !live.answered)
        ? 'missed-soft'
        : String(d.cause || '');
      _sfpPost(live, 'failed', { cause });
      _sfpLive.delete(sid);
    }
  });

  function showConversation(chat) {
    E.emptyState.classList.add('hidden');
    E.convView.classList.remove('hidden');
    E.chatName.textContent = chat.name || chat.phone || 'Unknown';
    // A shared-number person name painted by _frPaintHeaderName belongs to the
    // PREVIOUS paint; the ring repaints it for this chat if it still applies.
    if (E.chatName.dataset.frPerson) {
      delete E.chatName.dataset.frPerson;
      E.chatName.removeAttribute('title');
    }
    // The previous chat's rename target must not survive into this one; the
    // family lookup for this chat repaints it (via _frPaintWho) when it lands.
    _personRenameSyncBtn();
    // Platform badge next to the name for social chats (FB / IG / TikTok) —
    // same glyphs as the conv-row badges, header-sized. textContent above
    // already wiped any badge from the previously open chat.
    {
      const pf = _socialPlatform(chat);
      if (pf) E.chatName.appendChild(_socialBadge(pf, 'md'));
    }
    studentStates.paintHeader(chat);
    // Subtitle under the chat name: members count for groups; a phone for
    // WhatsApp chats; a channel label for FB/IG/TT (since those don't
    // have phones — the chatId is a PSID / IG id / TT username, not a
    // dialable number).
    const channel = chat.channel || ((chat.id || '').endsWith('@s.whatsapp.net') ? 'wa'
                  : (chat.id || '').endsWith('@g.us') ? 'wa-group'
                  : ((chat.id || '').includes(':') ? (chat.id || '').split(':')[0] : 'wa'));
    const personSources = Array.isArray(chat._personSources) ? chat._personSources : [];
    if (chat.isGroup) {
      E.chatPhone.textContent = (chat.members || 0) + ' members';
    } else if (channel === 'fbm') {
      E.chatPhone.textContent = 'Facebook Messenger';
    } else if (channel === 'ig') {
      E.chatPhone.textContent = 'Instagram DM';
    } else if (channel === 'tt') {
      E.chatPhone.textContent = 'TikTok DM';
    } else if (personSources.length) {
      const phone = chat.phone || (chat.id || '').replace(/^support2:/, '').split('@')[0];
      const sourceTrail = personSources.map(source => _PERSON_SOURCE_LABEL[source] || source.toUpperCase()).join(' · ');
      E.chatPhone.textContent = (phone ? formatPhoneForDisplay(displayPhone(phone)) + ' · ' : '') + sourceTrail;
    } else if (channel === 'support2') {
      // Mirrored: say where it lives, and keep the number when we have one.
      const src = chat.s2Kind === 'app' ? 'App' : 'Support2';
      E.chatPhone.textContent = chat.phone
        ? (formatPhoneForDisplay(displayPhone(chat.phone)) + ' · ' + src)
        : src;
    } else {
      E.chatPhone.textContent = formatPhoneForDisplay(displayPhone(chat.phone || (chat.id || '').split('@')[0]));
    }
    // Mirrored chats now carry the full composer — text, attachments and voice
    // all leave through support2's line, so nothing is hidden here.
    if (E.attachBtn) E.attachBtn.classList.remove('hidden');
    if (E.micBtn) E.micBtn.classList.remove('hidden');
    applyContactAvatar(E.chatAvatar, chat, 'avatar avatar-md ' + avatarColorClass(chat.id));
    // Admin-only "delete contact" trash icon — only for admins, and only on 1-1
    // contacts (deleting a group's "contact" makes no sense).
    if (E.deleteContactBtn) E.deleteContactBtn.classList.toggle('hidden', state.me?.role !== 'admin' || !!chat.isGroup);
    // Block button — admin / super_sales only, 1-1 WhatsApp chats only. Blocking
    // an FB/IG/TT thread or a group makes no sense (blocklist keys on a phone).
    if (E.blockContactBtn) E.blockContactBtn.classList.toggle('hidden', !_isSalesAdmin() || !!chat.isGroup || channel !== 'wa');
    // Availability requests belong to one client conversation. Never offer the
    // action on group chats; role CSS already excludes teachers/viewers.
    if (E.availabilityRequestBtn) E.availabilityRequestBtn.classList.toggle('hidden', !!chat.isGroup);
    // Call via 3CX — only for chats with a real dialable phone (WA/SMS 1-1).
    if (E.cxCallBtn) E.cxCallBtn.classList.toggle('hidden', !cxDialableDigits(chat));
    // Kanza qualification call — sales admins, dialable 1-1 chats only.
    if (E.kanzaCallBtn) E.kanzaCallBtn.classList.toggle('hidden', !isSalesAdminMe() || !cxDialableDigits(chat) || !!chat.isGroup);
    renderKanzaChatStrip();
    // Personalized website placement test — one-to-one contacts only. The
    // click flow creates a link + preview; it never sends automatically.
    if (E.websiteTestBtn) E.websiteTestBtn.classList.toggle('hidden', !!chat.isGroup || !pipelineExtractPhone(chat));
    // The contact's placement-test recording — test reviewers, one-to-one only.
    _trecHeaderSync(chat);
    // Unified channel: show/refresh the WhatsApp<->SMS compose toggle. Reset the
    // per-chat manual override + default to the chat's own channel (the 24h
    // banner refresh may auto-switch to SMS when the WhatsApp window is closed).
    state.composeChannelUserSet = false;
    const primarySource = _inboxSourceOf(chat);
    state.composeChannel = primarySource === 'support2' ? 'support2'
      : primarySource === 'app' ? 'app'
      : primarySource === 'sms' ? 'sms'
      : 'wa';
    // Undefined, not false: the next renderMessages must repaint the picker
    // either way, because "no app thread" is also an answer that changed.
    state._composeAppAvail = undefined;
    _renderComposeChannelBtn();
    // The toggles above changed which actions this chat shows, so the strip is
    // re-measured here. The deferred pass catches the badge/lane refreshes that
    // openChat fires right after us and can reveal one more button.
    reflowChatActions();
    requestAnimationFrame(reflowChatActions);
  }

  // ============ Messages ============
  // Cheap fingerprint of the message list — used to skip re-render when the
  // 8-second poll returns the same data we already show. Without this, the
  // messages area gets blown away and rebuilt every 8 seconds, which destroys
  // <audio> elements mid-playback (the user hears voice notes cut off at exactly
  // 8 seconds).
  function messagesFingerprint(list) { return JSON.stringify(Array.isArray(list) ? list : []); }

  // Per-group sender-name lookup: { senderJid → resolved name }. Built
  // from /api/groups/:id/wa-members on chat open. Used by buildBubble so
  // the in-bubble sender label reads "ait hadi hanae" instead of the
  // pushName "بري" (or worse, the raw 14-digit LID).
  state.groupMembersById = state.groupMembersById || {};
  // Per-session cache of timeline events keyed by chatId. Populated by
  // loadChatEvents on chat open + appended live by the 'chat_event' socket
  // listener. The renderer interleaves these into the message stream as
  // centered chip rows ("Asmae a créé un ticket #12345").
  if (!state.chatEventsById) state.chatEventsById = {};
  // Per-chat fingerprint of the last events payload we processed. The
  // messages poll re-fires loadChatEvents every tick; without this, every
  // tick reset state._messagesFingerprint and forced a full 80-bubble
  // re-render + bot-corrections refetch even when nothing changed.
  if (!state._chatEventsFpById) state._chatEventsFpById = {};

  // Cheap stable hash of an events payload: count + last event identity +
  // serialized length. Events are append-only server-side (capped slice),
  // so any insertion/edit moves at least one of these.
  function chatEventsFingerprint(events) {
    const last = events.length ? events[events.length - 1] : null;
    let len = 0;
    try { len = JSON.stringify(events).length; } catch { len = -1; }
    return events.length + ':' + (last ? (last.id || '') + ':' + (last.at || 0) : '') + ':' + len;
  }

  function _inboxVisible() { return state.activeView === 'inbox' && !document.hidden; }
  function _inboxReadScope() {
    return JSON.stringify([state.token, state.ownershipEpoch, state.selectedChatId, state.personChat?.key, state._inboxReadGeneration || 0]);
  }
  const _inboxReads = new Map();
  function _cancelInboxReads() {
    state._inboxReadGeneration = (state._inboxReadGeneration || 0) + 1;
    for (const job of _inboxReads.values()) job.controller.abort();
    _inboxReads.clear();
    state._inboxMessageJob = null;
    state.messagesLoadingOlder = false;
    state._inboxNeedsRefresh = true;
  }
  async function _inboxRead(path, opts = {}) {
    if (opts.method && opts.method !== 'GET') throw new Error('Inbox reader requires GET');
    const scope = _inboxReadScope(), key = scope + path;
    if (_inboxReads.has(key)) return _inboxReads.get(key).promise;
    const controller = new AbortController(), job = { controller };
    _inboxReads.set(key, job);
    job.promise = (async () => {
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await api(path, { ...opts, signal: controller.signal });
        if (!response.ok && response.status !== 304) throw new Error('HTTP ' + response.status);
        const data = response.status === 304 ? null : await response.json();
        if (controller.signal.aborted || scope !== _inboxReadScope()) throw new Error('Obsolete Inbox read');
        return { ok: response.ok, status: response.status, headers: response.headers, json: async () => data };
      } finally {
        clearTimeout(timer);
        if (_inboxReads.get(key) === job) _inboxReads.delete(key);
      }
    })();
    return job.promise;
  }
  function _mergeInboxReadRows(incoming, current, baseline, hasMore = false) {
    const key = m => String(m.id), byId = new Map(current.map(m => [key(m), m]));
    const ids = new Set(incoming.map(key));
    const stamp = m => Number(m.timestamp || m.at || 0);
    const oldest = incoming.length ? Math.min(...incoming.map(stamp)) : 0;
    const next = incoming.flatMap(m => {
      const id = key(m), before = baseline.get(id), live = byId.get(id);
      if (before !== undefined && !live) return [];
      if (live && (before === undefined || JSON.stringify(live) !== before)) return [live];
      return [m];
    });
    for (const m of current) if (!ids.has(key(m)) && (!baseline.has(key(m)) || (hasMore && stamp(m) < oldest))) next.push(m);
    return next.sort((a, b) => stamp(a) - stamp(b));
  }
  // `forbidden` = a lead chat answered 403 "not assigned to you": a colleague's
  // lead (speed-to-lead chips, calls bar) — say so instead of a generic error
  // (Mariem/Asma 19–20/10). Retry stays: an assignment can change meanwhile.
  function _inboxReadError(failed, forbidden = false) {
    document.getElementById('inboxReadError')?.remove();
    if (!failed || !_inboxVisible()) return;
    const notice = el('div', { id: 'inboxReadError', class: 'inbox-read-error', role: 'status' });
    notice.appendChild(el('span', { text: forbidden
      ? 'Had conversation machi dyalek — l-messages kay-banou ghir l mmoulat l-lead.'
      : (state.messages.length ? 'Message refresh unavailable. Loaded messages are still shown.' : 'Messages unavailable.') }));
    const retry = el('button', { type: 'button', text: 'Retry messages' });
    retry.addEventListener('click', () => { loadMessages({ force: true }); });
    notice.appendChild(retry);
    E.messagesArea.parentElement.insertBefore(notice, E.messagesArea);
  }
  function _resumeInboxMessages() {
    if (!_inboxVisible()) return;
    if (state._messagesRenderPending) renderMessages();
    if (state._inboxNeedsRefresh && state.selectedChatId) {
      state._inboxNeedsRefresh = false;
      loadMessages({ force: true });
    }
  }
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) _cancelInboxReads(); else _resumeInboxMessages();
  });

  async function loadChatEvents({ force = false } = {}) {
    const chatId = state.selectedChatId;
    if (!chatId || !_inboxVisible()) return;
    const scope = _inboxReadScope();
    state._chatEventsReadAt = state._chatEventsReadAt || {};
    if (!force && Date.now() - (state._chatEventsReadAt[chatId] || 0) < 30000) return;
    const before = new Map((state.chatEventsById[chatId] || []).map(m => [String(m.id), JSON.stringify(m)]));
    try {
      const r = await _inboxRead('/api/inbox/chats/' + encodeURIComponent(chatId) + '/events');
      const j = await r.json();
      if (scope !== _inboxReadScope()) return;
      state._chatEventsReadAt[chatId] = Date.now();
      const events = _mergeInboxReadRows(Array.isArray(j.events) ? j.events : [], state.chatEventsById[chatId] || [], before);
      const fp = chatEventsFingerprint(events);
      // Same payload as the previous tick for this chat → keep the DOM (and
      // the local cache, which may hold a fresher socket-pushed event) intact.
      // First load of a chat has no stored fingerprint, so it always renders.
      if (state._chatEventsFpById[chatId] === fp) return;
      state._chatEventsFpById[chatId] = fp;
      state.chatEventsById[chatId] = events;
      // Re-render so the events appear in the open chat.
      if (state.selectedChatId === chatId) {
        forceMessagesRerender();
        try { renderMessages(); } catch { /* renderer may not be ready yet */ }
        // The filter bar only exists once a conversation has events to filter,
        // so it is (re)built from the payload we just stored.
        try { renderTimelineFilterBar(); } catch { /* bar is optional chrome */ }
      }
    } catch { /* best-effort */ }
  }

  async function ensureGroupMembersForCurrentChat() {
    const chat = state.selectedChat;
    if (!chat?.isGroup || !chat.id) { state.currentGroupMembers = null; return; }
    if (state.groupMembersById[chat.id]) {
      state.currentGroupMembers = state.groupMembersById[chat.id];
      return;
    }
    try {
      const r = await _inboxRead('/api/groups/' + encodeURIComponent(chat.id) + '/wa-members');
      const j = await r.json();
      const members = Array.isArray(j.members) ? j.members : [];
      const map = {};
      for (const m of members) {
        const display = m.name || m.phone || '';
        if (!display) continue;
        if (m.jid) {
          map[m.jid] = display;
          map[jidUserSafe(m.jid)] = display;
        }
        if (m.lid) {
          map[m.lid] = display;
          map[jidUserSafe(m.lid)] = display;
        }
      }
      state.groupMembersById[chat.id] = map;
      state.currentGroupMembers = map;
      // Re-render messages so existing bubbles pick up the resolved names.
      if (state.messages?.length) renderMessages();
    } catch { /* best-effort — fall back to pushName if it fails */ }
  }

  // One local request already returns Leads7 WA + SMS together (the server
  // unions both ids by last-nine). Keep one local representative, then add the
  // authorised Support2/App rows, so we never download that local history twice.
  function _personTimelineRows(person) {
    if (!person || !Array.isArray(person.rows)) return [];
    const rows = person.rows.filter(row => row && row.id);
    const primary = rows.find(row => String(row.id) === String(person.primaryId));
    const localRows = rows.filter(row => ['leads7', 'sms'].includes(_inboxSourceOf(row)));
    const local = primary && localRows.includes(primary)
      ? primary
      : (localRows.find(row => _inboxSourceOf(row) === 'leads7') || localRows[0]);
    const out = [];
    if (primary && !localRows.includes(primary)) out.push(primary);
    if (local) out.push(local);
    for (const row of rows) {
      if (!['support2', 'app'].includes(_inboxSourceOf(row))) continue;
      if (!out.some(existing => String(existing.id) === String(row.id))) out.push(row);
    }
    return out;
  }

  function _messagePersonSource(m, rowSource) {
    const channel = String(m?.channel || '').toLowerCase();
    const chatId = String(m?.chatId || '');
    if (rowSource === 'app' && channel === 'support2') return 'app';
    if (m?.sentVia === 'support2') return 'support2';
    if (channel === 'app' || chatId.startsWith('app:')) return 'app';
    if (channel === 'sms' || chatId.startsWith('sms:')) return 'sms';
    if (channel === 'support2') return rowSource === 'app' ? 'app' : 'support2';
    return rowSource || 'leads7';
  }

  function _tagPersonMessages(messages, row) {
    const rowSource = _inboxSourceOf(row);
    return (Array.isArray(messages) ? messages : []).map(message => {
      const ts = Number(message?.timestamp || 0);
      const source = _messagePersonSource(message, rowSource);
      return {
        ...message,
        // Some upstream Baileys payloads use epoch seconds; every Leads7
        // renderer and local row uses milliseconds.
        timestamp: ts > 0 && ts < 1e12 ? ts * 1000 : ts,
        // The remote bridge transports App rows through Support2, but the
        // conversation channel the agent sees/replies to is still App.
        channel: source === 'app' ? 'app' : message.channel,
        _personSource: source,
        _personThreadId: row?.id || message?.chatId || '',
      };
    });
  }

  function _personMessageSemanticKey(m) {
    const text = String(m?.text || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const file = String(m?.fileName || '').trim().toLowerCase();
    // Empty media rows cannot be safely matched across stores: two photos sent
    // together are legitimate distinct messages. Exact-id dedupe still applies.
    if (!text && !file) return '';
    return [m?.fromMe ? 'out' : 'in', m?.type || 'text', text, file].join('|');
  }

  // One support2 message can reach this thread three ways: its forwarded copy
  // (`s2in:<id>`), an assigned `support2:` row (raw id) and the peer feed
  // (`peer:s2:<id>`). All three dedupe on the raw support2 id.
  function _personExactKey(m) {
    // One 3CX recording = one call, whichever app listed it (same MinIO key).
    if (m.type === 'call' && m.callData?.recordingKey) return 'rec:' + m.callData.recordingKey;
    if (m.peerSrcId) return 's2id:' + m.peerSrcId;
    const id = String(m.id);
    if (id.startsWith('s2in:')) return 's2id:' + id.slice(5);
    if (m._personSource === 'support2' && !m.sentVia) return 's2id:' + id;
    return id;
  }

  function _mergePersonMessageBatches(batches) {
    const rows = (batches || []).flatMap(batch => Array.isArray(batch) ? batch : [])
      .slice().sort((a, b) => Number(a.timestamp || 0) - Number(b.timestamp || 0));
    const exact = new Set();
    const semantic = new Map();
    const out = [];
    for (const message of rows) {
      const exactKey = message.id ? _personExactKey(message) : '';
      if (exactKey && exact.has(exactKey)) continue;
      const semanticKey = _personMessageSemanticKey(message);
      const prior = semanticKey ? semantic.get(semanticKey) : null;
      const mirroredDuplicate = prior
        && prior._personSource !== message._personSource
        && Math.abs(Number(prior.timestamp || 0) - Number(message.timestamp || 0)) <= 1500;
      if (mirroredDuplicate) {
        if (exactKey) exact.add(exactKey);
        continue;
      }
      if (exactKey) exact.add(exactKey);
      if (semanticKey) semantic.set(semanticKey, message);
      out.push(message);
    }
    return out;
  }

  function _messagesForPerson(person) {
    if (!person) return state.messages || [];
    const batches = [];
    for (const row of _personTimelineRows(person)) {
      const cached = state.personMessageCache.get(String(row.id));
      if (cached?.messages) batches.push(cached.messages);
    }
    const peer = _peerFeedEntry(person)?.messages;
    if (peer?.length) batches.push(peer);
    return _mergePersonMessageBatches(batches);
  }

  // ── Leads7 ⇄ support2 conversation mirror (Ayoub 2026-09-30) ─────────────
  // Everything support2 holds for this client — its inbound, what its team or
  // Mr.Bot sent, its timeline — read by phone through our server and merged
  // into this thread, read-only. Refreshed on chat open, on a `peer:activity`
  // ping for this client, and at most every 60 s while open. A support2
  // outage only means these rows are missing; the thread itself is untouched.
  const PEER_FEED_REFRESH_MS = 60000;
  function _peerFeedTargetId(person = state.personChat) {
    const rows = person ? (person.rows || []) : [state.selectedChat].filter(Boolean);
    const local = rows.find(row => ['leads7', 'sms'].includes(_inboxSourceOf(row)));
    if (local?.id) return String(local.id);
    if (person) return '';
    const id = String(state.selectedChatId || '');
    return (id.endsWith('@s.whatsapp.net') || id.startsWith('sms:')) ? id : '';
  }
  function _peerFeedEntry(person = state.personChat) {
    const target = _peerFeedTargetId(person);
    return target ? state.peerFeed.get(target) || null : null;
  }
  function _peerEventsForOpenChat() {
    return _peerFeedEntry()?.events || [];
  }
  // More support2 history than what is loaded (the feed pages 150 at a time).
  function _peerHasMore() {
    return !!_peerFeedEntry()?.hasMore;
  }
  // Scroll-up has something to load: our older page, or support2's.
  function _threadHasMore() {
    return !!state.messagesHasMore || _peerHasMore();
  }
  // One older page of support2's rows (before the oldest one loaded), merged
  // into the entry. Resolves true when it added rows.
  async function _loadOlderPeer() {
    const target = _peerFeedTargetId();
    const entry = target ? state.peerFeed.get(target) : null;
    if (!entry || !entry.hasMore || entry.loading || !entry.messages.length) return false;
    const oldest = Math.min(...entry.messages.map(m => Number(m.timestamp) || Infinity));
    if (!Number.isFinite(oldest)) return false;
    entry.loading = true;
    try {
      const r = await _inboxRead('/api/inbox/chats/' + encodeURIComponent(target) + '/peer-feed?before=' + Math.floor(oldest));
      const j = await r.json();
      if (!j || j.ok !== true) return false;
      const older = (Array.isArray(j.messages) ? j.messages : []).map(m => ({ ...m, _personSource: 'support2' }));
      const ids = new Set(entry.messages.map(m => String(m.id)));
      entry.messages = older.filter(m => !ids.has(String(m.id))).concat(entry.messages);
      entry.hasMore = !!j.hasMore && older.length > 0;
      entry.olderLoaded = true;
      return older.length > 0;
    } catch { return false; }
    finally { entry.loading = false; }
  }
  function _peerDigits(chatId) {
    const s = String(chatId || '');
    if (s.endsWith('@s.whatsapp.net')) return s.split('@')[0].replace(/\D/g, '');
    if (s.startsWith('sms:')) return s.slice(4).replace(/\D/g, '');
    return '';
  }
  // Is a ping about the conversation on screen? Compared on the full number
  // (a +33 and a +212 client share their last 9 digits).
  function _peerPingIsOpenChat(chatId) {
    const target = _peerFeedTargetId();
    if (!target) return false;
    const a = _peerDigits(chatId), b = _peerDigits(target);
    if (!a || !b) return false;
    if (a === b) return true;
    const na = a.length === 10 && a.startsWith('0') ? '212' + a.slice(1) : a;
    const nb = b.length === 10 && b.startsWith('0') ? '212' + b.slice(1) : b;
    return na === nb;
  }
  function _repaintWithPeer() {
    const next = state.personChat
      ? _messagesForPerson(state.personChat)
      : _mergePersonMessageBatches([(state.messages || []).filter(m => !m.peer), _peerFeedEntry()?.messages || []]);
    const fp = messagesFingerprint(next);
    if (fp === state._messagesFingerprint) { renderMessages(); return; }
    state._messagesFingerprint = fp;
    state.messages = next;
    renderMessages();
  }
  async function _refreshPeerFeed({ force = false } = {}) {
    const target = _peerFeedTargetId();
    if (!target || !_inboxVisible()) return;
    const prev = state.peerFeed.get(target);
    if (prev?.loading) return;
    if (!force && prev && Date.now() - prev.at < PEER_FEED_REFRESH_MS) return;
    const scope = _inboxReadScope();
    state.peerFeed.set(target, { ...(prev || {}), messages: prev?.messages || [], events: prev?.events || [], at: Date.now(), loading: true });
    let entry = { ...(prev || {}), messages: prev?.messages || [], events: prev?.events || [], at: Date.now(), loading: false };
    try {
      const r = await _inboxRead('/api/inbox/chats/' + encodeURIComponent(target) + '/peer-feed');
      const j = await r.json();
      // Unreachable / switched off: keep what we last had rather than blinking.
      if (j && j.ok === true) {
        const page = (Array.isArray(j.messages) ? j.messages : []).map(m => ({ ...m, _personSource: 'support2' }));
        // Older pages the agent already scrolled into stay loaded.
        const pageOldest = page.length ? Math.min(...page.map(m => Number(m.timestamp) || Infinity)) : Infinity;
        const keepOlder = prev?.olderLoaded ? (prev.messages || []).filter(m => Number(m.timestamp) < pageOldest) : [];
        entry = {
          messages: keepOlder.concat(page),
          events: Array.isArray(j.events) ? j.events : [],
          at: Date.now(),
          hasMore: prev?.olderLoaded ? !!prev.hasMore : !!j.hasMore,
          olderLoaded: !!prev?.olderLoaded,
        };
      }
    } catch { /* best-effort — the local thread stays as it is */ }
    state.peerFeed.set(target, entry);
    if (scope !== _inboxReadScope() || _peerFeedTargetId() !== target) return;
    if (!entry.messages.length && !entry.events.length && !prev?.messages?.length && !prev?.events?.length) return;
    _repaintWithPeer();
  }

  function _appendOpenPersonMessage(message, targetChatId, source) {
    if (!message) return;
    const tagged = { ...message, _personSource: source || message._personSource };
    const person = state.personChat;
    if (!person) {
      state.messages.push(tagged);
    } else {
      const rows = _personTimelineRows(person);
      let cacheRow = rows.find(row => String(row.id) === String(targetChatId));
      // Synthesised WA/SMS targets share the one local server timeline. Store
      // their optimistic row under the local representative's cache key.
      if (!cacheRow && ['leads7', 'sms', 'support2', 'app'].includes(source)) {
        cacheRow = rows.find(row => ['leads7', 'sms'].includes(_inboxSourceOf(row)));
      }
      if (cacheRow) {
        const key = String(cacheRow.id);
        const cached = state.personMessageCache.get(key) || { messages: [], hasMore: false };
        state.personMessageCache.set(key, {
          ...cached,
          messages: _mergePersonMessageBatches([cached.messages, [tagged]]),
          at: Date.now(),
        });
        state.messages = _messagesForPerson(person);
      } else {
        state.messages.push(tagged);
      }
    }
    state._messagesFingerprint = messagesFingerprint(state.messages);
    renderMessages();
  }

  async function _refreshPersonOtherMessages(person, { force = false } = {}) {
    if (!person || state.personChat?.key !== person.key || !_inboxVisible()) return;
    const scope = _inboxReadScope(), now = Date.now();
    if (person.refreshing && person.refreshScope === scope) return;
    if (!force && now - Number(person.otherRefreshedAt || 0) < 20000) return;
    const others = _personTimelineRows(person).filter(row => String(row.id) !== String(person.primaryId));
    if (!others.length) return;
    person.refreshing = true; person.refreshScope = scope; person.otherRefreshedAt = now;
    try {
      await Promise.allSettled(others.map(async row => {
        const key = String(row.id), old = state.personMessageCache.get(key)?.messages || [];
        const baseline = new Map(old.map(m => [String(m.id), JSON.stringify(m)]));
        const response = await _inboxRead('/api/inbox/chats/' + encodeURIComponent(row.id) + '/messages?limit=100');
        const data = await response.json();
        if (scope !== _inboxReadScope()) return;
        const current = state.personMessageCache.get(key)?.messages || [];
        state.personMessageCache.set(key, {
          messages: _mergeInboxReadRows(_tagPersonMessages(data.messages, row), current, baseline, !!data.hasMore),
          hasMore: !!data.hasMore, at: Date.now(),
        });
        const next = _messagesForPerson(person), fp = messagesFingerprint(next);
        if (fp === state._messagesFingerprint) return;
        state._messagesFingerprint = fp; state.messages = next;
        renderMessages();
        try { refreshWindow24hBanner(); } catch { /* optional banner */ }
      }));
    } finally {
      if (person.refreshScope === scope) person.refreshing = false;
    }
  }

  async function loadMessages({ force = false } = {}) {
    if (!_inboxVisible() || !state.selectedChatId) { state._inboxNeedsRefresh = true; return; }
    if (state.messagesLoadingOlder) { state._inboxNeedsRefresh = true; return; }
    const scope = _inboxReadScope(), existing = state._inboxMessageJob;
    if (existing?.scope === scope) { if (force) existing.again = true; return existing.promise; }
    const job = { scope, again: false };
    state._inboxMessageJob = job;
    job.promise = _loadInboxMessages({ force }).finally(() => {
      if (state._inboxMessageJob !== job) return;
      state._inboxMessageJob = null;
      if (job.again && _inboxVisible() && scope === _inboxReadScope()) loadMessages({ force: true });
    });
    return job.promise;
  }

  async function _loadInboxMessages({ force = false } = {}) {
    if (!state.selectedChatId) return;
    // Pin the chat identity at request time. If the agent switches chats
    // while this fetch is in flight, a late response would overwrite the
    // new chat's state with stale data (and the prior chat's messages
    // would visibly leak into the open one). Guard after every await.
    const requestedChatId = state.selectedChatId;
    const scope = _inboxReadScope();
    const initial = state.personChat ? state.personMessageCache.get(String(requestedChatId))?.messages || [] : state.messages;
    const baseline = new Map(initial.map(m => [String(m.id), JSON.stringify(m)]));
    const requestedPerson = state.personChat;
    const requestedPersonKey = requestedPerson?.key || '';
    // Fire-and-forget the participant lookup so the first paint can use
    // pushName, then the second paint replaces with resolved names once
    // the wa-members fetch lands.
    ensureGroupMembersForCurrentChat();
    // Fire-and-forget the per-chat timeline events fetch — assign, label,
    // ticket-create, group-edit, etc. — so they render inline with messages.
    loadChatEvents({ force });
    _refreshPersonOtherMessages(requestedPerson, { force });
    _refreshPeerFeed({ force });
    try {
      // ETag / 304 (perf audit 2026-07-28 #3): replay the validator from the
      // last payload we accepted for THIS chat. When nothing changed the
      // server answers an empty 304 and we keep state + DOM exactly as they
      // are — the poll used to re-download the full ~40 KB body every cycle
      // only for messagesFingerprint to discard it. Never sent on `force`
      // (chat open / explicit refresh must repaint + jump to bottom) and
      // never across chats (the validator is per-chat by construction).
      const inmEtag = (!force && state._messagesEtag && state._messagesEtag.chatId === requestedChatId)
        ? state._messagesEtag.etag : null;
      const r = await _inboxRead('/api/inbox/chats/' + encodeURIComponent(requestedChatId) + '/messages?limit=80',
        inmEtag ? { headers: { 'If-None-Match': inmEtag } } : {});
      if (state.selectedChatId !== requestedChatId) return; // user switched chats — abandon stale response
      if (r.status === 304) _refreshPersonOtherMessages(requestedPerson, { force: false });
      if (r.status === 304) { _inboxReadError(false); return; } // unchanged primary — keep current messages, fingerprint and DOM intact
      const data = await r.json();
      if (scope !== _inboxReadScope()) return;
      _inboxReadError(false);
      if (state.selectedChatId !== requestedChatId) return;
      if ((state.personChat?.key || '') !== requestedPersonKey) return;
      // Remember the validator only once the response survived both
      // chat-switch guards — a stale response must never leave its ETag
      // behind to 304-mask the next real fetch.
      const freshEtag = r.headers.get('ETag');
      if (freshEtag) state._messagesEtag = { chatId: requestedChatId, etag: freshEtag };
      const primaryRow = requestedPerson?.rows?.find(row => String(row.id) === String(requestedChatId))
        || state.chats.find(row => String(row.id) === String(requestedChatId))
        || state.selectedChat;
      const receivedMessages = requestedPerson
        ? _tagPersonMessages(data.messages, primaryRow)
        : (Array.isArray(data.messages) ? data.messages : []);
      const current = requestedPerson ? state.personMessageCache.get(String(requestedChatId))?.messages || [] : state.messages;
      const primaryMessages = _mergeInboxReadRows(receivedMessages, current, baseline, !!data.hasMore);
      if (requestedPerson) {
        state.personMessageCache.set(String(requestedChatId), {
          messages: primaryMessages,
          hasMore: !!data.hasMore,
          at: Date.now(),
        });
      }
      const next = requestedPerson
        ? _messagesForPerson(requestedPerson)
        : _mergePersonMessageBatches([primaryMessages.filter(m => !m.peer), _peerFeedEntry()?.messages || []]);
      const fp = messagesFingerprint(next);
      // This chat's first answer ends the "Loading messages…" state even when
      // the history is identical to what is shown (e.g. a truly empty chat).
      const wasLoading = state._messagesLoadingFor === requestedChatId;
      if (wasLoading) state._messagesLoadingFor = null;
      const changed = force || wasLoading || fp !== state._messagesFingerprint;
      state._messagesFingerprint = fp;
      state.messages = next;
      // hasMore tells us whether to wire the scroll-to-load-older listener.
      // Reset on every chat switch so old chat's flag doesn't leak.
      // The newest page always says "more" for a long thread — once scroll-up
      // reached its first message, a background poll must not bring the
      // "older" hint (and the boundary that hides older rows) back (10-01).
      const _ex = state._olderExhausted;
      state.messagesHasMore = !!data.hasMore
        && !(_ex && _ex.chatId === requestedChatId && _localOldestLoaded() <= _ex.oldest);
      state.messagesLoadingOlder = false;
      // Secondary sources still need their own 20s refresh even when the
      // primary source returned byte-for-byte the same payload.
      _refreshPersonOtherMessages(requestedPerson, { force });
      if (!changed) return;
      // Fire-and-forget: refresh bot-coaching corrections + my feedback
      // votes for this chat so badges/chips render on the next paint.
      refreshBotCorrectionsForCurrentChat();
      refreshBotFeedbackForCurrentChat();
      // `force` means "fresh chat open" (chat switch / explicit refresh) —
      // those should jump to the latest message even if the previous chat
      // had the user scrolled up. Polling re-renders (force=false) honor
      // the agent's current scroll position.
      renderMessages({ forceBottom: force });
      // 24h-window banner — recompute now that state.messages is fresh.
      // Cheap O(messages) scan from the end, no I/O.
      try { refreshWindow24hBanner(); } catch (err) { console.warn('[24h banner] failed:', err); }
      // Mr.Call AI coach banner — refresh after messages render. Internally
      // no-ops if the chat hasn't changed AND the last message timestamp
      // hasn't advanced (server cache hits + client skip).
      // COST (2026-07-26): do NOT pass `force` here. `force` means "fresh chat
      // open", but on the coach endpoint it BYPASSES the cache — so every chat
      // switch re-ran a full Gemini analysis of an UNCHANGED conversation. The
      // cache key is the last message timestamp, so a cache hit is the exact
      // same analysis. Only the explicit refresh button forces a re-analysis.
      if (typeof window.refreshMrCallBanner === 'function') {
        window.refreshMrCallBanner().catch(err => console.warn('[mr-call coach] failed:', err));
      }
    } catch (err) {
      // 403 means "a colleague's lead" only on a lead chat (WhatsApp / ig: /
      // fbm:). A support2 student chat also answers 403 while s2 is slow or
      // restarting (_s2MirrorAuthorize sees an empty list), and recruitment /
      // service chats have their own rules — those keep the generic notice.
      const leadChat = /@s\.whatsapp\.net$|^ig:|^fbm:/.test(String(requestedChatId || ''));
      if (scope === _inboxReadScope()) _inboxReadError(true, leadChat && /^HTTP 403$/.test(String(err && err.message)));
    }
  }

  // Lazy-load older messages when the user scrolls near the top of the
  // messages area. Called from the scroll handler — keeps the chat fast
  // on first open (only 80 most-recent messages) and lets agents drill
  // back through long histories without blowing the initial render.
  async function loadOlderMessages() {
    if (!_inboxVisible() || !state.selectedChatId || !_threadHasMore() || state.messagesLoadingOlder || state._inboxMessageJob) return;
    if (!state.messages.length) return;
    // Pin chat identity so a chat switch mid-fetch doesn't prepend an old
    // chat's history onto the now-open one.
    const requestedChatId = state.selectedChatId;
    const scope = _inboxReadScope();
    const requestedPerson = state.personChat;
    const requestedPersonKey = requestedPerson?.key || '';
    state.messagesLoadingOlder = true;
    const primaryCached = requestedPerson
      ? state.personMessageCache.get(String(requestedChatId))?.messages
      : null;
    const primaryExisting = primaryCached?.length ? primaryCached : state.messages.filter(m => !m.peer);
    const oldestTs = Math.min(...primaryExisting.map(m => m.timestamp || Infinity));
    try {
      // support2's older page rides along (mirror, 2026-10-01): the thread is
      // one conversation, so scrolling up must reach the start of BOTH sides.
      const peerOlder = _peerHasMore() ? _loadOlderPeer() : Promise.resolve(false);
      let data = { messages: [], hasMore: false };
      if (state.messagesHasMore && Number.isFinite(oldestTs)) {
        const r = await _inboxRead('/api/inbox/chats/' + encodeURIComponent(requestedChatId) + '/messages?limit=80&before=' + oldestTs);
        if (state.selectedChatId !== requestedChatId || (state.personChat?.key || '') !== requestedPersonKey) return;
        data = await r.json();
      }
      const peerGot = await peerOlder;
      if (scope !== _inboxReadScope()) return;
      if (state.selectedChatId !== requestedChatId || (state.personChat?.key || '') !== requestedPersonKey) return;
      const primaryRow = requestedPerson?.rows?.find(row => String(row.id) === String(requestedChatId))
        || state.chats.find(row => String(row.id) === String(requestedChatId));
      const older = requestedPerson
        ? _tagPersonMessages(data.messages, primaryRow)
        : (Array.isArray(data.messages) ? data.messages : []);
      if (older.length || peerGot) {
        // Preserve scroll anchor — note current scrollHeight, prepend, then
        // restore relative position so the user stays on the same message
        // they were reading.
        const area = E.messagesArea;
        const beforeH = area.scrollHeight;
        const beforeTop = area.scrollTop;
        if (requestedPerson) {
          if (older.length) {
            const primaryMessages = _mergePersonMessageBatches([older, primaryExisting]);
            state.personMessageCache.set(String(requestedChatId), {
              messages: primaryMessages,
              hasMore: !!data.hasMore,
              at: Date.now(),
            });
          }
          state.messages = _messagesForPerson(requestedPerson);
        } else {
          state.messages = _mergePersonMessageBatches([older, state.messages.filter(m => !m.peer), _peerFeedEntry()?.messages || []]);
        }
        state._messagesFingerprint = messagesFingerprint(state.messages);
        if (state.messagesHasMore) state.messagesHasMore = !!data.hasMore && older.length > 0;
        if (!state.messagesHasMore) state._olderExhausted = { chatId: requestedChatId, oldest: _localOldestLoaded() };
        renderMessages();
        // Re-render moves scrollTop to bottom; pull it back so the row the
        // user was looking at stays visible.
        const delta = area.scrollHeight - beforeH;
        area.scrollTop = beforeTop + delta;
      } else {
        state.messagesHasMore = false;
        state._olderExhausted = { chatId: requestedChatId, oldest: _localOldestLoaded() };
      }
    } catch { /* keep prior state */ }
    finally { if (scope === _inboxReadScope()) { state.messagesLoadingOlder = false; _resumeInboxMessages(); } }
  }

  function isRenderableMessage(m) {
    // Skip empty payloads — Baileys' history sync sometimes emits text-typed frames
    // for reactions, system events, or unsupported message subtypes. Without this
    // filter they render as empty green/grey bubbles with just a timestamp.
    if (m.text && m.text.trim()) return true;
    if (m.mediaUrl) return true;
    if (m.type && m.type !== 'text') return true;
    return false;
  }

  // Fingerprint of state.messages so polls that bring zero changes can skip
  // the whole rebuild. Walks each msg and folds (timestamp, text length,
  // mediaUrl presence, edit timestamp, reaction count) into one 32-bit hash.
  // Catches: new msg, edited msg (text length change OR editedAt bump),
  // reaction added/removed, media attached. Doesn't catch identical-length
  // text replacements with no editedAt — those are vanishingly rare in
  // production (would require a manual DB tweak; the WA edit path always
  // updates editedAt).
  function _messagesFingerprint(msgs) {
    let h = 0 | 0;
    for (let i = 0; i < msgs.length; i++) {
      const m = msgs[i];
      h = (h * 31 + ((m.timestamp || 0) | 0)) | 0;
      h = (h * 31 + (m.text ? m.text.length : 0)) | 0;
      if (m.mediaUrl) h = (h * 31 + 7) | 0;
      if (m._personSource) {
        for (let j = 0; j < m._personSource.length; j++) h = (h * 31 + m._personSource.charCodeAt(j)) | 0;
      }
      if (m.editedAt) h = (h * 31 + (Number(m.editedAt) | 0)) | 0;
      if (m.reactions) h = (h * 31 + (Array.isArray(m.reactions) ? m.reactions.length : 1)) | 0;
    }
    return msgs.length + ':' + h;
  }

  // Defeat renderMessages()'s fast-path so the NEXT call actually rebuilds.
  //
  // WHY this helper exists: there are two similarly-named fingerprints and they
  // guard different things.
  //   state._messagesFingerprint  (messagesFingerprint)  → the LOADER guard:
  //                               "the fetched message list is unchanged, don't
  //                               re-set state.messages".
  //   state._lastMessagesFp       (_messagesFingerprint) → the RENDER guard:
  //                               "state.messages is unchanged, keep the DOM".
  //
  // Callers wanting to re-render because something OTHER than the messages
  // changed — a timeline event arrived, the header filter switched — were
  // clearing the loader one and calling renderMessages(), which then returned
  // early on the render guard and left the DOM untouched. Chat events have
  // silently depended on an unrelated message change to appear ever since.
  // Resetting the render guard is the fix; forceBottom would also work but
  // yanks the agent's scroll position to the newest message.
  function forceMessagesRerender() {
    state._messagesFingerprint = '';
    state._lastMessagesFp = null;
  }

  function _reconcileInboxChildren(parent, nodes) {
    const wanted = new Set(nodes);
    for (const child of Array.from(parent.childNodes)) if (!wanted.has(child)) {
      child.querySelectorAll?.('audio,video').forEach(media => media.pause());
      child.remove();
    }
    let cursor = parent.firstChild;
    for (const node of nodes) {
      if (node !== cursor) parent.insertBefore(node, cursor);
      cursor = node.nextSibling;
    }
  }

  function _retainInboxPlayer(oldRow, nextRow) {
    const live = oldRow.querySelector('.audio-player');
    const cold = nextRow.querySelector('.audio-player');
    if (!live?.querySelector('audio')?.hasAttribute('src') || !cold) return nextRow;
    const oldBubble = live.parentElement, nextBubble = cold.parentElement;
    if (oldBubble.parentElement !== oldRow || nextBubble.parentElement !== nextRow) return nextRow;
    _reconcileInboxChildren(oldBubble, Array.from(nextBubble.childNodes, node => node === cold ? live : node));
    oldBubble.className = nextBubble.className;
    _reconcileInboxChildren(oldRow, Array.from(nextRow.childNodes, node => node === nextBubble ? oldBubble : node));
    oldRow.className = nextRow.className;
    return oldRow;
  }

  function renderMessages({ forceBottom = false } = {}) {
    const _renderMeasuredAt = performance.now();
    try {
    if (!_inboxVisible()) { state._messagesRenderPending = true; return; }
    state._messagesRenderPending = false;
    const area = E.messagesArea;
    // Whether this contact has an app thread is only knowable once their
    // messages are here — and the picker is built when the chat OPENS, before
    // the fetch returns. Without this it painted "App —" on someone who was
    // demonstrably writing from the app two bubbles up. Repaint only on a real
    // change, so the 8-second poll can't close a menu under the agent's cursor.
    {
      const has = _chatHasAppThread();
      if (has !== state._composeAppAvail) {
        state._composeAppAvail = has;
        _renderComposeChannelBtn();
      }
    }
    const scope = JSON.stringify([state.token, state.ownershipEpoch, state.selectedChatId, state.personChat?.key]);
    const context = JSON.stringify([state.selectedChat?.isGroup, state.currentGroupMembers || {},
      state.teamUsers || [], state.me?.id, state.me?.username, state.me?.role, !!state.personChat]);
    const loadingThisChat = !!state.selectedChatId && state._messagesLoadingFor === state.selectedChatId;
    const fp = JSON.stringify([scope, state.messages, context, state.timelineFilter,
      state.chatEventsById?.[state.selectedChatId] || [], _peerEventsForOpenChat(), state._notesByChat?.[state.selectedChatId] || [],
      state.botCorrections?.[state.selectedChatId] || [], state.botFeedback?.[state.selectedChatId] || {},
      [...(state.selectedMessageIds || [])], state.messagesHasMore, state.messagesLoadingOlder, loadingThisChat]);
    if (!forceBottom && fp === state._lastMessagesFp && area._messageScope === scope) return;
    state._lastMessagesFp = fp;
    area.dataset.chatId = state.selectedChatId || '';
    // Multi-line WhatsApp: header chip while the latest client message is on
    // another of our numbers. Cosmetic — must never break the thread render.
    try { _paintWaLineHeader(); } catch (_) { /* chip is optional */ }
    // WA-Web scroll-preservation pattern. The 8-s msgPollTimer + socket
    // events (chat_event, message:edited, group-members hydrate, …) re-
    // render the chat at any time. If the agent scrolled up to play an
    // old audio bubble, the next poll would jerk them back to the latest
    // message — Ayoub hit this. Only force scroll-to-bottom when:
    //   1) the caller explicitly asks (chat switch / fresh open), OR
    //   2) the area was empty before this render (wasInitial), OR
    //   3) the agent was already within NEAR_BOTTOM_PX of the bottom
    //      (still effectively "reading the latest").
    // Otherwise restore scrollTop so the agent's reading position is
    // preserved across the re-render.
    const NEAR_BOTTOM_PX = 80;
    const wasInitial = area.scrollHeight === 0;
    const wasAtBottom = forceBottom || wasInitial ||
      (area.scrollHeight - area.scrollTop - area.clientHeight) < NEAR_BOTTOM_PX;
    const savedTop = area.scrollTop;

    const previous = area._messageScope === scope ? area._messageRows || new Map() : new Map();
    const nextRows = new Map(), nodes = [];
    const target = { appendChild(node) { nodes.push(node); return node; } };
    const rowFor = (m, isGroup) => {
      const key = JSON.stringify([m._personSource || m.channel, m._personThreadId || m.chatId || state.selectedChatId, m.type, m.id]);
      const signature = JSON.stringify([m, state.selectedMessageIds?.has(m.id), !!state.selectedMessageIds?.size,
        state.botCorrections?.[state.selectedChatId]?.filter(c => c.messageId === m.id) || [],
        state.botFeedback?.[state.selectedChatId]?.[m.id] || null]) + context;
      const old = m.id ? previous.get(key) : null;
      const mediaUrl = m.callData?.recordingUrl || m.mediaUrl || '';
      let row, bound = m;
      if (old?.signature === signature) { row = old.node; bound = Object.assign(old.message, m); }
      else {
        row = buildBubble(m, isGroup);
        if (old && old.mediaUrl === mediaUrl && !m.revoked) {
          row = _retainInboxPlayer(old.node, row);
          if (row === old.node) bound = Object.assign(old.message, m);
        }
      }
      row.querySelector('.audio-player')?._setSource?.(mediaUrl);
      if (m.id) nextRows.set(key, { node: row, message: bound, signature, mediaUrl });
      return row;
    };
    const commit = () => {
      _reconcileInboxChildren(area, nodes);
      area._messageScope = scope; area._messageRows = nextRows;
      refreshIcons(area);
    };

    // Top indicator — "Loading older…" while a fetch is in-flight, or
    // "↑ Scroll up for older" hint when there's more history but no
    // active fetch. Hidden once we've loaded everything.
    if (_threadHasMore()) {
      const top = el('div', {
        class: 'messages-top-indicator',
        text: state.messagesLoadingOlder ? 'Loading older messages…' : '↑ Scroll up for older messages'
      });
      target.appendChild(top);
    }
    const renderable = state.messages.filter(isRenderableMessage);
    const hasChatNotes = ((state._notesByChat && state._notesByChat[state.selectedChatId]) || []).length > 0;
    if (renderable.length === 0 && !hasChatNotes) {
      target.appendChild(el('div', { class: 'messages-empty', text: loadingThisChat ? 'Loading messages…' : 'No messages yet' }));
      commit();
      return;
    }
    const isGroup = !!(state.selectedChat && state.selectedChat.isGroup);
    appendBubblesWithDateSeparators(target, renderable, isGroup, rowFor);
    // The chat's own notes/events (or the same person's cached source) can be
    // on screen before its history lands — still say the messages are coming,
    // or the agent reads a notes-only pane as "no messages".
    if (loadingThisChat) target.appendChild(el('div', { class: 'messages-top-indicator', text: 'Loading messages…' }));
    commit();
    if (wasAtBottom) {
      area.scrollTop = area.scrollHeight;
    } else {
      area.scrollTop = savedTop;
    }
    // Re-apply the in-chat search highlights after every full re-render
    // (new message arrived, lazy-loaded older history, etc.). Without this
    // the highlight class is wiped along with the DOM.
    if (state.chatSearch?.active && state.chatSearch.query) applyChatSearch({ keepIndex: true });

    } finally {
      _inboxPerf.record('render_ms', performance.now() - _renderMeasuredAt);
      _inboxPerf.painted((state.messages || []).length > 0 && !!document.getElementById('messagesArea')?.querySelector('[data-msg-id]'));
    }
  }

  // ============ In-chat search ============
  // Toggled by #chatSearchBtn in the chat header. Highlights every
  // message in the currently-loaded history that matches the query —
  // text body, caption (filename), pushName, quoted message, and the
  // resolved group-member name. Up/Down arrows (and Enter / Shift+Enter)
  // cycle between matches with a scroll-into-view + flash. To search
  // older history, scroll up first to load it, then re-search.
  state.chatSearch = { active: false, query: '', matches: [], activeIndex: -1 };

  function openChatSearch() {
    if (!state.selectedChatId) return;
    state.chatSearch.active = true;
    document.getElementById('chatSearchBar')?.classList.remove('hidden');
    const inp = document.getElementById('chatSearchInput');
    if (inp) { inp.value = state.chatSearch.query || ''; inp.focus(); inp.select(); }
    refreshIcons();
  }
  function closeChatSearch() {
    state.chatSearch.active = false;
    state.chatSearch.query = '';
    state.chatSearch.matches = [];
    state.chatSearch.activeIndex = -1;
    document.getElementById('chatSearchBar')?.classList.add('hidden');
    const inp = document.getElementById('chatSearchInput');
    if (inp) inp.value = '';
    clearChatSearchHighlights();
    updateChatSearchUi();
  }
  function clearChatSearchHighlights() {
    document.querySelectorAll('.bubble.search-match, .bubble.search-match-active').forEach(b => {
      b.classList.remove('search-match', 'search-match-active');
    });
  }
  function applyChatSearch({ keepIndex = false } = {}) {
    clearChatSearchHighlights();
    const q = (state.chatSearch.query || '').trim().toLowerCase();
    if (!q) {
      state.chatSearch.matches = [];
      state.chatSearch.activeIndex = -1;
      updateChatSearchUi();
      return;
    }
    const isGroup = !!(state.selectedChat && state.selectedChat.isGroup);
    const matches = [];
    for (const m of (state.messages || [])) {
      const parts = [m.text, m.fileName, m.pushName, m.quotedMessage?.text].filter(Boolean).map(s => String(s).toLowerCase());
      if (isGroup && m.sender) {
        const resolved = state.currentGroupMembers?.[m.sender]
          || state.currentGroupMembers?.[jidUserSafe(m.sender)];
        if (resolved) parts.push(resolved.toLowerCase());
      }
      if (parts.some(p => p.includes(q))) matches.push(m);
    }
    state.chatSearch.matches = matches;
    if (!matches.length) {
      state.chatSearch.activeIndex = -1;
    } else if (!keepIndex || state.chatSearch.activeIndex < 0 || state.chatSearch.activeIndex >= matches.length) {
      state.chatSearch.activeIndex = 0;
    }
    for (const m of matches) {
      const node = m.id && document.querySelector('[data-msg-id="' + CSS.escape(m.id) + '"] .bubble');
      if (node) node.classList.add('search-match');
    }
    if (state.chatSearch.activeIndex >= 0) scrollToActiveMatch({ smooth: !keepIndex });
    updateChatSearchUi();
  }
  function scrollToActiveMatch({ smooth = true } = {}) {
    const m = state.chatSearch.matches[state.chatSearch.activeIndex];
    if (!m?.id) return;
    document.querySelectorAll('.bubble.search-match-active').forEach(b => b.classList.remove('search-match-active'));
    const row = document.querySelector('[data-msg-id="' + CSS.escape(m.id) + '"]');
    const bubble = row?.querySelector('.bubble');
    if (bubble) bubble.classList.add('search-match-active');
    if (row) row.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
  }
  function navigateChatSearchMatch(dir) {
    const total = state.chatSearch.matches.length;
    if (total < 2) return;
    state.chatSearch.activeIndex = ((state.chatSearch.activeIndex + dir) % total + total) % total;
    scrollToActiveMatch();
    updateChatSearchUi();
  }
  function updateChatSearchUi() {
    const cnt = document.getElementById('chatSearchCount');
    const prev = document.getElementById('chatSearchPrev');
    const next = document.getElementById('chatSearchNext');
    const total = state.chatSearch.matches.length;
    const idx = state.chatSearch.activeIndex;
    if (cnt) {
      if (!state.chatSearch.query) cnt.textContent = '';
      else if (!total) cnt.textContent = '0 results';
      else cnt.textContent = (idx + 1) + ' / ' + total;
    }
    if (prev) prev.disabled = total < 2;
    if (next) next.disabled = total < 2;
  }

  // ============ AI feature A: per-message Translate-to-Arabic (🌐) ============
  // Shared across EVERY chat surface (customer inbox, groups, team chat).
  // makeTranslateButton(text, container) returns the small 🌐 button for a
  // message's meta row. toggleInlineTranslation(container, text, btn) shows /
  // hides an inline RTL block under the message with the Arabic translation.
  // Plain emoji icons (no icon-font dependency). Model output is rendered with
  // textContent only — never innerHTML — so a translated message can't inject markup.
  function toggleInlineTranslation(container, text, btn) {
    if (!container) return;
    const existing = container.querySelector(':scope > .msg-translation');
    if (existing) { existing.remove(); if (btn) btn.classList.remove('active'); return; }
    if (btn) btn.classList.add('active');
    const block = el('div', { class: 'msg-translation', dir: 'rtl' });
    const head = el('div', { class: 'msg-translation-head' });
    head.appendChild(el('span', { class: 'msg-translation-title', text: '🌐 العربية' }));
    const hide = el('button', { class: 'msg-translation-hide', type: 'button', 'aria-label': 'إخفاء', title: 'إخفاء', text: '✕' });
    hide.addEventListener('click', (e) => { e.stopPropagation(); block.remove(); if (btn) btn.classList.remove('active'); });
    head.appendChild(hide);
    block.appendChild(head);
    const body = el('div', { class: 'msg-translation-body', text: '…' });
    block.appendChild(body);
    container.appendChild(block);
    const src = String(text || '').slice(0, 8000);
    (async () => {
      try {
        const r = await api('/api/ai/translate', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: src, target: 'ar' }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) { body.textContent = j.error || (r.status === 503 ? 'الترجمة غير مُفعّلة' : 'تعذّرت الترجمة'); return; }
        body.textContent = j.translation || '—';
      } catch { body.textContent = 'تعذّرت الترجمة'; }
    })();
  }
  function makeTranslateButton(text, container) {
    const btn = el('button', { class: 'msg-translate-btn', type: 'button', title: 'ترجمة إلى العربية', 'aria-label': 'Translate to Arabic', text: '🌐' });
    btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); toggleInlineTranslation(container, text, btn); });
    return btn;
  }

  // ============ AI feature B: writing assistant (✨) ============
  // ONE shared modal (#aiComposeModal in index.html). openComposeAssistant
  // pre-fills the intent with the composer's current text and remembers the
  // insert callback. Generate → POST /api/ai/compose → editable draft. Insert →
  // insert(draft) (the caller sets its own composer value + fires input + focus).
  // NEVER auto-sends — a human always reviews and presses send.
  let _composeAssistInsert = null;
  let _composeAssistWired = false;
  let _composeAssistAction = 'compose';
  let _composeAssistRun = 0;
  function openComposeAssistant({ seed = '', insert = null } = {}) {
    _composeAssistRun++;
    _composeAssistAction = 'compose';
    _composeAssistInsert = (typeof insert === 'function') ? insert : null;
    const intent = $('aiComposeIntent');
    const draft = $('aiComposeDraft');
    if (intent) intent.value = String(seed || '');
    if (draft) { draft.value = ''; draft.removeAttribute('aria-busy'); }
    const error = $('aiComposeError');
    if (error) { error.textContent = ''; error.classList.add('hidden'); }
    ['aiComposeGenerate', 'aiComposeCorrect', 'aiComposeRegenerate'].forEach(id => { const b = $(id); if (b) b.disabled = false; });
    if ($('aiComposeInsert')) $('aiComposeInsert').disabled = true;
    _wireComposeAssistant();
    openModal('aiComposeModal');
    if (intent) intent.focus();
  }
  function _wireComposeAssistant() {
    if (_composeAssistWired) return;
    _composeAssistWired = true;
    const genBtn = $('aiComposeGenerate');
    const correctBtn = $('aiComposeCorrect');
    const regenBtn = $('aiComposeRegenerate');
    const insertBtn = $('aiComposeInsert');
    const showError = message => {
      const error = $('aiComposeError');
      if (error) { error.textContent = message; error.classList.remove('hidden'); }
    };
    async function generate(action = 'compose') {
      const intent = $('aiComposeIntent'); const draft = $('aiComposeDraft'); const langSel = $('aiComposeLang');
      const text = (intent?.value || '').trim();
      if (!text) { showError('Write your draft first.'); return; }
      const language = langSel?.value || 'ar';
      const run = ++_composeAssistRun;
      _composeAssistAction = action;
      const error = $('aiComposeError');
      if (error) { error.textContent = ''; error.classList.add('hidden'); }
      [genBtn, correctBtn, regenBtn, insertBtn].forEach(b => { if (b) b.disabled = true; });
      if (draft) draft.setAttribute('aria-busy', 'true');
      try {
        const r = await api(action === 'correct' ? '/api/ai/correct' : '/api/ai/compose', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(action === 'correct' ? { text: text.slice(0, 4000) } : { text: text.slice(0, 4000), language }),
        });
        const j = await r.json().catch(() => ({}));
        if (run !== _composeAssistRun) return;
        if (!r.ok) { showError(j.error || 'The assistant is unavailable. Please try again.'); return; }
        if (typeof j.draft !== 'string' || !j.draft.trim()) { showError('The assistant returned no text. Please try again.'); return; }
        if (draft) draft.value = j.draft;
      } catch { if (run === _composeAssistRun) showError('Could not reach the assistant. Please try again.'); }
      finally {
        if (run === _composeAssistRun) {
          [genBtn, correctBtn, regenBtn].forEach(b => { if (b) b.disabled = false; });
          if (draft) draft.removeAttribute('aria-busy');
          if (insertBtn) insertBtn.disabled = !(draft?.value || '').trim();
        }
      }
    }
    if (genBtn) genBtn.addEventListener('click', () => generate('compose'));
    if (correctBtn) correctBtn.addEventListener('click', () => generate('correct'));
    if (regenBtn) regenBtn.addEventListener('click', () => generate(_composeAssistAction));
    $('aiComposeDraft')?.addEventListener('input', () => {
      if (insertBtn) insertBtn.disabled = $('aiComposeDraft').getAttribute('aria-busy') === 'true' || !$('aiComposeDraft').value.trim();
    });
    if (insertBtn) insertBtn.addEventListener('click', () => {
      const draft = $('aiComposeDraft');
      const val = (draft?.value || '').trim();
      if (!val) { toast('لا يوجد نص لإدراجه', 'error'); return; }
      if (_composeAssistInsert) { try { _composeAssistInsert(val); } catch (e) { /* caller surface gone */ } }
      closeModal('aiComposeModal');
    });
    document.querySelectorAll('[data-ai-compose-close]').forEach(b => b.addEventListener('click', () => closeModal('aiComposeModal')));
  }
  // Build a ✨ button for a composer toolbar. getSeed() returns the composer's
  // current text; insert(text) writes the chosen draft back into that composer.
  function makeComposeAssistButton(getSeed, insert) {
    const btn = el('button', { class: 'compose-ai-btn', type: 'button', title: 'مساعد الكتابة بالذكاء الاصطناعي', 'aria-label': 'AI writing assistant', text: '✨' });
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openComposeAssistant({ seed: (typeof getSeed === 'function' ? getSeed() : ''), insert });
    });
    return btn;
  }

  // Render free-text into a node, replacing every Moroccan phone-number
  // match with a clickable chip that opens the matching 1-1 chat. Falls
  // back to a plain text node when no phone is found, so the change is
  // a no-op for messages that don't contain numbers.
  //
  // Accepts the common formats agents share:
  //   0612345678   06 12 34 56 78   06-12-34-56-78
  //   612-345-678  670-203249       0629314405
  //   +212 612 345 678               212612345678
  // Min 9 digits, max 12, with optional country prefix and any of dash /
  // space / dot as a separator.
  const PHONE_REGEX = /(\+?212|00212|0)?\s?\d{1,3}[-\s.]?\d{3}[-\s.]?\d{3,4}\b/g;
  // "+<country>" or "00<country>" followed by digit groups — any country.
  const INTL_PHONE_REGEX = /(?:\+|\b00)[1-9](?:[-\s.]?\d){6,17}/g;
  // Resolve an @<digits> mention to a display name. The digit run after the @
  // can be a phone (12 digits, "212...") or a WhatsApp LID (15+ digits) — we
  // check both. Sources, in order:
  //   1. The current group's WA participants (populated lazily by
  //      loadForwardGroupMembers when the chat opens).
  //   2. Team users — whatsappPhone field. Lets @-tags addressed to team
  //      members render as "@Ahlam" instead of "@21234567890".
  // Returns null when nothing matches; caller renders the raw text.
  function _resolveMentionName(digits) {
    if (!digits) return null;
    // 1. Current group's members
    if (state.selectedChat?.isGroup) {
      const cache = state._forwardGroupMembersCache?.get(state.selectedChat.id);
      const members = cache?.members || [];
      for (const m of members) {
        const jidUser = (m.jid || '').split('@')[0];
        const lidUser = (m.lid || '').split('@')[0];
        if (jidUser === digits || lidUser === digits || m.phone === digits) {
          return m.name || m.phone || null;
        }
      }
    }
    // 2. Team users
    for (const u of (state.teamUsers || [])) {
      const phoneDigits = String(u.whatsappPhone || '').replace(/\D/g, '');
      if (phoneDigits && phoneDigits === digits) {
        return userLabel(u.username, u.displayName || u.username);
      }
    }
    return null;
  }

  function buildTextWithPhones(text) {
    const frag = document.createDocumentFragment();
    if (!text) return frag;
    const tryMatch = (raw) => {
      // Need at least 9 digits to count as a phone — filters out random
      // numeric tokens like "16 mai 2026" or invoice IDs.
      const digits = raw.replace(/\D/g, '');
      if (digits.length < 9 || digits.length > 13) return null;
      // International form for the chat lookup. WA stores numbers without
      // the leading 0 and with the 212 prefix.
      let intl = digits;
      if (digits.length === 9) intl = '212' + digits;       // bare 9-digit
      else if (digits.length === 10 && digits.startsWith('0')) intl = '212' + digits.slice(1);
      else if (digits.length === 12 && digits.startsWith('212')) intl = digits;
      else if (digits.length === 13 && digits.startsWith('0212')) intl = digits.slice(1);
      else if (digits.length >= 11 && digits.length <= 12) intl = digits.slice(-12);
      else return null;
      // Sanity — Moroccan mobiles start with 5/6/7 after the country code.
      const local = intl.slice(3, 4);
      if (!/[5-7]/.test(local)) return null;
      return intl;
    };
    // Collect URL + phone matches with offsets, then walk the text in
    // order so we can render each as the right kind of node. URLs win
    // when ranges overlap (a phone-looking segment inside a URL stays
    // part of the link).
    const URL_RE = /\bhttps?:\/\/[^\s<>"]+/gi;
    const matches = [];
    text.replace(URL_RE, (raw, offset) => {
      // Trim trailing sentence punctuation that's almost certainly not
      // part of the URL — ".", ",", ")", "]", "!", "?", ";", ":".
      const trimmed = raw.replace(/[.,);\]!?;:]+$/, '');
      matches.push({ kind: 'url', offset, length: trimmed.length, raw: trimmed });
      return raw;
    });
    const insideUrlAt = (offset) => matches.some(x => x.kind === 'url' && offset >= x.offset && offset < x.offset + x.length);
    // Explicit international numbers first ("+49 15560 028505", "0033 6…").
    // The Moroccan regex below would read only their tail and turn it into a
    // fake 212 number (incident 2026-10-09: +49 15560 028505 → 212560028505).
    // Keep the longest leading run of digit groups that _phoneForWhatsapp
    // accepts, so trailing words or numbers after the phone do not void it.
    // A run that never validates still blocks the Moroccan pass.
    const intlRanges = [];
    text.replace(INTL_PHONE_REGEX, (raw, offset) => {
      if (insideUrlAt(offset)) return raw;
      intlRanges.push([offset, offset + raw.length]);
      const groups = raw.match(/^(?:\+|00)?\d+|[-\s.]+\d+/g) || [];
      for (let n = groups.length; n >= 1; n--) {
        const candidate = groups.slice(0, n).join('');
        const intl = _phoneForWhatsapp(candidate);
        // The country is the one written: _phoneForWhatsapp reads a bare
        // 9-digit run as Moroccan, so "+7 912 345 67" must not become 212….
        if (intl && intl === candidate.replace(/\D/g, '').replace(/^00/, '')) {
          matches.push({ kind: 'phone', offset, length: candidate.length, raw: candidate, intl });
          break;
        }
      }
      return raw;
    });
    text.replace(PHONE_REGEX, (raw, _g1, offset) => {
      // Never start inside a longer number: "155|60 028505" is not a phone.
      if (/[\d+]/.test(text.charAt(offset - 1))) return raw;
      if (intlRanges.some(([from, to]) => offset < to && offset + raw.length > from)) return raw;
      const intl = tryMatch(raw);
      if (!intl) return raw;
      if (insideUrlAt(offset)) return raw;
      matches.push({ kind: 'phone', offset, length: raw.length, raw, intl });
      return raw;
    });
    // @-mentions: a literal @ followed by 8-17 digits. WhatsApp ships mentions
    // as the raw JID user-part inside the message body (e.g. "@212XXXXXXX" for
    // phone JIDs or "@193247792103599" for LIDs). The renderer resolves each
    // to a contact name via _resolveMentionName; unresolved mentions fall
    // back to the raw text.
    const MENTION_RE = /@(\d{8,17})\b/g;
    text.replace(MENTION_RE, (raw, mentionDigits, offset) => {
      const insideUrl = matches.some(x => x.kind === 'url' && offset >= x.offset && offset < x.offset + x.length);
      if (insideUrl) return raw;
      matches.push({ kind: 'mention', offset, length: raw.length, raw, mentionDigits });
      return raw;
    });
    matches.sort((a, b) => a.offset - b.offset);

    let lastIndex = 0;
    for (const mt of matches) {
      if (mt.offset < lastIndex) continue; // overlapping match — skip
      if (mt.offset > lastIndex) frag.appendChild(document.createTextNode(text.slice(lastIndex, mt.offset)));
      if (mt.kind === 'url') {
        const link = el('a', {
          href: mt.raw, class: 'msg-link',
          target: '_blank', rel: 'noopener noreferrer'
        });
        link.textContent = mt.raw;
        // Stop double-click handlers on the row from hijacking link clicks.
        link.addEventListener('click', e => e.stopPropagation());
        frag.appendChild(link);
      } else if (mt.kind === 'mention') {
        const resolved = _resolveMentionName(mt.mentionDigits);
        if (resolved) {
          const chip = el('span', {
            class: 'msg-mention-chip',
            title: '@' + mt.mentionDigits + ' (resolved)'
          });
          chip.textContent = '@' + resolved;
          frag.appendChild(chip);
        } else {
          // Unknown JID — render the raw text (group members not loaded
          // yet, or the JID isn't in our local data). The members fetch
          // re-renders messages on completion via the group-open hook.
          frag.appendChild(document.createTextNode(mt.raw));
        }
      } else {
        const chip = el('a', {
          href: '#', class: 'msg-phone-link',
          title: 'Open chat with ' + mt.intl
        });
        chip.textContent = mt.raw;
        chip.addEventListener('click', e => {
          e.preventDefault();
          openChatForPhoneNumber(mt.intl);
        });
        frag.appendChild(chip);
      }
      lastIndex = mt.offset + mt.length;
    }
    if (lastIndex < text.length) frag.appendChild(document.createTextNode(text.slice(lastIndex)));
    return frag;
  }
  // Switch to the inbox + open a 1-1 chat with the given group participant
  // (sender of a group message). Same pattern as openChatForPhoneNumber
  // but starts from a JID instead of digits — works for both phone JIDs
  // and LID-only senders.
  function openChatForGroupParticipant(jid, displayName) {
    if (!jid) return;
    showView('inbox');
    if (!state.chats.find(c => c.id === jid)) {
      const phone = jid.endsWith('@s.whatsapp.net') ? jid.split('@')[0] : '';
      state.chats.unshift({
        id: jid, name: displayName || phone || jid.split('@')[0],
        phone, isGroup: false, lastMessage: null, unread: 0
      });
    }
    setTimeout(() => selectConversation(jid), 80);
  }

  // Switch to the inbox + open a 1-1 chat with the given international
  // phone number. Reuses the synthetic-chat trick the HubSpot search uses
  // so the conversation appears even if the bridge has no prior history.
  function openChatForPhoneNumber(intlDigits) {
    intlDigits = _phoneForWhatsapp(intlDigits);
    // Last line of defense: masked/truncated/social ids must never mint a chat.
    if (!intlDigits) { toast('Numéro invalide', 'error'); return; }
    const jid = intlDigits + '@s.whatsapp.net';
    showView('inbox');
    if (!state.chats.find(c => c.id === jid)) {
      state.chats.unshift({
        id: jid, name: intlDigits, phone: intlDigits, isGroup: false,
        lastMessage: null, unread: 0, _leadCreationSource: 'manual_phone'
      });
    }
    setTimeout(() => selectConversation(jid), 80);
  }

  // Oldest of OUR rows in the open thread (support2's are paged separately).
  function _localOldestLoaded() {
    let t = Infinity;
    for (const m of state.messages || []) {
      const ts = Number(m && m.timestamp) || 0;
      if (ts && !m.peer && ts < t) t = ts;
    }
    return t;
  }

  // Oldest moment the thread can show without a hole: the oldest loaded row of
  // each side that still has older pages (ours, support2's). 0 = all loaded.
  function _threadLoadedBoundary(messages) {
    if (!_threadHasMore()) return 0;
    let local = Infinity, peer = Infinity;
    for (const m of messages || []) {
      const t = Number(m && m.timestamp) || 0;
      if (!t) continue;
      if (m.peer) { if (t < peer) peer = t; } else if (t < local) local = t;
    }
    let b = 0;
    if (state.messagesHasMore && Number.isFinite(local)) b = Math.max(b, local);
    if (_peerHasMore() && Number.isFinite(peer)) b = Math.max(b, peer);
    return b;
  }

  // Render a list of message objects into a container with date-separator
  // chips between days. Without these, replies that span multiple days
  // look like time-stamps are out of order (e.g. 22:43 followed by 15:44).
  function appendBubblesWithDateSeparators(container, messages, isGroup, rowFor = buildBubble) {
    // Merge messages + timeline events into one chronological stream so every
    // action ever taken on this lead — by the team, the bot or the system —
    // appears inline at the moment it happened. Events are centered chip rows
    // ("Asmae a créé un ticket"), visible only to the team (server never sends
    // them to WhatsApp).
    //
    // The server derives most events at read time from the pipeline tables
    // (stage_history, lead_reminders, test_slots, sent_documents, …), so the
    // history is complete back to the first row — see
    // docs/superpowers/specs/2026-07-26-conversation-timeline-design.md.
    const filter = state.timelineFilter || 'all';
    // While older history is still unloaded (ours or support2's), nothing older
    // than what is loaded is drawn. The timeline is complete back to the first
    // row, so it used to stack weeks of events above the oldest loaded message
    // and the thread read as "nothing before Aug 8" (Ayoub 2026-10-01). Each
    // scroll-up now brings that period's messages AND events together.
    const boundary = _threadLoadedBoundary(messages);
    const inWindow = item => !boundary || (item.ts || 0) >= boundary;
    const events = (state.chatEventsById?.[state.selectedChatId] || []).concat(_peerEventsForOpenChat())
      .map(e => ({ __kind: 'event', ts: e.at || 0, evt: e }))
      .filter(item => timelineItemPasses(item, filter) && inWindow(item));
    // A composer note that has a notes-store copy is drawn once, from the
    // store (2026-10-20): its row is flagged noteTwin, and older rows copied
    // at boot are matched by id.
    const storeNoteIds = new Set((state._notesByChat?.[state.selectedChatId] || []).map(n => n.id));
    const msgs = messages
      .filter(m => !(m.type === 'note' && (m.noteTwin || storeNoteIds.has(m.id))))
      .map(m => ({ __kind: 'message', ts: m.timestamp || 0, msg: m }))
      .filter(item => timelineItemPasses(item, filter) && inWindow(item));
    // Team notes ride the same chronological stream (2026-08-14, Ayoub) —
    // the note in the Details ▸ Notes panel also shows inline where it was
    // written, exactly like support2's amber note bubbles. Team-facing
    // filters only (Tout / Équipe); they are annotations, not messages.
    const notesVisible = filter === 'all' || filter === 'team';
    const chatNotes = !notesVisible ? [] : (state._notesByChat?.[state.selectedChatId] || [])
      .map(n => ({ __kind: 'chatnote', ts: Date.parse(n.createdAt || '') || 0, note: n }))
      .filter(inWindow);
    const merged = msgs.concat(events, chatNotes).sort((a, b) => (a.ts || 0) - (b.ts || 0));

    let lastDay = '';
    for (let i = 0; i < merged.length; i++) {
      const item = merged[i];
      const ts = item.ts || 0;
      const d = ts ? new Date(ts) : null;
      const dayKey = d ? MA.fmt('ca-day', 'en-CA', {}, d) : '';
      if (dayKey && dayKey !== lastDay) {
        container.appendChild(el('div', {
          class: 'date-separator',
          text: friendlyDayLabel(d)
        }));
        lastDay = dayKey;
      }

      if (item.__kind === 'chatnote') {
        container.appendChild(buildInlineChatNote(item.note));
        continue;
      }

      // Collapse a RUN of consecutive low-signal events (bot skips, automatic
      // reminders, Zoom joins, sync rows) into one expandable line, so a busy
      // lead reads as its real milestones instead of a wall of machine chatter.
      // Grouping is done here rather than server-side because only the client
      // knows the final interleaving — a message landing between two secondary
      // events legitimately breaks the run. Runs are also cut at a day boundary
      // so a group never straddles a date separator.
      if (item.__kind === 'event' && item.evt && item.evt.importance === 'secondary') {
        const run = [item];
        let j = i + 1;
        while (j < merged.length) {
          const nxt = merged[j];
          if (nxt.__kind !== 'event' || !nxt.evt || nxt.evt.importance !== 'secondary') break;
          const nd = nxt.ts ? new Date(nxt.ts) : null;
          const nKey = nd ? MA.dayKey(nd) : '';
          if (nKey !== dayKey) break;
          run.push(nxt);
          j++;
        }
        if (run.length > 1) {
          container.appendChild(buildEventGroupChip(run.map(r => r.evt)));
          i = j - 1;
          continue;
        }
        // A lone technical event is cheap to show — rendering it muted beats
        // hiding a single line behind a click.
        container.appendChild(buildEventChip(item.evt));
        continue;
      }

      if (item.__kind === 'event') container.appendChild(buildEventChip(item.evt));
      // Messages sent on support2's line before 2026-07-31 were stored as
      // internal notes, so they render as a yellow team-note card — an
      // annotation, not the message the client received. The rows are already
      // written; read them the way they are written now instead of leaving a
      // permanent seam in the thread.
      else if (item.msg && item.msg.eventType === 'support2_send') {
        container.appendChild(rowFor({
          ...item.msg,
          fromMe: true,
          type: 'text',
          internal: false,
          sentVia: 'support2',
          sentBy: item.msg.actorUsername || item.msg.sentBy || '',
          senderLabel: item.msg.authorName || item.msg.senderLabel || '',
        }, isGroup));
      }
      // Internal AI/agent note (e.g. the 3CX AI voice-agent call note) — a
      // team-only card with the note body; never sent to the customer.
      else if (item.msg && item.msg.type === 'note') {
        container.appendChild(buildNoteBubble(item.msg));
      }
      // Team-only system rows (e.g. the returning-client banner) render as a
      // centered chip, NOT a customer/agent bubble — they were never sent over WA.
      else if (item.msg && (item.msg.internal || item.msg.type === 'system')) {
        container.appendChild(buildEventChip({ type: item.msg.eventType || 'system', text: item.msg.text, at: item.msg.timestamp, actorUsername: item.msg.actorUsername }));
      }
      else container.appendChild(rowFor(item.msg, isGroup));
    }
  }

  // ─── Timeline filter ──────────────────────────────────────────────────
  // Buckets in the conversation header. A filter spans the WHOLE stream, not
  // just events: picking "Bot" shows the bot's messages next to its decisions,
  // which is the only way to read what it actually said versus what it chose.
  const TIMELINE_FILTERS = [
    { id: 'all', label: 'Tout' },
    { id: 'messages', label: 'Messages' },
    { id: 'team', label: 'Équipe' },
    { id: 'bot', label: 'Bot' },
    { id: 'system', label: 'Système' },
  ];

  function timelineItemPasses(item, filter) {
    if (!filter || filter === 'all') return true;
    if (item.__kind === 'message') {
      const m = item.msg || {};
      if (filter === 'messages') return true;
      // Outbound rows carry `sentBy`; the funnel bot writes 'bot_funnel'.
      if (filter === 'bot') return m.fromMe && m.sentBy === 'bot_funnel';
      if (filter === 'team') return m.fromMe && m.sentBy && m.sentBy !== 'bot_funnel';
      return false; // 'system'
    }
    if (filter === 'messages') return false;
    // `category` is set by the server (lib/timeline categoryFor). Legacy
    // chat_events predating the timeline carry none — treat them as system so
    // they never vanish from a filtered view.
    return (item.evt && item.evt.category ? item.evt.category : 'system') === filter;
  }

  function renderTimelineFilterBar() {
    const bar = document.getElementById('timelineFilterBar');
    if (!bar) return;
    const events = state.chatEventsById?.[state.selectedChatId] || [];
    // Nothing but messages in this conversation → no filter to offer.
    if (!events.length) {
      bar.classList.add('hidden');
      clear(bar);
      return;
    }
    const active = state.timelineFilter || 'all';
    clear(bar);
    for (const f of TIMELINE_FILTERS) {
      const btn = el('button', {
        class: 'timeline-filter-btn' + (f.id === active ? ' is-active' : ''),
        type: 'button',
        text: f.label,
      });
      btn.addEventListener('click', () => {
        state.timelineFilter = f.id;
        // The message payload has not changed — only what we want shown of it —
        // so both fingerprint guards have to be cleared or the DOM stays put.
        forceMessagesRerender();
        try { renderMessages(); } catch { /* renderer may not be ready */ }
        renderTimelineFilterBar();
      });
      bar.appendChild(btn);
    }
    bar.classList.remove('hidden');
  }

  // Build a centered system-event chip for an inline timeline event
  // (assign, label, ticket-create, group-edit, etc.). Team-only — never
  // shipped over WhatsApp. The icon switches on event type.
  const TIMELINE_ICONS = {
    // chat_events.json — the original eight
    chat_assigned: '👤',
    chat_unassigned: '👤',
    label_added: '🏷️',
    label_removed: '🏷️',
    ticket_created: '🎫',
    bot_ticket_created: '🤖',
    note_added: '📝',
    group_subject_updated: '✏️',
    group_description_updated: '✏️',
    group_team_only_toggled: '🔒',
    group_participant_add: '➕',
    group_participant_remove: '➖',
    chat_name_set: '✏️',
    chat_name_cleared: '✏️',
    person_renamed: '✏️',
    returning_client: '🔁',
    group_student_added: '➕',
    bot_social_lead_created: '🤖',
    bot_auto_stopped: '🛑',
    // derived from the pipeline tables
    lead_created: '✨',
    stage_changed: '📊',
    reminder_created: '⏰',
    reminder_fired: '🔔',
    reminder_done: '✅',
    test_booked: '📅',
    test_confirmation_sent: '✉️',
    test_reminder_sent: '🔔',
    test_started: '▶️',
    test_completed: '🎓',
    test_no_show: '🚫',
    test_cancelled: '❌',
    zoom_teacher_joined: '🎥',
    zoom_student_joined: '🎥',
    zoom_ended: '🎬',
    meet_link_created: '🔗',
    meet_link_opened: '👀',
    document_sent: '📄',
    payment_recorded: '💰',
    receipt_submitted: '🧾',
    receipt_verified: '🔎',
    call_missed: '📞',
    bot_acted: '🤖',
    bot_skipped: '💤',
    website_test_completed: '✅',
  };

  function buildEventChip(evt) {
    const icon = TIMELINE_ICONS[evt.type] || '•';
    const row = el('div', { class: 'event-chip-row' });
    const cls = 'event-chip'
      + (evt.importance === 'secondary' ? ' is-secondary' : '')
      + (evt.detail ? ' has-detail' : '')
      + (evt.actorType ? ' actor-' + evt.actorType : '');
    const chip = el('div', { class: cls, title: evt.actorUsername ? '@' + userLabel(evt.actorUsername) : '' });
    const head = el('div', { class: 'event-chip-head' });
    head.appendChild(el('span', { class: 'event-chip-icon', text: icon }));
    head.appendChild(el('span', { class: 'event-chip-text', text: adminText(evt.text || '') }));
    if (evt.at) {
      head.appendChild(el('span', { class: 'event-chip-time', text: ' · ' + formatTime(evt.at) }));
    }
    // WHO did it, named. Without this the thread says "Test réservé — 11:00"
    // and an agent has no idea whether a teammate, the bot or the system booked
    // it. Skipped when the person is already named inside the title (legacy
    // chat_events sentences, "Le bot a …" rows) so nobody is credited twice.
    if (evt.actorName && !evt.actorInTitle) {
      head.appendChild(el('span', { class: 'event-chip-actor', text: '· par ' + evt.actorName }));
    }
    chip.appendChild(head);
    // Second line: the "why" — a stage-change reason, a bot snippet, a payment
    // breakdown. This is the difference between "stage changed" and knowing
    // who changed it and on what grounds.
    if (evt.detail) chip.appendChild(el('div', { class: 'event-chip-detail', text: evt.detail }));
    row.appendChild(chip);
    return row;
  }

  // One collapsed line standing in for a run of consecutive low-signal events.
  // Click expands in place — the events are never dropped, only folded.
  function buildEventGroupChip(events) {
    const row = el('div', { class: 'event-chip-row' });
    const wrap = el('div', { class: 'event-group' });
    const toggle = el('button', {
      class: 'event-group-toggle',
      type: 'button',
      text: '⚭ ' + events.length + ' événements système',
    });
    const body = el('div', { class: 'event-group-body hidden' });
    for (const e of events) body.appendChild(buildEventChip(e));
    toggle.addEventListener('click', () => {
      const hidden = body.classList.toggle('hidden');
      toggle.textContent = hidden
        ? '⚭ ' + events.length + ' événements système'
        : '⚭ masquer les ' + events.length + ' événements';
    });
    wrap.appendChild(toggle);
    wrap.appendChild(body);
    row.appendChild(wrap);
    return row;
  }
  // Internal note card (3CX AI voice-agent call note). Team-only — never sent to
  // the customer. Header line (who → which agent) + the Darija note body (RTL).
  // Inline team-note card in the conversation stream (2026-08-14, Ayoub):
  // the same note that lives in Conversation Details ▸ Notes also shows at
  // its chronological spot in the thread — support2 renders its notes the
  // same way, so both platforms read alike. Team-only, never sent to the
  // contact. Support2-origin rows keep their chip + @mentions (read-only).
  // One @mention chip on a note card. MY OWN chip is a button — click toggles
  // the "seen" check (✓) via /api/notes/:id/check (s2:-rows relay to support2
  // server-side); everyone else's chips stay read-only.
  function _noteMentionChipEl(n, un) {
    const done = !!(n.mentionChecks || {})[un];
    const base = 'padding:1px 7px;border-radius:8px;font-size:10.5px;font-weight:600;';
    const meUn = _mentionKeyFor(n);
    if (un === meUn) {
      const btn = el('button', {
        type: 'button',
        text: '@' + userLabel(un) + (done ? ' ✓' : ' · ✓ ?'),
        title: done ? 'Checked — click bach t-annuler' : 'Click melli t-saliha (kay-tmse7 mn Mes notes)',
        style: base + 'border:1px solid #16a34a;cursor:pointer;font:inherit;font-size:10.5px;font-weight:700;'
          + (done ? 'background:rgba(22,163,74,.12);color:#15803d' : 'background:#16a34a;color:#fff'),
      });
      btn.addEventListener('click', e => { e.stopPropagation(); _toggleMentionCheck(n.id); });
      return btn;
    }
    return el('span', {
      class: 'conv-note-mention',
      text: '@' + userLabel(un) + (done ? ' ✓' : ''),
      style: base + 'background:rgba(22,163,74,.12);color:#15803d',
    });
  }

  // "Marki seen" (2026-08-15, Ayoub — ported from support2): every note gets an
  // explicit seen button, tagged or not. Tagging you is what makes it COUNT in
  // Mes notes; the button is just how you say you read it, so clearing a note
  // no longer means hunting for the tiny @chip.
  function _noteSeenBtnEl(n) {
    const meUn = _mentionKeyFor(n);
    const seen = !!(n.mentionChecks || {})[meUn];
    const btn = el('button', {
      type: 'button',
      class: 'note-seen-btn' + (seen ? ' done' : ''),
      text: seen ? '✓ Seen' : 'Marki seen',
      title: seen ? "Klik bach trje3ha 'mazal'" : "Rah cheftiha — kat-mse7 mn 'Mes notes'",
      style: 'margin-top:6px;align-self:flex-start;font:700 10.5px/1 inherit;padding:5px 10px;'
        + 'border-radius:999px;cursor:pointer;'
        + (seen
          ? 'border:1px solid var(--border);background:transparent;color:var(--text-muted)'
          : 'border:1px solid #16a34a;background:var(--surface);color:#15803d'),
    });
    btn.addEventListener('click', e => { e.stopPropagation(); _toggleMentionCheck(n.id); });
    return btn;
  }

  // ─── Note edit + edit trail (2026-09-11, Ayoub) ───────────────────────
  // A team note is evidence — it gets corrected, never silently rewritten.
  // Saving pushes the old wording onto the server's `edits` trail, and the
  // card grows a "modifié" line that expands every previous version in place
  // so anyone can see what the note used to say and who changed it.
  // Support2-origin rows are editable too (the PATCH relays over the bridge),
  // but only by an admin — we can't prove authorship across platforms.
  function _noteCanEdit(n) {
    if (state.me?.role === 'admin') return true;
    if (n.platform) return false;
    const uid = n.author?.userId;
    return !uid || uid === state.me?.id;
  }

  function _noteEditTrailEl(n) {
    const edits = Array.isArray(n.edits) ? n.edits : [];
    if (!edits.length) return null;
    const last = edits[edits.length - 1];
    const wrap = el('div', { class: 'note-edit-trail', style: 'margin-top:5px' });
    const label = '✎ modifié'
      + (last && last.byName ? ' · ' + last.byName : '')
      + (last && last.at ? ' · ' + formatNoteTime(last.at) : '')
      + (edits.length > 1 ? ' (' + edits.length + '×)' : '');
    const toggle = el('button', {
      type: 'button',
      text: label,
      title: "Chouf l'versions l-9dam",
      style: 'background:none;border:0;padding:0;cursor:pointer;text-align:left;'
        + 'font:600 10.5px/1.4 inherit;color:var(--text-muted);text-decoration:underline dotted',
    });
    const body = el('div', {
      class: 'hidden',
      style: 'margin-top:5px;border-left:2px solid var(--border);padding-left:8px;'
        + 'display:flex;flex-direction:column;gap:6px',
    });
    for (let i = edits.length - 1; i >= 0; i--) {
      const e = edits[i] || {};
      body.appendChild(el('div', {}, [
        el('div', {
          text: 'v' + (i + 1) + ' · ' + userLabel(e.by, e.byName || e.by || '?') + (e.at ? ' · ' + formatNoteTime(e.at) : ''),
          style: 'font:700 10px/1.3 inherit;color:var(--text-muted)',
        }),
        el('div', {
          text: e.previous || '',
          style: 'font-size:11.5px;white-space:pre-wrap;color:var(--text-muted);opacity:.9',
        }),
      ]));
    }
    toggle.addEventListener('click', ev => { ev.stopPropagation(); body.classList.toggle('hidden'); });
    wrap.appendChild(toggle);
    wrap.appendChild(body);
    return wrap;
  }

  async function _saveNoteEdit(n, content) {
    const chatId = n.chatId || state.selectedChatId;
    if (!chatId) { toast('Chat inconnu', 'error'); return false; }
    try {
      const r = await api('/api/notes/' + encodeURIComponent(n.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, content }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.error) { toast(j.error || 'Edit failed', 'error'); return false; }
      return true;
    } catch (err) {
      toast('Edit failed: ' + (err.message || err), 'error');
      return false;
    }
  }

  // Swap the note body for a textarea in place. The @ autocomplete rides along
  // so adding @amina on an edit really tags her (the server re-parses).
  function _startNoteEdit(n, bodyEl, onSaved) {
    if (!bodyEl || bodyEl._noteEditing) return;
    bodyEl._noteEditing = true;
    const host = bodyEl.parentElement;
    const ta = el('textarea', {
      class: 'note-edit-input',
      style: 'width:100%;min-height:64px;resize:vertical;font:inherit;font-size:12.5px;'
        + 'padding:6px 8px;border:1px solid var(--border);border-radius:8px;'
        + 'background:var(--surface);color:var(--text)',
    });
    const shown = adminMentionHandles(n.content || n.text || '');
    ta.value = shown;
    const btnCss = 'font:700 10.5px/1 inherit;padding:5px 10px;border-radius:999px;cursor:pointer;';
    const save = el('button', {
      type: 'button', text: 'Sauvegarder',
      style: btnCss + 'border:1px solid #16a34a;background:#16a34a;color:#fff',
    });
    const cancel = el('button', {
      type: 'button', text: 'Annuler',
      style: btnCss + 'border:1px solid var(--border);background:transparent;color:var(--text-muted)',
    });
    const bar = el('div', { style: 'display:flex;gap:6px;margin-top:6px' }, [save, cancel]);
    bodyEl.classList.add('hidden');
    host.insertBefore(ta, bodyEl.nextSibling);
    host.insertBefore(bar, ta.nextSibling);
    try { _attachMentionAc(ta); } catch { /* autocomplete is a nicety, never a blocker */ }
    const close = () => {
      ta.remove(); bar.remove();
      bodyEl.classList.remove('hidden');
      bodyEl._noteEditing = false;
    };
    cancel.addEventListener('click', ev => { ev.stopPropagation(); close(); });
    save.addEventListener('click', async ev => {
      ev.stopPropagation();
      const next = ta.value.trim();
      if (!next) { toast('Note khawya', 'error'); return; }
      if (next === shown) { close(); return; }
      save.disabled = true; save.textContent = '…';
      const ok = await _saveNoteEdit(n, next);
      save.disabled = false; save.textContent = 'Sauvegarder';
      if (!ok) return;
      close();
      if (typeof onSaved === 'function') onSaved();
    });
    ta.focus();
  }

  function _noteEditBtnEl(n, bodyEl, onSaved) {
    const btn = el('button', {
      type: 'button',
      class: 'note-edit-btn',
      title: 'Editi had l\'note',
      'aria-label': 'Edit note',
      style: 'background:none;border:0;padding:2px;cursor:pointer;color:var(--text-muted);'
        + 'display:inline-flex;align-items:center;flex:none',
    }, [lucideIcon('pencil')]);
    btn.addEventListener('click', ev => { ev.stopPropagation(); _startNoteEdit(n, bodyEl, onSaved); });
    return btn;
  }

  function buildInlineChatNote(n) {
    // The id is what the "Kayna note 3lik hna" bar scrolls to.
    const row = el('div', { class: 'note-card-row', 'data-note-id': String(n.id || '') });
    const card = el('div', { class: 'note-card' });
    const head = el('div', { class: 'note-card-head', style: 'display:flex;align-items:center;gap:6px' });
    head.appendChild(el('span', { text: '📝 Note privée · team ghir' }));
    if (n.platform === 'support2') {
      head.appendChild(el('span', {
        text: 'Support2',
        style: 'padding:1px 7px;border-radius:8px;font-size:10px;font-weight:700;background:#16a34a;color:#fff;letter-spacing:.4px;flex:none'
      }));
    }
    const inlineBody = el('div', { class: 'note-card-body', text: adminText(n.content || '') });
    const inlineAudio = _noteAudioEl(n.audio);
    if (_noteCanEdit(n)) {
      head.appendChild(_noteEditBtnEl(n, inlineBody, () => {
        refreshNotesBadge();
        if (E.main().classList.contains('with-conv-details') && state.convDetailsTab === 'notes') loadNotes();
      }));
    }
    card.appendChild(head);
    card.appendChild(inlineBody);
    if (inlineAudio) card.appendChild(inlineAudio);
    const inlineTrail = _noteEditTrailEl(n);
    if (inlineTrail) card.appendChild(inlineTrail);
    const noteMentions = Array.isArray(n.mentions) ? n.mentions : [];
    if (noteMentions.length) {
      const mrow = el('div', { style: 'display:flex;flex-wrap:wrap;gap:4px;margin-top:6px' });
      for (const un of noteMentions) mrow.appendChild(_noteMentionChipEl(n, un));
      card.appendChild(mrow);
    }
    card.appendChild(_noteSeenBtnEl(n));
    const meta = el('div', { class: 'note-card-meta' });
    const who = n.author ? userLabel(n.author.username, n.author.displayName || n.author.username || '') : '';
    if (who) meta.appendChild(el('span', { text: who }));
    if (n.createdAt) meta.appendChild(el('span', { text: (who ? ' · ' : '') + formatNoteTime(n.createdAt) }));
    card.appendChild(meta);
    row.appendChild(card);
    return row;
  }

  function buildNoteBubble(m) {
    const row = el('div', { class: 'note-card-row' });
    const card = el('div', { class: 'note-card', title: m.actorUsername ? '@' + userLabel(m.actorUsername) : '' });
    if (m.noteHeader) card.appendChild(el('div', { class: 'note-card-head', text: adminText(m.noteHeader) }));
    card.appendChild(el('div', { class: 'note-card-body', text: adminText(m.text || '') }));
    const bAudio = _noteAudioEl(m.audio);
    if (bAudio) card.appendChild(bAudio);
    const bMentions = Array.isArray(m.mentions) ? m.mentions : [];
    if (bMentions.length) {
      const mrow = el('div', { style: 'display:flex;flex-wrap:wrap;gap:4px;margin-top:6px' });
      for (const un of bMentions) {
        mrow.appendChild(el('span', {
          text: '@' + userLabel(un),
          style: 'padding:1px 7px;border-radius:8px;font-size:10.5px;font-weight:600;background:rgba(22,163,74,.12);color:#15803d'
        }));
      }
      card.appendChild(mrow);
    }
    const meta = el('div', { class: 'note-card-meta' });
    meta.appendChild(el('span', { class: 'note-card-tag', text: 'note interne' }));
    // Who wrote it — a team note without an author is an anonymous claim.
    const who = userLabel(m.actorUsername, m.authorName || m.actorUsername || '');
    if (who) meta.appendChild(el('span', { text: ' · ' + who }));
    if (m.timestamp) meta.appendChild(el('span', { text: ' · ' + formatTime(m.timestamp) }));
    card.appendChild(meta);
    row.appendChild(card);
    return row;
  }
  function friendlyDayLabel(d) {
    if (!d) return '';
    const today = new Date();
    const tdKey = MA.fmt('ca-day', 'en-CA', {}, today);
    const ydKey = MA.fmt('ca-day', 'en-CA', {}, today.getTime() - 86400000);
    const dKey = MA.fmt('ca-day', 'en-CA', {}, d);
    if (dKey === tdKey) return 'Today';
    if (dKey === ydKey) return 'Yesterday';
    return MA.fmt('gb-wdmy', 'en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }, d);
  }

  // ─── Reply / quote helpers ────────────────────────────────────────────
  // WhatsApp Web's quoted-pill style: emoji prefix per media type, plain
  // text otherwise, truncated at 80 chars.
  function quotePreviewFor(m) {
    if (!m) return '';
    if (m.text && m.text.trim()) return m.text.replace(/\s+/g, ' ').slice(0, 80);
    const map = { audio: '🎵 Voice note', image: '📷 Photo', video: '🎥 Video', document: '📎 ' + (m.fileName || 'Document') };
    return map[m.type] || '[message]';
  }
  function quoteAuthorFor(m, fallback) {
    if (!m) return fallback || 'Member';
    if (m.fromMe) return 'You';
    return m.pushName || m.fromName || fallback || 'Member';
  }
  state.replyingTo = null; // { id, text, type, mediaUrl, fileName, fromMe, pushName }
  function startReply(m) {
    if (!m) return;
    state.replyingTo = {
      id: m.id,
      text: m.text || '',
      type: m.type || 'text',
      mediaUrl: m.mediaUrl || '',
      fileName: m.fileName || '',
      fromMe: !!m.fromMe,
      pushName: m.pushName || ''
    };
    renderReplyPreview();
    if (E.composeInput) E.composeInput.focus();
  }

  // Inline edit for an Inbox (WhatsApp) bubble. Swaps the .text element
  // for a textarea + Save/Cancel; PATCH /api/inbox/messages/:id pushes
  // the edit through Baileys to WhatsApp. Mirrors the Team chat editor.
  // "Corriger" — a true edit is impossible on the WhatsApp Cloud API (Meta has
  // no edit endpoint). Instead we open an inline form pre-filled with the WRONG
  // text; on send we ship the corrected version as a reply that QUOTES the wrong
  // message via the same /api/inbox/send replyTo path the Reply button uses
  // (cloud-api → context.message_id). The quote alone ties the fix to the
  // mistake (no marker prefix). The original stays on the client's phone,
  // unchanged; the correction lands right under it.
  function startInboxCorrection(m, row) {
    if (!m?.id || !row) return;
    const bubble = row.querySelector('.bubble');
    const textEl = bubble?.querySelector('.text');
    if (!bubble) return;
    if (!textEl) { toast('Seuls les messages texte peuvent être corrigés', 'error'); return; }
    if (bubble.querySelector('.inbox-edit-form')) return;
    const form = el('div', { class: 'inbox-edit-form' });
    const hint = el('div', { class: 'inbox-correction-hint', text: "L'original reste chez le client — ceci envoie une correction qui le cite." });
    const ta = el('textarea', { class: 'inbox-edit-textarea', rows: 2 });
    ta.value = m.text || '';
    const btns = el('div', { class: 'inbox-edit-btns' });
    const save = el('button', { class: 'btn btn-primary btn-sm', type: 'button', text: 'Envoyer la correction' });
    const cancel = el('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: 'Annuler' });
    btns.appendChild(save); btns.appendChild(cancel);
    form.appendChild(hint); form.appendChild(ta); form.appendChild(btns);
    textEl.style.display = 'none';
    bubble.insertBefore(form, textEl);
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);
    function done(restore) { form.remove(); if (restore) textEl.style.display = ''; }
    cancel.addEventListener('click', () => done(true));
    ta.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); done(true); }
      else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save.click(); }
      e.stopPropagation();
    });
    save.addEventListener('click', async () => {
      const newText = ta.value.trim();
      if (!newText) { toast('Le message ne peut pas être vide', 'error'); return; }
      if (newText === m.text) { done(true); return; } // unchanged → nothing to correct
      save.disabled = true; cancel.disabled = true;
      // Send the fix as a quoted reply to the wrong message — same proven path
      // as the Reply button (/api/inbox/send with replyTo → context.message_id).
      // No marker prefix: the quote alone signals the correction.
      const correctedText = newText;
      try {
        const r = await fetchT('/api/inbox/send', {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ chatId: m.chatId, text: correctedText, replyTo: m.id }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j.error) {
          toast(j.error || 'Échec de la correction', 'error');
          save.disabled = false; cancel.disabled = false;
          return;
        }
        if (j.replyResolved === false) {
          // Original was pruned from our store → Meta got a plain message, not a
          // quoted reply. Still delivered; just warn the agent.
          toast('Message original introuvable — correction envoyée sans citation', 'warning');
        }
        done(true); // close form; the original bubble stays unchanged
        // Optimistic render if still on the same chat (mirrors sendText()).
        if (state.selectedChatId === m.chatId && j.message) {
          state.messages.push(j.message);
          renderMessages();
        }
      } catch {
        toast('Échec de la correction — réessayez', 'error');
        save.disabled = false; cancel.disabled = false;
      }
    });
  }

  async function deleteInboxMessage(m) {
    if (!m?.id) return;
    const isInbound = !m.fromMe;
    const ask = isInbound
      ? 'Remove this incoming message from the Leads7 view?\n\nIt stays on the customer\'s phone — WhatsApp can\'t un-send a message they sent.'
      : 'Delete this message for everyone? This sends a WhatsApp tombstone to the recipient.';
    if (!confirm(ask)) return;
    try {
      const r = await fetchT('/api/inbox/messages/' + encodeURIComponent(m.id), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(j.error || 'Delete failed', 'error'); return; }
      // Outbound: only drop locally once WhatsApp acknowledged the revoke, so
      // our view stays in sync with the recipient's. Inbound: the server has
      // already removed it locally (localOnly) — drop our copy to match.
      if (j.waOk === true || j.localOnly === true) {
        removeInboxMessageLocal(m.id);
        toast(isInbound ? 'Message removed from this view' : 'Message deleted', 'success');
      } else {
        toast('WhatsApp rejected the delete: ' + (j.waError || 'unknown') + ' — the message stays in both views.', 'error');
      }
    } catch {
      toast('Échec de la suppression — réessayez', 'error');
    }
  }

  // Live message store is the flat `state.messages` array for the currently
  // open chat (mirrors onReactionChanged). The delete socket event only
  // carries the messageId, so we match by id within the open chat's list.
  function removeInboxMessageLocal(messageId) {
    if (!messageId) return;
    const arr = state.messages || [];
    const i = arr.findIndex(x => x && x.id === messageId);
    if (i < 0) return;
    arr.splice(i, 1);
    renderMessages();
  }

  function updateInboxMessageLocal({ chatId, messageId, text, editedAt }) {
    if (!messageId) return;
    if (state.selectedChatId !== chatId) return; // not the visible chat — list rebuilds on next load
    const arr = state.messages || [];
    const i = arr.findIndex(x => x && x.id === messageId);
    if (i < 0) return;
    arr[i] = { ...arr[i], text, editedAt };
    renderMessages();
  }
  // Mirrors delivery / read / failed updates that the server fires via
  // `message:status`. Without this listener the bubble keeps the
  // optimistic ✓✓ icon even after Meta rejects the send (e.g. outside
  // the 24h customer-service window), which masked real send failures.
  function updateInboxMessageStatusLocal({ chatId, messageId, status, statusError }) {
    if (!messageId) return;
    if (state.selectedChatId !== chatId) return; // not the visible chat — list rebuilds on next load
    const arr = state.messages || [];
    const i = arr.findIndex(x => x && x.id === messageId);
    if (i < 0) return;
    arr[i] = { ...arr[i], status, statusError: statusError || null };
    renderMessages();
  }
  function cancelReply() {
    state.replyingTo = null;
    renderReplyPreview();
  }
  function renderReplyPreview() {
    const r = state.replyingTo;
    // Inbox composer's pill (mounted at boot in index.html).
    if (E.replyPreview) {
      if (!r) {
        E.replyPreview.classList.add('hidden');
      } else {
        E.replyPreview.classList.remove('hidden');
        if (E.replyPreviewAuthor) E.replyPreviewAuthor.textContent = quoteAuthorFor(r);
        if (E.replyPreviewSnippet) E.replyPreviewSnippet.textContent = quotePreviewFor(r);
      }
    }
  }
  // Build the in-bubble quoted block. Click → scroll to original message.
  function buildQuotedBlock(quoted) {
    if (!quoted || (!quoted.text && !quoted.type)) return null;
    const block = el('div', { class: 'quoted-block' });
    block.appendChild(el('div', { class: 'quoted-block-bar' }));
    const body = el('div', { class: 'quoted-block-body' });
    body.appendChild(el('div', { class: 'quoted-block-author', text: quoteAuthorFor(quoted) }));
    body.appendChild(el('div', { class: 'quoted-block-snippet', text: quotePreviewFor(quoted) }));
    block.appendChild(body);
    block.addEventListener('click', () => {
      const target = quoted.id && document.querySelector('[data-msg-id="' + CSS.escape(quoted.id) + '"]');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.add('msg-flash');
        setTimeout(() => target.classList.remove('msg-flash'), 1200);
      }
    });
    return block;
  }

  // Multi-select state for forward / copy. WhatsApp-style: hover a
  // bubble → small ◯ button appears → click adds to selection and
  // shows the bottom toolbar; clicking other bubbles after that
  // toggles them in place. Cancel clears the set.
  state.selectedMessageIds = state.selectedMessageIds || new Set();

  // WhatsApp-style quick-react menu. Same 6 emojis WA's mobile UI surfaces.
  const QUICK_REACT_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

  // Render an emoji-chip strip under a message bubble. Aggregates by emoji
  // (same emoji from multiple senders → one chip + count). If the agent
  // already reacted with an emoji, that chip is highlighted; clicking it
  // removes the reaction. Clicking a non-active chip switches the agent's
  // reaction to that emoji.
  function buildReactionStrip(m) {
    const strip = el('div', { class: 'msg-reactions' });
    const list = Array.isArray(m.reactions) ? m.reactions : [];
    if (!list.length) { strip.classList.add('hidden'); return strip; }
    // Group by emoji, with mine flagged so we can highlight my chip.
    const byEmoji = new Map();
    let myEmoji = null;
    for (const r of list) {
      if (!r.emoji) continue;
      const slot = byEmoji.get(r.emoji) || { emoji: r.emoji, count: 0, mine: false };
      slot.count += 1;
      if (r.fromMe) { slot.mine = true; myEmoji = r.emoji; }
      byEmoji.set(r.emoji, slot);
    }
    for (const slot of byEmoji.values()) {
      const chip = el('button', {
        class: 'msg-reaction-chip' + (slot.mine ? ' is-mine' : ''),
        type: 'button',
        title: slot.mine ? 'Click to remove your reaction' : 'React with ' + slot.emoji,
      });
      chip.appendChild(el('span', { class: 'mrc-emoji', text: slot.emoji }));
      if (slot.count > 1) chip.appendChild(el('span', { class: 'mrc-count', text: String(slot.count) }));
      chip.addEventListener('click', (ev) => {
        ev.preventDefault(); ev.stopPropagation();
        // Click own emoji = remove; click other = switch to that emoji
        const send = slot.mine ? '' : slot.emoji;
        sendInboxReaction(m, send);
      });
      strip.appendChild(chip);
    }
    return strip;
  }

  // Hover-reveal quick-react bar with the 6 WA default emojis. Anchored to
  // the side of the bubble away from the screen edge (top-right for them,
  // top-left for me). My current pick gets a coloured ring.
  function buildQuickReactBar(m) {
    const list = Array.isArray(m.reactions) ? m.reactions : [];
    const myEmoji = list.find(r => r.fromMe)?.emoji || '';
    const bar = el('div', { class: 'msg-quick-react-bar' });
    for (const e of QUICK_REACT_EMOJIS) {
      const btn = el('button', {
        class: 'mqr-btn' + (e === myEmoji ? ' is-mine' : ''),
        type: 'button',
        title: e === myEmoji ? 'Remove your ' + e : 'React with ' + e,
        text: e,
      });
      btn.addEventListener('click', (ev) => {
        ev.preventDefault(); ev.stopPropagation();
        sendInboxReaction(m, e === myEmoji ? '' : e);
      });
      bar.appendChild(btn);
    }
    return bar;
  }

  async function sendInboxReaction(m, emoji) {
    if (!m?.id) return;
    try {
      const r = await api('/api/inbox/messages/' + encodeURIComponent(m.id) + '/react', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji: emoji || '' }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast(j.error || 'React failed', 'error');
        return;
      }
      // Server emits message:reaction-changed via socket → onReactionChanged
      // updates state + re-renders. Don't double-patch state locally.
    } catch (err) {
      toast('Erreur réseau — vérifiez votre connexion et réessayez', 'error');
    }
  }

  // Socket handler — server fires this for both in-bound (peer reacted) +
  // out-bound (we reacted) flows so the UI converges with the same code path.
  function onReactionChanged({ chatId, messageId, reactions }) {
    if (!chatId || !messageId) return;
    if (state.selectedChatId !== chatId) return; // not the visible chat — message list will rebuild on next load
    const idx = (state.messages || []).findIndex(m => m && m.id === messageId);
    if (idx < 0) return;
    state.messages[idx] = { ...state.messages[idx], reactions: reactions || [] };
    // Surgical re-render of the affected bubble only — no full message-list
    // rebuild. Find the row by data-msg-id, replace it in place.
    const row = document.querySelector('[data-msg-id="' + CSS.escape(messageId) + '"]');
    if (!row) return;
    const isGroup = !!state.selectedChat?.isGroup;
    const newRow = buildBubble(state.messages[idx], isGroup);
    row.replaceWith(newRow);
    refreshIcons(newRow);
  }

  // Outbound-status icon. Server tracks status ∈ {sent, delivered, read,
  // failed} via Meta's status webhook (server.js onStatus). Without this,
  // the bubble would always render ✓✓ even when Meta rejected the send,
  // hiding real failures (e.g. 24h customer-service window violations).
  function buildStatusIcon(m) {
    const status = m.status || 'sent';
    // SMS rows sit 'queued' until the office bridge polls them off the outbox
    // (per-SIM pacing: ~1 SMS / 2 min). A clock is honest; the old fallthrough
    // rendered a fake "Sent" tick while the SMS hadn't left the queue yet.
    if (status === 'queued') {
      const icon = lucideIcon('clock');
      icon.classList.add('msg-status', 'msg-status-queued');
      icon.style.opacity = '0.7';
      icon.setAttribute('title', "En file d'attente SMS — envoi progressif");
      return icon;
    }
    if (status === 'failed') {
      const wrap = el('span', { class: 'msg-status msg-status-failed' });
      wrap.appendChild(lucideIcon('alert-circle'));
      const err = m.statusError;
      const reason = err && (err.message || err.title)
        ? `Send failed — ${err.title || ''}${err.title && err.message ? ': ' : ''}${err.message || ''}`
        : 'Send failed — likely outside the 24h customer-service window (need an approved WABA template for first-touch)';
      wrap.title = reason;
      return wrap;
    }
    if (status === 'read') {
      const icon = lucideIcon('check-check');
      icon.classList.add('msg-status', 'msg-status-read');
      icon.setAttribute('title', 'Read');
      return icon;
    }
    if (status === 'delivered') {
      const icon = lucideIcon('check-check');
      icon.classList.add('msg-status', 'msg-status-delivered');
      icon.setAttribute('title', 'Delivered');
      return icon;
    }
    // sent (or unknown) → single check
    const icon = lucideIcon('check');
    icon.classList.add('msg-status', 'msg-status-sent');
    icon.setAttribute('title', 'Sent');
    return icon;
  }

  // Custom WhatsApp-style audio player. A hidden <audio> drives playback while
  // the visible UI is a round play/pause button, a seekable faux-waveform that
  // fills as it plays, and a live time counter (elapsed while playing, total
  // when idle). Keeps `src` untouched so 3CX recordings still stream
  // route → 302 → MinIO. Reusable for voice notes too.
  // Playback-speed control shared by every listen-back audio surface (voice
  // notes, call recordings, team-chat voice). Sticky/global like WhatsApp: the
  // chosen rate persists in localStorage and applies to all current + future
  // players. Steps cycle 1× → 1.5× → 2× → 1×.
  const AUDIO_SPEEDS = [1, 1.5, 2];
  function _audioSpeedLabel(r) { return (r === 1 ? '1' : String(r)) + '×'; }
  function _getAudioSpeed() { const v = parseFloat(localStorage.getItem('lougha_audio_speed')); return (v === 1.5 || v === 2) ? v : 1; }
  function _applyAudioSpeed(rate) {
    try { localStorage.setItem('lougha_audio_speed', String(rate)); } catch (_) {}
    document.querySelectorAll('.audio-player').forEach((p) => {
      const a = p.querySelector('audio'); if (a) a.playbackRate = rate;
      const s = p.querySelector('.audio-speed'); if (s) s.textContent = _audioSpeedLabel(rate);
    });
  }

  function buildAudioPlayer(src, opts) {
    opts = opts || {};
    const wrap = document.createElement('div');
    wrap.className = 'audio-player';

    const audio = document.createElement('audio');
    // No src prevents every unplayed vocal from creating a media request.
    audio.preload = 'none';
    let pendingSeek = null;
    wrap._setSource = next => { src = next; };
    const ensureSource = () => {
      audio.preload = 'auto';
      if (!audio.hasAttribute('src')) audio.src = src;
      else if (audio.error) { audio.src = src; audio.load(); }
    };
    if (opts.mime) audio.setAttribute('type', opts.mime);
    audio.style.display = 'none';

    const PLAY = '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>';
    const PAUSE = '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><path d="M7 5h3.2v14H7zM13.8 5H17v14h-3.2z" fill="currentColor"/></svg>';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'audio-pp';
    btn.setAttribute('aria-label', 'Play');
    btn.innerHTML = PLAY;

    // Deterministic faux-waveform — a real waveform needs decodeAudioData on the
    // raw bytes, which the cross-origin presigned MinIO URL can't expose (no
    // CORS). Seed the bar heights from `src` so each clip has a stable, organic
    // shape instead of a flat bar.
    const BARS = 40;
    const wave = document.createElement('div');
    wave.className = 'audio-wave';
    let seed = 0; for (let i = 0; i < src.length; i++) seed = (seed * 31 + src.charCodeAt(i)) >>> 0;
    const rnd = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    const bars = [];
    for (let i = 0; i < BARS; i++) {
      const b = document.createElement('span');
      b.className = 'audio-bar';
      b.style.height = (22 + Math.round(rnd() * 78)) + '%';
      wave.appendChild(b);
      bars.push(b);
    }

    const time = document.createElement('span');
    time.className = 'audio-time';
    time.textContent = '0:00';

    const fmt = (s) => { s = Math.max(0, Math.floor(s || 0)); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };
    const paint = (r) => { const f = Math.round(r * BARS); for (let i = 0; i < BARS; i++) bars[i].classList.toggle('played', i < f); };

    // Show the live position once there's progress (playing OR scrubbed while
    // paused); fall back to the real duration when idle/reset — like WhatsApp.
    // With preload:'none' the media metadata isn't loaded yet, so use the
    // caller-supplied durationSec (known from call_logs) instead of showing 0:00.
    const renderTime = () => {
      if (audio.currentTime > 0.05) { time.textContent = fmt(audio.currentTime); return; }
      if (isFinite(audio.duration) && audio.duration > 0) { time.textContent = fmt(audio.duration); return; }
      time.textContent = opts.durationSec ? fmt(opts.durationSec) : '0:00';
    };
    renderTime();
    const sync = () => { paint(audio.duration ? audio.currentTime / audio.duration : 0); renderTime(); };

    // Some browsers reset playbackRate when fresh media loads — re-assert the
    // sticky speed once metadata arrives so the rate survives the first play.
    audio.addEventListener('loadedmetadata', () => { audio.playbackRate = _getAudioSpeed(); renderTime(); });
    audio.addEventListener('timeupdate', sync);
    audio.addEventListener('seeking', sync);
    audio.addEventListener('play', () => {
      btn.innerHTML = PAUSE; btn.setAttribute('aria-label', 'Pause');
      // Only one recording plays at a time (matches WhatsApp).
      document.querySelectorAll('.audio-player audio').forEach((o) => { if (o !== audio) o.pause(); });
    });
    audio.addEventListener('pause', () => { btn.innerHTML = PLAY; btn.setAttribute('aria-label', 'Play'); renderTime(); });
    audio.addEventListener('ended', () => { btn.innerHTML = PLAY; btn.setAttribute('aria-label', 'Play'); audio.currentTime = 0; paint(0); renderTime(); });
    // Surface a failed load (404 / 502 / expired presign) instead of a silent
    // dead button; clearing on loadstart lets a tap retry (a fresh presign).
    audio.addEventListener('error', () => { btn.innerHTML = PLAY; btn.setAttribute('aria-label', 'Recording unavailable — tap to retry'); wrap.classList.add('audio-error'); });
    audio.addEventListener('loadstart', () => wrap.classList.remove('audio-error'));

    btn.addEventListener('click', () => { if (audio.paused || audio.error) { ensureSource(); audio.play().catch(() => {}); } else audio.pause(); });

    // Guard duration: webm/ogg voice notes can report Infinity until played, so
    // a pre-load scrub must be ignored rather than seeking to Infinity.
    const applyPendingSeek = () => {
      if (pendingSeek === null || audio.readyState < 2) return;
      try { audio.currentTime = pendingSeek; pendingSeek = null; } catch { /* wait for seekable data */ }
    };
    audio.addEventListener('loadeddata', applyPendingSeek);
    audio.addEventListener('canplay', applyPendingSeek);
    const seekTo = x => {
      const duration = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : Number(opts.durationSec) || 0;
      if (!(duration > 0)) return;
      const rc = wave.getBoundingClientRect();
      const r = Math.min(1, Math.max(0, (x - rc.left) / rc.width));
      ensureSource();
      if (audio.readyState < 2) { pendingSeek = r * duration; if (audio.networkState !== 2) audio.load(); }
      else { pendingSeek = null; audio.currentTime = r * duration; }
      paint(r);
    };
    let drag = false;
    wave.addEventListener('pointerdown', (e) => { drag = true; try { wave.setPointerCapture(e.pointerId); } catch (_) {} seekTo(e.clientX); });
    wave.addEventListener('pointermove', (e) => { if (drag) seekTo(e.clientX); });
    const endDrag = (e) => { drag = false; try { wave.releasePointerCapture(e.pointerId); } catch (_) {} };
    wave.addEventListener('pointerup', endDrag);
    wave.addEventListener('pointercancel', endDrag);

    // Speed pill — tap cycles 1× → 1.5× → 2×. The choice is sticky/global, so a
    // new player opens at whatever the agent last picked.
    const speed = document.createElement('button');
    speed.type = 'button';
    speed.className = 'audio-speed';
    speed.setAttribute('aria-label', 'Playback speed');
    const initRate = _getAudioSpeed();
    audio.playbackRate = initRate;
    speed.textContent = _audioSpeedLabel(initRate);
    speed.addEventListener('click', (e) => {
      e.stopPropagation();
      const cur = _getAudioSpeed();
      const next = AUDIO_SPEEDS[(AUDIO_SPEEDS.indexOf(cur) + 1) % AUDIO_SPEEDS.length];
      _applyAudioSpeed(next);
    });

    wrap.appendChild(audio); wrap.appendChild(btn); wrap.appendChild(wave); wrap.appendChild(time); wrap.appendChild(speed);
    return wrap;
  }

  // ── Email channel (spec 2026-07-02) ──────────────────────────────────
  // Compose modal + email bubbles. Emails live as type:'email' rows in the
  // same conversation as WhatsApp messages.
  async function openEmailCompose() {
    const chatId = state.selectedChatId;
    if (!chatId || !chatId.endsWith('@s.whatsapp.net')) {
      toast('Email dispo seulement sur une conversation WhatsApp 1-1', 'warning');
      return;
    }
    openModal('emailComposeModal');
    const banner = document.getElementById('emailStatusBanner');
    const sendBtn = E.emailSendBtn;
    banner.classList.add('hidden');
    sendBtn.disabled = true;
    document.getElementById('emailSubject').value = '';
    document.getElementById('emailBody').value = '';
    document.getElementById('emailFiles').value = '';
    document.getElementById('emailTo').value = '';
    document.getElementById('emailAiInstr').value = '';
    try {
      // Prefill from the lead (cached fetch — same source as the pipeline bar).
      const [st, lead] = await Promise.all([
        api('/api/email/status').then(r => r.json()).catch(() => null),
        fetchLeadForChat(chatId, { useCache: true }).catch(() => null),
      ]);
      document.getElementById('emailTo').value = lead?.email || '';
      const canSend = !!(st && st.enabled && st.connected);
      banner.classList.toggle('hidden', canSend);
      if (!canSend) {
        banner.textContent = !st?.enabled
          ? 'Email désactivé sur ce serveur (EMAIL_ENABLED).'
          : 'support@ non connecté — un admin doit ouvrir /api/email/oauth/start';
      }
      sendBtn.disabled = !canSend;
    } catch (_) {
      banner.textContent = 'Statut email indisponible — réessaie.';
      banner.classList.remove('hidden');
    }
  }

  async function sendEmailFromModal() {
    const chatId = state.selectedChatId;
    if (!chatId) return;
    const to = document.getElementById('emailTo').value.trim();
    const subject = document.getElementById('emailSubject').value.trim();
    const body = document.getElementById('emailBody').value.trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(to)) { toast('Adresse email invalide', 'error'); return; }
    if (!subject) { toast('Sujet requis', 'error'); return; }
    if (!body) { toast('Message requis', 'error'); return; }
    const fd = new FormData();
    fd.append('chatId', chatId);
    fd.append('to', to);
    fd.append('subject', subject);
    fd.append('body', body);
    for (const f of document.getElementById('emailFiles').files) fd.append('files', f);
    const btn = E.emailSendBtn;
    btn.disabled = true;
    try {
      // fetchT (not api()): FormData must keep its own multipart boundary.
      const r = await fetchT('/api/email/send', { method: 'POST', headers: authHeaders(), body: fd }, 90000);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || ('HTTP ' + r.status));
      toast('Email envoyé ✉️', 'success');
      closeModal('emailComposeModal');
      loadMessages({ force: true }).catch(() => {});
    } catch (err) {
      toast('Email: ' + (err?.message || err), 'error');
    } finally {
      btn.disabled = false;
    }
  }

  // AI draft — Gemini reads the conversation (+ optional instruction) and
  // fills Sujet + Message. The agent reviews then clicks Envoyer; a second
  // click regenerates. Port of support2's proven flow (c56d03d).
  async function emailAiDraft() {
    const chatId = state.selectedChatId;
    if (!chatId) return;
    const btn = E.emailAiBtn;
    const orig = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = 'Rédaction…';
    try {
      const r = await api('/api/email/conversation-ai-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, instruction: document.getElementById('emailAiInstr').value.trim() }),
        timeoutMs: 70000,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || ('HTTP ' + r.status));
      document.getElementById('emailSubject').value = j.subject || '';
      document.getElementById('emailBody').value = j.body || '';
      toast('Brouillon AI prêt — relis et envoie ✨', 'success');
    } catch (err) {
      toast('AI: ' + (err?.message || err), 'error');
    } finally {
      btn.disabled = false;
      btn.innerHTML = orig;
      refreshIcons(btn.parentElement || btn);
    }
  }

  function buildEmailBubble(m) {
    const ed = m.emailData || {};
    const fromMe = !!m.fromMe;
    const row = el('div', { class: 'message-row' + (fromMe ? ' fromMe' : '') });
    if (m.id) row.setAttribute('data-msg-id', m.id);
    const bubble = el('div', { class: 'email-bubble' + (fromMe ? ' from-me' : ' from-them') });
    bubble.appendChild(el('div', { class: 'email-bubble-head' }, [
      lucideIcon('mail'),
      document.createTextNode((fromMe ? 'Email' : 'Réponse email') + (ed.subject ? ' · ' + ed.subject : '')),
    ]));
    bubble.appendChild(el('div', { class: 'email-bubble-body', text: m.text || '' }));
    const atts = ed.attachments || [];
    if (atts.length) {
      bubble.appendChild(el('div', { class: 'email-bubble-atts', text: '📎 ' + atts.map(a => a.name).filter(Boolean).join(', ') }));
    }
    const metaBits = [];
    if (m.sentBy) metaBits.push(userLabel(m.sentBy));
    metaBits.push(MA.loc(new Date(m.timestamp || Date.now()), 't', 'fr-FR', { hour: '2-digit', minute: '2-digit' }));
    bubble.appendChild(el('div', { class: 'email-bubble-meta', text: metaBits.join(' · ') }));
    row.appendChild(bubble);
    return row;
  }

  function buildCallBubble(m) {
    const fromMe = !!m.fromMe;
    const d = m.callData || {};
    const st = String(d.status || '').toLowerCase();
    const recordingOnly = !!d.recordingOnly || st === 'recorded';
    const row = el('div', { class: 'message-row' + (fromMe ? ' fromMe' : '') });
    if (m.id) row.setAttribute('data-msg-id', m.id);
    const bubble = el('div', { class: 'bubble call-bubble' + (fromMe ? ' fromMe' : '') });
    const header = el('div', { class: 'call-bubble-head' });
    // Status-specific label + icon (swat:B5) — every outcome the WA/3CX
    // pipelines persist reads correctly instead of the generic in/out label.
    const FAIL_ICONS = {
      missed: 'phone-missed', noanswer: 'phone-missed',
      canceled: 'phone-off', busy: 'phone-off', rejected: 'phone-off',
      failed: 'phone-off',
    };
    const STATUS_LABELS = {
      missed: 'Appel manqué', canceled: 'Annulé', busy: 'Occupé',
      noanswer: 'Sans réponse', ringing: 'Appel en cours…', rejected: 'Refusé',
      failed: 'Appel échoué',
    };
    const dirIcon = recordingOnly ? 'audio-lines' : FAIL_ICONS[st]
      || (st === 'ringing' ? 'phone-call' : (fromMe ? 'phone-outgoing' : 'phone-incoming'));
    header.appendChild(lucideIcon(dirIcon));
    const labelText = recordingOnly ? 'Enregistrement 3CX' : (STATUS_LABELS[st] || (fromMe ? 'Outgoing call' : 'Incoming call'));
    header.appendChild(el('span', { class: 'call-bubble-label', text: labelText }));
    // Which line carried the call — helps agents tell PBX (3CX/GSM) calls
    // from WhatsApp voice. Inherits the bubble's text color (token-safe).
    if (d.source) {
      header.appendChild(el('span', {
        class: 'call-bubble-src',
        text: d.source === '3cx' ? '3CX' : (d.source === 'kanza' ? 'Kenza' : 'WhatsApp'),
        style: 'font-size:10px;font-weight:600;letter-spacing:.04em;opacity:.55;border:1px solid currentColor;border-radius:999px;padding:1px 7px;white-space:nowrap;',
      }));
    }
    // Duration only makes sense for connected calls — a failed/ringing state
    // with a stray durationSec of 0 must not render a confusing "00:00".
    if (d.durationSec != null && !FAIL_ICONS[st] && st !== 'ringing') {
      const mm = String(Math.floor(d.durationSec / 60)).padStart(2, '0');
      const ss = String(d.durationSec % 60).padStart(2, '0');
      header.appendChild(el('span', { class: 'call-bubble-duration', text: mm + ':' + ss }));
    }
    bubble.appendChild(header);
    if (d.recordingUrl || m.mediaUrl) {
      bubble.appendChild(buildAudioPlayer(d.recordingUrl || m.mediaUrl, { mime: d.recordingMime || 'audio/wav', preload: 'none', durationSec: d.durationSec }));
    } else if (d.source === 'kanza' && d.recordingPending && !FAIL_ICONS[st] && Number(d.durationSec) > 0) {
      // Her uploader runs at hang-up, so a fresh call is briefly audio-less.
      // The next result POST for the same call id fills the player in.
      // A call nobody picked up never gets a file, so it must not sit here
      // claiming one is coming — the duration guard also silences the rows
      // written before the server started sending her real status.
      bubble.appendChild(el('div', {
        text: 'Enregistrement en cours de traitement…',
        style: 'margin-top:6px;font-size:12px;opacity:.6;font-style:italic;',
      }));
    } else if (_threecxRecordingIsPending(d, m.timestamp)) {
      // Gateway sentinel: the wav is still being uploaded — B4 backfill will
      // swap this note for a real player once the index discovers it. Failed
      // terminal outcomes and sentinels older than the uploader/index grace
      // window are not advertised as "processing" forever.
      bubble.appendChild(el('div', {
        text: 'Enregistrement en cours de traitement…',
        style: 'margin-top:6px;font-size:12px;opacity:.6;font-style:italic;',
      }));
    }
    // AI/transcription one-liner (what the call was about) — read without listening.
    if (d.summaryDarija) {
      bubble.appendChild(el('div', { class: 'call-bubble-summary', text: d.summaryDarija }));
    }
    const meta = el('div', { class: 'meta' });
    meta.appendChild(el('span', { text: formatTime(m.timestamp) }));
    bubble.appendChild(meta);
    row.appendChild(bubble);
    return row;
  }

  // Did this outbound send reach the client, and what can the agent do about
  // it? Single source of truth for the in-bubble failure state — buildBubble()
  // asks once, so the badge, its wording and its button can never drift apart.
  //
  // Returns null when no red block belongs on the bubble, otherwise
  // { label, canTemplate }.
  //
  // 131047 proves the service window is closed. 131051 is unsupported
  // media; 131026 is undeliverable, without proving why. Neither means a
  // template will fix it. The existing 131053 audio fallback has its own UI.
  const _TEMPLATE_FIXABLE_CODES = new Set([131047]);
  function _failedSendNotice(m) {
    if (!m || !m.fromMe || m.status !== 'failed') return null;
    const code = Number(m.statusError?.code);
    if (code === 131053) return null;
    if (_TEMPLATE_FIXABLE_CODES.has(code)) {
      return {
        label: 'Not delivered — Leads7 24h window closed',
        detail: 'Replies on Support2 do not open Leads7. Send an approved template, then wait for the customer to reply on Leads7 before resending this message.',
        canTemplate: true,
      };
    }
    if (code === 131051) {
      return { label: 'Not delivered — unsupported message type', canTemplate: false };
    }
    if (code === 131026) {
      return { label: 'Not delivered — WhatsApp could not deliver this message', canTemplate: false };
    }
    return { label: 'Not delivered — sending failed', canTemplate: false };
  }

  // An explicit re-engagement action belongs to Leads7 even when the
  // closed-window banner has defaulted the composer to SMS.
  function _openLeads7WindowTemplates() {
    _setComposeChannel('wa', true);
    openTemplatesModal();
  }

  function buildBubble(m, isGroup) {
    // Special bubble for WhatsApp Calls — renders direction icon +
    // duration + inline audio player for the recording. The call entry
    // is just a regular inbox_messages row with type='call' so the
    // chat-history fetch picks it up automatically.
    if (m.type === 'call') return _peerMarkRow(buildCallBubble(m), m);
    // Email channel — sent mails + client replies (spec 2026-07-02).
    if (m.type === 'email') return _peerMarkRow(buildEmailBubble(m), m);
    const fromMe = !!m.fromMe;
    // Bot detection: legacy support bot persists `bot:true`; the funnel
    // agent only persists `sentBy:'bot_funnel'` — accept both markers.
    const isBot = !!m.bot || m.sentBy === 'bot_funnel' || !!m.botFunnel;
    // Influencer bot replies: same purple bubble, own tag, no sales coaching.
    const isInfluencerBot = fromMe && m.sentBy === 'bot_influencer';
    const row = el('div', { class: 'message-row' + (fromMe ? ' fromMe' : '') });
    if (m.id) row.setAttribute('data-msg-id', m.id);
    const bubble = el('div', { class: 'bubble' + (fromMe ? ' fromMe' : '') + ((isBot || isInfluencerBot) ? ' is-bot' : '') });

    // Selection checkbox — visible on hover when nothing is selected,
    // permanently visible (and reflecting state) once at least one
    // message is selected. Sits at the row's leading edge so it
    // doesn't fight with the bubble's content.
    const selected = m.id && state.selectedMessageIds.has(m.id);
    const selectBtn = el('button', {
      class: 'msg-select-btn' + (selected ? ' selected' : ''),
      'aria-label': selected ? 'Unselect message' : 'Select message',
      type: 'button',
    });
    selectBtn.appendChild(lucideIcon(selected ? 'check' : 'circle'));
    selectBtn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      if (!m.id) return;
      toggleMessageSelection(m.id);
    });
    row.appendChild(selectBtn);
    // Wire the click-anywhere-to-toggle handler unconditionally so
    // rows that were built before selection started still respond
    // when the user clicks them. The handler itself bails when no
    // selection is active, so single click on a regular message
    // doesn't accidentally start a selection.
    if (selected) row.classList.add('is-selected');
    if (state.selectedMessageIds.size > 0) row.classList.add('selection-mode');
    row.addEventListener('click', (ev) => {
      if (state.selectedMessageIds.size === 0) return;  // not in selection mode
      if (ev.target.closest('a, button, audio, video, .msg-select-btn')) return;
      if (m.id) toggleMessageSelection(m.id);
    });
    if (m.forwarded) {
      bubble.appendChild(el('div', { class: 'forwarded-tag' }, [
        lucideIcon('corner-up-right'),
        el('span', { text: 'Forwarded' })
      ]));
    }

    if (isGroup && !fromMe && (m.pushName || m.sender)) {
      // Sender name above each group bubble — click opens a 1-1 chat with
      // that participant (when we know their phone JID). For LID-only
      // senders (privacy mode) the click still opens the LID JID — Baileys
      // routes outbound messages to LIDs correctly.
      const senderJid = m.sender || '';
      // Prefer the resolved member name (HubSpot / WA notify name) loaded
      // by ensureGroupMembersForCurrentChat over m.pushName. WhatsApp lets
      // users pick any short name as their pushName ("بري", "?", emoji-
      // only, etc.) which makes group bubbles unreadable. The resolved
      // map falls back to pushName cleanly when no match exists.
      const resolved = state.currentGroupMembers?.[senderJid]
        || state.currentGroupMembers?.[jidUserSafe(senderJid)];
      // Don't render a raw 14+-digit JID user portion as the label — we'd
      // rather show the pushName, or "Member" when nothing readable is
      // available. (Older messages where sender == group JID are scrubbed
      // server-side so senderJid is empty here for that bug class.)
      const senderUser = senderJid.split('@')[0] || '';
      const isRawId = /^\d{12,}$/.test(senderUser);
      const display = resolved || m.pushName || (isRawId ? 'Member' : (senderUser || 'Member'));
      const senderEl = el('div', { class: 'sender-name' + (senderJid ? ' clickable' : ''), text: display });
      if (senderJid) {
        senderEl.title = 'Open 1-1 chat with ' + display;
        senderEl.addEventListener('click', e => {
          e.preventDefault(); e.stopPropagation();
          openChatForGroupParticipant(senderJid, display);
        });
      }
      bubble.appendChild(senderEl);
    }
    if (isInfluencerBot && !isBot) {
      const header = el('div', { class: 'bot-bubble-header' });
      header.appendChild(el('div', { class: 'bot-tag' }, [lucideIcon('star'), el('span', { text: 'Bot Influencer' })]));
      bubble.appendChild(header);
    }
    if (isBot) {
      // Inline header: Bot tag + always-visible coach button + (optional) badge.
      // Putting these IN the bubble so they aren't clipped by the messages-area overflow.
      const header = el('div', { class: 'bot-bubble-header' });
      header.appendChild(el('div', { class: 'bot-tag' }, [lucideIcon('bot'), el('span', { text: 'Bot' })]));
      if (m.id) {
        const coachBtn = el('button', {
          class: 'bot-coach-inline-btn',
          title: 'Coach the bot — teach it what it should have said',
          'aria-label': 'Coach the bot',
        }, [el('span', { text: '🎓' }), el('span', { text: ' Coach' })]);
        coachBtn.addEventListener('click', e => {
          e.preventDefault(); e.stopPropagation();
          openBotCoachModal(m);
        });
        header.appendChild(coachBtn);
      }
      // Coaching badge — agents have already left correction notes on this reply.
      const corrections = (state.botCorrections?.[state.selectedChatId] || []).filter(c => c.messageId === m.id);
      if (corrections.length) {
        const badge = el('button', {
          class: 'bot-coach-badge' + (corrections.some(c => c.isPermanent) ? ' has-permanent' : ''),
          title: corrections.length + ' coaching note' + (corrections.length > 1 ? 's' : ''),
          'aria-label': 'Bot coaching notes',
        }, [
          el('span', { text: '💬' }),
          el('span', { class: 'bot-coach-count', text: String(corrections.length) }),
        ]);
        badge.addEventListener('click', e => {
          e.preventDefault(); e.stopPropagation();
          openBotCoachPopover(badge, m, corrections);
        });
        header.appendChild(badge);
      }
      bubble.appendChild(header);
    }

    if (m.quotedMessage) {
      const q = buildQuotedBlock(m.quotedMessage);
      if (q) bubble.appendChild(q);
    }

    // Helper: text body with clickable phone chips.
    const textNode = () => {
      const div = el('div', { class: 'text' });
      div.appendChild(buildTextWithPhones(m.text || ''));
      return div;
    };
    if (m.type === 'audio' && m.mediaUrl) {
      // Pick the type from the URL extension first — m.mimeType in old message
      // records may be stale (e.g., audio/webm for a file that was post-PR-201
      // remuxed to ogg). Stale mime hints make Chrome/Safari terminate playback
      // early when the decoded format doesn't match the declared one.
      let _at = 'audio/mpeg';
      if (/\.mp3$/i.test(m.mediaUrl)) _at = 'audio/mpeg';
      else if (/\.ogg$/i.test(m.mediaUrl)) _at = 'audio/ogg';
      else if (/\.webm$/i.test(m.mediaUrl)) _at = 'audio/webm';
      else if (/\.m4a$/i.test(m.mediaUrl) || /\.mp4$/i.test(m.mediaUrl)) _at = 'audio/mp4';
      else if (m.mimeType) _at = m.mimeType;
      // Use the stored duration; fetch bytes only after Play or seek.
      bubble.appendChild(buildAudioPlayer(m.mediaUrl, { mime: _at, durationSec: m.duration }));
      if (m.text) bubble.appendChild(textNode());
    } else if (m.type === 'image' && m.mediaUrl) {
      // Click → in-platform lightbox (no more target=_blank tab spawn).
      // The url is a server-relative path so it stays inside the agent's
      // session.
      const img = document.createElement('img');
      img.src = m.mediaUrl; img.alt = 'image';
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', (ev) => { ev.stopPropagation(); openImageLightbox(m.mediaUrl); });
      bubble.appendChild(img);
      if (m.text) bubble.appendChild(textNode());
    } else if (m.type === 'video' && m.mediaUrl) {
      const v = document.createElement('video');
      v.controls = true; v.preload = 'metadata'; v.src = m.mediaUrl;
      v.setAttribute('style', 'max-width:280px;border-radius:8px;display:block;');
      bubble.appendChild(v);
      if (m.text) bubble.appendChild(textNode());
    } else if (m.type === 'document' && m.mediaUrl) {
      const pdfView = _officePdfUrl(m);
      const link = el('a', { href: pdfView || m.mediaUrl, target: '_blank', class: 'doc-card' }, [
        lucideIcon('file-text'),
        el('div', null, [
          el('div', { class: 'doc-name', text: m.fileName || 'Document' }),
          pdfView ? el('div', { class: 'doc-sub', text: 'Opens as PDF' }) : null,
        ])
      ]);
      bubble.appendChild(link);
      if (pdfView) {
        bubble.appendChild(el('a', { href: m.mediaUrl, download: m.fileName || '', class: 'doc-download', text: 'Download original' }));
      }
      if (m.text) bubble.appendChild(textNode());
    } else if (m.type === 'sticker' && m.mediaUrl) {
      // Stickers are .webp images. Render smaller than a regular
      // image bubble (matches WhatsApp's own sticker size) and
      // strip the bubble background so transparent stickers don't
      // sit on a white box. Click opens the lightbox same as a
      // regular image.
      bubble.classList.add('sticker-bubble');
      const img = document.createElement('img');
      img.src = m.mediaUrl; img.alt = 'sticker';
      img.className = 'sticker';
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', (ev) => { ev.stopPropagation(); openImageLightbox(m.mediaUrl); });
      bubble.appendChild(img);
    } else {
      // For text messages with empty body (history-synced reactions / system frames /
      // unsupported message types), drop the bubble text entirely instead of rendering
      // the literal "[text]" placeholder. Non-text types without a mediaUrl get a
      // visible warning chip so the agent sees the message exists AND knows the
      // media payload never made it to disk (Baileys decryption usually) — they can
      // ask the sender to resend.
      if (m.contacts && m.contacts.length) {
        for (const c of m.contacts) bubble.appendChild(_sharedContactCard(c));
      } else if (m.text) {
        bubble.appendChild(textNode());
      } else if (m.type && m.type !== 'text') {
        const labelMap = {
          audio: '🎵 Voice note — failed to download',
          image: '📷 Image — failed to download',
          video: '🎥 Video — failed to download',
          document: '📎 Document — failed to download',
          // Older shared-contact cards (received before the contact-card fix)
          // never stored the name/phone, so there's nothing to render — show a
          // clear note instead of the alarming "failed to download".
          contact: '👤 Contact partagé — détails indisponibles (demandez de le renvoyer)'
        };
        const chip = el('div', {
          class: 'media-failed-chip',
          text: labelMap[m.type] || '[' + m.type + ' — failed to download]'
        });
        bubble.appendChild(chip);
      }
    }

    // Outbound bubbles get a small "by <agent>" prefix in the meta line so
    // the team can see who actually sent each reply — useful when 6 agents
    // share one WhatsApp account and a customer's chat has replies from
    // several of them. Falls back to the raw sentBy username when team
    // data isn't loaded yet (no breakage either way).
    const meta = el('div', { class: 'meta' });
    if (fromMe && m.peer && m.senderLabel) {
      meta.appendChild(el('span', { class: 'meta-sender', text: userLabel(m.senderLabel) }));
    } else if (fromMe && m.sentBy) {
      const teamUser = (state.teamUsers || []).find(u => u.username === m.sentBy);
      // support2-mirror sends carry the REAL sender in senderLabel (agent name
      // or Mr.Bot) — show that instead of the raw service-account username.
      // Admin senders (own bubbles included — self is not in teamUsers) show
      // "Administration N", never the login.
      const display = m.senderLabel ? userLabel(m.senderLabel) : userLabel(m.sentBy, teamUser?.displayName || m.sentBy);
      meta.appendChild(el('span', { class: 'meta-sender', text: display }));
    }
    meta.appendChild(el('span', { text: formatTime(m.timestamp) }));
    // Unified-thread channel tag — flag SMS so WhatsApp vs SMS is obvious in the
    // merged conversation (WhatsApp stays the implicit default, no tag).
    {
      const _mch = m._personSource || (m.sentVia === 'support2' ? 'support2' : m.channel)
        || ((m.chatId || '').endsWith('@s.whatsapp.net') || (m.chatId || '').endsWith('@g.us') ? 'wa'
          : ((m.chatId || '').startsWith('sms:') ? 'sms' : ''));
      if (_mch === 'leads7' && state.personChat) meta.appendChild(el('span', { class: 'meta-channel meta-channel-l7', text: 'L7', title: 'Leads7 WhatsApp', style: 'color:var(--accent);font-weight:700;' }));
      if (_mch === 'sms') meta.appendChild(el('span', { class: 'meta-channel meta-channel-sms', text: 'SMS', title: 'Par SMS', style: 'color:#7c3aed;font-weight:700;' }));
      // Written inside the Lougha student app, not on WhatsApp. Worth its own
      // tag: it tells the agent this person is already using the app — and that
      // a WhatsApp reply lands on a different screen than the one they wrote
      // from. Same treatment as SMS; WhatsApp stays the untagged default.
      if (_mch === 'app') meta.appendChild(el('span', { class: 'meta-channel meta-channel-app', text: 'APP', title: "Depuis l'app Lougha", style: 'color:#e11d48;font-weight:700;' }));
      // Sent on support2's WhatsApp line instead of ours. It is a normal
      // message in a normal bubble — the tag is the only thing that says the
      // client will see it arrive from the support number, and that their
      // reply lands in support2's inbox, not here.
      if (m.peer === 'support2') meta.appendChild(el('span', { class: 'meta-channel meta-channel-s2', text: 'S2', title: 'Conversation sur support2 — affichée ici en lecture seule', style: 'color:#0ea5e9;font-weight:700;' }));
      else if (_mch === 'support2') meta.appendChild(el('span', { class: 'meta-channel meta-channel-s2', text: 'SUPPORT2', title: 'Envoyé depuis le numéro support2 — la réponse arrive dans support2', style: 'color:#0ea5e9;font-weight:700;' }));
      // Multi-line WhatsApp: this message came in on (or left from) one of our
      // other numbers — an agent's own line — not the main 0661. Replies to
      // the client leave from the number of their latest message.
      if (m.waLine && !m.peer) meta.appendChild(el('span', { class: 'meta-wa-line', text: _waLineText(m.waLine), title: _waLineTitle(m.waLine, fromMe) }));
    }
    if (m.editedAt) meta.appendChild(el('span', { class: 'meta-edited', text: 'edited', title: 'edited ' + formatTime(Date.parse(m.editedAt) || Date.now()) }));
    if (fromMe) meta.appendChild(buildStatusIcon(m));
    // 🌐 Translate-to-Arabic — any message carrying plain text gets the button.
    if ((m.text || '').trim()) meta.appendChild(makeTranslateButton(m.text, bubble));
    bubble.appendChild(meta);

    // "This one reached NOBODY" block. A rejected outbound used to announce
    // itself with a 14px ✗ in the meta line plus — for 131047 only — an amber
    // pill borrowed from the warning family, worded as advice. Quiet enough
    // that the desk read the threads as delivered: a prod audit on 2026-09-04
    // found 43 voice notes Meta had refused (imane 18, mary 10, bouchra 8,
    // doha 6) that every one of those agents had treated as sent. Danger
    // palette + the action in place, so the state cannot be mistaken for a
    // hint. Built from ONE condition and ONE element: the badge and its
    // button can never disagree about whether the client got the message.
    const failedNotice = _failedSendNotice(m);
    if (failedNotice) {
      const box = el('div', { class: 'msg-send-failed', role: 'status' });
      const head = el('div', { class: 'msg-send-failed-head' });
      head.appendChild(lucideIcon('triangle-alert'));
      head.appendChild(el('span', { text: failedNotice.label }));
      box.appendChild(head);
      if (failedNotice.detail) box.appendChild(el('div', { class: 'msg-send-failed-detail', text: failedNotice.detail }));
      // Only offered where a template is genuinely the thing that gets
      // through. openTemplatesModal() with no target is the composer flow
      // already used by the 24h banner — it opens on the CURRENT chat,
      // pre-filtered to that lead's pipeline stage, and routes the send via
      // Meta's template API. No second picker, no second send path.
      if (failedNotice.canTemplate) {
        const btn = el('button', {
          class: 'msg-send-failed-btn',
          type: 'button',
          title: 'Choose an approved template for Leads7',
        });
        btn.appendChild(lucideIcon('message-square-text'));
        btn.appendChild(el('span', { text: 'Choose a template' }));
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          _openLeads7WindowTemplates();
        });
        box.appendChild(btn);
      }
      bubble.appendChild(box);
    }

    // Meta 131053 — its media pipeline refuses the ogg voice note even though
    // it accepted and stored the upload byte-perfect (started 2026-08-27). The
    // server re-sends the same recording as an audio file on its own, so the
    // agent needs to read "it went out the other way", not a bare ✗ they'd
    // answer by recording the whole thing again.
    if (fromMe && m.status === 'failed' && m.statusError?.code === 131053) {
      const note = el('span', { class: 'msg-voice-fallback-note' });
      note.appendChild(lucideIcon('refresh-cw'));
      note.appendChild(el('span', {
        text: m.voiceNoteFallbackSent
          ? 'Note vocale refusée par WhatsApp — renvoyée en fichier audio'
          : 'Note vocale refusée par WhatsApp — renvoi automatique en cours',
      }));
      bubble.appendChild(note);
    }

    // Reactions chip strip — aggregates same-emoji reactions and shows count.
    // Clicking my own emoji removes it; clicking others' adds my own.
    bubble.appendChild(buildReactionStrip(m));

    // Hover-reveal quick-react bar — 6 default emojis, click to send.
    bubble.appendChild(buildQuickReactBar(m));

    row.appendChild(bubble);

    // Reply / Edit / Delete chips live INSIDE the bubble so absolute
    // positioning anchors to the bubble's actual edge, not the
    // full-width row. (Row-relative chips ended up far from the bubble
    // for fromMe rows, since the bubble is right-aligned inside an
    // 800-px wide row.) CSS makes .bubble position:relative.
    const replyBtn = el('button', { class: 'bubble-reply-trigger', title: 'Reply', 'aria-label': 'Reply' });
    replyBtn.appendChild(lucideIcon('reply'));
    replyBtn.addEventListener('click', e => {
      e.preventDefault(); e.stopPropagation();
      startReply(m);
    });
    bubble.appendChild(replyBtn);

    // Receipt flag (Virements, 2026-06-12) — inbound payment proofs (photo
    // of a virement, PDF receipt, or a typed "j'ai payé 1500" text) can be
    // sent to the payment-verification queue by ANY agent. One claim per
    // message server-side (409 → already flagged).
    const canFlagReceipt = !fromMe && m.id && (
      (m.type === 'image' && m.mediaUrl) ||
      (m.type === 'document' && m.mediaUrl) ||
      ((m.type === 'text' || !m.type) && m.text)
    );
    if (canFlagReceipt) {
      const receiptBtn = el('button', { class: 'bubble-receipt-trigger', title: 'Marquer comme reçu de paiement', 'aria-label': 'Marquer comme reçu de paiement' });
      receiptBtn.appendChild(lucideIcon('receipt'));
      receiptBtn.addEventListener('click', e => {
        e.preventDefault(); e.stopPropagation();
        flagMessageAsReceipt(m);
      });
      bubble.appendChild(receiptBtn);
    }

    // Correction — on our own outbound TEXT messages. The WhatsApp Cloud API
    // can't truly edit a sent message (Meta exposes no edit endpoint), so
    // "Corriger" sends a corrected version as a reply that QUOTES the wrong
    // message (the quote alone signals the fix — no marker prefix). The original
    // stays on the client's phone; the fix lands right under it. No time window —
    // it's a normal message (the 24h free-form window is enforced server-side).
    if (fromMe && m.id) {
      // WhatsApp only — the quoted-correction UX is a WhatsApp concept.
      // On FB/IG/TikTok DMs it's meaningless, and on comment threads the send
      // 400s (commentId required). Outbound rows always carry m.channel; the
      // WA-jid suffix is a belt-and-suspenders fallback for legacy WA rows.
      const _ch = m.channel || '';
      const isWaMsg = _ch === 'wa' || _ch === 'wa-group'
        || (!_ch && /@(s\.whatsapp\.net|g\.us)$/.test(m.chatId || ''));
      const canCorrect = isWaMsg && (m.type === 'text' || !m.type) && !!m.text;
      if (canCorrect) {
        const editBtn = el('button', { class: 'bubble-edit-trigger', title: 'Corriger (renvoyer une version corrigée)', 'aria-label': 'Corriger' });
        editBtn.appendChild(lucideIcon('pencil'));
        editBtn.addEventListener('click', e => {
          e.preventDefault(); e.stopPropagation();
          startInboxCorrection(m, row);
        });
        bubble.appendChild(editBtn);
      }
    }
    // Delete affordance — INBOUND (customer) messages only, for sales-admins
    // (admin / super_sales): a local-only removal from the Leads7 view (the
    // message stays on the customer's phone). The old outbound "Delete for
    // everyone" is gone — the WhatsApp Cloud API can't un-send a sent message
    // (Meta exposes no revoke endpoint); use "Corriger" to fix a mistake.
    if (m.id && !fromMe && _isSalesAdmin()) {
      const delBtn = el('button', { class: 'bubble-delete-trigger', title: 'Remove from this view', 'aria-label': 'Delete' });
      delBtn.appendChild(lucideIcon('trash-2'));
      delBtn.addEventListener('click', e => {
        e.preventDefault(); e.stopPropagation();
        deleteInboxMessage(m);
      });
      bubble.appendChild(delBtn);
    }

    if (isBot && m.id) {
      // Hover-only coach trigger — lets any teammate leave a coaching
      // note on the bot's reply. Mirrors bubble-reply-trigger styling.
      const coachBtn = el('button', { class: 'bubble-coach-trigger', title: 'Coach the bot', 'aria-label': 'Coach the bot' });
      coachBtn.appendChild(lucideIcon('graduation-cap'));
      coachBtn.addEventListener('click', e => {
        e.preventDefault(); e.stopPropagation();
        openBotCoachModal(m);
      });
      row.appendChild(coachBtn);

      // 👍/👎 vote chips — one vote per agent per message, toggle/switch on
      // the server. The active vote stays visible without hover (filled chip)
      // and feeds the AI Bot "Bot feedback" pane.
      const myVote = (state.botFeedback && state.botFeedback[state.selectedChatId] && state.botFeedback[state.selectedChatId][m.id]) || null;
      const upBtn = el('button', {
        class: 'bubble-vote-trigger bubble-vote-up' + (myVote === 'up' ? ' is-active' : ''),
        title: 'Bonne réponse du bot', 'aria-label': 'Bonne réponse', type: 'button',
      });
      upBtn.appendChild(lucideIcon('thumbs-up'));
      upBtn.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); voteBotFeedback(m, 'up'); });
      bubble.appendChild(upBtn);
      const downBtn = el('button', {
        class: 'bubble-vote-trigger bubble-vote-down' + (myVote === 'down' ? ' is-active' : ''),
        title: 'Mauvaise réponse du bot', 'aria-label': 'Mauvaise réponse', type: 'button',
      });
      downBtn.appendChild(lucideIcon('thumbs-down'));
      downBtn.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); voteBotFeedback(m, 'down'); });
      bubble.appendChild(downBtn);
    }

    // Double-click anywhere on the row → instant reply. Listening on the
    // row (not the bubble) covers the empty strip beside short bubbles
    // and the padding around audio/image bubbles, which the bubble-only
    // listener missed. Skip when the user is selecting text or when the
    // double-click lands on the audio scrubber, a link, or another
    // interactive control so we don't hijack their native gestures.
    row.addEventListener('dblclick', e => {
      if (m.peer) return;
      const sel = window.getSelection?.();
      if (sel && sel.toString().trim()) return;
      if (e.target.closest('audio,video,a,button,.bubble-reply-trigger')) return;
      e.preventDefault();
      startReply(m);
    });

    return _peerMarkRow(row, m);
  }

  // A support2 row mirrored into this thread (peer feed) lives in support2's
  // store: read-only here — no quote-reply, reaction, correction, delete,
  // receipt flag, bot vote or selection. Content + who sent it only. Applies to
  // message, call (3CX recording) and email bubbles alike.
  function _peerMarkRow(row, m) {
    if (!row || !m || !m.peer) return row;
    row.classList.add('is-peer');
    row.querySelectorAll('.msg-select-btn, .msg-quick-react-bar, .bubble-reply-trigger, .bubble-receipt-trigger, .bubble-edit-trigger, .bubble-delete-trigger, .bubble-coach-trigger, .bubble-vote-trigger')
      .forEach(n => n.remove());
    if (!row.querySelector('.meta-channel-s2')) {
      const host = row.querySelector('.meta') || row.querySelector('.email-bubble, .call-bubble, .bubble') || row;
      host.appendChild(el('span', { class: 'meta-channel meta-channel-s2', text: 'S2', title: 'Conversation sur support2 — affichée ici en lecture seule', style: 'color:#0ea5e9;font-weight:700;margin-left:6px;' }));
    }
    return row;
  }

  // ============ Multi-select / Forward / Copy ============
  function toggleMessageSelection(id) {
    if (!id) return;
    if (state.selectedMessageIds.has(id)) state.selectedMessageIds.delete(id);
    else state.selectedMessageIds.add(id);
    // In-place DOM update — full renderMessages() would reset
    // E.messagesArea.scrollTop to scrollHeight, jerking the agent
    // back to the bottom every time they tick a message.
    applyMessageSelectionDom();
    updateMsgSelectionBar();
  }

  function clearMessageSelection() {
    state.selectedMessageIds.clear();
    applyMessageSelectionDom();
    updateMsgSelectionBar();
  }

  // Reflect state.selectedMessageIds onto the existing message-row
  // DOM without rebuilding the list. Toggles three things per row:
  //   - .is-selected (green tint)
  //   - .selected on the inner .msg-select-btn (filled green check)
  //   - .selection-mode on every row when count > 0 (so anywhere on
  //     the row counts as a click target, matching the WA pattern)
  function applyMessageSelectionDom() {
    // Target every .message-row in the document — the inbox view
    // mounts them under #messagesArea, but Team Groups view reuses
    // the same buildBubble renderer and mounts them under its own
    // .teamchat-messages container. One Set drives both.
    const rows = document.querySelectorAll('.message-row');
    const anySelected = state.selectedMessageIds.size > 0;
    rows.forEach(row => {
      const id = row.getAttribute('data-msg-id');
      const isSelected = id && state.selectedMessageIds.has(id);
      row.classList.toggle('is-selected', !!isSelected);
      row.classList.toggle('selection-mode', anySelected);
      const btn = row.querySelector('.msg-select-btn');
      if (btn) {
        btn.classList.toggle('selected', !!isSelected);
        btn.setAttribute('aria-label', isSelected ? 'Unselect message' : 'Select message');
        // Swap the icon (check vs circle) to match selection.
        const iconEl = btn.querySelector('[data-lucide]');
        const wantName = isSelected ? 'check' : 'circle';
        if (iconEl && iconEl.getAttribute('data-lucide') !== wantName) {
          btn.replaceChild(lucideIcon(wantName), iconEl);
        }
      }
    });
    refreshIcons();
  }

  function updateMsgSelectionBar() {
    let bar = document.getElementById('msgSelectionBar');
    const count = state.selectedMessageIds.size;
    const inboxCompose = document.querySelector('.compose');
    const compose = inboxCompose;
    if (!bar) {
      bar = el('div', { id: 'msgSelectionBar', class: 'msg-selection-bar hidden' });
      const cancelBtn = el('button', { class: 'icon-btn msg-selection-x', id: 'msgSelectionCancelBtn', 'aria-label': 'Cancel selection' }, [lucideIcon('x')]);
      const countEl = el('span', { class: 'msg-selection-count', text: '0 Selected' });
      const spacer = el('div', { style: 'flex:1;' });
      const copyBtn = el('button', { class: 'btn btn-ghost', id: 'msgSelectionCopyBtn' }, [
        lucideIcon('copy'),
        document.createTextNode(' Copy')
      ]);
      const fwdBtn = el('button', { class: 'btn btn-primary', id: 'msgSelectionFwdBtn' }, [
        lucideIcon('corner-up-right'),
        document.createTextNode(' Forward')
      ]);
      cancelBtn.addEventListener('click', clearMessageSelection);
      copyBtn.addEventListener('click', copySelectedMessages);
      fwdBtn.addEventListener('click', openForwardPicker);
      bar.appendChild(cancelBtn);
      bar.appendChild(countEl);
      bar.appendChild(spacer);
      bar.appendChild(copyBtn);
      bar.appendChild(fwdBtn);
      if (compose && compose.parentNode) {
        compose.parentNode.insertBefore(bar, compose);
      } else {
        E.main().appendChild(bar);
      }
      refreshIcons();
    }
    bar.querySelector('.msg-selection-count').textContent = count + ' Selected';
    bar.classList.toggle('hidden', count === 0);
    // Re-parent the bar onto the currently visible compose so a
    // view switch (inbox ↔ team groups) doesn't strand it in the
    // wrong tree.
    if (compose && compose.parentNode && bar.nextSibling !== compose) {
      compose.parentNode.insertBefore(bar, compose);
    }
    if (inboxCompose) inboxCompose.classList.toggle('hidden-during-selection', count > 0);
  }

  async function copySelectedMessages() {
    const ordered = (state.messages || []).filter(m => state.selectedMessageIds.has(m.id));
    const lines = ordered.map(m => {
      if (m.text) return m.text;
      if (m.fileName) return '[' + m.fileName + ']';
      if (m.type) return '[' + m.type + ']';
      return '';
    }).filter(Boolean);
    const text = lines.join('\n\n');
    if (!text) { toast('Nothing to copy (selection is media-only)', 'info'); return; }
    try {
      await navigator.clipboard.writeText(text);
      toast('Copied ' + lines.length + ' message' + (lines.length > 1 ? 's' : ''), 'success');
      clearMessageSelection();
    } catch {
      toast('Copy failed — clipboard blocked', 'error');
    }
  }

  async function openForwardPicker(opts) {
    // opts.sourceIds defaults to the inbox selection, but team-chat
    // can pass its own selection set so the picker works as a
    // shared "forward N messages to N targets" UI for both surfaces.
    // opts.onSuccess fires after a successful forward — the caller
    // uses it to clear its own selection state.
    opts = opts || {};
    const sourceIds = Array.isArray(opts.sourceIds) ? opts.sourceIds : Array.from(state.selectedMessageIds || []);
    if (!sourceIds.length) return;
    state.forwardSourceIds = sourceIds;
    state.forwardOnSuccess = typeof opts.onSuccess === 'function' ? opts.onSuccess : null;
    // Eagerly load team users + rooms so the Team section renders
    // even if the agent has never visited the team-chat tab in this
    // session. Both calls are cached on state.
    if (!state.teamUsers?.length && typeof loadTeamchatUsers === 'function') {
      try { await loadTeamchatUsers(); } catch { /* fall through — Team section just empty */ }
    }
    if (!state.teamRooms && typeof loadTeamchatRooms === 'function') {
      try { await loadTeamchatRooms(); } catch { /* idem */ }
    }
    let modal = document.getElementById('forwardPickerModal');
    if (!modal) {
      modal = el('div', { id: 'forwardPickerModal', class: 'modal hidden' });
      const backdrop = el('div', { class: 'modal-backdrop' });
      backdrop.addEventListener('click', () => closeModal('forwardPickerModal'));
      const card = el('div', { class: 'modal-card', style: 'max-width:420px;' });
      const head = el('header', { class: 'modal-head' });
      head.appendChild(el('h2', null, [lucideIcon('corner-up-right'), document.createTextNode(' Forward to…')]));
      const closeX = el('button', { class: 'icon-btn' }, [lucideIcon('x')]);
      closeX.addEventListener('click', () => closeModal('forwardPickerModal'));
      head.appendChild(closeX);
      const body = el('div', { class: 'modal-body', style: 'display:flex;flex-direction:column;gap:8px;' });
      const search = el('input', { id: 'fwdSearch', type: 'search', class: 'input', placeholder: 'Search conversations…', autocomplete: 'off' });
      search.addEventListener('input', renderForwardPickerList);
      body.appendChild(search);
      body.appendChild(el('div', { id: 'fwdChatList', class: 'fwd-chat-list' }));
      const footRow = el('div', { class: 'row', style: 'justify-content:space-between;align-items:center;margin-top:8px;' });
      footRow.appendChild(el('span', { id: 'fwdPickedCount', style: 'font-size:12px;color:var(--gray-600);', text: '0 picked · max 10' }));
      const btnRow = el('div', { class: 'row', style: 'gap:8px;' });
      const cancelBtn = el('button', { class: 'btn btn-ghost', text: 'Cancel' });
      cancelBtn.addEventListener('click', () => closeModal('forwardPickerModal'));
      btnRow.appendChild(cancelBtn);
      const confirmBtn = el('button', { id: 'fwdConfirmBtn', class: 'btn btn-primary' }, [
        lucideIcon('send'),
        document.createTextNode(' Forward')
      ]);
      confirmBtn.disabled = true;
      confirmBtn.addEventListener('click', doForwardSelected);
      btnRow.appendChild(confirmBtn);
      footRow.appendChild(btnRow);
      body.appendChild(footRow);
      card.appendChild(head);
      card.appendChild(body);
      modal.appendChild(backdrop);
      modal.appendChild(card);
      document.body.appendChild(modal);
      refreshIcons();
    }
    state.forwardPicked = new Set();
    // If the user is forwarding from inside a group chat, remember the group
    // JID so the picker can show its WA participants as an extra section.
    // Falls back to null for 1-1 chats — the section just won't render.
    state.forwardCurrentGroupId = state.selectedChat?.isGroup ? state.selectedChat.id : null;
    document.getElementById('fwdSearch').value = '';
    openModal('forwardPickerModal');
    renderForwardPickerList();
    // Kick off the members fetch in background; the render function will
    // re-run once data lands. Cached on state so reopening the picker on the
    // same group doesn't refetch.
    if (state.forwardCurrentGroupId) {
      loadForwardGroupMembers(state.forwardCurrentGroupId);
    }
  }

  // Per-session cache: groupJid → { loading, members, error }. Members are
  // { jid, phone, name, hubspotId } as returned by /api/groups/:groupJid/wa-members.
  if (!state._forwardGroupMembersCache) state._forwardGroupMembersCache = new Map();

  async function loadForwardGroupMembers(groupJid) {
    const cache = state._forwardGroupMembersCache;
    const existing = cache.get(groupJid);
    if (existing && (existing.members || existing.error)) return; // already loaded
    cache.set(groupJid, { loading: true, members: null, error: null });
    try {
      const r = await api('/api/groups/' + encodeURIComponent(groupJid) + '/wa-members');
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        throw new Error('HTTP ' + r.status + (txt ? ': ' + txt.slice(0, 100) : ''));
      }
      const j = await r.json();
      cache.set(groupJid, { loading: false, members: j.members || [], error: null });
    } catch (err) {
      cache.set(groupJid, { loading: false, members: null, error: err?.message || 'fetch failed' });
    }
    // Re-render if the modal is still showing this group.
    if (state.forwardCurrentGroupId === groupJid) {
      try { renderForwardPickerList(); } catch { /* picker might have been closed */ }
    }
    // Also re-render the open chat — newly-cached members may unlock
    // @-mention resolution in the visible messages (buildTextWithPhones).
    if (state.selectedChatId === groupJid) {
      forceMessagesRerender(); // bust BOTH dedups so renderMessages actually redraws
      try { renderMessages(); } catch { /* inbox might have moved away */ }
    }
  }

  function renderForwardPickerList() {
    const list = document.getElementById('fwdChatList');
    if (!list) return;
    clear(list);
    const q = (document.getElementById('fwdSearch')?.value || '').trim().toLowerCase();

    // ── Members of this group (only when forwarding from a group chat) ──
    // Sticky top so the agent's most-likely target — a student already in
    // the same group — is the first thing they see. Falls through to the
    // Team + WhatsApp sections below for the other common destinations.
    if (state.forwardCurrentGroupId) {
      const cache = state._forwardGroupMembersCache?.get(state.forwardCurrentGroupId);
      list.appendChild(el('div', { class: 'fwd-section-title' }, [
        lucideIcon('users-round'),
        document.createTextNode(' Members of this group'),
      ]));
      if (!cache || cache.loading) {
        list.appendChild(el('div', { class: 'empty-row', text: 'Loading members…' }));
      } else if (cache.error) {
        list.appendChild(el('div', { class: 'empty-row', text: 'Failed to load members' }));
      } else {
        const members = (cache.members || [])
          .filter(m => m && m.jid && m.jid.endsWith('@s.whatsapp.net'))
          .filter(m => !q || (m.name || '').toLowerCase().includes(q) || (m.phone || '').includes(q)
            // masked m.phone can't match a typed full number — the jid digits can
            || (q.replace(/\D/g, '').length >= 4 && String(m.jid || '').split('@')[0].includes(q.replace(/\D/g, ''))));
        if (!members.length) {
          list.appendChild(el('div', { class: 'empty-row', text: q ? 'No members match this search' : 'No members to forward to' }));
        } else {
          members.slice(0, 200).forEach(m => {
            const picked = state.forwardPicked.has(m.jid);
            const row = el('div', { class: 'fwd-chat-row' + (picked ? ' picked' : '') });
            row.appendChild(el('div', { class: 'avatar ' + avatarColorClass(m.jid), text: initials(m.name || m.phone) || '?' }));
            row.appendChild(el('div', { class: 'fwd-chat-name', text: (m.name || m.phone || 'Unknown') + (m.phone && m.name ? ' · ' + m.phone : '') }));
            const tick = el('div', { class: 'fwd-chat-tick' });
            tick.appendChild(lucideIcon(picked ? 'check-circle-2' : 'circle'));
            row.appendChild(tick);
            row.addEventListener('click', () => toggleForwardPick(m.jid));
            list.appendChild(row);
          });
        }
      }
    }

    // ── Team section ────────────────────────────────
    // Always renders so an agent can forward an inbox message into
    // a teammate's DM in one click without leaving the modal.
    // Self is excluded (forwarding to yourself doesn't make sense).
    const meId = state.me?.id;
    const teamUsers = (state.teamUsers || [])
      .filter(u => u.id !== meId)
      .filter(u => !q || (userLabel(u.username, u.displayName || u.username) || '').toLowerCase().includes(q));
    const teamRooms = (state.teamRooms || [])
      .filter(r => !q || (r.name || '').toLowerCase().includes(q));

    if (teamUsers.length || teamRooms.length) {
      const teamHead = el('div', { class: 'fwd-section-title' }, [
        lucideIcon('users-round'),
        document.createTextNode(' Team'),
      ]);
      list.appendChild(teamHead);

      for (const u of teamUsers) {
        const targetId = 'team-user:' + u.id;
        const picked = state.forwardPicked.has(targetId);
        const row = el('div', { class: 'fwd-chat-row fwd-team-row' + (picked ? ' picked' : '') });
        row.appendChild(el('div', { class: 'avatar ' + avatarColorClass(u.id), text: initials(userLabel(u.username, u.displayName || u.username)) || '?' }));
        row.appendChild(el('div', { class: 'fwd-chat-name', text: (userLabel(u.username, u.displayName || u.username)) + ' · Team' }));
        const tick = el('div', { class: 'fwd-chat-tick' });
        tick.appendChild(lucideIcon(picked ? 'check-circle-2' : 'circle'));
        row.appendChild(tick);
        row.addEventListener('click', () => toggleForwardPick(targetId));
        list.appendChild(row);
      }
      for (const r of teamRooms) {
        const targetId = 'team-room:' + r.id;
        const picked = state.forwardPicked.has(targetId);
        const row = el('div', { class: 'fwd-chat-row fwd-team-row' + (picked ? ' picked' : '') });
        row.appendChild(el('div', { class: 'avatar avatar-room', text: '#' }));
        row.appendChild(el('div', { class: 'fwd-chat-name', text: r.name + ' · Room' }));
        const tick = el('div', { class: 'fwd-chat-tick' });
        tick.appendChild(lucideIcon(picked ? 'check-circle-2' : 'circle'));
        row.appendChild(tick);
        row.addEventListener('click', () => toggleForwardPick(targetId));
        list.appendChild(row);
      }
    }

    // ── WhatsApp section ────────────────────────────────────────
    const chats = (state.chats || []).slice(0, 500);
    const waMatches = q
      ? chats.filter(c => (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(q)
          // masked c.phone can't match a typed full number — the chat id digits can
          || (q.replace(/\D/g, '').length >= 4 && String(c.id || '').split('@')[0].includes(q.replace(/\D/g, ''))))
      : chats;
    list.appendChild(el('div', { class: 'fwd-section-title' }, [
      lucideIcon('message-circle'),
      document.createTextNode(' WhatsApp'),
    ]));
    if (!waMatches.length) {
      list.appendChild(el('div', { class: 'empty-row', text: 'No conversations found' }));
    } else {
      waMatches.slice(0, 200).forEach(c => {
        const picked = state.forwardPicked.has(c.id);
        const row = el('div', { class: 'fwd-chat-row' + (picked ? ' picked' : '') });
        row.appendChild(el('div', { class: 'avatar ' + avatarColorClass(c.id), text: c.isGroup ? '#' : initials(c.name || c.phone) }));
        row.appendChild(el('div', { class: 'fwd-chat-name', text: c.name || c.phone || 'Unknown' }));
        const tick = el('div', { class: 'fwd-chat-tick' });
        tick.appendChild(lucideIcon(picked ? 'check-circle-2' : 'circle'));
        row.appendChild(tick);
        row.addEventListener('click', () => toggleForwardPick(c.id));
        list.appendChild(row);
      });
    }

    refreshIcons();
  }

  function toggleForwardPick(targetId) {
    if (state.forwardPicked.has(targetId)) {
      state.forwardPicked.delete(targetId);
    } else {
      if (state.forwardPicked.size >= 10) { toast('Max 10 targets per forward', 'error'); return; }
      state.forwardPicked.add(targetId);
    }
    renderForwardPickerList();
    const cnt = state.forwardPicked.size;
    const cntEl = document.getElementById('fwdPickedCount');
    if (cntEl) cntEl.textContent = cnt + ' picked · max 10';
    const btn = document.getElementById('fwdConfirmBtn');
    if (btn) btn.disabled = cnt === 0;
  }

  async function doForwardSelected() {
    // sourceIds set when picker opened — falls back to inbox
    // selection for backwards compat with old callers.
    const sourceMessageIds = Array.isArray(state.forwardSourceIds) && state.forwardSourceIds.length
      ? state.forwardSourceIds.slice()
      : Array.from(state.selectedMessageIds || []);
    const targetChatIds = Array.from(state.forwardPicked || []);
    if (!sourceMessageIds.length || !targetChatIds.length) return;
    const btn = document.getElementById('fwdConfirmBtn');
    if (btn) btn.disabled = true;
    try {
      const r = await api('/api/inbox/forward', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceMessageIds, targetChatIds }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.error) {
        toast('Forward failed: ' + (j.error || `HTTP ${r.status}`), 'error');
        if (btn) btn.disabled = false;
        return;
      }
      const total = sourceMessageIds.length * targetChatIds.length;
      const okCount = j.sent || 0;
      const failCount = j.failed || 0;
      if (failCount === 0) {
        toast('Forwarded ' + okCount + ' message' + (okCount > 1 ? 's' : ''), 'success');
      } else {
        toast(`Forwarded ${okCount}/${total} (${failCount} failed)`, failCount === total ? 'error' : 'info');
      }
      closeModal('forwardPickerModal');
      // Caller decides how to clear its selection (inbox vs. team).
      if (typeof state.forwardOnSuccess === 'function') {
        try { state.forwardOnSuccess(); } catch { /* ignore */ }
      } else {
        clearMessageSelection();
      }
      state.forwardSourceIds = null;
      state.forwardOnSuccess = null;
    } catch (err) {
      toast('Forward failed: ' + (err?.message || 'network'), 'error');
      if (btn) btn.disabled = false;
    }
  }

  // ============ Sending ============
  // ── Payment links (💳 in the composer) ───────────────────────────────────
  // Stripe (favorite provider — always listed first, featured rows pinned at
  // the very top) + Whop checkout links, 74 of them across MAD/EUR/USD, some
  // duplicates and some sharing a program name at two different prices.
  // Each row carries a provider chip so Stripe vs Whop reads at a glance.
  // Three things keep an agent from sending the wrong one: search that matches
  // the PRICE as well as the name, an admin-pinned shortlist on top, and a
  // preview pane — nothing leaves on a single click.
  const _payLink = {
    links: [], pinned: new Set(), featured: new Set(), currency: 'all', query: '',
    lang: 'dar', picked: null, loaded: false, source: '', updatedAt: null,
  };
  const PAY_LANG_LABEL = { dar: 'Darija', ar: 'عربية', fr: 'Français' };
  const PAY_PROVIDER_LABEL = { stripe: 'Stripe', whop: 'Whop' };
  const _payProvider = (l) => (l && l.provider === 'stripe' ? 'stripe' : 'whop');
  // Product names are typed by hand in the Whop dashboard — escape before
  // they reach innerHTML.
  const _payEsc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  function _payFormat(link) {
    const n = Number(link.amount).toLocaleString('fr-FR');
    const cur = String(link.currency || '').toLowerCase();
    if (cur === 'mad') return n + ' DH';
    if (cur === 'eur') return '€ ' + n;
    if (cur === 'usd') return '$ ' + n;
    return n + ' ' + cur.toUpperCase();
  }

  // Mirrors lib/payments/payment-links.js matchesQuery — name OR price.
  function _payMatches(link, q) {
    const t = String(q || '').trim().toLowerCase();
    if (!t) return true;
    if (String(link.name || '').toLowerCase().includes(t)) return true;
    const digits = t.replace(/[^0-9]/g, '');
    return !!digits && String(link.amount).includes(digits);
  }

  // Whop's hosted checkout ignores query parameters: there is no locale (the
  // page is English whichever language the message is in) and no lead
  // reference. Send the bare link rather than decorating the client's address
  // bar with parameters that do nothing. Mirrors buildUrl on the server.
  function _payBuildUrl(link) {
    return new URL(link.url).toString();
  }

  function _payRenderMessage(link) {
    const who = String(state.selectedChat?.name || '').trim().split(/\s+/)[0] || '';
    const agent = String(state.me?.displayName || state.me?.username || '').trim();
    const price = _payFormat(link);
    const url = _payBuildUrl(link);
    const sign = agent ? '— ' + agent + ', Lougha Institute' : '— Lougha Institute';
    if (_payLink.lang === 'ar') {
      return [
        ('السلام عليكم ' + who).trim() + ' 👋',
        'البرنامج: ' + link.name,
        'الثمن: ' + price,
        '',
        'هذا رابط الأداء الرسمي ديال Lougha Institute:',
        url,
        '',
        'من بعد ما تخلص، غادي يوصلك إيميل ديال التأكيد، والمقعد ديالك غادي يتحجز.',
        'إلا كان عندك شي سؤال أنا هنا 🙏',
        agent ? '— ' + agent + '، Lougha Institute' : '— Lougha Institute',
      ].join('\n');
    }
    if (_payLink.lang === 'fr') {
      return [
        ('Bonjour ' + who).trim() + ' 👋',
        'Programme : ' + link.name,
        'Montant : ' + price,
        '',
        'Voici votre lien de paiement officiel Lougha Institute :',
        url,
        '',
        'Vous recevrez un reçu par e-mail dès la confirmation, et votre place sera réservée.',
        'Je reste disponible si vous avez la moindre question 🙏',
        sign,
      ].join('\n');
    }
    return [
      ('Salam ' + who).trim() + ' 👋',
      'Programme : ' + link.name,
      'Taman : ' + price,
      '',
      'Hak l-lien rasmi dyal Lougha Institute bach t-khelles b l-carte :',
      url,
      '',
      'Mnin t-khelles ghadi ywselek email dyal ta2kid, o l-blasa dyalek t-t7jez.',
      'Ila kan 3andek chi sou2al ana hna 🙏',
      sign,
    ].join('\n');
  }

  function _payRenderChips() {
    const counts = { all: _payLink.links.length };
    for (const l of _payLink.links) counts[l.currency] = (counts[l.currency] || 0) + 1;
    const defs = [['all', 'Tout'], ['mad', '🇲🇦 DH'], ['eur', '🇪🇺 EUR'], ['usd', '🇺🇸 USD']];
    const box = document.getElementById('payLinkChips');
    if (!box) return;
    box.innerHTML = defs
      .filter(([k]) => k === 'all' || counts[k])
      .map(([k, label]) => '<button type="button" data-currency="' + k + '"'
        + (_payLink.currency === k ? ' class="on"' : '') + '>' + label
        + ' <span style="opacity:.6">' + (counts[k] || 0) + '</span></button>')
      .join('');
  }

  function _payRenderRows() {
    const rows = document.getElementById('payLinkRows');
    if (!rows) return;
    const matched = _payLink.links.filter(l =>
      (_payLink.currency === 'all' || l.currency === _payLink.currency) && _payMatches(l, _payLink.query));
    // Server order is already Stripe-first; filters keep it. Featured Stripe
    // rows lead the pinned group, whatever the admin pin list says.
    const isFeatured = (l) => _payLink.featured.has(l.id);
    const pinned = matched.filter(l => isFeatured(l)).concat(
      matched.filter(l => !isFeatured(l) && _payLink.pinned.has(l.id)));
    const rest = matched.filter(l => !isFeatured(l) && !_payLink.pinned.has(l.id));
    const row = (l) => {
      const flags = (l.dupes > 1 ? '<span class="paylink-flag">' + l.dupes + ' liens identiques</span>' : '')
        + (l.conflict ? '<span class="paylink-flag">2 prix</span>' : '');
      const prov = _payProvider(l);
      const star = isFeatured(l)
        ? '<span class="paylink-star on locked" title="Favori Stripe — toujours en haut">★</span>'
        : '<button type="button" class="paylink-star' + (_payLink.pinned.has(l.id) ? ' on' : '')
          + '" data-pin="' + _payEsc(l.id) + '" title="Épingler">'
          + (_payLink.pinned.has(l.id) ? '★' : '☆') + '</button>';
      return '<div class="paylink-row" data-provider="' + prov + '">'
        + star
        + '<span class="paylink-name"><b>' + _payEsc(l.name) + flags + '</b>'
        + '<small>' + _payEsc(l.url.replace('https://', '')) + '</small></span>'
        + '<span class="paylink-prov ' + prov + '" title="Paiement via ' + PAY_PROVIDER_LABEL[prov] + '">'
        + PAY_PROVIDER_LABEL[prov] + '</span>'
        + '<span class="paylink-price">' + _payEsc(_payFormat(l)) + '</span>'
        + '<button type="button" class="paylink-btn primary" data-pick="' + _payEsc(l.id) + '">Envoyer</button>'
        + '</div>';
    };
    let html = '';
    if (pinned.length) html += '<div class="paylink-sec">★ Épinglés</div>' + pinned.map(row).join('');
    if (rest.length) html += '<div class="paylink-sec">Tous les liens actifs (' + rest.length + ')</div>' + rest.map(row).join('');
    if (!matched.length) html = '<div class="paylink-empty">Aucun lien ne correspond.</div>';
    rows.innerHTML = html;
    const count = document.getElementById('payLinkCount');
    if (count) count.textContent = String(matched.length);
    const meta = document.getElementById('payLinkMeta');
    if (meta) {
      const when = _payLink.updatedAt ? MA.loc(new Date(_payLink.updatedAt), 'd', 'fr-FR') : '—';
      // Name every provider in the list — never just "Whop" on a mixed list.
      const hasStripe = _payLink.links.some(l => _payProvider(l) === 'stripe');
      const hasWhop = _payLink.links.some(l => _payProvider(l) === 'whop');
      const parts = [];
      if (hasStripe) parts.push('Stripe');
      if (hasWhop) parts.push('Whop' + (_payLink.source === 'whop' ? ' live' : ''));
      meta.textContent = (parts.length ? parts.join(' + ') : 'Catalogue') + ' · maj ' + when;
    }
  }

  async function _payLoad() {
    if (_payLink.loaded) return;
    try {
      const r = await api('/api/payment-links');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      _payLink.links = Array.isArray(j.links) ? j.links : [];
      _payLink.pinned = new Set((j.pinned || []).map(String));
      _payLink.featured = new Set((j.featured || []).map(String));
      _payLink.source = j.source || '';
      _payLink.updatedAt = j.updatedAt || null;
      _payLink.loaded = true;
    } catch (err) {
      console.warn('[paylinks] load failed:', err.message || err);
      toast('Liens de paiement indisponibles', 'error');
    }
  }

  function _payShowList() {
    document.getElementById('payLinkList')?.classList.remove('hidden');
    document.getElementById('payLinkPreview')?.classList.add('hidden');
  }

  function _payClose() {
    document.getElementById('payLinkPop')?.classList.add('hidden');
    document.getElementById('payLinkBtn')?.classList.remove('active');
    _payShowList();
  }

  function _payOpenPreview(id) {
    const link = _payLink.links.find(l => l.id === id);
    if (!link) return;
    _payLink.picked = link;
    const box = document.getElementById('payLinkText');
    if (box) {
      box.value = _payRenderMessage(link);
      box.classList.toggle('rtl', _payLink.lang === 'ar');
    }
    const label = document.getElementById('payLinkPreviewLang');
    if (label) label.textContent = PAY_LANG_LABEL[_payLink.lang] || '';
    const meta = document.getElementById('payLinkPreviewMeta');
    if (meta) meta.textContent = link.name + ' · ' + _payFormat(link) + ' · ' + PAY_PROVIDER_LABEL[_payProvider(link)];
    document.getElementById('payLinkList')?.classList.add('hidden');
    document.getElementById('payLinkPreview')?.classList.remove('hidden');
    box?.focus();
  }

  async function _paySavePins() {
    try {
      const r = await api('/api/payment-links/pins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinned: [..._payLink.pinned] }),
      });
      if (r.status === 403) { toast('Épingler : admin seulement', 'error'); return false; }
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return true;
    } catch (err) {
      console.warn('[paylinks] pin save failed:', err.message || err);
      toast('Épinglage non enregistré', 'error');
      return false;
    }
  }

  /* <propose-groups:begin> */
  // ── Propose groups (composer) — 2026-10-08, Ayoub ──────────────────────
  // Port of support2's composer picker (whatsapp-group-sender #631/#632/#634):
  // the team proposed classes by screenshotting the "Groups in process" table
  // and sending the picture. Same flow as the 💳 payment links: pick the
  // classes from the live group picker (/api/hubspot/group-picker — same
  // list, filters and ranking as Profile ▸ Add to a group), preview written
  // WhatsApp text (Arabic, English or both), then Send through the ordinary
  // sendText() so every channel rule holds (Cloud API 24h window, support2 /
  // App / SMS lanes). Nothing leaves on a single click.

  // <gofr-pure:begin> — no DOM, no state: the message builder (tested in node).
  // TWO messages, one per language, each complete on its own (Ayoub,
  // 2026-10-08: "bi 3arbiya direct o bi english direct" … "kol 7aja mratba
  // bo7dha"). Mixing both scripts in one bubble scrambles the lines on the
  // phone; one language per bubble reads cleanly in either direction.
  const GOFR = (function () {
    const DAYS = {
      ar: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
      en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    };
    const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const AND = { ar: ' و', en: ' & ' };
    const OR = { ar: ' أو ', en: ' or ' };
    const LIST_SEP = { ar: '، ', en: ', ' };
    const NUM = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣'];
    const MAX = NUM.length;
    const WEEK = [1, 2, 3, 4, 5, 6, 0]; // Monday first, like the class grid
    // Where a foreign client lives, from the phone's country code — so the
    // message adds "⏰ Your time 🇫🇷: 21:00–22:30". Longest prefix wins.
    // +1 (US/Canada, many zones) is left out on purpose: a wrong local hour
    // is worse than none.
    const ABROAD = [
      ['971', 'Asia/Dubai', '🇦🇪'], ['966', 'Asia/Riyadh', '🇸🇦'], ['974', 'Asia/Qatar', '🇶🇦'],
      ['965', 'Asia/Kuwait', '🇰🇼'], ['973', 'Asia/Bahrain', '🇧🇭'], ['968', 'Asia/Muscat', '🇴🇲'],
      ['352', 'Europe/Luxembourg', '🇱🇺'], ['353', 'Europe/Dublin', '🇮🇪'], ['351', 'Europe/Lisbon', '🇵🇹'],
      ['213', 'Africa/Algiers', '🇩🇿'], ['216', 'Africa/Tunis', '🇹🇳'],
      ['33', 'Europe/Paris', '🇫🇷'], ['32', 'Europe/Brussels', '🇧🇪'], ['31', 'Europe/Amsterdam', '🇳🇱'],
      ['34', 'Europe/Madrid', '🇪🇸'], ['39', 'Europe/Rome', '🇮🇹'], ['49', 'Europe/Berlin', '🇩🇪'],
      ['41', 'Europe/Zurich', '🇨🇭'], ['43', 'Europe/Vienna', '🇦🇹'], ['44', 'Europe/London', '🇬🇧'],
      ['45', 'Europe/Copenhagen', '🇩🇰'], ['46', 'Europe/Stockholm', '🇸🇪'], ['47', 'Europe/Oslo', '🇳🇴'],
      ['90', 'Europe/Istanbul', '🇹🇷'], ['20', 'Africa/Cairo', '🇪🇬'],
    ];
    const T = {
      hello: { ar: 'السلام عليكم 👋', en: 'Hello 👋' },
      teacher: { ar: 'الأستاذ(ة): ', en: 'Teacher: ' },
      introMany: {
        ar: (w) => w ? 'هذه هي المجموعات المتاحة لـ ' + w : 'هذه هي المجموعات المتاحة حاليا',
        en: (w) => w ? 'Here are the groups available for ' + w : 'Here are the groups available right now',
      },
      introOne: {
        ar: (w) => w ? 'هذه هي المجموعة المتاحة لـ ' + w : 'هذه هي المجموعة المتاحة حاليا',
        en: (w) => w ? 'Here is the group available for ' + w : 'Here is the group available right now',
      },
      kind: { ar: { kids: 'أطفال', adults: 'كبار' }, en: { kids: 'Kids', adults: 'Adults' } },
      level: { ar: 'المستوى ', en: 'level ' },
      starts: { ar: 'البداية: ', en: 'Starts: ' },
      today: { ar: 'اليوم', en: 'today' },
      same: { ar: 'نفس الوقت', en: 'same time' },
      yourTime: { ar: 'بتوقيتكم', en: 'Your time' },
      replyMany: {
        ar: (n) => 'المرجو الرد برقم المجموعة التي تناسبكم (' + n + ') لنحجز لكم المقعد 🙏',
        en: (n) => 'Please reply with the number of the group that suits you (' + n + ') and we will reserve your seat 🙏',
      },
      replyOne: {
        ar: 'إذا كانت هذه المجموعة تناسبكم، يكفي الرد بـ « نعم » لنحجز لكم المقعد 🙏',
        en: 'If this group suits you, just reply "yes" and we will reserve your seat 🙏',
      },
    };

    const pad2 = (n) => String(n).padStart(2, '0');
    const toMin = (hhmm) => { const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim()); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
    const fromMin = (min) => { const x = ((min % 1440) + 1440) % 1440; return pad2(Math.floor(x / 60)) + ':' + pad2(x % 60); };

    function abroadOf(phone) {
      let d = String(phone || '').replace(/\D/g, '');
      if (d.startsWith('00')) d = d.slice(2);
      if (!d || d.startsWith('212') || d.length < 8) return null;
      for (const [cc, zone, flag] of ABROAD) if (d.startsWith(cc)) return { cc, zone, flag };
      return null;
    }

    // "LC 104  kids" → "LC 104": the type is said once in the intro line.
    function classLabel(name) {
      const clean = String(name || '').replace(/\s+/g, ' ').trim();
      const short = clean.replace(/\s*\b(kids?|adults?|adultes?|enfants?|juniors?)\b\s*$/i, '').trim();
      return short || clean || '—';
    }

    const DAY_KEYS = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
    // The teachers7 grid first (a 4-day class has two times); HubSpot's
    // "monday-wednesday" + "17:30 to 19:00" only when the grid has nothing.
    function slotsOf(group) {
      const grid = Array.isArray(group && group.schedule_slots) ? group.schedule_slots : [];
      const fromGrid = grid
        .map(s => ({ dow: Number(s.dow), start: toMin(s.startTime), end: toMin(s.endTime) }))
        .filter(s => s.dow >= 0 && s.dow <= 6 && s.start != null);
      if (fromGrid.length) return fromGrid;
      const dows = [...new Set(String((group && group.schedule_days) || '').toLowerCase().split(/[-,/&\s]+/)
        .map(x => DAY_KEYS[x.slice(0, 3)]).filter(x => x != null))];
      const tm = /(\d{1,2})[:hH](\d{2})?\s*(?:to|-|–|à|a)\s*(\d{1,2})[:hH](\d{2})?/.exec(String((group && group.schedule_time) || ''));
      if (!dows.length || !tm) return [];
      const start = Number(tm[1]) * 60 + Number(tm[2] || 0), end = Number(tm[3]) * 60 + Number(tm[4] || 0);
      return dows.map(dow => ({ dow, start, end }));
    }

    // Same hours → one line ("Mon & Wed 17:30–19:00"), Monday first.
    function timeLines(group) {
      const byTime = new Map();
      for (const s of slotsOf(group).sort((a, b) => WEEK.indexOf(a.dow) - WEEK.indexOf(b.dow) || a.start - b.start)) {
        const key = s.start + '-' + s.end;
        if (!byTime.has(key)) byTime.set(key, { dows: [], start: s.start, end: s.end == null ? s.start : s.end });
        const line = byTime.get(key);
        if (!line.dows.includes(s.dow)) line.dows.push(s.dow);
      }
      return [...byTime.values()];
    }

    function daysText(dows, l) {
      const names = dows.map(d => DAYS[l][d]);
      if (names.length <= 1) return names.join('');
      if (names.length === 2) return names[0] + AND[l] + names[1];
      return names.slice(0, -1).join(LIST_SEP[l]) + AND[l] + names[names.length - 1];
    }
    function hoursText(start, end, l) {
      return l === 'ar' ? 'من ' + fromMin(start) + ' إلى ' + fromMin(end) : fromMin(start) + '–' + fromMin(end);
    }

    // The instant of the next class on that weekday (Morocco wall clock),
    // so a client abroad gets the offset of the day they will actually
    // attend (Europe changes clocks on 25 Oct; Morocco no longer does).
    function nextInstant(dow, startMin, now, maOffsetMin) {
      const wall = new Date(now + maOffsetMin * 60000);
      const ahead = (dow - wall.getUTCDay() + 7) % 7;
      const midnight = Date.UTC(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate() + ahead);
      return midnight + startMin * 60000 - maOffsetMin * 60000;
    }

    // The client's own clock, on its own line under the Morocco hours
    // ("⏰ Your time 🇫🇷: 21:00–22:30") — inline it wrapped mid-hour on phones.
    function localLine(line, abroad, l, deps) {
      if (!abroad || typeof deps.offsetOf !== 'function') return '';
      const at = nextInstant(line.dows[0], line.start, deps.now, deps.maOffset(deps.now));
      const there = deps.offsetOf(abroad.zone, at);
      if (!Number.isFinite(there)) return '';
      const diff = there - deps.maOffset(at);
      const head = '⏰ ' + T.yourTime[l] + ' ' + abroad.flag + ': ';
      if (!diff) return head + T.same[l];
      const s = line.start + diff;
      const shift = Math.floor(s / 1440);
      const days = shift ? daysText(line.dows.map(d => (d + shift + 7) % 7), l) + ' · ' : '';
      return head + days + hoursText(s, line.end + diff, l);
    }

    function startText(startDate, l, deps) {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(startDate || ''));
      if (!m) return '';
      const today = deps.todayKey;
      const key = m[1] + '-' + m[2] + '-' + m[3];
      if (!today || key < today) return '';
      if (key === today) return T.starts[l] + T.today[l];
      const dow = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay();
      const day = Number(m[3]), mon = Number(m[2]);
      if (l === 'en') return T.starts[l] + DAYS.en[dow] + ' ' + day + ' ' + MONTHS_EN[mon - 1];
      return T.starts[l] + DAYS.ar[dow] + ' ' + day + '/' + mon;
    }

    function levelsOf(group) {
      return [...new Set((String((group && group.level_of_groups) || '').toUpperCase().match(/\b(?:A0|A1|A2|B1|B2|C1|C2)\b/g) || []))];
    }
    // "Kids · level A1" only when every proposed class agrees on it.
    function audience(groups, l) {
      const kinds = [...new Set(groups.map(g => g.class_type_key || ''))];
      const kind = kinds.length === 1 ? T.kind[l][kinds[0]] || '' : '';
      const sets = groups.map(levelsOf);
      const common = sets.length ? sets[0].filter(x => sets.every(s => s.includes(x))) : [];
      const level = common.length === 1 ? T.level[l] + common[0] : '';
      return [kind, level].filter(Boolean).join(' · ');
    }

    function numbersText(n, l) {
      const nums = Array.from({ length: n }, (_, i) => String(i + 1));
      return nums.length <= 1 ? nums.join('') : nums.slice(0, -1).join(LIST_SEP[l]) + OR[l] + nums[nums.length - 1];
    }

    // One whole message in language l.
    function buildLang(groups, l, opts, deps, abroad) {
      const who = String(opts.who || '').trim();
      const aud = audience(groups, l);
      const intro = (groups.length === 1 ? T.introOne : T.introMany)[l](who ? '*' + who + '*' : '');
      const audText = aud ? (l === 'ar' ? ' (' + aud + ')' : ' — ' + aud) : '';
      const out = [T.hello[l], intro + audText + ':'];
      groups.forEach((g, i) => {
        out.push('');
        // Message 1 must start every line with Arabic, or the phone flips
        // that line left-to-right ("1️⃣ LC 186" read as a Latin line).
        out.push(NUM[i] + ' ' + (l === 'ar' ? 'المجموعة ' : '') + '*' + classLabel(g.name) + '*');
        const lines = timeLines(g);
        if (!lines.length) out.push('🗓 ' + (g.schedule_label || [g.schedule_days, g.schedule_time].filter(Boolean).join(' · ') || '—'));
        for (const line of lines) {
          out.push('🗓 ' + daysText(line.dows, l) + ' · ' + hoursText(line.start, line.end, l) + (abroad ? ' 🇲🇦' : ''));
          const local = localLine(line, abroad, l, deps);
          if (local) out.push(local);
        }
        const teacher = String(g.lougha_teachers || '').split(/\s*;\s*/).filter(Boolean).join(' / ');
        if (opts.teacher !== false && teacher) out.push('🧑‍🏫 ' + T.teacher[l] + teacher);
        const st = opts.start !== false ? startText(g.start_date, l, deps) : '';
        if (st) out.push('🚀 ' + st);
      });
      out.push('');
      out.push(groups.length === 1 ? T.replyOne[l] : T.replyMany[l](numbersText(groups.length, l)));
      return out;
    }

    // opts: { groups, langs, who, agent, phone, teacher, start,
    //         now, todayKey, maOffset(ms) → min, offsetOf(zone, ms) → min }
    // langs: ['ar', 'en'] (default) | ['ar'] | ['en'] — the agent's pick
    // (Ayoub, 2026-10-08 13:09: "dir lihom ikhtiyar 3arbiya wla English").
    // → one message per language, in that order ([] when nothing is picked).
    // The signature closes the LAST one — it is the last thing read; in
    // Arabic it leads with an Arabic word so the line stays right-to-left.
    function build(opts) {
      const groups = (opts.groups || []).slice(0, MAX);
      if (!groups.length) return [];
      const deps = {
        now: Number.isFinite(opts.now) ? opts.now : Date.now(),
        maOffset: typeof opts.maOffset === 'function' ? opts.maOffset : () => 0,
        offsetOf: opts.offsetOf, todayKey: opts.todayKey || '',
      };
      const abroad = abroadOf(opts.phone);
      const agent = String(opts.agent || '').trim();
      let langs = (Array.isArray(opts.langs) ? opts.langs : []).filter(l => l === 'ar' || l === 'en');
      if (!langs.length) langs = ['ar', 'en'];
      const msgs = langs.map(l => buildLang(groups, l, opts, deps, abroad));
      const last = msgs[msgs.length - 1];
      last.push(langs[langs.length - 1] === 'ar'
        ? '— فريق Lougha Institute' + (agent ? ' · ' + agent : '')
        : '— ' + (agent ? agent + ', ' : '') + 'Lougha Institute');
      return msgs.map(m => m.join('\n'));
    }

    return { MAX, abroadOf, classLabel, slotsOf, timeLines, audience, build };
  })();
  // <gofr-pure:end>

  const _gofr = {
    groups: [], facets: { days: [], times: [], levels: [] }, picked: [],
    q: '', level: '', classType: '', days: '', time: '', status: '',
    lang: 'both', teacher: true, start: true,
    chatId: '', contactId: '', leadId: '', contact: null, phone: '', requestId: 0, timer: null,
  };
  // Which message(s) go out: both (Arabic, then English), or just one.
  const GOFR_LANG_SETS = { both: ['ar', 'en'], ar: ['ar'], en: ['en'] };
  const GOFR_LANG_KEY = 's2.gofr.lang';
  // HubSpot spells the statuses three ways ("In Process", "active",
  // "expandable") — one spelling on screen, one colour each.
  const GOFR_STATUS = {
    'in process': { label: 'In Process', tone: 'open' },
    active: { label: 'Active', tone: 'live' },
    expandable: { label: 'Expandable', tone: 'grow' },
  };
  const _gofrStatusKey = (g) => String((g && g.group_status) || '').trim().toLowerCase();
  const _gofrEsc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  // Minutes east of UTC for an IANA zone at an instant — Intl is right for
  // every zone we list (only Africa/Casablanca is stale in browsers, and
  // Morocco's side comes from MA, the server's real offset).
  const _gofrOffCache = new Map();
  function _gofrOffsetOf(zone, ms) {
    try {
      let f = _gofrOffCache.get(zone);
      if (!f) _gofrOffCache.set(zone, f = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' }));
      const p = f.formatToParts(new Date(ms)).find(x => x.type === 'timeZoneName');
      const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec((p && p.value) || '');
      if (!m) return /^GMT$/.test((p && p.value) || '') ? 0 : NaN;
      return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0));
    } catch { return NaN; }
  }

  // The never-masked number of the open chat: the WhatsApp JID first, then
  // a profile/chat phone that carries no masking star.
  function _gofrPhone() {
    const id = String(state.selectedChatId || '');
    const jid = /^(\d{8,15})(?::\d+)?@s\.whatsapp\.net$/.exec(id);
    if (jid) return jid[1];
    for (const raw of [_gofr.contact?.phone, state.selectedChat?.phone]) {
      const s = String(raw || '');
      if (!s || s.includes('*')) continue;
      const d = s.replace(/\D/g, '');
      if (/^\d{8,15}$/.test(d)) return d;
    }
    return '';
  }

  // Student's first name(s) — the contact's firstname property, never the
  // phone a nameless chat carries as its name.
  function _gofrWho() {
    const c = _gofr.contact || {};
    const first = String(c.properties?.firstname || '').trim();
    const raw = first || String(c.name || '').trim().split(/\s+/).slice(0, 2).join(' ');
    if (!raw || /^[+\d(]/.test(raw) || raw.includes('@')) return '';
    return raw;
  }

  // Signature: the agent's display name, never an internal handle
  // ("Administration 1") — then just the institute.
  function _gofrAgent() {
    const name = String(state.me?.displayName || '').trim();
    if (!name || /^administration\b/i.test(name) || /^(ayoub|mansour|mmehanane)$/i.test(name)) return '';
    return name.split(/\s+/)[0];
  }

  function _gofrMessages() {
    return GOFR.build({
      groups: _gofr.picked, langs: GOFR_LANG_SETS[_gofr.lang] || GOFR_LANG_SETS.both, who: _gofrWho(), agent: _gofrAgent(), phone: _gofr.phone,
      teacher: _gofr.teacher, start: _gofr.start, now: Date.now(),
      todayKey: MA.dayKey(),
      maOffset: (ms) => MA.offsetAt(ms),
      offsetOf: _gofrOffsetOf,
    });
  }

  function _gofrChatAllowed() {
    const id = String(state.selectedChatId || '');
    if (!id) { toast('Open a conversation first', ''); return false; }
    if (id.startsWith('teacher:')) { toast('Group proposals are for clients, not teachers', 'error'); return false; }
    // Note mode routes the composer to an internal note — a proposal sent
    // that way would look sent and never reach the client.
    if (state.composeNoteMode) { toast('Turn Note mode off before proposing groups', 'error'); return false; }
    if (state.selectedChat?.isGroup || /@g\.us$/.test(id)) { toast('Open the client\'s own chat, not a group', 'error'); return false; }
    return true;
  }

  // The HubSpot contact of the open chat, for the person the profile follows
  // on a shared number (the picker route checks contactId against THAT
  // person and answers 409 profile_changed otherwise). The loaded profile
  // when it holds this chat, else the same /api/contact the panel reads —
  // never written into state, so the panel is untouched.
  async function _gofrContact() {
    const id = String(state.selectedChatId || '');
    const data = state.activeProfile;
    if (data && String(data.chatId || '') === id) {
      return data.isGroup || !data.contact ? null : { contact: data.contact, leadId: String(data.person?.leadId || '') };
    }
    const leadId = String(_activePersonLeadId(id) || '');
    try {
      const r = await api('/api/contact?chatId=' + encodeURIComponent(_profilePersonChatId())
        + (leadId ? '&leadId=' + encodeURIComponent(leadId) : ''));
      const j = r.ok ? await r.json().catch(() => null) : null;
      if (String(state.selectedChatId || '') !== id) return null;
      return j && !j.isGroup && j.contact ? { contact: j.contact, leadId: String(j.person?.leadId || leadId || '') } : null;
    } catch { return null; }
  }

  function _gofrMount() {
    if (document.getElementById('gofrBtn')) return true;
    const anchor = document.getElementById('payLinkBtn');
    if (!anchor) return false;
    const btn = el('button', {
      class: 'icon-btn compose-gofr-btn', id: 'gofrBtn', type: 'button',
      'aria-label': 'Propose groups', title: 'Propose groups — send the client a clean list of classes',
    }, [lucideIcon('users-round')]);
    anchor.insertAdjacentElement('afterend', btn);
    const pop = el('div', { id: 'gofrPop', class: 'paylink-pop gofr-pop hidden', role: 'dialog', 'aria-label': 'Propose groups' });
    pop.innerHTML = ''
      + '<div id="gofrList" class="paylink-pane">'
      + '<div class="paylink-head"><span class="paylink-brand"><i data-lucide="users-round"></i></span>'
      + '<b>Propose groups</b><span class="paylink-count" id="gofrCount"></span>'
      + '<button type="button" class="paylink-x" id="gofrClose" aria-label="Close">✕</button></div>'
      + '<div class="gofr-for" id="gofrFor"></div>'
      + '<div class="gofr-filters">'
      + '<div class="paylink-search gofr-search"><i data-lucide="search"></i>'
      + '<input type="text" id="gofrQuery" placeholder="Group or teacher" autocomplete="off"></div>'
      + '<select id="gofrLevel" class="gofr-select" aria-label="Level"></select>'
      + '<select id="gofrType" class="gofr-select" aria-label="Type">'
      + '<option value="">All types</option><option value="kids">Kids</option><option value="teenagers">Teenagers</option><option value="adults">Adults</option></select>'
      + '<select id="gofrStatus" class="gofr-select" aria-label="Status"><option value="">Any status</option>'
      + Object.entries(GOFR_STATUS).map(([k, v]) => '<option value="' + k + '">' + v.label + '</option>').join('') + '</select>'
      + '<select id="gofrDays" class="gofr-select" aria-label="Days"></select>'
      + '<select id="gofrTime" class="gofr-select" aria-label="Time"></select>'
      + '</div>'
      + '<div class="paylink-rows gofr-rows" id="gofrRows"></div>'
      + '<div class="paylink-foot"><span id="gofrMeta"></span>'
      + '<span class="paylink-langs gofr-langs" id="gofrLangs" role="group" aria-label="Message language">'
      + '<button type="button" data-lang="both">عربية + English</button>'
      + '<button type="button" data-lang="ar">عربية</button>'
      + '<button type="button" data-lang="en">English</button></span>'
      + '<button type="button" class="paylink-btn primary" id="gofrNext" disabled>Preview</button></div>'
      + '</div>'
      + '<div id="gofrPreview" class="paylink-pane hidden">'
      + '<div class="paylink-head"><button type="button" class="paylink-back" id="gofrBack" aria-label="Back">←</button>'
      + '<b>Message preview</b><span class="paylink-count" id="gofrPreviewLang"></span>'
      + '<button type="button" class="paylink-x" id="gofrClose2" aria-label="Close">✕</button></div>'
      + '<div class="gofr-opts">'
      + '<label><input type="checkbox" id="gofrOptTeacher" checked> Teacher name</label>'
      + '<label><input type="checkbox" id="gofrOptStart" checked> Start date (classes not started yet)</label>'
      + '<span class="gofr-abroad" id="gofrAbroad"></span>'
      + '</div>'
      + '<div class="paylink-preview-body gofr-two">'
      + '<div id="gofrBoxAr"><label class="gofr-msg-label" id="gofrLabelAr">عربية</label>'
      + '<textarea id="gofrTextAr" class="paylink-text gofr-text rtl" rows="10" dir="rtl" lang="ar"></textarea></div>'
      + '<div id="gofrBoxEn"><label class="gofr-msg-label" id="gofrLabelEn">English</label>'
      + '<textarea id="gofrTextEn" class="paylink-text gofr-text" rows="10" dir="ltr" lang="en"></textarea></div>'
      + '</div>'
      + '<div class="paylink-foot"><span id="gofrPreviewMeta"></span>'
      + '<button type="button" class="paylink-btn primary" id="gofrSend">Send both</button></div>'
      + '</div>';
    btn.insertAdjacentElement('afterend', pop);
    return true;
  }

  function _gofrSetOptions(id, values, emptyLabel, current) {
    const sel = document.getElementById(id);
    if (!sel) return;
    const list = [...new Set([...(values || []), ...(current ? [current] : [])])];
    sel.innerHTML = '<option value="">' + _gofrEsc(emptyLabel) + '</option>'
      + list.map(v => '<option value="' + _gofrEsc(v) + '">' + _gofrEsc(v) + '</option>').join('');
    sel.value = current || '';
  }

  function _gofrShowList() {
    document.getElementById('gofrList')?.classList.remove('hidden');
    document.getElementById('gofrPreview')?.classList.add('hidden');
  }

  function _gofrClose() {
    document.getElementById('gofrPop')?.classList.add('hidden');
    document.getElementById('gofrBtn')?.classList.remove('active');
    _gofrShowList();
  }

  function _gofrRenderFooter() {
    const n = _gofr.picked.length;
    const next = document.getElementById('gofrNext');
    if (next) { next.disabled = !n; next.textContent = n ? 'Preview · ' + n : 'Preview'; }
    const meta = document.getElementById('gofrMeta');
    if (meta) meta.textContent = n ? n + ' picked · max ' + GOFR.MAX : 'Pick up to ' + GOFR.MAX + ' classes';
  }

  function _gofrRenderRows() {
    const rows = document.getElementById('gofrRows');
    if (!rows) return;
    // The status filter is local: the picker already returns every running
    // class (In Process · Active · Expandable), so no second request.
    const shown = _gofr.status ? _gofr.groups.filter(g => _gofrStatusKey(g) === _gofr.status) : _gofr.groups;
    const count = document.getElementById('gofrCount');
    if (count) count.textContent = String(shown.length);
    if (!shown.length) {
      rows.innerHTML = '<div class="paylink-empty">No running class matches these filters.<br>Clear a filter to see more.</div>';
      _gofrRenderFooter();
      return;
    }
    const pickedIds = _gofr.picked.map(g => String(g.id));
    rows.innerHTML = shown.map(g => {
      const at = pickedIds.indexOf(String(g.id));
      const lines = GOFR.timeLines(g);
      const sched = g.schedule_label || [g.schedule_days, g.schedule_time].filter(Boolean).join(' · ') || 'Schedule not set';
      const started = /^\d{4}-\d{2}-\d{2}/.test(String(g.start_date || '')) ? String(g.start_date).slice(0, 10) : '';
      const today = MA.dayKey();
      const startNote = started ? (started >= today ? 'starts ' + started.slice(8, 10) + '/' + started.slice(5, 7) : 'started ' + started.slice(8, 10) + '/' + started.slice(5, 7) + '/' + started.slice(2, 4)) : '';
      const meta = [g.level_of_groups, g.lougha_teachers, (Number(g.student_count) || 0) + ((Number(g.student_count) || 0) === 1 ? ' student' : ' students'), startNote].filter(Boolean).join(' · ');
      const st = GOFR_STATUS[_gofrStatusKey(g)] || { label: g.group_status || '', tone: 'other' };
      return '<button type="button" class="gofr-row' + (at >= 0 ? ' on' : '') + (lines.length ? '' : ' gofr-row-nosched')
        + '" data-gid="' + _gofrEsc(g.id) + '" aria-pressed="' + (at >= 0) + '">'
        + '<span class="gofr-check">' + (at >= 0 ? String(at + 1) : '') + '</span>'
        + '<span class="paylink-name"><b>' + _gofrEsc(g.name) + ' <em class="gofr-status gofr-st-' + st.tone + '">'
        + _gofrEsc(st.label) + '</em></b>'
        + '<small class="gofr-sched">' + _gofrEsc(sched) + '</small>'
        + '<small>' + _gofrEsc(meta) + '</small></span>'
        + '</button>';
    }).join('');
    _gofrRenderFooter();
  }

  async function _gofrLoad() {
    const rid = ++_gofr.requestId;
    const rows = document.getElementById('gofrRows');
    if (rows) rows.innerHTML = '<div class="paylink-empty">Loading classes…</div>';
    const params = new URLSearchParams({
      chatId: _gofr.chatId, contactId: _gofr.contactId, ...(_gofr.leadId ? { leadId: _gofr.leadId } : {}),
      mode: 'recommended', q: _gofr.q,
      days: _gofr.days, time: _gofr.time, level: _gofr.level, classType: _gofr.classType, intent: 'add',
    });
    try {
      const r = await api('/api/hubspot/group-picker?' + params.toString());
      const j = await r.json().catch(() => ({}));
      if (rid !== _gofr.requestId) return;
      if (!r.ok) throw new Error(j.error === 'profile_changed' ? 'The profile changed — close and open the picker again.'
        : j.error === 'WhatsApp contact required' ? 'Group proposals need a WhatsApp contact chat.'
        : (j.error || 'Could not load classes'));
      // Private one-to-one slots are never offered to a new student.
      _gofr.groups = (Array.isArray(j.groups) ? j.groups : []).filter(g => g.class_type_key !== 'individual');
      _gofr.facets = j.facets || _gofr.facets;
      _gofrSetOptions('gofrDays', _gofr.facets.days, 'Any days', _gofr.days);
      _gofrSetOptions('gofrTime', _gofr.facets.times, 'Any time', _gofr.time);
      _gofrRenderRows();
    } catch (err) {
      if (rid !== _gofr.requestId) return;
      _gofr.groups = [];
      if (rows) rows.innerHTML = '<div class="paylink-empty">' + _gofrEsc(err.message || 'Could not load classes') + '</div>';
      _gofrRenderFooter();
    }
  }

  // Fill the preview boxes for the chosen language(s); the other box hides.
  function _gofrFillPreview() {
    const langs = GOFR_LANG_SETS[_gofr.lang] || GOFR_LANG_SETS.both;
    const msgs = _gofrMessages();
    const name = { ar: 'عربية', en: 'English' };
    for (const [l, key] of [['ar', 'Ar'], ['en', 'En']]) {
      const at = langs.indexOf(l);
      document.getElementById('gofrBox' + key)?.classList.toggle('hidden', at < 0);
      const box = document.getElementById('gofrText' + key);
      if (box) box.value = at >= 0 ? (msgs[at] || '') : '';
      const label = document.getElementById('gofrLabel' + key);
      if (label) label.textContent = (langs.length > 1 ? 'Message ' + (at + 1) + ' · ' : 'Message · ') + name[l];
    }
    const many = langs.length > 1;
    const head = document.getElementById('gofrPreviewLang');
    if (head) head.textContent = many ? '2 messages' : '1 message';
    const send = document.getElementById('gofrSend');
    if (send) send.textContent = many ? 'Send both' : 'Send';
    const meta = document.getElementById('gofrPreviewMeta');
    if (meta) meta.textContent = _gofr.picked.map(g => GOFR.classLabel(g.name)).join(' · ')
      + ' · ' + (many ? 'Arabic, then English' : langs[0] === 'ar' ? 'Arabic' : 'English');
  }

  function _gofrSyncLangs() {
    document.querySelectorAll('#gofrLangs [data-lang]').forEach(b => {
      const on = b.getAttribute('data-lang') === _gofr.lang;
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', String(on));
    });
  }

  function _gofrOpenPreview() {
    if (!_gofr.picked.length) return;
    _gofrFillPreview();
    const ab = GOFR.abroadOf(_gofr.phone);
    const abroad = document.getElementById('gofrAbroad');
    if (abroad) abroad.textContent = ab ? 'Client abroad ' + ab.flag + ' — local time added' : '';
    document.getElementById('gofrList')?.classList.add('hidden');
    document.getElementById('gofrPreview')?.classList.remove('hidden');
    document.querySelector('#gofrPreview .gofr-two')?.scrollTo?.(0, 0);
  }

  async function _gofrOpen() {
    if (!_gofrChatAllowed()) return;
    const pop = document.getElementById('gofrPop');
    const btn = document.getElementById('gofrBtn');
    if (!pop || !btn) return;
    try { closeAttachMenu(); } catch {}
    try { _payClose(); } catch {}
    const chatId = String(state.selectedChatId || '');
    const found = await _gofrContact();
    if (String(state.selectedChatId || '') !== chatId) return;
    const contact = found?.contact;
    if (!contact?.id) { toast('This chat has no HubSpot contact yet — create or link the contact first', 'error'); return; }
    _gofr.contact = contact;
    _gofr.leadId = found.leadId || '';
    const props = contact.properties || {};
    // A new chat starts from the student's own level and age group; the
    // same chat keeps what the agent picked a minute ago.
    if (_gofr.chatId !== chatId || _gofr.contactId !== String(contact.id)) {
      Object.assign(_gofr, {
        chatId, contactId: String(contact.id), picked: [], q: '', days: '', time: '', status: '',
        level: _igpProfileLevel(props),
        classType: (() => { const t = _igpProfileClassType(props); return t === 'individual' ? '' : t; })(),
      });
    }
    _gofr.phone = _gofrPhone();
    const ab = GOFR.abroadOf(_gofr.phone);
    const who = _gofrWho();
    const forBox = document.getElementById('gofrFor');
    if (forBox) forBox.textContent = 'For ' + (who || 'this client') + (ab ? ' · abroad ' + ab.flag : '');
    const q = document.getElementById('gofrQuery');
    if (q) q.value = _gofr.q;
    _gofrSetOptions('gofrLevel', ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'], 'Any level', _gofr.level);
    const type = document.getElementById('gofrType');
    if (type) type.value = _gofr.classType;
    const status = document.getElementById('gofrStatus');
    if (status) status.value = _gofr.status;
    let saved = '';
    try { saved = localStorage.getItem(GOFR_LANG_KEY) || ''; } catch {}
    _gofr.lang = GOFR_LANG_SETS[saved] ? saved : 'both';
    _gofrSyncLangs();
    _gofrShowList();
    pop.classList.remove('hidden');
    btn.classList.add('active');
    refreshIcons(pop);
    _gofrRenderFooter();
    await _gofrLoad();
    document.getElementById('gofrQuery')?.focus();
  }

  function initProposeGroups() {
    if (!_gofrMount()) return;
    const btn = document.getElementById('gofrBtn');
    const pop = document.getElementById('gofrPop');
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      if (!pop.classList.contains('hidden')) { _gofrClose(); return; }
      _gofrOpen();
    });
    for (const id of ['gofrClose', 'gofrClose2']) document.getElementById(id)?.addEventListener('click', (ev) => { ev.stopPropagation(); _gofrClose(); });
    document.getElementById('gofrBack')?.addEventListener('click', (ev) => { ev.stopPropagation(); _gofrShowList(); });
    document.getElementById('gofrQuery')?.addEventListener('input', (ev) => {
      _gofr.q = ev.target.value.trim();
      clearTimeout(_gofr.timer);
      _gofr.timer = setTimeout(_gofrLoad, 250);
    });
    for (const [id, key] of [['gofrLevel', 'level'], ['gofrType', 'classType'], ['gofrDays', 'days'], ['gofrTime', 'time']]) {
      document.getElementById(id)?.addEventListener('change', (ev) => { _gofr[key] = ev.target.value; _gofrLoad(); });
    }
    document.getElementById('gofrStatus')?.addEventListener('change', (ev) => { _gofr.status = ev.target.value; _gofrRenderRows(); });
    document.getElementById('gofrLangs')?.addEventListener('click', (ev) => {
      const b = ev.target.closest('[data-lang]');
      if (!b) return;
      _gofr.lang = b.getAttribute('data-lang');
      try { localStorage.setItem(GOFR_LANG_KEY, _gofr.lang); } catch {}
      _gofrSyncLangs();
    });
    document.getElementById('gofrRows')?.addEventListener('click', (ev) => {
      const row = ev.target.closest('[data-gid]');
      if (!row) return;
      const id = row.getAttribute('data-gid');
      const at = _gofr.picked.findIndex(g => String(g.id) === id);
      if (at >= 0) _gofr.picked.splice(at, 1);
      else {
        if (_gofr.picked.length >= GOFR.MAX) { toast('Up to ' + GOFR.MAX + ' classes in one message', 'error'); return; }
        const g = _gofr.groups.find(x => String(x.id) === id);
        if (g) _gofr.picked.push(g);
      }
      _gofrRenderRows();
    });
    document.getElementById('gofrNext')?.addEventListener('click', (ev) => { ev.stopPropagation(); _gofrOpenPreview(); });
    for (const [id, key] of [['gofrOptTeacher', 'teacher'], ['gofrOptStart', 'start']]) {
      document.getElementById(id)?.addEventListener('change', (ev) => {
        _gofr[key] = !!ev.target.checked;
        _gofrFillPreview();
      });
    }
    const toComposer = (text) => {
      // Free-form text, not a Meta template — clear any template routing
      // a previous pick left on the composer (same as the 💳 links).
      state.composeTemplate = null;
      state.composeTemplateRendered = '';
      E.composeInput.value = text;
      autoSize(); toggleSendBtn();
      E.composeInput.dispatchEvent(new Event('input', { bubbles: true }));
    };
    document.getElementById('gofrSend')?.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      const texts = (GOFR_LANG_SETS[_gofr.lang] || GOFR_LANG_SETS.both)
        .map(l => (document.getElementById(l === 'ar' ? 'gofrTextAr' : 'gofrTextEn')?.value || '').trim()).filter(Boolean);
      if (!texts.length || !_gofrChatAllowed()) return;
      // The chat may have changed under an open popover — never send a
      // list built for one client into another client's chat.
      if (String(state.selectedChatId || '') !== _gofr.chatId) { toast('The open chat changed — pick the classes again', 'error'); _gofrClose(); return; }
      const chatId = _gofr.chatId;
      _gofrClose();
      _gofr.picked = [];
      // Same road as the 💳 links: sendText() keeps every channel rule
      // (Cloud API 24h window, support2 / App / SMS lanes, WA-down) and
      // awaits the send. Arabic first, then English — the second only
      // when the first really left (a failed send puts its text back in
      // the composer) and the agent is still on the same chat.
      for (let i = 0; i < texts.length; i++) {
        if (String(state.selectedChatId || '') !== chatId) return;
        if (i > 0 && E.composeInput.value.trim()) {
          toast('The second message was not sent — the first one failed', 'error');
          return;
        }
        toComposer(texts[i]);
        await sendText();
      }
    });
    document.addEventListener('click', (ev) => {
      if (pop.classList.contains('hidden')) return;
      // A row click re-renders the list, so the target is detached by the
      // time the event reaches document — not an outside click.
      if (!ev.target || !ev.target.isConnected) return;
      if (ev.target.closest('#gofrPop') || ev.target.closest('#gofrBtn')) return;
      _gofrClose();
    });
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && !pop.classList.contains('hidden')) _gofrClose();
    });
    refreshIcons(btn.parentElement);
  }
  /* <propose-groups:end> */

  function initPayLinks() {
    const btn = document.getElementById('payLinkBtn');
    const pop = document.getElementById('payLinkPop');
    if (!btn || !pop) return;

    btn.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      if (!pop.classList.contains('hidden')) { _payClose(); return; }
      if (!state.selectedChatId) { toast('Ouvre une conversation d\'abord'); return; }
      closeAttachMenu();
      await _payLoad();
      _payRenderChips();
      _payRenderRows();
      _payShowList();
      pop.classList.remove('hidden');
      btn.classList.add('active');
      document.getElementById('payLinkQuery')?.focus();
    });

    document.getElementById('payLinkClose')?.addEventListener('click', (ev) => { ev.stopPropagation(); _payClose(); });
    document.getElementById('payLinkBack')?.addEventListener('click', (ev) => { ev.stopPropagation(); _payShowList(); });
    document.getElementById('payLinkQuery')?.addEventListener('input', (ev) => {
      _payLink.query = ev.target.value;
      _payRenderRows();
    });
    document.getElementById('payLinkChips')?.addEventListener('click', (ev) => {
      const chip = ev.target.closest('[data-currency]');
      if (!chip) return;
      _payLink.currency = chip.getAttribute('data-currency');
      _payRenderChips();
      _payRenderRows();
    });
    document.getElementById('payLinkLangs')?.addEventListener('click', (ev) => {
      const b = ev.target.closest('[data-lang]');
      if (!b) return;
      _payLink.lang = b.getAttribute('data-lang');
      b.parentElement.querySelectorAll('button').forEach(x => x.classList.toggle('on', x === b));
    });
    document.getElementById('payLinkRows')?.addEventListener('click', async (ev) => {
      const pick = ev.target.closest('[data-pick]');
      if (pick) { _payOpenPreview(pick.getAttribute('data-pick')); return; }
      const pin = ev.target.closest('[data-pin]');
      if (!pin) return;
      const id = pin.getAttribute('data-pin');
      const had = _payLink.pinned.has(id);
      if (had) _payLink.pinned.delete(id); else _payLink.pinned.add(id);
      _payRenderRows();
      // Server rejects non-admins — put the star back so the UI never claims
      // a pin the team won't actually see.
      if (!await _paySavePins()) {
        if (had) _payLink.pinned.add(id); else _payLink.pinned.delete(id);
        _payRenderRows();
      }
    });

    document.getElementById('payLinkInsert')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const text = document.getElementById('payLinkText')?.value || '';
      if (!text.trim()) return;
      E.composeInput.value = text;
      E.composeInput.dispatchEvent(new Event('input', { bubbles: true }));
      _payClose();
      E.composeInput.focus();
    });

    document.getElementById('payLinkSend')?.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      const text = document.getElementById('payLinkText')?.value || '';
      if (!text.trim()) return;
      // Note mode routes the composer to an internal note. Sending a payment
      // link as a note would look sent but never reach the client.
      if (state.composeNoteMode) { toast('Désactive le mode Note avant d\'envoyer', 'error'); return; }
      // Free-form text, not a Meta template — clear any template routing a
      // previous pick left on the composer.
      state.composeTemplate = null;
      state.composeTemplateRendered = '';
      E.composeInput.value = text;
      _payClose();
      await sendText();
    });

    document.addEventListener('click', (ev) => {
      if (pop.classList.contains('hidden')) return;
      if (ev.target.closest('#payLinkPop') || ev.target.closest('#payLinkBtn')) return;
      _payClose();
    });
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && !pop.classList.contains('hidden')) _payClose();
    });
  }

  async function sendText() {
    const text = E.composeInput.value.trim();
    if (!text || !state.selectedChatId) return;
    const selectedChatIdAtSend = state.selectedChatId;
    // NOTE MODE — the composer is annotating, not messaging. Route to the
    // note endpoint and return BEFORE any send path can run: nothing here may
    // reach WhatsApp. (Guard sits first on purpose.)
    if (state.composeNoteMode) { await _submitInternalNote(text); return; }
    // Pin chat identity so a mid-send chat switch doesn't push the optimistic
    // bubble into the now-open chat (would look like Mary's message landed
    // in Bob's conversation). The send itself still completes server-side
    // and the socket update will populate the original chat's view; we just
    // skip the local push if the agent has navigated away.
    // Unified channel: honor the WhatsApp/SMS compose toggle on 1-1 contacts —
    // re-target the send to the chosen channel's chatId (same contact phone), so
    // the agent can reply by SMS or WhatsApp from one box. SMS goes to the
    // queue-based sms: branch server-side (no 24h window, no META templates —
    // channel:'sms' templates render as plain text and the server resolves
    // their {{vars}} in the sms: branch). Shared retarget logic lives in
    // _composeTargetChatId (also used by template quick-send + multi-send).
    let requestedChatId = _composeTargetChatId();
    const _cc = _composeChannel();
    // Unfilled {{var}} guard (2026-09-22 — a Test Done offer reached a client on
    // the support2 line reading "{{level}}"). The support2 line and the app have
    // no fill-form retry, so stop here, before anything is cleared. Vars the
    // server fills from real data (level, name, test_date…) are left to it — it
    // re-checks and refuses whatever it could not fill (PLACEHOLDER_UNFILLED).
    // Our WhatsApp line keeps its fill-form flow further down.
    if ((_cc === 'support2' || _cc === 'app') && /\{\{\s*[a-zA-Z0-9_]+\s*\}\}/.test(text)) {
      const catalog = _cc === 'support2' ? await loadVarCatalog() : [];
      const serverFills = new Set((catalog || []).filter(v => v.source !== 'manual').map(v => v.name));
      const open = [...new Set([...text.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g)].map(m => m[1]))]
        .filter(n => !serverFills.has(n));
      if (open.length) {
        const list = open.slice(0, 5).map(n => '{{' + n + '}}').join(', ');
        toast('Remplis ' + list + " avant d'envoyer — 3emmer " + list + ' 9bel ma tsifet.', 'error');
        return;
      }
    }
    const replyTo = state.replyingTo?.id || null;
    const replyingSnapshot = state.replyingTo;
    // If the agent picked a WABA-mapped template and didn't edit the body,
    // route via type=template so the message reaches the contact even
    // outside the 24h customer-service window.
    const tpl = state.composeTemplate;
    // Meta templates are a Cloud API concept — they mean nothing on the SMS
    // queue or on support2's Baileys socket, where the rendered text is simply
    // sent as-is.
    const usingTemplate = _cc !== 'sms' && _cc !== 'support2' && _cc !== 'app'
        && tpl && tpl.meta_name && tpl.meta_status === 'APPROVED'
        && text === (state.composeTemplateRendered || '').trim();
    let templatePayload = null;
    if (usingTemplate) {
      // Resolve every meta_param into a concrete string. For {name} /
      // {agent} / {date} / {time} we already have the values; for any
      // custom param, fall back to '' and let the agent edit + retry as
      // free-form if Meta complains.
      const now = new Date();
      const ctx = {
        name: state.selectedChat?.name || '',
        agent: state.me?.displayName || state.me?.username || '',
        date: MA.loc(now, 'd', 'en-GB'),
        time: MA.loc(now, 't', 'en-GB', { hour: '2-digit', minute: '2-digit' }),
      };
      // A personalized test link is minted per lead and is one-use, so it can
      // only come from the flow that just created it — the server has no way to
      // re-derive it, and re-minting on every send would burn a token per send.
      // Bind it to the chat it was minted for so a chat switch cannot carry a
      // stale URL into someone else's template.
      if (state.pendingTestLink && state.pendingTestLink.chatId === state.selectedChatId && state.pendingTestLink.url) {
        ctx.test_link = state.pendingTestLink.url;
      }
      const params = {};
      const missing = [];
      // Vars the SERVER resolves from real data (HubSpot mirror + booking +
      // system) — never nag the agent about these; the server overrides them
      // at send time anyway. Only truly-custom placeholders count as missing.
      const autoNames = new Set((_varCatalog || []).map(v => v.name));
      for (const p of (tpl.meta_params || [])) {
        if (ctx[p] != null && ctx[p] !== '') params[p] = ctx[p];
        else if (autoNames.has(p)) params[p] = '';
        else missing.push(p);
      }
      if (missing.length) {
        const proceed = confirm('Template "' + tpl.meta_name + '" has unfilled params: ' + missing.join(', ') + '\n\nSend anyway? (empty values will go to Meta — they may reject.)\n\nClick OK to send as a template anyway, or Cancel to send as a plain text message.');
        if (!proceed) {
          // Clear template routing — fall through to free-form text path.
        } else {
          for (const p of missing) params[p] = '';
          templatePayload = { templateId: tpl.id, templateParams: params };
        }
      } else {
        templatePayload = { templateId: tpl.id, templateParams: params };
      }
    }
    E.composeInput.value = '';
    autoSize();
    toggleSendBtn();
    cancelReply();
    // Clear template state on send (success or fail — the next message is
    // a new decision).
    const pendingTestLinkSnapshot = state.pendingTestLink;
    state.pendingTestLink = null;
    const tplSnapshot = state.composeTemplate;
    const tplRenderedSnapshot = state.composeTemplateRendered;
    state.composeTemplate = null;
    state.composeTemplateRendered = '';
    updateTemplateBadge();
    const restoreDraft = () => {
      // Send failed — give the agent back BOTH the typed text AND the
      // reply target instead of forcing them to retype from scratch.
      E.composeInput.value = text;
      autoSize();
      toggleSendBtn();
      if (replyingSnapshot && !state.replyingTo) { state.replyingTo = replyingSnapshot; renderReplyPreview(); }
      // Restore template state so a quick retry still routes via template.
      if (tplSnapshot) { state.composeTemplate = tplSnapshot; state.composeTemplateRendered = tplRenderedSnapshot; updateTemplateBadge(); }
      // ...and the minted link with it, or the retry would send the template
      // with an empty {{test_link}} and Meta would reject the whole thing.
      if (pendingTestLinkSnapshot) state.pendingTestLink = pendingTestLinkSnapshot;
    };
    // FB/IG comment threads (chatId 'fbm-comment:' / 'ig-comment:') are NOT
    // DM sends — the server replies via the Comments API and needs the
    // specific commentId, else it 400s with "commentId required". Reply to the
    // most recent inbound comment in the open thread (its commentId is stored
    // on the inbound row by the webhook adapter).
    let commentIdPayload = null;
    if (/^(fbm|ig)-comment:/.test(requestedChatId)) {
      const lastComment = [...(state.messages || [])].reverse()
        .find(m => !m.fromMe && m.commentId);
      if (lastComment) commentIdPayload = { commentId: lastComment.commentId };
    }
    // Disable the send button during the network round-trip so a second
    // click can't fire a duplicate message — visible spinner state via
    // .sending class also tells the agent the action is in flight.
    const sendBtn = E.sendBtn;
    if (sendBtn) {
      sendBtn.disabled = true;
      sendBtn.classList.add('sending');
    }
    try {
      // Reply INSIDE the Lougha app — the screen the client is actually looking
      // at. Its own route: none of the WhatsApp story (24h window, templates,
      // delivery receipts, reply-quoting) exists there, so none of it is sent.
      // Out through SUPPORT2's WhatsApp line. Leads7 has no socket on that
      // number — the server proxies to support2, which sends and keeps the
      // thread, so the client's reply lands in support2's inbox. Nothing here
      // pretends the message left our number: the server writes an internal
      // note on this conversation instead of a fake outbound bubble.
      if (_cc === 'support2') {
        if (!String(requestedChatId).startsWith('support2:')) {
          const r = await api('/api/inbox/chats/' + encodeURIComponent(requestedChatId) + '/send-via-support2', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text }),
          });
          const data = await r.json().catch(() => ({}));
          if (!r.ok || data.error) {
            // The server now sends a machine code next to a human message
            // (send-failure ledger, 2026-09-18); key on the code, show the text.
            const reason = String(data.code || data.error || '');
            const detail = data.error && String(data.error) !== reason ? ' (' + String(data.error).slice(0, 160) + ')' : '';
            toast(
              reason === 'PLACEHOLDER_UNFILLED' ? String(data.error)
              : reason === 'whatsapp_disconnected' ? 'WhatsApp dyal support2 machi mrbot — 3awed mn ba3d.'
              : reason === 'support2_bridge_not_configured' ? "L'bridge m3a support2 ma-t-configura-ch."
              : reason === 'support2_unreachable' ? 'Support2 ma-jaweb-ch — 3awed.' + detail
              : 'Envoi via support2 échoué — ' + (data.error || reason || 'réessaie.'),
              'error');
            restoreDraft();
            return;
          }
          if (state.selectedChatId === selectedChatIdAtSend && data.message) {
            _appendOpenPersonMessage(data.message, requestedChatId, 'support2');
          }
          toast('Msifta men ra9m support2 ✓', 'success');
          return;
        }
      }
      if (_cc === 'app') {
        if (!String(requestedChatId).startsWith('support2:')) {
          const r = await api('/api/inbox/send-app', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ chatId: requestedChatId, text }),
          });
          const data = await r.json().catch(() => ({}));
          if (!r.ok || data.error) {
            toast(data.error === 'no app thread for this chat'
              ? "Ce contact n'a pas de conversation dans l'app."
              : data.code === 'PLACEHOLDER_UNFILLED' ? String(data.error)
              : "Envoi dans l'app échoué — réessaie.", 'error');
            restoreDraft();
            return;
          }
          if (state.selectedChatId === selectedChatIdAtSend) {
            _appendOpenPersonMessage({
              id: data.id || Math.random().toString(36).slice(2), _personSource: 'app',
              chatId: requestedChatId, channel: 'app', fromMe: true,
              type: 'text', text, sentBy: state.me?.displayName || state.me?.username || '',
              timestamp: Date.now(),
            }, requestedChatId, 'app');
          }
          return;
        }
      }
      const r = await api('/api/inbox/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: requestedChatId, text, replyTo, ...(commentIdPayload || {}), ...(templatePayload || {}) })
      });
      const data = await r.json();
      if (data.error) {
        // WhatsApp adapter down (Meta outage / expired token). The server
        // answers 400 "WhatsApp غير متصل" — surface a clear toast instead of
        // the raw string, flip the conn pill, and sync the admin banner. The
        // draft is restored so nothing is lost.
        if (/غير متصل|not connected|not ready/i.test(String(data.error))) {
          state.clientState = 'DISCONNECTED';
          updateConnUI(false);
          toast('WhatsApp API down — message non envoyé. Réessaie plus tard.', 'error');
          restoreDraft();
          return;
        }
        // A template variable the server couldn't auto-fill (e.g. {{zoom_link}}
        // for a lead with no booking). Let the agent type the value(s) and retry
        // via the template path so it still delivers outside the 24h window.
        const needVars = (Array.isArray(data.needParams) && data.needParams.length)
          ? data.needParams
          : (data.unresolvedPlaceholders
              ? (String(data.unresolvedPlaceholders).match(/\{\{([a-zA-Z0-9_]+)\}\}/g) || []).map(s => s.replace(/[{}]/g, ''))
              : []);
        if (needVars.length) {
          const sent = await _fillTemplateVarsAndResend(needVars, requestedChatId, text, tplSnapshot, replyTo, commentIdPayload);
          if (sent) return;
        }
        toast(data.error, 'error');
        restoreDraft();
        return;
      }
      if (replyTo && data.replyResolved === false) {
        // We sent the message, but the original message was already
        // pruned from our store. WhatsApp got a plain message, not a
        // reply — let the agent know so they can resend in-app.
        toast('Original message no longer in history — sent as plain message', 'warning');
      }
      if (data.sentVia === 'waba_template_auto') {
        toast('Sent as WABA template (matched automatically)', 'success');
      }
      // Only paint the optimistic bubble if the agent is still on the chat
      // they sent from. Otherwise the bubble would land in whatever chat
      // they switched to, which is data-leak-grade ugly.
      if (state.selectedChatId === selectedChatIdAtSend) {
        const sentMessage = data.message || {
          id: (data && (data.id)) || Math.random().toString(36).slice(2),
          chatId: requestedChatId, channel: (_cc || undefined), fromMe: true, type: 'text', text,
          status: (_cc === 'sms' ? 'queued' : undefined), timestamp: Date.now()
        };
        _appendOpenPersonMessage(sentMessage, requestedChatId,
          _cc === 'wa' ? 'leads7' : (_cc || sentMessage._personSource));
      }
    } catch (err) {
      toast(err?.message ? 'Send failed: ' + err.message : 'Send failed', 'error');
      restoreDraft();
    } finally {
      if (sendBtn) {
        sendBtn.disabled = false;
        sendBtn.classList.remove('sending');
      }
    }
  }

  // Template send blocked by unresolved variables (a price/programme/date the
  // server can't auto-fill): open a clean fill-form so the agent types each value
  // in one place — with FR labels + examples from the variable catalog and a live
  // WhatsApp-style preview — then re-send via the template path (templateId +
  // filled params) so it still delivers outside the 24h window. Returns a Promise
  // that resolves true when the retry was sent, false when cancelled. tplSnapshot
  // carries the template the agent used (so the WABA mapping survives the retry).
  async function _fillTemplateVarsAndResend(varNames, chatId, text, tplSnapshot, replyTo, commentIdPayload) {
    // "{{ level }}" → "{{level}}" so the substitution below matches it.
    text = String(text || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, '{{$1}}');
    await loadVarCatalog();
    const catBy = new Map((_varCatalog || []).map(v => [v.name, v]));
    return new Promise((resolveOuter) => {
      // Rebuild fresh each call — the var set differs per template/send.
      document.getElementById('tplFillModal')?.remove();
      const modal = el('div', { id: 'tplFillModal', class: 'modal hidden' });
      const backdrop = el('div', { class: 'modal-backdrop' });
      const card = el('div', { class: 'modal-card', style: 'max-width:460px;' });
      const head = el('header', { class: 'modal-head' });
      head.appendChild(el('h2', null, [lucideIcon('message-square'), document.createTextNode(' Compléter le message')]));
      const closeX = el('button', { class: 'icon-btn' }, [lucideIcon('x')]);
      head.appendChild(closeX);
      const body = el('div', { class: 'modal-body tpl-fill-body' });
      const tplLabel = (tplSnapshot && (tplSnapshot.name || tplSnapshot.meta_name)) || '';
      body.appendChild(el('p', { class: 'tpl-fill-sub' }, [
        document.createTextNode('Remplis les valeurs manquantes'),
        tplLabel ? el('span', { class: 'tpl-fill-tplname', text: ' · ' + tplLabel }) : null,
      ]));
      const inputs = {};
      const fields = el('div', { class: 'tpl-fill-fields' });
      for (const v of varNames) {
        const def = catBy.get(v) || {};
        const inp = el('input', { class: 'input', placeholder: def.example ? ('ex : ' + def.example) : ('{{' + v + '}}'), autocomplete: 'off' });
        inputs[v] = inp;
        fields.appendChild(el('label', { class: 'tpl-fill-field' }, [
          el('span', { class: 'tpl-fill-lbl', text: def.label || v }),
          inp,
        ]));
      }
      body.appendChild(fields);
      // Live preview — the message with every {{var}} filled; still-empty ones
      // stay highlighted so the agent sees exactly what the client will receive.
      const bubble = el('div', { class: 'tpl-fill-bubble' });
      body.appendChild(el('div', { class: 'tpl-fill-preview' }, [
        el('div', { class: 'tpl-fill-lbl', text: 'Aperçu' }),
        bubble,
      ]));
      function renderPreview() {
        let out = String(text || '');
        for (const v of varNames) {
          const val = inputs[v].value.trim();
          if (val) out = out.split('{{' + v + '}}').join(val);
        }
        clear(bubble);
        for (const part of out.split(/(\{\{[a-zA-Z0-9_]+\}\})/g)) {
          if (/^\{\{[a-zA-Z0-9_]+\}\}$/.test(part)) bubble.appendChild(el('span', { class: 'tpl-fill-missing', text: part }));
          else if (part) bubble.appendChild(document.createTextNode(part));
        }
      }
      const cancelBtn = el('button', { class: 'btn btn-ghost', text: 'Annuler' });
      const sendBtn = el('button', { class: 'btn btn-primary' }, [lucideIcon('send'), document.createTextNode(' Envoyer')]);
      body.appendChild(el('div', { class: 'tpl-fill-footer' }, [cancelBtn, sendBtn]));
      card.appendChild(head); card.appendChild(body);
      modal.appendChild(backdrop); modal.appendChild(card);
      document.body.appendChild(modal);

      let done = false;
      const finish = (val) => {
        if (done) return; done = true;
        closeModal('tplFillModal');
        setTimeout(() => modal.remove(), 220);
        resolveOuter(val);
      };
      const validate = () => { sendBtn.disabled = !varNames.every(v => inputs[v].value.trim()); };
      backdrop.addEventListener('click', () => finish(false));
      closeX.addEventListener('click', () => finish(false));
      cancelBtn.addEventListener('click', () => finish(false));
      for (const v of varNames) inputs[v].addEventListener('input', () => { renderPreview(); validate(); });

      async function doSend() {
        const filled = {};
        for (const v of varNames) {
          const val = inputs[v].value.trim();
          if (!val) { inputs[v].focus(); return; }
          filled[v] = val;
        }
        let outText = text;
        for (const [k, val] of Object.entries(filled)) outText = outText.split('{{' + k + '}}').join(val);
        const reqBody = { chatId, text: outText, replyTo, ...(commentIdPayload || {}) };
        if (tplSnapshot && tplSnapshot.id) { reqBody.templateId = tplSnapshot.id; reqBody.templateParams = filled; }
        sendBtn.disabled = true; sendBtn.classList.add('sending');
        try {
          const r = await api('/api/inbox/send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(reqBody) });
          const data = await r.json();
          if (data.error) { toast(data.error, 'error'); sendBtn.classList.remove('sending'); validate(); return; }
          toast('Envoyé ✓', 'success');
          if (state.selectedChatId === chatId) {
            state.messages.push(data.message || { id: Math.random().toString(36).slice(2), chatId, fromMe: true, type: 'text', text: outText, timestamp: Date.now() });
            renderMessages();
          }
          finish(true);
        } catch (e) {
          toast('Send failed: ' + (e.message || e), 'error');
          sendBtn.classList.remove('sending'); validate();
        }
      }
      sendBtn.addEventListener('click', doSend);
      for (const v of varNames) inputs[v].addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' && !sendBtn.disabled) { ev.preventDefault(); doSend(); }
      });

      renderPreview();
      validate();
      openModal('tplFillModal');
      refreshIcons();
      setTimeout(() => inputs[varNames[0]]?.focus(), 60);
    });
  }

  function toggleSendBtn() {
    const has = E.composeInput.value.trim().length > 0;
    E.sendBtn.classList.toggle('hidden', !has);
    E.micBtn.classList.toggle('hidden', has);
  }
  function autoSize() {
    E.composeInput.style.height = 'auto';
    E.composeInput.style.height = Math.min(120, E.composeInput.scrollHeight) + 'px';
  }

  // ============ Mic device picker ============
  // Sales agents on Macs often have a virtual loopback device (Background
  // Music, BlackHole, VB-Cable, etc.) selected as the OS-default input.
  // getUserMedia({audio:true}) blindly picks it → recorded voice notes
  // come back SILENT. Instead of fighting macOS System Settings, we
  // enumerate audio inputs in-app and let the agent pin a deviceId that
  // we then pass to getUserMedia via {deviceId:{exact:id}}. Choice
  // persists in localStorage so it survives page reloads and applies to
  // both the inbox recorder and the teamchat recorder.
  const MIC_LS_KEY = 'leads7.preferredMicDeviceId';
  const VIRTUAL_MIC_RE = /virtual|loopback|blackhole|soundflower|vb-cable|vb-audio|stream mix/i;

  function getPreferredMicId() {
    try { return localStorage.getItem(MIC_LS_KEY) || null; } catch { return null; }
  }
  function setPreferredMicId(id) {
    try { localStorage.setItem(MIC_LS_KEY, id || ''); } catch {}
  }
  // Build the audio constraints both recorders use. Keeps DSP on
  // (echo/noise/AGC), forces mono, and pins to the preferred deviceId
  // if the agent picked one. We intentionally do NOT set sampleRate —
  // forcing 48000 on a 44100-native device caused the ~-62dB scratchy
  // bug fixed 2026-05-17 (see startRecording comment).
  function buildMicConstraints() {
    const base = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1
    };
    const id = getPreferredMicId();
    if (id) base.deviceId = { exact: id };
    return base;
  }
  // Single entry point both the inbox and team-chat recorders go through.
  // If the saved deviceId points to a mic that no longer exists on this
  // machine (different OS user, USB device unplugged, etc.), getUserMedia
  // throws OverconstrainedError. Detect that, drop the stale id, and retry
  // once with default constraints so the recorder still opens.
  async function getMicStream() {
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: buildMicConstraints() });
    } catch (err) {
      const name = err && err.name;
      const constraint = err && err.constraint;
      if (name === 'OverconstrainedError' || constraint === 'deviceId') {
        const stale = getPreferredMicId();
        if (stale) {
          setPreferredMicId('');
          console.warn('[voice] cleared stale mic deviceId:', stale, err);
          toast('Saved mic dyalek mab9ach mawjoud — bedlna 3la system default. Click ⌄ jenb mic bach tpicker mic akhor.', 'warning');
          return await navigator.mediaDevices.getUserMedia({ audio: buildMicConstraints() });
        }
      }
      throw err;
    }
  }
  // Browsers (esp. Chrome) hide device labels until the page has been
  // granted mic permission at least once. We do a throwaway getUserMedia
  // to unlock labels, then immediately stop the tracks so the LED goes
  // back off. Returns [{deviceId,label,groupId,kind}] or [] on failure.
  async function enumerateMics() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return [];
    try {
      // First, enumerate WITHOUT trying to unlock — once any tab on this origin
      // has granted mic permission (which happens automatically the first time
      // the user clicks the mic button), labels are present.
      let devices = await navigator.mediaDevices.enumerateDevices();
      let audio = devices.filter(d => d.kind === 'audioinput');
      const haveLabels = audio.some(d => d.label && d.label.length > 0);
      if (!haveLabels) {
        // Labels missing — try to unlock with a short timeout so we never
        // hang the popover on a flaky permission state.
        const unlockPromise = navigator.mediaDevices.getUserMedia({ audio: true });
        const timeout = new Promise(resolve => setTimeout(() => resolve(null), 1500));
        const unlockStream = await Promise.race([unlockPromise.catch(() => null), timeout]);
        if (unlockStream && unlockStream.getTracks) {
          try { unlockStream.getTracks().forEach(t => t.stop()); } catch {}
        }
        devices = await navigator.mediaDevices.enumerateDevices();
        audio = devices.filter(d => d.kind === 'audioinput');
      }
      return audio.map(d => ({ deviceId: d.deviceId, label: d.label, groupId: d.groupId, kind: d.kind }));
    } catch (err) {
      console.warn('[mic-picker] enumerateDevices failed:', err);
      return [];
    }
  }

  function clearMicPickerPopover(pop) {
    while (pop.firstChild) pop.removeChild(pop.firstChild);
  }
  function closeMicPickerPopover() {
    const pop = document.getElementById('micPickerPopover');
    if (pop) pop.classList.add('hidden');
  }
  async function openMicPickerPopover() {
    const pop = document.getElementById('micPickerPopover');
    if (!pop) return;
    if (!pop.classList.contains('hidden')) { closeMicPickerPopover(); return; }
    // Render a loading row first so the popover opens instantly.
    clearMicPickerPopover(pop);
    const loading = document.createElement('div');
    loading.className = 'mic-picker-empty';
    loading.textContent = 'Loading mics…';
    pop.appendChild(loading);
    pop.classList.remove('hidden');
    const mics = await enumerateMics();
    const current = getPreferredMicId();
    clearMicPickerPopover(pop);
    const makeRow = (deviceId, label, isVirtual) => {
      const row = document.createElement('label');
      row.className = 'mic-picker-row';
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'leads7MicPicker';
      radio.value = deviceId;
      // Treat null/'' as "system default". Match either form.
      if ((deviceId || '') === (current || '')) radio.checked = true;
      const span = document.createElement('span');
      span.className = 'mic-label';
      span.textContent = label || '(unnamed device)';
      row.appendChild(radio);
      row.appendChild(span);
      if (isVirtual) {
        const warn = document.createElement('span');
        warn.className = 'mic-warn';
        warn.textContent = '(virtual — likely silent)';
        row.appendChild(warn);
      }
      row.addEventListener('click', () => {
        // Defer so the native label click first toggles the radio.
        setTimeout(() => {
          setPreferredMicId(deviceId);
          closeMicPickerPopover();
          toast('Mic: ' + (label || (deviceId ? deviceId.slice(0, 8) : 'System default')), '');
        }, 0);
      });
      return row;
    };
    pop.appendChild(makeRow('', 'System default', false));
    if (!mics.length) {
      const empty = document.createElement('div');
      empty.className = 'mic-picker-empty';
      empty.textContent = 'No mics detected. Grant mic permission in the browser.';
      pop.appendChild(empty);
      return;
    }
    mics.forEach(m => {
      pop.appendChild(makeRow(m.deviceId, m.label, VIRTUAL_MIC_RE.test(m.label || '')));
    });
  }

  // ============ Voice recording ============
  async function startRecording() {
    if (!navigator.mediaDevices) { toast('Mic not available', 'error'); return; }
    try {
      // Voice-optimized capture: enable browser DSP (echo cancel, noise
      // suppression, AGC) and force mono. We deliberately do NOT pin
      // sampleRate — forcing 48000 on a device whose native rate is 44100
      // can cause the browser to resample with attenuation and the
      // recorded signal comes out ~40 dB below normal speech (the
      // "scratchy/empty" bug fixed 2026-05-17). Without DSP, raw mic
      // input also carries room hiss + wind + AC hum into the encode.
      const stream = await getMicStream();
      // Log what the browser actually picked — useful when debugging a
      // device that ignores constraints or silently swaps to a different
      // input (Bluetooth headset, virtual mic, etc.). Also log the
      // resolved deviceId so we can confirm the mic-picker selection
      // actually took effect (browsers may downgrade {exact} silently).
      try {
        const track = stream.getAudioTracks()[0];
        const settings = track && track.getSettings ? track.getSettings() : {};
        const label = (track && track.label) || '';
        console.log('[voice] inbox track settings:', settings, 'label:', label, 'deviceId:', settings.deviceId);
        // Warn if the OS picked a virtual / loopback device — those return silence
        // and ship voice notes that the recipient hears as scratchy hiss.
        if (VIRTUAL_MIC_RE.test(label)) {
          toast('Mic dyalek = "' + label + '" (virtual device). Click the ⌄ next to the mic button to pick a real one.', 'warning');
        }
      } catch (e) { /* ignore */ }
      const mimeType = pickRecordMime();
      // 64 kbps Opus = ~8 KB/s for voice, still very small but gives the
      // encoder enough headroom to preserve dynamics. Previous setting
      // (32 kbps) combined with the forced 48 kHz constraint produced
      // near-silent audio (mean -62 dB) on some Macs.
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 64000 } : { audioBitsPerSecond: 64000 });
      const chunks = [];
      recorder.addEventListener('dataavailable', e => { if (e.data && e.data.size) chunks.push(e.data); });
      // 250ms timeslice → flush a chunk every 250ms instead of buffering
      // the whole take in one final blob. Prevents the "send stops at 6s"
      // bug where the last dataavailable arrived after we had already
      // finalized the upload.
      recorder.start(250);
      const startedAt = Date.now();
      const tick = setInterval(() => {
        const s = Math.floor((Date.now() - startedAt) / 1000);
        const mm = Math.floor(s / 60), ss = s % 60;
        E.recordTime.textContent = mm + ':' + (ss < 10 ? '0' : '') + ss;
      }, 200);
      // Live waveform — Web Audio AnalyserNode on the same stream the
      // recorder is consuming. Renders a moving green waveform on the
      // canvas inside the record bar. Cleaned up in stopRecordingTracks().
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const canvas = document.getElementById('recordWaveform');
      const drawState = { running: true, bars: [] };
      drawWaveform(canvas, analyser, drawState);
      // Reset pause state so a previous paused take doesn't carry over.
      const pauseBtn = document.getElementById('recordPauseBtn');
      if (pauseBtn) pauseBtn.classList.remove('paused');
      state.recording = { stream, recorder, chunks, tick, startedAt, audioCtx, analyser, drawState };
      // Note on when the take starts → it is a team voice note, whatever
      // happens to the toggle before it ends (finishRecording).
      state.recordingAsNote = !!state.composeNoteMode;
      state.recordingChatId = state.selectedChatId;
      _renderRecordNoteBadge(state.recordingAsNote);
      E.recordOverlay.classList.remove('hidden');
    } catch (err) {
      console.warn('[voice] getUserMedia failed:', err);
      toast(_micErrorMessage(err), 'error');
    }
  }

  // Map a getUserMedia error name to a human message. Generic "permission
  // denied" was misleading for non-permission failures (OverconstrainedError
  // from a stale saved deviceId, NotFoundError when no mic exists, etc.).
  function _micErrorMessage(err) {
    const n = err && err.name;
    if (n === 'NotAllowedError' || n === 'SecurityError') return 'Microphone permission denied';
    if (n === 'NotFoundError' || n === 'DevicesNotFoundError') return 'No microphone detected on this device';
    if (n === 'NotReadableError' || n === 'TrackStartError') return 'Microphone is busy (another app is using it)';
    if (n === 'OverconstrainedError') return 'Saved mic not available — pick another from the ⌄ next to mic';
    return 'Microphone error: ' + (n || (err && err.message) || 'unknown');
  }

  // Animate a rolling-bar visualization. Each frame we sample the analyser,
  // push the loudest delta into a fixed-size buffer, then redraw all bars.
  function drawWaveform(canvas, analyser, state) {
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext('2d');
    const buf = new Uint8Array(analyser.fftSize);
    const BARS = 80;
    state.bars = new Array(BARS).fill(2);
    function frame() {
      if (!state.running) return;
      analyser.getByteTimeDomainData(buf);
      // Peak amplitude in this slice (0-128, centered on 128).
      let peak = 0;
      for (let i = 0; i < buf.length; i++) {
        const d = Math.abs(buf[i] - 128);
        if (d > peak) peak = d;
      }
      // Skip pushing while paused — keeps the existing waveform visible.
      if (!state.paused) {
        state.bars.shift();
        state.bars.push(Math.max(2, Math.min(canvas.height - 4, (peak / 128) * canvas.height * 1.5)));
      }
      // Repaint
      const W = canvas.width = canvas.clientWidth * (window.devicePixelRatio || 1);
      const H = canvas.height = canvas.clientHeight * (window.devicePixelRatio || 1);
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = state.paused ? '#9ca3af' : '#25d366';
      const barW = W / BARS;
      const cy = H / 2;
      for (let i = 0; i < BARS; i++) {
        const h = (state.bars[i] / canvas.clientHeight) * H;
        ctx.fillRect(i * barW + barW * 0.2, cy - h / 2, barW * 0.6, h);
      }
      requestAnimationFrame(frame);
    }
    frame();
  }

  function togglePauseRecording() {
    if (!state.recording) return;
    const { recorder, drawState } = state.recording;
    const btn = document.getElementById('recordPauseBtn');
    try {
      if (recorder.state === 'recording') {
        recorder.pause();
        if (drawState) drawState.paused = true;
        btn?.classList.add('paused');
        const icon = btn?.querySelector('[data-lucide]');
        if (icon) { icon.setAttribute('data-lucide', 'play'); refreshIcons(); }
      } else if (recorder.state === 'paused') {
        recorder.resume();
        if (drawState) drawState.paused = false;
        btn?.classList.remove('paused');
        const icon = btn?.querySelector('[data-lucide]');
        if (icon) { icon.setAttribute('data-lucide', 'pause'); refreshIcons(); }
      }
    } catch (err) {
      console.warn('[voice] toggle pause failed:', err);
    }
  }
  function pickRecordMime() {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
    for (const m of candidates) if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m;
    return '';
  }
  function stopRecordingTracks() {
    if (!state.recording) return;
    const { recorder, stream, tick, audioCtx, drawState } = state.recording;
    clearInterval(tick);
    if (drawState) drawState.running = false;
    try { if (audioCtx && audioCtx.state !== 'closed') audioCtx.close(); } catch {}
    try { if (recorder.state !== 'inactive') recorder.stop(); } catch {}
    try { stream.getTracks().forEach(t => t.stop()); } catch {}
    // Reset pause UI for next take.
    const btn = document.getElementById('recordPauseBtn');
    btn?.classList.remove('paused');
    const icon = btn?.querySelector('[data-lucide]');
    if (icon && icon.getAttribute('data-lucide') === 'play') {
      icon.setAttribute('data-lucide', 'pause');
      refreshIcons();
    }
  }
  // "Team ghir" tag on the record bar while a Note take runs — the agent
  // must see this clip is not going to the client.
  function _renderRecordNoteBadge(on) {
    const bar = E.recordOverlay;
    if (!bar) return;
    let badge = bar.querySelector('.record-note-badge');
    if (!on) { if (badge) badge.remove(); bar.classList.remove('note-mode'); return; }
    if (!badge) {
      badge = el('span', {
        class: 'record-note-badge',
        text: '📝 Note team ghir',
        title: "Had l-audio kay-t-sjjel note dyal l-équipe — ma-kay-mchi-ch l'client",
        style: 'flex:none;margin-left:8px;padding:3px 8px;border-radius:999px;font:700 11px/1.2 inherit;background:#f59e0b;color:#fff;white-space:nowrap',
      });
      (bar.querySelector('.record-status') || bar).appendChild(badge);
    }
    bar.classList.add('note-mode');
  }

  function cancelRecording() {
    stopRecordingTracks();
    state.recording = null;
    state.recordingMode = null;
    state.recordingAsNote = false;
    _renderRecordNoteBadge(false);
    E.recordOverlay.classList.add('hidden');
    E.recordTime.textContent = '0:00';
    // If the user was mid-batch and cancelled the voice take, put the
    // composer back into batching layout (regular controls hidden) so
    // the preview strip keeps owning the visible composer area.
    if (state.pendingAttachments?.length || state.pendingVoiceBlob) {
      document.querySelector('.compose')?.classList.add('batching');
    }
  }
  async function finishRecording() {
    if (!state.recording) return;
    const { recorder, chunks, startedAt } = state.recording;
    // Reject takes shorter than 350ms — usually a misclick. Saves a server
    // roundtrip and an awkward 0-second voice note in the chat.
    if (Date.now() - startedAt < 350) {
      cancelRecording();
      toast('Recording too short', 'error');
      return;
    }
    // requestData() flushes any in-flight buffer BEFORE stop, so we don't
    // lose the tail of the recording when the browser hadn't yet emitted
    // the final dataavailable event.
    try { if (recorder.state === 'recording') recorder.requestData(); } catch {}
    await new Promise(res => {
      if (recorder.state === 'inactive') return res();
      recorder.addEventListener('stop', res, { once: true });
      try { recorder.stop(); } catch { res(); }
    });
    stopRecordingTracks();
    const blob = new Blob(chunks, { type: (chunks[0] && chunks[0].type) || 'audio/webm' });
    const durationMs = Date.now() - startedAt;
    const mode = state.recordingMode;
    const asNote = !!state.recordingAsNote || !!state.composeNoteMode;
    const noteChatId = state.recordingChatId || state.selectedChatId;
    state.recordingChatId = null;
    state.recording = null;
    state.recordingMode = null;
    state.recordingAsNote = false;
    _renderRecordNoteBadge(false);
    E.recordOverlay.classList.add('hidden');
    E.recordTime.textContent = '0:00';

    if (!blob.size || !state.selectedChatId) return;
    // Note on (at the start or now) → team voice note, never a send.
    if (asNote) {
      if (state.pendingAttachments?.length || state.pendingVoiceBlob) {
        document.querySelector('.compose')?.classList.add('batching');
      }
      await _submitVoiceNote(blob, durationMs, noteChatId);
      return;
    }
    // Recording started from the multi-attach preview strip → don't
    // auto-send, queue the blob into the batch instead. The user then
    // hits the preview's send button to ship images + voice together.
    if (mode === 'attach') {
      state.pendingVoiceBlob = blob;
      state.pendingVoiceDurationMs = durationMs;
      // Re-apply the batching layout the overlay temporarily replaced
      // (we removed .batching when starting the take so the overlay
      // had a non-zero .compose to fill). Render preview so the strip
      // shows the new "Voice ready" pill.
      if (state.pendingAttachments?.length || state.pendingVoiceBlob) {
        document.querySelector('.compose')?.classList.add('batching');
      }
      renderAttachPreviewVoicePill();
      return;
    }
    // Inline "sending..." placeholder so the agent gets feedback. Replaced
    // by the real message once the server responds. Pin the chat we sent
    // from so the placeholder cleanup + real message land in the right
    // conversation even if the agent switches chats mid-upload (mirrors
    // sendText's requestedChatId guard).
    const selectedChatIdAtRecord = state.selectedChatId;
    const requestedChatId = _composeTargetChatId();
    const voiceChannel = _composeChannel();
    const voiceSource = voiceChannel === 'wa' ? 'leads7' : voiceChannel;
    const placeholderId = 'tmp-voice-' + Math.random().toString(36).slice(2);
    state.messages.push({
      id: placeholderId, chatId: requestedChatId, fromMe: true,
      type: 'audio', text: '[sending voice…]', timestamp: Date.now(), pending: true,
      _personSource: voiceSource || undefined,
    });
    renderMessages();

    const fd = new FormData();
    fd.append('chatId', requestedChatId);
    if (_composeViaSupport2Line(requestedChatId)) fd.append('via', 'support2');
    fd.append('audio', blob, 'voice.webm');
    try {
      const r = await fetchT('/api/inbox/send-audio', {
        method: 'POST', headers: authHeaders(), body: fd
      }, 120000);
      const data = await r.json().catch(() => ({
        error: 'Voice send failed — HTTP ' + r.status + ((r.status === 502 || r.status === 503 || r.status === 504)
          ? ' (serveur indisponible ~40 s, déploiement en cours ?) — réessayez dans 30 s' : ''),
      }));
      // Only touch the message list if the agent is still on the chat they
      // recorded from — otherwise the placeholder/real bubble would render
      // into whatever chat they switched to. The server filed it correctly.
      if (state.selectedChatId !== selectedChatIdAtRecord) {
        if (data.error) toast(data.error, 'error');
        return;
      }
      // Drop the optimistic placeholder before injecting the real message.
      state.messages = state.messages.filter(m => m.id !== placeholderId);
      if (data.error) { toast(data.error, 'error'); renderMessages(); return; }
      if (data.message) _appendOpenPersonMessage(data.message, requestedChatId, voiceSource || data.message._personSource);
      else renderMessages();
    } catch (e) {
      if (state.selectedChatId === selectedChatIdAtRecord) {
        state.messages = state.messages.filter(m => m.id !== placeholderId);
        renderMessages();
      }
      toast('Voice send failed' + (e && e.message ? ': ' + e.message : ' (réseau / timeout — réessayez)'), 'error');
    }
  }

  // ============ Filter dropdowns (three stacked bars) ============
  // The inbox has three sibling dropdown bars — All conversations / Date /
  // Assign to. They behave as one group: opening one closes the others.
  const INBOX_FILTER_BARS = [
    ['filterMenu', 'filterBtn'],
    ['dateFilterMenu', 'dateFilterBtn'],
    ['assignFilterMenu', 'assignFilterBtn'],
  ];
  function closeAllInboxFilters() {
    for (const [m, b] of INBOX_FILTER_BARS) {
      const menu = document.getElementById(m);
      const btn = document.getElementById(b);
      if (menu) menu.classList.add('hidden');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    }
  }
  // Toggle one bar's menu; force=true/false forces open/closed. Always closes
  // the sibling menus first so only one is open at a time.
  function toggleDropdown(menu, btn, force) {
    if (!menu) return;
    const willOpen = force == null ? menu.classList.contains('hidden') : !!force;
    closeAllInboxFilters();
    if (willOpen) {
      menu.classList.remove('hidden');
      btn?.setAttribute('aria-expanded', 'true');
    }
  }
  // Back-compat wrapper for the All-conversations bar.
  function toggleFilter(force) { toggleDropdown(E.filterMenu, E.filterBtn, force); }

  // ============ Event wiring ============
  function bind() {
    E.loginBtn.addEventListener('click', tryLogin);
    E.passwordInput.addEventListener('keydown', e => { if (e.key === 'Enter') tryLogin(); });
    if (E.usernameInput) E.usernameInput.addEventListener('keydown', e => { if (e.key === 'Enter') E.passwordInput.focus(); });

    // Archive shortcut row — click toggles the archived-only view.
    // Keyboard support (Enter / Space) for the role="button" element.
    const archRow = document.getElementById('archiveRow');
    archRow?.addEventListener('click', () => setArchiveView(!state.archiveView));
    archRow?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setArchiveView(!state.archiveView); }
    });

    // Click-to-copy on contact-profile fields. Delegated so every freshly
    // rendered profile (chat switch, ticket reload, group profile) picks
    // it up without rewiring per row. Skipped for editable rows (those
    // have their own click→edit handler) and when the agent is mid-text-
    // selection (we don't want a click on a half-selected word to clobber
    // the selection with a copy of the whole field).
    document.getElementById('profileSections')?.addEventListener('click', (ev) => {
      const target = ev.target.closest('.field-val, .li-sub, .li-title');
      if (!target) return;
      const row = target.closest('.field-row, .list-item');
      if (row?.classList.contains('field-row-editable')) return;
      if (target.classList?.contains('empty')) return;
      // If the agent has actively selected text, leave that alone.
      const sel = window.getSelection?.();
      if (sel && sel.toString && sel.toString().length > 0) return;
      // Don't intercept clicks on inner buttons / links.
      if (ev.target.closest('button, a, [role="button"]')) return;
      const text = (target.textContent || '').trim();
      if (!text || text === '—') return;
      const copy = (navigator.clipboard?.writeText
        ? navigator.clipboard.writeText(text)
        : Promise.resolve().then(() => {
            // Fallback for older browsers / non-secure contexts
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed'; ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
          })
      );
      copy.then(() => {
        const preview = text.length > 48 ? text.slice(0, 45) + '…' : text;
        toast('Copied: ' + preview, 'success');
      }).catch(() => toast('Copy failed', 'error'));
    });

    // In-chat search wiring (button + bar controls + keyboard)
    document.getElementById('chatSearchBtn')?.addEventListener('click', () => {
      if (state.chatSearch.active) closeChatSearch();
      else openChatSearch();
    });
    document.getElementById('chatSearchClose')?.addEventListener('click', closeChatSearch);
    document.getElementById('chatSearchPrev')?.addEventListener('click', () => navigateChatSearchMatch(-1));
    document.getElementById('chatSearchNext')?.addEventListener('click', () => navigateChatSearchMatch(1));
    let _chatSearchT = null;
    const chatSearchInputEl = document.getElementById('chatSearchInput');
    if (chatSearchInputEl) {
      chatSearchInputEl.addEventListener('input', (e) => {
        clearTimeout(_chatSearchT);
        state.chatSearch.query = e.target.value || '';
        _chatSearchT = setTimeout(() => applyChatSearch(), 150);
      });
      chatSearchInputEl.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); closeChatSearch(); }
        else if (e.key === 'Enter') {
          e.preventDefault();
          navigateChatSearchMatch(e.shiftKey ? -1 : 1);
        }
      });
    }
    // Pre-fill last username
    const lastUser = localStorage.getItem(USERNAME_KEY);
    if (lastUser && E.usernameInput) E.usernameInput.value = lastUser;
    document.getElementById('logoutBtn')?.addEventListener('click', userLogout);
    document.getElementById('teamAddUserBtn')?.addEventListener('click', () => openUserEditor(null));
    document.getElementById('teamRefreshActivityBtn')?.addEventListener('click', loadTeamActivity);

    // Team chat sidebar — search + room create button. The search filters
    // whichever tab is active (teammates on Équipe, rooms on Rooms).
    document.getElementById('teamchatSearch')?.addEventListener('input', (e) => {
      state.teamchatSearch = e.target.value || '';
      renderTeamchatList();
    });
    document.getElementById('teamRoomCreateBtn')?.addEventListener('click', () => openTeamRoomModal());
    document.getElementById('teamRoomSaveBtn')?.addEventListener('click', saveTeamRoom);
    document.getElementById('userEditorSaveBtn')?.addEventListener('click', saveUserEditor);
    document.getElementById('userEditorDeleteBtn')?.addEventListener('click', deleteUserFromEditor);
    document.getElementById('uePasswordGenBtn')?.addEventListener('click', _generateUserPassword);
    document.getElementById('uePasswordToggleBtn')?.addEventListener('click', _toggleUserPasswordVisibility);
    document.getElementById('ueCurrentPwToggleBtn')?.addEventListener('click', _toggleCurrentPasswordVisibility);
    document.getElementById('ueCurrentPwCopyBtn')?.addEventListener('click', _copyCurrentPassword);
    document.getElementById('bookTestBtn')?.addEventListener('click', () => openPlacementTestModal());
    document.getElementById('websiteTestBtn')?.addEventListener('click', () => openWebsiteTestLink());
    document.getElementById('testRecordingBtn')?.addEventListener('click', () => openContactTestRecording());
    // Two-step booking footer (2026-06-15): phase 1 "Continuer" → note step,
    // phase 2 "Passer"/"Confirmer" → book, "Retour"/summary → back to slots.
    document.getElementById('ptContinueBtn')?.addEventListener('click', () => {
      const sel = state.placementTest?.selectedSlot;
      _ptGoToNoteStep(sel ? { kind: 'slot', teacherId: sel.teacherId, date: state.placementTest?.date, time: sel.time, teacherName: sel.teacherName } : null);
    });
    document.getElementById('ptBackBtn')?.addEventListener('click', () => _ptGoToSlotsStep());
    document.getElementById('ptNoteSummary')?.addEventListener('click', () => _ptGoToSlotsStep());
    document.getElementById('ptSkipBtn')?.addEventListener('click', () => _ptCommitBooking({ withNote: false }));
    document.getElementById('ptConfirmBtn')?.addEventListener('click', () => _ptCommitBooking({ withNote: true }));

    // Assignment dropdown
    E.assignBtn?.addEventListener('click', e => { e.stopPropagation(); toggleAssignDropdown(); });
    document.addEventListener('click', e => {
      if (!state.assignDropdownOpen) return;
      if (E.assignDropdown.contains(e.target) || E.assignBtn.contains(e.target)) return;
      closeAssignDropdown();
    });

    // Conversation toolbar overflow ("…") — same open/close idiom as Assign.
    // Rows inside it ARE the real toolbar buttons, so their own handlers run;
    // we only close the menu behind them.
    document.getElementById('chatMoreBtn')?.addEventListener('click', e => {
      e.stopPropagation(); toggleChatMoreMenu();
    });
    document.getElementById('chatMoreList')?.addEventListener('click', () => closeChatMoreMenu());
    document.addEventListener('click', e => {
      const menu = document.getElementById('chatMoreMenu');
      if (!menu || menu.classList.contains('hidden')) return;
      if (document.getElementById('chatMoreWrap')?.contains(e.target)) return;
      closeChatMoreMenu();
    });
    watchChatActionsWidth();

    // Conversation Details panel
    document.getElementById('convDetailsCloseBtn')?.addEventListener('click', () => {
      E.main().classList.remove('with-conv-details');
    });
    document.getElementById('convTakeOverBtn')?.addEventListener('click', takeOverChat);
    document.querySelectorAll('.conv-details-tab').forEach(t => {
      t.addEventListener('click', () => switchConvDetailsTab(t.getAttribute('data-conv-tab')));
    });
    document.getElementById('convNoteAddBtn')?.addEventListener('click', addNote);
    document.getElementById('convNoteInput')?.addEventListener('keydown', e => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); }
    });

    E.qrLogoutBtn.addEventListener('click', async () => {
      await api('/api/logout', { method: 'POST' });
      toast('Session reset — waiting for new QR…');
    });
    // Skip button — let the user into the app even with WhatsApp disconnected
    // so they can still work on Pipeline / Test Schedule / Dashboards / Tickets
    // (none of those need WA). Sets a sessionStorage flag so subsequent reloads
    // also skip (until they hard-reset).
    E.cloudApiSkipBtn?.addEventListener('click', () => {
      try { localStorage.setItem('leads7.skipWaError', '1'); } catch {}
      showScreen('app');
      updateConnUI(false);
      loadConversations();
      toast('WhatsApp not connected — send/receive disabled until token is fixed.', 'warning');
    });
    // Retry button on the cloud-api-error screen — re-fetches /api/status; if
    // the server has been restarted with a fresh token, init will have succeeded
    // and we'll route to the app screen automatically.
    E.cloudApiRetryBtn?.addEventListener('click', async () => {
      E.cloudApiRetryBtn.disabled = true;
      E.cloudApiRetryBtn.textContent = 'Checking…';
      try {
        const sr = await api('/api/status');
        const sd = await sr.json();
        state.clientState = sd.state || 'DISCONNECTED';
        state.driver = sd.driver || 'baileys';
        state.initError = sd.initError || null;
        if (state.clientState === 'READY') {
          showScreen('app'); updateConnUI(true); loadConversations();
          return;
        }
        if (E.cloudApiErrorDetail && state.initError) E.cloudApiErrorDetail.textContent = 'Initialization failed: ' + state.initError;
        toast('Still not ready. Have you restarted the server after updating the token?', 'warning');
      } finally {
        E.cloudApiRetryBtn.disabled = false;
        E.cloudApiRetryBtn.textContent = 'Retry connection';
      }
    });
    // WA-down banner (admin-only strip at the top of #app) — Retry re-inits
    // the adapter server-side; X dismisses for this page-load only.
    E.waDownRetryBtn?.addEventListener('click', () => retryWaConnection(E.waDownRetryBtn));
    E.waDownDismissBtn?.addEventListener('click', () => {
      state.waDownDismissed = true;
      syncWaDownBanner();
    });
    // New-build banner. Dismiss only snoozes: a stale bundle is a correctness
    // problem (see startAppVersionWatch), so it comes back until they reload.
    E.appUpdateReloadBtn?.addEventListener('click', () => location.reload());
    E.appUpdateDismissBtn?.addEventListener('click', () => {
      E.appUpdateBanner?.classList.add('hidden');
      _appUpdateQuietUntil = Date.now() + APP_VERSION_SNOOZE_MS;
    });
    // Mirror the same disconnect action from the Super Admin page header so
    // Ayoub doesn't have to dig into the QR side-panel to do it.
    document.getElementById('superadminDisconnectBtn')?.addEventListener('click', async () => {
      if (!confirm('Disconnect WhatsApp + force a new QR pairing? Team will stop receiving messages until you scan again.')) return;
      const r = await api('/api/logout', { method: 'POST' });
      if (r.ok) toast('WhatsApp disconnected — waiting for new QR…', 'success');
      else { const j = await r.json().catch(() => ({})); toast(j.error || 'Disconnect failed', 'error'); }
    });

    E.sidebarToggleBtn.addEventListener('click', () => {
      E.main().classList.toggle('sidebar-collapsed');
    });

    // Theme toggle — flips data-theme on <html> and persists in localStorage.
    // Initial value is set by an inline script in <head> (before stylesheet
    // renders) to avoid a flash; this only handles the manual flip.
    document.getElementById('themeToggleBtn')?.addEventListener('click', () => {
      const html = document.documentElement;
      const next = html.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
      html.setAttribute('data-theme', next);
      try { localStorage.setItem('theme', next); } catch (_) { /* private mode */ }
      // Re-render icons so the sun/moon swap picks up (Lucide observes
      // mutations but only refreshes on data-attribute changes inside icons).
      if (window.lucide) lucide.createIcons();
    });

    E.connActionBtn.addEventListener('click', handleConnAction);
    E.refreshChatsBtn.addEventListener('click', () => loadConversations());

    // Inbox ⇄ Équipe ⇄ Rooms segmented toggles (one instance per sidebar —
    // inbox + teamchat). Équipe and Rooms are two tabs over the SAME teamchat
    // view: the seg drives state.teamchatActiveTab and the list renders only
    // teammates (Équipe) or only rooms (Rooms). All navigate through showView
    // so active state, badges and view lifecycles stay the single source of
    // truth. Teachers never get the client inbox: CSS hides the Inbox segment
    // for .role-teacher, and this guard backs that up at the handler level.
    document.querySelectorAll('.teamchat-seg-btn').forEach(b => {
      b.addEventListener('click', () => {
        const seg = b.dataset.seg;
        const target = seg === 'inbox' ? 'inbox' : 'teamchat';
        if (target === 'inbox' && state.me?.role === 'teacher') return;
        if (target === 'teamchat') setTeamchatTab(seg === 'rooms' ? 'rooms' : 'dms');
        if (state.activeView === target) {
          // Already on the teamchat surface — a tab flip only needs the list
          // re-rendered, not the full view lifecycle (avoids a digest refetch).
          if (target === 'teamchat') renderTeamchatList();
          return;
        }
        showView(target);
      });
    });
    // The sidebar "+" room button proxies the original (admin-gated) header
    // button so the create-room modal wiring stays in one place.
    document.getElementById('teamRoomCreateBtnSide')?.addEventListener('click', () =>
      document.getElementById('teamRoomCreateBtn')?.click());

    document.querySelectorAll('.tab').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.tab').forEach(t => t.classList.remove('tab-active'));
        btn.classList.add('tab-active');
        state.activeChannel = btn.getAttribute('data-channel');
        kzRenderSidebarStatus();
        if (state.activeChannel !== 'kanza') E.conversationList?.classList.remove('kz-inbox');
        if (state.activeChannel === 'kanza') { kzEnterChannel(); return; }
        loadConversations();
      });
    });

    _buildInboxDateFilter();
    const dateFilterBtn = document.getElementById('dateFilterBtn');
    const dateFilterMenu = document.getElementById('dateFilterMenu');
    const assignFilterBtn = document.getElementById('assignFilterBtn');
    const assignFilterMenu = document.getElementById('assignFilterMenu');
    // Each bar's trigger toggles its own menu (and closes the siblings).
    E.filterBtn.addEventListener('click', () => toggleDropdown(E.filterMenu, E.filterBtn));
    dateFilterBtn?.addEventListener('click', () => toggleDropdown(dateFilterMenu, dateFilterBtn));
    assignFilterBtn?.addEventListener('click', () => toggleDropdown(assignFilterMenu, assignFilterBtn));
    document.addEventListener('click', e => {
      // A click on a dynamic "Assigned to X" row detaches its node mid-bubble
      // (the toggle re-renders the list synchronously), so contains() would
      // read it as an outside click and wrongly close the menu.
      if (!e.target.isConnected) return;
      const inside = INBOX_FILTER_BARS.some(([m, b]) => {
        const menu = document.getElementById(m), btn = document.getElementById(b);
        return (btn && btn.contains(e.target)) || (menu && menu.contains(e.target));
      });
      if (!inside) closeAllInboxFilters();
    });
    // Multi-select toggles (HubSpot-style): each click flips its own entry in
    // state.inboxFilters and the menu STAYS OPEN so several filters can be
    // combined in one visit (outside-click closes it). Each bar's clear row
    // ("All conversations" / "Any date" / "Anyone") resets just that bar and
    // closes its menu. Items may set `data-display-label` to override the pill
    // text (the raw row markup would include an avatar initial + "(me)").
    const toggleSet = (set, key) => { if (set.has(key)) set.delete(key); else set.add(key); };

    // Bar 1 · All conversations — quick-filter types + pipeline stages.
    E.filterMenu.addEventListener('click', e => {
      const itemEl = e.target.closest('.dropdown-item');
      if (!itemEl || !E.filterMenu.contains(itemEl)) return;
      const f = itemEl.getAttribute('data-filter') || 'all';
      const F = state.inboxFilters;
      if (f === 'all') {
        F.types.clear(); F.stages.clear(); F.crm.clear();
        toggleDropdown(E.filterMenu, E.filterBtn, false);
      } else if (f.startsWith('crm:')) {
        toggleSet(F.crm, f.slice(4));
      } else if (f.startsWith('pipeline:')) {
        toggleSet(F.stages, f.slice('pipeline:'.length));
      } else {
        toggleSet(F.types, f);
        if (f === 'mynotes' && F.types.has('mynotes')) _fetchNoteMentions();
      }
      syncFilterMenuChecks();
      updateFilterLabel();
      _renderMyNotesUi();
      renderConversations();
    });

    // Source chips — narrow the merged list to one platform (click again = all).
    document.getElementById('s2ChipLeads7')?.addEventListener('click', () => _s2mToggleSrc('leads7'));
    document.getElementById('s2ChipSupport2')?.addEventListener('click', () => _s2mToggleSrc('support2'));
    document.getElementById('s2ChipSms')?.addEventListener('click', () => _s2mToggleSrc('sms'));
    document.getElementById('s2ChipApp')?.addEventListener('click', () => _s2mToggleSrc('app'));

    // The orange "Mes notes" chip above the search box toggles the same
    // mynotes quick filter (mirror of support2's chip).
    document.getElementById('myNotesChip')?.addEventListener('click', () => {
      const F = state.inboxFilters;
      toggleSet(F.types, 'mynotes');
      // One choice across the rail — Mes notes replaces L7/S2/SMS/App.
      if (F.types.has('mynotes')) { state.srcFilter = ''; _fetchNoteMentions(); }
      syncFilterMenuChecks();
      updateFilterLabel();
      _renderMyNotesUi();
      renderConversations();
    });

    // Bar 2 · Date — single-select preset / custom range.
    dateFilterMenu?.addEventListener('click', e => {
      const itemEl = e.target.closest('.dropdown-item');
      if (!itemEl || !dateFilterMenu.contains(itemEl)) return;
      const f = itemEl.getAttribute('data-filter') || '';
      const F = state.inboxFilters;
      if (f === 'date-clear') {
        _resetInboxDateFilter();
        toggleDropdown(dateFilterMenu, dateFilterBtn, false);
      } else if (f.startsWith('date:')) {
        // Re-clicking the active preset clears it; 'between' reveals the custom
        // inputs but doesn't filter until both dates are set.
        const preset = f.slice('date:'.length);
        const D = F.date;
        if (D.preset === preset) { _resetInboxDateFilter(); }
        else { D.preset = preset; D.from = null; D.to = null; }
        _syncInboxDateUI();
      }
      syncFilterMenuChecks();
      updateFilterLabel();
      renderConversations();
    });

    // Bar 3 · Assign to — multi-select assignees (+ "Assigned to me").
    assignFilterMenu?.addEventListener('click', e => {
      const itemEl = e.target.closest('.dropdown-item');
      if (!itemEl || !assignFilterMenu.contains(itemEl)) return;
      const f = itemEl.getAttribute('data-filter') || '';
      const F = state.inboxFilters;
      if (f === 'assign-clear') {
        F.assignees.clear();
        toggleDropdown(assignFilterMenu, assignFilterBtn, false);
      } else if (f === 'assigned-me') {
        if (state.me?.id) toggleSet(F.assignees, state.me.id);
      } else if (f.startsWith('assigned:')) {
        toggleSet(F.assignees, f.slice('assigned:'.length));
      }
      syncFilterMenuChecks();
      updateFilterLabel();
      renderConversations();
    });

    E.searchInput.addEventListener('input', () => {
      clearTimeout(state.searchTimer);
      state.searchTimer = setTimeout(() => {
        state.searchQ = E.searchInput.value.trim();
        loadConversations();
      }, 250);
    });

    E.conversationList.addEventListener('click', e => {
      const item = e.target.closest('[data-chat-id]');
      if (item) selectConversation(item.getAttribute('data-chat-id'));
    });
    E.conversationList.addEventListener('scroll', () => {
      // Scroll events can fire dozens of times per frame. Coalesce them so a
      // single near-bottom gesture grows exactly one render window at a time.
      if (_convScrollFrame) return;
      _convScrollFrame = requestAnimationFrame(() => {
        _convScrollFrame = 0;
        _convMaybeGrowWindow();
      });
    }, { passive: true });

    E.chatBackBtn.addEventListener('click', () => {
      // Browser-style back: pop the most recent cross-chat link off the
      // history stack and re-select that conversation. When the stack is
      // empty fall back to the original mobile-collapse behaviour.
      if (state.chatHistory && state.chatHistory.length) {
        const previousId = state.chatHistory.pop();
        updateChatHistoryBodyClass();
        if (previousId && previousId !== state.selectedChatId) {
          // If the previous chat is no longer in state.chats (filter active,
          // search shrunk the list, server cleanup), selectConversation
          // would silently no-op. Restore the entry + tell the user instead
          // of having the button look broken.
          const stillExists = (state.chats || []).some(c => c.id === previousId);
          if (!stillExists) {
            state.chatHistory.push(previousId);
            updateChatHistoryBodyClass();
            toast('Previous chat no longer in this list — clear filters or search to reach it.', 'info');
            return;
          }
          selectConversation(previousId);
          return;
        }
      }
      E.main().classList.remove('show-chat');
    });

    E.composeInput.addEventListener('input', () => {
      autoSize(); toggleSendBtn();
      // If the agent edits the templated body, drop the WABA template
      // routing — Meta would reject a type=template send whose body no
      // longer matches the approved template anyway.
      if (state.composeTemplate && E.composeInput.value !== state.composeTemplateRendered) {
        state.composeTemplate = null;
        state.composeTemplateRendered = '';
        updateTemplateBadge();
      }
    });
    E.composeInput.addEventListener('keydown', e => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); }
      else if (e.key === 'Escape' && state.replyingTo) { e.preventDefault(); cancelReply(); }
    });
    E.sendBtn.addEventListener('click', sendText);
    E.composeChannelBtn?.addEventListener('click', _toggleComposeChannel);
    E.composeNoteBtn?.addEventListener('click', () => _setComposeNoteMode(!state.composeNoteMode));
    // @mention autocomplete on both note inputs (composer note-mode + panel).
    _attachMentionAc(E.composeInput, { noteModeOnly: true });
    _attachMentionAc(document.getElementById('convNoteInput'));
    // ✨ AI writing assistant — inbox composer. Inserts the polished draft into
    // #composeInput for human review; never auto-sends.
    document.getElementById('aiComposeBtn')?.addEventListener('click', () => {
      openComposeAssistant({
        seed: E.composeInput?.value || '',
        insert: (text) => {
          if (!E.composeInput) return;
          E.composeInput.value = text;
          E.composeInput.dispatchEvent(new Event('input'));
          try { autoSize(); toggleSendBtn(); } catch (_) {}
          E.composeInput.focus();
        },
      });
    });
    // ✨ AI writing assistant — bulk broadcast composer.
    document.getElementById('bulkAiBtn')?.addEventListener('click', () => {
      const bulk = document.getElementById('bulkMessageInput');
      openComposeAssistant({
        seed: bulk?.value || '',
        insert: (text) => { if (!bulk) return; bulk.value = text; bulk.dispatchEvent(new Event('input')); bulk.focus(); },
      });
    });
    E.replyPreviewClose?.addEventListener('click', cancelReply);
    // Stop any live MediaRecorder + MediaStream when the agent closes the
    // tab or navigates away; otherwise the mic LED stays on after reload.
    window.addEventListener('beforeunload', () => {
      try { if (state.recording) cancelRecording(); } catch {}
      try { if (state.teamchatRecording) cancelTeamchatRecording(); } catch {}
    });
    E.micBtn.addEventListener('click', startRecording);
    // Mic-device picker — gear/chevron button to the LEFT of #micBtn that
    // opens a popover listing every audio input. Click outside the
    // popover (or on the button again) to close. Mirrors the
    // #attachMenu open/close pattern.
    document.getElementById('micDeviceBtn')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      openMicPickerPopover();
    });
    document.addEventListener('click', (ev) => {
      const pop = document.getElementById('micPickerPopover');
      if (!pop || pop.classList.contains('hidden')) return;
      if (ev.target.closest('#micPickerPopover') || ev.target.closest('#micDeviceBtn')) return;
      closeMicPickerPopover();
    });
    E.cancelRecordBtn.addEventListener('click', cancelRecording);
    E.sendRecordBtn.addEventListener('click', finishRecording);
    document.getElementById('recordPauseBtn')?.addEventListener('click', togglePauseRecording);

    // Wired action icons
    E.profileBtn.addEventListener('click', toggleProfilePanel);
    E.botToggleBtn.addEventListener('click', e => {
      if (e.shiftKey) { openBotSettingsModal(); return; }
      toggleBot();
    });
    E.availabilityRequestBtn?.addEventListener('click', () => openAvailabilityRequestModal());
    document.getElementById('recruitmentLaneBtn')?.addEventListener('click', toggleRecruitmentLane);
    document.getElementById('influencerLaneBtn')?.addEventListener('click', toggleInfluencerLane);
    document.getElementById('searchyOpenBtn')?.addEventListener('click', openSearchyPanel);
    if (E.waCallBtn) {
      E.waCallBtn.addEventListener('click', () => waCall.start());
    }
    if (E.cxCallBtn) {
      E.cxCallBtn.addEventListener('click', () => openCxLinePicker(cxDialableDigits(state.selectedChat), state.selectedChat && state.selectedChat.name, E.cxCallBtn));
    if (E.kanzaCallBtn)
      E.kanzaCallBtn.addEventListener('click', () => kanzaRequestCall({ phone: cxDialableDigits(state.selectedChat), name: state.selectedChat && state.selectedChat.name }, E.kanzaCallBtn));
    }
    document.getElementById('botPromptSaveBtn')?.addEventListener('click', saveBotPrompt);
    document.getElementById('botPromptResetBtn')?.addEventListener('click', resetBotPrompt);

    // ─── Bulk send view bindings ───
    // Single tabs (only Inbox now, after the dropdown refactor) — fire
    // showView directly. Dropdown triggers are handled separately below
    // and the actual view-switching for grouped views happens via the
    // .view-tab-item menu items.
    document.querySelectorAll('.view-tab[data-view]').forEach(t => {
      t.addEventListener('click', () => showView(t.getAttribute('data-view')));
    });
    // Dropdown group triggers — toggle open/closed; outside click + Esc
    // close any open menu. Mirrors the existing #attachMenu pattern so
    // the bar's three dropdowns behave the same as the attach popover.
    document.querySelectorAll('.view-tab-group .view-tab-trigger').forEach(trigger => {
      trigger.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const group = trigger.closest('.view-tab-group');
        if (!group) return;
        const wasOpen = group.classList.contains('open');
        // Close all other groups first so only one is open at a time.
        document.querySelectorAll('.view-tab-group.open').forEach(g => {
          g.classList.remove('open');
          g.querySelector('.view-tab-trigger')?.setAttribute('aria-expanded', 'false');
        });
        if (!wasOpen) {
          group.classList.add('open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
    // Menu items — switch view + close the dropdown they live in.
    // Items on the Inbox dropdown also carry data-channel — that flips
    // state.activeChannel and refreshes the conversation list so a
    // single click in the top-bar dropdown opens the right channel.
    document.querySelectorAll('.view-tab-menu .view-tab-item').forEach(item => {
      item.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const view = item.getAttribute('data-view');
        if (!view) return;
        const group = item.closest('.view-tab-group');
        if (group) {
          group.classList.remove('open');
          group.querySelector('.view-tab-trigger')?.setAttribute('aria-expanded', 'false');
        }
        const channel = item.getAttribute('data-channel');
        // meta_review may ONLY view the Facebook + Instagram channels — the
        // merged Social tab qualifies (its list is hard-floored to FB/IG for
        // this role in renderConversations). Block any other channel tab
        // (WhatsApp / SMS / groups). UX backstop — the real gate is
        // /api/inbox/chats server-side.
        if (state.me?.role === 'meta_review' && channel && !['fbm', 'ig', 'social'].includes(channel)) return;
        if (channel) {
          // Update active-state across the dropdown so the user sees
          // which channel is currently selected without having to open
          // the menu again.
          const menu = item.closest('.view-tab-menu');
          if (menu) menu.querySelectorAll('.view-tab-item').forEach(el => el.classList.remove('view-tab-active'));
          item.classList.add('view-tab-active');
          state.activeChannel = channel;
          kzRenderSidebarStatus();
          _syncSearchyBar();
          if (channel !== 'kanza') E.conversationList?.classList.remove('kz-inbox');
          // Opening the safety-net tab is an explicit "show me now" — bypass
          // both the client throttle and the server memo so the admin never
          // triages a list that is up to a minute out of date.
          if (channel === 'orphans') loadOrphans({ force: true });
          // The notice belongs to this tab only — leaving it must clear the
          // banner even though no fetch runs.
          _paintOrphanNotice();
          _paintOrphanChips();
        }
        showView(view);
        if (channel && view === 'inbox') {
          // force:true bypasses the fingerprint short-circuit so the
          // sidebar re-renders with the new channel filter even if the
          // raw server payload is identical (which it usually is —
          // /api/inbox/chats?type=all returns the same rows regardless
          // of which channel the agent is viewing; the per-channel
          // narrowing happens client-side in renderConversations).
          try { loadConversations({ force: true }); } catch (_) { /* not ready on first paint */ }
        }
      });
    });
    // Social platform chips (Tous / Facebook / Instagram / TikTok) — narrow the
    // merged Social list to one platform. Pure client-side re-render, same
    // spirit as state.inboxFilters. Icons share the badge SVGs so the chip and
    // the row badge read as the same visual language.
    document.querySelectorAll('#socialChips .social-chip').forEach(chip => {
      const iconSlot = chip.querySelector('.social-chip-icon');
      const pf = chip.getAttribute('data-social');
      if (iconSlot && SOCIAL_PLATFORM_SVG[pf]) iconSlot.innerHTML = SOCIAL_PLATFORM_SVG[pf];
      chip.addEventListener('click', () => {
        state.socialFilter = pf || 'all';
        document.querySelectorAll('#socialChips .social-chip').forEach(c2 => {
          const on = c2 === chip;
          c2.classList.toggle('active', on);
          c2.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        renderConversations();
      });
    });
    // "Non attribuées" channel chips (Tous / WhatsApp / Facebook / Instagram).
    // Pure client-side narrowing of the already-loaded orphan rows.
    document.querySelectorAll('#orphanChips .social-chip').forEach(chip => {
      const ch = chip.getAttribute('data-orphan-channel') || 'all';
      const iconSlot = chip.querySelector('.social-chip-icon');
      const svg = ORPHAN_CHANNEL_SVG[ch] || SOCIAL_PLATFORM_SVG[ch];
      if (iconSlot && svg) iconSlot.innerHTML = svg;
      chip.addEventListener('click', () => {
        if (chip.disabled) return;
        state.orphans.channelFilter = ch;
        _paintOrphanChips();
        renderConversations();
      });
    });
    document.getElementById('orphanBulkUser')?.addEventListener('change', _paintOrphanBulk);
    document.getElementById('orphanBulkBtn')?.addEventListener('click', _runOrphanBulkAssign);
    // Outside click / Escape close — single document-level listener,
    // same idiom used by the attach popover.
    document.addEventListener('click', (ev) => {
      if (ev.target.closest('.view-tab-group')) return;
      document.querySelectorAll('.view-tab-group.open').forEach(g => {
        g.classList.remove('open');
        g.querySelector('.view-tab-trigger')?.setAttribute('aria-expanded', 'false');
      });
    });
    document.addEventListener('keydown', (ev) => {
      if (ev.key !== 'Escape') return;
      const open = document.querySelector('.view-tab-group.open');
      if (!open) return;
      open.classList.remove('open');
      open.querySelector('.view-tab-trigger')?.setAttribute('aria-expanded', 'false');
    });
    document.getElementById('bulkGroupSearch')?.addEventListener('input', e => {
      state.bulkSearch = e.target.value || '';
      renderBulkGroups();
    });
    document.getElementById('bulkSelectAllBtn')?.addEventListener('click', bulkSelectAllVisible);
    document.getElementById('bulkSelectNoneBtn')?.addEventListener('click', bulkSelectNone);
    document.getElementById('bulkAttachBtn')?.addEventListener('click', () => document.getElementById('bulkMediaInput').click());
    document.getElementById('bulkMediaInput')?.addEventListener('change', bulkOnAttach);
    document.getElementById('bulkSendBtn')?.addEventListener('click', bulkSend);
    document.getElementById('bulkCancelBtn')?.addEventListener('click', bulkCancel);
    document.getElementById('bulkHistoryBtn')?.addEventListener('click', openHistoryModal);

    // HubSpot Group Send
    document.getElementById('hsGroupSearch')?.addEventListener('input', onHsSearchInput);
    document.getElementById('hsSendBtn')?.addEventListener('click', hsSend);
    document.getElementById('hsCancelBtn')?.addEventListener('click', hsCancel);
    document.getElementById('hsClearBtn')?.addEventListener('click', () => {
      state.hsSelectedGroups = [];
      renderHsGroups();
      rebuildHsAggregate();
    });
    document.getElementById('bulkTemplatesBtn')?.addEventListener('click', () => openTemplatesModal('bulk'));
    document.getElementById('hsTemplatesBtn')?.addEventListener('click', () => openTemplatesModal('hubspot'));
    document.getElementById('hsAttachBtn')?.addEventListener('click', () => document.getElementById('hsMediaInput')?.click());
    document.getElementById('hsMediaInput')?.addEventListener('change', hsOnAttach);
    E.notesBtn.addEventListener('click', () => toggleConvDetailsPanel('notes'));
    E.templatesBtn.addEventListener('click', () => openTemplatesModal());
    // Inbox onboarding video (team-facing) — sidebar head, next to Refresh.
    document.getElementById('inboxTutorialBtn')?.addEventListener('click', () => openInboxTutorial());
    // Tutoriels picker — sibling of the text templates, one click per send.
    E.mediaTemplatesBtn?.addEventListener('click', () => openMediaTemplatesModal());
    document.getElementById('mtplSearch')?.addEventListener('input', (ev) => {
      state.mediaTemplateSearch = ev.target.value || '';
      renderMediaTemplates();
    });
    document.getElementById('mtplAddToggle')?.addEventListener('click', () => {
      const panel = document.getElementById('mtplUploadPanel');
      if (!panel) return;
      const opening = panel.classList.contains('hidden');
      panel.classList.toggle('hidden', !opening);
      if (opening) document.getElementById('mtplTitle')?.focus();
    });
    document.getElementById('mtplUploadCancel')?.addEventListener('click', () => _mtplCloseUpload());
    document.getElementById('mtplUploadSave')?.addEventListener('click', () => _mtplUpload());
    // 24h-window banner: "Ouvrir les templates" opens the same picker as
    // the header button. Pipeline-stage pre-filter is handled inside
    // openTemplatesModal() so the agent lands on stage-relevant rows.
    document.getElementById('window24hBannerOpenBtn')?.addEventListener('click', () => _openLeads7WindowTemplates());
    // Dismiss for this conversation. Stored in sessionStorage so a refresh
    // brings the banner back (safety net for 131047 errors).
    document.getElementById('window24hBannerDismissBtn')?.addEventListener('click', () => {
      const chatId = String(state.selectedChat?.id || '');
      if (chatId) _dismiss24hFor(chatId);
      document.getElementById('window24hBanner')?.classList.add('hidden');
    });
    E.documentsBtn.addEventListener('click', () => openDocumentsModal());
    E.emailComposeBtn.addEventListener('click', () => openEmailCompose());
    E.emailSendBtn.addEventListener('click', () => sendEmailFromModal());
    E.emailAiBtn.addEventListener('click', () => emailAiDraft());
    // Repurposed 2026-07-14 (Ayoub): this toolbar button used to open the
    // contact in HubSpot (window.open of the record URL). It now opens THIS
    // conversation's contact in-app on the Contacts page, focused on that
    // contact — no external jump. The external-link icon stays per Ayoub; only
    // the tooltip/aria label change to read accurately.
    E.addContactBtn.setAttribute('data-tip', 'Ouvrir dans Contacts');
    E.addContactBtn.setAttribute('aria-label', 'Ouvrir dans Contacts');
    E.addContactBtn.addEventListener('click', () => {
      if (!state.selectedChat) return;
      const chat = state.selectedChat;
      const phone = (chat.phone || (chat.id || '').split('@')[0] || '').trim();
      // Search by phone — the Contacts search matches on the last 9 digits, so a
      // HubSpot contact id isn't required. Fall back to the display name only
      // when there's no phone at all.
      const term = phone || (chat.name || '').trim();
      if (!term) { toast('Aucun contact pour cette conversation', 'error'); return; }
      // Hand the term to the Contacts view. showView('contacts') kicks off
      // initContacts(), which applies this term AFTER the default saved view
      // loads: it pins the Paid stage filter (these are paid clients) and clears
      // owner/date/advanced so nothing else hides the row.
      CONTACTS.pendingFocusTerm = term;
      showView('contacts');
    });
    // Admin-only full delete: HubSpot archive (~90d recovery) + lead delete +
    // hide the chat. Guarded by a confirm() and a server-side requireAdmin.
    if (E.deleteContactBtn) E.deleteContactBtn.addEventListener('click', async () => {
      if (!state.selectedChat) return;
      if (state.me?.role !== 'admin') { toast('Admin only', 'error'); return; }
      const chat = state.selectedChat;
      const phone = (chat.phone || (chat.id || '').split('@')[0] || '').trim();
      const name = chat.name || phone || 'this contact';
      const ok = window.confirm(
        `Delete "${name}" permanently?\n\n` +
        `• Archives the contact in HubSpot (recoverable ~90 days)\n` +
        `• Deletes the lead + pipeline history in Leads7\n` +
        `• Hides this conversation\n\n` +
        `This cannot be easily undone.`
      );
      if (!ok) return;
      E.deleteContactBtn.disabled = true;
      try {
        const r = await api('/api/admin/delete-contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone, hubspotId: chat.hubspotContactId || '' }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) { toast('Delete failed: ' + (j.error || ('HTTP ' + r.status)), 'error'); return; }
        toast('Contact deleted' + (j.hubspotArchived ? ' — archived in HubSpot' : ''), '');
        // Hide this conversation from the inbox, refresh the list, go back.
        try { await api('/api/inbox/archive', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ chatId: state.selectedChatId, archived: true }) }); } catch { /* best-effort */ }
        try { await loadConversations({ force: true }); } catch { /* best-effort */ }
        E.chatBackBtn?.click();
      } catch (e) {
        toast('Delete failed: ' + (e.message || e), 'error');
      } finally {
        E.deleteContactBtn.disabled = false;
      }
    });
    // Admin / super_sales only: block this number. POST /api/blocklist → the
    // server hides the conversation and ignores any future message/call from it.
    // Reversible from the "Blocked numbers" view.
    if (E.blockContactBtn) E.blockContactBtn.addEventListener('click', async () => {
      if (!state.selectedChat) return;
      if (!_isSalesAdmin()) { toast('Admin only', 'error'); return; }
      const chat = state.selectedChat;
      const phone = (chat.phone || (chat.id || '').split('@')[0] || '').trim();
      const ok = window.confirm(
        'Bloquer ce numéro ?\n\n' +
        (chat.name || phone) + '\n\n' +
        'La conversation disparaîtra et tout message/appel futur de ce numéro sera ignoré. Réversible depuis « Blocked numbers ».'
      );
      if (!ok) return;
      E.blockContactBtn.disabled = true;
      try {
        const r = await api('/api/blocklist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone, reason: 'spam' }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) { toast('Block failed: ' + (j.error || ('HTTP ' + r.status)), 'error'); return; }
        toast('Numéro bloqué', '');
        await loadConversations({ force: true });
        E.chatBackBtn?.click();
      } catch (e) {
        toast('Block failed: ' + (e.message || e), 'error');
      } finally {
        E.blockContactBtn.disabled = false;
      }
    });
    E.teamChatBtn.addEventListener('click', () => toast('Team chat — wire to your tool of choice', ''));
    E.emojiBtn.addEventListener('click', () => toast('Emoji picker — coming later', ''));
    // Attach button → popover menu (Document / Photos & Vidéos / Audio /
    // Réponses rapides). Replaces the old direct file-picker click.
    E.attachBtn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      toggleAttachMenu();
    });
    E.attachInput.addEventListener('change', sendAttachment);
    // Drag a file onto a conversation — or paste one — and it sends itself.
    initDropToSend();
    // Each menu item routes to either the file picker (with a tailored
    // accept filter) or directly opens the templates modal.
    document.querySelectorAll('#attachMenu .attach-menu-item').forEach(btn => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const kind = btn.getAttribute('data-attach');
        closeAttachMenu();
        if (kind === 'document') {
          E.attachInput.accept = 'application/pdf,application/zip,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,application/json,application/xml';
          // Allow multi-doc selection. PDFs loop through /api/documents/send
          // (per-file POST with anti-ban pacing); other doc types go through
          // the staging preview strip same as media.
          E.attachInput.multiple = true;
          E.attachInput.click();
        } else if (kind === 'media') {
          E.attachInput.accept = 'image/*,video/*';
          E.attachInput.multiple = true;
          E.attachInput.click();
        } else if (kind === 'audio') {
          E.attachInput.accept = 'audio/*';
          E.attachInput.multiple = true;
          E.attachInput.click();
        } else if (kind === 'templates') {
          openTemplatesModal();
        }
      });
    });
    // Close attach menu on outside click. Listening on document is fine
    // because toggleAttachMenu re-binds on each open.
    document.addEventListener('click', (ev) => {
      const menu = document.getElementById('attachMenu');
      if (!menu || menu.classList.contains('hidden')) return;
      if (ev.target.closest('#attachMenu') || ev.target.closest('#attachBtn')) return;
      closeAttachMenu();
    });

    initPayLinks();
    try { initProposeGroups(); } catch (err) { console.warn('[propose-groups] init failed:', err); }

    // Multi-attach preview controls.
    document.getElementById('attachPreviewMicBtn')?.addEventListener('click', () => {
      // Tag the recorder so finishRecording stages the blob into the
      // batch instead of auto-sending.
      state.recordingMode = 'attach';
      // Temporarily restore .compose's normal layout so #recordOverlay
      // (position:absolute; inset:0 of .compose) has a non-zero box to
      // fill. .batching is reapplied when recording finishes/cancels.
      document.querySelector('.compose')?.classList.remove('batching');
      startRecording();
    });
    document.getElementById('attachPreviewVoiceDiscard')?.addEventListener('click', () => {
      state.pendingVoiceBlob = null;
      state.pendingVoiceDurationMs = 0;
      renderAttachPreviewVoicePill();
    });
    document.getElementById('attachPreviewCancelBtn')?.addEventListener('click', cancelAttachBatch);
    document.getElementById('attachPreviewSendBtn')?.addEventListener('click', sendAttachBatch);
    // Pressing Enter inside the caption sends the batch (matches the
    // regular composer's Enter-to-send muscle memory).
    document.getElementById('attachPreviewCaption')?.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); sendAttachBatch(); }
      else if (ev.key === 'Escape') { ev.preventDefault(); cancelAttachBatch(); }
    });

    // ---- Image lightbox ----
    document.getElementById('imageLightboxClose')?.addEventListener('click', closeImageLightbox);
    document.querySelector('#imageLightbox .image-lightbox-backdrop')?.addEventListener('click', closeImageLightbox);
    // Don't close when clicking the image itself (only the backdrop).
    document.getElementById('imageLightboxImg')?.addEventListener('click', (ev) => ev.stopPropagation());
    // Esc closes — only when the lightbox is the topmost overlay.
    document.addEventListener('keydown', (ev) => {
      if (ev.key !== 'Escape') return;
      const lb = document.getElementById('imageLightbox');
      if (lb && !lb.classList.contains('hidden')) {
        ev.stopPropagation();
        closeImageLightbox();
      }
    });

    // ---- Image editor toolbar ----
    document.querySelectorAll('#imageEditorModal .image-editor-tool').forEach(btn => {
      btn.addEventListener('click', () => {
        const tool = btn.getAttribute('data-tool');
        if (!tool) return;
        _imgEditor.tool = tool;
        document.querySelectorAll('#imageEditorModal .image-editor-tool').forEach(b => {
          b.classList.toggle('active', b === btn);
        });
      });
    });
    document.querySelectorAll('#imageEditorModal .image-editor-color').forEach(btn => {
      btn.addEventListener('click', () => {
        const color = btn.getAttribute('data-color');
        if (!color) return;
        _imgEditor.color = color;
        document.querySelectorAll('#imageEditorModal .image-editor-color').forEach(b => {
          b.classList.toggle('active', b === btn);
        });
      });
    });
    document.getElementById('imageEditorUndoBtn')?.addEventListener('click', imageEditorUndo);
    document.getElementById('imageEditorResetBtn')?.addEventListener('click', imageEditorReset);
    document.getElementById('imageEditorCancelBtn')?.addEventListener('click', closeImageEditor);
    document.getElementById('imageEditorDoneBtn')?.addEventListener('click', imageEditorDone);

    // Canvas mouse + touch events. Pointer events would be cleaner but
    // some Safari versions still mis-fire the pointer-cancel handler;
    // mouse + touch is widest-compat.
    const editorCanvas = document.getElementById('imageEditorCanvas');
    if (editorCanvas) {
      editorCanvas.addEventListener('mousedown', imageEditorStart);
      editorCanvas.addEventListener('mousemove', imageEditorMove);
      editorCanvas.addEventListener('mouseup', imageEditorEnd);
      editorCanvas.addEventListener('mouseleave', imageEditorEnd);
      editorCanvas.addEventListener('touchstart', (e) => {
        if (e.touches[0]) imageEditorStart({ preventDefault: () => e.preventDefault(), clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
      }, { passive: false });
      editorCanvas.addEventListener('touchmove', (e) => {
        if (e.touches[0]) imageEditorMove({ preventDefault: () => e.preventDefault(), clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
      }, { passive: false });
      editorCanvas.addEventListener('touchend', (e) => imageEditorEnd({ preventDefault: () => e.preventDefault() }));
      editorCanvas.addEventListener('touchcancel', (e) => imageEditorEnd({ preventDefault: () => e.preventDefault() }));
    }

    // Profile panel
    document.getElementById('profileCloseBtn').addEventListener('click', closeProfilePanel);
    document.getElementById('aiSummaryRefreshBtn').addEventListener('click', () => generateAiSummary(true));
    document.getElementById('aiSummaryCloseBtn')?.addEventListener('click', () => {
      document.getElementById('aiSummaryCard')?.classList.add('hidden');
    });
    document.getElementById('aiThumbUpBtn')?.addEventListener('click', () => submitAiFeedback('up'));
    document.getElementById('aiThumbDownBtn')?.addEventListener('click', () => submitAiFeedback('down'));
    document.getElementById('aiCopyBtn')?.addEventListener('click', copyAiSummary);
    document.getElementById('aiAskBtn')?.addEventListener('click', openAskAiModal);
    document.getElementById('askAiSubmitBtn')?.addEventListener('click', submitAskAi);
    document.getElementById('askAiInput')?.addEventListener('keydown', e => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submitAskAi(); }
    });
    document.getElementById('addPropSaveBtn')?.addEventListener('click', saveAddProp);
    document.getElementById('addPropSearch')?.addEventListener('input', onAddPropSearchInput);
    document.getElementById('createTicketSubmit')?.addEventListener('click', submitCreateTicket);
    document.getElementById('igpConfirmBtn')?.addEventListener('click', _igpConfirm);
    document.getElementById('igpSearch')?.addEventListener('input', e => {
      clearTimeout(state.inboxGroupPicker.searchTimer);
      state.inboxGroupPicker.q = e.target.value.trim();
      state.inboxGroupPicker.searchTimer = setTimeout(_igpLoad, 250);
    });
    for (const [id, key] of [['igpDays', 'days'], ['igpTime', 'time'], ['igpLevel', 'level'], ['igpClassType', 'classType']]) {
      document.getElementById(id)?.addEventListener('change', e => {
        state.inboxGroupPicker[key] = e.target.value;
        _igpLoad();
      });
    }
    document.getElementById('igpRecommendedTab')?.addEventListener('click', () => {
      state.inboxGroupPicker.mode = 'recommended';
      _igpSyncTabs();
      _igpLoad();
    });
    document.getElementById('igpAllTab')?.addEventListener('click', () => {
      state.inboxGroupPicker.mode = 'all';
      _igpSyncTabs();
      _igpLoad();
    });
    document.getElementById('addPropOnlyFilled')?.addEventListener('change', () => renderAddPropChipList());
    document.getElementById('addPropKey')?.addEventListener('keydown', e => {
      if (e.key === 'Enter') { e.preventDefault(); document.getElementById('addPropValue')?.focus(); }
    });
    document.getElementById('addPropValue')?.addEventListener('keydown', e => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveAddProp(); }
    });

    // Note search
    document.getElementById('noteSearchBtn')?.addEventListener('click', openNoteSearchModal);
    document.getElementById('noteSearchInput')?.addEventListener('input', onNoteSearchInput);

    // Modal close handlers — accept BOTH attribute conventions because some
    // modals (reminderModal, sheetConfigModal) shipped with `data-close`
    // while the rest use `data-modal-close`. Wiring only one silently broke
    // the Cancel and × buttons on those screens.
    document.querySelectorAll('[data-modal-close]').forEach(btn => {
      btn.addEventListener('click', () => closeModal(btn.getAttribute('data-modal-close')));
    });
    document.querySelectorAll('[data-close]').forEach(btn => {
      btn.addEventListener('click', () => closeModal(btn.getAttribute('data-close')));
    });
    document.querySelectorAll('.modal .modal-backdrop').forEach(bd => {
      bd.addEventListener('click', () => bd.parentElement.classList.add('hidden'));
    });

    // Templates modal
    document.getElementById('tplSaveBtn').addEventListener('click', saveTemplate);
    document.getElementById('tplResetBtn').addEventListener('click', resetTemplateForm);
    document.getElementById('tplSearch')?.addEventListener('input', onTemplatesSearchInput);
    document.querySelectorAll('#tplStageTabs .tpl-stage-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const nextStage = btn.dataset.tplstage || 'all';
        if (nextStage === 'archived'
          && (state.templatePickerTarget !== 'manage' || !_isSalesAdmin())) return;
        state.templateActiveStage = nextStage;
        document.querySelectorAll('#tplStageTabs .tpl-stage-btn').forEach(b => {
          b.classList.toggle('active', b === btn);
        });
        await loadTemplates();
        // Pre-select the new stage in the create form so the next saved
        // template lands in the matching pipeline stage.
        const stageSel = document.getElementById('tplStage');
        if (stageSel && !['all', 'unsorted', 'archived'].includes(state.templateActiveStage)) {
          stageSel.value = state.templateActiveStage;
        }
      });
    });

    // Notes modal
    // Legacy noteAddBtn binding removed — notes now flow through #convNoteAddBtn (Conv Details panel).

    // Documents modal
    // Documents v2 — picker → form → preview/send
    document.getElementById('docBackBtn')?.addEventListener('click', () => {
      if (state.docState?.step === 'preview') showDocStep('form');
      else showDocStep('picker');
    });
    document.getElementById('docFormPreviewBtn')?.addEventListener('click', previewDoc);
    document.getElementById('docPreviewBackBtn')?.addEventListener('click', () => showDocStep('form'));
    document.getElementById('docPreviewDownloadBtn')?.addEventListener('click', downloadDocFromPreview);
    document.getElementById('docPreviewSendBtn')?.addEventListener('click', () => sendDoc(true));

    // Polls are a safety-net for when Socket.IO is unreachable (rare, but
    // happened in the past — see commit 855e104). When the WS is connected
    // we get server-pushed events for inbox + message updates, so we can
    // skip every other tick: effective 30s/16s. When the WS is offline the
    // polls run at full speed (15s/8s) — they're the only update channel.
    // The skip-every-other pattern means a reconnect picks up updates
    // within at most one full interval, no extra timer plumbing needed.
    let _convPollTick = 0;
    let _msgPollTick = 0;
    state.refreshTimer = setInterval(() => {
      if (document.hidden || state.clientState !== 'READY') return;
      _convPollTick += 1;
      if (state.socket?.connected && _convPollTick % 2 !== 0) return;
      loadConversations();
    }, 15000);
    state.msgPollTimer = setInterval(() => {
      if (!_inboxVisible() || !state.selectedChatId) return;
      _msgPollTick += 1;
      if (state.socket?.connected && _msgPollTick % 2 !== 0) return;
      loadMessages();
    }, 8000);

    // Lazy-load older messages on scroll-up. Threshold of 100px from the
    // top so it kicks in slightly before the user hits the very top —
    // smoother UX than waiting for scrollTop === 0. Throttled by the
    // messagesLoadingOlder flag so a fast scroll only fires one fetch.
    E.messagesArea?.addEventListener('scroll', () => {
      if (E.messagesArea.scrollTop < 100 && _threadHasMore() && !state.messagesLoadingOlder) {
        loadOlderMessages();
      }
    }, { passive: true });
  }

  // ============ Profile panel ============
  let profileOpenChatId = null;

  function toggleProfilePanel() {
    if (!state.selectedChatId) return;
    const isOpen = E.main().classList.contains('with-profile');
    if (isOpen && profileOpenChatId === state.selectedChatId) {
      closeProfilePanel();
    } else {
      openProfilePanel();
    }
  }
  function openProfilePanel() {
    if (!state.selectedChat) return;
    profileOpenChatId = state.selectedChatId;
    E.main().classList.add('with-profile');
    // Sync the notes button state — profile panel forces the conv-details panel closed,
    // so the notes button must visually reflect that it's no longer "active".
    if (E.main().classList.contains('with-conv-details')) {
      E.main().classList.remove('with-conv-details');
      E.notesBtn?.classList.remove('notes-active');
    }
    E.profileBtn.classList.add('profile-active');
    E.profileBtn.setAttribute('aria-pressed', 'true');
    // The Lougha AI popup lost its header trigger (2026-08-22) but the card is
    // still in the DOM — keep hiding it on every chat switch so a leftover open
    // state can never carry over to the next contact.
    const aiCard = document.getElementById('aiSummaryCard');
    if (aiCard) aiCard.classList.add('hidden');
    // Re-enable only after this chat's exact HubSpot profile resolves. Never
    // carry a previous student's preview action across conversation switches.
    document.getElementById('profileCheckAppBtn')?.classList.add('hidden');
    window.LoughaTeacherNotes?.prepare(null, '');
    loadProfileData();
    if (!state.selectedChat.isGroup) {
      loadAiSummaryCache();
      loadAiFeedback();
    }
  }
  // Inline edit for the contact's displayed name. Swaps the <h2>
  // for an <input> + Save/Cancel; saving PATCHes /api/inbox/chats/:id/name
  // with the new value (empty string clears the alias). Server broadcasts
  // chat:nameUpdated so every open tab + every teammate sees the change.
  function startProfileNameEdit() {
    if (!state.selectedChat || state.selectedChat.isGroup) return;
    const row = document.querySelector('.profile-name-row');
    const h2 = document.getElementById('profileName');
    const editBtn = document.getElementById('profileNameEditBtn');
    if (!row || !h2 || row.querySelector('.profile-name-edit-form')) return;
    const currentName = state.selectedChat.name || '';
    const form = el('div', { class: 'profile-name-edit-form' });
    const input = el('input', { type: 'text', class: 'profile-name-edit-input', maxlength: 80, placeholder: 'Contact name' });
    input.value = currentName;
    const saveBtn = el('button', { class: 'btn btn-primary btn-sm', type: 'button', text: 'Save' });
    const cancelBtn = el('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: 'Cancel' });
    form.appendChild(input);
    form.appendChild(saveBtn);
    form.appendChild(cancelBtn);
    h2.style.display = 'none';
    if (editBtn) editBtn.style.display = 'none';
    row.appendChild(form);
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
    function close() {
      form.remove();
      h2.style.display = '';
      if (editBtn) editBtn.style.display = '';
    }
    cancelBtn.addEventListener('click', close);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'Enter') { e.preventDefault(); saveBtn.click(); }
      e.stopPropagation();
    });
    saveBtn.addEventListener('click', async () => {
      const newName = input.value.trim();
      if (newName === currentName) { close(); return; }
      saveBtn.disabled = true; cancelBtn.disabled = true;
      try {
        const r = await fetchT('/api/inbox/chats/' + encodeURIComponent(state.selectedChat.id) + '/name', {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newName }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) {
          toast(j.error || 'Could not update name', 'error');
          saveBtn.disabled = false; cancelBtn.disabled = false;
          return;
        }
        // Optimistic local update. The socket handler will run too, but
        // updating here keeps the UI snappy on slow connections.
        applyChatNameUpdate(state.selectedChat.id, j.name);
        close();
      } catch {
        toast('Could not update name', 'error');
        saveBtn.disabled = false; cancelBtn.disabled = false;
      }
    });
  }

  // Patch chat name across all places it surfaces: the active chat
  // header, the sidebar row, the contact profile pane, and the chat
  // object stored in state. Called by both the optimistic save path
  // and the socket listener so a same-user rename on another tab
  // stays in sync.
  function applyChatNameUpdate(chatId, name) {
    if (!chatId) return;
    // Empty name → fall back to whatever the next /api/inbox/chats
    // refresh hands us; for the immediate UI, drop back to phone.
    const chat = (state.chats || []).find(c => c.id === chatId);
    if (chat) chat.name = name || chat.phone || chat.id;
    if (state.selectedChat?.id === chatId) {
      state.selectedChat.name = chat?.name || name || state.selectedChat.phone || '—';
      // Chat header (above the messages area)
      const headerName = document.querySelector('#chatName, .chat-header .chat-header-name, #chatTitle');
      if (headerName) headerName.textContent = state.selectedChat.name;
    }
    // Sidebar row name — find by data-chat-id
    const row = document.querySelector('#conversationList .conv-item[data-chat-id="' + CSS.escape(chatId) + '"] .conv-name');
    if (row) row.textContent = chat?.name || name || '';
  }

  function closeProfilePanel() {
    E.main().classList.remove('with-profile');
    E.profileBtn.classList.remove('profile-active');
    E.profileBtn.setAttribute('aria-pressed', 'false');
    profileOpenChatId = null;
  }

  async function loadProfileData() {
    const sectionsEl = document.getElementById('profileSections');
    clear(sectionsEl);
    sectionsEl.appendChild(el('div', { class: 'empty-row', text: 'Loading profile…' }));
    // Pin chat identity so a mid-load chat switch doesn't end up rendering
    // Alice's contact card while the agent is now talking to Bob. Profile
    // data is high-value PII (phone, email, HubSpot id) — leaking it to
    // the wrong chat is worse than just visual flicker.
    const requestedChatId = state.selectedChatId;
    const requestedPersonKey = state.personChat?.key || '';
    const profileChatId = _profilePersonChatId();
    // Shared number: the person the panel follows (see _activePersonLeadId).
    // A Switch landing mid-load must not let the previous person's data paint.
    const requestedLeadId = _activePersonLeadId(requestedChatId) || '';
    const leadQs = requestedLeadId ? '&leadId=' + encodeURIComponent(requestedLeadId) : '';
    _profileShown = { chatId: String(requestedChatId || ''), leadId: requestedLeadId };
    const stillCurrent = () => state.selectedChatId === requestedChatId
      && (state.personChat?.key || '') === requestedPersonKey
      && (_activePersonLeadId(requestedChatId) || '') === requestedLeadId;
    // Ticket schema + pipelines (for mapping hs_pipeline_stage="4" → "Waiting on
    // us"). First call costs one HTTP roundtrip; subsequent profile opens are
    // instant because the result is cached on `state`.
    await loadTicketLookups();
    if (!stillCurrent()) return;
    try {
      const [contactRes, fullRes] = await Promise.all([
        api('/api/contact?chatId=' + encodeURIComponent(profileChatId) + leadQs),
        api('/api/hubspot/contact-properties-full?chatId=' + encodeURIComponent(profileChatId) + leadQs).catch(() => null),
        loadCustomProps(requestedChatId),
        loadPinnedFields(requestedChatId),
        loadPinnedTicketFields(requestedChatId)
      ]);
      if (!stillCurrent()) return;
      const data = await contactRes.json();
      if (!stillCurrent()) return;
      try {
        const fullJson = fullRes ? await fullRes.json() : null;
        if (fullJson?.properties && data.contact) {
          const merged = { ...(data.contact.properties || {}) };
          for (const p of fullJson.properties) if (p.value) merged[p.name] = p.value;
          data.contact.properties = merged;
          for (const p of fullJson.properties) state.addPropChipsLabels[p.name] = p.label;
        }
      } catch { /* keep base properties */ }

      // For 1-1 contacts: also fetch tickets WITH the pinned ticket fields (so each ticket renders its own data)
      if (!data.isGroup && data.contact?.id) {
        try {
          const pinnedTickets = getPinnedTicketFields(requestedChatId);
          const tr = await api('/api/hubspot/tickets-full?chatId=' + encodeURIComponent(profileChatId) + '&fields=' + encodeURIComponent(pinnedTickets.join(',')) + leadQs);
          const tj = await tr.json();
          if (Array.isArray(tj.tickets)) data.ticketsFull = tj.tickets;
        } catch { /* keep base tickets */ }
      }

      // For groups: also fetch the live WhatsApp participant list. This runs
      // independently of the HubSpot company match so the members section
      // always renders, even when the group has no HubSpot association.
      if (data.isGroup && requestedChatId) {
        try {
          const wm = await api('/api/groups/' + encodeURIComponent(requestedChatId) + '/wa-members');
          const wj = await wm.json();
          if (Array.isArray(wj.members)) data.waMembers = wj.members;
        } catch { /* members list is optional */ }
      }

      // For groups: try to match a HubSpot company by group name and pull its properties
      if (data.isGroup && data.group?.name) {
        try {
          const m = await api('/api/hubspot/match-company?name=' + encodeURIComponent(data.group.name));
          const mj = await m.json();
          if (mj.matched && mj.company?.id) {
            const f = await api('/api/hubspot/company-properties-full?companyId=' + encodeURIComponent(mj.company.id));
            const fj = await f.json();
            data.hubspotCompany = {
              id: mj.company.id,
              name: mj.company.name,
              matchType: mj.matchType,
              alternates: mj.alternates || [],
              properties: {}
            };
            if (Array.isArray(fj.properties)) {
              for (const p of fj.properties) {
                data.hubspotCompany.properties[p.name] = p.value || '';
                state.addPropChipsLabels[p.name] = p.label;
              }
              state.lastCompanyChips = fj.properties.map(p => ({ raw: p.name, key: p.label, value: p.value || '', hasValue: !!p.hasValue }));
            }
            await loadPinnedCompanyFields(mj.company.id);
            // Fetch the student roster for this matched company so we can show
            // who's in the class right alongside the schedule info. Mirrors
            // what the agent sees on a 1-1 chat for an individual student.
            try {
              const sr = await api('/api/hubspot/groups/' + encodeURIComponent(mj.company.id) + '/students');
              const sj = await sr.json();
              if (Array.isArray(sj.students)) data.hubspotCompany.students = sj.students;
            } catch { /* roster is optional */ }
            // Tickets associated with this class (HubSpot company). Same shape as
            // the per-contact tickets, so the existing render code can be reused.
            try {
              const pinnedTickets = getPinnedTicketFields(requestedChatId);
              const tr = await api('/api/hubspot/company-tickets?companyId=' + encodeURIComponent(mj.company.id) + '&fields=' + encodeURIComponent(pinnedTickets.join(',')));
              const tj = await tr.json();
              if (Array.isArray(tj.tickets)) data.ticketsFull = tj.tickets;
            } catch { /* tickets optional */ }
          }
        } catch { /* group has no HubSpot match — skip */ }
      }
      // Final guard before publishing the (potentially long-fetch-chain) result.
      // If the agent switched chats during any of the awaits above, dropping
      // here means the next chat's profile load will populate cleanly.
      if (!stillCurrent()) return;
      state.activeProfile = data;
      // Sync the chat header member count after the profile loads — chats
      // synthesized from Today's Classes start with members=0 and only the
      // /api/contact response knows the real number.
      if (data.isGroup && data.group && state.selectedChatId === data.chatId) {
        const chat = state.chats.find(c => c.id === data.chatId);
        if (chat && typeof data.group.members === 'number' && chat.members !== data.group.members) {
          chat.members = data.group.members;
          if (E.chatPhone) E.chatPhone.textContent = data.group.members + ' members';
        }
      }
      renderProfileSections(data, sectionsEl);
    } catch {
      clear(sectionsEl);
      sectionsEl.appendChild(el('div', { class: 'empty-row', text: 'Failed to load profile' }));
    }
  }

  // ===== Pinned-fields preference for HubSpot companies (group profiles) =====
  const DEFAULT_PINNED_COMPANY_FIELDS = ['name', 'schedule_days', 'schedule_time', 'group_class_type', 'lifecyclestage', 'createdate'];
  state.pinnedCompanyByCompanyId = state.pinnedCompanyByCompanyId || {};

  function getPinnedCompanyFields(companyId) {
    if (!companyId) return [...DEFAULT_PINNED_COMPANY_FIELDS];
    const cached = state.pinnedCompanyByCompanyId[companyId];
    if (Array.isArray(cached) && cached.length) return cached;
    return [...DEFAULT_PINNED_COMPANY_FIELDS];
  }

  async function loadPinnedCompanyFields(companyId) {
    if (!companyId) return [...DEFAULT_PINNED_COMPANY_FIELDS];
    try {
      const r = await api('/api/preferences?key=' + encodeURIComponent('pinned-company-fields:' + companyId));
      const j = await r.json();
      const list = Array.isArray(j?.prefs?.fields) ? j.prefs.fields : null;
      if (list && list.length) {
        state.pinnedCompanyByCompanyId[companyId] = list;
        if (j.prefs.labels) state.addPropChipsLabels = { ...state.addPropChipsLabels, ...j.prefs.labels };
        return list;
      }
    } catch { /* fallback */ }
    state.pinnedCompanyByCompanyId[companyId] = [...DEFAULT_PINNED_COMPANY_FIELDS];
    return state.pinnedCompanyByCompanyId[companyId];
  }

  // ===== Pinned-fields preference for HubSpot tickets (per-chat) =====
  // Mirror the Tickets table columns (status / priority / feedback status /
  // student feedback). The card title already shows the subject (= TICKET
  // NAME), and CONTACT / COMPANIES are implicit from the parent chat, so we
  // only pin the four fields that vary per ticket.
  const DEFAULT_PINNED_TICKET_FIELDS = ['hs_pipeline_stage', 'hs_ticket_priority', 'feedback_status', 'student_feedback'];
  state.pinnedTicketByChat = state.pinnedTicketByChat || {};

  function getPinnedTicketFields(chatId) {
    if (!chatId) return [...DEFAULT_PINNED_TICKET_FIELDS];
    const cached = state.pinnedTicketByChat[chatId];
    if (Array.isArray(cached) && cached.length) return cached;
    return [...DEFAULT_PINNED_TICKET_FIELDS];
  }

  async function loadPinnedTicketFields(chatId) {
    if (!chatId) return [...DEFAULT_PINNED_TICKET_FIELDS];
    try {
      const r = await api('/api/preferences?key=' + encodeURIComponent('pinned-ticket-fields:' + chatId));
      const j = await r.json();
      const list = Array.isArray(j?.prefs?.fields) ? j.prefs.fields : null;
      if (list && list.length) {
        state.pinnedTicketByChat[chatId] = list;
        if (j.prefs.labels) state.addPropChipsLabels = { ...state.addPropChipsLabels, ...j.prefs.labels };
        return list;
      }
    } catch { /* fallback */ }
    state.pinnedTicketByChat[chatId] = [...DEFAULT_PINNED_TICKET_FIELDS];
    return state.pinnedTicketByChat[chatId];
  }

  async function savePinnedTicketFields(chatId, fields, labels) {
    if (!chatId) return;
    state.pinnedTicketByChat[chatId] = fields;
    if (labels) state.addPropChipsLabels = { ...state.addPropChipsLabels, ...labels };
    try {
      await api('/api/preferences', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: 'pinned-ticket-fields:' + chatId, prefs: { fields, labels: state.addPropChipsLabels } })
      });
    } catch { /* best effort */ }
  }

  // ============ Create Ticket modal — full HubSpot-style form ============
  state.createTicket = { schema: [], pipelines: [], values: {}, showAdvanced: false };

  // Lazy-load + cache ticket schema and pipelines so we can map raw enumeration
  // values (like hs_pipeline_stage="4") to their display labels ("Waiting on us")
  // when rendering the Tickets accordion. Without this, every status field shows
  // a meaningless internal stage ID.
  state.ticketSchema = state.ticketSchema || null;
  state.ticketPipelines = state.ticketPipelines || null;
  let _ticketLookupPromise = null;
  async function loadTicketLookups() {
    if (state.ticketSchema && state.ticketPipelines) return;
    if (_ticketLookupPromise) return _ticketLookupPromise;
    _ticketLookupPromise = (async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          api('/api/hubspot/ticket-pipelines'),
          api('/api/hubspot/ticket-properties-schema?includeReadOnly=1')
        ]);
        const pj = await pRes.json();
        const sj = await sRes.json();
        state.ticketSchema = Array.isArray(sj.properties) ? sj.properties : [];
        state.ticketPipelines = Array.isArray(pj.pipelines) ? pj.pipelines : [];
      } catch { /* schema is optional; raw IDs are an acceptable fallback */ }
      finally { _ticketLookupPromise = null; }
    })();
    return _ticketLookupPromise;
  }

  // Map a raw HubSpot ticket-property value to its display label using the cached
  // schema. Falls through to the raw value when no mapping is known.
  function ticketDisplayValue(fieldName, rawValue) {
    if (rawValue == null || rawValue === '') return '';
    const v = String(rawValue);
    if (fieldName === 'hs_pipeline_stage') {
      for (const pipe of (state.ticketPipelines || [])) {
        const stage = (pipe.stages || []).find(s => String(s.id) === v);
        if (stage) return stage.label || v;
      }
    }
    if (fieldName === 'hs_pipeline') {
      const pipe = (state.ticketPipelines || []).find(p => String(p.id) === v);
      if (pipe) return pipe.label || v;
    }
    const prop = (state.ticketSchema || []).find(p => p.name === fieldName);
    if (prop && Array.isArray(prop.options) && prop.options.length) {
      const opt = prop.options.find(o => String(o.value) === v);
      if (opt) return opt.label || v;
    }
    return v;
  }

  async function openCreateTicketModal() {
    if (!state.selectedChatId) { toast('Open a chat first', 'error'); return; }
    state.createTicket = { schema: [], pipelines: [], values: {}, showAdvanced: false };
    const body = document.getElementById('createTicketBody');
    clear(body);
    body.appendChild(el('div', { class: 'bulk-loading', style: 'padding:20px;', text: 'Loading HubSpot pipelines + properties…' }));
    openModal('createTicketModal');
    try {
      const [pRes, sRes] = await Promise.all([
        api('/api/hubspot/ticket-pipelines'),
        api('/api/hubspot/ticket-properties-schema')
      ]);
      const pj = await pRes.json();
      const sj = await sRes.json();
      state.createTicket.pipelines = pj.pipelines || [];
      state.createTicket.schema = sj.properties || [];

      // Defaults: first pipeline + its first stage
      const firstPipe = state.createTicket.pipelines[0];
      if (firstPipe) {
        state.createTicket.values.hs_pipeline = firstPipe.id;
        state.createTicket.values.hs_pipeline_stage = firstPipe.stages?.[0]?.id || '';
      }
      renderCreateTicketForm();
    } catch (err) {
      clear(body);
      body.appendChild(el('div', { class: 'error-text', text: 'Failed to load: ' + (err.message || err) }));
    }
  }

  // The exact ordered subset of HubSpot ticket properties we expose in the
  // Create Ticket modal — mirrors Lougha's HubSpot "Create new" form so
  // agents see the same 8 fields they're used to filling in HubSpot,
  // nothing more, nothing less. Custom properties matched by their human
  // label (case-insensitive) so the form keeps working when HubSpot
  // renames them or the internal property name differs from the label.
  const TICKET_ESSENTIAL_FIELDS = ['subject', 'hs_pipeline', 'hs_pipeline_stage', 'hs_ticket_priority', 'content'];
  // Pipeline is hidden — there's only one pipeline (Support Pipeline) and
  // the Status dropdown reads from values.hs_pipeline which is auto-set
  // to the first pipeline in openCreateTicketModal. The backend payload
  // still includes hs_pipeline because submitCreateTicket reads the
  // values map, not the form fields.
  const TICKET_VISIBLE_FIELDS = [
    { name: 'subject',                 label: 'Ticket name' },
    { name: 'hs_pipeline_stage',       label: 'Ticket status' },
    { name: null,                      label: 'Student Feedback' },
    { name: null,                      label: 'Feedback Status' },
    { name: 'hs_ticket_priority',      label: 'Priority' },
    { name: null,                      label: 'Create Tickets Note' },
    { name: null,                      label: 'Teacher Name' }
  ];

  function findTicketProp(entry) {
    const schema = state.createTicket.schema || [];
    if (entry.name) {
      const byName = schema.find(p => p.name === entry.name);
      if (byName) return byName;
    }
    if (entry.label) {
      const target = entry.label.trim().toLowerCase();
      const byLabel = schema.find(p => (p.label || '').trim().toLowerCase() === target);
      if (byLabel) return byLabel;
    }
    return null;
  }

  function renderCreateTicketForm() {
    const body = document.getElementById('createTicketBody');
    clear(body);
    // Render the curated HubSpot-form subset only — same fields the agent
    // fills in HubSpot's own Create Ticket flow.
    for (const entry of TICKET_VISIBLE_FIELDS) {
      const prop = findTicketProp(entry);
      // hs_pipeline_stage is allowed even without a schema row (it's
      // synthesised from the pipeline's stages list).
      if (!prop && entry.name !== 'hs_pipeline_stage') {
        // Don't disappear silently — if HubSpot renames or removes the
        // property the agent should see WHICH field is unreachable so they
        // can flag it to admin instead of wondering why the form shrunk.
        body.appendChild(el('div', { class: 'ct-field ct-field-missing' }, [
          el('label', { class: 'ct-label', text: entry.label }),
          el('div', { class: 'ct-missing-note', text: '⚠ Not in HubSpot schema — contact admin to restore "' + entry.label + '".' })
        ]));
        continue;
      }
      const propName = prop?.name || entry.name;
      body.appendChild(buildTicketField(propName, prop));
    }
    refreshIcons();
  }

  function buildTicketField(name, prop) {
    const wrap = el('div', { class: 'ct-field' });
    const label = (prop?.label) || (name === 'hs_pipeline' ? 'Pipeline' : name === 'hs_pipeline_stage' ? 'Status' : humanizeKey(name));
    const required = ['subject', 'hs_pipeline', 'hs_pipeline_stage'].includes(name);
    wrap.appendChild(el('label', { class: 'ct-label', text: label + (required ? ' *' : '') }));

    let input;
    if (name === 'hs_pipeline') {
      input = el('select', { class: 'input ct-input', id: 'ct_hs_pipeline' });
      for (const pipe of state.createTicket.pipelines) {
        input.appendChild(el('option', { value: pipe.id, text: pipe.label }));
      }
      input.value = state.createTicket.values.hs_pipeline || '';
      input.addEventListener('change', () => {
        state.createTicket.values.hs_pipeline = input.value;
        const pipe = state.createTicket.pipelines.find(p => p.id === input.value);
        state.createTicket.values.hs_pipeline_stage = pipe?.stages?.[0]?.id || '';
        renderCreateTicketForm();
      });
    } else if (name === 'hs_pipeline_stage') {
      input = el('select', { class: 'input ct-input', id: 'ct_hs_pipeline_stage' });
      const pipe = state.createTicket.pipelines.find(p => p.id === state.createTicket.values.hs_pipeline);
      for (const stage of (pipe?.stages || [])) {
        input.appendChild(el('option', { value: stage.id, text: stage.label }));
      }
      input.value = state.createTicket.values.hs_pipeline_stage || '';
      input.addEventListener('change', () => { state.createTicket.values.hs_pipeline_stage = input.value; });
    } else if (prop && (prop.type === 'enumeration' || prop.fieldType === 'select' || prop.fieldType === 'radio')) {
      input = el('select', { class: 'input ct-input' });
      input.appendChild(el('option', { value: '', text: '— Select —' }));
      for (const opt of (prop.options || []).filter(o => !o.hidden)) {
        input.appendChild(el('option', { value: opt.value, text: opt.label }));
      }
      input.value = state.createTicket.values[name] || '';
      input.addEventListener('change', () => { state.createTicket.values[name] = input.value; });
    } else if (prop && (prop.type === 'date' || prop.type === 'datetime')) {
      input = el('input', { class: 'input ct-input', type: prop.type === 'datetime' ? 'datetime-local' : 'date' });
      input.value = state.createTicket.values[name] || '';
      input.addEventListener('input', () => { state.createTicket.values[name] = input.value; });
    } else if (prop && prop.type === 'bool') {
      input = el('select', { class: 'input ct-input' });
      input.appendChild(el('option', { value: '', text: '— Select —' }));
      input.appendChild(el('option', { value: 'true', text: 'Yes' }));
      input.appendChild(el('option', { value: 'false', text: 'No' }));
      input.value = state.createTicket.values[name] || '';
      input.addEventListener('change', () => { state.createTicket.values[name] = input.value; });
    } else if (prop && prop.type === 'number') {
      input = el('input', { class: 'input ct-input', type: 'number' });
      input.value = state.createTicket.values[name] || '';
      input.addEventListener('input', () => { state.createTicket.values[name] = input.value; });
    } else if (name === 'content') {
      input = el('textarea', { class: 'input ct-input', rows: '3', placeholder: 'Add a note…' });
      input.value = state.createTicket.values[name] || '';
      input.addEventListener('input', () => { state.createTicket.values[name] = input.value; });
    } else {
      input = el('input', { class: 'input ct-input', type: 'text' });
      input.value = state.createTicket.values[name] || '';
      input.addEventListener('input', () => { state.createTicket.values[name] = input.value; });
    }
    wrap.appendChild(input);
    return wrap;
  }

  async function submitCreateTicket() {
    const v = state.createTicket.values || {};
    if (!v.subject || !v.subject.trim()) { toast('Ticket name is required', 'error'); return; }
    if (!v.hs_pipeline || !v.hs_pipeline_stage) { toast('Pipeline + Status are required', 'error'); return; }
    const btn = document.getElementById('createTicketSubmit');
    if (btn) { btn.disabled = true; btn.textContent = 'Creating…'; }
    try {
      const companyId = state.activeProfile?.hubspotCompany?.id || '';
      // Shared number: the ticket goes on the person the profile shows.
      const personLeadId = state.activeProfile?.person?.leadId || '';
      const r = await api('/api/hubspot/tickets/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: state.selectedChatId, properties: v, companyId, ...(personLeadId ? { leadId: personLeadId } : {}) })
      });
      const j = await r.json();
      if (!r.ok || j.error) { toast(j.error || 'Create failed', 'error'); return; }
      toast('Ticket created', 'success');
      closeModal('createTicketModal');
      loadProfileData();
    } catch (err) {
      toast('Create failed: ' + (err.message || err), 'error');
    } finally {
      if (btn) {
        btn.disabled = false;
        clear(btn);
        btn.appendChild(lucideIcon('plus-circle'));
        btn.appendChild(document.createTextNode(' Create'));
        refreshIcons();
      }
    }
  }

  function addTicketPinnedFieldRow(parent, label, value, fieldName) {
    const v = (value == null || value === '') ? '' : String(value);
    const row = el('div', { class: 'field-row pinned-field-row', 'data-field-name': fieldName, draggable: 'true' });
    row.appendChild(el('span', { class: 'pinned-field-drag', title: 'Drag to reorder' }, [lucideIcon('grip-vertical')]));
    row.appendChild(el('span', { class: 'field-key', text: label }));
    row.appendChild(el('span', { class: 'field-val ' + (v ? '' : 'empty'), text: v || '—' }));
    const removeBtn = el('button', { class: 'pinned-field-remove', title: 'Retirer de Tickets', type: 'button' });
    removeBtn.appendChild(lucideIcon('x'));
    removeBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const current = getPinnedTicketFields(state.selectedChatId).filter(f => f !== fieldName);
      await savePinnedTicketFields(state.selectedChatId, current);
      loadProfileData();
    });
    row.appendChild(removeBtn);
    parent.appendChild(row);
    refreshIcons();
  }

  async function savePinnedCompanyFields(companyId, fields, labels) {
    if (!companyId) return;
    state.pinnedCompanyByCompanyId[companyId] = fields;
    if (labels) state.addPropChipsLabels = { ...state.addPropChipsLabels, ...labels };
    try {
      await api('/api/preferences', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: 'pinned-company-fields:' + companyId, prefs: { fields, labels: state.addPropChipsLabels } })
      });
    } catch { /* best effort */ }
  }

  // ============ Inbox → Add current student to a group ============
  // Keeps group enrolment anchored to the exact chat/contact pair currently
  // visible. The server repeats the same identity check before writing.
  state.inboxGroupPicker = {
    mode: 'recommended',
    q: '',
    days: '',
    time: '',
    level: '',
    classType: '',
    groups: [],
    selectedId: '',
    chatId: '',
    contactId: '',
    requestId: 0,
    searchTimer: null,
    // Booking scope — 'always' links the class in HubSpot (the original
    // "Add to a group"), '1'..'4' books that many real upcoming sessions as
    // a trial through support2's find-group engine. `intent` only decides
    // which one is pre-selected; both are reachable from either entry point.
    intent: 'add',
    scope: 'always',
    preview: null,
    previewError: '',
    previewLoading: false,
    previewToken: 0,
    clientRequestId: '',
  };

  // support2 caps a trial at 4 classes (FG_MAX_TRIAL_CLASSES); asking for
  // more is refused server-side, so never offer it.
  const IGP_TRIAL_MAX_CLASSES = 4;
  // Mirrors support2's Groups-panel copy so the two inboxes read the same.
  const IGP_PREVIEW_REASONS = {
    group_not_bookable: 'This class is closed in HubSpot — no trial. "Always" still links it to the student.',
    stale_level: 'This class has no level set in HubSpot.',
    no_upcoming_occurrences: "No upcoming class in this group's schedule — a trial needs a real date.",
    group_not_found: 'This class no longer exists in HubSpot.',
    support2_bridge_not_configured: 'The support2 bridge is not configured — trials are unavailable.',
    support2_unreachable: 'support2 did not answer — trials are unavailable right now.',
    support2_timeout: 'support2 took too long to answer. Try again in a moment.',
    sales_only: 'Only the sales team can book a trial class.',
  };

  function _igpProfileLevel(props) {
    const raw = [props?.new_level, props?.level, props?.test_level_web, props?.student_level]
      .filter(Boolean).join(' ').toUpperCase();
    return raw.match(/\b(?:A0|A1|A2|B1|B2|C1|C2)\b/)?.[0] || '';
  }

  function _igpProfileClassType(props) {
    const rawType = String(props?.group_class_type || props?.class_type || '').toLowerCase();
    if (/individual|individuel|private|one[ -]?to[ -]?one/.test(rawType)) return 'individual';
    if (/teen|adolescent|\bados?\b/.test(rawType)) return 'teenagers';
    if (/kid|child|enfant|junior/.test(rawType)) return 'kids';
    if (/adult|adulte/.test(rawType)) return 'adults';
    const age = Number.parseInt(props?.age, 10);
    if (Number.isFinite(age)) return age < 18 ? 'kids' : 'adults';
    return '';
  }

  // Booking a trial hits _fgProxy, which is gated on the sales allowlist.
  // Hide the option for anyone it would 403 rather than let them click into
  // an error.
  function _igpCanBookTrial() {
    const role = String(state.me?.role || '').toLowerCase();
    return ['admin', 'super_admin', 'super_sales', 'agent'].includes(role);
  }

  function _igpNewRequestId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'trial-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  // The number the trial message is actually sent to. The chat's own JID is
  // the authoritative, never-masked source; a profile phone is only trusted
  // when it carries no masking star (see maskPhone) — sending a de-masked
  // "+2126****99" would text a stranger.
  function _igpStudentPhone() {
    const chatId = String(state.inboxGroupPicker.chatId || '');
    if (chatId.endsWith('@s.whatsapp.net')) {
      const digits = chatId.split('@')[0].split(':')[0];
      if (/^\d{8,15}$/.test(digits)) return digits;
    }
    const raw = String(state.activeProfile?.contact?.phone || state.selectedChat?.phone || '');
    if (!raw || raw.includes('*')) return '';
    const digits = raw.replace(/\D/g, '');
    return /^\d{8,15}$/.test(digits) ? digits : '';
  }

  function _igpStatusTone(status) {
    const s = String(status || '').toLowerCase();
    if (s === 'in process') return 'igp-status-open';
    if (s === 'active') return 'igp-status-active';
    if (s === 'expandable') return 'igp-status-open';
    return 'igp-status-other';
  }

  // Months until the class ends, phrased like the Book class card. The mirror
  // only has the end date, so the months are derived here rather than trusted
  // from a field that does not exist.
  function _igpTimeLeft(endDate) {
    const ms = Date.parse(String(endDate || '').slice(0, 10));
    if (!ms || Number.isNaN(ms)) return '—';
    const months = Math.round((ms - Date.now()) / (1000 * 60 * 60 * 24 * 30.44));
    const head = months <= 0 ? 'ending now' : '~' + months + ' mo';
    return head + ' · ' + _fgFmtDate(endDate);
  }

  function _igpTrialDateLabel(occurrence) {
    if (!occurrence) return '';
    let date = occurrence.classDate || '';
    try {
      date = new Date(date + 'T12:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
    } catch { /* keep the ISO date */ }
    const from = occurrence.startTime || '';
    const to = occurrence.endTime || '';
    return date + (from ? ' · ' + from + (to ? '–' + to : '') : '');
  }

  // Upcoming sessions this group can actually be booked on, capped at what
  // support2 accepts.
  function _igpOccurrences() {
    return _igpBookableOccurrences().slice(0, IGP_TRIAL_MAX_CLASSES);
  }

  // The week as the teachers7 grid holds it ("Mon & Wed 22:00–23:30 · Tue &
  // Thu 20:30–22:00"), HubSpot's single days + time only when the grid has
  // nothing. C4LG7 read "tuesday-thursday" while it meets 4 days (2026-09-28).
  function _igpScheduleText(group) {
    if (!group) return '';
    if (group.schedule_label) {
      const n = Number(group.days_per_week) || 0;
      return (n ? n + (n === 1 ? ' day' : ' days') + '/week · ' : '') + group.schedule_label;
    }
    return [group.schedule_days, group.schedule_time].filter(Boolean).join(' · ');
  }

  // Every upcoming class the agent may tick for a custom trial (~3 weeks).
  function _igpPickableOccurrences() {
    return _igpBookableOccurrences().slice(0, 12);
  }

  // support2's upcoming sessions, minus any before the class's own start date.
  function _igpBookableOccurrences() {
    const picker = state.inboxGroupPicker;
    const preview = picker.preview;
    if (!preview || preview.trialAccessEnabled === false) return [];
    const group = (picker.groups || []).find(g => String(g.id) === String(picker.selectedId));
    return _fgFromGroupStart(preview.occurrences, group && group.start_date);
  }

  function _igpPickedOccurrences() {
    const picked = new Set(state.inboxGroupPicker.picked || []);
    return _igpPickableOccurrences().filter(o => picked.has(o.classDate));
  }

  // The trial is "how many classes" + "which days". The team books a student
  // on the day THEY can come — tomorrow, or the day after — not only on the
  // next class (Ayoub, 2026-09-28: "ykon ikhtiyar 3la day imta bghin y3tiha lih").
  function _igpTrialCount() {
    const picker = state.inboxGroupPicker;
    const limit = Math.min(IGP_TRIAL_MAX_CLASSES, _igpPickableOccurrences().length) || 1;
    return Math.min(Math.max(1, Number(picker.trialCount) || 1), limit);
  }

  function _igpEnsurePicked() {
    const picker = state.inboxGroupPicker;
    const pickable = _igpPickableOccurrences();
    const known = new Set(pickable.map(o => o.classDate));
    // NEVER preselect — same trap as the Book class dropdown (#431). Starting on
    // the next class sent "Tomorrow" for a student who had agreed on next week
    // (Safae SEBAI / LC187, 2026-10-15). The agent taps the day; until then the
    // button reads "Pick 1 more day" and stays disabled.
    picker.picked = (picker.picked || []).filter(d => known.has(d));
  }

  // More classes = keep the chosen days and add the ones right after the
  // first chosen day; fewer = drop from the end.
  function _igpSetTrialCount(n) {
    const picker = state.inboxGroupPicker;
    const pickable = _igpPickableOccurrences().map(o => o.classDate);
    picker.trialCount = n;
    const count = _igpTrialCount();
    let picked = pickable.filter(d => (picker.picked || []).includes(d));
    if (picked.length > count) picked = picked.slice(0, count);
    // Extends from the day the agent chose — never invents a first day.
    const start = picked.length ? pickable.indexOf(picked[0]) : pickable.length;
    for (let i = start; i < pickable.length && picked.length < count; i += 1) {
      if (!picked.includes(pickable[i])) picked.push(pickable[i]);
    }
    picker.picked = pickable.filter(d => picked.includes(d));
    _igpRefreshScopePanel();
    _igpRenderSelection();
  }

  // One class: a tap MOVES it to that day. Several: a tap adds / removes a day.
  function _igpTogglePick(classDate) {
    const picker = state.inboxGroupPicker;
    const count = _igpTrialCount();
    const pickable = _igpPickableOccurrences().map(o => o.classDate);
    if (count === 1) {
      picker.picked = [classDate];
    } else {
      const set = new Set(picker.picked || []);
      if (set.has(classDate)) set.delete(classDate);
      else if (set.size < count) set.add(classDate);
      picker.picked = pickable.filter(d => set.has(d));
    }
    _igpRefreshScopePanel();
    _igpRenderSelection();
  }

  function _igpBuildTrialPicker() {
    const picker = state.inboxGroupPicker;
    const pickable = _igpPickableOccurrences();
    const count = _igpTrialCount();
    const picked = new Set(picker.picked || []);
    const wrap = el('div', { class: 'igp-trial-picker' });

    const countRow = el('div', { class: 'igp-trial-row' }, [el('span', { class: 'igp-trial-label', text: 'How many classes?' })]);
    const seg = el('div', { class: 'igp-seg', role: 'group', 'aria-label': 'Number of trial classes' });
    for (let n = 1; n <= Math.min(IGP_TRIAL_MAX_CLASSES, pickable.length); n += 1) {
      const btn = el('button', { type: 'button', class: 'igp-seg-btn' + (n === count ? ' active' : ''), text: String(n), 'aria-pressed': n === count ? 'true' : 'false' });
      btn.addEventListener('click', () => _igpSetTrialCount(n));
      seg.appendChild(btn);
    }
    countRow.appendChild(seg);
    wrap.appendChild(countRow);

    wrap.appendChild(el('span', {
      class: 'igp-trial-label',
      text: count === 1 ? 'Which day? — tap the day the student can come' : 'Which days? — ' + picked.size + ' of ' + count + ' chosen',
    }));
    const today = MA.dayKey();
    const tomorrow = MA.dayKey(Date.now() + 86400000);
    const list = el('div', { class: 'igp-pick-list', role: 'group', 'aria-label': 'Trial class days' });
    for (const o of pickable) {
      const on = picked.has(o.classDate);
      const full = count > 1 && !on && picked.size >= count;
      const chip = el('button', {
        type: 'button',
        class: 'igp-pick-chip' + (on ? ' selected' : '') + (full ? ' disabled' : ''),
        'aria-pressed': on ? 'true' : 'false',
        'data-class-date': o.classDate,
      });
      chip.disabled = full;
      const tag = o.classDate === today ? 'Today' : (o.classDate === tomorrow ? 'Tomorrow' : '');
      if (tag) chip.appendChild(el('span', { class: 'igp-pick-tag', text: tag }));
      chip.appendChild(el('span', { text: _igpTrialDateLabel(o) }));
      chip.addEventListener('click', () => _igpTogglePick(o.classDate));
      list.appendChild(chip);
    }
    wrap.appendChild(list);
    return wrap;
  }

  // Ask support2 what this group's next real sessions are. Runs on every
  // selection change; a stale answer is dropped via previewToken so a fast
  // click-through never shows the previous group's dates.
  async function _igpLoadPreview() {
    const picker = state.inboxGroupPicker;
    const group = picker.groups.find(g => String(g.id) === String(picker.selectedId));
    const token = ++picker.previewToken;
    picker.preview = null;
    picker.previewError = '';
    picker.picked = [];
    if (!group || !_igpCanBookTrial()) {
      picker.previewLoading = false;
      picker.scope = 'always';
      _igpRefreshScopePanel();
      return;
    }
    picker.previewLoading = true;
    picker.clientRequestId = _igpNewRequestId();
    _igpRefreshScopePanel();
    const params = new URLSearchParams({
      companyId: String(group.id),
      studentName: String(state.activeProfile?.contact?.name || state.selectedChat?.name || ''),
      clientRequestId: picker.clientRequestId,
    });
    let payload = null;
    let failure = '';
    try {
      const response = await api('/api/find-group/book-preview?' + params.toString());
      payload = await response.json().catch(() => ({}));
      if (!response.ok) failure = (payload && payload.error) || 'HTTP ' + response.status;
    } catch (err) {
      failure = err.message || String(err);
    }
    if (token !== picker.previewToken) return;
    picker.previewLoading = false;
    picker.preview = failure ? null : payload;
    picker.previewError = failure;
    const canTrial = !failure && _igpOccurrences().length > 0;
    picker.scope = (picker.intent === 'trial' && canTrial) ? 'trial' : 'always';
    picker.trialCount = 1;
    _igpEnsurePicked();
    _igpRefreshScopePanel();
    _igpRenderSelection();
  }

  // The scope chooser that opens under the group you just picked: one row
  // per number of trial classes, plus the permanent "Always" enrolment.
  function _igpBuildScopePanel() {
    const picker = state.inboxGroupPicker;
    const panel = el('div', { class: 'igp-scope', id: 'igpScope' });
    const group = picker.groups.find(g => String(g.id) === String(picker.selectedId));
    if (!group) return panel;
    _igpFillScopePanel(panel);
    return panel;
  }

  function _igpRefreshScopePanel() {
    const panel = document.getElementById('igpScope');
    if (!panel) return;
    clear(panel);
    _igpFillScopePanel(panel);
    refreshIcons(panel);
  }

  function _igpFillScopePanel(panel) {
    const picker = state.inboxGroupPicker;
    const occurrences = _igpOccurrences();
    const canTrial = !picker.previewLoading && !picker.previewError && occurrences.length > 0;
    const trialOffered = _igpCanBookTrial();
    // Enrolling a student who is already linked to this class does nothing, so
    // "Always" is not offered there. Settle the scope BEFORE any radio is
    // built, or the pre-checked one and the button label disagree.
    const group = picker.groups.find(g => String(g.id) === String(picker.selectedId));
    const alreadyLinked = !!(group && group.already_linked);
    if (alreadyLinked && picker.scope === 'always') picker.scope = canTrial ? 'trial' : '';
    if (!canTrial && !alreadyLinked && picker.scope !== 'always') picker.scope = 'always';
    if (picker.scope === 'trial') _igpEnsurePicked();

    if (trialOffered && picker.previewLoading) {
      panel.appendChild(el('div', { class: 'igp-scope-note', text: 'Checking this class schedule…' }));
    } else if (trialOffered && !canTrial) {
      const reason = IGP_PREVIEW_REASONS[picker.previewError]
        || (picker.previewError ? 'Cannot book a trial here: ' + picker.previewError : IGP_PREVIEW_REASONS.no_upcoming_occurrences);
      panel.appendChild(el('div', { class: 'igp-scope-note igp-scope-note-warn', text: reason }));
    }

    const addChoice = (value, label, detail) => {
      const row = el('label', { class: 'igp-scope-choice' + (picker.scope === value ? ' selected' : '') });
      const radio = el('input', { type: 'radio', name: 'igp-scope', value });
      radio.checked = picker.scope === value;
      radio.addEventListener('change', () => {
        picker.scope = value;
        _igpRefreshScopePanel();
        _igpRenderSelection();
      });
      row.appendChild(radio);
      row.appendChild(el('div', { class: 'igp-scope-copy' }, [
        el('strong', { text: label }),
        el('small', { text: detail })
      ]));
      panel.appendChild(row);
    };

    const week = _igpScheduleText(group);
    if (canTrial && week) {
      panel.appendChild(el('div', { class: 'igp-scope-week' }, [lucideIcon('calendar-days'), el('span', { text: 'This class meets ' + week })]));
    }
    if (canTrial) {
      const chosen = _igpPickedOccurrences();
      addChoice('trial', 'Trial class', picker.scope === 'trial' && chosen.length
        ? chosen.map(_igpTrialDateLabel).join('  +  ')
        : 'Pick the day — any upcoming class, not only the next one');
      if (picker.scope === 'trial') panel.appendChild(_igpBuildTrialPicker());
    }
    if (alreadyLinked) {
      panel.appendChild(el('div', { class: 'igp-scope-note', text: 'This student is already enrolled in this class — a trial session is all there is left to add.' }));
    } else {
      addChoice('always', 'Always — enrol as a student', 'Links the class in HubSpot. Full app access, no end date.');
    }
  }

  function _igpSetSelectOptions(id, values, emptyLabel, preferredValue) {
    const select = document.getElementById(id);
    if (!select) return;
    const wanted = preferredValue != null ? String(preferredValue) : select.value;
    clear(select);
    select.appendChild(el('option', { value: '', text: emptyLabel }));
    const unique = [...new Set((values || []).filter(Boolean).map(String))];
    if (wanted && !unique.includes(wanted)) unique.unshift(wanted);
    for (const value of unique) select.appendChild(el('option', { value, text: value }));
    select.value = wanted;
  }

  function _igpSyncTabs() {
    const picker = state.inboxGroupPicker;
    const recommended = document.getElementById('igpRecommendedTab');
    const all = document.getElementById('igpAllTab');
    recommended?.classList.toggle('active', picker.mode === 'recommended');
    all?.classList.toggle('active', picker.mode === 'all');
    recommended?.setAttribute('aria-selected', picker.mode === 'recommended' ? 'true' : 'false');
    all?.setAttribute('aria-selected', picker.mode === 'all' ? 'true' : 'false');
  }

  function _igpRenderResults(meta) {
    const picker = state.inboxGroupPicker;
    const results = document.getElementById('igpResults');
    const resultMeta = document.getElementById('igpResultMeta');
    if (!results || !resultMeta) return;
    clear(results);

    const shown = picker.groups.length;
    resultMeta.textContent = picker.mode === 'recommended'
      ? `${shown} matching group${shown === 1 ? '' : 's'} · ${meta?.eligibleTotal || 0} eligible overall`
      : `${shown} eligible group${shown === 1 ? '' : 's'}`;

    if (!shown) {
      const empty = el('div', { class: 'igp-empty' }, [
        lucideIcon('search-x'),
        el('strong', { text: picker.mode === 'recommended' ? 'No exact match found' : 'No eligible group found' }),
        el('span', { text: picker.mode === 'recommended'
          ? 'Change the filters or check all eligible groups.'
          : 'Try another group name or teacher.' })
      ]);
      if (picker.mode === 'recommended') {
        const allBtn = el('button', { class: 'btn btn-ghost btn-sm', type: 'button' }, [
          lucideIcon('list-filter'), document.createTextNode(' Show all eligible')
        ]);
        allBtn.addEventListener('click', () => {
          picker.mode = 'all';
          _igpSyncTabs();
          _igpLoad();
        });
        empty.appendChild(allBtn);
      }
      results.appendChild(empty);
      refreshIcons(results);
      _igpRenderSelection();
      return;
    }

    for (const group of picker.groups) {
      const selected = String(group.id) === String(picker.selectedId);
      // A typed search can surface a class the student already belongs to
      // (Doha, 2026-09-30: LC 205 after adding the student from the Groups tab).
      // In the add door there is nothing left to do with it: shown, labelled,
      // not selectable. The trial door still books a session in it.
      const alreadyIn = !!group.already_linked && picker.intent !== 'trial';
      const row = el('button', {
        class: 'igp-group-row' + (selected ? ' selected' : '') + (group.already_linked ? ' igp-card-linked' : ''),
        type: 'button',
        'data-group-id': group.id,
        'aria-pressed': selected ? 'true' : 'false'
      });
      if (alreadyIn) {
        row.disabled = true;
        row.title = 'This student is already in ' + (group.name || 'this group');
      }
      const head = el('div', { class: 'igp-card-head' });
      head.appendChild(el('div', { class: 'igp-card-title', text: group.name || '(unnamed group)', title: group.name || '' }));
      head.appendChild(el('span', { class: 'igp-status ' + _igpStatusTone(group.group_status), text: group.group_status ? humanizeKey(group.group_status) : 'Eligible' }));
      if (group.already_linked) head.appendChild(el('span', { class: 'igp-status igp-status-linked', text: 'Already in it' }));
      if (group.match_pct != null) {
        head.appendChild(el('div', {
          class: 'igp-match ' + (group.match_pct >= 75 ? 'igp-match-hi' : group.match_pct >= 50 ? 'igp-match-mid' : 'igp-match-lo'),
          title: group.match_pct + '% of the filters you set'
        }, [
          el('span', { class: 'igp-match-num', text: String(group.match_pct) }),
          el('span', { class: 'igp-match-cap', text: 'fit' })
        ]));
      }
      row.appendChild(head);

      // Same facts, in the same order, as the Book class card — minus the two
      // the mirror genuinely does not hold (program % and seat capacity live
      // in support2's engine). An absent number beats an invented one.
      const meta = el('div', { class: 'igp-card-meta' });
      const kv = (k, v, opts = {}) => {
        const cell = el('div', { class: 'igp-kv' + (opts.wide ? ' igp-kv-wide' : '') });
        cell.appendChild(el('span', { class: 'igp-k', text: k }));
        cell.appendChild(el('span', { class: 'igp-v' + (opts.numeric ? ' igp-v-num' : ''), text: v, title: v }));
        meta.appendChild(cell);
      };
      kv('Teacher', group.lougha_teachers || '—');
      kv('Level', group.level_of_groups || '—');
      kv('Type', group.class_type_key ? humanizeKey(group.class_type_key) : '—');
      kv('Students', String(Number(group.student_count) || 0), { numeric: true });
      kv('Schedule', _igpScheduleText(group) || '—', { wide: true });
      kv('Started', group.start_date ? _fgFmtDate(group.start_date) : '—', { numeric: true });
      kv('Time left', _igpTimeLeft(group.end_date), { numeric: true, wide: true });
      row.appendChild(meta);

      row.appendChild(el('div', { class: 'igp-card-foot' }, [
        el('span', { class: 'igp-select-indicator' }, [
          lucideIcon(selected || alreadyIn ? 'check-circle-2' : 'circle'),
          document.createTextNode(alreadyIn ? ' Already in this group' : (selected ? ' Selected' : ' Select'))
        ])
      ]));
      row.addEventListener('click', () => {
        picker.selectedId = selected ? '' : String(group.id);
        results.querySelectorAll('.igp-needs-selection').forEach(node => node.classList.remove('igp-needs-selection'));
        const error = document.getElementById('igpError');
        if (error?.textContent === 'Choose a group from the list first.') {
          error.classList.add('hidden');
          error.textContent = '';
        }
        _igpRenderResults(meta);
        // Selecting a group is what asks support2 for its real upcoming
        // sessions — the scope rows below the row fill in when it answers.
        _igpLoadPreview();
      });
      results.appendChild(row);
      if (selected) results.appendChild(_igpBuildScopePanel());
    }
    refreshIcons(results);
    _igpRenderSelection();
  }

  function _igpRenderSelection() {
    const picker = state.inboxGroupPicker;
    const summary = document.getElementById('igpSelectedSummary');
    const confirm = document.getElementById('igpConfirmBtn');
    const confirmLabel = document.getElementById('igpConfirmLabel');
    const group = picker.groups.find(g => String(g.id) === String(picker.selectedId));
    // A class the student is already in, with no bookable date, leaves nothing
    // to confirm — say that on the button instead of failing on click.
    const pickedCount = picker.scope === 'trial' ? _igpPickedOccurrences().length : 0;
    const missing = picker.scope === 'trial' ? Math.max(0, _igpTrialCount() - pickedCount) : 0;
    const nothingToDo = !!group && (!picker.scope || (picker.scope === 'trial' && (!pickedCount || missing > 0)));
    if (confirm) {
      confirm.disabled = nothingToDo;
      confirm.classList.toggle('igp-awaiting-selection', !group);
      confirm.title = group ? (nothingToDo ? 'Nothing to book or link for this class' : '') : 'Choose one group from the list first';
    }
    if (confirmLabel) {
      const classes = picker.scope === 'trial' ? pickedCount : Number(picker.scope);
      confirmLabel.textContent = !group
        ? 'Choose a group first'
        : picker.scope === 'trial' && (!pickedCount || missing > 0)
          ? 'Pick ' + (missing || 1) + ' more day' + ((missing || 1) === 1 ? '' : 's')
        : (nothingToDo
          ? 'Nothing to do here'
          : (Number.isFinite(classes) && classes > 0
            ? 'Send trial · ' + classes + (classes === 1 ? ' class' : ' classes')
            : 'Add to group'));
    }
    if (!summary) return;
    clear(summary);
    if (!group) {
      summary.appendChild(el('span', { text: 'No group selected' }));
      return;
    }
    summary.appendChild(lucideIcon('check-circle-2'));
    summary.appendChild(el('div', {}, [
      el('strong', { text: group.name }),
      el('span', { text: _igpScheduleText(group) || 'Schedule not set' })
    ]));
    refreshIcons(summary);
  }

  async function _igpLoad() {
    const picker = state.inboxGroupPicker;
    if (!picker.chatId || !picker.contactId) return;
    const requestId = ++picker.requestId;
    const results = document.getElementById('igpResults');
    const resultMeta = document.getElementById('igpResultMeta');
    const error = document.getElementById('igpError');
    if (error) { error.classList.add('hidden'); error.textContent = ''; }
    if (resultMeta) resultMeta.textContent = 'Finding available groups…';
    if (results) {
      clear(results);
      results.appendChild(el('div', { class: 'igp-loading' }, [lucideIcon('loader-circle'), document.createTextNode(' Loading groups…')]));
      refreshIcons(results);
    }
    const params = new URLSearchParams({
      chatId: picker.chatId,
      contactId: picker.contactId,
      ...(picker.leadId ? { leadId: picker.leadId } : {}),
      mode: picker.mode,
      q: picker.q,
      days: picker.days,
      time: picker.time,
      level: picker.level,
      classType: picker.classType,
      // Trial mode widens the list server-side: a class the student is already
      // in is still bookable for a session, and a typed search reaches classes
      // outside the enrolment statuses.
      intent: picker.intent,
    });
    try {
      const response = await api('/api/hubspot/group-picker?' + params.toString());
      const json = await response.json().catch(() => ({}));
      if (requestId !== picker.requestId) return;
      if (!response.ok) throw new Error(json.error || 'Could not load groups');
      picker.groups = Array.isArray(json.groups) ? json.groups : [];
      if (!picker.groups.some(g => String(g.id) === String(picker.selectedId))) picker.selectedId = '';
      _igpSetSelectOptions('igpDays', json.facets?.days, 'Any days', picker.days);
      _igpSetSelectOptions('igpTime', json.facets?.times, 'Any time', picker.time);
      _igpSetSelectOptions('igpLevel', json.facets?.levels, 'Any level', picker.level);
      _igpRenderResults(json);
    } catch (err) {
      if (requestId !== picker.requestId) return;
      picker.groups = [];
      picker.selectedId = '';
      if (results) clear(results);
      if (resultMeta) resultMeta.textContent = '';
      if (error) {
        error.textContent = err.message === 'profile_changed'
          ? 'This conversation changed. Close the picker and try again.'
          : (err.message || 'Could not load groups');
        error.classList.remove('hidden');
      }
      _igpRenderSelection();
    }
  }

  function openInboxGroupPicker(intent) {
    const data = state.activeProfile;
    const chatId = String(state.selectedChatId || '');
    const contact = data?.contact;
    if (!chatId || data?.isGroup || !contact?.id || String(data.chatId || '') !== chatId) {
      toast('Open a student conversation and wait for the profile to load', 'error');
      return;
    }
    const props = contact.properties || {};
    const picker = state.inboxGroupPicker;
    const wantsTrial = intent === 'trial' && _igpCanBookTrial();
    Object.assign(picker, {
      mode: 'recommended',
      q: '',
      days: '',
      time: '',
      level: _igpProfileLevel(props),
      // The trial list carries no individual classes, so a student whose
      // profile says "individual" must not arrive pre-filtered to nothing.
      classType: wantsTrial && _igpProfileClassType(props) === 'individual' ? '' : _igpProfileClassType(props),
      groups: [],
      selectedId: '',
      chatId,
      contactId: String(contact.id),
      // Shared number: the person the profile was rendered for (server-side
      // group-picker + add compare the contact against THAT person).
      leadId: String(data.person?.leadId || ''),
      intent: wantsTrial ? 'trial' : 'add',
      scope: 'always',
      preview: null,
      previewError: '',
      previewLoading: false,
      previewToken: picker.previewToken + 1,
      clientRequestId: '',
    });
    const title = document.getElementById('igpTitleText');
    if (title) title.textContent = wantsTrial ? 'Book a class for this student' : 'Add student to a group';
    document.getElementById('igpSearch').value = '';
    _igpSetSelectOptions('igpDays', [], 'Any days', '');
    _igpSetSelectOptions('igpTime', [], 'Any time', '');
    _igpSetSelectOptions('igpLevel', ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'], 'Any level', picker.level);
    // Offering a filter the trial list can never return is a dead option —
    // drop it from the select, and put it back for the enrolment door.
    const classTypeSelect = document.getElementById('igpClassType');
    const individualOption = classTypeSelect?.querySelector('option[value="individual"]');
    if (individualOption) individualOption.hidden = wantsTrial;
    if (classTypeSelect) classTypeSelect.value = picker.classType;
    const meta = document.getElementById('igpStudentMeta');
    if (meta) meta.textContent = `${contact.name || state.selectedChat?.name || 'Current student'} · ${contact.phone || state.selectedChat?.phone || ''}`.replace(/ · $/, '');
    _igpSyncTabs();
    _igpRenderSelection();
    openModal('inboxGroupPickerModal');
    _igpLoad();
  }

  // One button, two outcomes: a trial books real sessions through support2
  // and WhatsApps the student; "always" links the class in HubSpot forever.
  function _igpConfirm() {
    const picker = state.inboxGroupPicker;
    if (!picker.scope && picker.selectedId) return;
    if (picker.scope === 'trial') {
      const picked = _igpPickedOccurrences();
      if (!picked.length || picked.length < _igpTrialCount()) return _igpShowError('Pick the day(s) of the trial class first.');
      return _igpConfirmTrial(picked);
    }
    const classes = Number(picker.scope);
    if (Number.isFinite(classes) && classes > 0) return _igpConfirmTrial(classes);
    return _igpConfirmAdd();
  }

  function _igpShowError(message) {
    const error = document.getElementById('igpError');
    if (error) {
      error.textContent = message;
      error.classList.remove('hidden');
    }
  }

  async function _igpConfirmTrial(classes) {
    const picker = state.inboxGroupPicker;
    const group = picker.groups.find(g => String(g.id) === String(picker.selectedId));
    const preview = picker.preview;
    if (!group || !preview) return;
    const take = Array.isArray(classes) ? classes.slice(0, IGP_TRIAL_MAX_CLASSES) : _igpOccurrences().slice(0, classes);
    if (!take.length) {
      _igpShowError(IGP_PREVIEW_REASONS.no_upcoming_occurrences);
      return;
    }
    const studentPhone = _igpStudentPhone();
    if (!studentPhone) {
      _igpShowError('No usable phone number on this conversation — a trial message needs one.');
      return;
    }
    const studentName = String(state.activeProfile?.contact?.name || state.selectedChat?.name || '').trim();
    if (!studentName) {
      _igpShowError('This contact has no name — support2 needs one to write the trial message.');
      return;
    }
    const previewGroup = preview.group || {};
    const confirm = document.getElementById('igpConfirmBtn');
    const snapshot = { chatId: picker.chatId };
    if (confirm) { confirm.disabled = true; confirm.classList.add('loading'); }
    try {
      const response = await api('/api/find-group/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientRequestId: picker.clientRequestId,
          companyId: String(group.id),
          groupId: previewGroup.groupId || '',
          studentPhone,
          studentName,
          studentContactId: String(picker.contactId || ''),
          studentLevel: preview.expectedLevel || previewGroup.level || '',
          expectedTeacher: preview.expectedTeacher || previewGroup.teacher || '',
          expectedLevel: preview.expectedLevel || previewGroup.level || '',
          classDates: take.map(o => o.classDate),
          // Each date carries its own time — a 4-day class runs at two.
          occurrences: take.map(o => ({ classDate: o.classDate, startTime: o.startTime, endTime: o.endTime })),
          startTime: take[0].startTime,
          endTime: take[0].endTime,
        })
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || !json.ok) throw new Error(json.error || 'HTTP ' + response.status);
      closeModal('inboxGroupPickerModal');
      // A booked-but-undelivered trial is a half-done job, not a success —
      // say so, because the student is expecting nothing.
      toast(
        json.studentMsgSent
          ? 'Trial sent · ' + take.length + (take.length === 1 ? ' class' : ' classes')
          : 'Trial booked, WhatsApp not delivered: ' + (json.studentErr || 'unknown'),
        json.studentMsgSent ? 'success' : 'error'
      );
      if (_profilePersonChatId() === snapshot.chatId) {
        await Promise.all([loadProfileData(), loadMessages({ force: true })]);
      }
    } catch (err) {
      const key = err.message || '';
      const messages = {
        stale_group_snapshot: 'This class changed in HubSpot while you were choosing. Reopen and try again.',
        stale_or_invalid_occurrence: 'The schedule changed while you were choosing. Reopen and pick the dates again.',
        stale_level: 'The student level does not match this class level.',
        teacher_not_resolved: 'This class has no teacher we can notify — fix the teacher in HubSpot first.',
        group_code_missing: 'This class has no code and no teacher email — students7 cannot open the room.',
        too_many_trial_classes: 'support2 accepts at most ' + IGP_TRIAL_MAX_CLASSES + ' trial classes.',
      };
      _igpShowError(messages[key] || IGP_PREVIEW_REASONS[key] || key || 'Could not book the trial');
      if (confirm) confirm.disabled = false;
    } finally {
      confirm?.classList.remove('loading');
    }
  }

  async function _igpConfirmAdd() {
    const picker = state.inboxGroupPicker;
    const group = picker.groups.find(g => String(g.id) === String(picker.selectedId));
    if (!group) {
      const error = document.getElementById('igpError');
      if (error) {
        error.textContent = 'Choose a group from the list first.';
        error.classList.remove('hidden');
      }
      const firstGroup = document.querySelector('#igpResults .igp-group-row');
      firstGroup?.classList.add('igp-needs-selection');
      firstGroup?.focus();
      toast('Choose a group first', 'error');
      return;
    }
    const confirm = document.getElementById('igpConfirmBtn');
    const snapshot = { chatId: picker.chatId, contactId: picker.contactId, companyId: String(group.id), leadId: picker.leadId || '' };
    if (confirm) { confirm.disabled = true; confirm.classList.add('loading'); }
    try {
      const response = await api('/api/inbox/chats/' + encodeURIComponent(snapshot.chatId) + '/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_id: snapshot.contactId,
          company_id: snapshot.companyId,
          ...(snapshot.leadId ? { leadId: snapshot.leadId } : {}),
        })
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.error || 'Could not add student');
      closeModal('inboxGroupPickerModal');
      toast(json.alreadyLinked ? 'Student is already in this group' : `Added to ${json.group?.name || group.name}`, 'success');
      if (_profilePersonChatId() === snapshot.chatId) {
        await Promise.all([loadProfileData(), loadMessages({ force: true })]);
      }
    } catch (err) {
      const messages = {
        profile_changed: 'The student profile changed. Reopen the picker and try again.',
        group_not_eligible: 'This group is no longer open for enrolment.',
        'not assigned to you': 'This conversation is no longer assigned to you.'
      };
      const error = document.getElementById('igpError');
      if (error) {
        error.textContent = messages[err.message] || err.message || 'Could not add student';
        error.classList.remove('hidden');
      }
      if (confirm) confirm.disabled = false;
    } finally {
      confirm?.classList.remove('loading');
    }
  }

  // Shared number, switched to a person whose lead has NO HubSpot contact.
  // The one action here mints that person's contact and links it to their
  // lead (POST …/person/:leadId/contact). It creates a REAL contact on a real
  // client's line, so it is gated twice like add-person: this flag is checked
  // on the first line (a held Enter / double click cannot fire twice), and the
  // server holds its own one-in-flight-per-lead lock.
  let _profileCreateContactBusy = false;
  // Lead ids whose automatic contact setup already ran this page (see below).
  const _profileAutoContactTried = new Set();
  function _profileNoContactForPerson(person) {
    const name = String(person?.name || '').trim() || 'this person';
    const box = el('div', { class: 'profile-person-empty' });
    box.appendChild(el('div', { class: 'profile-person-empty-title', text: name + ' has no HubSpot contact yet' }));
    if (person?.linkedContactId) {
      // Linked, but the contact could not be read — offering "Create" here
      // would mint a duplicate of a person who already has one.
      box.appendChild(el('div', { class: 'profile-person-empty-text',
        text: 'Their lead is linked to a HubSpot contact that could not be loaded right now. Reopen the profile in a minute.' }));
      return box;
    }
    const chatId = String(state.selectedChatId || '');
    // AUTOMATIC (Ayoub 2026-09-30: "khas normal automatic"). The server picks
    // the right move in one call: link an unlinked contact of this person on
    // the number, MOVE this person's contact off a relative's lead (the
    // zohra/Asaad case), or create one. The button is only the fallback when
    // that automatic attempt could not finish.
    const status = el('div', { class: 'profile-person-empty-text', text: 'Setting up ' + name + '’s contact…' });
    box.appendChild(status);
    const err = el('div', { class: 'profile-person-empty-err hidden' });
    const btn = el('button', { type: 'button', class: 'btn btn-primary btn-sm profile-person-create hidden' }, [
      lucideIcon('user-plus'), document.createTextNode(' Try again for ' + name),
    ]);
    const run = async () => {
      if (_profileCreateContactBusy) return;
      const phone = (_frCtx && _frCtx.chatId === chatId && _frCtx.phone) || pipelineExtractPhone(state.selectedChat);
      if (!person?.leadId || !phone) return;
      _profileCreateContactBusy = true;
      btn.disabled = true;
      err.classList.add('hidden');
      let keepDisabled = false;
      try {
        const r = await api('/api/leads/by-number/' + encodeURIComponent(phone) + '/person/'
          + encodeURIComponent(person.leadId) + '/contact', { method: 'POST' });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) {
          // An orphan contact means it EXISTS already: never invite a retry.
          keepDisabled = !!j.orphanContact;
          status.textContent = name + ' has no HubSpot contact yet.';
          err.textContent = j.error || 'Could not set up the contact';
          err.classList.remove('hidden');
          btn.classList.toggle('hidden', keepDisabled);
          return;
        }
        keepDisabled = true;
        if (String(state.selectedChatId || '') === chatId) {
          toast(j.moved ? 'Contact moved to ' + name + (j.movedFrom ? ' (it was on ' + j.movedFrom + ')' : '')
            : j.linked ? 'Linked ' + name + ' to the contact already on this number'
            : (j.existing ? name + ' already had a contact — loading it' : 'Contact created for ' + name), 'success');
        }
        if (_frCtx && _frCtx.chatId === chatId) _frRefresh();
        if (String(state.selectedChatId || '') === chatId) loadProfileData();
      } catch (e) {
        status.textContent = name + ' has no HubSpot contact yet.';
        err.textContent = 'Network error';
        err.classList.remove('hidden');
        btn.classList.remove('hidden');
      } finally {
        _profileCreateContactBusy = false;
        if (btn.isConnected && !keepDisabled) btn.disabled = false;
      }
    };
    btn.addEventListener('click', ev => { ev.preventDefault(); if (!btn.disabled) run(); });
    box.appendChild(btn);
    box.appendChild(err);
    refreshIcons(box);
    // Once per person per page: a failure shows the fallback button instead of
    // retrying on every repaint.
    const role = String(state.me?.role || '').toLowerCase();
    const canWrite = ['agent', 'super_sales', 'admin', 'super_admin'].includes(role) || _isSalesAdmin();
    if (canWrite && person?.leadId && !_profileAutoContactTried.has(person.leadId)) {
      _profileAutoContactTried.add(person.leadId);
      setTimeout(run, 0);
    } else {
      status.textContent = name + ' has no HubSpot contact yet.';
      if (canWrite) btn.classList.remove('hidden');
    }
    return box;
  }

  function renderProfileSections(data, parent) {
    clear(parent);
    // Re-shown below only after an exact 1-1 HubSpot contact has resolved.
    document.getElementById('profileCheckAppBtn')?.classList.add('hidden');
    window.LoughaTeacherNotes?.prepare(null, '');
    if (data.isGroup) {
      const g = data.group || {};
      parent.appendChild(buildAccordion('group-info', 'users', 'Group info', true, body => {
        const isAdmin = state.me?.role === 'admin';
        if (isAdmin) {
          addEditableGroupField(body, 'Name', g.name || '', state.selectedChatId, 'subject');
        } else {
          addField(body, 'Name', g.name);
        }
        addField(body, 'Members', g.members);
        addField(body, 'Owner', g.owner);
        addField(body, 'Created', g.creation ? formatChatDate(g.creation) : '');
        if (isAdmin) {
          addEditableGroupField(body, 'Description', g.desc || '', state.selectedChatId, 'description', { multiline: true });
        } else {
          addField(body, 'Description', g.desc);
        }
      }));

      // WhatsApp members — always rendered when the bridge could pull the
      // participant list, regardless of HubSpot company match. Click a row to
      // jump into that member's 1-1 chat.
      const wm = Array.isArray(data.waMembers) ? data.waMembers : [];
      if (wm.length) {
        parent.appendChild(buildAccordion('wa-members', 'users', 'WhatsApp members (' + wm.length + ')', true, body => {
          for (const m of wm) {
            const display = m.name || m.phone || (m.jid || '').split('@')[0];
            const row = el('div', { class: 'group-student-row', style: 'display:flex;align-items:center;gap:8px;padding:6px 8px;border-radius:6px;cursor:pointer;' });
            row.appendChild(el('div', {
              class: 'avatar avatar-sm ' + avatarColorClass(m.jid || m.phone),
              style: 'width:28px;height:28px;font-size:11px;flex-shrink:0;',
              text: initials(display) || '?'
            }));
            const subtitle = m.phone || (m.jid?.endsWith('@lid') ? 'private id (no phone shown by WhatsApp)' : '');
            row.appendChild(el('div', { style: 'flex:1;min-width:0;' }, [
              el('div', { style: 'font-size:13px;font-weight:600;color:var(--gray-900);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', text: display + (m.isAdmin ? ' · admin' : '') }),
              el('div', { style: 'font-size:11px;color:var(--gray-500);', text: subtitle })
            ]));
            row.addEventListener('mouseenter', () => row.style.background = 'var(--gray-50)');
            row.addEventListener('mouseleave', () => row.style.background = '');
            row.addEventListener('click', () => {
              if (m.phone) {
                // Phone known → use existing 1-1 chat sync helper which
                // dedupes by phone and pulls history.
                openChatForHubspotContact({ id: m.hubspotId || '', name: m.name || display, phone: m.phone });
              } else if (m.jid) {
                // LID-only — synthesize a chat keyed on the LID JID. Baileys
                // routes outbound messages to LIDs correctly.
                showView('inbox');
                if (!state.chats.find(c => c.id === m.jid)) {
                  state.chats.unshift({ id: m.jid, name: display, phone: '', isGroup: false, lastMessage: null, unread: 0 });
                }
                setTimeout(() => selectConversation(m.jid), 100);
              }
            });
            body.appendChild(row);
          }
        }));
      }

      // HubSpot company match — show pinned company fields, customizable like Student Info
      if (data.hubspotCompany?.id) {
        const c = data.hubspotCompany;
        const pinned = getPinnedCompanyFields(c.id);
        const portalId = window._hubspotPortalId || '';
        // A company created since the registry cut-over has a local `3…` id HubSpot
        // never saw; its copy arrives as hubspot_remote_id once the outbox runs.
        const hsId = /^[123][0-9]{12}$/.test(String(c.id)) ? (c.properties?.hubspot_remote_id || '') : c.id;
        const url = (portalId && hsId)
          ? `https://app.hubspot.com/contacts/${portalId}/record/0-2/${hsId}`
          : `https://app.hubspot.com/contacts/`;
        parent.appendChild(buildAccordion('hubspot-company', 'building-2', 'HubSpot company', true, body => {
          if (c.matchType === 'contains') {
            body.appendChild(el('div', { class: 'hs-match-hint', text: '⚠ Match approximatif (contient le nom). Vérifie ci-dessous.' }));
          }
          for (const fieldName of pinned) {
            const label = state.addPropChipsLabels[fieldName] || HUBSPOT_PROP_LABELS[fieldName] || humanizeKey(fieldName);
            let value = c.properties[fieldName] || '';
            if (fieldName === 'createdate' && value) value = formatChatDate(value);
            addCompanyPinnedFieldRow(body, label, value, fieldName, c.id);
          }
          setupPinnedFieldsDnd(
            body,
            () => getPinnedCompanyFields(c.id),
            (newOrder) => savePinnedCompanyFields(c.id, newOrder)
          );
          // Team-wide free-form custom properties for this group are rendered
          // automatically by buildAccordion's wrappedFill for non-student-info
          // sections, so we don't call appendCustomPropsToSection here.
          const addCustomBtn = el('button', {
            class: 'add-custom-prop-inline',
            type: 'button',
            style: 'display:flex;align-items:center;gap:6px;margin-top:8px;padding:6px 10px;font-size:12px;font-weight:600;color:var(--blue-600);background:var(--blue-50);border:1px dashed var(--blue-300);border-radius:6px;cursor:pointer;'
          }, [lucideIcon('plus'), el('span', { text: 'Add custom property' })]);
          addCustomBtn.addEventListener('click', () => {
            openAddPropModal('hubspot-company', { mode: 'company', companyId: c.id });
            // Inline-button entry point: user wants a free-form prop, so expand
            // the manual key/value section by default. The HubSpot picker stays
            // visible above for users who do want to pin an existing field.
            const manualDetails = document.getElementById('addPropManualSection');
            if (manualDetails) manualDetails.open = true;
            setTimeout(() => document.getElementById('addPropKey')?.focus(), 120);
          });
          body.appendChild(addCustomBtn);
          const link = el('a', { href: url, target: '_blank', text: 'Open in HubSpot ↗',
            style: 'display:block;margin-top:8px;font-size:11px;color:var(--blue-500);text-decoration:none;' });
          body.appendChild(link);
          if (c.alternates?.length) {
            body.appendChild(el('div', { class: 'hs-alternates-label', text: 'Autres correspondances:', style: 'margin-top:10px;font-size:11px;color:var(--gray-500);' }));
            for (const alt of c.alternates) {
              body.appendChild(el('div', { class: 'hs-alternate-row', style: 'font-size:12px;color:var(--gray-700);padding:2px 0;', text: '• ' + (alt.name || alt.id) }));
            }
          }
        }, { count: pinned.length, showAddProp: true, addPropSection: 'hubspot-company', addPropContext: { mode: 'company', companyId: c.id } }));

        // Students accordion — list of HubSpot contacts associated with the
        // matched company. Click a row → opens that student's 1-1 chat (same
        // synthetic-chat trick the inbox HubSpot search uses).
        if (Array.isArray(c.students) && c.students.length) {
          parent.appendChild(buildAccordion('group-students', 'users', 'Students (' + c.students.length + ')', true, body => {
            for (const s of c.students) {
              const row = el('div', { class: 'group-student-row', style: 'display:flex;align-items:center;gap:8px;padding:6px 8px;border-radius:6px;cursor:pointer;' });
              row.appendChild(el('div', {
                class: 'avatar avatar-sm ' + avatarColorClass(s.id),
                style: 'width:28px;height:28px;font-size:11px;flex-shrink:0;',
                text: initials(s.name) || '?'
              }));
              row.appendChild(el('div', { style: 'flex:1;min-width:0;' }, [
                el('div', { style: 'font-size:13px;font-weight:600;color:var(--gray-900);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', text: s.name || s.phone }),
                el('div', { style: 'font-size:11px;color:var(--gray-500);', text: s.phone || '' })
              ]));
              row.addEventListener('mouseenter', () => row.style.background = 'var(--gray-50)');
              row.addEventListener('mouseleave', () => row.style.background = '');
              row.addEventListener('click', () => {
                if (!s.phone) return;
                openChatForHubspotContact({ id: s.id, name: s.name, phone: s.phone });
              });
              body.appendChild(row);
            }
          }));
        }

        // Tickets accordion — same render as for individual contacts, but the
        // tickets are pulled by companyId. Create Ticket → submitCreateTicket
        // detects the group chat + companyId and associates only to the company.
        const ticketsRich = data.ticketsFull || [];
        parent.appendChild(buildAccordion('tickets', 'ticket', 'Tickets', true, body => {
          if (!ticketsRich.length) {
            body.appendChild(el('div', { class: 'empty-row', text: 'No tickets for this class.' }));
            return;
          }
          const pinned = getPinnedTicketFields(state.selectedChatId);
          for (const t of ticketsRich) {
            const card = el('div', { class: 'ticket-card' });
            card.appendChild(el('div', { class: 'ticket-card-title' }, [
              el('span', { class: 'ticket-card-subject', text: t.subject || '(No subject)' }),
              el('a', { href: t.url, target: '_blank', class: 'ticket-card-link', title: 'Open in HubSpot' }, [lucideIcon('external-link')])
            ]));
            const fieldsBox = el('div', { class: 'ticket-card-fields' });
            const props = t.properties || t;
            for (const fieldName of pinned) {
              const label = state.addPropChipsLabels[fieldName] || HUBSPOT_PROP_LABELS[fieldName] || humanizeKey(fieldName);
              let value = props[fieldName] != null ? String(props[fieldName]) : '';
              if (fieldName === 'createdate' && value) value = formatChatDate(value);
              else if (fieldName === 'hs_lastmodifieddate' && value) value = formatChatDate(value);
              else if (value) value = ticketDisplayValue(fieldName, value);
              addTicketPinnedFieldRow(fieldsBox, label, value, fieldName);
            }
            setupPinnedFieldsDnd(
              fieldsBox,
              () => getPinnedTicketFields(state.selectedChatId),
              (newOrder) => savePinnedTicketFields(state.selectedChatId, newOrder)
            );
            card.appendChild(fieldsBox);
            body.appendChild(card);
          }
        }, { count: ticketsRich.length, showAddProp: true, showCreateTicket: true, addPropContext: { mode: 'ticket' } }));
      }
      // Apply user's saved per-section drag order first (it's a global pref,
      // not group-specific), then enforce hubspot-company-first as the group
      // default UNLESS the agent has explicitly placed hubspot-company in
      // their saved order (= they intentionally pinned it elsewhere).
      applySavedSectionOrder();
      const savedOrder = state.me?.sectionOrder;
      const userPinnedCompany = Array.isArray(savedOrder) && savedOrder.includes('hubspot-company');
      if (!userPinnedCompany) {
        const companyCardFirst = parent.querySelector('.profile-card[data-section="hubspot-company"]');
        if (companyCardFirst && parent.firstElementChild !== companyCardFirst) {
          parent.insertBefore(companyCardFirst, parent.firstElementChild);
        }
      }
      refreshIcons();
      return;
    }

    const c = data.contact;
    if (!c && data.person?.switched) {
      // Shared number, switched to a person whose lead has no HubSpot contact
      // yet. The panel stays EMPTY — the number's default contact is another
      // family member, and showing it here is the bug Switch exists to stop.
      parent.appendChild(_profileNoContactForPerson(data.person));
      return;
    }
    if (!c) {
      parent.appendChild(el('div', { class: 'empty-row',
        text: data.phone ? 'No HubSpot contact found for this number.' : 'No phone resolved for this chat.' }));
      return;
    }

    _mountCheckAppPill();
    const profileChatId = _profilePersonChatId();
    window.LoughaTeacherNotes?.prepare(data, profileChatId);

    // Student info — driven by per-chat pinned list (user picks which HubSpot fields to show)
    const pinned = getPinnedFields(state.selectedChatId);
    // The encaissement fields always show; the per-chat pins only add whatever
    // ISN'T already in that fixed block (no duplicate rows).
    const extraPinned = pinned.filter(f => !ENCAISSEMENT_CARD_FIELDS.includes(f));
    parent.appendChild(buildAccordion('student-info', 'user', 'Student Info', true, body => {
      const p = c.properties || {};
      const labelOverrides = state.addPropChipsLabels || {};
      // Fixed encaissement block — same order and grouping as the modal.
      for (const [groupLabel, fields] of ENCAISSEMENT_CARD_GROUPS) {
        body.appendChild(el('div', { class: 'enc-group-head', text: groupLabel }));
        for (const fieldName of fields) {
          const label = labelOverrides[fieldName] || HUBSPOT_PROP_LABELS[fieldName] || humanizeKey(fieldName);
          const value = p[fieldName] != null ? String(p[fieldName]) : '';
          addEncaissementFieldRow(body, label, value, fieldName, c.id);
        }
      }
      if (extraPinned.length) {
        body.appendChild(el('div', { class: 'enc-group-head', text: 'Autres champs' }));
      }
      const extrasHost = el('div', { class: 'enc-extra-pins' });
      body.appendChild(extrasHost);
      for (const fieldName of extraPinned) {
        let label = labelOverrides[fieldName] || HUBSPOT_PROP_LABELS[fieldName] || humanizeKey(fieldName);
        let value = '';
        if (fieldName === 'full_name') value = c.name || '';
        else if (fieldName === 'phone') value = p.phone || p.mobilephone || p.whatsapp_phone || '';
        else if (fieldName === 'createdate' && p.createdate) value = formatChatDate(p.createdate);
        else value = p[fieldName] != null ? String(p[fieldName]) : '';
        addPinnedFieldRow(extrasHost, label, value, fieldName);
      }
      setupPinnedFieldsDnd(
        extrasHost,
        () => getPinnedFields(state.selectedChatId),
        (newOrder) => savePinnedFields(state.selectedChatId, newOrder)
      );
      const link = el('a', { href: c.url, target: '_blank', text: 'Open in HubSpot ↗',
        style: 'display:block;margin-top:8px;font-size:11px;color:var(--blue-500);text-decoration:none;' });
      body.appendChild(link);
    }, { count: ENCAISSEMENT_CARD_FIELDS.length + extraPinned.length, showAddProp: true, showHubspotImport: false }));

    // Same read-only Classes & App view as support2, scoped to the selected learner.
    window.LoughaClassesApp?.renderSection(parent, data, profileChatId);

    // Same exact club eligibility the student sees in Students7. The external
    // module renders the shell synchronously, then fills it without delaying
    // the rest of Contact Profile.
    window.LoughaClubs?.renderSection(parent, data, profileChatId);

    // Groups (companies) — each company row tries to resolve to a real WA
    // group from state.chats so the agent can click the row to jump
    // straight into the group conversation. Resolution order: exact name
    // match → case-insensitive contains → LC/LG class-code extraction.
    // Returns null when no WA group is loaded for that company (admin can
    // still see the row, just non-clickable).
    function findWaGroupForCompany(companyName) {
      const trimmed = String(companyName || '').trim();
      if (!trimmed) return null;
      const groups = (state.chats || []).filter(c => c.isGroup);
      let hit = groups.find(c => c.name === trimmed);
      if (hit) return hit;
      const lower = trimmed.toLowerCase();
      hit = groups.find(c => (c.name || '').toLowerCase() === lower);
      if (hit) return hit;
      hit = groups.find(c => (c.name || '').toLowerCase().includes(lower));
      if (hit) return hit;
      // Class-code path — both sides may differ in spacing/case ("LG378
      // KIDS" company name vs "LG378 KIDS" / "LG 378 kids" group name).
      const codeMatch = trimmed.match(/(LC|LG)\s*[-_ ]?\s*(\d+)/i);
      if (codeMatch) {
        const target = codeMatch[1].toUpperCase() + ' ' + codeMatch[2];
        hit = groups.find(c => {
          const m = (c.name || '').match(/(LC|LG)\s*[-_ ]?\s*(\d+)/i);
          return m && (m[1].toUpperCase() + ' ' + m[2]) === target;
        });
        if (hit) return hit;
      }
      return null;
    }

    const groupsCount = (c.companies || []).length;
    parent.appendChild(buildAccordion('groups', 'users-round', 'Groups', true, body => {
      const addToGroup = el('button', { class: 'profile-group-add-cta', type: 'button' }, [
        el('span', { class: 'profile-group-add-icon' }, [lucideIcon('user-plus')]),
        el('span', { class: 'profile-group-add-copy' }, [
          el('strong', { text: 'Add to a group' }),
          el('small', { text: 'Find a matching class without leaving Inbox' })
        ]),
        lucideIcon('chevron-right')
      ]);
      addToGroup.addEventListener('click', () => openInboxGroupPicker('add'));
      body.appendChild(addToGroup);
      // Second door into the same picker: same list, same modal, but the
      // scope rows open on "this class only" instead of the permanent
      // enrolment — the team books one or two real sessions and the student
      // gets the WhatsApp with the dates and the app link.
      if (_igpCanBookTrial()) {
        const bookClass = el('button', { class: 'profile-group-add-cta profile-group-book-cta', type: 'button' }, [
          el('span', { class: 'profile-group-add-icon' }, [lucideIcon('calendar-plus')]),
          el('span', { class: 'profile-group-add-copy' }, [
            el('strong', { text: 'Book a class' }),
            el('small', { text: 'One or more trial sessions — the student gets the details on WhatsApp' })
          ]),
          lucideIcon('chevron-right')
        ]);
        bookClass.addEventListener('click', () => openInboxGroupPicker('trial'));
        body.appendChild(bookClass);
      }
      if (!c.companies?.length) {
        body.appendChild(el('div', { class: 'empty-row', text: 'No groups linked.' }));
        return;
      }
      c.companies.forEach(g => {
        // Prefer server-resolved waGroupId (full groups_cache, even for
        // groups outside the inbox top-200). Fall back to the client-side
        // lookup against state.chats when the server didn't resolve one.
        let matchingChat = null;
        if (g.waGroupId) {
          const inState = (state.chats || []).find(x => x.id === g.waGroupId);
          matchingChat = inState || { id: g.waGroupId, name: g.waGroupName || g.name, isGroup: !!g.waGroupId.endsWith?.('@g.us') };
        }
        if (!matchingChat) matchingChat = findWaGroupForCompany(g.name);
        const item = el('div', { class: 'list-item' + (matchingChat ? ' list-item-clickable' : '') });
        const titleRow = el('div', { class: 'li-title-row', style: 'display:flex;align-items:center;gap:6px;' });
        titleRow.appendChild(el('span', { class: 'li-title', text: g.name || '(unnamed)' }));
        if (matchingChat) {
          titleRow.appendChild(el('span', { class: 'li-open-hint muted', style: 'font-size:11px;', text: '↗ Open' }));
        }
        const removeGroup = el('button', {
          class: 'profile-group-remove-btn',
          type: 'button',
          title: 'Remove this student from the HubSpot group',
          'aria-label': 'Remove from ' + (g.name || 'group')
        }, [lucideIcon('user-minus')]);
        removeGroup.addEventListener('click', async event => {
          event.preventDefault();
          event.stopPropagation();
          const snapshot = {
            chatId: profileChatId,
            contactId: String(c.id),
            companyId: String(g.id),
            companyName: g.name || 'this group',
            // Shared number: the person this panel was rendered for — the
            // server compares the contact against THAT person, not the default.
            leadId: data.person?.leadId || ''
          };
          if (!confirm(`Remove ${c.name || 'this student'} from «${snapshot.companyName}»?`)) return;
          removeGroup.disabled = true;
          try {
            const params = new URLSearchParams({ contact_id: snapshot.contactId });
            if (snapshot.leadId) params.set('leadId', snapshot.leadId);
            const response = await api('/api/inbox/chats/' + encodeURIComponent(snapshot.chatId)
              + '/groups/' + encodeURIComponent(snapshot.companyId) + '?' + params.toString(), { method: 'DELETE' });
            const json = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(json.error || 'Could not remove student');
            toast(`Removed from ${json.group?.name || snapshot.companyName}`, 'success');
            window.LoughaClubs?.invalidate(snapshot.chatId);
            if (_profilePersonChatId() === snapshot.chatId) {
              await Promise.all([loadProfileData(), loadMessages({ force: true })]);
            }
          } catch (err) {
            const messages = {
              profile_changed: 'The student profile changed. Reopen the profile and try again.',
              group_not_linked: 'This student is no longer linked to that group.',
              'not assigned to you': 'This conversation is no longer assigned to you.'
            };
            toast(messages[err.message] || err.message || 'Could not remove student', 'error');
            removeGroup.disabled = false;
          }
        });
        titleRow.appendChild(removeGroup);
        item.appendChild(titleRow);
        if (g.schedule_days || g.schedule_time) {
          item.appendChild(el('div', { class: 'li-sub', text: [g.schedule_days, g.schedule_time].filter(Boolean).join(' · ') }));
        }
        if (g.group_class_type) {
          item.appendChild(el('div', { class: 'li-sub', text: 'Type: ' + g.group_class_type }));
        }
        if (matchingChat) {
          item.title = 'Open: ' + matchingChat.name;
          item.style.cursor = 'pointer';
          item.addEventListener('click', () => {
            const existing = state.chats.find(x => x.id === matchingChat.id);
            if (!existing) {
              state.chats.unshift({
                id: matchingChat.id,
                name: matchingChat.name,
                phone: '',
                isGroup: !!matchingChat.isGroup,
                lastMessage: null,
                unread: 0
              });
            }
            // navigateToChat is a no-op when the target is already
            // selected — flash the conv-item briefly so the click feels
            // responsive even in that case.
            const wasSame = state.selectedChatId === matchingChat.id;
            setTimeout(() => {
              navigateToChat(matchingChat.id);
              if (wasSame) {
                const node = document.querySelector('.conv-item.active');
                if (node) {
                  node.classList.add('flash');
                  setTimeout(() => node.classList.remove('flash'), 600);
                }
              }
            }, 50);
          });
        }
        body.appendChild(item);
      });
    }, { count: groupsCount, showAddProp: true }));

    // Tickets — each ticket renders the same user-pinned HubSpot ticket fields
    const ticketsCount = (data.tickets || []).length;
    const ticketsRich = data.ticketsFull || data.tickets || []; // ticketsFull populated by loadProfileData
    parent.appendChild(buildAccordion('tickets', 'ticket', 'Tickets', true, body => {
      if (!ticketsRich.length) {
        body.appendChild(el('div', { class: 'empty-row', text: 'No open tickets.' }));
        return;
      }
      const pinned = getPinnedTicketFields(state.selectedChatId);
      for (const t of ticketsRich) {
        const card = el('div', { class: 'ticket-card' });
        const titleRow = el('div', { class: 'ticket-card-title' }, [
          el('span', { class: 'ticket-card-subject', text: t.subject || '(No subject)' }),
          el('a', { href: t.url, target: '_blank', class: 'ticket-card-link', title: 'Open in HubSpot' }, [lucideIcon('external-link')])
        ]);
        card.appendChild(titleRow);
        const fieldsBox = el('div', { class: 'ticket-card-fields' });
        const props = t.properties || t;
        for (const fieldName of pinned) {
          const label = state.addPropChipsLabels[fieldName] || HUBSPOT_PROP_LABELS[fieldName] || humanizeKey(fieldName);
          let value = props[fieldName] != null ? String(props[fieldName]) : '';
          if (fieldName === 'createdate' && value) value = formatChatDate(value);
          else if (fieldName === 'hs_lastmodifieddate' && value) value = formatChatDate(value);
          else if (value) value = ticketDisplayValue(fieldName, value);
          addTicketPinnedFieldRow(fieldsBox, label, value, fieldName);
        }
        setupPinnedFieldsDnd(
          fieldsBox,
          () => getPinnedTicketFields(state.selectedChatId),
          (newOrder) => savePinnedTicketFields(state.selectedChatId, newOrder)
        );
        card.appendChild(fieldsBox);
        body.appendChild(card);
      }
    }, { count: ticketsCount, showAddProp: true, showCreateTicket: true, addPropContext: { mode: 'ticket' } }));

    // Documents (will list from /api/documents) — count lazily updated.
    // Shared number: with the active person's leadId the server splits the
    // chat's documents into THIS person's (`documents`) and everything else
    // sent on the number (`otherDocuments` — older sends that carry no person,
    // or a sibling's), shown collapsed below. Without a person: unchanged.
    const docsPersonLeadId = data.person?.leadId || '';
    const docsUrl = '/api/documents?chatId=' + encodeURIComponent(state.selectedChatId)
      + (docsPersonLeadId ? '&leadId=' + encodeURIComponent(docsPersonLeadId) : '');
    const docItem = d => {
      const item = el('div', { class: 'list-item' });
      item.appendChild(el('div', { class: 'li-title', text: d.fileName || d.docType }));
      item.appendChild(el('div', { class: 'li-sub', text: formatChatDate(d.sentAt) + ' · ' + d.docType }));
      item.appendChild(el('a', { href: d.fileUrl, target: '_blank', text: 'Download PDF' }));
      return item;
    };
    const documentsCard = buildAccordion('documents', 'file-text', 'Documents', true, async body => {
      body.appendChild(el('div', { class: 'empty-row', text: 'Loading…' }));
      try {
        const r = await api(docsUrl);
        const j = await r.json();
        clear(body);
        const others = Array.isArray(j.otherDocuments) ? j.otherDocuments : [];
        if (!j.documents?.length && !others.length) {
          body.appendChild(el('div', { class: 'empty-row', text: 'No documents sent yet.' }));
          return;
        }
        if (!j.documents?.length) {
          body.appendChild(el('div', { class: 'empty-row', text: 'No documents sent for ' + (data.person?.name || 'this person') + ' yet.' }));
        }
        (j.documents || []).slice().reverse().forEach(d => body.appendChild(docItem(d)));
        if (others.length) {
          const more = el('details', { class: 'profile-docs-others' });
          more.appendChild(el('summary', { text: 'Other documents on this number (' + others.length + ')' }));
          others.slice().reverse().forEach(d => more.appendChild(docItem(d)));
          body.appendChild(more);
        }
      } catch {
        clear(body);
        body.appendChild(el('div', { class: 'empty-row', text: 'Failed to load documents' }));
      }
    }, { count: 0, showAddProp: true });
    parent.appendChild(documentsCard);
    // Lazy-update the count badge
    api(docsUrl)
      .then(r => r.json())
      .then(j => {
        const count = (j.documents || []).length;
        const badge = documentsCard.querySelector('.card-title-count');
        if (badge) badge.textContent = '(' + count + ')';
      })
      .catch(() => {});

    // Activity — count = messages in chat
    const activityCount = (state.messages || []).length;
    parent.appendChild(buildAccordion('activity', 'activity', 'Activity', false, body => {
      if (!state.messages?.length) {
        body.appendChild(el('div', { class: 'empty-row', text: 'No activity yet.' }));
        return;
      }
      state.messages.slice(-15).reverse().forEach(m => {
        const item = el('div', { class: 'list-item' });
        const who = m.fromMe ? 'You' : (m.pushName || 'Contact');
        item.appendChild(el('div', { class: 'li-title', text: who }));
        const body2 = m.type !== 'text' ? `[${m.type}]` : (m.text || '');
        item.appendChild(el('div', { class: 'li-sub', text: body2.slice(0, 80) + (body2.length > 80 ? '…' : '') }));
        item.appendChild(el('div', { class: 'li-sub', text: formatChatDate(m.timestamp) }));
        body.appendChild(item);
      });
    }, { count: activityCount }));

    // Group profiles: surface the HubSpot company section FIRST — it carries
    // the most useful business info (LC name, schedule, lifecycle stage) and
    // is the section agents check most. Per-user drag-and-drop order saved
    // via applySavedSectionOrder still wins if the agent has customised it.
    if (data.isGroup) {
      const companyCard = parent.querySelector('.profile-card[data-section="hubspot-company"]');
      if (companyCard && parent.firstElementChild !== companyCard) {
        parent.insertBefore(companyCard, parent.firstElementChild);
      }
    }

    applySavedSectionOrder();
    refreshIcons();
  }

  // ============ Custom properties (per-chat per-section) ============
  state.customProps = {}; // chatId -> { section: [{id, key, value, ...}] }

  async function loadCustomProps(chatId) {
    if (!chatId) return {};
    try {
      const r = await api('/api/inbox/chats/' + encodeURIComponent(chatId) + '/custom-props');
      const j = await r.json();
      state.customProps[chatId] = j.props || {};
      return j.props || {};
    } catch { return {}; }
  }

  function customPropsForSection(section) {
    const chatId = state.selectedChatId;
    if (!chatId) return [];
    const all = state.customProps[chatId] || {};
    return all[section] || [];
  }

  function appendCustomPropsToSection(body, section) {
    const props = customPropsForSection(section);
    if (!props.length) return;
    body.appendChild(el('div', { class: 'custom-props-divider' }));
    body.appendChild(el('div', { class: 'custom-props-label' }, [
      lucideIcon('sparkles'), document.createTextNode(' Custom')
    ]));
    for (const p of props) body.appendChild(buildCustomPropRow(p));
    refreshIcons();
  }

  function buildCustomPropRow(p) {
    const row = el('div', { class: 'custom-prop-row', 'data-prop-id': p.id });
    if (p.source) row.setAttribute('data-source', p.source);
    const content = el('div', { class: 'cp-content' }, [
      el('div', { class: 'cp-key', text: p.key || '' }),
      el('div', { class: 'cp-value', text: p.value || '—' }),
      p.createdByName || p.createdBy
        ? el('div', { class: 'cp-author', text: 'by ' + userLabel(p.createdBy, p.createdByName || p.createdBy) })
        : null
    ].filter(Boolean));
    row.appendChild(content);

    // Inline edit on click of value
    const valueEl = content.querySelector('.cp-value');
    valueEl?.addEventListener('click', () => editCustomPropValue(p, row));

    const editBtn = el('button', { class: 'cp-edit-btn', title: 'Rename' }, [lucideIcon('pencil')]);
    editBtn.addEventListener('click', e => { e.stopPropagation(); editCustomPropKey(p, row); });
    row.appendChild(editBtn);

    const canDelete = !p.createdBy || p.createdBy === state.me?.username || state.me?.role === 'admin';
    if (canDelete) {
      const delBtn = el('button', { class: 'cp-del-btn', title: 'Delete' }, [lucideIcon('trash-2')]);
      delBtn.addEventListener('click', e => { e.stopPropagation(); deleteCustomProp(p.id); });
      row.appendChild(delBtn);
    }
    return row;
  }

  function editCustomPropKey(p, row) {
    const keyEl = row.querySelector('.cp-key');
    if (!keyEl) return;
    const input = el('input', { class: 'cp-key-edit', type: 'text' });
    input.value = p.key;
    keyEl.replaceWith(input);
    input.focus();
    input.select();
    const commit = async () => {
      const newKey = input.value.trim();
      if (!newKey || newKey === p.key) { restoreKey(); return; }
      try {
        const r = await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/custom-props/' + encodeURIComponent(p.id), {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: newKey })
        });
        const j = await r.json();
        if (j.error) { toast(j.error, 'error'); restoreKey(); return; }
        p.key = j.prop.key;
        restoreKey();
      } catch { toast('Échec de la sauvegarde — vérifiez votre connexion', 'error'); restoreKey(); }
    };
    const restoreKey = () => {
      const fresh = el('div', { class: 'cp-key', text: p.key || '' });
      input.replaceWith(fresh);
    };
    input.addEventListener('blur', commit);
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
      if (e.key === 'Escape') { restoreKey(); }
    });
  }

  function editCustomPropValue(p, row) {
    const valEl = row.querySelector('.cp-value');
    if (!valEl) return;
    const textarea = el('textarea', { class: 'cp-value-edit', rows: '2' });
    textarea.value = p.value || '';
    valEl.replaceWith(textarea);
    textarea.focus();
    textarea.select();
    const commit = async () => {
      const newValue = textarea.value;
      if (newValue.trim() === (p.value || '').trim()) { restoreValue(); return; }
      try {
        const r = await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/custom-props/' + encodeURIComponent(p.id), {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: newValue })
        });
        const j = await r.json();
        if (j.error) { toast(j.error, 'error'); restoreValue(); return; }
        p.value = j.prop.value;
        restoreValue();
      } catch { toast('Échec de la sauvegarde — vérifiez votre connexion', 'error'); restoreValue(); }
    };
    const restoreValue = () => {
      const fresh = el('div', { class: 'cp-value', text: p.value || '—' });
      fresh.addEventListener('click', () => editCustomPropValue(p, row));
      textarea.replaceWith(fresh);
    };
    textarea.addEventListener('blur', commit);
    textarea.addEventListener('keydown', e => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); textarea.blur(); }
      if (e.key === 'Escape') { restoreValue(); }
    });
  }

  async function deleteCustomProp(propId) {
    if (!state.selectedChatId) return;
    if (!confirm('Delete this property?')) return;
    try {
      const r = await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/custom-props/' + encodeURIComponent(propId), { method: 'DELETE' });
      const j = await r.json();
      if (j.error) { toast(j.error, 'error'); return; }
      // Remove locally + re-render section
      const all = state.customProps[state.selectedChatId] || {};
      for (const sec of Object.keys(all)) {
        all[sec] = all[sec].filter(p => p.id !== propId);
      }
      // Reload profile sections to reflect changes
      const sectionsEl = document.getElementById('profileSections');
      if (sectionsEl) loadProfileData();
    } catch { toast('Échec de la suppression — réessayez', 'error'); }
  }

  // Add Property modal
  state.addPropTargetSection = 'general';
  state.propTemplates = [];

  async function loadPropTemplates() {
    try {
      const r = await api('/api/property-templates');
      const j = await r.json();
      state.propTemplates = j.templates || [];
    } catch { state.propTemplates = []; }
  }

  const HUBSPOT_PROP_LABELS = {
    firstname: 'First name', lastname: 'Last name', full_name: 'Full name',
    email: 'Email', phone: 'Phone', mobilephone: 'Mobile phone', whatsapp_phone: 'WhatsApp phone',
    lifecyclestage: 'Lifecycle', hs_lead_status: 'Lead status', createdate: 'Created',
    company: 'Company', jobtitle: 'Job title', address: 'Address', city: 'City', country: 'Country',
    state: 'State/Region', zip: 'Zip', website: 'Website', industry: 'Industry',
    native_language: 'Native language', class_type: 'Class type', goal: 'Goal',
    lead_source: 'Lead source', birthday: 'Birthday', age: 'Age', level: 'Level',
    hs_object_id: 'HubSpot ID', hs_lastmodifieddate: 'Last modified',
    subject: 'Ticket name', hs_pipeline_stage: 'Status', hs_ticket_priority: 'Priority',
    feedback_status: 'Feedback status', student_feedback: 'Student feedback',
    student_status: 'Student status', student_note: 'Student note',
    date_of_payment: 'Date of payment', date_of_first_session: 'Date of first session',
    new_level: 'New level', pack_hours: 'Program hours', amount: 'Amount',
    // Encaissement-form parity (2026-08-22) — the Student Info card mirrors the
    // "Confirmer l'encaissement" fields, so every one of them needs a label.
    paid_status: 'Paid status', mode_de_paiment: 'Mode de paiement',
    rest: 'Reste', date_of_rest: 'Date du reste',
    client_bank: 'Banque client', our_bank: 'Notre banque', sent_by: 'Envoyé par',
    note_payment: 'Note payment', confirmation_status: 'Confirmation status',
    pack: 'Program', pack_duration: 'Program duration',
    date_of_end: 'Date of end', groups: 'Groups',
  };
  function humanizeKey(k) {
    if (HUBSPOT_PROP_LABELS[k]) return HUBSPOT_PROP_LABELS[k];
    return String(k).replace(/^hs_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  // Skip clutter: long ids, system fields, things never useful as a profile pin
  const HUBSPOT_PROP_SKIP = new Set(['hs_object_id', 'hs_object_source', 'hs_object_source_id',
    'hs_object_source_label', 'createdate', 'hs_lastmodifieddate', 'lastmodifieddate',
    'hs_persona', 'hs_lead_status_internal']);

  state.addPropChips = [];        // all loaded chips for this contact
  state.addPropSelected = new Set(); // selected raw keys

  async function renderTemplateChips(section) {
    const container = document.getElementById('addPropTemplates');
    clear(container);
    state.addPropChips = [];
    state.addPropSelected = new Set();
    updateAddPropSaveLabel();
    if (!state.selectedChatId) return;

    container.appendChild(el('div', { class: 'empty-row', style: 'padding:8px;font-size:12px;', text: 'Loading HubSpot properties…' }));

    try {
      const mode = state.addPropContext?.mode;
      let url;
      if (mode === 'company') {
        url = '/api/hubspot/company-properties-full?companyId=' + encodeURIComponent(state.addPropContext.companyId);
      } else if (mode === 'ticket' || state.addPropTargetSection === 'tickets') {
        url = '/api/hubspot/ticket-properties-schema';
      } else {
        // Shared number: the values shown are the ACTIVE person's.
        url = '/api/hubspot/contact-properties-full?chatId=' + encodeURIComponent(_profilePersonChatId())
          + _activePersonQs(state.selectedChatId);
      }
      const r = await api(url);
      const data = await r.json();
      if (data?.properties?.length) {
        for (const p of data.properties) {
          if (HUBSPOT_PROP_SKIP.has(p.name)) continue;
          state.addPropChips.push({ key: p.label, value: p.value || '', raw: p.name, hasValue: !!p.hasValue });
        }
      }
    } catch { /* fall through */ }

    // Fallback: if schema endpoint fails (no token, etc.), pull populated props only via /api/contact.
    if (!state.addPropChips.length) {
      try {
        const r = await api('/api/contact?chatId=' + encodeURIComponent(_profilePersonChatId())
          + _activePersonQs(state.selectedChatId));
        const data = await r.json();
        const props = data?.contact?.properties || {};
        for (const [k, v] of Object.entries(props)) {
          if (HUBSPOT_PROP_SKIP.has(k)) continue;
          const value = v == null ? '' : String(v).trim();
          if (!value) continue;
          state.addPropChips.push({ key: humanizeKey(k), value, raw: k, hasValue: true });
        }
      } catch { /* leave empty */ }
    }

    updateAddPropSchemaCount();
    renderAddPropChipList();
  }

  function updateAddPropSchemaCount() {
    const subtitle = document.getElementById('addPropSubtitle');
    if (!subtitle) return;
    const total = state.addPropChips.length;
    const filled = state.addPropChips.filter(c => c.hasValue).length;
    subtitle.textContent = total
      ? `${total} propriétés HubSpot disponibles (${filled} déjà remplies). Coche celles à épingler.`
      : 'Aucune propriété HubSpot disponible pour ce contact.';
  }

  function renderAddPropChipList() {
    const container = document.getElementById('addPropTemplates');
    clear(container);
    if (!state.addPropChips.length) {
      container.appendChild(el('div', { class: 'empty-row', style: 'padding:6px 4px;font-size:12px;', text: 'Aucune propriété disponible.' }));
      return;
    }
    const q = (document.getElementById('addPropSearch')?.value || '').trim().toLowerCase();
    const onlyFilled = !!document.getElementById('addPropOnlyFilled')?.checked;
    let filtered = state.addPropChips;
    if (onlyFilled) filtered = filtered.filter(t => t.hasValue);
    if (q) filtered = filtered.filter(t =>
      t.key.toLowerCase().includes(q) ||
      (t.value || '').toLowerCase().includes(q) ||
      t.raw.toLowerCase().includes(q)
    );

    if (!filtered.length) {
      container.appendChild(el('div', { class: 'empty-row', style: 'padding:6px 4px;font-size:12px;', text: q ? 'Aucun résultat pour "' + q + '".' : 'Aucune propriété remplie.' }));
      return;
    }

    for (const t of filtered) {
      const isSelected = state.addPropSelected.has(t.raw);
      const value = t.value || '';
      const preview = value ? (value.length > 24 ? value.slice(0, 24) + '…' : value) : '(vide)';
      const chip = el('button', {
        class: 'add-prop-tpl' + (isSelected ? ' selected' : '') + (t.hasValue ? '' : ' empty-prop'),
        title: `${t.key}${value ? ': ' + value : ' (pas de valeur)'}`,
        type: 'button'
      }, [
        lucideIcon(isSelected ? 'check-circle-2' : 'sparkles'),
        document.createTextNode(' ' + t.key + ' '),
        el('span', { style: 'opacity:.65;font-weight:500;', text: '· ' + preview })
      ]);
      chip.addEventListener('click', () => {
        if (state.addPropSelected.has(t.raw)) state.addPropSelected.delete(t.raw);
        else state.addPropSelected.add(t.raw);
        renderAddPropChipList();
        updateAddPropSaveLabel();
      });
      container.appendChild(chip);
    }
    refreshIcons();
  }

  function updateAddPropSaveLabel() {
    const n = state.addPropSelected.size;
    const labelEl = document.getElementById('addPropSaveLabel');
    const counterEl = document.getElementById('addPropSelectedCount');
    if (labelEl) labelEl.textContent = n > 0 ? `Ajouter ${n} propriété${n > 1 ? 's' : ''}` : 'Save property';
    if (counterEl) {
      if (n > 0) {
        counterEl.classList.remove('hidden');
        counterEl.textContent = `${n} sélectionnée${n > 1 ? 's' : ''}`;
      } else {
        counterEl.classList.add('hidden');
      }
    }
  }

  function onAddPropSearchInput() {
    renderAddPropChipList();
  }

  function openAddPropModal(section, context) {
    if (!state.selectedChatId) { toast('Open a chat first', 'error'); return; }
    state.addPropTargetSection = section || 'general';
    state.addPropContext = context || null; // { mode: 'company', companyId: '...' } when from group profile
    const titleMap = {
      'student-info': 'Student Info', 'hubspot-company': 'HubSpot company',
      groups: 'Groups', tickets: 'Tickets',
      documents: 'Documents', activity: 'Activity', general: 'this chat'
    };
    document.getElementById('addPropTitle').textContent = 'Add property to ' + (titleMap[state.addPropTargetSection] || section);
    // Reflect the actual HubSpot object the chips come from. The picker loads
    // company props when context.mode === 'company' (group profiles), ticket
    // props when in the tickets section, contact props otherwise.
    const chipsLabel = document.getElementById('addPropChipsLabel');
    if (chipsLabel) {
      const mode = context?.mode;
      if (mode === 'company') chipsLabel.textContent = 'Propriétés HubSpot dyal had company';
      else if (mode === 'ticket' || state.addPropTargetSection === 'tickets') chipsLabel.textContent = 'Propriétés HubSpot dyal tickets';
      else chipsLabel.textContent = 'Propriétés HubSpot dyal had contact';
    }
    document.getElementById('addPropKey').value = '';
    document.getElementById('addPropValue').value = '';
    document.getElementById('addPropError').classList.add('hidden');
    const searchInput = document.getElementById('addPropSearch');
    if (searchInput) searchInput.value = '';
    const manualDetails = document.getElementById('addPropManualSection');
    if (manualDetails) manualDetails.open = false;
    state.addPropSelected = new Set();
    updateAddPropSaveLabel();
    renderTemplateChips(state.addPropTargetSection).catch(() => {});
    openModal('addPropModal');
    setTimeout(() => document.getElementById('addPropKey')?.focus(), 100);
  }

  async function saveAddProp() {
    const errEl = document.getElementById('addPropError');
    errEl.classList.add('hidden');
    const selected = [...state.addPropSelected];
    const manualKey = document.getElementById('addPropKey').value.trim();
    const manualValue = document.getElementById('addPropValue').value.trim();

    if (!selected.length && !manualKey) {
      errEl.textContent = 'Sélectionne au moins une propriété ou saisis un nom.';
      errEl.classList.remove('hidden');
      return;
    }

    // Section "tickets": pin HubSpot ticket fields (applies to all tickets of this contact)
    if (state.addPropTargetSection === 'tickets' && selected.length) {
      const labels = {};
      for (const raw of selected) {
        const chip = state.addPropChips.find(c => c.raw === raw);
        if (chip) labels[raw] = chip.key;
      }
      const current = getPinnedTicketFields(state.selectedChatId);
      const merged = [...current];
      for (const raw of selected) if (!merged.includes(raw)) merged.push(raw);
      await savePinnedTicketFields(state.selectedChatId, merged, labels);
      closeModal('addPropModal');
      toast(`${selected.length} propriété${selected.length > 1 ? 's ajoutées' : ' ajoutée'}`, 'success');
      loadProfileData();
      return;
    }

    // Section "hubspot-company": pin selected HubSpot company fields per
    // matched company. Manual key/value (if provided) also saves as a
    // free-form team-wide custom property under section 'hubspot-company'.
    if (state.addPropTargetSection === 'hubspot-company' && (selected.length || manualKey)) {
      const companyId = state.addPropContext?.companyId;
      if (selected.length && !companyId) {
        errEl.textContent = 'No HubSpot company linked.';
        errEl.classList.remove('hidden');
        return;
      }
      if (selected.length) {
        const labels = {};
        for (const raw of selected) {
          const chip = state.addPropChips.find(c => c.raw === raw);
          if (chip) labels[raw] = chip.key;
        }
        const current = getPinnedCompanyFields(companyId);
        const merged = [...current];
        for (const raw of selected) if (!merged.includes(raw)) merged.push(raw);
        await savePinnedCompanyFields(companyId, merged, labels);
      }
      let manualOk = 0, manualErr = '';
      if (manualKey) {
        try {
          const r = await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/custom-props', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ section: 'hubspot-company', key: manualKey, value: manualValue })
          });
          const j = await r.json();
          if (j.error) manualErr = j.error;
          else manualOk = 1;
        } catch (err) { manualErr = err.message || 'Save failed'; }
      }
      if (manualErr && !selected.length) {
        errEl.textContent = manualErr;
        errEl.classList.remove('hidden');
        return;
      }
      closeModal('addPropModal');
      const total = selected.length + manualOk;
      toast(`${total} propriété${total > 1 ? 's ajoutées' : ' ajoutée'}`, 'success');
      loadProfileData();
      return;
    }

    // Section "student-info": pin selected HubSpot fields as inline rows (NOT separate custom-props cards).
    // Other sections still use the legacy custom_props storage for free-text fields.
    if (state.addPropTargetSection === 'student-info' && selected.length) {
      const labels = {};
      for (const raw of selected) {
        const chip = state.addPropChips.find(c => c.raw === raw);
        if (chip) labels[raw] = chip.key;
      }
      const current = getPinnedFields(state.selectedChatId);
      const merged = [...current];
      for (const raw of selected) if (!merged.includes(raw)) merged.push(raw);
      await savePinnedFields(state.selectedChatId, merged, labels);
      // Manual entry still goes to custom_props (free-text fallback)
      let manualOk = 0, manualErr = '';
      if (manualKey) {
        try {
          const r = await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/custom-props', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ section: state.addPropTargetSection, key: manualKey, value: manualValue })
          });
          const j = await r.json();
          if (j.error) manualErr = j.error;
          else manualOk = 1;
        } catch (err) { manualErr = err.message || 'Save failed'; }
      }
      closeModal('addPropModal');
      const total = selected.length + manualOk;
      toast(`${total} propriété${total > 1 ? 's ajoutées' : ' ajoutée'}`, 'success');
      loadProfileData();
      return;
    }

    // Legacy path: other sections — keep custom_props rows
    const toSave = [];
    for (const raw of selected) {
      const chip = state.addPropChips.find(c => c.raw === raw);
      if (chip) toSave.push({ key: chip.key, value: chip.value });
    }
    if (manualKey) toSave.push({ key: manualKey, value: manualValue });

    let okCount = 0;
    let firstErr = '';
    for (const p of toSave) {
      try {
        const r = await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/custom-props', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ section: state.addPropTargetSection, key: p.key, value: p.value })
        });
        const j = await r.json();
        if (j.error) { if (!firstErr) firstErr = `${p.key}: ${j.error}`; continue; }
        const chatProps = state.customProps[state.selectedChatId] || {};
        if (!chatProps[state.addPropTargetSection]) chatProps[state.addPropTargetSection] = [];
        chatProps[state.addPropTargetSection].push(j.prop);
        state.customProps[state.selectedChatId] = chatProps;
        okCount++;
      } catch (err) {
        if (!firstErr) firstErr = err.message || 'Save failed';
      }
    }

    if (!okCount && firstErr) {
      errEl.textContent = firstErr;
      errEl.classList.remove('hidden');
      return;
    }
    closeModal('addPropModal');
    toast(`${okCount} propriété${okCount > 1 ? 's ajoutées' : ' ajoutée'}`, 'success');
    loadProfileData();
  }

  // Import all eligible HubSpot contact properties as custom props
  async function importFromHubspot() {
    if (!state.selectedChatId) return;
    if (!confirm('Import all HubSpot contact properties as custom fields?')) return;
    try {
      const r = await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/import-hubspot', { method: 'POST' });
      const j = await r.json();
      if (j.error) { toast(j.error, 'error'); return; }
      if (j.imported === 0) {
        toast('Nothing new to import — all HubSpot properties are already shown.', '');
      } else {
        toast(`Imported ${j.imported} HubSpot property(ies)`, 'success');
      }
      loadProfileData();
    } catch (err) {
      toast('Import failed: ' + (err.message || err), 'error');
    }
  }

  // ============ Search across all notes ============
  function openNoteSearchModal() {
    document.getElementById('noteSearchInput').value = '';
    const list = document.getElementById('noteSearchResults');
    clear(list);
    list.appendChild(el('div', { class: 'bulk-loading', style: 'padding:20px;', text: 'Type to search…' }));
    openModal('noteSearchModal');
    setTimeout(() => document.getElementById('noteSearchInput')?.focus(), 100);
  }

  let noteSearchDebounce = null;
  function onNoteSearchInput() {
    clearTimeout(noteSearchDebounce);
    noteSearchDebounce = setTimeout(runNoteSearch, 220);
  }

  async function runNoteSearch() {
    const q = (document.getElementById('noteSearchInput')?.value || '').trim();
    const list = document.getElementById('noteSearchResults');
    clear(list);
    if (q.length < 2) {
      list.appendChild(el('div', { class: 'bulk-loading', style: 'padding:20px;', text: 'Type at least 2 characters…' }));
      return;
    }
    list.appendChild(el('div', { class: 'bulk-loading', style: 'padding:20px;', text: 'Searching…' }));
    try {
      const r = await api('/api/notes/search?q=' + encodeURIComponent(q));
      const j = await r.json();
      const results = j.results || [];
      clear(list);
      if (!results.length) {
        list.appendChild(el('div', { class: 'bulk-loading', style: 'padding:20px;', text: 'No notes match.' }));
        return;
      }
      for (const r of results) list.appendChild(buildNoteSearchRow(r, q));
      refreshIcons();
    } catch {
      clear(list);
      list.appendChild(el('div', { class: 'bulk-loading', style: 'padding:20px;', text: 'Search failed.' }));
    }
  }

  function buildNoteSearchRow(r, q) {
    const row = el('div', { class: 'note-search-row' });
    const head = el('div', { class: 'nsr-head' }, [
      el('span', { class: 'nsr-chat', text: r.chatName || r.chatId }),
      el('span', { text: userLabel(r.author?.username, r.author?.displayName || r.author?.username || 'Unknown') }),
      el('span', { text: r.createdAt ? formatNoteTime(r.createdAt) : '' })
    ]);
    // Build content with highlighted match (safe DOM construction — no innerHTML)
    const content = el('div', { class: 'nsr-content' });
    const text = r.content || '';
    const lower = text.toLowerCase();
    const needle = (q || '').toLowerCase();
    let i = 0;
    while (i < text.length) {
      const found = needle ? lower.indexOf(needle, i) : -1;
      if (found < 0) {
        content.appendChild(document.createTextNode(text.slice(i)));
        break;
      }
      if (found > i) content.appendChild(document.createTextNode(text.slice(i, found)));
      content.appendChild(el('mark', { text: text.slice(found, found + needle.length) }));
      i = found + needle.length;
    }
    row.appendChild(head);
    row.appendChild(content);
    row.addEventListener('click', () => {
      closeModal('noteSearchModal');
      // Open the chat that contains this note
      if (state.selectedChatId !== r.chatId) {
        const existing = state.chats.find(c => c.id === r.chatId);
        if (existing) selectConversation(r.chatId);
        else {
          // Synthesize a minimal chat object so user can still navigate
          state.chats.unshift({ id: r.chatId, name: r.chatName, phone: '' });
          selectConversation(r.chatId);
        }
      }
      // Open conv-details on Notes tab
      E.main().classList.add('with-conv-details');
      E.main().classList.remove('with-profile');
      switchConvDetailsTab('notes');
    });
    return row;
  }

  // ============ Drag-reorder sections ============
  let dragSrcSection = null; // { id, chatId } — chatId guards against cross-chat drops if user navigates mid-drag

  function setupSectionDnd(card) {
    card.addEventListener('dragstart', e => {
      dragSrcSection = { id: card.getAttribute('data-section'), chatId: state.selectedChatId };
      card.classList.add('dragging');
      try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', dragSrcSection.id || ''); } catch { /* ignore */ }
    });
    card.addEventListener('dragend', () => {
      card.classList.remove('dragging');
      document.querySelectorAll('.profile-card.drop-target').forEach(x => x.classList.remove('drop-target'));
      dragSrcSection = null;
    });
    card.addEventListener('dragover', e => {
      if (!dragSrcSection || dragSrcSection.id === card.getAttribute('data-section')) return;
      e.preventDefault();
      card.classList.add('drop-target');
    });
    card.addEventListener('dragleave', () => card.classList.remove('drop-target'));
    card.addEventListener('drop', async e => {
      e.preventDefault();
      card.classList.remove('drop-target');
      const target = card.getAttribute('data-section');
      if (!dragSrcSection || dragSrcSection.id === target) return;
      // Abort if the user navigated to a different chat between dragstart and drop.
      if (dragSrcSection.chatId && dragSrcSection.chatId !== state.selectedChatId) {
        dragSrcSection = null;
        return;
      }
      const parent = document.getElementById('profileSections');
      const cards = Array.from(parent.querySelectorAll('.profile-card'));
      const srcEl = cards.find(c => c.getAttribute('data-section') === dragSrcSection.id);
      if (!srcEl) return;
      // Move srcEl before card
      parent.insertBefore(srcEl, card);
      const order = Array.from(parent.querySelectorAll('.profile-card')).map(c => c.getAttribute('data-section')).filter(Boolean);
      try {
        const r = await api('/api/me/section-order', {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order })
        });
        const j = await r.json();
        if (j.sectionOrder) {
          state.me = state.me || {};
          state.me.sectionOrder = j.sectionOrder;
          toast('Order saved', '');
        }
      } catch { toast('Save order failed', 'error'); }
    });
  }

  function buildAccordion(id, icon, title, openByDefault, fillFn, opts) {
    opts = opts || {};
    const card = el('section', { class: 'profile-card' + (openByDefault ? ' open' : ''), 'data-section': id });
    card.draggable = true;

    const dragHandle = el('span', { class: 'card-drag-handle', title: 'Drag to reorder' }, [lucideIcon('grip-vertical')]);
    const titleParts = [lucideIcon(icon), document.createTextNode(' ' + title)];
    if (typeof opts.count === 'number') {
      titleParts.push(el('span', { class: 'card-title-count', text: '(' + opts.count + ')' }));
    }
    const headChildren = [
      dragHandle,
      el('span', { class: 'card-title' }, titleParts)
    ];
    if (opts.showHubspotImport) {
      const hsBtn = el('button', { class: 'card-hs-import', title: 'Import all properties from HubSpot' }, [
        lucideIcon('download'), el('span', { text: 'Import HubSpot' })
      ]);
      hsBtn.addEventListener('click', e => { e.stopPropagation(); importFromHubspot(); });
      headChildren.push(hsBtn);
    }
    if (opts.showAddProp) {
      const addBtn = el('button', { class: 'card-add-prop', title: 'Add custom property' }, [
        lucideIcon('plus'), el('span', { text: 'Add property' })
      ]);
      addBtn.addEventListener('click', e => {
        e.stopPropagation();
        openAddPropModal(id, opts.addPropContext);
      });
      headChildren.push(addBtn);
    }
    if (opts.showCreateTicket) {
      const createBtn = el('button', { class: 'card-create-ticket', title: 'Create new ticket in HubSpot' }, [
        lucideIcon('ticket'), el('span', { text: 'Create ticket' })
      ]);
      createBtn.addEventListener('click', e => { e.stopPropagation(); openCreateTicketModal(); });
      headChildren.push(createBtn);
    }
    headChildren.push(el('button', { class: 'card-toggle', 'aria-label': 'Toggle' }, [lucideIcon('chevron-down')]));

    const head = el('header', { class: 'profile-card-head' }, headChildren);
    const body = el('div', { class: 'profile-card-body' });
    card.appendChild(head);
    card.appendChild(body);

    const wrappedFill = async (b) => {
      await Promise.resolve(fillFn(b));
      // Student Info section uses pinned-fields exclusively (no separate "Custom" violet card).
      if (id !== 'student-info') appendCustomPropsToSection(b, id);
    };

    const toggle = () => {
      const open = card.classList.toggle('open');
      if (open && body.dataset.filled !== '1') {
        clear(body);
        Promise.resolve(wrappedFill(body)).then(refreshIcons).catch(() => {});
        body.dataset.filled = '1';
      }
    };
    // Only the title area toggles; the add-prop button stops propagation
    head.addEventListener('click', toggle);
    if (openByDefault) {
      Promise.resolve(wrappedFill(body)).then(refreshIcons).catch(() => {});
      body.dataset.filled = '1';
    }
    setupSectionDnd(card);
    return card;
  }

  // Apply user's saved section order to a freshly-rendered profileSections element.
  // Sections not in the saved order are appended at the end in their default render order
  // (so newly-added sections from a future feature don't get hidden at the top).
  function applySavedSectionOrder() {
    const order = state.me?.sectionOrder;
    if (!Array.isArray(order) || !order.length) return;
    const parent = document.getElementById('profileSections');
    if (!parent) return;
    const cards = Array.from(parent.querySelectorAll('.profile-card'));
    const byId = new Map(cards.map(c => [c.getAttribute('data-section'), c]));
    const renderedIds = cards.map(c => c.getAttribute('data-section'));
    const desired = [...order, ...renderedIds.filter(id => id && !order.includes(id))];
    if (byId.has('classes-app') && !order.includes('classes-app')) {
      desired.splice(desired.indexOf('classes-app'), 1);
      const studentIndex = desired.indexOf('student-info');
      desired.splice(studentIndex < 0 ? 0 : studentIndex + 1, 0, 'classes-app');
    }
    for (const sectionId of desired) {
      const card = byId.get(sectionId);
      if (card) parent.appendChild(card); // moves card to current end → ends in `desired` order
    }
  }

  function addField(parent, key, val) {
    const v = (val == null || val === '') ? '' : String(val);
    parent.appendChild(el('div', { class: 'field-row' }, [
      el('span', { class: 'field-key', text: key }),
      el('span', { class: 'field-val ' + (v ? '' : 'empty'), text: v || '—' })
    ]));
  }

  // Editable group field — click-to-edit Name / Description on the Group
  // info accordion. Posts to /api/groups/:id/{kind}, then patches the
  // local chat list so the new value sticks without a full reload.
  function addEditableGroupField(parent, key, val, groupId, kind, opts = {}) {
    const v = (val == null || val === '') ? '' : String(val);
    const row = el('div', { class: 'field-row field-row-editable', style: 'cursor:pointer;' });
    const keyEl = el('span', { class: 'field-key', text: key });
    const valEl = el('span', { class: 'field-val ' + (v ? '' : 'empty'), text: v || '—' });
    valEl.title = 'Click to edit';
    row.appendChild(keyEl);
    row.appendChild(valEl);
    parent.appendChild(row);

    const startEdit = () => {
      if (row.classList.contains('editing')) return;
      row.classList.add('editing');
      row.style.flexDirection = 'column';
      row.style.alignItems = 'stretch';
      row.style.cursor = 'default';
      const input = opts.multiline
        ? el('textarea', { class: 'input', rows: '4', style: 'width:100%;resize:vertical;font-size:13px;' })
        : el('input', { class: 'input', type: 'text', style: 'width:100%;font-size:13px;' });
      input.value = v;
      const actions = el('div', { style: 'display:flex;gap:6px;margin-top:6px;' });
      const saveBtn = el('button', { class: 'btn btn-primary btn-sm', text: 'Save' });
      const cancelBtn = el('button', { class: 'btn btn-ghost btn-sm', text: 'Cancel' });
      actions.appendChild(saveBtn);
      actions.appendChild(cancelBtn);
      // Replace the value span with the editor
      valEl.style.display = 'none';
      keyEl.style.display = 'none';
      row.appendChild(input);
      row.appendChild(actions);
      input.focus();
      if (input.select) input.select();

      const cleanup = () => {
        row.classList.remove('editing');
        row.style.flexDirection = '';
        row.style.alignItems = '';
        row.style.cursor = 'pointer';
        valEl.style.display = '';
        keyEl.style.display = '';
        if (input.parentNode === row) row.removeChild(input);
        if (actions.parentNode === row) row.removeChild(actions);
      };

      cancelBtn.addEventListener('click', cleanup);
      saveBtn.addEventListener('click', async () => {
        const newVal = input.value.trim();
        if (kind === 'subject' && !newVal) { toast('Name required', 'error'); return; }
        saveBtn.disabled = true; saveBtn.textContent = 'Saving…';
        try {
          const body = kind === 'subject' ? { subject: newVal } : { description: input.value };
          const r = await api('/api/groups/' + encodeURIComponent(groupId) + '/' + kind, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          });
          const j = await r.json();
          if (j.error) {
            // Cleanup back to read-only state showing the OLD value, otherwise
            // the failed input lingers and looks like the change saved (the
            // server didn't apply it but the user still sees their typed
            // value sitting in the editor). cleanup() restores valEl which
            // still holds `v`, the pre-edit value.
            toast(j.error, 'error');
            cleanup();
            return;
          }
          // Patch local chat name + state.selectedChat for instant UI sync.
          if (kind === 'subject') {
            const chat = (state.chats || []).find(c => c.id === groupId);
            if (chat) chat.name = newVal;
            const headerName = document.getElementById('chatName');
            if (headerName && state.selectedChatId === groupId) headerName.textContent = newVal;
            renderConversations();
          }
          // Re-render the profile so the Group info card shows the new value.
          loadProfileData();
          toast('Group ' + key.toLowerCase() + ' updated', 'success');
        } catch (err) {
          toast('Update failed: ' + (err.message || err), 'error');
          saveBtn.disabled = false; saveBtn.textContent = 'Save';
        }
      });
    };

    valEl.addEventListener('click', startEdit);
    keyEl.addEventListener('click', startEdit);
  }

  // ===== Pinned-fields preference (per-chat list of HubSpot property names shown in Student Info) =====
  // Lougha's preferred default pin set for Student Info — the 10 properties
  // the team most often needs to see at a glance on every contact. Sourced
  // from the Tickets-table column ask on 2026-05-13 (Ayoub's screenshots).
  // Internal HubSpot names verified via /api/hubspot/contact-properties-full.
  // Note: "Program Hours" is `pack_hours` in HubSpot, not program_hours.
  const DEFAULT_PINNED_FIELDS = [
    'student_status', 'student_note', 'date_of_payment', 'date_of_first_session',
    'level', 'new_level', 'pack_hours', 'phone', 'age', 'amount',
  ];
  // ===== Encaissement parity block (2026-08-22) =====
  // Ayoub: everything the "Confirmer l'encaissement" form collects has to stay
  // visible on the contact too — "dima 3andhom client dyalhom" — and be
  // editable from there. These rows are FIXED: unlike the pinned list below
  // they can't be removed per-chat, and they render in the same order and
  // grouping as the modal so the team reads one layout, not two.
  const ENCAISSEMENT_CARD_GROUPS = [
    ['Paiement', ['amount', 'date_of_payment', 'paid_status', 'mode_de_paiment', 'rest',
      'date_of_rest', 'client_bank', 'our_bank', 'sent_by', 'confirmation_status', 'note_payment']],
    ['Client', ['firstname', 'lastname', 'email', 'age', 'city']],
    ['Programme', ['pack', 'pack_duration', 'pack_hours', 'level']],
    ['Étudiant', ['date_of_first_session', 'date_of_end', 'student_status', 'student_note', 'groups']],
  ];
  const ENCAISSEMENT_CARD_FIELDS = ENCAISSEMENT_CARD_GROUPS.flatMap(([, fields]) => fields);
  const ENCAISSEMENT_DATE_FIELDS = new Set(['date_of_payment', 'date_of_rest', 'date_of_first_session', 'date_of_end']);
  // ===== Payment methods (2026-10-17) =====
  // ONE list for every form that records how a client paid — the Paid modal,
  // this card, the wallet "Ajouter un paiement" form and administration's
  // add-request queue. /api/me serves it from lib/payments/payment-modes.js;
  // this copy only covers a tab that has not heard from /api/me yet, and
  // test/payment-mode-required.test.js fails the build if it drifts.
  const PAYMENT_MODES_FALLBACK = [
    { value: 'Espese', label: 'Espèce', ledger: 'cash', hubspot: 'Espese' },
    { value: 'virement', label: 'Virement', ledger: 'bank', hubspot: 'virement' },
    { value: 'versement', label: 'Versement', ledger: 'bank', hubspot: 'versement' },
    { value: 'stripe', label: 'Stripe', ledger: 'stripe', hubspot: null },
    { value: 'whop', label: 'Whop', ledger: 'whop', hubspot: null },
    { value: 'wafacash', label: 'Wafacash', ledger: 'cash', hubspot: null },
    { value: 'cashplus', label: 'CashPlus', ledger: 'cash', hubspot: null },
  ];
  function paymentModes() {
    return (Array.isArray(state.paymentModes) && state.paymentModes.length) ? state.paymentModes : PAYMENT_MODES_FALLBACK;
  }
  // The required method <select> of the wallet forms (one method per payment).
  function paymentModeSelect(selected, extraClass) {
    const sel = el('select', { class: 'input' + (extraClass ? ' ' + extraClass : ''), 'aria-label': 'Mode de paiement' });
    sel.appendChild(el('option', { value: '', text: 'Mode de paiement…' }));
    for (const m of paymentModes()) sel.appendChild(el('option', { value: m.value, text: m.label }));
    const want = String(selected || '').split(';')[0].trim().toLowerCase();
    const hit = paymentModes().find(m => m.value.toLowerCase() === want);
    sel.value = hit ? hit.value : '';
    return sel;
  }
  // This card writes the HubSpot contact property straight away, and HubSpot
  // only has options for the historical methods — so it offers those only.
  // [value, label] pairs, the shape the chips below already used.
  function encaissementModes() {
    return paymentModes().filter(m => m.hubspot === m.value).map(m => [m.value, m.label]);
  }

  state.pinnedFieldsByChat = state.pinnedFieldsByChat || {};
  state.addPropChipsLabels = state.addPropChipsLabels || {}; // raw-name -> label cache

  function getPinnedFields(chatId) {
    if (!chatId) return [...DEFAULT_PINNED_FIELDS];
    const cached = state.pinnedFieldsByChat[chatId];
    if (Array.isArray(cached) && cached.length) return cached;
    return [...DEFAULT_PINNED_FIELDS];
  }

  async function loadPinnedFields(chatId) {
    if (!chatId) return [...DEFAULT_PINNED_FIELDS];
    try {
      const r = await api('/api/preferences?key=' + encodeURIComponent('pinned-fields:' + chatId));
      const j = await r.json();
      const list = Array.isArray(j?.prefs?.fields) ? j.prefs.fields : null;
      if (list && list.length) {
        state.pinnedFieldsByChat[chatId] = list;
        if (j.prefs.labels) state.addPropChipsLabels = { ...state.addPropChipsLabels, ...j.prefs.labels };
        return list;
      }
    } catch { /* fallback to default */ }
    state.pinnedFieldsByChat[chatId] = [...DEFAULT_PINNED_FIELDS];
    return state.pinnedFieldsByChat[chatId];
  }

  async function savePinnedFields(chatId, fields, labels) {
    if (!chatId) return;
    state.pinnedFieldsByChat[chatId] = fields;
    if (labels) state.addPropChipsLabels = { ...state.addPropChipsLabels, ...labels };
    try {
      await api('/api/preferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: 'pinned-fields:' + chatId, prefs: { fields, labels: state.addPropChipsLabels } })
      });
    } catch { /* best effort */ }
  }

  function addCompanyPinnedFieldRow(parent, label, value, fieldName, companyId) {
    const v = (value == null || value === '') ? '' : String(value);
    const row = el('div', { class: 'field-row pinned-field-row', 'data-field-name': fieldName, draggable: 'true' });
    const handle = el('span', { class: 'pinned-field-drag', title: 'Drag to reorder' }, [lucideIcon('grip-vertical')]);
    row.appendChild(handle);
    row.appendChild(el('span', { class: 'field-key', text: label }));
    row.appendChild(el('span', { class: 'field-val ' + (v ? '' : 'empty'), text: v || '—' }));
    const removeBtn = el('button', {
      class: 'pinned-field-remove',
      title: 'Retirer de HubSpot company',
      type: 'button'
    });
    removeBtn.appendChild(lucideIcon('x'));
    removeBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const current = getPinnedCompanyFields(companyId).filter(f => f !== fieldName);
      await savePinnedCompanyFields(companyId, current);
      loadProfileData();
    });
    row.appendChild(removeBtn);
    parent.appendChild(row);
    refreshIcons();
  }

  function addPinnedFieldRow(parent, label, value, fieldName) {
    const v = (value == null || value === '') ? '' : String(value);
    const row = el('div', { class: 'field-row pinned-field-row', 'data-field-name': fieldName, draggable: 'true' });
    const handle = el('span', { class: 'pinned-field-drag', title: 'Drag to reorder' }, [lucideIcon('grip-vertical')]);
    row.appendChild(handle);
    row.appendChild(el('span', { class: 'field-key', text: label }));
    row.appendChild(el('span', { class: 'field-val ' + (v ? '' : 'empty'), text: v || '—' }));
    const removeBtn = el('button', {
      class: 'pinned-field-remove',
      title: 'Retirer de Student Info',
      type: 'button'
    });
    removeBtn.appendChild(lucideIcon('x'));
    removeBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const current = getPinnedFields(state.selectedChatId).filter(f => f !== fieldName);
      await savePinnedFields(state.selectedChatId, current);
      loadProfileData();
    });
    row.appendChild(removeBtn);
    parent.appendChild(row);
    refreshIcons();
  }

  // One click-to-edit row of the encaissement block. The editor shape follows
  // the property: a date input for the date props, chips for the multi-checkbox
  // `mode_de_paiment`, a <select> whenever the payment schema knows the option
  // list (so we can never PATCH an off-list enum value), free text otherwise.
  // Saving reuses PATCH /api/hubspot/contacts/:id — already role-gated, already
  // mirrors back and already syncs payment edits into the lead_payments ledger.
  function addEncaissementFieldRow(parent, label, value, fieldName, contactId) {
    const v = (value == null || value === '') ? '' : String(value);
    const row = el('div', { class: 'field-row enc-field-row', 'data-field-name': fieldName });
    const keyEl = el('span', { class: 'field-key', text: label });
    const valEl = el('span', { class: 'field-val ' + (v ? '' : 'empty'), text: v || '—' });
    row.appendChild(keyEl);
    row.appendChild(valEl);
    parent.appendChild(row);
    if (!contactId) return;                       // no HubSpot contact → read-only
    // `level` starts as the teacher's placement-test level but the whole team
    // may correct it (Ayoub, 2026-09-10) — same rule as the fiche / Paid form.
    row.classList.add('enc-field-editable');
    valEl.title = 'Cliquer pour modifier';

    const startEdit = async () => {
      if (row.classList.contains('editing')) return;
      row.classList.add('editing');
      keyEl.style.display = 'none';
      valEl.style.display = 'none';

      const editor = el('div', { class: 'enc-field-editor' });
      editor.appendChild(el('label', { class: 'enc-field-editor-key', text: label }));
      let readValue;
      if (fieldName === 'mode_de_paiment') {
        const picked = new Set(v.split(';').map(s => s.trim()).filter(Boolean));
        const chips = el('div', { class: 'paid-modes' });
        const boxes = encaissementModes().map(([val, lbl]) => {
          const box = el('input', { type: 'checkbox' });
          box.checked = picked.has(val);
          box.dataset.modeVal = val;
          chips.appendChild(el('label', { class: 'paid-mode-chip' }, [box, el('span', { text: lbl })]));
          return box;
        });
        editor.appendChild(chips);
        readValue = () => boxes.filter(b => b.checked).map(b => b.dataset.modeVal).join(';');
      } else if (ENCAISSEMENT_DATE_FIELDS.has(fieldName)) {
        const input = el('input', { class: 'input', type: 'date' });
        input.value = _paidHsDate(v);
        editor.appendChild(input);
        readValue = () => input.value;
      } else {
        // Ask the payment schema for this property's options (cached per session).
        let opts = null;
        try {
          const schema = await _paidLoadPaymentSchema();
          opts = schema[fieldName] || null;
        } catch (_) { /* fall through to a text input */ }
        if (opts && opts.length) {
          const sel = el('select', { class: 'input' });
          sel.appendChild(el('option', { value: '', text: '— aucun —' }));
          for (const o of opts) sel.appendChild(el('option', { value: o.value, text: o.label }));
          // Keep a value HubSpot already holds but that isn't on the option
          // list — otherwise opening the editor would silently blank it.
          if (v && !opts.some(o => o.value === v)) sel.appendChild(el('option', { value: v, text: v }));
          sel.value = v;
          editor.appendChild(sel);
          readValue = () => sel.value;
        } else {
          const long = fieldName === 'student_note' || fieldName === 'note_payment';
          const input = long
            ? el('textarea', { class: 'input', rows: '3' })
            : el('input', { class: 'input', type: fieldName === 'age' ? 'number' : 'text' });
          input.value = v;
          editor.appendChild(input);
          readValue = () => String(input.value).trim();
        }
      }
      const saveBtn = el('button', { class: 'btn btn-primary btn-sm', type: 'button', text: 'Save' });
      const cancelBtn = el('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: 'Annuler' });
      editor.appendChild(el('div', { class: 'enc-field-actions' }, [saveBtn, cancelBtn]));
      row.appendChild(editor);
      editor.querySelector('input,select,textarea')?.focus();

      const cleanup = () => {
        row.classList.remove('editing');
        keyEl.style.display = '';
        valEl.style.display = '';
        if (editor.parentNode === row) row.removeChild(editor);
      };
      cancelBtn.addEventListener('click', cleanup);
      saveBtn.addEventListener('click', async () => {
        const next = readValue();
        if (next === v) { cleanup(); return; }
        saveBtn.disabled = true; saveBtn.textContent = 'Saving…';
        try {
          const r = await api('/api/hubspot/contacts/' + encodeURIComponent(contactId), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ properties: { [fieldName]: next } }),
          });
          const j = await r.json().catch(() => ({}));
          if (!r.ok || j.error) {
            // Leave the OLD value on screen — a failed PATCH that still showed
            // the typed value would read as saved.
            toast(j.error || ('Échec de la mise à jour (' + r.status + ')'), 'error');
            cleanup();
            return;
          }
          toast(label + ' mis à jour', 'success');
          loadProfileData();
        } catch (err) {
          toast('Échec: ' + (err?.message || err), 'error');
          saveBtn.disabled = false; saveBtn.textContent = 'Save';
        }
      });
    };
    valEl.addEventListener('click', startEdit);
    keyEl.addEventListener('click', startEdit);
  }

  // Drag-to-reorder pinned property rows. `getFields` returns current array,
  // `saveFields(newOrder)` persists; called after each successful drop.
  function setupPinnedFieldsDnd(container, getFields, saveFields) {
    if (!container || container.dataset.pinnedDnd === '1') return;
    container.dataset.pinnedDnd = '1';
    let srcName = null;
    container.addEventListener('dragstart', e => {
      const row = e.target.closest('.pinned-field-row');
      if (!row || !container.contains(row)) return;
      // Prevent the parent profile-card section drag from also activating
      e.stopPropagation();
      srcName = row.dataset.fieldName;
      row.classList.add('dragging');
      try { e.dataTransfer.setData('text/plain', srcName); e.dataTransfer.effectAllowed = 'move'; } catch {}
    });
    container.addEventListener('dragend', () => {
      container.querySelectorAll('.pinned-field-row.dragging').forEach(r => r.classList.remove('dragging'));
      container.querySelectorAll('.pinned-field-row.drop-above,.pinned-field-row.drop-below').forEach(r => r.classList.remove('drop-above', 'drop-below'));
      srcName = null;
    });
    container.addEventListener('dragover', e => {
      const row = e.target.closest('.pinned-field-row');
      if (!row || !container.contains(row) || row.dataset.fieldName === srcName) return;
      e.preventDefault();
      try { e.dataTransfer.dropEffect = 'move'; } catch {}
      const r = row.getBoundingClientRect();
      const above = e.clientY < r.top + r.height / 2;
      container.querySelectorAll('.pinned-field-row.drop-above,.pinned-field-row.drop-below').forEach(x => x.classList.remove('drop-above', 'drop-below'));
      row.classList.add(above ? 'drop-above' : 'drop-below');
    });
    container.addEventListener('drop', async e => {
      e.preventDefault();
      const row = e.target.closest('.pinned-field-row');
      if (!row || !srcName || !container.contains(row)) return;
      const dstName = row.dataset.fieldName;
      if (dstName === srcName) return;
      const r = row.getBoundingClientRect();
      const above = e.clientY < r.top + r.height / 2;
      const fields = getFields().filter(f => f !== srcName);
      const insertAt = fields.indexOf(dstName) + (above ? 0 : 1);
      fields.splice(insertAt, 0, srcName);
      await saveFields(fields);
      loadProfileData();
    });
  }

  // ============ AI summary ============
  async function loadAiSummaryCache() {
    if (!state.selectedChatId) return;
    try {
      const r = await api('/api/ai/summary?chatId=' + encodeURIComponent(state.selectedChatId));
      const j = await r.json();
      if (j.summary?.summary) renderAiSummary(j.summary.summary, j.summary.generatedAt);
    } catch { /* ignore */ }
  }

  async function generateAiSummary(force) {
    const body = document.getElementById('aiSummaryBody');
    const btn = document.getElementById('aiSummaryRefreshBtn');
    if (!state.selectedChatId) return;
    clear(body);
    body.classList.add('loading');
    body.appendChild(el('span', { class: 'spinner-sm' }));
    body.appendChild(el('span', { text: 'Generating…' }));
    btn.disabled = true;
    try {
      const r = await api('/api/ai/summarize', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: state.selectedChatId, force: !!force }),
        timeoutMs: 120000, // Gemini over a long conversation can outlive the 60s default
      });
      const j = await r.json();
      body.classList.remove('loading');
      if (j.error) {
        clear(body);
        body.appendChild(el('p', { class: 'muted small',
          text: j.error.includes('GEMINI') ? 'GEMINI_API_KEY not configured. Add it to .env to enable.' : j.error }));
        return;
      }
      renderAiSummary(j.summary, new Date().toISOString());
    } catch {
      body.classList.remove('loading');
      clear(body);
      body.appendChild(el('p', { class: 'muted small', text: 'AI request failed.' }));
    } finally {
      btn.disabled = false;
    }
  }

  function renderAiSummary(text, generatedAt) {
    const body = document.getElementById('aiSummaryBody');
    clear(body);
    body.classList.remove('loading');
    body.appendChild(el('div', { text }));
    state.aiSummaryText = text || '';
    const tEl = document.getElementById('aiGeneratedAt');
    if (tEl) tEl.textContent = generatedAt ? formatNoteTime(generatedAt) : '—';
  }

  // ============ AI feedback (thumbs up / down) ============
  async function loadAiFeedback() {
    if (!state.selectedChatId) return;
    try {
      const r = await api('/api/ai/feedback?chatId=' + encodeURIComponent(state.selectedChatId));
      const j = await r.json();
      const value = j.feedback?.value || null;
      document.getElementById('aiThumbUpBtn')?.classList.toggle('up-active', value === 'up');
      document.getElementById('aiThumbDownBtn')?.classList.toggle('down-active', value === 'down');
    } catch { /* ignore */ }
  }

  async function submitAiFeedback(value) {
    if (!state.selectedChatId) { toast('Open a chat first', 'error'); return; }
    // Toggle off if same button clicked twice
    const upActive = document.getElementById('aiThumbUpBtn')?.classList.contains('up-active');
    const downActive = document.getElementById('aiThumbDownBtn')?.classList.contains('down-active');
    let newValue = value;
    if ((value === 'up' && upActive) || (value === 'down' && downActive)) newValue = null;
    try {
      const r = await api('/api/ai/feedback', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: state.selectedChatId, value: newValue })
      });
      await r.json();
      document.getElementById('aiThumbUpBtn')?.classList.toggle('up-active', newValue === 'up');
      document.getElementById('aiThumbDownBtn')?.classList.toggle('down-active', newValue === 'down');
      if (newValue) toast(newValue === 'up' ? 'Marked as helpful' : 'Marked as not helpful', '');
    } catch {
      toast('Feedback failed', 'error');
    }
  }

  function copyAiSummary() {
    const text = state.aiSummaryText || '';
    if (!text) { toast('Generate a summary first', 'error'); return; }
    navigator.clipboard?.writeText(text).then(
      () => toast('Summary copied', 'success'),
      () => toast('Copy failed', 'error')
    );
  }

  // ============ Ask AI (modal) ============
  function openAskAiModal() {
    if (!state.selectedChatId) { toast('Open a chat first', 'error'); return; }
    document.getElementById('askAiInput').value = '';
    const ans = document.getElementById('askAiAnswer');
    if (ans) { ans.textContent = ''; ans.classList.add('hidden'); }
    openModal('askAiModal');
    setTimeout(() => document.getElementById('askAiInput')?.focus(), 100);
  }

  async function submitAskAi() {
    if (!state.selectedChatId) return;
    const input = document.getElementById('askAiInput');
    const question = (input?.value || '').trim();
    if (!question) { toast('Type a question first', 'error'); return; }
    const ans = document.getElementById('askAiAnswer');
    const submitBtn = document.getElementById('askAiSubmitBtn');
    if (ans) {
      clear(ans);
      ans.appendChild(el('span', { class: 'spinner-sm' }));
      ans.appendChild(el('span', { text: ' Thinking…' }));
      ans.classList.remove('hidden');
    }
    if (submitBtn) submitBtn.disabled = true;
    try {
      const r = await api('/api/ai/ask', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: state.selectedChatId, question }),
        timeoutMs: 120000, // Gemini over a long conversation can outlive the 60s default
      });
      const j = await r.json();
      if (j.error) {
        if (ans) { clear(ans); ans.appendChild(el('span', { text: 'Error: ' + j.error })); }
        return;
      }
      if (ans) { clear(ans); ans.appendChild(el('div', { text: j.answer || '' })); }
    } catch (err) {
      if (ans) { clear(ans); ans.appendChild(el('span', { text: 'Failed: ' + (err.message || err) })); }
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  // ============ Recruitment lane (this chat is a job candidate) ============
  // One click moves a conversation out of sales entirely. Used for candidates
  // who write to the number instead of filling the application form — without
  // it they become a lead, land in an agent's list and get a course pitch.
  const _recruitmentLaneChats = new Set();

  function _laneKeyForChat(chatId) {
    const digits = String(chatId || '').split('@')[0].replace(/\D/g, '');
    return digits.length >= 9 ? digits.slice(-9) : '';
  }

  async function refreshRecruitmentLaneBtn() {
    const button = document.getElementById('recruitmentLaneBtn');
    if (!button || !state.selectedChatId) return;
    const key = _laneKeyForChat(state.selectedChatId);
    if (!key) { button.classList.add('hidden'); return; }
    button.classList.remove('hidden');
    if (!_recruitmentLaneChats.size) {
      try {
        const response = await api('/api/recruitment/admin/lane');
        if (response.ok) {
          const payload = await response.json().catch(() => ({}));
          for (const entry of payload.entries || []) _recruitmentLaneChats.add(entry.key);
        }
      } catch { /* the button still works; it just starts unlit */ }
    }
    _paintRecruitmentLaneBtn(_recruitmentLaneChats.has(key));
  }

  function _paintRecruitmentLaneBtn(inLane) {
    const button = document.getElementById('recruitmentLaneBtn');
    if (!button) return;
    button.classList.toggle('lane-on', inLane);
    button.dataset.tip = inLane ? 'Job candidate — outside sales' : 'This is a job candidate';
  }

  async function toggleRecruitmentLane() {
    if (!state.selectedChatId) return;
    const key = _laneKeyForChat(state.selectedChatId);
    if (!key) return;
    const inLane = _recruitmentLaneChats.has(key);
    if (!inLane && !confirm('Marquer cette conversation comme candidature ?\n\nElle sort des ventes : aucun lead, aucun agent assigné, absente des chiffres. Le bot de recrutement répond à sa place.')) return;
    try {
      const response = inLane
        ? await api(`/api/recruitment/admin/lane/${encodeURIComponent(key)}`, { method: 'DELETE' })
        : await api('/api/recruitment/admin/lane', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chatId: state.selectedChatId, name: state.chats?.find(c => c.id === state.selectedChatId)?.name || '' }),
        });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) return toast(payload.error || 'Could not update this conversation', 'error');
      if (inLane) _recruitmentLaneChats.delete(key); else _recruitmentLaneChats.add(key);
      _paintRecruitmentLaneBtn(!inLane);
      const moved = Number(payload.movedLeads || 0);
      toast(inLane
        ? 'Conversation back in sales'
        : `Marked as job candidate${moved ? ` · ${moved} lead${moved > 1 ? 's' : ''} moved out of the funnel` : ''}`, 'success');
    } catch (error) {
      toast(error.message || 'Could not update this conversation', 'error');
    }
  }

  // ============ Influencer lane (Inbox ▸ Influencer, 2026-11-02) ============
  // ⭐ in the chat header marks an influencer / affiliate partner; the card
  // under the header shows the partner account, the step in the programme,
  // links, requests, commissions and the bot switch. Admin roles only (same
  // gate as Recrutement — the server 403s every /api/influencer/* for others).
  const INFLUENCER_STAGES = [['prospect', 'Prospect'], ['contacted', 'Contacted'], ['interested', 'Interested'], ['not_interested', 'Not interested']];
  const _influencer = { chatId: null, data: null, open: false, searchTimer: null, seq: 0 };

  function _influencerAllowed() {
    return document.body.classList.contains('role-system-admin') || document.body.classList.contains('role-sales-admin');
  }

  async function refreshInfluencerLane() {
    const button = document.getElementById('influencerLaneBtn');
    const strip = document.getElementById('influencerChatStrip');
    const chatId = state.selectedChatId;
    if (!button || !strip) return;
    const chat = (state.chats || []).find(c => c.id === chatId);
    const eligible = !!chatId && _influencerAllowed() && !(chat && chat.isGroup)
      && (/@s\.whatsapp\.net$/.test(chatId) || /^(ig|fbm):/.test(chatId));
    if (!eligible) { button.classList.add('hidden'); strip.classList.add('hidden'); strip.replaceChildren(); return; }
    button.classList.remove('hidden');
    if (_influencer.chatId !== chatId) { _influencer.open = false; _influencer.data = null; }
    _influencer.chatId = chatId;
    const seq = ++_influencer.seq;
    try {
      const response = await api('/api/influencer/card?chatId=' + encodeURIComponent(chatId));
      const payload = await response.json().catch(() => ({}));
      if (seq !== _influencer.seq || state.selectedChatId !== chatId) return;
      _influencer.data = response.ok ? payload : { inLane: false };
    } catch { if (seq === _influencer.seq) _influencer.data = { inLane: false }; }
    _paintInfluencer();
  }

  function _paintInfluencer() {
    const button = document.getElementById('influencerLaneBtn');
    const inLane = !!(_influencer.data && _influencer.data.inLane);
    if (button) {
      button.classList.toggle('lane-on', inLane);
      button.dataset.tip = inLane ? 'Influencer — partner card' : 'This is an influencer';
    }
    renderInfluencerStrip();
  }

  async function toggleInfluencerLane() {
    const chatId = state.selectedChatId;
    if (!chatId) return;
    if (_influencer.data && _influencer.data.inLane) { _influencer.open = !_influencer.open; renderInfluencerStrip(); return; }
    if (!confirm('Mark this conversation as an influencer / partner?\n\nIt leaves sales: no lead, no agent, out of the figures. Only admins see it, in Inbox ▸ Influencer.')) return;
    const chat = (state.chats || []).find(c => c.id === chatId);
    const ok = await _saveInfluencer({ name: chat?.name || '', stage: 'contacted' }, 'Added to Influencer');
    if (ok) { _influencer.open = true; renderInfluencerStrip(); }
  }

  async function _saveInfluencer(patch, okText) {
    const chatId = state.selectedChatId;
    try {
      const response = await api('/api/influencer/lane', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, ...patch }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) { toast(payload.error || 'Could not save', 'error'); return false; }
      if (okText) toast(okText, 'success');
      const chat = (state.chats || []).find(c => c.id === chatId);
      if (chat) chat.influencerLane = true;
      await refreshInfluencerLane();
      return true;
    } catch (e) { toast(e.message || 'Could not save', 'error'); return false; }
  }

  async function _removeInfluencer() {
    const chatId = state.selectedChatId;
    if (!chatId || !confirm('Remove this conversation from Influencer?')) return;
    try {
      const response = await api('/api/influencer/lane?chatId=' + encodeURIComponent(chatId), { method: 'DELETE' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) return toast(payload.error || 'Could not remove', 'error');
      const chat = (state.chats || []).find(c => c.id === chatId);
      if (chat) delete chat.influencerLane;
      toast('Removed from Influencer', 'success');
      _influencer.open = false;
      await refreshInfluencerLane();
    } catch (e) { toast(e.message || 'Could not remove', 'error'); }
  }

  function renderInfluencerStrip() {
    const strip = document.getElementById('influencerChatStrip');
    if (!strip) return;
    const data = _influencer.data;
    if (!data || !data.inLane || state.activeView !== 'inbox') { strip.classList.add('hidden'); strip.replaceChildren(); return; }
    const card = data.card || null;
    const lane = (card && card.lane) || data.entry || {};
    const partner = card && card.partner;
    strip.className = 'influencer-strip' + (card && card.blocked ? ' is-alert' : '') + (lane.botPaused ? ' is-paused' : '');
    const title = partner ? (partner.name || partner.email) : (lane.name || lane.handle || 'Influencer');
    const detail = !card ? 'Partner data is not available right now.' : (card.nextAction || '');
    const head = el('div', { class: 'infl-head' }, [
      el('span', { class: 'infl-avatar', 'aria-hidden': 'true' }, [lucideIcon('star')]),
      el('div', { class: 'infl-copy' }, [
        el('span', { class: 'infl-eyebrow', text: partner ? 'INFLUENCER · PARTNER ' + String(partner.status || '').toUpperCase() : 'INFLUENCER · NO ACCOUNT YET' }),
        el('strong', { text: title }),
        el('small', { text: detail }),
      ]),
    ]);
    const actions = el('div', { class: 'infl-actions' });
    const btn = (icon, label, cls, fn) => {
      const b = el('button', { class: 'calls-action' + (cls ? ' ' + cls : ''), type: 'button' }, [lucideIcon(icon), el('span', { text: label })]);
      b.addEventListener('click', fn); actions.appendChild(b); return b;
    };
    btn(lane.botPaused ? 'bot-off' : 'bot', lane.botPaused ? 'Bot paused' : 'Bot on', lane.botPaused ? 'is-off' : 'primary', () => {
      _saveInfluencer({ botPaused: !lane.botPaused }, lane.botPaused ? 'Bot back on for this partner' : 'Bot paused for this partner');
    }).title = lane.botPaused ? ('Paused' + (lane.pausedReason ? ': ' + lane.pausedReason : '') + ' — click to turn the bot back on here') : 'The influencer bot answers this partner (when the bot switch is on). Click to pause it here.';
    btn(_influencer.open ? 'chevron-up' : 'chevron-down', _influencer.open ? 'Less' : 'Details', '', () => { _influencer.open = !_influencer.open; renderInfluencerStrip(); });
    head.appendChild(actions);

    const ladder = el('ol', { class: 'infl-ladder', 'aria-label': 'Step in the partner programme' });
    for (const s of (card && card.steps) || []) {
      ladder.appendChild(el('li', { class: (s.done ? 'is-done' : '') + (s.current ? ' is-current' : ''), title: s.label }, [el('span', { text: s.label })]));
    }
    const kids = [head];
    if (card && card.steps) kids.push(ladder);
    if (_influencer.open) kids.push(_influencerDetails(card, lane));
    strip.replaceChildren(...kids);
    refreshIcons(strip);
  }

  // ── Searchy prospects (Inbox ▸ Influencer) ──────────────────────────────
  // Searchy (an agent on Ayoub's Mac) lists creators whose audience wants to
  // learn a language. Only what they published for business is shown. Nothing
  // here sends a message: the team opens the profile and writes by hand.
  const SEARCHY_STATUSES = [['new', 'New'], ['contacted', 'Sent'], ['seen', 'Seen'], ['replied', 'Replied'], ['agreed', 'Agreed'], ['joined', 'Started'], ['not_fit', 'Not a fit']];
  const _searchy = { status: 'new', rows: [], counts: null, countAt: 0, loading: false, seq: 0 };

  // The bar follows the channel, even before (or without) a list render.
  function _syncSearchyBar() {
    const row = document.getElementById('searchyBar');
    if (!row) return;
    const on = state.activeChannel === 'influencer';
    row.classList.toggle('hidden', !on);
    if (on) refreshSearchyCount();
  }

  async function refreshSearchyCount(force) {
    if (!_influencerAllowed()) return;
    if (!force && Date.now() - _searchy.countAt < 60000) return _paintSearchyCount();
    _searchy.countAt = Date.now();
    try {
      const response = await api('/api/influencer/prospects?counts=1&outreach=1');
      if (!response.ok) return;
      const payload = await response.json();
      _searchy.counts = payload.counts || null;
    } catch { /* the badge just stays as it was */ }
    _paintSearchyCount();
  }

  function _paintSearchyCount() {
    const badge = document.getElementById('searchyNewCount');
    if (!badge) return;
    const n = (_searchy.counts && _searchy.counts.new) || 0;
    badge.textContent = n ? String(n > 999 ? '999+' : n) + ' new' : '';
    badge.classList.toggle('hidden', !n);
  }

  function _searchyFollowers(n) {
    if (n == null) return '—';
    if (n >= 1e6) return (n / 1e6).toFixed(n >= 1e7 ? 0 : 1).replace(/\.0$/, '') + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(n >= 1e4 ? 0 : 1).replace(/\.0$/, '') + 'k';
    return String(n);
  }

  function openSearchyPanel() {
    let overlay = document.getElementById('searchyPanel');
    if (!overlay) {
      overlay = el('div', { id: 'searchyPanel', class: 'searchy-overlay', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Searchy prospects' });
      overlay.addEventListener('click', e => { if (e.target === overlay) closeSearchyPanel(); });
      document.body.appendChild(overlay);
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && document.getElementById('searchyPanel')?.classList.contains('open')) closeSearchyPanel(); });
    }
    overlay.classList.add('open');
    _loadSearchy();
  }

  function closeSearchyPanel() {
    document.getElementById('searchyPanel')?.classList.remove('open');
    refreshSearchyCount(true);
  }

  async function _loadSearchy() {
    const seq = ++_searchy.seq;
    _searchy.loading = true;
    _renderSearchy();
    try {
      // Only creators Ayoub rated Very good / Good in Affiliate staff ▸ Creators; "No" never shows here.
      const response = await api('/api/influencer/prospects?outreach=1&status=' + encodeURIComponent(_searchy.status));
      const payload = await response.json().catch(() => ({}));
      if (seq !== _searchy.seq) return; // another tab was clicked meanwhile
      if (!response.ok) throw new Error(payload.error || 'Could not load');
      _searchy.rows = payload.prospects || [];
      _searchy.counts = payload.counts || _searchy.counts;
      _searchy.error = '';
    } catch (e) {
      if (seq !== _searchy.seq) return;
      _searchy.rows = [];
      _searchy.error = e.message || 'Could not load';
    }
    _searchy.loading = false;
    _renderSearchy();
    _paintSearchyCount();
  }

  async function _saveSearchy(row, patch, done) {
    try {
      const response = await api('/api/influencer/prospects/update', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: row.id, ...patch }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Could not save');
      Object.assign(row, payload.prospect || {});
      _searchy.counts = payload.counts || _searchy.counts;
      toast(done || 'Saved', 'success');
      if (patch.status && patch.status !== _searchy.status) _searchy.rows = _searchy.rows.filter(r => r.id !== row.id);
      _renderSearchy();
      _paintSearchyCount();
    } catch (e) { toast(e.message || 'Could not save', 'error'); }
  }

  function _renderSearchy() {
    const overlay = document.getElementById('searchyPanel');
    if (!overlay) return;
    clear(overlay);
    const panel = el('div', { class: 'searchy-panel' });
    const close = el('button', { class: 'icon-btn', type: 'button', 'aria-label': 'Close' }, [lucideIcon('x')]);
    close.addEventListener('click', closeSearchyPanel);
    panel.appendChild(el('div', { class: 'searchy-head' }, [
      el('div', {}, [
        el('h3', { text: 'Searchy · creators to contact' }),
        el('p', { text: 'Creators rated Very good or Good. The full table, the pipeline and the Email / WhatsApp templates are in Contacts ▸ Influencers.' }),
      ]),
      (() => {
        const open = el('button', { class: 'calls-action primary', type: 'button' }, [lucideIcon('table-2'), el('span', { text: 'Open Influencers' })]);
        open.addEventListener('click', () => { closeSearchyPanel(); showView('influencers'); });
        return open;
      })(),
      close,
    ]));
    const tabs = el('div', { class: 'searchy-tabs', role: 'tablist' });
    for (const [value, label] of SEARCHY_STATUSES) {
      const n = (_searchy.counts && _searchy.counts[value]) || 0;
      const tab = el('button', { type: 'button', role: 'tab', class: 'searchy-tab' + (value === _searchy.status ? ' active' : ''), 'aria-selected': value === _searchy.status ? 'true' : 'false' }, [label, el('span', { text: String(n) })]);
      tab.addEventListener('click', () => { if (_searchy.status !== value) { _searchy.status = value; _loadSearchy(); } });
      tabs.appendChild(tab);
    }
    panel.appendChild(tabs);
    const list = el('div', { class: 'searchy-list' });
    if (_searchy.loading) list.appendChild(el('div', { class: 'searchy-empty', text: 'Loading…' }));
    else if (_searchy.error) list.appendChild(el('div', { class: 'searchy-empty', text: _searchy.error }));
    else if (!_searchy.rows.length) list.appendChild(el('div', { class: 'searchy-empty', text: _searchy.status === 'new' ? 'Nothing to contact yet. Creators appear here once they are rated Very good or Good in Affiliate staff ▸ Creators.' : 'Nobody here yet.' }));
    for (const row of _searchy.rows) list.appendChild(_searchyCard(row));
    panel.appendChild(list);
    overlay.appendChild(panel);
    refreshIcons(overlay);
  }

  function _searchyCard(row) {
    const card = el('article', { class: 'searchy-card' });
    const platformLabel = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube', facebook: 'Facebook' }[row.platform] || row.platform;
    card.appendChild(el('div', { class: 'searchy-card-head' }, [
      el('span', { class: 'searchy-platform searchy-' + row.platform, text: platformLabel }),
      el('strong', { text: row.name || '@' + row.handle }),
      el('span', { class: 'searchy-handle', text: '@' + row.handle }),
      row.rating === 'very_good' || row.rating === 'good' ? el('span', { class: 'searchy-rating searchy-rating-' + row.rating, text: row.rating === 'very_good' ? '★★ Very good' : '★ Good' }) : null,
      el('span', { class: 'searchy-score', title: 'How well the audience fits (Searchy)', text: String(row.score) + '/100' }),
    ]));
    const meta = [ _searchyFollowers(row.followers) + ' followers', row.niche, row.language, row.city, row.lastPostDays != null ? ('last post ' + row.lastPostDays + ' d ago') : '' ].filter(Boolean).join(' · ');
    card.appendChild(el('div', { class: 'searchy-meta', text: meta }));
    if (row.audience) card.appendChild(el('div', { class: 'searchy-line' }, [el('span', { text: 'Audience' }), row.audience]));
    if (row.why) card.appendChild(el('div', { class: 'searchy-line' }, [el('span', { text: 'Why' }), row.why]));
    const contacts = el('div', { class: 'searchy-contacts' });
    const profile = el('a', { class: 'calls-action primary', href: row.url, target: '_blank', rel: 'noopener noreferrer' }, [lucideIcon('external-link'), el('span', { text: 'Open profile' })]);
    contacts.appendChild(profile);
    if (row.email) contacts.appendChild(el('a', { class: 'calls-action', href: 'mailto:' + row.email }, [lucideIcon('mail'), el('span', { text: row.email })]));
    if (row.link) {
      let host = '';
      try { host = new URL(row.link).hostname.replace(/^www\./, ''); } catch { /* server keeps https links only */ }
      if (host) contacts.appendChild(el('a', { class: 'calls-action', href: row.link, target: '_blank', rel: 'noopener noreferrer', title: row.link }, [lucideIcon('link'), el('span', { text: 'Link in bio · ' + host })]));
    }
    if (row.whatsapp) contacts.appendChild(el('span', { class: 'searchy-wa', title: 'Written in their bio. Prefer a DM or an email first: cold WhatsApp messages can get our number blocked.' }, [lucideIcon('phone'), el('span', { text: '+' + row.whatsapp })]));
    card.appendChild(contacts);
    if (row.message) {
      const msg = el('div', { class: 'searchy-message' });
      const copy = el('button', { class: 'calls-action', type: 'button' }, [lucideIcon('copy'), el('span', { text: 'Copy message' })]);
      copy.addEventListener('click', async () => {
        try { await navigator.clipboard.writeText(row.message); toast('Message copied', 'success'); }
        catch { toast('Could not copy: select the text and copy it', 'error'); }
      });
      msg.append(el('p', { text: row.message }), copy);
      card.appendChild(msg);
    }
    const form = el('div', { class: 'searchy-form' });
    const status = el('select', { 'aria-label': 'Status' });
    for (const [value, label] of SEARCHY_STATUSES) { const o = el('option', { value, text: label }); if (row.status === value) o.selected = true; status.appendChild(o); }
    const note = el('input', { type: 'text', placeholder: 'Team note', maxlength: '1000', value: row.note || '', 'aria-label': 'Team note' });
    const save = el('button', { class: 'calls-action primary', type: 'button' }, [lucideIcon('save'), el('span', { text: 'Save' })]);
    save.addEventListener('click', () => _saveSearchy(row, { status: status.value, note: note.value }));
    form.append(status, note, save);
    card.appendChild(form);
    if (row.updatedBy) card.appendChild(el('div', { class: 'searchy-by', text: 'Last change: ' + row.updatedBy }));
    return card;
  }

  function _influencerDetails(card, lane) {
    const box = el('div', { class: 'infl-details' });
    const fact = (label, value) => el('div', { class: 'infl-fact' }, [el('span', { text: label }), el('strong', { text: String(value) })]);
    const p = card && card.partner;
    if (p) {
      const facts = el('div', { class: 'infl-facts' }, [
        fact('Email', p.email || '—'),
        fact('Shares on', p.channel || '—'),
        fact('Email confirmed', p.emailVerified ? 'Yes' : 'No'),
        fact('Identity checked', p.identityVerified ? 'Yes' : 'No'),
        fact('Bank details', p.bankDetailsSaved ? 'Saved' : 'Missing'),
        fact('Visits (30 days)', card.visits30 || 0),
        fact('Requests', `${card.referrals?.total || 0} · ${card.referrals?.review || 0} in review · ${card.referrals?.paidLearners || 0} paid`),
        fact('Commissions (DH)', `${card.commissionsDh?.pending || 0} waiting · ${card.commissionsDh?.approved || 0} approved · ${card.commissionsDh?.paid || 0} paid`),
        fact('Transfers', `${card.payouts?.count || 0} · ${card.payouts?.totalDh || 0} DH`),
      ]);
      box.appendChild(facts);
      if (card.links && card.links.length) {
        const list = el('ul', { class: 'infl-links' });
        for (const l of card.links) {
          const copy = el('button', { class: 'infl-copy-link', type: 'button', title: 'Copy link' }, [lucideIcon('copy')]);
          copy.addEventListener('click', () => { try { navigator.clipboard.writeText(l.url); toast('Link copied', 'success'); } catch { /* */ } });
          list.appendChild(el('li', {}, [el('span', { text: l.programme }), el('small', { text: `${l.visits} visits${l.active ? '' : ' · paused'}` }), copy]));
        }
        box.appendChild(list);
      }
      if (p.adminNote) box.appendChild(el('p', { class: 'infl-note', text: 'Staff note: ' + p.adminNote }));
      const staff = el('a', { class: 'calls-action', href: '/affiliate-staff#partner=' + encodeURIComponent(p.id), target: '_blank', rel: 'noopener' }, [lucideIcon('external-link'), el('span', { text: 'Open in Affiliate staff' })]);
      box.appendChild(staff);
    }

    // Lane fields (outreach before an account exists, and the team's own note).
    const form = el('div', { class: 'infl-form' });
    const stage = el('select', { 'aria-label': 'Outreach stage' });
    for (const [v, label] of INFLUENCER_STAGES) { const o = el('option', { value: v, text: label }); if (lane.stage === v) o.selected = true; stage.appendChild(o); }
    const handle = el('input', { type: 'text', placeholder: '@handle', value: lane.handle || '', maxlength: '80', 'aria-label': 'Handle' });
    const note = el('textarea', { rows: '2', placeholder: 'Team note (only the team sees it)', maxlength: '1000', 'aria-label': 'Team note' });
    note.value = lane.note || '';
    const save = el('button', { class: 'calls-action primary', type: 'button' }, [lucideIcon('save'), el('span', { text: 'Save' })]);
    save.addEventListener('click', () => _saveInfluencer({ stage: stage.value, handle: handle.value, note: note.value }, 'Saved'));
    form.append(el('label', {}, [el('span', { text: 'Stage' }), stage]), el('label', {}, [el('span', { text: 'Handle' }), handle]), el('label', { class: 'wide' }, [el('span', { text: 'Note' }), note]), save);
    box.appendChild(form);

    // Link / change the partner account.
    const link = el('div', { class: 'infl-link-partner' });
    const search = el('input', { type: 'search', placeholder: p ? 'Change partner account: name, email…' : 'Link the partner account: name, email, @brand…', 'aria-label': 'Search partner accounts' });
    const results = el('div', { class: 'infl-results' });
    search.addEventListener('input', () => {
      clearTimeout(_influencer.searchTimer);
      _influencer.searchTimer = setTimeout(async () => {
        const q = search.value.trim();
        if (q.length < 2) { results.replaceChildren(); return; }
        try {
          const response = await api('/api/influencer/partners?search=' + encodeURIComponent(q));
          const payload = await response.json().catch(() => ({}));
          results.replaceChildren(...(payload.partners || []).map(x => {
            const b = el('button', { type: 'button', class: 'infl-result' }, [el('strong', { text: x.name || x.email }), el('small', { text: `${x.email} · ${x.status}${x.channel ? ' · ' + x.channel : ''}` })]);
            b.addEventListener('click', () => _saveInfluencer({ partnerId: x.id }, 'Partner account linked'));
            return b;
          }));
          if (!(payload.partners || []).length) results.replaceChildren(el('small', { text: 'No partner account found.' }));
        } catch { results.replaceChildren(el('small', { text: 'Search unavailable.' })); }
      }, 300);
    });
    link.append(search, results);
    if (p && lane.partnerId) {
      const unlink = el('button', { class: 'calls-action', type: 'button' }, [lucideIcon('unlink'), el('span', { text: 'Unlink account' })]);
      unlink.addEventListener('click', () => _saveInfluencer({ partnerId: null }, 'Account unlinked'));
      link.appendChild(unlink);
    }
    box.appendChild(link);
    const remove = el('button', { class: 'calls-action infl-remove', type: 'button' }, [lucideIcon('x'), el('span', { text: 'Remove from Influencer' })]);
    remove.addEventListener('click', _removeInfluencer);
    box.appendChild(remove);
    return box;
  }

  // ============ Bot toggle ============
  async function loadBotState() {
    if (!state.selectedChatId) return;
    try {
      const r = await api('/api/bot?chatId=' + encodeURIComponent(state.selectedChatId));
      const j = await r.json();
      E.botToggleBtn.classList.toggle('bot-on', !!j.on);
    } catch { /* ignore */ }
  }
  async function toggleBot() {
    if (!state.selectedChatId) return;
    const willTurnOn = !E.botToggleBtn.classList.contains('bot-on');
    try {
      const r = await api('/api/bot', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: state.selectedChatId, on: willTurnOn })
      });
      const j = await r.json();
      E.botToggleBtn.classList.toggle('bot-on', !!j.on);
      toast(j.on ? 'Bot ON — replies in ~8s after each message. Shift+click 🤖 to edit prompt.' : 'Bot disabled', 'success');
    } catch {
      toast('Bot toggle failed', 'error');
    }
  }

  // Bot Settings modal — opened via Shift+Click on 🤖
  async function openBotSettingsModal() {
    const pill = document.getElementById('botStatusPill');
    const isOn = E.botToggleBtn.classList.contains('bot-on');
    if (pill) {
      pill.textContent = isOn ? 'on' : 'off';
      pill.classList.toggle('on', isOn);
    }
    const status = document.getElementById('botPromptStatus');
    const input = document.getElementById('botPromptInput');
    if (status) status.textContent = 'Loading…';
    if (input) input.value = '';
    openModal('botSettingsModal');
    try {
      const r = await api('/api/bot/config');
      const j = await r.json();
      if (input) input.value = j.prompt || '';
      if (status) {
        status.textContent = j.isDefault
          ? 'Using default prompt. Edit and save to customize.'
          : `Custom prompt saved ${j.updatedAt ? formatChatDate(new Date(j.updatedAt).getTime()) : ''}`;
      }
    } catch {
      if (status) status.textContent = 'Failed to load prompt.';
    }
  }

  async function saveBotPrompt() {
    const input = document.getElementById('botPromptInput');
    const prompt = (input?.value || '').trim();
    if (!prompt) { toast('Prompt cannot be empty (use Reset for default)', 'error'); return; }
    try {
      const r = await api('/api/bot/config', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      });
      const j = await r.json();
      if (j.error) { toast(j.error, 'error'); return; }
      toast('Bot prompt saved', 'success');
      const status = document.getElementById('botPromptStatus');
      if (status) status.textContent = 'Custom prompt saved just now.';
    } catch {
      toast('Échec de la sauvegarde — vérifiez votre connexion', 'error');
    }
  }

  async function resetBotPrompt() {
    try {
      const r = await api('/api/bot/config', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset: true })
      });
      const j = await r.json();
      const input = document.getElementById('botPromptInput');
      if (input) input.value = j.prompt || '';
      const status = document.getElementById('botPromptStatus');
      if (status) status.textContent = 'Reset to default. Save not required — default is now active.';
      toast('Reset to default prompt', 'success');
    } catch {
      toast('Reset failed', 'error');
    }
  }

  // ============ Modal helpers ============
  function openModal(id) {
    const m = document.getElementById(id);
    if (m) { m.classList.remove('hidden'); refreshIcons(); }
  }
  function closeModal(id) {
    const m = document.getElementById(id);
    if (m) m.classList.add('hidden');
  }

  // ============ Inbox onboarding tutorial (video popup) ============
  // Two modes, decided by /api/onboarding/inbox-tutorial:
  //  - first-time (mandatory && !completed): auto-opens on the Inbox once per
  //    page load, resumes where the user left off, custom controls only and
  //    every seek snaps back — the whole video has to be watched once.
  //  - reference (completed, or not mandatory): native controls + chapters.
  // Since 2026-09-11 the server returns mandatory:false for everyone by
  // default (the team has watched it; the popup had become noise), so the
  // reference mode is what the whole floor gets and the icon is the only way
  // in. The first-time path below stays wired for the next campaign — flip
  // INBOX_TUTORIAL_MANDATORY=1 on the server and it comes back as-is.
  // The server owns completion (it refuses completed:true before the tail),
  // this side only reports maxWatchedSec honestly.
  const ITUT = {
    data: null,        // last API payload
    loaded: false,     // src assigned to the <video>
    lastLegit: 0,      // last position reached by normal playback (locked mode)
    maxWatched: 0,
    autoOpened: false, // once per page load
    timer: null,       // 10s progress poster while playing
    lastPosted: -1,
    allowSeek: false,  // programmatic seeks (resume / chapters) bypass the snap
    lang: 'fr',        // chapter language (FR/EN/AR) — localStorage leads7_itut_lang
    team: null,        // admin panel cache (GET …/team), fetched lazily
    teamOpen: false,
  };
  // The app has no global UI locale (only the recruitment admin one), so the
  // chapter language is its own preference, default French.
  const ITUT_LANGS = ['fr', 'en', 'ar'];
  const ITUT_I18N = {
    fr: { chapters: 'Chapitres', team: 'Équipe', mandatory: 'Obligatoire', seen: 'vu', left: 'restant', done: 'Terminé',
          hint: "Regarde la vidéo jusqu'au bout pour débloquer l'avance rapide et les chapitres. Tu peux fermer et reprendre plus tard — ta progression est sauvegardée.",
          teamEmpty: 'Aucun compte agent.', teamErr: 'Progression équipe indisponible.', loading: 'Chargement…', chapter: 'Chapitre' },
    en: { chapters: 'Chapters', team: 'Team', mandatory: 'Mandatory', seen: 'watched', left: 'left', done: 'Completed',
          hint: 'Watch the video to the end to unlock seeking and chapters. You can close and resume later — your progress is saved.',
          teamEmpty: 'No agent accounts.', teamErr: 'Team progress unavailable.', loading: 'Loading…', chapter: 'Chapter' },
    ar: { chapters: 'الفصول', team: 'الفريق', mandatory: 'إجباري', seen: 'تمت مشاهدته', left: 'متبقي', done: 'مكتمل',
          hint: 'شاهد الفيديو إلى النهاية لفتح التقديم السريع والفصول. يمكنك الإغلاق والعودة لاحقاً — تقدمك محفوظ.',
          teamEmpty: 'لا توجد حسابات.', teamErr: 'تقدم الفريق غير متاح.', loading: 'جارٍ التحميل…', chapter: 'فصل' },
  };
  function _itutT(key) { return (ITUT_I18N[ITUT.lang] || ITUT_I18N.fr)[key] || ITUT_I18N.fr[key] || ''; }
  function _itutText(v) {
    if (!v) return '';
    if (typeof v === 'string') return v;
    return v[ITUT.lang] || v.fr || v.en || v.ar || '';
  }
  function _itutLoadLang() {
    try { const l = localStorage.getItem('leads7_itut_lang'); if (ITUT_LANGS.includes(l)) ITUT.lang = l; } catch { /* */ }
  }
  function _itutSetLang(l) {
    if (!ITUT_LANGS.includes(l) || l === ITUT.lang) return;
    ITUT.lang = l;
    try { localStorage.setItem('leads7_itut_lang', l); } catch { /* */ }
    _itutRenderBadge();
    _itutRenderHint();
    _itutRenderChapters();
  }
  function _itutRenderHint() {
    const span = document.getElementById('itutHintText');
    if (!span) return;
    span.textContent = _itutT('hint');
    span.closest('.itut-hint')?.setAttribute('dir', ITUT.lang === 'ar' ? 'rtl' : 'ltr');
  }
  function _itutFmt(sec) {
    const s = Math.max(0, Math.floor(Number(sec) || 0));
    const m = Math.floor(s / 60), r = s % 60;
    const h = Math.floor(m / 60);
    return h ? `${h}:${String(m % 60).padStart(2, '0')}:${String(r).padStart(2, '0')}` : `${m}:${String(r).padStart(2, '0')}`;
  }
  function _itutLocked() {
    const d = ITUT.data;
    return !!(d && d.mandatory && !(d.progress && d.progress.completed));
  }
  function _itutDuration() {
    const v = document.getElementById('itutVideo');
    const real = v && Number.isFinite(v.duration) && v.duration > 0 ? v.duration : 0;
    return real || Number(ITUT.data?.durationSec) || 0;
  }
  async function inboxTutorialInit() {
    const btn = document.getElementById('inboxTutorialBtn');
    if (!btn) return;
    const role = String(state.me?.role || '').toLowerCase();
    // Teachers / Meta reviewers have no Inbox to learn — keep the icon hidden.
    if (role === 'teacher' || role === 'meta_review') return;
    try {
      const r = await api('/api/onboarding/inbox-tutorial');
      if (!r.ok) return; // 403 for scoped roles → no icon, no auto-open
      ITUT.data = await r.json();
    } catch { return; }
    ITUT.maxWatched = Number(ITUT.data.progress?.maxWatchedSec) || 0;
    ITUT.lastLegit = ITUT.maxWatched;
    _itutLoadLang();
    btn.classList.remove('hidden');
    _itutRenderIcon();
    if (state.activeView === 'inbox') inboxTutorialOnInboxShown();
  }
  function _itutRenderIcon() {
    const dot = document.querySelector('#inboxTutorialBtn .itut-btn-dot');
    if (dot) dot.classList.toggle('hidden', !_itutLocked());
    const btn = document.getElementById('inboxTutorialBtn');
    if (btn) btn.title = _itutLocked() ? 'Tutoriel Inbox — obligatoire' : 'Tutoriel Inbox';
  }
  // Called by showView('inbox') and by init. Opens the tutorial once per page
  // load ONLY while the mandatory campaign is on — with the flag off
  // `_itutLocked()` is false for everyone and this is a no-op, which is what
  // stops the popup from reappearing on every load.
  function inboxTutorialOnInboxShown() {
    if (!ITUT.data || ITUT.autoOpened || !_itutLocked()) return;
    ITUT.autoOpened = true;
    openInboxTutorial();
  }
  function _itutRenderBadge() {
    const badge = document.getElementById('itutBadge');
    if (!badge) return;
    const d = ITUT.data;
    if (!d) { badge.classList.add('hidden'); return; }
    badge.setAttribute('dir', ITUT.lang === 'ar' ? 'rtl' : 'ltr');
    if (d.progress?.completed) {
      badge.textContent = _itutT('done');
      badge.className = 'itut-badge itut-badge-done';
    } else if (d.mandatory) {
      const dur = _itutDuration();
      const pct = dur ? Math.min(100, Math.round((ITUT.maxWatched / dur) * 100)) : 0;
      badge.textContent = `${_itutT('mandatory')} · ${pct} % ${_itutT('seen')} · ${100 - pct} % ${_itutT('left')}`;
      badge.className = 'itut-badge itut-badge-mandatory';
    } else {
      badge.classList.add('hidden');
      return;
    }
    badge.classList.remove('hidden');
  }
  function _itutLangToggle() {
    const labels = { fr: 'FR', en: 'EN', ar: 'AR' };
    return el('div', { class: 'itut-lang', role: 'group', 'aria-label': 'Langue' }, ITUT_LANGS.map(l =>
      el('button', {
        type: 'button', class: 'itut-lang-btn' + (l === ITUT.lang ? ' active' : ''),
        'aria-pressed': l === ITUT.lang ? 'true' : 'false', 'data-lang': l,
        onclick: () => _itutSetLang(l),
      }, [labels[l]])));
  }
  // The chapters rail is for EVERYONE — it is the map of the video, and the
  // team reads it to jump to the part they need (Ayoub 2026-09-11: "otla3
  // lihom 7tta hadi, chapitres"). Only the "Équipe" tab next to it is
  // admin-only: it is the read-only watch-progress of the whole floor, and
  // `isAdmin` comes from the server (role === 'admin'; super_sales is not one).
  // The rail is hidden only while the mandatory campaign locks the player,
  // because each chapter row is a seek and seeking is what the lock forbids —
  // with the campaign off (the default since 2026-09-11) it always shows.
  function _itutRenderChapters() {
    const box = document.getElementById('itutChapters');
    if (!box) return;
    const locked = _itutLocked();
    const chapters = Array.isArray(ITUT.data?.chapters) ? ITUT.data.chapters : [];
    const isAdmin = !!ITUT.data?.isAdmin;
    box.classList.toggle('hidden', locked || (chapters.length === 0 && !isAdmin));
    if (locked || (!chapters.length && !isAdmin)) return;
    const rtl = ITUT.lang === 'ar';
    box.innerHTML = '';
    box.classList.toggle('itut-rtl', rtl);
    // Rail head: FR·EN·AR toggle + (admin) Chapitres / Équipe tabs.
    const head = el('div', { class: 'itut-rail-head' }, [
      isAdmin
        ? el('div', { class: 'itut-rail-tabs', role: 'tablist' }, [
            el('button', { type: 'button', role: 'tab', class: 'itut-rail-tab' + (ITUT.teamOpen ? '' : ' active'), 'aria-selected': ITUT.teamOpen ? 'false' : 'true', onclick: () => { ITUT.teamOpen = false; _itutRenderChapters(); } }, [_itutT('chapters')]),
            el('button', { type: 'button', role: 'tab', class: 'itut-rail-tab' + (ITUT.teamOpen ? ' active' : ''), 'aria-selected': ITUT.teamOpen ? 'true' : 'false', onclick: () => { ITUT.teamOpen = true; _itutRenderChapters(); _itutLoadTeam(); } }, [_itutT('team')]),
          ])
        : el('div', { class: 'itut-chapters-title' }, [_itutT('chapters')]),
      _itutLangToggle(),
    ]);
    box.appendChild(head);
    if (isAdmin && ITUT.teamOpen) { box.appendChild(_itutTeamPanel()); return; }
    const v = document.getElementById('itutVideo');
    const cur = v ? v.currentTime : 0;
    let activeIdx = 0;
    chapters.forEach((c, i) => { if (cur >= c.t) activeIdx = i; });
    const list = el('div', { class: 'itut-chapter-list', dir: rtl ? 'rtl' : 'ltr' });
    chapters.forEach((c, i) => {
      const title = _itutText(c.title) || `${_itutT('chapter')} ${i + 1}`;
      const desc = _itutText(c.desc);
      const row = el('button', { type: 'button', class: 'itut-chapter' + (i === activeIdx ? ' active' : '') }, [
        el('span', { class: 'itut-chapter-t' }, [_itutFmt(c.t)]),
        el('span', { class: 'itut-chapter-txt' }, [
          el('span', { class: 'itut-chapter-title' }, [title]),
          desc ? el('span', { class: 'itut-chapter-desc' }, [desc]) : null,
        ].filter(Boolean)),
      ]);
      row.addEventListener('click', () => {
        if (_itutLocked() || !v) return;
        ITUT.allowSeek = true;
        v.currentTime = c.t;
        v.play().catch(() => {});
      });
      list.appendChild(row);
    });
    box.appendChild(list);
  }
  // Admin "Équipe" panel — read-only list of every non-admin account with
  // its watch progress. Fetched lazily the first time the tab opens.
  async function _itutLoadTeam(force) {
    if (ITUT.team && !force) return;
    try {
      const r = await api('/api/onboarding/inbox-tutorial/team');
      ITUT.team = r.ok ? await r.json() : { error: true };
    } catch { ITUT.team = { error: true }; }
    if (ITUT.teamOpen) _itutRenderChapters();
  }
  function _itutTeamPanel() {
    const wrap = el('div', { class: 'itut-team', id: 'itutTeam', dir: ITUT.lang === 'ar' ? 'rtl' : 'ltr' });
    const t = ITUT.team;
    if (!t) { wrap.appendChild(el('div', { class: 'itut-team-empty' }, [_itutT('loading')])); return wrap; }
    if (t.error) { wrap.appendChild(el('div', { class: 'itut-team-empty' }, [_itutT('teamErr')])); return wrap; }
    const users = Array.isArray(t.users) ? t.users : [];
    if (!users.length) { wrap.appendChild(el('div', { class: 'itut-team-empty' }, [_itutT('teamEmpty')])); return wrap; }
    users.forEach(u => {
      const pct = Math.max(0, Math.min(100, Number(u.pct) || 0));
      const when = u.completedAt ? MA.loc(new Date(u.completedAt), 'd', 'fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
      wrap.appendChild(el('div', { class: 'itut-team-row' + (u.completed ? ' done' : ''), title: userLabel(u.username || '') }, [
        el('div', { class: 'itut-team-top' }, [
          el('span', { class: 'itut-team-name' }, [userLabel(u.username, u.displayName || u.username || u.userId)]),
          u.completed
            ? el('span', { class: 'itut-team-done' }, [lucideIcon('check'), when])
            : el('span', { class: 'itut-team-pct' }, [`${pct} %`]),
        ]),
        el('div', { class: 'itut-team-bar', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(pct) }, [
          el('div', { class: 'itut-team-fill', style: `width:${pct}%` }),
        ]),
      ]));
    });
    refreshIcons(wrap);
    return wrap;
  }
  function _itutApplyMode() {
    const v = document.getElementById('itutVideo');
    const ctl = document.getElementById('itutControls');
    const hint = document.getElementById('itutHint');
    if (!v) return;
    const locked = _itutLocked();
    v.controls = !locked;
    // Native context menu offers "Show controls" — off while locked.
    if (locked) v.setAttribute('controlslist', 'nodownload noplaybackrate'); else v.removeAttribute('controlslist');
    v.oncontextmenu = locked ? (e) => { e.preventDefault(); } : null;
    ctl?.classList.toggle('hidden', !locked);
    hint?.classList.toggle('hidden', !locked);
    document.getElementById('inboxTutorialModal')?.classList.toggle('itut-locked', locked);
    _itutRenderBadge();
    _itutRenderHint();
    _itutRenderChapters();
    _itutRenderIcon();
  }
  function _itutUpdateControls() {
    const v = document.getElementById('itutVideo');
    if (!v) return;
    const dur = _itutDuration();
    const t = document.getElementById('itutTime');
    if (t) t.textContent = `${_itutFmt(v.currentTime)} / ${_itutFmt(dur)}`;
    const fill = document.getElementById('itutProgressFill');
    if (fill) fill.style.width = (dur ? Math.min(100, (v.currentTime / dur) * 100) : 0) + '%';
    document.querySelector('#itutControls .itut-progress')?.setAttribute('aria-valuenow', String(dur ? Math.round((v.currentTime / dur) * 100) : 0));
    const play = document.getElementById('itutPlay');
    if (play) {
      play.innerHTML = '';
      play.appendChild(lucideIcon(v.paused ? 'play' : 'pause'));
      play.setAttribute('aria-label', v.paused ? 'Lecture' : 'Pause');
      refreshIcons(play);
    }
    const mute = document.getElementById('itutMute');
    if (mute) {
      mute.innerHTML = '';
      mute.appendChild(lucideIcon(v.muted ? 'volume-x' : 'volume-2'));
      refreshIcons(mute);
    }
  }
  async function _itutPost(completed) {
    if (!ITUT.data) return;
    const sec = Math.floor(ITUT.maxWatched);
    if (!completed && sec === ITUT.lastPosted) return;
    ITUT.lastPosted = sec;
    try {
      const r = await api('/api/onboarding/inbox-tutorial/progress', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maxWatchedSec: sec, completed: !!completed }),
      });
      if (!r.ok) return;
      const j = await r.json();
      if (j && j.progress) {
        const wasLocked = _itutLocked();
        ITUT.data.progress = j.progress;
        if (j.progress.completed) ITUT.maxWatched = Math.max(ITUT.maxWatched, _itutDuration());
        if (wasLocked && !_itutLocked()) {
          _itutApplyMode();
          toast(_itutT('done'), 'success');
        } else {
          _itutRenderBadge();
        }
      }
    } catch { /* offline — next tick retries */ }
  }
  function _itutStartTimer() {
    _itutStopTimer();
    ITUT.timer = setInterval(() => _itutPost(false), 10000);
  }
  function _itutStopTimer() {
    if (ITUT.timer) { clearInterval(ITUT.timer); ITUT.timer = null; }
  }
  function _itutBindVideo() {
    const v = document.getElementById('itutVideo');
    if (!v || v.dataset.itutBound) return;
    v.dataset.itutBound = '1';
    v.addEventListener('loadedmetadata', () => {
      // Resume from the saved position (allowed programmatic seek).
      const resume = Math.min(ITUT.maxWatched, Math.max(0, v.duration - 1));
      if (resume > 0 && Math.abs(v.currentTime - resume) > 1) { ITUT.allowSeek = true; v.currentTime = resume; }
      ITUT.lastLegit = resume;
      _itutUpdateControls();
      _itutRenderBadge();
    });
    v.addEventListener('seeking', () => {
      if (!_itutLocked()) return;
      if (ITUT.allowSeek) { ITUT.allowSeek = false; return; }
      // Any user seek (either direction) snaps back to the last legit spot.
      if (Math.abs(v.currentTime - ITUT.lastLegit) > 1.5) v.currentTime = ITUT.lastLegit;
    });
    v.addEventListener('seeked', () => { ITUT.allowSeek = false; });
    v.addEventListener('ratechange', () => {
      if (_itutLocked() && v.playbackRate !== 1) v.playbackRate = 1;
    });
    v.addEventListener('timeupdate', () => {
      if (!v.seeking) {
        if (_itutLocked()) {
          // Only advance the legit marker through normal playback (≤2s jumps).
          if (v.currentTime >= ITUT.lastLegit - 0.5 && v.currentTime <= ITUT.lastLegit + 2.5) ITUT.lastLegit = v.currentTime;
        }
        if (v.currentTime > ITUT.maxWatched && v.currentTime <= ITUT.lastLegit + 2.5) ITUT.maxWatched = v.currentTime;
        if (!_itutLocked() && v.currentTime > ITUT.maxWatched) ITUT.maxWatched = v.currentTime;
      }
      _itutUpdateControls();
      if (Math.floor(v.currentTime) % 5 === 0) { _itutRenderBadge(); }
    });
    v.addEventListener('play', () => { _itutStartTimer(); _itutUpdateControls(); });
    v.addEventListener('pause', () => { _itutStopTimer(); _itutUpdateControls(); _itutPost(false); });
    v.addEventListener('ended', () => {
      _itutStopTimer();
      ITUT.maxWatched = Math.max(ITUT.maxWatched, _itutDuration());
      ITUT.lastLegit = ITUT.maxWatched;
      _itutUpdateControls();
      _itutPost(true);
    });
    v.addEventListener('volumechange', _itutUpdateControls);
    v.addEventListener('error', () => {
      document.getElementById('itutError')?.classList.remove('hidden');
      document.getElementById('itutControls')?.classList.add('hidden');
    });
    v.addEventListener('click', () => {
      if (!_itutLocked()) return; // native controls handle it
      if (v.paused) v.play().catch(() => {}); else v.pause();
    });
    document.getElementById('itutPlay')?.addEventListener('click', () => {
      if (v.paused) v.play().catch(() => {}); else v.pause();
    });
    document.getElementById('itutMute')?.addEventListener('click', () => { v.muted = !v.muted; });
    document.getElementById('itutFull')?.addEventListener('click', () => {
      const stage = v.closest('.itut-stage');
      const target = stage || v;
      if (document.fullscreenElement) document.exitFullscreen?.();
      else target.requestFullscreen?.().catch?.(() => {});
    });
    // Close (× or backdrop) → pause + flush progress. The generic
    // [data-modal-close] wiring hides the modal; this runs alongside it.
    document.querySelectorAll('#inboxTutorialModal [data-modal-close]').forEach(b => {
      b.addEventListener('click', () => {
        try { v.pause(); } catch { /* */ }
        _itutStopTimer();
        if (document.fullscreenElement) document.exitFullscreen?.();
        _itutPost(false);
      });
    });
  }
  function openInboxTutorial() {
    if (!ITUT.data) { toast('Tutoriel indisponible', 'error'); return; }
    const v = document.getElementById('itutVideo');
    if (!v) return;
    _itutBindVideo();
    document.getElementById('itutError')?.classList.add('hidden');
    if (!ITUT.loaded) {
      // The URL is behind the leads7_tuto cookie, minted by the API call in
      // inboxTutorialInit — so the <video> GET is already authorised.
      if (ITUT.data.posterUrl) v.poster = ITUT.data.posterUrl;
      v.src = ITUT.data.videoUrl;
      ITUT.loaded = true;
    }
    _itutApplyMode();
    _itutUpdateControls();
    openModal('inboxTutorialModal');
    refreshIcons(document.getElementById('inboxTutorialModal'));
    if (_itutLocked()) v.play().catch(() => { /* autoplay policy — user presses play */ });
  }

  // ============ Mr.Call pipeline bar (Sprint 4 Phase 1) ============
  // Mirrors lib/leads/pipeline.js — keep in sync if the canonical
  // constants ever change. The 6 forward stages render as numbered
  // pills; the 4 side branches render as small chips after a divider.
  const PIPELINE_FORWARD = ['new', 'contacted', 'book_test', 'test_done', 'proforma_sent', 'paid'];
  const PIPELINE_SIDE    = ['no_answer', 'no_show', 'not_interested', 'lost'];
  const PIPELINE_ALL     = ['new', 'no_answer', 'contacted', 'book_test', 'no_show', 'test_done', 'proforma_sent', 'paid', 'not_interested', 'lost'];
  const PIPELINE_LABELS  = {
    new:            'New',
    no_answer:      'No answer',
    contacted:      'Contacted',
    book_test:      'Test scheduled',
    no_show:        'No show',
    test_done:      'Test done',
    proforma_sent:  'Proforma sent',
    paid:           'Paid',
    not_interested: 'Not interested',
    lost:           'Lost',
  };
  const PIPELINE_SIDE_CLASS = { no_answer: 'side-warning', no_show: 'side-warning', not_interested: 'side-danger', lost: 'side-danger' };

  // Per-chat cache so we don't refetch on every re-render. Keyed by chatId.
  // Cleared when the user transitions a stage (so the next openChat re-fetches).
  const pipelineLeadCache = {};
  // Chat IDs where an auto-create POST is currently in-flight. Prevents the
  // race where renderPipelineBar is called twice in quick succession (chat
  // list refresh + chat select) and both fire a POST for the same phone.
  const _pipelineAutoCreateInFlight = new Set();

  // Extract E.164 phone digits from the chat object — same idiom used by
  // showConversation(). Groups (`@g.us`) have no lead and are skipped.
  function pipelineExtractPhone(chat) {
    if (!chat || chat.isGroup) return '';
    // A post's comment thread (many commenters) is never one person: its id
    // carries the post/media id, which must not become a lead's "phone".
    if (/^(?:fbm-comment|ig-comment|ig-mention):/.test(String(chat.id || ''))) return '';
    // Skip masked values ('0653****32' → digits '065332' would poison the
    // /api/leads/by-number lookup) — the JID always carries the real digits.
    const p = String(chat.phone || '');
    const raw = (p && !p.includes('*')) ? p : ((chat.id || '').split('@')[0] || '');
    return String(raw).replace(/[^\d]/g, '');
  }

  // A client with a WhatsApp username who hides their number: their chat id
  // carries no digits (server: lib/whatsapp/wa-user-id.js).
  function _isWaUsernameChatId(id) {
    return /^bsuid\.[a-z]{2}\.[a-z]+@s\.whatsapp\.net$/.test(String(id || ''));
  }

  // What could identify the client, for the link search: a phone number they
  // typed, else a full name they typed ("Lina Khouira"), else an email, else
  // their WhatsApp profile name when it reads like a full name. Greetings and
  // dates ("24/03/2016") never qualify.
  function _waUsernameLinkGuess(messages, profileName = '') {
    const texts = (messages || []).filter(m => m && !m.fromMe && m.text).map(m => String(m.text).trim());
    const phone = texts.find(t => /^\+?[\d\s.-]{9,20}$/.test(t) && t.replace(/\D/g, '').length >= 9);
    if (phone) return phone;
    const greeting = /^(salam|slm|salut|bonjour|bonsoir|merci|hello|hi|ok|سلام|السلام|عليكم|مرحبا|شكرا)$/i;
    const name = texts.find(t => {
      const words = t.split(/\s+/).filter(Boolean);
      return t.length <= 40 && /^[A-Za-zÀ-ÿ؀-ۿ' -]+$/.test(t)
        && words.length >= 2 && words.length <= 4 && !words.some(w => greeting.test(w));
    });
    if (name) return name;
    const email = texts.find(t => /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(t));
    if (email) return email;
    const profile = String(profileName || '').trim();
    const pWords = profile.split(/\s+/).filter(Boolean);
    return (profile.length <= 40 && /^[A-Za-z\u00C0-\u00FF\u0600-\u06FF' -]+$/.test(profile)
      && pWords.length >= 2 && pWords.length <= 4 && !pWords.some(w => greeting.test(w))) ? profile : '';
  }

  // A WhatsApp-username client hides their number. Once the team knows who it
  // is, the chat is linked to that number: its messages move into the
  // contact's conversation and the next ones land there (POST …/link-phone).
  // The search reads the local contacts mirror only — never HubSpot live.
  function _waUsernameLinkBar(chatId) {
    const wrap = el('div', { class: 'wa-user-link' });
    wrap.appendChild(el('span', { class: 'pipeline-bar-empty', text: 'WhatsApp sans numéro — liez-le à son contact :' }));
    const input = el('input', { class: 'wa-user-link-input', type: 'search', placeholder: 'Nom ou numéro du client', 'aria-label': 'Nom ou numéro du client' });
    const go = el('button', { class: 'conv-orphan-btn', type: 'button', text: 'Chercher' });
    const results = el('div', { class: 'wa-user-link-results' });
    const link = async (target, label, btn) => {
      btn.disabled = true;
      btn.textContent = '…';
      try {
        const r = await api('/api/inbox/chats/' + encodeURIComponent(chatId) + '/link-phone', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(target),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) { toast(j.error || ('HTTP ' + r.status), 'error'); btn.disabled = false; btn.textContent = 'Lier'; return; }
        toast('Conversation liée à ' + label, 'success');
        try { await loadConversations({ force: true }); } catch (_) { /* list refreshes on its next poll */ }
        if (state.chats.some(c => c.id === j.chatId)) selectConversation(j.chatId);
      } catch (err) {
        toast('Lien impossible : ' + (err.message || err), 'error');
        btn.disabled = false;
        btn.textContent = 'Lier';
      }
    };
    const addResult = (label, sub, target) => {
      const btn = el('button', { class: 'conv-orphan-btn', type: 'button', text: 'Lier' });
      btn.addEventListener('click', () => link(target, label, btn));
      results.appendChild(el('div', { class: 'wa-user-link-row' }, [
        el('span', { class: 'wa-user-link-name', text: label }),
        sub ? el('span', { class: 'wa-user-link-sub', text: sub }) : null,
        btn,
      ].filter(Boolean)));
    };
    const search = async () => {
      const q = input.value.trim();
      clear(results);
      if (q.length < 2) { input.focus(); return; }
      go.disabled = true;
      try {
        const digits = q.replace(/\D/g, '');
        if (digits.length >= 9 && !/[A-Za-z@]/.test(q)) addResult(q, 'ce numéro', { phone: q });
        const r = await api('/api/hubspot/contacts?limit=6&q=' + encodeURIComponent(q));
        const j = await r.json().catch(() => ({}));
        for (const c of (j.contacts || []).slice(0, 6)) {
          if (!c || !c.chatJid) continue;
          const name = ((c.firstname || '') + ' ' + (c.lastname || '')).trim() || c.email || c.chatJid.split('@')[0];
          addResult(name, c.phone || '', { chatJid: c.chatJid });
        }
        if (!results.firstChild) results.appendChild(el('span', { class: 'wa-user-link-sub', text: 'Aucun contact — tapez son numéro.' }));
      } catch (err) {
        results.appendChild(el('span', { class: 'wa-user-link-sub', text: 'Recherche impossible : ' + (err.message || err) }));
      } finally {
        go.disabled = false;
      }
    };
    go.addEventListener('click', search);
    input.addEventListener('keydown', ev => { if (ev.key === 'Enter') { ev.preventDefault(); search(); } });
    wrap.appendChild(input);
    wrap.appendChild(go);
    wrap.appendChild(results);
    // The thread loads after this bar: once this chat's messages are in, start
    // from what the client typed and show the matching contact right away —
    // one person, one contact, one click.
    let tries = 0;
    const suggest = () => {
      if (state.selectedChatId !== chatId || input.value || !wrap.isConnected) return;
      const own = (state.messages || []).filter(m => m && m.chatId === chatId);
      if (!own.length) { if (tries++ < 15) setTimeout(suggest, 400); return; }
      const guess = _waUsernameLinkGuess(own, (state.chats.find(c => c.id === chatId) || {}).name);
      if (guess) { input.value = guess; search(); }
    };
    setTimeout(suggest, 150);
    return wrap;
  }

  // Attribute auto-created leads to their actual inbox channel. The previous
  // whatsapp_direct constant also labelled FB/IG PSIDs as WA phone numbers,
  // preventing the backend from enforcing a source-aware identity contract.
  function _leadSourceForChat(chat) {
    const id = String(chat?.id || '').toLowerCase();
    const channel = String(chat?.channel || '').toLowerCase();
    // Explicit agent-created conversations do not have durable inbox evidence
    // yet. Keep them on a separate server contract instead of pretending they
    // are observed whatsapp_direct traffic.
    if (chat?._leadCreationSource === 'shared_contact') return 'shared_contact';
    if (chat?._leadCreationSource === 'manual_phone') return 'manual_phone';
    if (chat?.hubspotContactId) return 'hubspot_contact';
    if (channel === 'fbm' || id.startsWith('fbm:') || id.startsWith('fbm-comment:')) return 'facebook';
    if (channel === 'ig' || id.startsWith('ig:') || id.startsWith('ig-comment:') || id.startsWith('ig-mention:')) return 'instagram';
    if (channel === 'tt' || id.startsWith('tt:')) return 'tiktok';
    if (channel === 'sms' || id.startsWith('sms:')) return 'sms_direct';
    return 'whatsapp_direct';
  }

  // Shared-number contact-switcher (Task B1): when 2-3 contacts share one
  // WhatsApp number (parent + child), this tracks which lead UUID the pipeline
  // bar / history are currently pointed at, keyed by chatId. Absent key = the
  // default (oldest lead for the phone). Reset per chat in selectConversation.
  const _activeLeadIdByChat = {};

  // THE PERSON the Contact Profile follows on a shared number (2026-09-30).
  // Ayoub: "ila daro switch, kahs ga3 l'ma3lomat ytswitchaw" — after Switch,
  // Student Info, Groups, Clubs, Tickets, Check app, teacher notes, documents
  // and the fiche/facture prefill must all be the switched person's own data.
  // Every one of those calls sends this id as `leadId`; the server honours it
  // only when the lead sits on the chat's number and the agent may open it.
  //   1. the agent's pick this session (Switch / chip row), or the team pin
  //      once the pipeline bar has resolved to it (_frAdoptPin seeds it);
  //   2. otherwise, on a line with 2+ people, the team pin — ONLY once the bar
  //      reported that very lead (what every money surface acts on);
  //   3. otherwise null → every call goes out exactly as before (the
  //      single-person line, and a family line nobody has picked on).
  function _activePersonLeadId(chatId = state.selectedChatId) {
    const id = String(chatId || '');
    if (!id) return null;
    if (_activeLeadIdByChat[id]) return String(_activeLeadIdByChat[id]);
    if (!_frCtx || _frCtx.chatId !== id || _frCtx.people.length < 2) return null;
    const pinned = String(_frCtx.pinnedId || '');
    if (!pinned || _frBarLead.chatId !== id || _frBarLead.leadId !== pinned) return null;
    return pinned;
  }

  // `&leadId=…` for a profile call, or '' when no person is active.
  function _activePersonQs(chatId = state.selectedChatId) {
    const leadId = _activePersonLeadId(chatId);
    return leadId ? '&leadId=' + encodeURIComponent(leadId) : '';
  }

  // The lead the Contact Profile on screen was loaded for — so a Switch (or
  // the team pin being adopted after the panel already loaded) can tell that
  // the panel now shows the wrong person and reload it.
  let _profileShown = { chatId: '', leadId: '' };
  function _profileFollowActivePerson(chatId = state.selectedChatId) {
    const id = String(chatId || '');
    if (!id || String(state.selectedChatId || '') !== id) return;
    if (!E.main().classList.contains('with-profile')) return;
    const want = _activePersonLeadId(id) || '';
    if (_profileShown.chatId === id && _profileShown.leadId === want) return;
    loadProfileData();
  }

  // Chats whose lead EXISTS but belongs to another agent (GET /api/leads/:id
  // answered 403). Keyed by chatId → { owner, stage }. This is NOT the same
  // state as "no lead": treating it as such made the bar auto-POST /api/leads,
  // where the anti-spoofing guard refused with "automatic WhatsApp source has
  // no server-observed inbox provenance" — an alarming technical error for
  // what is simply someone else's client (2026-09-04). Cleared on every
  // successful fetch so a re-assignment un-sticks it.
  const _leadDeniedByChat = {};

  async function fetchLeadForChat(chatId, { useCache = true, forceLeadId = null } = {}) {
    const chat = state.chats.find(c => c.id === chatId) || state.selectedChat;
    const phone = pipelineExtractPhone(chat);
    if (!phone) return null;
    // When a specific lead is selected in the shared-number switcher, fetch it
    // by UUID instead of the phone→oldest-lead default. Never cached on chatId
    // (that slot holds the default lead).
    if (forceLeadId) {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 12_000);
      try {
        const r = await api('/api/leads/' + encodeURIComponent(forceLeadId), { signal: ctrl.signal });
        delete _leadDeniedByChat[chatId];
        if (r.status === 403) {
          // Same rule as the phone lookup below: picking a shared-number
          // contact that belongs to a colleague must land on "owned by X",
          // never on the auto-create path.
          let info = null;
          try { info = await r.json(); } catch { /* body is optional */ }
          _leadDeniedByChat[chatId] = {
            owner: info?.owner?.displayName || info?.owner?.username || '',
            stage: info?.stage || '',
          };
          return null;
        }
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const j = await r.json();
        return j.lead || null;
      } catch (err) {
        console.warn('[pipeline] fetchLeadForChat(byId) failed:', err.message || err);
        return null;
      } finally {
        clearTimeout(timer);
      }
    }
    if (useCache && pipelineLeadCache[chatId]) return pipelineLeadCache[chatId];
    // 12s hard timeout so a Supabase pooler hiccup can't leave the pipeline
    // bar stuck on "Loading…" indefinitely (agent complaint 2026-05-23).
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 12_000);
    try {
      const r = await api('/api/leads/' + encodeURIComponent(phone), { signal: ctrl.signal });
      delete _leadDeniedByChat[chatId];
      if (r.status === 404) {
        pipelineLeadCache[chatId] = null;
        return null;
      }
      if (r.status === 403) {
        // The lead is real — we just don't own it. Remember that so the bar
        // says so instead of trying to create a second one on top of it.
        let info = null;
        try { info = await r.json(); } catch { /* body is optional */ }
        _leadDeniedByChat[chatId] = {
          owner: info?.owner?.displayName || info?.owner?.username || '',
          stage: info?.stage || '',
        };
        pipelineLeadCache[chatId] = null;
        return null;
      }
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      pipelineLeadCache[chatId] = j.lead || null;
      return pipelineLeadCache[chatId];
    } catch (err) {
      console.warn('[pipeline] fetchLeadForChat failed:', err.message || err);
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  // Build one forward-stage pill (or active / past variant).
  function buildPipelineStageEl(stage, currentStage, leadId, position) {
    const fwdIdx = PIPELINE_FORWARD.indexOf(stage);
    const curIdx = PIPELINE_FORWARD.indexOf(currentStage);
    let cls = 'pipeline-stage';
    if (stage === currentStage) cls += ' active';
    else if (curIdx >= 0 && fwdIdx >= 0 && fwdIdx < curIdx) cls += ' past';
    else cls += ' future';

    const num = el('span', { class: 'pipeline-stage-num', text: String(position) });
    const label = el('span', { class: 'pipeline-stage-label', text: PIPELINE_LABELS[stage] || stage });
    const isForward = curIdx === -1 || (fwdIdx >= 0 && fwdIdx > curIdx);
    const titleText = stage === currentStage
      ? 'Current stage — click for history'
      : (isForward
          ? ('Move to ' + (PIPELINE_LABELS[stage] || stage) + ' (1 click)')
          : ('Move back to ' + (PIPELINE_LABELS[stage] || stage) + ' (confirm)'));
    const btn = el('button', {
      class: cls,
      type: 'button',
      'data-stage': stage,
      title: titleText,
      onclick: (ev) => onPipelineStageClick(ev, leadId, stage, currentStage),
    }, [num, label]);
    return btn;
  }

  function buildPipelineSideEl(stage, currentStage, leadId) {
    const isActive = (stage === currentStage);
    const variant = PIPELINE_SIDE_CLASS[stage] || '';
    const cls = 'pipeline-stage-side' + (isActive ? ' active ' + variant : '');
    return el('button', {
      class: cls,
      type: 'button',
      'data-stage': stage,
      title: isActive ? 'Current stage — click for history' : ('Move to ' + (PIPELINE_LABELS[stage] || stage)),
      text: PIPELINE_LABELS[stage] || stage,
      onclick: (ev) => onPipelineStageClick(ev, leadId, stage, currentStage),
    });
  }

  // Click handler for any stage pill. Optimised for the agent flow where
  // forward progression (1 → 6) happens constantly: forward clicks transition
  // instantly with an Undo toast. Click on a side branch (No answer / No show /
  // Not interested / Lost) or BACKWARD in the forward flow keeps the confirm popover because
  // those clicks are destructive / unusual.
  //   1. Click the active stage                       → open history modal.
  //   2. Click a forward stage AHEAD                  → instant transition.
  //   3. Click a forward stage BEHIND                 → confirm popover.
  //   4. Click any side branch (no_answer/no_show/not_interested/lost) → confirm popover.
  function onPipelineStageClick(ev, leadId, stage, currentStage) {
    ev.preventDefault();
    ev.stopPropagation();
    if (!leadId) return;
    if (stage === currentStage) {
      openPipelineHistory(leadId);
      return;
    }
    if (stage === 'paid') {
      // Paid always carries an encaissement — route through the montant
      // modal instead of the instant transition. (Paid is the last forward
      // stage, so this is the single client path that can reach it.)
      showPaidModal(leadId);
      return;
    }
    const curFwd = PIPELINE_FORWARD.indexOf(currentStage);
    const newFwd = PIPELINE_FORWARD.indexOf(stage);
    const isForwardFlow = (newFwd >= 0); // target is in the forward pipeline (1-6)
    const isForward = isForwardFlow && (curFwd === -1 || newFwd > curFwd);
    if (isForward) {
      // Instant transition — fastest path for the common case. Misclicks
      // can be reverted by clicking the previous stage (which goes through
      // the confirm popover since BACKWARD is still guarded).
      transitionPipelineStage(leadId, stage, null);
      return;
    }
    const isBack = (curFwd >= 0 && newFwd >= 0 && newFwd < curFwd);
    showPipelineConfirm(ev.currentTarget, leadId, stage, { isBack });
  }

  // Render the bar for the currently-selected chat. Called from
  // selectConversation() and from re-renders after a transition.
  async function renderPipelineBar(chatId, options = {}) {
    const host = document.getElementById('pipelineBar');
    if (!host) return;
    // Hide for groups or before a chat is selected.
    const chat = state.chats.find(c => c.id === chatId) || state.selectedChat;
    const paymentReadOnlyOpen = options.readOnly === true || state._paymentChatReadOnlyChatId === chatId;
    if (!chat || chat.isGroup) {
      host.classList.add('hidden');
      clear(host);
      return;
    }
    // RECRUITMENT LANE — a job candidate is not a sales lead by design (no
    // lead row, no owner, no funnel figure). Without this guard, opening a
    // candidate's conversation fired the auto-create POST below and either
    // 422'd into a misleading "Create lead" button or, for agent-opened
    // synthetic chats, minted a real sales lead for the candidate.
    if (chat.recruitmentLane === true) {
      host.classList.remove('hidden');
      clear(host);
      host.appendChild(el('span', { class: 'pipeline-bar-empty', text: 'Candidat recrutement — pas de pipeline de vente.' }));
      return;
    }
    host.classList.remove('hidden');
    // Silent loading — no visible "Loading pipeline…" placeholder. Agents
    // complained it lingered too long on slow chats. The bar just stays
    // empty for the fetch window (12s max per fetchLeadForChat timeout),
    // then renders the actual stages OR the empty-state in one paint.
    clear(host);

    // Shared-number switcher (Task B1): if a specific contact was picked for
    // this chat, render the pipeline for THAT lead UUID; otherwise the default
    // phone→oldest lead. The 99% single-contact case never sets this key.
    const forceLeadId = _activeLeadIdByChat[chatId] || null;
    const lead = await fetchLeadForChat(chatId, { useCache: false, forceLeadId });
    // Bail if the user switched chats during the fetch.
    if (state.selectedChatId !== chatId) return;
    clear(host);
    // The header's Test recording button plays THIS lead's test — no lead, no
    // test to play, so it greys out with the reason as its tooltip.
    _trecHeaderNoteLead(chatId, lead);

    if (!lead) {
      const phone = pipelineExtractPhone(chat);
      // No phone → can't ever create a lead, surface the static notice and bail.
      if (!phone) {
        if (_isWaUsernameChatId(chatId)) { host.appendChild(_waUsernameLinkBar(chatId)); return; }
        host.appendChild(el('span', { class: 'pipeline-bar-empty', text: 'No phone — pipeline unavailable.' }));
        return;
      }
      if (paymentReadOnlyOpen) {
        host.appendChild(el('span', { class: 'pipeline-bar-empty', text: 'No lead record yet · payment chat opened read-only.' }));
        return;
      }
      // The lead exists but is someone else's. Say so — never auto-create a
      // duplicate on top of it, and never offer "Create lead" (that POST is
      // rejected anyway on a chat with no inbox provenance, which is how this
      // surfaced as a spoofing error instead of a permissions one).
      const denied = _leadDeniedByChat[chatId];
      if (denied) {
        const who = denied.owner || 'another agent';
        const stage = denied.stage ? ` · ${PIPELINE_LABELS[denied.stage] || denied.stage}` : '';
        host.appendChild(el('span', {
          class: 'pipeline-bar-empty',
          text: `Lead owned by ${who}${stage} — read-only.`,
        }));
        return;
      }
      // Phone present but no lead row yet — auto-create one in the
      // background so the agent never sees the "Create lead" friction step
      // (Lougha is a sales platform: every contact IS a lead). The manual
      // createLeadForChat path stays available below as a fallback if the
      // auto-create POST fails.
      host.appendChild(el('span', { class: 'pipeline-bar-empty', text: 'Setting up lead…' }));
      if (!_pipelineAutoCreateInFlight.has(chatId)) {
        _pipelineAutoCreateInFlight.add(chatId);
        (async () => {
          try {
            const r = await api('/api/leads', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                phone, name: chat?.name || '', source: _leadSourceForChat(chat),
                chat_id: String(chat?.id || chatId || ''),
              }),
            });
            if (!r.ok) throw new Error('HTTP ' + r.status);
            delete pipelineLeadCache[chatId];
            if (state.selectedChatId === chatId) await renderPipelineBar(chatId);
          } catch (err) {
            console.warn('[pipeline] auto-create lead failed:', err?.message || err);
            // Fall back to the manual button so the agent isn't stuck.
            if (state.selectedChatId === chatId) {
              clear(host);
              host.appendChild(el('span', { class: 'pipeline-bar-empty', text: 'No lead record yet.' }));
              const btn = el('button', {
                class: 'pipeline-create-btn',
                type: 'button',
                title: 'Create a lead row for this phone',
                onclick: () => createLeadForChat(chatId),
              }, [lucideIcon('plus'), document.createTextNode('Create lead')]);
              host.appendChild(btn);
              refreshIcons(host);
            }
          } finally {
            _pipelineAutoCreateInFlight.delete(chatId);
          }
        })();
      }
      return;
    }

    const currentStage = lead.pipeline_stage || 'new';

    // Forward flow with arrows between pills.
    const flow = el('div', { class: 'pipeline-bar-flow' });
    PIPELINE_FORWARD.forEach((s, i) => {
      flow.appendChild(buildPipelineStageEl(s, currentStage, lead.id, i + 1));
      if (i < PIPELINE_FORWARD.length - 1) {
        flow.appendChild(el('span', { class: 'pipeline-stage-arrow', text: '›' }));
      }
    });
    host.appendChild(flow);

    // Side branches.
    const side = el('div', { class: 'pipeline-bar-side' });
    PIPELINE_SIDE.forEach(s => side.appendChild(buildPipelineSideEl(s, currentStage, lead.id)));
    host.appendChild(side);

    // Payment-verification chip (Virements, 2026-06-12) — the lead row
    // carries payment_verification {pending,verified,rejected}|null from
    // GET /api/leads/:id. Pending wins over verified (a new receipt on an
    // already-verified lead needs eyes); nothing renders when there are no
    // claims. Live-updated via the 'payment-verification:updated' socket.
    const pvChip = _pvBuildChip(lead.payment_verification);
    if (pvChip) host.appendChild(pvChip);

    // Reminder chip (Rappels, 2026-06-13) — lead.reminders carries
    // {next, overdueCount, pendingCount}|null. Renders nothing when there
    // are no pending reminders. Wrapped so a bad value can never blank the
    // bar (a missing chip is far better than a missing pipeline).
    try {
      const remChip = _reminderBuildChip(lead.reminders, lead.id);
      if (remChip) host.appendChild(remChip);
    } catch (_) { /* never break the bar over a reminder chip */ }

    // Right-side actions: Terminé→Suivi (paid only), + Rappel, History.
    const actions = el('div', { class: 'pipeline-bar-actions' });
    // Sales → support2 handoff (mig 0041). Paid alone no longer hands the
    // client to the suivi team — the agent clicks "Terminé → Suivi" once
    // onboarding (groupe, confirmation) is finished. Until then support2
    // neither sees the chat nor gets its calls.
    if (currentStage === 'paid') {
      const doHandoff = async (done) => {
        try {
          const r = await api('/api/leads/' + encodeURIComponent(lead.id) + '/handoff', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ done }),
          });
          const j = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(j?.error || ('HTTP ' + r.status));
          toast(done ? 'Client remis au suivi (support2) ✓' : 'Handoff annulé — le client reste côté sales', 'success');
          delete pipelineLeadCache[chatId];
          if (state.selectedChatId === chatId) await renderPipelineBar(chatId);
        } catch (err) {
          toast('Handoff: ' + (err?.message || err), 'error');
        }
      };
      if (!lead.handed_off_at) {
        const handoffBtn = el('button', {
          class: 'pipeline-action-btn pipeline-action-handoff',
          type: 'button',
          title: 'Onboarding terminé — remettre ce client à l’équipe suivi (support2)',
          onclick: () => {
            if (!confirm('Remettre ce client au suivi (support2) ?\nIl apparaîtra dans leur inbox et leurs appels.')) return;
            doHandoff(true);
          },
        }, [lucideIcon('check-check'), document.createTextNode('Terminé → Suivi')]);
        actions.appendChild(handoffBtn);
      } else {
        const who = lead.handed_off_by && lead.handed_off_by !== 'system:grandfather'
          ? ' (' + userLabel(lead.handed_off_by) + ')' : '';
        const badge = el('span', {
          class: 'pipeline-handoff-badge',
          title: 'Client remis au suivi (support2)' + who,
        }, [lucideIcon('check-check'), document.createTextNode('Suivi ✓')]);
        actions.appendChild(badge);
        const undoBtn = el('button', {
          class: 'pipeline-action-btn pipeline-action-handoff-undo',
          type: 'button',
          title: 'Annuler le handoff — le client redevient côté sales, support2 ne le verra plus',
          onclick: () => {
            if (!confirm('Annuler le handoff ? Support2 ne verra plus ce client.')) return;
            doHandoff(false);
          },
        }, [lucideIcon('undo-2'), document.createTextNode('Annuler')]);
        actions.appendChild(undoBtn);
      }
    }
    const remBtn = el('button', {
      class: 'pipeline-action-btn pipeline-action-rem',
      type: 'button',
      title: 'Ajouter un rappel / une relance',
      onclick: () => { try { openReminderModal(lead.id); } catch (_) { /* noop */ } },
    }, [lucideIcon('bell-plus'), document.createTextNode('Rappel')]);
    actions.appendChild(remBtn);
    const histBtn = el('button', {
      class: 'pipeline-action-btn',
      type: 'button',
      title: 'View stage history',
      onclick: () => openPipelineHistory(lead.id),
    }, [lucideIcon('history'), document.createTextNode('History')]);
    actions.appendChild(histBtn);
    host.appendChild(actions);

    refreshIcons(host);

    // Shared-number contact-switcher (Task B1) — only appears when 2-3 contacts
    // share this WhatsApp number. Fire-and-forget: it fetches /by-number and,
    // if count>1, injects a chip row near the bar. Single-contact numbers (99%)
    // render nothing and the bar is byte-for-byte unchanged.
    _renderNumSwitch(chatId, lead.id);
    // Tell the header ring which lead the bar actually resolved to, so its
    // "Active" row can never name a different person than the pipeline below.
    try { _frNoteActiveLead(chatId, lead.id); } catch (err) { /* ring is an extra */ }
  }

  // Renders the shared-number contact-switcher chip row for `chatId`. The
  // active lead is `activeLeadId` (the lead the bar is currently showing).
  // Removes any prior switcher first; appends a new row to #pipelineBar only
  // when GET /api/leads/by-number returns more than one accessible contact.
  async function _renderNumSwitch(chatId, activeLeadId) {
    const host = document.getElementById('pipelineBar');
    if (!host) return;
    host.querySelector('.numswitch')?.remove();
    const chat = state.chats.find(c => c.id === chatId) || state.selectedChat;
    const phone = pipelineExtractPhone(chat);
    if (!phone) return;
    let leads = [];
    try {
      const r = await api('/api/leads/by-number/' + encodeURIComponent(phone));
      if (!r.ok) return; // 404 / error → no switcher, leave the bar as-is
      const j = await r.json().catch(() => null);
      leads = Array.isArray(j?.leads) ? j.leads : [];
      // Trust count when present; fall back to the array length.
      const count = Number.isFinite(j?.count) ? j.count : leads.length;
      if (count <= 1 || leads.length <= 1) return; // single contact → render nothing
    } catch (err) {
      console.warn('[numswitch] by-number failed:', err?.message || err);
      return;
    }
    // Bail if the user switched chats during the round-trip.
    if (state.selectedChatId !== chatId) return;
    host.querySelector('.numswitch')?.remove();
    // The header ring ("People on this number", below) is the richer
    // presentation of exactly this list — who is who, not just who. When it is
    // live for this chat the chip row stands down instead of saying the same
    // thing twice; if the ring's own lookup failed, these chips remain the
    // fallback.
    if (_frIsLive(chatId)) return;

    // Oldest-first so the default (first) chip is the original contact.
    leads = leads.slice().sort((a, b) => {
      const ta = Date.parse(a?.created_at || '') || 0;
      const tb = Date.parse(b?.created_at || '') || 0;
      return ta - tb;
    });
    const activeId = activeLeadId || _activeLeadIdByChat[chatId] || leads[0]?.id;

    const row = el('div', { class: 'numswitch', 'aria-label': 'Contacts sur ce numéro' });
    row.appendChild(el('span', { class: 'numswitch-lead-glyph', title: 'Plusieurs contacts sur ce numéro' }, [lucideIcon('users')]));
    for (const ld of leads) {
      if (!ld || !ld.id) continue;
      const isActive = ld.id === activeId;
      const chip = el('button', {
        class: 'numswitch-chip' + (isActive ? ' active' : ''),
        type: 'button',
        title: (ld.name || 'Contact') + ' — ' + (ld.paid ? 'Payé' : (ld.stage_label || ld.pipeline_stage || '')),
        'aria-pressed': isActive ? 'true' : 'false',
        onclick: () => _numSwitchSelect(chatId, ld.id),
      }, [
        el('span', { class: 'numswitch-chip-name', text: ld.name || 'Contact' }),
        ld.paid
          ? el('span', { class: 'numswitch-badge numswitch-badge-paid', text: 'Payé' })
          : el('span', { class: 'numswitch-badge', text: ld.stage_label || ld.pipeline_stage || '—' }),
      ]);
      row.appendChild(chip);
    }
    host.appendChild(row);
    refreshIcons(host);
  }

  // Click handler for a switcher chip: make that lead the active one for this
  // chat and re-render the pipeline bar (which re-points history/coach at it).
  function _numSwitchSelect(chatId, leadId) {
    if (!leadId) return;
    if (_activeLeadIdByChat[chatId] === leadId) return; // already active — no-op
    if (typeof _websiteTestInvalidatePreview === 'function') {
      _websiteTestInvalidatePreview('Contact changé — recréez le lien depuis la conversation active');
    }
    _activeLeadIdByChat[chatId] = leadId;
    renderPipelineBar(chatId).catch(err => console.warn('[numswitch] re-render failed:', err?.message || err));
    // Everything in the Contact Profile follows the switched person, not only
    // the pipeline bar (2026-09-30).
    _profileFollowActivePerson(chatId);
  }

  // ─── "People on this number" — the shared-line ring + popover ─────────────
  // One WhatsApp number, several people: a parent and their kids, a couple, or
  // the very common line where only the CHILD is registered while the parent
  // holds the phone. The flat chip row above (_renderNumSwitch) listed those
  // contacts but never said WHO IS WHO, so an agent could not tell whether they
  // were writing to the mother or to the child — and the personalised website
  // test link is minted for whoever is ACTIVE. Picking the wrong one sends a
  // child's test to a parent, or a link that dies on the wrong record.
  //
  // This is support2's "Family ring", re-presented on the header avatar: a
  // segmented ring (one arc per person, colour = role) with a count badge, and
  // a popover that names everyone, groups them Adults / Children / Not known
  // yet, and switches the active one. The switcher MECHANISM is unchanged —
  // _activeLeadIdByChat still drives forceLeadId in renderPipelineBar; only the
  // presentation moved.
  //
  // A role is a GUESS until a human confirms it. The server returns role=null
  // when nobody has confirmed, and the ladder below fills the blank:
  //   (1) what the team saved → confirmed
  //   (2) age: <=17 child; older is "parent" only when the line also carries a
  //       minor AND this person has no student history of their own, else adult
  //   (3) the class / group audience ("… kids", "… adultes")
  //   (4) nothing → "not known yet"
  // "Parent" is never emitted on a line without a minor: two adults on one
  // phone are just two adults, and inventing a family out of a couple would be
  // worse than silence. A guessed chip carries a DASHED border AND a trailing
  // "?" — colour is never the only channel, because the grey ramp inverts in
  // dark mode.
  //
  // FAMILY TAG (2026-10-02, contract shared with support2 + students7). The
  // team's answer is a numbered tag — Parent 1 · Parent 2 · Kid 1..4 — stored
  // in the markaz, so a tag set in support2 shows here and ours shows there,
  // and students7 logs families in on it. One slot per person per line: a slot
  // somebody else holds is shown as taken and the server answers 409.
  // Only a CONFIRMED tag puts a person under Parents / Kids; a guess (the
  // markaz backfill's or the age ladder's) stays under "Not set yet" wearing
  // its dashed "Kid ?" chip until somebody confirms it.
  const FR_KID_MAX_AGE = 17;
  const FR_ROLE_META = {
    parent:  { label: 'Parent',  cls: 'fr-role-parent'  },
    adult:   { label: 'Adult',   cls: 'fr-role-adult'   },
    kid:     { label: 'Kid',     cls: 'fr-role-kid'     },
    unknown: { label: 'Not set', cls: 'fr-role-unknown' },
  };
  // Exactly three sections (contract). A pre-contract "Adult" answer has no
  // numbered slot, so it waits under "Not set yet" with its solid chip.
  const FR_SECTIONS = [['parents', 'Parents'], ['kids', 'Kids'], ['unset', 'Not set yet']];
  // Every slot a line hands out, in picker order.
  const FR_SLOTS = [
    { role: 'parent', rank: 1 }, { role: 'parent', rank: 2 },
    { role: 'kid', rank: 1 }, { role: 'kid', rank: 2 }, { role: 'kid', rank: 3 }, { role: 'kid', rank: 4 },
  ].map(s => ({ ...s, label: (s.role === 'parent' ? 'Parent ' : 'Kid ') + s.rank, key: s.role + ':' + s.rank }));
  // The popover's height budget — see _frAvail for the arithmetic.
  const FR_POP_GAP = 10;      // .fr-pop { top: calc(100% + 10px) }
  const FR_POP_MARGIN = 12;   // air kept under the popover
  const FR_POP_FLOOR = 220;   // head + one person + foot stays usable

  // { chatId, phone, count, people, activeId } for the conversation on screen.
  let _frCtx = null;
  let _frPop = null;
  // Re-entry guard: a header repaint for the SAME chat must keep the ring on
  // screen instead of flashing it off and re-fetching the roster.
  let _frBootKey = '';
  // One mutation at a time on this popover. Every write goes through it,
  // including add-person: two adds racing each other mint two real contacts
  // on a real client's line, and neither the client dup check nor the
  // server's read-then-write guard can see a sibling call in flight.
  let _frBusy = false;
  // Whatever is half-typed in the add form, so a refresh triggered by another
  // action (a role save landing while the form is open) does not throw it away.
  let _frAddDraft = null;
  // Which rows have their read-only detail panel expanded, for the line on
  // screen. Deliberately NOT on _frCtx: _frApply builds a brand-new context on
  // every refresh (a role save, a Switch), and parking it there would slam
  // every panel shut under the agent mid-read. Deliberately NOT global either
  // — it is re-scoped the moment the chat changes, so one line's open rows can
  // never be applied to another's.
  let _frOpenDetails = { chatId: '', keys: new Set() };
  // The last lead renderPipelineBar reported for a chat. Both run off the same
  // chat open and the bar usually answers FIRST, before this feature's own
  // lookup has a context to write into — so the report is parked here and
  // read back by _frApply. Without it the ring would sit on "N people on this
  // number" for the whole session on any line with no team pin.
  let _frBarLead = { chatId: '', leadId: '' };

  function _frIsLive(chatId) {
    return !!(_frCtx && _frCtx.chatId === String(chatId) && _frCtx.people.length >= 2);
  }
  function _frLast9(v) {
    const d = String(v == null ? '' : v).replace(/\D/g, '');
    return d.length >= 9 ? d.slice(-9) : '';
  }
  function _frAge(v) {
    const n = parseInt(String(v == null ? '' : v).trim(), 10);
    return Number.isFinite(n) && n >= 1 && n <= 99 ? n : null;
  }
  // The row identity. A person on this line is a lead row, a HubSpot contact,
  // or both — so neither id alone can key a row, and keying on leadId alone
  // would collapse every contact-only person onto one blank key.
  // The lead id wins when there is one: it is what every write and every
  // downstream surface acts on.
  function _frKey(leadId, hubspotContactId) {
    if (leadId) return 'L:' + String(leadId);
    if (hubspotContactId) return 'H:' + String(hubspotContactId);
    return '';
  }
  // "Lougha Class LC98 kids" → kids. The group name travels with the person, so
  // an age-less contact still lands on the right side of the popover.
  function _frAudience(groupName) {
    const s = String(groupName || '');
    if (/\b(kids?|enfants?|child(?:ren)?)\b/i.test(s)) return 'kids';
    if (/\badults?\b/i.test(s)) return 'adults';
    return '';
  }

  // Normalises the server payload and runs the guess ladder. No client-side
  // de-duplication: the server already access-filters this list and publishes
  // its own `count`, and quietly dropping a row here would make the badge lie.
  function _frClassify(rawPeople) {
    const list = (Array.isArray(rawPeople) ? rawPeople : []).map(p => ({
      leadId: String(p.leadId || ''),
      // A person on this line may be a HubSpot contact with NO lead row yet.
      // support2 lists those; we used to drop them, which is exactly how a
      // two-person number rendered as one person. `isLead` is the server's
      // word for it — an older server that does not send the flag only ever
      // returned lead rows, so the leadId is the fallback answer.
      isLead: p.isLead == null ? !!p.leadId : !!p.isLead,
      name: String(p.name || '').trim() || '(no name)',
      hubspotContactId: p.hubspotContactId ? String(p.hubspotContactId) : '',
      // The linked HubSpot contact's own name, and the server's verdict that it
      // shares no name token with the lead (a mother's lead linked to her
      // son's contact). An older server sends neither → no warning line.
      contactName: String(p.contactName || '').trim(),
      contactNameMismatch: !!p.contactNameMismatch && !!String(p.contactName || '').trim(),
      pipelineStage: p.pipelineStage || '',
      stageLabel: p.stageLabel || '',
      paid: !!p.paid,
      age: _frAge(p.age),
      role: (p.role && FR_ROLE_META[p.role] && p.role !== 'unknown') ? p.role : null,
      roleSure: p.roleSure !== false,
      // The tag's number: 1-2 for a parent, 1-4 for a kid. null on a guess and
      // on a pre-contract answer (a rankless Parent / Adult / Kid).
      rank: (Number.isInteger(p.rank) && p.rank >= 1 && p.rank <= 4) ? p.rank : null,
      // 'markaz' = confirmed in the shared registry (maybe from support2),
      // 'local' = confirmed here, 'guess' = the registry's unranked backfill.
      roleSource: String(p.roleSource || ''),
      // Confirmed here while the registry did not know this person yet; it is
      // delivered on a later read. Shown, but said.
      rolePending: !!p.rolePending,
      roleBy: String(p.roleBy || ''),
      studentStatus: String(p.studentStatus || ''),
      level: String(p.level || ''),
      groupName: String(p.groupName || ''),
      student: !!(p.hasStudentSignal || p.paid || p.groupName),
      audience: _frAudience(p.groupName),
      serverActive: !!p.isActive,
      // Who holds this person, and whether the lead behind them is one this
      // agent may not open. An older server sends neither, and then every row
      // falls back to exactly the behaviour it had before (owner chip off,
      // "no lead yet" on a contact-only row).
      owner: (p.owner && (p.owner.username || p.owner.displayName))
        ? {
          username: String(p.owner.username || ''),
          displayName: String(p.owner.displayName || p.owner.username || ''),
        }
        : null,
      ownerStage: String(p.ownerStage || ''),
      restrictedLead: !!p.restrictedLead,
      // A SEPARATE fact from `owner` being null, and it must stay separate:
      // null also means "held by someone we cannot name". See the server.
      restrictedUnassigned: !!p.restrictedUnassigned,
      // HubSpot's own owner for this contact — Leads7's is `owner`. null means
      // the contact has none; { known:false } means it has one we cannot name.
      // The server never sends the numeric id or an address, but this is the
      // last place before the screen, so both are refused here too: a name that
      // fails the check is demoted to "unknown", never printed.
      hubspotOwner: (p.hubspotOwner && typeof p.hubspotOwner === 'object')
        ? (() => {
          const n = String(p.hubspotOwner.displayName || '').replace(/\s+/g, ' ').trim();
          const ok = !!p.hubspotOwner.known && !!n && !n.includes('@')
            && !/\d{4,}/.test(n.replace(/[\s.+()-]/g, ''));
          return { known: ok, displayName: ok ? n : '' };
        })()
        : null,
      // Decided by the server on IDENTITIES. This client never compares two
      // display names to decide whether two systems disagree.
      ownerMismatch: !!p.ownerMismatch,
      // Present keys only, by contract — a key that is not here is a label the
      // panel must not draw. Guard the shape so an older payload (or none at
      // all) cannot turn Object.keys() into a crash.
      details: (p.details && typeof p.details === 'object' && !Array.isArray(p.details)) ? p.details : {},
    })).filter(p => p.leadId || p.hubspotContactId);
    for (const p of list) p.key = _frKey(p.leadId, p.hubspotContactId);
    // Pass 1 over the WHOLE line before any role is assigned: "parent" only
    // means something next to a child.
    const hasKid = list.some(p => p.role === 'kid'
      || (!p.role && (p.age != null ? p.age <= FR_KID_MAX_AGE : p.audience === 'kids')));
    for (const p of list) {
      if (p.role) { p.sure = p.roleSure; continue; }
      p.sure = false;
      if (p.age != null) {
        p.role = p.age <= FR_KID_MAX_AGE ? 'kid' : ((!p.student && hasKid) ? 'parent' : 'adult');
        continue;
      }
      if (p.audience === 'kids') { p.role = 'kid'; continue; }
      if (p.audience === 'adults') { p.role = (!p.student && hasKid) ? 'parent' : 'adult'; continue; }
      p.role = 'unknown';
    }
    for (const p of list) { if (!p.sure) p.rank = null; }
    return list;
  }

  // Which popover section a person sits in: only a CONFIRMED parent / kid tag
  // earns Parents / Kids — a guess or nothing is "Not set yet".
  function _frGroup(p) {
    if (p.sure && p.role === 'parent') return 'parents';
    if (p.sure && p.role === 'kid') return 'kids';
    return 'unset';
  }
  // The badge text: "Parent 1" / "Kid 2" when confirmed with its number,
  // "Parent" / "Kid" / "Adult" for a pre-contract answer, "Kid ?" for a guess,
  // "Not set" for nothing.
  function _frTagLabel(p) {
    if (p.role === 'unknown') return FR_ROLE_META.unknown.label;
    const base = FR_ROLE_META[p.role].label;
    if (!p.sure) return base + ' ?';
    return p.rank ? base + ' ' + p.rank : base;
  }
  // Who else on this line holds a slot, as far as this agent can see. The
  // server is the real gate (409) — a colleague's hidden lead is not in this
  // list — this only greys out what we already know is taken.
  function _frSlotHolder(slot, exceptKey) {
    if (!_frCtx) return null;
    return _frCtx.people.find(x => x.key !== exceptKey && x.sure
      && x.role === slot.role && x.rank === slot.rank) || null;
  }

  // ---- the ring on the header avatar -------------------------------------
  // The ring lives in a WRAPPER inserted around #chatAvatar, never inside it:
  // applyContactAvatar() rewrites that node's textContent AND className on
  // every header paint, so anything parked inside is wiped a tick later.
  // The wrapper is exactly the avatar's own size and the ring is drawn as an
  // outset overlay, so turning it on never shifts the header by a pixel.
  function _frEnsureWrap() {
    const av = document.getElementById('chatAvatar');
    if (!av) return null;
    let wrap = av.parentElement;
    if (!wrap || wrap.id !== 'chatAvatarRing') {
      wrap = el('span', { id: 'chatAvatarRing', class: 'fr-ring' });
      av.parentNode.insertBefore(wrap, av);
      wrap.appendChild(av);
    }
    return wrap;
  }
  function _frClearRing() {
    _frCtx = null;
    _frAddDraft = null;
    _frOpenDetails = { chatId: '', keys: new Set() };
    _frClosePopover();
    document.getElementById('chatWhoChip')?.remove();
    // The line chip is not the ring's: repaint it for whatever is open now
    // (a chat switch clears the previous chat's; a family lookup that comes
    // back empty must not hide this chat's until the next message).
    try { _paintWaLineHeader(); } catch (_) { /* chip is optional */ }
    _frPaintHeaderName(); // no ring → no person title; restores the chat name if one was painted
    const wrap = document.getElementById('chatAvatarRing');
    if (!wrap) return;
    wrap.classList.remove('is-on', 'is-solo');
    for (const n of [...wrap.children]) { if (n.id !== 'chatAvatar') n.remove(); }
  }

  // The ring alone is a colour and a count behind an unlabeled button. On the
  // one surface where picking the wrong person sends a child's test link to
  // his mother, the NAME has to be readable without opening anything — the
  // chip row this feature replaced always spelled it out. So the header's
  // subtitle line carries "Active: <name>", ellipsised, sharing the row with
  // the phone. #chatPhone is wrapped once, at runtime, for the same reason the
  // avatar is: showConversation rewrites that node's textContent on every
  // chat open and would wipe anything parked inside it.
  function _frEnsureIdRow() {
    const phoneEl = document.getElementById('chatPhone');
    if (!phoneEl) return null;
    let row = phoneEl.parentElement;
    if (!row || !row.classList.contains('fr-idrow')) {
      row = el('span', { class: 'fr-idrow' });
      phoneEl.parentNode.insertBefore(row, phoneEl);
      row.appendChild(phoneEl);
    }
    return row;
  }

  // ── Multi-line WhatsApp (2026-10-05) ──────────────────────────────────────
  // Some clients write to one of our OTHER WhatsApp numbers (an agent's own
  // line) instead of the main 0661. The server tags those rows `waLine`
  // ({ id, label, number }) and every reply to that client leaves from the
  // number of their LATEST message. Two small chips say so: one in the meta
  // line of each such bubble, one in the header while the latest client
  // message is on a line. Nothing is shown for the main line.
  // Our line's number in the Moroccan local form ("0664598906"), any input.
  function _waLineLocal(line) {
    const d = String(line?.number || '').replace(/\D/g, '');
    return d.length === 12 && d.startsWith('212') ? '0' + d.slice(3) : d;
  }
  // "via 0664 · Doha"
  function _waLineText(line) {
    const label = String(line?.label || '').trim();
    return 'via ' + ([_waLineLocal(line).slice(0, 4), label].filter(Boolean).join(' · ') || 'WhatsApp');
  }
  // "06 64 59 89 06 — Doha"
  // The number the composer's WhatsApp channel sends from for the open chat:
  // its line while the client's latest message is on one ("0664 598 906 · Doha"),
  // else the main number. The picker promises "the number the CLIENT will see".
  function _composeWaNumberLabel() {
    try {
      const line = _waLineOfOpenChat();
      if (!line) return _CH_WA_NUMBER;
      const local = _waLineLocal(line);
      const num = /^0\d{9}$/.test(local) ? local.slice(0, 4) + ' ' + local.slice(4, 7) + ' ' + local.slice(7) : local;
      return [num, String(line.label || '').trim()].filter(Boolean).join(' · ') || _CH_WA_NUMBER;
    } catch (_) { return _CH_WA_NUMBER; }
  }
  function _waLineWho(line) {
    const local = _waLineLocal(line);
    const num = /^0\d{9}$/.test(local) ? local.match(/.{2}/g).join(' ') : (local ? formatPhoneForDisplay(local) : '');
    return [num, String(line?.label || '').trim()].filter(Boolean).join(' — ') || 'another line';
  }
  function _waLineTitle(line, fromMe) {
    return (fromMe ? 'Sent from' : 'Received on') + ' the WhatsApp line ' + _waLineWho(line)
      + ', not the main number';
  }
  // The line of the open chat's latest CLIENT message (null = the main line).
  // Same rows the server routes on: WhatsApp 1-1, not team notes / calls, not
  // support2's rows mixed into a person view.
  function _waLineOfOpenChat() {
    let best = null;
    for (const m of (state.messages || [])) {
      if (!m || m.fromMe || m.internal || m.peer) continue;
      if (m.type === 'call' || m.type === 'system' || m.type === 'note') continue;
      if (m.sentVia === 'support2' || String(m.id || '').startsWith('s2in:')) continue;
      if (m._personSource && m._personSource !== 'leads7') continue;
      const cid = String(m.chatId || '');
      if (!cid.endsWith('@s.whatsapp.net')) continue;
      if (m.channel && m.channel !== 'wa' && m.channel !== 'whatsapp') continue;
      if (!best || (Number(m.timestamp) || 0) >= (Number(best.timestamp) || 0)) best = m;
    }
    return best && best.waLine ? best.waLine : null;
  }
  function _paintWaLineHeader() {
    const chat = state.selectedChat;
    let line = chat && !chat.isGroup && state.selectedChatId ? _waLineOfOpenChat() : null;
    // A number chosen in the composer wins until the client writes again.
    const view = chat && !chat.isGroup ? _waLinesView() : null;
    const cur = view && view.pick ? view.lines.find(l => l.id === view.current) : null;
    if (cur) line = cur.id === 'main' ? null : cur;
    let chip = document.getElementById('chatWaLineChip');
    if (!line) { chip?.remove(); return; }
    const text = _waLineText(line);
    const title = cur
      ? 'Your replies to this client leave from the WhatsApp line ' + _waLineWho(line) + ' (chosen in the composer)'
      : 'This client last wrote to the WhatsApp line ' + _waLineWho(line)
        + ' — your replies leave from that number';
    if (chip && chip.textContent === text && chip.title === title) return;
    const row = _frEnsureIdRow();
    if (!row) return;
    if (!chip) {
      chip = el('span', { id: 'chatWaLineChip', class: 'wa-line-chip' }, [el('span', { class: 'wa-line-chip-text' })]);
      // Right after the phone, before the "who" chip, whatever painted first.
      const phoneEl = document.getElementById('chatPhone');
      if (phoneEl && phoneEl.parentElement === row) phoneEl.after(chip); else row.appendChild(chip);
    }
    chip.firstChild.textContent = text;
    chip.title = title;
  }

  // The header TITLE (#chatName, painted from chat.name by showConversation)
  // follows the active person too, once one is explicitly active on a 2+
  // people line (a Switch, or the team pin the bar resolved to — see
  // _activePersonLeadId). A family line nobody has picked on keeps the chat's
  // own name. The chat name is restored the moment no person is active.
  function _frPaintHeaderName() {
    const nameEl = E.chatName;
    if (!nameEl) return;
    const chatId = String(state.selectedChatId || '');
    const leadId = (_frCtx && _frCtx.chatId === chatId) ? _activePersonLeadId(chatId) : null;
    const person = leadId ? _frCtx.people.find(p => p.leadId === leadId) : null;
    if (person && person.name) {
      if (nameEl.textContent !== person.name) nameEl.textContent = person.name;
      nameEl.dataset.frPerson = leadId;
      nameEl.title = person.name + ' — one of ' + _frCtx.count + ' people on this number';
      return;
    }
    if (nameEl.dataset.frPerson) {
      delete nameEl.dataset.frPerson;
      nameEl.removeAttribute('title');
      const chat = state.selectedChat;
      if (chat && String(chat.id || '') === chatId) nameEl.textContent = chat.name || chat.phone || 'Unknown';
    }
  }

  function _frPaintWho() {
    document.getElementById('chatWhoChip')?.remove();
    _personRenameSyncBtn();
    // A single person needs no "who is this" chip — the header already names them.
    if (!_frCtx || _frCtx.people.length < 2) return;
    const row = _frEnsureIdRow();
    if (!row) return;
    // `activeId` is a LEAD id, and a contact-only person's leadId is the
    // empty string — so without this guard an unresolved line would find the
    // first lead-less person and name them "Active".
    const active = (_frCtx.activeId
      ? _frCtx.people.find(p => p.leadId === _frCtx.activeId)
      : null) || null;
    const chip = el('button', {
      type: 'button',
      id: 'chatWhoChip',
      class: 'fr-who' + (active ? '' : ' is-unresolved'),
      title: active
        ? ('This conversation is attributed to ' + active.name + ' — click to change who')
        : (_frCtx.count + ' people share this number — click to pick who this conversation is'),
    }, [
      lucideIcon('users'),
      el('span', {
        class: 'fr-who-name',
        text: active ? active.name : _frCtx.count + ' people on this number',
      }),
    ]);
    chip.addEventListener('click', ev => { ev.preventDefault(); ev.stopPropagation(); _frTogglePopover(); });
    row.appendChild(chip);
    refreshIcons(row);
  }

  // ── Rename a person (chat header + family popover) ────────────────────────
  // Ayoub 2026-09-30: the whole sales team renames from the header, and the
  // name stays — Leads7, HubSpot (markaz outbox) and support2. One route:
  // PATCH /api/leads/by-number/:phone/person/:leadId/name. The header renames
  // the person the conversation is attributed to (the only person, or the
  // Active one on a shared number); the popover renames any row.
  function _personRenameTarget() {
    if (!_frCtx || _frCtx.chatId !== String(state.selectedChatId || '')) return null;
    const people = _frCtx.people || [];
    const p = people.length === 1
      ? people[0]
      : (() => {
        // Same "who is active" answer the header and the profile panel use.
        const activeId = (typeof _activePersonLeadId === 'function' && _activePersonLeadId(_frCtx.chatId)) || _frCtx.activeId;
        return activeId ? people.find(x => x.leadId === activeId) : null;
      })();
    return p && p.leadId && !p.restrictedLead ? p : null;
  }

  function _personRenameSyncBtn() {
    const btn = document.getElementById('chatNameEditBtn');
    if (!btn) return;
    const shared = !!(_frCtx && _frCtx.chatId === String(state.selectedChatId || '') && _frCtx.people.length >= 2);
    const target = _personRenameTarget();
    // On a shared number with nobody picked yet, the pencil stays visible and
    // says what to do — a hidden control there reads as "you cannot rename".
    btn.classList.toggle('is-on', !!target || shared);
    btn.title = target
      ? 'Rename ' + target.name
      : 'Pick who this conversation is first (click the avatar), then rename';
    btn.dataset.ready = target ? '1' : '';
  }

  // Swap `textEl` for an input; resolves with the typed name, or null on Esc.
  function _personRenameInline(textEl, current) {
    // Only one rename field at a time: blurring cancels any other open one.
    document.querySelectorAll('.chat-name-input').forEach(i => i.blur());
    return new Promise(resolve => {
      const input = el('input', {
        type: 'text', class: 'chat-name-input', value: current || '',
        maxlength: '60', 'aria-label': 'New name',
      });
      const prevDisplay = textEl.style.display;
      textEl.style.display = 'none';
      textEl.insertAdjacentElement('afterend', input);
      input.focus();
      input.select();
      let done = false;
      const finish = (value) => {
        if (done) return;
        done = true;
        input.remove();
        textEl.style.display = prevDisplay;
        resolve(value);
      };
      input.addEventListener('keydown', ev => {
        ev.stopPropagation();
        if (ev.key === 'Enter') { ev.preventDefault(); finish(input.value.replace(/\s+/g, ' ').trim()); }
        else if (ev.key === 'Escape') { ev.preventDefault(); finish(null); }
      });
      input.addEventListener('click', ev => ev.stopPropagation());
      // Leaving the field cancels: a half-typed name must never be saved by a stray click.
      input.addEventListener('blur', () => finish(null));
    });
  }

  let _personRenameBusy = false;
  async function _personRename(p, textEl) {
    if (!p || !p.leadId || p.restrictedLead || !_frCtx || _personRenameBusy) return;
    const ctx = _frCtx;
    const name = await _personRenameInline(textEl, p.name);
    if (!name || name === p.name) return;
    _personRenameBusy = true;
    try {
      const r = await api('/api/leads/by-number/' + encodeURIComponent(ctx.phone)
        + '/person/' + encodeURIComponent(p.leadId) + '/name', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) return void toast(j.error || 'Rename failed', 'error');
      _personRenameApply({ chatId: ctx.chatId, leadId: p.leadId, name: j.name || name, solo: !!j.solo });
      if (j.contactSkipped) {
        // The linked HubSpot contact is another person's (a relative's, or one
        // several people share) — only this lead was renamed.
        toast('Nom changé dans Leads7 — le contact HubSpot lié appartient à une autre personne, il n’a pas été modifié', 'warning');
      } else if (j.hasContact && !j.contactWritten) {
        toast('Nom changé dans Leads7 — HubSpot pas encore à jour, réessayez plus tard', 'warning');
      } else {
        toast('Nom changé : ' + (j.name || name), '');
      }
    } catch (err) {
      toast('Network error', 'error');
    } finally {
      _personRenameBusy = false;
    }
  }

  // Local + socket path: every surface that shows this person's name.
  function _personRenameApply({ chatId, leadId, name, solo }) {
    if (!leadId || !name) return;
    let touched = false;
    if (_frCtx) {
      for (const p of _frCtx.people || []) {
        if (p.leadId === String(leadId)) { p.name = name; touched = true; }
      }
    }
    if (touched) {
      if (typeof _frPaint === 'function') _frPaint();
      if (_frPop) _frRenderPopover();
    }
    const open = String(state.selectedChatId || '');
    const onThisChat = touched && _frCtx && _frCtx.chatId === open;
    if (solo && chatId) applyChatNameUpdate(chatId, name);
    else if (onThisChat && typeof _frPaintHeaderName === 'function') _frPaintHeaderName();
    _personRenameSyncBtn();
  }

  document.getElementById('chatNameEditBtn')?.addEventListener('click', ev => {
    ev.preventDefault();
    ev.stopPropagation();
    const target = _personRenameTarget();
    if (!target) {
      if (_frCtx && _frCtx.people.length >= 2) _frTogglePopover();
      return;
    }
    _personRename(target, E.chatName);
  });

  // One <circle> per person on a 50-unit box (the 40px avatar plus 5px of air
  // on each side). The active arc is thicker and fully opaque; a guessed arc is
  // cut into three ticks; an unknown one is dotted.
  function _frRingSvg(people, activeId) {
    const R = 23, C = 2 * Math.PI * R, n = people.length;
    const step = C / n, gap = n > 4 ? 6 : (n > 2 ? 8 : 10), len = Math.max(4, step - gap);
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 50 50');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('class', 'fr-ring-svg');
    people.forEach((p, i) => {
      const c = document.createElementNS(ns, 'circle');
      const active = !!activeId && p.leadId === activeId;
      let dash;
      if (p.role === 'unknown') dash = '2,4';
      else if (p.sure) dash = len + ',' + (C - len);
      else { const d = Math.max(2, (len - 8) / 3); dash = d + ',4,' + d + ',4,' + d + ',' + (C - len); }
      c.setAttribute('cx', '25');
      c.setAttribute('cy', '25');
      c.setAttribute('r', String(R));
      c.setAttribute('fill', 'none');
      c.setAttribute('stroke-width', active ? '3.2' : '2.2');
      c.setAttribute('stroke-linecap', p.role === 'unknown' ? 'butt' : 'round');
      c.setAttribute('stroke-dasharray', dash);
      if (p.role !== 'unknown') c.setAttribute('stroke-dashoffset', String(-(i * step + gap / 2)));
      c.setAttribute('transform', 'rotate(-90 25 25)');
      c.setAttribute('class', 'fr-arc ' + FR_ROLE_META[p.role].cls + (active ? ' is-active' : ''));
      svg.appendChild(c);
    });
    return svg;
  }

  function _frPaint() {
    const wrap = _frEnsureWrap();
    if (!wrap || !_frCtx) return;
    const keep = _frPop;
    for (const n of [...wrap.children]) { if (n.id !== 'chatAvatar') n.remove(); }
    const people = _frCtx.people;
    const count = _frCtx.count;
    // One person on the line (Ayoub, 2026-09-23 — the same entry point support2
    // has): the ring still opens, and the badge is a "+" instead of a count, so
    // an agent can add a second person on the SAME number from any chat — not
    // only once a family already exists.
    const solo = people.length < 2;
    wrap.classList.add('is-on');
    wrap.classList.toggle('is-solo', solo);
    wrap.insertBefore(_frRingSvg(people, _frCtx.activeId), wrap.firstChild);
    const badge = solo
      ? el('span', { class: 'fr-badge is-add', text: '+' })
      : el('span', { class: 'fr-badge', text: String(count) });
    if (!solo && people.some(p => p.role === 'unknown' || !p.sure)) badge.classList.add('is-guess');
    wrap.appendChild(badge);
    const label = solo ? '1 person on this number' : count + ' people share this number';
    const hit = el('button', {
      type: 'button',
      class: 'fr-hit',
      'aria-haspopup': 'dialog',
      'aria-expanded': keep ? 'true' : 'false',
      title: label + ' — click to see who',
      'aria-label': label + ', open the list',
    });
    hit.addEventListener('click', ev => { ev.preventDefault(); ev.stopPropagation(); _frTogglePopover(); });
    wrap.appendChild(hit);
    if (keep) wrap.appendChild(keep); // an open popover survives a repaint
    // …and a repaint is exactly when the header may have moved (a banner
    // appearing above it), so the room below is measured again.
    if (keep) _frFitPopover();
    _frPaintWho();
    _frPaintHeaderName();
    // The header's subtitle row was just repainted: keep the line chip on it.
    try { _paintWaLineHeader(); } catch (_) { /* chip is optional */ }
    // The chip row under the pipeline bar says the same thing with less: when
    // the ring is up it stands down (it stays the fallback if the ring's own
    // lookup failed).
    document.getElementById('pipelineBar')?.querySelector('.numswitch')?.remove();
  }

  // ---- data --------------------------------------------------------------
  function _frApply(data, chatId, phone) {
    if (String(state.selectedChatId || '') !== String(chatId)) return;
    const people = _frClassify(data && data.people);
    const count = Number.isFinite(data && data.count) ? Number(data.count) : people.length;
    if (!people.length || count < 1) return void _frClearRing();
    // The team's pin, straight from the server. This client never adopts it
    // into _activeLeadIdByChat by RE-RENDERING: GET /api/leads/:phone already
    // resolves to the pinned lead server-side (the same shape support2 uses,
    // where the contact lock is consulted before any phone scoring). Seeding
    // it by calling _numSwitchSelect would fire a SECOND renderPipelineBar for
    // the same chat open, and that function has no generation token — the two
    // renders can land out of order and leave _activeLeadIdByChat naming one
    // person while the bar, the ring and every money surface that reads it
    // (fiche / facture / proforma, the teacher-level prefill, the website-test
    // link) act on another. What _frAdoptPin does instead (2026-09-30) is
    // write the map ONLY once the bar has REPORTED that very lead — no render,
    // nothing to race — so those readers agree with the bar after a reopen.
    const pinnedId = data && data.activeLeadId ? String(data.activeLeadId) : '';
    const picked = _activeLeadIdByChat[chatId] ? String(_activeLeadIdByChat[chatId]) : '';
    const prev = (_frCtx && _frCtx.chatId === String(chatId)) ? _frCtx.activeId : '';
    const barLead = _frBarLead.chatId === String(chatId) ? _frBarLead.leadId : '';
    // "Active" may only ever name a lead we know the app actually resolved to,
    // and only one that is really on this list. In order: the agent's own pick
    // this session, then what renderPipelineBar reported (it IS what every
    // downstream surface acts on), then the team's pin, then what we were
    // already showing. Anything that survives none of those leaves the marker
    // empty rather than guessing a name.
    const onList = id => !!id && people.some(p => p.leadId === id);
    const activeId = [picked, barLead, pinnedId, prev,
      people.find(p => p.serverActive)?.leadId].find(onList) || '';
    // Re-scope the expanded rows to THIS chat: same chat keeps them open
    // across a refresh, a different line starts from scratch.
    _frDetailScope(chatId);
    _frCtx = {
      chatId: String(chatId),
      phone: String(phone || ''),
      count,
      people,
      pinnedId,
      activeId,
    };
    _frAdoptPin(chatId);
    _frPaint();
    if (_frPop) _frRenderPopover();
  }

  // The reopen fix (2026-09-30). selectConversation wipes this chat's entry
  // in _activeLeadIdByChat on every open, while the team pin persists
  // server-side and the pipeline bar resolves to it. Once the bar has REPORTED
  // the pinned lead on a 2+ people line, record it in the map — no re-render,
  // the bar already shows it — so every reader of the map agrees with the bar
  // and the Contact Profile: the encaissement prefill's `switched` check, the
  // fiche level, documentData.lead_id, isSecondaryLead. Never over a pick the
  // agent made this session; never on a single-person line.
  function _frAdoptPin(chatId) {
    const id = String(chatId || '');
    if (!id || _activeLeadIdByChat[id]) return;
    const pinned = _activePersonLeadId(id); // map empty → the pin-once-reported rule
    if (!pinned) return;
    _activeLeadIdByChat[id] = pinned;
    _profileFollowActivePerson(id);
  }

  async function _frFetch(phone) {
    const r = await api('/api/leads/by-number/' + encodeURIComponent(phone) + '/people');
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return await r.json();
  }

  async function _frBootstrap(chatId, phone) {
    try {
      _frApply(await _frFetch(phone), chatId, phone);
    } catch (err) {
      // The ring is an extra — a failed lookup just leaves it off and the chip
      // row under the pipeline bar remains the fallback.
      console.warn('[people] lookup failed:', err?.message || err);
    }
  }

  // Re-read the line after every mutation. Deliberately NOT cached across
  // chats: roles and the active person are team-wide, so a stale local copy
  // would show one agent a choice another agent has already changed.
  async function _frRefresh() {
    if (!_frCtx) return;
    const { chatId, phone } = _frCtx;
    const body = _frPop?.querySelector('.fr-pop-body');
    const scroll = body ? body.scrollTop : 0;
    try {
      _frApply(await _frFetch(phone), chatId, phone);
      const next = _frPop?.querySelector('.fr-pop-body');
      if (next) next.scrollTop = scroll;
    } catch (err) {
      console.warn('[people] refresh failed:', err?.message || err);
    }
  }

  // Called by renderPipelineBar with the lead the bar actually resolved. The
  // bar is the authority — it is what every downstream surface acts on — so
  // whatever it reports wins over the pin the server advertised. When the two
  // disagree (a pin on a lead this agent cannot open, or a server older than
  // this client), the popover says so out loud instead of quietly picking one.
  function _frNoteActiveLead(chatId, leadId) {
    if (!leadId) return;
    const id = String(leadId);
    // Parked even when there is no context yet — the bar routinely answers
    // before this feature's own lookup does, and _frApply reads it back.
    _frBarLead = { chatId: String(chatId), leadId: id };
    if (!_frCtx || _frCtx.chatId !== String(chatId)) return;
    _frAdoptPin(chatId);
    if (_frCtx.activeId === id) return void _frPaintHeaderName();
    if (!_frCtx.people.some(p => p.leadId === id)) return;
    _frCtx.activeId = id;
    _frPaint();
    if (_frPop) _frRenderPopover();
  }

  // Entry point, called once per conversation open.
  function _frOnChat(chat) {
    const chatId = String((chat && chat.id) || state.selectedChatId || '');
    if (chatId && chatId === _frBootKey) {
      // Same conversation, selected again. selectConversation has just wiped
      // this chat's entry in _activeLeadIdByChat, so the ring must stop
      // claiming a pick that no longer exists: fall back to the pin and
      // re-read the line, then let the pipeline bar report what it resolves
      // to. Keeping the ring on screen (instead of flashing it off) is the
      // whole point of this guard.
      if (_frCtx && _frCtx.chatId === chatId) {
        _frCtx.activeId = _frCtx.pinnedId || '';
        _frPaint();
        if (_frPop) _frRenderPopover();
        _frRefresh();
      }
      return;
    }
    _frClearRing();
    _frBarLead = { chatId: '', leadId: '' }; // a new chat: the old report is void
    _frBootKey = chatId;
    // No ring on groups, and none on a recruitment-lane chat: a job candidate
    // is not a sales lead and has no family on the line.
    if (!chat || chat.isGroup || chat.recruitmentLane === true) return;
    const phone = pipelineExtractPhone(chat);
    if (!_frLast9(phone)) return;
    _frBootstrap(chatId, phone);
  }

  // ---- the popover -------------------------------------------------------
  // HOW TALL THE POPOVER MAY BE. Its POSITION is pure CSS and stays that way
  // (see _frTogglePopover). The one thing a stylesheet cannot know is how far
  // down the viewport the avatar sits: the prod audit measured the popover
  // starting at y≈249, under the app chrome, the banners and the header — and
  // `max-height: min(62vh, 480px)` is 62% of the WINDOW, not of what is left
  // below y=249, so at any innerHeight under ~656 (1280x640 measured; a
  // 1366x768 laptop with a bookmarks bar gets there) the last 6px sat under the
  // fold. So the room is measured, once on open and again on resize, and handed
  // to CSS as --fr-pop-avail. The body already scrolls inside that box.
  //   1280x640 : 640 - 239 - 10 - 12 = 379 → 379px, bottom edge 628
  //   1024x720 : 720 - 239 - 10 - 12 = 459 → 459px, bottom edge 708
  //   1280x800 : 800 - 239 - 10 - 12 = 539 → capped at 480px, bottom edge 729
  // (239 = the anchor's bottom edge when the popover starts at 249.)
  // The FLOOR keeps head + one person + foot usable on a very short window; it
  // is the only case where the popover may still pass the fold, by design.
  // (FR_POP_GAP / FR_POP_MARGIN / FR_POP_FLOOR live with the other FR_
  // constants at the top of this block, so no call order can reach them early.)
  function _frAvail(anchorBottom, viewportH) {
    const a = Number(anchorBottom);
    const v = Number(viewportH);
    if (!Number.isFinite(a) || !Number.isFinite(v) || v <= 0) return null;
    return Math.max(FR_POP_FLOOR, Math.floor(v - a - FR_POP_GAP - FR_POP_MARGIN));
  }
  function _frFitPopover() {
    if (!_frPop || !_frPop.style) return;
    const wrap = document.getElementById('chatAvatarRing');
    const rect = wrap && typeof wrap.getBoundingClientRect === 'function' ? wrap.getBoundingClientRect() : null;
    const px = rect ? _frAvail(rect.bottom, window.innerHeight) : null;
    // No measurement → drop the property and let the stylesheet's own fallback
    // stand, rather than pinning a stale number.
    if (px == null) _frPop.style.removeProperty('--fr-pop-avail');
    else _frPop.style.setProperty('--fr-pop-avail', px + 'px');
  }

  function _frClosePopover() {
    if (_frPop) { _frPop.remove(); _frPop = null; }
    window.removeEventListener('resize', _frFitPopover);
    document.removeEventListener('mousedown', _frOutside, true);
    document.removeEventListener('keydown', _frEsc, true);
    document.querySelector('#chatAvatarRing .fr-hit')?.setAttribute('aria-expanded', 'false');
  }
  function _frOutside(ev) {
    if (!_frPop) return;
    if (_frPop.contains(ev.target)) return;
    if (ev.target?.closest?.('#chatAvatarRing')) return;
    _frClosePopover();
  }
  function _frEsc(ev) {
    if (ev.key !== 'Escape' || !_frPop) return;
    ev.stopPropagation();
    const hit = document.querySelector('#chatAvatarRing .fr-hit');
    _frClosePopover();
    hit?.focus();
  }
  // Anchored in the flow of the ring wrapper rather than pinned to a measured
  // viewport rectangle, so it can never drift away from the avatar when the
  // pane reflows (the contact panel opening, the action strip re-flowing, a
  // resize, a scroll) and needs no listener to keep it in place.
  function _frTogglePopover() {
    if (_frPop) return void _frClosePopover();
    if (!_frCtx) return;
    const wrap = _frEnsureWrap();
    if (!wrap) return;
    _frPop = el('div', { class: 'fr-pop', role: 'dialog', 'aria-label': 'People on this number' });
    wrap.appendChild(_frPop);
    _frRenderPopover();
    // Height only — the position still needs no listener (see above).
    _frFitPopover();
    window.addEventListener('resize', _frFitPopover);
    document.addEventListener('mousedown', _frOutside, true);
    document.addEventListener('keydown', _frEsc, true);
    wrap.querySelector('.fr-hit')?.setAttribute('aria-expanded', 'true');
  }

  function _frCount(people) {
    const n = { parents: 0, kids: 0, unset: 0 };
    for (const p of people) n[_frGroup(p)]++;
    const bits = [];
    if (n.parents) bits.push(n.parents + (n.parents > 1 ? ' parents' : ' parent'));
    if (n.kids) bits.push(n.kids + (n.kids > 1 ? ' kids' : ' kid'));
    if (n.unset) bits.push(n.unset + ' not set yet');
    return bits.join(' · ');
  }

  function _frRenderPopover() {
    if (!_frPop || !_frCtx) return;
    const people = _frCtx.people;
    clear(_frPop);
    const head = el('div', { class: 'fr-pop-head' }, [
      el('div', { class: 'fr-pop-title', text: _frCtx.count === 1 ? '1 person on this number' : _frCtx.count + ' people on this number' }),
      el('div', { class: 'fr-pop-sub', text: _frCount(people) }),
    ]);
    // A pin this agent cannot see at all: no row can carry the "team pin" tag,
    // so say it here rather than let the popover read as if nobody pinned.
    if (_frCtx.pinnedId && !people.some(p => p.leadId === _frCtx.pinnedId)) {
      head.appendChild(el('div', {
        class: 'fr-pop-warn',
        text: 'The team pinned someone on this line that you cannot open.',
      }));
    }
    _frPop.appendChild(head);
    const body = el('div', { class: 'fr-pop-body' });
    for (const [group, label] of FR_SECTIONS) {
      const rows = people.filter(p => _frGroup(p) === group);
      // Parent 1 before Parent 2, Kid 1 before Kid 2; a rankless answer last.
      // "Not set yet" keeps the server's order. sort() is stable.
      if (group !== 'unset') rows.sort((a, b) => (a.rank || 9) - (b.rank || 9));
      if (!rows.length) continue;
      body.appendChild(el('div', { class: 'fr-group', text: label }));
      for (const p of rows) {
        body.appendChild(_frRow(p));
        // An expanded panel survives this rebuild (a role save, a Switch, a
        // refresh) — and only for the rows that were open, so expanding one
        // person never touches the others.
        if (_frDetailIsOpen(p)) body.appendChild(_frDetailPanel(p));
      }
    }
    const addBtn = el('button', { type: 'button', class: 'fr-addbtn' }, [
      lucideIcon('user-plus'),
      el('span', { text: 'Add someone on this number' }),
    ]);
    addBtn.addEventListener('click', ev => {
      ev.stopPropagation();
      if (_frPop.querySelector('.fr-add')) return;
      _frAddDraft = { name: '', tag: '', age: '' };
      addBtn.disabled = true;
      body.appendChild(_frAddForm());
      body.scrollTop = body.scrollHeight;
    });
    body.appendChild(addBtn);
    // A refresh (a role save landing while the agent is mid-typing) rebuilds
    // this whole popover. Re-open the form and put the draft back instead of
    // throwing away what they had typed.
    if (_frAddDraft) {
      addBtn.disabled = true;
      body.appendChild(_frAddForm());
    }
    _frPop.appendChild(body);
    _frPop.appendChild(el('div', { class: 'fr-pop-foot' }, [
      lucideIcon('info'),
      el('span', { text: 'Tags are shared with the whole team, support2 and the student app.' }),
    ]));
    refreshIcons(_frPop);
  }

  // ---- the read-only contact detail, next to Switch / Active ---------------
  // Ayoub, 2026-09-21: "khso tswitha contact info kamla li kina 3la hadak
  // contact bash y3rf … wash ghir howa nfes, ghir 3wed tsjal, wla msjel bash
  // ysjel wlidto, wla 3lah mdoublikiti, wla kan 3andna ch7al hadi o bgha ydir
  // renew — had shi kaml khso ytle3 hda dik switch o active". Four questions,
  // and every one of them is answered by a DATE or a pack, not by a stage —
  // which is why the server now carries them and why the summary line below
  // is a timeline rather than a status.

  function _frDetailScope(chatId) {
    const id = String(chatId || '');
    if (_frOpenDetails.chatId !== id) _frOpenDetails = { chatId: id, keys: new Set() };
    return _frOpenDetails.keys;
  }
  function _frDetailIsOpen(p) {
    return !!(_frCtx && _frOpenDetails.chatId === _frCtx.chatId && _frOpenDetails.keys.has(p.key));
  }

  // Months by index — NOT Intl. `date_of_end` & friends arrive as a FLOATING
  // date ("2023-11-13"): no instant, no zone. Parsing it into a Date and
  // formatting it would apply Morocco's offset to a day that never had one and
  // print the 12th. The ISO createdate IS an instant, so that one goes through
  // MA.fmt like every other timestamp in this app.
  const FR_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  // The DAY is validated against the month's real length, not just 1-31: a
  // malformed "2026-11-45" from a hand-edited HubSpot property would otherwise
  // render "45 Nov 2026" with a straight face. No such value exists in the book
  // today — this is the door closed before anyone walks through it.
  function _frDay(v) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || '').trim());
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!(mo >= 1 && mo <= 12) || !(d >= 1)) return null;
    // Day 0 of the next month IS the last day of this one, leap years included.
    if (d > new Date(Date.UTC(y, mo, 0)).getUTCDate()) return null;
    return { y, m: mo, d, text: d + ' ' + FR_MONTHS[mo - 1] + ' ' + m[1] };
  }
  // Is this floating day already past? Compared against TODAY in Morocco, read
  // through MA so a browser still on the old tz database cannot flip the
  // verdict at the edges of a day.
  function _frDayIsPast(day) {
    if (!day) return false;
    const today = MA.fmt('ca-day', 'en-CA', {}, Date.now());
    const key = day.y + '-' + String(day.m).padStart(2, '0') + '-' + String(day.d).padStart(2, '0');
    return key < today;
  }
  // createdate is an ISO INSTANT, so MA turns it into the Morocco day first
  // ("ca-day" is the YYYY-MM-DD key this app already uses everywhere) and the
  // month name comes off the same table as every other date here. Going
  // straight through Intl with { month: 'short' } does NOT match: en-GB spells
  // September "Sept", so one row read "First seen Sept 2026 · Paid 18 Sep 2026"
  // — two spellings of one month, two centimetres apart.
  function _frMonthYear(iso) {
    const t = Date.parse(String(iso || ''));
    if (!Number.isFinite(t)) return '';
    const day = _frDay(MA.fmt('ca-day', 'en-CA', {}, t));
    return day ? FR_MONTHS[day.m - 1] + ' ' + day.y : '';
  }

  // The always-visible answer to "have we met this person before, and did
  // their pack run out?" — facts only, in time order, and nothing when the
  // contact carries none of them. No verdict is rendered: "Pack ended 13 Nov
  // 2023" is the evidence, the agent draws the conclusion.
  function _frHistBits(p) {
    const d = p.details || {};
    const out = [];
    const since = _frMonthYear(d.createDate);
    if (since) out.push({ text: 'First seen ' + since });
    const end = _frDay(d.dateOfEnd);
    const pay = _frDay(d.dateOfPayment);
    if (end) {
      const past = _frDayIsPast(end);
      out.push({ text: (past ? 'Pack ended ' : 'Pack runs to ') + end.text, past });
    } else if (pay) {
      out.push({ text: 'Paid ' + pay.text });
    }
    const club = _frDay(d.clubDateOfEnd);
    if (!end && !pay && club) out.push({ text: 'Club to ' + club.text, past: _frDayIsPast(club) });
    return out;
  }

  // The expanded grid. Every entry is dropped when its value is absent — the
  // rule that keeps a label from ever standing over an empty box (the
  // empty-string trap that put a level-less chip on screen in #435 is handled
  // server-side with nullif, and re-checked here).
  //
  // Each row is tagged `echo`: true when the row ALREADY shows that value
  // without expanding anything — the role chip carries the age, .fr-row-sub
  // carries the status / level / target group, .fr-row-hist carries the dates,
  // the owner chip carries the agent. The panel still prints them (it is the
  // whole record, and a record with holes in it reads as missing data), but the
  // chevron is gated on there being at least one NON-echo row. Without that, a
  // plain agent's own lead grows a button whose panel just repeats the line
  // above it.
  function _frDetailRows(p) {
    const d = p.details || {};
    const rows = [];
    const add = (label, value, echo) => {
      const s = value == null ? '' : String(value).trim();
      if (s) rows.push({ k: label, v: s, echo: !!echo });
    };
    // Mirrors the `bits` array built in _frRow — kept in step with it by hand;
    // the two are eight lines apart on purpose.
    const subShowsStage = !p.student && !p.studentStatus;
    if (p.owner) add('Agent', p.owner.displayName, true);
    // HubSpot's answer, right under Leads7's — PANEL ONLY, never on the row.
    // It earns the chevron in exactly two cases: the two systems DISAGREE, or
    // HubSpot names somebody while Leads7 names nobody (an unassigned lead, a
    // lead-less contact). A HubSpot owner that simply matches the agent, or
    // one we cannot name, tells the reader nothing the row did not.
    if (p.hubspotOwner) {
      const hs = p.hubspotOwner;
      const mismatch = p.ownerMismatch && !!p.owner;
      const name = hs.known ? hs.displayName : 'unknown HubSpot owner';
      rows.push({
        k: 'HubSpot owner',
        v: name,
        echo: !(mismatch || (hs.known && !p.owner)),
        warn: mismatch,
        flag: mismatch ? 'differs' : '',
        title: mismatch
          ? ('Leads7 and HubSpot disagree on who holds this person: Leads7 has them with '
            + p.owner.displayName + ', HubSpot with '
            + (hs.known ? name : 'an owner we cannot name') + '.')
          : name,
      });
    }
    add('Stage', p.restrictedLead ? p.ownerStage : (p.stageLabel || p.pipelineStage),
      !p.restrictedLead && subShowsStage);
    add('Lead status', d.leadStatus, false);
    add('Lifecycle', d.lifecycleStage, false);
    add('Student', p.studentStatus, true);
    add('Level', p.level, true);
    // `groups` is the class they are actually IN; `potential_group` is the one
    // they were lined up for. Two different answers, so two different labels —
    // collapsing them would be the kind of guess this popover exists to avoid.
    add('Class', d.groups, false);
    add('Target group', p.groupName, true);
    add('Pack', d.pack, false);
    add('Duration', d.packDuration, false);
    add('First seen', _frMonthYear(d.createDate), true);
    const first = _frDay(d.dateOfFirstSession);
    add('First session', first && first.text, false);
    const end = _frDay(d.dateOfEnd);
    const pay = _frDay(d.dateOfPayment);
    // The timeline prints the payment date only when there is no pack end date.
    add('Last payment', pay && pay.text, !end);
    add(end && _frDayIsPast(end) ? 'Pack ended' : 'Pack ends', end && end.text, true);
    const cs = _frDay(d.clubDateOfStart);
    const ce = _frDay(d.clubDateOfEnd);
    // The timeline prints the club END date, and only as a last resort.
    if (cs || ce) add('Club', cs && ce ? (cs.text + ' → ' + ce.text) : ((cs || ce).text),
      !cs && !!ce && !end && !pay);
    add('Age', p.age == null ? '' : String(p.age), true);
    add('Gender', d.gender, false);
    add('Email', d.email, false);
    return rows;
  }
  // Does expanding this person tell the agent anything the row did not?
  function _frDetailHasExtra(p) {
    return _frDetailRows(p).some(r => !r.echo);
  }

  function _frDetailPanel(p) {
    const wrap = el('div', { class: 'fr-det', 'data-fr-det': p.key });
    const grid = el('dl', { class: 'fr-det-grid' });
    for (const r of _frDetailRows(p)) {
      grid.appendChild(el('dt', { class: 'fr-det-k', text: r.k }));
      if (r.warn) {
        // A disagreement between two systems. Colour is never the only
        // channel in this popover: the value turns to the warning ink AND
        // carries the word, and the title spells out who says what.
        grid.appendChild(el('dd', { class: 'fr-det-v is-warn', title: r.title || r.v }, [
          el('span', { class: 'fr-det-vt', text: r.v }),
          el('span', { class: 'fr-det-flag', text: r.flag || 'differs' }),
        ]));
      } else {
        grid.appendChild(el('dd', { class: 'fr-det-v', title: r.title || r.v, text: r.v }));
      }
    }
    wrap.appendChild(grid);
    wrap.appendChild(el('div', { class: 'fr-det-foot', text: 'Read-only — from the contact record.' }));
    return wrap;
  }

  function _frRow(p) {
    const isActive = !!_frCtx.activeId && p.leadId === _frCtx.activeId;
    // The team pinned this person but the pipeline resolved to someone else.
    // Never silently pick a winner: the Active pill follows the pipeline (it
    // is what the app acts on) and this row says the pin disagrees.
    const isStalePin = !!_frCtx.pinnedId && p.leadId === _frCtx.pinnedId && !isActive;
    const row = el('div', {
      class: 'fr-row' + (isActive ? ' is-active' : ''),
      'data-fr-key': p.key,
      'data-lead-id': p.leadId,
    });

    row.appendChild(el('span', { class: 'fr-row-av ' + FR_ROLE_META[p.role].cls }, [
      el('span', { class: 'fr-row-av-in', text: initials(p.name) || '?' }),
    ]));

    const meta = el('div', { class: 'fr-row-meta' });
    const line1 = el('div', { class: 'fr-row-name' }, [el('span', { class: 'fr-row-name-txt', text: p.name })]);
    // Rename any person with a lead of their own, straight from the list.
    if (p.leadId && !p.restrictedLead) {
      const nameTxt = line1.firstChild;
      const rn = el('button', {
        type: 'button', class: 'fr-row-rename is-on',
        title: 'Rename ' + p.name, 'aria-label': 'Rename ' + p.name,
      }, [lucideIcon('pencil')]);
      rn.addEventListener('click', ev => { ev.preventDefault(); ev.stopPropagation(); _personRename(p, nameTxt); });
      line1.appendChild(rn);
    }

    // A ROW THAT IS SOMEBODY ELSE'S CLIENT IS COMPLETELY INERT.
    // Every write on this row — Switch and the role chip alike — goes through
    // _frMintLead → POST …/person, which answers 403 for a person whose lead
    // belongs to someone else. Those are the ONLY two paths to a write from a
    // row (swept 2026-09-21: the Info expander is read-only, the Active pill is
    // a span, and the add-form's "Show me that person" only refreshes and
    // scrolls). So both are disabled here, from the same sentence, because
    // fixing one dead gesture and shipping the other is not a fix.
    const mine = String((state.me && (state.me.username || '')) || '').toLowerCase();
    const ownerElse = !!p.owner && String(p.owner.username || '').toLowerCase() !== mine;
    const ownerName = p.owner ? p.owner.displayName : '';
    const lockedWhy = !p.restrictedLead ? ''
      : (p.restrictedUnassigned
        ? 'This person already has a lead and no agent is assigned to it — ask a supervisor to assign it.'
        : (ownerName
          ? 'Their lead belongs to ' + ownerName + ' — ask them to take the conversation.'
          : 'Their lead belongs to another agent — ask a supervisor who is on it.'));

    // THE FAMILY TAG BADGE — and the control that changes it. Confirmed =
    // solid border + the number ("Parent 1", "Kid 2"); a guess = dashed border
    // + a trailing "?"; nothing = "Not set". The chevron says "click to pick".
    const tagLabel = _frTagLabel(p);
    const who = p.roleBy ? ' by ' + userLabel(p.roleBy) : '';
    const tagState = p.role === 'unknown' ? 'Not set yet'
      : (!p.sure ? 'A guess (' + tagLabel + ')'
        : (p.roleSource === 'markaz'
          ? tagLabel + ' — confirmed' + who + ', shared with support2 and the student app'
          : (p.rolePending
            ? tagLabel + ' — confirmed' + who + '. Saved here; it reaches the shared registry as soon as this person is synced'
            : tagLabel + ' — confirmed' + who)));
    const chip = el('button', {
      type: 'button',
      class: 'fr-chip ' + FR_ROLE_META[p.role].cls
        + (p.role === 'unknown' ? ' is-unset' : (p.sure ? ' is-sure' : ' is-guess'))
        + (p.rolePending ? ' is-pending' : ''),
      // A real `disabled` on a real <button>, so the dead state is a CSS
      // :disabled rule and not a colour pasted in here.
      disabled: p.restrictedLead ? 'disabled' : null,
      'aria-disabled': p.restrictedLead ? 'true' : null,
      'aria-haspopup': p.restrictedLead ? null : 'true',
      'aria-label': (p.restrictedLead ? 'Family tag: ' : 'Change family tag: ') + tagLabel,
      title: p.restrictedLead
        ? tagState + ' — read-only here. ' + lockedWhy
        : tagState + ' — click to change the tag',
    }, [
      // The dashed border AND the trailing "?" both say "guess". Colour alone
      // is never the signal.
      el('span', { class: 'fr-chip-t', text: tagLabel }),
      p.age != null ? el('span', { class: 'fr-chip-age', text: '· ' + p.age }) : null,
      p.rolePending ? lucideIcon('clock') : null,
      p.restrictedLead ? null : lucideIcon('chevron-down'),
    ].filter(Boolean));
    // No handler at all on a restricted row — not a handler that returns early.
    // A disabled <button> already swallows clicks, but the picker must be
    // unreachable, not merely hard to reach.
    if (!p.restrictedLead) {
      chip.addEventListener('click', ev => { ev.stopPropagation(); _frToggleRolePick(row, p); });
    }
    line1.appendChild(chip);
    if (isStalePin) {
      line1.appendChild(el('span', {
        class: 'fr-pin-tag',
        text: 'team pin',
        title: 'The team pinned this person, but this conversation currently resolves to someone else. Switch to make it match.',
      }));
    }
    // WHO HOLDS THIS PERSON — or, only when nobody does, "no lead yet".
    //
    // These two tags are mutually exclusive on purpose. Until 2026-09-21 a
    // colleague's client showed up here tagged "no lead yet", and to a
    // non-owner that tag is a LIE: the person has a lead, it is simply not
    // theirs. The agent only learned the truth by clicking Switch and reading
    // the 403's toast — a wasted gesture on a real client's conversation.
    // `restrictedLead` is the server saying "there IS a lead here and it is not
    // yours", so it wins, and "no lead yet" is kept for the case it actually
    // describes: nobody anywhere has a lead for this person.
    //
    // The third case is a row the caller CAN open that belongs to someone else
    // — only ever reachable by a sales admin, since an ordinary agent's
    // visible rows are their own. Same chip, informational wording.
    // (`mine` / `ownerElse` / `ownerName` / `lockedWhy` are computed above the
    // role chip, which needs the same sentence.)
    if (p.restrictedUnassigned) {
      // A lead exists and NOBODY holds it — 45% of the book. Saying "another
      // agent" here would invent a colleague and send the agent off a lead
      // they may simply be given, so this state gets its own words and its own
      // referral: a supervisor, never "ask them".
      line1.appendChild(el('span', {
        class: 'fr-owner is-free',
        title: 'This person already has a lead, and no agent is assigned to it. '
          + (p.ownerStage ? 'It sits at ' + p.ownerStage + '. ' : '')
          + 'You cannot open it from here — ask a supervisor to assign it.',
        // An OPEN padlock against the closed one on the held state: same
        // vocabulary, opposite answer. Both names are already used elsewhere in
        // this bundle, so neither is a bet on the lucide build.
      }, [lucideIcon('unlock'), el('span', { class: 'fr-owner-n', text: 'not assigned' })]));
    } else if (p.restrictedLead) {
      // Held by a colleague. `ownerName` is '' when agent_id is an email
      // address or a synthetic account (see _lineOwner) — there IS a holder,
      // we just will not print a personal Gmail on the floor's screen.
      line1.appendChild(el('span', {
        class: 'fr-owner is-locked',
        title: (ownerName || 'Another agent') + ' holds this client. You can see who is on the number,'
          + ' not their lead —'
          + (p.ownerStage ? ' their pipeline stage is ' + p.ownerStage + '.' : ' this row is read-only here.')
          + (ownerName ? ' Ask them to take the conversation.' : ' Ask a supervisor who is on it.'),
      }, [lucideIcon('lock'), el('span', { class: 'fr-owner-n', text: ownerName || 'another agent' })]));
    } else if (ownerElse) {
      line1.appendChild(el('span', {
        class: 'fr-owner',
        title: 'This lead belongs to ' + p.owner.displayName + '.',
      }, [lucideIcon('user'), el('span', { class: 'fr-owner-n', text: p.owner.displayName })]));
    } else if (!p.isLead) {
      // On this number as a contact, with no lead row of its own, anywhere.
      // An agent must never read this as a lead that merely has no stage yet,
      // because none of the lead surfaces exist for them until a Switch (or a
      // role confirmation) mints the row.
      line1.appendChild(el('span', {
        class: 'fr-nolead',
        text: 'no lead yet',
        title: 'On this number as a contact, with no lead record. Switching to them — or confirming their role — creates one.',
      }));
    }
    meta.appendChild(line1);

    const bits = [];
    if (p.age == null) bits.push('no age');
    if (p.paid) bits.push('paid');
    if (p.studentStatus) bits.push(p.studentStatus);
    if (p.level) bits.push('Level ' + p.level);
    if (!p.student && !p.studentStatus) {
      const stage = p.stageLabel || p.pipelineStage || '';
      bits.push(stage ? 'Lead · ' + stage : 'no class');
    }
    if (p.groupName) bits.push(p.groupName);
    if (bits.length) {
      meta.appendChild(el('div', { class: 'fr-row-sub', title: bits.join(' · '), text: bits.join(' · ') }));
    }
    // The lead and the HubSpot contact linked to it name two different people
    // (no shared name token) — the Contact Profile that follows this person
    // shows the CONTACT's record, so say whose it is before anyone acts on it.
    if (p.isLead && p.contactNameMismatch) {
      meta.appendChild(el('div', {
        class: 'fr-row-hsname',
        title: 'This lead is linked to the HubSpot contact “' + p.contactName + '”. The names do not match — '
          + 'if that contact is someone else, ask a supervisor to fix the link before sending anything.',
        text: 'HubSpot: ' + p.contactName + ' ⚠',
      }));
    }
    // The timeline, always visible. This is the line that answers "same person
    // registering again / a parent booking for their kid / a duplicate / an old
    // client due a renew" without opening anything — two rows on one number
    // with different "First seen" months ARE the duplicate.
    const hist = _frHistBits(p);
    if (hist.length) {
      const line = el('div', { class: 'fr-row-hist', title: hist.map(h => h.text).join(' · ') });
      hist.forEach((h, i) => {
        if (i) line.appendChild(el('span', { class: 'fr-hist-sep', text: '·' }));
        line.appendChild(el('span', { class: 'fr-hist-b' + (h.past ? ' is-past' : ''), text: h.text }));
      });
      meta.appendChild(line);
    }
    row.appendChild(meta);

    const actions = el('div', { class: 'fr-row-actions' });
    if (isActive) {
      actions.appendChild(el('span', { class: 'fr-active-pill', text: 'Active' }));
    } else if (p.restrictedLead) {
      // THE SERVER WILL REFUSE THIS. Switching a contact-only row goes through
      // POST …/person, whose duplicate guard answers 403 "not your lead" for a
      // person whose lead belongs to someone else. Offering an enabled button
      // beside a padlock re-creates the exact wasted gesture this whole change
      // exists to remove — so the refusal is stated on the control, before the
      // click, and the button is dead. Same sentence as the role chip, from
      // the same variable, so the two can never drift apart.
      actions.appendChild(el('button', {
        type: 'button', class: 'fr-switch', text: 'Switch', disabled: 'disabled',
        'aria-disabled': 'true',
        title: 'This conversation cannot be pointed at ' + p.name + '. ' + lockedWhy,
      }));
    } else {
      const sw = el('button', {
        type: 'button', class: 'fr-switch', text: 'Switch',
        title: p.isLead
          ? 'Point this conversation at ' + p.name
          : 'Create a lead for ' + p.name + ' and point this conversation at them',
      });
      sw.addEventListener('click', ev => { ev.stopPropagation(); _frSwitchTo(p, sw); });
      actions.appendChild(sw);
    }
    // The third control, in the SAME group as Switch / Active because that is
    // where Ayoub asked for it ("hda dik switch o active"). Read-only: it
    // opens a panel and does nothing else — no write, no navigation. Rendered
    // only when the panel would say something the row does not already say, so
    // the chevron is never a promise of an empty box or of an echo.
    if (_frDetailHasExtra(p)) {
      const open = _frDetailIsOpen(p);
      const more = el('button', {
        type: 'button',
        class: 'fr-more' + (open ? ' is-open' : ''),
        'aria-expanded': open ? 'true' : 'false',
        title: 'What we hold on ' + p.name + ' — read-only',
      }, [el('span', { class: 'fr-more-t', text: 'Info' }), lucideIcon('chevron-down')]);
      more.addEventListener('click', ev => {
        ev.stopPropagation();
        const keys = _frDetailScope(_frCtx && _frCtx.chatId);
        const now = !keys.has(p.key);
        if (now) keys.add(p.key); else keys.delete(p.key);
        more.classList.toggle('is-open', now);
        more.setAttribute('aria-expanded', now ? 'true' : 'false');
        // Found by key rather than by nextElementSibling: the role picker is a
        // sibling here too, and either of them may already sit in that slot.
        const existing = [..._frPop.querySelectorAll('.fr-det')].find(n => n.dataset.frDet === p.key);
        if (now) { if (!existing) row.parentNode.insertBefore(_frDetailPanel(p), row.nextSibling); }
        else if (existing) existing.remove();
      });
      actions.appendChild(more);
    }
    row.appendChild(actions);
    return row;
  }

  // Switching is the whole reason this popover exists: it changes WHICH person
  // on the line the app attributes the conversation to, and therefore who a
  // personalised test link is minted for. A link built for the previous person
  // must die with the switch, so the preview is invalidated unconditionally
  // here — never left to the no-op guard inside _numSwitchSelect.
  // A contact-only person cannot be pinned, cannot carry a role and cannot be
  // what the pipeline resolves to — all three live on a `leads` row. So every
  // write about them mints that row first. One gesture for the agent, two
  // calls underneath, and the mint is idempotent server-side (200 when the
  // lead already existed, 201 when it was just created) so a retry after a
  // failed second call can never double the contact.
  // Returns the lead id, or '' after reporting the failure itself.
  async function _frMintLead(ctx, p) {
    // THE LAST LINE. Both write paths on a row funnel through here, so a row
    // that belongs to someone else is refused once, at the boundary, whatever
    // control reached it. The server would answer 403 anyway — this just means
    // we never spend a real client's request finding that out, and the two
    // callers below cannot be made unsafe by a future edit to their buttons.
    if (p.restrictedLead) {
      toast(p.restrictedUnassigned
        ? (p.name + ' already has a lead with no agent assigned — ask a supervisor')
        : (p.owner
          ? (p.name + ' belongs to ' + p.owner.displayName + ' — ask them to take this conversation')
          : (p.name + ' belongs to another agent — ask a supervisor who is on it')), 'error');
      return '';
    }
    if (p.leadId) return p.leadId;
    if (!p.hubspotContactId) {
      toast('This contact has no HubSpot id — reopen the conversation', 'error');
      return '';
    }
    let j = {};
    try {
      const r = await api('/api/leads/by-number/' + encodeURIComponent(ctx.phone) + '/person', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hubspotContactId: p.hubspotContactId, name: p.name }),
      });
      j = await r.json().catch(() => ({}));
      if (!r.ok) {
        // The popover deliberately lists a COLLEAGUE's contact too — hiding a
        // person from the badge is the bug this whole change fixes — so an
        // ordinary agent can land on a 403 here. The server names the owner
        // (and nothing else); say who, instead of a bare "not your lead", so
        // the agent knows who to ask rather than clicking again.
        const owner = j.restricted && j.owner ? userLabel(j.owner.username, j.owner.displayName || j.owner.username || '') : '';
        toast(owner
          ? (p.name + ' belongs to ' + owner + ' — ask them to take this conversation')
          : (j.error || 'Could not open this contact'), 'error');
        return '';
      }
    } catch (err) {
      toast('Network error', 'error');
      return '';
    }
    // POST …/person answers with the whole row ({ lead, created }); the older
    // support2-facing shapes are accepted too rather than guessed at.
    const leadId = String((j.lead && j.lead.id) || j.leadId || j.id || '');
    if (!leadId) { toast('Could not open this contact', 'error'); return ''; }
    // Promote the row in place, so the popover stops calling them lead-less
    // even if the refresh that follows never lands.
    const wasKey = p.key;
    p.leadId = leadId;
    p.isLead = true;
    p.restrictedLead = false; // it is ours now — the row just minted it
    p.key = _frKey(leadId, p.hubspotContactId);
    // The row identity moves from H:<contact> to L:<lead>; carry an expanded
    // detail panel across with it instead of letting the refresh close it.
    if (_frOpenDetails.keys.delete(wasKey)) _frOpenDetails.keys.add(p.key);
    return leadId;
  }

  async function _frSwitchTo(p, btn) {
    if (!_frCtx || _frBusy) return;
    const ctx = _frCtx;
    _frBusy = true;
    if (btn) { btn.disabled = true; btn.textContent = '…'; }
    try {
      const minted = !p.leadId; // read BEFORE the mint writes it back
      const leadId = await _frMintLead(ctx, p);
      if (!leadId) return; // _frMintLead already said why
      const r = await api('/api/leads/by-number/' + encodeURIComponent(ctx.phone) + '/active', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) return void toast(j.error || 'Switch failed', 'error');
      if (typeof _websiteTestInvalidatePreview === 'function') {
        _websiteTestInvalidatePreview('Contact changé — recréez le lien depuis la conversation active');
      }
      if (_frCtx === ctx) {
        ctx.activeId = String(j.activeLeadId || leadId);
        ctx.pinnedId = ctx.activeId; // the switch IS the new team pin
      }
      if (typeof _numSwitchSelect === 'function') _numSwitchSelect(ctx.chatId, leadId);
      if (_frCtx === ctx) { _frPaint(); if (_frPop) _frRenderPopover(); }
      // A row that was just minted carries a stage, an owner and a created_at
      // this client has never seen — re-read the line so its sub-line stops
      // describing a contact with no lead.
      if (minted) await _frRefresh();
      // Only report it on the conversation it happened in — the agent may
      // have moved on while the round-trip was in flight, and a toast naming
      // a person they are no longer looking at reads as a mistake.
      if (String(state.selectedChatId || '') === ctx.chatId) {
        toast('Conversation attribuée à ' + p.name, '');
      }
    } catch (err) {
      toast('Network error', 'error');
    } finally {
      _frBusy = false;
      if (btn?.isConnected) { btn.disabled = false; btn.textContent = 'Switch'; }
    }
  }

  // The role picker doubles as the age prompt: a contact with no age is the one
  // case the ladder cannot solve, and the agent usually knows it from the
  // conversation they are already reading. The age field only appears when the
  // age is missing — correcting a wrong age belongs in the contact profile.
  function _frToggleRolePick(row, p) {
    // THE GATE, not just the missing handler. Saving a role on a person whose
    // lead belongs to someone else goes through _frMintLead and comes back
    // 403, so the picker must be unreachable for such a row by any route — a
    // future caller, a stale closure, a keyboard path on a re-enabled button.
    // The chip that opens this is already rendered without a listener; this is
    // the invariant behind it.
    if (!p || p.restrictedLead) return;
    // Found by KEY, not by nextElementSibling: the read-only detail panel is a
    // sibling in that same slot, and when it is open the old check missed the
    // picker entirely — clicking the chip a second time stopped closing it.
    const open = [..._frPop.querySelectorAll('.fr-pick')].find(n => n.dataset.frKey === p.key);
    if (open) return void open.remove();
    for (const n of [..._frPop.querySelectorAll('.fr-pick')]) n.remove();
    const pick = el('div', { class: 'fr-pick', 'data-fr-key': p.key });
    pick.appendChild(el('div', { class: 'fr-pick-title', text: 'Tag ' + p.name }));
    pick.appendChild(_frSlotPicker({
      currentKey: p.sure && p.rank ? p.role + ':' + p.rank : '',
      unsetOn: !p.sure,
      exceptKey: p.key,
      onPick: (slot, btn) => _frSaveRole(p, slot ? slot.role : null, slot ? slot.rank : null, null, btn),
    }));
    if (p.age == null) {
      const inp = el('input', { type: 'number', min: '1', max: '99', placeholder: 'Age', 'aria-label': 'Age' });
      const ok = el('button', { type: 'button', class: 'fr-pick-btn', text: 'Save age' });
      const save = () => {
        const v = _frAge(inp.value);
        if (v == null) return void toast('Age must be between 1 and 99', 'error');
        // Age only: the tag is left exactly as it is (keepTag).
        _frSaveRole(p, undefined, undefined, v, ok);
      };
      ok.addEventListener('click', ev => { ev.stopPropagation(); save(); });
      inp.addEventListener('keydown', ev => { if (ev.key === 'Enter') { ev.preventDefault(); save(); } });
      pick.appendChild(el('div', { class: 'fr-age' }, [inp, ok]));
    }
    row.parentNode.insertBefore(pick, row.nextSibling);
    refreshIcons(pick);
  }

  // THE SLOT PICKER — the same control on a row and in the add form, so the
  // team learns it once. Parent 1 · Parent 2 on the first line, Kid 1-4 on the
  // second, "Not set" last. A slot somebody else on this line holds is a dead
  // button carrying that person's initials ("taken by …") — the server would
  // answer 409 anyway; this says it before the click.
  //   currentKey  'parent:1' … the slot this person (or the draft) holds
  //   unsetOn     "Not set" is the current state (pressed, nothing to clear)
  //   exceptKey   this person's row key — their own slot is never "taken"
  //   onPick(slot|null, button)
  function _frSlotPicker({ currentKey, unsetOn, exceptKey, onPick }) {
    const grid = el('div', { class: 'fr-slots', role: 'group', 'aria-label': 'Family tag' });
    const btns = [];
    for (const slot of FR_SLOTS) {
      const on = currentKey === slot.key;
      const holder = on ? null : _frSlotHolder(slot, exceptKey);
      const b = el('button', {
        type: 'button',
        class: 'fr-slot-btn fr-role-' + slot.role + (slot.role === 'parent' ? ' is-parent' : '')
          + (on ? ' is-on' : '') + (holder ? ' is-taken' : ''),
        'aria-pressed': on ? 'true' : 'false',
        disabled: holder ? 'disabled' : null,
        'aria-label': slot.label + (holder ? ', taken by ' + holder.name : ''),
        title: holder ? slot.label + ' — taken by ' + holder.name : (on ? slot.label + ' — current tag' : 'Tag as ' + slot.label),
      }, [
        el('span', { class: 'fr-slot-t', text: slot.label }),
        holder ? el('span', { class: 'fr-slot-who', text: initials(holder.name) || '•' }) : null,
      ].filter(Boolean));
      b.addEventListener('click', ev => {
        ev.stopPropagation();
        if (on || holder) return;
        onPick(slot, b);
      });
      btns.push(b);
      grid.appendChild(b);
    }
    const none = el('button', {
      type: 'button',
      class: 'fr-slot-btn is-unset' + (unsetOn ? ' is-on' : ''),
      'aria-pressed': unsetOn ? 'true' : 'false',
      title: unsetOn ? 'Not set — no tag yet' : 'Remove the tag (Not set)',
    }, [el('span', { class: 'fr-slot-t', text: 'Not set' })]);
    none.addEventListener('click', ev => {
      ev.stopPropagation();
      if (unsetOn) return;
      onPick(null, none);
    });
    grid.appendChild(none);
    return grid;
  }

  // role/rank: a tag (role 'parent'|'kid' + rank) or null = Not set.
  // role === undefined = save the AGE only and leave the tag as it is.
  async function _frSaveRole(p, role, rank, age, btn) {
    if (!_frCtx || _frBusy) return;
    const ctx = _frCtx;
    _frBusy = true;
    if (btn) btn.disabled = true;
    try {
      // The tag lives on a lead row (and in the markaz), so a contact-only
      // person needs the lead row before anything about them can be saved.
      const leadId = await _frMintLead(ctx, p);
      if (!leadId) return; // _frMintLead already said why
      const payload = role === undefined
        ? { leadId, keepTag: true }
        : { leadId, role: role || null, rank: role ? rank : null };
      if (age != null) payload.age = String(age);
      const r = await api('/api/leads/by-number/' + encodeURIComponent(ctx.phone) + '/role', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) {
        // Taken by someone this list did not show as taken (a colleague's
        // client, or a tag set in support2 a moment ago). Re-read the line so
        // the picker greys it out, and say who when the server may say it.
        toast(j.error || 'That tag is already taken on this number', 'error');
        await _frRefresh();
        return;
      }
      if (!r.ok) return void toast(j.error || 'Save failed', 'error');
      if (role !== undefined && String(state.selectedChatId || '') === ctx.chatId) {
        const label = role ? FR_SLOTS.find(s => s.role === role && s.rank === rank)?.label : 'Not set';
        toast(p.name + ' → ' + (label || 'saved') + (j.markaz === 'pending' ? ' (syncing to the shared registry)' : ''), 'success');
      }
      await _frRefresh();
    } catch (err) {
      toast('Network error', 'error');
    } finally {
      _frBusy = false;
      if (btn?.isConnected) btn.disabled = false;
    }
  }

  // "Zid wa7ed 3la had r-ra9m" — the missing person, created from the chat.
  // On a lot of lines only the kids are registered and the parent holding the
  // phone has no record at all, so there was nobody to name and the agent had
  // to leave the inbox. The role is picked here, so the new person lands
  // already confirmed instead of being guessed at.
  function _frAddForm() {
    const draft = _frAddDraft || (_frAddDraft = { name: '', tag: '', age: '' });
    const wrap = el('div', { class: 'fr-add' });
    const nameIn = el('input', {
      type: 'text', class: 'fr-add-name', placeholder: 'Full name',
      'aria-label': 'Full name', maxlength: '60', value: draft.name || '',
    });
    const ageIn = el('input', {
      type: 'number', class: 'fr-add-age', min: '1', max: '99',
      placeholder: 'Age', 'aria-label': 'Age (optional)', value: draft.age || '',
    });
    nameIn.addEventListener('input', () => { draft.name = nameIn.value; });
    ageIn.addEventListener('input', () => { draft.age = ageIn.value; });
    // The SAME slot picker as a row's tag chip: a missing parent goes straight
    // in as Parent 1 / Parent 2. `draft.tag` is 'parent:1' … or 'none' for an
    // explicit "Not set"; '' = nothing picked yet (the form asks for a pick).
    // A slot that gets taken while the form is open is greyed on the next
    // refresh, because the picker is rebuilt from the line on every render.
    const roleRow = el('div', { class: 'fr-add-roles' });
    const paintTags = () => {
      clear(roleRow);
      roleRow.appendChild(_frSlotPicker({
        currentKey: draft.tag && draft.tag !== 'none' ? draft.tag : '',
        unsetOn: draft.tag === 'none',
        exceptKey: '',
        onPick: slot => {
          // In the form a pick is a local choice, not a save: pressing the
          // current one again is harmless, and "Not set" is a real choice.
          draft.tag = slot ? slot.key : 'none';
          paintTags();
        },
      }));
    };
    // A draft whose slot was taken meanwhile loses the pick rather than
    // submitting into a 409.
    if (draft.tag && draft.tag !== 'none') {
      const s = FR_SLOTS.find(x => x.key === draft.tag);
      if (!s || _frSlotHolder(s, '')) draft.tag = '';
    }
    paintTags();
    const err = el('div', { class: 'fr-add-err hidden' });
    const save = el('button', { type: 'button', class: 'fr-add-save', text: 'Add' });
    const cancel = el('button', { type: 'button', class: 'fr-pick-btn', text: 'Cancel' });

    const fail = (msg, key) => {
      clear(err);
      err.appendChild(el('span', { text: msg }));
      // The duplicate answer carries whoever it clashed with, so this is an
      // exit instead of a dead end: take me to that person. It is a ROW key,
      // not a lead id — the clash is just as likely to be a contact with no
      // lead row, and one of those has no lead id to point at.
      if (key) {
        const go = el('button', { type: 'button', class: 'fr-add-err-go', text: 'Show me that person' });
        go.addEventListener('click', async ev => {
          ev.stopPropagation();
          await _frRefresh();
          _frHighlight(String(key));
        });
        err.appendChild(go);
      }
      err.classList.remove('hidden');
    };

    // THIS CREATES A REAL CONTACT ON A REAL CLIENT'S LINE, so it is guarded
    // twice over. `save.disabled` only ever swallowed *clicks*: Enter
    // auto-repeats ~15x/second while held and the input is never blurred, so
    // the keydown path could fire N submits before the first POST answered —
    // N HubSpot contacts and N leads, since the client dup check reads a list
    // that is stale for every concurrent call and the server's own guard is a
    // read-then-write with no lock. The first line of the function is the
    // real gate; _frBusy is the second, and it also stops an add from racing
    // a role save or a switch.
    const submit = async () => {
      if (save.disabled || _frBusy) return;
      const ctx = _frCtx;
      if (!ctx) return;
      err.classList.add('hidden');
      const name = String(nameIn.value || '').replace(/\s+/g, ' ').trim();
      if (name.length < 2) return fail('Enter the full name.');
      if (!draft.tag) return fail('Pick a tag: Parent 1 / Parent 2 / Kid 1-4, or Not set.');
      const slot = draft.tag === 'none' ? null : FR_SLOTS.find(x => x.key === draft.tag);
      if (ageIn.value && _frAge(ageIn.value) == null) return fail('Age must be between 1 and 99.');
      // The same refusal the server makes, said here first so the agent never
      // watches a round-trip just to be told the name is taken. ctx.people now
      // carries the contact-only people too, so a name that is on this line
      // ONLY as a HubSpot contact is caught here instead of coming back as a
      // server 409.
      const norm = v => String(v || '').toLowerCase().normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
      const clash = ctx.people.find(x => norm(x.name) === norm(name));
      if (clash) return fail('Already on this number.', clash.key);
      _frBusy = true;
      save.disabled = true;
      save.textContent = '…';
      try {
        const r = await api('/api/leads/by-number/' + encodeURIComponent(ctx.phone) + '/add-person', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name, role: slot ? slot.role : null, rank: slot ? slot.rank : null, age: ageIn.value || '',
          }),
        });
        const j = await r.json().catch(() => ({}));
        if (r.status === 409 && j.code === 'slot_taken') {
          // The tag, not the person: someone on the line already holds it, and
          // nobody was created. Said as a toast because the refresh below
          // rebuilds this form (the draft — name, age — survives it, the stale
          // pick is dropped and the slot comes back greyed out).
          toast((j.error || 'That tag is already taken on this number') + ' — pick another tag', 'error');
          draft.tag = '';
          await _frRefresh();
          return;
        }
        if (r.status === 409) return fail(j.error || 'Already on this number.', _frKey(j.leadId, j.hubspotContactId));
        if (!r.ok) return fail(j.error || 'Could not add');
        if (String(state.selectedChatId || '') === ctx.chatId) {
          // tagError: the slot was taken between the check and the save (two
          // agents on one line) — the person was added WITHOUT a tag.
          if (j.tagError) toast(name + ' added. ' + j.tagError, 'error');
          else toast('Added on this number: ' + name + (slot ? ' · ' + slot.label : ''), 'success');
        }
        _frAddDraft = null; // done — do not restore the form after the refresh
        await _frRefresh();
        if (j.leadId) _frHighlight(_frKey(j.leadId, ''));
      } catch (e) {
        fail('Network error');
      } finally {
        _frBusy = false;
        if (save.isConnected) { save.disabled = false; save.textContent = 'Add'; }
      }
    };
    save.addEventListener('click', ev => { ev.stopPropagation(); submit(); });
    cancel.addEventListener('click', ev => {
      ev.stopPropagation();
      _frAddDraft = null;
      _frRenderPopover();
    });
    nameIn.addEventListener('keydown', ev => { if (ev.key === 'Enter') { ev.preventDefault(); submit(); } });
    ageIn.addEventListener('keydown', ev => { if (ev.key === 'Enter') { ev.preventDefault(); submit(); } });

    wrap.appendChild(el('div', { class: 'fr-add-title', text: 'Add someone on this number' }));
    wrap.appendChild(nameIn);
    wrap.appendChild(roleRow);
    wrap.appendChild(el('div', { class: 'fr-add-line' }, [ageIn, save, cancel]));
    wrap.appendChild(err);
    wrap.appendChild(el('div', {
      class: 'fr-add-note',
      text: 'Creates a new contact on this number. Do not add someone who is already listed.',
    }));
    // Focus only a FRESH form. A restored draft must not yank the caret away
    // from wherever the agent already is after a background refresh repainted
    // the popover under them.
    if (!draft.name && !draft.age && !draft.tag) {
      setTimeout(() => { try { nameIn.focus(); } catch (e) { /* not focusable yet */ } }, 0);
    }
    return wrap;
  }

  // Takes a ROW key (see _frKey), not a lead id — the row it has to find may
  // belong to someone who has no lead row at all.
  function _frHighlight(key) {
    if (!key) return;
    const row = _frPop?.querySelector('.fr-row[data-fr-key="' + String(key).replace(/["\\]/g, '') + '"]');
    if (!row) return;
    row.classList.add('is-flash');
    row.scrollIntoView({ block: 'nearest' });
    setTimeout(() => row.classList.remove('is-flash'), 1600);
  }

  // ============ Encaissement modal (Paid → montant + pack) ============
  // Static markup in index.html (#paidAmountModal); the global
  // [data-modal-close] wiring covers backdrop / Annuler / ×.
  let _paidModalLeadId = null;
  // { leadId, level, editable } once the teacher-level lookup for the open
  // modal has been applied — the HubSpot prefill must not repaint after it.
  let _paidTeacherLevelState = null;
  let _paidModalWired = false;
  function _paidParseAmount(raw) {
    const n = parseFloat(String(raw || '').replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  // Payment-form select options. Primary source = live HubSpot schema
  // (/api/hubspot/properties/contacts/full); CONTACTS.propsByName is reused when
  // the Contacts page was already opened; a hardcoded fallback keeps the modal
  // usable during a HubSpot outage. Cached once per session.
  let _paidFormSchema = null;
  const _PAID_FALLBACK_OPTS = {
    statuts_of_payments: ['Pending Payment', 'Received', 'Not Received'],
    client_bank: ['Cih', 'Barid bank', 'Tijari Bank', 'Banque Populaire', 'Cash Plus', 'Wafa Cash', 'BMCE', 'Credit arigcole', 'Bank Of Africa', 'Paypal', 'Moneygram', 'Western Union', 'Other Bank', 'wise', 'oumnia bank', 'crédit du maroc', 'saham', 'Bmci', 'Taptap send', 'Sendwave', 'Ria', 'Remitly', 'Foreign bank', 'Payment link'],
    our_bank: ['Lougha Bank', 'CIH', 'Barid Bank', 'Attijariwafa bank', 'Banque Populaire', 'CashPlus', 'Wafacash', 'Wafacash Account', 'Paypal', 'CIH Mme Fatima', 'CIH Mme Houda', 'Wise', 'Taptap Send', 'Sendwave', 'Ria', 'Western union', 'Remitly', 'MoneyGram', 'Foreign bank', 'oumnia bank', 'crédit du maroc', 'saham', 'Bmci', 'LLC Stripe', 'Loghat', 'Whop'],
    rest: ['100', '150', '200', '250', '300', '400', '450', '500', '600', '700', '750', '800', '900', '1000', '1200', '1300', '1400', '1500', '1600', '1700', '2000', '2100', '2300', '3360'],
    amount: ['150', '200', '300', '400', '500', '600', '700', '750', '800', '900', '1000', '1200', '1300', '1400', '1500', '1600', '1800', '2000', '2100', '2250', '2500', '2700', '3000', '3200', '3800', '4000', '5000'],
    paid_status: ['Rest', 'Total', 'Avance', 'Réserve'],
    confirmation_status: ['Sans Test', 'with Test', 'clt reffrl', 'influencer', 'written test'],
    pack: ['English-lik', 'Pack American', 'Pack Business', 'Individual classes', 'Club', 'pack starter', 'Tahfiz', 'English Kids'],
    pack_duration: ['1 Mois', '2 Mois', '3 Mois', '6 Mois', '7 Mois', '8 Mois', '9 Mois', '10 Mois', '12 Mois'],
    pack_hours: ['8', '10', '15', '20', '24', '30', '36', '40', '50', '80', '120', '160'],
    level: ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Beg 0', 'Beg 1', 'Beg 2', 'Beg 3', 'Intermediate 1', 'Intermediate 2', 'Intermediate 3', 'Advanced'],
    student_status: ['pending', 'active', 'inactive', 'expired', 'done', 'pause', 'renewed', 'left', 'trial class'],
    groups: [],
  };
  const _PAID_OPT_KEYS = ['statuts_of_payments', 'client_bank', 'our_bank', 'rest', 'amount', 'paid_status', 'confirmation_status', 'pack', 'pack_duration', 'pack_hours', 'level', 'student_status', 'groups'];
  function _paidPickOpts(byName) {
    const out = {};
    for (const k of _PAID_OPT_KEYS) {
      const def = byName && byName[k];
      const opts = def && Array.isArray(def.options)
        ? def.options.map(o => ({ label: o.label || o.value, value: o.value })).filter(o => o.value)
        : null;
      out[k] = (opts && opts.length) ? opts : _PAID_FALLBACK_OPTS[k].map(v => ({ label: v, value: v }));
    }
    return out;
  }
  async function _paidLoadPaymentSchema() {
    // 1. Contacts page already loaded the catalog → reuse (no fetch).
    if (typeof CONTACTS !== 'undefined' && CONTACTS.propsByName && Object.keys(CONTACTS.propsByName).length) {
      return _paidPickOpts(CONTACTS.propsByName);
    }
    if (_paidFormSchema) return _paidFormSchema;
    try {
      const r = await api('/api/hubspot/properties/contacts/full');
      if (r.ok) {
        const j = await r.json();
        const byName = {};
        (j.properties || []).forEach(p => { if (p && p.name) byName[p.name] = p; });
        _paidFormSchema = _paidPickOpts(byName);
        return _paidFormSchema;
      }
    } catch (_) { /* fall through to hardcoded */ }
    _paidFormSchema = _paidPickOpts(null); // all fallbacks
    return _paidFormSchema;
  }
  // Muted "placeholder" look on empty selects so filled vs blank scans instantly.
  function _paidSyncSelectEmpty(sel) {
    if (sel) sel.classList.toggle('is-empty', !sel.value);
  }
  function _paidFillSelect(sel, opts, { placeholder } = {}) {
    if (!sel) return;
    sel.innerHTML = '';
    if (placeholder) {
      const o = document.createElement('option');
      o.value = ''; o.textContent = placeholder;
      sel.appendChild(o);
    }
    for (const opt of opts) {
      const o = document.createElement('option');
      o.value = opt.value; o.textContent = opt.label;
      sel.appendChild(o);
    }
    if (!sel._emptyWired) {
      sel._emptyWired = true;
      sel.addEventListener('change', () => _paidSyncSelectEmpty(sel));
    }
    _paidSyncSelectEmpty(sel);
  }
  // Styled combobox for the Montant / Reste inputs — replaces the ugly native
  // <datalist> with a clean HubSpot-style dropdown: type-to-filter, click or
  // arrow+Enter to pick, still free-typeable. The panel is fixed + body-appended
  // so it escapes the modal-body's overflow clipping. Call ._comboSetOptions(vals)
  // to (re)load the option list.
  function _paidAttachCombo(input, values) {
    if (!input || input._comboAttached) return;
    input._comboAttached = true;
    input.setAttribute('autocomplete', 'off');
    input.removeAttribute('list');
    const panel = document.createElement('div');
    panel.className = 'paid-combo-panel hidden';
    document.body.appendChild(panel);
    let opts = Array.isArray(values) ? values.slice() : [];
    let openState = false, activeIdx = -1, shown = [], noFilterMode = true;
    const position = () => {
      const r = input.getBoundingClientRect();
      const ph = panel.offsetHeight || 300;
      // Flip ABOVE the input when there isn't room below (long lists near the
      // bottom of the modal) so the options are never off-screen.
      const roomBelow = window.innerHeight - r.bottom;
      const top = (roomBelow < ph + 10 && r.top > ph + 10) ? (r.top - ph - 4) : (r.bottom + 4);
      panel.style.top = Math.round(top) + 'px';
      panel.style.left = Math.round(r.left) + 'px';
      panel.style.width = Math.round(r.width) + 'px';
    };
    const norm = (s) => String(s == null ? '' : s).replace(/\s/g, '');
    const render = () => {
      // Space-insensitive filter so a pre-filled "1 500" still matches "1500".
      const f = noFilterMode ? '' : norm(input.value);
      shown = f ? opts.filter(v => norm(v).includes(f)) : opts.slice();
      if (activeIdx >= shown.length) activeIdx = shown.length - 1;
      panel.innerHTML = '';
      if (!shown.length) { const e = document.createElement('div'); e.className = 'paid-combo-empty'; e.textContent = 'Aucun montant'; panel.appendChild(e); return; }
      let selEl = null;
      shown.forEach((v, i) => {
        const o = document.createElement('div');
        const isSel = norm(v) === norm(input.value);
        o.className = 'paid-combo-opt' + (i === activeIdx ? ' is-active' : '') + (isSel ? ' is-sel' : '');
        o.textContent = v;
        o.addEventListener('mousedown', (ev) => { ev.preventDefault(); pick(v); });
        panel.appendChild(o);
        if (isSel) selEl = o;
      });
      if (selEl) selEl.scrollIntoView({ block: 'nearest' });
    };
    // Open shows ALL options (highlights the current value); typing filters.
    // Render → unhide → position so we can measure the panel height (for flip).
    const show = () => { openState = true; activeIdx = -1; noFilterMode = true; render(); panel.classList.remove('hidden'); position(); };
    const hide = () => { openState = false; panel.classList.add('hidden'); };
    const pick = (v) => { input.value = v; hide(); input.dispatchEvent(new Event('input', { bubbles: true })); };
    // Open on click / typing / ArrowDown — NOT on focus, so it doesn't auto-pop
    // when the modal opens and the amount field auto-focuses.
    input.addEventListener('click', () => { if (!openState) show(); });
    input.addEventListener('input', () => { activeIdx = -1; if (!openState) show(); noFilterMode = false; render(); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { hide(); return; }
      if (!openState) { if (e.key === 'ArrowDown') show(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); activeIdx = Math.min(shown.length - 1, activeIdx + 1); render(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); activeIdx = Math.max(0, activeIdx - 1); render(); }
      else if (e.key === 'Enter' && activeIdx >= 0 && shown[activeIdx] != null) { e.preventDefault(); e.stopImmediatePropagation(); pick(shown[activeIdx]); }
    });
    input.addEventListener('blur', () => setTimeout(hide, 120));
    document.addEventListener('mousedown', (e) => { if (openState && e.target !== input && !panel.contains(e.target)) hide(); });
    // Hide when an OUTER container scrolls (the input scrolls away from the fixed
    // panel), but NOT when the user scrolls INSIDE the panel itself — otherwise
    // scrolling the option list to reach lower amounts would close the dropdown.
    document.addEventListener('scroll', (e) => { if (openState && !panel.contains(e.target)) hide(); }, true);
    window.addEventListener('resize', () => { if (openState) position(); });
    input._comboSetOptions = (vals) => { opts = Array.isArray(vals) ? vals.slice() : []; if (openState) render(); };
  }
  async function _paidPopulateFormOptions() {
    const schema = await _paidLoadPaymentSchema();
    // Only paint if the modal is still open (async race with a fast close).
    if (document.getElementById('paidAmountModal')?.classList.contains('hidden')) return;
    _paidFillSelect(document.getElementById('paidClientBank'), schema.client_bank, { placeholder: '— aucune —' });
    _paidFillSelect(document.getElementById('paidOurBank'), schema.our_bank, { placeholder: '— aucune —' });
    _paidFillSelect(document.getElementById('paidPaidStatus'), schema.paid_status, { placeholder: '— auto (selon reste) —' });
    _paidFillSelect(document.getElementById('paidConfirmationStatus'), schema.confirmation_status, { placeholder: '— aucun —' });
    _paidFillSelect(document.getElementById('paidPackInput'), schema.pack, { placeholder: '— aucun —' });
    _paidFillSelect(document.getElementById('paidPackDuration'), schema.pack_duration, { placeholder: '— aucune —' });
    _paidFillSelect(document.getElementById('paidPackHours'), schema.pack_hours, { placeholder: '— aucune —' });
    _paidFillSelect(document.getElementById('paidLevel'), schema.level, { placeholder: '— aucun —' });
    _paidFillSelect(document.getElementById('paidStudentStatus'), schema.student_status, { placeholder: '— aucun —' });
    // Groups: searchable combo (~1000 options).
    const groupsInput = document.getElementById('paidGroups');
    if (groupsInput && groupsInput._comboSetOptions) groupsInput._comboSetOptions((schema.groups || []).map(o => o.value));
    // Feed the styled comboboxes. Reste keeps its preset amounts; Montant offers
    // the amounts curated in the HubSpot `amount` property (sorted ascending) so
    // the agent picks one instead of retyping.
    const restInput = document.getElementById('paidRest');
    if (restInput && restInput._comboSetOptions) restInput._comboSetOptions((schema.rest || []).map(o => o.value));
    const amtInput = document.getElementById('paidAmountInput');
    if (amtInput && amtInput._comboSetOptions) {
      const amts = (schema.amount || []).map(o => o.value).sort((a, b) => (Number(a) || 0) - (Number(b) || 0));
      amtInput._comboSetOptions(amts);
    }
  }
  // HubSpot date properties read back as 'YYYY-MM-DD' on v3, but older rows can
  // still carry epoch-ms — normalise both to what <input type="date"> accepts.
  function _paidHsDate(v) {
    const s = String(v == null ? '' : v).trim();
    if (!s) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = new Date(/^\d{10,}$/.test(s) ? Number(s) : s);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA', { timeZone: 'UTC' });
  }
  // "Sessions / semaine" (2026-10-16): what the students7 app gives with the
  // chosen pack — the same rule as students7 (lib/leads/sessions-per-week.js on
  // the server). Two packs → the agent picks the total and the server writes it
  // as the student's exception until the pack ends.
  function _paidPackWeeklyRate(pack) {
    const s = String(pack || '');
    const indiv = ['individual classes', 'ind general english/m', 'ind general english/f',
      'ind business english/m', 'ind business english/f', 'one-on-one ielts/toefl',
      'one-on-one general english', 'one-on-one esp', 'one-on-one business english'];
    if (indiv.includes(s.trim().toLowerCase())) return null;
    const m = /(?:^|[^a-z0-9])(?:(\d{1,2})\s*s|s\s*(\d{1,2}))(?![a-z0-9])/i.exec(s);
    const n = m ? parseInt(m[1] || m[2], 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 2;
  }
  function _paidSessionsHint() {
    const hint = document.getElementById('paidSessionsHint');
    if (!hint) return;
    const rate = _paidPackWeeklyRate(document.getElementById('paidPackInput')?.value || '');
    const chosen = document.getElementById('paidSessionsPerWeek')?.value || '';
    if (rate == null) {
      hint.textContent = 'Cours individuel : pas de limite par semaine dans l\'app.';
    } else if (chosen && Number(chosen) !== rate) {
      hint.textContent = 'L\'app donnera ' + chosen + ' / semaine (le pack seul donne ' + rate + ') jusqu\'à la fin du pack.';
    } else {
      hint.textContent = 'Le pack donne ' + rate + ' / semaine dans l\'app. Deux packs ? Choisissez le total (ex : ' + (rate * 2) + ').';
    }
  }
  document.addEventListener('change', (e) => {
    if (e.target && (e.target.id === 'paidPackInput' || e.target.id === 'paidSessionsPerWeek')) _paidSessionsHint();
  });
  // The Paid modal's method chips, from the shared list — rendered once, on
  // the first open, before the one-time wiring below attaches to them.
  function _paidRenderModes() {
    const box = document.getElementById('paidModes');
    if (!box || box.querySelector('input[type=checkbox]')) return;
    for (const m of paymentModes()) {
      const input = el('input', { type: 'checkbox' });
      input.dataset.modeVal = m.value;
      box.appendChild(el('label', { class: 'paid-mode-chip' }, [input, el('span', { text: m.label })]));
    }
  }
  function showPaidModal(leadId, prefill = null) {
    _paidModalLeadId = leadId;
    _paidRenderModes();
    const amountInput = document.getElementById('paidAmountInput');
    const packInput = document.getElementById('paidPackInput');
    const errEl = document.getElementById('paidAmountError');
    const btn = document.getElementById('paidConfirmBtn');
    if (amountInput) { amountInput.value = ''; amountInput.classList.remove('is-invalid'); }
    if (packInput) packInput.value = '';
    if (errEl) errEl.classList.add('hidden');
    document.getElementById('paidPrefillHint')?.classList.add('hidden');
    const whoEl = document.getElementById('paidModalWho');
    if (whoEl) whoEl.textContent = '';
    if (btn) { btn.disabled = false; btn.textContent = "Confirmer l'encaissement"; }
    // Reset the payment-form fields. Date de paiement defaults to today.
    const today = MA.dayKey(); // YYYY-MM-DD
    const setVal = (id, v) => { const e = document.getElementById(id); if (e) e.value = v; };
    setVal('paidDateOfPayment', today);
    // A payment cannot have happened tomorrow, and the server refuses a future
    // date for paid_at (it would hide the money in a month nobody reads), so stop
    // it at the picker instead of letting the backend silently fall back.
    const dopEl = document.getElementById('paidDateOfPayment');
    if (dopEl) dopEl.max = today;
    setVal('paidDateOfRest', '');
    setVal('paidRest', '');
    setVal('paidSentBy', '');
    setVal('paidClientBank', '');
    setVal('paidOurBank', '');
    ['paidPaidStatus', 'paidConfirmationStatus', 'paidNotePayment', 'paidPackDuration', 'paidPackHours', 'paidLevel', 'paidStudentStatus', 'paidStudentNote', 'paidGroups', 'paidSessionsPerWeek'].forEach(id => setVal(id, ''));
    _paidSessionsHint();
    // Client identity + student dates (2026-08-22) — cleared here, then pre-filled
    // from the HubSpot contact by the best-effort fetch further down.
    ['paidFirstName', 'paidLastName', 'paidEmail', 'paidAge', 'paidCity',
      'paidDateOfFirstSession', 'paidDateOfEnd'].forEach(id => setVal(id, ''));
    document.getElementById('paidEmailError')?.classList.add('hidden');
    // Clear any leftover per-field invalid highlights from a previous attempt.
    document.querySelectorAll('#paidAmountModal .is-invalid').forEach(e => e.classList.remove('is-invalid'));
    document.querySelectorAll('#paidModes input[type=checkbox]').forEach(c => { c.checked = false; });
    document.getElementById('paidFormError')?.classList.add('hidden');
    if (!_paidModalWired) {
      _paidModalWired = true;
      // Styled dropdowns for Montant + Reste (attached before the Enter handler so
      // Enter picks a highlighted option before it can submit the form).
      _paidAttachCombo(amountInput, []);
      _paidAttachCombo(document.getElementById('paidRest'), []);
      _paidAttachCombo(document.getElementById('paidGroups'), []);
      // Inline recovery: fixing a required field clears its red state immediately.
      document.getElementById('paidDateOfPayment')?.addEventListener('input', e => e.target.classList.remove('is-invalid'));
      document.getElementById('paidEmail')?.addEventListener('input', e => {
        e.target.classList.remove('is-invalid');
        document.getElementById('paidEmailError')?.classList.add('hidden');
      });
      document.querySelectorAll('#paidModes input[type=checkbox]').forEach(c =>
        c.addEventListener('change', () => document.getElementById('paidModes')?.classList.remove('is-invalid')));
      amountInput?.addEventListener('keydown', e => { if (e.key === 'Enter') _paidConfirm(); });
      amountInput?.addEventListener('input', () => {
        amountInput.classList.remove('is-invalid');
        errEl?.classList.add('hidden');
      });
      btn?.addEventListener('click', _paidConfirm);
    }
    openModal('paidAmountModal');
    // Receipt prefill (semi-auto Paid): the flag-message flow passes the
    // Gemini-extracted montant/date/bank — fill them, flag the montant with
    // an amber "vérifier" hint. Runs before the HubSpot pre-fill below, whose
    // `!inp.value` guard then leaves the receipt amount alone.
    if (prefill && Number(prefill.amount) > 0 && amountInput) {
      amountInput.value = String(Math.round(Number(prefill.amount))).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      document.getElementById('paidPrefillHint')?.classList.remove('hidden');
    }
    if (prefill && prefill.date) setVal('paidDateOfPayment', prefill.date);
    // Populate the select/datalist options (lazy; safe if it lands after paint).
    // Held as a promise: the HubSpot pre-fill below awaits it before touching a
    // <select>, because assigning .value to a select with no options is silently
    // dropped — that is why the payment block used to come up blank.
    const optionsReady = _paidPopulateFormOptions().then(() => {
      // Bank best-effort: options only exist after the populate — exact
      // case-insensitive match on the extracted bank name, never overwrite.
      if (prefill && prefill.clientBank && _paidModalLeadId === leadId) {
        const sel = document.getElementById('paidClientBank');
        const want = String(prefill.clientBank).trim().toLowerCase();
        const opt = sel && Array.from(sel.options).find(o => o.value && o.value.trim().toLowerCase() === want);
        if (opt && !sel.value) { sel.value = opt.value; _paidSyncSelectEmpty(sel); }
      }
    }).catch(() => { /* fallbacks already applied inside */ });
    // Pre-fill the WHOLE form from the HubSpot contact (Ayoub, 2026-08-30:
    // "kol chi li 3amer f HubSpot khasso y3mer f l'form"). Mary reported the
    // form coming up blank on contacts HubSpot already knows everything about —
    // measured on the production mirror, of 10,393 contacts carrying a
    // paid_status the contact already holds mode_de_paiment 99%, client_bank
    // and our_bank 98%, pack/duration/level 99%, student_status 99%, groups 96%.
    // Only the Client block used to be pre-filled, so she retyped the rest by
    // hand every time.
    //
    // Two invariants keep this safe:
    //   · fill ONLY empty fields — a pre-fill landing late must lose to a human
    //     keystroke, and to the receipt-extracted values from a semi-auto Paid;
    //   · the server skips blanks, so writing a value straight back is a no-op.
    // Date de paiement is deliberately NOT pre-filled: it already carries
    // today's date, and this encaissement is a new payment, not the old one.
    //
    // Reste, Date du reste and Mode now arrive pre-filled from the LAST payment.
    // That is what was asked for, and the section legends say "pré-rempli depuis
    // HubSpot" so they read as values to check, not as values typed here.
    (async () => {
      try {
        const chatId = _profilePersonChatId();
        if (!chatId) return;
        // Shared number (parent + child): after a Switch, pre-fill from the
        // PICKED person's contact — the number's default contact is the other
        // family member (2026-09-25: the child's form came up as the mother).
        const switched = !!state.selectedChatId && _activeLeadIdByChat[state.selectedChatId] === leadId;
        const r = await api('/api/hubspot/contact-properties-full?chatId=' + encodeURIComponent(chatId)
          + (switched ? '&leadId=' + encodeURIComponent(leadId) : ''));
        if (!r.ok) return;
        const j = await r.json();
        if (_paidModalLeadId !== leadId) return; // modal closed / moved on
        const byName = {};
        (j.properties || []).forEach(p => { if (p && p.name) byName[p.name] = p.value; });
        // Say whose encaissement this is — on a shared number it is the only
        // way to tell the mother's form from the child's.
        const who = document.getElementById('paidModalWho');
        const whoName = j.lead?.name || [byName.firstname, byName.lastname].filter(Boolean).join(' ').trim();
        if (who) who.textContent = whoName ? ' · ' + whoName : '';
        const fillIfEmpty = (id, v) => {
          const e = document.getElementById(id);
          if (e && !e.value && v) e.value = String(v);
        };
        // Free-text, date and combo inputs need no option list — fill them now.
        fillIfEmpty('paidFirstName', byName.firstname);
        fillIfEmpty('paidLastName', byName.lastname);
        fillIfEmpty('paidEmail', byName.email);
        fillIfEmpty('paidAge', byName.age);
        fillIfEmpty('paidCity', byName.city);
        fillIfEmpty('paidDateOfFirstSession', _paidHsDate(byName.date_of_first_session));
        fillIfEmpty('paidDateOfEnd', _paidHsDate(byName.date_of_end));
        fillIfEmpty('paidSentBy', byName.sent_by);
        fillIfEmpty('paidNotePayment', byName.note_payment);
        fillIfEmpty('paidStudentNote', byName.student_note);
        fillIfEmpty('paidRest', byName.rest);
        fillIfEmpty('paidDateOfRest', _paidHsDate(byName.date_of_rest));
        fillIfEmpty('paidGroups', byName.groups);
        // Mode de paiement is a multi-checkbox: tick what the contact carries,
        // but only while the agent has ticked nothing — never re-tick over them.
        const modeBoxes = Array.from(document.querySelectorAll('#paidModes input[type=checkbox]'));
        if (modeBoxes.length && !modeBoxes.some(c => c.checked) && byName.mode_de_paiment) {
          const want = String(byName.mode_de_paiment).split(';')
            .map(x => x.trim().toLowerCase()).filter(Boolean);
          modeBoxes.forEach(c => {
            if (want.includes(String(c.dataset.modeVal || '').toLowerCase())) c.checked = true;
          });
          if (modeBoxes.some(c => c.checked)) {
            document.getElementById('paidModes')?.classList.remove('is-invalid');
          }
        }
        if (byName.amount) {
          // `amount` can hold multiple tranches ("750;300") — sum them.
          const sum = String(byName.amount).split(';')
            .map(s => parseFloat(String(s).replace(/\s/g, '').replace(',', '.')))
            .filter(Number.isFinite).reduce((a, b) => a + b, 0);
          const inp = document.getElementById('paidAmountInput');
          if (sum > 0 && inp && !inp.value && _paidModalLeadId === leadId) {
            inp.value = String(Math.round(sum)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
          }
        }
        // The <select>s hold nothing until the schema fetch has painted their
        // options, so wait for it. Awaiting also lets the receipt-extracted bank
        // land first — that one describes THIS payment and must win.
        await optionsReady;
        if (_paidModalLeadId !== leadId) return;
        const fillSelectIfEmpty = (id, v) => {
          const sel = document.getElementById(id);
          if (!sel || sel.value || v == null || String(v).trim() === '') return;
          const want = String(v).trim().toLowerCase();
          const opt = Array.from(sel.options)
            .find(o => o.value && o.value.trim().toLowerCase() === want);
          // A value HubSpot holds but this property's option list no longer
          // offers stays blank rather than being invented into the dropdown.
          if (!opt) return;
          sel.value = opt.value;
          _paidSyncSelectEmpty(sel);
        };
        fillSelectIfEmpty('paidPaidStatus', byName.paid_status);
        fillSelectIfEmpty('paidClientBank', byName.client_bank);
        fillSelectIfEmpty('paidOurBank', byName.our_bank);
        fillSelectIfEmpty('paidConfirmationStatus', byName.confirmation_status);
        fillSelectIfEmpty('paidPackInput', byName.pack);
        fillSelectIfEmpty('paidPackDuration', byName.pack_duration);
        fillSelectIfEmpty('paidPackHours', byName.pack_hours);
        // Level: the teacher-level lookup (_paidApplyTeacherLevel) has the
        // last word — never repaint HubSpot's value over a deliberate blank.
        if (!(_paidTeacherLevelState && _paidTeacherLevelState.leadId === leadId)) fillSelectIfEmpty('paidLevel', byName.level);
        fillSelectIfEmpty('paidStudentStatus', byName.student_status);
        _paidSessionsHint(); // the pack may just have been filled in
      } catch (_) { /* pre-fill is best-effort */ }
    })();
    _paidApplyTeacherLevel(leadId, optionsReady);
    setTimeout(() => amountInput?.focus(), 60);
  }
  // Level = the teacher's placement-test level (Ayoub, 2026-09-07). Runs after
  // the option lists are painted (a select with no options drops .value) and
  // AFTER the HubSpot prefill above, so the teacher level always wins over the
  // contact's `level` property. It is a PREFILL, not a lock: the whole team may
  // change it (Ayoub, 2026-09-10). `editable` still comes from the server so a
  // re-locked policy disables the select without a client change.
  async function _paidApplyTeacherLevel(leadId, optionsReady) {
    const sel = document.getElementById('paidLevel');
    const hint = document.getElementById('paidLevelHint');
    if (!sel) return;
    let editable = true;
    sel.disabled = false;
    if (hint) { hint.textContent = ''; hint.classList.add('hidden'); }
    _paidTeacherLevelState = null;
    let info = null;
    try {
      const r = await api('/api/teacher-level?leadId=' + encodeURIComponent(leadId));
      if (r.ok) info = await r.json();
    } catch { /* best-effort — the server enforces the rule anyway */ }
    try { await optionsReady; } catch { /* options fallbacks already applied */ }
    if (_paidModalLeadId !== leadId) return;
    editable = !!(info?.editable ?? true);
    sel.disabled = !editable;
    const level = String(info?.level || '').trim();
    if (level) {
      let opt = Array.from(sel.options).find(o => o.value && o.value.replace(/\s+/g, '').toUpperCase() === level);
      if (!opt) { opt = el('option', { value: level, text: level }); sel.appendChild(opt); }
      sel.value = opt.value;
    } else if (!editable) {
      sel.value = '';
    }
    _paidSyncSelectEmpty(sel);
    _paidTeacherLevelState = { leadId, level, editable };
    if (hint) {
      const who = info?.teacher ? ' (' + info.teacher + (info.slotDate ? ', ' + info.slotDate : '') + ')' : '';
      hint.textContent = level
        ? (editable ? 'Niveau du test : ' + level + who + ' — modifiable si besoin.'
                    : 'Niveau saisi par le professeur lors du test' + who + ' — non modifiable.')
        : (editable ? 'Aucun test évalué — niveau à saisir.'
                    : 'Aucun test évalué pour ce client — le niveau restera vide.');
      hint.classList.remove('hidden');
    }
  }
  async function _paidConfirm() {
    const amountInput = document.getElementById('paidAmountInput');
    const packInput = document.getElementById('paidPackInput');
    const errEl = document.getElementById('paidAmountError');
    const btn = document.getElementById('paidConfirmBtn');
    const amount = _paidParseAmount(amountInput?.value);
    if (!amount) {
      amountInput?.classList.add('is-invalid');
      errEl?.classList.remove('hidden');
      amountInput?.focus();
      return;
    }
    const pack = (packInput?.value || '').trim() || null;
    // Required payment-form fields (per Ayoub): Montant (above) + Date + Mode + Statut.
    // Error-clarity + focus-management (ui-ux-pro-max): light up EACH missing field,
    // name them in the message, and focus the first one — not just a generic line.
    const gv = (id) => (document.getElementById(id)?.value || '').trim();
    const modes = Array.from(document.querySelectorAll('#paidModes input[type=checkbox]:checked')).map(c => c.dataset.modeVal);
    const dateOfPayment = gv('paidDateOfPayment');
    const formErr = document.getElementById('paidFormError');
    const missing = [];
    const mark = (id, bad) => { const e = document.getElementById(id); if (e) e.classList.toggle('is-invalid', bad); return bad; };
    if (mark('paidDateOfPayment', !dateOfPayment)) missing.push(['Date de paiement', 'paidDateOfPayment']);
    if (mark('paidModes', !modes.length)) missing.push(['Mode de paiement', 'paidModes']);
    // Email is optional, but a typo would be pushed straight to the HubSpot
    // contact — reject the obviously malformed rather than write it.
    const email = gv('paidEmail');
    const emailBad = !!email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
    document.getElementById('paidEmail')?.classList.toggle('is-invalid', emailBad);
    document.getElementById('paidEmailError')?.classList.toggle('hidden', !emailBad);
    if (emailBad) missing.push(['Email', 'paidEmail']);
    if (missing.length) {
      if (formErr) {
        formErr.textContent = 'Champs requis manquants : ' + missing.map(m => m[0]).join(' · ');
        formErr.classList.remove('hidden');
      }
      // Focus the first missing field (checkbox group → its first checkbox).
      const first = document.getElementById(missing[0][1]);
      (first?.matches('input,select') ? first : first?.querySelector('input'))?.focus();
      return;
    }
    if (formErr) formErr.classList.add('hidden');
    const payFields = {
      date_of_payment: dateOfPayment,
      // The "Statut du paiement" select was removed from the form (Ayoub,
      // 2026-08-22): an encaissement IS money received, and the select was
      // always left on its "Received" default. Kept in the payload so the
      // downstream verification / rest flows still see the same value.
      statuts_of_payments: 'Received',
      mode_de_paiment: modes.join(';'),
      rest: _paidParseAmount(gv('paidRest')),   // null when blank/0
      date_of_rest: gv('paidDateOfRest') || null,
      client_bank: gv('paidClientBank') || null,
      our_bank: gv('paidOurBank') || null,
      sent_by: gv('paidSentBy') || null,
      note_payment: gv('paidNotePayment') || null,
      paid_status: gv('paidPaidStatus') || null,   // explicit; server derives from reste if blank
      confirmation_status: gv('paidConfirmationStatus') || null,
      pack_duration: gv('paidPackDuration') || null,
      pack_hours: gv('paidPackHours') || null,
      level: gv('paidLevel') || null,
      student_status: gv('paidStudentStatus') || null,
      student_note: gv('paidStudentNote') || null,
      groups: gv('paidGroups') || null,
      // Client identity + student window — written onto the HubSpot contact.
      firstname: gv('paidFirstName') || null,
      lastname: gv('paidLastName') || null,
      email: email || null,
      age: gv('paidAge') || null,
      city: gv('paidCity') || null,
      date_of_first_session: gv('paidDateOfFirstSession') || null,
      date_of_end: gv('paidDateOfEnd') || null,
      sessions_per_week: gv('paidSessionsPerWeek') || null,
    };
    // Shared-number guard (Task B2): 2-3 leads can share one WhatsApp number
    // (parent + child). If ANOTHER lead on this number is already marked paid,
    // the agent may be double-counting the same person's payment (inflates the
    // encaissé). Warn + confirm before encaissement. NON-blocking: a real
    // parent+child both paying is legit, and a lookup failure must never stop a
    // genuine encaissement.
    if (btn) { btn.disabled = true; btn.textContent = 'Vérification…'; }
    try {
      const phone = pipelineExtractPhone(state.selectedChat || {});
      if (phone) {
        const r = await api('/api/leads/by-number/' + encodeURIComponent(phone));
        if (r.ok) {
          const j = await r.json();
          const otherPaid = (j.leads || []).filter(l =>
            String(l.id) !== String(_paidModalLeadId) && l.paid);
          if (otherPaid.length > 0) {
            const names = otherPaid
              .map(l => (l.name || '').trim() || 'Sans nom')
              .join(', ');
            const proceed = window.confirm(
              'Attention : ' + otherPaid.length + ' autre(s) contact(s) sur ce numéro ' +
              'sont déjà marqués payés (' + names + ').\n\n' +
              'Si c\'est la même personne, vous risquez de compter le paiement deux fois ' +
              'et de gonfler l\'encaissé.\n\nEncaisser quand même ?'
            );
            if (!proceed) {
              if (btn) { btn.disabled = false; btn.textContent = "Confirmer l'encaissement"; }
              return; // Abort the paid transition, keep the modal open.
            }
          }
        }
      }
    } catch (err) {
      // Lookup failed (network/timeout) — never block a legit encaissement.
      console.warn('[paid] shared-number guard lookup failed:', err && err.message || err);
    }
    if (btn) { btn.textContent = 'Confirmation…'; }
    try {
      const ok = await transitionPipelineStage(_paidModalLeadId, 'paid', null, { amount, pack, ...payFields });
      // Keep the modal open on failure so the agent can correct + retry
      // (the error toast explains what went wrong).
      if (ok) closeModal('paidAmountModal');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = "Confirmer l'encaissement"; }
    }
  }

  // Inline confirm popover anchored to the clicked stage pill. Optional
  // reason text; on confirm POSTs the transition and re-renders the bar.
  function showPipelineConfirm(anchorEl, leadId, toStage, { isBack = false } = {}) {
    closePipelineConfirm();
    const host = document.getElementById('pipelineBar');
    if (!host) return;
    // Position relative to the chat-pane so absolute coords work even
    // when the bar wraps on narrower screens.
    const pop = el('div', { class: 'pipeline-confirm' });
    const titleText = (isBack ? 'Move BACK to ' : 'Move to ');
    const title = el('div', {
      class: 'pipeline-confirm-title' + (isBack ? ' is-back' : ''),
    }, [
      document.createTextNode(titleText),
      el('strong', { text: PIPELINE_LABELS[toStage] || toStage }),
      document.createTextNode('?'),
    ]);
    const reason = el('textarea', {
      class: 'pipeline-confirm-reason',
      placeholder: 'Reason (optional)',
      rows: '2',
    });
    const cancel = el('button', { class: 'btn', type: 'button', text: 'Cancel', onclick: closePipelineConfirm });
    const confirm = el('button', {
      class: 'btn btn-primary',
      type: 'button',
      text: 'Confirm',
      onclick: async () => {
        confirm.disabled = true; cancel.disabled = true;
        try {
          await transitionPipelineStage(leadId, toStage, reason.value.trim());
        } finally {
          closePipelineConfirm();
        }
      },
    });
    const actions = el('div', { class: 'pipeline-confirm-actions' }, [cancel, confirm]);
    pop.appendChild(title); pop.appendChild(reason); pop.appendChild(actions);

    // Use position: fixed against the viewport so we never depend on which
    // ancestor happens to be the nearest positioned element. Previous version
    // anchored to E.chatPane (whose position rule is fragile) and ended up
    // placing the popover at left:95px over the sidebar instead of under
    // the clicked pill.
    document.body.appendChild(pop);
    pop.style.position = 'fixed';
    const aRect = anchorEl.getBoundingClientRect();
    pop.style.top = (aRect.bottom + 6) + 'px';
    let left = aRect.left;
    const popW = 350;
    const margin = 8;
    if (left + popW + margin > window.innerWidth) left = window.innerWidth - popW - margin;
    if (left < margin) left = margin;
    pop.style.left = left + 'px';

    pipelineConfirmEl = pop;
    setTimeout(() => reason.focus(), 0);
    // Close on outside click — added on next tick so the click that
    // opened us doesn't immediately close us.
    setTimeout(() => {
      document.addEventListener('click', pipelineConfirmOutsideClick, { once: false });
    }, 0);
  }
  let pipelineConfirmEl = null;
  function pipelineConfirmOutsideClick(ev) {
    if (!pipelineConfirmEl) return;
    if (pipelineConfirmEl.contains(ev.target)) return;
    // Clicking another stage pill should swap the popover, not close+open
    // — but the new click will fire showPipelineConfirm anyway, which
    // calls closePipelineConfirm first. So just close here.
    closePipelineConfirm();
  }
  function closePipelineConfirm() {
    document.removeEventListener('click', pipelineConfirmOutsideClick);
    if (pipelineConfirmEl && pipelineConfirmEl.parentNode) {
      pipelineConfirmEl.parentNode.removeChild(pipelineConfirmEl);
    }
    pipelineConfirmEl = null;
  }

  async function transitionPipelineStage(leadId, toStage, reason, extra = {}) {
    if (!leadId || !toStage) return;
    try {
      const r = await api('/api/leads/' + encodeURIComponent(leadId) + '/transition', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_stage: toStage, reason: reason || null, source: 'manual', ...extra }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast(j.error || ('Failed to move stage (' + r.status + ')'), 'error');
        return false;
      }
      // Invalidate the cache so the re-render fetches the fresh row.
      const chatId = state.selectedChatId;
      if (chatId) delete pipelineLeadCache[chatId];
      if (j.noop) {
        toast('Already in ' + (PIPELINE_LABELS[toStage] || toStage));
      } else {
        toast('Moved to ' + (PIPELINE_LABELS[toStage] || toStage));
      }
      // Cross-agent double-encaissement net (Task B2): the backend flags any
      // OTHER lead on the same number already marked paid — including ones owned
      // by a DIFFERENT agent that the owner-scoped pre-check in _paidConfirm
      // can't see. Surface it as a warning toast so the encaissé isn't silently
      // doubled across agents.
      // Amber, not red: on a family number (parent + child) a second paid
      // person is the normal case — 2026-09-25 the red "déjà payé" made the
      // agent think the child's encaissement was a mistake.
      if (toStage === 'paid' && Array.isArray(j.siblingPaid) && j.siblingPaid.length) {
        const names = j.siblingPaid.map(s => s.name || s.id).filter(Boolean).join(', ');
        toast('Info : même numéro, déjà payé' + (names ? ' : ' + names : '')
          + '. OK si c\'est une autre personne (parent / enfant) — sinon vérifiez un double encaissement.', 'warning');
      }
      // The payment-form → HubSpot push is awaited server-side; if it failed the
      // details are safe in Postgres but not on the contact — tell the agent.
      if (toStage === 'paid' && j.paymentFormWarning) {
        toast('Détails paiement enregistrés localement mais pas sur HubSpot — ' + j.paymentFormWarning, 'error');
      }
      // The typed date was refused for the ledger (future, or too far back) and
      // the encaissement was dated today instead. Say so: otherwise the fiche
      // shows one date while Objectif/salaire count it in another month — the
      // exact divergence the client-payment-date change exists to remove.
      if (toStage === 'paid' && j.paymentDateWarning) {
        toast(j.paymentDateWarning, 'error');
      }
      // Sessions a week → students7. Saved: say until when. Failed: the payment
      // is fine, the number must be set in s2 (Contact Profile ▸ Live sessions).
      if (toStage === 'paid' && j.sessionLimit) {
        const until = j.sessionLimit.validUntil ? ' jusqu\'au ' + j.sessionLimit.validUntil.split('-').reverse().join('/') : '';
        toast('App : ' + j.sessionLimit.weeklyRate + ' sessions / semaine' + until + '.', 'success');
      }
      if (toStage === 'paid' && j.sessionLimitWarning) {
        toast('Paiement enregistré, mais les sessions / semaine ne sont pas passées dans l\'app (' + j.sessionLimitWarning
          + '). À régler dans s2 : Contact Profile ▸ Live sessions.', 'error');
      }
      // Optimistic local update of the per-row + per-chat tints so the
      // colour swap is INSTANT after a stage click (no need to wait for
      // the next /api/inbox/chats poll). Per Ayoub: "wash possible ghir
      // yklikiwe 3liah doz automatic mashi darori refresh".
      // Skip the optimistic chat-tint update when a SECONDARY contact (shared
      // number, Task B1) was transitioned — the row/pane tint tracks the
      // primary/default lead, so a child's stage change must not hijack it.
      // The next inbox poll re-syncs chat.pipelineStage from the server.
      const isSecondaryLead = !!(chatId && _activeLeadIdByChat[chatId] && _activeLeadIdByChat[chatId] === leadId);
      if (chatId && !j.noop && !isSecondaryLead) {
        // 1) Update the chat record so the row tint repaints.
        const chatRec = (state.chats || []).find(c => c.id === chatId);
        if (chatRec) chatRec.pipelineStage = toStage;
        if (state.selectedChat && state.selectedChat.id === chatId) {
          state.selectedChat.pipelineStage = toStage;
        }
        // 2) Re-stamp the chat pane so its background tint flips with
        //    the new stage immediately.
        const chatPaneEl = document.getElementById('chatPane');
        if (chatPaneEl) chatPaneEl.dataset.stage = toStage;
        // 3) Re-paint the sidebar so the row tint picks up the new
        //    data-stage attribute. Cheap — just a DOM diff against the
        //    already-updated state.chats.
        try { renderConversations(); } catch (_) { /* renderConversations may not be defined in some test contexts */ }
      }
      // Re-render the bar with the new state.
      if (chatId) await renderPipelineBar(chatId);
      // Broadcast for other listeners (Funnel dashboard etc.) to refresh.
      try {
        document.dispatchEvent(new CustomEvent('leads7:stageChanged', {
          detail: { leadId, toStage, chatId, historyId: j.historyId || null },
        }));
      } catch (_) { /* CustomEvent may fail in very old browsers; harmless */ }
      return true;
    } catch (err) {
      console.warn('[pipeline] transition failed:', err);
      toast('Network error moving stage', 'error');
      return false;
    }
  }

  async function createLeadForChat(chatId) {
    const chat = state.chats.find(c => c.id === chatId) || state.selectedChat;
    const phone = pipelineExtractPhone(chat);
    if (!phone) { toast('No phone for this chat', 'error'); return; }
    try {
      const r = await api('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone, name: chat?.name || '', source: _leadSourceForChat(chat),
          chat_id: String(chat?.id || chatId || ''),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(j.error || 'Failed to create lead', 'error'); return; }
      delete pipelineLeadCache[chatId];
      toast('Lead created');
      await renderPipelineBar(chatId);
    } catch (err) {
      console.warn('[pipeline] createLeadForChat failed:', err);
      toast('Network error creating lead', 'error');
    }
  }

  async function openPipelineHistory(leadId) {
    if (!leadId) return;
    openModal('pipelineHistoryModal');
    const listEl = document.getElementById('pipelineHistoryList');
    if (!listEl) return;
    clear(listEl);
    listEl.appendChild(el('div', { class: 'pipeline-history-loading', text: 'Loading…' }));
    try {
      const r = await api('/api/leads/' + encodeURIComponent(leadId) + '/history');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      const rows = Array.isArray(j.history) ? j.history : [];
      clear(listEl);
      if (!rows.length) {
        listEl.appendChild(el('div', { class: 'pipeline-history-empty', text: 'No stage transitions yet.' }));
        return;
      }
      for (const row of rows) {
        const fromLabel = row.from_stage ? (PIPELINE_LABELS[row.from_stage] || row.from_stage) : '—';
        const toLabel   = PIPELINE_LABELS[row.to_stage] || row.to_stage;
        const when = row.changed_at ? MA.loc(new Date(row.changed_at), 'dt', 'en-GB', {
          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        }) : '';
        const transition = el('div', { class: 'hist-transition' }, [
          el('span', { class: 'pill from', text: fromLabel }),
          document.createTextNode('→'),
          el('span', { class: 'pill to', text: toLabel }),
        ]);
        const whenEl = el('div', { class: 'hist-when', text: when });
        const metaBits = [];
        if (row.changed_by) metaBits.push('by ' + userLabel(row.changed_by));
        if (row.source) metaBits.push('source: ' + row.source);
        if (row.confidence != null) metaBits.push('conf: ' + row.confidence);
        const meta = el('div', { class: 'hist-meta', text: metaBits.join(' · ') });
        const rowEl = el('div', { class: 'pipeline-history-row' }, [transition, whenEl, meta]);
        if (row.reason) {
          rowEl.appendChild(el('div', { class: 'hist-reason', text: '“' + row.reason + '”' }));
        }
        listEl.appendChild(rowEl);
      }
    } catch (err) {
      clear(listEl);
      listEl.appendChild(el('div', { class: 'pipeline-history-empty', text: 'Failed to load history.' }));
      console.warn('[pipeline] history fetch failed:', err);
    }
  }
  // Close the confirm popover when the user switches chats.
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') closePipelineConfirm();
  });
  // ============ /Mr.Call pipeline bar ============

  // ============ Mr.Call AI coach banner (Sprint 7+) ============
  // Top banner above the messages list — calls /api/mr-call/coach which
  // forwards the last 60 messages to Gemini and returns score+stage+sentiment
  // +objections+suggestedNext in one shot. Cached server-side by chatId+lastTs
  // so reloading the same chat is free.
  //
  // Refresh triggers:
  //  - chat opened (loadMessages first call)
  //  - new message arrives (socket message handler)
  //  - manual ⟳ button
  const _mrCallState = { lastChatId: null, lastTsAnalyzed: 0, inFlight: false };

  // Mr.Call Script panel — backed by /api/coach/script + mig 0009.
  // The 7 steps rarely change (admin-managed); cache them in-memory once
  // per page load. Stage → step mapping mirrors lib/coach/scripts.js.
  const _MRCALL_STAGE_TO_STEP = {
    new: 1, no_answer: 0, contacted: 2, book_test: 3,
    no_show: 0, test_done: 4, proforma_sent: 5, paid: 7, not_interested: 0, lost: 0,
  };
  const _MRCALL_JOURNEY_LABEL_FR = { 1: 'Accueil', 2: 'Discovery', 3: 'Présentation', 4: 'Offre', 5: 'Objections', 6: 'Closing' };
  let _mrCallScriptSteps = null;
  let _mrCallScriptInFlight = null;
  async function _loadMrCallScriptSteps() {
    if (_mrCallScriptSteps) return _mrCallScriptSteps;
    if (_mrCallScriptInFlight) return _mrCallScriptInFlight;
    _mrCallScriptInFlight = (async () => {
      try {
        const r = await api('/api/coach/script?all=1');
        if (!r.ok) { _mrCallScriptSteps = null; return []; }
        const j = await r.json();
        const all = Array.isArray(j?.all_steps) ? j.all_steps : [];
        _mrCallScriptSteps = all;
        return all;
      } catch (err) {
        console.warn('[coach/script] preload failed:', err);
        return [];
      } finally {
        _mrCallScriptInFlight = null;
      }
    })();
    return _mrCallScriptInFlight;
  }
  function _resolveMrCallStepNumber(stage, journeyStage) {
    if (typeof journeyStage === 'number' && journeyStage >= 1 && journeyStage <= 6) return journeyStage;
    return _MRCALL_STAGE_TO_STEP[stage] ?? 1;
  }
  async function _renderMrCallScript(coach) {
    const panel  = document.getElementById('mrCallScript');
    const head   = document.getElementById('mrCallScriptStepLabel');
    const live   = document.getElementById('mrCallScriptLive');
    const openEl = document.getElementById('mrCallScriptOpen');
    if (!panel || !head || !openEl) return;
    const steps = await _loadMrCallScriptSteps();
    if (!Array.isArray(steps) || !steps.length) { panel.classList.add('hidden'); return; }

    const stage = (state.selectedChat && state.selectedChat.pipelineStage) || 'new';
    const journeyNum = Number(coach?.journeyStage?.num);
    const journeyStage = Number.isInteger(journeyNum) && journeyNum >= 1 && journeyNum <= 6 ? journeyNum : null;
    const stepNum = _resolveMrCallStepNumber(stage, journeyStage);
    if (!stepNum) { panel.classList.add('hidden'); return; }
    const step = steps.find(s => Number(s.step_number) === stepNum);
    if (!step) { panel.classList.add('hidden'); return; }

    const isLive = journeyStage !== null;
    const liveLabel = isLive ? (_MRCALL_JOURNEY_LABEL_FR[journeyStage] || step.title_fr) : null;
    head.textContent = `Step ${step.step_number}: ${liveLabel || step.title_fr || step.title_darija || ''}`;
    live.classList.toggle('hidden', !isLive);
    openEl.textContent = step.opening_line ? `“${step.opening_line}”` : (step.goal || '');
    panel.classList.remove('hidden');
  }

  function _mrCallScoreClass(label) {
    return label === 'hot' ? 'score-hot' : label === 'warm' ? 'score-warm' : 'score-cold';
  }

  // Global "hide Mr.Call coach banner" — same UX as the 24h banner global
  // hide. Agents asked for the coach to be dismissible site-wide (not just
  // per-chat) because it was crowding the conversation header.
  function _isMrCallGloballyHidden() {
    try { return localStorage.getItem('mrcall_global_hidden') === '1'; }
    catch { return false; }
  }
  function _setHideMrCallGlobal(hidden) {
    try {
      if (hidden) localStorage.setItem('mrcall_global_hidden', '1');
      else localStorage.removeItem('mrcall_global_hidden');
    } catch { /* quota / private mode */ }
  }

  function _mrCallRender(coach) {
    const banner = document.getElementById('mrCallBanner');
    if (!banner || !coach) return;
    if (_isMrCallGloballyHidden()) { banner.classList.add('hidden'); return; }
    banner.classList.remove('hidden');
    const score = Number(coach.score ?? 0);
    const scoreLabel = String(coach.scoreLabel || 'cold').toLowerCase();
    const pill = document.getElementById('mrCallScorePill');
    pill.textContent = `${score}/10 ${scoreLabel}`;
    pill.className = 'mrcall-score-pill ' + _mrCallScoreClass(scoreLabel);
    const stage = coach.journeyStage || {};
    document.getElementById('mrCallStage').textContent = stage.num
      ? `Étape ${stage.num}: ${stage.label || ''}`
      : (stage.label || '—');
    document.getElementById('mrCallSuggest').textContent = coach.suggestedNext || '(no suggestion)';
    document.getElementById('mrCallSuggest').title = coach.suggestedNext || '';
    document.getElementById('mrCallSentiment').textContent = coach.sentiment || '—';
    document.getElementById('mrCallGenAt').textContent = MA.loc(new Date(), 't');
    const objsHost = document.getElementById('mrCallObjections');
    clear(objsHost);
    for (const o of (coach.objections || [])) {
      objsHost.appendChild(el('div', { class: 'mrcall-obj' }, [
        el('div', { class: 'mrcall-obj-label', text: o.label || '' }),
        el('div', { class: 'mrcall-obj-rebuttal', text: o.rebuttal || '' }),
      ]));
    }
    // store suggestion on the Copy button so the click handler can grab it
    document.getElementById('mrCallCopyBtn').dataset.suggest = coach.suggestedNext || '';
    // 6 axioms checklist (Phase 2). For each defined axiom in the response,
    // toggle the row's filled/empty state. Missing axioms object (older
    // cached coach payload) leaves rows as-is.
    const axioms = coach.axioms || null;
    if (axioms) {
      let doneCount = 0;
      for (const row of document.querySelectorAll('#mrCallAxioms .axiom-row')) {
        const key = row.dataset.axiom;
        const done = !!axioms[key];
        row.classList.toggle('axiom-done', done);
        row.querySelector('.axiom-mark').textContent = done ? '●' : '○';
        if (done) doneCount++;
      }
      const cnt = document.getElementById('mrCallAxiomsCount');
      if (cnt) cnt.textContent = `${doneCount}/6`;
    }
    // Stash the latest coach payload globally so the Objections modal can
    // sort raised-first + the header badge can count them.
    window._lastMrCallCoach = coach;
    if (typeof window._objUpdateBadge === 'function') window._objUpdateBadge();
    // Render the Script panel — uses coach.journeyStage if available
    // (Gemini live), otherwise falls back to pipeline stage mapping.
    _renderMrCallScript(coach).catch(err => console.warn('[coach/script render] failed:', err));
  }

  // Recompute the "outside 24h customer-service window" banner above the
  // composer. WhatsApp Cloud API forbids free-form sends when more than
  // 24h have passed since the customer's last inbound — agents must use
  // an approved template instead. Without this hint, free-form sends
  // silently fail with Meta error 131047. Hidden when:
  //   - no chat selected
  //   - group chat (no 24h rule for groups)
  //   - non-WhatsApp channel (FB/IG/TT have different rules)
  //   - no inbound from customer yet (first-touch territory — Layer 2
  //     failure pill catches any 131047 here)
  //   - most recent inbound is <=24h ago
  const WINDOW_24H_MS = 24 * 60 * 60 * 1000;
  // Global "hide 24h banner" toggle. Used to be per-chat sessionStorage but
  // agents found the banner intrusive even when dismissed once — they want
  // it GONE everywhere until they explicitly want it back. localStorage
  // makes the choice persist across logout / refresh / new tab. Reset by
  // clearing localStorage key `window24h_global_hidden`.
  function _is24hGloballyHidden() {
    try { return localStorage.getItem('window24h_global_hidden') === '1'; }
    catch { return false; }
  }
  function _setHide24hGlobal(hidden) {
    try {
      if (hidden) localStorage.setItem('window24h_global_hidden', '1');
      else localStorage.removeItem('window24h_global_hidden');
    } catch { /* quota / private mode */ }
  }
  // Per-chat session-dismiss kept as a deprecated alias so the existing
  // click handler keeps compiling; the new behavior just flips the global.
  function _is24hDismissed(chatId) { return _is24hGloballyHidden(); }
  function _dismiss24hFor(_chatId) { _setHide24hGlobal(true); }

  // Is the WhatsApp customer-service window closed for this chat? Extracted from
  // the banner so send paths can ask the question directly: the banner is
  // globally dismissible and hides itself on an empty thread, and neither of
  // those means Meta will accept free-form text.
  //
  // "No inbound at all" counts as closed — a window that never opened still
  // needs a template, which is exactly the 131047 case agents kept retrying.
  function _is24hWindowClosed(chat) {
    const target = chat || state.selectedChat;
    if (!target || target.isGroup) return false;
    const chatId = String(target.id || '');
    if (!chatId.endsWith('@s.whatsapp.net')) return false;
    const msgs = Array.isArray(state.messages) ? state.messages : [];
    const now = Date.now();
    const cutoff = now - WINDOW_24H_MS;
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i];
      if (!m || m.fromMe || m.chatId !== chatId) continue;
      // A merged thread can contain another business number's messages.
      // Only a customer reply to THIS WhatsApp line opens its 24h window.
      if (m.sentVia === 'support2' || String(m.id || '').startsWith('s2in:')) continue;
      if (m.channel && !['wa', 'whatsapp'].includes(m.channel)) continue;
      if (m._personSource && m._personSource !== 'leads7') continue;
      if (m._ocSrc && m._ocSrc !== 'leads7') continue;
      if (m.type && !['text', 'image', 'video', 'audio', 'document', 'sticker', 'location', 'contact', 'contacts', 'interactive', 'button'].includes(m.type)) continue;
      const rawTs = Number(m.timestamp || 0);
      const ts = rawTs > 0 && rawTs < 1e12 ? rawTs * 1000 : rawTs;
      if (Number.isFinite(ts) && ts > cutoff && ts <= now) return false;
    }
    return true;
  }

  function refreshWindow24hBanner() {
    const banner = document.getElementById('window24hBanner');
    if (!banner) return;
    const chat = state.selectedChat;
    if (!chat || chat.isGroup) { banner.classList.add('hidden'); return; }
    const id = String(chat.id || '');
    if (!id.endsWith('@s.whatsapp.net')) { banner.classList.add('hidden'); return; }
    // Agent explicitly dismissed this banner for this chat → respect it
    // until they refresh / logout. They've acknowledged the 24h rule.
    const msgs = Array.isArray(state.messages) ? state.messages : [];
    // Don't show on a completely empty chat — the agent is just exploring;
    // they haven't tried to send yet and a banner here would feel noisy.
    if (!msgs.length) { banner.classList.add('hidden'); return; }
    // The line the CLIENT is talking on comes first. When the last WhatsApp
    // message of the thread went through the Support2 line, the Leads7 24h
    // window is beside the point: the reply must leave on Support2, and a
    // "window closed → template / SMS" banner would push the agent the wrong
    // way. Doha ↔ 212661904054 (2026-09-17): three voice notes + a text out on
    // Support2, the client's audio back on Support2 — and this poll kept
    // resetting the picker to Leads7 every 8 s.
    const onSupport2 = _lastWaLineInThread() === 'support2';
    if (onSupport2) {
      try {
        if (!state.composeChannelUserSet && _chatIsWaOrSms(state.selectedChat) && state.composeChannel !== 'support2') {
          _setComposeChannel('support2', false);
        }
      } catch (e) { /* non-fatal */ }
      banner.classList.add('hidden');
      const sug = document.getElementById('window24hSuggestions');
      if (sug) clear(sug);
      return;
    }
    // Agent explicitly dismissed this banner for this chat → respect it
    // until they refresh / logout. They've acknowledged the 24h rule.
    if (_is24hDismissed(id)) { banner.classList.add('hidden'); return; }
    // Two states need a template:
    //   1. Customer messaged but >24h ago — window expired.
    //   2. Customer never messaged at all — window never opened. (First-
    //      touch territory; Meta still requires a template.) This catches
    //      chats like Mohammed Bnhassan / Ossama where every prior
    //      outbound failed 131047 because the agent kept retrying free-
    //      form on a never-opened window.
    const stale = _is24hWindowClosed(chat);
    banner.classList.toggle('hidden', !stale);
    // Smart default: when the WhatsApp 24h window is closed, pre-select SMS in
    // the compose toggle (SMS has no window) — unless the agent already chose a
    // channel manually for this chat.
    try {
      if (!state.composeChannelUserSet && _chatIsWaOrSms(state.selectedChat)) {
        _setComposeChannel(stale ? 'sms' : 'wa', false);
      }
    } catch (e) { /* non-fatal */ }
    if (stale) {
      renderWindow24hSuggestions(chat).catch(err => console.warn('[24h suggestions] failed:', err));
    } else {
      const sug = document.getElementById('window24hSuggestions');
      if (sug) clear(sug);
    }
  }

  // Suggest 3 stage-matched approved templates inside the 24h banner.
  // Lazily warms state.templatesCache on first show so we don't fetch
  // /api/templates on every chat that's still inside the window. Click a
  // chip → useTemplate() inserts the body with {{name}}/{{agent}}/{{date}}
  // substituted AND sets state.composeTemplate so the send routes via
  // Meta's template API (the only thing Meta accepts outside 24h).
  async function renderWindow24hSuggestions(chat) {
    const container = document.getElementById('window24hSuggestions');
    if (!container) return;
    clear(container);
    if (!Array.isArray(state.templatesCache) || !state.templatesCache.length) {
      try {
        const r = await api('/api/templates');
        const j = await r.json();
        state.templatesCache = j.templates || [];
      } catch { return; }
    }
    const stage = chat?.pipelineStage || null;
    const langPref = (state.me?.locale || navigator.language || 'fr').slice(0, 2).toLowerCase();
    const approved = state.templatesCache.filter(t => (t.meta_status || '').toUpperCase() === 'APPROVED');
    const stageMatched = stage ? approved.filter(t => templateStageOf(t) === stage) : approved;
    const pool = stageMatched.length ? stageMatched : approved;
    // The same three templates in every chat, all day, was the sales team's
    // complaint (Mariem, 09-24 → 10-02). Turn the pool by an offset that depends
    // on the chat and the day: chats see different templates, one chat keeps its
    // three while an agent works it. The agent's language still comes first.
    let seed = 0;
    for (const ch of String(chat?.id || chat?.chatId || state.selectedChatId || '') + new Date().toISOString().slice(0, 10)) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
    const turn = (list) => { const k = list.length ? seed % list.length : 0; return list.slice(k).concat(list.slice(0, k)); };
    const ofLang = (t) => (t.meta_language || '').toLowerCase() === langPref;
    const ranked = [...turn(pool.filter(ofLang)), ...turn(pool.filter(t => !ofLang(t)))].slice(0, 3);
    for (const t of ranked) {
      const chip = el('button', { class: 'window-24h-suggestion-chip', type: 'button', title: t.meta_name || t.name || '' });
      chip.textContent = (t.name || t.meta_name || 'Template');
      chip.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        _setComposeChannel('wa', true);
        state.templatePickerTarget = 'compose';
        if (typeof useTemplate === 'function') useTemplate(t);
      });
      container.appendChild(chip);
    }
  }

  async function refreshMrCallBanner({ force = false } = {}) {
    const chatId = state.selectedChatId;
    if (!chatId) return;
    // SMS service threads (verification codes) never go to the AI coach.
    if (String(chatId).startsWith('sms:svc:')) return;
    // Skip if the same chat hasn't received new messages since the last analysis
    const msgs = Array.isArray(state.messages) ? state.messages : [];
    const lastTs = msgs.length ? Number(msgs[msgs.length - 1].timestamp || 0) : 0;
    if (!force && chatId === _mrCallState.lastChatId && lastTs && lastTs === _mrCallState.lastTsAnalyzed) return;
    if (_mrCallState.inFlight) return;
    _mrCallState.inFlight = true;
    const suggestEl = document.getElementById('mrCallSuggest');
    if (suggestEl) suggestEl.textContent = 'Analyzing…';
    try {
      const r = await api('/api/mr-call/coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, force }),
        timeoutMs: 120000, // Gemini over a long conversation can outlive the 60s default
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        // Bail if the user switched chats during the round-trip — don't
        // clobber the now-visible chat's banner with this one's error/state.
        if (state.selectedChatId !== chatId) return;
        // 503 = no Gemini key — silently hide; 400 = no messages — hide
        if (r.status === 503 || r.status === 400) {
          document.getElementById('mrCallBanner')?.classList.add('hidden');
        } else if (r.status === 429) {
          // Gemini rate-limited. Don't spam retries; show a calm hint and
          // back off until the next chat switch (which clears lastTsAnalyzed).
          if (suggestEl) suggestEl.textContent = 'Coach paused — Gemini rate-limited, will retry on next conversation switch';
          _mrCallState.lastChatId = chatId;
          _mrCallState.lastTsAnalyzed = lastTs; // pin so the same chat doesn't re-fire
        } else if (suggestEl) {
          suggestEl.textContent = `Error: ${j.error || r.status}`;
        }
        return;
      }
      const coach = await r.json();
      // Bail if the user switched chats during the coach round-trip —
      // rendering stale analysis into the now-visible chat (and clobbering
      // window._lastMrCallCoach via _mrCallRender) is a data leak.
      if (state.selectedChatId !== chatId) return;
      _mrCallState.lastChatId = chatId;
      _mrCallState.lastTsAnalyzed = lastTs;
      _mrCallRender(coach);
    } catch (err) {
      console.warn('[mr-call coach] fetch failed:', err);
    } finally {
      _mrCallState.inFlight = false;
    }
  }

  // Wire banner buttons (after DOM is ready). Defensive lookups so the page
  // doesn't crash if the banner block is missing for some reason.
  document.addEventListener('DOMContentLoaded', () => {
    document.getElementById('mrCallRefreshBtn')?.addEventListener('click', () => refreshMrCallBanner({ force: true }));
    // Global dismiss — banner stays hidden across all chats until the agent
    // re-enables via localStorage.removeItem('mrcall_global_hidden') in
    // DevTools (no UI toggle yet — keeping it deliberately hard to undo so
    // it doesn't get re-shown accidentally by a stray click).
    document.getElementById('mrCallDismissBtn')?.addEventListener('click', () => {
      _setHideMrCallGlobal(true);
      document.getElementById('mrCallBanner')?.classList.add('hidden');
      toast('Coach masqué pour toutes les conversations. Réactiver via les paramètres du navigateur.', 'info');
    });
    document.getElementById('mrCallExpandBtn')?.addEventListener('click', () => {
      const exp = document.getElementById('mrCallExpanded');
      const btn = document.getElementById('mrCallExpandBtn');
      const open = exp.classList.toggle('hidden');
      btn.textContent = open ? '▾' : '▴';
    });
    document.getElementById('mrCallCopyBtn')?.addEventListener('click', () => {
      const txt = document.getElementById('mrCallCopyBtn')?.dataset.suggest || '';
      if (!txt) return;
      const input = document.getElementById('messageInput');
      if (input) {
        input.value = txt;
        input.focus();
        toast('Suggestion copied to message box', 'success');
      } else {
        navigator.clipboard?.writeText(txt);
        toast('Suggestion copied to clipboard', 'success');
      }
    });
  });

  // Expose for the message-loaders and socket handlers to call
  window.refreshMrCallBanner = refreshMrCallBanner;
  // ============ /Mr.Call AI coach banner ============

  // ============ Mr.Call coaching — Objections library popup (Phase 1) ============
  // Modal of 30+ pre-written objection responses. Highlights the ones Gemini
  // flagged in the current conversation. Powered by GET /api/coach/objections
  // (read-only, in-memory cache on the server, 60-s TTL).
  const _objState = {
    cards: null,         // full list, fetched lazily
    cardsFetchedAt: 0,   // memo TTL on the client too (5 min)
    currentDetail: null, // card currently shown in detail step
  };
  const OBJ_CLIENT_TTL_MS = 5 * 60 * 1000;

  // Match Gemini's `objections_raised` keywords against a card. Mirrors the
  // logic from the leads-platform `objectionMatchesRaised` helper — checks
  // title / phrase / category / tactic / keywords[] for a case-insensitive hit.
  function objCardMatchesRaised(card, raised) {
    if (!Array.isArray(raised) || raised.length === 0) return false;
    const fields = [
      card.objection_text || '',
      card.category || '',
      card.why_it_works || '',
      ...(Array.isArray(card.keywords) ? card.keywords : []),
    ].join(' ').toLowerCase();
    return raised.some(r => {
      const needle = String(r || '').toLowerCase().trim();
      return needle && fields.includes(needle);
    });
  }

  async function _objLoadCards() {
    const now = Date.now();
    if (_objState.cards && (now - _objState.cardsFetchedAt) < OBJ_CLIENT_TTL_MS) {
      return _objState.cards;
    }
    const r = await api('/api/coach/objections');
    if (!r.ok) throw new Error('Failed to load objection cards (HTTP ' + r.status + ')');
    const data = await r.json();
    _objState.cards = Array.isArray(data.cards) ? data.cards : [];
    _objState.cardsFetchedAt = now;
    return _objState.cards;
  }

  function _objShowStep(step) {
    document.getElementById('objListStep')?.classList.toggle('hidden', step !== 'list');
    document.getElementById('objDetailStep')?.classList.toggle('hidden', step !== 'detail');
    document.getElementById('objBackBtn')?.classList.toggle('hidden', step !== 'detail');
    const titleEl = document.getElementById('objModalTitleText');
    if (titleEl) titleEl.textContent = step === 'list' ? 'Objections' : 'Objection detail';
  }

  function _objRenderList(cards, raised) {
    const host = document.getElementById('objCardList');
    const empty = document.getElementById('objListEmpty');
    const hint = document.getElementById('objRaisedHint');
    const hintText = document.getElementById('objRaisedHintText');
    clear(host);
    if (!cards.length) {
      empty.classList.remove('hidden');
      hint.classList.add('hidden');
      return;
    }
    empty.classList.add('hidden');
    const raisedArr = Array.isArray(raised) ? raised : [];
    if (raisedArr.length) {
      hint.classList.remove('hidden');
      hintText.textContent = `Gemini detected ${raisedArr.length} objection${raisedArr.length > 1 ? 's' : ''} in this chat — highlighted below.`;
    } else {
      hint.classList.add('hidden');
    }
    // Sort: raised first, then everything else (preserving original sort_order within each bucket).
    const decorated = cards.map((c, i) => ({
      card: c,
      raised: objCardMatchesRaised(c, raisedArr),
      idx: i,
    }));
    decorated.sort((a, b) => {
      if (a.raised !== b.raised) return a.raised ? -1 : 1;
      return a.idx - b.idx;
    });
    for (const { card, raised: isRaised } of decorated) {
      const chip = el('button', {
        type: 'button',
        class: 'objection-chip' + (isRaised ? ' objection-chip-raised' : ''),
      }, [
        el('span', { class: 'obj-chip-icon', text: isRaised ? '🎯' : '⚠️' }),
        el('span', { class: 'obj-chip-text', text: '“' + (card.objection_text || 'Objection') + '”' }),
      ]);
      chip.addEventListener('click', () => _objShowDetail(card));
      host.appendChild(chip);
    }
  }

  function _objShowDetail(card) {
    _objState.currentDetail = card;
    document.getElementById('objDetailTitle').textContent = card.objection_text || 'Objection';
    document.getElementById('objDetailResponse').textContent = card.response_darija || card.response_fr || '(no recommended response)';

    // Real-example block — only show if we have an agent + resume
    const hasExample = !!(card.real_example_agent && card.real_example_resume);
    const exWrap = document.getElementById('objDetailExampleWrap');
    if (hasExample) {
      exWrap.classList.remove('hidden');
      const scorePart = card.real_example_score != null ? `, score ${card.real_example_score}` : '';
      document.getElementById('objDetailExampleLabel').textContent = `🎯 REAL EXAMPLE (${card.real_example_agent}${scorePart})`;
      document.getElementById('objDetailExampleText').textContent = card.real_example_resume;
    } else {
      exWrap.classList.add('hidden');
    }

    // Tactic footer
    const tacticWrap = document.getElementById('objDetailTacticWrap');
    if (card.why_it_works) {
      tacticWrap.classList.remove('hidden');
      document.getElementById('objDetailTactic').textContent = card.why_it_works;
    } else {
      tacticWrap.classList.add('hidden');
    }

    _objShowStep('detail');
    refreshIcons();
  }

  async function openObjectionsModal() {
    try {
      const cards = await _objLoadCards();
      // Read the latest Gemini objections from the coach cache (set by _mrCallRender)
      const raised = Array.isArray(window._lastMrCallCoach?.objections)
        ? window._lastMrCallCoach.objections.map(o => typeof o === 'string' ? o : (o.label || o.category || ''))
        : [];
      _objRenderList(cards, raised);
      _objShowStep('list');
      openModal('objectionsModal');
      refreshIcons();
    } catch (err) {
      toast(err.message || 'Failed to open objections', 'error');
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.getElementById('objectionsBtn')?.addEventListener('click', () => openObjectionsModal());
    document.getElementById('objBackBtn')?.addEventListener('click', () => _objShowStep('list'));
    document.getElementById('objCopyBtn')?.addEventListener('click', () => {
      const card = _objState.currentDetail;
      if (!card) return;
      const txt = card.response_darija || card.response_fr || '';
      if (!txt) return;
      navigator.clipboard?.writeText(txt).then(() => toast('Response copied to clipboard', 'success'));
    });
    document.getElementById('objSendBtn')?.addEventListener('click', () => {
      const card = _objState.currentDetail;
      if (!card) return;
      const txt = card.response_darija || card.response_fr || '';
      if (!txt) return;
      const input = document.getElementById('messageInput');
      if (input) {
        input.value = txt;
        input.focus();
        closeModal('objectionsModal');
        toast('Response sent to message box — review then send', 'success');
      } else {
        navigator.clipboard?.writeText(txt);
        toast('Response copied (no chat selected)', 'warning');
      }
    });
  });

  // Update the objections badge whenever the coach banner re-renders.
  // Stash the latest coach payload globally so openObjectionsModal can read it.
  // _mrCallRender writes to window._lastMrCallCoach in a small patch below.
  function _objUpdateBadge() {
    const badge = document.getElementById('objectionsBadge');
    if (!badge) return;
    const raised = Array.isArray(window._lastMrCallCoach?.objections)
      ? window._lastMrCallCoach.objections.length : 0;
    if (raised > 0) {
      badge.textContent = String(raised);
      badge.classList.remove('hidden');
    } else {
      badge.classList.add('hidden');
    }
  }
  window._objUpdateBadge = _objUpdateBadge;
  // ============ /Mr.Call coaching — Objections library popup ============

  // ============ Mr.Call Sales Coach sidebar (Sprint 4 Feature 4 — tabs 1-3) ============
  // Right-side panel with 4 tabs: Script / Objections / Templates / Ask AI.
  // Ask AI is a placeholder in this phase; Phase 3 fills it.
  // - Script tab: per-stage talking points (7 ordered steps, language toggle)
  // - Objections tab: card grid with client phrase (red) + rebuttal (green) + copy
  // - Templates tab: list with Send → preview modal → wa.sendText via /api
  // - Lang choice persisted in localStorage (leads7.coachLang)
  // - Sidebar open/collapsed state persisted in localStorage too


  // ============== HubSpot contact picker (dup-resolution) ==============
  // When a chat's phone matches multiple HubSpot contacts (e.g., the same
  // student entered twice), the server auto-picks the richest one. This
  // picker lets the agent override by locking a specific contact id to
  // the chat. Lock is server-side (chat_contact_locks.json) so every team
  // member sees the same choice across reboots.

  // Open the students7 app as the exact contact currently shown in the
  // profile. The server re-checks chat access and the contact id before it
  // asks students7 to mint a short-lived, one-time impersonation link.
  async function _checkAppOpen() {
    const chat = state.selectedChat || {};
    const chatId = _profilePersonChatId(chat);
    if (!chatId.endsWith('@s.whatsapp.net') || !cxDialableDigits(chat)) {
      toast('No valid student phone for this chat', 'error');
      return;
    }
    const profileMatchesChat = String(state.activeProfile?.chatId || '') === chatId;
    const contactId = profileMatchesChat ? String(state.activeProfile?.contact?.id || '') : '';
    if (!contactId) {
      toast('Wait for the profile to finish loading, then try Check app again', 'error');
      return;
    }

    const btn = document.getElementById('profileCheckAppBtn');
    if (btn) btn.disabled = true;
    try {
      // Shared number: the server re-resolves the SAME person the profile shows.
      const personLeadId = profileMatchesChat ? String(state.activeProfile?.person?.leadId || '') : '';
      const r = await api('/api/students7/preview-link?chatId=' + encodeURIComponent(chatId)
        + '&contactId=' + encodeURIComponent(contactId)
        + (personLeadId ? '&leadId=' + encodeURIComponent(personLeadId) : ''));
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.url) {
        const msg = j.error === 'no_student' ? 'This contact does not have a student app yet'
          : j.error === 'hubspot_copy_pending' ? 'This contact was just created — its HubSpot copy lands within a minute, try again'
          : j.error === 'needs_pick' ? 'Choose the correct HubSpot contact first'
          : j.error === 'profile_changed' ? 'The profile changed — reload it and try again'
          : j.error === 'not_configured' ? 'Student app preview is not configured yet'
          : j.error === 'not_assigned' ? 'This conversation is not assigned to you'
          : 'Could not open the student app — try again';
        toast(msg, 'error');
        return;
      }
      window.open(j.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.warn('[checkApp] failed:', err?.message || err);
      toast('Could not open the student app — try again', 'error');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  async function _sendAppAccessLink() {
    const chat = state.selectedChat || {};
    const chatId = _profilePersonChatId(chat);
    if (!chatId.endsWith('@s.whatsapp.net') || !cxDialableDigits(chat)) {
      toast('No valid student WhatsApp chat', 'error');
      return;
    }
    const profileMatchesChat = String(state.activeProfile?.chatId || '') === chatId;
    const contact = profileMatchesChat ? state.activeProfile?.contact : null;
    const contactId = String(contact?.id || '');
    if (!contactId) {
      toast('Wait for the profile to finish loading, then try again', 'error');
      return;
    }

    const displayName = String(contact?.name || chat.name || 'this student');
    if (!window.confirm(`Send a one-time app login link to ${displayName}?\n\nThe link expires in 15 minutes and works once.`)) {
      return;
    }

    const btn = document.getElementById('profileSendAppLinkBtn');
    if (btn) btn.disabled = true;
    try {
      // Shared number: the server re-resolves the SAME person the profile shows.
      const personLeadId = String(state.activeProfile?.person?.leadId || '');
      const linkResponse = await api('/api/students7/access-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, contactId, ...(personLeadId ? { leadId: personLeadId } : {}) }),
      });
      const linkData = await linkResponse.json().catch(() => ({}));
      if (!linkResponse.ok || !linkData.url) {
        const msg = linkData.error === 'hubspot_copy_pending' ? 'This contact was just created — its HubSpot copy lands within a minute, try again'
          : linkData.error === 'no_student' ? 'This contact does not have a student app yet'
          : linkData.error === 'profile_changed' ? 'The profile changed — reload it and try again'
          : linkData.error === 'not_assigned' ? 'This conversation is not assigned to you'
          : linkData.error === 'access_store_unavailable' ? 'Login links are not ready yet'
          : 'Could not create the login link — try again';
        toast(msg, 'error');
        return;
      }

      const text = `هذا رابط الدخول الخاص بك لتطبيق لوغة:\n${linkData.url}\n\nصالح لمدة 15 دقيقة ولمرة واحدة فقط.`;
      const sendResponse = await api('/api/inbox/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, text }),
      });
      const sendData = await sendResponse.json().catch(() => ({}));
      if (!sendResponse.ok || sendData.error) {
        toast(sendData.error || 'Link created, but WhatsApp send failed', 'error');
        return;
      }

      // Mirror the regular composer behavior so the agent sees the sent link
      // immediately. The pinned chat guard prevents cross-chat optimistic UI.
      if (_profilePersonChatId() === chatId && sendData.message) {
        state.messages.push(sendData.message);
        renderMessages();
      }
      toast('App login link sent', 'success');
    } catch (err) {
      console.warn('[sendAppAccessLink] failed:', err?.message || err);
      toast('Could not send the app login link — try again', 'error');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  function _mountCheckAppPill() {
    const chat = state.selectedChat || {};
    const chatId = _profilePersonChatId(chat);
    if (!chatId.endsWith('@s.whatsapp.net') || !cxDialableDigits(chat)) return;
    const pills = document.querySelector('#profilePanel .profile-head-pills');
    if (!pills) return;
    let btn = document.getElementById('profileCheckAppBtn');
    if (!btn) {
      btn = el('button', {
        id: 'profileCheckAppBtn', class: 'profile-link-pill', type: 'button',
        title: 'Open this student\'s app as them',
      }, [lucideIcon('app-window'), el('span', { text: 'Check app' })]);
      btn.addEventListener('click', _checkAppOpen);
      pills.appendChild(btn);
      refreshIcons(btn);
    }
    btn.classList.remove('hidden');

    let accessBtn = document.getElementById('profileSendAppLinkBtn');
    if (!accessBtn) {
      accessBtn = el('button', {
        id: 'profileSendAppLinkBtn', class: 'profile-link-pill profile-access-pill', type: 'button',
        title: 'Send a one-time student login link',
      }, [lucideIcon('log-in'), el('span', { text: 'Send app link' })]);
      accessBtn.addEventListener('click', _sendAppAccessLink);
      pills.appendChild(accessBtn);
      refreshIcons(accessBtn);
    }
    accessBtn.classList.remove('hidden');
  }

  async function onProfileLinkBtnClick() {
    const chatId = _profilePersonChatId();
    const activeProfilePhone = String(state.activeProfile?.chatId || '') === chatId
      ? state.activeProfile?.phone : '';
    const phone = activeProfilePhone || state.selectedChat?.phone
      || String(chatId).split('@')[0];
    if (!phone) { toast('No phone for this chat', 'error'); return; }
    let candidates = [];
    try {
      const r = await api('/api/hubspot/contacts/search-by-phone?phone=' + encodeURIComponent(phone));
      const j = await r.json();
      candidates = Array.isArray(j.results) ? j.results : [];
    } catch (err) {
      console.warn('[ContactPicker] search failed:', err.message || err);
    }
    if (candidates.length === 0) {
      // Same legacy behaviour: send the agent to HubSpot's import-by-phone UI.
      window.open('https://app.hubspot.com/contacts/import?phone=' + encodeURIComponent(phone), '_blank', 'noopener,noreferrer');
      return;
    }
    if (candidates.length === 1) {
      // No ambiguity — open the contact in HubSpot directly.
      window.open(candidates[0].url, '_blank', 'noopener,noreferrer');
      return;
    }
    // 2+ matches — read current lock, then render picker.
    let currentLockId = null;
    try {
      const r2 = await api('/api/inbox/chats/' + encodeURIComponent(chatId) + '/contact-lock');
      const j2 = await r2.json();
      currentLockId = j2?.lock?.hubspotContactId || null;
    } catch { /* non-fatal */ }
    _renderContactPickerModal(chatId, candidates, currentLockId);
    openModal('contactPickerModal');
  }

  function _renderContactPickerModal(chatId, candidates, currentLockId) {
    const listEl = document.getElementById('contactPickerList');
    if (!listEl) return;
    clear(listEl);
    for (const c of candidates) {
      const isActive = currentLockId ? (c.id === currentLockId) : false;
      const row = el('div', { class: 'contact-picker-row' + (isActive ? ' active' : ''), 'data-contact-id': c.id });
      const props = c.properties || {};
      const status = props.student_status || '';
      const level = props.level || '';
      const dop = props.date_of_payment || '';
      const dofs = props.date_of_first_session || '';
      const amount = props.amount || '';
      const modified = props.hs_lastmodifieddate ? new Date(props.hs_lastmodifieddate).toISOString().slice(0, 10) : '';
      row.appendChild(el('div', { class: 'cpr-head' }, [
        el('span', { class: 'cpr-name', text: c.name || '(no name)' }),
        isActive ? el('span', { class: 'cpr-active-chip', text: '🔒 locked' }) : null,
        el('span', { class: 'cpr-score', text: 'score ' + (c.score || 0) }),
      ].filter(Boolean)));
      const fieldsBox = el('div', { class: 'cpr-fields' });
      const addField = (k, v) => {
        if (!v) return;
        fieldsBox.appendChild(el('div', { class: 'cpr-field' }, [
          el('span', { class: 'cpr-key', text: k }),
          el('span', { class: 'cpr-val', text: String(v) }),
        ]));
      };
      addField('Status', status);
      addField('Level', level);
      addField('Date of payment', dop);
      addField('Date of first session', dofs);
      addField('Amount', amount);
      addField('Last modified', modified);
      addField('HubSpot id', c.id);
      row.appendChild(fieldsBox);
      row.addEventListener('click', () => _saveContactLock(chatId, c.id));
      listEl.appendChild(row);
    }
    // Clear-lock affordance — only render when a lock currently exists.
    const clearBtn = document.getElementById('contactPickerClearBtn');
    if (clearBtn) {
      clearBtn.classList.toggle('hidden', !currentLockId);
      clearBtn.onclick = () => _saveContactLock(chatId, null);
    }
    refreshIcons();
  }

  async function _saveContactLock(chatId, contactId) {
    try {
      const r = await api('/api/inbox/chats/' + encodeURIComponent(chatId) + '/contact-lock', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hubspotContactId: contactId }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast(j.error || 'Save lock failed', 'error');
        return;
      }
      toast(contactId ? 'Contact locked' : 'Lock cleared', 'success');
      closeModal('contactPickerModal');
      // Re-pull the profile so the now-resolved contact is reflected everywhere.
      if (typeof loadProfileData === 'function') loadProfileData();
    } catch (err) {
      console.warn('[ContactPicker] save failed:', err.message || err);
      toast('Erreur réseau — vérifiez votre connexion et réessayez', 'error');
    }
  }

  // ============== Bot coaching loop ==============
  async function refreshBotCorrectionsForCurrentChat({ rerender = true } = {}) {
    if (!state.selectedChatId) return;
    const chatId = state.selectedChatId;
    state.botCorrections = state.botCorrections || {};
    const before = JSON.stringify(state.botCorrections[chatId] || []);
    try {
      const r = await _inboxRead('/api/bot/corrections?chatId=' + encodeURIComponent(chatId));
      const j = await r.json();
      if (chatId !== state.selectedChatId) return;
      state.botCorrections[chatId] = Array.isArray(j.corrections) ? j.corrections : [];
      // Re-render only if the set actually changed — avoids fighting the fingerprint diff in loadMessages.
      const after = JSON.stringify(state.botCorrections[chatId]);
      if (rerender && before !== after && state.messages?.length) {
        // Force the next renderMessages to actually paint by clearing BOTH
        // fingerprints, otherwise it sees the same messages and no-ops.
        forceMessagesRerender();
        renderMessages();
      }
    } catch { /* leave whatever was cached */ }
  }

  // ============== Bot feedback (👍/👎) ==============
  // state.botFeedback = { [chatId]: { [messageId]: 'up'|'down' } } — the
  // CURRENT agent's own votes, fetched once per conversation open.
  async function refreshBotFeedbackForCurrentChat() {
    if (!state.selectedChatId) return;
    state.botFeedback = state.botFeedback || {};
    const chatId = state.selectedChatId;
    const before = JSON.stringify(state.botFeedback[chatId] || {});
    try {
      const r = await _inboxRead('/api/bot/feedback/mine?chatId=' + encodeURIComponent(chatId));
      if (!r.ok) return;
      const j = await r.json();
      const map = {};
      for (const v of (j.votes || [])) map[v.messageId] = v.verdict;
      state.botFeedback[chatId] = map;
      if (before !== JSON.stringify(map) && state.messages?.length && state.selectedChatId === chatId) {
        // Bust BOTH fingerprints: _lastMessagesFp gates renderMessages'
        // fast-path (votes aren't part of the message hash, so without
        // this the repaint no-ops); _messagesFingerprint gates the next
        // loadMessages poll.
        state._lastMessagesFp = '';
        state._messagesFingerprint = '';
        renderMessages();
      }
    } catch { /* keep whatever was cached */ }
  }

  async function voteBotFeedback(m, verdict) {
    if (!m?.id) return;
    const chatId = m.chatId || state.selectedChatId;
    try {
      const chat = state.chats.find(c => c.id === chatId);
      const r = await api('/api/bot/feedback', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId: m.id, chatId, verdict, chatName: chat?.name || '' }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      state.botFeedback = state.botFeedback || {};
      const votes = state.botFeedback[state.selectedChatId] || (state.botFeedback[state.selectedChatId] = {});
      if (j.myVote) votes[m.id] = j.myVote; else delete votes[m.id];
      state._lastMessagesFp = '';   // votes aren't in the render hash — force the repaint
      state._messagesFingerprint = '';
      renderMessages();
    } catch (err) {
      toast('Vote failed: ' + (err.message || err), 'error');
    }
  }

  // Resolve the inbound message that immediately preceded a given bot
  // reply, used to seed the modal context. Walks state.messages backwards
  // from the bot reply's id.
  function findInboundBeforeBotReply(botMsg) {
    const msgs = state.messages || [];
    const idx = msgs.findIndex(m => m.id === botMsg.id);
    if (idx < 0) return null;
    for (let i = idx - 1; i >= 0; i--) {
      if (!msgs[i].fromMe) return msgs[i];
    }
    return null;
  }

  let _botCoachContext = null;
  function openBotCoachModal(botMsg) {
    if (!botMsg?.id) { toast('Cannot coach: missing message id', 'error'); return; }
    _botCoachContext = botMsg;
    const inbound = findInboundBeforeBotReply(botMsg);
    const inboundEl = document.getElementById('botCoachInbound');
    const replyEl = document.getElementById('botCoachReply');
    const ta = document.getElementById('botCoachComment');
    if (inboundEl) inboundEl.textContent = inbound?.text || inbound?.audioTranscript || (inbound ? `[${inbound.type || 'message'}]` : '(no inbound found before this reply)');
    if (replyEl) replyEl.textContent = botMsg.text || '(empty reply)';
    if (ta) ta.value = '';
    openModal('botCoachModal');
    setTimeout(() => ta?.focus(), 50);
  }

  async function _submitBotCoach(comment) {
    if (!_botCoachContext) return;
    if (!comment || !comment.trim()) { toast('Note kayna khawya — kteb chno khaso bot y9oul', 'error'); return; }
    try {
      const r = await api('/api/bot/corrections', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: state.selectedChatId, messageId: _botCoachContext.id, comment }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'save failed');
      toast('Coaching note saved 🙏', 'success');
      closeModal('botCoachModal');
      await refreshBotCorrectionsForCurrentChat();
      renderMessages();
    } catch (err) {
      toast('Save failed: ' + (err.message || err), 'error');
    }
  }

  function wireBotCoachModal() {
    const saveBtn = document.getElementById('botCoachSave');
    const goodBtn = document.getElementById('botCoachGood');
    const cancelBtn = document.getElementById('botCoachCancel');
    const closeBtn = document.getElementById('botCoachClose');
    saveBtn?.addEventListener('click', () => {
      const ta = document.getElementById('botCoachComment');
      _submitBotCoach(ta?.value || '');
    });
    goodBtn?.addEventListener('click', () => _submitBotCoach('good'));
    cancelBtn?.addEventListener('click', () => closeModal('botCoachModal'));
    closeBtn?.addEventListener('click', () => closeModal('botCoachModal'));
  }

  // Floating popover anchored to the badge — lists each coaching note
  // with author + time + admin Promote/Delete + author Delete.
  let _botCoachPopover = null;
  // Named outside-click handler so removeEventListener actually removes it.
  // The previous version registered an inline arrow inside setTimeout, then
  // tried to remove `_closeBotCoachPopover` itself — different reference, so
  // the listener leaked one new entry per badge open. Long sessions piled
  // up dozens, each running on every page click.
  function _onBotCoachOutsideClick(ev) {
    if (!_botCoachPopover) return;
    if (_botCoachPopover.contains(ev.target)) return;
    _closeBotCoachPopover();
  }
  function _closeBotCoachPopover() {
    if (_botCoachPopover) { _botCoachPopover.remove(); _botCoachPopover = null; }
    document.removeEventListener('click', _onBotCoachOutsideClick, true);
    document.removeEventListener('scroll', _closeBotCoachPopover, true);
  }
  function openBotCoachPopover(anchor, botMsg, corrections) {
    _closeBotCoachPopover();
    const isAdmin = state.me?.role === 'admin';
    const me = state.me?.username || '';
    const pop = el('div', { class: 'bot-coach-popover' });
    pop.appendChild(el('div', { class: 'bot-coach-popover-title', text: 'Coaching notes (' + corrections.length + ')' }));
    for (const c of corrections) {
      const row = el('div', { class: 'bot-coach-popover-row' + (c.isPermanent ? ' permanent' : '') });
      row.appendChild(el('div', { class: 'bot-coach-popover-meta', text: '@' + userLabel(c.agentUsername || '?') + ' · ' + MA.loc(new Date(c.createdAt || Date.now()), 'dt') + (c.isPermanent ? ' · ⭐ permanent' : '') }));
      row.appendChild(el('div', { class: 'bot-coach-popover-body', text: c.comment === 'good' ? '👍 marked as good' : c.comment }));
      const actions = el('div', { class: 'bot-coach-popover-actions' });
      if (isAdmin) {
        const promoteBtn = el('button', { class: 'btn btn-tiny', title: c.isPermanent ? 'Demote (no longer permanent)' : 'Promote to permanent rule', text: c.isPermanent ? '⭐ Demote' : '⭐ Promote' });
        promoteBtn.addEventListener('click', async () => {
          try {
            const r = await api('/api/bot/corrections/' + encodeURIComponent(c.id) + '/promote', { method: 'POST' });
            const j = await r.json();
            if (!r.ok) throw new Error(j.error || 'failed');
            toast(j.isPermanent ? 'Promoted to permanent rule' : 'Demoted', 'success');
            _closeBotCoachPopover();
            await refreshBotCorrectionsForCurrentChat();
            renderMessages();
          } catch (err) { toast('Failed: ' + (err.message || err), 'error'); }
        });
        actions.appendChild(promoteBtn);
      }
      if (isAdmin || c.agentUsername === me) {
        const delBtn = el('button', { class: 'btn btn-tiny btn-danger', title: 'Delete this note', text: '🗑' });
        delBtn.addEventListener('click', async () => {
          if (!confirm('Delete this coaching note?')) return;
          try {
            const r = await api('/api/bot/corrections/' + encodeURIComponent(c.id), { method: 'DELETE' });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.error || 'failed');
            toast('Deleted', 'success');
            _closeBotCoachPopover();
            await refreshBotCorrectionsForCurrentChat();
            renderMessages();
          } catch (err) { toast('Failed: ' + (err.message || err), 'error'); }
        });
        actions.appendChild(delBtn);
      }
      if (actions.children.length) row.appendChild(actions);
      pop.appendChild(row);
    }
    document.body.appendChild(pop);
    const r = anchor.getBoundingClientRect();
    pop.style.position = 'fixed';
    pop.style.left = Math.min(window.innerWidth - 340, Math.max(8, r.left - 100)) + 'px';
    pop.style.top = Math.min(window.innerHeight - 200, r.bottom + 6) + 'px';
    _botCoachPopover = pop;
    // Close on outside click / scroll. Capture phase so the click that
    // opened us doesn't immediately close us via bubbling. Register the
    // NAMED handler (see _onBotCoachOutsideClick above) so the matching
    // removeEventListener inside _closeBotCoachPopover actually unbinds.
    setTimeout(() => {
      document.addEventListener('click', _onBotCoachOutsideClick, true);
      document.addEventListener('scroll', _closeBotCoachPopover, true);
    }, 0);
  }
  // ============== /Bot coaching loop ==============
  // Escape closes the topmost open modal — standard expectation that was missing.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = [...document.querySelectorAll('.modal:not(.hidden)')].pop();
    if (open && open.id) closeModal(open.id);
  });

  // ============ Templates ============
  let editingTemplateId = null;

  // target: 'compose' (chat) | 'bulk' (Bulk send view) | 'hubspot' (HubSpot view)
  //         | 'reminder' (Today's Classes reminder modal) | 'manage' (top-level Templates tab)
  // — the same modal is reused for all targets; useTemplate() reads the target
  // and fills the right textarea, then closes. {name} stays literal in bulk/hubspot
  // so the server can substitute per-recipient at send time.
  async function openTemplatesModal(target) {
    state.templatePickerTarget = target || 'compose';
    // Auto-pick the pipeline-stage pill that matches the active chat's
    // current lead stage. When mid-conversation, the agent almost always
    // wants templates for the stage the lead is sitting on — so we open
    // the modal pre-filtered there. Falls back to 'all' when there's no
    // lead context (bulk send, hubspot, bulk-contacts, etc).
    let stage = 'all';
    if (target !== 'bulk-contacts' && target !== 'bulk-tests') {
      try {
        const leadStage = state.currentLead?.pipeline_stage || state.selectedChat?.pipelineStage;
        if (typeof leadStage === 'string' && PIPELINE_ALL.includes(leadStage)) stage = leadStage;
      } catch { /* */ }
    }
    state.templateActiveStage = stage;
    const archivedTab = document.querySelector('#tplStageTabs [data-tplstage="archived"]');
    const canManageArchived = state.templatePickerTarget === 'manage' && _isSalesAdmin();
    archivedTab?.classList.toggle('hidden', !canManageArchived);
    document.querySelectorAll('#tplStageTabs .tpl-stage-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.tplstage === stage);
    });
    const stageSel = document.getElementById('tplStage');
    if (stageSel && stage !== 'all' && stage !== 'unsorted') stageSel.value = stage;
    const search = document.getElementById('tplSearch');
    if (search) search.value = '';
    // bulk-contacts mode: stamp the contact count into the header so the
    // agent always sees "Templates · 93 contacts" before picking one.
    const headerTitle = document.querySelector('#templatesModal .modal-head h2');
    if (headerTitle) {
      // Strip any prior count suffix span so re-opens don't stack.
      headerTitle.querySelector('.tpl-bulk-count-suffix')?.remove();
      if (target === 'bulk-contacts' || target === 'bulk-tests') {
        const isContacts = target === 'bulk-contacts';
        // NB: CONTACTS is the closure-scoped Contacts-view state (it was never
        // exported to window — window.CONTACTS always read undefined → "0").
        const n = isContacts
          ? (CONTACTS.selectedIds?.size || 0)
          : (state.testsView?.selectedSlotIds?.size || 0);
        const noun = isContacts ? 'contact' : 'student';
        const suffix = el('span', {
          class: 'tpl-bulk-count-suffix',
          style: 'font-size:13px; font-weight:500; color:var(--text-secondary); margin-left:10px;',
          text: `· Send to ${n} ${noun}${n === 1 ? '' : 's'}`,
        });
        headerTitle.appendChild(suffix);
      }
    }
    // Property picker + live preview for the template-content editor. Built
    // once, reused on every open. Preview resolves against the open chat (if
    // any) so the agent sees real values; otherwise it shows examples.
    const vt = document.getElementById('tplVarTools');
    if (vt && !vt._built) {
      vt._built = true;
      const getTa = () => document.getElementById('tplContent');
      const prev = buildVarPreview(getTa, () => state.selectedChatId || null);
      const ins = buildVarInserter(getTa, () => prev._refresh());
      vt.appendChild(ins);
      vt.appendChild(prev);
      getTa()?.addEventListener('input', () => prev._refreshDebounced());
      vt._preview = prev;
    }
    if (vt?._preview) vt._preview._refresh();
    openModal('templatesModal');
    setTimeout(() => search?.focus(), 80);
    await loadTemplates();
  }
  state.templatesCache = [];
  state.templateActiveStage = 'all';

  async function loadTemplates() {
    const list = document.getElementById('tplList');
    clear(list);
    try {
      const archived = state.templateActiveStage === 'archived';
      const r = await api('/api/templates' + (archived ? '?archived=1' : ''));
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      state.templatesCache = j.templates || [];
      if (!archived) {
        const activeIds = new Set(state.templatesCache.map(t => t.id));
        state.selectedTemplateIds = new Set([...state.selectedTemplateIds].filter(id => activeIds.has(id)));
      }
      renderTemplatesList();
    } catch {
      list.appendChild(el('div', { class: 'empty-row', text: 'Failed to load templates' }));
    }
  }

  // Resolve a template's effective pipeline stage. Mirrors server-side
  // resolveTemplateStage. The server already populates `pipeline_stage`
  // on each row, but we re-derive defensively in case an older cache
  // surfaces a row without it.
  const TPL_META_PREFIX_TO_STAGE = {
    new: 'new', contacted: 'contacted', testdone: 'test_done', test: 'book_test',
    proforma: 'proforma_sent', paid: 'paid', noanswer: 'no_answer',
    noshow: 'no_show', notinterested: 'not_interested', not_interested: 'not_interested', lost: 'lost',
  };
  function templateStageOf(t) {
    if (PIPELINE_ALL.includes(t.pipeline_stage)) return t.pipeline_stage;
    const m = String(t.meta_name || '').toLowerCase();
    if (!m) return null;
    for (const [prefix, stage] of Object.entries(TPL_META_PREFIX_TO_STAGE)) {
      if (m.startsWith(prefix + '_')) return stage;
    }
    return null;
  }
  // Language detection: prefer the explicit meta_language, else fall
  // back to the flag emoji in the local-template name (🇲🇦/🇫🇷/🇬🇧).
  function templateLangOf(t) {
    const l = String(t.meta_language || '').toLowerCase();
    if (l === 'ar' || l === 'fr' || l === 'en') return l;
    const n = String(t.name || '');
    if (n.includes('🇲🇦')) return 'ar';
    if (n.includes('🇫🇷')) return 'fr';
    if (n.includes('🇬🇧')) return 'en';
    return 'zz';
  }
  const TPL_LANG_RANK = { ar: 0, fr: 1, en: 2, zz: 3 };

  function renderTemplatesList() {
    const list = document.getElementById('tplList');
    const counter = document.getElementById('tplCount');
    clear(list);
    const archivedView = state.templateActiveStage === 'archived';
    document.querySelector('#templatesModal .template-form')?.classList.toggle('hidden', archivedView);
    // Channel scoping by picker context (server default = 'wa' for legacy rows):
    //   - manage + bulk-contacts → both channels (managing / choosing per card)
    //   - compose with the SMS toggle ON → ONLY sms templates
    //   - every other (WhatsApp-bound) context → hide sms templates
    const tgt = state.templatePickerTarget || 'compose';
    const smsCompose = tgt === 'compose' && _composeChannel() === 'sms';
    const cached = state.templatesCache || [];
    const allRaw = (tgt === 'manage' || tgt === 'bulk-contacts')
      ? cached
      : cached.filter(t => ((t.channel || 'wa') === 'sms') === smsCompose);
    const stage = state.templateActiveStage || 'all';
    const all = (() => {
      if (stage === 'archived') return allRaw;
      if (stage === 'all') return allRaw;
      if (stage === 'unsorted') return allRaw.filter(t => !templateStageOf(t));
      return allRaw.filter(t => templateStageOf(t) === stage);
    })();
    if (!all.length) {
      const stageLabel = stage === 'all' ? '' : (PIPELINE_LABELS[stage] || stage);
      const msg = stage === 'archived'
        ? 'No archived templates.'
        : stage === 'all'
        ? 'No templates yet. Save your first one above.'
        : stage === 'unsorted'
          ? 'No unsorted templates — every template has a stage.'
          : `No templates yet for "${stageLabel}".`;
      list.appendChild(el('div', { class: 'empty-row', text: msg }));
      if (counter) counter.textContent = '';
      return;
    }
    const q = (document.getElementById('tplSearch')?.value || '').trim().toLowerCase();
    const matched = q ? all.filter(t =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.content || '').toLowerCase().includes(q)
    ) : all;
    // Sort priority: (Meta status — APPROVED first) → (language ar→fr→en)
    // → (name). Lets the agent reach the safe-to-send Darija copy first
    // when they pick a pipeline-stage pill, with French + English close
    // behind for cross-language teams.
    const statusRank = (t) => {
      if (t.meta_name && t.meta_status === 'APPROVED') return 0;
      if (t.meta_name && t.meta_status === 'PENDING') return 1;
      if (!t.meta_name) return 2;
      return 3;
    };
    const filtered = matched.slice().sort((a, b) => {
      const dr = statusRank(a) - statusRank(b);
      if (dr !== 0) return dr;
      const dl = (TPL_LANG_RANK[templateLangOf(a)] ?? 9) - (TPL_LANG_RANK[templateLangOf(b)] ?? 9);
      if (dl !== 0) return dl;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
    if (counter) counter.textContent = q ? `${filtered.length} / ${all.length} templates` : `${all.length} template${all.length > 1 ? 's' : ''}`;
    if (!filtered.length) {
      list.appendChild(el('div', { class: 'empty-row', text: 'Aucun résultat pour "' + q + '".' }));
      return;
    }
    filtered.forEach(t => list.appendChild(buildTemplateItem(t)));
    refreshIcons();
    updateTemplateMultiSendBar();
  }

  // Debounce keystrokes — 129 templates × full re-render per keypress was
  // 200-400ms of jank. 180ms feels typist-fast without firing on every keypress.
  let _tplSearchDebounceHandle = null;
  function onTemplatesSearchInput() {
    if (_tplSearchDebounceHandle) clearTimeout(_tplSearchDebounceHandle);
    _tplSearchDebounceHandle = setTimeout(() => {
      _tplSearchDebounceHandle = null;
      renderTemplatesList();
    }, 180);
  }

  // Multi-select for templates — agent can check 2-4 templates and
  // send them all to the currently open chat in one shot. State is
  // a Set of template IDs; the bottom bar mounts inside the modal
  // body the first time count > 0.
  state.selectedTemplateIds = state.selectedTemplateIds || new Set();

  function toggleTemplateSelection(id) {
    if (!id) return;
    if (state.selectedTemplateIds.has(id)) state.selectedTemplateIds.delete(id);
    else state.selectedTemplateIds.add(id);
    renderTemplatesList();
  }

  function clearTemplateSelection() {
    state.selectedTemplateIds.clear();
    renderTemplatesList();
  }

  function updateTemplateMultiSendBar() {
    const count = state.selectedTemplateIds.size;
    const target = state.templatePickerTarget || 'compose';
    let bar = document.getElementById('tplMultiSendBar');
    // Multi-send only makes sense in chat compose context. In bulk /
    // hubspot pickers the bar stays hidden — those flows are about
    // inserting one template into a composer/textarea.
    const allowed = target === 'compose';
    // Hide the bar while an inline edit is active so the agent
    // can't accidentally fire while typing into the form.
    const editing = !!state.inlineEditingTplId;
    if (!bar) {
      const modalBody = document.querySelector('#templatesModal .modal-body');
      if (!modalBody) return;
      bar = el('div', { id: 'tplMultiSendBar', class: 'tpl-multi-send-bar hidden' });
      const cancelBtn = el('button', { class: 'btn btn-ghost', id: 'tplMultiCancelBtn', text: 'Cancel' });
      const countEl = el('span', { class: 'tpl-multi-count', text: '0 selected' });
      const spacer = el('div', { style: 'flex:1;' });
      const sendBtn = el('button', { class: 'btn btn-primary', id: 'tplMultiSendBtn' }, [
        lucideIcon('send'),
        document.createTextNode(' Send 0 templates')
      ]);
      cancelBtn.addEventListener('click', clearTemplateSelection);
      sendBtn.addEventListener('click', sendSelectedTemplates);
      bar.appendChild(cancelBtn);
      bar.appendChild(countEl);
      bar.appendChild(spacer);
      bar.appendChild(sendBtn);
      modalBody.appendChild(bar);
      refreshIcons();
    }
    bar.querySelector('.tpl-multi-count').textContent = count + ' selected';
    const sendBtn = bar.querySelector('#tplMultiSendBtn');
    if (sendBtn) {
      // Replace the trailing text node with the new label.
      const label = ' Send ' + count + ' template' + (count === 1 ? '' : 's');
      const lastNode = sendBtn.lastChild;
      if (lastNode && lastNode.nodeType === Node.TEXT_NODE) lastNode.textContent = label;
    }
    bar.classList.toggle('hidden', !allowed || editing || count === 0);
  }

  async function sendSelectedTemplates() {
    if (!state.selectedChatId) { toast('Open a conversation first', 'error'); return; }
    // Snapshot the target chat ONCE — if the agent navigates to a
    // different conversation mid-send (each iteration awaits the
    // server + 1.5 s pacing), the loop must keep firing into the
    // chat the agent picked when they clicked Send. Routed through the
    // compose-channel retarget so multi-send respects the SMS toggle
    // (same fix as quickSendTemplate — raw selectedChatId ships "SMS"
    // sends down the WhatsApp branch).
    const targetChatId = _composeTargetChatId();
    const ids = Array.from(state.selectedTemplateIds);
    const ordered = (state.templatesCache || []).filter(t => ids.includes(t.id));
    if (!ordered.length) return;
    const sendBtn = document.getElementById('tplMultiSendBtn');
    if (sendBtn) sendBtn.disabled = true;
    closeModal('templatesModal');
    // Active reply target applies to the FIRST template only — the rest go
    // out as standalone messages so the agent isn't quoting the same source
    // N times. Snapshot + clear up front; restore on first-template failure
    // so the agent can retry without re-clicking the message they replied to.
    let pendingReplyTo = state.replyingTo?.id || null;
    const replyingSnapshot = state.replyingTo;
    if (pendingReplyTo) cancelReply();
    let sent = 0, failed = 0;
    for (let i = 0; i < ordered.length; i++) {
      const t = ordered[i];
      const text = applyTemplateVariables(t.content);
      if (!text.trim()) { failed++; continue; }
      const body = { chatId: targetChatId, text };
      if (pendingReplyTo) { body.replyTo = pendingReplyTo; pendingReplyTo = null; }
      try {
        // Support2 line: the rendered text rides the text bridge (no quoting
        // there — support2's socket gets plain text only).
        const r = _composeViaSupport2Line(targetChatId)
          ? await api('/api/inbox/chats/' + encodeURIComponent(targetChatId) + '/send-via-support2', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text })
          })
          : await api('/api/inbox/send', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          });
        const data = await r.json().catch(() => ({}));
        if (!r.ok || data.error) {
          failed++;
          toast(t.name + ': ' + (data.error || 'send failed'), 'error');
          if (i === 0 && replyingSnapshot && !state.replyingTo) { state.replyingTo = replyingSnapshot; renderReplyPreview(); }
        } else {
          sent++;
          // Only push the optimistic bubble into the visible chat
          // when the agent didn't navigate away — otherwise the
          // user would see a stranger's message appear in their
          // current conversation.
          if (state.selectedChatId === targetChatId) {
            state.messages.push(data.message || {
              id: Math.random().toString(36).slice(2),
              chatId: targetChatId, fromMe: true, type: 'text', text, timestamp: Date.now()
            });
            if (typeof renderMessages === 'function') renderMessages();
          }
        }
      } catch (err) {
        failed++;
        toast(t.name + ': ' + (err?.message || 'network error'), 'error');
        if (i === 0 && replyingSnapshot && !state.replyingTo) { state.replyingTo = replyingSnapshot; renderReplyPreview(); }
      }
      // 1.5 s pacing between sends — same anti-ban delay used by
      // /api/send-media and the forward flow.
      if (i < ordered.length - 1) await new Promise(r => setTimeout(r, 1500));
    }
    if (sendBtn) sendBtn.disabled = false;
    state.selectedTemplateIds.clear();
    if (failed === 0) toast('Sent ' + sent + ' template' + (sent === 1 ? '' : 's'), 'success');
    else toast(`Sent ${sent}/${ordered.length} (${failed} failed)`, failed === ordered.length ? 'error' : 'info');
  }

  function buildTemplateItem(t) {
    const target = state.templatePickerTarget || 'compose';

    if (t.archivedAt) {
      const restoreBtn = el('button', {
        class: 'btn btn-ghost',
        type: 'button',
        title: 'Restore this template to the active library',
      }, [lucideIcon('archive-restore'), document.createTextNode(' Restore')]);
      restoreBtn.addEventListener('click', () => restoreTemplate(t, restoreBtn));
      const archivedWhen = new Date(t.archivedAt);
      const archiveMeta = Number.isNaN(archivedWhen.getTime())
        ? 'Archived'
        : 'Archived ' + MA.loc(archivedWhen, 'dt');
      return el('div', { class: 'tpl-item' }, [
        el('div', { class: 'tpl-body' }, [
          el('div', { class: 'tpl-name' }, [
            el('span', { text: t.name }),
            el('span', { class: 'tpl-meta-badge tpl-meta-local', text: 'Archived' }),
          ]),
          el('div', { class: 'tpl-content', text: t.content }),
          el('div', {
            style: 'font-size:11px;color:var(--gray-500);margin-top:5px;',
            text: archiveMeta + (t.archivedBy ? ' by ' + userLabel(t.archivedBy) : ''),
          }),
        ]),
        el('div', { class: 'tpl-actions' }, [restoreBtn]),
      ]);
    }

    // Inline edit mode — clicking ✏️ flips this single card into a
    // form so the agent can update name / pipeline_stage / content
    // right here. Save = PUT then exit edit mode; Cancel = exit.
    if (state.inlineEditingTplId === t.id) {
      const currentStage = templateStageOf(t) || '';
      const nameInput = el('input', { class: 'input tpl-edit-name', type: 'text', value: t.name, placeholder: 'Template name' });
      const contentTa = el('textarea', { class: 'input tpl-edit-content', rows: 4, placeholder: 'Template content. Use {{name}}, {{agent}}, {{date}}, {{time}} as variables.' });
      contentTa.value = t.content || '';
      const stageSelect = el('select', { class: 'input tpl-edit-stage' });
      // First option = unset (falls back to meta_name derivation on the server).
      stageSelect.appendChild(el('option', { value: '', text: '— Unsorted —' }));
      PIPELINE_ALL.forEach(s => {
        const opt = el('option', { value: s, text: PIPELINE_LABELS[s] || s });
        if (s === currentStage) opt.selected = true;
        stageSelect.appendChild(opt);
      });
      // Channel select — WhatsApp (Meta pipeline) vs SMS (Yeastar gateway).
      const channelSelect = el('select', { class: 'input tpl-edit-channel' });
      channelSelect.appendChild(el('option', { value: 'wa', text: 'WhatsApp' }));
      channelSelect.appendChild(el('option', { value: 'sms', text: 'SMS' }));
      channelSelect.value = (t.channel === 'sms') ? 'sms' : 'wa';
      const saveBtn = el('button', { class: 'btn btn-primary' }, [lucideIcon('save'), document.createTextNode(' Save')]);
      const cancelBtn = el('button', { class: 'btn btn-ghost', text: 'Cancel' });
      saveBtn.addEventListener('click', () => saveInlineTemplateEdit(t.id, nameInput, contentTa, stageSelect, saveBtn, channelSelect));
      cancelBtn.addEventListener('click', () => { state.inlineEditingTplId = null; renderTemplatesList(); });
      return el('div', { class: 'tpl-item tpl-item-editing' }, [
        el('div', { class: 'tpl-edit-form' }, [
          nameInput,
          contentTa,
          el('div', { class: 'tpl-edit-actions' }, [
            el('label', { style: 'font-size:12px;color:var(--gray-600);', text: 'Canal' }),
            channelSelect,
            el('label', { style: 'font-size:12px;color:var(--gray-600);', text: 'Stage' }),
            stageSelect,
            saveBtn,
            cancelBtn,
          ]),
        ]),
      ]);
    }

    // bulk-contacts target = template picker opened from the Contacts bulk
    // toolbar. There's no composer to "Use" into and no single chat to quick-
    // send into — every click here fires one bulk POST that sprays the
    // template across every selected contact. Replace useBtn with a primary
    // green "Send to N" button + hide the (otherwise-inert) compose buttons.
    // bulk-tests = same UX but sourced from the new Tests-view selection
    // (slot ids → student phones → mirror lookup server-side). Both modes
    // share the per-card Send-to-N green button.
    const isBulkContacts = target === 'bulk-contacts';
    const isBulkTests = target === 'bulk-tests';
    const isAnyBulk = isBulkContacts || isBulkTests;
    const useBtn = isAnyBulk
      ? null
      : (() => {
          const b = el('button', { class: 'icon-btn-sm', 'aria-label': 'Use', title: 'Use (insert into composer)' }, [lucideIcon('arrow-left')]);
          b.addEventListener('click', () => useTemplate(t));
          return b;
        })();

    // Quick-send: only available in chat compose context (bulk / hubspot need full compose flow)
    let sendBtn = null;
    if (target === 'compose') {
      sendBtn = el('button', { class: 'icon-btn-sm tpl-send-btn', 'aria-label': 'Send now', title: 'Envoyer immédiatement' }, [lucideIcon('send')]);
      sendBtn.addEventListener('click', () => quickSendTemplate(t));
    } else if (isAnyBulk) {
      const n = isBulkContacts
        ? (CONTACTS.selectedIds?.size || 0)   // closure state — window.CONTACTS was never set
        : (state.testsView?.selectedSlotIds?.size || 0);
      const noun = isBulkContacts ? 'contact' : 'student';
      if (isBulkContacts && t.channel === 'sms') {
        // SMS template card in the Contacts bulk picker → one POST to
        // /api/sms/bulk-send-template that ENQUEUES per contact; the office
        // bridge drains the queue at ~1 SMS / 2 min per SIM. Disabled when the
        // body carries manual/unknown vars — the server preflight would 400.
        const bulkable = !!t.resolvable && !(t.manualParams || []).length;
        sendBtn = el('button', {
          class: 'cbb-btn cbb-btn-primary tpl-bulk-send-btn',
          title: bulkable
            ? `Envoyer ce template par SMS à ${n} contact${n === 1 ? '' : 's'} (envoi progressif anti-blocage)`
            : 'Variables manuelles/inconnues dans le texte — non envoyable en masse',
          type: 'button',
        }, [lucideIcon('send'), document.createTextNode(` SMS to ${n}`)]);
        if (!bulkable) sendBtn.disabled = true;
        sendBtn.addEventListener('click', () => _bulkSendSmsTemplateById(t.id, sendBtn));
      } else {
        // Visual + behavioural match to the Send template bar button: green WA
        // pill with the send icon. Disabled state = non-APPROVED templates,
        // since the server will reject them anyway.
        const approved = !!(t.meta_name && (!t.meta_status || t.meta_status === 'APPROVED'));
        sendBtn = el('button', {
          class: 'cbb-btn cbb-btn-primary tpl-bulk-send-btn',
          title: approved
            ? `Send this template to ${n} selected ${noun}${n === 1 ? '' : 's'}`
            : 'Template not Meta-approved — won\'t send outside the 24h window',
          type: 'button',
        }, [lucideIcon('send'), document.createTextNode(` Send to ${n}`)]);
        if (!approved) sendBtn.disabled = true;
        sendBtn.addEventListener('click', () => {
          if (isBulkTests) _testsBulkSendTemplate(t.id);
          else _bulkSendTemplateById(t.id);
        });
      }
    }
    const editBtn = el('button', { class: 'icon-btn-sm', 'aria-label': 'Edit', title: 'Edit' }, [lucideIcon('pencil')]);
    editBtn.addEventListener('click', () => editTemplate(t));
    let archiveBtn = null;
    if (_isSalesAdmin() && target === 'manage') {
      archiveBtn = el('button', { class: 'icon-btn-sm', 'aria-label': 'Archive', title: 'Archive (recoverable)' }, [lucideIcon('archive')]);
      archiveBtn.addEventListener('click', () => archiveTemplate(t));
    }
    // Meta template actions (admin / super_sales only): submit a local template
    // to Meta for approval, and refresh its status while PENDING. Hidden for
    // regular agents and for already-APPROVED rows (no re-submit needed).
    let metaBtn = null, refreshBtn = null;
    if (_isSalesAdmin() && t.channel !== 'sms') {
      if (t.meta_status !== 'APPROVED') {
        metaBtn = el('button', { class: 'icon-btn-sm tpl-meta-btn', 'aria-label': 'Submit to Meta', title: t.meta_name ? 'Re-submit to Meta' : 'Create this template in Meta (send outside 24h)' }, [lucideIcon('upload-cloud')]);
        metaBtn.addEventListener('click', () => openMetaTemplateModal(t));
      }
      if (t.meta_name && t.meta_status === 'PENDING') {
        refreshBtn = el('button', { class: 'icon-btn-sm tpl-meta-refresh-btn', 'aria-label': 'Refresh Meta status', title: 'Check Meta approval status' }, [lucideIcon('refresh-cw')]);
        refreshBtn.addEventListener('click', () => refreshMetaStatus(t));
      }
    }
    // Resolve the effective pipeline stage for this template, used by
    // the inline stage tag rendered next to the meta-status badge.
    const stage = templateStageOf(t);
    // WABA status badge — tells the agent at a glance whether this
    // template will route via type=template (delivers outside 24h) or
    // fall back to free-form text (will fail outside 24h on first-touch).
    // Localised inline so we don't need a translation dictionary.
    let metaBadge = null;
    if (t.channel === 'sms') {
      // SMS templates live outside the Meta lifecycle entirely — one distinct
      // badge instead of the approval traffic-light. Accent tokens keep it
      // visually separate from the green/yellow/red Meta states in both themes.
      metaBadge = el('span', {
        class: 'tpl-meta-badge tpl-channel-sms',
        style: 'background:var(--accent-soft);color:var(--accent);',
        title: 'Template SMS · envoyé via la passerelle SMS (aucune validation Meta requise)',
        text: '✉ SMS',
      });
    } else if (t.meta_name && t.meta_status === 'APPROVED') {
      metaBadge = el('span', { class: 'tpl-meta-badge tpl-meta-approved', title: 'Meta-approved · ready to send to any contact', text: '✓ Meta' });
    } else if (t.meta_name && t.meta_status === 'PENDING') {
      metaBadge = el('span', { class: 'tpl-meta-badge tpl-meta-pending', title: 'Pending Meta approval · may fail outside 24h window', text: '⏳ Meta' });
    } else if (t.meta_name && t.meta_status === 'REJECTED') {
      metaBadge = el('span', { class: 'tpl-meta-badge tpl-meta-rejected', title: 'Rejected by Meta · won\'t send outside 24h window', text: '✗ Meta' });
    } else {
      metaBadge = el('span', { class: 'tpl-meta-badge tpl-meta-local', title: 'Local-only · works inside 24h only', text: '◌ Local' });
    }
    // Pipeline-stage tag — replaces the old Student/Group/etc. category.
    // Renders only when the row has a resolved stage; "Unsorted" rows
    // intentionally show no tag so the visual chrome stays clean.
    const stageTag = stage
      ? el('span', { class: 'tpl-stage-tag tpl-stage-tag-' + stage, text: PIPELINE_LABELS[stage] || stage })
      : null;
    const nameRow = el('div', { class: 'tpl-name' }, [
      el('span', { text: t.name }),
      metaBadge,
      stageTag,
    ].filter(Boolean));
    // Checkbox for multi-template send. Hidden in non-compose
    // pickers (bulk / hubspot) since multi-send only makes sense
    // when a single chat is the target.
    const isSelected = state.selectedTemplateIds.has(t.id);
    const checkbox = el('button', {
      class: 'tpl-checkbox' + (isSelected ? ' selected' : ''),
      'aria-label': isSelected ? 'Unselect template' : 'Select template',
      type: 'button',
    }, [lucideIcon(isSelected ? 'check' : 'circle')]);
    checkbox.addEventListener('click', (ev) => {
      ev.stopPropagation();
      toggleTemplateSelection(t.id);
    });
    const item = el('div', { class: 'tpl-item' + (isSelected ? ' tpl-item-selected' : '') }, [
      target === 'compose' ? checkbox : null,
      el('div', { class: 'tpl-body' }, [
        nameRow,
        el('div', { class: 'tpl-content', text: t.content })
      ]),
      el('div', { class: 'tpl-actions' }, [useBtn, sendBtn, metaBtn, refreshBtn, editBtn, archiveBtn].filter(Boolean))
    ].filter(Boolean));
    return item;
  }

  async function saveInlineTemplateEdit(id, nameInput, contentTa, stageSelect, saveBtn, channelSelect) {
    const name = nameInput.value.trim();
    const content = contentTa.value.trim();
    // '' (Unsorted) means clear the explicit override on the server — the
    // row falls back to meta_name derivation.
    const pipeline_stage = stageSelect.value || '';
    const channel = channelSelect?.value === 'sms' ? 'sms' : 'wa';
    if (!name || !content) { toast('Name and content required', 'error'); return; }
    saveBtn.disabled = true;
    try {
      const r = await api('/api/templates/' + encodeURIComponent(id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, content, pipeline_stage, channel }),
      });
      if (!r.ok) throw new Error();
      state.inlineEditingTplId = null;
      await loadTemplates();
      toast('Saved', 'success');
    } catch {
      toast('Échec de la sauvegarde — vérifiez votre connexion', 'error');
      saveBtn.disabled = false;
    }
  }

  async function quickSendTemplate(t) {
    if (!state.selectedChatId) { toast('Open a conversation first', 'error'); return; }
    const text = applyTemplateVariables(t.content);
    if (!text.trim()) { toast('Template vide', 'error'); return; }
    // Honor the WhatsApp/SMS compose toggle — same retarget as sendText.
    // Posting state.selectedChatId raw would push an SMS-channel template
    // down the WhatsApp free-form branch (fails outside the 24h window,
    // silently goes out as WhatsApp inside it).
    const targetChatId = _composeTargetChatId();
    // Inherit the active reply target (if any) so a quick-send template lands
    // as a quoted reply, not a fresh message. Mirrors sendText's snapshot +
    // restore-on-fail pattern so the agent can retry without re-clicking.
    const replyTo = state.replyingTo?.id || null;
    const replyingSnapshot = state.replyingTo;
    if (replyTo) cancelReply();
    closeModal('templatesModal');
    try {
      const r = _composeViaSupport2Line(targetChatId)
        ? await api('/api/inbox/chats/' + encodeURIComponent(targetChatId) + '/send-via-support2', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text })
        })
        : await api('/api/inbox/send', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chatId: targetChatId, text, replyTo })
        });
      const data = await r.json().catch(() => ({}));
      if (data.error) {
        toast(data.error, 'error');
        if (replyingSnapshot && !state.replyingTo) { state.replyingTo = replyingSnapshot; renderReplyPreview(); }
        return;
      }
      if (replyTo && data.replyResolved === false) {
        toast('Original message no longer in history — sent as plain message', 'warning');
      }
      state.messages.push(data.message || {
        id: Math.random().toString(36).slice(2),
        chatId: state.selectedChatId, fromMe: true, type: 'text', text, timestamp: Date.now()
      });
      if (typeof renderMessages === 'function') renderMessages();
      toast('Template envoyé', 'success');
    } catch {
      toast('Échec d\'envoi', 'error');
      if (replyingSnapshot && !state.replyingTo) { state.replyingTo = replyingSnapshot; renderReplyPreview(); }
    }
  }
  // ─── Tutoriels (media templates) ──────────────────────────────────────────
  // A library of saved how-to clips ("kifach nkhdem b l'app", "kifach n-activi
  // le son", "installation PC"). Clicking a tile SENDS it into the open
  // conversation right away — the same one-click contract as the text
  // templates' quick-send button. Uploading is sales-admin only; the panel is
  // hidden for everyone else by the [data-sales-admin-only] CSS rule.
  const MTPL_ALL = '__all__';

  async function openMediaTemplatesModal() {
    if (!state.selectedChatId) { toast('Ouvre une conversation d\'abord', 'error'); return; }
    state.mediaTemplateCategory = MTPL_ALL;
    state.mediaTemplateSearch = '';
    const search = document.getElementById('mtplSearch');
    if (search) search.value = '';
    _mtplCloseUpload();
    openModal('mediaTemplatesModal');
    // Always refetch on open: send counts and newly uploaded tutorials should
    // be current, and the payload is a handful of rows.
    await loadMediaTemplates();
  }

  async function loadMediaTemplates() {
    const grid = document.getElementById('mtplGrid');
    if (!grid) return;
    _mtplState(grid, 'loader', 'Chargement des tutoriels…');
    try {
      // Admins also get deactivated rows so they can spot and restore one
      // without a trip to the database.
      const r = await api('/api/media-templates' + (_isSalesAdmin() ? '?includeInactive=true' : ''));
      const data = await r.json().catch(() => ({}));
      if (data.error) throw new Error(data.error);
      state.mediaTemplates = Array.isArray(data.items) ? data.items : [];
      renderMediaTemplates();
    } catch (err) {
      state.mediaTemplates = null;
      _mtplState(grid, 'alert-triangle', err.message || 'Impossible de charger les tutoriels', true);
    }
  }

  function _mtplState(grid, icon, message, isError) {
    clear(grid);
    grid.appendChild(el('div', { class: 'mtpl-state' + (isError ? ' is-error' : '') }, [
      lucideIcon(icon),
      el('span', { text: message }),
    ]));
    refreshIcons(grid);
  }

  function _mtplBytes(n) {
    const b = Number(n) || 0;
    if (b >= 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
    if (b >= 1024) return Math.round(b / 1024) + ' KB';
    return b + ' B';
  }

  function _mtplDuration(ms) {
    const total = Math.round((Number(ms) || 0) / 1000);
    if (!total) return '';
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  function renderMediaTemplates() {
    const grid = document.getElementById('mtplGrid');
    const chipsBox = document.getElementById('mtplChips');
    const countBox = document.getElementById('mtplCount');
    if (!grid) return;
    const all = Array.isArray(state.mediaTemplates) ? state.mediaTemplates : [];

    // Chips are derived from the data: the team invents categories as it
    // uploads, so a hardcoded list would be stale by the second tutorial.
    if (chipsBox) {
      const cats = [...new Set(all.map(t => (t.category || '').trim()).filter(Boolean))].sort();
      clear(chipsBox);
      const mkChip = (value, label) => {
        const btn = el('button', {
          type: 'button',
          class: 'tpl-stage-btn' + (state.mediaTemplateCategory === value ? ' active' : ''),
          text: label,
          onclick: () => { state.mediaTemplateCategory = value; renderMediaTemplates(); },
        });
        return btn;
      };
      chipsBox.appendChild(mkChip(MTPL_ALL, 'Tout'));
      for (const c of cats) chipsBox.appendChild(mkChip(c, c));
      chipsBox.classList.toggle('hidden', cats.length === 0);

      // Same list feeds the upload form's autocomplete. Without it "Application"
      // and "application" become two chips for the same thing.
      const datalist = document.getElementById('mtplCategoryList');
      if (datalist) {
        clear(datalist);
        for (const c of cats) datalist.appendChild(el('option', { value: c }));
      }
    }

    const q = String(state.mediaTemplateSearch || '').trim().toLowerCase();
    const items = all.filter(t => {
      if (state.mediaTemplateCategory !== MTPL_ALL && (t.category || '') !== state.mediaTemplateCategory) return false;
      if (!q) return true;
      return `${t.title} ${t.description} ${t.category} ${t.caption}`.toLowerCase().includes(q);
    });

    if (countBox) {
      countBox.textContent = items.length
        ? `${items.length} tutoriel${items.length > 1 ? 's' : ''}`
        : '';
    }

    clear(grid);
    if (!items.length) {
      grid.appendChild(el('div', { class: 'mtpl-state' }, [
        lucideIcon(all.length ? 'search-x' : 'circle-play'),
        el('strong', { text: all.length ? 'Aucun résultat' : 'Pas encore de tutoriel' }),
        el('span', {
          text: all.length
            ? 'Essaie un autre mot ou une autre catégorie.'
            : 'Ajoute une vidéo ou une photo : elle sera compressée et prête à envoyer en un clic.',
        }),
      ]));
      refreshIcons(grid);
      return;
    }

    for (const t of items) grid.appendChild(_mtplTile(t));
    refreshIcons(grid);
  }

  function _mtplTile(t) {
    const thumbKids = [];
    if (t.posterUrl || t.kind === 'image') {
      thumbKids.push(el('img', { src: t.posterUrl || t.url, alt: '', loading: 'lazy' }));
    } else {
      thumbKids.push(el('div', { class: 'mtpl-thumb-fallback' }, [lucideIcon('film')]));
    }
    if (t.kind === 'video') {
      thumbKids.push(el('div', { class: 'mtpl-play' }, [el('span', {}, [lucideIcon('play')])]));
      const dur = _mtplDuration(t.durationMs);
      if (dur) thumbKids.push(el('div', { class: 'mtpl-duration', text: dur }));
    }

    const meta = [el('span', { text: _mtplBytes(t.byteSize) })];
    if (t.category) meta.unshift(el('span', { class: 'mtpl-tile-cat', text: t.category }));
    if (t.sendCount) meta.push(el('span', { text: `· ${t.sendCount} envoi${t.sendCount > 1 ? 's' : ''}` }));

    // The send action is ONE button covering the whole tile. The admin
    // controls are siblings, not children: a <button> inside a <button> is
    // invalid — the HTML parser closes the outer one and the tile falls apart.
    const main = el('button', {
      type: 'button',
      class: 'mtpl-tile-main',
      title: t.description || t.title,
      onclick: () => sendMediaTemplate(t, tile),
    }, [
      el('div', { class: 'mtpl-thumb' }, thumbKids),
      el('div', { class: 'mtpl-tile-body' }, [
        el('div', { class: 'mtpl-tile-title', text: t.title }),
        el('div', { class: 'mtpl-tile-meta' }, meta),
      ]),
      el('div', { class: 'mtpl-send-hint' }, [lucideIcon('send'), el('span', { text: 'Envoyer' })]),
    ]);

    const kids = [main];
    if (_isSalesAdmin()) {
      kids.push(el('div', { class: 'mtpl-tile-admin' }, [
        el('button', {
          type: 'button', title: t.isActive ? 'Désactiver' : 'Réactiver',
          'aria-label': t.isActive ? 'Désactiver' : 'Réactiver',
          onclick: () => _mtplToggleActive(t),
        }, [lucideIcon(t.isActive ? 'eye-off' : 'eye')]),
        el('button', {
          type: 'button', class: 'mtpl-del', title: 'Supprimer', 'aria-label': 'Supprimer',
          onclick: () => _mtplDelete(t),
        }, [lucideIcon('trash-2')]),
      ]));
    }

    const tile = el('div', {
      class: 'mtpl-tile' + (t.isActive ? '' : ' is-inactive'),
    }, kids);
    return tile;
  }

  // One click = one send. Mirrors quickSendTemplate: close the picker, post,
  // push the returned message into the thread, toast the outcome.
  async function sendMediaTemplate(t, tile) {
    if (!state.selectedChatId) { toast('Ouvre une conversation d\'abord', 'error'); return; }
    if (tile) tile.classList.add('is-sending');
    const targetChatId = state.selectedChatId;
    const caption = applyTemplateVariables(t.caption || '');
    closeModal('mediaTemplatesModal');
    try {
      // "Support2" picked in the composer → the clip leaves from support2's
      // number, like every other attachment (PR #410).
      const payload = { chatId: targetChatId, caption };
      if (_composeViaSupport2Line(targetChatId)) payload.via = 'support2';
      const r = await api(`/api/media-templates/${encodeURIComponent(t.id)}/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        // support2 compresses before its WhatsApp upload (server waits 120 s).
        timeoutMs: payload.via ? 130000 : undefined,
      });
      const data = await r.json().catch(() => ({}));
      // A 502 page during a deploy has no JSON error — never call that a success.
      if (data.error || !r.ok) { toast(data.error || `Échec d'envoi (HTTP ${r.status})`, 'error'); return; }
      if (data.message && state.selectedChatId === targetChatId) {
        state.messages.push(data.message);
        if (typeof renderMessages === 'function') renderMessages();
      }
      t.sendCount = (Number(t.sendCount) || 0) + 1;
      toast('Tutoriel envoyé', 'success');
    } catch {
      toast('Échec d\'envoi', 'error');
    } finally {
      if (tile) tile.classList.remove('is-sending');
    }
  }

  function _mtplCloseUpload() {
    const panel = document.getElementById('mtplUploadPanel');
    if (panel) panel.classList.add('hidden');
    const status = document.getElementById('mtplUploadStatus');
    if (status) { status.textContent = ''; status.className = 'mtpl-upload-status'; }
    for (const id of ['mtplTitle', 'mtplCategory', 'mtplCaption', 'mtplFile', 'mtplPoster']) {
      const node = document.getElementById(id);
      if (node) node.value = '';
    }
  }

  function _mtplUploadStatus(text, kind) {
    const status = document.getElementById('mtplUploadStatus');
    if (!status) return;
    status.textContent = text || '';
    status.className = 'mtpl-upload-status' + (kind ? ' is-' + kind : '');
  }

  async function _mtplUpload() {
    const title = String(document.getElementById('mtplTitle')?.value || '').trim();
    const fileInput = document.getElementById('mtplFile');
    const file = fileInput?.files?.[0];
    if (!title) { _mtplUploadStatus('Donne un titre au tutoriel.', 'error'); return; }
    if (!file) { _mtplUploadStatus('Choisis une vidéo ou une photo.', 'error'); return; }

    const saveBtn = document.getElementById('mtplUploadSave');
    if (saveBtn) saveBtn.disabled = true;
    // A screen recording takes a real transcode server-side, so say so rather
    // than leaving the admin staring at a frozen button.
    _mtplUploadStatus('Compression en cours… ça peut prendre une minute.');

    const fd = new FormData();
    fd.append('title', title);
    fd.append('category', String(document.getElementById('mtplCategory')?.value || '').trim());
    fd.append('caption', String(document.getElementById('mtplCaption')?.value || '').trim());
    fd.append('file', file, file.name);
    // Optional designed cover; the server only uses it for a video and falls
    // back to a frame of the video when it is missing or unusable.
    const poster = document.getElementById('mtplPoster')?.files?.[0];
    if (poster && String(file.type || '').startsWith('video/')) fd.append('poster', poster, poster.name);
    try {
      // FormData must keep its own multipart boundary — fetchT only adds an
      // AbortController, api() would rewrite the headers. 6 min covers a long
      // screen recording's two-pass encode.
      const r = await fetchT('/api/media-templates', { method: 'POST', headers: authHeaders(), body: fd }, 360000);
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.error) { _mtplUploadStatus(data.error || 'Upload échoué', 'error'); return; }
      _mtplCloseUpload();
      toast('Tutoriel ajouté', 'success');
      await loadMediaTemplates();
    } catch {
      _mtplUploadStatus('Upload échoué — réessaie.', 'error');
    } finally {
      if (saveBtn) saveBtn.disabled = false;
    }
  }

  async function _mtplToggleActive(t) {
    try {
      const r = await api(`/api/media-templates/${encodeURIComponent(t.id)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !t.isActive }),
      });
      const data = await r.json().catch(() => ({}));
      if (data.error) { toast(data.error, 'error'); return; }
      await loadMediaTemplates();
    } catch { toast('Échec', 'error'); }
  }

  async function _mtplDelete(t) {
    if (!confirm(`Supprimer définitivement « ${t.title} » ?`)) return;
    try {
      const r = await api(`/api/media-templates/${encodeURIComponent(t.id)}`, { method: 'DELETE' });
      const data = await r.json().catch(() => ({}));
      if (data.error) { toast(data.error, 'error'); return; }
      toast('Tutoriel supprimé', 'success');
      await loadMediaTemplates();
    } catch { toast('Échec', 'error'); }
  }

  function applyTemplateVariables(text) {
    const now = new Date();
    // {{name}} uses the contact's FIRST name only — matches the
    // server-side auto-router's resolver, so picking a template +
    // sending it as-is reliably routes via type=template instead of
    // falling through to free-form text. Resolution order:
    //   1. state.selectedChat.firstname (from HubSpot mirror, server-supplied)
    //   2. first whitespace-separated word of selectedChat.name
    //   3. raw selectedChat.name (last-ditch)
    const chat = state.selectedChat || {};
    const fullName = chat.name || '';
    const firstFromMirror = (chat.firstname || '').trim();
    const firstFromName = String(fullName).trim().split(/\s+/)[0] || '';
    const name = firstFromMirror || firstFromName || fullName;
    const agent = state.me?.displayName || state.me?.username || '';
    const date = MA.loc(now, 'd', 'en-GB');
    const time = MA.loc(now, 't', 'en-GB', { hour: '2-digit', minute: '2-digit' });
    return String(text || '')
      // Double-brace (Meta-style) — substitute first so the single-brace regex
      // below doesn't strip the inner braces and leave a half-substituted "{name}".
      .replace(/\{\{name\}\}/g, name)
      .replace(/\{\{agent\}\}/g, agent)
      .replace(/\{\{date\}\}/g, date)
      .replace(/\{\{time\}\}/g, time)
      // Single-brace (support2 legacy) — kept for templates that predate the Meta-style import.
      .replace(/\{name\}/g, name)
      .replace(/\{date\}/g, date)
      .replace(/\{time\}/g, time);
  }
  function useTemplate(t) {
    const target = state.templatePickerTarget || 'compose';
    if (target === 'bulk') {
      const ta = document.getElementById('bulkMessageInput');
      if (!ta) return;
      // Keep {name} literal so the server can substitute per-group at send time;
      // still expand {date} and {time} to today's values.
      const now = new Date();
      ta.value = String(t.content || '')
        .replace(/\{date\}/g, MA.loc(now, 'd', 'en-GB'))
        .replace(/\{time\}/g, MA.loc(now, 't', 'en-GB', { hour: '2-digit', minute: '2-digit' }));
      closeModal('templatesModal');
      ta.focus();
      return;
    }
    if (target === 'hubspot') {
      const ta = document.getElementById('hsMessageInput');
      if (!ta) return;
      const now = new Date();
      ta.value = String(t.content || '')
        .replace(/\{date\}/g, MA.loc(now, 'd', 'en-GB'))
        .replace(/\{time\}/g, MA.loc(now, 't', 'en-GB', { hour: '2-digit', minute: '2-digit' }));
      closeModal('templatesModal');
      ta.focus();
      return;
    }
    if (target === 'teamchat') {
      const ta = document.querySelector('#teamchatActiveChat .teamchat-textarea');
      if (!ta) { toast('Open a teammate first', 'error'); return; }
      // {name} resolves to the teammate's name in DM context, or the room
      // name in room context. {date} and {time} expand to today's values.
      const target = state.teamchatActiveTarget || {};
      const now = new Date();
      const memberName = target.type === 'room'
        ? (target.room?.name || '')
        : userLabel(target.user?.username, target.user?.displayName || target.user?.username || '');
      ta.value = String(t.content || '')
        .replace(/\{name\}/g, memberName)
        .replace(/\{date\}/g, MA.loc(now, 'd', 'en-GB'))
        .replace(/\{time\}/g, MA.loc(now, 't', 'en-GB', { hour: '2-digit', minute: '2-digit' }));
      closeModal('templatesModal');
      ta.focus();
      return;
    }
    // Default: chat compose input — needs an active chat for {name} substitution
    if (!state.selectedChatId) { toast('Open a conversation first', 'error'); return; }
    E.composeInput.value = applyTemplateVariables(t.content);
    autoSize(); toggleSendBtn();
    closeModal('templatesModal');
    E.composeInput.focus();
    // Track the picked WABA-mapped template so the send path can route via
    // type=template (the only way to reach a contact outside the 24h
    // customer-service window). Cleared the moment the agent edits the
    // compose text away from the templated body — at that point Meta would
    // reject the template send anyway because the body wouldn't match.
    if (t.meta_name && t.meta_status === 'APPROVED') {
      state.composeTemplate = t;
      state.composeTemplateRendered = E.composeInput.value;
      updateTemplateBadge();
    } else {
      state.composeTemplate = null;
      state.composeTemplateRendered = '';
      updateTemplateBadge();
    }
  }

  // Inline "📋 WABA template" badge on the compose row — tells the agent
  // the next send will go via type=template instead of free-form text.
  function updateTemplateBadge() {
    let badge = document.getElementById('composeTemplateBadge');
    if (state.composeTemplate) {
      if (!badge) {
        badge = el('div', { id: 'composeTemplateBadge', class: 'compose-template-badge' });
        const composeArea = E.composeInput?.parentElement;
        if (composeArea) composeArea.insertBefore(badge, composeArea.firstChild);
      }
      const t = state.composeTemplate;
      badge.innerHTML = '';
      badge.appendChild(el('span', { class: 'ctb-icon', text: '📋' }));
      badge.appendChild(el('span', { class: 'ctb-label', text: 'WABA template: ' + t.meta_name + ' [' + (t.meta_language || '?') + ']' }));
      const clearBtn = el('button', { class: 'ctb-clear', type: 'button', 'aria-label': 'Send as free-form text instead' });
      clearBtn.textContent = '×';
      clearBtn.addEventListener('click', () => {
        state.composeTemplate = null;
        state.composeTemplateRendered = '';
        updateTemplateBadge();
      });
      badge.appendChild(clearBtn);
    } else if (badge) {
      badge.remove();
    }
  }
  function editTemplate(t) {
    // Inline edit — flip this single card into an editable form so
    // the agent doesn't have to scroll to the top of the modal to
    // change anything. The legacy editingTemplateId path (top-of-
    // modal form populates with the row's values) was confusing
    // because the form was hidden above the fold.
    state.inlineEditingTplId = t.id;
    renderTemplatesList();
    // Focus the inline name input on the next tick so it's ready
    // for typing.
    setTimeout(() => {
      const nameEl = document.querySelector('.tpl-item-editing .tpl-edit-name');
      if (nameEl) { nameEl.focus(); nameEl.select(); }
    }, 30);
  }
  async function archiveTemplate(t) {
    if (!confirm('Archive template "' + t.name + '"?\n\nIt will disappear from every picker and can be restored from Templates → Archived.')) return;
    try {
      const r = await api('/api/templates/' + encodeURIComponent(t.id) + '/archive', { method: 'POST' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast('Archive failed: ' + (j.error || ('HTTP ' + r.status)), 'error');
        return;
      }
      state.selectedTemplateIds.delete(t.id);
      await loadTemplates();
      toast('Archived — restore it from the Archived tab', 'success');
    } catch (err) {
      toast('Archive failed: ' + (err?.message || 'network error'), 'error');
    }
  }

  async function restoreTemplate(t, btn) {
    if (btn) btn.disabled = true;
    try {
      const r = await api('/api/templates/' + encodeURIComponent(t.id) + '/restore', { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast('Restore failed: ' + (j.error || ('HTTP ' + r.status)), 'error');
        return;
      }
      await loadTemplates();
      toast('Template restored', 'success');
    } catch (err) {
      toast('Restore failed: ' + (err?.message || 'network error'), 'error');
    } finally {
      if (btn?.isConnected) btn.disabled = false;
    }
  }

  // ── Meta template authoring (admin / super_sales) ──────────────────────────
  // Body-only ("Text + variables"): pick a snake_case name + language + category,
  // edit the body, give one example per {{var}}. On submit we convert the named
  // placeholders to Meta's positional {{1}},{{2}} form, build the BODY component
  // (+ example), POST /submit-meta, and store meta_params so the existing send
  // path can re-substitute the named variables at send time.
  // Known/special variables the SEND path auto-fills per message: {{name}} →
  // the contact's first name, {{agent}} → the sending agent's display name
  // (so one template works for every agent — Mary sends "…m3ak Mary", Bouchra
  // sends "…m3ak Bouchra"). The example values here are ONLY the sample Meta
  // shows during review; the real values are substituted at send time.
  const META_VAR_EXAMPLES = { name: 'Ahmed', agent: 'Maryam', date: '12/06', time: '15:00', city: 'Casablanca', program: 'Anglais' };
  function _insertMetaVar(v) {
    const ta = document.getElementById('metaTplBody');
    if (!ta) return;
    const ins = '{{' + v + '}}';
    const s = ta.selectionStart ?? ta.value.length;
    const e = ta.selectionEnd ?? ta.value.length;
    ta.value = ta.value.slice(0, s) + ins + ta.value.slice(e);
    ta.focus();
    ta.selectionStart = ta.selectionEnd = s + ins.length;
    _renderMetaTplVars();
  }
  function _slugifyMetaName(s) {
    return String(s || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9_]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 60);
  }
  function _extractTplVars(body) {
    const out = [], seen = new Set();
    for (const m of String(body || '').matchAll(/\{\{\s*(\w+)\s*\}\}/g)) {
      if (!seen.has(m[1])) { seen.add(m[1]); out.push(m[1]); }
    }
    return out;
  }
  function _renderMetaTplVars() {
    const host = document.getElementById('metaTplVars');
    if (!host) return;
    const vars = _extractTplVars(document.getElementById('metaTplBody')?.value || '');
    const prev = {};
    host.querySelectorAll('input[data-var]').forEach(i => { prev[i.getAttribute('data-var')] = i.value; });
    clear(host);
    if (!vars.length) return;
    host.appendChild(el('div', { class: 'meta-tpl-lbl', text: 'Example value for each variable (Meta requires it):' }));
    for (const v of vars) {
      const inp = el('input', { class: 'input', 'data-var': v, placeholder: 'example for ' + v, autocomplete: 'off' });
      if (prev[v]) inp.value = prev[v];
      else if (META_VAR_EXAMPLES[v]) inp.value = META_VAR_EXAMPLES[v];
      host.appendChild(el('div', { class: 'row', style: 'gap:8px;align-items:center;margin-top:4px;' }, [
        el('span', { class: 'meta-tpl-varname', text: '{{' + v + '}}' }), inp,
      ]));
    }
  }

  // ── Template variable catalog (property picker + live preview) ──────────────
  // The server owns the catalog (lib/templates/variables.js) and resolves
  // every {{var}} from real data at send time. The client just renders a
  // searchable inserter + a preview so the agent SEES the real value before
  // sending — killing the old "agent hand-typed the wrong date/zoom" problem.
  let _varCatalog = null;
  let _varCatalogPromise = null;
  async function loadVarCatalog() {
    if (_varCatalog) return _varCatalog;
    if (_varCatalogPromise) return _varCatalogPromise;
    _varCatalogPromise = (async () => {
      try {
        const r = await api('/api/templates/variables');
        const j = await r.json();
        _varCatalog = Array.isArray(j.variables) ? j.variables : [];
      } catch { _varCatalog = []; }
      // Seed Meta-submission examples for the new vars so _renderMetaTplVars
      // prefills a sensible sample for each.
      for (const v of _varCatalog) {
        if (v.example && !META_VAR_EXAMPLES[v.name]) META_VAR_EXAMPLES[v.name] = v.example;
      }
      return _varCatalog;
    })();
    return _varCatalogPromise;
  }

  function _insertVarAtCursor(ta, name) {
    if (!ta) return;
    const ins = '{{' + name + '}}';
    const s = ta.selectionStart ?? ta.value.length;
    const e = ta.selectionEnd ?? ta.value.length;
    ta.value = ta.value.slice(0, s) + ins + ta.value.slice(e);
    ta.focus();
    ta.selectionStart = ta.selectionEnd = s + ins.length;
    ta.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // Grouped <select> (type-to-search via native select) + quick chips for the
  // most-used vars. onInsert fires after each insert so the preview refreshes.
  function buildVarInserter(getTa, onInsert) {
    const wrap = el('div', { class: 'var-inserter' });
    const sel = el('select', { class: 'input var-inserter-sel', title: 'Insérer une variable' });
    const quickRow = el('div', { class: 'var-quick-chips' });
    const QUICK = ['name', 'agent', 'date', 'time', 'test_date', 'test_time', 'teacher', 'zoom_link'];
    function rebuild() {
      clear(sel);
      sel.appendChild(el('option', { value: '', text: '➕ Insérer une variable…' }));
      const groups = {};
      for (const v of (_varCatalog || [])) (groups[v.group] = groups[v.group] || []).push(v);
      for (const g of Object.keys(groups)) {
        const og = el('optgroup', { label: g });
        for (const v of groups[g]) og.appendChild(el('option', { value: v.name, text: `{{${v.name}}} — ${v.label}` }));
        sel.appendChild(og);
      }
      clear(quickRow);
      const known = new Set((_varCatalog || []).map(v => v.name));
      for (const n of QUICK) {
        if (_varCatalog && !known.has(n)) continue;
        const chip = el('button', { type: 'button', class: 'meta-tpl-chip', text: '{{' + n + '}}' });
        chip.addEventListener('click', () => { _insertVarAtCursor(getTa(), n); if (onInsert) onInsert(); });
        quickRow.appendChild(chip);
      }
    }
    sel.addEventListener('change', () => {
      const name = sel.value;
      sel.value = '';
      if (!name) return;
      _insertVarAtCursor(getTa(), name);
      if (onInsert) onInsert();
    });
    wrap.appendChild(sel);
    wrap.appendChild(quickRow);
    rebuild();
    loadVarCatalog().then(rebuild);
    return wrap;
  }

  // Live preview: shows each {{var}} in the body → its real value for the
  // currently-open chat (or the catalog example when no chat is open). Red
  // row when the chat has no data for that var (e.g. no booking yet).
  function buildVarPreview(getTa, getChatId) {
    const box = el('div', { class: 'var-preview' });
    let _t = null;
    box._refresh = async () => {
      await loadVarCatalog();
      const ta = getTa();
      const text = ta ? ta.value : '';
      const names = [...new Set((text.match(/\{\{([a-zA-Z0-9_]+)\}\}/g) || []).map(s => s.replace(/[{}]/g, '')))];
      clear(box);
      if (!names.length) return;
      const chatId = getChatId && getChatId();
      let values = {};
      if (chatId) {
        try {
          const r = await api('/api/templates/resolve?chatId=' + encodeURIComponent(chatId) + '&vars=' + encodeURIComponent(names.join(',')));
          const j = await r.json();
          values = j.values || {};
        } catch { /* preview is best-effort */ }
      }
      const exMap = {};
      for (const v of (_varCatalog || [])) exMap[v.name] = v.example;
      box.appendChild(el('div', { class: 'var-preview-title', text: chatId ? '👁 Aperçu (données réelles de ce contact)' : '👁 Aperçu (exemples)' }));
      for (const n of names) {
        const known = exMap.hasOwnProperty(n);
        const val = chatId ? (values[n] || '') : (exMap[n] || '');
        const empty = !val;
        const row = el('div', { class: 'var-preview-row' + (empty ? ' empty' : '') });
        row.appendChild(el('span', { class: 'var-preview-key', text: '{{' + n + '}}' }));
        row.appendChild(el('span', { class: 'var-preview-val', text: empty ? (chatId ? '⚠️ vide (aucune donnée)' : (known ? '—' : '⚠️ variable inconnue')) : val }));
        box.appendChild(row);
      }
    };
    box._refreshDebounced = () => { if (_t) clearTimeout(_t); _t = setTimeout(() => box._refresh(), 250); };
    return box;
  }

  function openMetaTemplateModal(t) {
    if (!_isSalesAdmin()) return;
    state._metaTplId = t.id;
    let modal = document.getElementById('metaTplModal');
    if (!modal) {
      modal = el('div', { id: 'metaTplModal', class: 'modal hidden' });
      const backdrop = el('div', { class: 'modal-backdrop' });
      backdrop.addEventListener('click', () => closeModal('metaTplModal'));
      const card = el('div', { class: 'modal-card', style: 'max-width:520px;' });
      const head = el('header', { class: 'modal-head' });
      head.appendChild(el('h2', null, [lucideIcon('upload-cloud'), document.createTextNode(' Create template in Meta')]));
      const closeX = el('button', { class: 'icon-btn' }, [lucideIcon('x')]);
      closeX.addEventListener('click', () => closeModal('metaTplModal'));
      head.appendChild(closeX);
      const body = el('div', { class: 'modal-body', style: 'display:flex;flex-direction:column;gap:10px;' });
      body.appendChild(el('label', { class: 'meta-tpl-lbl', text: 'Meta name (lowercase, a–z 0–9 _)' }));
      body.appendChild(el('input', { id: 'metaTplName', class: 'input', placeholder: 'e.g. new_followup_fr', autocomplete: 'off' }));
      const langSel = el('select', { id: 'metaTplLang', class: 'input' });
      for (const [v, lbl] of [['fr', 'Français (fr)'], ['ar', 'العربية (ar)'], ['en', 'English (en)'], ['en_US', 'English US (en_US)'], ['es', 'Español (es)']]) {
        langSel.appendChild(el('option', { value: v, text: lbl }));
      }
      const catSel = el('select', { id: 'metaTplCat', class: 'input' });
      for (const v of ['UTILITY', 'MARKETING', 'AUTHENTICATION']) catSel.appendChild(el('option', { value: v, text: v }));
      body.appendChild(el('div', { class: 'row', style: 'gap:8px;' }, [
        el('div', { style: 'flex:1;' }, [el('label', { class: 'meta-tpl-lbl', text: 'Language' }), langSel]),
        el('div', { style: 'flex:1;' }, [el('label', { class: 'meta-tpl-lbl', text: 'Category' }), catSel]),
      ]));
      body.appendChild(el('label', { class: 'meta-tpl-lbl', text: 'Body — insère une variable, elle se remplit avec les vraies données à l’envoi' }));
      const ta = el('textarea', { id: 'metaTplBody', class: 'input', rows: '5', style: 'resize:vertical;' });
      const metaPreview = buildVarPreview(() => document.getElementById('metaTplBody'), () => state.selectedChatId || null);
      ta.addEventListener('input', () => { _renderMetaTplVars(); metaPreview._refreshDebounced(); });
      body.appendChild(ta);
      // Searchable property picker (Client / Contact HubSpot / Test booking /
      // Système) — replaces the old 2-chip row. The server resolves every
      // inserted {{var}} from real data at send time.
      body.appendChild(buildVarInserter(() => document.getElementById('metaTplBody'), () => metaPreview._refresh()));
      body.appendChild(metaPreview);
      body.appendChild(el('div', { id: 'metaTplVars', class: 'meta-tpl-vars' }));
      body.appendChild(el('div', { id: 'metaTplResult', class: 'meta-tpl-result', style: 'font-size:12px;' }));
      const submitBtn = el('button', { id: 'metaTplSubmit', class: 'btn btn-primary' }, [lucideIcon('upload-cloud'), document.createTextNode(' Submit to Meta')]);
      submitBtn.addEventListener('click', submitMetaTemplate);
      const cancelBtn = el('button', { class: 'btn btn-ghost', text: 'Cancel' });
      cancelBtn.addEventListener('click', () => closeModal('metaTplModal'));
      body.appendChild(el('div', { class: 'row', style: 'gap:8px;justify-content:flex-end;margin-top:6px;' }, [cancelBtn, submitBtn]));
      card.appendChild(head);
      card.appendChild(body);
      modal.appendChild(backdrop);
      modal.appendChild(card);
      document.body.appendChild(modal);
    }
    document.getElementById('metaTplName').value = t.meta_name || _slugifyMetaName(t.name);
    document.getElementById('metaTplLang').value = t.meta_language || 'fr';
    document.getElementById('metaTplCat').value = t.meta_category || 'UTILITY';
    document.getElementById('metaTplBody').value = t.content || '';
    const resEl = document.getElementById('metaTplResult');
    resEl.textContent = ''; resEl.className = 'meta-tpl-result';
    _renderMetaTplVars();
    document.querySelector('#metaTplModal .var-preview')?._refresh?.();
    openModal('metaTplModal');
    refreshIcons();
  }
  async function submitMetaTemplate() {
    const id = state._metaTplId;
    if (!id) return;
    const name = _slugifyMetaName(document.getElementById('metaTplName').value);
    const language = document.getElementById('metaTplLang').value;
    const category = document.getElementById('metaTplCat').value;
    const body = document.getElementById('metaTplBody').value.trim();
    const resEl = document.getElementById('metaTplResult');
    resEl.className = 'meta-tpl-result';
    const fail = (msg) => { resEl.textContent = msg; resEl.classList.add('err'); };
    if (!name) return fail('Name required (lowercase a–z 0–9 _).');
    if (!body) return fail('Body required.');
    const vars = _extractTplVars(body);
    const exMap = {};
    let missingEx = false;
    document.querySelectorAll('#metaTplVars input[data-var]').forEach(i => {
      exMap[i.getAttribute('data-var')] = i.value.trim();
      if (!i.value.trim()) missingEx = true;
    });
    if (missingEx) return fail('Provide an example for every variable.');
    // named {{name}} → positional {{1}}, stable order from `vars`.
    let positional = body;
    vars.forEach((v, i) => {
      positional = positional.replace(new RegExp('\\{\\{\\s*' + v + '\\s*\\}\\}', 'g'), '{{' + (i + 1) + '}}');
    });
    const components = vars.length
      ? [{ type: 'BODY', text: positional, example: { body_text: [vars.map(v => exMap[v])] } }]
      : [{ type: 'BODY', text: positional }];
    const meta = { name, language, category, components };
    const btn = document.getElementById('metaTplSubmit');
    if (btn) btn.disabled = true;
    resEl.textContent = 'Submitting to Meta…';
    try {
      const r = await api('/api/templates/' + encodeURIComponent(id) + '/submit-meta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meta, meta_params: vars }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { return fail('Meta rejected it: ' + (j.error || ('HTTP ' + r.status))); }
      resEl.textContent = '✓ Submitted — status: ' + (j.template?.meta_status || 'PENDING') + '. Use ⟳ to refresh in a few minutes.';
      resEl.classList.add('ok');
      await loadTemplates();
      setTimeout(() => closeModal('metaTplModal'), 1600);
    } catch (e) {
      fail('Network error — try again.');
    } finally {
      if (btn) btn.disabled = false;
    }
  }
  async function refreshMetaStatus(t) {
    if (!_isSalesAdmin()) return;
    try {
      const r = await api('/api/templates/' + encodeURIComponent(t.id) + '/meta-refresh', { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast('Status check failed: ' + (j.error || ('HTTP ' + r.status)), 'error'); return; }
      await loadTemplates();
      toast('Meta status: ' + (j.metaStatus || 'unknown'), j.metaStatus === 'APPROVED' ? 'success' : 'info');
    } catch (e) {
      toast('Status check failed — try again', 'error');
    }
  }

  async function saveTemplate() {
    const name = document.getElementById('tplName').value.trim();
    const content = document.getElementById('tplContent').value.trim();
    // Stage select replaces the old category select. Empty = Unsorted
    // (server falls back to meta_name derivation if any).
    const pipeline_stage = document.getElementById('tplStage')?.value || '';
    // Channel select: 'wa' (default, Meta pipeline) or 'sms' (Yeastar gateway).
    const channel = document.getElementById('tplChannel')?.value === 'sms' ? 'sms' : 'wa';
    if (!name || !content) { toast('Le nom et le contenu sont obligatoires', 'error'); return; }
    // Disable save button + pulse spinner during round-trip so the agent
    // doesn't fire a duplicate save / create.
    const saveBtn = document.getElementById('tplSaveBtn');
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.classList.add('sending');
    }
    try {
      const opts = {
        method: editingTemplateId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, content, pipeline_stage, channel }),
      };
      const url = '/api/templates' + (editingTemplateId ? '/' + editingTemplateId : '');
      const r = await api(url, opts);
      if (!r.ok) throw new Error();
      resetTemplateForm();
      loadTemplates();
      toast('Saved', 'success');
    } catch {
      toast('Échec de la sauvegarde — vérifiez votre connexion', 'error');
    } finally {
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.classList.remove('sending');
      }
    }
  }
  function resetTemplateForm() {
    editingTemplateId = null;
    document.getElementById('tplName').value = '';
    document.getElementById('tplContent').value = '';
    const channelSel = document.getElementById('tplChannel');
    if (channelSel) channelSel.value = 'wa';
    const stage = document.getElementById('tplStage');
    if (stage) {
      const active = state.templateActiveStage;
      stage.value = (active && active !== 'all' && active !== 'unsorted') ? active : '';
    }
  }

  // ============ Conversation Details panel (Notes + Activity + Take over) ============
  state.convDetailsTab = 'notes';

  function toggleConvDetailsPanel(focusTab) {
    if (!state.selectedChatId) { toast('Open a conversation first', 'error'); return; }
    const main = E.main();
    const isOpen = main.classList.contains('with-conv-details');
    if (isOpen && (!focusTab || focusTab === state.convDetailsTab)) {
      // Same tab requested → close
      main.classList.remove('with-conv-details');
      return;
    }
    // Open + ensure other right panel is closed
    main.classList.add('with-conv-details');
    main.classList.remove('with-profile');
    if (focusTab) switchConvDetailsTab(focusTab);
    else switchConvDetailsTab(state.convDetailsTab);
  }

  function switchConvDetailsTab(tab) {
    state.convDetailsTab = tab;
    document.querySelectorAll('.conv-details-tab').forEach(t => {
      t.classList.toggle('active', t.getAttribute('data-conv-tab') === tab);
    });
    document.getElementById('convNotesPane').classList.toggle('hidden', tab !== 'notes');
    document.getElementById('convActivityPane').classList.toggle('hidden', tab !== 'activity');
    if (tab === 'notes') loadNotes();
    if (tab === 'activity') loadChatActivity();
  }

  async function loadNotes() {
    const list = document.getElementById('convNotesList');
    if (!list) return;
    if (!state.selectedChatId) {
      clear(list);
      list.appendChild(el('div', { class: 'bulk-loading', text: 'Open a chat to see its notes' }));
      return;
    }
    clear(list);
    list.appendChild(el('div', { class: 'bulk-loading', text: 'Loading…' }));
    try {
      const r = await api('/api/notes?chatId=' + encodeURIComponent(state.selectedChatId));
      const j = await r.json();
      const notes = j.notes || [];
      clear(list);
      if (!notes.length) {
        list.appendChild(el('div', { class: 'bulk-loading', style: 'padding:24px;', text: 'No notes yet. Use the input below to add one.' }));
      } else {
        notes.slice().reverse().forEach(n => list.appendChild(buildNoteCard(n)));
      }
      updateNotesBadge(notes.length);
      refreshIcons();
    } catch {
      clear(list);
      list.appendChild(el('div', { class: 'bulk-loading', text: 'Failed to load notes' }));
    }
  }

  function buildNoteCard(n) {
    const author = n.author || {};
    const authorName = userLabel(author.username, author.displayName || author.username || 'Unknown');
    const initials = authorName.split(/\s+/).slice(0, 2).map(s => s.charAt(0)).join('') || '?';
    const colorClass = colorClassForName(author.username || authorName);

    const head = el('div', { class: 'conv-note-card-head' }, [
      el('div', { class: 'conv-note-avatar ' + colorClass, text: initials }),
      el('span', { class: 'conv-note-author', text: authorName }),
      el('span', { class: 'conv-note-time', text: n.createdAt ? formatNoteTime(n.createdAt) : '' })
    ]);
    // Cross-platform: notes mirrored from Support2 wear an origin chip and are
    // read-only here (delete lives on the platform that owns the note).
    if (n.platform === 'support2') {
      head.appendChild(el('span', {
        class: 'conv-note-platform',
        text: 'Support2',
        style: 'margin-left:auto;padding:1px 7px;border-radius:8px;font-size:10px;font-weight:700;background:#16a34a;color:#fff;letter-spacing:.4px;flex:none'
      }));
    }
    const contentEl = el('div', { class: 'conv-note-content', text: adminText(n.content || '') });
    const card = el('div', { class: 'conv-note-card' }, [head, contentEl]);
    const panelAudio = _noteAudioEl(n.audio);
    if (panelAudio) card.appendChild(panelAudio);
    if (_noteCanEdit(n)) {
      const editBtn = _noteEditBtnEl(n, contentEl, () => { loadNotes(); refreshNotesBadge(); });
      // The Support2 chip already claims the right edge; without it the pencil does.
      if (!n.platform) editBtn.style.marginLeft = 'auto';
      head.appendChild(editBtn);
    }
    const trail = _noteEditTrailEl(n);
    if (trail) card.appendChild(trail);
    // Support2 @mentions ride along read-only (who was tagged + whether they
    // checked it off over there).
    const noteMentions = Array.isArray(n.mentions) ? n.mentions : [];
    if (noteMentions.length) {
      const row = el('div', { class: 'conv-note-mentions', style: 'display:flex;flex-wrap:wrap;gap:4px;margin-top:6px' });
      for (const un of noteMentions) row.appendChild(_noteMentionChipEl(n, un));
      card.appendChild(row);
    }
    card.appendChild(_noteSeenBtnEl(n));
    // Show delete button only for own notes (or if admin) — never for
    // cross-platform rows (they live in the other platform's store).
    const canDelete = !n.platform && (!author.userId || author.userId === state.me?.id || state.me?.role === 'admin');
    if (canDelete) {
      const del = el('button', { class: 'conv-note-delete', 'aria-label': 'Delete' }, [lucideIcon('trash-2')]);
      del.addEventListener('click', e => { e.stopPropagation(); deleteNote(n.id); });
      card.appendChild(del);
    }
    return card;
  }

  function formatNoteTime(iso) {
    try {
      const d = new Date(iso);
      const now = new Date();
      const sameDay = MA.dayKey(d) === MA.dayKey(now);
      if (sameDay) return MA.loc(d, 't', [], { hour: '2-digit', minute: '2-digit' });
      return MA.loc(d, 'd', [], { month: 'short', day: 'numeric' }) + ' · ' + MA.loc(d, 't', [], { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  }

  async function addNote() {
    const input = document.getElementById('convNoteInput');
    const content = (input?.value || '').trim();
    if (!content || !state.selectedChatId) return;
    try {
      const r = await api('/api/notes', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: state.selectedChatId, content })
      });
      const j = await r.json();
      if (j.error) { toast(j.error, 'error'); return; }
      input.value = '';
      loadNotes();
      refreshNotesBadge();
    } catch {
      toast('Failed to add note', 'error');
    }
  }

  async function deleteNote(noteId) {
    if (!confirm('Delete this note?')) return;
    try {
      const r = await api('/api/notes/' + encodeURIComponent(noteId) + '?chatId=' + encodeURIComponent(state.selectedChatId), { method: 'DELETE' });
      const j = await r.json();
      if (j.error) { toast(j.error, 'error'); return; }
      loadNotes();
      refreshNotesBadge();
    } catch {
      toast('Échec de la suppression — réessayez', 'error');
    }
  }

  function updateNotesBadge(count) {
    // Lock icon top-bar badge
    const badge = document.getElementById('notesBadge');
    if (badge) {
      if (count > 0) { badge.textContent = String(count); badge.classList.remove('hidden'); }
      else badge.classList.add('hidden');
    }
    // Inside the conv-details tab pill
    const tabCount = document.getElementById('convNotesCount');
    if (tabCount) {
      if (count > 0) { tabCount.textContent = String(count); tabCount.classList.remove('hidden'); }
      else tabCount.classList.add('hidden');
    }
    // Lock icon orange highlight
    E.notesBtn?.classList.toggle('has-notes', count > 0);
  }

  async function loadChatActivity() {
    const list = document.getElementById('convActivityList');
    if (!list) return;
    if (!state.selectedChatId) {
      clear(list);
      list.appendChild(el('div', { class: 'bulk-loading', text: 'Open a chat to see its activity' }));
      return;
    }
    clear(list);
    list.appendChild(el('div', { class: 'bulk-loading', text: 'Loading activity…' }));
    try {
      const r = await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/activity');
      if (r.status === 401) return;
      const j = await r.json();
      const entries = j.entries || [];
      clear(list);
      if (!entries.length) {
        list.appendChild(el('div', { class: 'bulk-loading', style: 'padding:24px;', text: 'No team activity yet for this chat.' }));
        return;
      }
      for (const e of entries) list.appendChild(buildChatActivityRow(e));
      refreshIcons();
    } catch {
      clear(list);
      list.appendChild(el('div', { class: 'bulk-loading', text: 'Failed to load activity' }));
    }
  }

  function buildChatActivityRow(e) {
    const iconName = ACTIVITY_ICONS[e.action] || 'circle-dot';
    const body = el('div', { class: 'ca-body' }, [
      activityText(e),
      el('div', { class: 'ca-time', text: e.timestamp ? formatNoteTime(e.timestamp) : '' })
    ]);
    return el('div', { class: 'conv-activity-row' }, [
      el('span', { class: 'ca-icon' }, [lucideIcon(iconName)]),
      body
    ]);
  }

  async function takeOverChat() {
    if (!state.selectedChatId) { toast('Open a chat first', 'error'); return; }
    if (!state.me?.id) return;
    const requestedChatId = state.selectedChatId;
    const current = currentAssignment();
    if (current?.userId === state.me.id) {
      toast('You are already handling this chat', '');
      return;
    }
    try {
      const r = await api('/api/inbox/assign', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: requestedChatId, userId: state.me.id })
      });
      const j = await r.json();
      if (j.error) { toast(j.error, 'error'); return; }
      _cacheOwnershipAssignment(j.chatId || requestedChatId, j.assignment || null);
      updateAssignBtnState();
      renderConversations();
      loadChatActivity();
      toast('Taken over by you', 'success');
    } catch (err) {
      toast('Take over failed: ' + (err.message || err), 'error');
    }
  }

  // ============ Documents v2 (Lougha 7-type system) ============
  state.docState = { step: 'picker', type: null, form: {}, blob: null, fileName: '' };

  function openDocumentsModal() {
    if (!state.selectedChatId) { toast('Open a conversation first', 'error'); return; }
    if (!window.LoughaDocs) { toast('Documents module not loaded — refresh the page.', 'error'); return; }
    state.docState = { step: 'picker', type: null, form: {}, blob: null, fileName: '' };
    const chat = state.selectedChat;
    document.getElementById('docContactAvatar').textContent = (chat?.name || chat?.phone || '?').charAt(0);
    document.getElementById('docContactName').textContent = chat?.name || chat?.phone || '—';
    document.getElementById('docContactPhone').textContent = chat?.phone || '';
    showDocStep('picker');
    renderDocPicker();
    openModal('documentsModal');
  }

  function showDocStep(step) {
    state.docState.step = step;
    document.getElementById('docPickerStep').classList.toggle('hidden', step !== 'picker');
    document.getElementById('docFormStep').classList.toggle('hidden', step !== 'form');
    document.getElementById('docFormFoot').classList.toggle('hidden', step !== 'form');
    document.getElementById('docPreviewStep').classList.toggle('hidden', step !== 'preview');
    document.getElementById('docPreviewFoot').classList.toggle('hidden', step !== 'preview');
    document.getElementById('docBackBtn').classList.toggle('hidden', step === 'picker');
    const titleEl = document.getElementById('docModalTitleText');
    if (step === 'picker') titleEl.textContent = 'Documents';
    else if (state.docState.type) titleEl.textContent = state.docState.type.label;
  }

  function renderDocPicker() {
    const list = document.getElementById('docPickerList');
    clear(list);
    for (const type of window.LoughaDocs.typeList) {
      const card = el('div', { class: 'doc-picker-card accent-' + type.accent }, [
        el('div', { class: 'dpc-icon', text: type.icon || '📄' }),
        el('div', null, [
          el('div', { class: 'dpc-label', text: type.label }),
          el('div', { class: 'dpc-desc', text: type.description })
        ]),
        el('span', { class: 'dpc-arrow' }, [lucideIcon('arrow-right')])
      ]);
      card.addEventListener('click', () => pickDocType(type.key));
      list.appendChild(card);
    }
    refreshIcons();
  }

  async function pickDocType(typeKey) {
    const type = window.LoughaDocs.types[typeKey];
    if (!type) return;
    state.docState.type = type;
    state.docState._afficheKey = null;
    state.docState._invoicePrefillSource = null;
    state.docState.form = await preFillDocForm(type);
    showDocStep('form');
    renderDocForm();
  }

  // Layered pre-fill: defaults → custom_properties.json → HubSpot contact → chat name
  // Compute the WhatsApp caption from a doc type's messageTemplate using the
  // current form values. {salutation} resolves to "Cher Monsieur" / "Chère
  // Madame" based on form.gender; otherwise to "Cher Monsieur / Chère
  // Madame" (both, agent deletes the one they don't need). Time-neutral —
  // no Bonjour/Bonsoir.
  function _computeDocCaption(type, form, fallbackName) {
    const tpl = type.messageTemplate || 'Bonjour {name},\n\nVeuillez trouver ci-joint votre document.\n\nLougha Institute';
    const nameForTpl = form.recipient_name || form.client_name || form.student_name || fallbackName || 'cher(e) étudiant(e)';
    const courseForTpl = form.course || form.program || 'votre formation';
    const genderRaw = String(form.gender || '').toLowerCase().trim();
    const salutationForTpl = (genderRaw === 'he' || genderRaw === 'm' || genderRaw === 'mr' || genderRaw === 'monsieur' || genderRaw === 'male')
      ? 'Cher Monsieur'
      : (genderRaw === 'she' || genderRaw === 'f' || genderRaw === 'mrs' || genderRaw === 'mme' || genderRaw === 'madame' || genderRaw === 'female')
        ? 'Chère Madame'
        : 'Cher Monsieur / Chère Madame';
    let text = String(tpl)
      .replace(/\{salutation\}/g, salutationForTpl)
      .replace(/\{name\}/g, nameForTpl)
      .replace(/\{course\}/g, courseForTpl);
    // The LLC takes dollar and euro payments from abroad: "virement bancaire ou en
    // espèces à l'institut" is the Moroccan school's way to pay, wrong for its clients.
    if (window.LoughaDocs?.docCompany?.(form.company).key === 'llc') {
      text = text.replace(/\s*Le règlement peut s['’]effectuer par virement bancaire ou en espèces à l['’]institut\./g, '');
    }
    return text;
  }

  // Live-regenerate the caption when the agent changes a field that
  // participates in the salutation/name/course substitution (gender,
  // recipient_name, client_name, student_name, course, program). If the
  // agent has hand-edited the caption since the last auto-fill, we leave
  // it alone — only refresh when the current caption still matches what
  // we last auto-generated.
  function _refreshDocCaption() {
    if (!state.docState || !state.docState.type) return;
    const form = state.docState.form;
    const newCaption = _computeDocCaption(state.docState.type, form, '');
    const currentCaption = String(form.caption || '');
    const lastAuto = state.docState._lastAutoCaption || '';
    // Only auto-replace if the agent hasn't edited the textarea since the
    // last auto-generation. Empty caption also triggers refresh.
    if (currentCaption && currentCaption !== lastAuto) return;
    form.caption = newCaption;
    state.docState._lastAutoCaption = newCaption;
    const captionTa = document.querySelector('[data-doc-field-key="caption"] textarea');
    if (captionTa) captionTa.value = newCaption;
  }

  // The fields that name ONE person. The chat-level memory below (custom props)
  // is per CHAT, and on a shared number a chat holds a whole family, so these
  // must come from the person the agent switched to — never from whoever the
  // last document on the chat was written for.
  const DOC_PERSON_NAME_KEYS = ['recipient_name', 'client_name', 'student_name'];

  async function preFillDocForm(type) {
    const form = {};
    const today = new Date().toISOString().slice(0, 10);
    const chat = state.selectedChat;
    // Shared number (Mariem, 2026-10-01: after Switch the other child "never
    // comes out in the proforma"; Ayoub, 2026-09-30: after Switch the
    // documents and the fiche prefill must be the switched person's own data).
    // personName is only trusted when the loaded profile is that very person.
    const personLeadId = _activePersonLeadId(state.selectedChatId);
    const profilePerson = state.activeProfile?.person;
    const personName = personLeadId && profilePerson && String(profilePerson.leadId || '') === String(personLeadId)
      ? String(profilePerson.name || '').trim() : '';

    // Layer 1: registry defaults + sensible empty shapes
    for (const f of type.fields) {
      if (f.defaultValue !== undefined) form[f.key] = f.defaultValue;
      if (f.type === 'date' && form[f.key] == null) form[f.key] = today;
      if (f.type === 'months' && form[f.key] == null) form[f.key] = new Array(12).fill(false);
      if (f.type === 'items' && form[f.key] == null) {
        form[f.key] = [{ description: '', quantity: 1, unit_price: 0 }];
      }
      if (f.type === 'receipt_lines' && form[f.key] == null) {
        form[f.key] = [{ description: '', periode: '', heures: '', reste: 0, montant: 0 }];
      }
    }

    // Layer 2: previously-saved values for this chat (custom_properties.json[general])
    try {
      const r = await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/custom-props');
      const j = await r.json();
      const docProps = (j.props || {}).general || [];
      for (const f of type.fields) {
        // Array/object-shaped fields (items / receipt_lines / months / offre)
        // are never persisted as custom props — any saved scalar for them is
        // legacy junk ("[object Object]") that would clobber the Layer-1
        // shape. receipt_no is per-document: restoring an old one would make
        // the next receipt reuse the previous LGH number.
        if (f.type === 'items' || f.type === 'receipt_lines' || f.type === 'months' || f.type === 'offre') continue;
        if (f.key === 'receipt_no') continue;
        // `date` is the day the document is issued: always today (Layer 1). Restoring it
        // printed the previous document's day on the next one (proforma of 29/10 dated
        // 28/10, Ayoub 2026-10-29). The Reçu's `date` is the PAYMENT day (its LGH number
        // uses it): a second language of the same receipt must keep it, so it stays.
        if (f.key === 'date' && type.pdfBuilder !== 'receipt') continue;
        if (personLeadId && DOC_PERSON_NAME_KEYS.includes(f.key)) continue;
        const saved = docProps.find(p => p.key === f.key);
        if (!saved || saved.value == null || saved.value === '') continue;
        if (f.type === 'number') form[f.key] = Number(saved.value) || 0;
        else if (f.type === 'toggle') form[f.key] = saved.value === 'true';
        else form[f.key] = saved.value;
      }
    } catch { /* non-fatal */ }

    // Layer 3: chat fallback (recipient/student/client name + phone). On a shared
    // number the chat's own name is the DEFAULT person's, so a switched person's
    // name waits for the profile / HubSpot (Layer 4); the phone is the line's.
    const fallbackName = personLeadId ? personName : (chat?.name || '');
    const fallbackPhone = chat?.phone || '';
    if (type.fields.some(f => f.key === 'recipient_name') && !form.recipient_name) form.recipient_name = fallbackName;
    if (type.fields.some(f => f.key === 'client_name') && !form.client_name) form.client_name = fallbackName;
    if (type.fields.some(f => f.key === 'student_name') && !form.student_name) form.student_name = fallbackName;
    if (type.fields.some(f => f.key === 'client_phone') && !form.client_phone) form.client_phone = fallbackPhone;

    // Layer 4: HubSpot contact properties — fill any field with a `studentColumn` that
    // matches a HubSpot key (firstname/lastname/email/phone/etc., plus extended set).
    // For document forms we also pull the FULL property set (419+ properties on
    // this account) so receipt-specific fields like `pack`, `our_bank`, `new_level`
    // — none of which are in the slim /api/contact projection — get prefilled
    // automatically.
    let hubspotContact = null;
    let hubspotProps = {};
    try {
      const profileChatId = _profilePersonChatId();
      // Shared number: prefill from the ACTIVE person's contact (the same
      // leadId the Contact Profile follows), never the number's default one.
      const personQs = _activePersonQs(state.selectedChatId);
      const r = await api('/api/contact?chatId=' + encodeURIComponent(profileChatId) + personQs);
      const j = await r.json();
      hubspotContact = j.contact || null;
      if (hubspotContact) {
        const props = { ...(hubspotContact.properties || {}) };
        // Merge full props on top so we have every HubSpot field, not just
        // the slim baseline.
        try {
          const fullR = await api('/api/hubspot/contact-properties-full?chatId=' + encodeURIComponent(profileChatId) + personQs);
          const fullJ = await fullR.json();
          if (Array.isArray(fullJ.properties)) {
            for (const p of fullJ.properties) if (p.value) props[p.name] = p.value;
          }
        } catch { /* fallback to slim props */ }
        hubspotProps = props;
        for (const f of type.fields) {
          if (form[f.key] !== undefined && form[f.key] !== '') continue;
          if (!f.studentColumn) continue;
          const v = props[f.studentColumn];
          if (v) form[f.key] = v;
        }
      }
    } catch { /* non-fatal */ }

    // A switched person neither the profile nor HubSpot could name still gets the
    // chat's name rather than a blank required field.
    if (personLeadId && !personName) {
      for (const k of DOC_PERSON_NAME_KEYS) {
        if (type.fields.some(f => f.key === k) && !form[k]) form[k] = chat?.name || '';
      }
    }

    // Layer 5: teacher-evaluated level (Ayoub, 2026-09-07). The Niveau on the
    // fiche is what the TEACHER entered on the placement test — it beats any
    // saved custom-prop or HubSpot value. Since 2026-09-10 it is an editable
    // prefill for the whole team; `editable: false` from the server (re-locked
    // policy) still empties + disables the field, as it did for sales before.
    state.docState._teacherLevel = null;
    if (type.fields.some(f => f.teacherLevel)) {
      let info = null;
      try {
        // Same identity the send uses (state.selectedChatId + the active
        // sibling lead), so prefill and the server gate resolve ONE lead.
        const activeLeadId = _activeLeadIdByChat[state.selectedChatId] || null;
        const q = activeLeadId
          ? 'leadId=' + encodeURIComponent(activeLeadId)
          : 'chatId=' + encodeURIComponent(state.selectedChatId || '');
        const r = await api('/api/teacher-level?' + q);
        if (r.ok) info = await r.json();
      } catch { /* non-fatal — falls back to the locked empty state */ }
      state.docState._teacherLevel = info;
      const locked = !(info?.editable ?? true);
      for (const f of type.fields) {
        if (!f.teacherLevel) continue;
        if (info?.level) form[f.key] = info.level;
        else if (locked) form[f.key] = '';
      }
    }

    // Facture: reuse the most recent Fiche d'inscription sent for this chat.
    // That preserves the exact pack/items/remise selected by the agent, while
    // filtering out "Offert" lines that must not be billed. Older fiches sent
    // before form snapshots existed fall back to HubSpot pack/duration/amount.
    if (type.key === 'facture' && window.LoughaDocs?.invoiceFormFromRegistration) {
      let registrationForm = null;
      try {
        // Shared number: the fiche sent for THIS person first (see the route).
        const latestR = await api('/api/documents/latest-registration?chatId=' + encodeURIComponent(state.selectedChatId)
          + _activePersonQs(state.selectedChatId));
        if (latestR.ok) {
          const latestJ = await latestR.json();
          if (latestJ && latestJ.form_data && typeof latestJ.form_data === 'object') {
            registrationForm = latestJ.form_data;
          }
        }
      } catch { /* fallback below */ }

      const invoiceSeed = window.LoughaDocs.invoiceFormFromRegistration(
        registrationForm || {},
        {
          amount: hubspotProps.amount,
          remise: form.remise,
          pack: hubspotProps.pack,
          pack_duration: hubspotProps.pack_duration,
          program: hubspotProps.program,
        },
      );
      if (Array.isArray(invoiceSeed.items) && invoiceSeed.items.length) {
        form.items = invoiceSeed.items.map(item => ({ ...item }));
        form.remise = Number(invoiceSeed.remise) || 0;
        form.remise_label = invoiceSeed.remise_label || 'Remise';
        form.tva_enabled = invoiceSeed.tva_enabled !== false;
        form.tva_rate = Number(invoiceSeed.tva_rate) || 20;
        state.docState._afficheKey = invoiceSeed.affiche_key || null;
        state.docState._invoicePrefillSource = registrationForm
          ? "Reprise automatique de la dernière Fiche d'inscription"
          : (invoiceSeed.suggested_preset
              ? `Pack suggéré depuis HubSpot : ${invoiceSeed.suggested_preset.label}`
              : 'Montant et programme repris automatiquement depuis HubSpot');
      }
    }

    // Proforma (Ayoub, 2026-10-29): a new proforma for this person starts from the last
    // proforma sent to them (lines, remise, TVA, issuer + currency, société, ICE, "Au nom
    // de la société"), so a re-issue only changes what the client asked for. With none,
    // the lines of their last Fiche d'inscription (the same seed the Facture uses), and
    // with neither the agent picks a programme in the gallery.
    if (type.key === 'proforma' && window.LoughaDocs?.proformaFormFromPrevious) {
      const latestDoc = async (docType) => {
        try {
          const r = await api('/api/documents/latest-registration?chatId=' + encodeURIComponent(state.selectedChatId)
            + '&docType=' + docType + _activePersonQs(state.selectedChatId));
          if (!r.ok) return null;
          const j = await r.json();
          return j && j.form_data && typeof j.form_data === 'object' ? j : null;
        } catch { return null; }
      };
      const previous = await latestDoc('proforma');
      const seed = previous ? window.LoughaDocs.proformaFormFromPrevious(previous.form_data) : null;
      if (seed) {
        form.items = seed.items.map(item => ({ ...item }));
        form.remise = seed.remise;
        form.remise_label = seed.remise_label;
        form.tva_enabled = seed.tva_enabled;
        form.tva_rate = seed.tva_rate;
        form.company = seed.company;
        form.currency = seed.currency;
        form.bill_to_company = seed.bill_to_company;
        if (seed.client_company) form.client_company = seed.client_company;
        if (seed.client_ice) form.client_ice = seed.client_ice;
        state.docState._afficheKey = seed.affiche_key || null;
        state.docState._invoicePrefillSource = 'Reprise automatique de la dernière proforma'
          + (previous.document_number ? ' (N° ' + previous.document_number + ')' : '');
      } else {
        const registration = await latestDoc('registration');
        const regSeed = registration && window.LoughaDocs.invoiceFormFromRegistration
          ? window.LoughaDocs.invoiceFormFromRegistration(registration.form_data, {})
          : null;
        if (regSeed && Array.isArray(regSeed.items) && regSeed.items.length) {
          // The Fiche's amounts are in the Fiche's currency (dirham when it predates
          // the stored currency, as the Facture prefill already assumes).
          if (registration.form_data.currency && window.LoughaDocs.docCurrency) {
            form.currency = window.LoughaDocs.docCurrency(registration.form_data.currency).key;
          }
          form.items = regSeed.items.map(item => ({ ...item }));
          form.remise = Number(regSeed.remise) || 0;
          form.remise_label = regSeed.remise_label || 'Remise';
          state.docState._afficheKey = regSeed.affiche_key || null;
          state.docState._invoicePrefillSource = "Reprise automatique de la dernière Fiche d'inscription";
        }
      }
    }

    // Layer 4b: HubSpot company → group_name / teacher_name auto-fill.
    // Attestation (and any other letter form that asks for the student's
    // class group + teacher) gets these prefilled from the matched HubSpot
    // company + the Google Sheet teacher index. Saves the agent from
    // typing them by hand on every attestation.
    const wantsGroupOrTeacher = type.fields.some(f => f.key === 'group_name' || f.key === 'teacher_name');
    if (wantsGroupOrTeacher && hubspotContact?.companies?.length) {
      const company = hubspotContact.companies[0];
      if (!form.group_name && company?.name) form.group_name = company.name;
      // Teacher lookup via the Sheet — only fires when the company name
      // contains an LC/LG class code we can extract.
      if (!form.teacher_name && company?.name) {
        const m = String(company.name).match(/(LC|LG)\s*[-_ ]?\s*(\d+)/i);
        if (m) {
          const classCode = m[1].toUpperCase() + ' ' + m[2];
          try {
            const r = await api('/api/documents/teacher-for-class?classCode=' + encodeURIComponent(classCode));
            const j = await r.json();
            // Prefer the HubSpot-matched display name (e.g. "Teacher Ibtissam Kasmi")
            // over the bare sheet field, since it's the form the agent expects.
            const t = j.teacherMatchedName || j.teacher;
            if (t) form.teacher_name = t;
          } catch { /* leave blank — agent fills in manually */ }
        }
      }
    }

    // Layer 5: body_paragraphs default for letter types
    if (type.letterKind && (!form.body_paragraphs || !String(form.body_paragraphs).trim())) {
      const contactCtx = { full_name: fallbackName, ...(hubspotContact?.properties || {}) };
      form.body_paragraphs = window.LoughaDocs.defaultLetterBody(type.letterKind, form, contactCtx);
    }

    // Receipt-only — reserve a sequential number from the server so the
    // agent sees the reference (LGH-YYYYMMDD-NNNN) before clicking
    // Generate. Random suffixes could collide when two agents printed
    // receipts inside the same minute, which silently overwrote the
    // earlier HubSpot last_receipt_number write. Skipped when the agent
    // already typed a number themselves.
    if (type.pdfBuilder === 'receipt' && !form.receipt_no) {
      try {
        const r = await api('/api/documents/receipt-number', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: form.date || today })
        });
        const j = await r.json();
        if (j.formatted) form.receipt_no = j.formatted;
      } catch { /* fall through to fallback below */ }
      if (!form.receipt_no) {
        const dateStr = (form.date || today).replace(/-/g, '');
        form.receipt_no = 'LGH-' + dateStr + '-' + Math.floor(1000 + Math.random() * 9000);
      }
    }

    // Layer 6: WhatsApp caption — pre-fill from per-type messageTemplate with
    // {salutation} / {name} / {course} substitutions. The agent can edit
    // before send. Caption auto-regenerates while the agent fills the form
    // (gender / name / course changes) UNLESS they've manually edited the
    // textarea — see _refreshDocCaption below.
    if (!form.caption || !String(form.caption).trim()) {
      form.caption = _computeDocCaption(type, form, fallbackName);
      state.docState._lastAutoCaption = form.caption;
    }

    return form;
  }

  function renderDocForm() {
    const container = document.getElementById('docFormFields');
    clear(container);
    const type = state.docState.type;
    if (!type) return;
    // Live drag-and-drop editor for the certificate. Renders ABOVE the
    // standard form fields. Drag-handlers write back to state.docState.form
    // so the numeric position fields below stay in sync.
    if (type.key === 'certificate_of_completion') {
      container.appendChild(_renderCertEditor());
    }
    // Who issues the document: Lougha Institute or LLT International LLC.
    if (_docHasCompanyChoice(type.key)) container.appendChild(_renderDocCompanyBar(type.key));
    // The same pack gallery also appears on Facture and Proforma (Ayoub, 2026-10-29:
    // "ykhtaro l-program o tban lihom l'affiche"). On both it keeps only billable lines;
    // the Facture also turns TVA on, matching the official facture template.
    if (type.key === 'registration' || type.key === 'facture' || type.key === 'devis' || type.key === 'proforma') {
      if ((type.key === 'facture' || type.key === 'proforma') && state.docState._invoicePrefillSource) {
        container.appendChild(el('div', { class: 'doc-invoice-prefill' }, [
          lucideIcon('sparkles'),
          el('span', { text: state.docState._invoicePrefillSource }),
        ]));
      }
      container.appendChild(_renderAffichePresets());
    }
    for (const f of type.fields) {
      if (f.hidden) continue;
      container.appendChild(buildDocFieldNode(f));
    }
    refreshIcons();
  }

  // Affiche pack presets — a gallery of the institute's standard offer packs,
  // shown above the "Fiche d'inscription" form. Clicking a pack fills items /
  // niveau / l'offre / remise into the form below, then re-renders it.
  /** Currency the doc form is currently quoted in. */
  function _docCurrencyKey() {
    const D = window.LoughaDocs;
    return (D && D.docCurrency ? D.docCurrency(state.docState.form?.currency).key : 'mad');
  }

  /**
   * A pack's net price in the active currency. The presets are priced in MAD;
   * their old hardcoded "2500 DH" labels were correct but could only ever say
   * dirham, so the figure is derived from the items instead (verified equal to
   * every previous label) and then converted.
   */
  function _presetNetLabel(preset) {
    const D = window.LoughaDocs;
    if (!D || !D.presetNetAmount) return preset.netLabel || '';
    const mad = D.presetNetAmount(preset);
    const cur = _docCurrencyKey();
    const value = D.convertAmount(mad, 'mad', cur);
    const c = D.docCurrency(cur);
    // Whole prices read better without ",00" on a picker card.
    const n = Number.isInteger(value) ? value.toLocaleString('fr-FR') : value.toFixed(2).replace('.', ',');
    return c.key === 'mad' ? `${n} ${c.symbol}` : `${c.symbol} ${n}`;
  }

  function _renderCurrencyPicker() {
    const D = window.LoughaDocs;
    if (!D || !D.DOC_CURRENCIES) return null;
    const active = _docCurrencyKey();
    const row = el('div', { class: 'doc-affiche-currency' });
    row.appendChild(el('span', { class: 'dac-label', text: 'Devise' }));
    const group = el('div', { class: 'dac-group', role: 'group' });
    for (const c of Object.values(D.DOC_CURRENCIES)) {
      const btn = el('button', {
        type: 'button',
        class: 'dac-btn' + (c.key === active ? ' on' : ''),
        'data-currency': c.key,
        text: `${c.flag} ${c.symbol}`,
        title: c.label,
      });
      btn.addEventListener('click', () => _setDocCurrency(c.key));
      group.appendChild(btn);
    }
    row.appendChild(group);
    if (active !== 'mad') {
      const c = D.docCurrency(active);
      row.appendChild(el('span', {
        class: 'dac-rate',
        text: `1 ${c.symbol} = ${c.madPerUnit} DH`,
        title: active === 'usd'
          ? "Taux indicatif — les prix en dollar des liens de paiement varient (8,3 à 9,5 DH). Vérifie le montant avant l'envoi."
          : 'Taux officiel de l\'institut (identique aux prix des liens de paiement en euro).',
      }));
    }
    return row;
  }

  /** Switch the whole form to another currency, converting every amount. */
  function _setDocCurrency(key) {
    const D = window.LoughaDocs;
    if (!D || !D.convertFormCurrency) return;
    if (_docCurrencyKey() === D.docCurrency(key).key) return;
    D.convertFormCurrency(state.docState.form, key);
    renderDocForm();
    _refreshDocCaption();
  }

  // ── Issuing company (Ayoub, 2026-10-05) ─────────────────────────────────────
  // The Proforma, the Facture and the Devis can be issued by Lougha Institute or by
  // LLT International LLC, the company that takes dollar and euro payments. The
  // choice only changes the issuer's identity on the PDF (see DOC_COMPANIES in
  // documents-v2.js), the number sequence, and the TVA default. It is NOT a field of
  // the type, so it is never saved to the chat's custom props: every new document
  // starts on Lougha and the agent picks the LLC on purpose.
  const DOC_COMPANY_TYPES = ['proforma', 'facture', 'devis'];
  function _docHasCompanyChoice(typeKey) { return DOC_COMPANY_TYPES.includes(typeKey); }

  function _docCompanyKey() {
    const D = window.LoughaDocs;
    return (D && D.docCompany ? D.docCompany(state.docState.form?.company).key : 'lougha');
  }

  /** Counter a number is reserved from: the LLC has its own sequences (facture_llc …) so Lougha's stay unbroken. */
  function _docCounterType(base, form) {
    const D = window.LoughaDocs;
    return base + (D && D.docCompany ? D.docCompany(form && form.company).counterSuffix : '');
  }

  /** "LLC-" for the LLC, "" for Lougha: the placeholder reference when no number could be reserved. */
  function _docRefPrefix(form) {
    const D = window.LoughaDocs;
    return D && D.docCompany ? D.docCompany(form && form.company).refPrefix : '';
  }

  function _renderDocCompanyBar(typeKey) {
    const D = window.LoughaDocs;
    const wrap = el('div', { class: 'doc-company-wrap' });
    if (!D || !D.DOC_COMPANIES) return wrap;
    const active = _docCompanyKey();
    const row = el('div', { class: 'doc-affiche-currency doc-company-bar' });
    row.appendChild(el('span', { class: 'dac-label', text: 'Société' }));
    const group = el('div', { class: 'dac-group', role: 'radiogroup', 'aria-label': 'Société émettrice' });
    for (const c of Object.values(D.DOC_COMPANIES)) {
      const on = c.key === active;
      const btn = el('button', {
        type: 'button', role: 'radio', 'aria-checked': on ? 'true' : 'false',
        class: 'dac-btn' + (on ? ' on' : ''),
        'data-company': c.key, text: `${c.flag} ${c.label}`, title: c.note,
      });
      btn.addEventListener('click', () => _setDocCompany(c.key));
      group.appendChild(btn);
    }
    row.appendChild(group);
    wrap.appendChild(row);
    if (active === 'llc') {
      wrap.appendChild(el('div', {
        class: 'doc-field-help',
        text: 'Document au nom de LLT International LLC · numérotation LLC-… · TVA 20 % ajoutée (modifiable plus bas) · choisis EUR ou USD.',
      }));
    }
    // The currency picker sits in the pack gallery's header on every type that has the
    // Société choice (the Proforma got its gallery on 2026-10-29), so not here.
    return wrap;
  }

  /**
   * Switch the issuer. The LLC adds TVA on top of the amount, so it turns TVA on (20 %
   * unless a rate is already set); going back to Lougha restores the type's own default
   * (Facture on, Proforma and Devis off). A number already reserved belongs to the OTHER
   * company's sequence: it is dropped so it can never print under the new identity.
   */
  function _setDocCompany(key) {
    const D = window.LoughaDocs;
    const ds = state.docState;
    if (!D || !D.docCompany || !ds.type) return;
    const next = D.docCompany(key);
    if (_docCompanyKey() === next.key) return;
    const f = ds.form;
    f.company = next.key;
    if (next.tvaDefault) {
      f.tva_enabled = true;
      f.tva_rate = Number(f.tva_rate) || 20;
    } else {
      const field = ds.type.fields.find(x => x.key === 'tva_enabled');
      f.tva_enabled = !!(field && field.defaultValue);
    }
    ds._reservedNumber = null; ds._reservedFormatted = null; ds._reservedSubtype = null;
    renderDocForm();
    _refreshDocCaption();
  }

  function _renderAffichePresets() {
    const presets = (window.LoughaDocs && window.LoughaDocs.AFFICHE_PRESETS) || [];
    const wrap = el('div', { class: 'doc-affiche-presets' });
    const isInvoice = state.docState.type?.key === 'facture';
    const isDevis = state.docState.type?.key === 'devis';
    const isProforma = state.docState.type?.key === 'proforma';
    const head = el('div', { class: 'doc-affiche-head' }, [
      el('div', {
        class: 'doc-affiche-title',
        text: isInvoice
          ? 'Pack suggéré — ajuste automatiquement les lignes et le montant'
          : (isDevis
              ? 'Choisis un programme — le devis se remplit automatiquement'
              : (isProforma
                  ? 'Choisis un programme — la proforma se remplit automatiquement'
                  : 'Choisis un pack — remplit la fiche automatiquement')),
      }),
    ]);
    const picker = _renderCurrencyPicker();
    if (picker) head.appendChild(picker);
    wrap.appendChild(head);
    const grid = el('div', { class: 'doc-affiche-grid' });
    for (const p of presets) {
      const card = el('div', {
        class: 'doc-affiche-card' + (state.docState._afficheKey === p.key ? ' selected' : ''),
        'data-affiche-key': p.key, tabindex: '0', role: 'button',
      }, [
        el('div', { class: 'daf-top' }, [
          el('span', { class: 'daf-icon', text: p.icon || '📄' }),
          el('span', { class: 'daf-net', text: _presetNetLabel(p) }),
        ]),
        el('div', { class: 'daf-label', text: p.label }),
        el('div', { class: 'daf-sub', text: p.sublabel || '' }),
      ]);
      const apply = () => _applyAffichePreset(p);
      card.addEventListener('click', apply);
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); apply(); }
      });
      grid.appendChild(card);
    }
    wrap.appendChild(grid);
    return wrap;
  }

  function _applyAffichePreset(preset) {
    const f = state.docState.form;
    const isInvoice = state.docState.type?.key === 'facture';
    const isProforma = state.docState.type?.key === 'proforma';
    // Deep-copy items + offre so edits don't mutate the shared preset object.
    // Facture and Proforma print billable lines only (no "Offert" column).
    f.items = (isInvoice || isProforma) && window.LoughaDocs?.billableInvoiceItems
      ? window.LoughaDocs.billableInvoiceItems(preset.items)
      : (preset.items || []).map(it => ({ ...it }));
    f.offre = { validity: (preset.offre && preset.offre.validity) || '', points: ((preset.offre && preset.offre.points) || []).slice() };
    f.remise = preset.remise || 0;
    f.remise_label = preset.remise_label || 'Remise';
    // Presets are priced in MAD. If the agent is quoting in euro or dollar,
    // convert what was just loaded rather than dropping dirham figures into a
    // euro fiche.
    const _cur = _docCurrencyKey();
    if (_cur !== 'mad' && window.LoughaDocs?.convertFormCurrency) {
      f.currency = 'mad';
      window.LoughaDocs.convertFormCurrency(f, _cur);
    }
    if (isInvoice) {
      f.tva_enabled = true;
      f.tva_rate = Number(f.tva_rate) || 20;
      state.docState._invoicePrefillSource = `Pack sélectionné : ${preset.label}`;
    }
    // The Proforma keeps its own TVA choice (off on Lougha, on for the LLC).
    if (isProforma) state.docState._invoicePrefillSource = `Programme sélectionné : ${preset.label}`;
    // Devis = the same pack printed as an affiche: headline, sub-line and the
    // fact tiles come from the pack's client-facing copy (DEVIS_POSTER).
    if (state.docState.type?.key === 'devis') {
      const poster = (window.LoughaDocs?.DEVIS_POSTER || {})[preset.key] || null;
      f.affiche_key = preset.key;
      f.pack_title = poster ? poster.title : preset.label;
      f.pack_subtitle = poster ? poster.subtitle : (preset.sublabel || '');
      f.facts = poster ? poster.facts.map(pair => pair.slice()) : [];
    }
    // Niveau is NEVER seeded from a pack: it is the teacher's placement-test
    // level (preFillDocForm layer 5). The generic `preset.niveau` defaults used
    // to stamp "B1" on fiches of clients the teacher had rated A0.
    state.docState._afficheKey = preset.key;
    renderDocForm();
    _refreshDocCaption();
  }

  // ============ Cert drag-and-drop editor ============
  // Renders a 254×143-mm aspect preview of the certificate with three
  // built-in draggable overlays (name / date / level) and zero-or-more
  // user-added custom text overlays. Every drag updates state.docState.form
  // so the existing numeric fields stay in sync.
  function _renderCertEditor() {
    const wrap = el('div', { class: 'cert-editor' });
    const toolbar = el('div', { class: 'cert-editor-toolbar' });
    toolbar.appendChild(el('span', { class: 'cert-editor-hint', text: 'Drag any element to position it. Double-click to edit text.' }));
    const addBtn = el('button', { class: 'cert-editor-add-btn', type: 'button', text: '+ Add custom text' });
    addBtn.addEventListener('click', _addCustomTextOverlay);
    toolbar.appendChild(addBtn);
    wrap.appendChild(toolbar);
    const stage = el('div', { class: 'cert-editor-stage' });
    const img = el('img', { class: 'cert-editor-bg', src: '/templates/certificate-bg.png', alt: '' });
    stage.appendChild(img);
    // Four built-in overlays — keys match state.docState.form fields.
    // The pronoun overlay is a SURGICAL mask over the baked-in "She" word
    // in the bg PNG (not a full body wipe) — keeps the rest of the body
    // copy intact and matches the PDF builder's pronoun-only overlay.
    // fontSize is in pt to match the server's CSS; the editor renders at
    // cqw units (relative to stage width) so a 30pt font in the cert
    // (254 mm wide) renders proportionally inside the smaller editor stage.
    const PT_TO_CQW = 0.139; // 1pt ≈ 0.139% of 254 mm in CSS pixels
    const built = [
      { key: 'name',    xField: 'name_x',    yField: 'name_y',    defaultX: 49, defaultY: 41,  label: () => state.docState.form.recipient_name || 'Name', fontSize: 30, fontWeight: 700, color: '#111' },
      { key: 'level',   xField: 'level_x',   yField: 'level_y',   defaultX: 88, defaultY: 22,  label: () => state.docState.form.english_level || 'B2', fontSize: 18, fontWeight: 800, color: '#142463' },
      { key: 'date',    xField: 'date_x',    yField: 'date_y',    defaultX: 36, defaultY: 87,  label: () => _formatCertDate(state.docState.form.date), fontSize: 13, fontWeight: 700, color: '#111' },
      // Pronoun overlay — y=61 matches cert line 2 where "She" is baked.
      // 58 was too high (landed on line 1 "Has obtained…").
      { key: 'pronoun', xField: 'pronoun_x', yField: 'pronoun_y', defaultX: 32, defaultY: 61.7, label: () => { const g = String(state.docState.form.gender || 'she').toLowerCase(); return (g.startsWith('h') || g === 'male' || g === 'm') ? 'He' : 'She'; }, fontSize: 10, fontWeight: 400, color: '#3c3c3c', mask: true },
      // Hours overlay — y=64.5 targets baked "80" on body line 3, one
      // line below the pronoun. (Was 70.5 — that was below line 3 in
      // empty space.) Falls back to '80' when n_of_hours is empty so
      // the rendered cert always matches the baked PNG content.
      { key: 'hours',   xField: 'hours_x',   yField: 'hours_y',   defaultX: 36.7, defaultY: 64.5, label: () => { const h = state.docState.form.n_of_hours; return (h === undefined || h === null || String(h).trim() === '') ? '80' : String(h); }, fontSize: 10, fontWeight: 400, color: '#3c3c3c', mask: true },
    ];
    // Track the pronoun + hours nodes so external changes (e.g. swapping
    // the gender dropdown or editing n_of_hours in the form) live-refresh
    // the label without rebuilding the whole editor stage.
    let _pronounNode = null;
    let _pronounLabelFn = null;
    let _hoursNode = null;
    let _hoursLabelFn = null;
    for (const ov of built) {
      const node = el('div', { class: 'cert-editor-layer', 'data-cert-key': ov.key });
      node.style.fontSize = (ov.fontSize * PT_TO_CQW) + 'cqw';
      node.style.fontWeight = String(ov.fontWeight);
      node.style.color = ov.color;
      // mask: true ⇒ this overlay covers an existing baked-in piece of the
      // PNG (the pronoun word in the body). Give it a white background +
      // a touch of horizontal padding so it visually replaces the baked
      // copy without leaking the underlying letters.
      if (ov.mask) {
        node.style.background = '#fff';
        node.style.padding = '0 ' + (4 * PT_TO_CQW) + 'cqw';
      }
      node.textContent = ov.label();
      _setOverlayPosition(node, ov.xField, ov.yField, ov.defaultX, ov.defaultY);
      _attachOverlayDrag(node, stage, ov.xField, ov.yField);
      stage.appendChild(node);
      if (ov.key === 'pronoun') { _pronounNode = node; _pronounLabelFn = ov.label; }
      if (ov.key === 'hours') { _hoursNode = node; _hoursLabelFn = ov.label; }
    }
    // Live-refresh hook for the gender dropdown + n_of_hours so the
    // editor overlays update instantly when those form values change
    // (renderDocForm's input/select handlers call this after writing
    // state.docState.form).
    state._certEditorRefreshBody = () => {
      if (_pronounNode && _pronounLabelFn) _pronounNode.textContent = _pronounLabelFn();
      if (_hoursNode && _hoursLabelFn) {
        // The hours node has no child elements (no × delete button), so
        // setting textContent is safe and won't wipe a delete button.
        _hoursNode.textContent = _hoursLabelFn();
      }
    };
    // Re-render the custom-text overlays whenever they change.
    _renderCertExtraTexts(stage);
    wrap.appendChild(stage);
    state._certEditorStage = stage; // referenced when adding new custom texts
    return wrap;
  }

  function _formatCertDate(raw) {
    if (!raw) return '__/__/____';
    try {
      const d = new Date(raw);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return dd + '/' + mm + '/' + d.getFullYear();
    } catch { return '__/__/____'; }
  }

  function _setOverlayPosition(node, xField, yField, defaultX, defaultY) {
    const x = Number.isFinite(Number(state.docState.form[xField])) ? Number(state.docState.form[xField]) : defaultX;
    const y = Number.isFinite(Number(state.docState.form[yField])) ? Number(state.docState.form[yField]) : defaultY;
    // Initialise the form values from the default if they were undefined.
    state.docState.form[xField] = x;
    state.docState.form[yField] = y;
    node.style.left = x + '%';
    node.style.top = y + '%';
  }

  function _attachOverlayDrag(node, stage, xField, yField) {
    node.addEventListener('pointerdown', (ev) => {
      // Skip dragging when we're inline-editing the text content.
      if (node.isContentEditable) return;
      ev.preventDefault();
      node.setPointerCapture(ev.pointerId);
      node.classList.add('cert-editor-layer-dragging');
      const stageRect = stage.getBoundingClientRect();
      const onMove = (e) => {
        const xPct = ((e.clientX - stageRect.left) / stageRect.width) * 100;
        const yPct = ((e.clientY - stageRect.top) / stageRect.height) * 100;
        const xClamped = Math.max(0, Math.min(100, xPct));
        const yClamped = Math.max(0, Math.min(100, yPct));
        node.style.left = xClamped + '%';
        node.style.top = yClamped + '%';
        state.docState.form[xField] = Math.round(xClamped * 10) / 10;
        state.docState.form[yField] = Math.round(yClamped * 10) / 10;
        // Sync the numeric input fields below the editor so the agent sees
        // the live value if they prefer to fine-tune by typing.
        _syncDocFieldInput(xField);
        _syncDocFieldInput(yField);
      };
      const onUp = () => {
        node.classList.remove('cert-editor-layer-dragging');
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    });
    // Double-click → inline-edit text. Only for custom overlays; the three
    // built-ins are bound to recipient_name / english_level / date and
    // shouldn't be free-edited from here (use the form fields below for them).
    if (node.dataset.certKey === 'custom') {
      node.addEventListener('dblclick', () => {
        node.contentEditable = 'true';
        node.focus();
        // Select all the text for quick replace
        const range = document.createRange();
        range.selectNodeContents(node);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      });
      node.addEventListener('blur', () => {
        node.contentEditable = 'false';
        const idx = Number(node.dataset.customIdx);
        const list = state.docState.form.extra_texts || [];
        if (list[idx]) {
          list[idx].content = node.textContent.trim();
          state.docState.form.extra_texts = list;
        }
      });
    }
  }

  function _syncDocFieldInput(fieldKey) {
    const wrap = document.querySelector('[data-doc-field-key="' + fieldKey + '"]');
    if (!wrap) return;
    const input = wrap.querySelector('input');
    if (input) input.value = state.docState.form[fieldKey];
  }

  function _renderCertExtraTexts(stage) {
    // Wipe existing custom overlays from the stage.
    [...stage.querySelectorAll('.cert-editor-layer[data-cert-key="custom"]')].forEach(n => n.remove());
    const list = state.docState.form.extra_texts || [];
    list.forEach((entry, idx) => {
      const node = el('div', { class: 'cert-editor-layer', 'data-cert-key': 'custom', 'data-custom-idx': String(idx) });
      const PT_TO_CQW = 0.139;
      node.style.fontSize = ((entry.fontSize || 14) * PT_TO_CQW) + 'cqw';
      node.style.fontWeight = String(entry.fontWeight || 700);
      node.style.color = entry.color || '#111';
      node.style.left = (entry.x ?? 50) + '%';
      node.style.top = (entry.y ?? 50) + '%';
      node.textContent = entry.content || 'Custom text';
      _attachCustomDrag(node, stage, idx);
      const delBtn = el('button', { class: 'cert-editor-del-btn', type: 'button', title: 'Remove text', text: '×' });
      delBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const arr = state.docState.form.extra_texts || [];
        arr.splice(idx, 1);
        state.docState.form.extra_texts = arr;
        _renderCertExtraTexts(stage);
      });
      node.appendChild(delBtn);
      stage.appendChild(node);
    });
  }

  function _attachCustomDrag(node, stage, idx) {
    node.addEventListener('pointerdown', (ev) => {
      if (node.isContentEditable) return;
      if (ev.target.classList.contains('cert-editor-del-btn')) return;
      ev.preventDefault();
      node.setPointerCapture(ev.pointerId);
      node.classList.add('cert-editor-layer-dragging');
      const stageRect = stage.getBoundingClientRect();
      const onMove = (e) => {
        const xPct = ((e.clientX - stageRect.left) / stageRect.width) * 100;
        const yPct = ((e.clientY - stageRect.top) / stageRect.height) * 100;
        const xClamped = Math.max(0, Math.min(100, xPct));
        const yClamped = Math.max(0, Math.min(100, yPct));
        node.style.left = xClamped + '%';
        node.style.top = yClamped + '%';
        const arr = state.docState.form.extra_texts || [];
        if (arr[idx]) {
          arr[idx].x = Math.round(xClamped * 10) / 10;
          arr[idx].y = Math.round(yClamped * 10) / 10;
          state.docState.form.extra_texts = arr;
        }
      };
      const onUp = () => {
        node.classList.remove('cert-editor-layer-dragging');
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    });
    node.addEventListener('dblclick', () => {
      node.contentEditable = 'true';
      node.focus();
      const range = document.createRange();
      range.selectNodeContents(node);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    });
    node.addEventListener('blur', () => {
      node.contentEditable = 'false';
      const arr = state.docState.form.extra_texts || [];
      if (arr[idx]) {
        // Drop the trailing × button text by reading only direct text nodes.
        const text = [...node.childNodes]
          .filter(n => n.nodeType === Node.TEXT_NODE)
          .map(n => n.textContent)
          .join('')
          .trim();
        arr[idx].content = text || 'Custom text';
        state.docState.form.extra_texts = arr;
      }
    });
  }

  function _addCustomTextOverlay() {
    const arr = state.docState.form.extra_texts || [];
    arr.push({ x: 50, y: 60, content: 'Custom text', fontSize: 14, fontWeight: 700, color: '#111' });
    state.docState.form.extra_texts = arr;
    const stage = state._certEditorStage;
    if (stage) _renderCertExtraTexts(stage);
  }

  function buildDocFieldNode(f) {
    const wrap = el('div', { class: 'doc-field', 'data-doc-field-key': f.key });
    const label = el('div', { class: 'doc-field-label' }, [document.createTextNode(f.label)]);
    if (f.required) label.appendChild(el('span', { class: 'doc-required', text: ' *' }));
    wrap.appendChild(label);

    const value = state.docState.form[f.key];

    // Fields whose value feeds the auto-generated caption — changes here
    // trigger _refreshDocCaption so the WhatsApp preview stays in sync.
    const CAPTION_SOURCE_KEYS = new Set(['gender', 'recipient_name', 'client_name', 'student_name', 'course', 'program']);
    const isCaptionSource = CAPTION_SOURCE_KEYS.has(f.key);

    if (f.teacherLevel) {
      // Teacher-evaluated level: prefilled from the placement test and editable
      // by the whole team (preFillDocForm layer 5 fetched it). A re-locked
      // policy answers `editable: false` and the field goes read-only again.
      const info = state.docState._teacherLevel;
      const editable = !!(info?.editable ?? true);
      const input = el('input', { class: 'input', type: 'text', placeholder: f.placeholder || '' });
      input.value = value != null ? String(value) : '';
      const who = info?.teacher ? ' (' + info.teacher + (info.slotDate ? ', ' + info.slotDate : '') + ')' : '';
      let help;
      if (!editable) {
        input.disabled = true;
        input.setAttribute('aria-readonly', 'true');
        help = info?.level
          ? 'Niveau saisi par le professeur lors du test' + who + ' — non modifiable.'
          : 'Aucun test évalué pour ce client — la fiche sera envoyée sans niveau.';
      } else {
        help = info?.level
          ? 'Niveau du test : ' + info.level + who + ' — modifiable si besoin.'
          : 'Aucun test évalué — niveau à saisir.';
        input.addEventListener('input', () => { state.docState.form[f.key] = input.value; });
      }
      wrap.appendChild(input);
      wrap.appendChild(el('div', { class: 'doc-field-help', text: help }));
    } else if (f.type === 'text' || f.type === 'number' || f.type === 'date') {
      const input = el('input', {
        class: 'input',
        type: f.type === 'number' ? 'number' : (f.type === 'date' ? 'date' : 'text'),
        placeholder: f.placeholder || ''
      });
      input.value = value != null ? String(value) : '';
      input.addEventListener('input', () => {
        state.docState.form[f.key] = f.type === 'number' ? (Number(input.value) || 0) : input.value;
        if (isCaptionSource) _refreshDocCaption();
        // Live-refresh cert body text when its source fields change
        // (gender / hours / course / level / recipient name) so the
        // editor preview always matches what the PDF will produce.
        state._certEditorRefreshBody?.();
      });
      wrap.appendChild(input);
    } else if (f.type === 'textarea') {
      const ta = el('textarea', { class: 'input', rows: '8' });
      ta.value = value != null ? String(value) : '';
      ta.addEventListener('input', () => {
        state.docState.form[f.key] = ta.value;
        state._certEditorRefreshBody?.();
      });
      wrap.appendChild(ta);
    } else if (f.type === 'select') {
      const sel = el('select', { class: 'input' });
      for (const opt of f.options || []) {
        const o = el('option', { value: opt, text: opt });
        if (opt === value) o.selected = true;
        sel.appendChild(o);
      }
      sel.addEventListener('change', () => {
        state.docState.form[f.key] = sel.value;
        if (isCaptionSource) _refreshDocCaption();
        state._certEditorRefreshBody?.();
      });
      wrap.appendChild(sel);
    } else if (f.type === 'toggle') {
      const togglebox = el('label', { class: 'doc-toggle' });
      const cb = el('input', { type: 'checkbox' });
      cb.checked = !!value;
      cb.addEventListener('change', () => { state.docState.form[f.key] = cb.checked; });
      togglebox.appendChild(cb);
      togglebox.appendChild(el('span', { text: 'Activé' }));
      wrap.appendChild(togglebox);
    } else if (f.type === 'months') {
      const grid = el('div', { class: 'doc-months-grid' });
      const monthLabels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
      const arr = Array.isArray(value) ? value.slice() : new Array(12).fill(false);
      state.docState.form[f.key] = arr;
      for (let i = 0; i < 12; i++) {
        const pill = el('div', { class: 'doc-month-pill' + (arr[i] ? ' paid' : ''), text: monthLabels[i] });
        pill.addEventListener('click', () => {
          arr[i] = !arr[i];
          pill.classList.toggle('paid', arr[i]);
        });
        grid.appendChild(pill);
      }
      wrap.appendChild(grid);
    } else if (f.type === 'items') {
      wrap.appendChild(buildItemsField(f.key));
    } else if (f.type === 'receipt_lines') {
      wrap.appendChild(buildReceiptLinesField(f.key));
    } else if (f.type === 'offre') {
      wrap.appendChild(buildOffreField());
    }

    if (f.help) wrap.appendChild(el('div', { class: 'doc-field-help', text: f.help }));
    return wrap;
  }

  function buildItemsField(key) {
    const wrap = el('div', { class: 'doc-items-list' });
    const arr = state.docState.form[key] || [{ description: '', quantity: 1, unit_price: 0 }];
    state.docState.form[key] = arr;
    // The "Offert" (struck-through, free, excluded from Total HT) toggle is only
    // meaningful on the Fiche d'inscription — the invoice builder ignores it.
    // The Devis prints the same pack lines, so it gets the toggle too.
    const isReg = !!state.docState.type && ['registration', 'devis'].includes(state.docState.type.key);

    function rebuild() {
      clear(wrap);
      if (!isReg) {
        wrap.appendChild(el('div', { class: 'doc-item-row doc-invoice-item-header' }, [
          el('span', { text: 'Description' }),
          el('span', { text: 'Qté' }),
          el('span', { text: 'Prix unitaire' }),
          el('span', { text: '' }),
        ]));
      }
      arr.forEach((item, idx) => {
        const row = el('div', { class: 'doc-item-row' + (isReg ? ' doc-reg-row' : '') + (isReg && item.offert ? ' offert' : '') });
        const desc = el('input', { class: 'input', type: 'text', placeholder: 'Description' });
        desc.value = item.description || '';
        desc.addEventListener('input', () => { arr[idx].description = desc.value; });

        const qty = el('input', { class: 'input', type: 'number', step: '1', min: '0' });
        qty.value = String(item.quantity || 1);
        qty.addEventListener('input', () => { arr[idx].quantity = Number(qty.value) || 0; delete arr[idx].line_total; });

        const price = el('input', { class: 'input', type: 'number', step: '0.01', min: '0' });
        price.value = String(item.unit_price || 0);
        price.addEventListener('input', () => { arr[idx].unit_price = Number(price.value) || 0; delete arr[idx].line_total; });

        row.appendChild(desc); row.appendChild(qty); row.appendChild(price);

        if (isReg) {
          const offertBtn = el('button', {
            class: 'doc-item-offert' + (item.offert ? ' on' : ''), type: 'button',
            title: 'Offert — prix barré, exclu du Total HT', text: 'Offert',
          });
          offertBtn.addEventListener('click', () => { arr[idx].offert = !arr[idx].offert; rebuild(); });
          row.appendChild(offertBtn);
        }

        const del = el('button', { class: 'doc-item-del', 'aria-label': 'Remove' }, [lucideIcon('x')]);
        del.addEventListener('click', () => { arr.splice(idx, 1); rebuild(); });

        row.appendChild(del);
        wrap.appendChild(row);
      });
      const add = el('button', { class: 'doc-add-item' }, [
        lucideIcon('plus'),
        el('span', { text: 'Ajouter une ligne' })
      ]);
      add.addEventListener('click', () => { arr.push({ description: '', quantity: 1, unit_price: 0 }); rebuild(); });
      wrap.appendChild(add);
      refreshIcons();
    }

    rebuild();
    return wrap;
  }

  // Editor for the "L'offre" block on the Fiche d'inscription: an optional
  // validity line (leave empty to hide it, e.g. Business individuel) + a list
  // of bullet points. Writes state.docState.form.offre = { validity, points }.
  function buildOffreField() {
    const wrap = el('div', { class: 'doc-offre-editor' });
    const form = state.docState.form;
    if (!form.offre || typeof form.offre !== 'object') form.offre = { validity: '', points: [] };
    if (!Array.isArray(form.offre.points)) form.offre.points = [];
    const offre = form.offre;

    const valInput = el('input', { class: 'input doc-offre-validity', type: 'text', placeholder: 'Validité — ex: Cette offre valable 6 mois (vide = cachée)' });
    valInput.value = offre.validity || '';
    valInput.addEventListener('input', () => { offre.validity = valInput.value; });
    wrap.appendChild(valInput);

    const list = el('div', { class: 'doc-offre-points' });
    function rebuild() {
      clear(list);
      offre.points.forEach((pt, idx) => {
        const row = el('div', { class: 'doc-offre-point' });
        row.appendChild(el('span', { class: 'doc-offre-bullet', text: '●' }));
        const inp = el('input', { class: 'input', type: 'text', placeholder: "Point de l'offre" });
        inp.value = pt || '';
        inp.addEventListener('input', () => { offre.points[idx] = inp.value; });
        const del = el('button', { class: 'doc-item-del', type: 'button', 'aria-label': 'Remove' }, [lucideIcon('x')]);
        del.addEventListener('click', () => { offre.points.splice(idx, 1); rebuild(); });
        row.appendChild(inp); row.appendChild(del);
        list.appendChild(row);
      });
      const add = el('button', { class: 'doc-add-item', type: 'button' }, [
        lucideIcon('plus'),
        el('span', { text: 'Ajouter un point' }),
      ]);
      add.addEventListener('click', () => { offre.points.push(''); rebuild(); });
      list.appendChild(add);
      refreshIcons();
    }
    rebuild();
    wrap.appendChild(list);
    return wrap;
  }

  // Repeatable line-item editor for the Reçu form. 5 columns matching the
  // new branded design: Description / Période / Heures / Reste / Montant.
  function buildReceiptLinesField(key) {
    const wrap = el('div', { class: 'doc-items-list' });
    // Array.isArray guard heals chats whose saved custom prop poisoned the
    // prefill with a "[object Object]" string (pre-skipKeys-fix sends) —
    // without it, arr.forEach below throws and the form dies mid-render.
    const arr = Array.isArray(state.docState.form[key])
      ? state.docState.form[key]
      : [{ description: '', periode: '', heures: '', reste: 0, montant: 0 }];
    state.docState.form[key] = arr;
    function rebuild() {
      clear(wrap);
      const header = el('div', { class: 'doc-item-row doc-receipt-row doc-receipt-header' });
      header.appendChild(el('span', { text: 'Description' }));
      header.appendChild(el('span', { text: 'Période' }));
      header.appendChild(el('span', { text: 'Heures' }));
      header.appendChild(el('span', { text: 'Reste (MAD)' }));
      header.appendChild(el('span', { text: 'Montant (MAD)' }));
      header.appendChild(el('span', { text: '' }));
      wrap.appendChild(header);
      arr.forEach((item, idx) => {
        const row = el('div', { class: 'doc-item-row doc-receipt-row' });
        const desc = el('input', { class: 'input', type: 'text', placeholder: 'ex: English lik - A2' });
        desc.value = item.description || '';
        desc.addEventListener('input', () => { arr[idx].description = desc.value; });
        const periode = el('input', { class: 'input', type: 'text', placeholder: 'ex: mai 2026' });
        periode.value = item.periode || '';
        periode.addEventListener('input', () => { arr[idx].periode = periode.value; });
        const heures = el('input', { class: 'input', type: 'text', placeholder: '-' });
        heures.value = String(item.heures || '');
        heures.addEventListener('input', () => { arr[idx].heures = heures.value; });
        const reste = el('input', { class: 'input', type: 'number', step: '0.01', min: '0' });
        reste.value = String(item.reste || 0);
        reste.addEventListener('input', () => { arr[idx].reste = Number(reste.value) || 0; });
        const montant = el('input', { class: 'input', type: 'number', step: '0.01', min: '0' });
        montant.value = String(item.montant || 0);
        montant.addEventListener('input', () => { arr[idx].montant = Number(montant.value) || 0; });
        const del = el('button', { class: 'doc-item-del', 'aria-label': 'Remove' }, [lucideIcon('x')]);
        del.addEventListener('click', () => { arr.splice(idx, 1); rebuild(); });
        row.appendChild(desc); row.appendChild(periode); row.appendChild(heures); row.appendChild(reste); row.appendChild(montant); row.appendChild(del);
        wrap.appendChild(row);
      });
      const add = el('button', { class: 'doc-add-item' }, [
        lucideIcon('plus'),
        el('span', { text: 'Ajouter une ligne' })
      ]);
      add.addEventListener('click', () => { arr.push({ description: '', periode: '', heures: '', reste: 0, montant: 0 }); rebuild(); });
      wrap.appendChild(add);
      refreshIcons();
    }
    rebuild();
    return wrap;
  }

  // Validate form before generating PDF — returns array of error strings, empty = valid.
  function validateDocForm() {
    const ds = state.docState;
    if (!ds.type) return ['Aucun type sélectionné'];
    const errors = [];
    const errorFields = new Set();
    const f = ds.form;

    // 1. Required fields
    for (const fd of ds.type.fields) {
      if (!fd.required) continue;
      const v = f[fd.key];
      const empty = v == null || v === '' || (Array.isArray(v) && v.length === 0)
        || (fd.type === 'items' && (!v.length || !v.some(it => it.description && it.description.trim())))
        || (fd.type === 'receipt_lines' && (!v.length || !v.some(it => (it.description && it.description.trim()) || Number(it.montant) > 0)));
      if (empty) {
        errors.push(`Le champ « ${fd.label} » est obligatoire.`);
        errorFields.add(fd.key);
      }
    }

    // "Au nom de la société" needs the société it prints instead of the client's name.
    if ((f.bill_to_company === true || f.bill_to_company === 'true') && !String(f.client_company || '').trim()) {
      errors.push('« Au nom de la société » est coché : remplis le champ « Société ».');
      errorFields.add('client_company');
    }

    // 2. Type-specific rules
    if (ds.type.pdfBuilder === 'invoice' || ds.type.pdfBuilder === 'registration' || ds.type.pdfBuilder === 'devis') {
      // Devis totals follow the Fiche rules (offert lines free, line_total wins).
      const isReg = ds.type.pdfBuilder === 'registration' || ds.type.pdfBuilder === 'devis';
      const items = Array.isArray(f.items) ? f.items : [];
      const validItems = items.filter(it => (it.description || '').trim() && (it.quantity || 0) > 0 && (it.unit_price || 0) >= 0);
      if (!validItems.length) {
        errors.push('Ajoutez au moins une ligne d\'item avec description et quantité > 0.');
        errorFields.add('items');
      }
      // On the Fiche, offert lines are free (excluded from Total HT) and line_total
      // overrides qty*unit — match the PDF builder so the remise check is accurate.
      const totalBrut = validItems.reduce((s, it) => {
        if (isReg && it.offert) return s;
        const lt = (isReg && it.line_total != null) ? Number(it.line_total) : (it.quantity || 0) * (it.unit_price || 0);
        return s + lt;
      }, 0);
      const remise = Number(f.remise || 0);
      if (remise > totalBrut) {
        errors.push(`La remise (${remise} DHs) ne peut pas dépasser le total brut (${totalBrut.toFixed(2)} DHs).`);
        errorFields.add('remise');
      }
      const netHT = totalBrut - remise;
      const tvaAmount = f.tva_enabled ? netHT * (Number(f.tva_rate || 0) / 100) : 0;
      const totalTTC = netHT + tvaAmount;
      if (totalTTC <= 0) {
        errors.push(`Le Total TTC doit être positif (actuellement ${totalTTC.toFixed(2)} DHs).`);
      }
    }

    if (ds.type.pdfBuilder === 'receipt') {
      // Either the simple `amount` field OR the multi-line table must yield
      // a positive total — agents on simple receipts rarely fill lines.
      const lines = Array.isArray(f.lines) ? f.lines : [];
      const linesTotal = lines.reduce((acc, l) => acc + (Number(l.montant) || 0), 0);
      const simpleAmount = Number(f.amount) || 0;
      if (simpleAmount <= 0 && linesTotal <= 0) {
        errors.push('Indiquez un montant payé — soit dans « Montant payé », soit en ajoutant au moins une ligne.');
        errorFields.add('amount');
      }
    }

    if (ds.type.pdfBuilder === 'letter') {
      const body = String(f.body_paragraphs || '').trim();
      if (!body) {
        errors.push('Le corps du document ne peut pas être vide.');
        errorFields.add('body_paragraphs');
      } else {
        // Warn if too many placeholders (___________) remain
        const placeholderCount = (body.match(/_{5,}/g) || []).length;
        if (placeholderCount >= 3) {
          errors.push(`Le corps contient ${placeholderCount} champs vides (___________). Remplissez les champs ci-dessus ou modifiez le texte directement.`);
        }
      }
    }

    // Mark error fields visually
    document.querySelectorAll('.doc-field').forEach(node => node.classList.remove('has-error'));
    if (errorFields.size) {
      const allFields = document.querySelectorAll('.doc-field');
      ds.type.fields.forEach((fd, idx) => {
        if (errorFields.has(fd.key) && allFields[idx]) allFields[idx].classList.add('has-error');
      });
    }

    return errors;
  }

  function showDocFormErrors(errors) {
    const box = document.getElementById('docFormErrors');
    if (!box) return;
    if (!errors.length) { box.classList.add('hidden'); clear(box); return; }
    clear(box);
    box.classList.remove('hidden');
    box.appendChild(el('strong', { text: errors.length === 1 ? 'Une erreur :' : `${errors.length} erreurs à corriger :` }));
    const ul = el('ul');
    for (const msg of errors) ul.appendChild(el('li', { text: msg }));
    box.appendChild(ul);
    box.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function generateDocPdf() {
    const ds = state.docState;
    if (!ds.type) return null;
    try {
      const f = ds.form;
      let pdfDoc, fileName;
      if (ds.type.pdfBuilder === 'invoice') {
        // Reserve an auto-incrementing number from the server. Cache it on
        // the form so a Cancel + Re-generate within the same modal session
        // doesn't burn a second slot. Subsequent attempts (e.g. switching
        // doc type then switching back) DO mint a fresh number — they
        // represent a new document.
        // The LLC numbers from its own sequence (facture_llc / proforma_llc). A number
        // cached for ANOTHER sequence is dropped first, so a failed reservation prints
        // the placeholder instead of the other company's number.
        const counterType = _docCounterType(ds.type.invoiceSubtype, f);
        if (ds._reservedSubtype !== counterType) { ds._reservedNumber = null; ds._reservedFormatted = null; }
        if (!ds._reservedNumber || ds._reservedSubtype !== counterType) {
          try {
            const r = await api('/api/documents/next-number', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ type: counterType })
            });
            const j = await r.json();
            if (!j.error) {
              ds._reservedNumber = j.number;
              ds._reservedFormatted = j.formatted;
              ds._reservedSubtype = counterType;
            }
          } catch { /* fall back to placeholder */ }
        }
        const formatted = ds._reservedFormatted || _docRefPrefix(f) + '00000/' + new Date().getFullYear();
        pdfDoc = await window.LoughaDocs.buildInvoicePdf(f, ds.type.invoiceSubtype, ds._reservedNumber || null);
        fileName = window.LoughaDocs.buildInvoiceFileName(ds.type.invoiceSubtype, window.LoughaDocs.documentClientLabel(f), formatted);
      } else if (ds.type.pdfBuilder === 'registration') {
        // "Fiche" gets its own auto-incrementing number (separate counter from
        // proforma/facture). Same reserve-once-per-modal caching as invoices.
        if (!ds._reservedNumber || ds._reservedSubtype !== 'registration') {
          try {
            const r = await api('/api/documents/next-number', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ type: 'registration' })
            });
            const j = await r.json();
            if (!j.error) {
              ds._reservedNumber = j.number;
              ds._reservedFormatted = j.formatted;
              ds._reservedSubtype = 'registration';
            }
          } catch { /* fall back to placeholder */ }
        }
        const formatted = ds._reservedFormatted || '00000/' + new Date().getFullYear();
        pdfDoc = await window.LoughaDocs.buildRegistrationPdf(f, ds._reservedNumber || null);
        fileName = window.LoughaDocs.buildRegistrationFileName(f.client_name, formatted);
      } else if (ds.type.pdfBuilder === 'devis') {
        // Devis has its own "Devis N° NNNNN/YYYY" counter, reserved once per
        // modal session like the fiche.
        // Same rule as the invoices: the LLC's devis comes from devis_llc.
        const counterType = _docCounterType('devis', f);
        if (ds._reservedSubtype !== counterType) { ds._reservedNumber = null; ds._reservedFormatted = null; }
        if (!ds._reservedNumber || ds._reservedSubtype !== counterType) {
          try {
            const r = await api('/api/documents/next-number', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ type: counterType })
            });
            const j = await r.json();
            if (!j.error) {
              ds._reservedNumber = j.number;
              ds._reservedFormatted = j.formatted;
              ds._reservedSubtype = counterType;
            }
          } catch { /* fall back to placeholder */ }
        }
        const formatted = ds._reservedFormatted || _docRefPrefix(f) + '00000/' + new Date().getFullYear();
        pdfDoc = await window.LoughaDocs.buildDevisPdf({ ...f, affiche_key: f.affiche_key || ds._afficheKey || null }, ds._reservedNumber || null);
        fileName = window.LoughaDocs.buildDevisFileName(f.client_name, formatted);
      } else if (ds.type.pdfBuilder === 'receipt') {
        // Server-side render via api2pdf (same path the certificate uses):
        // the new branded design with rounded cards + RTL Arabic support
        // is too involved for jsPDF, but trivial as HTML+CSS.
        // Drop untouched editor rows (the default empty line counts) so a
        // lines array that is junk-only behaves like "no lines" — the
        // builders then synthesize the program/duration row instead of
        // printing a blank row and suppressing it. Local copy only: the
        // form state keeps its rows so the editor stays intact if the
        // agent goes back from the preview.
        const sendLines = (Array.isArray(f.lines) ? f.lines : []).filter(l =>
          String(l?.description || '').trim() || String(l?.periode || '').trim() ||
          (String(l?.heures || '').trim() && String(l.heures).trim() !== '-') ||
          Number(l?.montant) > 0 || Number(l?.reste) > 0);
        if (!f.receipt_no) {
          // Receipt numbers MUST come from the server-side counter
          // (`_counterChain` at server.js:7861) so two agents can't mint
          // the same LGH-YYYYMMDD-NNNN. The old client random fallback
          // voided that guarantee — collisions would overwrite the
          // wrong contact's `last_receipt_number` on HubSpot. If the
          // counter call fails the agent must retry; we don't ship.
          try {
            const rn = await api('/api/documents/receipt-number', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ date: f.date || new Date().toISOString().slice(0, 10) })
            });
            if (!rn.ok) throw new Error('HTTP ' + rn.status);
            const rj = await rn.json();
            if (!rj.formatted) throw new Error('no number returned');
            f.receipt_no = rj.formatted;
          } catch (err) {
            toast('Receipt numbering unavailable — try again in a moment (' + (err.message || 'error') + ')', 'error');
            return;
          }
        }
        const langMap = { 'Français': 'fr', 'English': 'en', 'العربية': 'ar' };
        const langCode = langMap[f.language] || 'fr';
        const safeName = String(f.student_name || 'client').replace(/\s+/g, '-').toLowerCase();
        fileName = (f.receipt_no || 'recu') + '-' + safeName + '-' + langCode + '.pdf';
        const r = await fetchT('/api/documents/generate-receipt', {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          // client_chat_id: lets the server recover the REAL digits for the
          // HubSpot receipt audit trail when the visible client_phone is
          // masked (non-sales-admin caller).
          body: JSON.stringify({ ...f, lines: sendLines, language: langCode, client_chat_id: state.selectedChatId || state.selectedChat?.id || null })
        }, 120000);
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          if (r.status === 503 && String(j.error || '').includes('API2PDF_API_KEY')) {
            // api2pdf key not configured on the server — fall back to the
            // local jsPDF builder so the receipt still goes out. The branded
            // HTML render takes over again once the key lands in the env.
            // Matching the error string keeps infra 503s (rolling deploy,
            // proxy) on the normal error path. jsPDF can't shape Arabic
            // script, so AR renders with FR labels — and the filename's
            // lang suffix follows what's actually inside the file.
            const effLang = langCode === 'ar' ? 'fr' : langCode;
            if (langCode === 'ar') toast('Version arabe momentanément indisponible — reçu généré en français.');
            const doc = await window.LoughaDocs.buildReceiptPdf({ ...f, lines: sendLines }, effLang);
            return { blob: doc.output('blob'), fileName: (f.receipt_no || 'recu') + '-' + safeName + '-' + effLang + '.pdf' };
          }
          throw new Error(j.error || 'Receipt generation failed');
        }
        const blob = await r.blob();
        return { blob, fileName };
      } else if (ds.type.pdfBuilder === 'letter') {
        pdfDoc = await window.LoughaDocs.buildLetterPdf(f, { docTitle: ds.type.label });
        fileName = window.LoughaDocs.buildLetterFileName(ds.type.letterKind, f);
      } else if (ds.type.pdfBuilder === 'certificate') {
        // Server-side render via api2pdf for crisp HTML+CSS placement (jsPDF overlay
        // had alignment / fuzziness issues on the decorative landscape template).
        const r = await fetchT('/api/documents/generate-certificate', {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...f, client_chat_id: state.selectedChatId || state.selectedChat?.id || null }),
        }, 120000);
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.error || `Server returned ${r.status}`);
        }
        const blob = await r.blob();
        fileName = window.LoughaDocs.buildCertificateFileName(f);
        return { blob, fileName };
      } else {
        toast('Unknown doc builder', 'error');
        return null;
      }
      return { blob: pdfDoc.output('blob'), fileName };
    } catch (err) {
      console.error('[Docs] PDF generation failed:', err);
      toast('PDF generation failed: ' + (err.message || err), 'error');
      return null;
    }
  }

  // Render PDF first page → JPEG blob — sent to server as `jpegThumbnail` so the
  // WhatsApp message bubble shows a big preview thumbnail instead of just an icon.
  async function generatePdfThumbnail(pdfBlob) {
    if (!window.pdfjsLib) return null;
    try {
      if (!window.pdfjsLib.GlobalWorkerOptions.workerSrc) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      }
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(1);
      // Render at scale that produces ~480px wide JPEG — enough for WhatsApp preview, small enough to embed.
      const targetWidth = 480;
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = targetWidth / baseViewport.width;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport }).promise;
      return await new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', 0.7));
    } catch (err) {
      console.warn('[Docs] thumbnail generation failed:', err.message || err);
      return null;
    }
  }

  async function previewDoc() {
    const ds = state.docState;
    // Validate first — block preview if invalid
    const errors = validateDocForm();
    showDocFormErrors(errors);
    if (errors.length) {
      toast(errors.length === 1 ? errors[0] : `${errors.length} erreurs — corrigez avant l'envoi`, 'error');
      return;
    }
    const r = await generateDocPdf();
    if (!r) return;
    ds.blob = r.blob;
    ds.fileName = r.fileName;
    ds.thumbnailBlob = null;
    document.getElementById('docPreviewFileName').textContent = r.fileName;
    const url = URL.createObjectURL(r.blob);
    document.getElementById('docPreviewFrame').src = url;
    showDocStep('preview');
    // Generate thumbnail in background — doesn't block preview
    generatePdfThumbnail(r.blob).then(thumb => { if (thumb) ds.thumbnailBlob = thumb; });
  }

  function downloadDocFromPreview() {
    const ds = state.docState;
    if (!ds.blob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(ds.blob);
    a.download = ds.fileName || 'document.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function sendDoc(useCachedBlob) {
    const ds = state.docState;
    // Guard against double-click. Without this, an impatient agent clicking
    // "Envoyer sur WhatsApp" twice would actually send the document twice
    // because each click awaits the api2pdf round-trip + WA send roundtrip.
    if (ds._sending) return;
    ds._sending = true;
    const sendBtn = document.getElementById('docPreviewSendBtn');
    const sendBtnOriginalText = sendBtn ? sendBtn.textContent : '';
    if (sendBtn) {
      sendBtn.disabled = true;
      sendBtn.style.opacity = '0.6';
      sendBtn.style.pointerEvents = 'none';
    }
    try {
      // Always validate — even cached blob path. Re-validation cheap.
      const errors = validateDocForm();
      showDocFormErrors(errors);
      if (errors.length) {
        toast(errors.length === 1 ? errors[0] : `${errors.length} erreurs — corrigez avant l'envoi`, 'error');
        // If sent from preview step, drop back to form so the user can fix
        if (ds.step === 'preview') showDocStep('form');
        return;
      }
      let blob = ds.blob;
      let fileName = ds.fileName;
      let thumbnail = ds.thumbnailBlob;
      if (!blob || !useCachedBlob) {
        const r = await generateDocPdf();
        if (!r) return;
        blob = r.blob; fileName = r.fileName;
        ds.blob = blob; ds.fileName = fileName;
        thumbnail = await generatePdfThumbnail(blob);
        ds.thumbnailBlob = thumbnail;
      }
      const fd = new FormData();
      // "Support2" picked → the fiche leaves on support2's number. Otherwise the
      // document keeps its historical target (the open chat), untouched.
      const docTargetChatId = _composeTargetChatId();
      const docViaSupport2 = _composeViaSupport2Line(docTargetChatId);
      fd.append('chatId', docViaSupport2 ? docTargetChatId : state.selectedChatId);
      if (docViaSupport2) fd.append('via', 'support2');
      fd.append('docType', ds.type.key);
      fd.append('fileName', fileName);
      fd.append('caption', String(ds.form.caption || '').trim());
      fd.append('file', blob, fileName);
      // Shared number: WHO this document is for (every doc type). The server
      // records it on the Documents index + sent_documents and moves THAT
      // lead to Proforma sent — never the line's oldest lead.
      {
        const personLeadId = _activeLeadIdByChat[state.selectedChatId] || '';
        if (personLeadId) fd.append('leadId', personLeadId);
      }
      if (['registration', 'facture', 'proforma', 'devis'].includes(ds.type.key)) {
        const formSnapshot = {
          ...ds.form,
          caption: undefined,
          affiche_key: ds._afficheKey || null,
          // Active sibling on a shared number — the server's level gate
          // honours it only if it really belongs to this chat's phone.
          lead_id: _activeLeadIdByChat[state.selectedChatId] || null,
          // Always the currency the amounts are in (a dirham form often has none set),
          // so the next Proforma restored from this one prints the right money.
          currency: window.LoughaDocs?.docCurrency ? window.LoughaDocs.docCurrency(ds.form.currency).key : ds.form.currency,
        };
        fd.append('documentData', JSON.stringify(formSnapshot));
      }
      if (thumbnail) fd.append('thumbnail', thumbnail, 'thumbnail.jpg');
      // Pass the issued number along so the server can patch HubSpot's
      // last_{facture,proforma,receipt}_number on the matched contact.
      // For invoices/proformas the number was reserved during generation;
      // for receipts the receipt_no lives on the form. Pick by type — a
      // stale _reservedFormatted from a facture minted earlier in the same
      // modal session must never ride along on a receipt send.
      const issuedNumber = ds.type.pdfBuilder === 'receipt'
        ? (ds.form.receipt_no || '')
        : (ds._reservedFormatted || '');
      if (issuedNumber) fd.append('documentNumber', issuedNumber);
      try {
        const r = await fetchT('/api/documents/send', { method: 'POST', headers: authHeaders(), body: fd }, 120000);
        const j = await r.json();
        if (j.error) { toast(j.error, 'error'); return; }
        closeModal('documentsModal');
        toast('Document sent', 'success');
        loadMessages();
        saveDocFieldsBack().catch(() => {});
      } catch (err) {
        toast('Send failed: ' + (err.message || err), 'error');
      }
    } finally {
      ds._sending = false;
      if (sendBtn) {
        sendBtn.disabled = false;
        sendBtn.style.opacity = '';
        sendBtn.style.pointerEvents = '';
        if (sendBtnOriginalText) sendBtn.textContent = sendBtnOriginalText;
      }
    }
  }

  // Persist scalar fields back to custom_properties.json[general] so the next
  // doc on the same chat pre-fills with whatever the agent just typed.
  async function saveDocFieldsBack() {
    const ds = state.docState;
    if (!ds.type || !state.selectedChatId) return;
    // Don't persist caption — it changes per-recipient and is heavily templated.
    // `lines` (receipt_lines) and `offre` are arrays/objects: String(them)
    // persists "[object Object]" which would poison the next prefill.
    // `receipt_no` must stay per-document: persisting it would prefill the
    // NEXT receipt on the same chat with the OLD number, both mint guards
    // would skip ("Auto-généré si vide"), and two payments would share one
    // official LGH number.
    // `pack_title` / `pack_subtitle` describe ONE devis — persisting them would
    // print last week's program on the next devis when no pack is picked.
    const skipKeys = new Set(['items', 'paid_months', 'body_paragraphs', 'caption', 'lines', 'offre', 'receipt_no', 'pack_title', 'pack_subtitle']);
    // `bill_to_company` belongs to ONE proforma (restored from the last proforma, see
    // preFillDocForm): saved on the chat it would make every later document a company one.
    skipKeys.add('bill_to_company');
    // The issue date belongs to this document only (see preFillDocForm Layer 2); the
    // Reçu's payment date is still remembered.
    if (ds.type.pdfBuilder !== 'receipt') skipKeys.add('date');
    // On a shared number the memory is per CHAT: saving the switched person's
    // name would hand it to the next document of every other person on the line.
    if (_activePersonLeadId(state.selectedChatId)) for (const k of DOC_PERSON_NAME_KEYS) skipKeys.add(k);
    let existing = [];
    try {
      const r = await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/custom-props');
      const j = await r.json();
      existing = (j.props || {}).general || [];
    } catch { return; }
    for (const f of ds.type.fields) {
      if (skipKeys.has(f.key)) continue;
      const v = ds.form[f.key];
      if (v == null || v === '' || v === false) continue;
      const valueStr = String(v);
      // A masked number (what sales see) saved here would come back as the next prefill.
      if (f.key === 'client_phone' && valueStr.includes('*')) continue;
      const found = existing.find(p => p.key === f.key);
      try {
        if (found) {
          if (String(found.value) === valueStr) continue;
          await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/custom-props/' + encodeURIComponent(found.id), {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: valueStr })
          });
        } else {
          await api('/api/inbox/chats/' + encodeURIComponent(state.selectedChatId) + '/custom-props', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ section: 'general', key: f.key, value: valueStr })
          });
        }
      } catch { /* best-effort */ }
    }
  }

  // ============ Generic attachment send ============
  function toggleAttachMenu() {
    const menu = document.getElementById('attachMenu');
    if (!menu) return;
    menu.classList.toggle('hidden');
    if (!menu.classList.contains('hidden')) refreshIcons();
  }
  function closeAttachMenu() {
    const menu = document.getElementById('attachMenu');
    menu?.classList.add('hidden');
  }

  // ============ Image lightbox ============
  // Tap any image bubble (inbox 1-1, group, team chat DM, team chat
  // WA-group inline) → opens a fullscreen overlay with the original
  // resolution. Esc / backdrop / × closes. Replaces the older
  // target=_blank link that punted the agent to a raw browser tab.
  function openImageLightbox(url) {
    if (!url) return;
    const wrap = document.getElementById('imageLightbox');
    const img = document.getElementById('imageLightboxImg');
    const dl = document.getElementById('imageLightboxDownload');
    if (!wrap || !img) return;
    img.src = url;
    if (dl) dl.href = url;
    wrap.classList.remove('hidden');
    refreshIcons();
  }
  function closeImageLightbox() {
    const wrap = document.getElementById('imageLightbox');
    const img = document.getElementById('imageLightboxImg');
    if (!wrap) return;
    wrap.classList.add('hidden');
    if (img) img.src = ''; // free memory
  }

  // ============ Image annotation editor ============
  // Lightweight WhatsApp-style editor opened by clicking a thumbnail
  // in the multi-attach preview strip. Provides:
  //   - Pen (free-draw, choose between 6 colors)
  //   - Redact (filled rectangle, drag to paint over sensitive info)
  //   - Undo (revert last stroke)
  //   - Reset (clear all annotations, restore original)
  //   - Cancel (close without saving)
  //   - Done (composite to JPEG blob, hand back to caller)
  // The base image draws once at full source resolution; the visible
  // canvas is scaled via CSS, so output keeps the original quality.
  const _imgEditor = {
    canvas: null, ctx: null,
    baseImg: null,
    file: null,
    onDone: null,
    tool: 'pen',
    color: '#ef4444',
    drawing: false,
    rectStart: null,
    history: [],   // ImageData snapshots for undo
  };

  function openImageEditor(file, onDone) {
    if (!file || !file.type?.startsWith('image/')) {
      toast('Editor only supports images', 'error');
      return;
    }
    const modal = document.getElementById('imageEditorModal');
    if (!modal) return;
    _imgEditor.file = file;
    _imgEditor.onDone = onDone;
    _imgEditor.tool = 'pen';
    _imgEditor.color = '#ef4444';
    _imgEditor.history = [];
    _imgEditor.canvas = document.getElementById('imageEditorCanvas');
    _imgEditor.ctx = _imgEditor.canvas.getContext('2d');
    // Sync toolbar UI with reset state.
    document.querySelectorAll('#imageEditorModal .image-editor-tool').forEach(b => {
      b.classList.toggle('active', b.getAttribute('data-tool') === 'pen');
    });
    document.querySelectorAll('#imageEditorModal .image-editor-color').forEach(b => {
      b.classList.toggle('active', b.getAttribute('data-color') === '#ef4444');
    });
    // Load file → draw to canvas at original resolution.
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      // Cap absurdly huge images at 2400px on longest side so the
      // editor stays responsive — still way above WA's preview quality.
      const MAX = 2400;
      let w = img.naturalWidth, h = img.naturalHeight;
      if (w > MAX || h > MAX) {
        const r = Math.min(MAX / w, MAX / h);
        w = Math.round(w * r); h = Math.round(h * r);
      }
      _imgEditor.canvas.width = w;
      _imgEditor.canvas.height = h;
      _imgEditor.ctx.drawImage(img, 0, 0, w, h);
      _imgEditor.baseImg = _imgEditor.ctx.getImageData(0, 0, w, h);
      URL.revokeObjectURL(url);
    };
    img.src = url;
    modal.classList.remove('hidden');
    refreshIcons();
  }

  function closeImageEditor() {
    const modal = document.getElementById('imageEditorModal');
    modal?.classList.add('hidden');
    _imgEditor.file = null;
    _imgEditor.onDone = null;
    _imgEditor.history = [];
    _imgEditor.baseImg = null;
  }

  function imageEditorUndo() {
    if (!_imgEditor.history.length) return;
    const snap = _imgEditor.history.pop();
    _imgEditor.ctx.putImageData(snap, 0, 0);
  }

  function imageEditorReset() {
    if (!_imgEditor.baseImg) return;
    _imgEditor.history = [];
    _imgEditor.ctx.putImageData(_imgEditor.baseImg, 0, 0);
  }

  function imageEditorPushHistory() {
    if (!_imgEditor.canvas) return;
    // Cap history at 30 entries — generous for the kind of edits agents
    // do (a few strokes), keeps memory bounded if someone goes wild.
    if (_imgEditor.history.length >= 30) _imgEditor.history.shift();
    _imgEditor.history.push(_imgEditor.ctx.getImageData(0, 0, _imgEditor.canvas.width, _imgEditor.canvas.height));
  }

  function imageEditorPointerPos(ev) {
    const rect = _imgEditor.canvas.getBoundingClientRect();
    const sx = _imgEditor.canvas.width / rect.width;
    const sy = _imgEditor.canvas.height / rect.height;
    const x = (ev.clientX - rect.left) * sx;
    const y = (ev.clientY - rect.top) * sy;
    return { x, y };
  }

  function imageEditorStart(ev) {
    if (!_imgEditor.canvas) return;
    ev.preventDefault();
    imageEditorPushHistory();
    _imgEditor.drawing = true;
    const p = imageEditorPointerPos(ev);
    if (_imgEditor.tool === 'pen') {
      const ctx = _imgEditor.ctx;
      ctx.strokeStyle = _imgEditor.color;
      // Stroke width scales with image resolution so it looks the same
      // on a 600px-wide phone screenshot vs a 4000px DSLR shot.
      ctx.lineWidth = Math.max(4, Math.round(_imgEditor.canvas.width * 0.006));
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    } else if (_imgEditor.tool === 'block') {
      _imgEditor.rectStart = p;
      // Snapshot for the live preview while dragging — we rewrite the
      // rect every mousemove against this clean state.
      _imgEditor.rectBase = _imgEditor.ctx.getImageData(0, 0, _imgEditor.canvas.width, _imgEditor.canvas.height);
    }
  }

  function imageEditorMove(ev) {
    if (!_imgEditor.drawing) return;
    ev.preventDefault();
    const p = imageEditorPointerPos(ev);
    if (_imgEditor.tool === 'pen') {
      const ctx = _imgEditor.ctx;
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    } else if (_imgEditor.tool === 'block' && _imgEditor.rectStart) {
      const ctx = _imgEditor.ctx;
      ctx.putImageData(_imgEditor.rectBase, 0, 0);
      const x = Math.min(_imgEditor.rectStart.x, p.x);
      const y = Math.min(_imgEditor.rectStart.y, p.y);
      const w = Math.abs(p.x - _imgEditor.rectStart.x);
      const h = Math.abs(p.y - _imgEditor.rectStart.y);
      ctx.fillStyle = _imgEditor.color;
      ctx.fillRect(x, y, w, h);
    }
  }

  function imageEditorEnd(ev) {
    if (!_imgEditor.drawing) return;
    if (ev) ev.preventDefault();
    _imgEditor.drawing = false;
    _imgEditor.rectStart = null;
    _imgEditor.rectBase = null;
  }

  async function imageEditorDone() {
    if (!_imgEditor.canvas || !_imgEditor.onDone) { closeImageEditor(); return; }
    const cb = _imgEditor.onDone;
    const orig = _imgEditor.file;
    // Output as JPEG @ 0.92 quality — keeps file size sane while
    // preserving annotations cleanly. PNG would be lossless but 3-5×
    // larger which tightens WA's CDN upload window.
    _imgEditor.canvas.toBlob((blob) => {
      if (blob) {
        // Re-wrap as a File so the existing send-file pipeline (which
        // reads file.name + file.type) keeps working.
        const edited = new File([blob], (orig?.name || 'edit.jpg').replace(/\.[^.]+$/, '') + '-edit.jpg', { type: 'image/jpeg' });
        cb(edited);
      }
      closeImageEditor();
    }, 'image/jpeg', 0.92);
  }

  // ============ Multi-attach preview ============
  // state.pendingAttachments — File[] staged in the preview strip.
  // state.pendingVoiceBlob — voice recorded via the preview-strip mic
  //   (recordingMode='attach'). Sent after the file batch when present.
  // While the strip is open, the regular composer is hidden and the
  // strip's own send button drives the loop.
  state.pendingAttachments = [];
  state.pendingVoiceBlob = null;
  state.pendingVoiceDurationMs = 0;

  function attachStrip() { return document.getElementById('attachPreview'); }
  function isBatchingActive() { return state.pendingAttachments.length > 0 || !!state.pendingVoiceBlob; }

  // ============ Drop / paste to send ============
  // Ayoub 2026-07-30: drag a video / photo / PDF onto a conversation — or just
  // paste it — and it goes, with no file picker and no Send click. Works the
  // same on the client inbox, on a teammate DM and in a room.
  //
  // A mis-drop cannot be taken back (WhatsApp Cloud gives us no delete-for-all),
  // so the send is HELD for a few seconds behind a visible "Annuler": zero
  // clicks when the drop was right, one when it wasn't. The countdown ring is
  // the only thing between the drop and the client.
  const DROP_SEND_HOLD_MS = 4000;
  let _dropHold = null; // { timer, node, cancel }

  function _cancelDropHold() {
    if (!_dropHold) return;
    clearTimeout(_dropHold.timer);
    try { _dropHold.node.remove(); } catch { /* already gone */ }
    const onCancel = _dropHold.onCancel;
    _dropHold = null;
    if (onCancel) onCancel();
  }

  // Show the hold pill inside `host` and run `send(files)` when it expires.
  // A second drop landing during the countdown MERGES into the pending set and
  // restarts the clock — dropping three clips in a row must send three clips,
  // not silently replace the first two.
  function _holdThenSend(host, files, send, onCancel, key) {
    if (key && _dropHold && _dropHold.key === key) {
      files = _dropHold.files.concat(files);
      clearTimeout(_dropHold.timer);
      if (_dropHold.onTick) _dropHold.onTick();
      try { _dropHold.node.remove(); } catch { /* already gone */ }
      _dropHold = null; // merged, so the previous hold must NOT run its cancel
    } else {
      _cancelDropHold();
    }
    const names = files.map(f => f.name).filter(Boolean);
    const label = names.length === 1 ? names[0] : `${files.length} fichiers`;
    const pill = el('div', { class: 'dropsend-hold' });
    const count = el('span', { class: 'dropsend-count', text: String(Math.round(DROP_SEND_HOLD_MS / 1000)) });
    pill.appendChild(count);
    pill.appendChild(el('span', { class: 'dropsend-label', text: label }));
    const cancelBtn = el('button', { class: 'dropsend-cancel', type: 'button', text: 'Annuler' });
    cancelBtn.addEventListener('click', _cancelDropHold);
    pill.appendChild(cancelBtn);
    host.appendChild(pill);

    const startedAt = Date.now();
    const tick = setInterval(() => {
      const left = Math.ceil((DROP_SEND_HOLD_MS - (Date.now() - startedAt)) / 1000);
      count.textContent = String(Math.max(0, left));
    }, 250);
    const pending = files;
    const timer = setTimeout(() => {
      clearInterval(tick);
      try { pill.remove(); } catch { /* already gone */ }
      _dropHold = null;
      send(pending);
    }, DROP_SEND_HOLD_MS);
    _dropHold = {
      timer,
      node: pill,
      key,
      files: pending,
      onTick: () => clearInterval(tick),
      onCancel: () => { clearInterval(tick); if (onCancel) onCancel(); },
    };
  }

  // Only real files count — dragging text or a link must not arm anything.
  function _dragCarriesFiles(ev) {
    const t = ev.dataTransfer && ev.dataTransfer.types;
    return !!t && Array.prototype.indexOf.call(t, 'Files') !== -1;
  }

  // Wire a panel as a drop target. `onFiles(File[])` runs only when `isReady()`
  // says a conversation is actually open, so a drop on an empty pane is inert
  // instead of half-sending into nothing.
  function wireDropToSend(node, isReady, onFiles) {
    if (!node || node.dataset.dropWired) return;
    node.dataset.dropWired = '1';
    // dragenter/dragleave fire for every child element the cursor crosses;
    // count them so the overlay doesn't strobe on the way in.
    let depth = 0;
    const show = () => node.classList.add('drop-target-active');
    const hide = () => { depth = 0; node.classList.remove('drop-target-active'); };
    node.addEventListener('dragenter', (ev) => {
      if (!_dragCarriesFiles(ev) || !isReady()) return;
      ev.preventDefault();
      depth++;
      show();
    });
    node.addEventListener('dragover', (ev) => {
      if (!_dragCarriesFiles(ev) || !isReady()) return;
      ev.preventDefault();
      ev.dataTransfer.dropEffect = 'copy';
    });
    node.addEventListener('dragleave', (ev) => {
      if (!_dragCarriesFiles(ev)) return;
      depth = Math.max(0, depth - 1);
      if (!depth) hide();
    });
    node.addEventListener('drop', (ev) => {
      if (!_dragCarriesFiles(ev)) return;
      ev.preventDefault();
      hide();
      if (!isReady()) return;
      const files = Array.from(ev.dataTransfer.files || []);
      if (files.length) onFiles(files);
    });
  }

  // Same treatment for Cmd-V. Bound on the document (a paste has no drop
  // target) and routed to whichever surface is on screen; text pastes fall
  // through untouched so the composer keeps working normally.
  function wirePasteToSend(isReady, onFiles) {
    document.addEventListener('paste', (ev) => {
      if (!isReady()) return;
      const files = Array.from((ev.clipboardData && ev.clipboardData.files) || []);
      if (!files.length) return;
      ev.preventDefault();
      onFiles(files);
    });
  }

  // ── The two surfaces ──────────────────────────────────────────────────
  // A client conversation is open and on screen.
  function _inboxDropReady() {
    return state.activeView === 'inbox' && !!state.selectedChatId;
  }
  // A teammate DM or a room is open. Same composer, same endpoint.
  function _teamDropReady() {
    return state.activeView === 'teamchat' && !!state.teamchatActiveTarget;
  }

  // Inbox: reuse the existing preview strip so the agent SEES the thumbnails
  // during the countdown, then fire the normal batch send. Images skip the
  // annotator here — opening it would defeat the whole point (no clicks); the
  // 📎 path still annotates.
  function _dropSendInbox(files) {
    if (isBatchingActive()) {
      // A batch is already staged by hand — respect that flow: add to it and
      // let the agent press send themselves.
      for (const f of files) { f._previewKey = 'a-' + Math.random().toString(36).slice(2, 9); state.pendingAttachments.push(f); }
      renderAttachPreview();
      return;
    }
    for (const f of files) { f._previewKey = 'a-' + Math.random().toString(36).slice(2, 9); state.pendingAttachments.push(f); }
    renderAttachPreview();
    const host = document.getElementById('chatPane');
    _holdThenSend(host, files, () => { sendAttachBatch(); }, () => { cancelAttachBatch(); }, 'inbox');
  }

  // Team / rooms: no staging strip on this surface, so the hold pill IS the
  // preview. Files go one by one, in the order they were dropped.
  function _dropSendTeam(files) {
    const host = document.getElementById('teamchatActiveChat');
    if (!host) return;
    _holdThenSend(host, files, async (pending) => {
      for (const f of pending) await sendTeamchatFile(f);
    }, null, 'team');
  }

  function initDropToSend() {
    wireDropToSend(document.getElementById('chatPane'), _inboxDropReady, _dropSendInbox);
    wireDropToSend(document.getElementById('teamchatActiveChat'), _teamDropReady, _dropSendTeam);
    wirePasteToSend(
      () => _inboxDropReady() || _teamDropReady(),
      (files) => (_teamDropReady() ? _dropSendTeam(files) : _dropSendInbox(files)),
    );
  }

  async function sendAttachment(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    e.target.removeAttribute('accept');
    if (!files.length || !state.selectedChatId) return;
    // Stage every file (including documents) into the preview strip so
    // the agent always sees what's about to ship and can confirm /
    // remove individual items / add a caption / queue a voice note
    // alongside before pressing send. PDFs render as a document tile
    // (icon + filename) instead of an image thumb. The actual send
    // routes by MIME inside sendAttachBatch — PDFs through
    // /api/documents/send, everything else through /api/inbox/send-file.
    //
    // Images go through the shared image annotator first (same one
    // used by Team chat + Team Groups), so the agent can pen / arrow /
    // text / box / redact before the file lands in the preview strip.
    // Cancel inside the annotator → that image is skipped; other
    // attachments in the same pick still flow through normally.
    for (const f of files) {
      let processed = f;
      if ((f.type || '').startsWith('image/')) {
        const edited = await openTeamchatImageAnnotator(f);
        if (!edited) continue;
        processed = edited;
      }
      // Tag each file with a unique key so per-thumb remove works
      // even after the underlying File reference loses identity.
      processed._previewKey = 'a-' + Math.random().toString(36).slice(2, 9);
      state.pendingAttachments.push(processed);
    }
    renderAttachPreview();
  }

  function renderAttachPreview() {
    const wrap = attachStrip();
    if (!wrap) return;
    if (!state.pendingAttachments.length && !state.pendingVoiceBlob) {
      wrap.classList.add('hidden');
      // Restore composer when batch fully cleared.
      document.querySelector('.compose')?.classList.remove('batching');
      return;
    }
    wrap.classList.remove('hidden');
    // Hide the regular composer's controls while preview is active. The
    // .batching class hides everything inside .compose EXCEPT
    // #recordOverlay so the batch's mic-record flow can still mount.
    document.querySelector('.compose')?.classList.add('batching');

    const strip = document.getElementById('attachPreviewStrip');
    if (strip) {
      clear(strip);
      // PDFs get a higher ceiling because the server runs Ghostscript
      // compression to bring them under WhatsApp's 100 MB cap. Other
      // files (images / videos / audio / non-PDF docs) stay at 100 MB
      // because there's no server-side compression for them.
      const MAX_BYTES_OTHER = 100 * 1024 * 1024;
      const MAX_BYTES_PDF   = 2 * 1024 * 1024 * 1024;
      const COMPRESS_THRESHOLD = 95 * 1024 * 1024;
      // Matches sendAttachBatch's CHUNK_THRESHOLD — past this a video is
      // uploaded in pieces and compressed server-side.
      const CHUNK_THRESHOLD_PREVIEW = 80 * 1024 * 1024;
      for (const f of state.pendingAttachments) {
        const isImage = (f.type || '').startsWith('image/');
        const isVideo = (f.type || '').startsWith('video/');
        const isAudio = (f.type || '').startsWith('audio/');
        const isPdf   = f.type === 'application/pdf';
        const isDoc = !isImage && !isVideo && !isAudio;
        // Video rides the same chunked lane as big PDFs now (the server
        // transcodes it down), so it is no longer capped at the single-POST
        // limit — flagging a 120 MB screen recording as "will be rejected"
        // was the whole complaint (Ayoub, 2026-07-30).
        const maxBytes = (isPdf || isVideo) ? MAX_BYTES_PDF : MAX_BYTES_OTHER;
        const oversize = f.size > maxBytes;
        // PDFs between 95 MB and 2 GB get a soft "will compress" hint
        // (yellow border / "compressing on send") instead of the red
        // oversize block. The server will run Ghostscript before send
        // so the recipient's download isn't 500 MB heavy. Big videos get the
        // same treatment — theirs is an ffmpeg pass instead of Ghostscript.
        const willCompress = !oversize && ((isPdf && f.size > COMPRESS_THRESHOLD) || (isVideo && f.size > CHUNK_THRESHOLD_PREVIEW));

        let thumb, url = null;
        const limitLabel = maxBytes >= 1024 * 1024 * 1024
          ? `${(maxBytes / 1024 / 1024 / 1024).toFixed(0)} GB`
          : `${maxBytes / 1024 / 1024} MB`;
        const titleFor = (name) =>
          oversize     ? name + ` — too large (>${limitLabel}), will be rejected`
          : willCompress ? name + (isVideo ? ' — big clip, server will compress before sending' : ' — over 95 MB, server will compress before sending')
          : name;
        if (isImage || isVideo) {
          // Image / video → use the file as a CSS background-image so
          // the thumbnail strip stays compact.
          url = URL.createObjectURL(f);
          const cls = 'attach-preview-thumb'
            + (isVideo ? ' video' : '')
            + (f._failed ? ' failed' : '')
            + (f._edited ? ' edited' : '')
            + (oversize ? ' oversize' : '');
          thumb = el('div', {
            class: cls,
            style: 'background-image:url(' + url + ')',
            title: titleFor(f.name),
            'data-key': String(f._previewKey || ''),
          });
        } else {
          // Document / audio / other — render a tile with an icon and
          // truncated filename + size. No native thumbnail to use.
          const cls = 'attach-preview-thumb attach-preview-doc'
            + (f._failed ? ' failed' : '')
            + (oversize ? ' oversize' : '')
            + (willCompress ? ' will-compress' : '');
          thumb = el('div', {
            class: cls,
            title: titleFor(f.name),
            'data-key': String(f._previewKey || ''),
          });
          const iconName = isAudio ? 'music' : 'file-text';
          const iconWrap = el('div', { class: 'attach-preview-doc-icon' });
          iconWrap.appendChild(el('i', { 'data-lucide': iconName }));
          thumb.appendChild(iconWrap);
          const meta = el('div', { class: 'attach-preview-doc-meta' });
          // Truncate long filenames so the tile stays the same size as
          // an image thumb. Full name is in the title tooltip.
          const shortName = f.name.length > 14 ? f.name.slice(0, 11) + '…' + f.name.slice(-4) : f.name;
          meta.appendChild(el('div', { class: 'attach-preview-doc-name', text: shortName }));
          const kb = Math.max(1, Math.round(f.size / 1024));
          const sizeText = kb >= 1024 ? (kb / 1024).toFixed(1) + ' MB' : kb + ' KB';
          const sizeSuffix = oversize ? ' · too large' : (willCompress ? ' · will compress' : '');
          meta.appendChild(el('div', {
            class: 'attach-preview-doc-size' + (oversize ? ' oversize' : (willCompress ? ' will-compress' : '')),
            text: sizeText + sizeSuffix
          }));
          thumb.appendChild(meta);
        }
        const rm = el('button', { type: 'button', class: 'attach-preview-thumb-remove', 'aria-label': 'Remove' });
        rm.textContent = '✕';
        rm.addEventListener('click', (ev) => {
          ev.stopPropagation();
          state.pendingAttachments = state.pendingAttachments.filter(x => x._previewKey !== f._previewKey);
          if (url) URL.revokeObjectURL(url);
          renderAttachPreview();
        });
        thumb.appendChild(rm);
        // Click on an image thumb body opens the annotation editor.
        // Videos / docs / audio skip the editor — they have nothing
        // to annotate visually.
        if (isImage) {
          thumb.addEventListener('click', (ev) => {
            if (ev.target.closest('.attach-preview-thumb-remove')) return;
            openImageEditor(f, (editedFile) => {
              editedFile._previewKey = f._previewKey;
              editedFile._edited = true;
              const idx = state.pendingAttachments.findIndex(x => x._previewKey === f._previewKey);
              if (idx >= 0) state.pendingAttachments[idx] = editedFile;
              if (url) URL.revokeObjectURL(url);
              renderAttachPreview();
            });
          });
        }
        strip.appendChild(thumb);
      }
      // Trailing (+) tile — reopens the picker with NO accept filter
      // so the agent can mix docs, images, audio, and video freely in
      // the same batch. Setting accept to image/video would gray out
      // PDFs in the OS picker, which is what made multi-add feel
      // broken when the agent already had a doc staged.
      const addBtn = el('button', { type: 'button', class: 'attach-preview-add', 'aria-label': 'Add more' });
      addBtn.appendChild(el('i', { 'data-lucide': 'plus' }));
      addBtn.addEventListener('click', () => {
        E.attachInput.removeAttribute('accept');
        E.attachInput.multiple = true;
        E.attachInput.click();
      });
      strip.appendChild(addBtn);
    }
    renderAttachPreviewVoicePill();
    refreshIcons();
  }

  function renderAttachPreviewVoicePill() {
    const pill = document.getElementById('attachPreviewVoicePill');
    const lbl = document.getElementById('attachPreviewVoiceLabel');
    if (!pill || !lbl) return;
    if (!state.pendingVoiceBlob) {
      pill.classList.add('hidden');
      // If voice was the only thing keeping the strip open and now it's
      // gone, close the strip (no pending files either).
      if (!state.pendingAttachments.length) renderAttachPreview();
      return;
    }
    pill.classList.remove('hidden');
    const sec = Math.max(1, Math.round((state.pendingVoiceDurationMs || 0) / 1000));
    lbl.textContent = 'Voice ready (' + Math.floor(sec / 60) + ':' + String(sec % 60).padStart(2, '0') + ')';
    refreshIcons();
  }

  function cancelAttachBatch() {
    state.pendingAttachments = [];
    state.pendingVoiceBlob = null;
    state.pendingVoiceDurationMs = 0;
    const cap = document.getElementById('attachPreviewCaption');
    if (cap) cap.value = '';
    renderAttachPreview();
  }

  // Updates the status text + progress bar on a single tile in place,
  // without re-rendering the whole strip. Called during sendAttachBatch
  // / uploadPdfChunked so the agent always knows where the upload is.
  // Pass progress 0..1 to fill the bottom bar; pass null to hide it.
  function setAttachTileStatus(file, statusText, progress) {
    const tile = document.querySelector(
      '#attachPreviewStrip .attach-preview-thumb[data-key="' + (file._previewKey || '') + '"]'
    );
    if (!tile) return;
    const sizeEl = tile.querySelector('.attach-preview-doc-size');
    if (sizeEl && statusText != null) sizeEl.textContent = statusText;
    let bar = tile.querySelector('.attach-preview-progress');
    if (progress == null) {
      if (bar) bar.remove();
      return;
    }
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'attach-preview-progress';
      tile.appendChild(bar);
    }
    bar.style.width = Math.max(0, Math.min(1, progress)) * 100 + '%';
  }


  // Chunked upload for PDFs that exceed Cloudflare's 100 MB POST cap.
  // Splits the file into 80 MB pieces, posts each to /upload-chunk,
  // then asks the server to assemble + compress + send via
  // /finalize-chunked. Each chunk request stays under 100 MB so it
  // passes Cloudflare; the server-side compression then squeezes the
  // assembled file under WhatsApp's 100 MB document cap.
  async function uploadPdfChunked(file, { chatId, fileName, docType }) {
    return uploadChunked(file, '/api/documents/finalize-chunked', { chatId, fileName, docType });
  }

  // Big VIDEO takes the same lane: Cloudflare caps a POST at 100 MB, so a
  // screen recording over that never reached the server at all — the agent
  // just saw "too large", even though the server would have compressed it to
  // a few MB. Chunks go to the SAME sink; only the finalize differs.
  async function uploadVideoChunked(file, { chatId, caption }) {
    return uploadChunked(file, '/api/inbox/finalize-chunked-video', {
      chatId, caption, fileName: file.name, mimeType: file.type || 'video/mp4',
    });
  }

  async function uploadChunked(file, finalizeUrl, payload) {
    // 20 MB, not 80. Two reasons, both learned on a 130 MB screen recording
    // over an office uplink (2026-07-30): an 80 MB POST can outrun the
    // per-request timeout on a slow line and fail the whole upload, and a
    // 2-chunk upload gives the agent almost no sense of progress — they see
    // "1/2" for a minute and assume it's stuck. Smaller pieces cost a few more
    // round-trips and buy a real progress bar.
    const CHUNK_SIZE = 20 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = (crypto.randomUUID && crypto.randomUUID())
      || ('xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      }));

    const pctLabel = (done) => `Uploading ${Math.round((done / totalChunks) * 100)}%`;
    setAttachTileStatus(file, pctLabel(0), 0);
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      // Carry the parent's MIME onto the slice — a bare Blob.slice() posts as
      // application/octet-stream, and it also makes the request self-describing
      // for anything sitting between us and the server.
      const blob = file.slice(start, end, file.type || 'application/octet-stream');
      const fd = new FormData();
      fd.append('uploadId', uploadId);
      fd.append('chunkIndex', String(i));
      fd.append('totalChunks', String(totalChunks));
      fd.append('chunk', blob, `chunk-${i}.bin`);
      setAttachTileStatus(file, pctLabel(i), i / totalChunks);
      // 5 min per chunk: a 20 MB piece on a bad line still lands, and a slow
      // link must not fail an upload that is genuinely progressing.
      const r = await fetchT('/api/documents/upload-chunk', { method: 'POST', headers: authHeaders(), body: fd }, 300000);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(`Chunk ${i+1}/${totalChunks} failed: ${j.error || ('HTTP ' + r.status)}`);
      }
      setAttachTileStatus(file, pctLabel(i + 1), (i + 1) / totalChunks);
    }

    setAttachTileStatus(file, 'Compressing on server…', 1);
    const finalizeRes = await fetchT(finalizeUrl, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadId, ...payload }),
      // Big-PDF finalize: server-side Ghostscript compression (up to ~180s,
      // serialized behind a single slot) + a 100MB+ media upload. A 120s cap
      // would false-abort a legit slow finalize → agent retries → duplicate PDF
      // sent to the client. Generous 10-min cap: still bounded (never hangs
      // forever) but won't trip on a real slow large-doc send.
    }, 600000);
    const j = await finalizeRes.json().catch(() => ({}));
    if (!finalizeRes.ok || j.error) {
      throw new Error(j.error || ('HTTP ' + finalizeRes.status));
    }
    setAttachTileStatus(file, 'Sent ✓', 1);
    return j;
  }

  async function sendAttachBatch() {
    if (!state.selectedChatId) return;
    // Note mode is team-only: files and voice in the strip would reach the client.
    if (state.composeNoteMode) { toast("Note mode: tfi Note bach t-sift l'client", 'error'); return; }
    const targetChatId = _composeTargetChatId();
    const remoteTarget = String(targetChatId || '').startsWith('support2:');
    const s2Line = _composeViaSupport2Line(targetChatId);
    const files = state.pendingAttachments.slice();
    const voice = state.pendingVoiceBlob;
    const captionRaw = document.getElementById('attachPreviewCaption')?.value || '';
    const caption = captionRaw.trim();
    if (!files.length && !voice) return;

    const sendBtn = document.getElementById('attachPreviewSendBtn');
    if (sendBtn) sendBtn.disabled = true;

    const MAX_BYTES_OTHER = 100 * 1024 * 1024;
    const MAX_BYTES_PDF   = 2 * 1024 * 1024 * 1024;
    // 80 MB safely under Cloudflare's 100 MB POST limit. PDFs over
    // this go through the chunked upload path.
    const CHUNK_THRESHOLD = 80 * 1024 * 1024;
    let sent = 0, failed = 0;
    const failedKeys = new Set();
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isPdf = file.type === 'application/pdf';
      const isVideo = (file.type || '').startsWith('video/');
      // Video is no longer capped at the single-POST limit — over the chunk
      // threshold it goes up in pieces and the server compresses it. Its real
      // ceiling is the transcoder's (5 min of footage), enforced server-side
      // with a message the agent can act on.
      const maxBytes = (isPdf || isVideo) ? MAX_BYTES_PDF : MAX_BYTES_OTHER;
      // Pre-flight size check — skip oversize files instead of burning
      // a round-trip just to get a 413 back. Mark them as failed so the
      // tile keeps the warning state and the agent can remove or
      // compress before retrying.
      if (file.size > maxBytes) {
        failed++;
        failedKeys.add(file._previewKey);
        const limitLabel = maxBytes >= 1024 * 1024 * 1024
          ? `${(maxBytes / 1024 / 1024 / 1024).toFixed(0)} GB`
          : `${maxBytes / 1024 / 1024} MB`;
        toast(file.name + ': too large (' + (file.size / 1024 / 1024).toFixed(1) + ` MB > ${limitLabel}) — split the file`, 'error');
        continue;
      }
      // The chunked paths below only know our own line. On the support2 line a
      // file travels as one POST through support2's tunnel (100 MB cap), so a
      // bigger one is refused here rather than sent from the wrong number.
      if (s2Line && (isVideo || isPdf) && file.size > CHUNK_THRESHOLD) {
        failed++;
        failedKeys.add(file._previewKey);
        setAttachTileStatus(file, 'Failed', null);
        toast(file.name + ': trop lourd pour la ligne Support2 (max 80 MB)', 'error');
        continue;
      }
      // PDFs over Cloudflare's 100 MB POST cap → chunked upload +
      // server-side compression. Other PDFs (≤ 80 MB) and non-PDFs go
      // through the existing single-shot path.
      if (isVideo && file.size > CHUNK_THRESHOLD) {
        try {
          await uploadVideoChunked(file, { chatId: targetChatId, caption: i === 0 ? caption : '' });
          sent++;
        } catch (err) {
          failed++;
          failedKeys.add(file._previewKey);
          setAttachTileStatus(file, 'Failed', null);
          toast(file.name + ': ' + (err?.message || 'chunked upload failed'), 'error');
        }
        if (i < files.length - 1) await new Promise(r => setTimeout(r, 1500));
        continue;
      }
      if (isPdf && file.size > CHUNK_THRESHOLD) {
        try {
          await uploadPdfChunked(file, {
            chatId: targetChatId,
            fileName: file.name,
            docType: 'document',
          });
          sent++;
        } catch (err) {
          failed++;
          failedKeys.add(file._previewKey);
          setAttachTileStatus(file, 'Failed', null);
          toast(file.name + ': ' + (err?.message || 'chunked upload failed'), 'error');
        }
        if (i < files.length - 1) await new Promise(r => setTimeout(r, 1500));
        continue;
      }

      const fd = new FormData();
      fd.append('chatId', targetChatId);
      // PDFs go through /api/documents/send so they stay tracked in
      // the documents-index (used for proforma / invoice lookups);
      // everything else uses /api/inbox/send-file with auto thumb +
      // compression + audio re-encode.
      const endpoint = isPdf && !remoteTarget ? '/api/documents/send' : '/api/inbox/send-file';
      if (isPdf && !remoteTarget) {
        fd.append('docType', 'document');
        fd.append('fileName', file.name);
      }
      if (s2Line) fd.append('via', 'support2');
      fd.append('file', file, file.name);
      // Caption rides only on the FIRST file — matches WhatsApp.
      // /api/documents/send doesn't accept a caption (it's a structured
      // document send), so caption only attaches to send-file targets.
      if (i === 0 && caption && !isPdf) fd.append('caption', caption);
      setAttachTileStatus(file, 'Sending…', 0.5);
      try {
        const r = await fetchT(endpoint, { method: 'POST', headers: authHeaders(), body: fd }, 120000);
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j.error) {
          failed++;
          failedKeys.add(file._previewKey);
          setAttachTileStatus(file, 'Failed', null);
          // Toast the SPECIFIC reason so the agent knows what to fix
          // ("File too large — max 100 MB"), not just a generic
          // "Upload failed" sea of red toasts.
          toast(file.name + ': ' + (j.error || `HTTP ${r.status}`), 'error');
        } else {
          sent++;
          setAttachTileStatus(file, 'Sent ✓', 1);
        }
      } catch (err) {
        failed++;
        failedKeys.add(file._previewKey);
        setAttachTileStatus(file, 'Failed', null);
        toast(file.name + ': ' + (err?.message || 'network error'), 'error');
      }
      // 1.5 s pause between files — anti-ban pacing matches /api/send-media.
      if (i < files.length - 1) await new Promise(r => setTimeout(r, 1500));
    }

    let voiceSent = false;
    if (voice) {
      const fd = new FormData();
      fd.append('chatId', targetChatId);
      if (s2Line) fd.append('via', 'support2');
      fd.append('audio', voice, 'voice.webm');
      try {
        const r = await fetchT('/api/inbox/send-audio', { method: 'POST', headers: authHeaders(), body: fd }, 120000);
        const j = await r.json();
        if (!j.error) voiceSent = true;
        else toast('Voice failed: ' + j.error, 'error');
      } catch { toast('Voice send failed', 'error'); }
    }

    if (sendBtn) sendBtn.disabled = false;

    // Mark failed thumbs + keep them in the batch for retry, drop the rest.
    state.pendingAttachments = state.pendingAttachments
      .filter(f => failedKeys.has(f._previewKey))
      .map(f => Object.assign(f, { _failed: true }));
    if (voiceSent) {
      state.pendingVoiceBlob = null;
      state.pendingVoiceDurationMs = 0;
    }
    if (!state.pendingAttachments.length && !state.pendingVoiceBlob) {
      const cap = document.getElementById('attachPreviewCaption');
      if (cap) cap.value = '';
    }
    renderAttachPreview();
    loadMessages();
    _refreshPersonOtherMessages(state.personChat, { force: true });

    const summary = [
      sent ? `${sent} file${sent === 1 ? '' : 's'} sent` : '',
      voiceSent ? 'voice sent' : '',
      failed ? `${failed} failed` : ''
    ].filter(Boolean).join(' · ');
    if (summary) toast(summary, failed ? 'error' : 'success');
  }

  // ============ View switching: Inbox vs Bulk Send ============
  state.activeView = 'inbox';
  state.bulkGroups = [];
  state.bulkSelected = new Set();
  state.bulkActiveTag = null;
  state.bulkSearch = '';
  state.bulkSending = false;
  state.bulkAttachments = [];

  function setBulkListMessage(text) {
    const list = document.getElementById('bulkGroupsList');
    clear(list);
    list.appendChild(el('div', { class: 'bulk-loading', text }));
  }

  function setHistoryListMessage(text) {
    const list = document.getElementById('historyList');
    clear(list);
    list.appendChild(el('div', { class: 'bulk-loading', text }));
  }

  // Mark the matching top-bar tab(s) as active. For grouped views, also
  // highlight the parent dropdown trigger so the user knows which area
  // they're in even when the menu is closed. Used by showView + by the
  // templates branch that rewinds the active view to inbox.
  function markActiveTab(view) {
    document.querySelectorAll('.view-tab, .view-tab-item').forEach(t => {
      // Items carrying a data-channel attribute (the Inbox dropdown's
      // per-channel children) own their own active state via state.
      // activeChannel — don't mass-toggle them by data-view, otherwise
      // every channel inside the Inbox group would light up at once.
      if (t.hasAttribute('data-channel')) {
        t.classList.toggle('view-tab-active',
          t.getAttribute('data-view') === view && t.getAttribute('data-channel') === state.activeChannel);
        return;
      }
      t.classList.toggle('view-tab-active', t.getAttribute('data-view') === view);
    });
    // Then walk dropdown groups: trigger lights up when one of its child
    // items matches the active view.
    document.querySelectorAll('.view-tab-group').forEach(group => {
      const trigger = group.querySelector('.view-tab-trigger');
      const hasActiveChild = !!group.querySelector('.view-tab-item.view-tab-active');
      trigger?.classList.toggle('view-tab-active', hasActiveChild);
    });
  }

  // Persist the last-active top-level view across page reloads. Agents kept
  // landing back on the Inbox after F5 even though they'd been working in
  // Contacts / Tickets / Dashboards — losing their context (active filter,
  // selected row, scroll position) every time. We store the view slug in
  // localStorage on every showView() call and restore it after bootApp() has
  // verified the token + put the app screen up. The `templates` pseudo-view
  // is excluded (it's a modal that rewinds to 'inbox' anyway).
  const LAST_VIEW_KEY = 'leads7.lastView';
  function _persistLastView(view) {
    try {
      if (!view || view === 'templates') return;
      localStorage.setItem(LAST_VIEW_KEY, view);
    } catch { /* localStorage may be disabled in some browsers */ }
  }

  // Inbox ⇄ Équipe ⇄ Rooms segmented toggles (one per sidebar) — keep both
  // instances' active state in sync with the current view + active teamchat
  // tab. Also swaps the sidebar search placeholder to match what the active
  // tab actually filters.
  function syncTeamSeg(view) {
    const active = view === 'teamchat'
      ? (state.teamchatActiveTab === 'rooms' ? 'rooms' : 'team')
      : 'inbox';
    document.querySelectorAll('.teamchat-seg-btn').forEach(b => {
      const on = b.dataset.seg === active;
      b.classList.toggle('on', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    const search = document.getElementById('teamchatSearch');
    if (search) {
      search.placeholder = state.teamchatActiveTab === 'rooms'
        ? 'Chercher une room…' : 'Chercher un teammate…';
    }
  }

  // ============================================================
  // Website Tests · personalized placement submissions. Agents get an own-only
  // read view; teacher/admin/super_sales keep the established review controls.
  // List + centered teacher review command center. Audio replays via the authed media endpoint
  // (/api/media/audio/<key>, fetched with the token → blob URL). Backend:
  // /api/level-tests{,/:id,/:id/mabrouk,/hubspot-search}.
  // ============================================================
  const LT = {
    inited: false, status: '', q: '', items: [], counts: {}, current: null,
    searchTimer: null, pollTimer: null, loading: false,
    evidenceFilter: 'all', expandedQuestionId: null,
    agentId: '', teacherId: '', date: '', filters: { agents: [], teachers: [] },
    canAssignTeacher: false, socketBound: false,
  };
  const LT_POLL_MS = 30000;

  // Teacher-facing UI must be fully English (2026-07-21 audit); Sales/admin
  // keep French/Darija. The Niveau view is shared between both audiences, so
  // every user-visible string routes through this picker.
  function _ltT(fr, en) {
    return String(state.me?.role || '').toLowerCase() === 'teacher' ? en : fr;
  }
  function _ltCanManage() {
    return ['teacher', 'super_sales', 'admin', 'super_admin'].includes(String(state.me?.role || '').toLowerCase());
  }
  function _ltIsScopedTeacher() {
    return String(state.me?.role || '').toLowerCase() === 'teacher' && state.me?.teacherManager !== true;
  }

  // `opts` is merged last so a caller can widen the watchdog — a PDF render
  // plus a WhatsApp upload outlives the 60s default.
  function _ltJson(path, method, body, opts = {}) {
    return api(path, {
      method: method || 'GET',
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined,
      ...opts,
    }).then(async r => { const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status)); return j; });
  }
  function _ltLevelClass(lvl) {
    const b = String(lvl || '').charAt(0).toUpperCase();
    return b === 'A' ? 'lt-lvl-a' : b === 'B' ? 'lt-lvl-b' : b === 'C' ? 'lt-lvl-c' : 'lt-lvl-none';
  }
  function _ltPill(lvl) { return el('span', { class: 'lt-pill ' + _ltLevelClass(lvl), text: lvl || '—' }); }
  function _ltFmtDate(ts) {
    try { return MA.loc(new Date(ts), 'd', 'fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  }
  function _ltFmtDateParts(ts) {
    try {
      const d = new Date(ts);
      return {
        date: MA.loc(d, 'd', _ltIsScopedTeacher() ? 'en-GB' : 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }),
        time: MA.loc(d, 't', _ltIsScopedTeacher() ? 'en-GB' : 'fr-FR', { hour: '2-digit', minute: '2-digit' }),
      };
    } catch { return { date: '', time: '' }; }
  }
  function _ltFmtDuration(totalSeconds, includeExactSeconds) {
    const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
    const minutes = Math.floor(seconds / 60);
    const clock = minutes + ':' + String(seconds % 60).padStart(2, '0');
    return includeExactSeconds ? clock + ' (' + seconds + 's)' : clock;
  }
  function _ltStatusBadge(status) {
    const map = { pending: { t: _ltT('En attente', 'Pending'), c: 'warning' }, reviewed: { t: _ltT('Évalué', 'Reviewed'), c: 'info' }, sent: { t: _ltT('Envoyé', 'Sent'), c: 'success' } };
    const m = map[status] || { t: status || '—', c: 'muted' };
    return el('span', { class: 'lt-status lt-status-' + m.c, text: m.t });
  }
  function _ltSection(title) { return el('div', { class: 'lt-sec', text: title }); }

  function _ltStopPolling() {
    if (LT.pollTimer) clearInterval(LT.pollTimer);
    LT.pollTimer = null;
  }

  function _ltStartPolling() {
    _ltStopPolling();
    LT.pollTimer = setInterval(() => {
      if (state.activeView !== 'level-tests') { _ltStopPolling(); return; }
      loadLevelTests({ background: true });
    }, LT_POLL_MS);
  }

  function initLevelTests() {
    if (!LT.inited) {
      LT.inited = true;
      document.getElementById('ltRefreshBtn')?.addEventListener('click', () => loadLevelTests());
      document.getElementById('ltGenBtn')?.addEventListener('click', () => _ltOpenGenModal());
      document.getElementById('ltChips')?.addEventListener('click', (e) => {
        const chip = e.target.closest('.lt-chip'); if (!chip) return;
        LT.status = chip.getAttribute('data-status') || '';
        document.querySelectorAll('#ltChips .lt-chip').forEach(c => c.classList.toggle('lt-chip-on', c === chip));
        loadLevelTests();
      });
      const search = document.getElementById('ltSearch');
      search?.addEventListener('input', () => {
        clearTimeout(LT.searchTimer);
        LT.searchTimer = setTimeout(() => { LT.q = search.value.trim(); loadLevelTests(); }, 280);
      });
      document.getElementById('ltAgentFilter')?.addEventListener('change', (event) => {
        LT.agentId = event.target.value || '';
        loadLevelTests();
      });
      document.getElementById('ltTeacherFilter')?.addEventListener('change', (event) => {
        LT.teacherId = event.target.value || '';
        loadLevelTests();
      });
      document.getElementById('ltDateFilter')?.addEventListener('change', (event) => {
        LT.date = event.target.value || '';
        loadLevelTests();
      });
      document.getElementById('ltClearFilters')?.addEventListener('click', () => {
        LT.agentId = ''; LT.teacherId = ''; LT.date = ''; LT.q = '';
        if (search) search.value = '';
        const agent = document.getElementById('ltAgentFilter'); if (agent) agent.value = '';
        const teacher = document.getElementById('ltTeacherFilter'); if (teacher) teacher.value = '';
        const date = document.getElementById('ltDateFilter'); if (date) date.value = '';
        loadLevelTests();
      });
      document.getElementById('ltDrawerBackdrop')?.addEventListener('click', closeLtDrawer);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !document.getElementById('ltDrawer')?.classList.contains('hidden')) closeLtDrawer();
      });
    }
    if (!LT.socketBound && state.socket) {
      LT.socketBound = true;
      state.socket.on('level-test:assignment', () => {
        if (state.activeView === 'level-tests') loadLevelTests({ background: true });
      });
    }
    const teacherFilter = document.getElementById('ltTeacherFilter')?.closest('.lt-filter-control');
    teacherFilter?.classList.toggle('hidden', _ltIsScopedTeacher());
    _ltStartPolling();
    loadLevelTests();
  }

  function loadLevelTests(options = {}) {
    const background = options?.background === true;
    if (LT.loading) return Promise.resolve();
    LT.loading = true;
    const listEl = document.getElementById('ltList');
    const loading = document.getElementById('ltLoading');
    const empty = document.getElementById('ltEmpty');
    if (!listEl) { LT.loading = false; return Promise.resolve(); }
    if (!background) loading?.classList.remove('hidden');
    empty?.classList.add('hidden');
    const params = new URLSearchParams();
    if (LT.status) params.set('status', LT.status);
    if (LT.q) params.set('q', LT.q);
    if (LT.agentId) params.set('agent_id', LT.agentId);
    if (LT.teacherId && !_ltIsScopedTeacher()) params.set('teacher_id', LT.teacherId);
    if (LT.date) params.set('date', LT.date);
    return _ltJson('/api/level-tests?' + params.toString()).then(j => {
      LT.items = j.submissions || [];
      LT.counts = {}; (j.counts || []).forEach(c => { LT.counts[c.review_status] = c.n; });
      LT.filters = j.filters || { agents: [], teachers: [] };
      LT.canAssignTeacher = j.can_assign_teacher === true;
      _ltRenderFilterOptions();
      _ltRenderCounts();
      _ltRenderList();
      loading?.classList.add('hidden');
    }).catch(err => {
      loading?.classList.add('hidden');
      if (!background) {
        clear(listEl);
        empty?.classList.remove('hidden');
        toast(err.message || _ltT('Erreur de chargement', 'Loading error'), 'error');
      }
    }).finally(() => { LT.loading = false; });
  }

  function _ltRenderCounts() {
    const total = (LT.counts.pending || 0) + (LT.counts.reviewed || 0) + (LT.counts.sent || 0);
    const set = (k, v) => { const e = document.querySelector('#ltChips [data-count="' + k + '"]'); if (e) e.textContent = v; };
    set('all', total); set('pending', LT.counts.pending || 0); set('reviewed', LT.counts.reviewed || 0); set('sent', LT.counts.sent || 0);
    const badge = document.getElementById('levelTestsTabBadge');
    if (badge) { const p = LT.counts.pending || 0; badge.textContent = p > 99 ? '99+' : String(p); badge.classList.toggle('hidden', p === 0); }
    const resultCount = document.getElementById('ltResultCount');
    if (resultCount) resultCount.textContent = LT.items.length + ' ' + _ltT('test' + (LT.items.length > 1 ? 's' : ''), 'test' + (LT.items.length === 1 ? '' : 's'));
    // Roll the Niveau pending count up into the Tests▾ trigger's aggregated badge.
    try { refreshGroupBadges(); } catch (_) { /* badge is advisory */ }
  }

  function _ltRenderFilterOptions() {
    const fill = (select, rows, firstLabel, extra) => {
      if (!select) return;
      const wanted = select.value;
      clear(select);
      select.appendChild(el('option', { value: '', text: firstLabel }));
      if (extra) select.appendChild(el('option', { value: extra.value, text: extra.text }));
      (rows || []).forEach(row => select.appendChild(el('option', { value: row.id, text: row.name || row.id })));
      select.value = wanted;
      if (select.value !== wanted) select.value = '';
    };
    fill(document.getElementById('ltAgentFilter'), LT.filters.agents, _ltT('Tous les agents', 'All agents'));
    fill(document.getElementById('ltTeacherFilter'), LT.filters.teachers, _ltT('Tous les professeurs', 'All teachers'), {
      value: 'unassigned', text: _ltT('Non assigné', 'Unassigned'),
    });
  }

  function _ltTeacherAssignmentControl(submission) {
    if (!LT.canAssignTeacher) {
      return el('span', {
        class: 'lt-teacher-name' + (submission.assigned_teacher_id ? '' : ' is-empty'),
        text: submission.assigned_teacher_name || _ltT('Non assigné', 'Unassigned'),
      });
    }
    const select = el('select', {
      class: 'lt-assignment-select',
      'aria-label': _ltT('Assigner le professeur', 'Assign teacher'),
      onclick: event => event.stopPropagation(),
      onkeydown: event => event.stopPropagation(),
    });
    select.appendChild(el('option', { value: '', text: _ltT('Non assigné', 'Unassigned') }));
    (LT.filters.teachers || []).forEach(teacher => {
      select.appendChild(el('option', { value: teacher.id, text: teacher.name || teacher.id }));
    });
    if (submission.assigned_teacher_id && !(LT.filters.teachers || []).some(teacher => String(teacher.id) === String(submission.assigned_teacher_id))) {
      select.appendChild(el('option', {
        value: submission.assigned_teacher_id,
        text: submission.assigned_teacher_name || _ltT('Professeur inactif', 'Inactive teacher'),
        disabled: 'disabled',
      }));
    }
    select.value = submission.assigned_teacher_id || '';
    select.addEventListener('change', event => {
      event.stopPropagation();
      const previous = submission.assigned_teacher_id || '';
      const teacherId = select.value || '';
      select.disabled = true;
      select.classList.add('is-saving');
      _ltJson('/api/level-tests/' + encodeURIComponent(submission.id) + '/assignment', 'PATCH', { teacher_id: teacherId || null })
        .then(result => {
          const updated = result.submission || {};
          submission.assigned_teacher_id = updated.assigned_teacher_id || null;
          submission.assigned_teacher_name = updated.assigned_teacher_name || '';
          toast(teacherId ? _ltT('Test assigné au professeur', 'Test assigned to teacher') : _ltT('Assignation supprimée', 'Assignment removed'), 'success');
          loadLevelTests({ background: true });
        })
        .catch(error => {
          select.value = previous;
          toast(error.message || _ltT("Impossible d'assigner le test", 'Could not assign test'), 'error');
        })
        .finally(() => { select.disabled = false; select.classList.remove('is-saving'); });
    });
    return select;
  }

  function _ltRenderList() {
    const listEl = document.getElementById('ltList');
    clear(listEl);
    if (!LT.items.length) { document.getElementById('ltEmpty')?.classList.remove('hidden'); return; }
    document.getElementById('ltEmpty')?.classList.add('hidden');
    listEl.appendChild(el('div', { class: 'lt-table-head', role: 'row' }, [
      el('span', { text: _ltT('Étudiant', 'Student') }),
      el('span', { text: 'Agent' }),
      el('span', { text: _ltT('Date du test', 'Test date') }),
      el('span', { text: _ltT('Niveau IA', 'AI level') }),
      el('span', { text: _ltT('Final', 'Final') }),
      el('span', { text: _ltT('Professeur assigné', 'Assigned teacher') }),
      el('span', { text: _ltT('Statut', 'Status') }),
      el('span', { text: '' }),
    ]));
    for (const s of LT.items) {
      const name = s.hubspot_name || s.name || s.phone || '—';
      const parts = _ltFmtDateParts(s.created_at);
      const initials = String(name).split(/\s+/).filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase() || '—';
      const row = el('div', {
        class: 'lt-row lt-table-row', 'data-id': s.id, role: 'button', tabindex: '0',
        'aria-label': _ltT('Ouvrir le test de ', 'Open website test for ') + name,
        onclick: () => openLtDrawer(s.id),
        onkeydown: (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openLtDrawer(s.id); } },
      });
      row.appendChild(el('div', { class: 'lt-student-cell' }, [
        el('span', { class: 'lt-student-avatar', text: initials }),
        el('span', { class: 'lt-student-copy' }, [
          el('strong', { class: 'lt-row-name', text: name }),
          el('small', { class: 'lt-row-sub', text: s.phone || '—' }),
        ]),
      ]));
      row.appendChild(el('span', { class: 'lt-agent-cell', 'data-label': 'Agent', text: s.agent_name || '—' }));
      row.appendChild(el('span', { class: 'lt-date-cell', 'data-label': _ltT('Date', 'Date') }, [
        el('strong', { text: parts.date || '—' }),
        el('small', { text: parts.time || '' }),
      ]));
      row.appendChild(el('span', { class: 'lt-level-cell', 'data-label': _ltT('Niveau IA', 'AI level') }, [_ltPill(s.overall_level)]));
      row.appendChild(el('span', { class: 'lt-level-cell', 'data-label': _ltT('Final', 'Final') }, [_ltPill(s.reviewer_level)]));
      row.appendChild(el('span', { class: 'lt-teacher-cell', 'data-label': _ltT('Professeur', 'Teacher') }, [_ltTeacherAssignmentControl(s)]));
      row.appendChild(el('span', { class: 'lt-status-cell', 'data-label': _ltT('Statut', 'Status') }, [_ltStatusBadge(s.review_status)]));
      row.appendChild(el('span', { class: 'lt-open-cell', 'aria-hidden': 'true' }, [lucideIcon('chevron-right')]));
      listEl.appendChild(row);
    }
    refreshIcons(listEl);
  }

  function closeLtDrawer() {
    const d = document.getElementById('ltDrawer');
    if (!d) return;
    d.classList.add('hidden'); d.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('lt-review-open');
    LT.current = null;
    LT.expandedQuestionId = null;
  }

  function openLtDrawer(id) {
    const d = document.getElementById('ltDrawer');
    const panel = document.getElementById('ltDrawerPanel');
    if (!d || !panel) return;
    d.classList.remove('hidden'); d.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('lt-review-open');
    LT.evidenceFilter = 'all';
    LT.expandedQuestionId = null;
    clear(panel); panel.appendChild(el('div', { class: 'lt-state', text: _ltT('Chargement…', 'Loading…') }));
    _ltJson('/api/level-tests/' + encodeURIComponent(id)).then(j => {
      LT.current = j.submission;
      const evidence = Array.isArray(j.submission?.question_evidence) ? j.submission.question_evidence : [];
      const firstAttention = evidence.find(item => _ltQuestionState(item) === 'incorrect')
        || evidence.find(item => _ltQuestionState(item) === 'skipped')
        || evidence[0];
      LT.expandedQuestionId = firstAttention ? String(firstAttention.id) : null;
      _ltRenderDrawer(j.submission);
    }).catch(err => { clear(panel); panel.appendChild(el('div', { class: 'lt-state', text: err.message || _ltT('Erreur', 'Error') })); });
  }

  function _ltLoadAudio(audioEl, key) {
    // key = "level-test/<sid>/q<item-id>.wav" → authed disk-serve route (MinIO fallback inside).
    const m = String(key || '').match(/^level-test\/([^/]+)\/(q[a-z0-9-]{1,40}\.wav)$/i);
    const url = m ? ('/api/level-tests/audio/' + encodeURIComponent(m[1]) + '/' + encodeURIComponent(m[2]))
                  : ('/api/media/audio/' + encodeURIComponent(key));
    api(url).then(r => r.ok ? r.blob() : null).then(b => {
      if (b) audioEl.src = URL.createObjectURL(b);
      else audioEl.replaceWith(el('div', { class: 'lt-muted lt-audio-miss', text: _ltT('(audio indisponible)', '(audio unavailable)') }));
    }).catch(() => { try { audioEl.replaceWith(el('div', { class: 'lt-muted lt-audio-miss', text: _ltT('(audio indisponible)', '(audio unavailable)') })); } catch {} });
  }

  function _ltSpeakingCard(a) {
    const card = el('div', { class: 'lt-qcard' });
    card.appendChild(el('div', { class: 'lt-qprompt', text: a.prompt || ('Question ' + a.index) }));
    if (a.audio_key) {
      const audio = el('audio', { class: 'lt-audio', controls: 'controls', preload: 'none' });
      _ltLoadAudio(audio, a.audio_key);
      card.appendChild(audio);
    }
    card.appendChild(el('div', { class: 'lt-transcript' + (a.transcript ? '' : ' lt-muted'), text: a.transcript || _ltT('(aucune transcription)', '(no transcript)') }));
    if (a.subscores) {
      const chips = el('div', { class: 'lt-dims' });
      const dl = { fluency_coherence: _ltT('Fluidité', 'Fluency'), pronunciation: _ltT('Prononc.', 'Pronunc.'), grammar: _ltT('Grammaire', 'Grammar'), vocabulary: _ltT('Vocab.', 'Vocab.'), task_achievement: _ltT('Réalisation', 'Task achievement'), interaction: _ltT('Interaction (historique)', 'Interaction (legacy)') };
      Object.keys(dl).forEach(d => { if (a.subscores[d]) chips.appendChild(el('span', { class: 'lt-dim', text: dl[d] + ' ' + a.subscores[d].score })); });
      card.appendChild(chips);
    }
    if (a.note) card.appendChild(el('div', { class: 'lt-note', text: a.note }));
    return card;
  }

  function _ltWritingCard(a) {
    const card = el('div', { class: 'lt-qcard' });
    card.appendChild(el('div', { class: 'lt-qprompt', text: a.prompt || ('Question ' + a.index) }));
    card.appendChild(el('div', { class: 'lt-wtext' + (a.text ? '' : ' lt-muted'), text: a.text || _ltT('(aucune réponse)', '(no answer)') }));
    const meta = el('div', { class: 'lt-dims' });
    if (a.score != null) meta.appendChild(el('span', { class: 'lt-dim', text: 'Score ' + a.score + '/100' }));
    if (a.band) meta.appendChild(_ltPill(a.band));
    if (meta.childNodes.length) card.appendChild(meta);
    if (a.note) card.appendChild(el('div', { class: 'lt-note', text: a.note }));
    return card;
  }

  function _ltBullets(title, arr, kind) {
    const ul = el('ul', { class: 'lt-bullets lt-bullets-' + kind });
    arr.forEach(x => ul.appendChild(el('li', { text: x })));
    return el('div', { class: 'lt-bullet-block' }, [el('div', { class: 'lt-bullet-title', text: title }), ul]);
  }

  function _ltQuestionState(item) {
    const trace = item?.trace || {};
    if (trace.skipped || trace.timed_out || item?.selected_index == null) return 'skipped';
    if (item?.is_correct === true) return 'correct';
    if (item?.is_correct === false) return 'incorrect';
    return 'review';
  }

  function _ltQuestionStateMeta(stateName) {
    const map = {
      correct: { icon: 'check', label: _ltT('Correcte', 'Correct') },
      incorrect: { icon: 'x', label: _ltT('Incorrecte', 'Incorrect') },
      skipped: { icon: 'minus', label: _ltT('Passée', 'Skipped') },
      review: { icon: 'circle-help', label: _ltT('À revoir', 'Review') },
    };
    return map[stateName] || map.review;
  }

  function _ltChoice(index, option, emptyLabel) {
    if (index == null && !option) return emptyLabel || '—';
    const letter = Number.isFinite(Number(index)) ? String.fromCharCode(65 + Number(index)) : '';
    return (letter ? letter + '. ' : '') + (option || '—');
  }

  function _ltEvidenceBlock(s) {
    const evidence = Array.isArray(s.question_evidence) ? s.question_evidence : [];
    const wrap = el('section', { class: 'lt-evidence-card', 'aria-label': _ltT('Preuves du test', 'Test evidence') });
    const head = el('div', { class: 'lt-evidence-head' }, [
      el('div', {}, [
        el('div', { class: 'lt-card-title', text: _ltT('Correction des réponses', 'Answer review') }),
        el('div', { class: 'lt-card-subtitle', text: _ltT('Ouvrez une ligne pour comparer la réponse, le corrigé et la preuve IA.', 'Open a row to compare the answer, teacher key and AI evidence.') }),
      ]),
      el('span', { class: 'lt-evidence-total', text: evidence.length + ' ' + _ltT('questions', 'questions') }),
    ]);
    wrap.appendChild(head);

    const counts = { all: evidence.length, correct: 0, incorrect: 0, skipped: 0, speaking: 0, writing: 0 };
    evidence.forEach(item => {
      const stateName = _ltQuestionState(item);
      if (counts[stateName] != null) counts[stateName] += 1;
      const skill = String(item.skill || '').toLowerCase();
      if (skill === 'speaking') counts.speaking += 1;
      if (skill === 'writing') counts.writing += 1;
    });

    const tabs = el('div', { class: 'lt-evidence-tabs', role: 'tablist', 'aria-label': _ltT('Filtrer les réponses', 'Filter responses') });
    const list = el('div', { class: 'lt-evidence-list' });
    const filters = [
      ['all', _ltT('Toutes', 'All'), 'list-filter'],
      ['correct', _ltT('Correctes', 'Correct'), 'circle-check'],
      ['incorrect', _ltT('Incorrectes', 'Incorrect'), 'circle-x'],
      ['skipped', _ltT('Passées', 'Skipped'), 'circle-minus'],
      ['speaking', _ltT('Oral', 'Speaking'), 'mic'],
      ['writing', _ltT('Écrit', 'Writing'), 'pen-line'],
    ];

    function filteredItems() {
      if (LT.evidenceFilter === 'all') return evidence;
      if (LT.evidenceFilter === 'speaking' || LT.evidenceFilter === 'writing') {
        return evidence.filter(item => String(item.skill || '').toLowerCase() === LT.evidenceFilter);
      }
      return evidence.filter(item => _ltQuestionState(item) === LT.evidenceFilter);
    }

    function renderTabs() {
      clear(tabs);
      filters.forEach(([value, label, icon]) => {
        const active = LT.evidenceFilter === value;
        const btn = el('button', {
          class: 'lt-evidence-tab lt-evidence-tab-' + value + (active ? ' on' : ''),
          type: 'button', role: 'tab', 'aria-selected': active ? 'true' : 'false',
          onclick: () => { LT.evidenceFilter = value; renderTabs(); renderRows(); },
        }, [lucideIcon(icon), el('span', { text: label }), el('b', { text: String(counts[value] || 0) })]);
        tabs.appendChild(btn);
      });
      refreshIcons(tabs);
    }

    function renderRows() {
      clear(list);
      const rows = filteredItems();
      if (!rows.length) {
        list.appendChild(el('div', { class: 'lt-evidence-empty', text: _ltT('Aucune réponse dans ce filtre.', 'No responses in this filter.') }));
        return;
      }
      list.appendChild(el('div', { class: 'lt-evidence-columns', 'aria-hidden': 'true' }, [
        el('span', { text: _ltT('Statut', 'Status') }),
        el('span', { text: _ltT('Compétence / question', 'Skill / question') }),
        el('span', { text: _ltT('Réponse élève', 'Student answer') }),
        el('span', { text: _ltT('Réponse clé', 'Teacher key') }),
        el('span', { text: _ltT('Temps', 'Time') }),
        el('span', { text: _ltT('Preuve IA', 'AI evidence') }),
      ]));
      rows.forEach(item => {
        const id = String(item.id || '—');
        const stateName = _ltQuestionState(item);
        const stateMeta = _ltQuestionStateMeta(stateName);
        const trace = item.trace || {};
        const expanded = LT.expandedQuestionId === id;
        const rowWrap = el('article', { class: 'lt-evidence-item lt-evidence-item-' + stateName + (expanded ? ' is-open' : '') });
        const row = el('div', {
          class: 'lt-evidence-row', role: 'button', tabindex: '0',
          'aria-expanded': expanded ? 'true' : 'false',
          onclick: () => { LT.expandedQuestionId = expanded ? null : id; renderRows(); },
          onkeydown: event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault(); LT.expandedQuestionId = expanded ? null : id; renderRows();
            }
          },
        }, [
          el('span', { class: 'lt-answer-state lt-answer-state-' + stateName }, [lucideIcon(stateMeta.icon), el('span', { text: stateMeta.label })]),
          el('span', { class: 'lt-question-cell' }, [
            el('b', { text: String(item.skill || _ltT('Question', 'Question')) + (item.target ? ' · ' + item.target : '') }),
            el('small', { text: item.prompt || id }),
          ]),
          el('span', { class: 'lt-answer-cell', text: _ltChoice(item.selected_index, item.selected_option, _ltT('Sans réponse', 'No answer')) }),
          el('span', { class: 'lt-key-cell', text: _ltChoice(item.correct_index, item.correct_option, '—') }),
          el('span', {
            class: 'lt-trace-cell',
            title: Math.max(0, Math.round(Number(trace.time_spent_seconds) || 0)) + ' ' + _ltT('secondes', 'seconds'),
            text: _ltFmtDuration(trace.time_spent_seconds) + (trace.listen_count ? ' · ' + trace.listen_count + '×' : ''),
          }),
          el('span', { class: 'lt-ai-cell' }, [item.answer_evidence ? lucideIcon('sparkles') : lucideIcon('minus'), lucideIcon(expanded ? 'chevron-up' : 'chevron-down')]),
        ]);
        rowWrap.appendChild(row);
        if (expanded) {
          rowWrap.appendChild(el('div', { class: 'lt-evidence-expanded' }, [
            el('div', { class: 'lt-compare-box lt-compare-student' }, [
              el('span', { text: _ltT('Réponse de l’élève', 'Student answer') }),
              el('strong', { text: _ltChoice(item.selected_index, item.selected_option, _ltT('Aucune réponse enregistrée', 'No answer recorded')) }),
            ]),
            el('div', { class: 'lt-compare-box lt-compare-key' }, [
              el('span', { text: _ltT('Réponse attendue', 'Expected answer') }),
              el('strong', { text: _ltChoice(item.correct_index, item.correct_option, _ltT('Pas de corrigé automatique', 'No automatic key')) }),
            ]),
            el('div', { class: 'lt-evidence-reason' }, [
              el('span', {}, [lucideIcon('sparkles'), el('b', { text: _ltT('Explication IA', 'AI explanation') })]),
              el('p', { text: item.answer_evidence || _ltT('Cette réponse doit être vérifiée par le professeur.', 'This answer needs teacher review.') }),
              el('small', { text: _ltT('Trace : ', 'Trace: ') + _ltFmtDuration(trace.time_spent_seconds, true) + ' · ' + _ltT('écoutes ', 'listens ') + (trace.listen_count || 0) + (trace.timed_out ? ' · timeout' : '') }),
            ]),
          ]));
        }
        list.appendChild(rowWrap);
      });
      refreshIcons(list);
    }

    renderTabs();
    renderRows();
    wrap.append(tabs, list);
    return wrap;
  }

  function _ltLinkBlock(s) {
    if (s.link_status === 'linked' && s.hubspot_contact_id) {
      return el('div', { class: 'lt-link lt-link-ok' }, [lucideIcon('link'), el('span', { text: _ltT('Lié à ', 'Linked to ') + (s.hubspot_name || s.hubspot_contact_id) })]);
    }
    const wrap = el('div', { class: 'lt-link lt-link-warn' });
    wrap.appendChild(el('div', { class: 'lt-link-msg' }, [lucideIcon('unlink'), el('span', { text: _ltT('Contact HubSpot non trouvé — recherchez pour lier.', 'HubSpot contact not found — search to link.') })]));
    const inp = el('input', { class: 'lt-input', type: 'tel', placeholder: _ltT('Téléphone', 'Phone'), value: s.phone || '' });
    const btn = el('button', { class: 'lt-btn lt-btn-ghost lt-btn-sm', text: _ltT('Chercher', 'Search') });
    const results = el('div', { class: 'lt-relink-results' });
    btn.addEventListener('click', () => {
      clear(results); results.appendChild(el('div', { class: 'lt-state', text: _ltT('Recherche…', 'Searching…') }));
      _ltJson('/api/level-tests/hubspot-search?phone=' + encodeURIComponent(inp.value.trim())).then(j => {
        clear(results);
        if (!j.candidates || !j.candidates.length) { results.appendChild(el('div', { class: 'lt-state', text: _ltT('Aucun résultat.', 'No results.') })); return; }
        j.candidates.forEach(c => results.appendChild(el('div', { class: 'lt-cand' }, [
          el('div', {}, [el('div', { class: 'lt-cand-name', text: c.name || c.id }), el('div', { class: 'lt-cand-sub', text: (c.phone || '') + (c.level ? ' · ' + c.level : '') })]),
          el('button', { class: 'lt-btn lt-btn-sm', text: _ltT('Lier', 'Link'), onclick: () => _ltSave({ link_contact_id: c.id }) }),
        ])));
      }).catch(() => { clear(results); results.appendChild(el('div', { class: 'lt-state', text: _ltT('Erreur.', 'Error.') })); });
    });
    wrap.appendChild(el('div', { class: 'lt-relink-row' }, [inp, btn]));
    wrap.appendChild(results);
    return wrap;
  }

  // The sales agent's half of the drawer: the client's report, nothing else.
  // Ayoub, 2026-09-06: "ahamm 7aja team, blach teacher — ma-3ndhomch contact
  // m3a l'client ila f Zoom." The teacher decides the level; the agent is who
  // the client actually hears from, so the agent is who sends it.
  function _ltAgentReportBlock(s) {
    const wrap = el('div', { class: 'lt-form lt-agent-report' });
    wrap.appendChild(el('div', { class: 'lt-flabel', text: _ltT('Rapport du client', 'Client report') }));
    const msg = el('div', { class: 'lt-formmsg' });
    if (!s.finalized_at && s.review_status !== 'reviewed') {
      wrap.appendChild(el('p', {
        class: 'lt-formhint',
        text: _ltT('Le professeur n’a pas encore finalisé le niveau. Le rapport sera disponible ensuite.',
                   'The teacher has not finalized the level yet. The report becomes available after that.'),
      }));
      return wrap;
    }
    const dl = el('button', { class: 'lt-btn lt-btn-ghost', type: 'button' }, [lucideIcon('file-down'), el('span', { text: _ltT('Rapport PDF', 'PDF report') })]);
    dl.addEventListener('click', () => _ltDownloadReport(s, dl, msg));
    const send = el('button', { class: 'lt-btn lt-btn-wa', type: 'button' }, [lucideIcon('file-up'), el('span', { text: _ltT('Envoyer au client', 'Send to the client') })]);
    send.addEventListener('click', () => {
      if (!confirm(_ltT('Envoyer le rapport PDF au client sur WhatsApp ?', 'Send the PDF report to the client on WhatsApp?'))) return;
      send.disabled = true;
      _ltSendReport(s.id, msg).finally(() => { send.disabled = false; });
    });
    wrap.appendChild(el('div', { class: 'lt-actions lt-review-actions' }, [send, dl]));
    wrap.appendChild(msg);
    return wrap;
  }

  function _ltReviewForm(s) {
    const form = el('section', { class: 'lt-form lt-review-card', 'aria-label': _ltT('Décision du professeur', 'Teacher decision') });
    form.appendChild(el('div', { class: 'lt-card-title lt-title-with-icon' }, [lucideIcon('graduation-cap'), el('span', { text: _ltT('Décision du professeur', 'Teacher decision') })]));
    form.appendChild(el('p', { class: 'lt-card-subtitle', text: _ltT('Validez le niveau final qui sera envoyé vers HubSpot.', 'Confirm the final level that will be sent to HubSpot.') }));

    let selLevel = s.reviewer_level || '';
    const picker = el('div', { class: 'lt-levelpick' });
    const c2Panel = el('div', { class: 'lt-c2-panel' });
    ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'].forEach(lv => {
      const b = el('button', { class: 'lt-lvlbtn ' + _ltLevelClass(lv) + (lv === selLevel ? ' on' : ''), type: 'button', text: lv, 'aria-pressed': lv === selLevel ? 'true' : 'false' });
      b.addEventListener('click', () => {
        selLevel = (selLevel === lv ? '' : lv);
        picker.querySelectorAll('.lt-lvlbtn').forEach(x => { x.classList.remove('on'); x.setAttribute('aria-pressed', 'false'); });
        if (selLevel) { b.classList.add('on'); b.setAttribute('aria-pressed', 'true'); }
        c2Panel.classList.toggle('is-visible', selLevel === 'C2');
      });
      picker.appendChild(b);
    });
    form.appendChild(el('label', { class: 'lt-flabel', text: _ltT('Niveau final (CEFR)', 'Final level (CEFR)') }));
    form.appendChild(picker);

    const existingEvidence = s.extended_moderated_evidence && typeof s.extended_moderated_evidence === 'object' ? s.extended_moderated_evidence : {};
    const c2Task = el('input', { class: 'lt-input', type: 'text', placeholder: _ltT('Référence de la tâche modérée', 'Moderated task reference'), value: existingEvidence.taskReference || '' });
    const c2Duration = el('input', { class: 'lt-input', type: 'number', min: '30', max: '240', step: '1', placeholder: _ltT('Durée (min. 30)', 'Duration (min. 30)'), value: existingEvidence.durationMinutes || '' });
    const c2Moderator = el('input', { class: 'lt-input', type: 'text', placeholder: _ltT('Identité du modérateur distinct', 'Distinct moderator identity'), value: existingEvidence.moderatorIdentity || '' });
    const c2Notes = el('textarea', { class: 'lt-textarea', rows: '2', placeholder: _ltT('Notes de preuve détaillées (40 caractères minimum)', 'Detailed evidence notes (40 characters minimum)') });
    c2Notes.value = existingEvidence.notes || '';
    const c2Attachment = el('input', { class: 'lt-input', type: 'text', placeholder: _ltT('Référence de pièce jointe', 'Attachment reference'), value: existingEvidence.attachmentRef || '' });
    c2Panel.appendChild(el('div', { class: 'lt-c2-head' }, [lucideIcon('shield-check'), el('span', { text: _ltT('Preuve structurée C2', 'Structured C2 evidence') })]));
    c2Panel.append(c2Task, c2Duration, c2Moderator, c2Notes, c2Attachment);
    c2Panel.classList.toggle('is-visible', selLevel === 'C2');
    form.appendChild(c2Panel);

    const notes = el('textarea', { class: 'lt-textarea', placeholder: _ltT('Notes internes (optionnel)…', 'Internal notes (optional)…'), rows: '2' });
    notes.value = s.reviewer_notes || '';
    form.appendChild(el('label', { class: 'lt-flabel', text: _ltT('Notes du professeur', 'Teacher notes') }));
    form.appendChild(notes);

    const msg = el('div', { class: 'lt-formmsg' });
    const saveBtn = el('button', { class: 'lt-btn lt-btn-ghost', type: 'button' }, [lucideIcon('save'), el('span', { text: _ltT('Enregistrer brouillon', 'Save draft') })]);
    saveBtn.addEventListener('click', () => {
      saveBtn.disabled = true;
      _ltSave({ review_status: s.review_status || 'pending', reviewer_notes: notes.value }, msg).finally(() => { saveBtn.disabled = false; });
    });
    const finalizeBtn = el('button', { class: 'lt-btn lt-btn-primary lt-finalize-btn', type: 'button' }, [lucideIcon(s.finalized_at ? 'circle-check-big' : 'badge-check'), el('span', { text: s.finalized_at ? _ltT('Niveau finalisé', 'Level finalized') : _ltT('Finaliser le niveau', 'Finalize level') })]);
    const mayFinalize = String(state.me?.role || '').toLowerCase() === 'teacher';
    finalizeBtn.hidden = !mayFinalize;
    finalizeBtn.disabled = !mayFinalize || !!s.finalized_at;
    finalizeBtn.addEventListener('click', () => {
      if (!selLevel) {
        msg.textContent = _ltT('Choisissez d’abord le niveau final.', 'Choose the final level first.');
        msg.className = 'lt-formmsg err';
        return;
      }
      finalizeBtn.disabled = true;
      _ltSave({
        finalize: true, review_status: 'reviewed', reviewer_level: selLevel, reviewer_notes: notes.value,
        extended_evidence: { taskReference:c2Task.value, durationMinutes:Number(c2Duration.value), moderatorIdentity:c2Moderator.value, notes:c2Notes.value, attachmentRef:c2Attachment.value }
      }, msg).finally(() => { finalizeBtn.disabled = !!s.finalized_at; });
    });
    const mabroukBtn = el('button', { class: 'lt-btn lt-btn-wa', type: 'button', title: _ltT('Envoyer le message Mabrouk (niveau)', 'Send the Mabrouk (level) message') }, [lucideIcon('send'), el('span', { text: _ltT('Envoyer le résultat', 'Send result') })]);
    // Anything that reaches the client is the sales team's, never a teacher's:
    // teachers have no relationship with the client outside the Zoom room, and
    // the server refuses them on these routes. Hiding it keeps them from
    // clicking into a 403.
    const maySendToClient = _isSalesAdmin() || String(state.me?.role || '').toLowerCase() === 'agent';
    mabroukBtn.hidden = !maySendToClient || (!s.finalized_at && s.review_status !== 'reviewed');
    mabroukBtn.addEventListener('click', () => {
      mabroukBtn.disabled = true;
      _ltMabrouk(s.id, msg).finally(() => { mabroukBtn.disabled = false; });
    });
    // The student's own copy of the result. Always available — before
    // finalization it previews the AI level, after it carries the teacher's.
    // Rendered in the learner's own locale unless the server is told otherwise.
    const reportBtn = el('button', { class: 'lt-btn lt-btn-ghost', type: 'button', title: _ltT('Télécharger le rapport PDF du student', 'Download the student PDF report') }, [lucideIcon('file-down'), el('span', { text: _ltT('Rapport PDF', 'PDF report') })]);
    reportBtn.addEventListener('click', () => _ltDownloadReport(s, reportBtn, msg));
    // Sending the PDF to the learner needs a level a teacher stands behind —
    // same gate as the Mabrouk message. Downloading stays open as a preview.
    const sendReportBtn = el('button', { class: 'lt-btn lt-btn-wa', type: 'button', title: _ltT('Envoyer le rapport PDF au student sur WhatsApp', 'Send the PDF report to the student on WhatsApp') }, [lucideIcon('file-up'), el('span', { text: _ltT('Envoyer le rapport', 'Send the report') })]);
    sendReportBtn.hidden = !maySendToClient || (!s.finalized_at && s.review_status !== 'reviewed');
    sendReportBtn.addEventListener('click', () => {
      if (!confirm(_ltT('Envoyer le rapport PDF au student sur WhatsApp ?', 'Send the PDF report to the student on WhatsApp?'))) return;
      sendReportBtn.disabled = true;
      _ltSendReport(s.id, msg).finally(() => { sendReportBtn.disabled = false; });
    });
    form.appendChild(el('div', { class: 'lt-actions lt-review-actions' }, [finalizeBtn, saveBtn, mabroukBtn, sendReportBtn, reportBtn]));
    // Delivery is OPT-IN. Finalizing settles the level and stops there; the
    // agent decides, per student, whether the client receives the document.
    // Until they do, this panel offers the choice rather than reporting on an
    // automatic send that no longer happens.
    if (s.finalized_at) {
      const deliveryInfo = el('p', { class: 'lt-formhint', text: _ltT('Vérification du statut d’envoi…', 'Checking delivery status…') });
      const queueDelivery = el('button', { class: 'lt-btn lt-btn-primary', type: 'button' }, [lucideIcon('send-horizontal'), el('span', { text: _ltT('Envoyer le rapport au client', 'Send the report to the client') })]);
      const retryDelivery = el('button', { class: 'lt-btn', type: 'button', text: _ltT('Réessayer l’envoi', 'Retry the delivery') });
      queueDelivery.hidden = true;
      retryDelivery.hidden = true;
      form.appendChild(deliveryInfo);
      form.appendChild(el('div', { class: 'lt-actions' }, [queueDelivery, retryDelivery]));
      const refreshDelivery = () => _ltJson('/api/level-tests/' + encodeURIComponent(s.id) + '/report/delivery', 'GET').then(j => {
        const d = j.delivery || {};
        const labels = {
          pending: _ltT('Rapport PDF + template en attente d’envoi.', 'PDF + template queued for delivery.'),
          preparing: _ltT('Préparation du rapport PDF…', 'Preparing the PDF report…'),
          sending: _ltT('Envoi du rapport à WhatsApp…', 'Submitting the report to WhatsApp…'),
          accepted: _ltT('Rapport PDF + template accepté par WhatsApp.', 'PDF + template accepted by WhatsApp.'),
          failed: _ltT('Préparation échouée. Corrigez la cause puis réessayez.', 'Preparation failed. Fix the cause, then retry.'),
          uncertain: _ltT('Envoi à vérifier dans WhatsApp avant toute nouvelle tentative.', 'Check the send in WhatsApp before any new attempt.'),
        };
        // No row = nothing was ever queued for this student. That is the normal
        // state now, not a gap — offer the send instead of explaining an absence.
        deliveryInfo.textContent = (labels[d.status]
          || _ltT('Le rapport n’a pas encore été envoyé au client.', 'The report has not been sent to the client yet.'))
          + (d.last_error ? ' ' + d.last_error : '');
        queueDelivery.hidden = !maySendToClient || !!d.status;
        retryDelivery.hidden = d.status !== 'failed';
        // Once a queued delivery is in flight, the two manual sends would
        // duplicate it — hold them until it settles.
        const inFlight = ['pending', 'preparing', 'sending'].includes(d.status);
        sendReportBtn.disabled = mabroukBtn.disabled = inFlight;
        if (inFlight) setTimeout(() => { if (form.isConnected) refreshDelivery(); }, 30000);
        refreshIcons();
      }).catch(() => { deliveryInfo.textContent = _ltT('Statut d’envoi indisponible. Rouvrez le test pour vérifier.', 'Delivery status unavailable. Reopen the test to check.'); });
      queueDelivery.addEventListener('click', () => {
        if (!confirm(_ltT('Envoyer le rapport PDF au client sur WhatsApp ?', 'Send the PDF report to the client on WhatsApp?'))) return;
        queueDelivery.disabled = true;
        _ltJson('/api/level-tests/' + encodeURIComponent(s.id) + '/report/delivery/queue', 'POST', {})
          .then(refreshDelivery).catch(err => { deliveryInfo.textContent = err.message; })
          .finally(() => { queueDelivery.disabled = false; });
      });
      retryDelivery.addEventListener('click', () => {
        retryDelivery.disabled = true;
        _ltJson('/api/level-tests/' + encodeURIComponent(s.id) + '/report/delivery/retry', 'POST', {})
          .then(refreshDelivery).catch(err => { deliveryInfo.textContent = err.message; })
          .finally(() => { retryDelivery.disabled = false; });
      });
      refreshDelivery();
    }

    // Both buttons above are conditionally hidden. Say WHY the panel is short,
    // otherwise an admin or super sales opening a pending test sees only "Save
    // draft" and has no way to know the decision is a teacher's to make.
    if (!mayFinalize) {
      form.appendChild(el('p', {
        class: 'lt-formhint',
        text: _ltT('Seul un professeur peut finaliser le niveau et envoyer le résultat.',
                   'Only a teacher can finalize the level and send the result.'),
      }));
    } else if (!s.finalized_at) {
      form.appendChild(el('p', {
        class: 'lt-formhint',
        text: _ltT('Finalisez le niveau pour pouvoir envoyer le résultat au client.',
                   'Finalize the level to unlock sending the result to the client.'),
      }));
    }
    form.appendChild(msg);
    if (s.finalized_at) form.appendChild(el('div', { class: 'lt-saved-meta', text: _ltT('Finalisé le ', 'Finalized on ') + _ltFmtDate(s.finalized_at) }));
    return form;
  }

  function _ltRenderDrawer(s) {
    const panel = document.getElementById('ltDrawerPanel');
    if (!panel) return;
    clear(panel);
    const name = s.hubspot_name || s.name || s.phone || '—';
    const itemIndex = LT.items.findIndex(item => String(item.id) === String(s.id));
    const totalItems = LT.items.length;
    const initials = String(name).split(/\s+/).filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase() || 'LT';
    panel.setAttribute('aria-labelledby', 'ltReviewTitle');
    panel.appendChild(el('header', { class: 'lt-dh' }, [
      el('div', { class: 'lt-dh-id' }, [
        el('span', { class: 'lt-review-avatar', text: initials }),
        el('div', {}, [
          el('div', { class: 'lt-dh-name', id: 'ltReviewTitle', text: name }),
          el('div', {
            class: 'lt-dh-sub',
            text: (s.phone || '') + (s.locale ? ' · ' + String(s.locale).toUpperCase() : '') + (s.agent_name ? ' · ' + _ltT('par ', 'by ') + s.agent_name : '') + ' · ' + _ltT('durée totale ', 'total time ') + _ltFmtDuration(s.duration_seconds, true),
          }),
        ]),
      ]),
      el('div', { class: 'lt-dh-status' }, [_ltStatusBadge(s.review_status)]),
      el('div', { class: 'lt-dh-nav' }, [
        el('button', { class: 'lt-dh-navbtn', type: 'button', title: _ltT('Test précédent', 'Previous test'), disabled: itemIndex <= 0 ? 'disabled' : null, onclick: () => { if (itemIndex > 0) openLtDrawer(LT.items[itemIndex - 1].id); } }, [lucideIcon('chevron-left')]),
        el('span', { text: totalItems ? (itemIndex + 1) + ' / ' + totalItems : '—' }),
        el('button', { class: 'lt-dh-navbtn', type: 'button', title: _ltT('Test suivant', 'Next test'), disabled: itemIndex < 0 || itemIndex >= totalItems - 1 ? 'disabled' : null, onclick: () => { if (itemIndex >= 0 && itemIndex < totalItems - 1) openLtDrawer(LT.items[itemIndex + 1].id); } }, [lucideIcon('chevron-right')]),
        el('span', { class: 'lt-dh-divider', 'aria-hidden': 'true' }),
        el('button', { class: 'lt-dh-close', type: 'button', title: _ltT('Fermer', 'Close'), onclick: closeLtDrawer }, [lucideIcon('x')]),
      ]),
    ]));

    const bodyWrap = el('div', { class: 'lt-dbody' });
    panel.appendChild(bodyWrap);
    const conf = (s.confidence != null) ? Math.round(Number(s.confidence) * 100) + '%' : '';
    const ps = s.per_skill || {};
    const order = ['speaking', 'writing', 'listening', 'reading', 'grammar'];
    const labels = { speaking: _ltT('Oral', 'Speaking'), writing: _ltT('Écrit', 'Writing'), listening: _ltT('Écoute', 'Listening'), reading: _ltT('Lecture', 'Reading'), grammar: _ltT('Grammaire', 'Grammar') };
    const summary = el('section', { class: 'lt-command-summary', 'aria-label': _ltT('Résumé du niveau', 'Level summary') });
    summary.appendChild(el('div', { class: 'lt-summary-levels' }, [
      el('div', { class: 'lt-summary-level' }, [el('span', { text: _ltT('Niveau actuel', 'Current level') }), el('strong', { text: s.current_level || _ltT('Non évalué', 'Not assessed') })]),
      el('span', { class: 'lt-summary-arrow' }, [lucideIcon('arrow-right')]),
      el('div', { class: 'lt-summary-level lt-summary-level-ai' }, [el('span', { text: _ltT('Recommandation IA', 'AI recommendation') }), el('strong', { text: s.overall_level || '—' }), conf ? el('small', { text: _ltT('Confiance ', 'Confidence ') + conf }) : null]),
      el('span', { class: 'lt-summary-arrow' }, [lucideIcon('arrow-right')]),
      el('div', { class: 'lt-summary-level lt-summary-level-final' }, [el('span', { text: _ltT('Décision finale', 'Final decision') }), el('strong', { text: s.reviewer_level || '—' })]),
    ]));
    const skillGrid = el('div', { class: 'lt-skills lt-command-skills' });
    order.forEach(k => {
      if (!ps[k]) return;
      skillGrid.appendChild(el('div', { class: 'lt-skill' }, [
        el('div', { class: 'lt-skill-name', text: labels[k] }),
        el('div', { class: 'lt-skill-score' }, [el('b', { text: String(ps[k].score) }), el('span', { class: 'lt-skill-100', text: '/100' })]),
        _ltPill(ps[k].band),
      ]));
    });
    summary.appendChild(skillGrid);
    bodyWrap.appendChild(summary);

    const commandGrid = el('div', { class: 'lt-review-grid' });
    const main = el('main', { class: 'lt-review-main' });
    const body = el('aside', { class: 'lt-review-rail' });
    main.appendChild(_ltEvidenceBlock(s));

    const spk = (s.speaking_answers || []).filter(a => a && (a.transcript || a.audio_key || a.subscores));
    const wrt = (s.writing_answers || []).filter(a => a && (a.text || a.score != null));
    if (spk.length || wrt.length) {
      const responseCard = el('section', { class: 'lt-response-card' });
      responseCard.appendChild(el('div', { class: 'lt-card-title', text: _ltT('Preuves ouvertes', 'Open-response evidence') }));
      if (spk.length) {
        const speakingWrap = el('details', { class: 'lt-response-group', open: 'open' });
        speakingWrap.appendChild(el('summary', {}, [lucideIcon('mic'), el('span', { text: _ltT('Preuve orale', 'Speaking evidence') }), el('b', { text: String(spk.length) })]));
        spk.forEach(a => speakingWrap.appendChild(_ltSpeakingCard(a)));
        responseCard.appendChild(speakingWrap);
      }
      if (wrt.length) {
        const writingWrap = el('details', { class: 'lt-response-group' });
        writingWrap.appendChild(el('summary', {}, [lucideIcon('pen-line'), el('span', { text: _ltT('Preuve écrite', 'Writing evidence') }), el('b', { text: String(wrt.length) })]));
        wrt.forEach(a => writingWrap.appendChild(_ltWritingCard(a)));
        responseCard.appendChild(writingWrap);
      }
      main.appendChild(responseCard);
    }

    if (s.summary_feedback || (s.strengths || []).length || (s.improvements || []).length) {
      const sum = el('section', { class: 'lt-summary lt-ai-card' });
      sum.appendChild(el('div', { class: 'lt-card-title lt-title-with-icon' }, [lucideIcon('sparkles'), el('span', { text: _ltT('Synthèse IA', 'AI summary') })]));
      if (s.summary_feedback) sum.appendChild(el('p', { class: 'lt-sum-fb', text: s.summary_feedback }));
      if ((s.strengths || []).length) sum.appendChild(_ltBullets(_ltT('Points forts', 'Strengths'), s.strengths, 'pos'));
      if ((s.improvements || []).length) sum.appendChild(_ltBullets(_ltT('À améliorer', 'To improve'), s.improvements, 'neg'));
      if (s.recommended_program) sum.appendChild(el('div', { class: 'lt-prog' }, [lucideIcon('sparkles'), el('span', { text: s.recommended_program })]));
      body.appendChild(sum);
    }

    if (_ltCanManage()) body.appendChild(_ltReviewForm(s));
    if (_ltCanManage()) body.appendChild(_ltLinkBlock(s));
    else {
      body.appendChild(el('div', { class: 'lt-agent-readonly' }, [lucideIcon('shield-check'), el('span', { text: 'Soumission reçue — la validation finale reste avec l’équipe pédagogique.' })]));
      // The agent owns the client conversation, so once a teacher has settled
      // the level the agent is who hands the report over. They get the report
      // actions WITHOUT the review form: they must not touch the level.
      body.appendChild(_ltAgentReportBlock(s));
    }
    commandGrid.append(main, body);
    bodyWrap.appendChild(commandGrid);
    refreshIcons(panel);
    bodyWrap.scrollTop = 0;
    panel.querySelector('.lt-dh-close')?.focus({ preventScroll: true });
  }

  function _ltSave(patch, msgEl) {
    if (!LT.current) return Promise.resolve();
    const id = LT.current.id;
    return _ltJson('/api/level-tests/' + encodeURIComponent(id), 'PATCH', patch).then(j => {
      if (j.submission && !j.submission.question_evidence) j.submission.question_evidence = LT.current.question_evidence || [];
      LT.current = j.submission;
      if (msgEl) {
        let m = _ltT('Enregistré.', 'Saved.');
        if (j.hubspotPushed === true) m += _ltT(' Niveau poussé vers HubSpot ✓', ' Level pushed to HubSpot ✓');
        else if (j.hubspotPushed === false) m += _ltT(' (échec HubSpot)', ' (HubSpot failed)');
        msgEl.textContent = m; msgEl.className = 'lt-formmsg ok';
      }
      _ltRenderDrawer(j.submission);
      loadLevelTests();
      toast(_ltT('Enregistré', 'Saved'), 'success');
    }).catch(err => {
      if (msgEl) { msgEl.textContent = err.message || 'Erreur'; msgEl.className = 'lt-formmsg err'; }
      toast(err.message || 'Erreur', 'error');
    });
  }

  function _ltMabrouk(id, msgEl) {
    return _ltJson('/api/level-tests/' + encodeURIComponent(id) + '/mabrouk', 'POST', {}).then(j => {
      if (msgEl) { msgEl.textContent = _ltT('Message Mabrouk envoyé ✓', 'Mabrouk message sent ✓'); msgEl.className = 'lt-formmsg ok'; }
      if (j.submission) { LT.current = j.submission; _ltRenderDrawer(j.submission); loadLevelTests(); }
      toast(_ltT('Mabrouk envoyé', 'Mabrouk sent'), 'success');
    }).catch(err => {
      if (msgEl) { msgEl.textContent = err.message || 'Erreur'; msgEl.className = 'lt-formmsg err'; }
      toast(err.message || 'Erreur', 'error');
    });
  }

  // Download the student's PDF report. Not _ltJson: the response is a PDF
  // stream, and the generous timeout is because a cache miss goes out to the
  // renderer. The language is left to the server, which picks the learner's
  // own locale from the submission.
  async function _ltDownloadReport(s, btn, msgEl) {
    const id = typeof s === 'string' ? s : s?.id;
    if (!id) return;
    const old = btn ? btn.innerHTML : null;
    if (btn) { btn.disabled = true; btn.textContent = '…'; }
    if (msgEl) { msgEl.textContent = _ltT('Préparation du rapport…', 'Preparing the report…'); msgEl.className = 'lt-formmsg'; }
    try {
      const r = await api('/api/level-tests/' + encodeURIComponent(id) + '/report', { timeoutMs: 130000 });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || ('HTTP ' + r.status));
      }
      const blob = await r.blob();
      const match = (r.headers.get('Content-Disposition') || '').match(/filename="?([^";]+)"?/i);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = match?.[1] || 'lougha-level-report.pdf'; link.style.display = 'none';
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if (msgEl) { msgEl.textContent = _ltT('Rapport téléchargé ✓', 'Report downloaded ✓'); msgEl.className = 'lt-formmsg ok'; }
      toast(_ltT('Rapport PDF téléchargé', 'PDF report downloaded'), 'success');
    } catch (err) {
      if (msgEl) { msgEl.textContent = err.message || 'Erreur'; msgEl.className = 'lt-formmsg err'; }
      toast(err.message || 'Erreur', 'error');
    } finally {
      if (btn) { btn.disabled = false; if (old !== null) btn.innerHTML = old; }
      if (window.lucide?.createIcons) { try { window.lucide.createIcons(); } catch {} }
    }
  }

  function _ltSendReport(id, msgEl) {
    if (msgEl) { msgEl.textContent = _ltT('Envoi du rapport…', 'Sending the report…'); msgEl.className = 'lt-formmsg'; }
    // Generous timeout: a cache miss renders the PDF before the send.
    return _ltJson('/api/level-tests/' + encodeURIComponent(id) + '/report/send', 'POST', {}, { timeoutMs: 130000 }).then(() => {
      if (msgEl) { msgEl.textContent = _ltT('Rapport envoyé au student ✓', 'Report sent to the student ✓'); msgEl.className = 'lt-formmsg ok'; }
      toast(_ltT('Rapport envoyé', 'Report sent'), 'success');
    }).catch(err => {
      if (msgEl) { msgEl.textContent = err.message || 'Erreur'; msgEl.className = 'lt-formmsg err'; }
      toast(err.message || 'Erreur', 'error');
    });
  }

  function _ltOpenGenModal() {
    const overlay = el('div', { class: 'lt-modal-overlay', onclick: (e) => { if (e.target === overlay) overlay.remove(); } });
    const box = el('div', { class: 'lt-modal' });
    box.appendChild(el('div', { class: 'lt-modal-head' }, [
      el('div', { class: 'lt-modal-title', text: _ltT('Générer un lien de test', 'Generate a test link') }),
      el('button', { class: 'lt-dh-close', onclick: () => overlay.remove() }, [lucideIcon('x')]),
    ]));
    const bodyEl = el('div', { class: 'lt-modal-body' });
    box.appendChild(bodyEl);

    const canAssign = (typeof _isSalesAdmin === 'function') ? _isSalesAdmin() : false;
    let agentSel = null;
    if (canAssign) {
      agentSel = el('select', { class: 'lt-input' });
      agentSel.appendChild(el('option', { value: '', text: _ltT('— Moi-même —', '— Myself —') }));
      bodyEl.appendChild(el('label', { class: 'lt-flabel', text: _ltT('Agent (équipe)', 'Agent (team)') }));
      bodyEl.appendChild(agentSel);
      api('/api/users/assignable').then(r => r.ok ? r.json() : { users: [] }).then(j => {
        (j.users || []).filter(u => ['agent', 'super_sales', 'admin', 'super_admin'].includes(String(u.role || '').toLowerCase()))
          .forEach(u => agentSel.appendChild(el('option', { value: u.id, text: u.displayName + ' (' + u.role + ')' })));
      }).catch(() => {});
    }
    const phoneInput = el('input', { class: 'lt-input', type: 'tel', placeholder: _ltT('Ex. 0612345678', 'E.g. 0612345678') });
    const nameInput = el('input', { class: 'lt-input', type: 'text', placeholder: _ltT('Nom (optionnel)', 'Name (optional)') });
    bodyEl.appendChild(el('label', { class: 'lt-flabel', text: _ltT('Téléphone du prospect (optionnel — pré-rempli + lié)', 'Prospect phone (optional — pre-filled + linked)') }));
    bodyEl.appendChild(phoneInput);
    bodyEl.appendChild(el('label', { class: 'lt-flabel', text: _ltT('Nom (optionnel)', 'Name (optional)') }));
    bodyEl.appendChild(nameInput);

    const result = el('div', { class: 'lt-genresult' });
    const genBtn = el('button', { class: 'lt-btn lt-btn-primary', text: _ltT('Générer le lien', 'Generate the link') });
    genBtn.addEventListener('click', () => {
      genBtn.disabled = true;
      const payload = { phone: phoneInput.value.trim() || undefined, name: nameInput.value.trim() || undefined };
      if (agentSel && agentSel.value) payload.agentId = agentSel.value;
      _ltJson('/api/level-tests/links', 'POST', payload).then(j => {
        clear(result);
        const urlInput = el('input', { class: 'lt-input', readonly: 'readonly', value: j.url });
        const copyBtn = el('button', { class: 'lt-btn lt-btn-ghost lt-btn-sm', text: _ltT('Copier', 'Copy') });
        copyBtn.addEventListener('click', () => {
          urlInput.select();
          if (navigator.clipboard) navigator.clipboard.writeText(j.url).then(() => toast(_ltT('Lien copié', 'Link copied'), 'success')).catch(() => {});
          else { try { document.execCommand('copy'); toast(_ltT('Lien copié', 'Link copied'), 'success'); } catch {} }
        });
        result.appendChild(el('div', { class: 'lt-genurl-row' }, [urlInput, copyBtn]));
        result.appendChild(el('div', { class: 'lt-muted', text: _ltT('Assigné à ', 'Assigned to ') + (j.agentName || '?') + _ltT(' · envoyez ce lien au prospect.', ' · send this link to the prospect.') }));
        genBtn.disabled = false;
      }).catch(err => { toast(err.message || 'Erreur', 'error'); genBtn.disabled = false; });
    });
    bodyEl.appendChild(el('div', { class: 'lt-modal-actions' }, [genBtn]));
    bodyEl.appendChild(result);

    overlay.appendChild(box);
    document.body.appendChild(overlay);
    refreshIcons(overlay);
  }

  // ===== Book class (mirrored from support2; data proxied via /api/find-group*) =====
  state.findGroup = { student: '', explicit: null, groupQuery: '', groupSearchData: null, groupSearchLoading: false, groupSearchError: null, groupSearchSeq: 0, mode: 'analysis', data: null, filters: { level: 'same', day: '', time: 'any', teacher: '', timeLeft: 'any', hasRoom: true }, loading: false, error: null, bookedIds: new Set(), bookCtx: null };
  function _fgApplyMode(mode) {
    const st = state.findGroup;
    if (st.mode !== mode) {
      st.mode = mode; st.data = null; st.student = ''; st.explicit = null; st.groupQuery = '';
      st.groupSearchData = null; st.groupSearchLoading = false; st.groupSearchError = null; st.groupSearchSeq += 1;
      const si = document.getElementById('fgStudentSearch'); if (si) si.value = '';
    }
    const book = mode === 'book';
    const t = document.getElementById('fgTitleText'); if (t) t.textContent = book ? 'Book class' : 'Find a group';
    const sub = document.getElementById('fgSubtitle'); if (sub) sub.textContent = book
      ? "9elleb 3la student (wla zid new student) → khtar group + date → student kayt-wssel b students7 access, teacher b notice."
      : "Student bgha y-bdel group? 9elleb 3lih, chof fin wsel (level + program), o jbed lih groups 9eriba li y9der y-dkhol lihom.";
    document.getElementById('fgNewStudentBtn')?.classList.toggle('hidden', !book);
    document.getElementById('fgBookingsToggle')?.classList.toggle('hidden', !book);
    document.getElementById('fgNewStudentForm')?.classList.add('hidden');
    document.getElementById('fgBookingsPanel')?.classList.add('hidden');
    renderFindGroup();
    if (book && !st.data && !st.loading) loadFindGroup();   // browse the pool by filters immediately
  }
  let _findGroupInitDone = false;
  function _findGroupInitOnce() {
    if (_findGroupInitDone) return;
    _findGroupInitDone = true;
    const input = document.getElementById('fgStudentSearch');
    const suggestBox = document.getElementById('fgSuggest');
    let _sugTimer = null, _groupSearchTimer = null, _sugItems = [], _sugActive = -1, _sugSeq = 0;
    const closeSuggest = () => { suggestBox?.classList.add('hidden'); input?.setAttribute('aria-expanded', 'false'); _sugActive = -1; };
    const go = (q) => {
      q = (q != null ? q : (input?.value || '')).trim();
      clearTimeout(_sugTimer);
      clearTimeout(_groupSearchTimer);
      _sugSeq += 1;
      closeSuggest();
      const st = state.findGroup;
      if (q && st.mode === 'book' && _fgLooksLikeGroupCodeQuery(q)) {
        // A group code stays a group lookup even when the agent presses Enter
        // or clicks Find. Never resolve C4/LC/LG codes as HubSpot students.
        st.student = '';
        st.explicit = null;
        st.groupQuery = q;
        _fgEnsureGroupSearchPool();
        renderFindGroup();
        return;
      }
      const hadGroupQuery = !!st.groupQuery;
      st.groupQuery = '';
      if (q) {
        // Enter / Find keeps its original meaning: resolve a student by
        // name/phone. Picking a student suggestion also lands here.
        st.explicit = null;
        st.student = q;
        loadFindGroup();
      } else if (hadGroupQuery) {
        renderFindGroup();
      }
    };
    const renderSuggest = () => {
      if (!suggestBox) return;
      if (!_sugItems.length) { closeSuggest(); return; }
      suggestBox.replaceChildren();
      _sugItems.forEach((s, i) => {
        const row = el('div', { class: 'fg-suggest-item' + (i === _sugActive ? ' active' : ''), role: 'option' });
        const main = el('div', { class: 'fg-suggest-main' });
        main.appendChild(el('span', { class: 'fg-suggest-name', text: s.name }));
        if (s.level) main.appendChild(el('span', { class: 'fg-suggest-level', text: s.level }));
        row.appendChild(main);
        row.appendChild(el('div', { class: 'fg-suggest-sub', text: [s.group, s.phone].filter(Boolean).join('  ·  ') || '—' }));
        row.addEventListener('mousedown', (e) => { e.preventDefault(); input.value = s.name; go(s.phone || s.name); });
        suggestBox.appendChild(row);
      });
      suggestBox.classList.remove('hidden'); input.setAttribute('aria-expanded', 'true');
    };
    const fetchSuggest = async (q, seq) => {
      try {
        const r = await api('/api/find-group/suggest?q=' + encodeURIComponent(q));
        const j = await r.json();
        if (seq !== _sugSeq || !_fgShouldSuggestStudents(state.findGroup.mode, input?.value || '')) return;
        _sugItems = j.suggestions || [];
        _sugActive = -1;
        renderSuggest();
      } catch { /* ignore */ }
    };
    input?.addEventListener('input', () => {
      const q = (input.value || '').trim();
      const suggestSeq = ++_sugSeq;
      clearTimeout(_sugTimer);
      clearTimeout(_groupSearchTimer);
      // Group browsing searches a cached, unfiltered pool so a direct code or
      // name can still find a class hidden by the visible browse filters.
      // Student autocomplete continues independently below.
      if (state.findGroup.mode === 'book') {
        _groupSearchTimer = setTimeout(() => {
          const st = state.findGroup;
          if (st.mode !== 'book') return;
          const hadStudentContext = !!st.student || !!st.explicit;
          if (hadStudentContext) { st.student = ''; st.explicit = null; }
          if (st.groupQuery === q && !hadStudentContext) return;
          st.groupQuery = q;
          if (q) _fgEnsureGroupSearchPool();
          renderFindGroup();
        }, 120);
      }
      if (!_fgShouldSuggestStudents(state.findGroup.mode, q)) {
        _sugItems = [];
        closeSuggest();
        return;
      }
      _sugTimer = setTimeout(() => fetchSuggest(q, suggestSeq), 200);
    });
    input?.addEventListener('keydown', (e) => {
      const open = suggestBox && !suggestBox.classList.contains('hidden') && _sugItems.length;
      if (e.key === 'ArrowDown' && open) { e.preventDefault(); _sugActive = Math.min(_sugItems.length - 1, _sugActive + 1); renderSuggest(); }
      else if (e.key === 'ArrowUp' && open) { e.preventDefault(); _sugActive = Math.max(0, _sugActive - 1); renderSuggest(); }
      else if (e.key === 'Enter') { e.preventDefault(); if (open && _sugActive >= 0) { const s = _sugItems[_sugActive]; input.value = s.name; go(s.phone || s.name); } else go(); }
      else if (e.key === 'Escape') closeSuggest();
    });
    document.getElementById('fgSearchBtn')?.addEventListener('click', () => go());
    document.addEventListener('click', (e) => { if (suggestBox && !suggestBox.contains(e.target) && e.target !== input) closeSuggest(); });
    const _fgReload = () => { if (state.findGroup.student || state.findGroup.explicit || state.findGroup.mode === 'book') loadFindGroup(); };
    document.getElementById('fgFLevel')?.addEventListener('change', (e) => {
      state.findGroup.filters.level = e.target.value;
      // Level is the first facet: clear downstream schedule choices so the
      // freshly returned Day/Time menus contain only real combinations.
      state.findGroup.filters.day = '';
      state.findGroup.filters.time = 'any';
      _fgReload();
    });
    document.getElementById('fgFDay')?.addEventListener('change', (e) => {
      state.findGroup.filters.day = e.target.value;
      // Time depends on Day. Never carry an incompatible time into a newly
      // selected schedule.
      state.findGroup.filters.time = 'any';
      _fgReload();
    });
    document.getElementById('fgFTime')?.addEventListener('change', (e) => { state.findGroup.filters.time = e.target.value; _fgReload(); });
    document.getElementById('fgFTeacher')?.addEventListener('change', (e) => { state.findGroup.filters.teacher = e.target.value; _fgReload(); });
    document.getElementById('fgFEnds')?.addEventListener('change', (e) => { state.findGroup.filters.timeLeft = e.target.value; _fgReload(); });
    document.getElementById('fgHasRoom')?.addEventListener('change', (e) => { state.findGroup.filters.hasRoom = !!e.target.checked; _fgReload(); });
    document.getElementById('fgBookingsToggle')?.addEventListener('click', _fgToggleBookings);
    document.getElementById('fgNewStudentBtn')?.addEventListener('click', () => document.getElementById('fgNewStudentForm')?.classList.toggle('hidden'));
    document.getElementById('fgNewCancelBtn')?.addEventListener('click', () => document.getElementById('fgNewStudentForm')?.classList.add('hidden'));
    document.getElementById('fgNewFindBtn')?.addEventListener('click', () => {
      const name = (document.getElementById('fgNewName')?.value || '').trim();
      const phone = (document.getElementById('fgNewPhone')?.value || '').replace(/\s/g, '');
      const level = (document.getElementById('fgNewLevel')?.value || '').trim();
      if (!phone || !level) { toast('Khass phone + level l new student.', 'error'); return; }
      state.findGroup.student = ''; state.findGroup.groupQuery = ''; state.findGroup.explicit = { name, phone, level };
      document.getElementById('fgNewStudentForm')?.classList.add('hidden');
      loadFindGroup();
    });
    document.getElementById('fgBookClose')?.addEventListener('click', _fgCloseBookModal);
    document.getElementById('fgBookCancel')?.addEventListener('click', _fgCloseBookModal);
    document.getElementById('fgBookSend')?.addEventListener('click', _fgBookSend);
    document.getElementById('fgBookStuLevel')?.addEventListener('change', _fgBookRefreshPreview);
    document.getElementById('fgBookClassDate')?.addEventListener('change', () => {
      const ctx = state.findGroup.bookCtx;
      if (ctx) ctx.dateChosen = !!(document.getElementById('fgBookClassDate').value);
      _fgBookRefreshPreview();
    });
    for (const which of ['student', 'teacher']) {
      document.getElementById(_FG_MSG_EL[which])?.addEventListener('input', () => {
        const ctx = state.findGroup.bookCtx; if (!ctx) return;
        ctx.dirty = ctx.dirty || {};
        ctx.dirty[which] = true;
        _fgToggleMsgReset(ctx, which);
      });
      document.getElementById(which === 'student' ? 'fgBookStudentReset' : 'fgBookTeacherReset')
        ?.addEventListener('click', () => _fgResetMsg(which));
    }
    // The booked student is almost always already in HubSpot — make the modal's Student field a live
    // HubSpot search (autocomplete) that fills name/phone/level; type manually only if not found.
    (function () {
      const input = document.getElementById('fgBookStuName');
      const box = document.getElementById('fgBookStuSuggest');
      if (!input || !box) return;
      let timer = null, items = [], active = -1, seq = 0;
      const close = () => { box.classList.add('hidden'); active = -1; };
      const render = () => {
        if (!items.length) { close(); return; }
        box.replaceChildren();
        items.forEach((s, i) => {
          const row = el('div', { class: 'fg-suggest-item' + (i === active ? ' active' : ''), role: 'option' });
          const main = el('div', { class: 'fg-suggest-main' });
          main.appendChild(el('span', { class: 'fg-suggest-name', text: s.name }));
          if (s.level) main.appendChild(el('span', { class: 'fg-suggest-level', text: s.level }));
          row.appendChild(main);
          row.appendChild(el('div', { class: 'fg-suggest-sub', text: [s.group, s.phone].filter(Boolean).join('  ·  ') || '—' }));
          row.addEventListener('mousedown', (e) => { e.preventDefault(); _fgBookPickStudent(s); close(); });
          box.appendChild(row);
        });
        box.classList.remove('hidden');
      };
      const fetchS = async (q) => { const my = ++seq; try { const r = await api('/api/find-group/suggest?q=' + encodeURIComponent(q)); const j = await r.json(); if (my !== seq) return; items = j.suggestions || []; active = -1; render(); } catch { } };
      input.addEventListener('input', () => { const ctx = state.findGroup.bookCtx; if (ctx) ctx.studentContactId = null; const q = (input.value || '').trim(); clearTimeout(timer); if (q.length < 2) { items = []; close(); return; } timer = setTimeout(() => fetchS(q), 200); });
      input.addEventListener('keydown', (e) => {
        const open = !box.classList.contains('hidden') && items.length;
        if (e.key === 'ArrowDown' && open) { e.preventDefault(); active = Math.min(items.length - 1, active + 1); render(); }
        else if (e.key === 'ArrowUp' && open) { e.preventDefault(); active = Math.max(0, active - 1); render(); }
        else if (e.key === 'Enter' && open && active >= 0) { e.preventDefault(); _fgBookPickStudent(items[active]); close(); }
        else if (e.key === 'Escape') close();
      });
      input.addEventListener('blur', () => setTimeout(() => { close(); _fgBookRefreshPreview(); }, 160));
    })();
    document.getElementById('fgBookModal')?.addEventListener('click', (e) => { if (e.target.id === 'fgBookModal') _fgCloseBookModal(); });
  }
  function _fgIsGroupBrowseSearch(st) {
    return !!(st && st.mode === 'book' && !st.student && !st.explicit && String(st.groupQuery || '').trim());
  }
  function _fgGroupSearchParams() {
    return { level: 'any', day: '', time: 'any', teacher: '', timeLeft: 'any', hasRoom: '0' };
  }
  function _fgDataForRender(st) {
    return _fgIsGroupBrowseSearch(st) ? (st.groupSearchData || null) : (st.data || null);
  }
  // Book class talks to support2 through a proxy, so its failures are upstream ones
  // (slow pool rebuild, bridge down). Show what happened and what to do about it —
  // the raw parser text ("Unexpected token '<'…") told the team nothing.
  function _fgErrText(code, status, body) {
    const c = String(code || '');
    if (c === 'support2_timeout' || (!c && status === 504)) return "Support2 khda wa9t bezzaf bach yjme3 l'groups. 3awed 9elleb — l'mrra jjaya kat-kon zerba.";
    if (c === 'support2_unreachable' || c === 'support2_bad_response') return "Support2 ma jaweb-ch daba. 3awed men be3d chi chwiya.";
    if (c === 'support2_bridge_not_configured') return "L'bridge m3a support2 machi configuré.";
    if (c === 'sales_only') return "Had l'page ghir l l'équipe dyal sales.";
    if (c === 'student_not_found') return 'Ma l9inach had student f HubSpot.';
    // Booking guards from support2 — each one has a concrete next step.
    // support2 answers stale_level with the group's own level — and with an EMPTY
    // one when the group has no level at all. In that case no value the agent types
    // can pass the guard, so never tell them to fix the field.
    if (c === 'stale_level') {
      const exp = (body && body.expectedLevel) ? String(body.expectedLevel) : '';
      return exp
        ? "L'khana LEVEL khassha tkoun level dyal l'class (" + exp + '), machi dyal student. Sale7-ha o 3awed.'
        : "Had l'group bla level f HubSpot — zid l'level dyal l'group 9bel l'booking (l'khana ma-tsawwebch had chi).";
    }
    if (c === 'stale_group_snapshot') return "L'group tbeddel (prof wla level) men melli 7ellity l'modal. Sedd o 3awed 7ell.";
    if (c === 'stale_or_invalid_occurrence') return "L'7essa li khtariti tbeddlat. Khtar date jdida o 3awed.";
    if (c === 'no_upcoming_occurrences') return "Had l'group ma-3andouch 7essa jaya f 30 youm.";
    if (c === 'invalid_class_date') return "Had l'date machi men l'7essas dyal had l'group.";
    if (c === 'teacher_not_resolved') return "Ma-l9inach l'prof f teachers7 — ma ymken-ch tsift l'notification dyalo.";
    if (c === 'group_not_found') return "Had l'group ma-b9ach kayn f support2.";
    if (c === 'trial_grant_failed') return "Students7 ma-3tach l'accès dyal trial. 3awed men be3d chi chwiya.";
    if (c === 'group_code_missing') return "Had l'group bla code — ma ymken-ch n-3tiw accès l l'app.";
    // An unparsable body (an HTML error page from support2 or an edge in front of
    // it) lands here — say what failed instead of leaking the parser's complaint.
    if (!c) return 'Support2 ma jaweb-ch mezyan (HTTP ' + (status || '?') + "). 3awed jarreb.";
    return c;
  }
  async function _fgEnsureGroupSearchPool() {
    const st = state.findGroup;
    if (!_fgIsGroupBrowseSearch(st) || st.groupSearchData || st.groupSearchLoading) return;
    const seq = (st.groupSearchSeq || 0) + 1;
    st.groupSearchSeq = seq;
    st.groupSearchLoading = true;
    st.groupSearchError = null;
    try {
      const r = await api('/api/find-group?' + new URLSearchParams(_fgGroupSearchParams()).toString());
      const j = await r.json().catch(() => null);
      if (seq !== st.groupSearchSeq) return;
      if (!r.ok || !j) {
        st.groupSearchData = null;
        st.groupSearchError = _fgErrText(j && j.error, r.status);
        return;
      }
      st.groupSearchData = j;
      st.groupSearchError = null;
    } catch (e) {
      if (seq !== st.groupSearchSeq) return;
      console.warn('[Book class] group pool fetch failed:', e);
      st.groupSearchData = null;
      st.groupSearchError = "Ma 9derna-ch njibo l'liste dyal groups. 3awed jarreb.";
    } finally {
      if (seq === st.groupSearchSeq) {
        st.groupSearchLoading = false;
        // A cleared query (or a student Find) keeps showing its untouched
        // browse/student response even when this background request finishes.
        if (_fgIsGroupBrowseSearch(st)) renderFindGroup();
      }
    }
  }
  async function loadFindGroup() {
    const st = state.findGroup;
    if (st.loading) { st.reloadQueued = true; return; }
    if (!st.student && !st.explicit && st.mode !== 'book') return;   // analysis needs a student; book mode browses
    st.loading = true; st.error = null;
    renderFindGroup();
    try {
      // A restored filter can pre-date the current pool. Settle cascading
      // Day/Time options before rendering, with a bounded corrective refetch.
      for (let attempt = 0; attempt < 3; attempt++) {
        const p = { level: st.filters.level, day: st.filters.day || '', time: st.filters.time || 'any', teacher: st.filters.teacher || '', timeLeft: st.filters.timeLeft || 'any', hasRoom: st.filters.hasRoom ? '1' : '0' };
        if (st.explicit) { p.studentName = st.explicit.name || ''; p.studentPhone = st.explicit.phone || ''; p.studentLevel = st.explicit.level || ''; }
        else if (st.student) p.student = st.student;   // else: no student → browse
        const r = await api('/api/find-group?' + new URLSearchParams(p).toString());
        const j = await r.json().catch(() => null);
        if (!r.ok || !j) {
          st.data = null;
          st.error = _fgErrText(j && j.error, r.status);
          break;
        }
        st.data = j;
        st.error = null;
        if (!_fgPopulateOptions(j)) break;
      }
    } catch (e) { console.warn('[Book class] find-group fetch failed:', e); st.data = null; st.error = "Ma 9derna-ch njibo l'groups. 3awed jarreb."; }
    finally {
      st.loading = false;
      renderFindGroup();
      if (st.reloadQueued) { st.reloadQueued = false; loadFindGroup(); }
    }
  }
  function _fgPct(p) { return (p == null) ? '—' : (Math.round(p) + '%'); }
  function _fgMonthsTxt(n) { return (n == null) ? '—' : (n <= 0 ? 'ending now' : ('~' + n + ' mo' + (n === 1 ? '' : ''))); }
  function _fgProgramTxt(o) { return o && o.progressPct != null ? (_fgPct(o.progressPct) + (o.progressTotal ? ' · ' + (o.progressCovered != null ? o.progressCovered : '?') + '/' + o.progressTotal : '')) : '—'; }
  function _fgFmtDate(s) { const ms = Date.parse(String(s || '').slice(0, 10)); if (!ms || isNaN(ms)) return s || '—'; try { return MA.loc(new Date(ms), 'd', 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; } }
  function _fgPackTxt(p) { const t = String(p || '').trim(); if (!t) return '—'; return /^\d+(\.\d+)?$/.test(t) ? t + ' months' : t; }
  function _fgMatchClass(m) { return m >= 75 ? 'fg-match-hi' : m >= 50 ? 'fg-match-mid' : 'fg-match-lo'; }
  function _fgKv(parent, k, v, title, vcls) { const d = el('div', { class: 'fg-kv' }); d.appendChild(el('span', { class: 'fg-k', text: k })); d.appendChild(el('span', { class: 'fg-v' + (vcls ? ' ' + vcls : ''), text: v, title: title || v })); parent.appendChild(d); }
  function _fgReasonTone(r) { return /✓|very close|same day & time|similar end/i.test(r) ? ' fg-reason-pos' : (/⚠|\(want |· far/i.test(r) ? ' fg-reason-warn' : ''); }
  function _fgSkeleton(n) { const g = el('div', { class: 'fg-skel-grid' }); for (let i = 0; i < (n || 6); i++) g.appendChild(el('div', { class: 'fg-skel' })); return g; }
  function _fgProgBar(pct, maxW) { const bar = el('div', { class: 'fg-prog' }); if (maxW) bar.style.maxWidth = maxW; const fill = el('div', { class: 'fg-prog-fill' }); fill.style.width = Math.max(2, Math.min(100, Math.round(pct))) + '%'; bar.appendChild(fill); return bar; }
  function _fgFillSelect(id, items, curVal, anyLabel, anyVal) {
    const sel = document.getElementById(id); if (!sel) return anyVal;
    const list = items || [];
    const sig = list.join('|');
    if (sel._fgSig !== sig) {
      sel._fgSig = sig;
      sel.replaceChildren(el('option', { value: anyVal, text: anyLabel }));
      for (const it of list) sel.appendChild(el('option', { value: it, text: it }));
    }
    const norm = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const valid = curVal === anyVal ? anyVal : (list.find(it => norm(it) === norm(curVal)) || anyVal);
    sel.value = valid;
    return valid;
  }
  function _fgPopulateOptions(d) {
    _fgFillSelect('fgFTeacher', d.teachers, state.findGroup.filters.teacher || '', 'Any teacher', '');
    const st = state.findGroup;
    const day = _fgFillSelect('fgFDay', d.days, st.filters.day || '', 'Any day', '');
    const time = _fgFillSelect('fgFTime', d.times, st.filters.time || 'any', 'Any time', 'any');
    let changed = false;
    if (day !== (st.filters.day || '')) { st.filters.day = day; changed = true; }
    if (time !== (st.filters.time || 'any')) { st.filters.time = time; changed = true; }
    return changed;
  }
  function _fgSearchNorm(value) {
    return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().trim().replace(/\s+/g, ' ');
  }
  function _fgLooksLikeGroupCodeQuery(value) {
    const compact = _fgSearchNorm(value).replace(/[^a-z0-9]+/g, '');
    return /^(?:lc\d*|lg\d*|c4(?:lg|l|g)?\d*)$/.test(compact);
  }
  function _fgShouldSuggestStudents(mode, query) {
    const q = String(query || '').trim();
    return q.length >= 2 && !(mode === 'book' && _fgLooksLikeGroupCodeQuery(q));
  }
  function _fgCanonicalShortCode(query) {
    // The team commonly omits the canonical "L" marker in C4LG codes
    // (C4G7 → C4LG7). Keep this deliberately narrow: a complete C4G+digits
    // query only, never a fuzzy rewrite inside names or other code families.
    const compact = _fgSearchNorm(query).replace(/[^a-z0-9]+/g, '');
    const match = compact.match(/^c4g(\d+)$/);
    return match ? 'c4lg' + match[1] : '';
  }
  function _fgHasExactCodeToken(value, token) {
    if (!token) return false;
    return new RegExp('(?:^|[^a-z0-9])' + token + '(?=$|[^a-z0-9])').test(value);
  }
  function _fgGroupSearchMatch(c, query) {
    const needle = _fgSearchNorm(query);
    if (!needle) return true;
    const compactNeedle = needle.replace(/[^a-z0-9]+/g, '');
    const canonicalShortCode = _fgCanonicalShortCode(needle);
    return [c && c.groupName, c && c.companyName, c && c.code].some((raw) => {
      const value = _fgSearchNorm(raw);
      if (!value) return false;
      return value.includes(needle)
        || (!!compactNeedle && value.replace(/[^a-z0-9]+/g, '').includes(compactNeedle))
        || _fgHasExactCodeToken(value, canonicalShortCode);
    });
  }
  function _fgGroupCard(c, isBest) {
    const tier = c.matchPct == null ? 'fg-card-lo' : (c.matchPct >= 75 ? 'fg-card-hi' : c.matchPct >= 50 ? 'fg-card-mid' : 'fg-card-lo');
    const canOpen = c.hasWaGroup !== false && !String(c.groupId || '').startsWith('hsco:');
    // Quality heatmap class (teacher score) — tints the card (2026-07-06 redesign).
    const qCls = c.teacherScore == null ? '' : (c.teacherScore >= 75 ? ' fg-q-hi' : c.teacherScore >= 50 ? ' fg-q-mid' : ' fg-q-lo');
    const card = el('div', {
      class: 'fg-card' + qCls + ' ' + tier + (isBest ? ' fg-card-best' : '') + (canOpen ? '' : ' fg-card-hubspot-only'),
      'data-id': c.groupId,
      tabindex: canOpen ? '0' : null,
      role: canOpen ? 'button' : null,
      'aria-label': (isBest ? 'Best fit. ' : '') + c.groupName
        + (c.teacherScore != null ? ', teacher score ' + c.teacherScore : '')
        + (c.matchPct != null ? ', ' + c.matchPct + '% match' : '')
        + (canOpen ? ' — open group chat' : ' — HubSpot class without a linked WhatsApp group')
    });
    if (!canOpen) card.style.cursor = 'default';
    if (isBest) card.appendChild(el('div', { class: 'fg-best-badge', text: 'Best fit' }));
    const head = el('div', { class: 'fg-card-head' });
    head.appendChild(el('div', { class: 'fg-card-title', text: c.groupName, title: c.groupName }));
    // Teacher quality (2026-07-06) — Morad's review rating + score, server-provided
    // (proxied from support2's find-group) so agents book the best teacher; list ordered by it.
    if (c.teacherRating) { const _rl = { very_good: '★ Very good', good: '★ Good', average: 'Average', not_good_enough: '⚠ Not good' }; head.appendChild(el('span', { class: 'fg-teacher-rev fg-rev-' + c.teacherRating, text: _rl[c.teacherRating] || c.teacherRating, title: 'Teacher review (Morad)' })); }
    if (c.matchPct != null) head.appendChild(el('div', { class: 'fg-match ' + _fgMatchClass(c.matchPct), text: c.matchPct + '%', title: c.matchPct + '% fit' }));
    if (c.teacherScore != null) {
      const _tsCls = c.teacherScore >= 75 ? 'fg-ts-hi' : c.teacherScore >= 50 ? 'fg-ts-mid' : 'fg-ts-lo';
      const sb = el('div', { class: 'fg-teacher-score ' + _tsCls, title: 'Teacher quality score' });
      sb.appendChild(el('span', { class: 'fg-ts-num', text: String(c.teacherScore) }));
      sb.appendChild(el('span', { class: 'fg-ts-cap', text: 'score' }));
      head.appendChild(sb);
    }
    card.appendChild(head);
    const meta = el('div', { class: 'fg-card-meta' });
    _fgKv(meta, 'Status', c.groupStatus ? (c.groupStatus.charAt(0).toUpperCase() + c.groupStatus.slice(1)) : '—');
    _fgKv(meta, 'Teacher', c.teacher || '—');
    _fgKv(meta, 'Type', c.type || '—');
    _fgKv(meta, 'Level', c.level || '—');
    _fgKv(meta, 'Program', _fgProgramTxt(c), '', 'fg-v-num');
    _fgKv(meta, 'Schedule', (c.scheduleLabel ? (c.daysPerWeek ? c.daysPerWeek + ' days/week · ' : '') + c.scheduleLabel : (c.day || '—') + (c.time ? ' · ' + c.time : '')), '', 'fg-v-wrap');
    _fgKv(meta, 'Started', _fgFmtDate(c.startDate), c.startDate || '', 'fg-v-num');
    _fgKv(meta, 'Time left', _fgMonthsTxt(c.monthsLeft) + (c.endDate ? ' · ' + _fgFmtDate(c.endDate) : ''), c.endDate || '', 'fg-v-num');
    _fgKv(meta, 'Active', (c.activeCount != null ? c.activeCount : '—') + ' / ' + (c.enrolled != null ? c.enrolled : '—'), '', 'fg-v-num');
    card.appendChild(meta);
    if (c.progressPct != null) card.appendChild(_fgProgBar(c.progressPct));
    if (Array.isArray(c.reasons) && c.reasons.length) {
      const r = el('div', { class: 'fg-reasons' });
      for (const reason of c.reasons.slice(0, 6)) r.appendChild(el('span', { class: 'fg-reason' + _fgReasonTone(reason), text: reason }));
      card.appendChild(r);
    }
    const actions = el('div', { class: 'fg-card-actions' });
    if (state.findGroup.mode === 'book') {
      const booked = state.findGroup.bookedIds && state.findGroup.bookedIds.has(c.groupId);
      const bookBtn = el('button', {
        class: 'btn btn-sm fg-book-btn ' + (booked || !canOpen ? 'btn-ghost' : 'btn-primary'),
        type: 'button',
        text: !canOpen ? 'No WA group' : (booked ? 'Booked ✓' : 'Book trial'),
        title: !canOpen ? 'Had class kayna f HubSpot walakin ma merboutach b WhatsApp group.' : null,
      });
      if (booked || !canOpen) bookBtn.disabled = true;
      if (canOpen) bookBtn.addEventListener('click', (e) => { e.stopPropagation(); _fgOpenBookModal(c); });
      actions.appendChild(bookBtn);
    } else { actions.appendChild(el('span')); }
    actions.appendChild(el('span', { class: 'fg-card-open', text: canOpen ? 'Open chat →' : 'HubSpot only' }));
    card.appendChild(actions);
    if (canOpen) {
      const open = () => openChatForGroupParticipant(c.groupId, c.groupName);
      card.addEventListener('click', open);
      card.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
    }
    return card;
  }
  function renderFindGroup() {
    const st = state.findGroup;
    const summary = document.getElementById('fgStudentSummary');
    const controls = document.getElementById('fgControls');
    const results = document.getElementById('fgResults');
    if (!results) return;
    const _setVal = (id, v) => { const e = document.getElementById(id); if (e && e.value !== v) e.value = v; };
    _setVal('fgFLevel', st.filters.level); _setVal('fgFDay', st.filters.day || ''); _setVal('fgFTime', st.filters.time || 'any');
    _setVal('fgFTeacher', st.filters.teacher || ''); _setVal('fgFEnds', st.filters.timeLeft || 'any');
    const hr = document.getElementById('fgHasRoom'); if (hr) hr.checked = st.filters.hasRoom;
    const groupSearchActive = _fgIsGroupBrowseSearch(st);
    const d = _fgDataForRender(st);
    if (!d) {
      summary?.classList.add('hidden');
      controls?.classList.toggle('hidden', st.mode !== 'book');   // Book class: filters always visible
      const busy = groupSearchActive ? st.groupSearchLoading : st.loading;
      const error = groupSearchActive ? st.groupSearchError : st.error;
      const empty = groupSearchActive
        ? 'Kan9ellebo f ga3 groups…'
        : (st.mode === 'book' ? "Set l'filters wla 9elleb 3la student." : '9elleb 3la student bach n-bdaw.');
      results.replaceChildren(busy ? _fgSkeleton(8) : el('div', { class: 'tickets-empty', text: error || empty }));
      return;
    }
    const s = d.student;
    controls.classList.remove('hidden');
    if (!s) { summary.classList.add('hidden'); }
    else {
    summary.classList.remove('hidden');
    summary.replaceChildren();
    const sumHead = el('div', { class: 'fg-sum-head' });
    const sumLeft = el('div', { class: 'fg-sum-left' });
    sumLeft.appendChild(el('div', { class: 'fg-avatar', text: ((s.name || '?').trim().charAt(0) || '?').toUpperCase() }));
    const nameWrap = el('div');
    nameWrap.appendChild(el('div', { class: 'fg-sum-name', text: s.name }));
    if (s.phone) nameWrap.appendChild(el('div', { class: 'fg-sum-phone', text: s.phone }));
    sumLeft.appendChild(nameWrap);
    sumHead.appendChild(sumLeft);
    if (s.phone) { const b = el('button', { class: 'btn btn-ghost btn-sm', text: 'Open chat' }); b.addEventListener('click', () => openChatForHubspotContact({ id: s.contactId, name: s.name, phone: s.phone })); sumHead.appendChild(b); }
    summary.appendChild(sumHead);
    const cg = s.currentGroup;
    const sumMeta = el('div', { class: 'fg-sum-meta' });
    _fgKv(sumMeta, 'Current group', cg ? cg.groupName : '— (none on file)');
    _fgKv(sumMeta, 'Level', (cg && cg.level) || s.level || '—');
    _fgKv(sumMeta, 'Program (where they reached)', _fgProgramTxt(cg), '', 'fg-v-num');
    _fgKv(sumMeta, 'Started', _fgFmtDate((cg && cg.startDate) || s.startDate), '', 'fg-v-num');
    _fgKv(sumMeta, 'Program duration', _fgPackTxt(s.packDuration));
    if (cg) { _fgKv(sumMeta, 'Teacher', cg.teacher || '—'); _fgKv(sumMeta, 'Schedule', (cg.scheduleLabel ? (cg.daysPerWeek ? cg.daysPerWeek + ' days/week · ' : '') + cg.scheduleLabel : (cg.day || '—') + (cg.time ? ' · ' + cg.time : '')), '', 'fg-v-wrap'); _fgKv(sumMeta, 'Time left', _fgMonthsTxt(cg.monthsLeft) + ((cg.endDate || cg.close9m) ? ' · ' + _fgFmtDate(cg.endDate || cg.close9m) : ''), '', 'fg-v-num'); }
    summary.appendChild(sumMeta);
    if (cg && cg.progressPct != null) summary.appendChild(_fgProgBar(cg.progressPct, '260px'));
    if (d.targetLevel && d.targetLevel !== ((cg && cg.level) || (s.level || '').toUpperCase())) summary.appendChild(el('div', { class: 'fg-sum-note', text: '→ Looking for ' + d.targetLevel + ' groups' }));
    }
    results.replaceChildren();
    const allCands = d.candidates || [];
    const groupQuery = groupSearchActive ? String(st.groupQuery || '').trim() : '';
    const cands = groupQuery ? allCands.filter(c => _fgGroupSearchMatch(c, groupQuery)) : allCands;
    const sourceTotal = Math.max(Number(d.total) || 0, allCands.length);
    const noun = d.browse ? 'group' : 'close group';
    results.appendChild(el('div', { class: 'fg-results-count', text: cands.length + ' ' + noun + (cands.length === 1 ? '' : 's') + (sourceTotal > cands.length ? ' (showing ' + cands.length + ' of ' + sourceTotal + ')' : '') }));
    if (!cands.length) {
      results.appendChild(el('div', {
        class: 'tickets-empty',
        text: groupQuery
          ? 'Ma l9inach group kaymatchi "' + groupQuery + '". Jarrb code wla smiya okhra.'
          : 'Ma l9inach groups. Jarrb "Any" level wla 7eyyed "Has room".'
      }));
      return;
    }
    const grid = el('div', { class: 'fg-grid' });
    cands.forEach((c, i) => {
      const card = _fgGroupCard(c, i === 0 && c.matchPct != null && c.matchPct >= 50);
      card.classList.add('stagger-item');
      card.style.setProperty('--i', Math.min(i, 10));
      grid.appendChild(card);
    });
    grid.classList.add('content-in');
    results.appendChild(grid);
  }

  // ---- Booking (trial class) ----
  function _fgNewRequestId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'trial-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }
  function _fgOccurrenceWhen(classDate) {
    // A friday-saturday group booked on a Friday lists tonight and tomorrow one
    // line apart, and the two read identically at a glance. Naming which is
    // which is what stops the agent sending the wrong one.
    try {
      const today = MA.dayKey();
      const days = Math.round((Date.parse(classDate + 'T12:00:00Z') - Date.parse(today + 'T12:00:00Z')) / 86400000);
      if (days === 0) return '  ← LYOUMA';
      if (days === 1) return '  ← ghedda';
    } catch { }
    return '';
  }
  // A class that has not started has no session to sit in. support2's preview
  // still lists the weekly slots from today, so LC187 (first session 23/10)
  // offered Fri 16 + Sat 17 Oct and Safae SEBAI was booked into an empty room
  // (2026-10-15). Every date before the group's own start date is dropped.
  function _fgFromGroupStart(occurrences, startDate) {
    const list = Array.isArray(occurrences) ? occurrences : [];
    const start = /^\d{4}-\d{2}-\d{2}/.test(String(startDate || '')) ? String(startDate).slice(0, 10) : '';
    return start ? list.filter(o => String((o && o.classDate) || '') >= start) : list;
  }
  function _fgOccurrenceLabel(o) {
    let date = o.classDate || '';
    try { date = new Date(date + 'T12:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }); } catch { }
    return date + ' · ' + (o.startTime || '') + '–' + (o.endTime || '') + _fgOccurrenceWhen(o.classDate);
  }
  function _fgRenderOccurrences(ctx, rows, selected) {
    const field = document.getElementById('fgBookDateField');
    const select = document.getElementById('fgBookClassDate');
    if (!field || !select) return;
    const list = Array.isArray(rows) ? rows : [];
    field.classList.toggle('hidden', !ctx.trialAccessEnabled);
    if (!ctx.trialAccessEnabled) return;
    // NEVER preselect. The list starts at the next upcoming class, so an
    // untouched dropdown used to book the one starting in a few hours instead
    // of the date the agent had agreed with the student (SAID / LC149,
    // 2026-09-18: a friday-saturday group booked Friday noon went out for that
    // same evening). The agent has to name the date out loud.
    const current = ctx.dateChosen ? (selected || select.value || '') : '';
    select.replaceChildren();
    select.appendChild(el('option', { value: '', text: '— Khtar l\'date —' }));
    for (const o of list) select.appendChild(el('option', { value: o.classDate, text: _fgOccurrenceLabel(o) }));
    select.value = list.some(o => o.classDate === current) ? current : '';
  }
  async function _fgOpenBookModal(c) {
    const s = (state.findGroup.data && state.findGroup.data.student) || null;
    state.findGroup.bookCtx = { group: c, trialAccessEnabled: false, clientRequestId: _fgNewRequestId(), studentContactId: s && s.contactId || null, occurrences: [], dirty: {}, composed: {} };
    const modal = document.getElementById('fgBookModal'); if (!modal) return;
    const gl = document.getElementById('fgBookGroupLine');
    const row = el('div', { class: 'fg-book-sum-row fg-book-sum-arrow' });
    row.appendChild(el('b', { text: c.groupName }));
    row.appendChild(document.createTextNode(' · ' + (c.scheduleLabel || ((c.day || '') + (c.time ? ' ' + c.time : ''))) + (c.teacher ? ' · ' + c.teacher : '')));
    gl.replaceChildren(row);
    document.getElementById('fgBookStuName').value = s ? (s.name || '') : '';
    document.getElementById('fgBookStuPhone').value = s ? (s.phone || '') : '';
    // support2's level guard is a STALENESS check — the level sent with a booking
    // must still equal the group's own level (_fgStudentLevel there). Prefilling it
    // with the student's CEFR level made every cross-level trial die on
    // `stale_level`, so this field carries the CLASS level and the student's own
    // level travels as a note instead.
    state.findGroup.bookCtx.studentOwnLevel = (s && s.level) || '';
    document.getElementById('fgBookStuLevel').value = c.level || (s && s.level) || '';
    document.getElementById('fgBookStudentMsg').value = '';
    document.getElementById('fgBookTeacherMsg').value = '';
    document.getElementById('fgBookStudentReset')?.classList.add('hidden');
    document.getElementById('fgBookTeacherReset')?.classList.add('hidden');
    document.getElementById('fgBookStatus').textContent = '';
    document.getElementById('fgBookTeacherNote').textContent = 'Loading…';
    document.getElementById('fgBookDateField')?.classList.add('hidden');
    modal.classList.remove('hidden');
    await _fgBookRefreshPreview();
  }
  async function _fgBookRefreshPreview() {
    const ctx = state.findGroup.bookCtx; if (!ctx) return;
    const name = (document.getElementById('fgBookStuName').value || '').trim();
    const level = (document.getElementById('fgBookStuLevel').value || '').trim();
    try {
      const selectedDate = document.getElementById('fgBookClassDate')?.value || '';
      const qs = new URLSearchParams({ groupId: ctx.group.groupId, studentName: name, level, trial: '1', clientRequestId: ctx.clientRequestId });
      if (selectedDate) qs.set('classDate', selectedDate);
      const r = await api('/api/find-group/book-preview?' + qs.toString());
      const j = await r.json().catch(() => null);
      if (!r.ok || !j) {
        // The modal prefills from the search pool (support2 caches it 60s), so the
        // level can already be stale when the modal opens. support2 hands back the
        // authoritative one — adopt it once rather than making the agent copy it
        // out of an error message. Once per modal, so a mismatch can't loop.
        if (j && j.error === 'stale_level' && j.expectedLevel && !ctx.levelAdopted) {
          ctx.levelAdopted = true;
          if (ctx.group) ctx.group.level = j.expectedLevel;
          document.getElementById('fgBookStuLevel').value = j.expectedLevel;
          return _fgBookRefreshPreview();
        }
        throw new Error(_fgErrText(j && j.error, r.status, j));
      }
      // The preview re-runs on every level / date / student change. Now that both
      // boxes are editable, overwriting them would silently eat what the agent
      // typed — keep the composed text aside and let them restore it on demand.
      _fgSetComposed(ctx, 'student', j.studentMsg || '');
      _fgSetComposed(ctx, 'teacher', j.teacherMsg || '');
      ctx.trialAccessEnabled = !!j.trialAccessEnabled;
      ctx.occurrences = _fgFromGroupStart(j.occurrences, ctx.group && ctx.group.startDate);
      ctx.classDate = j.classDate || '';
      ctx.startTime = j.startTime || '';
      ctx.endTime = j.endTime || '';
      ctx.expectedTeacher = j.expectedTeacher || '';
      ctx.expectedLevel = j.expectedLevel || '';
      _fgRenderOccurrences(ctx, ctx.occurrences, ctx.classDate);
      // The student box was locked while its text could not reach anyone: the trial
      // path sent an approved Meta template. It now travels as free-form whenever the
      // student's 24h window is open (support2 → /api/inbox/send-template-params),
      // so the team edits it like the teacher one. The students7 link placeholder is
      // swapped for the real grant URL at delivery, so rewriting the text is safe.
      const studentMsg = document.getElementById('fgBookStudentMsg');
      if (studentMsg) { studentMsg.readOnly = false; studentMsg.classList.remove('fg-readonly'); }
      const note = document.getElementById('fgBookTeacherNote');
      if (j.teacherResolved) { note.className = 'fg-book-note fg-book-note-ok'; note.textContent = '✓ Teacher ' + (j.teacherName || '') + ' ghadi y-tnotifa f l\'app (Lougha Support)'; }
      else { note.className = 'fg-book-note fg-book-note-warn'; note.textContent = '⚠ Teacher ma t-resolvach f teachers7 — student-only.'; }
      const occNote = !ctx.trialAccessEnabled ? ''
        : !((ctx.occurrences || []).length) ? '⚠ Ma kaynach occurrence صالحة f 30 youm.'
        : !ctx.dateChosen ? '⚠ Khtar l\'date dyal l\'class 9bel ma tsifet.'
        : '';
      document.getElementById('fgBookStatus').textContent = occNote || _fgBookLevelNote(ctx);
    } catch (e) { document.getElementById('fgBookTeacherNote').textContent = ''; document.getElementById('fgBookStatus').textContent = 'Preview failed: ' + ((e && e.message) || e); }
  }
  // Both message boxes are hand-editable, and the preview refreshes under them on
  // every level / date / student change. A box the agent touched is theirs until
  // they ask for the composed text back.
  const _FG_MSG_EL = { student: 'fgBookStudentMsg', teacher: 'fgBookTeacherMsg' };
  function _fgSetComposed(ctx, which, text) {
    const el = document.getElementById(_FG_MSG_EL[which]);
    if (!el) return;
    ctx.composed = ctx.composed || {};
    ctx.composed[which] = text;
    if (!(ctx.dirty && ctx.dirty[which])) el.value = text;
    _fgToggleMsgReset(ctx, which);
  }
  function _fgToggleMsgReset(ctx, which) {
    const btn = document.getElementById(which === 'student' ? 'fgBookStudentReset' : 'fgBookTeacherReset');
    if (!btn) return;
    const composed = (ctx.composed && ctx.composed[which]) || '';
    const el = document.getElementById(_FG_MSG_EL[which]);
    btn.classList.toggle('hidden', !composed || !el || el.value === composed);
  }
  function _fgResetMsg(which) {
    const ctx = state.findGroup.bookCtx; if (!ctx) return;
    const el = document.getElementById(_FG_MSG_EL[which]);
    if (!el) return;
    el.value = (ctx.composed && ctx.composed[which]) || '';
    if (ctx.dirty) ctx.dirty[which] = false;
    _fgToggleMsgReset(ctx, which);
  }
  // A trial in a group whose level differs from the student's is a normal thing to
  // book — say so plainly instead of letting support2 answer `stale_level`.
  function _fgBookLevelNote(ctx) {
    const cls = String((ctx && ctx.group && ctx.group.level) || '').toUpperCase();
    const stu = String((ctx && ctx.studentOwnLevel) || '').toUpperCase();
    if (!cls) return "⚠ Had l'group bla level f HubSpot — zid l'level dyal l'class 9bel ma tsifet.";
    if (stu && stu !== cls) return 'ℹ Fiche student: ' + stu + ' · class: ' + cls + " — l'booking ghadi ykoun 3la level dyal l'class.";
    return '';
  }
  function _fgBookPickStudent(s) {
    const ctx = state.findGroup.bookCtx;
    if (ctx) { ctx.studentContactId = s.id || null; ctx.studentOwnLevel = s.level || ''; }
    document.getElementById('fgBookStuName').value = s.name || '';
    document.getElementById('fgBookStuPhone').value = s.phone || '';
    // Never overwrite the class level with the student's own — that is exactly what
    // support2 rejects. Only fill it when the group itself carries no level.
    const lvl = document.getElementById('fgBookStuLevel');
    if (lvl && !String(lvl.value || '').trim() && s.level) lvl.value = s.level;
    _fgBookRefreshPreview();
  }
  function _fgPrefersReducedMotion() { return !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches); }
  function _fgCloseBookModal() {
    const m = document.getElementById('fgBookModal');
    state.findGroup.bookCtx = null;
    if (!m) return;
    if (_fgPrefersReducedMotion()) { m.classList.add('hidden'); return; }
    m.classList.add('modal-leaving');
    let done = false;
    const fin = () => { if (done) return; done = true; m.classList.remove('modal-leaving'); m.classList.add('hidden'); };
    m.addEventListener('animationend', function _ml(e) { if (e.target === m) { m.removeEventListener('animationend', _ml); fin(); } });
    setTimeout(fin, 260);
  }
  async function _fgBookSend() {
    const ctx = state.findGroup.bookCtx; if (!ctx) return;
    const btn = document.getElementById('fgBookSend'); const statusEl = document.getElementById('fgBookStatus');
    const name = (document.getElementById('fgBookStuName').value || '').trim();
    const phone = (document.getElementById('fgBookStuPhone').value || '').replace(/\s/g, '');
    const level = (document.getElementById('fgBookStuLevel').value || '').trim();
    const studentMsg = (document.getElementById('fgBookStudentMsg').value || '').trim();
    const teacherMsg = (document.getElementById('fgBookTeacherMsg').value || '').trim();
    if (!phone) { statusEl.textContent = "Khass phone dyal l'student."; return; }
    if (!studentMsg) { statusEl.textContent = "L'message dyal l'student khawi."; return; }
    // With the picker live the agent's choice is the ONLY source of the date.
    // Falling back to `ctx.classDate` here would reinstate the silent default
    // the picker exists to remove.
    const classDate = ctx.trialAccessEnabled
      ? (document.getElementById('fgBookClassDate')?.value || '')
      : (ctx.classDate || '');
    const occurrence = (ctx.occurrences || []).find(o => o.classDate === classDate) || null;
    if (ctx.trialAccessEnabled && !occurrence) { statusEl.textContent = '⚠ Khtar l\'date dyal l\'class — ma-kayna 7etta wa7da mokhtara.'; return; }
    // The student reads the date out of the message body, which the agent can
    // edit. If the two disagree, the one they act on is the wrong one.
    if (occurrence && studentMsg.indexOf(occurrence.classDate) === -1
        && !window.confirm('L\'message ma-fih-ch ' + occurrence.classDate + '. Tsifet b hakka?')) { return; }
    btn.disabled = true; statusEl.textContent = 'Sending…';
    try {
      const r = await api('/api/find-group/book', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({
        studentName: name, studentPhone: phone, studentLevel: level,
        studentContactId: ctx.studentContactId, groupId: ctx.group.groupId, groupName: ctx.group.groupName,
        studentMsg, teacherMsg, trial: true, clientRequestId: ctx.clientRequestId,
        classDate: occurrence && occurrence.classDate, startTime: occurrence && occurrence.startTime,
        endTime: occurrence && occurrence.endTime, expectedTeacher: ctx.expectedTeacher,
        expectedLevel: ctx.expectedLevel,
      }) });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j) {
        // support2 finishes the booking even after leads7 stops waiting, so a
        // timeout here means "unknown", not "not sent" — never invite a blind
        // re-send that would put a second trial template on the student's phone.
        const code = String((j && j.error) || '');
        const unknown = r.status === 504 || code === 'support2_timeout' || code === 'support2_unreachable' || code === 'support2_bad_response';
        throw new Error(_fgErrText(code, r.status, j) + (unknown ? " Chouf f Bookings 9bel ma t3awed tsifet — momkin l'booking dazet." : ''));
      }
      state.findGroup.bookedIds.add(ctx.group.groupId);
      // Free-form only travels inside WhatsApp's 24h window; outside it Meta accepts
      // the approved template alone. Say which one left so nobody assumes their edit
      // reached the student when it could not.
      const mode = j.studentSendMode === 'text' ? ' · texte dyalek' : (j.studentSendMode === 'template' ? " · template Meta (l'texte ma-mchach)" : '');
      const t = j.studentMsgSent ? ((j.teacherMsgSent ? 'Booked ✓ — student + teacher notified' : 'Booked ✓ — student notified (teacher: ' + (j.teacherSkipReason || 'skipped') + ')') + mode) : 'Booking saved — delivery failed. Use Retry in Bookings.';
      toast(t, j.studentMsgSent ? 'success' : 'error');
      _fgCloseBookModal();
      renderFindGroup();
      if (!document.getElementById('fgBookingsPanel').classList.contains('hidden')) _fgLoadBookings();
    } catch (e) { statusEl.textContent = 'Send failed: ' + ((e && e.message) || e); btn.disabled = false; }
  }
  async function _fgToggleBookings() {
    const panel = document.getElementById('fgBookingsPanel'); if (!panel) return;
    const willShow = panel.classList.contains('hidden');
    panel.classList.toggle('hidden', !willShow);
    if (willShow) _fgLoadBookings();
  }
  async function _fgLoadBookings() {
    const panel = document.getElementById('fgBookingsPanel'); if (!panel) return;
    panel.replaceChildren(el('div', { class: 'fg-book-empty', text: 'Loading…' }));
    try {
      const r = await api('/api/find-group/bookings'); const j = await r.json().catch(() => null);
      // A failed load must never render as "no bookings yet" — that reads as a fact.
      if (!r.ok || !j) { panel.replaceChildren(el('div', { class: 'fg-book-empty', text: _fgErrText(j && j.error, r.status, j) })); return; }
      const rows = (j.bookings || []);
      if (!rows.length) { panel.replaceChildren(el('div', { class: 'fg-book-empty', text: 'Mazal ta booking.' })); return; }
      const head = el('div', { class: 'fg-bk-row fg-bk-head' });
      ['Booked', 'Class date', 'Student', 'Group', 'App access', 'WhatsApp', 'Action'].forEach(h => head.appendChild(el('div', { class: 'fg-bk-cell', text: h })));
      const frag = document.createDocumentFragment(); frag.appendChild(head);
      for (const bk of rows.slice(0, 100)) {
        const row = el('div', { class: 'fg-bk-row' });
        row.appendChild(el('div', { class: 'fg-bk-cell fg-v-num', text: _fgFmtDateTime(bk.at) }));
        const dateCell = el('div', { class: 'fg-bk-cell fg-v-num', text: bk.classDate ? (bk.classDate + ' ' + (bk.startTime || '')) : '—' });
        // The grant froze this time and nothing re-syncs it. Once the group is
        // edited, the student holds a link for a class that no longer runs then
        // and the join gate turns them away (SAID / LC149, 2026-09-18). support2
        // compares the two on read — say so here, where someone can act on it.
        if (bk.scheduleDrift) {
          const d = bk.scheduleDrift;
          const moved = (d.dates || [])
            .map(x => x.classDate + ' → ' + (x.nowRuns || 'ma-b9atch f l\'schedule'))
            .join(' | ');
          dateCell.appendChild(el('span', { class: 'fg-bk-drift', text: ' ⚠ schedule tbeddel' }));
          dateCell.title = 'Bookit 3la ' + (d.bookedAs || '?')
            + ' · l\'group daba: ' + (d.liveDays || '?') + ' ' + (d.liveTime || '?')
            + '\n' + moved
            + '\nL\'link dyal l\'student mazal 3la l\'wa9t l-9dim — 3awed booki-h.';
        }
        row.appendChild(dateCell);
        row.appendChild(el('div', { class: 'fg-bk-cell', text: (bk.studentName || bk.studentPhone || '—') }));
        row.appendChild(el('div', { class: 'fg-bk-cell', text: bk.groupName || '—', title: bk.groupName || '' }));
        row.appendChild(el('div', { class: 'fg-bk-cell', text: bk.appAccessStatus || (bk.grantId ? 'issued' : 'legacy') }));
        row.appendChild(el('div', { class: 'fg-bk-cell', text: bk.whatsappStatus || (bk.studentMsgSent ? '✓ sent' : '✕ failed') }));
        const action = el('div', { class: 'fg-bk-cell' });
        if (!bk.studentMsgSent && bk.grantId) {
          const retry = el('button', { class: 'btn btn-ghost fg-bk-retry', type: 'button', text: 'Retry' });
          retry.addEventListener('click', () => _fgRetryBooking(bk.id, retry));
          action.appendChild(retry);
        } else action.textContent = '—';
        row.appendChild(action);
        frag.appendChild(row);
      }
      panel.replaceChildren(frag);
    } catch (e) { panel.replaceChildren(el('div', { class: 'fg-book-empty', text: 'Bookings load failed.' })); }
  }
  async function _fgRetryBooking(id, btn) {
    btn.disabled = true; btn.textContent = '…';
    try {
      const r = await api('/api/find-group/book/' + encodeURIComponent(id) + '/retry', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j) throw new Error(_fgErrText(j && j.error, r.status, j));
      toast(j.studentMsgSent ? 'Trial link reissued and sent ✓' : 'Retry failed: ' + (j.studentErr || 'WhatsApp'), j.studentMsgSent ? 'success' : 'error');
      _fgLoadBookings();
    } catch (e) { toast('Retry failed: ' + ((e && e.message) || e), 'error'); btn.disabled = false; btn.textContent = 'Retry'; }
  }
  function _fgFmtDateTime(ms) { try { return MA.loc(new Date(ms), 'dt', 'en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } }


  // ============================================================
  // 3LASH · Growth intelligence (Admin + Super Sales)
  // Evidence-first dashboard over cohort funnel, sources, team,
  // conversations and revenue. Gemini adds decisions, never raw facts.
  // ============================================================
  const WHY = { inited: false, report: null, meta: null, requestSeq: 0, metaRequestSeq: 0, analyzing: false, sourcesLoaded: false, activePage: 'overview' };
  const WHY_ADS = { inited: false, data: null, requestSeq: 0, dimension: 'ads' };
  // Objectif sub-page — the funnel journey map, owner-scoped. `agentsLoaded`
  // guards the one-shot fill of the Agent dropdown (admin / super_sales only;
  // the API returns an empty list for everyone else).
  const WHY_OBJ = { inited: false, data: null, requestSeq: 0, sourcesLoaded: false, agentsLoaded: false };

  function _whyIsoLocal(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function _whySetRange(kind) {
    const today = new Date();
    let from = new Date(today.getFullYear(), today.getMonth(), 1);
    if (kind !== 'mtd') {
      const days = Math.max(1, Number(kind) || 30);
      from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - days + 1);
    }
    const fromEl = document.getElementById('whyDateFrom');
    const toEl = document.getElementById('whyDateTo');
    if (fromEl) fromEl.value = _whyIsoLocal(from);
    if (toEl) toEl.value = _whyIsoLocal(today);
    document.querySelectorAll('.why-preset').forEach(btn => btn.classList.toggle('why-preset-active', btn.dataset.whyRange === String(kind)));
  }

  function _whyFmt(value, digits = 0) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(n);
  }
  function _whyMoney(value) { return _whyFmt(value, 0) + ' DH'; }
  function _whyAdMoney(value, currency) {
    if (value === null || value === undefined || value === '') return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    const code = String(currency || 'MAD').toUpperCase();
    if (code === 'MAD') return _whyFmt(n, n < 100 ? 2 : 0) + ' DH';
    try { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: code, maximumFractionDigits: n < 100 ? 2 : 0 }).format(n); }
    catch { return _whyFmt(n, 2) + ' ' + code; }
  }
  function _whyPct(value) { return _whyFmt(value, 1) + '%'; }
  function _whySourceLabel(value) {
    const raw = String(value || '').trim();
    if (!raw) return 'Unknown';
    try { return _leadSourceMeta(raw).label || raw; } catch { return raw; }
  }
  function _whyMins(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    if (n < 60) return Math.round(n) + 'm';
    return _whyFmt(n / 60, 1) + 'h';
  }
  // A missing median (nobody called) reads "—", never "0m".
  function _whyMinsOrDash(value) {
    return value == null || value === '' ? '—' : _whyMins(value);
  }
  function _whyImpactClass(value) {
    const v = String(value || '').toLowerCase();
    return v === 'high' ? 'why-chip-high' : (v === 'medium' ? 'why-chip-medium' : 'why-chip-low');
  }
  function _whyConfidenceLabel(value) {
    return value === 'high' ? 'Strong' : (value === 'medium' ? 'Solid' : 'Early signal');
  }

  async function _whyJson(path, options) {
    const response = await api(path, { timeoutMs: 180000, ...(options || {}) });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || ('HTTP ' + response.status));
    return payload;
  }

  function _whySectionHead(icon, kicker, title, note) {
    return el('div', { class: 'why-section-head' }, [
      el('div', { class: 'why-section-icon' }, [lucideIcon(icon)]),
      el('div', { class: 'why-section-copy' }, [
        el('span', { class: 'why-section-kicker', text: kicker }),
        el('h2', { text: title }),
        note ? el('p', { text: note }) : null,
      ]),
    ]);
  }

  function _whyKpi(label, value, hint, tone, icon) {
    return el('article', { class: 'why-kpi why-kpi-' + (tone || 'ink') }, [
      el('div', { class: 'why-kpi-top' }, [
        el('span', { class: 'why-kpi-label', text: label }),
        el('span', { class: 'why-kpi-icon' }, [lucideIcon(icon || 'activity')]),
      ]),
      el('strong', { class: 'why-kpi-value', text: value }),
      el('span', { class: 'why-kpi-hint', text: hint }),
    ]);
  }

  function _whyEmpty(text) {
    return el('div', { class: 'why-inline-empty', text: text || 'No signal in this filter.' });
  }

  function _whyAdsStartOfWeek(date) {
    const out = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    out.setDate(out.getDate() - ((out.getDay() + 6) % 7));
    return out;
  }

  function _whyAdsSetRange(kind) {
    const today = new Date();
    let from = new Date(today.getFullYear(), today.getMonth(), 1);
    let to = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (kind === 'today') from = new Date(to);
    else if (kind === 'yesterday') {
      from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
      to = new Date(from);
    } else if (kind === 'this_week') from = _whyAdsStartOfWeek(today);
    else if (kind === 'last_week') {
      to = new Date(_whyAdsStartOfWeek(today));
      to.setDate(to.getDate() - 1);
      from = new Date(to);
      from.setDate(from.getDate() - 6);
    } else if (kind === 'last_month') {
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      to = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (kind === 'between') {
      document.querySelectorAll('.why-ads-preset').forEach(btn => btn.classList.toggle('is-active', btn.dataset.adsRange === 'between'));
      document.getElementById('whyAdsDateFrom')?.focus();
      return;
    }
    const fromEl = document.getElementById('whyAdsDateFrom');
    const toEl = document.getElementById('whyAdsDateTo');
    if (fromEl) fromEl.value = _whyIsoLocal(from);
    if (toEl) toEl.value = _whyIsoLocal(to);
    document.querySelectorAll('.why-ads-preset').forEach(btn => btn.classList.toggle('is-active', btn.dataset.adsRange === kind));
  }

  function _whyAdsStat(label, value, note, icon, tone, locked) {
    return el('article', { class: `why-ads-stat why-ads-stat-${tone || 'blue'}${locked ? ' is-locked' : ''}` }, [
      el('div', { class: 'why-ads-stat-head' }, [el('span', { text: label }), lucideIcon(locked ? 'lock-keyhole' : icon)]),
      el('strong', { text: value }),
      el('small', { text: note }),
    ]);
  }

  function _whyAdsTrackingText(data) {
    const tracking = data?.tracking || {};
    if (tracking.decision_ready) return `${_whyFmt(tracking.matched_leads)} leads matched to Meta IDs · sales-cost decisions ready`;
    if (tracking.matched_leads) return `${_whyFmt(tracking.matched_leads)} Meta-tagged leads matched · ${_whyPct(tracking.result_match_rate)} of Meta results`;
    return 'Meta delivery is live. Exact Creative → Test → Paid tracking starts when ad_id reaches Leads7.';
  }

  function _whyAdsTrend(data) {
    const rows = _whyMetaBucketDaily(data.daily || [], 46);
    const currency = data.currency || 'MAD';
    const card = el('section', { class: 'why-ads-chart-panel' }, [
      el('div', { class: 'why-ads-section-head' }, [
        el('div', {}, [el('span', { text: 'SPEND RHYTHM' }), el('h3', { text: 'Daily budget vs new conversations' })]),
        el('small', { text: `${data.range?.from || ''} → ${data.range?.to || ''}` }),
      ]),
    ]);
    if (!rows.length) {
      card.appendChild(_whyEmpty('No Meta delivery in this period.'));
      return card;
    }
    const maxSpend = Math.max(1, ...rows.map(row => Number(row.spend) || 0));
    const chart = el('div', { class: 'why-ads-daily-chart', role: 'img', 'aria-label': 'Daily Meta ad spend and results' });
    rows.forEach((row, index) => {
      const spend = Number(row.spend) || 0;
      const height = Math.max(spend ? 4 : 1, spend * 100 / maxSpend);
      const showLabel = index % Math.max(1, Math.ceil(rows.length / 9)) === 0 || index === rows.length - 1;
      chart.appendChild(el('div', {
        class: 'why-ads-day',
        title: `${row.date_start || ''} · ${_whyAdMoney(spend, currency)} · ${_whyFmt(row.results)} results`,
      }, [
        row.results ? el('b', { text: _whyFmt(row.results) }) : el('b', { text: '' }),
        el('div', { class: 'why-ads-day-slot' }, [el('span', { style: `height:${height}%` })]),
        el('small', { text: showLabel ? String(row.label || row.date_start || '').replace(/^2026-/, '') : '' }),
      ]));
    });
    card.appendChild(chart);
    card.appendChild(el('div', { class: 'why-ads-chart-legend' }, [
      el('span', {}, [el('i', { class: 'is-spend' }), document.createTextNode('Spend')]),
      el('span', {}, [el('b', { text: '12' }), document.createTextNode('Meta results above bars')]),
    ]));
    return card;
  }

  function _whyAdsFunnel(data) {
    const tracking = data.tracking || {};
    const stageCosts = data.stage_costs || {};
    const stages = stageCosts.stages || [];
    const reliable = !!stageCosts.reliable;
    const panel = el('section', { class: 'why-ads-funnel-panel' }, [
      el('div', { class: 'why-ads-section-head' }, [
        el('div', {}, [el('span', { text: 'THE TWO MONEY GATES' }), el('h3', { text: 'Where ad-acquired leads stop progressing' })]),
        el('span', { class: `why-ads-confidence is-${tracking.confidence || 'low'}`, text: reliable ? 'Decision ready' : 'Tracking · learning' }),
      ]),
    ]);
    if (!stages.length || !tracking.matched_leads) {
      panel.appendChild(el('div', { class: 'why-ads-funnel-empty' }, [
        lucideIcon('route'),
        el('div', {}, [
          el('strong', { text: 'The funnel is ready; exact ad attribution is starting now.' }),
          el('p', { text: 'Meta already gives Spend and CPL. New Click-to-WhatsApp referrals will now save ad_id, then this view will reveal Cost/Test, Cost/Paid and the money lost at each gate.' }),
        ]),
      ]));
      return panel;
    }
    const flow = el('div', { class: 'why-ads-funnel-flow' });
    stages.forEach((stage, index) => {
      const max = Math.max(1, Number(stage.entered) || 0);
      const width = Math.max(8, Math.min(100, (Number(stage.advanced) || 0) * 100 / max));
      flow.appendChild(el('article', { class: `why-ads-gate why-ads-gate-${index + 1}` }, [
        el('div', { class: 'why-ads-gate-top' }, [
          el('span', { text: `GATE 0${index + 1}` }),
          el('b', { text: _whyPct(stage.conversion) }),
        ]),
        el('h4', { text: stage.label }),
        el('div', { class: 'why-ads-gate-numbers' }, [
          el('div', {}, [el('small', { text: 'Entered' }), el('strong', { text: _whyFmt(stage.entered) })]),
          el('span', {}, [lucideIcon('arrow-right')]),
          el('div', {}, [el('small', { text: 'Advanced' }), el('strong', { text: _whyFmt(stage.advanced) })]),
        ]),
        el('div', { class: 'why-ads-gate-track' }, [el('span', { style: `width:${width}%` })]),
        el('div', { class: 'why-ads-gate-loss' }, [
          el('span', {}, [lucideIcon('user-x'), document.createTextNode(`${_whyFmt(stage.lost)} lost`)]),
          el('strong', { text: reliable && stage.allocated_lost_spend != null ? _whyAdMoney(stage.allocated_lost_spend, data.currency) : 'Early estimate' }),
        ]),
        el('small', { class: 'why-ads-gate-cost', text: stage.cost_per_advanced != null
          ? `${_whyAdMoney(stage.cost_per_advanced, data.currency)} per ${index ? 'Paid' : 'Test done'}${reliable ? '' : ' · low coverage'}`
          : 'Waiting for downstream conversions' }),
      ]));
    });
    panel.appendChild(flow);
    panel.appendChild(el('p', { class: 'why-ads-method-note', text: reliable
      ? `Allocated loss uses ${_whyAdMoney(stageCosts.matched_spend, data.currency)} of campaign spend matched to tracked Leads7 leads.`
      : 'Stage-spend values are directional until at least half of Meta results match tracked Leads7 leads.' }));
    return panel;
  }

  function _whyAdsWinnerCard(kind, row, data, salesMode) {
    const isCreative = kind === 'Creative';
    const name = isCreative ? (row?.ad_name || row?.creative?.headline) : (kind === 'Ad set' ? row?.adset_name : row?.campaign_name);
    const card = el('article', { class: `why-ads-winner${isCreative ? ' is-creative' : ''}` });
    if (isCreative) {
      const src = row?.creative?.image_url || row?.creative?.thumbnail_url;
      card.appendChild(src
        ? el('img', { src, alt: name || 'Winning ad creative', loading: 'lazy', referrerpolicy: 'no-referrer' })
        : el('div', { class: 'why-ads-creative-placeholder' }, [lucideIcon('image')]));
    }
    card.appendChild(el('div', { class: 'why-ads-winner-body' }, [
      el('div', { class: 'why-ads-winner-label' }, [
        el('span', { text: kind }),
        el('em', { text: salesMode ? 'SALES WINNER' : 'MEDIA WINNER' }),
      ]),
      el('h4', { text: name || 'Not enough delivery yet' }),
      isCreative && row?.creative?.headline ? el('p', { text: row.creative.headline }) : null,
      row ? el('div', { class: 'why-ads-winner-metrics' }, [
        el('span', {}, [el('small', { text: 'Spend' }), el('b', { text: _whyAdMoney(row.spend, data.currency) })]),
        el('span', {}, [el('small', { text: 'Results' }), el('b', { text: _whyFmt(row.results) })]),
        el('span', {}, [el('small', { text: 'Meta CPL' }), el('b', { text: _whyAdMoney(row.cpl, data.currency) })]),
        el('span', {}, [el('small', { text: 'Paid' }), el('b', { text: row.crm?.paid ? _whyFmt(row.crm.paid) : '—' })]),
      ]) : el('p', { text: 'Needs at least 3 Meta results.' }),
    ]));
    return card;
  }

  function _whyAdsWinners(data) {
    const salesMode = !!data.tracking?.decision_ready;
    const winners = data.winners || {};
    const picks = salesMode
      ? [winners.campaign_sales || winners.campaign_media, winners.adset_sales || winners.adset_media, winners.creative_sales || winners.creative_media]
      : [winners.campaign_media, winners.adset_media, winners.creative_media];
    return el('section', { class: 'why-ads-winners-panel' }, [
      el('div', { class: 'why-ads-section-head' }, [
        el('div', {}, [el('span', { text: 'WINNERS BOARD' }), el('h3', { text: salesMode ? 'What actually reaches Paid' : 'What buys results efficiently now' })]),
        el('small', { text: salesMode ? 'Ranked by Leads7 Paid outcomes' : 'Ranked by Meta CPL with minimum 3 results' }),
      ]),
      el('div', { class: 'why-ads-winners-grid' }, [
        _whyAdsWinnerCard('Campaign', picks[0], data, salesMode && !!winners.campaign_sales),
        _whyAdsWinnerCard('Ad set', picks[1], data, salesMode && !!winners.adset_sales),
        _whyAdsWinnerCard('Creative', picks[2], data, salesMode && !!winners.creative_sales),
      ]),
    ]);
  }

  function _whyAdsSalesAgents(data) {
    const rows = (data.sales_agents || []).slice(0, 8);
    const panel = el('section', { class: 'why-ads-agents-panel' }, [
      el('div', { class: 'why-ads-section-head' }, [
        el('div', {}, [el('span', { text: 'SALES HANDOFF' }), el('h3', { text: 'Who converts ad leads after the click' })]),
        el('small', { text: 'Ranked by Paid, then conversion · tracked ad leads only' }),
      ]),
    ]);
    if (!rows.length) {
      panel.appendChild(_whyEmpty('Agent ranking will appear when tracked ad leads are assigned.'));
      return panel;
    }
    const list = el('div', { class: 'why-ads-agent-list' });
    rows.forEach((row, index) => list.appendChild(el('div', { class: `why-ads-agent-row${index === 0 ? ' is-winner' : ''}` }, [
      el('span', { class: 'why-ads-agent-rank', text: String(index + 1).padStart(2, '0') }),
      el('span', { class: 'why-ads-agent-avatar', text: String(userLabel(row.agent_id || '?')).charAt(0).toUpperCase() }),
      el('div', { class: 'why-ads-agent-name' }, [
        el('strong', { text: userLabel(row.agent_id || 'Unassigned') }),
        el('small', { text: `${_whyFmt(row.leads)} ad leads handled` }),
      ]),
      el('span', {}, [el('small', { text: 'Test done' }), el('b', { text: _whyFmt(row.test_done) })]),
      el('span', {}, [el('small', { text: 'Paid' }), el('b', { text: _whyFmt(row.paid) })]),
      el('span', {}, [el('small', { text: 'Lead → Paid' }), el('b', { text: _whyPct(row.lead_to_paid_rate) })]),
    ])));
    panel.appendChild(list);
    return panel;
  }

  function _whyAdsTableRows(data) {
    return WHY_ADS.dimension === 'campaigns' ? (data.campaigns || [])
      : (WHY_ADS.dimension === 'adsets' ? (data.adsets || []) : (data.ads || []));
  }

  function _whyAdsRenderTable(data, root) {
    clear(root);
    const rows = _whyAdsTableRows(data);
    const tableWrap = el('div', { class: 'why-ads-table-wrap' });
    if (!rows.length) {
      tableWrap.appendChild(_whyEmpty('No delivery at this level for the selected period.'));
      root.appendChild(tableWrap);
      return;
    }
    const table = el('table', { class: 'why-ads-table' });
    table.appendChild(el('thead', {}, [el('tr', {}, [
      'Name', 'Spend', 'Results', 'Meta CPL', 'CTR', 'Tracked leads', 'Test done', 'Paid', 'Cost / Test', 'Cost / Paid',
    ].map(text => el('th', { text })))]));
    const body = el('tbody');
    rows.forEach((row, index) => {
      const isAd = WHY_ADS.dimension === 'ads';
      const name = isAd ? row.ad_name : (WHY_ADS.dimension === 'adsets' ? row.adset_name : row.campaign_name);
      const image = row.creative?.thumbnail_url || row.creative?.image_url;
      body.appendChild(el('tr', { class: index === 0 ? 'is-top-spend' : '' }, [
        el('td', {}, [
          el('div', { class: 'why-ads-name-cell' }, [
            isAd ? (image ? el('img', { src: image, alt: '', loading: 'lazy', referrerpolicy: 'no-referrer' }) : el('span', { class: 'why-ads-mini-placeholder' }, [lucideIcon('image')])) : null,
            el('div', {}, [
              el('strong', { text: name || 'Unnamed' }),
              el('small', { text: isAd ? `${row.adset_name || ''} · ${row.campaign_name || ''}` : (WHY_ADS.dimension === 'adsets' ? row.campaign_name || '' : `ID ${row.campaign_id || ''}`) }),
            ]),
          ]),
        ]),
        el('td', { text: _whyAdMoney(row.spend, data.currency) }),
        el('td', { text: _whyFmt(row.results) }),
        el('td', { text: _whyAdMoney(row.cpl, data.currency) }),
        el('td', { text: _whyPct(row.ctr) }),
        el('td', { text: row.crm?.leads ? _whyFmt(row.crm.leads) : '—' }),
        el('td', { text: row.crm?.test_done ? _whyFmt(row.crm.test_done) : '—' }),
        el('td', { text: row.crm?.paid ? _whyFmt(row.crm.paid) : '—' }),
        el('td', { text: row.cost_per_test != null ? _whyAdMoney(row.cost_per_test, data.currency) : '—' }),
        el('td', { text: row.cost_per_paid != null ? _whyAdMoney(row.cost_per_paid, data.currency) : '—' }),
      ]));
    });
    table.appendChild(body);
    tableWrap.appendChild(table);
    root.appendChild(tableWrap);
    refreshIcons(root);
  }

  function _whyAdsExplorer(data) {
    const tableRoot = el('div', { class: 'why-ads-table-root' });
    const controls = el('div', { class: 'why-ads-dimension-tabs' });
    [
      ['campaigns', 'Campaigns', 'layers-3'],
      ['adsets', 'Ad sets', 'users-round'],
      ['ads', 'Creatives', 'images'],
    ].forEach(([key, label, icon]) => {
      const btn = el('button', { type: 'button', class: `why-ads-dimension${WHY_ADS.dimension === key ? ' is-active' : ''}` }, [lucideIcon(icon), el('span', { text: label })]);
      btn.addEventListener('click', () => {
        WHY_ADS.dimension = key;
        controls.querySelectorAll('button').forEach(node => node.classList.toggle('is-active', node === btn));
        _whyAdsRenderTable(data, tableRoot);
      });
      controls.appendChild(btn);
    });
    const section = el('section', { class: 'why-ads-explorer' }, [
      el('div', { class: 'why-ads-section-head why-ads-explorer-head' }, [
        el('div', {}, [el('span', { text: 'FULL MONEY MAP' }), el('h3', { text: 'Compare every campaign, audience and creative' })]),
        controls,
      ]),
      tableRoot,
    ]);
    _whyAdsRenderTable(data, tableRoot);
    return section;
  }

  function _whyAdsRender(data) {
    const root = document.getElementById('whyAdsDashboard');
    if (!root) return;
    clear(root);
    WHY_ADS.data = data;
    const accountRoot = document.getElementById('whyAdsAccount');
    if (accountRoot) {
      const accountChildren = [
        el('span', { text: 'Ad Account' }),
        el('strong', { text: data.account?.name || 'Not selected' }),
      ];
      if (data.account?.id) accountChildren.push(el('small', { text: `${data.currency || ''} · ${String(data.account.id).replace('act_', '')}` }));
      accountRoot.replaceChildren(...accountChildren);
    }
    if (!data.account || !data.totals) {
      root.appendChild(el('section', { class: 'why-ads-connect-state' }, [
        lucideIcon('link-2-off'),
        el('div', {}, [el('h3', { text: 'Meta Ads account is not ready' }), el('p', { text: 'Return to Decision room, connect Meta Ads and choose the Ad Account.' })]),
        el('button', { type: 'button', onclick: () => _whySetSubpage('overview'), text: 'Open connection settings' }),
      ]));
      refreshIcons(root);
      return;
    }
    const totals = data.totals || {};
    const tracked = Number(data.tracking?.matched_leads) || 0;
    const stageCosts = data.stage_costs || {};
    root.appendChild(el('section', { class: 'why-ads-summary' }, [
      el('div', { class: 'why-ads-summary-top' }, [
        el('div', {}, [
          el('span', { class: 'why-ads-live-pill' }, [el('i'), document.createTextNode('LIVE FROM META')]),
          el('h3', { text: `${_whyAdMoney(totals.spend, data.currency)} spent to create ${_whyFmt(totals.results)} ${_whyMetaResultLabel(totals.result_type).toLowerCase()}` }),
          el('p', { text: _whyAdsTrackingText(data) }),
        ]),
        el('div', { class: `why-ads-tracking-score is-${data.tracking?.confidence || 'low'}` }, [
          el('strong', { text: _whyPct(data.tracking?.result_match_rate || 0) }),
          el('span', { text: 'CRM match' }),
        ]),
      ]),
      el('div', { class: 'why-ads-stat-grid' }, [
        _whyAdsStat('Total ad spend', _whyAdMoney(totals.spend, data.currency), `${_whyFmt(totals.impressions)} impressions`, 'wallet-cards', 'ink'),
        _whyAdsStat(_whyMetaResultLabel(totals.result_type), _whyFmt(totals.results), `${_whyFmt(totals.clicks)} clicks · ${_whyPct(totals.ctr)} CTR`, 'message-circle-more', 'blue'),
        _whyAdsStat('Meta cost / result', _whyAdMoney(totals.cpl, data.currency), 'Immediate media efficiency', 'badge-dollar-sign', 'green'),
        _whyAdsStat('Tracked Leads7 leads', tracked ? _whyFmt(tracked) : 'Starting now', tracked
          ? `${_whyFmt(data.crm?.matched?.test_done)} tests · ${_whyFmt(data.crm?.matched?.paid)} paid${data.tracking?.historical_meta_referrals_recovered ? ` · ${_whyFmt(data.tracking.historical_meta_referrals_recovered)} historical recovered` : ''}`
          : 'ad_id will connect future WhatsApp leads', 'scan-line', 'cyan', !tracked),
        _whyAdsStat('Cost / Test done', stageCosts.cost_per_test != null ? _whyAdMoney(stageCosts.cost_per_test, data.currency) : 'Tracking needed', tracked ? `${_whyFmt(data.crm?.matched?.test_done)} attributed tests` : 'Never divide spend by untracked tests', 'flask-conical', 'amber', !stageCosts.reliable),
        _whyAdsStat('Cost / Paid', stageCosts.cost_per_paid != null ? _whyAdMoney(stageCosts.cost_per_paid, data.currency) : 'Tracking needed', tracked ? `${_whyFmt(data.crm?.matched?.paid)} attributed Paid clients` : 'Paid = Leads7 stage only', 'credit-card', 'violet', !stageCosts.reliable),
      ]),
    ]));
    root.appendChild(el('div', { class: 'why-ads-main-grid' }, [
      _whyAdsFunnel(data),
      _whyAdsTrend(data),
    ]));
    root.appendChild(_whyAdsWinners(data));
    root.appendChild(_whyAdsSalesAgents(data));
    root.appendChild(_whyAdsExplorer(data));
    root.appendChild(el('section', { class: `why-ads-tracking-note${data.tracking?.decision_ready ? ' is-ready' : ''}` }, [
      lucideIcon(data.tracking?.decision_ready ? 'badge-check' : 'shield-alert'),
      el('div', {}, [
        el('strong', { text: data.tracking?.decision_ready ? 'Creative → Paid attribution is decision-ready' : 'Spend and Meta CPL are exact; sales attribution is still learning' }),
        el('p', { text: data.tracking?.decision_ready
          ? `${_whyFmt(data.tracking.matched_leads)} leads match Meta identifiers. You can compare Cost/Test and Cost/Paid.`
          : `${_whyFmt(data.tracking?.identifier_tracked_leads || 0)} leads carry campaign/ad identifiers${data.tracking?.historical_meta_referrals_recovered ? ` (${_whyFmt(data.tracking.historical_meta_referrals_recovered)} recovered from historical Meta referrals)` : ''}. ${_whyFmt(data.tracking?.unattributed_ad_whatsapp || 0)} untracked WhatsApp ad leads stay in general 3LASH only and are excluded from ad costs. ${_whyFmt(data.tracking?.meta_results || 0)} Meta results were recorded.` }),
        el('small', { text: `Important: Meta “purchase” actions are ignored. Paid means only the Leads7 Paid stage. Revenue stays in MAD; ad spend stays in ${data.currency}.` }),
      ]),
    ]));
    refreshIcons(root);
  }

  async function _whyAdsLoad(force) {
    const seq = ++WHY_ADS.requestSeq;
    const loading = document.getElementById('whyAdsLoading');
    const error = document.getElementById('whyAdsError');
    const dashboard = document.getElementById('whyAdsDashboard');
    loading?.classList.remove('hidden');
    error?.classList.add('hidden');
    if (dashboard && !WHY_ADS.data) clear(dashboard);
    try {
      const params = new URLSearchParams();
      const from = document.getElementById('whyAdsDateFrom')?.value;
      const to = document.getElementById('whyAdsDateTo')?.value;
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (force) params.set('force', '1');
      const data = await _whyJson('/api/meta-ads/intelligence?' + params.toString());
      if (seq !== WHY_ADS.requestSeq) return;
      _whyAdsRender(data);
      const status = document.getElementById('whyStatus');
      if (status) status.textContent = 'Ads data live';
    } catch (err) {
      if (seq !== WHY_ADS.requestSeq) return;
      error.textContent = err.message || 'Could not load Ads Intelligence.';
      error.classList.remove('hidden');
    } finally {
      if (seq === WHY_ADS.requestSeq) loading?.classList.add('hidden');
    }
  }

  function _whyAdsInit() {
    if (!WHY_ADS.inited) {
      WHY_ADS.inited = true;
      _whyAdsSetRange('this_month');
      document.querySelectorAll('.why-ads-preset').forEach(btn => btn.addEventListener('click', () => {
        _whyAdsSetRange(btn.dataset.adsRange);
        if (btn.dataset.adsRange !== 'between') _whyAdsLoad(false);
      }));
      ['whyAdsDateFrom', 'whyAdsDateTo'].forEach(id => document.getElementById(id)?.addEventListener('change', () => {
        document.querySelectorAll('.why-ads-preset').forEach(btn => btn.classList.toggle('is-active', btn.dataset.adsRange === 'between'));
      }));
      document.getElementById('whyAdsApplyBtn')?.addEventListener('click', () => _whyAdsLoad(false));
      document.getElementById('whyAdsRefreshBtn')?.addEventListener('click', () => _whyAdsLoad(true));
      _whyAdsLoad(false);
    } else if (!WHY_ADS.data) _whyAdsLoad(false);
  }

  function _whySetSubpage(page) {
    // Decision room and Ads are sales-admin surfaces; an agent that somehow
    // asks for one lands on Objectif instead (the API would 403 anyway).
    let next = ['ads', 'objectif', 'overview'].includes(page) ? page : 'overview';
    if (next !== 'objectif' && !_isSalesAdmin()) next = 'objectif';
    WHY.activePage = next;
    document.querySelectorAll('[data-why-page]').forEach(btn => btn.classList.toggle('is-active', btn.dataset.whyPage === next));
    document.getElementById('whyOverviewPage')?.classList.toggle('hidden', next !== 'overview');
    document.getElementById('whyObjectifPage')?.classList.toggle('hidden', next !== 'objectif');
    document.getElementById('whyAdsPage')?.classList.toggle('hidden', next !== 'ads');
    document.getElementById('whyAnalyticsView')?.classList.toggle('is-ads-page', next === 'ads');
    const status = document.getElementById('whyStatus');
    if (status) {
      if (next === 'ads') status.textContent = WHY_ADS.data ? 'Ads data live' : 'Connecting Ads';
      else if (next === 'objectif') status.textContent = WHY_OBJ.data ? 'Parcours ready' : 'Ready';
      else status.textContent = WHY.report ? 'Facts ready' : 'Ready';
    }
    if (next === 'ads') _whyAdsInit();
    if (next === 'objectif') _whyObjInit();
  }

  function _whyRenderAi(analysis, meta) {
    const root = document.getElementById('whyAiDecision');
    if (!root) return;
    clear(root);
    const top = el('div', { class: 'why-ai-top' }, [
      el('div', { class: 'why-ai-mark' }, [lucideIcon('sparkles')]),
      el('div', { class: 'why-ai-title' }, [
        el('span', { text: '3LASH AI ANALYST' }),
        el('small', { text: meta?.generated_at ? ('Updated ' + MA.loc(new Date(meta.generated_at), 'dt', 'fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })) : 'Evidence is being analyzed' }),
      ]),
      meta?.model ? el('span', { class: 'why-model-pill', text: meta.model }) : null,
    ]);
    root.appendChild(top);

    if (!analysis) {
      root.appendChild(el('div', { class: 'why-ai-thinking' }, [
        el('span', { class: 'why-ai-pulse' }),
        el('div', {}, [
          el('strong', { text: 'Kan9ra l-patterns, machi ghir l-ar9am…' }),
          el('p', { text: 'Sources, drop-offs, objections, agent conversion and ad angles.' }),
        ]),
      ]));
      return;
    }

    root.appendChild(el('div', { class: 'why-ai-brief' }, [
      el('h2', { text: analysis.headline || 'Decision brief' }),
      el('p', { text: analysis.executive_summary || '' }),
    ]));

    const findings = Array.isArray(analysis.priority_findings) ? analysis.priority_findings : [];
    if (findings.length) {
      const grid = el('div', { class: 'why-finding-grid' });
      findings.forEach((finding, index) => {
        grid.appendChild(el('article', { class: 'why-finding-card' }, [
          el('div', { class: 'why-finding-num', text: String(index + 1).padStart(2, '0') }),
          el('div', { class: 'why-finding-body' }, [
            el('div', { class: 'why-finding-heading' }, [
              el('h3', { text: finding.title || 'Finding' }),
              el('span', { class: 'why-mini-chip ' + _whyImpactClass(finding.impact), text: (finding.impact || 'medium') + ' impact' }),
              el('span', { class: 'why-mini-chip why-chip-confidence', text: _whyConfidenceLabel(finding.confidence) }),
            ]),
            el('p', { class: 'why-finding-evidence', text: finding.evidence || '' }),
            el('p', { class: 'why-finding-why', text: finding.why_it_matters || '' }),
            el('div', { class: 'why-finding-action' }, [lucideIcon('arrow-up-right'), el('span', { text: finding.action || '' })]),
          ]),
        ]));
      });
      root.appendChild(grid);
    }

    const experiments = Array.isArray(analysis.ad_experiments) ? analysis.ad_experiments : [];
    if (experiments.length) {
      const block = el('div', { class: 'why-ai-subblock' }, [
        _whySectionHead('megaphone', 'PAID ACQUISITION', 'Winning ideas to test', 'Ad hypotheses grounded in the selected cohort.'),
      ]);
      const grid = el('div', { class: 'why-ad-grid' });
      experiments.forEach(exp => {
        grid.appendChild(el('article', { class: 'why-ad-card' }, [
          el('div', { class: 'why-ad-card-top' }, [
            el('span', { class: 'why-ad-angle', text: exp.angle || 'Ad angle' }),
            el('span', { class: 'why-mini-chip why-chip-confidence', text: _whyConfidenceLabel(exp.confidence) }),
          ]),
          el('span', { class: 'why-ad-audience', text: exp.audience || '' }),
          el('blockquote', { text: exp.hook || '' }),
          el('dl', { class: 'why-ad-details' }, [
            el('div', {}, [el('dt', { text: 'Proof' }), el('dd', { text: exp.proof || '—' })]),
            el('div', {}, [el('dt', { text: 'CTA' }), el('dd', { text: exp.cta || '—' })]),
            el('div', {}, [el('dt', { text: 'Measure' }), el('dd', { text: exp.success_metric || '—' })]),
          ]),
          el('small', { class: 'why-based-on', text: 'Based on: ' + (exp.based_on || 'selected data') }),
        ]));
      });
      block.appendChild(grid);
      root.appendChild(block);
    }

    const plays = Array.isArray(analysis.sales_plays) ? analysis.sales_plays : [];
    if (plays.length) {
      const block = el('div', { class: 'why-ai-subblock' }, [
        _whySectionHead('route', 'SALES OPERATING SYSTEM', 'Next plays', 'What changes Monday morning, and how to measure it.'),
      ]);
      const list = el('div', { class: 'why-play-list' });
      plays.forEach(play => list.appendChild(el('article', { class: 'why-play' }, [
        el('div', { class: 'why-play-stage', text: play.stage || 'Funnel' }),
        el('div', { class: 'why-play-main' }, [
          el('strong', { text: play.problem || 'Problem' }),
          el('p', { text: play.play || '' }),
        ]),
        el('div', { class: 'why-play-owner' }, [el('span', { text: play.owner || 'Sales' }), el('small', { text: play.kpi || '' })]),
      ])));
      block.appendChild(list);
      root.appendChild(block);
    }

    const footItems = [];
    const watchouts = Array.isArray(analysis.watchouts) ? analysis.watchouts : [];
    const questions = Array.isArray(analysis.questions_to_investigate) ? analysis.questions_to_investigate : [];
    if (watchouts.length) footItems.push(el('div', { class: 'why-ai-foot-card' }, [
      el('h3', {}, [lucideIcon('triangle-alert'), el('span', { text: 'Watchouts' })]),
      el('ul', {}, watchouts.map(item => el('li', { text: item }))),
    ]));
    if (questions.length) footItems.push(el('div', { class: 'why-ai-foot-card' }, [
      el('h3', {}, [lucideIcon('circle-help'), el('span', { text: 'Next questions' })]),
      el('ul', {}, questions.map(item => el('li', { text: item }))),
    ]));
    if (footItems.length) root.appendChild(el('div', { class: 'why-ai-foot' }, footItems));
    refreshIcons(root);
  }

  function _whyVoiceCard(title, subtitle, rows, tone) {
    const card = el('article', { class: 'why-voice-card why-voice-' + tone }, [
      el('div', { class: 'why-voice-head' }, [el('h3', { text: title }), el('span', { text: subtitle })]),
    ]);
    const list = el('div', { class: 'why-voice-list' });
    (rows || []).slice(0, 7).forEach(row => {
      list.appendChild(el('div', { class: 'why-voice-row' }, [
        el('div', { class: 'why-voice-label' }, [el('strong', { text: row.label }), el('span', { text: _whyFmt(row.count) + ' leads' })]),
        el('div', { class: 'why-bar-track' }, [el('span', { class: 'why-bar-fill', style: `width:${Math.min(100, Number(row.share) || 0)}%` })]),
        el('span', { class: 'why-voice-share', text: _whyPct(row.share) }),
        row.quotes?.[0] ? el('q', { text: row.quotes[0] }) : null,
      ]));
    });
    card.appendChild(list.childNodes.length ? list : _whyEmpty('No category captured yet.'));
    return card;
  }

  function _whySourceMix(label, rows, tone, emptyText) {
    const block = el('div', { class: 'why-source-mix why-source-mix-' + tone }, [
      el('span', { class: 'why-source-mix-label', text: label }),
    ]);
    const list = el('div', { class: 'why-source-mix-list' });
    (rows || []).slice(0, 3).forEach(row => list.appendChild(el('span', { class: 'why-source-mix-chip' }, [
      el('strong', { text: row.label }),
      el('small', { text: _whyPct(row.share) }),
    ])));
    block.appendChild(list.childNodes.length ? list : el('span', { class: 'why-source-mix-empty', text: emptyText }));
    return block;
  }

  function _whySourceDetailCard(row, index) {
    const weak = row.weakest_stage || {};
    const delta = Number(row.delta_vs_team_paid_pp) || 0;
    return el('article', { class: 'why-source-detail ' + (index === 0 ? 'why-source-detail-winner' : '') }, [
      el('div', { class: 'why-source-detail-head' }, [
        el('div', { class: 'why-source-title' }, [
          el('span', { class: 'why-source-rank', text: String(index + 1).padStart(2, '0') }),
          el('div', {}, [
            el('h3', { text: _whySourceLabel(row.source) }),
            el('span', { text: _whyFmt(row.leads) + ' leads · ' + _whyConfidenceLabel(row.confidence) }),
          ]),
        ]),
        el('div', { class: 'why-source-paid' }, [
          el('strong', { text: _whyPct(row.paid_rate) }),
          el('span', { class: delta >= 0 ? 'is-positive' : 'is-negative', text: (delta >= 0 ? '+' : '') + _whyFmt(delta, 1) + ' pp vs team' }),
        ]),
      ]),
      el('div', { class: 'why-source-metrics' }, [
        el('div', {}, [el('span', { text: 'Paid' }), el('strong', { text: _whyFmt(row.paid) })]),
        el('div', {}, [el('span', { text: 'Value / lead' }), el('strong', { text: _whyMoney(row.revenue_per_lead) })]),
        el('div', {}, [el('span', { text: 'Test → PF' }), el('strong', { text: _whyPct(row.test_to_proforma) })]),
        el('div', {}, [el('span', { text: 'PF → Paid' }), el('strong', { text: _whyPct(row.proforma_to_paid) })]),
      ]),
      el('div', { class: 'why-source-story' }, [
        _whySourceMix('Why they register', row.motives, 'motive', 'Motive not captured yet'),
        _whySourceMix('What they ask', row.questions, 'question', 'Question not captured yet'),
        _whySourceMix('Main friction', row.objections, 'objection', 'No explicit objection captured'),
      ]),
      el('div', { class: 'why-source-detail-foot' }, [
        el('div', { class: 'why-source-leak' }, [
          lucideIcon('triangle-alert'),
          el('div', {}, [el('span', { text: 'BIGGEST LEAK' }), el('strong', { text: weak.label || 'Not enough data' })]),
          weak.label ? el('b', { text: _whyPct(weak.conversion) }) : null,
        ]),
        el('span', { class: 'why-source-coverage' }, [lucideIcon('scan-text'), el('b', { text: _whyPct(row.signal_coverage) }), document.createTextNode(' customer signal')]),
      ]),
    ]);
  }

  function _whyJourneyNode(stage, journey, nodeIndex) {
    const isEnd = nodeIndex === (journey.stages || []).length - 1;
    return el('div', { class: 'why-journey-node ' + (isEnd ? 'why-journey-node-end' : '') }, [
      el('span', { class: 'why-journey-node-index', text: String(nodeIndex + 1).padStart(2, '0') }),
      el('div', { class: 'why-journey-node-icon' }, [lucideIcon(isEnd ? (journey.key === 'test_to_paid' ? 'badge-check' : 'clipboard-check') : 'circle-dot')]),
      el('strong', { text: _whyFmt(stage.count) }),
      el('span', { class: 'why-journey-node-label', text: stage.label }),
      el('small', { text: _whyPct(stage.journey_share) + ' of journey start' }),
    ]);
  }

  function _whyJourneyTransition(row) {
    const problem = row.main_problem || {};
    const statusLabel = row.status === 'critical' ? 'MAIN BOTTLENECK' : (row.status === 'watch' ? 'WATCH' : 'FLOWING');
    const evidenceLabel = problem.evidence === 'data_gap'
      ? 'Reason data missing'
      : (_whyFmt(problem.count) + ' / ' + _whyFmt(row.lost) + ' lost · ' + _whyPct(problem.share));
    return el('div', { class: 'why-journey-transition why-transition-' + (row.status || 'watch') }, [
      el('div', { class: 'why-transition-score' }, [
        el('span', { class: 'why-transition-status', text: statusLabel }),
        el('strong', { text: _whyPct(row.conversion) }),
        el('small', { text: _whyFmt(row.advanced) + ' advanced · ' + _whyFmt(row.lost) + ' lost' }),
      ]),
      el('div', { class: 'why-flow-arrow', 'aria-label': _whyPct(row.conversion) + ' conversion' }, [
        el('span', { class: 'why-flow-arrow-fill', style: `width:${Math.max(3, Math.min(100, Number(row.conversion) || 0))}%` }),
        el('i'),
      ]),
      el('div', { class: 'why-transition-diagnostic' }, [
        el('div', { class: 'why-transition-problem' }, [
          el('span', {}, [lucideIcon('scan-search'), document.createTextNode(' WHAT BREAKS HERE')]),
          el('strong', { text: problem.label || 'Reason not captured' }),
          el('small', { text: evidenceLabel }),
        ]),
        el('div', { class: 'why-transition-solution' }, [
          el('span', {}, [lucideIcon('lightbulb'), document.createTextNode(' RECOMMENDED PLAY')]),
          el('p', { text: row.solution || 'Review this step with the sales manager.' }),
        ]),
        el('div', { class: 'why-transition-owner' }, [
          el('span', {}, [lucideIcon('user-round-cog'), document.createTextNode(row.owner || 'Sales')]),
          el('span', {}, [lucideIcon('crosshair'), document.createTextNode(row.kpi || '')]),
        ]),
      ]),
    ]);
  }

  function _whyJourneyMap(journey, index) {
    const card = el('article', { class: 'why-journey-card why-journey-' + journey.key }, [
      el('div', { class: 'why-journey-head' }, [
        el('div', { class: 'why-journey-number', text: String(index + 1).padStart(2, '0') }),
        el('div', { class: 'why-journey-title' }, [
          el('span', { text: 'JOURNEY ' + (index + 1) }),
          el('h3', { text: journey.label }),
          el('p', { text: journey.promise || '' }),
        ]),
        el('div', { class: 'why-journey-total' }, [
          el('strong', { text: _whyPct(journey.conversion) }),
          el('span', { text: _whyFmt(journey.start_count) + ' start → ' + _whyFmt(journey.end_count) + ' finish' }),
          el('small', { text: _whyFmt(journey.lost) + ' lost across this journey' }),
        ]),
      ]),
    ]);
    const stages = journey.stages || [];
    const transitions = journey.transitions || [];
    const flow = el('div', { class: 'why-journey-flow why-journey-flow-' + stages.length });
    stages.forEach((stage, nodeIndex) => {
      flow.appendChild(_whyJourneyNode(stage, journey, nodeIndex));
      if (transitions[nodeIndex]) flow.appendChild(_whyJourneyTransition(transitions[nodeIndex]));
    });
    card.appendChild(flow);
    return card;
  }

  function _whyMetaNameKey(value) {
    return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function _whyMetaAttribution(meta, report) {
    const crmRows = (report?.campaigns || []).map(row => {
      const bits = String(row.campaign || row.id || '').split(' · ');
      const rawName = bits[bits.length - 1] || '';
      return { ...row, raw_name: rawName, name_key: _whyMetaNameKey(rawName), campaign_id: String(row.campaign_id || '') };
    });
    const campaigns = (meta?.campaigns || []).map(row => {
      const key = _whyMetaNameKey(row.campaign_name);
      const campaignId = String(row.campaign_id || '');
      const crm = crmRows.find(candidate => candidate.campaign_id && candidate.campaign_id === campaignId)
        || crmRows.find(candidate => candidate.name_key && (
          candidate.name_key === key ||
          (candidate.name_key.length >= 7 && key.includes(candidate.name_key)) ||
          (key.length >= 7 && candidate.name_key.includes(key))
        )) || null;
      return { ...row, crm };
    });
    const matched = campaigns.filter(row => row.crm);
    const matchedSpend = matched.reduce((sum, row) => sum + (Number(row.spend) || 0), 0);
    const crmLeads = matched.reduce((sum, row) => sum + (Number(row.crm?.leads) || 0), 0);
    const crmTests = matched.reduce((sum, row) => sum + (Number(row.crm?.test_done) || 0), 0);
    const crmPaid = matched.reduce((sum, row) => sum + (Number(row.crm?.paid) || 0), 0);
    const crmRevenue = matched.reduce((sum, row) => sum + (Number(row.crm?.revenue_mad) || 0), 0);
    const campaignCoverage = Number(report?.scope?.campaign_coverage) || 0;
    const reliable = campaignCoverage >= 50 && matchedSpend > 0 && crmLeads > 0;
    const roasAvailable = String(meta?.currency || '').toUpperCase() === 'MAD';
    return {
      campaigns, matched_campaigns: matched.length, matched_spend: matchedSpend,
      crm_leads: crmLeads, crm_tests: crmTests, crm_paid: crmPaid, crm_revenue: crmRevenue,
      campaign_coverage: campaignCoverage, reliable, roas_available: roasAvailable,
      cost_per_test: reliable && crmTests ? matchedSpend / crmTests : null,
      cost_per_paid: reliable && crmPaid ? matchedSpend / crmPaid : null,
      roas: reliable && roasAvailable && matchedSpend ? crmRevenue / matchedSpend : null,
    };
  }

  function _whyMetaKpi(label, value, hint, icon, tone, locked) {
    return el('article', { class: 'why-meta-kpi why-meta-kpi-' + (tone || 'blue') + (locked ? ' is-locked' : '') }, [
      el('div', { class: 'why-meta-kpi-head' }, [el('span', { text: label }), lucideIcon(locked ? 'lock-keyhole' : icon)]),
      el('strong', { text: value }),
      el('small', { text: hint }),
    ]);
  }

  function _whyMetaResultLabel(type) {
    if (type === 'lead') return 'Meta leads';
    if (type === 'messaging_conversation') return 'New conversations';
    return type === 'mixed' ? 'Meta results' : 'No result event';
  }

  function _whyMetaBucketDaily(rows, maxBuckets = 42) {
    const source = Array.isArray(rows) ? rows : [];
    if (source.length <= maxBuckets) return source.map(row => ({ ...row, label: String(row.date_start || '').slice(5) }));
    const size = Math.ceil(source.length / maxBuckets);
    const out = [];
    for (let i = 0; i < source.length; i += size) {
      const chunk = source.slice(i, i + size);
      const first = chunk[0], last = chunk[chunk.length - 1];
      out.push({
        date_start: first.date_start, date_stop: last.date_stop || last.date_start,
        label: String(first.date_start || '').slice(5) + (chunk.length > 1 ? '…' + String(last.date_start || '').slice(8) : ''),
        spend: chunk.reduce((sum, row) => sum + (Number(row.spend) || 0), 0),
        results: chunk.reduce((sum, row) => sum + (Number(row.results) || 0), 0),
      });
    }
    return out;
  }

  function _whyMetaTrend(meta) {
    const currency = meta.currency || 'MAD';
    const rows = _whyMetaBucketDaily(meta.daily || []);
    const wrap = el('div', { class: 'why-meta-trend' });
    const head = el('div', { class: 'why-meta-subhead' }, [
      el('div', {}, [el('strong', { text: 'Daily spend pulse' }), el('span', { text: 'Bars = spend · result count sits above each active day' })]),
      el('span', { class: 'why-meta-live-pill', text: meta.cached ? '5 min cache' : 'Live from Meta' }),
    ]);
    wrap.appendChild(head);
    if (!rows.length) {
      wrap.appendChild(_whyEmpty('No ad delivery in this period.'));
      return wrap;
    }
    const maxSpend = Math.max(1, ...rows.map(row => Number(row.spend) || 0));
    const chart = el('div', { class: 'why-meta-chart' });
    rows.forEach((row, index) => {
      const height = Math.max(row.spend > 0 ? 5 : 1, Math.round((Number(row.spend) || 0) * 100 / maxSpend));
      const title = `${row.date_start || ''}${row.date_stop && row.date_stop !== row.date_start ? ' → ' + row.date_stop : ''} · ${_whyAdMoney(row.spend, currency)} · ${_whyFmt(row.results)} results`;
      chart.appendChild(el('div', { class: 'why-meta-day', title }, [
        row.results ? el('span', { class: 'why-meta-day-result', text: _whyFmt(row.results) }) : null,
        el('div', { class: 'why-meta-bar-slot' }, [el('span', { class: 'why-meta-spend-bar', style: `height:${height}%` })]),
        el('small', { text: (index % Math.max(1, Math.ceil(rows.length / 10)) === 0 || index === rows.length - 1) ? row.label : '' }),
      ]));
    });
    wrap.appendChild(chart);
    return wrap;
  }

  async function _whyMetaConnect() {
    try {
      const payload = await _whyJson('/api/meta-ads/oauth/start?json=1');
      const popup = window.open(payload.url, 'leads7_meta_ads', 'width=680,height=780,resizable=yes,scrollbars=yes');
      if (!popup) throw new Error('Popup blocked — allow popups for Leads7 and try again');
      popup.focus();
    } catch (err) { toast(err.message || 'Meta Ads connection failed', 'error'); }
  }

  async function _whyMetaSelectAccount(accountId) {
    if (!accountId) return;
    try {
      await _whyJson('/api/meta-ads/accounts/select', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ account_id: accountId }),
      });
      toast('Meta Ad Account selected', 'success');
      _whyLoadMeta(true);
    } catch (err) { toast(err.message || 'Could not select Ad Account', 'error'); }
  }

  async function _whyMetaDisconnect() {
    if (!window.confirm('Disconnect Meta Ads from 3LASH? Messenger and Instagram inbox will stay connected.')) return;
    try {
      await _whyJson('/api/meta-ads/disconnect', { method: 'POST' });
      WHY.meta = null;
      toast('Meta Ads disconnected', 'success');
      _whyLoadMeta(true);
    } catch (err) { toast(err.message || 'Could not disconnect Meta Ads', 'error'); }
  }

  function _whyRenderMeta(meta, report) {
    const root = document.getElementById('whyMetaAds');
    if (!root) return;
    clear(root);
    if (!meta) {
      root.appendChild(el('div', { class: 'why-meta-loading' }, [
        el('span', { class: 'why-loader-orb' }),
        el('div', {}, [el('strong', { text: 'Connecting Ads Manager…' }), el('span', { text: 'Spend, results, CPL and campaign efficiency.' })]),
      ]));
      return;
    }
    if (meta.error) {
      root.appendChild(el('div', { class: 'why-meta-error' }, [
        lucideIcon('triangle-alert'),
        el('div', {}, [el('strong', { text: 'Meta Ads data could not load' }), el('span', { text: meta.error })]),
        el('button', { type: 'button', class: 'why-meta-btn why-meta-btn-dark', onclick: () => _whyLoadMeta(true) }, [lucideIcon('refresh-cw'), el('span', { text: 'Retry' })]),
      ]));
      refreshIcons(root);
      return;
    }
    const status = meta.status || meta;
    if (!status.connected) {
      root.appendChild(el('div', { class: 'why-meta-connect' }, [
        el('div', { class: 'why-meta-connect-mark' }, [lucideIcon('megaphone')]),
        el('div', { class: 'why-meta-connect-copy' }, [
          el('span', { text: 'META BUSINESS ADS' }),
          el('h2', { text: 'Link Ads Manager to see what every lead costs' }),
          el('p', { text: 'Read-only access. The Facebook/Instagram inbox token stays separate and untouched.' }),
          el('div', { class: 'why-meta-connect-preview' }, [
            ['Spend', '—'], ['Meta CPL', '—'], ['Cost / Paid', 'UTM'], ['ROAS', 'UTM'],
          ].map(([label, value]) => el('span', {}, [el('small', { text: label }), el('b', { text: value })]))),
        ]),
        _isAdminClient()
          ? el('button', { type: 'button', class: 'why-meta-connect-btn', onclick: _whyMetaConnect }, [lucideIcon('link-2'), el('span', { text: status.needs_reconnect ? 'Reconnect Meta Ads' : 'Connect Meta Ads' })])
          : el('span', { class: 'why-meta-admin-note', text: 'An admin must connect the Ad Account.' }),
      ]));
      refreshIcons(root);
      return;
    }
    if (status.needs_account_selection || !status.selected_account) {
      const select = el('select', { class: 'why-meta-account-select' }, [el('option', { value: '', text: 'Choose an Ad Account…' })]);
      (status.accounts || []).forEach(account => select.appendChild(el('option', { value: account.id, text: `${account.name} · ${account.currency}` })));
      select.addEventListener('change', () => _whyMetaSelectAccount(select.value));
      root.appendChild(el('div', { class: 'why-meta-account-pick' }, [
        lucideIcon('building-2'),
        el('div', {}, [el('strong', { text: 'Choose the business Ad Account' }), el('span', { text: `${(status.accounts || []).length} accounts are available with this Meta login.` })]),
        _isAdminClient() ? select : el('span', { class: 'why-meta-admin-note', text: 'Waiting for admin selection.' }),
      ]));
      refreshIcons(root);
      return;
    }

    const account = status.selected_account;
    const totals = meta.totals || {};
    const currency = meta.currency || account.currency || 'MAD';
    const attribution = _whyMetaAttribution(meta, report);
    const selectedSource = report?.filters?.source || '';
    const header = el('div', { class: 'why-meta-header' }, [
      el('div', { class: 'why-meta-brand' }, [
        el('div', { class: 'why-meta-logo' }, [lucideIcon('megaphone')]),
        el('div', {}, [
          el('span', { text: 'META ADS · LIVE COST INTELLIGENCE' }),
          el('h2', { text: account.name || 'Meta Ad Account' }),
          el('p', { text: `${account.business_name || 'Business portfolio'} · ${currency} · ${meta.range?.from || ''} → ${meta.range?.to || ''}` }),
        ]),
      ]),
      el('div', { class: 'why-meta-controls' }, [
        (status.accounts || []).length > 1 && _isAdminClient() ? (() => {
          const select = el('select', { class: 'why-meta-account-select', title: 'Ad Account' });
          status.accounts.forEach(item => select.appendChild(el('option', { value: item.id, text: item.name })));
          select.value = account.id;
          select.addEventListener('change', () => _whyMetaSelectAccount(select.value));
          return select;
        })() : null,
        el('button', { type: 'button', class: 'why-meta-icon-btn', title: 'Refresh Meta data', onclick: () => _whyLoadMeta(true) }, [lucideIcon('refresh-cw')]),
        _isAdminClient() ? el('button', { type: 'button', class: 'why-meta-icon-btn why-meta-disconnect-btn', title: 'Disconnect Ads only', onclick: _whyMetaDisconnect }, [lucideIcon('unlink')]) : null,
      ]),
    ]);
    root.appendChild(header);
    if (status.expires_soon) root.appendChild(el('div', { class: 'why-meta-token-warning' }, [lucideIcon('clock-alert'), el('span', { text: 'Ads access expires soon. Reconnect Meta Ads before reporting stops.' })]));

    const resultLabel = _whyMetaResultLabel(totals.result_type);
    root.appendChild(el('div', { class: 'why-meta-kpi-grid' }, [
      _whyMetaKpi('Ad spend', _whyAdMoney(totals.spend, currency), `${_whyFmt(totals.impressions)} impressions`, 'wallet-cards', 'navy'),
      _whyMetaKpi(resultLabel, _whyFmt(totals.results), `${_whyFmt(totals.clicks)} clicks · ${_whyPct(totals.ctr)}`, 'users-round', 'cyan'),
      _whyMetaKpi('Meta cost / result', _whyAdMoney(totals.cpl, currency), totals.result_type === 'messaging_conversation' ? 'Cost per new conversation' : 'Cost per lead from Meta', 'badge-dollar-sign', 'green'),
      _whyMetaKpi('Cost / Test done', attribution.reliable ? _whyAdMoney(attribution.cost_per_test, currency) : 'UTM needed', attribution.reliable ? `${_whyFmt(attribution.crm_tests)} attributed tests` : `${_whyPct(attribution.campaign_coverage)} campaign tracking`, 'flask-conical', 'amber', !attribution.reliable),
      _whyMetaKpi('Cost / Paid', attribution.reliable ? _whyAdMoney(attribution.cost_per_paid, currency) : 'UTM needed', attribution.reliable ? `${_whyFmt(attribution.crm_paid)} attributed clients` : 'Never divide spend by untracked sales', 'credit-card', 'violet', !attribution.reliable),
      _whyMetaKpi('ROAS', attribution.reliable && attribution.roas != null ? (_whyFmt(attribution.roas, 2) + '×') : (attribution.reliable ? 'FX needed' : 'UTM needed'), attribution.reliable ? `${_whyMoney(attribution.crm_revenue)} revenue · spend is ${currency}` : 'Revenue ÷ matched campaign spend', 'trending-up', 'rose', !attribution.reliable || !attribution.roas_available),
    ]));

    root.appendChild(el('div', { class: 'why-meta-split' }, [
      _whyMetaTrend(meta),
      el('div', { class: 'why-meta-efficiency' }, [
        el('div', { class: 'why-meta-subhead' }, [el('div', {}, [el('strong', { text: 'Media efficiency' }), el('span', { text: 'Fast signals before the CRM outcome matures' })])]),
        el('div', { class: 'why-meta-efficiency-grid' }, [
          ['CPC', _whyAdMoney(totals.cpc, currency)],
          ['CPM', _whyAdMoney(totals.cpm, currency)],
          ['CTR', _whyPct(totals.ctr)],
          ['Reach', _whyFmt(totals.reach)],
          ['Link clicks', _whyFmt(totals.inline_link_clicks)],
          ['Campaigns', _whyFmt((meta.campaigns || []).length)],
        ].map(([label, value]) => el('span', {}, [el('small', { text: label }), el('b', { text: value })]))),
      ]),
    ]));

    const campaigns = attribution.campaigns || [];
    const campaignBlock = el('div', { class: 'why-meta-campaigns' }, [
      el('div', { class: 'why-meta-subhead' }, [
        el('div', {}, [el('strong', { text: 'Campaign money map' }), el('span', { text: 'Where the budget goes, what Meta returns, and what reached Paid in Leads7.' })]),
        el('span', { class: 'why-meta-match-pill ' + (attribution.reliable ? 'is-good' : 'is-low'), text: `${_whyFmt(attribution.matched_campaigns)} matched · ${_whyPct(attribution.campaign_coverage)} UTM` }),
      ]),
    ]);
    if (campaigns.length) {
      const maxSpend = Math.max(1, ...campaigns.map(row => Number(row.spend) || 0));
      const spendMap = el('div', { class: 'why-meta-spend-map' });
      campaigns.slice(0, 6).forEach((row, index) => spendMap.appendChild(el('div', { class: 'why-meta-spend-row' }, [
        el('span', { class: 'why-meta-rank', text: String(index + 1).padStart(2, '0') }),
        el('div', { class: 'why-meta-spend-name' }, [el('strong', { text: row.campaign_name || 'Campaign' }), el('span', { text: `${_whyFmt(row.results)} results · ${_whyAdMoney(row.cpl, currency)} / result` })]),
        el('div', { class: 'why-meta-spend-track' }, [el('span', { style: `width:${Math.max(2, (Number(row.spend) || 0) * 100 / maxSpend)}%` })]),
        el('b', { text: _whyAdMoney(row.spend, currency) }),
      ])));
      campaignBlock.appendChild(spendMap);
      const tableWrap = el('div', { class: 'why-table-wrap why-meta-table-wrap' });
      const table = el('table', { class: 'why-table why-meta-table' });
      table.appendChild(el('thead', {}, [el('tr', {}, ['Campaign', 'Spend', 'Results', 'Meta CPL', 'CTR', 'CRM leads', 'Tests', 'Paid', 'ROAS'].map(text => el('th', { text })))]));
      const body = el('tbody');
      campaigns.forEach(row => body.appendChild(el('tr', {}, [
        el('td', {}, [el('strong', { text: row.campaign_name || 'Campaign' }), el('small', { text: row.campaign_id ? ('ID ' + row.campaign_id) : '' })]),
        el('td', { text: _whyAdMoney(row.spend, currency) }),
        el('td', { text: _whyFmt(row.results) }),
        el('td', { text: _whyAdMoney(row.cpl, currency) }),
        el('td', { text: _whyPct(row.ctr) }),
        el('td', { text: row.crm ? _whyFmt(row.crm.leads) : '—' }),
        el('td', { text: row.crm ? _whyFmt(row.crm.test_done) : '—' }),
        el('td', { text: row.crm ? _whyFmt(row.crm.paid) : '—' }),
        el('td', { text: attribution.roas_available && row.crm && Number(row.spend) > 0 ? (_whyFmt(Number(row.crm.revenue_mad || 0) / Number(row.spend), 2) + '×') : '—' }),
      ])));
      table.appendChild(body); tableWrap.appendChild(table); campaignBlock.appendChild(tableWrap);
    } else campaignBlock.appendChild(_whyEmpty('No campaign spent money in this period.'));
    root.appendChild(campaignBlock);

    const tracking = el('div', { class: 'why-meta-tracking ' + (attribution.reliable ? 'is-good' : 'is-warning') }, [
      lucideIcon(attribution.reliable ? 'badge-check' : 'shield-alert'),
      el('div', {}, [
        el('strong', { text: attribution.reliable ? 'CRM attribution is decision-ready' : 'Meta CPL is ready; downstream cost is still locked' }),
        el('p', { text: attribution.reliable
          ? `${_whyFmt(attribution.crm_leads)} matched leads connect ad spend to Test, Paid and Revenue.`
          : `Only ${_whyFmt(report?.scope?.campaigns_tracked || 0)} / ${_whyFmt(report?.scope?.leads || 0)} Leads7 leads carry utm_campaign. Add URL parameters before moving budget based on Cost/Paid or ROAS.` }),
        selectedSource ? el('small', { text: `Source filter “${selectedSource}” scopes CRM outcomes only; Meta spend remains the full selected Ad Account for the date range.` }) : null,
      ]),
      _isAdminClient() ? el('button', { type: 'button', class: 'why-meta-utm-btn', onclick: () => {
        const value = 'utm_source={{site_source_name}}&utm_medium=paid_social&utm_campaign={{campaign.name}}&campaign_id={{campaign.id}}&adset_id={{adset.id}}&ad_id={{ad.id}}';
        navigator.clipboard?.writeText(value).then(() => toast('Meta UTM template copied', 'success')).catch(() => toast(value, 'info'));
      } }, [lucideIcon('copy'), el('span', { text: 'Copy UTM template' })]) : null,
    ]);
    root.appendChild(tracking);
    refreshIcons(root);
  }

  async function _whyLoadMeta(force) {
    const seq = ++WHY.metaRequestSeq;
    _whyRenderMeta(null, WHY.report);
    const params = new URLSearchParams();
    const from = document.getElementById('whyDateFrom')?.value;
    const to = document.getElementById('whyDateTo')?.value;
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (force) params.set('force', '1');
    try {
      const meta = await _whyJson('/api/meta-ads/insights?' + params.toString());
      if (seq !== WHY.metaRequestSeq) return;
      WHY.meta = meta;
      _whyRenderMeta(meta, WHY.report);
    } catch (err) {
      if (seq !== WHY.metaRequestSeq) return;
      WHY.meta = { error: err.message || 'Meta Ads data unavailable' };
      _whyRenderMeta(WHY.meta, WHY.report);
    }
  }

  function _whyRenderReport(report) {
    const root = document.getElementById('whyDashboard');
    if (!root) return;
    clear(root);
    const k = report.kpis || {};
    if (report.scope?.leads) {
      const kpis = el('div', { class: 'why-kpi-grid' }, [
        _whyKpi('Leads analyzed', _whyFmt(k.leads), _whyPct(report.scope.customer_signal_coverage ?? report.scope.conversation_coverage) + ' customer signal', 'ink', 'users'),
        _whyKpi('Paid conversion', _whyPct(k.paid_rate), _whyFmt(k.paid) + ' clients paid', 'green', 'badge-check'),
        _whyKpi('Revenue', _whyMoney(k.revenue_mad), _whyMoney(k.revenue_per_lead) + ' / lead', 'blue', 'banknote'),
        _whyKpi('Test → Proforma', _whyPct(k.test_to_proforma), _whyFmt(k.test_done) + ' tests done', 'amber', 'file-check-2'),
        _whyKpi('Proforma → Paid', _whyPct(k.proforma_to_paid), _whyFmt(k.proforma) + ' proformas', 'violet', 'credit-card'),
        _whyKpi('Time to paid', k.median_days_to_paid == null ? '—' : (_whyFmt(k.median_days_to_paid, 1) + ' days'), 'Median from lead creation', 'rose', 'timer'),
        // Speed to lead (plan item 7): share of leads called within 15 min.
        _whyKpi('Called ≤ 15 min', _whyPct(k.called_within_15_rate), 'Median 1st call ' + _whyMinsOrDash(k.median_minutes_to_first_call), 'blue', 'phone-outgoing'),
      ]);
      root.appendChild(kpis);
    }

    const metaAdsSection = el('section', { id: 'whyMetaAds', class: 'why-panel why-meta-panel' });
    root.appendChild(metaAdsSection);
    _whyRenderMeta(WHY.meta, report);

    if (!report.scope?.leads) {
      root.appendChild(el('div', { class: 'why-zero' }, [
        lucideIcon('search-x'), el('h2', { text: 'Ma kaynach CRM data f had filter' }),
        el('p', { text: 'Meta Ads spend can still appear above. Wesse3 date range wla 7yed source filter for funnel analysis.' }),
      ]));
      refreshIcons(root);
      return;
    }

    const ai = el('section', { id: 'whyAiDecision', class: 'why-ai-panel' });
    root.appendChild(ai);
    _whyRenderAi(report.ai, report.ai_meta);

    // The journey map now lives on its own Objectif sub-page (it is the one
    // view the whole sales team can read, per-agent). Leave a pointer here so
    // the Decision room does not simply lose it.
    const funnelSection = el('section', { class: 'why-panel why-funnel-panel' }, [
      _whySectionHead('target', 'FUNNEL DIAGNOSTIC MAP', 'Kayn f Objectif', 'Two journeys, every leak and the play to fix it — with a per-agent filter.'),
      el('button', {
        type: 'button',
        class: 'why-apply why-funnel-jump',
        onclick: () => _whySetSubpage('objectif'),
      }, [lucideIcon('target'), el('span', { text: 'Ouvrir Objectif' })]),
    ]);
    root.appendChild(funnelSection);

    const sourceSection = el('section', { class: 'why-panel' }, [
      _whySectionHead('trophy', 'SOURCE × WHY INTELLIGENCE', 'The numbers — and the psychology behind them', 'See which source wins, why its people register, what they ask, and exactly where its funnel leaks.'),
    ]);
    const winnerGrid = el('div', { class: 'why-winner-grid' });
    (report.source_winners || []).forEach((winner, index) => {
      let formatted = _whyFmt(winner.value, 1);
      if (winner.key === 'conversion') formatted = _whyPct(winner.value);
      if (winner.key === 'value') formatted = _whyMoney(winner.value);
      if (winner.key === 'volume') formatted = _whyFmt(winner.value);
      winnerGrid.appendChild(el('article', { class: 'why-winner-card why-winner-' + winner.key }, [
        el('div', { class: 'why-winner-card-top' }, [
          el('span', { text: winner.label }),
          lucideIcon(index === 0 ? 'award' : (winner.key === 'conversion' ? 'badge-check' : (winner.key === 'value' ? 'gem' : 'waves'))),
        ]),
        el('strong', { text: winner.source ? _whySourceLabel(winner.source) : '—' }),
        el('div', { class: 'why-winner-card-value' }, [el('b', { text: formatted }), el('span', { text: winner.metric || '' })]),
      ]));
    });
    sourceSection.appendChild(winnerGrid);

    const sourceCoverage = report.scope?.customer_signal_coverage ?? report.scope?.conversation_coverage;
    sourceSection.appendChild(el('div', { class: 'why-source-psych-head' }, [
      el('div', {}, [
        el('span', { text: 'SOURCE DEEP DIVES' }),
        el('strong', { text: 'The secret behind every source' }),
        el('p', { text: 'Motives combine registration-form answers and inbound WhatsApp messages. Percentages are share of all leads from that source.' }),
      ]),
      el('div', { class: 'why-source-signal-badge' }, [lucideIcon('radar'), el('strong', { text: _whyPct(sourceCoverage) }), el('span', { text: 'signal coverage' })]),
    ]));
    const sourceDetailGrid = el('div', { class: 'why-source-detail-grid' });
    (report.source_insights || []).slice(0, 6).forEach((row, index) => sourceDetailGrid.appendChild(_whySourceDetailCard(row, index)));
    sourceSection.appendChild(sourceDetailGrid.childNodes.length ? sourceDetailGrid : _whyEmpty('No source psychology is available for this filter.'));

    sourceSection.appendChild(el('div', { class: 'why-source-table-head' }, [
      el('strong', { text: 'All sources · audited numbers' }),
      el('span', { text: 'Ranked with sample-size protection, not raw conversion alone.' }),
    ]));
    const sourceWrap = el('div', { class: 'why-table-wrap' });
    const sourceTable = el('table', { class: 'why-table why-source-table' });
    sourceTable.appendChild(el('thead', {}, [el('tr', {}, ['Source', 'Leads', 'Test → PF', 'PF → Paid', 'Paid', 'Revenue', 'Value / lead', 'Confidence'].map(h => el('th', { text: h })))]));
    const sourceBody = el('tbody');
    (report.sources || []).forEach((row, index) => sourceBody.appendChild(el('tr', { class: index === 0 ? 'why-winner-row' : '' }, [
      el('td', {}, [index === 0 ? el('span', { class: 'why-rank-crown' }, [lucideIcon('crown')]) : el('span', { class: 'why-rank-num', text: '#' + (index + 1) }), el('strong', { text: _whySourceLabel(row.source || row.id) })]),
      el('td', { text: _whyFmt(row.leads) }),
      el('td', { text: _whyPct(row.test_to_proforma) }),
      el('td', { text: _whyPct(row.proforma_to_paid) }),
      el('td', {}, [el('strong', { text: _whyPct(row.paid_rate) }), el('small', { text: _whyFmt(row.paid) + ' paid' })]),
      el('td', { text: _whyMoney(row.revenue_mad) }),
      el('td', { text: _whyMoney(row.revenue_per_lead) }),
      el('td', {}, [el('span', { class: 'why-confidence why-confidence-' + row.confidence, text: _whyConfidenceLabel(row.confidence) })]),
    ])));
    sourceTable.appendChild(sourceBody);
    sourceWrap.appendChild(sourceTable);
    sourceSection.appendChild(sourceWrap);

    // Same cohort, grouped by marketing channel (server lib/leads/channels.js):
    // the site visit, the coded WhatsApp bio links, the ads. Zero rows stay so
    // a channel that brings nobody is visible.
    const channelRows = report.channels || [];
    if (channelRows.length) {
      sourceSection.appendChild(el('div', { class: 'why-source-table-head' }, [
        el('strong', { text: 'By channel · leads → tests → paid' }),
        el('span', { text: 'Each lead counted once, under the channel that first brought it.' }),
      ]));
      const channelWrap = el('div', { class: 'why-table-wrap' });
      const channelTable = el('table', { class: 'why-table why-channel-table' });
      channelTable.appendChild(el('thead', {}, [el('tr', {}, ['Channel', 'Leads', 'Tests booked', 'Tests done', 'Paid', 'Revenue', 'Called ≤ 15 min'].map(h => el('th', { text: h })))]));
      const channelBody = el('tbody');
      channelRows.forEach(row => channelBody.appendChild(el('tr', { class: row.leads ? '' : 'why-channel-empty' }, [
        el('td', {}, [el('strong', { text: row.label || row.channel })]),
        el('td', { text: _whyFmt(row.leads) }),
        el('td', { text: _whyFmt(row.test_scheduled) }),
        el('td', { text: _whyFmt(row.test_done) }),
        el('td', {}, [el('strong', { text: _whyFmt(row.paid) }), el('small', { text: _whyPct(row.paid_rate) + ' of leads' })]),
        el('td', { text: _whyMoney(row.revenue_mad) }),
        el('td', {}, [el('strong', { text: _whyPct(row.called_within_15_rate) }), el('small', { text: 'median ' + _whyMinsOrDash(row.median_minutes_to_first_call) })]),
      ])));
      channelTable.appendChild(channelBody);
      channelWrap.appendChild(channelTable);
      sourceSection.appendChild(channelWrap);
    }

    const campaignRows = report.campaigns || [];
    const campaignCoverage = Number(report.scope?.campaign_coverage) || 0;
    const campaignBlock = el('div', { class: 'why-campaign-block' });
    campaignBlock.appendChild(el('div', { class: 'why-campaign-head' }, [
      el('div', {}, [
        el('strong', { text: 'Campaign / UTM drilldown' }),
        el('span', { text: 'Paid-media winner only when campaign tracking exists.' }),
      ]),
      el('span', { class: 'why-tracking-pill ' + (campaignCoverage < 50 ? 'why-tracking-low' : ''), text: _whyPct(campaignCoverage) + ' tracked' }),
    ]));
    if (campaignRows.length) {
      const campaignWrap = el('div', { class: 'why-table-wrap' });
      const campaignTable = el('table', { class: 'why-table why-campaign-table' });
      campaignTable.appendChild(el('thead', {}, [el('tr', {}, ['Campaign', 'Leads', 'Test → PF', 'PF → Paid', 'Paid', 'Revenue', 'Value / lead', 'Confidence'].map(h => el('th', { text: h })))]));
      const campaignBody = el('tbody');
      campaignRows.forEach((row, index) => campaignBody.appendChild(el('tr', {}, [
        el('td', {}, [el('span', { class: 'why-rank-num', text: '#' + (index + 1) }), el('strong', { text: row.campaign || row.id || 'Unknown campaign' })]),
        el('td', { text: _whyFmt(row.leads) }),
        el('td', { text: _whyPct(row.test_to_proforma) }),
        el('td', { text: _whyPct(row.proforma_to_paid) }),
        el('td', {}, [el('strong', { text: _whyPct(row.paid_rate) }), el('small', { text: _whyFmt(row.paid) + ' paid' })]),
        el('td', { text: _whyMoney(row.revenue_mad) }),
        el('td', { text: _whyMoney(row.revenue_per_lead) }),
        el('td', {}, [el('span', { class: 'why-confidence why-confidence-' + row.confidence, text: _whyConfidenceLabel(row.confidence) })]),
      ])));
      campaignTable.appendChild(campaignBody);
      campaignWrap.appendChild(campaignTable);
      campaignBlock.appendChild(campaignWrap);
    } else {
      campaignBlock.appendChild(_whyEmpty('No utm_campaign captured in this cohort. Add UTM tracking before calling any ad a winner.'));
    }
    if (campaignCoverage < 50) campaignBlock.appendChild(el('p', { class: 'why-tracking-warning', text: 'Tracking gap: ' + _whyFmt(report.scope?.campaigns_tracked || 0) + ' / ' + _whyFmt(report.scope?.leads || 0) + ' leads carry utm_campaign. Source conclusions are usable; campaign/ad conclusions remain directional.' }));
    sourceSection.appendChild(campaignBlock);
    root.appendChild(sourceSection);

    const vocSection = el('section', { class: 'why-panel why-voc-panel' }, [
      _whySectionHead('messages-square', 'VOICE OF CUSTOMER', 'What people actually want, ask and resist', 'Categories are counted once per lead; short quotes are anonymized evidence.'),
      el('div', { class: 'why-voice-grid' }, [
        _whyVoiceCard('Why English?', 'Motivations', report.voice_of_customer?.motives, 'motive'),
        _whyVoiceCard('What do they ask?', 'Questions', report.voice_of_customer?.questions, 'question'),
        _whyVoiceCard('Why do they hesitate?', 'Objections', report.voice_of_customer?.objections, 'objection'),
      ]),
    ]);
    root.appendChild(vocSection);

    const dropSection = el('section', { class: 'why-panel' }, [
      _whySectionHead('circle-off', 'DROP-OFF FORENSICS', 'Why the last two stages leak', 'Explicit objection first; silence and missing follow-up only when no explicit reason exists.'),
    ]);
    const dropGrid = el('div', { class: 'why-drop-grid' });
    (report.dropoffs || []).forEach(drop => {
      const card = el('article', { class: 'why-drop-card' }, [
        el('div', { class: 'why-drop-top' }, [
          el('div', {}, [el('span', { text: drop.label }), el('strong', { text: _whyFmt(drop.count) + ' stuck' })]),
          el('div', { class: 'why-drop-rate' }, [el('strong', { text: _whyPct(drop.share_of_stage) }), el('span', { text: 'drop-off' })]),
        ]),
        el('div', { class: 'why-drop-meta' }, [
          el('span', {}, [lucideIcon('clock-3'), el('b', { text: drop.median_days_stuck == null ? '—' : (_whyFmt(drop.median_days_stuck, 1) + 'd median') })]),
          el('span', {}, [lucideIcon('alarm-clock'), el('b', { text: _whyPct(drop.stale_3d_rate) + ' stale 3d+' })]),
        ]),
      ]);
      const reasons = el('div', { class: 'why-reason-list' });
      (drop.reasons || []).slice(0, 6).forEach(reason => reasons.appendChild(el('div', { class: 'why-reason-row' }, [
        el('div', {}, [el('strong', { text: reason.label }), el('span', { text: _whyFmt(reason.count) })]),
        el('div', { class: 'why-bar-track' }, [el('span', { class: 'why-bar-fill', style: `width:${Math.min(100, Number(reason.share) || 0)}%` })]),
      ])));
      card.appendChild(reasons.childNodes.length ? reasons : _whyEmpty('No reason captured.'));
      if (drop.samples?.[0]) card.appendChild(el('q', { class: 'why-drop-quote', text: drop.samples[0] }));
      dropGrid.appendChild(card);
    });
    dropSection.appendChild(dropGrid);
    root.appendChild(dropSection);

    const signalSection = el('section', { class: 'why-panel' }, [
      _whySectionHead('zap', 'ACTIVATION SIGNALS', 'What appears in conversations that convert', 'Directional correlation. Turn the strongest signal into a controlled script or ad test.'),
    ]);
    const signalList = el('div', { class: 'why-signal-list' });
    (report.activation_signals || []).slice(0, 8).forEach(row => {
      const positive = Number(row.uplift_pp) >= 0;
      signalList.appendChild(el('div', { class: 'why-signal-row' }, [
        el('div', { class: 'why-signal-name' }, [el('strong', { text: row.label }), el('span', { text: _whyFmt(row.exposed) + ' exposed · ' + _whyConfidenceLabel(row.confidence) })]),
        el('div', { class: 'why-signal-rates' }, [
          el('span', {}, [el('small', { text: 'With' }), el('b', { text: _whyPct(row.paid_rate_with) })]),
          el('span', {}, [el('small', { text: 'Without' }), el('b', { text: _whyPct(row.paid_rate_without) })]),
        ]),
        el('span', { class: 'why-uplift ' + (positive ? 'why-uplift-up' : 'why-uplift-down'), text: (positive ? '+' : '') + _whyFmt(row.uplift_pp, 1) + ' pp' }),
      ]));
    });
    signalSection.appendChild(signalList.childNodes.length ? signalList : _whyEmpty('More conversation coverage is needed to calculate lift.'));
    root.appendChild(signalSection);

    const agentSection = el('section', { class: 'why-panel' }, [
      _whySectionHead('users-round', 'TEAM CONVERSION', 'Who wins, who needs a playbook', 'Compare stage conversion before judging people; volume and confidence stay visible.'),
    ]);
    const agentWrap = el('div', { class: 'why-table-wrap' });
    const agentTable = el('table', { class: 'why-table why-agent-table' });
    agentTable.appendChild(el('thead', {}, [el('tr', {}, ['Agent', 'Leads', 'Paid', 'Test → PF', 'PF → Paid', 'Revenue', 'Response', '1st call', 'Readout'].map(h => el('th', { text: h })))]));
    const agentBody = el('tbody');
    (report.agents || []).forEach(row => {
      const flagLabel = row.performance_flag === 'outperforming' ? 'Outperforming' : (row.performance_flag === 'needs_attention' ? 'Needs attention' : 'Stable');
      agentBody.appendChild(el('tr', {}, [
        el('td', {}, [el('span', { class: 'why-agent-avatar', text: String(userLabel(row.agent_id || '?')).charAt(0).toUpperCase() }), el('strong', { text: userLabel(row.agent_id || 'Unassigned') })]),
        el('td', { text: _whyFmt(row.leads) }),
        el('td', {}, [el('strong', { text: _whyPct(row.paid_rate) }), el('small', { text: _whyFmt(row.paid) + ' paid' })]),
        el('td', { text: _whyPct(row.test_to_proforma) }),
        el('td', { text: _whyPct(row.proforma_to_paid) }),
        el('td', { text: _whyMoney(row.revenue_mad) }),
        el('td', { text: _whyMins(row.median_response_minutes) }),
        el('td', {}, [el('strong', { text: _whyMinsOrDash(row.median_minutes_to_first_call) }), el('small', { text: _whyPct(row.called_within_15_rate) + ' ≤ 15 min' })]),
        el('td', {}, [el('span', { class: 'why-agent-flag why-agent-' + row.performance_flag, text: flagLabel }), el('small', { text: (row.delta_vs_team_paid_pp >= 0 ? '+' : '') + _whyFmt(row.delta_vs_team_paid_pp, 1) + ' pp vs team' })]),
      ]));
    });
    agentTable.appendChild(agentBody);
    agentWrap.appendChild(agentTable);
    agentSection.appendChild(agentWrap);
    // Speed to lead by week (plan item 7): leads of the week, called within
    // 15 min, median minutes to the first outbound call.
    const speedWeeks = report.speed_to_lead?.by_week || [];
    if (speedWeeks.length) {
      agentSection.appendChild(el('div', { class: 'why-source-table-head' }, [
        el('strong', { text: 'Speed to lead · by week' }),
        el('span', { text: report.speed_to_lead.definition || '' }),
      ]));
      const speedWrap = el('div', { class: 'why-table-wrap' });
      const speedTable = el('table', { class: 'why-table why-speed-table' });
      speedTable.appendChild(el('thead', {}, [el('tr', {}, ['Week of', 'Leads', 'Called', 'Called ≤ 15 min', 'Median 1st call'].map(h => el('th', { text: h })))]));
      const speedBody = el('tbody');
      speedWeeks.forEach(row => speedBody.appendChild(el('tr', {}, [
        el('td', {}, [el('strong', { text: row.id })]),
        el('td', { text: _whyFmt(row.leads) }),
        el('td', { text: _whyPct(row.called_rate) }),
        el('td', {}, [el('strong', { text: _whyPct(row.called_within_15_rate) }), el('small', { text: _whyFmt(row.called_within_15) + ' leads' })]),
        el('td', { text: _whyMinsOrDash(row.median_minutes_to_first_call) }),
      ])));
      speedTable.appendChild(speedBody);
      speedWrap.appendChild(speedTable);
      agentSection.appendChild(speedWrap);
    }
    root.appendChild(agentSection);

    const botSection = el('section', { class: 'why-panel why-bot-panel' }, [
      _whySectionHead('bot', 'BOT × TEAM', 'Assistance, not attribution', 'Bot-assisted means the bot touched the journey; it does not claim the bot caused the sale.'),
    ]);
    const botGrid = el('div', { class: 'why-bot-grid' });
    (report.bot_vs_team || []).forEach((segment, index) => botGrid.appendChild(el('article', { class: 'why-bot-card ' + (index === 0 ? 'why-bot-assisted' : '') }, [
      el('div', { class: 'why-bot-title' }, [lucideIcon(index === 0 ? 'bot' : 'headset'), el('h3', { text: segment.label })]),
      el('strong', { text: _whyPct(segment.paid_rate) }),
      el('span', { text: _whyFmt(segment.paid) + ' paid / ' + _whyFmt(segment.leads) + ' leads' }),
      el('div', { class: 'why-bot-metrics' }, [
        el('span', {}, [el('small', { text: 'Revenue' }), el('b', { text: _whyMoney(segment.revenue_mad) })]),
        el('span', {}, [el('small', { text: 'Median reply' }), el('b', { text: _whyMins(segment.median_response_minutes) })]),
      ]),
    ])));
    botSection.appendChild(botGrid);
    root.appendChild(botSection);

    const q = report.quality || {};
    root.appendChild(el('section', { class: 'why-quality' }, [
      el('div', {}, [lucideIcon('shield-check'), el('div', {}, [el('strong', { text: 'Data confidence: ' + _whyConfidenceLabel(q.confidence) }), el('span', { text: _whyPct(report.scope.customer_signal_coverage ?? report.scope.conversation_coverage) + ' customer-signal coverage · ' + _whyFmt(q.no_motive_captured) + ' motives still unknown' })])]),
      el('p', { text: (q.notes || []).join(' · ') }),
    ]));

    refreshIcons(root);
  }

  async function _whyAnalyze(force) {
    if (!WHY.report || WHY.analyzing) return;
    WHY.analyzing = true;
    const status = document.getElementById('whyStatus');
    const btn = document.getElementById('whyAiRefreshBtn');
    status.textContent = 'AI analyzing';
    status.classList.add('why-status-busy');
    btn?.classList.add('is-loading');
    _whyRenderAi(null, null);
    try {
      const payload = await _whyJson('/api/why-analytics/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from: document.getElementById('whyDateFrom')?.value || null,
          to: document.getElementById('whyDateTo')?.value || null,
          source: document.getElementById('whySource')?.value || null,
          force: !!force,
        }),
      });
      WHY.report.ai = payload.analysis;
      WHY.report.ai_meta = { generated_at: payload.generated_at, model: payload.model, cached: payload.cached };
      _whyRenderAi(payload.analysis, WHY.report.ai_meta);
      status.textContent = payload.cached ? 'AI insight · cached' : 'AI insight · fresh';
    } catch (err) {
      _whyRenderAi(null, null);
      const aiRoot = document.getElementById('whyAiDecision');
      aiRoot?.appendChild(el('div', { class: 'why-ai-error', text: 'AI layer ma tchargatch: ' + (err.message || err) + '. Facts li ta7t mazalin valid.' }));
      status.textContent = 'Facts ready · AI unavailable';
    } finally {
      WHY.analyzing = false;
      status.classList.remove('why-status-busy');
      btn?.classList.remove('is-loading');
      refreshIcons(document.getElementById('whyAnalyticsView'));
    }
  }

  async function _whyLoadSources() {
    if (WHY.sourcesLoaded) return;
    try {
      const payload = await _whyJson('/api/funnel/sources');
      const select = document.getElementById('whySource');
      const current = select?.value || '';
      (payload.sources || []).forEach(row => select?.appendChild(el('option', { value: row.source, text: _whySourceLabel(row.source) + ' · ' + _whyFmt(row.n) })));
      if (select) select.value = current;
      WHY.sourcesLoaded = true;
    } catch { /* main report still works without dropdown enrichment */ }
  }

  async function _whyLoad() {
    const seq = ++WHY.requestSeq;
    WHY.meta = null;
    const loading = document.getElementById('whyLoading');
    const error = document.getElementById('whyError');
    const status = document.getElementById('whyStatus');
    loading?.classList.remove('hidden');
    error?.classList.add('hidden');
    status.textContent = 'Connecting data';
    status.classList.add('why-status-busy');
    try {
      const params = new URLSearchParams();
      const from = document.getElementById('whyDateFrom')?.value;
      const to = document.getElementById('whyDateTo')?.value;
      const source = document.getElementById('whySource')?.value;
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (source) params.set('source', source);
      const report = await _whyJson('/api/why-analytics?' + params.toString());
      if (seq !== WHY.requestSeq) return;
      WHY.report = report;
      WHY.meta = report.meta_ads || null;
      _whyRenderReport(report);
      if (!report.meta_ads) _whyLoadMeta(false);
      const note = document.getElementById('whyRangeNote');
      if (note) note.textContent = `${report.scope?.definition || ''} ${_whyFmt(report.scope?.leads)} leads.`;
      status.textContent = 'Facts ready';
      if (!report.ai && report.scope?.leads) setTimeout(() => _whyAnalyze(false), 150);
    } catch (err) {
      if (seq !== WHY.requestSeq) return;
      clear(document.getElementById('whyDashboard'));
      error.textContent = err.message || 'Could not load 3LASH analytics.';
      error.classList.remove('hidden');
      status.textContent = 'Load failed';
    } finally {
      if (seq === WHY.requestSeq) {
        loading?.classList.add('hidden');
        status.classList.remove('why-status-busy');
      }
    }
  }

  // ── Objectif sub-page ──────────────────────────────────────────────────────
  // Same journey map as the Decision room, but standalone and owner-scoped:
  // every agent reads their own funnel, admin / super_sales read the team and
  // can drill into one agent. The scoping is enforced by the API, not here.

  function _whyObjSetRange(kind) {
    const today = new Date();
    let from = new Date(today.getFullYear(), today.getMonth(), 1);
    if (kind !== 'mtd') {
      const days = Math.max(1, Number(kind) || 30);
      from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - days + 1);
    }
    const fromEl = document.getElementById('whyObjDateFrom');
    const toEl = document.getElementById('whyObjDateTo');
    if (fromEl) fromEl.value = _whyIsoLocal(from);
    if (toEl) toEl.value = _whyIsoLocal(today);
    document.querySelectorAll('[data-obj-range]').forEach(btn => btn.classList.toggle('why-preset-active', btn.dataset.objRange === String(kind)));
  }

  async function _whyObjLoadSources() {
    if (WHY_OBJ.sourcesLoaded) return;
    try {
      const payload = await _whyJson('/api/funnel/sources');
      const select = document.getElementById('whyObjSource');
      const current = select?.value || '';
      (payload.sources || []).forEach(row => select?.appendChild(el('option', { value: row.source, text: _whySourceLabel(row.source) + ' · ' + _whyFmt(row.n) })));
      if (select) select.value = current;
      WHY_OBJ.sourcesLoaded = true;
    } catch { /* the journeys still render without the dropdown */ }
  }

  function _whyObjSyncAgents(payload) {
    const field = document.getElementById('whyObjAgentField');
    const select = document.getElementById('whyObjAgent');
    if (!field || !select) return;
    field.classList.toggle('hidden', !payload?.can_filter_agents);
    if (!payload?.can_filter_agents || WHY_OBJ.agentsLoaded) return;
    const current = select.value || '';
    (payload.agents || []).forEach(a => select.appendChild(el('option', { value: a.username, text: a.displayName })));
    select.value = current;
    WHY_OBJ.agentsLoaded = true;
  }

  function _whyObjScopeLabel(payload) {
    const owner = payload?.filters?.agent_id || '';
    if (!owner) return "Toute l'équipe";
    const known = (payload?.agents || []).find(a => a.username === owner);
    if (known) return known.displayName;
    // An agent gets no roster back, so fall back to their own identity.
    if (owner === state.me?.username) return state.me?.displayName || owner;
    return owner;
  }

  // How old the cohort is, in days. A window is not one age: on 01→10 Aug the
  // oldest lead is 9 days old and the youngest is brand new. Judging the whole
  // window by its youngest lead ("this month" would read as 0 days old, all
  // month long) understates it as badly as judging it by its oldest overstates
  // it, so the readiness call is made on the MIDPOINT and both ends are shown.
  function _whyObjCohortAge(range) {
    const from = range?.from ? new Date(String(range.from) + 'T00:00:00') : null;
    const to = range?.to ? new Date(String(range.to) + 'T00:00:00') : null;
    if (!to || isNaN(to.getTime())) return null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const age = date => Math.max(0, Math.floor((today - date) / 86400_000));
    const youngest = age(to);
    const oldest = (from && !isNaN(from.getTime())) ? Math.max(youngest, age(from)) : youngest;
    return { youngest, oldest, mid: Math.floor((youngest + oldest) / 2) };
  }

  // Measured on real data (739 leads created in one June week): a cohort holds
  // ~27% of its final test-done count at 1 day, ~55% at 3, ~79% at 7, ~84% at
  // 14, ~94% at 30, and stops moving near 60. Reading a young cohort as if it
  // were finished is the easiest way to misjudge the team, so the board says
  // the age out loud instead of leaving it implicit.
  function _whyObjMaturityNote(range) {
    const age = _whyObjCohortAge(range);
    if (!age) return null;
    const span = age.oldest === age.youngest
      ? (age.youngest === 0 ? 'leads dyal l-youm' : age.youngest + ' youm')
      : ('l-a9dam ' + age.oldest + ' youm · l-jdid ' + (age.youngest === 0 ? 'l-youm' : age.youngest + ' youm'));
    if (age.mid >= 30) {
      return el('div', { class: 'why-cohort-note why-cohort-note-ok' }, [
        lucideIcon('circle-check'),
        el('p', { text: 'Cohorte naDja (' + span + ') — l-ar9am wselo l ≈94%+ mn l-9imma n-ha2iya, t9der t9aren biha b ti9a.' }),
      ]);
    }
    const ready = age.mid >= 14 ? '≈85%' : (age.mid >= 7 ? '≈80%' : (age.mid >= 3 ? '≈55%' : '≈27%'));
    return el('div', { class: 'why-cohort-note why-cohort-note-warn' }, [
      lucideIcon('hourglass'),
      el('p', { text: 'Cohorte mazal jdida (' + span + ') — l-ar9am li te7t kay-werriw ' + ready + ' mn l-9imma n-ha2iya o mazal ghadi ytal3o. 9aren-ha ghir m3a cohorte b nefs l-3omr, machi m3a chher li fat.' }),
    ]);
  }

  // The headline table: the cohort defined by create date, and how far that
  // same batch got. "% dyal l-cohorte" is the number that matters at the end;
  // "% mn l-étape 9bel" is where the leak actually is.
  function _whyObjCohortTable(payload) {
    const stages = payload?.funnel || [];
    const total = stages[0]?.count || 0;
    const section = el('section', { class: 'why-panel' }, [
      _whySectionHead(
        'layers',
        'COHORTE · CREATE DATE',
        _whyObjScopeLabel(payload) + ' · ' + _whyFmt(total) + ' leads f had l-periode',
        'L-leads li tkhel9o f l-periode l-mkhtara, o fin wselo mn ba3d. Kol lead kay-t7seb f ab3ad étape wsel-lha 3emmrou, 7tta ila rje3 l l-wra mn ba3d.',
      ),
    ]);
    const note = _whyObjMaturityNote(payload?.range);
    if (note) section.appendChild(note);
    if (!total) return section;

    const wrap = el('div', { class: 'why-table-wrap' });
    const table = el('table', { class: 'why-table why-cohort-table' });
    table.appendChild(el('thead', {}, [el('tr', {},
      ['Étape', 'Leads', '% dyal l-cohorte', '% mn l-étape 9bel', 'Perdus'].map(h => el('th', { text: h })))]));
    const body = el('tbody');
    stages.forEach((stage, index) => {
      const count = stage.count || 0;
      const prev = index ? (stages[index - 1]?.count || 0) : total;
      const lost = Math.max(0, prev - count);
      const share = Math.max(0, Math.min(100, Number(stage.share) || 0));
      // Under half the previous stage surviving is the threshold the floor
      // already reads as a leak on the journey map — keep the two consistent.
      const weak = index > 0 && Number(stage.step_conversion) < 50;
      body.appendChild(el('tr', {}, [
        el('td', {}, [
          el('span', { class: 'why-cohort-index', text: String(index + 1).padStart(2, '0') }),
          el('strong', { text: stage.label }),
        ]),
        el('td', {}, [el('strong', { text: _whyFmt(count) })]),
        el('td', {}, [
          el('span', { class: 'why-cohort-share', text: _whyPct(stage.share) }),
          el('span', { class: 'why-cohort-bar' }, [el('span', { style: 'width:' + share + '%' })]),
        ]),
        el('td', {}, index === 0
          ? [el('span', { class: 'why-cohort-muted', text: '—' })]
          : [el('strong', { class: weak ? 'why-cohort-weak' : 'why-cohort-ok', text: _whyPct(stage.step_conversion) })]),
        el('td', {}, index === 0
          ? [el('span', { class: 'why-cohort-muted', text: '—' })]
          : [el('span', { class: 'why-cohort-lost', text: '−' + _whyFmt(lost) })]),
      ]));
    });
    table.appendChild(body);
    wrap.appendChild(table);
    section.appendChild(wrap);
    return section;
  }

  // Same cohort, split by owner. Server sends this to admin / super_sales only,
  // so an empty list is the normal case for a plain agent — render nothing
  // rather than an empty table that looks broken.
  function _whyObjAgentTable(payload) {
    const rows = payload?.cohort_agents || [];
    if (!rows.length) return null;
    const section = el('section', { class: 'why-panel' }, [
      _whySectionHead(
        'users-round',
        'PER AGENT · NEFS L-COHORTE',
        'Chkoun dar ach b l-leads li jaw-lo',
        'Nefs l-periode o nefs l-9a3ida l kol wa7ed. L-percentages kollhom mn l-leads dyal dak l-agent, machi mn l-total dyal l-équipe.',
      ),
    ]);
    const wrap = el('div', { class: 'why-table-wrap' });
    const table = el('table', { class: 'why-table why-cohort-agent-table' });
    table.appendChild(el('thead', {}, [el('tr', {},
      ['Agent', 'Leads jaw', 'Contacted', 'Test scheduled', 'Test done', 'Proforma', 'Paid'].map(h => el('th', { text: h })))]));
    const body = el('tbody');
    const cell = (value, base) => el('td', {}, [
      el('strong', { text: _whyFmt(value) }),
      el('small', { text: _whyPct(base ? (Math.round((value / base) * 1000) / 10) : 0) }),
    ]);
    rows.forEach(row => {
      const base = row.leads || 0;
      body.appendChild(el('tr', {}, [
        el('td', {}, [
          el('span', { class: 'why-agent-avatar', text: String(userLabel(row.agent_id || '?')).charAt(0).toUpperCase() }),
          el('strong', { text: userLabel(row.agent_id || 'Bla owner') }),
        ]),
        el('td', {}, [el('strong', { text: _whyFmt(base) })]),
        cell(row.contacted, base),
        cell(row.test_scheduled, base),
        cell(row.test_done, base),
        cell(row.proforma, base),
        cell(row.paid, base),
      ]));
    });
    table.appendChild(body);
    wrap.appendChild(table);
    section.appendChild(wrap);
    return section;
  }

  function _whyObjRender(payload) {
    const root = document.getElementById('whyObjectifBoard');
    if (!root) return;
    clear(root);
    const journeys = payload?.funnel_journeys || [];
    // The cohort table answers "ach wq3a l l-leads dyal had l-periode" on its
    // own, so it leads; the journey maps below it explain WHY each step leaks.
    root.appendChild(_whyObjCohortTable(payload));
    const section = el('section', { class: 'why-panel why-funnel-panel' }, [
      _whySectionHead(
        'target',
        'OBJECTIF · FUNNEL DIAGNOSTIC MAP',
        _whyObjScopeLabel(payload) + ' · ' + _whyFmt(payload?.scope?.leads) + ' leads',
        'Two journeys, every leak and the play to fix it. Each arrow shows flow; every diagnostic combines observed loss reasons with a stage-specific next action.',
      ),
    ]);
    if (!payload?.scope?.leads) {
      section.appendChild(_whyEmpty('Ma kaynach leads f had filter. Wesse3 date range wla 7yed source / agent.'));
    } else {
      const stack = el('div', { class: 'why-journey-stack' });
      journeys.forEach((journey, index) => stack.appendChild(_whyJourneyMap(journey, index)));
      section.appendChild(stack.childNodes.length ? stack : _whyEmpty('Funnel diagnostics are not available for this filter.'));
    }
    root.appendChild(section);
    const agentTable = payload?.scope?.leads ? _whyObjAgentTable(payload) : null;
    if (agentTable) root.appendChild(agentTable);
    refreshIcons(root);
  }

  async function _whyObjLoad() {
    const seq = ++WHY_OBJ.requestSeq;
    const loading = document.getElementById('whyObjLoading');
    const error = document.getElementById('whyObjError');
    const status = document.getElementById('whyStatus');
    loading?.classList.remove('hidden');
    error?.classList.add('hidden');
    if (status) { status.textContent = 'Connecting data'; status.classList.add('why-status-busy'); }
    try {
      const params = new URLSearchParams();
      const from = document.getElementById('whyObjDateFrom')?.value;
      const to = document.getElementById('whyObjDateTo')?.value;
      const source = document.getElementById('whyObjSource')?.value;
      const agent = document.getElementById('whyObjAgent')?.value;
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (source) params.set('source', source);
      if (agent) params.set('agent', agent);
      const payload = await _whyJson('/api/why-analytics/objectif?' + params.toString());
      if (seq !== WHY_OBJ.requestSeq) return;
      WHY_OBJ.data = payload;
      _whyObjSyncAgents(payload);
      _whyObjRender(payload);
      const note = document.getElementById('whyObjRangeNote');
      if (note) note.textContent = `${payload.scope?.definition || ''} ${_whyFmt(payload.scope?.leads)} leads.`;
      if (status) status.textContent = 'Parcours ready';
    } catch (err) {
      if (seq !== WHY_OBJ.requestSeq) return;
      clear(document.getElementById('whyObjectifBoard'));
      error.textContent = err.message || 'Could not load Objectif.';
      error.classList.remove('hidden');
      if (status) status.textContent = 'Load failed';
    } finally {
      if (seq === WHY_OBJ.requestSeq) {
        loading?.classList.add('hidden');
        status?.classList.remove('why-status-busy');
      }
    }
  }

  function _whyObjInit() {
    if (!WHY_OBJ.inited) {
      WHY_OBJ.inited = true;
      _whyObjSetRange('mtd');
      document.querySelectorAll('[data-obj-range]').forEach(btn => btn.addEventListener('click', () => { _whyObjSetRange(btn.dataset.objRange); _whyObjLoad(); }));
      ['whyObjDateFrom', 'whyObjDateTo'].forEach(id => document.getElementById(id)?.addEventListener('change', () => document.querySelectorAll('[data-obj-range]').forEach(btn => btn.classList.remove('why-preset-active'))));
      document.getElementById('whyObjApplyBtn')?.addEventListener('click', _whyObjLoad);
      document.getElementById('whyObjSource')?.addEventListener('change', _whyObjLoad);
      document.getElementById('whyObjAgent')?.addEventListener('change', _whyObjLoad);
      _whyObjLoadSources();
      _whyObjLoad();
    } else if (!WHY_OBJ.data) {
      _whyObjLoad();
    }
  }

  function initWhyAnalytics() {
    // The whole sales side reaches 3LASH now; teachers / viewers / the Meta
    // review account do not (the API repeats this gate).
    const role = String(state.me?.role || '').toLowerCase();
    if (!_isSalesAdmin() && role !== 'agent') { toast('Sales team only', 'error'); showView('inbox'); return; }
    // A plain agent only ever gets Objectif — the AI re-analyze button belongs
    // to the Decision room, so it goes with it.
    if (!_isSalesAdmin()) document.getElementById('whyAiRefreshBtn')?.classList.add('hidden');
    if (!WHY.inited) {
      WHY.inited = true;
      document.querySelectorAll('[data-why-page]').forEach(btn => btn.addEventListener('click', () => _whySetSubpage(btn.dataset.whyPage)));
      _whySetSubpage(_isSalesAdmin() ? 'overview' : 'objectif');
      if (!_isSalesAdmin()) return;   // agents skip the sales-admin report entirely
      _whySetRange('mtd');
      document.querySelectorAll('.why-preset').forEach(btn => btn.addEventListener('click', () => { _whySetRange(btn.dataset.whyRange); _whyLoad(); }));
      ['whyDateFrom', 'whyDateTo'].forEach(id => document.getElementById(id)?.addEventListener('change', () => document.querySelectorAll('.why-preset').forEach(btn => btn.classList.remove('why-preset-active'))));
      document.getElementById('whyApplyBtn')?.addEventListener('click', _whyLoad);
      document.getElementById('whySource')?.addEventListener('change', _whyLoad);
      document.getElementById('whyAiRefreshBtn')?.addEventListener('click', () => _whyAnalyze(true));
      window.addEventListener('message', event => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'leads7:meta-ads-connected') {
          toast('Meta Ads connected — loading spend', 'success');
          _whyLoadMeta(true);
        } else if (event.data?.type === 'leads7:meta-ads-error') {
          toast('Meta Ads permission was not completed', 'error');
          _whyLoadMeta(true);
        }
      });
      _whyLoadSources();
      _whyLoad();
    } else if (!WHY.report && _isSalesAdmin()) {
      _whyLoad();
    } else {
      _whySetSubpage(WHY.activePage);
    }
  }

  // ============ Test-Day Cockpit ============
  state.cockpit = {
    inited: false,
    loading: false,
    timer: null,
    data: null,
    controllerReady: false,
    teacherReady: false,
    joinMode: '',
    controllerChannel: '',
    controllerBoot: null,
    pendingRoutes: new Map(),
    teacherActionAt: new Map(),
    controllerHeartbeat: null,
    controllerLeaseActive: false,
    controllerLeaseEpoch: 0,
    // One random token per page lifetime. Unlike sessionStorage this is not
    // cloned into a duplicated tab, so the server can fence split-brain hosts.
    controllerInstanceId: ckNonce(),
    autoJoinInFlight: false,
    autoJoinNextAt: 0,
    zoomBootWatchdog: null,
    zoomResetting: null,
    claimHeartbeat: null,
    heartbeatIds: [],
    socketBound: false,
    // Zoom dock view (visual only): 'expanded' = big in-flow panel,
    // 'dock' = small fixed bottom-right dock. The iframe itself is NEVER
    // reparented/unmounted — mode switches are pure CSS class swaps so the
    // live Zoom controller connection survives. zoomViewUser=true once the
    // operator picks a mode manually (stops the auto default for the session).
    zoomView: '',
    zoomViewUser: false,
  };

  function ckToday() {
    try {
      return MA.dayKey();
    } catch (_) { return new Date().toISOString().slice(0, 10); }
  }

  function ckTeacherScope() {
    return state.cockpit.data?.scope === 'teacher' || String(state.me?.role || '').toLowerCase() === 'teacher';
  }

  function ckStatusLabel(stage) {
    if (ckTeacherScope()) return ({
      not_arrived: 'NOT ARRIVED', waiting_room: 'WAITING', sales_queue: 'SALES QUEUE',
      sales_check: 'WITH SALES', sound_ok: 'SOUND OK', teacher_queue: 'READY FOR TEACHER',
      routing: 'MOVING TO ROOM', in_test: 'IN TEST', disconnected: 'DISCONNECTED',
      problem: 'PROBLEM', done: 'COMPLETED',
    })[stage] || String(stage || '').replace(/_/g, ' ').toUpperCase();
    return ({
      not_arrived: 'MAZAL MADKHEL', waiting_room: 'KITSNA', sales_queue: 'NOBAT SALES',
      sales_check: 'M3A SALES', sound_ok: 'SON OK', routing: 'KAYDOZ L PROF',
      in_test: 'M3A PROF', disconnected: 'T9TE3', problem: 'MOSHKIL', done: 'SALA',
    })[stage] || String(stage || '').replace(/_/g, ' ').toUpperCase();
  }

  function ckElapsed(iso) {
    const ms = iso ? Date.now() - new Date(iso).getTime() : NaN;
    if (!Number.isFinite(ms) || ms < 0) return '';
    const min = Math.floor(ms / 60000);
    if (min < 1) return ckTeacherScope() ? 'now' : 'daba';
    if (min < 60) return min + ' min';
    return Math.floor(min / 60) + 'h ' + (min % 60) + 'min';
  }

  async function ckJson(path, opts) {
    const response = await api(path, opts || {});
    let body = {};
    try { body = await response.json(); } catch (_) {}
    if (!response.ok || body.ok === false) {
      const err = new Error(body.error || body.message || ('HTTP ' + response.status));
      err.status = response.status;
      err.body = body;
      throw err;
    }
    return body;
  }

  function ckNonce() {
    const bytes = new Uint8Array(18);
    if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
  function ckOperationId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const n = ckNonce();
    return n.slice(0, 8) + '-' + n.slice(8, 12) + '-4' + n.slice(13, 16) + '-a' + n.slice(17, 20) + '-' + n.slice(20, 32);
  }

  function ckPostController(command, payload, requestId) {
    const frame = $('ckZoomFrame');
    if (!frame?.contentWindow || !state.cockpit.controllerChannel) throw new Error('Zoom is not ready yet');
    frame.contentWindow.postMessage({
      source: 'leads7-cockpit',
      type: 'zoom-controller:command',
      channelNonce: state.cockpit.controllerChannel,
      requestId: requestId || (command + '-' + ckNonce()),
      command,
      payload: payload || {},
    }, window.location.origin);
  }

  function ckInitControllerChannel() {
    const frame = $('ckZoomFrame');
    if (!frame?.contentWindow || !state.cockpit.controllerChannel) return;
    frame.contentWindow.postMessage({
      source: 'leads7-cockpit',
      type: 'zoom-controller:init',
      channelNonce: state.cockpit.controllerChannel,
    }, window.location.origin);
  }

  function ckFailPendingRoutes(message) {
    const operationIds = Array.from(state.cockpit.pendingRoutes.keys());
    if (!operationIds.length) return Promise.resolve();
    const reports = operationIds.map(operationId => Promise.resolve(
      ckControllerRouteResult({
        operationId,
        ok: false,
        error: message || 'Zoom disconnected before route confirmation',
      }),
    ).catch(() => {}));
    // Never hold the retry UI forever on a slow network. Each real reporter
    // keeps running after this bound and has its own safe catch; the timeout
    // only permits local iframe teardown to continue.
    return Promise.race([
      Promise.allSettled(reports).then(() => undefined),
      new Promise(resolve => window.setTimeout(resolve, 1500)),
    ]).catch(() => {});
  }

  function ckClearZoomBootWatchdog() {
    if (!state.cockpit.zoomBootWatchdog) return;
    window.clearTimeout(state.cockpit.zoomBootWatchdog);
    state.cockpit.zoomBootWatchdog = null;
  }

  // A successful session response only reserves the DB lease and gives the
  // iframe credentials. It does NOT prove the Meeting SDK loaded or joined.
  // If the iframe/CSP/SDK channel never answers, release the local stuck state
  // so the normal bounded auto-join loop can try again after the lease expires.
  function ckArmZoomBootWatchdog(mode, channel) {
    ckClearZoomBootWatchdog();
    state.cockpit.zoomBootWatchdog = window.setTimeout(() => {
      state.cockpit.zoomBootWatchdog = null;
      if (state.cockpit.joinMode !== mode || state.cockpit.controllerChannel !== channel) return;
      const ready = mode === 'teacher' ? state.cockpit.teacherReady : state.cockpit.controllerReady;
      if (ready) return;
      const message = mode === 'teacher'
        ? 'Zoom did not finish connecting. Retrying automatically.'
        : 'Zoom controller ma kmelch l-connection. Ghadi n3awd nconnecta automatic.';
      Promise.resolve(ckResetZoomSession(message))
        .catch(() => {})
        .finally(() => loadTestDayCockpit(true));
    }, 60000);
  }

  function ckReleaseControllerLease(keepalive) {
    if (state.cockpit.joinMode !== 'controller' || !state.cockpit.controllerInstanceId) return Promise.resolve();
    const path = '/api/test-day/controller/release';
    const body = JSON.stringify({ controller_instance_id: state.cockpit.controllerInstanceId });
    // fetch keepalive preserves the normal x-auth-token header during reload;
    // sendBeacon cannot carry that header and would be rejected by auth.
    return ckJson(path, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: !!keepalive,
    }).catch(() => {});
  }

  function ckResetZoomSession(message) {
    ckClearZoomBootWatchdog();
    const mode = state.cockpit.joinMode;
    const teacherMode = mode === 'teacher';
    if (mode === 'controller') ckReleaseControllerLease(false);
    if (teacherMode) state.cockpit.teacherReady = false;
    else state.cockpit.controllerReady = false;
    clearInterval(state.cockpit.controllerHeartbeat);
    state.cockpit.controllerHeartbeat = null;
    state.cockpit.controllerLeaseActive = false;
    state.cockpit.controllerLeaseEpoch += 1;
    const statusNode = teacherMode ? $('ckTeacherJoinStatus') : $('ckControllerStatus');
    if (statusNode && message) statusNode.textContent = String(message);
    if (state.cockpit.zoomResetting) return state.cockpit.zoomResetting;

    const finishReset = () => {
      state.cockpit.controllerBoot = null;
      state.cockpit.controllerChannel = '';
      state.cockpit.pendingRoutes.clear();
      state.cockpit.joinMode = '';
      state.cockpit.autoJoinInFlight = false;
      state.cockpit.autoJoinNextAt = Date.now() + 5000;
      state.cockpit.zoomView = '';
      state.cockpit.zoomViewUser = false;
      const button = teacherMode ? $('ckStartTeacherBtn') : $('ckStartControllerBtn');
      if (button) {
        button.classList.remove('hidden');
        button.disabled = false;
        button.textContent = teacherMode ? 'TRY JOINING AGAIN' : '3AWD DKHOL L ZOOM';
      }
      const frame = $('ckZoomFrame');
      frame?.classList.add('hidden');
      // A channel nonce belongs to one iframe lifetime. Reload the isolated
      // surface after a terminal Zoom failure so the retry can establish a
      // fresh nonce instead of being ignored by the old child channel.
      const frameSrc = frame?.getAttribute('src');
      if (frame && frameSrc) frame.setAttribute('src', frameSrc);
      ckApplyZoomView();
      state.cockpit.zoomResetting = null;
    };

    if (!state.cockpit.pendingRoutes.size) {
      finishReset();
      return Promise.resolve();
    }
    state.cockpit.zoomResetting = ckFailPendingRoutes(message).then(finishReset, finishReset);
    return state.cockpit.zoomResetting;
  }

  function ckHandleControllerLeaseLoss(message) {
    if (state.cockpit.joinMode !== 'controller' || !state.cockpit.controllerLeaseActive) return;
    // Revoke parent-side authority synchronously. The nonce-protected stop is
    // posted before reset reloads the iframe, so the stale host disables its
    // policy and exits without another reconcile/refresh pass.
    state.cockpit.controllerLeaseActive = false;
    state.cockpit.controllerLeaseEpoch += 1;
    state.cockpit.controllerReady = false;
    clearInterval(state.cockpit.controllerHeartbeat);
    state.cockpit.controllerHeartbeat = null;
    try { ckPostController('relinquish-controller', {}, 'relinquish-' + ckNonce()); } catch (_) {}
    window.setTimeout(() => {
      ckResetZoomSession(message || 'Controller tbedel. 3awed dkhol ila b9a khassk.');
      loadTestDayCockpit(true);
    }, 250);
  }

  function ckControllerAuthorityLost(err) {
    return (err?.status === 409 && err?.body?.error === 'controller_expired')
      || (err?.status === 403 && err?.body?.error === 'controller_not_allowed')
      || err?.status === 401;
  }

  async function ckStartController(autoStart) {
    const btn = $('ckStartControllerBtn');
    if (btn) { btn.disabled = true; btn.textContent = 'KAYDKHOL...'; }
    try {
      const boot = await ckJson('/api/test-day/controller/session', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ controller_instance_id: state.cockpit.controllerInstanceId }), timeoutMs: 30000,
      });
      state.cockpit.joinMode = boot.mode === 'sales' ? 'sales' : 'controller';
      state.cockpit.controllerLeaseActive = false;
      state.cockpit.controllerBoot = boot.controller;
      state.cockpit.controllerChannel = ckNonce();
      ckArmZoomBootWatchdog(state.cockpit.joinMode, state.cockpit.controllerChannel);
      const frame = $('ckZoomFrame');
      frame?.classList.remove('hidden');
      ckApplyZoomView(); // dock/expanded classes ready before the panel reveals
      const init = () => ckInitControllerChannel();
      if (frame?.contentWindow) {
        // The iframe may already be loaded from a previous view. Sending now
        // and once on load is idempotent inside the child.
        init();
        frame.addEventListener('load', init, { once: true });
      }
      if ($('ckControllerStatus')) $('ckControllerStatus').textContent = 'Kayconnecta m3a Zoom...';
    } catch (err) {
      const message = err.body?.error === 'hub_not_live'
        ? 'Admin mazal ma 7ellch Zoom Hub.'
        : (err.status === 409 ? 'Controller deja m7lol 3nd Admin akhor' : ('Zoom: ' + err.message));
      if (!autoStart) toast(message, 'error');
      else if ($('ckControllerStatus')) $('ckControllerStatus').textContent = message + ' Ghadi n3awd nconnecta automatic.';
      if (btn) { btn.disabled = false; btn.textContent = '3AWD DKHOL L ZOOM'; }
    }
  }

  async function ckStartTeacher(autoStart) {
    const btn = $('ckStartTeacherBtn');
    if (btn) { btn.disabled = true; btn.textContent = 'JOINING...'; }
    try {
      state.cockpit.joinMode = 'teacher';
      state.cockpit.controllerLeaseActive = false;
      const boot = await ckJson('/api/test-day/teacher/session', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}', timeoutMs: 30000,
      });
      state.cockpit.controllerBoot = boot.teacher;
      state.cockpit.controllerChannel = ckNonce();
      ckArmZoomBootWatchdog(state.cockpit.joinMode, state.cockpit.controllerChannel);
      const frame = $('ckZoomFrame');
      frame?.classList.remove('hidden');
      ckApplyZoomView(); // teacher scope defaults to the big expanded panel
      const init = () => ckInitControllerChannel();
      if (frame?.contentWindow) {
        init();
        frame.addEventListener('load', init, { once: true });
      }
      if ($('ckTeacherJoinStatus')) $('ckTeacherJoinStatus').textContent = 'Connecting to Zoom...';
    } catch (err) {
      const message = err.body?.error === 'hub_not_live' ? 'Sales has not started the Zoom Hub yet.' : ('Zoom: ' + err.message);
      if (!autoStart) toast(message, 'error');
      else if ($('ckTeacherJoinStatus')) $('ckTeacherJoinStatus').textContent = message + ' Retrying automatically.';
      state.cockpit.joinMode = '';
      if (btn) { btn.disabled = false; btn.textContent = 'TRY JOINING AGAIN'; }
    }
  }

  // swat:B27 — nobody owns an Admit button any more. Opening Cockpit is
  // enough: an eligible admin/Sales browser competes for the single host
  // lease, while a teacher joins as an authenticated attendee. A bounded
  // retry lets the always-open kiosk recover after deploys/network drops and
  // lets staff who opened the page before the Hub came live join afterwards.
  function ckMaybeAutoJoin() {
    const data = state.cockpit.data || {};
    if (!data.enabled || !data.hub?.configured) return;
    if (state.cockpit.joinMode || state.cockpit.zoomResetting || state.cockpit.autoJoinInFlight) return;
    const teacher = data.scope === 'teacher';
    if (!teacher && data.can_control !== true) return;
    const now = Date.now();
    if (now < Number(state.cockpit.autoJoinNextAt || 0)) return;
    state.cockpit.autoJoinInFlight = true;
    state.cockpit.autoJoinNextAt = now + 30000;
    Promise.resolve(teacher ? ckStartTeacher(true) : ckStartController(true))
      .finally(() => { state.cockpit.autoJoinInFlight = false; });
  }

  function ckRunTeacherActions(actions) {
    for (const action of (Array.isArray(actions) ? actions : [])) {
      const userId = Number(action?.userId);
      const kind = action?.action === 'admit' ? 'admit' : (action?.action === 'route' ? 'route' : '');
      if (!Number.isInteger(userId) || userId <= 0 || !kind) continue;
      const key = kind + ':' + userId;
      const now = Date.now();
      if (now - Number(state.cockpit.teacherActionAt.get(key) || 0) < 5000) continue;
      state.cockpit.teacherActionAt.set(key, now);
      if (kind === 'admit') {
        ckPostController('admit-participant', { userId }, 'teacher-admit-' + userId + '-' + ckNonce());
      } else if (String(action.targetRoomId || '')) {
        ckPostController('route-participant', {
          userId, targetRoomId: String(action.targetRoomId), operation: 'auto',
        }, 'teacher-route-' + userId + '-' + ckNonce());
      }
    }
  }

  async function ckControllerRouteResult(msg) {
    const operationId = String(msg.operationId || '');
    const pending = state.cockpit.pendingRoutes.get(operationId);
    if (!pending) return;
    state.cockpit.pendingRoutes.delete(operationId);
    try {
      await ckJson('/api/test-day/cockpit/flows/' + encodeURIComponent(pending.flowId) + '/route-result', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation_id: operationId, ok: msg.ok === true, error: msg.error || null }),
      });
      if (msg.ok) toast('Client daz l prof', 'success');
      else toast('Route ma kmlatch: ' + (msg.error || 'Zoom error'), 'error');
    } catch (err) { toast('Route result: ' + err.message, 'error'); }
    loadTestDayCockpit(true);
  }

  function ckOnControllerMessage(event) {
    const frame = $('ckZoomFrame');
    const msg = event.data || {};
    if (event.origin !== window.location.origin || event.source !== frame?.contentWindow) return;
    if (msg.source !== 'leads7-zoom-controller' || !state.cockpit.controllerChannel || msg.channelNonce !== state.cockpit.controllerChannel) return;
    if (msg.type === 'zoom-controller:wheel') {
      const view = $('testDayCockpitView');
      if (!view || view.classList.contains('hidden')) return;
      let delta = Number(msg.deltaY) || 0;
      if (Number(msg.deltaMode) === 1) delta *= 16;
      else if (Number(msg.deltaMode) === 2) delta *= view.clientHeight;
      view.scrollTop += delta;
    } else if (msg.type === 'zoom-controller:ready' && msg.phase === 'channel') {
      ckPostController('boot', state.cockpit.controllerBoot, 'boot-' + ckNonce());
    } else if (msg.type === 'zoom-controller:ready' && msg.phase === 'meeting') {
      ckClearZoomBootWatchdog();
      if (state.cockpit.joinMode === 'teacher') {
        state.cockpit.teacherReady = true;
        if ($('ckTeacherJoinStatus')) $('ckTeacherJoinStatus').textContent = 'ZOOM READY - wait for the next student in your room';
        if ($('ckStartTeacherBtn')) $('ckStartTeacherBtn').classList.add('hidden');
        loadTestDayCockpit(true);
        return;
      }
      if (state.cockpit.joinMode === 'sales') {
        state.cockpit.controllerReady = true;
        if ($('ckControllerStatus')) $('ckControllerStatus').textContent = 'ROOM DYALK WAJDA - tsenna clients dyalk';
        if ($('ckStartControllerBtn')) $('ckStartControllerBtn').classList.add('hidden');
        loadTestDayCockpit(true);
        return;
      }
      state.cockpit.controllerReady = true;
      state.cockpit.controllerLeaseActive = true;
      state.cockpit.controllerLeaseEpoch += 1;
      const leaseEpoch = state.cockpit.controllerLeaseEpoch;
      if ($('ckControllerStatus')) $('ckControllerStatus').textContent = 'READY - t9der tbda m3a clients';
      if ($('ckStartControllerBtn')) $('ckStartControllerBtn').classList.add('hidden');
      ckPostController('refresh-attendees', {}, 'refresh-' + ckNonce());
      clearInterval(state.cockpit.controllerHeartbeat);
      // Mark the lease live only after the Meeting SDK confirms that the host
      // actually joined. The board must never call a reserved-but-stuck lease
      // AUTO LIVE.
      ckJson('/api/test-day/controller/heartbeat', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ controller_instance_id: state.cockpit.controllerInstanceId }),
      }).then(() => loadTestDayCockpit(true)).catch((err) => {
        if (ckControllerAuthorityLost(err)) ckHandleControllerLeaseLoss('Controller session salat. 3awed dkhoul l-platform.');
      });
      state.cockpit.controllerHeartbeat = setInterval(async () => {
        if (!state.cockpit.controllerLeaseActive || leaseEpoch !== state.cockpit.controllerLeaseEpoch) return;
        try {
          await ckJson('/api/test-day/controller/heartbeat', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ controller_instance_id: state.cockpit.controllerInstanceId }),
          });
        } catch (err) {
          if (ckControllerAuthorityLost(err)) ckHandleControllerLeaseLoss('Controller ma b9ach 3nd had session. Zoom twa9ef hna.');
          else state.cockpit.controllerReady = false;
          return;
        }
        if (!state.cockpit.controllerLeaseActive || leaseEpoch !== state.cockpit.controllerLeaseEpoch) return;
        try { ckPostController('refresh-rooms', {}, 'rooms-' + ckNonce()); } catch (_) {}
        try { ckPostController('refresh-attendees', {}, 'refresh-' + ckNonce()); } catch (_) {}
      }, 30000);
      loadTestDayCockpit(true);
    } else if (msg.type === 'zoom-controller:status') {
      const statusNode = state.cockpit.joinMode === 'teacher' ? $('ckTeacherJoinStatus') : $('ckControllerStatus');
      if (statusNode && msg.message) statusNode.textContent = String(msg.message);
      if (msg.state === 'disconnected') ckResetZoomSession(msg.message || (state.cockpit.joinMode === 'teacher'
        ? 'Zoom disconnected. Please join again.'
        : 'Zoom t9te3. 3awed dkhol.'));
    } else if (msg.type === 'zoom-controller:participant-list') {
      if (state.cockpit.joinMode === 'controller' && !state.cockpit.controllerLeaseActive) return;
      const leaseEpoch = state.cockpit.controllerLeaseEpoch;
      ckJson('/api/test-day/controller/reconcile', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ controller_instance_id: state.cockpit.controllerInstanceId,
          attendees: msg.participants || [], rooms: msg.rooms || [] }),
      }).then((result) => {
        if (!state.cockpit.controllerLeaseActive || leaseEpoch !== state.cockpit.controllerLeaseEpoch) return;
        ckRunTeacherActions([...(result.teacher_actions || []), ...(result.sales_actions || [])]);
        loadTestDayCockpit(true);
      }).catch((err) => {
        if (ckControllerAuthorityLost(err)) ckHandleControllerLeaseLoss('Controller ma b9ach 3nd had session. Zoom twa9ef hna.');
      });
    } else if (msg.type === 'zoom-controller:room-list') {
      if (state.cockpit.joinMode === 'controller' && !state.cockpit.controllerLeaseActive) return;
      const leaseEpoch = state.cockpit.controllerLeaseEpoch;
      ckJson('/api/test-day/controller/reconcile', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ controller_instance_id: state.cockpit.controllerInstanceId, rooms: msg.rooms || [] }),
      }).then(() => {
        if (!state.cockpit.controllerLeaseActive || leaseEpoch !== state.cockpit.controllerLeaseEpoch) return;
        // Startup's first attendee read may beat persistence of the freshly
        // created teacher rooms. Only the parent has the server ACK, so issue
        // the second read here, after that write is durably ordered.
        if (String(msg.requestId || '').startsWith('rooms-boot-')) {
          ckPostController('refresh-attendees', {}, 'refresh-after-rooms-' + ckNonce());
        }
        loadTestDayCockpit(true);
      }).catch((err) => {
        if (ckControllerAuthorityLost(err)) ckHandleControllerLeaseLoss('Controller ma b9ach 3nd had session. Zoom twa9ef hna.');
      });
    } else if (msg.type === 'zoom-controller:route-success') {
      ckControllerRouteResult({ operationId: msg.requestId, ok: true });
    } else if (msg.type === 'zoom-controller:admit-success') {
      loadTestDayCockpit(true);
      if (String(msg.requestId || '').startsWith('teacher-admit-')) {
        setTimeout(() => {
          try { ckPostController('refresh-attendees', {}, 'refresh-teacher-' + ckNonce()); } catch (_) {}
        }, 2000);
      }
    } else if (msg.type === 'zoom-controller:error') {
      if (state.cockpit.pendingRoutes.has(String(msg.requestId || ''))) {
        ckControllerRouteResult({ operationId: msg.requestId, ok: false, error: msg.message || msg.code });
      } else if (['invalid_boot', 'sdk_load_failed', 'join_failed', 'not_connected'].includes(String(msg.code || ''))) {
        const teacherMode = state.cockpit.joinMode === 'teacher';
        ckResetZoomSession(msg.message || (teacherMode ? 'Zoom could not start. Please try again.' : 'Zoom madkhelch. 3awed jareb.'));
        toast('Zoom: ' + (msg.message || msg.code || 'error'), 'error');
      } else {
        toast('Zoom Controller: ' + (msg.message || msg.code || 'error'), 'error');
      }
    }
  }

  async function ckRunAction(flow, action, extra, button) {
    const teacherComplete = action === 'complete' && ckTeacherScope();
    if (button) button.disabled = true;
    try {
      const result = await ckJson('/api/test-day/cockpit/flows/' + encodeURIComponent(flow.id) + '/' + action, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expected_version: flow.state_version, ...(extra || {}) }),
      });
      if (action === 'claim' && result.admit_user_id) {
        // Nonce like every other command: the controller iframe dedups by
        // requestId, so a bare 'admit-<flowId>' could never be retried after
        // a failed first admit + re-claim.
        ckPostController('admit-participant', { userId: result.admit_user_id }, 'admit-' + flow.id + '-' + ckNonce());
      }
      if (action === 'route') {
        const route = result.route || {};
        // The numeric Zoom user id stored in Postgres can change on every
        // admit/rejoin.  Never send that durable id straight to the SDK: ask
        // the controller for a fresh attendee snapshot, then the normal
        // reconcile relay resolves the live id by stable customer key and
        // performs the route.  The webhook remains the authoritative success
        // confirmation, so this path does not need a speculative route-result.
        ckPostController('refresh-attendees', {}, 'route-refresh-' + (route.operation_id || ckNonce()));
      }
      if (action === 'sound-ok') toast('SON OK - daba route l prof', 'success');
      if (action === 'complete') toast(teacherComplete ? 'Test completed' : 'Test sala', 'success');
      await loadTestDayCockpit(true);
    } catch (err) {
      if (err.status === 409) toast(teacherComplete
        ? 'This test was already updated. Refresh and try again.'
        : (err.body?.message || 'Chi wa7ed sb9k. Dir Refresh.'));
      else toast(err.message, 'error');
      await loadTestDayCockpit(true);
    } finally {
      if (button) button.disabled = false;
    }
  }

  function ckMetric(value, label, tone) {
    return el('div', { class: 'ck-metric' + (tone ? ' ck-metric-' + tone : '') }, [el('strong', { text: String(value || 0) }), el('span', { text: label })]);
  }

  // Zoom dock (design pass 2026-07-21, visual only): the controller iframe
  // lives inside #ckZoomDock (static markup, never reparented — moving an
  // iframe in the DOM reloads it and would drop the live Hub connection).
  // Two CSS modes: 'expanded' (big in-flow panel) / 'dock' (small fixed
  // bottom-right). Auto default follows the queue — expanded while no active
  // clients, dock once the board fills — until the operator toggles manually.
  function ckApplyZoomView() {
    const dock = $('ckZoomDock');
    if (!dock) return;
    const flows = Array.isArray(state.cockpit.data?.flows) ? state.cockpit.data.flows : [];
    const hasActive = flows.some(f => !['done', 'not_arrived'].includes(f.state));
    // Defaults per scope (until the operator toggles manually): teachers live
    // INSIDE Zoom (they run the test there — video + screen share must be big),
    // so teacher scope defaults to expanded always; sales defaults to expanded
    // only while the queue is empty, dock once clients are on the board.
    if (!state.cockpit.zoomViewUser) state.cockpit.zoomView = (ckTeacherScope() || !hasActive) ? 'expanded' : 'dock';
    dock.classList.toggle('ck-zoom-dock--teacher', ckTeacherScope());
    const mode = state.cockpit.zoomView === 'dock' ? 'dock' : 'expanded';
    dock.classList.toggle('ck-zoom-dock--dock', mode === 'dock');
    dock.classList.toggle('ck-zoom-dock--expanded', mode === 'expanded');
    const ready = state.cockpit.joinMode === 'teacher' ? state.cockpit.teacherReady : state.cockpit.controllerReady;
    const title = $('ckZoomDockTitle');
    if (title) title.textContent = ready ? 'Zoom · READY' : 'Zoom Hub';
    dock.classList.toggle('ck-zoom-dock--ready', !!ready);
  }

  function ckToggleZoomView() {
    state.cockpit.zoomViewUser = true;
    state.cockpit.zoomView = state.cockpit.zoomView === 'dock' ? 'expanded' : 'dock';
    ckApplyZoomView();
  }

  // teachers.display_name may already start with "Teacher" (e.g. "Teacher
  // Issam"); strip it before a scope prefix is added so cards never read
  // "Teacher Teacher Issam" / "Prof Teacher Issam" (audit 2026-07-21).
  function ckTeacherLabel(name) {
    return String(name || '').replace(/^\s*teacher\s+/i, '').trim();
  }

  // Claim heartbeat (audit blocker 2): the server claim lives 90s; while the
  // viewer OWNS a flow mid-handling (SON OK / route / routing in flight), renew
  // it every 30s so it can't expire under them. The board recomputes the owned
  // set on every poll; an expired/foreign claim just 409s and is dropped here.
  function ckSyncClaimHeartbeat(flows) {
    const ids = ckTeacherScope() ? [] : (flows || [])
      .filter(f => f.claimed_by_me && f.id && (f.can_sound_ok || f.can_route || f.state === 'routing'))
      .map(f => f.id);
    state.cockpit.heartbeatIds = ids;
    if (ids.length && !state.cockpit.claimHeartbeat) {
      state.cockpit.claimHeartbeat = setInterval(() => {
        // Same gate as the 10s board poll: once the owner leaves the Cockpit,
        // stop renewing so the 90s claim expires and colleagues can take over.
        if (state.activeView !== 'test-day-cockpit') return;
        for (const id of state.cockpit.heartbeatIds || []) {
          ckJson('/api/test-day/cockpit/flows/' + encodeURIComponent(id) + '/heartbeat', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
          }).catch(() => { /* expired or re-claimed — next poll re-renders the truth */ });
        }
      }, 30000);
    } else if (!ids.length && state.cockpit.claimHeartbeat) {
      clearInterval(state.cockpit.claimHeartbeat);
      state.cockpit.claimHeartbeat = null;
    }
  }

  function ckDirectStatusLabel(value) {
    return ({
      offline: 'OFFLINE', waiting_for_teacher: 'WAITING FOR TEACHER', ready: 'READY',
      busy: 'BUSY', overlap: 'OVERLAP', disconnected: 'DISCONNECTED', verifying: 'VERIFYING',
    })[value] || String(value || 'offline').replace(/_/g, ' ').toUpperCase();
  }

  function ckDirectPhone(value) {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.length >= 8 ? digits : '';
  }

  function ckDirectAction(label, icon, phone, name, kind) {
    const button = el('button', { class: 'ck-contact-btn', type: 'button' }, [lucideIcon(icon), el('span', { text: label })]);
    const digits = ckDirectPhone(phone);
    button.disabled = !digits;
    if (!digits) button.title = 'Phone not configured';
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      if (!digits) return;
      if (kind === 'call') cxDial(digits, name || '', button);
      else openChatForPhoneNumber(digits);
    });
    return button;
  }

  function ckAlertText(alert) {
    const who = alert.student_name ? ' — ' + alert.student_name : '';
    const label = ({
      teacher_late: 'Teacher ما دخلش بعد دقيقتين',
      student_waiting: 'Student كيتسنى أكثر من دقيقتين',
      teacher_left_student_inside: 'Teacher خرج وStudent مازال داخل',
      student_overlap: 'أكثر من Student داخل نفس الغرفة',
      session_overtime: 'Session فاتت الموعد بخمس دقايق',
      presence_mismatch: 'Zoom API وwebhook مختلفين جوج دورات',
      meet_link_failure: 'تعذر إنشاء Lougha meet link',
      room_not_configured: '⚠️ Teacher عندو tests اليوم وقاعة Zoom ديالو ماشي مضبوطة',
      room_adopted: 'Teacher حل قاعة Zoom أخرى — Leads7 لقاها وسجّلها',
    })[alert.kind] || String(alert.kind || 'Alert').replace(/_/g, ' ');
    return label + who;
  }

  function ckSoundNewAlerts(alerts) {
    const key = 'leads7:cockpit-heard-alerts:v1';
    let heard = [];
    try { heard = JSON.parse(localStorage.getItem(key) || '[]'); } catch (_) { heard = []; }
    const known = new Set(Array.isArray(heard) ? heard.map(String) : []);
    const fresh = (alerts || []).filter(a => a.id && !known.has(String(a.id)));
    if (!fresh.length) return;
    for (const alert of fresh) known.add(String(alert.id));
    try { localStorage.setItem(key, JSON.stringify(Array.from(known).slice(-250))); } catch (_) {}
    // One short two-note signal per refresh batch, never one sound per alert.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.42);
      gain.connect(ctx.destination);
      [0, 0.16].forEach((delay, i) => {
        const osc = ctx.createOscillator();
        osc.frequency.value = i ? 720 : 560;
        osc.connect(gain); osc.start(ctx.currentTime + delay); osc.stop(ctx.currentTime + delay + 0.2);
      });
      window.setTimeout(() => ctx.close().catch(() => {}), 700);
    } catch (_) { /* autoplay policy can defer sound until the next user gesture */ }
  }

  function ckCloseTeacherDrawer() {
    $('ckTeacherDrawer')?.classList.remove('ck-drawer--open');
    $('ckDrawerBackdrop')?.classList.add('hidden');
    $('ckTeacherDrawer')?.setAttribute('aria-hidden', 'true');
  }

  function ckTimelineLabel(type) {
    return ({
      teacher_joined: 'Teacher joined', teacher_left: 'Teacher left',
      student_waiting: 'Student entered queue', student_released: 'Student released',
      student_joined: 'Student joined', student_left: 'Student left',
      student_reconnected: 'Student reconnected', meeting_ended: 'Meeting ended',
      // Test rescue (2026-09-28): steps the client's meet page reported.
      client_home: 'Client opened the test page', client_connecting: 'Client connecting to Zoom',
      client_waiting: 'Client waiting in the page', client_in_meeting: 'Client in the meeting',
      client_zoom_failed: 'Zoom failed on the client page', client_zoom_stuck: 'Client stuck on "Joining Meeting…"',
      client_mic_denied: 'Client refused the mic',
      client_in_app_browser: 'Client in WhatsApp browser', client_old_browser: 'Client browser too old',
      client_reported_link_problem: 'Client said the link fails', client_backup_link_sent: 'Backup link sent',
      rescue_backup_attempt: 'Backup link attempt',
      // Sound check before joining (2026-10-04).
      client_sound_check: 'Client testing mic + sound', client_sound_ok: 'Client sound check passed',
      client_sound_mic_silent: 'Sound check: mic hears nothing', client_sound_speaker_no: 'Sound check: client hears nothing',
      client_sound_skipped: 'Client skipped the sound check',
    })[type] || String(type || 'Event').replace(/_/g, ' ');
  }

  async function ckOpenTeacherDrawer(teacher) {
    const drawer = $('ckTeacherDrawer');
    const backdrop = $('ckDrawerBackdrop');
    if (!drawer || !backdrop) return;
    drawer.classList.add('ck-drawer--open');
    drawer.setAttribute('aria-hidden', 'false');
    backdrop.classList.remove('hidden');
    if ($('ckDrawerTitle')) $('ckDrawerTitle').textContent = teacher.name || 'Teacher';
    const actions = $('ckDrawerActions');
    if (actions) {
      clear(actions);
      actions.append(
        ckDirectAction('Call Teacher', 'phone', teacher.teacher_phone, teacher.name, 'call'),
        ckDirectAction('WhatsApp Teacher', 'message-circle', teacher.teacher_phone, teacher.name, 'wa'),
      );
      if (teacher.current_student) actions.append(
        ckDirectAction('Call ' + teacher.current_student, 'phone-call', teacher.current_student_phone, teacher.current_student, 'call'),
        ckDirectAction('WhatsApp ' + teacher.current_student, 'message-square', teacher.current_student_phone, teacher.current_student, 'wa'),
      );
    }
    const body = $('ckDrawerBody');
    if (body) { clear(body); body.appendChild(el('div', { class: 'ck-empty', text: 'Loading timeline...' })); }
    try {
      const data = await ckJson('/api/test-day/cockpit/teachers/' + encodeURIComponent(teacher.id) + '?date=' + encodeURIComponent(ckToday()), { timeoutMs: 15000 });
      if (!body) return;
      clear(body);
      if (!(data.timeline || []).length) {
        body.appendChild(el('div', { class: 'ck-empty', text: 'No room events today.' }));
      } else {
        for (const event of data.timeline) {
          const at = event.at ? MA.loc(new Date(event.at), 't', [], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
          body.appendChild(el('div', { class: 'ck-timeline-item' }, [
            el('span', { class: 'ck-timeline-dot ck-timeline-dot--' + (event.source || 'system') }),
            el('div', { class: 'ck-timeline-copy' }, [
              el('strong', { text: ckTimelineLabel(event.type) + (event.student_name ? ' — ' + event.student_name : '') }),
              el('span', { text: at + ' · ' + String(event.source || 'system').toUpperCase() }),
            ]),
          ]));
        }
      }
      refreshIcons(drawer);
    } catch (err) {
      if (body) { clear(body); body.appendChild(el('div', { class: 'ck-empty', text: 'Timeline unavailable: ' + err.message })); }
    }
  }

  function renderDirectRoomsCockpit(data) {
    if ($('ckSubtitle')) $('ckSubtitle').textContent = 'Zoom truth, queue, and alerts — بلا دخول للـmeeting.';
    if ($('ckQueueTitle')) $('ckQueueTitle').textContent = 'Alerts live';
    if ($('ckQueueHint')) $('ckQueueHint').textContent = 'الصوت كيتشعل مرة وحدة غير مع alert جديد.';
    const live = $('ckLivePill');
    if (live) { live.className = 'ck-live-pill ck-on ck-live'; live.innerHTML = '<span></span>LIVE MONITOR'; }
    const alerts = Array.isArray(data.alerts) ? data.alerts : [];
    ckSoundNewAlerts(alerts);
    const notice = $('ckNotice');
    if (notice) {
      notice.classList.toggle('hidden', !alerts.length);
      notice.textContent = alerts.length ? alerts.length + ' alert(s) خاصهم الانتباه دابا.' : '';
    }
    const metrics = $('ckMetrics');
    if (metrics) {
      clear(metrics);
      const m = data.metrics || {};
      metrics.append(
        ckMetric(m.online, 'ONLINE', 'online'), ckMetric(m.ready, 'READY', 'ready'),
        ckMetric(m.busy, 'BUSY', 'busy'), ckMetric(m.waiting, 'WAITING', 'waiting'),
        ckMetric(m.alerts, 'ALERTS', 'alerts'),
      );
    }
    const teachers = $('ckTeachers');
    if (teachers) {
      clear(teachers);
      for (const teacher of (data.teachers || [])) {
        const stateName = teacher.state || 'offline';
        const card = el('article', { class: 'ck-teacher ck-direct-card ck-teacher--' + stateName, tabindex: '0', role: 'button' });
        card.style.setProperty('--ck-teacher-color', teacher.color || '#2563eb');
        const busyStatus = stateName === 'busy' && teacher.current_student;
        // Keep the LTR status shell and isolate the student's own writing
        // direction. Without BDI, an Arabic name can move "2 min" beside
        // "M3a" and make one student look like a count of two students.
        const statePill = busyStatus
          ? el('span', { class: 'ck-state ck-state-' + stateName, dir: 'ltr' }, [
              'BUSY — M3a ',
              el('bdi', { dir: 'auto', text: teacher.current_student }),
              ' — ' + ckElapsed(teacher.busy_since),
            ])
          : el('span', { class: 'ck-state ck-state-' + stateName, text: ckDirectStatusLabel(stateName) });
        const verification = teacher.last_verified_at
          ? 'Verified ' + ckElapsed(teacher.last_verified_at) + ' · ' + String(teacher.source || '').toUpperCase()
          : String(teacher.source || 'NO SIGNAL').toUpperCase();
        const info = el('div', { class: 'ck-direct-card-main' }, [
          el('div', { class: 'ck-direct-card-top' }, [
            el('span', { class: 'ck-teacher-name', text: teacher.name || 'Teacher' }),
            statePill,
          ]),
          el('span', { class: 'ck-teacher-current', text: teacher.next_student
            ? 'Next: ' + teacher.next_student + ' · Waiting ' + teacher.waiting_count
            : 'Next: — · Waiting ' + (teacher.waiting_count || 0) }),
          el('span', { class: 'ck-verified', text: verification }),
        ]);
        const actions = el('div', { class: 'ck-card-actions' }, [
          ckDirectAction('Call Teacher', 'phone', teacher.teacher_phone, teacher.name, 'call'),
          ckDirectAction('WhatsApp Teacher', 'message-circle', teacher.teacher_phone, teacher.name, 'wa'),
        ]);
        if (teacher.current_student) actions.append(
          ckDirectAction('Call Student', 'phone-call', teacher.current_student_phone, teacher.current_student, 'call'),
          ckDirectAction('WhatsApp Student', 'message-square', teacher.current_student_phone, teacher.current_student, 'wa'),
        );
        card.append(info, actions);
        card.addEventListener('click', () => ckOpenTeacherDrawer(teacher));
        card.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); ckOpenTeacherDrawer(teacher); } });
        teachers.appendChild(card);
      }
    }
    const body = $('ckBody');
    if (body) {
      clear(body);
      if (!alerts.length) body.appendChild(el('div', { class: 'ck-empty ck-all-clear', text: 'All clear — ما كاين حتى alert دابا.' }));
      for (const alert of alerts) {
        const card = el('article', { class: 'ck-alert ck-alert--' + alert.kind }, [
          el('span', { class: 'ck-alert-icon' }, [lucideIcon('triangle-alert')]),
          el('div', { class: 'ck-alert-copy' }, [
            el('strong', { text: ckAlertText(alert) }),
            el('span', { text: (alert.teacher_name || 'Teacher') + ' · ' + ckElapsed(alert.first_seen_at) }),
          ]),
        ]);
        const teacher = (data.teachers || []).find(t => String(t.id) === String(alert.teacher_id));
        if (teacher) card.addEventListener('click', () => ckOpenTeacherDrawer(teacher));
        body.appendChild(card);
      }
    }
    ckSyncClaimHeartbeat([]);
    const badge = $('cockpitTabBadge');
    const attention = Number(data.metrics?.alerts || 0) + Number(data.metrics?.waiting || 0);
    if (badge) { badge.textContent = attention ? String(attention) : ''; badge.classList.toggle('hidden', !attention); }
    refreshIcons($('testDayCockpitView'));
  }

  function renderTestDayCockpit() {
    const data = state.cockpit.data || {};
    if (data.mode === 'direct_rooms') return renderDirectRoomsCockpit(data);
    const teacherScope = data.scope === 'teacher';
    if ($('ckSubtitle')) $('ckSubtitle').textContent = teacherScope
      ? 'Join your room, receive your assigned students, and complete each test.'
      : 'Kol client, kol prof, w next click f nafs blassa.';
    if ($('ckQueueTitle')) $('ckQueueTitle').textContent = teacherScope ? 'Your students today' : 'Clients daba';
    if ($('ckQueueHint')) $('ckQueueHint').textContent = teacherScope
      ? 'Students appear here when Sales sends them to your room.'
      : 'Click ghir 3la l-action lkbira li katban.';
    const live = $('ckLivePill');
    if (live) {
      const on = data.enabled && data.hub?.configured;
      const controllerLive = on && data.hub?.controllerLive === true;
      // A Zoom webhook session alone is not automation. Show LIVE only while
      // the browser executor holds a fresh lease and can actually admit/route.
      live.className = 'ck-live-pill ' + (on ? (controllerLive ? 'ck-on ck-live' : 'ck-on') : 'ck-off');
      live.innerHTML = '<span></span>' + (on
        ? (controllerLive ? 'AUTO LIVE' : (data.hub?.live ? 'CONNECTING CONTROLLER' : 'Hub READY'))
        : 'Hub OFF');
    }
    const notice = $('ckNotice');
    if (notice) {
      notice.classList.toggle('hidden', !!(data.enabled && data.hub?.configured));
      notice.textContent = teacherScope
        ? (data.enabled ? 'The Zoom Hub configuration is incomplete. Please contact an administrator.' : 'The Central Hub is currently offline.')
        : (data.enabled
          ? 'Hub config na9sa. Check server config qbel ma ydkhol chi client.'
          : 'Central Hub mazal OFF. L-flow l9dim dyal rooms dyal profs ba9i khddam bla taghyir.');
    }

    const flows = Array.isArray(data.flows) ? data.flows : [];
    const active = flows.filter(f => !['done', 'not_arrived'].includes(f.state));
    ckSyncClaimHeartbeat(flows); // before any early return so an empty board also stops the heartbeat
    const metrics = $('ckMetrics');
    if (metrics) {
      clear(metrics);
      metrics.append(
        ckMetric(flows.filter(f => ['waiting_room', 'sales_queue'].includes(f.state)).length, teacherScope ? 'WAITING' : 'KITSNAW', 'waiting'),
        ckMetric(flows.filter(f => ['sales_check', 'sound_ok'].includes(f.state)).length, teacherScope ? 'WITH SALES' : 'M3A SALES', 'sales'),
        ckMetric(flows.filter(f => ['routing', 'in_test'].includes(f.state)).length, teacherScope ? 'IN TEST' : 'M3A PROFS', 'test'),
        ckMetric(flows.filter(f => f.state === 'done').length, teacherScope ? 'COMPLETED TODAY' : 'SALAW LYOUM', 'done'),
      );
    }

    const teachers = $('ckTeachers');
    if (teachers) {
      clear(teachers);
      for (const t of (data.teachers || [])) {
        const card = el('div', { class: 'ck-teacher ck-teacher--' + (t.state || 'offline') });
        card.style.setProperty('--ck-teacher-color', t.color || '#2563eb');
        card.append(
          el('div', {}, [
            el('span', { class: 'ck-teacher-name', text: t.name || (teacherScope ? 'Teacher' : 'Prof') }),
            el('span', { class: 'ck-teacher-current', text: t.current_student
              ? ((teacherScope ? 'With ' : 'M3a ') + t.current_student + ' - ' + ckElapsed(t.busy_since))
              : (teacherScope ? 'Available for the next student' : 'Ma3ndo 7ta client daba') }),
          ]),
          el('span', { class: 'ck-state ck-state-' + (t.state || 'offline'), text: String(t.state || 'offline').toUpperCase() }),
        );
        teachers.appendChild(card);
      }
    }

    const controllerPanel = $('ckControllerPanel');
    if (controllerPanel) {
      controllerPanel.classList.toggle('hidden', !data.can_control);
      const salesMode = data.controller_mode === 'sales';
      const title = controllerPanel.querySelector('.ck-controller-copy strong');
      if (title) title.textContent = salesMode ? 'Zoom dyalk' : 'Zoom Controller';
      const startButton = $('ckStartControllerBtn');
      if (startButton && !state.cockpit.controllerReady) startButton.textContent = salesMode ? 'DKHOL L ROOM DYALI' : 'DKHOL L ZOOM CONTROLLER';
      // Visual only: once the Zoom controller is READY the panel collapses to
      // a slim status line (the big CTA button is already hidden by the
      // ready handler; this just shrinks the chrome around the status text).
      controllerPanel.classList.toggle('ck-ready', !!state.cockpit.controllerReady);
    }
    const teacherPanel = $('ckTeacherPanel');
    if (teacherPanel) {
      teacherPanel.classList.toggle('hidden', data.scope !== 'teacher');
      teacherPanel.classList.toggle('ck-ready', !!state.cockpit.teacherReady);
    }
    const body = $('ckBody');
    if (!body) return;
    clear(body);
    ckApplyZoomView(); // both paths (empty board auto-expands the Zoom panel)
    if (!flows.length) {
      // scope === 'agent' → the board is per-agent (swat:B9): the empty state
      // means THEIR clients are absent, not the whole floor's.
      const agentScope = (data.scope || '') === 'agent';
      body.appendChild(el('div', { class: 'ck-empty', text: teacherScope
        ? (data.enabled ? 'No students assigned to you right now.' : 'The Zoom Hub is not available yet.')
        : (data.enabled
          ? (agentScope ? 'Ma kayn 7ta client dyalek lyoum daba.' : 'Ma kayn 7ta client lyoum daba.')
          : (agentScope ? 'Hub ila tcha3el, clients dyalek ghaybano hna.' : 'Hub ila tcha3el, ga3 clients ghaybano hna.')) }));
      return;
    }

    // Visual grouping only (design pass 2026-07-21): cards are ordered by
    // workflow phase with a small section header per phase, so the taslsol
    // reads top-to-bottom. Every card keeps the exact same action wiring.
    const ckGroups = [
      { key: 'attention', states: ['problem', 'disconnected'], label: teacherScope ? 'Needs attention' : 'Moshkil' },
      { key: 'waiting', states: ['waiting_room', 'sales_queue'], label: teacherScope ? 'Waiting' : 'Kitsnaw' },
      { key: 'sales', states: ['sales_check', 'sound_ok'], label: teacherScope ? 'With Sales' : 'M3a Sales' },
      { key: 'prof', states: ['teacher_queue', 'routing', 'in_test'], label: teacherScope ? 'In test' : 'M3a profs' },
      { key: 'later', states: ['not_arrived'], label: teacherScope ? 'Not arrived yet' : 'Mazal madkhlouch' },
      { key: 'done', states: ['done'], label: teacherScope ? 'Completed' : 'Salaw' },
    ];

    const buildFlowCard = (flow) => {
      const card = el('article', { class: 'ck-client ck-flow-' + (flow.state || 'unknown') + (flow.claimed_by_me ? ' ck-current' : '') });
      const showNoba = !!flow.queue_position && ['waiting_room', 'sales_queue'].includes(flow.state);
      const name = el('div', { class: 'ck-client-main' }, [
        showNoba ? el('span', { class: 'ck-noba', text: String(flow.queue_position), title: teacherScope ? 'Queue position' : 'Noba' }) : null,
        el('div', { class: 'ck-client-id' }, [
          el('span', { class: 'ck-client-name', text: flow.student_name || 'Client' }),
          el('div', { class: 'ck-client-meta' }, [
            el('span', { text: flow.slot_time || '--:--' }),
            el('span', { text: flow.teacher_name
              ? ((teacherScope ? 'Teacher ' : 'Prof ') + (ckTeacherLabel(flow.teacher_name) || flow.teacher_name))
              : (teacherScope ? 'Teacher not assigned' : 'Prof mazel') }),
            (!showNoba && flow.queue_position) ? el('span', { text: (teacherScope ? 'Queue ' : 'Noba ') + flow.queue_position }) : null,
          ]),
        ]),
      ]);
      const status = el('div', { class: 'ck-client-status' }, [
        el('span', { class: 'ck-chip ck-chip-' + flow.state, text: ckStatusLabel(flow.state) }),
        el('span', { class: 'ck-elapsed', text: ckElapsed(flow.state_since || flow.last_transition_at) }),
      ]);
      const teacherCell = el('div', { class: 'ck-teacher-cell' });
      const actions = el('div', { class: 'ck-actions' });

      // Most-advanced action wins (audit blocker 2): the server flags are now
      // mutually exclusive, but keep route > sound > claim priority here so a
      // stale/overlapping payload can never hide SON OK or DWEZ L PROF behind
      // BDA M3AH again.
      if (flow.can_sound_ok) {
        const b = el('button', { class: 'ck-big-btn ck-success', text: 'SON OK' });
        b.addEventListener('click', () => ckRunAction(flow, 'sound-ok', {}, b));
        actions.appendChild(b);
      } else if (flow.can_route) {
        const select = el('select', { class: 'ck-teacher-select', 'aria-label': 'Prof' });
        for (const t of (data.teachers || []).filter(x => x.state === 'free' || x.id === flow.teacher_id)) {
          const o = el('option', { value: t.id, text: (t.name || 'Prof') + (t.state === 'free' ? ' - FREE' : ' - ' + String(t.state || '').toUpperCase()) });
          if (t.id === flow.teacher_id) o.selected = true;
          select.appendChild(o);
        }
        teacherCell.appendChild(select);
        const b = el('button', { class: 'ck-big-btn ck-route', text: 'DWEZ L PROF' });
        b.disabled = !state.cockpit.controllerReady || !select.value;
        b.addEventListener('click', () => ckRunAction(flow, 'route', { teacher_id: select.value, operation_id: ckOperationId() }, b));
        actions.appendChild(b);
      } else if (flow.can_claim) {
        const b = el('button', { class: 'ck-big-btn ck-primary', text: state.cockpit.controllerReady || flow.location === 'main_room' ? 'BDA M3AH' : 'DKHOL L ZOOM LWL' });
        b.disabled = !state.cockpit.controllerReady && flow.location !== 'main_room';
        b.addEventListener('click', () => ckRunAction(flow, 'claim', {}, b));
        actions.appendChild(b);
      } else {
        teacherCell.appendChild(el('span', { class: 'ck-elapsed', text: flow.claimed_by && !flow.claimed_by_me
          ? ((teacherScope ? 'With Sales: ' : 'M3a ') + flow.claimed_by)
          : '' }));
      }

      if (flow.can_complete) {
        const b = el('button', { class: 'ck-big-btn ck-finish', text: teacherScope ? 'FINISH TEST' : 'SALI TEST' });
        b.addEventListener('click', () => ckRunAction(flow, 'complete', {}, b));
        actions.appendChild(b);
      }
      card.append(name, status, teacherCell, actions);
      return card;
    };

    const seen = new Set();
    for (const group of ckGroups) {
      const items = flows.filter(f => group.states.includes(f.state));
      if (!items.length) continue;
      for (const f of items) seen.add(f);
      body.appendChild(el('div', { class: 'ck-group-head ck-group-' + group.key }, [
        el('span', { class: 'ck-group-label', text: group.label }),
        el('span', { class: 'ck-group-count', text: String(items.length) }),
      ]));
      for (const flow of items) body.appendChild(buildFlowCard(flow));
    }
    // Defensive: an unknown/new server state must never drop a card off the board.
    for (const flow of flows) if (!seen.has(flow)) body.appendChild(buildFlowCard(flow));

    const badge = $('cockpitTabBadge');
    if (badge) {
      badge.textContent = active.length ? String(active.length) : '';
      badge.classList.toggle('hidden', !active.length);
    }
    refreshIcons($('testDayCockpitView'));
  }

  async function loadTestDayCockpit(silent) {
    if (state.cockpit.loading) return;
    state.cockpit.loading = true;
    try {
      state.cockpit.data = await ckJson('/api/test-day/cockpit?date=' + encodeURIComponent(ckToday()), { timeoutMs: 15000 });
      renderTestDayCockpit();
      ckMaybeAutoJoin();
    } catch (err) {
      if (!silent) toast('Cockpit: ' + err.message, 'error');
      const body = $('ckBody');
      if (body && !state.cockpit.data) {
        clear(body);
        body.appendChild(el('div', { class: 'ck-empty', text: ckTeacherScope() ? 'The Cockpit could not load. Click Refresh.' : 'Cockpit ma t7ellch. Klik Refresh.' }));
      }
    } finally { state.cockpit.loading = false; }
  }

  function ensureTestDayCockpitInit() {
    if (!state.cockpit.inited) {
      state.cockpit.inited = true;
      $('ckRefreshBtn')?.addEventListener('click', () => loadTestDayCockpit(false));
      $('ckStartControllerBtn')?.addEventListener('click', () => {
        state.cockpit.autoJoinNextAt = 0;
        ckStartController(false);
      });
      $('ckStartTeacherBtn')?.addEventListener('click', () => {
        state.cockpit.autoJoinNextAt = 0;
        ckStartTeacher(false);
      });
      $('ckZoomDockToggle')?.addEventListener('click', ckToggleZoomView);
      $('ckDrawerClose')?.addEventListener('click', ckCloseTeacherDrawer);
      $('ckDrawerBackdrop')?.addEventListener('click', ckCloseTeacherDrawer);
      window.addEventListener('message', ckOnControllerMessage);
      window.addEventListener('pagehide', () => ckReleaseControllerLease(true));
    }
    if (state.socket && !state.cockpit.socketBound) {
      state.cockpit.socketBound = true;
      state.socket.on('test-day:update', () => {
        if (state.activeView === 'test-day-cockpit') loadTestDayCockpit(true);
      });
    }
    clearInterval(state.cockpit.timer);
    state.cockpit.timer = setInterval(() => {
      if (state.activeView === 'test-day-cockpit') loadTestDayCockpit(true);
    }, 10000);
    loadTestDayCockpit(false);
  }

  // ============ Zoom Live (2026-08-24) ============
  // Raw Zoom Dashboard truth: GET /api/zoom/live-meetings (server-cached 25s).
  // Poll every 30s while the view is active; live durations tick client-side
  // every second off data-zl-start attributes so the DOM isn't rebuilt.
  const _zl = { inited: false, timer: null, ticker: null, data: null, loading: false, expanded: new Set(), showOthers: false };

  function _zlFmtDuration(sec) {
    if (sec == null || !Number.isFinite(sec)) return '—';
    const s = Math.max(0, Math.round(sec));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
    const mm = String(m).padStart(2, '0'), rr = String(r).padStart(2, '0');
    return h > 0 ? (h + ':' + mm + ':' + rr) : (m + ':' + rr);
  }
  function _zlFmtClock(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return MA.fmt('fr-hm', 'fr-FR', { hour: '2-digit', minute: '2-digit' }, d);
  }
  function _zlTick() {
    const view = $('zoomLiveView');
    if (!view || view.classList.contains('hidden')) return;
    const now = Date.now();
    view.querySelectorAll('[data-zl-start]').forEach((n) => {
      const t = Date.parse(n.getAttribute('data-zl-start'));
      if (Number.isFinite(t)) n.textContent = _zlFmtDuration((now - t) / 1000);
    });
  }

  function _zlFlagChips(flags) {
    const defs = [
      ['video', 'video', 'Video'],
      ['screen_share', 'monitor-up', 'Share'],
      ['recording', 'circle-dot', 'REC'],
      ['voip', 'mic', 'VoIP'],
    ];
    const wrap = el('div', { class: 'zl-flags' });
    for (const [key, icon, label] of defs) {
      if (!flags || !flags[key]) continue;
      wrap.appendChild(el('span', { class: 'zl-flag zl-flag-' + key, title: label }, [lucideIcon(icon), label]));
    }
    return wrap;
  }

  // Move a client who is stuck in one teacher's waiting room to a teacher who
  // has room. The booking is transplanted server-side; the client's existing
  // /meet link resolves the NEW teacher, so they just click it again — Zoom
  // gives us no way to drag somebody across meetings.
  function _zlCloseMovePanel() {
    document.querySelectorAll('.zl-move-panel').forEach(n => n.remove());
    _zl.movePanelFor = null;
  }

  async function _zlOpenMovePanel(anchorBtn, participant) {
    const wasOpenHere = _zl.movePanelFor === anchorBtn;
    _zlCloseMovePanel();
    if (wasOpenHere) return; // second click on the same button closes it

    const booking = participant.booking;
    const panel = el('div', { class: 'zl-move-panel', onclick: (e) => e.stopPropagation() }, [
      el('div', { class: 'zl-move-title', text: 'Nqel ' + (booking.lead_name || participant.name) + ' l:' }),
      booking.match === 'name'
        ? el('div', { class: 'zl-move-warn', text: "⚠︎ M-3arraf b l'ism bark — t2ekked blli howa hada 9bel ma t-nqel." })
        : null,
      el('div', { class: 'zl-move-list', text: 'Kan-9elleb 3la teachers khawyin…' }),
    ]);
    // Body-anchored + fixed: the participant table is an overflow container and
    // would slice the picker in half.
    document.body.appendChild(panel);
    _zl.movePanelFor = anchorBtn;
    const r = anchorBtn.getBoundingClientRect();
    panel.style.top = Math.round(r.bottom + 6) + 'px';
    panel.style.left = Math.round(Math.max(8, Math.min(r.right - panel.offsetWidth, window.innerWidth - panel.offsetWidth - 8))) + 'px';

    const list = panel.querySelector('.zl-move-list');
    try {
      const r = await api('/api/zoom/live/move-targets?slot_id=' + encodeURIComponent(booking.slot_id));
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      clear(list);
      const teachers = j.teachers || [];
      if (!teachers.length) {
        list.appendChild(el('div', { class: 'zl-move-empty', text: 'Walou teacher khawi f had l wa9t (kollhom 3amrin wla barra l horaire).' }));
        return;
      }
      for (const t of teachers) {
        const free = Number(t.free_seats || 0);
        list.appendChild(el('button', {
          class: 'zl-move-opt', type: 'button',
          onclick: () => _zlDoMove(panel, booking, t),
        }, [
          el('span', { class: 'zl-move-opt-name', text: t.display_name }),
          el('span', { class: 'zl-move-opt-free', text: free + ' bla' + (free === 1 ? 'sa' : 'yes') }),
        ]));
      }
    } catch (err) {
      clear(list);
      list.appendChild(el('div', { class: 'zl-move-empty', text: 'Ma 9dernach n-jibo l teachers: ' + err.message }));
    }
  }

  async function _zlDoMove(panel, booking, teacher) {
    panel.querySelectorAll('button').forEach(b => { b.disabled = true; });
    const list = panel.querySelector('.zl-move-list');
    if (list) { clear(list); list.appendChild(el('div', { class: 'zl-move-empty', text: 'Kan-nqel…' })); }
    try {
      const r = await api('/api/zoom/live/move', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot_id: booking.slot_id, teacher_id: teacher.id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      _zlCloseMovePanel();
      toast(
        j.notified
          ? (booking.lead_name || 'Client') + ' t-neqqel l ' + teacher.display_name + ' — t-sifet lih WhatsApp bach y-3awed klik.'
          : (booking.lead_name || 'Client') + ' t-neqqel l ' + teacher.display_name + ' — walakin l WhatsApp ma-mchach, 3eyyet lih.',
        j.notified ? 'success' : 'warning',
      );
      loadZoomLive(true);
    } catch (err) {
      _zlCloseMovePanel();
      toast('Ma t-neqqelch: ' + err.message, 'error');
    }
  }

  // Loose compare so "meryeme ouali" and "Meryeme Ouali" do not both render.
  function _zlNameKeyish(v) {
    return String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().replace(/[^a-z0-9\u0600-\u06ff]+/g, ' ').trim();
  }
  const _nameKeyish = _zlNameKeyish;

  function _zlParticipantRows(meeting) {
    const list = meeting.participants;
    if (list === null) {
      return el('div', { class: 'zl-empty zl-empty-sub', text: 'Participants ma bansh — Zoom metrics rate limit. 3awed refresh mn ba3d chwiya.' });
    }
    if (!list.length) {
      return el('div', { class: 'zl-empty zl-empty-sub', text: '7etta participant daba f had l meeting.' });
    }
    const table = el('table', { class: 'zl-ptable' }, [
      el('thead', null, el('tr', null, [
        el('th', { text: 'Participant' }),
        el('th', { text: 'Joined' }),
        el('th', { text: 'F l meeting' }),
        el('th', { text: 'Device' }),
        el('th', { text: 'Status' }),
        el('th', { class: 'zl-th-act' }),
      ])),
      el('tbody', null, list.map((p) => {
        // Zoom hands both states back on the same endpoint; without this chip a
        // client stuck at the door looks exactly like one sitting in the test.
        const waiting = p.status === 'waiting';
        return el('tr', { class: waiting ? 'zl-prow-wait' : null }, [
          el('td', { class: 'zl-pname' }, [
            el('div', { text: p.name || '—' }),
            p.booking ? el('div', { class: 'zl-psub' }, [
              // The Zoom display name is whatever they typed; show the booked
              // name too when the two disagree, or nobody can match them up.
              _nameKeyish(p.booking.lead_name) !== _nameKeyish(p.name)
                ? el('span', { class: 'zl-psub-real', text: p.booking.lead_name }) : null,
              el('span', { class: 'zl-psub-agent', title: 'L agent li 3ando had l client' }, [
                lucideIcon('user-round'),
                userLabel(p.booking.agent_id, p.booking.agent_name || p.booking.agent_id || 'bla agent'),
              ]),
            ]) : null,
          ]),
          el('td', { text: _zlFmtClock(p.join_time) }),
          el('td', { class: 'zl-num', 'data-zl-start': p.join_time || null, text: _zlFmtDuration(p.duration_sec) }),
          el('td', { class: 'zl-device', text: p.device || '—' }),
          el('td', null, el('span', { class: 'zl-pbadges' }, [
            el('span', {
              class: 'zl-pstate ' + (waiting ? 'zl-pstate-wait' : 'zl-pstate-in'),
              title: waiting ? "Mazal f salle d'attente — ma dkhelsh l meeting" : 'Dakhel f l meeting',
            }, [lucideIcon(waiting ? 'hourglass' : 'check'), waiting ? "Salle d'attente" : 'Dakhel']),
            p.sharing ? el('span', { class: 'zl-flag zl-flag-screen_share', text: 'Share' }) : null,
            p.recording ? el('span', { class: 'zl-flag zl-flag-recording', text: 'REC' }) : null,
            (!p.sharing && !p.recording && p.network_type) ? el('span', { class: 'zl-pquiet', text: p.network_type }) : null,
          ])),
          el('td', { class: 'zl-pact' }, (waiting && p.booking) ? el('button', {
            class: 'zl-move-btn', type: 'button',
            title: 'Nqlo l teacher akhor — l link dyalo ghadi y-weddih l room j-jdida',
            onclick: (e) => { e.stopPropagation(); _zlOpenMovePanel(e.currentTarget, p); },
          }, [lucideIcon('arrow-right-left'), 'Bedel teacher']) : null),
        ]);
      })),
    ]);
    return el('div', { class: 'zl-ptable-wrap' }, table);
  }

  // Join as Assistant (2026-10-07): opens /zoom-assist.html, which takes the
  // single assistant seat and joins the meeting as "Lougha Support" with
  // co-host powers. One seat for the whole account — while somebody holds it,
  // every other row shows who, instead of a button that would only 409.
  function _zlAssistBtn(meeting, seat) {
    const me = String(state.me?.username || '').toLowerCase();
    const mine = seat && seat.username === me;
    const here = seat && String(seat.meeting_id) === String(meeting.id);
    const busy = seat && !mine;
    const label = mine && here ? 'Assisting' : 'Assist';
    const title = busy
      ? (seat.display_name || seat.username) + ' is assisting "' + (seat.topic || seat.meeting_id) + '" — one assistant at a time'
      : 'Join this meeting as "Lougha Support", mic and camera off, with co-host controls (mute, remove, waiting room)';
    return el('button', {
      class: 'zl-assist-btn' + (mine && here ? ' zl-assist-on' : ''), type: 'button', title,
      disabled: busy ? true : null,
      onclick: (e) => {
        e.stopPropagation();
        const w = window.open('/zoom-assist.html?m=' + encodeURIComponent(meeting.id), 'lougha-zoom-assist');
        if (!w) toast('Allow pop-ups for Leads7 to open the assistant tab.', 'warning');
        setTimeout(() => loadZoomLive(true), 4000);
      },
    }, [lucideIcon(busy ? 'lock' : 'headset'), busy ? 'Busy' : label]);
  }
  // Copy the room's link two ways (Ayoub, 2026-10-19): Chrome = Zoom web
  // client, joins from the browser with nothing to install; App = the normal
  // /j/ link that opens the Zoom app. Both carry the passcode.
  function _zlCopyLinks(meeting) {
    const links = meeting.join_links;
    const who = meeting.teacher ? meeting.teacher.display_name : (meeting.topic || ('Meeting ' + meeting.id));
    const btn = (kind, label) => {
      const url = links && links[kind];
      return el('button', {
        class: 'zl-link-btn', type: 'button',
        disabled: url ? null : true,
        title: url ? ('Copy link ' + label + ' — ' + url) : "Zoom ma-3tach l-link dyal had l meeting",
        onclick: (e) => {
          e.stopPropagation();
          const b = e.currentTarget;
          _websiteTestCopy(url).then(() => {
            toast('Link ' + label + ' dyal ' + who + ' t-copia', 'success');
            b.classList.add('zl-link-done');
            setTimeout(() => b.classList.remove('zl-link-done'), 1500);
          }).catch(() => toast('Copy ma-mchach — 3awed', 'error'));
        },
      }, [lucideIcon('copy'), label]);
    };
    return el('div', { class: 'zl-links' }, [btn('browser', 'Chrome'), btn('app', 'App')]);
  }

  // Meetings that are not a test room: classes (also our teachers' own, like
  // Issam's "LC 172 Kids") and personal rooms. Off by default — but counted out
  // loud, so an unmapped teacher shows up as "1 meeting akhor" instead of
  // disappearing.
  function _zlOthersToggle(count) {
    return el('button', {
      class: 'zl-others-toggle', type: 'button',
      onclick: () => { _zl.showOthers = !_zl.showOthers; renderZoomLive(); },
    }, [
      lucideIcon(_zl.showOthers ? 'eye-off' : 'eye'),
      _zl.showOthers
        ? 'Khebbi l meetings li machi dyal tests'
        : (count + ' meeting' + (count === 1 ? '' : 's') + ' akhor machi dyal tests (classes…) — bayynhom'),
    ]);
  }

  function renderZoomLive() {
    const body = $('zlBody');
    if (!body) return;
    const data = _zl.data;
    clear(body);
    const notice = $('zlNotice');
    if (notice) {
      const isStale = !!(data && data.stale);
      notice.classList.toggle('hidden', !isStale);
      if (isStale) notice.textContent = data.stale_reason === 'rate_limited'
        ? 'Zoom rate limit — kan3ardo akhir snapshot s7i7 (stale).'
        : 'Zoom ma jawbsh daba — kan3ardo akhir snapshot s7i7 (stale).';
    }
    if (!data) { body.appendChild(el('div', { class: 'zl-empty', text: 'Loading...' })); return; }
    const others = data.other_meetings || [];
    const shown = (data.meetings || []).concat(_zl.showOthers ? others : []);
    const assistOn = !!(data.assist && data.assist.allowed);
    if (!shown.length) {
      body.appendChild(el('div', { class: 'zl-empty' }, [
        el('div', { class: 'zl-empty-icon' }, lucideIcon('video-off')),
        el('div', { class: 'zl-empty-title', text: '0 meetings live daba' }),
        el('div', { class: 'zl-empty-hint', text: 'Melli chi teacher ybda meeting f Zoom, ghadi yban hna automatiquement.' }),
        others.length ? _zlOthersToggle(others.length) : null,
      ]));
      refreshIcons($('zoomLiveView'));
      return;
    }
    const table = el('table', { class: 'zl-table' }, [
      el('thead', null, el('tr', null, [
        el('th', { text: 'Meeting' }),
        el('th', { text: 'Host' }),
        el('th', { text: 'Bda' }),
        el('th', { text: 'Live mn' }),
        el('th', { class: 'zl-th-center', text: 'Participants' }),
        el('th', { text: 'Status' }),
        el('th', { class: 'zl-th-links', text: 'Copy link' }),
        assistOn ? el('th', { class: 'zl-th-assist' }) : null,
        el('th', { class: 'zl-th-chev' }),
      ])),
    ]);
    const tbody = el('tbody');
    for (const m of shown) {
      const key = String(m.id);
      const open = _zl.expanded.has(key);
      const t = m.teacher;
      const row = el('tr', {
        class: 'zl-row' + (open ? ' zl-open' : ''),
        onclick: () => {
          if (_zl.expanded.has(key)) _zl.expanded.delete(key); else _zl.expanded.add(key);
          renderZoomLive();
        },
      }, [
        el('td', { class: 'zl-topic' }, [
          el('div', { class: 'zl-topic-name', text: m.topic || ('Meeting ' + m.id) }),
          el('div', { class: 'zl-topic-id', text: 'ID ' + m.id }),
        ]),
        el('td', null, el('div', { class: 'zl-host' }, [
          t ? el('span', { class: 'zl-teacher-dot', style: t.color ? ('background:' + t.color) : null }) : null,
          el('div', null, [
            el('div', { class: 'zl-host-name', text: t ? t.display_name : (m.host || '—') }),
            t
              ? el('div', { class: 'zl-host-sub', text: t.tests_booked_today + ' test' + (t.tests_booked_today === 1 ? '' : 's') + ' booked lyoum' })
              : (m.host_email ? el('div', { class: 'zl-host-sub', text: m.host_email }) : null),
          ]),
        ])),
        el('td', { class: 'zl-num', text: _zlFmtClock(m.start_time) }),
        el('td', { class: 'zl-num zl-dur', 'data-zl-start': m.start_time || null, text: _zlFmtDuration(m.duration_live_sec) }),
        el('td', { class: 'zl-count-cell' }, el('span', { class: 'zl-count-group' }, [
          el('span', {
            class: 'zl-count' + (m.participant_count ? '' : ' zl-count-zero'),
            title: 'F l meeting',
            text: String(m.participant_count),
          }),
          m.waiting_count ? el('span', { class: 'zl-count zl-count-wait', title: "Kay-tsennaw f salle d'attente" }, [lucideIcon('hourglass'), String(m.waiting_count)]) : null,
        ])),
        el('td', null, _zlFlagChips(m.flags)),
        el('td', { class: 'zl-links-cell' }, _zlCopyLinks(m)),
        assistOn ? el('td', { class: 'zl-assist-cell' }, _zlAssistBtn(m, data.assist.seat)) : null,
        el('td', { class: 'zl-chev' }, lucideIcon(open ? 'chevron-up' : 'chevron-down')),
      ]);
      tbody.appendChild(row);
      if (open) {
        tbody.appendChild(el('tr', { class: 'zl-detail-row' },
          el('td', { colspan: assistOn ? '9' : '8' }, _zlParticipantRows(m))));
      }
    }
    table.appendChild(tbody);
    body.appendChild(el('div', { class: 'zl-table-wrap' }, table));
    if (others.length) body.appendChild(_zlOthersToggle(others.length));
    refreshIcons($('zoomLiveView'));
  }

  async function loadZoomLive(silent) {
    if (_zl.loading) return;
    _zl.loading = true;
    const btn = $('zlRefreshBtn');
    if (!silent && btn) btn.disabled = true;
    try {
      const r = await api('/api/zoom/live-meetings');
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.message || j.error || ('HTTP ' + r.status));
      _zl.data = j;
      const upd = $('zlUpdated');
      if (upd) upd.textContent = 'Maj ' + MA.fmt('fr-hms', 'fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }, Date.now());
      renderZoomLive();
    } catch (err) {
      if (!silent) toast('Zoom Live: ' + err.message, 'error');
      if (!_zl.data) {
        const body = $('zlBody');
        if (body) {
          clear(body);
          body.appendChild(el('div', { class: 'zl-empty' }, [
            el('div', { class: 'zl-empty-icon' }, lucideIcon('cloud-off')),
            el('div', { class: 'zl-empty-title', text: 'Zoom Live ma t7ellch' }),
            el('div', { class: 'zl-empty-hint', text: err.message + ' — klik Refresh bach t3awed.' }),
          ]));
          refreshIcons($('zoomLiveView'));
        }
      }
    } finally {
      _zl.loading = false;
      if (btn) btn.disabled = false;
    }
  }

  function ensureZoomLiveInit() {
    if (!_zl.inited) {
      _zl.inited = true;
      $('zlRefreshBtn')?.addEventListener('click', () => loadZoomLive(false));
      document.addEventListener('click', () => _zlCloseMovePanel());
      window.addEventListener('resize', () => _zlCloseMovePanel());
      window.addEventListener('scroll', () => _zlCloseMovePanel(), true);
    }
    clearInterval(_zl.timer);
    _zl.timer = setInterval(() => {
      // Never re-render under an open teacher picker — the agent would lose it
      // mid-choice and the click would land on nothing.
      if (state.activeView === 'zoom-live' && !document.querySelector('.zl-move-panel')) loadZoomLive(true);
    }, 30000);
    clearInterval(_zl.ticker);
    _zl.ticker = setInterval(_zlTick, 1000);
    loadZoomLive(false);
  }

  // Supervisor board: a fresh PBX sample, an explicit handler, and confirmed history.
  const _cxl = { data: null, fetchedAt: 0, loading: false, timer: null, ticker: null, inited: false,
    lastFresh: null, clocks: new Map(), openDetails: new Set(), request: null, generation: 0 };
  const CXL_LABELS = {
    human: { text: 'En conversation', icon: 'headset', tone: 'human' },
    ai: { text: 'Avec IA', icon: 'bot', tone: 'ai' },
    talking: { text: 'En conversation', icon: 'phone', tone: 'unknown' },
    ringing: { text: 'Sonnerie', icon: 'bell-ring', tone: 'ringing' },
    connecting: { text: 'Connexion', icon: 'phone-outgoing', tone: 'ringing' },
    held: { text: 'En attente', icon: 'pause', tone: 'unknown' },
    unknown: { text: 'État non confirmé', icon: 'circle-help', tone: 'unknown' },
    idle: { text: 'Libre', icon: 'phone', tone: 'idle' },
    offline: { text: 'Trunk déconnecté', icon: 'unplug', tone: 'idle' },
    unconfigured: { text: 'Non configurée', icon: 'circle-minus', tone: 'idle' },
    stale: { text: 'À confirmer', icon: 'wifi-off', tone: 'idle' },
  };
  function cxlFresh(d = _cxl.data, now = Date.now()) {
    const received = Date.parse(d?.received_at), source = Date.parse(d?.pbx_ts);
    return !!(d?.online && Number.isFinite(received) && Number.isFinite(source)
      && now - received >= -5000 && now - source >= -5000
      && now - received < 30000 && now - source < 30000);
  }
  function cxlPhase(call) {
    if (call.phase && CXL_LABELS[call.phase]) return call.phase;
    if (call.talking) return 'talking';
    if (/^(ringing|ring)$/i.test(call.status || '')) return 'ringing';
    if (/^(connecting|dialing|dialling)$/i.test(call.status || '')) return 'connecting';
    if (/^(held|hold|onhold)$/i.test(call.status || '')) return 'held';
    return 'unknown';
  }
  function cxlState(call, line, fresh) {
    if (!fresh) return CXL_LABELS.stale;
    if (call) {
      const phase = cxlPhase(call);
      return phase === 'talking' ? (['human', 'ai'].includes(call.handler) ? CXL_LABELS[call.handler] : CXL_LABELS.talking) : CXL_LABELS[phase];
    }
    if (line?.port === 2 && !line.number) return CXL_LABELS.unconfigured;
    return line?.online === true ? CXL_LABELS.idle : line?.online === false ? CXL_LABELS.offline : CXL_LABELS.stale;
  }
  function cxlClockKey(call) { return String(call.id) + ':' + (call.established_at || call.since_at || call.last_change_at || ''); }
  function cxlClockValue(clock, now = Date.now()) {
    if (!clock) return null;
    return clock.value + (clock.running ? Math.max(0, Math.floor((Math.min(now, clock.expires) - clock.at) / 1000)) : 0);
  }
  function cxlSyncClocks(d, now = Date.now()) {
    const next = new Map(), fresh = cxlFresh(d, now);
    const expires = Math.min(Date.parse(d.received_at), Date.parse(d.pbx_ts)) + 30000;
    for (const call of d.calls || []) {
      const key = cxlClockKey(call), prev = _cxl.clocks.get(key);
      const sample = call.duration_sec == null ? null : Number(call.duration_sec);
      if (!Number.isFinite(sample) && !prev) continue;
      const prior = prev ? cxlClockValue(prev, now) : null;
      next.set(key, { value: fresh ? Math.max(0, sample || 0, prior || 0) : prior ?? Math.max(0, sample || 0),
        at: now, expires, running: fresh });
    }
    _cxl.clocks = next;
  }
  function cxlTime(seconds) {
    if (seconds == null || !Number.isFinite(Number(seconds))) return '—';
    const s = Math.max(0, Math.floor(Number(seconds))), h = Math.floor(s / 3600);
    return (h ? h + ':' : '') + String(Math.floor(s / 60) % 60).padStart(2, '0') + ':' + String(s % 60).padStart(2, '0');
  }
  function cxlClockLabel(at) {
    const t = Date.parse(at);
    return Number.isFinite(t) ? MA.loc(new Date(t), 't', 'fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'Heure inconnue';
  }
  function cxlIcon(name, className = '') {
    const icon = lucideIcon(name); icon.setAttribute('aria-hidden', 'true');
    if (className) icon.className = className;
    return icon;
  }
  function cxlLabel(status) {
    return el('span', { class: 'cxl-state cxl-tone-' + status.tone }, [cxlIcon(status.icon), el('span', { text: status.text })]);
  }
  function cxlDuration(call) {
    const node = el('time', { class: 'cxl-duration', 'aria-label': 'Durée de cet état' });
    node.dataset.cxlClock = cxlClockKey(call);
    node.textContent = cxlTime(cxlClockValue(_cxl.clocks.get(cxlClockKey(call))));
    return node;
  }
  function cxlHandler(call, fresh) {
    if (!fresh) return 'Dernière activité connue';
    const phase = cxlPhase(call);
    if (phase === 'ringing') return 'En attente de réponse';
    if (phase === 'connecting') return 'Mise en relation';
    if (call.handler === 'human') return (call.handler_name || call.agent_name || 'Agent identifié') + ' · Agent';
    if (call.handler === 'ai') return (call.handler_name || 'Assistant') + ' · IA';
    return 'Intervenant non confirmé';
  }
  function cxlLineName(line) {
    return ({ 1: 'Doha / Support', 2: 'Sans SIM', 3: 'Meriem / International', 4: 'Kanza', 5: 'Support WhatsApp',
      6: 'Bouchra', 7: 'Secours', 8: 'Support enseignants' })[line.port] || 'Ligne non identifiée';
  }
  function cxlDetail(call, line) {
    const key = cxlClockKey(call);
    const details = el('details', { class: 'cxl-details' }, [el('summary', { title: 'Détails de la ligne et de l’appel', 'aria-label': 'Détails de l’appel' }, [cxlIcon('ellipsis-vertical')])]);
    details.open = _cxl.openDetails.has(key);
    details.addEventListener('toggle', () => {
      if (!details.isConnected) return;
      if (details.open) _cxl.openDetails.add(key); else _cxl.openDetails.delete(key);
    });
    const direction = { inbound: 'Entrant', outbound: 'Sortant', internal: 'Interne' }[call.direction] || 'Sens non confirmé';
    const content = el('div', { class: 'cxl-detail-popover' }, [
      el('strong', { text: direction + (call.ext ? ' · Ext. ' + call.ext : '') }),
      el('p', { text: line?.number ? 'Numéro de ligne : ' + line.number : 'Ligne non identifiée' }),
      el('p', { text: 'Statut PBX : ' + (call.status || 'Non communiqué') }),
      el('p', { text: call.handler === 'unknown' || !call.handler ? 'L’appel est visible, mais son intervenant n’est pas confirmé.' : cxlHandler(call, true) }),
      el('p', { text: call.raw ? [call.raw.caller, call.raw.callee].filter(Boolean).join(' → ') : '' }),
    ]);
    details.appendChild(content);
    return details;
  }
  // «Écouter» (Ayoub, 2026-09-28): an admin listens in on a live call from
  // their own Leads7 softphone — no 3CX app. Offered on answered calls with a
  // client number that a person (not Kanza) handles; the office gateway finds
  // the agent's browser leg by that number and says so when there is none (3CX
  // app / desk phone). softphone.js owns the listen bar and its three modes.
  function cxlListenBtn(call, fresh) {
    const sp = window.Softphone;
    const digits = String(call.client_phone || '').replace(/\D/g, '');
    if (!fresh || !sp || typeof sp.listen !== 'function' || call.handler === 'ai' || digits.length < 9
      || !['talking', 'held'].includes(cxlPhase(call))) return null;
    const on = sp.listening();
    const mine = !!(on && on.number.slice(-9) === digits.slice(-9));
    const blocked = mine ? '' : !sp.isReady() ? 'Softphone non connecté'
      : sp.status().calls ? 'Terminez votre appel avant d’écouter' : '';
    const label = [call.handler_name, call.lead?.name || call.client_label || call.client_phone].filter(Boolean).join(' · ');
    const btn = el('button', { type: 'button', class: 'cxl-listen' + (mine ? ' is-on' : ''), 'aria-pressed': mine ? 'true' : 'false',
      title: mine ? 'Arrêter l’écoute' : blocked || 'Écouter cet appel en direct' }, [cxlIcon('headphones'), el('span', { text: mine ? 'À l’écoute' : 'Écouter' })]);
    if (blocked) btn.disabled = true;
    btn.addEventListener('click', () => {
      if (mine) { sp.stopListen(); return; }
      btn.disabled = true;
      sp.listen(digits, label).catch(err => { toast(err?.message || 'Écoute impossible', 'error'); renderThreecxLive(); });
    });
    return btn;
  }
  function cxlLineRow(line, call, fresh, continued = false) {
    const status = cxlState(call, line, fresh);
    const row = el('tr', { class: 'cxl-row cxl-tone-' + status.tone + (call ? ' cxl-row-active' : ''), 'data-cxl-phase': call ? cxlPhase(call) : 'idle' });
    const identity = el('div', { class: 'cxl-line-identity' }, [
      el('span', { class: 'cxl-line-number', text: line.port || '—' }),
      el('div', {}, [el('strong', { text: cxlLineName(line), title: line.owner || '' }),
        el('span', { class: 'cxl-secondary', text: continued ? 'Autre appel sur cette ligne' : line.number || (line.port === 2 ? 'Aucune SIM configurée' : 'Numéro à confirmer') })]),
    ]);
    row.appendChild(el('th', { scope: 'row' }, [identity]));
    row.appendChild(el('td', { 'data-label': 'Appel' }, call ? [el('div', { class: 'cxl-caller' }, [cxlIcon(call.direction === 'outbound' ? 'phone-outgoing' : 'phone-incoming'),
      el('div', {}, [el('span', { text: call.lead?.name || call.client_label || call.client_phone || 'Participant non identifié' }),
        call.lead?.name && call.client_phone ? el('span', { class: 'cxl-secondary', text: call.client_phone }) : null])])] : [el('span', { class: 'cxl-empty-dash', text: '—' })]));
    row.appendChild(el('td', { 'data-label': 'État' }, [cxlLabel(status)]));
    row.appendChild(el('td', { 'data-label': 'Pris en charge par' }, call ? [el('div', { class: 'cxl-handler' }, [cxlIcon(call.handler === 'ai' ? 'bot' : call.handler === 'human' ? 'headset' : 'circle-help'), el('span', { text: cxlHandler(call, fresh) })])] : [el('span', { class: 'cxl-empty-dash', text: '—' })]));
    row.appendChild(el('td', { 'data-label': 'Durée', class: 'cxl-time-cell' }, call ? [cxlDuration(call)] : [el('span', { class: 'cxl-empty-dash', text: '—' })]));
    row.appendChild(el('td', { class: 'cxl-detail-cell' }, call ? [el('div', { class: 'cxl-row-actions' }, [cxlListenBtn(call, fresh), cxlDetail(call, line)])] : []));
    return row;
  }
  function cxlEvents(d) {
    const section = el('aside', { class: 'cxl-panel cxl-activity', 'aria-labelledby': 'cxlEventsTitle' }, [
      el('div', { class: 'cxl-panel-head' }, [el('h3', { id: 'cxlEventsTitle', text: 'Derniers événements' }), cxlIcon('history')]),
    ]);
    const list = el('ol', { class: 'cxl-events' });
    for (const event of (d.recent_events || []).slice(0, 4)) {
      const missed = event.kind === 'missed', answered = event.kind === 'answered', wasAnswered = answered || event.answered === true;
      const outcomeLabel = { failed: 'Échec de l’appel', busy: 'Ligne occupée', canceled: 'Appel annulé', cancelled: 'Appel annulé', rejected: 'Appel refusé' }[event.outcome];
      const tone = missed ? 'missed' : wasAnswered && event.handler === 'ai' ? 'ai' : wasAnswered && event.handler === 'human' ? 'human' : 'idle';
      const icon = missed ? 'phone-missed' : event.handler === 'ai' ? 'bot' : answered ? 'headset' : 'phone-off';
      const name = event.handler_name || (event.handler === 'ai' ? 'IA' : 'un agent');
      const title = missed ? 'Appel sans réponse' : outcomeLabel || (answered && event.handler !== 'unknown' ? 'Répondu par ' + name : answered ? 'Appel connecté' : 'Appel terminé');
      const detail = missed ? 'Aucun interlocuteur n’a répondu' : !wasAnswered ? 'Réponse non confirmée' : event.handler === 'unknown' ? 'Intervenant non confirmé' : event.handler === 'ai' ? 'Pris en charge par IA' : 'Pris en charge par un agent';
      list.appendChild(el('li', { class: 'cxl-event cxl-tone-' + tone }, [
        el('span', { class: 'cxl-event-icon' }, [cxlIcon(icon)]),
        el('div', { class: 'cxl-event-content' }, [el('time', { datetime: event.at || '', class: 'cxl-secondary', text: cxlClockLabel(event.at) }),
          el('strong', { text: title }), el('span', { class: 'cxl-event-detail', text: detail }),
          el('span', { class: 'cxl-secondary', text: [event.port ? 'Ligne ' + event.port : null, event.client_name || event.client_phone].filter(Boolean).join(' · ') || 'Ligne non identifiée' }),
          event.duration_sec != null && !missed ? el('span', { class: 'cxl-event-duration', text: cxlTime(event.duration_sec) }) : null]),
      ]));
    }
    if (!list.children.length) list.appendChild(el('li', { class: 'cxl-event-empty' }, [cxlIcon('history'),
      el('strong', { text: d.history_available === false ? 'Historique indisponible' : 'Aucun événement récent' }),
      el('span', { text: d.history_available === false ? 'Les appels en cours restent visibles. Nouvelle tentative automatique.' : 'Les prises d’appel et les fins confirmées apparaîtront ici.' })]));
    section.appendChild(list);
    if (d.history_available === false && list.querySelector('.cxl-event')) section.appendChild(el('p', { class: 'cxl-history-notice', text: 'Historique non actualisé. Derniers événements connus.' }));
    const legend = el('div', { class: 'cxl-legend' }, [el('h4', { text: 'Légende des états' })]);
    const items = el('div', { class: 'cxl-legend-items' });
    for (const [icon, text, tone] of [['headset', 'Agent', 'human'], ['bot', 'Avec IA', 'ai'], ['bell-ring', 'Sonnerie', 'ringing'], ['phone', 'Libre', 'idle'], ['phone-missed', 'Sans réponse', 'missed'], ['circle-help', 'Non confirmé', 'unknown']]) {
      items.appendChild(el('span', { class: 'cxl-tone-' + tone }, [cxlIcon(icon), el('span', { text })]));
    }
    legend.appendChild(items); section.appendChild(legend); return section;
  }
  function cxlAgents(d, fresh) {
    const section = el('section', { class: 'cxl-panel cxl-team', 'aria-labelledby': 'cxlTeamTitle', 'aria-describedby': 'cxlTeamNote' }, [
      el('div', { class: 'cxl-panel-head' }, [el('h3', { id: 'cxlTeamTitle', text: 'Équipe et extensions' }),
        el('span', { class: 'cxl-secondary', text: 'Connexion téléphonique · ' + d.agents.length + ' extensions' })]),
    ]);
    const list = el('div', { class: 'cxl-agents' });
    for (const agent of d.agents) {
      const call = d.calls.find(c => (c.participant_extensions || [c.ext]).includes(agent.ext));
      const status = call ? cxlState(call, null, fresh) : null;
      const tone = status?.tone || 'idle';
      const label = !fresh ? 'À confirmer' : status ? status.text : agent.registered ? 'Enregistré' : agent.registered === false ? 'Non enregistré' : 'Non confirmé';
      const avatar = el('span', { class: 'cxl-avatar cxl-tone-' + tone }, [cxlIcon(call?.handler === 'ai' ? 'bot' : 'user-round')]);
      list.appendChild(el('div', { class: 'cxl-agent' }, [avatar, el('div', { class: 'cxl-agent-info' }, [
        el('strong', { text: agent.name || 'Extension ' + agent.ext, title: agent.name || '' }),
        el('span', { class: 'cxl-secondary', text: agent.ext }),
        el('span', { class: 'cxl-agent-status cxl-tone-' + tone }, [cxlIcon(status?.icon || (agent.registered && fresh ? 'circle-check' : 'circle-minus')), el('span', { text: label })]),
      ])]));
    }
    if (!d.agents.length) list.appendChild(el('p', { class: 'cxl-secondary', text: 'Aucune extension reçue pour le moment.' }));
    section.appendChild(list);
    section.appendChild(el('p', { id: 'cxlTeamNote', class: 'cxl-sr-only', text: '« Enregistré » indique la connexion au téléphone, pas la disponibilité de la personne.' }));
    return section;
  }
  function renderThreecxLive() {
    const d = _cxl.data, body = $('cxlBody'); if (!d || !body) return;
    // Preserve horizontal reading position, native disclosure and focus on refresh.
    const previousScroller = body.querySelector('.cxl-table-scroll');
    const scrollLeft = previousScroller?.scrollLeft || 0;
    const tableFocused = document.activeElement === previousScroller;
    const focused = document.activeElement?.closest('.cxl-details');
    const focusKey = focused?.dataset.cxlKey;
    const fresh = cxlFresh(); _cxl.lastFresh = fresh;
    const count = key => fresh ? String(d.calls.filter(c => key === 'ringing' ? ['ringing', 'connecting'].includes(cxlPhase(c)) : cxlPhase(c) === 'talking' && c.handler === key).length) : '—';
    const free = fresh ? String(d.lines.filter(l => l.online === true && !(l.port === 2 && !l.number) && !d.calls.some(c => c.port === l.port)).length) : '—';
    const summary = el('div', { class: 'cxl-summary', 'aria-label': 'Résumé des appels' });
    for (const [icon, value, label, tone] of [['headset', count('human'), 'en conversation', 'human'], ['bot', count('ai'), 'avec IA', 'ai'], ['bell-ring', count('ringing'), 'en sonnerie / connexion', 'ringing'], ['phone', free, 'lignes libres', 'idle']]) {
      summary.appendChild(el('div', { class: 'cxl-stat' }, [el('span', { class: 'cxl-stat-icon cxl-tone-' + tone }, [cxlIcon(icon)]), el('strong', { text: value }), el('span', { text: label })]));
    }
    const panel = el('section', { class: 'cxl-panel cxl-lines', 'aria-labelledby': 'cxlLinesTitle' }, [el('div', { class: 'cxl-panel-head' }, [el('h3', { id: 'cxlLinesTitle', text: 'Les ' + d.lines.length + ' lignes' }),
      el('span', { class: 'cxl-secondary', text: fresh ? d.calls.length + ' appel' + (d.calls.length === 1 ? '' : 's') + ' en cours' : 'Dernière activité connue' })])]);
    const table = el('table', { class: 'cxl-table' }, [el('caption', { class: 'cxl-sr-only', text: 'État des lignes téléphoniques, intervenant et durée' }),
      el('thead', {}, [el('tr', {}, ['Ligne', 'Appel', 'État', 'Pris en charge par', 'Durée', ''].map(text => el('th', { scope: 'col', text })))])]);
    const rows = el('tbody');
    for (const line of d.lines) {
      const calls = d.calls.filter(c => c.port === line.port);
      if (!calls.length) rows.appendChild(cxlLineRow(line, null, fresh));
      else calls.forEach((call, i) => rows.appendChild(cxlLineRow(line, call, fresh, i > 0)));
    }
    for (const call of d.calls.filter(c => !d.lines.some(l => l.port === c.port))) rows.appendChild(cxlLineRow({ port: null }, call, fresh));
    table.appendChild(rows); panel.appendChild(el('div', { class: 'cxl-table-scroll', tabindex: '0', 'aria-label': 'Tableau des lignes, défilement horizontal' }, [table]));
    const unknownCount = d.calls.filter(c => cxlPhase(c) === 'talking' && !['human', 'ai'].includes(c.handler)).length;
    if (fresh && unknownCount) panel.appendChild(el('p', { class: 'cxl-table-note' }, [cxlIcon('info'), el('span', { text: unknownCount + ' appel(s) connecté(s) : agent ou IA non confirmé.' })]));
    body.replaceChildren(summary, el('div', { class: 'cxl-workspace' }, [panel, cxlEvents(d)]), cxlAgents(d, fresh));
    const nextScroller = body.querySelector('.cxl-table-scroll');
    nextScroller.scrollLeft = scrollLeft;
    if (tableFocused) nextScroller.focus({ preventScroll: true });
    for (const details of body.querySelectorAll('.cxl-details')) {
      const clock = details.closest('tr').querySelector('[data-cxl-clock]');
      details.dataset.cxlKey = clock?.dataset.cxlClock || '';
      if (focusKey === details.dataset.cxlKey) details.querySelector('summary').focus({ preventScroll: true });
    }
    refreshIcons($('threecxLiveView')); cxlTick();
  }
  function cxlTick() {
    if (state.activeView !== 'threecx-live') return;
    const d = _cxl.data, fresh = cxlFresh(), now = Date.now();
    if (d && _cxl.lastFresh !== fresh) { renderThreecxLive(); return; }
    const status = $('cxlStatus');
    status.textContent = fresh ? 'Connecté' : d ? 'Déconnecté' : _cxl.loading ? 'Connexion…' : 'Indisponible';
    status.className = 'cxl-status' + (fresh ? ' is-live' : '');
    $('cxlUpdated').textContent = fresh ? 'il y a ' + Math.max(0, Math.floor((now - Date.parse(d.pbx_ts)) / 1000)) + ' s'
      : d?.received_at ? 'Dernière réception à ' + cxlClockLabel(d.received_at) : 'En attente du premier signal';
    const notice = $('cxlNotice');
    notice.textContent = d ? 'Le flux est interrompu. Voici la dernière activité connue ; les durées sont figées et la disponibilité reste à confirmer.' : 'Connexion au flux des appels…';
    notice.classList.toggle('hidden', fresh || (!d && _cxl.loading));
    $('threecxLiveView').classList.toggle('cxl-stale', !fresh);
    for (const node of document.querySelectorAll('[data-cxl-clock]')) node.textContent = cxlTime(cxlClockValue(_cxl.clocks.get(node.dataset.cxlClock), now));
  }
  async function loadThreecxLive() {
    if (_cxl.loading || document.hidden || state.activeView !== 'threecx-live') return;
    _cxl.loading = true; const generation = _cxl.generation;
    const button = $('cxlRefresh'); button.disabled = true; button.setAttribute('aria-busy', 'true');
    const controller = new AbortController(); _cxl.request = controller;
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await api('/api/3cx/live', { signal: controller.signal });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const data = await response.json();
      if (generation !== _cxl.generation || state.activeView !== 'threecx-live') return;
      if (!data || !Array.isArray(data.calls) || !Array.isArray(data.lines) || !Array.isArray(data.agents)) throw new Error('Invalid live board');
      cxlSyncClocks(data); _cxl.data = data; _cxl.fetchedAt = Date.now(); renderThreecxLive();
    } catch {
      if (generation !== _cxl.generation || state.activeView !== 'threecx-live') return;
      if (_cxl.data) { _cxl.data.online = false; cxlSyncClocks(_cxl.data); renderThreecxLive(); }
      else $('cxlBody').replaceChildren(el('div', { class: 'cxl-load-state', role: 'status' }, [cxlIcon('wifi-off'), el('strong', { text: 'Le flux des appels est indisponible' }), el('p', { text: 'Nouvelle tentative automatique. Vous pouvez aussi actualiser.' })]));
    } finally {
      clearTimeout(timeout);
      if (generation === _cxl.generation) { _cxl.loading = false; _cxl.request = null; button.disabled = false; button.removeAttribute('aria-busy'); cxlTick(); }
    }
  }
  function setThreecxLiveActive(active) {
    clearInterval(_cxl.timer); clearInterval(_cxl.ticker);
    if (!active) {
      _cxl.generation++; _cxl.request?.abort(); _cxl.request = null; _cxl.loading = false;
      _cxl.data = null; _cxl.lastFresh = null; _cxl.clocks.clear(); _cxl.openDetails.clear();
      $('cxlBody')?.replaceChildren(el('div', { class: 'cxl-load-state', text: 'Chargement de l’activité…' }));
      return;
    }
    if (!_cxl.inited) {
      _cxl.inited = true;
      $('cxlRefresh').addEventListener('click', loadThreecxLive);
      // «Écouter» started / changed / ended → repaint the row's button now.
      document.addEventListener('sfp:listen', () => { if (state.activeView === 'threecx-live') renderThreecxLive(); });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) { cxlTick(); loadThreecxLive(); } });
      $('cxlBody').addEventListener('keydown', event => {
        if (event.key === 'Escape') { for (const details of $('cxlBody').querySelectorAll('details[open]')) details.open = false; _cxl.openDetails.clear(); }
      });
    }
    loadThreecxLive();
    _cxl.timer = setInterval(loadThreecxLive, 3000); _cxl.ticker = setInterval(cxlTick, 1000);
  }

  function showView(view) {
    if (view === 'campaigns' && state.me?.role !== 'admin') return;
    // Safety: if any modal overlay (e.g., image annotator) leaked a global
    // lock, clear it before switching views so scroll is never trapped.
    document.body.classList.remove('ia-open');
    // Same for a lingering 3CX dial panel — it is tied to the view it was
    // opened from (swat:B1).
    if (typeof _cxClosePanel === 'function') _cxClosePanel();
    // Recordings were merged into Calls. Keep old saved URLs/preferences safe
    // by routing the retired view to the unified page.
    if (view === 'recordings') view = 'calls';
    if (view === 'teacher-earnings' && state.me?.role !== 'teacher') return;
    // All team members can browse every section now. Admin-only mutations
    // (create user, edit role, delete user, send bulk) are still gated
    // server-side via requireAdmin, so an agent landing on these views can
    // observe but not break anything.
    // meta_review (Meta App Review reviewer) is the one exception: it may ONLY
    // access the inbox and its own personal Settings. Other views are blocked at the
    // central router so no code path (tab, dropdown, programmatic) can move it
    // off the FB/IG inbox. The server backs this up (leads/contacts/etc 403).
    if (state.me?.role === 'meta_review' && !['inbox', 'settings'].includes(view)) return;
    if (state.me?.role === 'trial_agent') {
      // 2026-08-28 (Ayoub): a candidate working real clients books the same
      // placement tests the floor books, so the grid + the Tests table join
      // the allowlist. Both are server-scoped to their own trial leads.
      // 2026-08-31 (Ayoub): "3tihom access b7al sales team, ghir kay-chofo ghir
      // dak chi li m-assigni 3andhom". Same working views as an agent —
      // Contacts, Objectif, Rappels, Équipe — each one scoped server-side to
      // the candidate's own leads. The list stays an ALLOWLIST: a view lands
      // here only once its data is proven scoped, never by default.
      const allowed = new Set(['academy', 'trial-leads', 'inbox', 'test-schedule', 'tests',
        'contacts', 'dashboards', 'reminders', 'teamchat', 'settings']);
      if (!allowed.has(view)) return;
      if (view !== 'academy' && view !== 'settings' && !document.body.classList.contains('trial-inbox-unlocked')) {
        toast('Complete the Academy and final exam first', 'error');
        view = 'academy';
      }
    }
    const _roleNow = String(state.me?.role || '').toLowerCase();
    if (view === 'calls' && !['agent', 'admin', 'super_admin', 'super_sales'].includes(_roleNow)) {
      toast('Sales calls only', 'error');
      return;
    }
    // Kanza console = supervising the AI agent (listen / whisper / take over /
    // kill switch / outbound queue). Sales-admins only — agents follow their
    // own clients from the Inbox instead (Ayoub 2026-08-26). CSS twin:
    // [data-sales-admin-only] on the nav tab + #conversationsView. The server
    // already answers 403 on /api/kanza/* for anyone else (requireSalesAdmin).
    if (view === 'threecx-live' && !isSalesAdminMe()) {
      _persistLastView('inbox');
      toast('Super sales / admin only', 'error');
      return;
    }
    // Serveur & coûts = Supabase size/cost (Ayoub 2026-09-27). CSS twin:
    // [data-super-admin-only]; the server answers 403 on /api/infra/*.
    if (view === 'infra' && !state.me?.superAdmin) {
      _persistLastView('inbox');
      return;
    }
    // Sécurité & accès = team location/device tracking. Super-admin only, the
    // same gate as the Super Admin roster; the server answers 403 otherwise.
    if (view === 'security' && !state.me?.superAdmin) {
      _persistLastView('inbox');
      return;
    }
    // Leads7 ▸ Influencers (Searchy's creators): the same admin roles as the
    // Influencer lane; the server answers 403 on /api/influencer/* otherwise.
    if (view === 'influencers' && !isSalesAdminMe()) {
      _persistLastView('inbox');
      toast('Super sales / admin only', 'error');
      return;
    }
    if (view === 'conversations' && !isSalesAdminMe()) {
      // Drop the saved slug too, otherwise an agent who used the page before
      // this gate re-triggers the toast on every reload from the boot restore.
      _persistLastView('inbox');
      toast('Super sales / admin only', 'error');
      return;
    }
    // Zoom Live = supervision of every live teacher room (other teachers'
    // meetings, their participants and today's booked-test counts). Teachers
    // get their own slot grid instead (Ayoub 2026-09-08). CSS twin:
    // [data-teacher-hidden] on the nav tab + #zoomLiveView; the server answers
    // 403 on /api/zoom/live-meetings for this role.
    if (view === 'zoom-live' && _roleNow === 'teacher') {
      _persistLastView('test-schedule');
      return;
    }
    if (view === 'test-library' && !['teacher', 'admin', 'super_admin', 'super_sales'].includes(_roleNow)) {
      toast('Test Library is available to teachers, admins and Super Sales', 'error');
      return;
    }
    if (view !== 'level-tests') _ltStopPolling();
    const previousView = state.activeView;
    if (view !== 'settings') clientCallRingtones.stopPreview();
    if (view === 'settings') history.replaceState(null, '', window.location.pathname + window.location.search + '#settings');
    else if (window.location.hash === '#settings') history.replaceState(null, '', window.location.pathname + window.location.search);
    state.activeView = view;
    if (view !== 'inbox') _cancelInboxReads();
    else if (previousView !== 'inbox') queueMicrotask(_resumeInboxMessages);
    // Mandatory Inbox tutorial for new accounts — auto-opens once per page load.
    if (view === 'inbox') { try { inboxTutorialOnInboxShown(); } catch { /* */ } }
    kzRenderSidebarStatus();
    _persistLastView(view);
    markActiveTab(view);
    syncTeamSeg(view);
    E.main().classList.toggle('hidden', view !== 'inbox');
    document.getElementById('bulkSendView').classList.toggle('hidden', view !== 'bulk');
    document.getElementById('academyView')?.classList.toggle('hidden', view !== 'academy');
    document.getElementById('trialLeadsView')?.classList.toggle('hidden', view !== 'trial-leads');
    document.getElementById('teamView').classList.toggle('hidden', view !== 'team');
    document.getElementById('settingsView')?.classList.toggle('hidden', view !== 'settings');
    document.getElementById('personalSettingsBtn')?.setAttribute('aria-current', view === 'settings' ? 'page' : 'false');
    document.getElementById('hubspotView')?.classList.toggle('hidden', view !== 'hubspot');
    document.getElementById('teamchatView')?.classList.toggle('hidden', view !== 'teamchat');
    document.getElementById('findGroupView')?.classList.toggle('hidden', view !== 'book-class');
    document.getElementById('ticketsView')?.classList.toggle('hidden', view !== 'tickets');
    document.getElementById('contactsView')?.classList.toggle('hidden', view !== 'contacts');
    document.getElementById('influencersView')?.classList.toggle('hidden', view !== 'influencers');
    document.getElementById('campaignsView')?.classList.toggle('hidden', view !== 'campaigns');
    document.getElementById('restView')?.classList.toggle('hidden', view !== 'rest');
    document.getElementById('verifyPaymentView')?.classList.toggle('hidden', view !== 'verify-payment');
    document.getElementById('leadStatusMapView')?.classList.toggle('hidden', view !== 'lead-status-map');
    document.getElementById('dedupeContactsView')?.classList.toggle('hidden', view !== 'dedupe-contacts');
    document.getElementById('blockedNumbersView')?.classList.toggle('hidden', view !== 'blocked-numbers');
    document.getElementById('infraView')?.classList.toggle('hidden', view !== 'infra');
    document.getElementById('securityView')?.classList.toggle('hidden', view !== 'security');
    document.getElementById('dashboardsView')?.classList.toggle('hidden', view !== 'dashboards');
    document.getElementById('whyAnalyticsView')?.classList.toggle('hidden', view !== 'why-analytics');
    document.getElementById('testDayCockpitView')?.classList.toggle('hidden', view !== 'test-day-cockpit');
    document.getElementById('zoomLiveView')?.classList.toggle('hidden', view !== 'zoom-live');
    // Test schedule + Availability share #testScheduleView. Availability =
    // the same grid in "availability-mode" (stats + today cards CSS-hidden).
    const _onSchedule = (view === 'test-schedule' || view === 'availability');
    const _tsViewEl = document.getElementById('testScheduleView');
    _tsViewEl?.classList.toggle('hidden', !_onSchedule);
    _tsViewEl?.classList.toggle('availability-mode', view === 'availability');
    document.getElementById('availabilityRequestsView')?.classList.toggle('hidden', view !== 'availability-requests');
    document.getElementById('groupsView')?.classList.toggle('hidden', view !== 'groups');
    document.getElementById('callsView')?.classList.toggle('hidden', view !== 'calls');
    document.getElementById('conversationsView')?.classList.toggle('hidden', view !== 'conversations');
    document.getElementById('threecxAgentsView')?.classList.toggle('hidden', view !== 'threecx-agents');
    document.getElementById('documentsView')?.classList.toggle('hidden', view !== 'documents');
    document.getElementById('aiBotView')?.classList.toggle('hidden', view !== 'ai-bot');
    document.getElementById('testsView')?.classList.toggle('hidden', view !== 'tests');
    document.getElementById('levelTestsView')?.classList.toggle('hidden', view !== 'level-tests');
    document.getElementById('testLibraryView')?.classList.toggle('hidden', view !== 'test-library');
    document.getElementById('teacherTestsView')?.classList.toggle('hidden', view !== 'teacher-tests');
    document.getElementById('testRecordingsView')?.classList.toggle('hidden', view !== 'test-recordings');
    document.getElementById('teacherEarningsView')?.classList.toggle('hidden', view !== 'teacher-earnings');
    document.getElementById('teacherAvailabilityView')?.classList.toggle('hidden', view !== 'teacher-availability');
    document.getElementById('teacherVacationView')?.classList.toggle('hidden', view !== 'teacher-vacation');
    document.getElementById('remindersView')?.classList.toggle('hidden', view !== 'reminders');
    if (view !== 'ai-bot') { try { aiBotStopPolling(); } catch { /* */ } }
    refreshIcons();
    if (view === 'bulk' && !state.bulkGroups.length) loadBulkGroups();
    if (view === 'book-class') { _findGroupInitOnce(); _fgApplyMode('book'); }
    if (view === 'team') { loadTeamUsers(); loadTeamActivity(); renderSuperAdminProfile(); }
    if (view === 'teamchat') loadTeamchat();
    if (view === 'settings') renderPersonalSettings();
    if (view === 'academy') initRecruitmentView();
    if (view === 'trial-leads') loadTrialLeads();
    if (view === 'dashboards') initDashboards();
    if (view === 'why-analytics') initWhyAnalytics();
    if (view === 'test-day-cockpit') ensureTestDayCockpitInit();
    document.getElementById('threecxLiveView')?.classList.toggle('hidden', view !== 'threecx-live');
    setThreecxLiveActive(view === 'threecx-live');
    if (view === 'zoom-live') ensureZoomLiveInit();
    if (view === 'calls') ensureCallsAdminInit();
    if (view === 'conversations') ensureConversationsInit();
    if (view === 'threecx-agents') ensureThreecxAgentsInit();
    if (view === 'documents') ensureDocumentsInit();
    if (view === 'ai-bot') initAiBot();
    if (view === 'tickets') {
      loadTickets();
      // Admin-only: reveal the "Map team" button so the picker is reachable.
      const mapBtn = document.getElementById('ticketsMapTeamBtn');
      if (mapBtn) mapBtn.classList.toggle('hidden', state.me?.role !== 'admin');
    } else if (state.ticketsSelected?.size) {
      // Leaving the tickets view — drop any active multi-selection so
      // returning later doesn't surface a stale bulk bar.
      clearTicketsSelection();
    }
    if (view === 'contacts') initContacts();
    if (view === 'campaigns') initCampaigns();
    if (view === 'rest') { _restInitOnce(); _restRenderOwnerFilter(); loadRestItems({ resetPaging: true }); }
    if (view === 'verify-payment') initVerifyPayment();
    if (view === 'lead-status-map') initLeadStatusMap();
    if (view === 'dedupe-contacts') initDedupeView();
    if (view === 'blocked-numbers') initBlockedNumbersView();
    if (view === 'infra') initInfraView();
    if (view === 'security') initSecurityView();
    if (view === 'test-schedule' || view === 'availability') ensureTestScheduleInit();
    if (view === 'availability-requests') ensureAvailabilityRequestsInit();
    if (view === 'tests') ensureTestsViewInit();
    if (view === 'level-tests') initLevelTests();
    if (view === 'test-library') ensureTestLibraryInit();
    if (view === 'teacher-tests') ensureTeacherTestsInit();
    if (view === 'test-recordings') ensureTestRecordingsInit();
    if (view === 'teacher-earnings') ensureTeacherEarnings(false);
    if (view === 'teacher-availability') ensureTeacherAvailabilityInit();
    if (view === 'teacher-vacation') ensureTeacherVacationInit();
    if (view === 'reminders') { ensureRemindersInit(); _remClearTabBadge(); }
    if (view === 'groups') ensureGroupsInit();
    if (view === 'influencers') ensureInfluencersInit();
    if (view === 'templates') {
      // No dedicated section yet — the templates modal IS the manager. Pop
      // it open and rewind the active tab back to inbox so the user lands
      // somewhere sane after closing.
      state.activeView = 'inbox';
      markActiveTab('inbox');
      E.main().classList.remove('hidden');
      openTemplatesModal('manage');
    }
  }

  // Campaign planning and controlled launches.
  let campaignDraftId = null;
  let campaignDraftStatus = 'draft';
  let campaignLaunchCount = 0;
  let campaignSelectedSubscription = '';
  let campaignInitialized = false;
  let campaignTemplates = [];
  let campaignApprovedWhatsApp = [];
  const campaignField = id => document.getElementById(id);
  function campaignRenderMessagePreview() {
    const channel = campaignField('campaignChannel').value;
    const box = campaignField('campaignMessagePreview');
    box.replaceChildren();
    campaignField('campaignPreviewChannel').textContent = channel === 'sms' ? 'SMS' : channel === 'whatsapp' ? 'WhatsApp' : 'Email';
    const sample = campaignField('campaignMessage').value.trim()
      .replace(/\{\{\s*first_name\s*\}\}/gi, 'Alex') || 'Your message preview will appear here.';
    if (channel === 'email') {
      const shell = document.createElement('div'); shell.className = 'campaigns-email-shell';
      const header = document.createElement('div'); header.className = 'campaigns-email-header';
      const logo = document.createElement('img'); logo.src = '/templates/lougha-logo.png'; logo.alt = 'Lougha Institute';
      header.append(logo);
      const title = document.createElement('h3'); title.textContent = campaignField('campaignSubject').value.trim() || 'Your subject line';
      const body = document.createElement('p'); body.textContent = sample;
      const footer = document.createElement('div'); footer.className = 'campaigns-email-footer';
      footer.textContent = 'Lougha Institute · Education that moves you forward';
      const unsub = document.createElement('small'); unsub.textContent = 'A signed unsubscribe link is added to every email.';
      shell.append(header, title, body, footer, unsub); box.append(shell);
    } else {
      const phone = document.createElement('div'); phone.className = 'campaigns-phone';
      const head = document.createElement('div'); head.className = 'campaigns-phone-head'; head.textContent = channel === 'sms' ? 'Lougha Institute · SMS' : 'Lougha Institute · WhatsApp';
      const bubble = document.createElement('div'); bubble.className = 'campaigns-phone-bubble'; bubble.textContent = sample;
      phone.append(head, bubble); box.append(phone);
    }
    campaignField('campaignTemplateNote').textContent = channel === 'whatsapp'
      ? 'A WhatsApp campaign can use only a Meta-approved template. The preview text must match its approved content.'
      : channel === 'sms' ? 'SMS delivery and cost depend on the connected SIM gateway. This preview does not send.'
      : 'The branded email includes the Lougha logo, a signed unsubscribe link, and Resend delivery tracking.';
  }
  function campaignRenderTemplateChoices(selected = '') {
    const channel = campaignField('campaignChannel').value;
    const picker = campaignField('campaignTemplate');
    picker.replaceChildren(new Option('Custom message', ''));
    for (const tpl of campaignTemplates.filter(t => t.channel === channel)) {
      const option = new Option(tpl.name, tpl.id); picker.add(option);
    }
    picker.value = selected;
    campaignField('campaignMetaField').classList.toggle('hidden', channel !== 'whatsapp');
    campaignField('campaignMetaTemplate').replaceChildren(new Option('Choose a Meta-approved template', ''));
    for (const tpl of campaignApprovedWhatsApp) campaignField('campaignMetaTemplate').add(new Option(`${tpl.name} · ${tpl.language || 'language unspecified'}`, tpl.id));
  }
  async function campaignLoadTemplates() {
    try {
      const response = await api('/api/campaigns/templates');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not load templates');
      campaignTemplates = data.templates || [];
      campaignApprovedWhatsApp = data.approvedWhatsAppTemplates || [];
      campaignRenderTemplateChoices(campaignField('campaignTemplate').value);
    } catch (error) { toast(error.message || 'Could not load templates', 'error'); }
  }
  async function campaignLoadReadiness() {
    const box = campaignField('campaignReadiness');
    try {
      const response = await api('/api/campaigns/readiness');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unavailable');
      box.replaceChildren();
      for (const [channel, state] of Object.entries(data.channels || {})) {
        const row = document.createElement('div'); row.className = 'campaigns-readiness-row';
        const status = document.createElement('span'); status.className = state.connected ? 'campaigns-ready' : 'campaigns-offline';
        status.textContent = state.connected ? 'Connected' : 'Setup needed';
        const copy = document.createElement('div'); const title = document.createElement('strong');
        const detail = document.createElement('small'); title.textContent = channel.toUpperCase(); detail.textContent = state.detail;
        copy.append(title, detail); row.append(copy, status); box.append(row);
      }
      const consent = document.createElement('p'); consent.className = 'campaigns-muted';
      consent.textContent = `${data.hubspotSubscriptions?.detail || 'Consent source unavailable'}. Audience counts are not send eligibility.`;
      box.append(consent);
      const subscription = campaignField('campaignSubscription');
      const selected = subscription.value;
      subscription.replaceChildren(new Option('Choose a subscription type', ''));
      for (const type of data.hubspotSubscriptions?.types || []) subscription.add(new Option(type.name, type.id));
      subscription.value = selected || campaignSelectedSubscription || '';
    } catch (error) { box.textContent = error.message || 'Could not check connections'; }
  }
  async function campaignLoadSuppressions() {
    const list = campaignField('campaignSuppressionList');
    try {
      const response = await api('/api/campaigns/suppressions');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not load suppressions');
      list.replaceChildren();
      campaignField('campaignSuppressionCount').textContent = `${data.suppressions.length} blocked`;
      if (!data.suppressions.length) {
        const p = document.createElement('p'); p.className = 'campaigns-muted'; p.textContent = 'No blocked addresses recorded here yet.'; list.append(p);
      }
      for (const item of data.suppressions) {
        const row = document.createElement('div'); row.className = 'campaigns-suppression-row';
        const info = document.createElement('div'); const strong = document.createElement('strong');
        const small = document.createElement('small'); strong.textContent = `${item.channel.toUpperCase()} · ${item.address}`;
        small.textContent = item.reason; info.append(strong,small);
        const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'btn btn-ghost'; remove.textContent = 'Remove';
        remove.addEventListener('click', async () => {
          const r = await api('/api/campaigns/suppressions', { method:'DELETE', headers:{'Content-Type':'application/json'}, body:JSON.stringify({channel:item.channel,address:item.address}) });
          if (r.ok) campaignLoadSuppressions(); else toast('Could not remove suppression', 'error');
        });
        row.append(info,remove); list.append(row);
      }
    } catch (error) { list.textContent = error.message || 'Could not load suppressions'; }
  }
  async function campaignLoadPermissions() {
    const list = campaignField('campaignPermissionList');
    try {
      const response = await api('/api/campaigns/permissions'); const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not load permissions');
      const count = (data.totals || []).filter(x => x.channel !== 'email').reduce((n,x) => n + Number(x.n || 0), 0);
      campaignField('campaignPermissionCount').textContent = `${count} approved`;
      list.replaceChildren();
      const items = (data.permissions || []).filter(x => x.channel !== 'email');
      if (!items.length) { const p = document.createElement('p'); p.className = 'campaigns-muted'; p.textContent = 'No SMS or WhatsApp permissions recorded.'; list.append(p); }
      for (const item of items) {
        const row = document.createElement('div'); row.className = 'campaigns-suppression-row';
        const info = document.createElement('div'); const strong = document.createElement('strong'); const small = document.createElement('small');
        strong.textContent = `${item.channel.toUpperCase()} · ${item.address}`; small.textContent = item.source; info.append(strong,small);
        const revoke = document.createElement('button'); revoke.type = 'button'; revoke.className = 'btn btn-ghost'; revoke.textContent = 'Revoke';
        revoke.addEventListener('click', async () => {
          const r = await api('/api/campaigns/permissions', {method:'DELETE',headers:{'Content-Type':'application/json'},body:JSON.stringify({channel:item.channel,address:item.address})});
          if (r.ok) campaignLoadPermissions(); else toast('Could not revoke permission','error');
        });
        row.append(info,revoke); list.append(row);
      }
    } catch (error) { list.textContent = error.message || 'Could not load permissions'; }
  }
  function campaignReset() {
    campaignDraftId = null;
    campaignDraftStatus = 'draft'; campaignLaunchCount = 0;
    for (const id of ['campaignName', 'campaignSubject', 'campaignMessage']) campaignField(id).value = '';
    campaignField('campaignChannel').value = 'email';
    campaignField('campaignAudience').value = 'all';
    campaignField('campaignSubscription').value = '';
    campaignSelectedSubscription = '';
    campaignRenderTemplateChoices();
    campaignField('campaignEditorTitle').textContent = 'New draft';
    campaignField('campaignSaveStatus').textContent = 'Drafts are private to administrators.';
    campaignUpdateLaunchControls();
    campaignField('campaignReportStatus').textContent = 'Draft';
    campaignField('campaignMetrics').replaceChildren();
    campaignField('campaignRecipientRows').innerHTML = '<tr><td colspan="4">Choose a campaign to see recipient activity.</td></tr>';
    campaignPreview();
  }
  function campaignUpdateLaunchControls() {
    const draft = campaignDraftStatus === 'draft';
    campaignField('campaignLaunchPreviewBtn').disabled = !campaignDraftId || !draft;
    campaignField('campaignPauseBtn').classList.toggle('hidden', campaignDraftStatus !== 'running');
    campaignField('campaignResumeBtn').classList.toggle('hidden', campaignDraftStatus !== 'paused');
    campaignField('campaignLaunchConfirm').classList.add('hidden');
    campaignField('campaignSaveBtn').disabled = !draft;
    campaignField('campaignLaunchSummary').textContent = !campaignDraftId ? 'Save a draft to review approved recipients.'
      : draft ? 'Review the eligible audience before starting a campaign.'
      : `Campaign status: ${campaignDraftStatus}. Recipient activity appears below.`;
  }
  async function campaignReviewLaunch() {
    if (!campaignDraftId) return;
    const button = campaignField('campaignLaunchPreviewBtn'); button.disabled = true;
    try {
      const response = await api(`/api/campaigns/${encodeURIComponent(campaignDraftId)}/launch-preview`);
      const data = await response.json(); if (!response.ok) throw new Error(data.error || 'Could not review recipients');
      campaignLaunchCount = data.eligible;
      const sample = (data.sample || []).map(r => r.address).join(', ');
      campaignField('campaignLaunchDetails').textContent = data.exceedsBatch
        ? 'More than 500 approved recipients. Narrow this campaign before sending.'
        : `${data.eligible} approved and unsuppressed recipients. ${sample ? `Sample: ${sample}. ` : ''}Type SEND ${data.eligible} to start.`;
      if (data.detail) campaignField('campaignLaunchDetails').textContent = data.detail;
      campaignField('campaignLaunchCode').value = '';
      campaignField('campaignLaunchBtn').disabled = !data.eligible || data.exceedsBatch;
      campaignField('campaignLaunchConfirm').classList.remove('hidden');
    } catch (error) { toast(error.message || 'Could not review recipients', 'error'); }
    finally { button.disabled = campaignDraftStatus !== 'draft'; }
  }
  async function campaignLoadDrafts() {
    const list = campaignField('campaignDraftList');
    try {
      const response = await api('/api/campaigns');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not load drafts');
      list.replaceChildren();
      if (!data.drafts.length) {
        const p = document.createElement('p'); p.className = 'campaigns-muted'; p.textContent = 'No campaigns yet.'; list.append(p);
      }
      for (const draft of data.drafts) {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'campaigns-draft';
        const title = document.createElement('strong'); title.textContent = draft.name;
        const meta = document.createElement('span'); meta.textContent = `${draft.channel.toUpperCase()} · ${draft.status || 'draft'} · ${MA.loc(new Date(draft.updated_at), 'd')}`;
        button.append(title, meta);
        button.addEventListener('click', () => {
          campaignDraftId = draft.id;
          campaignDraftStatus = draft.status || 'draft';
          campaignField('campaignName').value = draft.name;
          campaignField('campaignChannel').value = draft.channel;
          campaignField('campaignAudience').value = draft.audience;
          campaignRenderTemplateChoices(draft.template_id || '');
          campaignField('campaignMetaTemplate').value = campaignTemplates.find(t => t.id === draft.template_id)?.meta_template_id || '';
          campaignField('campaignSubject').value = draft.subject;
          campaignSelectedSubscription = draft.subscription_id || '';
          campaignField('campaignSubscription').value = campaignSelectedSubscription;
          campaignField('campaignMessage').value = draft.message;
          campaignField('campaignEditorTitle').textContent = draft.name;
          campaignField('campaignSaveStatus').textContent = campaignDraftStatus === 'draft' ? 'Saved draft · no messages sent' : `Campaign ${campaignDraftStatus}`;
          campaignUpdateLaunchControls();
          campaignPreview();
          campaignLoadReport(draft.id);
        });
        list.append(button);
      }
    } catch (error) { list.textContent = error.message || 'Could not load drafts'; }
  }
  async function campaignLoadReport(id) {
    const tbody = campaignField('campaignRecipientRows');
    try {
      const response = await api(`/api/campaigns/${encodeURIComponent(id)}/report`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not load activity');
      if (campaignDraftId !== id) return;
      campaignField('campaignReportStatus').textContent = data.campaign.status || 'Draft';
      campaignDraftStatus = data.campaign.status || 'draft';
      campaignUpdateLaunchControls();
      const metrics = campaignField('campaignMetrics'); metrics.replaceChildren();
      for (const key of ['queued', 'sent', 'delivered', 'opened', 'clicked', 'read', 'failed']) {
        const card = document.createElement('div'); const value = document.createElement('strong');
        const label = document.createElement('span'); value.textContent = Number(data.totals[key] || 0).toLocaleString();
        label.textContent = key.charAt(0).toUpperCase() + key.slice(1); card.append(value, label); metrics.append(card);
      }
      tbody.replaceChildren();
      if (!data.recipients.length) {
        const row = tbody.insertRow(); const cell = row.insertCell(); cell.colSpan = 4;
        cell.textContent = 'No recipient activity yet. Saving a draft does not send messages.';
      }
      for (const recipient of data.recipients) {
        const row = tbody.insertRow();
        for (const value of [recipient.display_name || recipient.contact_id, recipient.address,
          recipient.status, recipient.status_at ? MA.loc(new Date(recipient.status_at), 'dt') : '—']) {
          const cell = row.insertCell(); cell.textContent = String(value || '—');
        }
      }
    } catch (error) { tbody.textContent = error.message || 'Could not load activity'; }
  }
  async function campaignPreview() {
    const audience = campaignField('campaignAudience').value;
    const channel = campaignField('campaignChannel').value;
    campaignField('campaignAudienceTitle').textContent = campaignField('campaignAudience').selectedOptions[0].textContent;
    campaignField('campaignSubjectField').classList.toggle('hidden', channel !== 'email');
    campaignField('campaignSubscriptionField').classList.toggle('hidden', channel !== 'email');
    campaignField('campaignReachableLabel').textContent = channel === 'email' ? 'Unique email addresses' : 'Unique phone numbers';
    campaignRenderMessagePreview();
    campaignField('campaignContactCount').textContent = '…';
    campaignField('campaignReachableCount').textContent = '…';
    try {
      const response = await api(`/api/campaigns/audience-preview?audience=${audience}&channel=${channel}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Audience unavailable');
      if (campaignField('campaignAudience').value !== audience || campaignField('campaignChannel').value !== channel) return;
      campaignField('campaignContactCount').textContent = Number(data.contactCount).toLocaleString();
      campaignField('campaignReachableCount').textContent = Number(data.reachableCount).toLocaleString();
      campaignField('campaignAudienceNote').textContent = 'Contact details only. Consent and exclusions have not been verified; no messages are sent from this page.';
    } catch (error) {
      campaignField('campaignContactCount').textContent = '—';
      campaignField('campaignReachableCount').textContent = '—';
      campaignField('campaignAudienceNote').textContent = error.message || 'Audience unavailable';
    }
  }
  function initCampaigns() {
    if (!['admin'].includes(String(state.me?.role || '').toLowerCase())) {
      toast('Admin only', 'error'); showView('inbox'); return;
    }
    if (campaignInitialized) { campaignLoadDrafts(); campaignLoadReadiness(); campaignLoadSuppressions(); campaignLoadPermissions(); campaignPreview(); if (campaignDraftId) campaignLoadReport(campaignDraftId); return; }
    campaignInitialized = true;
    campaignField('campaignNewBtn').addEventListener('click', campaignReset);
    campaignField('campaignRefreshBtn').addEventListener('click', campaignPreview);
    campaignField('campaignLaunchPreviewBtn').addEventListener('click', campaignReviewLaunch);
    campaignField('campaignLaunchBtn').addEventListener('click', async () => {
      if (!campaignDraftId) return;
      const code = campaignField('campaignLaunchCode').value.trim();
      if (code !== `SEND ${campaignLaunchCount}`) { toast(`Type SEND ${campaignLaunchCount} exactly`, 'error'); return; }
      const button = campaignField('campaignLaunchBtn'); button.disabled = true;
      try {
        const response = await api(`/api/campaigns/${encodeURIComponent(campaignDraftId)}/launch`, {
          method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({confirmation:code}) });
        const data = await response.json(); if (!response.ok) throw new Error(data.error || 'Could not launch campaign');
        campaignDraftStatus = 'running'; campaignUpdateLaunchControls();
        toast(`${data.queued} recipients queued`, 'success'); campaignLoadDrafts(); campaignLoadReport(campaignDraftId);
      } catch (error) { toast(error.message || 'Could not launch campaign', 'error'); }
      finally { button.disabled = false; }
    });
    for (const [buttonId, action] of [['campaignPauseBtn','pause'],['campaignResumeBtn','resume']]) {
      campaignField(buttonId).addEventListener('click', async () => {
        if (!campaignDraftId) return;
        const button = campaignField(buttonId); button.disabled = true;
        try {
          const response = await api(`/api/campaigns/${encodeURIComponent(campaignDraftId)}/${action}`,{method:'POST'});
          const data = await response.json(); if (!response.ok || !data.ok) throw new Error(`Could not ${action} campaign`);
          campaignDraftStatus = action === 'pause' ? 'paused' : 'running'; campaignUpdateLaunchControls();
          campaignLoadDrafts(); campaignLoadReport(campaignDraftId);
        } catch (error) { toast(error.message || `Could not ${action} campaign`, 'error'); }
        finally { button.disabled = false; }
      });
    }
    campaignField('campaignChannel').addEventListener('change', () => { campaignRenderTemplateChoices(); campaignPreview(); });
    campaignField('campaignSubscription').addEventListener('change', () => { campaignSelectedSubscription = campaignField('campaignSubscription').value; });
    campaignField('campaignAudience').addEventListener('change', campaignPreview);
    campaignField('campaignTemplate').addEventListener('change', () => {
      const tpl = campaignTemplates.find(t => t.id === campaignField('campaignTemplate').value);
      if (tpl) {
        campaignField('campaignSubject').value = tpl.subject || '';
        campaignField('campaignMessage').value = tpl.body || '';
        campaignField('campaignMetaTemplate').value = tpl.meta_template_id || '';
      }
      campaignRenderMessagePreview();
    });
    for (const id of ['campaignSubject', 'campaignMessage']) campaignField(id).addEventListener('input', campaignRenderMessagePreview);
    campaignField('campaignSaveTemplateBtn').addEventListener('click', async () => {
      const name = campaignField('campaignName').value.trim();
      const body = campaignField('campaignMessage').value.trim();
      if (!name || !body) { toast('Add a campaign name and message first', 'error'); return; }
      const button = campaignField('campaignSaveTemplateBtn'); button.disabled = true;
      try {
        const response = await api('/api/campaigns/templates', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, channel: campaignField('campaignChannel').value,
            subject: campaignField('campaignSubject').value, body,
            metaTemplateId: campaignField('campaignMetaTemplate').value || null, design: 'lougha' }) });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Could not save template');
        await campaignLoadTemplates();
        campaignField('campaignTemplate').value = data.template.id;
        toast('Template saved', 'success');
      } catch (error) { toast(error.message || 'Could not save template', 'error'); }
      finally { button.disabled = false; }
    });
    campaignField('campaignSuppressionAdd').addEventListener('click', async () => {
      const addresses = campaignField('campaignSuppressionAddresses').value.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
      const reason = campaignField('campaignSuppressionReason').value.trim();
      if (!addresses.length || !reason) { toast('Add addresses and a reason', 'error'); return; }
      const button = campaignField('campaignSuppressionAdd'); button.disabled = true;
      try {
        const r = await api('/api/campaigns/suppressions', { method:'POST', headers:{'Content-Type':'application/json'},
          body:JSON.stringify({channel:campaignField('campaignSuppressionChannel').value,addresses,reason}) });
        const data = await r.json(); if (!r.ok) throw new Error(data.error || 'Could not add suppressions');
        campaignField('campaignSuppressionAddresses').value = '';
        toast(`${data.added} address${data.added === 1 ? '' : 'es'} blocked`, 'success');
        campaignLoadSuppressions();
      } catch (error) { toast(error.message || 'Could not add suppressions', 'error'); }
      finally { button.disabled = false; }
    });
    campaignField('campaignPermissionAdd').addEventListener('click', async () => {
      const addresses = campaignField('campaignPermissionAddresses').value.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
      const source = campaignField('campaignPermissionSource').value.trim();
      if (!addresses.length || source.length < 5) { toast('Add phone numbers and a permission source', 'error'); return; }
      const button = campaignField('campaignPermissionAdd'); button.disabled = true;
      try {
        const r = await api('/api/campaigns/permissions', { method:'POST', headers:{'Content-Type':'application/json'},
          body:JSON.stringify({channel:campaignField('campaignPermissionChannel').value,addresses,source}) });
        const data = await r.json(); if (!r.ok) throw new Error(data.error || 'Could not record permission');
        campaignField('campaignPermissionAddresses').value = '';
        toast(`${data.approved} permission${data.approved === 1 ? '' : 's'} recorded`, 'success');
        campaignLoadPermissions();
      } catch (error) { toast(error.message || 'Could not record permission','error'); }
      finally { button.disabled = false; }
    });
    campaignField('campaignSaveBtn').addEventListener('click', async () => {
      const button = campaignField('campaignSaveBtn');
      button.disabled = true;
      try {
        const payload = { id: campaignDraftId, name: campaignField('campaignName').value,
          channel: campaignField('campaignChannel').value, audience: campaignField('campaignAudience').value,
          subject: campaignField('campaignSubject').value, message: campaignField('campaignMessage').value,
          templateId: campaignField('campaignTemplate').value || null,
          subscriptionId: campaignField('campaignSubscription').value || null };
        const response = await api('/api/campaigns', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Could not save draft');
        campaignDraftId = data.draft.id;
        campaignDraftStatus = data.draft.status || 'draft';
        campaignUpdateLaunchControls();
        campaignField('campaignEditorTitle').textContent = data.draft.name;
        campaignField('campaignSaveStatus').textContent = 'Saved draft · no messages sent';
        toast('Campaign draft saved', 'success');
        campaignLoadDrafts();
        campaignLoadReport(campaignDraftId);
      } catch (error) { toast(error.message || 'Could not save draft', 'error'); }
      finally { button.disabled = false; }
    });
    campaignUpdateLaunchControls();
    campaignLoadTemplates(); campaignLoadDrafts(); campaignLoadReadiness(); campaignLoadSuppressions(); campaignLoadPermissions(); campaignPreview();
    setInterval(() => { if (state.activeView === 'campaigns' && campaignDraftId && ['running','paused'].includes(campaignDraftStatus)) campaignLoadReport(campaignDraftId); }, 15000);
  }

  // ============================================================
  // Sales recruitment + Academy
  // ============================================================
  // How many controlled clients one practice cohort allocates (allocateLeads
  // caps the practice cohort at this). The manager gate is judged against it.
  // Entries the give route accepts in one request. Not a limit on how many
  // clients a candidate may hold — there is none.
  const RECRUITMENT_GIVE_BATCH_MAX = 100;
  const RECRUITMENT_STAGE_LABEL = {
    shadow: 'Call shadowing', academy: 'Sales Academy', exam: 'Knowledge exam',
    practice_ready: 'Preparing trial leads', practice: '10-client practical test',
    manager_review: 'Manager review', live: '100-lead live trial',
    completed: 'Trial complete', coaching: 'Coaching required',
  };
  const RECRUITMENT_STAGE_LABEL_AR = {
    shadow: 'الاستماع للمكالمات', academy: 'أكاديمية المبيعات', exam: 'اختبار المعرفة',
    practice_ready: 'جاهز للعمل التطبيقي', practice: '10 عملاء حقيقيين', manager_review: 'مراجعة المدير',
    live: 'تجربة 100 عميل', completed: 'اكتملت التجربة', coaching: 'يحتاج إلى توجيه',
  };

  function _recruitmentAdminLanguage() {
    return state.recruitment.adminLanguage === 'ar' ? 'ar' : 'en';
  }

  function _applyRecruitmentAdminLocale() {
    const lang = _recruitmentAdminLanguage();
    const ar = lang === 'ar';
    document.querySelectorAll('#academyView [data-recruitment-en]').forEach(node => {
      node.textContent = ar ? node.dataset.recruitmentAr : node.dataset.recruitmentEn;
    });
    document.querySelectorAll('#academyView [data-recruitment-placeholder-en]').forEach(node => {
      node.placeholder = ar ? node.dataset.recruitmentPlaceholderAr : node.dataset.recruitmentPlaceholderEn;
    });
    document.querySelectorAll('#academyView [data-recruitment-label-en]').forEach(node => {
      node.setAttribute('aria-label', ar ? node.dataset.recruitmentLabelAr : node.dataset.recruitmentLabelEn);
    });
    document.querySelectorAll('#academyView [data-recruitment-language]').forEach(button => {
      const active = button.dataset.recruitmentLanguage === lang;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    for (const id of ['recruitmentAdminShell', 'recruitmentCandidateShell']) {
      const node = document.getElementById(id);
      if (node) { node.lang = lang; node.dir = ar ? 'rtl' : 'ltr'; }
    }
  }

  // A node inside #academyView whose text is LIVE state must carry its own
  // translations: _applyRecruitmentAdminLocale rewrites every
  // [data-recruitment-en] node from those attributes, so state written without
  // them is clobbered back to the static default the next time the admin
  // switches language (the bug PR #130 fixed on the bot-state row).
  function _recruitmentLiveNode(tag, props, en, arText) {
    const ar = _recruitmentAdminLanguage() === 'ar';
    const node = el(tag, { ...(props || {}), text: ar ? arText : en });
    node.dataset.recruitmentEn = en;
    node.dataset.recruitmentAr = arText;
    return node;
  }

  function _renderRecruitmentOwnerPreviewHeader() {
    const ar = _recruitmentAdminLanguage() === 'ar';
    const welcome = document.getElementById('academyWelcome');
    const summary = document.getElementById('academyStageSummary');
    const stage = document.getElementById('academyStageLabel');
    const percent = document.getElementById('academyStagePercent');
    if (welcome) welcome.textContent = ar ? 'معاينة رحلة المترشح كاملة' : 'Full candidate journey preview';
    if (summary) summary.textContent = ar ? 'جميع المكالمات والدروس والفيديوهات والاختبارات مفتوحة. إجراءات المعاينة لا تغيّر تقدم المترشح.' : 'Every call, lesson, video and quiz is open. Preview actions never change candidate progress.';
    if (stage) stage.textContent = ar ? 'معاينة المالك' : 'Owner preview';
    if (percent) percent.textContent = ar ? 'التخطي مفعّل' : 'Skip on';
  }

  function _setRecruitmentAdminLanguage(language) {
    if (!['en', 'ar'].includes(language)) return;
    state.recruitment.adminLanguage = language;
    localStorage.setItem('leads7_recruitment_admin_language', language);
    _applyRecruitmentAdminLocale();
    const frame = document.getElementById('academyFrame');
    if (frame?.dataset.loaded === 'owner') {
      _renderRecruitmentOwnerPreviewHeader();
      frame.src = `/sales-recruitment.html?embedded=1&mode=owner&view=candidate&lang=${language}`;
    } else if (!document.getElementById('recruitmentAdminShell')?.classList.contains('hidden') && state.recruitment.overview) {
      loadRecruitmentOverview();
    }
  }

  function _applyRecruitmentLocale(candidate) {
    const ar = candidate?.language === 'ar';
    for (const id of ['recruitmentCandidateShell', 'trialLeadsView', 'trialEvidenceBar']) {
      const node = document.getElementById(id); if (node) node.dir = ar ? 'rtl' : 'ltr';
    }
    const set = (id, en, arText) => { const node = document.getElementById(id); if (node) node.textContent = ar ? arText : en; };
    set('academyNavLabel', 'Sales Academy', 'أكاديمية المبيعات');
    set('trialLeadsNavLabel', 'Trial Leads', 'عملاء التجربة');
    set('academyCandidateKicker', 'Leads7 · Sales Academy', 'Leads7 · أكاديمية المبيعات');
    set('trialLeadsKicker', 'Practical stage · Real Leads7 Inbox', 'المرحلة العملية · صندوق وارد Leads7 الحقيقي');
    set('trialLeadsHeading', 'My Trial Leads', 'عملاء تجربتي');
    set('trialLeadsIntro', 'Open an assigned client, work in the real Inbox and save the follow-up evidence.', 'افتح العميل المعيّن لك، واعمل داخل صندوق الوارد الحقيقي، ثم احفظ دليل المتابعة.');
    set('trialLeadsBannerTitle', 'Controlled access', 'وصول مراقب');
    set('trialLeadsBannerText', 'You can only open customers assigned to this trial account.', 'يمكنك فتح العملاء المعيّنين لهذا الحساب التجريبي فقط.');
    set('trialLeadsEmpty', 'Your Academy is complete. Leads will appear as soon as the controlled pool is ready.', 'أكملت الأكاديمية. سيظهر العملاء هنا فور تجهيز القائمة المراقبة.');
    set('trialEvidenceSaveLabel', 'Save & unlock next', 'حفظ وفتح العميل التالي');
    const outcome = document.getElementById('trialEvidenceOutcome');
    if (outcome) {
      const labels = ar
        ? ['النتيجة…', 'تم التواصل — متابعة', 'مؤهل', 'تم حجز الاختبار', 'لا جواب', 'غير مهتم', 'رقم خاطئ']
        : ['Outcome…', 'Connected — follow-up', 'Qualified', 'Test booked', 'No answer', 'Not interested', 'Wrong number'];
      [...outcome.options].forEach((option, index) => { if (labels[index]) option.textContent = labels[index]; });
      outcome.setAttribute('aria-label', ar ? 'نتيجة المكالمة' : 'Call outcome');
    }
    const nextStep = document.getElementById('trialEvidenceNextStep');
    nextStep?.setAttribute('aria-label', ar ? 'تاريخ المتابعة التالية' : 'Next follow-up date');
    const note = document.getElementById('trialEvidenceNote');
    if (note) note.placeholder = ar ? 'ملاحظة CRM مفيدة: السياق والاعتراض والإجراء التالي المتفق عليه…' : 'Useful CRM note: context, objection and agreed next action…';
  }

  // How many journeys the curated shadow library actually holds, as the server
  // read it from the manifest. NEVER a literal: the library was cut from 20 to
  // 10 (PR #124) and every hardcoded "/20" showed a candidate who had listened
  // to everything as half-done. null = unknown; callers must then show a plain
  // count rather than a made-up denominator.
  // Two sources because two audiences: an observer gets it on /admin/overview,
  // and a candidate — who is 403 on that route — gets it on /api/recruitment/me.
  // Reading only the observer one made every candidate's shadow header 0%.
  function _recruitmentJourneyTotal() {
    const count = Number(state.recruitment.overview?.library?.journeyCount
      ?? state.recruitment.library?.journeyCount);
    return Number.isFinite(count) && count > 0 ? count : null;
  }

  function _recruitmentRecordingTotal() {
    const count = Number(state.recruitment.overview?.library?.recordingCount
      ?? state.recruitment.library?.recordingCount);
    return Number.isFinite(count) && count > 0 ? count : null;
  }

  // The furthest second reached in a recording that is not finished yet. Reads
  // the same checkpoints the store credits from, so the manager sees exactly
  // what the gate saw.
  function _recruitmentListeningInProgress(candidate) {
    const seconds = candidate?.progress?.recordingSeconds;
    if (!seconds || typeof seconds !== 'object') return 'Nothing part-way';
    const heard = new Set((candidate.progress.listenedRecordingIds || []).map(String));
    const pending = Object.entries(seconds)
      .filter(([id, value]) => !heard.has(String(id)) && Number(value) > 0)
      .sort((a, b) => Number(b[1]) - Number(a[1]));
    if (!pending.length) return 'Nothing part-way';
    const best = Math.round(Number(pending[0][1]) / 60);
    return `${pending.length} recording${pending.length > 1 ? 's' : ''} part-heard · longest ${best} min in`;
  }

  function _recruitmentStagePercent(candidate) {
    if (!candidate) return 0;
    const progress = candidate.progress || {};
    if (candidate.stage === 'shadow') {
      const total = _recruitmentJourneyTotal();
      if (!total) return 0;
      return Math.min(25, Math.round((Number(progress.listenedJourneys) || 0) / total * 25));
    }
    if (candidate.stage === 'academy') return 25 + Math.round((progress.completedModules?.length || 0) / 8 * 35);
    if (candidate.stage === 'exam') return 65;
    if (candidate.stage === 'practice_ready') return 70;
    if (candidate.stage === 'practice') return 70 + Math.round((candidate.practiceLeads || []).filter(x => x.status === 'completed').length / 10 * 20);
    if (candidate.stage === 'manager_review') return 92;
    if (candidate.stage === 'live') return 96;
    if (candidate.stage === 'completed') return 100;
    return 65;
  }

  function _syncTrialAccess(candidate) {
    const hasLeads = !!((candidate?.practiceLeads?.length) || (candidate?.liveLeads?.length));
    document.body.classList.toggle('trial-inbox-unlocked', state.me?.role === 'trial_agent' && hasLeads);
    const nav = document.querySelector('[data-trial-leads-nav="true"]');
    nav?.classList.toggle('hidden', !hasLeads);
    const badge = document.getElementById('trialLeadsTabBadge');
    if (badge && hasLeads) {
      const list = candidate.stage === 'live' || candidate.stage === 'completed' ? candidate.liveLeads : candidate.practiceLeads;
      const due = (list || []).filter(item => ['available', 'in_progress'].includes(item.status)).length;
      badge.textContent = due ? String(due) : '';
      badge.classList.toggle('hidden', !due);
    }
  }

  function _renderCandidateLanguageGate(candidate) {
    const gate = document.getElementById('academyLanguageGate');
    if (!gate) return;
    const required = state.me?.role === 'trial_agent' && !candidate?.languageConfirmedAt;
    gate.classList.toggle('hidden', !required);
    gate.setAttribute('aria-hidden', String(!required));
    document.body.classList.toggle('academy-language-required', required);
  }

  function _postCandidateContextToAcademy(candidate) {
    const frame = document.getElementById('academyFrame');
    if (!frame?.contentWindow || !candidate) return;
    frame.contentWindow.postMessage({
      type: 'leads7:candidate-context',
      candidate: { fullName: candidate.fullName || state.me?.displayName || '', language: candidate.language === 'ar' ? 'ar' : 'en' },
    }, location.origin);
  }

  function _loadCandidateAcademyFrame(candidate) {
    const frame = document.getElementById('academyFrame');
    if (!frame || !candidate?.languageConfirmedAt) {
      if (frame) { frame.removeAttribute('src'); frame.dataset.loaded = ''; }
      return;
    }
    const lang = candidate.language === 'ar' ? 'ar' : 'en';
    const key = `candidate:${lang}`;
    if (frame.dataset.loaded === key) { _postCandidateContextToAcademy(candidate); return; }
    frame.addEventListener('load', () => _postCandidateContextToAcademy(candidate), { once: true });
    frame.src = `/sales-recruitment.html?embedded=1&view=candidate&lang=${lang}`;
    frame.dataset.loaded = key;
  }

  async function selectRecruitmentCandidateLanguage(language) {
    if (!['en', 'ar'].includes(language)) return;
    const buttons = [...document.querySelectorAll('[data-candidate-language]')];
    buttons.forEach(button => { button.disabled = true; });
    try {
      const response = await api('/api/recruitment/me/language', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ language }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.candidate) throw new Error(payload.error || 'Language could not be saved');
      state.recruitment.candidate = payload.candidate;
      _applyRecruitmentLocale(payload.candidate);
      _renderCandidateLanguageGate(payload.candidate);
      _loadCandidateAcademyFrame(payload.candidate);
      await loadRecruitmentCandidate();
      toast(language === 'ar' ? 'تم حفظ العربية كلغة الأكاديمية' : 'English saved as your Academy language', 'success');
    } catch (error) {
      toast(error.message || 'Language could not be saved', 'error');
    } finally {
      buttons.forEach(button => { button.disabled = false; });
    }
  }

  async function loadRecruitmentCandidate() {
    if (state.me?.role !== 'trial_agent') return null;
    const response = await api('/api/recruitment/me');
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Candidate profile unavailable');
    state.recruitment.candidate = payload.candidate;
    // Must be stored BEFORE the header renders below — _recruitmentStagePercent
    // reads it to size the shadow stage.
    if (payload.library) state.recruitment.library = payload.library;
    state.recruitment.loaded = true;
    _syncTrialAccess(payload.candidate);
    const candidate = payload.candidate;
    _renderCandidateLanguageGate(candidate);
    _applyRecruitmentLocale(candidate);
    const isArabic = candidate.language === 'ar';
    const firstName = String(candidate.fullName || state.me.displayName || '').split(/\s+/)[0];
    const welcome = document.getElementById('academyWelcome');
    if (welcome) welcome.textContent = isArabic ? (firstName ? `مرحباً ${firstName}.` : 'تجربتك الموجهة في المبيعات') : (firstName ? `Welcome, ${firstName}.` : 'Your guided sales trial');
    const label = (isArabic ? RECRUITMENT_STAGE_LABEL_AR : RECRUITMENT_STAGE_LABEL)[candidate.stage] || candidate.stage;
    const stageLabel = document.getElementById('academyStageLabel');
    const stagePercent = document.getElementById('academyStagePercent');
    if (stageLabel) stageLabel.textContent = label;
    if (stagePercent) stagePercent.textContent = _recruitmentStagePercent(candidate) + '%';
    const summary = document.getElementById('academyStageSummary');
    if (summary) summary.textContent = isArabic
      ? (candidate.stage === 'practice' ? 'عملاؤك الحقيقيون جاهزون في قائمة التجربة. تبقى الأكاديمية متاحة للمراجعة.' : candidate.stage === 'manager_review' ? 'أدلة العملاء العشرة الآن لدى المدير لاتخاذ قرار بشري.' : candidate.stage === 'live' ? 'تم فتح قائمة 100 عميل لتجربة العمل الحقيقية لمدة سبعة أيام.' : 'أكمل كل مرحلة تعليمية لفتح العمل الحقيقي مع العملاء.')
      : (candidate.stage === 'practice' ? 'Your real clients are ready in Trial Leads. Academy remains available for review.' : candidate.stage === 'manager_review' ? 'Your 10-client evidence is with the manager for a human decision.' : candidate.stage === 'live' ? 'Your 100-lead portfolio is open for the seven-day live trial.' : 'Complete each learning gate to unlock real customer work.');
    if (candidate.languageConfirmedAt) _postCandidateContextToAcademy(candidate);
    return candidate;
  }

  // Recruitment roles — mirrors the server (lib/recruitment canObserve/canManage
  // + requireRecruitmentObserver/Manager). Sales supervisors run the recruitment
  // lane end-to-end, so both sets are the same today; they are kept as two
  // functions because the read/write split is the thing worth preserving, and
  // the server is the real boundary either way.
  const _RECRUITMENT_OBSERVER_ROLES = ['admin', 'super_admin', 'super_sales'];
  const _RECRUITMENT_MANAGER_ROLES = ['admin', 'super_admin', 'super_sales'];
  function _recruitmentRole() { return String(state.me?.role || '').toLowerCase(); }
  function _recruitmentCanObserve() { return _RECRUITMENT_OBSERVER_ROLES.includes(_recruitmentRole()); }
  function _recruitmentCanManage() { return _RECRUITMENT_MANAGER_ROLES.includes(_recruitmentRole()); }

  function initRecruitmentView() {
    const adminShell = document.getElementById('recruitmentAdminShell');
    const candidateShell = document.getElementById('recruitmentCandidateShell');
    const canObserve = _recruitmentCanObserve();
    const canManage = _recruitmentCanManage();
    adminShell?.classList.toggle('hidden', !canObserve);
    adminShell?.classList.toggle('is-observer', canObserve && !canManage);
    candidateShell?.classList.toggle('hidden', canObserve);
    if (canObserve) {
      _applyRecruitmentAdminLocale();
      loadRecruitmentOverview();
      _bindRecruitmentAdmin();
      return;
    }
    if (state.me?.role !== 'trial_agent') return;
    loadRecruitmentCandidate().then(candidate => {
      _loadCandidateAcademyFrame(candidate);
    }).catch(error => toast(error.message || error, 'error'));
    _bindRecruitmentCandidate();
  }

  let _recruitmentAdminBound = false;
  function _bindRecruitmentAdmin() {
    if (_recruitmentAdminBound) return;
    _recruitmentAdminBound = true;
    document.getElementById('recruitmentRefreshBtn')?.addEventListener('click', loadRecruitmentOverview);
    document.getElementById('recruitmentPoolAddBtn')?.addEventListener('click', addRecruitmentPoolEntries);
    document.getElementById('recruitmentPoolSuggestBtn')?.addEventListener('click', suggestRecruitmentPoolLeads);
    document.getElementById('recruitmentPoolReviewBtn')?.addEventListener('click', reviewRecruitmentPool);
    document.getElementById('recruitmentPickerClose')?.addEventListener('click', closeRecruitmentLeadPicker);
    document.getElementById('recruitmentPickerCancel')?.addEventListener('click', closeRecruitmentLeadPicker);
    document.getElementById('recruitmentPickerGive')?.addEventListener('click', giveRecruitmentPickedLeads);
    document.getElementById('pickerStageBtn')?.addEventListener('click', () => _pickerToggleMenu('pickerStageMenu'));
    document.getElementById('pickerOwnerBtn')?.addEventListener('click', () => _pickerToggleMenu('pickerOwnerMenu'));
    document.getElementById('pickerQuietBtn')?.addEventListener('click', () => _pickerToggleMenu('pickerQuietMenu'));
    document.getElementById('pickerCreatedBtn')?.addEventListener('click', () => _pickerToggleMenu('pickerCreatedMenu'));
    document.getElementById('pickerSelectAll')?.addEventListener('click', _recruitmentPickerSelectAllShown);
    document.querySelector('#pickerStageMenu [data-picker-stage]')?.addEventListener('click', () => {
      state.recruitment.picker.stages.clear();
      loadRecruitmentPickerRows();
    });
    document.querySelector('#pickerOwnerMenu [data-picker-owner]')?.addEventListener('click', () => {
      state.recruitment.picker.owners.clear();
      loadRecruitmentPickerRows();
    });
    document.querySelectorAll('#pickerQuietMenu [data-picker-quiet]').forEach(button => {
      button.addEventListener('click', () => {
        state.recruitment.picker.minQuiet = button.dataset.pickerQuiet === '' ? null : Number(button.dataset.pickerQuiet);
        _pickerCloseMenus();
        loadRecruitmentPickerRows();
      });
    });
    document.querySelector('#pickerCreatedMenu [data-picker-created]')?.addEventListener('click', () => {
      state.recruitment.picker.created = null;
      _pickerCloseMenus();
      loadRecruitmentPickerRows();
    });
    // Clicking anywhere else in the dialog closes an open menu.
    document.getElementById('recruitmentPickerModal')?.addEventListener('click', event => {
      if (!event.target.closest('#pickerStageDropdown, #pickerOwnerDropdown, #pickerQuietDropdown, #pickerCreatedDropdown')) _pickerCloseMenus();
    });
    let pickerSearchTimer = null;
    document.getElementById('recruitmentPickerSearch')?.addEventListener('input', event => {
      const picker = state.recruitment.picker;
      if (!picker) return;
      picker.query = String(event.target.value || '');
      clearTimeout(pickerSearchTimer);
      // The search runs server-side over every retained conversation, not over
      // the page already loaded — typing a name must reach a chat the first
      // 120 rows never included.
      pickerSearchTimer = setTimeout(loadRecruitmentPickerRows, 300);
    });
    document.getElementById('recruitmentPreviewBtn')?.addEventListener('click', openRecruitmentOwnerPreview);
    document.getElementById('recruitmentBotToggle')?.addEventListener('click', toggleRecruitmentBot);
    document.getElementById('recruitmentKnowledgeBtn')?.addEventListener('click', openRecruitmentKnowledgeEditor);
    document.getElementById('recruitmentPreviewBackBtn')?.addEventListener('click', closeRecruitmentOwnerPreview);
    document.getElementById('recruitmentReviewClose')?.addEventListener('click', closeRecruitmentCandidateReview);
    document.getElementById('recruitmentReviewModal')?.addEventListener('click', event => {
      if (event.target === event.currentTarget) closeRecruitmentCandidateReview();
    });
    const search = document.getElementById('recruitmentCandidateSearch');
    search?.addEventListener('input', () => {
      state.recruitment.adminSearch = search.value.trim();
      _renderRecruitmentCandidates();
    });
    document.querySelectorAll('[data-recruitment-filter]').forEach(button => button.addEventListener('click', () => {
      state.recruitment.adminFilter = button.dataset.recruitmentFilter || 'all';
      _renderRecruitmentCandidates();
    }));
    document.querySelectorAll('[data-recruitment-quick-filter]').forEach(button => button.addEventListener('click', () => {
      state.recruitment.adminFilter = button.dataset.recruitmentQuickFilter || 'all';
      document.getElementById('recruitmentCandidatesBody')?.closest('.recruitment-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      _renderRecruitmentCandidates();
    }));
    document.getElementById('recruitmentFilterReset')?.addEventListener('click', () => {
      state.recruitment.adminFilter = 'all';
      state.recruitment.adminSearch = '';
      if (search) search.value = '';
      _renderRecruitmentCandidates();
      search?.focus();
    });
    document.addEventListener('keydown', event => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k' || state.activeView !== 'academy') return;
      event.preventDefault();
      search?.focus();
    });
    document.querySelectorAll('[data-recruitment-language]').forEach(button => button.addEventListener('click', () => _setRecruitmentAdminLanguage(button.dataset.recruitmentLanguage)));
  }

  function openRecruitmentOwnerPreview() {
    const adminShell = document.getElementById('recruitmentAdminShell');
    const candidateShell = document.getElementById('recruitmentCandidateShell');
    const frame = document.getElementById('academyFrame');
    adminShell?.classList.add('hidden');
    candidateShell?.classList.remove('hidden');
    document.querySelector('.recruitment-preview-language')?.classList.remove('hidden');
    document.getElementById('recruitmentPreviewBackBtn')?.classList.remove('hidden');
    _applyRecruitmentAdminLocale();
    _renderRecruitmentOwnerPreviewHeader();
    if (frame) {
      frame.src = `/sales-recruitment.html?embedded=1&mode=owner&view=candidate&lang=${_recruitmentAdminLanguage()}`;
      frame.dataset.loaded = 'owner';
    }
  }

  function closeRecruitmentOwnerPreview() {
    document.getElementById('recruitmentCandidateShell')?.classList.add('hidden');
    document.getElementById('recruitmentAdminShell')?.classList.remove('hidden');
    document.querySelector('.recruitment-preview-language')?.classList.add('hidden');
    document.getElementById('recruitmentPreviewBackBtn')?.classList.add('hidden');
    const frame = document.getElementById('academyFrame');
    if (frame) { frame.removeAttribute('src'); frame.dataset.loaded = ''; }
  }

  function _recruitmentActionButton(label, cls, handler) {
    const button = el('button', { class: `btn ${cls || 'btn-ghost'}`, type: 'button', text: label });
    button.addEventListener('click', handler);
    return button;
  }

  // ── Recruitment bot panel ────────────────────────────────────────────────
  // Its own switch and its own answers. Nothing here reaches the sales funnel
  // bot on the AI Bot page, and nothing there reaches this one.
  async function loadRecruitmentLane() {
    const response = await api('/api/recruitment/admin/lane');
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) return;
    state.recruitment.lane = payload;
    const ar = _recruitmentAdminLanguage() === 'ar';
    const bot = payload.bot || {};
    const stateNode = document.getElementById('recruitmentBotState');
    const metaNode = document.getElementById('recruitmentBotMeta');
    const toggle = document.getElementById('recruitmentBotToggle')?.querySelector('span');
    const laneCount = document.getElementById('recruitmentLaneCount');
    document.querySelector('.recruitment-bot-card')?.classList.toggle('is-on', bot.enabled === true);
    // The locale switcher rewrites textContent from data-recruitment-en/-ar on
    // every language apply, so a node whose text is live state must keep those
    // attributes in sync — otherwise the static markup default ("Off") wins and
    // an admin reads a running bot as stopped.
    const setLiveText = (node, en, arText) => {
      if (!node) return;
      node.dataset.recruitmentEn = en;
      node.dataset.recruitmentAr = arText;
      node.textContent = ar ? arText : en;
    };
    setLiveText(stateNode, bot.enabled ? 'On' : 'Off', bot.enabled ? 'يعمل' : 'مطفأ');
    if (metaNode) {
      const updated = payload.knowledge?.updatedAt ? MA.loc(new Date(payload.knowledge.updatedAt), 'd') : null;
      setLiveText(
        metaNode,
        updated ? `answers updated ${updated}` : 'default answers',
        updated ? `الأجوبة محدّثة ${updated}` : 'أجوبة افتراضية',
      );
    }
    setLiveText(toggle, bot.enabled ? 'Turn off' : 'Turn on', bot.enabled ? 'إيقاف' : 'تشغيل');
    if (laneCount) laneCount.textContent = String((payload.entries || []).length);
  }

  async function toggleRecruitmentBot() {
    const enabled = !(state.recruitment.lane?.bot?.enabled === true);
    const response = await api('/api/recruitment/admin/bot', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) return toast(payload.error || 'Could not update the recruitment bot', 'error');
    toast(enabled ? 'Recruitment bot is on' : 'Recruitment bot is off', 'success');
    await loadRecruitmentLane();
  }

  // The knowledge is what the bot is allowed to say. Editing it here means a
  // wrong answer is corrected in a minute, without a deploy.
  const RECRUITMENT_KNOWLEDGE_FIELDS = [
    { key: 'role', label: 'Poste', rows: 1 },
    { key: 'mission', label: 'Mission', rows: 3 },
    { key: 'conditions', label: 'Conditions requises (une par ligne)', rows: 6, list: true },
    { key: 'schedule', label: 'Horaires', rows: 2 },
    { key: 'pay', label: 'Rémunération', rows: 2 },
    { key: 'location', label: 'Lieu', rows: 2 },
    { key: 'process', label: 'Processus (une étape par ligne)', rows: 5, list: true },
    { key: 'applyUrl', label: 'Lien de candidature', rows: 1 },
    { key: 'outOfScope', label: 'Hors sujet (questions clients)', rows: 2 },
  ];

  function openRecruitmentKnowledgeEditor() {
    const modal = document.getElementById('recruitmentReviewModal');
    const body = document.getElementById('recruitmentReviewBody');
    const title = document.getElementById('recruitmentReviewTitle');
    if (!modal || !body) return;
    const knowledge = state.recruitment.lane?.knowledge?.knowledge || {};
    if (title) title.textContent = 'Réponses du bot de recrutement';
    clear(body);
    const inputs = new Map();
    const grid = el('div', { class: 'recruitment-knowledge-form' });
    for (const field of RECRUITMENT_KNOWLEDGE_FIELDS) {
      const value = field.list ? (knowledge[field.key] || []).join('\n') : (knowledge[field.key] || '');
      const input = el('textarea', { class: 'input', rows: String(field.rows) });
      input.value = value;
      inputs.set(field.key, { input, list: field.list });
      grid.appendChild(el('label', { class: 'form-label', text: field.label }));
      grid.appendChild(input);
    }
    body.appendChild(grid);
    body.appendChild(el('p', {
      class: 'muted small',
      text: 'Le bot ne dit que ce qui est écrit ici. Une information absente : il répond que l\'administration confirmera.',
    }));
    const actions = el('div', { class: 'recruitment-review-actions' });
    actions.appendChild(_recruitmentActionButton('Annuler', 'btn-ghost', closeRecruitmentCandidateReview));
    actions.appendChild(_recruitmentActionButton('Enregistrer', 'btn-primary', async () => {
      const next = { ...knowledge };
      for (const [key, entry] of inputs) {
        const raw = entry.input.value;
        next[key] = entry.list ? raw.split('\n').map(line => line.trim()).filter(Boolean) : raw.trim();
      }
      const response = await api('/api/recruitment/admin/bot/knowledge', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ knowledge: next }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) return toast(payload.error || 'Could not save', 'error');
      toast('Réponses enregistrées', 'success');
      closeRecruitmentCandidateReview();
      await loadRecruitmentLane();
    }));
    body.appendChild(actions);
    modal.classList.remove('hidden');
    document.body.classList.add('modal-open');
  }

  function _recruitmentNeedsAttention(application, candidate) {
    const status = String(application?.status || 'received');
    if (status === 'manual_review' || status === 'incomplete') return true;
    // An auto-qualified application without a provisioned Academy account is
    // an operational gap, not a healthy application waiting in the queue.
    if (!candidate) return status === 'auto_qualified';
    if (!_recruitmentDelivery(candidate.invite?.notification).reached) return true;
    return ['manager_review', 'coaching'].includes(candidate.stage);
  }

  function _recruitmentApplicationStatusLabel(status, ar) {
    const normalized = String(status || 'received');
    const labels = {
      manual_review: ['Manual review', 'مراجعة يدوية'],
      academy_access: ['Account created', 'تم إنشاء الحساب'],
      received: ['Received', 'تم الاستلام'],
      incomplete: ['Incomplete', 'بيانات ناقصة'],
      auto_qualified: ['Auto-qualified', 'مؤهل تلقائياً'],
      manually_approved: ['Manually approved', 'تمت الموافقة يدوياً'],
    };
    return labels[normalized]?.[ar ? 1 : 0] || normalized.replaceAll('_', ' ');
  }

  function _recruitmentFilterMatch(filter, application, candidate) {
    if (filter === 'all') return true;
    if (filter === 'attention') return _recruitmentNeedsAttention(application, candidate);
    const stage = candidate?.stage || '';
    if (filter === 'training') return ['shadow', 'academy', 'exam'].includes(stage);
    if (filter === 'practical') return ['practice_ready', 'practice', 'live'].includes(stage);
    if (filter === 'decision') return stage === 'manager_review';
    if (filter === 'completed') return stage === 'completed';
    return true;
  }

  function _recruitmentRelativeTime(value, ar) {
    const stamp = new Date(value || 0);
    if (!Number.isFinite(stamp.getTime())) return { relative: '—', absolute: '' };
    const diffMinutes = Math.round((stamp.getTime() - Date.now()) / 60000);
    let amount = diffMinutes;
    let unit = 'minute';
    if (Math.abs(diffMinutes) >= 60) { amount = Math.round(diffMinutes / 60); unit = 'hour'; }
    if (Math.abs(diffMinutes) >= 1440) { amount = Math.round(diffMinutes / 1440); unit = 'day'; }
    const locale = ar ? 'ar-MA' : 'en';
    let relative = MA.loc(stamp, 'd', locale, { day: 'numeric', month: 'short' });
    try { relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(amount, unit); } catch {}
    return {
      relative,
      absolute: MA.loc(stamp, 'dt', locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    };
  }

  function _setRecruitmentLiveText(id, en, arText) {
    const node = document.getElementById(id);
    if (!node) return;
    node.dataset.recruitmentEn = en;
    node.dataset.recruitmentAr = arText;
    node.textContent = _recruitmentAdminLanguage() === 'ar' ? arText : en;
  }

  // ── Candidate row vocabulary ────────────────────────────────────────────
  // The old row showed a status chip and a percentage and left every REASON
  // behind a modal, so the page answered "what state is this?" but never "what
  // is blocking, and what do I press?". These helpers put both on the row.

  // Five visible steps. A percentage alone ("0%") tells a manager nothing about
  // where a candidate actually stands; the named step does.
  const RECRUITMENT_PATH_STEPS = [
    { key: 'application', en: 'Application', ar: 'الطلب' },
    { key: 'training', en: 'Training', ar: 'التكوين' },
    { key: 'exam', en: 'Exam', ar: 'الاختبار' },
    { key: 'practice', en: 'Practice', ar: 'التطبيق' },
    { key: 'decision', en: 'Decision', ar: 'القرار' },
  ];

  function _recruitmentPathIndex(candidate) {
    const stage = candidate?.stage || '';
    if (!candidate) return 0;
    if (['shadow', 'academy', 'coaching'].includes(stage)) return 1;
    if (stage === 'exam') return 2;
    if (['practice_ready', 'practice'].includes(stage)) return 3;
    if (['manager_review', 'live', 'completed'].includes(stage)) return 4;
    return 1;
  }

  // Why this candidate is stuck, in the manager's own words, plus the one
  // control that unblocks them. Returning the handler here (rather than a
  // label) keeps the row and the modal offering the SAME action.
  function _recruitmentNextStep(application, candidate, ar) {
    const copy = (en, arText) => ar ? arText : en;
    const status = String(application?.status || 'received');
    const missing = (application?.missingFields || []).join(', ');
    if (status === 'incomplete') {
      return {
        tone: 'warning',
        reason: missing
          ? copy(`Missing answers: ${missing}`, `إجابات ناقصة: ${missing}`)
          : copy('The application is incomplete', 'الطلب غير مكتمل'),
        label: copy('Review', 'مراجعة'),
        run: () => openRecruitmentApplicationReview(application),
      };
    }
    if (status === 'manual_review') {
      const reasons = (application?.screeningReasons || []).map(reason => _recruitmentReasonText(reason, ar)).filter(Boolean);
      return {
        tone: 'warning',
        reason: reasons.length ? reasons.join(' · ') : copy('Automatic screening could not decide', 'الفرز التلقائي لم يستطع الحسم'),
        label: copy('Open the Academy', 'قبول في الأكاديمية'),
        run: () => approveRecruitmentApplication(application.id),
        primary: true,
      };
    }
    if (!candidate) {
      return {
        tone: 'warning',
        reason: copy('Qualified, but no Academy account exists yet', 'مؤهل، لكن لم يُنشأ حساب الأكاديمية بعد'),
        label: copy('Create the account', 'إنشاء الحساب'),
        run: () => approveRecruitmentApplication(application.id),
        primary: true,
      };
    }
    const delivery = _recruitmentDelivery(candidate.invite?.notification);
    if (!delivery.reached) {
      return {
        tone: 'warning',
        reason: copy('The access link never reached them', 'رابط الدخول لم يصل إليه'),
        label: copy('Resend access', 'إعادة إرسال الدخول'),
        run: () => resendRecruitmentInvite(candidate.userId),
        primary: true,
      };
    }
    // Finished the Academy and holding. This row used to fall through to a
    // silent "Open record" — which is how a candidate with a perfect exam sat
    // waiting for two days in August 2026 with nothing on the board saying so.
    if (candidate.stage === 'practice_ready') {
      return {
        tone: 'warning',
        reason: copy('Passed the Academy — waiting for their practice clients', 'اجتاز الأكاديمية — ينتظر عملاءه العمليين'),
        label: copy('Choose their leads', 'اختر عملاءه'),
        run: () => openRecruitmentLeadPicker(application, candidate),
        primary: true,
      };
    }
    if (candidate.stage === 'practice') {
      const done = _recruitmentPracticeCompleted(candidate);
      const total = (candidate.practiceLeads || []).length;
      return {
        tone: 'calm',
        reason: copy(`Working their clients — ${done}/${total} done`, `يشتغل على عملائه — ${done}/${total} منتهية`),
        label: copy('Add leads', 'إضافة عملاء'),
        run: () => openRecruitmentLeadPicker(application, candidate),
      };
    }
    if (candidate.stage === 'manager_review') {
      const total = (candidate.practiceLeads || []).length;
      return {
        tone: 'decision',
        reason: copy(`${total} clients done — waiting on your decision`, `أنهى ${total} عملاء — ينتظر قرارك`),
        label: copy('Decide', 'اتخاذ القرار'),
        run: () => openRecruitmentCandidateReview(application, candidate),
        primary: true,
      };
    }
    if (candidate.stage === 'coaching') {
      return {
        tone: 'warning',
        reason: copy('Kept for coaching before a second attempt', 'محتفظ به للتوجيه قبل محاولة ثانية'),
        label: copy('Open record', 'فتح السجل'),
        run: () => openRecruitmentCandidateReview(application, candidate),
      };
    }
    return {
      tone: 'calm',
      reason: '',
      label: copy('Open record', 'فتح السجل'),
      run: () => openRecruitmentCandidateReview(application, candidate),
    };
  }

  // Screening reasons arrive from the server in English. Translating the known
  // ones keeps the Arabic page Arabic; anything new falls through unchanged
  // rather than being dropped.
  const RECRUITMENT_REASON_AR = {
    'Automatic connection or microphone check not passed': 'فحص الاتصال أو الميكروفون لم ينجح',
    'Stable home internet not confirmed': 'لم يؤكد وجود إنترنت منزلي مستقر',
    'Recording consent not given': 'لم يوافق على تسجيل المكالمات',
  };
  function _recruitmentReasonText(reason, ar) {
    const raw = String(reason || '').trim();
    if (!raw) return '';
    return ar ? (RECRUITMENT_REASON_AR[raw] || raw) : raw;
  }

  const RECRUITMENT_VALUE_AR = {
    call_center: 'مركز اتصال', phone_sales: 'مبيعات هاتفية', field_sales: 'مبيعات ميدانية',
    retail: 'بيع بالتجزئة', none: 'بدون تجربة',
    darija: 'الدارجة', french: 'الفرنسية', english: 'الإنجليزية', arabic: 'العربية', spanish: 'الإسبانية',
    // The two-letter form is what the application form stores for the language
    // the candidate filled it in. Left raw it printed "ar" on every row.
    ar: 'العربية', en: 'الإنجليزية', fr: 'الفرنسية',
    passed: 'ناجح', failed: 'فشل', not_run: 'لم يُجرَ', sent: 'أُرسل', pending: 'في الانتظار',
    yes: 'نعم', no: 'لا',
  };
  const RECRUITMENT_VALUE_EN = {
    call_center: 'Call center', phone_sales: 'Phone sales', field_sales: 'Field sales',
    retail: 'Retail', none: 'No experience',
    darija: 'Darija', french: 'French', english: 'English', arabic: 'Arabic', spanish: 'Spanish',
    ar: 'Arabic', en: 'English', fr: 'French',
    passed: 'Passed', failed: 'Failed', not_run: 'Not run', sent: 'Sent', pending: 'Pending',
    yes: 'Yes', no: 'No',
  };

  // Arabic counts one, two, three-to-ten and eleven-plus differently. Writing
  // "3 مترشحاً" everywhere reads as broken Arabic to the person who lives on
  // this page all day.
  function _recruitmentApplicantNoun(count) {
    const n = Number(count) || 0;
    if (n === 1) return 'مترشح';
    if (n === 2) return 'مترشحان';
    if (n >= 3 && n <= 10) return 'مترشحين';
    return 'مترشحاً';
  }
  function _recruitmentValueText(value, ar) {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    const table = ar ? RECRUITMENT_VALUE_AR : RECRUITMENT_VALUE_EN;
    return table[raw.toLowerCase()] || raw;
  }

  function _recruitmentDetailFact(label, value, options = {}) {
    const text = value == null || value === '' ? '—' : String(value);
    const node = el('div', { class: `recruitment-fact${options.wide ? ' is-wide' : ''}` }, [
      el('span', { text: label }),
      el('strong', { class: options.ltr ? 'recruitment-ltr' : '', text }),
    ]);
    return node;
  }

  // Everything the candidate actually answered, on the row itself. This is the
  // whole point of the redesign: the manager reads the person without opening
  // anything, and the modal stays for the practical evidence record.
  function _recruitmentDetailRow(application, candidate, ar) {
    const copy = (en, arText) => ar ? arText : en;
    const answers = application.answers || {};
    const device = application.deviceCheck || {};
    const attribution = application.attribution || {};
    const body = el('div', { class: 'recruitment-detail' });

    const kit = [
      ['hasComputer', copy('Computer', 'حاسوب')],
      ['hasStableInternet', copy('Internet', 'إنترنت')],
      ['hasHeadset', copy('Headset', 'سماعة')],
      ['hasQuietWorkspace', copy('Quiet room', 'مكان هادئ')],
    ];
    const kitRow = el('div', { class: 'recruitment-kit-row' });
    for (const [key, label] of kit) {
      const ok = answers[key] === true;
      const chip = el('span', { class: `recruitment-kit-chip ${ok ? 'is-ok' : 'is-missing'}`, text: label });
      chip.prepend(lucideIcon(ok ? 'check' : 'x'));
      kitRow.appendChild(chip);
    }
    body.appendChild(el('div', { class: 'recruitment-detail-group' }, [
      el('h4', { text: copy('Work setup', 'تجهيزات العمل') }),
      kitRow,
    ]));

    const micro = _recruitmentValueText(device.microphone || 'not_run', ar);
    // A check that never ran has no latency to report — "not run · —" is noise.
    const latencyMs = Number(device.latencyMs);
    const micLine = Number.isFinite(latencyMs) && latencyMs > 0 ? `${micro} · ${latencyMs} ms` : micro;
    const languages = (answers.languages || []).map(item => _recruitmentValueText(item, ar)).filter(Boolean).join(' · ');
    const delivery = candidate ? _recruitmentDelivery(candidate.invite?.notification) : null;
    const facts = el('div', { class: 'recruitment-fact-grid' }, [
      _recruitmentDetailFact(copy('City', 'المدينة'), application.city),
      _recruitmentDetailFact(copy('Available from', 'التوفر'), answers.availability),
      _recruitmentDetailFact(copy('Languages', 'اللغات'), languages),
      _recruitmentDetailFact(copy('Call experience', 'تجربة المكالمات'), _recruitmentValueText(answers.callExperience, ar)),
      _recruitmentDetailFact(copy('For how long', 'المدة'), answers.experienceDuration),
      _recruitmentDetailFact(copy('Calls per day', 'مكالمات في اليوم'), answers.dailyCalls, { ltr: true }),
      _recruitmentDetailFact(copy('Call type', 'نوع المكالمات'), answers.callType),
      _recruitmentDetailFact(copy('CRM', 'CRM'), answers.crmExperience),
      _recruitmentDetailFact(copy('Microphone check', 'فحص الميكروفون'), micLine),
      _recruitmentDetailFact(copy('Applied from', 'قدّم من'), device.platform || '—', { ltr: true }),
      _recruitmentDetailFact(copy('Phone', 'الهاتف'), application.phone, { ltr: true }),
      _recruitmentDetailFact(copy('Email', 'البريد'), application.email, { ltr: true }),
    ]);
    if (delivery) {
      facts.appendChild(_recruitmentDetailFact(
        copy('Access delivery', 'إيصال الدخول'),
        `WhatsApp ${_recruitmentValueText(delivery.whatsapp, ar)} · Email ${_recruitmentValueText(delivery.email, ar)}`,
      ));
    }
    const campaign = [attribution.utm_source, attribution.utm_campaign, attribution.utm_content].filter(Boolean).join(' · ');
    if (campaign) facts.appendChild(_recruitmentDetailFact(copy('Campaign', 'الحملة'), campaign, { ltr: true }));
    if (answers.motivation) facts.appendChild(_recruitmentDetailFact(copy('Why this job', 'لماذا هذا العمل'), answers.motivation, { wide: true }));
    body.appendChild(el('div', { class: 'recruitment-detail-group' }, [
      el('h4', { text: copy('Application answers', 'إجابات الطلب') }),
      facts,
    ]));

    // First contact. WhatsApp is the only channel these candidates actually
    // read, so whether it went out belongs on the record — not buried in a log.
    const outreach = application.outreach || null;
    const sent = outreach?.whatsapp === 'sent';
    const state = el('span', { class: `recruitment-outreach-state ${sent ? 'is-sent' : outreach ? 'is-failed' : 'is-none'}` }, [
      lucideIcon(sent ? 'check-check' : outreach ? 'circle-alert' : 'circle-dashed'),
      el('span', {
        text: sent
          ? copy(`WhatsApp sent ${_recruitmentRelativeTime(outreach.at, ar).relative}`, `أُرسلت رسالة واتساب ${_recruitmentRelativeTime(outreach.at, ar).relative}`)
          : outreach
            ? copy(`WhatsApp not delivered — ${outreach.error || outreach.whatsapp}`, `لم تصل رسالة واتساب — ${outreach.error || outreach.whatsapp}`)
            : copy('No WhatsApp message sent yet', 'لم تُرسل أي رسالة واتساب بعد'),
      }),
    ]);
    const outreachRow = el('div', { class: 'recruitment-outreach-row' }, [state]);
    const outreachActions = el('div', { class: 'recruitment-outreach-actions' });
    if (_recruitmentChatJid(application)) {
      const open = el('button', { class: 'btn btn-ghost recruitment-outreach-btn', type: 'button' }, [
        lucideIcon('message-circle'),
        el('span', { text: copy('Open conversation', 'فتح المحادثة') }),
      ]);
      open.addEventListener('click', event => { event.stopPropagation(); openRecruitmentCandidateChat(application); });
      outreachActions.appendChild(open);
    }
    if (_recruitmentCanManage()) {
      const send = el('button', { class: 'btn btn-secondary recruitment-outreach-btn', type: 'button' }, [
        lucideIcon('send'),
        el('span', { text: sent ? copy('Send again', 'إرسال مرة أخرى') : copy('Send WhatsApp', 'إرسال واتساب') }),
      ]);
      send.addEventListener('click', event => { event.stopPropagation(); sendRecruitmentOutreach(application, send); });
      outreachActions.appendChild(send);
    }
    if (outreachActions.childElementCount) outreachRow.appendChild(outreachActions);
    body.appendChild(el('div', { class: 'recruitment-detail-group' }, [
      el('h4', { text: copy('First contact', 'أول تواصل') }),
      outreachRow,
    ]));
    return body;
  }

  function _renderRecruitmentCandidates() {
    const payload = state.recruitment.overview;
    const body = document.getElementById('recruitmentCandidatesBody');
    const empty = document.getElementById('recruitmentCandidatesEmpty');
    if (!payload || !body) return;
    const applications = payload.applications || [];
    const candidates = payload.candidates || [];
    const byApplication = new Map(candidates.map(candidate => [candidate.applicationId, candidate]));
    const rows = applications.map(application => ({ application, candidate: byApplication.get(application.id) || null }));
    // Removed applicants live in their own list and are reachable only through
    // their own filter — they must never dilute a pipeline count.
    const archivedRows = (payload.archivedApplications || [])
      .map(application => ({ application, candidate: byApplication.get(application.id) || null, archived: true }));
    const ar = _recruitmentAdminLanguage() === 'ar';
    const copy = (en, arText) => ar ? arText : en;
    const counts = {
      archived: archivedRows.length,
      all: rows.length,
      attention: rows.filter(row => _recruitmentFilterMatch('attention', row.application, row.candidate)).length,
      training: rows.filter(row => _recruitmentFilterMatch('training', row.application, row.candidate)).length,
      practical: rows.filter(row => _recruitmentFilterMatch('practical', row.application, row.candidate)).length,
      decision: rows.filter(row => _recruitmentFilterMatch('decision', row.application, row.candidate)).length,
      completed: rows.filter(row => _recruitmentFilterMatch('completed', row.application, row.candidate)).length,
    };
    const countIds = {
      all: 'recruitmentFilterAllCount', attention: 'recruitmentFilterAttentionCount', training: 'recruitmentFilterTrainingCount',
      practical: 'recruitmentFilterPracticalCount', decision: 'recruitmentFilterDecisionCount', completed: 'recruitmentFilterCompletedCount',
      archived: 'recruitmentFilterArchivedCount',
    };
    // The Removed segment only exists once something has been removed.
    document.getElementById('recruitmentArchivedSeg')?.classList.toggle('hidden', counts.archived === 0);
    for (const [key, id] of Object.entries(countIds)) {
      const node = document.getElementById(id);
      if (!node) continue;
      node.textContent = String(counts[key]);
      // An amber count that is amber even at zero stops meaning anything.
      node.closest('.recruitment-rail-seg')?.classList.toggle('has-items', counts[key] > 0);
    }

    let filter = state.recruitment.adminFilter || 'all';
    // Restoring the last removed applicant empties this filter; falling back to
    // "all" beats leaving the manager on a blank list with no segment to click.
    if (filter === 'archived' && !archivedRows.length) filter = state.recruitment.adminFilter = 'all';
    const source = filter === 'archived' ? archivedRows : rows;
    const query = String(state.recruitment.adminSearch || '').trim().toLocaleLowerCase();
    const visible = source.filter(({ application, candidate }) => {
      if (filter !== 'archived' && !_recruitmentFilterMatch(filter, application, candidate)) return false;
      if (!query) return true;
      const haystack = [
        application.fullName, application.email, application.phone, application.city, application.status,
        _recruitmentAttributionLabel(application), candidate?.stage, candidate?.username,
      ].filter(Boolean).join(' ').toLocaleLowerCase();
      return haystack.includes(query);
    }).sort((a, b) => {
      const attention = Number(_recruitmentNeedsAttention(b.application, b.candidate)) - Number(_recruitmentNeedsAttention(a.application, a.candidate));
      if (attention) return attention;
      return String(b.candidate?.updatedAt || b.application.updatedAt || b.application.createdAt || '').localeCompare(String(a.candidate?.updatedAt || a.application.updatedAt || a.application.createdAt || ''));
    });

    document.querySelectorAll('[data-recruitment-filter]').forEach(button => {
      const active = button.dataset.recruitmentFilter === filter;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    document.querySelectorAll('[data-recruitment-quick-filter]').forEach(button => button.classList.toggle('active', button.dataset.recruitmentQuickFilter === filter));
    const reset = document.getElementById('recruitmentFilterReset');
    reset?.classList.toggle('hidden', filter === 'all' && !query);
    _setRecruitmentLiveText(
      'recruitmentResultsSummary',
      `Showing ${visible.length} of ${source.length} applicant${source.length === 1 ? '' : 's'}`,
      `عرض ${visible.length} من أصل ${source.length} ${_recruitmentApplicantNoun(source.length)}`,
    );

    clear(body);
    empty?.classList.toggle('hidden', visible.length > 0);
    if (empty && !visible.length) {
      const noData = !rows.length;
      empty.dataset.recruitmentEn = noData ? 'No candidates yet.' : 'No candidates match this search or filter.';
      empty.dataset.recruitmentAr = noData ? 'لا يوجد مترشحون بعد.' : 'لا يوجد مترشح يطابق هذا البحث أو التصفية.';
      empty.textContent = ar ? empty.dataset.recruitmentAr : empty.dataset.recruitmentEn;
    }

    const canManage = _recruitmentCanManage();
    for (const { application, candidate, archived } of visible) {
      const needsAttention = !archived && _recruitmentNeedsAttention(application, candidate);
      const next = archived
        ? {
          tone: 'calm',
          reason: copy('Removed from the pipeline — the account still works', 'محذوف من المسار — الحساب مازال يعمل'),
          label: copy('Restore', 'استرجاع'),
          run: () => restoreRecruitmentApplication(application),
        }
        : _recruitmentNextStep(application, candidate, ar);
      const initials = String(application.fullName || '?').split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
      const attributionLabel = _recruitmentAttributionLabel(application);

      // Identity: who they are and where they came from, on three tight lines.
      // The phone and email are forced LTR — Arabic direction otherwise moves
      // the country code to the wrong end of the number.
      const identityLines = [el('strong', { text: application.fullName || copy('Applicant', 'مترشح') })];
      const place = [application.city, _recruitmentValueText(application.platformLanguage, ar)].filter(Boolean).join(' · ');
      if (place) identityLines.push(el('span', { class: 'recruitment-candidate-place', text: place }));
      if (application.phone) identityLines.push(el('span', { class: 'recruitment-candidate-contact recruitment-ltr', text: application.phone }));
      if (attributionLabel) {
        identityLines.push(el('span', { class: 'recruitment-candidate-source', title: attributionLabel }, [
          lucideIcon('megaphone'), el('span', { text: attributionLabel }),
        ]));
      }
      const name = el('div', { class: 'recruitment-candidate-name' }, [
        el('span', { class: `recruitment-candidate-avatar${needsAttention ? ' has-alert' : ''}`, text: initials || '?' }),
        el('div', null, identityLines),
      ]);

      // Status: the chip AND the reason. A chip alone made every blocked
      // candidate look identical to a healthy one waiting in the queue.
      const status = String(application.status || 'received');
      const statusNeedsAttention = status === 'manual_review' || status === 'incomplete' || (status === 'auto_qualified' && !candidate);
      const screen = el('div', { class: 'recruitment-screen-cell' }, [
        el('span', {
          class: `recruitment-stage-chip ${statusNeedsAttention ? 'is-warning' : status === 'academy_access' ? 'is-success' : ''}`,
          text: _recruitmentApplicationStatusLabel(status, ar),
        }),
      ]);
      if (next.reason) {
        screen.appendChild(el('span', { class: `recruitment-reason is-${next.tone}` }, [
          lucideIcon(next.tone === 'decision' ? 'gavel' : 'circle-alert'),
          el('span', { text: next.reason }),
        ]));
      } else if (candidate) {
        screen.appendChild(el('span', { class: 'recruitment-delivery-state is-delivered', text: copy('Access delivered', 'وصل رابط الدخول') }));
      }

      // Training path: five named steps beat one percentage. "0%" said nothing;
      // "Training · step 2 of 5" says where the person actually is.
      const pathIndex = _recruitmentPathIndex(candidate);
      const allDone = candidate?.stage === 'completed';
      const stageLabel = candidate
        ? ((ar ? RECRUITMENT_STAGE_LABEL_AR : RECRUITMENT_STAGE_LABEL)[candidate.stage] || candidate.stage)
        : copy('Application received', 'تم استلام الطلب');
      const steps = el('div', { class: 'recruitment-steps', role: 'img', 'aria-label': `${stageLabel} — ${pathIndex + 1}/${RECRUITMENT_PATH_STEPS.length}` });
      RECRUITMENT_PATH_STEPS.forEach((step, index) => {
        const done = allDone || index < pathIndex;
        const current = !allDone && index === pathIndex;
        steps.appendChild(el('span', {
          class: `recruitment-step${done ? ' is-done' : ''}${current ? ' is-current' : ''}`,
          title: ar ? step.ar : step.en,
        }));
      });
      const percent = candidate ? _recruitmentStagePercent(candidate) : 0;
      const journey = el('div', { class: 'recruitment-journey-cell' }, [
        el('div', { class: 'recruitment-journey-label' }, [
          el('span', { text: stageLabel }),
          el('strong', { text: candidate ? `${percent}%` : '—' }),
        ]),
        steps,
      ]);

      // Two dates, both labelled. "Applied" is what tells a manager a candidate
      // has been waiting since morning; "last seen" alone hid that.
      const applied = _recruitmentRelativeTime(application.createdAt, ar);
      const seen = _recruitmentRelativeTime(candidate?.updatedAt || application.updatedAt || application.createdAt, ar);
      const activity = el('div', { class: 'recruitment-activity-cell' }, [
        el('span', { class: 'recruitment-activity-row' }, [
          el('em', { text: copy('Applied', 'تقدّم') }),
          el('strong', { title: applied.absolute, text: applied.relative }),
        ]),
        el('span', { class: 'recruitment-activity-row' }, [
          el('em', { text: copy('Last seen', 'آخر نشاط') }),
          el('span', { title: seen.absolute, text: seen.relative }),
        ]),
      ]);

      // The action carries its own verb, so the row says what pressing it does.
      const act = el('button', {
        class: `btn ${next.primary ? 'btn-primary' : 'btn-ghost'} recruitment-inspect-btn`,
        type: 'button',
        'aria-label': `${next.label} — ${application.fullName || ''}`,
      }, [el('span', { text: next.label })]);
      act.addEventListener('click', event => { event.stopPropagation(); next.run(); });
      const expand = el('button', {
        class: 'icon-btn recruitment-expand-btn',
        type: 'button',
        'aria-expanded': 'false',
        'aria-label': copy('Show application answers', 'عرض إجابات الطلب'),
      }, [lucideIcon('chevron-down')]);
      const actions = el('div', { class: 'recruitment-row-actions' }, [act]);
      // Straight into the candidate's WhatsApp thread — the manager reads the
      // exchange (or its absence) without hunting the number down in the Inbox.
      if (_recruitmentChatJid(application)) {
        const chat = el('button', {
          class: 'icon-btn recruitment-chat-btn',
          type: 'button',
          title: copy('Open conversation', 'فتح المحادثة'),
          'aria-label': `${copy('Open conversation', 'فتح المحادثة')} — ${application.fullName || ''}`,
        }, [lucideIcon('message-circle')]);
        chat.addEventListener('click', event => { event.stopPropagation(); openRecruitmentCandidateChat(application); });
        actions.appendChild(chat);
      }
      // Removing is deliberately the quietest control on the row and asks
      // first: a real applicant sits one pixel away from a test entry.
      if (canManage && !archived) {
        const remove = el('button', {
          class: 'icon-btn recruitment-remove-btn',
          type: 'button',
          title: copy('Remove from the list', 'حذف من القائمة'),
          'aria-label': `${copy('Remove', 'حذف')} ${application.fullName || ''}`,
        }, [lucideIcon('trash-2')]);
        remove.addEventListener('click', event => { event.stopPropagation(); removeRecruitmentApplication(application); });
        actions.appendChild(remove);
      }
      actions.appendChild(expand);

      const row = el('tr', { class: `${needsAttention ? 'needs-attention' : ''}${archived ? ' is-archived' : ''}`.trim() });
      for (const [cell, label] of [
        [name, copy('Candidate', 'المترشح')],
        [screen, copy('Status', 'الحالة')],
        [journey, copy('Training path', 'مسار التكوين')],
        [activity, copy('Applied · last seen', 'تاريخ الطلب · آخر نشاط')],
        [actions, copy('Action', 'الإجراء')],
      ]) {
        const td = el('td', { 'data-label': label }); td.appendChild(cell); row.appendChild(td);
      }

      // The answers open in place. A modal round-trip for facts the manager
      // reads on every single candidate is the thing that made this page slow.
      const detailCell = el('td', { colspan: '5' });
      detailCell.appendChild(_recruitmentDetailRow(application, candidate, ar));
      const detailRow = el('tr', { class: 'recruitment-detail-row hidden' }, [detailCell]);
      const toggle = () => {
        const open = detailRow.classList.toggle('hidden') === false;
        expand.setAttribute('aria-expanded', String(open));
        expand.classList.toggle('is-open', open);
        row.classList.toggle('is-expanded', open);
      };
      expand.addEventListener('click', event => { event.stopPropagation(); toggle(); });
      row.addEventListener('click', toggle);

      body.appendChild(row);
      body.appendChild(detailRow);
    }
    refreshIcons(body);
  }

  // Jump from a candidate row straight into their WhatsApp conversation in the
  // Inbox. The jid comes from the server (`waChatId`, canonicalised there —
  // never re-derived from the raw typed phone); _phoneForWhatsapp is only the
  // fallback for a payload predating the enrichment. When no thread exists yet
  // (the candidate never replied), the same synthetic-chat trick as
  // openChatForPhoneNumber makes the conversation appear — but stamped
  // `recruitmentLane` and WITHOUT `_leadCreationSource`, so the pipeline bar
  // never mints a sales lead for a job candidate.
  function _recruitmentChatJid(application) {
    // The server's verdict is authoritative: an explicit `waChatId: null`
    // means _canonicalizePhone refused the number, and the looser client-side
    // derivation must not overrule it — the row then simply offers no
    // conversation to open. The raw-phone fallback exists only for a payload
    // that predates the enrichment (old server during a rolling deploy).
    if (application && 'waChatId' in application) {
      return _validatedFunctionalChatJid(application.waChatId);
    }
    const digits = _phoneForWhatsapp(application?.phone);
    return digits ? digits + '@s.whatsapp.net' : '';
  }

  async function openRecruitmentCandidateChat(application, jidOverride = '') {
    const ar = _recruitmentAdminLanguage() === 'ar';
    const jid = _validatedFunctionalChatJid(jidOverride) || _recruitmentChatJid(application);
    if (!jid) { toast(ar ? 'رقم غير صالح للواتساب' : 'Numéro invalide pour WhatsApp', 'error'); return; }
    try { await loadConversations(); } catch { /* the synthetic stub below still opens the thread */ }
    if (!state.chats.find(chat => chat.id === jid)) {
      state.chats.unshift({
        id: jid, name: application.fullName || jid.split('@')[0], phone: jid.split('@')[0],
        isGroup: false, lastMessage: null, unread: 0, recruitmentLane: true,
      });
    }
    showView('inbox');
    setTimeout(() => selectConversation(jid), 80);
  }

  // Sending the opening WhatsApp by hand. Real message to a real person, so the
  // button states who it goes to and disables itself while in flight — a double
  // click here is a second message on somebody's phone. On success it lands the
  // manager inside the candidate's conversation, where the mirrored template
  // now sits ("nsyft lih hadak template dir liya tema" — Ayoub, 2026-08-21).
  async function sendRecruitmentOutreach(application, button) {
    const ar = _recruitmentAdminLanguage() === 'ar';
    const name = application.fullName || (ar ? 'هذا المترشح' : 'this applicant');
    const question = ar
      ? `إرسال رسالة واتساب إلى ${name} (${application.phone})؟`
      : `Send the WhatsApp message to ${name} (${application.phone})?`;
    if (!window.confirm(question)) return;
    if (button) button.disabled = true;
    try {
      const response = await api(`/api/recruitment/admin/applications/${encodeURIComponent(application.id)}/outreach`, { method: 'POST' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) return toast(payload.error || (ar ? 'تعذّر الإرسال' : 'Could not send'), 'error');
      toast(ar ? `أُرسلت إلى ${name}` : `Sent to ${name}`, 'success');
      loadRecruitmentOverview().catch(() => { /* the send already succeeded */ });
      await openRecruitmentCandidateChat(application, payload.chatId || '');
    } finally {
      if (button) button.disabled = false;
    }
  }

  // Removing an applicant is an archive on the server, so this asks once and
  // then says where the row went — a manager who mis-clicks needs to know the
  // way back without hunting for it.
  async function removeRecruitmentApplication(application) {
    const ar = _recruitmentAdminLanguage() === 'ar';
    const name = application.fullName || (ar ? 'هذا المترشح' : 'this applicant');
    const question = ar
      ? `حذف ${name} من قائمة المترشحين؟\n\nالحساب يبقى يعمل، ويمكن استرجاع السطر من تبويب «المحذوفون».`
      : `Remove ${name} from the applicant list?\n\nTheir account keeps working, and the row can be restored from the "Removed" tab.`;
    if (!window.confirm(question)) return;
    const response = await api(`/api/recruitment/admin/applications/${encodeURIComponent(application.id)}`, { method: 'DELETE' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) return toast(payload.error || (ar ? 'تعذّر الحذف' : 'Could not remove the applicant'), 'error');
    toast(ar ? `${name} حُذف — يمكن استرجاعه من «المحذوفون»` : `${name} removed — restore it from "Removed"`, 'success');
    await loadRecruitmentOverview();
  }

  async function restoreRecruitmentApplication(application) {
    const ar = _recruitmentAdminLanguage() === 'ar';
    const response = await api(`/api/recruitment/admin/applications/${encodeURIComponent(application.id)}/restore`, { method: 'POST' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) return toast(payload.error || (ar ? 'تعذّر الاسترجاع' : 'Could not restore the applicant'), 'error');
    toast(ar ? 'رجع إلى قائمة المترشحين' : 'Back in the applicant list', 'success');
    await loadRecruitmentOverview();
  }

  async function loadRecruitmentOverview() {
    loadRecruitmentLane().catch(() => { /* the panel is secondary to the pipeline */ });
    const shell = document.getElementById('recruitmentAdminShell');
    const refresh = document.getElementById('recruitmentRefreshBtn');
    const pipelineState = document.getElementById('recruitmentPipelineState');
    shell?.classList.add('is-loading');
    if (refresh) refresh.disabled = true;
    _setRecruitmentLiveText('recruitmentPipelineState', 'Refreshing…', 'جارٍ التحديث…');
    _setRecruitmentLiveText('recruitmentPoolGuardLabel', 'Checking guard', 'جارٍ التحقق');
    try {
      const response = await api('/api/recruitment/admin/overview');
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Recruitment data unavailable');
      state.recruitment.overview = payload;
      _applyRecruitmentAdminLocale();
      const applications = payload.applications || [];
      const candidates = payload.candidates || [];
      const byApplication = new Map(candidates.map(candidate => [candidate.applicationId, candidate]));
      const attention = applications.filter(application => _recruitmentNeedsAttention(application, byApplication.get(application.id))).length;
      // The per-bucket counts render on the filter rail itself, from
      // _renderRecruitmentCandidates — the four hero tiles that used to repeat
      // them here are gone.
      const set = (id, value) => { const node = document.getElementById(id); if (node) node.textContent = String(value); };
      const ar = _recruitmentAdminLanguage() === 'ar';
      set('recruitmentCandidateCount', `${applications.length} ${ar ? _recruitmentApplicantNoun(applications.length) : applications.length === 1 ? 'applicant' : 'applicants'}`);
      set('recruitmentPoolAvailable', payload.pool?.available || 0);
      const guarded = payload.pool?.guarded === true;
      document.getElementById('recruitmentPoolGuardState')?.classList.toggle('is-warning', !guarded);
      _setRecruitmentLiveText(
        'recruitmentPoolGuardLabel',
        guarded ? 'Eligibility checked' : 'Guard unavailable',
        guarded ? 'تم التحقق' : 'الحارس غير متاح',
      );
      // The pill's icon lives in its own node so a language switch — which
      // rewrites every [data-recruitment-en] node's textContent — can never
      // wipe it. Only the text node carries the translations.
      const summary = document.getElementById('recruitmentAttentionSummary');
      summary?.classList.toggle('has-attention', attention > 0);
      summary?.toggleAttribute('disabled', attention === 0);
      _setRecruitmentLiveText(
        'recruitmentAttentionText',
        attention ? `${attention} candidate${attention === 1 ? ' needs' : 's need'} you` : 'Nothing waiting on you',
        attention
          ? `${attention} ${_recruitmentApplicantNoun(attention)} ${attention === 1 ? 'ينتظر' : attention === 2 ? 'ينتظران' : attention <= 10 ? 'ينتظرون' : 'ينتظر'} إجراءك`
          : 'لا شيء ينتظر إجراءك',
      );
      const now = new Date();
      _setRecruitmentLiveText(
        'recruitmentLastUpdated',
        `Updated ${MA.loc(now, 't', 'en', { hour: '2-digit', minute: '2-digit' })}`,
        `آخر تحديث ${MA.loc(now, 't', 'ar-MA', { hour: '2-digit', minute: '2-digit' })}`,
      );
      _renderRecruitmentPoolBlocked(payload.pool);
      _renderRecruitmentCandidates();
      _setRecruitmentLiveText('recruitmentPipelineState', 'Live data', 'بيانات مباشرة');
      shell?.classList.remove('has-error');
      refreshIcons(shell);
    } catch (error) {
      shell?.classList.add('has-error');
      _setRecruitmentLiveText('recruitmentPipelineState', 'Could not refresh', 'تعذّر التحديث');
      toast(error.message || 'Recruitment data unavailable', 'error');
    } finally {
      shell?.classList.remove('is-loading');
      if (refresh) refresh.disabled = false;
    }
  }

  function _recruitmentFact(label, value) {
    return el('div', { class: 'recruitment-review-fact' }, [el('span', { text: label }), el('strong', { text: value == null || value === '' ? '—' : String(value) })]);
  }

  // Controlled clients this candidate has actually produced evidence for —
  // counting the ones since released, or a candidate whose leads were handed
  // back reads "0/10" while ten pieces of their evidence sit right below it.
  //
  // Counted by DISTINCT conversation and capped at the ten a practice cohort
  // allocates: a candidate released and then re-allocated to a DIFFERENT set can
  // legitimately hold more than ten records, and "15/10" reads as a bug to the
  // manager. The full history is still listed row by row underneath.
  function _recruitmentPracticeCompleted(candidate) {
    const chats = new Set();
    for (const lead of candidate?.practiceLeads || []) {
      if (lead.status === 'completed') chats.add(lead.chatId);
    }
    for (const entry of candidate?.evidenceArchive || []) {
      if (entry.cohort !== 'live') chats.add(entry.chatId);
    }
    return chats.size;
  }

  // One row of practical evidence. `archived` rows are work on a client that has
  // since gone back to the team: the record stays, labelled, because the manager
  // gate judges it. Their "Open Inbox" button stays LIVE on purpose — the chat is
  // no longer the candidate's, but an admin sees every conversation
  // (_userCanAccessChat short-circuits for sales admins), so the button opens the
  // real thread rather than misleading or 404-ing.
  //
  // Language is resolved HERE, at render time, because #recruitmentReviewModal is
  // a SIBLING of #academyView — _applyRecruitmentAdminLocale never walks it, so
  // nothing rewrites these nodes on a language switch (the modal is rebuilt on
  // every open instead). _recruitmentLiveNode is still used so the -en/-ar
  // attributes are there the day the switcher's scope widens.
  function _recruitmentEvidenceRow(lead, archived = false) {
    const ar = _recruitmentAdminLanguage() === 'ar';
    const evidence = lead.evidence;
    const detail = evidence
      ? `${evidence.outcome} · ${evidence.note} · ${ar ? 'التالي' : 'next'} ${MA.loc(new Date(evidence.nextStepAt), 'dt')}`
      : String(lead.status || 'locked').replaceAll('_', ' ');
    const openButton = _recruitmentLiveNode('button', { class: 'btn btn-ghost', type: 'button' }, 'Open Inbox', 'فتح المحادثة');
    openButton.addEventListener('click', () => openRecruitmentAdminLead(lead));
    if (!archived && (lead.status === 'locked' || lead.status === 'assignment_conflict')) openButton.disabled = true;
    const label = lead.label || `${ar ? 'عميل مراقب' : 'Controlled client'} ${lead.order || ''}`.trim();
    const detailCell = el('div', null, [el('strong', { text: label }), el('small', { text: detail })]);
    if (archived) {
      // One date per language attribute — a single toLocaleDateString() call
      // bakes the CURRENT locale's format into both, so the Arabic string would
      // carry an English date. Latin digits on purpose, matching the rest of the
      // Arabic recruitment UI.
      const at = lead.archivedAt ? new Date(lead.archivedAt) : null;
      const whenEn = at ? ` · ${MA.loc(at, 'd', 'en-GB')}` : '';
      const whenAr = at ? ` · ${MA.loc(at, 'd', 'ar-MA-u-nu-latn')}` : '';
      const live = lead.cohort === 'live';
      detailCell.appendChild(_recruitmentLiveNode(
        'small',
        { class: 'recruitment-evidence-released' },
        `${live ? 'Released from the 100-lead trial' : 'Client released back to the team'}${whenEn}`,
        `${live ? 'أُرجع من تجربة 100 عميل' : 'أُرجع العميل إلى الفريق'}${whenAr}`,
      ));
    }
    return el('article', { class: `recruitment-evidence-row${archived ? ' is-released' : ''}` }, [
      el('span', { text: (archived || lead.status === 'completed') ? '✓' : String(lead.order || '').padStart(2, '0') }),
      detailCell,
      openButton,
    ]);
  }

  function _recruitmentApplicationSection(application, candidate = null) {
    const answers = application.answers || {};
    return el('section', { class: 'recruitment-review-section' }, [
      el('h3', { text: 'Application readiness' }),
      el('div', { class: 'recruitment-review-grid' }, [
        _recruitmentFact('Screening', String(application.screeningStatus || application.status || '').replaceAll('_', ' ')),
        _recruitmentFact('Computer', answers.hasComputer ? 'Confirmed' : 'Needs review'),
        _recruitmentFact('Home internet', answers.hasStableInternet ? 'Confirmed' : 'Needs review'),
        _recruitmentFact('Headset', answers.hasHeadset ? 'Confirmed' : 'Needs review'),
        _recruitmentFact('Quiet workspace', answers.hasQuietWorkspace ? 'Confirmed' : 'Needs review'),
        _recruitmentFact('Call experience', answers.callExperience || 'Needs review'),
        _recruitmentFact('Languages', (answers.languages || []).join(' + ')),
        _recruitmentFact('Device check', `${application.deviceCheck?.microphone || 'not run'} · ${application.deviceCheck?.latencyMs ?? '—'} ms`),
        _recruitmentFact('Availability', answers.availability || '—'),
        _recruitmentFact('Invite delivery', candidate ? `WhatsApp ${candidate.invite?.notification?.whatsapp || 'pending'} · Email ${candidate.invite?.notification?.email || 'pending'}` : 'Not created yet'),
        _recruitmentFact('Review reasons', (application.screeningReasons || []).join(' · ') || 'None'),
        _recruitmentFact('Ad source', _recruitmentAttributionLabel(application) || '—'),
        _recruitmentFact('Motivation', answers.motivation || '—'),
      ]),
    ]);
  }

  // ── Hand-picking practice conversations ──────────────────────────────────
  //
  // The automatic pool asks "which old lead is provably safe?" and, on prod,
  // answers "none" — permanently. It wants a lead quiet for 30 days that still
  // has a conversation, and the inbox retains only ~16 days of messages, so the
  // two windows cannot overlap. A candidate who finished the Academy therefore
  // waits forever, which is exactly what happened.
  //
  // Here a human answers instead. Every row shows the stage, the current owner
  // and how long the conversation has been quiet, so the choice is informed —
  // and the rows the SAFETY guard refuses (paying client, live sale, candidate's
  // own chat, no lead) arrive already refused and cannot be ticked.

  // The picker is an ADMIN surface and speaks English only.
  //
  // The admin EN/AR toggle reached the strings JS writes, but nothing else in
  // this dialog: the static menu headings ("QUIET FOR AT LEAST", "All stages",
  // "BY PIPELINE STAGE"), the pipeline vocabulary and the agents' own names
  // have no Arabic to switch to. Turning it on produced a dialog half in each
  // language, flipped to RTL, with the candidate's name and the sentence around
  // it fighting over the start of the title (Ayoub, 2026-08-27, with the
  // screenshots). Half a translation is worse than none.
  //
  // The candidate-facing Academy keeps its Arabic — that is where it means
  // something, because the candidate reads it.
  const _pickerCopy = (en) => en;

  function closeRecruitmentLeadPicker() {
    document.getElementById('recruitmentPickerModal')?.classList.add('hidden');
    document.body.classList.remove('modal-open');
    state.recruitment.picker = null;
  }

  function openRecruitmentLeadPicker(application, candidate) {
    if (!_recruitmentCanManage()) return;
    const modal = document.getElementById('recruitmentPickerModal');
    if (!modal || !candidate?.userId) return;
    const copy = _pickerCopy;
    // English, and left-to-right, whatever the admin toggle says — see the note
    // on _pickerCopy.
    modal.dir = 'ltr';
    state.recruitment.picker = {
      application,
      candidate,
      rows: [],
      selected: new Set(),
      query: '',
      // A candidate already on the live trial is topped up from the live
      // cohort; everyone else is practising.
      cohort: candidate.stage === 'live' ? 'live' : 'practice',
      token: 0,
      // The Inbox's filter vocabulary. Multi-select for stage and owner, one
      // choice for silence — exactly how the Inbox bars behave.
      //
      // The silence presets stop at 14 days on purpose: the clock is the
      // CONVERSATION's, and the inbox is a 20k-message rolling file holding
      // about 16 days, so a 30- or 60-day option could only ever return
      // nothing. Measured on prod 2026-08-26: minQuiet=30 -> 0 rows. A filter
      // that is always empty reads as a broken app, not as an honest answer.
      stages: new Set(),
      owners: new Set(),
      minQuiet: null,
      // How old the lead is — one cumulative window at a time, and only the
      // windows the server actually found rows in are ever offered.
      created: null,
      facets: { stages: [], owners: [], created: [] },
    };
    const name = application?.fullName || candidate.fullName || copy('the candidate', 'المترشح');
    const title = document.getElementById('recruitmentPickerTitle');
    // The candidate's name is isolated: an Arabic name inside an English
    // sentence (or the reverse) otherwise drags the punctuation around with it
    // and the title reads scrambled.
    if (title) {
      clear(title);
      title.appendChild(document.createTextNode(copy('Choose leads for ', 'اختر عملاء لـ ')));
      title.appendChild(el('bdi', { text: name }));
    }
    const cancel = document.getElementById('recruitmentPickerCancel');
    if (cancel) cancel.textContent = copy('Cancel', 'إلغاء');
    const search = document.getElementById('recruitmentPickerSearch');
    if (search) {
      search.value = '';
      search.placeholder = copy('Search by name or phone', 'ابحث بالاسم أو الهاتف');
    }
    modal.classList.remove('hidden');
    document.body.classList.add('modal-open');
    _renderRecruitmentPickerFoot();
    loadRecruitmentPickerRows();
    search?.focus();
  }

  async function loadRecruitmentPickerRows() {
    const picker = state.recruitment.picker;
    const body = document.getElementById('recruitmentPickerBody');
    if (!picker || !body) return;
    const copy = _pickerCopy;
    // Every keystroke starts a request; only the newest one may paint. Without
    // this a slow early response lands after a fast later one and the list
    // shows results for a query the admin has already changed.
    const token = ++picker.token;
    clear(body);
    body.appendChild(_recruitmentPickerState(
      'loader',
      copy('Loading conversations…', 'جارٍ تحميل المحادثات…'),
      null,
    ));
    refreshIcons(body);
    let response;
    let payload = {};
    try {
      const params = new URLSearchParams({ limit: '120', q: picker.query || '' });
      if (picker.stages.size) params.set('stages', [...picker.stages].join(','));
      if (picker.owners.size) params.set('owners', [...picker.owners].join(','));
      if (picker.minQuiet != null) params.set('minQuiet', String(picker.minQuiet));
      if (picker.created) params.set('created', picker.created);
      response = await api(`/api/recruitment/admin/lead-picker?${params.toString()}`);
      payload = await response.json().catch(() => ({}));
    } catch (error) {
      payload = { error: error.message };
    }
    if (state.recruitment.picker !== picker || token !== picker.token) return;
    clear(body);
    if (!response?.ok) {
      body.appendChild(_recruitmentPickerState(
        'alert-triangle',
        copy('Conversations could not be listed', 'تعذّر عرض المحادثات'),
        payload.error || null,
      ));
      refreshIcons(body);
      return;
    }
    picker.rows = payload.rows || [];
    // Facets are measured over what the SEARCH matched, so the counts beside
    // the other stages do not move as you tick one.
    if (payload.facets) picker.facets = payload.facets;
    _renderRecruitmentPickerFilters();
    // A tick survives a search that hides its row: the admin picked it, and a
    // filter is not an un-pick. The give call sends the Set, not the DOM.
    // `eligible` is counted over the rows on THIS page — the guard is the
    // expensive half and only runs on what is being shown. Reading it as
    // "83 of 1 572" claims a check nobody ran over 1 572 conversations.
    const shown = payload.shown ?? (payload.rows || []).length;
    // English on both sides, so a later locale pass cannot flip this dialog
    // back into the half-Arabic state — see _pickerCopy.
    const countText = `${payload.eligible || 0} of the ${shown} shown can be given · ${payload.total || 0} conversations`;
    _setRecruitmentLiveText('recruitmentPickerCount', countText, countText);
    if (!picker.rows.length) {
      body.appendChild(_recruitmentPickerState(
        'search-x',
        copy('No conversation matches', 'لا توجد محادثة مطابقة'),
        copy('Widen the filters or clear the search.', 'وسّع التصفية أو امسح البحث.'),
      ));
      refreshIcons(body);
      _renderRecruitmentPickerFoot();
      return;
    }
    const list = el('div', { class: 'recruitment-picker-list' });
    for (const row of picker.rows) list.appendChild(_recruitmentPickerRow(row, copy));
    body.appendChild(list);
    refreshIcons(body);
    _renderRecruitmentPickerFoot();
  }

  function _recruitmentPickerRow(row, copy) {
    const picker = state.recruitment.picker;
    const checkbox = el('input', { type: 'checkbox', class: 'recruitment-picker-check' });
    checkbox.dataset.chatId = row.chatId;
    // Eligibility is a property of the ROW; room is a property of the cohort
    // and changes with every tick. Both gate the box, so the row's half is
    // parked on the element and re-read by _syncRecruitmentPickerChecks.
    checkbox.dataset.eligible = row.eligible ? '1' : '';
    checkbox.checked = picker.selected.has(row.chatId);
    checkbox.disabled = !row.eligible || (!checkbox.checked && _recruitmentPickerRoom() <= 0);
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) picker.selected.add(row.chatId);
      else picker.selected.delete(row.chatId);
      _renderRecruitmentPickerFoot();
      _renderRecruitmentPickerFilters();
    });
    // Name, then the facts, then the warnings — three bands with their own
    // weight. As five equal lines of text the row said everything at once and
    // therefore nothing: the eye had no anchor to scan down.
    const cells = [checkbox, el('div', { class: 'recruitment-picker-name', text: row.name || row.phone })];
    if (row.stage) {
      cells.push(el('span', {
        class: `recruitment-picker-stage ${RECRUITMENT_PICKER_STAGE_TONE[row.stage] || ''}`.trim(),
        text: RECRUITMENT_PICKER_STAGE_LABEL(row.stage, copy),
      }));
    }
    const meta = [el('span', { class: 'recruitment-ltr', text: row.phone })];
    const sep = () => el('span', { class: 'sep', text: '·' });
    meta.push(sep(), el('span', {
      text: row.quietDays == null
        ? copy('no dated message', 'بدون رسالة مؤرخة')
        : copy(`quiet ${row.quietDays}d`, `صامت ${row.quietDays} يوم`),
    }));
    const createdText = _recruitmentPickerCreatedText(row.createdAt);
    if (createdText) meta.push(sep(), el('span', { text: `created ${createdText}` }));
    // Worth saying on its own: this person wrote and nobody replied. It is not
    // a reason to refuse the row, but it changes what handing it over means.
    if (row.neverAnswered) meta.push(sep(), el('span', { text: copy('we never replied', 'لم نرد عليه قط') }));
    cells.push(el('div', { class: 'recruitment-picker-meta' }, meta));

    const flags = [];
    // Naming the owner is the whole point of the row: taking a conversation off
    // a teammate is a decision, and it is not made blind.
    if (row.currentAgent) {
      flags.push(el('span', { class: 'recruitment-picker-flag is-owner' }, [
        lucideIcon(row.currentAgentIsChatOwner ? 'arrow-left-right' : 'user-round'),
        el('span', {
          text: row.currentAgentIsChatOwner
            ? copy(`Currently ${row.currentAgent}'s — giving it moves it`, `حالياً لدى ${row.currentAgent} — منحه ينقله`)
            : copy(`Lead owner: ${row.currentAgent}`, `مسؤول العميل: ${row.currentAgent}`),
        }),
      ]));
    }
    if (!row.eligible) {
      flags.push(el('span', { class: 'recruitment-picker-flag is-blocked' }, [
        lucideIcon('ban'),
        el('span', {
          text: row.message || row.reason || 'refused',
        }),
      ]));
    }
    if (flags.length) cells.push(el('div', { class: 'recruitment-picker-flags' }, flags));
    return el('label', { class: `recruitment-picker-row${row.eligible ? '' : ' is-refused'}` }, cells);
  }

  // Loading, empty and error get the same designed shape — a grey sentence in
  // the middle of an empty dialog reads as a broken screen, not as an answer.
  function _recruitmentPickerState(icon, title, hint) {
    return el('div', { class: `recruitment-picker-state${icon === 'loader' ? ' is-loading' : ''}` }, [
      lucideIcon(icon),
      el('strong', { text: title }),
      hint ? el('span', { text: hint }) : null,
    ]);
  }

  // Cumulative windows on the lead's age. "Last 30 days" contains "last 7", so
  // the counts read the way an admin expects rather than as disjoint slices.
  function RECRUITMENT_PICKER_CREATED_LABEL(bucket) {
    const table = {
      '7': 'Last 7 days',
      '30': 'Last 30 days',
      '90': 'Last 3 months',
      '365': 'Last 12 months',
      '365+': 'Over a year ago',
    };
    return table[bucket] || bucket;
  }

  // A date the admin can read at a glance. Only the year when it is not this
  // one — "4 Mar" is enough for a lead from this year, and printing 2026 on
  // every row is noise.
  function _recruitmentPickerCreatedText(ms) {
    if (!ms) return null;
    const at = new Date(ms);
    if (Number.isNaN(at.getTime())) return null;
    const sameYear = at.getFullYear() === new Date().getFullYear();
    return MA.loc(at, 'd', 'en-GB', sameYear
      ? { day: 'numeric', month: 'short' }
      : { day: 'numeric', month: 'short', year: 'numeric' });
  }

  // What each stage MEANS, so the chip is scannable without reading the word:
  // work in progress, at risk, won, lost.
  const RECRUITMENT_PICKER_STAGE_TONE = {
    new: 'tone-open',
    contacted: 'tone-open',
    book_test: 'tone-open',
    proforma_sent: 'tone-open',
    no_answer: 'tone-warm',
    no_show: 'tone-warm',
    test_done: 'tone-warm',
    paid: 'tone-won',
    lost: 'tone-lost',
    not_interested: 'tone-lost',
  };

  function RECRUITMENT_PICKER_STAGE_LABEL(stage, copy) {
    const table = {
      new: copy('new', 'جديد'),
      no_answer: copy('no answer', 'لم يجب'),
      contacted: copy('contacted', 'تم التواصل'),
      no_show: copy('no show', 'لم يحضر'),
      test_done: copy('test done', 'أنهى الاختبار'),
      lost: copy('lost', 'ضائع'),
      // The other four reach this list too — unlabelled they surfaced raw, so
      // the row said "proforma_sent" where every neighbour said "no answer".
      book_test: copy('test booked', 'حجز اختبار'),
      proforma_sent: copy('proforma sent', 'أُرسل العرض'),
      paid: copy('paid', 'دفع'),
      not_interested: copy('not interested', 'غير مهتم'),
    };
    return table[stage] || String(stage).replace(/_/g, ' ');
  }

  const RECRUITMENT_PICKER_STAGES = [
    'new', 'contacted', 'no_answer', 'no_show', 'test_done', 'lost',
    'not_interested', 'book_test', 'proforma_sent', 'paid',
  ];

  // One open menu at a time inside the modal, and clicking the page closes it —
  // the same behaviour as the Inbox bars, kept local so the two never fight
  // over each other's state.
  function _pickerCloseMenus(except) {
    for (const id of ['pickerStageMenu', 'pickerOwnerMenu', 'pickerQuietMenu', 'pickerCreatedMenu']) {
      if (id === except) continue;
      document.getElementById(id)?.classList.add('hidden');
    }
  }

  function _pickerToggleMenu(id) {
    const menu = document.getElementById(id);
    if (!menu) return;
    const willOpen = menu.classList.contains('hidden');
    _pickerCloseMenus(id);
    menu.classList.toggle('hidden', !willOpen);
  }

  function _renderRecruitmentPickerFilters() {
    const picker = state.recruitment.picker;
    if (!picker) return;
    const copy = _pickerCopy;

    // Stages: only the ones that actually occur, so the menu never offers a
    // filter that can only return nothing.
    const stageCounts = new Map((picker.facets.stages || []).map(item => [item.stage, item.count]));
    const stageList = document.getElementById('pickerStageList');
    if (stageList) {
      clear(stageList);
      const present = RECRUITMENT_PICKER_STAGES.filter(stage => stageCounts.get(stage));
      if (stageCounts.get('none')) present.push('none');
      for (const stage of present) {
        const on = picker.stages.has(stage);
        const item = el('button', { class: `dropdown-item filter-multi${on ? ' active' : ''}`, type: 'button' }, [
          el('span', { class: 'filter-check' }),
          el('span', { text: stage === 'none' ? copy('No lead', 'بدون عميل') : RECRUITMENT_PICKER_STAGE_LABEL(stage, copy) }),
          el('span', { class: 'filter-count-pill', text: String(stageCounts.get(stage) || 0), style: 'margin-inline-start:auto' }),
        ]);
        item.addEventListener('click', () => {
          if (on) picker.stages.delete(stage); else picker.stages.add(stage);
          loadRecruitmentPickerRows();
        });
        stageList.appendChild(item);
      }
      if (!present.length) {
        stageList.appendChild(el('div', { class: 'muted small', style: 'padding:6px 12px', text: copy('No stage to filter on', 'لا توجد مرحلة للتصفية') }));
      }
    }

    // Owners come from the data, not a hard-coded team list — an agent who
    // leaves stops appearing without anyone editing this.
    const ownerList = document.getElementById('pickerOwnerList');
    if (ownerList) {
      clear(ownerList);
      for (const item of (picker.facets.owners || [])) {
        const on = picker.owners.has(item.owner);
        const node = el('button', { class: `dropdown-item filter-multi${on ? ' active' : ''}`, type: 'button' }, [
          el('span', { class: 'filter-check' }),
          el('span', { text: item.owner }),
          el('span', { class: 'filter-count-pill', text: String(item.count), style: 'margin-inline-start:auto' }),
        ]);
        node.addEventListener('click', () => {
          if (on) picker.owners.delete(item.owner); else picker.owners.add(item.owner);
          loadRecruitmentPickerRows();
        });
        ownerList.appendChild(node);
      }
      if (!(picker.facets.owners || []).length) {
        ownerList.appendChild(el('div', { class: 'muted small', style: 'padding:6px 12px', text: copy('Nobody owns these yet', 'لا أحد يملكها بعد') }));
      }
    }

    document.querySelectorAll('#pickerQuietMenu [data-picker-quiet]').forEach(button => {
      const value = button.dataset.pickerQuiet === '' ? null : Number(button.dataset.pickerQuiet);
      button.classList.toggle('active', value === picker.minQuiet);
    });

    // Create-date windows, rendered from the facets: a window the search found
    // nothing in is not offered at all — the same rule that removed the 30- and
    // 60-day silence presets, which could only ever return nothing.
    const createdList = document.getElementById('pickerCreatedList');
    if (createdList) {
      clear(createdList);
      for (const item of (picker.facets.created || [])) {
        const on = picker.created === item.bucket;
        const node = el('button', { class: `dropdown-item filter-multi${on ? ' active' : ''}`, type: 'button' }, [
          el('span', { class: 'filter-check' }),
          el('span', { text: RECRUITMENT_PICKER_CREATED_LABEL(item.bucket) }),
          el('span', { class: 'filter-count-pill', text: String(item.count), style: 'margin-inline-start:auto' }),
        ]);
        node.addEventListener('click', () => {
          picker.created = on ? null : item.bucket;
          _pickerCloseMenus();
          loadRecruitmentPickerRows();
        });
        createdList.appendChild(node);
      }
      if (!(picker.facets.created || []).length) {
        createdList.appendChild(el('div', { class: 'muted small', style: 'padding:6px 12px', text: 'No lead here carries a create date' }));
      }
    }
    document.querySelector('#pickerCreatedMenu [data-picker-created]')?.classList.toggle('active', !picker.created);
    document.querySelector('#pickerStageMenu [data-picker-stage]')?.classList.toggle('active', picker.stages.size === 0);
    document.querySelector('#pickerOwnerMenu [data-picker-owner]')?.classList.toggle('active', picker.owners.size === 0);

    const stageLabel = document.getElementById('pickerStageLabel');
    if (stageLabel) {
      stageLabel.textContent = picker.stages.size
        ? (picker.stages.size === 1
          ? RECRUITMENT_PICKER_STAGE_LABEL([...picker.stages][0], copy)
          : copy(`${picker.stages.size} stages`, `${picker.stages.size} مراحل`))
        : copy('All stages', 'كل المراحل');
    }
    const ownerLabel = document.getElementById('pickerOwnerLabel');
    if (ownerLabel) {
      ownerLabel.textContent = picker.owners.size
        ? (picker.owners.size === 1 ? [...picker.owners][0] : copy(`${picker.owners.size} owners`, `${picker.owners.size} مسؤولين`))
        : copy('Anyone', 'أي شخص');
    }
    const quietLabel = document.getElementById('pickerQuietLabel');
    if (quietLabel) {
      quietLabel.textContent = picker.minQuiet == null
        ? copy('Any silence', 'أي مدة')
        : copy(`Quiet ${picker.minQuiet}d+`, `صامت ${picker.minQuiet} يوم+`);
    }
    const createdLabel = document.getElementById('pickerCreatedLabel');
    if (createdLabel) {
      createdLabel.textContent = picker.created
        ? RECRUITMENT_PICKER_CREATED_LABEL(picker.created)
        : copy('Any create date', 'أي تاريخ إنشاء');
    }
    document.getElementById('pickerStageMark')?.classList.toggle('hidden', picker.stages.size === 0);
    document.getElementById('pickerOwnerMark')?.classList.toggle('hidden', picker.owners.size === 0);
    document.getElementById('pickerQuietMark')?.classList.toggle('hidden', picker.minQuiet == null);
    document.getElementById('pickerCreatedMark')?.classList.toggle('hidden', !picker.created);

    const selectAll = document.getElementById('pickerSelectAll');
    if (selectAll) {
      const room = _recruitmentPickerRoom();
      const takeable = picker.rows.filter(row => row.eligible && !picker.selected.has(row.chatId)).length;
      // Write the label, not the button: the button owns an icon now.
      const label = document.getElementById('pickerSelectAllLabel') || selectAll;
      label.textContent = copy(
        `Select all shown (${Math.min(takeable, room)})`,
        `اختر كل المعروض (${Math.min(takeable, room)})`,
      );
      selectAll.disabled = !takeable || !room;
    }
  }

  // How many more this candidate can hold — the cap is the cohort's, and both
  // the footer and "select all" have to respect the same number.
  function _recruitmentPickerRoom() {
    const picker = state.recruitment.picker;
    if (!picker) return 0;
    // No cohort cap any more (Ayoub, 2026-08-31) — a candidate may hold as many
    // clients as an admin decides to hand them. What is left is the size of ONE
    // hand-over: the route takes 100 entries per request, so the picker stops
    // there instead of letting 20 ticks be silently dropped server-side.
    return Math.max(0, RECRUITMENT_GIVE_BATCH_MAX - picker.selected.size);
  }

  // Tick every eligible row on screen, stopping at the cap rather than
  // selecting more than can actually be given.
  function _recruitmentPickerSelectAllShown() {
    const picker = state.recruitment.picker;
    if (!picker) return;
    let room = _recruitmentPickerRoom();
    for (const row of picker.rows) {
      if (room <= 0) break;
      if (!row.eligible || picker.selected.has(row.chatId)) continue;
      picker.selected.add(row.chatId);
      room -= 1;
    }
    _syncRecruitmentPickerChecks();
    _renderRecruitmentPickerFoot();
    _renderRecruitmentPickerFilters();
  }

  // Every box on screen agrees with the Set AND with the room left. A row that
  // cannot fit under the cap is refused up front — the old modal accepted the
  // tick, dropped it silently in the foot render, and left the box looking
  // checked while the counter quoted a number nobody could act on.
  function _syncRecruitmentPickerChecks() {
    const picker = state.recruitment.picker;
    if (!picker) return;
    const room = _recruitmentPickerRoom();
    for (const box of document.querySelectorAll('.recruitment-picker-check')) {
      const chatId = box.dataset.chatId;
      if (!chatId) continue;
      box.checked = picker.selected.has(chatId);
      const capped = !box.checked && room <= 0;
      box.disabled = !box.dataset.eligible || capped;
      box.title = capped ? _pickerCopy('One hand-over takes 100 at a time', 'المنح الواحد يأخذ 100 دفعة واحدة') : '';
    }
  }

  function _renderRecruitmentPickerFoot() {
    const picker = state.recruitment.picker;
    const give = document.getElementById('recruitmentPickerGive');
    const giveLabel = document.getElementById('recruitmentPickerGiveLabel');
    if (!picker) return;
    const copy = _pickerCopy;
    const held = picker.cohort === 'live'
      ? (picker.candidate.liveLeads || []).length
      : (picker.candidate.practiceLeads || []).length;
    const room = RECRUITMENT_GIVE_BATCH_MAX;
    // One hand-over is capped at the batch size, and trimming has to reach the
    // DOM too: dropping the entries while the boxes stayed ticked is exactly
    // how the dialog came to read "1 picked" under four ticked rows.
    if (picker.selected.size > room) {
      for (const chatId of [...picker.selected].slice(room)) picker.selected.delete(chatId);
      _syncRecruitmentPickerChecks();
    }
    const count = picker.selected.size;
    const first = String(picker.application?.fullName || picker.candidate.fullName || '').split(/\s+/)[0] || copy('the candidate', 'المترشح');
    // No cap to report — just what is picked and what they already hold. The
    // batch ceiling only shows up when it is actually reached.
    const pickedText = count >= room
      ? `${count} picked · ${room} per hand-over — give these, then pick more`
      : `${count} picked · ${held} already theirs`;
    _setRecruitmentLiveText('recruitmentPickerSelected', pickedText, pickedText);
    if (giveLabel) {
      giveLabel.textContent = count
        ? copy(`Give ${count} to ${first}`, `امنح ${count} لـ ${first}`)
        : copy('Give the leads', 'امنح العملاء');
    }
    if (give) {
      give.disabled = !count;
      give.title = '';
    }
  }

  async function giveRecruitmentPickedLeads() {
    const picker = state.recruitment.picker;
    if (!picker || !picker.selected.size) return;
    const copy = _pickerCopy;
    const give = document.getElementById('recruitmentPickerGive');
    if (give) give.disabled = true;
    const entries = [...picker.selected].map(chatId => ({ chatId, adopt: true }));
    let payload = {};
    let response;
    try {
      response = await api(`/api/recruitment/admin/candidates/${encodeURIComponent(picker.candidate.userId)}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries, cohort: picker.cohort }),
      });
      payload = await response.json().catch(() => ({}));
    } catch (error) {
      payload = { error: error.message };
    }
    if (!response?.ok) {
      if (give) give.disabled = false;
      return toast(payload.error || copy('The leads could not be given', 'تعذّر منح العملاء'), 'error');
    }
    const refused = (payload.rejected || []).length;
    // Refusals are named, never swallowed — the same rule the pool feedback
    // follows, and for the same reason.
    if (refused) _renderRecruitmentPoolFeedback(payload.rejected);
    // A give that could not move the HubSpot owner leaves the row sitting in
    // the old agent's Contacts — the opposite of what giving a lead means, so
    // it is said out loud instead of hiding behind a green "N given".
    const ownership = payload.ownership || {};
    const ownershipGap = payload.given && (ownership.skipped || ownership.failed);
    toast(
      refused
        ? copy(`${payload.given} given · ${refused} refused`, `${payload.given} ممنوح · ${refused} مرفوض`)
        : copy(`${payload.given} clients are now in their Inbox`, `${payload.given} عملاء الآن في صندوق وارده`),
      payload.given ? 'success' : 'error',
    );
    if (ownershipGap) {
      toast(
        ownership.skipped === 'no_trial_hubspot_owner'
          ? copy('Contacts NOT moved: no HubSpot owner for the trial. The previous agent still sees them.',
            'لم تُنقل جهات الاتصال: لا يوجد مالك HubSpot للتجربة. المسؤول السابق ما زال يراها.')
          : copy(`${ownership.failed} contact(s) stayed with the previous agent`,
            `${ownership.failed} جهة اتصال بقيت مع المسؤول السابق`),
        'error',
      );
    }
    if (payload.given) closeRecruitmentLeadPicker();
    else if (give) give.disabled = false;
    loadRecruitmentOverview();
  }

  function closeRecruitmentCandidateReview() {
    document.getElementById('recruitmentReviewModal')?.classList.add('hidden');
    document.body.classList.remove('modal-open');
  }

  function openRecruitmentApplicationReview(application) {
    const modal = document.getElementById('recruitmentReviewModal');
    const body = document.getElementById('recruitmentReviewBody');
    const title = document.getElementById('recruitmentReviewTitle');
    if (!modal || !body) return;
    if (title) title.textContent = application.fullName || 'Application review';
    clear(body);
    body.appendChild(_recruitmentApplicationSection(application));
    const actions = el('div', { class: 'recruitment-review-actions' });
    const canManage = _recruitmentCanManage();
    actions.appendChild(_recruitmentActionButton('Close', 'btn-ghost', closeRecruitmentCandidateReview));
    if (canManage) {
      actions.appendChild(_recruitmentActionButton('Approve Academy account', 'btn-primary', async () => {
        await approveRecruitmentApplication(application.id);
        closeRecruitmentCandidateReview();
      }));
    }
    body.appendChild(actions);
    modal.classList.remove('hidden');
    document.body.classList.add('modal-open');
    refreshIcons(modal);
  }

  function openRecruitmentCandidateReview(application, candidate) {
    const modal = document.getElementById('recruitmentReviewModal');
    const body = document.getElementById('recruitmentReviewBody');
    const title = document.getElementById('recruitmentReviewTitle');
    if (!modal || !body) return;
    if (title) title.textContent = application.fullName || candidate.fullName || 'Candidate review';
    clear(body);
    body.appendChild(_recruitmentApplicationSection(application, candidate));

    const progress = candidate.progress || {};
    const journeyTotal = _recruitmentJourneyTotal();
    body.appendChild(el('section', { class: 'recruitment-review-section' }, [
      el('h3', { text: 'Learning progress' }),
      el('div', { class: 'recruitment-review-grid' }, [
        _recruitmentFact('Current stage', RECRUITMENT_STAGE_LABEL[candidate.stage] || candidate.stage),
        _recruitmentFact('Client journeys', journeyTotal
          ? `${progress.listenedJourneys || 0}/${journeyTotal}`
          : String(progress.listenedJourneys || 0)),
        _recruitmentFact('Protected recordings', _recruitmentRecordingTotal()
          ? `${progress.listenedCalls || 0}/${_recruitmentRecordingTotal()}`
          : String(progress.listenedCalls || 0)),
        // A candidate can be 90% through a single eight-minute recording and
        // still read as "4/10 clients". Jihane said she had listened to
        // everything and nobody could check it without opening the store on the
        // server (2026-08-31) — this row is that check.
        _recruitmentFact('Listening in progress', _recruitmentListeningInProgress(candidate)),
        _recruitmentFact('Academy videos', `${progress.completedModules?.length || 0}/8`),
        // Score is a hint for the manager, never a gate — practice opens on completion.
        _recruitmentFact('Final exam', progress.examComplete
          ? `${progress.examScore || 0}% · ${progress.examPassed ? 'reached the 85% target' : 'below the 85% target (hint only)'}`
          : 'Not completed'),
        _recruitmentFact('Clients worked', `${_recruitmentPracticeCompleted(candidate)}/${(candidate.practiceLeads || []).length + (candidate.liveLeads || []).length} given`),
        // Whether they can place a call at all. Two candidates worked real
        // clients for days with a dead call button and nobody could see it
        // (2026-08-31); the phone now follows the clients, and says so here.
        _recruitmentFact('Phone (3CX)', candidate.hasPhoneLine
          ? 'On the recruitment line'
          : ((candidate.practiceLeads || []).length + (candidate.liveLeads || []).length
            ? 'No line — they cannot call'
            : 'No line (no clients yet)')),
      ]),
    ]));

    // Live rows first, then the work on clients already handed back. A released
    // candidate used to arrive at this gate with an EMPTY evidence list — the
    // release wiped exactly what the manager is here to judge (2026-08-07).
    const evidenceList = el('div', { class: 'recruitment-evidence-list' });
    for (const lead of candidate.practiceLeads || []) evidenceList.appendChild(_recruitmentEvidenceRow(lead));
    for (const entry of candidate.evidenceArchive || []) evidenceList.appendChild(_recruitmentEvidenceRow(entry, true));
    if (!evidenceList.childElementCount) {
      evidenceList.appendChild(_recruitmentLiveNode('p', { class: 'muted small' }, 'No practical clients allocated yet.', 'لم يتم تخصيص أي عميل عملي بعد.'));
    }
    body.appendChild(el('section', { class: 'recruitment-review-section' }, [
      _recruitmentLiveNode('h3', null, '10-client practical evidence', 'الأدلة العملية · 10 عملاء'),
      evidenceList,
    ]));

    const canManage = _recruitmentCanManage();
    const ar = _recruitmentAdminLanguage() === 'ar';
    const copy = (en, arText) => ar ? arText : en;
    const actions = el('div', { class: 'recruitment-review-actions recruitment-record-actions' });
    actions.appendChild(_recruitmentActionButton(copy('Close', 'إغلاق'), 'btn-ghost', closeRecruitmentCandidateReview));
    if (canManage) {
      actions.appendChild(_recruitmentActionButton(copy('Resend access', 'إعادة إرسال الدخول'), 'btn-ghost', async () => {
        if (await resendRecruitmentInvite(candidate.userId)) closeRecruitmentCandidateReview();
      }));
    }
    if (canManage && ((candidate.practiceLeads || []).length || (candidate.liveLeads || []).length)) {
      actions.appendChild(_recruitmentActionButton(copy('Release leads', 'إرجاع العملاء'), 'btn-ghost', async () => {
        if (await releaseRecruitmentTrialLeads(candidate.userId, application.fullName)) closeRecruitmentCandidateReview();
      }));
    }
    // The trial is one manual loop now (Ayoub, 2026-08-31): hand over clients,
    // read what they did, then decide. So the decision is available whenever
    // the manager wants it, not only at a stage the old two-cohort exercise
    // used to reach — and "Accept" ends it by making them an agent.
    if (canManage && candidate.stage !== 'hired') {
      actions.appendChild(_recruitmentActionButton(copy('Keep for coaching', 'إبقاء للتوجيه'), 'btn-ghost', async () => { await decideRecruitmentCandidate(candidate.userId, 'coach'); closeRecruitmentCandidateReview(); }));
      actions.appendChild(_recruitmentActionButton(copy('Accept — make them an agent', 'قبول — اجعله وكيلاً'), 'btn-primary', async () => {
        if (await hireRecruitmentCandidate(candidate.userId, application.fullName)) closeRecruitmentCandidateReview();
      }));
    }
    body.appendChild(actions);
    modal.classList.remove('hidden');
    document.body.classList.add('modal-open');
    refreshIcons(modal);
  }

  async function openRecruitmentAdminLead(lead) {
    closeRecruitmentCandidateReview();
    try { await loadConversations(); } catch {}
    if (!state.chats.find(chat => chat.id === lead.chatId)) {
      state.chats.unshift({ id: lead.chatId, name: lead.label || `Controlled client ${lead.order}`, phone: '', isGroup: false, lastMessage: null, unread: 0 });
    }
    showView('inbox');
    setTimeout(() => selectConversation(lead.chatId), 60);
  }

  async function approveRecruitmentApplication(id) {
    const response = await api(`/api/recruitment/admin/applications/${encodeURIComponent(id)}/approve`, { method: 'POST' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) return toast(payload.error || 'Approval failed', 'error');
    toast('Academy account created and invitation prepared', 'success');
    loadRecruitmentOverview();
  }

  async function resendRecruitmentInvite(userId) {
    const response = await api(`/api/recruitment/admin/candidates/${encodeURIComponent(userId)}/invite`, { method: 'POST' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) { toast(payload.error || 'Invitation failed', 'error'); return false; }
    try { await navigator.clipboard.writeText(payload.accessUrl); } catch {}
    // A 200 here only means the invite was minted — each channel reports its
    // own outcome. Say which ones actually carried the link so nobody assumes
    // a candidate was reached when both legs failed. The password rotates
    // ONLY when the server confirms the credentials email left
    // (payload.passwordRotated) — otherwise the old one keeps working and the
    // toast must say so, or an admin hands out a credential that was killed.
    const delivery = _recruitmentDelivery(payload.notification);
    const rotated = payload.passwordRotated === true;
    toast(
      delivery.reached
        ? (rotated
          ? `Access link copied · new password emailed · ${delivery.label}`
          : `Access link copied · password NOT changed (credentials email did not go out) · ${delivery.label}`)
        : `Access link copied, but nothing was delivered · password NOT changed · ${delivery.label} — send it manually`,
      delivery.reached && rotated ? 'success' : 'error',
    );
    return true;
  }

  // Shared read of an invite's per-channel delivery state.
  function _recruitmentDelivery(notification) {
    const state = notification || {};
    const whatsapp = state.whatsapp || 'pending';
    const email = state.email || 'pending';
    return {
      whatsapp,
      email,
      reached: whatsapp === 'sent' || email === 'sent',
      label: `WhatsApp ${String(whatsapp).replaceAll('_', ' ')} · Email ${String(email).replaceAll('_', ' ')}`,
    };
  }

  // Which ad brought this candidate — compact "source · campaign" readout of
  // the UTM attribution stored on the application. Records created before
  // attribution existed return '' and the row/detail simply omit the line.
  function _recruitmentAttributionLabel(application) {
    const attribution = application?.attribution || {};
    const parts = [attribution.utm_source, attribution.utm_campaign].filter(Boolean);
    if (!parts.length && attribution.fbclid) parts.push('facebook click');
    return parts.join(' · ');
  }

  // Accept: the candidate becomes a real agent and keeps the clients they were
  // working. Confirmed by name — it changes what an account can reach, and it
  // is not undone by clicking again.
  async function hireRecruitmentCandidate(userId, fullName) {
    const who = String(fullName || '').trim() || 'this candidate';
    if (!confirm(`Make ${who} an agent? They keep the clients they are working, and the trial ends.`)) return false;
    const response = await api(`/api/recruitment/admin/candidates/${encodeURIComponent(userId)}/hire`, { method: 'POST' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) { toast(payload.error || 'Could not accept this candidate', 'error'); return false; }
    // The one thing hiring cannot decide for you. Said as a warning rather than
    // a success line, because until it is done their Contacts stays empty.
    if (payload.needsHubspotOwner) {
      toast(`${who} is now an agent — map a HubSpot owner in Workspace → Team so their Contacts fills in`, 'error');
    } else {
      toast(`${who} is now an agent`, 'success');
    }
    loadRecruitmentOverview();
    return true;
  }

  async function decideRecruitmentCandidate(userId, decision) {
    const note = decision === 'coach' ? window.prompt('Coaching note for this candidate:') : '';
    if (decision === 'coach' && note === null) return;
    const response = await api(`/api/recruitment/admin/candidates/${encodeURIComponent(userId)}/decision`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ decision, note: note || '' }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) return toast(payload.error || 'Decision failed', 'error');
    toast(decision === 'approve' ? 'Marked as approved — give them clients or accept them' : 'Candidate moved to coaching', 'success');
    loadRecruitmentOverview();
  }

  // What the server refused and why. Silent skipping is how six paying clients
  // reached the pool unnoticed on 2026-08-07 — every refusal is now named.
  function _renderRecruitmentPoolFeedback(rejected) {
    const box = document.getElementById('recruitmentPoolFeedback');
    if (!box) return;
    clear(box);
    const rows = Array.isArray(rejected) ? rejected : [];
    box.classList.toggle('hidden', !rows.length);
    if (!rows.length) return;
    box.appendChild(_recruitmentLiveNode(
      'strong', { class: 'recruitment-pool-feedback-title' },
      `${rows.length} refused`, `${rows.length} مرفوض`,
    ));
    for (const row of rows.slice(0, 40)) {
      box.appendChild(el('div', { class: 'recruitment-pool-feedback-row' }, [
        el('span', { class: 'recruitment-pool-feedback-phone', text: row.phone || row.chatId || '—' }),
        _recruitmentLiveNode(
          'span', { class: 'recruitment-pool-feedback-reason' },
          row.message || row.reason || 'refused',
          row.messageAr || row.message || row.reason || 'مرفوض',
        ),
      ]));
    }
  }

  // Entries sitting in the pool that the guard will refuse. They can never be
  // allocated, but they still occupy the list — so name them and offer the way
  // out, which is how the six paid clients from 2026-08-07 get cleared.
  function _renderRecruitmentPoolBlocked(pool) {
    const node = document.getElementById('recruitmentPoolBlocked');
    if (!node) return;
    clear(node);
    const blocked = Number(pool?.blocked) || 0;
    node.classList.toggle('hidden', !blocked);
    if (!blocked) return;
    node.appendChild(_recruitmentLiveNode(
      'span', null,
      `${blocked} refused by the guard — review the list`,
      `${blocked} مرفوضون من الحارس — راجع القائمة`,
    ));
    const btn = _recruitmentLiveNode('button', { class: 'btn btn-ghost', type: 'button' }, 'Review pool', 'مراجعة القائمة');
    btn.addEventListener('click', reviewRecruitmentPool);
    node.appendChild(btn);
  }

  // The pool as it really is, one row per entry, each removable. Reuses the
  // suggestions panel so the admin works in one place.
  async function reviewRecruitmentPool() {
    const box = document.getElementById('recruitmentPoolSuggestions');
    if (!box) return;
    clear(box);
    box.classList.remove('hidden');
    const response = await api('/api/recruitment/admin/pool/entries');
    const payload = await response.json().catch(() => ({}));
    clear(box);
    if (!response.ok) {
      box.appendChild(_recruitmentLiveNode('p', { class: 'muted small' }, payload.error || 'Pool could not be listed', 'تعذّر عرض القائمة'));
      return;
    }
    const entries = payload.entries || [];
    if (!entries.length) {
      box.appendChild(_recruitmentLiveNode('p', { class: 'muted small' }, 'The pool is empty.', 'القائمة فارغة.'));
      return;
    }
    const list = el('div', { class: 'recruitment-suggestion-list' });
    for (const entry of entries) {
      const lines = [
        el('strong', { text: entry.label || entry.phone || entry.chatId }),
        el('span', { class: 'muted small', text: `${entry.phone || ''} · ${entry.assignedTo ? 'assigned' : (entry.eligible ? 'ready' : 'refused')}` }),
      ];
      if (!entry.eligible && entry.reason) {
        lines.push(_recruitmentLiveNode('span', { class: 'recruitment-suggestion-owner' }, entry.message || entry.reason, entry.messageAr || entry.message || entry.reason));
      }
      const row = el('div', { class: 'recruitment-suggestion-row' }, [el('div', { class: 'recruitment-suggestion-body' }, lines)]);
      if (!entry.assignedTo) {
        const del = _recruitmentLiveNode('button', { class: 'btn btn-ghost recruitment-pool-remove', type: 'button' }, 'Remove', 'حذف');
        del.addEventListener('click', () => removeRecruitmentPoolEntry(entry.id, entry.phone || entry.chatId));
        row.appendChild(del);
      }
      list.appendChild(row);
    }
    box.appendChild(list);
  }

  async function removeRecruitmentPoolEntry(id, label) {
    if (!confirm(`Remove ${label || 'this entry'} from the trial pool?`)) return;
    const response = await api(`/api/recruitment/admin/pool/${encodeURIComponent(id)}`, { method: 'DELETE' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) return toast(payload.error || 'Entry could not be removed', 'error');
    toast('Removed from the trial pool', 'success');
    await reviewRecruitmentPool();
    loadRecruitmentOverview();
  }

  async function _postRecruitmentPoolEntries(entries, emptyMessage) {
    if (!entries.length) return toast(emptyMessage, 'error');
    const response = await api('/api/recruitment/admin/pool', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ entries }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      // Clear the previous run's refusals — leaving them up next to a failed
      // request reads as if those were this attempt's verdicts.
      _renderRecruitmentPoolFeedback([]);
      toast(payload.error || 'Pool update failed', 'error');
      return null;
    }
    _renderRecruitmentPoolFeedback(payload.rejected);
    const refused = (payload.rejected || []).length;
    toast(
      refused
        ? `${payload.added || 0} added · ${refused} refused`
        : `${payload.added || 0} controlled leads added`,
      payload.added ? 'success' : 'error',
    );
    loadRecruitmentOverview();
    return payload;
  }

  async function addRecruitmentPoolEntries() {
    const input = document.getElementById('recruitmentPoolInput');
    const phones = String(input?.value || '').split(/\r?\n/).map(value => value.trim()).filter(Boolean);
    const payload = await _postRecruitmentPoolEntries(phones.map(phone => ({ phone })), 'Add at least one phone number');
    // Keep what was typed when nothing landed — the admin needs the numbers in
    // front of them to act on the refusal reasons.
    if (payload?.added && input) input.value = '';
  }

  // Old leads a candidate could safely practise on. Almost every real dormant
  // lead already sits with an agent — those are listed too, with the owner
  // named, because taking one is a decision the admin makes per row.
  async function suggestRecruitmentPoolLeads() {
    const box = document.getElementById('recruitmentPoolSuggestions');
    if (!box) return;
    clear(box);
    box.classList.remove('hidden');
    box.appendChild(_recruitmentLiveNode('p', { class: 'muted small' }, 'Looking for old leads…', 'جاري البحث عن عملاء قدامى…'));
    const response = await api('/api/recruitment/admin/pool/suggestions?limit=20');
    const payload = await response.json().catch(() => ({}));
    clear(box);
    if (!response.ok) {
      box.appendChild(_recruitmentLiveNode('p', { class: 'muted small' }, payload.error || 'Old leads could not be listed', 'تعذّر عرض العملاء القدامى'));
      return;
    }
    const suggestions = payload.suggestions || [];
    if (!suggestions.length) {
      box.appendChild(_recruitmentLiveNode(
        'p', { class: 'muted small' },
        'No dormant lead is eligible right now.', 'لا يوجد عميل قديم مؤهل حالياً.',
      ));
      // WHY it is empty. Almost always the same cause: plenty of old leads
      // exist, but their conversations have aged out of the inbox, and a lead
      // with no conversation is not workable. Saying so turns a dead end into
      // something an admin can act on (paste the numbers by hand instead).
      const info = payload.diagnostics || {};
      if (info.dormantLeads != null) {
        box.appendChild(_recruitmentLiveNode(
          'p', { class: 'muted small' },
          `${info.dormantLeads} dormant leads exist, but only ${info.withConversation} still have a conversation in the inbox (${info.retainedChats} conversations retained).`,
          `يوجد ${info.dormantLeads} عميلاً قديماً، لكن ${info.withConversation} فقط ما زالت لديهم محادثة في صندوق الوارد (${info.retainedChats} محادثة محفوظة).`,
        ));
      }
      const dropped = Array.isArray(info.dropped) ? info.dropped : [];
      if (dropped.length) {
        box.appendChild(_recruitmentLiveNode(
          'p', { class: 'muted small' },
          dropped.map(item => `${item.message} ${item.count}`).join(' · '),
          dropped.map(item => `${item.messageAr} ${item.count}`).join(' · '),
        ));
      }
      return;
    }
    const list = el('div', { class: 'recruitment-suggestion-list' });
    for (const item of suggestions) {
      const box2 = el('input', { type: 'checkbox', class: 'recruitment-suggestion-check' });
      box2.dataset.chatId = item.chatId;
      box2.dataset.adopt = item.currentAgentIsChatOwner ? '1' : '';
      const lines = [
        el('strong', { text: item.name || item.phone }),
        el('span', { class: 'muted small', text: `${item.phone} · ${item.stage || '—'} · ${item.quietDays ?? '—'}d` }),
      ];
      if (item.currentAgent) {
        lines.push(_recruitmentLiveNode(
          'span', { class: 'recruitment-suggestion-owner' },
          `now with ${item.currentAgent}`, `حالياً مع ${item.currentAgent}`,
        ));
      }
      list.appendChild(el('label', { class: 'recruitment-suggestion-row' }, [
        box2,
        el('div', { class: 'recruitment-suggestion-body' }, lines),
      ]));
    }
    box.appendChild(list);
    const addBtn = _recruitmentLiveNode(
      'button', { class: 'btn btn-primary btn-block', type: 'button' },
      'Add selected to pool', 'إضافة المحدد إلى القائمة',
    );
    addBtn.addEventListener('click', async () => {
      const entries = [...list.querySelectorAll('.recruitment-suggestion-check')]
        .filter(node => node.checked)
        .map(node => ({ chatId: node.dataset.chatId, adopt: node.dataset.adopt === '1' }));
      // Ticking a row that belongs to an agent moves their conversation to a
      // trainee. Say how many before doing it — ten ticks is ten teammates.
      const taken = entries.filter(entry => entry.adopt).length;
      if (taken && !confirm(
        `${taken} of these conversations currently belong to an agent. Move them to the trial account?`,
      )) return;
      const payload2 = await _postRecruitmentPoolEntries(entries, 'Tick at least one lead');
      if (payload2?.added) { clear(box); box.classList.add('hidden'); }
    });
    box.appendChild(addBtn);
  }

  // The supported way out of a mis-allocation. Drops only the assignments this
  // system created, and only for a trial account.
  async function releaseRecruitmentTrialLeads(userId, name) {
    // Say out loud what this does and does NOT touch. It once silently deleted
    // the candidate's written evidence along with the assignments.
    if (!confirm(`Release ${name || 'this candidate'}'s trial leads back to the team?\n\nTheir written evidence is kept and stays visible in the review.`)) return false;
    const response = await api(`/api/recruitment/admin/candidates/${encodeURIComponent(userId)}/release-leads`, { method: 'POST' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) { toast(payload.error || 'Trial leads could not be released', 'error'); return false; }
    // The delta this release archived, never the archive's running total — the
    // number is read as proof that work was preserved.
    const archived = Number(payload.archivedEvidence) || 0;
    // Say how many contacts went back to their agent: a release that only drops
    // the conversation would leave the contact under the trial owner.
    const restored = Number(payload.ownership?.restored) || 0;
    toast(
      archived
        ? `${payload.releasedChats || 0} conversations released · ${restored} contacts returned · ${archived} evidence records archived`
        : `${payload.releasedChats || 0} conversations released · ${restored} contacts returned`,
      'success',
    );
    if (payload.ownership?.failed) {
      toast(`${payload.ownership.failed} contact(s) could not be returned — check the activity log`, 'error');
    }
    await loadRecruitmentOverview();
    return true;
  }

  let _recruitmentCandidateBound = false;
  function _bindRecruitmentCandidate() {
    if (_recruitmentCandidateBound) return;
    _recruitmentCandidateBound = true;
    window.addEventListener('message', async event => {
      if (event.origin !== location.origin || event.source !== document.getElementById('academyFrame')?.contentWindow) return;
      if (event.data?.type === 'leads7:open-trial-leads') {
        await loadRecruitmentCandidate().catch(() => null);
        if (!state.recruitment.candidate?.practiceLeads?.length && !state.recruitment.candidate?.liveLeads?.length) {
          toast('Your manager must add enough controlled leads to the recruitment pool first', 'error');
          return;
        }
        showView('trial-leads');
        return;
      }
      if (event.data?.type !== 'leads7:academy-progress') return;
      try {
        const response = await api('/api/recruitment/me/academy-progress', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(event.data.progress || {}),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'Progress could not be saved');
        state.recruitment.candidate = payload.candidate;
        _syncTrialAccess(payload.candidate);
        await loadRecruitmentCandidate();
        if (payload.allocation?.allocated?.length) toast('Your 10 real trial clients are ready', 'success');
      } catch (error) { console.warn('[academy progress]', error); }
    });
    document.getElementById('trialEvidenceSave')?.addEventListener('click', saveTrialEvidence);
    document.querySelectorAll('[data-candidate-language]').forEach(button => button.addEventListener('click', () => selectRecruitmentCandidateLanguage(button.dataset.candidateLanguage)));
  }

  async function loadTrialLeads() {
    if (state.me?.role !== 'trial_agent') return;
    const ar = state.recruitment.candidate?.language === 'ar';
    const response = await api('/api/recruitment/me/trial-leads');
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) return toast(payload.error || 'Trial leads unavailable', 'error');
    state.recruitment.trialLeads = payload.leads || [];
    const list = document.getElementById('trialLeadsList');
    const empty = document.getElementById('trialLeadsEmpty');
    const progress = document.getElementById('trialLeadsProgressValue');
    const label = document.getElementById('trialLeadsProgressLabel');
    if (progress) progress.textContent = `${payload.completed || 0}/${payload.total || (payload.stage === 'live' ? 100 : 10)}`;
    if (label) label.textContent = ar ? (payload.stage === 'live' ? 'عميل مكتمل في التجربة المباشرة' : 'عميل مكتمل في التطبيق') : (payload.stage === 'live' ? 'live clients complete' : 'practice clients complete');
    if (!list) return;
    clear(list);
    empty?.classList.toggle('hidden', !!payload.leads?.length);
    const arStatus = { completed: 'مكتمل', locked: 'مقفل', available: 'متاح', in_progress: 'قيد العمل', assignment_conflict: 'غير متاح' };
    for (const lead of payload.leads || []) {
      const complete = lead.status === 'completed';
      const locked = lead.status === 'locked' || lead.status === 'assignment_conflict';
      const row = el('article', { class: `trial-lead-row${complete ? ' is-complete' : ''}${locked ? ' is-locked' : ''}` }, [
        el('span', { class: 'trial-lead-index', text: complete ? '✓' : String(lead.order).padStart(2, '0') }),
        el('div', { class: 'trial-lead-person' }, [el('strong', { text: lead.label || (ar ? `العميل المراقب ${lead.order}` : `Controlled client ${lead.order}`) }), el('span', { text: ar ? (complete ? 'تم حفظ دليل المتابعة' : locked ? 'أكمل العميل السابق أولاً' : 'مُعيّن داخل صندوق وارد Leads7 الحقيقي') : (complete ? 'Follow-up evidence saved' : locked ? 'Complete the previous client first' : 'Assigned in your real Leads7 Inbox') })]),
        el('span', { class: 'recruitment-stage-chip ' + (complete ? 'is-success' : locked ? 'is-warning' : ''), text: ar ? (arStatus[lead.status] || 'متاح') : String(lead.status || 'available').replaceAll('_', ' ') }),
        el('span', { class: 'trial-lead-meta', text: lead.evidence?.nextStepAt ? (ar ? `التالي: ${MA.loc(new Date(lead.evidence.nextStepAt), 'dt', 'ar-MA')}` : `Next: ${MA.loc(new Date(lead.evidence.nextStepAt), 'dt')}`) : (ar ? 'واتساب + المكالمات متاحة' : 'WhatsApp + calls available') }),
      ]);
      const button = _recruitmentActionButton(ar ? (complete ? 'فتح المتابعة' : locked ? 'مقفل' : 'فتح في صندوق الوارد') : (complete ? 'Open follow-up' : locked ? 'Locked' : 'Open in Inbox'), complete ? 'btn-ghost' : 'btn-primary', () => openTrialLead(lead));
      button.disabled = locked;
      row.appendChild(button);
      list.appendChild(row);
    }
    refreshIcons(list);
  }

  async function openTrialLead(lead) {
    const response = await api('/api/recruitment/me/trial-leads/open', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ chatId: lead.chatId }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) return toast(payload.error || 'Lead is locked', 'error');
    try { await loadConversations(); } catch {}
    if (!state.chats.find(chat => chat.id === lead.chatId)) {
      state.chats.unshift({ id: lead.chatId, name: lead.label || `Trial client ${lead.order}`, phone: '', isGroup: false, lastMessage: null, unread: 0 });
    }
    showView('inbox');
    setTimeout(() => selectConversation(lead.chatId), 60);
  }

  function renderTrialEvidenceForChat(chatId) {
    const bar = document.getElementById('trialEvidenceBar');
    if (!bar) return;
    if (state.me?.role !== 'trial_agent' || !chatId) { bar.classList.add('hidden'); return; }
    const leads = state.recruitment.trialLeads?.length
      ? state.recruitment.trialLeads
      : [...(state.recruitment.candidate?.practiceLeads || []), ...(state.recruitment.candidate?.liveLeads || [])];
    const lead = leads.find(item => item.chatId === chatId);
    if (!lead) { bar.classList.add('hidden'); return; }
    const ar = state.recruitment.candidate?.language === 'ar';
    bar.classList.remove('hidden');
    bar.classList.toggle('is-complete', lead.status === 'completed');
    const step = document.getElementById('trialEvidenceStep');
    const title = document.getElementById('trialEvidenceTitle');
    const status = document.getElementById('trialEvidenceStatus');
    if (step) step.textContent = ar ? `عميل التجربة ${lead.order} من ${leads.length}` : `Trial client ${lead.order} of ${leads.length}`;
    if (lead.status === 'completed') {
      if (title) title.textContent = ar ? 'تم حفظ دليل المتابعة' : 'Follow-up evidence saved';
      if (status) status.textContent = ar
        ? `${lead.evidence?.outcome || 'مكتمل'} · المتابعة التالية ${lead.evidence?.nextStepAt ? MA.loc(new Date(lead.evidence.nextStepAt), 'dt', 'ar-MA') : 'محفوظة'}`
        : `${lead.evidence?.outcome || 'Completed'} · Next step ${lead.evidence?.nextStepAt ? MA.loc(new Date(lead.evidence.nextStepAt), 'dt') : 'saved'}`;
    } else {
      if (title) title.textContent = ar ? 'احفظ النتيجة قبل فتح العميل التالي' : 'Save the result before opening the next client';
      if (status) status.textContent = ar ? 'اتصل أو راسل هذا العميل، ثم سجّل المتابعة الحقيقية.' : 'Call or message this client, then document the real follow-up.';
    }
    bar.dataset.chatId = chatId;
    bar.dataset.poolId = lead.poolId || '';
  }

  async function saveTrialEvidence() {
    const bar = document.getElementById('trialEvidenceBar');
    const chatId = bar?.dataset.chatId || '';
    const outcome = document.getElementById('trialEvidenceOutcome')?.value || '';
    const nextStepAt = document.getElementById('trialEvidenceNextStep')?.value || '';
    const note = document.getElementById('trialEvidenceNote')?.value.trim() || '';
    const ar = state.recruitment.candidate?.language === 'ar';
    if (!outcome || !nextStepAt || note.length < 8) return toast(ar ? 'النتيجة والملاحظة المفيدة وموعد المتابعة مطلوبة' : 'Outcome, useful note and next step are required', 'error');
    const button = document.getElementById('trialEvidenceSave');
    if (button) button.disabled = true;
    try {
      const response = await api('/api/recruitment/me/trial-leads/evidence', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ chatId, outcome, nextStepAt, note }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Evidence could not be saved');
      state.recruitment.candidate = payload.candidate;
      state.recruitment.trialLeads = [];
      _syncTrialAccess(payload.candidate);
      document.getElementById('trialEvidenceOutcome').value = '';
      document.getElementById('trialEvidenceNextStep').value = '';
      document.getElementById('trialEvidenceNote').value = '';
      await loadTrialLeads();
      renderTrialEvidenceForChat(chatId);
      toast(
        payload.candidate.stage === 'manager_review'
          ? (ar ? 'اكتمل 10 عملاء — تم فتح مراجعة المدير' : '10 clients complete — manager review opened')
          : (ar ? 'تم الحفظ — فُتح العميل التالي' : 'Saved — the next client is unlocked'),
        'success',
      );
    } catch (error) {
      toast(error.message || error, 'error');
    } finally {
      if (button) button.disabled = false;
    }
  }

  // Aggregate child badges into the parent dropdown trigger badge.
  // Called by refreshTeamchatBadge / refreshTeamgroupsBadge after they
  // update their individual badge. The aggregated number is the SUM of
  // child badge numerals — keeps the indicator informative without
  // doing per-child rendering on the trigger itself.
  function refreshGroupBadges() {
    document.querySelectorAll('.view-tab-group').forEach(group => {
      const parentBadge = group.querySelector('[data-group-badge]');
      if (!parentBadge) return;
      let total = 0;
      group.querySelectorAll('.view-tab-item .view-tab-badge').forEach(b => {
        if (b.classList.contains('hidden')) return;
        const n = parseInt(b.textContent || '', 10);
        if (Number.isFinite(n)) total += n;
      });
      if (total > 0) {
        parentBadge.textContent = total > 99 ? '99+' : String(total);
        parentBadge.classList.remove('hidden');
      } else {
        parentBadge.textContent = '';
        parentBadge.classList.add('hidden');
      }
    });
  }

  // ============================================================
  // AI Bot · Control Center (admin-only) — control + observe the funnel bot.
  // Backend: /api/bot/funnel/{overview,activity,conversations,control,pause,
  //   handoffs/:id/followup,stage-profiles,knowledge,knowledge/*,dry-run,pilot}
  // Live updates via socket 'funnel_event'.
  // ============================================================
  const AIBOT = { inited: false, pane: 'chat', overview: null, messages: [], pending: null, sending: false, pollTimer: null, chatId: null, chats: [], eye: null };
  const AIBOT_ACTION = {
    qualify: { label: 'Qualify', cls: 'info' },
    propose_test: { label: 'Propose test', cls: 'info' },
    wait: { label: 'Wait', cls: 'muted' },
    booked: { label: 'Booked test', cls: 'success' },
    handoff: { label: 'Handoff', cls: 'warn' },
    book_failed: { label: 'Book failed', cls: 'warn' },
    book_rejected_phantom: { label: 'Phantom slot', cls: 'warn' },
    ai_error: { label: 'AI error', cls: 'danger' },
    breaker_tripped: { label: 'Auto-paused', cls: 'danger' },
  };
  function _aibotActionMeta(a) { return AIBOT_ACTION[a] || { label: a || '—', cls: 'muted' }; }
  function aiBotFmtDate(ts) { try { return MA.loc(new Date(Number(ts)), 'dt'); } catch { return '—'; } }
  function aiBotTimeAgo(ts) {
    const t = Number(ts) || 0; if (!t) return '—';
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return (s < 1 ? 0 : s) + 's';
    const m = Math.floor(s / 60); if (m < 60) return m + 'm';
    const h = Math.floor(m / 60); if (h < 24) return h + 'h';
    return Math.floor(h / 24) + 'd';
  }
  function _aibotJson(path, method, body, opts) {
    // 180s default: the assistant planner (Gemini + tool execution) routinely
    // outlives api()'s 60s watchdog. Callers can still override (re-engage
    // waves send to real clients → timeoutMs: 0 there).
    return api(path, { method: method || 'GET', headers: body ? { 'Content-Type': 'application/json' } : {}, body: body ? JSON.stringify(body) : undefined, timeoutMs: 180000, ...(opts || {}) })
      .then(async r => { const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status)); return j; });
  }

  function initAiBot() {
    if (!_isSalesAdmin()) { toast('Admin only', 'error'); showView('inbox'); return; }
    if (!AIBOT.inited) {
      AIBOT.inited = true;
      const input = document.getElementById('aiBotChatInput');
      document.getElementById('aiBotChatSend')?.addEventListener('click', () => { const v = input.value; input.value = ''; aiBotSend(v); });
      input?.addEventListener('keydown', e => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); const v = input.value; input.value = ''; aiBotSend(v); }
      });
      input?.addEventListener('input', () => { input.style.height = 'auto'; input.style.height = Math.min(input.scrollHeight, 200) + 'px'; });
      document.getElementById('aiBotRefreshBtn')?.addEventListener('click', aiBotLoadOverview);
      document.getElementById('aiBotPauseBtn')?.addEventListener('click', aiBotTogglePause);
      document.getElementById('aiBotNewChat')?.addEventListener('click', aiBotNewChat);
      document.getElementById('aiBotSettingsBtn')?.addEventListener('click', () => aiBotSwitchPane('settings'));
      document.getElementById('aiBotReengageBtn')?.addEventListener('click', () => aiBotSwitchPane('reengage'));
      document.getElementById('aiBotReengageRefresh')?.addEventListener('click', aiBotRenderReengage);
      document.getElementById('aiBotFeedbackBtn')?.addEventListener('click', () => aiBotSwitchPane('feedback'));
      document.getElementById('aiBotFeedbackRefresh')?.addEventListener('click', aiBotRenderFeedback);
      document.querySelectorAll('.aibot-fb-tab').forEach(btn => btn.addEventListener('click', () => {
        AIBOT.fbVerdict = btn.dataset.verdict === 'down' ? 'down' : 'up';
        _aiBotPaintFeedbackList();
      }));
      aiBotRenderQuickChips();
      const nm = String(state.me?.displayName || state.me?.username || '').trim().split(' ')[0];
      const wt = document.getElementById('aiBotWelcomeTitle');
      if (wt) { clear(wt); if (nm) { wt.appendChild(document.createTextNode('Salam, ')); wt.appendChild(el('em', { text: nm })); } else { wt.textContent = 'Salam'; } }
      document.getElementById('aiBotChat')?.classList.add('is-empty');
      aiBotLoadChatsList();
    }
    aiBotLoadOverview();
    aiBotLoadEyeStatus();
    aiBotStartPolling();
    if (AIBOT.pane === 'chat') setTimeout(() => document.getElementById('aiBotChatInput')?.focus(), 60);
  }
  // L'3in (Claude) answers in 3LASH when its switch + key are on; otherwise the
  // Gemini planner keeps the chat exactly as before.
  async function aiBotLoadEyeStatus() {
    try { AIBOT.eye = await _aibotJson('/api/bot/eye/status'); } catch { AIBOT.eye = null; }
    const on = !!(AIBOT.eye && AIBOT.eye.enabled);
    const sub = document.querySelector('#aiBotWelcome .aibot-welcome-sub');
    if (sub) sub.textContent = on
      ? "Sewwel 3la ay 7aja f l-platform — clients, chats, calls, tests, no-show, khlas, sources. L'3in kay-9ra data o kay-jawbek b l-asbab."
      : "Kifash n3awnk? Goul liya ach n-dir mn l'bot — l-7ala, l-prix, l-conversations, wla w9ef kollchi.";
    const eb = document.querySelector('#aiBotWelcome .aibot-welcome-eyebrow');
    if (eb) eb.textContent = on ? "L'3in · Claude" : "L'3in · funnel co-pilot";
    aiBotRenderQuickChips();
  }
  function aiBotSwitchPane(pane) {
    AIBOT.pane = pane;
    document.getElementById('aiBotChatPane')?.classList.toggle('hidden', pane !== 'chat');
    document.getElementById('aiBotSettingsPane')?.classList.toggle('hidden', pane !== 'settings');
    document.getElementById('aiBotReengagePane')?.classList.toggle('hidden', pane !== 'reengage');
    document.getElementById('aiBotFeedbackPane')?.classList.toggle('hidden', pane !== 'feedback');
    document.getElementById('aiBotSettingsBtn')?.classList.toggle('active', pane === 'settings');
    document.getElementById('aiBotReengageBtn')?.classList.toggle('active', pane === 'reengage');
    document.getElementById('aiBotFeedbackBtn')?.classList.toggle('active', pane === 'feedback');
    if (pane === 'settings') aiBotRenderSettings();
    else if (pane === 'reengage') aiBotRenderReengage();
    else if (pane === 'feedback') aiBotRenderFeedback();
    else setTimeout(() => document.getElementById('aiBotChatInput')?.focus(), 40);
  }

  // ── Chat history (server-persisted, sidebar) ────────────────────────────────
  function aiBotNewChat() {
    AIBOT.chatId = null; AIBOT.messages = []; AIBOT.pending = null;
    const t = aiBotThread(); if (t) clear(t);
    document.getElementById('aiBotChat')?.classList.add('is-empty');
    aiBotSwitchPane('chat');
    aiBotRenderChatsActive();
    setTimeout(() => document.getElementById('aiBotChatInput')?.focus(), 40);
  }
  async function aiBotLoadChatsList() {
    const host = document.getElementById('aiBotChatsList'); if (!host) return;
    try {
      const r = await _aibotJson('/api/bot/funnel/chats');
      AIBOT.chats = r.chats || [];
      clear(host);
      if (!AIBOT.chats.length) { host.appendChild(el('div', { class: 'aibot-sb-empty', text: 'Ma kayن chat بعد.' })); return; }
      AIBOT.chats.forEach(c => {
        const item = el('button', { class: 'aibot-chat-item' + (c.id === AIBOT.chatId ? ' active' : ''), type: 'button', 'data-chat-id': c.id });
        item.appendChild(el('span', { class: 'aibot-chat-item-title', text: c.title || 'Chat' }));
        const del = el('span', { class: 'aibot-chat-del', title: 'Delete' }, [lucideIcon('trash-2')]);
        del.addEventListener('click', (e) => { e.stopPropagation(); aiBotDeleteChat(c.id); });
        item.appendChild(del);
        item.addEventListener('click', () => aiBotOpenChatById(c.id));
        host.appendChild(item);
      });
      refreshIcons(host);
    } catch (e) { /* non-fatal */ }
  }
  function aiBotRenderChatsActive() {
    document.querySelectorAll('#aiBotChatsList .aibot-chat-item').forEach(b =>
      b.classList.toggle('active', b.getAttribute('data-chat-id') === AIBOT.chatId));
  }
  async function aiBotOpenChatById(id) {
    try {
      const r = await _aibotJson('/api/bot/funnel/chats/' + encodeURIComponent(id));
      AIBOT.chatId = id;
      AIBOT.messages = (r.chat && r.chat.messages) || [];
      AIBOT.pending = null;
      aiBotSwitchPane('chat');
      document.getElementById('aiBotChat')?.classList.toggle('is-empty', !AIBOT.messages.some(m => m.role === 'user'));
      aiBotRenderThread();
      aiBotRenderChatsActive();
    } catch (e) { toast('Could not open chat', 'error'); }
  }
  async function aiBotDeleteChat(id) {
    try {
      await _aibotJson('/api/bot/funnel/chats/' + encodeURIComponent(id), 'DELETE');
      if (AIBOT.chatId === id) aiBotNewChat();
      aiBotLoadChatsList();
    } catch (e) { toast(e.message, 'error'); }
  }
  function aiBotRenderThread() {
    const t = aiBotThread(); if (!t) return;
    clear(t);
    for (const m of AIBOT.messages) {
      t.appendChild(el('div', { class: 'aibot-msg ' + (m.role === 'user' ? 'me' : 'them') }, [el('div', { class: 'aibot-msg-text', text: m.text })]));
    }
    aiBotScroll();
  }
  async function aiBotPersist() {
    try {
      if (!AIBOT.messages.some(m => m.role === 'user')) return;
      const wasNew = !AIBOT.chatId;
      const r = await _aibotJson('/api/bot/funnel/chats', 'POST', { id: AIBOT.chatId, messages: AIBOT.messages.map(m => ({ role: m.role, text: m.text })) });
      if (r && r.chat && r.chat.id) { AIBOT.chatId = r.chat.id; if (wasNew) aiBotLoadChatsList(); }
    } catch (e) { /* non-fatal */ }
  }
  function aiBotStartPolling() {
    aiBotStopPolling();
    AIBOT.pollTimer = setInterval(() => {
      if (state.activeView !== 'ai-bot') { aiBotStopPolling(); return; }
      aiBotLoadOverview();
    }, 12000);
  }
  function aiBotStopPolling() { if (AIBOT.pollTimer) { clearInterval(AIBOT.pollTimer); AIBOT.pollTimer = null; } }
  async function aiBotLoadOverview() {
    try {
      const ov = await _aibotJson('/api/bot/funnel/overview?windowHours=24');
      AIBOT.overview = ov;
      aiBotRenderHeader(ov);
      if (AIBOT.pane === 'settings') aiBotRenderSettings();
    } catch (e) { const t = document.getElementById('aiBotStatusText'); if (t) t.textContent = 'error'; }
  }

  // ── Mr.Call chat (the control surface) ──────────────────────────────────────
  function aiBotGreet() {
    // Claude-style: no bot greeting message — the welcome empty-state (#aiBotWelcome)
    // is the greeting; the thread starts with the user's first message.
  }
  function aiBotRenderQuickChips() {
    const host = document.getElementById('aiBotChatQuick'); if (!host) return;
    clear(host);
    const eyeOn = !!(AIBOT.eye && AIBOT.eye.enabled);
    const caps = eyeOn ? [
      { icon: 'calendar-x', label: '3lach no-show had simana?', sub: "L'3in kay-9ra chats dyalhom", prompt: '3lach no-show had simana? 9ra chats dyalhom o 3tini l-asbab' },
      { icon: 'funnel', label: 'Fin kay-w9fo leads?', sub: 'Funnel · stages · 7 ayyam', prompt: 'Fin kay-w9fo leads f akher 7 ayyam? 3tini l-ar9am dyal kol stage o 3lach' },
      { icon: 'phone-missed', label: 'Chkoun ma-tjawbch?', sub: 'Clients bla jawab', prompt: 'Chkoun mn l-clients kteb lina f akher 48 sa3a o ma-jawbnahch?' },
      { icon: 'radar', label: 'Mnin jaw l-clients?', sub: 'Sources · channels', prompt: 'Mnin jaw l-leads had chhar (source/channel) o chmen wa7d kay-khelles ktar?' },
    ] : [
      { icon: 'activity', label: 'Chno l-7ala?', sub: 'Live funnel snapshot', prompt: 'Chno l-7ala?' },
      { icon: 'messages-square', label: 'Wri liya l-conversations', sub: 'Threads · handoffs', prompt: 'Wri liya l-conversations' },
      { icon: 'tag', label: 'Baddel l-prix', sub: 'Edit offer · b diff', prompt: 'Baddel l-prix' },
      { icon: 'flask-conical', label: 'Jarrab b7al client', sub: 'Dry-run mn ghir risk', prompt: 'Jarrab b7al client' },
    ];
    caps.forEach(c => {
      const b = el('button', { class: 'aibot-chip-q' }, [
        el('span', { class: 'aibot-cap-ico' }, [lucideIcon(c.icon)]),
        el('span', { class: 'aibot-cap-txt' }, [
          el('span', { class: 'aibot-cap-label', text: c.label }),
          el('span', { class: 'aibot-cap-sub', text: c.sub }),
        ]),
      ]);
      b.addEventListener('click', () => { const i = document.getElementById('aiBotChatInput'); if (i) { i.value = c.prompt; i.focus(); } });
      host.appendChild(b);
    });
    refreshIcons(host);
  }
  function aiBotThread() { return document.getElementById('aiBotChatThread'); }
  function aiBotScroll() { const t = aiBotThread(); if (t) t.scrollTop = t.scrollHeight; }
  function aiBotAppendUser(text) {
    AIBOT.messages.push({ role: 'user', text });
    const t = aiBotThread(); if (!t) return;
    t.appendChild(el('div', { class: 'aibot-msg me' }, [el('div', { class: 'aibot-msg-text', text })]));
    aiBotScroll();
  }
  function aiBotAppendAssistant(text, extra = {}) {
    AIBOT.messages.push({ role: 'assistant', text });
    const t = aiBotThread(); if (!t) return;
    const bubble = el('div', { class: 'aibot-msg them' });
    if (text) bubble.appendChild(el('div', { class: 'aibot-msg-text', text }));
    if (extra.pendingAction) bubble.appendChild(aiBotConfirmCard(extra.pendingAction, extra.confirm));
    if (extra.data) { const card = aiBotDataCard(extra.data); if (card) bubble.appendChild(card); }
    t.appendChild(bubble);
    refreshIcons(bubble); aiBotScroll();
  }
  function aiBotTyping(on) {
    const t = aiBotThread(); if (!t) return;
    let n = document.getElementById('aiBotTyping');
    if (on) { if (!n) { n = el('div', { class: 'aibot-msg them aibot-typing', id: 'aiBotTyping' }, [el('span', { class: 'aibot-dot' }), el('span', { class: 'aibot-dot' }), el('span', { class: 'aibot-dot' })]); t.appendChild(n); aiBotScroll(); } }
    else if (n) n.remove();
  }
  function aiBotTypingLabel(label) {
    const n = document.getElementById('aiBotTyping'); if (!n) return;
    let l = n.querySelector('.aibot-eye-step');
    if (!l) { l = el('span', { class: 'aibot-eye-step' }); n.appendChild(l); }
    l.textContent = label || '';
  }
  // L'3in: start a background job, poll its steps, then render the answer.
  async function aiBotEyeAsk(text) {
    const history = AIBOT.messages.slice(0, -1).slice(-12).map(m => ({ role: m.role, text: m.text }));
    const start = await _aibotJson('/api/bot/eye/ask', 'POST', { question: text, messages: history }, { timeoutMs: 30000 });
    if (start.disabled) { AIBOT.eye = null; return 'fallback'; } // switched off meanwhile → Gemini planner answers
    if (!start.jobId) { aiBotTyping(false); aiBotAppendAssistant(start.reply || '…', {}); aiBotPersist(); return; }
    aiBotTypingLabel("L'3in kay-fekker…");
    const t0 = Date.now();
    for (;;) {
      await new Promise(r => setTimeout(r, 1500));
      let j;
      try { j = await _aibotJson('/api/bot/eye/jobs/' + encodeURIComponent(start.jobId), 'GET', null, { timeoutMs: 20000 }); }
      catch (e) { if (Date.now() - t0 > 10 * 60 * 1000) throw e; continue; }
      if (j.status === 'running') { if (j.steps && j.steps.length) aiBotTypingLabel(j.steps[j.steps.length - 1] + ' · ' + j.stepCount + ' step'); continue; }
      aiBotTyping(false);
      if (j.status === 'error') { aiBotAppendAssistant(j.error || 'Kayn mochkil.', {}); aiBotPersist(); return; }
      const r = j.result || {};
      // L'3in proposals are confirmed by the button only (typed "wakha" never
      // applies them) and by job id — the args never leave the server.
      const confirm = r.pendingAction ? {
        onYes: async () => {
          aiBotTyping(true);
          try { const c = await _aibotJson('/api/bot/eye/confirm', 'POST', { jobId: start.jobId }); aiBotTyping(false); aiBotHandleResponse(c); }
          catch (e) { aiBotTyping(false); aiBotAppendAssistant('SmH, l-confirm fchel: ' + e.message, {}); }
        },
        onNo: () => aiBotAppendAssistant('Wakha, ma dert walou. 🙅', {}),
      } : null;
      // The bubble shows plain text: drop markdown emphasis if the model slips.
      const reply = String(r.reply || '…').replace(/\*\*([^*\n]+)\*\*/g, '$1').replace(/^#{1,6}\s+/gm, '');
      aiBotAppendAssistant(reply, { pendingAction: r.pendingAction || null, confirm, data: { eye: { cards: r.cards || [], usd: r.usd, turns: r.turns, ms: r.ms } } });
      aiBotPersist();
      return;
    }
  }
  async function aiBotSend(text) {
    text = String(text || '').trim();
    if (!text || AIBOT.sending) return;
    AIBOT.sending = true;
    document.getElementById('aiBotChat')?.classList.remove('is-empty');
    aiBotAppendUser(text);
    aiBotTyping(true);
    const body = { messages: AIBOT.messages.map(m => ({ role: m.role, text: m.text })), hasPending: !!AIBOT.pending };
    if (AIBOT.pending && /^\s*(wakha|wax|wa5a|واخا|ah|آه|oui|yallah|yalah|safi|صافي|sir|سير|na3am|نعم|ok|okay|d.accord|go)\b/i.test(text)) body.confirm = AIBOT.pending;
    if (!body.confirm && AIBOT.eye && AIBOT.eye.enabled) {
      let r = null;
      try { r = await aiBotEyeAsk(text); }
      catch (e) { aiBotTyping(false); aiBotAppendAssistant('SmH, kayn mochkil: ' + e.message, {}); }
      if (r !== 'fallback') { AIBOT.sending = false; return; }
    }
    try {
      const j = await _aibotJson('/api/bot/funnel/assistant', 'POST', body);
      aiBotTyping(false); aiBotHandleResponse(j);
    } catch (e) { aiBotTyping(false); aiBotAppendAssistant('SmH, kayن mochkil: ' + e.message, {}); }
    finally { AIBOT.sending = false; }
  }
  function aiBotHandleResponse(j) {
    if (!j) return;
    if (j.applied !== undefined) { AIBOT.pending = null; aiBotAppendAssistant(j.reply || '✓ Done.', {}); aiBotLoadOverview(); }
    else if (j.pendingAction) { AIBOT.pending = j.pendingAction; aiBotAppendAssistant(j.reply || '', { pendingAction: j.pendingAction }); }
    else { aiBotAppendAssistant(j.reply || '…', { data: j.data }); if (j.data) aiBotLoadOverview(); }
    aiBotPersist();
  }
  async function aiBotConfirmPending() {
    const p = AIBOT.pending; if (!p) return;
    AIBOT.pending = null; aiBotTyping(true);
    try { const j = await _aibotJson('/api/bot/funnel/assistant', 'POST', { messages: [], confirm: p }); aiBotTyping(false); aiBotHandleResponse(j); }
    catch (e) { aiBotTyping(false); aiBotAppendAssistant('SmH, l-confirm fchel: ' + e.message, {}); }
  }
  function aiBotCancelPending() { AIBOT.pending = null; aiBotAppendAssistant('Wakha, ma درت walو. 🙅', {}); }
  function aiBotConfirmCard(pa, handlers) {
    const card = el('div', { class: 'aibot-confirm-card' });
    if (pa.summary) card.appendChild(el('div', { class: 'aibot-confirm-summary', text: pa.summary }));
    if (pa.reason) card.appendChild(el('div', { class: 'aibot-dc-dim', text: "L'3in: " + pa.reason }));
    if (pa.diff && ((pa.diff.removed && pa.diff.removed.length) || (pa.diff.added && pa.diff.added.length))) {
      const d = el('div', { class: 'aibot-diff' });
      (pa.diff.removed || []).forEach(l => d.appendChild(el('div', { class: 'aibot-diff-del', text: '- ' + l })));
      (pa.diff.added || []).forEach(l => d.appendChild(el('div', { class: 'aibot-diff-add', text: '+ ' + l })));
      card.appendChild(d);
    }
    const row = el('div', { class: 'aibot-confirm-actions' });
    const yes = el('button', { class: 'btn btn-sm btn-primary', text: '✓ Confirm' });
    const no = el('button', { class: 'btn btn-sm btn-ghost', text: 'Cancel' });
    yes.addEventListener('click', () => { row.querySelectorAll('button').forEach(b => b.disabled = true); if (handlers && handlers.onYes) handlers.onYes(); else aiBotConfirmPending(); });
    no.addEventListener('click', () => { row.querySelectorAll('button').forEach(b => b.disabled = true); if (handlers && handlers.onNo) handlers.onNo(); else aiBotCancelPending(); });
    row.appendChild(yes); row.appendChild(no); card.appendChild(row);
    return card;
  }
  function aiBotDataCard(data) {
    if (!data) return null;
    const card = el('div', { class: 'aibot-data-card' });
    if (data.status) {
      const s = data.status, st = s.status || {}, m = s.metrics || {};
      const state = st.breakerTripped ? 'Auto-paused' : (!st.enabled ? 'Paused' : (st.quietNow ? 'Quiet hours' : 'Running'));
      card.appendChild(el('div', { class: 'aibot-dc-row' }, [el('strong', { text: state }), el('span', { class: 'aibot-dc-dim', text: ' · ' + (s.pilot && s.pilot.allLeads ? 'full-auto' : 'pilot') + ' · ' + ((s.gemini && s.gemini.model) || '') })]));
      card.appendChild(el('div', { class: 'aibot-dc-stats' }, [
        el('span', {}, [el('b', { text: String(s.activeChats == null ? '—' : s.activeChats) }), el('span', { text: ' active' })]),
        el('span', {}, [el('b', { text: String(m.handled || 0) }), el('span', { text: ' handled' })]),
        el('span', {}, [el('b', { text: String(m.booked || 0) }), el('span', { text: ' booked' })]),
        el('span', {}, [el('b', { text: String(m.handoff || 0) }), el('span', { text: ' handoff' })]),
        el('span', { class: (m.errors ? 'aibot-dc-err' : '') }, [el('b', { text: String(m.errors || 0) }), el('span', { text: ' err' })]),
      ]));
      return card;
    }
    if (data.conversations) {
      const c = data.conversations, rows = [...(c.owned || []), ...(c.handoffs || [])].slice(0, 12);
      if (!rows.length) { card.appendChild(el('div', { class: 'aibot-dc-dim', text: 'Walou daba.' })); return card; }
      rows.forEach(r => {
        const line = el('div', { class: 'aibot-dc-conv' }, [el('span', { class: 'aibot-dc-name', text: (r.name || r.phone || '—') }), el('span', { class: 'aibot-dc-dim', text: ' · ' + (r.stage || '') + (r.handoffReason ? ' · ' + r.handoffReason : '') })]);
        if (r.chatId) { line.style.cursor = 'pointer'; line.addEventListener('click', () => aiBotOpenChat(r.chatId, r.phone)); }
        card.appendChild(line);
      });
      return card;
    }
    if (data.events) {
      const evs = data.events.slice(0, 15);
      if (!evs.length) { card.appendChild(el('div', { class: 'aibot-dc-dim', text: 'Ma kان walou.' })); return card; }
      evs.forEach(e => { const meta = _aibotActionMeta(e.action); card.appendChild(el('div', { class: 'aibot-dc-ev' }, [el('span', { class: 'aibot-ev-badge aibot-b-' + meta.cls, text: meta.label }), el('span', { class: 'aibot-dc-dim', text: ' ' + (e.phone || '') + ' · ' + aiBotTimeAgo(e.ts) })])); });
      return card;
    }
    if (data.velocity) {
      const v = data.velocity;
      const duration = hours => {
        if (hours == null || !Number.isFinite(Number(hours))) return '—';
        const mins = Math.round(Number(hours) * 60), days = Math.floor(mins / 1440);
        const rest = mins % 1440, h = Math.floor(rest / 60), m = rest % 60;
        if (days) return days + 'd ' + h + 'h' + (m ? ' ' + m + 'm' : '');
        if (h) return h + 'h' + (m ? ' ' + m + 'm' : '');
        return m + 'm';
      };
      card.appendChild(el('div', { class: 'aibot-dc-row' }, [
        el('strong', { text: (v.toStageLabel || v.toStage || 'Target stage') + ' · ' + String(v.cohortCount || 0) + ' conversations' }),
        el('span', { class: 'aibot-dc-dim', text: ' · ' + (v.range?.from || '') + ' → ' + (v.range?.to || '') }),
      ]));
      card.appendChild(el('div', { class: 'aibot-dc-stats' }, [
        el('span', {}, [el('b', { text: duration(v.avgFirstMessageToTargetHours) }), el('span', { text: (v.messageMetricsReliable ? ' first msg → stage' : ' sample: first msg → stage') })]),
        el('span', {}, [el('b', { text: duration(v.avgNewToTargetHours) }), el('span', { text: ' New → stage' })]),
        el('span', {}, [el('b', { text: v.avgMessages == null ? '—' : String(v.avgMessages) }), el('span', { text: v.messageMetricsReliable ? ' avg messages' : ' sample messages' })]),
        el('span', {}, [el('b', { text: v.avgInboundMessages == null ? '—' : String(v.avgInboundMessages) }), el('span', { text: ' client' })]),
        el('span', {}, [el('b', { text: v.avgOutboundMessages == null ? '—' : String(v.avgOutboundMessages) }), el('span', { text: ' sent' })]),
      ]));
      card.appendChild(el('div', { class: 'aibot-dc-dim' + (v.messageMetricsReliable ? '' : ' aibot-dc-err'), text: 'Inbox coverage: ' + String(v.leadsWithMessages || 0) + '/' + String(v.cohortCount || 0) + ' (' + String(v.messageCoveragePct || 0) + '%)' + (v.messageMetricsReliable ? '' : ' · message averages are a retained sample, not the full cohort') + ' · median first msg → stage: ' + duration(v.medianFirstMessageToTargetHours) }));
      return card;
    }
    if (data.dryRun) {
      const d = data.dryRun.decision || {};
      card.appendChild(el('div', { class: 'aibot-dc-row' }, [el('span', { class: 'aibot-ev-badge aibot-b-info', text: '👁 preview' }), el('span', { class: 'aibot-dc-dim', text: ' ' + (d.intent || '') + ' · ma t-sift walu' })]));
      card.appendChild(el('div', { class: 'aibot-dc-preview', text: d.reply || (data.dryRun.error ? ('error: ' + data.dryRun.error) : '(walou)') }));
      return card;
    }
    if (data.knowledge !== undefined) { card.appendChild(el('pre', { class: 'aibot-dc-pre', text: String(data.knowledge || '').slice(0, 4000) })); return card; }
    if (data.profiles) { card.appendChild(el('pre', { class: 'aibot-dc-pre', text: JSON.stringify(data.profiles, null, 2).slice(0, 4000) })); return card; }
    // make a row open the contact's conversation in the inbox on click
    const _open = (node, chatId, phone) => {
      if (chatId || phone) {
        node.classList.add('aibot-dc-open');
        node.title = 'Dkhol l l-conversation';
        node.appendChild(el('span', { class: 'aibot-dc-go' }, [lucideIcon('arrow-up-right')]));
        node.addEventListener('click', () => aiBotOpenChat(chatId, phone));
      }
      return node;
    };
    if (data.eye) {
      const lists = (data.eye.cards || []).filter(l => l && Array.isArray(l.clients) && l.clients.length);
      lists.forEach(l => {
        card.appendChild(el('div', { class: 'aibot-eye-title' }, [el('strong', { text: l.title || 'Clients' }), el('span', { class: 'aibot-dc-dim', text: ' · ' + l.clients.length })]));
        l.clients.forEach(c => card.appendChild(_open(el('div', { class: 'aibot-dc-msg' }, [
          el('div', { class: 'aibot-dc-msg-head' }, [
            el('span', { class: 'aibot-dc-name', text: c.name || c.phone || '—' }),
            el('span', { class: 'aibot-dc-dim', text: c.phone && c.name ? c.phone : '' }),
          ]),
          el('div', { class: 'aibot-dc-msg-text', text: c.note || '' }),
        ]), c.chatId, c.phone)));
      });
      const meta = [];
      if (data.eye.turns) meta.push(data.eye.turns + ' step');
      if (data.eye.ms) meta.push(Math.round(data.eye.ms / 1000) + ' s');
      if (data.eye.usd != null) meta.push('$' + Number(data.eye.usd).toFixed(3));
      card.appendChild(el('div', { class: 'aibot-eye-meta', text: "L'3in · " + meta.join(' · ') }));
      return card;
    }
    if (data.messages) {
      const ms = data.messages;
      if (!ms.length) { card.appendChild(el('div', { class: 'aibot-dc-dim', text: 'Ma kان walou.' })); return card; }
      ms.forEach(m => card.appendChild(_open(el('div', { class: 'aibot-dc-msg' }, [
        el('div', { class: 'aibot-dc-msg-head' }, [
          el('span', { class: 'aibot-dc-name', text: m.name || m.phone || '—' }),
          el('span', { class: 'aibot-dc-tag aibot-dc-tag-' + (m.dir === 'out' ? 'out' : 'in'), text: m.who || (m.dir === 'out' ? 'out' : 'in') }),
          el('span', { class: 'aibot-dc-dim', text: m.when ? aiBotTimeAgo(m.when) : '' }),
        ]),
        el('div', { class: 'aibot-dc-msg-text', text: m.text || '' }),
      ]), m.chatId, m.phone)));
      return card;
    }
    if (data.lastReply !== undefined) {
      const r = data.lastReply;
      if (!r) { card.appendChild(el('div', { class: 'aibot-dc-dim', text: 'L\'bot ma sift 7tta msg بعد.' })); return card; }
      card.appendChild(_open(el('div', { class: 'aibot-dc-msg' }, [
        el('div', { class: 'aibot-dc-msg-head' }, [el('span', { class: 'aibot-ev-badge aibot-b-info', text: '🤖 → ' + (r.name || r.phone || '') }), el('span', { class: 'aibot-dc-dim', text: r.when ? aiBotTimeAgo(r.when) : '' })]),
        el('div', { class: 'aibot-dc-preview', text: r.text || '' }),
      ]), r.chatId, r.phone));
      return card;
    }
    if (data.conversation !== undefined) {
      const c = data.conversation;
      if (!c) { card.appendChild(el('div', { class: 'aibot-dc-dim', text: 'Ma l9itش had l-contact.' })); return card; }
      card.appendChild(_open(el('div', { class: 'aibot-dc-msg-head aibot-dc-convhead' }, [el('span', { class: 'aibot-dc-name', text: c.name || c.phone || '—' }), el('span', { class: 'aibot-dc-dim', text: (c.phone || '') + (c.stage ? ' · ' + c.stage : '') })]), c.chatId, c.phone));
      const thread = el('div', { class: 'aibot-dc-thread' });
      (c.messages || []).forEach(m => thread.appendChild(el('div', { class: 'aibot-dc-tmsg ' + (m.dir === 'out' ? 'out' : 'in') }, [el('span', { class: 'aibot-dc-tmsg-who', text: (m.who || '') + ': ' }), el('span', { text: m.text || '' })])));
      if (!(c.messages || []).length) thread.appendChild(el('div', { class: 'aibot-dc-dim', text: 'Walou f had l-conversation.' }));
      card.appendChild(thread);
      return card;
    }
    if (data.leads) {
      const ls = data.leads;
      if (!ls.length) { card.appendChild(el('div', { class: 'aibot-dc-dim', text: 'Ma l9itش 7tta lead.' })); return card; }
      ls.forEach(l => card.appendChild(_open(el('div', { class: 'aibot-dc-conv' }, [
        el('span', { class: 'aibot-dc-name', text: l.name || l.phone || '—' }),
        el('span', { class: 'aibot-dc-dim', text: ' · ' + (l.phone || '') + (l.stage ? ' · ' + l.stage : '') + (l.agent ? ' · ' + l.agent : '') })
      ]), l.chatId, l.phone)));
      return card;
    }
    return null;
  }

  function aiBotRenderHeader(ov) {
    const pill = document.getElementById('aiBotStatusPill');
    const txt = document.getElementById('aiBotStatusText');
    const pauseLabel = document.getElementById('aiBotPauseLabel');
    const pauseBtn = document.getElementById('aiBotPauseBtn');
    const meta = document.getElementById('aiBotMetaLine');
    const banner = document.getElementById('aiBotBreakerBanner');
    if (!ov || !pill) return;
    const s = ov.status || {};
    let label, cls;
    if (s.breakerTripped) { label = 'Auto-paused'; cls = 'aibot-status-danger'; }
    else if (!s.enabled) { label = 'Paused'; cls = 'aibot-status-paused'; }
    else if (s.quietNow) { label = 'Quiet hours'; cls = 'aibot-status-quiet'; }
    else { label = 'Running'; cls = 'aibot-status-running'; }
    pill.className = 'aibot-status-pill ' + cls;
    if (txt) txt.textContent = label;
    if (pauseLabel) pauseLabel.textContent = s.enabled ? 'PAUSE ALL' : 'RESUME';
    if (pauseBtn) pauseBtn.classList.toggle('aibot-pause-resume', !s.enabled);
    // Sidebar rail mirror of the live status
    const rail = document.getElementById('aiBotRailStatus');
    const railLbl = document.getElementById('aiBotRailLbl');
    const railMeta = document.getElementById('aiBotRailMeta');
    if (rail) rail.className = 'aibot-rail-status ' + cls;
    if (railLbl) railLbl.textContent = s.breakerTripped ? 'Bot auto-paused' : (!s.enabled ? 'Bot paused' : (s.quietNow ? 'Quiet hours' : 'Bot is live'));
    if (railMeta) { const mm = ov.metrics || {}; railMeta.textContent = '24h · ' + (mm.handled || 0) + ' replies · ' + (mm.booked || 0) + ' booked'; }
    if (meta) {
      clear(meta);
      const g = ov.gemini || {};
      meta.appendChild(el('span', { class: 'aibot-meta-chip' }, [lucideIcon('sparkles'), el('span', { text: 'Gemini ' + (g.configured ? '✓' : '✗') })]));
      meta.appendChild(el('span', { class: 'aibot-meta-chip' }, [lucideIcon('cpu'), el('span', { text: g.model || '—' })]));
      meta.appendChild(el('span', { class: 'aibot-meta-chip' }, [lucideIcon('target'), el('span', { text: ov.pilot?.allLeads ? 'Full-auto' : ('Pilot · ' + (ov.pilot?.phones?.length || 0) + ' #') })]));
      if (ov.lastError) meta.appendChild(el('span', { class: 'aibot-meta-chip aibot-meta-err' }, [lucideIcon('triangle-alert'), el('span', { text: 'last error: ' + String(ov.lastError.error || '—').slice(0, 60) })]));
      else meta.appendChild(el('span', { class: 'aibot-meta-chip aibot-meta-ok' }, [lucideIcon('check'), el('span', { text: 'no recent errors' })]));
      refreshIcons(meta);
    }
    if (banner) {
      if (s.breakerTripped) {
        clear(banner); banner.classList.remove('hidden');
        const cb = ov.control?.circuitBreaker || {};
        const resume = el('button', { class: 'btn btn-sm btn-primary' }, [el('span', { text: 'Resume bot' })]);
        resume.addEventListener('click', aiBotResumeBreaker);
        banner.appendChild(el('div', { class: 'aibot-breaker-inner' }, [
          el('span', { class: 'aibot-breaker-icon' }, [lucideIcon('octagon-alert')]),
          el('div', { class: 'aibot-breaker-text' }, [el('strong', { text: 'Bot auto-paused' }), el('span', { text: ' — ' + (cb.threshold || '?') + ' AI errors within ' + (cb.windowMin || '?') + ' min. Fix the cause, then resume.' })]),
          resume,
        ]));
        refreshIcons(banner);
      } else { banner.classList.add('hidden'); }
    }
  }

  async function aiBotTogglePause() {
    const enabled = !!AIBOT.overview?.status?.enabled;
    try { await _aibotJson('/api/bot/funnel/pause', 'POST', { paused: enabled }); toast(enabled ? 'Bot paused' : 'Bot resumed', enabled ? 'info' : 'success'); aiBotLoadOverview(); }
    catch (e) { toast(e.message, 'error'); }
  }
  async function aiBotResumeBreaker() {
    try { await _aibotJson('/api/bot/funnel/control', 'POST', { resumeBreaker: true }); toast('Bot resumed', 'success'); aiBotLoadOverview(); }
    catch (e) { toast(e.message, 'error'); }
  }

  // ── Settings pane (status + editable controls) ──────────────────────────────
  function aiBotSwitch(checked, onChange) {
    const input = el('input', { type: 'checkbox' });
    input.checked = !!checked;
    input.addEventListener('change', () => onChange(input.checked));
    return el('label', { class: 'aibot-switch' }, [input, el('span', { class: 'aibot-switch-track' }, [el('span', { class: 'aibot-switch-thumb' })])]);
  }
  function aiBotSetRow(label, desc, control) {
    return el('div', { class: 'aibot-set-row' }, [
      el('div', { class: 'aibot-set-info' }, [el('div', { class: 'aibot-set-label', text: label }), desc ? el('div', { class: 'aibot-set-desc', text: desc }) : null]),
      el('div', { class: 'aibot-set-control' }, [].concat(control)),
    ]);
  }
  async function aiBotSavePilot(patch) {
    try {
      const cur = AIBOT.overview?.pilot || {};
      await _aibotJson('/api/bot/funnel/pilot', 'POST', { enabled: cur.enabled, allLeads: cur.allLeads, phones: cur.phones || [], ...patch });
      toast('Scope updated', 'success'); aiBotLoadOverview();
    } catch (e) { toast(e.message, 'error'); }
  }
  async function aiBotSaveControl(patch) {
    try { await _aibotJson('/api/bot/funnel/control', 'POST', patch); toast('Saved', 'success'); aiBotLoadOverview(); }
    catch (e) { toast(e.message, 'error'); }
  }
  function aiBotRenderSettings() {
    const body = document.getElementById('aiBotSettingsBody');
    if (!body) return;
    const ov = AIBOT.overview;
    if (!ov) { clear(body); body.appendChild(el('div', { class: 'aibot-loading', text: 'Loading…' })); return; }
    clear(body);
    const pilot = ov.pilot || {}, ctrl = ov.control || {}, m = ov.metrics || {};
    const metrics = el('div', { class: 'aibot-metrics' });
    [['Active', ov.activeChats == null ? '—' : ov.activeChats, ''], ['Handled', m.handled || 0, ''], ['Booked', m.booked || 0, 'success'], ['Handoffs', m.handoff || 0, 'warn'], ['Errors', m.errors || 0, m.errors ? 'danger' : 'success']].forEach(([k, v, cls]) => {
      metrics.appendChild(el('div', { class: 'aibot-stat' + (cls ? ' aibot-stat-' + cls : '') }, [el('div', { class: 'aibot-stat-cap', text: k }), el('div', { class: 'aibot-stat-val', text: String(v) })]));
    });
    body.appendChild(metrics);

    const list = el('div', { class: 'aibot-set-list' });
    // Master
    {
      const on = !!pilot.enabled;
      list.appendChild(aiBotSetRow('Bot', on ? 'Running — owns new/contacted leads' : 'Paused', aiBotSwitch(on, async (v) => { try { await _aibotJson('/api/bot/funnel/pause', 'POST', { paused: !v }); toast(v ? 'Bot ON' : 'Bot paused', v ? 'success' : 'info'); aiBotLoadOverview(); } catch (e) { toast(e.message, 'error'); } })));
    }
    // Scope
    {
      const fullAuto = !!pilot.allLeads;
      const seg = el('div', { class: 'aibot-seg' }, [
        (() => { const b = el('button', { class: 'aibot-seg-btn' + (fullAuto ? ' on' : ''), text: 'Full-auto' }); b.addEventListener('click', () => { if (!fullAuto) aiBotSavePilot({ allLeads: true }); }); return b; })(),
        (() => { const b = el('button', { class: 'aibot-seg-btn' + (!fullAuto ? ' on' : ''), text: 'Pilot' }); b.addEventListener('click', () => { if (fullAuto) aiBotSavePilot({ allLeads: false }); }); return b; })(),
      ]);
      const ctrlNodes = [seg];
      if (!fullAuto) {
        const chips = el('div', { class: 'aibot-chips' });
        (pilot.phones || []).forEach(p => {
          const x = el('button', { class: 'aibot-chip-x', text: '×' });
          x.addEventListener('click', () => aiBotSavePilot({ phones: (pilot.phones || []).filter(q => q !== p) }));
          chips.appendChild(el('span', { class: 'aibot-chip' }, [el('span', { text: p }), x]));
        });
        const inp = el('input', { class: 'input aibot-input-sm', placeholder: '2126…' });
        const add = el('button', { class: 'btn btn-sm btn-ghost', text: 'Add' });
        add.addEventListener('click', () => { const v = (inp.value || '').replace(/\D/g, ''); if (v.length >= 8) aiBotSavePilot({ phones: [...new Set([...(pilot.phones || []), v])] }); else toast('Enter a valid number', 'error'); });
        ctrlNodes.push(el('div', { class: 'aibot-chip-add' }, [chips, inp, add]));
      }
      list.appendChild(aiBotSetRow('Scope', 'Which chats the bot owns', ctrlNodes));
    }
    // Models — funnel bot (clients) + Mr.Call (admin), independent
    const _modelOpts = [['', 'Default (gemini-2.5-flash)'], ['gemini-2.5-flash', 'gemini-2.5-flash'], ['gemini-2.5-flash-lite', 'gemini-2.5-flash-lite'], ['gemini-2.5-pro', 'gemini-2.5-pro'], ['gemini-3.1-pro-preview', 'Gemini 3 Pro (3.1-preview)']];
    {
      const sel = el('select', { class: 'input aibot-input-sm' });
      _modelOpts.forEach(([v, t]) => sel.appendChild(el('option', { value: v, text: t })));
      sel.value = ctrl.model || '';
      sel.addEventListener('change', () => aiBotSaveControl({ model: sel.value || null }));
      list.appendChild(aiBotSetRow('Bot model (clients)', 'Gemini model the funnel bot uses with clients', sel));
    }
    {
      const sel = el('select', { class: 'input aibot-input-sm' });
      _modelOpts.forEach(([v, t]) => sel.appendChild(el('option', { value: v, text: t })));
      sel.value = ctrl.assistantModel || '';
      sel.addEventListener('change', () => aiBotSaveControl({ assistantModel: sel.value || null }));
      list.appendChild(aiBotSetRow('Mr.Call model (admin)', 'Gemini model I (Mr.Call) reply with', sel));
    }
    // Quiet hours
    {
      const q = ctrl.quietHours || {};
      const start = el('input', { type: 'time', class: 'input aibot-input-sm' }); start.value = q.start || '09:00';
      const end = el('input', { type: 'time', class: 'input aibot-input-sm' }); end.value = q.end || '21:00';
      const save = el('button', { class: 'btn btn-sm btn-ghost', text: 'Save' });
      save.addEventListener('click', () => aiBotSaveControl({ quietHours: { tz: q.tz || 'Africa/Casablanca', enabled: !!q.enabled, start: start.value, end: end.value } }));
      const sw = aiBotSwitch(!!q.enabled, (v) => aiBotSaveControl({ quietHours: { tz: q.tz || 'Africa/Casablanca', start: start.value, end: end.value, enabled: v } }));
      list.appendChild(aiBotSetRow('Quiet hours', 'Bot replies only inside this window (Morocco)', [sw, el('span', { class: 'aibot-inline' }, [start, el('span', { class: 'aibot-dash', text: '–' }), end, save])]));
    }
    // Circuit breaker
    {
      const cb = ctrl.circuitBreaker || {};
      const thr = el('input', { type: 'number', min: '1', class: 'input aibot-input-xs' }); thr.value = String(cb.threshold || 5);
      const win = el('input', { type: 'number', min: '1', class: 'input aibot-input-xs' }); win.value = String(cb.windowMin || 10);
      const save = el('button', { class: 'btn btn-sm btn-ghost', text: 'Save' });
      save.addEventListener('click', () => aiBotSaveControl({ circuitBreaker: { enabled: !!cb.enabled, threshold: Number(thr.value) || 5, windowMin: Number(win.value) || 10 } }));
      const sw = aiBotSwitch(!!cb.enabled, (v) => aiBotSaveControl({ circuitBreaker: { threshold: Number(thr.value) || 5, windowMin: Number(win.value) || 10, enabled: v } }));
      list.appendChild(aiBotSetRow('Auto-pause', 'Pause the bot if AI errors spike', [sw, el('span', { class: 'aibot-inline' }, [el('span', { class: 'aibot-set-desc', text: '>' }), thr, el('span', { class: 'aibot-set-desc', text: 'err /' }), win, el('span', { class: 'aibot-set-desc', text: 'min' }), save])]));
    }
    body.appendChild(list);
    refreshIcons(body);
  }

  // ── Re-engagement campaign (cold leads → stage template → bot continues) ────
  const _REENGAGE_STAGE_LABELS = { new: 'New', no_answer: 'No answer', contacted: 'Contacted', book_test: 'Test scheduled', no_show: 'No show' };
  async function aiBotRenderReengage() {
    const body = document.getElementById('aiBotReengageBody');
    if (!body) return;
    clear(body); body.appendChild(el('div', { class: 'aibot-loading', text: 'Loading…' }));
    let data;
    try { data = await _aibotJson('/api/reengage/overview'); }
    catch (e) { clear(body); body.appendChild(el('div', { class: 'aibot-re-empty' }, [el('div', { class: 'aibot-re-empty-title', text: 'Failed to load' }), el('div', { class: 'aibot-re-empty-sub', text: e.message })])); return; }
    clear(body);
    const stages = data.stages || [];
    const totalCold = stages.reduce((s, x) => s + (x.cold || 0), 0);
    if (!totalCold) {
      body.appendChild(el('div', { class: 'aibot-re-empty' }, [
        el('div', { class: 'aibot-re-empty-emoji', text: '✓' }),
        el('div', { class: 'aibot-re-empty-title', text: 'No cold leads' }),
        el('div', { class: 'aibot-re-empty-sub', text: 'Every pre-test lead is inside the 24h window — the bot already covers them.' }),
      ]));
      return;
    }
    const list = el('div', { class: 'aibot-set-list' });
    stages.forEach(st => {
      const label = _REENGAGE_STAGE_LABELS[st.stage] || st.stage;
      const tpls = st.templates || [];
      const info = el('div', { class: 'aibot-set-info' }, [
        el('div', { class: 'aibot-set-label', text: label }),
        el('div', { class: 'aibot-re-count' }, [
          el('span', { class: 'aibot-re-cold' + (st.cold ? '' : ' zero'), text: String(st.cold) }),
          el('span', { class: 'aibot-re-cold-cap', text: 'cold' }),
          el('span', { class: 'aibot-re-total', text: '/ ' + st.total + (st.capped ? '+' : '') + ' total' }),
        ]),
      ]);
      let control;
      if (!st.cold) {
        control = el('span', { class: 'aibot-re-done', text: 'all warm' });
      } else if (!tpls.length) {
        control = el('span', { class: 'aibot-re-warn', text: 'No approved template' });
      } else {
        const sel = el('select', { class: 'input aibot-input-sm' });
        tpls.forEach(t => sel.appendChild(el('option', { value: t.id, text: (t.name || t.meta_name) })));
        const qty = el('input', { type: 'number', min: '1', max: '500', class: 'input aibot-input-xs', title: 'Wave size' }); qty.value = String(Math.min(50, st.cold));
        const send = el('button', { class: 'btn btn-sm aibot-re-send', text: 'Send wave' });
        send.addEventListener('click', () => aiBotReengageRun(st.stage, sel.value, Math.max(1, Math.min(500, Number(qty.value) || 50)), send));
        control = el('div', { class: 'aibot-re-ctl' }, [sel, qty, send]);
      }
      list.appendChild(el('div', { class: 'aibot-set-row' }, [info, el('div', { class: 'aibot-set-control' }, [].concat(control))]));
    });
    body.appendChild(list);
    body.appendChild(el('div', { class: 'aibot-re-log', id: 'aiBotReengageLog' }));
    refreshIcons(body);
  }
  async function aiBotReengageRun(stage, templateId, limit, btn) {
    const label = _REENGAGE_STAGE_LABELS[stage] || stage;
    if (!confirm('Send the re-engagement template to ' + limit + ' cold "' + label + '" lead(s)?')) return;
    const old = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    try {
      // timeoutMs: 0 — a 500-lead wave at 450ms/lead runs for minutes and
      // sends to REAL clients; aborting client-side wouldn't stop the server,
      // it would only fake a failure while messages keep going out.
      const r = await _aibotJson('/api/reengage/run', 'POST', { stage, templateId, limit, delayMs: 450 }, { timeoutMs: 0 });
      const failed = (r.failed || []).length;
      toast('Sent to ' + (r.succeeded || 0) + (failed ? (' · ' + failed + ' failed') : '') + ' — ' + label, failed ? 'info' : 'success');
      const log = document.getElementById('aiBotReengageLog');
      if (log) log.prepend(el('div', { class: 'aibot-re-log-row' }, [
        el('span', { class: 'aibot-re-log-stage', text: label }),
        el('span', { class: 'aibot-re-log-res', text: '✓ ' + (r.succeeded || 0) + (failed ? (' · ✗ ' + failed) : '') }),
      ]));
      setTimeout(aiBotRenderReengage, 700);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = old; }
    }
  }

  function aiBotOpenChat(chatId, phone) {
    if (!chatId) { toast('Open the inbox and search ' + (phone || ''), 'info'); return; }
    try { showView('inbox'); selectConversation(chatId); } catch { toast('Could not open chat', 'error'); }
  }

  // ── Bot feedback pane (Positif / Négatif votes on bot replies) ──────────────
  async function aiBotRenderFeedback() {
    const body = document.getElementById('aiBotFeedbackBody');
    if (!body) return;
    clear(body);
    body.appendChild(el('div', { class: 'aibot-sb-empty', text: 'Loading…' }));
    let r;
    try {
      r = await _aibotJson('/api/bot/feedback?limit=300');
    } catch (e) {
      clear(body);
      body.appendChild(el('div', { class: 'aibot-sb-empty', text: 'Load failed: ' + (e.message || e) }));
      return;
    }
    AIBOT.fbItems = r.feedback || [];
    const c = r.counts || { up: 0, down: 0 };
    const cu = document.getElementById('aiBotFbCountUp'); if (cu) cu.textContent = String(c.up || 0);
    const cd = document.getElementById('aiBotFbCountDown'); if (cd) cd.textContent = String(c.down || 0);
    _aiBotPaintFeedbackList();
  }
  // Short relative time for the feedback cards ('5m' / '3h' / '2j' / date).
  // callHistory has its own _formatTimeAgo but it's private to that IIFE.
  function _aiBotFbAgo(ts) {
    const t = ts ? Date.parse(ts) : 0;
    if (!t || isNaN(t)) return '';
    const sec = Math.floor((Date.now() - t) / 1000);
    if (sec < 60) return 'now';
    if (sec < 3600) return Math.floor(sec / 60) + 'm';
    if (sec < 86400) return Math.floor(sec / 3600) + 'h';
    if (sec < 30 * 86400) return Math.floor(sec / 86400) + 'j';
    return MA.loc(new Date(t), 'd', 'fr-FR', { day: '2-digit', month: 'short' });
  }
  function _aiBotPaintFeedbackList() {
    const body = document.getElementById('aiBotFeedbackBody');
    if (!body) return;
    const verdict = AIBOT.fbVerdict === 'down' ? 'down' : 'up';
    document.getElementById('aiBotFbTabUp')?.classList.toggle('active', verdict === 'up');
    document.getElementById('aiBotFbTabDown')?.classList.toggle('active', verdict === 'down');
    clear(body);
    const items = (AIBOT.fbItems || []).filter(f => f.verdict === verdict);
    if (!items.length) {
      body.appendChild(el('div', { class: 'aibot-sb-empty', text: verdict === 'up' ? 'Aucun feedback positif pour le moment.' : 'Aucun feedback négatif pour le moment.' }));
      return;
    }
    // Card grid in the CALLS-strip style: same .call-row skeleton so the
    // per-stage badge colours (app.css [data-stage=…] rules) come for free.
    const grid = el('div', { class: 'aibot-fb-grid' });
    items.forEach(f => {
      // Server attaches f.phone (enriched). Fallback (older server during a
      // rolling deploy): derive from the jid — but a @g.us group id is NOT a
      // phone, and short non-WA ids aren't either. Mirrors the server rule.
      const jid = String(f.chatId || '');
      const jidDigits = jid.endsWith('@g.us') ? '' : jid.split('@')[0].replace(/\D/g, '');
      const phone = f.phone || (jidDigits.length >= 9 ? jidDigits : '');
      const hasName = !!(f.leadName || f.chatName);
      const card = el('button', {
        class: 'call-row aibot-fbcard', type: 'button',
        'data-stage': f.pipelineStage || '',
        title: 'Ouvrir la conversation au message voté',
      });
      // Head — verdict circle (dir slot) · lead name + phone · time-ago
      const head = el('div', { class: 'call-row-head' });
      head.appendChild(el('span', { class: 'aibot-fb-verdict ' + (f.verdict === 'up' ? 'up' : 'down') }, [lucideIcon(f.verdict === 'up' ? 'thumbs-up' : 'thumbs-down')]));
      const id = el('div', { class: 'call-row-id' });
      id.appendChild(el('span', { class: 'call-row-name', text: f.leadName || f.chatName || (phone ? formatPhoneForDisplay(displayPhone(phone)) : '(inconnu)') }));
      if (hasName && phone) id.appendChild(el('span', { class: 'call-row-phone', text: formatPhoneForDisplay(displayPhone(phone)) }));
      head.appendChild(id);
      head.appendChild(el('span', { class: 'call-row-time', text: _aiBotFbAgo(f.createdAt) }));
      card.appendChild(head);
      // Middle — the voted bot reply, clamped to 2 lines
      card.appendChild(el('div', { class: 'aibot-fbcard-msg', text: f.messageText || '(message)' }));
      // Foot — pipeline-stage badge · agent chip · open arrow
      const foot = el('div', { class: 'call-row-foot' });
      if (f.pipelineStage) {
        foot.appendChild(el('span', { class: 'call-row-stage', title: 'Pipeline: ' + (PIPELINE_LABELS[f.pipelineStage] || f.pipelineStage) }, [
          el('span', { class: 'dot' }),
          el('span', { class: 'lbl', text: PIPELINE_LABELS[f.pipelineStage] || f.pipelineStage }),
        ]));
      }
      const agentName = userLabel(f.agent, f.agentDisplayName || (f.agent ? '@' + f.agent : ''));
      if (agentName) {
        foot.appendChild(el('span', { class: 'call-row-agent', title: 'Agent: ' + agentName }, [
          el('span', { class: 'call-row-agent-av', text: (agentName.replace(/^@/, '').trim().charAt(0) || '?').toUpperCase() }),
          el('span', { class: 'call-row-agent-nm', text: agentName }),
        ]));
      }
      foot.appendChild(el('span', { class: 'aibot-fb-open' }, [lucideIcon('arrow-up-right')]));
      card.appendChild(foot);
      card.addEventListener('click', () => openChatAtMessage(f.chatId, f.messageId, f.leadName || f.chatName));
      grid.appendChild(card);
    });
    body.appendChild(grid);
    refreshIcons(body);
  }

  // Open the inbox on a specific chat, then scroll to + flash one message.
  // Messages render async (loadMessages → renderMessages), so poll the DOM
  // for [data-msg-id]; if the message is older than the loaded page, pull
  // older pages a few times before giving up.
  function openChatAtMessage(chatId, messageId, chatName) {
    if (!chatId) return;
    showView('inbox');
    if (!state.chats.find(c => c.id === chatId)) {
      // Chat list may be filtered/unloaded — synthesize a stub row (same as
      // the wa-members jump) so selectConversation doesn't bail.
      state.chats.unshift({ id: chatId, name: chatName || String(chatId).split('@')[0], phone: String(chatId).split('@')[0], isGroup: String(chatId).endsWith('@g.us'), lastMessage: null, unread: 0 });
      try { renderConversations(); } catch { /* list repaints on next poll */ }
    }
    selectConversation(chatId);
    _scrollToMessageWhenRendered(chatId, messageId, 20, 0);
  }
  function _scrollToMessageWhenRendered(chatId, messageId, attemptsLeft, olderPagesPulled) {
    if (state.selectedChatId !== chatId) return;   // user navigated away
    const target = messageId && document.querySelector('[data-msg-id="' + CSS.escape(messageId) + '"]');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('msg-flash');
      setTimeout(() => target.classList.remove('msg-flash'), 1200);
      return;
    }
    if (attemptsLeft <= 0) {
      toast("Message introuvable dans l'historique chargé", 'info');
      return;
    }
    // Messages painted but the target is beyond the latest page → walk history up.
    if (state.messages?.length && state.messagesHasMore && olderPagesPulled < 5 && !state.messagesLoadingOlder) {
      loadOlderMessages().finally(() =>
        _scrollToMessageWhenRendered(chatId, messageId, attemptsLeft - 1, olderPagesPulled + 1));
      return;
    }
    setTimeout(() => _scrollToMessageWhenRendered(chatId, messageId, attemptsLeft - 1, olderPagesPulled), 250);
  }

  // ============================================================
  // Dashboards view (Sprint 4 Feature 3) — Funnel + Agents
  // Backend: /api/funnel, /api/funnel/agents, /api/funnel/time-in-stage
  // Refreshes automatically on `leads7:stageChanged` (pipeline-bar event).
  // ============================================================
  const DASH = {
    inited: false,
    activeTab: 'objectif',      // 'objectif' | 'funnel' | 'agents' | 'coaching'
    objPeriod: 'month',         // 'month' (mensuel) | 'week' (hebdo challenge)
    objMonth: null,             // null = current month; else 'YYYY-MM' for the monthly view stepper (capped to current + previous)
    objWeek: null,              // null = current week; else a Monday YMD for the Hebdo week filter
    objWeekMonth: null,         // 'YYYY-MM' the week-filter stepper is currently showing (defaults to current month)
    agentSort: { key: 'conversion_to_paid', dir: 'desc' },
    funnelMode: 'events',       // 'events' (activité/période) | 'cohort' (par date de création)
    lastFunnel: null,
    countryOptions: null,       // last country-less `countries` list → Pays dropdown options
    lastAgents: null,
    lastWallet: null,
    lastWalletWeek: null,
    lastPayments: null,
    lastRestStats: null,
    loadedOnceFunnel: false,
    loadedOnceAgents: false,
    lastHanane: null,
    loadedOnceHanane: false,
    lastOthers: null,           // admin-only Support (others) monthly board cache
    lastOthersPayments: null,
    lastOthersWeek: null,       // Support tab weekly board cache
    othersPeriod: 'month',      // Support tab: 'month' | 'week'
    othersMonth: null,          // Support tab month stepper (null = current; capped like objMonth)
    othersWeek: null,           // Support tab week (Monday YMD; null = live week)
    othersWeekMonth: null,      // month shown in the Support week picker
    teachPeriod: 'month',       // 'month' (mensuel) | 'week' (hebdo) — Teachers tab, independent of objPeriod
    teachMonth: null,           // null = current month; else 'YYYY-MM' for the monthly stepper
    teachWeek: null,            // null = current week; else a Monday YMD
    teachWeekMonth: null,       // 'YYYY-MM' the week-filter stepper is currently showing
    lastTeachers: null,         // /api/funnel/teachers?month= cache
    lastTeachersWeek: null,     // /api/funnel/teachers?week= cache
  };

  function dashIsoToday() { return new Date().toISOString().slice(0, 10); }
  function dashIsoMinusDays(n) {
    const d = new Date(Date.now() - n * 86400000);
    return d.toISOString().slice(0, 10);
  }

  function initDashboards() {
    // First-time wiring of inputs + tab switchers + auto-refresh hook.
    if (!DASH.inited) {
      DASH.inited = true;
      const fromInput = document.getElementById('dashDateFrom');
      const toInput = document.getElementById('dashDateTo');
      if (fromInput && !fromInput.value) fromInput.value = dashIsoMinusDays(30);
      if (toInput && !toInput.value) toInput.value = dashIsoToday();

      document.querySelectorAll('.dash-subtab').forEach(btn => {
        btn.addEventListener('click', () => {
          const tab = btn.getAttribute('data-dash-tab');
          if (tab === 'whop') { window.location.assign('/payments?view=whop'); return; }
          dashSwitchTab(tab);
        });
      });
      const applyBtn = document.getElementById('dashApplyBtn');
      if (applyBtn) applyBtn.addEventListener('click', () => dashApplyFilters());
      const refreshBtn = document.getElementById('dashRefreshBtn');
      if (refreshBtn) refreshBtn.addEventListener('click', () => dashApplyFilters());

      // Funnel model toggle: Activité (events) vs Cohorte (par date de création).
      document.querySelectorAll('[data-funnel-mode]').forEach(btn => {
        btn.addEventListener('click', () => {
          const mode = btn.getAttribute('data-funnel-mode') === 'cohort' ? 'cohort' : 'events';
          if (DASH.funnelMode === mode) return;
          DASH.funnelMode = mode;
          document.querySelectorAll('[data-funnel-mode]').forEach(b =>
            b.classList.toggle('active', b.getAttribute('data-funnel-mode') === mode));
          refreshFunnel();
        });
      });

      // Sortable agent table headers
      document.querySelectorAll('#dashAgentsTable th[data-sort]').forEach(th => {
        th.addEventListener('click', () => dashSortAgents(th.getAttribute('data-sort')));
      });

      // Auto-refresh when a stage transition is dispatched (pipeline bar event
      // is fired on `document`; the spec asked for `window` — we listen to
      // both so callers from either surface trigger the refresh).
      const onStageChanged = () => {
        if (state.activeView !== 'dashboards') return;
        // Debounce burst transitions (e.g. bulk re-assign).
        clearTimeout(DASH._refreshTimer);
        DASH._refreshTimer = setTimeout(() => {
          if (DASH.activeTab === 'objectif') refreshObjectifActive();
          else refreshFunnel();
        }, 400);
      };
      document.addEventListener('leads7:stageChanged', onStageChanged);
      window.addEventListener('leads7:stageChanged', onStageChanged);
      // Live wallet refresh when a Mbrouk broadcast lands (any agent, any tab).
      document.addEventListener('leads7:leadPaid', () => {
        if (state.activeView !== 'dashboards' || DASH.activeTab !== 'objectif') return;
        clearTimeout(DASH._walletTimer);
        DASH._walletTimer = setTimeout(() => refreshObjectifActive(), 600);
      });

      // Populate agent + source dropdowns once
      dashLoadAgentDropdown().catch(() => {});
      dashLoadSourceDropdown().catch(() => {});
    }

    // Each open: load the currently active sub-tab if not yet loaded.
    if (DASH.activeTab === 'automation-costs') refreshAutomationCosts();
    else if (DASH.activeTab === 'meta-costs') refreshMetaCosts();
    else if (DASH.activeTab === 'objectif') refreshObjectifActive();
    else if (DASH.activeTab === 'funnel') refreshFunnel();
    else if (DASH.activeTab === 'teachers') refreshTeachersActive();
    else if (DASH.activeTab === 'teacher-earnings') ensureTeacherEarnings(true);
    else if (DASH.activeTab === 'others') refreshOthers();
    else refreshAgents();
    _dashSyncFilterRow();
  }

  // The Objectif and Teachers tabs are self-contained (own Mensuel/Hebdo
  // toggle) — the shared date/agent filter row doesn't apply, so hide the
  // whole strip on those tabs.
  function _dashSyncFilterRow() {
    const row = document.querySelector('#dashboardsView .dash-filter-row');
    if (row) row.style.display = ['objectif', 'teachers', 'teacher-earnings', 'meta-costs', 'automation-costs'].includes(DASH.activeTab) ? 'none' : '';
  }

  function dashSwitchTab(tab) {
    if (['meta-costs', 'automation-costs'].includes(tab) && !_isSalesAdmin()) return;
    if (tab === 'teacher-earnings' && !_isSystemAdmin()) return;
    if (tab === 'others' && !state.me?.superAdmin) return;
    if (!tab || tab === DASH.activeTab) return;
    DASH.activeTab = tab;
    document.querySelectorAll('.dash-subtab').forEach(b => {
      b.classList.toggle('dash-subtab-active', b.getAttribute('data-dash-tab') === tab);
    });
    document.getElementById('dashMetaCostsBody')?.classList.toggle('hidden', tab !== 'meta-costs');
    document.getElementById('dashAutomationCostsBody')?.classList.toggle('hidden', tab !== 'automation-costs');
    document.getElementById('dashObjectifBody')?.classList.toggle('hidden', tab !== 'objectif');
    document.getElementById('dashFunnelBody').classList.toggle('hidden', tab !== 'funnel');
    document.getElementById('dashAgentsBody').classList.toggle('hidden', tab !== 'agents');
    document.getElementById('dashCoachingBody')?.classList.toggle('hidden', tab !== 'coaching');
    document.getElementById('dashHananeBody')?.classList.toggle('hidden', tab !== 'hanane');
    document.getElementById('dashOthersBody')?.classList.toggle('hidden', tab !== 'others');
    document.getElementById('dashTeachersBody')?.classList.toggle('hidden', tab !== 'teachers');
    document.getElementById('dashTeacherEarningsBody')?.classList.toggle('hidden', tab !== 'teacher-earnings');
    // Agent filter only makes sense for funnel — hide its label on the other tabs.
    const agentLbl = document.getElementById('dashAgentFilterLabel');
    if (agentLbl) agentLbl.style.display = (tab === 'funnel') ? '' : 'none';
    // Same for the SOURCE filter — funnel-only.
    const sourceLbl = document.getElementById('dashSourceFilterLabel');
    if (sourceLbl) sourceLbl.style.display = (tab === 'funnel') ? '' : 'none';
    // Same for the REGION filter — funnel-only.
    const regionLbl = document.getElementById('dashRegionFilterLabel');
    if (regionLbl) regionLbl.style.display = (tab === 'funnel') ? '' : 'none';
    // Same for the PAYS (country) filter — funnel-only.
    const countryLbl = document.getElementById('dashCountryFilterLabel');
    if (countryLbl) countryLbl.style.display = (tab === 'funnel') ? '' : 'none';
    _dashSyncFilterRow();
    refreshIcons();
    if (tab === 'automation-costs') refreshAutomationCosts();
    else if (tab === 'meta-costs') refreshMetaCosts();
    else if (tab === 'objectif') refreshObjectifActive();
    else if (tab === 'funnel') refreshFunnel();
    else if (tab === 'agents') refreshAgents();
    else if (tab === 'coaching') refreshCoaching();
    else if (tab === 'hanane') refreshHanane();
    else if (tab === 'others') refreshOthers();
    else if (tab === 'teachers') refreshTeachersActive();
    else if (tab === 'teacher-earnings') ensureTeacherEarnings(true);
  }

  function refreshAutomationCosts() {
    if (_isSalesAdmin()) window.LoughaAutomationCosts?.load(document.getElementById('dashAutomationCostsBody'), api);
  }

  function refreshMetaCosts() {
    if (_isSalesAdmin()) window.LoughaMetaCosts?.load(document.getElementById('dashMetaCostsBody'), api);
  }

  function dashApplyFilters() {
    if (DASH.activeTab === 'automation-costs') { refreshAutomationCosts(); return; }
    if (DASH.activeTab === 'meta-costs') { refreshMetaCosts(); return; }
    if (DASH.activeTab === 'teacher-earnings') { ensureTeacherEarnings(true); return; }
    if (DASH.activeTab === 'objectif') refreshObjectifActive();
    else if (DASH.activeTab === 'funnel') refreshFunnel();
    else if (DASH.activeTab === 'agents') refreshAgents();
    else if (DASH.activeTab === 'coaching') refreshCoaching();
  }

  // ---- Coaching tab (Phase 4) ----
  function _coachScoreClass(score) {
    if (score == null) return '';
    const n = Number(score);
    if (n >= 7) return 'is-good';
    if (n >= 4) return 'is-mid';
    return 'is-bad';
  }

  async function refreshCoaching() {
    const loading = document.getElementById('dashCoachingLoading');
    const content = document.getElementById('dashCoachingContent');
    if (loading) loading.style.display = '';
    if (content) content.classList.add('hidden');
    try {
      const { from, to } = dashCurrentRange();
      const r = await api('/api/funnel/coaching?from=' + encodeURIComponent(from) + '&to=' + encodeURIComponent(to));
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const data = await r.json();
      _renderCoachingAgents(data.agents || []);
      _renderCoachingBottlenecks(data.bottlenecks || []);
      if (loading) loading.style.display = 'none';
      if (content) content.classList.remove('hidden');
    } catch (err) {
      if (loading) loading.textContent = 'Failed to load: ' + (err.message || err);
    }
  }

  function _renderCoachingAgents(agents) {
    const tbody = document.getElementById('dashCoachingAgentsTbody');
    clear(tbody);
    if (!agents.length) {
      tbody.appendChild(el('tr', {}, [el('td', { colspan: 7, style: 'text-align:center; padding:18px; color:var(--text-secondary); font-style:italic;', text: 'No leads with coaching data in this window yet. Analyze a conversation in the inbox to seed.' })]));
      return;
    }
    for (const a of agents) {
      const scoreCls = _coachScoreClass(a.avg_score_global);
      tbody.appendChild(el('tr', {}, [
        el('td', { text: userLabel(a.agentId || '—') }),
        el('td', { text: String(a.leads_total) }),
        el('td', { text: String(a.leads_analyzed) }),
        el('td', {}, [
          a.avg_score_global != null
            ? el('span', { class: 'coach-score-pill ' + scoreCls, text: a.avg_score_global.toFixed(1) + '/10' })
            : document.createTextNode('—')
        ]),
        el('td', { text: a.avg_axioms_covered != null ? a.avg_axioms_covered.toFixed(1) + '/6' : '—' }),
        el('td', { text: a.no_answer_rate + '%' }),
        el('td', { text: a.paid_rate + '%' }),
      ]));
    }
  }

  function _renderCoachingBottlenecks(bottlenecks) {
    const tbody = document.getElementById('dashCoachingBottlenecksTbody');
    clear(tbody);
    if (!bottlenecks.length) {
      tbody.appendChild(el('tr', {}, [el('td', { colspan: 4, style: 'text-align:center; padding:18px; color:var(--text-secondary); font-style:italic;', text: 'No data yet.' })]));
      return;
    }
    for (const b of bottlenecks) {
      tbody.appendChild(el('tr', {}, [
        el('td', { text: b.label }),
        el('td', { text: String(b.total_arrived) }),
        el('td', { text: String(b.count_stuck) }),
        el('td', {}, [
          el('div', { style: 'display:flex; align-items:center; gap:8px;' }, [
            el('span', { text: b.stuck_pct + '%', style: 'min-width:42px;' }),
            el('div', { class: 'coach-stuck-bar', style: 'flex:1; max-width:200px;' }, [
              el('div', { class: 'coach-stuck-bar-fill', style: 'width:' + Math.min(100, b.stuck_pct) + '%;' })
            ])
          ])
        ]),
      ]));
    }
  }

  // ============ Contacts view (Phase B) ============
  // Spreadsheet-style HubSpot contact list. Loads on tab-open via initContacts().
  // Mirror-first, server-side filter on team / status / date range.
  const CONTACTS = {
    inited: false,
    loading: false,
    rows: [],
    paging: null,
    ownersMap: {},
    leadStatusMap: null,    // { hubspotValue: ourStage }
    distinctStatuses: [],   // [{ value, n }]
    columns: [],            // mirror of activeView.columns (kept for legacy code path)
    propsCatalog: null,     // [{ name, label, type, fieldType, options[], ... }]
    propsByName: {},        // { name: propertyDef }
    teamUsers: [],          // [{ id, username, displayName }] — for admin column picker
    // Phase E2 — Saved Views state
    views: [],              // visible saved views (sorted by createdAt)
    activeView: null,       // currently active ViewObject (source of truth for columns + filters)
    defaultViewId: null,    // current user's default view id
    dirty: false,           // user changed something since the last save/switch
    _baselineSig: '',       // signature of activeView at load time (to detect dirt)
    pageSize: 25,           // HubSpot-familiar page size; adjustable in the pager
    pageIndex: 1,           // 1-based current cursor page
    pageCursors: [''],      // cursor used to load each page (index = page - 1)
    total: 0,
    sortKey: '',            // local sort for the currently loaded page
    sortDir: 'asc',
    lastActivityPreset: '', // quick-filter label/state (persisted through advancedGroups)
    pendingFocusTerm: null, // deferred search term set by the inbox toolbar's
                            // "open contact in Contacts" button; consumed once
                            // initContacts() has settled — lands on the Paid view
                            // (see _consumePendingContactFocus)
  };

  // ── Object-workspace context (2026-08-15) ─────────────────────────────
  // The HubSpot-style workspace machinery (saved-view tabs, All-views popover,
  // view modal, advanced-filter drawer, column resize/reorder, Edit-columns
  // modal, pager, skeleton, inline editors) is object-agnostic: every shared
  // helper takes a `ws` descriptor and defaults to this Contacts one, so the
  // Contacts page keeps its exact behaviour while Groups (HubSpot companies)
  // instantiates the same machinery with `_GROUPS_WS` (see the Groups view).
  const _CONTACTS_WS = {
    key: 'contacts',
    objectType: 'contact',
    get state() { return CONTACTS; },
    viewsApi: '/api/contacts-views',
    viewsQuery: '',
    viewsLabel: 'contacts',
    colWidthsKey: 'contacts_col_widths_v1',
    ids: {
      tabsScroll: 'viewsTabsScroll', addViewBtn: 'viewNewBtn', dirtyBtn: 'viewSaveDirtyBtn',
      allViewsPopover: 'contactsAllViewsPopover', advSummary: 'contactsAdvSummary',
      advToggle: 'contactsAdvancedToggle', tableViewport: 'contactsTableViewport',
      rangeNote: 'contactsRangeNote', pageLabel: 'contactsPageLabel', pageNumbers: 'contactsPageNumbers',
      prevBtn: 'contactsPrevPageBtn', nextBtn: 'contactsNextPageBtn', loading: 'contactsLoading',
    },
    refresh: () => refreshContacts(),
    checkDirty: () => _checkDirty(),
    applyView: (v) => _applyView(v),
    loadViews: () => _contactsLoadViews(),
    switchView: (id) => _switchView(id),
    saveDirty: () => _saveActiveViewDirty(),
    deleteView: (v) => _deleteView(v),
    signView: () => _signActiveView(),
    updateDirtyPill: () => _updateDirtyPill(),
    // Filter payload persisted with a saved view (create / edit / dirty-save).
    viewPayload: () => ({
      ownerFilter: document.getElementById('contactsTeamFilter')?.value || '',
      statusFilter: document.getElementById('contactsStatusFilter')?.value || '',
      dateFromFilter: document.getElementById('contactsDateFrom')?.value || '',
      searchQuery: document.getElementById('contactsSearch')?.value || '',
    }),
    reorderColumn: (from, to, before) => _reorderContactsColumn(from, to, before),
    rerenderTable: () => _renderContactsTable(CONTACTS.rows || []),
    loadCatalog: () => _contactsLoadPropsCatalog(),
    // Edit-columns modal: props hidden from the addable catalog.
    hideCatalogProp: (p) => !_isSystemAdmin() && _isLeadStatusField(p.name),
    goToPage: (n) => _contactsGoToPage(n),
  };

  // Shared pipeline-stage labels — chat-pipeline-bar wording, reused by the
  // STATUS dropdown AND the "Our stage" column (head + cells). Keys are the raw
  // leads.pipeline_stage values.
  const STAGE_LABELS = { new:'New', contacted:'Contacted', book_test:'Test scheduled',
    test_done:'Test done', proforma_sent:'Proforma sent', paid:'Paid',
    no_answer:'No answer', no_show:'No show', not_interested:'Not interested', lost:'Lost' };

  async function initContacts() {
    if (CONTACTS.inited) {
      // A pending focus (from the inbox toolbar) drives its own refresh; only
      // fall back to a plain refresh when there's nothing to focus.
      if (!_consumePendingContactFocus()) await refreshContacts();
      return;
    }
    CONTACTS.inited = true;
    // Restore the operator's table preferences without changing shared views.
    try {
      const storedSize = Number(localStorage.getItem('contacts_page_size_v1'));
      if ([25, 50, 100].includes(storedSize)) CONTACTS.pageSize = storedSize;
      const compact = localStorage.getItem('contacts_density_v1') === 'compact';
      document.getElementById('contactsView')?.classList.toggle('is-compact', compact);
      document.getElementById('contactsDensityBtn')?.classList.toggle('is-active', compact);
    } catch { /* local preference is optional */ }
    // Phase E6 fix: don't pre-seed the From date. The active view controls
    // dateFromFilter — blank means "all time". A blank input + advanced
    // date filter now returns the EXPECTED row count (matches HubSpot).

    // Load owners + lead-status map + property catalog + saved views in parallel
    await Promise.all([
      _contactsLoadTeamDropdown(),
      _contactsLoadStatusDropdown(),
      _contactsLoadPropsCatalog(),
      _contactsLoadViews(),
    ]);

    // Filter inputs → mark active view dirty on change (so the Save pill appears)
    const markDirtyOn = ['contactsTeamFilter', 'contactsStatusFilter', 'contactsDateFrom', 'contactsSearch'];
    for (const id of markDirtyOn) {
      const el = document.getElementById(id);
      if (!el) continue;
      el.addEventListener('change', _checkDirty);
      el.addEventListener('input', _checkDirty);
    }

    // Status dropdown auto-applies on change — no Apply click needed (Ayoub).
    // Reuses refreshContacts() (reads owner/date/search/advanced fresh, like Apply).
    // Programmatic `.value=` (in _applyView / _contactsLoadStatusDropdown / _submitAddContact)
    // does NOT dispatch 'change', so view-switch / add-contact never double-fetch.
    document.getElementById('contactsStatusFilter')?.addEventListener('change', () => {
      _syncContactsQuickFilterState();
      refreshContacts();
    });
    document.getElementById('contactsTeamFilter')?.addEventListener('change', () => {
      _syncContactsQuickFilterState();
      refreshContacts();
    });

    document.getElementById('contactsApplyBtn')?.addEventListener('click', () => refreshContacts());
    document.getElementById('contactsRefreshBtn')?.addEventListener('click', () => refreshContacts());
    document.getElementById('contactsDateFromBtn')?.addEventListener('click', (ev) => {
      ev.preventDefault();
      _openDateFromPicker(ev.currentTarget);
    });
    document.getElementById('contactsLoadMoreBtn')?.addEventListener('click', () => refreshContacts({ append: true }));
    const _contactsSearchEl = document.getElementById('contactsSearch');
    if (_contactsSearchEl) {
      // Live search: re-query ~300ms after the user stops typing — no Enter
      // needed. >=2 chars searches; clearing the box restores the default list;
      // a single char is skipped (a 1-char "%x%" matches a huge slice).
      let _searchDebounce = null;
      _contactsSearchEl.addEventListener('input', () => {
        clearTimeout(_searchDebounce);
        const v = (_contactsSearchEl.value || '').trim();
        if (v.length === 1) return;
        _searchDebounce = setTimeout(() => refreshContacts(), 300);
      });
      // Enter searches immediately (cancel the pending debounce so they don't double-fire).
      _contactsSearchEl.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') { clearTimeout(_searchDebounce); refreshContacts(); }
      });
    }
    document.getElementById('contactsEditColumnsBtn')?.addEventListener('click', () => openContactsColumnsModal());
    document.getElementById('contactsExportBtn')?.addEventListener('click', () => _bulkExportCsv());
    document.getElementById('contactsAddBtn')?.addEventListener('click', _openAddContactModal);
    document.getElementById('contactsDataQualityBtn')?.addEventListener('click', _showContactsDataQuality);
    document.getElementById('contactsActionsBtn')?.addEventListener('click', (ev) => _openContactsActionsMenu(ev.currentTarget));
    document.getElementById('contactsImportBtn')?.addEventListener('click', () => {
      window.open('https://app.hubspot.com/imports/', '_blank', 'noopener,noreferrer');
    });
    document.getElementById('contactsObjectMenuBtn')?.addEventListener('click', (ev) => _openContactsObjectMenu(ev.currentTarget));
    document.getElementById('contactsAllViewsBtn')?.addEventListener('click', (ev) => _openAllViewsPopover(ev.currentTarget));
    document.getElementById('contactsClearFiltersBtn')?.addEventListener('click', _clearContactsFilters);
    document.getElementById('contactsAddQuickFilterBtn')?.addEventListener('click', (ev) => _addContactsQuickFilter(ev.currentTarget));
    document.getElementById('contactsLastActivityBtn')?.addEventListener('click', (ev) => _openLastActivityQuickFilter(ev.currentTarget));
    document.getElementById('contactsDensityBtn')?.addEventListener('click', (ev) => {
      const view = document.getElementById('contactsView');
      const compact = !view?.classList.contains('is-compact');
      view?.classList.toggle('is-compact', compact);
      ev.currentTarget.classList.toggle('is-active', compact);
      try { localStorage.setItem('contacts_density_v1', compact ? 'compact' : 'comfortable'); } catch {}
    });
    document.getElementById('contactsPrevPageBtn')?.addEventListener('click', () => _contactsGoToPage(CONTACTS.pageIndex - 1));
    document.getElementById('contactsNextPageBtn')?.addEventListener('click', () => _contactsGoToPage(CONTACTS.pageIndex + 1));
    const pageSizeEl = document.getElementById('contactsPageSize');
    if (pageSizeEl) {
      pageSizeEl.value = String(CONTACTS.pageSize);
      pageSizeEl.addEventListener('change', () => {
        CONTACTS.pageSize = Number(pageSizeEl.value) || 25;
        try { localStorage.setItem('contacts_page_size_v1', String(CONTACTS.pageSize)); } catch {}
        refreshContacts();
      });
    }
    document.getElementById('addContactSubmitBtn')?.addEventListener('click', _submitAddContact);
    document.getElementById('addContactSource')?.addEventListener('change', _syncAddContactSourceDetail);
    document.getElementById('viewNewBtn')?.addEventListener('click', () => _openViewModal({ mode: 'new' }));
    document.getElementById('viewSaveDirtyBtn')?.addEventListener('click', () => _saveActiveViewDirty());
    // Phase E4 wiring
    _initBulkBarWiring();
    _initAdvancedFilterWiring();

    // Keep the owner quick-filter visible for the whole team. Sales admins can
    // choose any HubSpot owner; agents get an explicit, read-only "My contacts"
    // value because the API correctly forces their own owner scope.
    document.getElementById('contactsTeamFilterLabel')?.classList.remove('hidden');
    _syncContactsQuickFilterState();

    // Modal save / delete wiring (the modal lives in the page DOM; shared with Groups)
    _wireViewModalOnce();

    // If we arrived here from the inbox toolbar's contact button, focus that
    // contact (clears filters + searches by phone); otherwise plain first load.
    if (!_consumePendingContactFocus()) await refreshContacts();
  }

  // Deferred contact focus: the inbox toolbar's contact button stashes a search
  // term on CONTACTS.pendingFocusTerm and switches to the Contacts view. This
  // runs once initContacts() has settled. These contacts are paid clients being
  // handed off to support2, so we PIN the Paid stage filter (Ayoub) — the
  // #contactsStatusFilter value 'paid' maps 1:1 to the server `stage` param,
  // which filters the REAL leads.pipeline_stage='paid'. Owner / date / advanced
  // filters are cleared so nothing ELSE hides the row. The term goes into
  // #contactsSearch and we fire the same search path the input uses. Returns
  // true when it consumed a pending term (so the caller skips its own
  // refreshContacts and avoids a double fetch).
  function _consumePendingContactFocus() {
    const term = CONTACTS.pendingFocusTerm;
    if (term == null) return false;
    CONTACTS.pendingFocusTerm = null;
    const ownerEl = document.getElementById('contactsTeamFilter'); if (ownerEl) ownerEl.value = '';
    const statusEl = document.getElementById('contactsStatusFilter'); if (statusEl) statusEl.value = 'paid';
    _setDateFromValue('', 'All time');
    CONTACTS.advancedGroups = [];
    const searchEl = document.getElementById('contactsSearch'); if (searchEl) searchEl.value = term;
    refreshContacts();
    return true;
  }

  // ---- Created-date filter picker (Phase E6.6) — same semantics as advanced ----
  // The hidden #contactsDateFrom holds the semantic value (e.g. 'this_week',
  // '2026-05-01', or 'all'); the visible button shows the pretty label.
  function _setDateFromValue(value, label) {
    const hidden = document.getElementById('contactsDateFrom');
    const btn = document.getElementById('contactsDateFromBtn');
    if (hidden) hidden.value = value || '';
    if (btn) {
      const text = btn.querySelector('span');
      if (text) text.textContent = value ? (label || 'Created date') : 'Created date';
      else btn.textContent = label || 'Created date';
      btn.classList.toggle('is-active', !!value);
      btn.title = value ? (label || value) : 'Created date';
    }
  }

  function _syncContactsQuickFilterState() {
    for (const id of ['contactsTeamFilter', 'contactsStatusFilter']) {
      const select = document.getElementById(id);
      select?.closest('.contacts-quick-select')?.classList.toggle('is-active', !!select.value);
    }
  }
  function _currentDateFromValue() {
    return (document.getElementById('contactsDateFrom')?.value || '').trim();
  }

  function _openDateFromPicker(anchorEl) {
    document.getElementById('dateFromPicker')?.remove();
    const wrap = el('div', {
      id: 'dateFromPicker',
      style: 'position:absolute; background:#fff; border:1px solid var(--border); border-radius:6px; box-shadow:0 8px 24px rgba(0,0,0,0.15); padding:6px; z-index:9999; min-width:240px; max-height:380px; overflow-y:auto;',
    });
    const rect = anchorEl.getBoundingClientRect();
    wrap.style.left = rect.left + 'px';
    wrap.style.top = (rect.bottom + 4) + 'px';

    const presets = [
      { value: '', label: 'All time' },
      { value: 'today', label: 'Today' },
      { value: 'yesterday', label: 'Yesterday' },
      { value: 'this_week', label: 'This week' },
      { value: 'last_week', label: 'Last week' },
      { value: 'this_month', label: 'This month' },
      { value: 'last_month', label: 'Last month' },
      { value: 'this_quarter', label: 'This quarter' },
      { value: 'this_year', label: 'This year' },
      { value: 'last_year', label: 'Last year' },
    ];
    for (const p of presets) {
      const opt = el('button', {
        type: 'button',
        style: 'display:block; width:100%; text-align:left; padding:6px 10px; border:none; background:transparent; cursor:pointer; font-size:13px; border-radius:4px;',
        text: p.label,
      });
      opt.addEventListener('mouseenter', () => opt.style.background = 'var(--gray-50, #f9fafb)');
      opt.addEventListener('mouseleave', () => opt.style.background = 'transparent');
      opt.addEventListener('click', () => {
        document.getElementById('dateFromPicker')?.remove();
        _setDateFromValue(p.value, p.label);
        _checkDirty();
        refreshContacts();
      });
      wrap.appendChild(opt);
    }
    // Custom date input at the bottom
    const customWrap = el('div', { style: 'border-top:1px solid var(--border); padding:8px 4px 4px; margin-top:4px;' });
    customWrap.appendChild(el('div', { style: 'font-size:11px; color:var(--text-secondary); font-weight:600; text-transform:uppercase; margin-bottom:4px;', text: 'Custom (from date →)' }));
    const dateIn = el('input', { type: 'date', class: 'input', style: 'width:100%; font-size:13px; padding:4px 8px;' });
    // Pre-fill if current value is a date string
    const cur = _currentDateFromValue();
    if (/^\d{4}-\d{2}-\d{2}$/.test(cur)) dateIn.value = cur;
    dateIn.addEventListener('change', () => {
      if (!dateIn.value) return;
      document.getElementById('dateFromPicker')?.remove();
      // Display like "Since 2026-05-01"
      _setDateFromValue(dateIn.value, 'Since ' + dateIn.value);
      _checkDirty();
      refreshContacts();
    });
    customWrap.appendChild(dateIn);
    wrap.appendChild(customWrap);
    document.body.appendChild(wrap);
    setTimeout(() => {
      const off = (ev) => {
        if (!wrap.contains(ev.target) && ev.target !== anchorEl) {
          wrap.remove(); document.removeEventListener('click', off);
        }
      };
      document.addEventListener('click', off);
    }, 0);
  }

  // ---- HubSpot-familiar Contacts chrome ---------------------------------
  // These helpers only orchestrate the existing Contacts APIs/actions. They do
  // not introduce a second data model or bypass the current permission gates.
  function _closeContactsFloatingUi() {
    for (const id of ['contactsActionsMenu', 'contactsObjectMenu', 'contactsAllViewsPopover', 'contactsQuickPresetMenu',
      'groupsActionsMenu', 'groupsObjectMenu', 'groupsAllViewsPopover', 'groupsQuickPresetMenu', 'groupsDataQualityPop',
      'influencersActionsMenu', 'influencersObjectMenu', 'influencersAllViewsPopover']) {
      document.getElementById(id)?.remove();
    }
  }

  function _placeContactsPopover(node, anchorEl, { align = 'left', width = 220 } = {}) {
    const rect = anchorEl.getBoundingClientRect();
    const left = align === 'right' ? rect.right - width : rect.left;
    node.style.width = width + 'px';
    node.style.left = Math.max(8, Math.min(window.innerWidth - width - 8, left)) + 'px';
    node.style.top = Math.min(window.innerHeight - 80, rect.bottom + 5) + 'px';
  }

  function _closeFloatingOnOutside(node, anchorEl) {
    setTimeout(() => {
      const off = (ev) => {
        if (!node.contains(ev.target) && ev.target !== anchorEl && !anchorEl.contains(ev.target)) {
          node.remove();
          document.removeEventListener('click', off);
        }
      };
      document.addEventListener('click', off);
    }, 0);
  }

  async function _showContactsDataQuality() {
    try {
      const r = await api('/api/admin/mirror/status');
      if (r.status === 403) {
        toast('Data quality details are available to admins', 'info');
        return;
      }
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const data = await r.json();
      const contactState = (data.state || []).find(s => s.resource === 'contacts');
      const ageMs = Number(contactState?.last_incremental_age_ms);
      const age = Number.isFinite(ageMs)
        ? (ageMs < 60000 ? 'just now' : Math.round(ageMs / 60000) + ' min ago')
        : 'unknown';
      toast(`${Number(data.counts?.contacts || 0).toLocaleString()} contacts · sync ${age}${contactState?.stale ? ' · stale' : ''}`, contactState?.stale ? 'warning' : 'success');
    } catch (err) {
      toast('Data quality unavailable: ' + (err.message || err), 'error');
    }
  }

  function _contactsMenuButton(icon, label, fn, extraClass = '') {
    const btn = el('button', { type: 'button', class: extraClass }, [
      el('i', { 'data-lucide': icon }),
      el('span', { text: label }),
    ]);
    btn.addEventListener('click', () => {
      _closeContactsFloatingUi();
      fn();
    });
    return btn;
  }

  function _openContactsActionsMenu(anchorEl) {
    const existing = document.getElementById('contactsActionsMenu');
    _closeContactsFloatingUi();
    if (existing) return;
    const menu = el('div', { id: 'contactsActionsMenu', class: 'contacts-actions-menu' });
    menu.appendChild(_contactsMenuButton('refresh-cw', 'Refresh contacts', () => refreshContacts()));
    // Export is admin-only (same policy as the toolbar/bulk-bar buttons, which
    // app.css hides by role). This menu item is built in JS with no stable id,
    // so it is gated here instead of in CSS.
    if (_isSalesAdmin()) menu.appendChild(_contactsMenuButton('download', 'Export current page', () => _bulkExportCsv()));
    menu.appendChild(_contactsMenuButton('columns-3', 'Edit columns', () => openContactsColumnsModal()));
    menu.appendChild(_contactsMenuButton('archive', CONTACTS.showArchived ? 'Show active contacts' : 'Show archived contacts', () => {
      document.getElementById('contactsArchivedToggle')?.click();
    }));
    menu.appendChild(_contactsMenuButton('plus', 'Create saved view', () => _openViewModal({ mode: 'new' })));
    document.body.appendChild(menu);
    _placeContactsPopover(menu, anchorEl, { align: 'right', width: 205 });
    refreshIcons();
    _closeFloatingOnOutside(menu, anchorEl);
  }

  function _openContactsObjectMenu(anchorEl) {
    const existing = document.getElementById('contactsObjectMenu');
    _closeContactsFloatingUi();
    if (existing) return;
    const menu = el('div', { id: 'contactsObjectMenu', class: 'contacts-actions-menu contacts-object-menu' });
    menu.appendChild(_contactsMenuButton('users', 'All contacts', () => {
      if (CONTACTS.showArchived) document.getElementById('contactsArchivedToggle')?.click();
      else _clearContactsFilters();
    }));
    menu.appendChild(_contactsMenuButton('archive', 'Archived contacts', () => {
      if (!CONTACTS.showArchived) document.getElementById('contactsArchivedToggle')?.click();
    }));
    document.body.appendChild(menu);
    _placeContactsPopover(menu, anchorEl, { width: 190 });
    refreshIcons();
    _closeFloatingOnOutside(menu, anchorEl);
  }

  function _clearContactsFilters() {
    const owner = document.getElementById('contactsTeamFilter'); if (owner) owner.value = '';
    const status = document.getElementById('contactsStatusFilter'); if (status) status.value = '';
    const search = document.getElementById('contactsSearch'); if (search) search.value = '';
    _setDateFromValue('', 'Created date');
    CONTACTS.advancedGroups = [];
    CONTACTS.advancedFilters = [];
    CONTACTS.lastActivityPreset = '';
    CONTACTS.lastActivityField = '';
    const lastBtn = document.getElementById('contactsLastActivityBtn');
    if (lastBtn) {
      lastBtn.classList.remove('is-active');
      const text = lastBtn.querySelector('span'); if (text) text.textContent = 'Last activity date';
    }
    if (CONTACTS.showArchived) {
      CONTACTS.showArchived = false;
      document.getElementById('contactsArchivedToggle')?.classList.remove('btn-primary');
    }
    _syncContactsQuickFilterState();
    _renderAdvGroups(_CONTACTS_WS);
    _renderAdvSummary(_CONTACTS_WS);
    _checkDirty();
    refreshContacts();
  }

  function _addContactsQuickFilter(anchorEl) {
    _openPropPicker(anchorEl, (chosen) => {
      _advWs = _CONTACTS_WS;
      if (!CONTACTS.advancedGroups.length) CONTACTS.advancedGroups = [[]];
      const def = CONTACTS.propsByName[chosen];
      const op = _opsForField(def)[0] || 'equals';
      const existing = CONTACTS.advancedGroups.flat().find(f => f.field === chosen);
      if (!existing) CONTACTS.advancedGroups[0].push({ field: chosen, op, value: '' });
      _renderAdvGroups(_CONTACTS_WS);
      _renderAdvSummary(_CONTACTS_WS);
      _checkDirty();
      _openAdvPanel(_CONTACTS_WS);
    });
  }

  function _openLastActivityQuickFilter(anchorEl) {
    const existing = document.getElementById('contactsQuickPresetMenu');
    _closeContactsFloatingUi();
    if (existing) return;
    const exact = (CONTACTS.propsCatalog || []).find(p => /last activity date/i.test(p.label || ''));
    const fallback = (CONTACTS.propsCatalog || []).find(p => /last.*(activity|contacted)/i.test((p.label || '') + ' ' + (p.name || '')));
    const def = exact || fallback || CONTACTS.propsByName.hs_lastmodifieddate || { name: 'hs_lastmodifieddate', type: 'datetime' };
    CONTACTS.lastActivityField = def.name;
    const menu = el('div', { id: 'contactsQuickPresetMenu', class: 'contacts-actions-menu' });
    const presets = [
      ['', 'Any time'], ['today', 'Today'], ['this_week', 'This week'],
      ['last_week', 'Last week'], ['this_month', 'This month'], ['last_month', 'Last month'],
    ];
    for (const [value, label] of presets) {
      const btn = el('button', { type: 'button' }, [
        el('i', { 'data-lucide': value === CONTACTS.lastActivityPreset ? 'check' : 'calendar-days' }),
        el('span', { text: label }),
      ]);
      btn.addEventListener('click', () => {
        menu.remove();
        for (const group of CONTACTS.advancedGroups || []) {
          for (let i = group.length - 1; i >= 0; i--) {
            if (group[i].field === def.name || group[i]._quick === 'last_activity') group.splice(i, 1);
          }
        }
        CONTACTS.advancedGroups = (CONTACTS.advancedGroups || []).filter(g => g.length);
        CONTACTS.lastActivityPreset = value;
        if (value) {
          if (!CONTACTS.advancedGroups.length) CONTACTS.advancedGroups = [[]];
          CONTACTS.advancedGroups[0].push({ field: def.name, op: 'is', value, _quick: 'last_activity' });
        }
        const text = anchorEl.querySelector('span'); if (text) text.textContent = value ? label : 'Last activity date';
        anchorEl.classList.toggle('is-active', !!value);
        _renderAdvSummary(_CONTACTS_WS);
        _checkDirty();
        refreshContacts();
      });
      menu.appendChild(btn);
    }
    document.body.appendChild(menu);
    _placeContactsPopover(menu, anchorEl, { width: 190 });
    refreshIcons();
    _closeFloatingOnOutside(menu, anchorEl);
  }

  function _openAllViewsPopover(anchorEl, ws = _CONTACTS_WS) {
    const existing = document.getElementById(ws.ids.allViewsPopover);
    _closeContactsFloatingUi();
    if (existing) return;
    const pop = el('div', { id: ws.ids.allViewsPopover, class: 'contacts-all-views-popover' });
    const searchWrap = el('div', { class: 'contacts-all-views-search' });
    const search = el('input', { type: 'search', placeholder: 'Search views' });
    searchWrap.appendChild(search);
    pop.appendChild(searchWrap);
    const grid = el('div', { class: 'contacts-all-views-grid' });
    pop.appendChild(grid);

    const render = (term = '') => {
      clear(grid);
      const q = term.trim().toLowerCase();
      const views = (ws.state.views || []).filter(v => !q || String(v.name || '').toLowerCase().includes(q));
      const groups = [
        ['Shared views', views.filter(v => v.visibility === 'public')],
        ['My views', views.filter(v => v.ownerId === state.me?.id && v.visibility !== 'public')],
        ['Team views', views.filter(v => v.ownerId !== state.me?.id && v.visibility !== 'public')],
      ];
      for (const [title, items] of groups) {
        const col = el('section', { class: 'contacts-view-group' });
        col.appendChild(el('h4', { text: `${title} (${items.length})` }));
        if (!items.length) col.appendChild(el('div', { class: 'muted small', text: 'No views' }));
        for (const v of items) {
          const b = el('button', { type: 'button', class: ws.state.activeView?.id === v.id ? 'is-active' : '', text: v.name, title: v.name });
          b.addEventListener('click', async () => { pop.remove(); await ws.switchView(v.id); });
          col.appendChild(b);
        }
        grid.appendChild(col);
      }
    };
    render();
    search.addEventListener('input', () => render(search.value));
    const create = el('button', { type: 'button', class: 'contacts-view-create', text: '+ Create new view' });
    create.addEventListener('click', () => { pop.remove(); _openViewModal({ mode: 'new' }, ws); });
    pop.appendChild(create);
    document.body.appendChild(pop);
    _placeContactsPopover(pop, anchorEl, { align: 'right', width: Math.min(760, window.innerWidth - 32) });
    _closeFloatingOnOutside(pop, anchorEl);
    setTimeout(() => search.focus(), 0);
  }

  function _renderContactsLoadingState() {
    _wsRenderSkeleton(document.getElementById('contactsLoading'));
  }
  // Shared loading skeleton for every object workspace grid.
  function _wsRenderSkeleton(loading) {
    if (!loading) return;
    clear(loading);
    for (let r = 0; r < 9; r++) {
      const row = el('div', { class: 'contacts-skeleton-row' });
      for (let c = 0; c < 7; c++) row.appendChild(el('span', { class: 'contacts-skeleton-bar' }));
      loading.appendChild(row);
    }
  }

  function _updateContactsPager() { _wsRenderPager(_CONTACTS_WS); }
  // Shared pager for every object workspace: range note, numbered pages with
  // ellipses, prev/next. `ws.state` carries total / rows / pageIndex / pageSize /
  // loading; `ws.goToPage(n)` performs the navigation.
  function _wsRenderPager(ws) {
    const S = ws.state;
    const total = Number(S.total || 0);
    const count = (S.rows || []).length;
    const start = count ? ((S.pageIndex - 1) * S.pageSize) + 1 : 0;
    const end = count ? start + count - 1 : 0;
    const range = document.getElementById(ws.ids.rangeNote);
    if (range) range.textContent = total ? `${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}` : '0 records';
    const label = document.getElementById(ws.ids.pageLabel);
    if (label) label.textContent = `Page ${S.pageIndex}`;
    const totalPages = Math.max(1, Math.ceil(total / (S.pageSize || 25)));
    const numbers = document.getElementById(ws.ids.pageNumbers);
    if (numbers) {
      clear(numbers);
      const pages = [];
      const add = value => { if (!pages.includes(value)) pages.push(value); };
      add(1);
      for (let p = Math.max(2, S.pageIndex - 2); p <= Math.min(totalPages - 1, S.pageIndex + 2); p++) add(p);
      if (totalPages > 1) add(totalPages);
      pages.sort((a, b) => a - b);
      let previous = 0;
      for (const page of pages) {
        if (previous && page - previous > 1) numbers.appendChild(el('span', { class: 'contacts-page-ellipsis', text: '…' }));
        const active = page === S.pageIndex;
        const btn = el('button', {
          type: 'button',
          class: 'contacts-page-number' + (active ? ' is-active' : ''),
          text: String(page),
          'aria-label': `Page ${page}`,
          ...(S.loading ? { disabled: 'disabled' } : {}),
          ...(active ? { 'aria-current': 'page' } : {}),
        });
        btn.addEventListener('click', () => ws.goToPage(page));
        numbers.appendChild(btn);
        previous = page;
      }
    }
    const prev = document.getElementById(ws.ids.prevBtn);
    const next = document.getElementById(ws.ids.nextBtn);
    if (prev) prev.disabled = S.pageIndex <= 1 || S.loading;
    if (next) next.disabled = S.pageIndex >= totalPages || S.loading;
  }

  async function _contactsGoToPage(targetPage) {
    const totalPages = Math.max(1, Math.ceil(Number(CONTACTS.total || 0) / (CONTACTS.pageSize || 25)));
    if (CONTACTS.loading || targetPage < 1 || targetPage > totalPages || targetPage === CONTACTS.pageIndex) return;
    CONTACTS.pageIndex = targetPage;
    await refreshContacts({ preservePage: true });
    document.getElementById('contactsTableViewport')?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }

  function _contactValueForSort(contact, key) {
    if (key === 'auto_name') return ((contact.firstname || '') + ' ' + (contact.lastname || '')).trim();
    if (key === 'auto_our_stage') return contact.real_pipeline_stage || '';
    if (key === 'auto_mr_call_score') return Number(contact.lead?.metadata?.last_score_global ?? -Infinity);
    if (key === 'auto_owner') return CONTACTS.ownersMap[contact.hubspot_owner_id] || '';
    if (key === 'auto_agent') return contact.agent_name || '';
    if (key === 'auto_activity') return Number(contact.last_activity_at || 0);
    if (key === 'auto_test_level') return contact.lead?.metadata?.level || '';
    const raw = contact[key] !== undefined && contact[key] !== '' ? contact[key] : contact.properties?.[key];
    const n = Number(raw);
    return raw !== '' && raw != null && Number.isFinite(n) ? n : String(raw || '');
  }

  function _sortedContactRows(rows) {
    if (!CONTACTS.sortKey) return rows;
    const dir = CONTACTS.sortDir === 'desc' ? -1 : 1;
    return rows.slice().sort((a, b) => {
      const av = _contactValueForSort(a, CONTACTS.sortKey);
      const bv = _contactValueForSort(b, CONTACTS.sortKey);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }

  function _toggleContactsSort(key) {
    if (CONTACTS.sortKey === key) CONTACTS.sortDir = CONTACTS.sortDir === 'asc' ? 'desc' : 'asc';
    else { CONTACTS.sortKey = key; CONTACTS.sortDir = 'asc'; }
    _renderContactsHead();
    _renderContactsTable(CONTACTS.rows);
  }

  // ---- Bulk selection state (Phase E4) ----
  CONTACTS.selectedIds = new Set();
  CONTACTS.advancedFilters = []; // [{ field, op, value }] — legacy flat path (deprecated)
  CONTACTS.advancedGroups = [];  // Phase E6: [[{ field, op, value }, ...], [...]] — AND in group, OR between

  function _initBulkBarWiring() {
    if (CONTACTS._bulkWired) return;
    CONTACTS._bulkWired = true;
    // Per-row checkboxes — delegated
    document.getElementById('contactsTbody')?.addEventListener('change', (ev) => {
      if (!ev.target.classList.contains('contact-row-check')) return;
      const tr = ev.target.closest('tr[data-contact-id]');
      const id = tr?.getAttribute('data-contact-id');
      if (!id) return;
      if (ev.target.checked) CONTACTS.selectedIds.add(id); else CONTACTS.selectedIds.delete(id);
      tr?.classList.toggle('is-selected', ev.target.checked);
      _renderBulkBar();
    });
    // Header select-all toggles the rows currently rendered
    document.body.addEventListener('change', (ev) => {
      if (ev.target?.id !== 'contactsSelectAll') return;
      const check = ev.target.checked;
      for (const cb of document.querySelectorAll('#contactsTbody .contact-row-check')) {
        cb.checked = check;
        const id = cb.closest('tr[data-contact-id]')?.getAttribute('data-contact-id');
        if (!id) continue;
        if (check) CONTACTS.selectedIds.add(id); else CONTACTS.selectedIds.delete(id);
        cb.closest('tr')?.classList.toggle('is-selected', check);
      }
      _renderBulkBar();
    });
    const clearSelection = () => {
      CONTACTS.selectedIds.clear();
      for (const cb of document.querySelectorAll('.contact-row-check, #contactsSelectAll')) cb.checked = false;
      document.querySelectorAll('#contactsTbody tr.is-selected').forEach(row => row.classList.remove('is-selected'));
      _renderBulkBar();
    };
    document.getElementById('bulkBarClear')?.addEventListener('click', clearSelection);
    document.getElementById('bulkBarCancel')?.addEventListener('click', clearSelection);
    document.getElementById('bulkBarSelectAll')?.addEventListener('click', () => _bulkSelectAllMatching());
    document.getElementById('bulkSendTemplateBtn')?.addEventListener('click', () => _openBulkSendTemplateModal());
    document.getElementById('bulkAssignBtn')?.addEventListener('click', () => _openBulkEditPropertyModal('assign'));
    document.getElementById('bulkEditPropertyBtn')?.addEventListener('click', () => _openBulkEditPropertyModal('edit'));
    document.getElementById('bulkExportCsvBtn')?.addEventListener('click', () => _bulkExportCsv());
    document.getElementById('bulkArchiveBtn')?.addEventListener('click', () => _bulkArchiveContacts());
    document.getElementById('bulkDeleteBtn')?.addEventListener('click', () => _bulkDeleteContacts());
    // Edit-property modal wiring — picker change updates the value control,
    // Apply triggers the bulk PATCH. (Send-template wiring lives in the
    // shared templatesModal — every template card carries its own button.)
    document.getElementById('bulkEditPropPicker')?.addEventListener('change', () => _renderBulkEditValue());
    document.getElementById('bulkEditApplyBtn')?.addEventListener('click', () => _bulkEditPropertyApply());
  }

  function _renderBulkBar() {
    const bar = document.getElementById('contactsBulkBar');
    if (!bar) return;
    const n = CONTACTS.selectedIds.size;
    bar.classList.toggle('hidden', n === 0);
    document.getElementById('bulkBarCount').textContent = String(n);
    // Assign / Edit / Delete are sales-admin only (admin · super_admin ·
    // super_sales). Re-evaluated on every render because state.me lands after
    // the first paint — the buttons ship `hidden` in the markup so an agent
    // never sees them, not even briefly, if this runs before /api/me resolves.
    const salesAdmin = (typeof _isSalesAdmin === 'function') ? _isSalesAdmin() : false;
    for (const id of ['bulkAssignBtn', 'bulkEditPropertyBtn', 'bulkDeleteBtn']) {
      document.getElementById(id)?.classList.toggle('hidden', !salesAdmin);
    }
  }

  // ─── Bulk: Send template ─────────────────────────────────────────────────
  // Reuses the existing inbox templatesModal (stage pill tabs + search +
  // template cards) with target='bulk-contacts' so the design stays 100%
  // consistent with the chat-side picker. Each card grows a primary green
  // "Send to N" button that fires one POST → /api/hubspot/contacts/bulk-
  // send-template, which loops contacts server-side and resolves {{name}} =
  // firstname per recipient.
  // Recipients per send — MUST match BULK_SEND_MAX in lib/bulk-send-roles.js
  // (a test asserts the two agree). Checked here as well as server-side so
  // "Select all matching" tells the agent to narrow the filter instead of
  // letting them pick a template, confirm, and only then collect a 400.
  const BULK_SEND_MAX = 100;
  function _bulkTooMany(n) {
    if (n <= BULK_SEND_MAX) return false;
    toast(`Max ${BULK_SEND_MAX} contacts per send — ${n} selected. Narrow the filter or send in batches.`, 'warning');
    return true;
  }

  function _openBulkSendTemplateModal() {
    const n = CONTACTS.selectedIds.size;
    if (!n) { toast('Select at least one contact', 'warning'); return; }
    if (_bulkTooMany(n)) return;
    openTemplatesModal('bulk-contacts');
  }

  async function _bulkSendTemplateById(templateId) {
    const ids = Array.from(CONTACTS.selectedIds);
    if (!ids.length) { toast('Select at least one contact', 'warning'); return; }
    if (!templateId) return;
    const tpl = (state.templatesCache || []).find(t => t.id === templateId);
    const label = tpl?.name || 'this template';
    if (!confirm(`Send "${label}" to ${ids.length} contact${ids.length === 1 ? '' : 's'}?`)) return;
    try {
      const r = await api('/api/hubspot/contacts/bulk-send-template', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, templateId }),
        // No watchdog: the server loops real WhatsApp sends — an abort here
        // would fake "Bulk send failed" while messages keep going out.
        timeoutMs: 0,
      });
      const d = await r.json();
      if (!r.ok) { toast('Bulk send failed: ' + (d.error || ('HTTP ' + r.status)), 'error'); return; }
      const failedCount = (d.failed || []).length;
      const okCount = d.succeeded || 0;
      toast(`Sent ${okCount} ok${failedCount ? ` · ${failedCount} failed` : ''}`, failedCount ? 'warning' : 'success');
      closeModal('templatesModal');
      CONTACTS.selectedIds.clear();
      for (const cb of document.querySelectorAll('.contact-row-check, #contactsSelectAll')) cb.checked = false;
      _renderBulkBar();
    } catch (err) {
      toast('Bulk send failed: ' + (err.message || err), 'error');
    }
  }

  // SMS sibling of _bulkSendTemplateById — fires ONE POST that ENQUEUES the
  // sms-channel template per selected contact ({{vars}} resolved server-side).
  // Nothing sends synchronously: the office bridge drains the queue through
  // the per-SIM-port throttle (~1 SMS / 2 min per SIM), so the confirm dialog
  // sets expectations about pacing before the agent commits.
  async function _bulkSendSmsTemplateById(templateId, btn) {
    const ids = Array.from(CONTACTS.selectedIds);
    if (!ids.length) { toast('Select at least one contact', 'warning'); return; }
    if (_bulkTooMany(ids.length)) return;
    if (!templateId) return;
    const tpl = (state.templatesCache || []).find(t => t.id === templateId);
    const label = tpl?.name || 'this template';
    const est = Math.ceil(ids.length * 2); // ~1 SMS / 2 min per SIM (server default)
    if (!confirm(`Envoyer "${label}" par SMS à ${ids.length} contact${ids.length === 1 ? '' : 's'} ?\n\nEnvoi progressif anti-blocage : 1 SMS toutes les 2 minutes par carte SIM (durée estimée ~${est} min). Les messages restent "en file d'attente" jusqu'à leur envoi.`)) return;
    // In-flight guard: the server resolves vars per contact, so a large batch
    // can take a while — a re-click mid-flight would enqueue the blast TWICE.
    if (btn) btn.disabled = true;
    try {
      const r = await api('/api/sms/bulk-send-template', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, templateId }),
        // No watchdog: the enqueue loop walks the mirror + resolves {{vars}}
        // per contact server-side — an abort here would fake "échoué" while
        // the rows keep queueing, and an agent retry would double-send the
        // whole blast to real clients. Same rationale as _bulkSendTemplateById.
        timeoutMs: 0,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast('Envoi SMS échoué : ' + (d.error || ('HTTP ' + r.status)), 'error');
        if (btn) btn.disabled = false;
        return;
      }
      const failedCount = (d.failed || []).length;
      toast(
        `${d.queued || 0} SMS en file d'attente (~${d.estimatedMinutes ?? est} min)` + (failedCount ? ` · ${failedCount} échec${failedCount === 1 ? '' : 's'}` : ''),
        failedCount ? 'warning' : 'success'
      );
      closeModal('templatesModal');
      CONTACTS.selectedIds.clear();
      for (const cb of document.querySelectorAll('.contact-row-check, #contactsSelectAll')) cb.checked = false;
      _renderBulkBar();
    } catch (err) {
      toast('Envoi SMS échoué : ' + (err.message || err), 'error');
      if (btn) btn.disabled = false;
    }
  }

  // Global archive/unarchive of the selected contacts (admin only — the server
  // enforces requireAdmin). Archiving hides them from the Contacts list for
  // everyone; in the Archived view the same button unarchives.
  async function _bulkArchiveContacts() {
    const ids = Array.from(CONTACTS.selectedIds);
    if (!ids.length) { toast('Select at least one contact', 'warning'); return; }
    const unarchiving = !!CONTACTS.showArchived;
    const verb = unarchiving ? 'Unarchive' : 'Archive';
    const extra = unarchiving ? '' : '\nThey will be hidden from the Contacts list for everyone (restore from the Archived view).';
    if (!confirm(`${verb} ${ids.length} contact${ids.length === 1 ? '' : 's'}?${extra}`)) return;
    try {
      const r = await api('/api/admin/contacts/archive', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, archived: !unarchiving }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast((d.error || 'Archive failed') + (r.status === 403 ? ' — admin only' : ''), 'error'); return; }
      toast(`${unarchiving ? 'Unarchived' : 'Archived'} ${d.updated ?? ids.length}`, 'success');
      CONTACTS.selectedIds.clear();
      for (const cb of document.querySelectorAll('.contact-row-check, #contactsSelectAll')) cb.checked = false;
      _renderBulkBar();
      refreshContacts();
    } catch (e) {
      toast('Archive failed: ' + (e.message || e), 'error');
    }
  }

  // ─── Add-contact modal (2026-05-25) ─────────────────────────────────────
  // Acquisition source is mandatory. "Manual" is only the intake method and
  // is stored server-side in metadata; it is never sent as leads.source.
  function _syncAddContactSourceDetail() {
    const source = String(document.getElementById('addContactSource')?.value || '');
    const group = document.getElementById('addContactSourceDetailGroup');
    const label = document.getElementById('addContactSourceDetailLabel');
    const input = document.getElementById('addContactSourceDetail');
    const error = document.getElementById('addContactError');
    const show = source === 'client_referral' || source === 'other';
    if (source) error?.classList.add('hidden');
    group?.classList.toggle('hidden', !show);
    if (label) label.textContent = source === 'client_referral' ? 'Who referred this contact?' : 'Explain the source *';
    if (input) {
      input.placeholder = source === 'client_referral' ? 'e.g. referred by Ahmed' : 'e.g. event name or partner';
      input.required = source === 'other';
      if (!show) input.value = '';
    }
  }

  function _openAddContactModal() {
    // Reset form
    document.getElementById('addContactName').value = '';
    document.getElementById('addContactPhone').value = '';
    document.getElementById('addContactAge').value = '';
    document.getElementById('addContactSource').value = '';
    document.getElementById('addContactSourceDetail').value = '';
    _syncAddContactSourceDetail();
    document.getElementById('addContactError').classList.add('hidden');
    _clearAddContactDupChoice();
    document.getElementById('addContactSubmitBtn').disabled = false;
    document.getElementById('addContactModal').classList.remove('hidden');
    refreshIcons();
    setTimeout(() => document.getElementById('addContactName')?.focus(), 50);
  }

  async function _submitAddContact() {
    const errEl = document.getElementById('addContactError');
    const submitBtn = document.getElementById('addContactSubmitBtn');
    const showErr = (msg) => { errEl.textContent = msg; errEl.classList.remove('hidden'); };
    // Wipe any previous shared-number choice UI from a prior submit.
    _clearAddContactDupChoice();

    const name = String(document.getElementById('addContactName').value || '').trim();
    const phoneRaw = String(document.getElementById('addContactPhone').value || '').trim();
    const ageRaw = String(document.getElementById('addContactAge').value || '').trim();
    const source = String(document.getElementById('addContactSource').value || '').trim();
    const sourceDetail = String(document.getElementById('addContactSourceDetail').value || '').trim();

    if (!name) return showErr('Name is required.');
    if (!source) return showErr('Source is required — choose where this contact came from.');
    if (source === 'other' && !sourceDetail) return showErr('Please explain the source when choosing Other.');
    const phoneDigits = phoneRaw.replace(/\D/g, '');
    if (phoneDigits.length < 8) return showErr('Phone number looks invalid (need at least 8 digits).');
    let age = null;
    if (ageRaw) {
      age = Number(ageRaw);
      if (!Number.isFinite(age) || age < 1 || age > 120) return showErr('Age must be a number between 1 and 120.');
    }

    // Split full name into first + last on first whitespace so HubSpot's
    // firstname / lastname columns get sensible values without forcing the
    // agent to fill two fields. "Sara El Amrani" → first="Sara", last="El Amrani".
    const { firstname, lastname } = _splitContactFullName(name);

    // The intake payload is reused verbatim for the "contact séparé" re-POST
    // (just with allowDuplicate:true) so the new lead carries the same name /
    // age / source the agent already typed.
    const intakePayload = {
      firstname,
      lastname,
      phone: phoneDigits,
      source,
      source_detail: sourceDetail,
      age: age != null ? String(age) : '',
    };
    await _doAddContactIntake(intakePayload, false);
  }

  // Single POST → /api/leads/intake. `allowDuplicate` (Task B1) forces a brand
  // new lead+contact even when the number already exists (parent + child on one
  // WhatsApp number). On a plain `returning:true` response we surface a French
  // choice instead of silently re-using the existing contact.
  async function _doAddContactIntake(payload, allowDuplicate) {
    const errEl = document.getElementById('addContactError');
    const submitBtn = document.getElementById('addContactSubmitBtn');
    const showErr = (msg) => { errEl.textContent = msg; errEl.classList.remove('hidden'); };
    submitBtn.disabled = true;
    errEl.classList.add('hidden');
    try {
      const r = await api('/api/leads/intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(allowDuplicate ? { ...payload, allowDuplicate: true } : payload),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok) {
        // Migration not applied yet → surface the server's own message instead
        // of a bare "HTTP 409" so the agent knows it's a backend-not-ready case.
        if (r.status === 409 && j?.error === 'duplicate_pending_migration') {
          showErr(j?.message || "Contact séparé indisponible — migration en attente.");
          _clearAddContactDupChoice();
          submitBtn.disabled = false;
          return;
        }
        // The hubspot-create entry point answers an existing number with a 409
        // carrying `existing:{id,name}` instead of `returning:true`. Treat it
        // the same way: offer keep-existing / create-separate (never a dead end).
        if (r.status === 409 && j?.existing && !allowDuplicate) {
          _renderAddContactDupChoice(payload, j);
          submitBtn.disabled = false;
          return;
        }
        showErr(j?.message || j?.error || ('HTTP ' + r.status));
        submitBtn.disabled = false;
        return;
      }

      // Existing number + we didn't force a separate contact → let the agent
      // choose: keep the existing one (the legacy behaviour) or create a
      // separate contact on the SAME number (parent + child case).
      const returning = !!(j?.returning || j?.returningClient);
      if (returning && !allowDuplicate && !j?.separate) {
        _renderAddContactDupChoice(payload, j);
        submitBtn.disabled = false;
        return;
      }

      _afterAddContactSuccess(payload.phone, j, { separate: allowDuplicate || !!j?.separate });
    } catch (err) {
      showErr('Network error: ' + (err.message || err));
      submitBtn.disabled = false;
    }
  }

  // Shared success path: close the modal, drop the date/status filters so the
  // new row is visible, toast, and refresh the table highlighting the phone.
  function _afterAddContactSuccess(phoneDigits, j, { separate = false } = {}) {
    _clearAddContactDupChoice();
    closeModal('addContactModal');

    // Make the just-added contact VISIBLE: the default Contacts view scopes to
    // "this month" + a status quick-filter, which would hide a returning client
    // (old createdate) or a status that doesn't match the filter. Drop both
    // restrictions before refreshing so the new row is guaranteed to be in the
    // result set. Reuse the existing date-filter helper (sets the hidden value
    // AND the visible button label) and clear the status quick-filter select.
    _setDateFromValue('', 'All time');
    const statusFilterEl = document.getElementById('contactsStatusFilter');
    if (statusFilterEl) statusFilterEl.value = '';
    _syncContactsQuickFilterState();

    // Toast feedback so the agent isn't left guessing where the contact went.
    const assignedTo = j?.assignedTo || '';
    const isMine = assignedTo && assignedTo === state.me?.username;
    const returning = !!(j?.returning || j?.returningClient);
    if (separate) {
      toast('Contact séparé créé sur le même numéro'
        + (assignedTo ? (isMine ? ' — dans vos contacts' : ' — assigné à @' + userLabel(assignedTo)) : ''), '');
    } else if (returning && isMine) {
      toast('Contact existant — réassigné à vous', '');
    } else if (isMine) {
      toast('Contact ajouté — il est dans vos contacts', '');
    } else if (j?.ownerlessCreator) {
      toast("Contact ajouté — votre compte n'a pas de mapping HubSpot, voir admin"
        + (assignedTo ? ' (assigné à @' + userLabel(assignedTo) + ')' : ''), '');
    } else if (assignedTo) {
      toast('Contact ajouté — assigné à @' + userLabel(assignedTo), '');
    } else {
      toast('Contact ajouté', '');
    }

    // Refresh the contacts table so the new row appears (now under All-time +
    // no status filter). Remember the phone so we can scroll-to/highlight it.
    CONTACTS._highlightPhone = phoneDigits;
    if (typeof refreshContacts === 'function') refreshContacts();
  }

  // Inline (non-blocking) choice shown in the Add-contact modal when the typed
  // number already belongs to a contact. Two actions: keep the existing one
  // (legacy behaviour — surface it in the table) or create a SEPARATE contact
  // on the same number (re-POST with allowDuplicate:true). French copy.
  function _renderAddContactDupChoice(payload, j) {
    const errEl = document.getElementById('addContactError');
    if (errEl) errEl.classList.add('hidden');
    _clearAddContactDupChoice();
    const body = document.querySelector('#addContactModal .modal-body');
    if (!body) return;
    // Intake returns `existingName`; the hubspot-create endpoint (other entry
    // point in the contract) returns `existing:{id,name}`. Read both shapes.
    const existingName = (j?.existingName || j?.existing?.name || j?.contact?.name || '').trim();
    const wrap = el('div', { class: 'numswitch-dupchoice', id: 'addContactDupChoice' }, [
      el('p', { class: 'numswitch-dupchoice-msg', text:
        'Ce numéro a déjà un contact' + (existingName ? ' (' + existingName + ')' : '') + '.' }),
      el('div', { class: 'numswitch-dupchoice-actions' }, [
        el('button', {
          class: 'btn btn-ghost numswitch-dupchoice-keep', type: 'button',
          onclick: () => _afterAddContactSuccess(payload.phone, j, { separate: false }),
        }, ['Garder l’existant']),
        el('button', {
          class: 'btn btn-primary numswitch-dupchoice-sep', type: 'button',
          onclick: () => _doAddContactIntake(payload, true),
        }, ['Créer un contact séparé']),
      ]),
    ]);
    body.appendChild(wrap);
  }

  function _clearAddContactDupChoice() {
    document.getElementById('addContactDupChoice')?.remove();
  }

  // ─── Bulk: Edit property modal ───────────────────────────────────────────
  // Single picker: pick a property from the curated whitelist (matches the
  // server-side BULK_EDITABLE_PROPS set), then either a select (status,
  // lifecycle, owner) or a free-text input (notes, program name, payment
  // status). Apply → POST /api/hubspot/contacts/bulk with action=set_property.
  const BULK_EDIT_PROPS_META = {
    hs_lead_status: { label: 'Lead status', valueKind: 'select-status' },
    lifecyclestage: { label: 'Lifecycle stage', valueKind: 'select', options: [
      ['subscriber', 'Subscriber'], ['lead', 'Lead'], ['marketingqualifiedlead', 'MQL'],
      ['salesqualifiedlead', 'SQL'], ['opportunity', 'Opportunity'], ['customer', 'Customer'],
      ['evangelist', 'Evangelist'], ['other', 'Other'],
    ] },
    hubspot_owner_id: { label: 'Owner', valueKind: 'select-owner' },
    agent_note: { label: 'Agent note', valueKind: 'text' },
    program_name: { label: 'Program name', valueKind: 'text' },
    payment_status: { label: 'Payment status', valueKind: 'text' },
  };

  // mode 'edit'   → the full property picker (status, lifecycle, owner, notes…).
  // mode 'assign' → the SAME modal locked to Owner: the property row is hidden
  //                 and the owner dropdown is pre-rendered, so "Assign" is one
  //                 pick + Apply instead of two. Both write through the same
  //                 POST /api/hubspot/contacts/bulk, which reassigns via the
  //                 core owner path (chat + lead acknowledgement included).
  function _openBulkEditPropertyModal(mode = 'edit') {
    const n = CONTACTS.selectedIds.size;
    if (!n) { toast('Select at least one contact', 'warning'); return; }
    if (!_isSalesAdmin()) { toast('Admin / Super Sales only', 'error'); return; }
    const assign = mode === 'assign';
    const cnt = document.getElementById('bulkEditCount');
    if (cnt) cnt.textContent = String(n);
    document.getElementById('bulkEditCount2').textContent = String(n);
    const picker = document.getElementById('bulkEditPropPicker');
    if (picker) picker.value = assign ? 'hubspot_owner_id' : '';
    document.getElementById('bulkEditValueInput').classList.add('hidden');
    document.getElementById('bulkEditValueInput').value = '';
    document.getElementById('bulkEditValueSelect').classList.add('hidden');
    document.getElementById('bulkEditHint').textContent = 'Pick a property to begin.';
    document.getElementById('bulkEditApplyBtn').disabled = true;
    if (picker) {
      const ownerOpt = [...picker.options].find(o => o.value === 'hubspot_owner_id');
      if (ownerOpt) ownerOpt.hidden = false; // gate above already proved sales-admin
    }
    document.getElementById('bulkEditPropRow')?.classList.toggle('hidden', assign);
    const title = document.getElementById('bulkEditTitle');
    if (title) {
      title.innerHTML = assign
        ? `<i data-lucide="arrow-right"></i> Assign <span id="bulkEditCount">${n}</span> contact${n === 1 ? '' : 's'} to an owner`
        : `<i data-lucide="square-pen"></i> Edit property on <span id="bulkEditCount">${n}</span> contacts`;
    }
    // Assign mode renders the owner dropdown immediately — there is no picker
    // `change` event to trigger it when the property row is hidden.
    if (assign) _renderBulkEditValue();
    document.getElementById('bulkEditPropertyModal').classList.remove('hidden');
    refreshIcons();
  }

  // ─── Bulk: Delete (sales-admin only) ─────────────────────────────────────
  // HubSpot's own list-view Delete: the contacts move to the HubSpot recycle
  // bin (restorable there for 90 days) and drop out of Leads7 at once via the
  // mirror's removed_at. Deliberately NOT the same thing as Archive, which
  // only hides them locally. Two gates before anything leaves: a plain confirm
  // and a typed SUPPRIMER, because there is no undo inside Leads7.
  async function _bulkDeleteContacts() {
    const ids = Array.from(CONTACTS.selectedIds);
    if (!ids.length) { toast('Select at least one contact', 'warning'); return; }
    if (!_isSalesAdmin()) { toast('Admin / Super Sales only', 'error'); return; }
    // Server caps at 200 per call and this flow sends ONE call — no chunking,
    // so a 5k "select all matching" can't turn into a 25-call delete storm.
    if (ids.length > 200) {
      toast(`Delete is capped at 200 contacts per action (${ids.length} selected) — narrow the filter first`, 'warning');
      return;
    }
    const label = `${ids.length} contact${ids.length === 1 ? '' : 's'}`;
    if (!confirm(`Delete ${label}?\n\nThey move to the HubSpot recycle bin (restorable there for 90 days) and disappear from Leads7 immediately.\n\nTo only hide them from the list, use Archive instead.`)) return;
    const typed = prompt(`This cannot be undone from Leads7.\n\nType SUPPRIMER to delete ${label}.`);
    if (String(typed || '').trim().toUpperCase() !== 'SUPPRIMER') { toast('Delete cancelled', 'info'); return; }
    const btn = document.getElementById('bulkDeleteBtn');
    if (btn) btn.disabled = true;
    try {
      const r = await api('/api/hubspot/contacts/bulk-delete', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
        timeoutMs: 300000, // 200 sequential HubSpot DELETEs outlive the 60s default
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast('Delete failed: ' + (d.error || ('HTTP ' + r.status)) + (r.status === 403 ? ' — admin / super sales only' : ''), 'error');
        return;
      }
      const failedCount = (d.failed || []).length;
      toast(`Deleted ${d.succeeded || 0}${failedCount ? ` · ${failedCount} failed` : ''}`, failedCount ? 'warning' : 'success');
      CONTACTS.selectedIds.clear();
      for (const cb of document.querySelectorAll('.contact-row-check, #contactsSelectAll')) cb.checked = false;
      _renderBulkBar();
      await refreshContacts();
    } catch (err) {
      toast('Delete failed: ' + (err.message || err), 'error');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  function _renderBulkEditValue() {
    const prop = document.getElementById('bulkEditPropPicker').value;
    const inputEl = document.getElementById('bulkEditValueInput');
    const selectEl = document.getElementById('bulkEditValueSelect');
    const hint = document.getElementById('bulkEditHint');
    const applyBtn = document.getElementById('bulkEditApplyBtn');
    inputEl.classList.add('hidden');
    selectEl.classList.add('hidden');
    inputEl.value = '';
    clear(selectEl);
    selectEl.appendChild(el('option', { value: '', text: '— pick —' }));
    if (!prop) {
      hint.textContent = 'Pick a property to begin.';
      applyBtn.disabled = true;
      return;
    }
    const meta = BULK_EDIT_PROPS_META[prop];
    if (!meta) {
      hint.textContent = 'Property not supported.';
      applyBtn.disabled = true;
      return;
    }
    hint.textContent = `Will overwrite ${meta.label} on every selected contact.`;
    if (meta.valueKind === 'text') {
      inputEl.classList.remove('hidden');
      inputEl.focus();
    } else if (meta.valueKind === 'select') {
      for (const [v, l] of meta.options) selectEl.appendChild(el('option', { value: v, text: l }));
      selectEl.classList.remove('hidden');
    } else if (meta.valueKind === 'select-status') {
      const statuses = (CONTACTS.distinctStatuses || []).map(s => s.value).filter(Boolean);
      for (const s of statuses) selectEl.appendChild(el('option', { value: s, text: s }));
      selectEl.classList.remove('hidden');
    } else if (meta.valueKind === 'select-owner') {
      const mappedOwnerIds = new Set((state.assignableUsers || [])
        .map(u => String(u.hubspotOwnerId || ''))
        .filter(Boolean));
      const owners = Object.entries(CONTACTS.ownersMap || {})
        .filter(([id]) => mappedOwnerIds.has(String(id)))
        .sort((a, b) => a[1].localeCompare(b[1]));
      for (const [id, name] of owners) selectEl.appendChild(el('option', { value: id, text: name }));
      selectEl.classList.remove('hidden');
    }
    applyBtn.disabled = false;
  }

  async function _bulkEditPropertyApply() {
    const prop = document.getElementById('bulkEditPropPicker').value;
    if (!prop) { toast('Pick a property first', 'warning'); return; }
    const meta = BULK_EDIT_PROPS_META[prop];
    let value;
    if (meta?.valueKind === 'text') {
      value = document.getElementById('bulkEditValueInput').value.trim();
    } else {
      value = document.getElementById('bulkEditValueSelect').value;
    }
    if (value === '' || value === undefined) { toast('Type or pick a value first', 'warning'); return; }
    const ids = Array.from(CONTACTS.selectedIds);
    if (!ids.length) return;
    if (!confirm(`Set ${meta.label} = "${value}" on ${ids.length} contacts?`)) return;
    const applyBtn = document.getElementById('bulkEditApplyBtn');
    applyBtn.disabled = true;
    try {
      let totalSuccess = 0, totalFail = 0;
      for (let i = 0; i < ids.length; i += 500) {
        const chunk = ids.slice(i, i + 500);
        const r = await api('/api/hubspot/contacts/bulk', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: chunk, action: 'set_property', propertyName: prop, value }),
          timeoutMs: 300000, // 500-contact HubSpot write chunk can outlive the 60s default
        });
        const d = await r.json();
        if (!r.ok) {
          toast('Bulk edit failed: ' + (d.error || ('HTTP ' + r.status)), 'error');
          applyBtn.disabled = false;
          return;
        }
        totalSuccess += d.succeeded || 0;
        totalFail += (d.failed || []).length;
      }
      toast(`Updated ${totalSuccess} ok${totalFail ? ` · ${totalFail} failed` : ''}`, totalFail ? 'warning' : 'success');
      CONTACTS.selectedIds.clear();
      for (const cb of document.querySelectorAll('.contact-row-check, #contactsSelectAll')) cb.checked = false;
      _renderBulkBar();
      document.getElementById('bulkEditPropertyModal').classList.add('hidden');
      await refreshContacts();
    } catch (err) {
      toast('Bulk edit failed: ' + (err.message || err), 'error');
      applyBtn.disabled = false;
    }
  }

  async function _bulkSelectAllMatching() {
    const params = _contactsFilterParams();
    params.delete('properties');
    params.delete('limit');
    params.delete('after');
    try {
      const r = await api('/api/hubspot/contacts/ids?' + params.toString());
      if (!r.ok) { toast('Select-all failed', 'error'); return; }
      const d = await r.json();
      for (const id of d.ids || []) CONTACTS.selectedIds.add(id);
      // Update visible checkboxes
      for (const cb of document.querySelectorAll('#contactsTbody .contact-row-check')) {
        const id = cb.closest('tr[data-contact-id]')?.getAttribute('data-contact-id');
        if (id && CONTACTS.selectedIds.has(id)) cb.checked = true;
      }
      _renderBulkBar();
      toast(`Selected ${d.ids?.length || 0} matching contacts`, 'success');
    } catch (err) { toast('Select-all failed: ' + (err.message || err), 'error'); }
  }

  async function _bulkApply(action, value) {
    if (!value) { toast('Pick a value first', 'warning'); return; }
    const ids = Array.from(CONTACTS.selectedIds);
    if (!ids.length) return;
    if (!confirm(`Apply ${action} = "${value}" to ${ids.length} contacts?`)) return;
    try {
      // Chunk to 500 per call (server limit)
      let totalSuccess = 0, totalFail = 0;
      for (let i = 0; i < ids.length; i += 500) {
        const chunk = ids.slice(i, i + 500);
        const r = await api('/api/hubspot/contacts/bulk', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: chunk, action, value }),
          timeoutMs: 300000, // 500-contact HubSpot write chunk can outlive the 60s default
        });
        const d = await r.json();
        totalSuccess += d.succeeded || 0;
        totalFail += (d.failed || []).length;
      }
      toast(`Bulk ${action}: ${totalSuccess} ok / ${totalFail} failed`, totalFail ? 'warning' : 'success');
      CONTACTS.selectedIds.clear();
      _renderBulkBar();
      await refreshContacts();
    } catch (err) { toast('Bulk apply failed: ' + (err.message || err), 'error'); }
  }

  function _bulkExportCsv() {
    // Admin-only. CSS hides the button for agents (body:not(.role-admin)…)
    // but a defensive guard here keeps the export safe even if the button
    // is un-hidden via DevTools or the role class lags the click handler.
    if (!_isSalesAdmin()) {
      toast('Export CSV is admin-only', 'error');
      return;
    }
    const ids = CONTACTS.selectedIds.size ? CONTACTS.selectedIds : null;
    // Export the currently rendered rows (matching view's columns)
    const rows = ids ? CONTACTS.rows.filter(r => ids.has(r.id)) : CONTACTS.rows;
    if (!rows.length) { toast('Nothing to export', 'warning'); return; }
    const cols = CONTACTS.columns || [];
    const header = ['id', 'name', ...cols].map(c => `"${String(c).replace(/"/g, '""')}"`).join(',');
    const lines = rows.map(r => {
      const name = ((r.firstname || '') + ' ' + (r.lastname || '')).trim();
      const cells = [r.id, name, ...cols.map(c => {
        const v = r[c] !== undefined && r[c] !== '' ? r[c] : (r.properties?.[c] ?? '');
        return String(v ?? '').replace(/"/g, '""');
      })];
      return cells.map(v => `"${v}"`).join(',');
    });
    const csv = header + '\n' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = el('a', { href: url, download: `contacts-${new Date().toISOString().slice(0, 10)}.csv`, style: 'display:none;' });
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast(`Exported ${rows.length} contacts`, 'success');
  }

  // Helper: extract current filter values as URLSearchParams (reused by ids endpoint)
  function _contactsFilterParams() {
    const params = new URLSearchParams();
    const ownerId = (document.getElementById('contactsTeamFilter')?.value || '').trim();
    const stage = (document.getElementById('contactsStatusFilter')?.value || '').trim();
    const q = (document.getElementById('contactsSearch')?.value || '').trim();
    const dateFrom = (document.getElementById('contactsDateFrom')?.value || '').trim();
    if (ownerId) params.set('ownerId', ownerId);
    if (stage) params.set('stage', stage);
    if (q) params.set('q', q);
    if (CONTACTS.showArchived) params.set('archived', '1');
    // Keep "Select all {total}" on the same rows the list is showing.
    if (CONTACTS.phoneFixOnly) params.set('phoneFix', '1');
    if (CONTACTS.phoneStatus) params.set('phoneStatus', CONTACTS.phoneStatus);
    // Phase E6.6: same translation as refreshContacts
    const semanticPresets = new Set(['today','yesterday','tomorrow','this_week','last_week','next_week','this_month','last_month','next_month','this_quarter','this_year','last_year']);
    let injectedDatePreset = null;
    if (!dateFrom) {
      params.set('createdAfter', 'all');
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateFrom)) {
      params.set('createdAfter', dateFrom + 'T00:00:00Z');
    } else if (semanticPresets.has(dateFrom)) {
      params.set('createdAfter', 'all');
      injectedDatePreset = dateFrom;
    } else {
      params.set('createdAfter', 'all');
    }
    let cleanGroups = (CONTACTS.advancedGroups || [])
      .map(g => g.filter(f => f && f.field && f.op))
      .filter(g => g.length);
    if (injectedDatePreset) {
      const synthetic = { field: 'createdate', op: 'is', value: injectedDatePreset };
      cleanGroups = cleanGroups.length ? cleanGroups.map(g => [...g, synthetic]) : [[synthetic]];
    }
    if (cleanGroups.length) params.set('adv', JSON.stringify(cleanGroups));
    // Legacy flat
    for (const f of CONTACTS.advancedFilters || []) {
      if (f.field && f.op) params.append('df', `${f.field}:${f.op}:${f.value || ''}`);
    }
    return params;
  }

  // ---- Advanced filter side-panel (Phase E6) ----
  function _initAdvancedFilterWiring() {
    if (CONTACTS._advWired) return;
    CONTACTS._advWired = true;
    document.getElementById('contactsAdvancedToggle')?.addEventListener('click', () => _openAdvPanel(_CONTACTS_WS));
    document.getElementById('contactsArchivedToggle')?.addEventListener('click', () => {
      CONTACTS.showArchived = !CONTACTS.showArchived;
      const tg = document.getElementById('contactsArchivedToggle');
      if (tg) tg.classList.toggle('btn-primary', CONTACTS.showArchived);
      // Flip the bulk action label so the same button unarchives in this view.
      const ab = document.getElementById('bulkArchiveBtn');
      if (ab) ab.innerHTML = CONTACTS.showArchived ? '<i data-lucide="archive-restore"></i> Unarchive' : '<i data-lucide="archive"></i> Archive';
      if (window.lucide?.createIcons) { try { window.lucide.createIcons(); } catch {} }
      CONTACTS.selectedIds.clear();
      for (const cb of document.querySelectorAll('.contact-row-check, #contactsSelectAll')) cb.checked = false;
      _renderBulkBar();
      refreshContacts();
    });
    // "Numéro à corriger" — narrows the list to contacts whose number is not
    // dialable. Intersects with the Team/Status filters server-side, so an agent
    // sees only their own; nothing here changes ownership.
    document.getElementById('contactsPhoneFixToggle')?.addEventListener('click', () => {
      CONTACTS.phoneFixOnly = !CONTACTS.phoneFixOnly;
      document.getElementById('contactsPhoneFixToggle')?.classList.toggle('btn-primary', CONTACTS.phoneFixOnly);
      CONTACTS.selectedIds.clear();
      for (const cb of document.querySelectorAll('.contact-row-check, #contactsSelectAll')) cb.checked = false;
      _renderBulkBar();
      refreshContacts();
    });
    _wireAdvPanelOnce();
  }

  // The advanced-filter drawer (#contactsAdvPanel) is ONE shared DOM node used
  // by every object workspace; `_advWs` is the workspace currently driving it.
  let _advWs = _CONTACTS_WS;
  let _advPanelWired = false;
  function _wireAdvPanelOnce() {
    if (_advPanelWired) return;
    _advPanelWired = true;
    document.getElementById('advPanelBackdrop')?.addEventListener('click', () => _closeAdvPanel());
    document.getElementById('advPanelCloseBtn')?.addEventListener('click', () => _closeAdvPanel());
    document.getElementById('advPanelApplyBtn')?.addEventListener('click', () => { const ws = _advWs; _closeAdvPanel(ws); _renderAdvSummary(ws); ws.checkDirty(); ws.refresh(); });
    document.getElementById('advPanelClearBtn')?.addEventListener('click', () => { const ws = _advWs; ws.state.advancedGroups = []; _renderAdvGroups(ws); _renderAdvSummary(ws); ws.checkDirty(); });
    document.getElementById('advAddGroupBtn')?.addEventListener('click', () => { const ws = _advWs; ws.state.advancedGroups.push([{ field: '', op: 'equals', value: '' }]); _renderAdvGroups(ws); });
  }

  function _openAdvPanel(ws = _advWs) {
    _advWs = ws;
    _wireAdvPanelOnce();
    if (!ws.state.advancedGroups) ws.state.advancedGroups = [];
    if (!ws.state.advancedGroups.length) ws.state.advancedGroups = [[{ field: '', op: 'equals', value: '' }]];
    document.getElementById('contactsAdvPanel')?.classList.remove('hidden');
    document.getElementById(ws.ids.advToggle)?.setAttribute('aria-expanded', 'true');
    _renderAdvGroups(ws);
    refreshIcons();
  }

  function _closeAdvPanel(ws = _advWs) {
    document.getElementById('contactsAdvPanel')?.classList.add('hidden');
    document.getElementById(ws.ids.advToggle)?.setAttribute('aria-expanded', 'false');
    // Drop completely-empty groups
    ws.state.advancedGroups = (ws.state.advancedGroups || [])
      .map(g => g.filter(f => f.field))
      .filter(g => g.length);
  }

  function _renderAdvGroups(ws = _advWs) {
    const host = document.getElementById('advGroupsList');
    if (!host) return;
    clear(host);
    if (!ws.state.advancedGroups) ws.state.advancedGroups = [];
    if (!ws.state.advancedGroups.length) {
      host.appendChild(el('div', { class: 'muted small', style: 'text-align:center; padding:20px;', text: 'No advanced filters yet. Add a filter group below.' }));
      return;
    }
    ws.state.advancedGroups.forEach((group, gi) => {
      if (gi > 0) host.appendChild(el('div', { class: 'adv-group-or', text: 'OR' }));
      const groupEl = el('div', { class: 'adv-group' });
      const head = el('div', { class: 'adv-group-head' }, [
        el('span', { text: 'Group ' + (gi + 1) }),
        el('span', { class: 'adv-group-actions' }, [
          _iconBtn('copy', 'Duplicate group', () => {
            ws.state.advancedGroups.splice(gi + 1, 0, JSON.parse(JSON.stringify(group)));
            _renderAdvGroups(ws);
          }),
          _iconBtn('trash-2', 'Delete group', () => {
            ws.state.advancedGroups.splice(gi, 1);
            _renderAdvGroups(ws);
          }),
        ]),
      ]);
      groupEl.appendChild(head);
      group.forEach((f, fi) => {
        if (fi > 0) groupEl.appendChild(el('div', { class: 'adv-and', text: 'and' }));
        groupEl.appendChild(_buildAdvFilterBlock(group, fi, ws));
      });
      const addBtn = el('button', { class: 'btn btn-ghost btn-sm', type: 'button', style: 'margin-top:6px;' }, [
        el('i', { 'data-lucide': 'plus' }),
        el('span', { text: 'Add filter' }),
      ]);
      addBtn.addEventListener('click', () => { group.push({ field: '', op: 'equals', value: '' }); _renderAdvGroups(ws); });
      groupEl.appendChild(addBtn);
      host.appendChild(groupEl);
    });
    refreshIcons();
  }

  function _iconBtn(icon, title, fn) {
    const b = el('button', { type: 'button', class: 'icon-btn', title }, [el('i', { 'data-lucide': icon })]);
    b.addEventListener('click', fn);
    return b;
  }

  function _buildAdvFilterBlock(group, fi, ws = _advWs) {
    const f = group[fi];
    const block = el('div', { class: 'adv-filter-block' });
    const rm = el('button', { class: 'adv-rm', type: 'button', title: 'Remove filter', 'aria-label': 'Remove filter' }, [
      el('i', { 'data-lucide': 'x' }),
    ]);
    rm.addEventListener('click', () => { group.splice(fi, 1); if (!group.length) { const gi = ws.state.advancedGroups.indexOf(group); if (gi >= 0) ws.state.advancedGroups.splice(gi, 1); } _renderAdvGroups(ws); });
    block.appendChild(rm);

    const fieldRow = el('div', { class: 'adv-field-row' });

    // Field pill — clicking opens the searchable property picker
    const def = ws.state.propsByName[f.field];
    const fieldPill = el('button', { type: 'button', class: 'adv-field-pill' }, [
      el('span', { class: f.field ? '' : 'placeholder', text: f.field ? (def?.label || f.field) : 'Pick a property…' }),
      el('i', { 'data-lucide': 'chevron-down' }),
    ]);
    fieldPill.addEventListener('click', () => _openPropPicker(fieldPill, (chosen) => {
      f.field = chosen;
      const newOps = _opsForField(ws.state.propsByName[f.field]);
      if (!newOps.includes(f.op)) f.op = newOps[0] || 'equals';
      f.value = '';
      _renderAdvGroups(ws);
    }, ws.state.propsCatalog));
    fieldRow.appendChild(fieldPill);

    // Op select — pretty labels
    if (f.field) {
      const opSel = el('select', { class: 'input' });
      for (const o of _opsForField(def)) {
        const opt = el('option', { value: o, text: _OP_LABELS[o] || o });
        if (f.op === o) opt.selected = true;
        opSel.appendChild(opt);
      }
      opSel.addEventListener('change', () => { f.op = opSel.value; f.value = ''; _renderAdvGroups(ws); });
      fieldRow.appendChild(opSel);
      // Value
      _appendValueInputPanel(fieldRow, f, def);
      // Info line — show resolved date range hint
      const info = _filterInfoLine(f, def);
      if (info) fieldRow.appendChild(el('div', { class: 'adv-info' }, [el('i', { 'data-lucide': 'circle-help' }), el('span', { text: info })]));
    }
    block.appendChild(fieldRow);
    return block;
  }

  function _filterInfoLine(f, def) {
    if (!f || !f.op || !f.value) return '';
    const isDate = def && (def.type === 'date' || def.type === 'datetime');
    if (!isDate) return '';
    // Mirror server-side semantics (rough preview)
    const presetLabels = {
      today: 'today', yesterday: 'yesterday', tomorrow: 'tomorrow',
      this_week: 'this week', last_week: 'last week', next_week: 'next week',
      this_month: 'this month', last_month: 'last month', next_month: 'next month',
      this_quarter: 'this quarter', this_year: 'this year', last_year: 'last year',
    };
    if (f.op === 'is' && presetLabels[f.value]) return 'Includes records dated ' + presetLabels[f.value];
    if (f.op === 'is_between' && f.value.includes('..')) {
      const [a, b] = f.value.split('..');
      if (a && b) return `Between ${a} and ${b}`;
    }
    const rel = String(f.value).match(/^(\d+)_(day|days|week|weeks|month|months|year|years)_(ago|from_now)$/);
    if (rel && (f.op === 'is_more_than' || f.op === 'is_less_than')) {
      return `${f.op === 'is_more_than' ? 'Older than' : 'Newer than'} ${rel[1]} ${rel[2]} ${rel[3] === 'ago' ? 'ago' : 'from now'}`;
    }
    return '';
  }

  function _appendValueInputPanel(row, f, def) {
    // Same logic as _appendValueInput but inside the side-panel layout
    _appendValueInput(row, f, def);
  }

  // Searchable property picker (Phase E6) — vanilla-JS combobox
  function _openPropPicker(anchorEl, onPick, catalog = null) {
    document.getElementById('advPropPicker')?.remove();
    const picker = el('div', { id: 'advPropPicker', class: 'adv-prop-picker' });
    const rect = anchorEl.getBoundingClientRect();
    picker.style.left = Math.min(rect.left, window.innerWidth - 380) + 'px';
    picker.style.top = (rect.bottom + 4) + 'px';
    const search = el('input', { type: 'search', class: 'input', placeholder: 'Search property…' });
    picker.appendChild(search);
    const list = el('div', { class: 'picker-list' });
    picker.appendChild(list);

    const renderList = (term) => {
      clear(list);
      const t = (term || '').toLowerCase().trim();
      // Virtual Leads7 columns are derived per-row at read time — the server
      // can't filter on them, so only the ones explicitly marked _filterable
      // (lead_source, resolved server-side) are offered here. Offering the
      // rest produced filters that silently matched nothing.
      const props = (catalog || CONTACTS.propsCatalog || []).filter(p => {
        if (p._virtual && !p._filterable) return false;
        if (!t) return true;
        return p.name.toLowerCase().includes(t) || (p.label || '').toLowerCase().includes(t);
      });
      // Group
      const groups = {};
      for (const p of props) (groups[p.groupName || 'misc'] ||= []).push(p);
      for (const [g, items] of Object.entries(groups).sort()) {
        list.appendChild(el('div', { class: 'picker-group-head', text: g }));
        for (const p of items) {
          const row = el('div', { class: 'picker-row' }, [
            el('span', { text: p.label || p.name }),
            el('span', { class: 'picker-meta', text: p.type + (p.fieldType ? '/' + p.fieldType : '') }),
          ]);
          row.addEventListener('click', () => { document.getElementById('advPropPicker')?.remove(); onPick(p.name); });
          list.appendChild(row);
        }
      }
    };
    search.addEventListener('input', () => renderList(search.value));
    renderList('');
    document.body.appendChild(picker);
    setTimeout(() => search.focus(), 0);
    setTimeout(() => {
      const off = (ev) => { if (!picker.contains(ev.target) && ev.target !== anchorEl) { picker.remove(); document.removeEventListener('click', off); } };
      document.addEventListener('click', off);
    }, 0);
  }

  // Active-filter summary chips (Phase E6) — visible above the table
  function _renderAdvSummary(ws = _advWs) {
    const host = document.getElementById(ws.ids.advSummary);
    if (!host) return;
    const groups = ws.state.advancedGroups || [];
    const totalFilters = groups.reduce((n, g) => n + g.filter(f => f.field).length, 0);
    if (!totalFilters) { host.classList.add('hidden'); clear(host); return; }
    host.classList.remove('hidden');
    clear(host);
    groups.forEach((g, gi) => {
      g.forEach(f => {
        if (!f.field) return;
        const def = ws.state.propsByName[f.field];
        const label = def?.label || f.field;
        const opLbl = _OP_LABELS[f.op] || f.op;
        // Enumeration values show their human label ("Website"), not the raw
        // stored value ("website_test-level-lead").
        const valLbl = def?.options?.find(o => o.value === f.value)?.label || f.value || '';
        const chip = el('button', {
          type: 'button',
          class: 'adv-summary-chip',
          text: `${label} ${opLbl} ${valLbl}`.trim(),
          title: 'Click to edit',
        });
        chip.addEventListener('click', () => _openAdvPanel(ws));
        host.appendChild(chip);
      });
      if (gi < groups.length - 1) {
        host.appendChild(el('span', { style: 'color:var(--text-secondary); font-weight:700; font-size:11px;', text: 'OR' }));
      }
    });
    const clr = el('button', { class: 'adv-summary-clear', type: 'button' }, [
      el('i', { 'data-lucide': 'x' }),
      el('span', { text: 'Clear filters' }),
    ]);
    clr.addEventListener('click', () => { ws.state.advancedGroups = []; _renderAdvSummary(ws); ws.checkDirty(); ws.refresh(); });
    host.appendChild(clr);
    refreshIcons(host);
  }

  // Phase E5: branch value-input by (op, type)
  function _appendValueInput(row, f, def) {
    const op = String(f.op || '').toLowerCase();
    const isDate = def && (def.type === 'date' || def.type === 'datetime');
    // is_known / is_unknown have no value
    if (op === 'is_known' || op === 'is_unknown') return;
    // Date field with 'is' op → preset dropdown
    if (isDate && op === 'is') {
      const sel = el('select', { class: 'input', style: 'min-width:180px;' });
      sel.appendChild(el('option', { value: '', text: '— pick —' }));
      for (const p of _DATE_PRESETS) {
        const opt = el('option', { value: p.value, text: p.label });
        if (f.value === p.value) opt.selected = true;
        sel.appendChild(opt);
      }
      sel.addEventListener('change', () => { f.value = sel.value; });
      row.appendChild(sel);
      return;
    }
    // Date field with 'is_between' → two date inputs joined with '..'
    if (isDate && op === 'is_between') {
      const [a, b] = String(f.value || '').split('..');
      const fromIn = el('input', { type: 'date', class: 'input', value: a || '' });
      const toIn = el('input', { type: 'date', class: 'input', value: b || '' });
      const update = () => { f.value = (fromIn.value || '') + '..' + (toIn.value || ''); };
      fromIn.addEventListener('change', update);
      toIn.addEventListener('change', update);
      row.appendChild(fromIn);
      row.appendChild(el('span', { text: 'to', style: 'color:var(--text-secondary);' }));
      row.appendChild(toIn);
      return;
    }
    // Date field with 'is_more_than' / 'is_less_than' → number + unit + direction
    if (isDate && (op === 'is_more_than' || op === 'is_less_than')) {
      const m = String(f.value || '').match(/^(\d+)_(day|days|week|weeks|month|months|year|years)_(ago|from_now)$/);
      const num = el('input', { type: 'number', class: 'input', value: m ? m[1] : '', style: 'width:80px;', min: '1' });
      const unitSel = el('select', { class: 'input', style: 'width:80px;' });
      for (const u of ['days', 'weeks', 'months', 'years']) {
        const opt = el('option', { value: u, text: u });
        if (m && (m[2] === u || m[2] === u.replace(/s$/, ''))) opt.selected = true;
        unitSel.appendChild(opt);
      }
      const dirSel = el('select', { class: 'input', style: 'width:100px;' });
      for (const d of [['ago', 'ago'], ['from_now', 'from now']]) {
        const opt = el('option', { value: d[0], text: d[1] });
        if (m && m[3] === d[0]) opt.selected = true;
        dirSel.appendChild(opt);
      }
      const update = () => {
        if (num.value && unitSel.value && dirSel.value) {
          f.value = `${num.value}_${unitSel.value}_${dirSel.value}`;
        }
      };
      num.addEventListener('input', update);
      unitSel.addEventListener('change', update);
      dirSel.addEventListener('change', update);
      row.appendChild(num);
      row.appendChild(unitSel);
      row.appendChild(dirSel);
      return;
    }
    // Date field with is_equal_to / is_before / is_after → single date input
    if (isDate) {
      const valInput = el('input', { type: 'date', class: 'input', value: f.value || '' });
      valInput.addEventListener('change', () => { f.value = valInput.value; });
      row.appendChild(valInput);
      return;
    }
    // Enum field → dropdown of options
    const isEnum = def && Array.isArray(def.options) && def.options.length;
    if (isEnum) {
      const sel = el('select', { class: 'input', style: 'min-width:160px;' });
      sel.appendChild(el('option', { value: '', text: '—' }));
      for (const o of def.options) {
        const opt = el('option', { value: o.value, text: o.label });
        if (f.value === o.value) opt.selected = true;
        sel.appendChild(opt);
      }
      sel.addEventListener('change', () => { f.value = sel.value; });
      row.appendChild(sel);
      return;
    }
    // Number / text fallback
    const valInput = el('input', {
      type: (def && def.type === 'number') ? 'number' : 'text',
      class: 'input',
      placeholder: 'value',
      value: f.value || '',
      style: (def && def.type === 'number') ? 'width:120px;' : 'min-width:160px;',
    });
    valInput.addEventListener('change', () => { f.value = valInput.value; });
    valInput.addEventListener('input', () => { f.value = valInput.value; });
    row.appendChild(valInput);
  }

  function _opsForField(def) {
    if (!def) return ['equals', 'contains'];
    if (def.options && def.options.length) return ['equals', 'is_known', 'is_unknown'];
    if (def.type === 'date' || def.type === 'datetime') {
      // HubSpot-style semantic ops
      return ['is', 'is_equal_to', 'is_before', 'is_after', 'is_between', 'is_more_than', 'is_less_than', 'is_known', 'is_unknown'];
    }
    if (def.type === 'number') return ['equals', '>=', '<=', 'is_known', 'is_unknown'];
    return ['equals', 'contains', 'is_known', 'is_unknown'];
  }

  // Phase E5: Pretty operator labels (HubSpot wording)
  const _OP_LABELS = {
    'equals': 'is equal to',
    '=': 'is equal to',
    'is': 'is',
    'is_equal_to': 'is equal to',
    'is_before': 'is before',
    'is_after': 'is after',
    'is_between': 'is between',
    'is_more_than': 'is more than',
    'is_less_than': 'is less than',
    'is_known': 'is known',
    'is_unknown': 'is unknown',
    'contains': 'contains',
    'before': 'is before',
    'after': 'is after',
    '>=': 'is at least',
    '<=': 'is at most',
    'empty': 'is unknown',
    'not_empty': 'is known',
  };

  // Phase E5: date presets shown when op='is' on a date field
  const _DATE_PRESETS = [
    { value: 'today', label: 'Today', desc: 'All of today' },
    { value: 'yesterday', label: 'Yesterday', desc: 'The previous 24h day' },
    { value: 'tomorrow', label: 'Tomorrow', desc: 'The next 24h day' },
    { value: 'this_week', label: 'This week', desc: 'Mon → Sun this week' },
    { value: 'last_week', label: 'Last week', desc: 'Mon → Sun last week' },
    { value: 'next_week', label: 'Next week', desc: 'Mon → Sun next week' },
    { value: 'this_month', label: 'This month', desc: '1st → end of this month' },
    { value: 'last_month', label: 'Last month', desc: 'Last calendar month' },
    { value: 'next_month', label: 'Next month', desc: 'Next calendar month' },
    { value: 'this_quarter', label: 'This quarter', desc: 'Current 3-month window' },
    { value: 'this_year', label: 'This year', desc: 'Jan 1 → Dec 31' },
    { value: 'last_year', label: 'Last year', desc: 'Previous calendar year' },
  ];

  // ---- Saved Views helpers (Phase E2) ----
  async function _contactsLoadViews() {
    try {
      const r = await api('/api/contacts-views');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      CONTACTS.views = d.views || [];
      CONTACTS.defaultViewId = d.defaultId || null;
      // First load without an explicit user choice (no saved default) lands on
      // the team-wide "All contacts" system view — zero filters, all owners —
      // never on whichever private view happens to sort first (Ayoub,
      // 2026-08-15). A default the user set themselves still wins.
      const active = CONTACTS.views.find(v => v.id === CONTACTS.defaultViewId)
                  || CONTACTS.views.find(v => v._systemKey === 'all_contacts')
                  || CONTACTS.views.find(v => v.ownerId === state.me?.id)
                  || CONTACTS.views[0]
                  || null;
      _applyView(active);
    } catch (err) {
      console.warn('[views] load failed:', err);
    }
  }

  function _applyView(view) {
    CONTACTS.activeView = view || null;
    // Carried by the view, not by any control on the toolbar — "Need to check"
    // IS the filter. Cleared on every other view so it can never leak sideways.
    CONTACTS.phoneStatus = view?.phoneFilter || '';
    CONTACTS.columns = view?.columns ? [...view.columns] : ['firstname','lastname','phone','hs_lead_status','lifecyclestage','city','createdate'];
    // Hydrate filter UI from view
    if (view) {
      const t = document.getElementById('contactsTeamFilter'); if (t) t.value = view.ownerFilter || '';
      const s = document.getElementById('contactsStatusFilter'); if (s) s.value = view.statusFilter || ''; // hydrate saved STATUS quick filter (falls back to "Lead status")
      const d = document.getElementById('contactsDateFrom');
      if (d) {
        const val = view.dateFromFilter || '';
        const labelMap = { today:'Today', yesterday:'Yesterday', tomorrow:'Tomorrow', this_week:'This week', last_week:'Last week', next_week:'Next week', this_month:'This month', last_month:'Last month', next_month:'Next month', this_quarter:'This quarter', this_year:'This year', last_year:'Last year' };
        const label = !val ? 'All time' : (labelMap[val] || val);
        _setDateFromValue(val, label);
      }
      const q = document.getElementById('contactsSearch'); if (q) q.value = view.searchQuery || '';
      // Hydrate advancedGroups from the saved view so filters reappear after switching
      const saved = Array.isArray(view.advancedFilters) ? view.advancedFilters : [];
      CONTACTS.advancedGroups = saved
        .map(g => (Array.isArray(g) ? g : [g])
          .filter(f => f && typeof f === 'object' && f.field && f.op))
        .filter(g => g.length);
      const lastActivityFilter = CONTACTS.advancedGroups.flat().find(f => {
        const def = CONTACTS.propsByName[f.field];
        return f._quick === 'last_activity' || /last.*(activity|contacted)/i.test((def?.label || '') + ' ' + f.field);
      });
      CONTACTS.lastActivityPreset = lastActivityFilter?.value || '';
      CONTACTS.lastActivityField = lastActivityFilter?.field || '';
      const lastBtn = document.getElementById('contactsLastActivityBtn');
      if (lastBtn) {
        const quickLabels = { today:'Today', this_week:'This week', last_week:'Last week', this_month:'This month', last_month:'Last month' };
        lastBtn.classList.toggle('is-active', !!CONTACTS.lastActivityPreset);
        const text = lastBtn.querySelector('span');
        if (text) text.textContent = quickLabels[CONTACTS.lastActivityPreset] || 'Last activity date';
      }
      // Re-render the side panel + summary chip if the user has them open
      try { if (typeof _renderAdvGroups === 'function') _renderAdvGroups(_CONTACTS_WS); } catch {}
      try { if (typeof _renderAdvSummary === 'function') _renderAdvSummary(_CONTACTS_WS); } catch {}
    } else {
      CONTACTS.advancedGroups = [];
    }
    CONTACTS._baselineSig = _signActiveView();
    CONTACTS.dirty = false;
    CONTACTS.pageIndex = 1;
    CONTACTS.pageCursors = [''];
    _syncContactsQuickFilterState();
    _renderViewsTabs();
    _updateDirtyPill();
  }

  // Sign the active-view-effective state (columns + filters) so we can diff
  // for the "dirty" pill.
  function _signActiveView() {
    if (!CONTACTS.activeView) return '';
    const t = (document.getElementById('contactsTeamFilter')?.value || '');
    const st = (document.getElementById('contactsStatusFilter')?.value || '');
    const d = (document.getElementById('contactsDateFrom')?.value || '');
    const q = (document.getElementById('contactsSearch')?.value || '');
    // Normalise advancedGroups so reordering doesn't change the signature
    const adv = (CONTACTS.advancedGroups || [])
      .map(g => (Array.isArray(g) ? g : [])
        .filter(f => f && f.field && f.op)
        .map(f => ({ field: f.field, op: f.op, value: f.value ?? '' })));
    return JSON.stringify({
      cols: CONTACTS.columns,
      frozen: CONTACTS.activeView.frozenCount || 0,
      ownerFilter: t,
      statusFilter: st,
      dateFromFilter: d,
      searchQuery: q,
      advancedGroups: adv,
    });
  }

  function _checkDirty() {
    const wasDirty = CONTACTS.dirty;
    CONTACTS.dirty = _signActiveView() !== CONTACTS._baselineSig;
    if (wasDirty !== CONTACTS.dirty) _updateDirtyPill();
  }

  function _updateDirtyPill() {
    const pill = document.getElementById('viewSaveDirtyBtn');
    if (!pill) return;
    pill.classList.toggle('hidden', !CONTACTS.dirty || !CONTACTS.activeView);
  }

  function _renderViewsTabs(ws = _CONTACTS_WS) {
    const scroll = document.getElementById(ws.ids.tabsScroll);
    if (!scroll) return;
    clear(scroll);
    for (const v of ws.state.views) {
      const isActive = ws.state.activeView?.id === v.id;
      const isDefault = ws.state.defaultViewId === v.id;
      const tab = el('div', {
        class: 'views-tab' + (isActive ? ' views-tab-active' : '')
             + (isDefault ? ' views-tab-default' : '')
             + ' views-tab-vis-' + (v.visibility || 'private'),
      });
      const label = el('button', {
        type: 'button',
        class: 'views-tab-label',
        role: 'tab',
        'aria-selected': String(isActive),
        text: v.name,
      });
      label.addEventListener('click', () => ws.switchView(v.id));
      tab.appendChild(label);
      if (v.visibility === 'public' || v.visibility === 'team-shared') {
        tab.appendChild(el('i', { 'data-lucide': 'users-round', title: 'Shared view' }));
      }
      // 3-dot menu
      const menuBtn = el('button', { class: 'views-tab-menu-btn', title: 'View options', 'aria-label': `View options for ${v.name}` }, [
        el('i', { 'data-lucide': 'more-vertical' }),
      ]);
      menuBtn.addEventListener('click', (ev) => { ev.stopPropagation(); _openViewTabMenu(menuBtn, v, ws); });
      tab.appendChild(menuBtn);
      scroll.appendChild(tab);
    }
    const add = document.getElementById(ws.ids.addViewBtn);
    if (add) {
      clear(add);
      add.appendChild(el('i', { 'data-lucide': 'plus' }));
      add.appendChild(document.createTextNode(` Add view (${ws.state.views.length}/50)`));
    }
    refreshIcons();
  }

  function _openViewTabMenu(anchorEl, view, ws = _CONTACTS_WS) {
    document.getElementById('viewTabPopMenu')?.remove();
    const menu = el('div', { id: 'viewTabPopMenu', class: 'views-tab-popmenu' });
    const rect = anchorEl.getBoundingClientRect();
    menu.style.left = rect.left + 'px';
    menu.style.top = (rect.bottom + 4) + 'px';
    menu.style.position = 'absolute';
    const isOwner = view.ownerId === state.me?.id;
    const isAdmin = _isSalesAdmin();
    const canEdit = isOwner || isAdmin;
    const makeBtn = (icon, text, fn, cls = '') => {
      const b = el('button', { class: cls }, [
        el('i', { 'data-lucide': icon }),
        el('span', { text }),
      ]);
      b.addEventListener('click', () => { document.getElementById('viewTabPopMenu')?.remove(); fn(); });
      menu.appendChild(b);
    };
    // Save the CURRENT filter/column state onto this view. Only meaningful for
    // the active view (the live filters belong to it). This is the discoverable
    // "Save" entry point — the dirty-pill alone wasn't obvious enough.
    const isActive = view.id === ws.state.activeView?.id;
    if (canEdit && isActive) makeBtn('save', 'Save filters to this view', () => ws.saveDirty());
    if (canEdit) makeBtn('settings-2', 'Rename / settings', () => _openViewModal({ mode: 'edit', view }, ws));
    makeBtn('star', 'Set as my default', async () => {
      const r = await api(ws.viewsApi + '/' + view.id + '/set-default', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      if (r.ok) { ws.state.defaultViewId = view.id; toast('Default updated', 'success'); _renderViewsTabs(ws); }
    });
    if (canEdit) makeBtn('trash-2', 'Delete view', () => ws.deleteView(view), 'danger');
    document.body.appendChild(menu);
    refreshIcons(menu);
    setTimeout(() => {
      const off = (ev) => {
        if (!menu.contains(ev.target)) { menu.remove(); document.removeEventListener('click', off); }
      };
      document.addEventListener('click', off);
    }, 0);
  }

  async function _switchView(viewId) {
    const v = CONTACTS.views.find(x => x.id === viewId);
    if (!v) return;
    // Saved views are navigation, not a destructive form submission. HubSpot
    // switches them immediately; a native confirm here made tabs such as Paid
    // look broken whenever a quick filter had marked the current view dirty.
    _closeContactsFloatingUi();
    _closeContactInlineEditor();
    _applyView(v);
    await refreshContacts();
  }

  async function _deleteView(view) {
    if (!confirm(`Delete view "${view.name}"? This can't be undone.`)) return;
    const r = await api('/api/contacts-views/' + view.id, { method: 'DELETE' });
    if (!r.ok) { toast('Échec de la suppression — réessayez', 'error'); return; }
    toast('View deleted', 'success');
    await _contactsLoadViews();
    await refreshContacts();
  }

  async function _saveActiveViewDirty() {
    if (!CONTACTS.activeView) return;
    const patch = {
      columns: CONTACTS.columns,
      ownerFilter: document.getElementById('contactsTeamFilter')?.value || '',
      statusFilter: document.getElementById('contactsStatusFilter')?.value || '',
      dateFromFilter: document.getElementById('contactsDateFrom')?.value || '',
      searchQuery: document.getElementById('contactsSearch')?.value || '',
      advancedFilters: (CONTACTS.advancedGroups || [])
        .map(g => (Array.isArray(g) ? g : []).filter(f => f && f.field && f.op))
        .filter(g => g.length),
    };
    try {
      const r = await api('/api/contacts-views/' + CONTACTS.activeView.id, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
      });
      if (!r.ok) {
        let serverMsg = '';
        try { serverMsg = (await r.json())?.error || ''; } catch { /* non-JSON body */ }
        toast('Save failed: ' + (serverMsg || ('HTTP ' + r.status)), 'error');
        console.error('[views] save failed:', r.status, serverMsg);
        return;
      }
      const d = await r.json();
      CONTACTS.activeView = d.view;
      Object.assign(CONTACTS.views.find(v => v.id === d.view.id) || {}, d.view);
      CONTACTS._baselineSig = _signActiveView();
      CONTACTS.dirty = false;
      _updateDirtyPill();
      toast('View saved', 'success');
    } catch (err) {
      console.error('[views] save threw:', err);
      toast('Save failed: ' + (err.message || 'network error'), 'error');
    }
  }

  // The save-view modal (#contactsViewModal) is shared by every workspace;
  // `_viewModalWs` remembers which one opened it so submit/delete route back.
  let _viewModalWs = _CONTACTS_WS;
  let _viewModalWired = false;
  function _wireViewModalOnce() {
    if (_viewModalWired) return;
    _viewModalWired = true;
    document.getElementById('viewModalSaveBtn')?.addEventListener('click', () => _viewModalSubmit());
    document.getElementById('viewModalDeleteBtn')?.addEventListener('click', () => _viewModalDelete());
    document.querySelectorAll('input[name="viewVis"]').forEach(r => {
      r.addEventListener('change', () => {
        document.getElementById('viewSharePicker')?.classList.toggle('hidden', r.value !== 'team-shared');
      });
    });
  }
  function _openViewModal({ mode, view }, ws = _CONTACTS_WS) {
    _viewModalWs = ws;
    document.getElementById('viewModalTitle').textContent = mode === 'edit' ? 'Edit view' : 'Save as new view';
    const nameInput = document.getElementById('viewModalName');
    nameInput.value = view?.name || '';
    document.querySelectorAll('input[name="viewVis"]').forEach(r => { r.checked = (r.value === (view?.visibility || 'private')); });
    document.getElementById('viewSharePicker')?.classList.toggle('hidden', (view?.visibility || 'private') !== 'team-shared');
    document.getElementById('viewSetAsDefault').checked = false;
    document.getElementById('viewModalDeleteBtn')?.classList.toggle('hidden', mode !== 'edit');
    document.getElementById('viewModalSaveBtn').dataset.mode = mode;
    document.getElementById('viewModalSaveBtn').dataset.viewId = view?.id || '';
    _populateSharePicker(view?.sharedWith || [], ws);
    openModal('contactsViewModal');
    refreshIcons();
    setTimeout(() => nameInput.focus(), 50);
  }

  async function _populateSharePicker(currentShared, ws = _CONTACTS_WS) {
    const wrap = document.getElementById('viewSharePicker');
    if (!wrap) return;
    if (!ws.state.teamUsers) ws.state.teamUsers = [];
    if (!ws.state.teamUsers.length) {
      try { const r = await api('/api/team/users'); if (r.ok) ws.state.teamUsers = (await r.json()).users || []; } catch {}
    }
    clear(wrap);
    for (const u of ws.state.teamUsers) {
      const row = el('label', { style: 'display:flex; gap:6px; align-items:center; padding:3px 0;' }, [
        el('input', { type: 'checkbox', class: 'view-share-cb', 'data-user-id': u.id, ...(currentShared.includes(u.id) ? { checked: 'checked' } : {}) }),
        el('span', { text: userLabel(u.username, u.displayName || u.username), style: 'font-size:13px;' }),
      ]);
      wrap.appendChild(row);
    }
  }

  async function _viewModalSubmit() {
    const ws = _viewModalWs;
    const btn = document.getElementById('viewModalSaveBtn');
    const mode = btn.dataset.mode || 'new';
    const viewId = btn.dataset.viewId || '';
    const name = (document.getElementById('viewModalName').value || '').trim();
    if (!name) { toast('Name required', 'warning'); return; }
    const visibility = document.querySelector('input[name="viewVis"]:checked')?.value || 'private';
    const sharedWith = Array.from(document.querySelectorAll('.view-share-cb:checked')).map(cb => cb.getAttribute('data-user-id')).filter(Boolean);
    const setDefault = document.getElementById('viewSetAsDefault').checked;
    const payload = {
      name, visibility,
      sharedWith: visibility === 'team-shared' ? sharedWith : [],
      objectType: ws.objectType,
      columns: ws.state.columns,
      frozenCount: ws.state.activeView?.frozenCount || 0,
      ...ws.viewPayload(),
      // Persist advanced filter groups with the view — same normalization as the
      // dirty-pill save (_saveActiveViewDirty). Without this, "+ New view" / "Edit
      // view" dropped the advanced filters on save.
      advancedFilters: (ws.state.advancedGroups || [])
        .map(g => (Array.isArray(g) ? g : []).filter(f => f && f.field && f.op))
        .filter(g => g.length),
    };
    try {
      let resp;
      if (mode === 'edit' && viewId) {
        resp = await api(ws.viewsApi + '/' + viewId, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      } else {
        resp = await api(ws.viewsApi + ws.viewsQuery, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      }
      if (!resp.ok) { const j = await resp.json().catch(() => ({})); toast('Save failed: ' + (j.error || resp.status), 'error'); return; }
      const data = await resp.json();
      const savedView = data.view;
      toast(mode === 'edit' ? 'View updated' : 'View created', 'success');
      if (setDefault) {
        await api(ws.viewsApi + '/' + savedView.id + '/set-default', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
        ws.state.defaultViewId = savedView.id;
      }
      closeModal('contactsViewModal');
      await ws.loadViews();
      // Switch to the newly created/edited view
      ws.applyView(ws.state.views.find(v => v.id === savedView.id) || savedView);
      await ws.refresh();
    } catch (err) { toast('Save failed: ' + (err.message || err), 'error'); }
  }

  async function _viewModalDelete() {
    const ws = _viewModalWs;
    const viewId = document.getElementById('viewModalSaveBtn').dataset.viewId || '';
    if (!viewId) return;
    const view = ws.state.views.find(v => v.id === viewId);
    if (!view) return;
    closeModal('contactsViewModal');
    await ws.deleteView(view);
  }

  async function _contactsLoadColumns() {
    // Phase E2: legacy — kept as a fallback no-op. The active view's columns
    // are the new source of truth. We still leave this for any caller that
    // wants to force-refresh the column list.
  }

  // Lead-origin source → display label + colour kind. Drives the read-only
  // "Lead Source" pill in the contacts table. `value` keys are leads.source
  // values (+ reserved future channels FB/IG/TikTok). Unknown values fall back
  // to a title-cased label with a neutral colour.
  const LEAD_SOURCE_OPTIONS = [
    { value: 'google_sheets',    label: 'Google Sheet',   kind: 'sheet' },
    { value: 'inbound_whatsapp', label: 'WhatsApp',       kind: 'whatsapp' },
    { value: 'whatsapp_direct',  label: 'WhatsApp direct', kind: 'whatsapp' },
    { value: 'website_whatsapp', label: 'Site → WhatsApp', kind: 'website' },
    { value: 'ad_whatsapp',      label: 'Meta Ad → WhatsApp', kind: 'facebook' },
    // Coded first messages from our own links — server lib/leads/channels.js.
    { value: 'youtube_whatsapp',      label: 'YouTube → WhatsApp',      kind: 'youtube' },
    { value: 'instagram_whatsapp',    label: 'Instagram → WhatsApp',    kind: 'instagram' },
    { value: 'facebook_whatsapp',     label: 'Facebook → WhatsApp',     kind: 'facebook' },
    { value: 'tiktok_whatsapp',       label: 'TikTok → WhatsApp',       kind: 'tiktok' },
    { value: 'google_maps_whatsapp',  label: 'Google Maps → WhatsApp',  kind: 'google' },
    { value: 'ai_assistant_whatsapp', label: 'AI assistant → WhatsApp', kind: 'ai' },
    { value: 'manual',           label: 'Legacy / Unknown', kind: 'unknown' },
    { value: 'auto',             label: 'Legacy / Unknown', kind: 'unknown' },
    { value: 'unknown_legacy',   label: 'Legacy / Unknown', kind: 'unknown' },
    { value: 'legacy_unknown',   label: 'Legacy / Unknown', kind: 'unknown' },
    { value: 'website',          label: 'Website',      kind: 'website' },
    { value: 'facebook',         label: 'Facebook',     kind: 'facebook' },
    { value: 'instagram',        label: 'Instagram',    kind: 'instagram' },
    { value: 'tiktok',           label: 'TikTok',       kind: 'tiktok' },
    { value: 'youtube',          label: 'YouTube',      kind: 'youtube' },
    { value: 'google',           label: 'Google',       kind: 'google' },
    { value: 'google_maps',      label: 'Google Maps',  kind: 'google' },
    { value: 'ai_assistant',     label: 'AI assistant', kind: 'ai' },
    { value: 'affiliate',        label: 'Affiliate partner', kind: 'referral' },
    { value: 'client_referral',  label: 'Client referral', kind: 'referral' },
    { value: 'shared_contact',   label: 'Shared contact', kind: 'referral' },
    { value: 'walk_in',          label: 'Walk-in',      kind: 'organic' },
    { value: 'organic',          label: 'Organic / community', kind: 'organic' },
    { value: 'other',            label: 'Other',        kind: 'other' },
    { value: 'hubspot',          label: 'HubSpot',      kind: 'hubspot' },
    { value: 'sms',              label: 'SMS',          kind: 'other' },
    { value: 'threecx_call',     label: '3CX call',     kind: 'other' },
  ];
  function _leadSourceMeta(raw) {
    const v = String(raw || '').trim().toLowerCase();
    if (!v) return { label: '', kind: 'unknown' };
    const direct = LEAD_SOURCE_OPTIONS.find(o => o.value === v);
    if (direct) return { label: direct.label, kind: direct.kind };
    if (v.includes('whatsapp')) return { label: 'WhatsApp', kind: 'whatsapp' };
    if (v.startsWith('website') || v === 'form') return { label: 'Website', kind: 'website' };
    if (v.startsWith('facebook') || v.startsWith('fbm') || v.startsWith('fb_')) return { label: 'Facebook', kind: 'facebook' };
    if (v.startsWith('instagram') || v.startsWith('ig')) return { label: 'Instagram', kind: 'instagram' };
    if (v.startsWith('tiktok') || v.startsWith('tt')) return { label: 'TikTok', kind: 'tiktok' };
    if (v.startsWith('hubspot')) return { label: 'HubSpot', kind: 'hubspot' };
    if (v.startsWith('sheet') || v.includes('google')) return { label: 'Google Sheet', kind: 'sheet' };
    const label = v.replace(/[_-]+/g, ' ').replace(/\b\w/g, m => m.toUpperCase());
    return { label, kind: 'other' };
  }

  async function _contactsLoadPropsCatalog() {
    try {
      const r = await api('/api/hubspot/properties/contacts/full');
      if (!r.ok) return;
      const d = await r.json();
      CONTACTS.propsCatalog = Array.isArray(d.properties) ? d.properties : [];
      // Inject the virtual "Lead Source" column (origin of the lead, from the
      // Postgres leads table — NOT a HubSpot property). Making it an
      // enumeration prop lets it show up in the column picker like any other
      // field; _buildContactCell renders it as a read-only coloured pill.
      // _filterable: the ONLY virtual prop the Advanced Filters may offer —
      // the server resolves it against Postgres leads.source (label
      // equivalence). The other virtuals (whatsapp_number, phone_suggestion)
      // are computed per-row at read time and can't be filtered server-side,
      // so the filter picker hides them (they stay available as columns).
      const _leadSourceProp = {
        name: 'lead_source', label: 'Lead Source', type: 'enumeration', fieldType: 'select',
        groupName: 'Leads7', _virtual: true, _filterable: true,
        options: LEAD_SOURCE_OPTIONS.map(o => ({ value: o.value, label: o.label })),
      };
      // Replace the static option list with the source families ACTUALLY in
      // the leads table (server collapses them by pill label) — so real-data
      // values the static list never knew ('6mois', 'page', 'SMS', …) become
      // filterable. Static list stays as the fallback when the call fails.
      try {
        const sr = await api('/api/leads/sources');
        if (sr.ok) {
          const sd = await sr.json();
          if (Array.isArray(sd.sources) && sd.sources.length) {
            _leadSourceProp.options = sd.sources.map(s => ({ value: s.value, label: s.label }));
          }
        }
      } catch { /* keep static fallback */ }
      // Virtual "N° WhatsApp" — the contact's REAL WhatsApp number, computed
      // server-side in _enrichContactsWithLeads (whatsapp_phone prop, else the
      // linked lead's WA chat number). _virtual keeps it out of the live-
      // hydrate `properties=` param; the cell renderer is read-only.
      const _waNumberProp = {
        name: 'whatsapp_number', label: 'WhatsApp number', type: 'string', fieldType: 'text',
        groupName: 'Leads7', _virtual: true,
      };
      // Drop any same-named HubSpot catalog entries before injecting: propsByName
      // is built with Object.fromEntries (last wins), so a portal property that
      // happens to be called whatsapp_number/lead_source would otherwise shadow
      // the virtual def — losing _virtual and re-triggering live-hydrate reads.
      // Virtual "Pays probable" — for a number that is only missing its country
      // code, the finished numbers it could be, most likely first. Read-only and
      // one click each: 324 of the 373 flagged leads had never been messaged
      // once, and 88 of them need nothing but this.
      const _phoneSuggestionProp = {
        name: 'phone_suggestion', label: 'Pays probable', type: 'string', fieldType: 'text',
        groupName: 'Leads7', _virtual: true,
      };
      CONTACTS.propsCatalog = CONTACTS.propsCatalog.filter(p => p.name !== 'lead_source' && p.name !== 'whatsapp_number' && p.name !== 'phone_suggestion');
      CONTACTS.propsCatalog = [_leadSourceProp, _waNumberProp, _phoneSuggestionProp, ...CONTACTS.propsCatalog];
      CONTACTS.propsByName = Object.fromEntries(CONTACTS.propsCatalog.map(p => [p.name, p]));
    } catch (err) { console.warn('[contacts] property catalog failed:', err); }
  }

  async function _contactsLoadTeamDropdown() {
    try {
      const r = await api('/api/users/assignable');
      if (!r.ok) return;
      const users = await r.json();
      // The /api/users/assignable endpoint returns local users; we need HubSpot
      // owner IDs. Fetch the cached owners list separately to populate the dropdown
      // with everyone HubSpot knows about (matches the existing tickets pattern).
      const ownersResp = await api('/api/hubspot/contacts?createdAfter=all&limit=1');
      const ownersMap = ownersResp.ok ? (await ownersResp.json()).ownersMap || {} : {};
      const select = document.getElementById('contactsTeamFilter');
      if (!select) return;
      clear(select);
      if (!_isSalesAdmin()) {
        select.appendChild(el('option', { value: '', text: 'Contact owner: me' }));
        select.disabled = true;
        select.title = 'Your Contacts view is scoped to your HubSpot owner';
        _syncContactsQuickFilterState();
        return;
      }
      select.disabled = false;
      select.title = 'Filter by contact owner';
      select.appendChild(el('option', { value: '', text: 'All contact owners' }));
      // Sort owners alphabetically by name for the dropdown
      const ownerEntries = Object.entries(ownersMap).sort((a, b) => a[1].localeCompare(b[1]));
      for (const [id, name] of ownerEntries) {
        select.appendChild(el('option', { value: id, text: name }));
      }
      if (CONTACTS.activeView) select.value = CONTACTS.activeView.ownerFilter || '';
      _syncContactsQuickFilterState();
    } catch (err) {
      console.warn('[contacts] team dropdown failed:', err);
    }
  }

  async function _contactsLoadStatusDropdown() {
    try {
      // Only admins can read /api/admin/lead-status-map. Agents would get a 403
      // logged to console on every page load — skip the call for them.
      const role = (state.me?.role || '').toLowerCase();
      if (role === 'admin' || role === 'super_admin') {
        let mapResp = await api('/api/admin/lead-status-map');
        if (mapResp.ok) {
          const md = await mapResp.json();
          CONTACTS.leadStatusMap = md.map || {};
        } else { CONTACTS.leadStatusMap = {}; }
      } else {
        CONTACTS.leadStatusMap = {};
      }
      // Load distinct HubSpot statuses for the dropdown options
      let distResp = await api('/api/hubspot/contacts/distinct-lead-statuses');
      if (distResp.ok) {
        const dd = await distResp.json();
        CONTACTS.distinctStatuses = dd.values || [];
      } else { CONTACTS.distinctStatuses = []; }
      const select = document.getElementById('contactsStatusFilter');
      if (!select) return;
      clear(select);
      select.appendChild(el('option', { value: '', text: 'Lead status' }));
      // Group: our 10 internal stages, labeled exactly like the chat pipeline bar.
      // Each value stays the raw stage key (e.g. book_test) and filters Contacts
      // by the REAL Postgres leads.pipeline_stage server-side. The old "HubSpot
      // raw values" optgroup was removed — the team filters by the live pipeline,
      // not the hs_lead_status mirror (which has no 'new' value and drifts).
      // STAGE_LABELS is the shared module const (also used by the "Our stage" column).
      const ourGroup = el('optgroup', { label: 'Our pipeline stages' });
      for (const stage of ['new','contacted','book_test','test_done','proforma_sent','paid','no_answer','no_show','not_interested','lost']) {
        ourGroup.appendChild(el('option', { value: stage, text: STAGE_LABELS[stage] || stage }));
      }
      select.appendChild(ourGroup);
      // Parallel-load race: if the active view loaded before these options
      // existed, _applyView couldn't set the value — reflect it now.
      if (CONTACTS.activeView) select.value = CONTACTS.activeView.statusFilter || '';
      _syncContactsQuickFilterState();
    } catch (err) {
      console.warn('[contacts] status dropdown failed:', err);
    }
  }

  async function refreshContacts({ append = false, ownershipRetry = 0, pageCursor = '', preservePage = false } = {}) {
    // Don't drop a fresh search while a slower one is still running (that was
    // what made fast typing feel dead) — only block a duplicate "Load more".
    // This bail MUST stay ABOVE the token bump: a bailed Load-more that
    // incremented searchToken would supersede the in-flight owner WITHOUT
    // launching a replacement, so the catch-side token guard below would
    // discard the owner's failure and leave "Loading contacts…" up forever.
    if (append && CONTACTS.loading) return;
    // Live-search stale-response guard: every call takes the next token, and a
    // response only touches the DOM if it's still the latest. So the final
    // keystroke always wins, even if an earlier slow request returns later.
    // Invariant: from here on this call always issues a request. Ownership
    // socket events may also bump the token without issuing inline; their
    // coalesced replacement refresh (or the next Contacts view-open) becomes
    // the new owner and settles the spinner.
    const myToken = (CONTACTS.searchToken = (CONTACTS.searchToken || 0) + 1);
    CONTACTS.loading = true;
    if (!append && !preservePage) {
      CONTACTS.pageIndex = 1;
      CONTACTS.pageCursors = [''];
      pageCursor = '';
    }
    const loadingEl = document.getElementById('contactsLoading');
    const emptyEl = document.getElementById('contactsEmpty');
    const tableEl = document.getElementById('contactsTable');
    const moreBtn = document.getElementById('contactsLoadMoreBtn');
    if (loadingEl && !append) {
      _renderContactsLoadingState();
      loadingEl.classList.remove('hidden');
    }
    if (emptyEl) emptyEl.classList.add('hidden');
    _updateContactsPager();

    try {
      const params = new URLSearchParams();
      const ownerId = (document.getElementById('contactsTeamFilter')?.value || '').trim();
      const stage = (document.getElementById('contactsStatusFilter')?.value || '').trim();
      const q = (document.getElementById('contactsSearch')?.value || '').trim();
      const dateFrom = (document.getElementById('contactsDateFrom')?.value || '').trim();
      if (ownerId) params.set('ownerId', ownerId);
      if (stage) params.set('stage', stage);
      if (q) params.set('q', q);
      if (CONTACTS.showArchived) params.set('archived', '1');
      if (CONTACTS.phoneFixOnly) params.set('phoneFix', '1');
      if (CONTACTS.phoneStatus) params.set('phoneStatus', CONTACTS.phoneStatus);
      // Phase E6.6: translate the From picker value:
      //   ''                 → all-time, no filter
      //   'YYYY-MM-DD'       → simple createdAfter
      //   'today'/'this_week'/... → injected as createdate advanced filter
      const dateFromVal = dateFrom; // already trimmed above
      const semanticPresets = new Set(['today','yesterday','tomorrow','this_week','last_week','next_week','this_month','last_month','next_month','this_quarter','this_year','last_year']);
      let injectedDatePreset = null;
      if (!dateFromVal) {
        params.set('createdAfter', 'all');
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateFromVal)) {
        params.set('createdAfter', dateFromVal + 'T00:00:00Z');
      } else if (semanticPresets.has(dateFromVal)) {
        params.set('createdAfter', 'all');
        injectedDatePreset = dateFromVal;
      } else {
        params.set('createdAfter', 'all');
      }
      // Phase E6: advanced groups (AND inside group, OR between groups)
      let cleanGroups = (CONTACTS.advancedGroups || [])
        .map(g => g.filter(f => f && f.field && f.op))
        .filter(g => g.length);
      if (injectedDatePreset) {
        // AND a synthetic group with the createdate preset across all existing groups
        const synthetic = { field: 'createdate', op: 'is', value: injectedDatePreset };
        if (cleanGroups.length) {
          cleanGroups = cleanGroups.map(g => [...g, synthetic]);
        } else {
          cleanGroups = [[synthetic]];
        }
      }
      if (cleanGroups.length) params.set('adv', JSON.stringify(cleanGroups));
      // Legacy: still honor advancedFilters flat list if any caller sets it
      for (const f of CONTACTS.advancedFilters || []) {
        if (f.field && f.op) params.append('df', `${f.field}:${f.op}:${f.value || ''}`);
      }
      params.set('limit', String(CONTACTS.pageSize || 25));
      params.set('page', String(CONTACTS.pageIndex || 1));
      if (pageCursor) params.set('after', pageCursor);
      else if (append && CONTACTS.paging?.nextAfter) params.set('after', CONTACTS.paging.nextAfter);

      // Phase D: ask the server to hydrate any column the user has selected
      // that isn't yet in the local mirror's properties_json snapshot.
      // Virtual columns (lead_source & friends) are Leads7-side derivations,
      // not HubSpot properties — sending them made every contact look
      // "missing a prop" and triggered a live HubSpot batch-read per refresh.
      const hubspotCols = (CONTACTS.columns || []).filter(c => !CONTACTS.propsByName?.[c]?._virtual);
      if (hubspotCols.length) params.set('properties', hubspotCols.join(','));

      const r = await api('/api/hubspot/contacts?' + params.toString());
      if (r.status === 409 && ownershipRetry < 2) {
        await new Promise(resolve => setTimeout(resolve, 50));
        return refreshContacts({ append, ownershipRetry: ownershipRetry + 1, pageCursor, preservePage });
      }
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const data = await r.json();
      if (myToken !== CONTACTS.searchToken) return; // superseded by a newer keystroke — discard
      CONTACTS.ownersMap = data.ownersMap || {};
      CONTACTS.paging = data.paging || null;
      CONTACTS.total = Number(data.total || 0);
      const rows = data.contacts || [];
      CONTACTS.rows = append ? [...CONTACTS.rows, ...rows] : rows;

      document.getElementById('contactsCount').textContent = `${CONTACTS.total.toLocaleString()} record${CONTACTS.total === 1 ? '' : 's'}`;
      // Backlog of undialable numbers for THIS user (owner-scoped server-side),
      // independent of the filters currently applied — hidden at zero so a clean
      // list stays clean.
      const fixCount = Number(data.phoneFixTotal) || 0;
      const fixEl = document.getElementById('contactsPhoneFixCount');
      if (fixEl) {
        fixEl.textContent = String(fixCount);
        fixEl.classList.toggle('hidden', fixCount === 0);
      }
      document.getElementById('contactsPhoneFixToggle')?.classList.toggle('hidden', fixCount === 0 && !CONTACTS.phoneFixOnly);
      _renderContactsHead();
      _renderContactsTable(CONTACTS.rows);
      if (loadingEl) loadingEl.classList.add('hidden');
      if (CONTACTS.rows.length === 0) {
        emptyEl?.classList.remove('hidden');
        tableEl?.classList.add('hidden');
      } else {
        tableEl?.classList.remove('hidden');
      }
      if (moreBtn) moreBtn.classList.add('hidden');
      _updateContactsPager();
      refreshIcons();
    } catch (err) {
      // Spinner invariant: only the request that still owns the view may
      // touch it. A superseded request's failure (incl. watchdog timeout)
      // must not stomp on the newer in-flight load that now owns the
      // element — per the bump invariant above, that newer owner always
      // exists and (with api()'s timeout) is guaranteed to settle the
      // spinner itself.
      if (myToken !== CONTACTS.searchToken) return; // owner's own finally resets CONTACTS.loading
      if (loadingEl) {
        loadingEl.textContent = 'Failed to load: ' + (err.message || err);
        loadingEl.classList.remove('hidden');
      }
      _updateContactsPager();
    } finally {
      // Ownership-scoped: a superseded request must NOT clear the in-flight
      // flag while the newer owner is still running — that window let a
      // Load-more click slip past the append guard and fire a concurrent
      // append. Ownership invalidation may leave it true for the 200ms debounce;
      // the scheduled replacement (or next view-open) takes ownership.
      if (myToken === CONTACTS.searchToken) {
        CONTACTS.loading = false;
        _updateContactsPager();
      }
    }
  }

  // Phase D: render thead dynamically from CONTACTS.columns.
  // Pinned columns always present: checkbox + Name. After that, the user's
  // chosen columns. Plus 2 derived ("Our stage", "Mr.Call score") if relevant
  // columns are picked (hs_lead_status implies "Our stage").
  // Columns auto-covered by the "Name" auto-column — skip them to avoid duplicates.
  const _NAME_COVERED = new Set(['firstname', 'lastname', 'full_name']);

  function _visibleColumns() {
    // Lead Status (hs_lead_status) + the derived read-only "Our stage" column are
    // a HubSpot-mirror field that is SEPARATE from the real leads.pipeline_stage.
    // Per Ayoub: the conversation pipeline bar is the single source of truth for
    // stage, so hide these columns from the team (agents + super_sales) across ALL
    // views. System-admin keeps them. "Our stage" auto-hides too — it's gated on
    // cols.includes('hs_lead_status'). Filters are unaffected (server-side).
    const hideLeadStatus = !_isSystemAdmin();
    return (CONTACTS.columns || []).filter(c =>
      !_NAME_COVERED.has(c) && !(hideLeadStatus && _isLeadStatusField(c)));
  }

  // Per-user column widths for the Contacts table — persisted in localStorage
  // so the agent's preferred layout survives reloads + view switches.
  const CONTACTS_COL_WIDTHS_KEY = 'contacts_col_widths_v1';
  function _loadColWidths(storageKey = CONTACTS_COL_WIDTHS_KEY) {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}') || {}; }
    catch { return {}; }
  }
  function _saveColWidths(map, storageKey = CONTACTS_COL_WIDTHS_KEY) {
    try { localStorage.setItem(storageKey, JSON.stringify(map)); } catch {}
  }
  function _setColWidth(key, px, storageKey = CONTACTS_COL_WIDTHS_KEY) {
    if (!key) return;
    const widths = _loadColWidths(storageKey);
    widths[key] = Math.max(60, Math.min(800, Math.round(px)));
    _saveColWidths(widths, storageKey);
  }
  function _attachColResize(th, colKey, ws = _CONTACTS_WS) {
    if (!th || !colKey) return;
    // Apply saved width
    const widths = _loadColWidths(ws.colWidthsKey);
    if (widths[colKey]) th.style.width = widths[colKey] + 'px';
    // Anchor for the absolute handle. We can't stack `position:` so we set
    // `--col-handle-anchor: relative` via inline style? CSS already has the
    // th as `position:sticky`, which IS a positioning context for children.
    const handle = el('span', { class: 'col-resize-handle', 'data-col-key': colKey });
    // Stop native HTML5 drag from initiating on the resize handle — otherwise
    // grabbing the right edge would trigger column-reorder instead of resize.
    handle.setAttribute('draggable', 'false');
    handle.addEventListener('dragstart', (e) => { e.preventDefault(); e.stopPropagation(); });
    th.appendChild(handle);
    // mousedown on the handle: flip body.col-resizing immediately + temporarily
    // disable the th's draggable attribute so a subsequent native dragstart
    // (which fires on move, AFTER mousedown) can't hijack into reorder mode.
    // Without the draggable=false flip, Chrome occasionally promoted a 1-2 px
    // drag into an HTML5 drag op, which prevented the resize mousemove from
    // firing and left the column at its original width (the symptom Ayoub
    // screenshot'd on 2026-05-24 — "wkha ktbghi tresizihom mkibghiwsh").
    handle.addEventListener('mousedown', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const startX = ev.clientX;
      const startW = th.getBoundingClientRect().width;
      const prevDraggable = th.getAttribute('draggable');
      th.setAttribute('draggable', 'false');
      handle.classList.add('is-dragging');
      document.body.classList.add('col-resizing');
      const onMove = (e) => {
        const w = Math.max(60, Math.min(800, startW + (e.clientX - startX)));
        th.style.width = w + 'px';
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        handle.classList.remove('is-dragging');
        document.body.classList.remove('col-resizing');
        if (prevDraggable !== null) th.setAttribute('draggable', prevDraggable);
        const finalW = th.getBoundingClientRect().width;
        _setColWidth(colKey, finalW, ws.colWidthsKey);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });
  }

  // Drag-to-reorder for user-chosen columns. Only attached to <th>s whose
  // key is a real entry in CONTACTS.columns (skip the auto columns: checkbox,
  // Name, Our stage, Mr.Call score, Owner). Reordering mutates CONTACTS.columns
  // and re-renders both head + body; the existing dirty-pill picks up the
  // change so the agent can Save the new layout to their active view.
  function _reorderContactsColumn(fromKey, toKey, insertBefore) {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const cols = (CONTACTS.columns || []).slice();
    const fromIdx = cols.indexOf(fromKey);
    if (fromIdx === -1) return;
    cols.splice(fromIdx, 1);
    let toIdx = cols.indexOf(toKey);
    if (toIdx === -1) {
      cols.push(fromKey);
    } else {
      if (!insertBefore) toIdx += 1;
      cols.splice(toIdx, 0, fromKey);
    }
    CONTACTS.columns = cols;
    _renderContactsHead();
    _renderContactsTable(CONTACTS.rows || []);
    if (typeof _checkDirty === 'function') _checkDirty();
  }

  function _attachColReorder(th, colKey, ws = _CONTACTS_WS) {
    if (!th || !colKey) return;
    th.setAttribute('draggable', 'true');
    th.dataset.colKey = colKey;
    th.classList.add('col-draggable');

    th.addEventListener('dragstart', (ev) => {
      // Suppress drag if the user actually grabbed the resize handle. Browsers
      // hand dragstart to the th even when the mousedown started on the child
      // handle — guard via `body.col-resizing` (set by the resize handler).
      if (document.body.classList.contains('col-resizing')) {
        ev.preventDefault();
        return;
      }
      ev.dataTransfer.effectAllowed = 'move';
      ev.dataTransfer.setData('text/plain', colKey);
      th.classList.add('col-dragging');
      document.body.classList.add('col-reorder-active');
    });
    th.addEventListener('dragend', () => {
      th.classList.remove('col-dragging');
      document.body.classList.remove('col-reorder-active');
      document.querySelectorAll('.col-drop-before, .col-drop-after').forEach(el => {
        el.classList.remove('col-drop-before', 'col-drop-after');
      });
    });
    th.addEventListener('dragover', (ev) => {
      if (!document.body.classList.contains('col-reorder-active')) return;
      ev.preventDefault();
      ev.dataTransfer.dropEffect = 'move';
      const rect = th.getBoundingClientRect();
      const before = (ev.clientX - rect.left) < rect.width / 2;
      th.classList.toggle('col-drop-before', before);
      th.classList.toggle('col-drop-after', !before);
    });
    th.addEventListener('dragleave', () => {
      th.classList.remove('col-drop-before', 'col-drop-after');
    });
    th.addEventListener('drop', (ev) => {
      ev.preventDefault();
      const fromKey = ev.dataTransfer.getData('text/plain');
      const rect = th.getBoundingClientRect();
      const before = (ev.clientX - rect.left) < rect.width / 2;
      th.classList.remove('col-drop-before', 'col-drop-after');
      ws.reorderColumn(fromKey, colKey, before);
    });
  }

  function _renderContactsHead() {
    const thead = document.getElementById('contactsThead');
    if (!thead) return;
    clear(thead);
    const fillHeader = (th, label, sortKey, iconName = '') => {
      th.setAttribute('scope', 'col');
      clear(th);
      const inner = el('div', { class: 'contacts-th-inner' });
      if (iconName) inner.appendChild(el('i', { 'data-lucide': iconName, class: 'contacts-th-leading-icon' }));
      inner.appendChild(el('span', { class: 'contacts-th-label', text: label }));
      if (sortKey) {
        const active = CONTACTS.sortKey === sortKey;
        th.setAttribute('aria-sort', active ? (CONTACTS.sortDir === 'asc' ? 'ascending' : 'descending') : 'none');
        const sortIcon = !active ? 'arrow-up-down' : (CONTACTS.sortDir === 'asc' ? 'arrow-up' : 'arrow-down');
        const sortBtn = el('button', {
          type: 'button',
          class: 'contacts-sort-btn' + (active ? ' is-active' : ''),
          title: active ? `Sorted ${CONTACTS.sortDir === 'asc' ? 'ascending' : 'descending'}` : `Sort by ${label}`,
          'aria-label': `Sort by ${label}`,
        }, [el('i', { 'data-lucide': sortIcon })]);
        sortBtn.addEventListener('mousedown', ev => ev.stopPropagation());
        sortBtn.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _toggleContactsSort(sortKey); });
        inner.appendChild(sortBtn);
      }
      th.appendChild(inner);
    };
    const frozenN = CONTACTS.activeView?.frozenCount || 0;
    const cols = _visibleColumns();
    const tr = el('tr', {});
    // Checkbox col is always sticky-frozen at offset 0
    const thCheck = el('th', { class: 'frozen', scope: 'col', style: 'left:0px; min-width:36px;' }, [el('input', { type: 'checkbox', id: 'contactsSelectAll', 'aria-label': 'Select all contacts on this page' })]);
    tr.appendChild(thCheck);
    const nameFrozen = frozenN > 0;
    const thName = el('th', { class: nameFrozen ? 'frozen' : '', style: nameFrozen ? 'left:36px; min-width:160px;' : 'min-width:160px;' });
    fillHeader(thName, 'Name', 'auto_name');
    tr.appendChild(thName);
    _attachColResize(thName, 'auto_name');
    let leftOffset = 36 + 160;
    // "Our stage" = REAL pipeline stage, visible to everyone, sat right next to
    // "Lead Source". `stagePlaced` guards against a double-render; if lead_source
    // isn't a visible column it falls back to its old slot after the loop.
    let stagePlaced = false;
    const appendOurStageTh = () => {
      const thStage = el('th');
      fillHeader(thStage, 'Our stage', 'auto_our_stage');
      tr.appendChild(thStage);
      _attachColResize(thStage, 'auto_our_stage');
      stagePlaced = true;
    };
    cols.forEach((propName, i) => {
      const def = CONTACTS.propsByName[propName] || { name: propName, label: propName };
      const isFrozen = i < frozenN;
      const th = el('th', {
        class: isFrozen ? 'frozen' : '',
        style: isFrozen ? `left:${leftOffset}px;` : '',
      });
      // The phone column reads "Phone Number" (its HubSpot label) — Ayoub
      // 2026-06-11: only the computed real-WA column carries the WhatsApp
      // branding (glyph + "WhatsApp number"); both keep click→inbox cells.
      if (propName === 'whatsapp_number') th.classList.add('contacts-th-wa');
      fillHeader(th, propName === 'whatsapp_number' ? 'WhatsApp number' : (def.label || propName), propName, propName === 'whatsapp_number' ? 'message-circle' : '');
      tr.appendChild(th);
      _attachColResize(th, propName);
      _attachColReorder(th, propName);
      if (isFrozen) leftOffset += 160;
      if (propName === 'lead_source') appendOurStageTh();
    });
    if (!stagePlaced) appendOurStageTh();
    const thScore = el('th');
    fillHeader(thScore, 'Mr.Call score', 'auto_mr_call_score');
    tr.appendChild(thScore);
    _attachColResize(thScore, 'auto_mr_call_score');
    const thLevel = el('th');
    fillHeader(thLevel, 'Test level', 'auto_test_level');
    tr.appendChild(thLevel);
    _attachColResize(thLevel, 'auto_test_level');
    const thSummary = el('th');
    fillHeader(thSummary, 'Test summary', 'auto_test_summary');
    tr.appendChild(thSummary);
    _attachColResize(thSummary, 'auto_test_summary');
    const thOwner = el('th');
    fillHeader(thOwner, 'Owner', 'auto_owner');
    tr.appendChild(thOwner);
    _attachColResize(thOwner, 'auto_owner');
    const thAgent = el('th');
    fillHeader(thAgent, 'Agent', 'auto_agent');
    tr.appendChild(thAgent);
    _attachColResize(thAgent, 'auto_agent');
    const thActivity = el('th');
    fillHeader(thActivity, 'Activity', 'auto_activity');
    tr.appendChild(thActivity);
    _attachColResize(thActivity, 'auto_activity');
    const thTest = el('th');
    fillHeader(thTest, 'Test', 'auto_test');
    tr.appendChild(thTest);
    _attachColResize(thTest, 'auto_test');
    thead.appendChild(tr);
    refreshIcons();
  }

  function _renderContactsTable(rows) {
    const tbody = document.getElementById('contactsTbody');
    if (!tbody) return;
    clear(tbody);
    const frozenN = CONTACTS.activeView?.frozenCount || 0;
    const cols = _visibleColumns();
    for (const c of _sortedContactRows(rows)) {
      const name = ((c.firstname || '') + ' ' + (c.lastname || '')).trim() || '(no name)';
      // last-9 phone digits → lets a just-added contact be scrolled to / highlighted
      // (the same last-9 match the backend dedups on, so a 212-prefixed mirror row
      // still matches the locally-typed number).
      const _ph = String(c.phone || c.properties?.phone || c.mobilephone || c.properties?.mobilephone || '').replace(/\D/g, '');
      const selected = CONTACTS.selectedIds.has(String(c.id));
      const tr = el('tr', { class: selected ? 'is-selected' : '', 'data-contact-id': c.id, 'data-phone9': _ph.slice(-9) });
      const tdCheck = el('td', { class: 'frozen', style: 'left:0px;' }, [el('input', { type: 'checkbox', class: 'contact-row-check', 'aria-label': `Select ${name}`, ...(selected ? { checked: 'checked' } : {}) })]);
      tr.appendChild(tdCheck);
      const nameFrozen = frozenN > 0;
      const tdName = el('td', {
        class: 'contact-name-cell' + (nameFrozen ? ' frozen' : ''),
        style: nameFrozen ? 'left:36px;' : '',
        title: 'Open contact preview',
      });
      const initials = `${c.firstname?.[0] || ''}${c.lastname?.[0] || ''}`.trim().toUpperCase() || '?';
      const nameLink = el('button', { type: 'button', class: 'contact-name-link', text: name, title: `Preview ${name}` });
      nameLink.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _openContactPreviewDrawer(c); });
      const previewBtn = el('button', { type: 'button', class: 'contact-preview-btn', title: 'Preview contact', 'aria-label': `Preview ${name}` }, [el('i', { 'data-lucide': 'panel-right-open' })]);
      previewBtn.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _openContactPreviewDrawer(c); });
      const editBtn = el('button', { type: 'button', class: 'contact-name-edit-btn', title: 'Edit name', 'aria-label': `Edit ${name}` }, [el('i', { 'data-lucide': 'pencil' })]);
      editBtn.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _openNameEditor(editBtn, c); });
      tdName.appendChild(el('div', { class: 'contact-name-wrap' }, [
        el('span', { class: 'contact-name-avatar', text: initials, 'aria-hidden': 'true' }),
        nameLink,
        previewBtn,
        editBtn,
      ]));
      // Keep the production phone-quality signal visible without making the
      // whole name cell an edit target; preview and edit now have explicit
      // controls, matching the HubSpot-style interaction model.
      if (c.phone_status === 'wrong') {
        tdName.appendChild(el('span', {
          class: 'contact-fix-badge contact-fix-badge--wrong',
          text: 'Numéro erroné',
          title: "Ce numéro n'existe pas : soit WhatsApp l'a refusé, soit ces chiffres ne forment un numéro dans aucun pays. Inutile d'appeler — il faut le vrai numéro du client.",
        }));
      } else if (c.phone_needs_fix) {
        tdName.appendChild(el('span', {
          class: 'contact-fix-badge',
          text: 'N° à corriger',
          title: "Ce numéro n'est pas joignable sur WhatsApp — corrigez-le pour pouvoir écrire au client.",
        }));
      }
      tr.appendChild(tdName);
      let leftOffset = 36 + 160;
      // "Our stage" cell = REAL pipeline stage, mirrors the head: sits next to
      // the Lead Source cell, falls back to its old slot if lead_source is hidden.
      let stagePlaced = false;
      const appendOurStageTd = () => {
        const st = c.real_pipeline_stage || null;
        tr.appendChild(el('td', {}, [ st
          ? el('span', { class: 'contact-pill is-stage-' + st, text: STAGE_LABELS[st] || st })
          : el('span', { class: 'contact-pill contact-pill-empty', text: '—' }) ]));
        stagePlaced = true;
      };
      cols.forEach((propName, i) => {
        const cell = _buildContactCell(c, propName);
        if (i < frozenN) {
          cell.classList.add('frozen');
          cell.style.left = leftOffset + 'px';
          leftOffset += 160;
        }
        tr.appendChild(cell);
        if (propName === 'lead_source') appendOurStageTd();
      });
      if (!stagePlaced) appendOurStageTd();
      const score = c.lead?.metadata?.last_score_global;
      tr.appendChild(el('td', { class: 'coach-score-cell', text: score != null ? `${Number(score).toFixed(1)}/10` : '—' }));
      // CEFR placement-test level + AI feedback (from the website level test → lead metadata)
      const lvl = c.lead?.metadata?.level;
      const fb = String(c.lead?.metadata?.message || '');
      if (lvl) {
        const band = String(lvl).charAt(0).toUpperCase();
        const fg = band === 'A' ? '#059669' : band === 'B' ? '#2563eb' : band === 'C' ? '#7c3aed' : '#334155';
        const bg = band === 'A' ? '#ecfdf5' : band === 'B' ? '#eff6ff' : band === 'C' ? '#f5f3ff' : '#f1f5f9';
        const pill = el('span', { class: 'contact-pill', text: lvl, title: 'CEFR level ' + lvl, style: `background:${bg};color:${fg};font-weight:700;` });
        tr.appendChild(el('td', { class: 'test-level-cell' }, [pill]));
      } else {
        tr.appendChild(el('td', { class: 'test-level-cell', text: '—' }));
      }
      // Test summary (AI feedback) — truncated cell; full text on hover + click
      if (fb) {
        const sumCell = el('td', { class: 'test-summary-cell', text: fb, title: fb, style: 'max-width:300px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;cursor:pointer;' });
        sumCell.addEventListener('click', (ev) => { ev.stopPropagation(); toast(fb); });
        tr.appendChild(sumCell);
      } else {
        tr.appendChild(el('td', { class: 'test-summary-cell', text: '—' }));
      }
      tr.appendChild(_buildOwnerCell(c));
      // Agent (leads7) — the REAL handler of the lead (vs the HubSpot Owner).
      tr.appendChild(_buildAgentCell(c));
      // Activité — last activity time + last call outcome.
      tr.appendChild(_buildActivityCell(c));
      // Test — the latest booked placement test (date · teacher · status).
      tr.appendChild(_buildTestCell(c));
      tr.querySelectorAll('td').forEach((cell, index) => {
        if (index === 0) return;
        cell.addEventListener('click', (ev) => {
          document.querySelectorAll('#contactsTbody td.is-active-cell').forEach(td => td.classList.remove('is-active-cell'));
          cell.classList.add('is-active-cell');
          // HubSpot behaviour (Ayoub, 2026-08-15): ONE click anywhere in an
          // editable cell opens its editor — not "focus first, open on the
          // value text". Clicks that landed on a real control (the value
          // pill, WA/3CX buttons, links, the pencil) already did their job.
          if (typeof cell._contactsOpenEditor !== 'function') return;
          if (ev.target.closest('button, a, input, select, textarea, label')) return;
          cell._contactsOpenEditor();
        });
      });
      tbody.appendChild(tr);
    }
    refreshIcons();

    // After "Add contact", scroll the freshly-added row into view and flash it
    // so the agent can see it landed (matched by last-9 digits). One-shot.
    if (CONTACTS._highlightPhone) {
      const want = String(CONTACTS._highlightPhone).replace(/\D/g, '').slice(-9);
      CONTACTS._highlightPhone = null;
      const row = want && tbody.querySelector('tr[data-phone9="' + want + '"]');
      if (row) {
        row.scrollIntoView({ block: 'center', behavior: 'smooth' });
        // Flash via inline style (app.css is off-limits here) — a soft success
        // tint that fades back. Uses existing tokens so it's dark-mode-correct.
        const cells = row.querySelectorAll('td');
        cells.forEach(td => {
          td.style.transition = 'background-color .25s ease';
          td.style.backgroundColor = 'var(--success-soft, rgba(16,185,129,.14))';
        });
        setTimeout(() => cells.forEach(td => { td.style.backgroundColor = ''; }), 1800);
      }
    }
  }

  function _openContactPreviewDrawer(contact) {
    document.getElementById('contactsPreviewDrawer')?.remove();
    const name = ((contact.firstname || '') + ' ' + (contact.lastname || '')).trim() || '(no name)';
    const initials = `${contact.firstname?.[0] || ''}${contact.lastname?.[0] || ''}`.trim().toUpperCase() || '?';
    const properties = contact.properties || {};
    const manualWa = contact.properties?.whatsapp_phone || contact.whatsapp_phone || contact.whatsapp_number || '';
    const phone = contact.phone || contact.properties?.phone || manualWa || '';
    const groupValue = properties.groups || properties.group || properties.company || '';
    const jid = _validatedFunctionalChatJid(contact.chatJid)
      || ((_phoneForWhatsapp(manualWa || phone) || '') + '@s.whatsapp.net');
    const drawer = el('aside', { id: 'contactsPreviewDrawer', class: 'contacts-preview-drawer', role: 'dialog', 'aria-modal': 'false', 'aria-label': `Contact preview: ${name}` });
    const head = el('div', { class: 'contacts-preview-head' });
    const close = el('button', { type: 'button', class: 'contacts-preview-close', title: 'Close preview', 'aria-label': 'Close contact preview' }, [el('i', { 'data-lucide': 'x' })]);
    let onKey = null;
    const closeDrawer = () => {
      document.getElementById('contactsPreviewActions')?.remove();
      _closeContactInlineEditor();
      drawer.remove();
      if (onKey) document.removeEventListener('keydown', onKey);
    };
    close.addEventListener('click', closeDrawer);
    head.appendChild(el('div', { class: 'contacts-preview-titlebar' }, [
      el('h2', { text: name }),
      close,
    ]));

    const openActionsMenu = (anchor) => {
      document.getElementById('contactsPreviewActions')?.remove();
      const menu = el('div', { id: 'contactsPreviewActions', class: 'contacts-actions-menu contacts-preview-actions-menu' });
      const addMenuAction = (icon, label, handler) => {
        const button = el('button', { type: 'button' }, [el('i', { 'data-lucide': icon }), el('span', { text: label })]);
        button.addEventListener('click', () => { menu.remove(); handler(); });
        menu.appendChild(button);
      };
      addMenuAction('pencil', 'Edit contact name', () => _openNameEditor(drawer.querySelector('.contacts-preview-profile-name'), contact));
      addMenuAction('notebook-pen', 'Add a note', () => {
        const def = CONTACTS.propsByName.notes || CONTACTS.propsByName.data_notes || { name: 'notes', label: 'Notes', type: 'string', fieldType: 'textarea' };
        _openLongTextEditor(contact, def, def.name, properties[def.name] || contact[def.name] || '');
      });
      addMenuAction('x', 'Close preview', closeDrawer);
      document.body.appendChild(menu);
      const rect = anchor.getBoundingClientRect();
      menu.style.left = `${Math.max(8, rect.right - 220)}px`;
      menu.style.top = `${rect.bottom + 5}px`;
      refreshIcons(menu);
      setTimeout(() => {
        const off = (ev) => {
          if (!menu.contains(ev.target) && ev.target !== anchor) {
            menu.remove();
            document.removeEventListener('pointerdown', off, true);
          }
        };
        document.addEventListener('pointerdown', off, true);
      }, 0);
    };
    const navActions = el('button', { type: 'button', class: 'contacts-preview-nav-actions' }, [
      el('span', { text: 'Actions' }), el('i', { 'data-lucide': 'chevron-down' }),
    ]);
    navActions.addEventListener('click', () => openActionsMenu(navActions));
    head.appendChild(el('div', { class: 'contacts-preview-nav' }, [
      el('span', { class: 'contacts-preview-nav-active', text: 'View record' }),
      navActions,
    ]));

    const profileName = el('button', { type: 'button', class: 'contacts-preview-profile-name', title: 'Edit contact name' }, [
      el('strong', { text: name }), el('i', { 'data-lucide': 'pencil' }),
    ]);
    profileName.addEventListener('click', () => _openNameEditor(profileName, contact));
    const person = el('div', { class: 'contacts-preview-person' }, [
      el('span', { class: 'contacts-preview-avatar', text: initials, 'aria-hidden': 'true' }),
      el('div', { class: 'contacts-preview-person-copy' }, [
        profileName,
        el('span', { text: String(groupValue || phone || contact.email || properties.email || 'No contact details') }),
      ]),
    ]);
    head.appendChild(person);

    const actions = el('div', { class: 'contacts-preview-actions' });
    const action = (icon, label, fn) => {
      const btn = el('button', { type: 'button', class: 'contacts-preview-action', title: label }, [
        el('span', {}, [el('i', { 'data-lucide': icon })]),
        el('span', { text: label }),
      ]);
      btn.addEventListener('click', fn);
      actions.appendChild(btn);
      return btn;
    };
    action('notebook-pen', 'Note', () => {
      const def = CONTACTS.propsByName.notes || CONTACTS.propsByName.data_notes || { name: 'notes', label: 'Notes', type: 'string', fieldType: 'textarea' };
      _openLongTextEditor(contact, def, def.name, contact.properties?.[def.name] || contact[def.name] || '');
    });
    action('message-circle', 'WhatsApp', () => {
      if (!jid || jid === '@s.whatsapp.net') return toast('No valid WhatsApp number', 'warning');
      closeDrawer();
      openInboxForChatJid(jid, name);
    });
    const callBtn = action('phone', 'Call', () => {
      const digits = _phoneForWhatsapp(phone || manualWa);
      if (!digits) return toast('No valid phone number', 'warning');
      cxDial(digits, name, callBtn);
    });
    action('check-square', 'Task', () => {
      if (contact.lead?.id && typeof openReminderModal === 'function') openReminderModal(contact.lead.id);
      else toast('Open this contact in the Inbox to create a task', 'info');
    });
    action('calendar-plus', 'Meeting', () => toast('Open the contact conversation to book a placement test', 'info'));
    action('more-horizontal', 'More', () => openActionsMenu(actions.lastElementChild));
    head.appendChild(actions);
    drawer.appendChild(head);

    const body = el('div', { class: 'contacts-preview-body' });
    const propRows = [];
    const addedKeys = new Set();
    const addProp = (key, label, value, def = null) => {
      if (addedKeys.has(key)) return;
      addedKeys.add(key);
      let display = value == null || value === '' ? '--' : String(value);
      if (def?.options?.length && display !== '--' && def.fieldType !== 'checkbox' && def.fieldType !== 'multiplecheckbox') {
        display = def.options.find(option => String(option.value) === display)?.label || display;
      }
      let valueNode;
      if (display !== '--' && (def?.fieldType === 'checkbox' || def?.fieldType === 'multiplecheckbox' || display.includes(';'))) {
        const chips = display.split(';').map(part => part.trim()).filter(Boolean).map(part => {
          const optionLabel = def?.options?.find(option => String(option.value) === part)?.label || part;
          return el('span', { class: 'contacts-preview-value-chip', text: optionLabel });
        });
        valueNode = el('div', { class: 'contacts-preview-property-value is-multi' }, chips.length ? chips : [document.createTextNode('--')]);
      } else if (display !== '--' && ['student_status', 'hs_lead_status'].includes(key)) {
        valueNode = el('div', { class: 'contacts-preview-property-value is-multi' }, [
          el('span', { class: 'contacts-preview-value-chip', text: display }),
        ]);
      } else {
        valueNode = el('div', { class: 'contacts-preview-property-value', text: display });
      }
      propRows.push(el('div', { class: 'contacts-preview-property' }, [
        el('label', { text: label }),
        valueNode,
      ]));
    };
    addProp('phone', 'Phone number', phone, CONTACTS.propsByName.phone);
    addProp('hubspot_owner_id', 'Contact owner', contact.hubspot_owner_id ? (CONTACTS.ownersMap[contact.hubspot_owner_id] || contact.hubspot_owner_id) : 'Unassigned');
    addProp('email', 'Email', contact.email || properties.email, CONTACTS.propsByName.email);
    addProp('age', 'Age', contact.age ?? properties.age, CONTACTS.propsByName.age);
    addProp('student_status', 'Student status', contact.student_status ?? properties.student_status, CONTACTS.propsByName.student_status);
    addProp('profession', 'Profession', contact.profession ?? properties.profession, CONTACTS.propsByName.profession);
    addProp('city', 'City', contact.city ?? properties.city, CONTACTS.propsByName.city);
    addProp('groups', 'Groups', groupValue, CONTACTS.propsByName.groups);
    addProp('pack', 'Program', contact.pack ?? properties.pack, CONTACTS.propsByName.pack);
    addProp('hs_lead_status', 'Lead status', contact.hs_lead_status ?? properties.hs_lead_status, CONTACTS.propsByName.hs_lead_status);
    addProp('whatsapp_number', 'WhatsApp number', manualWa || contact.whatsapp_number, CONTACTS.propsByName.whatsapp_phone);
    addProp('lead_source', 'Lead source', _leadSourceMeta(contact.lead_source).label);
    addProp('real_pipeline_stage', 'Our stage', STAGE_LABELS[contact.real_pipeline_stage] || contact.real_pipeline_stage);
    for (const key of _visibleColumns().slice(0, 12)) {
      if (addedKeys.has(key)) continue;
      const def = CONTACTS.propsByName[key] || { label: key };
      const value = contact[key] !== undefined && contact[key] !== '' ? contact[key] : contact.properties?.[key];
      addProp(key, def.label || key, value, def);
    }
    const aboutActions = el('button', { type: 'button', class: 'contacts-preview-section-action' }, [
      el('span', { text: 'Actions' }), el('i', { 'data-lucide': 'chevron-down' }),
    ]);
    aboutActions.addEventListener('click', (ev) => {
      ev.preventDefault(); ev.stopPropagation(); openActionsMenu(aboutActions);
    });
    const aboutSettings = el('button', { type: 'button', class: 'contacts-preview-section-settings', title: 'Property settings', 'aria-label': 'Property settings' }, [
      el('i', { 'data-lucide': 'settings' }),
    ]);
    aboutSettings.addEventListener('click', (ev) => {
      ev.preventDefault(); ev.stopPropagation(); openContactsColumnsModal();
    });
    const about = el('details', { class: 'contacts-preview-section', open: 'open' }, [
      el('summary', {}, [
        el('span', { class: 'contacts-preview-section-title', text: 'About this contact' }),
        el('span', { class: 'contacts-preview-section-spacer' }),
        aboutActions,
        aboutSettings,
      ]),
      el('div', { class: 'contacts-preview-properties' }, propRows.length ? propRows : [el('div', { class: 'muted small', text: 'No properties available' })]),
    ]);
    body.appendChild(about);

    const activityParts = [];
    if (contact.last_activity_at) activityParts.push(`Last activity ${typeof _formatTimeAgo === 'function' ? _formatTimeAgo(contact.last_activity_at) : MA.loc(new Date(contact.last_activity_at), 'dt')}`);
    if (contact.last_call?.status) activityParts.push(`Last call: ${contact.last_call.status}`);
    if (contact.latest_test?.status) activityParts.push(`Placement test: ${contact.latest_test.status}`);
    body.appendChild(el('details', { class: 'contacts-preview-section', open: 'open' }, [
      el('summary', { text: 'Recent activity' }),
      el('div', { class: 'contacts-preview-activity', text: activityParts.join(' · ') || 'No recent activity found for this contact.' }),
    ]));

    const associations = [];
    if (groupValue) associations.push(el('div', { class: 'contacts-preview-activity', text: String(groupValue) }));
    body.appendChild(el('details', { class: 'contacts-preview-section' }, [
      el('summary', { text: 'Associations' }),
      ...(associations.length ? associations : [el('div', { class: 'contacts-preview-activity', text: 'No associated records.' })]),
    ]));
    drawer.appendChild(body);
    document.body.appendChild(drawer);
    refreshIcons();
    onKey = (ev) => {
      if (ev.key === 'Escape') {
        closeDrawer();
      }
    };
    document.addEventListener('keydown', onKey);
  }

  // Agent (leads7) cell — coloured chip with the real handler's name. Distinct
  // from the HubSpot Owner; "—" when the contact isn't a leads7 lead / unassigned.
  function _buildAgentCell(c) {
    const name = c.agent_name || '';
    if (!name) return el('td', { class: 'contact-agent-cell', text: '—' });
    const av = el('span', { class: 'contact-agent-av', text: (name.trim()[0] || '?').toUpperCase() });
    const lbl = el('span', { class: 'contact-agent-nm', text: name });
    return el('td', { class: 'contact-agent-cell' }, [el('span', { class: 'contact-agent-chip', title: 'Agent: ' + name }, [av, lbl])]);
  }

  // Activité cell — relative time of the last message + a last-call dot
  // (green answered / red missed). "—" when there's no inbox activity.
  function _buildActivityCell(c) {
    const ts = c.last_activity_at || 0;
    const lc = c.last_call || null;
    if (!ts && !lc) return el('td', { class: 'contact-activity-cell', text: '—' });
    const wrap = el('td', { class: 'contact-activity-cell' });
    const inner = el('span', { class: 'contact-activity', title: ts ? MA.loc(new Date(ts), 'dt', 'fr-FR') : '' });
    if (ts) inner.appendChild(el('span', { class: 'contact-activity-ago', text: (typeof _formatTimeAgo === 'function' ? _formatTimeAgo(ts) : '') }));
    if (lc) {
      const missed = /missed|rejected/.test(String(lc.status || ''));
      inner.appendChild(el('span', {
        class: 'contact-activity-call ' + (missed ? 'is-missed' : 'is-answered'),
        title: 'Dernier appel: ' + (lc.status || '') + (lc.direction ? ' (' + lc.direction + ')' : ''),
      }, [el('i', { 'data-lucide': missed ? 'phone-missed' : 'phone-call' })]));
    }
    wrap.appendChild(inner);
    return wrap;
  }

  // Test cell — latest booked test: date · teacher · status pill. "—" when none.
  function _buildTestCell(c) {
    const t = c.test || null;
    if (!t || !t.date) return el('td', { class: 'contact-test-cell', text: '—' });
    let dstr = '';
    try { dstr = MA.loc(new Date(t.date), 'd', 'fr-FR', { day: '2-digit', month: 'short' }); } catch { dstr = String(t.date).slice(0, 10); }
    const parts = [dstr];
    if (t.teacher) parts.push(t.teacher);
    const wrap = el('td', { class: 'contact-test-cell' });
    const inner = el('span', { class: 'contact-test', title: 'Test ' + (t.status || '') + (t.level ? ' · ' + t.level : '') + (t.score != null ? ' · ' + t.score : '') });
    inner.appendChild(el('span', { class: 'contact-test-meta', text: parts.join(' · ') }));
    if (t.status) inner.appendChild(el('span', { class: 'contact-test-status is-' + String(t.status).replace(/[^a-z_]/gi, ''), text: t.status }));
    wrap.appendChild(inner);
    return wrap;
  }

  // Phase E6.5 — open the leads7 inbox at a server-validated functional JID.
  // If no chat row exists yet (no prior WhatsApp messages from / to this
  // number), we inject a virtual placeholder so the conversation pane mounts.
  // The exact JID contract is important: display phones may be masked.
  async function openInboxForChatJid(chatJid, displayName) {
    const chatId = _validatedFunctionalChatJid(chatJid);
    if (!chatId) return false;
    const functionalPhone = chatId.split('@')[0];
    // Switch to inbox view (sidebar is mounted there)
    if (typeof showView === 'function') showView('inbox');
    else document.querySelector('[data-view="inbox"]')?.click();
    // Make sure chat list is loaded so navigateToChat can find it
    state.chats = state.chats || [];
    if (!state.chats.length && typeof loadConversations === 'function') {
      try { await loadConversations(); } catch { /* ok */ }
    }
    // Inject placeholder if missing
    if (!state.chats.find(c => c.id === chatId)) {
      state.chats.unshift({
        id: chatId,
        name: displayName || functionalPhone,
        phone: functionalPhone,
        lastMessage: '',
        timestamp: Date.now(),
        unread: 0,
        isGroup: false,
        _virtual: true,  // marker — re-renders won't drop it; real data will overwrite
      });
      if (typeof renderConversations === 'function') renderConversations();
    }
    // Brief delay so the view-switch + render settle, then select the chat
    setTimeout(() => {
      if (typeof navigateToChat === 'function') navigateToChat(chatId);
      else if (typeof selectConversation === 'function') selectConversation(chatId);
    }, 60);
    return true;
  }

  // Local/raw input wrapper. Never mint a virtual chat from a masked display
  // value (0620****26 -> 062026), a short fragment or a social object id.
  async function openInboxForPhone(phoneDigits, displayName) {
    const functionalPhone = _phoneForWhatsapp(phoneDigits);
    if (!functionalPhone) return false;
    return openInboxForChatJid(functionalPhone + '@s.whatsapp.net', displayName);
  }
  window.openInboxForChatJid = openInboxForChatJid;
  window.openInboxForPhone = openInboxForPhone;

  // Open the conversation of a call row (Calls strip, Calls page). `chatId` is
  // the functional JID and is never masked; `phone` IS masked (0707****00) for
  // an agent without "voir les numéros", and openInboxForPhone refuses a masked
  // value — so a click opened nothing for the sales team while it worked for
  // admins (Mme Mariem, 2026-10-12). Open by the JID first.
  async function openInboxForCall(call, displayName) {
    if (!call) return false;
    const jid = _validatedFunctionalChatJid(call.chatId);
    if (jid) {
      // Never let a new placeholder chat be titled with the full number.
      return openInboxForChatJid(jid, displayName || displayPhone(jid.split('@')[0]));
    }
    if (call.chatId && typeof selectConversation === 'function') {
      // Facebook / Instagram / SMS thread: not a phone JID, open it as it is.
      if (typeof showView === 'function') showView('inbox');
      selectConversation(call.chatId);
      return true;
    }
    return openInboxForPhone(call.phone, displayName);
  }
  // The number to dial for a call row: the JID's digits, never a masked phone.
  function callDialDigits(call) {
    const jid = _validatedFunctionalChatJid(call && call.chatId);
    if (jid) return jid.split('@')[0];
    const phone = String((call && call.phone) || '');
    return phone.includes('*') ? '' : phone.replace(/\D/g, '');
  }

  // ============ REST view — unpaid program balances ============
  // Ported from support2 2026-06-11 (consultation-only build: no row
  // selection, no bulk send, no kick-from-groups). Server endpoint
  // /api/hubspot/rest-items is per-agent scoped; this module renders
  // the list, re-buckets client-side per the agent's settings drawer,
  // and opens the student's inbox conversation on click.
  //
  // CRITICAL ordering: constants MUST be declared before
  // _restLoadSettings() runs (TDZ inside the IIFE — a const read in
  // its temporal dead zone throws and silently breaks the whole file).
  const REST_SETTINGS_DEFAULTS = {
    graceDays: 30,
    upcomingWindowDays: 30,
    pastEndedDays: 0,
    price6: 1500,
    price12: 2500,
    visibleCols: { bucket: true, program: true, amount: true, rest: true, first: true, days: true, status: true, last: true, restdate: true, actions: true },
  };
  const REST_COLUMN_DEFS = [
    { key: 'bucket',   label: 'Bucket' },
    { key: 'program',  label: 'Program' },
    { key: 'amount',   label: 'Paid · Owed' },
    { key: 'rest',     label: 'Rest' },
    { key: 'first',    label: 'First session' },
    { key: 'days',     label: 'Days left' },
    { key: 'status',   label: 'Status' },
    { key: 'last',     label: 'Last contact' },
    { key: 'restdate', label: 'Date of rest' },
    { key: 'actions',  label: 'Actions' },
  ];

  state.restItems = [];
  state.restBucket = 'all';
  state.restOwner = 'all'; // sales-admin owner filter; agents are server-scoped
  state.restSearch = '';
  state.restAfter = null;
  state.restLoading = false;
  state.restBucketCounts = { past: 0, current: 0, upcoming: 0, all: 0 };
  state.restSettings = _restLoadSettings();
  let _restInitDone = false;
  let _restSearchDebounce = null;

  // Relative "time-ago" formatter for the Last contact column.
  // <1m "just now" / <1h "Xm ago" / <24h "Xh ago" / <30d "Xd ago" / else date.
  function formatRelativeAgo(ts) {
    if (!ts) return '';
    const t = typeof ts === 'number' ? ts : Date.parse(ts);
    if (!t || isNaN(t)) return '';
    const diffMs = Date.now() - t;
    if (diffMs < 60000) return 'just now';
    const mins = Math.floor(diffMs / 60000);
    if (mins < 60) return mins + 'm ago';
    const hours = Math.floor(mins / 60);
    if (hours < 24) return hours + 'h ago';
    const days = Math.floor(hours / 24);
    if (days < 30) return days + 'd ago';
    const d = new Date(t);
    return MA.loc(d, 'd', 'en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }

  function _restLoadSettings() {
    try {
      const raw = localStorage.getItem('rest_settings_v1');
      if (!raw) return { ...REST_SETTINGS_DEFAULTS, visibleCols: { ...REST_SETTINGS_DEFAULTS.visibleCols } };
      const parsed = JSON.parse(raw);
      return {
        ...REST_SETTINGS_DEFAULTS,
        ...parsed,
        visibleCols: { ...REST_SETTINGS_DEFAULTS.visibleCols, ...(parsed.visibleCols || {}) },
      };
    } catch {
      return { ...REST_SETTINGS_DEFAULTS, visibleCols: { ...REST_SETTINGS_DEFAULTS.visibleCols } };
    }
  }
  function _restSaveSettings() {
    try { localStorage.setItem('rest_settings_v1', JSON.stringify(state.restSettings)); } catch { /* non-fatal */ }
  }

  // Client-side bucket recompute — the server buckets with default
  // thresholds; agent tweaks in Settings re-bucket without refetching.
  function _restRebucket(items) {
    const s = state.restSettings;
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const upcomingWindow = Math.max(0, s.upcomingWindowDays | 0);
    const pastEnded = Math.max(0, s.pastEndedDays | 0);
    return items.map(it => {
      const first = it.properties?.date_of_first_session ? new Date(it.properties.date_of_first_session) : null;
      const months = _restExtractMonths(it.properties?.pack_duration || it.programDuration || '');
      const endDate = (first && months) ? _restAddMonths(first, months) : null;
      let bucket = 'upcoming';
      if (!first) bucket = 'upcoming';
      else {
        const daysSinceFirst = Math.round((today - first) / 86400000);
        if (daysSinceFirst <= upcomingWindow) bucket = 'upcoming';
        else if (endDate && today <= endDate) bucket = 'current';
        else if (endDate) {
          const daysAfterEnd = Math.round((today - endDate) / 86400000);
          bucket = daysAfterEnd >= pastEnded ? 'past' : 'current';
        } else bucket = 'current';
      }
      // Pricing override if the agent edited price6/price12 in Settings.
      let expectedTotal = it.expectedTotal;
      if (months >= 4 && months <= 11) expectedTotal = s.price6;
      else if (months >= 12) expectedTotal = s.price12;
      else if (months >= 1 && months <= 3) expectedTotal = Math.round((s.price6 / 6) * months);
      const paidSum = it.paidSum || 0;
      const restOwed = it.restOwed > 0 ? it.restOwed : Math.max(0, (expectedTotal || 0) - paidSum);
      return { ...it, bucket, expectedTotal, restOwed };
    });
  }
  function _restExtractMonths(s) {
    if (!s) return 0;
    const str = String(s).toLowerCase();
    if (/\b1\s*an\b|\bun\s*an\b/.test(str)) return 12;
    const m = str.match(/(\d+)\s*mois/);
    if (m) return parseInt(m[1], 10) || 0;
    const justNum = str.match(/^(\d+)/);
    return justNum ? parseInt(justNum[1], 10) || 0 : 0;
  }
  function _restAddMonths(d, n) {
    const out = new Date(d);
    out.setMonth(out.getMonth() + n);
    return out;
  }

  function _restInitOnce() {
    if (_restInitDone) return;
    _restInitDone = true;
    document.querySelectorAll('#restBucketChips [data-bucket]').forEach(btn => {
      btn.addEventListener('click', () => {
        const b = btn.getAttribute('data-bucket') || 'all';
        if (state.restBucket === b) return;
        state.restBucket = b;
        document.querySelectorAll('#restBucketChips [data-bucket]').forEach(c => {
          const active = c.getAttribute('data-bucket') === b;
          c.classList.toggle('active', active);
          c.setAttribute('aria-selected', String(active));
        });
        renderRestTable();
      });
    });
    const search = document.getElementById('restSearch');
    if (search) {
      search.addEventListener('input', () => {
        clearTimeout(_restSearchDebounce);
        _restSearchDebounce = setTimeout(() => {
          state.restSearch = search.value.trim();
          loadRestItems({ resetPaging: true });
        }, 280);
      });
    }
    document.getElementById('restOwnerFilter')?.addEventListener('change', () => {
      const sel = document.getElementById('restOwnerFilter');
      state.restOwner = sel?.value || 'all';
      loadRestItems({ resetPaging: true });
    });
    document.getElementById('restRefreshBtn')?.addEventListener('click', () => loadRestItems({ resetPaging: true }));
    document.getElementById('restSettingsBtn')?.addEventListener('click', _restOpenSettings);
    document.getElementById('restSettingsClose')?.addEventListener('click', _restCloseSettings);
    document.getElementById('restSettingsScrim')?.addEventListener('click', _restCloseSettings);
    _restBindSettingsInputs();
    _restApplyVisibleColumns();
  }

  // Owner filter — sales-admins only. Options rebuilt on every Rest-view
  // entry so a late loadAssignableUsers() still populates it; the current
  // selection is restored from state so re-entering keeps the filter.
  function _restRenderOwnerFilter() {
    const wrap = document.getElementById('restOwnerWrap');
    const select = document.getElementById('restOwnerFilter');
    if (!wrap || !select) return;
    if (!isSalesAdminMe()) { wrap.classList.add('hidden'); return; }
    const agents = (state.assignableUsers || []).filter(u =>
      u.hubspotOwnerId && ['agent', 'super_sales'].includes(String(u.role || '').toLowerCase()));
    clear(select);
    select.appendChild(el('option', { value: 'all', text: 'Tous les agents' }));
    for (const u of agents) {
      select.appendChild(el('option', { value: String(u.hubspotOwnerId), text: userLabel(u.username, u.displayName || u.username) }));
    }
    select.value = state.restOwner || 'all';
    if (select.value !== (state.restOwner || 'all')) {
      // Stored owner no longer maps to an assignable agent — fall back.
      state.restOwner = 'all';
      select.value = 'all';
    }
    wrap.classList.remove('hidden');
    refreshIcons(wrap);
  }

  // ─── Settings drawer ───────────────────────────────────────
  function _restOpenSettings() {
    const panel = document.getElementById('restSettingsPanel');
    const scrim = document.getElementById('restSettingsScrim');
    if (!panel) return;
    panel.classList.remove('hidden');
    panel.setAttribute('aria-hidden', 'false');
    if (scrim) scrim.classList.remove('hidden');
    _restSyncSettingsUI();
    refreshIcons(panel);
  }
  function _restCloseSettings() {
    document.getElementById('restSettingsPanel')?.classList.add('hidden');
    document.getElementById('restSettingsPanel')?.setAttribute('aria-hidden', 'true');
    document.getElementById('restSettingsScrim')?.classList.add('hidden');
  }
  function _restSyncSettingsUI() {
    const s = state.restSettings;
    const grace = document.getElementById('restGraceSlider');
    const graceVal = document.getElementById('restGraceValue');
    if (grace) {
      grace.value = s.graceDays;
      grace.style.setProperty('--slider-fill', `${(s.graceDays / 90) * 100}%`);
    }
    if (graceVal) graceVal.textContent = s.graceDays === 1 ? '1 day' : `${s.graceDays} days`;
    const upDays = document.getElementById('restUpcomingDays');
    if (upDays) upDays.value = s.upcomingWindowDays;
    const pastDays = document.getElementById('restPastDays');
    if (pastDays) pastDays.value = s.pastEndedDays;
    const p6 = document.getElementById('restPrice6');
    if (p6) p6.value = s.price6;
    const p12 = document.getElementById('restPrice12');
    if (p12) p12.value = s.price12;
    _restRenderColumnToggles();
  }
  function _restRenderColumnToggles() {
    const container = document.getElementById('restColumnToggles');
    if (!container) return;
    clear(container);
    for (const def of REST_COLUMN_DEFS) {
      const isOn = state.restSettings.visibleCols[def.key] !== false;
      const row = el('div', { class: 'rest-toggle-row' + (isOn ? ' is-on' : ''), role: 'button', tabindex: '0', 'data-col': def.key });
      row.appendChild(el('span', { class: 'rest-toggle-row-label', text: def.label }));
      row.appendChild(el('span', { class: 'rest-toggle-switch' }));
      row.addEventListener('click', () => {
        state.restSettings.visibleCols[def.key] = !state.restSettings.visibleCols[def.key];
        _restSaveSettings();
        _restApplyVisibleColumns();
        row.classList.toggle('is-on', state.restSettings.visibleCols[def.key]);
      });
      row.addEventListener('keydown', (ev) => {
        if (ev.key === ' ' || ev.key === 'Enter') { ev.preventDefault(); row.click(); }
      });
      container.appendChild(row);
    }
  }
  function _restApplyVisibleColumns() {
    const shell = document.getElementById('restView');
    if (!shell) return;
    for (const def of REST_COLUMN_DEFS) {
      const hidden = state.restSettings.visibleCols[def.key] === false;
      shell.toggleAttribute('data-hide-' + def.key, hidden);
    }
  }
  function _restBindSettingsInputs() {
    const grace = document.getElementById('restGraceSlider');
    if (grace) {
      grace.addEventListener('input', () => {
        state.restSettings.graceDays = parseInt(grace.value, 10) || 0;
        grace.style.setProperty('--slider-fill', `${(state.restSettings.graceDays / 90) * 100}%`);
        const v = document.getElementById('restGraceValue');
        if (v) v.textContent = state.restSettings.graceDays === 1 ? '1 day' : `${state.restSettings.graceDays} days`;
      });
      grace.addEventListener('change', () => {
        _restSaveSettings();
        // Grace period changes the SERVER filter — refetch.
        loadRestItems({ resetPaging: true });
      });
    }
    const upDays = document.getElementById('restUpcomingDays');
    if (upDays) {
      upDays.addEventListener('change', () => {
        state.restSettings.upcomingWindowDays = parseInt(upDays.value, 10) || 0;
        _restSaveSettings();
        renderRestTable(); // client-side re-bucket, no refetch
      });
    }
    const pastDays = document.getElementById('restPastDays');
    if (pastDays) {
      pastDays.addEventListener('change', () => {
        state.restSettings.pastEndedDays = parseInt(pastDays.value, 10) || 0;
        _restSaveSettings();
        renderRestTable();
      });
    }
    const p6 = document.getElementById('restPrice6');
    if (p6) {
      p6.addEventListener('change', () => {
        state.restSettings.price6 = parseInt(p6.value, 10) || 0;
        _restSaveSettings();
        renderRestTable();
      });
    }
    const p12 = document.getElementById('restPrice12');
    if (p12) {
      p12.addEventListener('change', () => {
        state.restSettings.price12 = parseInt(p12.value, 10) || 0;
        _restSaveSettings();
        renderRestTable();
      });
    }
    const reset = document.getElementById('restSettingsReset');
    if (reset) {
      reset.addEventListener('click', () => {
        state.restSettings = { ...REST_SETTINGS_DEFAULTS, visibleCols: { ...REST_SETTINGS_DEFAULTS.visibleCols } };
        _restSaveSettings();
        _restSyncSettingsUI();
        _restApplyVisibleColumns();
        loadRestItems({ resetPaging: true });
      });
    }
  }

  async function loadRestItems({ resetPaging = false } = {}) {
    if (state.restLoading) return;
    state.restLoading = true;
    if (resetPaging) {
      state.restAfter = null;
      state.restItems = [];
    }
    const empty = document.getElementById('restEmpty');
    if (empty && resetPaging) empty.textContent = 'Loading…';
    try {
      const params = new URLSearchParams();
      params.set('bucket', 'all'); // bucket filter is client-side
      params.set('limit', '50');
      params.set('graceDays', String(state.restSettings.graceDays || 30));
      if (state.restOwner && state.restOwner !== 'all') params.set('ownerId', state.restOwner);
      if (state.restSearch) params.set('q', state.restSearch);
      if (state.restAfter) params.set('after', state.restAfter);
      const r = await api('/api/hubspot/rest-items?' + params.toString());
      const j = await r.json();
      if (!r.ok || j.error) {
        state.restLoading = false;
        if (empty) empty.textContent = 'Failed to load — ' + (j.error || ('HTTP ' + r.status));
        return;
      }
      if (j.ownerless) {
        state.restLoading = false;
        state.restItems = [];
        const table = document.getElementById('restTable');
        if (table) {
          clear(table);
          table.appendChild(el('div', { class: 'rest-empty', text: "Ton compte n'est pas encore lié à un owner HubSpot — demande à l'admin de te mapper (Team → owner mapping)." }));
        }
        return;
      }
      state.restItems = resetPaging ? (j.items || []) : [...state.restItems, ...(j.items || [])];
      state.restAfter = j.paging?.next?.after || null;
      renderRestTable();
    } catch (err) {
      if (empty) empty.textContent = 'Failed to load — ' + (err.message || err);
    } finally {
      state.restLoading = false;
    }
  }

  function renderRestTable() {
    const table = document.getElementById('restTable');
    if (!table) return;
    clear(table);

    // Re-bucket with the agent's settings; counts reflect their thresholds.
    const rebucketed = _restRebucket(state.restItems);
    const counts = { all: rebucketed.length, past: 0, current: 0, upcoming: 0 };
    for (const it of rebucketed) counts[it.bucket] = (counts[it.bucket] || 0) + 1;
    state.restBucketCounts = counts;
    for (const b of ['all', 'past', 'current', 'upcoming']) {
      const node = document.querySelector('[data-bucket-count="' + b + '"]');
      if (node) node.textContent = String(counts[b] ?? 0);
    }
    const visible = state.restBucket === 'all' ? rebucketed : rebucketed.filter(it => it.bucket === state.restBucket);

    const headerCount = document.getElementById('restCount');
    if (headerCount) {
      headerCount.textContent = visible.length ? `· ${visible.length} ${visible.length === 1 ? 'student' : 'students'}` : '';
    }

    if (!visible.length) {
      const msg = state.restSearch
        ? `No matches for "${state.restSearch}" in this bucket.`
        : (state.restBucket === 'all'
            ? 'Nothing here — every student in the filter window is paid up.'
            : `No students in the ${state.restBucket} bucket right now.`);
      table.appendChild(el('div', { class: 'rest-empty', text: msg }));
      const pager = document.getElementById('restPager');
      if (pager) clear(pager);
      return;
    }

    // Header row — same skeleton as body rows so grid columns line up.
    const header = el('div', { class: 'tickets-row tickets-row-header' });
    header.appendChild(el('div', { class: 'rest-cell-name', text: 'Student' }));
    header.appendChild(el('div', { class: 'rest-cell-bucket', text: 'Bucket' }));
    header.appendChild(el('div', { class: 'rest-cell-program', text: 'Program' }));
    header.appendChild(el('div', { class: 'rest-cell-amount', text: 'Paid · Owed' }));
    header.appendChild(el('div', { class: 'rest-cell-rest', text: 'Rest' }));
    header.appendChild(el('div', { class: 'rest-cell-first', text: 'First session' }));
    header.appendChild(el('div', { class: 'rest-cell-days', text: 'Days left' }));
    header.appendChild(el('div', { class: 'rest-cell-status', text: 'Status' }));
    header.appendChild(el('div', { class: 'rest-cell-last', text: 'Last contact' }));
    header.appendChild(el('div', { class: 'rest-cell-restdate', text: 'Date of rest' }));
    header.appendChild(el('div', { class: 'rest-cell-actions', text: '' }));
    table.appendChild(header);

    for (const it of visible) table.appendChild(buildRestRow(it));

    const pager = document.getElementById('restPager');
    if (pager) {
      clear(pager);
      if (state.restAfter) {
        const btn = el('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: 'Load more' });
        btn.addEventListener('click', () => loadRestItems({ resetPaging: false }));
        pager.appendChild(btn);
      }
    }

    refreshIcons(table);
  }

  function buildRestRow(item) {
    const row = el('div', {
      class: 'tickets-row rest-row-clickable',
      'data-rest-id': item.id,
      'data-bucket': item.bucket,
    });

    // Functional routing comes ONLY from the server-validated raw JID.
    // `item.phone` is display-only and masked for agents; deriving digits
    // from it created bogus short conversations such as 062026@s.whatsapp.net.
    const functionalJid = _validatedFunctionalChatJid(item.chatJid);
    const intl = functionalJid ? functionalJid.split('@')[0] : '';

    // Student name — clickable shortcut into the inbox conversation.
    const nameCell = el('div', { class: 'rest-cell-name' });
    if (intl) {
      const link = el('a', {
        href: '#', class: 'rest-name rest-name-link',
        title: 'Open chat with ' + item.name,
        text: item.name,
      });
      link.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        openInboxForChatJid(functionalJid, item.name || '');
      });
      nameCell.appendChild(link);
    } else {
      nameCell.appendChild(el('div', { class: 'rest-name', text: item.name }));
    }
    if (item.phone) nameCell.appendChild(el('div', { class: 'rest-phone', text: formatPhoneForDisplay(item.phone) }));
    row.appendChild(nameCell);

    // Row-level click → open chat (skip actions / links / buttons).
    if (intl) {
      row.style.cursor = 'pointer';
      row.addEventListener('click', (ev) => {
        if (ev.target.closest('.rest-cell-actions, .rest-cell-restdate, .rest-cell-rest, .rest-name-link, a, button, input')) return;
        openInboxForChatJid(functionalJid, item.name || '');
      });
    }

    // Bucket pill
    const bucketCell = el('div', { class: 'rest-cell-bucket' });
    bucketCell.appendChild(el('span', { class: 'rest-pill', 'data-bucket': item.bucket, text:
      item.bucket === 'past' ? 'Overdue' :
      item.bucket === 'current' ? 'Active' :
      'Upcoming' }));
    row.appendChild(bucketCell);

    // Program
    const programCell = el('div', { class: 'rest-cell-program' });
    programCell.appendChild(el('div', { class: 'rest-program-main', text: item.programDuration || '—' }));
    if (item.programHours) programCell.appendChild(el('div', { class: 'rest-program-hours', text: item.programHours + ' h' }));
    row.appendChild(programCell);

    // Amount stack: Paid / Total / Owed
    const amountCell = el('div', { class: 'rest-cell-amount' });
    const stack = el('div', { class: 'rest-amount-stack' });
    const paidLine = el('div', { class: 'rest-amount-paid' });
    paidLine.appendChild(el('span', { class: 'rest-amount-label', text: 'Paid' }));
    paidLine.appendChild(document.createTextNode((item.paidSum || 0) + ' DH'));
    stack.appendChild(paidLine);
    if (item.expectedTotal) {
      const expLine = el('div', { class: 'rest-amount-expected' });
      expLine.appendChild(el('span', { class: 'rest-amount-label', text: 'Total' }));
      expLine.appendChild(document.createTextNode(item.expectedTotal + ' DH'));
      stack.appendChild(expLine);
    }
    if (item.restOwed > 0) {
      const owedLine = el('div', { class: 'rest-amount-owed' });
      owedLine.appendChild(document.createTextNode(item.restOwed));
      owedLine.appendChild(el('span', { class: 'rest-amount-unit', text: 'DH owed' }));
      stack.appendChild(owedLine);
    }
    amountCell.appendChild(stack);
    row.appendChild(amountCell);

    // Rest — HubSpot's multi-checkbox enum of numeric tokens ("500;200"),
    // summed client-side (mirrors server sumEnumTokens); the multi-token
    // breakdown lives in the title attr. EDITABLE (same explicit-click
    // contract as the date-of-rest editor below — financial data): clicking
    // the value or the hover pencil swaps to a number input prefilled with
    // the SUM. Saving writes ONE token that REPLACES the whole multi-token
    // value — intended semantics: the team corrects the remaining amount.
    // The server ensures the enum option exists before the HubSpot PATCH.
    const restCell = el('div', { class: 'rest-cell-rest' });
    const restTokens = String(item.properties?.rest || '')
      .split(';').map(t => t.trim()).filter(Boolean);
    let restSum = 0;
    for (const t of restTokens) {
      const n = Number(t.replace(/[^\d.-]/g, ''));
      if (Number.isFinite(n)) restSum += n;
    }
    const restDisplay = el('div', { class: 'rest-restsum-display' });
    const restVal = restTokens.length
      ? el('div', { class: 'rest-restsum', text: restSum + ' DH', title: restTokens.length > 1 ? restTokens.join(' + ') : 'Modifier le montant Rest' })
      : el('div', { class: 'rest-restsum-empty', text: '—', title: 'Ajouter un montant Rest' });
    const restEditBtn = el('button', {
      class: 'rest-rest-editbtn', type: 'button',
      title: 'Modifier le montant Rest', 'aria-label': 'Modifier le montant Rest',
    });
    restEditBtn.appendChild(lucideIcon('pencil'));
    restDisplay.appendChild(restVal);
    restDisplay.appendChild(restEditBtn);

    const restEditor = el('div', { class: 'rest-rest-editor hidden' });
    const restInput = el('input', {
      type: 'number', class: 'rest-rest-input', min: '0', step: '50',
      placeholder: 'DH', 'aria-label': 'Montant Rest (DH)',
    });
    const restSaveBtn = el('button', {
      class: 'rest-rest-save', type: 'button', text: 'Save',
      title: 'Enregistrer — remplace le montant Rest sur HubSpot',
    });
    const restCancelBtn = el('button', {
      class: 'rest-rest-cancel', type: 'button', text: '✕',
      title: 'Annuler', 'aria-label': 'Annuler la modification du Rest',
    });
    const restEnterEdit = () => {
      restDisplay.classList.add('hidden');
      restEditor.classList.remove('hidden');
      restInput.value = restTokens.length ? String(restSum) : '';
      restInput.focus();
      try { restInput.select(); } catch { /* number inputs may not support select() everywhere */ }
    };
    const restExitEdit = () => {
      restEditor.classList.add('hidden');
      restDisplay.classList.remove('hidden');
      restSaveBtn.disabled = false;
      restSaveBtn.textContent = 'Save';
    };
    restVal.addEventListener('click', (ev) => { ev.stopPropagation(); restEnterEdit(); });
    restEditBtn.addEventListener('click', (ev) => { ev.stopPropagation(); restEnterEdit(); });
    restCancelBtn.addEventListener('click', (ev) => { ev.stopPropagation(); restExitEdit(); });
    restInput.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') { ev.stopPropagation(); restExitEdit(); }
      else if (ev.key === 'Enter') { ev.preventDefault(); restSaveBtn.click(); }
    });
    restSaveBtn.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      if (restSaveBtn.disabled) return;
      const raw = restInput.value.trim();
      const val = Number(raw);
      // Integers only — the HubSpot enum options are integer strings; a
      // decimal would mint a junk option like "450.5".
      if (raw === '' || !Number.isFinite(val) || val < 0 || !Number.isInteger(val)) {
        toast('Montant Rest invalide — entrez un nombre entier ≥ 0', 'error');
        return;
      }
      restSaveBtn.disabled = true;
      restSaveBtn.textContent = '…';
      try {
        const r = await api('/api/hubspot/contacts/' + encodeURIComponent(item.id), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ properties: { rest: String(val) } }),
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          toast('Échec — ' + (j.error || ('HTTP ' + r.status)), 'error');
          restSaveBtn.disabled = false;
          restSaveBtn.textContent = 'Save';
          return; // stay in edit mode so the value isn't lost
        }
        // 0 is allowed but doesn't remove the row (only date_of_rest does) —
        // nudge toward the Date-of-rest editor if the money was collected.
        toast(val === 0
          ? '⚠️ Rest = 0 — pensez à remplir Date of rest si encaissé'
          : 'Montant Rest mis à jour', 'success');
        // Mutate the CANONICAL state item — buildRestRow receives a shallow
        // _restRebucket copy (shared `properties` ref, own `restOwed`).
        const stItem = state.restItems.find(x => x.id === item.id) || item;
        stItem.properties = stItem.properties || {};
        stItem.properties.rest = String(val);
        // Owed mirrors server enrichRestItem: rest when > 0, else the
        // expected−paid fallback (_restRebucket recomputes exactly that
        // when restOwed is 0).
        stItem.restOwed = val > 0 ? val : 0;
        renderRestTable();
      } catch (err) {
        toast('Échec — ' + (err.message || 'network error'), 'error');
        restSaveBtn.disabled = false;
        restSaveBtn.textContent = 'Save';
      }
    });
    restEditor.appendChild(restInput);
    restEditor.appendChild(restSaveBtn);
    restEditor.appendChild(restCancelBtn);
    restCell.appendChild(restDisplay);
    restCell.appendChild(restEditor);
    row.appendChild(restCell);

    // First session + program end
    const firstCell = el('div', { class: 'rest-cell-first' });
    firstCell.appendChild(el('div', { class: 'rest-date-main', text: item.properties?.date_of_first_session
      ? formatChatDate(item.properties.date_of_first_session) : '—' }));
    if (item.programEndDate) {
      firstCell.appendChild(el('div', { class: 'rest-date-sub', text: 'ends ' + formatChatDate(item.programEndDate) }));
    }
    row.appendChild(firstCell);

    // Days left / overdue
    const daysCell = el('div', { class: 'rest-cell-days' });
    if (item.daysUntilEnd === null || item.daysUntilEnd === undefined) {
      daysCell.appendChild(el('div', { class: 'rest-days-empty', text: '—' }));
    } else if (item.daysUntilEnd < 0) {
      daysCell.appendChild(el('div', { class: 'rest-days-overdue', text: Math.abs(item.daysUntilEnd) + ' d overdue' }));
    } else {
      daysCell.appendChild(el('div', { class: 'rest-days-left', text: item.daysUntilEnd + ' d left' }));
    }
    row.appendChild(daysCell);

    // Status (paid_status tag + student_status sub)
    const statusCell = el('div', { class: 'rest-cell-status' });
    const statusWrap = el('div', { class: 'rest-status' });
    if (item.properties?.paid_status) {
      statusWrap.appendChild(el('div', { class: 'rest-status-tag', text: item.properties.paid_status }));
    }
    if (item.properties?.student_status) {
      statusWrap.appendChild(el('div', { class: 'rest-status-sub', text: item.properties.student_status }));
    }
    statusCell.appendChild(statusWrap);
    row.appendChild(statusCell);

    // Last contact — relative time + source badge (no timeline modal in Leads7).
    const lastCell = el('div', { class: 'rest-cell-last' });
    if (item.lastContact?.at) {
      const lcWrap = el('div', { class: 'rest-lastcontact' });
      lcWrap.appendChild(el('div', { class: 'rest-lastcontact-ago', text: formatRelativeAgo(item.lastContact.at) }));
      lcWrap.appendChild(el('div', {
        class: 'rest-lastcontact-source rest-lastcontact-source-' + (item.lastContact.source || 'unknown'),
        text: item.lastContact.source === 'whatsapp' ? 'WhatsApp'
            : item.lastContact.source === '3cx' ? '3CX'
            : 'HubSpot',
      }));
      lastCell.appendChild(lcWrap);
    } else {
      lastCell.appendChild(el('div', { class: 'rest-lastcontact-empty', text: '—' }));
    }
    row.appendChild(lastCell);

    // Date of rest — inline editor. CRITICAL SEMANTICS: the server list
    // filter is `date_of_rest NOT_HAS_PROPERTY`, so saving a date means
    // "rest settled" and the student legitimately LEAVES this page. The
    // save button only appears once a date is picked (explicit click, no
    // silent on-blur write — this touches financial data), and success
    // removes the row + re-renders so the counts update.
    const dorCell = el('div', { class: 'rest-cell-restdate' });
    if (item.properties?.date_of_rest) {
      // Rare — the server filter excludes settled students, but pagination
      // races can surface one. Show it read-only.
      dorCell.appendChild(el('div', { class: 'rest-date-main', text: formatChatDate(item.properties.date_of_rest) }));
    } else {
      const dorEditor = el('div', { class: 'rest-dor-editor' });
      // max=today: a past-month date desyncs the Objectif (the sync worker only
      // reconciles the current month) and a future date removes the student
      // before the money counts — clamp to today (review finding, 2026-07-11).
      const dorInput = el('input', { type: 'date', class: 'rest-dor-input', 'aria-label': 'Date of rest', max: MA.loc(new Date(), 'd', 'en-CA') });
      const dorSave = el('button', {
        class: 'rest-dor-save hidden', type: 'button', text: 'Save',
        title: 'Enregistrer — le rest est encaissé, l’étudiant quitte cette liste',
      });
      dorInput.addEventListener('input', () => {
        dorSave.classList.toggle('hidden', !dorInput.value);
      });
      dorSave.addEventListener('click', async (ev) => {
        ev.stopPropagation();
        const val = dorInput.value;
        if (!val || dorSave.disabled) return;
        // Reject out-of-range picks typed manually (the max attr only guards the picker).
        const todayIso = MA.loc(new Date(), 'd', 'en-CA');
        if (val > todayIso) { toast('La date ne peut pas être dans le futur', 'error'); return; }
        dorSave.disabled = true;
        dorSave.textContent = '…';
        try {
          const r = await api('/api/hubspot/contacts/' + encodeURIComponent(item.id), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ properties: { date_of_rest: val } }),
          });
          if (!r.ok) {
            const j = await r.json().catch(() => ({}));
            toast('Échec — ' + (j.error || ('HTTP ' + r.status)), 'error');
            dorSave.disabled = false;
            dorSave.textContent = 'Save';
            return;
          }
          // Honest toast: with an empty Rest amount the sync worker has nothing
          // to credit — the student still leaves the list, but no money lands
          // in the Objectif (review finding, 2026-07-11).
          toast(restSum > 0
            ? 'Date of rest enregistrée — étudiant retiré de la liste (rest encaissé)'
            : 'Date enregistrée — ⚠️ montant Rest vide sur HubSpot, rien ne sera compté dans l’Objectif', 'success');
          state.restItems = state.restItems.filter(x => x.id !== item.id);
          renderRestTable();
        } catch (err) {
          toast('Échec — ' + (err.message || 'network error'), 'error');
          dorSave.disabled = false;
          dorSave.textContent = 'Save';
        }
      });
      dorEditor.appendChild(dorInput);
      dorEditor.appendChild(dorSave);
      dorCell.appendChild(dorEditor);
    }
    row.appendChild(dorCell);

    // Actions: open chat + HubSpot record
    const actionsCell = el('div', { class: 'rest-cell-actions' });
    const actions = el('div', { class: 'rest-actions' });
    if (intl) {
      const chatBtn = el('button', { class: 'rest-action-btn', type: 'button', title: 'Open WhatsApp chat', 'aria-label': 'Open chat' });
      chatBtn.appendChild(lucideIcon('message-circle'));
      chatBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        openInboxForChatJid(functionalJid, item.name || '');
      });
      actions.appendChild(chatBtn);
    }
    const hubBtn = el('a', { class: 'rest-action-btn', href: item.url, target: '_blank', rel: 'noopener', title: 'Open in HubSpot', 'aria-label': 'Open in HubSpot' });
    hubBtn.appendChild(lucideIcon('external-link'));
    actions.appendChild(hubBtn);
    actionsCell.appendChild(actions);
    row.appendChild(actionsCell);

    return row;
  }

  // ════ Virements — payment verification vs bank statements (2026-06-12) ════
  // System-admin view (#verifyPaymentView, data-system-admin-only): the
  // "À vérifier" queue of payment claims (agent-flagged receipts + paid
  // encaissements) sits above the imported bank statement lines. Suggestions
  // come server-side from lib/payments/matching.js; confirming a claim locks
  // it to a bank credit. The bubble receipt-flag + pipeline pv-chip below are
  // visible to EVERY agent — only this view is admin-gated.
  let _vpInitDone = false;
  // true → admin / super_admin / super_sales (full view: import, scan, bank-tx
  // panel, confirm/reject/link, receipt-upload). false → a sales agent who sees
  // ONLY her own claims, read-only (no admin controls, no bank-tx load). Set on
  // every initVerifyPayment() so a role change between view opens is honoured.
  let _vpAdmin = false;
  let _vpAccount = 'all';
  // (swat:V2) Certainty chip filter — memory only, survives reloads of the
  // queue but not a page refresh. Buckets come from _vpClaimBucket.
  let _vpMatchFilter = 'all';
  let _vpMonth = (() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
  })();
  let _vpQueueItems = [];
  let _vpQueueLoading = false;
  let _vpBankItems = [];
  let _vpBankTotal = 0;
  let _vpBankReqId = 0;
  let _vpImportAnalyzed = false;
  let _vpImportTab = 'file';
  let _vpImportBusy = false;
  let _vpLinkClaim = null;
  let _vpLinkSearchDebounce = null;

  const VP_ACCOUNT_LABELS = { lougha: 'Lougha', loghat: 'LOGHAT', fatima: 'Fatima', houda: 'Houda', stripe: 'Stripe', whop: 'Whop' };

  // Keep in sync with AMOUNT_PENDING_NOTE in lib/payments/verifications.js —
  // the marker stored in match_note when a flagged receipt's amount couldn't
  // be read (claimed_amount then holds a 1.00 placeholder). (swat:B8)
  const VP_AMOUNT_PENDING_NOTE = 'montant en attente (extraction reçue/édition manuelle)';
  function _vpIsAmountPending(claim) {
    return claim.match_note === VP_AMOUNT_PENDING_NOTE || !(Number(claim.claimed_amount) > 1);
  }
  // Stranded extraction (swat:B17, shared by the row builder + V2 buckets):
  // a 'pending' extract older than 5 min (mirrors the server threshold) is
  // treated as stuck.
  function _vpExtractStale(claim) {
    return claim.extract_status === 'pending'
      && (Date.now() - new Date(claim.updated_at || 0).getTime() > 5 * 60000);
  }
  // Disjoint review buckets for the overview. Cash/remittance services get a
  // dedicated operational queue once the receipt/payment form identifies the
  // provider. A broken/amount-less receipt still stays in "problem" first.
  function _vpClaimBucket(claim) {
    if (_vpIsAmountPending(claim) || claim.extract_status === 'failed' || _vpExtractStale(claim)) return 'problem';
    if (claim.cash_transfer_service) return 'cash';
    const suggs = claim.suggestions || [];
    if (suggs.some(s => s.certainty === 'sure')) return 'sure';
    if (suggs.length) return 'ambiguous';
    return 'none';
  }

  function _vpOutcome(claim) {
    const bucket = _vpClaimBucket(claim);
    const top = (claim.suggestions || [])[0] || null;
    const why = (top && top.why) || {};
    if (bucket === 'problem') {
      if (claim.extract_status === 'pending' && !_vpExtractStale(claim)) {
        return { bucket, icon: 'loader-circle', title: 'Reading receipt', reason: 'The document is being analyzed.', top };
      }
      if (claim.extract_status === 'failed' || _vpExtractStale(claim)) {
        return { bucket, icon: 'triangle-alert', title: 'Unreadable or stuck receipt', reason: 'Retry the receipt scan or correct the details.', top };
      }
      return { bucket, icon: 'triangle-alert', title: 'Amount required', reason: 'The amount could not be read from the receipt.', top };
    }
    if (bucket === 'cash') {
      const service = claim.cash_transfer_service || 'Transfer service';
      return {
        bucket, icon: 'hand-coins', title: service + ' — review payment',
        reason: 'Transfer service recognized. Check the receipt, then confirm the correct client.', top,
      };
    }
    if (bucket === 'sure') {
      return {
        bucket, icon: 'shield-check', title: 'Payment found and identified',
        reason: 'Exact amount, consistent date, and one unique match.', top,
      };
    }
    if (bucket === 'ambiguous') {
      let reason = 'A possible credit exists, but there is no unique proof.';
      const shared = why.sharedWith | 0;
      const alternatives = why.altCandidates | 0;
      if (shared > 0) {
        reason = `The same credit may match ${shared + 1} client records.`;
      } else if (alternatives > 0) {
        reason = `${alternatives + 1} bank credits are possible.`;
      } else if (why.nameStatus === 'mismatch') {
        reason = 'The transfer name does not match the declared payer.';
      } else if (why.dateGapDays != null && (why.dateGapDays < -3 || why.dateGapDays > 10)) {
        reason = `The bank date is ${Math.abs(why.dateGapDays)} days away.`;
      }
      return { bucket, icon: 'circle-help', title: 'Possible payment — identify client', reason, top };
    }
    return {
      bucket, icon: 'search-x', title: 'Not found in statement',
      reason: 'No matching bank credit has been found for this payment yet.', top: null,
    };
  }

  function _vpVisibleDecidedItems(kind) {
    const items = _vpDecided[kind]?.items || [];
    return items.filter((claim) => {
      if (_vpAccount === 'all') return true;
      const account = claim.matched_tx?.account || claim.claimed_account || null;
      return !account || account === _vpAccount;
    });
  }

  function _vpRenderOverview(bucketCounts = null, visibleCount = null) {
    if (!_vpAdmin) return;
    const visible = _vpQueueItems.filter(c =>
      _vpAccount === 'all' || !c.claimed_account || c.claimed_account === _vpAccount);
    const counts = bucketCounts || { sure: 0, cash: 0, ambiguous: 0, none: 0, problem: 0 };
    if (!bucketCounts) {
      for (const claim of visible) counts[_vpClaimBucket(claim)]++;
    }
    const total = visibleCount == null ? visible.length : visibleCount;
    const allCount = document.querySelector('#vpMatchChips [data-match-n="all"]');
    if (allCount) allCount.textContent = String(total);
    for (const key of ['sure', 'cash', 'ambiguous', 'none', 'problem']) {
      const countEl = document.querySelector('#vpMatchChips [data-match-n="' + key + '"]');
      if (countEl) countEl.textContent = String(counts[key] || 0);
    }
    const sureCard = document.querySelector('#vpMatchChips [data-match="sure"]');
    if (sureCard) sureCard.classList.toggle('hidden', !(counts.sure > 0));
    const verifiedCount = document.getElementById('vpOverviewVerifiedCount');
    if (verifiedCount) {
      const ready = _vpDecided.verified.month === _vpMonth;
      verifiedCount.textContent = ready ? String(_vpVisibleDecidedItems('verified').length) : '…';
    }
  }

  function _vpFmtMoney(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    return v.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  function _vpFmtDate(d) {
    if (!d) return '—';
    // Postgres DATE arrives as 'YYYY-MM-DD' (often with a T00:00:00.000Z
    // suffix) — read the calendar date straight off the string so timezones
    // can never shift it, and never hand it to the chat-relative formatter
    // (which renders '01:00' / 'Mon' instead of an actual date).
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
    if (m) return m[3] + '/' + m[2] + '/' + m[1];
    try {
      return MA.loc(new Date(d), 'd', 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return String(d); }
  }
  function _vpAccountLabel(a) { return VP_ACCOUNT_LABELS[a] || (a ? String(a) : '—'); }

  // Pipeline-bar chip — pending beats verified (a fresh receipt on an
  // already-verified lead still needs review); null/zero claims → no chip.
  function _pvBuildChip(pv) {
    if (!pv) return null;
    if ((pv.pending | 0) > 0) {
      return el('span', {
        class: 'pv-chip pv-pending',
        title: pv.pending + ' receipt(s) awaiting payment verification',
        text: 'Pending 🟡',
      });
    }
    if ((pv.verified | 0) > 0) {
      return el('span', {
        class: 'pv-chip pv-verified',
        title: 'Payment verified against the bank statement',
        text: 'Verified ✅',
      });
    }
    return null;
  }

  // ============ Reminders — pipeline-bar chip (Rappels, 2026-06-13) ============
  // lead.reminders = { next:<earliest pending remind_at|null>, overdueCount,
  // pendingCount }. Renders nothing when there are no pending reminders.
  // Overdue gets a distinct (warning) tone; otherwise neutral/info. Click →
  // a small popover with the exact date+time and a "Terminer" shortcut.
  // Caller already wraps this in try/catch — keep it defensive anyway.
  function _reminderRelative(remindAt) {
    if (!remindAt) return '';
    const ts = new Date(remindAt).getTime();
    if (!Number.isFinite(ts)) return '';
    const diff = ts - Date.now();
    const abs = Math.abs(diff);
    const mins = Math.round(abs / 60000);
    const fmt = () => {
      if (mins < 1) return "à l'instant";
      if (mins < 60) return mins + ' min';
      const h = Math.round(mins / 60);
      if (h < 24) return h + ' h';
      const d = Math.round(h / 24);
      return d + ' j';
    };
    return diff < 0 ? ('il y a ' + fmt()) : ('dans ' + fmt());
  }
  function _reminderFmtExact(remindAt) {
    if (!remindAt) return '';
    const d = new Date(remindAt);
    if (isNaN(d.getTime())) return String(remindAt);
    return MA.loc(d, 'dt', undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }
  function _reminderBuildChip(rem, leadId) {
    if (!rem) return null;
    const overdue = (rem.overdueCount | 0) > 0;
    const pending = (rem.pendingCount | 0) > 0;
    if (!pending && !overdue) return null;
    const count = pending ? (rem.pendingCount | 0) : (rem.overdueCount | 0);
    const rel = rem.next ? _reminderRelative(rem.next) : '';
    const label = overdue
      ? ('Rappel en retard' + (count > 1 ? ' (' + count + ')' : ''))
      : ('Rappel' + (count > 1 ? ' (' + count + ')' : '') + (rel ? ' · ' + rel : ''));
    const chip = el('button', {
      class: 'rem-chip' + (overdue ? ' rem-chip-overdue' : ''),
      type: 'button',
      title: rem.next ? ('Prochain rappel : ' + _reminderFmtExact(rem.next)) : 'Rappel en attente',
    }, [lucideIcon('bell'), el('span', { text: label })]);
    chip.addEventListener('click', (ev) => {
      ev.stopPropagation();
      _openReminderChipPopover(chip, leadId, rem);
    });
    return chip;
  }

  // Small popover anchored to the reminder chip. Shows the next due exact
  // time + a "Terminer" that PATCHes the earliest pending reminder to done,
  // then busts the pipeline cache and re-renders the bar. Open the full
  // Rappels view for the rest.
  let _remChipPopEl = null;
  function _closeReminderChipPopover() {
    document.removeEventListener('click', _remChipPopOutside);
    if (_remChipPopEl && _remChipPopEl.parentNode) _remChipPopEl.parentNode.removeChild(_remChipPopEl);
    _remChipPopEl = null;
  }
  function _remChipPopOutside(ev) {
    if (!_remChipPopEl) return;
    if (_remChipPopEl.contains(ev.target)) return;
    _closeReminderChipPopover();
  }
  function _openReminderChipPopover(anchorEl, leadId, rem) {
    _closeReminderChipPopover();
    const pop = el('div', { class: 'rem-pop' });
    pop.appendChild(el('div', { class: 'rem-pop-title', text: (rem.overdueCount | 0) > 0 ? 'Rappel en retard' : 'Prochain rappel' }));
    if (rem.next) pop.appendChild(el('div', { class: 'rem-pop-when' }, [lucideIcon('clock'), el('span', { text: _reminderFmtExact(rem.next) })]));
    const pCount = rem.pendingCount | 0;
    if (pCount > 1) pop.appendChild(el('div', { class: 'rem-pop-sub', text: pCount + ' rappels en attente' }));
    const actions = el('div', { class: 'rem-pop-actions' });
    const doneBtn = el('button', { class: 'btn btn-sm', type: 'button' }, [lucideIcon('check'), el('span', { text: 'Terminer' })]);
    doneBtn.addEventListener('click', async () => {
      doneBtn.disabled = true;
      const ok = await _reminderCompleteNextForLead(leadId);
      _closeReminderChipPopover();
      if (ok) {
        const chatId = state.selectedChatId;
        if (chatId) { delete pipelineLeadCache[chatId]; await renderPipelineBar(chatId).catch(() => {}); }
      } else { doneBtn.disabled = false; }
    });
    const openBtn = el('button', { class: 'btn btn-sm btn-ghost', type: 'button' }, [lucideIcon('bell'), el('span', { text: 'Voir les rappels' })]);
    openBtn.addEventListener('click', () => { _closeReminderChipPopover(); try { showView('reminders'); } catch (_) {} });
    actions.appendChild(doneBtn);
    actions.appendChild(openBtn);
    pop.appendChild(actions);
    document.body.appendChild(pop);
    pop.style.position = 'fixed';
    const r = anchorEl.getBoundingClientRect();
    pop.style.top = (r.bottom + 6) + 'px';
    let left = r.left;
    const w = 260, margin = 8;
    if (left + w + margin > window.innerWidth) left = window.innerWidth - w - margin;
    if (left < margin) left = margin;
    pop.style.left = left + 'px';
    refreshIcons(pop);
    _remChipPopEl = pop;
    setTimeout(() => document.addEventListener('click', _remChipPopOutside), 0);
  }

  // PATCH the earliest pending reminder for a lead to done. Used by the chip
  // popover's "Terminer". Looks the reminder up via /api/reminders scoped to
  // the lead. Returns true on success.
  async function _reminderCompleteNextForLead(leadId) {
    if (!leadId) return false;
    try {
      const r = await api('/api/reminders?filter=upcoming&lead_id=' + encodeURIComponent(leadId));
      const j = await r.json().catch(() => ({}));
      let list = (j.reminders || []).filter(x => x.status === 'pending');
      if (!list.length) {
        // Maybe the next-due one is already overdue — pull those too.
        const r2 = await api('/api/reminders?filter=overdue&lead_id=' + encodeURIComponent(leadId));
        const j2 = await r2.json().catch(() => ({}));
        list = (j2.reminders || []).filter(x => x.status === 'pending');
      }
      if (!list.length) { toast('Aucun rappel en attente'); return false; }
      list.sort((a, b) => new Date(a.remind_at).getTime() - new Date(b.remind_at).getTime());
      const target = list[0];
      const pr = await api('/api/reminders/' + encodeURIComponent(target.id), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'done' }),
      });
      if (!pr.ok) { const e = await pr.json().catch(() => ({})); throw new Error(e.error || ('HTTP ' + pr.status)); }
      toast('Rappel terminé ✓', 'success');
      if (state.activeView === 'reminders') _remLoad();
      return true;
    } catch (err) {
      toast('Échec — ' + (err.message || err), 'error');
      return false;
    }
  }

  // ============ Reminder modal (create / edit) ============
  // Static markup in #reminderModal. openReminderModal(leadId, existing?)
  // seeds the form; save → POST (new) or PATCH (edit) /api/reminders. The
  // optional template attach is OFF by default and only matters for the
  // human 1-click send when the reminder is later due.
  let _reminderModalWired = false;
  const _remModal = { leadId: null, editing: null };
  function _remPad2(n) { return String(n).padStart(2, '0'); }
  function _remDefaultDate() {
    // Default to tomorrow 10:00 — a sane "follow up tomorrow morning" nudge.
    const d = MA.today(); d.setDate(d.getDate() + 1);
    return d.getFullYear() + '-' + _remPad2(d.getMonth() + 1) + '-' + _remPad2(d.getDate());
  }
  function _remSplitRemindAt(remindAt) {
    // Split a stored remind_at into Morocco date + HH:MM for the <input>s. MA.at() + UTC getters:
    // the browser's own getHours() is an hour late, so a re-saved reminder moved by +1h.
    const d = MA.at(remindAt);
    if (isNaN(d.getTime())) return { date: _remDefaultDate(), time: '10:00' };
    return {
      date: d.getUTCFullYear() + '-' + _remPad2(d.getUTCMonth() + 1) + '-' + _remPad2(d.getUTCDate()),
      time: _remPad2(d.getUTCHours()) + ':' + _remPad2(d.getUTCMinutes()),
    };
  }

  // Populate the template <select> from the app's existing template library
  // (state.templatesCache, the same list the Templates manager uses). Only
  // Meta-approved templates (a meta_name + APPROVED status) can fire outside
  // the 24h window, so we only offer those — anything else can't 1-click send.
  // We store the reminder's template_name as the Meta name (meta_name).
  async function _remEnsureTemplatesLoaded() {
    if (Array.isArray(state.templatesCache) && state.templatesCache.length) return;
    try {
      const r = await api('/api/templates');
      const j = await r.json().catch(() => ({}));
      state.templatesCache = j.templates || [];
    } catch { state.templatesCache = state.templatesCache || []; }
  }
  function _remSendableTemplates() {
    return (state.templatesCache || []).filter(t => t.meta_name && (!t.meta_status || t.meta_status === 'APPROVED'));
  }
  async function _remFillTemplateSelect(selectedName) {
    const sel = document.getElementById('reminderTplSelect');
    if (!sel) return;
    await _remEnsureTemplatesLoaded();
    clear(sel);
    const tpls = _remSendableTemplates();
    if (!tpls.length) {
      sel.appendChild(el('option', { value: '', text: '— aucun template approuvé —' }));
      sel.disabled = true;
      return;
    }
    sel.disabled = false;
    sel.appendChild(el('option', { value: '', text: '— choisir un template —' }));
    for (const t of tpls) {
      sel.appendChild(el('option', { value: t.meta_name, text: t.name || t.meta_name }));
    }
    if (selectedName && tpls.some(t => t.meta_name === selectedName)) sel.value = selectedName;
  }

  function _wireReminderModal() {
    if (_reminderModalWired) return;
    _reminderModalWired = true;
    const toggle = document.getElementById('reminderTplToggle');
    const panel = document.getElementById('reminderTplPanel');
    toggle?.addEventListener('change', () => {
      const on = !!toggle.checked;
      panel?.classList.toggle('hidden', !on);
      if (on) _remFillTemplateSelect(_remModal.editing?.template_name || '');
    });
    document.getElementById('reminderSaveBtn')?.addEventListener('click', _reminderSave);
    document.getElementById('reminderNote')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); _reminderSave(); }
    });
    // Quick-note chips — click to fill textarea, click again to clear
    document.getElementById('reminderModal')?.addEventListener('click', (e) => {
      const chip = e.target.closest('.rem-quick-chip');
      if (!chip) return;
      const noteEl = document.getElementById('reminderNote');
      if (!noteEl) return;
      const text = chip.dataset.note || '';
      const chips = document.querySelectorAll('#reminderQuickChips .rem-quick-chip');
      if (noteEl.value.trim() === text) {
        noteEl.value = '';
        chip.classList.remove('rem-quick-chip--active');
      } else {
        noteEl.value = text;
        chips.forEach(c => c.classList.remove('rem-quick-chip--active'));
        chip.classList.add('rem-quick-chip--active');
      }
      noteEl.focus();
    });
    // Clear active chip when agent manually edits the note
    document.getElementById('reminderNote')?.addEventListener('input', () => {
      const noteEl = document.getElementById('reminderNote');
      const chips = document.querySelectorAll('#reminderQuickChips .rem-quick-chip');
      const matchingChip = [...chips].find(c => c.dataset.note === noteEl?.value.trim());
      chips.forEach(c => c.classList.remove('rem-quick-chip--active'));
      if (matchingChip) matchingChip.classList.add('rem-quick-chip--active');
    });
  }

  async function openReminderModal(leadId, existing) {
    _wireReminderModal();
    _remModal.leadId = leadId || existing?.lead_id || null;
    _remModal.editing = existing || null;
    const titleEl = document.getElementById('reminderModalTitle');
    if (titleEl) titleEl.textContent = existing ? 'Modifier le rappel' : 'Nouveau rappel';
    const errEl = document.getElementById('reminderError');
    if (errEl) errEl.classList.add('hidden');
    const noteEl = document.getElementById('reminderNote');
    const dateEl = document.getElementById('reminderDate');
    const timeEl = document.getElementById('reminderTime');
    const toggle = document.getElementById('reminderTplToggle');
    const panel = document.getElementById('reminderTplPanel');
    const card = document.getElementById('reminderLeadCard');
    // Lead identity line — name + phone when we can resolve it from the open
    // chat or the editing row. Pure text (XSS-safe via el text).
    if (card) {
      clear(card);
      const nm = existing?.lead_name || state.selectedChat?.name || '';
      const ph = existing?.lead_phone || pipelineExtractPhone(state.selectedChat || {}) || '';
      if (nm || ph) {
        card.classList.remove('hidden');
        if (nm) card.appendChild(el('div', { class: 'rem-lead-name', text: nm }));
        if (ph) card.appendChild(el('div', { class: 'rem-lead-phone', text: ph }));
      } else { card.classList.add('hidden'); }
    }
    if (noteEl) noteEl.value = existing?.note || '';
    // Sync quick-chip active state with current note value
    const existingNote = existing?.note || '';
    document.querySelectorAll('#reminderQuickChips .rem-quick-chip').forEach(c => {
      c.classList.toggle('rem-quick-chip--active', !!existingNote && c.dataset.note === existingNote);
    });
    if (existing?.remind_at) {
      const { date, time } = _remSplitRemindAt(existing.remind_at);
      if (dateEl) dateEl.value = date;
      if (timeEl) timeEl.value = time;
    } else {
      if (dateEl) dateEl.value = _remDefaultDate();
      if (timeEl) timeEl.value = '10:00';
    }
    // Reset / pre-fill the template attach. ON only when editing a reminder
    // that already had a template attached.
    const hasTpl = !!existing?.template_name;
    if (toggle) toggle.checked = hasTpl;
    if (panel) panel.classList.toggle('hidden', !hasTpl);
    if (hasTpl) await _remFillTemplateSelect(existing.template_name);
    openModal('reminderModal');
    refreshIcons(document.getElementById('reminderModal'));
    setTimeout(() => document.getElementById('reminderNote')?.focus(), 60);
  }

  async function _reminderSave() {
    const btn = document.getElementById('reminderSaveBtn');
    const errEl = document.getElementById('reminderError');
    const note = (document.getElementById('reminderNote')?.value || '').trim();
    const date = (document.getElementById('reminderDate')?.value || '').trim();
    const time = (document.getElementById('reminderTime')?.value || '').trim();
    const tplOn = !!document.getElementById('reminderTplToggle')?.checked;
    const templateName = tplOn ? (document.getElementById('reminderTplSelect')?.value || '') : '';
    const showErr = (msg) => { if (errEl) { errEl.textContent = String(msg); errEl.classList.remove('hidden'); } };
    if (errEl) errEl.classList.add('hidden');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return showErr('Choisissez une date.');
    if (!/^\d{2}:\d{2}/.test(time)) return showErr('Choisissez une heure.');
    if (tplOn && !templateName) return showErr('Choisissez un template ou décochez l\'option.');
    const editing = _remModal.editing;
    if (!editing && !_remModal.leadId) return showErr('Aucun lead sélectionné.');
    if (btn) btn.disabled = true;
    try {
      let r;
      if (editing) {
        // PATCH: send only date/time/note + template_name (always, so
        // unchecking the toggle clears it server-side).
        r = await api('/api/reminders/' + encodeURIComponent(editing.id), {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date, time, note, template_name: templateName || null }),
        });
      } else {
        const body = { lead_id: _remModal.leadId, date, time, note };
        if (templateName) body.template_name = templateName;
        r = await api('/api/reminders', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      closeModal('reminderModal');
      toast(editing ? 'Rappel mis à jour ✓' : 'Rappel créé ✓', 'success');
      // Refresh the pipeline bar (chip) + the Rappels view if open.
      const chatId = state.selectedChatId;
      if (chatId) { delete pipelineLeadCache[chatId]; renderPipelineBar(chatId).catch(() => {}); }
      if (state.activeView === 'reminders') _remLoad();
    } catch (err) {
      showErr(err.message || err);
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  // ============ Rappels view (grouped cards) ============
  // GET /api/reminders?filter=… → grouped Overdue / Today / Upcoming / Done.
  // Cards mirror the .tt-card pattern. Each card: lead name (→ open chat),
  // exact remind_at, note, and actions Terminer / Reporter / Supprimer. A
  // due/overdue reminder WITH a template shows "Envoyer le rappel" (1-click,
  // human-confirmed send → mark done).
  function ensureRemindersInit() {
    state.reminders = state.reminders || { inited: false, rows: [], filter: 'upcoming', search: '' };
    const st = state.reminders;
    if (st.inited) { _remLoad(); return; }
    st.inited = true;
    document.getElementById('remRefreshBtn')?.addEventListener('click', _remLoad);
    document.getElementById('remTestSoundBtn')?.addEventListener('click', _remTestSound);
    document.querySelectorAll('#remFilters .tt-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        st.filter = chip.getAttribute('data-filter') || 'upcoming';
        document.querySelectorAll('#remFilters .tt-chip').forEach(c => c.classList.toggle('tt-chip-on', c === chip));
        _remLoad();
      });
    });
    document.getElementById('remSearch')?.addEventListener('input', (ev) => {
      st.search = (ev.target.value || '').trim().toLowerCase();
      _remRender();
    });
    _remLoad();
  }

  async function _remLoad() {
    const st = state.reminders; if (!st) return;
    const loading = document.getElementById('remLoading');
    const empty = document.getElementById('remEmpty');
    if (loading) loading.classList.remove('hidden');
    if (empty) empty.classList.add('hidden');
    try {
      const r = await api('/api/reminders?filter=' + encodeURIComponent(st.filter || 'upcoming'));
      const j = await r.json().catch(() => ({}));
      st.rows = j.reminders || [];
    } catch (err) {
      console.warn('[reminders] load failed:', err?.message || err);
      st.rows = [];
    } finally {
      if (loading) loading.classList.add('hidden');
      _remRender();
    }
  }

  // Bucket a reminder for the grouped display. Done/dismissed always go to the
  // "Done" bucket; pending ones split by remind_at vs now/end-of-today.
  function _remBucket(rem) {
    if (rem.status && rem.status !== 'pending') return 'done';
    const ts = new Date(rem.remind_at).getTime();
    if (!Number.isFinite(ts)) return 'upcoming';
    const now = Date.now();
    if (ts < now) return 'overdue';
    const eod = new Date(); eod.setHours(23, 59, 59, 999);
    if (ts <= eod.getTime()) return 'today';
    return 'upcoming';
  }

  function _remUpdateChipCounts() {
    const st = state.reminders; if (!st) return;
    const counts = { upcoming: 0, overdue: 0, done: 0, all: st.rows.length };
    for (const r of st.rows) {
      const b = _remBucket(r);
      if (b === 'done') counts.done++;
      else if (b === 'overdue') counts.overdue++;
      else counts.upcoming++; // today + upcoming both fold into "upcoming" chip
    }
    for (const k of Object.keys(counts)) {
      const c = document.querySelector('#remFilters .tt-chip-count[data-count="' + k + '"]');
      if (c) c.textContent = counts[k];
    }
  }

  function _remRender() {
    const st = state.reminders; if (!st) return;
    const host = document.getElementById('remGroups');
    const empty = document.getElementById('remEmpty');
    const countEl = document.getElementById('remResultCount');
    if (!host) return;
    clear(host);
    _remUpdateChipCounts();
    let rows = st.rows.slice();
    if (st.search) {
      rows = rows.filter(r => [r.lead_name, r.lead_phone, r.note, r.template_name]
        .map(x => (x || '').toString().toLowerCase()).join(' ').includes(st.search));
    }
    if (countEl) countEl.textContent = rows.length ? (rows.length + (rows.length === 1 ? ' rappel' : ' rappels')) : '';
    if (!rows.length) { if (empty) empty.classList.remove('hidden'); return; }
    if (empty) empty.classList.add('hidden');
    // Group + order: Overdue → Today → Upcoming → Done.
    const groups = { overdue: [], today: [], upcoming: [], done: [] };
    for (const r of rows) groups[_remBucket(r)].push(r);
    // Pending buckets: soonest first. Done: most recent first.
    const byRemindAsc = (a, b) => new Date(a.remind_at).getTime() - new Date(b.remind_at).getTime();
    const byRemindDesc = (a, b) => new Date(b.remind_at).getTime() - new Date(a.remind_at).getTime();
    groups.overdue.sort(byRemindAsc);
    groups.today.sort(byRemindAsc);
    groups.upcoming.sort(byRemindAsc);
    groups.done.sort(byRemindDesc);
    const sections = [
      ['overdue', 'En retard', 'alert-triangle'],
      ['today', "Aujourd'hui", 'sun'],
      ['upcoming', 'À venir', 'calendar'],
      ['done', 'Terminés', 'check-check'],
    ];
    for (const [key, label, icon] of sections) {
      const list = groups[key];
      if (!list.length) continue;
      const sec = el('div', { class: 'rem-section rem-section-' + key });
      sec.appendChild(el('div', { class: 'rem-section-head' }, [
        lucideIcon(icon),
        el('span', { class: 'rem-section-title', text: label }),
        el('span', { class: 'rem-section-count', text: String(list.length) }),
      ]));
      const grid = el('div', { class: 'rem-cards' });
      for (const r of list) grid.appendChild(_remCard(r, key));
      sec.appendChild(grid);
      host.appendChild(sec);
    }
    refreshIcons(host);
  }

  function _remCard(rem, bucket) {
    const isDone = rem.status && rem.status !== 'pending';
    const card = el('div', { class: 'rem-card rem-card-' + (bucket || 'upcoming') + (isDone ? ' rem-card-done' : '') });
    // Head: when + status pill.
    const head = el('div', { class: 'rem-card-head' });
    head.appendChild(el('div', { class: 'rem-when' }, [
      el('span', { class: 'rem-when-exact', text: _reminderFmtExact(rem.remind_at) }),
      el('span', { class: 'rem-when-rel', text: _reminderRelative(rem.remind_at) }),
    ]));
    if (isDone) {
      head.appendChild(el('span', { class: 'rem-pill rem-pill-done', text: rem.status === 'dismissed' ? 'Ignoré' : 'Terminé' }));
    } else if (bucket === 'overdue') {
      head.appendChild(el('span', { class: 'rem-pill rem-pill-overdue', text: 'En retard' }));
    }
    card.appendChild(head);
    // Lead name → click opens the chat. Stage tag when present.
    const nameRow = el('div', { class: 'rem-name-row' });
    // lead_phone is masked for agents — the open-chat link uses the raw JID
    // the server ships (lead_chat_jid); masked digits would target a bogus chat.
    const phone = rem.lead_chat_jid
      ? String(rem.lead_chat_jid).split('@')[0].replace(/\D/g, '')
      : (String(rem.lead_phone || '').includes('*') ? '' : String(rem.lead_phone || '').replace(/\D/g, ''));
    const nameEl = el('button', {
      class: 'rem-name' + (phone ? ' rem-name-link' : ''),
      type: 'button',
      title: phone ? 'Ouvrir la conversation' : '',
    }, [el('span', { text: rem.lead_name || rem.lead_phone || '(sans nom)' })]);
    if (phone) nameEl.addEventListener('click', () => { try { openChatForPhoneNumber(phone); } catch (_) {} });
    else nameEl.disabled = true;
    nameRow.appendChild(nameEl);
    if (rem.pipeline_stage) nameRow.appendChild(el('span', { class: 'rem-tag rem-tag-stage', text: (PIPELINE_LABELS[rem.pipeline_stage] || rem.pipeline_stage) }));
    card.appendChild(nameRow);
    // Note (read text, XSS-safe).
    if (rem.note) card.appendChild(el('div', { class: 'rem-note' }, [lucideIcon('sticky-note'), el('span', { text: rem.note })]));
    // Attached template line.
    if (rem.template_name) card.appendChild(el('div', { class: 'rem-tpl-line' }, [lucideIcon('message-square-text'), el('span', { text: 'Template : ' + rem.template_name + (rem.template_language ? ' · ' + rem.template_language : '') })]));
    // Actions.
    const actions = el('div', { class: 'rem-card-actions' });
    if (!isDone) {
      // Due = remind_at has actually arrived (covers overdue too). A reminder
      // scheduled for later today is NOT yet due — no 1-click send button.
      const remTs = new Date(rem.remind_at).getTime();
      const dueOrOverdue = Number.isFinite(remTs) && remTs <= Date.now();
      if (rem.template_name && dueOrOverdue) {
        const sendBtn = el('button', { class: 'rem-act rem-act-send', type: 'button' }, [lucideIcon('send'), el('span', { text: 'Envoyer le rappel' })]);
        sendBtn.addEventListener('click', () => _reminderSendTemplate(rem, sendBtn));
        actions.appendChild(sendBtn);
      }
      const doneBtn = el('button', { class: 'rem-act', type: 'button' }, [lucideIcon('check'), el('span', { text: 'Terminer' })]);
      doneBtn.addEventListener('click', () => _reminderSetStatus(rem, 'done'));
      actions.appendChild(doneBtn);
      const editBtn = el('button', { class: 'rem-act', type: 'button' }, [lucideIcon('calendar-clock'), el('span', { text: 'Reporter' })]);
      editBtn.addEventListener('click', () => openReminderModal(rem.lead_id, rem));
      actions.appendChild(editBtn);
    }
    const delBtn = el('button', { class: 'rem-act rem-act-del', type: 'button', title: 'Supprimer' }, [lucideIcon('trash-2')]);
    delBtn.addEventListener('click', () => _reminderDelete(rem));
    actions.appendChild(delBtn);
    card.appendChild(actions);
    return card;
  }

  async function _reminderSetStatus(rem, status) {
    try {
      const r = await api('/api/reminders/' + encodeURIComponent(rem.id), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || ('HTTP ' + r.status)); }
      toast(status === 'done' ? 'Rappel terminé ✓' : 'Rappel mis à jour', 'success');
      _remLoad();
      // Refresh the open chat's bar in case it's the same lead.
      const chatId = state.selectedChatId;
      if (chatId) { delete pipelineLeadCache[chatId]; renderPipelineBar(chatId).catch(() => {}); }
    } catch (err) {
      toast('Échec — ' + (err.message || err), 'error');
    }
  }

  async function _reminderDelete(rem) {
    if (!confirm('Supprimer ce rappel ?')) return;
    try {
      const r = await api('/api/reminders/' + encodeURIComponent(rem.id), { method: 'DELETE' });
      if (!r.ok && r.status !== 204) { const e = await r.json().catch(() => ({})); throw new Error(e.error || ('HTTP ' + r.status)); }
      toast('Rappel supprimé');
      _remLoad();
      const chatId = state.selectedChatId;
      if (chatId) { delete pipelineLeadCache[chatId]; renderPipelineBar(chatId).catch(() => {}); }
    } catch (err) {
      toast('Échec — ' + (err.message || err), 'error');
    }
  }

  // The row of a Meta name the 1-click sends: one template name has a row per
  // language (test_done_no_answer: fr + ar), and the first row in the list
  // used to go whatever the client spoke. Now an approved row in the lead's
  // language (metadata.bot_lang: fr / en, else Arabic), then ar → fr → en.
  // Same rule as lib/leads/post-test-followup.js rowFor.
  function _remPickTemplateRow(templates, metaName, leadLang) {
    const rows = (templates || []).filter(t => t && t.meta_name === metaName);
    const approved = rows.filter(t => !t.meta_status || t.meta_status === 'APPROVED');
    const raw = String(leadLang || '').trim().toLowerCase();
    const lang = raw === 'fr' || raw === 'en' ? raw : 'ar';
    const family = (t) => String(t.meta_language || '').toLowerCase().replace('-', '_').split('_')[0];
    for (const l of [...new Set([lang, 'ar', 'fr', 'en'])]) {
      const hit = approved.find(t => family(t) === l);
      if (hit) return hit;
    }
    return approved[0] || rows[0] || null;
  }

  // The row a reminder sends. A follow-up task stores the exact row's language
  // next to the name (template_language): that (name, language) row or nothing
  // — never another language's text. A reminder without a language (set by
  // hand) picks its row as above.
  function _remTemplateRowFor(rem, templates) {
    const name = rem && rem.template_name;
    const wanted = String((rem && rem.template_language) || '').trim().toLowerCase();
    if (!wanted) return _remPickTemplateRow(templates, name, rem && rem.lead_lang);
    const rows = (templates || []).filter(t => t && t.meta_name === name
      && String(t.meta_language || '').trim().toLowerCase() === wanted);
    return rows.find(t => !t.meta_status || t.meta_status === 'APPROVED') || rows[0] || null;
  }

  // 1-CLICK SEND — human in the loop. Confirm → resolve the reminder's
  // template_name (= Meta name) to a local template id → REUSE the existing
  // per-phone template-send endpoint (the one the Tests bulk-send uses) with
  // a single phone → on success PATCH the reminder to done. There is NO
  // auto-send loop anywhere; this fires ONLY on this explicit click.
  async function _reminderSendTemplate(rem, btn) {
    const phone = String(rem.lead_phone || '').replace(/\D/g, '');
    if (!phone || phone.length < 8) { toast('Aucun numéro WhatsApp valide sur ce rappel', 'warning'); return; }
    await _remEnsureTemplatesLoaded();
    const tpl = _remTemplateRowFor(rem, state.templatesCache);
    if (!tpl && rem.template_language) {
      toast('Aucun template dans cette langue (' + (rem.template_name || '') + ' · ' + rem.template_language + ') — rien n’a été envoyé', 'error');
      return;
    }
    if (!tpl) { toast('Template introuvable (' + (rem.template_name || '') + ')', 'error'); return; }
    if (tpl.meta_status && tpl.meta_status !== 'APPROVED') { toast('Template non approuvé par Meta', 'error'); return; }
    const label = tpl.name || tpl.meta_name;
    const who = rem.lead_name || phone;
    if (!confirm(`Envoyer le template « ${label} » à ${who} sur WhatsApp ?`)) return;
    if (btn) btn.disabled = true;
    try {
      const r = await api('/api/templates/bulk-send-by-phone', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phones: [phone], templateId: tpl.id }),
        timeoutMs: 0, // real WhatsApp send — no client watchdog
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || ('HTTP ' + r.status));
      const failed = (d.failed || []).length;
      if (failed) { toast('Envoi échoué pour ce numéro', 'error'); if (btn) btn.disabled = false; return; }
      toast('Rappel envoyé ✓', 'success');
      // Mark the reminder done now that the human-triggered send succeeded.
      await api('/api/reminders/' + encodeURIComponent(rem.id), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'done' }),
      }).catch(() => {});
      _remLoad();
      const chatId = state.selectedChatId;
      if (chatId) { delete pipelineLeadCache[chatId]; renderPipelineBar(chatId).catch(() => {}); }
    } catch (err) {
      toast('Envoi échoué — ' + (err.message || err), 'error');
      if (btn) btn.disabled = false;
    }
  }

  // Reminders nav-tab badge — sum of pending+overdue surfaced by the due-loop
  // socket. Reuses the .view-tab-badge pattern (like teamchatTabBadge).
  function _remBumpTabBadge(delta) {
    const b = document.getElementById('remindersTabBadge');
    if (!b) return;
    const cur = parseInt(b.textContent || '0', 10) || 0;
    const next = Math.max(0, cur + (delta || 0));
    if (next > 0) { b.textContent = next > 99 ? '99+' : String(next); b.classList.remove('hidden'); }
    else { b.textContent = ''; b.classList.add('hidden'); }
    try { refreshGroupBadges(); } catch (_) {}
  }
  function _remClearTabBadge() {
    const b = document.getElementById('remindersTabBadge');
    if (b) { b.textContent = ''; b.classList.add('hidden'); }
    try { refreshGroupBadges(); } catch (_) {}
  }

  // Bubble action — flag an inbound message (image / pdf / text) as a
  // payment receipt. Server resolves the lead by phone, creates the claim
  // and (waitExtract) holds the response until Gemini read the amount, so
  // the encaissement modal can open pre-filled — the semi-auto Paid flow.
  // 409 = this exact message was already flagged (uq_pv_inbox_msg).
  async function flagMessageAsReceipt(m) {
    if (!m?.id || !state.selectedChatId || m._flagging) return;
    m._flagging = true;
    try {
      toast('Receipt marked — reading the amount…');
      const r = await api('/api/payment-verify/flag-message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId: m.id, chatId: state.selectedChatId, waitExtract: true }),
      });
      if (r.status === 409) { toast('Already marked'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast('Receipt sent for verification 🟡');
      // Semi-auto Paid: lead resolved + not already paid → open the
      // encaissement form pre-filled from the receipt so the agent just
      // verifies and confirms. Already-paid leads keep the toast-only flow.
      if (j.lead && j.lead.id && j.lead.pipeline_stage !== 'paid') {
        showPaidModal(j.lead.id, {
          amount: j.prefill?.amount || null,
          date: j.prefill?.date || null,
          clientBank: j.prefill?.clientBank || null,
        });
      }
    } catch (err) {
      toast('Could not mark receipt — ' + (err.message || err), 'error');
    } finally {
      m._flagging = false;
    }
  }

  function initVerifyPayment() {
    _vpAdmin = _isSalesAdmin();
    // Hide every admin-only control + the bank-statement panel for agents.
    // Toggle .hidden directly (no app.css change): the Importer + Détecter
    // buttons (mutate / scan, admin endpoints) and the whole Relevé bancaire
    // panel (reveals bank_transactions). Agents keep the month filter, account
    // chips and refresh — those drive only their own self-scoped claim list.
    const view = document.getElementById('verifyPaymentView');
    if (view) view.classList.toggle('vp-admin', _vpAdmin);
    const hideForAgent = (elm) => { if (elm) elm.classList.toggle('hidden', !_vpAdmin); };
    hideForAgent(document.getElementById('vpScanBtn'));
    hideForAgent(document.getElementById('vpImportBtn'));
    hideForAgent(document.getElementById('vpBankTable')?.closest('.vp-panel'));
    // (swat:V2) Certainty chips derive from suggestions — agents get none.
    hideForAgent(document.getElementById('vpMatchChips'));
    // Undo sections (swat:B6) — admin-only: Rouvrir is a sales-admin endpoint
    // and verified rows expose matched bank labels.
    hideForAgent(document.getElementById('vpVerifiedPanel'));
    hideForAgent(document.getElementById('vpRejectedPanel'));
    _vpInitOnce();
    loadVerifications();                 // now self-scoped server-side for agents
    if (_vpAdmin) {
      loadBankTx({ reset: true });        // bank-tx endpoint is admin-only (403 otherwise)
      _vpLoadDecided('verified');         // always feed the "Receipt confirmed" overview card
    }
  }

  function _vpInitOnce() {
    if (_vpInitDone) return;
    _vpInitDone = true;
    const monthInput = document.getElementById('vpMonthInput');
    if (monthInput) {
      monthInput.value = _vpMonth;
      monthInput.addEventListener('change', () => {
        if (!monthInput.value) return;
        _vpMonth = monthInput.value;
        loadVerifications();
        if (_vpAdmin) {
          loadBankTx({ reset: true });
          _vpLoadDecided('verified');
        }
      });
    }
    document.querySelectorAll('#vpAccountChips [data-account]').forEach(btn => {
      btn.addEventListener('click', () => {
        const a = btn.getAttribute('data-account') || 'all';
        if (_vpAccount === a) return;
        _vpAccount = a;
        document.querySelectorAll('#vpAccountChips [data-account]').forEach(c => {
          const active = c.getAttribute('data-account') === a;
          c.classList.toggle('active', active);
          c.setAttribute('aria-selected', String(active));
        });
        renderVerifyQueue();              // claim filter is client-side
        if (_vpAdmin) {
          _vpRenderDecided('verified');
          _vpRenderDecided('rejected');
          loadBankTx({ reset: true });     // bank filter is server-side (admin-only)
        }
      });
    });
    // (swat:V2) Certainty chips — pure client-side narrowing of the loaded
    // queue; counts repaint on every renderVerifyQueue.
    document.querySelectorAll('#vpMatchChips [data-match]').forEach(btn => {
      btn.addEventListener('click', () => {
        const m = btn.getAttribute('data-match') || 'all';
        if (_vpMatchFilter === m) return;
        _vpMatchFilter = m;
        document.querySelectorAll('#vpMatchChips [data-match]').forEach(c => {
          const active = c.getAttribute('data-match') === m;
          c.classList.toggle('active', active);
          c.setAttribute('aria-selected', String(active));
        });
        renderVerifyQueue();
      });
    });
    document.getElementById('vpRefreshBtn')?.addEventListener('click', () => {
      loadVerifications();
      if (_vpAdmin) {
        loadBankTx({ reset: true });
        _vpLoadDecided('verified');
      }
    });
    document.getElementById('vpOverviewVerified')?.addEventListener('click', () => {
      if (!_vpAdmin) return;
      if (!_vpDecided.verified.open) _vpToggleDecided('verified');
      else _vpLoadDecided('verified');
      setTimeout(() => document.getElementById('vpVerifiedPanel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 40);
    });
    document.getElementById('vpDetailBackdrop')?.addEventListener('click', _vpCloseDetail);
    document.getElementById('vpDetailClose')?.addEventListener('click', _vpCloseDetail);
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && !document.getElementById('vpDetailDrawer')?.classList.contains('hidden')) _vpCloseDetail();
    });
    document.getElementById('vpImportBtn')?.addEventListener('click', _vpOpenImportModal);
    document.getElementById('vpScanBtn')?.addEventListener('click', _vpScanReceipts);
    // Import modal — tab switching + invalidation of a stale dry-run.
    document.querySelectorAll('#vpImportTabs [data-vp-tab]').forEach(btn => {
      btn.addEventListener('click', () => _vpSetImportTab(btn.getAttribute('data-vp-tab') || 'file'));
    });
    document.getElementById('vpImportAccount')?.addEventListener('change', _vpInvalidateAnalysis);
    document.getElementById('vpImportFile')?.addEventListener('change', _vpInvalidateAnalysis);
    document.getElementById('vpImportPaste')?.addEventListener('input', _vpInvalidateAnalysis);
    document.getElementById('vpAnalyzeBtn')?.addEventListener('click', _vpAnalyze);
    document.getElementById('vpImportConfirmBtn')?.addEventListener('click', _vpConfirmImport);
    // Lier… picker — server-side q search, debounced like the Rest search.
    document.getElementById('vpLinkSearch')?.addEventListener('input', () => {
      clearTimeout(_vpLinkSearchDebounce);
      _vpLinkSearchDebounce = setTimeout(_vpLoadLinkTxs, 280);
    });
    // Undo sections (swat:B6) — collapsed by default, lazy-load on expand.
    document.getElementById('vpVerifiedToggle')?.addEventListener('click', () => _vpToggleDecided('verified'));
    document.getElementById('vpRejectedToggle')?.addEventListener('click', () => _vpToggleDecided('rejected'));
  }

  // ─── Vérifiés / Rejetés — undo safety net (swat:B6) ──────────────────
  // Two collapsible sections under the pending board. Lazy: nothing is
  // fetched until first expand; an open section re-fetches whenever the
  // pending queue reloads (socket / month change / refresh) so a reopened
  // row disappears from here as it reappears above.
  const _vpDecided = {
    verified: { open: false, month: null, items: [], loading: false },
    rejected: { open: false, month: null, items: [], loading: false },
  };
  function _vpDecidedEls(kind) {
    const cap = kind === 'verified' ? 'Verified' : 'Rejected';
    return {
      toggle: document.getElementById('vp' + cap + 'Toggle'),
      body: document.getElementById('vp' + cap + 'Body'),
      table: document.getElementById('vp' + cap + 'Table'),
      count: document.getElementById('vp' + cap + 'Count'),
    };
  }
  function _vpToggleDecided(kind) {
    const st = _vpDecided[kind];
    const { toggle, body } = _vpDecidedEls(kind);
    st.open = !st.open;
    if (toggle) toggle.setAttribute('aria-expanded', String(st.open));
    if (body) body.classList.toggle('hidden', !st.open);
    if (st.open && (st.month !== _vpMonth || !st.items.length)) _vpLoadDecided(kind);
  }
  async function _vpLoadDecided(kind) {
    const st = _vpDecided[kind];
    if (st.loading) return;
    st.loading = true;
    const { table } = _vpDecidedEls(kind);
    if (table && !st.items.length) {
      clear(table);
      table.appendChild(el('div', { class: 'vp-empty', text: 'Loading…' }));
    }
    try {
      const params = new URLSearchParams();
      params.set('status', kind);
      params.set('month', _vpMonth);
      const r = await api('/api/payment-verify/verifications?' + params.toString());
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      st.items = j.items || [];
      st.month = _vpMonth;
      _vpRenderDecided(kind);
    } catch (err) {
      if (table) {
        clear(table);
        table.appendChild(el('div', { class: 'vp-empty', text: 'Could not load — ' + (err.message || err) }));
      }
    } finally {
      st.loading = false;
    }
  }
  // Open sections follow the pending queue's reloads (called from
  // loadVerifications) so both sides of a Rouvrir stay in sync.
  function _vpReloadDecidedOpen() {
    if (!_vpAdmin) return;
    // Verified always feeds the outcome overview count, even while its detail
    // table is collapsed. Rejected remains lazy because it has no KPI card.
    _vpLoadDecided('verified');
    if (_vpDecided.rejected.open) _vpLoadDecided('rejected');
  }
  function _vpFmtDateTime(v) {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return MA.loc(d, 'd', 'en-GB', { day: '2-digit', month: '2-digit' })
      + ' ' + MA.loc(d, 't', 'en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  function _vpRenderDecided(kind) {
    const st = _vpDecided[kind];
    const { table, count } = _vpDecidedEls(kind);
    if (!table) return;
    clear(table);
    const visibleItems = _vpVisibleDecidedItems(kind);
    if (count) count.textContent = visibleItems.length ? '· ' + visibleItems.length : '· 0';
    if (!visibleItems.length) {
      table.appendChild(el('div', {
        class: 'vp-empty',
        text: kind === 'verified' ? 'No verified payments this month.' : 'No rejected receipts this month.',
      }));
      _vpRenderOverview();
      return;
    }
    const header = el('div', { class: 'tickets-row tickets-row-header' });
    for (const t of ['Client', 'Amount', 'Date', kind === 'verified' ? 'Bank credit' : 'Account', 'Note', 'Decided by', '']) {
      header.appendChild(el('div', { text: t }));
    }
    table.appendChild(header);
    for (const claim of visibleItems) table.appendChild(_vpBuildDecidedRow(claim, kind));
    refreshIcons(table);
    _vpRenderOverview();
  }
  function _vpBuildDecidedRow(claim, kind) {
    const row = el('div', { class: 'tickets-row vp-decided-row', 'data-claim-id': claim.id });
    const clientCell = el('div', { class: 'vp-client' });
    clientCell.appendChild(el('div', { class: 'rest-name', text: claim.client_name || claim.claimed_payer || '—' }));
    if (claim.client_phone) clientCell.appendChild(el('div', { class: 'rest-phone', text: formatPhoneForDisplay(claim.client_phone) }));
    row.appendChild(clientCell);
    const amount = kind === 'verified' && claim.matched_amount != null ? claim.matched_amount : claim.claimed_amount;
    row.appendChild(el('div', { class: 'vp-amount', text: _vpFmtMoney(amount) + ' DH' }));
    row.appendChild(el('div', { class: 'vp-date', text: _vpFmtDate(claim.claimed_date) }));
    // Verified → the matched bank line (account pill + label); rejected → the
    // claimed account if any.
    const bankCell = el('div', { class: 'vp-decided-bank' });
    const tx = claim.matched_tx || null;
    const account = (tx && tx.account) || claim.claimed_account || null;
    if (account) bankCell.appendChild(el('span', { class: 'vp-account-pill', 'data-account': account, text: _vpAccountLabel(account) }));
    if (tx && tx.label) {
      bankCell.appendChild(el('span', { class: 'vp-decided-bank-label', title: tx.label + ' · ' + _vpFmtDate(tx.op_date), text: tx.label }));
    } else if (!account) {
      bankCell.appendChild(el('span', { class: 'vp-muted', text: '—' }));
    }
    row.appendChild(bankCell);
    row.appendChild(el('div', { class: 'vp-decided-note', title: claim.match_note || '', text: claim.match_note || '—' }));
    const byCell = el('div', { class: 'vp-decided-by' });
    const decidedBy = claim.decided_by === 'system:auto-match'
      ? 'Automatic ✓'
      : userLabel(claim.decided_by || '—');
    byCell.appendChild(el('div', { text: decidedBy }));
    if (claim.decided_at) byCell.appendChild(el('div', { class: 'rest-phone', text: _vpFmtDateTime(claim.decided_at) }));
    row.appendChild(byCell);
    const actCell = el('div', { class: 'vp-actions' });
    const btn = el('button', {
      class: 'vp-act-btn vp-act-reopen', type: 'button',
      title: 'Reopen — returns the payment to “To review” and releases the bank credit',
    }, [lucideIcon('undo-2'), el('span', { text: 'Reopen' })]);
    btn.addEventListener('click', ev => { ev.stopPropagation(); _vpReopenClaim(claim, btn); });
    actCell.appendChild(btn);
    row.appendChild(actCell);
    return row;
  }
  async function _vpReopenClaim(claim, btn) {
    if (!claim?.id) return;
    if (btn) btn.disabled = true;
    try {
      const r = await api('/api/payment-verify/verifications/' + encodeURIComponent(claim.id) + '/reopen', { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast('Payment reopened — back in “To review”');
      // The socket refresh repaints everything; reload directly too in case
      // the socket is down.
      loadVerifications();
      if (_vpAdmin) loadBankTx({ reset: true });
    } catch (err) {
      toast('Could not reopen payment — ' + (err.message || err), 'error');
      if (btn) btn.disabled = false;
    }
  }

  // ─── Détecter les reçus — scan conversations for receipts ────────────
  // POST /api/payment-verify/scan-receipts kicks a background job that
  // sweeps each pending claim's WhatsApp conversation for a receipt image /
  // PDF / payment text and attaches the best Gemini-scored match. Results
  // stream back over 'payment-verification:updated' (which reloads this
  // view), so the button just disables while the scan runs — re-enabled on
  // the next loadVerifications() or after 30s, whichever comes first.
  function _vpScanBtnReset() {
    const btn = document.getElementById('vpScanBtn');
    if (!btn || !btn.disabled) return;
    btn.disabled = false;
    if (btn.dataset.vpHtml) {
      btn.innerHTML = btn.dataset.vpHtml;
      delete btn.dataset.vpHtml;
    }
  }

  async function _vpScanReceipts() {
    const btn = document.getElementById('vpScanBtn');
    try {
      const r = await api('/api/payment-verify/scan-receipts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: _vpMonth }),
      });
      if (r.status === 409) { toast('A receipt scan is already running'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast(`Receipt scan started — ${j.claims} payments queued`);
      if (btn && !btn.disabled) {
        btn.dataset.vpHtml = btn.innerHTML;
        btn.textContent = 'Scanning…';
        btn.disabled = true;
        setTimeout(_vpScanBtnReset, 30000);
      }
    } catch (err) {
      toast('Receipt scan failed — ' + (err.message || err), 'error');
    }
  }

  // ─── À vérifier — pending claims queue ───────────────────────
  // (swat:B9) Socket-triggered refresh that never destroys in-progress
  // typing: 'payment-verification:updated' rebuilds the whole queue table,
  // which used to wipe a focused "Qui a payé ?" / montant input mid-keystroke.
  // If focus sits inside the board, defer the reload to that input's blur;
  // the blur handler re-checks (setTimeout 0, after focus settles) so tabbing
  // straight into another board input keeps deferring instead of clobbering
  // it. Real updates are never dropped — only delayed past the typing.
  let _vpReloadDeferred = false;
  function _vpSocketRefresh() {
    const ae = document.activeElement;
    const typing = !!(ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA') && ae.closest('#vpQueueTable'));
    if (typing) {
      if (!_vpReloadDeferred) {
        _vpReloadDeferred = true;
        ae.addEventListener('blur', () => {
          _vpReloadDeferred = false;
          setTimeout(() => {
            if (state.activeView === 'verify-payment') _vpSocketRefresh();
          }, 0);
        }, { once: true });
      }
      return;
    }
    loadVerifications();
    if (_vpAdmin) loadBankTx({ reset: true }); // bank-tx is admin-only
  }

  async function loadVerifications() {
    if (_vpQueueLoading) return;
    _vpQueueLoading = true;
    const empty = document.getElementById('vpQueueEmpty');
    if (empty) empty.textContent = 'Loading…';
    try {
      const params = new URLSearchParams();
      params.set('status', 'pending');
      params.set('month', _vpMonth);
      const r = await api('/api/payment-verify/verifications?' + params.toString());
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      _vpQueueItems = j.items || [];
      renderVerifyQueue();
    } catch (err) {
      const table = document.getElementById('vpQueueTable');
      if (table) {
        clear(table);
        table.appendChild(el('div', { class: 'vp-empty', text: 'Could not load — ' + (err.message || err) }));
      }
    } finally {
      _vpQueueLoading = false;
      _vpScanBtnReset();
      _vpReloadDecidedOpen(); // (swat:B6) open undo sections follow every queue reload
    }
  }

  function renderVerifyQueue() {
    const table = document.getElementById('vpQueueTable');
    if (!table) return;
    clear(table);

    // Account chips filter claims client-side; claims with no detected
    // account stay visible on every chip (they could belong to any).
    const visible = _vpQueueItems.filter(c =>
      _vpAccount === 'all' || !c.claimed_account || c.claimed_account === _vpAccount);

    // (swat:V2) Certainty buckets — counts follow the account filter, the
    // active chip then narrows the rendered rows. Buckets are disjoint
    // (priority in _vpClaimBucket), so the counts sum to visible.length.
    const bucketCounts = { sure: 0, cash: 0, ambiguous: 0, none: 0, problem: 0 };
    if (_vpAdmin) {
      for (const c of visible) bucketCounts[_vpClaimBucket(c)]++;
      for (const k of Object.keys(bucketCounts)) {
        const nEl = document.querySelector('#vpMatchChips [data-match-n="' + k + '"]');
        if (nEl) nEl.textContent = String(bucketCounts[k]);
      }
      _vpRenderOverview(bucketCounts, visible.length);
    }
    const filtered = (!_vpAdmin || _vpMatchFilter === 'all')
      ? visible
      : visible.filter(c => _vpClaimBucket(c) === _vpMatchFilter);

    const queueCount = document.getElementById('vpQueueCount');
    if (queueCount) queueCount.textContent = filtered.length ? '· ' + filtered.length : '';
    const headerCount = document.getElementById('vpCount');
    if (headerCount) {
      headerCount.textContent = _vpQueueItems.length
        ? `· ${_vpQueueItems.length} pending` : '';
    }

    if (!visible.length) {
      table.appendChild(el('div', { class: 'vp-empty', text: 'Nothing to review — all receipts for this month are processed. ✓' }));
      return;
    }
    if (!filtered.length) {
      table.appendChild(el('div', { class: 'vp-empty', text: 'No payments in this filter — choose “All” to review everything.' }));
      return;
    }

    // Account chip active → claims with no detected account stay visible on
    // every chip (they could belong to any) — say so instead of looking like
    // the filter did nothing.
    if (_vpAccount !== 'all') {
      const noAccount = visible.filter(c => !c.claimed_account).length;
      if (noAccount > 0) {
        table.appendChild(el('div', {
          class: 'vp-account-notice',
          text: noAccount + ' receipts have no detected account — shown under every account',
        }));
      }
    }

    const header = el('div', { class: 'tickets-row tickets-row-header' });
    // The grid template is a fixed 8 columns (CSS). Agents get a read-only
    // header: the Suggestion slot becomes "Statut" and the Actions slot is
    // blank (no bank-tx reveal, no mutating buttons) — same 8 cells so the
    // grid stays aligned without a CSS change.
    const cols = _vpAdmin
      ? ['Client', 'Agent', 'Amount', 'Date', 'Account', 'Receipt', 'Diagnostic', 'Actions']
      : ['Client', 'Agent', 'Amount', 'Date', 'Account', 'Receipt', 'Status', ''];
    for (const t of cols) header.appendChild(el('div', { text: t }));
    table.appendChild(header);

    for (const claim of filtered) table.appendChild(_vpBuildQueueRow(claim));
    refreshIcons(table);
  }

  function _vpBuildQueueRow(claim) {
    const bucket = _vpClaimBucket(claim);
    const row = el('div', { class: 'tickets-row vp-queue-row vp-row-' + bucket, 'data-claim-id': claim.id });

    // Client — name + phone, click-through to the inbox conversation.
    const clientCell = el('div', { class: 'vp-client' });
    const intl = _phoneForWhatsapp(claim.client_phone);
    if (intl) {
      const link = el('a', { href: '#', class: 'rest-name rest-name-link', text: claim.client_name || claim.claimed_payer || 'Client', title: 'Open conversation' });
      link.addEventListener('click', ev => {
        ev.preventDefault(); ev.stopPropagation();
        openInboxForPhone(intl, claim.client_name || '');
      });
      clientCell.appendChild(link);
    } else {
      clientCell.appendChild(el('div', { class: 'rest-name', text: claim.client_name || claim.claimed_payer || '—' }));
    }
    if (claim.client_phone) clientCell.appendChild(el('div', { class: 'rest-phone', text: formatPhoneForDisplay(claim.client_phone) }));
    // "Payé par" — the team-entered payer (claimed_payer). Editable inline for
    // admins (PATCH feeds the matcher + HubSpot SENT BY). Agents are read-only:
    // they see the value as plain text (the PATCH endpoint 403s them anyway).
    if (_vpAdmin) {
      clientCell.appendChild(_vpBuildPayerEdit(claim));
    } else if (claim.claimed_payer) {
      clientCell.appendChild(el('div', { class: 'vp-payer-ro rest-phone', text: 'Paid by: ' + claim.claimed_payer }));
    }
    row.appendChild(clientCell);

    row.appendChild(el('div', { class: 'vp-agent', text: userLabel(claim.agent_id || '—') }));
    // Montant — inline-editable for admins (swat:B8a): the 1-DH placeholder
    // rows MUST be correctable before any confirm. Agents keep plain text.
    if (_vpAdmin && String(claim.status || 'pending') === 'pending') {
      row.appendChild(_vpBuildAmountEdit(claim));
    } else {
      row.appendChild(el('div', { class: 'vp-amount', text: _vpFmtMoney(claim.claimed_amount) + ' DH' }));
    }
    row.appendChild(el('div', { class: 'vp-date', text: _vpFmtDate(claim.claimed_date) }));

    const accCell = el('div', {});
    if (claim.claimed_account) accCell.appendChild(el('span', { class: 'vp-account-pill', 'data-account': claim.claimed_account, text: _vpAccountLabel(claim.claimed_account) }));
    else accCell.appendChild(el('span', { class: 'vp-muted', text: '—' }));
    row.appendChild(accCell);

    // Reçu — thumbnail for images, file icon for PDFs, text icon otherwise.
    const proofCell = el('div', { class: 'vp-proof' });
    if (claim.media_url) {
      const mediaWrap = el('div', { class: 'vp-proof-media' });
      if ((claim.mime_type || '').startsWith('image/')) {
        const img = el('img', { class: 'vp-proof-thumb', src: claim.media_url, alt: 'Receipt', title: 'View receipt' });
        img.addEventListener('click', ev => { ev.stopPropagation(); openImageLightbox(claim.media_url); });
        mediaWrap.appendChild(img);
      } else {
        const isPdf = (claim.mime_type || '').includes('pdf') || /\.pdf(\?|$)/i.test(claim.media_url);
        const link = el('a', { class: 'vp-proof-file', href: claim.media_url, target: '_blank', rel: 'noopener', title: isPdf ? 'Open PDF' : 'Open file' });
        link.appendChild(lucideIcon(isPdf ? 'file-text' : 'file'));
        mediaWrap.appendChild(link);
      }
      // Detach ✕ (swat:B14) — wrong receipt on a pending claim is no longer
      // permanent. Admin-only (endpoint is sales-admin gated); the file
      // itself stays in the conversation, only the claim link is cut.
      if (_vpAdmin && String(claim.status || 'pending') === 'pending') {
        const dBtn = el('button', {
          class: 'vp-proof-detach', type: 'button', text: '✕',
          title: 'Detach this receipt (the file stays in the conversation)',
          'aria-label': 'Detach receipt',
        });
        dBtn.addEventListener('click', ev => { ev.stopPropagation(); _vpDetachReceipt(claim, dBtn); });
        mediaWrap.appendChild(dBtn);
      }
      proofCell.appendChild(mediaWrap);
    } else {
      const txt = el('span', { class: 'vp-proof-file vp-proof-text', title: claim.source === 'whatsapp_receipt' ? 'Text message marked as a receipt' : 'No media — ' + (claim.source || '') + ' declaration' });
      txt.appendChild(lucideIcon('text'));
      proofCell.appendChild(txt);
      // Manual upload — claims with no WhatsApp conversation never get media
      // from scan-receipts; the receipt is attached from disk instead. Shown to
      // admins AND agents: agents only ever see their OWN claims (server is
      // self-scoped) and the receipt-upload endpoint is owner-OR-admin, so the
      // button is safe on every visible row. This is the ONLY mutating control
      // an agent gets — confirm/reject/link stay behind the _vpAdmin early
      // return below.
      const upBtn = el('button', { class: 'vp-upload-btn', type: 'button', title: 'Attach receipt (image/PDF)' });
      upBtn.appendChild(lucideIcon('upload'));
      upBtn.addEventListener('click', ev => { ev.stopPropagation(); _vpPickReceiptFile(claim.id); });
      proofCell.appendChild(upBtn);
    }
    row.appendChild(proofCell);

    // ─── Agent (read-only) — Statut pill in the Suggestion slot, blank Actions
    // slot. No bank-matching suggestion (server omits it) and no mutating
    // buttons. Same 8 cells → grid stays aligned.
    if (!_vpAdmin) {
      const st = String(claim.status || 'pending');
      const stCell = el('div', { class: 'vp-status-cell' });
      // Reuse the existing pv-chip tokens (pv-pending / pv-verified). The list
      // only ever requests status=pending, so 'verified' is for the rare race
      // and 'rejected' falls back to the neutral base chip — all token-driven.
      const pill = { pending: ['Pending 🟡', 'pv-pending'], verified: ['Verified ✅', 'pv-verified'], rejected: ['Rejected ✖', ''] }[st]
        || ['Pending 🟡', 'pv-pending'];
      stCell.appendChild(el('span', { class: ('pv-chip ' + pill[1]).trim(), text: pill[0] }));
      row.appendChild(stCell);
      const detailsCell = el('div', { class: 'vp-actions' });
      const detailsBtn = el('button', { class: 'vp-act-btn vp-act-details', type: 'button' }, [
        lucideIcon('panel-right-open'), el('span', { text: 'Details' }),
      ]);
      detailsBtn.addEventListener('click', ev => { ev.stopPropagation(); _vpOpenDetail(claim); });
      detailsCell.appendChild(detailsBtn);
      row.appendChild(detailsCell);
      return row;
    }

    // Diagnostic — answer "is the money in the bank?" in plain language.
    // The full evidence trail stays one click away in the detail drawer.
    const suggCell = el('div', { class: 'vp-sugg-cell' });
    const top = (claim.suggestions || [])[0] || null;
    const outcome = _vpOutcome(claim);
    const statusBox = el('div', { class: 'vp-outcome vp-outcome-' + outcome.bucket });
    const statusIcon = el('span', { class: 'vp-outcome-icon' }, [lucideIcon(outcome.icon)]);
    const statusCopy = el('span', { class: 'vp-outcome-copy' });
    statusCopy.appendChild(el('span', { class: 'vp-outcome-title', text: outcome.title }));
    statusCopy.appendChild(el('span', { class: 'vp-outcome-reason', text: outcome.reason }));
    if (top && top.tx) {
      const bankMeta = [
        _vpFmtMoney(top.tx.credit) + ' DH',
        _vpFmtDate(top.tx.op_date),
        _vpAccountLabel(top.tx.account),
      ].join(' · ');
      statusCopy.appendChild(el('span', {
        class: 'vp-outcome-bank',
        title: String(top.tx.label || ''),
        text: bankMeta,
      }));
    }
    statusBox.appendChild(statusIcon);
    statusBox.appendChild(statusCopy);
    suggCell.appendChild(statusBox);

    // Retry affordance for a failed/stale receipt extraction.
    if (bucket === 'problem' && (claim.media_url || claim.inbox_message_id)) {
      const rBtn = el('button', {
        class: 'vp-act-btn vp-act-retry', type: 'button',
        title: 'Retry automatic receipt reading',
      }, [lucideIcon('refresh-cw'), el('span', { text: 'Read receipt again' })]);
      rBtn.addEventListener('click', ev => { ev.stopPropagation(); _vpReExtract(claim, rBtn); });
      suggCell.appendChild(rBtn);
    }
    row.appendChild(suggCell);

    // Actions — ✓ Confirmer (SURE suggestions only, swat:V3), ✗ Rejeter
    // (note), Lier… (picker). The server re-enforces the sure gate at
    // confirm time — this is UX, not the security boundary.
    const actCell = el('div', { class: 'vp-actions' });
    const detailsBtn = el('button', {
      class: 'vp-act-btn vp-act-details', type: 'button',
      title: 'View the receipt, proposed bank credit, and status reasons',
    }, [lucideIcon('panel-right-open'), el('span', { text: 'Details' })]);
    detailsBtn.addEventListener('click', ev => { ev.stopPropagation(); _vpOpenDetail(claim); });
    actCell.appendChild(detailsBtn);
    if (top && top.certainty === 'sure') {
      const okBtn = el('button', { class: 'vp-act-btn vp-act-confirm', type: 'button', title: 'Confirm the exact, unique match' }, [lucideIcon('check'), el('span', { text: 'Confirm' })]);
      okBtn.addEventListener('click', ev => { ev.stopPropagation(); _vpConfirmClaim(claim, top.bankTxId, okBtn); });
      actCell.appendChild(okBtn);
    }
    // Lier… is hidden on placeholder rows (swat:B17): the server refuses to
    // confirm a never-read claim anyway — offering the picker was a dead end.
    // The Suggestion-slot badge tells the admin to edit the montant first.
    if (!_vpIsAmountPending(claim)) {
      const linkBtn = el('button', { class: 'vp-act-btn vp-act-link', type: 'button', title: 'Choose the matching bank credit' }, [lucideIcon('link'), el('span', { text: 'Choose bank credit' })]);
      linkBtn.addEventListener('click', ev => { ev.stopPropagation(); _vpOpenLinkModal(claim); });
      actCell.appendChild(linkBtn);
    }
    const rejBtn = el('button', { class: 'vp-act-btn vp-act-reject', type: 'button', title: 'Reject this receipt' }, [lucideIcon('x'), el('span', { text: 'Reject' })]);
    rejBtn.addEventListener('click', ev => { ev.stopPropagation(); _vpRejectClaim(claim, rejBtn); });
    actCell.appendChild(rejBtn);
    row.appendChild(actCell);

    return row;
  }

  // ─── Payment evidence drawer ─────────────────────────────────────────
  // Keeps the queue scannable while making every decision auditable without
  // tooltips: declaration → receipt extraction → bank candidate → checks.
  let _vpDetailClaim = null;

  function _vpCloseDetail() {
    const drawer = document.getElementById('vpDetailDrawer');
    if (drawer) drawer.classList.add('hidden');
    document.body.classList.remove('vp-detail-open');
    _vpDetailClaim = null;
  }

  function _vpDetailFact(label, value, opts = {}) {
    const fact = el('div', { class: 'vp-detail-fact' + (opts.wide ? ' vp-detail-fact-wide' : '') });
    fact.appendChild(el('span', { class: 'vp-detail-fact-label', text: label }));
    fact.appendChild(el('span', {
      class: 'vp-detail-fact-value' + (opts.muted ? ' vp-detail-fact-muted' : ''),
      title: opts.title || '',
      text: value == null || value === '' ? 'Not provided' : String(value),
    }));
    return fact;
  }

  function _vpDetailSection(title, icon) {
    const section = el('section', { class: 'vp-detail-section' });
    section.appendChild(el('h4', { class: 'vp-detail-section-title' }, [
      lucideIcon(icon), el('span', { text: title }),
    ]));
    return section;
  }

  function _vpDetailCheck(label, stateName, text) {
    const safeState = ['ok', 'warn', 'bad', 'neutral'].includes(stateName) ? stateName : 'neutral';
    const icon = safeState === 'ok' ? 'check' : safeState === 'bad' ? 'x' : safeState === 'warn' ? 'triangle-alert' : 'minus';
    const row = el('div', { class: 'vp-detail-check vp-detail-check-' + safeState });
    row.appendChild(el('span', { class: 'vp-detail-check-icon' }, [lucideIcon(icon)]));
    const copy = el('span', { class: 'vp-detail-check-copy' });
    copy.appendChild(el('strong', { text: label }));
    copy.appendChild(el('span', { text }));
    row.appendChild(copy);
    return row;
  }

  function _vpOpenDetail(claim) {
    if (!claim) return;
    _vpDetailClaim = claim;
    const drawer = document.getElementById('vpDetailDrawer');
    const body = document.getElementById('vpDetailBody');
    const actions = document.getElementById('vpDetailActions');
    const title = document.getElementById('vpDetailTitle');
    const subtitle = document.getElementById('vpDetailSubtitle');
    if (!drawer || !body || !actions) return;

    const outcome = _vpAdmin ? _vpOutcome(claim) : {
      bucket: 'pending', icon: 'clock-3', title: 'Pending review',
      reason: 'The verification team must compare this receipt with the bank statement.',
      top: null,
    };
    const top = outcome.top;
    const x = claim.extracted && typeof claim.extracted === 'object' ? claim.extracted : {};
    if (title) title.textContent = claim.client_name || claim.claimed_payer || 'Payment';
    if (subtitle) {
      const bits = [claim.agent_id ? 'Agent ' + claim.agent_id : null, claim.client_phone ? formatPhoneForDisplay(claim.client_phone) : null].filter(Boolean);
      subtitle.textContent = bits.join(' · ');
    }
    clear(body);
    clear(actions);

    const banner = el('div', { class: 'vp-detail-verdict vp-detail-verdict-' + outcome.bucket });
    banner.appendChild(el('span', { class: 'vp-detail-verdict-icon' }, [lucideIcon(outcome.icon)]));
    const bannerCopy = el('span', { class: 'vp-detail-verdict-copy' });
    bannerCopy.appendChild(el('strong', { text: outcome.title }));
    bannerCopy.appendChild(el('span', { text: outcome.reason }));
    banner.appendChild(bannerCopy);
    body.appendChild(banner);

    const declaration = _vpDetailSection('Declared payment', 'wallet-cards');
    const declarationGrid = el('div', { class: 'vp-detail-grid' });
    declarationGrid.appendChild(_vpDetailFact('Amount', _vpIsAmountPending(claim) ? 'Required' : _vpFmtMoney(claim.claimed_amount) + ' DH'));
    declarationGrid.appendChild(_vpDetailFact('Declared date', _vpFmtDate(claim.claimed_date)));
    declarationGrid.appendChild(_vpDetailFact('Destination account', claim.claimed_account ? _vpAccountLabel(claim.claimed_account) : null, { muted: !claim.claimed_account }));
    declarationGrid.appendChild(_vpDetailFact('Cash / transfer channel', claim.cash_transfer_service || null, { muted: !claim.cash_transfer_service }));
    declarationGrid.appendChild(_vpDetailFact('Paid by', claim.claimed_payer || null, { muted: !claim.claimed_payer }));
    declaration.appendChild(declarationGrid);
    body.appendChild(declaration);

    const receipt = _vpDetailSection('Receipt evidence', 'receipt-text');
    const receiptLayout = el('div', { class: 'vp-detail-receipt' });
    if (claim.media_url) {
      if ((claim.mime_type || '').startsWith('image/')) {
        const image = el('img', { class: 'vp-detail-receipt-image', src: claim.media_url, alt: 'Payment receipt', title: 'Click to enlarge' });
        image.addEventListener('click', () => openImageLightbox(claim.media_url));
        receiptLayout.appendChild(image);
      } else {
        const fileLink = el('a', {
          class: 'vp-detail-file', href: claim.media_url, target: '_blank', rel: 'noopener',
        }, [lucideIcon('file-text'), el('span', { text: 'Open receipt' })]);
        receiptLayout.appendChild(fileLink);
      }
    } else {
      receiptLayout.appendChild(el('div', { class: 'vp-detail-no-proof' }, [
        lucideIcon('file-question'), el('span', { text: 'No receipt file' }),
      ]));
    }
    const extractionGrid = el('div', { class: 'vp-detail-grid vp-detail-grid-receipt' });
    const extractionStatus = claim.extract_status === 'done'
      ? 'Read automatically'
      : claim.extract_status === 'failed'
        ? 'Reading failed'
        : claim.extract_status === 'pending'
          ? (_vpExtractStale(claim) ? 'Reading stuck' : 'Reading in progress')
          : 'Not analyzed';
    const confidence = Number(x.confidence);
    extractionGrid.appendChild(_vpDetailFact('Receipt reading', extractionStatus));
    extractionGrid.appendChild(_vpDetailFact('Confidence', Number.isFinite(confidence) ? Math.round(confidence) + '%' : null, { muted: !Number.isFinite(confidence) }));
    extractionGrid.appendChild(_vpDetailFact('Payer name read', x.payer_name || null, { muted: !x.payer_name }));
    extractionGrid.appendChild(_vpDetailFact('Issuing bank', x.bank || null, { muted: !x.bank }));
    extractionGrid.appendChild(_vpDetailFact('Reference', x.reference || null, { muted: !x.reference }));
    extractionGrid.appendChild(_vpDetailFact('Beneficiary read', x.beneficiary_raw || null, { muted: !x.beneficiary_raw }));
    receiptLayout.appendChild(extractionGrid);
    receipt.appendChild(receiptLayout);
    body.appendChild(receipt);

    const bank = _vpDetailSection(top ? 'Best bank credit' : 'Statement search', 'landmark');
    if (top && top.tx) {
      const tx = top.tx;
      const bankGrid = el('div', { class: 'vp-detail-grid' });
      bankGrid.appendChild(_vpDetailFact('Credit', _vpFmtMoney(tx.credit) + ' DH'));
      bankGrid.appendChild(_vpDetailFact('Bank date', _vpFmtDate(tx.op_date)));
      bankGrid.appendChild(_vpDetailFact('Account', _vpAccountLabel(tx.account)));
      bankGrid.appendChild(_vpDetailFact('Bank reference', tx.ref || null, { muted: !tx.ref }));
      bankGrid.appendChild(_vpDetailFact('Full description', tx.label || null, { wide: true, muted: !tx.label }));
      bank.appendChild(bankGrid);

      const why = top.why || {};
      const checks = el('div', { class: 'vp-detail-checks' });
      const claimedAmount = Number(claim.claimed_amount);
      const remaining = Number(tx.remaining);
      const exactAmount = Number.isFinite(claimedAmount) && Number.isFinite(remaining)
        && Math.abs(claimedAmount - remaining) <= 0.01;
      checks.appendChild(_vpDetailCheck(
        'Amount',
        exactAmount ? 'ok' : 'warn',
        exactAmount ? 'The declared amount exactly matches the available credit.' : 'The amount is not an exact match.',
      ));
      const gap = why.dateGapDays;
      checks.appendChild(_vpDetailCheck(
        'Date',
        gap == null ? 'neutral' : (gap >= -3 && gap <= 10 ? 'ok' : 'warn'),
        gap == null ? 'Not enough date information to compare.' : (gap === 0 ? 'Same day.' : `${Math.abs(gap)} day${Math.abs(gap) > 1 ? 's' : ''} apart.`),
      ));
      const nameState = why.nameStatus === 'match' ? 'ok' : why.nameStatus === 'mismatch' ? 'bad' : 'neutral';
      const nameText = why.nameStatus === 'match'
        ? 'The payer name matches the bank description.'
        : why.nameStatus === 'mismatch'
          ? 'The transfer name does not match the client or payer.'
          : 'No reliable name is available to compare.';
      checks.appendChild(_vpDetailCheck('Payer name', nameState, nameText));
      const accountKnown = !!(claim.claimed_account && tx.account);
      const accountMatches = accountKnown && claim.claimed_account === tx.account;
      checks.appendChild(_vpDetailCheck(
        'Account',
        accountKnown ? (accountMatches ? 'ok' : 'bad') : 'neutral',
        !accountKnown ? 'The destination account is not provided on both sides.' : (accountMatches ? 'Same destination account.' : 'The declared account is different.'),
      ));
      const shared = why.sharedWith | 0;
      const alternatives = why.altCandidates | 0;
      checks.appendChild(_vpDetailCheck(
        'Uniqueness',
        shared === 0 && alternatives === 0 ? 'ok' : 'warn',
        shared > 0
          ? `This credit is also proposed for ${shared} other client record${shared > 1 ? 's' : ''}.`
          : alternatives > 0
            ? `${alternatives + 1} credits may match this client record.`
            : 'Only one client record and one credit match.',
      ));
      bank.appendChild(checks);
    } else {
      bank.appendChild(el('div', { class: 'vp-detail-bank-empty' }, [
        lucideIcon('search-x'),
        el('span', { text: 'No matching credit was found in the imported statements for this period.' }),
      ]));
    }
    body.appendChild(bank);

    const closeBtn = el('button', { class: 'btn btn-ghost', type: 'button', text: 'Close' });
    closeBtn.addEventListener('click', _vpCloseDetail);
    actions.appendChild(closeBtn);
    if (_vpAdmin && top && top.certainty === 'sure') {
      const confirmBtn = el('button', { class: 'btn btn-primary vp-detail-confirm', type: 'button' }, [
        lucideIcon('check'), el('span', { text: 'Confirm this payment' }),
      ]);
      confirmBtn.addEventListener('click', () => {
        _vpCloseDetail();
        _vpConfirmClaim(claim, top.bankTxId, confirmBtn);
      });
      actions.appendChild(confirmBtn);
    }
    if (_vpAdmin && !_vpIsAmountPending(claim)) {
      const linkBtn = el('button', { class: 'btn btn-primary', type: 'button' }, [
        lucideIcon('link'), el('span', { text: 'Choose bank credit' }),
      ]);
      linkBtn.addEventListener('click', () => {
        _vpCloseDetail();
        _vpOpenLinkModal(claim);
      });
      actions.appendChild(linkBtn);
    }
    if (_vpAdmin) {
      const rejectBtn = el('button', { class: 'btn btn-ghost vp-detail-reject', type: 'button' }, [
        lucideIcon('x'), el('span', { text: 'Reject' }),
      ]);
      rejectBtn.addEventListener('click', () => {
        _vpCloseDetail();
        _vpRejectClaim(claim, rejectBtn);
      });
      actions.appendChild(rejectBtn);
    }

    drawer.classList.remove('hidden');
    document.body.classList.add('vp-detail-open');
    refreshIcons(drawer);
    document.getElementById('vpDetailClose')?.focus();
  }

  // "Payé par" inline edit — a compact ghost input under the client name.
  // PATCHes claimed_payer on commit; the server re-emits
  // 'payment-verification:updated' which reloads the queue (refreshed
  // suggestions: the name axis now has data). Optimistically marks the row
  // busy and reverts on failure.
  function _vpBuildPayerEdit(claim) {
    const wrap = el('div', { class: 'vp-payer-edit' });
    wrap.appendChild(el('span', { class: 'vp-payer-label', text: 'Paid by' }));
    const input = el('input', {
      type: 'text', class: 'vp-payer-input', maxlength: 120,
      placeholder: 'Who paid?', autocomplete: 'off', spellcheck: 'false',
      value: claim.claimed_payer || '',
      title: 'Name of the person who paid (Sent by)',
    });
    let saved = claim.claimed_payer || '';
    const commit = async () => {
      const next = input.value.trim();
      if (next === saved) { input.value = saved; return; }
      input.disabled = true;
      try {
        const r = await api('/api/payment-verify/verifications/' + encodeURIComponent(claim.id), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ claimed_payer: next || null }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
        saved = (j.verification && j.verification.claimed_payer) || '';
        claim.claimed_payer = saved;
        input.value = saved;
        toast(next ? 'Payer updated ✓' : 'Payer cleared ✓');
        // The 'payment-verification:updated' socket reloads the queue (with
        // refreshed suggestions) when the view is open; nothing else to do.
      } catch (err) {
        input.value = saved; // revert the field to the last persisted value
        toast('Failed — ' + (err.message || err), 'error');
      } finally {
        input.disabled = false;
      }
    };
    input.addEventListener('click', ev => ev.stopPropagation());
    input.addEventListener('blur', commit);
    input.addEventListener('keydown', ev => {
      if (ev.key === 'Enter') { ev.preventDefault(); input.blur(); }
      else if (ev.key === 'Escape') { ev.preventDefault(); input.value = saved; input.blur(); }
    });
    wrap.appendChild(input);
    return wrap;
  }

  // Inline claimed-amount editor (swat:B8a) — same ghost-input UX as the
  // payer editor above. PATCHes claimed_amount on commit; the server clears
  // the AMOUNT_PENDING marker (so a later Gemini pass can't clobber the
  // admin's figure) and re-emits the socket event, which reloads the queue
  // with suggestions computed against the real amount.
  function _vpBuildAmountEdit(claim) {
    const pending = _vpIsAmountPending(claim);
    const cell = el('div', { class: 'vp-amount vp-amount-edit' + (pending ? ' vp-amount-pending' : '') });
    const input = el('input', {
      type: 'text', inputmode: 'decimal', class: 'vp-amount-input', maxlength: 12,
      autocomplete: 'off', spellcheck: 'false', placeholder: 'Amount?',
      value: pending ? '' : _vpFmtMoney(claim.claimed_amount),
      title: pending
        ? 'Amount not read from receipt — enter the amount (DH)'
        : 'Declared amount (DH) — click to edit',
    });
    let saved = input.value;
    const commit = async () => {
      const rawTxt = input.value.trim();
      if (rawTxt === saved) { input.value = saved; return; }
      if (!rawTxt) { input.value = saved; return; }        // never PATCH an empty amount
      const amount = parseFloat(rawTxt.replace(/\s/g, '').replace(',', '.'));
      if (!Number.isFinite(amount) || amount <= 0) {
        toast('Invalid amount', 'error');
        input.value = saved;
        return;
      }
      input.disabled = true;
      try {
        const r = await api('/api/payment-verify/verifications/' + encodeURIComponent(claim.id), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ claimed_amount: amount }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
        claim.claimed_amount = (j.verification && j.verification.claimed_amount) || amount;
        claim.match_note = j.verification ? j.verification.match_note : claim.match_note;
        saved = _vpFmtMoney(claim.claimed_amount);
        input.value = saved;
        cell.classList.remove('vp-amount-pending');
        toast('Amount updated ✓');
        // The socket reload repaints the row (badge gone, fresh suggestions).
      } catch (err) {
        input.value = saved;
        toast('Failed — ' + (err.message || err), 'error');
      } finally {
        input.disabled = false;
      }
    };
    input.addEventListener('click', ev => ev.stopPropagation());
    input.addEventListener('blur', commit);
    input.addEventListener('keydown', ev => {
      if (ev.key === 'Enter') { ev.preventDefault(); input.blur(); }
      else if (ev.key === 'Escape') { ev.preventDefault(); input.value = saved; input.blur(); }
    });
    cell.appendChild(input);
    cell.appendChild(el('span', { class: 'vp-amount-unit', text: 'DH' }));
    return cell;
  }

  // ─── Manual receipt upload ────────────────────────────────────────────
  // One shared hidden <input type=file>, created lazily; the clicked claim's
  // id is stashed before opening the picker. On success the server emits
  // 'payment-verification:updated' (thumbnail now, Gemini extraction later)
  // which reloads this queue — no optimistic rendering needed.
  let _vpUploadInput = null;
  let _vpUploadClaimId = null;
  function _vpPickReceiptFile(claimId) {
    if (!_vpUploadInput) {
      _vpUploadInput = el('input', { type: 'file', accept: 'image/jpeg,image/png,image/webp,application/pdf,.pdf', class: 'hidden' });
      _vpUploadInput.addEventListener('change', () => {
        const f = _vpUploadInput.files && _vpUploadInput.files[0];
        const id = _vpUploadClaimId;
        _vpUploadInput.value = ''; // same file re-pickable after a failure
        if (f && id) _vpUploadReceipt(id, f);
      });
      document.body.appendChild(_vpUploadInput);
    }
    _vpUploadClaimId = claimId;
    _vpUploadInput.click();
  }

  async function _vpUploadReceipt(claimId, file) {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api('/api/payment-verify/verifications/' + encodeURIComponent(claimId) + '/receipt-upload', {
        method: 'POST',
        body: fd,
        timeoutMs: 120_000,
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) { toast('This payment already has a receipt', 'error'); loadVerifications(); return; }
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast('Receipt attached — reading in progress…');
    } catch (err) {
      toast('Upload failed — ' + (err.message || err), 'error');
    }
  }

  // Detach a wrong receipt from a pending claim (swat:B14). confirm() first —
  // this clears the claim↔receipt link (and possibly resets a Gemini-read
  // amount back to the placeholder); the media file itself is never deleted.
  async function _vpDetachReceipt(claim, btn) {
    if (!claim?.id) return;
    if (!confirm('Detach this receipt from the payment?\nThe file will stay in the conversation; only its link to this payment will be removed.')) return;
    if (btn) btn.disabled = true;
    try {
      const r = await api('/api/payment-verify/verifications/' + encodeURIComponent(claim.id) + '/receipt-detach', { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast('Receipt detached ✓');
      loadVerifications(); // socket refresh also lands; direct reload covers a dropped socket
    } catch (err) {
      toast('Failed — ' + (err.message || err), 'error');
      if (btn) btn.disabled = false;
    }
  }

  // Re-run the Gemini read on the existing receipt (swat:B13). The server
  // answers {started:true} immediately; extract_status flips pending →
  // done/failed over the socket, which repaints the row.
  async function _vpReExtract(claim, btn) {
    if (!claim?.id) return;
    if (btn) btn.disabled = true;
    try {
      const r = await api('/api/payment-verify/verifications/' + encodeURIComponent(claim.id) + '/re-extract', { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) { toast(j.error || 'Receipt reading is already in progress'); return; }
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast('Receipt reading restarted…');
    } catch (err) {
      toast('Failed — ' + (err.message || err), 'error');
      if (btn) btn.disabled = false;
    }
  }

  async function _vpConfirmClaim(claim, bankTxId, btn, opts = {}) {
    if (!claim?.id || !bankTxId) return;
    if (btn) btn.disabled = true;
    try {
      const body = { bankTxId };
      // (swat:B7a) Lier… on a partially consumed credit passes the remaining
      // capacity explicitly; otherwise the server defaults to claimed_amount.
      if (opts.matchedAmount != null && Number(opts.matchedAmount) > 0) body.matchedAmount = Number(opts.matchedAmount);
      // (swat:V3) Lier… = deliberate human choice → the server skips the
      // sure-only gate. Suggestion confirms never send this flag.
      if (opts.manual) body.manual = true;
      const r = await api('/api/payment-verify/verifications/' + encodeURIComponent(claim.id) + '/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) {
        toast(j.code === 'overconsumed'
          ? 'This credit has already been consumed by other verifications — choose another line.'
          : j.code === 'not_sure'
            ? (j.error || 'The match is not certain — use “Choose bank credit” to select it manually.')
            : 'Conflict — this payment has already been processed.', 'error');
        loadVerifications();
        return;
      }
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast('Payment verified ✅');
      loadVerifications();
      loadBankTx({ reset: true });
    } catch (err) {
      toast('Confirmation failed — ' + (err.message || err), 'error');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  async function _vpRejectClaim(claim, btn) {
    if (!claim?.id) return;
    const note = prompt('Reason for rejection (internal note):', '');
    if (note === null) return; // cancelled
    if (btn) btn.disabled = true;
    try {
      const r = await api('/api/payment-verify/verifications/' + encodeURIComponent(claim.id) + '/reject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast('Receipt rejected');
      loadVerifications();
    } catch (err) {
      toast('Rejection failed — ' + (err.message || err), 'error');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  // ─── Relevé bancaire — imported statement lines ─────────────
  async function loadBankTx({ reset = false } = {}) {
    // Latest-wins: every call supersedes any in-flight one (a chip click
    // during the initial load must never be silently dropped) — a stale
    // response is simply ignored when it resolves.
    const reqId = ++_vpBankReqId;
    if (reset) { _vpBankItems = []; _vpBankTotal = 0; }
    const empty = document.getElementById('vpBankEmpty');
    if (empty && reset) empty.textContent = 'Loading…';
    try {
      const params = new URLSearchParams();
      if (_vpAccount !== 'all') params.set('account', _vpAccount);
      params.set('month', _vpMonth);
      params.set('limit', '50');
      params.set('offset', String(_vpBankItems.length));
      const r = await api('/api/payment-verify/bank-transactions?' + params.toString());
      const j = await r.json().catch(() => ({}));
      if (reqId !== _vpBankReqId) return; // superseded by a newer request
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      _vpBankItems = reset ? (j.items || []) : [..._vpBankItems, ...(j.items || [])];
      _vpBankTotal = j.total ?? _vpBankItems.length;
      renderBankTable();
    } catch (err) {
      if (reqId !== _vpBankReqId) return; // superseded — don't paint a stale error
      const table = document.getElementById('vpBankTable');
      if (table) {
        clear(table);
        table.appendChild(el('div', { class: 'vp-empty', text: 'Could not load — ' + (err.message || err) }));
      }
    }
  }

  function _vpTxStatus(tx) {
    const matched = (tx.verifications || []).some(v => v.status === 'verified');
    if (matched) return { label: 'Matched', cls: 'vp-st-matched' };
    if (tx.excluded) return { label: 'Excluded', cls: 'vp-st-excluded' };
    if (tx.suspect_reason) return { label: 'Suspicious', cls: 'vp-st-suspect' };
    if (tx.credit != null) return { label: 'To match', cls: 'vp-st-open' };
    return { label: '—', cls: 'vp-st-debit' };
  }

  function renderBankTable() {
    const table = document.getElementById('vpBankTable');
    if (!table) return;
    clear(table);

    const bankCount = document.getElementById('vpBankCount');
    if (bankCount) bankCount.textContent = _vpBankTotal ? '· ' + _vpBankTotal : '';

    if (!_vpBankItems.length) {
      // Account-aware empty state — "0 lignes pour Fatima" is expected data,
      // not a missing import.
      const msg = _vpAccount !== 'all'
        ? 'No statement lines for “' + _vpAccountLabel(_vpAccount) + '” this month'
        : 'No statement lines this month — use “Import” to add a statement.';
      table.appendChild(el('div', { class: 'vp-empty', text: msg }));
      const pager = document.getElementById('vpBankPager');
      if (pager) clear(pager);
      return;
    }

    const header = el('div', { class: 'tickets-row tickets-row-header' });
    for (const t of ['Date', 'Description', 'Ref', 'Debit', 'Credit', 'Account', 'Status']) {
      header.appendChild(el('div', { text: t }));
    }
    table.appendChild(header);

    for (const tx of _vpBankItems) {
      const row = el('div', { class: 'tickets-row vp-bank-row', 'data-tx-id': tx.id });
      row.appendChild(el('div', { class: 'vp-date', text: _vpFmtDate(tx.op_date) }));
      row.appendChild(el('div', { class: 'vp-label', title: tx.label || '', text: tx.label || '—' }));
      row.appendChild(el('div', { class: 'vp-ref', text: tx.ref || '—' }));
      row.appendChild(el('div', { class: 'vp-amount vp-debit', text: tx.debit != null ? _vpFmtMoney(tx.debit) : '' }));
      row.appendChild(el('div', { class: 'vp-amount vp-credit', text: tx.credit != null ? _vpFmtMoney(tx.credit) : '' }));
      const accCell = el('div', {});
      accCell.appendChild(el('span', { class: 'vp-account-pill', 'data-account': tx.account, text: _vpAccountLabel(tx.account) }));
      row.appendChild(accCell);
      const st = _vpTxStatus(tx);
      const stCell = el('div', {});
      const matchedNames = (tx.verifications || []).filter(v => v.status === 'verified').map(v => v.client_name).filter(Boolean);
      stCell.appendChild(el('span', {
        class: 'vp-status-pill ' + st.cls,
        title: matchedNames.length ? ('Matched with: ' + matchedNames.join(', ')) : (tx.suspect_reason || tx.excluded_reason || ''),
        text: st.label,
      }));
      row.appendChild(stCell);
      table.appendChild(row);
    }

    const pager = document.getElementById('vpBankPager');
    if (pager) {
      clear(pager);
      if (_vpBankItems.length < _vpBankTotal) {
        const btn = el('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: 'Load more (' + _vpBankItems.length + '/' + _vpBankTotal + ')' });
        btn.addEventListener('click', () => loadBankTx({ reset: false }));
        pager.appendChild(btn);
      }
    }
    refreshIcons(table);
  }

  // ─── Import modal — Fichier / Coller / Manuel + dry-run preview ─────
  function _vpOpenImportModal() {
    _vpImportTab = 'file';
    _vpSetImportTab('file');
    const file = document.getElementById('vpImportFile');
    if (file) file.value = '';
    const paste = document.getElementById('vpImportPaste');
    if (paste) paste.value = '';
    for (const id of ['vpManualDate', 'vpManualLabel', 'vpManualAmount', 'vpManualNote']) {
      const n = document.getElementById(id);
      if (n) n.value = '';
    }
    _vpInvalidateAnalysis();
    openModal('vpImportModal');
  }

  function _vpSetImportTab(tab) {
    _vpImportTab = tab;
    document.querySelectorAll('#vpImportTabs [data-vp-tab]').forEach(btn => {
      const active = btn.getAttribute('data-vp-tab') === tab;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-selected', String(active));
    });
    document.querySelectorAll('#vpImportModal [data-vp-pane]').forEach(pane => {
      pane.classList.toggle('hidden', pane.getAttribute('data-vp-pane') !== tab);
    });
    // Manual rows skip the dry-run pass — Analyser hides, Confirmer is live.
    const analyzeBtn = document.getElementById('vpAnalyzeBtn');
    if (analyzeBtn) analyzeBtn.classList.toggle('hidden', tab === 'manual');
    const confirmBtn = document.getElementById('vpImportConfirmBtn');
    if (confirmBtn) {
      confirmBtn.textContent = tab === 'manual' ? 'Add line' : 'Confirm import';
      confirmBtn.disabled = tab === 'manual' ? false : !_vpImportAnalyzed;
    }
  }

  function _vpInvalidateAnalysis() {
    _vpImportAnalyzed = false;
    const confirmBtn = document.getElementById('vpImportConfirmBtn');
    if (confirmBtn && _vpImportTab !== 'manual') confirmBtn.disabled = true;
    const preview = document.getElementById('vpImportPreview');
    if (preview) { preview.classList.add('hidden'); clear(preview); }
  }

  // POST the import (multipart for files, JSON for paste). dryRun=1 returns
  // the preview without writing anything.
  async function _vpPostImport({ dryRun }) {
    const account = document.getElementById('vpImportAccount')?.value || 'lougha';
    if (_vpImportTab === 'file') {
      const f = document.getElementById('vpImportFile')?.files?.[0];
      if (!f) { toast('Choose a .csv, .xlsx, or .pdf file first.', 'error'); return null; }
      const fd = new FormData();
      fd.append('file', f);
      fd.append('account', account);
      if (dryRun) fd.append('dryRun', '1');
      return api('/api/payment-verify/import', { method: 'POST', body: fd, timeoutMs: 120_000 });
    }
    const text = document.getElementById('vpImportPaste')?.value || '';
    if (!text.trim()) { toast('Paste the statement rows first.', 'error'); return null; }
    return api('/api/payment-verify/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account, text, dryRun: dryRun ? 1 : 0 }),
      timeoutMs: 120_000,
    });
  }

  async function _vpAnalyze() {
    if (_vpImportBusy || _vpImportTab === 'manual') return;
    _vpImportBusy = true;
    const analyzeBtn = document.getElementById('vpAnalyzeBtn');
    const analyzeBtnHtml = analyzeBtn?.innerHTML || '';
    if (analyzeBtn) {
      analyzeBtn.disabled = true;
      analyzeBtn.setAttribute('aria-busy', 'true');
      analyzeBtn.textContent = 'Analyzing…';
    }
    try {
      const r = await _vpPostImport({ dryRun: true });
      if (!r) return;
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      _vpRenderPreview(j);
      // `inserted` is the dry-run's projected NEW-row count. A statement can
      // parse perfectly while every row is already present; enabling Confirm
      // in that case made the importer look broken ("0 inserted") instead of
      // explaining that the same statement had already been imported.
      const total = Number(j.total) || 0;
      const projectedNew = Number(j.inserted) || 0;
      const duplicates = Number(j.duplicates) || 0;
      const hasNewRows = projectedNew > 0;
      _vpImportAnalyzed = hasNewRows;
      const confirmBtn = document.getElementById('vpImportConfirmBtn');
      if (confirmBtn) confirmBtn.disabled = !hasNewRows;
      if (total <= 0) {
        toast('No importable rows detected — check the file format.', 'error');
      } else if (!hasNewRows && duplicates > 0) {
        toast(`This statement has already been imported — ${duplicates} duplicate row(s), no new rows.`, 'error');
      } else if (!hasNewRows) {
        toast('No new rows to import.', 'error');
      }
    } catch (err) {
      toast('Analysis failed — ' + (err.message || err), 'error');
    } finally {
      _vpImportBusy = false;
      if (analyzeBtn) {
        analyzeBtn.disabled = false;
        analyzeBtn.removeAttribute('aria-busy');
        analyzeBtn.innerHTML = analyzeBtnHtml;
      }
    }
  }

  function _vpRenderPreview(res) {
    const host = document.getElementById('vpImportPreview');
    if (!host) return;
    clear(host);
    host.classList.remove('hidden');

    const counts = el('div', { class: 'vp-import-counts' });
    const stats = [
      ['Rows', res.total ?? 0, ''],
      ['New', res.inserted ?? 0, 'vp-cnt-ok'],
      ['Duplicates', res.duplicates ?? 0, 'vp-cnt-dup'],
      ['Suspicious', res.suspects ?? 0, 'vp-cnt-sus'],
    ];
    for (const [label, n, cls] of stats) {
      const chip = el('div', { class: 'vp-import-count ' + cls });
      chip.appendChild(el('span', { class: 'vp-import-count-num', text: String(n) }));
      chip.appendChild(el('span', { class: 'vp-import-count-label', text: label }));
      counts.appendChild(chip);
    }
    host.appendChild(counts);

    const total = Number(res.total) || 0;
    const projectedNew = Number(res.inserted) || 0;
    const duplicates = Number(res.duplicates) || 0;
    if (total > 0 && projectedNew === 0 && duplicates > 0) {
      const account = document.getElementById('vpImportAccount')?.value || 'lougha';
      const already = el('div', { class: 'vp-import-notice vp-import-notice-duplicate' });
      already.appendChild(el('i', { 'data-lucide': 'circle-check-big' }));
      const copy = el('div', { class: 'vp-import-notice-copy' });
      copy.appendChild(el('strong', { text: 'This statement has already been imported' }));
      copy.appendChild(el('span', {
        text: `${duplicates} row(s) already exist in ${_vpAccountLabel(account)}. There are no new transactions to add.`,
      }));
      already.appendChild(copy);
      host.appendChild(already);
    }

    const errors = res.errors || [];
    if (errors.length) {
      const errBox = el('div', { class: 'vp-import-errors' });
      errBox.appendChild(el('div', { class: 'vp-import-errors-title', text: errors.length + ' ignored row(s):' }));
      for (const e of errors.slice(0, 5)) {
        errBox.appendChild(el('div', { class: 'vp-import-error-row', text: 'Row ' + e.line + ' — ' + e.msg }));
      }
      if (errors.length > 5) errBox.appendChild(el('div', { class: 'vp-import-error-row', text: '… and ' + (errors.length - 5) + ' more.' }));
      host.appendChild(errBox);
    }

    const rows = res.preview || [];
    if (rows.length) {
      const prev = el('div', { class: 'vp-prev-table' });
      const head = el('div', { class: 'vp-prev-row vp-prev-head' });
      for (const t of ['Date', 'Description', 'Debit', 'Credit', '']) head.appendChild(el('div', { text: t }));
      prev.appendChild(head);
      for (const m of rows.slice(0, 8)) {
        const r = el('div', { class: 'vp-prev-row' });
        r.appendChild(el('div', { text: _vpFmtDate(m.op_date) }));
        r.appendChild(el('div', { class: 'vp-prev-label', title: m.label || '', text: m.label || '—' }));
        r.appendChild(el('div', { class: 'vp-amount vp-debit', text: m.debit != null ? _vpFmtMoney(m.debit) : '' }));
        r.appendChild(el('div', { class: 'vp-amount vp-credit', text: m.credit != null ? _vpFmtMoney(m.credit) : '' }));
        const flag = m.excluded ? 'excluded' : (m.suspect_reason ? 'suspicious' : '');
        r.appendChild(el('div', { class: 'vp-prev-flag', text: flag }));
        prev.appendChild(r);
      }
      if (rows.length > 8) prev.appendChild(el('div', { class: 'vp-prev-more', text: '… ' + (rows.length - 8) + ' more rows in the preview.' }));
      host.appendChild(prev);
    }
    refreshIcons(host);
    // The preview is below the file control inside a scrollable modal body.
    // Bring the result into view so a successful analysis never appears to do
    // nothing on shorter screens.
    requestAnimationFrame(() => host.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
  }

  async function _vpConfirmImport() {
    if (_vpImportBusy) return;
    const confirmBtn = document.getElementById('vpImportConfirmBtn');

    // Manual tab — single bank row, POSTed directly (no dry-run pass).
    if (_vpImportTab === 'manual') {
      const account = document.getElementById('vpImportAccount')?.value || 'lougha';
      const op_date = document.getElementById('vpManualDate')?.value || '';
      const label = (document.getElementById('vpManualLabel')?.value || '').trim();
      const note = (document.getElementById('vpManualNote')?.value || '').trim();
      const amount = parseFloat(String(document.getElementById('vpManualAmount')?.value || '').replace(/\s/g, '').replace(',', '.'));
      if (!op_date) { toast('Date is required.', 'error'); return; }
      if (!Number.isFinite(amount) || amount <= 0) { toast('A valid amount greater than 0 is required.', 'error'); return; }
      _vpImportBusy = true;
      if (confirmBtn) confirmBtn.disabled = true;
      try {
        const r = await api('/api/payment-verify/bank-transactions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ account, op_date, label, credit: amount, note: note || null }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
        toast('Line added to the statement ✓');
        closeModal('vpImportModal');
        loadBankTx({ reset: true });
        loadVerifications(); // new credit may unlock a suggestion
      } catch (err) {
        toast('Could not add the line — ' + (err.message || err), 'error');
      } finally {
        _vpImportBusy = false;
        if (confirmBtn) confirmBtn.disabled = false;
      }
      return;
    }

    // File / paste — real import after a successful dry-run.
    if (!_vpImportAnalyzed) { toast('Run “Analyze” first to check the file.', 'error'); return; }
    _vpImportBusy = true;
    if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.textContent = 'Importing…'; }
    try {
      const r = await _vpPostImport({ dryRun: false });
      if (!r) return;
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast(`Import complete — ${j.inserted ?? 0} inserted · ${j.duplicates ?? 0} duplicates · ${j.suspects ?? 0} suspicious`);
      closeModal('vpImportModal');
      loadBankTx({ reset: true });
      loadVerifications(); // fresh credits → fresh suggestions
    } catch (err) {
      toast('Import failed — ' + (err.message || err), 'error');
    } finally {
      _vpImportBusy = false;
      if (confirmBtn) { confirmBtn.disabled = false; confirmBtn.textContent = 'Confirm import'; }
    }
  }

  // ─── "Lier…" picker — searchable unmatched bank credits ─────────────
  function _vpOpenLinkModal(claim) {
    _vpLinkClaim = claim;
    const summary = document.getElementById('vpLinkClaimSummary');
    if (summary) {
      clear(summary);
      summary.appendChild(el('span', { class: 'vp-link-claim-name', text: claim.client_name || claim.claimed_payer || 'Client' }));
      summary.appendChild(el('span', { class: 'vp-link-claim-meta', text: ' · ' + _vpFmtMoney(claim.claimed_amount) + ' DH · ' + _vpFmtDate(claim.claimed_date) }));
    }
    const search = document.getElementById('vpLinkSearch');
    if (search) search.value = '';
    openModal('vpLinkModal');
    _vpLoadLinkTxs();
    setTimeout(() => search?.focus(), 60);
  }

  async function _vpLoadLinkTxs() {
    const list = document.getElementById('vpLinkList');
    if (!list || !_vpLinkClaim) return;
    clear(list);
    list.appendChild(el('div', { class: 'vp-empty', text: 'Loading…' }));
    try {
      const q = (document.getElementById('vpLinkSearch')?.value || '').trim();
      // (swat:B7b) Cross-month window: a July claim must see a June 30 credit.
      // Fetch every month touched by claimed_date ±35 days (the page-level
      // month picker is deliberately NOT involved); no claimed date → fall
      // back to the page month.
      const DAY = 86400000;
      const dm = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(_vpLinkClaim.claimed_date || ''));
      const claimMs = dm ? Date.UTC(+dm[1], +dm[2] - 1, +dm[3]) : null;
      let months, winFrom = null, winTo = null;
      if (claimMs != null) {
        winFrom = claimMs - 35 * DAY;
        winTo = claimMs + 35 * DAY;
        months = [...new Set([winFrom, claimMs, winTo].map(ms => new Date(ms).toISOString().slice(0, 7)))];
      } else {
        months = [_vpMonth];
      }
      const pages = await Promise.all(months.map(async (month) => {
        const params = new URLSearchParams();
        params.set('month', month);
        params.set('limit', '100');
        if (q) params.set('q', q);
        const r = await api('/api/payment-verify/bank-transactions?' + params.toString());
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
        return j.items || [];
      }));
      // (swat:B7a) Only credits with enough REMAINING capacity are linkable:
      // remaining = credit − Σ(verified matched_amount). A credit partially
      // consumed by another claim stays available for the rest — dropping it
      // outright hid legitimate family/aggregate virements.
      const claimed = Number(_vpLinkClaim.claimed_amount) || 0;
      const seen = new Set();
      const candidates = [];
      for (const tx of pages.flat()) {
        if (!tx || seen.has(tx.id)) continue;
        seen.add(tx.id);
        if (tx.credit == null || tx.excluded) continue;
        const consumed = (tx.verifications || []).reduce((s, v) =>
          s + (v.status === 'verified' ? (Number(v.matched_amount) || 0) : 0), 0);
        const remaining = Math.round((Number(tx.credit) - consumed) * 100) / 100;
        if (remaining < claimed - 1) continue; // same tolerance as the matcher prefilter
        if (winFrom != null) {
          const tm = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(tx.op_date || ''));
          const txMs = tm ? Date.UTC(+tm[1], +tm[2] - 1, +tm[3]) : null;
          if (txMs != null && (txMs < winFrom || txMs > winTo)) continue;
        }
        candidates.push({ ...tx, remaining, partial: consumed > 0 });
      }
      candidates.sort((a, b) => String(b.op_date || '').localeCompare(String(a.op_date || '')));
      _vpRenderLinkList(candidates);
    } catch (err) {
      clear(list);
      list.appendChild(el('div', { class: 'vp-empty', text: 'Could not load — ' + (err.message || err) }));
    }
  }

  function _vpRenderLinkList(txs) {
    const list = document.getElementById('vpLinkList');
    if (!list) return;
    clear(list);
    if (!txs.length) {
      list.appendChild(el('div', { class: 'vp-empty', text: 'No unmatched bank credit fits this payment.' }));
      return;
    }
    // (swat:V3) Competition map — how many OTHER pending claims this credit
    // is suggested for. Data comes straight from the V1 single-pass
    // suggestions already loaded on the board (no extra fetch).
    const competition = new Map();
    for (const c of _vpQueueItems) {
      if (!c || (_vpLinkClaim && c.id === _vpLinkClaim.id)) continue;
      if (String(c.status || 'pending') !== 'pending') continue;
      for (const s of (c.suggestions || [])) {
        competition.set(s.bankTxId, (competition.get(s.bankTxId) || 0) + 1);
      }
    }
    for (const tx of txs) {
      const rowBtn = el('button', { class: 'vp-link-row', type: 'button', 'data-tx-id': tx.id });
      const main = el('div', { class: 'vp-link-row-main' });
      main.appendChild(el('span', { class: 'vp-link-row-label', title: tx.label || '', text: tx.label || '(no description)' }));
      // Date first — the picker now crosses months (swat:B7b), so it must be obvious.
      main.appendChild(el('span', { class: 'vp-link-row-meta', text: _vpFmtDate(tx.op_date) + ' · ' + _vpAccountLabel(tx.account) + (tx.ref ? ' · ref ' + tx.ref : '') }));
      const nComp = competition.get(tx.id) || 0;
      if (nComp > 0) {
        main.appendChild(el('span', {
          class: 'vp-link-row-competition',
          title: 'This credit is also the best candidate for other pending payments — verify before linking.',
          text: `⚠ also proposed for ${nComp} other pending payment${nComp > 1 ? 's' : ''}`,
        }));
      }
      rowBtn.appendChild(main);
      const amountWrap = el('span', { class: 'vp-link-row-amountwrap' });
      amountWrap.appendChild(el('span', { class: 'vp-link-row-amount', text: _vpFmtMoney(tx.credit) + ' DH' }));
      if (tx.partial && tx.remaining != null) {
        amountWrap.appendChild(el('span', {
          class: 'vp-link-row-rest',
          title: 'Credit partially consumed by other verifications',
          text: _vpFmtMoney(tx.remaining) + ' DH remaining',
        }));
      }
      rowBtn.appendChild(amountWrap);
      rowBtn.addEventListener('click', async () => {
        if (!_vpLinkClaim) return;
        rowBtn.disabled = true;
        // (swat:B7a) Partially consumed credit smaller than the claim: pass
        // the remaining as the matched amount instead of letting the server
        // default to claimed_amount and 409 'overconsumed'.
        const claimAmt = Number(_vpLinkClaim.claimed_amount) || 0;
        const matchedAmount = (tx.remaining != null && tx.remaining > 0 && tx.remaining < claimAmt)
          ? tx.remaining : null;
        // (swat:V3) manual: the Lier… picker IS the deliberate human choice —
        // it bypasses the sure-only server gate by design.
        await _vpConfirmClaim(_vpLinkClaim, tx.id, null, { matchedAmount, manual: true });
        closeModal('vpLinkModal');
        _vpLinkClaim = null;
      });
      list.appendChild(rowBtn);
    }
  }

  // Normalize a phone number to international digits suitable for WhatsApp.
  // This is the browser's dependency-free counterpart to lib/phone.js:
  // Morocco local numbers are canonicalized, while international numbers
  // must use a served-country code and a plausible national length. Keeping
  // the country allowlist here prevents Meta/social object IDs from becoming
  // virtual WhatsApp chats merely because they contain 8–15 digits.
  //
  // Keep the country set in sync with SERVED_COUNTRIES in lib/phone.js.
  // Length bounds are deliberately fail-closed and cover each country's
  // valid libphonenumber range; the server remains authoritative whenever it
  // supplies a functional chatJid.
  function _phoneForWhatsapp(raw) {
    if (!raw) return null;
    const source = String(raw).trim();
    if (!source || source.includes('*')) return null;
    let d = source.replace(/[^\d]/g, '');
    if (!d) return null;
    if (d.startsWith('00')) d = d.slice(2); // 0021266… → 21266…

    // Moroccan national forms are the only safe implicit-country inputs.
    if (/^0[5-7]\d{8}$/.test(d)) d = '212' + d.slice(1);
    else if (/^[5-7]\d{8}$/.test(d)) d = '212' + d;

    if (d.length < 8 || d.length > 15) return null;

    // calling code → accepted national-number length range. Calling codes
    // are ordered longest-first so 351/352/353/356/358 do not hit 35x
    // ambiguously. `7` is intentionally absent (RU/KZ are not served), which
    // also blocks the HubSpot/Meta object-id shape behind this incident.
    const served = [
      ['212', 9, 9], ['213', 8, 9], ['216', 8, 8], ['218', 8, 10],
      ['222', 8, 8], ['221', 9, 9], ['225', 10, 10], ['223', 8, 8],
      ['351', 9, 9], ['352', 4, 11], ['353', 7, 9], ['356', 8, 8], ['358', 5, 12],
      ['420', 9, 9],
      ['966', 9, 9], ['971', 9, 9], ['974', 8, 8], ['965', 8, 8],
      ['973', 8, 8], ['968', 8, 8], ['962', 8, 9], ['961', 7, 8],
      ['20', 10, 10], ['33', 9, 9], ['34', 9, 9], ['39', 6, 11],
      ['49', 7, 11], ['32', 8, 9], ['31', 9, 9], ['41', 9, 9],
      ['44', 10, 10], ['43', 7, 13], ['46', 7, 10], ['45', 8, 8],
      ['47', 8, 8], ['30', 10, 10], ['40', 9, 9], ['90', 10, 10],
      ['86', 11, 11], ['1', 10, 10],
    ];
    for (const [cc, min, max] of served) {
      if (!d.startsWith(cc)) continue;
      const nationalLength = d.length - cc.length;
      return nationalLength >= min && nationalLength <= max ? d : null;
    }
    return null;
  }

  // Functional chat ids come from server-side validation and survive display
  // masking. Accept only the exact 1-1 WhatsApp JID contract: never strip
  // punctuation from arbitrary values because that recreates truncated chats.
  function _validatedFunctionalChatJid(raw) {
    const match = /^(\d{8,15})@s\.whatsapp\.net$/.exec(String(raw || ''));
    return match ? match[0] : '';
  }

  // Fields only admins can rewrite. Mirrored on the server PATCH endpoint.
  // Agents see the value as plain text (or, for phone, still get the
  // click-to-inbox link without the edit pencil). Name fields stay agent-
  // editable since agents often fix typos / add the full name themselves.
  // whatsapp_phone is deliberately NOT here: it's the manual-entry field the
  // sales team fills with the WA number the client gave them when the main
  // phone isn't reachable — agents must be able to set it themselves.
  const CONTACT_ADMIN_ONLY = new Set([
    'phone', 'mobilephone',
    'hubspot_owner_id',
  ]);
  // Long-text fields open in a modal with a real textarea instead of the
  // tiny inline input — agents use these to write paragraph-length notes.
  const CONTACT_LONGTEXT = new Set([
    'notes', 'data_notes', 'data_feedback',
    'note_payment', 'note_date_of_payment',
  ]);
  function _isLongTextField(def, propName) {
    if (!def) return CONTACT_LONGTEXT.has(propName);
    if (def.fieldType === 'textarea' || def.type === 'string_textarea') return true;
    return CONTACT_LONGTEXT.has(propName);
  }
  // Sales super-user gate (mirror of the backend _isSalesAdmin). True for admin,
  // super_admin, and the new super_sales role — admin-equivalent for the
  // sales/inbox surface (see-all, assign, edit owner/phone). super_sales never
  // gets state.me.superAdmin, so it stays excluded from super-admin chrome.
  function _isSalesAdmin() {
    const role = (state.me?.role || '').toLowerCase();
    return role === 'admin' || role === 'super_admin' || role === 'super_sales' || !!state.me?.superAdmin;
  }
  function _userIsAdmin() {
    return _isSalesAdmin();
  }

  // 3CX call icon for phone cells — sits next to the WhatsApp icon so agents
  // dial straight from the Contacts table. Same digits as the WA button
  // (real ones via chatJid even when the displayed value is masked).
  // The WhatsApp glyph, in one place. It is used on the phone cell and on each
  // suggested number, and a 1.5 KB path duplicated across two call sites drifts.
  const _WA_ICON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="#25D366"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>';

  function _contactWaBtn(jid, displayName, title = 'Open WhatsApp conversation') {
    const btn = el('button', { type: 'button', class: 'contact-wa-btn', title, 'aria-label': title });
    btn.innerHTML = _WA_ICON_SVG;
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      openInboxForChatJid(jid, displayName);
    });
    return btn;
  }

  function _contactCxBtn(digits, displayName) {
    const btn = el('button', {
      type: 'button',
      class: 'contact-cx-btn',
      title: 'Appeler via 3CX',
      'aria-label': 'Appeler via 3CX',
    });
    btn.appendChild(el('i', { 'data-lucide': 'phone-call' }));
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      cxDial(digits, displayName, btn);
    });
    return btn;
  }

  // Universal cell builder. Reads the property definition to decide UI:
  //   - phone/phonenumber → clickable WhatsApp link (with edit-on-hover)
  //   - enumeration → click pill → dropdown of options[]
  //   - bool → click pill → toggle true/false/(clear)
  //   - date / datetime → click → date input
  //   - number → click → number input
  //   - string / textarea → click → text input
  function _buildContactCell(c, propName) {
    const def = CONTACTS.propsByName[propName] || { name: propName, type: 'string', options: [] };
    const td = el('td');

    // "Lead Source" — read-only origin pill (derived from leads.source, not a
    // HubSpot property and not editable). Rendered as a <span> (no editor) with
    // a colour class per source kind. Shows "—" when there is no lead record.
    if (propName === 'lead_source') {
      const meta = _leadSourceMeta(c.lead_source);
      const pill = el('span', {
        class: 'contact-pill source-pill source-' + meta.kind + (meta.label ? '' : ' contact-pill-empty'),
        text: meta.label || '—',
        title: c.lead_source ? ('Lead source: ' + c.lead_source) : 'No lead record for this contact',
      });
      td.appendChild(pill);
      return td;
    }

    // "Pays probable" — one pill per finished number this one could be, most
    // likely first, straight from the server. Clicking a pill IS the fix: the
    // lead, the HubSpot contact and the flags all move together, and the row
    // leaves this view on the next refresh.
    //
    // No pill means we could not place the number, which is the honest answer
    // for the ~20 where five countries fit equally — those need the client.
    if (propName === 'phone_suggestion') {
      const sug = Array.isArray(c.phone_suggestions) ? c.phone_suggestions : [];
      if (!sug.length) {
        td.appendChild(el('span', { class: 'contact-pill contact-pill-empty', text: '—' }));
        return td;
      }
      const displayName = ((c.firstname || '') + ' ' + (c.lastname || '')).trim() || '';
      sug.forEach((s, i) => {
        const row = el('span', { class: 'contact-suggest-row' });
        const pill = el('button', {
          type: 'button',
          class: 'contact-pill contact-suggest-pill' + (i === 0 ? ' contact-suggest-pill--top' : ''),
          text: `${_countryFlag(s.country)} ${s.formatted || ('+' + s.e164)}`,
          title: `Confirmer ce numéro pour ${(c.firstname || '') + ' ' + (c.lastname || '')}`.trim()
            + (i === 0 ? ' — le plus probable' : ''),
        });
        pill.addEventListener('click', async (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          // A number is not something to change by accident from a list view.
          if (!confirm(`Confirmer ${s.formatted || ('+' + s.e164)} comme numéro de ce client ?`)) return;
          pill.disabled = true;
          try {
            // api() returns the raw Response and does NOT reject on 4xx/5xx —
            // reading it as parsed JSON reported every failure as a success.
            // And without the Content-Type the body never reaches req.body, so
            // the very first click 400'd while the toast said it had worked.
            const r = await api('/api/leads/apply-phone-suggestion', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ contactId: c.id, phone: s.e164 }),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j?.error || ('HTTP ' + r.status));
            toast(`Numéro confirmé : +${j.to}`, 'success');
            await refreshContacts();
          } catch (err) {
            pill.disabled = false;
            toast(err?.message || 'Échec de la confirmation', 'error');
          }
        });
        row.appendChild(pill);
        // Reach the number BEFORE committing to it. Confirming rewrites the
        // client's record, so the honest order is: try it, then confirm — and
        // an agent who gets an answer on WhatsApp has all the proof she needs.
        // These act on the SUGGESTED number, not the broken one on the row.
        row.appendChild(_contactWaBtn(s.e164 + '@s.whatsapp.net', displayName || s.formatted,
          'Écrire sur WhatsApp à ce numéro (avant de confirmer)'));
        row.appendChild(_contactCxBtn(s.e164, displayName || s.formatted));
        td.appendChild(row);
      });
      return td;
    }

    // "N° WhatsApp" — the contact's REAL WhatsApp number. Display priority:
    // the manually-entered whatsapp_phone property (the team fills it with
    // the number the client gave them when the main phone isn't reachable),
    // else the computed server-side fallback (linked lead's WA chat number).
    // Editable by ALL roles — edits PATCH the real whatsapp_phone property;
    // the editor is seeded with the DISPLAYED value (manual, else computed) and
    // an unchanged save is a no-op, so the computed number is never written.
    if (propName === 'whatsapp_number') {
      const manual = String(c.properties?.whatsapp_phone || c.whatsapp_phone || '').trim();
      const v = manual || String(c.whatsapp_number || '').trim();
      const waDef = CONTACTS.propsByName['whatsapp_phone']
        || { name: 'whatsapp_phone', label: 'WhatsApp phone', type: 'string', fieldType: 'text' };
      if (!v) {
        // Empty cells stay BLANK (Ayoub, 2026-08-15) — no "set" placeholder;
        // the pill keeps its hit-area/hover affordance via CSS.
        const setPill = el('button', {
          type: 'button',
          class: 'contact-pill contact-pill-empty',
          title: 'Add the WhatsApp number the client gave you',
          'aria-label': 'Set WhatsApp number',
        });
        setPill.addEventListener('click', () => _openContactEditor(setPill, c, waDef, ''));
        td.appendChild(setPill);
        td._contactsOpenEditor = () => setPill.click();
        return td;
      }
      // Masked values (agents) don't normalize — use the exact functional JID
      // the server validated for actions. Raw input is only a strict fallback.
      const localWa = _phoneForWhatsapp(v);
      const functionalJid = _validatedFunctionalChatJid(c.chatJid)
        || (localWa ? localWa + '@s.whatsapp.net' : '');
      const wa = functionalJid ? functionalJid.split('@')[0] : '';
      const wrap = el('div', { style: 'display:flex; align-items:center; gap:6px;' });
      if (wa) {
        const displayName = ((c.firstname || '') + ' ' + (c.lastname || '')).trim() || v;
        const waBtn = el('button', {
          type: 'button',
          class: 'contact-wa-btn',
          title: 'Open WhatsApp conversation',
          'aria-label': 'Open WhatsApp conversation',
        });
        waBtn.appendChild(el('i', { 'data-lucide': 'message-circle' }));
        waBtn.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          openInboxForChatJid(functionalJid, displayName);
        });
        wrap.appendChild(waBtn);
        wrap.appendChild(_contactCxBtn(wa, displayName));
        const link = el('button', {
          type: 'button',
          class: 'contact-wa-link',
          title: 'Open conversation in inbox',
          text: v,
        });
        link.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          openInboxForChatJid(functionalJid, displayName);
        });
        wrap.appendChild(link);
      } else {
        wrap.appendChild(el('span', { text: v, style: 'color:var(--text-secondary);' }));
      }
      // Manual-entry pencil for every role (not admin-only like the main
      // phone): correcting / adding the client-given WA number is exactly
      // the agents' workflow for this field.
      const editBtn = el('button', {
        type: 'button',
        class: 'contact-wa-edit',
        title: 'Edit WhatsApp number',
        'aria-label': 'Edit WhatsApp number',
      }, [el('i', { 'data-lucide': 'pencil' })]);
      // Prefill with the number the cell SHOWS (manual value, else the computed
      // WA-chat fallback) so the agent only corrects a digit — an editor that
      // opened empty was the bug (Ayoub, 2026-08-15). Saving the prefilled
      // value unchanged is a no-op in the cell editor, so a computed fallback
      // is never written to HubSpot by accident.
      editBtn.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _openContactEditor(editBtn, c, waDef, v); });
      wrap.appendChild(editBtn);
      td.appendChild(wrap);
      td._contactsOpenEditor = () => editBtn.click();
      return td;
    }

    // Read value from top-level mirror columns first, then properties bag
    const rawValue = (c[propName] !== undefined && c[propName] !== '') ? c[propName]
                     : (c.properties?.[propName] ?? '');
    const isAdmin = _userIsAdmin();
    const isAdminOnly = CONTACT_ADMIN_ONLY.has(propName);

    // Phone-shaped fields → click jumps to the leads7 INBOX with that
    // contact's conversation auto-selected (matches user's expectation —
    // open the chat inside the platform, not WhatsApp web).
    const isPhoneField = def.fieldType === 'phonenumber'
                       || propName === 'phone' || propName === 'mobilephone' || propName === 'whatsapp_phone'
                       || /phone/i.test(propName);
    if (isPhoneField && rawValue) {
      const localWa = _phoneForWhatsapp(rawValue);
      const functionalJid = _validatedFunctionalChatJid(c.chatJid)
        || (localWa ? localWa + '@s.whatsapp.net' : '');
      const wa = functionalJid ? functionalJid.split('@')[0] : '';
      const wrap = el('div', { style: 'display:flex; align-items:center; gap:6px;' });
      if (wa) {
        const displayName = ((c.firstname || '') + ' ' + (c.lastname || '')).trim() || rawValue;
        // WhatsApp green icon — clickable, opens the internal inbox
        // conversation for this phone (NOT wa.me — user wants internal).
        wrap.appendChild(_contactWaBtn(functionalJid, displayName));
        wrap.appendChild(_contactCxBtn(wa, displayName));

        const link = el('button', {
          type: 'button',
          class: 'contact-wa-link',
          title: 'Open conversation in inbox',
          text: rawValue,
        });
        link.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          openInboxForChatJid(functionalJid, displayName);
        });
        wrap.appendChild(link);
      } else {
        wrap.appendChild(el('span', { text: rawValue, style: 'color:var(--text-secondary);' }));
      }
      // Pencil-to-edit is admin-only — except whatsapp_phone, the manual-entry
      // field agents fill themselves with the client-given WA number.
      if (isAdmin || propName === 'whatsapp_phone') {
        const editBtn = el('button', {
          type: 'button',
          class: 'contact-wa-edit',
          title: 'Edit phone (admin)',
          'aria-label': 'Edit phone',
        }, [el('i', { 'data-lucide': 'pencil' })]);
        editBtn.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _openContactEditor(editBtn, c, def, rawValue); });
        wrap.appendChild(editBtn);
        td._contactsOpenEditor = () => editBtn.click();
      }
      td.appendChild(wrap);
      return td;
    }

    const isMulti = def.fieldType === 'checkbox' || def.fieldType === 'multiplecheckbox';
    const isEnum = (def.fieldType === 'select' || def.fieldType === 'radio' || def.type === 'enumeration')
                   && Array.isArray(def.options) && def.options.length;
    const isBool = def.type === 'bool' || def.fieldType === 'booleancheckbox';
    const isDate = def.type === 'date' || def.type === 'datetime';

    // Display
    let display = '';
    if (isEnum && rawValue) {
      if (isMulti) {
        display = String(rawValue).split(';').map(value => {
          const option = def.options.find(candidate => String(candidate.value) === value.trim());
          return option?.label || value.trim();
        }).filter(Boolean).join(', ');
      } else {
        const opt = def.options.find(o => o.value === rawValue);
        display = opt ? opt.label : rawValue;
      }
    } else if (isBool) {
      display = rawValue === 'true' || rawValue === true ? 'Yes' : rawValue === 'false' || rawValue === false ? 'No' : '—';
    } else if (isDate && rawValue) {
      try {
        const _d = new Date(rawValue);
        // Datetime properties (createdate / last-modified) show the time too —
        // the user wants "day and time" on the Created Date column. Plain `date`
        // properties (birthday, custom date pickers) stay date-only.
        const _withTime = def.type === 'datetime' || propName === 'createdate'
          || propName === 'hs_lastmodifieddate' || propName === 'lastmodifieddate';
        display = _withTime
          ? MA.loc(_d, 'dt', 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
          : MA.loc(_d, 'd', 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch { display = String(rawValue); }
    } else {
      display = rawValue || '';
    }

    // Agents cannot edit identity/ownership fields — render plain text instead
    // of a clickable pill. Empty values stay blank like every other cell.
    if (isAdminOnly && !isAdmin) {
      td.appendChild(document.createTextNode(display || ''));
      return td;
    }

    // Color-code the lead-status pill (and the few cousin fields) so paid,
    // booked, pending, and no-answer values read differently at a glance
    // instead of forming an undifferentiated wall of identical pills. The
    // semantic class drives CSS background/border in app.css.
    const semanticKind = _isLeadStatusField(propName) ? _leadStatusKind(rawValue) : '';
    // Empty values render BLANK (no "set" placeholder — Ayoub, 2026-08-15).
    // The empty pill keeps a min hit-area + hover affordance in CSS, and the
    // whole cell opens the editor, so blank never means "not editable".
    const pill = el('button', {
      type: 'button',
      class: 'contact-pill' + (display ? '' : ' contact-pill-empty') + (isMulti ? ' contact-pill-multi' : '') + (semanticKind ? ' status-' + semanticKind : ''),
      text: display || '',
      ...(display ? {} : { 'aria-label': 'Set ' + (def.label || propName), title: 'Set ' + (def.label || propName) }),
    });
    // Long-text fields open a modal with a real textarea so agents can type
    // a paragraph; everything else uses the inline editor.
    if (_isLongTextField(def, propName)) {
      pill.addEventListener('click', () => _openLongTextEditor(c, def, propName, rawValue));
    } else {
      pill.addEventListener('click', () => _openContactEditor(pill, c, def, rawValue));
    }
    td.appendChild(pill);
    // Whole-cell click opens the same editor (see the row click handler).
    td._contactsOpenEditor = () => pill.click();
    return td;
  }

  // Modal popup with a full-size textarea. Used for long-text fields like
  // notes, data_notes, data_feedback — anything where a one-liner inline input
  // wouldn't fit a paragraph.
  function _openLongTextEditor(contact, def, propName, currentValue, { onSave = null, subtitle: subtitleText = null } = {}) {
    document.getElementById('contactLongTextModal')?.remove();
    const overlay = el('div', {
      id: 'contactLongTextModal',
      style: 'position:fixed; inset:0; background:rgba(0,0,0,0.45); z-index:10000; display:flex; align-items:center; justify-content:center; padding:24px;',
    });
    const card = el('div', {
      style: 'background:#fff; border-radius:8px; box-shadow:0 8px 32px rgba(0,0,0,0.18); width:min(640px, 100%); max-height:90vh; display:flex; flex-direction:column;',
    });
    // Header
    const head = el('div', {
      style: 'padding:14px 18px; border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between;',
    });
    const title = el('h3', {
      text: 'Edit ' + (def.label || propName),
      style: 'margin:0; font-size:15px; font-weight:600;',
    });
    const closeBtn = el('button', {
      type: 'button',
      'aria-label': 'Close editor',
      title: 'Close editor',
      style: 'background:transparent; border:none; cursor:pointer; color:var(--text-secondary); padding:4px 8px;',
    }, [el('i', { 'data-lucide': 'x' })]);
    closeBtn.addEventListener('click', () => overlay.remove());
    head.appendChild(title);
    head.appendChild(closeBtn);

    // Body — large textarea
    const body = el('div', { style: 'padding:14px 18px; flex:1; overflow-y:auto;' });
    const subtitle = el('p', {
      text: subtitleText || (contact.firstname || contact.lastname || contact.phone || contact.name || '—'),
      style: 'margin:0 0 10px; color:var(--text-secondary); font-size:12px;',
    });
    body.appendChild(subtitle);
    const ta = el('textarea', {
      class: 'input',
      rows: '10',
      placeholder: 'Type here…',
      style: 'width:100%; min-height:220px; resize:vertical; font:inherit; padding:10px; box-sizing:border-box;',
    });
    ta.value = currentValue || '';
    body.appendChild(ta);

    // Footer — Cancel + Save
    const foot = el('div', {
      style: 'padding:12px 18px; border-top:1px solid var(--border); display:flex; gap:8px; justify-content:flex-end;',
    });
    const cancel = el('button', { type: 'button', class: 'btn btn-ghost', text: 'Cancel' });
    cancel.addEventListener('click', () => overlay.remove());
    const save = el('button', { type: 'button', class: 'btn btn-primary', text: 'Save' });
    save.addEventListener('click', async () => {
      save.disabled = true; cancel.disabled = true;
      // Other object workspaces (Groups) persist through their own endpoint.
      if (typeof onSave === 'function') {
        try {
          const ok = await onSave(contact, { ...def, name: propName }, ta.value);
          if (ok === false) { save.disabled = false; cancel.disabled = false; return; }
          overlay.remove();
        } catch (err) {
          toast('Save failed: ' + (err.message || 'network error'), 'error');
          save.disabled = false; cancel.disabled = false;
        }
        return;
      }
      try {
        const r = await api('/api/hubspot/contacts/' + encodeURIComponent(contact.id), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ properties: { [propName]: ta.value } }),
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          toast('Save failed: ' + (j.error || ('HTTP ' + r.status)), 'error');
          save.disabled = false; cancel.disabled = false;
          return;
        }
        contact[propName] = ta.value;
        if (contact.properties) contact.properties[propName] = ta.value;
        toast('Saved', 'success');
        overlay.remove();
        await refreshContacts();
      } catch (err) {
        toast('Save failed: ' + (err.message || 'network error'), 'error');
        save.disabled = false; cancel.disabled = false;
      }
    });
    foot.appendChild(cancel);
    foot.appendChild(save);

    card.appendChild(head);
    card.appendChild(body);
    card.appendChild(foot);
    overlay.appendChild(card);
    // Close on backdrop click (not card click)
    overlay.addEventListener('click', (ev) => { if (ev.target === overlay) overlay.remove(); });
    document.body.appendChild(overlay);
    refreshIcons(overlay);
    setTimeout(() => ta.focus(), 0);
  }

  function _closeContactInlineEditor() {
    const active = document.getElementById('contactInlineEditor');
    if (!active) return;
    if (typeof active._contactsCleanup === 'function') active._contactsCleanup();
    else active.remove();
  }

  // One positioning and dismissal contract for every Contacts cell editor.
  // The editor is rendered at document level so frozen columns and the
  // spreadsheet's overflow container never clip dropdowns or calendars.
  function _createContactEditorShell(anchorEl, {
    kind = 'menu',
    minWidth = 320,
    maxWidth = 420,
    overlayCell = false,
    ariaLabel = 'Edit contact property',
  } = {}) {
    _closeContactInlineEditor();
    const anchorCell = anchorEl?.closest?.('td') || anchorEl;
    const wrap = el('div', {
      id: 'contactInlineEditor',
      class: `contact-inline-editor is-${kind}`,
      role: kind === 'menu' ? 'dialog' : 'group',
      'aria-label': ariaLabel,
    });
    document.body.appendChild(wrap);
    anchorCell?.classList?.add('is-editing-cell');

    let closed = false;
    const place = () => {
      if (closed || !anchorCell?.getBoundingClientRect) return;
      const rect = anchorCell.getBoundingClientRect();
      const viewportGap = 10;
      const wanted = overlayCell
        ? Math.max(rect.width, minWidth)
        : Math.max(minWidth, Math.min(maxWidth, rect.width || minWidth));
      const width = Math.min(wanted, window.innerWidth - viewportGap * 2);
      wrap.style.width = `${width}px`;
      wrap.style.left = `${Math.max(viewportGap, Math.min(window.innerWidth - width - viewportGap, rect.left))}px`;
      if (overlayCell) {
        wrap.style.top = `${Math.max(viewportGap, Math.min(window.innerHeight - rect.height - viewportGap, rect.top))}px`;
        wrap.style.minHeight = `${rect.height}px`;
        return;
      }
      const height = Math.min(wrap.scrollHeight || 0, window.innerHeight - viewportGap * 2);
      const below = rect.bottom + 5;
      const above = rect.top - height - 5;
      wrap.style.top = `${below + height <= window.innerHeight - viewportGap ? below : Math.max(viewportGap, above)}px`;
    };
    const onOutside = (ev) => {
      if (!wrap.contains(ev.target) && ev.target !== anchorEl) {
        if (typeof wrap._beforeDismiss === 'function' && wrap._beforeDismiss(ev) === false) return;
        close();
      }
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') close();
    };
    const close = () => {
      if (closed) return;
      closed = true;
      document.removeEventListener('pointerdown', onOutside, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      anchorCell?.classList?.remove('is-editing-cell');
      wrap.remove();
    };
    wrap._contactsCleanup = close;
    wrap._contactsPlace = place;
    setTimeout(() => document.addEventListener('pointerdown', onOutside, true), 0);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return wrap;
  }

  function _contactEditorSearch(placeholder = 'Search') {
    const input = el('input', {
      type: 'search',
      class: 'contact-editor-search-input',
      placeholder,
      autocomplete: 'off',
      'aria-label': placeholder,
    });
    const shell = el('label', { class: 'contact-editor-search' }, [
      el('i', { 'data-lucide': 'search', 'aria-hidden': 'true' }),
      input,
    ]);
    return { shell, input };
  }

  // Combined firstname+lastname editor — popover anchored to the Name cell.
  // Saves both fields in one HubSpot PATCH so the agent fixes a typo in one
  // click without opening the column-picker to add firstname/lastname.
  // Full name → HubSpot firstname/lastname. Same convention as the Add-contact
  // form: first token = firstname, the rest = lastname
  // ("Sara El Amrani" → first="Sara", last="El Amrani").
  function _splitContactFullName(fullName) {
    const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
    const firstname = parts.shift() || '';
    return { firstname, lastname: parts.join(' ') };
  }

  function _openNameEditor(anchorEl, contact) {
    const wrap = _createContactEditorShell(anchorEl, {
      kind: 'form', minWidth: 340, maxWidth: 420, ariaLabel: 'Edit contact name',
    });
    wrap.appendChild(el('div', { class: 'contact-editor-title', text: 'Edit contact name' }));

    // ONE full-name field (Ayoub, 2026-08-15): the agent types the name the way
    // it is said; we split it into firstname / lastname on save.
    const currentFirst = String(contact.firstname || '').trim();
    const currentLast = String(contact.lastname || '').trim();
    const full = el('input', {
      class: 'contact-editor-input',
      type: 'text',
      placeholder: 'Full name',
      'aria-label': 'Full name',
      autocomplete: 'off',
    });
    full.value = (currentFirst + ' ' + currentLast).trim();
    wrap.appendChild(full);

    const row = el('div', { class: 'contact-editor-footer' });
    const cancel = el('button', { type: 'button', class: 'contact-editor-btn is-secondary', text: 'Cancel' });
    cancel.addEventListener('click', () => _closeContactInlineEditor());
    const save = el('button', { type: 'button', class: 'contact-editor-btn is-primary', text: 'Save' });
    save.addEventListener('click', async () => {
      const typed = full.value.trim();
      // Unchanged text → nothing to write (no accidental HubSpot PATCH).
      if (typed === (currentFirst + ' ' + currentLast).trim()) { _closeContactInlineEditor(); return; }
      const split = _splitContactFullName(typed);
      const properties = {};
      if (split.firstname !== currentFirst) properties.firstname = split.firstname;
      if (split.lastname !== currentLast) properties.lastname = split.lastname;
      if (!Object.keys(properties).length) { _closeContactInlineEditor(); return; }
      save.disabled = true; cancel.disabled = true;
      try {
        const r = await api('/api/hubspot/contacts/' + encodeURIComponent(contact.id), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ properties }),
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          toast('Save failed: ' + (j.error || ('HTTP ' + r.status)), 'error');
          save.disabled = false; cancel.disabled = false;
          return;
        }
        Object.assign(contact, properties);
        toast('Name updated', 'success');
        _closeContactInlineEditor();
        await refreshContacts();
      } catch (err) {
        toast('Save failed: ' + (err.message || 'network error'), 'error');
        save.disabled = false; cancel.disabled = false;
      }
    });
    row.appendChild(cancel);
    row.appendChild(save);
    wrap.appendChild(row);

    refreshIcons(wrap);
    wrap._contactsPlace?.();
    setTimeout(() => { full.focus(); full.select(); }, 0);
    // Enter saves, Escape closes
    full.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') { ev.preventDefault(); save.click(); }
      else if (ev.key === 'Escape') { ev.preventDefault(); _closeContactInlineEditor(); }
    });
  }

  // OWNER cell — admins see a clickable pill that opens an owner picker.
  // Agents see plain text. The server PATCH endpoint also rejects
  // hubspot_owner_id writes from non-admin (defense in depth).
  function _buildOwnerCell(c) {
    const td = el('td');
    const ownerId = c.hubspot_owner_id || '';
    const ownerName = ownerId ? (CONTACTS.ownersMap[ownerId] || ('id:' + ownerId)) : '—';
    const role = (state.me?.role || '').toLowerCase();
    const isAdmin = _isSalesAdmin();
    if (!isAdmin) {
      td.appendChild(document.createTextNode(ownerName));
      return td;
    }
    const pill = el('button', {
      type: 'button',
      class: 'contact-pill' + (ownerId ? '' : ' contact-pill-empty'),
      text: ownerName,
      title: 'Reassign owner (admin)',
    });
    pill.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      _openOwnerPicker(pill, c);
    });
    td.appendChild(pill);
    td._contactsOpenEditor = () => pill.click();
    return td;
  }

  function _openOwnerPicker(anchorEl, contact) {
    const wrap = _createContactEditorShell(anchorEl, {
      kind: 'menu', minWidth: 320, maxWidth: 390, ariaLabel: 'Choose contact owner',
    });

    const mappedOwnerIds = new Set((state.assignableUsers || [])
      .map(u => String(u.hubspotOwnerId || ''))
      .filter(Boolean));
    const owners = Object.entries(CONTACTS.ownersMap || {})
      .filter(([id]) => mappedOwnerIds.has(String(id)))
      .sort((a, b) => a[1].localeCompare(b[1]));

    const { shell, input } = _contactEditorSearch('Search owners');
    const list = el('div', { class: 'contact-editor-options', role: 'listbox' });
    wrap.appendChild(shell);
    wrap.appendChild(list);

    const render = (term = '') => {
      clear(list);
      const query = term.trim().toLowerCase();
      const rows = [['', '— (unassigned)'], ...owners]
        .filter(([, name]) => !query || String(name).toLowerCase().includes(query));
      if (!rows.length) {
        list.appendChild(el('div', { class: 'contact-editor-empty', text: 'No owners found' }));
        return;
      }
      for (const [id, name] of rows) {
        const selected = String(id) === String(contact.hubspot_owner_id || '');
        const opt = el('button', {
          type: 'button',
          class: 'contact-editor-option' + (selected ? ' is-selected' : ''),
          role: 'option',
          'aria-selected': selected ? 'true' : 'false',
        }, [
          el('span', { class: 'contact-editor-option-label', text: name }),
          ...(selected ? [el('i', { 'data-lucide': 'check', 'aria-hidden': 'true' })] : []),
        ]);
        opt.addEventListener('click', () => _commitOwnerChange(contact, id));
        list.appendChild(opt);
      }
      refreshIcons(list);
    };
    input.addEventListener('input', () => render(input.value));
    render();
    refreshIcons(wrap);
    wrap._contactsPlace?.();
    setTimeout(() => input.focus(), 0);
  }

  async function _commitOwnerChange(contact, newOwnerId) {
    _closeContactInlineEditor();
    if ((newOwnerId || '') === (contact.hubspot_owner_id || '')) return; // no-op
    try {
      const r = await api('/api/hubspot/contacts/' + encodeURIComponent(contact.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ properties: { hubspot_owner_id: newOwnerId || '' } }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast('Reassign failed: ' + (j.error || ('HTTP ' + r.status)), 'error');
        return;
      }
      // Patch local row + re-render. We don't refresh the whole list to keep
      // the user's scroll/selection — the row will pick up the new owner on
      // the next refreshContacts() anyway.
      contact.hubspot_owner_id = newOwnerId || '';
      const newName = newOwnerId ? (CONTACTS.ownersMap[newOwnerId] || ('id:' + newOwnerId)) : '—';
      toast('Reassigned to ' + newName, 'success');
      await refreshContacts();
    } catch (err) {
      console.error('[contacts] owner reassign threw:', err);
      toast('Reassign failed: ' + (err.message || 'network error'), 'error');
    }
  }

  // Classify a status string into a semantic category so the team can
  // distinguish at a glance — green for closed-positive, amber for
  // pending/in-progress, red for lost/negative, grey for neutral. Keyword
  // matching so brand-new statuses added in HubSpot get a reasonable color
  // without code changes. Returns '' for unknown → falls back to the
  // default neutral pill style.
  function _leadStatusKind(raw) {
    const s = String(raw || '').toLowerCase();
    if (!s) return '';
    // Order matters — "not interested" must hit negative BEFORE the
    // generic "interested" → positive heuristic below.
    if (/(not.interested|hung.?up|incorrect|wrong|expired|cancelled|canceled|refund|lost|problem|distrust|spam|bad|fake|junk|unsub)/i.test(s)) return 'neg';
    if (/(no.answer|not.reached|unreachable|silent|dead.lead|cold)/i.test(s)) return 'neg';
    if (/(paid|customer|renewed|enrolled|classroom|graduated|completed|closed.won|success|signed)/i.test(s)) return 'pos';
    if (/(booked|scheduled|trial|test.done|interested|warm|qualified|demo.done|won)/i.test(s)) return 'pos';
    if (/(pending|waiting|in.progress|call.later|decide.later|follow.up|callback|to.call|hot|new|^test$|maybe|considering)/i.test(s)) return 'pending';
    return '';
  }

  // Format status labels into category buckets so the dropdown reads like
  // a triage list instead of one long flat column of identical-looking
  // pills. Returns { positive: [...], pending: [...], negative: [...], other: [...] }
  // preserving the original label order within each bucket.
  function _groupStatusOptions(options) {
    const buckets = { pos: [], pending: [], neg: [], other: [] };
    for (const o of options) {
      const k = _leadStatusKind(o.value || o.label) || 'other';
      buckets[k].push(o);
    }
    return buckets;
  }

  // True for the lead-status property + a couple of close cousins. Used to
  // gate semantic coloring + dropdown grouping so we don't accidentally
  // apply it to unrelated select fields (e.g. "lead source").
  function _isLeadStatusField(propName) {
    return /^(hs_lead_status|lead_status|our_status|pipeline_status)$/.test(String(propName || ''));
  }

  function _contactDateValue(raw) {
    if (!raw) return '';
    const direct = String(raw).match(/^(\d{4}-\d{2}-\d{2})/);
    if (direct) return direct[1];
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return '';
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function _renderContactCalendar(wrap, currentValue, onPick) {
    const selectedValue = _contactDateValue(currentValue);
    const selected = selectedValue ? new Date(`${selectedValue}T12:00:00`) : null;
    let cursor = new Date(selected || Date.now());
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const calendar = el('div', { class: 'contact-calendar' });
    wrap.appendChild(calendar);

    const render = () => {
      clear(calendar);
      const titleText = cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      const previous = el('button', { type: 'button', class: 'contact-calendar-nav', 'aria-label': 'Previous month' }, [el('i', { 'data-lucide': 'chevron-left' })]);
      const next = el('button', { type: 'button', class: 'contact-calendar-nav', 'aria-label': 'Next month' }, [el('i', { 'data-lucide': 'chevron-right' })]);
      previous.addEventListener('click', () => { cursor = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1); render(); });
      next.addEventListener('click', () => { cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1); render(); });
      calendar.appendChild(el('div', { class: 'contact-calendar-head' }, [
        previous,
        el('strong', { text: titleText.charAt(0).toUpperCase() + titleText.slice(1) }),
        next,
      ]));

      const grid = el('div', { class: 'contact-calendar-grid' });
      for (const label of ['lu', 'ma', 'me', 'je', 've', 'sa', 'di']) {
        grid.appendChild(el('span', { class: 'contact-calendar-weekday', text: label }));
      }
      const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const leading = (monthStart.getDay() + 6) % 7;
      const gridStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1 - leading);
      const todayValue = _contactDateValue(new Date());
      for (let index = 0; index < 42; index += 1) {
        const day = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index);
        const value = _contactDateValue(day);
        const outside = day.getMonth() !== cursor.getMonth();
        const isSelected = value === selectedValue;
        const isToday = value === todayValue;
        const button = el('button', {
          type: 'button',
          class: 'contact-calendar-day'
            + (outside ? ' is-outside' : '')
            + (isSelected ? ' is-selected' : '')
            + (isToday ? ' is-today' : ''),
          text: String(day.getDate()),
          'aria-label': day.toLocaleDateString('fr-FR', { dateStyle: 'full' }),
        });
        button.addEventListener('click', () => onPick(value));
        grid.appendChild(button);
      }
      calendar.appendChild(grid);

      const today = el('button', { type: 'button', class: 'contact-calendar-footer-btn', text: 'Today' });
      const clearDate = el('button', { type: 'button', class: 'contact-calendar-footer-btn', text: 'Clear' });
      today.addEventListener('click', () => onPick(_contactDateValue(new Date())));
      clearDate.addEventListener('click', () => onPick(''));
      calendar.appendChild(el('div', { class: 'contact-calendar-footer' }, [today, clearDate]));
      refreshIcons(calendar);
      wrap._contactsPlace?.();
    };
    render();
  }

  function _contactEditorOption({ label, value, selected = false, kind = '', icon = '', onPick }) {
    const children = [];
    if (kind) children.push(el('span', { class: `contact-editor-option-dot is-${kind}`, 'aria-hidden': 'true' }));
    if (icon) children.push(el('i', { 'data-lucide': icon, 'aria-hidden': 'true' }));
    children.push(el('span', { class: 'contact-editor-option-label', text: label }));
    if (selected) children.push(el('i', { 'data-lucide': 'check', class: 'contact-editor-option-check', 'aria-hidden': 'true' }));
    const option = el('button', {
      type: 'button',
      class: 'contact-editor-option' + (selected ? ' is-selected' : ''),
      role: 'option',
      'aria-selected': selected ? 'true' : 'false',
      'data-value': String(value ?? ''),
    }, children);
    option.addEventListener('click', (ev) => { ev.stopPropagation(); onPick(value); });
    return option;
  }

  function _openContactEditor(anchorEl, contact, def, currentValue, { onSave = null } = {}) {
    // `onSave(record, def, value)` lets other object workspaces (Groups) reuse
    // every editor kind while persisting through their own endpoint.
    const _saveContactEdit = onSave || _saveContactEditDefault;
    const options = Array.isArray(def.options) ? def.options : [];
    const isMulti = (def.fieldType === 'checkbox' || def.fieldType === 'multiplecheckbox') && options.length;
    const isEnum = !isMulti
      && (def.fieldType === 'select' || def.fieldType === 'radio' || def.type === 'enumeration')
      && options.length;
    const isBool = def.type === 'bool' || def.fieldType === 'booleancheckbox';
    const isDate = def.type === 'date' || def.type === 'datetime';
    const isNumber = def.type === 'number';

    if (isDate) {
      const wrap = _createContactEditorShell(anchorEl, {
        kind: 'calendar', minWidth: 320, maxWidth: 340, ariaLabel: `Choose ${def.label || def.name}`,
      });
      _renderContactCalendar(wrap, currentValue, (value) => _saveContactEdit(contact, def, value));
      return;
    }

    if (isMulti) {
      const wrap = _createContactEditorShell(anchorEl, {
        kind: 'multiselect', minWidth: 350, maxWidth: 430, ariaLabel: `Choose ${def.label || def.name}`,
      });
      const chosen = new Set(String(currentValue || '').split(';').map(value => value.trim()).filter(Boolean));
      const { shell, input } = _contactEditorSearch(`Search ${String(def.label || 'options').toLowerCase()}`);
      const list = el('div', { class: 'contact-editor-options is-checkbox-list', role: 'listbox', 'aria-multiselectable': 'true' });
      const render = () => {
        clear(list);
        const query = input.value.trim().toLowerCase();
        const matches = options.filter(option => !query || String(option.label || option.value).toLowerCase().includes(query));
        if (!matches.length) list.appendChild(el('div', { class: 'contact-editor-empty', text: 'No options found' }));
        // Some HubSpot checkbox properties (notably Groups) have 1,000+
        // options. Keep the selected values visible and cap the rendered DOM;
        // search still reaches every option without creating a thousand rows.
        const selectedMatches = matches.filter(option => chosen.has(String(option.value)));
        const unselectedMatches = matches.filter(option => !chosen.has(String(option.value)));
        const visibleMatches = [...selectedMatches, ...unselectedMatches].slice(0, 100);
        for (const option of visibleMatches) {
          const checked = chosen.has(String(option.value));
          const checkbox = el('input', { type: 'checkbox', ...(checked ? { checked: 'checked' } : {}), tabindex: '-1' });
          const row = el('button', {
            type: 'button',
            class: 'contact-editor-check-option' + (checked ? ' is-selected' : ''),
            role: 'option',
            'aria-selected': checked ? 'true' : 'false',
          }, [checkbox, el('span', { text: option.label || option.value })]);
          row.addEventListener('click', () => {
            if (chosen.has(String(option.value))) chosen.delete(String(option.value));
            else chosen.add(String(option.value));
            render();
          });
          list.appendChild(row);
        }
        if (matches.length > visibleMatches.length) {
          list.appendChild(el('div', {
            class: 'contact-editor-results-note',
            text: `Showing ${visibleMatches.length} of ${matches.length} — type to search`,
          }));
        }
        wrap._contactsPlace?.();
      };
      input.addEventListener('input', render);
      const clearBtn = el('button', { type: 'button', class: 'contact-editor-btn is-link', text: 'Clear' });
      clearBtn.addEventListener('click', () => { chosen.clear(); render(); });
      const cancel = el('button', { type: 'button', class: 'contact-editor-btn is-secondary', text: 'Cancel' });
      cancel.addEventListener('click', _closeContactInlineEditor);
      const save = el('button', { type: 'button', class: 'contact-editor-btn is-primary', text: 'Save' });
      save.addEventListener('click', () => _saveContactEdit(contact, def, [...chosen].join(';')));
      wrap.appendChild(shell);
      wrap.appendChild(list);
      wrap.appendChild(el('div', { class: 'contact-editor-footer' }, [clearBtn, el('span', { class: 'contact-editor-footer-spacer' }), cancel, save]));
      render();
      refreshIcons(wrap);
      wrap._contactsPlace?.();
      setTimeout(() => input.focus(), 0);
      return;
    }

    if (isEnum || isBool) {
      const wrap = _createContactEditorShell(anchorEl, {
        kind: 'menu', minWidth: 320, maxWidth: 400, ariaLabel: `Choose ${def.label || def.name}`,
      });
      const { shell, input } = _contactEditorSearch(`Search ${String(def.label || 'options').toLowerCase()}`);
      const list = el('div', { class: 'contact-editor-options', role: 'listbox' });
      const enumOptions = isBool
        ? [{ label: 'Yes', value: 'true', kind: 'pos', icon: 'check' }, { label: 'No', value: 'false', kind: 'neg', icon: 'x' }]
        : options;
      const renderOption = (option, kind = '') => list.appendChild(_contactEditorOption({
        label: option.label || option.value,
        value: option.value,
        selected: String(option.value) === String(currentValue ?? ''),
        kind,
        icon: option.icon || '',
        onPick: (value) => _saveContactEdit(contact, def, value),
      }));
      const render = () => {
        clear(list);
        const query = input.value.trim().toLowerCase();
        if (!query || '(clear)'.includes(query)) {
          renderOption({ label: '(clear)', value: '' });
        }
        const filtered = enumOptions.filter(option => !query || String(option.label || option.value).toLowerCase().includes(query));
        if (_isLeadStatusField(def.name) && !query && !isBool) {
          const groups = _groupStatusOptions(filtered);
          const appendGroup = (title, items, kind) => {
            if (!items.length) return;
            list.appendChild(el('div', { class: `contact-editor-group is-${kind}`, text: title }));
            for (const option of items) renderOption(option, kind);
          };
          appendGroup('Positive', groups.pos, 'pos');
          appendGroup('Pending', groups.pending, 'pending');
          appendGroup('Negative', groups.neg, 'neg');
          appendGroup('Other', groups.other, 'other');
        } else {
          for (const option of filtered) renderOption(option, option.kind || '');
        }
        if (!list.querySelector('.contact-editor-option')) {
          list.appendChild(el('div', { class: 'contact-editor-empty', text: 'No options found' }));
        }
        refreshIcons(list);
        wrap._contactsPlace?.();
      };
      input.addEventListener('input', render);
      wrap.appendChild(shell);
      wrap.appendChild(list);
      if (_isLeadStatusField(def.name) && _userIsAdmin && _userIsAdmin()) {
        const addBtn = el('button', { type: 'button', class: 'contact-editor-add-option' }, [
          el('i', { 'data-lucide': 'plus' }), el('span', { text: 'Add new status…' }),
        ]);
        addBtn.addEventListener('click', () => { _closeContactInlineEditor(); _addNewLeadStatus(contact, def); });
        wrap.appendChild(addBtn);
      }
      render();
      refreshIcons(wrap);
      wrap._contactsPlace?.();
      setTimeout(() => input.focus(), 0);
      return;
    }

    // Text and numeric fields use a true spreadsheet editor over the whole
    // cell: no green card or second Save click. Enter or leaving the cell saves;
    // Escape cancels, matching HubSpot's table editing behaviour.
    const wrap = _createContactEditorShell(anchorEl, {
      kind: 'cell-input', minWidth: 180, maxWidth: 640, overlayCell: true,
      ariaLabel: `Edit ${def.label || def.name}`,
    });
    const input = el('input', {
      type: isNumber ? 'number' : 'text',
      class: 'contact-cell-editor-input',
      value: currentValue || '',
      'aria-label': def.label || def.name,
    });
    wrap.appendChild(input);
    wrap._contactsPlace?.();
    let saving = false;
    let cancelled = false;
    const commit = () => {
      if (saving || cancelled) return;
      saving = true;
      if (String(input.value) === String(currentValue || '')) {
        _closeContactInlineEditor();
        return;
      }
      _saveContactEdit(contact, def, input.value);
    };
    wrap._beforeDismiss = () => { commit(); return false; };
    input.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') { ev.preventDefault(); commit(); }
      if (ev.key === 'Escape') { cancelled = true; ev.preventDefault(); _closeContactInlineEditor(); }
    });
    input.addEventListener('blur', () => {
      if (!cancelled && wrap.isConnected) commit();
    });
    setTimeout(() => { input.focus(); input.select(); }, 0);
  }

  // Admin-only: coin a new lead-status option (e.g. "Hot Lead", "VIP") + apply
  // it to the current contact in one flow. Prompts for the label, POSTs to the
  // HubSpot Properties API via our wrapper, refreshes the locally-cached
  // option list, then chains into _saveContactEdit so the contact gets the
  // new status without a second click.
  async function _addNewLeadStatus(contact, def) {
    const label = prompt('Nom du nouveau statut (ex: "Hot Lead", "VIP", "Cold"):');
    if (!label) return;
    const trimmed = label.trim();
    if (!trimmed) return;
    if (trimmed.length > 60) {
      toast('Nom trop long (max 60 caractères)', 'error');
      return;
    }
    try {
      const r = await api('/api/hubspot/properties/contacts/hs_lead_status/options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: trimmed }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok) {
        toast('Échec: ' + (j?.error || ('HTTP ' + r.status)), 'error');
        return;
      }
      if (j.already_exists) {
        toast('Ce statut existe déjà — application au contact', 'info');
      } else {
        toast(`Statut "${trimmed}" créé dans HubSpot`, 'success');
      }
      // Refresh the cached distinct-statuses + the property's own options so
      // the dropdown will include it next time. Force a reload of the contacts
      // property schema so def.options picks up the new entry.
      try {
        await fetchT('/api/hubspot/contacts/distinct-lead-statuses', {
          headers: { 'x-auth-token': localStorage.getItem('lougha_chats_token') },
        }).then(rr => rr.json()).then(dd => { CONTACTS.distinctStatuses = dd.values || []; });
      } catch { /* non-fatal — the next refresh will sync */ }
      // Push the new option into the in-memory def so the next dropdown open
      // shows it without a full props reload.
      if (Array.isArray(def.options) && !def.options.find(o => o.value === trimmed)) {
        def.options.push({ label: trimmed, value: trimmed });
      }
      // Apply to the current contact.
      _saveContactEdit(contact, def, trimmed);
    } catch (err) {
      toast('Erreur réseau: ' + (err.message || err), 'error');
    }
  }

  function _saveContactEditDefault(contact, def, value) { return _saveContactEdit(contact, def, value); }
  async function _saveContactEdit(contact, def, value) {
    _closeContactInlineEditor();
    try {
      const r = await api('/api/hubspot/contacts/' + encodeURIComponent(contact.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ properties: { [def.name]: value } }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast(`Update ${def.label} failed: ${j.error || r.status}`, 'error');
        return;
      }
      toast(`${def.label} updated`, 'success');
      // Update local state + re-render
      contact.properties = { ...(contact.properties || {}), [def.name]: value };
      // Special-case the mirror's top-level columns so the UI reads back the new value
      if (def.name === 'hs_lead_status') {
        contact.hs_lead_status = value;
        contact.our_pipeline_stage = value && CONTACTS.leadStatusMap ? (CONTACTS.leadStatusMap[value] || null) : null;
      }
      if (def.name === 'lifecyclestage') contact.lifecyclestage = value;
      if (def.name === 'phone') contact.phone = value;
      // Keep the computed "N° WhatsApp" column in sync until the next fetch.
      if (def.name === 'whatsapp_phone') contact.whatsapp_number = value;
      if (def.name === 'email') contact.email = value;
      if (def.name === 'firstname') contact.firstname = value;
      if (def.name === 'lastname') contact.lastname = value;
      _renderContactsTable(CONTACTS.rows);
    } catch (err) {
      toast('Update failed: ' + (err.message || err), 'error');
    }
  }

  // ---- Polished column editor (Phase E3) ----
  // Two-pane: LEFT = selected (ordered, draggable, removable) / RIGHT = catalog
  // (grouped, searchable, click to add). Frozen-count selector controls how
  // many of the leading columns stick to the left on horizontal scroll.
  // The Edit-columns modal (#contactsColumnsModal) is shared by every object
  // workspace; `_colsWs` is the one that opened it (state carries the catalog +
  // the in-progress _editColumns / _editFrozen / _modalPicked).
  let _colsWs = _CONTACTS_WS;
  async function openContactsColumnsModal(ws = _CONTACTS_WS) {
    _colsWs = ws;
    const S = ws.state;
    if (!S.propsCatalog) await ws.loadCatalog();
    // The active view is the source of truth. Seed local edit state from it.
    S._editColumns = S.activeView ? [...S.activeView.columns] : [...(S.columns || [])];
    S._editFrozen = S.activeView ? (S.activeView.frozenCount || 0) : 0;
    document.getElementById('colsActiveViewName').textContent = S.activeView ? `· in "${S.activeView.name}"` : '';
    document.getElementById('colsFrozenCount').value = String(S._editFrozen);
    document.getElementById('colsFrozenCount').onchange = (ev) => {
      S._editFrozen = Math.max(0, Math.min(parseInt(ev.target.value, 10) || 0, S._editColumns.length));
      _renderColsEditor();
    };
    document.getElementById('colsSearch').oninput = (ev) => _renderColsCatalog(ev.target.value);
    document.getElementById('colsSaveBtn').onclick = () => _saveColumnsForUser(state.me?.id);
    _renderColsEditor();
    _renderColsCatalog('');
    openModal('contactsColumnsModal');
    refreshIcons();
  }

  function _renderColsEditor() {
    const list = document.getElementById('colsSelectedList');
    if (!list) return;
    clear(list);
    document.getElementById('colsSelectedCount').textContent = _colsWs.state._editColumns.length;
    if (!_colsWs.state._editColumns.length) {
      list.appendChild(el('div', { class: 'col-edit-empty', text: 'No columns yet — add some from the right pane.' }));
      // Sync the "_modalPicked" used by _saveColumnsForUser
      _colsWs.state._modalPicked = new Set();
      return;
    }
    _colsWs.state._editColumns.forEach((propName, idx) => {
      const def = _colsWs.state.propsByName[propName] || { name: propName, label: propName, type: 'string' };
      const isFrozen = idx < _colsWs.state._editFrozen;
      const handle = el('span', { class: 'col-edit-handle', title: 'Drag to reorder' }, [
        el('i', { 'data-lucide': 'grip-vertical' }),
      ]);
      const row = el('div', {
        class: 'col-edit-row' + (isFrozen ? ' frozen-marker' : ''),
        'data-prop': propName,
        draggable: 'true',
      }, [
        handle,
        el('span', { class: 'col-edit-label', text: def.label || propName }),
        el('span', { class: 'col-edit-type', text: def.type + (def.fieldType ? '/' + def.fieldType : '') }),
        el('span', { class: 'col-edit-row-actions' }, [
          _makeColEditBtn('arrow-up', 'Move up', () => _editColMove(idx, idx - 1)),
          _makeColEditBtn('arrow-down', 'Move down', () => _editColMove(idx, idx + 1)),
          _makeColEditBtn('x', 'Remove', () => _editColRemove(propName), 'remove'),
        ]),
      ]);
      _wireDragRow(row, propName);
      list.appendChild(row);
    });
    refreshIcons(list);
    // Sync the _modalPicked set used by _saveColumnsForUser
    _colsWs.state._modalPicked = new Set(_colsWs.state._editColumns);
  }

  function _makeColEditBtn(icon, title, fn, cls) {
    const b = el('button', { type: 'button', title, 'aria-label': title, ...(cls ? { class: cls } : {}) }, [
      el('i', { 'data-lucide': icon }),
    ]);
    b.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); fn(); });
    return b;
  }

  function _editColMove(from, to) {
    if (to < 0 || to >= _colsWs.state._editColumns.length) return;
    const arr = _colsWs.state._editColumns;
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    _renderColsEditor();
  }

  function _editColRemove(propName) {
    _colsWs.state._editColumns = _colsWs.state._editColumns.filter(c => c !== propName);
    if (_colsWs.state._editFrozen > _colsWs.state._editColumns.length) {
      _colsWs.state._editFrozen = _colsWs.state._editColumns.length;
      document.getElementById('colsFrozenCount').value = String(_colsWs.state._editFrozen);
    }
    _renderColsEditor();
    _renderColsCatalog(document.getElementById('colsSearch').value || '');
  }

  function _editColAdd(propName) {
    if (_colsWs.state._editColumns.includes(propName)) return;
    _colsWs.state._editColumns.push(propName);
    _renderColsEditor();
    _renderColsCatalog(document.getElementById('colsSearch').value || '');
  }

  function _wireDragRow(rowEl, propName) {
    rowEl.addEventListener('dragstart', (ev) => {
      ev.dataTransfer.setData('text/plain', propName);
      rowEl.classList.add('dragging');
    });
    rowEl.addEventListener('dragend', () => {
      rowEl.classList.remove('dragging');
      document.querySelectorAll('.col-edit-row').forEach(r => {
        r.classList.remove('drop-target-above');
        r.classList.remove('drop-target-below');
      });
    });
    rowEl.addEventListener('dragover', (ev) => {
      ev.preventDefault();
      const rect = rowEl.getBoundingClientRect();
      const above = ev.clientY < rect.top + rect.height / 2;
      rowEl.classList.toggle('drop-target-above', above);
      rowEl.classList.toggle('drop-target-below', !above);
    });
    rowEl.addEventListener('dragleave', () => {
      rowEl.classList.remove('drop-target-above');
      rowEl.classList.remove('drop-target-below');
    });
    rowEl.addEventListener('drop', (ev) => {
      ev.preventDefault();
      const fromName = ev.dataTransfer.getData('text/plain');
      if (!fromName || fromName === propName) return;
      const arr = _colsWs.state._editColumns;
      const fromIdx = arr.indexOf(fromName);
      const toIdx = arr.indexOf(propName);
      if (fromIdx < 0 || toIdx < 0) return;
      const above = rowEl.classList.contains('drop-target-above');
      arr.splice(fromIdx, 1);
      const insertAt = above ? Math.max(0, toIdx + (fromIdx < toIdx ? -1 : 0))
                             : Math.min(arr.length, toIdx + (fromIdx < toIdx ? 0 : 1));
      arr.splice(insertAt, 0, fromName);
      _renderColsEditor();
    });
  }

  function _renderColsCatalog(searchTerm) {
    const list = document.getElementById('colsAvailableList');
    if (!list) return;
    clear(list);
    const term = String(searchTerm || '').toLowerCase().trim();
    const selected = new Set(_colsWs.state._editColumns);
    const props = (_colsWs.state.propsCatalog || []).filter(p => {
      if (selected.has(p.name)) return false;
      // Lead Status / stage fields are hidden from the team (managed only via the
      // conversation pipeline bar) — keep them out of the addable catalog too so
      // the team can't re-add them. System-admin still sees them.
      if (typeof _colsWs.hideCatalogProp === 'function' && _colsWs.hideCatalogProp(p)) return false;
      if (!term) return true;
      return p.name.toLowerCase().includes(term) || (p.label || '').toLowerCase().includes(term);
    });
    if (!props.length) {
      list.appendChild(el('div', { class: 'col-edit-empty', text: term ? 'No matches.' : 'All HubSpot properties already in the view.' }));
      return;
    }
    // Group by groupName, sorted
    const groups = {};
    for (const p of props) {
      const g = p.groupName || 'misc';
      (groups[g] = groups[g] || []).push(p);
    }
    for (const [groupName, items] of Object.entries(groups).sort()) {
      list.appendChild(el('div', { class: 'col-edit-group-label', text: groupName }));
      for (const p of items) {
        const row = el('div', { class: 'col-edit-row', style: 'cursor:pointer;' }, [
          el('span', { class: 'col-edit-add-icon' }, [el('i', { 'data-lucide': 'plus' })]),
          el('span', { class: 'col-edit-label', text: p.label || p.name }),
          el('span', { class: 'col-edit-type', text: p.type + (p.fieldType ? '/' + p.fieldType : '') }),
        ]);
        row.addEventListener('click', () => _editColAdd(p.name));
        list.appendChild(row);
      }
    }
    refreshIcons(list);
  }

  async function _saveColumnsForUser(userId) {
    const cols = Array.from(_colsWs.state._modalPicked || new Set());
    if (!cols.length) { toast('Pick at least one column', 'warning'); return; }
    // Phase E2: column changes now persist to the active view (or apply locally
    // if there's no active view yet). The modal's per-user picker is deprecated
    // in favor of the per-view sharedWith model — admins use a view's "team-shared"
    // visibility to scope it to specific agents.
    if (_colsWs.state.activeView) {
      try {
        const r = await api(_colsWs.viewsApi + '/' + _colsWs.state.activeView.id, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ columns: cols, frozenCount: _colsWs.state._editFrozen ?? _colsWs.state.activeView.frozenCount ?? 0 }),
        });
        if (!r.ok) { const j = await r.json().catch(() => ({})); toast('Save failed: ' + (j.error || r.status), 'error'); return; }
        const d = await r.json();
        _colsWs.state.activeView = d.view;
        _colsWs.state.columns = cols;
        Object.assign(_colsWs.state.views.find(v => v.id === d.view.id) || {}, d.view);
        _colsWs.state._baselineSig = _colsWs.signView();
        _colsWs.state.dirty = false;
        _colsWs.updateDirtyPill();
        toast(`Saved ${cols.length} columns to "${d.view.name}"`, 'success');
      } catch (err) { toast('Save failed: ' + (err.message || err), 'error'); return; }
    } else {
      // Fallback: no active view → just apply locally
      _colsWs.state.columns = cols;
      toast(`Applied ${cols.length} columns (no view to save into)`, 'success');
    }
    closeModal('contactsColumnsModal');
    await _colsWs.refresh();
  }
  // ============ /Contacts view ============

  // ============ Lead status mapping admin (Phase C) ============
  // Admin-only Workspace sub-tab. Lets the user define how each HubSpot
  // `hs_lead_status` value translates to one of our 10 pipeline stages.
  // Used by the Contacts view to render "Our stage" pills + translate filters.
  const LEAD_MAP_STAGES = ['ignore','new','no_answer','contacted','book_test','no_show','test_done','proforma_sent','paid','not_interested','lost'];
  const LEAD_MAP = { distinct: [], map: {}, dirty: false };

  async function initLeadStatusMap() {
    const loading = document.getElementById('leadMapLoading');
    const table = document.getElementById('leadMapTable');
    const empty = document.getElementById('leadMapEmpty');
    loading?.classList.remove('hidden');
    table?.classList.add('hidden');
    empty?.classList.add('hidden');
    try {
      const [distResp, mapResp] = await Promise.all([
        api('/api/hubspot/contacts/distinct-lead-statuses'),
        api('/api/admin/lead-status-map'),
      ]);
      if (!distResp.ok) throw new Error('distinct fetch failed');
      if (!mapResp.ok) throw new Error('map fetch failed');
      LEAD_MAP.distinct = (await distResp.json()).values || [];
      LEAD_MAP.map = (await mapResp.json()).map || {};
      LEAD_MAP.dirty = false;
      _renderLeadMap();
      loading?.classList.add('hidden');
      if (!LEAD_MAP.distinct.length) {
        empty?.classList.remove('hidden');
      } else {
        table?.classList.remove('hidden');
      }
      // Wire save (idempotent if already wired)
      const saveBtn = document.getElementById('leadMapSaveBtn');
      if (saveBtn && !saveBtn._wired) {
        saveBtn._wired = true;
        saveBtn.addEventListener('click', _saveLeadMap);
      }
    } catch (err) {
      if (loading) loading.textContent = 'Failed to load: ' + (err.message || err);
    }
  }

  function _renderLeadMap() {
    const tbody = document.getElementById('leadMapTbody');
    if (!tbody) return;
    clear(tbody);
    for (const row of LEAD_MAP.distinct) {
      const current = LEAD_MAP.map[row.value] || '';
      const select = el('select', { class: 'input', style: 'font-size:13px; padding:4px 8px;' });
      select.appendChild(el('option', { value: '', text: '— (unmapped)' }));
      for (const stage of LEAD_MAP_STAGES) {
        const opt = el('option', { value: stage, text: stage });
        if (current === stage) opt.selected = true;
        select.appendChild(opt);
      }
      select.addEventListener('change', () => {
        const v = select.value;
        if (v) LEAD_MAP.map[row.value] = v; else delete LEAD_MAP.map[row.value];
        LEAD_MAP.dirty = true;
        document.getElementById('leadMapSaveBtn')?.classList.add('btn-pulse');
      });
      tbody.appendChild(el('tr', {}, [
        el('td', { style: 'font-weight:600;', text: row.value }),
        el('td', { style: 'color:var(--text-secondary);', text: String(row.n) }),
        el('td', {}, [select]),
      ]));
    }
  }

  async function _saveLeadMap() {
    const btn = document.getElementById('leadMapSaveBtn');
    if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
    try {
      const r = await api('/api/admin/lead-status-map', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ map: LEAD_MAP.map }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast('Save failed: ' + (j.error || r.status), 'error');
        return;
      }
      const out = await r.json();
      LEAD_MAP.map = out.map || {};
      LEAD_MAP.dirty = false;
      toast(`Saved ${Object.keys(LEAD_MAP.map).length} mapping entries`, 'success');
      btn?.classList.remove('btn-pulse');
    } catch (err) {
      toast('Save failed: ' + (err.message || err), 'error');
    } finally {
      if (btn) { btn.disabled = false; btn.innerHTML = '<i data-lucide="save"></i> Save mapping'; refreshIcons(); }
    }
  }
  // ============ /Lead status mapping admin ============

  function dashCurrentRange() {
    const from = (document.getElementById('dashDateFrom')?.value || '').trim();
    const to = (document.getElementById('dashDateTo')?.value || '').trim();
    const agent = (document.getElementById('dashAgentFilter')?.value || '').trim();
    const source = (document.getElementById('dashSourceFilter')?.value || '').trim();
    const region = (document.getElementById('dashRegionFilter')?.value || '').trim();
    const country = (document.getElementById('dashCountryFilter')?.value || '').trim();
    return { from, to, agent, source, region, country };
  }

  async function dashLoadAgentDropdown() {
    const select = document.getElementById('dashAgentFilter');
    if (!select) return;
    const role = (state.me?.role || '').toLowerCase();
    const isAdmin = _isSalesAdmin();
    if (!isAdmin) {
      // Agents can't switch — they only ever see their own data. Lock the
      // dropdown to themselves so the UI matches what the server allows.
      while (select.children.length > 0) select.removeChild(select.firstChild);
      const me = state.me?.username || '';
      select.appendChild(el('option', { value: me, text: state.me?.displayName || me }));
      select.disabled = true;
      // Hide the wrapping label too if available
      const wrap = select.closest('label, .field, .form-row');
      if (wrap) wrap.style.opacity = '0.7';
      return;
    }
    try {
      const r = await api('/api/users/assignable');
      const j = await r.json();
      const users = Array.isArray(j.users) ? j.users : [];
      // Keep the leading "All agents" option, then add one <option> per user.
      while (select.children.length > 1) select.removeChild(select.lastChild);
      users.forEach(u => {
        select.appendChild(el('option', { value: u.username, text: userLabel(u.username, u.displayName || u.username) }));
      });
    } catch (err) {
      console.warn('[dashboards] agent dropdown load failed:', err);
    }
  }

  // SOURCE filter dropdown (funnel tab). Unlike the agent select, this stays
  // enabled for every role — source is not a permission axis. Options come
  // from /api/funnel/sources (real distinct sources + lead counts), grouped:
  // Principales (common), Website (website_*), Rares (< 3 leads).
  async function dashLoadSourceDropdown() {
    const select = document.getElementById('dashSourceFilter');
    if (!select) return;
    try {
      const r = await api('/api/funnel/sources');
      const j = await r.json();
      const rows = Array.isArray(j.sources) ? j.sources : [];
      // Keep the leading "All sources" option, then rebuild the groups.
      while (select.children.length > 1) select.removeChild(select.lastChild);
      const isWebsite = s => String(s.source).startsWith('website_');
      const main = rows.filter(s => !isWebsite(s) && s.n >= 3);
      const website = rows.filter(isWebsite);
      const rare = rows.filter(s => !isWebsite(s) && s.n < 3);
      const addGroup = (label, items) => {
        if (!items.length) return;
        const grp = el('optgroup', { label });
        items.forEach(s => {
          grp.appendChild(el('option', { value: s.source, text: `${_whySourceLabel(s.source)} (${s.n})` }));
        });
        select.appendChild(grp);
      };
      addGroup('Principales', main);
      addGroup('Website', website);
      addGroup('Rares', rare);
    } catch (err) {
      console.warn('[dashboards] source dropdown load failed:', err);
    }
  }

  // ---- Objectif tab — motivational month-to-date wallet ----
  // Hero: total encaissé this month (count-up) vs the configurable team
  // objective (progress line). Board: full per-agent leaderboard — visible
  // to EVERY sales role (that's the point). Side: quick month stats.
  function _objNum(n) {
    return String(Math.round(Number(n) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }
  function _objMonthLabel(monthStr) {
    try {
      const parts = String(monthStr || '').split('-');
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
      const s = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      return s.charAt(0).toUpperCase() + s.slice(1);
    } catch (_) { return monthStr || ''; }
  }
  function _objDaysLeft(monthStr) {
    const now = new Date();
    const cur = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
    if (monthStr && monthStr !== cur) return null;    // not the current month
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return last - now.getDate() + 1;                  // today counts
  }
  function animateCountUp(node, to, fmt) {
    const reduced = !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
    const format = fmt || _objNum;
    if (node._objRaf) cancelAnimationFrame(node._objRaf);
    const from = Number(node._objVal) || 0;
    node._objVal = to;
    if (reduced || to === from) { node.textContent = format(to); return; }
    const dur = 1100;
    const t0 = performance.now();
    const step = (t) => {
      // A re-render may have detached this node (clear(body)) with a frame
      // still queued — stop the loop instead of animating a ghost.
      if (!node.isConnected) { node._objRaf = null; return; }
      const k = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - k, 3);             // easeOutCubic
      node.textContent = format(from + (to - from) * e);
      if (k < 1) node._objRaf = requestAnimationFrame(step);
    };
    node._objRaf = requestAnimationFrame(step);
  }

  // Routes the Objectif tab to either the monthly wallet or the weekly
  // challenge depending on DASH.objPeriod (toggled by the Mensuel/Hebdo seg).
  function refreshObjectifActive() {
    if (DASH.objPeriod === 'week') refreshObjectifWeek();
    else refreshObjectif();
  }

  // Mensuel / Hebdo segmented control — rendered at the top of BOTH renderers
  // so it persists across switches. Reuses the .teamchat-seg house pattern.
  // ctx (optional) re-targets the toggle at another DASH period key — the
  // Support tab passes its own (periodKey + onSwitch). Default = Objectif.
  function _objPeriodToggle(ctx) {
    const c = ctx || {
      periodKey: 'objPeriod',
      onSwitch: (period) => {
        // Entering Hebdo always starts on the live week (don't re-open on a
        // previously-browsed past week); bust the cache so it re-fetches.
        if (period === 'week') { DASH.objWeek = null; DASH.objWeekMonth = null; DASH.lastWalletWeek = null; }
        refreshObjectifActive();
      },
    };
    const seg = el('div', { class: 'obj-period-seg teamchat-seg', role: 'tablist', 'aria-label': 'Monthly or Weekly' });
    [['month', 'Monthly'], ['week', 'Weekly']].forEach(([period, label]) => {
      const on = DASH[c.periodKey] === period;
      const btn = el('button', {
        class: 'teamchat-seg-btn' + (on ? ' on' : ''),
        type: 'button',
        role: 'tab',
        'aria-selected': on ? 'true' : 'false',
        text: label,
        onclick: () => {
          if (DASH[c.periodKey] === period) return;
          DASH[c.periodKey] = period;
          c.onSwitch(period);
        },
      });
      seg.appendChild(btn);
    });
    return seg;
  }

  // Compact month stepper for the MONTHLY Objectif view. Range is capped to the
  // current month + the previous (closed) month ONLY — per Ayoub, agents and
  // admins navigate just "this month" and "last month". Back chevron disabled
  // at the previous month; forward chevron disabled at the current month (never
  // the future). Mirrors the weekly challenge's month stepper.
  // ctx (optional): { monthKey, go(next, curMonth) } re-targets the stepper at
  // another DASH month key (the Support tab). Default = the Objectif wallet.
  function _objMonthSelector(data, ctx) {
    const c = ctx || {
      monthKey: 'objMonth',
      go: (next, cur) => {
        DASH.objMonth = (next === cur) ? null : next;
        DASH.lastWallet = null;      // fresh totals + count-up
        DASH.lastPayments = null;    // payments modal must match the shown month
        DASH.lastRestStats = null;   // rest-stats are current-month-only
        refreshObjectif();
      },
    };
    const curMonth = _objCurrentMonthYM();
    const prevMonth = _objAddMonths(curMonth, -1);
    // Month actually shown (server-confirmed) — else the DASH month key, else current.
    const shown = (data && data.month) ? data.month : (DASH[c.monthKey] || curMonth);
    const atCur = shown >= curMonth;      // can't step forward past the current month
    const atFloor = shown <= prevMonth;   // can't step back past the previous month
    const go = (dir) => {
      let next = _objAddMonths(shown, dir);
      if (next < prevMonth) next = prevMonth;
      if (next > curMonth) next = curMonth;
      c.go(next, curMonth);
    };
    const wrap = el('div', {
      class: 'obj-month-stepper',
      style: 'display:inline-flex;align-items:center;gap:2px;flex:none;'
        + 'padding:2px;border-radius:8px;background:var(--surface-sunken);border:1px solid var(--border-subtle);',
    });
    const mkChev = (dir, disabled) => {
      const b = el('button', {
        class: 'icon-btn-sm', type: 'button',
        'aria-label': dir < 0 ? 'Mois précédent' : 'Mois suivant',
        disabled: disabled ? 'disabled' : null,
        style: 'width:24px;height:24px;border-radius:6px;' + (disabled ? 'opacity:.4;cursor:not-allowed;' : ''),
        onclick: disabled ? null : () => go(dir),
      });
      b.innerHTML = dir < 0
        ? '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>'
        : '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
      return b;
    };
    wrap.appendChild(mkChev(-1, atFloor));
    wrap.appendChild(el('span', {
      text: _objMonthLabelFr(shown),
      style: 'min-width:96px;text-align:center;font-size:12px;font-weight:600;color:var(--text-secondary);'
        + 'font-variant-numeric:tabular-nums;padding:0 2px;',
    }));
    wrap.appendChild(mkChev(1, atCur));
    return wrap;
  }

  async function refreshObjectif() {
    const body = document.getElementById('dashObjectifBody');
    if (!body) return;
    if (!DASH.lastWallet) {
      clear(body);
      body.appendChild(el('div', { class: 'dash-loading', text: "Chargement de l'objectif…" }));
    }
    try {
      // DASH.objMonth ('YYYY-MM') lets an agent/admin browse the previous month; null = current.
      const qs = DASH.objMonth ? ('?month=' + encodeURIComponent(DASH.objMonth)) : '';
      const r = await api('/api/funnel/wallet' + qs);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      const prevTotal = DASH.lastWallet ? Number(DASH.lastWallet.total) || 0 : 0;
      DASH.lastWallet = j;
      renderObjectif(j, prevTotal);
      // Warm the payments list so "Détail des paiements" opens instantly.
      prefetchWalletPayments();
    } catch (err) {
      clear(body);
      const box = el('div', { class: 'obj-error' }, [
        el('div', { class: 'obj-error-text', text: 'Impossible de charger l\'objectif — ' + (err.message || err) }),
        el('button', { class: 'btn btn-sm', type: 'button', text: 'Réessayer', onclick: () => { DASH.lastWallet = null; refreshObjectif(); } }),
      ]);
      body.appendChild(box);
    }
  }

  function renderObjectif(data, prevTotal) {
    const body = document.getElementById('dashObjectifBody');
    if (!body) return;
    // Stop any in-flight count-up before its node gets detached.
    const oldNum = body.querySelector('.obj-amount-num');
    if (oldNum && oldNum._objRaf) cancelAnimationFrame(oldNum._objRaf);
    clear(body);
    const total = Number(data.total) || 0;
    const objective = Number(data.objective) || 0;
    const pct = objective > 0 ? Math.min(100, (total / objective) * 100) : 0;
    const pctLabel = objective > 0 ? Math.round((total / objective) * 100) : 0;
    const reste = Math.max(0, objective - total);
    const reached = objective > 0 && total >= objective;
    const agentObjective = Number(data.agentObjective) || 0;
    const agents = Array.isArray(data.agents) ? data.agents : [];
    const me = (state.me && state.me.username) || '';
    // scope 'self' = agent view: ONLY their own numbers vs their personal
    // objective — no leaderboard, no team figures (server enforces it too).
    const isSelf = data.scope === 'self';
    // Is the shown month the current (in-progress) one? _objDaysLeft returns
    // null for any non-current month. Confirmation UI (agent button + admin
    // badges) and the current-month-only rest stats key off this.
    const isCurMonth = _objDaysLeft(data.month) != null;

    // "Palier atteint" chip — appears once an agent has CLEARED at least one
    // objective rung (base 100k → +50k each). value = highest cleared rung,
    // count = how many cleared. The progress bar keeps climbing to the NEXT
    // rung; this chip just celebrates the one(s) already passed. Count ≥ 2
    // gets the richer gold (--warning) treatment, like the gauge's champion.
    // Tokens-only, no app.css (compact, won't wrap the row at 13").
    const _objPalierChip = (value, count) => {
      if (!(Number(value) > 0)) return null;
      const gold = Number(count) >= 2;
      const fg = gold ? 'var(--warning)' : 'var(--success)';
      const bg = gold ? 'var(--warning-soft)' : 'var(--success-soft)';
      const chip = el('span', {
        class: 'obj-palier' + (gold ? ' is-multi' : ''),
        style: 'display:inline-flex;align-items:center;gap:4px;flex:none;'
          + 'padding:1px 8px;border-radius:999px;font-size:11px;font-weight:600;'
          + 'line-height:1.5;white-space:nowrap;color:' + fg + ';background:' + bg + ';',
        title: count >= 2 ? (count + ' paliers atteints') : 'Palier atteint',
      });
      // Tiny check glyph (currentColor, ~12px), no emoji.
      const ic = el('span', { style: 'display:inline-flex;width:12px;height:12px;flex:none;' });
      ic.innerHTML = '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      chip.appendChild(ic);
      chip.appendChild(el('span', { text: 'Palier ' + fmtDH(value) }));
      return chip;
    };

    const wrap = el('div', { class: 'obj-wrap' + (isSelf ? ' is-self' : '') });
    wrap.appendChild(_objPeriodToggle());
    // Month navigation — current + previous month only (per Ayoub). Own line
    // under the Monthly/Weekly toggle, mirroring the weekly challenge filter.
    const monthNav = el('div', {
      class: 'obj-month-nav',
      style: 'display:flex;justify-content:center;align-items:center;gap:8px;flex-wrap:wrap;margin:10px 0 2px;',
    });
    monthNav.appendChild(_objMonthSelector(data));
    wrap.appendChild(monthNav);

    // ----- Hero -----
    const hero = el('div', { class: 'obj-hero' + (reached ? ' is-reached' : '') });
    const head = el('div', { class: 'obj-hero-head' }, [
      el('div', { class: 'obj-month', text: (isSelf ? 'Mon encaissé · ' : 'Encaissé · ') + _objMonthLabel(data.month) }),
    ]);
    if (_isSalesAdmin()) {
      head.appendChild(el('button', {
        class: 'obj-edit-btn icon-btn-sm',
        type: 'button',
        title: "Modifier l'objectif",
        'aria-label': "Modifier l'objectif",
        onclick: () => _objStartEdit(),
      }));
    }
    hero.appendChild(head);

    const num = el('span', { class: 'obj-amount-num', text: '0' });
    num._objVal = prevTotal || 0;
    hero.appendChild(el('div', { class: 'obj-amount' }, [num, el('span', { class: 'obj-amount-cur', text: 'DH' })]));

    const fill = el('div', { class: 'obj-progress-fill' + (reached ? ' is-done' : '') });
    const prevPct = objective > 0 ? Math.min(100, Math.max(0, ((prevTotal || 0) / objective) * 100)) : 0;
    fill.style.width = prevPct.toFixed(1) + '%';
    const meta = el('div', { class: 'obj-progress-meta' }, [
      el('strong', { class: 'obj-progress-pct', text: pctLabel + ' %' }),
      el('span', { class: 'obj-progress-obj', id: 'objMetaObjective', text: (isSelf ? 'Objectif personnel : ' : 'Objectif : ') + fmtDH(objective) }),
      el('span', {
        class: 'obj-progress-reste' + (reached ? ' is-done' : ''),
        text: reached ? (isSelf ? 'Objectif atteint — Mbrouk !' : 'Objectif atteint — Mbrouk l\'équipe !') : 'reste ' + fmtDH(reste),
      }),
    ]);
    // Paid vs rest split — the hero total merges both; show the breakdown
    // whenever rest tranches were collected this month.
    const restTotal = Number(data.restTotal) || 0;
    if (restTotal > 0) {
      const split = el('span', { class: 'obj-progress-restsplit' });
      split.appendChild(el('span', { text: _objNum(total - restTotal) + ' paiements' }));
      split.appendChild(el('span', { class: 'obj-rest-accent', text: ' + ' + _objNum(restTotal) + ' rests' }));
      meta.appendChild(split);
    }
    hero.appendChild(el('div', { class: 'obj-progress' }, [
      el('div', { class: 'obj-progress-track' }, [fill]),
      meta,
    ]));
    // Self hero: celebrate the highest cleared rung under the bar while it keeps
    // climbing to the next target. e.g. "Palier 100 000 atteint · prochain 150 000".
    if (isSelf) {
      const selfPalierVal = data.palierValue != null ? Number(data.palierValue) : Number((agents[0] || {}).palierValue) || 0;
      const selfPalierCnt = data.palierCount != null ? Number(data.palierCount) : Number((agents[0] || {}).palierCount) || 0;
      const heroChip = _objPalierChip(selfPalierVal, selfPalierCnt);
      if (heroChip) {
        const line = el('div', {
          class: 'obj-hero-palier',
          style: 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:10px;',
        });
        line.appendChild(heroChip);
        if (objective > 0) {
          line.appendChild(el('span', {
            style: 'font-size:11.5px;color:var(--muted,var(--text));opacity:0.7;',
            text: 'atteint · prochain ' + fmtDH(objective),
          }));
        }
        hero.appendChild(line);
      }
    } else if (Number(data.palierValue) > 0) {
      // Team milestone cleared (500 000 → the month now aims at 700 000).
      const teamChip = _objPalierChip(Number(data.palierValue), 1);
      if (teamChip) {
        const line = el('div', {
          class: 'obj-hero-palier',
          style: 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:10px;',
        });
        line.appendChild(teamChip);
        line.appendChild(el('span', {
          style: 'font-size:11.5px;color:var(--muted,var(--text));opacity:0.7;',
          text: 'atteint · prochain ' + fmtDH(objective),
        }));
        hero.appendChild(line);
      }
    }
    wrap.appendChild(hero);

    // Self scope (agent view): the salary gauge normally lives in a leaderboard
    // row, but agents have NO board — so render their own gauge under the hero.
    if (isSelf && data.commissionConfig) {
      const selfRow = agents.find(a => a.agent_id === me) || agents[0] || null;
      if (selfRow && selfRow.commission != null) {
        const salCard = el('div', { class: 'obj-self-salary' });
        salCard.appendChild(_objSalaryGauge(selfRow, data.commissionConfig));
        wrap.appendChild(salCard);
      }
    }

    // Monthly sign-off (agent self view) — ONLY on a CLOSED (past) month; the
    // current in-progress month is not confirmable. Once confirmed, the button
    // becomes a locked "✓ Confirmé" indicator (the agent validated their total).
    if (isSelf && !isCurMonth) {
      wrap.appendChild(_objConfirmBlock(data));
    }

    // ----- Grid: leaderboard + side stats (no board in self scope) -----
    let board = null;
    if (!isSelf) {
    board = el('div', { class: 'obj-board' });
    const boardHeadRight = el('div', { class: 'obj-board-objline', id: 'objAgentObjective' }, [
      el('span', { class: 'obj-board-objtxt', text: 'Objectif / agent : ' + fmtDH(agentObjective) }),
    ]);
    if (_isSalesAdmin()) {
      boardHeadRight.appendChild(el('button', {
        class: 'obj-edit-btn obj-edit-btn-agent icon-btn-sm',
        type: 'button',
        title: "Modifier l'objectif par agent",
        'aria-label': "Modifier l'objectif par agent",
        onclick: () => _objStartEdit('agent'),
      }));
    }
    board.appendChild(el('div', { class: 'obj-board-head' }, [
      el('div', {}, [
        el('div', { class: 'obj-board-title', text: 'Classement du mois' }),
        el('div', { class: 'obj-board-sub', text: 'Encaissements par agent' }),
      ]),
      boardHeadRight,
    ]));
    if (!agents.length) {
      const empty = el('div', { class: 'obj-board-empty' });
      const ic = el('div', { class: 'obj-board-empty-ic' });
      ic.innerHTML = '<i data-lucide="target"></i>';
      empty.appendChild(ic);
      empty.appendChild(el('div', { class: 'obj-board-empty-txt', text: 'Aucun encaissement ce mois-ci — le premier Mbrouk t\'attend.' }));
      board.appendChild(empty);
    } else {
      agents.forEach((a, idx) => {
        const isTop = idx === 0 && (Number(a.total) || 0) > 0;
        const row = el('div', { class: 'obj-row' + (isTop ? ' is-top' : '') });
        row.appendChild(el('div', { class: 'obj-rank' + (idx < 3 ? ' is-podium' : ''), text: String(idx + 1) }));
        const av = el('div', { class: 'avatar obj-avatar ' + avatarColorClass(a.agent_id || a.displayName || String(idx)), text: initials(userLabel(a.agent_id, a.displayName || a.agent_id || '?')) });
        row.appendChild(av);
        const nameKids = [];
        if (isTop) {
          const crown = el('span', { class: 'obj-crown' });
          crown.innerHTML = '<i data-lucide="crown"></i>';
          nameKids.push(crown);
        }
        nameKids.push(el('span', { text: userLabel(a.agent_id, a.displayName || a.agent_id || '—') }));
        if (me && a.agent_id === me) nameKids.push(el('span', { class: 'obj-me', text: '(moi)' }));
        row.appendChild(el('div', { class: 'obj-name' }, nameKids));
        // Chips (paiements + "Palier atteint") share ONE grid cell so the
        // 5-col row (rank·avatar·name·chips·total) stays aligned once an agent
        // crosses a rung and the palier badge appears — otherwise the extra
        // grid item pushed the total onto its own line.
        // The count opens that agent's payments for the shown month (same
        // popup as the Support board; «Réassigner» only for super admins).
        const aCount = a.payments_count || 0;
        const chips = el('div', { class: 'obj-chips' }, [
          el('button', {
            class: 'obj-paid-count obj-paid-count-btn',
            type: 'button',
            title: 'Voir les paiements',
            'aria-label': 'Voir les ' + aCount + ' paiement' + (aCount > 1 ? 's' : '') + ' de ' + userLabel(a.agent_id, a.displayName || a.agent_id || 'cet agent'),
            text: aCount + ' paiement' + (aCount > 1 ? 's' : ''),
            onclick: () => _objOpenAgentPayments(a, data),
          }),
        ]);
        // "Palier atteint" chip — only when she has cleared at least one rung.
        const palierChip = _objPalierChip(a.palierValue, a.palierCount);
        if (palierChip) chips.appendChild(palierChip);
        // Monthly sign-off status — only on a CLOSED month (the current
        // in-progress month is not confirmable, so no badge is shown then).
        if (!isCurMonth) {
          const isMine = me && a.agent_id === me;
          const cbadge = el('span', {
            class: 'obj-confirm-badge ' + (a.confirmed ? 'is-confirmed' : 'is-pending'),
            title: a.confirmed
              ? ('Confirmé' + (a.confirmedAt ? ' le ' + MA.loc(new Date(a.confirmedAt), 'd', 'fr-FR') : ''))
              : 'En attente de confirmation',
            text: a.confirmed ? '✓ Confirmé' : 'En attente',
          });
          if (a.confirmed && _isSalesAdmin()) {
            // Admin/super_sales can reopen a mistaken sign-off (safety valve).
            cbadge.style.cursor = 'pointer';
            cbadge.title += ' · cliquer pour rouvrir';
            cbadge.onclick = () => _objReopenConfirm(data.month, a.agent_id, a.displayName || a.agent_id);
          } else if (!a.confirmed && isMine) {
            // The admin who is ALSO a roster agent (Mary/super_sales) sees the
            // team board, not the agent hero button — let her confirm her own
            // month straight from her row.
            cbadge.style.cursor = 'pointer';
            cbadge.title = 'Confirmer mes paiements de ' + _objMonthLabel(data.month);
            cbadge.textContent = 'Confirmer';
            cbadge.onclick = () => _objConfirmMonth(data.month, null);
          }
          chips.appendChild(cbadge);
          if (a.confirmed && a.confirmDrift) {
            chips.appendChild(el('span', { class: 'obj-confirm-badge is-drift', title: 'Paiements modifiés depuis la confirmation', text: '⚠ modifié' }));
          }
        }
        row.appendChild(chips);
        // Revenue cell — prominent total ("Encaissé") with a colour-coded
        // paid/rest split underneath (per Ayoub: paid AND rest visible
        // separately — paid in green, reste in gold). Non-sensitive: revenue
        // is already shown to everyone; no commission field is exposed here.
        const restTotal = Number(a.rest_total) || 0;
        const paidTotal = (Number(a.total) || 0) - restTotal;
        const totalCell = el('div', { class: 'obj-total' });
        totalCell.appendChild(el('div', { class: 'obj-total-num', text: fmtDH(a.total) }));
        const sub = el('div', { class: 'obj-total-split' });
        sub.appendChild(el('span', { class: 'obj-total-paid' }, [
          el('span', { class: 'obj-total-dot' }),
          el('span', { text: _objNum(paidTotal) + ' payé' }),
        ]));
        if (restTotal > 0) {
          sub.appendChild(el('span', { class: 'obj-total-rest' }, [
            el('span', { class: 'obj-total-dot' }),
            el('span', { text: '+ ' + _objNum(restTotal) + ' reste' }),
          ]));
        }
        totalCell.appendChild(sub);
        row.appendChild(totalCell);
        // Personal objective progress — PROGRESSIVE ladder. Each agent's bar
        // measures against her OWN rolled-up target (base 100k → 150k → 200k …,
        // the server sends a.objective/objPct/objReached once she reaches a
        // rung). Fall back to the shared base if an old server omits the fields.
        if (agentObjective > 0) {
          const rowObj = Number(a.objective) || agentObjective;
          const aPct = a.objPct != null ? Number(a.objPct) : Math.min(100, ((Number(a.total) || 0) / rowObj) * 100);
          const aDone = a.objReached != null ? !!a.objReached : (Number(a.total) || 0) >= rowObj;
          const rowFill = el('div', { class: 'obj-row-fill' + (aDone ? ' is-done' : '') });
          rowFill.style.width = Math.min(100, aPct).toFixed(1) + '%';
          row.appendChild(el('div', { class: 'obj-row-progress' }, [
            el('div', { class: 'obj-row-track' }, [rowFill]),
            el('span', { class: 'obj-row-pct' + (aDone ? ' is-done' : ''), text: Math.round(aPct) + ' %' }),
          ]));
        }
        // Salary / commission gauge — full-width sub-block spanning the row.
        // Rendered per-row, driven entirely by what the server sent: the
        // backend attaches a.commission* only to rows the caller may see (an
        // agent → just her own row, super_sales → her own, admin → all rows),
        // so each user sees the gauge on their own row and the admin on every
        // row, with no client-side leak risk. commissionConfig (tiers only —
        // rates are omitted for non-admins) is shipped whenever any row carries
        // commission; _objSalaryGauge needs just tiers + the per-agent fields.
        if (data.commissionConfig && a.commission != null) {
          row.appendChild(_objSalaryGauge(a, data.commissionConfig));
        }
        board.appendChild(row);
      });
    }
    } // end !isSelf — agents get no leaderboard at all

    const side = el('div', { class: 'obj-side' });
    const avg = data.paymentsCount > 0 ? total / data.paymentsCount : 0;
    const daysLeft = _objDaysLeft(data.month);
    const stats = [
      { label: 'Paiements ce mois', val: String(data.paymentsCount || 0) },
      { label: 'Panier moyen', val: avg > 0 ? fmtDH(avg) : '—' },
      { label: 'Jours restants', val: daysLeft == null ? '—' : String(daysLeft) },
    ];
    for (const s of stats) {
      side.appendChild(el('div', { class: 'obj-stat' }, [
        el('div', { class: 'obj-stat-num', text: s.val }),
        el('div', { class: 'obj-stat-label', text: s.label }),
      ]));
    }
    // Rest stats (HubSpot-derived, loaded async — cached values paint
    // instantly, '…' on true cold load). Scoped per agent server-side.
    // Current month ONLY: /api/funnel/rest-stats is locked to the current
    // Casablanca month, so on a past month these would be misleading — hide them.
    if (isCurMonth) {
      const rs = DASH.lastRestStats;
      side.appendChild(el('div', { class: 'obj-stat' }, [
        el('div', { class: 'obj-stat-num obj-stat-owed', id: 'objRestOwedNum', text: rs ? fmtDH(rs.owed?.total || 0) : '…' }),
        el('div', { class: 'obj-stat-label', text: 'Reste à encaisser' }),
      ]));
      side.appendChild(el('div', { class: 'obj-stat' }, [
        el('div', { class: 'obj-stat-num obj-stat-collected', id: 'objRestCollectedNum', text: rs ? fmtDH(rs.collected?.total || 0) : '…' }),
        el('div', { class: 'obj-stat-label', text: 'Rest encaissé ce mois' }),
      ]));
    }
    // Team payroll — sum of every agent's commission this month. Visible to the
    // whole sales-admin board (super_sales included); the rate-edit button below
    // stays system-admin-only. Server omits totalCommission for regular agents.
    if (_isSalesAdmin() && data.totalCommission != null) {
      const bonusTotal = Number(data.totalSalaryBonus) || 0;
      const payrollStat = el('div', { class: 'obj-stat' }, [
        el('div', { class: 'obj-stat-num obj-stat-payroll', text: fmtDH((Number(data.totalCommission) || 0) + bonusTotal) }),
        el('div', { class: 'obj-stat-label', text: bonusTotal > 0
          ? 'Salaires à payer ce mois · dont ' + fmtDH(bonusTotal) + ' de bonus'
          : 'Commissions à payer ce mois' }),
      ]);
      if (_isSystemAdmin() && data.commissionConfig) {
        const cfgBtn = el('button', {
          class: 'obj-edit-btn obj-edit-btn-commission icon-btn-sm',
          type: 'button',
          title: 'Modifier les commissions',
          'aria-label': 'Modifier les commissions',
          onclick: () => _objEditCommission(),
        });
        payrollStat.appendChild(cfgBtn);
      }
      side.appendChild(payrollStat);
    }
    const payLink = el('button', { class: 'obj-side-link', type: 'button', onclick: openWalletPayments });
    payLink.innerHTML = '<span>Détail des paiements</span><i data-lucide="receipt"></i>';
    side.appendChild(payLink);
    const funnelLink = el('button', { class: 'obj-side-link', type: 'button', onclick: () => dashSwitchTab('funnel') });
    funnelLink.innerHTML = '<span>Voir le funnel complet</span><i data-lucide="arrow-right"></i>';
    side.appendChild(funnelLink);

    wrap.appendChild(el('div', { class: 'obj-grid' + (isSelf ? ' is-self' : '') }, isSelf ? [side] : [board, side]));
    body.appendChild(wrap);
    refreshIcons(body);

    // Rest stats fill in when the (cached) HubSpot lookup lands — current month only.
    if (isCurMonth) _loadRestStats();

    // Animations after paint: count-up + progress sweep.
    animateCountUp(num, total);
    requestAnimationFrame(() => { fill.style.width = pct.toFixed(1) + '%'; });
  }

  // Rest stats for the Objectif side card — async (live HubSpot behind a
  // 5-min server cache); fills the two placeholder stats when it lands.
  async function _loadRestStats() {
    try {
      const r = await api('/api/funnel/rest-stats');
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      DASH.lastRestStats = j;
      const o = document.getElementById('objRestOwedNum');
      if (o) o.textContent = fmtDH(j.owed?.total || 0);
      const c = document.getElementById('objRestCollectedNum');
      if (c) c.textContent = fmtDH(j.collected?.total || 0);
    } catch (_) {
      const o = document.getElementById('objRestOwedNum');
      if (o && o.textContent === '…') o.textContent = '—';
      const c = document.getElementById('objRestCollectedNum');
      if (c && c.textContent === '…') c.textContent = '—';
    }
  }

  // Agent monthly sign-off block (self view, CLOSED month only). Either the
  // "Confirmer mes paiements de <mois>" button, or — once signed off — a locked
  // "✓ Confirmé le <date>" indicator (+ a subtle drift note if the paid list
  // changed since). See POST /api/funnel/wallet/confirm.
  function _objConfirmBlock(data) {
    const monthTxt = _objMonthLabel(data.month);
    const box = el('div', { class: 'obj-confirm' });
    if (data.confirmed) {
      const when = data.confirmedAt
        ? MA.loc(new Date(data.confirmedAt), 'd', 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
        : '';
      const done = el('div', { class: 'obj-confirm-done' });
      done.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      done.appendChild(el('span', { text: 'Confirmé' + (when ? ' le ' + when : '') }));
      box.appendChild(done);
      if (data.confirmDrift) {
        box.appendChild(el('div', {
          class: 'obj-confirm-drift',
          text: 'Vos paiements ont changé depuis la confirmation — prévenez un admin si besoin.',
        }));
      }
    } else {
      const btn = el('button', {
        class: 'obj-confirm-btn btn btn-primary',
        type: 'button',
        text: 'Confirmer mes paiements de ' + monthTxt,
      });
      btn.onclick = () => _objConfirmMonth(data.month, btn);
      box.appendChild(btn);
      box.appendChild(el('div', {
        class: 'obj-confirm-hint',
        text: 'En confirmant, vous validez que votre liste de paiements de ' + monthTxt + ' est correcte (base de votre paie).',
      }));
    }
    return box;
  }

  // Agent confirms their own CLOSED month → POST, then re-render so the button
  // flips to the locked "✓ Confirmé" indicator.
  async function _objConfirmMonth(month, btn) {
    if (btn) btn.disabled = true;
    try {
      const r = await api('/api/funnel/wallet/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(j.error || 'Échec de la confirmation', 'error'); if (btn) btn.disabled = false; return; }
      toast('Paiements confirmés — Mbrouk !', 'success');
      DASH.lastWallet = null;   // refetch to flip the button → done badge
      refreshObjectif();
    } catch (_) {
      toast('Erreur réseau', 'error');
      if (btn) btn.disabled = false;
    }
  }

  // Admin/super_sales reopen an agent's monthly sign-off (safety valve).
  async function _objReopenConfirm(month, agentId, name) {
    if (!confirm('Rouvrir la confirmation de ' + (name || agentId) + ' pour ' + _objMonthLabel(month) + ' ?\n\nL\'agent devra re-confirmer ses paiements.')) return;
    try {
      const r = await api('/api/funnel/wallet/confirm/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, agentId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(j.error || 'Échec de la réouverture', 'error'); return; }
      toast('Confirmation rouverte', 'success');
      DASH.lastWallet = null;
      refreshObjectif();
    } catch (_) { toast('Erreur réseau', 'error'); }
  }

  // ── Weekly sign-off (Weekly challenge view) — twin of the monthly block ──────
  function _objConfirmBlockWeek(data) {
    const range = _objWeekRangeFr(data.weekStart, data.weekEnd);
    const box = el('div', { class: 'obj-confirm' });
    if (data.confirmed) {
      const when = data.confirmedAt
        ? MA.loc(new Date(data.confirmedAt), 'd', 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
        : '';
      const done = el('div', { class: 'obj-confirm-done' });
      done.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      done.appendChild(el('span', { text: 'Semaine confirmée' + (when ? ' le ' + when : '') }));
      box.appendChild(done);
      if (data.confirmDrift) {
        box.appendChild(el('div', {
          class: 'obj-confirm-drift',
          text: 'Vos paiements ont changé depuis la confirmation — prévenez un admin si besoin.',
        }));
      }
    } else {
      const btn = el('button', {
        class: 'obj-confirm-btn btn btn-primary',
        type: 'button',
        text: 'Confirmer mes paiements de la semaine (' + range + ')',
      });
      btn.onclick = () => _objConfirmWeek(data.weekStart, btn);
      box.appendChild(btn);
      box.appendChild(el('div', {
        class: 'obj-confirm-hint',
        text: 'En confirmant, vous validez que votre collecte de la semaine (' + range + ') est correcte.',
      }));
    }
    return box;
  }

  async function _objConfirmWeek(week, btn) {
    if (btn) btn.disabled = true;
    try {
      const r = await api('/api/funnel/wallet/weekly/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ week }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(j.error || 'Échec de la confirmation', 'error'); if (btn) btn.disabled = false; return; }
      toast('Semaine confirmée — Mbrouk !', 'success');
      DASH.lastWalletWeek = null;
      refreshObjectifWeek();
    } catch (_) {
      toast('Erreur réseau', 'error');
      if (btn) btn.disabled = false;
    }
  }

  async function _objReopenWeekConfirm(week, agentId, name) {
    if (!confirm('Rouvrir la confirmation de ' + (name || agentId) + ' pour cette semaine ?\n\nL\'agent devra re-confirmer ses paiements.')) return;
    try {
      const r = await api('/api/funnel/wallet/weekly/confirm/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ week, agentId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(j.error || 'Échec de la réouverture', 'error'); return; }
      toast('Confirmation rouverte', 'success');
      DASH.lastWalletWeek = null;
      refreshObjectifWeek();
    } catch (_) { toast('Erreur réseau', 'error'); }
  }

  // "Détail des paiements" — month's payment list (agents: only their own,
  // server-enforced). Clicking a client name jumps to their inbox convo so
  // the team can verify nothing was forgotten.
  // Perceived-speed strategy: the list is PREFETCHED in the background when
  // the Objectif tab loads (and after every Mbrouk), so opening the modal
  // paints instantly from DASH.lastPayments; a silent re-fetch then keeps
  // it fresh. The spinner only ever shows on a true cold open.
  async function _loadWalletPayments() {
    // Match the month currently shown by the board stepper.
    const qs = DASH.objMonth ? ('?month=' + encodeURIComponent(DASH.objMonth)) : '';
    const r = await api('/api/funnel/wallet/payments' + qs);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
    DASH.lastPayments = j;
    return j;
  }
  function prefetchWalletPayments() {
    _loadWalletPayments().catch(() => { /* background prefetch is best-effort */ });
  }
  async function openWalletPayments() {
    openModal('walletPaymentsModal');
    const box = document.getElementById('wpayContent');
    if (!box) return;
    if (DASH.lastPayments) {
      renderWalletPayments(DASH.lastPayments);     // instant from cache
      _loadWalletPayments().then(j => {            // silent refresh
        const m = document.getElementById('walletPaymentsModal');
        if (m && !m.classList.contains('hidden')) renderWalletPayments(j);
      }).catch(() => { /* keep showing cache */ });
      return;
    }
    clear(box);
    box.appendChild(el('div', { class: 'dash-loading', text: 'Chargement…' }));
    try {
      renderWalletPayments(await _loadWalletPayments());
    } catch (err) {
      clear(box);
      box.appendChild(el('div', { class: 'obj-error' }, [
        el('div', { class: 'obj-error-text', text: 'Erreur : ' + (err.message || err) }),
        el('button', { class: 'btn btn-sm', type: 'button', text: 'Réessayer', onclick: () => openWalletPayments() }),
      ]));
    }
  }
  // ---- Deleting a payment recorded by mistake (mig 0058) ------------------
  // Whoever holds the permission removes their OWN wrong payment here, with a
  // reason; everyone else's 🗑 files a request that administration answers
  // (Oui / Non) from the panel at the top of this same modal. Nothing is
  // erased server-side — the row is voided, and Objectif shows the net.
  function _wpayColCount(isTeam) { return isTeam ? 8 : 7; }

  function _wpayActionCell(p, isTeam) {
    const td = el('td', { class: 'wpay-act' });
    if (p.voidRequest) {
      td.appendChild(el('span', {
        class: 'wpay-void-badge',
        title: 'Demandé par ' + userLabel(p.voidRequest.requestedBy || '?') + (p.voidRequest.reason ? ' — ' + adminText(p.voidRequest.reason) : ''),
        text: 'Demande envoyée',
      }));
      return td;
    }
    td.appendChild(el('button', {
      class: 'icon-btn wpay-del' + (p.canDelete ? '' : ' wpay-del-ask'),
      type: 'button',
      title: p.canDelete
        ? 'Supprimer ce paiement (saisie erronée)'
        : "Demander la suppression à l'administration",
      'aria-label': p.canDelete ? 'Supprimer ce paiement' : 'Demander la suppression',
      onclick: (ev) => _wpayOpenVoidForm(ev.currentTarget.closest('tr'), p, isTeam),
    }, [el('i', { 'data-lucide': 'trash-2' })]));
    return td;
  }

  // Inline reason form — a sibling row, so the table never jumps and the
  // client/amount being removed stay visible right above it.
  function _wpayOpenVoidForm(tr, p, isTeam) {
    if (!tr || !tr.parentNode) return;
    if (tr.nextSibling && tr.nextSibling.classList && tr.nextSibling.classList.contains('wpay-void-form')) return;
    const input = el('input', {
      class: 'input wpay-void-input', type: 'text', maxlength: '200',
      placeholder: p.canDelete ? 'Raison (doublon, montant erroné…)' : 'Pourquoi faut-il le supprimer ?',
    });
    const submit = el('button', {
      class: 'btn btn-sm btn-danger', type: 'button',
      text: p.canDelete ? 'Supprimer' : 'Envoyer la demande',
    });
    const formRow = el('tr', { class: 'wpay-void-form' }, [
      el('td', { colspan: String(_wpayColCount(isTeam)) }, [
        el('div', { class: 'wpay-void-form-in' }, [
          el('span', { class: 'wpay-void-q', text: fmtDH(p.amount) + ' · ' + (p.clientName || '—') }),
          input,
          submit,
          el('button', {
            class: 'btn btn-sm btn-ghost', type: 'button', text: 'Annuler',
            onclick: () => formRow.remove(),
          }),
        ]),
      ]),
    ]);
    submit.addEventListener('click', () => _wpaySubmitVoid(p, input.value, submit));
    input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') _wpaySubmitVoid(p, input.value, submit); });
    tr.parentNode.insertBefore(formRow, tr.nextSibling);
    input.focus();
  }

  async function _wpaySubmitVoid(p, reason, btn) {
    const why = String(reason || '').trim();
    if (why.length < 3) { toast('Merci d\'indiquer une raison', 'error'); return; }
    btn.disabled = true;
    try {
      const r = await api('/api/funnel/wallet/payments/' + encodeURIComponent(p.id) + '/void', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: why }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok && r.status !== 202) throw new Error(j.error || ('HTTP ' + r.status));
      toast(j.voided ? 'Paiement supprimé — Objectif mis à jour' : "Demande envoyée à l'administration", 'success');
      DASH.lastPayments = null;
      renderWalletPayments(await _loadWalletPayments());
      if (j.voided) refreshObjectifActive();   // the board total just changed
    } catch (err) {
      toast(err.message || 'Erreur', 'error');
      btn.disabled = false;
    }
  }

  // Administration's queue, shown at the top of the modal for sales admins.
  // Two kinds now: deletions (0058) and additions (0059) — same panel, because
  // to administration they are the same question, "does this money count?".
  function _wpayVoidRequestsPanel() {
    const box = el('div', { class: 'wpay-req-panel hidden' });
    _wpayLoadVoidRequests(box);
    return box;
  }
  function _wpayAddRequestsPanel(reload) {
    const box = el('div', { class: 'wpay-req-panel wpay-req-panel-add hidden' });
    _wpayLoadAddRequests(box, reload);
    return box;
  }
  async function _wpayLoadAddRequests(box, reload) {
    try {
      const r = await api('/api/funnel/wallet/add-requests');
      const j = await r.json().catch(() => ({}));
      if (!r.ok) return;                       // the panel is a bonus — stay silent
      const reqs = Array.isArray(j.requests) ? j.requests : [];
      clear(box);
      if (!reqs.length) { box.classList.add('hidden'); return; }
      box.classList.remove('hidden');
      const mine = j.scope === 'self';
      box.appendChild(el('div', { class: 'wpay-req-head' }, [
        el('i', { 'data-lucide': mine ? 'clock' : 'alert-circle' }),
        el('span', {
          text: mine
            ? reqs.length + (reqs.length > 1 ? ' ajouts en attente de validation' : ' ajout en attente de validation')
            : reqs.length + (reqs.length > 1 ? ' demandes d’ajout' : ' demande d’ajout'),
        }),
      ]));
      reqs.forEach(rq => box.appendChild(mine ? _wpayMyAddRow(rq) : _wpayAddRequestRow(rq, reload)));
      refreshIcons(box);
    } catch { /* never break the payments list over the review panel */ }
  }
  // What an agent sees: their own submission, waiting. No buttons — the whole
  // point is that they cannot decide it.
  function _wpayMyAddRow(rq) {
    return el('div', { class: 'wpay-req-row' }, [
      el('div', { class: 'wpay-req-info' }, [
        el('strong', { text: fmtDH(rq.amount) + ' · ' + (rq.client_name || rq.client_phone || '—') }),
        el('span', { class: 'wpay-req-meta', text: 'en attente de l’administration' }),
      ]),
    ]);
  }
  function _wpayAddRequestRow(rq, reload) {
    // Administration approves the method too: pre-filled with the agent's
    // pick, required for a request filed before the method existed.
    const modeSel = paymentModeSelect(rq.mode_de_paiment, 'wpay-req-mode');
    const decide = async (decision, btn) => {
      if (decision === 'approve' && !modeSel.value) {
        toast('Choisis le mode de paiement avant d’ajouter', 'error');
        modeSel.focus();
        return;
      }
      btn.disabled = true;
      try {
        const r = await api('/api/funnel/wallet/add-requests/' + encodeURIComponent(rq.id) + '/decide', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(decision === 'approve' ? { decision, mode_de_paiment: modeSel.value } : { decision }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
        toast(j.created ? 'Paiement ajouté' : 'Demande refusée', 'success');
        DASH.lastPayments = null;
        if (typeof reload === 'function') await reload();
        else renderWalletPayments(await _loadWalletPayments());
        if (j.created) refreshObjectifActive();
      } catch (err) {
        toast(err.message || 'Erreur', 'error');
        btn.disabled = false;
      }
    };
    const yes = el('button', { class: 'btn btn-sm btn-primary', type: 'button', text: 'Oui, ajouter' });
    const no = el('button', { class: 'btn btn-sm btn-ghost', type: 'button', text: 'Non' });
    yes.addEventListener('click', () => decide('approve', yes));
    no.addEventListener('click', () => decide('reject', no));
    const when = rq.paid_on ? new Date(rq.paid_on) : null;
    return el('div', { class: 'wpay-req-row' }, [
      el('div', { class: 'wpay-req-info' }, [
        el('strong', { text: fmtDH(rq.amount) + ' · ' + (rq.client_name || rq.client_phone || '—') }),
        el('span', {
          class: 'wpay-req-meta',
          text: 'pour ' + userLabel(rq.agent_id || '?')
            + ' · demande de ' + userLabel(rq.requested_by || '?')
            + (when && !isNaN(when) ? ' · encaissé le ' + MA.loc(when, 'd', 'fr-FR', { day: '2-digit', month: 'short' }) : ''),
        }),
        rq.pack ? el('span', { class: 'wpay-req-reason', text: rq.pack }) : el('span', { class: 'hidden' }),
      ]),
      el('div', { class: 'wpay-req-actions' }, [modeSel, yes, no]),
    ]);
  }
  async function _wpayLoadVoidRequests(box) {
    try {
      const r = await api('/api/funnel/wallet/void-requests');
      const j = await r.json().catch(() => ({}));
      if (!r.ok) return;                       // the panel is a bonus — stay silent
      const reqs = Array.isArray(j.requests) ? j.requests : [];
      clear(box);
      if (!reqs.length) { box.classList.add('hidden'); return; }
      box.classList.remove('hidden');
      box.appendChild(el('div', { class: 'wpay-req-head' }, [
        el('i', { 'data-lucide': 'alert-circle' }),
        el('span', { text: reqs.length + (reqs.length > 1 ? ' demandes de suppression' : ' demande de suppression') }),
      ]));
      reqs.forEach(rq => box.appendChild(_wpayRequestRow(rq, box)));
      refreshIcons(box);
    } catch { /* never break the payments list over the review panel */ }
  }
  function _wpayRequestRow(rq, box) {
    const snap = rq.payment_snapshot || {};
    const amount = rq.payment_amount != null ? rq.payment_amount : snap.amount;
    const client = rq.payment_client_name || snap.client_name || '—';
    const agent = rq.payment_agent_id || snap.agent_id || '—';
    const decide = async (decision, btn) => {
      btn.disabled = true;
      try {
        const r = await api('/api/funnel/wallet/void-requests/' + encodeURIComponent(rq.id) + '/decide', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ decision }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
        toast(j.voided ? 'Paiement supprimé' : 'Demande refusée', 'success');
        DASH.lastPayments = null;
        renderWalletPayments(await _loadWalletPayments());
        if (j.voided) refreshObjectifActive();
      } catch (err) {
        toast(err.message || 'Erreur', 'error');
        btn.disabled = false;
      }
    };
    const yes = el('button', { class: 'btn btn-sm btn-danger', type: 'button', text: 'Oui, supprimer' });
    const no = el('button', { class: 'btn btn-sm btn-ghost', type: 'button', text: 'Non' });
    yes.addEventListener('click', () => decide('approve', yes));
    no.addEventListener('click', () => decide('reject', no));
    return el('div', { class: 'wpay-req-row' }, [
      el('div', { class: 'wpay-req-info' }, [
        el('strong', { text: fmtDH(amount) + ' · ' + client }),
        el('span', { class: 'wpay-req-meta', text: 'encaissé par ' + userLabel(agent) + ' · demande de ' + userLabel(rq.requested_by || '?') }),
        el('span', { class: 'wpay-req-reason', text: rq.reason || '' }),
      ]),
      el('div', { class: 'wpay-req-actions' }, [yes, no]),
    ]);
  }

  // ---- Search + "add a missing payment" toolbar ---------------------------
  // Ayoub (2026-08-01): "zidhom search bach yl9aw hadak li ki9elbo 3lih direct"
  // — 92 rows is far past the point where scrolling is the answer. Filtering is
  // client-side over the month already loaded, so it is instant and the footer
  // count/total follow the filter (that total is what agents check).
  const _wpayNorm = (s) => String(s == null ? '' : s)
    .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  // A hand-typed payment is the ONE kind nobody had to approve — the Paid
  // transition and the HubSpot import both come from a real event. Ayoub
  // (2026-08-05) asked to be able to tell them apart on sight, so the row
  // carries a badge saying who typed it and when.
  function _wpayManualBadge(p) {
    const who = userLabel(p.recordedBy || p.agent_id || '');
    const when = p.recordedAt ? new Date(p.recordedAt) : null;
    const title = 'Ajouté à la main'
      + (who ? ' par ' + who : '')
      + (when && !isNaN(when) ? ' le ' + MA.loc(when, 'd', 'fr-FR', { day: '2-digit', month: 'short' }) : '');
    return el('span', { class: 'wpay-manual-badge', text: 'Manuel', title });
  }
  const _wpayManualCount = (data) =>
    (Array.isArray(data?.payments) ? data.payments : []).filter(p => p.isManual).length;

  function _wpayToolbar(data, onAdded) {
    const search = el('input', {
      class: 'input wpay-search', type: 'search', autocomplete: 'off',
      placeholder: 'Rechercher un client, un téléphone, un pack…',
      'aria-label': 'Rechercher dans les paiements',
    });
    const addBtn = el('button', { class: 'btn btn-sm wpay-add-btn', type: 'button' }, [
      el('i', { 'data-lucide': 'plus' }), el('span', { text: 'Ajouter un paiement' }),
    ]);
    const panelHost = el('div');
    addBtn.addEventListener('click', () => {
      if (panelHost.firstChild) { clear(panelHost); return; }   // toggle
      clear(panelHost);
      panelHost.appendChild(_wpayAddForm(data, onAdded, () => clear(panelHost)));
      refreshIcons(panelHost);
      panelHost.querySelector('input')?.focus();
    });
    // One click to "show me only what the team typed in by hand" — scrolling
    // 184 rows hunting for a badge is not an answer. Hidden when there are none.
    const manualN = _wpayManualCount(data);
    const kids = [search];
    if (manualN) {
      const chip = el('button', {
        class: 'btn btn-sm wpay-manual-chip', type: 'button',
        title: 'Ne montrer que les paiements ajoutés à la main',
      }, [
        el('i', { 'data-lucide': 'pencil-line' }),
        el('span', { text: manualN + ' ajout' + (manualN > 1 ? 's' : '') + ' à la main' }),
      ]);
      chip.addEventListener('click', () => {
        const on = chip.classList.toggle('is-on');
        search.value = on ? 'manuel' : '';
        search.dispatchEvent(new Event('input'));
      });
      kids.push(chip);
    }
    kids.push(addBtn);
    return { bar: el('div', { class: 'wpay-toolbar' }, kids), search, panelHost };
  }

  // The add form. The client comes from the SAME global search the app already
  // uses, so a payment lands on the real lead/HubSpot contact instead of a
  // loose name — but free text still works for someone not in the system yet.
  function _wpayAddForm(data, onAdded, close) {
    let picked = null;                       // { name, phone, leadId, hubspotContactId }
    const today = MA.loc(new Date(), 'd', 'en-CA');
    const clientIn = el('input', { class: 'input', type: 'text', placeholder: 'Client — nom ou téléphone', autocomplete: 'off' });
    const results = el('div', { class: 'wpay-add-results hidden' });
    const chip = el('div', { class: 'wpay-add-chip hidden' });
    const amountIn = el('input', { class: 'input wpay-add-amount', type: 'number', min: '1', step: '50', placeholder: 'Montant (DH)' });
    const dateIn = el('input', { class: 'input wpay-add-date', type: 'date', value: today, max: today });
    const packList = [...new Set((data.payments || []).map(p => p.pack).filter(Boolean))].slice(0, 30);
    const packIn = el('input', { class: 'input', type: 'text', placeholder: 'Pack (optionnel)', list: 'wpayPackList', autocomplete: 'off' });
    const datalist = el('datalist', { id: 'wpayPackList' }, packList.map(p => el('option', { value: p })));
    // Required: how the client paid — it decides where SwufiPay looks for it.
    const modeSel = paymentModeSelect('', 'wpay-add-mode');
    // Admin only: which collector the money belongs to (it feeds their board).
    const agents = [...new Set((data.payments || []).map(p => p.agent_id).filter(Boolean))].sort();
    const agentSel = data.scope === 'team'
      ? el('select', { class: 'input wpay-add-agent' }, agents.map(a => el('option', { value: a, text: a })))
      : null;
    const err = el('div', { class: 'wpay-add-err hidden' });
    const save = el('button', { class: 'btn btn-sm btn-primary', type: 'button', text: 'Enregistrer' });

    const setPicked = (r) => {
      // Agents see MASKED phones ("4916****77") — storing that string would put
      // a mangled number in the ledger. Keep the identifiers instead; the server
      // resolves the real phone from the lead.
      const masked = /[*•]/.test(String(r.phone || ''));
      picked = { ...r, phone: masked ? '' : r.phone, maskedPhone: masked ? r.phone : null };
      r = picked;
      clear(chip);
      chip.classList.remove('hidden');
      const shownPhone = r.phone || r.maskedPhone || '';
      chip.appendChild(el('span', { text: (r.name || shownPhone) + (shownPhone ? ' · ' + shownPhone : '') }));
      chip.appendChild(el('button', {
        class: 'wpay-add-chip-x', type: 'button', text: '×', 'aria-label': 'Changer de client',
        onclick: () => { picked = null; chip.classList.add('hidden'); clientIn.value = ''; clientIn.focus(); },
      }));
      results.classList.add('hidden');
      clientIn.value = '';
    };
    let timer = null;
    clientIn.addEventListener('input', () => {
      clearTimeout(timer);
      const q = clientIn.value.trim();
      if (q.length < 2) { results.classList.add('hidden'); return; }
      timer = setTimeout(async () => {
        try {
          const r = await api('/api/search/global?q=' + encodeURIComponent(q));
          const j = await r.json().catch(() => ({}));
          const list = (j.results || []).filter(x => x.source !== 'call').slice(0, 6);
          clear(results);
          if (!list.length) { results.classList.add('hidden'); return; }
          list.forEach(x => results.appendChild(el('button', {
            class: 'wpay-add-result', type: 'button',
            onclick: () => setPicked(x),
          }, [
            el('strong', { text: x.name || x.phone || '—' }),
            el('span', { text: [x.phone, x.context].filter(Boolean).join(' · ') }),
          ])));
          results.classList.remove('hidden');
        } catch { results.classList.add('hidden'); }
      }, 260);
    });

    const submit = async (allowDuplicate) => {
      err.classList.add('hidden');
      const typed = clientIn.value.trim();
      const clientName = picked ? (picked.name || '') : typed.replace(/[\d\s+]/g, '').trim();
      const clientPhone = picked ? (picked.phone || '') : typed.replace(/\D/g, '');
      if (!clientName && !clientPhone) { err.textContent = 'Choisis un client (ou tape son nom).'; err.classList.remove('hidden'); return; }
      const amount = Number(amountIn.value);
      if (!Number.isFinite(amount) || amount <= 0) { err.textContent = 'Montant invalide.'; err.classList.remove('hidden'); return; }
      if (!modeSel.value) { err.textContent = 'Choisis le mode de paiement.'; err.classList.remove('hidden'); modeSel.focus(); return; }
      save.disabled = true;
      try {
        const r = await api('/api/funnel/wallet/payments', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount, pack: packIn.value.trim() || null, paidOn: dateIn.value,
            mode_de_paiment: modeSel.value,
            clientName, clientPhone,
            leadId: picked?.leadId || null, hubspotContactId: picked?.hubspotContactId || null,
            agentId: agentSel ? agentSel.value : undefined,
            allowDuplicate: !!allowDuplicate,
          }),
        });
        const j = await r.json().catch(() => ({}));
        if (r.status === 409 && j.code === 'DUPLICATE') {
          // A client CAN pay twice in a day — ask instead of blocking.
          err.textContent = j.error + ' — cliquer à nouveau pour l\'enregistrer quand même.';
          err.classList.remove('hidden');
          save.disabled = false;
          save.textContent = 'Enregistrer quand même';
          save.onclick = () => submit(true);
          return;
        }
        if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
        // 202 = it went to administration instead of into the ledger. Say so
        // plainly: an agent who thinks the money is counted will add it twice.
        toast(j.requested
          ? (j.joinedExisting
              ? 'Demande déjà envoyée — l’administration doit la valider'
              : 'Demande envoyée — l’administration doit la valider')
          : 'Paiement ajouté — Objectif mis à jour', 'success');
        close();
        onAdded();
      } catch (e) {
        err.textContent = e.message || 'Erreur';
        err.classList.remove('hidden');
        save.disabled = false;
      }
    };
    save.addEventListener('click', () => submit(false));

    return el('div', { class: 'wpay-add-panel' }, [
      el('div', { class: 'wpay-add-row' }, [
        el('div', { class: 'wpay-add-client' }, [chip, clientIn, results]),
        amountIn, dateIn, modeSel, packIn, datalist, agentSel, save,
        el('button', { class: 'btn btn-sm btn-ghost', type: 'button', text: 'Annuler', onclick: close }),
      ]),
      err,
    ]);
  }

  function renderWalletPayments(data) {
    const box = document.getElementById('wpayContent');
    if (!box) return;
    clear(box);
    const title = document.getElementById('wpayTitle');
    if (title) title.textContent = (data.scope === 'self' ? 'Mes paiements · ' : 'Paiements · ') + _objMonthLabel(data.month);
    const pays = Array.isArray(data.payments) ? data.payments : [];
    const reload = async () => {
      DASH.lastPayments = null;
      renderWalletPayments(await _loadWalletPayments());
      refreshObjectifActive();
    };
    const tools = _wpayToolbar(data, reload);
    box.appendChild(tools.bar);
    box.appendChild(tools.panelHost);
    // The queues render BEFORE the empty check on purpose: a month with no
    // payments is exactly when someone files a request to add the one that is
    // missing, and that request must not disappear behind "Aucun paiement".
    // Admins see both queues; an agent sees only their own pending adds.
    if (data.canDecideVoids) box.appendChild(_wpayVoidRequestsPanel());
    box.appendChild(_wpayAddRequestsPanel(reload));
    if (!pays.length) {
      box.appendChild(el('div', { class: 'wpay-empty', text: 'Aucun paiement ce mois-ci.' }));
      refreshIcons(box);
      return;
    }
    const isTeam = data.scope === 'team';
    const thead = el('thead', {}, [el('tr', {}, [
      el('th', { text: '#' }),
      el('th', { text: 'Client' }),
      el('th', { text: 'Téléphone' }),
      el('th', { text: 'Pack' }),
      el('th', { class: 'wpay-num', text: 'Montant' }),
      el('th', { text: 'Date' }),
      ...(isTeam ? [el('th', { text: 'Agent' })] : []),
      el('th', { class: 'wpay-act-h', text: '' }),
    ])]);
    const tbody = el('tbody');
    const rowsMeta = [];
    let total = 0;
    pays.forEach((p, i) => {
      total += Number(p.amount) || 0;
      const digits = String(p.clientPhone || '').replace(/[^\d]/g, '');
      const nameCell = el('td', { class: 'wpay-client' });
      if (digits.length >= 9) {
        nameCell.appendChild(el('button', {
          class: 'wpay-client-link',
          type: 'button',
          title: 'Ouvrir la conversation',
          onclick: () => { closeModal('walletPaymentsModal'); openInboxForPhone(digits, p.clientName || ''); },
        }, [p.clientName || digits]));
      } else {
        nameCell.appendChild(el('span', { class: 'wpay-client-name', text: p.clientName || '—' }));
      }
      // The name is wrapped (link OR span) so the cell can go flex here: a long
      // client name must truncate and leave the badge standing, not the reverse
      // — a clipped badge defeats the whole point of flagging the row.
      if (p.isManual) {
        nameCell.classList.add('wpay-client-flagged');
        nameCell.appendChild(_wpayManualBadge(p));
      }
      const d = p.paid_at ? new Date(p.paid_at) : null;
      const row = el('tr', {}, [
        el('td', { class: 'wpay-idx', text: String(i + 1) }),
        nameCell,
        el('td', { class: 'wpay-phone', text: digits || '—' }),
        el('td', { class: 'wpay-pack', text: p.pack || '—' }),
        el('td', { class: 'wpay-num', text: fmtDH(p.amount) }),
        el('td', { class: 'wpay-date', text: d ? MA.loc(d, 'd', 'fr-FR', { day: '2-digit', month: 'short' }) : '—' }),
        ...(isTeam ? [el('td', { class: 'wpay-agent', text: userLabel(p.agent_id, p.agentName || p.agent_id || '—') })] : []),
        _wpayActionCell(p, isTeam),
      ]);
      tbody.appendChild(row);
      rowsMeta.push({
        tr: row, amount: Number(p.amount) || 0,
        hay: _wpayNorm([p.clientName, digits, p.pack, p.agentName, p.agent_id,
          p.isManual ? 'manuel manual ajout' : ''].filter(Boolean).join(' ')),
      });
    });
    const table = el('table', { class: 'wpay-table' }, [thead, tbody]);
    box.appendChild(el('div', { class: 'wpay-scroll' }, [table]));
    const countEl = el('span', { text: pays.length + ' paiement' + (pays.length > 1 ? 's' : '') });
    const totalEl = el('strong', { class: 'wpay-total', text: 'Total : ' + fmtDH(total) });
    box.appendChild(el('div', { class: 'wpay-foot' }, [countEl, totalEl]));
    const empty = el('div', { class: 'wpay-empty hidden', text: 'Aucun paiement ne correspond à cette recherche.' });
    box.appendChild(empty);

    // Filtering keeps the footer honest: the count says "x sur y" and the total
    // is the total of what is ON SCREEN, which is what agents actually read.
    const applyFilter = () => {
      const raw = tools.search.value.trim();
      const q = _wpayNorm(raw);
      const qDigits = raw.replace(/\D/g, '');
      let shown = 0, sum = 0;
      for (const m of rowsMeta) {
        const hit = !q || m.hay.includes(q) || (qDigits.length >= 3 && m.hay.includes(qDigits));
        m.tr.style.display = hit ? '' : 'none';
        const form = m.tr.nextElementSibling;
        if (form && form.classList.contains('wpay-void-form')) form.style.display = hit ? '' : 'none';
        if (hit) { shown++; sum += m.amount; }
      }
      countEl.textContent = shown + ' paiement' + (shown > 1 ? 's' : '') + (q ? ' sur ' + rowsMeta.length : '');
      totalEl.textContent = 'Total : ' + fmtDH(sum);
      empty.classList.toggle('hidden', shown > 0);
    };
    tools.search.addEventListener('input', applyFilter);
    refreshIcons(box);
  }

  // ---- Support tab — super-admin board for the support / back-office collectors
  // (everyone outside the sales roster). Same concept as the sales Objectif:
  // Monthly = support objective + progress + classement + side stats; Weekly =
  // the Mon–Sun board behind the week picker (no tiers — support has no bonus
  // scheme). Both end with the inline payments detail. Nav is
  // data-super-admin-only; every endpoint is isSuperAdmin-gated server-side
  // (401/403 handled gracefully so a stray call never crashes the dashboard).
  // The period toggle / month stepper / week picker / inline objective edit are
  // the Objectif helpers, parametrised by these contexts (DASH.others* state).
  const _OTHERS_PERIOD_CTX = {
    periodKey: 'othersPeriod',
    onSwitch: (period) => {
      // Entering Weekly always starts on the live week; bust its cache.
      if (period === 'week') { DASH.othersWeek = null; DASH.othersWeekMonth = null; DASH.lastOthersWeek = null; }
      refreshOthers();
    },
  };
  const _OTHERS_MONTH_CTX = {
    monthKey: 'othersMonth',
    go: (next, curMonth) => {
      DASH.othersMonth = (next === curMonth) ? null : next;
      DASH.lastOthers = null;          // fresh totals + count-up
      DASH.lastOthersPayments = null;  // detail list must match the shown month
      refreshOthers();
    },
  };
  const _OTHERS_WEEK_CTX = {
    weekKey: 'othersWeek', weekMonthKey: 'othersWeekMonth', cacheKey: 'lastOthersWeek',
    hostSel: '.obj-others-weeksel', refresh: () => refreshOthers(),
  };
  const _OTHERS_EDIT_CTX = {
    teamId: 'othersMetaObjective', agentId: 'othersAgentObjective',
    endpoint: '/api/funnel/objective/support', cacheKey: 'lastOthers',
    teamValueKey: 'configuredObjective',
    refresh: () => refreshOthers(),
    teamLabel: 'Objectif support :', agentLabel: 'Objectif / collecteur :',
  };
  function _othersCurrentMonth() {
    const now = new Date();
    return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
  }
  async function refreshOthers() {
    if (!state.me?.superAdmin) return;
    const body = document.getElementById('dashOthersBody');
    if (!body) return;
    const weekly = DASH.othersPeriod === 'week';
    const cacheKey = weekly ? 'lastOthersWeek' : 'lastOthers';
    if (!DASH[cacheKey]) {
      clear(body);
      const wrap = el('div', { class: 'obj-wrap' });
      wrap.appendChild(_objPeriodToggle(_OTHERS_PERIOD_CTX));
      wrap.appendChild(el('div', { class: 'dash-loading', text: 'Chargement des encaissements support…' }));
      body.appendChild(wrap);
    }
    try {
      // DASH.othersMonth ('YYYY-MM') / DASH.othersWeek (Monday YMD) browse a past
      // period; null = current (matches the backend defaults).
      const url = weekly
        ? '/api/funnel/wallet/others/weekly' + (DASH.othersWeek ? ('?week=' + encodeURIComponent(DASH.othersWeek)) : '')
        : '/api/funnel/wallet/others' + (DASH.othersMonth ? ('?month=' + encodeURIComponent(DASH.othersMonth)) : '');
      const r = await api(url);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      const prevTotal = DASH[cacheKey] ? Number(DASH[cacheKey].total) || 0 : 0;
      DASH[cacheKey] = j;
      renderOthers(j, prevTotal);
    } catch (err) {
      clear(body);
      const wrap = el('div', { class: 'obj-wrap' });
      wrap.appendChild(_objPeriodToggle(_OTHERS_PERIOD_CTX));
      wrap.appendChild(el('div', { class: 'obj-error' }, [
        el('div', { class: 'obj-error-text', text: 'Impossible de charger les encaissements support — ' + (err.message || err) }),
        el('button', { class: 'btn btn-sm', type: 'button', text: 'Réessayer', onclick: () => { DASH[cacheKey] = null; refreshOthers(); } }),
      ]));
      body.appendChild(wrap);
    }
  }
  function renderOthers(data, prevTotal) {
    const body = document.getElementById('dashOthersBody');
    if (!body) return;
    // Stop any in-flight count-up before its node gets detached.
    const oldNum = body.querySelector('.obj-amount-num');
    if (oldNum && oldNum._objRaf) cancelAnimationFrame(oldNum._objRaf);
    clear(body);
    const weekly = !!data.weekStart;
    const collectors = Array.isArray(data.collectors) ? data.collectors : [];
    const total = Number(data.total) || 0;
    const paymentsCount = Number(data.paymentsCount) || 0;
    const restTotal = Number(data.restTotal) || 0;
    // Objectives are MONTHLY only (the weekly board has no target).
    const objective = weekly ? 0 : (Number(data.objective) || 0);
    const agentObjective = weekly ? 0 : (Number(data.agentObjective) || 0);
    const pct = objective > 0 ? Math.min(100, (total / objective) * 100) : 0;
    const pctLabel = objective > 0 ? Math.round((total / objective) * 100) : 0;
    const reste = Math.max(0, objective - total);
    const reached = objective > 0 && total >= objective;
    const isCurrentWeek = weekly && data.weekStart === _currentMondayYMD();
    const periodTxt = weekly ? 'cette semaine' : 'ce mois';

    const wrap = el('div', { class: 'obj-wrap' });
    wrap.appendChild(_objPeriodToggle(_OTHERS_PERIOD_CTX));
    // Period picker — month stepper (current + previous) or the week filter.
    // The weekly host class is load-bearing: the week picker re-renders itself
    // into it when its month chevrons are used.
    const nav = el('div', {
      class: 'obj-month-nav' + (weekly ? ' obj-others-weeksel' : ''),
      style: 'display:flex;justify-content:center;align-items:center;gap:8px;flex-wrap:wrap;margin:10px 0 2px;',
    });
    nav.appendChild(weekly ? _objWeekSelector(data, _OTHERS_WEEK_CTX) : _objMonthSelector(data, _OTHERS_MONTH_CTX));
    wrap.appendChild(nav);

    // ----- Hero: support total (+ objective bar on the monthly view) -----
    const hero = el('div', { class: 'obj-hero' + (reached ? ' is-reached' : '') });
    const heroLabel = 'Encaissements support · ' + (weekly
      ? (isCurrentWeek ? 'cette semaine' : _objWeekRangeFr(data.weekStart, data.weekEnd))
      : _objMonthLabel(data.month));
    const head = el('div', { class: 'obj-hero-head' }, [el('div', { class: 'obj-month', text: heroLabel })]);
    if (!weekly && _isSystemAdmin()) {
      head.appendChild(el('button', {
        class: 'obj-edit-btn icon-btn-sm',
        type: 'button',
        title: "Modifier l'objectif support",
        'aria-label': "Modifier l'objectif support",
        onclick: () => _objStartEdit('team', _OTHERS_EDIT_CTX),
      }));
    }
    hero.appendChild(head);
    const num = el('span', { class: 'obj-amount-num', text: '0' });
    num._objVal = prevTotal || 0;
    hero.appendChild(el('div', { class: 'obj-amount' }, [num, el('span', { class: 'obj-amount-cur', text: 'DH' })]));
    const restSplit = () => {
      const split = el('span', { class: 'obj-progress-restsplit' });
      split.appendChild(el('span', { text: _objNum(total - restTotal) + ' paiements' }));
      split.appendChild(el('span', { class: 'obj-rest-accent', text: ' + ' + _objNum(restTotal) + ' rests' }));
      return split;
    };
    let fill = null;
    if (!weekly) {
      fill = el('div', { class: 'obj-progress-fill' + (reached ? ' is-done' : '') });
      const prevPct = objective > 0 ? Math.min(100, Math.max(0, ((prevTotal || 0) / objective) * 100)) : 0;
      fill.style.width = prevPct.toFixed(1) + '%';
      const meta = el('div', { class: 'obj-progress-meta' }, [
        el('strong', { class: 'obj-progress-pct', text: pctLabel + ' %' }),
        el('span', { class: 'obj-progress-obj', id: 'othersMetaObjective', text: 'Objectif support : ' + fmtDH(objective) }),
        el('span', {
          class: 'obj-progress-reste' + (reached ? ' is-done' : ''),
          text: reached ? 'Objectif atteint — Mbrouk l\'équipe support !' : 'reste ' + fmtDH(reste),
        }),
      ]);
      if (restTotal > 0) meta.appendChild(restSplit());
      hero.appendChild(el('div', { class: 'obj-progress' }, [el('div', { class: 'obj-progress-track' }, [fill]), meta]));
    } else {
      const meta = el('div', { class: 'obj-progress-meta', style: 'margin-top:8px;' }, [
        el('span', { class: 'obj-progress-obj', text: paymentsCount + ' paiement' + (paymentsCount > 1 ? 's' : '') + ' · hors équipe de vente' }),
      ]);
      if (restTotal > 0) meta.appendChild(restSplit());
      hero.appendChild(meta);
    }
    wrap.appendChild(hero);

    // ----- Board: collectors ranked (reuses the leaderboard .obj-row grid) -----
    const board = el('div', { class: 'obj-board' });
    const boardHead = el('div', { class: 'obj-board-head' }, [
      el('div', {}, [
        el('div', { class: 'obj-board-title', text: weekly ? 'Classement de la semaine' : 'Classement du mois' }),
        el('div', { class: 'obj-board-sub', text: 'Encaissements par collecteur · support / back-office' }),
      ]),
    ]);
    if (!weekly) {
      const objLine = el('div', { class: 'obj-board-objline', id: 'othersAgentObjective' }, [
        el('span', { class: 'obj-board-objtxt', text: 'Objectif / collecteur : ' + fmtDH(agentObjective) }),
      ]);
      if (_isSystemAdmin()) {
        objLine.appendChild(el('button', {
          class: 'obj-edit-btn obj-edit-btn-agent icon-btn-sm',
          type: 'button',
          title: "Modifier l'objectif par collecteur",
          'aria-label': "Modifier l'objectif par collecteur",
          onclick: () => _objStartEdit('agent', _OTHERS_EDIT_CTX),
        }));
      }
      boardHead.appendChild(objLine);
    }
    board.appendChild(boardHead);
    if (!collectors.length) {
      const empty = el('div', { class: 'obj-board-empty' });
      const ic = el('div', { class: 'obj-board-empty-ic' });
      ic.innerHTML = '<i data-lucide="hand-coins"></i>';
      empty.appendChild(ic);
      empty.appendChild(el('div', { class: 'obj-board-empty-txt', text: 'Aucun encaissement hors équipe ' + periodTxt + '.' }));
      board.appendChild(empty);
    } else {
      collectors.forEach((c, idx) => {
        const cTotal = Number(c.total) || 0;
        const isTop = idx === 0 && cTotal > 0;
        const row = el('div', { class: 'obj-row' + (isTop ? ' is-top' : '') });
        row.appendChild(el('div', { class: 'obj-rank' + (idx < 3 ? ' is-podium' : ''), text: String(idx + 1) }));
        row.appendChild(el('div', {
          class: 'avatar obj-avatar ' + avatarColorClass(c.agent_id || c.displayName || String(idx)),
          text: initials(userLabel(c.agent_id, c.displayName || c.agent_id || '?')),
        }));
        const nameKids = [];
        if (isTop) {
          const crown = el('span', { class: 'obj-crown' });
          crown.innerHTML = '<i data-lucide="crown"></i>';
          nameKids.push(crown);
        }
        nameKids.push(el('span', { text: userLabel(c.agent_id, c.displayName || c.agent_id || '—') }));
        row.appendChild(el('div', { class: 'obj-name' }, nameKids));
        // The count opens that collector's payments for the shown month/week
        // (monthly AND weekly rows — `data` carries month or weekStart).
        const cCount = c.payments_count || 0;
        row.appendChild(el('div', { class: 'obj-chips' }, [
          el('button', {
            class: 'obj-paid-count obj-paid-count-btn',
            type: 'button',
            title: 'Voir les paiements',
            'aria-label': 'Voir les ' + cCount + ' paiement' + (cCount > 1 ? 's' : '') + ' de ' + userLabel(c.agent_id, c.displayName || c.agent_id || 'ce collecteur'),
            text: cCount + ' paiement' + (cCount > 1 ? 's' : ''),
            onclick: () => _othersOpenCollectorPayments(c, data),
          }),
        ]));
        const cRest = Number(c.rest_total) || 0;
        const cPaid = cTotal - cRest;
        const totalCell = el('div', { class: 'obj-total' });
        totalCell.appendChild(el('div', { class: 'obj-total-num', text: fmtDH(cTotal) }));
        const sub = el('div', { class: 'obj-total-split' });
        sub.appendChild(el('span', { class: 'obj-total-paid' }, [
          el('span', { class: 'obj-total-dot' }),
          el('span', { text: _objNum(cPaid) + ' payé' }),
        ]));
        if (cRest > 0) {
          sub.appendChild(el('span', { class: 'obj-total-rest' }, [
            el('span', { class: 'obj-total-dot' }),
            el('span', { text: '+ ' + _objNum(cRest) + ' reste' }),
          ]));
        }
        totalCell.appendChild(sub);
        row.appendChild(totalCell);
        // Per-collector progress vs the flat support objective (monthly only).
        if (agentObjective > 0) {
          const cPct = c.objPct != null ? Number(c.objPct) : Math.min(100, (cTotal / agentObjective) * 100);
          const cDone = c.objReached != null ? !!c.objReached : cTotal >= agentObjective;
          const rowFill = el('div', { class: 'obj-row-fill' + (cDone ? ' is-done' : '') });
          rowFill.style.width = Math.min(100, cPct).toFixed(1) + '%';
          row.appendChild(el('div', { class: 'obj-row-progress' }, [
            el('div', { class: 'obj-row-track' }, [rowFill]),
            el('span', { class: 'obj-row-pct' + (cDone ? ' is-done' : ''), text: Math.round(cPct) + ' %' }),
          ]));
        }
        board.appendChild(row);
      });
    }

    // ----- Side stats -----
    const side = el('div', { class: 'obj-side' });
    const avg = paymentsCount > 0 ? total / paymentsCount : 0;
    let daysLeft = null;
    if (weekly) {
      // Live week only: days left through Sunday, today included (Mon → 7).
      if (isCurrentWeek) { const dow = new Date().getDay(); daysLeft = dow === 0 ? 1 : 8 - dow; }
    } else {
      daysLeft = _objDaysLeft(data.month);
    }
    const activeCollectors = collectors.filter(c => (Number(c.total) || 0) > 0).length;
    const stats = [
      { label: weekly ? 'Paiements cette semaine' : 'Paiements ce mois', val: String(paymentsCount) },
      { label: 'Panier moyen', val: avg > 0 ? fmtDH(avg) : '—' },
      { label: 'Jours restants', val: daysLeft == null ? '—' : String(daysLeft) },
      { label: 'Collecteurs actifs', val: String(activeCollectors) },
    ];
    if (objective > 0) {
      stats.push({ label: reached ? 'Objectif dépassé de' : "Reste à l'objectif", val: fmtDH(reached ? total - objective : reste), cls: reached ? ' obj-stat-collected' : ' obj-stat-owed' });
    }
    for (const s of stats) {
      side.appendChild(el('div', { class: 'obj-stat' }, [
        el('div', { class: 'obj-stat-num' + (s.cls || ''), text: s.val }),
        el('div', { class: 'obj-stat-label', text: s.label }),
      ]));
    }
    const payLink = el('button', {
      class: 'obj-side-link', type: 'button',
      onclick: () => { const d = document.getElementById('othersPaymentsDetail'); if (d) d.scrollIntoView({ behavior: 'smooth', block: 'start' }); },
    });
    payLink.innerHTML = '<span>Détail des paiements</span><i data-lucide="receipt"></i>';
    side.appendChild(payLink);
    const salesLink = el('button', { class: 'obj-side-link', type: 'button', onclick: () => dashSwitchTab('objectif') });
    salesLink.innerHTML = '<span>Voir l\'objectif équipe de vente</span><i data-lucide="arrow-right"></i>';
    side.appendChild(salesLink);

    wrap.appendChild(el('div', { class: 'obj-grid' }, [board, side]));

    // ----- Détail des paiements — inline list (reuses the .wpay-* table) -----
    const pays = el('div', { class: 'obj-board', id: 'othersPaymentsDetail' });
    pays.appendChild(el('div', { class: 'obj-board-head' }, [
      el('div', {}, [
        el('div', { class: 'obj-board-title', text: 'Détail des paiements' }),
        el('div', { class: 'obj-board-sub', text: 'Encaissements hors équipe ' + periodTxt }),
      ]),
    ]));
    const listBox = el('div', { style: 'padding:12px 16px 16px;' });
    if (collectors.length) {
      listBox.appendChild(el('div', { class: 'dash-loading', text: 'Chargement…' }));
      _loadOthersPayments(listBox, data);
    } else {
      listBox.appendChild(el('div', { class: 'wpay-empty', text: 'Aucun paiement hors équipe ' + periodTxt + '.' }));
    }
    pays.appendChild(listBox);
    wrap.appendChild(pays);

    body.appendChild(wrap);
    refreshIcons(body);

    // Animations after paint: count-up + progress sweep.
    animateCountUp(num, total);
    if (fill) requestAnimationFrame(() => { fill.style.width = pct.toFixed(1) + '%'; });
  }
  async function _loadOthersPayments(listBox, data) {
    try {
      const url = data && data.weekStart
        ? '/api/funnel/wallet/others/weekly/payments?week=' + encodeURIComponent(data.weekStart)
        : '/api/funnel/wallet/others/payments?month=' + encodeURIComponent((data && data.month) || _othersCurrentMonth());
      const r = await api(url);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      DASH.lastOthersPayments = j;
      _renderOthersPayments(listBox, j);
    } catch (err) {
      clear(listBox);
      listBox.appendChild(el('div', { class: 'wpay-empty', text: 'Impossible de charger le détail — ' + (err.message || err) }));
    }
  }
  function _renderOthersPayments(listBox, data) {
    clear(listBox);
    const pays = Array.isArray(data.payments) ? data.payments : [];
    if (!pays.length) {
      listBox.appendChild(el('div', { class: 'wpay-empty', text: 'Aucun paiement hors équipe sur cette période.' }));
      return;
    }
    const thead = el('thead', {}, [el('tr', {}, [
      el('th', { text: '#' }),
      el('th', { text: 'Client' }),
      el('th', { text: 'Pack' }),
      el('th', { class: 'wpay-num', text: 'Montant' }),
      el('th', { text: 'Date' }),
      el('th', { text: 'Collecteur' }),
    ])]);
    const tbody = el('tbody');
    let total = 0;
    pays.forEach((p, i) => {
      total += Number(p.amount) || 0;
      const digits = String(p.clientPhone || '').replace(/[^\d]/g, '');
      const nameCell = el('td', { class: 'wpay-client' });
      if (digits.length >= 9) {
        nameCell.appendChild(el('button', {
          class: 'wpay-client-link',
          type: 'button',
          title: 'Ouvrir la conversation',
          onclick: () => { openInboxForPhone(digits, p.clientName || ''); },
        }, [p.clientName || digits]));
      } else {
        nameCell.appendChild(el('span', { class: 'wpay-client-name', text: p.clientName || '—' }));
      }
      // The name is wrapped (link OR span) so the cell can go flex here: a long
      // client name must truncate and leave the badge standing, not the reverse
      // — a clipped badge defeats the whole point of flagging the row.
      if (p.isManual) {
        nameCell.classList.add('wpay-client-flagged');
        nameCell.appendChild(_wpayManualBadge(p));
      }
      const d = p.paid_at ? new Date(p.paid_at) : null;
      tbody.appendChild(el('tr', {}, [
        el('td', { class: 'wpay-idx', text: String(i + 1) }),
        nameCell,
        el('td', { class: 'wpay-pack', text: p.pack || '—' }),
        el('td', { class: 'wpay-num', text: fmtDH(p.amount) }),
        el('td', { class: 'wpay-date', text: d ? MA.loc(d, 'd', 'fr-FR', { day: '2-digit', month: 'short' }) : '—' }),
        el('td', { class: 'wpay-agent', text: userLabel(p.agent_id, p.agentName || p.agent_id || '—') }),
      ]));
    });
    listBox.appendChild(el('div', { class: 'wpay-scroll' }, [el('table', { class: 'wpay-table' }, [thead, tbody])]));
    listBox.appendChild(el('div', { class: 'wpay-foot' }, [
      el('span', { text: pays.length + ' paiement' + (pays.length > 1 ? 's' : '') }),
      el('strong', { class: 'wpay-total', text: 'Total : ' + fmtDH(total) }),
    ]));
  }

  // ---- Support board — ONE collector's payments (popup behind "N paiements") --
  // Ayoub (2026-09-17): the count on a collector row opens the payments that
  // make it up, for the month / week the board is showing. Super admins
  // (ayoub + mmehanane — server-side isSuperAdmin, echoed as `canReassign`)
  // also get a per-row «Réassigner» that moves ONE payment to another
  // collector (POST /api/funnel/wallet/payments/:id/reassign). The server
  // stores it as an override the HubSpot payments sync never rewrites, and
  // re-checks the gate — the select is only the door, never the permission.
  // Same popup on the SALES Objectif monthly board (2026-09-17): `_OCP.board`
  // ('support' | 'sales') picks the list endpoint, the target order and which
  // board a move leaves; everything else is shared.
  const _OCP_MODAL_ID = 'othersCollectorPaymentsModal';
  const _OCP = { board: 'support', collector: null, period: null, seq: 0, movedOut: [] };

  function _ocpEnsureModal() {
    let modal = document.getElementById(_OCP_MODAL_ID);
    if (modal) return modal;
    // Built once and reused (forwardPickerModal pattern). Escape is handled by
    // the global "close the topmost open .modal" listener.
    modal = el('div', { id: _OCP_MODAL_ID, class: 'modal hidden', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'ocpTitle' });
    const backdrop = el('div', { class: 'modal-backdrop' });
    backdrop.addEventListener('click', () => closeModal(_OCP_MODAL_ID));
    const closeX = el('button', { class: 'icon-btn', type: 'button', 'aria-label': 'Fermer' }, [lucideIcon('x')]);
    closeX.addEventListener('click', () => closeModal(_OCP_MODAL_ID));
    modal.appendChild(backdrop);
    modal.appendChild(el('div', { class: 'modal-card modal-lg ocp-card' }, [
      el('header', { class: 'modal-head' }, [
        el('h2', { class: 'ocp-head' }, [lucideIcon('receipt'), el('span', { id: 'ocpTitle', class: 'ocp-title', text: 'Paiements' })]),
        closeX,
      ]),
      el('div', { class: 'modal-body' }, [el('div', { id: 'ocpContent' })]),
    ]));
    document.body.appendChild(modal);
    return modal;
  }

  function _ocpPeriodLabel(p) {
    return p && p.weekStart ? 'Semaine du ' + _objWeekRangeFr(p.weekStart, p.weekEnd) : _objMonthLabel(p && p.month);
  }

  function _othersOpenCollectorPayments(c, data) {
    _ocpOpen('support', c, data);
  }
  // Sales Objectif board — monthly only (the weekly challenge rows carry no
  // count pill), so the period is always the board's month.
  function _objOpenAgentPayments(a, data) {
    _ocpOpen('sales', a, { month: data && data.month });
  }

  function _ocpOpen(board, c, data) {
    if (!c) return;
    _ocpEnsureModal();
    _OCP.board = board === 'sales' ? 'sales' : 'support';
    _OCP.collector = { agent_id: String(c.agent_id || ''), displayName: c.displayName || c.agent_id || 'Non assigné' };
    _OCP.period = data && data.weekStart
      ? { weekStart: data.weekStart, weekEnd: data.weekEnd }
      : { month: (data && data.month) || _othersCurrentMonth() };
    _OCP.movedOut = [];
    const title = document.getElementById('ocpTitle');
    if (title) title.textContent = 'Paiements · ' + _OCP.collector.displayName + ' · ' + _ocpPeriodLabel(_OCP.period);
    openModal(_OCP_MODAL_ID);
    _ocpLoad();
  }

  async function _ocpLoad({ quiet = false } = {}) {
    const box = document.getElementById('ocpContent');
    if (!box || !_OCP.collector) return;
    const seq = ++_OCP.seq;
    if (!quiet) { clear(box); box.appendChild(el('div', { class: 'dash-loading', text: 'Chargement…' })); }
    const p = _OCP.period || {};
    const agentQs = '&agent=' + encodeURIComponent(_OCP.collector.agent_id);
    const url = _OCP.board === 'sales'
      ? '/api/funnel/wallet/payments?month=' + encodeURIComponent(p.month || _othersCurrentMonth()) + agentQs
      : p.weekStart
        ? '/api/funnel/wallet/others/weekly/payments?week=' + encodeURIComponent(p.weekStart) + agentQs
        : '/api/funnel/wallet/others/payments?month=' + encodeURIComponent(p.month || _othersCurrentMonth()) + agentQs;
    try {
      const r = await api(url);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      if (seq !== _OCP.seq) return;   // a newer load (other collector / a reassign) owns the box
      _ocpRender(box, j);
    } catch (err) {
      if (seq !== _OCP.seq) return;
      clear(box);
      box.appendChild(el('div', { class: 'obj-error' }, [
        el('div', { class: 'obj-error-text', text: 'Impossible de charger les paiements — ' + (err.message || err) }),
        el('button', { class: 'btn btn-sm', type: 'button', text: 'Réessayer', onclick: () => _ocpLoad() }),
      ]));
    }
  }

  // Same tranche test as the wallet SQL (payment_kind, then the legacy pack suffix).
  function _ocpIsRest(p) {
    const pack = String(p.pack || '');
    return p.paymentKind === 'rest' || /— rest$/.test(pack) || pack === 'Rest';
  }
  function _ocpSourceLabel(src) {
    if (src === 'hubspot') return 'HubSpot';
    if (src === 'manual') return 'Manuel';
    if (src === 'backfill') return 'Import';
    return 'App';
  }

  // Data columns of the popup table. `w` = default width (px); the viewer's
  // own drag-resized widths override it (localStorage, per browser).
  const _OCP_COLS = [
    { key: 'idx', label: '#', w: 40, resize: false },
    { key: 'client', label: 'Client', w: 180 },
    { key: 'phone', label: 'Téléphone', w: 124 },
    { key: 'pack', label: 'Pack', w: 112 },
    { key: 'kind', label: 'Type', w: 84 },
    { key: 'amount', label: 'Montant', w: 100, cls: 'wpay-num' },
    { key: 'date', label: 'Date', w: 76 },
    { key: 'source', label: 'Source', w: 88 },
    { key: 'owner', label: 'Owner du lead', w: 170 },
  ];
  const _OCP_COL_WIDTHS_KEY = 'ocp_col_widths_v1';
  const _OCP_COL_MIN = 56;
  const _OCP_COL_MAX = 600;
  function _ocpLoadColWidths() {
    try { return JSON.parse(localStorage.getItem(_OCP_COL_WIDTHS_KEY) || '{}') || {}; }
    catch { return {}; }
  }
  function _ocpSaveColWidth(key, px) {
    try {
      const map = _ocpLoadColWidths();
      if (px == null) delete map[key];
      else map[key] = Math.round(px);
      localStorage.setItem(_OCP_COL_WIDTHS_KEY, JSON.stringify(map));
    } catch { /* storage blocked — the width just won't persist */ }
  }
  // Drag the header's right edge to resize its <col>; double-click restores
  // the default width. Pointer events so trackpads and touch work too.
  function _ocpAttachColResize(th, col, def, onChange) {
    const handle = el('span', {
      class: 'col-resize-handle ocp-col-resize', 'aria-hidden': 'true',
      title: 'Glisser pour redimensionner · double-clic : largeur par défaut',
    });
    handle.addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const startX = ev.clientX;
      const startW = parseFloat(col.style.width) || def.w;
      handle.setPointerCapture(ev.pointerId);
      handle.classList.add('is-dragging');
      document.body.classList.add('col-resizing');
      const onMove = (e) => {
        const w = Math.max(_OCP_COL_MIN, Math.min(_OCP_COL_MAX, startW + (e.clientX - startX)));
        col.style.width = w + 'px';
        onChange();
      };
      const onUp = () => {
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onUp);
        handle.removeEventListener('pointercancel', onUp);
        handle.classList.remove('is-dragging');
        document.body.classList.remove('col-resizing');
        _ocpSaveColWidth(def.key, parseFloat(col.style.width) || def.w);
      };
      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', onUp);
      handle.addEventListener('pointercancel', onUp);
    });
    handle.addEventListener('click', (e) => e.stopPropagation());
    handle.addEventListener('dblclick', (e) => {
      e.preventDefault();
      e.stopPropagation();
      col.style.width = def.w + 'px';
      _ocpSaveColWidth(def.key, null);
      onChange();
    });
    th.appendChild(handle);
  }

  function _ocpRender(box, data) {
    clear(box);
    const pays = Array.isArray(data.payments) ? data.payments : [];
    const canReassign = !!data.canReassign;
    const targets = Array.isArray(data.reassignTargets) ? data.reassignTargets : [];
    if (_OCP.movedOut.length) box.appendChild(_ocpMovedOutPanel());
    if (!pays.length) {
      box.appendChild(el('div', { class: 'wpay-empty', text: 'Aucun paiement pour ' + _OCP.collector.displayName + ' sur cette période.' }));
      refreshIcons(box);
      return;
    }
    // Fixed layout driven by a <colgroup>: every data column is drag-resizable
    // from its header edge (Ayoub 2026-09-17 — "Owner du lead" was cut off
    // under the pinned action column). Widths persist per viewer.
    const widths = _ocpLoadColWidths();
    const actW = canReassign ? (window.matchMedia('(max-width: 1024px)').matches ? 232 : 262) : 0;
    const table = el('table', { class: 'wpay-table ocp-table' });
    const cols = _OCP_COLS.map(c => el('col', { 'data-col': c.key, style: 'width:' + (widths[c.key] || c.w) + 'px' }));
    const colgroup = el('colgroup', {}, [...cols, canReassign ? el('col', { style: 'width:' + actW + 'px' }) : null]);
    const syncTableWidth = () => {
      const sum = cols.reduce((s, col) => s + (parseFloat(col.style.width) || 0), 0) + actW;
      table.style.width = sum + 'px';
    };
    syncTableWidth();
    const thead = el('thead', {}, [el('tr', {}, [
      ..._OCP_COLS.map((c, i) => {
        const th = el('th', { class: c.cls || '', text: c.label, title: c.label });
        if (c.resize !== false) _ocpAttachColResize(th, cols[i], c, syncTableWidth);
        return th;
      }),
      canReassign ? el('th', { class: 'wpay-act-h ocp-act-h', text: 'Réassigner' }) : null,
    ])]);
    const tbody = el('tbody');
    let total = 0;
    pays.forEach((p, i) => {
      total += Number(p.amount) || 0;
      const digits = String(p.clientPhone || '').replace(/[^\d]/g, '');
      // Content sits in an inner flex wrapper: a display:flex <td> stops being
      // a table-cell and breaks the fixed column grid.
      const nameCell = el('td', { class: 'wpay-client' });
      const nameInner = el('div', { class: 'ocp-client-inner' });
      nameCell.appendChild(nameInner);
      if (digits.length >= 9) {
        nameInner.appendChild(el('button', {
          class: 'wpay-client-link', type: 'button', title: (p.clientName ? p.clientName + ' — ' : '') + 'Ouvrir la conversation',
          onclick: () => { closeModal(_OCP_MODAL_ID); openInboxForPhone(digits, p.clientName || ''); },
        }, [p.clientName || displayPhone(digits)]));
      } else {
        nameInner.appendChild(el('span', { class: 'wpay-client-name', text: p.clientName || '—', title: p.clientName || null }));
      }
      if (p.overridden) {
        nameInner.appendChild(el('span', {
          class: 'ocp-moved-badge', text: 'Réassigné',
          title: "Réassigné à la main — l'attribution HubSpot est ignorée pour ce paiement",
        }));
      }
      const d = p.paid_at ? new Date(p.paid_at) : null;
      const rest = _ocpIsRest(p);
      tbody.appendChild(el('tr', {}, [
        el('td', { class: 'wpay-idx', text: String(i + 1) }),
        nameCell,
        el('td', { class: 'wpay-phone', text: digits ? displayPhone(digits) : '—' }),
        el('td', { class: 'wpay-pack', text: p.pack || '—', title: p.pack || null }),
        el('td', {}, [el('span', { class: 'ocp-kind' + (rest ? ' is-rest' : ''), text: rest ? 'Reste' : 'Montant' })]),
        el('td', { class: 'wpay-num', text: fmtDH(p.amount) }),
        el('td', { class: 'wpay-date', text: d ? MA.loc(d, 'd', 'fr-FR', { day: '2-digit', month: 'short' }) : '—' }),
        el('td', { class: 'wpay-agent', text: _ocpSourceLabel(p.source) }),
        el('td', { class: 'wpay-agent', text: p.leadAgentName || '—', title: p.leadAgentName || 'Aucun lead Leads7 lié à ce paiement' }),
        canReassign ? _ocpActionCell(p, targets) : null,
      ]));
    });
    table.appendChild(colgroup);
    table.appendChild(thead);
    table.appendChild(tbody);
    box.appendChild(el('div', { class: 'wpay-scroll' }, [table]));
    box.appendChild(el('div', { class: 'wpay-foot' }, [
      el('span', { text: pays.length + ' paiement' + (pays.length > 1 ? 's' : '') }),
      el('strong', { class: 'wpay-total', text: 'Total : ' + fmtDH(total) }),
    ]));
    refreshIcons(box);
  }

  function _ocpActionCell(p, targets) {
    const td = el('td', { class: 'wpay-act ocp-act' });
    const current = String(p.agent_id || '');
    const sel = el('select', { class: 'input ocp-select', 'aria-label': 'Nouveau collecteur pour ce paiement' });
    sel.appendChild(el('option', { value: '', text: 'Choisir…' }));
    // The popup's own board first; the warning flags a move OFF that board.
    const onSales = _OCP.board === 'sales';
    const groups = onSales ? [['sales', 'Sales'], ['others', 'Support']] : [['others', 'Support'], ['sales', 'Sales']];
    for (const [board, label] of groups) {
      const og = el('optgroup', { label });
      for (const t of targets) {
        if (t.board === board && t.id !== current) og.appendChild(el('option', { value: t.id, text: t.name }));
      }
      if (og.children.length) sel.appendChild(og);
    }
    if (p.overridden) sel.appendChild(el('option', { value: '__auto__', text: '↺ Auto (HubSpot)' }));
    const btn = el('button', { class: 'btn btn-sm btn-primary ocp-go', type: 'button', text: 'Réassigner' });
    btn.disabled = true;
    const warnBoard = onSales ? 'others' : 'sales';
    const warn = el('div', {
      class: 'ocp-warn hidden', role: 'note',
      text: onSales
        ? 'Ce paiement quittera le board Sales et ne comptera plus dans la commission.'
        : 'Ce paiement passera au board Sales et comptera dans sa commission.',
    });
    sel.addEventListener('change', () => {
      const t = targets.find(x => x.id === sel.value);
      btn.disabled = !sel.value;
      warn.classList.toggle('hidden', !(t && t.board === warnBoard));
    });
    btn.addEventListener('click', () => {
      if (!sel.value) return;
      _ocpReassign(p, sel.value === '__auto__' ? null : sel.value, { btn, sel, targets });
    });
    td.appendChild(el('div', { class: 'ocp-act-row' }, [sel, btn]));
    td.appendChild(warn);
    return td;
  }

  async function _ocpPostReassign(paymentId, agentId) {
    const r = await api('/api/funnel/wallet/payments/' + encodeURIComponent(paymentId) + '/reassign', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentId: agentId || null }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
    return j;
  }

  // Every board that sums payments may have moved: the Support board, its
  // detail list and the sales Objectif caches — busted on BOTH boards. Only
  // the board under the popup refetches now (both bodies sit in the DOM, the
  // other hidden); the other one refetches when its tab opens.
  function _ocpAfterChange() {
    DASH.lastOthers = null;
    DASH.lastOthersWeek = null;
    DASH.lastOthersPayments = null;
    DASH.lastPayments = null;
    DASH.lastWallet = null;
    DASH.lastWalletWeek = null;
    _ocpLoad({ quiet: true });
    if (_OCP.board === 'sales') refreshObjectifActive();
    else refreshOthers();
  }

  async function _ocpReassign(p, agentId, ui) {
    const { btn, sel, targets } = ui;
    const collectorAtStart = _OCP.collector;
    const label = btn.textContent;
    btn.disabled = true; sel.disabled = true;
    btn.textContent = 'Enregistrement…';
    try {
      const j = await _ocpPostReassign(p.id, agentId);
      const target = agentId ? targets.find(t => t.id === agentId) : null;
      const toName = j.agentName || (target && target.name) || j.agentId || '—';
      toast(agentId ? ('Paiement réassigné à ' + toName) : ('Attribution HubSpot rétablie — ' + toName), 'success');
      // The row leaves this collector's list — keep it reachable here so a
      // wrong pick (above all onto the Sales board) can be undone on the spot.
      if (_OCP.collector === collectorAtStart && String(j.agentId || '') !== collectorAtStart.agent_id) {
        _OCP.movedOut.unshift({
          payment: p, toName, toBoard: j.board,
          previousAgentId: j.previousAgentId, previousOverridden: !!j.previousOverridden,
        });
      }
      _ocpAfterChange();
    } catch (err) {
      toast('Réassignation impossible — ' + (err.message || err), 'error');
      btn.disabled = false; sel.disabled = false; btn.textContent = label;
    }
  }

  function _ocpMovedOutPanel() {
    const panel = el('div', { class: 'ocp-moved' }, [
      el('div', { class: 'ocp-moved-head', text: 'Réassignés depuis cette fenêtre' }),
    ]);
    // Name the destination board only when the payment left the popup's own.
    const homeBoard = _OCP.board === 'sales' ? 'sales' : 'others';
    for (const m of _OCP.movedOut) {
      const undo = el('button', { class: 'btn btn-sm btn-ghost', type: 'button', text: 'Annuler' });
      undo.addEventListener('click', () => _ocpUndo(m, undo));
      const boardTag = m.toBoard && m.toBoard !== homeBoard ? (m.toBoard === 'sales' ? ' (Sales)' : ' (Support)') : '';
      panel.appendChild(el('div', { class: 'ocp-moved-row' }, [
        el('span', {
          class: 'ocp-moved-txt',
          text: fmtDH(m.payment.amount) + ' · ' + (m.payment.clientName || '—') + ' → ' + m.toName + boardTag,
        }),
        undo,
      ]));
    }
    return panel;
  }

  // Put the payment back exactly as it was: re-pin the previous collector if
  // it was pinned, otherwise drop the pin (back to the HubSpot / app owner).
  async function _ocpUndo(m, btn) {
    btn.disabled = true;
    try {
      await _ocpPostReassign(m.payment.id, m.previousOverridden ? m.previousAgentId : null);
      _OCP.movedOut = _OCP.movedOut.filter(x => x !== m);
      toast('Réassignation annulée', 'success');
      _ocpAfterChange();
    } catch (err) {
      toast('Annulation impossible — ' + (err.message || err), 'error');
      btn.disabled = false;
    }
  }

  // Inline objective edit — admin / super_sales only (button is gated above;
  // the server 403s anyone else anyway). kind: 'team' (hero objective) or
  // 'agent' (the personal per-agent objective shown in the board head).
  // ctx (optional): { teamId, agentId, endpoint, cacheKey, refresh, teamLabel,
  // agentLabel } re-targets the editor at another board (the Support tab's
  // objectives). Default = the sales Objectif.
  function _objStartEdit(kind = 'team', ctx) {
    const c = ctx || {
      teamId: 'objMetaObjective', agentId: 'objAgentObjective',
      endpoint: '/api/funnel/objective', cacheKey: 'lastWallet',
      refresh: () => refreshObjectif(),
      teamLabel: 'Objectif :', agentLabel: 'Objectif / agent :',
      // The team target shown can be the 700 000 milestone; the editor edits
      // the configured base (500 000).
      teamValueKey: 'configuredObjective',
    };
    const isAgent = kind === 'agent';
    const span = document.getElementById(isAgent ? c.agentId : c.teamId);
    if (!span || span.dataset.editing === '1') return;
    span.dataset.editing = '1';
    const current = DASH[c.cacheKey] ? Number(DASH[c.cacheKey][isAgent ? 'agentObjective' : (c.teamValueKey || 'objective')]) || 0 : 0;
    clear(span);
    const input = el('input', { class: 'input obj-edit-input', type: 'number', min: '1', step: '1000' });
    input.value = String(current);
    const ok = el('button', { class: 'obj-edit-ok icon-btn-sm', type: 'button', 'aria-label': 'Valider' });
    ok.innerHTML = '<i data-lucide="check"></i>';
    const cancel = el('button', { class: 'obj-edit-cancel icon-btn-sm', type: 'button', 'aria-label': 'Annuler' });
    cancel.innerHTML = '<i data-lucide="x"></i>';
    const done = () => { span.dataset.editing = ''; c.refresh(); };
    const save = async () => {
      const v = Number(input.value);
      if (!Number.isFinite(v) || v <= 0) { input.classList.add('is-invalid'); return; }
      ok.disabled = true; cancel.disabled = true;
      try {
        const r = await api(c.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(isAgent ? { agentObjective: v } : { objective: v }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) { toast(j.error || 'Échec de la mise à jour', 'error'); done(); return; }
        toast((isAgent ? c.agentLabel : c.teamLabel).replace(/\s*:$/, '') + ' mis à jour : ' + fmtDH(isAgent ? j.agentObjective : j.objective), 'success');
        DASH[c.cacheKey] = null;   // force fresh totals + pct
        done();
      } catch (_) { toast('Erreur réseau', 'error'); done(); }
    };
    ok.addEventListener('click', save);
    cancel.addEventListener('click', done);
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') save();
      else if (e.key === 'Escape') done();
    });
    span.appendChild(el('span', { class: 'obj-edit-label', text: isAgent ? c.agentLabel : c.teamLabel }));
    span.appendChild(input);
    span.appendChild(ok);
    span.appendChild(cancel);
    refreshIcons(span);
    input.focus();
    input.select();
  }

  // ─── Salary / commission gauge (system-admin only) ───────────────────────
  // A milestone bar for one agent: the agent's commission this month plotted
  // against the configured salary tiers. topSalary (last tier) is the 100 %
  // anchor; each tier drops a tick at salary/topSalary; the fill colour tracks
  // the highest reached tier. No "%" is shown anywhere — only DH amounts.
  // Tier colour → app token map (index-based, never hardcoded hex):
  //   0 Start = --lougha · 1 Good = --success · 2 Top = --accent ·
  //   3+ Champion = --warning (the gold/amber tier). Each entry carries the
  //   solid colour, its soft fill, and a CSS-class key for per-tier styling.
  const _OBJ_TIER_TOKENS = [
    { key: 'start', c: 'var(--lougha)',  soft: 'var(--lougha-soft)' },
    { key: 'good',  c: 'var(--success)', soft: 'var(--success-soft)' },
    { key: 'top',   c: 'var(--accent)',  soft: 'var(--accent-soft)' },
    { key: 'champ', c: 'var(--warning)', soft: 'var(--warning-soft)' },
  ];
  function _objTierTok(i) { return _OBJ_TIER_TOKENS[Math.max(0, Math.min(_OBJ_TIER_TOKENS.length - 1, i))]; }

  // Inline medal SVGs (NO emoji), one consistent badge family — a filled disc
  // with an escalating emblem per tier. `big` renders the 40px headline medal
  // (gradient disc); small (22px) sits inside each milestone node.
  function _objMedalSvg(i, big) {
    const tok = _objTierTok(i);
    // Escalating emblem: spark → shield → star → crown.
    const emblem = [
      '<path d="M12 5.2l1.7 4.1 4.4.35-3.4 2.85 1.1 4.3L12 18.6l-3.8 2.4 1.1-4.3-3.4-2.85 4.4-.35z"/>',
      '<path d="M12 5l5 1.9v3.5c0 3.2-2.1 5.6-5 6.6-2.9-1-5-3.4-5-6.6V6.9z"/>',
      '<path d="M12 4.4l2.1 4.7 5.1.45-3.85 3.4 1.16 5L12 15.6l-4.62 2.75 1.16-5L4.7 9.55l5.1-.45z"/>',
      '<path d="M4.5 8.2l3.6 3.3L12 5.4l3.9 6.1 3.6-3.3-1.5 9.4H6z"/><rect x="6" y="18.4" width="12" height="1.9" rx=".9"/>',
    ][Math.max(0, Math.min(3, i))];
    if (big) {
      // Headline medal — gradient disc in the tier colour with a white emblem.
      return '<svg class="obj-sg-medal-svg" viewBox="0 0 40 40" aria-hidden="true" focusable="false">'
        + '<circle cx="20" cy="20" r="18" fill="' + tok.c + '"/>'
        + '<circle cx="20" cy="20" r="18" fill="none" stroke="#fff" stroke-opacity=".4" stroke-width="1.5"/>'
        + '<g transform="translate(8 8) scale(1)" fill="#fff" stroke="none">' + emblem + '</g>'
        + '</svg>';
    }
    // Small node emblem — strokes/fills inherit currentColor via the node class.
    return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor" stroke="none">' + emblem + '</svg>';
  }

  function _objSalaryGauge(a, cfg) {
    const tiers = Array.isArray(cfg.tiers) ? cfg.tiers.slice() : [];
    // Keep tiers ascending by salary so the nodes lay out left→right.
    tiers.sort((x, y) => (Number(x.salary) || 0) - (Number(y.salary) || 0));
    const commission = Number(a.commission) || 0;
    // One-off bonuses (server-sent, same privacy scope as the commission).
    // With a bonus the headline + the whole gauge measure the SALARY TOTAL
    // (commission + bonus) — the server already ranks the tiers on it.
    const bonuses = Array.isArray(a.salaryBonuses) ? a.salaryBonuses : [];
    const hasBonus = bonuses.length > 0;
    const salary = !hasBonus ? commission
      : (Number.isFinite(Number(a.salaryTotal))
        ? Number(a.salaryTotal)
        : commission + bonuses.reduce((sum, b) => sum + (Number(b.amount) || 0), 0));
    const tierIdx = Number.isFinite(Number(a.commissionTier)) ? Number(a.commissionTier) : -1;
    const lastSalary = tiers.length ? (Number(tiers[tiers.length - 1].salary) || 0) : 0;
    const curTok = _objTierTok(tierIdx < 0 ? 0 : tierIdx);
    const isChamp = tiers.length > 0 && tierIdx >= tiers.length - 1;
    // Past the top tier (Champion) the gauge re-anchors on the commission
    // objective the server sends (15 000 DH) instead of stopping at the max.
    const stretch = isChamp && Number(a.commissionStretch) > lastSalary ? Number(a.commissionStretch) : 0;
    const topSalary = stretch || lastSalary;
    const fillPct = topSalary > 0 ? Math.min(100, (salary / topSalary) * 100) : 0;

    const block = el('div', { class: 'obj-sg' + (isChamp ? ' is-champ' : '') });

    // ── Headline row: medal + commission amount + tier badge | next-tier CTA ──
    const head = el('div', { class: 'obj-sg-head' });

    const left = el('div', { class: 'obj-sg-headline' });
    const medal = el('div', { class: 'obj-sg-medal' });
    medal.innerHTML = _objMedalSvg(tierIdx < 0 ? 0 : tierIdx, true);
    left.appendChild(medal);

    const amtCol = el('div', { class: 'obj-sg-amt-col' });
    amtCol.appendChild(el('div', { class: 'obj-sg-cap', text: hasBonus ? 'Salaire total ce mois' : 'Commission ce mois' }));
    amtCol.appendChild(el('div', { class: 'obj-sg-amt' }, [
      el('span', { class: 'obj-sg-amt-num', text: _objNum(salary) }),
      el('span', { class: 'obj-sg-amt-cur', text: 'DH' }),
    ]));
    const badge = el('span', { class: 'obj-sg-badge is-' + curTok.key });
    if (a.commissionTierLabel || tierIdx >= 0) {
      badge.textContent = a.commissionTierLabel || (tiers[tierIdx] && tiers[tierIdx].label) || 'Palier';
    } else {
      // Below tier 1 → invite to the first tier.
      badge.classList.remove('is-' + curTok.key);
      badge.classList.add('is-pending');
      badge.textContent = 'Bientôt ' + ((tiers[0] && tiers[0].label) || 'Start');
    }
    amtCol.appendChild(el('div', { class: 'obj-sg-badge-row' }, [badge]));
    left.appendChild(amtCol);
    head.appendChild(left);

    // Next-tier CTA — "Plus que {gap} DH" / "pour {label}", or champion state.
    const cta = el('div', { class: 'obj-sg-cta' });
    if (a.commissionNextSalary != null) {
      const gap = Math.max(0, Number(a.commissionNextSalary) - salary);
      const nextTok = _objTierTok(tierIdx + 1);
      cta.appendChild(el('div', { class: 'obj-sg-cta-main' }, [
        el('span', { text: 'Plus que ' }),
        el('b', { class: 'obj-sg-cta-gap', text: _objNum(gap) + ' DH' }),
      ]));
      if (stretch) {
        cta.appendChild(el('div', { class: 'obj-sg-cta-sub' }, [
          el('span', { text: 'pour l\'objectif ' }),
          el('b', { class: 'obj-sg-cta-next is-champ', text: _objNum(stretch) + ' DH' }),
        ]));
      } else if (a.commissionNextLabel) {
        const nextB = el('b', { class: 'obj-sg-cta-next is-' + nextTok.key, text: a.commissionNextLabel });
        cta.appendChild(el('div', { class: 'obj-sg-cta-sub' }, [
          el('span', { text: 'pour ' }), nextB,
        ]));
      }
    } else if (stretch) {
      cta.classList.add('is-max');
      cta.appendChild(el('div', { class: 'obj-sg-cta-main', text: 'Objectif atteint' }));
      cta.appendChild(el('div', { class: 'obj-sg-cta-sub', text: _objNum(stretch) + ' DH' }));
    } else {
      cta.classList.add('is-max');
      cta.appendChild(el('div', { class: 'obj-sg-cta-main', text: 'Champion atteint' }));
      cta.appendChild(el('div', { class: 'obj-sg-cta-sub', text: 'palier maximum' }));
    }
    head.appendChild(cta);
    block.appendChild(head);

    // ── Journey gauge: track + gradient fill + one milestone node per tier ──
    const journey = el('div', { class: 'obj-sg-journey' });
    const track = el('div', { class: 'obj-sg-track' });

    const fill = el('div', { class: 'obj-sg-fill' });
    // Gradient runs from the start colour to the current tier colour; the
    // round end-marker inherits the current colour via `color`.
    fill.style.background = 'linear-gradient(90deg, var(--lougha), ' + curTok.c + ')';
    fill.style.color = curTok.c;
    fill.style.width = '0%';            // animate to fillPct after paint
    track.appendChild(fill);

    const nodesWrap = el('div', { class: 'obj-sg-nodes' });
    tiers.forEach((t, i) => {
      const sal = Number(t.salary) || 0;
      const pos = topSalary > 0 ? Math.min(100, (sal / topSalary) * 100) : 0;
      const reached = tierIdx >= i;
      // The next node = first unreached (or the very first when below tier 1).
      const isNext = !reached && (tierIdx === i - 1 || (tierIdx < 0 && i === 0));
      const tok = _objTierTok(i);
      const node = el('div', {
        class: 'obj-sg-node r-' + tok.key
          + (reached ? ' is-reached' : '')
          + (isNext ? ' is-next' : ''),
      });
      node.style.left = pos.toFixed(2) + '%';
      // Per-node colour drives reached fill, the next-pulse, and label tint.
      node.style.setProperty('--sg-c', tok.c);
      node.style.setProperty('--sg-soft', tok.soft);
      const dot = el('div', { class: 'obj-sg-dot' });
      dot.innerHTML = _objMedalSvg(i, false);
      node.appendChild(dot);
      node.appendChild(el('div', { class: 'obj-sg-node-lbl' }, [
        el('span', { class: 'obj-sg-node-amt', text: _objNum(sal) }),
        t.label ? el('span', { class: 'obj-sg-node-name', text: t.label }) : null,
      ].filter(Boolean)));
      nodesWrap.appendChild(node);
    });
    if (stretch) {
      // The objective past Champion — its own node at the 100 % anchor.
      const reached = salary >= stretch;
      const tok = _objTierTok(3);
      const node = el('div', {
        class: 'obj-sg-node r-' + tok.key + ' is-stretch' + (reached ? ' is-reached' : ' is-next'),
      });
      node.style.left = '100%';
      node.style.setProperty('--sg-c', tok.c);
      node.style.setProperty('--sg-soft', tok.soft);
      const dot = el('div', { class: 'obj-sg-dot' });
      dot.innerHTML = _objMedalSvg(3, false);
      node.appendChild(dot);
      node.appendChild(el('div', { class: 'obj-sg-node-lbl' }, [
        el('span', { class: 'obj-sg-node-amt', text: _objNum(stretch) }),
        el('span', { class: 'obj-sg-node-name', text: 'Objectif' }),
      ]));
      nodesWrap.appendChild(node);
    }
    track.appendChild(nodesWrap);
    journey.appendChild(track);
    block.appendChild(journey);

    // Salary breakdown under the gauge: commission + each bonus = total.
    if (hasBonus) {
      const extra = el('div', { class: 'obj-sg-bonus' });
      extra.appendChild(el('div', { class: 'obj-sg-bonus-row is-base' }, [
        el('span', { class: 'obj-sg-bonus-lbl', text: 'Commission' }),
        el('span', { class: 'obj-sg-bonus-amt', text: _objNum(commission) + ' DH' }),
      ]));
      for (const b of bonuses) {
        extra.appendChild(el('div', { class: 'obj-sg-bonus-row' }, [
          el('span', { class: 'obj-sg-bonus-lbl', text: '+ ' + (b.label || 'Bonus') }),
          el('span', { class: 'obj-sg-bonus-amt', text: _objNum(Number(b.amount) || 0) + ' DH' }),
        ]));
      }
      extra.appendChild(el('div', { class: 'obj-sg-bonus-row is-total' }, [
        el('span', { class: 'obj-sg-bonus-lbl', text: 'Salaire total' }),
        el('span', { class: 'obj-sg-bonus-amt', text: _objNum(salary) + ' DH' }),
      ]));
      block.appendChild(extra);
    }

    // Sweep the fill once it's in the DOM (CSS drops the transition under
    // prefers-reduced-motion, so the bar simply appears at its width).
    requestAnimationFrame(() => { fill.style.width = fillPct.toFixed(2) + '%'; });
    return block;
  }

  // Commission config editor — system-admin only. Rates are entered as PERCENT
  // (6 / 5 / 4 / 3) and converted to decimals on POST; thresholds are DH.
  // Two base slices (rateFirst up to `threshold` DH of revenue, then rateRest)
  // followed by an open ladder of `breaks`: each says "once the agent has EARNED
  // this much commission, drop to this rate". Tiers are {salary,label} rows.
  // Reuses #objTiersModal-style markup at #objCommissionModal.
  // `_objLadderSlices` below MIRRORS ladderSlices() in lib/leads/wallet.js — the
  // two are kept in step by test/commission-ladder.test.js, which evaluates this
  // very source against the server's. Change one, change both.
  // MIRROR of ladderSlices()/_revenueForCommission() in lib/leads/wallet.js.
  // Kept in step by test/commission-ladder.test.js, which evaluates this source
  // against the server's on a battery of configs.
  function _objLadderSlices(threshold, rateFirst, rateRest, breaks) {
    const slices = [{ from: 0, rate: rateFirst }, { from: threshold, rate: rateRest }];
    for (const b of (Array.isArray(breaks) ? breaks : [])) {
      let acc = 0, at = Infinity;
      for (let i = 0; i < slices.length; i++) {
        const from = slices[i].from;
        const to = i + 1 < slices.length ? slices[i + 1].from : Infinity;
        const rate = slices[i].rate;
        const pays = rate > 0 ? (to - from) * rate : 0;
        if (rate > 0 && acc + pays >= Number(b.atCommission)) { at = from + (Number(b.atCommission) - acc) / rate; break; }
        acc += pays;
      }
      if (!Number.isFinite(at)) break;
      while (slices.length > 1 && slices[slices.length - 1].from >= at) slices.pop();
      slices.push({ from: at, rate: Number(b.rate) });
    }
    return slices;
  }

  function _objEditCommission() {
    if (!_isSystemAdmin()) return;
    const cfg = (DASH.lastWallet && DASH.lastWallet.commissionConfig) || {};
    const box = document.getElementById('objCommissionContent');
    if (!box) return;
    clear(box);

    // ── Rates section ──
    const rateWrap = el('div', { class: 'obj-comm-rates' });
    const threshIn = el('input', { class: 'input obj-edit-input', type: 'number', min: '0', step: '500', placeholder: 'DH' });
    threshIn.value = cfg.threshold != null ? String(cfg.threshold) : '';
    // Stored as decimal (0.06) → shown as percent (6).
    const pctOf = (v) => (v == null || v === '') ? '' : String(Math.round(Number(v) * 1000) / 10);
    const rateFirstIn = el('input', { class: 'input obj-edit-input', type: 'number', min: '0', step: '0.5', placeholder: '%' });
    rateFirstIn.value = pctOf(cfg.rateFirst);
    const rateRestIn = el('input', { class: 'input obj-edit-input', type: 'number', min: '0', step: '0.5', placeholder: '%' });
    rateRestIn.value = pctOf(cfg.rateRest);
    rateWrap.appendChild(el('label', { class: 'obj-tiers-field' }, [
      el('span', { class: 'obj-tiers-flabel', text: 'Seuil (DH)' }), threshIn,
    ]));
    rateWrap.appendChild(el('label', { class: 'obj-tiers-field' }, [
      el('span', { class: 'obj-tiers-flabel', text: 'Taux 1er (%)' }), rateFirstIn,
    ]));
    rateWrap.appendChild(el('label', { class: 'obj-tiers-field' }, [
      el('span', { class: 'obj-tiers-flabel', text: 'Taux 2e (%)' }), rateRestIn,
    ]));
    box.appendChild(rateWrap);

    // ── Break ladder: "à partir de X DH de commission → Y %" ──
    box.appendChild(el('div', { class: 'obj-comm-tiers-title', text: 'Bascules de taux' }));
    const breakRows = el('div', { class: 'obj-tiers-rows' });
    const breakInputs = [];
    const curBreaks = Array.isArray(cfg.breaks) && cfg.breaks.length
      ? cfg.breaks
      // A payload from before the ladder existed carried a single pair.
      : (cfg.breakCommission != null ? [{ atCommission: cfg.breakCommission, rate: cfg.rateAfter }] : []);
    const MAX_BREAKS = 6;
    for (let i = 0; i < MAX_BREAKS; i++) {
      const b = curBreaks[i] || {};
      const atIn = el('input', { class: 'input obj-edit-input', type: 'number', min: '0', step: '500', placeholder: 'DH' });
      atIn.value = b.atCommission != null ? String(b.atCommission) : '';
      const rIn = el('input', { class: 'input obj-edit-input', type: 'number', min: '0', step: '0.5', placeholder: '%' });
      rIn.value = pctOf(b.rate);
      breakInputs.push([atIn, rIn]);
      breakRows.appendChild(el('div', { class: 'obj-tiers-row' }, [
        el('span', { class: 'obj-tiers-rownum', text: 'À partir de' }),
        el('label', { class: 'obj-tiers-field' }, [el('span', { class: 'obj-tiers-flabel', text: 'Commission (DH)' }), atIn]),
        el('span', { class: 'obj-challenge-tier-arrow', text: '→' }),
        el('label', { class: 'obj-tiers-field' }, [el('span', { class: 'obj-tiers-flabel', text: 'Taux' }), rIn]),
      ]));
    }
    box.appendChild(breakRows);

    // Live plain-French restatement of the whole ladder, so the admin never has
    // to convert "bascule à 5 000 DH de commission" into a revenue figure.
    const ruleHint = el('div', { class: 'obj-comm-hint' });
    const _renderHint = () => {
      const th = Number(threshIn.value);
      const r1 = Number(rateFirstIn.value) / 100;
      const r2 = Number(rateRestIn.value) / 100;
      if (![th, r1, r2].every(v => Number.isFinite(v) && v > 0)) { ruleHint.textContent = ''; return; }
      const brs = [];
      for (const [a, r] of breakInputs) {
        if (a.value === '' && r.value === '') continue;
        const at = Number(a.value), rate = Number(r.value) / 100;
        if (!Number.isFinite(at) || at <= 0 || !Number.isFinite(rate) || rate <= 0 || rate > 1) { ruleHint.textContent = ''; return; }
        brs.push({ atCommission: at, rate });
      }
      // Same sort + dedupe the server applies, so the sentence under Ayoub's
      // eyes is the one the server would actually pay.
      brs.sort((x, y) => x.atCommission - y.atCommission);
      const deduped = brs.filter((b, i) => i === 0 || b.atCommission !== brs[i - 1].atCommission);
      brs.length = 0;
      brs.push(...deduped);
      const slices = _objLadderSlices(th, r1, r2, brs);
      // Rates keep their decimal (the inputs step by 0.5) — _objNum rounds to a
      // whole number, which would show 6.5 % as "7 %" on a payroll screen.
      const pct = (v) => String(Math.round(v * 1000) / 10);
      const parts = slices.map((s, i) => {
        const to = i + 1 < slices.length ? slices[i + 1].from : null;
        return to == null
          ? `${pct(s.rate * 100)} % au-delà`
          : `${pct(s.rate * 100)} % jusqu'à ${_objNum(Math.round(to))} DH`;
      });
      const earned = brs.map(b => `${_objNum(b.atCommission)} DH de commission`).join(', ');
      ruleHint.textContent = parts.join(' · ') + '.'
        + (earned ? ` Les bascules tombent à ${earned}.` : '');
    };
    [threshIn, rateFirstIn, rateRestIn].forEach(i => i.addEventListener('input', _renderHint));
    breakInputs.forEach(([a, r]) => { a.addEventListener('input', _renderHint); r.addEventListener('input', _renderHint); });
    _renderHint();
    box.appendChild(ruleHint);
    // The recruitment bot quotes these rates verbatim to candidates on WhatsApp
    // (lib/recruitment/knowledge.js). Its guard test pins the copy to the rates
    // in the CODE, which stops being the truth the moment this editor is used —
    // so say it here, where the change is being made.
    box.appendChild(el('div', { class: 'obj-comm-hint obj-comm-warn' }, [
      el('strong', { text: '⚠ ' }),
      el('span', { text: 'Le bot de recrutement annonce ces taux aux candidats. Si tu les changes ici, mets aussi à jour Recrutement → Connaissances.' }),
    ]));

    // ── Tier rows (salary + label) ──
    box.appendChild(el('div', { class: 'obj-comm-tiers-title', text: 'Paliers de salaire' }));
    const rowsWrap = el('div', { class: 'obj-tiers-rows' });
    const curTiers = Array.isArray(cfg.tiers) ? cfg.tiers : [];
    const tierInputs = [];
    for (let i = 0; i < 4; i++) {
      const t = curTiers[i] || {};
      const salIn = el('input', { class: 'input obj-edit-input', type: 'number', min: '0', step: '500', placeholder: 'Salaire DH' });
      salIn.value = t.salary != null ? String(t.salary) : '';
      const labelIn = el('input', { class: 'input obj-edit-input', type: 'text', maxlength: '24', placeholder: 'Label (ex. Good)' });
      labelIn.value = t.label != null ? String(t.label) : '';
      tierInputs.push([salIn, labelIn]);
      rowsWrap.appendChild(el('div', { class: 'obj-tiers-row' }, [
        el('span', { class: 'obj-tiers-rownum', text: 'Palier ' + (i + 1) }),
        el('label', { class: 'obj-tiers-field' }, [el('span', { class: 'obj-tiers-flabel', text: 'Salaire' }), salIn]),
        el('label', { class: 'obj-tiers-field' }, [el('span', { class: 'obj-tiers-flabel', text: 'Label' }), labelIn]),
      ]));
    }
    box.appendChild(rowsWrap);

    const err = el('div', { class: 'obj-tiers-err hidden' });
    box.appendChild(err);
    const foot = el('div', { class: 'obj-tiers-foot' });
    const cancel = el('button', { class: 'btn btn-sm', type: 'button', text: 'Annuler', onclick: () => closeModal('objCommissionModal') });
    const saveBtn = el('button', { class: 'btn btn-sm btn-primary', type: 'button', text: 'Enregistrer' });
    saveBtn.addEventListener('click', async () => {
      const threshold = Number(threshIn.value);
      const rateFirstPct = Number(rateFirstIn.value);
      const rateRestPct = Number(rateRestIn.value);
      if (![threshold, rateFirstPct, rateRestPct].every(v => Number.isFinite(v) && v > 0)) {
        err.textContent = 'Seuil et taux doivent être des nombres > 0.';
        err.classList.remove('hidden');
        return;
      }
      const breaks = [];
      for (const [a, r] of breakInputs) {
        if (a.value === '' && r.value === '') continue;        // skip blank rows
        const at = Number(a.value), rPct = Number(r.value);
        if (!Number.isFinite(at) || at <= 0 || !Number.isFinite(rPct) || rPct <= 0) {
          err.textContent = 'Chaque bascule remplie doit avoir un montant et un taux > 0.';
          err.classList.remove('hidden');
          return;
        }
        breaks.push({ atCommission: at, rate: Math.round((rPct / 100) * 10000) / 10000 });
      }
      breaks.sort((x, y) => x.atCommission - y.atCommission);
      const tiers = [];
      for (const [s, l] of tierInputs) {
        if (s.value === '' && l.value.trim() === '') continue;   // skip blank rows
        const sv = Number(s.value);
        if (!Number.isFinite(sv) || sv < 0) {
          err.textContent = 'Chaque palier rempli doit avoir un salaire valide (≥ 0).';
          err.classList.remove('hidden');
          return;
        }
        tiers.push({ salary: sv, label: l.value.trim() });
      }
      if (!tiers.length) {
        err.textContent = 'Renseigne au moins un palier.';
        err.classList.remove('hidden');
        return;
      }
      tiers.sort((a, b) => a.salary - b.salary);
      err.classList.add('hidden');
      saveBtn.disabled = true; cancel.disabled = true;
      try {
        const r = await api('/api/funnel/commission-config', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            threshold,
            rateFirst: Math.round((rateFirstPct / 100) * 10000) / 10000,   // % → decimal
            rateRest: Math.round((rateRestPct / 100) * 10000) / 10000,
            breaks,
            tiers,
          }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) { err.textContent = j.error || 'Échec de la mise à jour'; err.classList.remove('hidden'); saveBtn.disabled = false; cancel.disabled = false; return; }
        toast('Commissions mises à jour', 'success');
        closeModal('objCommissionModal');
        DASH.lastWallet = null;
        refreshObjectifActive();
      } catch (_) {
        err.textContent = 'Erreur réseau';
        err.classList.remove('hidden');
        saveBtn.disabled = false; cancel.disabled = false;
      }
    });
    foot.appendChild(cancel);
    foot.appendChild(saveBtn);
    box.appendChild(foot);
    refreshIcons(box);
    openModal('objCommissionModal');
  }

  // ─── Hebdo week math (Monday-anchored, Casablanca, mirrors the backend) ──
  // The backend's weekMondayCasablanca() does NOT snap to Monday — it trusts
  // the YMD verbatim — so the CLIENT must send the exact Monday of the chosen
  // week. All dates anchored at UTC-noon to dodge any DST/offset edge.
  const _OBJ_MOIS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
  function _objYmdToUtcNoon(ymd) {
    const [Y, M, D] = String(ymd).split('-').map(Number);
    return new Date(Date.UTC(Y, M - 1, D, 12, 0, 0));
  }
  function _objYmd(d) { return d.toISOString().slice(0, 10); }
  // Monday (YMD) of the week containing the given YMD.
  function _mondayOf(ymd) {
    const d = _objYmdToUtcNoon(ymd);
    const back = (d.getUTCDay() + 6) % 7;   // Mon→0 .. Sun→6
    d.setUTCDate(d.getUTCDate() - back);
    return _objYmd(d);
  }
  // Monday of the CURRENT week in Casablanca — matches the backend default.
  function _currentMondayYMD() {
    const today = MA.dayKey(); // YYYY-MM-DD
    return _mondayOf(today);
  }
  // Casablanca 'YYYY-MM' of the current month (for the stepper default).
  function _objCurrentMonthYM() {
    const t = MA.dayKey();
    return t.slice(0, 7);
  }
  function _objAddMonths(ym, delta) {
    const [Y, M] = String(ym).split('-').map(Number);
    const d = new Date(Date.UTC(Y, M - 1 + delta, 1, 12, 0, 0));
    return d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0');
  }
  // French "Mois AAAA" label for the stepper.
  function _objMonthLabelFr(ym) {
    const [Y, M] = String(ym).split('-').map(Number);
    const s = _OBJ_MOIS_FR[M - 1] || '';
    return (s.charAt(0).toUpperCase() + s.slice(1)) + ' ' + Y;
  }
  // Short FR Mon–Sun range, e.g. "8–14 juin" / "29 juin – 5 juil." — used in
  // the hero label when a non-current week is chosen.
  function _objWeekRangeFr(weekStart, weekEnd) {
    if (!weekStart) return 'cette semaine';
    try {
      const mon = _objYmdToUtcNoon(weekStart);
      const sun = weekEnd ? _objYmdToUtcNoon(weekEnd) : (() => { const d = new Date(mon); d.setUTCDate(d.getUTCDate() + 6); return d; })();
      const dM = mon.getUTCDate(), dS = sun.getUTCDate();
      const moM = _OBJ_MOIS_FR[mon.getUTCMonth()], moS = _OBJ_MOIS_FR[sun.getUTCMonth()];
      return (mon.getUTCMonth() === sun.getUTCMonth())
        ? (dM + '–' + dS + ' ' + moM)
        : (dM + ' ' + moM + ' – ' + dS + ' ' + moS);
    } catch (_) { return 'cette semaine'; }
  }
  // Weeks of a month — a week BELONGS to the month containing its Monday
  // (mirrors the backend, which keys each week by its Monday). Returns
  // [{ n, monday: 'YYYY-MM-DD', label: 'Semaine N · 8–14 juin' }]. The range
  // shows both months when the Mon..Sun span crosses a month boundary.
  function _weeksOfMonth(year, month /* 1-12 */) {
    const first = new Date(Date.UTC(year, month - 1, 1, 12, 0, 0));
    const last = new Date(Date.UTC(year, month, 0, 12, 0, 0)); // day 0 of next month
    let mon = _objYmdToUtcNoon(_mondayOf(_objYmd(first)));
    // First Monday whose date is within [first, last]: if the 1st isn't a
    // Monday, its week's Monday falls in the previous month → skip it.
    if (mon.getTime() < first.getTime()) mon.setUTCDate(mon.getUTCDate() + 7);
    const out = [];
    let n = 1;
    while (mon.getTime() <= last.getTime()) {
      const sun = new Date(mon); sun.setUTCDate(sun.getUTCDate() + 6);
      const dM = mon.getUTCDate(), dS = sun.getUTCDate();
      const moM = _OBJ_MOIS_FR[mon.getUTCMonth()], moS = _OBJ_MOIS_FR[sun.getUTCMonth()];
      const range = (mon.getUTCMonth() === sun.getUTCMonth())
        ? (dM + '–' + dS + ' ' + moM)
        : (dM + ' ' + moM + ' – ' + dS + ' ' + moS);
      out.push({ n, monday: _objYmd(mon), label: 'Semaine ' + n + ' · ' + range });
      mon.setUTCDate(mon.getUTCDate() + 7);
      n++;
    }
    return out;
  }

  // The compact week filter that sits on the `obj-challenge-sub` line: a month
  // stepper [‹] «Juin 2026» [›] + a week <select>. Future weeks (Monday after
  // the current week's Monday) are omitted. Selecting a week sets DASH.objWeek
  // (null when it's the current week), busts the cache, and re-fetches.
  // ctx (optional): { weekKey, weekMonthKey, cacheKey, hostSel, refresh }
  // re-targets the picker at another DASH week state (the Support tab).
  // Default = the Objectif weekly challenge.
  function _objWeekSelector(data, ctx) {
    const c = ctx || {
      weekKey: 'objWeek', weekMonthKey: 'objWeekMonth', cacheKey: 'lastWalletWeek',
      hostSel: '.obj-challenge-sub', refresh: () => refreshObjectifWeek(),
    };
    const curMonday = _currentMondayYMD();
    const curMonth = _objCurrentMonthYM();
    // The Monday actually shown right now (server-confirmed): the DASH week key or current.
    const activeMonday = data && data.weekStart ? data.weekStart : (DASH[c.weekKey] || curMonday);
    // Default the stepper to the month of the active week if not chosen yet.
    if (!DASH[c.weekMonthKey]) DASH[c.weekMonthKey] = activeMonday.slice(0, 7);
    const shownMonth = DASH[c.weekMonthKey];
    const [Y, M] = shownMonth.split('-').map(Number);

    const wrap = el('div', {
      class: 'obj-week-filter',
      style: 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:14px;',
    });

    // ----- Month stepper -----
    const stepper = el('div', {
      style: 'display:inline-flex;align-items:center;gap:2px;flex:none;'
        + 'padding:2px;border-radius:8px;background:var(--surface-sunken);border:1px solid var(--border-subtle);',
    });
    const atOrPastCurMonth = shownMonth >= curMonth;   // can't step into the future
    const mkChev = (dir, disabled) => {
      const b = el('button', {
        class: 'icon-btn-sm', type: 'button',
        'aria-label': dir < 0 ? 'Mois précédent' : 'Mois suivant',
        disabled: disabled ? 'disabled' : null,
        style: 'width:24px;height:24px;border-radius:6px;' + (disabled ? 'opacity:.4;cursor:not-allowed;' : ''),
        onclick: disabled ? null : () => {
          DASH[c.weekMonthKey] = _objAddMonths(shownMonth, dir);
          // Re-render the selector in place (no fetch — just changes the listed weeks).
          const host = document.querySelector(c.hostSel);
          if (host) { clear(host); host.appendChild(_objWeekSelector(DASH[c.cacheKey], c)); refreshIcons(host); }
        },
      });
      b.innerHTML = dir < 0
        ? '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>'
        : '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
      return b;
    };
    stepper.appendChild(mkChev(-1, false));
    stepper.appendChild(el('span', {
      text: _objMonthLabelFr(shownMonth),
      style: 'min-width:96px;text-align:center;font-size:12px;font-weight:600;color:var(--text-secondary);'
        + 'font-variant-numeric:tabular-nums;padding:0 2px;',
    }));
    stepper.appendChild(mkChev(1, atOrPastCurMonth));
    wrap.appendChild(stepper);

    // ----- Week dropdown (only selectable, i.e. non-future, weeks) -----
    const weeks = _weeksOfMonth(Y, M).filter(w => w.monday <= curMonday);
    const sel = el('select', {
      class: 'obj-week-select',
      'aria-label': 'Choisir la semaine',
      style: 'flex:none;max-width:100%;font-size:12px;font-weight:600;color:var(--text);'
        + 'background:var(--surface-sunken);border:1px solid var(--border-subtle);border-radius:8px;'
        + 'padding:5px 28px 5px 10px;cursor:pointer;appearance:none;-webkit-appearance:none;'
        + 'font-variant-numeric:tabular-nums;'
        // chevron-down baked in as a background SVG (token-driven stroke via currentColor isn't possible on bg, so use a neutral gray)
        + 'background-image:url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 fill=%27none%27 stroke=%27%239ca3af%27 stroke-width=%272.2%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27><polyline points=%276 9 12 15 18 9%27/></svg>");'
        + 'background-repeat:no-repeat;background-position:right 9px center;',
      onchange: (e) => {
        const monday = e.target.value;
        if (!monday) return;
        // null when it's the current week (keeps the fetch param clean & matches the backend default).
        DASH[c.weekKey] = (monday === curMonday) ? null : monday;
        DASH[c.cacheKey] = null;   // load-bearing: bust the cache so the new week renders
        c.refresh();
      },
    });
    if (!weeks.length) {
      sel.appendChild(el('option', { value: '', text: 'Aucune semaine', disabled: 'disabled', selected: 'selected' }));
      sel.setAttribute('disabled', 'disabled');
      sel.style.opacity = '.5';
      sel.style.cursor = 'not-allowed';
    } else {
      // Selected week = the active one if it's in this month; else the last selectable week.
      const inThisMonth = activeMonday.slice(0, 7) === shownMonth && weeks.some(w => w.monday === activeMonday);
      const selectedMonday = inThisMonth ? activeMonday : weeks[weeks.length - 1].monday;
      weeks.forEach((w) => {
        const isCur = w.monday === curMonday;
        sel.appendChild(el('option', {
          value: w.monday,
          text: w.label + (isCur ? ' · en cours' : ''),
          selected: w.monday === selectedMonday ? 'selected' : null,
        }));
      });
    }
    wrap.appendChild(sel);

    // The server-confirmed week range, kept as a quiet caption beside the controls.
    if (data && data.weekLabel) {
      wrap.appendChild(el('span', {
        text: data.weekLabel,
        style: 'font-size:12px;color:var(--text-tertiary);font-variant-numeric:tabular-nums;',
      }));
    }
    return wrap;
  }

  // ─── Hebdo : challenge primes (tiered bonus per agent this week) ──────────
  // Parallel to refreshObjectif/renderObjectif but for the weekly tiered
  // challenge: each agent earns a prime when their week total crosses a tier.
  async function refreshObjectifWeek() {
    const body = document.getElementById('dashObjectifBody');
    if (!body) return;
    if (!DASH.lastWalletWeek) {
      clear(body);
      const wrap = el('div', { class: 'obj-wrap' });
      wrap.appendChild(_objPeriodToggle());
      wrap.appendChild(el('div', { class: 'dash-loading', text: 'Loading the challenge…' }));
      body.appendChild(wrap);
    }
    try {
      // DASH.objWeek (a Monday YMD) lets an admin browse a past week; null = current.
      const qs = DASH.objWeek ? ('?week=' + encodeURIComponent(DASH.objWeek)) : '';
      const r = await api('/api/funnel/wallet/weekly' + qs);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      const prevTotal = DASH.lastWalletWeek ? Number(DASH.lastWalletWeek.total) || 0 : 0;
      DASH.lastWalletWeek = j;
      renderObjectifWeek(j, prevTotal);
    } catch (err) {
      clear(body);
      const wrap = el('div', { class: 'obj-wrap' });
      wrap.appendChild(_objPeriodToggle());
      wrap.appendChild(el('div', { class: 'obj-error' }, [
        el('div', { class: 'obj-error-text', text: 'Could not load the challenge — ' + (err.message || err) }),
        el('button', { class: 'btn btn-sm', type: 'button', text: 'Retry', onclick: () => { DASH.lastWalletWeek = null; refreshObjectifWeek(); } }),
      ]));
      body.appendChild(wrap);
    }
  }

  // The weekly prime scheme (20k→200 / 25k→250 / 30k→350 DH) was REMOVED on
  // 2026-09-18 at Ayoub's request — the weekly view is now a pure collections
  // board (total, ranking, sign-off). No tier chips, no prime badges, no
  // "bonuses to pay" stat and no tier editor.
  function renderObjectifWeek(data, prevTotal) {
    const body = document.getElementById('dashObjectifBody');
    if (!body) return;
    const oldNum = body.querySelector('.obj-amount-num');
    if (oldNum && oldNum._objRaf) cancelAnimationFrame(oldNum._objRaf);
    clear(body);
    const total = Number(data.total) || 0;
    const agents = Array.isArray(data.agents) ? data.agents : [];
    const me = (state.me && state.me.username) || '';
    const isSelf = data.scope === 'self';
    const selfAgent = isSelf ? (agents.find(a => a.agent_id === me) || agents[0] || null) : null;

    // Is the rendered week the live one? (drives the hero label + days-left).
    const isCurrentWeek = (data.weekStart || null) === _currentMondayYMD();
    // Only a CLOSED (finished) week is confirmable — the live week is not.
    const isClosedWeek = !!data.weekStart && !isCurrentWeek;
    // Hero label: keep "this week" wording only for the live week; otherwise
    // name the chosen week (its Mon–Sun range) so it's never misleading.
    const weekScopeLabel = isCurrentWeek
      ? 'this week'
      : _objWeekRangeFr(data.weekStart, data.weekEnd);
    const heroLabel = (isSelf ? 'My collected · ' : 'Collected · ') + weekScopeLabel;

    const wrap = el('div', { class: 'obj-wrap' + (isSelf ? ' is-self' : '') });
    wrap.appendChild(_objPeriodToggle());

    // ----- Hero: team weekly total (self: own) -----
    const hero = el('div', { class: 'obj-hero obj-hero-challenge' });
    hero.appendChild(el('div', { class: 'obj-hero-head' }, [
      el('div', { class: 'obj-month', text: heroLabel }),
    ]));
    const num = el('span', { class: 'obj-amount-num', text: '0' });
    num._objVal = prevTotal || 0;
    hero.appendChild(el('div', { class: 'obj-amount' }, [num, el('span', { class: 'obj-amount-cur', text: 'DH' })]));
    // The obj-challenge-sub line now hosts the week filter (stepper + dropdown)
    // — the server-confirmed weekLabel lives inside it as a quiet caption.
    hero.appendChild(el('div', { class: 'obj-challenge-sub' }, [_objWeekSelector(data)]));
    wrap.appendChild(hero);

    // ----- Grid: leaderboard (team) or self row, + side -----
    let board = null;
    if (!isSelf) {
      board = el('div', { class: 'obj-board' });
      board.appendChild(el('div', { class: 'obj-board-head' }, [
        el('div', {}, [
          el('div', { class: 'obj-board-title', text: 'This week' }),
          el('div', { class: 'obj-board-sub', text: 'Collected per agent' }),
        ]),
      ]));
    }
    const rowsHost = isSelf ? wrap : board;
    const rowAgents = isSelf ? (selfAgent ? [selfAgent] : []) : agents;
    if (!isSelf && !rowAgents.length) {
      const empty = el('div', { class: 'obj-board-empty' });
      const ic = el('div', { class: 'obj-board-empty-ic' });
      ic.innerHTML = '<i data-lucide="trophy"></i>';
      empty.appendChild(ic);
      empty.appendChild(el('div', { class: 'obj-board-empty-txt', text: 'No payments this week yet.' }));
      board.appendChild(empty);
    } else {
      rowAgents.forEach((a, idx) => {
        const aTotal = Number(a.total) || 0;
        const isTop = !isSelf && idx === 0 && aTotal > 0;
        const row = el('div', { class: 'obj-row obj-row-challenge' + (isTop ? ' is-top' : '') });
        if (!isSelf) row.appendChild(el('div', { class: 'obj-rank' + (idx < 3 ? ' is-podium' : ''), text: String(idx + 1) }));
        const av = el('div', { class: 'avatar obj-avatar ' + avatarColorClass(a.agent_id || a.displayName || String(idx)), text: initials(userLabel(a.agent_id, a.displayName || a.agent_id || '?')) });
        row.appendChild(av);
        const nameKids = [];
        if (isTop) {
          const crown = el('span', { class: 'obj-crown' });
          crown.innerHTML = '<i data-lucide="crown"></i>';
          nameKids.push(crown);
        }
        nameKids.push(el('span', { text: userLabel(a.agent_id, a.displayName || a.agent_id || '—') }));
        if (me && a.agent_id === me) nameKids.push(el('span', { class: 'obj-me', text: '(me)' }));
        // Weekly sign-off status — only on a CLOSED week, team view.
        if (isClosedWeek && !isSelf) {
          const isMine = me && a.agent_id === me;
          const cbadge = el('span', {
            class: 'obj-confirm-badge ' + (a.confirmed ? 'is-confirmed' : 'is-pending'),
            title: a.confirmed
              ? ('Confirmé' + (a.confirmedAt ? ' le ' + MA.loc(new Date(a.confirmedAt), 'd', 'fr-FR') : ''))
              : 'En attente de confirmation',
            text: a.confirmed ? '✓ Confirmé' : 'En attente',
          });
          if (a.confirmed && _isSalesAdmin()) {
            cbadge.style.cursor = 'pointer';
            cbadge.title += ' · cliquer pour rouvrir';
            cbadge.onclick = () => _objReopenWeekConfirm(data.weekStart, a.agent_id, a.displayName || a.agent_id);
          } else if (!a.confirmed && isMine) {
            cbadge.style.cursor = 'pointer';
            cbadge.title = 'Confirmer ma semaine';
            cbadge.textContent = 'Confirmer';
            cbadge.onclick = () => _objConfirmWeek(data.weekStart, null);
          }
          nameKids.push(cbadge);
          if (a.confirmed && a.confirmDrift) {
            nameKids.push(el('span', { class: 'obj-confirm-badge is-drift', title: 'Paiements modifiés depuis la confirmation', text: '⚠ modifié' }));
          }
        }
        row.appendChild(el('div', { class: 'obj-name' }, nameKids));
        row.appendChild(el('div', { class: 'obj-total', text: fmtDH(aTotal) }));
        rowsHost.appendChild(row);
      });
    }

    // Agent weekly sign-off (self view) — ONLY on a CLOSED week.
    if (isSelf && isClosedWeek) {
      wrap.appendChild(_objConfirmBlockWeek(data));
    }

    // ----- Side -----
    const side = el('div', { class: 'obj-side' });
    side.appendChild(el('div', { class: 'obj-stat' }, [
      el('div', { class: 'obj-stat-num', text: String(data.paymentsCount || 0) }),
      el('div', { class: 'obj-stat-label', text: 'Payments this week' }),
    ]));
    // Live week → a real countdown; a past/finalized week → "Finalisé" (no
    // misleading countdown — the server already returned that week's final figures).
    if (isCurrentWeek) {
      const daysLeft = _objWeekDaysLeft(data.weekEnd);
      side.appendChild(el('div', { class: 'obj-stat' }, [
        el('div', { class: 'obj-stat-num', text: daysLeft == null ? '—' : String(daysLeft) }),
        el('div', { class: 'obj-stat-label', text: 'Days left' }),
      ]));
    } else {
      side.appendChild(el('div', { class: 'obj-stat' }, [
        el('div', { class: 'obj-stat-num', text: 'Finalisé' }),
        el('div', { class: 'obj-stat-label', text: 'Week closed' }),
      ]));
    }
    wrap.appendChild(el('div', { class: 'obj-grid' + (isSelf ? ' is-self' : '') }, isSelf ? [side] : [board, side]));
    body.appendChild(wrap);
    refreshIcons(body);

    animateCountUp(num, total);
  }

  // Days left until the end of the challenge week (inclusive of today).
  function _objWeekDaysLeft(weekEnd) {
    if (!weekEnd) return null;
    try {
      const end = new Date(weekEnd + 'T23:59:59');
      const now = new Date();
      if (isNaN(end.getTime())) return null;
      const diff = Math.ceil((end.getTime() - now.getTime()) / 86400000);
      return diff < 0 ? 0 : diff;
    } catch (_) { return null; }
  }


  async function refreshFunnel() {
    const loadingEl = document.getElementById('dashFunnelLoading');
    const chartEl = document.getElementById('dashFunnelChart');
    const chipsEl = document.getElementById('dashFunnelSideChips');
    const noteEl = document.getElementById('dashRangeNote');
    if (!chartEl) return;
    loadingEl.classList.remove('hidden');
    chartEl.classList.add('hidden');
    const { from, to, agent, source, region, country } = dashCurrentRange();
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (agent) params.set('agent', agent);
    if (source) params.set('source', source);
    if (region) params.set('region', region);
    if (country) params.set('country', country);
    params.set('model', DASH.funnelMode);
    try {
      const r = await api('/api/funnel?' + params.toString());
      const j = await r.json();
      if (!r.ok) {
        loadingEl.textContent = j.error || ('Funnel load failed: ' + r.status);
        return;
      }
      DASH.lastFunnel = j;
      DASH.loadedOnceFunnel = true;
      renderFunnel(j);
      renderFunnelCountries(j);
      dashSyncCountryOptions(j);
      if (noteEl) {
        noteEl.textContent = j.model === 'cohort'
          ? `Range: ${j.range?.from} → ${j.range?.to} · cohorte = leads créés dans la période, suivis jusqu'à leur étape la plus avancée · (${j.cohort_total || 0} leads) · % = part de la cohorte`
          : `Range: ${j.range?.from} → ${j.range?.to} · barres = leads passés par chaque étape dans la période · (N now) = en ce moment`;
      }
      loadingEl.classList.add('hidden');
      chartEl.classList.remove('hidden');
    } catch (err) {
      console.error('[dashboards] funnel fetch error:', err);
      loadingEl.textContent = 'Network error loading funnel';
    }
  }

  function renderFunnel(data) {
    const chartEl = document.getElementById('dashFunnelChart');
    const chipsEl = document.getElementById('dashFunnelSideChips');
    clear(chartEl);
    clear(chipsEl);
    const forward = data.forward_stages || [];
    const labels = data.stage_labels || {};
    // EVENTS model: bars = distinct leads that ENTERED each stage in the range.
    // COHORT model: bars = leads created in the range that reached ≥ each stage
    // (monotonic), right column = % of the created cohort. `stage_counts` is the
    // live "(N now)" snapshot (events) or cohort-currently-at-stage (cohort).
    // Fall back to stage_cumulative if an older server is still deployed.
    const isCohort = data.model === 'cohort';
    const entered = data.stage_entered || data.stage_cumulative || {};
    const counts = data.stage_counts || {};
    const share = data.stage_share || {};
    // Largest entered count = top of funnel (typically 'new').
    let maxCount = 0;
    for (const s of forward) maxCount = Math.max(maxCount, entered[s] || 0);
    if (maxCount < 1) maxCount = 1;

    forward.forEach((stage, idx) => {
      const reached = entered[stage] || 0;
      const here = counts[stage] || 0;
      // Bar width: min 4% so empty bars are still visible.
      const pct = Math.max(4, Math.round((reached / maxCount) * 100));
      const row = el('div', { class: 'dash-funnel-row' });
      row.appendChild(el('div', { class: 'dash-funnel-label', text: labels[stage] || stage }));
      const track = el('div', { class: 'dash-funnel-bar-track' });
      const fill = el('div', { class: 'dash-funnel-bar-fill', text: String(reached) });
      fill.style.width = pct + '%';
      track.appendChild(fill);
      row.appendChild(track);
      const rightText = isCohort ? `${share[stage] || 0}% créés` : `${here} now`;
      row.appendChild(el('div', { class: 'dash-funnel-count', text: `(${rightText})` }));
      chartEl.appendChild(row);

      // Conversion arrow between this and the next forward stage
      if (idx < forward.length - 1) {
        const conv = (data.conversions || [])[idx];
        const arrowRow = el('div', { class: 'dash-funnel-arrow-row' });
        const arrowWrap = el('div', { class: 'dash-funnel-arrow' });
        arrowWrap.appendChild(el('div', { class: 'dash-funnel-arrow-bar' }));
        const rate = conv ? conv.rate : 0;
        const pctClass = rate >= 60 ? 'dash-high' : rate >= 30 ? 'dash-mid' : 'dash-low';
        arrowWrap.appendChild(el('span', { class: 'dash-funnel-arrow-pct ' + pctClass, text: rate.toFixed(1) + '%' }));
        const toLabel = labels[forward[idx + 1]] || forward[idx + 1];
        arrowWrap.appendChild(el('span', { text: '→ ' + toLabel }));
        arrowRow.appendChild(arrowWrap);
        chartEl.appendChild(arrowRow);
      }
    });

    // Side branches as chips below — EVENTS too: leads that fell into this
    // branch (No answer / No show / Not interested / Lost) DURING the range, not the live count.
    const side = data.side_branch_stages || [];
    side.forEach(stage => {
      const n = entered[stage] || 0;
      const chip = el('span', { class: 'dash-side-chip' });
      chip.appendChild(el('span', { text: (labels[stage] || stage) + ': ' }));
      chip.appendChild(el('strong', { text: String(n) }));
      chipsEl.appendChild(chip);
    });
    refreshIcons(chartEl);
    refreshIcons(chipsEl);
  }

  // 'MA' → 🇲🇦 (regional-indicator pair). 'XX' (Inconnu) / junk → 🌐.
  function _countryFlag(cc) {
    if (!cc || !/^[A-Z]{2}$/.test(cc) || cc === 'XX') return '🌐';
    return String.fromCodePoint(...[...cc].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
  }

  // "Par pays" panel — country origin of the leads behind the funnel, scoped
  // by the SAME filters (date/agent/source/région). Top 8 countries + an
  // aggregated "Autres" row. Older servers don't send `countries` → hide.
  function renderFunnelCountries(data) {
    const host = document.getElementById('dashFunnelCountries');
    if (!host) return;
    clear(host);
    const list = Array.isArray(data.countries) ? data.countries : null;
    if (!list) { host.classList.add('hidden'); return; }
    host.classList.remove('hidden');

    const total = Number(data.countries_total) || 0;
    const head = el('div', { class: 'dash-countries-head' }, [
      el('span', { class: 'dash-countries-title', text: 'Par pays' }),
      el('span', { class: 'dash-countries-total', text: total + ' lead' + (total > 1 ? 's' : '') }),
    ]);
    host.appendChild(head);

    if (!total) {
      host.appendChild(el('div', { class: 'dash-countries-empty', text: 'Aucun lead sur cette période / ces filtres.' }));
      return;
    }

    const TOP_N = 8;
    const top = list.slice(0, TOP_N);
    const rest = list.slice(TOP_N);
    const rows = top.map(c => ({ code: c.code, flag: _countryFlag(c.code), name: c.name, count: c.count, pct: c.pct }));
    if (rest.length) {
      const restCount = rest.reduce((s, c) => s + (c.count || 0), 0);
      rows.push({
        code: null, // aggregate — not clickable
        flag: '🌐',
        name: 'Autres (' + rest.length + ' pays)',
        count: restCount,
        pct: total > 0 ? Math.round((restCount / total) * 1000) / 10 : 0,
      });
    }
    const maxCount = rows.reduce((m, r) => Math.max(m, r.count), 1);

    const grid = el('div', { class: 'dash-countries-grid' });
    rows.forEach(r => {
      const clickable = !!r.code;
      const row = el('div', { class: 'dash-country-row' + (clickable ? ' is-clickable' : '') });
      if (clickable) {
        // Click-to-filter: set the Pays select to this country and re-apply.
        row.title = 'Filtrer le funnel sur ' + r.name;
        row.addEventListener('click', () => dashPickCountry(r.code, r.name));
      }
      row.appendChild(el('span', { class: 'dash-country-flag', text: r.flag }));
      row.appendChild(el('span', { class: 'dash-country-name', text: r.name, title: r.name }));
      const track = el('div', { class: 'dash-country-track' });
      const fill = el('div', { class: 'dash-country-fill' });
      fill.style.width = Math.max(2, Math.round((r.count / maxCount) * 100)) + '%';
      track.appendChild(fill);
      row.appendChild(track);
      row.appendChild(el('span', { class: 'dash-country-count', text: String(r.count) }));
      row.appendChild(el('span', { class: 'dash-country-pct', text: (r.pct || 0).toFixed(1).replace(/\.0$/, '') + '%' }));
      grid.appendChild(row);
    });
    host.appendChild(grid);
  }

  // PAYS (country) filter dropdown. Options come from the funnel response's
  // `countries` array — the countries that actually have leads under the
  // current date/agent/source/région filters — labelled "🇫🇷 France (107)".
  // When a country filter is ACTIVE the response collapses to that one country,
  // so we keep the last country-less option list (DASH.countryOptions) instead
  // of shrinking the dropdown to a single choice. The current selection stays
  // sticky across refreshes even when its count changes or drops out.
  function dashSyncCountryOptions(data) {
    const select = document.getElementById('dashCountryFilter');
    if (!select) return;
    if (!Array.isArray(data.countries)) return; // older server payload — leave as-is
    const activeCountry = (data.filters && data.filters.country) || null;
    if (!activeCountry) DASH.countryOptions = data.countries;
    const opts = DASH.countryOptions || data.countries;
    const current = select.value;
    // Keep the leading "Tous les pays" option, rebuild the rest.
    while (select.children.length > 1) select.removeChild(select.lastChild);
    const seen = new Set();
    opts.forEach(c => {
      if (!c || !c.code || seen.has(c.code)) return;
      seen.add(c.code);
      select.appendChild(el('option', {
        value: c.code,
        text: _countryFlag(c.code) + ' ' + c.name + ' (' + c.count + ')',
      }));
    });
    // Sticky selection: if the selected country fell out of the list (count
    // went to 0 under the new filters), keep it selectable anyway.
    if (current && !seen.has(current)) {
      const known = (DASH.lastFunnel?.countries || []).find(c => c.code === current);
      select.appendChild(el('option', { value: current, text: _countryFlag(current) + ' ' + (known?.name || current) }));
    }
    select.value = current;
  }

  // "Par pays" row click → select that country in the Pays filter + re-apply.
  function dashPickCountry(code, name) {
    const select = document.getElementById('dashCountryFilter');
    if (!select || !code) return;
    if (select.value === code) return; // already filtered on it — no-op
    let opt = Array.from(select.options).find(o => o.value === code);
    if (!opt) {
      opt = el('option', { value: code, text: _countryFlag(code) + ' ' + (name || code) });
      select.appendChild(opt);
    }
    select.value = code;
    dashApplyFilters();
  }

  async function refreshAgents() {
    const loadingEl = document.getElementById('dashAgentsLoading');
    const tableEl = document.getElementById('dashAgentsTable');
    if (!tableEl) return;
    loadingEl.classList.remove('hidden');
    tableEl.classList.add('hidden');
    const { from, to } = dashCurrentRange();
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    try {
      const r = await api('/api/funnel/agents?' + params.toString());
      const j = await r.json();
      if (!r.ok) {
        loadingEl.textContent = j.error || ('Agents load failed: ' + r.status);
        return;
      }
      DASH.lastAgents = j;
      DASH.loadedOnceAgents = true;
      renderAgents();
      loadingEl.classList.add('hidden');
      tableEl.classList.remove('hidden');
    } catch (err) {
      console.error('[dashboards] agents fetch error:', err);
      loadingEl.textContent = 'Network error loading agents';
    }
  }

  function dashSortAgents(key) {
    if (!key) return;
    if (DASH.agentSort.key === key) {
      DASH.agentSort.dir = DASH.agentSort.dir === 'desc' ? 'asc' : 'desc';
    } else {
      DASH.agentSort.key = key;
      DASH.agentSort.dir = 'desc';
    }
    // Update th visual state
    document.querySelectorAll('#dashAgentsTable th[data-sort]').forEach(th => {
      const active = th.getAttribute('data-sort') === key;
      th.classList.toggle('dash-th-active', active);
      th.classList.toggle('dash-th-asc', active && DASH.agentSort.dir === 'asc');
    });
    renderAgents();
  }

  function renderAgents() {
    const tbody = document.getElementById('dashAgentsTbody');
    if (!tbody || !DASH.lastAgents) return;
    clear(tbody);
    const rows = (DASH.lastAgents.agents || []).slice();
    const { key, dir } = DASH.agentSort;
    rows.sort((a, b) => {
      const av = a[key], bv = b[key];
      // null/undefined last regardless of direction
      const aNull = av == null, bNull = bv == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return dir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av), bs = String(bv);
      return dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    if (!rows.length) {
      tbody.appendChild(el('tr', {}, el('td', { colspan: '7', class: 'dash-loading', text: 'No agent activity in this range.' })));
      return;
    }
    rows.forEach(a => {
      const tr = el('tr');
      tr.appendChild(el('td', { text: userLabel(a.agent_id || '—') }));
      tr.appendChild(el('td', { class: 'dash-num', text: String(a.leads_created) }));
      tr.appendChild(el('td', { class: 'dash-num', text: String(a.transitions_made) }));
      tr.appendChild(el('td', { class: 'dash-num', text: String(a.paid_count) }));
      tr.appendChild(el('td', { class: 'dash-num', text: (a.conversion_to_paid != null ? a.conversion_to_paid.toFixed(1) : '0') + '%' }));
      tr.appendChild(el('td', { class: 'dash-num', text: a.avg_days_to_paid != null ? String(a.avg_days_to_paid) : '—' }));
      tr.appendChild(el('td', { class: 'dash-num', text: a.avg_days_in_stage != null ? String(a.avg_days_in_stage) : '—' }));
      tbody.appendChild(tr);
    });
  }

  // ============ Dashboard "Teachers" tab — per-teacher placement-test report ====
  // Admin + super_sales only (server: /api/funnel/teachers gates on _isSalesAdmin).
  // Own Mensuel/Hebdo toggle, independent of the Objectif tab's state — standalone
  // copies of the Objectif toggle/steppers (deliberately not parametrized, so this
  // never collides with concurrent edits to the objectif code). Reuses the shared
  // French month/week label helpers (_objMonthLabelFr, _objCurrentMonthYM,
  // _objAddMonths, _currentMondayYMD, _objWeekRangeFr) as-is.
  function refreshTeachersActive() {
    if (DASH.teachPeriod === 'week') refreshTeachersWeek();
    else refreshTeachersMonth();
  }

  // Mensuel / Hebdo segmented control — twin of _objPeriodToggle, keyed to
  // DASH.teachPeriod so switching tabs never disturbs the Objectif toggle state.
  function _teachPeriodToggle() {
    // margin-top: the dashboards subtab bar is sticky — without top clearance
    // the seg renders half-covered under it (seen in the T4 audit screenshot).
    const seg = el('div', { class: 'obj-period-seg teamchat-seg', style: 'margin-top: 14px;', role: 'tablist', 'aria-label': 'Mensuel ou Hebdomadaire' });
    [['month', 'Mensuel'], ['week', 'Hebdo']].forEach(([period, label]) => {
      const on = DASH.teachPeriod === period;
      const btn = el('button', {
        class: 'teamchat-seg-btn' + (on ? ' on' : ''),
        type: 'button',
        role: 'tab',
        'aria-selected': on ? 'true' : 'false',
        text: label,
        onclick: () => {
          if (DASH.teachPeriod === period) return;
          DASH.teachPeriod = period;
          if (period === 'week') { DASH.teachWeek = null; DASH.teachWeekMonth = null; DASH.lastTeachersWeek = null; }
          refreshTeachersActive();
        },
      });
      seg.appendChild(btn);
    });
    return seg;
  }

  // Compact month stepper for the MONTHLY Teachers view — twin of
  // _objMonthSelector, capped to current + previous month only (same UX as
  // Objectif), keyed to DASH.teachMonth.
  function _teachMonthSelector(data) {
    const curMonth = _objCurrentMonthYM();
    const prevMonth = _objAddMonths(curMonth, -1);
    const shown = (data && data.month) ? data.month : (DASH.teachMonth || curMonth);
    const atCur = shown >= curMonth;
    const atFloor = shown <= prevMonth;
    const go = (dir) => {
      let next = _objAddMonths(shown, dir);
      if (next < prevMonth) next = prevMonth;
      if (next > curMonth) next = curMonth;
      DASH.teachMonth = (next === curMonth) ? null : next;
      DASH.lastTeachers = null;
      refreshTeachersMonth();
    };
    const wrap = el('div', {
      class: 'obj-month-stepper',
      style: 'display:inline-flex;align-items:center;gap:2px;flex:none;'
        + 'padding:2px;border-radius:8px;background:var(--surface-sunken);border:1px solid var(--border-subtle);',
    });
    const mkChev = (dir, disabled) => {
      const b = el('button', {
        class: 'icon-btn-sm', type: 'button',
        'aria-label': dir < 0 ? 'Mois précédent' : 'Mois suivant',
        disabled: disabled ? 'disabled' : null,
        style: 'width:24px;height:24px;border-radius:6px;' + (disabled ? 'opacity:.4;cursor:not-allowed;' : ''),
        onclick: disabled ? null : () => go(dir),
      });
      b.innerHTML = dir < 0
        ? '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>'
        : '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
      return b;
    };
    wrap.appendChild(mkChev(-1, atFloor));
    wrap.appendChild(el('span', {
      text: _objMonthLabelFr(shown),
      style: 'min-width:96px;text-align:center;font-size:12px;font-weight:600;color:var(--text-secondary);'
        + 'font-variant-numeric:tabular-nums;padding:0 2px;',
    }));
    wrap.appendChild(mkChev(1, atCur));
    return wrap;
  }

  // Week filter (month stepper + week <select>) for the WEEKLY Teachers view —
  // twin of _objWeekSelector, keyed to DASH.teachWeek/teachWeekMonth. Future
  // weeks are omitted; picking one busts the cache and re-fetches.
  function _teachWeekSelector(data) {
    const curMonday = _currentMondayYMD();
    const curMonth = _objCurrentMonthYM();
    const activeMonday = data && data.weekStart ? data.weekStart : (DASH.teachWeek || curMonday);
    if (!DASH.teachWeekMonth) DASH.teachWeekMonth = activeMonday.slice(0, 7);
    const shownMonth = DASH.teachWeekMonth;
    const [Y, M] = shownMonth.split('-').map(Number);

    const wrap = el('div', {
      class: 'obj-week-filter',
      style: 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:14px;',
    });

    const stepper = el('div', {
      style: 'display:inline-flex;align-items:center;gap:2px;flex:none;'
        + 'padding:2px;border-radius:8px;background:var(--surface-sunken);border:1px solid var(--border-subtle);',
    });
    const atOrPastCurMonth = shownMonth >= curMonth;
    const mkChev = (dir, disabled) => {
      const b = el('button', {
        class: 'icon-btn-sm', type: 'button',
        'aria-label': dir < 0 ? 'Mois précédent' : 'Mois suivant',
        disabled: disabled ? 'disabled' : null,
        style: 'width:24px;height:24px;border-radius:6px;' + (disabled ? 'opacity:.4;cursor:not-allowed;' : ''),
        onclick: disabled ? null : () => {
          DASH.teachWeekMonth = _objAddMonths(shownMonth, dir);
          const host = document.querySelector('.teach-week-host');
          if (host) { clear(host); host.appendChild(_teachWeekSelector(DASH.lastTeachersWeek)); refreshIcons(host); }
        },
      });
      b.innerHTML = dir < 0
        ? '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>'
        : '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
      return b;
    };
    stepper.appendChild(mkChev(-1, false));
    stepper.appendChild(el('span', {
      text: _objMonthLabelFr(shownMonth),
      style: 'min-width:96px;text-align:center;font-size:12px;font-weight:600;color:var(--text-secondary);'
        + 'font-variant-numeric:tabular-nums;padding:0 2px;',
    }));
    stepper.appendChild(mkChev(1, atOrPastCurMonth));
    wrap.appendChild(stepper);

    const weeks = _weeksOfMonth(Y, M).filter(w => w.monday <= curMonday);
    const sel = el('select', {
      class: 'obj-week-select',
      'aria-label': 'Choisir la semaine',
      style: 'flex:none;max-width:100%;font-size:12px;font-weight:600;color:var(--text);'
        + 'background:var(--surface-sunken);border:1px solid var(--border-subtle);border-radius:8px;'
        + 'padding:5px 28px 5px 10px;cursor:pointer;appearance:none;-webkit-appearance:none;'
        + 'font-variant-numeric:tabular-nums;'
        + 'background-image:url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 fill=%27none%27 stroke=%27%239ca3af%27 stroke-width=%272.2%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27><polyline points=%276 9 12 15 18 9%27/></svg>");'
        + 'background-repeat:no-repeat;background-position:right 9px center;',
      onchange: (e) => {
        const monday = e.target.value;
        if (!monday) return;
        DASH.teachWeek = (monday === curMonday) ? null : monday;
        DASH.lastTeachersWeek = null;
        refreshTeachersWeek();
      },
    });
    if (!weeks.length) {
      sel.appendChild(el('option', { value: '', text: 'Aucune semaine', disabled: 'disabled', selected: 'selected' }));
      sel.setAttribute('disabled', 'disabled');
      sel.style.opacity = '.5';
      sel.style.cursor = 'not-allowed';
    } else {
      const inThisMonth = activeMonday.slice(0, 7) === shownMonth && weeks.some(w => w.monday === activeMonday);
      const selectedMonday = inThisMonth ? activeMonday : weeks[weeks.length - 1].monday;
      weeks.forEach((w) => {
        const isCur = w.monday === curMonday;
        sel.appendChild(el('option', {
          value: w.monday,
          text: w.label + (isCur ? ' · en cours' : ''),
          selected: w.monday === selectedMonday ? 'selected' : null,
        }));
      });
    }
    wrap.appendChild(sel);

    if (data && data.weekStart) {
      wrap.appendChild(el('span', {
        text: _objWeekRangeFr(data.weekStart, data.weekEnd),
        style: 'font-size:12px;color:var(--text-tertiary);font-variant-numeric:tabular-nums;',
      }));
    }
    return wrap;
  }

  async function refreshTeachersMonth() {
    const body = document.getElementById('dashTeachersBody');
    if (!body) return;
    if (!DASH.lastTeachers) {
      clear(body);
      const wrap = el('div', { class: 'obj-wrap' });
      wrap.appendChild(_teachPeriodToggle());
      wrap.appendChild(el('div', { class: 'dash-loading', text: 'Chargement des tests…' }));
      body.appendChild(wrap);
    }
    try {
      const qs = DASH.teachMonth ? ('?month=' + encodeURIComponent(DASH.teachMonth)) : '';
      const r = await api('/api/funnel/teachers' + qs);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      DASH.lastTeachers = j;
      renderTeachers(j);
    } catch (err) {
      clear(body);
      const wrap = el('div', { class: 'obj-wrap' });
      wrap.appendChild(_teachPeriodToggle());
      wrap.appendChild(el('div', { class: 'obj-error' }, [
        el('div', { class: 'obj-error-text', text: 'Impossible de charger les tests — ' + (err.message || err) }),
        el('button', { class: 'btn btn-sm', type: 'button', text: 'Réessayer', onclick: () => { DASH.lastTeachers = null; refreshTeachersMonth(); } }),
      ]));
      body.appendChild(wrap);
    }
  }

  async function refreshTeachersWeek() {
    const body = document.getElementById('dashTeachersBody');
    if (!body) return;
    if (!DASH.lastTeachersWeek) {
      clear(body);
      const wrap = el('div', { class: 'obj-wrap' });
      wrap.appendChild(_teachPeriodToggle());
      wrap.appendChild(el('div', { class: 'dash-loading', text: 'Chargement des tests…' }));
      body.appendChild(wrap);
    }
    try {
      // ALWAYS send ?week= — the combined endpoint defaults to MONTH mode
      // when no param is given (unlike /wallet/weekly which is week-only), so
      // the current week must be spelled out explicitly.
      const monday = DASH.teachWeek || _currentMondayYMD();
      const r = await api('/api/funnel/teachers?week=' + encodeURIComponent(monday));
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      DASH.lastTeachersWeek = j;
      renderTeachers(j);
    } catch (err) {
      clear(body);
      const wrap = el('div', { class: 'obj-wrap' });
      wrap.appendChild(_teachPeriodToggle());
      wrap.appendChild(el('div', { class: 'obj-error' }, [
        el('div', { class: 'obj-error-text', text: 'Impossible de charger les tests — ' + (err.message || err) }),
        el('button', { class: 'btn btn-sm', type: 'button', text: 'Réessayer', onclick: () => { DASH.lastTeachersWeek = null; refreshTeachersWeek(); } }),
      ]));
      body.appendChild(wrap);
    }
  }

  // Small "rate" pill colour — reuses house semantic tokens only (no new
  // hex): green ≥60%, amber ≥35%, red below. Used inline for completion /
  // no-show / paid rates in the side stats and per-row numbers.
  function _teachRateColor(pct) {
    const n = Number(pct) || 0;
    if (n >= 60) return 'var(--success)';
    if (n >= 35) return 'var(--warning)';
    return 'var(--danger)';
  }
  // Inverse of _teachRateColor — for metrics where LOWER is better (no-show rate).
  function _teachNoShowColor(pct) {
    const n = Number(pct) || 0;
    if (n <= 40) return 'var(--success)';
    if (n <= 65) return 'var(--warning)';
    return 'var(--danger)';
  }
  // Excel-style "best in column" finder for the teachers comparison table.
  // Only highlights when >=2 teachers have a non-zero denominator (per getElig)
  // AND their values aren't all tied — otherwise returns an empty Set (no-op).
  function _teachBestSet(teachers, getVal, getElig, mode) {
    const eligible = [];
    teachers.forEach((t, i) => {
      if (!getElig(t)) return;
      const v = getVal(t);
      if (v == null || Number.isNaN(v)) return;
      eligible.push({ i, v });
    });
    if (eligible.length < 2) return new Set();
    if (eligible.every(x => x.v === eligible[0].v)) return new Set();
    const best = mode === 'min' ? Math.min(...eligible.map(x => x.v)) : Math.max(...eligible.map(x => x.v));
    return new Set(eligible.filter(x => x.v === best).map(x => x.i));
  }

  const TEACH_SRC_CATS = [
    { key: 'bot', label: 'Bot', color: 'var(--info)' },
    { key: 'sales', label: 'Équipe sales', color: 'var(--success)' },
    { key: 'support', label: 'Support', color: 'var(--warning)' },
    { key: 'autre', label: 'Autre', color: 'var(--text-tertiary)' },
  ];
  // Small inline colored dot for the booking-source legend (house tokens only).
  function _teachSrcDot(color) {
    return el('span', { style: 'display:inline-block;width:7px;height:7px;border-radius:50%;background:' + color + ';flex:none;transform:translateY(-1px);' });
  }
  // Rate pill — soft tint of the semantic tone with the tone as text color.
  // The best value across teachers gets `.teach-best-mark`: an inset ring in
  // the same tone + a bolder weight.
  function _teachRatePill(pct, tone, isBest) {
    return el('span', {
      class: 'teach-pill' + (isBest ? ' teach-best-mark' : ''),
      style: 'background: color-mix(in srgb, ' + tone + ' 14%, transparent); color: ' + tone + ';'
        + (isBest ? ' box-shadow: inset 0 0 0 1.5px ' + tone + '; font-weight: 700;' : ''),
      text: (Number(pct) || 0).toFixed(1) + ' %',
    });
  }
  // Per-booker names for one category: " — Mary 51 · Bouchra 18 · Doha 8"
  // (max 5 names, then "+X"). Bookers come from the API pre-sorted n desc
  // with a server-side `label`. Null when the category has no non-zero bookers.
  function _teachSrcNames(bookers, catKey) {
    const list = (Array.isArray(bookers) ? bookers : []).filter(b => b && b.category === catKey && (b.n || 0) > 0);
    if (!list.length) return null;
    const MAX = 5;
    const parts = list.slice(0, MAX).map(b => (b.label || b.username || '?') + ' ' + (b.n || 0));
    const extra = list.length - MAX;
    return el('span', { class: 'teach-mix-names', text: ' — ' + parts.join(' · ') + (extra > 0 ? ' +' + extra : '') });
  }
  // Full-width stacked source-mix bar — one tinted segment per non-zero
  // category, widths = pct of the entity's total, over a sunken track.
  function _teachMixBar(sources) {
    const bar = el('div', { class: 'teach-mix-bar' });
    TEACH_SRC_CATS.forEach(c => {
      const s = (sources && sources[c.key]) || { n: 0, pct: 0 };
      if (!(s.n > 0)) return;
      bar.appendChild(el('span', { class: 'teach-mix-seg', style: 'width:' + (Number(s.pct) || 0) + '%;background:' + c.color + ';' }));
    });
    return bar;
  }
  // Vertical legend lines: dot + "Bot · 45 (45.9 %)", one per non-zero
  // category (order bot/sales/support/autre); `alwaysCore` (team block)
  // forces bot/sales/support even at 0. Sales/Support lines carry the
  // per-booker names inline — the dot is its own flex item, so wrapped
  // name text stays indented under the text column.
  function _teachSourceLines(sources, bookers, alwaysCore) {
    const lines = [];
    TEACH_SRC_CATS.forEach(c => {
      const s = (sources && sources[c.key]) || { n: 0, pct: 0 };
      if (!(s.n > 0) && !(alwaysCore && c.key !== 'autre')) return;
      const txt = el('span', { class: 'teach-mix-txt' }, [
        document.createTextNode(c.label + ' · ' + (s.n || 0) + ' (' + (Number(s.pct) || 0).toFixed(1) + ' %)'),
      ]);
      if (c.key === 'sales' || c.key === 'support') {
        const names = _teachSrcNames(bookers, c.key);
        if (names) txt.appendChild(names);
      }
      lines.push(el('div', { class: 'teach-mix-line' }, [_teachSrcDot(c.color), txt]));
    });
    return lines;
  }
  // Sources block: uppercase micro-header + full-width stacked bar + vertical
  // legend. Used inside each teacher card and (with `alwaysCore` + the team
  // totals) in the side panel.
  function _teachSourcesBlock(sources, bookers, alwaysCore, header) {
    const blk = el('div', { class: 'teach-srcblock' }, [
      el('div', { class: 'teach-srcblock-hd', text: header || 'Sources' }),
      _teachMixBar(sources),
    ]);
    _teachSourceLines(sources, bookers, alwaysCore).forEach(l => blk.appendChild(l));
    return blk;
  }
  // One teacher card: header (avatar / name + "n tests · n à venir" / rank
  // medal), KPI strip (Complétés · No-show · Payés — the team-best value gets
  // a success pill), rates list (tone pills, best keeps the inset ring),
  // score, then a divider + the sources block. `best` = _teachBestSet results.
  function _teachCard(t, idx, best) {
    const shownName = String(t.displayName || t.name || '—').replace(/^teacher\s+/i, '');
    const av = el('div', {
      class: 'avatar obj-avatar',
      style: 'background:' + (t.color || '#6b7280') + ';',
      text: initials(shownName || '?'),
    });
    const sub = (t.total || 0) + ' tests' + ((t.upcoming || 0) > 0 ? ' · ' + t.upcoming + ' à venir' : '');
    const head = el('div', { class: 'teach-card-head' }, [
      av,
      el('div', { class: 'teach-card-id' }, [
        el('div', { class: 'teach-card-name', text: shownName }),
        el('div', { class: 'teach-card-sub', text: sub }),
      ]),
      el('div', { class: 'teach-rank' + (idx === 0 ? ' teach-rank-1' : ''), text: String(idx + 1) }),
    ]);
    const kpi = (label, val, isBest) => el('div', { class: 'teach-kpi' }, [
      el('div', { class: 'teach-kpi-num' }, [
        isBest ? el('span', { class: 'teach-kpi-best', text: String(val) }) : document.createTextNode(String(val)),
      ]),
      el('div', { class: 'teach-kpi-label', text: label }),
    ]);
    const kpis = el('div', { class: 'teach-kpis' }, [
      kpi('Complétés', t.completed || 0, best.completed.has(idx)),
      kpi('No-show', t.noShow || 0, best.noShow.has(idx)),
      kpi('Payés', t.paidCount || 0, best.paidCount.has(idx)),
    ]);
    const rateRow = (label, valueNode) => el('div', { class: 'teach-rate-row' }, [
      el('span', { text: label }), valueNode,
    ]);
    const rates = el('div', { class: 'teach-rates' }, [
      rateRow('% complétion', _teachRatePill(t.completionRate, _teachRateColor(t.completionRate), best.completionRate.has(idx))),
      rateRow('% no-show', _teachRatePill(t.noShowRate, _teachNoShowColor(t.noShowRate), best.noShowRate.has(idx))),
      rateRow('% payé', _teachRatePill(t.paidRate, _teachRateColor(t.paidRate), best.paidRate.has(idx))),
      rateRow('Score moyen', el('span', {
        class: 'teach-score' + (t.avgScore == null ? ' is-empty' : ''),
        text: t.avgScore != null ? t.avgScore.toFixed(1) : '—',
      })),
    ]);
    const card = el('div', { class: 'teach-card' + (idx === 0 ? ' teach-card-top' : '') }, [head, kpis, rates]);
    if ((t.total || 0) > 0) {
      card.appendChild(el('div', { class: 'teach-card-div' }));
      card.appendChild(_teachSourcesBlock(t.sources, t.bookers, false, 'Sources'));
    }
    return card;
  }

  function renderTeachers(data) {
    const body = document.getElementById('dashTeachersBody');
    if (!body) return;
    clear(body);
    const periodType = data.periodType === 'week' ? 'week' : 'month';
    const teachers = Array.isArray(data.teachers) ? data.teachers : [];
    const totals = data.totals || {};

    const wrap = el('div', { class: 'obj-wrap' });
    wrap.appendChild(_teachPeriodToggle());

    const nav = el('div', {
      class: 'obj-month-nav' + (periodType === 'week' ? ' teach-week-host' : ''),
      style: 'display:flex;justify-content:center;align-items:center;gap:8px;flex-wrap:wrap;margin:10px 0 2px;',
    });
    nav.appendChild(periodType === 'week' ? _teachWeekSelector(data) : _teachMonthSelector(data));
    wrap.appendChild(nav);

    // ----- Left column: per-teacher CARDS (form correction — the wide table
    // clipped on 13" screens). 3 entities × many metrics reads better as
    // cards; ranked order preserved (most paid → completion rate → completed).
    let left;
    if (!teachers.length) {
      left = el('div', { class: 'obj-board' });
      const empty = el('div', { class: 'obj-board-empty' });
      const ic = el('div', { class: 'obj-board-empty-ic' });
      ic.innerHTML = '<i data-lucide="graduation-cap"></i>';
      empty.appendChild(ic);
      empty.appendChild(el('div', { class: 'obj-board-empty-txt', text: 'Aucun professeur actif.' }));
      left.appendChild(empty);
    } else {
      // Ranking "3la 7essab nata2ij" (Ayoub, bonus decisions): most PAID
      // conversions first — the outcome that matters — then completion rate,
      // then completed count as final tie-break.
      const ranked = teachers.slice().sort((a, b) =>
        (b.paidCount || 0) - (a.paidCount || 0)
        || (b.completionRate || 0) - (a.completionRate || 0)
        || (b.completed || 0) - (a.completed || 0));

      // Best performer per metric across teachers. Only among teachers with
      // a non-zero denominator, and only when values actually differ (see
      // _teachBestSet).
      const best = {
        completed: _teachBestSet(ranked, t => t.completed, t => (t.total || 0) > 0, 'max'),
        noShow: _teachBestSet(ranked, t => t.noShow, t => (t.total || 0) > 0, 'min'),
        paidCount: _teachBestSet(ranked, t => t.paidCount, t => (t.completedLeads || 0) > 0, 'max'),
        completionRate: _teachBestSet(ranked, t => t.completionRate, t => ((t.completed || 0) + (t.noShow || 0)) > 0, 'max'),
        noShowRate: _teachBestSet(ranked, t => t.noShowRate, t => ((t.completed || 0) + (t.noShow || 0)) > 0, 'min'),
        paidRate: _teachBestSet(ranked, t => t.paidRate, t => (t.completedLeads || 0) > 0, 'max'),
      };
      left = el('div', { class: 'teach-cards' });
      ranked.forEach((t, idx) => left.appendChild(_teachCard(t, idx, best)));
    }
    wrap.appendChild(el('div', { class: 'obj-grid' }, [left, (() => {
      const side = el('div', { class: 'obj-side' });
      const scoreVal = totals.avgScore != null ? totals.avgScore.toFixed(1) : '—';
      const stats = [
        { label: 'Total tests', val: String(totals.total || 0) },
        { label: 'Complétés', val: String(totals.completed || 0), color: 'var(--success)' },
        { label: 'No-show', val: String(totals.noShow || 0), color: 'var(--danger)' },
        { label: 'Taux complétion', val: (totals.completionRate != null ? totals.completionRate.toFixed(1) : '0') + ' %', color: _teachRateColor(totals.completionRate) },
        { label: 'Taux no-show', val: (totals.noShowRate != null ? totals.noShowRate.toFixed(1) : '0') + ' %' },
        { label: 'Taux payé', val: (totals.paidRate != null ? totals.paidRate.toFixed(1) : '0') + ' %', color: 'var(--success)' },
        { label: 'Score moyen', val: scoreVal },
        { label: 'À venir', val: String(totals.upcoming || 0) },
        { label: 'Annulés', val: String(totals.cancelled || 0) },
      ];
      for (const s of stats) {
        side.appendChild(el('div', { class: 'obj-stat' }, [
          el('div', { class: 'obj-stat-num', style: s.color ? ('color:' + s.color + ';') : '', text: s.val }),
          el('div', { class: 'obj-stat-label', text: s.label }),
        ]));
      }
      // Team-wide booking-source mix — always lists bot/sales/support (a
      // zero is informative), with booker names on the sales/support lines.
      if (totals.sources) {
        side.appendChild(_teachSourcesBlock(totals.sources, totals.bookers, true, 'Sources équipe'));
      }
      return side;
    })()]));
    body.appendChild(wrap);
    refreshIcons(body);
  }

  // Private teacher salary view, shared with the admin team summary.
  const teacherEarningsViews = {};
  function ensureTeacherEarnings(admin) {
    if (admin ? !_isSystemAdmin() : state.me?.role !== 'teacher') return;
    const key = admin ? 'team' : 'self';
    if (teacherEarningsViews[key]) { teacherEarningsViews[key].refresh(); return; }
    const root = document.getElementById(admin ? 'dashTeacherEarningsBody' : 'teacherEarningsView');
    if (!root || !window.TeacherEarnings) return;
    teacherEarningsViews[key] = window.TeacherEarnings.mount(root, {
      admin,
      isVisible: () => admin ? state.activeView === 'dashboards' && DASH.activeTab === 'teacher-earnings' : state.activeView === 'teacher-earnings',
      monitorActive: () => admin
        ? _isSystemAdmin() && state.activeView === 'dashboards' && DASH.activeTab === 'teacher-earnings'
        : state.me?.role === 'teacher' && !!state.token && !E.app.classList.contains('hidden'),
      onOpenTests: () => showView('teacher-tests'),
      request: async (month, signal) => {
        const response = await api('/api/teacher-earnings?month=' + encodeURIComponent(month), { signal });
        const result = await response.json();
        if (!response.ok) throw Object.assign(new Error(result.error || 'Calcul indisponible.'), { status: response.status });
        return result;
      },
    });
    // Events stay private: only the self-scoped API can populate this view.
    state.socket?.on('test-day:update', () => {
      if (admin ? state.activeView === 'dashboards' && DASH.activeTab === 'teacher-earnings' : state.me?.role === 'teacher') teacherEarningsViews[key]?.refresh();
    });
  }

  // ============ Dashboard "Hanane AI" tab — AI voice-agent call-note log ========
  async function refreshHanane() {
    const loadingEl = document.getElementById('dashHananeLoading');
    const tableEl = document.getElementById('dashHananeTable');
    if (!tableEl) return;
    loadingEl.classList.remove('hidden');
    tableEl.classList.add('hidden');
    const { from, to } = dashCurrentRange();
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    try {
      const r = await api('/api/3cx/ai-notes?' + params.toString());
      const j = await r.json();
      if (!r.ok) { loadingEl.textContent = j.error || ('Hanane load failed: ' + r.status); return; }
      DASH.lastHanane = j;
      DASH.loadedOnceHanane = true;
      renderHanane();
      loadingEl.classList.add('hidden');
      tableEl.classList.remove('hidden');
    } catch (err) {
      console.error('[dashboards] hanane fetch error:', err);
      loadingEl.textContent = 'Erreur réseau (Hanane)';
    }
  }

  function renderHanane() {
    const tbody = document.getElementById('dashHananeTbody');
    if (!tbody || !DASH.lastHanane) return;
    clear(tbody);
    const rows = DASH.lastHanane.notes || [];
    if (!rows.length) {
      tbody.appendChild(el('tr', {}, el('td', { colspan: '6', class: 'dash-loading', text: 'Walo call dyal Hanane f had l période.' })));
      return;
    }
    rows.forEach(n => {
      const tr = el('tr');
      let dt = '—';
      try { dt = MA.loc(new Date(n.at), 'dt', 'fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { /* keep — */ }
      tr.appendChild(el('td', { class: 'dash-num', text: dt }));
      tr.appendChild(el('td', {}, [
        el('div', { style: 'font-weight:600;', text: n.contact_name || '—' }),
        el('div', { class: 'dash-sub', text: n.contact_phone || '' }),
      ]));
      tr.appendChild(el('td', { text: n.agent || '—' }));
      const noteTd = el('td', { class: 'hanane-note', title: n.note || '', text: n.note || '—' });
      if (n.note) noteTd.addEventListener('click', () => { try { toast(n.note); } catch { /* */ } });
      tr.appendChild(noteTd);
      tr.appendChild(el('td', { text: n.callback || '—' }));
      const d = Number(n.duration_seconds) || 0;
      tr.appendChild(el('td', { class: 'dash-num', text: d ? (Math.floor(d / 60) + ':' + String(d % 60).padStart(2, '0')) : '—' }));
      tbody.appendChild(tr);
    });
  }

  // ============ HubSpot group → 1-to-1 DM send (admin, multi-group) ============
  state.hsGroups = [];
  state.hsSelectedGroups = []; // array of selected group objects
  state.hsMembers = [];        // deduped aggregate across selected groups
  state.hsSending = false;
  state.hsAttachment = null;   // optional File object (image or PDF)
  let hsSearchTimer = null;
  // Per-group member cache so toggling selections doesn't refetch every time
  const hsMembersCache = new Map(); // groupId -> { name, members: [{id,name,phone}] }

  function onHsSearchInput(e) {
    const q = (e.target.value || '').trim();
    clearTimeout(hsSearchTimer);
    hsSearchTimer = setTimeout(() => searchHsGroups(q), 280);
  }

  async function searchHsGroups(q) {
    const list = document.getElementById('hsGroupsList');
    const counter = document.getElementById('hsResultsCount');
    if (!q) {
      clear(list);
      list.appendChild(el('div', { class: 'bulk-loading', text: 'Tape une recherche pour voir les groupes HubSpot.' }));
      counter.textContent = 'Cherche pour commencer';
      return;
    }
    clear(list);
    list.appendChild(el('div', { class: 'bulk-loading', text: 'Recherche…' }));
    try {
      const r = await api('/api/hubspot/groups?q=' + encodeURIComponent(q));
      const j = await r.json();
      if (j.error) {
        clear(list);
        list.appendChild(el('div', { class: 'bulk-loading', text: 'Erreur: ' + j.error }));
        return;
      }
      state.hsGroups = j.groups || [];
      counter.textContent = `${state.hsGroups.length} groupe(s)`;
      renderHsGroups();
    } catch (err) {
      clear(list);
      list.appendChild(el('div', { class: 'bulk-loading', text: 'Recherche échouée: ' + (err.message || err) }));
    }
  }

  function renderHsGroups() {
    const list = document.getElementById('hsGroupsList');
    clear(list);
    if (!state.hsGroups.length) {
      list.appendChild(el('div', { class: 'bulk-loading', text: 'Aucun groupe trouvé.' }));
      return;
    }
    const selectedIds = new Set(state.hsSelectedGroups.map(g => g.id));
    for (const g of state.hsGroups) {
      const cb = el('input', { type: 'checkbox' });
      cb.checked = selectedIds.has(g.id);
      cb.addEventListener('click', e => e.stopPropagation());
      cb.addEventListener('change', () => toggleHsGroup(g));

      const meta = [g.schedule_days, g.schedule_time, g.group_class_type].filter(Boolean).join(' · ');
      const row = el('div', { class: 'bulk-group-row' + (cb.checked ? ' hs-selected' : ''), 'data-hs-id': g.id });
      row.appendChild(cb);
      row.appendChild(el('div', { class: 'bulk-g-name', text: g.name || '(sans nom)' }));
      if (meta) row.appendChild(el('span', { class: 'bulk-g-meta', text: meta }));
      row.addEventListener('click', () => {
        cb.checked = !cb.checked;
        toggleHsGroup(g);
      });
      list.appendChild(row);
    }
    refreshIcons();
  }

  async function toggleHsGroup(group) {
    const idx = state.hsSelectedGroups.findIndex(g => g.id === group.id);
    if (idx >= 0) {
      state.hsSelectedGroups.splice(idx, 1);
    } else {
      state.hsSelectedGroups.push(group);
      // Pre-fetch members in the background — speeds up the aggregate render
      if (!hsMembersCache.has(group.id)) await loadHsGroupMembers(group);
    }
    // Re-render group list (selected styling) + members aggregate
    renderHsGroups();
    rebuildHsAggregate();
  }

  async function loadHsGroupMembers(group) {
    if (hsMembersCache.has(group.id)) return hsMembersCache.get(group.id);
    try {
      const r = await api('/api/hubspot/groups/' + encodeURIComponent(group.id) + '/students');
      const j = await r.json();
      if (j.error) return null;
      const entry = { name: group.name, members: j.students || [] };
      hsMembersCache.set(group.id, entry);
      return entry;
    } catch { return null; }
  }

  // Aggregate members across all currently-selected groups, deduped by phone.
  function rebuildHsAggregate() {
    const seenPhones = new Set();
    const aggregated = [];
    let pending = 0;
    for (const g of state.hsSelectedGroups) {
      const cached = hsMembersCache.get(g.id);
      if (!cached) { pending++; continue; }
      for (const m of cached.members) {
        const key = String(m.phone || '').replace(/\D/g, '');
        if (!key || seenPhones.has(key)) continue;
        seenPhones.add(key);
        aggregated.push(m);
      }
    }
    state.hsMembers = aggregated;

    // Header: render each selected group as a removable chip so the agent can
    // drop a group from the selection without having to re-search and re-click
    // it in the left list. Earlier this was a plain string, which gave them no
    // way to undo a click.
    const header = document.getElementById('hsSelectedGroupName');
    clear(header);
    if (state.hsSelectedGroups.length === 0) {
      header.textContent = '';
    } else {
      const count = state.hsSelectedGroups.length;
      header.appendChild(el('span', {
        style: 'color:var(--gray-600);font-size:13px;margin-right:8px;',
        text: count + (count === 1 ? ' groupe : ' : ' groupes : ')
      }));
      const wrap = el('span', { style: 'display:inline-flex;flex-wrap:wrap;gap:4px;align-items:center;' });
      for (const g of state.hsSelectedGroups) {
        const chip = el('span', {
          class: 'hs-group-chip',
          style: 'display:inline-flex;align-items:center;gap:6px;padding:3px 8px;border-radius:12px;background:#eef2ff;color:#3730a3;font-size:12px;font-weight:600;'
        });
        chip.appendChild(el('span', { text: g.name || '?' }));
        const xBtn = el('button', {
          type: 'button',
          title: 'Retirer ce groupe',
          style: 'background:none;border:0;padding:0;margin:0;cursor:pointer;display:inline-flex;align-items:center;color:#6366f1;'
        });
        xBtn.appendChild(lucideIcon('x', { style: 'width:12px;height:12px;' }));
        xBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          toggleHsGroup(g);
        });
        chip.appendChild(xBtn);
        wrap.appendChild(chip);
      }
      header.appendChild(wrap);
    }

    renderHsMembers(pending);
  }

  function renderHsMembers(pending) {
    const list = document.getElementById('hsMembersList');
    clear(list);
    const sendBtn = document.getElementById('hsSendBtn');
    const createBtn = document.getElementById('hsCreateGroupBtn');
    if (!state.hsSelectedGroups.length) {
      list.appendChild(el('div', { class: 'bulk-loading', text: 'Choisis un ou plusieurs groupes à gauche.' }));
      if (sendBtn) sendBtn.disabled = true;
      if (createBtn) createBtn.disabled = true;
      document.getElementById('hsSendCount').textContent = '0';
      return;
    }
    if (pending) {
      list.appendChild(el('div', { class: 'bulk-loading', text: `Chargement des membres de ${pending} groupe(s)…` }));
      if (sendBtn) sendBtn.disabled = true;
      if (createBtn) createBtn.disabled = true;
      return;
    }
    if (!state.hsMembers.length) {
      list.appendChild(el('div', { class: 'bulk-loading', text: 'Aucun membre avec téléphone dans les groupes sélectionnés.' }));
      if (sendBtn) sendBtn.disabled = true;
      if (createBtn) createBtn.disabled = true;
      document.getElementById('hsSendCount').textContent = '0';
      return;
    }
    for (const m of state.hsMembers) {
      const row = el('div', { class: 'hs-member-row' + (m.phone ? '' : ' no-phone') }, [
        el('span', { class: 'hs-member-name', text: m.name || '(sans nom)' }),
        el('span', { class: 'hs-member-phone', text: m.phone || 'Pas de téléphone' }),
      ]);
      list.appendChild(row);
    }
    if (sendBtn) sendBtn.disabled = false;
    if (createBtn) createBtn.disabled = false;
    document.getElementById('hsSendCount').textContent = String(state.hsMembers.length);
  }

  async function hsSend() {
    if (state.hsSending) return;
    if (!state.hsSelectedGroups.length) { toast('Choisis au moins un groupe', 'error'); return; }
    if (state.clientState !== 'READY') { toast('WhatsApp non connecté', 'error'); return; }
    const message = document.getElementById('hsMessageInput').value.trim();
    if (!message && !state.hsAttachment) { toast('Tape un message ou joins un fichier', 'error'); return; }
    const delay = Number(document.getElementById('hsDelay').value) || 5;
    const mediaLabel = state.hsAttachment ? ` + 1 fichier (${state.hsAttachment.name})` : '';
    if (!confirm(`Envoyer à ${state.hsMembers.length} membres dans ${state.hsSelectedGroups.length} groupe(s)${mediaLabel} ?`)) return;

    state.hsSending = true;
    document.getElementById('hsCancelBtn').classList.remove('hidden');
    document.getElementById('hsSendBtn').disabled = true;
    clear(document.getElementById('hsProgressLog'));
    showHsProgress(0, state.hsMembers.length, 'Démarrage…');

    try {
      // FormData so we can attach an optional file alongside the JSON-encoded fields
      const fd = new FormData();
      fd.append('groupIds', JSON.stringify(state.hsSelectedGroups.map(g => g.id)));
      fd.append('message', message);
      fd.append('delay', String(delay));
      if (state.hsAttachment) fd.append('file', state.hsAttachment, state.hsAttachment.name);

      const r = await fetchT('/api/hubspot/send-to-group', {
        method: 'POST', headers: authHeaders(), body: fd,
      }, 120000);
      const j = await r.json().catch(() => ({}));
      if (j.error) { toast(j.error, 'error'); resetHsSendingUi(); }
    } catch (err) {
      toast('Échec: ' + (err.message || err), 'error');
      resetHsSendingUi();
    }
  }

  function hsOnAttach(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) { state.hsAttachment = null; document.getElementById('hsAttachStatus').textContent = 'Pas de fichier'; return; }
    state.hsAttachment = file;
    document.getElementById('hsAttachStatus').textContent = file.name + ' (' + Math.round(file.size / 1024) + ' KB)';
  }

  // ============ Group tracking modal + Create WA group modal (REMOVED — Sprint 3) ============
  // Removed alongside /api/group-trackings, /api/group-student-status,
  // /api/resend-group-reminder, /api/create-group server routes.

  function resetHsSendingUi() {
    state.hsSending = false;
    document.getElementById('hsSendBtn').disabled = false;
    document.getElementById('hsCancelBtn').classList.add('hidden');
  }

  function showHsProgress(current, total, label) {
    const wrap = document.getElementById('hsProgress');
    wrap.classList.remove('hidden');
    const pct = total ? (current / total) * 100 : 0;
    document.getElementById('hsProgressFill').style.width = pct.toFixed(1) + '%';
    document.getElementById('hsProgressLabel').textContent = `${label || ''}  ${current}/${total}`;
  }

  function appendHsLog(text, cls) {
    const log = document.getElementById('hsProgressLog');
    log.appendChild(el('div', { class: cls || '', text }));
    log.scrollTop = log.scrollHeight;
    while (log.children.length > 80) log.removeChild(log.firstChild);
  }

  async function hsCancel() {
    try { await api('/api/cancel-send', { method: 'POST' }); toast('Annulation…', ''); }
    catch { /* ignore */ }
  }

  // ============ Team / users module (admin only) ============
  state.teamUsers = [];
  state.teamEditing = null;

  // Super Admin profile header — fills the card at the top of the Super
  // Admin page with Ayoub's account info. Only renders for super admins
  // (the page itself is gated by data-super-admin-only in the topbar).
  function renderSuperAdminProfile() {
    const me = state.me;
    if (!me) return;
    const setText = (id, val) => { const el2 = document.getElementById(id); if (el2) el2.textContent = val; };
    const avatar = document.getElementById('superadminAvatar');
    if (avatar) {
      avatar.textContent = initials(userLabel(me.username, me.displayName || me.username)) || '?';
      // Remove old color class then add fresh deterministic colour by user id.
      avatar.className = 'avatar avatar-lg superadmin-avatar ' + (avatarColorClass(me.id) || 'avatar-c1');
    }
    setText('superadminName', userLabel(me.username, me.displayName || me.username || 'Super Admin'));
    setText('superadminUsername', adminLabelFor(me.username) ? '' : '@' + (me.username || ''));
    setText('superadminCreatedAt', me.createdAt ? new Date(me.createdAt).toISOString().slice(0, 10) : '—');
    setText('superadminLastLogin', me.lastLoginAt ? MA.loc(new Date(me.lastLoginAt), 'dt', 'en-GB', { hour12: false }) : '—');
    refreshIcons(document.querySelector('.superadmin-profile'));
    // Platform mappings section — refresh the HubSpot card stats + bind the
    // "Open mapping" button to the existing team-owner modal (same path the
    // Tickets toolbar uses). Future platforms get their own card here.
    renderSuperAdminMappings();
  }

  async function renderSuperAdminMappings() {
    const countEl = document.getElementById('superadminHubspotMappedCount');
    const btn = document.getElementById('superadminHubspotMapBtn');
    if (btn && !btn._wired) {
      btn._wired = true;
      btn.addEventListener('click', () => openTeamOwnerMapModal());
    }
    if (!countEl) return;
    try {
      const j = await api('/api/users/assignable').then(r => r.json());
      const users = j.users || [];
      const mapped = users.filter(u => u.hubspotOwnerId).length;
      countEl.textContent = `${mapped} / ${users.length} mapped`;
    } catch { countEl.textContent = '—'; }
    refreshIcons(document.querySelector('.superadmin-mappings'));
  }

  async function loadTeamUsers() {
    const list = document.getElementById('teamUsersList');
    clear(list); list.appendChild(el('div', { class: 'bulk-loading', text: 'Loading users…' }));
    // Hide admin-only chrome (Add user button) for agents — they should see the
    // roster but not the management controls.
    const isAdmin = state.me?.role === 'admin';
    const addBtn = document.getElementById('teamAddUserBtn');
    if (addBtn) addBtn.classList.toggle('hidden', !isAdmin);
    const search = document.getElementById('teamUsersSearch');
    if (search && !search._wired) {
      search._wired = true;
      search.addEventListener('input', () => { state.teamUsersQuery = search.value; renderTeamUsers(); });
      search.addEventListener('keydown', e => { if (e.key === 'Escape') { search.value = ''; state.teamUsersQuery = ''; renderTeamUsers(); } });
    }
    const url = isAdmin ? '/api/admin/users' : '/api/users/assignable';
    try {
      const r = await api(url);
      const j = await r.json();
      state.teamUsers = j.users || [];
      renderTeamUsers();
    } catch (err) {
      clear(list); list.appendChild(el('div', { class: 'bulk-loading', text: 'Failed: ' + (err.message || err) }));
    }
  }

  // Roster order: the people who work here today first (active before
  // inactive), then by role weight so the sales floor sits above candidates
  // and teachers, then by name. The API returns creation order, which buries
  // the newest agent — exactly the one being debugged — at the bottom.
  const TEAM_ROLE_WEIGHT = { admin: 0, super_sales: 1, agent: 2, trial_agent: 3, teacher: 4, meta_review: 5, viewer: 6 };
  function _teamUsersSorted() {
    const q = String(state.teamUsersQuery || '').trim().toLowerCase();
    return state.teamUsers
      .filter(u => !q || [u.displayName, u.username, u.role].some(v => String(v || '').toLowerCase().includes(q)))
      .sort((a, b) => (Number(!a.active) - Number(!b.active))
        || ((TEAM_ROLE_WEIGHT[a.role] ?? 9) - (TEAM_ROLE_WEIGHT[b.role] ?? 9))
        || String(userLabel(a.username, a.displayName || a.username)).localeCompare(String(userLabel(b.username, b.displayName || b.username))));
  }

  // Who the super admin may "Log as": anyone active who is not an admin and
  // not themselves — the same rule the server enforces.
  function _canLogAs(u) {
    return !!(state.me?.superAdmin && !state.impersonatedBy && u && u.active && u.role !== 'admin' && u.id !== state.me?.id);
  }

  function renderTeamUsers() {
    const list = document.getElementById('teamUsersList');
    clear(list);
    const isAdmin = state.me?.role === 'admin';
    const users = _teamUsersSorted();
    const count = document.getElementById('teamUsersCount');
    if (count) {
      const active = state.teamUsers.filter(u => u.active).length;
      count.textContent = active + ' actifs';
      count.hidden = !state.teamUsers.length;
    }
    if (!users.length) {
      list.appendChild(el('div', { class: 'team-empty' }, [
        lucideIcon(state.teamUsers.length ? 'search-x' : 'users'),
        el('div', { class: 'team-empty-title', text: state.teamUsers.length ? 'Aucun membre ne correspond' : 'No users yet.' }),
        state.teamUsers.length ? el('div', { class: 'team-empty-sub', text: 'Essayez un autre nom ou un rôle (agent, teacher…).' }) : null,
      ].filter(Boolean)));
      refreshIcons(list);
      return;
    }
    for (const u of users) {
      const row = el('div', {
        class: 'team-user-row role-' + (u.role || 'agent') + (!u.active ? ' inactive' : ''),
        'data-user-id': u.id || '',
      });
      const _tuName = userLabel(u.username, u.displayName || u.username);
      row.appendChild(personalAvatar(u, 'tu-avatar'));
      const _tuPlace = isAdmin ? _teamPlaceLine(u) : null;
      row.appendChild(el('div', { class: 'tu-ident' }, (adminLabelFor(u.username)
        ? [el('div', { class: 'tu-name', text: _tuName })]
        : [
          el('div', { class: 'tu-name', text: _tuName }),
          el('div', { class: 'tu-username', text: '@' + u.username })
        ]).concat(_tuPlace ? [_tuPlace] : [])));
      row.appendChild(el('span', { class: 'tu-role-pill role-' + (u.role || 'agent'), text: String(u.role || 'agent').replace('_', ' ') }));
      // "Accès" — the two silent breakers for a new agent. Only the admin list
      // carries `softphone`; the assignable list (agents) shows a dash.
      const access = el('div', { class: 'tu-access' });
      const isStaff = ['admin', 'super_sales', 'agent', 'trial_agent'].includes(u.role);
      if (isAdmin && isStaff) {
        access.appendChild(el('span', {
          class: 'tu-chip' + (u.hubspotOwnerId ? ' on' : ' off'),
          title: u.hubspotOwnerId ? 'HubSpot owner mappé (' + u.hubspotOwnerId + ')' : 'Pas de HubSpot owner — ses Contacts restent vides',
        }, [lucideIcon('database'), el('span', { text: 'HubSpot' })]));
        access.appendChild(el('span', {
          class: 'tu-chip' + (u.softphone ? ' on' : ' off'),
          title: u.softphone ? 'Ligne 3CX provisionnée (softphone.json)' : 'Pas de ligne 3CX — «3CX n\'est pas activé pour ce compte»',
        }, [lucideIcon('phone'), el('span', { text: '3CX' })]));
        // Location alert of the last 7 days (other city than declared, or the
        // same account live in two cities). Opens the editor's history.
        if (u.access && u.access.alerts7d > 0) {
          access.appendChild(el('span', {
            class: 'tu-chip alert',
            title: (u.access.lastAlert?.message || 'Alerte de connexion') + (u.access.alerts7d > 1 ? ` (+${u.access.alerts7d - 1} autre(s) cette semaine)` : ''),
          }, [lucideIcon('map-pin'), el('span', { text: 'Lieu' })]));
        }
      } else {
        access.appendChild(el('span', { class: 'tu-access-na', text: '—' }));
      }
      row.appendChild(access);
      row.appendChild(el('span', {
        class: 'tu-last-login',
        title: u.lastLoginAt ? MA.loc(new Date(u.lastLoginAt), 'dt', 'fr-FR') : 'Jamais connecté',
        text: u.lastLoginAt ? formatChatDate(new Date(u.lastLoginAt).getTime()) : 'jamais',
      }));
      const actions = el('div', { class: 'tu-actions' });
      if (_canLogAs(u)) {
        const logAs = el('button', {
          class: 'tu-logas-btn', type: 'button',
          title: 'Ouvrir Leads7 comme ' + (userLabel(u.username, u.displayName || u.username)) + ' — voir exactement ce qu\'il/elle voit',
        }, [lucideIcon('user-round-search'), el('span', { text: 'Log as' })]);
        logAs.addEventListener('click', e => { e.stopPropagation(); startImpersonation(u); });
        actions.appendChild(logAs);
      }
      // Edit + click-to-edit only for admins; agents see the roster read-only.
      if (isAdmin) {
        const editBtn = el('button', { class: 'tu-edit-btn', type: 'button', 'aria-label': 'Edit', title: 'Modifier' }, [lucideIcon('pencil')]);
        editBtn.addEventListener('click', e => { e.stopPropagation(); openUserEditor(u); });
        actions.appendChild(editBtn);
        row.addEventListener('click', () => openUserEditor(u));
      }
      row.appendChild(actions);
      list.appendChild(row);
    }
    refreshIcons(list);
  }

  // ── Where each member opens Leads7 from (server: lib/agent-access.js) ──────
  // City comes from Cloudflare; without it only the country is known. IP
  // geolocation names the operator's city — a signal to look at, not proof.
  function _accessWhere(p) {
    if (!p) return '';
    // GPS (the member's real city) wins over the IP's city (the operator's).
    if (p.gps && p.gps.city) return p.gps.city + ' (GPS)';
    if (p.hosting) return (p.isp || 'VPN / serveur') + ' — lieu caché';
    if (p.via === 'team-app' && !p.city && !p.country) return 'App téléphone (lieu inconnu)';
    const where = p.city || p.country || 'Lieu inconnu';
    return p.city && p.country && p.country !== 'MA' ? where + ', ' + p.country : where;
  }
  function _accessDevice(p) {
    if (!p) return '';
    return [p.os, p.browser].filter(v => v && v !== 'Autre').join(' · ') || 'Appareil inconnu';
  }
  function _accessIsp(p) {
    if (!p || !p.isp) return '';
    return p.hosting ? '🛡️ ' + p.isp : p.isp;
  }

  // Third line under the name on the Super Admin roster.
  function _teamPlaceLine(u) {
    const a = u && u.access;
    if (!a) return null;
    const isp = _accessIsp(a);
    const line = el('div', {
      class: 'tu-place' + (a.alerts7d > 0 ? ' alert' : '') + (a.hosting ? ' vpn' : ''),
      title: 'Vu pour la dernière fois ' + MA.loc(new Date(a.lastSeen), 'dt', 'fr-FR')
        + (a.host ? ' sur ' + a.host : '') + (isp ? ' · ' + a.isp : '')
        + (a.deviceCount > 1 ? ' · ' + a.deviceCount + ' appareils' : '')
        + (u.homeCity ? ' — ville déclarée : ' + u.homeCity : ''),
    }, [lucideIcon(a.hosting ? 'shield-alert' : 'map-pin'), el('span', {
      text: _accessWhere(a) + ' · ' + _accessDevice(a) + (isp && !a.hosting ? ' · ' + a.isp : ''),
    })]);
    return line;
  }

  // "Connexions" section of the Edit-user modal: every place (IP + device)
  // newest first, then the alerts.
  async function _loadUserAccess(user) {
    const box = document.getElementById('ueAccessSection');
    const list = document.getElementById('ueAccessList');
    if (!box || !list) return;
    clear(list);
    if (!user || !user.id) { box.classList.add('hidden'); return; }
    box.classList.remove('hidden');
    list.appendChild(el('div', { class: 'ue-access-empty', text: 'Chargement…' }));
    let j = null;
    try { j = await api('/api/admin/users/' + encodeURIComponent(user.id) + '/access').then(r => r.json()); }
    catch { j = null; }
    if (state.teamEditing !== user) return; // modal moved on
    clear(list);
    if (!j || j.error) { list.appendChild(el('div', { class: 'ue-access-empty', text: 'Historique indisponible' })); return; }
    for (const a of (j.alerts || []).slice(0, 5)) {
      list.appendChild(el('div', { class: 'ue-access-alert' }, [
        lucideIcon('triangle-alert'),
        el('span', { text: a.message || a.kind }),
        el('span', { class: 'ue-access-when', text: formatChatDate(new Date(a.at).getTime()) }),
      ]));
    }
    const places = j.places || [];
    if (!places.length) {
      list.appendChild(el('div', { class: 'ue-access-empty', text: 'Aucune connexion enregistrée depuis l\'activation du suivi.' }));
    }
    for (const p of places.slice(0, 20)) {
      const gpsCity = p.gps && p.gps.city;
      const off = j.homeCity && (gpsCity || p.city) && String(gpsCity || p.city).toLowerCase() !== String(j.homeCity).toLowerCase();
      const ispLine = _accessIsp(p);
      const metaLeft = [(p.ip || '—'), ispLine, p.host].filter(Boolean).join(' · ');
      list.appendChild(el('div', { class: 'ue-access-row' + (off ? ' off' : '') + (p.hosting ? ' vpn' : '') }, [
        el('div', { class: 'ue-access-main' }, [
          el('span', { class: 'ue-access-where', text: _accessWhere(p) + (p.region && p.city && !gpsCity && p.region !== p.city ? ' (' + p.region + ')' : '') }),
          el('span', { class: 'ue-access-dev', text: _accessDevice(p) + (p.deviceType === 'mobile' ? ' (mobile)' : '') + (p.gpsStatus === 'denied' ? ' · GPS refusé' : '') }),
        ]),
        el('div', { class: 'ue-access-meta' }, [
          el('span', { text: metaLeft }),
          el('span', {
            class: 'ue-access-when',
            title: 'Première fois ' + MA.loc(new Date(p.firstSeen), 'dt', 'fr-FR') + (p.lastLoginAt ? ' · login ' + MA.loc(new Date(p.lastLoginAt), 'dt', 'fr-FR') : ''),
            text: formatChatDate(new Date(p.lastSeen).getTime()),
          }),
        ]),
      ]));
    }

    // Known machines (device fingerprints) — a new one is the clearest sign of
    // a shared or stolen login.
    const devices = j.devices || [];
    if (devices.length) {
      list.appendChild(el('div', { class: 'ue-access-subhead', text: 'Appareils (' + devices.length + ')' }));
      for (const d of devices.slice(0, 10)) {
        list.appendChild(el('div', { class: 'ue-access-row device' }, [
          el('div', { class: 'ue-access-main' }, [
            lucideIcon('monitor'),
            el('span', { class: 'ue-access-where', text: d.label || 'Appareil inconnu' }),
          ]),
          el('span', { class: 'ue-access-when', text: formatChatDate(new Date(d.lastSeen).getTime()) }),
        ]));
      }
    }

    // Rejected logins on this account — a burst is worth a look.
    const failures = j.failures || [];
    if (failures.length) {
      list.appendChild(el('div', { class: 'ue-access-subhead', text: 'Connexions refusées (' + failures.length + ')' }));
      for (const f of failures.slice(0, 8)) {
        const where = [f.city || f.country, f.isp].filter(Boolean).join(' · ') || (f.ip || 'lieu inconnu');
        list.appendChild(el('div', { class: 'ue-access-row fail' }, [
          el('div', { class: 'ue-access-main' }, [
            lucideIcon('key-round'),
            el('span', { class: 'ue-access-where', text: (f.hosting ? '🛡️ ' : '') + where }),
          ]),
          el('span', { class: 'ue-access-when', text: formatChatDate(new Date(f.at).getTime()) }),
        ]));
      }
    }
    refreshIcons(list);
  }

  // ── Sécurité & accès ──────────────────────────────────────────────────────
  // One page over the whole team's access: who is online and from where, the
  // device + ISP, the VPN / two-places / abroad flags, every recent alert and
  // every rejected login. Data: GET /api/admin/security/overview (super admin).
  let _secTimer = null;
  function initSecurityView() {
    const btn = document.getElementById('secRefreshBtn');
    if (btn && !btn._wired) { btn._wired = true; btn.addEventListener('click', () => loadSecurityOverview()); }
    loadSecurityOverview();
    // Refresh while the page is open so "online now" stays live.
    if (_secTimer) clearInterval(_secTimer);
    _secTimer = setInterval(() => { if (state.activeView === 'security') loadSecurityOverview(); }, 60 * 1000);
  }

  async function loadSecurityOverview() {
    const listEl = document.getElementById('secTeamList');
    let j = null;
    try { j = await api('/api/admin/security/overview').then(r => r.ok ? r.json() : Promise.reject(r.status)); }
    catch { if (listEl) { clear(listEl); listEl.appendChild(el('div', { class: 'sec-empty', text: 'Indisponible' })); } return; }
    if (state.activeView !== 'security') return;
    _secRenderBanner(j);
    _secRenderCounters(j);
    _secRenderTeam(j);
    _secRenderAlerts(j);
    _secRenderFailures(j);
    refreshIcons(document.getElementById('securityView'));
  }

  function _secRenderBanner(j) {
    const b = document.getElementById('secCfBanner');
    if (b) b.classList.toggle('hidden', !!j.cloudflareCityOn);
  }

  function _secRenderCounters(j) {
    const box = document.getElementById('secCounters');
    if (!box) return;
    clear(box);
    const c = j.counters || {};
    const cards = [
      { k: 'online', label: 'En ligne', val: c.online, icon: 'wifi', tone: 'ok' },
      { k: 'total', label: 'Suivis', val: c.total, icon: 'users', tone: '' },
      { k: 'alerts', label: 'Avec alerte (7j)', val: c.withAlerts, icon: 'triangle-alert', tone: c.withAlerts ? 'warn' : '' },
      { k: 'vpn', label: 'VPN / serveur', val: c.onVpn, icon: 'shield-alert', tone: c.onVpn ? 'danger' : '' },
      { k: 'abroad', label: 'À l\'étranger', val: c.abroad, icon: 'globe', tone: c.abroad ? 'warn' : '' },
      { k: 'two', label: '2 villes (7j)', val: c.twoPlaces, icon: 'map-pin', tone: c.twoPlaces ? 'warn' : '' },
    ];
    for (const card of cards) {
      box.appendChild(el('div', { class: 'sec-counter' + (card.tone ? ' ' + card.tone : '') }, [
        lucideIcon(card.icon),
        el('div', { class: 'sec-counter-body' }, [
          el('div', { class: 'sec-counter-val', text: String(card.val == null ? '—' : card.val) }),
          el('div', { class: 'sec-counter-label', text: card.label }),
        ]),
      ]));
    }
  }

  function _secRenderTeam(j) {
    const box = document.getElementById('secTeamList');
    if (!box) return;
    clear(box);
    const rows = j.users || [];
    if (!rows.length) { box.appendChild(el('div', { class: 'sec-empty', text: 'Aucune connexion enregistrée depuis l\'activation du suivi.' })); return; }
    for (const u of rows) {
      const where = _accessWhere(u);
      const isp = _accessIsp(u);
      const flags = el('div', { class: 'sec-row-flags' });
      if (u.hosting) flags.appendChild(el('span', { class: 'sec-flag danger', title: 'VPN / serveur — le vrai lieu est caché' }, [lucideIcon('shield-alert'), el('span', { text: 'VPN' })]));
      if (u.country && u.country !== 'MA') flags.appendChild(el('span', { class: 'sec-flag warn', title: 'Connexion depuis l\'étranger' }, [lucideIcon('globe'), el('span', { text: u.country })]));
      if (u.citiesThisWeek > 1) flags.appendChild(el('span', { class: 'sec-flag warn', title: u.citiesThisWeek + ' villes cette semaine' }, [lucideIcon('map-pin'), el('span', { text: u.citiesThisWeek + ' villes' })]));
      if (u.gpsStatus === 'denied') flags.appendChild(el('span', { class: 'sec-flag', title: 'A refusé le partage de localisation' }, [lucideIcon('crosshair'), el('span', { text: 'GPS refusé' })]));
      if (u.alerts7d > 0) flags.appendChild(el('span', { class: 'sec-flag danger', title: u.lastAlert?.message || 'Alerte' }, [lucideIcon('triangle-alert'), el('span', { text: u.alerts7d + ' alerte' + (u.alerts7d > 1 ? 's' : '') })]));

      const meta = [];
      if (u.homeCity) meta.push('déclarée : ' + u.homeCity);
      meta.push(u.placesCount + ' lieu' + (u.placesCount > 1 ? 'x' : ''));
      if (u.deviceCount) meta.push(u.deviceCount + ' appareil' + (u.deviceCount > 1 ? 's' : ''));

      const row = el('div', { class: 'sec-row' + (u.alerts7d > 0 || u.hosting ? ' flagged' : '') });
      row.appendChild(el('span', { class: 'sec-row-dot' + (u.online ? ' online' : '') , title: u.online ? 'En ligne' : 'Hors ligne' }));
      row.appendChild(el('div', { class: 'sec-row-main' }, [
        el('div', { class: 'sec-row-name' }, [
          el('strong', { text: userLabel(u.username, u.displayName) }),
          el('span', { class: 'sec-row-role', text: String(u.role || '').replace('_', ' ') }),
        ]),
        el('div', { class: 'sec-row-where' }, [
          lucideIcon(u.hosting ? 'shield-alert' : 'map-pin'),
          el('span', { text: where + ' · ' + _accessDevice(u) + (isp && !u.hosting ? ' · ' + u.isp : '') }),
        ]),
        el('div', { class: 'sec-row-meta', text: meta.join(' · ') }),
        flags,
      ]));
      row.appendChild(el('div', { class: 'sec-row-when', title: MA.loc(new Date(u.lastSeen), 'dt', 'fr-FR') }, [
        el('span', { class: 'sec-row-when-label', text: u.online ? 'maintenant' : formatChatDate(new Date(u.lastSeen).getTime()) }),
        el('button', { class: 'sec-row-detail', type: 'button', title: 'Voir toutes les connexions' }, [lucideIcon('chevron-right')]),
      ]));
      // Clicking the row opens that member's full Connexions history (the
      // existing Edit-user modal, scrolled to the access section).
      row.addEventListener('click', () => _secOpenUser(u.id));
      box.appendChild(row);
    }
  }

  async function _secOpenUser(userId) {
    // Reuse the Super Admin roster's editor. Load the user record if the Team
    // page hasn't been opened this session.
    try {
      let u = (state.teamUsers || []).find(x => x.id === userId);
      if (!u) {
        const data = await api('/api/admin/users').then(r => r.json());
        state.teamUsers = data.users || [];
        u = state.teamUsers.find(x => x.id === userId);
      }
      if (u && typeof openUserEditor === 'function') openUserEditor(u);
    } catch { /* ignore */ }
  }

  function _secAlertIcon(kind) {
    return ({
      two_places: 'users', two_places_gps: 'users', gps_outside_home: 'map-pin',
      outside_home_city: 'map-pin', foreign_country: 'globe', vpn: 'shield-alert',
      new_device: 'monitor', failed_logins: 'key-round', failed_login_vpn: 'key-round',
    })[kind] || 'triangle-alert';
  }

  function _secRenderAlerts(j) {
    const box = document.getElementById('secAlerts');
    if (!box) return;
    clear(box);
    const alerts = j.alerts || [];
    if (!alerts.length) { box.appendChild(el('div', { class: 'sec-empty', text: 'Aucune alerte cette semaine. ✅' })); return; }
    for (const a of alerts.slice(0, 40)) {
      box.appendChild(el('div', { class: 'sec-alert' }, [
        lucideIcon(_secAlertIcon(a.kind)),
        el('div', { class: 'sec-alert-body' }, [
          el('div', { class: 'sec-alert-msg', text: a.message || a.kind }),
          el('div', { class: 'sec-alert-when', text: formatChatDate(new Date(a.at).getTime()) }),
        ]),
      ]));
    }
  }

  function _secRenderFailures(j) {
    const box = document.getElementById('secFailures');
    if (!box) return;
    clear(box);
    const failures = j.failures || [];
    if (!failures.length) { box.appendChild(el('div', { class: 'sec-empty', text: 'Aucune.' })); return; }
    for (const f of failures.slice(0, 30)) {
      const who = f.username ? '@' + f.username : 'compte inconnu';
      const where = [f.city || f.country, f.isp].filter(Boolean).join(' · ') || (f.ip || 'lieu inconnu');
      box.appendChild(el('div', { class: 'sec-fail' }, [
        lucideIcon('key-round'),
        el('div', { class: 'sec-fail-body' }, [
          el('div', { class: 'sec-fail-who', text: who + (f.known ? '' : ' (n\'existe pas)') }),
          el('div', { class: 'sec-fail-where', text: (f.hosting ? '🛡️ ' : '') + where }),
        ]),
        el('span', { class: 'sec-fail-when', text: formatChatDate(new Date(f.at).getTime()) }),
      ]));
    }
  }


  function openUserEditor(user) {
    state.teamEditing = user || null;
    document.getElementById('userEditorTitle').textContent = user ? 'Edit ' + (userLabel(user.username, user.displayName || user.username)) : 'Add team member';
    document.getElementById('ueUsername').value = user?.username || '';
    document.getElementById('ueUsername').disabled = !!user;
    document.getElementById('ueDisplayName').value = user?.displayName || '';
    const pwInp = document.getElementById('uePassword');
    pwInp.value = '';
    pwInp.type = 'password';
    document.getElementById('uePasswordHint').textContent = user ? '(leave empty to keep current)' : '';
    // Reset reveal state + the post-generate hint
    const toggleBtn = document.getElementById('uePasswordToggleBtn');
    if (toggleBtn) toggleBtn.innerHTML = '<i data-lucide="eye"></i>';
    const genHint = document.getElementById('uePasswordGenHint');
    if (genHint) genHint.style.display = 'none';
    // Current stored password (admin-only, from GET /api/admin/users). Always
    // reopen masked; the real value is read from state.teamEditing on demand.
    const curRow = document.getElementById('ueCurrentPwRow');
    const curVal = document.getElementById('ueCurrentPwValue');
    const curToggle = document.getElementById('ueCurrentPwToggleBtn');
    // Clear any cached fetched plaintext so a reopened modal re-fetches (and
    // re-logs) instead of reusing a stale value.
    if (state.teamEditing) state.teamEditing._pwCache = undefined;
    if (curRow && curVal) {
      if (user && user.hasStoredPassword) {
        curRow.classList.remove('hidden');
        curVal.textContent = '••••••••';
        curVal.dataset.revealed = '0';
        if (curToggle) curToggle.innerHTML = '<i data-lucide="eye"></i>';
      } else {
        curRow.classList.add('hidden');
      }
    }
    document.getElementById('ueRole').value = user?.role || 'agent';
    document.getElementById('ueActive').checked = user ? !!user.active : true;
    document.getElementById('ueWhatsappPhone').value = user?.whatsappPhone || '';
    const _hc = document.getElementById('ueHomeCity');
    if (_hc) _hc.value = user?.homeCity || '';
    _loadUserAccess(user);
    // Per-user payment-deletion permission (mig 0058). Sales admins always
    // have it, so the box shows checked+disabled for them.
    const _cdp = document.getElementById('ueCanDeletePayments');
    if (_cdp) {
      const alwaysAllowed = ['admin', 'super_admin', 'super_sales'].includes(String(user?.role || '').toLowerCase());
      _cdp.checked = alwaysAllowed || !!user?.canDeletePayments;
      _cdp.disabled = alwaysAllowed;
    }
    // Per-user phone-mask exemption — same always-on-for-admins shape.
    const _csp = document.getElementById('ueCanSeePhones');
    if (_csp) {
      const alwaysAllowed = ['admin', 'super_admin', 'super_sales'].includes(String(user?.role || '').toLowerCase());
      _csp.checked = alwaysAllowed || user?.canSeePhones === true;
      _csp.disabled = alwaysAllowed;
    }
    document.getElementById('userEditorError').classList.add('hidden');
    const delBtn = document.getElementById('userEditorDeleteBtn');
    if (user && user.id !== state.me?.id) delBtn.classList.remove('hidden');
    else delBtn.classList.add('hidden');
    openModal('userEditorModal');
  }

  // Generate a memorable but secure password: 14 chars from a pool that
  // avoids ambiguous characters (0/O, 1/l/I). Fills the input, reveals it,
  // copies to clipboard, and shows the hint until the modal closes.
  function _generateUserPassword() {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnpqrstuvwxyz';
    const digits = '23456789';
    const all = upper + lower + digits;
    const buf = new Uint32Array(14);
    (window.crypto || window.msCrypto).getRandomValues(buf);
    // Guarantee at least one of each class so it always passes strength checks.
    let out = '';
    out += upper[buf[0] % upper.length];
    out += lower[buf[1] % lower.length];
    out += digits[buf[2] % digits.length];
    for (let i = 3; i < buf.length; i++) out += all[buf[i] % all.length];
    // Shuffle (Fisher-Yates) so the guaranteed chars aren't always at the start
    const arr = out.split('');
    for (let i = arr.length - 1; i > 0; i--) {
      const j = buf[i] % (i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    const pw = arr.join('');

    const inp = document.getElementById('uePassword');
    if (!inp) return;
    inp.value = pw;
    inp.type = 'text'; // reveal so the admin can read/share before saving
    // Update the eye icon
    const eyeBtn = document.getElementById('uePasswordToggleBtn');
    if (eyeBtn) eyeBtn.innerHTML = '<i data-lucide="eye-off"></i>';
    refreshIcons();

    // Copy to clipboard (best-effort; fall back to manual selection)
    try {
      navigator.clipboard?.writeText(pw);
      const hint = document.getElementById('uePasswordGenHint');
      if (hint) hint.style.display = 'block';
      toast('Password copied — share with the user', 'success');
    } catch {
      toast('Password generated — copy manually', '');
    }
  }

  function _toggleUserPasswordVisibility() {
    const inp = document.getElementById('uePassword');
    const btn = document.getElementById('uePasswordToggleBtn');
    if (!inp || !btn) return;
    if (inp.type === 'password') {
      inp.type = 'text';
      btn.innerHTML = '<i data-lucide="eye-off"></i>';
    } else {
      inp.type = 'password';
      btn.innerHTML = '<i data-lucide="eye"></i>';
    }
    refreshIcons();
  }

  // Fetch the stored plaintext on demand (audited server-side). Cached on the
  // editing record so a second reveal/copy in the same modal doesn't re-fetch.
  async function _fetchCurrentPassword() {
    const u = state.teamEditing;
    if (!u || !u.id) return null;
    if (u._pwCache !== undefined) return u._pwCache;
    try {
      const r = await api('/api/admin/users/' + encodeURIComponent(u.id) + '/password');
      const j = await r.json();
      u._pwCache = j && typeof j.password === 'string' ? j.password : null;
    } catch { u._pwCache = null; }
    return u._pwCache;
  }

  // Show/hide the CURRENT stored password (read-only row in the Edit-user
  // modal). The plaintext is fetched on demand from the audited per-user
  // endpoint — never present in the bulk list — and never cached in the DOM
  // while masked.
  async function _toggleCurrentPasswordVisibility() {
    const valEl = document.getElementById('ueCurrentPwValue');
    const btn = document.getElementById('ueCurrentPwToggleBtn');
    if (!valEl || !btn) return;
    if (valEl.dataset.revealed === '1') {
      valEl.textContent = '••••••••';
      valEl.dataset.revealed = '0';
      btn.innerHTML = '<i data-lucide="eye"></i>';
    } else {
      const pw = await _fetchCurrentPassword();
      if (!pw) { toast('No stored password for this user', ''); return; }
      valEl.textContent = pw;
      valEl.dataset.revealed = '1';
      btn.innerHTML = '<i data-lucide="eye-off"></i>';
    }
    refreshIcons();
  }

  async function _copyCurrentPassword() {
    const pw = await _fetchCurrentPassword();
    if (!pw) { toast('No stored password for this user', ''); return; }
    try { await navigator.clipboard?.writeText(pw); toast('Password copied', 'success'); }
    catch { toast('Copy failed — reveal and copy manually', ''); }
  }

  async function saveUserEditor() {
    const errEl = document.getElementById('userEditorError');
    errEl.classList.add('hidden');
    const username = document.getElementById('ueUsername').value.trim().toLowerCase();
    const displayName = document.getElementById('ueDisplayName').value.trim();
    const password = document.getElementById('uePassword').value;
    const role = document.getElementById('ueRole').value;
    const active = document.getElementById('ueActive').checked;
    const whatsappPhone = document.getElementById('ueWhatsappPhone').value.trim();
    const canDeletePayments = !!document.getElementById('ueCanDeletePayments')?.checked;
    // Disabled checkbox = sales-admin role (implicitly unmasked). OMIT the
    // field entirely then (undefined drops out of JSON.stringify): a disabled
    // box still reports checked=true, and sending it alongside a role
    // demotion in the same save would silently plant a permanent flag.
    const _cspEl = document.getElementById('ueCanSeePhones');
    const canSeePhones = (_cspEl && !_cspEl.disabled) ? !!_cspEl.checked : undefined;
    const _hcEl = document.getElementById('ueHomeCity');
    const homeCity = _hcEl ? _hcEl.value.trim() : undefined;

    try {
      let r;
      if (state.teamEditing) {
        // Edit
        const body = { displayName, role, active, whatsappPhone, canDeletePayments, canSeePhones, homeCity };
        if (password) body.password = password;
        r = await api('/api/admin/users/' + encodeURIComponent(state.teamEditing.id), {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
        });
      } else {
        // Create
        if (!username) { errEl.textContent = 'Username required'; errEl.classList.remove('hidden'); return; }
        if (!password) { errEl.textContent = 'Password required'; errEl.classList.remove('hidden'); return; }
        r = await api('/api/admin/users', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, displayName, password, role, whatsappPhone, canDeletePayments, canSeePhones, homeCity })
        });
      }
      const j = await r.json();
      if (j.error) { errEl.textContent = j.error; errEl.classList.remove('hidden'); return; }
      closeModal('userEditorModal');
      toast(state.teamEditing ? 'User updated' : 'User created', 'success');
      loadTeamUsers();
      loadTeamActivity();
    } catch (err) {
      errEl.textContent = err.message || 'Save failed';
      errEl.classList.remove('hidden');
    }
  }

  async function deleteUserFromEditor() {
    if (!state.teamEditing) return;
    const u = state.teamEditing;
    if (!confirm(`Delete @${userLabel(u.username)}? This cannot be undone.`)) return;
    try {
      const r = await api('/api/admin/users/' + encodeURIComponent(u.id), { method: 'DELETE' });
      const j = await r.json();
      if (j.error) { toast(j.error, 'error'); return; }
      closeModal('userEditorModal');
      toast('User deleted', 'success');
      loadTeamUsers();
      loadTeamActivity();
    } catch (err) {
      toast('Delete failed: ' + (err.message || err), 'error');
    }
  }

  async function loadTeamActivity() {
    const list = document.getElementById('teamActivityList');
    clear(list);
    // Activity feed is admin-only on the server (it shows everyone's actions).
    // Skip the fetch entirely for agents — show a friendly note instead of
    // letting the request 403.
    if (state.me?.role !== 'admin') {
      list.appendChild(el('div', { class: 'bulk-loading', text: 'Activity feed visible to admins only.' }));
      return;
    }
    list.appendChild(el('div', { class: 'bulk-loading', text: 'Loading activity…' }));
    try {
      const r = await api('/api/admin/activity?limit=80');
      const j = await r.json();
      const entries = j.entries || [];
      clear(list);
      if (!entries.length) { list.appendChild(el('div', { class: 'bulk-loading', text: 'No activity yet.' })); return; }
      for (const e of entries) list.appendChild(buildActivityRow(e));
      refreshIcons();
    } catch (err) {
      clear(list); list.appendChild(el('div', { class: 'bulk-loading', text: 'Failed: ' + (err.message || err) }));
    }
  }

  const ACTIVITY_ICONS = {
    login: 'log-in', logout: 'log-out',
    user_created: 'user-plus', user_updated: 'user-cog', user_deleted: 'user-x',
    chat_message_sent: 'send', voice_note_sent: 'mic',
    bulk_send_text: 'megaphone', bulk_send_media: 'megaphone',
    bot_enabled: 'bot', bot_disabled: 'bot-off',
    bot_prompt_updated: 'pencil', bot_prompt_reset: 'rotate-ccw',
    chat_assigned: 'user-check', chat_unassigned: 'user-minus',
    note_added: 'lock', note_deleted: 'unlock',
    custom_prop_added: 'plus-circle', custom_prop_updated: 'pencil', custom_prop_deleted: 'trash-2',
    ai_question_asked: 'sparkles',
    access_alert: 'map-pin'
  };

  function activityText(e) {
    const who = el('strong', { text: userLabel(e.username || 'unknown') });
    const node = el('span', { class: 'ta-text' }, [who]);
    const append = (txt) => node.appendChild(document.createTextNode(' ' + txt));
    switch (e.action) {
      case 'login':                append('signed in' + (e.details?.city ? ' from ' + e.details.city : e.details?.country ? ' from ' + e.details.country : '')); break;
      case 'access_alert':         node.classList.add('ta-alert'); append('⚠ ' + (e.details?.message
        || (e.details?.kind === 'two_places'
          ? `active in two places at once: ${e.details?.city || '?'} and ${e.details?.otherCity || '?'}`
          : `signed in from ${e.details?.city || e.details?.country || 'an unknown place'}` + (e.details?.homeCity ? ` (declared city: ${e.details.homeCity})` : ''))
          + (e.details?.device ? ' · ' + e.details.device : ''))); break;
      case 'logout':               append('signed out'); break;
      case 'user_created':         append('created @' + userLabel(e.target || '')); break;
      case 'user_updated':         append('updated @' + userLabel(e.target || '')); break;
      case 'user_deleted':         append('deleted @' + userLabel(e.target || '')); break;
      case 'chat_message_sent':    append('replied in a chat'); break;
      case 'voice_note_sent':      append('sent a voice note'); break;
      case 'bulk_send_text':       append(`bulk-sent text to ${e.details?.groups || 0} groups (${e.details?.success || 0} ✓ / ${e.details?.failed || 0} ✗)`); break;
      case 'bulk_send_media':      append(`bulk-sent ${e.details?.files || 0} file(s) to ${e.details?.groups || 0} groups (${e.details?.success || 0} ✓ / ${e.details?.failed || 0} ✗)`); break;
      case 'bot_enabled':          append('turned bot ON for a chat'); break;
      case 'bot_disabled':         append('turned bot OFF for a chat'); break;
      case 'bot_prompt_updated':   append('updated the bot prompt'); break;
      case 'bot_prompt_reset':     append('reset bot prompt to default'); break;
      case 'chat_assigned':        append('assigned a chat to @' + userLabel(e.details?.to || '')); break;
      case 'chat_unassigned':      append('unassigned a chat' + (e.details?.from ? ' from @' + userLabel(e.details.from) : '')); break;
      case 'note_added':            append('added a note'); break;
      case 'note_deleted':          append('deleted a note'); break;
      case 'custom_prop_added':     append(`added a property "${e.details?.key || ''}"`); break;
      case 'custom_prop_updated':   append(`updated property "${e.details?.key || ''}"`); break;
      case 'custom_prop_deleted':   append(`deleted property "${e.details?.key || ''}"`); break;
      case 'ai_question_asked':     append('asked the AI a question'); break;
      default:                     append(e.action);
    }
    return node;
  }

  function buildActivityRow(e) {
    const iconName = ACTIVITY_ICONS[e.action] || 'circle-dot';
    const row = el('div', { class: 'team-activity-row' });
    row.appendChild(el('span', { class: 'ta-icon' }, [lucideIcon(iconName)]));
    row.appendChild(activityText(e));
    row.appendChild(el('span', { class: 'ta-time', text: e.timestamp ? formatChatDate(new Date(e.timestamp).getTime()) : '' }));
    return row;
  }

  async function loadBulkGroups() {
    setBulkListMessage('Loading groups…');
    try {
      const r = await api('/api/groups');
      const j = await r.json();
      if (j.error) { setBulkListMessage('Error: ' + j.error); return; }
      state.bulkGroups = (j.groups || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      renderBulkTagFilter();
      renderBulkGroups();
    } catch (err) {
      setBulkListMessage('Failed to load: ' + (err.message || String(err)));
    }
  }

  function uniqueBulkTags() {
    const set = new Set();
    for (const g of state.bulkGroups) (g.tags || []).forEach(t => t && set.add(t));
    return [...set].sort();
  }

  function renderBulkTagFilter() {
    const container = document.getElementById('bulkTagFilter');
    clear(container);
    const tags = uniqueBulkTags();
    if (!tags.length) return;
    const allBtn = el('button', { class: 'bulk-tag-pill' + (!state.bulkActiveTag ? ' active' : ''), text: 'All' });
    allBtn.addEventListener('click', () => { state.bulkActiveTag = null; renderBulkTagFilter(); renderBulkGroups(); });
    container.appendChild(allBtn);
    for (const tag of tags) {
      const btn = el('button', { class: 'bulk-tag-pill' + (state.bulkActiveTag === tag ? ' active' : ''), text: tag });
      btn.addEventListener('click', () => {
        state.bulkActiveTag = state.bulkActiveTag === tag ? null : tag;
        renderBulkTagFilter();
        renderBulkGroups();
      });
      container.appendChild(btn);
    }
  }

  function filteredBulkGroups() {
    const q = state.bulkSearch.trim().toLowerCase();
    return state.bulkGroups.filter(g => {
      if (state.bulkActiveTag && !(g.tags || []).includes(state.bulkActiveTag)) return false;
      if (q && !(g.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }

  function renderBulkGroups() {
    const list = document.getElementById('bulkGroupsList');
    clear(list);
    const filtered = filteredBulkGroups();
    if (!filtered.length) {
      list.appendChild(el('div', { class: 'bulk-loading', text: 'No groups match.' }));
      return;
    }
    for (const g of filtered) {
      const cb = el('input', { type: 'checkbox' });
      cb.checked = state.bulkSelected.has(g.id);
      cb.addEventListener('change', () => {
        if (cb.checked) state.bulkSelected.add(g.id);
        else state.bulkSelected.delete(g.id);
        updateBulkCount();
      });
      const tagsEl = (g.tags || []).map(t => el('span', { class: 'bulk-g-tag', text: t }));
      const row = el('div', { class: 'bulk-group-row' }, [
        cb,
        el('div', { class: 'bulk-g-name', text: g.name || 'Unknown' }),
        el('span', { class: 'bulk-g-meta', text: (g.members || 0) + ' members' }),
        el('div', { class: 'bulk-g-tags' }, tagsEl)
      ]);
      row.addEventListener('click', e => {
        if (e.target === cb) return;
        cb.checked = !cb.checked;
        cb.dispatchEvent(new Event('change'));
      });
      list.appendChild(row);
    }
    refreshIcons();
  }

  function updateBulkCount() {
    document.getElementById('bulkSelectedCount').textContent = state.bulkSelected.size + ' selected';
    document.getElementById('bulkSendCount').textContent = state.bulkSelected.size;
  }

  function bulkSelectAllVisible() {
    for (const g of filteredBulkGroups()) state.bulkSelected.add(g.id);
    renderBulkGroups();
    updateBulkCount();
  }

  function bulkSelectNone() {
    state.bulkSelected.clear();
    renderBulkGroups();
    updateBulkCount();
  }

  function bulkOnAttach(e) {
    const files = [...(e.target.files || [])];
    state.bulkAttachments = files.slice(0, 10);
    const status = document.getElementById('bulkAttachStatus');
    if (state.bulkAttachments.length) {
      const names = state.bulkAttachments.map(f => f.name).join(', ');
      status.textContent = state.bulkAttachments.length + ' file(s): ' + (names.length > 80 ? names.slice(0, 77) + '…' : names);
    } else {
      status.textContent = 'No files';
    }
  }

  async function bulkSend() {
    if (state.bulkSending) return;
    if (state.clientState !== 'READY') { toast('WhatsApp not connected', 'error'); return; }
    const groupIds = [...state.bulkSelected];
    if (!groupIds.length) { toast('Select at least one group', 'error'); return; }

    const message = document.getElementById('bulkMessageInput').value.trim();
    const delay = Number(document.getElementById('bulkDelay').value) || 5;
    const batchSize = Number(document.getElementById('bulkBatchSize').value) || 10;
    const batchPause = Number(document.getElementById('bulkBatchPause').value) || 60;

    if (state.bulkAttachments.length === 0 && !message) {
      toast('Type a message or attach files', 'error');
      return;
    }

    state.bulkSending = true;
    document.getElementById('bulkCancelBtn').classList.remove('hidden');
    document.getElementById('bulkSendBtn').disabled = true;
    clear(document.getElementById('bulkProgressLog'));
    showBulkProgress(0, groupIds.length, 'Starting…');

    try {
      let r;
      if (state.bulkAttachments.length > 0) {
        const fd = new FormData();
        fd.append('groupIds', JSON.stringify(groupIds));
        fd.append('caption', message);
        fd.append('delay', String(delay));
        fd.append('batchSize', String(batchSize));
        fd.append('batchPause', String(batchPause));
        for (const f of state.bulkAttachments) fd.append('media', f, f.name);
        r = await fetchT('/api/send-media', { method: 'POST', headers: authHeaders(), body: fd }, 120000);
      } else {
        // NOTE: /api/send is the multi-group BLAST, not a single send. It is
        // safe under api()'s 60s default ONLY because the server replies
        // res.json({status:'sending', total}) BEFORE entering the send loop
        // (progress streams via socket.io). If a server refactor ever moves
        // that response after the loop, this call needs timeoutMs: 0.
        r = await api('/api/send', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ groupIds, message, delay, batchSize, batchPause })
        });
      }
      const j = await r.json().catch(() => ({}));
      if (j.error) {
        toast(j.error, 'error');
        bulkResetSendingUi();
      }
    } catch (err) {
      toast('Send failed: ' + (err.message || String(err)), 'error');
      bulkResetSendingUi();
    }
  }

  function bulkResetSendingUi() {
    state.bulkSending = false;
    document.getElementById('bulkSendBtn').disabled = false;
    document.getElementById('bulkCancelBtn').classList.add('hidden');
  }

  async function bulkCancel() {
    try { await api('/api/cancel-send', { method: 'POST' }); toast('Cancelling…', ''); }
    catch { /* ignore */ }
  }

  // ── Duplicate-contacts admin tool ────────────────────────────────────────
  let _dedupeWired = false;
  let _dedupePlanHash = null;
  let _dedupeMergedTotal = 0;
  function initDedupeView() {
    if (_dedupeWired) return;
    _dedupeWired = true;
    const summary = document.getElementById('dedupeSummary');
    const tbody = document.getElementById('dedupeTbody');
    const table = document.getElementById('dedupeTable');
    const scanBtn = document.getElementById('dedupeScanBtn');
    const mergeBtn = document.getElementById('dedupeMergeBtn');
    const backfillBtn = document.getElementById('dedupeBackfillBtn');
    const pWrap = document.getElementById('dedupeProgressWrap');
    const pFill = document.getElementById('dedupeProgressFill');
    const pLabel = document.getElementById('dedupeProgressLabel');

    backfillBtn?.addEventListener('click', async () => {
      backfillBtn.disabled = true; summary.textContent = 'Re-indexing phone numbers…';
      try {
        const r = await api('/api/admin/contacts/dedupe/backfill', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dryRun: false }) });
        const d = await r.json();
        if (!r.ok) { toast(d.error || 'failed', 'error'); }
        else { toast(`Re-indexed ${d.updated} of ${d.scanned}`, 'success'); summary.textContent = `Re-indexed ${d.updated} contact(s) that were missing a phone index (of ${d.scanned} scanned).`; }
      } catch { toast('network error', 'error'); }
      backfillBtn.disabled = false;
    });

    scanBtn?.addEventListener('click', async () => {
      scanBtn.disabled = true; mergeBtn.classList.add('hidden'); clear(tbody); table.classList.add('hidden');
      summary.textContent = 'Scanning all contacts for duplicates… (this can take ~20s)';
      try {
        // Read-only planning pass over the full ~125k-contact mirror: ~20s on
        // a healthy server, can cross 60s on a degraded one. Retry-safe, so
        // give it headroom instead of a spurious "scan error" at 60s.
        const r = await api('/api/admin/contacts/dedupe/scan', { timeoutMs: 180000 });
        const d = await r.json();
        if (!r.ok) { toast(d.error || 'scan failed', 'error'); summary.textContent = 'Scan failed.'; scanBtn.disabled = false; return; }
        const s = d.summary || {};
        _dedupePlanHash = s.planHash; _dedupeMergedTotal = 0;
        const skipped = Object.entries(s.skipped || {}).map(([k, v]) => `${v} ${k}`).join(', ') || 'none';
        summary.innerHTML = `<strong>${s.totalGroups || 0}</strong> duplicate sets · <strong style="color:#16a34a">${s.autoLosers || 0}</strong> extra cards safe to merge (${s.autoGroups || 0} sets) · held for manual review: ${skipped}.`;
        clear(tbody);
        for (const g of (s.sample || [])) {
          const flag = g.flag && g.flag !== 'auto' ? g.flag : 'safe';
          tbody.appendChild(el('tr', {}, [
            el('td', { text: String(g.key || '').replace(/^e164:|^last9:/, '') }),
            el('td', { text: String(g.size) }),
            el('td', { text: String(g.keeper) + (g.keeperLeadLinked ? ' ★' : '') }),
            el('td', { text: String(g.losers) }),
            el('td', { text: flag, style: flag === 'safe' ? 'color:#16a34a' : 'color:#b45309' }),
          ]));
        }
        table.classList.remove('hidden');
        if ((s.autoLosers || 0) > 0) { mergeBtn.classList.remove('hidden'); mergeBtn.querySelector('span')?.remove(); mergeBtn.lastChild && (mergeBtn.lastChild.textContent = ` Merge ${s.autoLosers} duplicates`); }
        toast(`Scan complete — ${s.autoLosers || 0} safe to merge`, 'success');
      } catch { toast('scan error', 'error'); summary.textContent = 'Scan error.'; }
      scanBtn.disabled = false;
    });

    mergeBtn?.addEventListener('click', async () => {
      if (!_dedupePlanHash) { toast('Run a scan first', 'warning'); return; }
      if (!confirm('Merge duplicates now? This permanently combines each duplicate set into one card (keeping the lead-linked/richest). This CANNOT be undone. Continue?')) return;
      mergeBtn.disabled = true; pWrap.classList.remove('hidden'); pFill.style.width = '5%'; pLabel.textContent = 'Merging…';
      try {
        // No watchdog (timeoutMs: 0): the server holds this request open for
        // the WHOLE merge batch — up to 300 sequential, IRREVERSIBLE HubSpot
        // merges throttled by the global 4 req/s limiter (105-150s+ per run).
        // A client-side abort does NOT stop the server: it would show "merge
        // error", re-enable the button mid-run, and a re-click with the
        // still-valid planHash would start a second merge loop CONCURRENTLY.
        // Live progress arrives via the dedup_progress socket event below.
        const r = await api('/api/admin/contacts/dedupe/run', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ confirm: _dedupePlanHash, maxMergesThisRun: 300 }), timeoutMs: 0 });
        const d = await r.json();
        if (!r.ok) { toast(d.error || 'merge failed', 'error'); pLabel.textContent = d.error || 'failed'; mergeBtn.disabled = false; return; }
        _dedupeMergedTotal += (d.merged || 0);
        pFill.style.width = '100%';
        pLabel.textContent = `Merged ${_dedupeMergedTotal} so far · failed ${d.failed.length} · leads re-pointed ${d.leadsRepointed}${d.capHit ? ' · batch cap reached, click Continue' : ' · done ✓'}`;
        toast(`Merged ${d.merged} duplicates`, 'success');
        if (d.capHit) { mergeBtn.lastChild && (mergeBtn.lastChild.textContent = ' Continue merging'); }
        else { mergeBtn.classList.add('hidden'); }
      } catch { toast('merge error', 'error'); pLabel.textContent = 'network error'; }
      mergeBtn.disabled = false;
    });

    // live per-merge progress
    try {
      state.socket?.on('dedup_progress', (p) => {
        if (p && p.total) { pFill.style.width = Math.min(100, ((p.merged || 0) / p.total) * 100).toFixed(0) + '%'; pLabel.textContent = `Merging… ${p.merged}/${p.total}` + (p.failed ? ` (${p.failed} failed)` : ''); }
      });
    } catch { /* ignore */ }
  }

  // Blocked numbers view (admin / super_sales). Lists everything on the
  // server-side blocklist with a per-row Unblock. Re-fetches on every open so
  // the list is always fresh (no wired-once guard — the load is cheap).
  async function initBlockedNumbersView() {
    const tbody = document.getElementById('blockedNumbersTbody');
    if (!tbody) return;
    await _blockedNumbersLoad(tbody);
  }

  async function _blockedNumbersLoad(tbody) {
    clear(tbody);
    tbody.appendChild(el('tr', {}, [
      el('td', { colspan: '5', class: 'muted small', style: 'padding:12px 2px;', text: 'Chargement…' }),
    ]));
    try {
      const r = await api('/api/blocklist');
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        clear(tbody);
        tbody.appendChild(el('tr', {}, [el('td', { colspan: '5', class: 'muted small', style: 'padding:12px 2px;', text: 'Erreur: ' + (j.error || ('HTTP ' + r.status)) })]));
        return;
      }
      const entries = Array.isArray(j.entries) ? j.entries : [];
      clear(tbody);
      if (!entries.length) {
        tbody.appendChild(el('tr', {}, [el('td', { colspan: '5', class: 'muted small', style: 'padding:12px 2px;', text: 'Aucun numéro bloqué.' })]));
        return;
      }
      for (const e of entries) {
        const last9 = String(e.last9 || '');
        const numCell = el('td', { text: e.raw || last9 || '—' });
        const byCell = el('td', { text: userLabel(e.blockedBy || '—') });
        let when = '—';
        if (e.blockedAt) {
          const d = new Date(e.blockedAt);
          if (!isNaN(d.getTime())) when = MA.loc(d, 'dt', 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        }
        const whenCell = el('td', { text: when });
        const reasonCell = el('td', { text: e.reason || '—' });
        const unblockBtn = el('button', { class: 'btn btn-ghost btn-sm', type: 'button' }, [lucideIcon('x'), el('span', { text: ' Débloquer' })]);
        unblockBtn.addEventListener('click', async () => {
          if (!confirm('Débloquer ' + (last9 || e.raw || '') + ' ?')) return;
          unblockBtn.disabled = true;
          try {
            const ur = await api('/api/blocklist/unblock', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone: last9 }),
            });
            const uj = await ur.json().catch(() => ({}));
            if (!ur.ok) { toast('Déblocage échoué: ' + (uj.error || ('HTTP ' + ur.status)), 'error'); unblockBtn.disabled = false; return; }
            toast('Numéro débloqué', '');
            await _blockedNumbersLoad(tbody);
          } catch (err) {
            toast('Déblocage échoué: ' + (err.message || err), 'error');
            unblockBtn.disabled = false;
          }
        });
        const actionCell = el('td', {}, [unblockBtn]);
        tbody.appendChild(el('tr', {}, [numCell, byCell, whenCell, reasonCell, actionCell]));
      }
      refreshIcons(tbody);
    } catch (err) {
      clear(tbody);
      tbody.appendChild(el('tr', {}, [el('td', { colspan: '5', class: 'muted small', style: 'padding:12px 2px;', text: 'Erreur réseau' })]));
    }
  }

  function showBulkProgress(current, total, label) {
    const wrap = document.getElementById('bulkProgress');
    wrap.classList.remove('hidden');
    const pct = total ? (current / total) * 100 : 0;
    document.getElementById('bulkProgressFill').style.width = pct.toFixed(1) + '%';
    document.getElementById('bulkProgressLabel').textContent = `${label || ''}  ${current}/${total}`;
  }

  function appendBulkLog(text, cls) {
    const log = document.getElementById('bulkProgressLog');
    log.appendChild(el('div', { class: cls || '', text }));
    log.scrollTop = log.scrollHeight;
    while (log.children.length > 80) log.removeChild(log.firstChild);
  }

  async function openHistoryModal() {
    openModal('historyModal');
    setHistoryListMessage('Loading…');
    try {
      const r = await api('/api/history');
      const j = await r.json();
      const items = j.history || [];
      if (!items.length) { setHistoryListMessage('No campaigns yet.'); return; }
      const list = document.getElementById('historyList');
      clear(list);
      for (const h of items.slice().reverse()) {
        const parts = [
          el('span', { class: 'h-summary', text: (h.message || '(media)').slice(0, 80) + ' — ' + (h.groupCount || 0) + ' groups' }),
          el('span', { class: 'h-success', text: '✓ ' + (h.success || 0) })
        ];
        if (h.failed) parts.push(el('span', { class: 'h-failed', text: '✗ ' + h.failed }));
        if (h.timestamp) parts.push(el('span', { class: 'h-time', text: formatChatDate(new Date(h.timestamp).getTime()) }));
        list.appendChild(el('div', { class: 'bulk-history-row' }, parts));
      }
      refreshIcons();
    } catch {
      setHistoryListMessage('Failed to load.');
    }
  }

  async function claimCandidateInviteFromUrl() {
    const url = new URL(location.href);
    const code = url.searchParams.get('candidate_invite');
    if (!code) return false;
    showScreen('login');
    if (E.loginError) {
      E.loginError.textContent = 'Opening your Sales Academy account…';
      E.loginError.classList.remove('hidden');
    }
    try {
      const response = await fetchT('/api/recruitment/invite/claim', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.token) throw new Error(payload.error || 'Invitation could not be opened');
      state.token = payload.token;
      state.me = payload.user || null;
      localStorage.setItem(TOKEN_KEY, state.token);
      url.searchParams.delete('candidate_invite');
      history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash);
      await bootApp();
    } catch (error) {
      if (E.loginError) {
        E.loginError.textContent = error.message || 'Invitation expired. Ask the team for a new secure link.';
        E.loginError.classList.remove('hidden');
      }
    }
    return true;
  }

  document.addEventListener('DOMContentLoaded', () => {
    refreshIcons();
    bind();
    startAppVersionWatch();
    void (async () => {
      if (await claimCandidateInviteFromUrl()) return;
      if (state.token) bootApp();
      else showScreen('login');
    })();
  });

  // ============ Team chat (DMs + rooms with media + ✓✓ + presence) ============
  // Messages persisted in team_chats.json, real-time via Socket.IO
  // (`team_message`, `team_read`, `presence`). A teammate's `whatsappPhone`
  // never re-routes the DM to their WhatsApp chat (2026-10-09) — that hid
  // every in-app message they sent behind their client thread.
  state.teamUsers = [];
  state.teamRooms = [];
  state.teamDigest = {};        // chatKey → { lastMessage, unread }
  state.teamPresence = new Set(); // online userIds
  state.teamchatActiveKey = null;

  // Per-user section prefs for the sectioned team roster (v1 = localStorage;
  // survives sessions per browser). Order default = viewer's own section
  // first (LoughaTeamSections.defaultOrder), manual ↑↓ overrides it.
  const TEAM_SEC_ORDER_KEY = 'leads7.teamSecOrder';
  const TEAM_SEC_COLLAPSED_KEY = 'leads7.teamSecCollapsed';
  function loadTeamSecPrefs() {
    let order = null, collapsed = {};
    try { order = JSON.parse(localStorage.getItem(TEAM_SEC_ORDER_KEY) || 'null'); } catch { /* */ }
    try { collapsed = JSON.parse(localStorage.getItem(TEAM_SEC_COLLAPSED_KEY) || '{}') || {}; } catch { /* */ }
    return { order, collapsed };
  }
  function saveTeamSecOrder(order) {
    try { localStorage.setItem(TEAM_SEC_ORDER_KEY, JSON.stringify(order)); } catch { /* */ }
  }
  function saveTeamSecCollapsed(collapsed) {
    try { localStorage.setItem(TEAM_SEC_COLLAPSED_KEY, JSON.stringify(collapsed)); } catch { /* */ }
  }
  // Active sidebar tab on the teamchat surface: 'dms' (Équipe — teammates
  // only) | 'rooms' (rooms only, flat list). Persisted per-browser, same
  // pattern as leads7.lastView, so agents keep their tab across reloads.
  const TEAMCHAT_TAB_KEY = 'leads7.teamchatTab';
  state.teamchatActiveTab = (() => {
    try { return localStorage.getItem(TEAMCHAT_TAB_KEY) === 'rooms' ? 'rooms' : 'dms'; }
    catch { return 'dms'; }
  })();
  function setTeamchatTab(tab) {
    const next = tab === 'rooms' ? 'rooms' : 'dms';
    if (state.teamchatActiveTab !== next) {
      state.teamchatActiveTab = next;
      // A stale query from the other tab would ghost-filter the new list
      // ("No match." on Rooms because a teammate name is still typed).
      state.teamchatSearch = '';
      const search = document.getElementById('teamchatSearch');
      if (search) search.value = '';
      try { localStorage.setItem(TEAMCHAT_TAB_KEY, next); } catch { /* private mode */ }
    }
    syncTeamSeg(state.activeView);
  }
  state.teamchatSearch = '';
  state.teamchatMessages = [];
  // Per-chatKey message cache so re-opening a teammate paints instantly
  // (optimistic) instead of waiting on /api/team/messages each switch. The
  // active chat's array is the SAME reference as state.teamchatMessages, so
  // socket pushes/edits keep both coherent; loadTeamchatMessages revalidates
  // in the background and heals any staleness for non-active chats.
  state.teamThreadCache = {};

  async function loadTeamchat() {
    await Promise.all([loadTeamchatUsers(), loadTeamchatRooms(), loadTeamchatDigest()]);
    renderTeamchatList();
  }

  // ============ Tickets view (HubSpot-style triage table) ============
  // Lives in its own top-bar button. Live-fetches tickets from HubSpot
  // (no local mirror) and writes every cell edit straight back to
  // HubSpot via PATCH so the platform + HubSpot UI share a single
  // source of truth. Reuses the modal infrastructure + the same Esc /
  // Enter muscle-memory as the inbox + team-chat edit flows.
  state.tickets = [];
  state.ticketsTotal = 0;
  state.ticketsAfter = null;
  state.ticketsLoading = false;
  state.ticketsStatusFilter = new Set();
  state.ticketsCategoryFilter = new Set(); // empty = no category filter
  state.ticketsCategorySearch = '';
  state.ticketsAssigneeFilter = new Set(); // empty = no assignee filter; entries are user ids, 'me', 'unmapped', 'unassigned'
  // Sortable column state for the Sheet-style Tickets table. null key =
  // server-default order (newest first). Click any header → toggles asc/desc;
  // a second click on the active header flips direction; a third resets.
  state.ticketsSort = { key: null, dir: 'asc' };
  // Multi-select state — checkbox column at row[0]. Selection persists
  // through filter changes (the bar shows even if a selected row scrolls
  // off-screen). Cleared on view-switch via the Cancel button.
  state.ticketsSelected = new Set();
  // Lazy-loaded so the inline-reassign dropdown + assignee filter list can
  // render without re-fetching every time. Populated by ensureTicketsTeamCache().
  state.ticketsTeamCache = { users: null, owners: null };
  state.ticketsSearch = '';
  state.ticketsPipelines = null;
  state.ticketsDefaultPipelineId = null;
  state.ticketsEnums = null;

  function ticketsApi(path, opts) {
    return fetchT(path, Object.assign({ headers: authHeaders() }, opts || {}))
      .then(async r => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
        return j;
      });
  }

  async function loadTicketsPipelinesAndEnums() {
    if (state.ticketsPipelines && state.ticketsEnums) return;
    try {
      const [pipes, enums] = await Promise.all([
        ticketsApi('/api/hubspot/ticket-pipelines'),
        ticketsApi('/api/hubspot/ticket-enums'),
      ]);
      state.ticketsPipelines = pipes.pipelines || pipes || [];
      state.ticketsDefaultPipelineId = state.ticketsPipelines?.[0]?.id || null;
      state.ticketsEnums = enums || { categories: [], priorities: [] };
      if (!state.ticketsStatusFilter.size && state.ticketsPipelines.length) {
        for (const s of (state.ticketsPipelines[0].stages || [])) {
          if (!s.closed && !/closed/i.test(s.label)) state.ticketsStatusFilter.add(String(s.id));
        }
      }
    } catch (err) {
      console.warn('[tickets] pipelines/enums load failed', err);
    }
  }

  async function loadTickets({ resetPaging = true } = {}) {
    if (state.ticketsLoading) return;
    state.ticketsLoading = true;
    try {
      await loadTicketsPipelinesAndEnums();
      if (resetPaging) {
        state.tickets = [];
        state.ticketsAfter = null;
      }
      const params = new URLSearchParams();
      params.set('limit', '100');
      const stages = [...state.ticketsStatusFilter];
      if (stages.length) params.set('stageIds', stages.join(','));
      const cats = [...state.ticketsCategoryFilter];
      if (cats.length) params.set('categories', cats.join(','));
      const assignees = [...state.ticketsAssigneeFilter];
      if (assignees.length) params.set('assignee', assignees.join(','));
      if (state.ticketsSearch) params.set('q', state.ticketsSearch);
      if (state.ticketsAfter && !resetPaging) params.set('after', state.ticketsAfter);
      const j = await ticketsApi('/api/hubspot/tickets?' + params.toString());
      state.tickets = resetPaging ? (j.tickets || []) : state.tickets.concat(j.tickets || []);
      state.ticketsTotal = j.total || 0;
      state.ticketsAfter = j.paging?.next?.after || null;
      // Flip loading OFF before render so an empty result shows "No tickets"
      // instead of "Loading tickets…" (which never updates after).
      state.ticketsLoading = false;
      renderTickets();
    } catch (err) {
      const tbl = document.getElementById('ticketsTable');
      if (tbl) {
        clear(tbl);
        tbl.appendChild(el('div', { class: 'tickets-empty', text: 'Failed to load tickets: ' + (err.message || err) }));
      }
    } finally {
      state.ticketsLoading = false;
    }
  }

  function ticketStageLabel(stageId) {
    if (!stageId) return '';
    for (const p of (state.ticketsPipelines || [])) {
      const hit = (p.stages || []).find(s => String(s.id) === String(stageId));
      if (hit) return hit.label;
    }
    return stageId;
  }
  function ticketStagePillClass(stageId) {
    const label = (ticketStageLabel(stageId) || '').toLowerCase();
    if (label.includes('closed')) return 'tickets-pill-closed';
    if (label.includes('waiting on us')) return 'tickets-pill-attention';
    if (label.includes('waiting on contact')) return 'tickets-pill-waiting';
    if (label.includes('new')) return 'tickets-pill-new';
    return '';
  }
  function ticketPriorityPillClass(priority) {
    const p = String(priority || '').toUpperCase();
    if (p === 'URGENT') return 'tickets-pill-urgent';
    if (p === 'HIGH') return 'tickets-pill-high';
    if (p === 'MEDIUM') return 'tickets-pill-medium';
    if (p === 'LOW') return 'tickets-pill-low';
    return '';
  }

  function renderTickets() {
    const chips = document.getElementById('ticketsFilterChips');
    const tbl = document.getElementById('ticketsTable');
    const pager = document.getElementById('ticketsPager');
    const count = document.getElementById('ticketsCount');
    if (!chips || !tbl) return;
    clear(chips);
    // Hide the "Closed" stage chip — Ayoub asked for it removed since
    // the team triages open work only. The stage still exists in the
    // pipeline (so editing a ticket's status to Closed still works),
    // it just doesn't appear in the filter bar anymore.
    const stagesP1 = (state.ticketsPipelines?.[0]?.stages || []).filter(s => !/closed/i.test(s.label));
    for (const stage of stagesP1) {
      const active = state.ticketsStatusFilter.has(String(stage.id));
      const chip = el('button', {
        class: 'tickets-filter-chip' + (active ? ' active' : '') + ' ' + ticketStagePillClass(stage.id),
        type: 'button',
        text: stage.label,
      });
      chip.addEventListener('click', () => {
        const idStr = String(stage.id);
        if (state.ticketsStatusFilter.has(idStr)) state.ticketsStatusFilter.delete(idStr);
        else state.ticketsStatusFilter.add(idStr);
        loadTickets({ resetPaging: true });
      });
      chips.appendChild(chip);
    }
    if (count) count.textContent = state.tickets.length + (state.ticketsTotal > state.tickets.length ? ' / ' + state.ticketsTotal : '');
    // Keep the category-filter button's count badge in sync with state.
    renderTicketsCategoryTrigger();
    clear(tbl);
    if (!state.tickets.length) {
      const empty = el('div', { class: 'tickets-empty' });
      if (state.ticketsLoading) {
        empty.appendChild(el('span', { class: 'tickets-spinner', 'aria-hidden': 'true' }));
        empty.appendChild(el('span', { text: ' Loading tickets…' }));
      } else {
        // Distinguish "filtered to zero" from "you have no tickets at all".
        const hasFilters = state.ticketsStatusFilter.size || state.ticketsCategoryFilter.size || state.ticketsAssigneeFilter.size || state.ticketsSearch;
        empty.appendChild(el('span', { text: hasFilters ? 'No tickets match these filters.' : 'No tickets for your contacts yet.' }));
      }
      tbl.appendChild(empty);
    } else {
      const header = el('div', { class: 'tickets-row tickets-row-head' });
      // Leading checkbox header — select all visible tickets at once.
      const selectAllCell = el('div', { class: 'tickets-cell tickets-cell-select' });
      const selectAll = el('input', { type: 'checkbox', 'aria-label': 'Select all tickets' });
      const allOnPage = state.tickets.length && state.tickets.every(t => state.ticketsSelected.has(t.id));
      selectAll.checked = !!allOnPage;
      selectAll.addEventListener('change', () => {
        if (selectAll.checked) for (const t of state.tickets) state.ticketsSelected.add(t.id);
        else for (const t of state.tickets) state.ticketsSelected.delete(t.id);
        renderTickets();
        renderTicketsBulkBar();
      });
      selectAllCell.appendChild(selectAll);
      header.appendChild(selectAllCell);
      // Each header is clickable to sort the visible rows. `key` is the
      // ticket field path used by ticketSortKey() — null means "not
      // sortable" (the Companies / Contact cells render objects).
      const cols = [
        { label: 'Ticket name',     key: 'subject' },
        { label: 'Status',          key: 'hs_pipeline_stage' },
        { label: 'Priority',        key: 'hs_ticket_priority' },
        { label: 'Feedback Status', key: 'feedback_status' },
        { label: 'Contact',         key: 'contact' },
        { label: 'Assigned',        key: 'assigned' },
        { label: 'Companies',       key: 'companies' },
        { label: 'Student feedback',key: 'student_feedback' },
        { label: 'Agent note',      key: 'note' },
        { label: 'Created',         key: 'createdate' },
      ];
      const sort = state.ticketsSort || { key: null, dir: 'asc' };
      for (const c of cols) {
        const isActive = sort.key === c.key;
        const head = el('div', {
          class: 'tickets-cell tickets-cell-head' + (isActive ? ' sort-active' : ''),
          'data-sort-key': c.key,
        }, [
          document.createTextNode(c.label),
          el('span', { class: 'sort-arrow', text: isActive ? (sort.dir === 'asc' ? '↑' : '↓') : '↕' }),
        ]);
        head.addEventListener('click', () => toggleTicketsSort(c.key));
        header.appendChild(head);
      }
      tbl.appendChild(header);
      const sortedTickets = sortTicketsForRender(state.tickets, sort);
      for (const t of sortedTickets) tbl.appendChild(renderTicketRow(t));
    }
    if (pager) {
      clear(pager);
      if (state.ticketsAfter) {
        const next = el('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: 'Load more →' });
        next.addEventListener('click', () => loadTickets({ resetPaging: false }));
        pager.appendChild(next);
      }
    }
    refreshIcons(tbl);
    renderTicketsBulkBar();
  }

  // Tri-state sort toggle on a column header: not-active → asc → desc →
  // reset (back to server-default order). Re-renders the table only — no
  // server hit, since the page is already in memory.
  function toggleTicketsSort(key) {
    const cur = state.ticketsSort || { key: null, dir: 'asc' };
    if (cur.key !== key) state.ticketsSort = { key, dir: 'asc' };
    else if (cur.dir === 'asc') state.ticketsSort = { key, dir: 'desc' };
    else state.ticketsSort = { key: null, dir: 'asc' };
    renderTickets();
  }

  // Pull a comparable scalar from a ticket for the given sort key.
  // Empty cells sort consistently below filled ones in asc, above in desc.
  function ticketSortKey(t, key) {
    const p = t.properties || {};
    if (key === 'subject' || key === 'feedback_status' || key === 'student_feedback' || key === 'note') {
      return String(p[key] || '').toLowerCase();
    }
    if (key === 'hs_pipeline_stage') return String(p.hs_pipeline_stage || '');
    if (key === 'hs_ticket_priority') {
      // Logical order: URGENT > HIGH > MEDIUM > LOW > (empty)
      const order = { URGENT: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
      return order[String(p.hs_ticket_priority || '').toUpperCase()] || 0;
    }
    if (key === 'createdate') return Number(p.createdate ? new Date(p.createdate).getTime() : 0);
    if (key === 'contact') return String(t.contact?.name || '').toLowerCase();
    if (key === 'companies') return String((t.companies || []).map(c => c.name).join(' ')).toLowerCase();
    if (key === 'assigned') return String(t.assignedTo?.platformUser?.displayName || t.assignedTo?.ownerName || '').toLowerCase();
    return '';
  }

  function sortTicketsForRender(tickets, sort) {
    if (!sort || !sort.key) return tickets;
    const arr = [...tickets];
    const dir = sort.dir === 'desc' ? -1 : 1;
    arr.sort((a, b) => {
      const av = ticketSortKey(a, sort.key);
      const bv = ticketSortKey(b, sort.key);
      // Empty values land at the END regardless of direction so a sorted
      // "Created" view doesn't push undated rows to the top in asc mode.
      const aEmpty = av === '' || av === 0;
      const bEmpty = bv === '' || bv === 0;
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }

  // Tri-state sort toggle on a column header: not-active → asc → desc →
  // reset (back to server-default order). Re-renders the table only — no
  // server hit, since the page is already in memory.
  function toggleTicketsSort(key) {
    const cur = state.ticketsSort || { key: null, dir: 'asc' };
    if (cur.key !== key) state.ticketsSort = { key, dir: 'asc' };
    else if (cur.dir === 'asc') state.ticketsSort = { key, dir: 'desc' };
    else state.ticketsSort = { key: null, dir: 'asc' };
    renderTickets();
  }

  // Pull a comparable scalar from a ticket for the given sort key.
  // Empty cells sort consistently below filled ones in asc, above in desc.
  function ticketSortKey(t, key) {
    const p = t.properties || {};
    if (key === 'subject' || key === 'feedback_status' || key === 'student_feedback' || key === 'note') {
      return String(p[key] || '').toLowerCase();
    }
    if (key === 'hs_pipeline_stage') return String(p.hs_pipeline_stage || '');
    if (key === 'hs_ticket_priority') {
      // Logical order: URGENT > HIGH > MEDIUM > LOW > (empty)
      const order = { URGENT: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
      return order[String(p.hs_ticket_priority || '').toUpperCase()] || 0;
    }
    if (key === 'createdate') return Number(p.createdate ? new Date(p.createdate).getTime() : 0);
    if (key === 'contact') return String(t.contact?.name || '').toLowerCase();
    if (key === 'companies') return String((t.companies || []).map(c => c.name).join(' ')).toLowerCase();
    if (key === 'assigned') return String(t.assignedTo?.platformUser?.displayName || t.assignedTo?.ownerName || '').toLowerCase();
    return '';
  }

  function sortTicketsForRender(tickets, sort) {
    if (!sort || !sort.key) return tickets;
    const arr = [...tickets];
    const dir = sort.dir === 'desc' ? -1 : 1;
    arr.sort((a, b) => {
      const av = ticketSortKey(a, sort.key);
      const bv = ticketSortKey(b, sort.key);
      // Empty values land at the END regardless of direction so a sorted
      // "Created" view doesn't push undated rows to the top in asc mode.
      const aEmpty = av === '' || av === 0;
      const bEmpty = bv === '' || bv === 0;
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }

  function renderTicketRow(t) {
    const isSelected = state.ticketsSelected.has(t.id);
    const row = el('div', { class: 'tickets-row' + (isSelected ? ' is-selected' : ''), 'data-ticket-id': t.id });
    // Leading checkbox cell — toggles selection + updates the bulk bar.
    const selCell = el('div', { class: 'tickets-cell tickets-cell-select' });
    const cb = el('input', { type: 'checkbox', 'aria-label': 'Select ticket' });
    cb.checked = isSelected;
    cb.addEventListener('click', (ev) => ev.stopPropagation());
    cb.addEventListener('change', () => {
      if (cb.checked) state.ticketsSelected.add(t.id);
      else state.ticketsSelected.delete(t.id);
      row.classList.toggle('is-selected', cb.checked);
      renderTicketsBulkBar();
    });
    selCell.appendChild(cb);
    row.appendChild(selCell);
    const p = t.properties || {};
    const nameCell = el('div', { class: 'tickets-cell tickets-cell-name' });
    nameCell.appendChild(el('span', { text: p.subject || '(No subject)' }));
    if (t.url) {
      const openLink = el('a', { href: t.url, target: '_blank', rel: 'noopener noreferrer', class: 'tickets-open-link', title: 'Open in HubSpot' });
      openLink.appendChild(lucideIcon('external-link'));
      nameCell.appendChild(openLink);
    }
    row.appendChild(nameCell);
    row.appendChild(buildStagePillCell(t));
    row.appendChild(buildPriorityPillCell(t));
    row.appendChild(buildCategoryPillCell(t));
    row.appendChild(buildContactCell(t));
    row.appendChild(buildAssignedCell(t));
    row.appendChild(buildCompaniesCell(t));
    row.appendChild(buildTextCell(t, 'student_feedback', p.student_feedback || '', 'Student feedback'));
    // HubSpot has TWO properties:
    //   - `note` (label: "Create Tickets Note") — built-in
    //   - `agent_note` (label: "Agent Note") — custom, what the AGENT NOTE
    //     column in HubSpot's tickets table displays — what the team
    //     actually writes into.
    // Prefer agent_note; fall back to legacy note for old records that
    // were written via the modal before this swap.
    row.appendChild(buildTextCell(t, 'agent_note', p.agent_note || p.note || '', 'Agent note'));
    row.appendChild(el('div', { class: 'tickets-cell tickets-cell-date', text: p.createdate ? formatChatDate(p.createdate) : '—' }));
    return row;
  }

  function openInlineDropdown(anchorEl, items, currentValue, onPick) {
    document.querySelectorAll('.tickets-inline-menu').forEach(m => m.remove());
    const menu = el('div', { class: 'tickets-inline-menu' });
    for (const it of items) {
      const itm = el('button', {
        class: 'tickets-inline-menu-item' + (String(it.value) === String(currentValue) ? ' active' : '') + (it.disabled ? ' disabled' : ''),
        type: 'button',
      });
      if (it.disabled) itm.disabled = true;
      if (it.dot) itm.appendChild(el('span', { class: 'tickets-pill-dot ' + it.dot }));
      itm.appendChild(el('span', { text: it.label }));
      itm.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (it.disabled) return;
        menu.remove();
        onPick(it.value);
      });
      menu.appendChild(itm);
    }
    document.body.appendChild(menu);
    const r = anchorEl.getBoundingClientRect();
    menu.style.left = r.left + 'px';
    menu.style.top = (r.bottom + 4) + 'px';
    setTimeout(() => {
      const close = (ev) => {
        if (menu.contains(ev.target)) return;
        menu.remove();
        document.removeEventListener('click', close);
      };
      document.addEventListener('click', close);
    }, 0);
  }

  function buildStagePillCell(t) {
    const cell = el('div', { class: 'tickets-cell' });
    const stageId = t.properties?.hs_pipeline_stage;
    const pill = el('button', {
      class: 'tickets-pill ' + ticketStagePillClass(stageId),
      type: 'button',
      text: ticketStageLabel(stageId) || '—',
    });
    pill.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const stages = state.ticketsPipelines?.[0]?.stages || [];
      const items = stages.map(s => ({ value: s.id, label: s.label, dot: ticketStagePillClass(s.id) }));
      openInlineDropdown(pill, items, stageId, (newId) => {
        editTicketProperty(t.id, 'hs_pipeline_stage', newId);
      });
    });
    cell.appendChild(pill);
    return cell;
  }
  function buildPriorityPillCell(t) {
    const cell = el('div', { class: 'tickets-cell' });
    const priority = t.properties?.hs_ticket_priority || '';
    const pill = el('button', {
      class: 'tickets-pill ' + ticketPriorityPillClass(priority),
      type: 'button',
      text: priority ? (priority[0] + priority.slice(1).toLowerCase()) : '—',
    });
    pill.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const items = (state.ticketsEnums?.priorities || []).map(p => ({
        value: p, label: p[0] + p.slice(1).toLowerCase(), dot: ticketPriorityPillClass(p),
      }));
      openInlineDropdown(pill, items, priority, (newP) => {
        editTicketProperty(t.id, 'hs_ticket_priority', newP);
      });
    });
    cell.appendChild(pill);
    return cell;
  }
  function buildCategoryPillCell(t) {
    const cell = el('div', { class: 'tickets-cell' });
    const cat = t.properties?.feedback_status || '';
    const pill = el('button', { class: 'tickets-pill tickets-pill-neutral', type: 'button', text: cat || '—' });
    pill.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const items = (state.ticketsEnums?.categories || []).map(c => ({ value: c, label: c }));
      openInlineDropdown(pill, items, cat, (newCat) => {
        editTicketProperty(t.id, 'feedback_status', newCat);
      });
    });
    cell.appendChild(pill);
    return cell;
  }
  // Contact cell — clickable shortcut into the 1-1 inbox conversation.
  // Resolves the contact's phone to a WhatsApp JID and reuses
  // openChatForPhoneNumber (the same helper the HubSpot search uses)
  // so the conversation appears even if the inbox bridge has no prior
  // history of that contact yet.
  function buildContactCell(t) {
    const cell = el('div', { class: 'tickets-cell tickets-cell-contact' });
    const c = t.contact;
    if (!c?.name) { cell.appendChild(el('span', { text: '—' })); return cell; }
    const phone = (c.phone || '').trim();
    // Normalise: Moroccan local "0XXXXXXXXX" → "212XXXXXXXXX".
    // International "+212..." → "212...". Anything else: take digits.
    // Masked values ('2126****27') never produce a dialable link.
    let intl = phone.includes('*') ? '' : phone.replace(/\D/g, '');
    if (intl.startsWith('0') && intl.length === 10) intl = '212' + intl.slice(1);
    const link = el('a', {
      href: '#', class: 'tickets-contact-link',
      title: phone ? 'Open chat with ' + c.name + ' (' + phone + ')' : c.name,
      text: c.name,
    });
    if (intl) {
      link.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        openChatForPhoneNumber(intl);
      });
    } else {
      link.style.cursor = 'default';
      link.style.color = 'var(--gray-700)';
    }
    cell.appendChild(link);
    return cell;
  }

  // Bulk-action bar — shows when ≥1 row is checked. Reassign / Change
  // status apply the chosen value to every selected ticket via the
  // existing PATCH endpoint (one call per ticket — Lougha's ticket volume
  // is small enough that this stays fast without a batch API).
  function renderTicketsBulkBar() {
    const bar = document.getElementById('ticketsBulkBar');
    const count = document.getElementById('ticketsBulkBarCount');
    if (!bar || !count) return;
    const n = state.ticketsSelected.size;
    if (!n) { bar.classList.add('hidden'); return; }
    bar.classList.remove('hidden');
    count.textContent = String(n);
    refreshIcons(bar);
  }
  function clearTicketsSelection() {
    state.ticketsSelected.clear();
    renderTicketsBulkBar();
    // Uncheck DOM without a full re-render — every row's checkbox is keyed
    // by data-ticket-id, so toggling them is cheap and avoids a flash.
    for (const cb of document.querySelectorAll('.tickets-row .tickets-cell-select input')) {
      cb.checked = false;
      cb.closest('.tickets-row')?.classList.remove('is-selected');
    }
  }
  async function bulkReassignTickets(anchorEl) {
    await ensureTicketsTeamCache(true);
    const users = state.ticketsTeamCache.users || [];
    const items = [
      { value: '', label: '— Clear assignee on selected' },
      ...users.map(u => ({
        value: u.hubspotOwnerId || '',
        label: (userLabel(u.username, u.displayName || u.username)) + (u.hubspotOwnerId ? '' : ' · not mapped'),
        disabled: !u.hubspotOwnerId,
      })),
    ];
    openInlineDropdown(anchorEl, items, '', async (newOwnerId) => {
      const value = newOwnerId || '';
      const ids = [...state.ticketsSelected];
      let ok = 0, fail = 0;
      for (const id of ids) {
        try {
          await ticketsApi('/api/hubspot/tickets/' + encodeURIComponent(id), {
            method: 'PATCH',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ properties: { hubspot_owner_id: value } }),
          });
          ok++;
        } catch { fail++; }
      }
      toast(`Reassigned ${ok}` + (fail ? ` · ${fail} failed` : ''), fail ? 'error' : 'success');
      state.ticketsSelected.clear();
      await loadTickets({ resetPaging: true });
    });
  }
  async function bulkChangeStatus(anchorEl) {
    await loadTicketsPipelinesAndEnums();
    const stages = (state.ticketsPipelines?.[0]?.stages || []).map(s => ({
      value: String(s.id),
      label: s.label,
    }));
    openInlineDropdown(anchorEl, stages, '', async (newStageId) => {
      const ids = [...state.ticketsSelected];
      let ok = 0, fail = 0;
      for (const id of ids) {
        try {
          await ticketsApi('/api/hubspot/tickets/' + encodeURIComponent(id), {
            method: 'PATCH',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ properties: { hs_pipeline_stage: newStageId } }),
          });
          ok++;
        } catch { fail++; }
      }
      toast(`Status updated on ${ok}` + (fail ? ` · ${fail} failed` : ''), fail ? 'error' : 'success');
      state.ticketsSelected.clear();
      await loadTickets({ resetPaging: true });
    });
  }

  // Admin-only modal: list every active platform user with a HubSpot-owner
  // dropdown so admin can pick which HubSpot user backs each platform user.
  // PATCH /api/users/:id/hubspot-owner persists the choice. On save, reload
  // the tickets list so the Assigned column refreshes immediately.

  // Lazy-load platform users + HubSpot owners once per session so both the
  // Assigned filter and the inline-reassign dropdown can render synchronously.
  async function ensureTicketsTeamCache(force = false) {
    if (!force && state.ticketsTeamCache.users && state.ticketsTeamCache.owners) return state.ticketsTeamCache;
    const [r1, r2] = await Promise.all([
      api('/api/users/assignable').then(r => r.json()).catch(() => ({ users: [] })),
      api('/api/hubspot/owners').then(r => r.json()).catch(() => ({ owners: [] })),
    ]);
    state.ticketsTeamCache.users = r1.users || [];
    state.ticketsTeamCache.owners = r2.owners || [];
    return state.ticketsTeamCache;
  }

  // Assignee filter dropdown — each row renders as a coloured pill so
  // the list visually mirrors the Assigned-column cells in the table.
  // Special filters (me / unmapped / no owner) get their own distinct
  // pill colours so they stand out from the team-member list below.
  function renderTicketsAssigneeList() {
    const list = document.getElementById('ticketsAssigneeList');
    if (!list) return;
    clear(list);
    const users = state.ticketsTeamCache.users || [];
    const rows = [
      { value: 'me',         label: 'Assigned to me', kind: 'special-me' },
      { value: 'unassigned', label: 'No owner',       kind: 'special-muted' },
      ...users.map(u => ({ value: u.id, label: userLabel(u.username, u.displayName || u.username), role: u.role, kind: 'user', userId: u.id })),
    ];
    // Defensively drop any stale 'unmapped' filter value left in state
    // from before this row was removed — keeps the count badge accurate.
    if (state.ticketsAssigneeFilter.delete('unmapped')) updateTicketsAssigneeCount();
    for (const r of rows) {
      const isChecked = state.ticketsAssigneeFilter.has(r.value);
      const row = el('label', { class: 'tickets-assignee-row' + (isChecked ? ' checked' : '') });
      const cb = el('input', { type: 'checkbox' });
      cb.checked = isChecked;
      cb.addEventListener('change', () => {
        if (cb.checked) state.ticketsAssigneeFilter.add(r.value);
        else state.ticketsAssigneeFilter.delete(r.value);
        row.classList.toggle('checked', cb.checked);
        updateTicketsAssigneeCount();
        loadTickets({ resetPaging: true }).catch(() => {});
      });
      row.appendChild(cb);
      // Build the pill — colour class varies by kind so special filters
      // (me / unmapped / no owner) are visually distinct from team members.
      // Team-member rows all share the same purple tint to match the
      // Tickets-cell assignee pills already shown in the table.
      const pillClass = 'tickets-assignee-pill ' + (
        r.kind === 'special-me'    ? 'pill-me' :
        r.kind === 'special-warn'  ? 'pill-warn' :
        r.kind === 'special-muted' ? 'pill-muted' :
        'pill-user'
      );
      const pill = el('span', { class: pillClass });
      // Avatar — initial for team members, icon glyph for specials.
      const avatar = el('span', { class: 'tap-avatar' });
      if (r.kind === 'special-me') avatar.textContent = '★';
      else if (r.kind === 'special-warn') avatar.textContent = '⚠';
      else if (r.kind === 'special-muted') avatar.textContent = '—';
      else avatar.textContent = ((r.label || '?').trim()[0] || '?').toUpperCase();
      pill.appendChild(avatar);
      pill.appendChild(el('span', { class: 'tap-name', text: r.label }));
      row.appendChild(pill);
      if (r.role) row.appendChild(el('span', { class: 'tickets-assignee-role', text: r.role }));
      list.appendChild(row);
    }
  }
  function updateTicketsAssigneeCount() {
    const n = state.ticketsAssigneeFilter.size;
    const badge = document.getElementById('ticketsAssigneeCount');
    if (!badge) return;
    if (n > 0) { badge.textContent = String(n); badge.classList.remove('hidden'); }
    else badge.classList.add('hidden');
  }
  function toggleTicketsAssigneeMenu() {
    const btn = document.getElementById('ticketsAssigneeBtn');
    const wrap = document.getElementById('ticketsAssigneeFilter');
    const menu = document.getElementById('ticketsAssigneeMenu');
    if (!btn || !menu) return;
    const isOpen = !menu.classList.contains('hidden');
    if (isOpen) { menu.classList.add('hidden'); btn.setAttribute('aria-expanded', 'false'); wrap?.classList.remove('open'); return; }
    ensureTicketsTeamCache().then(() => {
      renderTicketsAssigneeList();
      menu.classList.remove('hidden');
      btn.setAttribute('aria-expanded', 'true');
      wrap?.classList.add('open');
    });
  }

  // Inline reassign — click the assignee cell, pick a platform user, server
  // translates to HubSpot owner id and PATCHes the ticket. Force-refresh the
  // team cache on every open so mappings just changed via the modal flow
  // through immediately.
  async function openAssigneeDropdown(anchorEl, t) {
    await ensureTicketsTeamCache(true);
    const users = state.ticketsTeamCache.users || [];
    const items = [
      { value: '', label: '— Clear assignee' },
      ...users.map(u => ({
        value: u.hubspotOwnerId || '',
        label: (userLabel(u.username, u.displayName || u.username)) + (u.hubspotOwnerId ? '' : ' · not mapped'),
        disabled: !u.hubspotOwnerId,
        _user: u,
      })),
    ];
    const currentOwnerId = t.assignedTo?.ownerId || '';
    openInlineDropdown(anchorEl, items, currentOwnerId, async (newOwnerId) => {
      const value = newOwnerId || ''; // '' = clear
      try {
        const r = await ticketsApi('/api/hubspot/tickets/' + encodeURIComponent(t.id), {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ properties: { hubspot_owner_id: value } }),
        });
        // Update local row's assignedTo so the pill re-renders without a full reload.
        const idx = state.tickets.findIndex(x => x.id === t.id);
        if (idx >= 0) {
          const owners = state.ticketsTeamCache.owners || [];
          const ownerObj = owners.find(o => o.id === String(value));
          const userObj = (state.ticketsTeamCache.users || []).find(u => String(u.hubspotOwnerId) === String(value));
          state.tickets[idx] = {
            ...state.tickets[idx],
            properties: { ...(state.tickets[idx].properties || {}), hubspot_owner_id: value || '' },
            assignedTo: value ? {
              ownerId: String(value),
              ownerName: ownerObj?.displayName || '',
              ownerEmail: ownerObj?.email || '',
              platformUser: userObj ? { id: userObj.id, username: userObj.username, displayName: userObj.displayName, role: userObj.role } : null,
            } : null,
          };
          const oldRow = document.querySelector('.tickets-row[data-ticket-id="' + CSS.escape(t.id) + '"]');
          const newRow = renderTicketRow(state.tickets[idx]);
          if (oldRow && oldRow.parentNode) oldRow.replaceWith(newRow);
          refreshIcons(newRow);
        }
        toast('Reassigned ✓', 'success');
      } catch (err) {
        toast('Reassign failed: ' + (err.message || err), 'error');
      }
    });
  }

  async function openTeamOwnerMapModal() {
    const listEl = document.getElementById('teamOwnerMapList');
    if (!listEl) return;
    clear(listEl);
    listEl.appendChild(el('div', { class: 'empty-row', text: 'Loading…' }));
    openModal('teamOwnerMapModal');
    let users = [], owners = [];
    try {
      const [r1, r2] = await Promise.all([
        api('/api/users/assignable').then(r => r.json()),
        api('/api/hubspot/owners').then(r => r.json()),
      ]);
      users = r1.users || [];
      owners = r2.owners || [];
    } catch (err) {
      clear(listEl);
      listEl.appendChild(el('div', { class: 'empty-row', text: 'Failed to load: ' + (err.message || err) }));
      return;
    }
    clear(listEl);
    for (const u of users) {
      const row = el('div', { class: 'team-owner-map-row', 'data-user-id': u.id });
      const left = el('div', { class: 'tomr-user' });
      const init = String(userLabel(u.username, u.displayName || u.username || '?')).trim()[0]?.toUpperCase() || '?';
      left.appendChild(el('span', { class: 'tomr-avatar ' + (avatarColorClass(u.id) || 'avatar-c1'), text: init }));
      left.appendChild(el('div', { class: 'tomr-user-info' }, [
        el('div', { class: 'tomr-name', text: userLabel(u.username, u.displayName || u.username) }),
        el('div', { class: 'tomr-role', text: adminLabelFor(u.username) ? u.role : u.role + ' · @' + u.username }),
      ]));
      row.appendChild(left);
      const select = el('select', { class: 'input tomr-select' });
      const noneOpt = el('option', { value: '', text: '— Not mapped —' });
      if (!u.hubspotOwnerId) noneOpt.selected = true;
      select.appendChild(noneOpt);
      for (const o of owners) {
        const opt = el('option', { value: o.id, text: o.displayName + (o.email ? ' (' + o.email + ')' : '') });
        if (String(u.hubspotOwnerId) === String(o.id)) opt.selected = true;
        select.appendChild(opt);
      }
      select.addEventListener('change', async () => {
        const newId = select.value || null;
        select.disabled = true;
        try {
          const r = await api('/api/users/' + encodeURIComponent(u.id) + '/hubspot-owner', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ hubspotOwnerId: newId }),
          });
          if (!r.ok) {
            const j = await r.json().catch(() => ({}));
            toast(j.error || 'Save failed', 'error');
          } else {
            toast('Saved', 'success');
            u.hubspotOwnerId = newId;
            // Invalidate the team cache so the inline reassign dropdown
            // sees the new mapping without a manual reload.
            state.ticketsTeamCache.users = null;
            // Reload tickets in the background so the Assigned column reflects
            // the new mapping next time the user is back on the table.
            if (state.activeView === 'tickets') loadTickets({ resetPaging: true }).catch(() => {});
          }
        } catch (err) {
          toast('Erreur réseau — vérifiez votre connexion et réessayez', 'error');
        }
        select.disabled = false;
      });
      row.appendChild(select);
      listEl.appendChild(row);
    }
    if (!users.length) listEl.appendChild(el('div', { class: 'empty-row', text: 'No team users.' }));
    refreshIcons();
  }

  // Assignee cell — server pre-computes t.assignedTo from the ticket's
  // hubspot_owner_id. When the HubSpot owner is mapped to a platform user
  // (admin sets this in the "Map team" modal), render a coloured pill with
  // the platform user's first name. Else show the raw HubSpot owner name
  // with an "(unmapped)" tag so admin knows to fix it. Empty owner → em-dash.
  function buildAssignedCell(t) {
    const cell = el('div', { class: 'tickets-cell tickets-cell-assigned tickets-cell-clickable', title: 'Click to reassign' });
    const a = t.assignedTo;
    if (!a) {
      cell.appendChild(el('span', { class: 'muted', text: '— assign' }));
    } else if (a.platformUser) {
      const name = userLabel(a.platformUser.username, a.platformUser.displayName || a.platformUser.username);
      const initial = (name || '?').trim()[0] || '?';
      const pill = el('span', { class: 'conv-assignee-pill ' + (avatarColorClass(a.platformUser.id) || 'avatar-c1') });
      pill.appendChild(el('span', { class: 'conv-assignee-init', text: initial.toUpperCase() }));
      pill.appendChild(el('span', { class: 'conv-assignee-name', text: name }));
      cell.appendChild(pill);
    } else if (a.ownerName) {
      const wrap = el('span', { class: 'ticket-assignee-unmapped', title: a.ownerEmail || '' });
      wrap.appendChild(el('span', { class: 'ticket-assignee-unmapped-name', text: a.ownerName }));
      wrap.appendChild(el('span', { class: 'ticket-assignee-unmapped-tag', text: '(unmapped)' }));
      cell.appendChild(wrap);
    } else {
      cell.appendChild(el('span', { class: 'muted', text: '— assign' }));
    }
    cell.addEventListener('click', (ev) => {
      ev.stopPropagation();
      openAssigneeDropdown(cell, t);
    });
    return cell;
  }

  // Companies cell — each associated HubSpot company renders as a
  // pill. If the company name resolves to a known WA group (via the
  // group_tracking.json fuzzy match server-side), the pill clicks
  // straight into that group's inbox chat. Falls back to plain text
  // when no matching WA group exists.
  function buildCompaniesCell(t) {
    const cell = el('div', { class: 'tickets-cell tickets-cell-companies' });
    const list = Array.isArray(t.companies) ? t.companies : [];
    if (!list.length) { cell.appendChild(el('span', { text: '—' })); return cell; }
    for (const co of list) {
      const link = el('a', {
        href: '#', class: 'tickets-company-link' + (co.waGroupId ? '' : ' tickets-company-link-disabled'),
        title: co.waGroupId ? 'Open WhatsApp group: ' + co.name : co.name + ' (no matching WA group)',
        text: co.name,
      });
      if (co.waGroupId) {
        link.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          showView('inbox');
          if (!state.chats.find(c => c.id === co.waGroupId)) {
            state.chats.unshift({
              id: co.waGroupId, name: co.name, phone: '',
              isGroup: true, lastMessage: null, unread: 0,
            });
          }
          setTimeout(() => selectConversation(co.waGroupId), 80);
        });
      }
      cell.appendChild(link);
    }
    return cell;
  }

  function buildTextCell(t, property, value, label) {
    const isEmpty = !value;
    const cell = el('div', {
      class: 'tickets-cell tickets-cell-text' + (isEmpty ? ' tickets-cell-empty' : ''),
      title: 'Click to edit',
    });
    const preview = isEmpty ? '—' : (value.length > 80 ? value.slice(0, 80) + '…' : value);
    cell.appendChild(el('span', { class: 'tickets-cell-text-preview', text: preview }));
    cell.addEventListener('click', () => openTicketEditTextModal(t, property, label));
    return cell;
  }

  function openTicketEditTextModal(ticket, property, label) {
    const modal = document.getElementById('ticketEditTextModal');
    const ta = document.getElementById('ticketEditTextArea');
    const title = document.getElementById('ticketEditTextTitle');
    const save = document.getElementById('ticketEditTextSaveBtn');
    if (!modal || !ta || !save) return;
    title.textContent = 'Edit ' + label;
    ta.value = ticket.properties?.[property] || '';
    save.onclick = async () => {
      const newVal = ta.value.trim();
      save.disabled = true;
      try {
        await editTicketProperty(ticket.id, property, newVal);
        modal.classList.add('hidden');
      } finally {
        save.disabled = false;
      }
    };
    ta.onkeydown = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); modal.classList.add('hidden'); }
    };
    modal.classList.remove('hidden');
    setTimeout(() => ta.focus(), 50);
    refreshIcons(modal);
  }

  // Feedback Status (category) multi-select filter — button opens a
  // dropdown panel with a searchable checkbox list of every category.
  // Each tick reloads the table; the button surfaces an "N selected"
  // counter so the agent always knows how many filters are active.
  function renderTicketsCategoryTrigger() {
    const countEl = document.getElementById('ticketsCategoryCount');
    if (!countEl) return;
    const n = state.ticketsCategoryFilter.size;
    if (n > 0) {
      countEl.textContent = String(n);
      countEl.classList.remove('hidden');
    } else {
      countEl.textContent = '';
      countEl.classList.add('hidden');
    }
  }
  function renderTicketsCategoryMenu() {
    const list = document.getElementById('ticketsCategoryList');
    if (!list) return;
    clear(list);
    const cats = (state.ticketsEnums?.categories || []);
    const q = (state.ticketsCategorySearch || '').toLowerCase();
    const filtered = q ? cats.filter(c => c.toLowerCase().includes(q)) : cats;
    if (!filtered.length) {
      list.appendChild(el('div', { class: 'tickets-category-empty', text: 'No match.' }));
      return;
    }
    for (const cat of filtered) {
      const row = el('label', { class: 'tickets-category-item' });
      const cb = el('input', { type: 'checkbox' });
      cb.checked = state.ticketsCategoryFilter.has(cat);
      cb.addEventListener('change', () => {
        if (cb.checked) state.ticketsCategoryFilter.add(cat);
        else state.ticketsCategoryFilter.delete(cat);
        renderTicketsCategoryTrigger();
        loadTickets({ resetPaging: true });
      });
      row.appendChild(cb);
      row.appendChild(el('span', { text: cat }));
      list.appendChild(row);
    }
  }
  function openTicketsCategoryMenu() {
    const menu = document.getElementById('ticketsCategoryMenu');
    const btn = document.getElementById('ticketsCategoryBtn');
    const wrap = document.getElementById('ticketsCategoryFilter');
    if (!menu || !btn || !wrap) return;
    menu.classList.remove('hidden');
    btn.setAttribute('aria-expanded', 'true');
    wrap.classList.add('open');
    renderTicketsCategoryMenu();
    setTimeout(() => document.getElementById('ticketsCategorySearch')?.focus(), 30);
  }
  function closeTicketsCategoryMenu() {
    document.getElementById('ticketsCategoryMenu')?.classList.add('hidden');
    document.getElementById('ticketsCategoryBtn')?.setAttribute('aria-expanded', 'false');
    document.getElementById('ticketsCategoryFilter')?.classList.remove('open');
  }

  async function editTicketProperty(ticketId, propertyName, newValue) {
    try {
      const j = await ticketsApi('/api/hubspot/tickets/' + encodeURIComponent(ticketId), {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ properties: { [propertyName]: newValue } }),
      });
      const idx = state.tickets.findIndex(t => t.id === ticketId);
      if (idx >= 0) {
        state.tickets[idx] = { ...state.tickets[idx], properties: { ...(state.tickets[idx].properties || {}), ...(j.properties || {}) }, updatedAt: j.updatedAt };
        const oldRow = document.querySelector('.tickets-row[data-ticket-id="' + CSS.escape(ticketId) + '"]');
        const newRow = renderTicketRow(state.tickets[idx]);
        if (oldRow && oldRow.parentNode) oldRow.replaceWith(newRow);
        refreshIcons(newRow);
      }
      toast('Saved ✓', 'success');
    } catch (err) {
      toast('Update failed: ' + (err.message || err), 'error');
      throw err;
    }
  }

  // ============ Team Groups view + team-only WA group sync (REMOVED — Sprint 3) ============
  // Removed alongside /api/groups, /api/inbox/chats team-only flag, and Baileys group event handlers.

  async function loadTeamchatUsers() {
    const wasEmpty = !state.teamUsers?.length;
    try {
      const r = await api('/api/team/users?withBot=1');
      const j = await r.json();
      // Team Bot (read-only alerts) is kept OUT of state.teamUsers so it never
      // shows up in call pickers, @mentions, forward targets or the inbox
      // "team" filter — it only lives as the pinned row of the Équipe list.
      state.teamBotUser = (j.users || []).find(u => u && u.system && u.id === 'team-bot') || null;
      let users = (j.users || []).filter(u => u.id !== state.me?.id && !u.system);
      // Merge the PEER platform's roster so support2 + Leads7 show one unified
      // team. Remote rows are virtual (id = platform@username, remote:true) and
      // never touch the WhatsApp inbox. Fail-soft: peer down → local-only.
      try {
        const pr = await api('/api/team/peer-users');
        const pj = await pr.json();
        if (Array.isArray(pj.users) && pj.users.length) {
          users = users.concat(pj.users.filter(u => u.id));
        }
      } catch {}
      const mineIdentity = state.me?.identityId || myCanonId();
      const byIdentity = new Map();
      for (const u of users) {
        const identityId = u.identityId || u.canonicalId || u.id;
        if (!identityId || identityId === mineIdentity) continue;
        const prev = byIdentity.get(identityId);
        if (!prev || (prev.remote && !u.remote)) {
          byIdentity.set(identityId, { ...(prev || {}), ...u, identityId, online: !!(u.online || prev?.online) });
        } else if (u.online) {
          prev.online = true;
        }
      }
      users = [...byIdentity.values()].sort((a, b) => (userLabel(a.username, a.displayName || a.username) || '').localeCompare(userLabel(b.username, b.displayName || b.username) || ''));
      state.teamUsers = users;
      // The peer bridge is fail-soft, so a transient failure at boot used to
      // just quietly omit S2 names from the flat list — but with the sectioned
      // roster it silently drops the ENTIRE Support section. One delayed retry
      // (single-shot per session) covers the flaky-boot case (2026-06-10).
      if (!users.some(u => u.remote) && !state._peerUsersRetried) {
        state._peerUsersRetried = true;
        setTimeout(() => {
          loadTeamchatUsers().then(() => {
            if (state.activeView === 'teamchat') renderTeamchatList();
          }).catch(() => { /* peer genuinely down — local-only roster stands */ });
        }, 6000);
      }
      // Seed presence from server-provided online flag (initial socket
      // event covers post-load updates).
      state.teamPresence = new Set(state.teamUsers.filter(u => u.online).map(u => u.id));
      // First-time team load → re-render the open chat so the bubble
      // "by <agent>" labels swap from raw username to displayName. Same
      // pattern as ensureGroupMembersForCurrentChat: cold-load showed
      // raw "akil" / "asmae" until a chat switch.
      if (wasEmpty && state.selectedChatId && state.messages?.length) {
        renderMessages();
      }
    } catch { state.teamUsers = []; }
  }
  async function loadTeamchatRooms() {
    try {
      const r = await api('/api/team/rooms');
      const j = await r.json();
      state.teamRooms = j.rooms || [];
    } catch { state.teamRooms = []; }
  }
  async function loadTeamchatDigest() {
    try {
      const r = await api('/api/team/digest');
      const j = await r.json();
      state.teamDigest = j.digest || {};
    } catch { state.teamDigest = {}; }
    refreshTeamchatBadge();
  }

  // Sum of unread across every team conversation. Surfaced on the Team
  // chat top-nav tab so an agent on a different view sees a red dot the
  // moment a teammate sends them something. Recomputed cheaply each time
  // a team_message event lands or read state changes.
  function refreshTeamchatBadge() {
    const badge = document.getElementById('teamchatTabBadge');
    if (!badge) return;
    let total = 0, roomsUnread = 0;
    for (const k of Object.keys(state.teamDigest || {})) {
      const u = state.teamDigest[k]?.unread || 0;
      total += u;
      if (k.startsWith('team:room:')) roomsUnread += u;
    }
    if (total > 0) {
      badge.textContent = total > 99 ? '99+' : String(total);
      badge.classList.remove('hidden');
    } else {
      badge.textContent = '';
      badge.classList.add('hidden');
    }
    // Split across the seg toggles: Équipe carries the DM unread, Rooms the
    // room unread — so an agent on the client inbox sees at a glance WHICH
    // tab is calling for attention.
    const setSegCount = (sel, n) => document.querySelectorAll(sel).forEach(elc => {
      if (n > 0) {
        elc.textContent = n > 99 ? '99+' : String(n);
        elc.classList.remove('hidden');
      } else {
        elc.textContent = '';
        elc.classList.add('hidden');
      }
    });
    setSegCount('[data-seg-count="team"]', total - roomsUnread);
    setSegCount('[data-seg-count="rooms"]', roomsUnread);
    refreshGroupBadges();
  }

  // My own canonical cross-platform id: `platform@username` (lowercased).
  // Built from /api/me (which now returns platform + username). Used so a
  // cross-platform DM key matches the one the peer server computes.
  function myCanonId() {
    const m = state.me;
    if (!m || !m.platform || !m.username) return '';
    return m.platform + '@' + String(m.username).toLowerCase();
  }

  function teamMyIdentityId() {
    return state.me?.identityId || myCanonId() || state.me?.id || '';
  }
  function teamMessageIsMine(m) {
    if (!m) return false;
    return m.fromUserId === state.me?.id || m.fromUserId === myCanonId() ||
      (!!m.fromIdentityId && m.fromIdentityId === teamMyIdentityId());
  }

  function teamDmKey(a, b) {
    const sa = String(a), sb = String(b);
    // Cross-platform DM — if either id is a canonical `platform@username`
    // (remote teammate), build the shared `team:xdm:` key from canonical ids
    // (never UUIDs) so both servers agree. Mirrors the server's teamXdmKey.
    if (sa.includes('@') || sb.includes('@')) {
      const ca = sa.includes('@') ? sa : myCanonId();
      const cb = sb.includes('@') ? sb : myCanonId();
      const [lo, hi] = [ca, cb].sort();
      return 'team:xdm:' + lo + '|' + hi;
    }
    const [lo, hi] = [sa, sb].sort();
    return 'team:dm:' + lo + ':' + hi;
  }

  // Friendly snippet for the team-chat sidebar row. Replaces the bare
  // "[audio]" / "[image]" / "[file]" placeholders that the old code
  // produced — those looked broken in the list, especially for voice
  // notes where the icon should signal "voice" not "audio file".
  function teamchatPreviewLabel(lastMsg) {
    if (!lastMsg) return 'No messages yet.';
    if (lastMsg.text) return lastMsg.text;
    if (lastMsg.type === 'audio') return '🎵 Voice note';
    if (lastMsg.type === 'image') return '📷 Photo';
    if (lastMsg.type === 'video') return '🎥 Video';
    if (lastMsg.type === 'file' || lastMsg.type === 'document') return '📎 ' + (lastMsg.fileName || 'File');
    return '[' + (lastMsg.type || 'message') + ']';
  }

  function renderTeamchatList() {
    const list = document.getElementById('teamchatList');
    if (!list) return;
    clear(list);
    const TS = window.LoughaTeamSections;
    const q = (state.teamchatSearch || '').toLowerCase();
    const me = state.me?.id;

    // Rooms tab (2026-07-09): flat rooms-only list — teammates live on the
    // Équipe tab. No section header needed, the tab IS the section.
    if (state.teamchatActiveTab === 'rooms') {
      const rooms = (state.teamRooms || []).filter(r => !q || r.name.toLowerCase().includes(q));
      if (!rooms.length) {
        list.appendChild(el('div', { class: 'teamchat-empty-row', text: q ? 'No match.' : 'No rooms yet.' }));
        return;
      }
      rooms.sort((a, b) => {
        const ta = new Date(state.teamDigest['team:room:' + a.id]?.lastMessage?.createdAt || 0).getTime();
        const tb = new Date(state.teamDigest['team:room:' + b.id]?.lastMessage?.createdAt || 0).getTime();
        if (tb !== ta) return tb - ta;
        return a.name.localeCompare(b.name);
      });
      for (const r of rooms) list.appendChild(buildTeamchatRoomRow(r));
      refreshIcons();
      return;
    }

    const users = state.teamUsers.filter(u => !q || (userLabel(u.username, u.displayName || u.username)).toLowerCase().includes(q));
    // Team Bot — pinned above every section (and above search results when it
    // matches), so an agent always finds their alerts in the same place.
    const bot = state.teamBotUser && (!q || String(state.teamBotUser.displayName || 'team bot').toLowerCase().includes(q))
      ? state.teamBotUser : null;
    if (!users.length && !bot) {
      list.appendChild(el('div', { class: 'teamchat-empty-row', text: q ? 'No match.' : 'No teammates yet.' }));
      return;
    }
    if (bot) list.appendChild(buildTeamchatUserRow(bot));
    if (!users.length) { refreshIcons(); return; }

    const byRecency = (a, b) => {
      const ka = teamDmKey(me, a.id), kb = teamDmKey(me, b.id);
      const ta = new Date(state.teamDigest[ka]?.lastMessage?.createdAt || 0).getTime();
      const tb = new Date(state.teamDigest[kb]?.lastMessage?.createdAt || 0).getTime();
      if (tb !== ta) return tb - ta;
      return (userLabel(a.username, a.displayName || a.username)).localeCompare(userLabel(b.username, b.displayName || b.username));
    };

    // While searching: flat recency list — sections would just hide matches
    // behind collapsed headers.
    if (q || !TS) {
      users.sort(byRecency);
      for (const u of users) list.appendChild(buildTeamchatUserRow(u));
      refreshIcons();
      return;
    }

    // Sectioned roster (2026-06-10): Sales / Support (S2) / Teachers / Admin
    // — auto from role/platform, collapsible, per-user reorderable. The
    // 'rooms' pseudo-section moved to its own tab (2026-07-09): it is skipped
    // here but kept inside the persisted order so old prefs stay valid.
    const groups = { sales: [], support: [], teachers: [], admin: [], rooms: [] };
    for (const u of users) groups[TS.sectionOf(u)].push(u);
    for (const k of ['sales', 'support', 'teachers', 'admin']) groups[k].sort(byRecency);

    const prefs = loadTeamSecPrefs();
    const order = TS.applySavedOrder(TS.defaultOrder(state.me?.role), prefs.order);
    // ↑↓ math runs over the VISIBLE subsequence only (review TEAM-4): empty
    // sections are skipped below, so swapping against the full order would
    // produce dead clicks and wrongly-enabled arrows at the visible edges.
    const visibleOrder = order.filter(k => k !== 'rooms' && groups[k].length);

    for (const key of order) {
      if (key === 'rooms') continue;             // rooms have their own tab
      const members = groups[key];
      if (!members.length) continue;             // empty sections hidden
      const unread = members.reduce((sum, m) =>
        sum + (state.teamDigest[teamDmKey(me, m.id)]?.unread || 0), 0);
      const isClosed = !!prefs.collapsed[key];
      list.appendChild(buildTeamSectionHeader(key, members.length, unread, isClosed, order, visibleOrder));
      const body = el('div', { class: 'teamchat-sec-body' + (isClosed ? ' hidden' : ''), 'data-sec-body': key });
      for (const m of members) body.appendChild(buildTeamchatUserRow(m));
      list.appendChild(body);
    }
    refreshIcons();
  }

  // Section header: chevron + colored dot + label + member count + ↑↓ (on
  // hover) + unread badge. Click toggles collapse; ↑↓ reorder and persist.
  // Arrow enable/disable + swaps run over `visibleOrder` (non-empty sections
  // only); persistence still writes the FULL order so hidden sections keep
  // their slot for the day they get members.
  function buildTeamSectionHeader(key, count, unread, isClosed, order, visibleOrder) {
    const TS = window.LoughaTeamSections;
    const meta = TS.SECTIONS[key];
    const h = el('div', { class: 'teamchat-sec-head' + (isClosed ? ' closed' : ''), 'data-sec': key, role: 'button', tabindex: '0' });
    h.appendChild(el('span', { class: 'teamchat-sec-chev' }, [lucideIcon('chevron-down')]));
    h.appendChild(el('span', { class: 'teamchat-sec-dot sec-' + meta.cls }));
    h.appendChild(el('span', { class: 'teamchat-sec-lbl', text: meta.label }));
    h.appendChild(el('span', { class: 'teamchat-sec-cnt', text: String(count) }));
    h.appendChild(el('span', { class: 'teamchat-sec-spacer' }));
    const mv = el('span', { class: 'teamchat-sec-mv' });
    const vIdx = visibleOrder.indexOf(key);
    const up = el('button', { type: 'button', class: 'teamchat-sec-mvbtn', title: 'Monter' }, [lucideIcon('chevron-up')]);
    const dn = el('button', { type: 'button', class: 'teamchat-sec-mvbtn', title: 'Descendre' }, [lucideIcon('chevron-down')]);
    if (vIdx <= 0) up.disabled = true;
    if (vIdx >= visibleOrder.length - 1) dn.disabled = true;
    up.addEventListener('click', (e) => { e.stopPropagation(); moveTeamSection(key, -1, order, visibleOrder); });
    dn.addEventListener('click', (e) => { e.stopPropagation(); moveTeamSection(key, +1, order, visibleOrder); });
    mv.appendChild(up); mv.appendChild(dn);
    h.appendChild(mv);
    if (unread > 0) {
      h.appendChild(el('span', {
        class: 'teamchat-sec-badge' + (key === 'rooms' ? ' soft' : ''),
        text: unread > 99 ? '99+' : String(unread),
      }));
    }
    const toggle = () => {
      const prefs = loadTeamSecPrefs();
      prefs.collapsed[key] = !prefs.collapsed[key];
      saveTeamSecCollapsed(prefs.collapsed);
      renderTeamchatList();
    };
    h.addEventListener('click', toggle);
    h.addEventListener('keydown', (e) => {
      // Enter/Space on a focused ↑↓ button must activate the BUTTON (native
      // click), not collapse the section (review SEC-1).
      if (e.target !== h) return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
    });
    return h;
  }

  // Swap `key` with its VISIBLE neighbour, but persist the swap inside the
  // full order array (hidden sections keep their relative slots).
  function moveTeamSection(key, dir, order, visibleOrder) {
    const vi = visibleOrder.indexOf(key);
    const vj = vi + dir;
    if (vi < 0 || vj < 0 || vj >= visibleOrder.length) return;
    const neighbor = visibleOrder[vj];
    const next = order.slice();
    const i = next.indexOf(key), j = next.indexOf(neighbor);
    if (i < 0 || j < 0) return;
    [next[i], next[j]] = [next[j], next[i]];
    saveTeamSecOrder(next);
    renderTeamchatList();
  }

  function buildTeamchatUserRow(u) {
    const me = state.me?.id;
    const key = teamDmKey(me, u.id);
    const digest = state.teamDigest[key] || {};
    const isActive = state.teamchatActiveKey === key;
    if (u.system) return buildTeamBotRow(u, key, digest, isActive);
    const row = el('div', { class: 'teamchat-row' + (isActive ? ' active' : '') });
    const avatarBox = el('div', { class: 'teamchat-avatar-wrap' });
    avatarBox.appendChild(personalAvatar(u, 'avatar'));
    // Always show a presence dot so OFFLINE is explicit (green = online, grey =
    // offline) — not just "no dot". Remote (cross-platform) presence isn't
    // bridged, so it shows a neutral "unknown" dot rather than a false offline.
    const _pres = u.remote ? 'unknown' : (state.teamPresence.has(u.id) ? 'online' : 'offline');
    avatarBox.appendChild(el('span', {
      class: 'presence-dot ' + _pres,
      title: _pres === 'online' ? 'Online' : (_pres === 'offline' ? 'Offline' : 'Other platform'),
    }));
    row.appendChild(avatarBox);
    const body = el('div', { class: 'teamchat-row-body' });
    const nameLine = el('div', { class: 'teamchat-row-name' });
    nameLine.appendChild(el('span', { text: userLabel(u.username, u.displayName || u.username) }));
    if (u.remote) {
      // Teammate on the OTHER platform — small pill so the divide is clear
      // (Maryam on support2 vs Mary on Leads7 are distinct people).
      nameLine.appendChild(el('span', { class: 'teamchat-row-tag remote', text: u.platform === 'leads7' ? 'L7' : 'S2', title: u.platform }));
    }
    body.appendChild(nameLine);
    const preview = teamchatPreviewLabel(digest.lastMessage);
    body.appendChild(el('div', { class: 'teamchat-row-preview', text: preview }));
    row.appendChild(body);
    if ((digest.unread || 0) > 0) {
      row.appendChild(el('span', { class: 'teamchat-unread', text: digest.unread > 99 ? '99+' : String(digest.unread) }));
    }
    row.addEventListener('click', () => openTeamchatWith(u, null));
    return row;
  }

  // ─── Team Bot (read-only alerts DM) ──────────────────────────────────
  // A virtual teammate: server-side alerts about the agent's OWN work, plus
  // the admin mirror. No presence, no call, no composer — each alert carries
  // "Ouvrir" buttons that jump straight to the conversation.
  function isTeamBotTarget(target) {
    return !!(target && target.type === 'user' && target.user && target.user.system);
  }
  function buildTeamBotAvatar(size) {
    return el('div', { class: 'avatar teamchat-bot-avatar' + (size ? ' ' + size : ''), 'aria-hidden': 'true' }, [lucideIcon('bot')]);
  }
  function buildTeamBotRow(u, key, digest, isActive) {
    const row = el('div', { class: 'teamchat-row teamchat-row-bot' + (isActive ? ' active' : ''), role: 'button', tabindex: '0' });
    row.appendChild(el('div', { class: 'teamchat-avatar-wrap' }, [buildTeamBotAvatar()]));
    const body = el('div', { class: 'teamchat-row-body' });
    const nameLine = el('div', { class: 'teamchat-row-name' });
    nameLine.appendChild(el('span', { text: u.displayName || 'Team Bot' }));
    nameLine.appendChild(el('span', { class: 'teamchat-row-tag bot', text: 'Alertes' }));
    body.appendChild(nameLine);
    body.appendChild(el('div', {
      class: 'teamchat-row-preview',
      text: digest.lastMessage ? teamchatPreviewLabel(digest.lastMessage) : 'Hna kay-banou l’alertes dyalek.',
    }));
    row.appendChild(body);
    if ((digest.unread || 0) > 0) {
      row.appendChild(el('span', { class: 'teamchat-unread', text: digest.unread > 99 ? '99+' : String(digest.unread) }));
    }
    row.addEventListener('click', () => openTeamchatWith(u, null));
    row.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTeamchatWith(u, null); }
    });
    return row;
  }
  // Jump from an alert to the client conversation. The chat may not be in the
  // loaded list yet (filters, pagination) — mint a minimal row like the note
  // search does. Never put digits in the stub: masked agents must not see a
  // number the server would have masked.
  function openTeamBotAlertChat(item) {
    const chatId = String(item?.chatId || '');
    if (!chatId) return;
    showView('inbox');
    if (!state.chats.find(c => c.id === chatId)) {
      state.chats.unshift({ id: chatId, name: item.label || 'Client', phone: '', isGroup: false, lastMessage: null, unread: 0 });
    }
    setTimeout(() => navigateToChat(chatId), 80);
  }
  function buildTeamBotActions(m) {
    const items = Array.isArray(m?.meta?.items) ? m.meta.items.filter(i => i && i.chatId) : [];
    if (!items.length) return null;
    const wrap = el('div', { class: 'teamchat-bot-actions' });
    for (const it of items) {
      const btn = el('button', {
        type: 'button', class: 'teamchat-bot-open',
        title: 'Ouvrir la conversation', 'aria-label': 'Ouvrir la conversation — ' + (it.label || 'client'),
      }, [lucideIcon('arrow-up-right'), el('span', { text: it.label || 'Client' })]);
      btn.addEventListener('click', (ev) => { ev.stopPropagation(); openTeamBotAlertChat(it); });
      wrap.appendChild(btn);
    }
    return wrap;
  }

  function buildTeamchatRoomRow(r) {
    const key = 'team:room:' + r.id;
    const digest = state.teamDigest[key] || {};
    const isActive = state.teamchatActiveKey === key;
    const row = el('div', { class: 'teamchat-row' + (isActive ? ' active' : '') });
    row.appendChild(el('div', {
      class: 'avatar ' + avatarColorClass(r.id),
      text: '#'
    }));
    const body = el('div', { class: 'teamchat-row-body' });
    body.appendChild(el('div', { class: 'teamchat-row-name', text: r.name }));
    body.appendChild(el('div', { class: 'teamchat-row-preview', text: (r.memberCount ?? r.memberIds?.length ?? 0) + ' members' }));
    row.appendChild(body);
    if ((digest.unread || 0) > 0) {
      row.appendChild(el('span', { class: 'teamchat-unread', text: digest.unread > 99 ? '99+' : String(digest.unread) }));
    }
    row.addEventListener('click', () => openTeamchatWith(null, r));
    return row;
  }

  // Resolve a digest chatKey back to its user/room and open it. Used by the
  // desktop-notification click so it lands on the exact conversation.
  // Resolve-then-retry (review TEAM-2): rooms are NOT boot-loaded and
  // state.teamRooms is [] from init (truthy — a presence guard can't tell
  // "not loaded yet" from "no rooms"), and Workspace > Team can transiently
  // overwrite state.teamUsers with the management roster. So: try the
  // lookup; on a miss, await one loadTeamchat() and try again.
  async function openTeamchatByKey(chatKey) {
    if (!chatKey) return;
    showView('teamchat');
    const find = () => {
      if (chatKey.startsWith('team:room:')) {
        const room = (state.teamRooms || []).find(r => 'team:room:' + r.id === chatKey);
        return room ? { room } : null;
      }
      const me = state.me?.id;
      if (state.teamBotUser && teamDmKey(me, state.teamBotUser.id) === chatKey) return { user: state.teamBotUser };
      const user = (state.teamUsers || []).find(u => teamDmKey(me, u.id) === chatKey);
      return user ? { user } : null;
    };
    let hit = find();
    if (!hit) {
      try { await loadTeamchat(); } catch { /* list just stays on placeholder */ }
      hit = find();
    }
    if (hit) openTeamchatWith(hit.user || null, hit.room || null);
  }

  async function openTeamchatWith(user, room) {
    const me = state.me?.id;
    // Drop any multi-select carried from the previous teamchat — same
    // safety as the inbox selectConversation does for selectedMessageIds.
    if (state.selectedTeamchatMessageIds?.size && typeof clearTeamchatSelection === 'function') {
      clearTeamchatSelection();
    }
    if (user) {
      state.teamchatActiveKey = teamDmKey(me, user.id);
      state.teamchatActiveTarget = { type: 'user', user };
      // Deep links (digest / notification / global search) can open a DM
      // while the Rooms tab is active — flip so the list shows the row.
      setTeamchatTab('dms');
    } else if (room) {
      state.teamchatActiveKey = 'team:room:' + room.id;
      state.teamchatActiveTarget = { type: 'room', room };
      setTeamchatTab('rooms');
    } else {
      return;
    }
    document.getElementById('teamchatPanePlaceholder')?.classList.add('hidden');
    document.getElementById('teamchatActiveChat')?.classList.remove('hidden');
    // Optimistic paint: show the pane immediately from cache (or an empty
    // shell on first open) so switching never blocks on the network. Then
    // revalidate in the background and re-render just the message list if the
    // user is still on this chat.
    const key = state.teamchatActiveKey;
    state.teamchatMessages = state.teamThreadCache[key] || [];
    renderTeamchatActive();
    renderTeamchatList(); // refresh active highlight
    markTeamchatRead();
    loadTeamchatMessages().then(() => {
      if (state.teamchatActiveKey === key) renderTeamchatMessages({ forceBottom: true });
    });
  }

  // Scroll-back (2026-10-02): the thread used to stop at its last 100 rows.
  function _teamchatMsgTs(m) { return Number(m?.timestamp) || Date.parse(m?.createdAt) || 0; }
  function teamchatHasMore() { return !!(state.teamchatHasMoreByKey || {})[state.teamchatActiveKey]; }
  async function loadOlderTeamchatMessages() {
    const key = state.teamchatActiveKey;
    if (!key || !teamchatHasMore() || state.teamchatOlderLoading === key) return;
    const oldest = (state.teamchatMessages || []).find((m) => _teamchatMsgTs(m) > 0);
    if (!oldest) { state.teamchatHasMoreByKey[key] = false; return; }
    state.teamchatOlderLoading = key;
    renderTeamchatOlderBar();
    try {
      const r = await api('/api/team/messages?chatKey=' + encodeURIComponent(key) + '&limit=200&before=' + _teamchatMsgTs(oldest));
      const j = await r.json();
      if (state.teamchatActiveKey !== key) return;
      const seen = new Set((state.teamchatMessages || []).map((m) => m.id).filter(Boolean));
      const older = (j.messages || []).filter((m) => !m.id || !seen.has(m.id));
      state.teamchatHasMoreByKey[key] = !!j.hasMore && older.length > 0;
      if (older.length) {
        state.teamchatMessages = older.concat(state.teamchatMessages);
        state.teamThreadCache[key] = state.teamchatMessages;
        renderTeamchatMessages(); // not near the bottom → keeps the reader's place
      }
    } catch { /* keep what we have; the bar stays for a retry */ } finally {
      if (state.teamchatOlderLoading === key) state.teamchatOlderLoading = null;
      if (state.teamchatActiveKey === key) renderTeamchatOlderBar();
    }
  }
  function renderTeamchatOlderBar() {
    const wrap = document.getElementById('teamchatMessages');
    if (!wrap) return;
    let bar = wrap.querySelector(':scope > .teamchat-older-bar');
    if (!teamchatHasMore() || !state.teamchatMessages?.length) { bar?.remove(); return; }
    if (!bar) {
      bar = el('button', {
        type: 'button',
        class: 'teamchat-older-bar',
        style: 'display:block;margin:6px auto 10px;padding:5px 14px;border-radius:999px;border:1px solid rgba(0,0,0,.12);background:rgba(255,255,255,.85);color:#475569;font-size:12px;cursor:pointer',
      });
      bar.addEventListener('click', () => loadOlderTeamchatMessages());
      wrap.prepend(bar);
    }
    const busy = state.teamchatOlderLoading === state.teamchatActiveKey;
    bar.disabled = busy;
    bar.textContent = busy ? 'Loading older messages…' : '↑ Load older messages';
  }

  async function loadTeamchatMessages() {
    const key = state.teamchatActiveKey;
    if (!key) return;
    try {
      const r = await api('/api/team/messages?chatKey=' + encodeURIComponent(key) + '&limit=200');
      const j = await r.json();
      const msgs = j.messages || [];
      state.teamThreadCache[key] = msgs;
      state.teamchatHasMoreByKey = state.teamchatHasMoreByKey || {};
      state.teamchatHasMoreByKey[key] = !!j.hasMore;
      if (state.teamchatActiveKey === key) state.teamchatMessages = msgs;
    } catch {
      if (state.teamchatActiveKey === key && !state.teamThreadCache[key]) state.teamchatMessages = [];
    }
  }

  // ===================== Team voice call (WebRTC, 2026-06-01) =================
  // 1-1 voice call between teammates, INCLUDING cross-platform (Leads7 ↔
  // support2). Signalling rides socket.io: LOCAL calls via the 4 call:* events
  // (server relays in the user:<id> rooms); CROSS-PLATFORM calls are relayed by
  // the server over the team bridge and arrive as a single 'team_call_signal'.
  // Media is P2P (browser↔browser) forced through the shared Cloudflare TURN.
  // UI is built inline (no index.html / app.css dependency). Mirrors support2's
  // team-call module; ids are DISTINCT from the waCall module (#waCallPanel).
  state.teamCall = {
    pc: null, localStream: null, remoteAudioEl: null,
    peerUserId: null, peerName: '', role: null,
    status: 'idle',           // idle | ringing-out | ringing-in | active
    startedAt: 0, muted: false, timer: null, ringTimeout: null, pendingOffer: null,
    callId: null, reinvite: null, // offline-callee flow: full-sdp re-invite loop id
  };
  // ── Group (mesh ≤5) call state — port of support2's engine (2026-07-15) ──
  // A group call = N pairwise WebRTC peers (full mesh, no SFU). Kept fully
  // separate from state.teamCall so the 1-1 path can't regress. Peers are
  // keyed by CANON id (`leads7@user` / `support2@user`) so one mesh can span
  // both platforms.
  state.groupCall = {
    active: false,
    groupCallId: null,
    role: null,            // 'initiator' | 'participant'
    status: 'idle',        // idle | ringing-out | ringing-in | active
    localStream: null,
    peers: new Map(),      // userId -> { pc, name, audioEl, pendingCandidates:[], status }
    names: {},             // userId -> displayName
    muted: false,
    startedAt: 0,
    timer: null,
    ringTimeout: null,
    pendingInvite: null,   // {groupCallId, from, fromName, participants, names} while ringing-in
    invited: new Map(),    // canon → invitedAt — ringing/not-yet-joined rows in the panel
    soloPeerId: null,      // set when the call is effectively 1-1 → leaves the missed trace in that DM
    sourceKey: 'leads7',
    sourceLabel: 'LEADS7',
    transportLabel: 'TEAM',
    sourceSection: '',
    sourcePlatform: 'leads7',
  };
  // Offline local teammates (mobile app closed/backgrounded): their devices are
  // rung by the team-app's web push, and the invite is re-sent every 4s so the
  // socket they open from the push tap catches the call. All fire-and-forget —
  // if team.lougha.app is down, the classic socket path still works for online
  // callees and this degrades silently.
  const TEAM_APP_ORIGIN = 'https://team.lougha.app';
  function _tcRingPush(targetUserId) {
    try {
      fetch(TEAM_APP_ORIGIN + '/api/team-call/ring', {
        method: 'POST',
        headers: { ...authHeaders(), 'content-type': 'application/json' },
        body: JSON.stringify({ targetUserId }),
      }).catch(() => {});
    } catch { /* degrade silently */ }
  }
  function _tcRingCancel(targetUserId) {
    try {
      fetch(TEAM_APP_ORIGIN + '/api/team-call/ring-cancel', {
        method: 'POST',
        headers: { ...authHeaders(), 'content-type': 'application/json' },
        body: JSON.stringify({ targetUserId }),
      }).catch(() => {});
    } catch { /* degrade silently */ }
  }
  function _tcMissedMsg(targetUserId) {
    // WhatsApp-style persistent trace: unread badge + WA mirror if applicable.
    try {
      const fd = new FormData();
      fd.append('targetUserId', targetUserId);
      fd.append('text', '📞 Appel manqué');
      fetch('/api/team/send', { method: 'POST', headers: authHeaders(), body: fd }).catch(() => {});
    } catch { /* best-effort */ }
  }
  function _tcStopReinvite() {
    if (state.teamCall.reinvite) { clearInterval(state.teamCall.reinvite); state.teamCall.reinvite = null; }
  }
  function _tcWaitIceComplete(pc, capMs) {
    return new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') return resolve();
      const to = setTimeout(resolve, capMs || 5000);
      pc.addEventListener('icegatheringstatechange', function onS() {
        if (pc.iceGatheringState === 'complete') {
          clearTimeout(to);
          pc.removeEventListener('icegatheringstatechange', onS);
          resolve();
        }
      });
    });
  }
  try { window.__teamCall = state.teamCall; } catch {} // devtools diag (mirrors __waCall)
  function _tcAudioConstraints() {
    return { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
  }
  async function _tcRtcConfig() {
    // Shared Cloudflare TURN, forced relay (symmetric-NAT one-way-audio fix) —
    // same source the waCall module uses, so both call ends agree on TURN.
    try {
      const r = await api('/api/calls/turn-credentials');
      if (r.ok) {
        const j = await r.json();
        if (Array.isArray(j.iceServers) && j.iceServers.length) {
          // Only force relay when a real TURN url is present. If the endpoint
          // degraded to a STUN-only body (Cloudflare creds unhealthy), relay-only
          // would have NO relay candidates → the call could never connect. Fall
          // back to 'all' so a direct/STUN path can still complete.
          const hasTurn = j.iceServers.some(s => { const u = s.urls; return Array.isArray(u) ? u.some(x => /^turns?:/i.test(x)) : /^turns?:/i.test(u || ''); });
          return { iceServers: j.iceServers, iceTransportPolicy: hasTurn ? 'relay' : 'all', bundlePolicy: 'max-bundle', rtcpMuxPolicy: 'require' };
        }
      }
    } catch (_) { /* fall through */ }
    return { iceServers: [{ urls: 'stun:stun.cloudflare.com:3478' }, { urls: 'stun:stun.l.google.com:19302' }], iceTransportPolicy: 'all', bundlePolicy: 'max-bundle', rtcpMuxPolicy: 'require' };
  }
  function _tcResolveName(userId) {
    const u = (state.teamUsers || []).find(x => x.id === userId);
    return userLabel(u?.username, u?.displayName || u?.username) || 'Teammate';
  }
  function _tcIsRemote(id) { return typeof id === 'string' && id.includes('@'); }
  function _tcClearRingTimeout() {
    if (state.teamCall.ringTimeout) { clearTimeout(state.teamCall.ringTimeout); state.teamCall.ringTimeout = null; }
  }
  function _tcArmRingTimeout(ms) {
    _tcClearRingTimeout();
    state.teamCall.ringTimeout = setTimeout(() => {
      if (state.teamCall.status === 'ringing-out') {
        toast('No answer', 'error');
        const peer = state.teamCall.peerUserId;
        if (peer && !_tcIsRemote(peer)) { _tcRingCancel(peer); _tcMissedMsg(peer); }
        endTeamCall('no_answer');
      }
    }, ms || 35000);
  }
  function _tcAttachPc(pc) {
    pc.onicecandidate = (ev) => {
      if (ev.candidate && state.teamCall.peerUserId && state.socket) {
        state.socket.emit('call:ice-candidate', { to: state.teamCall.peerUserId, candidate: ev.candidate });
      }
    };
    pc.ontrack = (ev) => {
      const stream = (ev.streams && ev.streams[0]) || new MediaStream([ev.track]);
      let a = document.getElementById('teamCallRemoteAudio');
      if (!a) { a = document.createElement('audio'); a.id = 'teamCallRemoteAudio'; a.autoplay = true; a.playsInline = true; a.style.display = 'none'; document.body.appendChild(a); }
      a.srcObject = stream; a.volume = 1.0; a.muted = false;
      a.play().catch(() => toast('Audio blocked — tap the call bar', 'error'));
      state.teamCall.remoteAudioEl = a;
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      const sub = document.getElementById('teamCallPillStatus');
      if (sub && state.teamCall.status === 'active') {
        if (st === 'connected' || st === 'completed') sub.textContent = 'Connected';
        else if (st === 'checking') sub.textContent = 'Connecting…';
        else if (st === 'failed') sub.textContent = 'NAT failed';
        else if (st === 'disconnected') sub.textContent = 'Reconnecting…';
      }
      if (st === 'failed') { toast('Call failed — NAT traversal.', 'error'); endTeamCall('ice_failed'); }
    };
    pc.onconnectionstatechange = () => {
      if (['failed', 'closed'].includes(pc.connectionState)) endTeamCall('connection_lost');
    };
  }
  async function startTeamCall(targetUserId, targetName) {
    if (state.teamCall.status !== 'idle') { toast('Already in a call', 'error'); return; }
    // Offline LOCAL teammate = mobile app closed/backgrounded: web-push their
    // phone + re-invite with a FULL sdp every 4s (candidates trickled before
    // their socket exists would be lost). Remote (support2) teammates keep the
    // blind-ring. Online local teammates keep the classic trickle flow.
    const offlineLocal = !_tcIsRemote(targetUserId) && !state.teamPresence.has(targetUserId);
    const cfg = await _tcRtcConfig();
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: _tcAudioConstraints() }); }
    catch { toast('Mic access denied', 'error'); return; }
    const pc = new RTCPeerConnection(cfg);
    state.teamCall.pc = pc; state.teamCall.localStream = stream;
    state.teamCall.peerUserId = targetUserId; state.teamCall.peerName = targetName;
    state.teamCall.role = 'caller'; state.teamCall.status = 'ringing-out';
    state.teamCall.callId = 'tc_' + Math.random().toString(36).slice(2, 10);
    stream.getTracks().forEach(t => pc.addTrack(t, stream));
    _tcAttachPc(pc);
    try {
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      if (offlineLocal) {
        await _tcWaitIceComplete(pc, 5000);
        if (state.teamCall.status !== 'ringing-out') return; // hung up while gathering
        const payload = { to: targetUserId, sdp: pc.localDescription, callId: state.teamCall.callId };
        state.socket.emit('call:invite', payload);
        _tcRingPush(targetUserId);
        toast(targetName + ' est hors ligne — son téléphone va sonner (notification)');
        _tcStopReinvite();
        state.teamCall.reinvite = setInterval(() => {
          if (state.teamCall.status === 'ringing-out' && state.teamCall.peerUserId === targetUserId) {
            state.socket.emit('call:invite', payload);
          } else _tcStopReinvite();
        }, 4000);
      } else {
        state.socket.emit('call:invite', { to: targetUserId, sdp: offer, callId: state.teamCall.callId });
      }
      _tcShowPill('ringing-out'); _tcPlayRing('out'); _tcArmRingTimeout(offlineLocal ? 40000 : 35000);
    } catch { endTeamCall('offer_failed'); }
  }
  function _tcOnIncomingInvite({ from, sdp }) {
    // Re-invites: an offline-callee caller re-sends the same invite every 4s so
    // a push-woken app catches the call. If we're already ringing/talking with
    // THIS peer it's the same call — ignore instead of replying 'busy' (which
    // would kill the live attempt).
    if (state.teamCall.status !== 'idle' && state.teamCall.peerUserId === from) return;
    if (state.teamCall.status !== 'idle') { state.socket?.emit('call:end', { to: from, reason: 'busy' }); return; }
    state.teamCall.peerUserId = from; state.teamCall.peerName = _tcResolveName(from);
    state.teamCall.role = 'callee'; state.teamCall.status = 'ringing-in'; state.teamCall.pendingOffer = sdp;
    _tcShowIncoming(); _tcPlayRing('in');
    // Callee-side ring timeout (45s > the caller's 35s). The caller's 'end'
    // signal rides a fire-and-forget bridge with no retry, so if it's lost the
    // banner+ring would hang forever — self-dismiss as a missed call.
    _tcClearRingTimeout();
    state.teamCall.ringTimeout = setTimeout(() => {
      if (state.teamCall.status === 'ringing-in') _tcOnRemoteEnd({ reason: 'no_answer' });
    }, 45000);
  }
  async function _tcAccept() {
    const { peerUserId, pendingOffer } = state.teamCall;
    if (!peerUserId || !pendingOffer) return;
    const cfg = await _tcRtcConfig();
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: _tcAudioConstraints() }); }
    catch { toast('Mic access denied', 'error'); _tcDecline(); return; }
    // The caller may have hung up while we were granting mic / fetching ICE.
    if (state.teamCall.status !== 'ringing-in' || state.teamCall.peerUserId !== peerUserId) { stream.getTracks().forEach(t => t.stop()); return; }
    const pc = new RTCPeerConnection(cfg);
    state.teamCall.pc = pc; state.teamCall.localStream = stream;
    stream.getTracks().forEach(t => pc.addTrack(t, stream));
    _tcAttachPc(pc);
    try {
      await pc.setRemoteDescription(pendingOffer);
      _tcFlushCandidates();
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      state.socket.emit('call:answer', { to: peerUserId, sdp: answer });
      state.teamCall.pendingOffer = null;
      _tcHideIncoming(); _tcMarkActive();
    } catch { endTeamCall('answer_failed'); }
  }
  function _tcDecline() {
    const peer = state.teamCall.peerUserId;
    if (peer) state.socket?.emit('call:end', { to: peer, reason: 'declined' });
    _tcReset(); _tcHideIncoming();
  }
  async function _tcOnAnswer({ sdp }) {
    const pc = state.teamCall.pc; if (!pc) return;
    _tcStopReinvite();
    const peer = state.teamCall.peerUserId;
    if (peer && !_tcIsRemote(peer)) _tcRingCancel(peer); // retract the ring on their other devices
    try { await pc.setRemoteDescription(sdp); _tcFlushCandidates(); _tcMarkActive(); }
    catch { endTeamCall('set_remote_failed'); }
  }
  // Buffer remote ICE until the pc EXISTS and its remoteDescription is set.
  // Critical for cross-platform forced-relay: the caller's single relay
  // candidate is gathered ~immediately and relayed while the callee is still
  // ringing (no pc yet) — adding it then throws and it is NEVER re-sent, so
  // ICE can't pair and there's no audio. Buffer now, flush after SRD.
  async function _tcOnIce({ candidate }) {
    if (!candidate) return;
    const pc = state.teamCall.pc;
    if (!pc || !pc.remoteDescription || !pc.remoteDescription.type) {
      (state.teamCall.pendingCandidates = state.teamCall.pendingCandidates || []).push(candidate);
      return;
    }
    try { await pc.addIceCandidate(candidate); } catch { /* dupe/late — fine */ }
  }
  function _tcFlushCandidates() {
    const pc = state.teamCall.pc;
    const list = state.teamCall.pendingCandidates || [];
    state.teamCall.pendingCandidates = [];
    if (!pc) return;
    for (const c of list) { pc.addIceCandidate(c).catch(() => {}); }
  }
  function _tcMarkActive() {
    state.teamCall.status = 'active'; state.teamCall.startedAt = Date.now();
    _tcClearRingTimeout();
    if (state.teamCall.timer) clearInterval(state.teamCall.timer);
    state.teamCall.timer = setInterval(_tcTickTimer, 1000);
    _tcShowPill('active'); _tcStopRing();
  }
  function endTeamCall(reason) {
    const peer = state.teamCall.peerUserId; const was = state.teamCall.status;
    if (peer && state.socket && was !== 'idle') state.socket.emit('call:end', { to: peer, reason: reason || 'hangup' });
    // hung up while still ringing → retract the push notification (timeout path
    // already cancelled + left the missed-call trace in _tcArmRingTimeout)
    if (peer && was === 'ringing-out' && reason !== 'no_answer' && !_tcIsRemote(peer)) _tcRingCancel(peer);
    _tcReset(); _tcHideIncoming(); _tcHidePill();
  }
  function _tcOnRemoteEnd({ reason }) {
    if (state.teamCall.status === 'idle') return;
    const was = state.teamCall.status;
    _tcReset(); _tcHideIncoming(); _tcHidePill();
    if (reason === 'busy') toast('User is in another call', 'error');
    else if (reason === 'declined' && was === 'ringing-out') toast('Call declined');
  }
  function _tcReset() {
    _tcClearRingTimeout();
    _tcStopReinvite();
    state.teamCall.callId = null;
    if (state.teamCall.timer) { clearInterval(state.teamCall.timer); state.teamCall.timer = null; }
    if (state.teamCall.localStream) state.teamCall.localStream.getTracks().forEach(t => t.stop());
    if (state.teamCall.pc) { try { state.teamCall.pc.close(); } catch {} }
    if (state.teamCall.remoteAudioEl) state.teamCall.remoteAudioEl.srcObject = null;
    state.teamCall.pc = null; state.teamCall.localStream = null;
    state.teamCall.peerUserId = null; state.teamCall.peerName = '';
    state.teamCall.role = null; state.teamCall.status = 'idle';
    state.teamCall.startedAt = 0; state.teamCall.muted = false; state.teamCall.pendingOffer = null;
    state.teamCall.pendingCandidates = [];
    _tcStopRing();
  }
  function _tcToggleMute() {
    const stream = state.teamCall.localStream; if (!stream) return;
    state.teamCall.muted = !state.teamCall.muted;
    stream.getAudioTracks().forEach(t => { t.enabled = !state.teamCall.muted; });
    const btn = document.getElementById('teamCallPillMute');
    if (btn) { btn.textContent = state.teamCall.muted ? '🔇' : '🎙'; btn.title = state.teamCall.muted ? 'Unmute' : 'Mute'; }
  }
  function _tcFmt(ms) { const s = Math.max(0, Math.floor(ms / 1000)); const m = Math.floor(s / 60); const ss = s % 60; return m + ':' + (ss < 10 ? '0' : '') + ss; }
  function _tcTickTimer() {
    const t = document.getElementById('teamCallPillTime');
    if (t && state.teamCall.startedAt) t.textContent = _tcFmt(Date.now() - state.teamCall.startedAt);
  }
  // ── Inline call pill (bottom-right) ──
  function _tcShowPill(mode) {
    _tcHideIncoming();
    let pill = document.getElementById('teamCallPill');
    if (!pill) {
      pill = el('div', { id: 'teamCallPill', style: 'position:fixed;right:18px;bottom:18px;z-index:10000;min-width:230px;background:#111b21;color:#fff;border-radius:14px;box-shadow:0 8px 30px rgba(0,0,0,.35);padding:12px 14px;display:flex;align-items:center;gap:12px;font-size:14px;' });
      document.body.appendChild(pill);
    }
    clear(pill);
    const info = el('div', { style: 'flex:1;min-width:0;' }, [
      el('div', { style: 'font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', text: state.teamCall.peerName || 'Call' }),
      el('div', { id: 'teamCallPillStatus', style: 'font-size:12px;opacity:.8;', text: mode === 'active' ? 'Connected' : 'Ringing…' }),
    ]);
    const time = el('div', { id: 'teamCallPillTime', style: 'font-variant-numeric:tabular-nums;font-size:12px;opacity:.85;', text: mode === 'active' ? '0:00' : '' });
    const muteBtn = el('button', { id: 'teamCallPillMute', title: 'Mute', style: 'background:#2a3942;border:none;color:#fff;width:34px;height:34px;border-radius:50%;cursor:pointer;font-size:15px;', text: '🎙' });
    muteBtn.addEventListener('click', _tcToggleMute);
    const endBtn = el('button', { title: 'End call', style: 'background:#e23b3b;border:none;color:#fff;width:34px;height:34px;border-radius:50%;cursor:pointer;font-size:16px;', text: '☎' });
    endBtn.addEventListener('click', () => endTeamCall('hangup'));
    pill.appendChild(info);
    if (mode === 'active') { pill.appendChild(time); pill.appendChild(muteBtn); }
    pill.appendChild(endBtn);
  }
  function _tcHidePill() { document.getElementById('teamCallPill')?.remove(); }
  // ── Inline incoming-call banner (top-center) ──
  function _tcShowIncoming() {
    let b = document.getElementById('teamIncomingCall');
    if (!b) {
      b = el('div', { id: 'teamIncomingCall', style: 'position:fixed;top:18px;left:50%;transform:translateX(-50%);z-index:10001;background:#111b21;color:#fff;border-radius:14px;box-shadow:0 8px 30px rgba(0,0,0,.4);padding:14px 16px;display:flex;align-items:center;gap:14px;font-size:14px;' });
      document.body.appendChild(b);
    }
    clear(b);
    b.appendChild(el('div', { style: 'flex:1;' }, [
      el('div', { style: 'font-weight:700;', text: state.teamCall.peerName || 'Teammate' }),
      el('div', { style: 'font-size:12px;opacity:.8;', text: 'Incoming call…' }),
    ]));
    const acc = el('button', { title: 'Accept', style: 'background:#0a8a5f;border:none;color:#fff;width:40px;height:40px;border-radius:50%;cursor:pointer;font-size:18px;', text: '☎' });
    acc.addEventListener('click', _tcAccept);
    const dec = el('button', { title: 'Decline', style: 'background:#e23b3b;border:none;color:#fff;width:40px;height:40px;border-radius:50%;cursor:pointer;font-size:18px;', text: '✕' });
    dec.addEventListener('click', _tcDecline);
    b.appendChild(acc); b.appendChild(dec);
  }
  function _tcHideIncoming() { document.getElementById('teamIncomingCall')?.remove(); }
  // ── Ring synth (WebAudio, no binary asset) ──
  let _tcRingCtx = null, _tcRingTimer = null, _tcRingNodes = [], _tcRingPlayer = null;
  function _tcStopRing() {
    if (_tcRingPlayer) { _tcRingPlayer.stop(); _tcRingPlayer = null; }
    if (_tcRingTimer) { clearInterval(_tcRingTimer); _tcRingTimer = null; }
    for (const n of _tcRingNodes) { try { n.stop(); n.disconnect(); } catch {} }
    _tcRingNodes = [];
  }
  function _tcPlayRing(dir) {
    _tcStopRing();
    if (dir === 'in') {
      try { _tcRingPlayer = window.__loughaCallRingtone?.start('team', 'ring'); if (_tcRingPlayer) return; } catch { /* legacy incoming ring below */ }
    }
    try {
      _tcRingCtx = _tcRingCtx || new (window.AudioContext || window.webkitAudioContext)();
      const ctx = _tcRingCtx;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const beep = () => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = 'sine'; o.frequency.value = dir === 'in' ? 480 : 440;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.05);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
        o.connect(g); g.connect(ctx.destination);
        o.start(); o.stop(ctx.currentTime + 0.65);
        _tcRingNodes.push(o);
      };
      beep();
      _tcRingTimer = setInterval(beep, dir === 'in' ? 1500 : 3000);
    } catch { /* WebAudio blocked — silent ring is acceptable */ }
  }

  // ═══ Group (mesh ≤5) team calls — port of support2's engine (2026-07-15) ═══
  // Full-mesh WebRTC: every participant holds one RTCPeerConnection per other
  // participant. The server (server.js group:*) tracks the roster and tells
  // each participant who to connect to; the deterministic offerer (smaller
  // CANON id) sends the offer so both ends don't offer at once (glare).
  // Reuses the local 1-1 helpers (_tcRtcConfig/_tcAudioConstraints/_tcPlayRing/
  // _tcStopRing/_tcResolveName) + a same-browser BroadcastChannel ring election.

  // Presentation-only source metadata for the call panel/ring card. Mirror of
  // support2's classifier with the platform flipped: the local catch-all is
  // LEADS7; support2/teachers peers get their own label.
  function _teamCallSourceMeta({ from, fromSection, fromPlatform } = {}) {
    const row = (state.teamUsers || []).find(x => String(x.id) === String(from || '')) || null;
    const canonicalPlatform = String(from || '').includes('@') ? String(from).split('@')[0] : '';
    const platform = String(fromPlatform || row?.platform || canonicalPlatform || state.me?.platform || 'leads7').trim().toLowerCase();
    const TS = window.LoughaTeamSections;
    const section = String(fromSection || (row && TS ? TS.sectionOf(row) : '') || '').trim().toLowerCase();
    if (section === 'teachers') return { sourceKey: 'teachers', sourceLabel: 'TEACHERS', transportLabel: 'TEAM', sourceSection: section, sourcePlatform: platform };
    if (platform === 'support2' || section === 'support') return { sourceKey: 'support2', sourceLabel: 'SUPPORT2', transportLabel: 'TEAM', sourceSection: section, sourcePlatform: platform };
    return { sourceKey: 'leads7', sourceLabel: 'LEADS7', transportLabel: 'TEAM', sourceSection: section, sourcePlatform: platform || 'leads7' };
  }
  // Flash the BROWSER TAB TITLE while a call is ringing — visible in the tab bar
  // even when the tab is in the background (the #1 reason a teammate "never got"
  // the call: the app is open but not the focused tab). Permission-free, unlike
  // the Notification API. Reset on answer/decline/end (via _gcHideIncomingModal).
  let _incomingTitleTimer = null, _origDocTitle = null;
  function _startIncomingTitle(name) {
    if (_incomingTitleTimer) return;
    if (_origDocTitle == null) _origDocTitle = document.title;
    const a = '📞 ' + (name || 'Incoming call'), b = '📞 Incoming call…';
    let on = true; document.title = a;
    _incomingTitleTimer = setInterval(() => { document.title = (on = !on) ? a : b; }, 1000);
  }
  function _stopIncomingTitle() {
    if (_incomingTitleTimer) { clearInterval(_incomingTitleTimer); _incomingTitleTimer = null; }
    if (_origDocTitle != null) { document.title = _origDocTitle; _origDocTitle = null; }
  }
  // Bridge for the lazily-loaded softphone module (separate scope, swat:B2).
  // document.title must have exactly ONE owner: two independent flashers each
  // capture "the original title" from the live value, so an overlapping WA +
  // 3CX ring garbles the tab and can leave a flash frame stuck as the title.
  // These already self-guard (start is a no-op while a flash is running).
  window.__sfpTitleFlash = { start: _startIncomingTitle, stop: _stopIncomingTitle };
  // Shared remote-audio attach (one hidden <audio> per mesh peer). Plays the
  // remote stream; on autoplay-block flags the panel so a tap re-attempts play().
  function _attachRemoteAudio(stream, audioElId, pillId) {
    let audioEl = document.getElementById(audioElId);
    if (!audioEl) {
      audioEl = document.createElement('audio');
      audioEl.id = audioElId;
      audioEl.autoplay = true;
      audioEl.playsInline = true;
      audioEl.controls = false;
      audioEl.style.display = 'none';
      document.body.appendChild(audioEl);
    }
    audioEl.srcObject = stream;
    audioEl.volume = 1.0;
    audioEl.muted = false;
    audioEl.play().then(() => {
      console.log('[call] remote audio playing', audioElId);
    }).catch((err) => {
      console.warn('[call] remote audio play() rejected:', err.name, err.message);
      toast('Audio blocked — tap the call panel once', 'error');
      if (pillId) document.getElementById(pillId)?.classList.add('needs-gesture');
    });
    return audioEl;
  }
  // ── Multi-tab ring coordination (same design + channel name as support2) ──
  // The server fans a group invite to EVERY tab of the callee (user:<id> room).
  // A same-origin BroadcastChannel elects ONE ringer (prefer the visible tab)
  // so only the active page makes noise, and answer/decline broadcasts a
  // dismiss to sibling tabs AND emits 'group:handled' (server-relayed → other
  // browsers). BroadcastChannel is same-origin, so support2 tabs can never
  // hear these messages — sharing the channel name is pure symmetry.
  let _tcBC = null, _tcTabId = null, _tcRingClaims = [], _tcElectTimer = null;
  try {
    _tcTabId = (crypto.randomUUID && crypto.randomUUID()) || String(Math.random());
    _tcBC = ('BroadcastChannel' in window) ? new BroadcastChannel('lougha_teamcall_v1') : null;
  } catch { _tcBC = null; }
  if (_tcBC) _tcBC.onmessage = (e) => {
    const m = (e && e.data) || {};
    if (m.k === 'claim' || m.k === 'claim-ack') {
      // A sibling tab is also ringing this group call — record its bid. (The
      // 1-1 lane on Leads7 doesn't stamp a callee-side callId, so it doesn't
      // take part in the election — group calls only.)
      const ringing = state.groupCall.status === 'ringing-in' && state.groupCall.groupCallId === m.callId;
      if (ringing) {
        if (!_tcRingClaims.some(c => c.tabId === m.tabId)) _tcRingClaims.push({ tabId: m.tabId, visible: !!m.visible });
        // Ack a FRESH claim so a tab that received our claim before it was ready
        // (invite arrives a few ms apart) still learns we exist — without this,
        // two tabs each think they're the sole winner and both ring. Don't ack
        // an ack (avoids an echo storm).
        if (m.k === 'claim') {
          try { _tcBC.postMessage({ k: 'claim-ack', callId: m.callId, tabId: _tcTabId, visible: document.visibilityState === 'visible' }); } catch {}
        }
      }
    } else if (m.k === 'dismiss') {
      _gcDismissHandledElsewhere(m.callId);
    }
  };

  const GC_MAX = 5;
  // Group calls are keyed by CANON id (`leads7@user` / `support2@user`) so one
  // mesh can span both platforms. myCanonId() is hoisted from the team-chat
  // module; fall back to the raw UUID while state.me is still loading.
  function _gcMyId() { return myCanonId() || (state.me?.id || ''); }
  // Canon id for a roster entry: remote teammates already carry one as their id;
  // local teammates are platform@username.
  function _gcCanonFor(u) {
    if (!u) return '';
    return u.remote ? String(u.id || '').toLowerCase()
      : ((state.me?.platform || 'leads7') + '@' + String(u.username || '').toLowerCase());
  }
  // Cross-platform peer? (different canon prefix → the leg rides the team-chat
  // bridge; media meets in the shared Cloudflare TURN relay.)
  function _gcIsCross(canon) {
    const s = String(canon || '').toLowerCase();
    if (!s.includes('@')) return false;   // legacy UUID = local, never cross
    return !s.startsWith((state.me?.platform || 'leads7') + '@');
  }
  function _gcName(userId, fallback) {
    return adminLabelFor(userId)
      || userLabel((state.groupCall.names && state.groupCall.names[userId]) || _tcResolveName(userId) || fallback || 'Teammate');
  }
  function _gcEnsureLocalStream() {
    if (state.groupCall.localStream) return Promise.resolve(state.groupCall.localStream);
    // Memoise the in-flight getUserMedia so concurrent peers (roster + an early
    // offer) don't each open a second mic stream.
    if (state.groupCall._streamPromise) return state.groupCall._streamPromise;
    state.groupCall._streamPromise = navigator.mediaDevices.getUserMedia({ audio: _tcAudioConstraints() })
      .then(stream => {
        state.groupCall.localStream = stream;
        if (state.groupCall.muted) stream.getAudioTracks().forEach(t => t.enabled = false);
        state.groupCall._streamPromise = null;
        return stream;
      })
      .catch(e => { state.groupCall._streamPromise = null; throw e; });
    return state.groupCall._streamPromise;
  }
  async function _gcMakePeer(userId) {
    let p = state.groupCall.peers.get(userId);
    if (p && p.pc) return p;
    // Per-peer media config. Leads7 is the SOURCE of the shared Cloudflare TURN
    // (/api/calls/turn-credentials): local 1-1 team calls already force the
    // relay through _tcRtcConfig(), and a cross-platform (support2) leg must
    // meet in the SAME relay — so every mesh leg rides that one config.
    const cfg = await _tcRtcConfig();
    p = state.groupCall.peers.get(userId);      // re-check after the await (no double-create)
    if (p && p.pc) return p;
    const pc = new RTCPeerConnection(cfg);
    p = { pc, name: _gcName(userId), audioEl: null, pendingCandidates: [], status: 'connecting' };
    state.groupCall.peers.set(userId, p);
    if (state.groupCall.peers.size >= 2) state.groupCall.soloPeerId = null; // became a real group → no 1-1 trace
    const ls = state.groupCall.localStream;
    if (ls) ls.getTracks().forEach(t => pc.addTrack(t, ls));
    pc.onicecandidate = (ev) => {
      if (ev.candidate && state.socket) {
        state.socket.emit('group:signal', { groupCallId: state.groupCall.groupCallId, to: userId, kind: 'ice', candidate: ev.candidate });
      }
    };
    pc.ontrack = (ev) => {
      const stream = (ev.streams && ev.streams[0]) || new MediaStream([ev.track]);
      p.audioEl = _attachRemoteAudio(stream, 'gcAudio_' + userId, 'groupCallPanel');
      p.status = 'connected';
      _gcRenderPanel();
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      if (st === 'connected' || st === 'completed') p.status = 'connected';
      else if (st === 'failed') p.status = 'failed';
      else if (st === 'checking') p.status = 'connecting';
      _gcRenderPanel();
    };
    return p;
  }
  function _gcFlushCandidates(userId) {
    const p = state.groupCall.peers.get(userId);
    if (!p || !p.pc || !p.pc.remoteDescription) return;
    for (const c of p.pendingCandidates.splice(0)) { try { p.pc.addIceCandidate(c); } catch {} }
  }
  async function _gcConnectTo(userId, initiate) {
    if (userId === _gcMyId()) return;
    await _gcEnsureLocalStream();
    const p = await _gcMakePeer(userId);
    if (initiate && p.pc.signalingState === 'stable' && !p.pc.localDescription) {
      try {
        const offer = await p.pc.createOffer({ offerToReceiveAudio: true });
        await p.pc.setLocalDescription(offer);
        state.socket.emit('group:signal', { groupCallId: state.groupCall.groupCallId, to: userId, kind: 'offer', sdp: offer });
      } catch (e) { console.warn('[gc] offer failed', userId, e.message); }
    }
    _gcRenderPanel();
  }
  async function startGroupCall(inviteeIds, names) {
    if (state.teamCall.status !== 'idle' || state.groupCall.active) { toast('Already in a call', 'error'); return; }
    inviteeIds = [...new Set((inviteeIds || []).filter(id => id && id !== _gcMyId()))].slice(0, GC_MAX - 1);
    if (!inviteeIds.length) return;
    try { await _gcEnsureLocalStream(); } catch { toast('Mic access denied', 'error'); state.groupCall.localStream = null; return; }
    const gid = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : ('gc_' + Date.now() + '_' + Math.random().toString(36).slice(2));
    state.groupCall.active = true;
    state.groupCall.groupCallId = gid;
    state.groupCall.role = 'initiator';
    state.groupCall.status = 'ringing-out';
    state.groupCall.startedAt = 0;
    state.groupCall.names = Object.assign({}, names || {});
    Object.assign(state.groupCall, _teamCallSourceMeta({ from: _gcMyId(), fromSection: state.me?.section, fromPlatform: state.me?.platform || 'leads7' }));
    state.groupCall.names[_gcMyId()] = userLabel(state.me?.username, state.me?.displayName || state.me?.username || 'Me');
    state.groupCall.soloPeerId = inviteeIds.length === 1 ? inviteeIds[0] : null; // 1-1 → missed trace to that DM
    // names travel with the start so the server can label REMOTE (peer-platform)
    // invitees it can't resolve locally; local names stay server-derived (anti-spoof).
    state.groupCall.invited = new Map(inviteeIds.map(id => [id, Date.now()]));   // "ringing…" rows
    state.socket.emit('group:start', { groupCallId: gid, invitees: inviteeIds, names: state.groupCall.names, fromName: state.groupCall.names[_gcMyId()] });
    _gcShowPanel();
    _tcPlayRing('out');
    clearTimeout(state.groupCall.ringTimeout);
    state.groupCall.ringTimeout = setTimeout(() => {
      if (state.groupCall.active && state.groupCall.peers.size === 0) { toast('No answer', 'error'); _gcLogSolo('unanswered', 'caller'); leaveGroupCall(); }
    }, 40000);
  }
  function _gcOnInvite(inv) {
    if (!inv || !inv.groupCallId) return;
    if (state.teamCall.status !== 'idle' || state.groupCall.active || state.groupCall.status === 'ringing-in') return;
    const others = (inv.participants || []).filter(id => id !== _gcMyId());
    const sourceMeta = _teamCallSourceMeta({ from: inv.from, fromSection: inv.fromSection, fromPlatform: inv.fromPlatform });
    state.groupCall.pendingInvite = { groupCallId: inv.groupCallId, from: inv.from, fromName: inv.fromName, fromSection: inv.fromSection, fromPlatform: inv.fromPlatform, participants: inv.participants || [], names: inv.names || {} };
    Object.assign(state.groupCall, sourceMeta);
    state.groupCall.groupCallId = inv.groupCallId;
    state.groupCall.names = Object.assign({}, inv.names || {});
    state.groupCall.status = 'ringing-in';
    state.groupCall.soloPeerId = others.length === 1 ? inv.from : null;
    _gcShowIncomingModal(inv.fromName || 'Teammate', others.length, sourceMeta);
    _gcElectRing(inv.groupCallId);
    clearTimeout(state.groupCall.ringTimeout);
    state.groupCall.ringTimeout = setTimeout(() => { if (state.groupCall.status === 'ringing-in') { _gcLogSolo('missed', 'callee'); _gcDismissIncoming(); } }, 45000);
  }
  async function acceptGroupCall() {
    const inv = state.groupCall.pendingInvite;
    if (!inv) return;
    try { await _gcEnsureLocalStream(); } catch {
      // A denied mic on this device must not decline for the same account's
      // other devices; dismiss only this local contender.
      toast('Mic access denied', 'error');
      _gcHideIncomingModal();
      _gcResetState();
      return;
    }
    if (state.groupCall.status !== 'ringing-in') return; // dismissed while granting mic
    state.groupCall.active = true;
    state.groupCall.role = 'participant';
    state.groupCall.status = 'active';
    state.groupCall.startedAt = Date.now();
    _gcSignalHandled(inv.groupCallId);          // stop the ring on my other tabs/devices
    _gcHideIncomingModal();
    _tcStopRing();
    state.socket.emit('group:accept', { groupCallId: inv.groupCallId });
    state.groupCall.pendingInvite = null;
    _gcShowPanel();
    _gcStartTimer();
  }
  function declineGroupCall() {
    const inv = state.groupCall.pendingInvite;
    if (inv) { try { state.socket?.emit('group:decline', { groupCallId: inv.groupCallId }); } catch {} _gcSignalHandled(inv.groupCallId); }
    _gcLogSolo('declined', 'callee');
    _gcHideIncomingModal();
    _gcResetState();
  }
  // Solo (1-1-equivalent) call outcome trace. Call sites mirror support2's
  // (missed/declined/ended/unanswered) but Leads7 has no /api/team/call-log
  // endpoint — so only the CALLER's unanswered ring leaves a trace, via the
  // same "📞 Appel manqué" team message the 1-1 lane uses (_tcMissedMsg); the
  // other statuses are accepted and ignored (future call-log hook point).
  // soloPeerId is a CANON id; the send endpoint expects a local UUID, so map a
  // same-platform canon back through the roster — cross-platform traces are
  // the peer server's concern (skip).
  function _gcLogSolo(status, role, ms) {
    if (status !== 'unanswered') return;
    let pid = state.groupCall.soloPeerId;
    if (!pid) return;
    if (String(pid).includes('@')) {
      if (_gcIsCross(pid)) return;
      const uname = String(pid).split('@')[1] || '';
      const u = (state.teamUsers || []).find(x => !x.remote && String(x.username || '').toLowerCase() === uname);
      if (!u) return;
      pid = u.id;
    }
    try { _tcMissedMsg(pid); } catch { }
  }
  function _gcOnRoster(msg) {
    if (!state.groupCall.active || state.groupCall.groupCallId !== msg.groupCallId) return;
    if (msg.names) Object.assign(state.groupCall.names, msg.names);
    for (const pid of (msg.peers || [])) _gcConnectTo(pid, _gcMyId() < pid);
    _gcRenderPanel();
  }
  function _gcOnPeerJoined(msg) {
    if (!state.groupCall.active || state.groupCall.groupCallId !== msg.groupCallId) return;
    if (msg.name) state.groupCall.names[msg.userId] = msg.name;
    try { state.groupCall.invited.delete(msg.userId); } catch { }
    if (state.groupCall.status === 'ringing-out') {
      state.groupCall.status = 'active'; state.groupCall.startedAt = Date.now();
      _tcStopRing(); _gcStartTimer(); clearTimeout(state.groupCall.ringTimeout);
    }
    _gcConnectTo(msg.userId, _gcMyId() < msg.userId);
  }
  async function _gcOnSignal(msg) {
    if (!state.groupCall.active || state.groupCall.groupCallId !== msg.groupCallId) return;
    const from = msg.from;
    if (!from || from === _gcMyId()) return;
    if (msg.kind === 'offer') {
      await _gcEnsureLocalStream();
      const p = await _gcMakePeer(from);
      try {
        await p.pc.setRemoteDescription(msg.sdp);
        _gcFlushCandidates(from);
        const answer = await p.pc.createAnswer();
        await p.pc.setLocalDescription(answer);
        state.socket.emit('group:signal', { groupCallId: msg.groupCallId, to: from, kind: 'answer', sdp: answer });
      } catch (e) { console.warn('[gc] answer failed', from, e.message); }
    } else if (msg.kind === 'answer') {
      const p = state.groupCall.peers.get(from);
      if (p && p.pc) { try { await p.pc.setRemoteDescription(msg.sdp); _gcFlushCandidates(from); } catch {} }
    } else if (msg.kind === 'ice') {
      const p = state.groupCall.peers.get(from);
      if (p && p.pc && msg.candidate) {
        if (p.pc.remoteDescription) { try { await p.pc.addIceCandidate(msg.candidate); } catch {} }
        else p.pendingCandidates.push(msg.candidate);
      }
    }
  }
  function _gcOnPeerLeft(msg) {
    if (state.groupCall.groupCallId !== msg.groupCallId) return;
    try { state.groupCall.invited.delete(msg.userId); } catch { }   // declined → drop the ringing row
    const p = state.groupCall.peers.get(msg.userId);
    if (p) { try { p.pc.close(); } catch {} if (p.audioEl) { try { p.audioEl.srcObject = null; p.audioEl.remove(); } catch {} } state.groupCall.peers.delete(msg.userId); }
    _gcRenderPanel();
  }
  function _gcOnEnd(msg) {
    if (state.groupCall.groupCallId !== (msg && msg.groupCallId)) return;
    if (state.groupCall.status === 'ringing-in') _gcLogSolo('missed', 'callee'); // caller cancelled before we picked up
    _gcHideIncomingModal();
    leaveGroupCall(true); // server already ended it
  }
  function leaveGroupCall(silent) {
    const gid = state.groupCall.groupCallId;
    const wasActive = state.groupCall.status === 'active';
    const dur = state.groupCall.startedAt ? Date.now() - state.groupCall.startedAt : 0;
    if (gid && !silent) { try { state.socket?.emit('group:leave', { groupCallId: gid }); } catch {} }
    if (wasActive) _gcLogSolo('ended', undefined, dur);
    _gcResetState();
  }
  function _gcToggleMute() {
    state.groupCall.muted = !state.groupCall.muted;
    const ls = state.groupCall.localStream;
    if (ls) ls.getAudioTracks().forEach(t => t.enabled = !state.groupCall.muted);
    _gcRenderPanel();
  }
  function _gcStartTimer() {
    if (state.groupCall.timer) clearInterval(state.groupCall.timer);
    state.groupCall.timer = setInterval(_gcRenderPanel, 1000);
  }
  function _gcResetState() {
    clearTimeout(state.groupCall.ringTimeout);
    if (state.groupCall.timer) { clearInterval(state.groupCall.timer); state.groupCall.timer = null; }
    for (const [, p] of state.groupCall.peers) { try { p.pc.close(); } catch {} if (p.audioEl) { try { p.audioEl.srcObject = null; p.audioEl.remove(); } catch {} } }
    state.groupCall.peers = new Map();
    if (state.groupCall.localStream) { state.groupCall.localStream.getTracks().forEach(t => t.stop()); state.groupCall.localStream = null; }
    state.groupCall.active = false; state.groupCall.groupCallId = null; state.groupCall.role = null;
    state.groupCall.status = 'idle'; state.groupCall.startedAt = 0; state.groupCall.muted = false;
    state.groupCall.pendingInvite = null; state.groupCall.names = {}; state.groupCall.soloPeerId = null;
    state.groupCall.invited = new Map();
    state.groupCall.sourceKey = 'leads7'; state.groupCall.sourceLabel = 'LEADS7';
    state.groupCall.transportLabel = 'TEAM'; state.groupCall.sourceSection = ''; state.groupCall.sourcePlatform = 'leads7';
    _gcHidePanel();
    _tcStopRing();
  }
  // ── Group ring election / cross-tab dismiss (BroadcastChannel above) ──
  function _gcElectRing(gid) {
    if (!_tcBC) { _tcPlayRing('in'); return; }
    _tcRingClaims = [{ tabId: _tcTabId, visible: document.visibilityState === 'visible' }];
    try { _tcBC.postMessage({ k: 'claim', callId: gid, tabId: _tcTabId, visible: document.visibilityState === 'visible' }); } catch {}
    clearTimeout(_tcElectTimer);
    _tcElectTimer = setTimeout(() => {
      if (state.groupCall.status !== 'ringing-in' || state.groupCall.groupCallId !== gid) return;
      const w = _tcRingClaims.slice().sort((a, b) => (Number(b.visible) - Number(a.visible)) || (a.tabId < b.tabId ? -1 : 1))[0];
      if (w && w.tabId === _tcTabId) _tcPlayRing('in');
    }, 350);
  }
  function _gcSignalHandled(gid) {
    try { state.socket?.emit('group:handled', { groupCallId: gid }); } catch {}
    try { _tcBC?.postMessage({ k: 'dismiss', callId: gid }); } catch {}
  }
  function _gcDismissHandledElsewhere(gid) {
    if (state.groupCall.status !== 'ringing-in') return;
    if (gid && state.groupCall.groupCallId && gid !== state.groupCall.groupCallId) return;
    clearTimeout(_tcElectTimer);
    _gcHideIncomingModal();
    _gcResetState();
  }
  function _gcDismissIncoming() { _gcHideIncomingModal(); _gcResetState(); }
  // Diagnostics surface (same spirit as window.__teamCall) — lets an operator
  // drive/inspect a group call from devtools; every action is still server-
  // validated (role/membership), so this exposes nothing a socket couldn't do.
  try {
    window.__groupCall = {
      start: (ids, names) => startGroupCall(ids, names),
      accept: () => acceptGroupCall(),
      decline: () => declineGroupCall(),
      leave: () => leaveGroupCall(),
      get: () => state.groupCall,
      peerStates: () => [...state.groupCall.peers.entries()].map(([id, p]) => ({ id, status: p.status, ice: p.pc && p.pc.iceConnectionState })),
    };
  } catch { }

  // ── Group call UI (dynamic, inline-styled with token fallbacks — no
  // index.html / app.css dependency, same as the 1-1 pill above) ──
  function _gcStatusChip(status) {
    const map = { connected: ['Live', 'var(--wa,#1fa855)'], connecting: ['Connecting…', 'var(--gray-500,#6b7280)'], failed: ['Failed', 'var(--danger,#e5484d)'], live: ['Live', 'var(--wa,#1fa855)'], muted: ['Muted', 'var(--gray-500,#6b7280)'], you: ['You', 'var(--gray-500,#6b7280)'] };
    const [txt, col] = map[status] || ['…', 'var(--gray-500,#6b7280)'];
    return el('span', { style: 'font-size:11px;font-weight:700;color:' + col + ';', text: txt });
  }
  function _gcRow(name, sub, status) {
    const row = el('div', { style: 'display:flex;align-items:center;gap:10px;padding:8px 10px;border-radius:9px;' });
    row.appendChild(el('div', { class: 'avatar avatar-sm ' + avatarColorClass(name), text: initials(name) || '?', style: 'flex:none;' }));
    const info = el('div', { style: 'flex:1;min-width:0;' });
    info.appendChild(el('div', { style: 'font-size:13.5px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', text: name }));
    if (sub) info.appendChild(el('div', { style: 'font-size:11px;color:var(--gray-500,#6b7280);', text: sub }));
    row.appendChild(info);
    row.appendChild(_gcStatusChip(status));
    return row;
  }
  function _gcBtnStyle(activeState) {
    return 'flex:1;padding:9px;border:1px solid var(--line,#e5e7eb);border-radius:10px;background:' + (activeState ? 'var(--wa-soft,#e7f6ee)' : 'var(--panel-2,#f7f7f4)') + ';color:var(--ink,#1b1d1f);font-weight:700;font-size:13px;cursor:pointer;';
  }
  function _gcShowPanel() {
    if (!document.getElementById('groupCallPanel')) {
      const p = el('div', { id: 'groupCallPanel', style: 'position:fixed;bottom:20px;right:20px;z-index:10000;width:300px;max-width:calc(100vw - 32px);background:var(--panel,#fff);border:1px solid var(--line,#e5e7eb);border-radius:16px;box-shadow:0 22px 50px -20px rgba(0,0,0,.5);overflow:hidden;' });
      document.body.appendChild(p);
      _makeDraggable(p, '.gc-panel-head', 'lougha_callpanel_pos'); // drag by the header, position remembered
    }
    _gcRenderPanel();
  }
  function _gcHidePanel() { document.getElementById('groupCallPanel')?.remove(); }
  // Make a fixed panel draggable by a handle; clamps to the viewport and
  // remembers the last position in localStorage (per-user, so each teammate
  // parks the call window wherever they like). Mouse + touch.
  function _makeDraggable(panel, handleSel, storageKey) {
    if (!panel || panel._loughaDragBound) return;
    panel._loughaDragBound = true;
    const restoreStoredPosition = () => {
      try {
        const s = JSON.parse(localStorage.getItem(storageKey) || 'null');
        if (s && typeof s.left === 'number') {
          panel.style.left = s.left + 'px'; panel.style.top = s.top + 'px';
          panel.style.right = 'auto'; panel.style.bottom = 'auto';
        }
      } catch { }
    };
    restoreStoredPosition();
    const clamp = () => {
      if (!panel.isConnected || panel.classList.contains('hidden') || !panel.offsetWidth) return;
      const mobileCallPanel = panel.id === 'gcIncomingModal' && window.matchMedia?.('(max-width: 640px)').matches;
      if (mobileCallPanel) {
        // A saved desktop drag position is inline CSS and would otherwise beat
        // any small-screen placement. Drop it until the viewport grows back.
        panel.style.removeProperty('left'); panel.style.removeProperty('top');
        panel.style.removeProperty('right'); panel.style.removeProperty('bottom');
        panel._usingMobilePosition = true;
        return;
      }
      if (panel._usingMobilePosition) {
        panel._usingMobilePosition = false;
        restoreStoredPosition();
      }
      const r = panel.getBoundingClientRect();
      const left = Math.max(4, Math.min(r.left, window.innerWidth - panel.offsetWidth - 4));
      const top = Math.max(4, Math.min(r.top, window.innerHeight - panel.offsetHeight - 4));
      if (Math.abs(left - r.left) > 0.5 || Math.abs(top - r.top) > 0.5) {
        panel.style.left = left + 'px'; panel.style.top = top + 'px';
        panel.style.right = 'auto'; panel.style.bottom = 'auto';
        // Inline left/top now own the placement — a panel still centred by a
        // CSS transform (the WA incoming banner) must drop it in the same
        // write or it shifts half its width. No-op for panels without the rule.
        panel.classList.add('is-positioned');
      }
    };
    panel._clampFixedPanel = clamp;
    let drag = null;
    const pt = e => e.touches ? e.touches[0] : e;
    const onMove = (e) => {
      if (!drag) return;
      const p = pt(e);
      let left = Math.max(4, Math.min(p.clientX - drag.dx, window.innerWidth - panel.offsetWidth - 4));
      let top = Math.max(4, Math.min(p.clientY - drag.dy, window.innerHeight - panel.offsetHeight - 4));
      panel.style.left = left + 'px'; panel.style.top = top + 'px';
      if (e.cancelable) e.preventDefault();
    };
    const onUp = () => {
      if (drag) { try { localStorage.setItem(storageKey, JSON.stringify({ left: parseInt(panel.style.left, 10), top: parseInt(panel.style.top, 10) })); } catch { } }
      drag = null;
      document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove); document.removeEventListener('touchend', onUp);
    };
    const onDown = (e) => {
      // Small screens: native page scrolling beats drag.
      if (window.matchMedia?.('(max-width: 640px)').matches) return;
      const h = e.target.closest(handleSel);
      if (!h || !panel.contains(h) || e.target.closest('button')) return;
      const p = pt(e), r = panel.getBoundingClientRect();
      drag = { dx: p.clientX - r.left, dy: p.clientY - r.top };
      panel.style.left = r.left + 'px'; panel.style.top = r.top + 'px'; panel.style.right = 'auto'; panel.style.bottom = 'auto';
      document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
      document.addEventListener('touchmove', onMove, { passive: false }); document.addEventListener('touchend', onUp);
      if (e.cancelable) e.preventDefault();
    };
    panel.addEventListener('mousedown', onDown);
    panel.addEventListener('touchstart', onDown, { passive: false });
    window.addEventListener('resize', clamp, { passive: true });
  }
  function _gcRenderPanel() {
    const panel = document.getElementById('groupCallPanel');
    if (!panel) return;
    clear(panel);
    // Solo (effectively 1-1) vs group → the header names the peer instead of
    // "Group call" so a normal 1-1 doesn't read as a group.
    const otherIds = Object.keys(state.groupCall.names || {}).filter(id => id !== _gcMyId());
    const isSolo = state.groupCall.peers.size <= 1 && otherIds.length <= 1;
    const soloName = isSolo ? ((otherIds[0] && _gcName(otherIds[0])) || (state.groupCall.peers.size ? _gcName([...state.groupCall.peers.keys()][0]) : 'Call')) : '';
    const head = el('div', { class: 'gc-panel-head', style: 'padding:12px 14px;background:var(--wa,#1fa855);color:#fff;display:flex;justify-content:space-between;align-items:center;cursor:move;user-select:none;' });
    const dur = state.groupCall.startedAt ? _tcFmt(Date.now() - state.groupCall.startedAt) : '';
    const base = isSolo ? soloName : 'Group call';
    const origin = (state.groupCall.sourceLabel || 'LEADS7') + ' · ' + (state.groupCall.transportLabel || 'TEAM');
    const title = state.groupCall.status === 'ringing-out'
      ? (origin + ' · ' + base + ' · ringing…')
      : (origin + ' · ' + base + (dur ? ' · ' + dur : ''));
    head.appendChild(el('span', { style: 'font-weight:700;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', text: title }));
    head.appendChild(el('span', { style: 'font-size:12px;opacity:.92;font-weight:600;', text: isSolo ? '⠿' : ((state.groupCall.peers.size + 1) + ' 👥') }));
    panel.appendChild(head);
    const list = el('div', { style: 'max-height:220px;overflow:auto;padding:6px;' });
    list.appendChild(_gcRow(userLabel(state.me?.username, state.me?.displayName || state.me?.username || 'Me'), 'you', state.groupCall.muted ? 'muted' : 'live'));
    for (const [id, p] of state.groupCall.peers) list.appendChild(_gcRow(adminLabelFor(id) || userLabel(p.name) || _gcName(id), '', p.status));
    // Invited-but-not-yet-joined → visible "ringing…" rows (45s window), so an
    // Add that nobody answered doesn't look like it did nothing.
    for (const [id, at] of (state.groupCall.invited || new Map())) {
      if (state.groupCall.peers.has(id)) continue;
      if (Date.now() - at > 45000) { state.groupCall.invited.delete(id); continue; }
      const sub = _gcIsCross(id) ? ('on ' + (String(id).split('@')[0] || 'peer')) : '';
      const row = _gcRow(_gcName(id), sub, 'connecting');
      row.style.opacity = '.65';
      const chip = row.lastChild; if (chip) chip.textContent = 'ringing…';
      list.appendChild(row);
    }
    if (state.groupCall.peers.size === 0 && state.groupCall.invited && state.groupCall.invited.size === 0 && state.groupCall.status === 'ringing-out') list.appendChild(el('div', { style: 'padding:10px;color:var(--gray-500,#6b7280);font-size:12.5px;', text: 'Ringing…' }));
    panel.appendChild(list);
    const bar = el('div', { style: 'display:flex;gap:8px;padding:10px;border-top:1px solid var(--line,#e5e7eb);' });
    const muteBtn = el('button', { style: _gcBtnStyle(state.groupCall.muted), text: state.groupCall.muted ? 'Unmute' : 'Mute' });
    muteBtn.addEventListener('click', _gcToggleMute);
    const addBtn = el('button', { style: _gcBtnStyle(false), text: 'Add' });
    addBtn.addEventListener('click', (e) => { e.stopPropagation(); openCallPicker(addBtn, { addMode: true }); });
    const leaveBtn = el('button', { style: 'flex:1;padding:9px;border:none;border-radius:10px;background:var(--danger,#e5484d);color:#fff;font-weight:700;font-size:13px;cursor:pointer;', text: 'Leave' });
    leaveBtn.addEventListener('click', () => leaveGroupCall());
    bar.appendChild(muteBtn); bar.appendChild(addBtn); bar.appendChild(leaveBtn);
    panel.appendChild(bar);
  }
  // Incoming ring card — Leads7's inline-styled idiom (same look as the 1-1
  // _tcShowIncoming banner; support2's rich .wa-call-* CSS doesn't exist here).
  function _gcShowIncomingModal(fromName, count, sourceMeta) {
    document.getElementById('gcIncomingModal')?.remove();
    const meta = sourceMeta || {
      sourceLabel: state.groupCall.sourceLabel || 'LEADS7',
      transportLabel: state.groupCall.transportLabel || 'TEAM',
    };
    const solo = (count || 0) <= 1;
    const b = el('div', { id: 'gcIncomingModal', role: 'region', 'aria-live': 'assertive', style: 'position:fixed;top:18px;left:50%;transform:translateX(-50%);z-index:10001;background:#111b21;color:#fff;border-radius:14px;box-shadow:0 8px 30px rgba(0,0,0,.4);padding:14px 16px;display:flex;align-items:center;gap:14px;font-size:14px;' });
    b.appendChild(el('div', { style: 'flex:1;min-width:0;' }, [
      el('div', { style: 'font-size:10.5px;font-weight:700;letter-spacing:.06em;opacity:.75;', text: (meta.sourceLabel || 'LEADS7') + ' · ' + (meta.transportLabel || 'TEAM') }),
      el('div', { style: 'font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', text: fromName || 'Teammate' }),
      el('div', { style: 'font-size:12px;opacity:.8;', text: solo ? 'Incoming call…' : ('Group call · ' + count + ' participants') }),
    ]));
    const acc = el('button', { title: 'Accept', 'aria-label': 'Accept call', style: 'background:#0a8a5f;border:none;color:#fff;width:40px;height:40px;border-radius:50%;cursor:pointer;font-size:18px;', text: '☎' });
    acc.addEventListener('click', acceptGroupCall);
    const dec = el('button', { title: 'Decline', 'aria-label': 'Decline call', style: 'background:#e23b3b;border:none;color:#fff;width:40px;height:40px;border-radius:50%;cursor:pointer;font-size:18px;', text: '✕' });
    dec.addEventListener('click', declineGroupCall);
    b.appendChild(acc); b.appendChild(dec);
    document.body.appendChild(b);
    _startIncomingTitle(fromName);
  }
  function _gcHideIncomingModal() { document.getElementById('gcIncomingModal')?.remove(); _stopIncomingTitle(); }

  // ── Multi-select call picker (1 selected → 1-1 call, 2+ → group call) ──
  function openCallPicker(anchorBtn, opts) {
    opts = opts || {};
    document.getElementById('roomCallPicker')?.remove();
    // Picker rows use raw roster ids (local UUID / remote canon); conversion to
    // canon happens at call time. `inCall` holds CANON ids (the group engine's
    // key space) so compare via _gcCanonFor.
    const meUuid = state.me?.id || '';
    const inCall = state.groupCall.active ? new Set(state.groupCall.peers.keys()) : new Set();
    // Remote (support2) teammates ARE selectable — the cross-platform mesh
    // (2026-07-15): their group leg rides the team-chat bridge.
    const pool = (state.teamUsers || [])
      .filter(u => u && u.id !== meUuid)
      .filter(u => !opts.addMode || !inCall.has(_gcCanonFor(u)))
      .sort((a, b) => (userLabel(a.username, a.displayName || a.username) || '').localeCompare(userLabel(b.username, b.displayName || b.username) || ''));
    const roomMembers = new Set(opts.room?.memberIds || []);
    const selected = new Set(pool
      .filter(u => roomMembers.has(u.identityId || u.canonicalId || u.id))
      .filter(u => u.remote || state.teamPresence.has(u.id))
      .map(u => u.id));
    const pop = el('div', { id: 'roomCallPicker', style: 'position:fixed;z-index:10002;width:250px;max-height:340px;display:flex;flex-direction:column;background:var(--panel,#fff);border:1px solid var(--line,#e5e7eb);border-radius:12px;box-shadow:0 12px 34px rgba(0,0,0,.22);overflow:hidden;' });
    pop.appendChild(el('div', { style: 'font-size:11px;font-weight:700;color:var(--gray-500,#6b7280);text-transform:uppercase;letter-spacing:.06em;padding:11px 12px 7px;', text: opts.addMode ? 'Add to call' : 'Start a call' }));
    const listWrap = el('div', { style: 'flex:1;overflow:auto;padding:0 6px 6px;' });
    if (!pool.length) listWrap.appendChild(el('div', { style: 'padding:10px;color:var(--gray-500,#6b7280);font-size:13px;', text: 'No callable members.' }));
    const callBtn = el('button', { style: 'margin:8px;padding:11px;border:none;border-radius:11px;background:var(--wa,#1fa855);color:#fff;font-weight:700;font-size:14px;cursor:pointer;' });
    const refreshBtn = () => { const n = selected.size; callBtn.textContent = n ? ('📞  Call (' + n + ')') : 'Select members'; callBtn.disabled = !n; callBtn.style.opacity = n ? '1' : '.5'; callBtn.style.cursor = n ? 'pointer' : 'not-allowed'; };
    for (const u of pool) {
      // Remote (peer-platform) teammates have no bridged presence → blind-ring:
      // always selectable, labelled by platform (same rule as the 1-1 DM button).
      const isRemote = !!u.remote;
      const online = isRemote ? true : state.teamPresence.has(u.id);
      const nm = userLabel(u.username, u.displayName || u.username);
      const row = el('div', { style: 'display:flex;align-items:center;gap:10px;padding:9px 8px;border-radius:9px;' + (online ? 'cursor:pointer;' : 'opacity:.45;cursor:not-allowed;') });
      const box = el('div', { style: 'flex:none;width:20px;height:20px;border-radius:6px;border:2px solid var(--line,#d1d5db);display:flex;align-items:center;justify-content:center;font-size:12px;color:#fff;font-weight:800;' });
      const paint = () => { const on = selected.has(u.id); box.style.background = on ? 'var(--wa,#1fa855)' : 'transparent'; box.style.borderColor = on ? 'var(--wa,#1fa855)' : 'var(--line,#d1d5db)'; box.textContent = on ? '✓' : ''; row.style.background = on ? 'var(--wa-soft,#e7f6ee)' : ''; };
      row.appendChild(box);
      row.appendChild(el('span', { style: 'flex:1;font-size:14px;color:var(--ink,#1b1d1f);', text: nm }));
      row.appendChild(el('span', { style: 'font-size:11px;font-weight:600;color:' + (isRemote ? 'var(--lougha,#2f8fd6)' : (online ? 'var(--wa,#16a34a)' : 'var(--gray-400,#9ca3af)')) + ';', text: isRemote ? ('on ' + (u.platform === 'leads7' ? 'Leads7' : 'support2')) : (online ? '● online' : 'offline') }));
      if (online) row.addEventListener('click', () => { if (selected.has(u.id)) selected.delete(u.id); else selected.add(u.id); paint(); refreshBtn(); });
      paint();
      listWrap.appendChild(row);
    }
    pop.appendChild(listWrap);
    callBtn.addEventListener('click', () => {
      const rawIds = [...selected];
      if (!rawIds.length) return;
      pop.remove();
      // Picker rows carry roster ids (local UUID / remote canon) — the group
      // engine is canon-keyed, so convert here, names keyed by canon too.
      const ids = []; const names = {};
      for (const rid of rawIds) {
        const u = (state.teamUsers || []).find(x => x.id === rid);
        if (!u) continue;
        const canon = _gcCanonFor(u);
        if (!canon) continue;
        ids.push(canon);
        names[canon] = userLabel(u.username, u.displayName || u.username);
      }
      if (!ids.length) return;
      if (opts.addMode && state.groupCall.active) {
        const added = ids.slice(0, GC_MAX - 1 - state.groupCall.peers.size);
        state.socket.emit('group:add', { groupCallId: state.groupCall.groupCallId, invitees: added, names });
        Object.assign(state.groupCall.names, names);
        for (const id of added) state.groupCall.invited.set(id, Date.now());   // show "ringing…" rows
        state.groupCall.soloPeerId = null; // adding people → no longer a 1-1
        _gcRenderPanel();
      } else {
        // Always the group engine (unified panel + Add) — 1 selected = a 1-1
        // (group of one); 2+ = a group.
        startGroupCall(ids, names);
      }
    });
    pop.appendChild(callBtn);
    refreshBtn();
    document.body.appendChild(pop);
    const r = anchorBtn.getBoundingClientRect();
    pop.style.top = Math.min(r.bottom + 6, window.innerHeight - 360) + 'px';
    pop.style.left = Math.max(8, Math.min(r.right - 250, window.innerWidth - 262)) + 'px';
    setTimeout(() => { const off = (ev) => { if (!pop.contains(ev.target) && ev.target !== anchorBtn) { pop.remove(); document.removeEventListener('click', off); } }; document.addEventListener('click', off); }, 0);
  }
  // =========================================================================

  function renderTeamchatActive() {
    const pane = document.getElementById('teamchatActiveChat');
    if (!pane) return;
    clear(pane);
    const target = state.teamchatActiveTarget || {};
    const headerTitle = target.type === 'room'
      ? target.room?.name || 'Room'
      : userLabel(target.user?.username, target.user?.displayName || target.user?.username || 'Chat');
    const head = el('header', { class: 'teamchat-active-head' });
    if (isTeamBotTarget(target)) {
      // Read-only alerts DM: bot avatar, a plain subtitle, no call button and
      // no composer — nothing typed here would ever reach anyone.
      head.appendChild(buildTeamBotAvatar('avatar-md'));
      head.appendChild(el('div', { class: 'teamchat-active-head-info' }, [
        el('div', { class: 'teamchat-active-name', text: headerTitle }),
        el('div', { class: 'teamchat-active-sub', text: 'Alertes automatiques · lecture seule' }),
      ]));
      pane.appendChild(head);
      pane.appendChild(el('div', { class: 'teamchat-messages', id: 'teamchatMessages' }));
      renderTeamchatMessages({ forceBottom: true });
      pane.appendChild(el('div', { class: 'teamchat-readonly-foot', role: 'note' }, [
        lucideIcon('lock'),
        el('span', { text: "Team Bot kay-sifet ghir l'alertes — ma-kay9rach l'jwab. Clique « Ouvrir » bach tmchi l'client." }),
      ]));
      refreshIcons();
      return;
    }
    head.appendChild(target.type === 'room'
      ? el('div', { class: 'avatar avatar-md ' + avatarColorClass(target.room?.id), text: '#' })
      : personalAvatar(target.user, 'avatar avatar-md'));
    // Subtitle: rooms → member count; remote DM → platform; local DM → a clear
    // green/grey status dot + Online/Offline so presence is obvious at a glance.
    const subEl = el('div', { class: 'teamchat-active-sub' });
    if (target.type === 'room') {
      subEl.textContent = (target.room?.memberCount ?? target.room?.memberIds?.length ?? 0) + ' members';
    } else if (target.user?.remote) {
      subEl.textContent = 'on ' + (target.user.platform === 'leads7' ? 'Leads7' : 'support2');
    } else {
      const on = state.teamPresence.has(target.user?.id);
      subEl.classList.add(on ? 'is-online' : 'is-offline');
      subEl.appendChild(el('span', { class: 'teamchat-sub-dot' }));
      subEl.appendChild(el('span', { text: on ? 'Online' : 'Offline' }));
    }
    const nameEl = el('div', { class: 'teamchat-active-name', text: headerTitle });
    head.appendChild(el('div', { class: 'teamchat-active-head-info' }, [
      nameEl,
      subEl,
    ]));
    // Admin room tools — inline rename (pencil). Rooms have no call button,
    // so this takes the header's right slot.
    if (target.type === 'room' && target.room?.id && state.me?.role === 'admin') {
      const renameBtn = el('button', { class: 'icon-btn', title: 'Renommer la room', 'aria-label': 'Renommer la room', style: 'margin-left:auto;' });
      renameBtn.appendChild(lucideIcon('pencil'));
      renameBtn.addEventListener('click', () => startTeamRoomRename(nameEl, target.room));
      head.appendChild(renameBtn);
      const membersBtn = el('button', { class: 'icon-btn', title: 'Gérer les membres', 'aria-label': 'Gérer les membres' });
      membersBtn.appendChild(lucideIcon('user-plus'));
      membersBtn.addEventListener('click', () => openTeamRoomModal(target.room));
      head.appendChild(membersBtn);
    }
    // Voice call button — 1-1 DMs only. Cross-platform (remote, support2)
    // teammates are callable via the team-chat bridge (2026-06-01); they have
    // no bridged presence → blind-ring. Local teammates are ALSO callable
    // while offline (2026-07-12): startTeamCall web-pushes their phone via
    // team.lougha.app and re-invites until they open the app — so no gating.
    if (target.type !== 'room' && target.user?.id) {
      const isRemote = !!target.user.remote;
      const callBtn = el('button', { class: 'icon-btn teamchat-call-btn', title: 'Voice call', 'aria-label': 'Voice call', style: 'margin-left:auto;' });
      callBtn.appendChild(lucideIcon('phone'));
      const online = isRemote ? true : state.teamPresence.has(target.user.id);
      if (isRemote) callBtn.title = 'Call (on ' + (target.user.platform === 'leads7' ? 'Leads7' : 'support2') + ')';
      else if (!online) callBtn.title = 'Appeler — sonnera sur son téléphone (notification)';
      // ONLINE local 1-1 → route through the group engine so the call shows the
      // unified panel (draggable, with an "Add" button to pull a 3rd person in
      // mid-call). Group ids are CANON (`platform@user`). Cross-platform
      // (support2) 1-1 stays on the bridge engine for now; an OFFLINE local
      // teammate also keeps the 1-1 engine — it web-pushes their phone via
      // team.lougha.app and re-invites until the app opens (the group engine
      // is socket-only and would ring into the void).
      callBtn.addEventListener('click', () => {
        const nm = userLabel(target.user.username, target.user.displayName || target.user.username);
        if (isRemote || !state.teamPresence.has(target.user.id)) return startTeamCall(target.user.id, nm);
        const canon = _gcCanonFor(target.user);
        startGroupCall([canon], { [canon]: nm });
      });
      head.appendChild(callBtn);
    }
    // Room: multi-select call picker — 1 selected → 1-1 call, 2+ → group
    // (mesh ≤5). Room members are pre-selected; any online teammate can be
    // added; remote (support2) teammates are selectable too (blind-ring).
    if (target.type === 'room' && (target.room?.memberIds || []).some(id => id !== teamMyIdentityId())) {
      const hasAdminTools = !!(target.room?.id && state.me?.role === 'admin'); // admin rename btn already claims margin-left:auto
      const roomCallBtn = el('button', { class: 'icon-btn teamchat-call-btn', title: 'Start a call', 'aria-label': 'Start a call', style: hasAdminTools ? '' : 'margin-left:auto;' });
      roomCallBtn.appendChild(lucideIcon('phone'));
      roomCallBtn.addEventListener('click', (e) => { e.stopPropagation(); openCallPicker(roomCallBtn, { room: target.room }); });
      head.appendChild(roomCallBtn);
    }
    pane.appendChild(head);
    const messages = el('div', { class: 'teamchat-messages', id: 'teamchatMessages' });
    messages.addEventListener('scroll', () => { if (messages.scrollTop < 150) loadOlderTeamchatMessages(); }, { passive: true });
    pane.appendChild(messages);
    renderTeamchatMessages({ forceBottom: true });
    // Compose
    const compose = el('div', { class: 'teamchat-compose' });
    // Reply preview pill — shown when a quoted message is staged.
    const reply = el('div', { class: 'reply-preview hidden', id: 'teamchatReplyPreview' });
    compose.appendChild(reply);
    const input = el('textarea', { class: 'input teamchat-textarea', placeholder: 'Message…', rows: 1 });
    input.addEventListener('keydown', e => {
      if (e.key === 'Escape' && state.teamchatReplyTarget) {
        e.preventDefault();
        setTeamchatReplyTarget(null);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendTeamchatText(input.value);
        input.value = '';
      }
    });
    const fileBtn = el('button', { class: 'icon-btn', title: 'Attach files (multi-select supported)' });
    fileBtn.appendChild(lucideIcon('paperclip'));
    const fileIn = el('input', { type: 'file', style: 'display:none;' });
    fileIn.multiple = true;
    fileIn.addEventListener('change', async () => {
      const picked = Array.from(fileIn.files || []);
      fileIn.value = '';
      if (!picked.length) return;
      for (const f of picked) {
        if (f.type && f.type.startsWith('image/')) {
          const edited = await openTeamchatImageAnnotator(f);
          if (edited) await sendTeamchatFile(edited);
        } else {
          await sendTeamchatFile(f);
        }
      }
    });
    fileBtn.addEventListener('click', () => fileIn.click());
    // Templates picker — same modal the inbox uses, target='teamchat'
    // makes useTemplate() insert into this textarea instead of the
    // inbox composer.
    const tplBtn = el('button', { class: 'icon-btn', title: 'Templates' });
    tplBtn.appendChild(lucideIcon('message-square-text'));
    tplBtn.addEventListener('click', () => openTemplatesModal('teamchat'));
    const micBtn = el('button', { class: 'icon-btn', title: 'Voice note' });
    micBtn.appendChild(lucideIcon('mic'));
    micBtn.addEventListener('click', () => startTeamchatRecording());
    const sendBtn = el('button', { class: 'icon-btn icon-btn-primary' });
    sendBtn.appendChild(lucideIcon('send'));
    sendBtn.addEventListener('click', () => { sendTeamchatText(input.value); input.value = ''; });
    compose.appendChild(fileBtn);
    compose.appendChild(fileIn);
    compose.appendChild(tplBtn);
    // ✨ AI writing assistant — team chat composer.
    const aiBtn = makeComposeAssistButton(
      () => input.value,
      (text) => { input.value = text; input.dispatchEvent(new Event('input')); input.focus(); }
    );
    aiBtn.classList.add('icon-btn');
    compose.appendChild(aiBtn);
    compose.appendChild(input);
    compose.appendChild(micBtn);
    compose.appendChild(sendBtn);
    // Inline recording overlay — same WhatsApp-style waveform as the inbox
    // recorder, scoped to the team chat compose so two simultaneous chat
    // windows don't fight over a single global recorder element.
    const overlay = el('div', { class: 'record-overlay teamchat-record-overlay hidden', id: 'teamchatRecordOverlay' });
    const trash = el('button', { class: 'icon-btn record-trash-btn', title: 'Discard' });
    trash.appendChild(lucideIcon('trash-2'));
    trash.addEventListener('click', () => cancelTeamchatRecording());
    const bar = el('div', { class: 'record-bar' });
    const status = el('div', { class: 'record-status' });
    status.appendChild(el('span', { class: 'record-dot' }));
    status.appendChild(el('span', { id: 'teamchatRecordTime', text: '0:00' }));
    bar.appendChild(status);
    bar.appendChild(el('canvas', { class: 'record-waveform', id: 'teamchatRecordWaveform', width: 600, height: 36 }));
    const sendRec = el('button', { class: 'icon-btn icon-btn-primary record-send-btn', title: 'Send' });
    sendRec.appendChild(lucideIcon('send'));
    sendRec.addEventListener('click', () => finishTeamchatRecording());
    overlay.appendChild(trash);
    overlay.appendChild(bar);
    overlay.appendChild(sendRec);
    compose.appendChild(overlay);
    pane.appendChild(compose);
    refreshIcons();
  }

  // Inline rename for a team room (admin only). Swaps the header name div for
  // an input; Enter/blur commits via PATCH /api/team/rooms/:id, Escape cancels,
  // empty input cancels without a request. Mirrors the Contact Profile pencil
  // pattern (startProfileNameEdit).
  function startTeamRoomRename(nameEl, room) {
    if (!nameEl || !room?.id) return;
    if (nameEl.style.display === 'none') return; // an edit is already in flight
    // Re-resolve from state.teamRooms — the header closure may hold a stale
    // room if another admin renamed/re-membered it since the last repaint.
    room = (state.teamRooms || []).find(r => r.id === room.id) || room;
    const input = el('input', {
      type: 'text', class: 'input', maxlength: 80, value: room.name || '',
      'aria-label': 'Nom de la room',
      style: 'height:30px;padding:2px 10px;font-size:14px;max-width:260px;'
    });
    nameEl.style.display = 'none';
    nameEl.parentElement.insertBefore(input, nameEl);
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
    let done = false;
    const close = () => {
      if (done) return;
      done = true;
      input.remove();
      nameEl.style.display = '';
    };
    const commit = async () => {
      if (done) return;
      const newName = input.value.trim();
      if (!newName || newName === room.name) { close(); return; }
      done = true; // blocks the blur that follows Enter
      input.disabled = true;
      try {
        const r = await api('/api/team/rooms/' + encodeURIComponent(room.id), {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newName })
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j.error) throw new Error(j.error || 'Échec du renommage');
        const saved = j.room || { ...room, name: newName };
        if (state.teamchatActiveTarget?.room?.id === room.id) {
          state.teamchatActiveTarget = { type: 'room', room: saved };
        }
        const idx = (state.teamRooms || []).findIndex(x => x.id === room.id);
        if (idx >= 0) state.teamRooms[idx] = { ...state.teamRooms[idx], ...saved };
        toast('Room renommée', 'success');
        renderTeamchatActive(); // full header repaint drops the input for us
        renderTeamchatList();
      } catch (err) {
        toast(err.message || 'Échec du renommage', 'error');
        input.remove();
        nameEl.style.display = '';
      }
    };
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'Enter') { e.preventDefault(); commit(); }
    });
    input.addEventListener('blur', () => commit());
  }

  // Multi-select state for team chat. Mirrors state.selectedMessageIds
  // pattern from the inbox; cleared on chat switch via openTeamchatWith.
  state.selectedTeamchatMessageIds = state.selectedTeamchatMessageIds || new Set();

  function toggleTeamchatMessageSelection(id) {
    if (!id) return;
    if (state.selectedTeamchatMessageIds.has(id)) state.selectedTeamchatMessageIds.delete(id);
    else state.selectedTeamchatMessageIds.add(id);
    // In-place DOM update — same scroll-preservation as the inbox
    // toggleMessageSelection. renderTeamchatMessages() preserves scroll unless
    // forceBottom is set (for initial opens and forced jumps).
    applyTeamchatSelectionDom();
    updateTeamchatSelectionBar();
  }

  function clearTeamchatSelection() {
    state.selectedTeamchatMessageIds.clear();
    applyTeamchatSelectionDom();
    updateTeamchatSelectionBar();
  }

  function applyTeamchatSelectionDom() {
    const rows = document.querySelectorAll('#teamchatActiveChat .teamchat-msg-row');
    const anySelected = state.selectedTeamchatMessageIds.size > 0;
    rows.forEach(row => {
      const id = row.getAttribute('data-msg-id');
      const isSelected = id && state.selectedTeamchatMessageIds.has(id);
      row.classList.toggle('is-selected', !!isSelected);
      row.classList.toggle('selection-mode', anySelected);
      const btn = row.querySelector('.msg-select-btn');
      if (btn) {
        btn.classList.toggle('selected', !!isSelected);
        btn.setAttribute('aria-label', isSelected ? 'Unselect message' : 'Select message');
        const iconEl = btn.querySelector('[data-lucide]');
        const wantName = isSelected ? 'check' : 'circle';
        if (iconEl && iconEl.getAttribute('data-lucide') !== wantName) {
          btn.replaceChild(lucideIcon(wantName), iconEl);
        }
      }
    });
    refreshIcons();
  }

  function updateTeamchatSelectionBar() {
    const compose = document.querySelector('#teamchatActiveChat .teamchat-compose');
    const count = state.selectedTeamchatMessageIds.size;
    let bar = document.getElementById('teamchatSelectionBar');
    if (!compose) return;
    if (!bar) {
      bar = el('div', { id: 'teamchatSelectionBar', class: 'msg-selection-bar hidden' });
      const cancelBtn = el('button', { class: 'icon-btn msg-selection-x', 'aria-label': 'Cancel selection' }, [lucideIcon('x')]);
      const countEl = el('span', { class: 'msg-selection-count', text: '0 Selected' });
      const spacer = el('div', { style: 'flex:1;' });
      const copyBtn = el('button', { class: 'btn btn-ghost' }, [lucideIcon('copy'), document.createTextNode(' Copy')]);
      const fwdBtn = el('button', { class: 'btn btn-primary' }, [lucideIcon('corner-up-right'), document.createTextNode(' Forward')]);
      cancelBtn.addEventListener('click', clearTeamchatSelection);
      copyBtn.addEventListener('click', copySelectedTeamchatMessages);
      fwdBtn.addEventListener('click', () => {
        // Re-use the inbox forward picker — sources come from the
        // team-chat selection set, picker shows Team + WhatsApp
        // sections so the agent can re-route to either.
        openForwardPicker({
          sourceIds: Array.from(state.selectedTeamchatMessageIds),
          onSuccess: clearTeamchatSelection,
        });
      });
      bar.appendChild(cancelBtn);
      bar.appendChild(countEl);
      bar.appendChild(spacer);
      bar.appendChild(copyBtn);
      bar.appendChild(fwdBtn);
      compose.parentNode.insertBefore(bar, compose);
      refreshIcons();
    }
    bar.querySelector('.msg-selection-count').textContent = count + ' Selected';
    bar.classList.toggle('hidden', count === 0);
    compose.classList.toggle('hidden-during-selection', count > 0);
  }

  async function copySelectedTeamchatMessages() {
    const ordered = (state.teamchatMessages || []).filter(m => state.selectedTeamchatMessageIds.has(m.id));
    const lines = ordered.map(m => {
      if (m.text) return m.text;
      if (m.fileName) return '[' + m.fileName + ']';
      if (m.type) return '[' + m.type + ']';
      return '';
    }).filter(Boolean);
    const text = lines.join('\n\n');
    if (!text) { toast('Nothing to copy (selection is media-only)', 'info'); return; }
    try {
      await navigator.clipboard.writeText(text);
      toast('Copied ' + lines.length + ' message' + (lines.length > 1 ? 's' : ''), 'success');
      clearTeamchatSelection();
    } catch {
      toast('Copy failed — clipboard blocked', 'error');
    }
  }

  // Reroute a support2-origin media URL through our same-origin proxy
  // (/uploads/inbox/s2-proxy — the leads7_media cookie authenticates it, the
  // server fetches the bytes with the shared secret). Local + other URLs pass
  // through untouched. Display srcs only — never persisted.
  function _s2TeamMediaUrl(url) {
    const m = /^https:\/\/support2\.lougha-admin\.link(\/uploads\/inbox\/[A-Za-z0-9._-]+)$/.exec(String(url || ''));
    return m ? ('/uploads/inbox/s2-proxy?u=' + encodeURIComponent(m[1])) : url;
  }

  function teamchatScrollTopAfterRender(snapshot, nextScrollHeight) {
    const previousHeight = Math.max(0, Number(snapshot?.scrollHeight) || 0);
    const previousTop = Math.max(0, Number(snapshot?.scrollTop) || 0);
    const viewportHeight = Math.max(0, Number(snapshot?.clientHeight) || 0);
    const nextHeight = Math.max(0, Number(nextScrollHeight) || 0);
    const nearBottom = !!snapshot?.forceBottom
      || previousHeight === 0
      || (previousHeight - previousTop - viewportHeight) <= 80;
    if (nearBottom) return nextHeight;
    const anchorFromBottom = Math.max(0, previousHeight - previousTop);
    return Math.max(0, nextHeight - anchorFromBottom);
  }

  function renderTeamchatMessages({ forceBottom = false } = {}) {
    const wrap = document.getElementById('teamchatMessages');
    if (!wrap) return;
    const scrollSnapshot = {
      scrollHeight: wrap.scrollHeight,
      scrollTop: wrap.scrollTop,
      clientHeight: wrap.clientHeight,
      forceBottom,
    };
    clear(wrap);
    const me = state.me?.id;
    // Empty-state placeholder — without it the messages container renders as
    // a huge blank canvas between the header and the composer (Ayoub flagged
    // this on 2026-05-13). Show a centered "Start the conversation" card.
    const botChat = isTeamBotTarget(state.teamchatActiveTarget);
    if (!state.teamchatMessages.length && botChat) {
      const empty = el('div', { class: 'teamchat-empty-state' });
      empty.appendChild(el('div', { class: 'teamchat-empty-bubble' }, [lucideIcon('bot')]));
      empty.appendChild(el('div', { class: 'teamchat-empty-title', text: 'Walou alertes daba' }));
      empty.appendChild(el('div', { class: 'teamchat-empty-sub', text: "Hna ghadi y-banou: clients kay-tsennaw jwab, reminders, mentions f notes, o clients jdad ma-t7allouch." }));
      wrap.appendChild(empty);
      refreshIcons(empty);
      return;
    }
    if (!state.teamchatMessages.length) {
      const target = state.teamchatActiveTarget || {};
      const headerTitle = target.type === 'room'
        ? (target.room?.name || 'this room')
        : (target.user?.displayName || target.user?.username || 'this teammate');
      const isRoom = target.type === 'room';
      const empty = el('div', { class: 'teamchat-empty-state' });
      empty.appendChild(el('div', { class: 'teamchat-empty-bubble' }, [
        lucideIcon(isRoom ? 'users-round' : 'message-square'),
      ]));
      empty.appendChild(el('div', { class: 'teamchat-empty-title', text: isRoom ? 'Start the conversation in ' + headerTitle : 'Send your first message to ' + headerTitle }));
      empty.appendChild(el('div', { class: 'teamchat-empty-sub', text: isRoom ? 'Messages are visible to every team member in this room.' : 'Internal team chat — only you and ' + headerTitle + ' see this.' }));
      wrap.appendChild(empty);
      refreshIcons(empty);
      return;
    }
    for (const m of state.teamchatMessages) {
      const fromMe = teamMessageIsMine(m);
      if (botChat) {
        // Alerts: no select / reply / forward affordances (there is no composer,
        // and alerts naming clients must never be forwarded) — text + Ouvrir.
        const botRow = el('div', { class: 'teamchat-msg-row them', 'data-msg-id': m.id || '' });
        const botBubble = el('div', { class: 'teamchat-msg them teamchat-msg-bot' + (m.meta?.kind === 'mirror' ? ' is-mirror' : '') });
        if (m.text) botBubble.appendChild(el('div', { class: 'teamchat-msg-text', text: m.text }));
        const actions = buildTeamBotActions(m);
        if (actions) botBubble.appendChild(actions);
        botBubble.appendChild(el('div', { class: 'teamchat-msg-meta' }, [el('span', { text: formatChatDate(m.createdAt) })]));
        botRow.appendChild(botBubble);
        wrap.appendChild(botRow);
        continue;
      }
      const isSelected = m.id && state.selectedTeamchatMessageIds.has(m.id);
      const row = el('div', { class: 'teamchat-msg-row ' + (fromMe ? 'me' : 'them') + (isSelected ? ' is-selected' : ''), 'data-msg-id': m.id || '' });
      // Selection checkbox — hover-revealed, permanent green when selected.
      const selectBtn = el('button', {
        class: 'msg-select-btn' + (isSelected ? ' selected' : ''),
        'aria-label': isSelected ? 'Unselect message' : 'Select message',
        type: 'button',
      }, [lucideIcon(isSelected ? 'check' : 'circle')]);
      selectBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (m.id) toggleTeamchatMessageSelection(m.id);
      });
      row.appendChild(selectBtn);
      // Hover reply icon — discoverable replacement for the
      // dblclick-to-reply gesture that nobody knew existed. Same
      // pattern as the inbox.
      const replyBtn = el('button', {
        class: 'msg-reply-btn',
        'aria-label': 'Reply',
        title: 'Reply',
        type: 'button',
      });
      replyBtn.appendChild(lucideIcon('reply'));
      replyBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        setTeamchatReplyTarget(m);
      });
      row.appendChild(replyBtn);
      // Edit + Delete — only on messages I sent. Edit follows WhatsApp's
      // 15-minute window (enforced server-side; we hide the button after
      // the window passes to avoid a confusing "edit then 403" UX).
      if (fromMe && m.id) {
        const ageMs = Date.now() - (Date.parse(m.createdAt) || 0);
        const canEdit = m.text && ageMs < 15 * 60_000;
        if (canEdit) {
          const editBtn = el('button', {
            class: 'msg-edit-btn',
            'aria-label': 'Edit message',
            title: 'Edit (within 15 min)',
            type: 'button',
          });
          editBtn.appendChild(lucideIcon('pencil'));
          editBtn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            startTeamchatEdit(m, row);
          });
          row.appendChild(editBtn);
        }
        const delBtn = el('button', {
          class: 'msg-delete-btn',
          'aria-label': 'Delete message',
          title: 'Delete',
          type: 'button',
        });
        delBtn.appendChild(lucideIcon('trash-2'));
        delBtn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          deleteTeamchatMessage(m);
        });
        row.appendChild(delBtn);
      }
      // Wire click-anywhere-to-toggle unconditionally (rows built
      // before selection started still respond). Handler bails out
      // when no selection is active so a casual click doesn't
      // toggle.
      if (state.selectedTeamchatMessageIds.size > 0) row.classList.add('selection-mode');
      row.addEventListener('click', (ev) => {
        if (state.selectedTeamchatMessageIds.size === 0) return;
        if (ev.target.closest('a, button, audio, video, .msg-select-btn, .quoted-block')) return;
        if (m.id) toggleTeamchatMessageSelection(m.id);
      });
      const bubble = el('div', { class: 'teamchat-msg ' + (fromMe ? 'me' : 'them') });
      if (!fromMe && state.teamchatActiveTarget?.type === 'room') {
        bubble.appendChild(el('div', { class: 'teamchat-msg-author', text: _teamMsgFromLabel(m, m.fromName || '') }));
      }
      // Quoted-reply preview at the top of the bubble. Click → scroll to
      // the original message + flash. Mirrors the inbox reply UX.
      if (m.quotedMessage) {
        const q = m.quotedMessage;
        const qBlock = el('div', { class: 'quoted-block teamchat-quoted' });
        qBlock.appendChild(el('div', { class: 'quoted-block-bar' }));
        const qBody = el('div', { class: 'quoted-block-body' });
        qBody.appendChild(el('div', { class: 'quoted-block-author', text: q.fromName || (teamMessageIsMine(q) ? 'You' : 'Member') }));
        let snippet = '';
        if (q.text) snippet = q.text;
        else if (q.type === 'audio') snippet = '🎵 Voice note';
        else if (q.type === 'image') snippet = '📷 Photo';
        else if (q.type === 'video') snippet = '🎥 Video';
        else if (q.type === 'file') snippet = '📎 File';
        else snippet = '(message)';
        qBody.appendChild(el('div', { class: 'quoted-block-snippet', text: snippet }));
        qBlock.appendChild(qBody);
        qBlock.addEventListener('click', () => {
          const origRow = q.id && document.querySelector('.teamchat-msg-row[data-msg-id="' + CSS.escape(q.id) + '"]');
          if (origRow) {
            origRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
            origRow.classList.add('msg-flash');
            setTimeout(() => origRow.classList.remove('msg-flash'), 1200);
          }
        });
        bubble.appendChild(qBlock);
      }
      if (m.text) bubble.appendChild(el('div', { class: 'teamchat-msg-text', text: m.text }));
      if (m.mediaUrl) {
        // support2-sent team media lives on THEIR gated origin — a direct
        // cross-origin src only worked when Cloudflare had it cached (the
        // mirror-media flakiness, reverse direction). Reroute display srcs
        // through /uploads/inbox/s2-proxy (2026-07-11); local URLs unchanged.
        const _mUrl = _s2TeamMediaUrl(m.mediaUrl);
        if (m.type === 'image') {
          const img = el('img', { src: _mUrl, class: 'teamchat-msg-image' });
          img.style.cursor = 'zoom-in';
          img.addEventListener('click', (ev) => { ev.stopPropagation(); openImageLightbox(_mUrl); });
          bubble.appendChild(img);
        } else if (m.type === 'video') {
          const v = document.createElement('video');
          v.controls = true; v.preload = 'metadata'; v.src = _mUrl;
          v.setAttribute('style', 'max-width:280px;border-radius:8px;display:block;');
          bubble.appendChild(v);
        } else if (m.type === 'audio') {
          // Honour the actual file format. The previous code had a copy-
          // paste typo — both ternary branches returned 'audio/mpeg', so
          // .ogg team-chat recordings were declared as audio/mpeg and
          // Safari refused to decode them. Pick from the URL extension
          // first, fall back to the stored mimeType, then audio/mpeg as
          // a last resort.
          let _mt = 'audio/mpeg';
          if (/\.ogg$/i.test(m.mediaUrl)) _mt = 'audio/ogg';
          else if (/\.webm$/i.test(m.mediaUrl)) _mt = 'audio/webm';
          else if (/\.m4a$/i.test(m.mediaUrl) || /\.mp4$/i.test(m.mediaUrl)) _mt = 'audio/mp4';
          else if (m.mimeType) _mt = m.mimeType;
          // Use the shared custom player so team-chat voice gets the same
          // waveform + speed pill (1×/1.5×/2×) as inbox voice notes.
          // Stored duration keeps the player idle until Play.
          bubble.appendChild(buildAudioPlayer(_mUrl, { mime: _mt, durationSec: m.duration }));
        } else {
          // File / document — render as a card with icon + filename, same
          // shape as the inbox's .doc-card. Click opens in a new tab.
          const card = el('a', { href: _mUrl, target: '_blank', rel: 'noopener noreferrer', class: 'teamchat-msg-file doc-card' });
          card.appendChild(lucideIcon('file-text'));
          const meta = el('div');
          meta.appendChild(el('div', { class: 'doc-name', text: m.fileName || 'File' }));
          if (typeof m.size === 'number' && m.size > 0) {
            const kb = Math.max(1, Math.round(m.size / 1024));
            const sizeText = kb >= 1024 ? (kb / 1024).toFixed(1) + ' MB' : kb + ' KB';
            meta.appendChild(el('div', { class: 'doc-size', text: sizeText, style: 'font-size:11px;color:var(--gray-500);' }));
          }
          card.appendChild(meta);
          bubble.appendChild(card);
        }
      }
      const meta = el('div', { class: 'teamchat-msg-meta' });
      meta.appendChild(el('span', { text: formatChatDate(m.createdAt) }));
      if (m.editedAt) meta.appendChild(el('span', { class: 'teamchat-msg-edited', text: 'edited', title: 'edited ' + formatChatDate(m.editedAt) }));
      if (fromMe) {
        const others = (state.teamchatActiveTarget?.type === 'room')
          ? (state.teamchatActiveTarget.room?.memberIds || []).filter(id => id !== teamMyIdentityId())
          : [state.teamchatActiveTarget.user?.id].filter(Boolean);
        const allRead = others.length > 0 && others.every(uid => Array.isArray(m.readBy) && m.readBy.includes(uid));
        meta.appendChild(el('span', {
          class: 'teamchat-tick' + (allRead ? ' read' : ''),
          text: allRead ? '✓✓' : '✓'
        }));
      }
      // 🌐 Translate-to-Arabic on team-chat messages too.
      if ((m.text || '').trim()) meta.appendChild(makeTranslateButton(m.text, bubble));
      bubble.appendChild(meta);
      // Double-click anywhere on the row → set reply target. Skipped for
      // clicks that originated on inner controls (audio scrubber, image,
      // download link, quoted-block click) so those keep working.
      row.addEventListener('dblclick', (ev) => {
        if (ev.target.closest('audio, video, a, .quoted-block')) return;
        setTeamchatReplyTarget(m);
      });
      row.appendChild(bubble);
      wrap.appendChild(row);
    }
    renderTeamchatOlderBar();
    wrap.scrollTop = teamchatScrollTopAfterRender(scrollSnapshot, wrap.scrollHeight);
    // Convert the freshly-inserted <i data-lucide="..."> elements into
    // real SVG icons. Without this the Reply / Edit / Delete chips on
    // own bubbles render as empty white circles until something else
    // triggers a global refresh.
    refreshIcons(wrap);
  }

  // Reply target for the active team chat — set by double-clicking a
  // bubble. Surfaced as a pill above the composer; cleared on send / Esc.
  state.teamchatReplyTarget = null;
  function setTeamchatReplyTarget(message) {
    state.teamchatReplyTarget = message || null;
    renderTeamchatReplyPreview();
  }
  function renderTeamchatReplyPreview() {
    const host = document.getElementById('teamchatReplyPreview');
    if (!host) return;
    clear(host);
    const m = state.teamchatReplyTarget;
    if (!m) { host.classList.add('hidden'); return; }
    host.classList.remove('hidden');
    const bar = el('div', { class: 'reply-preview-bar' });
    const body = el('div', { class: 'reply-preview-body' });
    body.appendChild(el('div', { class: 'reply-preview-author', text: teamMessageIsMine(m) ? 'You' : _teamMsgFromLabel(m, m.fromName || 'Member') }));
    let snippet = '';
    if (m.text) snippet = m.text;
    else if (m.type === 'audio') snippet = '🎵 Voice note';
    else if (m.type === 'image') snippet = '📷 Photo';
    else if (m.type === 'video') snippet = '🎥 Video';
    else if (m.type === 'file') snippet = '📎 File';
    body.appendChild(el('div', { class: 'reply-preview-snippet', text: snippet }));
    const closeBtn = el('button', { class: 'icon-btn', title: 'Cancel reply' });
    closeBtn.appendChild(lucideIcon('x'));
    closeBtn.addEventListener('click', () => setTeamchatReplyTarget(null));
    host.appendChild(bar);
    host.appendChild(body);
    host.appendChild(closeBtn);
    refreshIcons();
  }

  async function sendTeamchatText(text) {
    text = (text || '').trim();
    if (!text) return;
    const fd = new FormData();
    fd.append('text', text);
    if (state.teamchatActiveTarget?.type === 'user') fd.append('targetUserId', state.teamchatActiveTarget.user.id);
    else if (state.teamchatActiveTarget?.type === 'room') fd.append('roomId', state.teamchatActiveTarget.room.id);
    else return;
    if (state.teamchatReplyTarget?.id) fd.append('replyTo', state.teamchatReplyTarget.id);
    try {
      const r = await fetchT('/api/team/send', { method: 'POST', headers: authHeaders(), body: fd });
      const j = await r.json();
      if (j.error) { toast(j.error, 'error'); return; }
      // Clear the reply target on successful send so the next message
      // doesn't accidentally inherit it. Server emits team_message which
      // is what populates state — no need to push manually here.
      setTeamchatReplyTarget(null);
    } catch { toast('Send failed', 'error'); }
  }

  // Voice recording for team chat — mirrors the inbox recorder (same DSP
  // constraints, same MediaRecorder bitrate, same 250ms timeslice flush)
  // but uploads through /api/team/send so the recipient gets it via the
  // team chat backend instead of WhatsApp.
  state.teamchatRecording = null;
  async function startTeamchatRecording() {
    if (state.teamchatRecording) return;
    if (!navigator.mediaDevices) { toast('Mic not available', 'error'); return; }
    try {
      // Same constraints as the inbox recorder — shared helper so a
      // mic picked in the inbox popover also applies here. getMicStream()
      // auto-recovers from OverconstrainedError (stale saved deviceId).
      const stream = await getMicStream();
      try {
        const track = stream.getAudioTracks()[0];
        const settings = track && track.getSettings ? track.getSettings() : {};
        const label = (track && track.label) || '';
        console.log('[voice] teamchat track settings:', settings, 'label:', label, 'deviceId:', settings.deviceId);
        if (VIRTUAL_MIC_RE.test(label)) {
          toast('Mic = "' + label + '" (virtual). Click the ⌄ next to the mic button in the inbox to pick a real one.', 'warning');
        }
      } catch (e) { /* ignore */ }
      const mimeType = (typeof pickRecordMime === 'function') ? pickRecordMime() : 'audio/webm;codecs=opus';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 64000 } : { audioBitsPerSecond: 64000 });
      const chunks = [];
      recorder.addEventListener('dataavailable', e => { if (e.data && e.data.size) chunks.push(e.data); });
      recorder.start(250);
      const startedAt = Date.now();
      const tick = setInterval(() => {
        const s = Math.floor((Date.now() - startedAt) / 1000);
        const mm = Math.floor(s / 60), ss = s % 60;
        const t = document.getElementById('teamchatRecordTime');
        if (t) t.textContent = mm + ':' + (ss < 10 ? '0' : '') + ss;
      }, 200);
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const canvas = document.getElementById('teamchatRecordWaveform');
      const drawState = { running: true, bars: [] };
      if (typeof drawWaveform === 'function') drawWaveform(canvas, analyser, drawState);
      state.teamchatRecording = { stream, recorder, chunks, tick, startedAt, audioCtx, drawState };
      document.getElementById('teamchatRecordOverlay')?.classList.remove('hidden');
    } catch (err) {
      console.warn('[voice] teamchat getUserMedia failed:', err);
      toast(_micErrorMessage(err), 'error');
    }
  }
  function stopTeamchatRecordingTracks() {
    if (!state.teamchatRecording) return;
    const { recorder, stream, tick, audioCtx, drawState } = state.teamchatRecording;
    clearInterval(tick);
    if (drawState) drawState.running = false;
    try { if (audioCtx && audioCtx.state !== 'closed') audioCtx.close(); } catch {}
    try { if (recorder.state !== 'inactive') recorder.stop(); } catch {}
    try { stream.getTracks().forEach(t => t.stop()); } catch {}
  }
  function cancelTeamchatRecording() {
    stopTeamchatRecordingTracks();
    state.teamchatRecording = null;
    document.getElementById('teamchatRecordOverlay')?.classList.add('hidden');
    const t = document.getElementById('teamchatRecordTime');
    if (t) t.textContent = '0:00';
  }
  async function finishTeamchatRecording() {
    if (!state.teamchatRecording) return;
    const { recorder, chunks, startedAt } = state.teamchatRecording;
    if (Date.now() - startedAt < 350) {
      cancelTeamchatRecording();
      toast('Recording too short', 'error');
      return;
    }
    try { if (recorder.state === 'recording') recorder.requestData(); } catch {}
    await new Promise(res => {
      if (recorder.state === 'inactive') return res();
      recorder.addEventListener('stop', res, { once: true });
      try { recorder.stop(); } catch { res(); }
    });
    stopTeamchatRecordingTracks();
    const blob = new Blob(chunks, { type: (chunks[0] && chunks[0].type) || 'audio/webm' });
    state.teamchatRecording = null;
    document.getElementById('teamchatRecordOverlay')?.classList.add('hidden');
    const t = document.getElementById('teamchatRecordTime');
    if (t) t.textContent = '0:00';
    if (!blob.size) return;
    // Wrap as a File so the server's multer picks it up under .file —
    // /api/team/send uses upload.single('file').
    const f = new File([blob], 'voice.webm', { type: blob.type });
    sendTeamchatFile(f);
  }

  // Team-chat image annotator — opens an overlay with the picked image
  // on a <canvas>, lets the user draw (pen) and redact (filled black
  // rectangles), undo/redo their strokes, then resolves with a new File
  // containing the annotated PNG. Resolves null if the user cancels.
  // Self-contained: builds its own DOM, cleans up on close.
  function openTeamchatImageAnnotator(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onerror = () => resolve(file); // fall back to original on read error
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => resolve(file);
        img.onload = () => mount(img, reader.result);
        img.src = reader.result;
      };
      reader.readAsDataURL(file);

      function mount(img) {
        const overlay = el('div', { class: 'image-annotator-overlay' });
        const toolbar = el('div', { class: 'image-annotator-toolbar' });
        // Tool registry. Move comes first since it's a meta-action
        // (select/reposition/delete existing strokes), then draw tools
        // sorted by frequency-of-use.
        const TOOLS = [
          { id: 'move',      label: '✥ Move' },
          { id: 'pen',       label: '✎ Pen' },
          { id: 'highlight', label: '▰ Highlight' },
          { id: 'text',      label: 'T Text' },
          { id: 'arrow',     label: '→ Arrow' },
          { id: 'box',       label: '▭ Box' },
          { id: 'redact',    label: '⬛ Redact' },
        ];
        const toolBtns = {};
        for (const t of TOOLS) {
          const b = el('button', { class: 'ia-tool', type: 'button', text: t.label });
          b.dataset.tool = t.id;
          toolBtns[t.id] = b;
          toolbar.appendChild(b);
        }
        // Colors — match the iOS-style swatches from the reference screenshot
        const colors = [
          { name: 'red',    hex: '#ef4444' },
          { name: 'yellow', hex: '#facc15' },
          { name: 'green',  hex: '#22c55e' },
          { name: 'blue',   hex: '#3b82f6' },
          { name: 'black',  hex: '#111827' },
          { name: 'white',  hex: '#ffffff' },
        ];
        const swatchWrap = el('div', { class: 'ia-swatches' });
        const swatchEls = colors.map(c => {
          const s = el('button', { class: 'ia-swatch', type: 'button', title: c.name, style: 'background:' + c.hex });
          s.dataset.hex = c.hex;
          swatchWrap.appendChild(s);
          return s;
        });
        const undoBtn = el('button', { class: 'ia-icon-btn', type: 'button', title: 'Undo', text: '↶' });
        const redoBtn = el('button', { class: 'ia-icon-btn', type: 'button', title: 'Redo', text: '↷' });
        const cancelBtn = el('button', { class: 'ia-text-btn', type: 'button', text: 'Cancel' });
        const doneBtn = el('button', { class: 'ia-text-btn ia-done', type: 'button', text: 'Done' });
        toolbar.appendChild(el('div', { class: 'ia-sep' }));
        toolbar.appendChild(swatchWrap);
        toolbar.appendChild(el('div', { class: 'ia-spacer' }));
        toolbar.appendChild(undoBtn);
        toolbar.appendChild(redoBtn);
        toolbar.appendChild(el('div', { class: 'ia-sep' }));
        toolbar.appendChild(cancelBtn);
        toolbar.appendChild(doneBtn);
        overlay.appendChild(toolbar);

        // Canvas — sized to image natural dimensions so exported file
        // preserves resolution; CSS scales it down to fit the viewport.
        const canvasWrap = el('div', { class: 'image-annotator-canvas-wrap' });
        const canvas = el('canvas', { class: 'image-annotator-canvas' });
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvasWrap.appendChild(canvas);
        overlay.appendChild(canvasWrap);
        document.body.appendChild(overlay);
        document.body.classList.add('ia-open');

        const ctx = canvas.getContext('2d');
        // Drawing state
        let tool = 'pen';
        let color = colors[0].hex; // red by default — most common annotation color
        toolBtns.pen.classList.add('active');
        swatchEls[0].classList.add('active');
        const ops = []; // committed draw ops, mutated in place by Move
        let drawing = null; // in-progress op (not yet committed)
        let selectedIdx = null; // index into ops, or null
        let dragging = null;   // { idx, lastPoint } while a Move drag is active

        // Snapshot-based history. Each entry is a deep clone of `ops` at
        // a committed state. Lets undo/redo cover Add, Move, and Delete
        // uniformly — pop/push-the-last-op didn't work once we let users
        // mutate ops in place via the Move tool.
        let history = [[]];
        let historyIdx = 0;
        function cloneOps(arr) {
          // structuredClone is available everywhere we care about and
          // handles the nested point arrays / rect objects natively.
          return typeof structuredClone === 'function'
            ? structuredClone(arr)
            : JSON.parse(JSON.stringify(arr));
        }
        function pushHistory() {
          // Truncate the redo branch — a new committed change invalidates
          // anything we could have re-done after the current point.
          history = history.slice(0, historyIdx + 1);
          history.push(cloneOps(ops));
          historyIdx = history.length - 1;
        }
        function restoreFromHistory() {
          const snap = cloneOps(history[historyIdx]);
          ops.length = 0;
          for (const o of snap) ops.push(o);
          // After undo/redo, the array contents change identity — even
          // if `selectedIdx` is still in range, it now points at a
          // different op than what the user had selected before. Clear
          // the selection unconditionally so the dashed outline doesn't
          // jump to an unrelated stroke.
          selectedIdx = null;
          render();
        }
        function doUndo() {
          if (historyIdx === 0) return;
          historyIdx--;
          restoreFromHistory();
        }
        function doRedo() {
          if (historyIdx >= history.length - 1) return;
          historyIdx++;
          restoreFromHistory();
        }

        // Stroke width scales with image dims so a thin pen on a 4K
        // screenshot doesn't disappear. Used as the base for pen / box /
        // arrow widths; highlight uses 4× this; redact ignores it.
        function baseStroke() {
          return Math.max(3, Math.round(Math.min(canvas.width, canvas.height) / 220));
        }
        function textFontSize() {
          return Math.max(16, Math.round(Math.min(canvas.width, canvas.height) / 28));
        }

        // Find the topmost Box op that fully contains a canvas-space
        // point — used so a Text click inside a Box wraps to the Box's
        // right edge. Skips Box ops without a real area to avoid
        // matching a zero-size committed Box from accidental clicks.
        function findContainingBox(p) {
          for (let i = ops.length - 1; i >= 0; i--) {
            const o = ops[i];
            if (o.kind !== 'box') continue;
            const r = o.rect;
            if (r.w < 8 || r.h < 8) continue;
            if (p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h) return o;
          }
          return null;
        }
        // Effective wrap-width for a text op. Pulled from the op's stored
        // `maxWidth` if it carries one (set at create time so the bound
        // doesn't drift if the user later moves the underlying Box), and
        // falls back to "everything between the text anchor and the
        // canvas right edge" for legacy ops that pre-date this feature.
        function textMaxWidthFor(op) {
          if (op && Number.isFinite(op.maxWidth) && op.maxWidth > 0) return op.maxWidth;
          return Math.max(40, canvas.width - (op?.x || 0) - 8);
        }
        // Word-wrap `text` so each rendered line fits within `maxWidth`
        // at the given font size. Preserves explicit \n line breaks.
        // First word on a line goes through even if it overflows on its
        // own — better to clip than to drop the word entirely.
        function wrapText(text, maxWidth, fontSize) {
          const lines = [];
          const paras = String(text || '').split('\n');
          ctx.save();
          ctx.font = `bold ${fontSize}px sans-serif`;
          for (const para of paras) {
            if (!para) { lines.push(''); continue; }
            const words = para.split(/(\s+)/).filter(s => s !== '');
            let cur = '';
            for (const w of words) {
              const test = cur + w;
              if (ctx.measureText(test).width <= maxWidth || !cur.trim()) {
                cur = test;
              } else {
                lines.push(cur.replace(/\s+$/, ''));
                cur = w.replace(/^\s+/, '');
              }
            }
            if (cur) lines.push(cur);
          }
          ctx.restore();
          return lines.length ? lines : [''];
        }

        function drawPolyline(pts) {
          if (!pts || pts.length < 2) return;
          ctx.beginPath();
          ctx.moveTo(pts[0].x, pts[0].y);
          for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
          ctx.stroke();
        }
        function drawArrow(from, to, c, stroke) {
          ctx.strokeStyle = c;
          ctx.fillStyle = c;
          ctx.lineWidth = stroke * 1.6;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(from.x, from.y);
          ctx.lineTo(to.x, to.y);
          ctx.stroke();
          const angle = Math.atan2(to.y - from.y, to.x - from.x);
          const head = Math.max(14, stroke * 6);
          ctx.beginPath();
          ctx.moveTo(to.x, to.y);
          ctx.lineTo(to.x - head * Math.cos(angle - Math.PI / 7), to.y - head * Math.sin(angle - Math.PI / 7));
          ctx.lineTo(to.x - head * Math.cos(angle + Math.PI / 7), to.y - head * Math.sin(angle + Math.PI / 7));
          ctx.closePath();
          ctx.fill();
        }

        // Compute an axis-aligned bounding box around an op, in canvas
        // coords. Used by Move-tool hit testing and by the selection
        // outline. Returns null for unknown / empty ops.
        function bboxOf(op) {
          if (!op) return null;
          if (op.kind === 'pen' || op.kind === 'highlight') {
            const pts = op.points;
            if (!pts || !pts.length) return null;
            let minX = pts[0].x, minY = pts[0].y, maxX = pts[0].x, maxY = pts[0].y;
            for (const p of pts) {
              if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
              if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
            }
            const pad = (op.kind === 'highlight' ? baseStroke() * 2 : Math.max(6, baseStroke()));
            return { x: minX - pad, y: minY - pad, w: maxX - minX + 2 * pad, h: maxY - minY + 2 * pad };
          }
          if (op.kind === 'arrow') {
            const minX = Math.min(op.from.x, op.to.x), maxX = Math.max(op.from.x, op.to.x);
            const minY = Math.min(op.from.y, op.to.y), maxY = Math.max(op.from.y, op.to.y);
            const pad = Math.max(8, baseStroke() * 2);
            return { x: minX - pad, y: minY - pad, w: maxX - minX + 2 * pad, h: maxY - minY + 2 * pad };
          }
          if (op.kind === 'box' || op.kind === 'redact') {
            return { x: op.rect.x, y: op.rect.y, w: op.rect.w, h: op.rect.h };
          }
          if (op.kind === 'text') {
            // Multi-line aware bbox. We wrap with the same width the
            // renderer uses, then measure the widest wrapped line so the
            // selection outline hugs the actual rendered text.
            ctx.save();
            ctx.font = `bold ${op.fontSize}px sans-serif`;
            const maxW = textMaxWidthFor(op);
            const lines = wrapText(op.text || '', maxW, op.fontSize);
            let widest = 0;
            for (const ln of lines) widest = Math.max(widest, ctx.measureText(ln).width);
            ctx.restore();
            const lineHeight = op.fontSize * 1.2;
            const ascent = op.fontSize * 0.8;
            const totalH = Math.max(1, lines.length) * lineHeight;
            return { x: op.x - 4, y: op.y - ascent - 4, w: (widest || 1) + 8, h: totalH + 8 };
          }
          return null;
        }
        function isInsideBbox(p, b) {
          return !!b && p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h;
        }
        // Topmost first — Move/Select should pick the visually-on-top op
        // when bboxes overlap, which is the last one in draw order.
        function hitTest(p) {
          for (let i = ops.length - 1; i >= 0; i--) {
            if (isInsideBbox(p, bboxOf(ops[i]))) return i;
          }
          return null;
        }
        function translateOp(op, dx, dy) {
          if (!op) return;
          if (op.kind === 'pen' || op.kind === 'highlight') {
            for (const p of op.points) { p.x += dx; p.y += dy; }
          } else if (op.kind === 'arrow') {
            op.from.x += dx; op.from.y += dy;
            op.to.x += dx; op.to.y += dy;
          } else if (op.kind === 'box' || op.kind === 'redact') {
            op.rect.x += dx; op.rect.y += dy;
          } else if (op.kind === 'text') {
            op.x += dx; op.y += dy;
          }
        }

        function render() {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const stroke = baseStroke();
          const all = drawing ? ops.concat([drawing]) : ops;
          for (const op of all) {
            ctx.globalAlpha = 1;
            if (op.kind === 'pen') {
              ctx.strokeStyle = op.color;
              ctx.lineWidth = op.stroke || stroke;
              ctx.lineCap = 'round';
              ctx.lineJoin = 'round';
              drawPolyline(op.points);
            } else if (op.kind === 'highlight') {
              ctx.strokeStyle = op.color;
              ctx.lineWidth = (op.stroke || stroke) * 4;
              ctx.lineCap = 'round';
              ctx.lineJoin = 'round';
              ctx.globalAlpha = 0.35;
              drawPolyline(op.points);
              ctx.globalAlpha = 1;
            } else if (op.kind === 'redact') {
              // Redact defaults to black, but respects the active swatch
              // when set — same gesture, the user picks the fill color.
              ctx.fillStyle = op.color || '#000';
              ctx.fillRect(op.rect.x, op.rect.y, op.rect.w, op.rect.h);
            } else if (op.kind === 'box') {
              ctx.strokeStyle = op.color;
              ctx.lineWidth = op.stroke || stroke;
              ctx.strokeRect(op.rect.x, op.rect.y, op.rect.w, op.rect.h);
            } else if (op.kind === 'arrow') {
              drawArrow(op.from, op.to, op.color, op.stroke || stroke);
            } else if (op.kind === 'text') {
              // Text wraps to either an enclosing Box's width or to the
              // canvas right edge, so the user's typing can't overflow
              // outside the visible area like it did in v3. Outline is
              // drawn under each line so the text stays legible against
              // any background.
              ctx.font = `bold ${op.fontSize}px sans-serif`;
              ctx.textBaseline = 'alphabetic';
              const outline = (op.color === '#ffffff' || op.color === '#facc15') ? '#111827' : '#ffffff';
              ctx.strokeStyle = outline;
              ctx.lineWidth = Math.max(2, op.fontSize / 10);
              ctx.lineJoin = 'round';
              ctx.miterLimit = 2;
              const maxW = textMaxWidthFor(op);
              const lines = wrapText(op.text, maxW, op.fontSize);
              const lineHeight = op.fontSize * 1.2;
              for (let li = 0; li < lines.length; li++) {
                const ly = op.y + li * lineHeight;
                ctx.strokeText(lines[li], op.x, ly);
              }
              ctx.fillStyle = op.color;
              for (let li = 0; li < lines.length; li++) {
                const ly = op.y + li * lineHeight;
                ctx.fillText(lines[li], op.x, ly);
              }
            }
          }
          // Selection outline — draw last so it sits on top of the op.
          // Dashed blue rectangle plus a hint of "you can drag this" via
          // the canvas cursor change handled in onPointerMove.
          if (selectedIdx !== null && ops[selectedIdx]) {
            const b = bboxOf(ops[selectedIdx]);
            if (b) {
              ctx.save();
              ctx.strokeStyle = '#3b82f6';
              ctx.lineWidth = Math.max(2, baseStroke() * 0.6);
              ctx.setLineDash([10, 6]);
              ctx.strokeRect(b.x, b.y, b.w, b.h);
              ctx.restore();
            }
          }
        }

        function pointerToCanvas(evt) {
          const rect = canvas.getBoundingClientRect();
          const x = (evt.clientX - rect.left) * (canvas.width / rect.width);
          const y = (evt.clientY - rect.top) * (canvas.height / rect.height);
          return { x, y };
        }

        // Text input overlay — a real <input> positioned over the canvas
        // at the click point. We commit on Enter / blur / Done; Escape
        // discards. Storing the input in canvas-pixel coords keeps the
        // export 1:1 with the click position regardless of CSS scaling.
        let textInputEl = null;
        let textInputAnchor = null;
        function openTextInput(p) {
          // Commit (don't discard) any previous in-progress text — if
          // the user typed something, clicked a new spot, we want to
          // save the old text and open a fresh input. Discarding here
          // silently dropped the user's work and was the root cause of
          // "I clicked outside but my text didn't save".
          closeTextInput();
          const fs = textFontSize();
          // If the click point falls inside a Box op, wrap text to the
          // Box's right edge (with a small inset margin). Otherwise wrap
          // to the canvas right edge. Stored on the op at commit so
          // later moves don't accidentally re-wrap to a different box.
          const container = findContainingBox(p);
          const insetRight = 8;
          let maxWidth;
          if (container) {
            maxWidth = (container.rect.x + container.rect.w) - p.x - insetRight;
          } else {
            maxWidth = canvas.width - p.x - insetRight;
          }
          maxWidth = Math.max(80, maxWidth);
          textInputAnchor = { x: p.x, y: p.y, fontSize: fs, color, maxWidth };
          positionTextInput();
        }
        function autosizeTextarea() {
          if (!textInputEl) return;
          // Reset to a single-row height first so the scrollHeight
          // reflects the current content rather than a previously-grown
          // size that would never shrink on delete.
          textInputEl.style.height = 'auto';
          textInputEl.style.height = textInputEl.scrollHeight + 'px';
        }
        function positionTextInput() {
          if (!textInputAnchor) return;
          if (!textInputEl) {
            // <textarea> instead of <input> so the user can see the
            // wrapping as they type — single-line <input> hid lines
            // off-screen and made the v3 overflow bug invisible to the
            // user until export.
            textInputEl = el('textarea', { class: 'ia-text-input', rows: '1' });
            textInputEl.spellcheck = false;
            document.body.appendChild(textInputEl);
            textInputEl.addEventListener('keydown', (e) => {
              if (e.key === 'Escape') { e.preventDefault(); closeTextInput(true); }
              else if (e.key === 'Tab') { e.preventDefault(); commitTextInput(); }
              else if (e.key === 'Enter' && !e.shiftKey) {
                // Enter without Shift = commit (matches v2 muscle memory
                // and the way every chat composer behaves). Shift+Enter
                // still inserts a literal newline so power users can
                // build a multi-line note in one go.
                e.preventDefault();
                commitTextInput();
                return;
              }
              e.stopPropagation(); // don't trigger global Esc/Cmd-Z while typing
            });
            textInputEl.addEventListener('input', autosizeTextarea);
            textInputEl.addEventListener('blur', () => {
              // setTimeout so a click on Done/Cancel can win the focus
              // race; otherwise blur fires first and Done sees an empty input.
              setTimeout(() => { if (textInputEl) commitTextInput(); }, 80);
            });
          }
          const a = textInputAnchor;
          const rect = canvas.getBoundingClientRect();
          const scale = rect.width / canvas.width;
          textInputEl.style.left = (rect.left + a.x * scale) + 'px';
          textInputEl.style.top = (rect.top + a.y * scale) + 'px';
          textInputEl.style.fontSize = (a.fontSize * scale) + 'px';
          textInputEl.style.width = (a.maxWidth * scale) + 'px';
          textInputEl.style.color = a.color;
          textInputEl.style.borderColor = a.color;
          autosizeTextarea();
          textInputEl.focus();
        }
        function commitTextInput() {
          if (!textInputEl || !textInputAnchor) return;
          const txt = textInputEl.value;
          const a = textInputAnchor;
          textInputEl.remove();
          textInputEl = null;
          textInputAnchor = null;
          if (txt.trim()) {
            // Y in fillText is the baseline; click position is the top
            // of the input, so offset down by ~font size for visual match.
            ops.push({ kind: 'text', x: a.x, y: a.y + a.fontSize * 0.85, text: txt, color: a.color, fontSize: a.fontSize, maxWidth: a.maxWidth });
            pushHistory();
            render();
          }
        }
        function closeTextInput(discard) {
          if (!textInputEl) return;
          if (!discard) { commitTextInput(); return; }
          textInputEl.remove();
          textInputEl = null;
          textInputAnchor = null;
        }

        function onPointerDown(e) {
          e.preventDefault();
          if (tool === 'text') {
            // Text mode: open inline input at click; no canvas drawing op
            const p = pointerToCanvas(e);
            openTextInput(p);
            return;
          }
          closeTextInput();
          const p = pointerToCanvas(e);
          if (tool === 'move') {
            // Move tool: pick the topmost op under the cursor, start
            // dragging it if there's a hit. Click on empty = deselect.
            const idx = hitTest(p);
            selectedIdx = idx;
            if (idx !== null) {
              canvas.setPointerCapture?.(e.pointerId);
              dragging = { idx, lastPoint: p, moved: false };
            } else {
              dragging = null;
            }
            render();
            return;
          }
          // Any draw tool clears the selection — the user is moving on
          // to creating a new op, not editing the existing selected one.
          selectedIdx = null;
          canvas.setPointerCapture?.(e.pointerId);
          if (tool === 'pen' || tool === 'highlight') {
            drawing = { kind: tool, color, points: [p] };
          } else if (tool === 'arrow') {
            drawing = { kind: 'arrow', from: p, to: p, color };
          } else if (tool === 'box') {
            drawing = { kind: 'box', rect: { x: p.x, y: p.y, w: 0, h: 0 }, _start: p, color };
          } else if (tool === 'redact') {
            // Redact carries the active color now (black still wins by
            // default because black is in the swatch palette).
            drawing = { kind: 'redact', rect: { x: p.x, y: p.y, w: 0, h: 0 }, _start: p, color };
          }
          render();
        }
        function onPointerMove(e) {
          // Move mode: drag the selected op around. Tracking the delta
          // between successive pointermove events avoids the "snap to
          // pointer" jump that happens if you translate by absolute
          // pointer position vs the op's anchor.
          if (tool === 'move' && dragging) {
            e.preventDefault();
            const p = pointerToCanvas(e);
            const dx = p.x - dragging.lastPoint.x;
            const dy = p.y - dragging.lastPoint.y;
            if (dx || dy) {
              translateOp(ops[dragging.idx], dx, dy);
              dragging.lastPoint = p;
              dragging.moved = true;
              render();
            }
            return;
          }
          // Move-mode hover affordance — show a "this is grabbable"
          // cursor when the pointer is over a hit op.
          if (tool === 'move' && !dragging) {
            const p = pointerToCanvas(e);
            canvas.style.cursor = (hitTest(p) !== null) ? 'move' : 'default';
            return;
          }
          if (!drawing) return;
          e.preventDefault();
          const p = pointerToCanvas(e);
          if (drawing.kind === 'pen' || drawing.kind === 'highlight') {
            drawing.points.push(p);
          } else if (drawing.kind === 'arrow') {
            drawing.to = p;
          } else { // box, redact
            const s = drawing._start;
            drawing.rect = {
              x: Math.min(s.x, p.x), y: Math.min(s.y, p.y),
              w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y),
            };
          }
          render();
        }
        function onPointerUp(e) {
          if (tool === 'move') {
            if (dragging) {
              // Only push history if the op actually moved — a plain
              // click-to-select shouldn't clutter the undo stack.
              if (dragging.moved) pushHistory();
              dragging = null;
            }
            return;
          }
          if (!drawing) return;
          e.preventDefault();
          // Discard zero-size ops so a stray click doesn't bloat the
          // undo stack with invisible no-ops.
          let valid = false;
          if (drawing.kind === 'pen' || drawing.kind === 'highlight') {
            valid = drawing.points.length > 1;
          } else if (drawing.kind === 'arrow') {
            valid = Math.hypot(drawing.to.x - drawing.from.x, drawing.to.y - drawing.from.y) >= 8;
          } else { // box, redact
            valid = drawing.rect.w >= 4 && drawing.rect.h >= 4;
          }
          if (valid) {
            delete drawing._start;
            ops.push(drawing);
            pushHistory();
          }
          drawing = null;
          render();
        }
        canvas.addEventListener('pointerdown', onPointerDown);
        canvas.addEventListener('pointermove', onPointerMove);
        canvas.addEventListener('pointerup', onPointerUp);
        canvas.addEventListener('pointercancel', onPointerUp);
        canvas.addEventListener('pointerleave', onPointerUp);

        function setTool(next) {
          // Switching tools while a text input is open commits the text
          // first — otherwise the input stays visually orphaned over
          // the canvas in a different mode.
          closeTextInput();
          // Moving from a draw tool to anything else also drops the
          // selection. Selection only makes sense inside Move mode.
          if (next !== 'move') selectedIdx = null;
          tool = next;
          for (const id in toolBtns) toolBtns[id].classList.toggle('active', id === next);
          // Cursor reflects the current mode: text uses text caret, move
          // shows the default pointer (a hover hit will flip it to "move"
          // inside onPointerMove), everything else uses crosshair.
          if (next === 'text') canvas.style.cursor = 'text';
          else if (next === 'move') canvas.style.cursor = 'default';
          else canvas.style.cursor = 'crosshair';
          render();
        }
        for (const id in toolBtns) {
          toolBtns[id].addEventListener('click', () => setTool(id));
        }
        swatchEls.forEach(s => {
          // mousedown.preventDefault keeps the open text input focused —
          // otherwise clicking a swatch blurs the textarea, which fires
          // the auto-commit-on-blur path and ends the editing session
          // every time the user tweaks a color mid-typing.
          s.addEventListener('mousedown', (e) => {
            if (textInputEl) e.preventDefault();
          });
          s.addEventListener('click', () => {
            color = s.dataset.hex;
            swatchEls.forEach(x => x.classList.toggle('active', x === s));
            // Live-update an open text input so the user sees the color
            // change before they commit.
            if (textInputEl && textInputAnchor) {
              textInputAnchor.color = color;
              textInputEl.style.color = color;
              textInputEl.style.borderColor = color;
            }
            // Recolor the selected op in Move mode — swatches act as a
            // live recolor tool for already-drawn annotations. All op
            // kinds carry a `color` (redact defaults to black at render,
            // but accepts any swatch when set).
            if (tool === 'move' && selectedIdx !== null && ops[selectedIdx]) {
              ops[selectedIdx].color = color;
              pushHistory();
              render();
            }
          });
        });

        undoBtn.addEventListener('click', () => {
          closeTextInput();
          doUndo();
        });
        redoBtn.addEventListener('click', () => {
          closeTextInput();
          doRedo();
        });

        function cleanup() {
          closeTextInput(true);
          overlay.remove();
          document.body.classList.remove('ia-open');
          document.removeEventListener('keydown', onKey);
        }
        function onKey(e) {
          // Don't hijack keys while the text input has focus — typing
          // Z / Y / Escape / Backspace in there should belong to the
          // input itself (those are handled by its own listener).
          if (document.activeElement === textInputEl) return;
          if (e.key === 'Escape') {
            e.preventDefault();
            // Esc while something is selected = just clear the selection,
            // don't kill the whole annotator (less destructive).
            if (selectedIdx !== null) { selectedIdx = null; render(); return; }
            finish(null);
          }
          else if ((e.key === 'Backspace' || e.key === 'Delete') && selectedIdx !== null) {
            e.preventDefault();
            ops.splice(selectedIdx, 1);
            selectedIdx = null;
            pushHistory();
            render();
          }
          else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
            e.preventDefault();
            undoBtn.click();
          } else if ((e.metaKey || e.ctrlKey) && ((e.key.toLowerCase() === 'z' && e.shiftKey) || e.key.toLowerCase() === 'y')) {
            e.preventDefault();
            redoBtn.click();
          }
        }
        document.addEventListener('keydown', onKey);
        // Keep the text input glued to its canvas anchor while the user
        // resizes the window or zooms.
        const onResize = () => positionTextInput();
        window.addEventListener('resize', onResize);

        function finish(result) {
          window.removeEventListener('resize', onResize);
          cleanup();
          resolve(result);
        }
        cancelBtn.addEventListener('click', () => {
          // Discard any pending text input — Cancel should never
          // sneak a committed text op into the unsent file.
          closeTextInput(true);
          finish(null);
        });
        doneBtn.addEventListener('click', () => {
          // Commit any open text input before exporting — otherwise
          // the user's last typed line is silently dropped.
          closeTextInput();
          // Render once more so the just-committed text lands in the
          // bitmap that toBlob captures.
          render();
          canvas.toBlob((blob) => {
            if (!blob) return finish(null);
            const origName = (file.name || 'image').replace(/\.[^.]+$/, '');
            const out = new File([blob], origName + '-annotated.png', { type: 'image/png' });
            finish(out);
          }, 'image/png');
        });

        render();
      }
    });
  }

  async function sendTeamchatFile(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    if (state.teamchatActiveTarget?.type === 'user') fd.append('targetUserId', state.teamchatActiveTarget.user.id);
    else if (state.teamchatActiveTarget?.type === 'room') fd.append('roomId', state.teamchatActiveTarget.room.id);
    else return;
    if (state.teamchatReplyTarget?.id) fd.append('replyTo', state.teamchatReplyTarget.id);
    try {
      const r = await fetchT('/api/team/send', { method: 'POST', headers: authHeaders(), body: fd }, 120000);
      const j = await r.json();
      if (j.error) { toast(j.error, 'error'); return; }
      setTeamchatReplyTarget(null);
    } catch { toast('Upload failed', 'error'); }
  }

  async function markTeamchatRead() {
    const key = state.teamchatActiveKey;
    if (!key) return;
    // Only the unread→0 transition needs a roster re-render. When the chat
    // already had 0 unread (the common case when clicking between teammates),
    // skip the second full renderTeamchatList() that openTeamchatWith already did.
    const hadUnread = (state.teamDigest[key]?.unread || 0) > 0;
    try {
      await api('/api/team/read', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatKey: key })
      });
      // Local digest update so the unread badge clears immediately.
      if (state.teamDigest[key]) {
        state.teamDigest[key].unread = 0;
      }
      if (hadUnread) {
        renderTeamchatList();
        refreshTeamchatBadge();
      }
    } catch { /* best effort */ }
  }

  // Replace the existing .teamchat-msg-text in the rendered row with a
  // textarea + Save/Cancel. Server enforces the 15-min window, so this
  // function trusts the button visibility logic to filter out expired
  // messages — but reports a clean error toast if it slips through.
  function startTeamchatEdit(m, row) {
    if (!m?.id || !row) return;
    const bubble = row.querySelector('.teamchat-msg');
    const textEl = bubble?.querySelector('.teamchat-msg-text');
    if (!bubble) return;
    // If the bubble has no .teamchat-msg-text yet (audio/image-only),
    // we still don't allow edit — the server rejects it anyway.
    if (!textEl) { toast('Only text messages can be edited', 'error'); return; }
    if (bubble.querySelector('.teamchat-edit-form')) return; // already editing
    const form = el('div', { class: 'teamchat-edit-form' });
    const ta = el('textarea', { class: 'teamchat-edit-textarea', rows: 2 });
    ta.value = m.text || '';
    const btns = el('div', { class: 'teamchat-edit-btns' });
    const save = el('button', { class: 'btn btn-primary btn-sm', type: 'button', text: 'Save' });
    const cancel = el('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: 'Cancel' });
    btns.appendChild(save); btns.appendChild(cancel);
    form.appendChild(ta); form.appendChild(btns);
    textEl.style.display = 'none';
    bubble.insertBefore(form, textEl);
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);
    function done(restoreOriginal) {
      form.remove();
      if (restoreOriginal) textEl.style.display = '';
    }
    cancel.addEventListener('click', () => done(true));
    ta.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); done(true); }
      else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save.click(); }
      e.stopPropagation();
    });
    save.addEventListener('click', async () => {
      const newText = ta.value.trim();
      if (!newText) { toast('Message cannot be empty', 'error'); return; }
      if (newText === m.text) { done(true); return; }
      save.disabled = true; cancel.disabled = true;
      try {
        const r = await fetchT('/api/team/messages/' + encodeURIComponent(m.id), {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: newText }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) {
          toast(j.error || 'Edit failed', 'error');
          save.disabled = false; cancel.disabled = false;
          return;
        }
        // Server broadcasts team_message:updated → onTeamchatMessageUpdated
        // will re-render with the new text. We only restore the bubble
        // structure here; the socket handler does the real work.
        done(true);
      } catch {
        toast('Échec de la modification — le message existe-t-il encore ?', 'error');
        save.disabled = false; cancel.disabled = false;
      }
    });
  }

  async function deleteTeamchatMessage(m) {
    if (!m?.id) return;
    if (!confirm('Delete this message? This can\'t be undone.')) return;
    try {
      const r = await fetchT('/api/team/messages/' + encodeURIComponent(m.id), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(j.error || 'Delete failed', 'error'); return; }
      // Optimistic — server will broadcast team_message:deleted too.
      removeTeamchatMessageLocal(m.id);
    } catch {
      toast('Échec de la suppression — réessayez', 'error');
    }
  }

  // Local helpers used by both optimistic UI and the socket handlers, so
  // a same-user delete on another tab still wipes the row here too.
  function removeTeamchatMessageLocal(messageId) {
    const idx = state.teamchatMessages.findIndex(x => x.id === messageId);
    if (idx >= 0) {
      state.teamchatMessages.splice(idx, 1);
      renderTeamchatMessages();
    }
  }
  function updateTeamchatMessageLocal(updated) {
    if (!updated?.id) return;
    const idx = state.teamchatMessages.findIndex(x => x.id === updated.id);
    if (idx >= 0) {
      state.teamchatMessages[idx] = { ...state.teamchatMessages[idx], ...updated };
      renderTeamchatMessages();
    }
  }

  function onTeamchatMessageUpdated(payload) {
    if (!payload?.message) return;
    // Update the active-chat list view (re-renders bubbles in place).
    if (state.teamchatActiveKey === payload.chatKey) {
      updateTeamchatMessageLocal(payload.message);
    }
    // Keep the digest preview honest if this was the last-message bubble.
    const cur = state.teamDigest[payload.chatKey];
    if (cur?.lastMessage && cur.lastMessage.fromUserId === payload.message.fromUserId) {
      cur.lastMessage = { type: payload.message.type, text: payload.message.text, createdAt: payload.message.createdAt, fromUserId: payload.message.fromUserId };
      if (state.activeView === 'teamchat') renderTeamchatList();
    }
  }

  function onTeamchatMessageDeleted(payload) {
    if (!payload?.messageId) return;
    if (state.teamchatActiveKey === payload.chatKey) {
      removeTeamchatMessageLocal(payload.messageId);
    }
    // No surgical digest update — the digest stays whatever the next
    // inbound message rewrites it to. Acceptable since the deleted
    // message is gone from the chat list view.
  }

  function onTeamchatSocketMessage(m) {
    // Update digest preview + unread for the matching chatKey, regardless
    // of whether the user is currently looking at that chat. Lets the
    // sidebar stay live across all open chats.
    if (m.chatKey) {
      const cur = state.teamDigest[m.chatKey] || {};
      cur.lastMessage = { type: m.type, text: m.text, createdAt: m.createdAt, fromUserId: m.fromUserId };
      const isMine = teamMessageIsMine(m);
      const isActiveChat = state.teamchatActiveKey === m.chatKey;
      cur.unread = (isMine || isActiveChat) ? 0 : (cur.unread || 0) + 1;
      state.teamDigest[m.chatKey] = cur;
      if (state.activeView === 'teamchat') renderTeamchatList();
      if (isActiveChat) {
        // Active chat: teamchatMessages IS teamThreadCache[chatKey] (same ref),
        // so this push updates the cache too.
        state.teamchatMessages.push(m);
        renderTeamchatMessages();
        if (!isMine) markTeamchatRead();
      } else if (state.teamThreadCache[m.chatKey]) {
        // Non-active but cached: keep the cache fresh so re-opening paints the
        // latest message instantly. Background revalidate would heal it anyway.
        state.teamThreadCache[m.chatKey].push(m);
      }
      refreshTeamchatBadge();
      // Ring + desktop notify for messages from teammates that aren't
      // already being viewed. Helps when the agent is on the inbox tab
      // or the browser is in the background.
      // Team Bot admin mirrors are an audit copy: they count as unread but never
      // ring or pop a desktop notification (one per tick, for every agent).
      const silentMirror = m.fromUserId === 'team-bot' && m.meta?.kind === 'mirror';
      if (!isMine && !silentMirror && (!isActiveChat || document.hidden)) {
        try { playNotifSound('team'); } catch (e) { /* */ }
        try { showTeamNotification(m); } catch (e) { /* */ }
      }
    }
  }
  function onTeamchatSocketRead(payload) {
    if (state.teamchatActiveKey !== payload.chatKey) return;
    for (const m of state.teamchatMessages) {
      if (Array.isArray(m.readBy) && !m.readBy.includes(payload.userId)) m.readBy.push(payload.userId);
    }
    renderTeamchatMessages();
  }
  function onPresenceUpdate({ userId, online }) {
    if (online) state.teamPresence.add(userId);
    else state.teamPresence.delete(userId);
    if (state.activeView === 'teamchat') renderTeamchatList();
    const tr = tst.rows.find(x => x.userId === userId);
    if (tr && tr.online !== !!online) { tr.online = !!online; _tstRenderTeam(); }
  }
  function onPresenceInitial({ online }) {
    state.teamPresence = new Set(online || []);
    if (state.activeView === 'teamchat') renderTeamchatList();
  }

  // Create/edit share one modal. null → create; a room object → edit its
  // members (and optionally its name — the field doubles as a rename input).
  // Reset at every open so a dismissed edit never leaks into the next create.
  let _editingTeamRoom = null;
  function openTeamRoomModal(room = null) {
    if (state.me?.role !== 'admin') { toast('Admin only', 'error'); return; }
    // Re-resolve from state.teamRooms — the header closure may hold a stale
    // snapshot; a checkbox list built from stale memberIds would evict members
    // added by another admin since the pane last repainted (full-replace save).
    if (room && room.id) room = (state.teamRooms || []).find(r => r.id === room.id) || room;
    _editingTeamRoom = (room && room.id) ? room : null; // guards the raw-listener MouseEvent too
    const editing = _editingTeamRoom;
    document.getElementById('teamRoomName').value = editing ? (editing.name || '') : '';
    const titleEl = document.getElementById('teamRoomModalTitle');
    if (titleEl) titleEl.textContent = editing ? 'Gérer les membres' : 'Create team room';
    const saveLabel = document.getElementById('teamRoomSaveLabel');
    if (saveLabel) saveLabel.textContent = editing ? 'Enregistrer' : 'Create';
    const wrap = document.getElementById('teamRoomMembers');
    clear(wrap);
    const members = new Set(editing?.memberIds || []);
    // Room membership uses the shared identity id; linked Support2/Leads7
    // accounts therefore render as one checkbox and remote teammates are real
    // room members instead of dead UUID-shaped entries.
    const addMemberRow = (memberId, label, suffix = '') => {
      const row = el('label', { class: 'teamroom-member-row' });
      const cb = el('input', { type: 'checkbox', value: memberId });
      cb.checked = members.has(memberId);
      row.appendChild(cb);
      row.appendChild(el('span', { text: label }));
      if (suffix) row.appendChild(el('span', { class: 'muted small', text: suffix }));
      wrap.appendChild(row);
    };
    // Match Support2: while editing, show the logged-in linked identity too.
    // It remains one person even though that identity has an account on both
    // platforms, and makes the room's Administration A/H membership explicit.
    const myMemberId = state.me?.identityId || myCanonId() || state.me?.id;
    if (editing && myMemberId) {
      addMemberRow(myMemberId, state.me?.displayName || state.me?.username || 'Moi', '· moi');
    }
    for (const u of state.teamUsers) {
      const memberId = u.identityId || u.canonicalId || u.id;
      addMemberRow(memberId, userLabel(u.username, u.displayName || u.username), u.remote ? '· Support2' : '');
    }
    openModal('teamRoomModal');
  }
  async function saveTeamRoom() {
    const name = document.getElementById('teamRoomName').value.trim();
    if (!name) { toast('Room name required', 'error'); return; }
    const cbs = Array.from(document.querySelectorAll('#teamRoomMembers input[type=checkbox]'));
    const memberIds = cbs.filter(c => c.checked).map(c => c.value);
    const editing = _editingTeamRoom;
    if (editing) {
      // Edit mode — the endpoint FULL-REPLACES memberIds, so preserve every
      // existing id that was never rendered as a checkbox: state.me.id (self
      // is excluded from the roster) and any legacy/departed ids.
      const rendered = new Set(cbs.map(c => c.value));
      for (const id of (editing.memberIds || [])) {
        if (!rendered.has(id) && !memberIds.includes(id)) memberIds.push(id);
      }
      try {
        const r = await api('/api/team/rooms/' + encodeURIComponent(editing.id) + '/members', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ memberIds })
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j.error) { toast(j.error || 'Échec de la mise à jour', 'error'); return; }
        let room = j.room || { ...editing, memberIds };
        // The name field doubles as a rename input — changed → PATCH (B1).
        let renameFailed = false;
        if (name !== editing.name) {
          try {
            const r2 = await api('/api/team/rooms/' + encodeURIComponent(editing.id), {
              method: 'PATCH', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name })
            });
            const j2 = await r2.json().catch(() => ({}));
            if (r2.ok && j2.room) room = j2.room;
            else { renameFailed = true; toast(j2.error || 'Membres mis à jour, renommage échoué', 'error'); }
          } catch { renameFailed = true; toast('Membres mis à jour, renommage échoué', 'error'); }
        }
        _editingTeamRoom = null;
        closeModal('teamRoomModal');
        if (!renameFailed) toast('Membres mis à jour', 'success');
        await loadTeamchatRooms();
        renderTeamchatList();
        if (state.teamchatActiveKey === 'team:room:' + room.id) {
          state.teamchatActiveTarget = { type: 'room', room };
          renderTeamchatActive();
        }
      } catch { toast('Échec de la mise à jour', 'error'); }
      return;
    }
    try {
      const r = await api('/api/team/rooms', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, memberIds })
      });
      const j = await r.json();
      if (j.error) { toast(j.error, 'error'); return; }
      closeModal('teamRoomModal');
      toast('Room created', 'success');
      await loadTeamchatRooms();
      renderTeamchatList();
    } catch { toast('Failed to create room', 'error'); }
  }

  // ============ Today's Classes + Today's Clubs (REMOVED — Sprint 3) ============
  // All schedule / clubs UI logic was removed in Sprint 3 along with the
  // server-side /api/schedule/* and /api/clubs/* routes. Restore from git
  // history if these features come back.

  document.addEventListener('DOMContentLoaded', () => {
    wireBotCoachModal();
    // One-time binding for the Contact Profile rename pencil. The button
    // itself never re-renders, so wiring once at boot is enough — both
    // openProfilePanel and the selectConversation refresh path leave the
    // button's onclick alone.

    // Pre-warm the Tickets view's lookup tables in the background so
    // the first click on the Tickets tab doesn't pay a HubSpot round-
    // trip for pipelines + enums. Fire-and-forget — if it fails the
    // first explicit click will retry the load.
    setTimeout(() => loadTicketsPipelinesAndEnums().catch(() => {}), 2000);
    // Tickets view — search input (debounced) + refresh button. Both
    // re-fetch the list with the current filter/search state. Modal
    // backdrop close already handled by the global data-modal-close
    // listener.
    let _ticketsSearchTimer = null;
    document.getElementById('ticketsSearch')?.addEventListener('input', (e) => {
      state.ticketsSearch = (e.target.value || '').trim();
      clearTimeout(_ticketsSearchTimer);
      _ticketsSearchTimer = setTimeout(() => loadTickets({ resetPaging: true }), 280);
    });
    document.getElementById('ticketsRefreshBtn')?.addEventListener('click', () => loadTickets({ resetPaging: true }));
    document.getElementById('ticketsMapTeamBtn')?.addEventListener('click', () => openTeamOwnerMapModal());
    // Bulk-action bar wiring (sheet-style multi-select)
    document.getElementById('ticketsBulkReassignBtn')?.addEventListener('click', (ev) => bulkReassignTickets(ev.currentTarget));
    document.getElementById('ticketsBulkStatusBtn')?.addEventListener('click', (ev) => bulkChangeStatus(ev.currentTarget));
    document.getElementById('ticketsBulkCancelBtn')?.addEventListener('click', () => clearTicketsSelection());
    // Feedback Status dropdown wiring
    document.getElementById('ticketsCategoryBtn')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const menu = document.getElementById('ticketsCategoryMenu');
      if (!menu) return;
      if (menu.classList.contains('hidden')) openTicketsCategoryMenu();
      else closeTicketsCategoryMenu();
    });
    document.getElementById('ticketsCategorySearch')?.addEventListener('input', (e) => {
      state.ticketsCategorySearch = (e.target.value || '').trim();
      renderTicketsCategoryMenu();
    });
    document.getElementById('ticketsCategoryClear')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      state.ticketsCategoryFilter.clear();
      state.ticketsCategorySearch = '';
      const searchEl = document.getElementById('ticketsCategorySearch');
      if (searchEl) searchEl.value = '';
      renderTicketsCategoryTrigger();
      renderTicketsCategoryMenu();
      loadTickets({ resetPaging: true });
    });
    document.getElementById('ticketsAssigneeBtn')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      toggleTicketsAssigneeMenu();
    });
    document.getElementById('ticketsAssigneeClear')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      state.ticketsAssigneeFilter.clear();
      updateTicketsAssigneeCount();
      renderTicketsAssigneeList();
      loadTickets({ resetPaging: true });
    });
    // Close assignee menu on outside click
    document.addEventListener('click', (ev) => {
      const wrap = document.getElementById('ticketsAssigneeFilter');
      if (!wrap || !wrap.contains(ev.target)) {
        document.getElementById('ticketsAssigneeMenu')?.classList.add('hidden');
        document.getElementById('ticketsAssigneeBtn')?.setAttribute('aria-expanded', 'false');
        wrap?.classList.remove('open');
      }
    });
    // Close on outside click — same idiom as the topbar dropdowns +
    // attach popover. The menu's own clicks (search input, checkboxes,
    // Clear button) stopPropagation so they don't accidentally close.
    document.addEventListener('click', (ev) => {
      const wrap = document.getElementById('ticketsCategoryFilter');
      if (!wrap || !wrap.classList.contains('open')) return;
      if (wrap.contains(ev.target)) return;
      closeTicketsCategoryMenu();
    });
    document.getElementById('ticketsCategoryMenu')?.addEventListener('click', (ev) => ev.stopPropagation());
  });

  // ============ Test schedule view (Sprint 4 Phase 2) ============
  // 3-teacher × 40-slot grid, 10:00–19:45 in 15-min steps.
  // Click an `available` cell → Booking modal (lead search → confirm).
  // Click a `booked` cell → Actions modal (in_test / completed / no_show / cancel).
  //
  // State lives on `state.ts` so we don't accidentally collide with other
  // view state. Refreshed lazily on view-enter via ensureTestScheduleInit().
  state.ts = state.ts || {
    inited: false,
    date: null,          // 'YYYY-MM-DD'
    teachers: [],
    times: [],
    slotsByCell: {},     // 'teacherId|HH:MM:SS' → slot
    bookSlot: null,      // active slot for booking modal
    bookLead: null,      // selected lead for booking modal
    bookSearchTimer: null,
    actionsSlot: null,
    // Eval modal draft state (swat:B1). The save handler used to read the
    // CEFR level from a `.active` class query at submit time and always sent
    // all 4 eval keys — an empty/partial DOM read silently NULLed or no-oped
    // the evaluation. The draft is seeded from the slot on EVERY modal open
    // (grid, Mes tests, teacher profile all go through openTsActionsModal)
    // and the submit builds its body from draft + inputs, omitting untouched
    // fields entirely.
    evalDraft: null,     // { slotId, new_level }
    evalBaseline: null,  // { slotId, new_level, notes, score, teacher_recommendation } as loaded
    // Day filters (2026-09-07, swat:B1). Purely client-side over the day
    // payload already fetched — no extra request, no server change.
    // teacherFilter = a teacher id or 'all'; agentFilter = a "par X"
    // attribution key (see _tsSeatAgentKey) or 'all'.
    teacherFilter: 'all',
    agentFilter: 'all',
  };

  // Filters survive the date arrows AND a reload — same localStorage idiom as
  // the teacher "Mes tests" view mode (`leads7.ttView`): read once, write on
  // change, always inside try/catch (private windows throw).
  const TS_FILTER_KEYS = { teacher: 'leads7.tsTeacherFilter', agent: 'leads7.tsAgentFilter' };
  function _tsLoadSavedFilters() {
    try { state.ts.teacherFilter = localStorage.getItem(TS_FILTER_KEYS.teacher) || 'all'; } catch { state.ts.teacherFilter = 'all'; }
    try { state.ts.agentFilter = localStorage.getItem(TS_FILTER_KEYS.agent) || 'all'; } catch { state.ts.agentFilter = 'all'; }
  }
  function _tsSaveFilter(which, value) {
    try {
      if (!value || value === 'all') localStorage.removeItem(TS_FILTER_KEYS[which]);
      else localStorage.setItem(TS_FILTER_KEYS[which], value);
    } catch { /* private window / storage off — the filter still works in-session */ }
  }

  // Stable option key for the agent attribution a seat carries — the very same
  // "par X" the grid prints on the seat chip. Two rules make it safe:
  //   • a MASKED seat (another agent's booking, server-scoped — swat:B1) has no
  //     attribution at all, so it belongs to no option and can never be revealed
  //     through this filter;
  //   • the key is the raw username, not the rendered label, because a bot row's
  //     label resolves the lead owner's display name and would change under the
  //     persisted value once state.teamUsers lands.
  // Bot bookings key on the lead OWNER (same rule as bookedByLabel), so they
  // stay a distinct, visible option ("Mme Doha · bot") instead of "bot_funnel".
  function _tsSeatAgentKey(s) {
    if (!s || s.masked) return '';
    const by = String(s.booked_by || '').trim();
    if (!by) return '';
    if (by === 'bot_funnel') return 'bot:' + String(s.lead_agent_id || '').trim().toLowerCase();
    return 'by:' + by.toLowerCase();
  }
  function _tsSeatMatchesAgent(s, agentKey) {
    if (!agentKey || agentKey === 'all') return true;
    return _tsSeatAgentKey(s) === agentKey;
  }

  const TS_STATUS_CLASS = {
    available:     'ts-cell-available',
    booked:        'ts-cell-booked',
    confirmed:     'ts-cell-confirmed',
    reminder_sent: 'ts-cell-reminder',
    in_test:       'ts-cell-in-test',
    completed:     'ts-cell-completed',
    no_show:       'ts-cell-no-show',
    cancelled:     'ts-cell-cancelled',
  };
  const TS_STATUS_LABEL = {
    available:     'Available',
    booked:        'Booked',
    confirmed:     'Confirmed',
    reminder_sent: 'Reminder sent',
    in_test:       'In test',
    completed:     'Completed',
    no_show:       'No show',
    cancelled:     'Cancelled',
  };
  // Overbooking capacity pip-meter: one pip per seat coloured by status, padded
  // with hollow pips to seatsPerSlot. The X/N count reflects LIVE bookings only
  // (booked/confirmed/reminder_sent/in_test) so a past all-no-show cell reads
  // "0/3", not a misleading "3/3" (audit FE-1).
  const TS_PIP_CLASS = {
    booked: 'pip-booked', confirmed: 'pip-confirmed', reminder_sent: 'pip-reminder',
    in_test: 'pip-in-test', completed: 'pip-completed', no_show: 'pip-no-show',
  };
  const TS_LIVE_STATUSES = ['booked', 'confirmed', 'reminder_sent', 'in_test'];
  function tsBuildMeter(seats, liveCount, seatsPerSlot) {
    const pips = el('span', { class: 'ts-seat-pips' });
    for (const s of seats) {
      const cls = TS_PIP_CLASS[s.status];
      pips.appendChild(el('span', { class: 'ts-seat-pip' + (cls ? ' is-taken ' + cls : '') }));
    }
    for (let i = seats.length; i < seatsPerSlot; i++) pips.appendChild(el('span', { class: 'ts-seat-pip' }));
    const meter = el('div', { class: 'ts-seat-meter' }, [pips]);
    meter.appendChild(el('span', { class: 'ts-seat-count', text: liveCount + '/' + seatsPerSlot,
      // Teacher UI is English-only (2026-07-21 audit); the sales grid keeps French.
      title: (state.me?.role || '').toLowerCase() === 'teacher'
        ? ('Overbooking: up to ' + seatsPerSlot + ' students/slot (≈50% no-shows)')
        : ('Surbooking: jusqu’à ' + seatsPerSlot + ' élèves/créneau (≈50% d’absents)') }));
    return meter;
  }

  // Morocco's calendar day, not the browser's (Chrome's tz data still says +01).
  function tsTodayIso() {
    return MA.dayKey();
  }

  // Returns true if a slot is in the past — used to grey out empty slots
  // that already ended (no reason to surface a 10:00 slot at 15:00 today).
  // Treats slot as "ended" when slot_time + 15min < now. Past-date strings
  // always return true; future-date strings always return false.
  function tsSlotIsPast(dateIso, slotTime) {
    if (!dateIso || !slotTime) return false;
    const today = tsTodayIso();
    if (dateIso < today) return true;
    if (dateIso > today) return false;
    // Same day — compare slot_time + 15min to now (HH:MM compare).
    const hm = String(slotTime).slice(0, 5);
    const [h, m] = hm.split(':').map(Number);
    const slotEndMin = h * 60 + m + 15;
    // Morocco wall clock via MA: getHours() ran an hour ahead and hid the next hour of free seats.
    const now = MA.at(Date.now());
    const nowMin = now.getUTCHours() * 60 + now.getUTCMinutes();
    return slotEndMin <= nowMin;
  }

  // Kept for callers outside the toolbar; the date control itself steps days
  // through _tsYmdShift (UTC-midnight maths, same as the server's copy).
  function tsShiftDate(iso, days) {
    const d = new Date(iso + 'T12:00:00');
    d.setDate(d.getDate() + days);
    return tsFormatIso(d);
  }
  function tsFormatIso(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }

  // ============ Tests view (2026-05-24) ============
  // HubSpot/Sheets-style spreadsheet of every booked / completed / no-show /
  // cancelled slot in a date range. Sister to Test schedule (grid). The
  // call-center team uses this to triage at a glance + open a row for inline
  // status edit. Filter chips + free-text search on top of the table; clicking
  // a row reuses the existing tsSlotDetailsModal (same modal Test schedule uses).
  state.testsView = state.testsView || {
    inited: false,
    from: null,
    to: null,
    rows: [],
    filter: 'all',       // all | booked | confirmed | in_test | completed | no_show | cancelled
    search: '',
    // The search string the SERVER is currently answering for (?q=). Non-empty
    // means the loaded rows are a whole-history match, not the date range, and
    // the local text filter must stand down — the server matched on raw phone
    // digits and on fields this client may only ever see masked.
    globalQuery: '',
    agent: 'all',        // 'all' | a username — see _testsRowBelongsTo
    teacher: 'all',      // 'all' | a teacher_id — see _testsPopulateTeacherFilter
    stage: 'all',        // 'all' | a leads.pipeline_stage — see _testsPopulateStageFilter
    loading: false,
    // Set of slot.id strings currently checked in the table. Used by the bulk
    // action bar (Send template) — populated by row checkbox clicks and the
    // master "Select all visible" link in the bar.
    selectedSlotIds: new Set(),
  };

  function _testsTodayIso() {
    return MA.dayKey();
  }
  function _testsAddDays(iso, n) {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  }
  // "par bot_funnel" tells nobody anything (Ayoub 2026-06-10) — when the BOT
  // booked the test, show the agent who OWNS the contact (leads.agent_id,
  // surfaced as lead_agent_id on slot/booking rows) with a small "· bot"
  // marker so the automation stays visible. Manual bookings keep booked_by.
  function _agentDisplay(username) {
    if (!username) return '';
    return userLabel(username, (state.teamUsers || []).find(u => u.username === username)?.displayName || username);
  }
  function bookedByLabel(s) {
    const by = s?.booked_by || '';
    if (by !== 'bot_funnel') return userLabel(by);
    const owner = s?.lead_agent_id;
    return owner ? (_agentDisplay(owner) + ' · bot') : 'bot';
  }
  // "Is this row that agent's?" for the Tests view agent filter. Mirrors the
  // server's ownership rule (_tsOwnsSlotRow, server.js) — the lead's OWNER or
  // whoever booked the seat — so picking an agent shows the page through their
  // eyes. Both columns matter because they diverge: the bot books
  // (booked_by='bot_funnel') on a lead that belongs to an agent, and an agent
  // can book onto a colleague's lead. The server's orphan clause (no owner +
  // non-real booker → visible to every agent) is deliberately NOT reproduced:
  // an orphan is nobody's, so it must not surface under a named agent — it
  // stays reachable under "All agents". Casing isn't guaranteed on either
  // column (mixed historical writers).
  function _testsRowBelongsTo(r, username) {
    const who = String(username || '').trim().toLowerCase();
    if (!who) return false;
    return String(r?.lead_agent_id || '').trim().toLowerCase() === who
      || String(r?.booked_by || '').trim().toLowerCase() === who;
  }

  // Tiny amber "⚠" marker shown next to a booking name the server flagged as
  // junk (row.name_unconfirmed) — emoji / phone / email / username / empty. Tells
  // the teacher + team to confirm the real full name. Tokens-only inline style
  // (this renders in several surfaces that share no CSS class; app.css untouched);
  // kept unobtrusive — a small chip, never a big red block. swat:B8.
  function _nameUnconfirmedBadge() {
    // Shared across teacher + sales surfaces — teacher sees English (2026-07-21 audit).
    const _teacherBadge = (state.me?.role || '').toLowerCase() === 'teacher';
    return el('span', {
      class: 'name-unconfirmed-badge',
      title: _teacherBadge
        ? 'Name to confirm — the bot could not get the full name'
        : "Nom à confirmer — le bot n'a pas pu obtenir le nom complet",
      'aria-label': _teacherBadge ? 'Name to confirm' : 'Nom à confirmer',
      style: 'display:inline-flex;align-items:center;justify-content:center;'
        + 'margin-inline-start:5px;padding:0 4px;height:15px;border-radius:999px;'
        + 'font-size:10px;font-weight:700;line-height:1;vertical-align:middle;'
        + 'white-space:nowrap;flex:0 0 auto;'
        + 'color:var(--warning);background:var(--warning-soft);'
        + 'border:1px solid var(--warning);',
      text: '⚠',
    });
  }

  function _testsFmtDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return MA.loc(d, 'd', undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }
  function _testsFmtTime(t) {
    if (!t) return '';
    return String(t).slice(0, 5);
  }
  function _testsFmtRelative(iso) {
    if (!iso) return '';
    const ts = new Date(iso).getTime();
    if (isNaN(ts)) return '';
    const diff = Date.now() - ts;
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return Math.round(diff / 60_000) + 'm ago';
    if (diff < 86_400_000) return Math.round(diff / 3_600_000) + 'h ago';
    return MA.loc(new Date(iso), 'dt');
  }
  function _testsStatusKind(status) {
    if (status === 'completed') return 'pos';
    if (status === 'no_show' || status === 'cancelled') return 'neg';
    if (status === 'in_test' || status === 'confirmed') return 'pending';
    return '';  // booked + others — neutral
  }
  function _testsStatusLabel(status) {
    return ({
      booked: 'Booked',
      confirmed: 'Confirmed',
      in_test: 'In test',
      completed: 'Done',
      no_show: 'No-show',
      cancelled: 'Cancelled',
    })[status] || status || '—';
  }
  function _testsRecKind(rec) {
    if (rec === 'recommend') return 'pos';
    if (rec === 'not_recommend') return 'neg';
    return rec ? 'pos' : 'pending';
  }

  const TS_ASSESSMENT_RECOMMENDATIONS = [
    { code: 'english_lik_6m', program: 'English-Lik', track: 'General English', duration: '6 months', legacy: 'English-lik · 6 Mois', icon: 'book-marked' },
    { code: 'english_lik_12m', program: 'English-Lik', track: 'General English', duration: '12 months', legacy: 'English-lik · 12 Mois', icon: 'book-marked' },
    { code: 'everyday_english_3m', program: 'Everyday English', track: 'Speaking program', duration: '3 months', legacy: 'Everyday English · 3 Mois', icon: 'messages-square' },
    { code: 'everyday_english_6m', program: 'Everyday English', track: 'Speaking program', duration: '6 months', legacy: 'Everyday English · 6 Mois', icon: 'messages-square' },
    { code: 'one_on_one_ielts', program: 'One-on-one classes', track: 'IELTS & TOEFL prep', duration: '', legacy: 'One-on-one · IELTS & TOEFL', icon: 'award' },
    { code: 'one_on_one_general', program: 'One-on-one classes', track: 'General English', duration: '', legacy: 'One-on-one · General English', icon: 'user' },
    { code: 'one_on_one_esp', program: 'One-on-one classes', track: 'English for specific purposes', duration: '', legacy: 'One-on-one · English for specific purposes', icon: 'briefcase' },
    { code: 'one_on_one_business', program: 'One-on-one classes', track: 'Business English', duration: '', legacy: 'One-on-one · Business English', icon: 'briefcase' },
    { code: 'nfp', program: 'NFP', track: 'Native Fluency · native speaker', duration: '', legacy: 'NFP · Native Fluency Program', icon: 'globe' },
  ];

  function _tsRecommendationCode(slot) {
    const direct = String(slot?.recommendation_code || '');
    if (TS_ASSESSMENT_RECOMMENDATIONS.some(r => r.code === direct)) return direct;
    const legacy = String(slot?.teacher_recommendation || '').toLowerCase();
    if (/everyday/.test(legacy) && /(?:^|\D)3(?:\D|$)/.test(legacy)) return 'everyday_english_3m';
    if (/everyday/.test(legacy) && /(?:^|\D)6(?:\D|$)/.test(legacy)) return 'everyday_english_6m';
    if (/english.?lik/.test(legacy) && /(?:^|\D)12(?:\D|$)/.test(legacy)) return 'english_lik_12m';
    if (/english.?lik/.test(legacy) && /(?:^|\D)6(?:\D|$)/.test(legacy)) return 'english_lik_6m';
    return '';
  }

  // Multi-select resolver: the new recommendation_codes[] column is the source
  // of truth; fall back to the single legacy code for rows saved before multi.
  function _tsRecommendationCodes(slot) {
    const known = new Set(TS_ASSESSMENT_RECOMMENDATIONS.map(r => r.code));
    const arr = Array.isArray(slot?.recommendation_codes) ? slot.recommendation_codes : [];
    const codes = arr.map(c => String(c)).filter(c => known.has(c));
    if (codes.length) return Array.from(new Set(codes));
    const single = _tsRecommendationCode(slot);
    return single ? [single] : [];
  }

  function _tsHasSkillScores(slot) {
    return ['speaking_score', 'listening_score', 'reading_score', 'writing_score']
      .some(key => slot?.[key] !== null && slot?.[key] !== undefined && slot?.[key] !== '');
  }

  function _tsAssessmentReadiness(slot) {
    const scores = ['speaking_score', 'listening_score', 'reading_score', 'writing_score'];
    const validScores = scores.every(key => {
      const n = Number(slot?.[key]);
      return slot?.[key] !== null && slot?.[key] !== undefined && slot?.[key] !== '' && Number.isFinite(n) && n >= 0 && n <= 10;
    });
    return !!(validScores && /^(A0|A1|A2|B1|B2|C1|C2)$/.test(String(slot?.new_level || ''))
      && _tsRecommendationCode(slot) && String(slot?.notes || '').trim());
  }

  // The learner's own report for a LIVE test — the same document a website
  // test produces. `mode` is 'download' (a PDF stream) or 'send' (WhatsApps it
  // to the student). Deliberately two separate actions: nothing reaches a
  // client unless somebody chose to send it.
  async function _tsClientReport(slotId, mode, language, trigger) {
    if (!slotId) return;
    const lang = ['fr', 'en', 'ar'].includes(language) ? language : 'fr';
    const siblings = trigger?.closest('.tests-client-report')?.querySelectorAll('button') || [];
    siblings.forEach(b => { b.disabled = true; });
    const base = '/api/test-schedule/slots/' + encodeURIComponent(slotId) + '/client-report';
    try {
      if (mode === 'send') {
        toast('Envoi du rapport client…');
        const r = await api(base + '/send', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lang }),
          timeoutMs: 130000, // a cache miss renders the PDF before the send
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
        toast('Rapport client envoyé', 'success');
        return;
      }
      toast('Préparation du rapport client ' + lang.toUpperCase() + '…');
      const r = await api(base + '?lang=' + encodeURIComponent(lang), { timeoutMs: 130000 });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || ('HTTP ' + r.status));
      }
      const blob = await r.blob();
      const match = (r.headers.get('Content-Disposition') || '').match(/filename="?([^";]+)"?/i);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = match?.[1] || ('lougha-client-report-' + lang + '.pdf'); link.style.display = 'none';
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast('Rapport client téléchargé', 'success');
    } catch (err) {
      toast('Rapport client : ' + (err?.message || err), 'error');
    } finally {
      siblings.forEach(b => { b.disabled = false; });
    }
  }

  async function _tsDownloadAssessmentReport(slotOrId, language, trigger) {
    const slotId = typeof slotOrId === 'string' ? slotOrId : slotOrId?.id;
    if (!slotId) return;
    const lang = ['fr', 'en', 'ar'].includes(language) ? language : 'fr';
    const buttons = trigger?.closest('.tests-report-actions, .ts-report-langs')?.querySelectorAll('button') || [];
    buttons.forEach(btn => { btn.disabled = true; });
    const oldText = trigger?.textContent;
    if (trigger) trigger.textContent = '…';
    toast('Preparing the ' + lang.toUpperCase() + ' action plan…');
    try {
      const r = await api('/api/test-schedule/slots/' + encodeURIComponent(slotId) + '/report?lang=' + encodeURIComponent(lang), { timeoutMs: 130000 });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || ('HTTP ' + r.status));
      }
      const blob = await r.blob();
      const disposition = r.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const fileName = match?.[1] || ('lougha-action-plan-' + lang + '.pdf');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = fileName; link.style.display = 'none';
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast(lang.toUpperCase() + ' action plan downloaded', 'success');
      if (state.testsView?.inited) _testsViewLoad().catch(() => {});
    } catch (err) {
      toast('Could not generate the action plan: ' + (err?.message || err), 'error');
    } finally {
      if (trigger) trigger.textContent = oldText;
      buttons.forEach(btn => { btn.disabled = false; });
    }
  }

  // ============ Teacher "Mes tests" view (2026-06-08) ============
  // Card history of the students who came to THIS teacher. Reuses the Tests
  // helpers above. Server (/api/test-schedule/bookings) scopes to the teacher's
  // teacher_id and strips phone/email. Teacher-only tab (data-teacher-only).
  // ─── Teacher "Mes disponibilités" (2026-06-15, redesigned 06-16) ──────────
  // Built for non-technical teachers: a day-pill strip, a live summary, one-tap
  // half-day presets (Morning / Afternoon / Evening / All day), and a grid of
  // tappable time chips grouped by Matin / Après-midi / Soir. Blocked times
  // auto-vanish from the booking grid + the funnel bot (server enforces).
  // Both interactions: presets + precise range AND per-chip tap.
  function _taHm(t) { const [h, m] = String(t).split(':').map(Number); return h * 60 + (m || 0); }
  function _taPeriod(slotTime) {
    const h = Number(String(slotTime).slice(0, 2));
    if (h < 14) return 'matin';
    if (h < 18) return 'aprem';
    return 'soir';
  }
  const _TA_PERIODS = [
    { key: 'matin', label: 'Morning', preset: 'Morning', icon: 'sunrise' },
    { key: 'aprem', label: 'Afternoon', preset: 'Afternoon', icon: 'sun' },
    { key: 'soir',  label: 'Evening', preset: 'Evening', icon: 'sunset' },
  ];

  function ensureTeacherAvailabilityInit() {
    state.teacherAvail = state.teacherAvail || {
      inited: false, date: '', teacherId: null, canPick: false, canEdit: false,
      teachers: [], times: [], teacherName: '', busy: false,
    };
    const st = state.teacherAvail;
    if (st.inited) { _taLoad(); return; }
    st.inited = true;
    st.date = _testsTodayIso();
    document.getElementById('taRefreshBtn')?.addEventListener('click', () => _taLoad());
    document.getElementById('taTeacherSelect')?.addEventListener('change', (e) => { st.teacherId = e.target.value || null; _taLoad(); });
    _taLoad();
  }

  async function _taLoad() {
    const st = state.teacherAvail; if (!st) return;
    const loading = document.getElementById('taLoading');
    const empty = document.getElementById('taEmpty');
    const groups = document.getElementById('taGroups');
    if (loading) loading.classList.remove('hidden');
    if (empty) empty.classList.add('hidden');
    if (groups) clear(groups);
    try {
      const qs = new URLSearchParams({ date: st.date });
      if (st.teacherId) qs.set('teacher_id', st.teacherId);
      const r = await api('/api/test-schedule/availability-editor?' + qs.toString());
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      st.times = j.times || [];
      st.teacherName = (j.teacher && j.teacher.name) || '';
      st.teacherId = (j.teacher && j.teacher.id) || st.teacherId;
      st.canPick = !!j.canPickTeacher;
      st.canEdit = !!j.canEdit;
      st.teachers = j.teachers || [];
    } catch (err) {
      console.warn('[teacher-avail] load failed:', err && (err.message || err));
      st.times = [];
    } finally {
      if (loading) loading.classList.add('hidden');
      _taRender();
      if (st.canPick) { _taLoadHours(); _taLoadStats(); }
    }
  }

  // Horizontal day-pill strip: the next 12 days. Every weekday appears, Sunday
  // included (2026-09-12) — a day is off only where the teacher holds no hours,
  // which the Working hours panel above says plainly.
  function _taRenderDays() {
    const st = state.teacherAvail;
    const host = document.getElementById('taDays'); if (!host) return;
    clear(host);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const wd = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const today = _testsTodayIso();
    let iso = today;
    for (let added = 0; added < 12; added++) {
      const d = new Date(iso + 'T12:00:00Z');
      const isActive = iso === st.date;
      const isToday = iso === today;
      const thisIso = iso;
      const pill = el('button', {
        type: 'button', role: 'tab',
        class: 'ta-day' + (isActive ? ' is-active' : ''),
        'aria-selected': isActive ? 'true' : 'false',
      }, [
        el('span', { class: 'ta-day-wd', text: isToday ? 'Today' : wd[d.getUTCDay()] }),
        el('span', { class: 'ta-day-num', text: String(d.getUTCDate()) }),
        el('span', { class: 'ta-day-mo', text: months[d.getUTCMonth()] }),
      ]);
      pill.addEventListener('click', () => { if (st.date !== thisIso) { st.date = thisIso; _taLoad(); } });
      host.appendChild(pill);
      iso = _testsAddDays(iso, 1);
    }
  }

  function _taRender() {
    const st = state.teacherAvail; if (!st) return;
    // Admin-only teacher picker
    const wrap = document.getElementById('taTeacherWrap');
    const sel = document.getElementById('taTeacherSelect');
    if (wrap && sel) {
      wrap.classList.toggle('hidden', !st.canPick);
      if (st.canPick && sel.dataset.filled !== String(st.teachers.length)) {
        clear(sel);
        for (const t of st.teachers) sel.appendChild(el('option', { value: t.id, text: t.name }));
        sel.dataset.filled = String(st.teachers.length);
      }
      if (st.canPick && st.teacherId) sel.value = st.teacherId;
    }
    _taRenderDays();
    _taRenderHours();
    _taRenderStats();
    // Live summary
    const sum = document.getElementById('taSummary');
    if (sum) {
      clear(sum);
      sum.appendChild(el('span', { class: 'ta-sum-date', text: _testsFmtDate(st.date) + (st.canPick && st.teacherName ? ' · ' + st.teacherName : '') }));
      if (st.times.length) {
        const blockedCount = st.times.filter(s => s.blocked).length;
        sum.appendChild(blockedCount
          ? el('span', { class: 'ta-sum-pill ta-sum-blocked' }, [lucideIcon('lock'), el('span', { text: blockedCount + ' unavailable' })])
          : el('span', { class: 'ta-sum-pill ta-sum-ok' }, [lucideIcon('check'), el('span', { text: 'Available all day' })]));
      }
    }
    _taRenderQuick();
    const groups = document.getElementById('taGroups');
    const empty = document.getElementById('taEmpty');
    const legend = document.getElementById('taLegend');
    const quick = document.getElementById('taQuick');
    if (groups) clear(groups);
    if (!st.times.length) {
      if (empty) empty.classList.remove('hidden');
      if (legend) legend.classList.add('hidden');
      if (quick) quick.classList.add('hidden');
      refreshIcons(document.getElementById('teacherAvailabilityView'));
      return;
    }
    if (empty) empty.classList.add('hidden');
    if (legend) legend.classList.remove('hidden');
    if (quick) quick.classList.remove('hidden');
    for (const p of _TA_PERIODS) {
      const inP = st.times.filter(s => _taPeriod(s.slot_time) === p.key);
      if (!inP.length) continue;
      const group = el('div', { class: 'ta-group' });
      group.appendChild(el('div', { class: 'ta-group-head' }, [lucideIcon(p.icon), el('span', { text: p.label })]));
      const grid = el('div', { class: 'ta-grid' });
      for (const slot of inP) grid.appendChild(_taChip(slot));
      group.appendChild(grid);
      groups.appendChild(group);
    }
    refreshIcons(document.getElementById('teacherAvailabilityView'));
  }

  function _taChip(slot) {
    const st = state.teacherAvail;
    // A teacher sees their day but does not set it (administration does), so
    // their chips are plain, unclickable text — a disabled button would invite
    // the click and then refuse it.
    if (!st.canEdit) {
      return el('span', {
        class: 'ta-slot is-readonly' + (slot.blocked ? ' is-blocked' : '') + (slot.bookedSeats ? ' has-booking' : ''),
        title: slot.blocked ? 'Marked unavailable by administration' : (slot.bookedSeats ? slot.bookedSeats + ' booking(s)' : 'Available'),
      }, [
        lucideIcon(slot.blocked ? 'lock' : 'clock'),
        document.createTextNode(slot.time),
        ...(slot.bookedSeats ? [el('span', { class: 'ta-slot-booked', text: String(slot.bookedSeats) })] : []),
      ]);
    }
    const chip = el('button', {
      type: 'button',
      class: 'ta-slot' + (slot.blocked ? ' is-blocked' : '') + (slot.bookedSeats ? ' has-booking' : ''),
      'aria-pressed': slot.blocked ? 'true' : 'false',
      title: slot.blocked
        ? 'Unavailable — tap to reopen'
        : (slot.bookedSeats
          ? (slot.bookedSeats + ' booking(s) — blocking hides the remaining free seats; the booking stays')
          : 'Tap to close this slot for this teacher'),
    }, [
      lucideIcon(slot.blocked ? 'lock' : 'clock'),
      document.createTextNode(slot.time),
    ]);
    if (slot.bookedSeats) chip.appendChild(el('span', { class: 'ta-slot-booked', text: String(slot.bookedSeats) }));
    chip.addEventListener('click', () => _taBulk([slot.slot_time], slot.blocked ? 'unblock' : 'block'));
    return chip;
  }

  function _taRenderQuick() {
    const st = state.teacherAvail;
    const host = document.getElementById('taQuick'); if (!host) return;
    clear(host);
    if (!st.times.length) return;
    // Read-only for a teacher: say who owns these hours and where a real
    // absence goes, instead of leaving them looking for a button.
    if (!st.canEdit) {
      host.appendChild(el('div', { class: 'ta-readonly-note' }, [
        lucideIcon('info'),
        el('span', { text: "Vos horaires sont fixés par l'administration. Pour une absence, faites une demande de congé (Vacation)." }),
      ]));
      return;
    }
    // Half-day preset toggles (block if not all blocked, else unblock).
    const presetRow = el('div', { class: 'ta-presets' });
    for (const p of _TA_PERIODS) {
      const inP = st.times.filter(s => _taPeriod(s.slot_time) === p.key);
      if (!inP.length) continue;
      const on = inP.every(s => s.blocked);
      const btn = el('button', { type: 'button', class: 'ta-preset' + (on ? ' is-on' : '') },
        [lucideIcon(on ? 'lock' : p.icon), el('span', { text: p.preset })]);
      btn.addEventListener('click', () => _taBulk(inP.map(s => s.slot_time), on ? 'unblock' : 'block'));
      presetRow.appendChild(btn);
    }
    const allOn = st.times.every(s => s.blocked);
    const dayBtn = el('button', { type: 'button', class: 'ta-preset ta-preset-day' + (allOn ? ' is-on' : '') },
      [lucideIcon(allOn ? 'calendar-check' : 'calendar-x'), el('span', { text: allOn ? 'Reopen day' : 'All day' })]);
    dayBtn.addEventListener('click', () => _taBulk(st.times.map(s => s.slot_time), allOn ? 'unblock' : 'block'));
    presetRow.appendChild(dayBtn);
    host.appendChild(presetRow);

    // Precise range (secondary): de [..] → [..] Bloquer.
    const fromSel = el('select', { class: 'input ta-range-sel' });
    const toSel = el('select', { class: 'input ta-range-sel' });
    for (const s of st.times) {
      fromSel.appendChild(el('option', { value: s.slot_time, text: s.time }));
      toSel.appendChild(el('option', { value: s.slot_time, text: s.time }));
    }
    toSel.value = st.times[st.times.length - 1].slot_time;
    const rangeBtn = el('button', { type: 'button', class: 'btn btn-sm btn-ghost ta-range-btn' }, [lucideIcon('lock'), el('span', { text: 'Block' })]);
    rangeBtn.addEventListener('click', () => {
      const a = _taHm(fromSel.value), b = _taHm(toSel.value);
      if (b < a) { toast('End time is before start', 'error'); return; }
      const times = st.times.filter(s => { const m = _taHm(s.slot_time); return m >= a && m <= b; }).map(s => s.slot_time);
      _taBulk(times, 'block');
    });
    host.appendChild(el('div', { class: 'ta-range' }, [
      el('span', { class: 'ta-range-lbl', text: 'Custom range' }),
      fromSel, el('span', { class: 'ta-range-sep', text: '→' }), toSel, rangeBtn,
    ]));
  }

  async function _taBulk(times, action) {
    const st = state.teacherAvail;
    if (!st || st.busy || !Array.isArray(times) || !times.length) return;
    st.busy = true;
    try {
      const body = { date: st.date, times, action };
      if (st.canPick && st.teacherId) body.teacher_id = st.teacherId;
      const r = await api('/api/test-schedule/unavailability', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (r.status === 503) { const j = await r.json().catch(() => ({})); toast(j.error || 'Server migration 0029 required', 'error'); return; }
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + r.status)); }
      const j = await r.json();
      st.times = j.times || st.times;
      _taRender();
      if (j.locked) toast(j.locked + ' slot(s) locked — only an admin can reopen', 'error');
      if (st.canPick) _taLoadStats(); // refresh admin counters/log after a change
    } catch (err) {
      toast(String((err && err.message) || err), 'error');
    } finally {
      st.busy = false;
    }
  }

  // ─── Working hours (2026-07-27) ──────────────────────────────────────────
  // The ROSTER: which days and hours this teacher is available at all. The grid
  // below is day-to-day exceptions INSIDE those hours, which is why this sits
  // above it and reads as a settings panel rather than another calendar.
  //
  // Before this, hours lived in a code constant: giving a new teacher their
  // times meant an engineer and a deploy, and Morad sat with zero availability
  // waiting for one. Managers only.
  const TA_DAYS = [[1, 'Monday'], [2, 'Tuesday'], [3, 'Wednesday'], [4, 'Thursday'], [5, 'Friday'], [6, 'Saturday'], [7, 'Sunday']];
  const TA_TIMES = (() => {
    const out = [];
    for (let m = 10 * 60; m <= 21 * 60; m += 15) {
      out.push(String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0'));
    }
    return out;
  })();

  function _taHoursState() {
    const st = state.teacherAvail;
    st.hours = st.hours || { draft: null, saved: null, open: false, busy: false };
    return st.hours;
  }

  function _taTimeSelect(value, onChange, { className = '' } = {}) {
    const sel = el('select', { class: 'input ta-hours-time ' + className });
    for (const t of TA_TIMES) sel.appendChild(el('option', { value: t, text: t }));
    sel.value = value;
    sel.addEventListener('change', () => onChange(sel.value));
    return sel;
  }

  function _taRenderHours() {
    const st = state.teacherAvail;
    const host = document.getElementById('taHours');
    if (!host) return;
    if (!st.canPick || !st.teacherId) { host.classList.add('hidden'); clear(host); return; }
    const h = _taHoursState();
    host.classList.remove('hidden');
    clear(host);

    // Header — collapsed by default so the day grid stays the focus; the
    // summary line is what a manager reads 90% of the time.
    const draft = h.draft || {};
    const openDays = TA_DAYS.filter(([d]) => draft[d]);
    const head = el('div', { class: 'ta-hours-head' });
    const title = el('div', { class: 'ta-hours-title' }, [
      lucideIcon('calendar-clock'),
      el('span', { text: 'Working hours' }),
    ]);
    const summary = el('div', {
      class: 'ta-hours-sum' + (openDays.length ? '' : ' ta-hours-sum--none'),
      text: openDays.length
        ? openDays.map(([d, name]) => `${name.slice(0, 3)} ${draft[d].start}–${draft[d].end}`).join('  ·  ')
        : 'No hours set — this teacher is not offered for any test.',
    });
    const toggle = el('button', {
      type: 'button', class: 'btn btn-sm ta-hours-toggle',
      text: h.open ? 'Close' : (openDays.length ? 'Edit' : 'Set hours'),
    });
    toggle.addEventListener('click', () => { h.open = !h.open; _taRenderHours(); });
    head.appendChild(title);
    head.appendChild(summary);
    head.appendChild(toggle);
    host.appendChild(head);
    if (!h.open) return;

    const body = el('div', { class: 'ta-hours-body' });
    for (const [dow, label] of TA_DAYS) {
      const win = draft[dow] || null;
      const row = el('div', { class: 'ta-hours-row' + (win ? ' is-on' : '') });
      const onBtn = el('button', {
        type: 'button',
        class: 'ta-hours-day' + (win ? ' is-on' : ''),
        'aria-pressed': win ? 'true' : 'false',
        text: label,
      });
      onBtn.addEventListener('click', () => {
        // A day switched on starts on the institute's usual window rather than
        // empty — one click for the common case, still editable.
        if (draft[dow]) delete draft[dow];
        else draft[dow] = { start: '10:00', end: '21:00' };
        h.draft = draft;
        _taRenderHours();
      });
      row.appendChild(onBtn);
      if (win) {
        row.appendChild(el('span', { class: 'ta-hours-lbl', text: 'from' }));
        row.appendChild(_taTimeSelect(win.start, (v) => { win.start = v; _taRenderHours(); }));
        row.appendChild(el('span', { class: 'ta-hours-lbl', text: 'to' }));
        row.appendChild(_taTimeSelect(win.end, (v) => { win.end = v; _taRenderHours(); }));
        // The break is optional and most teachers have one (14:00–15:00), so
        // it is offered inline rather than hidden behind another screen.
        const hasBrk = Array.isArray(win.brk);
        const brkBtn = el('button', {
          type: 'button', class: 'ta-hours-brk' + (hasBrk ? ' is-on' : ''),
          text: hasBrk ? 'Break' : '+ Break',
        });
        brkBtn.addEventListener('click', () => {
          if (hasBrk) delete win.brk; else win.brk = ['14:00', '15:00'];
          _taRenderHours();
        });
        row.appendChild(brkBtn);
        if (hasBrk) {
          row.appendChild(_taTimeSelect(win.brk[0], (v) => { win.brk[0] = v; _taRenderHours(); }, { className: 'ta-hours-time--brk' }));
          row.appendChild(el('span', { class: 'ta-hours-lbl', text: '→' }));
          row.appendChild(_taTimeSelect(win.brk[1], (v) => { win.brk[1] = v; _taRenderHours(); }, { className: 'ta-hours-time--brk' }));
        }
        if (_hmLocal(win.start) >= _hmLocal(win.end)) {
          row.appendChild(el('span', { class: 'ta-hours-err', text: 'End must be after start' }));
        }
      } else {
        row.appendChild(el('span', { class: 'ta-hours-off', text: 'Day off' }));
      }
      body.appendChild(row);
    }
    host.appendChild(body);

    const foot = el('div', { class: 'ta-hours-foot' });
    const dirty = JSON.stringify(draft) !== JSON.stringify(h.saved || {});
    const bad = TA_DAYS.some(([d]) => draft[d] && _hmLocal(draft[d].start) >= _hmLocal(draft[d].end));
    const save = el('button', { type: 'button', class: 'btn btn-primary btn-sm', text: h.busy ? 'Saving…' : 'Save hours' });
    save.disabled = h.busy || !dirty || bad;
    save.addEventListener('click', () => _taSaveHours());
    const reset = el('button', { type: 'button', class: 'btn btn-sm', text: 'Cancel' });
    reset.disabled = h.busy || !dirty;
    reset.addEventListener('click', () => { h.draft = JSON.parse(JSON.stringify(h.saved || {})); _taRenderHours(); });
    foot.appendChild(el('span', {
      class: 'ta-hours-note',
      text: 'Applies to the booking grid, the sales team and the bot straight away.',
    }));
    foot.appendChild(reset);
    foot.appendChild(save);
    host.appendChild(foot);
  }

  function _hmLocal(t) { const [a, b] = String(t).split(':').map(Number); return a * 60 + b; }

  async function _taLoadHours() {
    const st = state.teacherAvail;
    if (!st?.canPick) return;
    try {
      const r = await api('/api/test-schedule/working-hours');
      if (!r.ok) return;
      const j = await r.json();
      const mine = (j.teachers || []).find(t => String(t.id) === String(st.teacherId));
      const h = _taHoursState();
      h.saved = mine?.working_hours ? JSON.parse(JSON.stringify(mine.working_hours)) : {};
      h.draft = JSON.parse(JSON.stringify(h.saved));
      _taRenderHours();
    } catch (_) { /* the panel is additive — never block the page */ }
  }

  async function _taSaveHours({ force = false } = {}) {
    const st = state.teacherAvail;
    const h = _taHoursState();
    h.busy = true; _taRenderHours();
    try {
      const r = await api('/api/test-schedule/working-hours', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teacher_id: st.teacherId, hours: h.draft || {}, force }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409 && Array.isArray(j.conflicts)) {
        // Narrowing hours can strand a client who already has an appointment.
        // Name them, then let the manager decide — never silently.
        const lines = j.conflicts.slice(0, 8)
          .map(c => `· ${c.slot_date} ${c.slot_time}${c.student_name ? ' — ' + c.student_name : ''}`).join('\n');
        const more = j.conflicts.length > 8 ? `\n… and ${j.conflicts.length - 8} more` : '';
        h.busy = false;
        if (!confirm(`${j.conflicts.length} test(s) already booked fall outside these hours:\n\n${lines}${more}\n\nSave anyway? The bookings stay — you will need to move them.`)) {
          _taRenderHours();
          return;
        }
        return _taSaveHours({ force: true });
      }
      if (!r.ok) throw new Error(j?.error || ('HTTP ' + r.status));
      h.saved = JSON.parse(JSON.stringify(h.draft || {}));
      h.busy = false;
      toast(j.days?.length ? `Hours saved — ${j.days.length} day(s) open` : 'Hours saved — no day open', 'success');
      _taLoad();          // the grid below is derived from these hours
    } catch (err) {
      h.busy = false;
      _taRenderHours();
      toast(String((err && err.message) || err), 'error');
    }
  }

  // Admin-only accountability panel: per-teacher blocked-slot counts + recent log.
  async function _taLoadStats() {
    const st = state.teacherAvail; if (!st || !st.canPick) return;
    try {
      const r = await api('/api/test-schedule/unavailability/stats');
      if (r.ok) { st.stats = await r.json(); _taRenderStats(); }
    } catch (_) { /* stats are advisory — never block the page */ }
  }

  function _taRenderStats() {
    const st = state.teacherAvail;
    const host = document.getElementById('taAdminStats'); if (!host) return;
    if (!st.canPick || !st.stats) { host.classList.add('hidden'); clear(host); return; }
    clear(host);
    host.classList.remove('hidden');
    const statsOwner = state.me?.teacherManager === true ? 'manager' : 'admin';
    host.appendChild(el('div', { class: 'ta-stats-head' }, [lucideIcon('bar-chart-3'), el('span', { text: 'Teacher unavailability (' + statsOwner + ')' })]));
    const counters = el('div', { class: 'ta-counters' });
    for (const c of (st.stats.counters || [])) {
      counters.appendChild(el('div', { class: 'ta-counter' }, [
        el('div', { class: 'ta-counter-name', text: c.name }),
        el('div', { class: 'ta-counter-nums' }, [
          el('span', { class: 'ta-counter-num', text: 'Today ' + (c.today || 0) }),
          el('span', { class: 'ta-counter-num ta-counter-month', text: 'Month ' + (c.month || 0) }),
        ]),
      ]));
    }
    host.appendChild(counters);
    // Log grouped by teacher + date (rows arrive newest-first).
    const groups = new Map();
    for (const r of (st.stats.log || [])) {
      const key = r.teacher_id + '|' + String(r.block_date).slice(0, 10);
      let g = groups.get(key);
      if (!g) { g = { name: r.name, date: String(r.block_date).slice(0, 10), times: [], by: r.created_by }; groups.set(key, g); }
      g.times.push(String(r.slot_time).slice(0, 5));
    }
    const log = el('div', { class: 'ta-log' });
    log.appendChild(el('div', { class: 'ta-log-title', text: 'Recent blocks (newest first)' }));
    const arr = Array.from(groups.values());
    if (!arr.length) log.appendChild(el('div', { class: 'ta-log-empty', text: 'No blocks yet.' }));
    for (const g of arr.slice(0, 40)) {
      g.times.sort();
      const range = g.times.length > 1 ? (g.times[0] + '–' + g.times[g.times.length - 1]) : g.times[0];
      log.appendChild(el('div', { class: 'ta-log-row' }, [
        el('span', { class: 'ta-log-who', text: g.name }),
        el('span', { class: 'ta-log-date', text: _testsFmtDate(g.date) }),
        el('span', { class: 'ta-log-times', text: g.times.length + ' slots · ' + range }),
        el('span', { class: 'ta-log-by', text: 'by ' + userLabel(g.by || '?') }),
      ]));
    }
    host.appendChild(log);
    refreshIcons(host);
  }

  // ─── Sales availability requests (migration 0056) ─────────────────────────
  // Sales asks for up to five preferred date/time options from the chat header.
  // Managers process the shared queue; Sales keeps a durable answer/history.
  function _arCanCreateClient() {
    return ['agent', 'super_sales', 'admin', 'super_admin'].includes(String(state.me?.role || '').toLowerCase());
  }
  function _arCanReviewClient() {
    return _isSalesAdmin() || state.me?.teacherManager === true;
  }
  function _arFmtDate(value) {
    const iso = String(value || '').slice(0, 10);
    return iso ? _testsFmtDate(iso) : '—';
  }
  function _arWeekday(value) {
    const iso = String(value || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return 'Choose a date';
    try {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        timeZone: 'UTC',
      }).format(new Date(iso + 'T00:00:00Z'));
    } catch (_) {
      return 'Choose a date';
    }
  }
  function _arFmtOptionDate(value) {
    const iso = String(value || '').slice(0, 10);
    return iso ? (_arWeekday(iso) + ' ' + _arFmtDate(iso)) : '—';
  }
  function _arFmtTime(value) { return String(value || '').slice(0, 5) || '—'; }
  function _arStatusLabel(status) {
    return ({ new: 'New', in_process: 'In process', done: 'Done', rejected: 'Rejected', cancelled: 'Cancelled' })[status] || status;
  }
  function _arStatusPill(status) {
    return el('span', { class: 'ar-pill ar-pill-' + status, text: _arStatusLabel(status) });
  }
  function _arWindows(req) {
    return Array.isArray(req?.requested_windows) ? req.requested_windows : [];
  }
  function _arResolutionSuggestions(req) {
    const rows = Array.isArray(req?.resolution_suggestions)
      ? req.resolution_suggestions
      : [];
    if (rows.length) return rows;
    if (req?.resolution_date && req?.resolution_time) {
      return [{ date: req.resolution_date, time: req.resolution_time }];
    }
    return [];
  }
  function _arSetBadge(value) {
    const n = Math.max(0, Number(value) || 0);
    for (const id of ['availabilityRequestsTabBadge', 'availabilityRequestHeaderBadge']) {
      const badge = document.getElementById(id);
      if (!badge) continue;
      if (n > 0) {
        badge.textContent = n > 99 ? '99+' : String(n);
        badge.classList.remove('hidden');
      } else {
        badge.textContent = '';
        badge.classList.add('hidden');
      }
    }
    try { refreshGroupBadges(); } catch (_) { /* advisory */ }
  }
  async function _arRefreshBadge() {
    if (!(_arCanCreateClient() || _arCanReviewClient())) { _arSetBadge(0); return; }
    try {
      const r = await api('/api/test-schedule/availability-requests/summary');
      if (!r.ok) return;
      const j = await r.json();
      _arSetBadge(j.summary?.actionCount || 0);
    } catch (_) { /* advisory */ }
  }

  function _arState() {
    const st = state.availabilityRequests || (state.availabilityRequests = {});
    const defaults = {
      inited: false, requests: [], summary: {}, canReview: false, canCreate: false,
      filter: 'active', filterInitialized: false, busy: false, teachers: [],
      requestContext: null, resolutionRequest: null, resolutionMode: 'done',
      controlsInited: false,
    };
    for (const [key, value] of Object.entries(defaults)) {
      if (!(key in st)) st[key] = value;
    }
    return st;
  }

  function _arEnsureControlsInit() {
    const st = _arState();
    if (st.controlsInited) return st;
    st.controlsInited = true;
    document.getElementById('arAddWindowBtn')?.addEventListener('click', () => _arAddWindowRow());
    document.getElementById('arAddSuggestionBtn')?.addEventListener('click', () => _arAddSuggestionRow());
    document.getElementById('arSubmitBtn')?.addEventListener('click', () => _arSubmit());
    document.getElementById('arResolutionConfirmBtn')?.addEventListener('click', () => _arConfirmResolution());
    document.getElementById('arResolutionDate')?.addEventListener('input', event => {
      _arSetResolutionWeekday(event.target?.value);
    });
    _arPopulateHalfHourSelect(document.getElementById('arResolutionTime'));
    return st;
  }

  function ensureAvailabilityRequestsInit() {
    const st = _arEnsureControlsInit();
    if (!st.inited) {
      st.inited = true;
      document.getElementById('arRefreshBtn')?.addEventListener('click', () => _arLoad());
      document.getElementById('arNewBtn')?.addEventListener('click', () => openAvailabilityRequestModal());
      document.getElementById('arFilters')?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ar-filter]');
        if (!button) return;
        st.filter = button.dataset.arFilter || 'active';
        st.filterInitialized = true;
        _arRender();
      });
    }
    _arLoad();
  }

  async function _arLoad() {
    const st = state.availabilityRequests || (state.availabilityRequests = {});
    const loading = document.getElementById('arLoading');
    loading?.classList.remove('hidden');
    try {
      const r = await api('/api/test-schedule/availability-requests');
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      st.requests = j.requests || [];
      st.summary = j.summary || {};
      st.canReview = !!j.canReview;
      st.canCreate = !!j.canCreate;
      if (!st.filterInitialized) {
        st.filter = st.canReview ? 'active' : 'all';
        st.filterInitialized = true;
      }
      _arSetBadge(st.summary.actionCount || 0);
      _arRender();
      // Rendering happens first, so the blue "new answer" card is visible in
      // this visit. Then acknowledge the badge without deleting the answer.
      if (!st.canReview && (st.summary.unseenDecisions || 0) > 0) {
        api('/api/test-schedule/availability-requests/seen', { method: 'POST' })
          .then(() => _arSetBadge(0))
          .catch(() => {});
      }
    } catch (err) {
      console.warn('[availability-requests] load failed:', err?.message || err);
      const list = document.getElementById('arList');
      if (list) {
        clear(list);
        list.appendChild(el('div', { class: 'ts-book-error', text: String(err?.message || err) }));
      }
    } finally {
      loading?.classList.add('hidden');
    }
  }

  function _arRenderStats() {
    const st = state.availabilityRequests;
    const host = document.getElementById('arStats');
    if (!host || !st) return;
    clear(host);
    const summary = st.summary || {};
    const stats = [
      ['new', 'New', summary.new || 0, 'inbox', 'ar-stat-new'],
      ['in_process', 'In process', summary.in_process || 0, 'loader-circle', 'ar-stat-process'],
      ['done', 'Done', summary.done || 0, 'circle-check', 'ar-stat-done'],
      ['rejected', 'Rejected', summary.rejected || 0, 'circle-x', 'ar-stat-rejected'],
    ];
    for (const [, label, value, icon, cls] of stats) {
      host.appendChild(el('div', { class: 'ar-stat ' + cls }, [
        el('span', { class: 'ar-stat-icon' }, [lucideIcon(icon)]),
        el('span', {}, [
          el('span', { class: 'ar-stat-value', text: String(value) }),
          el('span', { class: 'ar-stat-label', text: label }),
        ]),
      ]));
    }
  }

  function _arFilteredRows() {
    const st = state.availabilityRequests;
    const rows = st?.requests || [];
    if (!st || st.filter === 'all') return rows;
    if (st.filter === 'active') return rows.filter(row => row.status === 'new' || row.status === 'in_process');
    return rows.filter(row => row.status === st.filter);
  }

  function _arRender() {
    const st = state.availabilityRequests;
    if (!st) return;
    const subtitle = document.getElementById('arSubtitle');
    if (subtitle) {
      subtitle.textContent = st.canReview
        ? 'Process Sales requests and send back the exact teacher, date and time — or explain that none was found.'
        : 'Your requests and the test team’s replies stay here until you need them.';
    }
    document.getElementById('arNewBtn')?.classList.toggle('hidden', !st.canCreate);
    _arRenderStats();
    const counts = {
      active: (st.summary.new || 0) + (st.summary.in_process || 0),
      new: st.summary.new || 0,
      in_process: st.summary.in_process || 0,
      done: st.summary.done || 0,
      rejected: st.summary.rejected || 0,
    };
    document.querySelectorAll('#arFilters [data-ar-filter]').forEach(button => {
      button.classList.toggle('is-active', button.dataset.arFilter === st.filter);
      const count = button.querySelector('[data-ar-count]');
      if (count) count.textContent = String(counts[button.dataset.arFilter] || 0);
    });
    const list = document.getElementById('arList');
    const empty = document.getElementById('arEmpty');
    if (!list || !empty) return;
    clear(list);
    const rows = _arFilteredRows();
    empty.classList.toggle('hidden', rows.length > 0);
    for (const req of rows) list.appendChild(_arCard(req));
    refreshIcons(document.getElementById('availabilityRequestsView'));
  }

  function _arWindowChips(req) {
    const host = el('div', { class: 'ar-window-chips' });
    for (const option of _arWindows(req)) {
      host.appendChild(el('span', { class: 'ar-window-chip' }, [
        lucideIcon('calendar-clock'),
        document.createTextNode(_arFmtOptionDate(option.date) + ' · ' + _arFmtTime(option.time)),
      ]));
    }
    return host;
  }

  function _arTeamNote(note) {
    if (!note) return null;
    return el('span', { class: 'ar-team-note' }, [
      el('b', { text: 'Team note: ' }),
      document.createTextNode(note),
    ]);
  }

  function _arCard(req) {
    const unseen = !state.availabilityRequests?.canReview
      && (req.status === 'done' || req.status === 'rejected')
      && !req.requester_seen_at;
    const clientName = req.client_name || req.client_phone || 'Client';
    const card = el('article', { class: 'ar-card ar-card-' + req.status + (unseen ? ' is-unseen' : '') }, [
      el('div', { class: 'ar-card-head' }, [
        el('div', { class: 'ar-card-client' }, [
          el('strong', { text: clientName }),
          el('span', { text: req.client_phone || ('Request #' + String(req.id || '').slice(0, 8)) }),
        ]),
        _arStatusPill(req.status),
      ]),
      state.availabilityRequests?.canReview
        ? el('div', { class: 'ar-requested-by' }, [
          lucideIcon('user-round'),
          el('span', { text: 'Requested by ' + userLabel(req.requested_by, req.requester_display_name || req.requested_by || 'Sales') }),
        ])
        : el('span'),
      _arWindowChips(req),
    ]);
    if (req.request_note) card.appendChild(el('div', { class: 'ar-card-note', text: req.request_note }));
    if (req.status === 'done') {
      card.appendChild(el('div', { class: 'ar-result ar-result-done' }, [
        el('strong', {}, [lucideIcon('circle-check'), document.createTextNode('Availability found')]),
        el('span', { text: [
          req.resolution_teacher_name || 'Teacher',
          _arFmtOptionDate(req.resolution_date),
          _arFmtTime(req.resolution_time),
        ].join(' · ') }),
        _arTeamNote(req.resolution_note) || el('span'),
      ]));
    }
    if (req.status === 'rejected') {
      const result = el('div', { class: 'ar-result ar-result-rejected' }, [
        el('strong', {}, [lucideIcon('circle-x'), document.createTextNode('Requested times unavailable')]),
      ]);
      const teamNote = _arTeamNote(req.resolution_note);
      if (teamNote) result.appendChild(teamNote);
      const suggestions = _arResolutionSuggestions(req);
      suggestions.forEach((suggestion, index) => {
        result.appendChild(el('div', { class: 'ar-suggestion-result' }, [
          lucideIcon('calendar-plus'),
          el('span', {}, [
            el('b', {
              text: suggestions.length > 1
                ? 'Suggested alternative ' + (index + 1)
                : 'Suggested alternative',
            }),
            el('span', {
              text: _arFmtOptionDate(suggestion.date) + ' · ' + _arFmtTime(suggestion.time),
            }),
          ]),
        ]));
      });
      if (!suggestions.length && !teamNote) {
        result.appendChild(el('span', { text: 'The test team could not find a matching time.' }));
      }
      card.appendChild(result);
    }
    card.appendChild(el('div', { class: 'ar-card-meta', text: 'Created ' + _arFmtDate(req.created_at) + (req.assigned_to ? ' · handled by ' + req.assigned_to : '') }));
    const actions = el('div', { class: 'ar-card-actions' });
    if (state.availabilityRequests?.canReview && req.status === 'new') {
      const start = el('button', { type: 'button', class: 'btn btn-sm ar-start-btn' }, [lucideIcon('play'), el('span', { text: 'Start processing' })]);
      start.addEventListener('click', () => _arStart(req.id, start));
      actions.appendChild(start);
    }
    if (state.availabilityRequests?.canReview && (req.status === 'new' || req.status === 'in_process')) {
      const found = el('button', { type: 'button', class: 'btn btn-sm ar-found-btn' }, [lucideIcon('calendar-check'), el('span', { text: 'Found' })]);
      const reject = el('button', { type: 'button', class: 'btn btn-sm btn-ghost ar-reject-btn' }, [lucideIcon('calendar-x'), el('span', { text: 'Not found' })]);
      found.addEventListener('click', () => _arOpenResolution(req, 'done'));
      reject.addEventListener('click', () => _arOpenResolution(req, 'reject'));
      actions.append(found, reject);
    }
    if (!state.availabilityRequests?.canReview && req.status === 'new') {
      const cancel = el('button', { type: 'button', class: 'btn btn-sm btn-ghost' }, [lucideIcon('x'), el('span', { text: 'Cancel request' })]);
      cancel.addEventListener('click', () => _arCancel(req.id, cancel));
      actions.appendChild(cancel);
    }
    if (req.chat_id && state.me?.role !== 'teacher') {
      const open = el('button', { type: 'button', class: 'btn btn-sm btn-ghost' }, [lucideIcon('message-circle'), el('span', { text: 'Open client' })]);
      open.addEventListener('click', () => {
        showView('inbox');
        setTimeout(() => selectConversation(req.chat_id), 80);
      });
      actions.appendChild(open);
    }
    if (actions.childNodes.length) card.appendChild(actions);
    return card;
  }

  function _arRenumberWindowRows() {
    document.querySelectorAll('#arRequestWindows .ar-window-row').forEach((row, index) => {
      const number = row.querySelector('.ar-window-number');
      if (number) number.textContent = 'Option ' + (index + 1);
    });
  }

  function _arSetResolutionWeekday(value) {
    const weekday = document.getElementById('arResolutionWeekday');
    if (weekday) weekday.textContent = _arWeekday(value);
  }

  function _arPopulateHalfHourSelect(select, value = '') {
    if (!select) return;
    clear(select);
    select.appendChild(el('option', { value: '', text: 'Choose time' }));
    for (let hour = 0; hour < 24; hour++) {
      for (const minute of ['00', '30']) {
        const option = String(hour).padStart(2, '0') + ':' + minute;
        select.appendChild(el('option', { value: option, text: option }));
      }
    }
    select.value = /^([01]\d|2[0-3]):(00|30)/.test(String(value || ''))
      ? String(value).slice(0, 5)
      : '';
  }

  function _arAddSuggestionRow(value = {}) {
    const host = document.getElementById('arSuggestionRows');
    if (!host) return;
    if (host.children.length >= 5) { toast('Maximum 5 suggestions', 'error'); return; }
    const date = el('input', {
      class: 'input ar-suggestion-date', type: 'date', min: _testsTodayIso(),
      value: value.date || '', 'aria-label': 'Suggested date',
    });
    const time = el('select', {
      class: 'input ar-suggestion-time', 'aria-label': 'Suggested time',
    });
    _arPopulateHalfHourSelect(time, value.time || '');
    const weekday = el('span', {
      class: 'ar-resolution-weekday',
      text: _arWeekday(date.value),
    });
    date.addEventListener('input', () => { weekday.textContent = _arWeekday(date.value); });
    const remove = el('button', {
      class: 'icon-btn-sm ar-suggestion-remove', type: 'button',
      title: 'Remove suggestion', 'aria-label': 'Remove suggestion',
    }, [lucideIcon('x')]);
    const row = el('div', { class: 'ar-suggestion-row' }, [
      el('div', {}, [
        el('div', { class: 'ar-resolution-label-row' }, [
          el('span', { class: 'form-label', text: 'Suggested date' }),
          weekday,
        ]),
        date,
      ]),
      el('label', {}, [
        el('span', { class: 'form-label', text: 'Suggested time' }),
        time,
      ]),
      remove,
    ]);
    remove.addEventListener('click', () => row.remove());
    host.appendChild(row);
    refreshIcons(row);
  }

  function _arAddWindowRow(value = {}) {
    const host = document.getElementById('arRequestWindows');
    if (!host) return;
    if (host.children.length >= 5) { toast('Maximum 5 options', 'error'); return; }
    const date = el('input', {
      class: 'input ar-window-date', type: 'date', min: _testsTodayIso(),
      value: value.date || '', 'aria-label': 'Exact date proposed by the client',
    });
    const time = el('select', {
      class: 'input ar-window-time', 'aria-label': 'Time proposed by the client',
    });
    _arPopulateHalfHourSelect(time, value.time || '');
    const weekday = el('strong', { class: 'ar-window-weekday', text: _arWeekday(date.value) });
    const dateField = el('label', { class: 'ar-window-field ar-window-date-field' }, [
      el('span', { class: 'ar-window-field-head' }, [
        el('span', { class: 'ar-window-field-label', text: 'Day & exact date' }),
        weekday,
      ]),
      date,
    ]);
    const timeField = el('label', { class: 'ar-window-field ar-window-time-field' }, [
      el('span', { class: 'ar-window-field-head' }, [
        el('span', { class: 'ar-window-field-label', text: 'Time proposed by client' }),
        el('span', { class: 'ar-window-field-hint', text: 'Exact time' }),
      ]),
      time,
    ]);
    date.addEventListener('input', () => { weekday.textContent = _arWeekday(date.value); });
    const remove = el('button', { class: 'icon-btn-sm ar-window-remove', type: 'button', title: 'Remove option' }, [lucideIcon('x')]);
    const row = el('div', { class: 'ar-window-row' }, [
      el('span', { class: 'ar-window-number' }),
      dateField,
      timeField,
      remove,
    ]);
    remove.addEventListener('click', () => {
      if (host.children.length <= 1) { toast('Keep at least one option', 'error'); return; }
      row.remove();
      _arRenumberWindowRows();
    });
    host.appendChild(row);
    _arRenumberWindowRows();
    refreshIcons(row);
  }

  async function openAvailabilityRequestModal() {
    if (!_arCanCreateClient()) { toast('Sales only', 'error'); return; }
    const chat = state.selectedChat;
    const chatId = state.selectedChatId || chat?.id;
    if (!chatId || !chat || chat.isGroup) { toast('Open a client conversation first', 'error'); return; }
    // The header icon can open this modal before the Availability requests
    // page has ever been visited. Bind its controls here so Add another option
    // and Send request always work directly from the active client chat.
    const st = _arEnsureControlsInit();
    const lead = await fetchLeadForChat(chatId, { useCache: true }).catch(() => null);
    st.requestContext = {
      lead,
      chatId,
      clientName: lead?.name || chat.name || chat.phone || 'Client',
      clientPhone: lead?.phone || pipelineExtractPhone(chat) || '',
    };
    const client = document.getElementById('arRequestClient');
    if (client) {
      clear(client);
      const label = st.requestContext.clientName;
      client.append(
        el('span', { class: 'ar-client-avatar', text: String(label || '?').charAt(0).toUpperCase() }),
        el('span', { class: 'ar-client-copy' }, [
          el('strong', { text: label }),
          el('span', { text: st.requestContext.clientPhone || 'Current conversation' }),
        ]),
      );
    }
    const windows = document.getElementById('arRequestWindows');
    if (windows) { clear(windows); _arAddWindowRow(); }
    const note = document.getElementById('arRequestNote'); if (note) note.value = '';
    const error = document.getElementById('arRequestError'); error?.classList.add('hidden');
    const submit = document.getElementById('arSubmitBtn'); if (submit) submit.disabled = false;
    openModal('availabilityRequestModal');
    refreshIcons(document.getElementById('availabilityRequestModal'));
  }

  async function _arSubmit() {
    const st = state.availabilityRequests;
    if (!st?.requestContext || st.busy) return;
    const error = document.getElementById('arRequestError');
    error?.classList.add('hidden');
    const windows = Array.from(document.querySelectorAll('#arRequestWindows .ar-window-row')).map(row => ({
      date: row.querySelector('.ar-window-date')?.value || '',
      time: row.querySelector('.ar-window-time')?.value || '',
    }));
    if (!windows.length || windows.some(option => !option.date || !option.time)) {
      if (error) { error.textContent = 'Add a date and time for every option.'; error.classList.remove('hidden'); }
      return;
    }
    st.busy = true;
    const submit = document.getElementById('arSubmitBtn'); if (submit) submit.disabled = true;
    try {
      const context = st.requestContext;
      const r = await api('/api/test-schedule/availability-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: context.lead?.id || null,
          chat_id: context.chatId,
          client_name: context.clientName,
          client_phone: context.clientPhone,
          windows,
          note: document.getElementById('arRequestNote')?.value?.trim() || null,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      closeModal('availabilityRequestModal');
      toast('Class availability request sent to the test team', 'success');
      _arRefreshBadge();
      if (state.activeView === 'availability-requests') _arLoad();
    } catch (err) {
      if (error) { error.textContent = String(err?.message || err); error.classList.remove('hidden'); }
    } finally {
      st.busy = false;
      if (submit) submit.disabled = false;
    }
  }

  async function _arStart(id, button) {
    if (button) button.disabled = true;
    try {
      const r = await api('/api/test-schedule/availability-requests/' + encodeURIComponent(id), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'start' }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast('Request moved to In process', 'success');
      _arLoad();
    } catch (err) {
      toast(String(err?.message || err), 'error');
      if (button) button.disabled = false;
    }
  }

  async function _arCancel(id, button) {
    if (button) button.disabled = true;
    try {
      const r = await api('/api/test-schedule/availability-requests/' + encodeURIComponent(id), { method: 'DELETE' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast('Request cancelled', 'success');
      _arLoad();
    } catch (err) {
      toast(String(err?.message || err), 'error');
      if (button) button.disabled = false;
    }
  }

  async function _arLoadTeachers() {
    const st = state.availabilityRequests;
    if (st.teachers?.length) return st.teachers;
    const r = await api('/api/test-schedule/teachers');
    if (!r.ok) throw new Error('Could not load teachers');
    const j = await r.json();
    st.teachers = j.teachers || [];
    return st.teachers;
  }

  async function _arOpenResolution(req, mode) {
    const st = state.availabilityRequests;
    st.resolutionRequest = req;
    st.resolutionMode = mode;
    const reject = mode === 'reject';
    const modal = document.querySelector('#availabilityResolutionModal .ar-resolution-modal');
    modal?.classList.toggle('is-reject', reject);
    const title = document.getElementById('arResolutionTitle');
    if (title) {
      clear(title);
      title.append(lucideIcon(reject ? 'calendar-x' : 'calendar-check'), document.createTextNode(reject ? ' No availability found' : ' Availability found'));
    }
    document.getElementById('arResolutionFoundFields')?.classList.toggle('hidden', reject);
    document.getElementById('arResolutionSuggestionFields')?.classList.toggle('hidden', !reject);
    const summary = document.getElementById('arResolutionRequest');
    if (summary) {
      clear(summary);
      summary.append(
        el('strong', { text: req.client_name || req.client_phone || 'Client' }),
        _arWindowChips(req),
      );
    }
    const first = _arWindows(req)[0] || {};
    const date = document.getElementById('arResolutionDate');
    const time = document.getElementById('arResolutionTime');
    if (date) { date.min = _testsTodayIso(); date.value = first.date || _testsTodayIso(); }
    _arSetResolutionWeekday(date?.value);
    _arPopulateHalfHourSelect(time, first.time || '');
    const suggestionRows = document.getElementById('arSuggestionRows');
    if (suggestionRows) {
      clear(suggestionRows);
      _arAddSuggestionRow();
    }
    const note = document.getElementById('arResolutionNote');
    if (note) {
      note.value = '';
      note.placeholder = reject
        ? 'Tell Sales what the team checked. Add a note, a suggested date/time, or both.'
        : 'Tell Sales what the team checked or add useful context.';
    }
    const error = document.getElementById('arResolutionError'); error?.classList.add('hidden');
    const confirm = document.getElementById('arResolutionConfirmBtn');
    if (confirm) {
      confirm.disabled = false;
      const text = confirm.lastChild;
      if (text?.nodeType === 3) text.textContent = reject ? ' Confirm not found' : ' Confirm found';
    }
    if (!reject) {
      const select = document.getElementById('arResolutionTeacher');
      if (select) {
        clear(select);
        select.appendChild(el('option', { value: '', text: 'Loading teachers…' }));
      }
      try {
        const teachers = await _arLoadTeachers();
        if (select) {
          clear(select);
          for (const teacher of teachers) {
            select.appendChild(el('option', {
              value: teacher.id,
              text: teacher.display_name || teacher.name || 'Teacher',
            }));
          }
        }
      } catch (err) {
        if (error) { error.textContent = String(err?.message || err); error.classList.remove('hidden'); }
      }
    }
    openModal('availabilityResolutionModal');
    refreshIcons(document.getElementById('availabilityResolutionModal'));
  }

  async function _arConfirmResolution() {
    const st = state.availabilityRequests;
    const req = st?.resolutionRequest;
    if (!req || st.busy) return;
    const reject = st.resolutionMode === 'reject';
    const error = document.getElementById('arResolutionError');
    error?.classList.add('hidden');
    const body = {
      action: reject ? 'reject' : 'done',
      note: document.getElementById('arResolutionNote')?.value?.trim() || null,
    };
    if (!reject) {
      body.teacher_id = document.getElementById('arResolutionTeacher')?.value || '';
      body.date = document.getElementById('arResolutionDate')?.value || '';
      body.time = document.getElementById('arResolutionTime')?.value || '';
      if (!body.teacher_id || !body.date || !body.time) {
        if (error) { error.textContent = 'Teacher, date and time are required.'; error.classList.remove('hidden'); }
        return;
      }
    } else {
      const suggestionRows = Array.from(document.querySelectorAll('#arSuggestionRows .ar-suggestion-row'));
      const rawSuggestions = suggestionRows.map(row => ({
        date: row.querySelector('.ar-suggestion-date')?.value || '',
        time: row.querySelector('.ar-suggestion-time')?.value || '',
      }));
      if (rawSuggestions.some(option => !!option.date !== !!option.time)) {
        if (error) { error.textContent = 'Each suggestion needs both a date and a time.'; error.classList.remove('hidden'); }
        return;
      }
      body.suggestions = rawSuggestions.filter(option => option.date && option.time);
      if (!body.note && !body.suggestions.length) {
        if (error) { error.textContent = 'Add a team note or at least one suggested date and time.'; error.classList.remove('hidden'); }
        return;
      }
    }
    st.busy = true;
    const confirm = document.getElementById('arResolutionConfirmBtn'); if (confirm) confirm.disabled = true;
    try {
      const r = await api('/api/test-schedule/availability-requests/' + encodeURIComponent(req.id), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      closeModal('availabilityResolutionModal');
      toast(reject ? 'Sales notified: no availability found' : 'Sales notified: availability found', 'success');
      _arLoad();
    } catch (err) {
      if (error) { error.textContent = String(err?.message || err); error.classList.remove('hidden'); }
      if (confirm) confirm.disabled = false;
    } finally {
      st.busy = false;
    }
  }

  // ─── Vacation (2026-06-16) — teacher leave requests + system-admin approval ──
  // Teacher submits a date-range request → system-admin approves/rejects →
  // approved days vanish from test availability. Mirrors the My-availability page.
  function _tvFmt(d) { return _testsFmtDate(String(d).slice(0, 10)); }
  function _isAdminClient() { const m = state.me || {}; return m.role === 'admin' || m.role === 'super_admin' || !!m.superAdmin; }
  function _tvRange(req) { const a = _tvFmt(req.from_date), b = _tvFmt(req.to_date); return a === b ? a : (a + ' → ' + b); }
  function _tvStatusPill(status) {
    const map = { pending: ['tv-pill-pending', 'Pending'], approved: ['tv-pill-approved', 'Approved'], rejected: ['tv-pill-rejected', 'Rejected'], cancelled: ['tv-pill-cancelled', 'Cancelled'] };
    const m = map[status] || ['tv-pill-pending', status];
    return el('span', { class: 'tv-pill ' + m[0], text: m[1] });
  }

  function ensureTeacherVacationInit() {
    state.teacherVac = state.teacherVac || { inited: false, requests: [], canReview: false, pendingCount: 0, busy: false };
    const st = state.teacherVac;
    if (st.inited) { _tvLoad(); return; }
    st.inited = true;
    document.getElementById('tvRefreshBtn')?.addEventListener('click', () => _tvLoad());
    _tvLoad();
  }

  async function _tvLoad() {
    const st = state.teacherVac; if (!st) return;
    const loading = document.getElementById('tvLoading');
    if (loading) loading.classList.remove('hidden');
    try {
      const r = await api('/api/test-schedule/vacation');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      st.requests = j.requests || [];
      st.canReview = !!j.canReview;
      st.pendingCount = j.pendingCount || 0;
    } catch (err) {
      console.warn('[vacation] load failed:', err && (err.message || err));
      st.requests = [];
    } finally {
      if (loading) loading.classList.add('hidden');
      _tvRender();
      _tvSetBadge(st.canReview ? st.pendingCount : 0);
    }
  }

  function _tvRender() {
    const st = state.teacherVac; if (!st) return;
    const form = document.getElementById('tvForm');
    const admin = document.getElementById('tvAdmin');
    const list = document.getElementById('tvList');
    const empty = document.getElementById('tvEmpty');
    if (form) clear(form);
    if (admin) clear(admin);
    if (list) clear(list);
    if (empty) empty.classList.add('hidden');

    if (st.canReview) {
      // Admin: pending approval queue + recent decided.
      if (form) form.classList.add('hidden');
      if (admin) admin.classList.remove('hidden');
      const pending = st.requests.filter(r => r.status === 'pending');
      const decided = st.requests.filter(r => r.status !== 'pending');
      admin.appendChild(el('div', { class: 'tv-section-head', text: 'Pending requests (' + pending.length + ')' }));
      if (!pending.length) admin.appendChild(el('div', { class: 'tv-empty', text: 'No pending requests.' }));
      for (const req of pending) admin.appendChild(_tvAdminCard(req));
      if (decided.length) {
        admin.appendChild(el('div', { class: 'tv-section-head tv-section-sub', text: 'Recent decisions' }));
        for (const req of decided.slice(0, 30)) admin.appendChild(_tvDecidedRow(req));
      }
      refreshIcons(admin);
      // System-admin-only company-holidays panel below the queue. super_sales
      // (Mary) gets the approval queue but NOT holidays (system concern).
      if (_isSystemAdmin()) {
        document.getElementById('tvHolidays')?.classList.remove('hidden');
        _tvLoadHolidays();
      } else {
        document.getElementById('tvHolidays')?.classList.add('hidden');
      }
      return;
    }

    // Teacher / sales: request form + own requests (no holidays panel).
    document.getElementById('tvHolidays')?.classList.add('hidden');
    if (form) { form.classList.remove('hidden'); _tvRenderForm(form); }
    if (admin) admin.classList.add('hidden');
    if (!st.requests.length) { if (empty) empty.classList.remove('hidden'); }
    else {
      list.appendChild(el('div', { class: 'tv-section-head', text: 'My requests' }));
      for (const req of st.requests) list.appendChild(_tvMyCard(req));
    }
    refreshIcons(document.getElementById('teacherVacationView'));
  }

  function _tvRenderForm(host) {
    if (!host) return;
    const today = _testsTodayIso();
    const fromI = el('input', { type: 'date', class: 'input tv-date', min: today });
    const toI = el('input', { type: 'date', class: 'input tv-date', min: today });
    const reasonI = el('textarea', { class: 'input tv-reason', rows: '2', maxlength: '300', placeholder: 'Reason (optional)' });
    const btn = el('button', { type: 'button', class: 'btn btn-primary' }, [lucideIcon('send'), el('span', { text: 'Request vacation' })]);
    btn.addEventListener('click', () => {
      const from = fromI.value, to = toI.value;
      if (!from || !to) { toast('Pick a start and end date', 'error'); return; }
      if (to < from) { toast('End date is before start', 'error'); return; }
      _tvSubmit({ from, to, reason: reasonI.value.trim() });
    });
    host.appendChild(el('div', { class: 'tv-form-card' }, [
      el('div', { class: 'tv-form-title', text: 'Request days off' }),
      el('div', { class: 'tv-form-row' }, [
        el('label', { class: 'tv-form-lbl', text: 'From' }), fromI,
        el('label', { class: 'tv-form-lbl', text: 'To' }), toI,
      ]),
      reasonI,
      el('div', { class: 'tv-form-actions' }, [btn]),
    ]));
  }

  function _tvMyCard(req) {
    const kids = [
      el('div', { class: 'tv-card-main' }, [
        el('div', { class: 'tv-card-range', text: _tvRange(req) }),
        req.reason ? el('div', { class: 'tv-card-reason', text: req.reason }) : el('span'),
      ]),
      _tvStatusPill(req.status),
    ];
    const card = el('div', { class: 'tv-card' }, kids);
    if (req.status === 'pending') {
      const cancel = el('button', { type: 'button', class: 'btn btn-sm btn-ghost tv-cancel' }, [lucideIcon('x'), el('span', { text: 'Cancel' })]);
      cancel.addEventListener('click', () => _tvCancel(req.id));
      card.appendChild(cancel);
    } else if (req.status === 'rejected' && req.review_note) {
      card.appendChild(el('div', { class: 'tv-card-note', text: '“' + req.review_note + '”' }));
    }
    return card;
  }

  function _tvAdminCard(req) {
    const conflicts = req.bookingConflicts || 0;
    const noteI = el('input', { type: 'text', class: 'input tv-note-in', placeholder: 'Note (optional)', maxlength: '300' });
    const approve = el('button', { type: 'button', class: 'btn btn-sm tv-approve' }, [lucideIcon('check'), el('span', { text: 'Approve' })]);
    const reject = el('button', { type: 'button', class: 'btn btn-sm btn-ghost tv-reject' }, [lucideIcon('x'), el('span', { text: 'Reject' })]);
    approve.addEventListener('click', () => _tvReview(req.id, 'approved', noteI.value.trim()));
    reject.addEventListener('click', () => _tvReview(req.id, 'rejected', noteI.value.trim()));
    const card = el('div', { class: 'tv-admin-card' }, [
      el('div', { class: 'tv-admin-head' }, [
        el('span', { class: 'tv-admin-teacher', text: req.member_name || req.teacher_name || 'Member' }),
        el('span', { class: 'tv-admin-range', text: _tvRange(req) }),
      ]),
      el('div', { class: 'tv-admin-meta', text: 'requested ' + _tvFmt(req.requested_at) + (req.requested_by ? ' by ' + userLabel(req.requested_by) : '') }),
    ]);
    if (req.reason) card.appendChild(el('div', { class: 'tv-admin-reason', text: req.reason }));
    if (conflicts) {
      // Actionable warning: expand → list the booked tests, transfer each (or
      // all) to another teacher at the same time. Expansion is sticky across
      // the full re-renders triggered by _tvLoad()/socket events.
      const st = state.teacherVac;
      if (st && !st.expandedConflicts) st.expandedConflicts = new Set();
      const listHost = el('div', { class: 'tv-conflict-list hidden' });
      const toggle = el('button', { type: 'button', class: 'btn btn-sm btn-ghost tv-conflict-toggle' }, [lucideIcon('arrow-right-left'), el('span', { text: 'Transfer…' })]);
      toggle.addEventListener('click', () => {
        const open = !listHost.classList.toggle('hidden');
        if (st) { if (open) st.expandedConflicts.add(req.id); else st.expandedConflicts.delete(req.id); }
        if (open && !listHost.dataset.loaded) _tvLoadConflicts(req, listHost);
      });
      card.appendChild(el('div', { class: 'tv-conflict-head' }, [
        el('div', { class: 'tv-conflict' }, [lucideIcon('alert-triangle'), el('span', { text: conflicts + ' test(s) already booked in this range' })]),
        toggle,
      ]));
      card.appendChild(listHost);
      if (st && st.expandedConflicts.has(req.id)) {
        listHost.classList.remove('hidden');
        _tvLoadConflicts(req, listHost);
      }
    }
    card.appendChild(el('div', { class: 'tv-admin-actions' }, [noteI, reject, approve]));
    return card;
  }

  function _tvDecidedRow(req) {
    return el('div', { class: 'tv-decided-row' }, [
      el('span', { class: 'tv-admin-teacher', text: req.teacher_name || 'Teacher' }),
      el('span', { class: 'tv-admin-range', text: _tvRange(req) }),
      _tvStatusPill(req.status),
      el('span', { class: 'tv-decided-by', text: req.reviewed_by ? ('by ' + req.reviewed_by) : '' }),
    ]);
  }

  async function _tvSubmit(body) {
    const st = state.teacherVac; if (!st || st.busy) return;
    st.busy = true;
    try {
      const r = await api('/api/test-schedule/vacation', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.status === 503) { const j = await r.json().catch(() => ({})); toast(j.error || 'Server migration 0030 required', 'error'); return; }
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + r.status)); }
      toast('Vacation request sent', 'success');
      _tvLoad();
    } catch (err) { toast(String((err && err.message) || err), 'error'); }
    finally { st.busy = false; }
  }

  async function _tvReview(id, status, note) {
    const st = state.teacherVac; if (!st || st.busy) return;
    st.busy = true;
    try {
      const r = await api('/api/test-schedule/vacation/' + encodeURIComponent(id), { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status, note }) });
      if (r.status === 503) { const j = await r.json().catch(() => ({})); toast(j.error || 'Server migration 0030 required', 'error'); return; }
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + r.status)); }
      toast(status === 'approved' ? 'Approved' : 'Rejected', 'success');
      _tvLoad();
    } catch (err) { toast(String((err && err.message) || err), 'error'); }
    finally { st.busy = false; }
  }

  // ── Conflict transfer (reassign booked tests to another teacher) ──────────
  function _tvConflictWhen(row) {
    const iso = String(row.slot_date).slice(0, 10);
    const d = new Date(iso + 'T12:00:00Z');
    const ds = isNaN(d) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    return ds + ' · ' + String(row.slot_time).slice(0, 5);
  }

  function _tvTeacherSelect(opts) {
    const sel = el('select', { class: 'input tv-conflict-sel' });
    for (const o of opts) sel.appendChild(el('option', { value: o.id, text: o.label }));
    return sel;
  }

  async function _tvLoadConflicts(req, host) {
    host.dataset.loaded = '1';
    clear(host);
    host.appendChild(el('div', { class: 'tv-conflict-loading', text: 'Loading booked tests…' }));
    let j = null;
    try {
      const r = await api('/api/test-schedule/vacation/' + encodeURIComponent(req.id) + '/conflicts');
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || ('HTTP ' + r.status)); }
      j = await r.json();
    } catch (err) {
      clear(host);
      host.appendChild(el('div', { class: 'tv-conflict-error', text: 'Could not load tests — ' + String((err && err.message) || err) }));
      host.dataset.loaded = ''; // allow retry on next expand
      return;
    }
    clear(host);
    const rows = j.conflicts || [];
    const teachers = j.teachers || [];
    if (!rows.length) {
      host.appendChild(el('div', { class: 'tv-empty', text: 'No booked tests left in this range.' }));
      return;
    }
    const movable = rows.filter(r => r.reassignable);
    if (movable.length > 1 && teachers.length) {
      const allSel = _tvTeacherSelect(teachers.map(t => ({ id: t.id, label: t.display_name })));
      const allBtn = el('button', { type: 'button', class: 'btn btn-sm tv-transfer-btn' }, [lucideIcon('arrow-right-left'), el('span', { text: 'Transfer all' })]);
      allBtn.addEventListener('click', async () => {
        if (!allSel.value || allBtn.disabled) return;
        allBtn.disabled = true;
        let ok = 0; const fails = [];
        for (const row of movable) {
          const res = await _tvReassign(row.id, allSel.value, { silent: true });
          if (res.ok) ok++; else fails.push(_tvConflictWhen(row) + ': ' + res.error);
        }
        if (fails.length) toast(ok + ' transferred · ' + fails.length + ' failed — ' + fails.join(' | '), 'error');
        else toast(ok + ' test(s) transferred', 'success');
        _tvLoad(); // sticky expansion re-opens + refetches this panel
      });
      host.appendChild(el('div', { class: 'tv-transfer-all' }, [
        el('span', { class: 'tv-transfer-all-lbl', text: 'Transfer all to' }), allSel, allBtn,
      ]));
    }
    for (const row of rows) host.appendChild(_tvConflictRow(row, teachers));
    refreshIcons(host);
  }

  function _tvConflictRow(row, teachers) {
    const rowEl = el('div', { class: 'tv-conflict-row' + (row.reassignable ? '' : ' tv-conflict-muted') }, [
      el('span', { class: 'tv-conflict-when', text: _tvConflictWhen(row) }),
      el('span', { class: 'tv-conflict-student', text: row.student_name || 'Unknown student' }),
      el('span', { class: 'tv-conflict-status', text: String(row.status || '').replace(/_/g, ' ') }),
    ]);
    if (!row.reassignable) return rowEl;
    const eligible = row.eligible_teachers || [];
    // Eligible teachers carry free-seat counts; when none fit (hours/full) fall
    // back to ALL teachers so the server's precise 409 reason still surfaces.
    const opts = eligible.length
      ? eligible.map(t => ({ id: t.id, label: t.display_name + ' (' + t.free_seats + ' free)' }))
      : teachers.map(t => ({ id: t.id, label: t.display_name }));
    const sel = _tvTeacherSelect(opts);
    const btn = el('button', { type: 'button', class: 'btn btn-sm tv-transfer-btn' }, [lucideIcon('arrow-right-left'), el('span', { text: 'Transfer' })]);
    btn.addEventListener('click', async () => {
      if (!sel.value || btn.disabled) return;
      btn.disabled = true; sel.disabled = true;
      const res = await _tvReassign(row.id, sel.value);
      btn.disabled = false; sel.disabled = false;
      if (res.ok) { toast('Test transferred', 'success'); _tvLoad(); }
    });
    const actions = el('div', { class: 'tv-conflict-actions' }, [sel, btn]);
    if (!eligible.length) actions.appendChild(el('span', { class: 'tv-conflict-warn', text: 'outside hours or full — server will verify' }));
    rowEl.appendChild(actions);
    return rowEl;
  }

  async function _tvReassign(slotId, teacherId, { silent = false } = {}) {
    try {
      const r = await api('/api/test-schedule/slots/' + encodeURIComponent(slotId) + '/reassign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teacher_id: teacherId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      return { ok: true, slot: j.slot };
    } catch (err) {
      const msg = String((err && err.message) || err);
      if (!silent) toast(msg, 'error');
      return { ok: false, error: msg };
    }
  }

  async function _tvCancel(id) {
    const st = state.teacherVac; if (!st || st.busy) return;
    st.busy = true;
    try {
      const r = await api('/api/test-schedule/vacation/' + encodeURIComponent(id), { method: 'DELETE' });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + r.status)); }
      toast('Request cancelled', 'success');
      _tvLoad();
    } catch (err) { toast(String((err && err.message) || err), 'error'); }
    finally { st.busy = false; }
  }

  // Admin nav-tab badge (pending count). Module-scoped so the socket listener +
  // app boot can call it before the Vacation view ever opens.
  function _tvSetBadge(n) {
    const b = document.getElementById('vacTabBadge');
    if (!b) return;
    if (n > 0) { b.textContent = String(n); b.classList.remove('hidden'); }
    else { b.textContent = ''; b.classList.add('hidden'); }
    // Roll the Vacation count up into the Tests▾ trigger's aggregated badge.
    try { refreshGroupBadges(); } catch (_) { /* badge is advisory */ }
  }
  async function _tvRefreshBadge() {
    // super_sales (Mary) now reviews vacations → she gets the pending-count
    // badge too. Use the sales-admin gate (not _isAdminClient, which is
    // system-admin-only and still used by the holidays panel above).
    if (!_isSalesAdmin()) { _tvSetBadge(0); return; }
    try {
      const r = await api('/api/test-schedule/vacation?status=pending');
      if (!r.ok) return;
      const j = await r.json();
      _tvSetBadge((j.requests || []).length);
    } catch (_) { /* badge is advisory */ }
  }

  // ── Company holidays (admin-only panel on the Vacation page) ────────────────
  function _tvHolRange(h) { const a = _tvFmt(h.from_date), b = _tvFmt(h.to_date); return a === b ? a : (a + ' → ' + b); }

  async function _tvLoadHolidays() {
    const st = state.teacherVac; if (!st) return;
    try {
      const from = _testsTodayIso();
      const to = _testsAddDays(from, 120);
      const r = await api('/api/test-schedule/holidays?from=' + from + '&to=' + to);
      if (r.status === 503) { const j = await r.json().catch(() => ({})); _tvRenderHolidays(j.error || 'Migration 0032 requise'); return; }
      if (!r.ok) return;
      const j = await r.json();
      st.holidays = j.holidays || [];
      _tvRenderHolidays();
    } catch (_) { /* advisory */ }
  }

  function _tvRenderHolidays(errMsg) {
    const st = state.teacherVac;
    const host = document.getElementById('tvHolidays'); if (!host) return;
    clear(host);
    host.appendChild(el('div', { class: 'tv-section-head tv-section-sub' }, [lucideIcon('palmtree'), el('span', { text: ' Jours fériés (école fermée)' })]));
    if (errMsg) { host.appendChild(el('div', { class: 'tv-empty', text: errMsg })); return; }
    const today = _testsTodayIso();
    const fromI = el('input', { type: 'date', class: 'input tv-date', min: today });
    const toI = el('input', { type: 'date', class: 'input tv-date', min: today });
    const labelI = el('input', { type: 'text', class: 'input tv-hol-label', placeholder: 'Libellé (ex: Aïd)', maxlength: '200' });
    const addBtn = el('button', { type: 'button', class: 'btn btn-sm btn-primary' }, [lucideIcon('plus'), el('span', { text: 'Ajouter' })]);
    addBtn.addEventListener('click', () => {
      const from = fromI.value, to = toI.value || fromI.value;
      if (!from) { toast('Choisissez une date', 'error'); return; }
      if (to < from) { toast('Date de fin avant le début', 'error'); return; }
      _tvAddHoliday({ from, to, label: labelI.value.trim() });
    });
    host.appendChild(el('div', { class: 'tv-hol-form' }, [
      el('span', { class: 'tv-form-lbl', text: 'Du' }), fromI,
      el('span', { class: 'tv-form-lbl', text: 'au' }), toI, labelI, addBtn,
    ]));
    const list = el('div', { class: 'tv-hol-list' });
    const hols = (st && st.holidays) || [];
    if (!hols.length) list.appendChild(el('div', { class: 'tv-empty', text: 'Aucun jour férié à venir.' }));
    for (const h of hols) {
      const row = el('div', { class: 'tv-hol-row' }, [
        lucideIcon('palmtree'),
        el('span', { class: 'tv-hol-range', text: _tvHolRange(h) }),
        h.label ? el('span', { class: 'tv-hol-label-txt', text: h.label }) : el('span'),
      ]);
      if (h.bookingConflicts) row.appendChild(el('span', { class: 'tv-conflict tv-hol-conflict' }, [lucideIcon('alert-triangle'), el('span', { text: h.bookingConflicts + ' test(s) réservé(s)' })]));
      const del = el('button', { type: 'button', class: 'btn btn-sm btn-ghost tv-hol-del', title: 'Supprimer' }, [lucideIcon('x')]);
      del.addEventListener('click', () => _tvRemoveHoliday(h.id));
      row.appendChild(del);
      list.appendChild(row);
    }
    host.appendChild(list);
    refreshIcons(host);
  }

  async function _tvAddHoliday(body) {
    const st = state.teacherVac; if (!st || st.busy) return;
    st.busy = true;
    try {
      const r = await api('/api/test-schedule/holidays', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.status === 503) { const j = await r.json().catch(() => ({})); toast(j.error || 'Migration 0032 requise', 'error'); return; }
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + r.status)); }
      const j = await r.json();
      const c = j.holiday && j.holiday.bookingConflicts;
      toast(c ? ('Jour férié ajouté — ⚠ ' + c + ' test(s) déjà réservé(s)') : 'Jour férié ajouté', c ? 'error' : 'success');
      _tvLoadHolidays();
    } catch (err) { toast(String((err && err.message) || err), 'error'); }
    finally { st.busy = false; }
  }

  async function _tvRemoveHoliday(id) {
    const st = state.teacherVac; if (!st || st.busy) return;
    st.busy = true;
    try {
      const r = await api('/api/test-schedule/holidays/' + encodeURIComponent(id), { method: 'DELETE' });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + r.status)); }
      toast('Jour férié supprimé', 'success');
      _tvLoadHolidays();
    } catch (err) { toast(String((err && err.message) || err), 'error'); }
    finally { st.busy = false; }
  }

  // ── Lougha Test Library (2026-08-30, simplified 2026-10-04) ────────
  // One page, one action: six level cards, each with a single Open button
  // into the Test Studio, which holds the slides, audio, timer and guide.
  // Teachers did not know where to start on the old page ("PDF or click
  // Start?"), so downloads, audio, search and the path builder are gone.
  // /test-library is served immutable for a year: bump this token after
  // re-rendering covers (scripts/test-library/build-library-slides.mjs).
  const TEST_LIBRARY_ASSET_VERSION = 'library-20261005';
  // The last two are the teachers' own decks (New A1 test, New A2 and B1 test),
  // added next to the Lougha packs on 2026-10-05.
  const TEST_LIBRARY_PACKS = [
    { id: 'kids-a0-a1', group: 'kids', badge: 'Pre-A1 – A1', title: 'My Discovery Day', audience: 'Kids · 7–12', accent: 'coral' },
    { id: 'kids-a2-b1', group: 'kids', badge: 'A2 – B1', title: 'Team Up, Speak Up', audience: 'Kids · 9–15', accent: 'blue' },
    { id: 'a0-foundation', group: 'adults', badge: 'A0', title: 'First English, Real Life', audience: 'Adults · First steps', accent: 'green' },
    { id: 'a1-essentials', group: 'adults', badge: 'A1', title: 'Everyday English in Motion', audience: 'Adults & teens · Beginner', accent: 'blue' },
    { id: 'a2-b1-communication', group: 'adults', badge: 'A2 – B1', title: 'Solve It, Explain It', audience: 'Adults · Intermediate', accent: 'violet' },
    { id: 'b2-c2-advanced', group: 'adults', badge: 'B2 – C2', title: 'Think Clearly Under Pressure', audience: 'Adults · Advanced', accent: 'navy' },
    { id: 'a1-proficiency', group: 'adults', badge: 'A1', title: 'English Proficiency Assessment', audience: 'Adults · New A1 test', accent: 'blue' },
    { id: 'a2-b1-proficiency', group: 'adults', badge: 'A2 – B1', title: 'English Proficiency Assessment', audience: 'Adults · New A2 & B1 test', accent: 'violet' }
  ].map(pack => ({
    ...pack,
    thumb: '/test-library/thumbs/' + pack.id + '.webp?v=' + TEST_LIBRARY_ASSET_VERSION,
    studio: '/test-studio/?pack=' + pack.id
  }));

  function _tlWireCrossLinks() {
    const node = document.getElementById('ttOpenLibraryBtn');
    if (!node || node.dataset.tlBound === '1') return;
    node.dataset.tlBound = '1';
    node.addEventListener('click', () => showView('test-library'));
  }

  function ensureTestLibraryInit() {
    _tlWireCrossLinks();
    _tlRenderCards();
  }

  // The whole card is the link, so a teacher can click anywhere on it; the
  // Open pill only makes the action obvious.
  function _tlPackCard(pack) {
    return el('a', { class: 'tl-card tl-accent-' + pack.accent, href: pack.studio, 'data-pack-id': pack.id, 'aria-label': 'Open ' + pack.badge + ' · ' + pack.title }, [
      el('div', { class: 'tl-card-visual' }, [
        el('img', { src: pack.thumb, alt: '', loading: 'lazy', decoding: 'async', width: '800', height: '450' })
      ]),
      el('div', { class: 'tl-card-body' }, [
        el('span', { class: 'tl-card-level', text: pack.badge }),
        el('strong', { class: 'tl-card-title', text: pack.title }),
        el('span', { class: 'tl-card-audience', text: pack.audience }),
        el('span', { class: 'tl-open' }, [el('span', { text: 'Open' }), lucideIcon('arrow-right')])
      ])
    ]);
  }

  function _tlRenderCards() {
    const hosts = { kids: document.getElementById('tlKidsCards'), adults: document.getElementById('tlAdultCards') };
    for (const [group, host] of Object.entries(hosts)) {
      if (!host) continue;
      clear(host);
      TEST_LIBRARY_PACKS.filter(pack => pack.group === group).forEach(pack => host.appendChild(_tlPackCard(pack)));
      refreshIcons(host);
    }
  }

  // ============================================================
  // Test recordings (2026-08-31) — the Zoom cloud recording of every placement
  // test, as a data table. One action per row: Watch opens the session in a
  // player dialog.
  //
  // The video carries both the picture and the sound, so the row stays a row —
  // no inline audio, no transcript panel. The dialog's Speaker/Audio switch is
  // there for a weak connection: the audio-only M4A is a tenth of the size.
  //
  // Playback streams through a signed ticket rather than a blob download: a
  // test session is 10–180 minutes, so it has to seek with Range instead of
  // being pulled whole before the first frame.
  // ============================================================
  function _trecFmtDate(iso) {
    return MA.fmt('fr-dmy', 'fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' }, iso) || String(iso || '').slice(0, 10);
  }
  function _trecFmtTime(iso) {
    return MA.fmt('fr-hm', 'fr-FR', { hour: '2-digit', minute: '2-digit' }, iso);
  }
  function _trecFmtSize(bytes) {
    const b = Number(bytes || 0);
    if (!b) return '—';
    const mb = b / 1048576;
    if (mb < 1) return Math.max(1, Math.round(b / 1024)) + ' KB';
    return mb >= 1024 ? (mb / 1024).toFixed(2) + ' GB' : mb.toFixed(mb < 10 ? 1 : 0) + ' MB';
  }
  function _trecRowSize(row) {
    return ['audio', 'video', 'transcript'].reduce((n, k) => n + Number(row[k]?.size || 0), 0);
  }
  function _trecRowFiles(row) {
    return ['audio', 'video', 'transcript'].filter(k => row[k]).length;
  }
  // Seconds to jump to once the player's file has its metadata (a chapter
  // picked while the playback ticket is still being minted).
  let _trecPendingSeek = null;
  // Every test inside the session (2026-10-04): a teacher's room holds several
  // back to back, each with its offset from the start of the recording.
  function _trecRowTests(row) {
    return Array.isArray(row?.tests) ? row.tests : [];
  }
  // How many tests a row stands for — its chapters, or the single matched
  // booking when the clock could not place one inside the session.
  function _trecRowTestCount(row) {
    const n = _trecRowTests(row).length;
    return n || (row?.booking?.studentName ? 1 : 0);
  }
  // Drive-archive rows carry no duration — show nothing rather than "null min".
  function _trecFmtDuration(row) {
    const m = Number(row?.durationMin);
    return row?.durationMin != null && Number.isFinite(m) && m > 0 ? m + ' min' : '';
  }
  // 30:15, or 1:02:07 past the hour.
  function _trecFmtOffset(sec) {
    const s = Math.max(0, Math.round(Number(sec) || 0));
    const h = Math.floor(s / 3600);
    const mm = String(Math.floor((s % 3600) / 60));
    const ss = String(s % 60).padStart(2, '0');
    return h ? h + ':' + mm.padStart(2, '0') + ':' + ss : mm.padStart(2, '0') + ':' + ss;
  }
  function _trecChapterChip(row, test, onPick) {
    const playable = row.video || row.audio;
    const label = [test.studentName || 'Student', test.level, _trecFmtOffset(test.offsetSec)].filter(Boolean).join(' · ');
    const kids = [
      el('span', { class: 'trec-chapter-dot', 'aria-hidden': 'true' }),
      el('span', { class: 'trec-chapter-name', dir: 'auto', text: test.studentName || 'Student' }),
    ];
    if (test.level) {
      kids.push(el('span', { class: 'trec-chapter-sep', 'aria-hidden': 'true', text: '·' }));
      kids.push(el('span', { class: 'trec-chapter-level', text: test.level }));
    }
    kids.push(el('span', { class: 'trec-chapter-sep', 'aria-hidden': 'true', text: '·' }));
    kids.push(el('span', { class: 'trec-chapter-time', text: _trecFmtOffset(test.offsetSec) }));
    return el('button', {
      class: 'trec-chapter trec-chapter-' + String(test.status || 'unknown').replace(/[^a-z_]/gi, ''),
      type: 'button',
      disabled: playable ? undefined : 'disabled',
      title: playable ? 'Play from ' + _trecFmtOffset(test.offsetSec) + ' — ' + label : 'No playable file for this session',
      'aria-label': 'Play ' + label,
      onclick: ev => { ev.stopPropagation(); onPick(test); },
    }, kids);
  }

  function ensureTestRecordingsInit() {
    state.testRecordings = state.testRecordings || {
      inited: false, from: '', to: '', rows: [], teachers: [], errors: [],
      teacher: 'all', status: 'matched', search: '', loading: false,
    };
    const st = state.testRecordings;
    if (st.inited) { _trecLoad(); return; }
    st.inited = true;
    st.to = _testsTodayIso();
    st.from = _testsAddDays(st.to, -13);          // default: last 14 days
    const fromEl = document.getElementById('trecFrom');
    const toEl = document.getElementById('trecTo');
    if (fromEl) fromEl.value = st.from;
    if (toEl) toEl.value = st.to;
    fromEl?.addEventListener('change', ev => { st.from = ev.target.value; _trecLoad(); });
    toEl?.addEventListener('change', ev => { st.to = ev.target.value; _trecLoad(); });
    document.getElementById('trecRefreshBtn')?.addEventListener('click', () => _trecLoad({ fresh: true }));
    document.getElementById('trecTeacher')?.addEventListener('change', ev => {
      st.teacher = ev.target.value; _trecRender();
    });
    document.getElementById('trecStatusSeg')?.addEventListener('click', ev => {
      const btn = ev.target.closest('[data-trec-status]');
      if (!btn) return;
      st.status = btn.getAttribute('data-trec-status');
      document.querySelectorAll('#trecStatusSeg .trec-seg-btn')
        .forEach(b => b.classList.toggle('trec-seg-on', b === btn));
      _trecRender();
    });
    document.getElementById('trecSearch')?.addEventListener('input', ev => {
      st.search = (ev.target.value || '').trim().toLowerCase(); _trecRender();
    });
    _trecWirePlayer();
    _trecLoad();
  }

  // The player dialog's own listeners, bound once — by this view, or by the
  // conversation header when a reviewer opens a contact's test before ever
  // visiting Test recordings.
  let _trecPlayerWired = false;
  function _trecWirePlayer() {
    if (_trecPlayerWired) return;
    _trecPlayerWired = true;
    document.getElementById('trecPlayerClose')?.addEventListener('click', _trecClosePlayer);
    document.getElementById('trecPlayer')?.addEventListener('click', ev => {
      if (ev.target.id === 'trecPlayer') _trecClosePlayer();   // click the backdrop
    });
    // A chapter picked before the file's metadata arrived seeks once it does.
    document.getElementById('trecPlayerVideo')?.addEventListener('loadedmetadata', ev => {
      if (_trecPendingSeek == null) return;
      const sec = _trecPendingSeek;
      _trecPendingSeek = null;
      try { ev.currentTarget.currentTime = sec; } catch { /* not seekable yet */ }
    });
    // Contact player only (a per-test clip on screen) — see _trecLoadClip.
    document.getElementById('trecPlayerVideo')?.addEventListener('error', _trecClipError);
    document.getElementById('trecPlayerModeBtn')?.addEventListener('click', _trecToggleMode);
  }

  function _trecSeek(sec) {
    const video = document.getElementById('trecPlayerVideo');
    const s = Number(sec);
    if (!video || !Number.isFinite(s) || s <= 0) { _trecPendingSeek = null; return; }
    if (video.currentSrc && video.readyState >= 1) {
      _trecPendingSeek = null;
      try { video.currentTime = s; } catch { /* */ }
      video.play().catch(() => { /* the user can press play */ });
    } else {
      _trecPendingSeek = s;
    }
  }

  async function _trecLoad(opts = {}) {
    const st = state.testRecordings; if (!st || st.loading) return;
    st.loading = true;
    const loading = document.getElementById('trecLoading');
    const errorEl = document.getElementById('trecError');
    loading?.classList.remove('hidden');
    errorEl?.classList.add('hidden');
    document.getElementById('trecEmpty')?.classList.add('hidden');
    try {
      const qs = new URLSearchParams({ from: st.from, to: st.to });
      if (opts.fresh) qs.set('fresh', '1');
      const r = await api('/api/test-recordings?' + qs.toString());
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      st.rows = j.rows || [];
      st.teachers = j.teachers || [];
      st.errors = j.errors || [];
      if (j.zoomConfigured === false) {
        st.errors = [{ teacher: '', error: 'Zoom is not configured on this server.' }];
      }
    } catch (err) {
      st.rows = [];
      if (errorEl) {
        errorEl.textContent = 'Could not reach Zoom — ' + (err?.message || err);
        errorEl.classList.remove('hidden');
      }
    } finally {
      st.loading = false;
      loading?.classList.add('hidden');
      _trecFillTeacherSelect();
      _trecRender();
    }
  }

  function _trecFillTeacherSelect() {
    const st = state.testRecordings;
    const sel = document.getElementById('trecTeacher');
    if (!sel || !st) return;
    const counts = new Map();
    for (const row of st.rows) counts.set(row.teacherId, (counts.get(row.teacherId) || 0) + 1);
    clear(sel);
    sel.appendChild(el('option', { value: 'all', text: 'All teachers (' + st.rows.length + ')' }));
    for (const t of st.teachers) {
      sel.appendChild(el('option', {
        value: t.id,
        // t.name, not displayName: the picker label has to read the same as
        // the Teacher column, and displayName carries a "Teacher " prefix.
        text: (t.name || t.displayName) + ' (' + (counts.get(t.id) || 0) + ')',
      }));
    }
    sel.value = st.teachers.some(t => t.id === st.teacher) ? st.teacher : 'all';
    st.teacher = sel.value;
  }

  function _trecVisibleRows() {
    const st = state.testRecordings;
    const q = st.search;
    return st.rows.filter(row => {
      if (st.teacher !== 'all' && row.teacherId !== st.teacher) return false;
      // "Matched" is the default because an unmatched session is usually a
      // re-test or a room left open — noise when you are looking for a student.
      if (st.status === 'matched' && !row.booking?.studentName && !_trecRowTests(row).length) return false;
      if (!q) return true;
      const hay = [row.teacherName, row.topic, row.booking?.studentName]
        .concat(_trecRowTests(row).map(t => t.studentName))
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }

  function _trecRender() {
    const st = state.testRecordings;
    const body = document.getElementById('trecList');
    if (!body || !st) return;
    clear(body);
    const rows = _trecVisibleRows();

    const totalSize = rows.reduce((n, r) => n + _trecRowSize(r), 0);
    const teacherCount = new Set(rows.map(r => r.teacherId)).size;
    const setStat = (id, v) => { const n = document.getElementById(id); if (n) n.textContent = v; };
    // Tests, not sessions: one room session often holds several tests.
    setStat('trecStatCount', String(rows.reduce((n, r) => n + _trecRowTestCount(r), 0)));
    setStat('trecStatSize', rows.length ? _trecFmtSize(totalSize) : '—');
    setStat('trecStatTeachers', String(teacherCount));

    const fileCount = rows.reduce((n, r) => n + _trecRowFiles(r), 0);
    const countEl = document.getElementById('trecResultCount');
    if (countEl) {
      countEl.textContent = rows.length
        ? rows.length + ' session' + (rows.length > 1 ? 's' : '') + ' · ' + fileCount + ' file' + (fileCount > 1 ? 's' : '')
        : '';
    }
    document.getElementById('trecEmpty')?.classList.toggle('hidden', rows.length > 0 || st.loading);

    // A per-teacher Zoom failure must not read as "no recordings" — say which
    // teacher could not be reached and still render everyone else.
    if (st.errors.length) {
      const errorEl = document.getElementById('trecError');
      if (errorEl) {
        errorEl.textContent = st.errors
          .map(e => (e.teacher ? (e.teacher === 'archive' ? 'Drive archive' : e.teacher) + ': ' : '') + e.error).join(' · ');
        errorEl.classList.remove('hidden');
      }
    }

    for (const row of rows) body.appendChild(_trecRow(row));
    refreshIcons();
  }

  function _trecRow(row) {
    const status = row.booking?.status || 'unmatched';
    const tr = el('tr', { class: 'trec-tr' });

    tr.appendChild(el('td', {}, [
      el('span', { class: 'trec-status trec-status-' + status, text: status.replace(/_/g, ' ') }),
    ]));
    tr.appendChild(el('td', { class: 'trec-date-cell' }, [
      document.createTextNode(_trecFmtDate(row.startTime) + ' '),
      el('span', { class: 'trec-date-time', text: _trecFmtTime(row.startTime) }),
    ]));
    tr.appendChild(el('td', { class: 'trec-teacher-cell', text: row.teacherName || '—' }));

    const student = el('div', { class: 'trec-student' });
    if (row.booking?.studentName) {
      const name = el('span', { class: 'trec-student-name' }, [document.createTextNode(row.booking.studentName)]);
      if (row.booking.level) name.appendChild(el('span', { class: 'trec-level', text: row.booking.level }));
      student.appendChild(name);
    } else {
      // Still worth showing: usually a walk-in, a re-test, or a session that
      // ran far from its slot.
      student.appendChild(el('span', { class: 'trec-student-name trec-student-name-unmatched', text: 'Unmatched session' }));
    }
    const sub = el('span', {
      class: 'trec-student-sub',
      text: [row.topic, _trecFmtDuration(row)].filter(Boolean).join(' · '),
    });
    // Zoom cloud is emptied nightly; older sessions are served from Drive.
    if (row.source === 'archive') sub.appendChild(el('span', { class: 'trec-source-tag', text: 'Drive archive' }));
    student.appendChild(sub);
    // One chip per test inside the session — a click opens the player at the
    // moment that test began.
    const tests = _trecRowTests(row);
    if (tests.length) {
      student.appendChild(el('div', { class: 'trec-chapters' },
        tests.map(t => _trecChapterChip(row, t, test => _trecOpenPlayer(row, { seekSec: test.offsetSec })))));
    }
    tr.appendChild(el('td', {}, [student]));

    tr.appendChild(el('td', { class: 'trec-num', text: _trecFmtSize(_trecRowSize(row)) }));

    const playable = row.video || row.audio;
    tr.appendChild(el('td', { class: 'trec-act' }, [
      el('button', {
        class: 'trec-watch', type: 'button',
        disabled: playable ? undefined : 'disabled',
        title: playable ? 'Watch this session' : 'Zoom has no playable file for this session',
        onclick: () => _trecOpenPlayer(row),
      }, [el('i', { 'data-lucide': 'play' }), document.createTextNode('Watch')]),
    ]));
    return tr;
  }

  // ── Player dialog ───────────────────────────────────────────
  // Kept out of the table: a video wide enough to read a whiteboard cannot live
  // in a row, and one dialog reused for every session means only one stream is
  // ever open.
  function _trecOpenPlayer(row, opts = {}) {
    const overlay = document.getElementById('trecPlayer');
    if (!overlay) return;
    // The view plays full sessions only — no clip state, no mode switch.
    _trecContact = null;
    _trecSetPrep(false);
    _trecSyncModeBtn();
    const title = row.booking?.studentName
      ? row.booking.studentName + ' · ' + (row.teacherName || '')
      : (row.topic || 'Session');
    document.getElementById('trecPlayerTitle').textContent = title;
    document.getElementById('trecPlayerSub').textContent =
      [_trecFmtDate(row.startTime) + ' ' + _trecFmtTime(row.startTime), _trecFmtDuration(row), row.meetingId,
        row.source === 'archive' ? 'Drive archive' : ''].filter(Boolean).join(' · ');

    const sources = _trecFillSourceSeg(row);

    // The same chapter chips inside the dialog: jump between the tests of the
    // session without closing it.
    const chapters = document.getElementById('trecPlayerChapters');
    if (chapters) {
      clear(chapters);
      const tests = _trecRowTests(row);
      for (const t of tests) chapters.appendChild(_trecChapterChip(row, t, test => _trecSeek(test.offsetSec)));
      chapters.classList.toggle('hidden', !tests.length || !sources.length);
    }

    overlay.classList.remove('hidden');
    document.addEventListener('keydown', _trecPlayerKeydown);
    if (sources.length) _trecLoadSource(row, sources[0], opts.seekSec);
  }

  // The Speaker / Audio switch for `row`; returns the sources, video first.
  // opts (contact player): `active` — the key shown selected; `onPick` — what a
  // click does instead of reloading the full file; `sizes: false` — no full-file
  // size on the labels (a clip is a fraction of it).
  function _trecFillSourceSeg(row, opts = {}) {
    const seg = document.getElementById('trecPlayerSeg');
    clear(seg);
    const sources = [];
    if (row.video) sources.push({ key: 'video', label: 'Speaker', file: row.video });
    if (row.audio) sources.push({ key: 'audio', label: 'Audio', file: row.audio });
    const activeKey = sources.some(s => s.key === opts.active) ? opts.active : sources[0]?.key;
    for (const src of sources) {
      seg.appendChild(el('button', {
        class: 'trec-seg-btn' + (src.key === activeKey ? ' trec-seg-on' : ''),
        type: 'button',
        onclick: ev => {
          seg.querySelectorAll('.trec-seg-btn').forEach(b => b.classList.toggle('trec-seg-on', b === ev.currentTarget));
          if (opts.onPick) { opts.onPick(src); return; }
          // Keep the place: switching to audio on a weak line should not send
          // the reviewer back to the start of a two-hour session.
          const at = document.getElementById('trecPlayerVideo')?.currentTime || 0;
          _trecLoadSource(row, src, at);
        },
      }, [document.createTextNode(opts.sizes === false ? src.label : src.label + ' · ' + _trecFmtSize(src.file.size))]));
    }
    // Hide the switch when there is nothing to switch between.
    seg.classList.toggle('hidden', sources.length < 2);
    return sources;
  }

  // Bumped by every load and by close: a ticket that comes back after the
  // reviewer picked another source — or closed the dialog — is dropped, so a
  // late answer can never start a stream nobody is looking at.
  let _trecLoadGen = 0;
  // `opts.note` — what the footer says once the file is attached (default: nothing).
  async function _trecLoadSource(row, src, seekSec, opts = {}) {
    const video = document.getElementById('trecPlayerVideo');
    const note = document.getElementById('trecPlayerNote');
    if (!video) return;
    const gen = ++_trecLoadGen;
    video.pause();
    video.removeAttribute('src');
    video.load();
    _trecPendingSeek = Number(seekSec) > 0 ? Number(seekSec) : null;
    // Seeking needs the metadata even if autoplay is refused.
    video.preload = _trecPendingSeek != null ? 'metadata' : 'none';
    // An audio-only M4A in a <video> renders as a black frame with working
    // controls — correct, and it saves maintaining a second element.
    video.classList.toggle('trec-player-audio', src.key === 'audio');
    note.textContent = 'Opening…';
    try {
      // A Drive-archive row has no Zoom meeting behind it any more — its file
      // is minted and streamed through the archive routes instead.
      const ticketUrl = row.source === 'archive'
        ? '/api/test-recordings/archive-ticket/' + encodeURIComponent(src.file.fileId)
        : '/api/test-recordings/ticket/' + encodeURIComponent(row.uuid) + '/' + encodeURIComponent(src.file.fileId);
      const r = await api(ticketUrl);
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      if (gen !== _trecLoadGen) return;
      video.src = j.url;
      note.textContent = opts.note || '';
      await video.play().catch(() => { /* the user can press play */ });
    } catch (err) {
      if (gen !== _trecLoadGen) return;
      note.textContent = 'Could not open the recording — ' + (err?.message || err);
    }
  }

  function _trecPlayerKeydown(ev) {
    if (ev.key === 'Escape') _trecClosePlayer();
  }

  function _trecClosePlayer() {
    const overlay = document.getElementById('trecPlayer');
    const video = document.getElementById('trecPlayerVideo');
    _trecPendingSeek = null;
    _trecLoadGen++;          // a ticket still on its way must not start playing
    _trecContact = null;
    _trecSetPrep(false);
    _trecSyncModeBtn();
    if (video) {
      // Drop the source, not just the playback: leaving it attached keeps the
      // range requests alive against Zoom after the dialog is gone.
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    overlay?.classList.add('hidden');
    document.removeEventListener('keydown', _trecPlayerKeydown);
  }

  // ── From the conversation header (2026-10-04) ──────────────
  // Ayoub: the contact's own placement test, played right in the conversation
  // ("mash imchi … itl3 lina live dyalha hena direct"). The same dialog as the
  // view, opened over the inbox, already at the second the test begins. The
  // tests come from /api/test-recordings/for-lead — the contact's own slots and
  // every slot booked on the same number, so on a family line each child's
  // test is its own chip.

  // The server's requireLevelTestReviewer (_canReviewLevelTests): teachers and
  // sales admins. The Test recordings nav item is shown to a wider audience
  // (agents too) that the server then refuses.
  function _canReviewLevelTestsMe() {
    return String(state.me?.role || '').toLowerCase() === 'teacher' || isSalesAdminMe();
  }
  // The header button itself follows requireTestRecordingListener (2026-10-27,
  // Ayoub: "twlli 3nd team kamla"): reviewers plus every sales agent, whom the
  // server serves only the tests of their own leads.
  function _canHearTestRecordingsMe() {
    return _canReviewLevelTestsMe() || String(state.me?.role || '').toLowerCase() === 'agent';
  }

  const TREC_HDR_TIP = 'Test recording';
  const TREC_HDR_NO_LEAD = 'Test recording — no lead for this conversation';
  // The lead the pipeline bar resolved for the open conversation.
  let _trecHdrLead = { chatId: '', leadId: '' };
  let _trecHdrBusy = false;
  // The contact the dialog is playing: its tests + the session on screen.
  let _trecContact = null;

  // Greyed out — not hidden — when there is no lead: the reviewer learns why
  // from the tooltip instead of wondering where the button went.
  function _trecHeaderSetLead(hasLead) {
    const btn = E.testRecordingBtn;
    if (!btn) return;
    btn.setAttribute('aria-disabled', hasLead ? 'false' : 'true');
    btn.setAttribute('data-tip', hasLead ? TREC_HDR_TIP : TREC_HDR_NO_LEAD);
    btn.setAttribute('aria-label', hasLead ? 'Play this contact\'s test recording' : TREC_HDR_NO_LEAD);
  }

  // showConversation: reviewers, one-to-one contacts (no group, no job
  // candidate). Until the pipeline bar reports, a number is the best sign a
  // lead exists; the click resolves it anyway.
  function _trecHeaderSync(chat) {
    const btn = E.testRecordingBtn;
    if (!btn) return;
    const show = _canHearTestRecordingsMe() && !!chat && !chat.isGroup && chat.recruitmentLane !== true;
    btn.classList.toggle('hidden', !show);
    if (!show) return;
    const known = _trecHdrLead.chatId === String(chat.id || '');
    _trecHeaderSetLead(known ? !!_trecHdrLead.leadId : !!pipelineExtractPhone(chat));
  }

  // renderPipelineBar → the lead it resolved for `chatId` (null: none yet, or
  // a colleague's the viewer cannot open).
  function _trecHeaderNoteLead(chatId, lead) {
    if (String(state.selectedChatId || '') !== String(chatId || '')) return;
    _trecHdrLead = { chatId: String(chatId), leadId: lead?.id ? String(lead.id) : '' };
    _trecHeaderSetLead(!!_trecHdrLead.leadId);
  }

  function _trecHdrSetBusy(busy) {
    _trecHdrBusy = !!busy;
    const btn = E.testRecordingBtn;
    if (!btn) return;
    btn.classList.toggle('is-loading', !!busy);
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function _trecContactPlayable(test) {
    return !!(test && test.recording && (test.recording.video || test.recording.audio));
  }
  // '2026-09-18' → '18/09/2026'. The slot's own Morocco wall date, as text —
  // no instant behind it, so nothing to convert.
  function _trecFmtSlotDay(day) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
    return m ? m[3] + '/' + m[2] + '/' + m[1] : '';
  }

  // Nothing to play: say so, and name what is booked but not recorded yet (the
  // archive copies the day's tests overnight, so today's appears tomorrow).
  function _trecNoRecordingText(tests, errors) {
    const base = 'No recorded test for this contact';
    if (!tests.length) return base;
    const latest = tests[0];
    const who = [latest.studentName || 'Test', _trecFmtSlotDay(latest.slotDate)].filter(Boolean).join(', ');
    const pending = tests.length === 1
      ? who + ': not recorded yet'
      : tests.length + ' tests not recorded yet (latest: ' + who + ')';
    const err = (errors || [])[0];
    const down = err ? ' · ' + (err.teacher === 'archive' ? 'Drive archive' : (err.teacher || 'Zoom')) + ' unreachable' : '';
    return base + ' — ' + pending + down;
  }

  // One chip per test of the contact: student · level · date time. A recorded
  // one plays it; one not recorded yet stays visible, greyed.
  function _trecContactChip(test, active, onPick) {
    const playable = _trecContactPlayable(test);
    const when = [_trecFmtSlotDay(test.slotDate), test.slotTime].filter(Boolean).join(' ');
    const label = [test.studentName || 'Student', test.level, when].filter(Boolean).join(' · ');
    const kids = [
      el('span', { class: 'trec-chapter-dot', 'aria-hidden': 'true' }),
      el('span', { class: 'trec-chapter-name', dir: 'auto', text: test.studentName || 'Student' }),
    ];
    if (test.level) {
      kids.push(el('span', { class: 'trec-chapter-sep', 'aria-hidden': 'true', text: '·' }));
      kids.push(el('span', { class: 'trec-chapter-level', text: test.level }));
    }
    if (when) {
      kids.push(el('span', { class: 'trec-chapter-sep', 'aria-hidden': 'true', text: '·' }));
      kids.push(el('span', { class: 'trec-chapter-time', text: when }));
    }
    if (!playable) kids.push(el('span', { class: 'trec-chapter-pending', text: 'not recorded yet' }));
    return el('button', {
      class: 'trec-chapter trec-chapter-' + String(test.status || 'unknown').replace(/[^a-z_]/gi, '')
        + (active ? ' trec-chapter-on' : ''),
      type: 'button',
      disabled: playable ? undefined : 'disabled',
      'aria-pressed': active ? 'true' : 'false',
      title: playable
        ? 'Play ' + label + ' — from ' + _trecFmtOffset(test.recording.offsetSec)
        : label + ' — not recorded yet',
      onclick: ev => { ev.stopPropagation(); onPick(test); },
    }, kids);
  }

  // Seek inside the session already loaded — 0:00 included (_trecSeek ignores
  // it, which is right for a chapter chip but not for a test that opens the
  // recording).
  function _trecSeekTo(sec) {
    const s = Math.max(0, Number(sec) || 0);
    if (s > 0) { _trecSeek(s); return; }
    const video = document.getElementById('trecPlayerVideo');
    _trecPendingSeek = null;
    if (!video || !video.currentSrc || video.readyState < 1) return;
    try { video.currentTime = 0; } catch { /* */ }
    video.play().catch(() => { /* the user can press play */ });
  }

  // ── Per-test clip (2026-10-04) ──────────────────────────────
  // Ayoub: the header player was "t9il bezaaf". An archived session is the
  // teacher's whole room — two hours, ~733 MB, a multi-MB index to download
  // before the first frame and a round trip per seek on a slow line. So the
  // header plays a clip of just this test, cut on the server by stream copy
  // and served from its disk: the first open of a test waits for the cut
  // (~10 s), every later one is instant. A Zoom-cloud session (under a day
  // old) and any clip the server cannot cut play the full file as before;
  // "Full session" switches to it on demand, at the same moment of the test.

  function _trecSetPrep(on, title, sub) {
    const prep = document.getElementById('trecPlayerPrep');
    if (!prep) return;
    prep.classList.toggle('hidden', !on);
    document.getElementById('trecPlayerCard')?.setAttribute('aria-busy', on ? 'true' : 'false');
    if (!on) return;
    const t = document.getElementById('trecPlayerPrepTitle');
    const s = document.getElementById('trecPlayerPrepSub');
    if (t) t.textContent = title || '';
    if (s) { s.textContent = sub || ''; s.classList.toggle('hidden', !sub); }
  }

  // The footer switch: a clip offers the full session, the full session
  // offers the clip back. Hidden when no clip can exist (view, Zoom cloud).
  function _trecSyncModeBtn() {
    const btn = document.getElementById('trecPlayerModeBtn');
    if (!btn) return;
    const c = _trecContact;
    // An agent is served an archived session as the test's clip only.
    const show = !!c && c.clipAble && _canReviewLevelTestsMe();
    btn.classList.toggle('hidden', !show);
    if (!show) return;
    const toFull = c.mode === 'clip';
    clear(btn);
    btn.appendChild(el('i', { 'data-lucide': toFull ? 'film' : 'scissors', 'aria-hidden': 'true' }));
    btn.appendChild(document.createTextNode(toFull ? 'Full session' : 'This test only'));
    btn.title = toFull
      ? 'Play the whole recorded session, from this moment of the test'
      : 'Back to the light clip of this test';
    refreshIcons();
  }

  // The Speaker / Audio switch of the contact player. A clip shows no file
  // size — the full file's would be wrong by a factor of ten.
  function _trecContactSeg() {
    const c = _trecContact;
    if (!c) return;
    c.sources = _trecFillSourceSeg(c.row, { active: c.kind, onPick: _trecContactPickKind, sizes: c.mode !== 'clip' });
    if (!c.sources.some(s => s.key === c.kind)) c.kind = c.sources[0]?.key || c.kind;
  }
  function _trecContactSrc() {
    const c = _trecContact;
    return (c?.sources || []).find(s => s.key === c.kind) || c?.sources?.[0] || null;
  }

  // Seconds since the test began, from whatever is playing (0 before anything is).
  function _trecRelToTest() {
    const c = _trecContact;
    const video = document.getElementById('trecPlayerVideo');
    if (!c || !video || !video.currentSrc || video.readyState < 1) return 0;
    const t = video.currentTime || 0;
    return c.mode === 'clip' ? (c.clip ? t - c.clip.startSec : 0) : t - (Number(c.offsetSec) || 0);
  }

  // Ask the server for this test's clip of the current kind, wait for it to be
  // cut, then play it `relSec` after the test's start. Anything short of a clip
  // ({clip:false}, busy, cut failed, timed out) → the full session, same place.
  async function _trecLoadClip(relSec) {
    const c = _trecContact;
    const video = document.getElementById('trecPlayerVideo');
    const note = document.getElementById('trecPlayerNote');
    if (!c || !video) return;
    const src = _trecContactSrc();
    if (!src) return;
    const gen = ++_trecLoadGen;
    c.mode = 'clip';
    c.clip = null;
    _trecContactSeg();
    _trecSyncModeBtn();
    video.pause();
    video.removeAttribute('src');
    video.load();
    _trecPendingSeek = null;
    video.classList.toggle('trec-player-audio', src.key === 'audio');
    if (note) note.textContent = '';
    _trecSetPrep(true, 'Opening the test…', '');
    const rel = Number(relSec) || 0;
    const toFull = () => {
      if (gen !== _trecLoadGen || _trecContact !== c) return;
      if (!_canReviewLevelTestsMe()) {
        _trecSetPrep(false);
        if (note) note.textContent = 'The clip of this test is not ready — try again in a minute';
        return;
      }
      _trecPlayFull(rel, 'Clip not available — playing the full session');
    };
    try {
      const r = await api('/api/test-recordings/clip-ticket/' + encodeURIComponent(c.slotId)
        + '?kind=' + encodeURIComponent(src.key));
      if (gen !== _trecLoadGen) return;
      if (!r.ok) return toFull();
      const j = await r.json();
      if (gen !== _trecLoadGen) return;
      if (!j.clip || !j.url) return toFull();
      if (!j.ready) _trecSetPrep(true, 'Preparing the clip…', 'First time ~10 s — then it opens instantly');
      // The HEAD answers once the clip is cut and on the server's disk.
      const head = await fetchT(j.url, { method: 'HEAD' }, 150000);
      if (gen !== _trecLoadGen) return;
      if (!head.ok) return toFull();
      const dur = Math.max(0, Number(j.durationSec) || 0);
      const startSec = Math.max(0, Number(j.startSec) || 0);
      c.clip = { startSec, durationSec: dur };
      const at = Math.min(Math.max(0, startSec + rel), Math.max(0, dur - 2));
      _trecPendingSeek = at > 0 ? at : null;
      video.preload = 'auto';
      video.src = j.url;
      _trecSetPrep(false);
      if (note) note.textContent = 'This test only · ' + _trecFmtOffset(dur);
      await video.play().catch(() => { /* the user can press play */ });
    } catch {
      toFull();
    }
  }

  // The full session file (the pre-clip behaviour), `relSec` after the test's start.
  function _trecPlayFull(relSec, noteText) {
    const c = _trecContact;
    if (!c) return;
    _trecSetPrep(false);
    c.mode = 'full';
    c.clip = null;
    _trecContactSeg();
    _trecSyncModeBtn();
    const src = _trecContactSrc();
    if (!src) return;
    const abs = Math.max(0, (Number(c.offsetSec) || 0) + (Number(relSec) || 0));
    _trecLoadSource(c.row, src, abs, { note: noteText || '' });
  }

  // Speaker ⇄ Audio in the contact player: the other kind, same moment of the test.
  function _trecContactPickKind(src) {
    const c = _trecContact;
    if (!c || c.kind === src.key) return;
    const rel = _trecRelToTest();
    c.kind = src.key;
    if (c.mode === 'clip') _trecLoadClip(rel);
    else _trecPlayFull(rel);
  }

  function _trecToggleMode() {
    const c = _trecContact;
    if (!c || !c.clipAble) return;
    const rel = _trecRelToTest();
    if (c.mode === 'clip') _trecPlayFull(rel);
    else _trecLoadClip(rel);
  }

  // A clip that stops loading mid-playback: usually its 5-minute ticket ran
  // out before a seek past the buffer. Re-open it at the same place with a
  // fresh ticket — at most once a minute; a clip that keeps failing gives way
  // to the full session.
  function _trecClipError(ev) {
    const c = _trecContact;
    const video = ev.currentTarget;
    if (!c || c.mode !== 'clip' || !c.clip || !video?.getAttribute('src')) return;
    const rel = (video.currentTime || 0) - c.clip.startSec;
    if (!c.clipRetryAt || Date.now() - c.clipRetryAt > 60000) {
      c.clipRetryAt = Date.now();
      _trecLoadClip(rel);
    } else {
      _trecPlayFull(rel, 'Clip not available — playing the full session');
    }
  }

  // The contact's tests in the shared dialog: title "student · level · teacher
  // · date", the test's clip (or the session seeked to where it begins), and —
  // when there are several — the chips to switch between them. In full-session
  // mode a test in the session on screen only seeks; otherwise it loads.
  function _trecOpenContactPlayer(tests, test) {
    const overlay = document.getElementById('trecPlayer');
    const rec = test && test.recording;
    if (!overlay || !rec) return;
    _trecWirePlayer();
    const prev = !overlay.classList.contains('hidden') ? _trecContact : null;
    const sameSession = !!prev && prev.mode === 'full'
      && prev.uuid === rec.uuid && prev.source === rec.source;
    const sameClip = !!prev && prev.mode === 'clip' && !!prev.clip && String(prev.slotId) === String(test.slotId);
    const clipAble = rec.source === 'archive';
    const c = {
      tests, slotId: test.slotId, uuid: rec.uuid, source: rec.source, offsetSec: rec.offsetSec,
      row: { source: rec.source, uuid: rec.uuid, video: rec.video, audio: rec.audio },
      clipAble,
      mode: sameSession || !clipAble ? 'full' : 'clip',
      // Speaker first — a clip is light — and the reviewer's pick kept between tests.
      kind: prev?.kind || (rec.video ? 'video' : 'audio'),
      sources: [],
      clip: sameClip ? prev.clip : null,
      clipRetryAt: 0,
    };
    _trecContact = c;

    document.getElementById('trecPlayerTitle').textContent =
      [test.studentName || 'Student', test.level, test.teacherName, _trecFmtSlotDay(test.slotDate)]
        .filter(Boolean).join(' · ');
    document.getElementById('trecPlayerSub').textContent = [
      test.slotTime ? 'Test ' + test.slotTime : '',
      'from ' + _trecFmtOffset(rec.offsetSec) + ' in the recording',
      rec.source === 'archive' ? 'Drive archive' : 'Zoom cloud',
    ].filter(Boolean).join(' · ');

    const chapters = document.getElementById('trecPlayerChapters');
    if (chapters) {
      clear(chapters);
      for (const t of tests) {
        chapters.appendChild(_trecContactChip(t, String(t.slotId) === String(test.slotId),
          picked => _trecOpenContactPlayer(tests, picked)));
      }
      chapters.classList.toggle('hidden', tests.length < 2);
    }

    overlay.classList.remove('hidden');
    document.addEventListener('keydown', _trecPlayerKeydown);
    _trecContactSeg();
    _trecSyncModeBtn();
    if (sameSession) { _trecSeekTo(rec.offsetSec); return; }
    if (sameClip) { _trecSeekTo(c.clip.startSec); return; }
    if (!c.sources.length) return;
    if (c.mode === 'clip') _trecLoadClip(0);
    else _trecPlayFull(0);
  }

  // Header click: resolve the person on screen, fetch their tests, open the
  // newest recorded one — theirs first on a shared number.
  async function openContactTestRecording() {
    const chatId = String(state.selectedChatId || '');
    if (!chatId || _trecHdrBusy || !_canHearTestRecordingsMe()) return;
    if (E.testRecordingBtn?.getAttribute('aria-disabled') === 'true') {
      toast('No lead for this conversation — no test to play');
      return;
    }
    _trecHdrSetBusy(true);
    try {
      // The shared-number pick, else the bar's lead, else the bar's own lookup.
      let leadId = _activePersonLeadId(chatId)
        || (_trecHdrLead.chatId === chatId ? _trecHdrLead.leadId : '');
      if (!leadId) leadId = String((await fetchLeadForChat(chatId))?.id || '');
      if (String(state.selectedChatId || '') !== chatId) return;
      if (!leadId) {
        _trecHeaderNoteLead(chatId, null);
        toast('No lead for this conversation — no test to play');
        return;
      }
      const r = await api('/api/test-recordings/for-lead/' + encodeURIComponent(leadId));
      if (r.status === 403) {
        toast('This contact is a colleague\'s lead — only its owner or a sales admin can play the test');
        return;
      }
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      if (String(state.selectedChatId || '') !== chatId) return;
      const tests = Array.isArray(j.tests) ? j.tests : [];
      const recorded = tests.filter(_trecContactPlayable);
      if (!recorded.length) {
        toast(_trecNoRecordingText(tests, j.errors));
        return;
      }
      _trecOpenContactPlayer(tests, recorded.find(t => t.forThisLead) || recorded[0]);
    } catch (err) {
      toast('Could not load the test recording — ' + (err?.message || err), 'error');
    } finally {
      _trecHdrSetBusy(false);
    }
  }

  // ─── Tests with no result (2026-10-12, Ayoub) ────────────────────────────
  // A seat the student joined (in_test) whose slot started 45+ min ago (and at
  // most 7 days ago, older ones are dropped server-side) and has no level yet — the teacher never recorded the assessment, so the lead stays
  // "Test scheduled" and the contact has no level. The server
  // (/api/test-schedule/missing-results) scopes a teacher to their own seats.
  // Each row opens the SAME assessment modal as a card (openTsActionsModal), so
  // there is one way to record a level. Rendered into #ttMissing (teacher
  // "My tests") and #testsMissing (admin Tests, sales admins only); both reload
  // with their view, which _tsRefreshAfterModalAction already triggers after a
  // save — the row disappears once the level is in.
  const _mrState = { expanded: {}, seq: {} };
  const MR_PREVIEW_ROWS = 5;

  async function _mrLoad(hostId) {
    const host = document.getElementById(hostId);
    if (!host) return;
    const seq = (_mrState.seq[hostId] || 0) + 1;
    _mrState.seq[hostId] = seq;
    let rows = [];
    try {
      const r = await api('/api/test-schedule/missing-results');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      rows = Array.isArray(j.slots) ? j.slots : [];
    } catch (err) {
      // Never block the view on this strip — just keep it hidden.
      console.warn('[missing-results] load failed:', err?.message || err);
      rows = [];
    }
    if (_mrState.seq[hostId] !== seq) return; // a newer load already rendered
    _mrRender(host, hostId, rows);
  }

  function _mrRender(host, hostId, rows) {
    clear(host);
    if (!rows.length) { host.classList.add('hidden'); return; }
    host.classList.remove('hidden');
    const me = state.me || {};
    const showTeacher = String(me.role || '').toLowerCase() !== 'teacher' || me.teacherManager === true;
    const expanded = !!_mrState.expanded[hostId];
    const n = rows.length;
    const head = el('div', { class: 'mr-head' }, [
      el('span', { class: 'mr-icon' }, [lucideIcon('alert-triangle')]),
      el('div', { class: 'mr-head-copy' }, [
        el('strong', { class: 'mr-title', text: n + (n === 1 ? ' test without a result' : ' tests without a result') }),
        el('span', { class: 'mr-sub', text: 'The student joined the test in the last 7 days, but no level was saved. Open each one and record the assessment.' }),
      ]),
    ]);
    if (n > MR_PREVIEW_ROWS) {
      const toggle = el('button', { type: 'button', class: 'mr-toggle', text: expanded ? 'Show less' : ('Show all ' + n) });
      toggle.addEventListener('click', () => {
        _mrState.expanded[hostId] = !expanded;
        _mrRender(host, hostId, rows);
      });
      head.appendChild(toggle);
    }
    host.appendChild(head);
    const list = el('div', { class: 'mr-list', role: 'list' });
    for (const r of (expanded ? rows : rows.slice(0, MR_PREVIEW_ROWS))) list.appendChild(_mrRow(r, showTeacher));
    host.appendChild(list);
    refreshIcons(host);
  }

  function _mrRow(r, showTeacher) {
    const name = r.lead_name || r.student_name || '(no name)';
    const meta = [_testsFmtDate(r.slot_date) + ' · ' + _testsFmtTime(r.slot_time)];
    if (r.seat_no) meta.push('Seat ' + r.seat_no);
    if (showTeacher) meta.push(r.teacher_name || r.teacher_display_name || 'Teacher');
    if (r.zoom_minutes != null && Number.isFinite(Number(r.zoom_minutes))) {
      const m = Math.max(0, Number(r.zoom_minutes));
      meta.push(m < 1 ? 'left Zoom in under 1 min' : ('~' + m + ' min in Zoom'));
    }
    const nameEl = el('span', { class: 'mr-name', text: name });
    const row = el('button', { type: 'button', class: 'mr-row', role: 'listitem', title: 'Open the assessment' }, [
      el('span', { class: 'mr-row-copy' }, [
        nameEl,
        el('span', { class: 'mr-meta', text: meta.join(' · ') }),
      ]),
      el('span', { class: 'mr-cta' }, [el('span', { text: 'Record result' }), lucideIcon('arrow-right')]),
    ]);
    if (r.name_unconfirmed) nameEl.appendChild(_nameUnconfirmedBadge());
    row.addEventListener('click', () => { try { openTsActionsModal(r); } catch (_) { /* noop */ } });
    return row;
  }

  function ensureTeacherTestsInit() {
    state.teacherTests = state.teacherTests || { inited: false, from: '', to: '', rows: [], filter: 'all', search: '', view: 'cards' };
    const st = state.teacherTests;
    if (st.inited) { _ttLoad(); return; }
    st.inited = true;
    try { st.view = localStorage.getItem('leads7.ttView') === 'list' ? 'list' : 'cards'; } catch { st.view = 'cards'; }
    st.to = _testsTodayIso();
    st.from = _testsAddDays(st.to, -29);            // default: last 30 days
    const fromEl = document.getElementById('ttFrom');
    const toEl = document.getElementById('ttTo');
    if (fromEl) fromEl.value = st.from;
    if (toEl) toEl.value = st.to;
    fromEl?.addEventListener('change', ev => { st.from = ev.target.value; _ttLoad(); });
    toEl?.addEventListener('change', ev => { st.to = ev.target.value; _ttLoad(); });
    document.getElementById('ttRefreshBtn')?.addEventListener('click', _ttLoad);
    _tlWireCrossLinks();
    document.querySelectorAll('#teacherTestsView .tt-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        st.filter = chip.getAttribute('data-filter') || 'all';
        document.querySelectorAll('#teacherTestsView .tt-chip').forEach(c => c.classList.toggle('tt-chip-on', c === chip));
        _ttRender();
      });
    });
    document.getElementById('ttSearch')?.addEventListener('input', ev => {
      st.search = (ev.target.value || '').trim().toLowerCase(); _ttRender();
    });
    // Cards / list toggle (persisted across sessions).
    document.querySelectorAll('#teacherTestsView .tt-vt-btn').forEach(btn => {
      btn.classList.toggle('tt-vt-on', btn.getAttribute('data-ttview') === st.view);
      btn.addEventListener('click', () => {
        st.view = btn.getAttribute('data-ttview') === 'list' ? 'list' : 'cards';
        try { localStorage.setItem('leads7.ttView', st.view); } catch { /* */ }
        document.querySelectorAll('#teacherTestsView .tt-vt-btn').forEach(b => b.classList.toggle('tt-vt-on', b === btn));
        _ttRender();
      });
    });
    _ttLoad();
  }

  async function _ttLoad() {
    const st = state.teacherTests; if (!st) return;
    const loading = document.getElementById('ttLoading');
    const empty = document.getElementById('ttEmpty');
    if (loading) loading.classList.remove('hidden');
    if (empty) empty.classList.add('hidden');
    _mrLoad('ttMissing'); // independent of the date range — never awaited
    try {
      const qs = new URLSearchParams({ from: st.from, to: st.to });
      const r = await api('/api/test-schedule/bookings?' + qs.toString());
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      st.rows = j.slots || [];
    } catch (err) {
      console.warn('[teacher-tests] load failed:', err?.message || err);
      st.rows = [];
    } finally {
      if (loading) loading.classList.add('hidden');
      _ttRender();
    }
  }

  function _ttRender() {
    const st = state.teacherTests; if (!st) return;
    const host = document.getElementById('ttCards');
    const empty = document.getElementById('ttEmpty');
    const countEl = document.getElementById('ttResultCount');
    if (!host) return;
    clear(host);
    // Chip counts reflect the full date-range set (not the active filter).
    const counts = { all: st.rows.length, booked: 0, confirmed: 0, in_test: 0, completed: 0, no_show: 0, cancelled: 0 };
    for (const r of st.rows) if (counts[r.status] !== undefined) counts[r.status]++;
    for (const k of Object.keys(counts)) {
      const c = document.querySelector('#teacherTestsView .tt-chip-count[data-count="' + k + '"]');
      if (c) c.textContent = counts[k];
    }
    let rows = st.rows;
    if (st.filter !== 'all') rows = rows.filter(r => r.status === st.filter);
    if (st.search) {
      rows = rows.filter(r => [r.lead_name, r.student_name, r.notes, r.sales_note, r.booked_by, r.teacher_recommendation]
        .map(x => (x || '').toString().toLowerCase()).join(' ').includes(st.search));
    }
    rows = rows.slice().sort((a, b) => {
      const da = String(a.slot_date || '').slice(0, 10), db = String(b.slot_date || '').slice(0, 10);
      if (da !== db) return db.localeCompare(da);
      return String(b.slot_time || '').localeCompare(String(a.slot_time || ''));
    });
    if (countEl) countEl.textContent = rows.length ? (rows.length + (rows.length === 1 ? ' test' : ' tests')) : '';
    if (!rows.length) { if (empty) empty.classList.remove('hidden'); return; }
    if (empty) empty.classList.add('hidden');
    const isList = st.view === 'list';
    host.classList.toggle('tt-cards', !isList);
    host.classList.toggle('tt-list', isList);
    if (isList) host.appendChild(_ttTable(rows));
    else for (const r of rows) host.appendChild(_ttCard(r));
    refreshIcons(host);
  }

  function _ttCard(r) {
    const status = r.status || 'booked';
    const card = el('div', { class: 'tt-card', 'data-status': status, title: 'Click to view / evaluate' });
    card.appendChild(el('div', { class: 'tt-card-head' }, [
      el('div', { class: 'tt-when' }, [
        el('span', { class: 'tt-when-date', text: _testsFmtDate(r.slot_date) }),
        el('span', { class: 'tt-when-time', text: _testsFmtTime(r.slot_time) }),
      ]),
      el('span', { class: 'tt-status tt-status-' + (_testsStatusKind(status) || 'neutral'), text: _testsStatusLabel(status) }),
    ]));
    const ttName = el('div', { class: 'tt-name' }, [el('span', { text: r.lead_name || r.student_name || '(no name)' })]);
    if (r.name_unconfirmed) ttName.appendChild(_nameUnconfirmedBadge());
    card.appendChild(ttName);
    const meta = el('div', { class: 'tt-meta' });
    if (r.lead_pipeline_stage) meta.appendChild(el('span', { class: 'tt-tag tt-tag-stage', text: (PIPELINE_LABELS[r.lead_pipeline_stage] || r.lead_pipeline_stage) }));
    if (r.new_level) meta.appendChild(el('span', { class: 'cefr-pill cefr-' + r.new_level, text: r.new_level }));
    if (r.score != null && r.score !== '') meta.appendChild(el('span', { class: 'tt-tag', text: 'Score ' + r.score }));
    if (r.teacher_recommendation) meta.appendChild(el('span', { class: 'tt-tag tt-rec-' + _testsRecKind(r.teacher_recommendation), text: r.teacher_recommendation }));
    if (meta.children.length) card.appendChild(meta);
    if (r.notes) card.appendChild(el('div', { class: 'tt-notes' }, [lucideIcon('sticky-note'), el('span', { text: r.notes })]));
    // Sales note (read-only, from the agent who booked) — distinct from the
    // teacher's own eval notes above. Different icon + class so it never
    // reads as something the teacher wrote.
    if (r.sales_note) card.appendChild(el('div', { class: 'tt-salesnote' }, [
      lucideIcon('target'),
      el('div', { class: 'tt-salesnote-copy' }, [
        el('strong', { text: 'Client goal & challenge' }),
        el('span', { text: r.sales_note }),
      ]),
    ]));
    const foot = el('div', { class: 'tt-foot' });
    if (r.booked_by) foot.appendChild(el('span', { class: 'tt-by' }, [lucideIcon('headset'), el('span', {}, [document.createTextNode('by '), el('strong', { text: bookedByLabel(r) })])]));
    if (r.booked_at) foot.appendChild(el('span', { class: 'tt-at', text: _testsFmtRelative(r.booked_at) }));
    if (foot.children.length) card.appendChild(foot);
    card.addEventListener('click', () => { try { openTsActionsModal(r); } catch (_) { /* noop */ } });
    return card;
  }

  // List view = a spreadsheet-style table (like the admin "Tests" view). Click
  // a row → the same eval/actions modal so the teacher can edit status / level /
  // score / recommendation / notes. No Teacher column (it's their own) and no
  // phone (PII stays locked for teachers).
  function _ttTable(rows) {
    const wrap = el('div', { class: 'tt-table-scroll' });
    const table = el('table', { class: 'tt-table' });
    const thead = el('thead');
    const htr = el('tr');
    ['Date', 'Time', 'Student', 'Pipeline', 'Status', 'Score', 'Level', 'Recommendation', 'Teacher notes', 'Agent note', 'Booked by', 'Booked on']
      .forEach(h => htr.appendChild(el('th', { text: h })));
    thead.appendChild(htr);
    table.appendChild(thead);
    const tb = el('tbody');
    for (const r of rows) tb.appendChild(_ttTableRow(r));
    table.appendChild(tb);
    wrap.appendChild(table);
    return wrap;
  }

  function _ttTableRow(r) {
    const status = r.status || 'booked';
    const dash = () => document.createTextNode('—');
    const tr = el('tr', { class: 'tt-tr', title: 'Click to view / edit' });
    tr.appendChild(el('td', { class: 'tt-td-date', text: _testsFmtDate(r.slot_date) }));
    tr.appendChild(el('td', { class: 'tt-td-time', text: _testsFmtTime(r.slot_time) }));
    const ttNameTd = el('td', { class: 'tt-td-name' }, [el('span', { text: r.lead_name || r.student_name || '—' })]);
    if (r.name_unconfirmed) ttNameTd.appendChild(_nameUnconfirmedBadge());
    tr.appendChild(ttNameTd);
    tr.appendChild(el('td', {}, r.lead_pipeline_stage ? [el('span', { class: 'tt-tag tt-tag-stage', text: (PIPELINE_LABELS[r.lead_pipeline_stage] || r.lead_pipeline_stage) })] : [dash()]));
    tr.appendChild(el('td', {}, [el('span', { class: 'tt-status tt-status-' + (_testsStatusKind(status) || 'neutral'), text: _testsStatusLabel(status) })]));
    tr.appendChild(el('td', { class: 'tt-td-num', text: (r.score != null && r.score !== '') ? String(r.score) : '—' }));
    tr.appendChild(el('td', {}, r.new_level ? [el('span', { class: 'cefr-pill cefr-' + r.new_level, text: r.new_level })] : [dash()]));
    tr.appendChild(el('td', {}, r.teacher_recommendation ? [el('span', { class: 'tt-tag tt-rec-' + _testsRecKind(r.teacher_recommendation), text: r.teacher_recommendation })] : [dash()]));
    tr.appendChild(el('td', { class: 'tt-td-notes', title: r.notes || '', text: r.notes ? (r.notes.slice(0, 48) + (r.notes.length > 48 ? '…' : '')) : '—' }));
    tr.appendChild(el('td', { class: 'tt-td-notes', title: r.sales_note || '', text: r.sales_note ? (r.sales_note.slice(0, 48) + (r.sales_note.length > 48 ? '…' : '')) : '—' }));
    tr.appendChild(el('td', { class: 'tt-td-by', text: bookedByLabel(r) || '—' }));
    tr.appendChild(el('td', { class: 'tt-td-by', text: r.booked_at ? _testsFmtRelative(r.booked_at) : '—' }));
    tr.addEventListener('click', () => { try { openTsActionsModal(r); } catch (_) { /* noop */ } });
    return tr;
  }

  // Agent filter dropdown. Sales admins only — a plain `agent` is already scoped
  // server-side to their own rows (_tsIsScopedAgent), so a list of colleagues
  // could only ever come back empty; same gate as the Contacts "Team" filter and
  // the Rest owner filter. Option value is the USERNAME — what test_slots.booked_by
  // and leads.agent_id both store. The roster comes from state.assignableUsers
  // (active-only, loaded at boot for every role); never hardcode it, every
  // hardcoded list in this repo is stale.
  //   "Sales team" = active agent / super_sales accounts. NOT also filtered on
  // hubspotOwnerId the way _restRenderOwnerFilter does: that filter is keyed on
  // owner ids so it must have them, we key on username — and requiring one here
  // would silently drop mmehanane and fouad, real super_sales accounts with no
  // owner id (checked against the prod roster 2026-07-17; mmehanane has booked).
  // Admins stay out (sales rows are never theirs) — bookings made by ayoub, the
  // bot, or a departed agent remain reachable under "All agents".
  function _testsPopulateAgentFilter() {
    const sel = document.getElementById('testsAgentFilter');
    if (!sel) return;
    if (!_isSalesAdmin()) {
      sel.classList.add('hidden');
      state.testsView.agent = 'all';
      return;
    }
    sel.classList.remove('hidden');
    const prev = state.testsView.agent || 'all';
    while (sel.children.length > 1) sel.removeChild(sel.lastChild);  // keep the "All agents" option
    const agents = (state.assignableUsers || [])
      .filter(u => ['agent', 'super_sales'].includes(String(u.role || '').toLowerCase()))
      .sort((a, b) => (userLabel(a.username, a.displayName || a.username) || '').localeCompare(userLabel(b.username, b.displayName || b.username) || ''));
    for (const u of agents) {
      sel.appendChild(el('option', { value: u.username, text: userLabel(u.username, u.displayName || u.username) }));
    }
    // Keep the current pick across rebuilds — the roster can land after the
    // first render, and every view re-entry repopulates.
    sel.value = Array.from(sel.options).some(o => o.value === prev) ? prev : 'all';
    state.testsView.agent = sel.value;
  }

  // Teacher filter (Ayoub 2026-10-04: "agent Meriem + today + Done" and also
  // pick the teacher). Built from the rows just loaded, so it lists exactly the
  // teachers who ran tests in this range. A picked teacher with no rows in a
  // new range stays in the list, so the pick survives a date change and simply
  // reads 0. Hidden while only one teacher is in view — a scoped teacher only
  // ever receives their own rows.
  function _testsPopulateTeacherFilter() {
    const names = new Map();
    for (const r of state.testsView.rows || []) {
      const id = String(r.teacher_id || '');
      if (id && !names.has(id)) names.set(id, r.teacher_display_name || r.teacher_name || 'Teacher');
    }
    _testsFillRowFilter('testsTeacherFilter', 'teacher', names, (a, b) => a[1].localeCompare(b[1]));
  }

  // Pipeline filter (Ayoub 2026-10-04: "wach paid wla b9a ghir test done wla
  // sent proforma wla no show") — where the person stands NOW in the sales
  // pipeline (leads.pipeline_stage), not the test's own status. Same rules as
  // the teacher filter; options in pipeline order with the pipeline bar labels.
  function _testsPopulateStageFilter() {
    const names = new Map();
    for (const r of state.testsView.rows || []) {
      const s = String(r.lead_pipeline_stage || '');
      if (s && !names.has(s)) names.set(s, PIPELINE_LABELS[s] || s);
    }
    const rank = s => { const i = PIPELINE_ALL.indexOf(s); return i < 0 ? PIPELINE_ALL.length : i; };
    _testsFillRowFilter('testsStageFilter', 'stage', names, (a, b) => rank(a[0]) - rank(b[0]) || a[1].localeCompare(b[1]));
  }

  // Shared by the row-derived filters: rebuild the options after the first
  // ("All …") one, keep the current pick even when it has no row in this range
  // (it then reads 0 instead of silently resetting), hide while there is
  // nothing to choose between.
  function _testsFillRowFilter(selId, key, names, order) {
    const sel = document.getElementById(selId);
    if (!sel) return;
    const prev = state.testsView[key] || 'all';
    if (prev !== 'all' && !names.has(prev)) {
      const known = Array.from(sel.options).find(o => o.value === prev);
      names.set(prev, known ? known.textContent : prev);
    }
    while (sel.children.length > 1) sel.removeChild(sel.lastChild);
    for (const [value, text] of Array.from(names.entries()).sort(order)) sel.appendChild(el('option', { value, text }));
    sel.value = names.has(prev) ? prev : 'all';
    state.testsView[key] = sel.value;
    sel.classList.toggle('hidden', names.size < 2 && state.testsView[key] === 'all');
  }

  function ensureTestsViewInit() {
    if (state.testsView.inited) {
      _testsPopulateAgentFilter();
      _testsViewLoad();
      return;
    }
    state.testsView.inited = true;
    _testsPopulateAgentFilter();
    state.testsView.to = _testsTodayIso();
    state.testsView.from = _testsAddDays(state.testsView.to, -6);  // last 7 days
    const fromEl = document.getElementById('testsFromDate');
    const toEl = document.getElementById('testsToDate');
    if (fromEl) fromEl.value = state.testsView.from;
    if (toEl) toEl.value = state.testsView.to;
    fromEl?.addEventListener('change', (ev) => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(ev.target.value)) {
        state.testsView.from = ev.target.value;
        _testsViewLoad();
      }
    });
    toEl?.addEventListener('change', (ev) => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(ev.target.value)) {
        state.testsView.to = ev.target.value;
        _testsViewLoad();
      }
    });
    document.getElementById('testsRefreshBtn')?.addEventListener('click', _testsViewLoad);
    document.getElementById('testsTodayBtn')?.addEventListener('click', () => {
      const today = _testsTodayIso();
      state.testsView.from = today;
      state.testsView.to = today;
      if (fromEl) fromEl.value = today;
      if (toEl) toEl.value = today;
      _testsViewLoad();
    });
    // Filter chip clicks
    document.getElementById('testsFilters')?.addEventListener('click', (ev) => {
      const btn = ev.target.closest('.tests-chip');
      if (!btn) return;
      const filter = btn.dataset.filter;
      if (!filter) return;
      state.testsView.filter = filter;
      document.querySelectorAll('.tests-chip').forEach(b => b.classList.toggle('is-active', b === btn));
      _testsViewRender();
    });
    // Search box. Typing anything real re-asks the SERVER over the whole history
    // instead of filtering the loaded date range — that's the only way a phone
    // lookup can find a test whose date you don't know, and the only way it can
    // match at all for a user whose phones come back masked. Debounced so a
    // 12-digit number is one request, not twelve. Under 2 chars there's nothing
    // worth a round-trip, so the local filter handles it.
    let _testsSearchTimer = null;
    document.getElementById('testsSearch')?.addEventListener('input', (ev) => {
      const raw = (ev.target.value || '').trim();
      state.testsView.search = raw.toLowerCase();
      clearTimeout(_testsSearchTimer);
      if (raw.length < 2) {
        // Cleared/too short → drop back to the date range if we had left it.
        if (state.testsView.globalQuery) {
          state.testsView.globalQuery = '';
          _testsViewLoad();
        } else {
          _testsViewRender();
        }
        return;
      }
      // Repaint immediately with what's already loaded so the box feels live,
      // then let the debounce widen the net to the full history.
      _testsViewRender();
      _testsSearchTimer = setTimeout(() => {
        state.testsView.globalQuery = raw;
        _testsViewLoad();
      }, 350);
    });
    document.getElementById('testsAgentFilter')?.addEventListener('change', (ev) => {
      state.testsView.agent = ev.target.value || 'all';
      _testsViewRender();
    });
    document.getElementById('testsTeacherFilter')?.addEventListener('change', (ev) => {
      state.testsView.teacher = ev.target.value || 'all';
      _testsViewRender();
    });
    document.getElementById('testsStageFilter')?.addEventListener('change', (ev) => {
      state.testsView.stage = ev.target.value || 'all';
      _testsViewRender();
    });
    // Click row → open the existing tsSlotDetailsModal so agents can edit
    // status / score / notes from this view too. Skip when the click landed
    // on the row's checkbox so toggling selection doesn't also open the modal.
    document.getElementById('testsTbody')?.addEventListener('click', (ev) => {
      if (ev.target.closest('.tests-row-check, .tests-report-lang')) return;
      const tr = ev.target.closest('tr[data-slot-id]');
      if (!tr) return;
      const slotId = tr.dataset.slotId;
      const row = state.testsView.rows.find(r => r.id === slotId);
      // Was `openTsSlotDetails(row)` — a function that never existed, so the
      // typeof guard silently made "Click a row to edit" a no-op on this view.
      // The shared actions/eval modal is the real details editor.
      if (row) {
        try { openTsActionsModal(row); } catch (e) { console.warn('[tests] openTsActionsModal failed:', e); }
      }
    });
    // Row-level checkbox click — toggle membership in the selection set.
    // Delegated so dynamically-rendered rows pick this up without re-binding.
    document.getElementById('testsTbody')?.addEventListener('change', (ev) => {
      const cb = ev.target.closest('.tests-row-check');
      if (!cb) return;
      const tr = cb.closest('tr[data-slot-id]');
      if (!tr) return;
      const slotId = tr.dataset.slotId;
      if (cb.checked) state.testsView.selectedSlotIds.add(slotId);
      else state.testsView.selectedSlotIds.delete(slotId);
      _testsBulkBarRender();
    });
    // Header "select all" — toggles every CURRENTLY VISIBLE row (after the
    // active filter + search). Indeterminate state when partial selection.
    document.getElementById('testsSelectAll')?.addEventListener('click', (ev) => {
      const checked = ev.target.checked;
      const visibleIds = Array.from(document.querySelectorAll('#testsTbody tr[data-slot-id]')).map(tr => tr.dataset.slotId);
      for (const id of visibleIds) {
        if (checked) state.testsView.selectedSlotIds.add(id);
        else state.testsView.selectedSlotIds.delete(id);
      }
      for (const cb of document.querySelectorAll('.tests-row-check')) cb.checked = checked;
      _testsBulkBarRender();
    });
    // Bulk bar actions
    document.getElementById('testsBulkBarSelectVisible')?.addEventListener('click', () => {
      const visibleIds = Array.from(document.querySelectorAll('#testsTbody tr[data-slot-id]')).map(tr => tr.dataset.slotId);
      for (const id of visibleIds) state.testsView.selectedSlotIds.add(id);
      for (const cb of document.querySelectorAll('.tests-row-check')) cb.checked = true;
      const sa = document.getElementById('testsSelectAll');
      if (sa) sa.checked = true;
      _testsBulkBarRender();
    });
    document.getElementById('testsBulkBarClear')?.addEventListener('click', _testsBulkClear);
    document.getElementById('testsBulkBarCancel')?.addEventListener('click', _testsBulkClear);
    document.getElementById('testsBulkSendTemplateBtn')?.addEventListener('click', () => {
      const n = state.testsView.selectedSlotIds.size;
      if (!n) { toast('Select at least one row', 'warning'); return; }
      // Reuse the existing templates modal in the new 'bulk-tests' mode. The
      // modal renders the same picker as bulk-contacts but the per-template
      // "Send to N" button routes to _testsBulkSendTemplate(...) instead of
      // _bulkSendTemplateById (which is HubSpot-id-keyed).
      if (typeof openTemplatesModal === 'function') openTemplatesModal('bulk-tests');
    });
    _testsViewLoad();
  }

  // Clear all test selections (used by Cancel + Clear links + post-send).
  function _testsBulkClear() {
    state.testsView.selectedSlotIds.clear();
    for (const cb of document.querySelectorAll('.tests-row-check')) cb.checked = false;
    const sa = document.getElementById('testsSelectAll');
    if (sa) { sa.checked = false; sa.indeterminate = false; }
    _testsBulkBarRender();
  }

  // Toggle visibility of the bulk bar based on whether anything is selected,
  // and refresh the count + master checkbox indeterminate state.
  function _testsBulkBarRender() {
    const bar = document.getElementById('testsBulkBar');
    const count = state.testsView.selectedSlotIds.size;
    if (bar) bar.classList.toggle('hidden', count === 0);
    const countEl = document.getElementById('testsBulkBarCount');
    if (countEl) countEl.textContent = String(count);
    // Master checkbox tri-state: fully checked when every visible row is
    // selected, indeterminate when some are, unchecked when none.
    const sa = document.getElementById('testsSelectAll');
    if (sa) {
      const visibleIds = Array.from(document.querySelectorAll('#testsTbody tr[data-slot-id]')).map(tr => tr.dataset.slotId);
      const checkedVisible = visibleIds.filter(id => state.testsView.selectedSlotIds.has(id)).length;
      sa.checked = visibleIds.length > 0 && checkedVisible === visibleIds.length;
      sa.indeterminate = checkedVisible > 0 && checkedVisible < visibleIds.length;
    }
    // Refresh icons after toggling visibility — Lucide swaps <i> for <svg>
    // only on first paint, so re-running ensures the bar's icon shows up
    // after the first time the bar appears.
    if (count > 0) refreshIcons();
  }

  // Collect unique student phones from the currently-selected slots. Used by
  // the bulk-template-send path. Returns digits-only E.164 strings. A slot
  // with no phone (rare — non-WA channels) is silently dropped; the failed
  // count surfaces in the toast after send.
  function _testsCollectSelectedPhones() {
    const phones = new Set();
    for (const id of state.testsView.selectedSlotIds) {
      const row = state.testsView.rows.find(r => r.id === id);
      // Display phones are masked for agents. Bulk operations must use the
      // same server-validated functional key as the per-row WA action.
      const jid = _validatedFunctionalChatJid(row?.lead_chat_jid);
      if (jid) phones.add(jid.split('@')[0]);
    }
    return Array.from(phones);
  }

  // Fire one bulk POST per the selected slots' phone numbers. Mirrors
  // _bulkSendTemplateById's UX (confirm count + toast on result) but feeds
  // phones instead of HubSpot ids to a new server endpoint that handles
  // the mirror lookup + send + inbox-record loop server-side.
  async function _testsBulkSendTemplate(templateId) {
    const phones = _testsCollectSelectedPhones();
    if (!phones.length) { toast('No usable phone on the selected slots', 'warning'); return; }
    if (_bulkTooMany(phones.length)) return;
    if (!templateId) return;
    const tpl = (state.templatesCache || []).find(t => t.id === templateId);
    const label = tpl?.name || 'this template';
    if (!confirm(`Send "${label}" to ${phones.length} student${phones.length === 1 ? '' : 's'}?`)) return;
    try {
      const r = await api('/api/templates/bulk-send-by-phone', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phones, templateId }),
        // No watchdog: real WhatsApp sends — see bulk-send-template above.
        timeoutMs: 0,
      });
      const d = await r.json();
      if (!r.ok) { toast('Bulk send failed: ' + (d.error || ('HTTP ' + r.status)), 'error'); return; }
      const failedCount = (d.failed || []).length;
      const okCount = d.succeeded || 0;
      toast(`Sent ${okCount} ok${failedCount ? ` · ${failedCount} failed` : ''}`, failedCount ? 'warning' : 'success');
      closeModal('templatesModal');
      _testsBulkClear();
    } catch (err) {
      toast('Bulk send failed: ' + (err.message || err), 'error');
    }
  }

  async function _testsViewLoad() {
    const tbody = document.getElementById('testsTbody');
    const loading = document.getElementById('testsLoading');
    const empty = document.getElementById('testsEmpty');
    if (!tbody) return;
    state.testsView.loading = true;
    if (loading) loading.classList.remove('hidden');
    if (empty) empty.classList.add('hidden');
    // Tests with no result — sales admins only (the assessment form is theirs
    // and the teachers'; agents get the Team Bot nudge instead).
    if (_isSalesAdmin()) _mrLoad('testsMissing');
    else document.getElementById('testsMissing')?.classList.add('hidden');
    try {
      const qs = new URLSearchParams({
        from: state.testsView.from,
        to: state.testsView.to,
      });
      // With a query the server searches the WHOLE history and ignores the
      // dates; from/to still ride along because the endpoint validates them.
      if (state.testsView.globalQuery) qs.set('q', state.testsView.globalQuery);
      const r = await api('/api/test-schedule/bookings?' + qs.toString());
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      state.testsView.rows = (j.slots || []);
      state.testsView.truncated = !!j.truncated;
      state.testsView.limit = j.limit || 0;
    } catch (err) {
      console.warn('[tests] load failed:', err?.message || err);
      state.testsView.rows = [];
      state.testsView.truncated = false;
    } finally {
      state.testsView.loading = false;
      if (loading) loading.classList.add('hidden');
      _testsPopulateTeacherFilter();
      _testsPopulateStageFilter();
      _testsViewRender();
    }
  }

  function _testsViewRender() {
    const tbody = document.getElementById('testsTbody');
    const empty = document.getElementById('testsEmpty');
    const resultCount = document.getElementById('testsResultCount');
    if (!tbody) return;
    clear(tbody);

    // The agent filter narrows the set BEFORE everything else, so "Booked 12"
    // reads for the picked agent and not for the whole sales floor.
    // The teacher and pipeline filters narrow it the same way, on top of the agent.
    const agent = state.testsView.agent || 'all';
    const teacher = state.testsView.teacher || 'all';
    const stage = state.testsView.stage || 'all';
    let base = agent === 'all'
      ? state.testsView.rows
      : state.testsView.rows.filter(r => _testsRowBelongsTo(r, agent));
    if (teacher !== 'all') base = base.filter(r => String(r.teacher_id || '') === teacher);
    if (stage !== 'all') base = base.filter(r => String(r.lead_pipeline_stage || '') === stage);

    // Refresh chip counts from the full (agent-scoped) row set, so counts
    // reflect the date range + agent, not the current status chip or search.
    const counts = { all: base.length, booked: 0, confirmed: 0, in_test: 0, completed: 0, no_show: 0, cancelled: 0 };
    for (const r of base) {
      if (counts[r.status] !== undefined) counts[r.status]++;
    }
    for (const k of Object.keys(counts)) {
      const el = document.querySelector('.tests-chip-count[data-count="' + k + '"]');
      if (el) el.textContent = counts[k];
    }

    // Filter
    const f = state.testsView.filter;
    const q = state.testsView.search;
    // A server-side global search is authoritative — re-running the text filter
    // here would throw away the rows it just found: it matched the phone on raw
    // digits, and what arrives in student_phone may be masked (or formatted
    // differently from what was typed). Only the status chip + agent narrow it.
    const serverMatched = !!state.testsView.globalQuery;
    let filtered = base;
    if (f !== 'all') filtered = filtered.filter(r => r.status === f);
    if (q && !serverMatched) {
      filtered = filtered.filter(r => {
        const hay = [r.lead_name, r.student_name, r.student_phone, r.teacher_name, r.teacher_display_name, r.notes, r.sales_note, r.booked_by]
          .map(x => (x || '').toString().toLowerCase()).join(' ');
        return hay.includes(q);
      });
    }

    if (!filtered.length) {
      if (empty) {
        // Say WHICH haystack came up empty, so "nothing found" is never read as
        // "widen the dates" when the dates were already ignored.
        empty.textContent = serverMatched
          ? 'No test matches this search — searched every date, not just the range above.'
          : 'No tests in this date range yet.';
        empty.classList.remove('hidden');
      }
      if (resultCount) resultCount.textContent = '';
      return;
    }
    if (empty) empty.classList.add('hidden');
    if (resultCount) {
      // Never let a capped result read as a complete one — a broad term (a
      // teacher's name) matches more slots than the endpoint will return.
      const capped = serverMatched && state.testsView.truncated;
      resultCount.textContent = filtered.length + (filtered.length === 1 ? ' test' : ' tests')
        + (serverMatched ? ' · all dates' : '')
        + (capped ? ' · most recent ' + (state.testsView.limit || filtered.length) + ', narrow the search for older ones' : '');
    }

    // Sort: most recent first by date desc, time desc
    filtered.sort((a, b) => {
      const da = String(a.slot_date || '').slice(0, 10);
      const db = String(b.slot_date || '').slice(0, 10);
      if (da !== db) return db.localeCompare(da);
      return String(b.slot_time || '').localeCompare(String(a.slot_time || ''));
    });

    for (const r of filtered) {
      const tr = el('tr', { 'data-slot-id': r.id });
      // Selection checkbox — first column. Preserves checked state across
      // re-renders (filter / search change) via the persistent selectedSlotIds
      // Set. Wrapped in <td> so click on the cell padding still hits the box.
      const checkTd = el('td', { class: 'tests-td-check' });
      const checkInput = el('input', { type: 'checkbox', class: 'tests-row-check' });
      checkInput.checked = state.testsView.selectedSlotIds.has(r.id);
      checkTd.appendChild(checkInput);
      tr.appendChild(checkTd);
      tr.appendChild(el('td', { class: 'tests-td-date', text: _testsFmtDate(r.slot_date) }));
      tr.appendChild(el('td', { class: 'tests-td-time', text: _testsFmtTime(r.slot_time) }));
      tr.appendChild(el('td', { text: r.teacher_display_name || r.teacher_name || '—' }));
      const testsNameTd = el('td', { class: 'tests-td-student' }, [el('span', { text: r.lead_name || r.student_name || '—' })]);
      if (r.name_unconfirmed) testsNameTd.appendChild(_nameUnconfirmedBadge());
      tr.appendChild(testsNameTd);
      tr.appendChild(el('td', { class: 'tests-td-phone', text: r.student_phone || '—' }));
      tr.appendChild(el('td', {}, [el('span', { class: 'tests-pill tests-pill-stage', text: r.lead_pipeline_stage || '—' })]));
      const statusKind = _testsStatusKind(r.status);
      tr.appendChild(el('td', {}, [el('span', { class: 'tests-pill tests-status' + (statusKind ? ' tests-status-' + statusKind : ''), text: _testsStatusLabel(r.status) })]));
      tr.appendChild(el('td', { class: 'tests-td-num', text: r.score != null && r.score !== '' ? String(r.score) : '—' }));
      tr.appendChild(el('td', {}, r.new_level ? [el('span', { class: 'cefr-pill cefr-' + r.new_level, text: r.new_level })] : [document.createTextNode('—')]));
      const rec = r.teacher_recommendation;
      tr.appendChild(el('td', {}, rec ? [el('span', { class: 'tests-pill tests-rec-' + _testsRecKind(rec), text: rec })] : [document.createTextNode('—')]));
      const reportTd = el('td', { class: 'tests-report-cell' });
      if (r.status === 'completed' && _tsAssessmentReadiness(r)) {
        const reportActions = el('div', { class: 'tests-report-actions', title: r.report_generated_at ? 'Client action plan ready' : 'The action plan will be prepared on first download' });
        for (const lang of ['fr', 'en', 'ar']) {
          const btn = el('button', {
            type: 'button', class: 'tests-report-lang',
            text: lang === 'ar' ? 'ع' : lang.toUpperCase(),
            'aria-label': 'Download the ' + lang.toUpperCase() + ' action plan',
          });
          btn.addEventListener('click', (ev) => {
            ev.preventDefault(); ev.stopPropagation();
            _tsDownloadAssessmentReport(r, lang, btn);
          });
          reportActions.appendChild(btn);
        }
        reportTd.appendChild(reportActions);
        // The learner's own document, alongside the team's action plan — the
        // same report a website test produces, so a student who sat the oral
        // test receives the same thing. Download is always available; sending
        // is the agent's choice, never automatic.
        if (r.new_level) {
          // Language is picked per click, exactly like the action plan row
          // above; the last one picked is what the send uses, so the operator
          // sees the document before it goes to the client.
          let chosen = 'fr';
          const clientActions = el('div', { class: 'tests-report-actions tests-client-report', title: 'Rapport client — la copie du student' });
          for (const lang of ['fr', 'en', 'ar']) {
            const b = el('button', {
              type: 'button', class: 'tests-report-lang',
              text: lang === 'ar' ? 'ع' : lang.toUpperCase(),
              'aria-label': 'Download the ' + lang.toUpperCase() + ' client report',
            });
            b.addEventListener('click', (ev) => {
              ev.preventDefault(); ev.stopPropagation();
              chosen = lang;
              _tsClientReport(r.id, 'download', lang, b);
            });
            clientActions.appendChild(b);
          }
          const send = el('button', { type: 'button', class: 'tests-report-lang', 'aria-label': 'Send the client report on WhatsApp' }, [lucideIcon('send')]);
          send.addEventListener('click', (ev) => {
            ev.preventDefault(); ev.stopPropagation();
            if (!confirm('Envoyer le rapport client (' + chosen.toUpperCase() + ') au student sur WhatsApp ?')) return;
            _tsClientReport(r.id, 'send', chosen, send);
          });
          clientActions.appendChild(send);
          reportTd.appendChild(clientActions);
        }
      } else {
        reportTd.appendChild(el('span', { class: 'tests-report-missing' }, [lucideIcon('circle-dashed'), el('span', { text: r.status === 'completed' ? 'Assessment incomplete' : '—' })]));
      }
      tr.appendChild(reportTd);
      tr.appendChild(el('td', { class: 'tests-td-notes', title: r.notes || '', text: r.notes ? (r.notes.slice(0, 60) + (r.notes.length > 60 ? '…' : '')) : '—' }));
      tr.appendChild(el('td', { class: 'tests-td-agent-note', title: r.sales_note || '', text: r.sales_note ? (r.sales_note.slice(0, 60) + (r.sales_note.length > 60 ? '…' : '')) : '—' }));
      tr.appendChild(el('td', { class: 'tests-td-by', text: bookedByLabel(r) || '—' }));
      tr.appendChild(el('td', { class: 'tests-td-by', text: _testsFmtRelative(r.booked_at) }));
      // First time this contact landed in our CRM (HubSpot createdate, see the
      // server resolver). Day precision reads better than a timestamp for a
      // date that is usually months old — the exact one lives in the tooltip.
      tr.appendChild(el('td', {
        class: 'tests-td-by tests-td-created',
        title: r.contact_created_at ? MA.loc(new Date(r.contact_created_at), 'dt') : '',
        text: r.contact_created_at ? _testsFmtDate(r.contact_created_at) : '—',
      }));
      tbody.appendChild(tr);
    }
    refreshIcons();
    // Re-sync the master checkbox + bar visibility now that the rows are
    // freshly rendered. Without this, switching filters could leave the
    // master in a stale tri-state.
    _testsBulkBarRender();
  }

  function ensureTestScheduleInit() {
    if (!state.ts.inited) {
      state.ts.inited = true;
      // The ONE date control owns both the grid day and the period, so a fresh
      // session starts on today and a saved selection is restored below by
      // _tsPeriodInit → _tsPeriodApply. The old date input and `Today` button
      // are gone: their jobs are "Un jour précis…" and "Aujourd'hui" inside
      // the menu (2026-09-17).
      state.ts.date = tsTodayIso();
      // ‹ / › step one day AND make that day the selection.
      document.getElementById('tsPrevDayBtn').addEventListener('click', () => _tsDateStepDay(-1));
      document.getElementById('tsNextDayBtn').addEventListener('click', () => _tsDateStepDay(1));
      document.getElementById('tsRefreshBtn').addEventListener('click', () => loadTestSchedule());

      // Day filters (teacher / agent). Restored from localStorage on first
      // entry so they survive a reload, and never refetch on change — the day
      // payload already carries the teacher and the booking agent per seat.
      _tsLoadSavedFilters();
      document.getElementById('tsTeacherFilter')?.addEventListener('change', (ev) => _tsSetFilter('teacher', ev.target.value));
      document.getElementById('tsAgentFilter')?.addEventListener('change', (ev) => _tsSetFilter('agent', ev.target.value));

      // The one date control: wires the button + the menu, then applies the
      // saved selection (a range moves the grid day before the first load).
      _tsPeriodInit();
      {
        const _saved = _tsPeriodRange();
        const _day = _tsGridDayFor(_saved);
        if (_day && _day !== state.ts.date) state.ts.date = _day;
      }

      // Book modal wiring
      document.getElementById('tsBookSearch').addEventListener('input', (ev) => {
        clearTimeout(state.ts.bookSearchTimer);
        const q = String(ev.target.value || '').trim();
        state.ts.bookSearchTimer = setTimeout(() => tsBookSearch(q), 220);
      });
      document.getElementById('tsBookConfirmBtn').addEventListener('click', () => tsBookConfirm());

      // Actions modal wiring — delegated to the shared idempotent wirer so the
      // teacher "Mes tests" entry point (which never runs this grid init) also
      // gets working modal buttons (incl. the eval Save). See _wireTsActionsModal.
      _wireTsActionsModal();
    }
    loadTestSchedule();
  }

  async function loadTestSchedule() {
    const grid = document.getElementById('tsGrid');
    const stats = document.getElementById('tsStats');
    if (!grid) return;
    // Refresh / prev / next / a date change re-reads the seats, so the cached
    // PERIOD numbers are stale by definition — drop them, otherwise the strip
    // keeps the old range count for good (the Refresh button looked broken).
    _tsInvalidatePeriodStats();
    clear(grid);
    grid.appendChild(el('div', { class: 'ts-empty', id: 'tsEmpty', text: 'Loading slots…' }));
    if (stats) clear(stats);
    // Pin the date we're fetching so overlapping prev/next/date-change
    // requests can't repaint the grid with a stale date's slots when an
    // older request resolves last.
    const reqDate = state.ts.date;
    try {
      const r = await api('/api/test-schedule/slots?date=' + encodeURIComponent(reqDate));
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      // The agent moved to a different date while this was in flight — drop
      // this response so it doesn't clobber the newer date's render.
      if (state.ts.date !== reqDate) return;
      state.ts.times = j.times || [];
      state.ts.seatsPerSlot = j.seats_per_slot || 3;
      // Overbooking: each `teacher_id|slot_time` key now holds an ARRAY of seat
      // rows (up to seatsPerSlot). The grid groups them into one cell.
      state.ts.slotsByCell = {};
      const teacherMap = new Map();
      for (const s of (j.slots || [])) {
        const key = `${s.teacher_id}|${s.slot_time}`;
        (state.ts.slotsByCell[key] || (state.ts.slotsByCell[key] = [])).push(s);
        if (!teacherMap.has(s.teacher_id)) {
          teacherMap.set(s.teacher_id, {
            id: s.teacher_id,
            name: s.teacher_display_name || s.teacher_name || '—',
            color: s.teacher_color || '#6b7280',
          });
        }
      }
      // Stable seat order within each cell.
      for (const k of Object.keys(state.ts.slotsByCell)) {
        state.ts.slotsByCell[k].sort((a, b) => (a.seat_no || 1) - (b.seat_no || 1));
      }
      state.ts.teachers = Array.from(teacherMap.values()).sort((a, b) => a.name.localeCompare(b.name));
      renderTestSchedule();
    } catch (err) {
      console.warn('[test-schedule] load failed:', err);
      clear(grid);
      grid.appendChild(el('div', { class: 'ts-empty ts-empty-error', text: 'Failed to load schedule: ' + (err.message || err) }));
    }
  }

  // Render the two teacher-only sections (Pending evaluations + Today) above
  // the slot grid. Hidden + empty for non-teacher roles.
  function _tsRenderTeacherDashboard() {
    const host = document.getElementById('teacherDashSections');
    if (!host) return;
    const role = (state.me?.role || '').toLowerCase();
    if (role !== 'teacher') { host.classList.add('hidden'); return; }
    host.classList.remove('hidden');

    const slots = Object.values(state.ts.slotsByCell || {}).flat(); // each cell is now a seat array
    // A Test Studio score closes the seat with its level but no skills yet —
    // the action plan is still to do, so it stays in Pending evaluations.
    const pending = slots.filter(s => s.status === 'completed' && (!s.new_level || !_tsHasSkillScores(s)));
    const todayIso = state.ts.date;
    const todayActive = slots.filter(s =>
      String(s.slot_date).slice(0,10) === todayIso &&
      ['booked','confirmed','reminder_sent','in_test'].includes(s.status)
    ).sort((a,b) => String(a.slot_time).localeCompare(String(b.slot_time)));

    // --- Pending evaluations ---
    const pendingHost = document.getElementById('teacherDashPending');
    clear(pendingHost);
    if (pending.length) {
      pendingHost.appendChild(el('div', { class: 'ts-section-head ts-section-head-warn' }, [
        lucideIcon('clipboard-pen'),
        el('span', { text: 'Pending evaluations' }),
        el('span', { class: 'ts-section-count', text: String(pending.length) }),
      ]));
      const list = el('div', { class: 'ts-pending-list' });
      for (const s of pending) {
        const pendName = el('span', { class: 'ts-pending-name', text: (s.lead_name || s.student_name || '(student)') });
        if (s.name_unconfirmed) pendName.appendChild(_nameUnconfirmedBadge());
        const row = el('button', { type: 'button', class: 'ts-pending-card', 'data-slot-id': s.id }, [
          pendName,
          el('span', { class: 'ts-pending-when', text: String(s.slot_date).slice(0,10) + ' · ' + String(s.slot_time).slice(0,5) }),
          el('span', { class: 'ts-pending-cta' }, [el('span', { text: 'Evaluate' }), lucideIcon('arrow-right')]),
        ]);
        row.addEventListener('click', () => onTsCellClick(s.id));
        list.appendChild(row);
      }
      pendingHost.appendChild(list);
    }

    // --- Today's tests ---
    const todayHost = document.getElementById('teacherDashToday');
    clear(todayHost);
    todayHost.appendChild(el('div', { class: 'ts-section-head' }, [
      lucideIcon('calendar-clock'),
      el('span', { text: 'Today' }),
      el('span', { class: 'ts-section-count', text: String(todayActive.length) }),
    ]));
    if (!todayActive.length) {
      todayHost.appendChild(el('div', { class: 'ts-today-empty', text: 'No tests scheduled for today.' }));
    } else {
      // Group by time so an OVERBOOKED slot (≤3 students sharing one time + one
      // Zoom) reads as a single labelled block, not 3 mystery back-to-back cards
      // (audit OPS-2/FE-3). ~1 of the 3 is expected to actually show.
      const byTime = new Map();
      for (const s of todayActive) {
        const k = String(s.slot_time).slice(0, 5);
        if (!byTime.has(k)) byTime.set(k, []);
        byTime.get(k).push(s);
      }
      const list = el('div', { class: 'ts-today-grid' });
      for (const [time, group] of byTime.entries()) {
        const overbooked = group.length > 1;
        const card = el('div', { class: 'ts-today-card' + (overbooked ? ' ts-today-card-multi' : ''), 'data-status': group[0].status });
        const top = el('div', { class: 'ts-today-top' }, [el('span', { class: 'ts-today-time', text: time })]);
        top.appendChild(overbooked
          ? el('span', { class: 'ts-today-overbook', title: 'Overbooking — several students share this slot, ≈1 shows up', text: group.length + ' booked · overbooking' })
          : el('span', { class: 'ts-today-status', text: TS_STATUS_LABEL[group[0].status] || group[0].status }));
        card.appendChild(top);
        // One row per student (full name + per-seat status when overbooked + agent).
        for (const s of group) {
          const nameRow = el('div', { class: 'ts-today-name' }, [lucideIcon('user'), el('span', { text: String(s.lead_name || s.student_name || '(no name)') })]);
          if (s.name_unconfirmed) nameRow.appendChild(_nameUnconfirmedBadge());
          if (overbooked) nameRow.appendChild(el('span', { class: 'ts-today-name-status', text: TS_STATUS_LABEL[s.status] || s.status }));
          card.appendChild(nameRow);
          if (s.booked_by) {
            card.appendChild(el('div', { class: 'ts-today-by', title: 'Booked by ' + bookedByLabel(s) }, [
              lucideIcon('headset'),
              el('span', {}, [document.createTextNode('by '), el('strong', { text: bookedByLabel(s) })]),
            ]));
          }
        }
        list.appendChild(card);
      }
      todayHost.appendChild(list);
    }
    refreshIcons(host);
  }

  // Human label for an agent key that is NOT present in the day (a filter kept
  // from another date). Mirrors bookedByLabel's bot rule so the option reads the
  // same as the chips would.
  function _tsAgentKeyLabel(key) {
    const k = String(key || '');
    if (k.startsWith('bot:')) {
      const owner = k.slice(4);
      if (!owner) return 'bot';
      const u = (state.teamUsers || []).find(x => String(x.username || '').toLowerCase() === owner);
      return userLabel(owner, u?.displayName || owner) + ' · bot';
    }
    return userLabel(k.startsWith('by:') ? k.slice(3) : k);
  }

  // Rebuild the two toolbar dropdowns from the DAY's own payload.
  //  • Teachers come from the same map the grid renders its columns from
  //    (state.ts.teachers) — never a hardcoded Morad/Nada/Rahaf list.
  //  • Agents come only from attributions this user can actually SEE: a masked
  //    seat (another agent's booking) yields no key, so a scoped agent's list
  //    holds their own name only and no colleague's name can leak through it.
  //    A teacher never sees "par X" on the grid, so they get no agent filter.
  //  • Options carry live counts, so nothing on offer returns an empty grid.
  function _tsPopulateFilters(allTeachers, scopedSeats) {
    const tSel = document.getElementById('tsTeacherFilter');
    const aSel = document.getElementById('tsAgentFilter');
    if (tSel) {
      const active = state.ts.teacherFilter || 'all';
      while (tSel.children.length > 1) tSel.removeChild(tSel.lastChild);
      for (const t of allTeachers) tSel.appendChild(el('option', { value: t.id, text: t.name }));
      // A filter carried over from another date whose teacher has no slots today
      // stays listed, so the operator can see (and clear) why the grid is empty.
      if (active !== 'all' && !allTeachers.some(t => t.id === active)) {
        tSel.appendChild(el('option', { value: active, text: 'Professeur sans créneau ce jour' }));
      }
      tSel.value = active;
      tSel.classList.toggle('is-filtered', active !== 'all');
      // A single column has nothing to narrow (teacher accounts are server-scoped
      // to their own) — but keep the bar while a filter is on so it is clearable.
      tSel.classList.toggle('hidden', allTeachers.length < 2 && active === 'all');
    }
    if (aSel) {
      const isTeacher = (state.me?.role || '').toLowerCase() === 'teacher';
      const active = state.ts.agentFilter || 'all';
      const opts = new Map();
      if (!isTeacher) {
        for (const s of scopedSeats) {
          const k = _tsSeatAgentKey(s);
          if (!k) continue;
          const cur = opts.get(k);
          if (cur) cur.n++;
          else opts.set(k, { label: bookedByLabel(s) || _tsAgentKeyLabel(k), n: 1 });
        }
      }
      while (aSel.children.length > 1) aSel.removeChild(aSel.lastChild);
      const rows = Array.from(opts.entries())
        .sort((a, b) => String(a[1].label).localeCompare(String(b[1].label), 'fr', { sensitivity: 'base' }));
      for (const [k, v] of rows) aSel.appendChild(el('option', { value: k, text: v.label + ' (' + v.n + ')' }));
      if (active !== 'all' && !opts.has(active)) {
        aSel.appendChild(el('option', { value: active, text: _tsAgentKeyLabel(active) + ' (0)' }));
      }
      aSel.value = active;
      aSel.classList.toggle('is-filtered', active !== 'all');
      aSel.classList.toggle('hidden', isTeacher || (!rows.length && active === 'all'));
    }
  }

  // Set one filter, persist it, repaint from the data already in memory (no
  // refetch — the day payload holds teacher + agent on every seat).
  function _tsSetFilter(which, value) {
    const v = value || 'all';
    if (which === 'teacher') state.ts.teacherFilter = v;
    else state.ts.agentFilter = v;
    _tsSaveFilter(which, v);
    renderTestSchedule();
  }

  // ─── The ONE date control (2026-09-17, Ayoub) ───────────────────────────
  // One button in the toolbar answers "which day / which period am I looking
  // at". It replaced three controls that asked the same question (a date
  // input, a `Today` button and a full-width period select that dropped to its
  // own line): "koulchi f bar wahed, o wahed ghir dak l'control".
  //
  //   * the GRID always shows ONE day — `state.ts.date`;
  //   * the stat cards + the no-show popup follow the selected PERIOD;
  //   * picking a range moves the grid to the range's last day, clamped to
  //     today when today is inside the range (so "Cette semaine" lands on
  //     today, not on Sunday);
  //   * `‹` / `›` step one day and switch the selection to that single day.
  //
  // Same maths as lib/test-schedule/periods.js (the server's copy): Casablanca
  // calendar dates, Monday week start, 92-day cap — e2e/test-schedule-period
  // .spec.js fails if the two drift. The menu reuses the Inbox filter-bar
  // idiom (.dropdown / .filter-btn / .dropdown-menu / .dropdown-item /
  // .filter-check / .dropdown-divider), plus keyboard navigation.
  const TS_PERIODS = [
    ['today', "Aujourd'hui"],
    ['yesterday', 'Hier'],
    ['this_week', 'Cette semaine'],
    ['last_week', 'Semaine dernière'],
    ['this_month', 'Ce mois'],
    ['last_month', 'Mois dernier'],
    ['day', 'Un jour précis…'],
    ['custom', 'Entre deux dates…'],
  ];
  const TS_PERIOD_MAX_DAYS = 92;
  const TS_PERIOD_LS_KEY = 'leads7.tsPeriod';

  function _tsYmdOk(v) { return /^\d{4}-\d{2}-\d{2}$/.test(String(v || '')); }
  function _tsYmdShift(ymd, days) {
    const d = new Date(String(ymd) + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  }
  function _tsIsoWeekday(ymd) {
    const wd = new Date(String(ymd) + 'T00:00:00Z').getUTCDay();
    return wd === 0 ? 7 : wd;
  }
  function _tsMonthStart(ymd) { return String(ymd).slice(0, 8) + '01'; }
  function _tsMonthEnd(ymd) {
    const d = new Date(_tsMonthStart(ymd) + 'T00:00:00Z');
    d.setUTCMonth(d.getUTCMonth() + 1);
    d.setUTCDate(0);
    return d.toISOString().slice(0, 10);
  }
  function _tsSpanDays(from, to) {
    return Math.round((new Date(to + 'T00:00:00Z') - new Date(from + 'T00:00:00Z')) / 86400000) + 1;
  }
  function _tsFmtDm(ymd) { return _tsYmdOk(ymd) ? String(ymd).slice(8, 10) + '/' + String(ymd).slice(5, 7) : ''; }
  function _tsFmtRange(from, to) {
    if (!_tsYmdOk(from) || !_tsYmdOk(to)) return '';
    if (from === to) return _tsFmtDm(from);
    const sameYear = String(from).slice(0, 4) === String(to).slice(0, 4);
    return sameYear
      ? _tsFmtDm(from) + ' → ' + _tsFmtDm(to)
      : _tsFmtDm(from) + '/' + String(from).slice(0, 4) + ' → ' + _tsFmtDm(to) + '/' + String(to).slice(0, 4);
  }

  // → { from, to } or { error } (custom only).
  function _tsResolvePeriod(key, anchor, custom) {
    const a = _tsYmdOk(anchor) ? anchor : tsTodayIso();
    switch (key) {
      case 'yesterday': { const d = _tsYmdShift(a, -1); return { from: d, to: d }; }
      case 'this_week': { const m = _tsYmdShift(a, -(_tsIsoWeekday(a) - 1)); return { from: m, to: _tsYmdShift(m, 6) }; }
      case 'last_week': { const m = _tsYmdShift(_tsYmdShift(a, -(_tsIsoWeekday(a) - 1)), -7); return { from: m, to: _tsYmdShift(m, 6) }; }
      case 'this_month': return { from: _tsMonthStart(a), to: _tsMonthEnd(a) };
      case 'last_month': { const prev = _tsYmdShift(_tsMonthStart(a), -1); return { from: _tsMonthStart(prev), to: _tsMonthEnd(prev) }; }
      case 'custom': {
        const from = custom?.from || '';
        const to = custom?.to || '';
        if (!_tsYmdOk(from) || !_tsYmdOk(to)) return { error: 'Choisis une date de début et une date de fin.' };
        if (from > to) return { error: 'La date de début doit être avant la date de fin.' };
        const span = _tsSpanDays(from, to);
        if (span > TS_PERIOD_MAX_DAYS) return { error: 'Période trop large : ' + span + ' jours (max ' + TS_PERIOD_MAX_DAYS + ').' };
        return { from, to };
      }
      default: return { from: a, to: a };
    }
  }

  function _tsPeriodState() {
    if (!state.ts.period) {
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem(TS_PERIOD_LS_KEY) || 'null'); } catch { /* storage off */ }
      const key = TS_PERIODS.some(([k]) => k === saved?.key) ? saved.key : 'today';
      state.ts.period = { key, from: saved?.from || '', to: saved?.to || '', day: saved?.day || '' };
    }
    return state.ts.period;
  }
  function _tsPeriodSave() {
    try { localStorage.setItem(TS_PERIOD_LS_KEY, JSON.stringify(_tsPeriodState())); }
    catch { /* private window — the choice still holds for this session */ }
  }

  // The range the cards + popup describe right now. Presets are anchored on
  // the REAL today (the control owns the day now, so there is no second
  // anchor); `day` is anchored on the date the agent picked. A saved custom
  // range that no longer validates degrades to the day on screen.
  function _tsPeriodRange() {
    const p = _tsPeriodState();
    const anchor = p.key === 'day' ? (p.day || state.ts.date) : tsTodayIso();
    const r = _tsResolvePeriod(p.key, anchor, p);
    if (r.error) return { from: state.ts.date, to: state.ts.date, error: r.error };
    return r;
  }

  // Which ONE day the grid shows for a selection: the range's last day, but
  // today when today falls inside the range — nobody opening "Cette semaine"
  // on Tuesday wants to land on Sunday.
  function _tsGridDayFor(range) {
    const today = tsTodayIso();
    if (range.from <= today && today <= range.to) return today;
    return range.to;
  }

  // Button label + the range chip beside it. Everything stays INSIDE the row.
  const TS_DC_WEEKDAYS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
  function _tsFmtDayLabel(ymd) {
    if (!_tsYmdOk(ymd)) return '';
    const wd = TS_DC_WEEKDAYS[new Date(ymd + 'T00:00:00Z').getUTCDay()] || '';
    return (wd ? wd + ' ' : '') + _tsFmtDm(ymd);
  }
  function _tsPeriodLabel() {
    const p = _tsPeriodState();
    const r = _tsPeriodRange();
    const preset = (TS_PERIODS.find(([k]) => k === p.key) || [])[1] || '';
    if (p.key === 'today') return { label: "Aujourd'hui", chip: '' };
    if (p.key === 'yesterday') return { label: 'Hier', chip: _tsFmtDm(r.from) };
    if (p.key === 'day') return { label: _tsFmtDayLabel(r.from) || preset, chip: '' };
    if (p.key === 'custom') {
      const span = _tsSpanDays(r.from, r.to);
      return { label: _tsFmtRange(r.from, r.to) || preset, chip: span > 1 ? span + ' j' : '' };
    }
    const span = _tsSpanDays(r.from, r.to);
    return { label: preset, chip: _tsFmtRange(r.from, r.to) + (span > 1 ? ' · ' + span + ' j' : '') };
  }

  // ── The menu ────────────────────────────────────────────────────────────
  // Built once, in the same DOM vocabulary as the Inbox filter menus, with two
  // inline panels: one date input for "Un jour précis…", two + Appliquer for
  // "Entre deux dates…".
  function _tsDateMenuBuild() {
    const menu = document.getElementById('tsDateMenu');
    if (!menu || menu.dataset.built) return menu;
    menu.dataset.built = '1';
    // Single-select affordance: a fixed-width leading slot that stays EMPTY on
    // the inactive rows (so every label keeps the same left edge) and carries a
    // check on the selected one. The inbox's `.filter-check` box was borrowed
    // at first and read as a row of empty checkboxes — wrong vocabulary for a
    // menu where exactly one row can be on (2026-09-17, Ayoub).
    const item = (key, label) => el('button', {
      class: 'dropdown-item ts-dc-item', type: 'button', role: 'menuitemradio',
      'aria-checked': 'false', 'data-period': key,
    }, [el('span', { class: 'ts-dc-tick' }), el('span', { text: label })]);
    const byKey = Object.fromEntries(TS_PERIODS);
    menu.appendChild(item('today', byKey.today));
    menu.appendChild(item('yesterday', byKey.yesterday));
    menu.appendChild(el('div', { class: 'dropdown-divider' }));
    menu.appendChild(item('this_week', byKey.this_week));
    menu.appendChild(item('last_week', byKey.last_week));
    menu.appendChild(item('this_month', byKey.this_month));
    menu.appendChild(item('last_month', byKey.last_month));
    menu.appendChild(el('div', { class: 'dropdown-divider' }));
    menu.appendChild(item('day', byKey.day));
    // One day — the old date input, now inside the menu.
    menu.appendChild(el('div', { class: 'ts-dc-panel hidden', id: 'tsDcDayPanel' }, [
      el('label', { class: 'ts-dc-field' }, [
        el('span', { class: 'ts-dc-field-lbl', text: 'Jour' }),
        el('input', { type: 'date', id: 'tsDcDayInput', class: 'input ts-dc-input' }),
      ]),
    ]));
    menu.appendChild(item('custom', byKey.custom));
    menu.appendChild(el('div', { class: 'ts-dc-panel hidden', id: 'tsDcRangePanel' }, [
      el('div', { class: 'ts-dc-fields' }, [
        el('label', { class: 'ts-dc-field' }, [
          el('span', { class: 'ts-dc-field-lbl', text: 'Du' }),
          el('input', { type: 'date', id: 'tsDcFrom', class: 'input ts-dc-input' }),
        ]),
        el('label', { class: 'ts-dc-field' }, [
          el('span', { class: 'ts-dc-field-lbl', text: 'Au' }),
          el('input', { type: 'date', id: 'tsDcTo', class: 'input ts-dc-input' }),
        ]),
      ]),
      el('button', { class: 'btn btn-primary btn-sm ts-dc-apply', id: 'tsDcApplyBtn', type: 'button', text: 'Appliquer' }),
      el('div', { class: 'ts-dc-hint', id: 'tsDcHint' }),
    ]));
    // A click inside a panel must not bubble to the menu's row delegator.
    for (const panel of menu.querySelectorAll('.ts-dc-panel')) {
      panel.addEventListener('click', ev => ev.stopPropagation());
    }
    menu.addEventListener('click', (ev) => {
      const row = ev.target.closest('.ts-dc-item');
      if (!row) return;
      _tsDatePick(row.getAttribute('data-period'));
    });
    document.getElementById('tsDcDayInput')?.addEventListener('change', (ev) => {
      const v = ev.target.value;
      if (!_tsYmdOk(v)) return;
      const p = _tsPeriodState();
      p.key = 'day';
      p.day = v;
      _tsPeriodSave();
      _tsDateClose();
      _tsPeriodApply();
    });
    document.getElementById('tsDcApplyBtn')?.addEventListener('click', () => _tsDcApplyRange());
    for (const id of ['tsDcFrom', 'tsDcTo']) {
      document.getElementById(id)?.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') { ev.preventDefault(); _tsDcApplyRange(); }
      });
    }
    return menu;
  }

  function _tsDcApplyRange() {
    const p = _tsPeriodState();
    const from = document.getElementById('tsDcFrom')?.value || '';
    const to = document.getElementById('tsDcTo')?.value || '';
    const r = _tsResolvePeriod('custom', null, { from, to });
    const hint = document.getElementById('tsDcHint');
    if (r.error) {
      if (hint) { hint.textContent = r.error; hint.classList.add('is-error'); }
      return;
    }
    if (hint) { hint.textContent = ''; hint.classList.remove('is-error'); }
    p.key = 'custom';
    p.from = from;
    p.to = to;
    _tsPeriodSave();
    _tsDateClose();
    _tsPeriodApply();
  }

  // Pick a row: the two "…" rows only reveal their panel; every other row is
  // an immediate selection.
  function _tsDatePick(key) {
    const p = _tsPeriodState();
    if (key === 'day') {
      const input = document.getElementById('tsDcDayInput');
      if (input && !input.value) input.value = p.day || state.ts.date || tsTodayIso();
      _tsDcPanels('day');
      input?.focus();
      return;
    }
    if (key === 'custom') {
      // Seed with the current week so Appliquer is one click away.
      const seed = _tsResolvePeriod('this_week', tsTodayIso(), null);
      const from = document.getElementById('tsDcFrom');
      const to = document.getElementById('tsDcTo');
      if (from && !from.value) from.value = p.from || seed.from;
      if (to && !to.value) to.value = p.to || seed.to;
      _tsDcPanels('custom');
      from?.focus();
      return;
    }
    p.key = key;
    _tsPeriodSave();
    _tsDateClose();
    _tsPeriodApply();
  }

  function _tsDcPanels(which) {
    document.getElementById('tsDcDayPanel')?.classList.toggle('hidden', which !== 'day');
    document.getElementById('tsDcRangePanel')?.classList.toggle('hidden', which !== 'custom');
  }

  // Selection → grid day (one day, always) → repaint. Re-reads the day only
  // when it actually changed, so picking a range that already contains the
  // visible day stays a repaint, not a fetch.
  function _tsPeriodApply() {
    const range = _tsPeriodRange();
    const day = _tsGridDayFor(range);
    _tsDateRenderButton();
    if (day && day !== state.ts.date) {
      state.ts.date = day;
      loadTestSchedule();
      return;
    }
    renderTestSchedule();
  }

  // ‹ / › — step one day and make that day the selection.
  function _tsDateStepDay(delta) {
    const next = _tsYmdShift(state.ts.date || tsTodayIso(), delta);
    const p = _tsPeriodState();
    // Landing on today / yesterday reads as "Aujourd'hui" / "Hier", not as a
    // bare date — the same words the menu uses for those two days.
    const todayIso = tsTodayIso();
    if (next === todayIso) { p.key = 'today'; }
    else if (next === _tsYmdShift(todayIso, -1)) { p.key = 'yesterday'; }
    else { p.key = 'day'; p.day = next; }
    _tsPeriodSave();
    state.ts.date = next;
    _tsDateRenderButton();
    loadTestSchedule();
  }

  function _tsDateRenderButton() {
    const btn = document.getElementById('tsDateBtn');
    const label = document.getElementById('tsDateBtnLabel');
    const chip = document.getElementById('tsDateBtnChip');
    if (!btn || !label) return;
    const p = _tsPeriodState();
    const r = _tsPeriodRange();
    const { label: text, chip: chipText } = _tsPeriodLabel();
    label.textContent = text;
    if (chip) {
      chip.textContent = chipText;
      chip.classList.toggle('hidden', !chipText);
    }
    btn.classList.toggle('is-filtered', p.key !== 'today');
    btn.title = r.error
      ? r.error
      : (r.from === r.to ? 'Jour affiché : ' + r.from : 'Compteurs du ' + r.from + ' au ' + r.to + ' · grille : ' + state.ts.date);
    // Mark the active row + keep the panels in sync with the selection.
    const menu = document.getElementById('tsDateMenu');
    if (menu?.dataset.built) {
      for (const row of menu.querySelectorAll('.ts-dc-item')) {
        const on = row.getAttribute('data-period') === p.key;
        // `active` keeps the row-highlight the Inbox menus use for their own
        // selected row; the tick is the single-select mark.
        row.classList.toggle('active', on);
        row.setAttribute('aria-checked', on ? 'true' : 'false');
        const slot = row.querySelector('.ts-dc-tick');
        if (!slot) continue;
        // Rebuild the slot every time: refreshIcons() REPLACES the <i> with an
        // <svg>, so appending without clearing would stack a second glyph on
        // each re-render.
        clear(slot);
        if (on) slot.appendChild(lucideIcon('check'));
      }
      refreshIcons(menu);
      if (!menu.classList.contains('hidden')) _tsDcPanels(p.key === 'day' || p.key === 'custom' ? p.key : null);
    }
  }

  // ── open / close, outside click, Escape, arrow keys ─────────────────────
  function _tsDateMenuRows() {
    const menu = document.getElementById('tsDateMenu');
    return menu ? [...menu.querySelectorAll('.ts-dc-item')] : [];
  }
  function _tsDateClose(focusBtn) {
    const menu = document.getElementById('tsDateMenu');
    const btn = document.getElementById('tsDateBtn');
    if (!menu || menu.classList.contains('hidden')) return;
    menu.classList.add('hidden');
    menu.classList.remove('is-flip-right');
    btn?.setAttribute('aria-expanded', 'false');
    _tsDcPanels(null);
    if (focusBtn) btn?.focus();
  }
  function _tsDateOpen(focusFirst) {
    const menu = _tsDateMenuBuild();
    const btn = document.getElementById('tsDateBtn');
    if (!menu) return;
    menu.classList.remove('hidden');
    btn?.setAttribute('aria-expanded', 'true');
    const p = _tsPeriodState();
    _tsDcPanels(p.key === 'day' || p.key === 'custom' ? p.key : null);
    _tsDateRenderButton();
    // Never clip at the viewport edge: flip to right-aligned when the menu
    // would overflow, and cap its height on a short window.
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth - 8) menu.classList.add('is-flip-right');
    refreshIcons(menu);
    if (focusFirst) {
      const rows = _tsDateMenuRows();
      (rows.find(r => r.classList.contains('active')) || rows[0])?.focus();
    }
  }
  function _tsDateToggle() {
    const menu = document.getElementById('tsDateMenu');
    if (!menu || menu.classList.contains('hidden')) _tsDateOpen(false);
    else _tsDateClose();
  }

  let _tsDateWired = false;
  function _tsPeriodInit() {
    const btn = document.getElementById('tsDateBtn');
    if (!btn) return;
    _tsDateMenuBuild();
    if (!_tsDateWired) {
      _tsDateWired = true;
      btn.addEventListener('click', (ev) => { ev.stopPropagation(); _tsDateToggle(); });
      btn.addEventListener('keydown', (ev) => {
        if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') { ev.preventDefault(); _tsDateOpen(true); }
      });
      // Rows are real <button>s, so Enter / Space activate them natively; the
      // arrows move between them and Escape hands focus back to the button.
      document.getElementById('tsDateMenu')?.addEventListener('keydown', (ev) => {
        const rows = _tsDateMenuRows();
        if (!rows.length) return;
        const i = rows.indexOf(document.activeElement);
        if (ev.key === 'ArrowDown') { ev.preventDefault(); rows[i < 0 ? 0 : (i + 1) % rows.length].focus(); }
        else if (ev.key === 'ArrowUp') { ev.preventDefault(); rows[i <= 0 ? rows.length - 1 : i - 1].focus(); }
        else if (ev.key === 'Home') { ev.preventDefault(); rows[0].focus(); }
        else if (ev.key === 'End') { ev.preventDefault(); rows[rows.length - 1].focus(); }
        else if (ev.key === 'Escape') { ev.preventDefault(); _tsDateClose(true); }
        else if (ev.key === 'Tab') { _tsDateClose(); }
      });
      // Escape anywhere and a click outside the control close it. Both no-op
      // when the menu is already closed, so no other view is affected.
      document.addEventListener('keydown', (ev) => { if (ev.key === 'Escape') _tsDateClose(true); });
      document.addEventListener('click', (ev) => {
        const menu = document.getElementById('tsDateMenu');
        if (!menu || menu.classList.contains('hidden')) return;
        if (!ev.target.closest?.('#tsDateControl')) _tsDateClose();
      });
    }
    _tsDateRenderButton();
  }

  // Stat cards over the period. A single-day range that IS the nav day needs no
  // request — the day payload already on screen carries those seats, so the
  // cards stay instant and identical to before. Anything wider is one call to
  // /api/test-schedule/stats, cached per (range + filters).
  let _tsStatsToken = 0;
  function _tsPeriodStatsKey(range) {
    return [range.from, range.to, state.ts.teacherFilter || 'all', state.ts.agentFilter || 'all'].join('|');
  }
  // Any change to the underlying seats (Refresh, a booking, a mark-no-show /
  // completed…) must drop the cached range numbers, or the strip would keep
  // showing a stale count while the grid below shows the new reality.
  function _tsInvalidatePeriodStats() {
    state.ts.statsCache = null;
  }

  // dayCounts/dayTotal describe the DAY on screen; used as-is when the period
  // resolves to exactly that day.
  function _tsApplyPeriodStats(dayCounts, dayTotal, filtered) {
    // Availability mode reuses this view with the stat strip CSS-hidden — no
    // strip to paint, and no reason to query a range for it.
    if (document.getElementById('testScheduleView')?.classList.contains('availability-mode')) return;
    const range = _tsPeriodRange();
    _tsDateRenderButton();
    if (range.from === state.ts.date && range.to === state.ts.date) {
      _tsRenderStats(dayCounts, dayTotal, filtered, { range, totalKind: 'seats', capacityScope: 'day' });
      return;
    }
    const key = _tsPeriodStatsKey(range);
    const cached = state.ts.statsCache;
    if (cached && cached.key === key) {
      _tsRenderStats(cached.counts, cached.total, filtered, { range, totalKind: cached.totalKind, capacityScope: cached.capacityScope });
      return;
    }
    _tsRenderStats(null, null, filtered, { range, loading: true });
    const token = ++_tsStatsToken;
    const qs = new URLSearchParams({ from: range.from, to: range.to });
    if (state.ts.teacherFilter && state.ts.teacherFilter !== 'all') qs.set('teacher_id', state.ts.teacherFilter);
    if (state.ts.agentFilter && state.ts.agentFilter !== 'all') qs.set('agent', state.ts.agentFilter);
    api('/api/test-schedule/stats?' + qs.toString())
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (token !== _tsStatsToken) return;
        if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
        const counts = j.counts || {};
        const meta = { totalKind: j.total_kind || 'booked_tests', capacityScope: j.capacity_scope || 'none' };
        state.ts.statsCache = { key, counts, total: counts.total || 0, ...meta };
        _tsRenderStats(counts, counts.total || 0, filtered, { range, ...meta });
      })
      .catch((err) => {
        if (token !== _tsStatsToken) return;
        _tsRenderStats(null, null, filtered, { range, error: String(err?.message || err) });
      });
  }

  // The stat strip. Extracted so the filtered-empty grid can still paint honest
  // zeros instead of leaving the previous day's numbers on screen.
  // `opts.range` = the period the numbers describe; `opts.loading` /
  // `opts.error` paint the strip honestly while the range is being counted
  // server-side instead of leaving the previous range's numbers on screen.
  function _tsRenderStats(counts, total, filtered, opts) {
    const stats = document.getElementById('tsStats');
    if (!stats) return;
    clear(stats);
    const range = opts?.range || null;
    const loading = !!opts?.loading;
    const failed = !!opts?.error;
    const c = counts || {};
    // Capacity is a single-day number: the grid SEEDS a day's free seats when
    // it opens it, so a range read only sees the days someone already visited.
    // Over a period the card prints "—" and `Total` counts the tests actually
    // booked instead (see the stats route's CAPACITY note).
    const capacityUnknown = opts?.capacityScope === 'none' || c.available === null;
    const num = (v) => (loading || failed ? '—' : String(v || 0));
    const TITLES = {
      total: opts?.totalKind === 'booked_tests'
        ? 'Tests li tbookaw f l’période (machi capacité)'
        : 'Seats dyal nhar li f l’écran',
      available: capacityUnknown
        ? 'Capacité = ghir nhar li f l’écran'
        : 'Seats khawyin f nhar li f l’écran',
    };
    const pairs = [
      ['total', filtered ? 'Total (filtered)' : (opts?.totalKind === 'booked_tests' ? 'Tests bookés' : 'Total'), total],
      ['available', 'Available', capacityUnknown ? null : c.available],
      ['booked', 'Booked', c.booked],
      ['in_test', 'In test', c.in_test],
      ['completed', 'Completed', c.completed],
      ['no_show', 'No-show', c.no_show],
      ['cancelled', 'Cancelled', c.cancelled],
    ];
    const _availabilityMode = !!document.getElementById('testScheduleView')?.classList.contains('availability-mode');
    const multiDay = !!range && range.from !== range.to;
    // Say which period the numbers describe whenever they are NOT the day the
    // grid is showing (a wider range, or a single day like "Hier").
    const offDay = !!range && (range.from !== state.ts.date || range.to !== state.ts.date);
    const rangeLabel = offDay ? _tsFmtRange(range.from, range.to) : '';
    stats.classList.toggle('is-loading', loading);
    stats.classList.toggle('is-stale', failed);
    if (failed) stats.title = 'Compteurs indisponibles : ' + opts.error;
    else stats.removeAttribute('title');
    for (const [key, k, v] of pairs) {
      // A null value is "not counted over a period" (capacity), not zero.
      const shown = v === null ? '—' : num(v);
      const kids = [
        el('span', { class: 'ts-stat-num', text: shown }),
        el('span', { class: 'ts-stat-lbl', text: k }),
      ];
      // NO-SHOW card → manual follow-up popup (sales team only, same gate as
      // the server's requireBulkSend). Everyone else keeps the plain tile. Over
      // a period it opens the whole range.
      if (key === 'no_show' && !_availabilityMode && _tsCanNoshowFollowup()) {
        const btn = el('button', {
          type: 'button',
          class: 'ts-stat ts-stat-' + key + ' is-actionable',
          title: multiDay ? 'Relancer les no-shows du ' + range.from + ' au ' + range.to : 'Relancer les no-shows du ' + ((range && range.from) || state.ts.date),
          'aria-label': 'No-show : ' + num(v) + ' — ouvrir la relance',
        }, kids);
        // The popup always follows the PERIOD (a single-day period opens that
        // day, which is not necessarily the day the grid is on — e.g. "Hier").
        btn.addEventListener('click', () => openTsNoshowModal(range ? (multiDay ? { from: range.from, to: range.to } : range.from) : state.ts.date));
        stats.appendChild(btn);
        continue;
      }
      const tile = el('div', { class: 'ts-stat ts-stat-' + key + (v === null ? ' is-na' : '') }, kids);
      if (TITLES[key]) tile.title = TITLES[key];
      stats.appendChild(tile);
    }
    if (rangeLabel) {
      stats.appendChild(el('div', { class: 'ts-stats-period' }, [
        lucideIcon('calendar-range'),
        el('span', { text: 'Période : ' + rangeLabel + (loading ? ' · calcul…' : '') }),
      ]));
      refreshIcons(stats);
    }
  }

  // ─── No-show follow-up popup (2026-09-17, Ayoub) ─────────────────────────
  // NO-SHOW stat card → the date's no-show seats (scoped server-side: an agent
  // only gets their own), every sendable row pre-ticked, the agent PICKS a
  // noshow_* template each time (nothing selected by default), confirms, and
  // reads per-row results. Rows that already moved on (rebooked, or a lead past
  // the test) are drawn disabled and are never sent — the server re-checks
  // scope, stage and template on every seat regardless of what this view says.
  const TS_NOSHOW_ROLES = ['admin', 'super_admin', 'super_sales', 'agent'];
  function _tsCanNoshowFollowup() {
    return TS_NOSHOW_ROLES.includes(String(state.me?.role || '').toLowerCase());
  }
  const TS_NOSHOW_REASON = {
    rebooked: 'Rebooké',
    no_phone: 'Sans numéro',
    not_found: 'Introuvable / hors périmètre',
    duplicate_phone: 'Même numéro, déjà dans cet envoi',
    already_sent: 'Déjà envoyé (ce template)',
    in_progress: 'Envoi déjà en cours',
    wrong_day: 'Mauvais jour pour ce template',
    'stage:test_done': 'Test déjà passé',
    'stage:proforma_sent': 'Proforma envoyée',
    'stage:paid': 'Déjà payé',
    'stage:not_interested': 'Pas intéressé',
    'stage:lost': 'Perdu',
  };
  function _tsNoshowReason(code) { return TS_NOSHOW_REASON[code] || String(code || 'Échec'); }
  function _tsNoshowDm(ymd) { return ymd ? String(ymd).slice(8, 10) + '/' + String(ymd).slice(5, 7) : ''; }
  function _tsNoshowWhen(iso) {
    const d = new Date(iso);
    if (!iso || Number.isNaN(d.getTime())) return '';
    return MA.fmt('fr-dmhm', 'fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }, d);
  }
  // Preview-only mirror of lib/test-schedule/noshow-followup.js firstName():
  // a compound first name ("عبد الله", "El Mehdi") keeps its second token.
  const TS_NOSHOW_NAME_PREFIXES = new Set(['عبد', 'ام', 'أم', 'ابو', 'أبو', 'بو', 'el', 'al', 'ben', 'ould', 'moulay', 'mulay', 'sidi', 'lalla', 'abd', 'abdel', 'abou', 'oum']);
  function _tsNoshowFirstName(full) {
    const tokens = String(full || '').trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return '';
    if (tokens.length >= 2 && TS_NOSHOW_NAME_PREFIXES.has(tokens[0].toLowerCase())) return tokens[0] + ' ' + tokens[1];
    return tokens[0];
  }

  let _tsNoshowWired = false;
  function _wireTsNoshowModal() {
    if (_tsNoshowWired) return;
    _tsNoshowWired = true;
    document.getElementById('tsNoshowAll')?.addEventListener('change', (ev) => {
      const ns = state.ts.noshow;
      if (!ns || ns.phase !== 'compose') return;
      const tpl = _tsNoshowTemplate();
      ns.checked = new Set(ev.target.checked ? ns.rows.filter(r => _tsNoshowSendable(r, tpl)).map(r => r.slot_id) : []);
      ns.autoUnticked = new Set();
      _tsNoshowRenderList();
      _tsNoshowSync();
    });
    document.getElementById('tsNoshowTemplate')?.addEventListener('change', () => _tsNoshowOnTemplate());
    for (const id of ['tsNoshowSlot1', 'tsNoshowSlot2']) {
      document.getElementById(id)?.addEventListener('input', () => _tsNoshowSync());
    }
    document.getElementById('tsNoshowSendBtn')?.addEventListener('click', () => _tsNoshowPrimary());
    document.getElementById('tsNoshowBackBtn')?.addEventListener('click', () => {
      const ns = state.ts.noshow;
      if (!ns || ns.phase !== 'confirm') return;
      ns.phase = 'compose';
      _tsNoshowRenderList();
      _tsNoshowSync();
    });
  }

  // Every open fetches FRESH rows (a new token drops any in-flight response of
  // an earlier open — e.g. a popup closed mid-send). `notice` is shown once the
  // fresh data is on screen (used after a failed/uncertain send).
  //
  // `range` is either a 'YYYY-MM-DD' (the single-day shape the live card uses)
  // or { from, to } coming from the period selector.
  async function openTsNoshowModal(range, { notice = '' } = {}) {
    const single = typeof range === 'string' ? range : (range && range.from === range.to ? range.from : '');
    const from = single || range?.from || '';
    const to = single || range?.to || '';
    if (!from || !to || !_tsCanNoshowFollowup()) return;
    const multiDayRange = !single;
    _wireTsNoshowModal();
    const token = String(Date.now()) + ':' + from + ':' + to + ':' + Math.random().toString(36).slice(2);
    state.ts.noshow = {
      token, date: single || '', from, to,
      rows: [], templates: [], checked: new Set(), autoUnticked: new Set(),
      phase: 'loading', results: null, freeSlots: null, max: 100, today: '', overCap: false, truncated: 0,
    };
    const title = document.getElementById('tsNoshowTitle');
    const dmy = (d) => String(d).slice(8, 10) + '/' + String(d).slice(5, 7) + '/' + String(d).slice(0, 4);
    if (title) title.textContent = 'No-show — ' + (single ? dmy(single) : dmy(from) + ' → ' + dmy(to));
    const select = document.getElementById('tsNoshowTemplate');
    if (select) { clear(select); select.appendChild(el('option', { value: '', text: 'Chargement…' })); select.disabled = true; }
    for (const id of ['tsNoshowSlot1', 'tsNoshowSlot2']) { const i = document.getElementById(id); if (i) { i.value = ''; i.disabled = false; } }
    document.getElementById('tsNoshowSlots')?.classList.add('hidden');
    document.getElementById('tsNoshowPreview')?.classList.add('hidden');
    document.getElementById('tsNoshowConfirm')?.classList.add('hidden');
    document.getElementById('tsNoshowError')?.classList.add('hidden');
    document.getElementById('tsNoshowError')?.classList.remove('is-notice');
    document.getElementById('tsNoshowCompose')?.classList.remove('hidden');
    const host = document.getElementById('tsNoshowList');
    if (host) { clear(host); host.appendChild(el('div', { class: 'ts-noshow-state', text: 'Chargement des no-shows…' })); }
    _tsNoshowSync();
    openModal('tsNoshowModal');
    try {
      const qs = single
        ? 'date=' + encodeURIComponent(single)
        : 'from=' + encodeURIComponent(from) + '&to=' + encodeURIComponent(to);
      const r = await api('/api/test-schedule/noshow-followup?' + qs);
      const j = await r.json().catch(() => ({}));
      if (state.ts.noshow?.token !== token) return;
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      const ns = state.ts.noshow;
      ns.rows = Array.isArray(j.rows) ? j.rows : [];
      ns.templates = Array.isArray(j.templates) ? j.templates : [];
      ns.max = Number(j.max) || 100;
      ns.today = j.today || '';
      ns.truncated = Number(j.truncated) || 0;
      ns.eligibleTotal = Number(j.eligible) || 0;
      // Over the cap nothing is pre-ticked: a silent slice of N would send to
      // the wrong third of the list. The agent narrows the period (or ticks).
      ns.overCap = !!j.over_cap;
      // Ayoub's "everything pre-checked" was decided for ONE day. Over a
      // period (a remembered "Ce mois" can mean 500+ clients) nothing is
      // pre-ticked — the agent ticks on purpose, or uses "Tout sélectionner".
      const preTick = !ns.overCap && !multiDayRange;
      ns.checked = preTick ? new Set(ns.rows.filter(row => row.eligible).map(row => row.slot_id)) : new Set();
      ns.phase = 'compose';
      _tsNoshowRenderTemplates();
      _tsNoshowRenderList();
      _tsNoshowSync();
      // One honest line about the scope. Over a period the popup opens with
      // NOTHING ticked, so it has to say what is loaded and what is selected.
      const errEl = document.getElementById('tsNoshowError');
      if (errEl && (ns.overCap || ns.truncated || multiDayRange)) {
        const head = ns.truncated
          ? ns.truncated + ' no-shows sur la période (affichés : ' + ns.rows.length + '). '
          : ns.eligibleTotal + ' no-show' + (ns.eligibleTotal === 1 ? '' : 's') + ' à relancer sur la période — 0 coché. ';
        errEl.textContent = head + (ns.overCap || ns.truncated
          ? 'Maximum ' + ns.max + ' par envoi — réduis la période ou coche une partie.'
          : 'Coche les lignes à relancer, ou utilise « Tout sélectionner ».');
        errEl.classList.toggle('is-notice', !ns.overCap && !ns.truncated);
        errEl.classList.remove('hidden');
      }
    } catch (err) {
      if (state.ts.noshow?.token !== token) return;
      state.ts.noshow.phase = 'error';
      if (host) {
        clear(host);
        host.appendChild(el('div', { class: 'ts-noshow-state is-error' }, [
          lucideIcon('alert-triangle'),
          el('span', { text: 'Impossible de charger les no-shows : ' + String(err?.message || err) }),
        ]));
        refreshIcons(host);
      }
      _tsNoshowSync();
    }
    if (notice && state.ts.noshow?.token === token) {
      const errEl = document.getElementById('tsNoshowError');
      if (errEl) { errEl.textContent = notice; errEl.classList.remove('hidden'); }
    }
  }

  function _tsNoshowTemplate() {
    const ns = state.ts.noshow;
    const id = document.getElementById('tsNoshowTemplate')?.value || '';
    return (ns && id) ? (ns.templates.find(t => String(t.id) === id) || null) : null;
  }
  function _tsNoshowNeedsSlots(tpl) {
    const p = Array.isArray(tpl?.agent_params) ? tpl.agent_params : [];
    return p.includes('slot1') || p.includes('slot2');
  }

  function _tsNoshowRenderTemplates() {
    const ns = state.ts.noshow;
    const select = document.getElementById('tsNoshowTemplate');
    if (!ns || !select) return;
    clear(select);
    if (!ns.templates.length) {
      select.appendChild(el('option', { value: '', text: 'Aucun template no-show approuvé' }));
      select.disabled = true;
      return;
    }
    select.appendChild(el('option', { value: '', text: 'Choisir un template…' }));
    const groups = new Map();
    for (const t of ns.templates) {
      if (!groups.has(t.meta_name)) groups.set(t.meta_name, []);
      groups.get(t.meta_name).push(t);
    }
    // Time-bound wording ("today" / "yesterday"): the server says whether ANY
    // listed seat is on the template's required date (day_ok). No match at all
    // → the option is disabled. Some matches → it stays pickable and the rows
    // of the other dates are unticked + disabled instead (see _tsNoshowSendable).
    // The send route enforces the same rule per seat.
    const DAY_HINT = { today: 'ghir l nhar lyoum', yesterday: 'ghir l lbare7' };
    for (const [metaName, list] of groups) {
      const og = el('optgroup', { label: metaName });
      for (const t of list) {
        const off = t.day_ok === false;
        const label = (t.name || t.meta_name) + (off && DAY_HINT[t.day_rule] ? ' — ' + DAY_HINT[t.day_rule] : '');
        const opt = el('option', { value: String(t.id), text: label });
        if (off) opt.disabled = true;
        og.appendChild(opt);
      }
      select.appendChild(og);
    }
    select.value = '';
    select.disabled = false;
  }

  // The follow-ups of `row` that used this meta_name (newest last).
  function _tsNoshowSentWith(row, metaName) {
    return (Array.isArray(row?.followups) ? row.followups : []).filter(f => metaName && f.meta_name === metaName);
  }

  // Does the picked template's date rule fit THIS row? Over a range a
  // "today only" / "yesterday only" template is pickable while some rows match,
  // so the mismatching rows are the ones taken out.
  function _tsNoshowDayOk(row, tpl) {
    const required = tpl?.required_date || '';
    return !required || String(row?.slot_date || '') === required;
  }
  // Can this row be sent with the picked template? (Row eligibility is the
  // server's; the rest mirrors the server's per-row guards.)
  function _tsNoshowSendable(row, tpl) {
    if (!row?.eligible) return false;
    if (!_tsNoshowDayOk(row, tpl)) return false;
    return true;
  }

  // A template change re-derives the default ticks: rows that ALREADY got this
  // template, or whose date breaks its day rule, are unticked (a wrong-day row
  // is also disabled — the server would refuse it); rows that got a different
  // template stay ticked. Rows unticked by a previous template choice are
  // re-ticked unless the agent touched them.
  function _tsNoshowApplyTemplateTicks(tpl) {
    const ns = state.ts.noshow;
    if (!ns) return;
    for (const id of ns.autoUnticked) {
      const row = ns.rows.find(r => r.slot_id === id);
      if (row && _tsNoshowSendable(row, tpl) && !_tsNoshowSentWith(row, tpl?.meta_name).length) ns.checked.add(id);
    }
    ns.autoUnticked = new Set();
    for (const row of ns.rows) {
      if (!ns.checked.has(row.slot_id)) continue;
      const blocked = !_tsNoshowSendable(row, tpl) || _tsNoshowSentWith(row, tpl?.meta_name).length > 0;
      if (blocked) {
        ns.checked.delete(row.slot_id);
        ns.autoUnticked.add(row.slot_id);
      }
    }
  }

  async function _tsNoshowOnTemplate() {
    const ns = state.ts.noshow;
    if (!ns) return;
    const tpl = _tsNoshowTemplate();
    _tsNoshowApplyTemplateTicks(tpl);
    _tsNoshowRenderList();
    const needsSlots = _tsNoshowNeedsSlots(tpl);
    document.getElementById('tsNoshowSlots')?.classList.toggle('hidden', !needsSlots);
    _tsNoshowSync();
    if (!needsSlots) return;
    // Prefill (once per popup) with the next free seats; never overwrite what
    // the agent already typed.
    if (!ns.freeSlots) {
      const token = ns.token;
      try {
        const r = await api('/api/test-schedule/noshow-followup/free-slots?limit=2');
        const j = await r.json().catch(() => ({}));
        if (state.ts.noshow?.token !== token) return;
        ns.freeSlots = r.ok && Array.isArray(j.slots) ? j.slots : [];
      } catch (_) {
        if (state.ts.noshow?.token !== token) return;
        ns.freeSlots = [];
      }
    }
    ['tsNoshowSlot1', 'tsNoshowSlot2'].forEach((id, i) => {
      const input = document.getElementById(id);
      if (input && !input.value && ns.freeSlots[i]) input.value = ns.freeSlots[i].label;
    });
    _tsNoshowSync();
  }

  function _tsNoshowRenderList() {
    const ns = state.ts.noshow;
    const host = document.getElementById('tsNoshowList');
    if (!ns || !host) return;
    clear(host);
    const multiDay = ns.from !== ns.to;
    if (!ns.rows.length) {
      host.appendChild(el('div', { class: 'ts-noshow-empty' }, [
        lucideIcon('calendar-check'),
        el('div', { class: 'ts-noshow-empty-title', text: multiDay ? 'Aucun no-show sur cette période' : 'Aucun no-show pour cette date' }),
        el('div', { class: 'ts-noshow-empty-sub', text: 'Les absences de vos tests apparaîtront ici.' }),
      ]));
      refreshIcons(host);
      return;
    }
    const locked = ns.phase !== 'compose';
    const results = ns.results || null;
    const tpl = _tsNoshowTemplate();
    for (const row of ns.rows) {
      const result = results ? results.get(row.slot_id) : null;
      const sendable = _tsNoshowSendable(row, tpl);
      const wrongDay = row.eligible && !_tsNoshowDayOk(row, tpl);
      const checkbox = el('input', { type: 'checkbox', class: 'ts-noshow-check', 'aria-label': 'Sélectionner ' + (row.student_name || 'ce client') });
      checkbox.checked = sendable && ns.checked.has(row.slot_id);
      checkbox.disabled = !sendable || locked;
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) ns.checked.add(row.slot_id); else ns.checked.delete(row.slot_id);
        ns.autoUnticked.delete(row.slot_id); // the agent's choice now wins
        _tsNoshowSync();
      });
      const owner = row.owner_name || (row.owner ? _agentDisplay(row.owner) : '') || 'Sans agent';
      const main = el('div', { class: 'ts-noshow-main' }, [
        el('div', { class: 'ts-noshow-line1' }, [
          el('span', { class: 'ts-noshow-name', text: row.student_name || 'Sans nom' }),
          row.phone ? el('span', { class: 'ts-noshow-phone', text: displayPhone(row.phone) }) : null,
        ].filter(Boolean)),
        el('div', { class: 'ts-noshow-line2' }, [
          // Over a range the date leads the line — a bare "10:15" would not say
          // which day the client missed.
          multiDay ? el('span', { class: 'ts-noshow-date', text: _tsNoshowDm(row.slot_date) }) : null,
          el('span', { class: 'ts-noshow-time', text: row.slot_time || '' }),
          el('span', { class: 'ts-noshow-dot', text: '·' }),
          el('span', { text: row.teacher_name || '' }),
          el('span', { class: 'ts-noshow-dot', text: '·' }),
          el('span', { text: owner }),
          el('span', { class: 'ts-noshow-dot', text: '·' }),
          el('span', { class: 'ts-noshow-stage', text: STAGE_LABELS[row.lead_stage] || row.lead_stage || '—' }),
        ]),
      ]);
      const tags = [];
      if (row.rebooked) {
        tags.push(el('span', { class: 'ts-noshow-tag is-info', text: 'Rebooké ' + _tsNoshowDm(row.rebooked.date) + (row.rebooked.time ? ' ' + row.rebooked.time : '') }));
      } else if (row.blocked_reason) {
        tags.push(el('span', { class: 'ts-noshow-tag is-muted', text: _tsNoshowReason(row.blocked_reason) }));
      }
      if (wrongDay) {
        // The picked template's wording only fits one date (today / yesterday).
        tags.push(el('span', {
          class: 'ts-noshow-tag is-muted',
          title: 'Ce template ne va qu’aux no-shows du ' + (tpl?.required_date || ''),
          text: 'Mauvais jour',
        }));
      }
      const followups = Array.isArray(row.followups) ? row.followups : [];
      if (followups.length) {
        // With a template picked, the badge names THAT template's send when
        // there is one (the reason the row got unticked); else the latest.
        const pickedMeta = _tsNoshowTemplate()?.meta_name || '';
        const same = _tsNoshowSentWith(row, pickedMeta);
        const last = same.length ? same[same.length - 1] : followups[followups.length - 1];
        const tplName = ns.templates.find(t => String(t.id) === String(last.templateId))?.name || last.meta_name || 'template';
        tags.push(el('span', {
          class: 'ts-noshow-tag is-done',
          title: followups.map(f => (f.meta_name || '') + ' · ' + _tsNoshowWhen(f.at) + (f.by ? ' · ' + _agentDisplay(f.by) : '')).join('\n'),
          text: 'Déjà relancé · ' + tplName + ' · ' + _tsNoshowWhen(last.at),
        }));
      }
      if (result) {
        tags.push(result.ok
          ? el('span', { class: 'ts-noshow-tag is-sent', text: 'Envoyé' })
          : el('span', { class: 'ts-noshow-tag is-failed', text: 'Échec : ' + _tsNoshowReason(result.error) }));
      }
      const cls = 'ts-noshow-row'
        + (!sendable ? ' is-disabled' : '')
        + (result ? (result.ok ? ' is-sent' : ' is-failed') : '');
      host.appendChild(el('label', { class: cls, 'data-slot-id': row.slot_id }, [
        checkbox, main, el('div', { class: 'ts-noshow-tags' }, tags),
      ]));
    }
  }

  // Everything that depends on the current selection/template/phase.
  function _tsNoshowSync() {
    const ns = state.ts.noshow;
    const sendBtn = document.getElementById('tsNoshowSendBtn');
    const backBtn = document.getElementById('tsNoshowBackBtn');
    const label = sendBtn?.querySelector('span');
    const all = document.getElementById('tsNoshowAll');
    const count = document.getElementById('tsNoshowCount');
    const preview = document.getElementById('tsNoshowPreview');
    const confirmBox = document.getElementById('tsNoshowConfirm');
    const select = document.getElementById('tsNoshowTemplate');
    const slotInputs = ['tsNoshowSlot1', 'tsNoshowSlot2'].map(id => document.getElementById(id));
    if (!ns || !sendBtn) return;

    const tpl = _tsNoshowTemplate();
    // "Eligible" = what CAN go out with the template currently picked, so a
    // time-bound template narrows the selection, the counter and select-all to
    // the rows of its own date.
    const eligible = ns.rows.filter(r => _tsNoshowSendable(r, tpl));
    const selected = eligible.filter(r => ns.checked.has(r.slot_id));
    const n = selected.length;
    const needsSlots = _tsNoshowNeedsSlots(tpl);
    const slot1 = (slotInputs[0]?.value || '').trim();
    const slot2 = (slotInputs[1]?.value || '').trim();
    const locked = ns.phase !== 'compose';

    if (all) {
      all.checked = eligible.length > 0 && n === eligible.length;
      all.indeterminate = n > 0 && n < eligible.length;
      all.disabled = locked || !eligible.length;
    }
    if (count) {
      const dayNote = tpl?.required_date ? ' · template du ' + _tsNoshowDm(tpl.required_date) : '';
      count.textContent = ns.phase === 'loading' || ns.phase === 'error' ? ''
        : n + ' sélectionné' + (n === 1 ? '' : 's') + ' · ' + eligible.length + ' éligible' + (eligible.length === 1 ? '' : 's') + ' sur ' + ns.rows.length + dayNote;
    }
    if (select && ns.templates.length) select.disabled = locked;
    slotInputs.forEach(i => { if (i) i.disabled = locked; });

    // Live preview — the first ticked client's first name.
    if (preview) {
      if (tpl && ns.phase !== 'done') {
        const first = selected[0] || eligible[0] || null;
        // `agent` is filled server-side from the sender's display name — mirror
        // that here so the preview never shows a raw {{agent}} placeholder.
        const values = {
          name: _tsNoshowFirstName(first?.student_name) || 'Prénom',
          agent: state.me?.displayName || state.me?.username || 'Agent',
          slot1: slot1 || '…',
          slot2: slot2 || '…',
        };
        const body = String(tpl.body || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (values[k] != null ? values[k] : m));
        clear(preview);
        preview.appendChild(el('div', { class: 'ts-noshow-preview-lbl', text: 'Aperçu' + (first?.student_name ? ' — ' + first.student_name : '') }));
        preview.appendChild(el('div', { class: 'ts-noshow-preview-body', dir: 'auto', text: body || '(template sans texte local)' }));
        preview.classList.remove('hidden');
      } else {
        preview.classList.add('hidden');
      }
    }

    const tooMany = n > (ns.max || 100);
    const ready = ns.phase === 'compose' && !!tpl && n > 0 && !tooMany && (!needsSlots || (slot1 && slot2));
    backBtn?.classList.toggle('hidden', ns.phase !== 'confirm');
    sendBtn.classList.toggle('hidden', ns.phase === 'done');
    if (confirmBox) {
      if (ns.phase === 'confirm') {
        clear(confirmBox);
        confirmBox.classList.remove('is-done');
        confirmBox.appendChild(lucideIcon('alert-triangle'));
        confirmBox.appendChild(el('span', { text: 'Envoyer « ' + (tpl?.name || tpl?.meta_name || '') + ' » à ' + n + ' client' + (n === 1 ? '' : 's') + ' sur WhatsApp ? Les messages partent immédiatement.' }));
        confirmBox.classList.remove('hidden');
        refreshIcons(confirmBox);
      } else if (ns.phase !== 'done') {
        confirmBox.classList.add('hidden');
      }
    }
    if (ns.phase === 'confirm') {
      sendBtn.disabled = false;
      if (label) label.textContent = 'Confirmer l’envoi à ' + n;
    } else if (ns.phase === 'sending') {
      sendBtn.disabled = true;
      if (label) label.textContent = 'Envoi en cours…';
    } else {
      sendBtn.disabled = !ready;
      if (label) label.textContent = tooMany ? ('Maximum ' + (ns.max || 100) + ' par envoi') : ('Envoyer à ' + n);
    }
  }

  async function _tsNoshowPrimary() {
    const ns = state.ts.noshow;
    if (!ns) return;
    if (ns.phase === 'compose') {
      const sendBtn = document.getElementById('tsNoshowSendBtn');
      if (sendBtn?.disabled) return;
      ns.phase = 'confirm';
      _tsNoshowRenderList();
      _tsNoshowSync();
      return;
    }
    if (ns.phase !== 'confirm') return;
    const tpl = _tsNoshowTemplate();
    const slotIds = ns.rows.filter(r => _tsNoshowSendable(r, tpl) && ns.checked.has(r.slot_id)).map(r => r.slot_id);
    if (!tpl || !slotIds.length) return;
    const body = { slotIds, templateId: String(tpl.id) };
    if (_tsNoshowNeedsSlots(tpl)) {
      body.slot1 = (document.getElementById('tsNoshowSlot1')?.value || '').trim();
      body.slot2 = (document.getElementById('tsNoshowSlot2')?.value || '').trim();
    }
    const errEl = document.getElementById('tsNoshowError');
    errEl?.classList.add('hidden');
    ns.phase = 'sending';
    _tsNoshowSync();
    const token = ns.token;
    try {
      const r = await api('/api/test-schedule/noshow-followup/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        // Sequential Meta sends — up to 100 of them must not hit the default timeout.
        timeoutMs: 10 * 60 * 1000,
      });
      const j = await r.json().catch(() => ({}));
      if (state.ts.noshow?.token !== token) return;
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      ns.results = new Map((Array.isArray(j.results) ? j.results : []).map(x => [String(x.slotId), x]));
      ns.phase = 'done';
      const confirmBox = document.getElementById('tsNoshowConfirm');
      if (confirmBox) {
        clear(confirmBox);
        confirmBox.appendChild(lucideIcon(j.failed ? 'alert-triangle' : 'check-circle-2'));
        confirmBox.appendChild(el('span', { text: (j.sent || 0) + ' envoyé' + ((j.sent || 0) === 1 ? '' : 's') + ' · ' + (j.failed || 0) + ' échec' + ((j.failed || 0) === 1 ? '' : 's') }));
        confirmBox.classList.toggle('is-done', !j.failed);
        confirmBox.classList.remove('hidden');
        refreshIcons(confirmBox);
      }
      document.getElementById('tsNoshowCompose')?.classList.add('hidden');
      _tsNoshowRenderList();
      _tsNoshowSync();
      toast((j.sent || 0) + ' relance' + ((j.sent || 0) === 1 ? '' : 's') + ' envoyée' + ((j.sent || 0) === 1 ? '' : 's')
        + (j.failed ? ' · ' + j.failed + ' échec' + (j.failed === 1 ? '' : 's') : ''), j.failed ? 'warning' : 'success');
    } catch (err) {
      if (state.ts.noshow?.token !== token) return;
      // ANY failure (network, proxy 524, non-2xx) may still have sent some of
      // the batch server-side. Never hand the same ticks back: reload the rows
      // (fresh "Déjà relancé" badges) and say so.
      const NOTICE = "Chi messages y9dro ykouno tsiftou — chouf l'badges 9bel ma t3awed.";
      const detail = String(err?.message || err || '').slice(0, 160);
      toast(NOTICE, 'warning');
      const modalOpen = !document.getElementById('tsNoshowModal')?.classList.contains('hidden');
      if (modalOpen) {
        await openTsNoshowModal(ns.date || { from: ns.from, to: ns.to }, { notice: (detail ? detail + ' — ' : '') + NOTICE });
      } else {
        // Closed mid-send: drop the stale state; the next open fetches fresh rows.
        state.ts.noshow = null;
      }
    }
  }

  function renderTestSchedule() {
    const grid = document.getElementById('tsGrid');
    if (!grid) return;
    clear(grid);
    // Drop the inline geometry the PREVIOUS render wrote (it is re-set below
    // from this render's column count). Without this, a one-column day — or a
    // teacher filter — leaves `width: fit-content` behind and the next empty
    // state renders shrink-wrapped instead of spanning the pane.
    grid.style.gridTemplateColumns = '';
    grid.style.minWidth = '';
    grid.style.width = '';
    grid.style.marginInline = '';

    const allTeachers = state.ts.teachers;
    // ─── Day filters (2026-09-07, swat:B1) ───────────────────────────────
    // Both narrow the day payload that is ALREADY in memory: the columns, the
    // time axis, the cells and the stat strip all read one filtered seat set,
    // so the numbers on top always describe what is on screen. A teacher never
    // sees the "par X" attribution on the grid, so the agent filter is not
    // theirs — force it open rather than filter on something they cannot read.
    if ((state.me?.role || '').toLowerCase() === 'teacher' && state.ts.agentFilter !== 'all') {
      state.ts.agentFilter = 'all';
      _tsSaveFilter('agent', 'all');
    }
    const teacherFilter = state.ts.teacherFilter || 'all';
    const agentFilter = state.ts.agentFilter || 'all';
    const _allSeats = Object.values(state.ts.slotsByCell || {}).flat();
    // Agent options are offered WITHIN the current teacher filter, so picking
    // one can never produce an empty grid.
    _tsPopulateFilters(allTeachers, teacherFilter === 'all' ? _allSeats : _allSeats.filter(s => s.teacher_id === teacherFilter));
    const teachers = teacherFilter === 'all' ? allTeachers : allTeachers.filter(t => t.id === teacherFilter);
    const _shownSeats = _allSeats.filter(s =>
      (teacherFilter === 'all' || s.teacher_id === teacherFilter) && _tsSeatMatchesAgent(s, agentFilter));
    const _filtered = teacherFilter !== 'all' || agentFilter !== 'all';
    // Time axis = the ACTUAL slot times present for this day (deduped, sorted) —
    // NOT the server's computed open-grid. Each cell key holds an ARRAY of seats
    // (up to seatsPerSlot), so flatten before mapping. Deriving rows from the
    // real slots keeps each day at its own interval AND keeps every booking
    // visible; falls back to the server grid only when a day has no slots.
    // Under a filter the axis collapses to the matching rows (no page of dashes)
    // and the server grid fallback is dropped — an empty result must read empty.
    const _slotTimes = _shownSeats.map(s => String(s.slot_time));
    const times = _slotTimes.length
      ? Array.from(new Set(_slotTimes)).sort()
      : (_filtered ? [] : (state.ts.times || []));

    // Teacher dashboard sections (visible only for role=teacher)
    _tsRenderTeacherDashboard();

    // Teachers are server-scoped to ONLY their own column (/api/test-schedule/slots
    // filters by their teacherId). The default "3 teachers …" subtitle would
    // wrongly imply they see the whole team — rewrite it to their own view.
    // Title + subtitle depend on the mode (Availability reuses this view) and
    // role. Availability = grid only; Test schedule = full.
    {
      const av = !!document.getElementById('testScheduleView')?.classList.contains('availability-mode');
      const isTeacher = (state.me?.role || '').toLowerCase() === 'teacher';
      const teacherManager = isTeacher && state.me?.teacherManager === true;
      const h2 = document.querySelector('#testScheduleView .ts-title-block h2');
      if (h2 && h2.lastChild && h2.lastChild.nodeType === 3) h2.lastChild.textContent = av ? ' Availability' : ' Test schedule';
      const sub = document.querySelector('.ts-subtitle');
      if (sub) sub.textContent = av
        ? 'Availability slots — free and booked (15 min, 10:00 to 21:00, every day; hours per teacher).'
        : (isTeacher
            ? (teacherManager
                ? "All teachers' placement-test slots — 15-min slots, 10:00 to 21:00, every day; hours per teacher."
                : 'Your placement-test slots — 15-min slots, 10:00 to 21:00, every day; hours per teacher.')
            : 'Placement-test slots — 3 teachers, 15-min slots, 10:00 to 21:00, every day; hours per teacher.');
    }

    if (!teachers.length || !times.length) {
      if (_filtered) {
        // Designed empty state: say WHICH filter emptied the day and offer the
        // way out, instead of a blank grid the operator has to reason about.
        const box = el('div', { class: 'ts-empty ts-empty-filtered' }, [
          lucideIcon('search-x'),
          el('div', { class: 'ts-empty-title', text: 'Aucun créneau pour ce filtre' }),
          el('div', { class: 'ts-empty-sub', text: 'Aucun test ne correspond à ce professeur / cet agent à cette date.' }),
        ]);
        const reset = el('button', { type: 'button', class: 'btn btn-ghost btn-sm ts-empty-reset', text: 'Réinitialiser les filtres' });
        reset.addEventListener('click', () => {
          state.ts.teacherFilter = 'all';
          _tsSaveFilter('teacher', 'all');
          _tsSetFilter('agent', 'all');
        });
        box.appendChild(reset);
        grid.appendChild(box);
        // The strip must describe the screen — zeros, not the unfiltered day.
        _tsApplyPeriodStats({ available: 0, booked: 0, in_test: 0, completed: 0, no_show: 0, cancelled: 0 }, 0, true);
        refreshIcons(grid);
        return;
      }
      grid.appendChild(el('div', { class: 'ts-empty', text: 'No teachers active for this date.' }));
      return;
    }

    // CSS grid: 1 time-label column + N teacher columns. A SINGLE teacher
    // (teacher role / one active teacher) used to stretch across the whole pane
    // (min-width:720px) and look sparse on a 13" — cap a lone column and center
    // it; only force a horizontal-scroll width when there are enough columns
    // (3+) to actually need it.
    const _nCols = teachers.length;
    const _colMax = _nCols === 1 ? '560px' : '1fr';
    grid.style.gridTemplateColumns = `80px repeat(${_nCols}, minmax(170px, ${_colMax}))`;
    grid.style.minWidth = _nCols >= 3 ? '720px' : '';
    // For a single teacher, shrink-wrap the grid box (fit-content) so the capped
    // column doesn't sit in a full-width box with dead space on the right, and
    // center it — a focused day-view column rather than one sparse stretched row.
    grid.style.width = _nCols === 1 ? 'fit-content' : '';
    grid.style.marginInline = _nCols === 1 ? 'auto' : '';

    // Header row
    grid.appendChild(el('div', { class: 'ts-corner', text: 'Time' }));
    for (const t of teachers) {
      const hdr = el('div', { class: 'ts-teacher-head' }, [
        el('span', { class: 'ts-teacher-color', style: `background:${t.color};` }),
        el('span', { class: 'ts-teacher-name', text: t.name }),
      ]);
      const canMoveDay = _isSalesAdmin() || state.me?.teacherManager === true;
      const movableCount = canMoveDay ? _tsMovableSlotsForTeacher(t.id).length : 0;
      if (movableCount) {
        const moveDayBtn = el('button', {
          type: 'button',
          class: 'ts-teacher-move-day',
          title: 'Move all ' + movableCount + ' transferable test' + (movableCount === 1 ? '' : 's') + ' for ' + t.name,
          'aria-label': 'Move ' + t.name + ' tests for this day',
        }, [lucideIcon('shuffle'), el('span', { text: 'Move day' })]);
        moveDayBtn.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          openTsBulkMoveModal(t);
        });
        hdr.appendChild(moveDayBtn);
      }
      grid.appendChild(hdr);
    }

    // Slot rows — each (teacher, time) cell now groups up to seatsPerSlot SEATS
    // (overbooking). Show a chip per booked seat + a "+ book" for free seats.
    const seatsPerSlot = state.ts.seatsPerSlot || 3;
    const _meRoleCell = (state.me?.role || '').toLowerCase();
    const _teacherCell = _meRoleCell === 'teacher';
    const counts = { available: 0, booked: 0, in_test: 0, completed: 0, no_show: 0, cancelled: 0, other: 0 };

    const _seatLabel = (s) => {
      if (s.lead_name || s.student_name) return String(s.lead_name || s.student_name); // full name everywhere
      if (!_teacherCell && s.lead_phone) return String(s.lead_phone);
      return '';
    };
    // One clickable chip (editorial mini-card) per booked seat → opens that
    // student's actions/eval modal. Status colour rides the CSS spine/eyebrow;
    // agent/CEFR/eval/notes are demoted into ONE .ts-seat-meta line. No inline
    // styles (they would override the stylesheet).
    const _seatChip = (s, tm, t) => {
      // Masked seat (server-scoped: another agent's booking, swat:B1) — a
      // neutral, NON-interactive occupancy marker: no name, no "par X", no
      // click. Reuses the existing gray ts-cell-other spine/eyebrow variant;
      // tokens-only inline override kills the .ts-seat pointer cursor.
      if (s.masked) {
        // Teachers get English everywhere (2026-07-21 audit); sales keep "Occupé".
        const _maskLabel = _teacherCell ? 'Busy' : 'Occupé';
        return el('div', {
          class: 'ts-seat ts-cell-other',
          style: 'cursor: default;',
          title: _maskLabel + ' • ' + tm.slice(0, 5) + ' • ' + t.name,
        }, [el('span', { class: 'ts-cell-status', text: _maskLabel })]);
      }
      const label = _seatLabel(s);
      const bookedBy = _teacherCell ? '' : bookedByLabel(s);
      const titleParts = [TS_STATUS_LABEL[s.status] || s.status, tm.slice(0, 5), t.name];
      if (label) titleParts.push(label);
      if (bookedBy) titleParts.push('by ' + bookedBy);
      const kids = [el('span', { class: 'ts-cell-status', text: TS_STATUS_LABEL[s.status] || s.status })];
      if (label) {
        const lbl = el('span', { class: 'ts-cell-label', text: label });
        if (s.name_unconfirmed) lbl.appendChild(_nameUnconfirmedBadge());
        kids.push(lbl);
      }
      const meta = [];
      if (bookedBy) meta.push(el('span', { class: 'ts-cell-by', text: 'par ' + bookedBy }));
      if (s.new_level) { meta.push(el('span', { class: 'cefr-pill cefr-' + s.new_level, text: s.new_level })); titleParts.push('Level: ' + s.new_level); }
      else if (s.status === 'completed') { meta.push(el('span', { class: 'ts-cell-pending-eval', text: 'Eval ?' })); titleParts.push('Pending evaluation'); }
      if (s.notes) { meta.push(el('span', { class: 'ts-cell-notes-dot', text: '📝', title: s.notes })); }
      if (meta.length) kids.push(el('div', { class: 'ts-seat-meta' }, meta));
      const chip = el('button', { class: `ts-seat ${TS_STATUS_CLASS[s.status] || 'ts-cell-other'}`, type: 'button', 'data-slot-id': s.id, title: titleParts.join(' • ') }, kids);
      chip.addEventListener('click', () => openTsActionsModal(s));
      return chip;
    };

    for (const tm of times) {
      grid.appendChild(el('div', { class: 'ts-time', text: tm.slice(0, 5) }));
      for (const t of teachers) {
        const cellSeats = state.ts.slotsByCell[`${t.id}|${tm}`];
        // No slot for this (teacher,time) → the teacher isn't working then (each
        // teacher has their own hours, e.g. Rahaf starts 12:00). Quiet dashed cell.
        if (!cellSeats || !cellSeats.length) { grid.appendChild(el('div', { class: 'ts-cell ts-cell-missing', text: '—', title: 'Hors des heures de ce prof' })); continue; }
        // The agent filter narrows WHICH seats are drawn in the cell; the
        // capacity meter below keeps reading the full cell so occupancy and the
        // "is-full" amber stay truthful.
        const seats = agentFilter === 'all' ? cellSeats : cellSeats.filter(s => _tsSeatMatchesAgent(s, agentFilter));
        if (!seats.length) { grid.appendChild(el('div', { class: 'ts-cell ts-cell-missing', text: '—', title: 'Aucun test de cet agent sur ce créneau' })); continue; }
        // Masked seats (another agent's booking, swat:B1) stay OUT of the status
        // KPIs — an agent's numbers reflect their own tests. They still occupy
        // the seat, so Total (physical capacity) and Available stay global.
        for (const s of seats) { if (s.masked) continue; if (counts[s.status] != null) counts[s.status]++; else counts.other++; }
        const isPast = tsSlotIsPast(state.ts.date, tm);
        // LIVE = active bookings that still might happen; TERMINAL = done/no-show;
        // FREE = bookable. The badge counts LIVE only (audit FE-1).
        const live = seats.filter(s => TS_LIVE_STATUSES.includes(s.status));
        const terminal = seats.filter(s => s.status === 'completed' || s.status === 'no_show');
        const free = seats.filter(s => s.status === 'available');
        // Cancelled (and any other non-live, non-terminal) seats are invisible in
        // the unfiltered grid by design. Under an AGENT filter they can be the
        // only thing that matched, so they must be drawn — otherwise the cell
        // would fall through and render as "Available" for a seat that is not
        // free. Empty (and therefore inert) whenever no agent filter is on.
        const other = agentFilter === 'all'
          ? []
          : seats.filter(s => s.status !== 'available' && !live.includes(s) && !terminal.includes(s));
        // Past with no live booking AND no terminal record → collapse (pure noise).
        if (isPast && !live.length && !terminal.length && !other.length) { grid.appendChild(el('div', { class: 'ts-cell ts-cell-past', text: '—', title: 'Past slot' })); continue; }
        // All free (no bookings at all) → one clean available cell + the pip meter.
        // Unreachable while an agent filter is on (a free seat carries no
        // attribution, so it never survives the filter) — by design: that view
        // is about one agent's bookings, not about capacity to sell.
        if (!live.length && !terminal.length && !other.length) {
          const cell = el('button', { class: 'ts-cell ts-cell-available', type: 'button', title: `Disponible • ${free.length}/${seatsPerSlot} • ${tm.slice(0,5)} • ${t.name}` }, [
            tsBuildMeter(seats, 0, seatsPerSlot),
            el('span', { class: 'ts-cell-status', text: 'Available' }),
            el('span', { class: 'ts-seat-free-hint', text: free.length + '/' + seatsPerSlot }),
          ]);
          cell.addEventListener('click', () => openTsBookModalForTime(t.id, state.ts.date, tm, t));
          grid.appendChild(cell);
          continue;
        }
        // Mixed/booked: pip-meter header + a chip per booked seat (live first, then
        // terminal) + "+ Ajouter" for free seats. .is-full → amber when 0 free.
        const isFull = !cellSeats.some(s => s.status === 'available');
        const wrap = el('div', { class: 'ts-cell ts-cell-multi' + (isFull ? ' is-full' : '') });
        wrap.appendChild(tsBuildMeter(cellSeats, cellSeats.filter(s => TS_LIVE_STATUSES.includes(s.status)).length, seatsPerSlot));
        for (const s of live) wrap.appendChild(_seatChip(s, tm, t));
        for (const s of terminal) wrap.appendChild(_seatChip(s, tm, t));
        for (const s of other) wrap.appendChild(_seatChip(s, tm, t));
        if (free.length && !isPast) {
          const add = el('button', { class: 'ts-seat-free', type: 'button', title: 'Surbooking — ajouter un élève (' + free.length + ' libre·s)' }, [lucideIcon('plus'), el('span', { text: 'Ajouter' })]);
          add.addEventListener('click', () => openTsBookModalForTime(t.id, state.ts.date, tm, t));
          wrap.appendChild(add);
        }
        grid.appendChild(wrap);
      }
    }

    // Stats bar — counted over the seats actually DRAWN, so a filtered grid and
    // its numbers can never disagree. Real seat-row count (not an idealized
    // times×teachers×seats grid) so the bar stays self-consistent on legacy /
    // partial / mid-migration days (audit BE-7).
    _tsApplyPeriodStats(counts, _shownSeats.length, _filtered);
    refreshIcons(grid);
  }

  function onTsCellClick(slotId) {
    const slot = Object.values(state.ts.slotsByCell).flat().find(s => s.id === slotId);
    if (!slot) return;
    if (slot.masked) return; // another agent's booking (server-masked, swat:B1) — not actionable
    if (slot.status === 'available') {
      openTsBookModalForTime(slot.teacher_id, slot.slot_date || state.ts.date, slot.slot_time, state.ts.teachers.find(x => x.id === slot.teacher_id));
    } else {
      openTsActionsModal(slot);
    }
  }

  // Book into a FREE SEAT at a (teacher, date, time) — overbooking. The server
  // picks the lowest free seat, so we send the TIME, not a seat id.
  function openTsBookModalForTime(teacherId, date, time, teacher) {
    state.ts.bookSlot = { seatBooking: true, teacher_id: teacherId, slot_date: date, slot_time: time };
    state.ts.bookLead = null;
    document.getElementById('tsBookSearch').value = '';
    document.getElementById('tsBookSalesNote').value = '';
    clear(document.getElementById('tsBookResults'));
    document.getElementById('tsBookError').classList.add('hidden');
    document.getElementById('tsBookConfirmBtn').disabled = true;
    const t = teacher || state.ts.teachers.find(x => x.id === teacherId);
    const summary = document.getElementById('tsBookSlotSummary');
    clear(summary);
    summary.appendChild(el('div', { class: 'ts-slot-summary-row' }, [
      el('strong', { text: (t?.name || 'Teacher') + ' · ' + String(time).slice(0, 5) + ' · ' + date }),
    ]));
    openModal('tsBookModal');
    setTimeout(() => document.getElementById('tsBookSearch').focus(), 50);
  }

  // ─── Booking modal ───
  function openTsBookModal(slot) {
    state.ts.bookSlot = slot;
    state.ts.bookLead = null;
    document.getElementById('tsBookSearch').value = '';
    document.getElementById('tsBookSalesNote').value = '';
    clear(document.getElementById('tsBookResults'));
    document.getElementById('tsBookError').classList.add('hidden');
    document.getElementById('tsBookConfirmBtn').disabled = true;
    const t = state.ts.teachers.find(x => x.id === slot.teacher_id);
    const summary = document.getElementById('tsBookSlotSummary');
    clear(summary);
    summary.appendChild(el('div', { class: 'ts-slot-summary-row' }, [
      el('strong', { text: (t?.name || 'Teacher') + ' · ' + String(slot.slot_time).slice(0,5) + ' · ' + state.ts.date }),
    ]));
    openModal('tsBookModal');
    setTimeout(() => document.getElementById('tsBookSearch').focus(), 50);
  }

  async function tsBookSearch(q) {
    const list = document.getElementById('tsBookResults');
    clear(list);
    if (!q) return;
    try {
      const r = await api('/api/leads?q=' + encodeURIComponent(q) + '&limit=20');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      const leads = j.leads || [];
      if (!leads.length) {
        list.appendChild(el('div', { class: 'ts-book-empty', text: 'No leads match. Try a phone or name fragment.' }));
        return;
      }
      for (const l of leads) {
        const row = el('button', {
          class: 'ts-book-result',
          type: 'button',
          onclick: () => {
            state.ts.bookLead = l;
            document.getElementById('tsBookConfirmBtn').disabled = false;
            list.querySelectorAll('.ts-book-result').forEach(n => n.classList.remove('selected'));
            row.classList.add('selected');
          },
        }, [
          el('div', { class: 'ts-book-result-main' }, [
            el('span', { class: 'ts-book-result-name', text: l.name || '(no name)' }),
            el('span', { class: 'ts-book-result-stage', text: l.pipeline_stage }),
          ]),
          el('div', { class: 'ts-book-result-phone', text: l.phone || '' }),
        ]);
        list.appendChild(row);
      }
    } catch (err) {
      list.appendChild(el('div', { class: 'ts-book-empty', text: 'Search failed: ' + (err.message || err) }));
    }
  }

  async function tsBookConfirm() {
    if (!state.ts.bookSlot || !state.ts.bookLead) return;
    const btn = document.getElementById('tsBookConfirmBtn');
    const errEl = document.getElementById('tsBookError');
    errEl.classList.add('hidden');
    btn.disabled = true;
    try {
      const bs = state.ts.bookSlot;
      const body = bs.seatBooking
        ? { teacher_id: bs.teacher_id, slot_date: bs.slot_date, slot_time: bs.slot_time, lead_id: state.ts.bookLead.id }
        : { slot_id: bs.id, lead_id: state.ts.bookLead.id };
      const salesNote = (document.getElementById('tsBookSalesNote')?.value || '').trim().slice(0, 500);
      if (salesNote) body.sales_note = salesNote;
      const r = await api('/api/test-schedule/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.status === 409) {
        const j = await r.json().catch(() => ({ error: 'Slot already booked' }));
        throw new Error(j.error || 'Conflict');
      }
      if (!r.ok) throw new Error('HTTP ' + r.status);
      closeModal('tsBookModal');
      toast('Booked. Lead moved to Test scheduled.', 'success');
      await loadTestSchedule();
    } catch (err) {
      errEl.textContent = String(err.message || err);
      errEl.classList.remove('hidden');
      btn.disabled = false;
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // Placement-test booking from chat header (lead-first flow).
  // Pre-fills the lead from the currently-selected chat; renders date tabs
  // (the full week, Sunday included) + per-teacher slot grids; one POST to
  // /api/test-schedule/book reserves the slot AND auto-advances the lead's
  // pipeline_stage to book_test (server-side).
  // ───────────────────────────────────────────────────────────────────
  function _websiteTestSetBusy(busy) {
    const btn = E.websiteTestBtn;
    if (!btn) return;
    btn.disabled = !!busy;
    btn.classList.toggle('is-loading', !!busy);
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function _websiteTestCopy(text) {
    if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
    return Promise.resolve().then(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } finally { ta.remove(); }
    });
  }

  function _websiteTestMessage(name, url) {
    const firstName = String(name || '').trim().split(/\s+/)[0];
    const hello = firstName ? ('Salam ' + firstName + ',') : 'Salam,';
    return hello + '\nHada howa test dyal niveau personnalisé dyalk: ' + url
      + '\n7ellih direct f navigateur — ma kay7taj la compte la application.';
  }

  // SMS carries the same link when the WhatsApp window is shut, but it is not
  // the same medium: the gateway sends the bytes as-is, so the copy stays
  // inside GSM-7 (no em dash, which alone would force UCS-2 and triple the
  // segment count) and short enough that the 64-char link still fits one part.
  function _websiteTestSmsMessage(name, url) {
    const firstName = String(name || '').trim().split(/\s+/)[0];
    const hello = firstName ? ('Salam ' + firstName + ',') : 'Salam,';
    return hello + ' hada test dyal niveau dyalk: ' + url + ' - 7ellih f navigateur, bla compte.';
  }

  // The Meta template that carries a personalized test link. It does not exist
  // until someone creates it and Meta approves it, so this is a lookup, not an
  // assumption: while it is missing the caller falls back to SMS, and the day it
  // is approved WhatsApp starts being used with no further code change.
  const WEBSITE_TEST_META_NAME = 'website_test_link';

  async function _websiteTestFindTemplate() {
    if (!Array.isArray(state.templatesCache) || !state.templatesCache.length) {
      try {
        const r = await api('/api/templates');
        const j = await r.json();
        state.templatesCache = j.templates || [];
      } catch { return null; }
    }
    const langPref = (state.me?.locale || navigator.language || 'fr').slice(0, 2).toLowerCase();
    const matches = (state.templatesCache || []).filter(t => t
      && t.meta_name === WEBSITE_TEST_META_NAME
      && String(t.meta_status || '').toUpperCase() === 'APPROVED');
    if (!matches.length) return null;
    return matches.find(t => String(t.meta_language || '').toLowerCase().startsWith(langPref)) || matches[0];
  }

  // Render the template body for the composer. The rendered text must match
  // byte-for-byte what sendText() compares against, otherwise the send silently
  // demotes itself to free-form and Meta refuses it all over again.
  function _websiteTestRenderTemplate(tpl, url) {
    return applyTemplateVariables(String(tpl.content || '')).replace(/\{\{test_link\}\}/g, url);
  }

  let _websiteTestPreview = null;

  function _websiteTestInvalidatePreview(message = '', { restoreFocus = true } = {}) {
    const preview = _websiteTestPreview;
    if (!preview) return false;
    _websiteTestPreview = null;
    preview.closed = true;
    preview.overlay?.remove();
    if (restoreFocus) {
      const target = preview.returnFocus;
      setTimeout(() => {
        if (target?.isConnected && !target.disabled) target.focus();
      }, 0);
    }
    if (message) toast(message, 'error');
    return true;
  }

  function _websiteTestLeadIdentity(lead) {
    if (!lead?.id) return '';
    const phone = String(lead.phone || '').replace(/\D/g, '').slice(-9);
    return [String(lead.id), String(lead.hubspot_contact_id || ''), phone].join('|');
  }

  async function _websiteTestCurrentLead(chatId) {
    if (!chatId || state.selectedChatId !== chatId) return null;
    const activeLeadId = _activeLeadIdByChat[chatId] || null;
    const lead = await fetchLeadForChat(chatId, { useCache: !activeLeadId, forceLeadId: activeLeadId });
    const stillActiveLeadId = _activeLeadIdByChat[chatId] || null;
    if (state.selectedChatId !== chatId || stillActiveLeadId !== activeLeadId) return null;
    return lead;
  }

  async function _websiteTestValidateRecipient(recipient, preview) {
    if (!recipient?.chatId || !recipient?.leadIdentity || preview?.closed
      || _websiteTestPreview !== preview || !preview.overlay?.isConnected) return false;
    const currentLead = await _websiteTestCurrentLead(recipient.chatId);
    return !preview.closed && _websiteTestPreview === preview && preview.overlay?.isConnected
      && _websiteTestLeadIdentity(currentLead) === recipient.leadIdentity;
  }

  function _websiteTestShowPreview(link, lead, recipient) {
    _websiteTestInvalidatePreview('', { restoreFocus: false });
    const activeElement = document.activeElement;
    const returnFocus = activeElement && activeElement !== document.body
      ? activeElement
      : E.websiteTestBtn;
    const overlay = el('div', {
      class: 'lt-modal-overlay website-test-overlay',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'websiteTestModalTitle',
    });
    const preview = { overlay, recipient, returnFocus, closed: false };
    const closePreview = (options) => {
      if (_websiteTestPreview === preview) _websiteTestInvalidatePreview('', options);
    };
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) closePreview();
    });
    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePreview();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...overlay.querySelectorAll('button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')]
        .filter(node => !node.hidden && node.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    const box = el('div', { class: 'lt-modal website-test-modal' });
    const closeBtn = el('button', {
      class: 'lt-dh-close', type: 'button', title: 'Fermer', 'aria-label': 'Fermer',
      onclick: () => closePreview(),
    }, [lucideIcon('x')]);
    box.appendChild(el('div', { class: 'lt-modal-head' }, [
      el('div', null, [
        el('div', { id: 'websiteTestModalTitle', class: 'lt-modal-title', text: 'Send website test' }),
        el('div', { class: 'website-test-subtitle', text: 'Lien personnel prêt — aucun message n’a été envoyé.' }),
      ]),
      closeBtn,
    ]));

    const body = el('div', { class: 'lt-modal-body' });
    const displayName = String(link.previewName || lead?.name || lead?.firstname || 'Client').trim();
    const initial = displayName.slice(0, 1).toUpperCase() || '?';
    body.appendChild(el('div', { class: 'website-test-person' }, [
      el('span', { class: 'website-test-avatar', text: initial }),
      el('div', { class: 'website-test-person-copy' }, [
        el('strong', { text: displayName }),
        el('span', { text: 'Identité Leads7 liée · une seule utilisation' }),
      ]),
      el('span', { class: 'website-test-ready' }, [lucideIcon('shield-check'), el('span', { text: 'Prêt' })]),
    ]));

    const urlInput = el('input', {
      class: 'lt-input website-test-url', type: 'text', readonly: 'readonly',
      value: link.url, 'aria-label': 'Lien personnel du test',
    });
    const copyBtn = el('button', { class: 'lt-btn lt-btn-ghost', type: 'button' }, [
      lucideIcon('copy'), el('span', { text: 'Copier' }),
    ]);
    copyBtn.addEventListener('click', async () => {
      copyBtn.disabled = true;
      copyBtn.setAttribute('aria-busy', 'true');
      try {
        if (!await _websiteTestValidateRecipient(recipient, preview)) {
          _websiteTestInvalidatePreview('Contact changé — recréez le lien depuis la conversation active');
          return;
        }
        await _websiteTestCopy(link.url);
        toast('Lien copié', 'success');
        copyBtn.querySelector('span').textContent = 'Copié';
      } catch (_) {
        toast('Impossible de copier le lien', 'error');
      } finally {
        copyBtn.disabled = false;
        copyBtn.removeAttribute('aria-busy');
      }
    });
    body.appendChild(el('label', { class: 'lt-flabel', text: 'Lien personnalisé' }));
    body.appendChild(el('div', { class: 'website-test-link-row' }, [urlInput, copyBtn]));
    body.appendChild(el('div', { class: 'website-test-note' }, [
      lucideIcon('sparkles'),
      el('span', { text: 'Le client verra son prénom et les informations déjà connues. Il complètera seulement les éléments manquants avant de commencer.' }),
    ]));

    const previewBtn = el('button', { class: 'lt-btn lt-btn-ghost', type: 'button' }, [
      lucideIcon('external-link'), el('span', { text: 'Aperçu' }),
    ]);
    previewBtn.addEventListener('click', async () => {
      if (previewBtn.disabled) return;
      previewBtn.disabled = true;
      previewBtn.setAttribute('aria-busy', 'true');
      try {
        if (!await _websiteTestValidateRecipient(recipient, preview)) {
          _websiteTestInvalidatePreview('Contact changé — recréez le lien depuis la conversation active');
          return;
        }
        window.open(link.url, '_blank', 'noopener,noreferrer');
      } catch (_) {
        _websiteTestInvalidatePreview('Identité du contact non vérifiable — recréez le lien');
      } finally {
        previewBtn.disabled = false;
        previewBtn.removeAttribute('aria-busy');
      }
    });
    const composerBtn = el('button', { class: 'lt-btn lt-btn-primary website-test-compose', type: 'button' }, [
      lucideIcon('message-circle'), el('span', { text: 'Préparer le message' }),
    ]);
    composerBtn.addEventListener('click', async () => {
      if (!E.composeInput || composerBtn.disabled) return;
      composerBtn.disabled = true;
      composerBtn.setAttribute('aria-busy', 'true');
      try {
        // A preview can stay open while the agent changes chat or switches
        // parent/child on one number. Re-resolve the current canonical lead
        // immediately before touching the composer so an old URL can never be
        // inserted into a new recipient's draft.
        if (!await _websiteTestValidateRecipient(recipient, preview)) {
          _websiteTestInvalidatePreview('Contact changé — recréez le lien depuis la conversation active');
          return;
        }
        // Free-form WhatsApp is refused once the 24h window shuts (Meta 131047)
        // and the send only fails after the fact, so the link would silently
        // never arrive. SMS has no window: route there instead of handing the
        // agent a draft that cannot be delivered.
        const windowClosed = _is24hWindowClosed(state.selectedChat) && _chatIsWaOrSms(state.selectedChat);
        const tpl = windowClosed ? await _websiteTestFindTemplate() : null;
        let routed = 'wa';
        if (tpl) {
          // An approved template is the only free-form-equivalent Meta accepts
          // outside the window — prefer it, and hand the send path the URL so
          // {{test_link}} resolves without prompting the agent for it.
          state.pendingTestLink = { url: link.url, chatId: state.selectedChatId };
          // The 24h banner auto-selects SMS on a stale chat, and sendText()
          // refuses to route a template over SMS — so claim the channel as an
          // explicit choice, or the banner would quietly demote this send to
          // free-form WhatsApp and Meta would reject it all over again.
          _setComposeChannel('wa', true);
          E.composeInput.value = _websiteTestRenderTemplate(tpl, link.url);
          state.composeTemplate = tpl;
          state.composeTemplateRendered = E.composeInput.value;
          updateTemplateBadge();
          routed = 'template';
        } else if (windowClosed) {
          state.pendingTestLink = null;
          _setComposeChannel('sms', true);
          E.composeInput.value = _websiteTestSmsMessage(displayName, link.url);
          routed = 'sms';
        } else {
          state.pendingTestLink = null;
          E.composeInput.value = _websiteTestMessage(displayName, link.url);
        }
        E.composeInput.dispatchEvent(new Event('input', { bubbles: true }));
        closePreview({ restoreFocus: false });
        E.composeInput.focus();
        toast(routed === 'template' ? 'Hors fenêtre 24h — préparé en template WhatsApp, vérifiez-le avant envoi'
          : routed === 'sms' ? 'Hors fenêtre 24h — préparé en SMS, vérifiez-le avant envoi'
          : 'Message préparé — vérifiez-le avant envoi', 'success');
      } catch (error) {
        _websiteTestInvalidatePreview('Identité du contact non vérifiable — recréez le lien');
      } finally {
        composerBtn.disabled = false;
        composerBtn.removeAttribute('aria-busy');
      }
    });
    body.appendChild(el('div', { class: 'website-test-actions' }, [previewBtn, composerBtn]));
    box.appendChild(body);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    _websiteTestPreview = preview;
    refreshIcons(overlay);
    setTimeout(() => composerBtn.focus(), 20);
  }

  // Leads7 refuses a link it cannot name; say what to fix, in the agents' words.
  const WEBSITE_TEST_LINK_ERRORS = {
    name_incomplete: 'Nom incomplet : ajoutez le prénom et le nom du contact, puis réessayez',
    phone_missing: 'Numéro manquant sur le contact : ajoutez-le, puis réessayez',
  };

  async function openWebsiteTestLink() {
    const chatId = state.selectedChatId;
    if (!chatId) { toast('Ouvrez d’abord une conversation', 'error'); return; }
    _websiteTestSetBusy(true);
    try {
      // Respect the shared-number contact switcher: the visible/active child or
      // parent is the identity that must be pinned into the one-use link.
      const activeLeadId = _activeLeadIdByChat[chatId] || null;
      const lead = await fetchLeadForChat(chatId, { useCache: !activeLeadId, forceLeadId: activeLeadId });
      if (!lead) throw new Error('Aucun contact Leads7 lié à cette conversation');
      const response = await api('/api/level-tests/links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId: lead.id, source: 'contact_toolbar' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.url) throw new Error(WEBSITE_TEST_LINK_ERRORS[data.code] || data.error || ('HTTP ' + response.status));
      // Re-resolve the canonical active contact after the async link request.
      // This covers chat changes and both directions of a shared-number switch,
      // including child → default where the explicit selector may be cleared.
      const currentLead = await _websiteTestCurrentLead(chatId);
      const leadIdentity = _websiteTestLeadIdentity(lead);
      if (!leadIdentity || _websiteTestLeadIdentity(currentLead) !== leadIdentity) {
        toast('Conversation changée — lien créé mais aperçu annulé', 'info');
        return;
      }
      _websiteTestShowPreview(data, lead, { chatId, leadIdentity });
    } catch (error) {
      toast(error.message || 'Impossible de créer le lien du test', 'error');
    } finally {
      _websiteTestSetBusy(false);
    }
  }

  const PT_DAYS_AHEAD = 7;
  state.placementTest = { lead: null, date: null, weekStart: null, slots: [], times: [], teachers: [], selectedSlot: null };

  async function openPlacementTestModal() {
    const chatId = state.selectedChatId;
    if (!chatId) { toast('Open a conversation first', 'error'); return; }
    // Find the lead for this chat. Reuse the pipeline-bar fetch which already
    // handles phone normalization + lead-creation fallback.
    // Shared number (a parent + kids): book the person picked in the switcher /
    // "People on this number", never the default contact. Ignoring the pick sent
    // every booking to the first contact — who already held a seat — so the 3rd
    // child could not be booked from the chat at all (2026-09-23, Mme Mariem).
    const activeLeadId = _activeLeadIdByChat[chatId] || null;
    const lead = await fetchLeadForChat(chatId, { useCache: !activeLeadId, forceLeadId: activeLeadId });
    if (!lead) {
      toast('No lead row for this chat — create one from the pipeline bar first', 'error');
      return;
    }
    // Check for an existing active slot first — if found, surface the small
    // "déjà programmé" modal so the agent can keep OR cancel+rebook instead
    // of silently double-booking. If the check ITSELF fails (network/5xx),
    // block + force the agent to retry — falling through would silently
    // allow a double-book on every check failure.
    try {
      const r = await api('/api/test-schedule/active-for-lead?lead_id=' + encodeURIComponent(lead.id));
      if (!r.ok) {
        toast('Impossible de vérifier les tests existants (HTTP ' + r.status + ') — réessayez', 'error');
        return;
      }
      const j = await r.json().catch(() => null);
      if (!j) {
        toast('Réponse invalide du serveur — réessayez', 'error');
        return;
      }
      if (j.slot) {
        _ptShowAlreadyBookedModal(lead, j.slot);
        return;
      }
      // j.slot === null → no active booking, safe to open the full grid.
    } catch (e) {
      console.error('[placement-test] active-for-lead check failed:', e);
      toast('Erreur réseau pendant la vérification — réessayez', 'error');
      return;
    }
    _ptOpenFullBookingModal(lead);
  }

  // Open the full slot-grid booking flow. Extracted from openPlacementTestModal
  // so the "Cancel + rebook" path can reuse it after killing the prior slot.
  async function _ptOpenFullBookingModal(lead) {
    const today = MA.today();
    let weekStart = _ptMondayOf(today);
    let initialDay = _ptFirstBookableDayOfWeek(weekStart);
    if (!initialDay) {
      weekStart = new Date(weekStart); weekStart.setDate(weekStart.getDate() + 7);
      initialDay = _ptFirstBookableDayOfWeek(weekStart);
    }
    state.placementTest = {
      lead,
      date: _ptIsoDate(initialDay || weekStart),
      weekStart,
      slots: [], times: [], teachers: [],
      selectedSlot: null,
      pending: null,
    };
    _ptRenderLeadCard(lead);
    _ptRenderDateTabs();
    _ptPopulateAgentSelect();
    _ptRenderCustomPanel();
    document.getElementById('ptError').classList.add('hidden');
    // Reset the optional sales note on every open (2026-06-13).
    const _ptNote = document.getElementById('ptSalesNote');
    if (_ptNote) _ptNote.value = '';
    _ptShowPhase('slots'); // always start on slot picking
    _ptResetConfirmBtn();
    // Also clear any stuck disabled state on the note-step (phase-2) buttons.
    ['ptSkipBtn', 'ptConfirmBtn'].forEach((id) => { const b = document.getElementById(id); if (b) b.disabled = false; });
    openModal('placementTestModal');
    await _ptLoadSlots();
  }

  // Render the "Already booked" mini-modal with slot summary + cancel/rebook button.
  function _ptShowAlreadyBookedModal(lead, slot) {
    const summary = document.getElementById('ptAbSummary');
    if (summary) {
      clear(summary);
      const date = String(slot.slot_date).slice(0, 10);
      const time = String(slot.slot_time).slice(0, 5);
      summary.appendChild(el('div', { style: 'font-weight:600; margin-bottom:6px;' }, [
        document.createTextNode((slot.teacher_name || 'Teacher') + ' · ' + date + ' à ' + time),
      ]));
      summary.appendChild(el('div', { style: 'color:var(--text-secondary); font-size:12px;', text: 'Statut: ' + (slot.status || '') }));
    }
    // Stash slot id on the button so the click handler can read it.
    const btn = document.getElementById('ptAbCancelRebookBtn');
    if (btn) {
      btn.dataset.slotId = slot.id;
      btn.dataset.leadId = lead.id;
      btn.onclick = () => _ptCancelAndRebook(lead, slot);
    }
    openModal('ptAlreadyBookedModal');
  }

  async function _ptCancelAndRebook(lead, slot) {
    const btn = document.getElementById('ptAbCancelRebookBtn');
    if (btn) btn.disabled = true;
    let cancelled = false;
    try {
      const r = await api('/api/test-schedule/slots/' + encodeURIComponent(slot.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled', reason: 'Replacement test requested by agent' }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast('Annulation échouée: ' + (j.error || ('HTTP ' + r.status)), 'error');
        return;
      }
      cancelled = true;
      closeModal('ptAlreadyBookedModal');
      toast('Test annulé — programmer le nouveau', 'success');
      // Open the full booking grid for the replacement. Wrap so an error
      // here doesn't leave the lead orphaned silently.
      try {
        _ptOpenFullBookingModal(lead);
      } catch (openErr) {
        console.error('[placement-test] rebook modal failed:', openErr);
        toast('Slot annulé mais l\'ouverture du calendrier a échoué — rouvrir manuellement Test Schedule', 'error');
      }
    } catch (err) {
      console.error('[placement-test] cancel+rebook failed:', err);
      toast('Erreur réseau: ' + (err.message || err), 'error');
    } finally {
      // Always re-enable the button so the agent can retry on any path
      // (success closes the modal so this is harmless on the happy path).
      if (btn) btn.disabled = false;
    }
  }

  function _ptIsoDate(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  // Monday (00:00 local) of the week containing `d`. JS getDay(): Sun=0…Sat=6.
  function _ptMondayOf(d) {
    const m = new Date(d); m.setHours(0, 0, 0, 0);
    const dow = m.getDay();
    const diff = (dow === 0) ? -6 : (1 - dow); // Sun → back to prev Monday
    m.setDate(m.getDate() + diff);
    return m;
  }

  // First non-past day of the week starting at `weekStart`
  // (a Monday). Returns a Date, or null if the whole week is in the past.
  function _ptFirstBookableDayOfWeek(weekStart) {
    const today = MA.today();
    for (let i = 0; i < PT_DAYS_AHEAD; i++) {
      const d = new Date(weekStart); d.setDate(weekStart.getDate() + i);
      if (d.getTime() >= today.getTime()) return d;
    }
    return null;
  }

  function _ptRenderLeadCard(lead) {
    const host = document.getElementById('ptLeadCard'); if (!host) return;
    clear(host);
    const name = lead.name || lead.firstname || 'Lead';
    const initials = (name || '?').trim().slice(0, 1).toUpperCase();
    const phone = lead.phone || '';
    const stage = lead.pipeline_stage || 'new';
    host.appendChild(el('div', { class: 'pt-lead-avatar', text: initials }));
    const info = el('div');
    info.appendChild(el('div', { class: 'pt-lead-name', text: name }));
    info.appendChild(el('div', { class: 'pt-lead-phone' }, [
      lucideIcon('phone'),
      document.createTextNode(' ' + phone),
    ]));
    host.appendChild(info);
    host.appendChild(el('div', { class: 'pt-lead-stage', text: stage }));
    refreshIcons(host);
  }

  // Shift the viewed week by ±7 days, snap the selection to that week's first
  // bookable day, then re-render tabs + reload slots. Guarded so we never land
  // on a past week (prev chevron is also disabled in that case).
  function _ptShiftWeek(deltaDays) {
    const pt = state.placementTest;
    if (!pt.weekStart) return;
    const next = new Date(pt.weekStart); next.setDate(pt.weekStart.getDate() + deltaDays);
    const thisMonday = _ptMondayOf(MA.today());
    if (next.getTime() < thisMonday.getTime()) return; // never go before this week
    pt.weekStart = next;
    const firstDay = _ptFirstBookableDayOfWeek(next) || next;
    pt.date = _ptIsoDate(firstDay);
    pt.selectedSlot = null;
    _ptRenderDateTabs();
    _ptResetConfirmBtn();
    _ptLoadSlots();
  }

  function _ptRenderDateTabs() {
    const host = document.getElementById('ptDateTabs'); if (!host) return;
    clear(host);
    const pt = state.placementTest;
    if (!pt.weekStart) pt.weekStart = _ptMondayOf(MA.today());
    const labels = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'];
    const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc'];
    const today = MA.today();
    const thisMonday = _ptMondayOf(today);

    // ‹ Prev chevron — disabled when we're already on this week (no past weeks).
    const prevDisabled = pt.weekStart.getTime() <= thisMonday.getTime();
    const prev = el('button', {
      type: 'button',
      class: 'pt-date-tab pt-week-nav' + (prevDisabled ? ' is-disabled' : ''),
      'aria-label': 'Semaine précédente',
      title: 'Semaine précédente',
      disabled: prevDisabled ? '' : null,
    }, [lucideIcon('chevron-left')]);
    if (!prevDisabled) prev.addEventListener('click', () => _ptShiftWeek(-7));
    host.appendChild(prev);

    // Tabs for the viewed week, Sunday included; past days are disabled.
    for (let i = 0; i < PT_DAYS_AHEAD; i++) {
      const d = new Date(pt.weekStart); d.setDate(pt.weekStart.getDate() + i);
      const iso = _ptIsoDate(d);
      const isPast = d.getTime() < today.getTime();
      const isToday = d.getTime() === today.getTime();
      const isActive = iso === pt.date;
      const label = isToday ? "Aujourd'hui" : labels[d.getDay()];
      const tab = el('button', {
        type: 'button',
        class: 'pt-date-tab' + (isActive ? ' active' : '') + (isPast ? ' is-disabled' : ''),
        'data-date': iso,
        disabled: isPast ? '' : null,
      });
      tab.appendChild(el('div', { class: 'pt-date-day', text: label }));
      tab.appendChild(el('div', { class: 'pt-date-num', text: d.getDate() + ' ' + months[d.getMonth()] }));
      if (!isPast) {
        tab.addEventListener('click', () => {
          pt.date = iso;
          pt.selectedSlot = null;
          _ptRenderDateTabs();
          _ptResetConfirmBtn();
          _ptLoadSlots();
        });
      }
      host.appendChild(tab);
    }

    // › Next chevron — always enabled (future weeks have no upper bound).
    const next = el('button', {
      type: 'button',
      class: 'pt-date-tab pt-week-nav',
      'aria-label': 'Semaine suivante',
      title: 'Semaine suivante',
    }, [lucideIcon('chevron-right')]);
    next.addEventListener('click', () => _ptShiftWeek(7));
    host.appendChild(next);

    refreshIcons(host);
  }

  function _ptPopulateAgentSelect() {
    const sel = document.getElementById('ptAgentSelect'); if (!sel) return;
    clear(sel);
    sel.appendChild(el('option', { value: '', text: 'Current agent (' + userLabel(state.me?.username || 'me') + ')' }));
    // Pull from state.users (loaded in the team management view) — fall back to a fetch if empty.
    const users = (state.users || []).filter(u => u.active !== false);
    if (!users.length) {
      // Best-effort one-time fetch (endpoint exposes active users only).
      api('/api/team/users').then(r => r.ok ? r.json() : null).then(j => {
        if (!j?.users) return;
        state.users = j.users;
        _ptPopulateAgentSelect();
      }).catch(() => {});
      return;
    }
    for (const u of users) {
      sel.appendChild(el('option', { value: u.username, text: userLabel(u.username, u.displayName || u.username) }));
    }
  }

  async function _ptLoadSlots() {
    const host = document.getElementById('ptSlotsHost'); if (!host) return;
    clear(host);
    host.appendChild(el('div', { class: 'ts-book-empty', text: 'Loading slots…' }));
    try {
      const r = await api('/api/test-schedule/slots?date=' + encodeURIComponent(state.placementTest.date));
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      state.placementTest.times = j.times || [];
      const slotsByTeacher = new Map();
      const teacherMeta = new Map();
      for (const s of (j.slots || [])) {
        if (!teacherMeta.has(s.teacher_id)) {
          teacherMeta.set(s.teacher_id, {
            id: s.teacher_id,
            name: s.teacher_display_name || s.teacher_name || '—',
            color: s.teacher_color || '#6b7280',
          });
        }
        if (!slotsByTeacher.has(s.teacher_id)) slotsByTeacher.set(s.teacher_id, []);
        slotsByTeacher.get(s.teacher_id).push(s);
      }
      // Pull teacher specialty ranking so we can decorate the section header
      // with a "Best match" badge. Best-effort — if it fails, fall back to
      // alpha sort by name.
      let rankingById = null;
      try {
        const rRank = await api('/api/test-schedule/teachers?ranked=1');
        if (rRank.ok) {
          const rj = await rRank.json();
          rankingById = new Map((rj.teachers || []).map(t => [t.id, t]));
        }
      } catch { /* fall back to alpha sort */ }
      const built = Array.from(teacherMeta.values());
      if (rankingById) {
        for (const t of built) {
          const r = rankingById.get(t.id);
          if (r) {
            t.score = r.score;
            t.match_reasons = r.match_reasons || [];
            t.tests_this_week = r.tests_this_week || 0;
          }
        }
        built.sort((a, b) => (b.score || 0) - (a.score || 0));
      } else {
        built.sort((a, b) => a.name.localeCompare(b.name));
      }
      state.placementTest.teachers = built;
      state.placementTest.slotsByTeacher = slotsByTeacher;
      _ptRenderSlots();
      _ptSyncCustomTeachers();
    } catch (err) {
      clear(host);
      host.appendChild(el('div', { class: 'ts-book-empty', text: 'Failed to load slots: ' + (err.message || err) }));
    }
  }

  function _ptRenderSlots() {
    const host = document.getElementById('ptSlotsHost'); if (!host) return;
    clear(host);
    if (!state.placementTest.teachers.length) {
      host.appendChild(el('div', { class: 'ts-book-empty', text: 'No teachers active for this date.' }));
      return;
    }
    // Overbooking framing so agents know a time can hold several students (audit OPS-5).
    host.appendChild(el('div', { class: 'pt-overbook-note', text: 'Surbooking activé — un créneau peut recevoir plusieurs élèves (≈50% d’absents).' }));
    for (let idx = 0; idx < state.placementTest.teachers.length; idx++) {
      const t = state.placementTest.teachers[idx];
      const block = el('div', { class: 'pt-teacher-block' });
      const slots = state.placementTest.slotsByTeacher.get(t.id) || [];
      // Overbooking: a time has up to 3 seats — count free TIMES, not free seats.
      const freeCount = new Set(slots.filter(s => s.status === 'available').map(s => s.slot_time)).size;
      const headerKids = [
        el('span', { class: 'pt-teacher-color', style: 'background:' + t.color + ';' }),
        document.createTextNode(t.name),
        el('span', { class: 'pt-teacher-free', text: '(' + freeCount + ' free)' }),
      ];
      // Top-ranked teacher gets a "Best match" badge if there's any score signal.
      if (idx === 0 && t.score !== undefined && t.score > 0) {
        const reason = (t.match_reasons || []).join(' · ') || ('load ' + (t.tests_this_week || 0) + ' this week');
        headerKids.push(el('span', {
          text: '✨ Best match — ' + reason,
          style: 'margin-left:8px; padding:2px 8px; border-radius:999px; background:#d1fae5; color:#047857; font-size:11px; font-weight:600;',
        }));
      } else if (t.match_reasons && t.match_reasons.length) {
        headerKids.push(el('span', {
          text: t.match_reasons.join(' · '),
          style: 'margin-left:8px; color:var(--text-secondary); font-size:11px;',
        }));
      }
      const header = el('div', { class: 'pt-teacher-header' }, headerKids);
      block.appendChild(header);
      const grid = el('div', { class: 'pt-slot-grid' });
      // Sort slots by time
      slots.sort((a, b) => a.slot_time.localeCompare(b.slot_time));
      // Skip past empty slots entirely — agent doesn't need to scroll past
      // a wall of greyed-out "10:00, 10:15, …" cells when it's already 19:30.
      const visibleSlots = slots.filter(s => !(s.status === 'available' && tsSlotIsPast(state.placementTest.date, s.slot_time)));
      if (!visibleSlots.length) {
        block.appendChild(el('div', { class: 'pt-slots-empty', style: 'padding:8px 4px; color:var(--text-tertiary); font-size:12px;', text: 'No upcoming free slots today.' }));
        host.appendChild(block);
        continue;
      }
      // Dedupe seats → one button per TIME (a time has ≥1 free seat = bookable).
      const byTime = new Map();
      for (const s of visibleSlots) {
        const e = byTime.get(s.slot_time) || { time: s.slot_time, free: 0, booked: 0 };
        if (s.status === 'available') e.free++; else e.booked++;
        byTime.set(s.slot_time, e);
      }
      const timeEntries = Array.from(byTime.values()).sort((a, b) => a.time.localeCompare(b.time));
      for (const e of timeEntries) {
        const isAvailable = e.free > 0;
        const sel = state.placementTest.selectedSlot;
        const isSelected = sel && sel.time === e.time && sel.teacherId === t.id;
        const btn = el('button', {
          type: 'button',
          class: 'pt-slot' + (isSelected ? ' selected' : ''),
          title: isAvailable ? (e.free + ' free — click to select') : 'Fully booked',
          disabled: !isAvailable ? '' : null,
        }, [
          lucideIcon('clock'),
          document.createTextNode(e.time.slice(0, 5)),
        ]);
        if (isAvailable) {
          btn.addEventListener('click', () => {
            state.placementTest.selectedSlot = { time: e.time, teacherId: t.id, teacherName: t.name, teacherColor: t.color };
            _ptRenderSlots();
            const continueBtn = document.getElementById('ptContinueBtn');
            if (continueBtn) {
              continueBtn.disabled = false;
              const span = continueBtn.querySelector('span');
              if (span) span.textContent = 'Continuer';
            }
          });
        }
        grid.appendChild(btn);
      }
      block.appendChild(grid);
      host.appendChild(block);
    }
    refreshIcons(host);
  }

  function _ptResetConfirmBtn() {
    const btn = document.getElementById('ptContinueBtn'); if (!btn) return;
    btn.disabled = true;
    const span = btn.querySelector('span'); if (span) span.textContent = 'Sélectionnez un créneau';
  }

  // ── Two-step booking: phase 1 (pick slot) → phase 2 (note for teacher) ──────
  // The note lives on its own step so the agent is actually prompted for it
  // instead of scrolling past a buried textarea. Both the grid path AND the
  // custom-time path converge on this step, so the note is captured identically.

  // Toggle the modal between the 'slots' phase and the 'note' phase. Only the
  // active phase's body + footer group are visible.
  function _ptShowPhase(phase) {
    const isNote = phase === 'note';
    document.getElementById('ptStepSlots')?.classList.toggle('hidden', isNote);
    document.getElementById('ptStepNote')?.classList.toggle('hidden', !isNote);
    document.getElementById('ptFooterSlots')?.classList.toggle('hidden', isNote);
    document.getElementById('ptFooterNote')?.classList.toggle('hidden', !isNote);
  }

  // Back to slot picking — keep the selection, custom inputs, and typed note.
  function _ptGoToSlotsStep() {
    document.getElementById('ptError')?.classList.add('hidden');
    _ptShowPhase('slots');
  }

  // Human-readable "20 juin · 15:30 · Issam" for the note-step header.
  function _ptFmtNoteSummary(p) {
    let datePart = String(p.date || '');
    try {
      const [y, m, d] = String(p.date).split('-').map(Number);
      const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc'];
      if (y && m && d) datePart = d + ' ' + (months[m - 1] || '');
    } catch (_) {}
    return [datePart, String(p.time || '').slice(0, 5), p.teacherName || ''].filter(Boolean).join(' · ');
  }

  // Advance from the chosen slot (grid or custom) to the optional-note step.
  function _ptGoToNoteStep(pending) {
    if (!pending || !pending.teacherId || !pending.date || !pending.time) {
      toast('Sélectionnez un créneau d\'abord', 'error');
      return;
    }
    state.placementTest.pending = pending;
    document.getElementById('ptError')?.classList.add('hidden');
    // Re-enable the note-step buttons on every entry: _ptCommitBooking disables
    // them and only re-enables them in its catch block, so after a SUCCESSFUL
    // booking they stayed disabled across modal close/reopen — blocking the next
    // booking and the cancel→rebook flow ("Confirmer"/"Passer" stuck greyed).
    const _ptSkip = document.getElementById('ptSkipBtn'); if (_ptSkip) _ptSkip.disabled = false;
    const _ptConfirm = document.getElementById('ptConfirmBtn'); if (_ptConfirm) _ptConfirm.disabled = false;
    const sumEl = document.querySelector('#ptNoteSummary .pt-note-summary-text');
    if (sumEl) sumEl.textContent = _ptFmtNoteSummary(pending);
    _ptShowPhase('note');
    const ta = document.getElementById('ptSalesNote');
    if (ta) { try { ta.focus(); } catch (_) {} }
  }

  // Commit the pending booking. withNote=false ("Passer") books with no sales
  // note; withNote=true ("Confirmer") attaches whatever is in the textarea.
  // Merges the old placementTestConfirm + _ptCustomConfirm POST bodies.
  async function _ptCommitBooking({ withNote } = {}) {
    const pt = state.placementTest;
    const pending = pt?.pending;
    if (!pt?.lead?.id || !pending?.time) return;
    const errEl = document.getElementById('ptError');
    const skipBtn = document.getElementById('ptSkipBtn');
    const confirmBtn = document.getElementById('ptConfirmBtn');
    errEl.classList.add('hidden');
    if (skipBtn) skipBtn.disabled = true;
    if (confirmBtn) confirmBtn.disabled = true;
    try {
      const agent = document.getElementById('ptAgentSelect')?.value || '';
      const body = {
        teacher_id: pending.teacherId,
        slot_date: pending.date,
        slot_time: pending.time,
        lead_id: pt.lead.id,
      };
      if (pending.kind === 'custom') { body.create_custom = true; body.acknowledge_out_of_hours = true; }
      if (agent) body.agent_username = agent; // server can ignore if not supported
      if (withNote) {
        const _sn = (document.getElementById('ptSalesNote')?.value || '').trim();
        if (_sn) body.sales_note = _sn.slice(0, 500);
      }
      const r = await api('/api/test-schedule/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.status === 409) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || 'Slot already booked');
      }
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || ('HTTP ' + r.status));
      }
      closeModal('placementTestModal');
      const timeLabel = String(pending.time).slice(0, 5);
      const who = pending.teacherName || 'professeur';
      toast(
        pending.kind === 'custom'
          ? ('Créneau personnalisé réservé à ' + timeLabel + ' avec ' + who + ' — lead déplacé vers Test programmé')
          : ('Booked at ' + timeLabel + ' with ' + who + ' — lead moved to Test scheduled'),
        'success'
      );
      // Invalidate pipeline cache + re-render the bar so the agent sees the new stage.
      if (state.selectedChatId) {
        delete pipelineLeadCache[state.selectedChatId];
        await renderPipelineBar(state.selectedChatId);
      }
    } catch (err) {
      errEl.textContent = String(err.message || err);
      errEl.classList.remove('hidden');
      if (skipBtn) skipBtn.disabled = false;
      if (confirmBtn) confirmBtn.disabled = false;
    }
  }

  // ── Custom-time slot panel ────────────────────────────────────────────────
  // Inline panel (NOT a nested modal) below #ptSlotsHost. Lets an agent book any
  // future date + any time, even outside normal teacher hours. The server is
  // authoritative on what it will accept.

  // Build/refresh the toggle + collapsible panel. Rendered once per modal open.
  function _ptRenderCustomPanel() {
    const slotsHost = document.getElementById('ptSlotsHost'); if (!slotsHost) return;
    // Re-create the host fresh each open so stale listeners don't accumulate.
    let host = document.getElementById('ptCustomHost');
    if (host) host.remove();
    host = el('div', { id: 'ptCustomHost', class: 'pt-custom-host' });
    slotsHost.insertAdjacentElement('afterend', host);

    const toggle = el('button', {
      type: 'button', class: 'pt-custom-toggle', id: 'ptCustomToggle',
      'aria-expanded': 'false',
    }, [
      customIcon('calendar-plus-custom.png?v=1', 'Créneau personnalisé'),
      el('span', { text: 'Ajouter un créneau personnalisé' }),
    ]);

    const panel = el('div', { id: 'ptCustomPanel', class: 'pt-custom-panel hidden' });

    const dateRow = el('div', { class: 'pt-custom-row' }, [
      el('label', { class: 'form-label', for: 'ptCustomDate', text: 'Date' }),
      el('input', { type: 'date', class: 'input', id: 'ptCustomDate', min: _ptIsoDate(new Date()) }),
    ]);
    const timeRow = el('div', { class: 'pt-custom-row' }, [
      el('label', { class: 'form-label', for: 'ptCustomTime', text: 'Heure' }),
      el('input', { type: 'time', class: 'input', id: 'ptCustomTime', step: '900' }),
    ]);
    const teacherRow = el('div', { class: 'pt-custom-row' }, [
      el('label', { class: 'form-label', for: 'ptCustomTeacher', text: 'Professeur' }),
      el('select', { class: 'input', id: 'ptCustomTeacher' }),
    ]);

    const warn = el('div', { id: 'ptCustomOohWarn', class: 'pt-ooh-warn hidden' });

    const confirm = el('button', {
      type: 'button', class: 'btn btn-primary pt-custom-confirm', id: 'ptCustomConfirmBtn',
    }, [lucideIcon('check'), el('span', { text: 'Réserver ce créneau' })]);

    panel.appendChild(dateRow);
    panel.appendChild(timeRow);
    panel.appendChild(teacherRow);
    panel.appendChild(warn);
    panel.appendChild(confirm);

    toggle.addEventListener('click', () => {
      const open = panel.classList.toggle('hidden') === false;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.classList.toggle('is-open', open);
      if (open) {
        // Seed the date with the currently-viewed tab for convenience.
        const di = document.getElementById('ptCustomDate');
        if (di && !di.value) di.value = state.placementTest.date || _ptIsoDate(new Date());
        _ptUpdateOohWarn();
      }
    });

    panel.addEventListener('change', _ptUpdateOohWarn);
    panel.addEventListener('input', _ptUpdateOohWarn);
    confirm.addEventListener('click', _ptCustomConfirm);

    host.appendChild(toggle);
    host.appendChild(panel);
    _ptSyncCustomTeachers();
    refreshIcons(host);
  }

  // Populate the custom-panel teacher <select> from the teachers loaded for the
  // current date, preserving the current selection when possible.
  function _ptSyncCustomTeachers() {
    const sel = document.getElementById('ptCustomTeacher'); if (!sel) return;
    const prev = sel.value;
    clear(sel);
    const teachers = state.placementTest.teachers || [];
    if (!teachers.length) {
      sel.appendChild(el('option', { value: '', text: '— aucun professeur —' }));
      return;
    }
    for (const t of teachers) {
      sel.appendChild(el('option', { value: t.id, text: t.name }));
    }
    if (prev && teachers.some(t => String(t.id) === String(prev))) sel.value = prev;
  }

  // Advisory out-of-hours check. Teacher windows aren't exposed client-side, so
  // we warn generically when the time is before 10:00 or at/after 21:00. The
  // server enforces the real rule (warn-not-block for custom slots).
  function _ptUpdateOohWarn() {
    const warn = document.getElementById('ptCustomOohWarn'); if (!warn) return;
    const timeStr = (document.getElementById('ptCustomTime')?.value || '').trim();
    const dateStr = (document.getElementById('ptCustomDate')?.value || '').trim();
    const sel = document.getElementById('ptCustomTeacher');
    const teacherName = sel ? (sel.options[sel.selectedIndex]?.textContent || '') : '';
    if (!/^\d{2}:\d{2}/.test(timeStr)) { warn.classList.add('hidden'); return; }
    const [hh, mm] = timeStr.split(':').map(Number);
    const minutes = hh * 60 + mm;
    const outOfHours = minutes < 600 || minutes >= 1260; // <10:00 or >=21:00
    if (outOfHours) {
      const who = teacherName ? ('de ' + teacherName) : 'du professeur';
      warn.textContent = '⚠ Hors des heures ' + who + '. Le test sera quand même réservé.';
      warn.classList.remove('hidden');
    } else {
      warn.classList.add('hidden');
    }
  }

  // Validate the custom-time inputs, then converge on the shared note step
  // (instead of booking directly) so the custom path also prompts for the note.
  function _ptCustomConfirm() {
    const pt = state.placementTest;
    const errEl = document.getElementById('ptError');
    const teacherSel = document.getElementById('ptCustomTeacher');
    const teacherId = teacherSel?.value || '';
    const slotDate = (document.getElementById('ptCustomDate')?.value || '').trim();
    const slotTime = (document.getElementById('ptCustomTime')?.value || '').trim();
    errEl.classList.add('hidden');
    if (!pt.lead?.id) { errEl.textContent = 'Aucun lead sélectionné.'; errEl.classList.remove('hidden'); return; }
    if (!teacherId) { errEl.textContent = 'Choisissez un professeur.'; errEl.classList.remove('hidden'); return; }
    if (!slotDate) { errEl.textContent = 'Choisissez une date.'; errEl.classList.remove('hidden'); return; }
    if (!/^\d{2}:\d{2}/.test(slotTime)) { errEl.textContent = 'Choisissez une heure.'; errEl.classList.remove('hidden'); return; }
    const teacherName = (teacherSel?.selectedOptions[0]?.textContent) || 'professeur';
    _ptGoToNoteStep({ kind: 'custom', teacherId, date: slotDate, time: slotTime, teacherName });
  }

  // ───────────────────────────────────────────────────────────────────
  // Groups view — HubSpot Companies as a HubSpot-style object workspace
  // (2026-08-15). Same shell + shared machinery as Contacts (saved views,
  // quick filters, advanced-filter drawer, dense grid with frozen name column,
  // resize/reorder, inline editors, preview drawer, pager, bulk bar) driven by
  // the `_GROUPS_WS` descriptor. Data = the companies mirror
  // (/api/hubspot/companies, whole scope loaded once, filtered client-side —
  // ~1400 rows max); edits go through PATCH /api/hubspot/companies/:id.
  //
  // Preserved from the previous Groups page: students pill / "+ Add" →
  // openGroupStudentsModal, status badges, teacher / date / class-type /
  // level filters, the stat chips (global status counts + data-quality
  // diagnostics), events report, name search.
  // ───────────────────────────────────────────────────────────────────
  const GROUPS = {
    inited: false, loading: false,
    all: [],                 // every mirrored company (scope = all statuses)
    filtered: [],            // after quick + advanced filters + sort
    rows: [],                // current page slice (what the grid shows)
    total: 0, pageIndex: 1, pageSize: 25,
    sortKey: 'name', sortDir: 'asc',
    columns: [], propsCatalog: null, propsByName: {},
    views: [], activeView: null, defaultViewId: null, dirty: false, _baselineSig: '',
    advancedGroups: [], teamUsers: [],
    selectedIds: new Set(),
    // quick filters (persisted in the view's quickFilters bag)
    status: '', fTeacher: '', classType: 'all', level: 'all', seats: '',
    fDateField: '', fDatePreset: '', fDateFrom: '', fDateTo: '',
    kpiMode: '',             // data-quality diagnostic narrow (noSchedule / noTeacher / noDates)
    statusCounts: null, totalGroups: null,
    // Live sync with HubSpot (support2 writes to the same portal).
    revision: null,          // server's mirror revision at our last fetch
    syncedAt: 0,             // epoch ms of the last mirror delta
    liveTimer: null, livePolling: false, liveFailures: 0,
  };
  // Legacy alias — the Group-students modal reloads the table via loadGroups()
  // and reads state.groups.rows; keep both pointing at the workspace state.
  state.groups = GROUPS;

  const _GROUPS_WS = {
    key: 'groups',
    objectType: 'company',
    get state() { return GROUPS; },
    viewsApi: '/api/contacts-views',
    viewsQuery: '?object=company',
    viewsLabel: 'groups',
    colWidthsKey: 'groups_col_widths_v1',
    ids: {
      tabsScroll: 'groupsViewsTabsScroll', addViewBtn: 'groupsViewNewBtn', dirtyBtn: 'groupsViewSaveDirtyBtn',
      allViewsPopover: 'groupsAllViewsPopover', advSummary: 'groupsAdvSummary',
      advToggle: 'groupsAdvancedToggle', tableViewport: 'groupsTableViewport',
      rangeNote: 'groupsRangeNote', pageLabel: 'groupsPageLabel', pageNumbers: 'groupsPageNumbers',
      prevBtn: 'groupsPrevPageBtn', nextBtn: 'groupsNextPageBtn', loading: 'groupsLoading',
    },
    refresh: () => refreshGroups(),
    checkDirty: () => _groupsCheckDirty(),
    applyView: (v) => _groupsApplyView(v),
    loadViews: () => _groupsLoadViews(),
    switchView: (id) => _groupsSwitchView(id),
    saveDirty: () => _groupsSaveActiveViewDirty(),
    deleteView: (v) => _groupsDeleteView(v),
    signView: () => _groupsSignView(),
    updateDirtyPill: () => _groupsUpdateDirtyPill(),
    viewPayload: () => ({
      statusFilter: GROUPS.status || '',
      searchQuery: document.getElementById('groupsSearch')?.value || '',
      quickFilters: _groupsQuickFilters(),
    }),
    reorderColumn: (from, to, before) => _groupsReorderColumn(from, to, before),
    rerenderTable: () => _renderGroupsTable(),
    loadCatalog: () => _groupsLoadPropsCatalog(),
    hideCatalogProp: (p) => p.name === 'name',
    goToPage: (n) => _groupsGoToPage(n),
  };

  const _GROUPS_DEFAULT_COLUMNS = ['student_count', 'schedule_time', 'schedule_days', 'level_of_groups', 'lougha_teachers',
    'date_of_start_of_classes', 'date_of_end', 'group_status', 'events_report'];
  const _GROUPS_STATUS_LABELS = { 'In Process': 'In Process', expandable: 'Expandable', active: 'Active', paused: 'Paused', done: 'Done', closed: 'Closed' };

  function ensureGroupsInit() {
    // Re-entering the view: render the cached rows instantly, then check with
    // the server in the background so we don't show a stale roster while the
    // agent decides something.
    if (GROUPS.inited) { refreshGroups(); _groupsStartLiveSync(); _groupsPollLive(); return; }
    GROUPS.inited = true;
    try {
      const storedSize = Number(localStorage.getItem('groups_page_size_v1'));
      if ([25, 50, 100].includes(storedSize)) GROUPS.pageSize = storedSize;
    } catch { /* optional */ }
    _wsRenderSkeleton(document.getElementById('groupsLoading'));
    _initGroupsWiring();
    _groupsStartLiveSync();
    Promise.all([_groupsLoadPropsCatalog(), _groupsLoadViews()]).then(() => refreshGroups());
  }

  function _initGroupsWiring() {
    const on = (id, ev, fn) => document.getElementById(id)?.addEventListener(ev, fn);
    on('groupsRefreshBtn', 'click', () => refreshGroups({ refetch: true }));
    // Live-sync pill: manual "catch me up now" escape hatch, and the place
    // the agent looks when they doubt the numbers.
    on('groupsSyncPill', 'click', () => { GROUPS.liveFailures = 0; refreshGroups({ refetch: true }); });
    // Coming back to the tab is the moment stale numbers hurt most — the
    // agent left, someone edited the group in s2, they return and act.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && _groupsViewIsOpen()) _groupsPollLive();
    });
    // Keep the "Live · Xs" age honest between polls.
    const ageTimer = setInterval(() => { if (_groupsViewIsOpen()) _groupsRenderSyncPill(); }, 5000);
    window.addEventListener('beforeunload', () => { clearInterval(ageTimer); _groupsStopLiveSync(); });
    on('groupsStatusFilter', 'change', (e) => { GROUPS.status = e.target.value; _groupsFiltersChanged(); });
    on('groupsClassType', 'change', (e) => { GROUPS.classType = e.target.value || 'all'; _groupsFiltersChanged(); });
    on('groupsLevelFilter', 'change', (e) => { GROUPS.level = e.target.value || 'all'; _groupsFiltersChanged(); });
    on('groupsSeatsFilter', 'change', (e) => { GROUPS.seats = e.target.value || ''; _groupsFiltersChanged(); });
    let tTimer = null;
    on('grpFTeacher', 'input', (e) => { clearTimeout(tTimer); tTimer = setTimeout(() => { GROUPS.fTeacher = e.target.value.trim(); _groupsFiltersChanged(); }, 250); });
    on('grpFDateField', 'change', (e) => { GROUPS.fDateField = e.target.value; if (!GROUPS.fDateField) GROUPS.fDatePreset = ''; _groupsFiltersChanged(); });
    on('grpFDatePreset', 'change', (e) => { GROUPS.fDatePreset = e.target.value; _groupsFiltersChanged(); });
    on('grpFDateFrom', 'change', (e) => { GROUPS.fDateFrom = e.target.value; _groupsFiltersChanged(); });
    on('grpFDateTo', 'change', (e) => { GROUPS.fDateTo = e.target.value; _groupsFiltersChanged(); });
    on('groupsClearFiltersBtn', 'click', _clearGroupsFilters);
    on('groupsAddQuickFilterBtn', 'click', (ev) => _addGroupsQuickFilter(ev.currentTarget));
    on('groupsAdvancedToggle', 'click', () => _openAdvPanel(_GROUPS_WS));
    let qTimer = null;
    const searchEl = document.getElementById('groupsSearch');
    searchEl?.addEventListener('input', () => { clearTimeout(qTimer); qTimer = setTimeout(() => _groupsFiltersChanged(), 250); });
    searchEl?.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { clearTimeout(qTimer); _groupsFiltersChanged(); } });
    // Stat chips (delegate — rebuilt every render).
    on('groupsKpis', 'click', (e) => {
      const tile = e.target.closest('[data-grp-kpi]');
      if (!tile) return;
      const mode = tile.dataset.grpKpi;
      if (mode === 'scopeAll') { GROUPS.status = ''; GROUPS.kpiMode = ''; }
      else if (mode.startsWith('status:')) { const st = mode.slice(7); GROUPS.status = GROUPS.status === st ? '' : st; }
      else GROUPS.kpiMode = GROUPS.kpiMode === mode ? '' : mode;
      const sel = document.getElementById('groupsStatusFilter'); if (sel) sel.value = GROUPS.status;
      _groupsFiltersChanged();
    });
    on('groupsEditColumnsBtn', 'click', () => openContactsColumnsModal(_GROUPS_WS));
    on('groupsExportBtn', 'click', () => _groupsExportCsv(GROUPS.filtered));
    on('groupsCreateBtn', 'click', _openCreateGroupModal);
    on('createGroupSubmitBtn', 'click', _submitCreateGroup);
    on('groupsDataQualityBtn', 'click', (ev) => _openGroupsDataQuality(ev.currentTarget));
    on('groupsActionsBtn', 'click', (ev) => _openGroupsActionsMenu(ev.currentTarget));
    on('groupsObjectMenuBtn', 'click', (ev) => _openGroupsObjectMenu(ev.currentTarget));
    on('groupsAllViewsBtn', 'click', (ev) => _openAllViewsPopover(ev.currentTarget, _GROUPS_WS));
    on('groupsViewNewBtn', 'click', () => _openViewModal({ mode: 'new' }, _GROUPS_WS));
    on('groupsViewSaveDirtyBtn', 'click', () => _groupsSaveActiveViewDirty());
    on('groupsPrevPageBtn', 'click', () => _groupsGoToPage(GROUPS.pageIndex - 1));
    on('groupsNextPageBtn', 'click', () => _groupsGoToPage(GROUPS.pageIndex + 1));
    const pageSizeEl = document.getElementById('groupsPageSize');
    if (pageSizeEl) {
      pageSizeEl.value = String(GROUPS.pageSize);
      pageSizeEl.addEventListener('change', () => {
        GROUPS.pageSize = Number(pageSizeEl.value) || 25;
        try { localStorage.setItem('groups_page_size_v1', String(GROUPS.pageSize)); } catch {}
        GROUPS.pageIndex = 1;
        _renderGroupsTable();
      });
    }
    // Bulk bar
    on('groupsBulkClear', 'click', () => { GROUPS.selectedIds.clear(); _renderGroupsTable(); });
    on('groupsBulkCancel', 'click', () => { GROUPS.selectedIds.clear(); _renderGroupsTable(); });
    on('groupsBulkSelectAll', 'click', () => { for (const g of GROUPS.filtered) GROUPS.selectedIds.add(String(g.id)); _renderGroupsTable(); });
    on('groupsBulkExportBtn', 'click', () => _groupsExportCsv(GROUPS.all.filter(g => GROUPS.selectedIds.has(String(g.id)))));
    on('groupsBulkEditBtn', 'click', _openGroupsBulkEditModal);
    on('groupsBulkEditApplyBtn', 'click', _groupsBulkEditApply);
    on('groupsBulkEditProp', 'change', _renderGroupsBulkEditValue);
    // The save-view modal + adv panel are shared DOM; Contacts wires their
    // buttons in initContacts(). Wire them here too (idempotent guards inside)
    // in case Groups is opened before Contacts in this session.
    _wireAdvPanelOnce();
    _wireViewModalOnce();
  }


  // ── Property catalog (mirrored HubSpot company properties + virtuals) ──
  async function _groupsLoadPropsCatalog() {
    let list = [];
    try {
      const r = await api('/api/hubspot/properties/companies/full');
      if (r.ok) list = (await r.json()).properties || [];
    } catch (err) { console.warn('[groups] property catalog failed:', err); }
    if (!list.length) {
      list = ['name', 'schedule_time', 'schedule_days', 'schedule_time_2', 'group_class_type', 'group_status', 'level_of_groups',
        'date_of_start_of_classes', 'date_of_end', 'lougha_teachers', 'events_report', 'hs_lastmodifieddate']
        .map(name => ({ name, label: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), type: /date/.test(name) ? 'date' : 'string', fieldType: 'text', groupName: 'companyinformation', options: [], readOnly: name === 'hs_lastmodifieddate' }));
    }
    for (const p of list) {
      p.groupName = p.groupName || 'companyinformation';
      p.options = Array.isArray(p.options) ? p.options : [];
      if (!p.label) p.label = p.name;
    }
    // Virtual (Leads7-derived) columns — filterable client-side.
    list.push({ name: 'student_count', label: 'Students', type: 'number', fieldType: 'number', groupName: 'leads7', options: [], _virtual: true, _filterable: true, readOnly: true });
    list.push({ name: 'seats_left', label: 'Seats left', type: 'number', fieldType: 'number', groupName: 'leads7', options: [], _virtual: true, _filterable: true, readOnly: true });
    GROUPS.propsCatalog = list;
    GROUPS.propsByName = Object.fromEntries(list.map(p => [p.name, p]));
  }

  // ── Saved views (object=company) ──
  async function _groupsLoadViews() {
    try {
      const r = await api('/api/contacts-views?object=company');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      GROUPS.views = d.views || [];
      GROUPS.defaultViewId = d.defaultId || null;
      // First load lands on the team-wide "All groups" system view unless the
      // user chose their own default (same rule as Contacts, Ayoub 2026-08-15).
      const active = GROUPS.views.find(v => v.id === GROUPS.defaultViewId)
                  || GROUPS.views.find(v => v._systemKey === 'all_companies')
                  || GROUPS.views.find(v => v.ownerId === state.me?.id)
                  || GROUPS.views[0]
                  || null;
      _groupsApplyView(active);
    } catch (err) {
      console.warn('[groups] views load failed:', err);
      _groupsApplyView(null);
    }
  }

  function _groupsQuickFilters() {
    return {
      teacher: GROUPS.fTeacher || '', classType: GROUPS.classType || 'all', level: GROUPS.level || 'all',
      seats: GROUPS.seats || '',
      dateField: GROUPS.fDateField || '', datePreset: GROUPS.fDatePreset || '',
      dateFrom: GROUPS.fDateFrom || '', dateTo: GROUPS.fDateTo || '', kpiMode: GROUPS.kpiMode || '',
    };
  }

  function _groupsApplyView(view) {
    GROUPS.activeView = view || null;
    GROUPS.columns = view?.columns ? [...view.columns] : [..._GROUPS_DEFAULT_COLUMNS];
    const qf = (view && view.quickFilters && typeof view.quickFilters === 'object') ? view.quickFilters : {};
    GROUPS.status = view?.statusFilter || '';
    GROUPS.fTeacher = String(qf.teacher || '');
    GROUPS.classType = qf.classType || 'all';
    GROUPS.level = qf.level || 'all';
    GROUPS.seats = qf.seats || '';
    GROUPS.fDateField = qf.dateField || '';
    GROUPS.fDatePreset = qf.datePreset || '';
    GROUPS.fDateFrom = qf.dateFrom || '';
    GROUPS.fDateTo = qf.dateTo || '';
    GROUPS.kpiMode = qf.kpiMode || '';
    const saved = Array.isArray(view?.advancedFilters) ? view.advancedFilters : [];
    GROUPS.advancedGroups = saved
      .map(g => (Array.isArray(g) ? g : [g]).filter(f => f && typeof f === 'object' && f.field && f.op))
      .filter(g => g.length);
    GROUPS.sortKey = view?.sortBy || 'name';
    GROUPS.sortDir = view?.sortDir === 'desc' ? 'desc' : 'asc';
    _groupsHydrateFilterUi(view?.searchQuery || '');
    _renderAdvGroups(_GROUPS_WS);
    _renderAdvSummary(_GROUPS_WS);
    GROUPS._baselineSig = _groupsSignView();
    GROUPS.dirty = false;
    GROUPS.pageIndex = 1;
    GROUPS.selectedIds.clear();
    _renderViewsTabs(_GROUPS_WS);
    _groupsUpdateDirtyPill();
  }

  function _groupsHydrateFilterUi(searchQuery) {
    const set = (id, v) => { const n = document.getElementById(id); if (n) n.value = v; };
    set('groupsStatusFilter', GROUPS.status);
    set('grpFTeacher', GROUPS.fTeacher);
    set('groupsClassType', GROUPS.classType);
    set('groupsSeatsFilter', GROUPS.seats);
    set('grpFDateField', GROUPS.fDateField);
    set('grpFDatePreset', GROUPS.fDatePreset);
    set('grpFDateFrom', GROUPS.fDateFrom);
    set('grpFDateTo', GROUPS.fDateTo);
    set('groupsSearch', searchQuery || '');
    _groupsSyncQuickFilterUi();
  }

  function _groupsSyncQuickFilterUi() {
    const hasField = !!GROUPS.fDateField;
    const between = hasField && GROUPS.fDatePreset === 'between';
    document.getElementById('grpFDatePresetWrap')?.classList.toggle('hidden', !hasField);
    document.getElementById('grpFDateFrom')?.classList.toggle('hidden', !between);
    document.getElementById('grpFDateTo')?.classList.toggle('hidden', !between);
    const mark = (id, active) => document.getElementById(id)?.closest('.contacts-quick-select')?.classList.toggle('is-active', !!active);
    mark('groupsStatusFilter', GROUPS.status);
    mark('grpFTeacher', GROUPS.fTeacher);
    mark('groupsClassType', GROUPS.classType !== 'all');
    mark('groupsSeatsFilter', GROUPS.seats);
    mark('groupsLevelFilter', GROUPS.level !== 'all');
    mark('grpFDateField', GROUPS.fDateField);
    const any = GROUPS.status || GROUPS.fTeacher || GROUPS.classType !== 'all' || GROUPS.level !== 'all'
      || GROUPS.fDateField || GROUPS.kpiMode || (GROUPS.advancedGroups || []).some(g => g.some(f => f.field))
      || (document.getElementById('groupsSearch')?.value || '').trim();
    document.getElementById('groupsClearFiltersBtn')?.classList.toggle('is-visible', !!any);
  }

  function _groupsSignView() {
    if (!GROUPS.activeView) return '';
    const adv = (GROUPS.advancedGroups || [])
      .map(g => (Array.isArray(g) ? g : []).filter(f => f && f.field && f.op).map(f => ({ field: f.field, op: f.op, value: f.value ?? '' })));
    return JSON.stringify({
      cols: GROUPS.columns, frozen: GROUPS.activeView.frozenCount || 0,
      statusFilter: GROUPS.status || '', searchQuery: document.getElementById('groupsSearch')?.value || '',
      quickFilters: _groupsQuickFilters(), advancedGroups: adv,
    });
  }
  function _groupsCheckDirty() {
    const was = GROUPS.dirty;
    GROUPS.dirty = _groupsSignView() !== GROUPS._baselineSig;
    if (was !== GROUPS.dirty) _groupsUpdateDirtyPill();
  }
  function _groupsUpdateDirtyPill() {
    document.getElementById('groupsViewSaveDirtyBtn')?.classList.toggle('hidden', !GROUPS.dirty || !GROUPS.activeView);
  }
  async function _groupsSwitchView(viewId) {
    const v = GROUPS.views.find(x => x.id === viewId);
    if (!v) return;
    _closeContactsFloatingUi();
    _closeContactInlineEditor();
    _groupsApplyView(v);
    await refreshGroups();
  }
  async function _groupsDeleteView(view) {
    if (!confirm(`Delete view "${view.name}"? This can't be undone.`)) return;
    const r = await api('/api/contacts-views/' + view.id, { method: 'DELETE' });
    if (!r.ok) { toast('Échec de la suppression — réessayez', 'error'); return; }
    toast('View deleted', 'success');
    await _groupsLoadViews();
    await refreshGroups();
  }
  async function _groupsSaveActiveViewDirty() {
    if (!GROUPS.activeView) return;
    const patch = {
      columns: GROUPS.columns,
      ..._GROUPS_WS.viewPayload(),
      sortBy: GROUPS.sortKey, sortDir: GROUPS.sortDir,
      advancedFilters: (GROUPS.advancedGroups || []).map(g => (Array.isArray(g) ? g : []).filter(f => f && f.field && f.op)).filter(g => g.length),
    };
    try {
      const r = await api('/api/contacts-views/' + GROUPS.activeView.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); toast('Save failed: ' + (j.error || ('HTTP ' + r.status)), 'error'); return; }
      const d = await r.json();
      GROUPS.activeView = d.view;
      Object.assign(GROUPS.views.find(v => v.id === d.view.id) || {}, d.view);
      GROUPS._baselineSig = _groupsSignView();
      GROUPS.dirty = false;
      _groupsUpdateDirtyPill();
      toast('View saved', 'success');
    } catch (err) { toast('Save failed: ' + (err.message || 'network error'), 'error'); }
  }

  // ── Header menus ──
  function _openGroupsActionsMenu(anchorEl) {
    const existing = document.getElementById('groupsActionsMenu');
    _closeContactsFloatingUi();
    if (existing) return;
    const menu = el('div', { id: 'groupsActionsMenu', class: 'contacts-actions-menu' });
    menu.appendChild(_contactsMenuButton('refresh-cw', 'Refresh groups', () => refreshGroups({ refetch: true })));
    // Admin-only — see the note on the Contacts actions menu above.
    if (_isSalesAdmin()) menu.appendChild(_contactsMenuButton('download', 'Export current view', () => _groupsExportCsv(GROUPS.filtered)));
    menu.appendChild(_contactsMenuButton('columns-3', 'Edit columns', () => openContactsColumnsModal(_GROUPS_WS)));
    menu.appendChild(_contactsMenuButton('plus', 'Create saved view', () => _openViewModal({ mode: 'new' }, _GROUPS_WS)));
    document.body.appendChild(menu);
    _placeContactsPopover(menu, anchorEl, { align: 'right', width: 205 });
    refreshIcons();
    _closeFloatingOnOutside(menu, anchorEl);
  }
  function _openGroupsObjectMenu(anchorEl) {
    const existing = document.getElementById('groupsObjectMenu');
    _closeContactsFloatingUi();
    if (existing) return;
    const menu = el('div', { id: 'groupsObjectMenu', class: 'contacts-actions-menu contacts-object-menu' });
    const goSystem = (key, fallbackStatus) => {
      const v = GROUPS.views.find(x => x._systemKey === key);
      if (v) return _groupsSwitchView(v.id);
      _clearGroupsFilters();
      GROUPS.status = fallbackStatus;
      const sel = document.getElementById('groupsStatusFilter'); if (sel) sel.value = fallbackStatus;
      _groupsFiltersChanged();
    };
    menu.appendChild(_contactsMenuButton('building-2', 'All groups', () => goSystem('all_companies', '')));
    menu.appendChild(_contactsMenuButton('hourglass', 'In Process', () => goSystem('companies_in_process', 'In Process')));
    menu.appendChild(_contactsMenuButton('user-plus', 'Open seats · Adults', () => goSystem('companies_open_seats_adults', '')));
    menu.appendChild(_contactsMenuButton('user-plus', 'Open seats · Kids', () => goSystem('companies_open_seats_kids', '')));
    document.body.appendChild(menu);
    _placeContactsPopover(menu, anchorEl, { width: 190 });
    refreshIcons();
    _closeFloatingOnOutside(menu, anchorEl);
  }
  // Data quality = the diagnostic stat chips as a popover: click applies the narrow.
  function _openGroupsDataQuality(anchorEl) {
    const existing = document.getElementById('groupsDataQualityPop');
    _closeContactsFloatingUi();
    if (existing) return;
    const pop = el('div', { id: 'groupsDataQualityPop', class: 'contacts-actions-menu' });
    const scope = _groupsScopeRows();
    for (const k of _GRP_KPIS.filter(k => k.test)) {
      const n = scope.filter(k.test).length;
      const b = el('button', { type: 'button', class: GROUPS.kpiMode === k.mode ? 'is-active' : '' }, [
        el('i', { 'data-lucide': k.icon }), el('span', { text: `${k.label} · ${n}` }),
      ]);
      b.addEventListener('click', () => { pop.remove(); GROUPS.kpiMode = GROUPS.kpiMode === k.mode ? '' : k.mode; _groupsFiltersChanged(); });
      pop.appendChild(b);
    }
    document.body.appendChild(pop);
    _placeContactsPopover(pop, anchorEl, { align: 'right', width: 220 });
    refreshIcons(pop);
    _closeFloatingOnOutside(pop, anchorEl);
  }
  function _addGroupsQuickFilter(anchorEl) {
    _openPropPicker(anchorEl, (chosen) => {
      if (!GROUPS.advancedGroups.length) GROUPS.advancedGroups = [[]];
      const def = GROUPS.propsByName[chosen];
      const op = _opsForField(def)[0] || 'equals';
      if (!GROUPS.advancedGroups.flat().find(f => f.field === chosen)) GROUPS.advancedGroups[0].push({ field: chosen, op, value: '' });
      _renderAdvGroups(_GROUPS_WS);
      _renderAdvSummary(_GROUPS_WS);
      _groupsCheckDirty();
      _openAdvPanel(_GROUPS_WS);
    }, GROUPS.propsCatalog);
  }
  function _clearGroupsFilters() {
    Object.assign(GROUPS, { status: '', fTeacher: '', classType: 'all', level: 'all', seats: '', fDateField: '', fDatePreset: '', fDateFrom: '', fDateTo: '', kpiMode: '', advancedGroups: [] });
    _groupsHydrateFilterUi('');
    _renderAdvGroups(_GROUPS_WS);
    _renderAdvSummary(_GROUPS_WS);
    _groupsFiltersChanged();
  }
  function _groupsFiltersChanged() {
    GROUPS.pageIndex = 1;
    _groupsCheckDirty();
    _renderGroupsTable();
  }

  // ── Data ──
  async function loadGroups() { return refreshGroups({ refetch: true }); }
  // `silent` refetches in place: no skeleton, no table teardown, no error
  // toast. Used by the live-sync poller so a background repaint never steals
  // the agent's scroll position or interrupts what they're reading.
  async function refreshGroups({ refetch = false, silent = false } = {}) {
    if (!GROUPS.inited) return;
    if (refetch || !GROUPS.all.length) {
      const loading = document.getElementById('groupsLoading');
      if (!silent) {
        GROUPS.loading = true;
        document.getElementById('groupsTable')?.classList.add('hidden');
        document.getElementById('groupsEmpty')?.classList.add('hidden');
        loading?.classList.remove('hidden');
        _wsRenderSkeleton(loading);
        _wsRenderPager(_GROUPS_WS);
      }
      try {
        // Whole scope in one call (all statuses, ~1400 rows) — every filter is
        // then instant and client-side, exactly like the previous page's tabs.
        const r = await api('/api/hubspot/companies?status=all&limit=2000');
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const j = await r.json();
        GROUPS.all = j.companies || [];
        GROUPS.statusCounts = j.statusCounts || GROUPS.statusCounts;
        GROUPS.totalGroups = j.totalGroups ?? GROUPS.all.length;
        if (j.revision !== undefined) GROUPS.revision = j.revision;
        if (j.syncedAt) GROUPS.syncedAt = j.syncedAt;
        GROUPS.liveFailures = 0;
        _groupsRenderSyncPill();
      } catch (err) {
        GROUPS.loading = false;
        if (silent) { GROUPS.liveFailures++; _groupsRenderSyncPill(); return; }
        loading?.classList.add('hidden');
        toast('Failed to load groups: ' + (err.message || err), 'error');
        return;
      }
      GROUPS.loading = false;
    }
    _renderGroupsTable();
  }

  // ── Live sync ──────────────────────────────────────────────────────────
  // Leads7 and support2 are two windows onto ONE HubSpot portal. Before this,
  // the Leads7 mirror only caught up on a background tick (companies 1h,
  // contacts 30m), so a group edited in s2 — a student added, a teacher
  // assigned — stayed wrong here long enough for an agent to act on it.
  // Now the server pulls the delta on every read, and this poller asks a cheap
  // sync-state route whether anything moved; only then do we refetch.
  // Cost control: one request per tick regardless of how many agents are
  // looking, and nothing runs while the tab is hidden or the view is closed.
  const GROUPS_LIVE_POLL_MS = 25 * 1000;

  function _groupsRenderSyncPill() {
    const pill = document.getElementById('groupsSyncPill');
    const label = document.getElementById('groupsSyncLabel');
    if (!pill || !label) return;
    const stale = GROUPS.liveFailures >= 2;
    pill.classList.toggle('is-syncing', GROUPS.livePolling && !stale);
    pill.classList.toggle('is-stale', stale);
    if (stale) {
      label.textContent = 'Sync off';
      pill.title = 'Cannot reach the server — the table may be out of date. Click to retry.';
      return;
    }
    const ageSec = GROUPS.syncedAt ? Math.max(0, Math.round((Date.now() - GROUPS.syncedAt) / 1000)) : null;
    label.textContent = ageSec == null ? 'Live'
      : ageSec < 60 ? 'Live · ' + ageSec + 's'
      : 'Live · ' + Math.round(ageSec / 60) + 'm';
    pill.title = 'Following HubSpot (same data support2 writes to). Click to refresh now.';
  }

  function _groupsViewIsOpen() {
    const v = document.getElementById('groupsView');
    return !!v && !v.classList.contains('hidden');
  }

  async function _groupsPollLive() {
    if (!GROUPS.inited || !_groupsViewIsOpen()) return;
    if (document.visibilityState === 'hidden') return;
    if (GROUPS.livePolling || GROUPS.loading) return;
    GROUPS.livePolling = true;
    _groupsRenderSyncPill();
    try {
      const r = await api('/api/hubspot/companies/sync-state');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      GROUPS.liveFailures = 0;
      if (j.syncedAt) GROUPS.syncedAt = j.syncedAt;
      // First poll after a page load with no revision yet: adopt it silently.
      if (GROUPS.revision == null) GROUPS.revision = j.revision;
      else if (j.revision !== GROUPS.revision) {
        GROUPS.revision = j.revision;
        await refreshGroups({ refetch: true, silent: true });
      }
    } catch {
      GROUPS.liveFailures++;
    } finally {
      GROUPS.livePolling = false;
      _groupsRenderSyncPill();
    }
  }

  function _groupsStartLiveSync() {
    if (GROUPS.liveTimer) return;
    GROUPS.liveTimer = setInterval(_groupsPollLive, GROUPS_LIVE_POLL_MS);
    _groupsRenderSyncPill();
  }
  function _groupsStopLiveSync() {
    if (!GROUPS.liveTimer) return;
    clearInterval(GROUPS.liveTimer);
    GROUPS.liveTimer = null;
  }

  // Filter pipeline (all client-side): search → status → teacher → date range
  // → class type → level → data-quality mode → advanced groups (OR of ANDs).
  function _groupsScopeRows() {
    const q = (document.getElementById('groupsSearch')?.value || '').trim().toLowerCase();
    const teacherQ = String(GROUPS.fTeacher || '').trim().toLowerCase();
    const dateRange = (GROUPS.fDateField && GROUPS.fDatePreset) ? _grpPresetRange(GROUPS.fDatePreset, GROUPS.fDateFrom, GROUPS.fDateTo) : null;
    return GROUPS.all.filter(g => {
      if (q && !String(g.name || '').toLowerCase().includes(q)) return false;
      if (GROUPS.status && String(g.group_status || '') !== GROUPS.status) return false;
      if (teacherQ) {
        const names = (g.teachers && g.teachers.length ? g.teachers : String(g.lougha_teachers || '').split(/[;,]/)).map(t => String(t).toLowerCase());
        if (!names.some(t => t.includes(teacherQ))) return false;
      }
      if (dateRange) {
        const ms = _grpRowDateMs(g, GROUPS.fDateField);
        if (ms == null || ms < dateRange.startMs || ms >= dateRange.endMs) return false;
      }
      if (GROUPS.classType !== 'all' && _groupClassTypeOf(g) !== GROUPS.classType) return false;
      if (!_grpSeatsMatch(g, GROUPS.seats)) return false;
      return true;
    });
  }
  function _groupsFilteredRows() {
    const scoped = _groupsScopeRows();
    _grpRenderLevelOptions(scoped);
    const levelFiltered = GROUPS.level === 'all' ? scoped : scoped.filter(g => _grpParseLevels(g.level_of_groups).includes(GROUPS.level));
    const kpi = _GRP_KPIS.find(k => k.mode === GROUPS.kpiMode && k.test);
    const kpiFiltered = kpi ? levelFiltered.filter(kpi.test) : levelFiltered;
    const groups = (GROUPS.advancedGroups || []).map(g => g.filter(f => f && f.field && f.op)).filter(g => g.length);
    const adv = groups.length ? kpiFiltered.filter(g => _wsRowMatchesAdvGroups(g, groups, GROUPS.propsByName, _groupValue)) : kpiFiltered;
    return _groupsSortRows(adv);
  }
  function _groupValue(g, key) {
    if (key === 'name') return g.name || '';
    if (key === 'student_count') return g.student_count || 0;
    // Individual classes sort last on seats (no cap → not a number).
    if (key === 'seats_left') { const s = _grpSeats(g); return s.left == null ? -1 : s.left; }
    if (g[key] !== undefined && g[key] !== null && g[key] !== '') return g[key];
    return g.properties?.[key] ?? '';
  }
  function _groupsSortRows(rows) {
    const key = GROUPS.sortKey || 'name';
    const dir = GROUPS.sortDir === 'desc' ? -1 : 1;
    const def = GROUPS.propsByName[key];
    const isNum = key === 'student_count' || key === 'seats_left' || def?.type === 'number';
    const isDate = def && (def.type === 'date' || def.type === 'datetime');
    return rows.slice().sort((a, b) => {
      let va = _groupValue(a, key), vb = _groupValue(b, key);
      if (isNum) { va = Number(va) || 0; vb = Number(vb) || 0; return (va - vb) * dir; }
      if (isDate) { va = _grpDateMs(va) || 0; vb = _grpDateMs(vb) || 0; return (va - vb) * dir; }
      return String(va || '').localeCompare(String(vb || ''), undefined, { sensitivity: 'base', numeric: true }) * dir;
    });
  }
  function _toggleGroupsSort(key) {
    if (GROUPS.sortKey === key) GROUPS.sortDir = GROUPS.sortDir === 'asc' ? 'desc' : 'asc';
    else { GROUPS.sortKey = key; GROUPS.sortDir = 'asc'; }
    GROUPS.pageIndex = 1;
    _renderGroupsTable();
  }
  function _groupsGoToPage(n) {
    const totalPages = Math.max(1, Math.ceil(GROUPS.total / (GROUPS.pageSize || 25)));
    if (n < 1 || n > totalPages || n === GROUPS.pageIndex) return;
    GROUPS.pageIndex = n;
    _renderGroupsTable();
    document.getElementById('groupsTableViewport')?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }

  // Client-side evaluator for the advanced-filter model (same ops/values the
  // Contacts drawer produces; Contacts evaluates them server-side).
  function _wsRowMatchesAdvGroups(row, groups, propsByName, getValue) {
    return groups.some(group => group.every(f => _wsFilterMatches(row, f, propsByName[f.field], getValue)));
  }
  function _wsFilterMatches(row, f, def, getValue) {
    const raw = getValue(row, f.field);
    const isEmpty = raw == null || raw === '' || (Array.isArray(raw) && !raw.length);
    const op = String(f.op || 'equals');
    if (op === 'is_known' || op === 'not_empty') return !isEmpty;
    if (op === 'is_unknown' || op === 'empty') return isEmpty;
    const isDate = def && (def.type === 'date' || def.type === 'datetime');
    const isNum = def && def.type === 'number';
    const val = String(f.value ?? '');
    if (isDate) {
      const ms = _grpDateMs(raw);
      if (op === 'is') {
        const range = _wsDatePresetRange(val);
        return !!range && ms != null && ms >= range.startMs && ms < range.endMs;
      }
      const dayMs = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || ''); return m ? new Date(+m[1], +m[2] - 1, +m[3]).getTime() : null; };
      if (op === 'is_between') { const [a, b] = val.split('..'); const s = dayMs(a), e = dayMs(b); return s != null && e != null && ms != null && ms >= s && ms < e + 86400000; }
      if (op === 'is_more_than' || op === 'is_less_than') {
        const m = val.match(/^(\d+)_(day|days|week|weeks|month|months|year|years)_(ago|from_now)$/);
        if (!m || ms == null) return false;
        const n = Number(m[1]); const unit = m[2].replace(/s$/, ''); const d = MA.today();
        const sign = m[3] === 'ago' ? -1 : 1;
        if (unit === 'day') d.setDate(d.getDate() + sign * n);
        else if (unit === 'week') d.setDate(d.getDate() + sign * 7 * n);
        else if (unit === 'month') d.setMonth(d.getMonth() + sign * n);
        else d.setFullYear(d.getFullYear() + sign * n);
        const pivot = d.getTime();
        // "more than N ago" = older than pivot; "less than N ago" = newer than pivot.
        return op === 'is_more_than' ? (sign < 0 ? ms < pivot : ms > pivot) : (sign < 0 ? ms >= pivot : ms <= pivot);
      }
      const target = dayMs(val);
      if (target == null || ms == null) return false;
      if (op === 'is_equal_to' || op === 'equals') return ms >= target && ms < target + 86400000;
      if (op === 'is_before' || op === 'before') return ms < target;
      if (op === 'is_after' || op === 'after') return ms >= target + 86400000;
      return false;
    }
    if (isNum) {
      const a = Number(raw), b = Number(val);
      if (!Number.isFinite(b) || isEmpty) return false;
      if (op === 'equals' || op === '=') return a === b;
      if (op === '>=') return a >= b;
      if (op === '<=') return a <= b;
      return false;
    }
    const hay = Array.isArray(raw) ? raw.join(';') : String(raw ?? '');
    const parts = hay.split(';').map(s => s.trim().toLowerCase());
    if (op === 'equals' || op === '=' || op === 'is_equal_to') return parts.includes(val.toLowerCase()) || hay.toLowerCase() === val.toLowerCase();
    if (op === 'contains') return hay.toLowerCase().includes(val.toLowerCase());
    return false;
  }
  // Every _DATE_PRESETS value → [startMs, endMs) in LOCAL time (Monday weeks).
  function _wsDatePresetRange(preset) {
    const today = MA.today();
    const addDays = (base, n) => { const d = new Date(base); d.setDate(d.getDate() + n); return d; };
    const monday = (w) => addDays(today, -((today.getDay() + 6) % 7) + w * 7);
    const month = (o) => new Date(today.getFullYear(), today.getMonth() + o, 1);
    const q = Math.floor(today.getMonth() / 3);
    const year = (o) => new Date(today.getFullYear() + o, 0, 1);
    switch (preset) {
      case 'today': return { startMs: today.getTime(), endMs: addDays(today, 1).getTime() };
      case 'yesterday': return { startMs: addDays(today, -1).getTime(), endMs: today.getTime() };
      case 'tomorrow': return { startMs: addDays(today, 1).getTime(), endMs: addDays(today, 2).getTime() };
      case 'this_week': return { startMs: monday(0).getTime(), endMs: monday(1).getTime() };
      case 'last_week': return { startMs: monday(-1).getTime(), endMs: monday(0).getTime() };
      case 'next_week': return { startMs: monday(1).getTime(), endMs: monday(2).getTime() };
      case 'this_month': return { startMs: month(0).getTime(), endMs: month(1).getTime() };
      case 'last_month': return { startMs: month(-1).getTime(), endMs: month(0).getTime() };
      case 'next_month': return { startMs: month(1).getTime(), endMs: month(2).getTime() };
      case 'this_quarter': return { startMs: new Date(today.getFullYear(), q * 3, 1).getTime(), endMs: new Date(today.getFullYear(), q * 3 + 3, 1).getTime() };
      case 'this_year': return { startMs: year(0).getTime(), endMs: year(1).getTime() };
      case 'last_year': return { startMs: year(-1).getTime(), endMs: year(0).getTime() };
    }
    return null;
  }

  // ── Grid ──
  function _groupsVisibleColumns() { return (GROUPS.columns || []).filter(c => c && c !== 'name'); }
  function _groupsReorderColumn(fromKey, toKey, insertBefore) {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const cols = (GROUPS.columns || []).slice();
    const fromIdx = cols.indexOf(fromKey);
    if (fromIdx === -1) return;
    cols.splice(fromIdx, 1);
    let toIdx = cols.indexOf(toKey);
    if (toIdx === -1) cols.push(fromKey); else { if (!insertBefore) toIdx += 1; cols.splice(toIdx, 0, fromKey); }
    GROUPS.columns = cols;
    _renderGroupsTable();
    _groupsCheckDirty();
  }
  function _renderGroupsHead() {
    const thead = document.getElementById('groupsThead');
    if (!thead) return;
    clear(thead);
    const fillHeader = (th, label, sortKey) => {
      th.setAttribute('scope', 'col');
      clear(th);
      const inner = el('div', { class: 'contacts-th-inner' });
      inner.appendChild(el('span', { class: 'contacts-th-label', text: label }));
      if (sortKey) {
        const active = GROUPS.sortKey === sortKey;
        th.setAttribute('aria-sort', active ? (GROUPS.sortDir === 'asc' ? 'ascending' : 'descending') : 'none');
        const sortBtn = el('button', {
          type: 'button', class: 'contacts-sort-btn' + (active ? ' is-active' : ''),
          title: active ? `Sorted ${GROUPS.sortDir === 'asc' ? 'ascending' : 'descending'}` : `Sort by ${label}`, 'aria-label': `Sort by ${label}`,
        }, [el('i', { 'data-lucide': !active ? 'arrow-up-down' : (GROUPS.sortDir === 'asc' ? 'arrow-up' : 'arrow-down') })]);
        sortBtn.addEventListener('mousedown', ev => ev.stopPropagation());
        sortBtn.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _toggleGroupsSort(sortKey); });
        inner.appendChild(sortBtn);
      }
      th.appendChild(inner);
    };
    const frozenN = GROUPS.activeView?.frozenCount || 0;
    const tr = el('tr', {});
    tr.appendChild(el('th', { class: 'frozen', scope: 'col', style: 'left:0px; min-width:36px;' }, [el('input', { type: 'checkbox', id: 'groupsSelectAll', 'aria-label': 'Select all groups on this page' })]));
    // Name is ALWAYS frozen right after the checkbox (HubSpot: the record name column sticks).
    const thName = el('th', { class: 'frozen', style: 'left:36px; min-width:200px;' });
    fillHeader(thName, 'Company name', 'name');
    tr.appendChild(thName);
    _attachColResize(thName, 'name', _GROUPS_WS);
    let leftOffset = 36 + 200;
    _groupsVisibleColumns().forEach((propName, i) => {
      const def = GROUPS.propsByName[propName] || { name: propName, label: propName };
      const isFrozen = i < frozenN;
      const th = el('th', { class: isFrozen ? 'frozen' : '', style: isFrozen ? `left:${leftOffset}px;` : '' });
      fillHeader(th, def.label || propName, propName);
      tr.appendChild(th);
      _attachColResize(th, propName, _GROUPS_WS);
      _attachColReorder(th, propName, _GROUPS_WS);
      if (isFrozen) leftOffset += 160;
    });
    thead.appendChild(tr);
    refreshIcons();
    document.getElementById('groupsSelectAll')?.addEventListener('change', (ev) => {
      for (const g of GROUPS.rows) { if (ev.target.checked) GROUPS.selectedIds.add(String(g.id)); else GROUPS.selectedIds.delete(String(g.id)); }
      _renderGroupsTable();
    });
  }

  function _renderGroupsTable() {
    const table = document.getElementById('groupsTable');
    const tbody = document.getElementById('groupsTbody');
    const empty = document.getElementById('groupsEmpty');
    const loading = document.getElementById('groupsLoading');
    if (!tbody) return;
    _grpFillTeacherOpts();
    _groupsSyncQuickFilterUi();
    GROUPS.filtered = _groupsFilteredRows();
    GROUPS.total = GROUPS.filtered.length;
    const totalPages = Math.max(1, Math.ceil(GROUPS.total / (GROUPS.pageSize || 25)));
    if (GROUPS.pageIndex > totalPages) GROUPS.pageIndex = totalPages;
    const start = (GROUPS.pageIndex - 1) * GROUPS.pageSize;
    GROUPS.rows = GROUPS.filtered.slice(start, start + GROUPS.pageSize);
    _grpRenderKpis(GROUPS.filtered);
    const count = document.getElementById('groupsCount');
    if (count) {
      const totalAll = GROUPS.all.length;
      count.textContent = totalAll && GROUPS.total !== totalAll
        ? `${GROUPS.total.toLocaleString()} of ${totalAll.toLocaleString()} groups`
        : `${GROUPS.total.toLocaleString()} record${GROUPS.total === 1 ? '' : 's'}`;
    }
    loading?.classList.add('hidden');
    _renderGroupsHead();
    clear(tbody);
    if (!GROUPS.rows.length) {
      table?.classList.add('hidden');
      empty?.classList.remove('hidden');
    } else {
      empty?.classList.add('hidden');
      table?.classList.remove('hidden');
      const frozenN = GROUPS.activeView?.frozenCount || 0;
      const cols = _groupsVisibleColumns();
      for (const g of GROUPS.rows) {
        const selected = GROUPS.selectedIds.has(String(g.id));
        const tr = el('tr', { class: selected ? 'is-selected' : '', 'data-company-id': g.id });
        const cb = el('input', { type: 'checkbox', class: 'contact-row-check', 'aria-label': `Select ${g.name}`, ...(selected ? { checked: 'checked' } : {}) });
        cb.addEventListener('change', () => { if (cb.checked) GROUPS.selectedIds.add(String(g.id)); else GROUPS.selectedIds.delete(String(g.id)); tr.classList.toggle('is-selected', cb.checked); _renderGroupsBulkBar(); });
        tr.appendChild(el('td', { class: 'frozen', style: 'left:0px;' }, [cb]));
        tr.appendChild(_buildGroupNameCell(g));
        let leftOffset = 36 + 200;
        cols.forEach((propName, i) => {
          const cell = _buildGroupCell(g, propName);
          if (i < frozenN) { cell.classList.add('frozen'); cell.style.left = leftOffset + 'px'; leftOffset += 160; }
          tr.appendChild(cell);
        });
        tr.querySelectorAll('td').forEach((cell, index) => {
          if (index === 0) return;
          cell.addEventListener('click', (ev) => {
            document.querySelectorAll('#groupsTbody td.is-active-cell').forEach(td => td.classList.remove('is-active-cell'));
            cell.classList.add('is-active-cell');
            // One click anywhere in an editable cell opens its editor (Contacts rule).
            if (typeof cell._contactsOpenEditor !== 'function') return;
            if (ev.target.closest('button, a, input, select, textarea, label')) return;
            cell._contactsOpenEditor();
          });
        });
        tbody.appendChild(tr);
      }
    }
    _renderGroupsBulkBar();
    _wsRenderPager(_GROUPS_WS);
    refreshIcons();
  }

  function _groupInitials(name) {
    return String(name || '').trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
  }
  function _buildGroupNameCell(g) {
    const name = g.name || '(no name)';
    const td = el('td', { class: 'contact-name-cell frozen', style: 'left:36px;', title: 'Open group preview' });
    const nameLink = el('button', { type: 'button', class: 'contact-name-link', text: name, title: `Preview ${name}` });
    nameLink.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _openGroupPreviewDrawer(g); });
    const previewBtn = el('button', { type: 'button', class: 'contact-preview-btn', title: 'Preview group', 'aria-label': `Preview ${name}` }, [el('i', { 'data-lucide': 'panel-right-open' })]);
    previewBtn.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _openGroupPreviewDrawer(g); });
    const editBtn = el('button', { type: 'button', class: 'contact-name-edit-btn', title: 'Edit name', 'aria-label': `Edit ${name}` }, [el('i', { 'data-lucide': 'pencil' })]);
    const nameDef = GROUPS.propsByName.name || { name: 'name', label: 'Company name', type: 'string', fieldType: 'text', options: [] };
    editBtn.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _openContactEditor(editBtn, g, nameDef, g.name || '', { onSave: _saveGroupEdit }); });
    td.appendChild(el('div', { class: 'contact-name-wrap' }, [
      el('span', { class: 'contact-name-avatar', text: _groupInitials(name), 'aria-hidden': 'true' }),
      nameLink, previewBtn, editBtn,
    ]));
    return td;
  }

  function _groupDisplayValue(g, propName, def) {
    const raw = _groupValue(g, propName);
    if (raw == null || raw === '') return '';
    if (def && (def.type === 'date' || def.type === 'datetime')) {
      const ms = _grpDateMs(raw);
      if (ms == null) return String(raw);
      const d = new Date(ms);
      return def.type === 'datetime'
        ? MA.loc(d, 'dt', 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
        : MA.loc(d, 'd', 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    if (def?.options?.length) {
      return String(raw).split(';').map(v => v.trim()).filter(Boolean)
        .map(v => def.options.find(o => String(o.value) === v)?.label || v).join(', ');
    }
    return String(raw);
  }

  function _buildGroupCell(g, propName) {
    const def = GROUPS.propsByName[propName] || { name: propName, label: propName, type: 'string', options: [] };
    const td = el('td');
    // Students — always clickable pill (count or "+ Add") → students modal.
    if (propName === 'student_count') {
      const pill = el('button', { type: 'button', class: 'contact-pill' + (g.student_count > 0 ? '' : ' contact-pill-empty'), title: 'View / add students' });
      pill.textContent = g.student_count > 0 ? (g.student_count + ' student' + (g.student_count > 1 ? 's' : '')) : '+ Add';
      pill.addEventListener('click', (ev) => { ev.stopPropagation(); openGroupStudentsModal(g); });
      td.appendChild(pill);
      td._contactsOpenEditor = () => pill.click();
      return td;
    }
    // Seats left — "7 free" / "Full" / "—" for individual classes. Reads as a
    // badge, not an editable pill: it is derived, never stored in HubSpot.
    if (propName === 'seats_left') {
      const s = _grpSeats(g);
      if (s.cap == null) {
        td.appendChild(el('span', { class: 'grp-seats grp-seats-na', text: '—', title: 'Individual class — no group capacity' }));
        return td;
      }
      const tone = !s.live ? 'off' : s.left <= 0 ? 'full' : s.left <= 2 ? 'low' : 'ok';
      td.appendChild(el('span', {
        class: 'grp-seats grp-seats-' + tone,
        text: s.left > 0 ? s.left + ' free' : 'Full',
        title: `${s.used} / ${s.cap} students · cap ${s.cap} (${s.kind})` + (s.live ? '' : ' · group not open'),
      }));
      return td;
    }
    const rawValue = _groupValue(g, propName);
    const display = _groupDisplayValue(g, propName, def);
    if (def.readOnly || def._virtual) {
      td.appendChild(document.createTextNode(display || ''));
      return td;
    }
    const isMulti = (def.fieldType === 'checkbox' || def.fieldType === 'multiplecheckbox') && def.options?.length;
    const semantic = propName === 'group_status' ? _groupStatusKind(rawValue) : '';
    // Empty values render BLANK (no "set" placeholder — Ayoub, 2026-08-15); the
    // whole cell still opens the editor.
    const pill = el('button', {
      type: 'button',
      class: 'contact-pill' + (display ? '' : ' contact-pill-empty') + (isMulti ? ' contact-pill-multi' : '') + (semantic ? ' status-' + semantic : ''),
      text: display || '',
      ...(display ? {} : { 'aria-label': 'Set ' + (def.label || propName), title: 'Set ' + (def.label || propName) }),
    });
    if (_isLongTextField(def, propName) || propName === 'events_report') {
      pill.addEventListener('click', (ev) => { ev.stopPropagation(); _openLongTextEditor(g, def, propName, rawValue, { onSave: _saveGroupEdit, subtitle: g.name }); });
    } else {
      pill.addEventListener('click', (ev) => { ev.stopPropagation(); _openContactEditor(pill, g, def, rawValue, { onSave: _saveGroupEdit }); });
    }
    td.appendChild(pill);
    td._contactsOpenEditor = () => pill.click();
    return td;
  }
  function _groupStatusKind(raw) {
    const v = String(raw || '').toLowerCase();
    if (v === 'active' || v === 'expandable') return 'pos';
    if (v === 'in process' || v === 'paused') return 'pending';
    if (v === 'closed' || v === 'done') return 'neutral';
    return '';
  }

  // Persist an inline edit: PATCH company → HubSpot + mirror; update the local
  // row so the grid reads back the new value without a refetch.
  async function _saveGroupEdit(g, def, value) {
    _closeContactInlineEditor();
    try {
      const r = await api('/api/hubspot/companies/' + encodeURIComponent(g.id), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ properties: { [def.name]: value } }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast(`Update ${def.label || def.name} failed: ${j.error || r.status}`, 'error');
        return false;
      }
      const j = await r.json().catch(() => ({}));
      const props = j.properties || { [def.name]: value };
      g.properties = { ...(g.properties || {}), ...props };
      for (const [k, v] of Object.entries(props)) if (k in g) g[k] = v;
      if (props.name != null) g.name = props.name;
      if (props.lougha_teachers != null) g.teachers = String(props.lougha_teachers || '').split(/[;,]/).map(s => s.trim()).filter(Boolean);
      toast(`${def.label || def.name} updated`, 'success');
      _renderGroupsTable();
      const drawer = document.getElementById('groupsPreviewDrawer');
      if (drawer && drawer._groupId === String(g.id)) _openGroupPreviewDrawer(g);
      return true;
    } catch (err) {
      toast('Update failed: ' + (err.message || err), 'error');
      return false;
    }
  }

  // ── Preview drawer ──
  function _openGroupPreviewDrawer(g) {
    document.getElementById('groupsPreviewDrawer')?.remove();
    const name = g.name || '(no name)';
    const drawer = el('aside', { id: 'groupsPreviewDrawer', class: 'contacts-preview-drawer', role: 'dialog', 'aria-modal': 'false', 'aria-label': `Group preview: ${name}` });
    drawer._groupId = String(g.id);
    const head = el('div', { class: 'contacts-preview-head' });
    const close = el('button', { type: 'button', class: 'contacts-preview-close', title: 'Close preview', 'aria-label': 'Close group preview' }, [el('i', { 'data-lucide': 'x' })]);
    let onKey = null;
    const closeDrawer = () => {
      document.getElementById('groupsPreviewActions')?.remove();
      _closeContactInlineEditor();
      drawer.remove();
      if (onKey) document.removeEventListener('keydown', onKey);
    };
    close.addEventListener('click', closeDrawer);
    head.appendChild(el('div', { class: 'contacts-preview-titlebar' }, [el('h2', { text: name }), close]));
    const nameDef = GROUPS.propsByName.name || { name: 'name', label: 'Company name', type: 'string', fieldType: 'text', options: [] };
    const reportDef = GROUPS.propsByName.events_report || { name: 'events_report', label: 'Events report', type: 'string', fieldType: 'textarea', options: [] };
    const openActionsMenu = (anchor) => {
      document.getElementById('groupsPreviewActions')?.remove();
      const menu = el('div', { id: 'groupsPreviewActions', class: 'contacts-actions-menu contacts-preview-actions-menu' });
      const add = (icon, label, fn) => { const b = el('button', { type: 'button' }, [el('i', { 'data-lucide': icon }), el('span', { text: label })]); b.addEventListener('click', () => { menu.remove(); fn(); }); menu.appendChild(b); };
      add('pencil', 'Edit group name', () => _openContactEditor(drawer.querySelector('.contacts-preview-profile-name'), g, nameDef, g.name || '', { onSave: _saveGroupEdit }));
      add('users', 'Manage students', () => openGroupStudentsModal(g));
      add('notebook-pen', 'Events report', () => _openLongTextEditor(g, reportDef, 'events_report', _groupValue(g, 'events_report'), { onSave: _saveGroupEdit, subtitle: g.name }));
      add('x', 'Close preview', closeDrawer);
      document.body.appendChild(menu);
      const rect = anchor.getBoundingClientRect();
      menu.style.left = `${Math.max(8, rect.right - 220)}px`;
      menu.style.top = `${rect.bottom + 5}px`;
      refreshIcons(menu);
      setTimeout(() => {
        const off = (ev) => { if (!menu.contains(ev.target) && ev.target !== anchor) { menu.remove(); document.removeEventListener('pointerdown', off, true); } };
        document.addEventListener('pointerdown', off, true);
      }, 0);
    };
    const navActions = el('button', { type: 'button', class: 'contacts-preview-nav-actions' }, [el('span', { text: 'Actions' }), el('i', { 'data-lucide': 'chevron-down' })]);
    navActions.addEventListener('click', () => openActionsMenu(navActions));
    head.appendChild(el('div', { class: 'contacts-preview-nav' }, [el('span', { class: 'contacts-preview-nav-active', text: 'View record' }), navActions]));
    const profileName = el('button', { type: 'button', class: 'contacts-preview-profile-name', title: 'Edit group name' }, [el('strong', { text: name }), el('i', { 'data-lucide': 'pencil' })]);
    profileName.addEventListener('click', () => _openContactEditor(profileName, g, nameDef, g.name || '', { onSave: _saveGroupEdit }));
    const subline = [g.group_status ? (_GROUPS_STATUS_LABELS[g.group_status] || g.group_status) : '', g.level_of_groups, g.schedule_days, g.schedule_time].filter(Boolean).join(' · ');
    head.appendChild(el('div', { class: 'contacts-preview-person' }, [
      el('span', { class: 'contacts-preview-avatar', text: _groupInitials(name), 'aria-hidden': 'true' }),
      el('div', { class: 'contacts-preview-person-copy' }, [profileName, el('span', { text: subline || 'No schedule details' })]),
    ]));
    const actions = el('div', { class: 'contacts-preview-actions' });
    const action = (icon, label, fn) => { const b = el('button', { type: 'button', class: 'contacts-preview-action', title: label }, [el('span', {}, [el('i', { 'data-lucide': icon })]), el('span', { text: label })]); b.addEventListener('click', fn); actions.appendChild(b); return b; };
    action('users', 'Students', () => openGroupStudentsModal(g));
    action('user-plus', 'Add student', () => openGroupStudentsModal(g));
    action('notebook-pen', 'Report', () => _openLongTextEditor(g, reportDef, 'events_report', _groupValue(g, 'events_report'), { onSave: _saveGroupEdit, subtitle: g.name }));
    action('more-horizontal', 'More', () => openActionsMenu(actions.lastElementChild));
    head.appendChild(actions);
    drawer.appendChild(head);

    const body = el('div', { class: 'contacts-preview-body' });
    const propRows = [];
    const addProp = (key, label, value, def) => {
      const display = value == null || value === '' ? '--' : String(value);
      const isMulti = display !== '--' && (def?.fieldType === 'checkbox' || def?.fieldType === 'multiplecheckbox' || (display.includes(';')));
      let valueNode;
      if (isMulti) {
        const chips = display.split(';').map(s => s.trim()).filter(Boolean).map(part => el('span', { class: 'contacts-preview-value-chip', text: def?.options?.find(o => String(o.value) === part)?.label || part }));
        valueNode = el('div', { class: 'contacts-preview-property-value is-multi' }, chips.length ? chips : [document.createTextNode('--')]);
      } else if (display !== '--' && key === 'group_status') {
        valueNode = el('div', { class: 'contacts-preview-property-value is-multi' }, [el('span', { class: 'contacts-preview-value-chip', text: display })]);
      } else {
        valueNode = el('div', { class: 'contacts-preview-property-value', text: display });
      }
      propRows.push(el('div', { class: 'contacts-preview-property' }, [el('label', { text: label }), valueNode]));
    };
    addProp('student_count', 'Students', g.student_count > 0 ? String(g.student_count) : '0');
    for (const p of (GROUPS.propsCatalog || [])) {
      if (p.name === 'name' || p.name === 'student_count') continue;
      addProp(p.name, p.label || p.name, _groupDisplayValue(g, p.name, p), p);
    }
    const aboutSettings = el('button', { type: 'button', class: 'contacts-preview-section-settings', title: 'Edit columns', 'aria-label': 'Edit columns' }, [el('i', { 'data-lucide': 'settings' })]);
    aboutSettings.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); openContactsColumnsModal(_GROUPS_WS); });
    body.appendChild(el('details', { class: 'contacts-preview-section', open: 'open' }, [
      el('summary', {}, [el('span', { class: 'contacts-preview-section-title', text: 'About this group' }), el('span', { class: 'contacts-preview-section-spacer' }), aboutSettings]),
      el('div', { class: 'contacts-preview-properties' }, propRows),
    ]));
    // Students list — fetched live from the mirror associations.
    const studentsHost = el('div', { class: 'contacts-preview-activity', text: 'Loading students…' });
    body.appendChild(el('details', { class: 'contacts-preview-section', open: 'open' }, [
      el('summary', { text: `Students (${g.student_count || 0})` }), studentsHost,
    ]));
    api('/api/hubspot/companies/' + encodeURIComponent(g.id) + '/students').then(async (r) => {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      const list = j.students || [];
      clear(studentsHost);
      if (!list.length) { studentsHost.textContent = 'No students linked yet.'; return; }
      const ul = el('div', { class: 'groups-preview-students' });
      for (const s of list) {
        ul.appendChild(el('div', { class: 'groups-preview-student' }, [
          el('span', { class: 'groups-preview-student-name', text: s.name || '(no name)' }),
          el('span', { class: 'groups-preview-student-meta', text: [s.phone, s.lead_status].filter(Boolean).join(' · ') }),
        ]));
      }
      studentsHost.appendChild(ul);
      const manage = el('button', { type: 'button', class: 'contacts-preview-section-action', text: 'Manage students' });
      manage.addEventListener('click', () => openGroupStudentsModal(g));
      studentsHost.appendChild(manage);
    }).catch(() => { studentsHost.textContent = 'Could not load students.'; });
    drawer.appendChild(body);
    document.body.appendChild(drawer);
    refreshIcons();
    onKey = (ev) => { if (ev.key === 'Escape') closeDrawer(); };
    document.addEventListener('keydown', onKey);
  }

  // ── Bulk bar ──
  function _renderGroupsBulkBar() {
    const bar = document.getElementById('groupsBulkBar');
    if (!bar) return;
    const n = GROUPS.selectedIds.size;
    bar.classList.toggle('hidden', !n);
    const c = document.getElementById('groupsBulkCount'); if (c) c.textContent = String(n);
    const all = document.getElementById('groupsSelectAll');
    if (all) { const pageIds = GROUPS.rows.map(g => String(g.id)); all.checked = !!pageIds.length && pageIds.every(id => GROUPS.selectedIds.has(id)); }
  }
  function _groupsExportCsv(rows) {
    if (!_isSalesAdmin()) { toast('Export CSV is admin-only', 'error'); return; }
    if (!rows || !rows.length) { toast('Nothing to export', 'warning'); return; }
    const cols = _groupsVisibleColumns();
    const header = ['id', 'name', ...cols].map(c => `"${String(c).replace(/"/g, '""')}"`).join(',');
    const lines = rows.map(g => [g.id, g.name || '', ...cols.map(c => _groupValue(g, c))].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([header + '\n' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = el('a', { href: url, download: `groups-${new Date().toISOString().slice(0, 10)}.csv`, style: 'display:none;' });
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast(`Exported ${rows.length} groups`, 'success');
  }
  function _groupsEditableProps() {
    return (GROUPS.propsCatalog || []).filter(p => !p.readOnly && !p._virtual && p.name !== 'name');
  }
  function _openGroupsBulkEditModal() {
    const n = GROUPS.selectedIds.size;
    if (!n) return;
    document.getElementById('groupsBulkEditCount').textContent = String(n);
    const sel = document.getElementById('groupsBulkEditProp');
    clear(sel);
    for (const p of _groupsEditableProps()) sel.appendChild(el('option', { value: p.name, text: p.label || p.name }));
    document.getElementById('groupsBulkEditError')?.classList.add('hidden');
    _renderGroupsBulkEditValue();
    openModal('groupsBulkEditModal');
    refreshIcons();
  }
  function _renderGroupsBulkEditValue() {
    const wrap = document.getElementById('groupsBulkEditValueWrap');
    const propName = document.getElementById('groupsBulkEditProp')?.value;
    if (!wrap) return;
    clear(wrap);
    const def = GROUPS.propsByName[propName];
    wrap.appendChild(el('span', { text: 'New value', style: 'font-size:11px; font-weight:600; color:var(--text-secondary); text-transform:uppercase;' }));
    if (def?.options?.length) {
      const s = el('select', { id: 'groupsBulkEditValue', class: 'input' });
      s.appendChild(el('option', { value: '', text: '(clear)' }));
      for (const o of def.options) s.appendChild(el('option', { value: o.value, text: o.label || o.value }));
      wrap.appendChild(s);
    } else if (def && (def.type === 'date' || def.type === 'datetime')) {
      wrap.appendChild(el('input', { id: 'groupsBulkEditValue', type: 'date', class: 'input' }));
    } else if (_isLongTextField(def, propName) || propName === 'events_report') {
      wrap.appendChild(el('textarea', { id: 'groupsBulkEditValue', class: 'input', rows: '4' }));
    } else {
      wrap.appendChild(el('input', { id: 'groupsBulkEditValue', type: def?.type === 'number' ? 'number' : 'text', class: 'input' }));
    }
  }
  async function _groupsBulkEditApply() {
    const propName = document.getElementById('groupsBulkEditProp')?.value;
    const value = document.getElementById('groupsBulkEditValue')?.value ?? '';
    const ids = [...GROUPS.selectedIds];
    const err = document.getElementById('groupsBulkEditError');
    if (!propName || !ids.length) return;
    if (!confirm(`Set "${GROUPS.propsByName[propName]?.label || propName}" on ${ids.length} group${ids.length > 1 ? 's' : ''}?`)) return;
    const btn = document.getElementById('groupsBulkEditApplyBtn');
    btn.disabled = true;
    let ok = 0, failed = 0;
    for (const id of ids) {
      try {
        const r = await api('/api/hubspot/companies/' + encodeURIComponent(id), { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ properties: { [propName]: value } }) });
        if (!r.ok) { failed++; continue; }
        const j = await r.json().catch(() => ({}));
        const g = GROUPS.all.find(x => String(x.id) === String(id));
        if (g) { const props = j.properties || { [propName]: value }; g.properties = { ...(g.properties || {}), ...props }; for (const [k, v] of Object.entries(props)) if (k in g) g[k] = v; }
        ok++;
      } catch { failed++; }
    }
    btn.disabled = false;
    if (failed) { if (err) { err.textContent = `${ok} updated · ${failed} failed`; err.classList.remove('hidden'); } }
    else { closeModal('groupsBulkEditModal'); toast(`${ok} group${ok > 1 ? 's' : ''} updated`, 'success'); GROUPS.selectedIds.clear(); }
    _renderGroupsTable();
  }

  // ── Create group ──
  function _openCreateGroupModal() {
    const host = document.getElementById('createGroupFields');
    if (!host) return;
    clear(host);
    document.getElementById('createGroupError')?.classList.add('hidden');
    const fields = ['name', 'group_status', 'group_class_type', 'level_of_groups', 'schedule_days', 'schedule_time', 'lougha_teachers', 'date_of_start_of_classes', 'date_of_end'];
    for (const key of fields) {
      const def = GROUPS.propsByName[key] || { name: key, label: key, type: 'string', options: [] };
      const lab = el('label', { style: 'display:flex; flex-direction:column; gap:4px;' });
      lab.appendChild(el('span', { text: (def.label || key) + (key === 'name' ? ' *' : ''), style: 'font-size:11px; font-weight:600; color:var(--text-secondary); text-transform:uppercase;' }));
      let input;
      if (def.options?.length && def.fieldType !== 'checkbox' && def.fieldType !== 'multiplecheckbox') {
        input = el('select', { class: 'input', 'data-create-prop': key });
        input.appendChild(el('option', { value: '', text: '—' }));
        for (const o of def.options) input.appendChild(el('option', { value: o.value, text: o.label || o.value }));
      } else if (def.type === 'date' || def.type === 'datetime') {
        input = el('input', { type: 'date', class: 'input', 'data-create-prop': key });
      } else {
        input = el('input', { type: 'text', class: 'input', 'data-create-prop': key, placeholder: key === 'lougha_teachers' ? 'Teacher A; Teacher B' : '' });
      }
      lab.appendChild(input);
      host.appendChild(lab);
    }
    openModal('createGroupModal');
    refreshIcons();
    setTimeout(() => host.querySelector('[data-create-prop="name"]')?.focus(), 50);
  }
  async function _submitCreateGroup() {
    const host = document.getElementById('createGroupFields');
    const err = document.getElementById('createGroupError');
    const btn = document.getElementById('createGroupSubmitBtn');
    const properties = {};
    host.querySelectorAll('[data-create-prop]').forEach(n => { if (n.value !== '') properties[n.dataset.createProp] = n.value; });
    if (!properties.name) { if (err) { err.textContent = 'Name is required'; err.classList.remove('hidden'); } return; }
    btn.disabled = true;
    try {
      const r = await api('/api/hubspot/companies', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ properties }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      toast(`Group "${properties.name}" created`, 'success');
      closeModal('createGroupModal');
      await refreshGroups({ refetch: true });
    } catch (e) {
      if (err) { err.textContent = String(e.message || e); err.classList.remove('hidden'); }
    } finally { btn.disabled = false; }
  }

  // ── Groups filter helpers (kept from the 2026-07-13 support2 port) ──
  function _grpDateMs(v) {
    if (!v) return null;
    const n = Number(v);
    if (Number.isFinite(n) && n > 1e10) return n;
    const t = Date.parse(String(v));
    return Number.isFinite(t) ? t : null;
  }
  function _grpRowDateMs(g, field) {
    if (field === 'start') return _grpDateMs(g.date_of_start_of_classes);
    if (field === 'end') return _grpDateMs(g.date_of_end);
    return null;
  }
  // Preset → [startMs, endMs) — Monday-based weeks, LOCAL date components
  // (never toISOString: UTC+1 off-by-one). Same math as support2's bar.
  function _grpPresetRange(preset, fromVal, toVal) {
    // Rolling windows — "groups that started in the last N days" is the
    // question the seats views ask, and the shared preset list only has
    // calendar buckets (this week / this month / …).
    const rolling = /^last_(\d+)_days$/.exec(preset || '');
    if (rolling) {
      const today = MA.today();
      const from = new Date(today); from.setDate(from.getDate() - Number(rolling[1]));
      return { startMs: from.getTime(), endMs: today.getTime() + 86400000 };
    }
    if (preset === 'between') {
      const parse = (v) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || ''); return m ? new Date(+m[1], +m[2] - 1, +m[3]).getTime() : null; };
      const a = parse(fromVal), b = parse(toVal);
      if (a == null || b == null || b < a) return null;
      return { startMs: a, endMs: b + 86400000 };
    }
    return _wsDatePresetRange(preset);
  }
  function _grpFillTeacherOpts() {
    const dl = document.getElementById('grpTeacherOpts');
    if (!dl) return;
    if (document.activeElement === document.getElementById('grpFTeacher') && dl.children.length) return;
    const names = [...new Set((GROUPS.all || []).flatMap(g => g.teachers || []).map(t => String(t).trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const sig = names.join('|');
    if (dl._grpSig === sig) return;
    dl._grpSig = sig;
    dl.replaceChildren(...names.map(n => el('option', { value: n })));
  }
  const _GRP_CEFR = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  function _grpParseLevels(v) {
    return String(v || '').split(/[;,/]+/).map(x => x.trim().toUpperCase()).filter(Boolean);
  }
  function _grpSortLevels(levels) {
    return levels.slice().sort((a, b) => {
      const ia = _GRP_CEFR.indexOf(a), ib = _GRP_CEFR.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });
  }
  // Level quick-filter options (CEFR order, with counts over the current scope).
  function _grpRenderLevelOptions(base) {
    const sel = document.getElementById('groupsLevelFilter');
    if (!sel) return;
    const counts = new Map();
    for (const g of base) for (const lv of new Set(_grpParseLevels(g.level_of_groups))) counts.set(lv, (counts.get(lv) || 0) + 1);
    if (GROUPS.level !== 'all' && !counts.has(GROUPS.level)) counts.set(GROUPS.level, 0);
    const sig = [...counts.entries()].map(([k, n]) => k + ':' + n).join('|') + '#' + GROUPS.level;
    if (sel._grpSig === sig) return;
    sel._grpSig = sig;
    clear(sel);
    sel.appendChild(el('option', { value: 'all', text: 'Level' }));
    for (const lv of _grpSortLevels([...counts.keys()])) sel.appendChild(el('option', { value: lv, text: `${lv} · ${counts.get(lv)}` }));
    sel.value = GROUPS.level;
  }
  // Stat chips: scope + status shortcuts (GLOBAL counts, always shown) +
  // data-quality diagnostics over `items` (the filtered set). Tiles only
  // filter, never edit.
  const _GRP_KPIS = [
    { mode: 'scopeAll', label: 'All companies', icon: 'database', tone: 'accent' },
    { mode: 'status:In Process', label: 'In Process', icon: 'hourglass', tone: 'accent', countKey: 'in process' },
    { mode: 'status:active', label: 'Active', icon: 'zap', tone: 'success', countKey: 'active' },
    { mode: 'status:expandable', label: 'Expandable', icon: 'arrow-up-right', tone: 'success', countKey: 'expandable' },
    { mode: 'noSchedule', label: 'No schedule', icon: 'clock', tone: 'warning', test: g => !g.schedule_time || !g.schedule_days },
    { mode: 'noTeacher', label: 'No teacher', icon: 'user-x', tone: 'warning', test: g => !(g.teachers || []).length && !g.lougha_teachers },
    { mode: 'noDates', label: 'Dates missing', icon: 'calendar-off', tone: 'warning', test: g => !g.date_of_start_of_classes || !g.date_of_end },
  ];
  function _grpRenderKpis(items) {
    const wrap = document.getElementById('groupsKpis');
    if (!wrap) return;
    clear(wrap);
    const counts = GROUPS.statusCounts || {};
    for (const k of _GRP_KPIS) {
      const active = k.mode === 'scopeAll' ? (!GROUPS.status && !GROUPS.kpiMode)
        : k.mode.startsWith('status:') ? GROUPS.status === k.mode.slice(7)
        : GROUPS.kpiMode === k.mode;
      const num = k.mode === 'scopeAll' ? (GROUPS.totalGroups == null ? '…' : String(GROUPS.totalGroups))
        : k.countKey ? String(counts[k.countKey] ?? 0)
        : String(items.filter(k.test).length);
      const tile = el('button', {
        type: 'button', class: 'groups-stat-chip is-' + k.tone + (active ? ' is-active' : ''),
        'data-grp-kpi': k.mode, 'aria-pressed': active ? 'true' : 'false',
        title: k.mode === 'scopeAll' ? 'Every company in HubSpot' : k.countKey ? 'Filter by status' : 'Data quality: narrow to these groups',
      }, [
        el('i', { 'data-lucide': k.icon }),
        el('span', { class: 'groups-stat-num', text: num }),
        el('span', { class: 'groups-stat-label', text: k.label }),
      ]);
      wrap.appendChild(tile);
    }
    refreshIcons(wrap);
  }
  // Classify a group's class type from the HubSpot property first, then
  // fall back to a name regex (since the property isn't always set on
  // older rows). Returns 'individual' | 'kids' | 'adults'.
  function _groupClassTypeOf(g) {
    // The server already classifies every row (same precedence) and ships it
    // as class_kind — trust it so the seat caps and this filter can never
    // disagree. The local derivation stays as the fallback for cached rows
    // fetched before the field existed.
    if (g.class_kind) return g.class_kind;
    const raw = String(g.group_class_type || '').toLowerCase().trim();
    if (raw.includes('individual')) return 'individual';
    if (raw.includes('teen')) return 'teenagers';
    if (raw.includes('kids') || raw.includes('kid')) return 'kids';
    if (raw) return 'adults';
    const name = String(g.name || '').toLowerCase();
    if (/individual/.test(name)) return 'individual';
    if (/\bteens?\b|\bteenagers?\b/.test(name)) return 'teenagers';
    if (/\bkids?\b/.test(name)) return 'kids';
    return 'adults';
  }

  // ── Seats ───────────────────────────────────────────────────────────────
  // A group is full at 12 students (kids) / 15 (adults); individual classes
  // have no cap. The server sends seats_cap/seats_left computed from the same
  // rule — recompute locally only when they're missing (older cached payload).
  const _GRP_SEAT_CAPS = { kids: 12, teenagers: 15, adults: 15 };
  // "Open seats" only means something for a group that is still running or
  // still being assembled — closed/done/paused groups can't take a student
  // even when their head count is under the cap.
  const _GRP_LIVE_STATUSES = new Set(['active', 'expandable', 'in process', 'new']);
  function _grpSeats(g) {
    const kind = _groupClassTypeOf(g);
    const used = Number(g.student_count) || 0;
    const cap = g.seats_cap != null ? Number(g.seats_cap) : (_GRP_SEAT_CAPS[kind] ?? null);
    if (cap == null) return { kind, used, cap: null, left: null, live: false, open: false };
    const left = g.seats_left != null ? Number(g.seats_left) : Math.max(0, cap - used);
    const live = _GRP_LIVE_STATUSES.has(String(g.group_status || '').toLowerCase().trim());
    return { kind, used, cap, left, live, open: live && left > 0 };
  }
  // Quick-filter predicate. Individual classes and non-open groups fail BOTH
  // modes on purpose: neither "has room" nor "full" is a meaningful answer for
  // a one-student class or a group that is closed.
  function _grpSeatsMatch(g, mode) {
    if (!mode) return true;
    const s = _grpSeats(g);
    if (s.cap == null || !s.live) return false;
    return mode === 'open' ? s.left > 0 : mode === 'full' ? s.left <= 0 : true;
  }
  // Legacy alias kept for the students modal (re-render without refetch).
  function renderGroupsRows() { _renderGroupsTable(); }

  // ───────────────────────────────────────────────────────────────────
  // Influencers — Searchy's creators as a Contacts-style workspace
  // (Ayoub 2026-11-02 16:51: "ghir had shi li 3andi fi leads7 fi contact,
  // clonih lia m3a had plan dyal influencer"). Same shell and shared
  // machinery as Groups, driven by `_INFLUENCERS_WS`: saved views
  // (object=influencer), quick + advanced filters, grid with inline edit,
  // preview drawer, pager, bulk bar. Data = /api/influencer/prospects (the
  // whole list once, filtered client-side). Admin roles only.
  // Pipeline: 1 New · 2 Sent · 3 Seen · 4 Replied · 5 Agreed · 6 Started · No.
  // Bulk "Send template" opens the influencer Templates modal (Email or
  // Phone number = WhatsApp 661); nothing is ever sent without typing SEND N.
  // ───────────────────────────────────────────────────────────────────
  const INF_STAGES = [['new', '1 New'], ['contacted', '2 Sent'], ['seen', '3 Seen'], ['replied', '4 Replied'], ['agreed', '5 Agreed'], ['joined', '6 Started'], ['not_fit', 'No']];
  const INF_STAGE_LABEL = Object.fromEntries(INF_STAGES);
  const INF_RATINGS = [['very_good', '1 · Very good'], ['good', '2 · Good'], ['no', '3 · No']];
  const INF_PLATFORM_LABEL = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube', facebook: 'Facebook' };
  const INF_SEEN = { seen: 'Seen', delivered: 'Delivered · not seen', sent: 'Sent · not seen', sending: 'Sending…', failed: 'Failed' };
  const INFLUENCERS = {
    inited: false, loading: false,
    all: [], filtered: [], rows: [],
    total: 0, pageIndex: 1, pageSize: 25,
    sortKey: 'found_at', sortDir: 'desc',
    columns: [], propsCatalog: null, propsByName: {},
    views: [], activeView: null, defaultViewId: null, dirty: false, _baselineSig: '',
    advancedGroups: [],
    selectedIds: new Set(),
    stage: '', rating: '', platform: '', contact: '',
  };
  const _INF_DEFAULT_COLUMNS = ['rating', 'stage', 'seen', 'followers', 'niche', 'instagram', 'tiktok', 'youtube', 'email', 'phone', 'last_sent', 'found_at'];

  const _INFLUENCERS_WS = {
    key: 'influencers',
    objectType: 'influencer',
    get state() { return INFLUENCERS; },
    viewsApi: '/api/contacts-views',
    viewsQuery: '?object=influencer',
    viewsLabel: 'influencers',
    colWidthsKey: 'influencers_col_widths_v1',
    ids: {
      tabsScroll: 'influencersViewsTabsScroll', addViewBtn: 'influencersViewNewBtn', dirtyBtn: 'influencersViewSaveDirtyBtn',
      allViewsPopover: 'influencersAllViewsPopover', advSummary: 'influencersAdvSummary',
      advToggle: 'influencersAdvancedToggle', tableViewport: 'influencersTableViewport',
      rangeNote: 'influencersRangeNote', pageLabel: 'influencersPageLabel', pageNumbers: 'influencersPageNumbers',
      prevBtn: 'influencersPrevPageBtn', nextBtn: 'influencersNextPageBtn', loading: 'influencersLoading',
    },
    refresh: () => refreshInfluencers(),
    checkDirty: () => _infCheckDirty(),
    applyView: (v) => _infApplyView(v),
    loadViews: () => _infLoadViews(),
    switchView: (id) => _infSwitchView(id),
    saveDirty: () => _infSaveActiveViewDirty(),
    deleteView: (v) => _infDeleteView(v),
    signView: () => _infSignView(),
    updateDirtyPill: () => _infUpdateDirtyPill(),
    viewPayload: () => ({
      statusFilter: INFLUENCERS.stage || '',
      searchQuery: document.getElementById('influencersSearch')?.value || '',
      quickFilters: _infQuickFilters(),
    }),
    reorderColumn: (from, to, before) => _infReorderColumn(from, to, before),
    rerenderTable: () => _renderInfluencersTable(),
    loadCatalog: () => _infLoadPropsCatalog(),
    hideCatalogProp: (p) => p.name === 'name',
    goToPage: (n) => _infGoToPage(n),
  };

  function ensureInfluencersInit() {
    if (!isSalesAdminMe()) return;
    if (INFLUENCERS.inited) { refreshInfluencers({ refetch: true, silent: true }); return; }
    INFLUENCERS.inited = true;
    try {
      const storedSize = Number(localStorage.getItem('influencers_page_size_v1'));
      if ([25, 50, 100].includes(storedSize)) INFLUENCERS.pageSize = storedSize;
    } catch { /* optional */ }
    _wsRenderSkeleton(document.getElementById('influencersLoading'));
    _initInfluencersWiring();
    Promise.all([_infLoadPropsCatalog(), _infLoadViews()]).then(() => refreshInfluencers({ refetch: true }));
  }

  function _initInfluencersWiring() {
    const on = (id, ev, fn) => document.getElementById(id)?.addEventListener(ev, fn);
    on('influencersRefreshBtn', 'click', () => refreshInfluencers({ refetch: true }));
    on('infStageFilter', 'change', (e) => { INFLUENCERS.stage = e.target.value; _infFiltersChanged(); });
    on('infRatingFilter', 'change', (e) => { INFLUENCERS.rating = e.target.value; _infFiltersChanged(); });
    on('infPlatformFilter', 'change', (e) => { INFLUENCERS.platform = e.target.value; _infFiltersChanged(); });
    on('infContactFilter', 'change', (e) => { INFLUENCERS.contact = e.target.value; _infFiltersChanged(); });
    on('influencersClearFiltersBtn', 'click', _clearInfluencersFilters);
    on('influencersAddQuickFilterBtn', 'click', (ev) => _addInfluencersQuickFilter(ev.currentTarget));
    on('influencersAdvancedToggle', 'click', () => _openAdvPanel(_INFLUENCERS_WS));
    let qTimer = null;
    const searchEl = document.getElementById('influencersSearch');
    searchEl?.addEventListener('input', () => { clearTimeout(qTimer); qTimer = setTimeout(() => _infFiltersChanged(), 250); });
    searchEl?.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { clearTimeout(qTimer); _infFiltersChanged(); } });
    on('influencersEditColumnsBtn', 'click', () => openContactsColumnsModal(_INFLUENCERS_WS));
    on('influencersExportBtn', 'click', () => _infExportCsv(INFLUENCERS.filtered));
    on('influencersTemplatesBtn', 'click', () => openInfluencerTemplates('manage'));
    on('influencersActionsBtn', 'click', (ev) => _openInfluencersActionsMenu(ev.currentTarget));
    on('influencersObjectMenuBtn', 'click', (ev) => _openInfluencersObjectMenu(ev.currentTarget));
    on('influencersAllViewsBtn', 'click', (ev) => _openAllViewsPopover(ev.currentTarget, _INFLUENCERS_WS));
    on('influencersViewNewBtn', 'click', () => _openViewModal({ mode: 'new' }, _INFLUENCERS_WS));
    on('influencersViewSaveDirtyBtn', 'click', () => _infSaveActiveViewDirty());
    on('influencersPrevPageBtn', 'click', () => _infGoToPage(INFLUENCERS.pageIndex - 1));
    on('influencersNextPageBtn', 'click', () => _infGoToPage(INFLUENCERS.pageIndex + 1));
    const pageSizeEl = document.getElementById('influencersPageSize');
    if (pageSizeEl) {
      pageSizeEl.value = String(INFLUENCERS.pageSize);
      pageSizeEl.addEventListener('change', () => {
        INFLUENCERS.pageSize = Number(pageSizeEl.value) || 25;
        try { localStorage.setItem('influencers_page_size_v1', String(INFLUENCERS.pageSize)); } catch { /* optional */ }
        INFLUENCERS.pageIndex = 1;
        _renderInfluencersTable();
      });
    }
    // Bulk bar
    on('influencersBulkClear', 'click', () => { INFLUENCERS.selectedIds.clear(); _renderInfluencersTable(); });
    on('influencersBulkCancel', 'click', () => { INFLUENCERS.selectedIds.clear(); _renderInfluencersTable(); });
    on('influencersBulkSelectAll', 'click', () => { for (const p of INFLUENCERS.filtered) INFLUENCERS.selectedIds.add(String(p.id)); _renderInfluencersTable(); });
    on('influencersBulkSendBtn', 'click', () => openInfluencerTemplates('send'));
    on('influencersBulkExportBtn', 'click', () => _infExportCsv(INFLUENCERS.all.filter(p => INFLUENCERS.selectedIds.has(String(p.id)))));
    on('influencersBulkEditBtn', 'click', _openInfluencersBulkEditModal);
    on('influencersBulkEditApplyBtn', 'click', _infBulkEditApply);
    on('influencersBulkEditProp', 'change', _renderInfluencersBulkEditValue);
    _wireAdvPanelOnce();
    _wireViewModalOnce();
    _wireInfluencerTemplatesOnce();
  }

  // ── Property catalog (static: the prospect fields Searchy fills) ──
  function _infLoadPropsCatalog() {
    const opt = (pairs) => pairs.map(([value, label]) => ({ value, label }));
    const ro = { readOnly: true, _virtual: true, _filterable: true };
    const list = [
      { name: 'name', label: 'Name', type: 'string', fieldType: 'text', groupName: 'Creator', ...ro },
      { name: 'rating', label: 'Rating', type: 'enumeration', fieldType: 'select', groupName: 'Pipeline', options: opt(INF_RATINGS), _filterable: true },
      { name: 'stage', label: 'Our stage', type: 'enumeration', fieldType: 'select', groupName: 'Pipeline', options: opt(INF_STAGES), _filterable: true },
      { name: 'seen', label: 'Seen', type: 'enumeration', fieldType: 'select', groupName: 'Pipeline', options: opt(Object.entries(INF_SEEN)), ...ro },
      { name: 'last_sent', label: 'Last sent', type: 'datetime', fieldType: 'date', groupName: 'Pipeline', options: [], ...ro },
      { name: 'last_channel', label: 'Last channel', type: 'enumeration', fieldType: 'select', groupName: 'Pipeline', options: opt([['email', 'Email'], ['whatsapp', 'WhatsApp']]), ...ro },
      { name: 'note', label: 'Team note', type: 'string', fieldType: 'textarea', groupName: 'Pipeline', options: [], _filterable: true },
      { name: 'platform', label: 'Platform', type: 'enumeration', fieldType: 'select', groupName: 'Creator', options: opt(Object.entries(INF_PLATFORM_LABEL)), ...ro },
      { name: 'followers', label: 'Followers', type: 'number', fieldType: 'number', groupName: 'Creator', options: [], ...ro },
      { name: 'niche', label: 'Niche', type: 'string', fieldType: 'text', groupName: 'Creator', options: [], ...ro },
      { name: 'language', label: 'Language', type: 'enumeration', fieldType: 'select', groupName: 'Creator', options: opt([['darija', 'Darija'], ['arabic', 'Arabic'], ['french', 'French'], ['english', 'English'], ['mixed', 'Mixed']]), ...ro },
      { name: 'city', label: 'City', type: 'string', fieldType: 'text', groupName: 'Creator', options: [], ...ro },
      { name: 'audience', label: 'Audience', type: 'string', fieldType: 'text', groupName: 'Creator', options: [], ...ro },
      { name: 'instagram', label: 'Instagram', type: 'string', fieldType: 'text', groupName: 'Contacts', options: [], ...ro },
      { name: 'tiktok', label: 'TikTok', type: 'string', fieldType: 'text', groupName: 'Contacts', options: [], ...ro },
      { name: 'youtube', label: 'YouTube', type: 'string', fieldType: 'text', groupName: 'Contacts', options: [], ...ro },
      { name: 'facebook', label: 'Facebook', type: 'string', fieldType: 'text', groupName: 'Contacts', options: [], ...ro },
      { name: 'email', label: 'Email', type: 'string', fieldType: 'text', groupName: 'Contacts', options: [], ...ro },
      { name: 'phone', label: 'Phone / WhatsApp', type: 'string', fieldType: 'text', groupName: 'Contacts', options: [], ...ro },
      { name: 'link', label: 'Link in bio', type: 'string', fieldType: 'text', groupName: 'Contacts', options: [], ...ro },
      { name: 'score', label: 'Searchy score', type: 'number', fieldType: 'number', groupName: 'Searchy', options: [], ...ro },
      { name: 'why', label: 'Why (Searchy)', type: 'string', fieldType: 'text', groupName: 'Searchy', options: [], ...ro },
      { name: 'found_at', label: 'Found', type: 'datetime', fieldType: 'date', groupName: 'Searchy', options: [], ...ro },
    ];
    INFLUENCERS.propsCatalog = list;
    INFLUENCERS.propsByName = Object.fromEntries(list.map(p => [p.name, p]));
    return Promise.resolve();
  }

  // ── Saved views (object=influencer) ──
  async function _infLoadViews() {
    try {
      const r = await api('/api/contacts-views?object=influencer');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      INFLUENCERS.views = d.views || [];
      INFLUENCERS.defaultViewId = d.defaultId || null;
      const active = INFLUENCERS.views.find(v => v.id === INFLUENCERS.defaultViewId)
                  || INFLUENCERS.views.find(v => v._systemKey === 'all_influencers')
                  || INFLUENCERS.views[0]
                  || null;
      _infApplyView(active);
    } catch (err) {
      console.warn('[influencers] views load failed:', err);
      _infApplyView(null);
    }
  }
  function _infQuickFilters() {
    return { rating: INFLUENCERS.rating || '', platform: INFLUENCERS.platform || '', contact: INFLUENCERS.contact || '' };
  }
  function _infApplyView(view) {
    INFLUENCERS.activeView = view || null;
    INFLUENCERS.columns = view?.columns ? [...view.columns] : [..._INF_DEFAULT_COLUMNS];
    const qf = (view && view.quickFilters && typeof view.quickFilters === 'object') ? view.quickFilters : {};
    INFLUENCERS.stage = view?.statusFilter || '';
    INFLUENCERS.rating = String(qf.rating || '');
    INFLUENCERS.platform = String(qf.platform || '');
    INFLUENCERS.contact = String(qf.contact || '');
    const saved = Array.isArray(view?.advancedFilters) ? view.advancedFilters : [];
    INFLUENCERS.advancedGroups = saved
      .map(g => (Array.isArray(g) ? g : [g]).filter(f => f && typeof f === 'object' && f.field && f.op))
      .filter(g => g.length);
    INFLUENCERS.sortKey = view?.sortBy && view.sortBy !== 'createdate' ? view.sortBy : 'found_at';
    INFLUENCERS.sortDir = view?.sortDir === 'asc' ? 'asc' : 'desc';
    _infHydrateFilterUi(view?.searchQuery || '');
    _renderAdvGroups(_INFLUENCERS_WS);
    _renderAdvSummary(_INFLUENCERS_WS);
    INFLUENCERS._baselineSig = _infSignView();
    INFLUENCERS.dirty = false;
    INFLUENCERS.pageIndex = 1;
    INFLUENCERS.selectedIds.clear();
    _renderViewsTabs(_INFLUENCERS_WS);
    _infUpdateDirtyPill();
  }
  function _infHydrateFilterUi(searchQuery) {
    const set = (id, v) => { const n = document.getElementById(id); if (n) n.value = v; };
    set('infStageFilter', INFLUENCERS.stage);
    set('infRatingFilter', INFLUENCERS.rating);
    set('infPlatformFilter', INFLUENCERS.platform);
    set('infContactFilter', INFLUENCERS.contact);
    set('influencersSearch', searchQuery || '');
    _infSyncQuickFilterUi();
  }
  function _infSyncQuickFilterUi() {
    const mark = (id, active) => document.getElementById(id)?.closest('.contacts-quick-select')?.classList.toggle('is-active', !!active);
    mark('infStageFilter', INFLUENCERS.stage);
    mark('infRatingFilter', INFLUENCERS.rating);
    mark('infPlatformFilter', INFLUENCERS.platform);
    mark('infContactFilter', INFLUENCERS.contact);
    const any = INFLUENCERS.stage || INFLUENCERS.rating || INFLUENCERS.platform || INFLUENCERS.contact
      || (INFLUENCERS.advancedGroups || []).some(g => g.some(f => f.field))
      || (document.getElementById('influencersSearch')?.value || '').trim();
    document.getElementById('influencersClearFiltersBtn')?.classList.toggle('is-visible', !!any);
  }
  function _infSignView() {
    if (!INFLUENCERS.activeView) return '';
    const adv = (INFLUENCERS.advancedGroups || [])
      .map(g => (Array.isArray(g) ? g : []).filter(f => f && f.field && f.op).map(f => ({ field: f.field, op: f.op, value: f.value ?? '' })));
    return JSON.stringify({
      cols: INFLUENCERS.columns, frozen: INFLUENCERS.activeView.frozenCount || 0,
      statusFilter: INFLUENCERS.stage || '', searchQuery: document.getElementById('influencersSearch')?.value || '',
      quickFilters: _infQuickFilters(), advancedGroups: adv,
    });
  }
  function _infCheckDirty() {
    const was = INFLUENCERS.dirty;
    INFLUENCERS.dirty = _infSignView() !== INFLUENCERS._baselineSig;
    if (was !== INFLUENCERS.dirty) _infUpdateDirtyPill();
  }
  function _infUpdateDirtyPill() {
    document.getElementById('influencersViewSaveDirtyBtn')?.classList.toggle('hidden', !INFLUENCERS.dirty || !INFLUENCERS.activeView);
  }
  async function _infSwitchView(viewId) {
    const v = INFLUENCERS.views.find(x => x.id === viewId);
    if (!v) return;
    _closeContactsFloatingUi();
    _closeContactInlineEditor();
    _infApplyView(v);
    _renderInfluencersTable();
  }
  async function _infDeleteView(view) {
    if (!confirm(`Delete view "${view.name}"? This can't be undone.`)) return;
    const r = await api('/api/contacts-views/' + view.id, { method: 'DELETE' });
    if (!r.ok) { toast('Échec de la suppression — réessayez', 'error'); return; }
    toast('View deleted', 'success');
    await _infLoadViews();
    _renderInfluencersTable();
  }
  async function _infSaveActiveViewDirty() {
    if (!INFLUENCERS.activeView) return;
    const patch = {
      columns: INFLUENCERS.columns,
      ..._INFLUENCERS_WS.viewPayload(),
      sortBy: INFLUENCERS.sortKey, sortDir: INFLUENCERS.sortDir,
      advancedFilters: (INFLUENCERS.advancedGroups || []).map(g => (Array.isArray(g) ? g : []).filter(f => f && f.field && f.op)).filter(g => g.length),
    };
    try {
      const r = await api('/api/contacts-views/' + INFLUENCERS.activeView.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); toast('Save failed: ' + (j.error || ('HTTP ' + r.status)), 'error'); return; }
      const d = await r.json();
      INFLUENCERS.activeView = d.view;
      Object.assign(INFLUENCERS.views.find(v => v.id === d.view.id) || {}, d.view);
      INFLUENCERS._baselineSig = _infSignView();
      INFLUENCERS.dirty = false;
      _infUpdateDirtyPill();
      toast('View saved', 'success');
    } catch (err) { toast('Save failed: ' + (err.message || 'network error'), 'error'); }
  }

  // ── Header menus ──
  function _openInfluencersActionsMenu(anchorEl) {
    const existing = document.getElementById('influencersActionsMenu');
    _closeContactsFloatingUi();
    if (existing) return;
    const menu = el('div', { id: 'influencersActionsMenu', class: 'contacts-actions-menu' });
    menu.appendChild(_contactsMenuButton('refresh-cw', 'Refresh influencers', () => refreshInfluencers({ refetch: true })));
    menu.appendChild(_contactsMenuButton('message-square-text', 'Templates', () => openInfluencerTemplates('manage')));
    menu.appendChild(_contactsMenuButton('download', 'Export current view', () => _infExportCsv(INFLUENCERS.filtered)));
    menu.appendChild(_contactsMenuButton('columns-3', 'Edit columns', () => openContactsColumnsModal(_INFLUENCERS_WS)));
    menu.appendChild(_contactsMenuButton('plus', 'Create saved view', () => _openViewModal({ mode: 'new' }, _INFLUENCERS_WS)));
    document.body.appendChild(menu);
    _placeContactsPopover(menu, anchorEl, { align: 'right', width: 215 });
    refreshIcons();
    _closeFloatingOnOutside(menu, anchorEl);
  }
  function _openInfluencersObjectMenu(anchorEl) {
    const existing = document.getElementById('influencersObjectMenu');
    _closeContactsFloatingUi();
    if (existing) return;
    const menu = el('div', { id: 'influencersObjectMenu', class: 'contacts-actions-menu contacts-object-menu' });
    const go = (key) => { const v = INFLUENCERS.views.find(x => x._systemKey === key); if (v) _infSwitchView(v.id); };
    menu.appendChild(_contactsMenuButton('users', 'All influencers', () => go('all_influencers')));
    menu.appendChild(_contactsMenuButton('star', 'To rate', () => go('influencers_to_rate')));
    menu.appendChild(_contactsMenuButton('send', 'Not sent', () => go('influencers_not_sent')));
    menu.appendChild(_contactsMenuButton('reply', 'Replied', () => go('influencers_replied')));
    document.body.appendChild(menu);
    _placeContactsPopover(menu, anchorEl, { width: 190 });
    refreshIcons();
    _closeFloatingOnOutside(menu, anchorEl);
  }
  function _addInfluencersQuickFilter(anchorEl) {
    _openPropPicker(anchorEl, (chosen) => {
      if (!INFLUENCERS.advancedGroups.length) INFLUENCERS.advancedGroups = [[]];
      const def = INFLUENCERS.propsByName[chosen];
      const op = _opsForField(def)[0] || 'equals';
      if (!INFLUENCERS.advancedGroups.flat().find(f => f.field === chosen)) INFLUENCERS.advancedGroups[0].push({ field: chosen, op, value: '' });
      _renderAdvGroups(_INFLUENCERS_WS);
      _renderAdvSummary(_INFLUENCERS_WS);
      _infCheckDirty();
      _openAdvPanel(_INFLUENCERS_WS);
    }, INFLUENCERS.propsCatalog);
  }
  function _clearInfluencersFilters() {
    Object.assign(INFLUENCERS, { stage: '', rating: '', platform: '', contact: '', advancedGroups: [] });
    _infHydrateFilterUi('');
    _renderAdvGroups(_INFLUENCERS_WS);
    _renderAdvSummary(_INFLUENCERS_WS);
    _infFiltersChanged();
  }
  function _infFiltersChanged() {
    INFLUENCERS.pageIndex = 1;
    _infCheckDirty();
    _renderInfluencersTable();
  }

  // ── Data ──
  async function refreshInfluencers({ refetch = false, silent = false } = {}) {
    if (!INFLUENCERS.inited) return;
    if (refetch || !INFLUENCERS.all.length) {
      const loading = document.getElementById('influencersLoading');
      if (!silent) {
        INFLUENCERS.loading = true;
        document.getElementById('influencersTable')?.classList.add('hidden');
        document.getElementById('influencersEmpty')?.classList.add('hidden');
        loading?.classList.remove('hidden');
        _wsRenderSkeleton(loading);
        _wsRenderPager(_INFLUENCERS_WS);
      }
      try {
        const r = await api('/api/influencer/prospects');
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const j = await r.json();
        INFLUENCERS.all = j.prospects || [];
      } catch (err) {
        INFLUENCERS.loading = false;
        if (silent) return;
        loading?.classList.add('hidden');
        toast('Failed to load influencers: ' + (err.message || err), 'error');
        return;
      }
      INFLUENCERS.loading = false;
    }
    _renderInfluencersTable();
  }

  // Last message we sent and where it is now (email: opened · WhatsApp: read).
  function _infLastSend(p) {
    const sends = Array.isArray(p.sends) ? p.sends : [];
    return sends.length ? sends[sends.length - 1] : null;
  }
  function _infSeenOf(p) {
    const s = _infLastSend(p);
    if (!s) return '';
    const st = String(s.status || '');
    if (st === 'opened' || st === 'read' || st === 'clicked') return 'seen';
    if (st === 'delivered') return 'delivered';
    if (st === 'sent') return 'sent';
    if (st === 'sending' || st === 'unknown') return 'sending';
    if (st === 'failed' || st === 'bounced') return 'failed';
    return '';
  }
  function _infLinkOf(p, platform) {
    return (p.links && p.links[platform]) || (p.platform === platform ? p.url : '') || '';
  }
  function _infValue(p, key) {
    switch (key) {
      case 'name': return p.name || '@' + (p.handle || '');
      case 'stage': return p.status || '';
      case 'rating': return p.rating || '';
      case 'seen': return _infSeenOf(p);
      case 'last_sent': return _infLastSend(p)?.at || '';
      case 'last_channel': return _infLastSend(p)?.channel || '';
      case 'instagram': case 'tiktok': case 'youtube': case 'facebook': return _infLinkOf(p, key);
      case 'phone': return p.whatsapp || p.phone || '';
      case 'found_at': return p.foundAt || '';
      case 'followers': case 'score': return p[key] == null ? '' : p[key];
      default: return p[key] == null ? '' : p[key];
    }
  }

  // Filter pipeline (client-side): search → stage → rating → platform → contact → advanced groups.
  function _infFilteredRows() {
    const q = (document.getElementById('influencersSearch')?.value || '').trim().toLowerCase();
    const digits = q.replace(/\D/g, '');
    const rows = INFLUENCERS.all.filter(p => {
      if (q) {
        const hay = [p.name, p.handle, p.email, p.niche, p.city].map(x => String(x || '').toLowerCase()).join(' ');
        const phoneHit = digits.length >= 4 && [p.whatsapp, p.phone].some(x => String(x || '').includes(digits));
        if (!hay.includes(q) && !phoneHit) return false;
      }
      if (INFLUENCERS.stage && (p.status || '') !== INFLUENCERS.stage) return false;
      const r = INFLUENCERS.rating;
      if (r === 'review' && p.rating) return false;
      if (r === 'ok' && p.rating !== 'very_good' && p.rating !== 'good') return false;
      if (r && r !== 'review' && r !== 'ok' && p.rating !== r) return false;
      if (INFLUENCERS.platform && p.platform !== INFLUENCERS.platform && !_infLinkOf(p, INFLUENCERS.platform)) return false;
      if (INFLUENCERS.contact === 'email' && !p.email) return false;
      if (INFLUENCERS.contact === 'phone' && !(p.whatsapp || p.phone)) return false;
      if (INFLUENCERS.contact === 'none' && (p.email || p.whatsapp || p.phone)) return false;
      return true;
    });
    const groups = (INFLUENCERS.advancedGroups || []).map(g => g.filter(f => f && f.field && f.op)).filter(g => g.length);
    const adv = groups.length ? rows.filter(p => _wsRowMatchesAdvGroups(p, groups, INFLUENCERS.propsByName, _infValue)) : rows;
    return _infSortRows(adv);
  }
  function _infSortRows(rows) {
    const key = INFLUENCERS.sortKey || 'found_at';
    const dir = INFLUENCERS.sortDir === 'desc' ? -1 : 1;
    const def = INFLUENCERS.propsByName[key];
    const isNum = def?.type === 'number';
    const isDate = def && (def.type === 'date' || def.type === 'datetime');
    const stageRank = Object.fromEntries(INF_STAGES.map(([k], i) => [k, i]));
    const ratingRank = { very_good: 0, good: 1, no: 2 };
    return rows.slice().sort((a, b) => {
      let va = _infValue(a, key), vb = _infValue(b, key);
      if (key === 'stage') return ((stageRank[va] ?? 9) - (stageRank[vb] ?? 9)) * dir;
      if (key === 'rating') return ((ratingRank[va] ?? 3) - (ratingRank[vb] ?? 3)) * dir;
      if (isNum) { va = va === '' ? -1 : Number(va); vb = vb === '' ? -1 : Number(vb); return (va - vb) * dir; }
      if (isDate) { va = _grpDateMs(va) || 0; vb = _grpDateMs(vb) || 0; return (va - vb) * dir; }
      return String(va || '').localeCompare(String(vb || ''), undefined, { sensitivity: 'base', numeric: true }) * dir;
    });
  }
  function _toggleInfluencersSort(key) {
    if (INFLUENCERS.sortKey === key) INFLUENCERS.sortDir = INFLUENCERS.sortDir === 'asc' ? 'desc' : 'asc';
    else { INFLUENCERS.sortKey = key; INFLUENCERS.sortDir = 'asc'; }
    INFLUENCERS.pageIndex = 1;
    _renderInfluencersTable();
  }
  function _infGoToPage(n) {
    const totalPages = Math.max(1, Math.ceil(INFLUENCERS.total / (INFLUENCERS.pageSize || 25)));
    if (n < 1 || n > totalPages || n === INFLUENCERS.pageIndex) return;
    INFLUENCERS.pageIndex = n;
    _renderInfluencersTable();
    document.getElementById('influencersTableViewport')?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }

  // ── Grid ──
  function _infVisibleColumns() { return (INFLUENCERS.columns || []).filter(c => c && c !== 'name'); }
  function _infReorderColumn(fromKey, toKey, insertBefore) {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const cols = (INFLUENCERS.columns || []).slice();
    const fromIdx = cols.indexOf(fromKey);
    if (fromIdx === -1) return;
    cols.splice(fromIdx, 1);
    let toIdx = cols.indexOf(toKey);
    if (toIdx === -1) cols.push(fromKey); else { if (!insertBefore) toIdx += 1; cols.splice(toIdx, 0, fromKey); }
    INFLUENCERS.columns = cols;
    _renderInfluencersTable();
    _infCheckDirty();
  }
  function _renderInfluencersHead() {
    const thead = document.getElementById('influencersThead');
    if (!thead) return;
    clear(thead);
    const fillHeader = (th, label, sortKey) => {
      th.setAttribute('scope', 'col');
      clear(th);
      const inner = el('div', { class: 'contacts-th-inner' });
      inner.appendChild(el('span', { class: 'contacts-th-label', text: label }));
      if (sortKey) {
        const active = INFLUENCERS.sortKey === sortKey;
        th.setAttribute('aria-sort', active ? (INFLUENCERS.sortDir === 'asc' ? 'ascending' : 'descending') : 'none');
        const sortBtn = el('button', {
          type: 'button', class: 'contacts-sort-btn' + (active ? ' is-active' : ''),
          title: active ? `Sorted ${INFLUENCERS.sortDir === 'asc' ? 'ascending' : 'descending'}` : `Sort by ${label}`, 'aria-label': `Sort by ${label}`,
        }, [el('i', { 'data-lucide': !active ? 'arrow-up-down' : (INFLUENCERS.sortDir === 'asc' ? 'arrow-up' : 'arrow-down') })]);
        sortBtn.addEventListener('mousedown', ev => ev.stopPropagation());
        sortBtn.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _toggleInfluencersSort(sortKey); });
        inner.appendChild(sortBtn);
      }
      th.appendChild(inner);
    };
    const frozenN = INFLUENCERS.activeView?.frozenCount || 0;
    const tr = el('tr', {});
    tr.appendChild(el('th', { class: 'frozen', scope: 'col', style: 'left:0px; min-width:36px;' }, [el('input', { type: 'checkbox', id: 'influencersSelectAll', 'aria-label': 'Select all influencers on this page' })]));
    const thName = el('th', { class: 'frozen', style: 'left:36px; min-width:220px;' });
    fillHeader(thName, 'Name', 'name');
    tr.appendChild(thName);
    _attachColResize(thName, 'name', _INFLUENCERS_WS);
    let leftOffset = 36 + 220;
    _infVisibleColumns().forEach((propName, i) => {
      const def = INFLUENCERS.propsByName[propName] || { name: propName, label: propName };
      const isFrozen = i < frozenN;
      const th = el('th', { class: isFrozen ? 'frozen' : '', style: isFrozen ? `left:${leftOffset}px;` : '' });
      fillHeader(th, def.label || propName, propName);
      tr.appendChild(th);
      _attachColResize(th, propName, _INFLUENCERS_WS);
      _attachColReorder(th, propName, _INFLUENCERS_WS);
      if (isFrozen) leftOffset += 160;
    });
    thead.appendChild(tr);
    refreshIcons();
    document.getElementById('influencersSelectAll')?.addEventListener('change', (ev) => {
      for (const p of INFLUENCERS.rows) { if (ev.target.checked) INFLUENCERS.selectedIds.add(String(p.id)); else INFLUENCERS.selectedIds.delete(String(p.id)); }
      _renderInfluencersTable();
    });
  }

  function _renderInfluencersTable() {
    const table = document.getElementById('influencersTable');
    const tbody = document.getElementById('influencersTbody');
    const empty = document.getElementById('influencersEmpty');
    const loading = document.getElementById('influencersLoading');
    if (!tbody) return;
    _infSyncQuickFilterUi();
    INFLUENCERS.filtered = _infFilteredRows();
    INFLUENCERS.total = INFLUENCERS.filtered.length;
    const totalPages = Math.max(1, Math.ceil(INFLUENCERS.total / (INFLUENCERS.pageSize || 25)));
    if (INFLUENCERS.pageIndex > totalPages) INFLUENCERS.pageIndex = totalPages;
    const start = (INFLUENCERS.pageIndex - 1) * INFLUENCERS.pageSize;
    INFLUENCERS.rows = INFLUENCERS.filtered.slice(start, start + INFLUENCERS.pageSize);
    const count = document.getElementById('influencersCount');
    if (count) {
      const totalAll = INFLUENCERS.all.length;
      count.textContent = totalAll && INFLUENCERS.total !== totalAll
        ? `${INFLUENCERS.total.toLocaleString()} of ${totalAll.toLocaleString()} influencers`
        : `${INFLUENCERS.total.toLocaleString()} record${INFLUENCERS.total === 1 ? '' : 's'}`;
    }
    if (INFLUENCERS.loading) return;
    loading?.classList.add('hidden');
    _renderInfluencersHead();
    clear(tbody);
    if (!INFLUENCERS.rows.length) {
      table?.classList.add('hidden');
      empty?.classList.remove('hidden');
    } else {
      empty?.classList.add('hidden');
      table?.classList.remove('hidden');
      const frozenN = INFLUENCERS.activeView?.frozenCount || 0;
      const cols = _infVisibleColumns();
      for (const p of INFLUENCERS.rows) {
        const selected = INFLUENCERS.selectedIds.has(String(p.id));
        const tr = el('tr', { class: selected ? 'is-selected' : '', 'data-influencer-id': p.id });
        const cb = el('input', { type: 'checkbox', class: 'contact-row-check', 'aria-label': `Select ${_infValue(p, 'name')}`, ...(selected ? { checked: 'checked' } : {}) });
        cb.addEventListener('change', () => { if (cb.checked) INFLUENCERS.selectedIds.add(String(p.id)); else INFLUENCERS.selectedIds.delete(String(p.id)); tr.classList.toggle('is-selected', cb.checked); _renderInfluencersBulkBar(); });
        tr.appendChild(el('td', { class: 'frozen', style: 'left:0px;' }, [cb]));
        tr.appendChild(_buildInfluencerNameCell(p));
        let leftOffset = 36 + 220;
        cols.forEach((propName, i) => {
          const cell = _buildInfluencerCell(p, propName);
          if (i < frozenN) { cell.classList.add('frozen'); cell.style.left = leftOffset + 'px'; leftOffset += 160; }
          tr.appendChild(cell);
        });
        tr.querySelectorAll('td').forEach((cell, index) => {
          if (index === 0) return;
          cell.addEventListener('click', (ev) => {
            document.querySelectorAll('#influencersTbody td.is-active-cell').forEach(td => td.classList.remove('is-active-cell'));
            cell.classList.add('is-active-cell');
            if (typeof cell._contactsOpenEditor !== 'function') return;
            if (ev.target.closest('button, a, input, select, textarea, label')) return;
            cell._contactsOpenEditor();
          });
        });
        tbody.appendChild(tr);
      }
    }
    _renderInfluencersBulkBar();
    _wsRenderPager(_INFLUENCERS_WS);
    refreshIcons();
  }

  function _infInitials(name) {
    return String(name || '').replace(/^@/, '').trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
  }
  function _buildInfluencerNameCell(p) {
    const name = _infValue(p, 'name');
    const td = el('td', { class: 'contact-name-cell frozen', style: 'left:36px;', title: 'Open preview' });
    const nameLink = el('button', { type: 'button', class: 'contact-name-link', text: name, title: `Preview ${name}` });
    nameLink.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _openInfluencerPreviewDrawer(p); });
    const previewBtn = el('button', { type: 'button', class: 'contact-preview-btn', title: 'Preview', 'aria-label': `Preview ${name}` }, [el('i', { 'data-lucide': 'panel-right-open' })]);
    previewBtn.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); _openInfluencerPreviewDrawer(p); });
    td.appendChild(el('div', { class: 'contact-name-wrap' }, [
      el('span', { class: 'contact-name-avatar', text: _infInitials(name), 'aria-hidden': 'true' }),
      nameLink,
      el('span', { class: 'inf-platform-pill inf-platform-' + (p.platform || 'other'), text: INF_PLATFORM_LABEL[p.platform] || p.platform || '' }),
      previewBtn,
    ]));
    return td;
  }
  function _infDateText(v) {
    const ms = _grpDateMs(v);
    if (ms == null) return '';
    return MA.loc(new Date(ms), 'd', 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
  function _infHandleOf(url) {
    const m = String(url || '').match(/\.com\/@?([^/?#]+)/);
    return m ? '@' + m[1] : url;
  }
  function _infSafeHttps(url) {
    try { const u = new URL(String(url || '')); return u.protocol === 'https:' ? u.toString() : ''; } catch { return ''; }
  }
  function _buildInfluencerCell(p, propName) {
    const def = INFLUENCERS.propsByName[propName] || { name: propName, label: propName, type: 'string', options: [] };
    const td = el('td');
    const raw = _infValue(p, propName);
    if (propName === 'rating') {
      const label = { very_good: '★★ Very good', good: '★ Good', no: 'No' }[raw] || '';
      const pill = el('button', { type: 'button', class: 'contact-pill inf-rating-pill' + (raw ? ' is-rating-' + raw : ' contact-pill-empty'), text: label, title: 'Rate: 1 Very good · 2 Good · 3 No' });
      pill.addEventListener('click', (ev) => { ev.stopPropagation(); _openContactEditor(pill, p, def, raw, { onSave: _saveInfluencerEdit }); });
      td.appendChild(pill);
      td._contactsOpenEditor = () => pill.click();
      return td;
    }
    if (propName === 'stage') {
      const pill = el('button', { type: 'button', class: 'contact-pill inf-stage-pill is-istage-' + (raw || 'new'), text: INF_STAGE_LABEL[raw] || raw || '', title: p.stageBy && String(p.stageBy).startsWith('auto:') ? 'Moved automatically (' + String(p.stageBy).slice(5) + ')' : 'Change stage' });
      pill.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (p.rating === 'no') { toast('Rated "No": change the rating first.', 'warning'); return; }
        _openContactEditor(pill, p, def, raw, { onSave: _saveInfluencerEdit });
      });
      td.appendChild(pill);
      td._contactsOpenEditor = () => pill.click();
      return td;
    }
    if (propName === 'seen') {
      if (raw) td.appendChild(el('span', { class: 'inf-seen inf-seen-' + raw, text: (raw === 'seen' ? '✓✓ ' : raw === 'failed' ? '⚠ ' : '✓ ') + (INF_SEEN[raw] || raw), title: _infLastSend(p)?.channel === 'whatsapp' ? 'WhatsApp read receipt' : 'Email opened (some apps hide opens)' }));
      return td;
    }
    if (['instagram', 'tiktok', 'youtube', 'facebook', 'link'].includes(propName)) {
      const href = _infSafeHttps(raw);
      if (href) td.appendChild(el('a', { href, target: '_blank', rel: 'noopener noreferrer', class: 'inf-link', text: propName === 'link' ? (new URL(href).hostname.replace(/^www\./, '')) : _infHandleOf(href), title: href }));
      return td;
    }
    if (propName === 'email') {
      if (raw) td.appendChild(el('a', { href: 'mailto:' + raw, class: 'inf-link', text: raw }));
      return td;
    }
    if (propName === 'phone') {
      if (raw) td.appendChild(el('span', { class: 'inf-phone', text: '+' + raw }));
      return td;
    }
    if (propName === 'followers') { td.appendChild(document.createTextNode(raw === '' ? '' : _searchyFollowers(Number(raw)))); return td; }
    if (propName === 'last_sent' || propName === 'found_at') { td.appendChild(document.createTextNode(_infDateText(raw))); return td; }
    if (propName === 'note') {
      const pill = el('button', { type: 'button', class: 'contact-pill' + (raw ? '' : ' contact-pill-empty'), text: raw ? String(raw).slice(0, 60) : '' });
      pill.addEventListener('click', (ev) => { ev.stopPropagation(); _openLongTextEditor(p, def, 'note', raw, { onSave: _saveInfluencerEdit, subtitle: _infValue(p, 'name') }); });
      td.appendChild(pill);
      td._contactsOpenEditor = () => pill.click();
      return td;
    }
    const display = def.options?.length ? (def.options.find(o => String(o.value) === String(raw))?.label || raw) : raw;
    td.appendChild(document.createTextNode(display == null ? '' : String(display)));
    return td;
  }

  // Inline edit → the prospects routes (rating → /rate, stage / note → /update).
  async function _saveInfluencerEdit(p, def, value) {
    _closeContactInlineEditor();
    let path = '/api/influencer/prospects/update', body = { id: p.id };
    if (def.name === 'rating') {
      if (!value) { toast('A rating cannot be cleared: choose Very good, Good or No.', 'warning'); return false; }
      path = '/api/influencer/prospects/rate'; body.rating = value;
    } else if (def.name === 'stage') body.status = value;
    else if (def.name === 'note') body.note = value;
    else return false;
    try {
      const r = await api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast(j.error === 'do_not_contact' ? 'Rated "No": change the rating first.' : `Update failed: ${j.error || r.status}`, 'error');
        return false;
      }
      if (j.prospect) Object.assign(p, j.prospect);
      toast(`${def.label || def.name} updated`, 'success');
      _renderInfluencersTable();
      const drawer = document.getElementById('influencersPreviewDrawer');
      if (drawer && drawer._infId === String(p.id)) _openInfluencerPreviewDrawer(p);
      return true;
    } catch (err) {
      toast('Update failed: ' + (err.message || err), 'error');
      return false;
    }
  }

  // ── Preview drawer ──
  function _openInfluencerPreviewDrawer(p) {
    document.getElementById('influencersPreviewDrawer')?.remove();
    const name = _infValue(p, 'name');
    const drawer = el('aside', { id: 'influencersPreviewDrawer', class: 'contacts-preview-drawer', role: 'dialog', 'aria-modal': 'false', 'aria-label': `Influencer preview: ${name}` });
    drawer._infId = String(p.id);
    const head = el('div', { class: 'contacts-preview-head' });
    const close = el('button', { type: 'button', class: 'contacts-preview-close', title: 'Close preview', 'aria-label': 'Close preview' }, [el('i', { 'data-lucide': 'x' })]);
    let onKey = null;
    const closeDrawer = () => { _closeContactInlineEditor(); drawer.remove(); if (onKey) document.removeEventListener('keydown', onKey); };
    close.addEventListener('click', closeDrawer);
    head.appendChild(el('div', { class: 'contacts-preview-titlebar' }, [el('h2', { text: name }), close]));
    head.appendChild(el('div', { class: 'contacts-preview-nav' }, [el('span', { class: 'contacts-preview-nav-active', text: 'View record' })]));
    const subline = [INF_PLATFORM_LABEL[p.platform] || p.platform, '@' + (p.handle || ''), p.followers != null ? _searchyFollowers(p.followers) + ' followers' : '', p.niche].filter(Boolean).join(' · ');
    head.appendChild(el('div', { class: 'contacts-preview-person' }, [
      el('span', { class: 'contacts-preview-avatar', text: _infInitials(name), 'aria-hidden': 'true' }),
      el('div', { class: 'contacts-preview-person-copy' }, [el('strong', { text: name }), el('span', { text: subline })]),
    ]));
    const actions = el('div', { class: 'contacts-preview-actions' });
    const action = (icon, label, fn) => { const b = el('button', { type: 'button', class: 'contacts-preview-action', title: label }, [el('span', {}, [el('i', { 'data-lucide': icon })]), el('span', { text: label })]); b.addEventListener('click', fn); actions.appendChild(b); return b; };
    const profile = _infSafeHttps(p.url);
    if (profile) action('external-link', 'Profile', () => window.open(profile, '_blank', 'noopener,noreferrer'));
    action('send', 'Send template', () => { INFLUENCERS.selectedIds.clear(); INFLUENCERS.selectedIds.add(String(p.id)); _renderInfluencersTable(); openInfluencerTemplates('send'); });
    action('notebook-pen', 'Note', () => _openLongTextEditor(p, INFLUENCERS.propsByName.note, 'note', p.note || '', { onSave: _saveInfluencerEdit, subtitle: name }));
    head.appendChild(actions);
    drawer.appendChild(head);

    const body = el('div', { class: 'contacts-preview-body' });
    // Pipeline: one click per step (a "No" stays locked until it is re-rated).
    const steps = el('div', { class: 'inf-steps' });
    for (const [key, label] of INF_STAGES) {
      const b = el('button', { type: 'button', class: 'inf-step is-istage-' + key + (p.status === key ? ' is-current' : ''), text: label });
      b.disabled = p.rating === 'no' && key !== 'not_fit';
      b.addEventListener('click', () => { if (p.status !== key) _saveInfluencerEdit(p, INFLUENCERS.propsByName.stage, key); });
      steps.appendChild(b);
    }
    const auto = p.stageBy && String(p.stageBy).startsWith('auto:') ? `Moved automatically (${String(p.stageBy).slice(5)})` : (p.stageBy ? 'Set by ' + p.stageBy : '');
    body.appendChild(el('details', { class: 'contacts-preview-section', open: 'open' }, [
      el('summary', { text: 'Pipeline' }), steps, auto ? el('div', { class: 'inf-steps-note', text: auto + (p.stageAt ? ' · ' + _infDateText(p.stageAt) : '') }) : null,
    ].filter(Boolean)));
    // Contacts
    const contacts = el('div', { class: 'inf-contacts' });
    for (const k of ['instagram', 'tiktok', 'youtube', 'facebook']) {
      const href = _infSafeHttps(_infLinkOf(p, k));
      if (href) contacts.appendChild(el('a', { class: 'calls-action', href, target: '_blank', rel: 'noopener noreferrer' }, [lucideIcon('external-link'), el('span', { text: INF_PLATFORM_LABEL[k] + ' · ' + _infHandleOf(href) })]));
    }
    if (p.email) contacts.appendChild(el('a', { class: 'calls-action', href: 'mailto:' + p.email }, [lucideIcon('mail'), el('span', { text: p.email })]));
    if (p.whatsapp || p.phone) contacts.appendChild(el('span', { class: 'calls-action' }, [lucideIcon('phone'), el('span', { text: '+' + (p.whatsapp || p.phone) })]));
    const link = _infSafeHttps(p.link);
    if (link) contacts.appendChild(el('a', { class: 'calls-action', href: link, target: '_blank', rel: 'noopener noreferrer' }, [lucideIcon('link'), el('span', { text: 'Link in bio' })]));
    if (!contacts.childNodes.length) contacts.appendChild(el('span', { class: 'contacts-preview-activity', text: 'No contact found yet (Searchy keeps looking).' }));
    body.appendChild(el('details', { class: 'contacts-preview-section', open: 'open' }, [el('summary', { text: 'Contacts' }), contacts]));
    // Sends history
    const sends = Array.isArray(p.sends) ? p.sends.slice().reverse() : [];
    const sendList = el('div', { class: 'inf-sends' });
    if (!sends.length) sendList.appendChild(el('div', { class: 'contacts-preview-activity', text: 'Nothing sent yet.' }));
    for (const s of sends) {
      sendList.appendChild(el('div', { class: 'inf-send' }, [
        el('strong', { text: (s.channel === 'whatsapp' ? 'WhatsApp' : 'Email') + ' · ' + (MA.loc(new Date(s.at), 'dt', 'en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })) }),
        el('span', { class: 'inf-seen inf-seen-' + (_infSeenOf({ sends: [s] }) || 'sent'), text: s.status + (s.error ? ' · ' + s.error : '') }),
        s.by ? el('span', { class: 'inf-send-by', text: 'by ' + s.by }) : null,
      ].filter(Boolean)));
    }
    body.appendChild(el('details', { class: 'contacts-preview-section', open: 'open' }, [el('summary', { text: `Messages sent (${sends.length})` }), sendList]));
    // Searchy
    if (p.message || p.why) {
      const box = el('div', { class: 'inf-searchy' });
      if (p.why) box.appendChild(el('p', { class: 'inf-why', text: p.why }));
      if (p.message) {
        box.appendChild(el('p', { class: 'inf-draft', text: p.message }));
        const copy = el('button', { class: 'calls-action', type: 'button' }, [lucideIcon('copy'), el('span', { text: 'Copy message' })]);
        copy.addEventListener('click', async () => { try { await navigator.clipboard.writeText(p.message); toast('Message copied', 'success'); } catch { toast('Could not copy', 'error'); } });
        box.appendChild(copy);
      }
      body.appendChild(el('details', { class: 'contacts-preview-section', open: 'open' }, [el('summary', { text: 'Searchy' }), box]));
    }
    // About
    const propRows = [];
    for (const def of INFLUENCERS.propsCatalog || []) {
      if (['name', 'note'].includes(def.name)) continue;
      let v = _infValue(p, def.name);
      if (def.name === 'stage') v = INF_STAGE_LABEL[v] || v;
      else if (def.name === 'seen') v = INF_SEEN[v] || v;
      else if (def.type === 'datetime') v = _infDateText(v);
      else if (def.name === 'followers' && v !== '') v = _searchyFollowers(Number(v));
      else if (def.name === 'phone' && v) v = '+' + v;
      else if (def.options?.length) v = def.options.find(o => String(o.value) === String(v))?.label || v;
      propRows.push(el('div', { class: 'contacts-preview-property' }, [el('label', { text: def.label }), el('div', { class: 'contacts-preview-property-value', text: v === '' || v == null ? '--' : String(v) })]));
    }
    propRows.push(el('div', { class: 'contacts-preview-property' }, [el('label', { text: 'Team note' }), el('div', { class: 'contacts-preview-property-value', text: p.note || '--' })]));
    body.appendChild(el('details', { class: 'contacts-preview-section' }, [el('summary', { text: 'About this creator' }), el('div', { class: 'contacts-preview-properties' }, propRows)]));
    drawer.appendChild(body);
    document.body.appendChild(drawer);
    refreshIcons();
    onKey = (ev) => { if (ev.key === 'Escape') closeDrawer(); };
    document.addEventListener('keydown', onKey);
  }

  // ── Bulk bar ──
  function _renderInfluencersBulkBar() {
    const bar = document.getElementById('influencersBulkBar');
    if (!bar) return;
    const n = INFLUENCERS.selectedIds.size;
    bar.classList.toggle('hidden', !n);
    const c = document.getElementById('influencersBulkCount'); if (c) c.textContent = String(n);
    const all = document.getElementById('influencersSelectAll');
    if (all) { const pageIds = INFLUENCERS.rows.map(p => String(p.id)); all.checked = !!pageIds.length && pageIds.every(id => INFLUENCERS.selectedIds.has(id)); }
  }
  function _infExportCsv(rows) {
    if (!_isSalesAdmin()) { toast('Export CSV is admin-only', 'error'); return; }
    if (!rows || !rows.length) { toast('Nothing to export', 'warning'); return; }
    const cols = _infVisibleColumns();
    // A cell starting with = + - @ would run as a formula in Excel / Sheets.
    const cell = (v) => { let s = String(v ?? ''); if (/^[=+\-@\t\r]/.test(s)) s = "'" + s; return `"${s.replace(/"/g, '""')}"`; };
    const label = (c) => INFLUENCERS.propsByName[c]?.label || c;
    const header = ['Name', 'Handle', ...cols.map(label)].map(cell).join(',');
    const lines = rows.map(p => [_infValue(p, 'name'), p.handle || '', ...cols.map(c => {
      const v = _infValue(p, c);
      if (c === 'stage') return INF_STAGE_LABEL[v] || v;
      if (c === 'seen') return INF_SEEN[v] || v;
      if (c === 'phone' && v) return '+' + v;
      return v;
    })].map(cell).join(','));
    const blob = new Blob(['﻿' + header + '\n' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = el('a', { href: url, download: `influencers-${new Date().toISOString().slice(0, 10)}.csv`, style: 'display:none;' });
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast(`Exported ${rows.length} influencers`, 'success');
  }
  function _openInfluencersBulkEditModal() {
    const n = INFLUENCERS.selectedIds.size;
    if (!n) return;
    document.getElementById('influencersBulkEditCount').textContent = String(n);
    const sel = document.getElementById('influencersBulkEditProp');
    clear(sel);
    sel.appendChild(el('option', { value: 'stage', text: 'Our stage' }));
    sel.appendChild(el('option', { value: 'rating', text: 'Rating' }));
    document.getElementById('influencersBulkEditError')?.classList.add('hidden');
    _renderInfluencersBulkEditValue();
    openModal('influencersBulkEditModal');
  }
  function _renderInfluencersBulkEditValue() {
    const wrap = document.getElementById('influencersBulkEditValueWrap');
    const propName = document.getElementById('influencersBulkEditProp')?.value || 'stage';
    if (!wrap) return;
    clear(wrap);
    wrap.appendChild(el('span', { text: 'New value', style: 'font-size:11px; font-weight:600; color:var(--text-secondary); text-transform:uppercase;' }));
    const s = el('select', { id: 'influencersBulkEditValue', class: 'input' });
    for (const [value, label] of (propName === 'rating' ? INF_RATINGS : INF_STAGES)) s.appendChild(el('option', { value, text: label }));
    wrap.appendChild(s);
  }
  async function _infBulkEditApply() {
    const propName = document.getElementById('influencersBulkEditProp')?.value;
    const value = document.getElementById('influencersBulkEditValue')?.value || '';
    const ids = [...INFLUENCERS.selectedIds];
    const err = document.getElementById('influencersBulkEditError');
    if (!propName || !value || !ids.length) return;
    const label = (propName === 'rating' ? INF_RATINGS : INF_STAGES).find(([k]) => k === value)?.[1] || value;
    if (!confirm(`Set ${propName === 'rating' ? 'rating' : 'stage'} "${label}" on ${ids.length} influencer${ids.length > 1 ? 's' : ''}?`)) return;
    const btn = document.getElementById('influencersBulkEditApplyBtn');
    btn.disabled = true;
    let ok = 0, failed = 0;
    for (const id of ids) {
      try {
        const r = await api(propName === 'rating' ? '/api/influencer/prospects/rate' : '/api/influencer/prospects/update', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(propName === 'rating' ? { id, rating: value } : { id, status: value }),
        });
        if (!r.ok) { failed++; continue; }
        const j = await r.json().catch(() => ({}));
        const p = INFLUENCERS.all.find(x => String(x.id) === String(id));
        if (p && j.prospect) Object.assign(p, j.prospect);
        ok++;
      } catch { failed++; }
    }
    btn.disabled = false;
    if (failed) { if (err) { err.textContent = `${ok} updated · ${failed} not changed (a "No" stays locked)`; err.classList.remove('hidden'); } }
    else { closeModal('influencersBulkEditModal'); toast(`${ok} influencer${ok > 1 ? 's' : ''} updated`, 'success'); INFLUENCERS.selectedIds.clear(); }
    _renderInfluencersTable();
  }

  // ───────────────────────────────────────────────────────────────────
  // Influencer Templates — the Leads7 Templates modal, duplicated for
  // creators. Canal = Email or Phone number (WhatsApp from 661 557999).
  // WhatsApp templates go through Meta approval (MARKETING) and stay locked
  // until Ayoub writes "ah khles" (switch influencer_whatsapp). Sending =
  // preview → skipped + reasons → one full example → type SEND N.
  // ───────────────────────────────────────────────────────────────────
  const INF_TPL = { target: 'manage', list: [], ready: null, stage: 'all', channel: 'email', editingId: null, loading: false, job: null, jobTimer: null, plan: null };
  const INF_VARS = [['name', 'Name'], ['first_name', 'First name'], ['platform', 'Platform'], ['niche', 'Niche'], ['draft', 'Searchy message (email only)'], ['partner_link', 'Partner link']];
  const INF_REASON = {
    do_not_contact: 'Rated "No"', not_rated_ok: 'Not rated Very good / Good', already_partner: 'Already agreed / started',
    already_replied: 'Already replied', not_a_fit: 'Not a fit', no_email: 'No email', no_phone: 'No phone number',
    unsubscribed: 'Unsubscribed', already_emailed: 'Already emailed', already_messaged: 'Already messaged on WhatsApp',
    emailed_recently: 'Emailed less than 14 days ago', messaged_recently: 'Messaged less than 14 days ago',
    contacted_by_hand: 'Already contacted by hand', send_in_progress_or_unknown: 'Last send not confirmed',
    already_queued: 'Already in a running send', daily_limit: 'Daily limit reached', per_send_limit: 'Over the limit for one send',
    empty_message: 'Empty message', forbidden_word: 'Forbidden word in the message', already_a_leads7_chat: 'Already talks to Leads7 — write by hand',
    already_a_leads7_contact: 'Already a Leads7 lead or client — write by hand', leads_not_loaded: 'Leads list not loaded yet — try again in a minute',
    same_person_twice: 'Same person as another selected profile',
    not_found: 'Not found', whatsapp_locked: 'WhatsApp locked',
  };
  const INF_ERRORS = {
    template_not_approved: 'This WhatsApp template is not approved by Meta yet.',
    template_channel_mismatch: 'This template is for another channel.',
    whatsapp_not_ready: 'WhatsApp sending is locked.', email_not_ready: 'Email sending is not ready.',
    confirm_mismatch: 'Type exactly SEND and the number.', another_send_running: 'Another send is still running.',
    whatsapp_locked: 'WhatsApp sending is locked until Ayoub writes "ah khles".',
    draft_is_email_only: '{{draft}} only works in Email: Meta refuses a WhatsApp message that is only a variable.',
    variable_at_start_or_end: 'Meta refuses a message that starts or ends with a variable: add words around it.',
    subject_required: 'An email needs a subject.', body_required: 'Write the message.', name_required: 'Give the template a name.',
    body_too_long: 'Too long (email 2000, WhatsApp 1024 characters).', forbidden_word: 'Forbidden word (Zoom or a team member name).',
    nobody_selected: 'Select creators in the table first.', built_in_template: 'The built-in template cannot be changed: save a copy with another name.',
    meta_not_configured: 'Meta is not configured on this server.', already_approved: 'Already approved by Meta.',
    stray_brace: 'A { or } is left in the text: use the variable chips.', template_not_approved_mid: 'Meta paused this template.',
  };
  const _infErr = (code, detail) => {
    const c = String(code || '');
    if (c.startsWith('unknown_placeholder:')) return 'Unknown variable {{' + c.slice(20) + '}}. Use the chips.';
    if (c.startsWith('missing_')) return 'Missing ' + c.slice(8).replace(/_/g, ' ');
    return (INF_ERRORS[c] || INF_REASON[c] || c || 'Failed') + (detail ? ' ' + detail : '');
  };

  let _infTplWired = false;
  function _wireInfluencerTemplatesOnce() {
    if (_infTplWired) return;
    _infTplWired = true;
    const on = (id, ev, fn) => document.getElementById(id)?.addEventListener(ev, fn);
    on('infTplSaveBtn', 'click', _infTplSave);
    on('infTplResetBtn', 'click', () => _infTplResetForm());
    on('infTplChannel', 'change', () => { _infTplSyncForm(); _infTplPreview(); });
    on('infTplContent', 'input', _infTplPreview);
    on('infTplSubject', 'input', _infTplPreview);
    on('infTplSearch', 'input', _infTplRenderList);
    document.getElementById('infTplStageTabs')?.addEventListener('click', (e) => {
      const b = e.target.closest('[data-inftplstage]');
      if (!b) return;
      INF_TPL.stage = b.dataset.inftplstage;
      document.querySelectorAll('#infTplStageTabs .tpl-stage-btn').forEach(x => x.classList.toggle('active', x === b));
      _infTplRenderList();
    });
    document.getElementById('infTplChannelTabs')?.addEventListener('click', (e) => {
      const b = e.target.closest('[data-inftplchannel]');
      if (!b) return;
      INF_TPL.channel = b.dataset.inftplchannel;
      // A new template follows the tab (Email / Phone number); an edit keeps its own channel.
      if (!INF_TPL.editingId) { const c = document.getElementById('infTplChannel'); if (c) c.value = INF_TPL.channel; _infTplSyncForm(); _infTplPreview(); }
      _infTplSyncChannelTabs();
      _infTplRenderList();
    });
    // Variable picker: same look as the sales modal (select + quick chips).
    const tools = document.getElementById('infTplVarTools');
    if (tools) {
      const wrap = el('div', { class: 'var-inserter' });
      const sel = el('select', { class: 'input var-inserter-sel', title: 'Insérer une variable' });
      sel.appendChild(el('option', { value: '', text: '➕ Insérer une variable…' }));
      for (const [k, label] of INF_VARS) sel.appendChild(el('option', { value: k, text: `{{${k}}} — ${label}` }));
      sel.addEventListener('change', () => { const k = sel.value; sel.value = ''; if (k) { _insertVarAtCursor(document.getElementById('infTplContent'), k); } });
      const chips = el('div', { class: 'var-quick-chips' });
      for (const [k] of INF_VARS) {
        const chip = el('button', { type: 'button', class: 'meta-tpl-chip', text: '{{' + k + '}}' });
        chip.addEventListener('click', () => _insertVarAtCursor(document.getElementById('infTplContent'), k));
        chips.appendChild(chip);
      }
      wrap.append(sel, chips);
      tools.appendChild(wrap);
    }
    on('infSendConfirmInput', 'input', _infSendSyncButton);
    on('infSendGoBtn', 'click', _infSendGo);
  }

  async function openInfluencerTemplates(target) {
    _wireInfluencerTemplatesOnce();
    INF_TPL.target = target === 'send' ? 'send' : 'manage';
    if (INF_TPL.target === 'send' && !INFLUENCERS.selectedIds.size) { toast('Select creators in the table first.', 'warning'); return; }
    const sub = document.getElementById('infTplSubtitle');
    const n = INFLUENCERS.selectedIds.size;
    if (sub) sub.textContent = INF_TPL.target === 'send' ? `· Send to ${n} influencer${n === 1 ? '' : 's'}` : '';
    openModal('infTemplatesModal');
    _infTplResetForm();
    await _infTplLoad();
  }

  async function _infTplLoad() {
    INF_TPL.loading = true;
    _infTplRenderList();
    try {
      const [t, r] = await Promise.all([api('/api/influencer/templates'), api('/api/influencer/outreach/ready')]);
      INF_TPL.list = t.ok ? ((await t.json()).templates || []) : [];
      INF_TPL.ready = r.ok ? await r.json() : null;
    } catch { INF_TPL.list = []; }
    INF_TPL.loading = false;
    _infTplSyncChannelTabs();
    _infTplRenderList();
    _infTplPreview();
  }

  function _infTplSyncChannelTabs() {
    document.querySelectorAll('#infTplChannelTabs [data-inftplchannel]').forEach(b => b.classList.toggle('active', b.dataset.inftplchannel === INF_TPL.channel));
    const note = document.getElementById('infTplChannelNote');
    if (!note) return;
    const ch = INF_TPL.ready?.channels?.[INF_TPL.channel];
    const sent = INF_TPL.ready?.sentTodayByChannel?.[INF_TPL.channel] ?? 0;
    const cap = INF_TPL.channel === 'whatsapp' ? 20 : 50;
    note.textContent = ch ? `${ch.connected ? 'Ready' : 'Not ready'} · ${ch.detail || ''} · ${sent}/${cap} sent today` : '';
    note.classList.toggle('is-off', !!ch && !ch.connected);
  }

  function _infTplSyncForm() {
    const ch = document.getElementById('infTplChannel')?.value || 'email';
    document.getElementById('infTplSubject')?.classList.toggle('hidden', ch !== 'email');
    document.getElementById('infTplLangWrap')?.classList.toggle('hidden', ch !== 'whatsapp');
    const ta = document.getElementById('infTplContent');
    if (ta) ta.placeholder = ch === 'whatsapp'
      ? 'WhatsApp message (Meta checks it before the first send). Variables: {{name}}, {{first_name}}, {{platform}}, {{niche}}, {{partner_link}} — not at the very start or end.'
      : 'Email message. Insère une variable ({{name}}, {{draft}}, {{partner_link}}…) — elle se remplit avec les vraies données à l’envoi.';
  }

  function _infTplResetForm(t) {
    INF_TPL.editingId = t && !t.builtIn ? t.id : null;
    const set = (id, v) => { const n = document.getElementById(id); if (n) n.value = v; };
    set('infTplName', t ? (t.builtIn ? t.name + ' (copy)' : t.name) : '');
    set('infTplSubject', t?.subject || '');
    set('infTplContent', t?.body || '');
    set('infTplChannel', t?.channel || INF_TPL.channel || 'email');
    set('infTplStage', t?.stage || '');
    set('infTplLang', t?.language || t?.meta_language || 'fr');
    const save = document.getElementById('infTplSaveBtn');
    if (save) save.lastChild.textContent = INF_TPL.editingId ? ' Save changes' : ' Save';
    _infTplSyncForm();
    _infTplPreview();
  }

  function _infFill(text, p) {
    const name = (String(p?.name || '').trim()) || '@' + (p?.handle || 'creator');
    const values = { name, first_name: name.replace(/^@/, '').split(/\s+/)[0] || name, platform: INF_PLATFORM_LABEL[p?.platform] || p?.platform || '', niche: p?.niche || '', draft: p?.message || '', partner_link: 'https://affiliate.loughainstitute.com' };
    return String(text || '').replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (all, k) => (Object.hasOwn(values, k) ? values[k] : all));
  }

  // Live preview for the first selected creator (the exact text they would get).
  function _infTplPreview() {
    const box = document.getElementById('infTplPreview');
    if (!box) return;
    clear(box);
    const body = document.getElementById('infTplContent')?.value || '';
    if (!body.trim()) { box.classList.add('hidden'); return; }
    box.classList.remove('hidden');
    const firstId = [...INFLUENCERS.selectedIds][0];
    const p = INFLUENCERS.all.find(x => String(x.id) === String(firstId)) || INFLUENCERS.all.find(x => x.rating === 'very_good') || INFLUENCERS.all[0] || null;
    const ch = document.getElementById('infTplChannel')?.value || 'email';
    box.appendChild(el('div', { class: 'inf-preview-head', text: 'Preview · ' + (p ? _infValue(p, 'name') : 'example') + (ch === 'whatsapp' ? ' · WhatsApp' : ' · Email') }));
    if (ch === 'email') box.appendChild(el('div', { class: 'inf-preview-subject', text: _infFill(document.getElementById('infTplSubject')?.value || '', p) }));
    box.appendChild(el('div', { class: 'inf-preview-body', text: _infFill(body, p) }));
  }

  async function _infTplSave() {
    const payload = {
      id: INF_TPL.editingId || undefined,
      name: document.getElementById('infTplName')?.value || '',
      channel: document.getElementById('infTplChannel')?.value || 'email',
      subject: document.getElementById('infTplSubject')?.value || '',
      body: document.getElementById('infTplContent')?.value || '',
      stage: document.getElementById('infTplStage')?.value || '',
      language: document.getElementById('infTplLang')?.value || 'fr',
    };
    try {
      const r = await api('/api/influencer/templates', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(_infErr(j.error), 'error'); return; }
      INF_TPL.list = j.templates || INF_TPL.list;
      INF_TPL.channel = payload.channel;
      _infTplSyncChannelTabs();
      toast(payload.channel === 'whatsapp' && !(j.template && j.template.meta_status === 'APPROVED') ? 'Saved. Submit it to Meta (cloud icon) before the first WhatsApp send.' : 'Template saved', 'success');
      _infTplResetForm();
      _infTplRenderList();
    } catch (e) { toast('Save failed: ' + (e.message || e), 'error'); }
  }

  async function _infTplDelete(t) {
    if (!confirm(`Delete template "${t.name}"?`)) return;
    const r = await api('/api/influencer/templates/delete', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: t.id }) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast(_infErr(j.error), 'error'); return; }
    INF_TPL.list = j.templates || INF_TPL.list;
    _infTplRenderList();
  }

  async function _infTplMeta(t, action) {
    const r = await api('/api/influencer/templates/' + (action === 'refresh' ? 'meta-refresh' : 'submit-meta'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: t.id }) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast(_infErr(j.error), 'error'); return; }
    INF_TPL.list = j.templates || INF_TPL.list;
    toast(action === 'refresh' ? 'Meta status: ' + (j.template?.meta_status || '?') : 'Sent to Meta for review (usually minutes to a few hours).', 'success');
    _infTplRenderList();
  }

  function _infTplRenderList() {
    const list = document.getElementById('infTplList');
    const count = document.getElementById('infTplCount');
    if (!list) return;
    clear(list);
    if (INF_TPL.loading) { list.appendChild(el('div', { class: 'contacts-loading', text: 'Loading templates…' })); return; }
    const q = (document.getElementById('infTplSearch')?.value || '').trim().toLowerCase();
    const rows = INF_TPL.list.filter(t => (t.channel || 'email') === INF_TPL.channel)
      .filter(t => INF_TPL.stage === 'all' || (INF_TPL.stage === 'unsorted' ? !t.stage : t.stage === INF_TPL.stage))
      .filter(t => !q || [t.name, t.subject, t.body].join(' ').toLowerCase().includes(q));
    if (count) count.textContent = `${rows.length} template${rows.length === 1 ? '' : 's'}`;
    if (!rows.length) list.appendChild(el('div', { class: 'contacts-empty', text: INF_TPL.channel === 'whatsapp' ? 'No Phone number template yet. Write one above with Canal = Phone number, save, then send it to Meta.' : 'No template here.' }));
    for (const t of rows) list.appendChild(_infTplItem(t));
    refreshIcons(list);
  }

  function _infTplItem(t) {
    const n = INFLUENCERS.selectedIds.size;
    const wa = t.channel === 'whatsapp';
    let badge;
    if (!wa) badge = el('span', { class: 'tpl-meta-badge tpl-channel-sms', style: 'background:var(--accent-soft);color:var(--accent);', text: '✉ Email', title: 'Email from partners@mail.loughainstitute.com · replies go to support@' });
    else if (t.meta_status === 'APPROVED') badge = el('span', { class: 'tpl-meta-badge tpl-meta-approved', text: '✓ Meta', title: 'Approved by Meta · WhatsApp 661' });
    else if (t.meta_status === 'PENDING') badge = el('span', { class: 'tpl-meta-badge tpl-meta-pending', text: '⏳ Meta', title: 'Waiting for Meta' });
    else if (t.meta_status === 'REJECTED') badge = el('span', { class: 'tpl-meta-badge tpl-meta-rejected', text: '✗ Meta', title: 'Rejected by Meta: change the text and save again' });
    else badge = el('span', { class: 'tpl-meta-badge tpl-meta-local', text: '◌ Not sent to Meta', title: 'Send it to Meta before the first WhatsApp message' });
    const stageTag = t.stage ? el('span', { class: 'tpl-stage-tag inf-tpl-stage is-istage-' + t.stage, text: INF_STAGE_LABEL[t.stage] || t.stage }) : null;
    const actions = [];
    if (INF_TPL.target === 'send') {
      const ready = INF_TPL.ready?.channels?.[t.channel || 'email'];
      const canSend = n > 0 && (!wa || t.meta_status === 'APPROVED');
      const send = el('button', { type: 'button', class: 'cbb-btn cbb-btn-primary tpl-bulk-send-btn', title: wa ? (t.meta_status === 'APPROVED' ? (ready?.connected ? 'Send by WhatsApp (661)' : (ready?.detail || 'WhatsApp locked')) : 'Not approved by Meta yet') : 'Send by email' }, [lucideIcon('send'), document.createTextNode(` Send to ${n}`)]);
      if (!canSend) send.disabled = true;
      send.addEventListener('click', () => _infOpenSendConfirm(t));
      actions.push(send);
    }
    if (wa && !t.builtIn && t.meta_status !== 'APPROVED' && t.meta_status !== 'PENDING') {
      const meta = el('button', { type: 'button', class: 'icon-btn-sm tpl-meta-btn', title: 'Send to Meta for approval', 'aria-label': 'Send to Meta' }, [lucideIcon('upload-cloud')]);
      meta.addEventListener('click', () => _infTplMeta(t, 'submit'));
      actions.push(meta);
    }
    if (wa && t.meta_name && t.meta_status === 'PENDING') {
      const refresh = el('button', { type: 'button', class: 'icon-btn-sm tpl-meta-refresh-btn', title: 'Check Meta approval', 'aria-label': 'Check Meta approval' }, [lucideIcon('refresh-cw')]);
      refresh.addEventListener('click', () => _infTplMeta(t, 'refresh'));
      actions.push(refresh);
    }
    const edit = el('button', { type: 'button', class: 'icon-btn-sm', title: t.builtIn ? 'Copy into the form' : 'Edit', 'aria-label': 'Edit' }, [lucideIcon('pencil')]);
    edit.addEventListener('click', () => { _infTplResetForm(t); document.getElementById('infTplName')?.focus(); });
    actions.push(edit);
    if (!t.builtIn) {
      const del = el('button', { type: 'button', class: 'icon-btn-sm', title: 'Delete', 'aria-label': 'Delete' }, [lucideIcon('trash-2')]);
      del.addEventListener('click', () => _infTplDelete(t));
      actions.push(del);
    }
    return el('div', { class: 'tpl-item' }, [
      el('div', { class: 'tpl-body' }, [
        el('div', { class: 'tpl-name' }, [el('span', { text: t.name }), badge, stageTag].filter(Boolean)),
        !wa && t.subject ? el('div', { class: 'inf-tpl-subject', text: t.subject }) : null,
        el('div', { class: 'tpl-content', text: t.body }),
      ].filter(Boolean)),
      el('div', { class: 'tpl-actions' }, actions),
    ]);
  }

  // ── Send: preview → confirm (SEND N) → background job ──
  async function _infOpenSendConfirm(t) {
    const ids = [...INFLUENCERS.selectedIds];
    const channel = t.channel || 'email';
    let plan;
    try {
      const r = await api('/api/influencer/outreach/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, templateId: t.id, channel }) });
      plan = await r.json().catch(() => ({}));
      if (!r.ok) { toast(_infErr(plan.error), 'error'); return; }
    } catch (e) { toast('Preview failed: ' + (e.message || e), 'error'); return; }
    INF_TPL.plan = { ...plan, template: t, ids, channel };
    const nameOf = (id) => { const p = INFLUENCERS.all.find(x => String(x.id) === String(id)); return p ? _infValue(p, 'name') : id; };
    const box = document.getElementById('infSendBody');
    clear(box);
    const chLabel = channel === 'whatsapp' ? 'WhatsApp · 661 557999' : 'Email · partners@mail.loughainstitute.com';
    box.appendChild(el('p', { class: 'inf-send-line' }, [el('strong', { text: `${plan.accepted.length} will receive "${t.name}"` }), document.createTextNode(` · ${chLabel} · ${plan.limits?.leftToday ?? 0} left today`)]));
    if (plan.locked) box.appendChild(el('p', { class: 'inf-send-warn', text: 'WhatsApp is locked: Meta bills each message, so it opens only after Ayoub writes "ah khles".' }));
    if (plan.skipped?.length) {
      const ul = el('ul', { class: 'inf-send-skipped' });
      for (const s of plan.skipped) ul.appendChild(el('li', {}, [el('strong', { text: nameOf(s.id) }), document.createTextNode(' — ' + _infErr(s.reason))]));
      box.appendChild(el('details', { class: 'inf-send-skip-box', ...(plan.accepted.length ? {} : { open: 'open' }) }, [el('summary', { text: `${plan.skipped.length} skipped` }), ul]));
    }
    if (plan.sample) {
      box.appendChild(el('div', { class: 'inf-preview' }, [
        el('div', { class: 'inf-preview-head', text: 'Example · to ' + plan.sample.to }),
        channel === 'email' ? el('div', { class: 'inf-preview-subject', text: plan.sample.subject }) : null,
        el('div', { class: 'inf-preview-body', text: plan.sample.body }),
      ].filter(Boolean)));
    }
    const input = document.getElementById('infSendConfirmInput');
    const label = document.getElementById('infSendConfirmLabel');
    const go = document.getElementById('infSendGoBtn');
    const progress = document.getElementById('infSendProgress');
    progress.textContent = '';
    input.value = '';
    input.placeholder = 'SEND ' + plan.accepted.length;
    input.disabled = !plan.accepted.length || plan.locked;
    label.textContent = plan.accepted.length ? `Type SEND ${plan.accepted.length} to send` : 'Nobody can receive it.';
    go.disabled = true;
    go.classList.remove('hidden');
    openModal('infSendModal');
    setTimeout(() => input.focus(), 50);
  }
  function _infSendSyncButton() {
    const plan = INF_TPL.plan;
    const go = document.getElementById('infSendGoBtn');
    if (!plan || !go) return;
    go.disabled = plan.locked || !plan.accepted.length || document.getElementById('infSendConfirmInput').value.trim() !== `SEND ${plan.accepted.length}`;
  }
  async function _infSendGo() {
    const plan = INF_TPL.plan;
    if (!plan) return;
    const go = document.getElementById('infSendGoBtn');
    const progress = document.getElementById('infSendProgress');
    go.disabled = true;
    try {
      const r = await api('/api/influencer/outreach/send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: plan.ids, templateId: plan.template.id, channel: plan.channel, confirm: document.getElementById('infSendConfirmInput').value.trim() }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast(_infErr(j.error, j.detail), 'error'); go.disabled = false; return; }
      if (!j.jobId) { progress.textContent = 'Nothing to send.'; return; }
      go.classList.add('hidden');
      document.getElementById('infSendConfirmInput').disabled = true;
      progress.textContent = `Sending… 0 / ${j.accepted}`;
      clearInterval(INF_TPL.jobTimer);
      INF_TPL.jobTimer = setInterval(async () => {
        try {
          const s = await api('/api/influencer/outreach/jobs/' + encodeURIComponent(j.jobId));
          if (!s.ok) return;
          const job = await s.json();
          const sent = job.results.filter(x => x.status === 'sent').length;
          const bad = job.results.filter(x => x.status !== 'sent').length;
          progress.textContent = `${job.state === 'done' ? 'Done' : 'Sending…'} · ${sent} sent${bad ? ' · ' + bad + ' not sent' : ''}${job.left ? ' · ' + job.left + ' left' : ''}`;
          if (job.state === 'done') {
            clearInterval(INF_TPL.jobTimer);
            INFLUENCERS.selectedIds.clear();
            refreshInfluencers({ refetch: true, silent: true });
            toast(`${sent} message${sent === 1 ? '' : 's'} sent`, sent ? 'success' : 'warning');
          }
        } catch { /* next tick */ }
      }, 2000);
    } catch (e) { toast('Send failed: ' + (e.message || e), 'error'); go.disabled = false; }
  }

  // ───────────────────────────────────────────────────────────────────
  // Group Students modal — list/add/remove students from a group, with
  // HubSpot association write-through. Wired up after the row's Students
  // pill is clicked.
  // ───────────────────────────────────────────────────────────────────
  state.groupStudents = { group: null, list: [], searchTimer: null };

  async function openGroupStudentsModal(group) {
    state.groupStudents = { group, list: [], searchTimer: null };
    const title = document.getElementById('gsModalTitle');
    if (title) title.textContent = group.name || 'Group students';
    document.getElementById('gsError')?.classList.add('hidden');
    document.getElementById('gsSearch').value = '';
    document.getElementById('gsResults').innerHTML = '';
    openModal('groupStudentsModal');
    if (!_groupStudentsWired) {
      _groupStudentsWired = true;
      document.getElementById('gsSearch')?.addEventListener('input', _gsOnSearch);
    }
    await _gsLoadCurrent();
  }
  let _groupStudentsWired = false;

  async function _gsLoadCurrent() {
    const host = document.getElementById('gsCurrent');
    if (!host) return;
    clear(host);
    host.appendChild(el('div', { class: 'ts-book-empty', text: 'Loading…' }));
    const coId = state.groupStudents.group?.id;
    if (!coId) return;
    try {
      const r = await api('/api/hubspot/companies/' + encodeURIComponent(coId) + '/students');
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      state.groupStudents.list = j.students || [];
      _gsRenderCurrent();
    } catch (err) {
      clear(host);
      host.appendChild(el('div', { class: 'ts-book-empty', text: 'Failed to load: ' + (err.message || err) }));
    }
  }

  function _gsRenderCurrent() {
    const host = document.getElementById('gsCurrent');
    if (!host) return;
    clear(host);
    const n = state.groupStudents.list.length;
    host.appendChild(el('h3', {
      text: 'Current students (' + n + ')',
      style: 'margin:0 0 8px; font-size:13px; font-weight:600;',
    }));
    if (!n) {
      host.appendChild(el('div', { class: 'ts-book-empty', text: 'No students linked yet.' }));
      return;
    }
    const list = el('div');
    for (const s of state.groupStudents.list) {
      const row = el('div', { class: 'gs-row' });
      // Age is read from the durable contact record at render time (see the
      // students route) — shown for every student however they were added.
      const ageNum = Number.parseInt(s.age, 10);
      const nameLine = el('div', { class: 'gs-name-line' }, [
        el('span', { text: s.name, style: 'font-weight:500;' }),
      ]);
      if (Number.isFinite(ageNum) && ageNum > 0) {
        nameLine.appendChild(el('span', { class: 'gs-age', text: ageNum + ' ans', title: 'Age (HubSpot)' }));
      }
      row.appendChild(el('div', { style: 'flex:1; min-width:0;' }, [
        nameLine,
        el('div', { text: s.phone || '(no phone)', style: 'font-size:11px; color:var(--text-secondary);' }),
      ]));
      if (s.lead_status) row.appendChild(el('span', { class: 'contact-pill', text: s.lead_status, style: 'font-size:10px;' }));
      // Open the student's conversation — same path as Contacts: the server-
      // validated functional JID (display phones may be masked), raw phone only
      // as a strict fallback. One thread per phone, so multi-contact numbers
      // land on the same chat Contacts opens.
      const localWa = _phoneForWhatsapp(s.phone);
      const functionalJid = _validatedFunctionalChatJid(s.chatJid)
        || (localWa ? localWa + '@s.whatsapp.net' : '');
      if (functionalJid) {
        const inboxBtn = el('button', {
          type: 'button',
          class: 'contact-wa-btn gs-inbox-btn',
          title: "Ouvrir dans l'Inbox",
          'aria-label': "Ouvrir dans l'Inbox",
        });
        inboxBtn.appendChild(el('i', { 'data-lucide': 'message-circle' }));
        inboxBtn.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          closeModal('groupStudentsModal');
          openInboxForChatJid(functionalJid, s.name || '');
        });
        row.appendChild(inboxBtn);
      }
      const removeBtn = el('button', {
        type: 'button',
        class: 'btn btn-ghost',
        style: 'color:#dc2626; padding:4px 8px; font-size:12px;',
        text: 'Remove',
      });
      removeBtn.addEventListener('click', () => _gsRemoveStudent(s));
      row.appendChild(removeBtn);
      list.appendChild(row);
    }
    host.appendChild(list);
    refreshIcons(host);
  }

  function _gsOnSearch(ev) {
    clearTimeout(state.groupStudents.searchTimer);
    const q = ev.target.value.trim();
    if (!q || q.length < 2) {
      document.getElementById('gsResults').innerHTML = '';
      return;
    }
    state.groupStudents.searchTimer = setTimeout(() => _gsRunSearch(q), 250);
  }

  async function _gsRunSearch(q) {
    const host = document.getElementById('gsResults');
    if (!host) return;
    clear(host);
    host.appendChild(el('div', { class: 'ts-book-empty', text: 'Searching…' }));
    try {
      const params = new URLSearchParams();
      params.set('q', q);
      params.set('limit', '20');
      params.set('createdAfter', 'all');
      const r = await api('/api/hubspot/contacts?' + params.toString());
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      const hits = (j.contacts || []).filter(c =>
        !state.groupStudents.list.find(s => s.id === c.id)
      );
      clear(host);
      if (!hits.length) {
        host.appendChild(el('div', { class: 'ts-book-empty', text: 'No matches (or all results already in this group).' }));
        return;
      }
      for (const c of hits) {
        const name = ((c.firstname || '') + ' ' + (c.lastname || '')).trim() || c.phone || '(no name)';
        const row = el('div', { style: 'display:flex; align-items:center; gap:10px; padding:6px 4px; border-bottom:1px solid var(--border);' });
        row.appendChild(el('div', { style: 'flex:1;' }, [
          el('div', { text: name, style: 'font-weight:500;' }),
          el('div', { text: c.phone || '(no phone)', style: 'font-size:11px; color:var(--text-secondary);' }),
        ]));
        const addBtn = el('button', {
          type: 'button',
          class: 'btn btn-primary',
          style: 'padding:4px 12px; font-size:12px;',
          text: 'Add to group',
        });
        addBtn.addEventListener('click', () => _gsAddStudent(c));
        row.appendChild(addBtn);
        host.appendChild(row);
      }
    } catch (err) {
      clear(host);
      host.appendChild(el('div', { class: 'ts-book-empty', text: 'Search failed: ' + (err.message || err) }));
    }
  }

  async function _gsAddStudent(contact) {
    const errEl = document.getElementById('gsError');
    errEl?.classList.add('hidden');
    const coId = state.groupStudents.group?.id;
    try {
      const r = await api('/api/hubspot/companies/' + encodeURIComponent(coId) + '/students', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: contact.id }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || ('HTTP ' + r.status));
      }
      const name = ((contact.firstname || '') + ' ' + (contact.lastname || '')).trim() || contact.phone || '(no name)';
      toast('Added ' + name + ' to ' + state.groupStudents.group.name, 'success');
      // Optimistic insert + reload count on the underlying Groups table
      state.groupStudents.list.push({
        id: contact.id,
        name,
        phone: contact.phone || '',
        email: contact.email || '',
        lead_status: contact.hs_lead_status || '',
        // Same shape the students route ships, so the optimistic row renders
        // its age chip + inbox shortcut before the reload lands.
        age: contact.properties?.age ?? null,
        chatJid: contact.chatJid || null,
      });
      _gsRenderCurrent();
      // Re-run the search so the added contact disappears from the picker
      const q = document.getElementById('gsSearch')?.value?.trim() || '';
      if (q.length >= 2) _gsRunSearch(q);
      // Reload the main Groups table so the count pill updates
      loadGroups();
    } catch (err) {
      if (errEl) {
        errEl.textContent = String(err.message || err);
        errEl.classList.remove('hidden');
      }
    }
  }

  async function _gsRemoveStudent(student) {
    if (!confirm('Remove ' + student.name + ' from this group?')) return;
    const errEl = document.getElementById('gsError');
    errEl?.classList.add('hidden');
    const coId = state.groupStudents.group?.id;
    try {
      const r = await api('/api/hubspot/companies/' + encodeURIComponent(coId) + '/students/' + encodeURIComponent(student.id), {
        method: 'DELETE',
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || ('HTTP ' + r.status));
      }
      toast('Removed ' + student.name, 'success');
      state.groupStudents.list = state.groupStudents.list.filter(s => s.id !== student.id);
      _gsRenderCurrent();
      loadGroups();
    } catch (err) {
      if (errEl) {
        errEl.textContent = String(err.message || err);
        errEl.classList.remove('hidden');
      }
    }
  }

  // ─── Actions modal ───
  // The actions/eval modal buttons (status changes + "Enregistrer l'évaluation")
  // used to be wired ONLY inside ensureTestScheduleInit() — the GRID init. But
  // teachers open this modal from the "Mes tests" view, which never inits the
  // grid, so the Save button had NO click handler and teacher evaluations
  // silently never saved (Nada, 2026-06-09 — every score/level/notes stayed
  // NULL). Wire idempotently on every open so EVERY entry point (grid, Mes
  // tests, admin Tests) gets working buttons.
  let _tsActionsWired = false;
  function _wireTsActionsModal() {
    if (_tsActionsWired) return;
    _tsActionsWired = true;
    document.getElementById('tsActionMarkInTestBtn')?.addEventListener('click', () => tsActionPatch('in_test'));
    document.getElementById('tsActionMarkCompletedBtn')?.addEventListener('click', () => {
      // A teacher finishing a test must submit the assessment evidence too.
      // Keep the legacy status-only action for sales admins, who may need to
      // repair operational state without impersonating the teacher.
      if (String(state.me?.role || '').toLowerCase() === 'teacher') tsEvalSubmit();
      else tsActionPatch('completed');
    });
    document.getElementById('tsActionMarkNoShowBtn')?.addEventListener('click', () => tsActionPatch('no_show'));
    document.getElementById('tsActionCancelBtn')?.addEventListener('click', () => tsActionPatch('cancelled'));
    document.getElementById('tsActionConfirmBtn')?.addEventListener('click', () => tsActionPatch('confirmed'));
    document.getElementById('tsActionSendLinkBtn')?.addEventListener('click', () => tsSendLink());
    document.getElementById('tsActionMoveBtn')?.addEventListener('click', () => tsMoveSlot());
    document.getElementById('tsMoveTeacherSelect')?.addEventListener('change', (ev) => {
      const btn = document.getElementById('tsActionMoveBtn');
      if (btn) btn.disabled = !ev.target.value;
    });
    document.getElementById('tsEvalSubmitBtn')?.addEventListener('click', () => tsEvalSubmit());
    document.querySelectorAll('[data-ts-report-lang]').forEach(btn => {
      btn.addEventListener('click', (ev) => {
        ev.preventDefault();
        _tsDownloadAssessmentReport(state.ts.actionsSlot, btn.dataset.tsReportLang, btn);
      });
    });
  }

  function _tsHasDisplayIdentity(slot) {
    return !!String(slot?.lead_name || slot?.student_name || slot?.lead_phone || slot?.student_phone || '').trim();
  }

  function _tsCanOpenStudentChat(slot) {
    // The server owns this authorization decision because only it has the
    // active-sales set needed to ignore deleted/stale ownership signals.
    // Never reconstruct authority from visible row fields in the browser.
    return slot?.lead_chat_action_allowed === true
      && !!_validatedFunctionalChatJid(slot?.lead_chat_jid);
  }

  function openTsActionsModal(slot) {
    // Defense in depth: a masked seat (another agent's booking, swat:B1) has no
    // lead/student fields — the modal would render junk and every action would
    // 4xx. The chip is already non-interactive; this guards stray callers.
    if (slot?.masked) return;
    _wireTsActionsModal();
    state.ts.actionsSlot = slot;
    document.getElementById('tsActionsError').classList.add('hidden');
    const t = state.ts.teachers.find(x => x.id === slot.teacher_id);
    // Date/teacher come from the slot itself (not state.ts.date) — the teacher
    // "Mes tests" page opens slots from any day, so the grid's selected date
    // would be wrong. Grid slots have slot_date === state.ts.date, so no change there.
    const _slotDate = slot.slot_date ? String(slot.slot_date).slice(0, 10) : state.ts.date;
    const _teacherName = t?.name || slot.teacher_display_name || slot.teacher_name || 'Teacher';
    const summary = document.getElementById('tsActionsSummary');
    clear(summary);
    summary.appendChild(el('div', { class: 'ts-slot-summary-row' }, [
      el('strong', { text: _teacherName + ' · ' + String(slot.slot_time).slice(0,5) + ' · ' + _slotDate }),
    ]));
    summary.appendChild(el('div', { class: 'ts-slot-summary-row' }, [
      el('span', { class: 'ts-slot-status-pill', text: TS_STATUS_LABEL[slot.status] || slot.status }),
    ]));
    // Show the FULL student name on its own line — the teacher needs the
    // last name too to identify the student properly (screenshot 2026-05-25
    // showed just "Mr" without "Akil" because the previous redaction split
    // on whitespace and kept only the first token, AND the server redacts
    // teacher-role responses the same way). Phone + booked-by stay
    // hidden from teachers for the existing privacy reason; only the name
    // becomes full-length.
    const _meRole = (state.me?.role || '').toLowerCase();
    const _redact = _meRole === 'teacher';
    const fullName = String(slot.lead_name || slot.student_name || '').trim();
    if (fullName) {
      summary.appendChild(el('div', {
        class: 'ts-slot-summary-row',
        style: 'font-weight:600; font-size:14px; margin-top:4px;',
        text: fullName,
      }));
    }
    if (!_tsHasDisplayIdentity(slot)) {
      summary.appendChild(el('div', {
        class: 'ts-slot-summary-row ts-rec-warning',
        text: 'Données étudiant indisponibles — recharge le planning.',
      }));
    }
    if (!_redact && (slot.lead_phone || slot.student_phone)) {
      const phoneRow = el('div', {
        class: 'ts-slot-summary-row ts-slot-summary-muted',
        style: 'display:flex; align-items:center; gap:6px;',
      });
      phoneRow.appendChild(el('span', { text: slot.lead_phone || slot.student_phone }));
      // WA + 3CX quick actions — only for the agent this lead is ASSIGNED to
      // (sales admins see them on every booking; teachers never reach here).
      // Dial/open digits come from lead_chat_jid (raw) because the displayed
      // phone is masked for agents.
      const _actJid = _validatedFunctionalChatJid(slot.lead_chat_jid);
      const _canAct = _tsCanOpenStudentChat(slot);
      if (_canAct) {
        const _actDigits = _actJid.split('@')[0];
        const _actName = fullName || _actDigits;
        const waBtn = el('button', {
          type: 'button',
          class: 'contact-wa-btn',
          title: 'Ouvrir la conversation WhatsApp',
          'aria-label': 'Ouvrir la conversation WhatsApp',
        });
        waBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="#25D366"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>';
        waBtn.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          closeModal('tsActionsModal'); // navigating to the inbox — drop the modal first
          openInboxForChatJid(_actJid, _actName);
        });
        phoneRow.appendChild(waBtn);
        phoneRow.appendChild(_contactCxBtn(_actDigits, _actName));
      }
      summary.appendChild(phoneRow);
    }
    if (!_redact && slot.booked_by) {
      summary.appendChild(el('div', { class: 'ts-slot-summary-row ts-slot-summary-muted', text: 'Booked by ' + bookedByLabel(slot) }));
    }
    // Sales note (2026-06-13) — read-only context the agent left for the
    // teacher. Sits ABOVE the eval form. Never bound to an input; el({text})
    // is textContent so it's XSS-safe. Shown to everyone who can see the
    // modal (teachers included — it's meant for them).
    if (slot.sales_note) {
      summary.appendChild(el('div', { class: 'ts-salesnote' }, [
        el('div', { class: 'ts-salesnote-label' }, [lucideIcon('target'), el('span', { text: 'Objectif & défi du client' })]),
        el('div', { class: 'ts-salesnote-body', text: slot.sales_note }),
      ]));
    }
    _tsLoadMoveTargets(slot);
    // Cancel slot: TEACHERS have no access (server 403s it) — hide the dead button
    // for them entirely. For agents/admins, still hide it on terminal seats
    // (cancelling a completed/no_show seat would destroy its eval/no-show record —
    // audit BE-1/OPS-3; server enforces too).
    const cancelBtn = document.getElementById('tsActionCancelBtn');
    if (cancelBtn) cancelBtn.style.display = (_redact || slot.status === 'cancelled' || slot.status === 'completed' || slot.status === 'no_show') ? 'none' : '';
    // Confirm button only meaningful from 'booked'
    const confirmBtn = document.getElementById('tsActionConfirmBtn');
    if (confirmBtn) confirmBtn.style.display = (slot.status === 'booked') ? '' : 'none';
    // A completed test with a saved level stays completed (server enforces too):
    // no "In test" / "No show" buttons that would reopen it.
    const levelLocked = slot.status === 'completed' && /^(A0|A1|A2|B1|B2|C1|C2)$/.test(String(slot.new_level || '').trim());
    for (const id of ['tsActionMarkInTestBtn', 'tsActionMarkNoShowBtn']) {
      const btn = document.getElementById(id);
      if (btn) btn.style.display = levelLocked ? 'none' : '';
    }
    // Send the queue-gated Lougha link: agent/admin only (server 403s teachers).
    const sendBtn = document.getElementById('tsActionSendLinkBtn');
    const linkable = !_redact && slot.lead_id && ['booked', 'confirmed', 'reminder_sent'].includes(slot.status);
    if (sendBtn) sendBtn.style.display = linkable ? '' : 'none';

    // Evaluation section — visible to teachers and sales admins, hidden for agents.
    const role = (state.me?.role || '').toLowerCase();
    const evalEl = document.getElementById('tsActionsEvalSection');
    if (evalEl) {
      const showEval = role === 'admin' || role === 'super_admin' || role === 'super_sales' || role === 'teacher';
      evalEl.classList.toggle('hidden', !showEval);
      if (showEval) _tsRenderEvalForm(slot);
    }
    openModal('tsActionsModal');
  }

  function _tsCanReassignSlot(slot) {
    const canManage = _isSalesAdmin() || state.me?.teacherManager === true;
    return canManage && ['booked', 'confirmed', 'reminder_sent'].includes(slot?.status);
  }

  async function _tsLoadMoveTargets(slot) {
    const panel = document.getElementById('tsMovePanel');
    const select = document.getElementById('tsMoveTeacherSelect');
    const button = document.getElementById('tsActionMoveBtn');
    const hint = document.getElementById('tsMoveHint');
    if (!panel || !select || !button || !hint) return;

    panel.classList.toggle('hidden', !_tsCanReassignSlot(slot));
    if (!_tsCanReassignSlot(slot)) return;
    clear(select);
    select.appendChild(el('option', { value: '', text: 'Loading available teachers…' }));
    select.disabled = true;
    button.disabled = true;
    hint.textContent = 'Choose another available teacher for the same time.';

    try {
      const r = await api('/api/test-schedule/slots/' + encodeURIComponent(slot.id) + '/reassign-targets');
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      // The user may have opened another slot while this request was in flight.
      if (String(state.ts.actionsSlot?.id || '') !== String(slot.id)) return;
      clear(select);
      const teachers = j.teachers || [];
      if (!teachers.length) {
        select.appendChild(el('option', { value: '', text: 'No teacher available at this time' }));
        hint.textContent = 'All other teachers are full, unavailable, or outside working hours.';
        return;
      }
      select.appendChild(el('option', { value: '', text: 'Choose a teacher…' }));
      for (const teacher of teachers) {
        const free = Number(teacher.free_seats || 0);
        select.appendChild(el('option', {
          value: teacher.id,
          text: teacher.display_name + ' · ' + free + ' free ' + (free === 1 ? 'seat' : 'seats'),
        }));
      }
      select.disabled = false;
    } catch (err) {
      if (String(state.ts.actionsSlot?.id || '') !== String(slot.id)) return;
      clear(select);
      select.appendChild(el('option', { value: '', text: 'Could not load teachers' }));
      hint.textContent = String(err?.message || err);
    }
  }

  const TS_REASSIGNABLE_STATUSES = new Set(['booked', 'confirmed', 'reminder_sent']);

  function _tsMovableSlotsForTeacher(teacherId) {
    return Object.values(state.ts.slotsByCell || {}).flat()
      .filter(slot =>
        !slot.masked &&
        String(slot.teacher_id) === String(teacherId) &&
        TS_REASSIGNABLE_STATUSES.has(slot.status)
      )
      .sort((a, b) => String(a.slot_time).localeCompare(String(b.slot_time)));
  }

  let _tsBulkMoveWired = false;
  function _wireTsBulkMoveModal() {
    if (_tsBulkMoveWired) return;
    _tsBulkMoveWired = true;
    document.getElementById('tsBulkMoveConfirmBtn')?.addEventListener('click', () => _tsBulkMoveExecute());
  }

  function _tsBulkMoveAutoAssign(rows) {
    const remaining = new Map();
    const assignedByTeacher = new Map();
    for (const row of rows) {
      for (const target of row.targets) {
        const key = String(target.id) + '|' + String(row.slot.slot_time);
        remaining.set(key, Math.max(remaining.get(key) || 0, Number(target.free_seats || 0)));
      }
    }

    // Place the most constrained tests first, then balance total assignments
    // between teachers while respecting each teacher/time capacity.
    const constrained = [...rows].sort((a, b) => {
      if (a.targets.length !== b.targets.length) return a.targets.length - b.targets.length;
      return String(a.slot.slot_time).localeCompare(String(b.slot.slot_time));
    });
    for (const row of constrained) {
      const candidates = row.targets.filter(target =>
        (remaining.get(String(target.id) + '|' + String(row.slot.slot_time)) || 0) > 0
      );
      candidates.sort((a, b) => {
        const loadA = assignedByTeacher.get(String(a.id)) || 0;
        const loadB = assignedByTeacher.get(String(b.id)) || 0;
        if (loadA !== loadB) return loadA - loadB;
        const freeA = remaining.get(String(a.id) + '|' + String(row.slot.slot_time)) || 0;
        const freeB = remaining.get(String(b.id) + '|' + String(row.slot.slot_time)) || 0;
        if (freeA !== freeB) return freeB - freeA;
        return String(a.display_name).localeCompare(String(b.display_name));
      });
      row.teacherId = candidates[0]?.id || '';
      if (row.teacherId) {
        const key = String(row.teacherId) + '|' + String(row.slot.slot_time);
        remaining.set(key, (remaining.get(key) || 0) - 1);
        assignedByTeacher.set(String(row.teacherId), (assignedByTeacher.get(String(row.teacherId)) || 0) + 1);
      }
    }
  }

  function _tsBulkMoveValidatePlan() {
    const bulk = state.ts.bulkMove;
    const confirmBtn = document.getElementById('tsBulkMoveConfirmBtn');
    const summary = document.getElementById('tsBulkMoveSummary');
    if (!bulk || !confirmBtn || !summary) return { ready: [], invalid: false };

    const capacity = new Map();
    const usage = new Map();
    for (const row of bulk.rows) {
      row.teacherId = String(row.select?.value || '');
      row.rowEl?.classList.remove('is-over-capacity');
      for (const target of row.targets) {
        const key = String(target.id) + '|' + String(row.slot.slot_time);
        capacity.set(key, Math.max(capacity.get(key) || 0, Number(target.free_seats || 0)));
      }
      if (row.teacherId) {
        const key = row.teacherId + '|' + String(row.slot.slot_time);
        usage.set(key, (usage.get(key) || 0) + 1);
      }
    }

    const invalidKeys = new Set();
    for (const [key, used] of usage) {
      if (used > (capacity.get(key) || 0)) invalidKeys.add(key);
    }
    for (const row of bulk.rows) {
      const key = row.teacherId + '|' + String(row.slot.slot_time);
      row.rowEl?.classList.toggle('is-over-capacity', !!row.teacherId && invalidKeys.has(key));
    }

    const assigned = bulk.rows.filter(row => row.teacherId);
    const ready = assigned.filter(row => !invalidKeys.has(row.teacherId + '|' + String(row.slot.slot_time)));
    const unassigned = bulk.rows.length - assigned.length;
    const invalid = invalidKeys.size > 0;
    const parts = [
      el('strong', { text: String(ready.length) }),
      document.createTextNode(' ready to move'),
    ];
    if (unassigned) parts.push(el('span', { class: 'ts-bulk-move-warning', text: unassigned + ' without an available teacher' }));
    if (invalid) parts.push(el('span', { class: 'ts-bulk-move-danger', text: 'Capacity exceeded — change the highlighted assignments' }));
    clear(summary);
    summary.append(...parts);

    confirmBtn.disabled = !ready.length || invalid;
    const label = confirmBtn.querySelector('span');
    if (label) label.textContent = ready.length ? ('Move ' + ready.length + ' test' + (ready.length === 1 ? '' : 's')) : 'Move tests';
    return { ready, invalid };
  }

  function _tsBulkMoveRenderRows() {
    const bulk = state.ts.bulkMove;
    const host = document.getElementById('tsBulkMoveList');
    if (!bulk || !host) return;
    clear(host);
    for (const row of bulk.rows) {
      const slot = row.slot;
      const select = el('select', {
        class: 'input ts-bulk-move-select',
        'aria-label': 'New teacher for ' + (slot.lead_name || slot.student_name || 'student'),
      });
      select.appendChild(el('option', { value: '', text: row.targets.length ? 'Leave with current teacher' : 'No teacher available' }));
      for (const target of row.targets) {
        select.appendChild(el('option', {
          value: target.id,
          text: target.display_name + ' · ' + target.free_seats + ' free',
        }));
      }
      select.value = String(row.teacherId || '');
      select.disabled = !row.targets.length;
      const rowEl = el('div', { class: 'ts-bulk-move-row' }, [
        el('span', { class: 'ts-bulk-move-time', text: String(slot.slot_time).slice(0, 5) }),
        el('span', { class: 'ts-bulk-move-student', text: slot.lead_name || slot.student_name || 'Unknown student' }),
        el('span', { class: 'ts-bulk-move-arrow' }, [lucideIcon('arrow-right')]),
        select,
      ]);
      row.select = select;
      row.rowEl = rowEl;
      select.addEventListener('change', () => _tsBulkMoveValidatePlan());
      host.appendChild(rowEl);
    }
    _tsBulkMoveValidatePlan();
    refreshIcons(host);
  }

  async function openTsBulkMoveModal(teacher) {
    _wireTsBulkMoveModal();
    const slots = _tsMovableSlotsForTeacher(teacher.id);
    if (!slots.length) { toast('No transferable tests for ' + teacher.name + ' on this day', 'warning'); return; }

    const token = String(Date.now()) + ':' + String(teacher.id);
    state.ts.bulkMove = { token, teacher, slots, rows: [] };
    const title = document.getElementById('tsBulkMoveTitle');
    const summary = document.getElementById('tsBulkMoveSummary');
    const host = document.getElementById('tsBulkMoveList');
    const errEl = document.getElementById('tsBulkMoveError');
    const confirmBtn = document.getElementById('tsBulkMoveConfirmBtn');
    if (title) title.textContent = 'Move ' + teacher.name + ' · ' + state.ts.date;
    if (summary) { clear(summary); summary.appendChild(el('span', { text: 'Finding the best available teachers…' })); }
    if (host) { clear(host); host.appendChild(el('div', { class: 'ts-bulk-move-loading', text: 'Checking live capacity for ' + slots.length + ' tests…' })); }
    errEl?.classList.add('hidden');
    if (confirmBtn) confirmBtn.disabled = true;
    openModal('tsBulkMoveModal');

    try {
      const rows = await Promise.all(slots.map(async slot => {
        const r = await api('/api/test-schedule/slots/' + encodeURIComponent(slot.id) + '/reassign-targets');
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error((slot.lead_name || slot.student_name || String(slot.slot_time).slice(0, 5)) + ': ' + (j.error || ('HTTP ' + r.status)));
        return { slot, targets: j.teachers || [], teacherId: '' };
      }));
      if (state.ts.bulkMove?.token !== token) return;
      _tsBulkMoveAutoAssign(rows);
      state.ts.bulkMove.rows = rows.sort((a, b) => String(a.slot.slot_time).localeCompare(String(b.slot.slot_time)));
      _tsBulkMoveRenderRows();
    } catch (err) {
      if (state.ts.bulkMove?.token !== token) return;
      if (host) {
        clear(host);
        host.appendChild(el('div', { class: 'ts-bulk-move-loading', text: 'Could not prepare the move.' }));
      }
      if (errEl) {
        errEl.textContent = String(err?.message || err);
        errEl.classList.remove('hidden');
      }
    }
  }

  async function _tsBulkMoveExecute() {
    const bulk = state.ts.bulkMove;
    const confirmBtn = document.getElementById('tsBulkMoveConfirmBtn');
    if (!bulk || !confirmBtn) return;
    const { ready, invalid } = _tsBulkMoveValidatePlan();
    if (!ready.length || invalid) return;

    confirmBtn.disabled = true;
    bulk.rows.forEach(row => { if (row.select) row.select.disabled = true; });
    const label = confirmBtn.querySelector('span');
    let moved = 0;
    const failures = [];
    for (let i = 0; i < ready.length; i++) {
      if (label) label.textContent = 'Moving ' + (i + 1) + '/' + ready.length + '…';
      const row = ready[i];
      try {
        const r = await api('/api/test-schedule/slots/' + encodeURIComponent(row.slot.id) + '/reassign', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ teacher_id: row.teacherId }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
        moved++;
      } catch (err) {
        failures.push(String(row.slot.slot_time).slice(0, 5) + ' ' + (row.slot.lead_name || row.slot.student_name || '') + ': ' + String(err?.message || err));
      }
    }

    closeModal('tsBulkMoveModal');
    if (failures.length) {
      toast(moved + ' moved · ' + failures.length + ' failed — ' + failures[0], 'error');
    } else {
      toast(moved + ' test' + (moved === 1 ? '' : 's') + ' auto-distributed', 'success');
    }
    await _tsRefreshAfterModalAction();
  }

  function _tsEvalNumeric(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(String(value).replace(',', '.'));
    return Number.isFinite(n) && n >= 0 && n <= 10 ? n : null;
  }

  const TS_SKILL_SCORE_IDS = ['tsEvalSpeakingScore', 'tsEvalListeningScore', 'tsEvalReadingScore', 'tsEvalWritingScore'];

  // Baseline skill scores suggested per CEFR level — C2 = 10 (per Ayoub),
  // stepped evenly down to A0 = 0. Picking a level auto-fills the four skill
  // inputs from this map; teachers stay free to bump any score up or down.
  const TS_LEVEL_SCORE_MAP = { A0: 0, A1: 2, A2: 3.5, B1: 5, B2: 6.5, C1: 8, C2: 10 };

  // Auto-fill the four skill inputs from the picked CEFR level, then recompute
  // the overall score exactly the way a manual edit does. Inputs stay editable.
  function _tsApplyLevelScores(level) {
    const score = TS_LEVEL_SCORE_MAP[level];
    if (score === undefined) return;
    for (const id of TS_SKILL_SCORE_IDS) {
      const input = document.getElementById(id);
      if (!input) continue;
      input.value = String(score);
      input.closest('.ts-skill-input')?.classList.remove('is-invalid');
    }
    _tsUpdateEvalOverall();
  }

  function _tsUpdateEvalOverall() {
    const ids = TS_SKILL_SCORE_IDS;
    const values = ids.map(id => _tsEvalNumeric(document.getElementById(id)?.value));
    const complete = values.every(v => v !== null);
    const overall = complete ? Math.round((values.reduce((sum, n) => sum + n, 0) / 4) * 10) / 10 : null;
    const display = document.getElementById('tsEvalOverallDisplay');
    if (display) display.textContent = overall === null ? '—' : overall.toFixed(1) + '/10';
    const legacy = document.getElementById('tsEvalScore');
    if (legacy) legacy.value = overall === null ? '' : String(overall);
    return overall;
  }

  // Current multi-selection (ordered; element 0 = primary). Lives on evalDraft.
  function _tsRecoCodes() {
    const d = state.ts?.evalDraft;
    if (!d) return [];
    if (!Array.isArray(d.recommendation_codes)) d.recommendation_codes = [];
    return d.recommendation_codes;
  }

  // Push the current selection to the DOM: primary → hidden code input, joined
  // legacy labels → legacy input, active class on every selected card, warning.
  function _tsRecoRefresh() {
    const codes = _tsRecoCodes();
    const codeInput = document.getElementById('tsEvalRecommendationCode');
    if (codeInput) codeInput.value = codes[0] || '';
    const legacyInput = document.getElementById('tsEvalRecommendation');
    if (legacyInput) {
      legacyInput.value = codes
        .map(c => TS_ASSESSMENT_RECOMMENDATIONS.find(r => r.code === c)?.legacy)
        .filter(Boolean).join(' + ');
    }
    const host = document.getElementById('tsEvalRecommendationGrid');
    if (host) host.querySelectorAll('.ts-rec-option').forEach(node =>
      node.classList.toggle('is-active', codes.includes(node.getAttribute('data-rec-code'))));
    _tsSyncRecommendationWarning();
  }

  function _tsSyncRecommendationWarning() {
    const warning = document.getElementById('tsEvalRecommendationWarning');
    if (!warning) return;
    const codes = _tsRecoCodes();
    const level = state.ts.evalDraft?.new_level || '';
    const caution = codes.some(c => c.startsWith('everyday_english_')) && ['A0', 'A1'].includes(level);
    warning.textContent = caution
      ? "Everyday English is generally recommended for A2 and above. Confirm that it matches this learner's needs."
      : '';
    warning.classList.toggle('hidden', !caution);
  }

  // Suggested learning path per CEFR level — beginners get the longer General
  // English foundation, advanced learners the shorter Speaking track. Kept
  // consistent with the A2+ Everyday-English guidance above. Picking a level
  // auto-highlights this card; the teacher can still choose a different one.
  const TS_LEVEL_RECOMMENDATION_MAP = {
    A0: 'english_lik_12m', A1: 'english_lik_12m', A2: 'english_lik_6m',
    B1: 'english_lik_6m', B2: 'everyday_english_6m',
    C1: 'everyday_english_6m', C2: 'everyday_english_3m',
  };

  // Auto-select the best-fit card for the picked CEFR level. Multi-select: the
  // level pick RESETS the selection to this single default; the teacher can then
  // toggle additional programs on. Teacher can still change the whole set.
  function _tsApplyLevelRecommendation(level) {
    const code = TS_LEVEL_RECOMMENDATION_MAP[level];
    if (!code || !state.ts?.evalDraft) return;
    if (!TS_ASSESSMENT_RECOMMENDATIONS.some(r => r.code === code)) return;
    state.ts.evalDraft.recommendation_codes = [code];
    _tsRecoRefresh();
  }

  function _tsRenderRecommendationOptions(slot) {
    const host = document.getElementById('tsEvalRecommendationGrid');
    if (!host) return;
    clear(host);
    state.ts.evalDraft.recommendation_codes = _tsRecommendationCodes(slot);
    for (const rec of TS_ASSESSMENT_RECOMMENDATIONS) {
      const btn = el('button', { type: 'button', class: 'ts-rec-option', 'data-rec-code': rec.code }, [
        el('span', { class: 'ts-rec-mark' }, [lucideIcon(rec.icon)]),
        el('span', { class: 'ts-rec-copy' }, [el('strong', { text: rec.program }), el('small', { text: rec.track })]),
        rec.duration ? el('span', { class: 'ts-rec-duration', text: rec.duration }) : el('span', { class: 'ts-rec-check' }, [lucideIcon('check')]),
      ]);
      // Multi-select toggle: add/remove this program from the ordered set.
      btn.addEventListener('click', () => {
        const codes = _tsRecoCodes();
        const i = codes.indexOf(rec.code);
        if (i >= 0) codes.splice(i, 1); else codes.push(rec.code);
        _tsRecoRefresh();
      });
      host.appendChild(btn);
    }
    _tsRecoRefresh();
  }

  // Seed and render the assessment form from the currently-opened slot. Raw
  // teacher notes remain raw; the server creates separate client-facing copy.
  function _tsRenderEvalForm(slot) {
    const scoreKeys = ['speaking_score', 'listening_score', 'reading_score', 'writing_score'];
    const baselineScores = Object.fromEntries(scoreKeys.map(key => [key, _tsEvalNumeric(slot[key])]));
    state.ts.evalBaseline = {
      slotId: slot.id,
      new_level: slot.new_level || '',
      notes: String(slot.notes || '').trim(),
      recommendation_codes: _tsRecommendationCodes(slot),
      ...baselineScores,
    };
    state.ts.evalDraft = {
      slotId: slot.id,
      new_level: slot.new_level || '',
      recommendation_codes: _tsRecommendationCodes(slot),
    };
    const host = document.getElementById('tsEvalLevelRow');
    if (host) {
      clear(host);
      for (const level of ['A0','A1','A2','B1','B2','C1','C2']) {
        const isActive = slot.new_level === level;
        const btn = el('button', {
          type: 'button', class: 'cefr-pill cefr-' + level + (isActive ? ' active' : ''),
          text: level, 'data-level': level,
        });
        btn.addEventListener('click', () => {
          state.ts.evalDraft.new_level = level;
          host.querySelectorAll('.cefr-pill').forEach(node => node.classList.toggle('active', node === btn));
          _tsApplyLevelScores(level);
          _tsApplyLevelRecommendation(level);
          _tsSyncRecommendationWarning();
        });
        host.appendChild(btn);
      }
    }
    const inputMap = {
      speaking_score: 'tsEvalSpeakingScore', listening_score: 'tsEvalListeningScore',
      reading_score: 'tsEvalReadingScore', writing_score: 'tsEvalWritingScore',
    };
    for (const [key, id] of Object.entries(inputMap)) {
      const input = document.getElementById(id);
      if (!input) continue;
      input.value = baselineScores[key] === null ? '' : String(baselineScores[key]);
      input.closest('.ts-skill-input')?.classList.remove('is-invalid');
      if (!input._tsOverallWired) {
        input._tsOverallWired = true;
        input.addEventListener('input', () => {
          input.closest('.ts-skill-input')?.classList.remove('is-invalid');
          _tsUpdateEvalOverall();
        });
      }
    }
    const notes = document.getElementById('tsEvalNotes');
    if (notes) notes.value = slot.notes || '';
    _tsUpdateEvalOverall();
    _tsRenderRecommendationOptions(slot);
    const reportActions = document.getElementById('tsEvalReportActions');
    if (reportActions) reportActions.classList.toggle('hidden', !(slot.status === 'completed' && _tsAssessmentReadiness(slot)));
    const submit = document.getElementById('tsEvalSubmitBtn');
    if (submit) { submit.disabled = false; submit.classList.remove('is-loading'); submit.querySelector('span').textContent = 'Save and prepare action plan'; }
    _tsLoadStudioResult(slot);
    refreshIcons();
  }

  // The teacher's "Finish & score" from the Test Studio, when they saved one
  // for this learner (Ayoub, 2026-10-05: the score sheet never said whose score
  // it was). Shown above the form; an empty observation box takes the teacher's
  // own Studio notes, so they check, pick the level and save as before.
  function _tsStudioObservation(result) {
    const parts = [];
    if (result.strength) parts.push('Strength: ' + result.strength);
    if (result.nextStep) parts.push('Next step: ' + result.nextStep);
    return parts.join('\n');
  }

  async function _tsLoadStudioResult(slot) {
    const host = document.getElementById('tsEvalStudioResult');
    if (!host) return;
    clear(host);
    host.classList.add('hidden');
    const slotId = slot?.id;
    if (!slotId) return;
    let result = null;
    try {
      const r = await api('/api/test-schedule/slots/' + encodeURIComponent(slotId) + '/studio-result');
      if (!r.ok) return;
      result = (await r.json().catch(() => ({}))).result || null;
    } catch (_) { return; }
    if (!result || state.ts.actionsSlot?.id !== slotId) return;
    const pack = TEST_LIBRARY_PACKS.find(item => item.id === result.packId);
    const observation = _tsStudioObservation(result);
    const notes = document.getElementById('tsEvalNotes');
    // The level the teacher chose on the Studio score sheet.
    const level = /^(A0|A1|A2|B1|B2|C1|C2)$/.test(String(result.level || '')) ? result.level : '';
    host.appendChild(el('div', { class: 'ts-studio-result-head' }, [
      el('span', { class: 'ts-studio-result-icon' }, [lucideIcon('presentation')]),
      el('div', { class: 'ts-studio-result-title' }, [
        el('strong', { text: 'Test Studio score' }),
        el('small', { text: pack ? pack.badge + ' test · ' + pack.title : 'Test Library' }),
      ]),
      level ? el('span', { class: 'cefr-pill cefr-' + level + ' ts-studio-result-level', text: level, title: 'Level chosen in the Test Studio' }) : null,
      el('span', { class: 'ts-studio-result-total', text: result.total + '/' + (result.maxTotal || 20) }),
    ]));
    // No assessment saved on this test yet: take the Studio's level, exactly as
    // a click on that pill would (skill scores and learning path follow). The
    // Studio save itself already put that level on the seat, so "no assessment"
    // = no level, or the Studio's level with no skill scores. The teacher still
    // checks and saves; a level they just picked is kept.
    const studioLevelOnly = !slot.new_level || (slot.new_level === level && !_tsHasSkillScores(slot));
    if (level && studioLevelOnly && state.ts.evalDraft?.slotId === slotId
        && (state.ts.evalDraft.new_level || '') === (slot.new_level || '')) {
      document.querySelector('#tsEvalLevelRow .cefr-pill[data-level="' + level + '"]')?.click();
    }
    if (result.band) host.appendChild(el('p', { class: 'ts-studio-result-band', text: result.band }));
    const details = [];
    if (result.strength) details.push(el('div', {}, [el('span', { text: 'Strength' }), el('p', { text: result.strength })]));
    if (result.nextStep) details.push(el('div', {}, [el('span', { text: 'Next step' }), el('p', { text: result.nextStep })]));
    if (details.length) host.appendChild(el('div', { class: 'ts-studio-result-notes' }, details));
    if (observation && notes) {
      const addBtn = el('button', { type: 'button', class: 'btn btn-sm btn-secondary' });
      const setAdded = (added) => {
        addBtn.disabled = added;
        clear(addBtn);
        addBtn.append(lucideIcon(added ? 'check' : 'plus'), el('span', { text: added ? 'In the observations' : 'Add to observations' }));
        refreshIcons(addBtn);
      };
      addBtn.addEventListener('click', () => {
        const current = String(notes.value || '').trim();
        if (!current.includes(observation)) notes.value = current ? current + '\n\n' + observation : observation;
        setAdded(true);
      });
      // The teacher's own words: an empty observation box takes them at once.
      if (!String(notes.value || '').trim() && !String(slot.notes || '').trim()) notes.value = observation;
      setAdded(String(notes.value || '').includes(observation));
      host.appendChild(el('div', { class: 'ts-studio-result-actions' }, [addBtn]));
    }
    host.classList.remove('hidden');
    refreshIcons(host);
  }

  async function tsEvalSubmit() {
    const slot = state.ts.actionsSlot;
    if (!slot) return;
    const errEl = document.getElementById('tsActionsError');
    errEl.classList.add('hidden');
    const showErr = (msg) => {
      errEl.textContent = String(msg);
      errEl.classList.remove('hidden');
      try { errEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch {}
    };
    const draft = (state.ts.evalDraft && state.ts.evalDraft.slotId === slot.id) ? state.ts.evalDraft : {};
    const base = (state.ts.evalBaseline && state.ts.evalBaseline.slotId === slot.id) ? state.ts.evalBaseline : {};
    if (!/^(A0|A1|A2|B1|B2|C1|C2)$/.test(String(draft.new_level || ''))) {
      return showErr("Select the learner's CEFR level.");
    }
    const knownCodes = new Set(TS_ASSESSMENT_RECOMMENDATIONS.map(r => r.code));
    const recommendationCodes = (Array.isArray(draft.recommendation_codes) ? draft.recommendation_codes : [])
      .map(c => String(c)).filter(c => knownCodes.has(c));
    if (!recommendationCodes.length) {
      return showErr('Select at least one recommended learning path.');
    }
    const notesVal = String(document.getElementById('tsEvalNotes')?.value || '').trim();
    if (!notesVal) return showErr('Add a short teacher observation to personalize the action plan.');

    const skillInputs = {
      speaking_score: document.getElementById('tsEvalSpeakingScore'),
      listening_score: document.getElementById('tsEvalListeningScore'),
      reading_score: document.getElementById('tsEvalReadingScore'),
      writing_score: document.getElementById('tsEvalWritingScore'),
    };
    const skillLabels = { speaking_score: 'Speaking', listening_score: 'Listening', reading_score: 'Reading', writing_score: 'Writing' };
    const skills = {};
    let invalidSkill = null;
    for (const [key, input] of Object.entries(skillInputs)) {
      input?.closest('.ts-skill-input')?.classList.remove('is-invalid');
      const raw = String(input?.value || '').trim().replace(',', '.');
      const value = raw === '' ? null : Number(raw);
      if (input?.validity?.badInput || value === null || !Number.isFinite(value) || value < 0 || value > 10
          || Math.abs(value * 10 - Math.round(value * 10)) > 1e-9) {
        invalidSkill = key;
        input?.closest('.ts-skill-input')?.classList.add('is-invalid');
        break;
      }
      skills[key] = value;
    }
    if (invalidSkill) return showErr(skillLabels[invalidSkill] + ': enter a score from 0 to 10 with no more than one decimal place.');

    const body = {};
    if (draft.new_level !== (base.new_level || '')) body.new_level = draft.new_level;
    if (notesVal && notesVal !== (base.notes || '')) body.notes = notesVal;
    for (const [key, value] of Object.entries(skills)) {
      if (value !== (base[key] ?? null)) body[key] = value;
    }
    const baseCodes = Array.isArray(base.recommendation_codes) ? base.recommendation_codes : [];
    if (recommendationCodes.length !== baseCodes.length || recommendationCodes.some((c, i) => c !== baseCodes[i])) {
      body.recommendation_codes = recommendationCodes;
    }
    // Saving a completed assessment is the teacher's single final action: it
    // closes any still-active slot and starts report preparation.
    if (['booked', 'confirmed', 'reminder_sent', 'in_test'].includes(slot.status)) body.status = 'completed';

    const submitBtn = document.getElementById('tsEvalSubmitBtn');
    const completedBtn = document.getElementById('tsActionMarkCompletedBtn');
    const submitLabel = submitBtn?.querySelector('span');
    if (submitBtn) { submitBtn.disabled = true; submitBtn.classList.add('is-loading'); }
    if (completedBtn) completedBtn.disabled = true;
    if (submitLabel) submitLabel.textContent = 'Saving…';
    try {
      if (!Object.keys(body).length) {
        const prepare = await api('/api/test-schedule/slots/' + encodeURIComponent(slot.id) + '/report/prepare', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}), timeoutMs: 75000,
        });
        const prepared = await prepare.json().catch(() => ({}));
        if (!prepare.ok) throw new Error(prepared.error || 'Action plan preparation failed');
        toast('Assessment already saved · action plan ready', 'success');
        closeModal('tsActionsModal');
        await _tsRefreshAfterModalAction();
        return;
      }
      const r = await api('/api/test-schedule/slots/' + encodeURIComponent(slot.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({ error: 'HTTP ' + r.status }));
        throw new Error(j.error || 'Save failed');
      }
      const saved = await r.json().catch(() => ({}));
      if (saved.slot) state.ts.actionsSlot = { ...slot, ...saved.slot };
      toast('Assessment saved · action plan is being prepared', 'success');
      closeModal('tsActionsModal');
      await _tsRefreshAfterModalAction();
    } catch (err) {
      showErr(err?.message || err);
      toast('Assessment was not saved: ' + (err?.message || err), 'error');
    } finally {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.classList.remove('is-loading'); }
      if (completedBtn) completedBtn.disabled = false;
      if (submitLabel) submitLabel.textContent = 'Save and prepare action plan';
    }
  }

  // Refresh every surface that can show slot data after a modal action.
  // The modal opens from THREE entry points — the grid, the teacher
  // "Mes tests" view, and the admin Tests table — but the old code only
  // refreshed the grid. A teacher saving from "Mes tests" still saw stale
  // "—" cards, which read as "the save didn't work" (Nada, 2026-06-09).
  // Each refresh is guarded: skip surfaces that were never inited (the grid
  // fetch with a null date was a guaranteed 400).
  async function _tsRefreshAfterModalAction() {
    // A seat action (book / in_test / completed / no_show / cancel / move)
    // changes what the period counts, so the strip must recount too.
    _tsInvalidatePeriodStats();
    if (state.ts.date) { try { await loadTestSchedule(); } catch (_) { /* noop */ } }
    if (state.teacherTests?.inited) { try { await _ttLoad(); } catch (_) { /* noop */ } }
    if (state.testsView?.inited) { try { await _testsViewLoad(); } catch (_) { /* noop */ } }
  }

  async function tsSendLink() {
    const slot = state.ts.actionsSlot;
    if (!slot) return;
    const errEl = document.getElementById('tsActionsError');
    errEl.classList.add('hidden');
    try {
      const r = await api('/api/test-schedule/slots/' + encodeURIComponent(slot.id) + '/send-link', { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'HTTP ' + r.status);
      toast('Lien Lougha envoyé au student', 'success');
      await _tsRefreshAfterModalAction();
    } catch (err) {
      errEl.textContent = String(err.message || err);
      errEl.classList.remove('hidden');
    }
  }

  async function tsMoveSlot() {
    const slot = state.ts.actionsSlot;
    const select = document.getElementById('tsMoveTeacherSelect');
    const button = document.getElementById('tsActionMoveBtn');
    const errEl = document.getElementById('tsActionsError');
    const teacherId = String(select?.value || '');
    if (!slot || !teacherId || !button || !errEl) return;

    errEl.classList.add('hidden');
    button.disabled = true;
    select.disabled = true;
    try {
      const r = await api('/api/test-schedule/slots/' + encodeURIComponent(slot.id) + '/reassign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teacher_id: teacherId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      const teacherName = select.options[select.selectedIndex]?.text?.split(' · ')[0] || 'the selected teacher';
      closeModal('tsActionsModal');
      toast('Test moved to ' + teacherName, 'success');
      await _tsRefreshAfterModalAction();
    } catch (err) {
      errEl.textContent = String(err?.message || err);
      errEl.classList.remove('hidden');
      select.disabled = false;
      button.disabled = !select.value;
    }
  }

  async function tsActionPatch(status) {
    const slot = state.ts.actionsSlot;
    if (!slot) return;
    const errEl = document.getElementById('tsActionsError');
    errEl.classList.add('hidden');
    try {
      const body = { status };
      if (status === 'cancelled') body.reason = 'Cancelled from grid';
      const r = await api('/api/test-schedule/slots/' + encodeURIComponent(slot.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({ error: 'HTTP ' + r.status }));
        throw new Error(j.error || 'Update failed');
      }
      closeModal('tsActionsModal');
      toast('Slot updated to ' + (TS_STATUS_LABEL[status] || status), 'success');
      await _tsRefreshAfterModalAction();
    } catch (err) {
      errEl.textContent = String(err.message || err);
      errEl.classList.remove('hidden');
    }
  }

})();
